import * as React from 'react';
import React__default, { useLayoutEffect as useLayoutEffect$1, useState, useCallback, useRef, useEffect, createContext, useContext, memo, forwardRef, Fragment, isValidElement as isValidElement$1, cloneElement, createElement as createElement$1, useReducer, createRef, useMemo, useImperativeHandle, Children } from 'react';
import { jsx, jsxs, Fragment as Fragment$1 } from 'react/jsx-runtime';
import ReactDOM, { createPortal, flushSync, unstable_batchedUpdates } from 'react-dom';
import { Controller as Controller$1 } from 'react-hook-form';

const colors$3 = {
    inherit: 'inherit',
    current: 'currentColor',
    transparent: 'transparent',
    black: '#000000',
    white: '#FFFFFF',
    primary: {
        900: '#0A3333',
        800: '#0E4B4B',
        700: '#116363',
        650: '#0D8080',
        600: '#009999',
        500: '#06ABAB',
        400: '#16BDBD',
        300: '#53D7D7',
        200: '#98E9E9',
        100: '#C2F2F2',
        50: '#F0FFFF',
    },
    blue: {
        900: '#0C233E',
        800: '#113054',
        700: '#17406F',
        600: '#1B4A80',
        500: '#215692',
        400: '#2C67AB',
        300: '#4E87C8',
        200: '#6DA0DB',
        100: '#98C0ED',
        50: '#CCE4FF',
    },
    green: {
        900: '#0A3321',
        800: '#0E452D',
        700: '#15593C',
        600: '#258058',
        500: '#42A67B',
        400: '#50C190',
        300: '#60D1A0',
        200: '#71DEAF',
        100: '#9FECCA',
        50: '#C2F2DD',
    },
    orange: {
        900: '#33240A',
        800: '#5A3F11',
        700: '#815916',
        600: '#BB7D13',
        500: '#E19208',
        400: '#F59B00',
        300: '#F3B241',
        200: '#F3C77A',
        100: '#F3D49E',
        50: '#F2E0C2',
    },
    lemon: {
        900: '#332700',
        800: '#5C4701',
        700: '#856704',
        600: '#C29913',
        500: '#D6AB1B',
        400: '#EBBD26',
        300: '#FFCF33',
        200: '#F7D563',
        100: '#F5DD8E',
        50: '#F2E7C2',
    },
    rsbry: {
        900: '#50111F',
        800: '#6D192C',
        700: '#A8364E',
        600: '#C54E68',
        500: '#E26F88',
        400: '#F0839B',
        300: '#FF99AF',
        200: '#F7A5B7',
        100: '#F5B2C0',
        50: '#F2C2CC',
    },
    lime: {
        900: '#14330A',
        800: '#1E4C10',
        700: '#296517',
        600: '#47962F',
        500: '#69BB50',
        400: '#86D46E',
        300: '#96E080',
        200: '#A1E88C',
        100: '#BBEFAB',
        50: '#CDF2C2',
    },
    avocado: {
        900: '#082E18',
        800: '#063D1E',
        700: '#095128',
        600: '#0E6534',
        500: '#157A41',
        400: '#30A262',
        300: '#48B678',
        200: '#68CA92',
        100: '#91DEB3',
        50: '#C2F2D7',
    },
    aqua: {
        900: '#0B333C',
        800: '#0D424E',
        700: '#0D505F',
        600: '#09657A',
        500: '#007994',
        400: '#2096B0',
        300: '#48A1B5',
        200: '#68BACC',
        100: '#89CEDE',
        50: '#C2EAF2',
    },
    gray: {
        900: '#111827',
        800: '#1F2937',
        700: '#374151',
        600: '#4B5563',
        500: '#6B7280',
        400: '#9CA3AF',
        300: '#D1D5DB',
        200: '#E5E7EB',
        100: '#F3F4F6',
        50: '#F9FAFB',
    },
};

var ColorVariant;
(function (ColorVariant) {
    ColorVariant["PRIMARY"] = "PRIMARY";
    ColorVariant["LIGHT_PRIMARY"] = "LIGHT_PRIMARY";
    ColorVariant["BLUE"] = "BLUE";
    ColorVariant["DARK_BLUE"] = "DARK_BLUE";
    ColorVariant["LIGHT_BLUE"] = "LIGHT_BLUE";
    ColorVariant["GREEN"] = "GREEN";
    ColorVariant["LIGHT_GREEN"] = "LIGHT_GREEN";
    ColorVariant["LIME"] = "LIME";
    ColorVariant["LIGHT_LIME"] = "LIGHT_LIME";
    ColorVariant["AQUA"] = "AQUA";
    ColorVariant["DARK_AQUA"] = "DARK_AQUA";
    ColorVariant["LIGHT_AVOCADO"] = "LIGHT_AVOCADO";
    ColorVariant["DARK_AVOCADO"] = "DARK_AVOCADO";
    ColorVariant["LEMON"] = "LEMON";
    ColorVariant["LIGHT_LEMON"] = "LIGHT_LEMON";
    ColorVariant["ORANGE"] = "ORANGE";
    ColorVariant["LIGHT_ORANGE"] = "LIGHT_ORANGE";
    ColorVariant["RSBRY"] = "RSBRY";
    ColorVariant["LIGHT_RSBRY"] = "LIGHT_RSBRY";
    ColorVariant["GRAY"] = "GRAY";
})(ColorVariant || (ColorVariant = {}));

const COLOR_RECIPE_PATTERN = {
    [ColorVariant.PRIMARY]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-primary-50',
                bg: 'bg-primary-600',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.primary[50],
                bg: colors$3.primary[600],
            },
        },
        darkBg: {
            className: {
                text: 'text-primary-500',
                highlightText: 'text-primary-500',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.primary[500],
                highlightText: colors$3.primary[500],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-primary-600',
                highlightText: 'text-primary-600',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.primary[600],
                highlightText: colors$3.primary[600],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.LIGHT_PRIMARY]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-primary-700',
                bg: 'bg-primary-400',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.primary[700],
                bg: colors$3.primary[400],
            },
        },
        darkBg: {
            className: {
                text: 'text-primary-500',
                highlightText: 'text-primary-500',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.primary[500],
                highlightText: colors$3.primary[500],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-primary-600',
                highlightText: 'text-primary-600',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.primary[600],
                highlightText: colors$3.primary[600],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.BLUE]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-blue-50',
                bg: 'bg-blue-600',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.blue[50],
                bg: colors$3.blue[600],
            },
        },
        darkBg: {
            className: {
                text: 'text-blue-300',
                highlightText: 'text-blue-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.blue[300],
                highlightText: colors$3.blue[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-blue-600',
                highlightText: 'text-blue-600',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.blue[600],
                highlightText: colors$3.blue[600],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.DARK_BLUE]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-blue-50',
                bg: 'bg-blue-900',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.blue[50],
                bg: colors$3.blue[900],
            },
        },
        darkBg: {
            className: {
                text: 'text-blue-200',
                highlightText: 'text-blue-200',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.blue[200],
                highlightText: colors$3.blue[200],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-blue-800',
                highlightText: 'text-blue-800',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.blue[800],
                highlightText: colors$3.blue[800],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.LIGHT_BLUE]: {
        colorBg: {
            className: {
                text: 'text-gray-800',
                highlightText: 'text-blue-50',
                bg: 'bg-blue-300',
            },
            color: {
                text: colors$3.gray[800],
                highlightText: colors$3.blue[50],
                bg: colors$3.blue[300],
            },
        },
        darkBg: {
            className: {
                text: 'text-blue-100',
                highlightText: 'text-blue-100',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.blue[100],
                highlightText: colors$3.blue[100],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-blue-300',
                highlightText: 'text-blue-300',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.blue[300],
                highlightText: colors$3.blue[300],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.GREEN]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-green-100',
                bg: 'bg-green-600',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.green[100],
                bg: colors$3.green[600],
            },
        },
        darkBg: {
            className: {
                text: 'text-green-300',
                highlightText: 'text-green-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.green[300],
                highlightText: colors$3.green[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-green-300',
                highlightText: 'text-green-300',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.green[300],
                highlightText: colors$3.green[300],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.LIGHT_GREEN]: {
        colorBg: {
            className: {
                text: 'text-gray-800',
                highlightText: 'text-green-700',
                bg: 'bg-green-300',
            },
            color: {
                text: colors$3.gray[800],
                highlightText: colors$3.green[700],
                bg: colors$3.green[300],
            },
        },
        darkBg: {
            className: {
                text: 'text-green-300',
                highlightText: 'text-green-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.green[300],
                highlightText: colors$3.green[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-green-300',
                highlightText: 'text-green-300',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.green[300],
                highlightText: colors$3.green[300],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.LIME]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-lime-50',
                bg: 'bg-lime-600',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.lime[50],
                bg: colors$3.lime[600],
            },
        },
        darkBg: {
            className: {
                text: 'text-lime-300',
                highlightText: 'text-lime-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.lime[300],
                highlightText: colors$3.lime[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-lime-500',
                highlightText: 'text-lime-500',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.lime[500],
                highlightText: colors$3.lime[500],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.LIGHT_LIME]: {
        colorBg: {
            className: {
                text: 'text-gray-800',
                highlightText: 'text-gray-700',
                bg: 'bg-lime-300',
            },
            color: {
                text: colors$3.gray[800],
                highlightText: colors$3.gray[700],
                bg: colors$3.lime[300],
            },
        },
        darkBg: {
            className: {
                text: 'text-lime-300',
                highlightText: 'text-lime-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.lime[300],
                highlightText: colors$3.lime[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-lime-600',
                highlightText: 'text-lime-600',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.lime[600],
                highlightText: colors$3.lime[600],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.AQUA]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-aqua-50',
                bg: 'bg-aqua-500',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.aqua[50],
                bg: colors$3.aqua[500],
            },
        },
        darkBg: {
            className: {
                text: 'text-aqua-200',
                highlightText: 'text-aqua-200',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.aqua[200],
                highlightText: colors$3.aqua[200],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-aqua-500',
                highlightText: 'text-aqua-500',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.aqua[500],
                highlightText: colors$3.aqua[500],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.DARK_AQUA]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-aqua-50',
                bg: 'bg-aqua-700',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.aqua[50],
                bg: colors$3.aqua[700],
            },
        },
        darkBg: {
            className: {
                text: 'text-aqua-300',
                highlightText: 'text-aqua-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.aqua[300],
                highlightText: colors$3.aqua[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-aqua-500',
                highlightText: 'text-aqua-500',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.aqua[500],
                highlightText: colors$3.aqua[500],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.DARK_AVOCADO]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-avocado-50',
                bg: 'bg-avocado-800',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.avocado[50],
                bg: colors$3.avocado[800],
            },
        },
        darkBg: {
            className: {
                text: 'text-avocado-300',
                highlightText: 'text-avocado-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.avocado[300],
                highlightText: colors$3.avocado[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-avocado-800',
                highlightText: 'text-avocado-800',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.avocado[800],
                highlightText: colors$3.avocado[800],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.LIGHT_AVOCADO]: {
        colorBg: {
            className: {
                text: 'text-gray-800',
                highlightText: 'text-avocado-700',
                bg: 'bg-avocado-300',
            },
            color: {
                text: colors$3.gray[800],
                highlightText: colors$3.avocado[700],
                bg: colors$3.avocado[300],
            },
        },
        darkBg: {
            className: {
                text: 'text-avocado-200',
                highlightText: 'text-avocado-200',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.avocado[200],
                highlightText: colors$3.avocado[200],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-avocado-300',
                highlightText: 'text-avocado-300',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.avocado[300],
                highlightText: colors$3.avocado[300],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.LEMON]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-lemon-800',
                bg: 'bg-lemon-600',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.lemon[800],
                bg: colors$3.lemon[600],
            },
        },
        darkBg: {
            className: {
                text: 'text-lemon-300',
                highlightText: 'text-lemon-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.lemon[300],
                highlightText: colors$3.lemon[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-lemon-600',
                highlightText: 'text-lemon-600',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.lemon[600],
                highlightText: colors$3.lemon[600],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.LIGHT_LEMON]: {
        colorBg: {
            className: {
                text: 'text-gray-800',
                highlightText: 'text-lemon-800',
                bg: 'bg-lemon-300',
            },
            color: {
                text: colors$3.gray[800],
                highlightText: colors$3.lemon[800],
                bg: colors$3.lemon[300],
            },
        },
        darkBg: {
            className: {
                text: 'text-lemon-300',
                highlightText: 'text-lemon-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.lemon[300],
                highlightText: colors$3.lemon[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-lemon-600',
                highlightText: 'text-lemon-600',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.lemon[600],
                highlightText: colors$3.lemon[600],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.ORANGE]: {
        colorBg: {
            className: {
                text: 'text-gray-800',
                highlightText: 'text-orange-800',
                bg: 'bg-orange-400',
            },
            color: {
                text: colors$3.gray[800],
                highlightText: colors$3.orange[800],
                bg: colors$3.orange[400],
            },
        },
        darkBg: {
            className: {
                text: 'text-orange-400',
                highlightText: 'text-orange-400',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.orange[400],
                highlightText: colors$3.orange[400],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-orange-500',
                highlightText: 'text-orange-500',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.orange[500],
                highlightText: colors$3.orange[500],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.LIGHT_ORANGE]: {
        colorBg: {
            className: {
                text: 'text-gray-800',
                highlightText: 'text-orange-700',
                bg: 'bg-orange-200',
            },
            color: {
                text: colors$3.gray[800],
                highlightText: colors$3.orange[700],
                bg: colors$3.orange[200],
            },
        },
        darkBg: {
            className: {
                text: 'text-orange-400',
                highlightText: 'text-orange-400',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.orange[400],
                highlightText: colors$3.orange[400],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-orange-500',
                highlightText: 'text-orange-500',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.orange[500],
                highlightText: colors$3.orange[500],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.RSBRY]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-rsbry-900',
                bg: 'bg-rsbry-600',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.rsbry[900],
                bg: colors$3.rsbry[600],
            },
        },
        darkBg: {
            className: {
                text: 'text-rsbry-300',
                highlightText: 'text-rsbry-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.rsbry[300],
                highlightText: colors$3.rsbry[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-rsbry-600',
                highlightText: 'text-rsbry-600',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.rsbry[600],
                highlightText: colors$3.rsbry[600],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.LIGHT_RSBRY]: {
        colorBg: {
            className: {
                text: 'text-gray-800',
                highlightText: 'text-rsbry-800',
                bg: 'bg-rsbry-300',
            },
            color: {
                text: colors$3.gray[800],
                highlightText: colors$3.rsbry[800],
                bg: colors$3.rsbry[300],
            },
        },
        darkBg: {
            className: {
                text: 'text-rsbry-300',
                highlightText: 'text-rsbry-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.rsbry[300],
                highlightText: colors$3.rsbry[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-rsbry-600',
                highlightText: 'text-rsbry-600',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.rsbry[600],
                highlightText: colors$3.rsbry[600],
                bg: colors$3.white,
            },
        },
    },
    [ColorVariant.GRAY]: {
        colorBg: {
            className: {
                text: 'text-white',
                highlightText: 'text-gray-50',
                bg: 'bg-gray-700',
            },
            color: {
                text: colors$3.white,
                highlightText: colors$3.gray[50],
                bg: colors$3.gray[700],
            },
        },
        darkBg: {
            className: {
                text: 'text-gray-300',
                highlightText: 'text-gray-300',
                bg: 'bg-gray-800',
            },
            color: {
                text: colors$3.gray[300],
                highlightText: colors$3.gray[300],
                bg: colors$3.gray[800],
            },
        },
        whiteBg: {
            className: {
                text: 'text-gray-700',
                highlightText: 'text-gray-700',
                bg: 'bg-white',
            },
            color: {
                text: colors$3.gray[700],
                highlightText: colors$3.gray[700],
                bg: colors$3.white,
            },
        },
    },
};

const colorStopToColorRange = (colorStop) => {
    if (typeof colorStop === 'number') {
        return `${colorStop}%`;
    }
    if (Array.isArray(colorStop)) {
        return `${colorStop[0]}% ${colorStop[1]}%`;
    }
    return '';
};
const convertRgbColorsToCss = (rgbColors) => rgbColors
    .map(([rgb, colorStop]) => [`rgb(${rgb.join()})`, colorStopToColorRange(colorStop)].filter(Boolean).join(' '))
    .join(', ');
const convertHexColorsToCss = (hexGradient) => hexGradient.map(([hex, colorStop]) => [hex, colorStopToColorRange(colorStop)].filter(Boolean).join(' ')).join(', ');
const createLinearGradientCss = ({ colors, direction = 'to right' }) => {
    if (typeof colors[0][0] === 'string') {
        return `linear-gradient(${direction}, ${convertHexColorsToCss(colors)})`;
    }
    return `linear-gradient(${direction}, ${convertRgbColorsToCss(colors)})`;
};
const pickColorStop = (colorStop) => {
    if (Array.isArray(colorStop)) {
        return colorStop[0];
    }
    return colorStop;
};
const isValidColorStop = (colorStop) => {
    if (Array.isArray(colorStop)) {
        return colorStop.every(isValidColorStop);
    }
    return typeof colorStop === 'number' && colorStop >= 0;
};
// pickRgbFromGradient requires a gradient with explicit stops
// If you don't know which stops you have to add, use this function to calc the stops in the same way CSS does
const sanitizeRgbGradient = (gradient) => {
    // Remove undefined from gradient stops
    // We make sure the first and last color stop always have a color stop
    const initialSanitizedGradient = gradient.map(([rgbColor, colorStop], index) => {
        if (index === 0) {
            if (!isValidColorStop(colorStop)) {
                return [rgbColor, 0];
            }
        }
        if (index === gradient.length - 1) {
            if (!isValidColorStop(colorStop)) {
                return [rgbColor, 100];
            }
        }
        // we change null/undefined by -1 to mark them using a number to meet interface typing
        // making sure they will have a correct color stop in next steps
        return [rgbColor, isValidColorStop(colorStop) ? pickColorStop(colorStop) : -1];
    });
    // get explicit gradient stop indexes
    const gradientStops = initialSanitizedGradient
        .map(([, colorStop], index) => [index, colorStop])
        .filter((rgbColorStop) => isValidColorStop(rgbColorStop[1]))
        .map(([index, colorStop]) => [index, pickColorStop(colorStop)]);
    // create inner gradients based on explicit gradient stops
    const innerGradients = [];
    for (let index = 0; index < gradientStops.length - 1; index++) {
        const firstGradientStop = gradientStops[index];
        const secondGradientStop = gradientStops[index + 1];
        const firstColorIndex = firstGradientStop[0];
        const secondColorIndex = secondGradientStop[0];
        const [, firstColorStop] = initialSanitizedGradient[firstGradientStop[0]];
        const [, secondColorStop] = initialSanitizedGradient[secondGradientStop[0]];
        if (firstColorIndex + 1 !== secondColorIndex) {
            innerGradients.push([
                firstColorIndex,
                secondColorIndex,
                (firstColorStop + secondColorStop) / (secondColorIndex - firstColorIndex),
            ]);
        }
    }
    // create the new gradient with explicit stops for each color
    const sanitizedGradient = initialSanitizedGradient.map(([rgbColor, colorStop], index) => {
        if (colorStop >= 0) {
            return [rgbColor, colorStop];
        }
        const innerGradient = innerGradients.find(([_, secondIndex]) => index < secondIndex);
        if (innerGradient) {
            const [firstColorIndex, , colorStop] = innerGradient;
            const sanitizedColorStop = Math.round((index - firstColorIndex) * colorStop);
            return [rgbColor, sanitizedColorStop];
        }
        return [rgbColor, colorStop];
    });
    return sanitizedGradient;
};
const pickRgbFromGradient = (selectedColorStop, gradient) => {
    let [firstColorIndex, secondColorIndex] = [];
    // Get the two closest colors from gradient
    for (let index = gradient.length - 1; index >= 0; index--) {
        const [, colorStop] = gradient[index];
        const firstColorStop = pickColorStop(colorStop);
        if (selectedColorStop <= firstColorStop) {
            [firstColorIndex, secondColorIndex] = [index - 1, index];
        }
    }
    // if second color index is undefined, there is no explicit stop at 100% so we return the last color in the gradient
    if (secondColorIndex === undefined) {
        const [RgbColor] = gradient[gradient.length - 1];
        return RgbColor;
    }
    // if first color index is less than 0, there is no explicit stop at 0% so we return the first color in the gradient
    if (firstColorIndex < 0) {
        const [RgbColor] = gradient[0];
        return RgbColor;
    }
    // Get the two closest colors from gradient
    const [firstRgbColor] = gradient[firstColorIndex];
    const [secondRgbColor] = gradient[secondColorIndex];
    // Calculate ratio between the two closest colors
    const [, firstColorStop] = gradient[firstColorIndex];
    const [, secondColorStop] = gradient[secondColorIndex];
    const colorsRatio = pickColorStop(secondColorStop) - pickColorStop(firstColorStop);
    // Calculate ratio between the value and lowest color
    const valueRatio = selectedColorStop - pickColorStop(firstColorStop);
    // Calculate overall ratio
    const ratio = valueRatio / colorsRatio;
    const result = pickRgbColor(secondRgbColor, firstRgbColor, ratio);
    return result;
};
const pickRgbColor = (color1, color2, ratio) => {
    const weight = ratio * 2 - 1;
    const weightColor1 = (weight / 1 + 1) / 2;
    const weightColor2 = 1 - weightColor1;
    const rgb = [
        Math.round(color1[0] * weightColor1 + color2[0] * weightColor2),
        Math.round(color1[1] * weightColor1 + color2[1] * weightColor2),
        Math.round(color1[2] * weightColor1 + color2[2] * weightColor2),
    ];
    return rgb;
};
const getRecipeClassName = (variant, pattern) => COLOR_RECIPE_PATTERN[variant][pattern]['className'];
const getRecipeColor = (variant, pattern) => COLOR_RECIPE_PATTERN[variant][pattern]['color'];
const convertTextToNumber = (text) => Math.abs(text.split('').reduce((total, letter) => total + (letter.charCodeAt(0) - 97), 0));
const convertTextToColor = (text, colors) => {
    const colorId = convertTextToNumber(text) % colors.length;
    return colors[colorId];
};

const Height = {
    0: 'h-0',
    0.5: 'h-0.5',
    1: 'h-1',
    1.5: 'h-1.5',
    2: 'h-2',
    2.5: 'h-2.5',
    3: 'h-3',
    3.5: 'h-3.5',
    4: 'h-4',
    5: 'h-5',
    6: 'h-6',
    7: 'h-7',
    8: 'h-8',
    9: 'h-9',
    10: 'h-10',
    11: 'h-11',
    12: 'h-12',
    14: 'h-14',
    16: 'h-16',
    20: 'h-20',
    24: 'h-24',
    28: 'h-28',
    32: 'h-32',
    36: 'h-36',
    40: 'h-40',
    44: 'h-44',
    48: 'h-48',
    52: 'h-52',
    56: 'h-56',
    60: 'h-60',
    64: 'h-64',
    72: 'h-72',
    80: 'h-80',
    96: 'h-96',
    ['1/2']: 'h-1/2',
    ['1/3']: 'h-1/3',
    ['2/3']: 'h-2/3',
    ['1/4']: 'h-1/4',
    ['2/4']: 'h-2/4',
    ['3/4']: 'h-3/4',
    ['1/5']: 'h-1/5',
    ['2/5']: 'h-2/5',
    ['3/5']: 'h-3/5',
    ['4/5']: 'h-4/5',
    ['1/6']: 'h-1/6',
    ['2/6']: 'h-2/6',
    ['3/6']: 'h-3/6',
    ['4/6']: 'h-4/6',
    ['5/6']: 'h-5/6',
    full: 'h-full',
    screen: 'h-screen',
    min: 'h-min',
    max: 'h-max',
    fit: 'h-fit',
};
const getHeight = (height) => (height ? Height[height] : null);

const Width = {
    0: 'w-0',
    0.5: 'w-0.5',
    1: 'w-1',
    1.5: 'w-1.5',
    2: 'w-2',
    2.5: 'w-2.5',
    3: 'w-3',
    3.5: 'w-3.5',
    4: 'w-4',
    5: 'w-5',
    6: 'w-6',
    7: 'w-7',
    8: 'w-8',
    9: 'w-9',
    10: 'w-10',
    11: 'w-11',
    12: 'w-12',
    14: 'w-14',
    16: 'w-16',
    20: 'w-20',
    24: 'w-24',
    28: 'w-28',
    32: 'w-32',
    36: 'w-36',
    40: 'w-40',
    44: 'w-44',
    48: 'w-48',
    52: 'w-52',
    56: 'w-56',
    60: 'w-60',
    64: 'w-64',
    72: 'w-72',
    80: 'w-80',
    96: 'w-96',
    ['1/2']: 'w-1/2',
    ['1/3']: 'w-1/3',
    ['2/3']: 'w-2/3',
    ['1/4']: 'w-1/4',
    ['2/4']: 'w-2/4',
    ['3/4']: 'w-3/4',
    ['1/5']: 'w-1/5',
    ['2/5']: 'w-2/5',
    ['3/5']: 'w-3/5',
    ['4/5']: 'w-4/5',
    ['1/6']: 'w-1/6',
    ['2/6']: 'w-2/6',
    ['3/6']: 'w-3/6',
    ['4/6']: 'w-4/6',
    ['5/6']: 'w-5/6',
    full: 'w-full',
    screen: 'w-screen',
    min: 'w-min',
    max: 'w-max',
    fit: 'w-fit',
};
const getWidth = (width) => (width ? Width[width] : null);

// This normalize function is based on a linear scale
const normalizeValue = ({ max, min, scaleMaxValue = 100, value }) => ((value - min) / (max - min)) * scaleMaxValue;
// This normalize function is based on a cube root for positive numbers and third power for negative numbers
const normalizeValueCubicRootExponentialScale = ({ max, min, scaleMaxValue = 100, value, }) => {
    let mx;
    if (max < Math.abs(min)) {
        mx = Math.pow(value - min, 3) / Math.pow(max - min, 3);
    }
    else {
        mx = Math.cbrt(value - min) / Math.cbrt(max - min);
    }
    return mx * scaleMaxValue;
};
// This normalize function is based on a square root for positive numbers and second power for negative numbers
const normalizeValueSquareRootExponentialScale = ({ max, min, scaleMaxValue = 100, value, }) => {
    let mx;
    if (max < Math.abs(min)) {
        mx = Math.pow(value - min, 2) / Math.pow(max - min, 2);
    }
    else {
        mx = Math.sqrt(value - min) / Math.sqrt(max - min);
    }
    return mx * scaleMaxValue;
};

const GAP_XY = {
    0: 'gap-0',
    0.5: 'gap-0.5', // 0.125rem;
    1: 'gap-1', // 0.25rem;
    1.5: 'gap-1.5', // 0.375rem;
    2: 'gap-2', // 0.5rem;
    2.5: 'gap-2.5', // 0.625rem;
    3: 'gap-3', // 0.75rem;
    3.5: 'gap-3.5', // 0.875rem;
    4: 'gap-4', // 1rem;
    5: 'gap-5', // 1.25rem;
    6: 'gap-6', // 1.5rem;
    7: 'gap-7', // 1.75rem;
    8: 'gap-8', // 2rem;
    9: 'gap-9',
    10: 'gap-10',
    11: 'gap-11',
    12: 'gap-12',
    14: 'gap-14',
    16: 'gap-16',
    20: 'gap-20',
    24: 'gap-24',
    28: 'gap-28',
    32: 'gap-32',
    36: 'gap-36',
    40: 'gap-40',
    44: 'gap-44',
    48: 'gap-48',
    52: 'gap-52',
    56: 'gap-56',
    60: 'gap-60',
    64: 'gap-64',
    72: 'gap-72',
    80: 'gap-80',
    96: 'gap-96',
    px: 'gap-px',
};
const GAP_X = {
    0: 'gap-x-0',
    0.5: 'gap-x-0.5',
    1: 'gap-x-1',
    1.5: 'gap-x-1.5',
    2: 'gap-x-2',
    2.5: 'gap-x-2.5',
    3: 'gap-x-3',
    3.5: 'gap-x-3.5',
    4: 'gap-x-4',
    5: 'gap-x-5',
    6: 'gap-x-6',
    7: 'gap-x-7',
    8: 'gap-x-8',
    9: 'gap-x-9',
    10: 'gap-x-10',
    11: 'gap-x-11',
    12: 'gap-x-12',
    14: 'gap-x-14',
    16: 'gap-x-16',
    20: 'gap-x-20',
    24: 'gap-x-24',
    28: 'gap-x-28',
    32: 'gap-x-32',
    36: 'gap-x-36',
    40: 'gap-x-40',
    44: 'gap-x-44',
    48: 'gap-x-48',
    52: 'gap-x-52',
    56: 'gap-x-56',
    60: 'gap-x-60',
    64: 'gap-x-64',
    72: 'gap-x-72',
    80: 'gap-x-80',
    96: 'gap-x-96',
    px: 'gap-x-px',
};
const GAP_Y = {
    0: 'gap-y-0',
    0.5: 'gap-y-0.5',
    1: 'gap-y-1',
    1.5: 'gap-y-1.5',
    2: 'gap-y-2',
    2.5: 'gap-y-2.5',
    3: 'gap-y-3',
    3.5: 'gap-y-3.5',
    4: 'gap-y-4',
    5: 'gap-y-5',
    6: 'gap-y-6',
    7: 'gap-y-7',
    8: 'gap-y-8',
    9: 'gap-y-9',
    10: 'gap-y-10',
    11: 'gap-y-11',
    12: 'gap-y-12',
    14: 'gap-y-14',
    16: 'gap-y-16',
    20: 'gap-y-20',
    24: 'gap-y-24',
    28: 'gap-y-28',
    32: 'gap-y-32',
    36: 'gap-y-36',
    40: 'gap-y-40',
    44: 'gap-y-44',
    48: 'gap-y-48',
    52: 'gap-y-52',
    56: 'gap-y-56',
    60: 'gap-y-60',
    64: 'gap-y-64',
    72: 'gap-y-72',
    80: 'gap-y-80',
    96: 'gap-y-96',
    px: 'gap-y-px',
};
const getGapXY = (gap) => (gap != null ? GAP_XY[gap] : '');
const getGapX = (gap) => (gap != null ? GAP_X[gap] : '');
const getGapY = (gap) => (gap != null ? GAP_Y[gap] : '');

const SpaceX = {
    0: 'space-x-0',
    0.5: 'space-x-0.5',
    1: 'space-x-1',
    1.5: 'space-x-1.5',
    2: 'space-x-2',
    2.5: 'space-x-2.5',
    3: 'space-x-3',
    3.5: 'space-x-3.5',
    4: 'space-x-4',
    5: 'space-x-5',
    6: 'space-x-6',
    7: 'space-x-7',
    8: 'space-x-8',
    9: 'space-x-9',
    10: 'space-x-10',
    11: 'space-x-11',
    12: 'space-x-12',
    14: 'space-x-14',
    16: 'space-x-16',
    20: 'space-x-20',
    24: 'space-x-24',
    28: 'space-x-28',
    32: 'space-x-32',
    36: 'space-x-36',
    40: 'space-x-40',
    44: 'space-x-44',
    48: 'space-x-48',
    52: 'space-x-52',
    56: 'space-x-56',
    60: 'space-x-60',
    64: 'space-x-64',
    72: 'space-x-72',
    80: 'space-x-80',
    96: 'space-x-96',
    px: 'space-x-px',
    reverse: 'space-x-reverse',
};
const SpaceY = {
    0: 'space-y-0',
    0.5: 'space-y-0.5',
    1: 'space-y-1',
    1.5: 'space-y-1.5',
    2: 'space-y-2',
    2.5: 'space-y-2.5',
    3: 'space-y-3',
    3.5: 'space-y-3.5',
    4: 'space-y-4',
    5: 'space-y-5',
    6: 'space-y-6',
    7: 'space-y-7',
    8: 'space-y-8',
    9: 'space-y-9',
    10: 'space-y-10',
    11: 'space-y-11',
    12: 'space-y-12',
    14: 'space-y-14',
    16: 'space-y-16',
    20: 'space-y-20',
    24: 'space-y-24',
    28: 'space-y-28',
    32: 'space-y-32',
    36: 'space-y-36',
    40: 'space-y-40',
    44: 'space-y-44',
    48: 'space-y-48',
    52: 'space-y-52',
    56: 'space-y-56',
    60: 'space-y-60',
    64: 'space-y-64',
    72: 'space-y-72',
    80: 'space-y-80',
    96: 'space-y-96',
    px: 'space-y-px',
    reverse: 'space-y-reverse',
};
const getSpaceX = (space) => (space != null ? SpaceX[space] : '');
const getSpaceY = (space) => (space != null ? SpaceY[space] : '');

const PaddingX = {
    0: 'px-0',
    0.5: 'px-0.5', // 0.125rem
    1: 'px-1', // 0.25rem
    1.5: 'px-1.5', // 0.375rem
    2: 'px-2', // 0.5rem
    2.5: 'px-2.5', // 0.625rem
    3: 'px-3', // 0.75rem
    3.5: 'px-3.5', // 0.875rem
    4: 'px-4', // 1rem
    5: 'px-5', // 1.25rem
    6: 'px-6', // 1.5rem
    7: 'px-7', // 1.75rem
    8: 'px-8', // 2rem
    9: 'px-9', // 2.25rem
    10: 'px-10', // 2.5rem
    11: 'px-11', // 2.75rem
    12: 'px-12', // 3rem
    14: 'px-14',
    16: 'px-16',
    20: 'px-20',
    24: 'px-24',
    28: 'px-28',
    32: 'px-32',
    36: 'px-36',
    40: 'px-40',
    44: 'px-44',
    48: 'px-48',
    52: 'px-52',
    56: 'px-56',
    60: 'px-60',
    64: 'px-64',
    72: 'px-72',
    80: 'px-80',
    96: 'px-96',
    px: 'px-px',
};
const PaddingY = {
    0: 'py-0',
    0.5: 'py-0.5',
    1: 'py-1',
    1.5: 'py-1.5',
    2: 'py-2',
    2.5: 'py-2.5',
    3: 'py-3',
    3.5: 'py-3.5',
    4: 'py-4',
    5: 'py-5',
    6: 'py-6',
    7: 'py-7',
    8: 'py-8',
    9: 'py-9',
    10: 'py-10',
    11: 'py-11',
    12: 'py-12',
    14: 'py-14',
    16: 'py-16',
    20: 'py-20',
    24: 'py-24',
    28: 'py-28',
    32: 'py-32',
    36: 'py-36',
    40: 'py-40',
    44: 'py-44',
    48: 'py-48',
    52: 'py-52',
    56: 'py-56',
    60: 'py-60',
    64: 'py-64',
    72: 'py-72',
    80: 'py-80',
    96: 'py-96',
    px: 'py-px',
};
const getPaddingX = (padding) => (padding != null ? PaddingX[padding] : '');
const getPaddingY = (padding) => (padding != null ? PaddingY[padding] : '');

const BoxShadow = {
    sm: 'shadow-sm',
    base: 'shadow',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
    '2xl': 'shadow-2xl',
    inner: 'shadow-inner',
    none: 'shadow-none',
};
const getBoxShadow = (boxShadow) => BoxShadow[boxShadow];

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getAugmentedNamespace(n) {
  if (n.__esModule) return n;
  var f = n.default;
	if (typeof f == "function") {
		var a = function a () {
			if (this instanceof a) {
				var args = [null];
				args.push.apply(args, arguments);
				var Ctor = Function.bind.apply(f, args);
				return new Ctor();
			}
			return f.apply(this, arguments);
		};
		a.prototype = f.prototype;
  } else a = {};
  Object.defineProperty(a, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

var lib = {};

var constants$1 = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.MAX_SEARCH_FIREBASE_IDENTITIES_BY_NAME = exports.MAX_SEARCH_FIREBASE_IDENTITIES = exports.PER_CONTRIBUTOR_ALLOWED_ROUNDING_ERROR = exports.TEST_CAPTURED_EMAILS_LOG_PATH = exports.DB_ROUNDING_NUM_DECIMALS = exports.USER_ROUNDING_NUM_DECIMALS = exports.LAST_MONTH_DATE_DICT = exports.OLDEST_SUPPORTED_QRE_PYR_YEAR = exports.COMMON_CONSTANTS = exports.MAX_RD_PAYROLL_TAX_CREDIT_CENTS_BEFORE_2023 = exports.MAX_RD_PAYROLL_TAX_CREDIT_CENTS = exports.US_COUNTRY_CODE = void 0;
	const MS_IN_DAY = 1000 * 60 * 60 * 24;
	const MS_IN_YEAR = MS_IN_DAY * 365;
	const STATES = [
	    'AL',
	    'AK',
	    'AS',
	    'AZ',
	    'AR',
	    'CA',
	    'CO',
	    'CT',
	    'DE',
	    'DC',
	    'FM',
	    'FL',
	    'GA',
	    'GU',
	    'HI',
	    'ID',
	    'IL',
	    'IN',
	    'IA',
	    'KS',
	    'KY',
	    'LA',
	    'ME',
	    'MH',
	    'MD',
	    'MA',
	    'MI',
	    'MN',
	    'MS',
	    'MO',
	    'MT',
	    'NE',
	    'NV',
	    'NH',
	    'NJ',
	    'NM',
	    'NY',
	    'NC',
	    'ND',
	    'MP',
	    'OH',
	    'OK',
	    'OR',
	    'PW',
	    'PA',
	    'PR',
	    'RI',
	    'SC',
	    'SD',
	    'TN',
	    'TX',
	    'UT',
	    'VT',
	    'VI',
	    'VA',
	    'WA',
	    'WV',
	    'WI',
	    'WY',
	];
	exports.US_COUNTRY_CODE = 'US';
	exports.MAX_RD_PAYROLL_TAX_CREDIT_CENTS = 50000000;
	exports.MAX_RD_PAYROLL_TAX_CREDIT_CENTS_BEFORE_2023 = 25000000;
	exports.COMMON_CONSTANTS = {
	    SENTRY_ORG_SLUG: 'neo-tax',
	    NUMBER_OF_MONTHLY_SUBSCRIPTION_ITERATIONS: 12,
	    NUMBER_OF_QUARTERLY_SUBSCRIPTION_ITERATIONS: 4,
	    NUMBER_OF_SEMI_ANNUAL_SUBSCRIPTION_ITERATIONS: 2,
	    NUMBER_OF_ONE_TIME_SUBSCRIPTION_ITERATIONS: 1,
	    OUTDATED_VERSION_ERROR_NAME: 'OutdatedVersion',
	    MS_IN_DAY,
	    MS_IN_YEAR,
	    STATES,
	    MAX_RD_PAYROLL_TAX_CREDIT_CENTS: exports.MAX_RD_PAYROLL_TAX_CREDIT_CENTS,
	    MAX_RD_PAYROLL_TAX_CREDIT_CENTS_BEFORE_2023: exports.MAX_RD_PAYROLL_TAX_CREDIT_CENTS_BEFORE_2023,
	};
	exports.OLDEST_SUPPORTED_QRE_PYR_YEAR = 1994;
	exports.LAST_MONTH_DATE_DICT = {
	    '1': '31',
	    '2': '28',
	    '3': '31',
	    '4': '30',
	    '5': '31',
	    '6': '30',
	    '7': '31',
	    '8': '31',
	    '9': '30',
	    '10': '31',
	    '11': '30',
	    '12': '31',
	};
	// RED: if you change this value, then you must change the sum_qualifying_amount_cents_finalfunc function in the database
	/**
	 * One order of magnitude less than the DB rounding (see rd-expense-project.entity.ts), to allow for some rounding error when aggregating
	 *
	 * Example:
	 *
	 * ## Setup:
	 * - Project A
	 * - Project A has 3 months with ticket's with effort m1, m2, and m3
	 * - Assume we use simple averaging of the allocation percentage in those 3 months to get the period total allocation
	 * - Assume DB_ROUNDING_NUM_DECIMALS = 2 and USER_ROUNDING_NUM_DECIMALS = 0
	 *
	 * ## Scenario:
	 * 1. User sets the total period allocation to 100%
	 * 2. We convert that into a per-month allocation percent of 100/3, which rounded using DB_ROUNDING_NUM_DECIMALS is 33.33%
	 * 3. We then re-aggregate this per-month allocation percentage to get the total period allocation percentage of 99.99%,
	 *   but before displaying to the user, we round to USER_ROUNDING_NUM_DECIMALS = 0 to get 100%.
	 *
	 * This example shows how using a two layer rounding system can help minimize rounding errors when using transformations that
	 * have inherent rounding errors, such as division.
	 */
	exports.USER_ROUNDING_NUM_DECIMALS = 0;
	exports.DB_ROUNDING_NUM_DECIMALS = 8; // 2 sig. fig. less than PMAs
	// JSONL sink for emails sent in LOCAL/TEST envs. The server appends one line per
	// capturable email; Cypress tasks read the most recent matching line to drive flows
	// that depend on the link end-to-end (the invitee/verifier can't open a real email
	// in test). Both producer (server) and consumer (cypress.config.js) must agree on
	// this path, which is why it lives in @common rather than being duplicated.
	exports.TEST_CAPTURED_EMAILS_LOG_PATH = '/tmp/neo-tax-captured-emails.jsonl';
	// RED: if this logic changes, then make sure to update the sum_qualifying_amount_cents_finalfunc function in the database
	/**
	 * The maximum allowed rounding error per contributor when summing up the allocation percentages.
	 */
	exports.PER_CONTRIBUTOR_ALLOWED_ROUNDING_ERROR = 1 / 10 ** (exports.DB_ROUNDING_NUM_DECIMALS - 1);
	// How many matching addresses the Identity Platform search considers, and so the most rows it can return.
	// Shared because the caller pages the results itself and has to say when it is showing a truncated set —
	// a number kept in two places would eventually disagree about when that is.
	exports.MAX_SEARCH_FIREBASE_IDENTITIES = 500;
	// Small because producing it scores every account in the project — Identity Platform indexes no name.
	exports.MAX_SEARCH_FIREBASE_IDENTITIES_BY_NAME = 25;
} (constants$1));

var neoTaxFeeDetails = {};

Object.defineProperty(neoTaxFeeDetails, "__esModule", { value: true });
neoTaxFeeDetails.computeNeoTaxFeeDetails = void 0;
// Stripe has a minimum fee per iteration of $0.50 (https://stripe.com/docs/api/charges/object)
// Important: This only applies to the pre-discount amount.  You can discount down to zero and
// Stripe will still create the invoice and everything works.  We apply the minimum to avoid
// errors being thrown when we create the subscription in Stripe.
const STRIPE_MINIMUM_FEE_PER_ITERATION = 50;
const getIntendedTotalDiscountCents = (totalFeeCentsNoDiscount, amountOffCents, percentOff) => {
    if (amountOffCents && amountOffCents > 0) {
        return amountOffCents;
    }
    else if (percentOff && percentOff > 0) {
        // when applying the percentOff is a number between 1 and 100
        return Math.round(totalFeeCentsNoDiscount * percentOff * 0.01);
    }
    return 0;
};
const computeNeoTaxFeeDetails = ({ amountOffCents, creditAmountCents, feePercent, flatFeeCents, numberOfIterations, percentOff, }) => {
    const minimumTotalFeeCents = STRIPE_MINIMUM_FEE_PER_ITERATION * numberOfIterations;
    const totalFeeBeforeDiscountCents = Math.max(minimumTotalFeeCents, typeof flatFeeCents === 'number' ? flatFeeCents : Math.round(creditAmountCents * feePercent));
    // Discount before considering minimums
    const intendedTotalDiscountCents = getIntendedTotalDiscountCents(totalFeeBeforeDiscountCents, amountOffCents, percentOff);
    const totalFeeAfterDiscountCents = Math.max(0, totalFeeBeforeDiscountCents - intendedTotalDiscountCents);
    const totalDiscountCents = totalFeeBeforeDiscountCents - totalFeeAfterDiscountCents;
    const iterationFeeBeforeDiscountCents = Math.max(STRIPE_MINIMUM_FEE_PER_ITERATION, 
    // We use Math.floor, so that we don't overcharge the customer.
    // The sum of the iterations should not exceed the total fee.
    Math.floor(totalFeeBeforeDiscountCents / numberOfIterations));
    const iterationFeeAfterDiscountCents = Math.floor(totalFeeAfterDiscountCents / numberOfIterations);
    const iterationDiscountCents = iterationFeeBeforeDiscountCents - iterationFeeAfterDiscountCents;
    return {
        iterationFeeBeforeDiscountCents,
        iterationFeeAfterDiscountCents,
        iterationDiscountCents,
        totalFeeBeforeDiscountCents,
        totalFeeAfterDiscountCents,
        totalDiscountCents,
    };
};
neoTaxFeeDetails.computeNeoTaxFeeDetails = computeNeoTaxFeeDetails;

var date_utils = {};

var dayjs_minExports = {};
var dayjs_min = {
  get exports(){ return dayjs_minExports; },
  set exports(v){ dayjs_minExports = v; },
};

(function (module, exports) {
	!function(t,e){module.exports=e();}(commonjsGlobal,(function(){var t=1e3,e=6e4,n=36e5,r="millisecond",i="second",s="minute",u="hour",a="day",o="week",f="month",h="quarter",c="year",d="date",$="Invalid Date",l=/^(\d{4})[-/]?(\d{1,2})?[-/]?(\d{0,2})[Tt\s]*(\d{1,2})?:?(\d{1,2})?:?(\d{1,2})?[.:]?(\d+)?$/,y=/\[([^\]]+)]|Y{1,4}|M{1,4}|D{1,2}|d{1,4}|H{1,2}|h{1,2}|a|A|m{1,2}|s{1,2}|Z{1,2}|SSS/g,M={name:"en",weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_")},m=function(t,e,n){var r=String(t);return !r||r.length>=e?t:""+Array(e+1-r.length).join(n)+t},g={s:m,z:function(t){var e=-t.utcOffset(),n=Math.abs(e),r=Math.floor(n/60),i=n%60;return (e<=0?"+":"-")+m(r,2,"0")+":"+m(i,2,"0")},m:function t(e,n){if(e.date()<n.date())return -t(n,e);var r=12*(n.year()-e.year())+(n.month()-e.month()),i=e.clone().add(r,f),s=n-i<0,u=e.clone().add(r+(s?-1:1),f);return +(-(r+(n-i)/(s?i-u:u-i))||0)},a:function(t){return t<0?Math.ceil(t)||0:Math.floor(t)},p:function(t){return {M:f,y:c,w:o,d:a,D:d,h:u,m:s,s:i,ms:r,Q:h}[t]||String(t||"").toLowerCase().replace(/s$/,"")},u:function(t){return void 0===t}},v="en",D={};D[v]=M;var p=function(t){return t instanceof _},S=function t(e,n,r){var i;if(!e)return v;if("string"==typeof e){var s=e.toLowerCase();D[s]&&(i=s),n&&(D[s]=n,i=s);var u=e.split("-");if(!i&&u.length>1)return t(u[0])}else {var a=e.name;D[a]=e,i=a;}return !r&&i&&(v=i),i||!r&&v},w=function(t,e){if(p(t))return t.clone();var n="object"==typeof e?e:{};return n.date=t,n.args=arguments,new _(n)},O=g;O.l=S,O.i=p,O.w=function(t,e){return w(t,{locale:e.$L,utc:e.$u,x:e.$x,$offset:e.$offset})};var _=function(){function M(t){this.$L=S(t.locale,null,!0),this.parse(t);}var m=M.prototype;return m.parse=function(t){this.$d=function(t){var e=t.date,n=t.utc;if(null===e)return new Date(NaN);if(O.u(e))return new Date;if(e instanceof Date)return new Date(e);if("string"==typeof e&&!/Z$/i.test(e)){var r=e.match(l);if(r){var i=r[2]-1||0,s=(r[7]||"0").substring(0,3);return n?new Date(Date.UTC(r[1],i,r[3]||1,r[4]||0,r[5]||0,r[6]||0,s)):new Date(r[1],i,r[3]||1,r[4]||0,r[5]||0,r[6]||0,s)}}return new Date(e)}(t),this.$x=t.x||{},this.init();},m.init=function(){var t=this.$d;this.$y=t.getFullYear(),this.$M=t.getMonth(),this.$D=t.getDate(),this.$W=t.getDay(),this.$H=t.getHours(),this.$m=t.getMinutes(),this.$s=t.getSeconds(),this.$ms=t.getMilliseconds();},m.$utils=function(){return O},m.isValid=function(){return !(this.$d.toString()===$)},m.isSame=function(t,e){var n=w(t);return this.startOf(e)<=n&&n<=this.endOf(e)},m.isAfter=function(t,e){return w(t)<this.startOf(e)},m.isBefore=function(t,e){return this.endOf(e)<w(t)},m.$g=function(t,e,n){return O.u(t)?this[e]:this.set(n,t)},m.unix=function(){return Math.floor(this.valueOf()/1e3)},m.valueOf=function(){return this.$d.getTime()},m.startOf=function(t,e){var n=this,r=!!O.u(e)||e,h=O.p(t),$=function(t,e){var i=O.w(n.$u?Date.UTC(n.$y,e,t):new Date(n.$y,e,t),n);return r?i:i.endOf(a)},l=function(t,e){return O.w(n.toDate()[t].apply(n.toDate("s"),(r?[0,0,0,0]:[23,59,59,999]).slice(e)),n)},y=this.$W,M=this.$M,m=this.$D,g="set"+(this.$u?"UTC":"");switch(h){case c:return r?$(1,0):$(31,11);case f:return r?$(1,M):$(0,M+1);case o:var v=this.$locale().weekStart||0,D=(y<v?y+7:y)-v;return $(r?m-D:m+(6-D),M);case a:case d:return l(g+"Hours",0);case u:return l(g+"Minutes",1);case s:return l(g+"Seconds",2);case i:return l(g+"Milliseconds",3);default:return this.clone()}},m.endOf=function(t){return this.startOf(t,!1)},m.$set=function(t,e){var n,o=O.p(t),h="set"+(this.$u?"UTC":""),$=(n={},n[a]=h+"Date",n[d]=h+"Date",n[f]=h+"Month",n[c]=h+"FullYear",n[u]=h+"Hours",n[s]=h+"Minutes",n[i]=h+"Seconds",n[r]=h+"Milliseconds",n)[o],l=o===a?this.$D+(e-this.$W):e;if(o===f||o===c){var y=this.clone().set(d,1);y.$d[$](l),y.init(),this.$d=y.set(d,Math.min(this.$D,y.daysInMonth())).$d;}else $&&this.$d[$](l);return this.init(),this},m.set=function(t,e){return this.clone().$set(t,e)},m.get=function(t){return this[O.p(t)]()},m.add=function(r,h){var d,$=this;r=Number(r);var l=O.p(h),y=function(t){var e=w($);return O.w(e.date(e.date()+Math.round(t*r)),$)};if(l===f)return this.set(f,this.$M+r);if(l===c)return this.set(c,this.$y+r);if(l===a)return y(1);if(l===o)return y(7);var M=(d={},d[s]=e,d[u]=n,d[i]=t,d)[l]||1,m=this.$d.getTime()+r*M;return O.w(m,this)},m.subtract=function(t,e){return this.add(-1*t,e)},m.format=function(t){var e=this,n=this.$locale();if(!this.isValid())return n.invalidDate||$;var r=t||"YYYY-MM-DDTHH:mm:ssZ",i=O.z(this),s=this.$H,u=this.$m,a=this.$M,o=n.weekdays,f=n.months,h=function(t,n,i,s){return t&&(t[n]||t(e,r))||i[n].slice(0,s)},c=function(t){return O.s(s%12||12,t,"0")},d=n.meridiem||function(t,e,n){var r=t<12?"AM":"PM";return n?r.toLowerCase():r},l={YY:String(this.$y).slice(-2),YYYY:this.$y,M:a+1,MM:O.s(a+1,2,"0"),MMM:h(n.monthsShort,a,f,3),MMMM:h(f,a),D:this.$D,DD:O.s(this.$D,2,"0"),d:String(this.$W),dd:h(n.weekdaysMin,this.$W,o,2),ddd:h(n.weekdaysShort,this.$W,o,3),dddd:o[this.$W],H:String(s),HH:O.s(s,2,"0"),h:c(1),hh:c(2),a:d(s,u,!0),A:d(s,u,!1),m:String(u),mm:O.s(u,2,"0"),s:String(this.$s),ss:O.s(this.$s,2,"0"),SSS:O.s(this.$ms,3,"0"),Z:i};return r.replace(y,(function(t,e){return e||l[t]||i.replace(":","")}))},m.utcOffset=function(){return 15*-Math.round(this.$d.getTimezoneOffset()/15)},m.diff=function(r,d,$){var l,y=O.p(d),M=w(r),m=(M.utcOffset()-this.utcOffset())*e,g=this-M,v=O.m(this,M);return v=(l={},l[c]=v/12,l[f]=v,l[h]=v/3,l[o]=(g-m)/6048e5,l[a]=(g-m)/864e5,l[u]=g/n,l[s]=g/e,l[i]=g/t,l)[y]||g,$?v:O.a(v)},m.daysInMonth=function(){return this.endOf(f).$D},m.$locale=function(){return D[this.$L]},m.locale=function(t,e){if(!t)return this.$L;var n=this.clone(),r=S(t,e,!0);return r&&(n.$L=r),n},m.clone=function(){return O.w(this.$d,this)},m.toDate=function(){return new Date(this.valueOf())},m.toJSON=function(){return this.isValid()?this.toISOString():null},m.toISOString=function(){return this.$d.toISOString()},m.toString=function(){return this.$d.toUTCString()},M}(),T=_.prototype;return w.prototype=T,[["$ms",r],["$s",i],["$m",s],["$H",u],["$W",a],["$M",f],["$y",c],["$D",d]].forEach((function(t){T[t[1]]=function(e){return this.$g(e,t[0],t[1])};})),w.extend=function(t,e){return t.$i||(t(e,_,w),t.$i=!0),w},w.locale=S,w.isDayjs=p,w.unix=function(t){return w(1e3*t)},w.en=D[v],w.Ls=D,w.p={},w}));
} (dayjs_min));

var dayjs = dayjs_minExports;

var customParseFormatExports = {};
var customParseFormat = {
  get exports(){ return customParseFormatExports; },
  set exports(v){ customParseFormatExports = v; },
};

(function (module, exports) {
	!function(e,t){module.exports=t();}(commonjsGlobal,(function(){var e={LTS:"h:mm:ss A",LT:"h:mm A",L:"MM/DD/YYYY",LL:"MMMM D, YYYY",LLL:"MMMM D, YYYY h:mm A",LLLL:"dddd, MMMM D, YYYY h:mm A"},t=/(\[[^[]*\])|([-_:/.,()\s]+)|(A|a|YYYY|YY?|MM?M?M?|Do|DD?|hh?|HH?|mm?|ss?|S{1,3}|z|ZZ?)/g,n=/\d\d/,r=/\d\d?/,i=/\d*[^-_:/,()\s\d]+/,o={},s=function(e){return (e=+e)+(e>68?1900:2e3)};var a=function(e){return function(t){this[e]=+t;}},f=[/[+-]\d\d:?(\d\d)?|Z/,function(e){(this.zone||(this.zone={})).offset=function(e){if(!e)return 0;if("Z"===e)return 0;var t=e.match(/([+-]|\d\d)/g),n=60*t[1]+(+t[2]||0);return 0===n?0:"+"===t[0]?-n:n}(e);}],h=function(e){var t=o[e];return t&&(t.indexOf?t:t.s.concat(t.f))},u=function(e,t){var n,r=o.meridiem;if(r){for(var i=1;i<=24;i+=1)if(e.indexOf(r(i,0,t))>-1){n=i>12;break}}else n=e===(t?"pm":"PM");return n},d={A:[i,function(e){this.afternoon=u(e,!1);}],a:[i,function(e){this.afternoon=u(e,!0);}],S:[/\d/,function(e){this.milliseconds=100*+e;}],SS:[n,function(e){this.milliseconds=10*+e;}],SSS:[/\d{3}/,function(e){this.milliseconds=+e;}],s:[r,a("seconds")],ss:[r,a("seconds")],m:[r,a("minutes")],mm:[r,a("minutes")],H:[r,a("hours")],h:[r,a("hours")],HH:[r,a("hours")],hh:[r,a("hours")],D:[r,a("day")],DD:[n,a("day")],Do:[i,function(e){var t=o.ordinal,n=e.match(/\d+/);if(this.day=n[0],t)for(var r=1;r<=31;r+=1)t(r).replace(/\[|\]/g,"")===e&&(this.day=r);}],M:[r,a("month")],MM:[n,a("month")],MMM:[i,function(e){var t=h("months"),n=(h("monthsShort")||t.map((function(e){return e.slice(0,3)}))).indexOf(e)+1;if(n<1)throw new Error;this.month=n%12||n;}],MMMM:[i,function(e){var t=h("months").indexOf(e)+1;if(t<1)throw new Error;this.month=t%12||t;}],Y:[/[+-]?\d+/,a("year")],YY:[n,function(e){this.year=s(e);}],YYYY:[/\d{4}/,a("year")],Z:f,ZZ:f};function c(n){var r,i;r=n,i=o&&o.formats;for(var s=(n=r.replace(/(\[[^\]]+])|(LTS?|l{1,4}|L{1,4})/g,(function(t,n,r){var o=r&&r.toUpperCase();return n||i[r]||e[r]||i[o].replace(/(\[[^\]]+])|(MMMM|MM|DD|dddd)/g,(function(e,t,n){return t||n.slice(1)}))}))).match(t),a=s.length,f=0;f<a;f+=1){var h=s[f],u=d[h],c=u&&u[0],l=u&&u[1];s[f]=l?{regex:c,parser:l}:h.replace(/^\[|\]$/g,"");}return function(e){for(var t={},n=0,r=0;n<a;n+=1){var i=s[n];if("string"==typeof i)r+=i.length;else {var o=i.regex,f=i.parser,h=e.slice(r),u=o.exec(h)[0];f.call(t,u),e=e.replace(u,"");}}return function(e){var t=e.afternoon;if(void 0!==t){var n=e.hours;t?n<12&&(e.hours+=12):12===n&&(e.hours=0),delete e.afternoon;}}(t),t}}return function(e,t,n){n.p.customParseFormat=!0,e&&e.parseTwoDigitYear&&(s=e.parseTwoDigitYear);var r=t.prototype,i=r.parse;r.parse=function(e){var t=e.date,r=e.utc,s=e.args;this.$u=r;var a=s[1];if("string"==typeof a){var f=!0===s[2],h=!0===s[3],u=f||h,d=s[2];h&&(d=s[2]),o=this.$locale(),!f&&d&&(o=n.Ls[d]),this.$d=function(e,t,n){try{if(["x","X"].indexOf(t)>-1)return new Date(("X"===t?1e3:1)*e);var r=c(t)(e),i=r.year,o=r.month,s=r.day,a=r.hours,f=r.minutes,h=r.seconds,u=r.milliseconds,d=r.zone,l=new Date,m=s||(i||o?1:l.getDate()),M=i||l.getFullYear(),Y=0;i&&!o||(Y=o>0?o-1:l.getMonth());var p=a||0,v=f||0,D=h||0,g=u||0;return d?new Date(Date.UTC(M,Y,m,p,v,D,g+60*d.offset*1e3)):n?new Date(Date.UTC(M,Y,m,p,v,D,g)):new Date(M,Y,m,p,v,D,g)}catch(e){return new Date("")}}(t,a,r),this.init(),d&&!0!==d&&(this.$L=this.locale(d).$L),u&&t!=this.format(a)&&(this.$d=new Date("")),o={};}else if(a instanceof Array)for(var l=a.length,m=1;m<=l;m+=1){s[1]=a[m-1];var M=n.apply(this,s);if(M.isValid()){this.$d=M.$d,this.$L=M.$L,this.init();break}m===l&&(this.$d=new Date(""));}else i.call(this,e);};}}));
} (customParseFormat));

var isLeapYearExports = {};
var isLeapYear = {
  get exports(){ return isLeapYearExports; },
  set exports(v){ isLeapYearExports = v; },
};

(function (module, exports) {
	!function(e,t){module.exports=t();}(commonjsGlobal,(function(){return function(e,t){t.prototype.isLeapYear=function(){return this.$y%4==0&&this.$y%100!=0||this.$y%400==0};}}));
} (isLeapYear));

var utcExports = {};
var utc = {
  get exports(){ return utcExports; },
  set exports(v){ utcExports = v; },
};

(function (module, exports) {
	!function(t,i){module.exports=i();}(commonjsGlobal,(function(){var t="minute",i=/[+-]\d\d(?::?\d\d)?/g,e=/([+-]|\d\d)/g;return function(s,f,n){var u=f.prototype;n.utc=function(t){var i={date:t,utc:!0,args:arguments};return new f(i)},u.utc=function(i){var e=n(this.toDate(),{locale:this.$L,utc:!0});return i?e.add(this.utcOffset(),t):e},u.local=function(){return n(this.toDate(),{locale:this.$L,utc:!1})};var o=u.parse;u.parse=function(t){t.utc&&(this.$u=!0),this.$utils().u(t.$offset)||(this.$offset=t.$offset),o.call(this,t);};var r=u.init;u.init=function(){if(this.$u){var t=this.$d;this.$y=t.getUTCFullYear(),this.$M=t.getUTCMonth(),this.$D=t.getUTCDate(),this.$W=t.getUTCDay(),this.$H=t.getUTCHours(),this.$m=t.getUTCMinutes(),this.$s=t.getUTCSeconds(),this.$ms=t.getUTCMilliseconds();}else r.call(this);};var a=u.utcOffset;u.utcOffset=function(s,f){var n=this.$utils().u;if(n(s))return this.$u?0:n(this.$offset)?a.call(this):this.$offset;if("string"==typeof s&&(s=function(t){void 0===t&&(t="");var s=t.match(i);if(!s)return null;var f=(""+s[0]).match(e)||["-",0,0],n=f[0],u=60*+f[1]+ +f[2];return 0===u?0:"+"===n?u:-u}(s),null===s))return this;var u=Math.abs(s)<=16?60*s:s,o=this;if(f)return o.$offset=u,o.$u=0===s,o;if(0!==s){var r=this.$u?this.toDate().getTimezoneOffset():-1*this.utcOffset();(o=this.local().add(u+r,t)).$offset=u,o.$x.$localOffset=r;}else o=this.utc();return o};var h=u.format;u.format=function(t){var i=t||(this.$u?"YYYY-MM-DDTHH:mm:ss[Z]":"");return h.call(this,i)},u.valueOf=function(){var t=this.$utils().u(this.$offset)?0:this.$offset+(this.$x.$localOffset||this.$d.getTimezoneOffset());return this.$d.valueOf()-6e4*t},u.isUTC=function(){return !!this.$u},u.toISOString=function(){return this.toDate().toISOString()},u.toString=function(){return this.toDate().toUTCString()};var l=u.toDate;u.toDate=function(t){return "s"===t&&this.$offset?n(this.format("YYYY-MM-DD HH:mm:ss:SSS")).toDate():l.call(this)};var c=u.diff;u.diff=function(t,i,e){if(t&&this.$u===t.$u)return c.call(this,t,i,e);var s=this.local(),f=n(t).local();return c.call(s,f,i,e)};}}));
} (utc));

var enums = {};

var analyticIdentifier_enum = {};

Object.defineProperty(analyticIdentifier_enum, "__esModule", { value: true });
analyticIdentifier_enum.AnalyticIdentifier = void 0;
/**
 * Identifier format - <PROJECT_NAME>_<PROJECT_SECTION>_<CONTEXT>
 * Acronyms
 * - RTC = RD_TAX_CREDIT
 * - RD_CAP = R&D CAPITALIZATION
 * - ELIG = ELIGIBILITY
 */
// * DON'T CHANGE NUMBERS WHEN ADDING/REMOVING NEW IDS *
// TODO the term "Gaap" when it refers to projects is deprecated, we should change the enum names accordingly
var AnalyticIdentifier;
(function (AnalyticIdentifier) {
    AnalyticIdentifier[AnalyticIdentifier["HEADER_LOGOUT"] = 1] = "HEADER_LOGOUT";
    AnalyticIdentifier[AnalyticIdentifier["HEADER_CALL_US"] = 2] = "HEADER_CALL_US";
    AnalyticIdentifier[AnalyticIdentifier["HEADER_SCHEDULE_CALL"] = 3] = "HEADER_SCHEDULE_CALL";
    AnalyticIdentifier[AnalyticIdentifier["MOBILE_HEADER_LOGOUT"] = 4] = "MOBILE_HEADER_LOGOUT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_LEGAL_NAME"] = 5] = "RTC_PREP_DETAILS_LEGAL_NAME";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_SHORT_NAME"] = 6] = "RTC_PREP_DETAILS_SHORT_NAME";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_ADDRESS1"] = 7] = "RTC_PREP_DETAILS_ADDRESS1";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_ADDRESS2"] = 8] = "RTC_PREP_DETAILS_ADDRESS2";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_CITY"] = 9] = "RTC_PREP_DETAILS_CITY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_STATE"] = 10] = "RTC_PREP_DETAILS_STATE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_ZIPCODE"] = 11] = "RTC_PREP_DETAILS_ZIPCODE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_ELEVATOR_PITCH"] = 12] = "RTC_PREP_DETAILS_ELEVATOR_PITCH";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_PARTNERSHIP"] = 13] = "RTC_OTHER_TAX_FACTORS_PARTNERSHIP";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_PRIOR_INC_DATE"] = 14] = "RTC_OTHER_TAX_FACTORS_PRIOR_INC_DATE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_INC_DATE"] = 15] = "RTC_OTHER_TAX_FACTORS_INC_DATE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_EIN"] = 16] = "RTC_PREP_DETAILS_EIN";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_PAYROLL_PROVIDERS"] = 17] = "RTC_PREP_DETAILS_PAYROLL_PROVIDERS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_HAS_PAYROLL_EIN"] = 18] = "RTC_PREP_DETAILS_HAS_PAYROLL_EIN";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_PAYROLL_EIN"] = 19] = "RTC_PREP_DETAILS_PAYROLL_EIN";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_HAS_W2_EMPLOYEES"] = 20] = "RTC_PREP_DETAILS_HAS_W2_EMPLOYEES";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_BOOKS_FINALIZED"] = 21] = "RTC_PREP_DETAILS_BOOKS_FINALIZED";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_HAS_ACCOUNTANT"] = 22] = "RTC_PREP_DETAILS_HAS_ACCOUNTANT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_ACCOUNTANT_NAME"] = 23] = "RTC_PREP_DETAILS_ACCOUNTANT_NAME";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_ACCOUNTANT_EMAIL"] = 24] = "RTC_PREP_DETAILS_ACCOUNTANT_EMAIL";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_HAS_CLAIMED_ERC"] = 25] = "RTC_PREP_DETAILS_HAS_CLAIMED_ERC";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_HAS_FUNDED_RESEARCH"] = 26] = "RTC_PREP_DETAILS_HAS_FUNDED_RESEARCH";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_FUNDING_TYPES"] = 27] = "RTC_PREP_DETAILS_FUNDING_TYPES";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_HAS_MADE_PAYMENTS"] = 28] = "RTC_PREP_DETAILS_HAS_MADE_PAYMENTS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_RESEARCH"] = 29] = "RTC_PREP_DETAILS_RESEARCH";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_PATENTS"] = 30] = "RTC_PREP_DETAILS_PATENTS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_PLANNING_TOOLS"] = 31] = "RTC_PREP_DETAILS_PLANNING_TOOLS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_DESIGN_TOOLS"] = 32] = "RTC_PREP_DETAILS_DESIGN_TOOLS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_IMPLEMENTATION_TOOLS"] = 33] = "RTC_PREP_DETAILS_IMPLEMENTATION_TOOLS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_OTHER_TOOLS"] = 34] = "RTC_PREP_DETAILS_OTHER_TOOLS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_HAS_MADE_REVENUE"] = 35] = "RTC_OTHER_TAX_FACTORS_HAS_MADE_REVENUE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_PYRS"] = 37] = "RTC_OTHER_TAX_FACTORS_PYRS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_HAS_FILED_RTC"] = 38] = "RTC_OTHER_TAX_FACTORS_HAS_FILED_RTC";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_PYQRES"] = 40] = "RTC_OTHER_TAX_FACTORS_PYQRES";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_TAX_INCOME"] = 41] = "RTC_PREP_DETAILS_TAX_INCOME";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_IS_PROFITABLE"] = 42] = "RTC_PREP_DETAILS_IS_PROFITABLE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_TAX_INCOME_AFTER_DEDUCT"] = 43] = "RTC_PREP_DETAILS_TAX_INCOME_AFTER_DEDUCT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_ADD_MANUALLY"] = 44] = "RTC_EXPENSES_ADD_MANUALLY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_SUBMIT_MANUALLY"] = 45] = "RTC_EXPENSES_SUBMIT_MANUALLY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_LINK_ACCOUNT"] = 46] = "RTC_EXPENSES_LINK_ACCOUNT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_SEARCH_QB"] = 47] = "RTC_EXPENSES_SEARCH_QB";
    AnalyticIdentifier[AnalyticIdentifier["LINK_GUSTO"] = 48] = "LINK_GUSTO";
    AnalyticIdentifier[AnalyticIdentifier["LINK_JUSTWORKS"] = 49] = "LINK_JUSTWORKS";
    AnalyticIdentifier[AnalyticIdentifier["LINK_RIPPLING"] = 50] = "LINK_RIPPLING";
    AnalyticIdentifier[AnalyticIdentifier["LINK_TRINET"] = 51] = "LINK_TRINET";
    AnalyticIdentifier[AnalyticIdentifier["LINK_ZENEFITS"] = 52] = "LINK_ZENEFITS";
    AnalyticIdentifier[AnalyticIdentifier["LINK_QUICKBOOKS"] = 53] = "LINK_QUICKBOOKS";
    AnalyticIdentifier[AnalyticIdentifier["LINK_QUICKBOOKS_PAYROLL"] = 54] = "LINK_QUICKBOOKS_PAYROLL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_ADP_RUN"] = 55] = "LINK_ADP_RUN";
    AnalyticIdentifier[AnalyticIdentifier["LINK_ADP_WORKFORCE"] = 56] = "LINK_ADP_WORKFORCE";
    AnalyticIdentifier[AnalyticIdentifier["LINK_PAYCHEX"] = 57] = "LINK_PAYCHEX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_MANUAL_UPLOAD"] = 58] = "RTC_EXPENSES_MANUAL_UPLOAD";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_UPLOAD_SUPPLIES"] = 59] = "RTC_EXPENSES_UPLOAD_SUPPLIES";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_ROLE_GUIDANCE"] = 60] = "RTC_EXPENSES_ROLE_GUIDANCE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_RIPPLING_HELP"] = 61] = "RTC_EXPENSES_RIPPLING_HELP";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_EMPLOYEES_NEXT"] = 62] = "RTC_EXPENSES_EMPLOYEES_NEXT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_CONTRACTORS_NEXT"] = 63] = "RTC_EXPENSES_CONTRACTORS_NEXT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_CLOUD_NEXT"] = 64] = "RTC_EXPENSES_CLOUD_NEXT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_LEGAL_NEXT"] = 65] = "RTC_EXPENSES_LEGAL_NEXT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_EMPLOYEES_DONE"] = 66] = "RTC_EXPENSES_EMPLOYEES_DONE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_CONTRACTORS_DONE"] = 67] = "RTC_EXPENSES_CONTRACTORS_DONE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_CLOUD_DONE"] = 68] = "RTC_EXPENSES_CLOUD_DONE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_LEGAL_DONE"] = 69] = "RTC_EXPENSES_LEGAL_DONE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES_SUPPLIES_DONE"] = 70] = "RTC_EXPENSES_SUPPLIES_DONE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_BILLING_CHOOSE_PAYMENT"] = 71] = "RTC_BILLING_CHOOSE_PAYMENT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_BILLING_CHANGE_PAYMENT"] = 72] = "RTC_BILLING_CHANGE_PAYMENT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_BILLING_SAVE_FIRST_PAYMENT"] = 73] = "RTC_BILLING_SAVE_FIRST_PAYMENT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_BILLING_SAVE_NEW_PAYMENT"] = 74] = "RTC_BILLING_SAVE_NEW_PAYMENT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_BILLING_ACCEPT_AND_CONTINUE"] = 75] = "RTC_BILLING_ACCEPT_AND_CONTINUE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_WEBSITE_URL"] = 76] = "RTC_ELIG_WEBSITE_URL";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HAS_FILED_TAX_RETURN"] = 77] = "RTC_ELIG_HAS_FILED_TAX_RETURN";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HOW_ARE_YOU_TAXED"] = 78] = "RTC_ELIG_HOW_ARE_YOU_TAXED";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_INC_YEAR"] = 79] = "RTC_ELIG_INC_YEAR";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_PARTNERSHIP"] = 80] = "RTC_ELIG_PARTNERSHIP";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_PRIOR_INC_YEAR"] = 81] = "RTC_ELIG_PRIOR_INC_YEAR";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_FISCAL_YEAR_ENDS_DEC"] = 82] = "RTC_ELIG_FISCAL_YEAR_ENDS_DEC";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HAS_TECHNICAL_PROJECTS"] = 84] = "RTC_ELIG_HAS_TECHNICAL_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HAS_US_WORK"] = 85] = "RTC_ELIG_HAS_US_WORK";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HAS_MADE_REVENUE"] = 86] = "RTC_ELIG_HAS_MADE_REVENUE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_FIRST_YEAR_REVENUE"] = 87] = "RTC_ELIG_FIRST_YEAR_REVENUE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HAS_MADE_FIVE_MIL"] = 88] = "RTC_ELIG_HAS_MADE_FIVE_MIL";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HAS_FILED_RTC"] = 89] = "RTC_ELIG_HAS_FILED_RTC";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_FIRST_YEAR_FILED_RTC"] = 90] = "RTC_ELIG_FIRST_YEAR_FILED_RTC";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HAS_ACQUIRED_ENTITY"] = 91] = "RTC_ELIG_HAS_ACQUIRED_ENTITY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HAS_SHARED_OWNERSHIP"] = 92] = "RTC_ELIG_HAS_SHARED_OWNERSHIP";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_SIGN_UP"] = 93] = "RTC_ELIG_SIGN_UP";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_SIGN_UP_AS_ACCOUNTANT"] = 94] = "RTC_ELIG_SIGN_UP_AS_ACCOUNTANT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_REVIEW_DOWNLOAD_PDF"] = 95] = "RTC_REVIEW_DOWNLOAD_PDF";
    AnalyticIdentifier[AnalyticIdentifier["SHEETS_TRIAL_BALANCE_ROW_SIZE_BUTTON"] = 96] = "SHEETS_TRIAL_BALANCE_ROW_SIZE_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_RESET_LOGIN_BUTTON"] = 97] = "RTC_RESET_LOGIN_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_LOGIN_BUTTON"] = 98] = "RTC_LOGIN_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIGNUP_BUTTON"] = 99] = "RTC_SIGNUP_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_GOOGLE_OAUTH_BUTTON"] = 100] = "RTC_GOOGLE_OAUTH_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_INTUIT_OAUTH_BUTTON"] = 101] = "RTC_INTUIT_OAUTH_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SWITCH_LOGIN_METHOD_BUTTON"] = 102] = "RTC_SWITCH_LOGIN_METHOD_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["SHEETS_BOOK_FIN_TRIAL_BALANCE_TAB"] = 103] = "SHEETS_BOOK_FIN_TRIAL_BALANCE_TAB";
    AnalyticIdentifier[AnalyticIdentifier["SHEETS_BOOK_FIN_BALANCE_SHEET_TAB"] = 104] = "SHEETS_BOOK_FIN_BALANCE_SHEET_TAB";
    AnalyticIdentifier[AnalyticIdentifier["SHEETS_BOOK_FIN_PROFIT_AND_LOSS_TAB"] = 105] = "SHEETS_BOOK_FIN_PROFIT_AND_LOSS_TAB";
    AnalyticIdentifier[AnalyticIdentifier["SHEETS_BOOK_FIN_BOOK_TAX_DIFF_TAB"] = 106] = "SHEETS_BOOK_FIN_BOOK_TAX_DIFF_TAB";
    AnalyticIdentifier[AnalyticIdentifier["RD_CAP_REVENUE_INPUT"] = 107] = "RD_CAP_REVENUE_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RD_CAP_EXPENSES_INPUT"] = 108] = "RD_CAP_EXPENSES_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RD_CAP_RD_PERCENTAGE_INPUT"] = 109] = "RD_CAP_RD_PERCENTAGE_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RD_CAP_DOMESTIC_RD_PERCENTAGE_INPUT"] = 110] = "RD_CAP_DOMESTIC_RD_PERCENTAGE_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RD_CAP_QRE_PERCENTAGE_INPUT"] = 111] = "RD_CAP_QRE_PERCENTAGE_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RD_CAP_OPTIMIZE_BUTTON"] = 112] = "RD_CAP_OPTIMIZE_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RD_CAP_LINK_QUICKBOOKS"] = 113] = "RD_CAP_LINK_QUICKBOOKS";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_EMAIL_INPUT"] = 114] = "ONBOARDING_EMAIL_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_PASSWORD_INPUT"] = 115] = "ONBOARDING_PASSWORD_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_FULL_NAME_INPUT"] = 116] = "ONBOARDING_FULL_NAME_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_PASSWORD_CONFIRMATION_INPUT"] = 117] = "ONBOARDING_PASSWORD_CONFIRMATION_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_COMPANY_NAME_INPUT"] = 119] = "ONBOARDING_COMPANY_NAME_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_CONNECT_ACCOUNT"] = 121] = "RTC_SIDE_NAV_CONNECT_ACCOUNT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_FOREIGN_RD"] = 122] = "RTC_SIDE_NAV_FOREIGN_RD";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_DOMESTIC_RD_EMPLOYEE"] = 123] = "RTC_SIDE_NAV_DOMESTIC_RD_EMPLOYEE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_DOMESTIC_RD_CONTRACTOR"] = 124] = "RTC_SIDE_NAV_DOMESTIC_RD_CONTRACTOR";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_DOMESTIC_RD_CLOUD"] = 125] = "RTC_SIDE_NAV_DOMESTIC_RD_CLOUD";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_DOMESTIC_RD_PATENT"] = 126] = "RTC_SIDE_NAV_DOMESTIC_RD_PATENT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_DOMESTIC_RD_SUPPLY"] = 127] = "RTC_SIDE_NAV_DOMESTIC_RD_SUPPLY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_DOMESTIC_RD"] = 128] = "RTC_SIDE_NAV_DOMESTIC_RD";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_PREPARATION_DETAILS"] = 129] = "RTC_SIDE_NAV_PREPARATION_DETAILS";
    AnalyticIdentifier[AnalyticIdentifier["SELECT_GUSTO_COMPANY"] = 130] = "SELECT_GUSTO_COMPANY";
    AnalyticIdentifier[AnalyticIdentifier["CANCEL_SELECT_GUSTO_COMPANY"] = 131] = "CANCEL_SELECT_GUSTO_COMPANY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_OTHER_TAX_FACTORS"] = 132] = "RTC_SIDE_NAV_OTHER_TAX_FACTORS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_NOL_AFTER_2017"] = 133] = "RTC_OTHER_TAX_FACTORS_NOL_AFTER_2017";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_NOL_BEFORE_2018"] = 134] = "RTC_OTHER_TAX_FACTORS_NOL_BEFORE_2018";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_INC_YEAR"] = 135] = "RTC_OTHER_TAX_FACTORS_INC_YEAR";
    AnalyticIdentifier[AnalyticIdentifier["LINK_GUSTO_CANCEL"] = 136] = "LINK_GUSTO_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_JUSTWORKS_CANCEL"] = 137] = "LINK_JUSTWORKS_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_RIPPLING_CANCEL"] = 138] = "LINK_RIPPLING_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_TRINET_CANCEL"] = 139] = "LINK_TRINET_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_ZENEFITS_CANCEL"] = 140] = "LINK_ZENEFITS_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_QUICKBOOKS_CANCEL"] = 141] = "LINK_QUICKBOOKS_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_QUICKBOOKS_PAYROLL_CANCEL"] = 142] = "LINK_QUICKBOOKS_PAYROLL_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_ADP_RUN_CANCEL"] = 143] = "LINK_ADP_RUN_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_ADP_WORKFORCE_CANCEL"] = 144] = "LINK_ADP_WORKFORCE_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_PAYCHEX_CANCEL"] = 145] = "LINK_PAYCHEX_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_RIPPLING_PREVIOUS_STEP"] = 146] = "LINK_RIPPLING_PREVIOUS_STEP";
    AnalyticIdentifier[AnalyticIdentifier["LINK_GUSTO_TRY_AGAIN"] = 147] = "LINK_GUSTO_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["LINK_JUSTWORKS_TRY_AGAIN"] = 148] = "LINK_JUSTWORKS_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["LINK_RIPPLING_TRY_AGAIN"] = 149] = "LINK_RIPPLING_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["LINK_TRINET_TRY_AGAIN"] = 150] = "LINK_TRINET_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["LINK_ZENEFITS_TRY_AGAIN"] = 151] = "LINK_ZENEFITS_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["LINK_QUICKBOOKS_TRY_AGAIN"] = 152] = "LINK_QUICKBOOKS_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["LINK_QUICKBOOKS_PAYROLL_TRY_AGAIN"] = 153] = "LINK_QUICKBOOKS_PAYROLL_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["LINK_ADP_RUN_TRY_AGAIN"] = 154] = "LINK_ADP_RUN_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["LINK_ADP_WORKFORCE_TRY_AGAIN"] = 155] = "LINK_ADP_WORKFORCE_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["LINK_PAYCHEX_TRY_AGAIN"] = 156] = "LINK_PAYCHEX_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_RD_TAX_SUMMARY"] = 157] = "RTC_SIDE_NAV_RD_TAX_SUMMARY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CREATE_PROJECT"] = 158] = "RTC_CREATE_PROJECT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT"] = 159] = "RTC_EDIT_PROJECT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_PROJECTS"] = 160] = "RTC_SIDE_NAV_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PROJECT_NAME"] = 161] = "RTC_PROJECT_NAME";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DELETE_PROJECT"] = 162] = "RTC_DELETE_PROJECT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_ELIGIBILITY"] = 163] = "RTC_SIDE_NAV_ELIGIBILITY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HARD_SCI_OR_ENG_PROJECTS"] = 164] = "RTC_ELIG_HARD_SCI_OR_ENG_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_HOW_ARE_YOU_TAXED"] = 165] = "RTC_OTHER_TAX_FACTORS_HOW_ARE_YOU_TAXED";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_PRIOR_INC_YEAR"] = 166] = "RTC_OTHER_TAX_FACTORS_PRIOR_INC_YEAR";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_NAME"] = 167] = "RTC_ADD_PROJECT_NAME";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_ABBREVIATION"] = 168] = "RTC_ADD_PROJECT_ABBREVIATION";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_PURPOSE"] = 169] = "RTC_ADD_PROJECT_PURPOSE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_HARD_SCIENCE"] = 170] = "RTC_ADD_PROJECT_HARD_SCIENCE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_IS_THERE_EXPERIMENTATION"] = 171] = "RTC_ADD_PROJECT_IS_THERE_EXPERIMENTATION";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_IS_THERE_UNCERTAINTY"] = 172] = "RTC_ADD_PROJECT_IS_THERE_UNCERTAINTY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_NAME"] = 173] = "RTC_EDIT_PROJECT_NAME";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_ABBREVIATION"] = 174] = "RTC_EDIT_PROJECT_ABBREVIATION";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_PURPOSE"] = 175] = "RTC_EDIT_PROJECT_PURPOSE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_HARD_SCIENCE"] = 176] = "RTC_EDIT_PROJECT_HARD_SCIENCE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_IS_THERE_EXPERIMENTATION"] = 177] = "RTC_EDIT_PROJECT_IS_THERE_EXPERIMENTATION";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_IS_THERE_UNCERTAINTY"] = 178] = "RTC_EDIT_PROJECT_IS_THERE_UNCERTAINTY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_CREATE_PRODUCT_RADIO"] = 179] = "RTC_ADD_PROJECT_CREATE_PRODUCT_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_IMPROVE_PRODUCT_RADIO"] = 180] = "RTC_ADD_PROJECT_IMPROVE_PRODUCT_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_COMPUTER_SCIENCE_RADIO"] = 181] = "RTC_ADD_PROJECT_COMPUTER_SCIENCE_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_ENGINEERING_RADIO"] = 182] = "RTC_ADD_PROJECT_ENGINEERING_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_PHYSICS_RADIO"] = 183] = "RTC_ADD_PROJECT_PHYSICS_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_CHEMISTRY_RADIO"] = 184] = "RTC_ADD_PROJECT_CHEMISTRY_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_BIOLOGY_RADIO"] = 185] = "RTC_ADD_PROJECT_BIOLOGY_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_CREATE_PRODUCT_RADIO"] = 186] = "RTC_EDIT_PROJECT_CREATE_PRODUCT_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_IMPROVE_PRODUCT_RADIO"] = 187] = "RTC_EDIT_PROJECT_IMPROVE_PRODUCT_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_COMPUTER_SCIENCE_RADIO"] = 188] = "RTC_EDIT_PROJECT_COMPUTER_SCIENCE_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_ENGINEERING_RADIO"] = 189] = "RTC_EDIT_PROJECT_ENGINEERING_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_PHYSICS_RADIO"] = 190] = "RTC_EDIT_PROJECT_PHYSICS_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_CHEMISTRY_RADIO"] = 191] = "RTC_EDIT_PROJECT_CHEMISTRY_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_BIOLOGY_RADIO"] = 192] = "RTC_EDIT_PROJECT_BIOLOGY_RADIO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CREATE_COMPANY_COMPANY_NAME_INPUT"] = 193] = "RTC_CREATE_COMPANY_COMPANY_NAME_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CREATE_COMPANY_WEBSITE_INPUT"] = 194] = "RTC_CREATE_COMPANY_WEBSITE_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_WEBSITE"] = 195] = "RTC_PREP_DETAILS_WEBSITE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_GUSTO"] = 196] = "RTC_SELECT_LINK_GUSTO";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_JUSTWORKS"] = 197] = "RTC_SELECT_LINK_JUSTWORKS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_RIPPLING"] = 198] = "RTC_SELECT_LINK_RIPPLING";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_TRINET"] = 199] = "RTC_SELECT_LINK_TRINET";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_QUICKBOOKS_PAYROLL"] = 200] = "RTC_SELECT_LINK_QUICKBOOKS_PAYROLL";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_QUICKBOOKS"] = 201] = "RTC_SELECT_LINK_QUICKBOOKS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_RD_PAYMENT"] = 202] = "RTC_SIDE_NAV_RD_PAYMENT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_TERMS_OF_SERVICE_CHECKBOX"] = 203] = "RTC_TERMS_OF_SERVICE_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_INVITE_MEMBER_EMAIL_INPUT"] = 204] = "RTC_INVITE_MEMBER_EMAIL_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DELETE_MEMBER_NAME_INPUT"] = 205] = "RTC_DELETE_MEMBER_NAME_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DELETE_COMPANY_NAME_INPUT"] = 206] = "RTC_DELETE_COMPANY_NAME_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["LINK_NETSUITE"] = 207] = "LINK_NETSUITE";
    AnalyticIdentifier[AnalyticIdentifier["LINK_NETSUITE_CANCEL"] = 208] = "LINK_NETSUITE_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_NETSUITE_TRY_AGAIN"] = 209] = "LINK_NETSUITE_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_NETSUITE"] = 210] = "RTC_SELECT_LINK_NETSUITE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_ADP_RUN"] = 211] = "RTC_SELECT_LINK_ADP_RUN";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_ADP_WORKFORCE_NOW"] = 212] = "RTC_SELECT_LINK_ADP_WORKFORCE_NOW";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_PAYCHEX_FLEX"] = 213] = "RTC_SELECT_LINK_PAYCHEX_FLEX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_PAYLOCITY"] = 214] = "RTC_SELECT_LINK_PAYLOCITY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_ZENEFITS"] = 215] = "RTC_SELECT_LINK_ZENEFITS";
    AnalyticIdentifier[AnalyticIdentifier["LINK_PAYLOCITY"] = 216] = "LINK_PAYLOCITY";
    AnalyticIdentifier[AnalyticIdentifier["LINK_PAYLOCITY_CANCEL"] = 217] = "LINK_PAYLOCITY_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_PAYLOCITY_TRY_AGAIN"] = 218] = "LINK_PAYLOCITY_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PAYMENT_TERMS_CHECKBOX"] = 219] = "RTC_PAYMENT_TERMS_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SUBMIT_FOR_HUMAN_REVIEW"] = 220] = "RTC_SUBMIT_FOR_HUMAN_REVIEW";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CANCEL_SUBMIT_FOR_HUMAN_REVIEW"] = 221] = "RTC_CANCEL_SUBMIT_FOR_HUMAN_REVIEW";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CLOSE_SUBMIT_FOR_HUMAN_REVIEW"] = 222] = "RTC_CLOSE_SUBMIT_FOR_HUMAN_REVIEW";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SIDE_NAV_PROJECT_DETAILS"] = 223] = "RTC_SIDE_NAV_PROJECT_DETAILS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_DESCRIPTION"] = 224] = "RTC_EDIT_PROJECT_DESCRIPTION";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_PROJECT_TECHNICAL_RISK"] = 225] = "RTC_EDIT_PROJECT_TECHNICAL_RISK";
    AnalyticIdentifier[AnalyticIdentifier["RTC_RIPPLING_CSV_DOWNLOAD_LINK"] = 226] = "RTC_RIPPLING_CSV_DOWNLOAD_LINK";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_MANUAL_EXPENSE_EXPENSE_TYPE"] = 227] = "RTC_ADD_MANUAL_EXPENSE_EXPENSE_TYPE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_MANUAL_EXPENSE_NAME"] = 228] = "RTC_ADD_MANUAL_EXPENSE_NAME";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_MANUAL_EXPENSE_AMOUNT"] = 229] = "RTC_ADD_MANUAL_EXPENSE_AMOUNT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_MANUAL_EXPENSE_COUNTRY"] = 230] = "RTC_ADD_MANUAL_EXPENSE_COUNTRY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_MANUAL_EXPENSE_STATE"] = 231] = "RTC_ADD_MANUAL_EXPENSE_STATE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_MANUAL_EXPENSE_JOB_TITLE"] = 232] = "RTC_ADD_MANUAL_EXPENSE_JOB_TITLE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_MANUAL_EXPENSE_WORK_EMAIL"] = 233] = "RTC_ADD_MANUAL_EXPENSE_WORK_EMAIL";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_MANUAL_EXPENSE_EXPENSE_TYPE"] = 234] = "RTC_EDIT_MANUAL_EXPENSE_EXPENSE_TYPE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_MANUAL_EXPENSE_NAME"] = 235] = "RTC_EDIT_MANUAL_EXPENSE_NAME";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_MANUAL_EXPENSE_AMOUNT"] = 236] = "RTC_EDIT_MANUAL_EXPENSE_AMOUNT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_MANUAL_EXPENSE_COUNTRY"] = 237] = "RTC_EDIT_MANUAL_EXPENSE_COUNTRY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_MANUAL_EXPENSE_STATE"] = 238] = "RTC_EDIT_MANUAL_EXPENSE_STATE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_MANUAL_EXPENSE_JOB_TITLE"] = 239] = "RTC_EDIT_MANUAL_EXPENSE_JOB_TITLE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EDIT_MANUAL_EXPENSE_WORK_EMAIL"] = 240] = "RTC_EDIT_MANUAL_EXPENSE_WORK_EMAIL";
    AnalyticIdentifier[AnalyticIdentifier["RD_CAP_OPEN_ALLOCATION_TROUSER"] = 241] = "RD_CAP_OPEN_ALLOCATION_TROUSER";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OPEN_ADD_PROJECT_MODAL"] = 242] = "RTC_OPEN_ADD_PROJECT_MODAL";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DELETE_PROJECT_BUTTON"] = 243] = "RTC_DELETE_PROJECT_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ALLOW_REAL_COMPANY_AVATAR"] = 244] = "RTC_ALLOW_REAL_COMPANY_AVATAR";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_SIGN_UP_USER"] = 245] = "ONBOARDING_SIGN_UP_USER";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_CONTINUE_LOGIN"] = 246] = "ONBOARDING_CONTINUE_LOGIN";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_LOGIN_USER"] = 247] = "ONBOARDING_LOGIN_USER";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_COMPLETE_PROFILE"] = 248] = "ONBOARDING_COMPLETE_PROFILE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PROJECT_DETAILS_ALERT_CARD"] = 249] = "RTC_PROJECT_DETAILS_ALERT_CARD";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OTHER_TAX_FACTORS_ALERT_CARD"] = 250] = "RTC_OTHER_TAX_FACTORS_ALERT_CARD";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_ALERT_CARD"] = 251] = "RTC_PREP_DETAILS_ALERT_CARD";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ADD_PROJECT_CANCEL_BUTTON"] = 252] = "RTC_ADD_PROJECT_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PROJECT_GUIDANCE_MODAL_NEXT"] = 253] = "RTC_PROJECT_GUIDANCE_MODAL_NEXT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PROJECT_GUIDANCE_MODAL_CLOSE"] = 254] = "RTC_PROJECT_GUIDANCE_MODAL_CLOSE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OPEN_DELETE_PROJECT_MODAL"] = 255] = "RTC_OPEN_DELETE_PROJECT_MODAL";
    AnalyticIdentifier[AnalyticIdentifier["OPEN_CONNECT_ACCOUNT_MODAL"] = 256] = "OPEN_CONNECT_ACCOUNT_MODAL";
    AnalyticIdentifier[AnalyticIdentifier["CONTINUE_CONNECT_ACCOUNT"] = 257] = "CONTINUE_CONNECT_ACCOUNT";
    AnalyticIdentifier[AnalyticIdentifier["CANCEL_CONNECT_ACCOUNT"] = 258] = "CANCEL_CONNECT_ACCOUNT";
    AnalyticIdentifier[AnalyticIdentifier["RIPPLING_UPLOAD_INPUT"] = 259] = "RIPPLING_UPLOAD_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RIPPLING_CLOSE_AFTER_SUCCESS_BUTTON"] = 260] = "RIPPLING_CLOSE_AFTER_SUCCESS_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_UPLOAD_ACCOUNTING_CSV"] = 261] = "RTC_SELECT_UPLOAD_ACCOUNTING_CSV";
    AnalyticIdentifier[AnalyticIdentifier["UPLOAD_ACCOUNTING_CSV"] = 262] = "UPLOAD_ACCOUNTING_CSV";
    AnalyticIdentifier[AnalyticIdentifier["UPLOAD_ACCOUNTING_CSV_CANCEL"] = 263] = "UPLOAD_ACCOUNTING_CSV_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["UPLOAD_ACCOUNTING_CSV_PREVIOUS"] = 264] = "UPLOAD_ACCOUNTING_CSV_PREVIOUS";
    AnalyticIdentifier[AnalyticIdentifier["UPLOAD_ACCOUNTING_CSV_INPUT"] = 265] = "UPLOAD_ACCOUNTING_CSV_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["UPLOAD_ACCOUNTING_CSV_CLOSE_AFTER_SUCCESS_BUTTON"] = 266] = "UPLOAD_ACCOUNTING_CSV_CLOSE_AFTER_SUCCESS_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_UPLOAD_PAYROLL_CSV"] = 267] = "RTC_SELECT_UPLOAD_PAYROLL_CSV";
    AnalyticIdentifier[AnalyticIdentifier["UPLOAD_PAYROLL_CSV"] = 268] = "UPLOAD_PAYROLL_CSV";
    AnalyticIdentifier[AnalyticIdentifier["UPLOAD_PAYROLL_CSV_CANCEL"] = 269] = "UPLOAD_PAYROLL_CSV_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["UPLOAD_PAYROLL_CSV_PREVIOUS"] = 270] = "UPLOAD_PAYROLL_CSV_PREVIOUS";
    AnalyticIdentifier[AnalyticIdentifier["UPLOAD_PAYROLL_CSV_INPUT"] = 271] = "UPLOAD_PAYROLL_CSV_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["UPLOAD_PAYROLL_CSV_CLOSE_AFTER_SUCCESS_BUTTON"] = 272] = "UPLOAD_PAYROLL_CSV_CLOSE_AFTER_SUCCESS_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CSV_UPLOAD_TEMPLATE_DOWNLOAD_LINK"] = 273] = "RTC_CSV_UPLOAD_TEMPLATE_DOWNLOAD_LINK";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CSV_UPLOAD_EXAMPLE_DOWNLOAD_LINK"] = 274] = "RTC_CSV_UPLOAD_EXAMPLE_DOWNLOAD_LINK";
    AnalyticIdentifier[AnalyticIdentifier["RTC_FOREIGN_ALLOCATION_SLIDER"] = 275] = "RTC_FOREIGN_ALLOCATION_SLIDER";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DOMESTIC_EMPLOYEES_ALLOCATION_SLIDER"] = 276] = "RTC_DOMESTIC_EMPLOYEES_ALLOCATION_SLIDER";
    AnalyticIdentifier[AnalyticIdentifier["RTC_FOREIGN_RD_GUIDANCE_MODAL_NEXT"] = 277] = "RTC_FOREIGN_RD_GUIDANCE_MODAL_NEXT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_FOREIGN_RD_GUIDANCE_MODAL_CLOSE"] = 278] = "RTC_FOREIGN_RD_GUIDANCE_MODAL_CLOSE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DOMESTIC_EMPLOYEES_GUIDANCE_MODAL_NEXT"] = 279] = "RTC_DOMESTIC_EMPLOYEES_GUIDANCE_MODAL_NEXT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DOMESTIC_EMPLOYEES_GUIDANCE_MODAL_CLOSE"] = 280] = "RTC_DOMESTIC_EMPLOYEES_GUIDANCE_MODAL_CLOSE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DOMESTIC_CONTRACTORS_ALLOCATION_SLIDER"] = 281] = "RTC_DOMESTIC_CONTRACTORS_ALLOCATION_SLIDER";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DOMESTIC_CLOUD_ALLOCATION_SLIDER"] = 282] = "RTC_DOMESTIC_CLOUD_ALLOCATION_SLIDER";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DOMESTIC_PATENTS_ALLOCATION_SLIDER"] = 283] = "RTC_DOMESTIC_PATENTS_ALLOCATION_SLIDER";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DOMESTIC_SUPPLIES_ALLOCATION_SLIDER"] = 284] = "RTC_DOMESTIC_SUPPLIES_ALLOCATION_SLIDER";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_GRANT_FUNDING_CHECKBOX"] = 285] = "RTC_PREP_DETAILS_GRANT_FUNDING_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_CONTRACT_FUNDING_CHECKBOX"] = 286] = "RTC_PREP_DETAILS_CONTRACT_FUNDING_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_SBIR_FUNDING_CHECKBOX"] = 287] = "RTC_PREP_DETAILS_SBIR_FUNDING_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_OTHER_FUNDING_CHECKBOX"] = 288] = "RTC_PREP_DETAILS_OTHER_FUNDING_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_RESEARCH_PAPERS_CHECKBOX"] = 289] = "RTC_PREP_DETAILS_RESEARCH_PAPERS_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_PATENTS_CHECKBOX"] = 290] = "RTC_PREP_DETAILS_PATENTS_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PREP_DETAILS_WHITE_PAPERS_CHECKBOX"] = 291] = "RTC_PREP_DETAILS_WHITE_PAPERS_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ACCOUNTANT_OPEN_CREATE_COMPANY_MODAL"] = 292] = "RTC_ACCOUNTANT_OPEN_CREATE_COMPANY_MODAL";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ACCOUNTANT_OPEN_INVITE_NEW_MEMBER_MODAL"] = 293] = "RTC_ACCOUNTANT_OPEN_INVITE_NEW_MEMBER_MODAL";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ACCOUNTANT_INVITE_NEW_MEMBER"] = 294] = "RTC_ACCOUNTANT_INVITE_NEW_MEMBER";
    AnalyticIdentifier[AnalyticIdentifier["RTC_COMPANY_LIST_TAB"] = 295] = "RTC_COMPANY_LIST_TAB";
    AnalyticIdentifier[AnalyticIdentifier["RTC_COMPANY_MEMBER_LIST_TAB"] = 296] = "RTC_COMPANY_MEMBER_LIST_TAB";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ACCOUNTANT_CREATE_COMPANY"] = 297] = "RTC_ACCOUNTANT_CREATE_COMPANY";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OPEN_SUBMIT_FOR_HUMAN_REVIEW_MODAL"] = 298] = "RTC_OPEN_SUBMIT_FOR_HUMAN_REVIEW_MODAL";
    AnalyticIdentifier[AnalyticIdentifier["COMPANY_NAV_RD_LAUNCHPAD"] = 299] = "COMPANY_NAV_RD_LAUNCHPAD";
    AnalyticIdentifier[AnalyticIdentifier["COMPANY_NAV_FORECASTING"] = 300] = "COMPANY_NAV_FORECASTING";
    AnalyticIdentifier[AnalyticIdentifier["RTC_OPEN_MANUAL_EXPENSE_MODAL"] = 301] = "RTC_OPEN_MANUAL_EXPENSE_MODAL";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CANCEL_MANUALLY_ADD_EXPENSE"] = 302] = "RTC_CANCEL_MANUALLY_ADD_EXPENSE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_COMPANY_NAV_BAR_BUTTON"] = 303] = "RTC_COMPANY_NAV_BAR_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_LAUNCH_PAD"] = 304] = "RTC_LAUNCH_PAD";
    AnalyticIdentifier[AnalyticIdentifier["RTC_REVENUES"] = 305] = "RTC_REVENUES";
    AnalyticIdentifier[AnalyticIdentifier["RTC_EXPENSES"] = 306] = "RTC_EXPENSES";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_ASANA"] = 307] = "RTC_SELECT_LINK_ASANA";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_JIRA"] = 308] = "RTC_SELECT_LINK_JIRA";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_LINEAR"] = 309] = "RTC_SELECT_LINK_LINEAR";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_TRELLO"] = 310] = "RTC_SELECT_LINK_TRELLO";
    AnalyticIdentifier[AnalyticIdentifier["GAAP_CAP_LAUNCH_PAD"] = 311] = "GAAP_CAP_LAUNCH_PAD";
    AnalyticIdentifier[AnalyticIdentifier["COMPANY_NAV_GAAP_CAP"] = 312] = "COMPANY_NAV_GAAP_CAP";
    AnalyticIdentifier[AnalyticIdentifier["GAAP_CAP_ELEVATOR_PITCH_INPUT"] = 317] = "GAAP_CAP_ELEVATOR_PITCH_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["GAAP_CAP_TECH_STACK_INPUT"] = 318] = "GAAP_CAP_TECH_STACK_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["TOS_ACCEPTED_CHECKBOX"] = 322] = "TOS_ACCEPTED_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RECAPTCHA_CHECKBOX"] = 323] = "RECAPTCHA_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_GITHUB"] = 324] = "RTC_SELECT_LINK_GITHUB";
    AnalyticIdentifier[AnalyticIdentifier["LINK_TICKETING"] = 326] = "LINK_TICKETING";
    AnalyticIdentifier[AnalyticIdentifier["LINK_TICKETING_CANCEL"] = 327] = "LINK_TICKETING_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_TICKETING_TRY_AGAIN"] = 328] = "LINK_TICKETING_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_SHORTCUT"] = 329] = "RTC_SELECT_LINK_SHORTCUT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HAS_FILED_EXTENSION"] = 332] = "RTC_ELIG_HAS_FILED_EXTENSION";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_MONTH_AND_DAY_TAX_YEAR_END"] = 336] = "RTC_ELIG_MONTH_AND_DAY_TAX_YEAR_END";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ELIG_HAD_SHORT_TAX_YEAR"] = 337] = "RTC_ELIG_HAD_SHORT_TAX_YEAR";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DRAWER_DETAIL_TAB"] = 338] = "ASC_PROJECT_DRAWER_DETAIL_TAB";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DRAWER_CONTRIBUTOR_TAB"] = 339] = "ASC_PROJECT_DRAWER_CONTRIBUTOR_TAB";
    AnalyticIdentifier[AnalyticIdentifier["GAAP_CAP_TICKETING_SYNC_PERIOD_INPUT"] = 342] = "GAAP_CAP_TICKETING_SYNC_PERIOD_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_ASC_PROJECTS"] = 343] = "ENTERPRISE_NAV_ASC_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_PAYROLL_QRE"] = 344] = "ENTERPRISE_NAV_PAYROLL_QRE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_EXPORT_QRE"] = 345] = "ENTERPRISE_NAV_EXPORT_QRE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_EXPORT_ASC"] = 346] = "ENTERPRISE_NAV_EXPORT_ASC";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DETAIL_NAME"] = 347] = "ASC_PROJECT_DETAIL_NAME";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DETAIL_DESCRIPTION"] = 348] = "ASC_PROJECT_DETAIL_DESCRIPTION";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DETAIL_CAPITALIZATION_START_DATE"] = 349] = "ASC_PROJECT_DETAIL_CAPITALIZATION_START_DATE";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DETAIL_CAPITALIZATION_END_DATE"] = 350] = "ASC_PROJECT_DETAIL_CAPITALIZATION_END_DATE";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DRAWER_TICKETS_TAB"] = 351] = "ASC_PROJECT_DRAWER_TICKETS_TAB";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_ADAPTATION_FOR_CUSTOMER"] = 353] = "ENTERPRISE_PROJECT_ADAPTATION_FOR_CUSTOMER";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_INTERNAL_USE_SOFTWARE"] = 354] = "ENTERPRISE_PROJECT_INTERNAL_USE_SOFTWARE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_RESEARCH_FUNDED_BY_GRANT_CONTRACT_ETC"] = 355] = "ENTERPRISE_PROJECT_RESEARCH_FUNDED_BY_GRANT_CONTRACT_ETC";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_RESEARCH_INTO_SOCIAL_SCIENCES"] = 356] = "ENTERPRISE_PROJECT_RESEARCH_INTO_SOCIAL_SCIENCES";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_RESEARCH_OUTSIDE_US"] = 357] = "ENTERPRISE_PROJECT_RESEARCH_OUTSIDE_US";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_REVERSE_ENGINEERING"] = 358] = "ENTERPRISE_PROJECT_REVERSE_ENGINEERING";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_SURVEY_OR_MARKET_RESEARCH"] = 359] = "ENTERPRISE_PROJECT_SURVEY_OR_MARKET_RESEARCH";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_TESTING_OR_QA_IN_PRODUCTION"] = 360] = "ENTERPRISE_PROJECT_TESTING_OR_QA_IN_PRODUCTION";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_NARRATIVE"] = 361] = "ENTERPRISE_PROJECT_NARRATIVE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_TECHNICAL_CHALLENGES"] = 362] = "ENTERPRISE_PROJECT_TECHNICAL_CHALLENGES";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_PROCESS_OF_EXPERIMENTATION"] = 363] = "ENTERPRISE_PROJECT_PROCESS_OF_EXPERIMENTATION";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_PURPOSE"] = 364] = "ENTERPRISE_PROJECT_PURPOSE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_BUSINESS_COMPONENT"] = 365] = "ENTERPRISE_PROJECT_BUSINESS_COMPONENT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_BUSINESS_PURPOSE"] = 366] = "ENTERPRISE_PROJECT_BUSINESS_PURPOSE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_IMPROVED_FUNCTION"] = 367] = "ENTERPRISE_PROJECT_IMPROVED_FUNCTION";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_IMPROVED_RELIABILITY"] = 368] = "ENTERPRISE_PROJECT_IMPROVED_RELIABILITY";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_IMPROVED_PERFORMANCE"] = 369] = "ENTERPRISE_PROJECT_IMPROVED_PERFORMANCE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_IMPROVED_QUALITY"] = 370] = "ENTERPRISE_PROJECT_IMPROVED_QUALITY";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_GENERAL_TAB"] = 371] = "ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_GENERAL_TAB";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_GENERAL_FORM_LINKED_TICKETING"] = 373] = "ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_GENERAL_FORM_LINKED_TICKETING";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_GENERAL_FORM_NAME"] = 374] = "ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_GENERAL_FORM_NAME";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_GENERAL_FORM_RD_CATEGORY"] = 375] = "ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_GENERAL_FORM_RD_CATEGORY";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_TAB"] = 376] = "ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_TAB";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_FORM_AMOUNT"] = 377] = "ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_FORM_AMOUNT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_FORM_JOB_TITLE"] = 378] = "ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_FORM_JOB_TITLE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_FORM_COUNTRY"] = 379] = "ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_FORM_COUNTRY";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_FORM_STATE"] = 380] = "ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_FORM_STATE";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_FISCAL_YEAR_END_MONTH"] = 381] = "ONBOARDING_FISCAL_YEAR_END_MONTH";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_COMPANY_INFO"] = 382] = "ENTERPRISE_NAV_COMPANY_INFO";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_CREDIT_CALCULATION"] = 383] = "ENTERPRISE_NAV_CREDIT_CALCULATION";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PRIOR_YEAR_QRES"] = 384] = "ENTERPRISE_PRIOR_YEAR_QRES";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PROJECT_NONE_OF_THE_ABOVE"] = 385] = "ENTERPRISE_PROJECT_NONE_OF_THE_ABOVE";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CREATE_COMPANY_FISCAL_YEAR_END_MONTH"] = 386] = "RTC_CREATE_COMPANY_FISCAL_YEAR_END_MONTH";
    AnalyticIdentifier[AnalyticIdentifier["RTC_INVITE_MEMBER_ROLE_INPUT"] = 387] = "RTC_INVITE_MEMBER_ROLE_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_CREATE_PROJECT_NAME"] = 388] = "ENTERPRISE_CREATE_PROJECT_NAME";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_CREATE_PROJECT_DESCRIPTION"] = 389] = "ENTERPRISE_CREATE_PROJECT_DESCRIPTION";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_ACCOUNTING_QRE"] = 390] = "ENTERPRISE_NAV_ACCOUNTING_QRE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_GENERAL_TAB"] = 391] = "ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_GENERAL_TAB";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_GENERAL_FORM_NAME"] = 392] = "ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_GENERAL_FORM_NAME";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_GENERAL_FORM_RD_CATEGORY"] = 393] = "ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_GENERAL_FORM_RD_CATEGORY";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_DETAILS_TAB"] = 394] = "ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_DETAILS_TAB";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_DETAILS_FORM_AMOUNT"] = 395] = "ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_DETAILS_FORM_AMOUNT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_DETAILS_FORM_COUNTRY"] = 396] = "ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_DETAILS_FORM_COUNTRY";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_DETAILS_FORM_STATE"] = 397] = "ENTERPRISE_ACCOUNTING_QRE_TABLE_DETAIL_DETAILS_FORM_STATE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_PRIOR_YEAR_REVENUE"] = 398] = "ENTERPRISE_PRIOR_YEAR_REVENUE";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_FILING_METHOD"] = 399] = "ENTERPRISE_FILING_METHOD";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_FIRST_YEAR_QRE_AND_GROSS_RECEIPTS"] = 400] = "ENTERPRISE_FIRST_YEAR_QRE_AND_GROSS_RECEIPTS";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_CREATE_CONTRIBUTOR_NAME"] = 401] = "ENTERPRISE_CREATE_CONTRIBUTOR_NAME";
    AnalyticIdentifier[AnalyticIdentifier["RTC_DELETE_INVITED_MEMBER_EMAIL_INPUT"] = 402] = "RTC_DELETE_INVITED_MEMBER_EMAIL_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_MICROSOFT_OAUTH_BUTTON"] = 403] = "RTC_MICROSOFT_OAUTH_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_UPLOAD_ACCOUNTING_PROJECT_ALLOCATION_REPORT"] = 405] = "ENTERPRISE_UPLOAD_ACCOUNTING_PROJECT_ALLOCATION_REPORT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_DOWNLOAD_ACCOUNTING_PROJECT_ALLOCATION_REPORT"] = 406] = "ENTERPRISE_DOWNLOAD_ACCOUNTING_PROJECT_ALLOCATION_REPORT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_UPLOAD_PAYROLL_PROJECT_ALLOCATION_REPORT"] = 407] = "ENTERPRISE_UPLOAD_PAYROLL_PROJECT_ALLOCATION_REPORT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_DOWNLOAD_PAYROLL_PROJECT_ALLOCATION_REPORT"] = 408] = "ENTERPRISE_DOWNLOAD_PAYROLL_PROJECT_ALLOCATION_REPORT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_DOWNLOAD_PAYROLL_PROJECT_ALLOCATION_REPORT_LINK"] = 409] = "ENTERPRISE_DOWNLOAD_PAYROLL_PROJECT_ALLOCATION_REPORT_LINK";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_DOWNLOAD_RD_CREDITS_REPORT_LINK"] = 410] = "ENTERPRISE_DOWNLOAD_RD_CREDITS_REPORT_LINK";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_RD_DATA_SOURCES"] = 411] = "ENTERPRISE_NAV_RD_DATA_SOURCES";
    AnalyticIdentifier[AnalyticIdentifier["COMPANY_MEMBER_INFORMATION_TAB"] = 412] = "COMPANY_MEMBER_INFORMATION_TAB";
    AnalyticIdentifier[AnalyticIdentifier["COMPANY_MEMBER_SCOPE_TAB"] = 413] = "COMPANY_MEMBER_SCOPE_TAB";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_SUPPORTING_DOCUMENT_UPLOAD"] = 414] = "RD_PROJECT_SUPPORTING_DOCUMENT_UPLOAD";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_REMOVE_SUPPORTING_DOCUMENT"] = 415] = "RD_PROJECT_REMOVE_SUPPORTING_DOCUMENT";
    AnalyticIdentifier[AnalyticIdentifier["RAW_TICKETING_PROVIDER_INPUT"] = 416] = "RAW_TICKETING_PROVIDER_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["COMPANY_BULK_TICKETING_DATA_FILE_UPLOAD"] = 417] = "COMPANY_BULK_TICKETING_DATA_FILE_UPLOAD";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CONTINUE_WITH_SSO_LINK"] = 418] = "RTC_CONTINUE_WITH_SSO_LINK";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_PROVIDER_ID_INPUT"] = 419] = "ONBOARDING_PROVIDER_ID_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_SUBMIT_PROVIDER_ID_BUTTON"] = 420] = "ONBOARDING_SUBMIT_PROVIDER_ID_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_BACK_TO_LOGIN_LINK"] = 421] = "RTC_BACK_TO_LOGIN_LINK";
    AnalyticIdentifier[AnalyticIdentifier["ONBOARDING_SSO_PROVIDER_TYPE_INPUT"] = 422] = "ONBOARDING_SSO_PROVIDER_TYPE_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["PRIOR_YEAR_TAX_DOCUMENT_UPLOAD"] = 423] = "PRIOR_YEAR_TAX_DOCUMENT_UPLOAD";
    AnalyticIdentifier[AnalyticIdentifier["COMPANY_DASHBOARD_LOGO"] = 424] = "COMPANY_DASHBOARD_LOGO";
    AnalyticIdentifier[AnalyticIdentifier["DELETE_CONNECTED_DATA_SOURCE_CONFIRM_BUTTON"] = 425] = "DELETE_CONNECTED_DATA_SOURCE_CONFIRM_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["DELETE_CONNECTED_DATA_SOURCE_CANCEL_BUTTON"] = 426] = "DELETE_CONNECTED_DATA_SOURCE_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ADD_TEAM_MEMBER_BUTTON"] = 427] = "ADD_TEAM_MEMBER_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["TEAM_MEMBER_SEARCH_INPUT"] = 428] = "TEAM_MEMBER_SEARCH_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["DATA_SOURCES_PAYROLL_MONTH_EXPENSE_UPLOAD_BUTTON"] = 429] = "DATA_SOURCES_PAYROLL_MONTH_EXPENSE_UPLOAD_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_ASC_WAGES"] = 430] = "ENTERPRISE_NAV_ASC_WAGES";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_PAYROLL_MONTH_EXPENSES"] = 431] = "ENTERPRISE_NAV_PAYROLL_MONTH_EXPENSES";
    AnalyticIdentifier[AnalyticIdentifier["RTC_ASC_MERGE_PROJECTS"] = 432] = "RTC_ASC_MERGE_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_PROJECT_SKIP_AUTO_CONNECT"] = 433] = "RTC_PROJECT_SKIP_AUTO_CONNECT";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CONFIRM_MERGE_ASC_PROJECTS"] = 434] = "RTC_CONFIRM_MERGE_ASC_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CANCEL_MERGE_ASC_PROJECTS"] = 435] = "RTC_CANCEL_MERGE_ASC_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_DOWNLOAD_ASC_WAGES_REPORT_LINK"] = 436] = "ENTERPRISE_DOWNLOAD_ASC_WAGES_REPORT_LINK";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_UPLOAD_ASC_WAGES_REPORT"] = 437] = "ENTERPRISE_UPLOAD_ASC_WAGES_REPORT";
    AnalyticIdentifier[AnalyticIdentifier["PROFILE_PAGE_LOGOUT_BUTTON"] = 438] = "PROFILE_PAGE_LOGOUT_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_COMPANY_NAV_PERIOD_PICKER"] = 439] = "RTC_COMPANY_NAV_PERIOD_PICKER";
    AnalyticIdentifier[AnalyticIdentifier["FISCAL_PERIOD_MODAL_CANCEL_BUTTON"] = 440] = "FISCAL_PERIOD_MODAL_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["FISCAL_PERIOD_MODAL_SELECT_BUTTON"] = 441] = "FISCAL_PERIOD_MODAL_SELECT_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["PROJECT_DETAIL_CAPITALIZATION_START_DATE_CLEAR_BUTTON"] = 442] = "PROJECT_DETAIL_CAPITALIZATION_START_DATE_CLEAR_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["PROJECT_DETAIL_CAPITALIZATION_END_DATE_CLEAR_BUTTON"] = 443] = "PROJECT_DETAIL_CAPITALIZATION_END_DATE_CLEAR_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["DATA_SOURCES_CONNECT_ACCOUNTING_BUTTON"] = 444] = "DATA_SOURCES_CONNECT_ACCOUNTING_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["DATA_SOURCES_CONNECT_PAYROLL_BUTTON"] = 445] = "DATA_SOURCES_CONNECT_PAYROLL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["DATA_SOURCES_UPLOAD_TICKETING_PROVIDER_BUTTON"] = 446] = "DATA_SOURCES_UPLOAD_TICKETING_PROVIDER_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["DATA_SOURCES_UPLOAD_TAX_DOCS_BUTTON"] = 447] = "DATA_SOURCES_UPLOAD_TAX_DOCS_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["DATA_SOURCES_UPLOAD_ASC_COST_BUTTON"] = 448] = "DATA_SOURCES_UPLOAD_ASC_COST_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["DATA_SOURCES_PAYROLL_MONTH_EXPENSE_CANCEL_BUTTON"] = 449] = "DATA_SOURCES_PAYROLL_MONTH_EXPENSE_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["DATA_SOURCES_PAYROLL_MONTH_EXPENSE_UPLOAD_INPUT"] = 450] = "DATA_SOURCES_PAYROLL_MONTH_EXPENSE_UPLOAD_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ASC_WAGES_LAYOUT_PAYROLL_MONTH_EXPENSE_UPLOAD_BUTTON"] = 451] = "ASC_WAGES_LAYOUT_PAYROLL_MONTH_EXPENSE_UPLOAD_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_WAGES_LAYOUT_PAYROLL_MONTH_EXPENSE_CANCEL_BUTTON"] = 452] = "ASC_WAGES_LAYOUT_PAYROLL_MONTH_EXPENSE_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_WAGES_LAYOUT_PAYROLL_MONTH_EXPENSE_UPLOAD_INPUT"] = 453] = "ASC_WAGES_LAYOUT_PAYROLL_MONTH_EXPENSE_UPLOAD_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["PROJECT_RD_ELIGIBLE_TOGGLE"] = 454] = "PROJECT_RD_ELIGIBLE_TOGGLE";
    AnalyticIdentifier[AnalyticIdentifier["PROJECT_SECTION_174_TOGGLE"] = 455] = "PROJECT_SECTION_174_TOGGLE";
    AnalyticIdentifier[AnalyticIdentifier["HEADER_PROFILE"] = 456] = "HEADER_PROFILE";
    AnalyticIdentifier[AnalyticIdentifier["GAAP_CAP_COMPANY_URL_INPUT"] = 457] = "GAAP_CAP_COMPANY_URL_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_RD_PROJECTS"] = 458] = "ENTERPRISE_NAV_RD_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DRAWER_DETAIL_TAB"] = 459] = "RD_PROJECT_DRAWER_DETAIL_TAB";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DRAWER_ELIGIBILITY_TAB"] = 460] = "RD_PROJECT_DRAWER_ELIGIBILITY_TAB";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DRAWER_CONTRIBUTOR_TAB"] = 461] = "RD_PROJECT_DRAWER_CONTRIBUTOR_TAB";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DRAWER_TICKETS_TAB"] = 462] = "RD_PROJECT_DRAWER_TICKETS_TAB";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DETAIL_NAME"] = 463] = "RD_PROJECT_DETAIL_NAME";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DETAIL_DESCRIPTION"] = 464] = "RD_PROJECT_DETAIL_DESCRIPTION";
    AnalyticIdentifier[AnalyticIdentifier["RTC_RD_MERGE_PROJECTS"] = 465] = "RTC_RD_MERGE_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CONFIRM_MERGE_RD_PROJECTS"] = 466] = "RTC_CONFIRM_MERGE_RD_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_CANCEL_MERGE_RD_PROJECTS"] = 467] = "RTC_CANCEL_MERGE_RD_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["CREATE_PROJECT_BUTTON"] = 468] = "CREATE_PROJECT_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["CREATE_ASC_PROJECT_BUTTON"] = 469] = "CREATE_ASC_PROJECT_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["CREATE_RD_PROJECT_BUTTON"] = 470] = "CREATE_RD_PROJECT_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["PROFILE_MENU"] = 471] = "PROFILE_MENU";
    AnalyticIdentifier[AnalyticIdentifier["COLUMN_VISIBILITY_MENU_BUTTON"] = 472] = "COLUMN_VISIBILITY_MENU_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["COLUMN_VISIBILITY_MENU_SELECT_ALL_CHECKBOX"] = 473] = "COLUMN_VISIBILITY_MENU_SELECT_ALL_CHECKBOX";
    AnalyticIdentifier[AnalyticIdentifier["FISCAL_PERIOD_MODAL_YEAR_SELECT"] = 474] = "FISCAL_PERIOD_MODAL_YEAR_SELECT";
    AnalyticIdentifier[AnalyticIdentifier["FISCAL_PERIOD_MODAL_PERIOD_SELECT"] = 475] = "FISCAL_PERIOD_MODAL_PERIOD_SELECT";
    AnalyticIdentifier[AnalyticIdentifier["ASC_WAGES_LAYOUT_EDIT_IN_BULK_BUTTON"] = 476] = "ASC_WAGES_LAYOUT_EDIT_IN_BULK_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_WAGES_LAYOUT_UPLOAD_BULK_BUTTON"] = 477] = "ASC_WAGES_LAYOUT_UPLOAD_BULK_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_WAGES_LAYOUT_CANCEL_BULK_BUTTON"] = 478] = "ASC_WAGES_LAYOUT_CANCEL_BULK_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_FILTER_MONTH_YEAR_INPUT"] = 479] = "ENTERPRISE_FILTER_MONTH_YEAR_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_FILTER_MONTH_YEAR_CLEAR_BUTTON"] = 480] = "ENTERPRISE_FILTER_MONTH_YEAR_CLEAR_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DETAIL_PRODUCT"] = 481] = "ASC_PROJECT_DETAIL_PRODUCT";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DETAIL_PRODUCT"] = 482] = "RD_PROJECT_DETAIL_PRODUCT";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DETAIL_PRODUCT_CLEAR_BUTTON"] = 483] = "ASC_PROJECT_DETAIL_PRODUCT_CLEAR_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DETAIL_PRODUCT_CLEAR_BUTTON"] = 484] = "RD_PROJECT_DETAIL_PRODUCT_CLEAR_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_UPDATE_PRODUCT_NAME"] = 485] = "ENTERPRISE_UPDATE_PRODUCT_NAME";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_SHORT_TAX_YEAR_PERIOD"] = 486] = "ENTERPRISE_SHORT_TAX_YEAR_PERIOD";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_SHORT_TAX_YEAR_PERIOD_START"] = 487] = "ENTERPRISE_SHORT_TAX_YEAR_PERIOD_START";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_SHORT_TAX_YEAR_PERIOD_END"] = 488] = "ENTERPRISE_SHORT_TAX_YEAR_PERIOD_END";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_GENERATE_ASC_REPORT_BUTTON"] = 489] = "ENTERPRISE_GENERATE_ASC_REPORT_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_WORKDAY"] = 490] = "RTC_SELECT_LINK_WORKDAY";
    AnalyticIdentifier[AnalyticIdentifier["LINK_WORKDAY"] = 491] = "LINK_WORKDAY";
    AnalyticIdentifier[AnalyticIdentifier["LINK_WORKDAY_CANCEL"] = 492] = "LINK_WORKDAY_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_WORKDAY_TRY_AGAIN"] = 493] = "LINK_WORKDAY_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["CREDIT_CALCULATION_COMPANY_OFFICERS"] = 494] = "CREDIT_CALCULATION_COMPANY_OFFICERS";
    AnalyticIdentifier[AnalyticIdentifier["START_ANALYSIS_MODAL_TICKETING_TOKEN_SELECT"] = 495] = "START_ANALYSIS_MODAL_TICKETING_TOKEN_SELECT";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_CREATE_RD_EXPENSE_NAME"] = 496] = "ENTERPRISE_CREATE_RD_EXPENSE_NAME";
    AnalyticIdentifier[AnalyticIdentifier["FILE_UPLOAD_TICKETING_PROVIDER_INPUT"] = 497] = "FILE_UPLOAD_TICKETING_PROVIDER_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["FILE_UPLOAD_TICKETING_ENTITY_TYPE_INPUT"] = 498] = "FILE_UPLOAD_TICKETING_ENTITY_TYPE_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DETAIL_CAPITALIZATION_STATUS"] = 499] = "ASC_PROJECT_DETAIL_CAPITALIZATION_STATUS";
    AnalyticIdentifier[AnalyticIdentifier["QRE_LAYOUT_BULK_EDIT_IN_EXCEL_BTN"] = 500] = "QRE_LAYOUT_BULK_EDIT_IN_EXCEL_BTN";
    AnalyticIdentifier[AnalyticIdentifier["QRE_LAYOUT_CONNECT_PROVIDER_BTN"] = 501] = "QRE_LAYOUT_CONNECT_PROVIDER_BTN";
    AnalyticIdentifier[AnalyticIdentifier["QRE_LAYOUT_SET_RD_PERCENTAGE_BTN"] = 502] = "QRE_LAYOUT_SET_RD_PERCENTAGE_BTN";
    AnalyticIdentifier[AnalyticIdentifier["QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_TO_EXPENSES_MODAL_ALLOCATION_SLIDER"] = 503] = "QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_TO_EXPENSES_MODAL_ALLOCATION_SLIDER";
    AnalyticIdentifier[AnalyticIdentifier["QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_TO_EXPENSES_MODAL_CANCEL_BTN"] = 504] = "QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_TO_EXPENSES_MODAL_CANCEL_BTN";
    AnalyticIdentifier[AnalyticIdentifier["QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_TO_EXPENSES_MODAL_SUBMIT_BTN"] = 505] = "QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_TO_EXPENSES_MODAL_SUBMIT_BTN";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_UPDATE_PROFILE_FULL_NAME"] = 506] = "ENTERPRISE_UPDATE_PROFILE_FULL_NAME";
    AnalyticIdentifier[AnalyticIdentifier["DATA_SOURCES_UPLOAD_ONE_SCHEMA_BUTTON"] = 507] = "DATA_SOURCES_UPLOAD_ONE_SCHEMA_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DETAIL_PROJECT_OWNER"] = 508] = "RD_PROJECT_DETAIL_PROJECT_OWNER";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DETAIL_PROJECT_OWNER_CLEAR_BUTTON"] = 509] = "RD_PROJECT_DETAIL_PROJECT_OWNER_CLEAR_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DETAIL_PROJECT_OWNER"] = 510] = "ASC_PROJECT_DETAIL_PROJECT_OWNER";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DETAIL_PROJECT_OWNER_CLEAR_BUTTON"] = 511] = "ASC_PROJECT_DETAIL_PROJECT_OWNER_CLEAR_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_LAYOUT_APPLY_WEIGHTED_CAPITALIZED_PERCENTAGE_TO_CONTRIBUTORS_BUTTON"] = 512] = "ASC_LAYOUT_APPLY_WEIGHTED_CAPITALIZED_PERCENTAGE_TO_CONTRIBUTORS_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_LAYOUT_APPLY_WEIGHTED_CAPITALIZED_PERCENTAGE_TO_CONTRIBUTORS_CANCEL_BUTTON"] = 513] = "ASC_LAYOUT_APPLY_WEIGHTED_CAPITALIZED_PERCENTAGE_TO_CONTRIBUTORS_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_COST_UPLOAD_PERIOD_INPUT"] = 514] = "ASC_COST_UPLOAD_PERIOD_INPUT";
    AnalyticIdentifier[AnalyticIdentifier["ASC_COST_UPLOAD_CANCEL_BUTTON"] = 515] = "ASC_COST_UPLOAD_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_COST_UPLOAD_CONTINUE_BUTTON"] = 516] = "ASC_COST_UPLOAD_CONTINUE_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DELETE_BUTTON"] = 517] = "RD_PROJECT_DELETE_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["RD_PROJECT_DELETE_CONFIRMATION_BUTTON"] = 518] = "RD_PROJECT_DELETE_CONFIRMATION_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["NAV_RD_TAX_CREDIT"] = 519] = "NAV_RD_TAX_CREDIT";
    AnalyticIdentifier[AnalyticIdentifier["NAV_ASC_350_40"] = 520] = "NAV_ASC_350_40";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_ASC_DATA_SOURCES"] = 521] = "ENTERPRISE_NAV_ASC_DATA_SOURCES";
    AnalyticIdentifier[AnalyticIdentifier["PRODUCT_SELECTION_MODAL_CANCEL_BUTTON"] = 522] = "PRODUCT_SELECTION_MODAL_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["PRODUCT_SELECTION_MODAL_SELECT_BUTTON"] = 523] = "PRODUCT_SELECTION_MODAL_SELECT_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["PRODUCT_SELECTION_MODAL_PRODUCT_SELECT"] = 524] = "PRODUCT_SELECTION_MODAL_PRODUCT_SELECT";
    AnalyticIdentifier[AnalyticIdentifier["FISCAL_PERIOD_MODAL_BACK_TO_PRODUCT_SELECTION"] = 525] = "FISCAL_PERIOD_MODAL_BACK_TO_PRODUCT_SELECTION";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DRAWER_APPROVAL_TAB"] = 526] = "ASC_PROJECT_DRAWER_APPROVAL_TAB";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_APPROVAL_PROJECT_OWNER"] = 527] = "ASC_PROJECT_APPROVAL_PROJECT_OWNER";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_APPROVAL_PROJECT_OWNER_CLEAR_BUTTON"] = 528] = "ASC_PROJECT_APPROVAL_PROJECT_OWNER_CLEAR_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_APPROVAL_COMMENT"] = 529] = "ASC_PROJECT_APPROVAL_COMMENT";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_APPROVAL_APPROVE_BUTTON"] = 530] = "ASC_PROJECT_APPROVAL_APPROVE_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_APPROVAL_RESET_BUTTON"] = 531] = "ASC_PROJECT_APPROVAL_RESET_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_APPROVAL_RESET_CONFIRM_BUTTON"] = 532] = "ASC_PROJECT_APPROVAL_RESET_CONFIRM_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_APPROVAL_RESET_CANCEL_BUTTON"] = 533] = "ASC_PROJECT_APPROVAL_RESET_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_SRED_PROJECTS"] = 534] = "ENTERPRISE_NAV_SRED_PROJECTS";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_CERIDIAN_DAYFORCE"] = 535] = "RTC_SELECT_LINK_CERIDIAN_DAYFORCE";
    AnalyticIdentifier[AnalyticIdentifier["LINK_CERIDIAN_DAYFORCE"] = 536] = "LINK_CERIDIAN_DAYFORCE";
    AnalyticIdentifier[AnalyticIdentifier["LINK_CERIDIAN_DAYFORCE_CANCEL"] = 537] = "LINK_CERIDIAN_DAYFORCE_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["LINK_CERIDIAN_DAYFORCE_TRY_AGAIN"] = 538] = "LINK_CERIDIAN_DAYFORCE_TRY_AGAIN";
    AnalyticIdentifier[AnalyticIdentifier["RTC_SELECT_LINK_JIRA_DATA_CENTER"] = 539] = "RTC_SELECT_LINK_JIRA_DATA_CENTER";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_NAV_SRED_PAYROLL_EXPENSES"] = 540] = "ENTERPRISE_NAV_SRED_PAYROLL_EXPENSES";
    AnalyticIdentifier[AnalyticIdentifier["SRED_PAYROLL_EXPENSE_LAYOUT_BULK_EDIT_IN_EXCEL_BTN"] = 541] = "SRED_PAYROLL_EXPENSE_LAYOUT_BULK_EDIT_IN_EXCEL_BTN";
    AnalyticIdentifier[AnalyticIdentifier["SRED_PAYROLL_EXPENSE_LAYOUT_CONNECT_PROVIDER_BTN"] = 542] = "SRED_PAYROLL_EXPENSE_LAYOUT_CONNECT_PROVIDER_BTN";
    AnalyticIdentifier[AnalyticIdentifier["ACCOUNTING_QRE_LAYOUT_BULK_EDIT_IN_EXCEL_BTN"] = 543] = "ACCOUNTING_QRE_LAYOUT_BULK_EDIT_IN_EXCEL_BTN";
    AnalyticIdentifier[AnalyticIdentifier["ACCOUNTING_QRE_LAYOUT_CONNECT_PROVIDER_BTN"] = 544] = "ACCOUNTING_QRE_LAYOUT_CONNECT_PROVIDER_BTN";
    AnalyticIdentifier[AnalyticIdentifier["SUPERVISOR_RD_PERCENTAGE_MODAL_GENERATE_PREVIEW_BTN"] = 545] = "SUPERVISOR_RD_PERCENTAGE_MODAL_GENERATE_PREVIEW_BTN";
    AnalyticIdentifier[AnalyticIdentifier["SUPERVISOR_RD_PERCENTAGE_MODAL_DOWNLOAD_PREVIEW_BTN"] = 546] = "SUPERVISOR_RD_PERCENTAGE_MODAL_DOWNLOAD_PREVIEW_BTN";
    AnalyticIdentifier[AnalyticIdentifier["SUPERVISOR_RD_PERCENTAGE_MODAL_CANCEL_BTN"] = 547] = "SUPERVISOR_RD_PERCENTAGE_MODAL_CANCEL_BTN";
    AnalyticIdentifier[AnalyticIdentifier["SUPERVISOR_RD_PERCENTAGE_MODAL_APPLY_BTN"] = 548] = "SUPERVISOR_RD_PERCENTAGE_MODAL_APPLY_BTN";
    AnalyticIdentifier[AnalyticIdentifier["ENTERPRISE_REDUCED_CREDIT"] = 549] = "ENTERPRISE_REDUCED_CREDIT";
    AnalyticIdentifier[AnalyticIdentifier["ACCOUNTING_QRE_LAYOUT_SET_RD_PERCENTAGE_BTN"] = 550] = "ACCOUNTING_QRE_LAYOUT_SET_RD_PERCENTAGE_BTN";
    AnalyticIdentifier[AnalyticIdentifier["ACCOUNTING_QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_CANCEL_BTN"] = 551] = "ACCOUNTING_QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_CANCEL_BTN";
    AnalyticIdentifier[AnalyticIdentifier["ACCOUNTING_QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_SUBMIT_BTN"] = 552] = "ACCOUNTING_QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_SUBMIT_BTN";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DETAIL_CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE"] = 553] = "ASC_PROJECT_DETAIL_CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_BULK_UPLOAD_INSTRUCTION_UPLOAD_BUTTON"] = 554] = "ASC_PROJECT_BULK_UPLOAD_INSTRUCTION_UPLOAD_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_BULK_UPLOAD_INSTRUCTION_CANCEL_BUTTON"] = 555] = "ASC_PROJECT_BULK_UPLOAD_INSTRUCTION_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_DRAWER_MATCHING_TAB"] = 556] = "ASC_PROJECT_DRAWER_MATCHING_TAB";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_MATCHING_TEAMS"] = 557] = "ASC_PROJECT_MATCHING_TEAMS";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PROJECT_MATCHING_LAUNCH_DATE"] = 558] = "ASC_PROJECT_MATCHING_LAUNCH_DATE";
    AnalyticIdentifier[AnalyticIdentifier["ASC_PERIOD_LOCK_TOGGLE"] = 559] = "ASC_PERIOD_LOCK_TOGGLE";
    AnalyticIdentifier[AnalyticIdentifier["LINK_GITHUB_METHOD_CONTINUE"] = 560] = "LINK_GITHUB_METHOD_CONTINUE";
    AnalyticIdentifier[AnalyticIdentifier["LINK_GITHUB_METHOD_CANCEL"] = 561] = "LINK_GITHUB_METHOD_CANCEL";
    AnalyticIdentifier[AnalyticIdentifier["ADD_TICKETING_CONNECTION"] = 562] = "ADD_TICKETING_CONNECTION";
    AnalyticIdentifier[AnalyticIdentifier["DISCONNECT_TICKETING_CONNECTION_CONFIRM_BUTTON"] = 563] = "DISCONNECT_TICKETING_CONNECTION_CONFIRM_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["DISCONNECT_TICKETING_CONNECTION_CANCEL_BUTTON"] = 564] = "DISCONNECT_TICKETING_CONNECTION_CANCEL_BUTTON";
    AnalyticIdentifier[AnalyticIdentifier["CHECK_GITHUB_APP_CONNECTION"] = 565] = "CHECK_GITHUB_APP_CONNECTION";
})(AnalyticIdentifier || (analyticIdentifier_enum.AnalyticIdentifier = AnalyticIdentifier = {}));

var expenseCategory_enums = {};

Object.defineProperty(expenseCategory_enums, "__esModule", { value: true });
expenseCategory_enums.ExpenseCategory = void 0;
/**
 * Should only be used in the back-end
 */
var ExpenseCategory;
(function (ExpenseCategory) {
    ExpenseCategory[ExpenseCategory["EMPLOYEE"] = 0] = "EMPLOYEE";
    ExpenseCategory[ExpenseCategory["CONTRACTORS"] = 1] = "CONTRACTORS";
    ExpenseCategory[ExpenseCategory["CLOUD"] = 2] = "CLOUD";
    ExpenseCategory[ExpenseCategory["LEGAL"] = 3] = "LEGAL";
    ExpenseCategory[ExpenseCategory["SUPPLIES"] = 4] = "SUPPLIES";
})(ExpenseCategory || (expenseCategory_enums.ExpenseCategory = ExpenseCategory = {}));

var enums_utils = {};

Object.defineProperty(enums_utils, "__esModule", { value: true });
enums_utils.convertExpenseCategoryToNum = void 0;
const expense_category_enums_1 = expenseCategory_enums;
/**
 * Use this function to convert between back-end ExpenseCategory values and string
 * generated through graphql layer
 * @returns -1 if undefined, otherwise the number value of the enum
 */
const convertExpenseCategoryToNum = (expenseCategory) => {
    if (!expenseCategory) {
        return -1;
    }
    return expense_category_enums_1.ExpenseCategory[expenseCategory];
};
enums_utils.convertExpenseCategoryToNum = convertExpenseCategoryToNum;

var quickbooks = {};

var quickbooksMonth_enum = {};

Object.defineProperty(quickbooksMonth_enum, "__esModule", { value: true });
quickbooksMonth_enum.QuickbooksMonth = void 0;
var QuickbooksMonth;
(function (QuickbooksMonth) {
    QuickbooksMonth["Jan"] = "Jan";
    QuickbooksMonth["Feb"] = "Feb";
    QuickbooksMonth["Mar"] = "Mar";
    QuickbooksMonth["Apr"] = "Apr";
    QuickbooksMonth["May"] = "May";
    QuickbooksMonth["Jun"] = "Jun";
    QuickbooksMonth["Jul"] = "Jul";
    QuickbooksMonth["Aug"] = "Aug";
    QuickbooksMonth["Sep"] = "Sep";
    QuickbooksMonth["Oct"] = "Oct";
    QuickbooksMonth["Nov"] = "Nov";
    QuickbooksMonth["Dec"] = "Dec";
})(QuickbooksMonth || (quickbooksMonth_enum.QuickbooksMonth = QuickbooksMonth = {}));

var quickbooksQueryField_enum = {};

Object.defineProperty(quickbooksQueryField_enum, "__esModule", { value: true });
quickbooksQueryField_enum.QuickbooksQueryField = void 0;
var QuickbooksQueryField;
(function (QuickbooksQueryField) {
    QuickbooksQueryField["CONNECTED_STATUS"] = "connected_accounting";
})(QuickbooksQueryField || (quickbooksQueryField_enum.QuickbooksQueryField = QuickbooksQueryField = {}));

var quickbooksConnectionStatus_enum = {};

Object.defineProperty(quickbooksConnectionStatus_enum, "__esModule", { value: true });
quickbooksConnectionStatus_enum.QuickbooksConnectionStatus = void 0;
var QuickbooksConnectionStatus;
(function (QuickbooksConnectionStatus) {
    QuickbooksConnectionStatus["SUCCESS"] = "successful";
    QuickbooksConnectionStatus["FAILED"] = "failed";
})(QuickbooksConnectionStatus || (quickbooksConnectionStatus_enum.QuickbooksConnectionStatus = QuickbooksConnectionStatus = {}));

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.QuickbooksConnectionStatus = exports.QuickbooksQueryField = exports.QuickbooksMonth = void 0;
	var quickbooks_month_enum_1 = quickbooksMonth_enum;
	Object.defineProperty(exports, "QuickbooksMonth", { enumerable: true, get: function () { return quickbooks_month_enum_1.QuickbooksMonth; } });
	var quickbooks_query_field_enum_1 = quickbooksQueryField_enum;
	Object.defineProperty(exports, "QuickbooksQueryField", { enumerable: true, get: function () { return quickbooks_query_field_enum_1.QuickbooksQueryField; } });
	var quickbooks_connection_status_enum_1 = quickbooksConnectionStatus_enum;
	Object.defineProperty(exports, "QuickbooksConnectionStatus", { enumerable: true, get: function () { return quickbooks_connection_status_enum_1.QuickbooksConnectionStatus; } });
} (quickbooks));

var githubApp = {};

var githubAppInstallQueryField_enum = {};

Object.defineProperty(githubAppInstallQueryField_enum, "__esModule", { value: true });
githubAppInstallQueryField_enum.GitHubAppInstallQueryField = void 0;
// Query param the GitHub App install Setup URL callback appends to the frontend redirect so the
// connect-provider slide-over can show the install outcome. Mirrors the connected_payroll /
// connected_version_control conventions used by the other connect flows.
var GitHubAppInstallQueryField;
(function (GitHubAppInstallQueryField) {
    GitHubAppInstallQueryField["CONNECTED_STATUS"] = "connected_github_app";
})(GitHubAppInstallQueryField || (githubAppInstallQueryField_enum.GitHubAppInstallQueryField = GitHubAppInstallQueryField = {}));

var githubAppInstallStatus_enum = {};

Object.defineProperty(githubAppInstallStatus_enum, "__esModule", { value: true });
githubAppInstallStatus_enum.GitHubAppInstallStatus = void 0;
var GitHubAppInstallStatus;
(function (GitHubAppInstallStatus) {
    // Install initiated and the PENDING connection was recorded; activation is webhook-driven.
    GitHubAppInstallStatus["PENDING"] = "pending";
    // The installer is not an org owner and could only *request* the install. GitHub fires no
    // callback when an owner later approves it, so unlike PENDING this will not resolve by polling.
    // The user completes the claim later through the standalone authorization recovery flow.
    GitHubAppInstallStatus["AWAITING_APPROVAL"] = "awaiting_approval";
    GitHubAppInstallStatus["FAILED"] = "failed";
})(GitHubAppInstallStatus || (githubAppInstallStatus_enum.GitHubAppInstallStatus = GitHubAppInstallStatus = {}));

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.GitHubAppInstallStatus = exports.GitHubAppInstallQueryField = void 0;
	var github_app_install_query_field_enum_1 = githubAppInstallQueryField_enum;
	Object.defineProperty(exports, "GitHubAppInstallQueryField", { enumerable: true, get: function () { return github_app_install_query_field_enum_1.GitHubAppInstallQueryField; } });
	var github_app_install_status_enum_1 = githubAppInstallStatus_enum;
	Object.defineProperty(exports, "GitHubAppInstallStatus", { enumerable: true, get: function () { return github_app_install_status_enum_1.GitHubAppInstallStatus; } });
} (githubApp));

var sso = {};

var ssoQueryField_enum = {};

Object.defineProperty(ssoQueryField_enum, "__esModule", { value: true });
ssoQueryField_enum.SsoQueryField = void 0;
var SsoQueryField;
(function (SsoQueryField) {
    SsoQueryField["ERROR"] = "sso_error";
    SsoQueryField["TOKEN"] = "firebase_custom_token";
    SsoQueryField["SSO_PROVIDER"] = "sso_provider";
})(SsoQueryField || (ssoQueryField_enum.SsoQueryField = SsoQueryField = {}));

var ssoError_enum = {};

Object.defineProperty(ssoError_enum, "__esModule", { value: true });
ssoError_enum.SsoError = void 0;
/**
 * These are the expected SSO errors and not a complete list.
 */
var SsoError;
(function (SsoError) {
    SsoError["NEEDS_EMAIL_VERIFICATION"] = "needs_email_verification";
    SsoError["QUICKBOOKS_FAILED"] = "quickbooks_failed";
    SsoError["UNKNOWN_ERROR"] = "unknown_error";
})(SsoError || (ssoError_enum.SsoError = SsoError = {}));

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.SsoError = exports.SsoQueryField = void 0;
	var sso_query_field_enum_1 = ssoQueryField_enum;
	Object.defineProperty(exports, "SsoQueryField", { enumerable: true, get: function () { return sso_query_field_enum_1.SsoQueryField; } });
	var sso_error_enum_1 = ssoError_enum;
	Object.defineProperty(exports, "SsoError", { enumerable: true, get: function () { return sso_error_enum_1.SsoError; } });
} (sso));

var neoTaxEnvironment_enum = {};

Object.defineProperty(neoTaxEnvironment_enum, "__esModule", { value: true });
neoTaxEnvironment_enum.NeoTaxEnvironment = void 0;
// Allowed values of the NEO_TAX_ENV environment variable.
var NeoTaxEnvironment;
(function (NeoTaxEnvironment) {
    NeoTaxEnvironment["TEST"] = "test";
    NeoTaxEnvironment["LOCAL"] = "local";
    NeoTaxEnvironment["DEPLOY_PREVIEW"] = "deploy-preview";
    NeoTaxEnvironment["PRODUCTION_PREVIEW"] = "production-preview";
    NeoTaxEnvironment["DEVELOPMENT"] = "development";
    NeoTaxEnvironment["STAGING"] = "staging";
    NeoTaxEnvironment["PRODUCTION"] = "production";
    NeoTaxEnvironment["TERRAFORM_LOCAL"] = "terraform-local";
    NeoTaxEnvironment["THOMSON_REUTERS"] = "thomson-reuters";
    NeoTaxEnvironment["NARVAR"] = "narvar";
    NeoTaxEnvironment["ZELIS"] = "zelis";
    NeoTaxEnvironment["NBH"] = "nbh";
    NeoTaxEnvironment["ADOBE"] = "adobe";
    NeoTaxEnvironment["LENDINGCLUB"] = "lendingclub";
    NeoTaxEnvironment["SANDBOX"] = "sandbox";
    NeoTaxEnvironment["CAPITALONE"] = "capitalone";
    NeoTaxEnvironment["BULLHORN"] = "bullhorn";
    NeoTaxEnvironment["DEMO"] = "demo";
    NeoTaxEnvironment["QUALCOMM"] = "qualcomm";
    NeoTaxEnvironment["EXPEDIA"] = "expedia";
    NeoTaxEnvironment["SHELL"] = "shell";
})(NeoTaxEnvironment || (neoTaxEnvironment_enum.NeoTaxEnvironment = NeoTaxEnvironment = {}));

var serviceScope_enum = {};

Object.defineProperty(serviceScope_enum, "__esModule", { value: true });
serviceScope_enum.ServiceScope = void 0;
var ServiceScope;
(function (ServiceScope) {
    ServiceScope["API_GATEWAY"] = "API_GATEWAY";
    ServiceScope["PROVIDER_ADAPTER"] = "PROVIDER_ADAPTER";
    ServiceScope["PREDICTION_SERVICE"] = "PREDICTION_SERVICE";
})(ServiceScope || (serviceScope_enum.ServiceScope = ServiceScope = {}));

var accountClassification_enum = {};

Object.defineProperty(accountClassification_enum, "__esModule", { value: true });
accountClassification_enum.AccountClassification = void 0;
var AccountClassification;
(function (AccountClassification) {
    AccountClassification["ASSET"] = "Asset";
    AccountClassification["EQUITY"] = "Equity";
    AccountClassification["EXPENSE"] = "Expense";
    AccountClassification["LIABILITY"] = "Liability";
    AccountClassification["REVENUE"] = "Revenue";
})(AccountClassification || (accountClassification_enum.AccountClassification = AccountClassification = {}));

var accountType_enum = {};

Object.defineProperty(accountType_enum, "__esModule", { value: true });
accountType_enum.AccountType = void 0;
// Superset of account type from Quickbooks and NetSuite
// Note: these are case-sensitive!
// QBO docs: https://developer.intuit.com/app/developer/qbo/docs/api/accounting/most-commonly-used/account
// NetSuite docs: https://docs.oracle.com/en/cloud/saas/netsuite/ns-online-help/section_3947502870.html
var AccountType;
(function (AccountType) {
    AccountType["ACCOUNTS_PAYABLE"] = "Accounts Payable";
    AccountType["ACCOUNTS_RECEIVABLE"] = "Accounts Receivable";
    AccountType["BANK"] = "Bank";
    AccountType["COST_OF_GOODS_SOLD"] = "Cost of Goods Sold";
    AccountType["CREDIT_CARD"] = "Credit Card";
    AccountType["EQUITY"] = "Equity";
    AccountType["EXPENSE"] = "Expense";
    AccountType["FIXED_ASSET"] = "Fixed Asset";
    AccountType["INCOME"] = "Income";
    AccountType["LONG_TERM_LIABILITY"] = "Long Term Liability";
    AccountType["OTHER_ASSET"] = "Other Asset";
    AccountType["OTHER_CURRENT_ASSET"] = "Other Current Asset";
    AccountType["OTHER_CURRENT_LIABILITY"] = "Other Current Liability";
    AccountType["OTHER_EXPENSE"] = "Other Expense";
    AccountType["OTHER_INCOME"] = "Other Income";
    AccountType["DEFERRED_REVENUE"] = "Deferred Revenue";
    AccountType["DEFERRED_EXPENSE"] = "Deferred Expense";
    AccountType["UNBILLED_RECEIVABLE"] = "Unbilled Receivable";
    AccountType["NON_POSTING"] = "Non Posting";
})(AccountType || (accountType_enum.AccountType = AccountType = {}));

var roundingMethod_enum = {};

Object.defineProperty(roundingMethod_enum, "__esModule", { value: true });
roundingMethod_enum.RoundingMethod = void 0;
var RoundingMethod;
(function (RoundingMethod) {
    RoundingMethod["ROUND"] = "ROUND";
    RoundingMethod["FLOOR"] = "FLOOR";
})(RoundingMethod || (roundingMethod_enum.RoundingMethod = RoundingMethod = {}));

var hardSciences_enum = {};

Object.defineProperty(hardSciences_enum, "__esModule", { value: true });
hardSciences_enum.HardSciences = void 0;
var HardSciences;
(function (HardSciences) {
    HardSciences["ENGINEERING"] = "ENGINEERING";
    HardSciences["PHYSICS"] = "PHYSICS";
    HardSciences["CHEMISTRY"] = "CHEMISTRY";
    HardSciences["BIOLOGY"] = "BIOLOGY";
    HardSciences["COMPUTER_SCIENCE"] = "COMPUTER_SCIENCE";
    HardSciences["NOT_TECHNICAL"] = "NOT_TECHNICAL";
})(HardSciences || (hardSciences_enum.HardSciences = HardSciences = {}));

var revenueSource_enum = {};

Object.defineProperty(revenueSource_enum, "__esModule", { value: true });
revenueSource_enum.getRevenueSourceRank = revenueSource_enum.RevenueSource = void 0;
var RevenueSource;
(function (RevenueSource) {
    RevenueSource["QUICKBOOKS_OR_XERO"] = "QUICKBOOKS/XERO";
    RevenueSource["SELF_REPORTED"] = "SELF_REPORTED";
    RevenueSource["RETOOL"] = "RETOOL";
})(RevenueSource || (revenueSource_enum.RevenueSource = RevenueSource = {}));
/**
 *
 * When a company has duplicate prior-year-revenue entries for the same tax year
 * (e.g. one from QuickBooks and one self-reported), we deduplicate to one per year.
 * If createdAt timestamps are identical (common in copyCompany), this priority
 * determines the winner: user-provided data beats automated imports.
 * Lower index = higher priority. Update when adding new RevenueSource values.
 */
const REVENUE_SOURCE_PRIORITY = [
    RevenueSource.SELF_REPORTED,
    RevenueSource.QUICKBOOKS_OR_XERO,
    RevenueSource.RETOOL,
];
const getRevenueSourceRank = (source) => {
    const index = REVENUE_SOURCE_PRIORITY.indexOf(source);
    return index === -1 ? Infinity : index;
};
revenueSource_enum.getRevenueSourceRank = getRevenueSourceRank;

var salesChannel_enum = {};

Object.defineProperty(salesChannel_enum, "__esModule", { value: true });
salesChannel_enum.SalesChannel = void 0;
var SalesChannel;
(function (SalesChannel) {
    SalesChannel["ACCOUNTANT"] = "accountant";
    SalesChannel["DIRECT"] = "direct";
    SalesChannel["PARTNER"] = "partner";
})(SalesChannel || (salesChannel_enum.SalesChannel = SalesChannel = {}));

var entityType_enum = {};

Object.defineProperty(entityType_enum, "__esModule", { value: true });
entityType_enum.EntityType = void 0;
var EntityType;
(function (EntityType) {
    EntityType["VENDOR"] = "Vendor";
    EntityType["CONTACT"] = "Contact";
    EntityType["EMPLOYEE"] = "Employee";
    EntityType["CUSTOM_JOB"] = "CustJob";
    EntityType["UNKNOWN"] = "Unknown";
})(EntityType || (entityType_enum.EntityType = EntityType = {}));

var accountingAcquisitionMethod_enum = {};

Object.defineProperty(accountingAcquisitionMethod_enum, "__esModule", { value: true });
accountingAcquisitionMethod_enum.AccountingAcquisitionMethod = void 0;
var AccountingAcquisitionMethod;
(function (AccountingAcquisitionMethod) {
    AccountingAcquisitionMethod["INTUIT"] = "INTUIT";
    AccountingAcquisitionMethod["MERGE_DEV"] = "MERGE_DEV";
    AccountingAcquisitionMethod["FILE"] = "FILE";
    AccountingAcquisitionMethod["MANUAL"] = "MANUAL";
})(AccountingAcquisitionMethod || (accountingAcquisitionMethod_enum.AccountingAcquisitionMethod = AccountingAcquisitionMethod = {}));

var dateMonth_enum = {};

Object.defineProperty(dateMonth_enum, "__esModule", { value: true });
dateMonth_enum.DateMonth = void 0;
var DateMonth;
(function (DateMonth) {
    DateMonth[DateMonth["January"] = 0] = "January";
    DateMonth[DateMonth["February"] = 1] = "February";
    DateMonth[DateMonth["March"] = 2] = "March";
    DateMonth[DateMonth["April"] = 3] = "April";
    DateMonth[DateMonth["May"] = 4] = "May";
    DateMonth[DateMonth["June"] = 5] = "June";
    DateMonth[DateMonth["July"] = 6] = "July";
    DateMonth[DateMonth["August"] = 7] = "August";
    DateMonth[DateMonth["September"] = 8] = "September";
    DateMonth[DateMonth["October"] = 9] = "October";
    DateMonth[DateMonth["November"] = 10] = "November";
    DateMonth[DateMonth["December"] = 11] = "December";
})(DateMonth || (dateMonth_enum.DateMonth = DateMonth = {}));

var payrollAcquisitionMethod_enum = {};

Object.defineProperty(payrollAcquisitionMethod_enum, "__esModule", { value: true });
payrollAcquisitionMethod_enum.PayrollAcquisitionMethod = void 0;
var PayrollAcquisitionMethod;
(function (PayrollAcquisitionMethod) {
    PayrollAcquisitionMethod["GUSTO"] = "GUSTO";
    PayrollAcquisitionMethod["FINCH"] = "FINCH";
    PayrollAcquisitionMethod["FILE"] = "FILE";
    PayrollAcquisitionMethod["MANUAL"] = "MANUAL";
})(PayrollAcquisitionMethod || (payrollAcquisitionMethod_enum.PayrollAcquisitionMethod = PayrollAcquisitionMethod = {}));

var accountingProvider_enum = {};

Object.defineProperty(accountingProvider_enum, "__esModule", { value: true });
accountingProvider_enum.AccountingProvider = void 0;
var AccountingProvider;
(function (AccountingProvider) {
    AccountingProvider["NETSUITE"] = "NETSUITE";
    AccountingProvider["QUICKBOOKS"] = "QUICKBOOKS";
    AccountingProvider["SELF_REPORTED"] = "SELF_REPORTED";
})(AccountingProvider || (accountingProvider_enum.AccountingProvider = AccountingProvider = {}));

var payrollProvider_enum = {};

Object.defineProperty(payrollProvider_enum, "__esModule", { value: true });
payrollProvider_enum.PayrollProvider = void 0;
var PayrollProvider;
(function (PayrollProvider) {
    PayrollProvider["ADP_RUN"] = "ADP_RUN";
    PayrollProvider["ADP_WORKFORCE_NOW"] = "ADP_WORKFORCE_NOW";
    PayrollProvider["CERIDIAN_DAYFORCE"] = "CERIDIAN_DAYFORCE";
    PayrollProvider["GUSTO"] = "GUSTO";
    PayrollProvider["JUSTWORKS"] = "JUSTWORKS";
    PayrollProvider["PAYCHEX_FLEX"] = "PAYCHEX_FLEX";
    PayrollProvider["PAYLOCITY"] = "PAYLOCITY";
    PayrollProvider["QUICKBOOKS"] = "QUICKBOOKS";
    PayrollProvider["RIPPLING"] = "RIPPLING";
    PayrollProvider["TRINET"] = "TRINET";
    PayrollProvider["WORKDAY"] = "WORKDAY";
    PayrollProvider["ZENEFITS"] = "ZENEFITS";
    PayrollProvider["SELF_REPORTED"] = "SELF_REPORTED";
})(PayrollProvider || (payrollProvider_enum.PayrollProvider = PayrollProvider = {}));

var wageType_enum = {};

Object.defineProperty(wageType_enum, "__esModule", { value: true });
wageType_enum.WageType = void 0;
var WageType;
(function (WageType) {
    WageType["W2"] = "W2";
    WageType["CONTRACTOR"] = "CONTRACTOR";
})(WageType || (wageType_enum.WageType = WageType = {}));

var serviceOperationalStatus_enum = {};

Object.defineProperty(serviceOperationalStatus_enum, "__esModule", { value: true });
serviceOperationalStatus_enum.ServiceOperationalStatus = void 0;
var ServiceOperationalStatus;
(function (ServiceOperationalStatus) {
    ServiceOperationalStatus["OPERATIONAL"] = "operational";
    ServiceOperationalStatus["DEGRADED"] = "degraded";
    ServiceOperationalStatus["OUTAGE"] = "outage";
})(ServiceOperationalStatus || (serviceOperationalStatus_enum.ServiceOperationalStatus = ServiceOperationalStatus = {}));

var corporationType_enum = {};

Object.defineProperty(corporationType_enum, "__esModule", { value: true });
corporationType_enum.CorporationType = void 0;
var CorporationType;
(function (CorporationType) {
    CorporationType["llc"] = "llc";
    CorporationType["sole_proprietor"] = "sole_proprietor";
    CorporationType["non_profit"] = "non_profit";
    CorporationType["partnership"] = "partnership";
    CorporationType["cooperative"] = "cooperative";
    CorporationType["s_corporation"] = "s_corporation";
    CorporationType["c_corporation"] = "c_corporation";
    CorporationType["b_corporation"] = "b_corporation";
})(CorporationType || (corporationType_enum.CorporationType = CorporationType = {}));

var eligibilityStatus_enum = {};

Object.defineProperty(eligibilityStatus_enum, "__esModule", { value: true });
eligibilityStatus_enum.EligibilityStatus = void 0;
var EligibilityStatus;
(function (EligibilityStatus) {
    EligibilityStatus["ELIGIBILITY_PENDING"] = "Decision Pending";
    EligibilityStatus["ELIGIBLE"] = "Qualified";
    EligibilityStatus["PROBABLY_ELIGIBLE"] = "Probably Qualified";
    EligibilityStatus["NOT_ELIGIBLE"] = "Not Qualified";
})(EligibilityStatus || (eligibilityStatus_enum.EligibilityStatus = EligibilityStatus = {}));

var companyEntityType_enum = {};

Object.defineProperty(companyEntityType_enum, "__esModule", { value: true });
companyEntityType_enum.CompanyEntityType = void 0;
var CompanyEntityType;
(function (CompanyEntityType) {
    CompanyEntityType["C_CORPORATION"] = "C-Corp (Form 1120)";
    CompanyEntityType["B_CORPORATION"] = "B-Corp (Form 1120)";
    CompanyEntityType["S_CORPORATION"] = "S-Corp (Form 1120-S)";
    CompanyEntityType["PARTNERSHIP"] = "Partnership (Form 1065)";
    CompanyEntityType["LLC_AS_C_CORPORATION"] = "LLC taxed as C-Corp (Form 1120)";
    CompanyEntityType["LLC_AS_S_CORPORATION"] = "LLC taxed as S-Corp (Form 1120-S)";
    CompanyEntityType["LLC_AS_PARTNERSHIP"] = "LLC taxed as Partnership (Form 1065)";
    CompanyEntityType["LLC_AS_SOLE_PROPRIETORSHIP"] = "LLC taxed as Sole Proprietorship";
})(CompanyEntityType || (companyEntityType_enum.CompanyEntityType = CompanyEntityType = {}));

var valueType_enum = {};

Object.defineProperty(valueType_enum, "__esModule", { value: true });
valueType_enum.ValueType = void 0;
var ValueType;
(function (ValueType) {
    ValueType["PERCENT"] = "PERCENT";
    ValueType["FRACTION"] = "FRACTION";
    ValueType["DOLLAR"] = "DOLLAR";
    ValueType["CENTS"] = "CENTS";
    ValueType["TEXT"] = "TEXT";
})(ValueType || (valueType_enum.ValueType = ValueType = {}));

var errorStatusCode = {};

Object.defineProperty(errorStatusCode, "__esModule", { value: true });
errorStatusCode.ErrorStatusCode = void 0;
/** Should be used for HTTP REST requests, not GraphQL. */
var ErrorStatusCode;
(function (ErrorStatusCode) {
    ErrorStatusCode["INTERNAL_SERVER_ERROR"] = "INTERNAL_SERVER_ERROR";
    ErrorStatusCode["OUTDATED_VERSION"] = "OUTDATED_VERSION";
    ErrorStatusCode["TOO_MANY_REQUESTS"] = "TOO_MANY_REQUESTS";
    ErrorStatusCode["UNAUTHENTICATED"] = "UNAUTHENTICATED";
    ErrorStatusCode["UNAUTHORIZED"] = "UNAUTHORIZED";
})(ErrorStatusCode || (errorStatusCode.ErrorStatusCode = ErrorStatusCode = {}));

var dayOfWeek_enum = {};

Object.defineProperty(dayOfWeek_enum, "__esModule", { value: true });
dayOfWeek_enum.DayOfWeek = void 0;
// Based on Dayjs enumeration of days implemented in date.utils
var DayOfWeek;
(function (DayOfWeek) {
    DayOfWeek[DayOfWeek["SUNDAY"] = 0] = "SUNDAY";
    DayOfWeek[DayOfWeek["MONDAY"] = 1] = "MONDAY";
    DayOfWeek[DayOfWeek["TUESDAY"] = 2] = "TUESDAY";
    DayOfWeek[DayOfWeek["WEDNESDAY"] = 3] = "WEDNESDAY";
    DayOfWeek[DayOfWeek["THURSDAY"] = 4] = "THURSDAY";
    DayOfWeek[DayOfWeek["FRIDAY"] = 5] = "FRIDAY";
    DayOfWeek[DayOfWeek["SATURDAY"] = 6] = "SATURDAY";
})(DayOfWeek || (dayOfWeek_enum.DayOfWeek = DayOfWeek = {}));

var payrollMonthExpenseProvider_enum = {};

Object.defineProperty(payrollMonthExpenseProvider_enum, "__esModule", { value: true });
payrollMonthExpenseProvider_enum.PayrollMonthExpenseProvider = void 0;
var PayrollMonthExpenseProvider;
(function (PayrollMonthExpenseProvider) {
    PayrollMonthExpenseProvider["SELF_REPORTED"] = "SELF_REPORTED";
})(PayrollMonthExpenseProvider || (payrollMonthExpenseProvider_enum.PayrollMonthExpenseProvider = PayrollMonthExpenseProvider = {}));

var periodName_enum = {};

var zod = {};

var external = {};

var core$4 = {};

var core$3 = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.globalConfig = exports.$ZodEncodeError = exports.$ZodAsyncError = exports.$brand = exports.NEVER = void 0;
	exports.$constructor = $constructor;
	exports.config = config;
	/** A special constant with type `never` */
	exports.NEVER = Object.freeze({
	    status: "aborted",
	});
	function $constructor(name, initializer, params) {
	    function init(inst, def) {
	        var _a;
	        Object.defineProperty(inst, "_zod", {
	            value: inst._zod ?? {},
	            enumerable: false,
	        });
	        (_a = inst._zod).traits ?? (_a.traits = new Set());
	        inst._zod.traits.add(name);
	        initializer(inst, def);
	        // support prototype modifications
	        for (const k in _.prototype) {
	            if (!(k in inst))
	                Object.defineProperty(inst, k, { value: _.prototype[k].bind(inst) });
	        }
	        inst._zod.constr = _;
	        inst._zod.def = def;
	    }
	    // doesn't work if Parent has a constructor with arguments
	    const Parent = params?.Parent ?? Object;
	    class Definition extends Parent {
	    }
	    Object.defineProperty(Definition, "name", { value: name });
	    function _(def) {
	        var _a;
	        const inst = params?.Parent ? new Definition() : this;
	        init(inst, def);
	        (_a = inst._zod).deferred ?? (_a.deferred = []);
	        for (const fn of inst._zod.deferred) {
	            fn();
	        }
	        return inst;
	    }
	    Object.defineProperty(_, "init", { value: init });
	    Object.defineProperty(_, Symbol.hasInstance, {
	        value: (inst) => {
	            if (params?.Parent && inst instanceof params.Parent)
	                return true;
	            return inst?._zod?.traits?.has(name);
	        },
	    });
	    Object.defineProperty(_, "name", { value: name });
	    return _;
	}
	//////////////////////////////   UTILITIES   ///////////////////////////////////////
	exports.$brand = Symbol("zod_brand");
	class $ZodAsyncError extends Error {
	    constructor() {
	        super(`Encountered Promise during synchronous parse. Use .parseAsync() instead.`);
	    }
	}
	exports.$ZodAsyncError = $ZodAsyncError;
	class $ZodEncodeError extends Error {
	    constructor(name) {
	        super(`Encountered unidirectional transform during encode: ${name}`);
	        this.name = "ZodEncodeError";
	    }
	}
	exports.$ZodEncodeError = $ZodEncodeError;
	exports.globalConfig = {};
	function config(newConfig) {
	    if (newConfig)
	        Object.assign(exports.globalConfig, newConfig);
	    return exports.globalConfig;
	}
} (core$3));

var parse$3 = {};

var errors$3 = {};

var util$3 = {};

Object.defineProperty(util$3, "__esModule", { value: true });
util$3.Class = util$3.BIGINT_FORMAT_RANGES = util$3.NUMBER_FORMAT_RANGES = util$3.primitiveTypes = util$3.propertyKeyTypes = util$3.getParsedType = util$3.allowsEval = util$3.captureStackTrace = void 0;
util$3.assertEqual = assertEqual;
util$3.assertNotEqual = assertNotEqual;
util$3.assertIs = assertIs;
util$3.assertNever = assertNever;
util$3.assert = assert;
util$3.getEnumValues = getEnumValues;
util$3.joinValues = joinValues;
util$3.jsonStringifyReplacer = jsonStringifyReplacer;
util$3.cached = cached;
util$3.nullish = nullish;
util$3.cleanRegex = cleanRegex;
util$3.floatSafeRemainder = floatSafeRemainder;
util$3.defineLazy = defineLazy;
util$3.objectClone = objectClone;
util$3.assignProp = assignProp;
util$3.mergeDefs = mergeDefs;
util$3.cloneDef = cloneDef;
util$3.getElementAtPath = getElementAtPath;
util$3.promiseAllObject = promiseAllObject;
util$3.randomString = randomString;
util$3.esc = esc;
util$3.isObject = isObject$c;
util$3.isPlainObject = isPlainObject$3;
util$3.shallowClone = shallowClone;
util$3.numKeys = numKeys;
util$3.escapeRegex = escapeRegex;
util$3.clone = clone$1;
util$3.normalizeParams = normalizeParams;
util$3.createTransparentProxy = createTransparentProxy;
util$3.stringifyPrimitive = stringifyPrimitive;
util$3.optionalKeys = optionalKeys;
util$3.pick = pick;
util$3.omit = omit$2;
util$3.extend = extend$1;
util$3.safeExtend = safeExtend;
util$3.merge = merge$2;
util$3.partial = partial;
util$3.required = required;
util$3.aborted = aborted;
util$3.prefixIssues = prefixIssues;
util$3.unwrapMessage = unwrapMessage;
util$3.finalizeIssue = finalizeIssue;
util$3.getSizableOrigin = getSizableOrigin;
util$3.getLengthableOrigin = getLengthableOrigin;
util$3.issue = issue;
util$3.cleanEnum = cleanEnum;
util$3.base64ToUint8Array = base64ToUint8Array;
util$3.uint8ArrayToBase64 = uint8ArrayToBase64;
util$3.base64urlToUint8Array = base64urlToUint8Array;
util$3.uint8ArrayToBase64url = uint8ArrayToBase64url;
util$3.hexToUint8Array = hexToUint8Array;
util$3.uint8ArrayToHex = uint8ArrayToHex;
// functions
function assertEqual(val) {
    return val;
}
function assertNotEqual(val) {
    return val;
}
function assertIs(_arg) { }
function assertNever(_x) {
    throw new Error();
}
function assert(_) { }
function getEnumValues(entries) {
    const numericValues = Object.values(entries).filter((v) => typeof v === "number");
    const values = Object.entries(entries)
        .filter(([k, _]) => numericValues.indexOf(+k) === -1)
        .map(([_, v]) => v);
    return values;
}
function joinValues(array, separator = "|") {
    return array.map((val) => stringifyPrimitive(val)).join(separator);
}
function jsonStringifyReplacer(_, value) {
    if (typeof value === "bigint")
        return value.toString();
    return value;
}
function cached(getter) {
    return {
        get value() {
            {
                const value = getter();
                Object.defineProperty(this, "value", { value });
                return value;
            }
        },
    };
}
function nullish(input) {
    return input === null || input === undefined;
}
function cleanRegex(source) {
    const start = source.startsWith("^") ? 1 : 0;
    const end = source.endsWith("$") ? source.length - 1 : source.length;
    return source.slice(start, end);
}
function floatSafeRemainder(val, step) {
    const valDecCount = (val.toString().split(".")[1] || "").length;
    const stepString = step.toString();
    let stepDecCount = (stepString.split(".")[1] || "").length;
    if (stepDecCount === 0 && /\d?e-\d?/.test(stepString)) {
        const match = stepString.match(/\d?e-(\d?)/);
        if (match?.[1]) {
            stepDecCount = Number.parseInt(match[1]);
        }
    }
    const decCount = valDecCount > stepDecCount ? valDecCount : stepDecCount;
    const valInt = Number.parseInt(val.toFixed(decCount).replace(".", ""));
    const stepInt = Number.parseInt(step.toFixed(decCount).replace(".", ""));
    return (valInt % stepInt) / 10 ** decCount;
}
const EVALUATING = Symbol("evaluating");
function defineLazy(object, key, getter) {
    let value = undefined;
    Object.defineProperty(object, key, {
        get() {
            if (value === EVALUATING) {
                // Circular reference detected, return undefined to break the cycle
                return undefined;
            }
            if (value === undefined) {
                value = EVALUATING;
                value = getter();
            }
            return value;
        },
        set(v) {
            Object.defineProperty(object, key, {
                value: v,
                // configurable: true,
            });
            // object[key] = v;
        },
        configurable: true,
    });
}
function objectClone(obj) {
    return Object.create(Object.getPrototypeOf(obj), Object.getOwnPropertyDescriptors(obj));
}
function assignProp(target, prop, value) {
    Object.defineProperty(target, prop, {
        value,
        writable: true,
        enumerable: true,
        configurable: true,
    });
}
function mergeDefs(...defs) {
    const mergedDescriptors = {};
    for (const def of defs) {
        const descriptors = Object.getOwnPropertyDescriptors(def);
        Object.assign(mergedDescriptors, descriptors);
    }
    return Object.defineProperties({}, mergedDescriptors);
}
function cloneDef(schema) {
    return mergeDefs(schema._zod.def);
}
function getElementAtPath(obj, path) {
    if (!path)
        return obj;
    return path.reduce((acc, key) => acc?.[key], obj);
}
function promiseAllObject(promisesObj) {
    const keys = Object.keys(promisesObj);
    const promises = keys.map((key) => promisesObj[key]);
    return Promise.all(promises).then((results) => {
        const resolvedObj = {};
        for (let i = 0; i < keys.length; i++) {
            resolvedObj[keys[i]] = results[i];
        }
        return resolvedObj;
    });
}
function randomString(length = 10) {
    const chars = "abcdefghijklmnopqrstuvwxyz";
    let str = "";
    for (let i = 0; i < length; i++) {
        str += chars[Math.floor(Math.random() * chars.length)];
    }
    return str;
}
function esc(str) {
    return JSON.stringify(str);
}
util$3.captureStackTrace = ("captureStackTrace" in Error ? Error.captureStackTrace : (..._args) => { });
function isObject$c(data) {
    return typeof data === "object" && data !== null && !Array.isArray(data);
}
util$3.allowsEval = cached(() => {
    // @ts-ignore
    if (typeof navigator !== "undefined" && navigator?.userAgent?.includes("Cloudflare")) {
        return false;
    }
    try {
        const F = Function;
        new F("");
        return true;
    }
    catch (_) {
        return false;
    }
});
function isPlainObject$3(o) {
    if (isObject$c(o) === false)
        return false;
    // modified constructor
    const ctor = o.constructor;
    if (ctor === undefined)
        return true;
    // modified prototype
    const prot = ctor.prototype;
    if (isObject$c(prot) === false)
        return false;
    // ctor doesn't have static `isPrototypeOf`
    if (Object.prototype.hasOwnProperty.call(prot, "isPrototypeOf") === false) {
        return false;
    }
    return true;
}
function shallowClone(o) {
    if (isPlainObject$3(o))
        return { ...o };
    return o;
}
function numKeys(data) {
    let keyCount = 0;
    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            keyCount++;
        }
    }
    return keyCount;
}
const getParsedType = (data) => {
    const t = typeof data;
    switch (t) {
        case "undefined":
            return "undefined";
        case "string":
            return "string";
        case "number":
            return Number.isNaN(data) ? "nan" : "number";
        case "boolean":
            return "boolean";
        case "function":
            return "function";
        case "bigint":
            return "bigint";
        case "symbol":
            return "symbol";
        case "object":
            if (Array.isArray(data)) {
                return "array";
            }
            if (data === null) {
                return "null";
            }
            if (data.then && typeof data.then === "function" && data.catch && typeof data.catch === "function") {
                return "promise";
            }
            if (typeof Map !== "undefined" && data instanceof Map) {
                return "map";
            }
            if (typeof Set !== "undefined" && data instanceof Set) {
                return "set";
            }
            if (typeof Date !== "undefined" && data instanceof Date) {
                return "date";
            }
            // @ts-ignore
            if (typeof File !== "undefined" && data instanceof File) {
                return "file";
            }
            return "object";
        default:
            throw new Error(`Unknown data type: ${t}`);
    }
};
util$3.getParsedType = getParsedType;
util$3.propertyKeyTypes = new Set(["string", "number", "symbol"]);
util$3.primitiveTypes = new Set(["string", "number", "bigint", "boolean", "symbol", "undefined"]);
function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
// zod-specific utils
function clone$1(inst, def, params) {
    const cl = new inst._zod.constr(def ?? inst._zod.def);
    if (!def || params?.parent)
        cl._zod.parent = inst;
    return cl;
}
function normalizeParams(_params) {
    const params = _params;
    if (!params)
        return {};
    if (typeof params === "string")
        return { error: () => params };
    if (params?.message !== undefined) {
        if (params?.error !== undefined)
            throw new Error("Cannot specify both `message` and `error` params");
        params.error = params.message;
    }
    delete params.message;
    if (typeof params.error === "string")
        return { ...params, error: () => params.error };
    return params;
}
function createTransparentProxy(getter) {
    let target;
    return new Proxy({}, {
        get(_, prop, receiver) {
            target ?? (target = getter());
            return Reflect.get(target, prop, receiver);
        },
        set(_, prop, value, receiver) {
            target ?? (target = getter());
            return Reflect.set(target, prop, value, receiver);
        },
        has(_, prop) {
            target ?? (target = getter());
            return Reflect.has(target, prop);
        },
        deleteProperty(_, prop) {
            target ?? (target = getter());
            return Reflect.deleteProperty(target, prop);
        },
        ownKeys(_) {
            target ?? (target = getter());
            return Reflect.ownKeys(target);
        },
        getOwnPropertyDescriptor(_, prop) {
            target ?? (target = getter());
            return Reflect.getOwnPropertyDescriptor(target, prop);
        },
        defineProperty(_, prop, descriptor) {
            target ?? (target = getter());
            return Reflect.defineProperty(target, prop, descriptor);
        },
    });
}
function stringifyPrimitive(value) {
    if (typeof value === "bigint")
        return value.toString() + "n";
    if (typeof value === "string")
        return `"${value}"`;
    return `${value}`;
}
function optionalKeys(shape) {
    return Object.keys(shape).filter((k) => {
        return shape[k]._zod.optin === "optional" && shape[k]._zod.optout === "optional";
    });
}
util$3.NUMBER_FORMAT_RANGES = {
    safeint: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    int32: [-2147483648, 2147483647],
    uint32: [0, 4294967295],
    float32: [-3.4028234663852886e38, 3.4028234663852886e38],
    float64: [-Number.MAX_VALUE, Number.MAX_VALUE],
};
util$3.BIGINT_FORMAT_RANGES = {
    int64: [/* @__PURE__*/ BigInt("-9223372036854775808"), /* @__PURE__*/ BigInt("9223372036854775807")],
    uint64: [/* @__PURE__*/ BigInt(0), /* @__PURE__*/ BigInt("18446744073709551615")],
};
function pick(schema, mask) {
    const currDef = schema._zod.def;
    const def = mergeDefs(schema._zod.def, {
        get shape() {
            const newShape = {};
            for (const key in mask) {
                if (!(key in currDef.shape)) {
                    throw new Error(`Unrecognized key: "${key}"`);
                }
                if (!mask[key])
                    continue;
                newShape[key] = currDef.shape[key];
            }
            assignProp(this, "shape", newShape); // self-caching
            return newShape;
        },
        checks: [],
    });
    return clone$1(schema, def);
}
function omit$2(schema, mask) {
    const currDef = schema._zod.def;
    const def = mergeDefs(schema._zod.def, {
        get shape() {
            const newShape = { ...schema._zod.def.shape };
            for (const key in mask) {
                if (!(key in currDef.shape)) {
                    throw new Error(`Unrecognized key: "${key}"`);
                }
                if (!mask[key])
                    continue;
                delete newShape[key];
            }
            assignProp(this, "shape", newShape); // self-caching
            return newShape;
        },
        checks: [],
    });
    return clone$1(schema, def);
}
function extend$1(schema, shape) {
    if (!isPlainObject$3(shape)) {
        throw new Error("Invalid input to extend: expected a plain object");
    }
    const checks = schema._zod.def.checks;
    const hasChecks = checks && checks.length > 0;
    if (hasChecks) {
        throw new Error("Object schemas containing refinements cannot be extended. Use `.safeExtend()` instead.");
    }
    const def = mergeDefs(schema._zod.def, {
        get shape() {
            const _shape = { ...schema._zod.def.shape, ...shape };
            assignProp(this, "shape", _shape); // self-caching
            return _shape;
        },
        checks: [],
    });
    return clone$1(schema, def);
}
function safeExtend(schema, shape) {
    if (!isPlainObject$3(shape)) {
        throw new Error("Invalid input to safeExtend: expected a plain object");
    }
    const def = {
        ...schema._zod.def,
        get shape() {
            const _shape = { ...schema._zod.def.shape, ...shape };
            assignProp(this, "shape", _shape); // self-caching
            return _shape;
        },
        checks: schema._zod.def.checks,
    };
    return clone$1(schema, def);
}
function merge$2(a, b) {
    const def = mergeDefs(a._zod.def, {
        get shape() {
            const _shape = { ...a._zod.def.shape, ...b._zod.def.shape };
            assignProp(this, "shape", _shape); // self-caching
            return _shape;
        },
        get catchall() {
            return b._zod.def.catchall;
        },
        checks: [], // delete existing checks
    });
    return clone$1(a, def);
}
function partial(Class, schema, mask) {
    const def = mergeDefs(schema._zod.def, {
        get shape() {
            const oldShape = schema._zod.def.shape;
            const shape = { ...oldShape };
            if (mask) {
                for (const key in mask) {
                    if (!(key in oldShape)) {
                        throw new Error(`Unrecognized key: "${key}"`);
                    }
                    if (!mask[key])
                        continue;
                    // if (oldShape[key]!._zod.optin === "optional") continue;
                    shape[key] = Class
                        ? new Class({
                            type: "optional",
                            innerType: oldShape[key],
                        })
                        : oldShape[key];
                }
            }
            else {
                for (const key in oldShape) {
                    // if (oldShape[key]!._zod.optin === "optional") continue;
                    shape[key] = Class
                        ? new Class({
                            type: "optional",
                            innerType: oldShape[key],
                        })
                        : oldShape[key];
                }
            }
            assignProp(this, "shape", shape); // self-caching
            return shape;
        },
        checks: [],
    });
    return clone$1(schema, def);
}
function required(Class, schema, mask) {
    const def = mergeDefs(schema._zod.def, {
        get shape() {
            const oldShape = schema._zod.def.shape;
            const shape = { ...oldShape };
            if (mask) {
                for (const key in mask) {
                    if (!(key in shape)) {
                        throw new Error(`Unrecognized key: "${key}"`);
                    }
                    if (!mask[key])
                        continue;
                    // overwrite with non-optional
                    shape[key] = new Class({
                        type: "nonoptional",
                        innerType: oldShape[key],
                    });
                }
            }
            else {
                for (const key in oldShape) {
                    // overwrite with non-optional
                    shape[key] = new Class({
                        type: "nonoptional",
                        innerType: oldShape[key],
                    });
                }
            }
            assignProp(this, "shape", shape); // self-caching
            return shape;
        },
        checks: [],
    });
    return clone$1(schema, def);
}
// invalid_type | too_big | too_small | invalid_format | not_multiple_of | unrecognized_keys | invalid_union | invalid_key | invalid_element | invalid_value | custom
function aborted(x, startIndex = 0) {
    if (x.aborted === true)
        return true;
    for (let i = startIndex; i < x.issues.length; i++) {
        if (x.issues[i]?.continue !== true) {
            return true;
        }
    }
    return false;
}
function prefixIssues(path, issues) {
    return issues.map((iss) => {
        var _a;
        (_a = iss).path ?? (_a.path = []);
        iss.path.unshift(path);
        return iss;
    });
}
function unwrapMessage(message) {
    return typeof message === "string" ? message : message?.message;
}
function finalizeIssue(iss, ctx, config) {
    const full = { ...iss, path: iss.path ?? [] };
    // for backwards compatibility
    if (!iss.message) {
        const message = unwrapMessage(iss.inst?._zod.def?.error?.(iss)) ??
            unwrapMessage(ctx?.error?.(iss)) ??
            unwrapMessage(config.customError?.(iss)) ??
            unwrapMessage(config.localeError?.(iss)) ??
            "Invalid input";
        full.message = message;
    }
    // delete (full as any).def;
    delete full.inst;
    delete full.continue;
    if (!ctx?.reportInput) {
        delete full.input;
    }
    return full;
}
function getSizableOrigin(input) {
    if (input instanceof Set)
        return "set";
    if (input instanceof Map)
        return "map";
    // @ts-ignore
    if (input instanceof File)
        return "file";
    return "unknown";
}
function getLengthableOrigin(input) {
    if (Array.isArray(input))
        return "array";
    if (typeof input === "string")
        return "string";
    return "unknown";
}
function issue(...args) {
    const [iss, input, inst] = args;
    if (typeof iss === "string") {
        return {
            message: iss,
            code: "custom",
            input,
            inst,
        };
    }
    return { ...iss };
}
function cleanEnum(obj) {
    return Object.entries(obj)
        .filter(([k, _]) => {
        // return true if NaN, meaning it's not a number, thus a string key
        return Number.isNaN(Number.parseInt(k, 10));
    })
        .map((el) => el[1]);
}
// Codec utility functions
function base64ToUint8Array(base64) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}
function uint8ArrayToBase64(bytes) {
    let binaryString = "";
    for (let i = 0; i < bytes.length; i++) {
        binaryString += String.fromCharCode(bytes[i]);
    }
    return btoa(binaryString);
}
function base64urlToUint8Array(base64url) {
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    return base64ToUint8Array(base64 + padding);
}
function uint8ArrayToBase64url(bytes) {
    return uint8ArrayToBase64(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}
function hexToUint8Array(hex) {
    const cleanHex = hex.replace(/^0x/, "");
    if (cleanHex.length % 2 !== 0) {
        throw new Error("Invalid hex string length");
    }
    const bytes = new Uint8Array(cleanHex.length / 2);
    for (let i = 0; i < cleanHex.length; i += 2) {
        bytes[i / 2] = Number.parseInt(cleanHex.slice(i, i + 2), 16);
    }
    return bytes;
}
function uint8ArrayToHex(bytes) {
    return Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}
// instanceof
class Class {
    constructor(..._args) { }
}
util$3.Class = Class;

var __createBinding$7 = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault$7 = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar$7 = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding$7(result, mod, k);
    __setModuleDefault$7(result, mod);
    return result;
};
Object.defineProperty(errors$3, "__esModule", { value: true });
errors$3.$ZodRealError = errors$3.$ZodError = void 0;
errors$3.flattenError = flattenError;
errors$3.formatError = formatError$1;
errors$3.treeifyError = treeifyError;
errors$3.toDotPath = toDotPath;
errors$3.prettifyError = prettifyError;
const core_js_1 = core$3;
const util$2 = __importStar$7(util$3);
const initializer$1 = (inst, def) => {
    inst.name = "$ZodError";
    Object.defineProperty(inst, "_zod", {
        value: inst._zod,
        enumerable: false,
    });
    Object.defineProperty(inst, "issues", {
        value: def,
        enumerable: false,
    });
    inst.message = JSON.stringify(def, util$2.jsonStringifyReplacer, 2);
    Object.defineProperty(inst, "toString", {
        value: () => inst.message,
        enumerable: false,
    });
};
errors$3.$ZodError = (0, core_js_1.$constructor)("$ZodError", initializer$1);
errors$3.$ZodRealError = (0, core_js_1.$constructor)("$ZodError", initializer$1, { Parent: Error });
function flattenError(error, mapper = (issue) => issue.message) {
    const fieldErrors = {};
    const formErrors = [];
    for (const sub of error.issues) {
        if (sub.path.length > 0) {
            fieldErrors[sub.path[0]] = fieldErrors[sub.path[0]] || [];
            fieldErrors[sub.path[0]].push(mapper(sub));
        }
        else {
            formErrors.push(mapper(sub));
        }
    }
    return { formErrors, fieldErrors };
}
function formatError$1(error, _mapper) {
    const mapper = _mapper ||
        function (issue) {
            return issue.message;
        };
    const fieldErrors = { _errors: [] };
    const processError = (error) => {
        for (const issue of error.issues) {
            if (issue.code === "invalid_union" && issue.errors.length) {
                issue.errors.map((issues) => processError({ issues }));
            }
            else if (issue.code === "invalid_key") {
                processError({ issues: issue.issues });
            }
            else if (issue.code === "invalid_element") {
                processError({ issues: issue.issues });
            }
            else if (issue.path.length === 0) {
                fieldErrors._errors.push(mapper(issue));
            }
            else {
                let curr = fieldErrors;
                let i = 0;
                while (i < issue.path.length) {
                    const el = issue.path[i];
                    const terminal = i === issue.path.length - 1;
                    if (!terminal) {
                        curr[el] = curr[el] || { _errors: [] };
                    }
                    else {
                        curr[el] = curr[el] || { _errors: [] };
                        curr[el]._errors.push(mapper(issue));
                    }
                    curr = curr[el];
                    i++;
                }
            }
        }
    };
    processError(error);
    return fieldErrors;
}
function treeifyError(error, _mapper) {
    const mapper = _mapper ||
        function (issue) {
            return issue.message;
        };
    const result = { errors: [] };
    const processError = (error, path = []) => {
        var _a, _b;
        for (const issue of error.issues) {
            if (issue.code === "invalid_union" && issue.errors.length) {
                // regular union error
                issue.errors.map((issues) => processError({ issues }, issue.path));
            }
            else if (issue.code === "invalid_key") {
                processError({ issues: issue.issues }, issue.path);
            }
            else if (issue.code === "invalid_element") {
                processError({ issues: issue.issues }, issue.path);
            }
            else {
                const fullpath = [...path, ...issue.path];
                if (fullpath.length === 0) {
                    result.errors.push(mapper(issue));
                    continue;
                }
                let curr = result;
                let i = 0;
                while (i < fullpath.length) {
                    const el = fullpath[i];
                    const terminal = i === fullpath.length - 1;
                    if (typeof el === "string") {
                        curr.properties ?? (curr.properties = {});
                        (_a = curr.properties)[el] ?? (_a[el] = { errors: [] });
                        curr = curr.properties[el];
                    }
                    else {
                        curr.items ?? (curr.items = []);
                        (_b = curr.items)[el] ?? (_b[el] = { errors: [] });
                        curr = curr.items[el];
                    }
                    if (terminal) {
                        curr.errors.push(mapper(issue));
                    }
                    i++;
                }
            }
        }
    };
    processError(error);
    return result;
}
/** Format a ZodError as a human-readable string in the following form.
 *
 * From
 *
 * ```ts
 * ZodError {
 *   issues: [
 *     {
 *       expected: 'string',
 *       code: 'invalid_type',
 *       path: [ 'username' ],
 *       message: 'Invalid input: expected string'
 *     },
 *     {
 *       expected: 'number',
 *       code: 'invalid_type',
 *       path: [ 'favoriteNumbers', 1 ],
 *       message: 'Invalid input: expected number'
 *     }
 *   ];
 * }
 * ```
 *
 * to
 *
 * ```
 * username
 *   ✖ Expected number, received string at "username
 * favoriteNumbers[0]
 *   ✖ Invalid input: expected number
 * ```
 */
function toDotPath(_path) {
    const segs = [];
    const path = _path.map((seg) => (typeof seg === "object" ? seg.key : seg));
    for (const seg of path) {
        if (typeof seg === "number")
            segs.push(`[${seg}]`);
        else if (typeof seg === "symbol")
            segs.push(`[${JSON.stringify(String(seg))}]`);
        else if (/[^\w$]/.test(seg))
            segs.push(`[${JSON.stringify(seg)}]`);
        else {
            if (segs.length)
                segs.push(".");
            segs.push(seg);
        }
    }
    return segs.join("");
}
function prettifyError(error) {
    const lines = [];
    // sort by path length
    const issues = [...error.issues].sort((a, b) => (a.path ?? []).length - (b.path ?? []).length);
    // Process each issue
    for (const issue of issues) {
        lines.push(`✖ ${issue.message}`);
        if (issue.path?.length)
            lines.push(`  → at ${toDotPath(issue.path)}`);
    }
    // Convert Map to formatted string
    return lines.join("\n");
}

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.safeDecodeAsync = exports._safeDecodeAsync = exports.safeEncodeAsync = exports._safeEncodeAsync = exports.safeDecode = exports._safeDecode = exports.safeEncode = exports._safeEncode = exports.decodeAsync = exports._decodeAsync = exports.encodeAsync = exports._encodeAsync = exports.decode = exports._decode = exports.encode = exports._encode = exports.safeParseAsync = exports._safeParseAsync = exports.safeParse = exports._safeParse = exports.parseAsync = exports._parseAsync = exports.parse = exports._parse = void 0;
	const core = __importStar(core$3);
	const errors = __importStar(errors$3);
	const util = __importStar(util$3);
	const _parse = (_Err) => (schema, value, _ctx, _params) => {
	    const ctx = _ctx ? Object.assign(_ctx, { async: false }) : { async: false };
	    const result = schema._zod.run({ value, issues: [] }, ctx);
	    if (result instanceof Promise) {
	        throw new core.$ZodAsyncError();
	    }
	    if (result.issues.length) {
	        const e = new (_params?.Err ?? _Err)(result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())));
	        util.captureStackTrace(e, _params?.callee);
	        throw e;
	    }
	    return result.value;
	};
	exports._parse = _parse;
	exports.parse = (0, exports._parse)(errors.$ZodRealError);
	const _parseAsync = (_Err) => async (schema, value, _ctx, params) => {
	    const ctx = _ctx ? Object.assign(_ctx, { async: true }) : { async: true };
	    let result = schema._zod.run({ value, issues: [] }, ctx);
	    if (result instanceof Promise)
	        result = await result;
	    if (result.issues.length) {
	        const e = new (params?.Err ?? _Err)(result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())));
	        util.captureStackTrace(e, params?.callee);
	        throw e;
	    }
	    return result.value;
	};
	exports._parseAsync = _parseAsync;
	exports.parseAsync = (0, exports._parseAsync)(errors.$ZodRealError);
	const _safeParse = (_Err) => (schema, value, _ctx) => {
	    const ctx = _ctx ? { ..._ctx, async: false } : { async: false };
	    const result = schema._zod.run({ value, issues: [] }, ctx);
	    if (result instanceof Promise) {
	        throw new core.$ZodAsyncError();
	    }
	    return result.issues.length
	        ? {
	            success: false,
	            error: new (_Err ?? errors.$ZodError)(result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config()))),
	        }
	        : { success: true, data: result.value };
	};
	exports._safeParse = _safeParse;
	exports.safeParse = (0, exports._safeParse)(errors.$ZodRealError);
	const _safeParseAsync = (_Err) => async (schema, value, _ctx) => {
	    const ctx = _ctx ? Object.assign(_ctx, { async: true }) : { async: true };
	    let result = schema._zod.run({ value, issues: [] }, ctx);
	    if (result instanceof Promise)
	        result = await result;
	    return result.issues.length
	        ? {
	            success: false,
	            error: new _Err(result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config()))),
	        }
	        : { success: true, data: result.value };
	};
	exports._safeParseAsync = _safeParseAsync;
	exports.safeParseAsync = (0, exports._safeParseAsync)(errors.$ZodRealError);
	const _encode = (_Err) => (schema, value, _ctx) => {
	    const ctx = _ctx ? Object.assign(_ctx, { direction: "backward" }) : { direction: "backward" };
	    return (0, exports._parse)(_Err)(schema, value, ctx);
	};
	exports._encode = _encode;
	exports.encode = (0, exports._encode)(errors.$ZodRealError);
	const _decode = (_Err) => (schema, value, _ctx) => {
	    return (0, exports._parse)(_Err)(schema, value, _ctx);
	};
	exports._decode = _decode;
	exports.decode = (0, exports._decode)(errors.$ZodRealError);
	const _encodeAsync = (_Err) => async (schema, value, _ctx) => {
	    const ctx = _ctx ? Object.assign(_ctx, { direction: "backward" }) : { direction: "backward" };
	    return (0, exports._parseAsync)(_Err)(schema, value, ctx);
	};
	exports._encodeAsync = _encodeAsync;
	exports.encodeAsync = (0, exports._encodeAsync)(errors.$ZodRealError);
	const _decodeAsync = (_Err) => async (schema, value, _ctx) => {
	    return (0, exports._parseAsync)(_Err)(schema, value, _ctx);
	};
	exports._decodeAsync = _decodeAsync;
	exports.decodeAsync = (0, exports._decodeAsync)(errors.$ZodRealError);
	const _safeEncode = (_Err) => (schema, value, _ctx) => {
	    const ctx = _ctx ? Object.assign(_ctx, { direction: "backward" }) : { direction: "backward" };
	    return (0, exports._safeParse)(_Err)(schema, value, ctx);
	};
	exports._safeEncode = _safeEncode;
	exports.safeEncode = (0, exports._safeEncode)(errors.$ZodRealError);
	const _safeDecode = (_Err) => (schema, value, _ctx) => {
	    return (0, exports._safeParse)(_Err)(schema, value, _ctx);
	};
	exports._safeDecode = _safeDecode;
	exports.safeDecode = (0, exports._safeDecode)(errors.$ZodRealError);
	const _safeEncodeAsync = (_Err) => async (schema, value, _ctx) => {
	    const ctx = _ctx ? Object.assign(_ctx, { direction: "backward" }) : { direction: "backward" };
	    return (0, exports._safeParseAsync)(_Err)(schema, value, ctx);
	};
	exports._safeEncodeAsync = _safeEncodeAsync;
	exports.safeEncodeAsync = (0, exports._safeEncodeAsync)(errors.$ZodRealError);
	const _safeDecodeAsync = (_Err) => async (schema, value, _ctx) => {
	    return (0, exports._safeParseAsync)(_Err)(schema, value, _ctx);
	};
	exports._safeDecodeAsync = _safeDecodeAsync;
	exports.safeDecodeAsync = (0, exports._safeDecodeAsync)(errors.$ZodRealError);
} (parse$3));

var schemas$3 = {};

var checks$2 = {};

var regexes = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.sha384_base64 = exports.sha384_hex = exports.sha256_base64url = exports.sha256_base64 = exports.sha256_hex = exports.sha1_base64url = exports.sha1_base64 = exports.sha1_hex = exports.md5_base64url = exports.md5_base64 = exports.md5_hex = exports.hex = exports.uppercase = exports.lowercase = exports.undefined = exports.null = exports.boolean = exports.number = exports.integer = exports.bigint = exports.string = exports.date = exports.e164 = exports.domain = exports.hostname = exports.base64url = exports.base64 = exports.cidrv6 = exports.cidrv4 = exports.ipv6 = exports.ipv4 = exports.browserEmail = exports.idnEmail = exports.unicodeEmail = exports.rfc5322Email = exports.html5Email = exports.email = exports.uuid7 = exports.uuid6 = exports.uuid4 = exports.uuid = exports.guid = exports.extendedDuration = exports.duration = exports.nanoid = exports.ksuid = exports.xid = exports.ulid = exports.cuid2 = exports.cuid = void 0;
	exports.sha512_base64url = exports.sha512_base64 = exports.sha512_hex = exports.sha384_base64url = void 0;
	exports.emoji = emoji;
	exports.time = time;
	exports.datetime = datetime;
	exports.cuid = /^[cC][^\s-]{8,}$/;
	exports.cuid2 = /^[0-9a-z]+$/;
	exports.ulid = /^[0-9A-HJKMNP-TV-Za-hjkmnp-tv-z]{26}$/;
	exports.xid = /^[0-9a-vA-V]{20}$/;
	exports.ksuid = /^[A-Za-z0-9]{27}$/;
	exports.nanoid = /^[a-zA-Z0-9_-]{21}$/;
	/** ISO 8601-1 duration regex. Does not support the 8601-2 extensions like negative durations or fractional/negative components. */
	exports.duration = /^P(?:(\d+W)|(?!.*W)(?=\d|T\d)(\d+Y)?(\d+M)?(\d+D)?(T(?=\d)(\d+H)?(\d+M)?(\d+([.,]\d+)?S)?)?)$/;
	/** Implements ISO 8601-2 extensions like explicit +- prefixes, mixing weeks with other units, and fractional/negative components. */
	exports.extendedDuration = /^[-+]?P(?!$)(?:(?:[-+]?\d+Y)|(?:[-+]?\d+[.,]\d+Y$))?(?:(?:[-+]?\d+M)|(?:[-+]?\d+[.,]\d+M$))?(?:(?:[-+]?\d+W)|(?:[-+]?\d+[.,]\d+W$))?(?:(?:[-+]?\d+D)|(?:[-+]?\d+[.,]\d+D$))?(?:T(?=[\d+-])(?:(?:[-+]?\d+H)|(?:[-+]?\d+[.,]\d+H$))?(?:(?:[-+]?\d+M)|(?:[-+]?\d+[.,]\d+M$))?(?:[-+]?\d+(?:[.,]\d+)?S)?)??$/;
	/** A regex for any UUID-like identifier: 8-4-4-4-12 hex pattern */
	exports.guid = /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;
	/** Returns a regex for validating an RFC 9562/4122 UUID.
	 *
	 * @param version Optionally specify a version 1-8. If no version is specified, all versions are supported. */
	const uuid = (version) => {
	    if (!version)
	        return /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/;
	    return new RegExp(`^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-${version}[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12})$`);
	};
	exports.uuid = uuid;
	exports.uuid4 = (0, exports.uuid)(4);
	exports.uuid6 = (0, exports.uuid)(6);
	exports.uuid7 = (0, exports.uuid)(7);
	/** Practical email validation */
	exports.email = /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-\.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9\-]*\.)+[A-Za-z]{2,}$/;
	/** Equivalent to the HTML5 input[type=email] validation implemented by browsers. Source: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email */
	exports.html5Email = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
	/** The classic emailregex.com regex for RFC 5322-compliant emails */
	exports.rfc5322Email = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	/** A loose regex that allows Unicode characters, enforces length limits, and that's about it. */
	exports.unicodeEmail = /^[^\s@"]{1,64}@[^\s@]{1,255}$/u;
	exports.idnEmail = /^[^\s@"]{1,64}@[^\s@]{1,255}$/u;
	exports.browserEmail = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
	// from https://thekevinscott.com/emojis-in-javascript/#writing-a-regular-expression
	const _emoji = `^(\\p{Extended_Pictographic}|\\p{Emoji_Component})+$`;
	function emoji() {
	    return new RegExp(_emoji, "u");
	}
	exports.ipv4 = /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])$/;
	exports.ipv6 = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|::|([0-9a-fA-F]{1,4})?::([0-9a-fA-F]{1,4}:?){0,6})$/;
	exports.cidrv4 = /^((25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\/([0-9]|[1-2][0-9]|3[0-2])$/;
	exports.cidrv6 = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|::|([0-9a-fA-F]{1,4})?::([0-9a-fA-F]{1,4}:?){0,6})\/(12[0-8]|1[01][0-9]|[1-9]?[0-9])$/;
	// https://stackoverflow.com/questions/7860392/determine-if-string-is-in-base64-using-javascript
	exports.base64 = /^$|^(?:[0-9a-zA-Z+/]{4})*(?:(?:[0-9a-zA-Z+/]{2}==)|(?:[0-9a-zA-Z+/]{3}=))?$/;
	exports.base64url = /^[A-Za-z0-9_-]*$/;
	// based on https://stackoverflow.com/questions/106179/regular-expression-to-match-dns-hostname-or-ip-address
	// export const hostname: RegExp = /^([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+$/;
	exports.hostname = /^(?=.{1,253}\.?$)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[-0-9a-zA-Z]{0,61}[0-9a-zA-Z])?)*\.?$/;
	exports.domain = /^([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
	// https://blog.stevenlevithan.com/archives/validate-phone-number#r4-3 (regex sans spaces)
	exports.e164 = /^\+(?:[0-9]){6,14}[0-9]$/;
	// const dateSource = `((\\d\\d[2468][048]|\\d\\d[13579][26]|\\d\\d0[48]|[02468][048]00|[13579][26]00)-02-29|\\d{4}-((0[13578]|1[02])-(0[1-9]|[12]\\d|3[01])|(0[469]|11)-(0[1-9]|[12]\\d|30)|(02)-(0[1-9]|1\\d|2[0-8])))`;
	const dateSource = `(?:(?:\\d\\d[2468][048]|\\d\\d[13579][26]|\\d\\d0[48]|[02468][048]00|[13579][26]00)-02-29|\\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\\d|30)|(?:02)-(?:0[1-9]|1\\d|2[0-8])))`;
	exports.date = new RegExp(`^${dateSource}$`);
	function timeSource(args) {
	    const hhmm = `(?:[01]\\d|2[0-3]):[0-5]\\d`;
	    const regex = typeof args.precision === "number"
	        ? args.precision === -1
	            ? `${hhmm}`
	            : args.precision === 0
	                ? `${hhmm}:[0-5]\\d`
	                : `${hhmm}:[0-5]\\d\\.\\d{${args.precision}}`
	        : `${hhmm}(?::[0-5]\\d(?:\\.\\d+)?)?`;
	    return regex;
	}
	function time(args) {
	    return new RegExp(`^${timeSource(args)}$`);
	}
	// Adapted from https://stackoverflow.com/a/3143231
	function datetime(args) {
	    const time = timeSource({ precision: args.precision });
	    const opts = ["Z"];
	    if (args.local)
	        opts.push("");
	    // if (args.offset) opts.push(`([+-]\\d{2}:\\d{2})`);
	    if (args.offset)
	        opts.push(`([+-](?:[01]\\d|2[0-3]):[0-5]\\d)`);
	    const timeRegex = `${time}(?:${opts.join("|")})`;
	    return new RegExp(`^${dateSource}T(?:${timeRegex})$`);
	}
	const string = (params) => {
	    const regex = params ? `[\\s\\S]{${params?.minimum ?? 0},${params?.maximum ?? ""}}` : `[\\s\\S]*`;
	    return new RegExp(`^${regex}$`);
	};
	exports.string = string;
	exports.bigint = /^\d+n?$/;
	exports.integer = /^\d+$/;
	exports.number = /^-?\d+(?:\.\d+)?/i;
	exports.boolean = /true|false/i;
	const _null = /null/i;
	exports.null = _null;
	const _undefined = /undefined/i;
	exports.undefined = _undefined;
	// regex for string with no uppercase letters
	exports.lowercase = /^[^A-Z]*$/;
	// regex for string with no lowercase letters
	exports.uppercase = /^[^a-z]*$/;
	// regex for hexadecimal strings (any length)
	exports.hex = /^[0-9a-fA-F]*$/;
	// Hash regexes for different algorithms and encodings
	// Helper function to create base64 regex with exact length and padding
	function fixedBase64(bodyLength, padding) {
	    return new RegExp(`^[A-Za-z0-9+/]{${bodyLength}}${padding}$`);
	}
	// Helper function to create base64url regex with exact length (no padding)
	function fixedBase64url(length) {
	    return new RegExp(`^[A-Za-z0-9-_]{${length}}$`);
	}
	// MD5 (16 bytes): base64 = 24 chars total (22 + "==")
	exports.md5_hex = /^[0-9a-fA-F]{32}$/;
	exports.md5_base64 = fixedBase64(22, "==");
	exports.md5_base64url = fixedBase64url(22);
	// SHA1 (20 bytes): base64 = 28 chars total (27 + "=")
	exports.sha1_hex = /^[0-9a-fA-F]{40}$/;
	exports.sha1_base64 = fixedBase64(27, "=");
	exports.sha1_base64url = fixedBase64url(27);
	// SHA256 (32 bytes): base64 = 44 chars total (43 + "=")
	exports.sha256_hex = /^[0-9a-fA-F]{64}$/;
	exports.sha256_base64 = fixedBase64(43, "=");
	exports.sha256_base64url = fixedBase64url(43);
	// SHA384 (48 bytes): base64 = 64 chars total (no padding)
	exports.sha384_hex = /^[0-9a-fA-F]{96}$/;
	exports.sha384_base64 = fixedBase64(64, "");
	exports.sha384_base64url = fixedBase64url(64);
	// SHA512 (64 bytes): base64 = 88 chars total (86 + "==")
	exports.sha512_hex = /^[0-9a-fA-F]{128}$/;
	exports.sha512_base64 = fixedBase64(86, "==");
	exports.sha512_base64url = fixedBase64url(86);
} (regexes));

(function (exports) {
	// import { $ZodType } from "./schemas.js";
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.$ZodCheckOverwrite = exports.$ZodCheckMimeType = exports.$ZodCheckProperty = exports.$ZodCheckEndsWith = exports.$ZodCheckStartsWith = exports.$ZodCheckIncludes = exports.$ZodCheckUpperCase = exports.$ZodCheckLowerCase = exports.$ZodCheckRegex = exports.$ZodCheckStringFormat = exports.$ZodCheckLengthEquals = exports.$ZodCheckMinLength = exports.$ZodCheckMaxLength = exports.$ZodCheckSizeEquals = exports.$ZodCheckMinSize = exports.$ZodCheckMaxSize = exports.$ZodCheckBigIntFormat = exports.$ZodCheckNumberFormat = exports.$ZodCheckMultipleOf = exports.$ZodCheckGreaterThan = exports.$ZodCheckLessThan = exports.$ZodCheck = void 0;
	const core = __importStar(core$3);
	const regexes$1 = __importStar(regexes);
	const util = __importStar(util$3);
	exports.$ZodCheck = core.$constructor("$ZodCheck", (inst, def) => {
	    var _a;
	    inst._zod ?? (inst._zod = {});
	    inst._zod.def = def;
	    (_a = inst._zod).onattach ?? (_a.onattach = []);
	});
	const numericOriginMap = {
	    number: "number",
	    bigint: "bigint",
	    object: "date",
	};
	exports.$ZodCheckLessThan = core.$constructor("$ZodCheckLessThan", (inst, def) => {
	    exports.$ZodCheck.init(inst, def);
	    const origin = numericOriginMap[typeof def.value];
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        const curr = (def.inclusive ? bag.maximum : bag.exclusiveMaximum) ?? Number.POSITIVE_INFINITY;
	        if (def.value < curr) {
	            if (def.inclusive)
	                bag.maximum = def.value;
	            else
	                bag.exclusiveMaximum = def.value;
	        }
	    });
	    inst._zod.check = (payload) => {
	        if (def.inclusive ? payload.value <= def.value : payload.value < def.value) {
	            return;
	        }
	        payload.issues.push({
	            origin,
	            code: "too_big",
	            maximum: def.value,
	            input: payload.value,
	            inclusive: def.inclusive,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckGreaterThan = core.$constructor("$ZodCheckGreaterThan", (inst, def) => {
	    exports.$ZodCheck.init(inst, def);
	    const origin = numericOriginMap[typeof def.value];
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        const curr = (def.inclusive ? bag.minimum : bag.exclusiveMinimum) ?? Number.NEGATIVE_INFINITY;
	        if (def.value > curr) {
	            if (def.inclusive)
	                bag.minimum = def.value;
	            else
	                bag.exclusiveMinimum = def.value;
	        }
	    });
	    inst._zod.check = (payload) => {
	        if (def.inclusive ? payload.value >= def.value : payload.value > def.value) {
	            return;
	        }
	        payload.issues.push({
	            origin,
	            code: "too_small",
	            minimum: def.value,
	            input: payload.value,
	            inclusive: def.inclusive,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckMultipleOf = 
	/*@__PURE__*/ core.$constructor("$ZodCheckMultipleOf", (inst, def) => {
	    exports.$ZodCheck.init(inst, def);
	    inst._zod.onattach.push((inst) => {
	        var _a;
	        (_a = inst._zod.bag).multipleOf ?? (_a.multipleOf = def.value);
	    });
	    inst._zod.check = (payload) => {
	        if (typeof payload.value !== typeof def.value)
	            throw new Error("Cannot mix number and bigint in multiple_of check.");
	        const isMultiple = typeof payload.value === "bigint"
	            ? payload.value % def.value === BigInt(0)
	            : util.floatSafeRemainder(payload.value, def.value) === 0;
	        if (isMultiple)
	            return;
	        payload.issues.push({
	            origin: typeof payload.value,
	            code: "not_multiple_of",
	            divisor: def.value,
	            input: payload.value,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckNumberFormat = core.$constructor("$ZodCheckNumberFormat", (inst, def) => {
	    exports.$ZodCheck.init(inst, def); // no format checks
	    def.format = def.format || "float64";
	    const isInt = def.format?.includes("int");
	    const origin = isInt ? "int" : "number";
	    const [minimum, maximum] = util.NUMBER_FORMAT_RANGES[def.format];
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        bag.format = def.format;
	        bag.minimum = minimum;
	        bag.maximum = maximum;
	        if (isInt)
	            bag.pattern = regexes$1.integer;
	    });
	    inst._zod.check = (payload) => {
	        const input = payload.value;
	        if (isInt) {
	            if (!Number.isInteger(input)) {
	                // invalid_format issue
	                // payload.issues.push({
	                //   expected: def.format,
	                //   format: def.format,
	                //   code: "invalid_format",
	                //   input,
	                //   inst,
	                // });
	                // invalid_type issue
	                payload.issues.push({
	                    expected: origin,
	                    format: def.format,
	                    code: "invalid_type",
	                    continue: false,
	                    input,
	                    inst,
	                });
	                return;
	                // not_multiple_of issue
	                // payload.issues.push({
	                //   code: "not_multiple_of",
	                //   origin: "number",
	                //   input,
	                //   inst,
	                //   divisor: 1,
	                // });
	            }
	            if (!Number.isSafeInteger(input)) {
	                if (input > 0) {
	                    // too_big
	                    payload.issues.push({
	                        input,
	                        code: "too_big",
	                        maximum: Number.MAX_SAFE_INTEGER,
	                        note: "Integers must be within the safe integer range.",
	                        inst,
	                        origin,
	                        continue: !def.abort,
	                    });
	                }
	                else {
	                    // too_small
	                    payload.issues.push({
	                        input,
	                        code: "too_small",
	                        minimum: Number.MIN_SAFE_INTEGER,
	                        note: "Integers must be within the safe integer range.",
	                        inst,
	                        origin,
	                        continue: !def.abort,
	                    });
	                }
	                return;
	            }
	        }
	        if (input < minimum) {
	            payload.issues.push({
	                origin: "number",
	                input,
	                code: "too_small",
	                minimum,
	                inclusive: true,
	                inst,
	                continue: !def.abort,
	            });
	        }
	        if (input > maximum) {
	            payload.issues.push({
	                origin: "number",
	                input,
	                code: "too_big",
	                maximum,
	                inst,
	            });
	        }
	    };
	});
	exports.$ZodCheckBigIntFormat = core.$constructor("$ZodCheckBigIntFormat", (inst, def) => {
	    exports.$ZodCheck.init(inst, def); // no format checks
	    const [minimum, maximum] = util.BIGINT_FORMAT_RANGES[def.format];
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        bag.format = def.format;
	        bag.minimum = minimum;
	        bag.maximum = maximum;
	    });
	    inst._zod.check = (payload) => {
	        const input = payload.value;
	        if (input < minimum) {
	            payload.issues.push({
	                origin: "bigint",
	                input,
	                code: "too_small",
	                minimum: minimum,
	                inclusive: true,
	                inst,
	                continue: !def.abort,
	            });
	        }
	        if (input > maximum) {
	            payload.issues.push({
	                origin: "bigint",
	                input,
	                code: "too_big",
	                maximum,
	                inst,
	            });
	        }
	    };
	});
	exports.$ZodCheckMaxSize = core.$constructor("$ZodCheckMaxSize", (inst, def) => {
	    var _a;
	    exports.$ZodCheck.init(inst, def);
	    (_a = inst._zod.def).when ?? (_a.when = (payload) => {
	        const val = payload.value;
	        return !util.nullish(val) && val.size !== undefined;
	    });
	    inst._zod.onattach.push((inst) => {
	        const curr = (inst._zod.bag.maximum ?? Number.POSITIVE_INFINITY);
	        if (def.maximum < curr)
	            inst._zod.bag.maximum = def.maximum;
	    });
	    inst._zod.check = (payload) => {
	        const input = payload.value;
	        const size = input.size;
	        if (size <= def.maximum)
	            return;
	        payload.issues.push({
	            origin: util.getSizableOrigin(input),
	            code: "too_big",
	            maximum: def.maximum,
	            inclusive: true,
	            input,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckMinSize = core.$constructor("$ZodCheckMinSize", (inst, def) => {
	    var _a;
	    exports.$ZodCheck.init(inst, def);
	    (_a = inst._zod.def).when ?? (_a.when = (payload) => {
	        const val = payload.value;
	        return !util.nullish(val) && val.size !== undefined;
	    });
	    inst._zod.onattach.push((inst) => {
	        const curr = (inst._zod.bag.minimum ?? Number.NEGATIVE_INFINITY);
	        if (def.minimum > curr)
	            inst._zod.bag.minimum = def.minimum;
	    });
	    inst._zod.check = (payload) => {
	        const input = payload.value;
	        const size = input.size;
	        if (size >= def.minimum)
	            return;
	        payload.issues.push({
	            origin: util.getSizableOrigin(input),
	            code: "too_small",
	            minimum: def.minimum,
	            inclusive: true,
	            input,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckSizeEquals = core.$constructor("$ZodCheckSizeEquals", (inst, def) => {
	    var _a;
	    exports.$ZodCheck.init(inst, def);
	    (_a = inst._zod.def).when ?? (_a.when = (payload) => {
	        const val = payload.value;
	        return !util.nullish(val) && val.size !== undefined;
	    });
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        bag.minimum = def.size;
	        bag.maximum = def.size;
	        bag.size = def.size;
	    });
	    inst._zod.check = (payload) => {
	        const input = payload.value;
	        const size = input.size;
	        if (size === def.size)
	            return;
	        const tooBig = size > def.size;
	        payload.issues.push({
	            origin: util.getSizableOrigin(input),
	            ...(tooBig ? { code: "too_big", maximum: def.size } : { code: "too_small", minimum: def.size }),
	            inclusive: true,
	            exact: true,
	            input: payload.value,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckMaxLength = core.$constructor("$ZodCheckMaxLength", (inst, def) => {
	    var _a;
	    exports.$ZodCheck.init(inst, def);
	    (_a = inst._zod.def).when ?? (_a.when = (payload) => {
	        const val = payload.value;
	        return !util.nullish(val) && val.length !== undefined;
	    });
	    inst._zod.onattach.push((inst) => {
	        const curr = (inst._zod.bag.maximum ?? Number.POSITIVE_INFINITY);
	        if (def.maximum < curr)
	            inst._zod.bag.maximum = def.maximum;
	    });
	    inst._zod.check = (payload) => {
	        const input = payload.value;
	        const length = input.length;
	        if (length <= def.maximum)
	            return;
	        const origin = util.getLengthableOrigin(input);
	        payload.issues.push({
	            origin,
	            code: "too_big",
	            maximum: def.maximum,
	            inclusive: true,
	            input,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckMinLength = core.$constructor("$ZodCheckMinLength", (inst, def) => {
	    var _a;
	    exports.$ZodCheck.init(inst, def);
	    (_a = inst._zod.def).when ?? (_a.when = (payload) => {
	        const val = payload.value;
	        return !util.nullish(val) && val.length !== undefined;
	    });
	    inst._zod.onattach.push((inst) => {
	        const curr = (inst._zod.bag.minimum ?? Number.NEGATIVE_INFINITY);
	        if (def.minimum > curr)
	            inst._zod.bag.minimum = def.minimum;
	    });
	    inst._zod.check = (payload) => {
	        const input = payload.value;
	        const length = input.length;
	        if (length >= def.minimum)
	            return;
	        const origin = util.getLengthableOrigin(input);
	        payload.issues.push({
	            origin,
	            code: "too_small",
	            minimum: def.minimum,
	            inclusive: true,
	            input,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckLengthEquals = core.$constructor("$ZodCheckLengthEquals", (inst, def) => {
	    var _a;
	    exports.$ZodCheck.init(inst, def);
	    (_a = inst._zod.def).when ?? (_a.when = (payload) => {
	        const val = payload.value;
	        return !util.nullish(val) && val.length !== undefined;
	    });
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        bag.minimum = def.length;
	        bag.maximum = def.length;
	        bag.length = def.length;
	    });
	    inst._zod.check = (payload) => {
	        const input = payload.value;
	        const length = input.length;
	        if (length === def.length)
	            return;
	        const origin = util.getLengthableOrigin(input);
	        const tooBig = length > def.length;
	        payload.issues.push({
	            origin,
	            ...(tooBig ? { code: "too_big", maximum: def.length } : { code: "too_small", minimum: def.length }),
	            inclusive: true,
	            exact: true,
	            input: payload.value,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckStringFormat = core.$constructor("$ZodCheckStringFormat", (inst, def) => {
	    var _a, _b;
	    exports.$ZodCheck.init(inst, def);
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        bag.format = def.format;
	        if (def.pattern) {
	            bag.patterns ?? (bag.patterns = new Set());
	            bag.patterns.add(def.pattern);
	        }
	    });
	    if (def.pattern)
	        (_a = inst._zod).check ?? (_a.check = (payload) => {
	            def.pattern.lastIndex = 0;
	            if (def.pattern.test(payload.value))
	                return;
	            payload.issues.push({
	                origin: "string",
	                code: "invalid_format",
	                format: def.format,
	                input: payload.value,
	                ...(def.pattern ? { pattern: def.pattern.toString() } : {}),
	                inst,
	                continue: !def.abort,
	            });
	        });
	    else
	        (_b = inst._zod).check ?? (_b.check = () => { });
	});
	exports.$ZodCheckRegex = core.$constructor("$ZodCheckRegex", (inst, def) => {
	    exports.$ZodCheckStringFormat.init(inst, def);
	    inst._zod.check = (payload) => {
	        def.pattern.lastIndex = 0;
	        if (def.pattern.test(payload.value))
	            return;
	        payload.issues.push({
	            origin: "string",
	            code: "invalid_format",
	            format: "regex",
	            input: payload.value,
	            pattern: def.pattern.toString(),
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckLowerCase = core.$constructor("$ZodCheckLowerCase", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.lowercase);
	    exports.$ZodCheckStringFormat.init(inst, def);
	});
	exports.$ZodCheckUpperCase = core.$constructor("$ZodCheckUpperCase", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.uppercase);
	    exports.$ZodCheckStringFormat.init(inst, def);
	});
	exports.$ZodCheckIncludes = core.$constructor("$ZodCheckIncludes", (inst, def) => {
	    exports.$ZodCheck.init(inst, def);
	    const escapedRegex = util.escapeRegex(def.includes);
	    const pattern = new RegExp(typeof def.position === "number" ? `^.{${def.position}}${escapedRegex}` : escapedRegex);
	    def.pattern = pattern;
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        bag.patterns ?? (bag.patterns = new Set());
	        bag.patterns.add(pattern);
	    });
	    inst._zod.check = (payload) => {
	        if (payload.value.includes(def.includes, def.position))
	            return;
	        payload.issues.push({
	            origin: "string",
	            code: "invalid_format",
	            format: "includes",
	            includes: def.includes,
	            input: payload.value,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckStartsWith = core.$constructor("$ZodCheckStartsWith", (inst, def) => {
	    exports.$ZodCheck.init(inst, def);
	    const pattern = new RegExp(`^${util.escapeRegex(def.prefix)}.*`);
	    def.pattern ?? (def.pattern = pattern);
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        bag.patterns ?? (bag.patterns = new Set());
	        bag.patterns.add(pattern);
	    });
	    inst._zod.check = (payload) => {
	        if (payload.value.startsWith(def.prefix))
	            return;
	        payload.issues.push({
	            origin: "string",
	            code: "invalid_format",
	            format: "starts_with",
	            prefix: def.prefix,
	            input: payload.value,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckEndsWith = core.$constructor("$ZodCheckEndsWith", (inst, def) => {
	    exports.$ZodCheck.init(inst, def);
	    const pattern = new RegExp(`.*${util.escapeRegex(def.suffix)}$`);
	    def.pattern ?? (def.pattern = pattern);
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        bag.patterns ?? (bag.patterns = new Set());
	        bag.patterns.add(pattern);
	    });
	    inst._zod.check = (payload) => {
	        if (payload.value.endsWith(def.suffix))
	            return;
	        payload.issues.push({
	            origin: "string",
	            code: "invalid_format",
	            format: "ends_with",
	            suffix: def.suffix,
	            input: payload.value,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	///////////////////////////////////
	/////    $ZodCheckProperty    /////
	///////////////////////////////////
	function handleCheckPropertyResult(result, payload, property) {
	    if (result.issues.length) {
	        payload.issues.push(...util.prefixIssues(property, result.issues));
	    }
	}
	exports.$ZodCheckProperty = core.$constructor("$ZodCheckProperty", (inst, def) => {
	    exports.$ZodCheck.init(inst, def);
	    inst._zod.check = (payload) => {
	        const result = def.schema._zod.run({
	            value: payload.value[def.property],
	            issues: [],
	        }, {});
	        if (result instanceof Promise) {
	            return result.then((result) => handleCheckPropertyResult(result, payload, def.property));
	        }
	        handleCheckPropertyResult(result, payload, def.property);
	        return;
	    };
	});
	exports.$ZodCheckMimeType = core.$constructor("$ZodCheckMimeType", (inst, def) => {
	    exports.$ZodCheck.init(inst, def);
	    const mimeSet = new Set(def.mime);
	    inst._zod.onattach.push((inst) => {
	        inst._zod.bag.mime = def.mime;
	    });
	    inst._zod.check = (payload) => {
	        if (mimeSet.has(payload.value.type))
	            return;
	        payload.issues.push({
	            code: "invalid_value",
	            values: def.mime,
	            input: payload.value.type,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCheckOverwrite = core.$constructor("$ZodCheckOverwrite", (inst, def) => {
	    exports.$ZodCheck.init(inst, def);
	    inst._zod.check = (payload) => {
	        payload.value = def.tx(payload.value);
	    };
	});
} (checks$2));

var doc = {};

Object.defineProperty(doc, "__esModule", { value: true });
doc.Doc = void 0;
class Doc {
    constructor(args = []) {
        this.content = [];
        this.indent = 0;
        if (this)
            this.args = args;
    }
    indented(fn) {
        this.indent += 1;
        fn(this);
        this.indent -= 1;
    }
    write(arg) {
        if (typeof arg === "function") {
            arg(this, { execution: "sync" });
            arg(this, { execution: "async" });
            return;
        }
        const content = arg;
        const lines = content.split("\n").filter((x) => x);
        const minIndent = Math.min(...lines.map((x) => x.length - x.trimStart().length));
        const dedented = lines.map((x) => x.slice(minIndent)).map((x) => " ".repeat(this.indent * 2) + x);
        for (const line of dedented) {
            this.content.push(line);
        }
    }
    compile() {
        const F = Function;
        const args = this?.args;
        const content = this?.content ?? [``];
        const lines = [...content.map((x) => `  ${x}`)];
        // console.log(lines.join("\n"));
        return new F(...args, lines.join("\n"));
    }
}
doc.Doc = Doc;

var versions = {};

Object.defineProperty(versions, "__esModule", { value: true });
versions.version = void 0;
versions.version = {
    major: 4,
    minor: 1,
    patch: 5,
};

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.$ZodMap = exports.$ZodRecord = exports.$ZodTuple = exports.$ZodIntersection = exports.$ZodDiscriminatedUnion = exports.$ZodUnion = exports.$ZodObjectJIT = exports.$ZodObject = exports.$ZodArray = exports.$ZodDate = exports.$ZodVoid = exports.$ZodNever = exports.$ZodUnknown = exports.$ZodAny = exports.$ZodNull = exports.$ZodUndefined = exports.$ZodSymbol = exports.$ZodBigIntFormat = exports.$ZodBigInt = exports.$ZodBoolean = exports.$ZodNumberFormat = exports.$ZodNumber = exports.$ZodCustomStringFormat = exports.$ZodJWT = exports.$ZodE164 = exports.$ZodBase64URL = exports.$ZodBase64 = exports.$ZodCIDRv6 = exports.$ZodCIDRv4 = exports.$ZodIPv6 = exports.$ZodIPv4 = exports.$ZodISODuration = exports.$ZodISOTime = exports.$ZodISODate = exports.$ZodISODateTime = exports.$ZodKSUID = exports.$ZodXID = exports.$ZodULID = exports.$ZodCUID2 = exports.$ZodCUID = exports.$ZodNanoID = exports.$ZodEmoji = exports.$ZodURL = exports.$ZodEmail = exports.$ZodUUID = exports.$ZodGUID = exports.$ZodStringFormat = exports.$ZodString = exports.clone = exports.$ZodType = void 0;
	exports.$ZodCustom = exports.$ZodLazy = exports.$ZodPromise = exports.$ZodFunction = exports.$ZodTemplateLiteral = exports.$ZodReadonly = exports.$ZodCodec = exports.$ZodPipe = exports.$ZodNaN = exports.$ZodCatch = exports.$ZodSuccess = exports.$ZodNonOptional = exports.$ZodPrefault = exports.$ZodDefault = exports.$ZodNullable = exports.$ZodOptional = exports.$ZodTransform = exports.$ZodFile = exports.$ZodLiteral = exports.$ZodEnum = exports.$ZodSet = void 0;
	exports.isValidBase64 = isValidBase64;
	exports.isValidBase64URL = isValidBase64URL;
	exports.isValidJWT = isValidJWT;
	const checks = __importStar(checks$2);
	const core = __importStar(core$3);
	const doc_js_1 = doc;
	const parse_js_1 = parse$3;
	const regexes$1 = __importStar(regexes);
	const util = __importStar(util$3);
	const versions_js_1 = versions;
	exports.$ZodType = core.$constructor("$ZodType", (inst, def) => {
	    var _a;
	    inst ?? (inst = {});
	    inst._zod.def = def; // set _def property
	    inst._zod.bag = inst._zod.bag || {}; // initialize _bag object
	    inst._zod.version = versions_js_1.version;
	    const checks = [...(inst._zod.def.checks ?? [])];
	    // if inst is itself a checks.$ZodCheck, run it as a check
	    if (inst._zod.traits.has("$ZodCheck")) {
	        checks.unshift(inst);
	    }
	    for (const ch of checks) {
	        for (const fn of ch._zod.onattach) {
	            fn(inst);
	        }
	    }
	    if (checks.length === 0) {
	        // deferred initializer
	        // inst._zod.parse is not yet defined
	        (_a = inst._zod).deferred ?? (_a.deferred = []);
	        inst._zod.deferred?.push(() => {
	            inst._zod.run = inst._zod.parse;
	        });
	    }
	    else {
	        const runChecks = (payload, checks, ctx) => {
	            let isAborted = util.aborted(payload);
	            let asyncResult;
	            for (const ch of checks) {
	                if (ch._zod.def.when) {
	                    const shouldRun = ch._zod.def.when(payload);
	                    if (!shouldRun)
	                        continue;
	                }
	                else if (isAborted) {
	                    continue;
	                }
	                const currLen = payload.issues.length;
	                const _ = ch._zod.check(payload);
	                if (_ instanceof Promise && ctx?.async === false) {
	                    throw new core.$ZodAsyncError();
	                }
	                if (asyncResult || _ instanceof Promise) {
	                    asyncResult = (asyncResult ?? Promise.resolve()).then(async () => {
	                        await _;
	                        const nextLen = payload.issues.length;
	                        if (nextLen === currLen)
	                            return;
	                        if (!isAborted)
	                            isAborted = util.aborted(payload, currLen);
	                    });
	                }
	                else {
	                    const nextLen = payload.issues.length;
	                    if (nextLen === currLen)
	                        continue;
	                    if (!isAborted)
	                        isAborted = util.aborted(payload, currLen);
	                }
	            }
	            if (asyncResult) {
	                return asyncResult.then(() => {
	                    return payload;
	                });
	            }
	            return payload;
	        };
	        // const handleChecksResult = (
	        //   checkResult: ParsePayload,
	        //   originalResult: ParsePayload,
	        //   ctx: ParseContextInternal
	        // ): util.MaybeAsync<ParsePayload> => {
	        //   // if the checks mutated the value && there are no issues, re-parse the result
	        //   if (checkResult.value !== originalResult.value && !checkResult.issues.length)
	        //     return inst._zod.parse(checkResult, ctx);
	        //   return originalResult;
	        // };
	        const handleCanaryResult = (canary, payload, ctx) => {
	            // abort if the canary is aborted
	            if (util.aborted(canary)) {
	                canary.aborted = true;
	                return canary;
	            }
	            // run checks first, then
	            const checkResult = runChecks(payload, checks, ctx);
	            if (checkResult instanceof Promise) {
	                if (ctx.async === false)
	                    throw new core.$ZodAsyncError();
	                return checkResult.then((checkResult) => inst._zod.parse(checkResult, ctx));
	            }
	            return inst._zod.parse(checkResult, ctx);
	        };
	        inst._zod.run = (payload, ctx) => {
	            if (ctx.skipChecks) {
	                return inst._zod.parse(payload, ctx);
	            }
	            if (ctx.direction === "backward") {
	                // run canary
	                // initial pass (no checks)
	                const canary = inst._zod.parse({ value: payload.value, issues: [] }, { ...ctx, skipChecks: true });
	                if (canary instanceof Promise) {
	                    return canary.then((canary) => {
	                        return handleCanaryResult(canary, payload, ctx);
	                    });
	                }
	                return handleCanaryResult(canary, payload, ctx);
	            }
	            // forward
	            const result = inst._zod.parse(payload, ctx);
	            if (result instanceof Promise) {
	                if (ctx.async === false)
	                    throw new core.$ZodAsyncError();
	                return result.then((result) => runChecks(result, checks, ctx));
	            }
	            return runChecks(result, checks, ctx);
	        };
	    }
	    inst["~standard"] = {
	        validate: (value) => {
	            try {
	                const r = (0, parse_js_1.safeParse)(inst, value);
	                return r.success ? { value: r.data } : { issues: r.error?.issues };
	            }
	            catch (_) {
	                return (0, parse_js_1.safeParseAsync)(inst, value).then((r) => (r.success ? { value: r.data } : { issues: r.error?.issues }));
	            }
	        },
	        vendor: "zod",
	        version: 1,
	    };
	});
	var util_js_1 = util$3;
	Object.defineProperty(exports, "clone", { enumerable: true, get: function () { return util_js_1.clone; } });
	exports.$ZodString = core.$constructor("$ZodString", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.pattern = [...(inst?._zod.bag?.patterns ?? [])].pop() ?? regexes$1.string(inst._zod.bag);
	    inst._zod.parse = (payload, _) => {
	        if (def.coerce)
	            try {
	                payload.value = String(payload.value);
	            }
	            catch (_) { }
	        if (typeof payload.value === "string")
	            return payload;
	        payload.issues.push({
	            expected: "string",
	            code: "invalid_type",
	            input: payload.value,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodStringFormat = core.$constructor("$ZodStringFormat", (inst, def) => {
	    // check initialization must come first
	    checks.$ZodCheckStringFormat.init(inst, def);
	    exports.$ZodString.init(inst, def);
	});
	exports.$ZodGUID = core.$constructor("$ZodGUID", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.guid);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodUUID = core.$constructor("$ZodUUID", (inst, def) => {
	    if (def.version) {
	        const versionMap = {
	            v1: 1,
	            v2: 2,
	            v3: 3,
	            v4: 4,
	            v5: 5,
	            v6: 6,
	            v7: 7,
	            v8: 8,
	        };
	        const v = versionMap[def.version];
	        if (v === undefined)
	            throw new Error(`Invalid UUID version: "${def.version}"`);
	        def.pattern ?? (def.pattern = regexes$1.uuid(v));
	    }
	    else
	        def.pattern ?? (def.pattern = regexes$1.uuid());
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodEmail = core.$constructor("$ZodEmail", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.email);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodURL = core.$constructor("$ZodURL", (inst, def) => {
	    exports.$ZodStringFormat.init(inst, def);
	    inst._zod.check = (payload) => {
	        try {
	            // Trim whitespace from input
	            const trimmed = payload.value.trim();
	            // @ts-ignore
	            const url = new URL(trimmed);
	            if (def.hostname) {
	                def.hostname.lastIndex = 0;
	                if (!def.hostname.test(url.hostname)) {
	                    payload.issues.push({
	                        code: "invalid_format",
	                        format: "url",
	                        note: "Invalid hostname",
	                        pattern: regexes$1.hostname.source,
	                        input: payload.value,
	                        inst,
	                        continue: !def.abort,
	                    });
	                }
	            }
	            if (def.protocol) {
	                def.protocol.lastIndex = 0;
	                if (!def.protocol.test(url.protocol.endsWith(":") ? url.protocol.slice(0, -1) : url.protocol)) {
	                    payload.issues.push({
	                        code: "invalid_format",
	                        format: "url",
	                        note: "Invalid protocol",
	                        pattern: def.protocol.source,
	                        input: payload.value,
	                        inst,
	                        continue: !def.abort,
	                    });
	                }
	            }
	            // Set the output value based on normalize flag
	            if (def.normalize) {
	                // Use normalized URL
	                payload.value = url.href;
	            }
	            else {
	                // Preserve the original input (trimmed)
	                payload.value = trimmed;
	            }
	            return;
	        }
	        catch (_) {
	            payload.issues.push({
	                code: "invalid_format",
	                format: "url",
	                input: payload.value,
	                inst,
	                continue: !def.abort,
	            });
	        }
	    };
	});
	exports.$ZodEmoji = core.$constructor("$ZodEmoji", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.emoji());
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodNanoID = core.$constructor("$ZodNanoID", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.nanoid);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodCUID = core.$constructor("$ZodCUID", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.cuid);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodCUID2 = core.$constructor("$ZodCUID2", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.cuid2);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodULID = core.$constructor("$ZodULID", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.ulid);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodXID = core.$constructor("$ZodXID", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.xid);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodKSUID = core.$constructor("$ZodKSUID", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.ksuid);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodISODateTime = core.$constructor("$ZodISODateTime", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.datetime(def));
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodISODate = core.$constructor("$ZodISODate", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.date);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodISOTime = core.$constructor("$ZodISOTime", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.time(def));
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodISODuration = core.$constructor("$ZodISODuration", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.duration);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodIPv4 = core.$constructor("$ZodIPv4", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.ipv4);
	    exports.$ZodStringFormat.init(inst, def);
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        bag.format = `ipv4`;
	    });
	});
	exports.$ZodIPv6 = core.$constructor("$ZodIPv6", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.ipv6);
	    exports.$ZodStringFormat.init(inst, def);
	    inst._zod.onattach.push((inst) => {
	        const bag = inst._zod.bag;
	        bag.format = `ipv6`;
	    });
	    inst._zod.check = (payload) => {
	        try {
	            // @ts-ignore
	            new URL(`http://[${payload.value}]`);
	            // return;
	        }
	        catch {
	            payload.issues.push({
	                code: "invalid_format",
	                format: "ipv6",
	                input: payload.value,
	                inst,
	                continue: !def.abort,
	            });
	        }
	    };
	});
	exports.$ZodCIDRv4 = core.$constructor("$ZodCIDRv4", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.cidrv4);
	    exports.$ZodStringFormat.init(inst, def);
	});
	exports.$ZodCIDRv6 = core.$constructor("$ZodCIDRv6", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.cidrv6); // not used for validation
	    exports.$ZodStringFormat.init(inst, def);
	    inst._zod.check = (payload) => {
	        const [address, prefix] = payload.value.split("/");
	        try {
	            if (!prefix)
	                throw new Error();
	            const prefixNum = Number(prefix);
	            if (`${prefixNum}` !== prefix)
	                throw new Error();
	            if (prefixNum < 0 || prefixNum > 128)
	                throw new Error();
	            // @ts-ignore
	            new URL(`http://[${address}]`);
	        }
	        catch {
	            payload.issues.push({
	                code: "invalid_format",
	                format: "cidrv6",
	                input: payload.value,
	                inst,
	                continue: !def.abort,
	            });
	        }
	    };
	});
	//////////////////////////////   ZodBase64   //////////////////////////////
	function isValidBase64(data) {
	    if (data === "")
	        return true;
	    if (data.length % 4 !== 0)
	        return false;
	    try {
	        // @ts-ignore
	        atob(data);
	        return true;
	    }
	    catch {
	        return false;
	    }
	}
	exports.$ZodBase64 = core.$constructor("$ZodBase64", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.base64);
	    exports.$ZodStringFormat.init(inst, def);
	    inst._zod.onattach.push((inst) => {
	        inst._zod.bag.contentEncoding = "base64";
	    });
	    inst._zod.check = (payload) => {
	        if (isValidBase64(payload.value))
	            return;
	        payload.issues.push({
	            code: "invalid_format",
	            format: "base64",
	            input: payload.value,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	//////////////////////////////   ZodBase64   //////////////////////////////
	function isValidBase64URL(data) {
	    if (!regexes$1.base64url.test(data))
	        return false;
	    const base64 = data.replace(/[-_]/g, (c) => (c === "-" ? "+" : "/"));
	    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
	    return isValidBase64(padded);
	}
	exports.$ZodBase64URL = core.$constructor("$ZodBase64URL", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.base64url);
	    exports.$ZodStringFormat.init(inst, def);
	    inst._zod.onattach.push((inst) => {
	        inst._zod.bag.contentEncoding = "base64url";
	    });
	    inst._zod.check = (payload) => {
	        if (isValidBase64URL(payload.value))
	            return;
	        payload.issues.push({
	            code: "invalid_format",
	            format: "base64url",
	            input: payload.value,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodE164 = core.$constructor("$ZodE164", (inst, def) => {
	    def.pattern ?? (def.pattern = regexes$1.e164);
	    exports.$ZodStringFormat.init(inst, def);
	});
	//////////////////////////////   ZodJWT   //////////////////////////////
	function isValidJWT(token, algorithm = null) {
	    try {
	        const tokensParts = token.split(".");
	        if (tokensParts.length !== 3)
	            return false;
	        const [header] = tokensParts;
	        if (!header)
	            return false;
	        // @ts-ignore
	        const parsedHeader = JSON.parse(atob(header));
	        if ("typ" in parsedHeader && parsedHeader?.typ !== "JWT")
	            return false;
	        if (!parsedHeader.alg)
	            return false;
	        if (algorithm && (!("alg" in parsedHeader) || parsedHeader.alg !== algorithm))
	            return false;
	        return true;
	    }
	    catch {
	        return false;
	    }
	}
	exports.$ZodJWT = core.$constructor("$ZodJWT", (inst, def) => {
	    exports.$ZodStringFormat.init(inst, def);
	    inst._zod.check = (payload) => {
	        if (isValidJWT(payload.value, def.alg))
	            return;
	        payload.issues.push({
	            code: "invalid_format",
	            format: "jwt",
	            input: payload.value,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodCustomStringFormat = core.$constructor("$ZodCustomStringFormat", (inst, def) => {
	    exports.$ZodStringFormat.init(inst, def);
	    inst._zod.check = (payload) => {
	        if (def.fn(payload.value))
	            return;
	        payload.issues.push({
	            code: "invalid_format",
	            format: def.format,
	            input: payload.value,
	            inst,
	            continue: !def.abort,
	        });
	    };
	});
	exports.$ZodNumber = core.$constructor("$ZodNumber", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.pattern = inst._zod.bag.pattern ?? regexes$1.number;
	    inst._zod.parse = (payload, _ctx) => {
	        if (def.coerce)
	            try {
	                payload.value = Number(payload.value);
	            }
	            catch (_) { }
	        const input = payload.value;
	        if (typeof input === "number" && !Number.isNaN(input) && Number.isFinite(input)) {
	            return payload;
	        }
	        const received = typeof input === "number"
	            ? Number.isNaN(input)
	                ? "NaN"
	                : !Number.isFinite(input)
	                    ? "Infinity"
	                    : undefined
	            : undefined;
	        payload.issues.push({
	            expected: "number",
	            code: "invalid_type",
	            input,
	            inst,
	            ...(received ? { received } : {}),
	        });
	        return payload;
	    };
	});
	exports.$ZodNumberFormat = core.$constructor("$ZodNumber", (inst, def) => {
	    checks.$ZodCheckNumberFormat.init(inst, def);
	    exports.$ZodNumber.init(inst, def); // no format checksp
	});
	exports.$ZodBoolean = core.$constructor("$ZodBoolean", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.pattern = regexes$1.boolean;
	    inst._zod.parse = (payload, _ctx) => {
	        if (def.coerce)
	            try {
	                payload.value = Boolean(payload.value);
	            }
	            catch (_) { }
	        const input = payload.value;
	        if (typeof input === "boolean")
	            return payload;
	        payload.issues.push({
	            expected: "boolean",
	            code: "invalid_type",
	            input,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodBigInt = core.$constructor("$ZodBigInt", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.pattern = regexes$1.bigint;
	    inst._zod.parse = (payload, _ctx) => {
	        if (def.coerce)
	            try {
	                payload.value = BigInt(payload.value);
	            }
	            catch (_) { }
	        if (typeof payload.value === "bigint")
	            return payload;
	        payload.issues.push({
	            expected: "bigint",
	            code: "invalid_type",
	            input: payload.value,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodBigIntFormat = core.$constructor("$ZodBigInt", (inst, def) => {
	    checks.$ZodCheckBigIntFormat.init(inst, def);
	    exports.$ZodBigInt.init(inst, def); // no format checks
	});
	exports.$ZodSymbol = core.$constructor("$ZodSymbol", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, _ctx) => {
	        const input = payload.value;
	        if (typeof input === "symbol")
	            return payload;
	        payload.issues.push({
	            expected: "symbol",
	            code: "invalid_type",
	            input,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodUndefined = core.$constructor("$ZodUndefined", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.pattern = regexes$1.undefined;
	    inst._zod.values = new Set([undefined]);
	    inst._zod.optin = "optional";
	    inst._zod.optout = "optional";
	    inst._zod.parse = (payload, _ctx) => {
	        const input = payload.value;
	        if (typeof input === "undefined")
	            return payload;
	        payload.issues.push({
	            expected: "undefined",
	            code: "invalid_type",
	            input,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodNull = core.$constructor("$ZodNull", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.pattern = regexes$1.null;
	    inst._zod.values = new Set([null]);
	    inst._zod.parse = (payload, _ctx) => {
	        const input = payload.value;
	        if (input === null)
	            return payload;
	        payload.issues.push({
	            expected: "null",
	            code: "invalid_type",
	            input,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodAny = core.$constructor("$ZodAny", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload) => payload;
	});
	exports.$ZodUnknown = core.$constructor("$ZodUnknown", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload) => payload;
	});
	exports.$ZodNever = core.$constructor("$ZodNever", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, _ctx) => {
	        payload.issues.push({
	            expected: "never",
	            code: "invalid_type",
	            input: payload.value,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodVoid = core.$constructor("$ZodVoid", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, _ctx) => {
	        const input = payload.value;
	        if (typeof input === "undefined")
	            return payload;
	        payload.issues.push({
	            expected: "void",
	            code: "invalid_type",
	            input,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodDate = core.$constructor("$ZodDate", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, _ctx) => {
	        if (def.coerce) {
	            try {
	                payload.value = new Date(payload.value);
	            }
	            catch (_err) { }
	        }
	        const input = payload.value;
	        const isDate = input instanceof Date;
	        const isValidDate = isDate && !Number.isNaN(input.getTime());
	        if (isValidDate)
	            return payload;
	        payload.issues.push({
	            expected: "date",
	            code: "invalid_type",
	            input,
	            ...(isDate ? { received: "Invalid Date" } : {}),
	            inst,
	        });
	        return payload;
	    };
	});
	function handleArrayResult(result, final, index) {
	    if (result.issues.length) {
	        final.issues.push(...util.prefixIssues(index, result.issues));
	    }
	    final.value[index] = result.value;
	}
	exports.$ZodArray = core.$constructor("$ZodArray", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, ctx) => {
	        const input = payload.value;
	        if (!Array.isArray(input)) {
	            payload.issues.push({
	                expected: "array",
	                code: "invalid_type",
	                input,
	                inst,
	            });
	            return payload;
	        }
	        payload.value = Array(input.length);
	        const proms = [];
	        for (let i = 0; i < input.length; i++) {
	            const item = input[i];
	            const result = def.element._zod.run({
	                value: item,
	                issues: [],
	            }, ctx);
	            if (result instanceof Promise) {
	                proms.push(result.then((result) => handleArrayResult(result, payload, i)));
	            }
	            else {
	                handleArrayResult(result, payload, i);
	            }
	        }
	        if (proms.length) {
	            return Promise.all(proms).then(() => payload);
	        }
	        return payload; //handleArrayResultsAsync(parseResults, final);
	    };
	});
	function handlePropertyResult(result, final, key, input) {
	    if (result.issues.length) {
	        final.issues.push(...util.prefixIssues(key, result.issues));
	    }
	    if (result.value === undefined) {
	        if (key in input) {
	            final.value[key] = undefined;
	        }
	    }
	    else {
	        final.value[key] = result.value;
	    }
	}
	function normalizeDef(def) {
	    const keys = Object.keys(def.shape);
	    for (const k of keys) {
	        if (!def.shape[k]._zod.traits.has("$ZodType")) {
	            throw new Error(`Invalid element at key "${k}": expected a Zod schema`);
	        }
	    }
	    const okeys = util.optionalKeys(def.shape);
	    return {
	        ...def,
	        keys,
	        keySet: new Set(keys),
	        numKeys: keys.length,
	        optionalKeys: new Set(okeys),
	    };
	}
	function handleCatchall(proms, input, payload, ctx, def, inst) {
	    const unrecognized = [];
	    // iterate over input keys
	    const keySet = def.keySet;
	    const _catchall = def.catchall._zod;
	    const t = _catchall.def.type;
	    for (const key of Object.keys(input)) {
	        if (keySet.has(key))
	            continue;
	        if (t === "never") {
	            unrecognized.push(key);
	            continue;
	        }
	        const r = _catchall.run({ value: input[key], issues: [] }, ctx);
	        if (r instanceof Promise) {
	            proms.push(r.then((r) => handlePropertyResult(r, payload, key, input)));
	        }
	        else {
	            handlePropertyResult(r, payload, key, input);
	        }
	    }
	    if (unrecognized.length) {
	        payload.issues.push({
	            code: "unrecognized_keys",
	            keys: unrecognized,
	            input,
	            inst,
	        });
	    }
	    if (!proms.length)
	        return payload;
	    return Promise.all(proms).then(() => {
	        return payload;
	    });
	}
	exports.$ZodObject = core.$constructor("$ZodObject", (inst, def) => {
	    // requires cast because technically $ZodObject doesn't extend
	    exports.$ZodType.init(inst, def);
	    const _normalized = util.cached(() => normalizeDef(def));
	    util.defineLazy(inst._zod, "propValues", () => {
	        const shape = def.shape;
	        const propValues = {};
	        for (const key in shape) {
	            const field = shape[key]._zod;
	            if (field.values) {
	                propValues[key] ?? (propValues[key] = new Set());
	                for (const v of field.values)
	                    propValues[key].add(v);
	            }
	        }
	        return propValues;
	    });
	    const isObject = util.isObject;
	    const catchall = def.catchall;
	    let value;
	    inst._zod.parse = (payload, ctx) => {
	        value ?? (value = _normalized.value);
	        const input = payload.value;
	        if (!isObject(input)) {
	            payload.issues.push({
	                expected: "object",
	                code: "invalid_type",
	                input,
	                inst,
	            });
	            return payload;
	        }
	        payload.value = {};
	        const proms = [];
	        const shape = value.shape;
	        for (const key of value.keys) {
	            const el = shape[key];
	            const r = el._zod.run({ value: input[key], issues: [] }, ctx);
	            if (r instanceof Promise) {
	                proms.push(r.then((r) => handlePropertyResult(r, payload, key, input)));
	            }
	            else {
	                handlePropertyResult(r, payload, key, input);
	            }
	        }
	        if (!catchall) {
	            return proms.length ? Promise.all(proms).then(() => payload) : payload;
	        }
	        return handleCatchall(proms, input, payload, ctx, _normalized.value, inst);
	    };
	});
	exports.$ZodObjectJIT = core.$constructor("$ZodObjectJIT", (inst, def) => {
	    // requires cast because technically $ZodObject doesn't extend
	    exports.$ZodObject.init(inst, def);
	    const superParse = inst._zod.parse;
	    const _normalized = util.cached(() => normalizeDef(def));
	    const generateFastpass = (shape) => {
	        const doc = new doc_js_1.Doc(["shape", "payload", "ctx"]);
	        const normalized = _normalized.value;
	        const parseStr = (key) => {
	            const k = util.esc(key);
	            return `shape[${k}]._zod.run({ value: input[${k}], issues: [] }, ctx)`;
	        };
	        doc.write(`const input = payload.value;`);
	        const ids = Object.create(null);
	        let counter = 0;
	        for (const key of normalized.keys) {
	            ids[key] = `key_${counter++}`;
	        }
	        // A: preserve key order {
	        doc.write(`const newResult = {}`);
	        for (const key of normalized.keys) {
	            const id = ids[key];
	            const k = util.esc(key);
	            doc.write(`const ${id} = ${parseStr(key)};`);
	            doc.write(`
        if (${id}.issues.length) {
          payload.issues = payload.issues.concat(${id}.issues.map(iss => ({
            ...iss,
            path: iss.path ? [${k}, ...iss.path] : [${k}]
          })));
        }
        
        if (${id}.value === undefined) {
          if (${k} in input) {
            newResult[${k}] = undefined;
          }
        } else {
          newResult[${k}] = ${id}.value;
        }
      `);
	        }
	        doc.write(`payload.value = newResult;`);
	        doc.write(`return payload;`);
	        const fn = doc.compile();
	        return (payload, ctx) => fn(shape, payload, ctx);
	    };
	    let fastpass;
	    const isObject = util.isObject;
	    const jit = !core.globalConfig.jitless;
	    const allowsEval = util.allowsEval;
	    const fastEnabled = jit && allowsEval.value; // && !def.catchall;
	    const catchall = def.catchall;
	    let value;
	    inst._zod.parse = (payload, ctx) => {
	        value ?? (value = _normalized.value);
	        const input = payload.value;
	        if (!isObject(input)) {
	            payload.issues.push({
	                expected: "object",
	                code: "invalid_type",
	                input,
	                inst,
	            });
	            return payload;
	        }
	        if (jit && fastEnabled && ctx?.async === false && ctx.jitless !== true) {
	            // always synchronous
	            if (!fastpass)
	                fastpass = generateFastpass(def.shape);
	            payload = fastpass(payload, ctx);
	            if (!catchall)
	                return payload;
	            return handleCatchall([], input, payload, ctx, value, inst);
	        }
	        return superParse(payload, ctx);
	    };
	});
	function handleUnionResults(results, final, inst, ctx) {
	    for (const result of results) {
	        if (result.issues.length === 0) {
	            final.value = result.value;
	            return final;
	        }
	    }
	    const nonaborted = results.filter((r) => !util.aborted(r));
	    if (nonaborted.length === 1) {
	        final.value = nonaborted[0].value;
	        return nonaborted[0];
	    }
	    final.issues.push({
	        code: "invalid_union",
	        input: final.value,
	        inst,
	        errors: results.map((result) => result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config()))),
	    });
	    return final;
	}
	exports.$ZodUnion = core.$constructor("$ZodUnion", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    util.defineLazy(inst._zod, "optin", () => def.options.some((o) => o._zod.optin === "optional") ? "optional" : undefined);
	    util.defineLazy(inst._zod, "optout", () => def.options.some((o) => o._zod.optout === "optional") ? "optional" : undefined);
	    util.defineLazy(inst._zod, "values", () => {
	        if (def.options.every((o) => o._zod.values)) {
	            return new Set(def.options.flatMap((option) => Array.from(option._zod.values)));
	        }
	        return undefined;
	    });
	    util.defineLazy(inst._zod, "pattern", () => {
	        if (def.options.every((o) => o._zod.pattern)) {
	            const patterns = def.options.map((o) => o._zod.pattern);
	            return new RegExp(`^(${patterns.map((p) => util.cleanRegex(p.source)).join("|")})$`);
	        }
	        return undefined;
	    });
	    const single = def.options.length === 1;
	    const first = def.options[0]._zod.run;
	    inst._zod.parse = (payload, ctx) => {
	        if (single) {
	            return first(payload, ctx);
	        }
	        let async = false;
	        const results = [];
	        for (const option of def.options) {
	            const result = option._zod.run({
	                value: payload.value,
	                issues: [],
	            }, ctx);
	            if (result instanceof Promise) {
	                results.push(result);
	                async = true;
	            }
	            else {
	                if (result.issues.length === 0)
	                    return result;
	                results.push(result);
	            }
	        }
	        if (!async)
	            return handleUnionResults(results, payload, inst, ctx);
	        return Promise.all(results).then((results) => {
	            return handleUnionResults(results, payload, inst, ctx);
	        });
	    };
	});
	exports.$ZodDiscriminatedUnion = 
	/*@__PURE__*/
	core.$constructor("$ZodDiscriminatedUnion", (inst, def) => {
	    exports.$ZodUnion.init(inst, def);
	    const _super = inst._zod.parse;
	    util.defineLazy(inst._zod, "propValues", () => {
	        const propValues = {};
	        for (const option of def.options) {
	            const pv = option._zod.propValues;
	            if (!pv || Object.keys(pv).length === 0)
	                throw new Error(`Invalid discriminated union option at index "${def.options.indexOf(option)}"`);
	            for (const [k, v] of Object.entries(pv)) {
	                if (!propValues[k])
	                    propValues[k] = new Set();
	                for (const val of v) {
	                    propValues[k].add(val);
	                }
	            }
	        }
	        return propValues;
	    });
	    const disc = util.cached(() => {
	        const opts = def.options;
	        const map = new Map();
	        for (const o of opts) {
	            const values = o._zod.propValues?.[def.discriminator];
	            if (!values || values.size === 0)
	                throw new Error(`Invalid discriminated union option at index "${def.options.indexOf(o)}"`);
	            for (const v of values) {
	                if (map.has(v)) {
	                    throw new Error(`Duplicate discriminator value "${String(v)}"`);
	                }
	                map.set(v, o);
	            }
	        }
	        return map;
	    });
	    inst._zod.parse = (payload, ctx) => {
	        const input = payload.value;
	        if (!util.isObject(input)) {
	            payload.issues.push({
	                code: "invalid_type",
	                expected: "object",
	                input,
	                inst,
	            });
	            return payload;
	        }
	        const opt = disc.value.get(input?.[def.discriminator]);
	        if (opt) {
	            return opt._zod.run(payload, ctx);
	        }
	        if (def.unionFallback) {
	            return _super(payload, ctx);
	        }
	        // no matching discriminator
	        payload.issues.push({
	            code: "invalid_union",
	            errors: [],
	            note: "No matching discriminator",
	            discriminator: def.discriminator,
	            input,
	            path: [def.discriminator],
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodIntersection = core.$constructor("$ZodIntersection", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, ctx) => {
	        const input = payload.value;
	        const left = def.left._zod.run({ value: input, issues: [] }, ctx);
	        const right = def.right._zod.run({ value: input, issues: [] }, ctx);
	        const async = left instanceof Promise || right instanceof Promise;
	        if (async) {
	            return Promise.all([left, right]).then(([left, right]) => {
	                return handleIntersectionResults(payload, left, right);
	            });
	        }
	        return handleIntersectionResults(payload, left, right);
	    };
	});
	function mergeValues(a, b) {
	    // const aType = parse.t(a);
	    // const bType = parse.t(b);
	    if (a === b) {
	        return { valid: true, data: a };
	    }
	    if (a instanceof Date && b instanceof Date && +a === +b) {
	        return { valid: true, data: a };
	    }
	    if (util.isPlainObject(a) && util.isPlainObject(b)) {
	        const bKeys = Object.keys(b);
	        const sharedKeys = Object.keys(a).filter((key) => bKeys.indexOf(key) !== -1);
	        const newObj = { ...a, ...b };
	        for (const key of sharedKeys) {
	            const sharedValue = mergeValues(a[key], b[key]);
	            if (!sharedValue.valid) {
	                return {
	                    valid: false,
	                    mergeErrorPath: [key, ...sharedValue.mergeErrorPath],
	                };
	            }
	            newObj[key] = sharedValue.data;
	        }
	        return { valid: true, data: newObj };
	    }
	    if (Array.isArray(a) && Array.isArray(b)) {
	        if (a.length !== b.length) {
	            return { valid: false, mergeErrorPath: [] };
	        }
	        const newArray = [];
	        for (let index = 0; index < a.length; index++) {
	            const itemA = a[index];
	            const itemB = b[index];
	            const sharedValue = mergeValues(itemA, itemB);
	            if (!sharedValue.valid) {
	                return {
	                    valid: false,
	                    mergeErrorPath: [index, ...sharedValue.mergeErrorPath],
	                };
	            }
	            newArray.push(sharedValue.data);
	        }
	        return { valid: true, data: newArray };
	    }
	    return { valid: false, mergeErrorPath: [] };
	}
	function handleIntersectionResults(result, left, right) {
	    if (left.issues.length) {
	        result.issues.push(...left.issues);
	    }
	    if (right.issues.length) {
	        result.issues.push(...right.issues);
	    }
	    if (util.aborted(result))
	        return result;
	    const merged = mergeValues(left.value, right.value);
	    if (!merged.valid) {
	        throw new Error(`Unmergable intersection. Error path: ` + `${JSON.stringify(merged.mergeErrorPath)}`);
	    }
	    result.value = merged.data;
	    return result;
	}
	exports.$ZodTuple = core.$constructor("$ZodTuple", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    const items = def.items;
	    const optStart = items.length - [...items].reverse().findIndex((item) => item._zod.optin !== "optional");
	    inst._zod.parse = (payload, ctx) => {
	        const input = payload.value;
	        if (!Array.isArray(input)) {
	            payload.issues.push({
	                input,
	                inst,
	                expected: "tuple",
	                code: "invalid_type",
	            });
	            return payload;
	        }
	        payload.value = [];
	        const proms = [];
	        if (!def.rest) {
	            const tooBig = input.length > items.length;
	            const tooSmall = input.length < optStart - 1;
	            if (tooBig || tooSmall) {
	                payload.issues.push({
	                    ...(tooBig ? { code: "too_big", maximum: items.length } : { code: "too_small", minimum: items.length }),
	                    input,
	                    inst,
	                    origin: "array",
	                });
	                return payload;
	            }
	        }
	        let i = -1;
	        for (const item of items) {
	            i++;
	            if (i >= input.length)
	                if (i >= optStart)
	                    continue;
	            const result = item._zod.run({
	                value: input[i],
	                issues: [],
	            }, ctx);
	            if (result instanceof Promise) {
	                proms.push(result.then((result) => handleTupleResult(result, payload, i)));
	            }
	            else {
	                handleTupleResult(result, payload, i);
	            }
	        }
	        if (def.rest) {
	            const rest = input.slice(items.length);
	            for (const el of rest) {
	                i++;
	                const result = def.rest._zod.run({
	                    value: el,
	                    issues: [],
	                }, ctx);
	                if (result instanceof Promise) {
	                    proms.push(result.then((result) => handleTupleResult(result, payload, i)));
	                }
	                else {
	                    handleTupleResult(result, payload, i);
	                }
	            }
	        }
	        if (proms.length)
	            return Promise.all(proms).then(() => payload);
	        return payload;
	    };
	});
	function handleTupleResult(result, final, index) {
	    if (result.issues.length) {
	        final.issues.push(...util.prefixIssues(index, result.issues));
	    }
	    final.value[index] = result.value;
	}
	exports.$ZodRecord = core.$constructor("$ZodRecord", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, ctx) => {
	        const input = payload.value;
	        if (!util.isPlainObject(input)) {
	            payload.issues.push({
	                expected: "record",
	                code: "invalid_type",
	                input,
	                inst,
	            });
	            return payload;
	        }
	        const proms = [];
	        if (def.keyType._zod.values) {
	            const values = def.keyType._zod.values;
	            payload.value = {};
	            for (const key of values) {
	                if (typeof key === "string" || typeof key === "number" || typeof key === "symbol") {
	                    const result = def.valueType._zod.run({ value: input[key], issues: [] }, ctx);
	                    if (result instanceof Promise) {
	                        proms.push(result.then((result) => {
	                            if (result.issues.length) {
	                                payload.issues.push(...util.prefixIssues(key, result.issues));
	                            }
	                            payload.value[key] = result.value;
	                        }));
	                    }
	                    else {
	                        if (result.issues.length) {
	                            payload.issues.push(...util.prefixIssues(key, result.issues));
	                        }
	                        payload.value[key] = result.value;
	                    }
	                }
	            }
	            let unrecognized;
	            for (const key in input) {
	                if (!values.has(key)) {
	                    unrecognized = unrecognized ?? [];
	                    unrecognized.push(key);
	                }
	            }
	            if (unrecognized && unrecognized.length > 0) {
	                payload.issues.push({
	                    code: "unrecognized_keys",
	                    input,
	                    inst,
	                    keys: unrecognized,
	                });
	            }
	        }
	        else {
	            payload.value = {};
	            for (const key of Reflect.ownKeys(input)) {
	                if (key === "__proto__")
	                    continue;
	                const keyResult = def.keyType._zod.run({ value: key, issues: [] }, ctx);
	                if (keyResult instanceof Promise) {
	                    throw new Error("Async schemas not supported in object keys currently");
	                }
	                if (keyResult.issues.length) {
	                    payload.issues.push({
	                        code: "invalid_key",
	                        origin: "record",
	                        issues: keyResult.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())),
	                        input: key,
	                        path: [key],
	                        inst,
	                    });
	                    payload.value[keyResult.value] = keyResult.value;
	                    continue;
	                }
	                const result = def.valueType._zod.run({ value: input[key], issues: [] }, ctx);
	                if (result instanceof Promise) {
	                    proms.push(result.then((result) => {
	                        if (result.issues.length) {
	                            payload.issues.push(...util.prefixIssues(key, result.issues));
	                        }
	                        payload.value[keyResult.value] = result.value;
	                    }));
	                }
	                else {
	                    if (result.issues.length) {
	                        payload.issues.push(...util.prefixIssues(key, result.issues));
	                    }
	                    payload.value[keyResult.value] = result.value;
	                }
	            }
	        }
	        if (proms.length) {
	            return Promise.all(proms).then(() => payload);
	        }
	        return payload;
	    };
	});
	exports.$ZodMap = core.$constructor("$ZodMap", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, ctx) => {
	        const input = payload.value;
	        if (!(input instanceof Map)) {
	            payload.issues.push({
	                expected: "map",
	                code: "invalid_type",
	                input,
	                inst,
	            });
	            return payload;
	        }
	        const proms = [];
	        payload.value = new Map();
	        for (const [key, value] of input) {
	            const keyResult = def.keyType._zod.run({ value: key, issues: [] }, ctx);
	            const valueResult = def.valueType._zod.run({ value: value, issues: [] }, ctx);
	            if (keyResult instanceof Promise || valueResult instanceof Promise) {
	                proms.push(Promise.all([keyResult, valueResult]).then(([keyResult, valueResult]) => {
	                    handleMapResult(keyResult, valueResult, payload, key, input, inst, ctx);
	                }));
	            }
	            else {
	                handleMapResult(keyResult, valueResult, payload, key, input, inst, ctx);
	            }
	        }
	        if (proms.length)
	            return Promise.all(proms).then(() => payload);
	        return payload;
	    };
	});
	function handleMapResult(keyResult, valueResult, final, key, input, inst, ctx) {
	    if (keyResult.issues.length) {
	        if (util.propertyKeyTypes.has(typeof key)) {
	            final.issues.push(...util.prefixIssues(key, keyResult.issues));
	        }
	        else {
	            final.issues.push({
	                code: "invalid_key",
	                origin: "map",
	                input,
	                inst,
	                issues: keyResult.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())),
	            });
	        }
	    }
	    if (valueResult.issues.length) {
	        if (util.propertyKeyTypes.has(typeof key)) {
	            final.issues.push(...util.prefixIssues(key, valueResult.issues));
	        }
	        else {
	            final.issues.push({
	                origin: "map",
	                code: "invalid_element",
	                input,
	                inst,
	                key: key,
	                issues: valueResult.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())),
	            });
	        }
	    }
	    final.value.set(keyResult.value, valueResult.value);
	}
	exports.$ZodSet = core.$constructor("$ZodSet", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, ctx) => {
	        const input = payload.value;
	        if (!(input instanceof Set)) {
	            payload.issues.push({
	                input,
	                inst,
	                expected: "set",
	                code: "invalid_type",
	            });
	            return payload;
	        }
	        const proms = [];
	        payload.value = new Set();
	        for (const item of input) {
	            const result = def.valueType._zod.run({ value: item, issues: [] }, ctx);
	            if (result instanceof Promise) {
	                proms.push(result.then((result) => handleSetResult(result, payload)));
	            }
	            else
	                handleSetResult(result, payload);
	        }
	        if (proms.length)
	            return Promise.all(proms).then(() => payload);
	        return payload;
	    };
	});
	function handleSetResult(result, final) {
	    if (result.issues.length) {
	        final.issues.push(...result.issues);
	    }
	    final.value.add(result.value);
	}
	exports.$ZodEnum = core.$constructor("$ZodEnum", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    const values = util.getEnumValues(def.entries);
	    const valuesSet = new Set(values);
	    inst._zod.values = valuesSet;
	    inst._zod.pattern = new RegExp(`^(${values
	        .filter((k) => util.propertyKeyTypes.has(typeof k))
	        .map((o) => (typeof o === "string" ? util.escapeRegex(o) : o.toString()))
	        .join("|")})$`);
	    inst._zod.parse = (payload, _ctx) => {
	        const input = payload.value;
	        if (valuesSet.has(input)) {
	            return payload;
	        }
	        payload.issues.push({
	            code: "invalid_value",
	            values,
	            input,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodLiteral = core.$constructor("$ZodLiteral", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    if (def.values.length === 0) {
	        throw new Error("Cannot create literal schema with no valid values");
	    }
	    inst._zod.values = new Set(def.values);
	    inst._zod.pattern = new RegExp(`^(${def.values
	        .map((o) => (typeof o === "string" ? util.escapeRegex(o) : o ? util.escapeRegex(o.toString()) : String(o)))
	        .join("|")})$`);
	    inst._zod.parse = (payload, _ctx) => {
	        const input = payload.value;
	        if (inst._zod.values.has(input)) {
	            return payload;
	        }
	        payload.issues.push({
	            code: "invalid_value",
	            values: def.values,
	            input,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodFile = core.$constructor("$ZodFile", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, _ctx) => {
	        const input = payload.value;
	        // @ts-ignore
	        if (input instanceof File)
	            return payload;
	        payload.issues.push({
	            expected: "file",
	            code: "invalid_type",
	            input,
	            inst,
	        });
	        return payload;
	    };
	});
	exports.$ZodTransform = core.$constructor("$ZodTransform", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, ctx) => {
	        if (ctx.direction === "backward") {
	            throw new core.$ZodEncodeError(inst.constructor.name);
	        }
	        const _out = def.transform(payload.value, payload);
	        if (ctx.async) {
	            const output = _out instanceof Promise ? _out : Promise.resolve(_out);
	            return output.then((output) => {
	                payload.value = output;
	                return payload;
	            });
	        }
	        if (_out instanceof Promise) {
	            throw new core.$ZodAsyncError();
	        }
	        payload.value = _out;
	        return payload;
	    };
	});
	function handleOptionalResult(result, input) {
	    if (result.issues.length && input === undefined) {
	        return { issues: [], value: undefined };
	    }
	    return result;
	}
	exports.$ZodOptional = core.$constructor("$ZodOptional", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.optin = "optional";
	    inst._zod.optout = "optional";
	    util.defineLazy(inst._zod, "values", () => {
	        return def.innerType._zod.values ? new Set([...def.innerType._zod.values, undefined]) : undefined;
	    });
	    util.defineLazy(inst._zod, "pattern", () => {
	        const pattern = def.innerType._zod.pattern;
	        return pattern ? new RegExp(`^(${util.cleanRegex(pattern.source)})?$`) : undefined;
	    });
	    inst._zod.parse = (payload, ctx) => {
	        if (def.innerType._zod.optin === "optional") {
	            const result = def.innerType._zod.run(payload, ctx);
	            if (result instanceof Promise)
	                return result.then((r) => handleOptionalResult(r, payload.value));
	            return handleOptionalResult(result, payload.value);
	        }
	        if (payload.value === undefined) {
	            return payload;
	        }
	        return def.innerType._zod.run(payload, ctx);
	    };
	});
	exports.$ZodNullable = core.$constructor("$ZodNullable", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    util.defineLazy(inst._zod, "optin", () => def.innerType._zod.optin);
	    util.defineLazy(inst._zod, "optout", () => def.innerType._zod.optout);
	    util.defineLazy(inst._zod, "pattern", () => {
	        const pattern = def.innerType._zod.pattern;
	        return pattern ? new RegExp(`^(${util.cleanRegex(pattern.source)}|null)$`) : undefined;
	    });
	    util.defineLazy(inst._zod, "values", () => {
	        return def.innerType._zod.values ? new Set([...def.innerType._zod.values, null]) : undefined;
	    });
	    inst._zod.parse = (payload, ctx) => {
	        // Forward direction (decode): allow null to pass through
	        if (payload.value === null)
	            return payload;
	        return def.innerType._zod.run(payload, ctx);
	    };
	});
	exports.$ZodDefault = core.$constructor("$ZodDefault", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    // inst._zod.qin = "true";
	    inst._zod.optin = "optional";
	    util.defineLazy(inst._zod, "values", () => def.innerType._zod.values);
	    inst._zod.parse = (payload, ctx) => {
	        if (ctx.direction === "backward") {
	            return def.innerType._zod.run(payload, ctx);
	        }
	        // Forward direction (decode): apply defaults for undefined input
	        if (payload.value === undefined) {
	            payload.value = def.defaultValue;
	            /**
	             * $ZodDefault returns the default value immediately in forward direction.
	             * It doesn't pass the default value into the validator ("prefault"). There's no reason to pass the default value through validation. The validity of the default is enforced by TypeScript statically. Otherwise, it's the responsibility of the user to ensure the default is valid. In the case of pipes with divergent in/out types, you can specify the default on the `in` schema of your ZodPipe to set a "prefault" for the pipe.   */
	            return payload;
	        }
	        // Forward direction: continue with default handling
	        const result = def.innerType._zod.run(payload, ctx);
	        if (result instanceof Promise) {
	            return result.then((result) => handleDefaultResult(result, def));
	        }
	        return handleDefaultResult(result, def);
	    };
	});
	function handleDefaultResult(payload, def) {
	    if (payload.value === undefined) {
	        payload.value = def.defaultValue;
	    }
	    return payload;
	}
	exports.$ZodPrefault = core.$constructor("$ZodPrefault", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.optin = "optional";
	    util.defineLazy(inst._zod, "values", () => def.innerType._zod.values);
	    inst._zod.parse = (payload, ctx) => {
	        if (ctx.direction === "backward") {
	            return def.innerType._zod.run(payload, ctx);
	        }
	        // Forward direction (decode): apply prefault for undefined input
	        if (payload.value === undefined) {
	            payload.value = def.defaultValue;
	        }
	        return def.innerType._zod.run(payload, ctx);
	    };
	});
	exports.$ZodNonOptional = core.$constructor("$ZodNonOptional", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    util.defineLazy(inst._zod, "values", () => {
	        const v = def.innerType._zod.values;
	        return v ? new Set([...v].filter((x) => x !== undefined)) : undefined;
	    });
	    inst._zod.parse = (payload, ctx) => {
	        const result = def.innerType._zod.run(payload, ctx);
	        if (result instanceof Promise) {
	            return result.then((result) => handleNonOptionalResult(result, inst));
	        }
	        return handleNonOptionalResult(result, inst);
	    };
	});
	function handleNonOptionalResult(payload, inst) {
	    if (!payload.issues.length && payload.value === undefined) {
	        payload.issues.push({
	            code: "invalid_type",
	            expected: "nonoptional",
	            input: payload.value,
	            inst,
	        });
	    }
	    return payload;
	}
	exports.$ZodSuccess = core.$constructor("$ZodSuccess", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, ctx) => {
	        if (ctx.direction === "backward") {
	            throw new core.$ZodEncodeError("ZodSuccess");
	        }
	        const result = def.innerType._zod.run(payload, ctx);
	        if (result instanceof Promise) {
	            return result.then((result) => {
	                payload.value = result.issues.length === 0;
	                return payload;
	            });
	        }
	        payload.value = result.issues.length === 0;
	        return payload;
	    };
	});
	exports.$ZodCatch = core.$constructor("$ZodCatch", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    util.defineLazy(inst._zod, "optin", () => def.innerType._zod.optin);
	    util.defineLazy(inst._zod, "optout", () => def.innerType._zod.optout);
	    util.defineLazy(inst._zod, "values", () => def.innerType._zod.values);
	    inst._zod.parse = (payload, ctx) => {
	        if (ctx.direction === "backward") {
	            return def.innerType._zod.run(payload, ctx);
	        }
	        // Forward direction (decode): apply catch logic
	        const result = def.innerType._zod.run(payload, ctx);
	        if (result instanceof Promise) {
	            return result.then((result) => {
	                payload.value = result.value;
	                if (result.issues.length) {
	                    payload.value = def.catchValue({
	                        ...payload,
	                        error: {
	                            issues: result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())),
	                        },
	                        input: payload.value,
	                    });
	                    payload.issues = [];
	                }
	                return payload;
	            });
	        }
	        payload.value = result.value;
	        if (result.issues.length) {
	            payload.value = def.catchValue({
	                ...payload,
	                error: {
	                    issues: result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())),
	                },
	                input: payload.value,
	            });
	            payload.issues = [];
	        }
	        return payload;
	    };
	});
	exports.$ZodNaN = core.$constructor("$ZodNaN", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, _ctx) => {
	        if (typeof payload.value !== "number" || !Number.isNaN(payload.value)) {
	            payload.issues.push({
	                input: payload.value,
	                inst,
	                expected: "nan",
	                code: "invalid_type",
	            });
	            return payload;
	        }
	        return payload;
	    };
	});
	exports.$ZodPipe = core.$constructor("$ZodPipe", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    util.defineLazy(inst._zod, "values", () => def.in._zod.values);
	    util.defineLazy(inst._zod, "optin", () => def.in._zod.optin);
	    util.defineLazy(inst._zod, "optout", () => def.out._zod.optout);
	    util.defineLazy(inst._zod, "propValues", () => def.in._zod.propValues);
	    inst._zod.parse = (payload, ctx) => {
	        if (ctx.direction === "backward") {
	            const right = def.out._zod.run(payload, ctx);
	            if (right instanceof Promise) {
	                return right.then((right) => handlePipeResult(right, def.in, ctx));
	            }
	            return handlePipeResult(right, def.in, ctx);
	        }
	        const left = def.in._zod.run(payload, ctx);
	        if (left instanceof Promise) {
	            return left.then((left) => handlePipeResult(left, def.out, ctx));
	        }
	        return handlePipeResult(left, def.out, ctx);
	    };
	});
	function handlePipeResult(left, next, ctx) {
	    if (left.issues.length) {
	        // prevent further checks
	        left.aborted = true;
	        return left;
	    }
	    return next._zod.run({ value: left.value, issues: left.issues }, ctx);
	}
	exports.$ZodCodec = core.$constructor("$ZodCodec", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    util.defineLazy(inst._zod, "values", () => def.in._zod.values);
	    util.defineLazy(inst._zod, "optin", () => def.in._zod.optin);
	    util.defineLazy(inst._zod, "optout", () => def.out._zod.optout);
	    util.defineLazy(inst._zod, "propValues", () => def.in._zod.propValues);
	    inst._zod.parse = (payload, ctx) => {
	        const direction = ctx.direction || "forward";
	        if (direction === "forward") {
	            const left = def.in._zod.run(payload, ctx);
	            if (left instanceof Promise) {
	                return left.then((left) => handleCodecAResult(left, def, ctx));
	            }
	            return handleCodecAResult(left, def, ctx);
	        }
	        else {
	            const right = def.out._zod.run(payload, ctx);
	            if (right instanceof Promise) {
	                return right.then((right) => handleCodecAResult(right, def, ctx));
	            }
	            return handleCodecAResult(right, def, ctx);
	        }
	    };
	});
	function handleCodecAResult(result, def, ctx) {
	    if (result.issues.length) {
	        // prevent further checks
	        result.aborted = true;
	        return result;
	    }
	    const direction = ctx.direction || "forward";
	    if (direction === "forward") {
	        const transformed = def.transform(result.value, result);
	        if (transformed instanceof Promise) {
	            return transformed.then((value) => handleCodecTxResult(result, value, def.out, ctx));
	        }
	        return handleCodecTxResult(result, transformed, def.out, ctx);
	    }
	    else {
	        const transformed = def.reverseTransform(result.value, result);
	        if (transformed instanceof Promise) {
	            return transformed.then((value) => handleCodecTxResult(result, value, def.in, ctx));
	        }
	        return handleCodecTxResult(result, transformed, def.in, ctx);
	    }
	}
	function handleCodecTxResult(left, value, nextSchema, ctx) {
	    // Check if transform added any issues
	    if (left.issues.length) {
	        left.aborted = true;
	        return left;
	    }
	    return nextSchema._zod.run({ value, issues: left.issues }, ctx);
	}
	exports.$ZodReadonly = core.$constructor("$ZodReadonly", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    util.defineLazy(inst._zod, "propValues", () => def.innerType._zod.propValues);
	    util.defineLazy(inst._zod, "values", () => def.innerType._zod.values);
	    util.defineLazy(inst._zod, "optin", () => def.innerType._zod.optin);
	    util.defineLazy(inst._zod, "optout", () => def.innerType._zod.optout);
	    inst._zod.parse = (payload, ctx) => {
	        if (ctx.direction === "backward") {
	            return def.innerType._zod.run(payload, ctx);
	        }
	        const result = def.innerType._zod.run(payload, ctx);
	        if (result instanceof Promise) {
	            return result.then(handleReadonlyResult);
	        }
	        return handleReadonlyResult(result);
	    };
	});
	function handleReadonlyResult(payload) {
	    payload.value = Object.freeze(payload.value);
	    return payload;
	}
	exports.$ZodTemplateLiteral = core.$constructor("$ZodTemplateLiteral", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    const regexParts = [];
	    for (const part of def.parts) {
	        if (typeof part === "object" && part !== null) {
	            // is Zod schema
	            if (!part._zod.pattern) {
	                // if (!source)
	                throw new Error(`Invalid template literal part, no pattern found: ${[...part._zod.traits].shift()}`);
	            }
	            const source = part._zod.pattern instanceof RegExp ? part._zod.pattern.source : part._zod.pattern;
	            if (!source)
	                throw new Error(`Invalid template literal part: ${part._zod.traits}`);
	            const start = source.startsWith("^") ? 1 : 0;
	            const end = source.endsWith("$") ? source.length - 1 : source.length;
	            regexParts.push(source.slice(start, end));
	        }
	        else if (part === null || util.primitiveTypes.has(typeof part)) {
	            regexParts.push(util.escapeRegex(`${part}`));
	        }
	        else {
	            throw new Error(`Invalid template literal part: ${part}`);
	        }
	    }
	    inst._zod.pattern = new RegExp(`^${regexParts.join("")}$`);
	    inst._zod.parse = (payload, _ctx) => {
	        if (typeof payload.value !== "string") {
	            payload.issues.push({
	                input: payload.value,
	                inst,
	                expected: "template_literal",
	                code: "invalid_type",
	            });
	            return payload;
	        }
	        inst._zod.pattern.lastIndex = 0;
	        if (!inst._zod.pattern.test(payload.value)) {
	            payload.issues.push({
	                input: payload.value,
	                inst,
	                code: "invalid_format",
	                format: def.format ?? "template_literal",
	                pattern: inst._zod.pattern.source,
	            });
	            return payload;
	        }
	        return payload;
	    };
	});
	exports.$ZodFunction = core.$constructor("$ZodFunction", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._def = def;
	    inst._zod.def = def;
	    inst.implement = (func) => {
	        if (typeof func !== "function") {
	            throw new Error("implement() must be called with a function");
	        }
	        return function (...args) {
	            const parsedArgs = inst._def.input ? (0, parse_js_1.parse)(inst._def.input, args) : args;
	            const result = Reflect.apply(func, this, parsedArgs);
	            if (inst._def.output) {
	                return (0, parse_js_1.parse)(inst._def.output, result);
	            }
	            return result;
	        };
	    };
	    inst.implementAsync = (func) => {
	        if (typeof func !== "function") {
	            throw new Error("implementAsync() must be called with a function");
	        }
	        return async function (...args) {
	            const parsedArgs = inst._def.input ? await (0, parse_js_1.parseAsync)(inst._def.input, args) : args;
	            const result = await Reflect.apply(func, this, parsedArgs);
	            if (inst._def.output) {
	                return await (0, parse_js_1.parseAsync)(inst._def.output, result);
	            }
	            return result;
	        };
	    };
	    inst._zod.parse = (payload, _ctx) => {
	        if (typeof payload.value !== "function") {
	            payload.issues.push({
	                code: "invalid_type",
	                expected: "function",
	                input: payload.value,
	                inst,
	            });
	            return payload;
	        }
	        // Check if output is a promise type to determine if we should use async implementation
	        const hasPromiseOutput = inst._def.output && inst._def.output._zod.def.type === "promise";
	        if (hasPromiseOutput) {
	            payload.value = inst.implementAsync(payload.value);
	        }
	        else {
	            payload.value = inst.implement(payload.value);
	        }
	        return payload;
	    };
	    inst.input = (...args) => {
	        const F = inst.constructor;
	        if (Array.isArray(args[0])) {
	            return new F({
	                type: "function",
	                input: new exports.$ZodTuple({
	                    type: "tuple",
	                    items: args[0],
	                    rest: args[1],
	                }),
	                output: inst._def.output,
	            });
	        }
	        return new F({
	            type: "function",
	            input: args[0],
	            output: inst._def.output,
	        });
	    };
	    inst.output = (output) => {
	        const F = inst.constructor;
	        return new F({
	            type: "function",
	            input: inst._def.input,
	            output,
	        });
	    };
	    return inst;
	});
	exports.$ZodPromise = core.$constructor("$ZodPromise", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, ctx) => {
	        return Promise.resolve(payload.value).then((inner) => def.innerType._zod.run({ value: inner, issues: [] }, ctx));
	    };
	});
	exports.$ZodLazy = core.$constructor("$ZodLazy", (inst, def) => {
	    exports.$ZodType.init(inst, def);
	    // let _innerType!: any;
	    // util.defineLazy(def, "getter", () => {
	    //   if (!_innerType) {
	    //     _innerType = def.getter();
	    //   }
	    //   return () => _innerType;
	    // });
	    util.defineLazy(inst._zod, "innerType", () => def.getter());
	    util.defineLazy(inst._zod, "pattern", () => inst._zod.innerType._zod.pattern);
	    util.defineLazy(inst._zod, "propValues", () => inst._zod.innerType._zod.propValues);
	    util.defineLazy(inst._zod, "optin", () => inst._zod.innerType._zod.optin ?? undefined);
	    util.defineLazy(inst._zod, "optout", () => inst._zod.innerType._zod.optout ?? undefined);
	    inst._zod.parse = (payload, ctx) => {
	        const inner = inst._zod.innerType;
	        return inner._zod.run(payload, ctx);
	    };
	});
	exports.$ZodCustom = core.$constructor("$ZodCustom", (inst, def) => {
	    checks.$ZodCheck.init(inst, def);
	    exports.$ZodType.init(inst, def);
	    inst._zod.parse = (payload, _) => {
	        return payload;
	    };
	    inst._zod.check = (payload) => {
	        const input = payload.value;
	        const r = def.fn(input);
	        if (r instanceof Promise) {
	            return r.then((r) => handleRefineResult(r, payload, input, inst));
	        }
	        handleRefineResult(r, payload, input, inst);
	        return;
	    };
	});
	function handleRefineResult(result, payload, input, inst) {
	    if (!result) {
	        const _iss = {
	            code: "custom",
	            input,
	            inst, // incorporates params.error into issue reporting
	            path: [...(inst._zod.def.path ?? [])], // incorporates params.error into issue reporting
	            continue: !inst._zod.def.abort,
	            // params: inst._zod.def.params,
	        };
	        if (inst._zod.def.params)
	            _iss.params = inst._zod.def.params;
	        payload.issues.push(util.issue(_iss));
	    }
	}
} (schemas$3));

var locales = {};

var arExports = {};
var ar$1 = {
  get exports(){ return arExports; },
  set exports(v){ arExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "حرف", verb: "أن يحوي" },
	        file: { unit: "بايت", verb: "أن يحوي" },
	        array: { unit: "عنصر", verb: "أن يحوي" },
	        set: { unit: "عنصر", verb: "أن يحوي" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "number";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "مدخل",
	        email: "بريد إلكتروني",
	        url: "رابط",
	        emoji: "إيموجي",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "تاريخ ووقت بمعيار ISO",
	        date: "تاريخ بمعيار ISO",
	        time: "وقت بمعيار ISO",
	        duration: "مدة بمعيار ISO",
	        ipv4: "عنوان IPv4",
	        ipv6: "عنوان IPv6",
	        cidrv4: "مدى عناوين بصيغة IPv4",
	        cidrv6: "مدى عناوين بصيغة IPv6",
	        base64: "نَص بترميز base64-encoded",
	        base64url: "نَص بترميز base64url-encoded",
	        json_string: "نَص على هيئة JSON",
	        e164: "رقم هاتف بمعيار E.164",
	        jwt: "JWT",
	        template_literal: "مدخل",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `مدخلات غير مقبولة: يفترض إدخال ${issue.expected}، ولكن تم إدخال ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `مدخلات غير مقبولة: يفترض إدخال ${util.stringifyPrimitive(issue.values[0])}`;
	                return `اختيار غير مقبول: يتوقع انتقاء أحد هذه الخيارات: ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return ` أكبر من اللازم: يفترض أن تكون ${issue.origin ?? "القيمة"} ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "عنصر"}`;
	                return `أكبر من اللازم: يفترض أن تكون ${issue.origin ?? "القيمة"} ${adj} ${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `أصغر من اللازم: يفترض لـ ${issue.origin} أن يكون ${adj} ${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `أصغر من اللازم: يفترض لـ ${issue.origin} أن يكون ${adj} ${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `نَص غير مقبول: يجب أن يبدأ بـ "${issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `نَص غير مقبول: يجب أن ينتهي بـ "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `نَص غير مقبول: يجب أن يتضمَّن "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `نَص غير مقبول: يجب أن يطابق النمط ${_issue.pattern}`;
	                return `${Nouns[_issue.format] ?? issue.format} غير مقبول`;
	            }
	            case "not_multiple_of":
	                return `رقم غير مقبول: يجب أن يكون من مضاعفات ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `معرف${issue.keys.length > 1 ? "ات" : ""} غريب${issue.keys.length > 1 ? "ة" : ""}: ${util.joinValues(issue.keys, "، ")}`;
	            case "invalid_key":
	                return `معرف غير مقبول في ${issue.origin}`;
	            case "invalid_union":
	                return "مدخل غير مقبول";
	            case "invalid_element":
	                return `مدخل غير مقبول في ${issue.origin}`;
	            default:
	                return "مدخل غير مقبول";
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ar$1, arExports));

var azExports = {};
var az = {
  get exports(){ return azExports; },
  set exports(v){ azExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "simvol", verb: "olmalıdır" },
	        file: { unit: "bayt", verb: "olmalıdır" },
	        array: { unit: "element", verb: "olmalıdır" },
	        set: { unit: "element", verb: "olmalıdır" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "number";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "input",
	        email: "email address",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO datetime",
	        date: "ISO date",
	        time: "ISO time",
	        duration: "ISO duration",
	        ipv4: "IPv4 address",
	        ipv6: "IPv6 address",
	        cidrv4: "IPv4 range",
	        cidrv6: "IPv6 range",
	        base64: "base64-encoded string",
	        base64url: "base64url-encoded string",
	        json_string: "JSON string",
	        e164: "E.164 number",
	        jwt: "JWT",
	        template_literal: "input",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Yanlış dəyər: gözlənilən ${issue.expected}, daxil olan ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Yanlış dəyər: gözlənilən ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Yanlış seçim: aşağıdakılardan biri olmalıdır: ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Çox böyük: gözlənilən ${issue.origin ?? "dəyər"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "element"}`;
	                return `Çox böyük: gözlənilən ${issue.origin ?? "dəyər"} ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Çox kiçik: gözlənilən ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                return `Çox kiçik: gözlənilən ${issue.origin} ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Yanlış mətn: "${_issue.prefix}" ilə başlamalıdır`;
	                if (_issue.format === "ends_with")
	                    return `Yanlış mətn: "${_issue.suffix}" ilə bitməlidir`;
	                if (_issue.format === "includes")
	                    return `Yanlış mətn: "${_issue.includes}" daxil olmalıdır`;
	                if (_issue.format === "regex")
	                    return `Yanlış mətn: ${_issue.pattern} şablonuna uyğun olmalıdır`;
	                return `Yanlış ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Yanlış ədəd: ${issue.divisor} ilə bölünə bilən olmalıdır`;
	            case "unrecognized_keys":
	                return `Tanınmayan açar${issue.keys.length > 1 ? "lar" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `${issue.origin} daxilində yanlış açar`;
	            case "invalid_union":
	                return "Yanlış dəyər";
	            case "invalid_element":
	                return `${issue.origin} daxilində yanlış dəyər`;
	            default:
	                return `Yanlış dəyər`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (az, azExports));

var beExports = {};
var be$5 = {
  get exports(){ return beExports; },
  set exports(v){ beExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	function getBelarusianPlural(count, one, few, many) {
	    const absCount = Math.abs(count);
	    const lastDigit = absCount % 10;
	    const lastTwoDigits = absCount % 100;
	    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
	        return many;
	    }
	    if (lastDigit === 1) {
	        return one;
	    }
	    if (lastDigit >= 2 && lastDigit <= 4) {
	        return few;
	    }
	    return many;
	}
	const error = () => {
	    const Sizable = {
	        string: {
	            unit: {
	                one: "сімвал",
	                few: "сімвалы",
	                many: "сімвалаў",
	            },
	            verb: "мець",
	        },
	        array: {
	            unit: {
	                one: "элемент",
	                few: "элементы",
	                many: "элементаў",
	            },
	            verb: "мець",
	        },
	        set: {
	            unit: {
	                one: "элемент",
	                few: "элементы",
	                many: "элементаў",
	            },
	            verb: "мець",
	        },
	        file: {
	            unit: {
	                one: "байт",
	                few: "байты",
	                many: "байтаў",
	            },
	            verb: "мець",
	        },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "лік";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "масіў";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "увод",
	        email: "email адрас",
	        url: "URL",
	        emoji: "эмодзі",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO дата і час",
	        date: "ISO дата",
	        time: "ISO час",
	        duration: "ISO працягласць",
	        ipv4: "IPv4 адрас",
	        ipv6: "IPv6 адрас",
	        cidrv4: "IPv4 дыяпазон",
	        cidrv6: "IPv6 дыяпазон",
	        base64: "радок у фармаце base64",
	        base64url: "радок у фармаце base64url",
	        json_string: "JSON радок",
	        e164: "нумар E.164",
	        jwt: "JWT",
	        template_literal: "увод",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Няправільны ўвод: чакаўся ${issue.expected}, атрымана ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Няправільны ўвод: чакалася ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Няправільны варыянт: чакаўся адзін з ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    const maxValue = Number(issue.maximum);
	                    const unit = getBelarusianPlural(maxValue, sizing.unit.one, sizing.unit.few, sizing.unit.many);
	                    return `Занадта вялікі: чакалася, што ${issue.origin ?? "значэнне"} павінна ${sizing.verb} ${adj}${issue.maximum.toString()} ${unit}`;
	                }
	                return `Занадта вялікі: чакалася, што ${issue.origin ?? "значэнне"} павінна быць ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    const minValue = Number(issue.minimum);
	                    const unit = getBelarusianPlural(minValue, sizing.unit.one, sizing.unit.few, sizing.unit.many);
	                    return `Занадта малы: чакалася, што ${issue.origin} павінна ${sizing.verb} ${adj}${issue.minimum.toString()} ${unit}`;
	                }
	                return `Занадта малы: чакалася, што ${issue.origin} павінна быць ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Няправільны радок: павінен пачынацца з "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Няправільны радок: павінен заканчвацца на "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Няправільны радок: павінен змяшчаць "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Няправільны радок: павінен адпавядаць шаблону ${_issue.pattern}`;
	                return `Няправільны ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Няправільны лік: павінен быць кратным ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Нераспазнаны ${issue.keys.length > 1 ? "ключы" : "ключ"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Няправільны ключ у ${issue.origin}`;
	            case "invalid_union":
	                return "Няправільны ўвод";
	            case "invalid_element":
	                return `Няправільнае значэнне ў ${issue.origin}`;
	            default:
	                return `Няправільны ўвод`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (be$5, beExports));

var caExports = {};
var ca = {
  get exports(){ return caExports; },
  set exports(v){ caExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "caràcters", verb: "contenir" },
	        file: { unit: "bytes", verb: "contenir" },
	        array: { unit: "elements", verb: "contenir" },
	        set: { unit: "elements", verb: "contenir" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "number";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "entrada",
	        email: "adreça electrònica",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "data i hora ISO",
	        date: "data ISO",
	        time: "hora ISO",
	        duration: "durada ISO",
	        ipv4: "adreça IPv4",
	        ipv6: "adreça IPv6",
	        cidrv4: "rang IPv4",
	        cidrv6: "rang IPv6",
	        base64: "cadena codificada en base64",
	        base64url: "cadena codificada en base64url",
	        json_string: "cadena JSON",
	        e164: "número E.164",
	        jwt: "JWT",
	        template_literal: "entrada",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Tipus invàlid: s'esperava ${issue.expected}, s'ha rebut ${parsedType(issue.input)}`;
	            // return `Tipus invàlid: s'esperava ${issue.expected}, s'ha rebut ${util.getParsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Valor invàlid: s'esperava ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Opció invàlida: s'esperava una de ${util.joinValues(issue.values, " o ")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "com a màxim" : "menys de";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Massa gran: s'esperava que ${issue.origin ?? "el valor"} contingués ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "elements"}`;
	                return `Massa gran: s'esperava que ${issue.origin ?? "el valor"} fos ${adj} ${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? "com a mínim" : "més de";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Massa petit: s'esperava que ${issue.origin} contingués ${adj} ${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Massa petit: s'esperava que ${issue.origin} fos ${adj} ${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `Format invàlid: ha de començar amb "${_issue.prefix}"`;
	                }
	                if (_issue.format === "ends_with")
	                    return `Format invàlid: ha d'acabar amb "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Format invàlid: ha d'incloure "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Format invàlid: ha de coincidir amb el patró ${_issue.pattern}`;
	                return `Format invàlid per a ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Número invàlid: ha de ser múltiple de ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Clau${issue.keys.length > 1 ? "s" : ""} no reconeguda${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Clau invàlida a ${issue.origin}`;
	            case "invalid_union":
	                return "Entrada invàlida"; // Could also be "Tipus d'unió invàlid" but "Entrada invàlida" is more general
	            case "invalid_element":
	                return `Element invàlid a ${issue.origin}`;
	            default:
	                return `Entrada invàlida`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ca, caExports));

var csExports = {};
var cs = {
  get exports(){ return csExports; },
  set exports(v){ csExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "znaků", verb: "mít" },
	        file: { unit: "bajtů", verb: "mít" },
	        array: { unit: "prvků", verb: "mít" },
	        set: { unit: "prvků", verb: "mít" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "číslo";
	            }
	            case "string": {
	                return "řetězec";
	            }
	            case "boolean": {
	                return "boolean";
	            }
	            case "bigint": {
	                return "bigint";
	            }
	            case "function": {
	                return "funkce";
	            }
	            case "symbol": {
	                return "symbol";
	            }
	            case "undefined": {
	                return "undefined";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "pole";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "regulární výraz",
	        email: "e-mailová adresa",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "datum a čas ve formátu ISO",
	        date: "datum ve formátu ISO",
	        time: "čas ve formátu ISO",
	        duration: "doba trvání ISO",
	        ipv4: "IPv4 adresa",
	        ipv6: "IPv6 adresa",
	        cidrv4: "rozsah IPv4",
	        cidrv6: "rozsah IPv6",
	        base64: "řetězec zakódovaný ve formátu base64",
	        base64url: "řetězec zakódovaný ve formátu base64url",
	        json_string: "řetězec ve formátu JSON",
	        e164: "číslo E.164",
	        jwt: "JWT",
	        template_literal: "vstup",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Neplatný vstup: očekáváno ${issue.expected}, obdrženo ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Neplatný vstup: očekáváno ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Neplatná možnost: očekávána jedna z hodnot ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Hodnota je příliš velká: ${issue.origin ?? "hodnota"} musí mít ${adj}${issue.maximum.toString()} ${sizing.unit ?? "prvků"}`;
	                }
	                return `Hodnota je příliš velká: ${issue.origin ?? "hodnota"} musí být ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Hodnota je příliš malá: ${issue.origin ?? "hodnota"} musí mít ${adj}${issue.minimum.toString()} ${sizing.unit ?? "prvků"}`;
	                }
	                return `Hodnota je příliš malá: ${issue.origin ?? "hodnota"} musí být ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Neplatný řetězec: musí začínat na "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Neplatný řetězec: musí končit na "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Neplatný řetězec: musí obsahovat "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Neplatný řetězec: musí odpovídat vzoru ${_issue.pattern}`;
	                return `Neplatný formát ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Neplatné číslo: musí být násobkem ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Neznámé klíče: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Neplatný klíč v ${issue.origin}`;
	            case "invalid_union":
	                return "Neplatný vstup";
	            case "invalid_element":
	                return `Neplatná hodnota v ${issue.origin}`;
	            default:
	                return `Neplatný vstup`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (cs, csExports));

var daExports = {};
var da = {
  get exports(){ return daExports; },
  set exports(v){ daExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "tegn", verb: "havde" },
	        file: { unit: "bytes", verb: "havde" },
	        array: { unit: "elementer", verb: "indeholdt" },
	        set: { unit: "elementer", verb: "indeholdt" },
	    };
	    const TypeNames = {
	        string: "streng",
	        number: "tal",
	        boolean: "boolean",
	        array: "liste",
	        object: "objekt",
	        set: "sæt",
	        file: "fil",
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    function getTypeName(type) {
	        return TypeNames[type] ?? type;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "tal";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "liste";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	                return "objekt";
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "input",
	        email: "e-mailadresse",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO dato- og klokkeslæt",
	        date: "ISO-dato",
	        time: "ISO-klokkeslæt",
	        duration: "ISO-varighed",
	        ipv4: "IPv4-område",
	        ipv6: "IPv6-område",
	        cidrv4: "IPv4-spektrum",
	        cidrv6: "IPv6-spektrum",
	        base64: "base64-kodet streng",
	        base64url: "base64url-kodet streng",
	        json_string: "JSON-streng",
	        e164: "E.164-nummer",
	        jwt: "JWT",
	        template_literal: "input",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Ugyldigt input: forventede ${getTypeName(issue.expected)}, fik ${getTypeName(parsedType(issue.input))}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Ugyldig værdi: forventede ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Ugyldigt valg: forventede en af følgende ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                const origin = getTypeName(issue.origin);
	                if (sizing)
	                    return `For stor: forventede ${origin ?? "value"} ${sizing.verb} ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "elementer"}`;
	                return `For stor: forventede ${origin ?? "value"} havde ${adj} ${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                const origin = getTypeName(issue.origin);
	                if (sizing) {
	                    return `For lille: forventede ${origin} ${sizing.verb} ${adj} ${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `For lille: forventede ${origin} havde ${adj} ${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Ugyldig streng: skal starte med "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Ugyldig streng: skal ende med "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Ugyldig streng: skal indeholde "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Ugyldig streng: skal matche mønsteret ${_issue.pattern}`;
	                return `Ugyldig ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Ugyldigt tal: skal være deleligt med ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `${issue.keys.length > 1 ? "Ukendte nøgler" : "Ukendt nøgle"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Ugyldig nøgle i ${issue.origin}`;
	            case "invalid_union":
	                return "Ugyldigt input: matcher ingen af de tilladte typer";
	            case "invalid_element":
	                return `Ugyldig værdi i ${issue.origin}`;
	            default:
	                return `Ugyldigt input`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (da, daExports));

var deExports = {};
var de$1 = {
  get exports(){ return deExports; },
  set exports(v){ deExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "Zeichen", verb: "zu haben" },
	        file: { unit: "Bytes", verb: "zu haben" },
	        array: { unit: "Elemente", verb: "zu haben" },
	        set: { unit: "Elemente", verb: "zu haben" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "Zahl";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "Array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "Eingabe",
	        email: "E-Mail-Adresse",
	        url: "URL",
	        emoji: "Emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO-Datum und -Uhrzeit",
	        date: "ISO-Datum",
	        time: "ISO-Uhrzeit",
	        duration: "ISO-Dauer",
	        ipv4: "IPv4-Adresse",
	        ipv6: "IPv6-Adresse",
	        cidrv4: "IPv4-Bereich",
	        cidrv6: "IPv6-Bereich",
	        base64: "Base64-codierter String",
	        base64url: "Base64-URL-codierter String",
	        json_string: "JSON-String",
	        e164: "E.164-Nummer",
	        jwt: "JWT",
	        template_literal: "Eingabe",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Ungültige Eingabe: erwartet ${issue.expected}, erhalten ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Ungültige Eingabe: erwartet ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Ungültige Option: erwartet eine von ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Zu groß: erwartet, dass ${issue.origin ?? "Wert"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "Elemente"} hat`;
	                return `Zu groß: erwartet, dass ${issue.origin ?? "Wert"} ${adj}${issue.maximum.toString()} ist`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Zu klein: erwartet, dass ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit} hat`;
	                }
	                return `Zu klein: erwartet, dass ${issue.origin} ${adj}${issue.minimum.toString()} ist`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Ungültiger String: muss mit "${_issue.prefix}" beginnen`;
	                if (_issue.format === "ends_with")
	                    return `Ungültiger String: muss mit "${_issue.suffix}" enden`;
	                if (_issue.format === "includes")
	                    return `Ungültiger String: muss "${_issue.includes}" enthalten`;
	                if (_issue.format === "regex")
	                    return `Ungültiger String: muss dem Muster ${_issue.pattern} entsprechen`;
	                return `Ungültig: ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Ungültige Zahl: muss ein Vielfaches von ${issue.divisor} sein`;
	            case "unrecognized_keys":
	                return `${issue.keys.length > 1 ? "Unbekannte Schlüssel" : "Unbekannter Schlüssel"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Ungültiger Schlüssel in ${issue.origin}`;
	            case "invalid_union":
	                return "Ungültige Eingabe";
	            case "invalid_element":
	                return `Ungültiger Wert in ${issue.origin}`;
	            default:
	                return `Ungültige Eingabe`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (de$1, deExports));

var en = {};

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.parsedType = void 0;
	exports.default = default_1;
	const util = __importStar(util$3);
	const parsedType = (data) => {
	    const t = typeof data;
	    switch (t) {
	        case "number": {
	            return Number.isNaN(data) ? "NaN" : "number";
	        }
	        case "object": {
	            if (Array.isArray(data)) {
	                return "array";
	            }
	            if (data === null) {
	                return "null";
	            }
	            if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                return data.constructor.name;
	            }
	        }
	    }
	    return t;
	};
	exports.parsedType = parsedType;
	const error = () => {
	    const Sizable = {
	        string: { unit: "characters", verb: "to have" },
	        file: { unit: "bytes", verb: "to have" },
	        array: { unit: "items", verb: "to have" },
	        set: { unit: "items", verb: "to have" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const Nouns = {
	        regex: "input",
	        email: "email address",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO datetime",
	        date: "ISO date",
	        time: "ISO time",
	        duration: "ISO duration",
	        ipv4: "IPv4 address",
	        ipv6: "IPv6 address",
	        cidrv4: "IPv4 range",
	        cidrv6: "IPv6 range",
	        base64: "base64-encoded string",
	        base64url: "base64url-encoded string",
	        json_string: "JSON string",
	        e164: "E.164 number",
	        jwt: "JWT",
	        template_literal: "input",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Invalid input: expected ${issue.expected}, received ${(0, exports.parsedType)(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Invalid input: expected ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Invalid option: expected one of ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Too big: expected ${issue.origin ?? "value"} to have ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elements"}`;
	                return `Too big: expected ${issue.origin ?? "value"} to be ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Too small: expected ${issue.origin} to have ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Too small: expected ${issue.origin} to be ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `Invalid string: must start with "${_issue.prefix}"`;
	                }
	                if (_issue.format === "ends_with")
	                    return `Invalid string: must end with "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Invalid string: must include "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Invalid string: must match pattern ${_issue.pattern}`;
	                return `Invalid ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Invalid number: must be a multiple of ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Unrecognized key${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Invalid key in ${issue.origin}`;
	            case "invalid_union":
	                return "Invalid input";
	            case "invalid_element":
	                return `Invalid value in ${issue.origin}`;
	            default:
	                return `Invalid input`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
} (en));

var eo = {};

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.parsedType = void 0;
	exports.default = default_1;
	const util = __importStar(util$3);
	const parsedType = (data) => {
	    const t = typeof data;
	    switch (t) {
	        case "number": {
	            return Number.isNaN(data) ? "NaN" : "nombro";
	        }
	        case "object": {
	            if (Array.isArray(data)) {
	                return "tabelo";
	            }
	            if (data === null) {
	                return "senvalora";
	            }
	            if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                return data.constructor.name;
	            }
	        }
	    }
	    return t;
	};
	exports.parsedType = parsedType;
	const error = () => {
	    const Sizable = {
	        string: { unit: "karaktrojn", verb: "havi" },
	        file: { unit: "bajtojn", verb: "havi" },
	        array: { unit: "elementojn", verb: "havi" },
	        set: { unit: "elementojn", verb: "havi" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const Nouns = {
	        regex: "enigo",
	        email: "retadreso",
	        url: "URL",
	        emoji: "emoĝio",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO-datotempo",
	        date: "ISO-dato",
	        time: "ISO-tempo",
	        duration: "ISO-daŭro",
	        ipv4: "IPv4-adreso",
	        ipv6: "IPv6-adreso",
	        cidrv4: "IPv4-rango",
	        cidrv6: "IPv6-rango",
	        base64: "64-ume kodita karaktraro",
	        base64url: "URL-64-ume kodita karaktraro",
	        json_string: "JSON-karaktraro",
	        e164: "E.164-nombro",
	        jwt: "JWT",
	        template_literal: "enigo",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Nevalida enigo: atendiĝis ${issue.expected}, riceviĝis ${(0, exports.parsedType)(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Nevalida enigo: atendiĝis ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Nevalida opcio: atendiĝis unu el ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Tro granda: atendiĝis ke ${issue.origin ?? "valoro"} havu ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementojn"}`;
	                return `Tro granda: atendiĝis ke ${issue.origin ?? "valoro"} havu ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Tro malgranda: atendiĝis ke ${issue.origin} havu ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Tro malgranda: atendiĝis ke ${issue.origin} estu ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Nevalida karaktraro: devas komenciĝi per "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Nevalida karaktraro: devas finiĝi per "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Nevalida karaktraro: devas inkluzivi "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Nevalida karaktraro: devas kongrui kun la modelo ${_issue.pattern}`;
	                return `Nevalida ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Nevalida nombro: devas esti oblo de ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Nekonata${issue.keys.length > 1 ? "j" : ""} ŝlosilo${issue.keys.length > 1 ? "j" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Nevalida ŝlosilo en ${issue.origin}`;
	            case "invalid_union":
	                return "Nevalida enigo";
	            case "invalid_element":
	                return `Nevalida valoro en ${issue.origin}`;
	            default:
	                return `Nevalida enigo`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
} (eo));

var esExports = {};
var es = {
  get exports(){ return esExports; },
  set exports(v){ esExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "caracteres", verb: "tener" },
	        file: { unit: "bytes", verb: "tener" },
	        array: { unit: "elementos", verb: "tener" },
	        set: { unit: "elementos", verb: "tener" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "número";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "arreglo";
	                }
	                if (data === null) {
	                    return "nulo";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "entrada",
	        email: "dirección de correo electrónico",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "fecha y hora ISO",
	        date: "fecha ISO",
	        time: "hora ISO",
	        duration: "duración ISO",
	        ipv4: "dirección IPv4",
	        ipv6: "dirección IPv6",
	        cidrv4: "rango IPv4",
	        cidrv6: "rango IPv6",
	        base64: "cadena codificada en base64",
	        base64url: "URL codificada en base64",
	        json_string: "cadena JSON",
	        e164: "número E.164",
	        jwt: "JWT",
	        template_literal: "entrada",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Entrada inválida: se esperaba ${issue.expected}, recibido ${parsedType(issue.input)}`;
	            // return `Entrada inválida: se esperaba ${issue.expected}, recibido ${util.getParsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Entrada inválida: se esperaba ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Opción inválida: se esperaba una de ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Demasiado grande: se esperaba que ${issue.origin ?? "valor"} tuviera ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementos"}`;
	                return `Demasiado grande: se esperaba que ${issue.origin ?? "valor"} fuera ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Demasiado pequeño: se esperaba que ${issue.origin} tuviera ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Demasiado pequeño: se esperaba que ${issue.origin} fuera ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Cadena inválida: debe comenzar con "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Cadena inválida: debe terminar en "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Cadena inválida: debe incluir "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Cadena inválida: debe coincidir con el patrón ${_issue.pattern}`;
	                return `Inválido ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Número inválido: debe ser múltiplo de ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Llave${issue.keys.length > 1 ? "s" : ""} desconocida${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Llave inválida en ${issue.origin}`;
	            case "invalid_union":
	                return "Entrada inválida";
	            case "invalid_element":
	                return `Valor inválido en ${issue.origin}`;
	            default:
	                return `Entrada inválida`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (es, esExports));

var faExports = {};
var fa = {
  get exports(){ return faExports; },
  set exports(v){ faExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "کاراکتر", verb: "داشته باشد" },
	        file: { unit: "بایت", verb: "داشته باشد" },
	        array: { unit: "آیتم", verb: "داشته باشد" },
	        set: { unit: "آیتم", verb: "داشته باشد" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "عدد";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "آرایه";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "ورودی",
	        email: "آدرس ایمیل",
	        url: "URL",
	        emoji: "ایموجی",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "تاریخ و زمان ایزو",
	        date: "تاریخ ایزو",
	        time: "زمان ایزو",
	        duration: "مدت زمان ایزو",
	        ipv4: "IPv4 آدرس",
	        ipv6: "IPv6 آدرس",
	        cidrv4: "IPv4 دامنه",
	        cidrv6: "IPv6 دامنه",
	        base64: "base64-encoded رشته",
	        base64url: "base64url-encoded رشته",
	        json_string: "JSON رشته",
	        e164: "E.164 عدد",
	        jwt: "JWT",
	        template_literal: "ورودی",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `ورودی نامعتبر: می‌بایست ${issue.expected} می‌بود، ${parsedType(issue.input)} دریافت شد`;
	            case "invalid_value":
	                if (issue.values.length === 1) {
	                    return `ورودی نامعتبر: می‌بایست ${util.stringifyPrimitive(issue.values[0])} می‌بود`;
	                }
	                return `گزینه نامعتبر: می‌بایست یکی از ${util.joinValues(issue.values, "|")} می‌بود`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `خیلی بزرگ: ${issue.origin ?? "مقدار"} باید ${adj}${issue.maximum.toString()} ${sizing.unit ?? "عنصر"} باشد`;
	                }
	                return `خیلی بزرگ: ${issue.origin ?? "مقدار"} باید ${adj}${issue.maximum.toString()} باشد`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `خیلی کوچک: ${issue.origin} باید ${adj}${issue.minimum.toString()} ${sizing.unit} باشد`;
	                }
	                return `خیلی کوچک: ${issue.origin} باید ${adj}${issue.minimum.toString()} باشد`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `رشته نامعتبر: باید با "${_issue.prefix}" شروع شود`;
	                }
	                if (_issue.format === "ends_with") {
	                    return `رشته نامعتبر: باید با "${_issue.suffix}" تمام شود`;
	                }
	                if (_issue.format === "includes") {
	                    return `رشته نامعتبر: باید شامل "${_issue.includes}" باشد`;
	                }
	                if (_issue.format === "regex") {
	                    return `رشته نامعتبر: باید با الگوی ${_issue.pattern} مطابقت داشته باشد`;
	                }
	                return `${Nouns[_issue.format] ?? issue.format} نامعتبر`;
	            }
	            case "not_multiple_of":
	                return `عدد نامعتبر: باید مضرب ${issue.divisor} باشد`;
	            case "unrecognized_keys":
	                return `کلید${issue.keys.length > 1 ? "های" : ""} ناشناس: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `کلید ناشناس در ${issue.origin}`;
	            case "invalid_union":
	                return `ورودی نامعتبر`;
	            case "invalid_element":
	                return `مقدار نامعتبر در ${issue.origin}`;
	            default:
	                return `ورودی نامعتبر`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (fa, faExports));

var fiExports = {};
var fi = {
  get exports(){ return fiExports; },
  set exports(v){ fiExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "merkkiä", subject: "merkkijonon" },
	        file: { unit: "tavua", subject: "tiedoston" },
	        array: { unit: "alkiota", subject: "listan" },
	        set: { unit: "alkiota", subject: "joukon" },
	        number: { unit: "", subject: "luvun" },
	        bigint: { unit: "", subject: "suuren kokonaisluvun" },
	        int: { unit: "", subject: "kokonaisluvun" },
	        date: { unit: "", subject: "päivämäärän" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "number";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "säännöllinen lauseke",
	        email: "sähköpostiosoite",
	        url: "URL-osoite",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO-aikaleima",
	        date: "ISO-päivämäärä",
	        time: "ISO-aika",
	        duration: "ISO-kesto",
	        ipv4: "IPv4-osoite",
	        ipv6: "IPv6-osoite",
	        cidrv4: "IPv4-alue",
	        cidrv6: "IPv6-alue",
	        base64: "base64-koodattu merkkijono",
	        base64url: "base64url-koodattu merkkijono",
	        json_string: "JSON-merkkijono",
	        e164: "E.164-luku",
	        jwt: "JWT",
	        template_literal: "templaattimerkkijono",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Virheellinen tyyppi: odotettiin ${issue.expected}, oli ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Virheellinen syöte: täytyy olla ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Virheellinen valinta: täytyy olla yksi seuraavista: ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Liian suuri: ${sizing.subject} täytyy olla ${adj}${issue.maximum.toString()} ${sizing.unit}`.trim();
	                }
	                return `Liian suuri: arvon täytyy olla ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Liian pieni: ${sizing.subject} täytyy olla ${adj}${issue.minimum.toString()} ${sizing.unit}`.trim();
	                }
	                return `Liian pieni: arvon täytyy olla ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Virheellinen syöte: täytyy alkaa "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Virheellinen syöte: täytyy loppua "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Virheellinen syöte: täytyy sisältää "${_issue.includes}"`;
	                if (_issue.format === "regex") {
	                    return `Virheellinen syöte: täytyy vastata säännöllistä lauseketta ${_issue.pattern}`;
	                }
	                return `Virheellinen ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Virheellinen luku: täytyy olla luvun ${issue.divisor} monikerta`;
	            case "unrecognized_keys":
	                return `${issue.keys.length > 1 ? "Tuntemattomat avaimet" : "Tuntematon avain"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return "Virheellinen avain tietueessa";
	            case "invalid_union":
	                return "Virheellinen unioni";
	            case "invalid_element":
	                return "Virheellinen arvo joukossa";
	            default:
	                return `Virheellinen syöte`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (fi, fiExports));

var frExports = {};
var fr = {
  get exports(){ return frExports; },
  set exports(v){ frExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "caractères", verb: "avoir" },
	        file: { unit: "octets", verb: "avoir" },
	        array: { unit: "éléments", verb: "avoir" },
	        set: { unit: "éléments", verb: "avoir" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "nombre";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "tableau";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "entrée",
	        email: "adresse e-mail",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "date et heure ISO",
	        date: "date ISO",
	        time: "heure ISO",
	        duration: "durée ISO",
	        ipv4: "adresse IPv4",
	        ipv6: "adresse IPv6",
	        cidrv4: "plage IPv4",
	        cidrv6: "plage IPv6",
	        base64: "chaîne encodée en base64",
	        base64url: "chaîne encodée en base64url",
	        json_string: "chaîne JSON",
	        e164: "numéro E.164",
	        jwt: "JWT",
	        template_literal: "entrée",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Entrée invalide : ${issue.expected} attendu, ${parsedType(issue.input)} reçu`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Entrée invalide : ${util.stringifyPrimitive(issue.values[0])} attendu`;
	                return `Option invalide : une valeur parmi ${util.joinValues(issue.values, "|")} attendue`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Trop grand : ${issue.origin ?? "valeur"} doit ${sizing.verb} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "élément(s)"}`;
	                return `Trop grand : ${issue.origin ?? "valeur"} doit être ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Trop petit : ${issue.origin} doit ${sizing.verb} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Trop petit : ${issue.origin} doit être ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Chaîne invalide : doit commencer par "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Chaîne invalide : doit se terminer par "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Chaîne invalide : doit inclure "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Chaîne invalide : doit correspondre au modèle ${_issue.pattern}`;
	                return `${Nouns[_issue.format] ?? issue.format} invalide`;
	            }
	            case "not_multiple_of":
	                return `Nombre invalide : doit être un multiple de ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Clé${issue.keys.length > 1 ? "s" : ""} non reconnue${issue.keys.length > 1 ? "s" : ""} : ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Clé invalide dans ${issue.origin}`;
	            case "invalid_union":
	                return "Entrée invalide";
	            case "invalid_element":
	                return `Valeur invalide dans ${issue.origin}`;
	            default:
	                return `Entrée invalide`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (fr, frExports));

var frCAExports = {};
var frCA = {
  get exports(){ return frCAExports; },
  set exports(v){ frCAExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "caractères", verb: "avoir" },
	        file: { unit: "octets", verb: "avoir" },
	        array: { unit: "éléments", verb: "avoir" },
	        set: { unit: "éléments", verb: "avoir" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "number";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "entrée",
	        email: "adresse courriel",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "date-heure ISO",
	        date: "date ISO",
	        time: "heure ISO",
	        duration: "durée ISO",
	        ipv4: "adresse IPv4",
	        ipv6: "adresse IPv6",
	        cidrv4: "plage IPv4",
	        cidrv6: "plage IPv6",
	        base64: "chaîne encodée en base64",
	        base64url: "chaîne encodée en base64url",
	        json_string: "chaîne JSON",
	        e164: "numéro E.164",
	        jwt: "JWT",
	        template_literal: "entrée",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Entrée invalide : attendu ${issue.expected}, reçu ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Entrée invalide : attendu ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Option invalide : attendu l'une des valeurs suivantes ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "≤" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Trop grand : attendu que ${issue.origin ?? "la valeur"} ait ${adj}${issue.maximum.toString()} ${sizing.unit}`;
	                return `Trop grand : attendu que ${issue.origin ?? "la valeur"} soit ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? "≥" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Trop petit : attendu que ${issue.origin} ait ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Trop petit : attendu que ${issue.origin} soit ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `Chaîne invalide : doit commencer par "${_issue.prefix}"`;
	                }
	                if (_issue.format === "ends_with")
	                    return `Chaîne invalide : doit se terminer par "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Chaîne invalide : doit inclure "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Chaîne invalide : doit correspondre au motif ${_issue.pattern}`;
	                return `${Nouns[_issue.format] ?? issue.format} invalide`;
	            }
	            case "not_multiple_of":
	                return `Nombre invalide : doit être un multiple de ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Clé${issue.keys.length > 1 ? "s" : ""} non reconnue${issue.keys.length > 1 ? "s" : ""} : ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Clé invalide dans ${issue.origin}`;
	            case "invalid_union":
	                return "Entrée invalide";
	            case "invalid_element":
	                return `Valeur invalide dans ${issue.origin}`;
	            default:
	                return `Entrée invalide`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (frCA, frCAExports));

var heExports = {};
var he$3 = {
  get exports(){ return heExports; },
  set exports(v){ heExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "אותיות", verb: "לכלול" },
	        file: { unit: "בייטים", verb: "לכלול" },
	        array: { unit: "פריטים", verb: "לכלול" },
	        set: { unit: "פריטים", verb: "לכלול" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "number";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "קלט",
	        email: "כתובת אימייל",
	        url: "כתובת רשת",
	        emoji: "אימוג'י",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "תאריך וזמן ISO",
	        date: "תאריך ISO",
	        time: "זמן ISO",
	        duration: "משך זמן ISO",
	        ipv4: "כתובת IPv4",
	        ipv6: "כתובת IPv6",
	        cidrv4: "טווח IPv4",
	        cidrv6: "טווח IPv6",
	        base64: "מחרוזת בבסיס 64",
	        base64url: "מחרוזת בבסיס 64 לכתובות רשת",
	        json_string: "מחרוזת JSON",
	        e164: "מספר E.164",
	        jwt: "JWT",
	        template_literal: "קלט",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `קלט לא תקין: צריך ${issue.expected}, התקבל ${parsedType(issue.input)}`;
	            // return `Invalid input: expected ${issue.expected}, received ${util.getParsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `קלט לא תקין: צריך ${util.stringifyPrimitive(issue.values[0])}`;
	                return `קלט לא תקין: צריך אחת מהאפשרויות  ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `גדול מדי: ${issue.origin ?? "value"} צריך להיות ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elements"}`;
	                return `גדול מדי: ${issue.origin ?? "value"} צריך להיות ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `קטן מדי: ${issue.origin} צריך להיות ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `קטן מדי: ${issue.origin} צריך להיות ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `מחרוזת לא תקינה: חייבת להתחיל ב"${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `מחרוזת לא תקינה: חייבת להסתיים ב "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `מחרוזת לא תקינה: חייבת לכלול "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `מחרוזת לא תקינה: חייבת להתאים לתבנית ${_issue.pattern}`;
	                return `${Nouns[_issue.format] ?? issue.format} לא תקין`;
	            }
	            case "not_multiple_of":
	                return `מספר לא תקין: חייב להיות מכפלה של ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `מפתח${issue.keys.length > 1 ? "ות" : ""} לא מזוה${issue.keys.length > 1 ? "ים" : "ה"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `מפתח לא תקין ב${issue.origin}`;
	            case "invalid_union":
	                return "קלט לא תקין";
	            case "invalid_element":
	                return `ערך לא תקין ב${issue.origin}`;
	            default:
	                return `קלט לא תקין`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (he$3, heExports));

var huExports = {};
var hu = {
  get exports(){ return huExports; },
  set exports(v){ huExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "karakter", verb: "legyen" },
	        file: { unit: "byte", verb: "legyen" },
	        array: { unit: "elem", verb: "legyen" },
	        set: { unit: "elem", verb: "legyen" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "szám";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "tömb";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "bemenet",
	        email: "email cím",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO időbélyeg",
	        date: "ISO dátum",
	        time: "ISO idő",
	        duration: "ISO időintervallum",
	        ipv4: "IPv4 cím",
	        ipv6: "IPv6 cím",
	        cidrv4: "IPv4 tartomány",
	        cidrv6: "IPv6 tartomány",
	        base64: "base64-kódolt string",
	        base64url: "base64url-kódolt string",
	        json_string: "JSON string",
	        e164: "E.164 szám",
	        jwt: "JWT",
	        template_literal: "bemenet",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Érvénytelen bemenet: a várt érték ${issue.expected}, a kapott érték ${parsedType(issue.input)}`;
	            // return `Invalid input: expected ${issue.expected}, received ${util.getParsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Érvénytelen bemenet: a várt érték ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Érvénytelen opció: valamelyik érték várt ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Túl nagy: ${issue.origin ?? "érték"} mérete túl nagy ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elem"}`;
	                return `Túl nagy: a bemeneti érték ${issue.origin ?? "érték"} túl nagy: ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Túl kicsi: a bemeneti érték ${issue.origin} mérete túl kicsi ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Túl kicsi: a bemeneti érték ${issue.origin} túl kicsi ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Érvénytelen string: "${_issue.prefix}" értékkel kell kezdődnie`;
	                if (_issue.format === "ends_with")
	                    return `Érvénytelen string: "${_issue.suffix}" értékkel kell végződnie`;
	                if (_issue.format === "includes")
	                    return `Érvénytelen string: "${_issue.includes}" értéket kell tartalmaznia`;
	                if (_issue.format === "regex")
	                    return `Érvénytelen string: ${_issue.pattern} mintának kell megfelelnie`;
	                return `Érvénytelen ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Érvénytelen szám: ${issue.divisor} többszörösének kell lennie`;
	            case "unrecognized_keys":
	                return `Ismeretlen kulcs${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Érvénytelen kulcs ${issue.origin}`;
	            case "invalid_union":
	                return "Érvénytelen bemenet";
	            case "invalid_element":
	                return `Érvénytelen érték: ${issue.origin}`;
	            default:
	                return `Érvénytelen bemenet`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (hu, huExports));

var idExports = {};
var id$1 = {
  get exports(){ return idExports; },
  set exports(v){ idExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "karakter", verb: "memiliki" },
	        file: { unit: "byte", verb: "memiliki" },
	        array: { unit: "item", verb: "memiliki" },
	        set: { unit: "item", verb: "memiliki" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "number";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "input",
	        email: "alamat email",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "tanggal dan waktu format ISO",
	        date: "tanggal format ISO",
	        time: "jam format ISO",
	        duration: "durasi format ISO",
	        ipv4: "alamat IPv4",
	        ipv6: "alamat IPv6",
	        cidrv4: "rentang alamat IPv4",
	        cidrv6: "rentang alamat IPv6",
	        base64: "string dengan enkode base64",
	        base64url: "string dengan enkode base64url",
	        json_string: "string JSON",
	        e164: "angka E.164",
	        jwt: "JWT",
	        template_literal: "input",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Input tidak valid: diharapkan ${issue.expected}, diterima ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Input tidak valid: diharapkan ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Pilihan tidak valid: diharapkan salah satu dari ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Terlalu besar: diharapkan ${issue.origin ?? "value"} memiliki ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elemen"}`;
	                return `Terlalu besar: diharapkan ${issue.origin ?? "value"} menjadi ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Terlalu kecil: diharapkan ${issue.origin} memiliki ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Terlalu kecil: diharapkan ${issue.origin} menjadi ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `String tidak valid: harus dimulai dengan "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `String tidak valid: harus berakhir dengan "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `String tidak valid: harus menyertakan "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `String tidak valid: harus sesuai pola ${_issue.pattern}`;
	                return `${Nouns[_issue.format] ?? issue.format} tidak valid`;
	            }
	            case "not_multiple_of":
	                return `Angka tidak valid: harus kelipatan dari ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Kunci tidak dikenali ${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Kunci tidak valid di ${issue.origin}`;
	            case "invalid_union":
	                return "Input tidak valid";
	            case "invalid_element":
	                return `Nilai tidak valid di ${issue.origin}`;
	            default:
	                return `Input tidak valid`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (id$1, idExports));

var is$1 = {};

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.parsedType = void 0;
	exports.default = default_1;
	const util = __importStar(util$3);
	const parsedType = (data) => {
	    const t = typeof data;
	    switch (t) {
	        case "number": {
	            return Number.isNaN(data) ? "NaN" : "númer";
	        }
	        case "object": {
	            if (Array.isArray(data)) {
	                return "fylki";
	            }
	            if (data === null) {
	                return "null";
	            }
	            if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                return data.constructor.name;
	            }
	        }
	    }
	    return t;
	};
	exports.parsedType = parsedType;
	const error = () => {
	    const Sizable = {
	        string: { unit: "stafi", verb: "að hafa" },
	        file: { unit: "bæti", verb: "að hafa" },
	        array: { unit: "hluti", verb: "að hafa" },
	        set: { unit: "hluti", verb: "að hafa" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const Nouns = {
	        regex: "gildi",
	        email: "netfang",
	        url: "vefslóð",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO dagsetning og tími",
	        date: "ISO dagsetning",
	        time: "ISO tími",
	        duration: "ISO tímalengd",
	        ipv4: "IPv4 address",
	        ipv6: "IPv6 address",
	        cidrv4: "IPv4 range",
	        cidrv6: "IPv6 range",
	        base64: "base64-encoded strengur",
	        base64url: "base64url-encoded strengur",
	        json_string: "JSON strengur",
	        e164: "E.164 tölugildi",
	        jwt: "JWT",
	        template_literal: "gildi",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Rangt gildi: Þú slóst inn ${(0, exports.parsedType)(issue.input)} þar sem á að vera ${issue.expected}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Rangt gildi: gert ráð fyrir ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Ógilt val: má vera eitt af eftirfarandi ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Of stórt: gert er ráð fyrir að ${issue.origin ?? "gildi"} hafi ${adj}${issue.maximum.toString()} ${sizing.unit ?? "hluti"}`;
	                return `Of stórt: gert er ráð fyrir að ${issue.origin ?? "gildi"} sé ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Of lítið: gert er ráð fyrir að ${issue.origin} hafi ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Of lítið: gert er ráð fyrir að ${issue.origin} sé ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `Ógildur strengur: verður að byrja á "${_issue.prefix}"`;
	                }
	                if (_issue.format === "ends_with")
	                    return `Ógildur strengur: verður að enda á "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Ógildur strengur: verður að innihalda "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Ógildur strengur: verður að fylgja mynstri ${_issue.pattern}`;
	                return `Rangt ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Röng tala: verður að vera margfeldi af ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Óþekkt ${issue.keys.length > 1 ? "ir lyklar" : "ur lykill"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Rangur lykill í ${issue.origin}`;
	            case "invalid_union":
	                return "Rangt gildi";
	            case "invalid_element":
	                return `Rangt gildi í ${issue.origin}`;
	            default:
	                return `Rangt gildi`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
} (is$1));

var itExports = {};
var it = {
  get exports(){ return itExports; },
  set exports(v){ itExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "caratteri", verb: "avere" },
	        file: { unit: "byte", verb: "avere" },
	        array: { unit: "elementi", verb: "avere" },
	        set: { unit: "elementi", verb: "avere" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "numero";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "vettore";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "input",
	        email: "indirizzo email",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "data e ora ISO",
	        date: "data ISO",
	        time: "ora ISO",
	        duration: "durata ISO",
	        ipv4: "indirizzo IPv4",
	        ipv6: "indirizzo IPv6",
	        cidrv4: "intervallo IPv4",
	        cidrv6: "intervallo IPv6",
	        base64: "stringa codificata in base64",
	        base64url: "URL codificata in base64",
	        json_string: "stringa JSON",
	        e164: "numero E.164",
	        jwt: "JWT",
	        template_literal: "input",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Input non valido: atteso ${issue.expected}, ricevuto ${parsedType(issue.input)}`;
	            // return `Input non valido: atteso ${issue.expected}, ricevuto ${util.getParsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Input non valido: atteso ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Opzione non valida: atteso uno tra ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Troppo grande: ${issue.origin ?? "valore"} deve avere ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementi"}`;
	                return `Troppo grande: ${issue.origin ?? "valore"} deve essere ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Troppo piccolo: ${issue.origin} deve avere ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Troppo piccolo: ${issue.origin} deve essere ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Stringa non valida: deve iniziare con "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Stringa non valida: deve terminare con "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Stringa non valida: deve includere "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Stringa non valida: deve corrispondere al pattern ${_issue.pattern}`;
	                return `Invalid ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Numero non valido: deve essere un multiplo di ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Chiav${issue.keys.length > 1 ? "i" : "e"} non riconosciut${issue.keys.length > 1 ? "e" : "a"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Chiave non valida in ${issue.origin}`;
	            case "invalid_union":
	                return "Input non valido";
	            case "invalid_element":
	                return `Valore non valido in ${issue.origin}`;
	            default:
	                return `Input non valido`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (it, itExports));

var jaExports = {};
var ja = {
  get exports(){ return jaExports; },
  set exports(v){ jaExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "文字", verb: "である" },
	        file: { unit: "バイト", verb: "である" },
	        array: { unit: "要素", verb: "である" },
	        set: { unit: "要素", verb: "である" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "数値";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "配列";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "入力値",
	        email: "メールアドレス",
	        url: "URL",
	        emoji: "絵文字",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO日時",
	        date: "ISO日付",
	        time: "ISO時刻",
	        duration: "ISO期間",
	        ipv4: "IPv4アドレス",
	        ipv6: "IPv6アドレス",
	        cidrv4: "IPv4範囲",
	        cidrv6: "IPv6範囲",
	        base64: "base64エンコード文字列",
	        base64url: "base64urlエンコード文字列",
	        json_string: "JSON文字列",
	        e164: "E.164番号",
	        jwt: "JWT",
	        template_literal: "入力値",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `無効な入力: ${issue.expected}が期待されましたが、${parsedType(issue.input)}が入力されました`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `無効な入力: ${util.stringifyPrimitive(issue.values[0])}が期待されました`;
	                return `無効な選択: ${util.joinValues(issue.values, "、")}のいずれかである必要があります`;
	            case "too_big": {
	                const adj = issue.inclusive ? "以下である" : "より小さい";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `大きすぎる値: ${issue.origin ?? "値"}は${issue.maximum.toString()}${sizing.unit ?? "要素"}${adj}必要があります`;
	                return `大きすぎる値: ${issue.origin ?? "値"}は${issue.maximum.toString()}${adj}必要があります`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? "以上である" : "より大きい";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `小さすぎる値: ${issue.origin}は${issue.minimum.toString()}${sizing.unit}${adj}必要があります`;
	                return `小さすぎる値: ${issue.origin}は${issue.minimum.toString()}${adj}必要があります`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `無効な文字列: "${_issue.prefix}"で始まる必要があります`;
	                if (_issue.format === "ends_with")
	                    return `無効な文字列: "${_issue.suffix}"で終わる必要があります`;
	                if (_issue.format === "includes")
	                    return `無効な文字列: "${_issue.includes}"を含む必要があります`;
	                if (_issue.format === "regex")
	                    return `無効な文字列: パターン${_issue.pattern}に一致する必要があります`;
	                return `無効な${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `無効な数値: ${issue.divisor}の倍数である必要があります`;
	            case "unrecognized_keys":
	                return `認識されていないキー${issue.keys.length > 1 ? "群" : ""}: ${util.joinValues(issue.keys, "、")}`;
	            case "invalid_key":
	                return `${issue.origin}内の無効なキー`;
	            case "invalid_union":
	                return "無効な入力";
	            case "invalid_element":
	                return `${issue.origin}内の無効な値`;
	            default:
	                return `無効な入力`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ja, jaExports));

var khExports = {};
var kh = {
  get exports(){ return khExports; },
  set exports(v){ khExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "តួអក្សរ", verb: "គួរមាន" },
	        file: { unit: "បៃ", verb: "គួរមាន" },
	        array: { unit: "ធាតុ", verb: "គួរមាន" },
	        set: { unit: "ធាតុ", verb: "គួរមាន" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "មិនមែនជាលេខ (NaN)" : "លេខ";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "អារេ (Array)";
	                }
	                if (data === null) {
	                    return "គ្មានតម្លៃ (null)";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "ទិន្នន័យបញ្ចូល",
	        email: "អាសយដ្ឋានអ៊ីមែល",
	        url: "URL",
	        emoji: "សញ្ញាអារម្មណ៍",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "កាលបរិច្ឆេទ និងម៉ោង ISO",
	        date: "កាលបរិច្ឆេទ ISO",
	        time: "ម៉ោង ISO",
	        duration: "រយៈពេល ISO",
	        ipv4: "អាសយដ្ឋាន IPv4",
	        ipv6: "អាសយដ្ឋាន IPv6",
	        cidrv4: "ដែនអាសយដ្ឋាន IPv4",
	        cidrv6: "ដែនអាសយដ្ឋាន IPv6",
	        base64: "ខ្សែអក្សរអ៊ិកូដ base64",
	        base64url: "ខ្សែអក្សរអ៊ិកូដ base64url",
	        json_string: "ខ្សែអក្សរ JSON",
	        e164: "លេខ E.164",
	        jwt: "JWT",
	        template_literal: "ទិន្នន័យបញ្ចូល",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `ទិន្នន័យបញ្ចូលមិនត្រឹមត្រូវ៖ ត្រូវការ ${issue.expected} ប៉ុន្តែទទួលបាន ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `ទិន្នន័យបញ្ចូលមិនត្រឹមត្រូវ៖ ត្រូវការ ${util.stringifyPrimitive(issue.values[0])}`;
	                return `ជម្រើសមិនត្រឹមត្រូវ៖ ត្រូវជាមួយក្នុងចំណោម ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `ធំពេក៖ ត្រូវការ ${issue.origin ?? "តម្លៃ"} ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "ធាតុ"}`;
	                return `ធំពេក៖ ត្រូវការ ${issue.origin ?? "តម្លៃ"} ${adj} ${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `តូចពេក៖ ត្រូវការ ${issue.origin} ${adj} ${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `តូចពេក៖ ត្រូវការ ${issue.origin} ${adj} ${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `ខ្សែអក្សរមិនត្រឹមត្រូវ៖ ត្រូវចាប់ផ្តើមដោយ "${_issue.prefix}"`;
	                }
	                if (_issue.format === "ends_with")
	                    return `ខ្សែអក្សរមិនត្រឹមត្រូវ៖ ត្រូវបញ្ចប់ដោយ "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `ខ្សែអក្សរមិនត្រឹមត្រូវ៖ ត្រូវមាន "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `ខ្សែអក្សរមិនត្រឹមត្រូវ៖ ត្រូវតែផ្គូផ្គងនឹងទម្រង់ដែលបានកំណត់ ${_issue.pattern}`;
	                return `មិនត្រឹមត្រូវ៖ ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `លេខមិនត្រឹមត្រូវ៖ ត្រូវតែជាពហុគុណនៃ ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `រកឃើញសោមិនស្គាល់៖ ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `សោមិនត្រឹមត្រូវនៅក្នុង ${issue.origin}`;
	            case "invalid_union":
	                return `ទិន្នន័យមិនត្រឹមត្រូវ`;
	            case "invalid_element":
	                return `ទិន្នន័យមិនត្រឹមត្រូវនៅក្នុង ${issue.origin}`;
	            default:
	                return `ទិន្នន័យមិនត្រឹមត្រូវ`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (kh, khExports));

var koExports = {};
var ko = {
  get exports(){ return koExports; },
  set exports(v){ koExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "문자", verb: "to have" },
	        file: { unit: "바이트", verb: "to have" },
	        array: { unit: "개", verb: "to have" },
	        set: { unit: "개", verb: "to have" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "number";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "입력",
	        email: "이메일 주소",
	        url: "URL",
	        emoji: "이모지",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO 날짜시간",
	        date: "ISO 날짜",
	        time: "ISO 시간",
	        duration: "ISO 기간",
	        ipv4: "IPv4 주소",
	        ipv6: "IPv6 주소",
	        cidrv4: "IPv4 범위",
	        cidrv6: "IPv6 범위",
	        base64: "base64 인코딩 문자열",
	        base64url: "base64url 인코딩 문자열",
	        json_string: "JSON 문자열",
	        e164: "E.164 번호",
	        jwt: "JWT",
	        template_literal: "입력",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `잘못된 입력: 예상 타입은 ${issue.expected}, 받은 타입은 ${parsedType(issue.input)}입니다`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `잘못된 입력: 값은 ${util.stringifyPrimitive(issue.values[0])} 이어야 합니다`;
	                return `잘못된 옵션: ${util.joinValues(issue.values, "또는 ")} 중 하나여야 합니다`;
	            case "too_big": {
	                const adj = issue.inclusive ? "이하" : "미만";
	                const suffix = adj === "미만" ? "이어야 합니다" : "여야 합니다";
	                const sizing = getSizing(issue.origin);
	                const unit = sizing?.unit ?? "요소";
	                if (sizing)
	                    return `${issue.origin ?? "값"}이 너무 큽니다: ${issue.maximum.toString()}${unit} ${adj}${suffix}`;
	                return `${issue.origin ?? "값"}이 너무 큽니다: ${issue.maximum.toString()} ${adj}${suffix}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? "이상" : "초과";
	                const suffix = adj === "이상" ? "이어야 합니다" : "여야 합니다";
	                const sizing = getSizing(issue.origin);
	                const unit = sizing?.unit ?? "요소";
	                if (sizing) {
	                    return `${issue.origin ?? "값"}이 너무 작습니다: ${issue.minimum.toString()}${unit} ${adj}${suffix}`;
	                }
	                return `${issue.origin ?? "값"}이 너무 작습니다: ${issue.minimum.toString()} ${adj}${suffix}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `잘못된 문자열: "${_issue.prefix}"(으)로 시작해야 합니다`;
	                }
	                if (_issue.format === "ends_with")
	                    return `잘못된 문자열: "${_issue.suffix}"(으)로 끝나야 합니다`;
	                if (_issue.format === "includes")
	                    return `잘못된 문자열: "${_issue.includes}"을(를) 포함해야 합니다`;
	                if (_issue.format === "regex")
	                    return `잘못된 문자열: 정규식 ${_issue.pattern} 패턴과 일치해야 합니다`;
	                return `잘못된 ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `잘못된 숫자: ${issue.divisor}의 배수여야 합니다`;
	            case "unrecognized_keys":
	                return `인식할 수 없는 키: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `잘못된 키: ${issue.origin}`;
	            case "invalid_union":
	                return `잘못된 입력`;
	            case "invalid_element":
	                return `잘못된 값: ${issue.origin}`;
	            default:
	                return `잘못된 입력`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ko, koExports));

var mkExports = {};
var mk = {
  get exports(){ return mkExports; },
  set exports(v){ mkExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "знаци", verb: "да имаат" },
	        file: { unit: "бајти", verb: "да имаат" },
	        array: { unit: "ставки", verb: "да имаат" },
	        set: { unit: "ставки", verb: "да имаат" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "број";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "низа";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "внес",
	        email: "адреса на е-пошта",
	        url: "URL",
	        emoji: "емоџи",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO датум и време",
	        date: "ISO датум",
	        time: "ISO време",
	        duration: "ISO времетраење",
	        ipv4: "IPv4 адреса",
	        ipv6: "IPv6 адреса",
	        cidrv4: "IPv4 опсег",
	        cidrv6: "IPv6 опсег",
	        base64: "base64-енкодирана низа",
	        base64url: "base64url-енкодирана низа",
	        json_string: "JSON низа",
	        e164: "E.164 број",
	        jwt: "JWT",
	        template_literal: "внес",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Грешен внес: се очекува ${issue.expected}, примено ${parsedType(issue.input)}`;
	            // return `Invalid input: expected ${issue.expected}, received ${util.getParsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Invalid input: expected ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Грешана опција: се очекува една ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Премногу голем: се очекува ${issue.origin ?? "вредноста"} да има ${adj}${issue.maximum.toString()} ${sizing.unit ?? "елементи"}`;
	                return `Премногу голем: се очекува ${issue.origin ?? "вредноста"} да биде ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Премногу мал: се очекува ${issue.origin} да има ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Премногу мал: се очекува ${issue.origin} да биде ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `Неважечка низа: мора да започнува со "${_issue.prefix}"`;
	                }
	                if (_issue.format === "ends_with")
	                    return `Неважечка низа: мора да завршува со "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Неважечка низа: мора да вклучува "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Неважечка низа: мора да одгоара на патернот ${_issue.pattern}`;
	                return `Invalid ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Грешен број: мора да биде делив со ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `${issue.keys.length > 1 ? "Непрепознаени клучеви" : "Непрепознаен клуч"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Грешен клуч во ${issue.origin}`;
	            case "invalid_union":
	                return "Грешен внес";
	            case "invalid_element":
	                return `Грешна вредност во ${issue.origin}`;
	            default:
	                return `Грешен внес`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (mk, mkExports));

var msExports = {};
var ms = {
  get exports(){ return msExports; },
  set exports(v){ msExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "aksara", verb: "mempunyai" },
	        file: { unit: "bait", verb: "mempunyai" },
	        array: { unit: "elemen", verb: "mempunyai" },
	        set: { unit: "elemen", verb: "mempunyai" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "nombor";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "input",
	        email: "alamat e-mel",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "tarikh masa ISO",
	        date: "tarikh ISO",
	        time: "masa ISO",
	        duration: "tempoh ISO",
	        ipv4: "alamat IPv4",
	        ipv6: "alamat IPv6",
	        cidrv4: "julat IPv4",
	        cidrv6: "julat IPv6",
	        base64: "string dikodkan base64",
	        base64url: "string dikodkan base64url",
	        json_string: "string JSON",
	        e164: "nombor E.164",
	        jwt: "JWT",
	        template_literal: "input",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Input tidak sah: dijangka ${issue.expected}, diterima ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Input tidak sah: dijangka ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Pilihan tidak sah: dijangka salah satu daripada ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Terlalu besar: dijangka ${issue.origin ?? "nilai"} ${sizing.verb} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elemen"}`;
	                return `Terlalu besar: dijangka ${issue.origin ?? "nilai"} adalah ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Terlalu kecil: dijangka ${issue.origin} ${sizing.verb} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Terlalu kecil: dijangka ${issue.origin} adalah ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `String tidak sah: mesti bermula dengan "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `String tidak sah: mesti berakhir dengan "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `String tidak sah: mesti mengandungi "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `String tidak sah: mesti sepadan dengan corak ${_issue.pattern}`;
	                return `${Nouns[_issue.format] ?? issue.format} tidak sah`;
	            }
	            case "not_multiple_of":
	                return `Nombor tidak sah: perlu gandaan ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Kunci tidak dikenali: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Kunci tidak sah dalam ${issue.origin}`;
	            case "invalid_union":
	                return "Input tidak sah";
	            case "invalid_element":
	                return `Nilai tidak sah dalam ${issue.origin}`;
	            default:
	                return `Input tidak sah`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ms, msExports));

var nlExports = {};
var nl = {
  get exports(){ return nlExports; },
  set exports(v){ nlExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "tekens" },
	        file: { unit: "bytes" },
	        array: { unit: "elementen" },
	        set: { unit: "elementen" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "getal";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "invoer",
	        email: "emailadres",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO datum en tijd",
	        date: "ISO datum",
	        time: "ISO tijd",
	        duration: "ISO duur",
	        ipv4: "IPv4-adres",
	        ipv6: "IPv6-adres",
	        cidrv4: "IPv4-bereik",
	        cidrv6: "IPv6-bereik",
	        base64: "base64-gecodeerde tekst",
	        base64url: "base64 URL-gecodeerde tekst",
	        json_string: "JSON string",
	        e164: "E.164-nummer",
	        jwt: "JWT",
	        template_literal: "invoer",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Ongeldige invoer: verwacht ${issue.expected}, ontving ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Ongeldige invoer: verwacht ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Ongeldige optie: verwacht één van ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Te lang: verwacht dat ${issue.origin ?? "waarde"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementen"} bevat`;
	                return `Te lang: verwacht dat ${issue.origin ?? "waarde"} ${adj}${issue.maximum.toString()} is`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Te kort: verwacht dat ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit} bevat`;
	                }
	                return `Te kort: verwacht dat ${issue.origin} ${adj}${issue.minimum.toString()} is`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `Ongeldige tekst: moet met "${_issue.prefix}" beginnen`;
	                }
	                if (_issue.format === "ends_with")
	                    return `Ongeldige tekst: moet op "${_issue.suffix}" eindigen`;
	                if (_issue.format === "includes")
	                    return `Ongeldige tekst: moet "${_issue.includes}" bevatten`;
	                if (_issue.format === "regex")
	                    return `Ongeldige tekst: moet overeenkomen met patroon ${_issue.pattern}`;
	                return `Ongeldig: ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Ongeldig getal: moet een veelvoud van ${issue.divisor} zijn`;
	            case "unrecognized_keys":
	                return `Onbekende key${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Ongeldige key in ${issue.origin}`;
	            case "invalid_union":
	                return "Ongeldige invoer";
	            case "invalid_element":
	                return `Ongeldige waarde in ${issue.origin}`;
	            default:
	                return `Ongeldige invoer`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (nl, nlExports));

var noExports = {};
var no = {
  get exports(){ return noExports; },
  set exports(v){ noExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "tegn", verb: "å ha" },
	        file: { unit: "bytes", verb: "å ha" },
	        array: { unit: "elementer", verb: "å inneholde" },
	        set: { unit: "elementer", verb: "å inneholde" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "tall";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "liste";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "input",
	        email: "e-postadresse",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO dato- og klokkeslett",
	        date: "ISO-dato",
	        time: "ISO-klokkeslett",
	        duration: "ISO-varighet",
	        ipv4: "IPv4-område",
	        ipv6: "IPv6-område",
	        cidrv4: "IPv4-spekter",
	        cidrv6: "IPv6-spekter",
	        base64: "base64-enkodet streng",
	        base64url: "base64url-enkodet streng",
	        json_string: "JSON-streng",
	        e164: "E.164-nummer",
	        jwt: "JWT",
	        template_literal: "input",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Ugyldig input: forventet ${issue.expected}, fikk ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Ugyldig verdi: forventet ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Ugyldig valg: forventet en av ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `For stor(t): forventet ${issue.origin ?? "value"} til å ha ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementer"}`;
	                return `For stor(t): forventet ${issue.origin ?? "value"} til å ha ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `For lite(n): forventet ${issue.origin} til å ha ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `For lite(n): forventet ${issue.origin} til å ha ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Ugyldig streng: må starte med "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Ugyldig streng: må ende med "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Ugyldig streng: må inneholde "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Ugyldig streng: må matche mønsteret ${_issue.pattern}`;
	                return `Ugyldig ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Ugyldig tall: må være et multiplum av ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `${issue.keys.length > 1 ? "Ukjente nøkler" : "Ukjent nøkkel"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Ugyldig nøkkel i ${issue.origin}`;
	            case "invalid_union":
	                return "Ugyldig input";
	            case "invalid_element":
	                return `Ugyldig verdi i ${issue.origin}`;
	            default:
	                return `Ugyldig input`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (no, noExports));

var otaExports = {};
var ota = {
  get exports(){ return otaExports; },
  set exports(v){ otaExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "harf", verb: "olmalıdır" },
	        file: { unit: "bayt", verb: "olmalıdır" },
	        array: { unit: "unsur", verb: "olmalıdır" },
	        set: { unit: "unsur", verb: "olmalıdır" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "numara";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "saf";
	                }
	                if (data === null) {
	                    return "gayb";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "giren",
	        email: "epostagâh",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO hengâmı",
	        date: "ISO tarihi",
	        time: "ISO zamanı",
	        duration: "ISO müddeti",
	        ipv4: "IPv4 nişânı",
	        ipv6: "IPv6 nişânı",
	        cidrv4: "IPv4 menzili",
	        cidrv6: "IPv6 menzili",
	        base64: "base64-şifreli metin",
	        base64url: "base64url-şifreli metin",
	        json_string: "JSON metin",
	        e164: "E.164 sayısı",
	        jwt: "JWT",
	        template_literal: "giren",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Fâsit giren: umulan ${issue.expected}, alınan ${parsedType(issue.input)}`;
	            // return `Fâsit giren: umulan ${issue.expected}, alınan ${util.getParsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Fâsit giren: umulan ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Fâsit tercih: mûteberler ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Fazla büyük: ${issue.origin ?? "value"}, ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elements"} sahip olmalıydı.`;
	                return `Fazla büyük: ${issue.origin ?? "value"}, ${adj}${issue.maximum.toString()} olmalıydı.`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Fazla küçük: ${issue.origin}, ${adj}${issue.minimum.toString()} ${sizing.unit} sahip olmalıydı.`;
	                }
	                return `Fazla küçük: ${issue.origin}, ${adj}${issue.minimum.toString()} olmalıydı.`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Fâsit metin: "${_issue.prefix}" ile başlamalı.`;
	                if (_issue.format === "ends_with")
	                    return `Fâsit metin: "${_issue.suffix}" ile bitmeli.`;
	                if (_issue.format === "includes")
	                    return `Fâsit metin: "${_issue.includes}" ihtivâ etmeli.`;
	                if (_issue.format === "regex")
	                    return `Fâsit metin: ${_issue.pattern} nakşına uymalı.`;
	                return `Fâsit ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Fâsit sayı: ${issue.divisor} katı olmalıydı.`;
	            case "unrecognized_keys":
	                return `Tanınmayan anahtar ${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `${issue.origin} için tanınmayan anahtar var.`;
	            case "invalid_union":
	                return "Giren tanınamadı.";
	            case "invalid_element":
	                return `${issue.origin} için tanınmayan kıymet var.`;
	            default:
	                return `Kıymet tanınamadı.`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ota, otaExports));

var psExports = {};
var ps = {
  get exports(){ return psExports; },
  set exports(v){ psExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "توکي", verb: "ولري" },
	        file: { unit: "بایټس", verb: "ولري" },
	        array: { unit: "توکي", verb: "ولري" },
	        set: { unit: "توکي", verb: "ولري" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "عدد";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "ارې";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "ورودي",
	        email: "بریښنالیک",
	        url: "یو آر ال",
	        emoji: "ایموجي",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "نیټه او وخت",
	        date: "نېټه",
	        time: "وخت",
	        duration: "موده",
	        ipv4: "د IPv4 پته",
	        ipv6: "د IPv6 پته",
	        cidrv4: "د IPv4 ساحه",
	        cidrv6: "د IPv6 ساحه",
	        base64: "base64-encoded متن",
	        base64url: "base64url-encoded متن",
	        json_string: "JSON متن",
	        e164: "د E.164 شمېره",
	        jwt: "JWT",
	        template_literal: "ورودي",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `ناسم ورودي: باید ${issue.expected} وای, مګر ${parsedType(issue.input)} ترلاسه شو`;
	            case "invalid_value":
	                if (issue.values.length === 1) {
	                    return `ناسم ورودي: باید ${util.stringifyPrimitive(issue.values[0])} وای`;
	                }
	                return `ناسم انتخاب: باید یو له ${util.joinValues(issue.values, "|")} څخه وای`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `ډیر لوی: ${issue.origin ?? "ارزښت"} باید ${adj}${issue.maximum.toString()} ${sizing.unit ?? "عنصرونه"} ولري`;
	                }
	                return `ډیر لوی: ${issue.origin ?? "ارزښت"} باید ${adj}${issue.maximum.toString()} وي`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `ډیر کوچنی: ${issue.origin} باید ${adj}${issue.minimum.toString()} ${sizing.unit} ولري`;
	                }
	                return `ډیر کوچنی: ${issue.origin} باید ${adj}${issue.minimum.toString()} وي`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `ناسم متن: باید د "${_issue.prefix}" سره پیل شي`;
	                }
	                if (_issue.format === "ends_with") {
	                    return `ناسم متن: باید د "${_issue.suffix}" سره پای ته ورسيږي`;
	                }
	                if (_issue.format === "includes") {
	                    return `ناسم متن: باید "${_issue.includes}" ولري`;
	                }
	                if (_issue.format === "regex") {
	                    return `ناسم متن: باید د ${_issue.pattern} سره مطابقت ولري`;
	                }
	                return `${Nouns[_issue.format] ?? issue.format} ناسم دی`;
	            }
	            case "not_multiple_of":
	                return `ناسم عدد: باید د ${issue.divisor} مضرب وي`;
	            case "unrecognized_keys":
	                return `ناسم ${issue.keys.length > 1 ? "کلیډونه" : "کلیډ"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `ناسم کلیډ په ${issue.origin} کې`;
	            case "invalid_union":
	                return `ناسمه ورودي`;
	            case "invalid_element":
	                return `ناسم عنصر په ${issue.origin} کې`;
	            default:
	                return `ناسمه ورودي`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ps, psExports));

var plExports = {};
var pl = {
  get exports(){ return plExports; },
  set exports(v){ plExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "znaków", verb: "mieć" },
	        file: { unit: "bajtów", verb: "mieć" },
	        array: { unit: "elementów", verb: "mieć" },
	        set: { unit: "elementów", verb: "mieć" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "liczba";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "tablica";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "wyrażenie",
	        email: "adres email",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "data i godzina w formacie ISO",
	        date: "data w formacie ISO",
	        time: "godzina w formacie ISO",
	        duration: "czas trwania ISO",
	        ipv4: "adres IPv4",
	        ipv6: "adres IPv6",
	        cidrv4: "zakres IPv4",
	        cidrv6: "zakres IPv6",
	        base64: "ciąg znaków zakodowany w formacie base64",
	        base64url: "ciąg znaków zakodowany w formacie base64url",
	        json_string: "ciąg znaków w formacie JSON",
	        e164: "liczba E.164",
	        jwt: "JWT",
	        template_literal: "wejście",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Nieprawidłowe dane wejściowe: oczekiwano ${issue.expected}, otrzymano ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Nieprawidłowe dane wejściowe: oczekiwano ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Nieprawidłowa opcja: oczekiwano jednej z wartości ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Za duża wartość: oczekiwano, że ${issue.origin ?? "wartość"} będzie mieć ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementów"}`;
	                }
	                return `Zbyt duż(y/a/e): oczekiwano, że ${issue.origin ?? "wartość"} będzie wynosić ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Za mała wartość: oczekiwano, że ${issue.origin ?? "wartość"} będzie mieć ${adj}${issue.minimum.toString()} ${sizing.unit ?? "elementów"}`;
	                }
	                return `Zbyt mał(y/a/e): oczekiwano, że ${issue.origin ?? "wartość"} będzie wynosić ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Nieprawidłowy ciąg znaków: musi zaczynać się od "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Nieprawidłowy ciąg znaków: musi kończyć się na "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Nieprawidłowy ciąg znaków: musi zawierać "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Nieprawidłowy ciąg znaków: musi odpowiadać wzorcowi ${_issue.pattern}`;
	                return `Nieprawidłow(y/a/e) ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Nieprawidłowa liczba: musi być wielokrotnością ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Nierozpoznane klucze${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Nieprawidłowy klucz w ${issue.origin}`;
	            case "invalid_union":
	                return "Nieprawidłowe dane wejściowe";
	            case "invalid_element":
	                return `Nieprawidłowa wartość w ${issue.origin}`;
	            default:
	                return `Nieprawidłowe dane wejściowe`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (pl, plExports));

var ptExports = {};
var pt = {
  get exports(){ return ptExports; },
  set exports(v){ ptExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "caracteres", verb: "ter" },
	        file: { unit: "bytes", verb: "ter" },
	        array: { unit: "itens", verb: "ter" },
	        set: { unit: "itens", verb: "ter" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "número";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "nulo";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "padrão",
	        email: "endereço de e-mail",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "data e hora ISO",
	        date: "data ISO",
	        time: "hora ISO",
	        duration: "duração ISO",
	        ipv4: "endereço IPv4",
	        ipv6: "endereço IPv6",
	        cidrv4: "faixa de IPv4",
	        cidrv6: "faixa de IPv6",
	        base64: "texto codificado em base64",
	        base64url: "URL codificada em base64",
	        json_string: "texto JSON",
	        e164: "número E.164",
	        jwt: "JWT",
	        template_literal: "entrada",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Tipo inválido: esperado ${issue.expected}, recebido ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Entrada inválida: esperado ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Opção inválida: esperada uma das ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Muito grande: esperado que ${issue.origin ?? "valor"} tivesse ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementos"}`;
	                return `Muito grande: esperado que ${issue.origin ?? "valor"} fosse ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Muito pequeno: esperado que ${issue.origin} tivesse ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Muito pequeno: esperado que ${issue.origin} fosse ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Texto inválido: deve começar com "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Texto inválido: deve terminar com "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Texto inválido: deve incluir "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Texto inválido: deve corresponder ao padrão ${_issue.pattern}`;
	                return `${Nouns[_issue.format] ?? issue.format} inválido`;
	            }
	            case "not_multiple_of":
	                return `Número inválido: deve ser múltiplo de ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Chave${issue.keys.length > 1 ? "s" : ""} desconhecida${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Chave inválida em ${issue.origin}`;
	            case "invalid_union":
	                return "Entrada inválida";
	            case "invalid_element":
	                return `Valor inválido em ${issue.origin}`;
	            default:
	                return `Campo inválido`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (pt, ptExports));

var ruExports = {};
var ru = {
  get exports(){ return ruExports; },
  set exports(v){ ruExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	function getRussianPlural(count, one, few, many) {
	    const absCount = Math.abs(count);
	    const lastDigit = absCount % 10;
	    const lastTwoDigits = absCount % 100;
	    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
	        return many;
	    }
	    if (lastDigit === 1) {
	        return one;
	    }
	    if (lastDigit >= 2 && lastDigit <= 4) {
	        return few;
	    }
	    return many;
	}
	const error = () => {
	    const Sizable = {
	        string: {
	            unit: {
	                one: "символ",
	                few: "символа",
	                many: "символов",
	            },
	            verb: "иметь",
	        },
	        file: {
	            unit: {
	                one: "байт",
	                few: "байта",
	                many: "байт",
	            },
	            verb: "иметь",
	        },
	        array: {
	            unit: {
	                one: "элемент",
	                few: "элемента",
	                many: "элементов",
	            },
	            verb: "иметь",
	        },
	        set: {
	            unit: {
	                one: "элемент",
	                few: "элемента",
	                many: "элементов",
	            },
	            verb: "иметь",
	        },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "число";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "массив";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "ввод",
	        email: "email адрес",
	        url: "URL",
	        emoji: "эмодзи",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO дата и время",
	        date: "ISO дата",
	        time: "ISO время",
	        duration: "ISO длительность",
	        ipv4: "IPv4 адрес",
	        ipv6: "IPv6 адрес",
	        cidrv4: "IPv4 диапазон",
	        cidrv6: "IPv6 диапазон",
	        base64: "строка в формате base64",
	        base64url: "строка в формате base64url",
	        json_string: "JSON строка",
	        e164: "номер E.164",
	        jwt: "JWT",
	        template_literal: "ввод",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Неверный ввод: ожидалось ${issue.expected}, получено ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Неверный ввод: ожидалось ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Неверный вариант: ожидалось одно из ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    const maxValue = Number(issue.maximum);
	                    const unit = getRussianPlural(maxValue, sizing.unit.one, sizing.unit.few, sizing.unit.many);
	                    return `Слишком большое значение: ожидалось, что ${issue.origin ?? "значение"} будет иметь ${adj}${issue.maximum.toString()} ${unit}`;
	                }
	                return `Слишком большое значение: ожидалось, что ${issue.origin ?? "значение"} будет ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    const minValue = Number(issue.minimum);
	                    const unit = getRussianPlural(minValue, sizing.unit.one, sizing.unit.few, sizing.unit.many);
	                    return `Слишком маленькое значение: ожидалось, что ${issue.origin} будет иметь ${adj}${issue.minimum.toString()} ${unit}`;
	                }
	                return `Слишком маленькое значение: ожидалось, что ${issue.origin} будет ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Неверная строка: должна начинаться с "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Неверная строка: должна заканчиваться на "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Неверная строка: должна содержать "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Неверная строка: должна соответствовать шаблону ${_issue.pattern}`;
	                return `Неверный ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Неверное число: должно быть кратным ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Нераспознанн${issue.keys.length > 1 ? "ые" : "ый"} ключ${issue.keys.length > 1 ? "и" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Неверный ключ в ${issue.origin}`;
	            case "invalid_union":
	                return "Неверные входные данные";
	            case "invalid_element":
	                return `Неверное значение в ${issue.origin}`;
	            default:
	                return `Неверные входные данные`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ru, ruExports));

var slExports = {};
var sl = {
  get exports(){ return slExports; },
  set exports(v){ slExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "znakov", verb: "imeti" },
	        file: { unit: "bajtov", verb: "imeti" },
	        array: { unit: "elementov", verb: "imeti" },
	        set: { unit: "elementov", verb: "imeti" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "število";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "tabela";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "vnos",
	        email: "e-poštni naslov",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO datum in čas",
	        date: "ISO datum",
	        time: "ISO čas",
	        duration: "ISO trajanje",
	        ipv4: "IPv4 naslov",
	        ipv6: "IPv6 naslov",
	        cidrv4: "obseg IPv4",
	        cidrv6: "obseg IPv6",
	        base64: "base64 kodiran niz",
	        base64url: "base64url kodiran niz",
	        json_string: "JSON niz",
	        e164: "E.164 številka",
	        jwt: "JWT",
	        template_literal: "vnos",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Neveljaven vnos: pričakovano ${issue.expected}, prejeto ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Neveljaven vnos: pričakovano ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Neveljavna možnost: pričakovano eno izmed ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Preveliko: pričakovano, da bo ${issue.origin ?? "vrednost"} imelo ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementov"}`;
	                return `Preveliko: pričakovano, da bo ${issue.origin ?? "vrednost"} ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Premajhno: pričakovano, da bo ${issue.origin} imelo ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Premajhno: pričakovano, da bo ${issue.origin} ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `Neveljaven niz: mora se začeti z "${_issue.prefix}"`;
	                }
	                if (_issue.format === "ends_with")
	                    return `Neveljaven niz: mora se končati z "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Neveljaven niz: mora vsebovati "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Neveljaven niz: mora ustrezati vzorcu ${_issue.pattern}`;
	                return `Neveljaven ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Neveljavno število: mora biti večkratnik ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Neprepoznan${issue.keys.length > 1 ? "i ključi" : " ključ"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Neveljaven ključ v ${issue.origin}`;
	            case "invalid_union":
	                return "Neveljaven vnos";
	            case "invalid_element":
	                return `Neveljavna vrednost v ${issue.origin}`;
	            default:
	                return "Neveljaven vnos";
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (sl, slExports));

var svExports = {};
var sv = {
  get exports(){ return svExports; },
  set exports(v){ svExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "tecken", verb: "att ha" },
	        file: { unit: "bytes", verb: "att ha" },
	        array: { unit: "objekt", verb: "att innehålla" },
	        set: { unit: "objekt", verb: "att innehålla" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "antal";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "lista";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "reguljärt uttryck",
	        email: "e-postadress",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO-datum och tid",
	        date: "ISO-datum",
	        time: "ISO-tid",
	        duration: "ISO-varaktighet",
	        ipv4: "IPv4-intervall",
	        ipv6: "IPv6-intervall",
	        cidrv4: "IPv4-spektrum",
	        cidrv6: "IPv6-spektrum",
	        base64: "base64-kodad sträng",
	        base64url: "base64url-kodad sträng",
	        json_string: "JSON-sträng",
	        e164: "E.164-nummer",
	        jwt: "JWT",
	        template_literal: "mall-literal",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Ogiltig inmatning: förväntat ${issue.expected}, fick ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Ogiltig inmatning: förväntat ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Ogiltigt val: förväntade en av ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `För stor(t): förväntade ${issue.origin ?? "värdet"} att ha ${adj}${issue.maximum.toString()} ${sizing.unit ?? "element"}`;
	                }
	                return `För stor(t): förväntat ${issue.origin ?? "värdet"} att ha ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `För lite(t): förväntade ${issue.origin ?? "värdet"} att ha ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `För lite(t): förväntade ${issue.origin ?? "värdet"} att ha ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `Ogiltig sträng: måste börja med "${_issue.prefix}"`;
	                }
	                if (_issue.format === "ends_with")
	                    return `Ogiltig sträng: måste sluta med "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Ogiltig sträng: måste innehålla "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Ogiltig sträng: måste matcha mönstret "${_issue.pattern}"`;
	                return `Ogiltig(t) ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Ogiltigt tal: måste vara en multipel av ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `${issue.keys.length > 1 ? "Okända nycklar" : "Okänd nyckel"}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Ogiltig nyckel i ${issue.origin ?? "värdet"}`;
	            case "invalid_union":
	                return "Ogiltig input";
	            case "invalid_element":
	                return `Ogiltigt värde i ${issue.origin ?? "värdet"}`;
	            default:
	                return `Ogiltig input`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (sv, svExports));

var taExports = {};
var ta = {
  get exports(){ return taExports; },
  set exports(v){ taExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "எழுத்துக்கள்", verb: "கொண்டிருக்க வேண்டும்" },
	        file: { unit: "பைட்டுகள்", verb: "கொண்டிருக்க வேண்டும்" },
	        array: { unit: "உறுப்புகள்", verb: "கொண்டிருக்க வேண்டும்" },
	        set: { unit: "உறுப்புகள்", verb: "கொண்டிருக்க வேண்டும்" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "எண் அல்லாதது" : "எண்";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "அணி";
	                }
	                if (data === null) {
	                    return "வெறுமை";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "உள்ளீடு",
	        email: "மின்னஞ்சல் முகவரி",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO தேதி நேரம்",
	        date: "ISO தேதி",
	        time: "ISO நேரம்",
	        duration: "ISO கால அளவு",
	        ipv4: "IPv4 முகவரி",
	        ipv6: "IPv6 முகவரி",
	        cidrv4: "IPv4 வரம்பு",
	        cidrv6: "IPv6 வரம்பு",
	        base64: "base64-encoded சரம்",
	        base64url: "base64url-encoded சரம்",
	        json_string: "JSON சரம்",
	        e164: "E.164 எண்",
	        jwt: "JWT",
	        template_literal: "input",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `தவறான உள்ளீடு: எதிர்பார்க்கப்பட்டது ${issue.expected}, பெறப்பட்டது ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `தவறான உள்ளீடு: எதிர்பார்க்கப்பட்டது ${util.stringifyPrimitive(issue.values[0])}`;
	                return `தவறான விருப்பம்: எதிர்பார்க்கப்பட்டது ${util.joinValues(issue.values, "|")} இல் ஒன்று`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `மிக பெரியது: எதிர்பார்க்கப்பட்டது ${issue.origin ?? "மதிப்பு"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "உறுப்புகள்"} ஆக இருக்க வேண்டும்`;
	                }
	                return `மிக பெரியது: எதிர்பார்க்கப்பட்டது ${issue.origin ?? "மதிப்பு"} ${adj}${issue.maximum.toString()} ஆக இருக்க வேண்டும்`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `மிகச் சிறியது: எதிர்பார்க்கப்பட்டது ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit} ஆக இருக்க வேண்டும்`; //
	                }
	                return `மிகச் சிறியது: எதிர்பார்க்கப்பட்டது ${issue.origin} ${adj}${issue.minimum.toString()} ஆக இருக்க வேண்டும்`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `தவறான சரம்: "${_issue.prefix}" இல் தொடங்க வேண்டும்`;
	                if (_issue.format === "ends_with")
	                    return `தவறான சரம்: "${_issue.suffix}" இல் முடிவடைய வேண்டும்`;
	                if (_issue.format === "includes")
	                    return `தவறான சரம்: "${_issue.includes}" ஐ உள்ளடக்க வேண்டும்`;
	                if (_issue.format === "regex")
	                    return `தவறான சரம்: ${_issue.pattern} முறைபாட்டுடன் பொருந்த வேண்டும்`;
	                return `தவறான ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `தவறான எண்: ${issue.divisor} இன் பலமாக இருக்க வேண்டும்`;
	            case "unrecognized_keys":
	                return `அடையாளம் தெரியாத விசை${issue.keys.length > 1 ? "கள்" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `${issue.origin} இல் தவறான விசை`;
	            case "invalid_union":
	                return "தவறான உள்ளீடு";
	            case "invalid_element":
	                return `${issue.origin} இல் தவறான மதிப்பு`;
	            default:
	                return `தவறான உள்ளீடு`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ta, taExports));

var thExports = {};
var th = {
  get exports(){ return thExports; },
  set exports(v){ thExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "ตัวอักษร", verb: "ควรมี" },
	        file: { unit: "ไบต์", verb: "ควรมี" },
	        array: { unit: "รายการ", verb: "ควรมี" },
	        set: { unit: "รายการ", verb: "ควรมี" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "ไม่ใช่ตัวเลข (NaN)" : "ตัวเลข";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "อาร์เรย์ (Array)";
	                }
	                if (data === null) {
	                    return "ไม่มีค่า (null)";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "ข้อมูลที่ป้อน",
	        email: "ที่อยู่อีเมล",
	        url: "URL",
	        emoji: "อิโมจิ",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "วันที่เวลาแบบ ISO",
	        date: "วันที่แบบ ISO",
	        time: "เวลาแบบ ISO",
	        duration: "ช่วงเวลาแบบ ISO",
	        ipv4: "ที่อยู่ IPv4",
	        ipv6: "ที่อยู่ IPv6",
	        cidrv4: "ช่วง IP แบบ IPv4",
	        cidrv6: "ช่วง IP แบบ IPv6",
	        base64: "ข้อความแบบ Base64",
	        base64url: "ข้อความแบบ Base64 สำหรับ URL",
	        json_string: "ข้อความแบบ JSON",
	        e164: "เบอร์โทรศัพท์ระหว่างประเทศ (E.164)",
	        jwt: "โทเคน JWT",
	        template_literal: "ข้อมูลที่ป้อน",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `ประเภทข้อมูลไม่ถูกต้อง: ควรเป็น ${issue.expected} แต่ได้รับ ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `ค่าไม่ถูกต้อง: ควรเป็น ${util.stringifyPrimitive(issue.values[0])}`;
	                return `ตัวเลือกไม่ถูกต้อง: ควรเป็นหนึ่งใน ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "ไม่เกิน" : "น้อยกว่า";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `เกินกำหนด: ${issue.origin ?? "ค่า"} ควรมี${adj} ${issue.maximum.toString()} ${sizing.unit ?? "รายการ"}`;
	                return `เกินกำหนด: ${issue.origin ?? "ค่า"} ควรมี${adj} ${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? "อย่างน้อย" : "มากกว่า";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `น้อยกว่ากำหนด: ${issue.origin} ควรมี${adj} ${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `น้อยกว่ากำหนด: ${issue.origin} ควรมี${adj} ${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `รูปแบบไม่ถูกต้อง: ข้อความต้องขึ้นต้นด้วย "${_issue.prefix}"`;
	                }
	                if (_issue.format === "ends_with")
	                    return `รูปแบบไม่ถูกต้อง: ข้อความต้องลงท้ายด้วย "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `รูปแบบไม่ถูกต้อง: ข้อความต้องมี "${_issue.includes}" อยู่ในข้อความ`;
	                if (_issue.format === "regex")
	                    return `รูปแบบไม่ถูกต้อง: ต้องตรงกับรูปแบบที่กำหนด ${_issue.pattern}`;
	                return `รูปแบบไม่ถูกต้อง: ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `ตัวเลขไม่ถูกต้อง: ต้องเป็นจำนวนที่หารด้วย ${issue.divisor} ได้ลงตัว`;
	            case "unrecognized_keys":
	                return `พบคีย์ที่ไม่รู้จัก: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `คีย์ไม่ถูกต้องใน ${issue.origin}`;
	            case "invalid_union":
	                return "ข้อมูลไม่ถูกต้อง: ไม่ตรงกับรูปแบบยูเนียนที่กำหนดไว้";
	            case "invalid_element":
	                return `ข้อมูลไม่ถูกต้องใน ${issue.origin}`;
	            default:
	                return `ข้อมูลไม่ถูกต้อง`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (th, thExports));

var tr$2 = {};

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.parsedType = void 0;
	exports.default = default_1;
	const util = __importStar(util$3);
	const parsedType = (data) => {
	    const t = typeof data;
	    switch (t) {
	        case "number": {
	            return Number.isNaN(data) ? "NaN" : "number";
	        }
	        case "object": {
	            if (Array.isArray(data)) {
	                return "array";
	            }
	            if (data === null) {
	                return "null";
	            }
	            if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                return data.constructor.name;
	            }
	        }
	    }
	    return t;
	};
	exports.parsedType = parsedType;
	const error = () => {
	    const Sizable = {
	        string: { unit: "karakter", verb: "olmalı" },
	        file: { unit: "bayt", verb: "olmalı" },
	        array: { unit: "öğe", verb: "olmalı" },
	        set: { unit: "öğe", verb: "olmalı" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const Nouns = {
	        regex: "girdi",
	        email: "e-posta adresi",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO tarih ve saat",
	        date: "ISO tarih",
	        time: "ISO saat",
	        duration: "ISO süre",
	        ipv4: "IPv4 adresi",
	        ipv6: "IPv6 adresi",
	        cidrv4: "IPv4 aralığı",
	        cidrv6: "IPv6 aralığı",
	        base64: "base64 ile şifrelenmiş metin",
	        base64url: "base64url ile şifrelenmiş metin",
	        json_string: "JSON dizesi",
	        e164: "E.164 sayısı",
	        jwt: "JWT",
	        template_literal: "Şablon dizesi",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Geçersiz değer: beklenen ${issue.expected}, alınan ${(0, exports.parsedType)(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Geçersiz değer: beklenen ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Geçersiz seçenek: aşağıdakilerden biri olmalı: ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Çok büyük: beklenen ${issue.origin ?? "değer"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "öğe"}`;
	                return `Çok büyük: beklenen ${issue.origin ?? "değer"} ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Çok küçük: beklenen ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                return `Çok küçük: beklenen ${issue.origin} ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Geçersiz metin: "${_issue.prefix}" ile başlamalı`;
	                if (_issue.format === "ends_with")
	                    return `Geçersiz metin: "${_issue.suffix}" ile bitmeli`;
	                if (_issue.format === "includes")
	                    return `Geçersiz metin: "${_issue.includes}" içermeli`;
	                if (_issue.format === "regex")
	                    return `Geçersiz metin: ${_issue.pattern} desenine uymalı`;
	                return `Geçersiz ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Geçersiz sayı: ${issue.divisor} ile tam bölünebilmeli`;
	            case "unrecognized_keys":
	                return `Tanınmayan anahtar${issue.keys.length > 1 ? "lar" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `${issue.origin} içinde geçersiz anahtar`;
	            case "invalid_union":
	                return "Geçersiz değer";
	            case "invalid_element":
	                return `${issue.origin} içinde geçersiz değer`;
	            default:
	                return `Geçersiz değer`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
} (tr$2));

var uaExports = {};
var ua = {
  get exports(){ return uaExports; },
  set exports(v){ uaExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "символів", verb: "матиме" },
	        file: { unit: "байтів", verb: "матиме" },
	        array: { unit: "елементів", verb: "матиме" },
	        set: { unit: "елементів", verb: "матиме" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "число";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "масив";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "вхідні дані",
	        email: "адреса електронної пошти",
	        url: "URL",
	        emoji: "емодзі",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "дата та час ISO",
	        date: "дата ISO",
	        time: "час ISO",
	        duration: "тривалість ISO",
	        ipv4: "адреса IPv4",
	        ipv6: "адреса IPv6",
	        cidrv4: "діапазон IPv4",
	        cidrv6: "діапазон IPv6",
	        base64: "рядок у кодуванні base64",
	        base64url: "рядок у кодуванні base64url",
	        json_string: "рядок JSON",
	        e164: "номер E.164",
	        jwt: "JWT",
	        template_literal: "вхідні дані",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Неправильні вхідні дані: очікується ${issue.expected}, отримано ${parsedType(issue.input)}`;
	            // return `Неправильні вхідні дані: очікується ${issue.expected}, отримано ${util.getParsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Неправильні вхідні дані: очікується ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Неправильна опція: очікується одне з ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Занадто велике: очікується, що ${issue.origin ?? "значення"} ${sizing.verb} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "елементів"}`;
	                return `Занадто велике: очікується, що ${issue.origin ?? "значення"} буде ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Занадто мале: очікується, що ${issue.origin} ${sizing.verb} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Занадто мале: очікується, що ${issue.origin} буде ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Неправильний рядок: повинен починатися з "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Неправильний рядок: повинен закінчуватися на "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Неправильний рядок: повинен містити "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Неправильний рядок: повинен відповідати шаблону ${_issue.pattern}`;
	                return `Неправильний ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Неправильне число: повинно бути кратним ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Нерозпізнаний ключ${issue.keys.length > 1 ? "і" : ""}: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Неправильний ключ у ${issue.origin}`;
	            case "invalid_union":
	                return "Неправильні вхідні дані";
	            case "invalid_element":
	                return `Неправильне значення у ${issue.origin}`;
	            default:
	                return `Неправильні вхідні дані`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ua, uaExports));

var urExports = {};
var ur$2 = {
  get exports(){ return urExports; },
  set exports(v){ urExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "حروف", verb: "ہونا" },
	        file: { unit: "بائٹس", verb: "ہونا" },
	        array: { unit: "آئٹمز", verb: "ہونا" },
	        set: { unit: "آئٹمز", verb: "ہونا" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "نمبر";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "آرے";
	                }
	                if (data === null) {
	                    return "نل";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "ان پٹ",
	        email: "ای میل ایڈریس",
	        url: "یو آر ایل",
	        emoji: "ایموجی",
	        uuid: "یو یو آئی ڈی",
	        uuidv4: "یو یو آئی ڈی وی 4",
	        uuidv6: "یو یو آئی ڈی وی 6",
	        nanoid: "نینو آئی ڈی",
	        guid: "جی یو آئی ڈی",
	        cuid: "سی یو آئی ڈی",
	        cuid2: "سی یو آئی ڈی 2",
	        ulid: "یو ایل آئی ڈی",
	        xid: "ایکس آئی ڈی",
	        ksuid: "کے ایس یو آئی ڈی",
	        datetime: "آئی ایس او ڈیٹ ٹائم",
	        date: "آئی ایس او تاریخ",
	        time: "آئی ایس او وقت",
	        duration: "آئی ایس او مدت",
	        ipv4: "آئی پی وی 4 ایڈریس",
	        ipv6: "آئی پی وی 6 ایڈریس",
	        cidrv4: "آئی پی وی 4 رینج",
	        cidrv6: "آئی پی وی 6 رینج",
	        base64: "بیس 64 ان کوڈڈ سٹرنگ",
	        base64url: "بیس 64 یو آر ایل ان کوڈڈ سٹرنگ",
	        json_string: "جے ایس او این سٹرنگ",
	        e164: "ای 164 نمبر",
	        jwt: "جے ڈبلیو ٹی",
	        template_literal: "ان پٹ",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `غلط ان پٹ: ${issue.expected} متوقع تھا، ${parsedType(issue.input)} موصول ہوا`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `غلط ان پٹ: ${util.stringifyPrimitive(issue.values[0])} متوقع تھا`;
	                return `غلط آپشن: ${util.joinValues(issue.values, "|")} میں سے ایک متوقع تھا`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `بہت بڑا: ${issue.origin ?? "ویلیو"} کے ${adj}${issue.maximum.toString()} ${sizing.unit ?? "عناصر"} ہونے متوقع تھے`;
	                return `بہت بڑا: ${issue.origin ?? "ویلیو"} کا ${adj}${issue.maximum.toString()} ہونا متوقع تھا`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `بہت چھوٹا: ${issue.origin} کے ${adj}${issue.minimum.toString()} ${sizing.unit} ہونے متوقع تھے`;
	                }
	                return `بہت چھوٹا: ${issue.origin} کا ${adj}${issue.minimum.toString()} ہونا متوقع تھا`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `غلط سٹرنگ: "${_issue.prefix}" سے شروع ہونا چاہیے`;
	                }
	                if (_issue.format === "ends_with")
	                    return `غلط سٹرنگ: "${_issue.suffix}" پر ختم ہونا چاہیے`;
	                if (_issue.format === "includes")
	                    return `غلط سٹرنگ: "${_issue.includes}" شامل ہونا چاہیے`;
	                if (_issue.format === "regex")
	                    return `غلط سٹرنگ: پیٹرن ${_issue.pattern} سے میچ ہونا چاہیے`;
	                return `غلط ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `غلط نمبر: ${issue.divisor} کا مضاعف ہونا چاہیے`;
	            case "unrecognized_keys":
	                return `غیر تسلیم شدہ کی${issue.keys.length > 1 ? "ز" : ""}: ${util.joinValues(issue.keys, "، ")}`;
	            case "invalid_key":
	                return `${issue.origin} میں غلط کی`;
	            case "invalid_union":
	                return "غلط ان پٹ";
	            case "invalid_element":
	                return `${issue.origin} میں غلط ویلیو`;
	            default:
	                return `غلط ان پٹ`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (ur$2, urExports));

var viExports = {};
var vi = {
  get exports(){ return viExports; },
  set exports(v){ viExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "ký tự", verb: "có" },
	        file: { unit: "byte", verb: "có" },
	        array: { unit: "phần tử", verb: "có" },
	        set: { unit: "phần tử", verb: "có" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "số";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "mảng";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "đầu vào",
	        email: "địa chỉ email",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ngày giờ ISO",
	        date: "ngày ISO",
	        time: "giờ ISO",
	        duration: "khoảng thời gian ISO",
	        ipv4: "địa chỉ IPv4",
	        ipv6: "địa chỉ IPv6",
	        cidrv4: "dải IPv4",
	        cidrv6: "dải IPv6",
	        base64: "chuỗi mã hóa base64",
	        base64url: "chuỗi mã hóa base64url",
	        json_string: "chuỗi JSON",
	        e164: "số E.164",
	        jwt: "JWT",
	        template_literal: "đầu vào",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Đầu vào không hợp lệ: mong đợi ${issue.expected}, nhận được ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Đầu vào không hợp lệ: mong đợi ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Tùy chọn không hợp lệ: mong đợi một trong các giá trị ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Quá lớn: mong đợi ${issue.origin ?? "giá trị"} ${sizing.verb} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "phần tử"}`;
	                return `Quá lớn: mong đợi ${issue.origin ?? "giá trị"} ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `Quá nhỏ: mong đợi ${issue.origin} ${sizing.verb} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `Quá nhỏ: mong đợi ${issue.origin} ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Chuỗi không hợp lệ: phải bắt đầu bằng "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Chuỗi không hợp lệ: phải kết thúc bằng "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Chuỗi không hợp lệ: phải bao gồm "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Chuỗi không hợp lệ: phải khớp với mẫu ${_issue.pattern}`;
	                return `${Nouns[_issue.format] ?? issue.format} không hợp lệ`;
	            }
	            case "not_multiple_of":
	                return `Số không hợp lệ: phải là bội số của ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Khóa không được nhận dạng: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Khóa không hợp lệ trong ${issue.origin}`;
	            case "invalid_union":
	                return "Đầu vào không hợp lệ";
	            case "invalid_element":
	                return `Giá trị không hợp lệ trong ${issue.origin}`;
	            default:
	                return `Đầu vào không hợp lệ`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (vi, viExports));

var zhCNExports = {};
var zhCN = {
  get exports(){ return zhCNExports; },
  set exports(v){ zhCNExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "字符", verb: "包含" },
	        file: { unit: "字节", verb: "包含" },
	        array: { unit: "项", verb: "包含" },
	        set: { unit: "项", verb: "包含" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "非数字(NaN)" : "数字";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "数组";
	                }
	                if (data === null) {
	                    return "空值(null)";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "输入",
	        email: "电子邮件",
	        url: "URL",
	        emoji: "表情符号",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO日期时间",
	        date: "ISO日期",
	        time: "ISO时间",
	        duration: "ISO时长",
	        ipv4: "IPv4地址",
	        ipv6: "IPv6地址",
	        cidrv4: "IPv4网段",
	        cidrv6: "IPv6网段",
	        base64: "base64编码字符串",
	        base64url: "base64url编码字符串",
	        json_string: "JSON字符串",
	        e164: "E.164号码",
	        jwt: "JWT",
	        template_literal: "输入",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `无效输入：期望 ${issue.expected}，实际接收 ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `无效输入：期望 ${util.stringifyPrimitive(issue.values[0])}`;
	                return `无效选项：期望以下之一 ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `数值过大：期望 ${issue.origin ?? "值"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "个元素"}`;
	                return `数值过大：期望 ${issue.origin ?? "值"} ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `数值过小：期望 ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `数值过小：期望 ${issue.origin} ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `无效字符串：必须以 "${_issue.prefix}" 开头`;
	                if (_issue.format === "ends_with")
	                    return `无效字符串：必须以 "${_issue.suffix}" 结尾`;
	                if (_issue.format === "includes")
	                    return `无效字符串：必须包含 "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `无效字符串：必须满足正则表达式 ${_issue.pattern}`;
	                return `无效${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `无效数字：必须是 ${issue.divisor} 的倍数`;
	            case "unrecognized_keys":
	                return `出现未知的键(key): ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `${issue.origin} 中的键(key)无效`;
	            case "invalid_union":
	                return "无效输入";
	            case "invalid_element":
	                return `${issue.origin} 中包含无效值(value)`;
	            default:
	                return `无效输入`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (zhCN, zhCNExports));

var zhTWExports = {};
var zhTW = {
  get exports(){ return zhTWExports; },
  set exports(v){ zhTWExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "字元", verb: "擁有" },
	        file: { unit: "位元組", verb: "擁有" },
	        array: { unit: "項目", verb: "擁有" },
	        set: { unit: "項目", verb: "擁有" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "number";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "array";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "輸入",
	        email: "郵件地址",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "ISO 日期時間",
	        date: "ISO 日期",
	        time: "ISO 時間",
	        duration: "ISO 期間",
	        ipv4: "IPv4 位址",
	        ipv6: "IPv6 位址",
	        cidrv4: "IPv4 範圍",
	        cidrv6: "IPv6 範圍",
	        base64: "base64 編碼字串",
	        base64url: "base64url 編碼字串",
	        json_string: "JSON 字串",
	        e164: "E.164 數值",
	        jwt: "JWT",
	        template_literal: "輸入",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `無效的輸入值：預期為 ${issue.expected}，但收到 ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `無效的輸入值：預期為 ${util.stringifyPrimitive(issue.values[0])}`;
	                return `無效的選項：預期為以下其中之一 ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `數值過大：預期 ${issue.origin ?? "值"} 應為 ${adj}${issue.maximum.toString()} ${sizing.unit ?? "個元素"}`;
	                return `數值過大：預期 ${issue.origin ?? "值"} 應為 ${adj}${issue.maximum.toString()}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing) {
	                    return `數值過小：預期 ${issue.origin} 應為 ${adj}${issue.minimum.toString()} ${sizing.unit}`;
	                }
	                return `數值過小：預期 ${issue.origin} 應為 ${adj}${issue.minimum.toString()}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with") {
	                    return `無效的字串：必須以 "${_issue.prefix}" 開頭`;
	                }
	                if (_issue.format === "ends_with")
	                    return `無效的字串：必須以 "${_issue.suffix}" 結尾`;
	                if (_issue.format === "includes")
	                    return `無效的字串：必須包含 "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `無效的字串：必須符合格式 ${_issue.pattern}`;
	                return `無效的 ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `無效的數字：必須為 ${issue.divisor} 的倍數`;
	            case "unrecognized_keys":
	                return `無法識別的鍵值${issue.keys.length > 1 ? "們" : ""}：${util.joinValues(issue.keys, "、")}`;
	            case "invalid_key":
	                return `${issue.origin} 中有無效的鍵值`;
	            case "invalid_union":
	                return "無效的輸入值";
	            case "invalid_element":
	                return `${issue.origin} 中有無效的值`;
	            default:
	                return `無效的輸入值`;
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (zhTW, zhTWExports));

var yoExports = {};
var yo = {
  get exports(){ return yoExports; },
  set exports(v){ yoExports = v; },
};

(function (module, exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = default_1;
	const util = __importStar(util$3);
	const error = () => {
	    const Sizable = {
	        string: { unit: "àmi", verb: "ní" },
	        file: { unit: "bytes", verb: "ní" },
	        array: { unit: "nkan", verb: "ní" },
	        set: { unit: "nkan", verb: "ní" },
	    };
	    function getSizing(origin) {
	        return Sizable[origin] ?? null;
	    }
	    const parsedType = (data) => {
	        const t = typeof data;
	        switch (t) {
	            case "number": {
	                return Number.isNaN(data) ? "NaN" : "nọ́mbà";
	            }
	            case "object": {
	                if (Array.isArray(data)) {
	                    return "akopọ";
	                }
	                if (data === null) {
	                    return "null";
	                }
	                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
	                    return data.constructor.name;
	                }
	            }
	        }
	        return t;
	    };
	    const Nouns = {
	        regex: "ẹ̀rọ ìbáwọlé",
	        email: "àdírẹ́sì ìmẹ́lì",
	        url: "URL",
	        emoji: "emoji",
	        uuid: "UUID",
	        uuidv4: "UUIDv4",
	        uuidv6: "UUIDv6",
	        nanoid: "nanoid",
	        guid: "GUID",
	        cuid: "cuid",
	        cuid2: "cuid2",
	        ulid: "ULID",
	        xid: "XID",
	        ksuid: "KSUID",
	        datetime: "àkókò ISO",
	        date: "ọjọ́ ISO",
	        time: "àkókò ISO",
	        duration: "àkókò tó pé ISO",
	        ipv4: "àdírẹ́sì IPv4",
	        ipv6: "àdírẹ́sì IPv6",
	        cidrv4: "àgbègbè IPv4",
	        cidrv6: "àgbègbè IPv6",
	        base64: "ọ̀rọ̀ tí a kọ́ ní base64",
	        base64url: "ọ̀rọ̀ base64url",
	        json_string: "ọ̀rọ̀ JSON",
	        e164: "nọ́mbà E.164",
	        jwt: "JWT",
	        template_literal: "ẹ̀rọ ìbáwọlé",
	    };
	    return (issue) => {
	        switch (issue.code) {
	            case "invalid_type":
	                return `Ìbáwọlé aṣìṣe: a ní láti fi ${issue.expected}, àmọ̀ a rí ${parsedType(issue.input)}`;
	            case "invalid_value":
	                if (issue.values.length === 1)
	                    return `Ìbáwọlé aṣìṣe: a ní láti fi ${util.stringifyPrimitive(issue.values[0])}`;
	                return `Àṣàyàn aṣìṣe: yan ọ̀kan lára ${util.joinValues(issue.values, "|")}`;
	            case "too_big": {
	                const adj = issue.inclusive ? "<=" : "<";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Tó pọ̀ jù: a ní láti jẹ́ pé ${issue.origin ?? "iye"} ${sizing.verb} ${adj}${issue.maximum} ${sizing.unit}`;
	                return `Tó pọ̀ jù: a ní láti jẹ́ ${adj}${issue.maximum}`;
	            }
	            case "too_small": {
	                const adj = issue.inclusive ? ">=" : ">";
	                const sizing = getSizing(issue.origin);
	                if (sizing)
	                    return `Kéré ju: a ní láti jẹ́ pé ${issue.origin} ${sizing.verb} ${adj}${issue.minimum} ${sizing.unit}`;
	                return `Kéré ju: a ní láti jẹ́ ${adj}${issue.minimum}`;
	            }
	            case "invalid_format": {
	                const _issue = issue;
	                if (_issue.format === "starts_with")
	                    return `Ọ̀rọ̀ aṣìṣe: gbọ́dọ̀ bẹ̀rẹ̀ pẹ̀lú "${_issue.prefix}"`;
	                if (_issue.format === "ends_with")
	                    return `Ọ̀rọ̀ aṣìṣe: gbọ́dọ̀ parí pẹ̀lú "${_issue.suffix}"`;
	                if (_issue.format === "includes")
	                    return `Ọ̀rọ̀ aṣìṣe: gbọ́dọ̀ ní "${_issue.includes}"`;
	                if (_issue.format === "regex")
	                    return `Ọ̀rọ̀ aṣìṣe: gbọ́dọ̀ bá àpẹẹrẹ mu ${_issue.pattern}`;
	                return `Aṣìṣe: ${Nouns[_issue.format] ?? issue.format}`;
	            }
	            case "not_multiple_of":
	                return `Nọ́mbà aṣìṣe: gbọ́dọ̀ jẹ́ èyà pípín ti ${issue.divisor}`;
	            case "unrecognized_keys":
	                return `Bọtìnì àìmọ̀: ${util.joinValues(issue.keys, ", ")}`;
	            case "invalid_key":
	                return `Bọtìnì aṣìṣe nínú ${issue.origin}`;
	            case "invalid_union":
	                return "Ìbáwọlé aṣìṣe";
	            case "invalid_element":
	                return `Iye aṣìṣe nínú ${issue.origin}`;
	            default:
	                return "Ìbáwọlé aṣìṣe";
	        }
	    };
	};
	function default_1() {
	    return {
	        localeError: error(),
	    };
	}
	module.exports = exports.default;
} (yo, yoExports));

(function (exports) {
	var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
	    return (mod && mod.__esModule) ? mod : { "default": mod };
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.yo = exports.zhTW = exports.zhCN = exports.vi = exports.ur = exports.ua = exports.tr = exports.th = exports.ta = exports.sv = exports.sl = exports.ru = exports.pt = exports.pl = exports.ps = exports.ota = exports.no = exports.nl = exports.ms = exports.mk = exports.ko = exports.kh = exports.ja = exports.it = exports.is = exports.id = exports.hu = exports.he = exports.frCA = exports.fr = exports.fi = exports.fa = exports.es = exports.eo = exports.en = exports.de = exports.da = exports.cs = exports.ca = exports.be = exports.az = exports.ar = void 0;
	var ar_js_1 = arExports;
	Object.defineProperty(exports, "ar", { enumerable: true, get: function () { return __importDefault(ar_js_1).default; } });
	var az_js_1 = azExports;
	Object.defineProperty(exports, "az", { enumerable: true, get: function () { return __importDefault(az_js_1).default; } });
	var be_js_1 = beExports;
	Object.defineProperty(exports, "be", { enumerable: true, get: function () { return __importDefault(be_js_1).default; } });
	var ca_js_1 = caExports;
	Object.defineProperty(exports, "ca", { enumerable: true, get: function () { return __importDefault(ca_js_1).default; } });
	var cs_js_1 = csExports;
	Object.defineProperty(exports, "cs", { enumerable: true, get: function () { return __importDefault(cs_js_1).default; } });
	var da_js_1 = daExports;
	Object.defineProperty(exports, "da", { enumerable: true, get: function () { return __importDefault(da_js_1).default; } });
	var de_js_1 = deExports;
	Object.defineProperty(exports, "de", { enumerable: true, get: function () { return __importDefault(de_js_1).default; } });
	var en_js_1 = en;
	Object.defineProperty(exports, "en", { enumerable: true, get: function () { return __importDefault(en_js_1).default; } });
	var eo_js_1 = eo;
	Object.defineProperty(exports, "eo", { enumerable: true, get: function () { return __importDefault(eo_js_1).default; } });
	var es_js_1 = esExports;
	Object.defineProperty(exports, "es", { enumerable: true, get: function () { return __importDefault(es_js_1).default; } });
	var fa_js_1 = faExports;
	Object.defineProperty(exports, "fa", { enumerable: true, get: function () { return __importDefault(fa_js_1).default; } });
	var fi_js_1 = fiExports;
	Object.defineProperty(exports, "fi", { enumerable: true, get: function () { return __importDefault(fi_js_1).default; } });
	var fr_js_1 = frExports;
	Object.defineProperty(exports, "fr", { enumerable: true, get: function () { return __importDefault(fr_js_1).default; } });
	var fr_CA_js_1 = frCAExports;
	Object.defineProperty(exports, "frCA", { enumerable: true, get: function () { return __importDefault(fr_CA_js_1).default; } });
	var he_js_1 = heExports;
	Object.defineProperty(exports, "he", { enumerable: true, get: function () { return __importDefault(he_js_1).default; } });
	var hu_js_1 = huExports;
	Object.defineProperty(exports, "hu", { enumerable: true, get: function () { return __importDefault(hu_js_1).default; } });
	var id_js_1 = idExports;
	Object.defineProperty(exports, "id", { enumerable: true, get: function () { return __importDefault(id_js_1).default; } });
	var is_js_1 = is$1;
	Object.defineProperty(exports, "is", { enumerable: true, get: function () { return __importDefault(is_js_1).default; } });
	var it_js_1 = itExports;
	Object.defineProperty(exports, "it", { enumerable: true, get: function () { return __importDefault(it_js_1).default; } });
	var ja_js_1 = jaExports;
	Object.defineProperty(exports, "ja", { enumerable: true, get: function () { return __importDefault(ja_js_1).default; } });
	var kh_js_1 = khExports;
	Object.defineProperty(exports, "kh", { enumerable: true, get: function () { return __importDefault(kh_js_1).default; } });
	var ko_js_1 = koExports;
	Object.defineProperty(exports, "ko", { enumerable: true, get: function () { return __importDefault(ko_js_1).default; } });
	var mk_js_1 = mkExports;
	Object.defineProperty(exports, "mk", { enumerable: true, get: function () { return __importDefault(mk_js_1).default; } });
	var ms_js_1 = msExports;
	Object.defineProperty(exports, "ms", { enumerable: true, get: function () { return __importDefault(ms_js_1).default; } });
	var nl_js_1 = nlExports;
	Object.defineProperty(exports, "nl", { enumerable: true, get: function () { return __importDefault(nl_js_1).default; } });
	var no_js_1 = noExports;
	Object.defineProperty(exports, "no", { enumerable: true, get: function () { return __importDefault(no_js_1).default; } });
	var ota_js_1 = otaExports;
	Object.defineProperty(exports, "ota", { enumerable: true, get: function () { return __importDefault(ota_js_1).default; } });
	var ps_js_1 = psExports;
	Object.defineProperty(exports, "ps", { enumerable: true, get: function () { return __importDefault(ps_js_1).default; } });
	var pl_js_1 = plExports;
	Object.defineProperty(exports, "pl", { enumerable: true, get: function () { return __importDefault(pl_js_1).default; } });
	var pt_js_1 = ptExports;
	Object.defineProperty(exports, "pt", { enumerable: true, get: function () { return __importDefault(pt_js_1).default; } });
	var ru_js_1 = ruExports;
	Object.defineProperty(exports, "ru", { enumerable: true, get: function () { return __importDefault(ru_js_1).default; } });
	var sl_js_1 = slExports;
	Object.defineProperty(exports, "sl", { enumerable: true, get: function () { return __importDefault(sl_js_1).default; } });
	var sv_js_1 = svExports;
	Object.defineProperty(exports, "sv", { enumerable: true, get: function () { return __importDefault(sv_js_1).default; } });
	var ta_js_1 = taExports;
	Object.defineProperty(exports, "ta", { enumerable: true, get: function () { return __importDefault(ta_js_1).default; } });
	var th_js_1 = thExports;
	Object.defineProperty(exports, "th", { enumerable: true, get: function () { return __importDefault(th_js_1).default; } });
	var tr_js_1 = tr$2;
	Object.defineProperty(exports, "tr", { enumerable: true, get: function () { return __importDefault(tr_js_1).default; } });
	var ua_js_1 = uaExports;
	Object.defineProperty(exports, "ua", { enumerable: true, get: function () { return __importDefault(ua_js_1).default; } });
	var ur_js_1 = urExports;
	Object.defineProperty(exports, "ur", { enumerable: true, get: function () { return __importDefault(ur_js_1).default; } });
	var vi_js_1 = viExports;
	Object.defineProperty(exports, "vi", { enumerable: true, get: function () { return __importDefault(vi_js_1).default; } });
	var zh_CN_js_1 = zhCNExports;
	Object.defineProperty(exports, "zhCN", { enumerable: true, get: function () { return __importDefault(zh_CN_js_1).default; } });
	var zh_TW_js_1 = zhTWExports;
	Object.defineProperty(exports, "zhTW", { enumerable: true, get: function () { return __importDefault(zh_TW_js_1).default; } });
	var yo_js_1 = yoExports;
	Object.defineProperty(exports, "yo", { enumerable: true, get: function () { return __importDefault(yo_js_1).default; } });
} (locales));

var registries = {};

Object.defineProperty(registries, "__esModule", { value: true });
registries.globalRegistry = registries.$ZodRegistry = registries.$input = registries.$output = void 0;
registries.registry = registry;
registries.$output = Symbol("ZodOutput");
registries.$input = Symbol("ZodInput");
class $ZodRegistry {
    constructor() {
        this._map = new Map();
        this._idmap = new Map();
    }
    add(schema, ..._meta) {
        const meta = _meta[0];
        this._map.set(schema, meta);
        if (meta && typeof meta === "object" && "id" in meta) {
            if (this._idmap.has(meta.id)) {
                throw new Error(`ID ${meta.id} already exists in the registry`);
            }
            this._idmap.set(meta.id, schema);
        }
        return this;
    }
    clear() {
        this._map = new Map();
        this._idmap = new Map();
        return this;
    }
    remove(schema) {
        const meta = this._map.get(schema);
        if (meta && typeof meta === "object" && "id" in meta) {
            this._idmap.delete(meta.id);
        }
        this._map.delete(schema);
        return this;
    }
    get(schema) {
        // return this._map.get(schema) as any;
        // inherit metadata
        const p = schema._zod.parent;
        if (p) {
            const pm = { ...(this.get(p) ?? {}) };
            delete pm.id; // do not inherit id
            const f = { ...pm, ...this._map.get(schema) };
            return Object.keys(f).length ? f : undefined;
        }
        return this._map.get(schema);
    }
    has(schema) {
        return this._map.has(schema);
    }
}
registries.$ZodRegistry = $ZodRegistry;
// registries
function registry() {
    return new $ZodRegistry();
}
registries.globalRegistry = registry();

var api = {};

var __createBinding$6 = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault$6 = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar$6 = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding$6(result, mod, k);
    __setModuleDefault$6(result, mod);
    return result;
};
Object.defineProperty(api, "__esModule", { value: true });
api.TimePrecision = void 0;
api._string = _string;
api._coercedString = _coercedString;
api._email = _email;
api._guid = _guid;
api._uuid = _uuid;
api._uuidv4 = _uuidv4;
api._uuidv6 = _uuidv6;
api._uuidv7 = _uuidv7;
api._url = _url;
api._emoji = _emoji;
api._nanoid = _nanoid;
api._cuid = _cuid;
api._cuid2 = _cuid2;
api._ulid = _ulid;
api._xid = _xid;
api._ksuid = _ksuid;
api._ipv4 = _ipv4;
api._ipv6 = _ipv6;
api._cidrv4 = _cidrv4;
api._cidrv6 = _cidrv6;
api._base64 = _base64;
api._base64url = _base64url;
api._e164 = _e164;
api._jwt = _jwt;
api._isoDateTime = _isoDateTime;
api._isoDate = _isoDate;
api._isoTime = _isoTime;
api._isoDuration = _isoDuration;
api._number = _number;
api._coercedNumber = _coercedNumber;
api._int = _int;
api._float32 = _float32;
api._float64 = _float64;
api._int32 = _int32;
api._uint32 = _uint32;
api._boolean = _boolean;
api._coercedBoolean = _coercedBoolean;
api._bigint = _bigint;
api._coercedBigint = _coercedBigint;
api._int64 = _int64;
api._uint64 = _uint64;
api._symbol = _symbol;
api._undefined = _undefined;
api._null = _null;
api._any = _any;
api._unknown = _unknown;
api._never = _never;
api._void = _void;
api._date = _date;
api._coercedDate = _coercedDate;
api._nan = _nan;
api._lt = _lt;
api._lte = _lte;
api._max = _lte;
api._lte = _lte;
api._max = _lte;
api._gt = _gt;
api._gte = _gte;
api._min = _gte;
api._gte = _gte;
api._min = _gte;
api._positive = _positive;
api._negative = _negative;
api._nonpositive = _nonpositive;
api._nonnegative = _nonnegative;
api._multipleOf = _multipleOf;
api._maxSize = _maxSize;
api._minSize = _minSize;
api._size = _size;
api._maxLength = _maxLength;
api._minLength = _minLength;
api._length = _length;
api._regex = _regex;
api._lowercase = _lowercase;
api._uppercase = _uppercase;
api._includes = _includes;
api._startsWith = _startsWith;
api._endsWith = _endsWith;
api._property = _property;
api._mime = _mime;
api._overwrite = _overwrite;
api._normalize = _normalize;
api._trim = _trim;
api._toLowerCase = _toLowerCase;
api._toUpperCase = _toUpperCase;
api._array = _array;
api._union = _union;
api._discriminatedUnion = _discriminatedUnion;
api._intersection = _intersection;
api._tuple = _tuple;
api._record = _record;
api._map = _map;
api._set = _set;
api._enum = _enum;
api._nativeEnum = _nativeEnum;
api._literal = _literal;
api._file = _file;
api._transform = _transform;
api._optional = _optional;
api._nullable = _nullable;
api._default = _default$2;
api._nonoptional = _nonoptional;
api._success = _success;
api._catch = _catch;
api._pipe = _pipe;
api._readonly = _readonly;
api._templateLiteral = _templateLiteral;
api._lazy = _lazy;
api._promise = _promise;
api._custom = _custom;
api._refine = _refine;
api._superRefine = _superRefine;
api._check = _check;
api._stringbool = _stringbool;
api._stringFormat = _stringFormat;
const checks$1 = __importStar$6(checks$2);
const schemas$2 = __importStar$6(schemas$3);
const util$1 = __importStar$6(util$3);
function _string(Class, params) {
    return new Class({
        type: "string",
        ...util$1.normalizeParams(params),
    });
}
function _coercedString(Class, params) {
    return new Class({
        type: "string",
        coerce: true,
        ...util$1.normalizeParams(params),
    });
}
function _email(Class, params) {
    return new Class({
        type: "string",
        format: "email",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _guid(Class, params) {
    return new Class({
        type: "string",
        format: "guid",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _uuid(Class, params) {
    return new Class({
        type: "string",
        format: "uuid",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _uuidv4(Class, params) {
    return new Class({
        type: "string",
        format: "uuid",
        check: "string_format",
        abort: false,
        version: "v4",
        ...util$1.normalizeParams(params),
    });
}
function _uuidv6(Class, params) {
    return new Class({
        type: "string",
        format: "uuid",
        check: "string_format",
        abort: false,
        version: "v6",
        ...util$1.normalizeParams(params),
    });
}
function _uuidv7(Class, params) {
    return new Class({
        type: "string",
        format: "uuid",
        check: "string_format",
        abort: false,
        version: "v7",
        ...util$1.normalizeParams(params),
    });
}
function _url(Class, params) {
    return new Class({
        type: "string",
        format: "url",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _emoji(Class, params) {
    return new Class({
        type: "string",
        format: "emoji",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _nanoid(Class, params) {
    return new Class({
        type: "string",
        format: "nanoid",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _cuid(Class, params) {
    return new Class({
        type: "string",
        format: "cuid",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _cuid2(Class, params) {
    return new Class({
        type: "string",
        format: "cuid2",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _ulid(Class, params) {
    return new Class({
        type: "string",
        format: "ulid",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _xid(Class, params) {
    return new Class({
        type: "string",
        format: "xid",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _ksuid(Class, params) {
    return new Class({
        type: "string",
        format: "ksuid",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _ipv4(Class, params) {
    return new Class({
        type: "string",
        format: "ipv4",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _ipv6(Class, params) {
    return new Class({
        type: "string",
        format: "ipv6",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _cidrv4(Class, params) {
    return new Class({
        type: "string",
        format: "cidrv4",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _cidrv6(Class, params) {
    return new Class({
        type: "string",
        format: "cidrv6",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _base64(Class, params) {
    return new Class({
        type: "string",
        format: "base64",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _base64url(Class, params) {
    return new Class({
        type: "string",
        format: "base64url",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _e164(Class, params) {
    return new Class({
        type: "string",
        format: "e164",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
function _jwt(Class, params) {
    return new Class({
        type: "string",
        format: "jwt",
        check: "string_format",
        abort: false,
        ...util$1.normalizeParams(params),
    });
}
api.TimePrecision = {
    Any: null,
    Minute: -1,
    Second: 0,
    Millisecond: 3,
    Microsecond: 6,
};
function _isoDateTime(Class, params) {
    return new Class({
        type: "string",
        format: "datetime",
        check: "string_format",
        offset: false,
        local: false,
        precision: null,
        ...util$1.normalizeParams(params),
    });
}
function _isoDate(Class, params) {
    return new Class({
        type: "string",
        format: "date",
        check: "string_format",
        ...util$1.normalizeParams(params),
    });
}
function _isoTime(Class, params) {
    return new Class({
        type: "string",
        format: "time",
        check: "string_format",
        precision: null,
        ...util$1.normalizeParams(params),
    });
}
function _isoDuration(Class, params) {
    return new Class({
        type: "string",
        format: "duration",
        check: "string_format",
        ...util$1.normalizeParams(params),
    });
}
function _number(Class, params) {
    return new Class({
        type: "number",
        checks: [],
        ...util$1.normalizeParams(params),
    });
}
function _coercedNumber(Class, params) {
    return new Class({
        type: "number",
        coerce: true,
        checks: [],
        ...util$1.normalizeParams(params),
    });
}
function _int(Class, params) {
    return new Class({
        type: "number",
        check: "number_format",
        abort: false,
        format: "safeint",
        ...util$1.normalizeParams(params),
    });
}
function _float32(Class, params) {
    return new Class({
        type: "number",
        check: "number_format",
        abort: false,
        format: "float32",
        ...util$1.normalizeParams(params),
    });
}
function _float64(Class, params) {
    return new Class({
        type: "number",
        check: "number_format",
        abort: false,
        format: "float64",
        ...util$1.normalizeParams(params),
    });
}
function _int32(Class, params) {
    return new Class({
        type: "number",
        check: "number_format",
        abort: false,
        format: "int32",
        ...util$1.normalizeParams(params),
    });
}
function _uint32(Class, params) {
    return new Class({
        type: "number",
        check: "number_format",
        abort: false,
        format: "uint32",
        ...util$1.normalizeParams(params),
    });
}
function _boolean(Class, params) {
    return new Class({
        type: "boolean",
        ...util$1.normalizeParams(params),
    });
}
function _coercedBoolean(Class, params) {
    return new Class({
        type: "boolean",
        coerce: true,
        ...util$1.normalizeParams(params),
    });
}
function _bigint(Class, params) {
    return new Class({
        type: "bigint",
        ...util$1.normalizeParams(params),
    });
}
function _coercedBigint(Class, params) {
    return new Class({
        type: "bigint",
        coerce: true,
        ...util$1.normalizeParams(params),
    });
}
function _int64(Class, params) {
    return new Class({
        type: "bigint",
        check: "bigint_format",
        abort: false,
        format: "int64",
        ...util$1.normalizeParams(params),
    });
}
function _uint64(Class, params) {
    return new Class({
        type: "bigint",
        check: "bigint_format",
        abort: false,
        format: "uint64",
        ...util$1.normalizeParams(params),
    });
}
function _symbol(Class, params) {
    return new Class({
        type: "symbol",
        ...util$1.normalizeParams(params),
    });
}
function _undefined(Class, params) {
    return new Class({
        type: "undefined",
        ...util$1.normalizeParams(params),
    });
}
function _null(Class, params) {
    return new Class({
        type: "null",
        ...util$1.normalizeParams(params),
    });
}
function _any(Class) {
    return new Class({
        type: "any",
    });
}
function _unknown(Class) {
    return new Class({
        type: "unknown",
    });
}
function _never(Class, params) {
    return new Class({
        type: "never",
        ...util$1.normalizeParams(params),
    });
}
function _void(Class, params) {
    return new Class({
        type: "void",
        ...util$1.normalizeParams(params),
    });
}
function _date(Class, params) {
    return new Class({
        type: "date",
        ...util$1.normalizeParams(params),
    });
}
function _coercedDate(Class, params) {
    return new Class({
        type: "date",
        coerce: true,
        ...util$1.normalizeParams(params),
    });
}
function _nan(Class, params) {
    return new Class({
        type: "nan",
        ...util$1.normalizeParams(params),
    });
}
function _lt(value, params) {
    return new checks$1.$ZodCheckLessThan({
        check: "less_than",
        ...util$1.normalizeParams(params),
        value,
        inclusive: false,
    });
}
function _lte(value, params) {
    return new checks$1.$ZodCheckLessThan({
        check: "less_than",
        ...util$1.normalizeParams(params),
        value,
        inclusive: true,
    });
}
function _gt(value, params) {
    return new checks$1.$ZodCheckGreaterThan({
        check: "greater_than",
        ...util$1.normalizeParams(params),
        value,
        inclusive: false,
    });
}
function _gte(value, params) {
    return new checks$1.$ZodCheckGreaterThan({
        check: "greater_than",
        ...util$1.normalizeParams(params),
        value,
        inclusive: true,
    });
}
function _positive(params) {
    return _gt(0, params);
}
// negative
function _negative(params) {
    return _lt(0, params);
}
// nonpositive
function _nonpositive(params) {
    return _lte(0, params);
}
// nonnegative
function _nonnegative(params) {
    return _gte(0, params);
}
function _multipleOf(value, params) {
    return new checks$1.$ZodCheckMultipleOf({
        check: "multiple_of",
        ...util$1.normalizeParams(params),
        value,
    });
}
function _maxSize(maximum, params) {
    return new checks$1.$ZodCheckMaxSize({
        check: "max_size",
        ...util$1.normalizeParams(params),
        maximum,
    });
}
function _minSize(minimum, params) {
    return new checks$1.$ZodCheckMinSize({
        check: "min_size",
        ...util$1.normalizeParams(params),
        minimum,
    });
}
function _size(size, params) {
    return new checks$1.$ZodCheckSizeEquals({
        check: "size_equals",
        ...util$1.normalizeParams(params),
        size,
    });
}
function _maxLength(maximum, params) {
    const ch = new checks$1.$ZodCheckMaxLength({
        check: "max_length",
        ...util$1.normalizeParams(params),
        maximum,
    });
    return ch;
}
function _minLength(minimum, params) {
    return new checks$1.$ZodCheckMinLength({
        check: "min_length",
        ...util$1.normalizeParams(params),
        minimum,
    });
}
function _length(length, params) {
    return new checks$1.$ZodCheckLengthEquals({
        check: "length_equals",
        ...util$1.normalizeParams(params),
        length,
    });
}
function _regex(pattern, params) {
    return new checks$1.$ZodCheckRegex({
        check: "string_format",
        format: "regex",
        ...util$1.normalizeParams(params),
        pattern,
    });
}
function _lowercase(params) {
    return new checks$1.$ZodCheckLowerCase({
        check: "string_format",
        format: "lowercase",
        ...util$1.normalizeParams(params),
    });
}
function _uppercase(params) {
    return new checks$1.$ZodCheckUpperCase({
        check: "string_format",
        format: "uppercase",
        ...util$1.normalizeParams(params),
    });
}
function _includes(includes, params) {
    return new checks$1.$ZodCheckIncludes({
        check: "string_format",
        format: "includes",
        ...util$1.normalizeParams(params),
        includes,
    });
}
function _startsWith(prefix, params) {
    return new checks$1.$ZodCheckStartsWith({
        check: "string_format",
        format: "starts_with",
        ...util$1.normalizeParams(params),
        prefix,
    });
}
function _endsWith(suffix, params) {
    return new checks$1.$ZodCheckEndsWith({
        check: "string_format",
        format: "ends_with",
        ...util$1.normalizeParams(params),
        suffix,
    });
}
function _property(property, schema, params) {
    return new checks$1.$ZodCheckProperty({
        check: "property",
        property,
        schema,
        ...util$1.normalizeParams(params),
    });
}
function _mime(types, params) {
    return new checks$1.$ZodCheckMimeType({
        check: "mime_type",
        mime: types,
        ...util$1.normalizeParams(params),
    });
}
function _overwrite(tx) {
    return new checks$1.$ZodCheckOverwrite({
        check: "overwrite",
        tx,
    });
}
// normalize
function _normalize(form) {
    return _overwrite((input) => input.normalize(form));
}
// trim
function _trim() {
    return _overwrite((input) => input.trim());
}
// toLowerCase
function _toLowerCase() {
    return _overwrite((input) => input.toLowerCase());
}
// toUpperCase
function _toUpperCase() {
    return _overwrite((input) => input.toUpperCase());
}
function _array(Class, element, params) {
    return new Class({
        type: "array",
        element,
        // get element() {
        //   return element;
        // },
        ...util$1.normalizeParams(params),
    });
}
function _union(Class, options, params) {
    return new Class({
        type: "union",
        options,
        ...util$1.normalizeParams(params),
    });
}
function _discriminatedUnion(Class, discriminator, options, params) {
    return new Class({
        type: "union",
        options,
        discriminator,
        ...util$1.normalizeParams(params),
    });
}
function _intersection(Class, left, right) {
    return new Class({
        type: "intersection",
        left,
        right,
    });
}
// export function _tuple(
//   Class: util.SchemaClass<schemas.$ZodTuple>,
//   items: [],
//   params?: string | $ZodTupleParams
// ): schemas.$ZodTuple<[], null>;
function _tuple(Class, items, _paramsOrRest, _params) {
    const hasRest = _paramsOrRest instanceof schemas$2.$ZodType;
    const params = hasRest ? _params : _paramsOrRest;
    const rest = hasRest ? _paramsOrRest : null;
    return new Class({
        type: "tuple",
        items,
        rest,
        ...util$1.normalizeParams(params),
    });
}
function _record(Class, keyType, valueType, params) {
    return new Class({
        type: "record",
        keyType,
        valueType,
        ...util$1.normalizeParams(params),
    });
}
function _map(Class, keyType, valueType, params) {
    return new Class({
        type: "map",
        keyType,
        valueType,
        ...util$1.normalizeParams(params),
    });
}
function _set(Class, valueType, params) {
    return new Class({
        type: "set",
        valueType,
        ...util$1.normalizeParams(params),
    });
}
function _enum(Class, values, params) {
    const entries = Array.isArray(values) ? Object.fromEntries(values.map((v) => [v, v])) : values;
    // if (Array.isArray(values)) {
    //   for (const value of values) {
    //     entries[value] = value;
    //   }
    // } else {
    //   Object.assign(entries, values);
    // }
    // const entries: util.EnumLike = {};
    // for (const val of values) {
    //   entries[val] = val;
    // }
    return new Class({
        type: "enum",
        entries,
        ...util$1.normalizeParams(params),
    });
}
/** @deprecated This API has been merged into `z.enum()`. Use `z.enum()` instead.
 *
 * ```ts
 * enum Colors { red, green, blue }
 * z.enum(Colors);
 * ```
 */
function _nativeEnum(Class, entries, params) {
    return new Class({
        type: "enum",
        entries,
        ...util$1.normalizeParams(params),
    });
}
function _literal(Class, value, params) {
    return new Class({
        type: "literal",
        values: Array.isArray(value) ? value : [value],
        ...util$1.normalizeParams(params),
    });
}
function _file(Class, params) {
    return new Class({
        type: "file",
        ...util$1.normalizeParams(params),
    });
}
function _transform(Class, fn) {
    return new Class({
        type: "transform",
        transform: fn,
    });
}
function _optional(Class, innerType) {
    return new Class({
        type: "optional",
        innerType,
    });
}
function _nullable(Class, innerType) {
    return new Class({
        type: "nullable",
        innerType,
    });
}
function _default$2(Class, innerType, defaultValue) {
    return new Class({
        type: "default",
        innerType,
        get defaultValue() {
            return typeof defaultValue === "function" ? defaultValue() : util$1.shallowClone(defaultValue);
        },
    });
}
function _nonoptional(Class, innerType, params) {
    return new Class({
        type: "nonoptional",
        innerType,
        ...util$1.normalizeParams(params),
    });
}
function _success(Class, innerType) {
    return new Class({
        type: "success",
        innerType,
    });
}
function _catch(Class, innerType, catchValue) {
    return new Class({
        type: "catch",
        innerType,
        catchValue: (typeof catchValue === "function" ? catchValue : () => catchValue),
    });
}
function _pipe(Class, in_, out) {
    return new Class({
        type: "pipe",
        in: in_,
        out,
    });
}
function _readonly(Class, innerType) {
    return new Class({
        type: "readonly",
        innerType,
    });
}
function _templateLiteral(Class, parts, params) {
    return new Class({
        type: "template_literal",
        parts,
        ...util$1.normalizeParams(params),
    });
}
function _lazy(Class, getter) {
    return new Class({
        type: "lazy",
        getter,
    });
}
function _promise(Class, innerType) {
    return new Class({
        type: "promise",
        innerType,
    });
}
function _custom(Class, fn, _params) {
    const norm = util$1.normalizeParams(_params);
    norm.abort ?? (norm.abort = true); // default to abort:false
    const schema = new Class({
        type: "custom",
        check: "custom",
        fn: fn,
        ...norm,
    });
    return schema;
}
// same as _custom but defaults to abort:false
function _refine(Class, fn, _params) {
    const schema = new Class({
        type: "custom",
        check: "custom",
        fn: fn,
        ...util$1.normalizeParams(_params),
    });
    return schema;
}
function _superRefine(fn) {
    const ch = _check((payload) => {
        payload.addIssue = (issue) => {
            if (typeof issue === "string") {
                payload.issues.push(util$1.issue(issue, payload.value, ch._zod.def));
            }
            else {
                // for Zod 3 backwards compatibility
                const _issue = issue;
                if (_issue.fatal)
                    _issue.continue = false;
                _issue.code ?? (_issue.code = "custom");
                _issue.input ?? (_issue.input = payload.value);
                _issue.inst ?? (_issue.inst = ch);
                _issue.continue ?? (_issue.continue = !ch._zod.def.abort); // abort is always undefined, so this is always true...
                payload.issues.push(util$1.issue(_issue));
            }
        };
        return fn(payload.value, payload);
    });
    return ch;
}
function _check(fn, params) {
    const ch = new checks$1.$ZodCheck({
        check: "custom",
        ...util$1.normalizeParams(params),
    });
    ch._zod.check = fn;
    return ch;
}
function _stringbool(Classes, _params) {
    const params = util$1.normalizeParams(_params);
    let truthyArray = params.truthy ?? ["true", "1", "yes", "on", "y", "enabled"];
    let falsyArray = params.falsy ?? ["false", "0", "no", "off", "n", "disabled"];
    if (params.case !== "sensitive") {
        truthyArray = truthyArray.map((v) => (typeof v === "string" ? v.toLowerCase() : v));
        falsyArray = falsyArray.map((v) => (typeof v === "string" ? v.toLowerCase() : v));
    }
    const truthySet = new Set(truthyArray);
    const falsySet = new Set(falsyArray);
    const _Codec = Classes.Codec ?? schemas$2.$ZodCodec;
    const _Boolean = Classes.Boolean ?? schemas$2.$ZodBoolean;
    const _String = Classes.String ?? schemas$2.$ZodString;
    const stringSchema = new _String({ type: "string", error: params.error });
    const booleanSchema = new _Boolean({ type: "boolean", error: params.error });
    const codec = new _Codec({
        type: "pipe",
        in: stringSchema,
        out: booleanSchema,
        transform: ((input, payload) => {
            let data = input;
            if (params.case !== "sensitive")
                data = data.toLowerCase();
            if (truthySet.has(data)) {
                return true;
            }
            else if (falsySet.has(data)) {
                return false;
            }
            else {
                payload.issues.push({
                    code: "invalid_value",
                    expected: "stringbool",
                    values: [...truthySet, ...falsySet],
                    input: payload.value,
                    inst: codec,
                    continue: false,
                });
                return {};
            }
        }),
        reverseTransform: ((input, _payload) => {
            if (input === true) {
                return truthyArray[0] || "true";
            }
            else {
                return falsyArray[0] || "false";
            }
        }),
        error: params.error,
    });
    return codec;
}
function _stringFormat(Class, format, fnOrRegex, _params = {}) {
    const params = util$1.normalizeParams(_params);
    const def = {
        ...util$1.normalizeParams(_params),
        check: "string_format",
        type: "string",
        format,
        fn: typeof fnOrRegex === "function" ? fnOrRegex : (val) => fnOrRegex.test(val),
        ...params,
    };
    if (fnOrRegex instanceof RegExp) {
        def.pattern = fnOrRegex;
    }
    const inst = new Class(def);
    return inst;
}

var toJsonSchema = {};

Object.defineProperty(toJsonSchema, "__esModule", { value: true });
toJsonSchema.JSONSchemaGenerator = void 0;
toJsonSchema.toJSONSchema = toJSONSchema;
const registries_js_1 = registries;
const util_js_1 = util$3;
class JSONSchemaGenerator {
    constructor(params) {
        this.counter = 0;
        this.metadataRegistry = params?.metadata ?? registries_js_1.globalRegistry;
        this.target = params?.target ?? "draft-2020-12";
        this.unrepresentable = params?.unrepresentable ?? "throw";
        this.override = params?.override ?? (() => { });
        this.io = params?.io ?? "output";
        this.seen = new Map();
    }
    process(schema, _params = { path: [], schemaPath: [] }) {
        var _a;
        const def = schema._zod.def;
        const formatMap = {
            guid: "uuid",
            url: "uri",
            datetime: "date-time",
            json_string: "json-string",
            regex: "", // do not set
        };
        // check for schema in seens
        const seen = this.seen.get(schema);
        if (seen) {
            seen.count++;
            // check if cycle
            const isCycle = _params.schemaPath.includes(schema);
            if (isCycle) {
                seen.cycle = _params.path;
            }
            return seen.schema;
        }
        // initialize
        const result = { schema: {}, count: 1, cycle: undefined, path: _params.path };
        this.seen.set(schema, result);
        // custom method overrides default behavior
        const overrideSchema = schema._zod.toJSONSchema?.();
        if (overrideSchema) {
            result.schema = overrideSchema;
        }
        else {
            const params = {
                ..._params,
                schemaPath: [..._params.schemaPath, schema],
                path: _params.path,
            };
            const parent = schema._zod.parent;
            if (parent) {
                // schema was cloned from another schema
                result.ref = parent;
                this.process(parent, params);
                this.seen.get(parent).isParent = true;
            }
            else {
                const _json = result.schema;
                switch (def.type) {
                    case "string": {
                        const json = _json;
                        json.type = "string";
                        const { minimum, maximum, format, patterns, contentEncoding } = schema._zod
                            .bag;
                        if (typeof minimum === "number")
                            json.minLength = minimum;
                        if (typeof maximum === "number")
                            json.maxLength = maximum;
                        // custom pattern overrides format
                        if (format) {
                            json.format = formatMap[format] ?? format;
                            if (json.format === "")
                                delete json.format; // empty format is not valid
                        }
                        if (contentEncoding)
                            json.contentEncoding = contentEncoding;
                        if (patterns && patterns.size > 0) {
                            const regexes = [...patterns];
                            if (regexes.length === 1)
                                json.pattern = regexes[0].source;
                            else if (regexes.length > 1) {
                                result.schema.allOf = [
                                    ...regexes.map((regex) => ({
                                        ...(this.target === "draft-7" || this.target === "draft-4" || this.target === "openapi-3.0"
                                            ? { type: "string" }
                                            : {}),
                                        pattern: regex.source,
                                    })),
                                ];
                            }
                        }
                        break;
                    }
                    case "number": {
                        const json = _json;
                        const { minimum, maximum, format, multipleOf, exclusiveMaximum, exclusiveMinimum } = schema._zod.bag;
                        if (typeof format === "string" && format.includes("int"))
                            json.type = "integer";
                        else
                            json.type = "number";
                        if (typeof exclusiveMinimum === "number") {
                            if (this.target === "draft-4" || this.target === "openapi-3.0") {
                                json.minimum = exclusiveMinimum;
                                json.exclusiveMinimum = true;
                            }
                            else {
                                json.exclusiveMinimum = exclusiveMinimum;
                            }
                        }
                        if (typeof minimum === "number") {
                            json.minimum = minimum;
                            if (typeof exclusiveMinimum === "number" && this.target !== "draft-4") {
                                if (exclusiveMinimum >= minimum)
                                    delete json.minimum;
                                else
                                    delete json.exclusiveMinimum;
                            }
                        }
                        if (typeof exclusiveMaximum === "number") {
                            if (this.target === "draft-4" || this.target === "openapi-3.0") {
                                json.maximum = exclusiveMaximum;
                                json.exclusiveMaximum = true;
                            }
                            else {
                                json.exclusiveMaximum = exclusiveMaximum;
                            }
                        }
                        if (typeof maximum === "number") {
                            json.maximum = maximum;
                            if (typeof exclusiveMaximum === "number" && this.target !== "draft-4") {
                                if (exclusiveMaximum <= maximum)
                                    delete json.maximum;
                                else
                                    delete json.exclusiveMaximum;
                            }
                        }
                        if (typeof multipleOf === "number")
                            json.multipleOf = multipleOf;
                        break;
                    }
                    case "boolean": {
                        const json = _json;
                        json.type = "boolean";
                        break;
                    }
                    case "bigint": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("BigInt cannot be represented in JSON Schema");
                        }
                        break;
                    }
                    case "symbol": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("Symbols cannot be represented in JSON Schema");
                        }
                        break;
                    }
                    case "null": {
                        if (this.target === "openapi-3.0") {
                            _json.type = "string";
                            _json.nullable = true;
                            _json.enum = [null];
                        }
                        else
                            _json.type = "null";
                        break;
                    }
                    case "any": {
                        break;
                    }
                    case "unknown": {
                        break;
                    }
                    case "undefined": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("Undefined cannot be represented in JSON Schema");
                        }
                        break;
                    }
                    case "void": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("Void cannot be represented in JSON Schema");
                        }
                        break;
                    }
                    case "never": {
                        _json.not = {};
                        break;
                    }
                    case "date": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("Date cannot be represented in JSON Schema");
                        }
                        break;
                    }
                    case "array": {
                        const json = _json;
                        const { minimum, maximum } = schema._zod.bag;
                        if (typeof minimum === "number")
                            json.minItems = minimum;
                        if (typeof maximum === "number")
                            json.maxItems = maximum;
                        json.type = "array";
                        json.items = this.process(def.element, { ...params, path: [...params.path, "items"] });
                        break;
                    }
                    case "object": {
                        const json = _json;
                        json.type = "object";
                        json.properties = {};
                        const shape = def.shape; // params.shapeCache.get(schema)!;
                        for (const key in shape) {
                            json.properties[key] = this.process(shape[key], {
                                ...params,
                                path: [...params.path, "properties", key],
                            });
                        }
                        // required keys
                        const allKeys = new Set(Object.keys(shape));
                        // const optionalKeys = new Set(def.optional);
                        const requiredKeys = new Set([...allKeys].filter((key) => {
                            const v = def.shape[key]._zod;
                            if (this.io === "input") {
                                return v.optin === undefined;
                            }
                            else {
                                return v.optout === undefined;
                            }
                        }));
                        if (requiredKeys.size > 0) {
                            json.required = Array.from(requiredKeys);
                        }
                        // catchall
                        if (def.catchall?._zod.def.type === "never") {
                            // strict
                            json.additionalProperties = false;
                        }
                        else if (!def.catchall) {
                            // regular
                            if (this.io === "output")
                                json.additionalProperties = false;
                        }
                        else if (def.catchall) {
                            json.additionalProperties = this.process(def.catchall, {
                                ...params,
                                path: [...params.path, "additionalProperties"],
                            });
                        }
                        break;
                    }
                    case "union": {
                        const json = _json;
                        const options = def.options.map((x, i) => this.process(x, {
                            ...params,
                            path: [...params.path, "anyOf", i],
                        }));
                        json.anyOf = options;
                        break;
                    }
                    case "intersection": {
                        const json = _json;
                        const a = this.process(def.left, {
                            ...params,
                            path: [...params.path, "allOf", 0],
                        });
                        const b = this.process(def.right, {
                            ...params,
                            path: [...params.path, "allOf", 1],
                        });
                        const isSimpleIntersection = (val) => "allOf" in val && Object.keys(val).length === 1;
                        const allOf = [
                            ...(isSimpleIntersection(a) ? a.allOf : [a]),
                            ...(isSimpleIntersection(b) ? b.allOf : [b]),
                        ];
                        json.allOf = allOf;
                        break;
                    }
                    case "tuple": {
                        const json = _json;
                        json.type = "array";
                        const prefixPath = this.target === "draft-2020-12" ? "prefixItems" : "items";
                        const restPath = this.target === "draft-2020-12" ? "items" : this.target === "openapi-3.0" ? "items" : "additionalItems";
                        const prefixItems = def.items.map((x, i) => this.process(x, {
                            ...params,
                            path: [...params.path, prefixPath, i],
                        }));
                        const rest = def.rest
                            ? this.process(def.rest, {
                                ...params,
                                path: [...params.path, restPath, ...(this.target === "openapi-3.0" ? [def.items.length] : [])],
                            })
                            : null;
                        if (this.target === "draft-2020-12") {
                            json.prefixItems = prefixItems;
                            if (rest) {
                                json.items = rest;
                            }
                        }
                        else if (this.target === "openapi-3.0") {
                            json.items = {
                                anyOf: prefixItems,
                            };
                            if (rest) {
                                json.items.anyOf.push(rest);
                            }
                            json.minItems = prefixItems.length;
                            if (!rest) {
                                json.maxItems = prefixItems.length;
                            }
                        }
                        else {
                            json.items = prefixItems;
                            if (rest) {
                                json.additionalItems = rest;
                            }
                        }
                        // length
                        const { minimum, maximum } = schema._zod.bag;
                        if (typeof minimum === "number")
                            json.minItems = minimum;
                        if (typeof maximum === "number")
                            json.maxItems = maximum;
                        break;
                    }
                    case "record": {
                        const json = _json;
                        json.type = "object";
                        if (this.target === "draft-7" || this.target === "draft-2020-12") {
                            json.propertyNames = this.process(def.keyType, {
                                ...params,
                                path: [...params.path, "propertyNames"],
                            });
                        }
                        json.additionalProperties = this.process(def.valueType, {
                            ...params,
                            path: [...params.path, "additionalProperties"],
                        });
                        break;
                    }
                    case "map": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("Map cannot be represented in JSON Schema");
                        }
                        break;
                    }
                    case "set": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("Set cannot be represented in JSON Schema");
                        }
                        break;
                    }
                    case "enum": {
                        const json = _json;
                        const values = (0, util_js_1.getEnumValues)(def.entries);
                        // Number enums can have both string and number values
                        if (values.every((v) => typeof v === "number"))
                            json.type = "number";
                        if (values.every((v) => typeof v === "string"))
                            json.type = "string";
                        json.enum = values;
                        break;
                    }
                    case "literal": {
                        const json = _json;
                        const vals = [];
                        for (const val of def.values) {
                            if (val === undefined) {
                                if (this.unrepresentable === "throw") {
                                    throw new Error("Literal `undefined` cannot be represented in JSON Schema");
                                }
                            }
                            else if (typeof val === "bigint") {
                                if (this.unrepresentable === "throw") {
                                    throw new Error("BigInt literals cannot be represented in JSON Schema");
                                }
                                else {
                                    vals.push(Number(val));
                                }
                            }
                            else {
                                vals.push(val);
                            }
                        }
                        if (vals.length === 0) ;
                        else if (vals.length === 1) {
                            const val = vals[0];
                            json.type = val === null ? "null" : typeof val;
                            if (this.target === "draft-4" || this.target === "openapi-3.0") {
                                json.enum = [val];
                            }
                            else {
                                json.const = val;
                            }
                        }
                        else {
                            if (vals.every((v) => typeof v === "number"))
                                json.type = "number";
                            if (vals.every((v) => typeof v === "string"))
                                json.type = "string";
                            if (vals.every((v) => typeof v === "boolean"))
                                json.type = "string";
                            if (vals.every((v) => v === null))
                                json.type = "null";
                            json.enum = vals;
                        }
                        break;
                    }
                    case "file": {
                        const json = _json;
                        const file = {
                            type: "string",
                            format: "binary",
                            contentEncoding: "binary",
                        };
                        const { minimum, maximum, mime } = schema._zod.bag;
                        if (minimum !== undefined)
                            file.minLength = minimum;
                        if (maximum !== undefined)
                            file.maxLength = maximum;
                        if (mime) {
                            if (mime.length === 1) {
                                file.contentMediaType = mime[0];
                                Object.assign(json, file);
                            }
                            else {
                                json.anyOf = mime.map((m) => {
                                    const mFile = { ...file, contentMediaType: m };
                                    return mFile;
                                });
                            }
                        }
                        else {
                            Object.assign(json, file);
                        }
                        // if (this.unrepresentable === "throw") {
                        //   throw new Error("File cannot be represented in JSON Schema");
                        // }
                        break;
                    }
                    case "transform": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("Transforms cannot be represented in JSON Schema");
                        }
                        break;
                    }
                    case "nullable": {
                        const inner = this.process(def.innerType, params);
                        if (this.target === "openapi-3.0") {
                            result.ref = def.innerType;
                            _json.nullable = true;
                        }
                        else {
                            _json.anyOf = [inner, { type: "null" }];
                        }
                        break;
                    }
                    case "nonoptional": {
                        this.process(def.innerType, params);
                        result.ref = def.innerType;
                        break;
                    }
                    case "success": {
                        const json = _json;
                        json.type = "boolean";
                        break;
                    }
                    case "default": {
                        this.process(def.innerType, params);
                        result.ref = def.innerType;
                        _json.default = JSON.parse(JSON.stringify(def.defaultValue));
                        break;
                    }
                    case "prefault": {
                        this.process(def.innerType, params);
                        result.ref = def.innerType;
                        if (this.io === "input")
                            _json._prefault = JSON.parse(JSON.stringify(def.defaultValue));
                        break;
                    }
                    case "catch": {
                        // use conditionals
                        this.process(def.innerType, params);
                        result.ref = def.innerType;
                        let catchValue;
                        try {
                            catchValue = def.catchValue(undefined);
                        }
                        catch {
                            throw new Error("Dynamic catch values are not supported in JSON Schema");
                        }
                        _json.default = catchValue;
                        break;
                    }
                    case "nan": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("NaN cannot be represented in JSON Schema");
                        }
                        break;
                    }
                    case "template_literal": {
                        const json = _json;
                        const pattern = schema._zod.pattern;
                        if (!pattern)
                            throw new Error("Pattern not found in template literal");
                        json.type = "string";
                        json.pattern = pattern.source;
                        break;
                    }
                    case "pipe": {
                        const innerType = this.io === "input" ? (def.in._zod.def.type === "transform" ? def.out : def.in) : def.out;
                        this.process(innerType, params);
                        result.ref = innerType;
                        break;
                    }
                    case "readonly": {
                        this.process(def.innerType, params);
                        result.ref = def.innerType;
                        _json.readOnly = true;
                        break;
                    }
                    // passthrough types
                    case "promise": {
                        this.process(def.innerType, params);
                        result.ref = def.innerType;
                        break;
                    }
                    case "optional": {
                        this.process(def.innerType, params);
                        result.ref = def.innerType;
                        break;
                    }
                    case "lazy": {
                        const innerType = schema._zod.innerType;
                        this.process(innerType, params);
                        result.ref = innerType;
                        break;
                    }
                    case "custom": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("Custom types cannot be represented in JSON Schema");
                        }
                        break;
                    }
                    case "function": {
                        if (this.unrepresentable === "throw") {
                            throw new Error("Function types cannot be represented in JSON Schema");
                        }
                        break;
                    }
                }
            }
        }
        // metadata
        const meta = this.metadataRegistry.get(schema);
        if (meta)
            Object.assign(result.schema, meta);
        if (this.io === "input" && isTransforming(schema)) {
            // examples/defaults only apply to output type of pipe
            delete result.schema.examples;
            delete result.schema.default;
        }
        // set prefault as default
        if (this.io === "input" && result.schema._prefault)
            (_a = result.schema).default ?? (_a.default = result.schema._prefault);
        delete result.schema._prefault;
        // pulling fresh from this.seen in case it was overwritten
        const _result = this.seen.get(schema);
        return _result.schema;
    }
    emit(schema, _params) {
        const params = {
            cycles: _params?.cycles ?? "ref",
            reused: _params?.reused ?? "inline",
            // unrepresentable: _params?.unrepresentable ?? "throw",
            // uri: _params?.uri ?? ((id) => `${id}`),
            external: _params?.external ?? undefined,
        };
        // iterate over seen map;
        const root = this.seen.get(schema);
        if (!root)
            throw new Error("Unprocessed schema. This is a bug in Zod.");
        // initialize result with root schema fields
        // Object.assign(result, seen.cached);
        // returns a ref to the schema
        // defId will be empty if the ref points to an external schema (or #)
        const makeURI = (entry) => {
            // comparing the seen objects because sometimes
            // multiple schemas map to the same seen object.
            // e.g. lazy
            // external is configured
            const defsSegment = this.target === "draft-2020-12" ? "$defs" : "definitions";
            if (params.external) {
                const externalId = params.external.registry.get(entry[0])?.id; // ?? "__shared";// `__schema${this.counter++}`;
                // check if schema is in the external registry
                const uriGenerator = params.external.uri ?? ((id) => id);
                if (externalId) {
                    return { ref: uriGenerator(externalId) };
                }
                // otherwise, add to __shared
                const id = entry[1].defId ?? entry[1].schema.id ?? `schema${this.counter++}`;
                entry[1].defId = id; // set defId so it will be reused if needed
                return { defId: id, ref: `${uriGenerator("__shared")}#/${defsSegment}/${id}` };
            }
            if (entry[1] === root) {
                return { ref: "#" };
            }
            // self-contained schema
            const uriPrefix = `#`;
            const defUriPrefix = `${uriPrefix}/${defsSegment}/`;
            const defId = entry[1].schema.id ?? `__schema${this.counter++}`;
            return { defId, ref: defUriPrefix + defId };
        };
        // stored cached version in `def` property
        // remove all properties, set $ref
        const extractToDef = (entry) => {
            // if the schema is already a reference, do not extract it
            if (entry[1].schema.$ref) {
                return;
            }
            const seen = entry[1];
            const { ref, defId } = makeURI(entry);
            seen.def = { ...seen.schema };
            // defId won't be set if the schema is a reference to an external schema
            if (defId)
                seen.defId = defId;
            // wipe away all properties except $ref
            const schema = seen.schema;
            for (const key in schema) {
                delete schema[key];
            }
            schema.$ref = ref;
        };
        // throw on cycles
        // break cycles
        if (params.cycles === "throw") {
            for (const entry of this.seen.entries()) {
                const seen = entry[1];
                if (seen.cycle) {
                    throw new Error("Cycle detected: " +
                        `#/${seen.cycle?.join("/")}/<root>` +
                        '\n\nSet the `cycles` parameter to `"ref"` to resolve cyclical schemas with defs.');
                }
            }
        }
        // extract schemas into $defs
        for (const entry of this.seen.entries()) {
            const seen = entry[1];
            // convert root schema to # $ref
            if (schema === entry[0]) {
                extractToDef(entry); // this has special handling for the root schema
                continue;
            }
            // extract schemas that are in the external registry
            if (params.external) {
                const ext = params.external.registry.get(entry[0])?.id;
                if (schema !== entry[0] && ext) {
                    extractToDef(entry);
                    continue;
                }
            }
            // extract schemas with `id` meta
            const id = this.metadataRegistry.get(entry[0])?.id;
            if (id) {
                extractToDef(entry);
                continue;
            }
            // break cycles
            if (seen.cycle) {
                // any
                extractToDef(entry);
                continue;
            }
            // extract reused schemas
            if (seen.count > 1) {
                if (params.reused === "ref") {
                    extractToDef(entry);
                    // biome-ignore lint:
                    continue;
                }
            }
        }
        // flatten _refs
        const flattenRef = (zodSchema, params) => {
            const seen = this.seen.get(zodSchema);
            const schema = seen.def ?? seen.schema;
            const _cached = { ...schema };
            // already seen
            if (seen.ref === null) {
                return;
            }
            // flatten ref if defined
            const ref = seen.ref;
            seen.ref = null; // prevent recursion
            if (ref) {
                flattenRef(ref, params);
                // merge referenced schema into current
                const refSchema = this.seen.get(ref).schema;
                if (refSchema.$ref &&
                    (params.target === "draft-7" || params.target === "draft-4" || params.target === "openapi-3.0")) {
                    schema.allOf = schema.allOf ?? [];
                    schema.allOf.push(refSchema);
                }
                else {
                    Object.assign(schema, refSchema);
                    Object.assign(schema, _cached); // prevent overwriting any fields in the original schema
                }
            }
            // execute overrides
            if (!seen.isParent)
                this.override({
                    zodSchema: zodSchema,
                    jsonSchema: schema,
                    path: seen.path ?? [],
                });
        };
        for (const entry of [...this.seen.entries()].reverse()) {
            flattenRef(entry[0], { target: this.target });
        }
        const result = {};
        if (this.target === "draft-2020-12") {
            result.$schema = "https://json-schema.org/draft/2020-12/schema";
        }
        else if (this.target === "draft-7") {
            result.$schema = "http://json-schema.org/draft-07/schema#";
        }
        else if (this.target === "draft-4") {
            result.$schema = "http://json-schema.org/draft-04/schema#";
        }
        else if (this.target === "openapi-3.0") ;
        else {
            // @ts-ignore
            console.warn(`Invalid target: ${this.target}`);
        }
        if (params.external?.uri) {
            const id = params.external.registry.get(schema)?.id;
            if (!id)
                throw new Error("Schema is missing an `id` property");
            result.$id = params.external.uri(id);
        }
        Object.assign(result, root.def);
        // build defs object
        const defs = params.external?.defs ?? {};
        for (const entry of this.seen.entries()) {
            const seen = entry[1];
            if (seen.def && seen.defId) {
                defs[seen.defId] = seen.def;
            }
        }
        // set definitions in result
        if (params.external) ;
        else {
            if (Object.keys(defs).length > 0) {
                if (this.target === "draft-2020-12") {
                    result.$defs = defs;
                }
                else {
                    result.definitions = defs;
                }
            }
        }
        try {
            // this "finalizes" this schema and ensures all cycles are removed
            // each call to .emit() is functionally independent
            // though the seen map is shared
            return JSON.parse(JSON.stringify(result));
        }
        catch (_err) {
            throw new Error("Error converting schema to JSON.");
        }
    }
}
toJsonSchema.JSONSchemaGenerator = JSONSchemaGenerator;
function toJSONSchema(input, _params) {
    if (input instanceof registries_js_1.$ZodRegistry) {
        const gen = new JSONSchemaGenerator(_params);
        const defs = {};
        for (const entry of input._idmap.entries()) {
            const [_, schema] = entry;
            gen.process(schema);
        }
        const schemas = {};
        const external = {
            registry: input,
            uri: _params?.uri,
            defs,
        };
        for (const entry of input._idmap.entries()) {
            const [key, schema] = entry;
            schemas[key] = gen.emit(schema, {
                ..._params,
                external,
            });
        }
        if (Object.keys(defs).length > 0) {
            const defsSegment = gen.target === "draft-2020-12" ? "$defs" : "definitions";
            schemas.__shared = {
                [defsSegment]: defs,
            };
        }
        return { schemas };
    }
    const gen = new JSONSchemaGenerator(_params);
    gen.process(input);
    return gen.emit(input, _params);
}
function isTransforming(_schema, _ctx) {
    const ctx = _ctx ?? { seen: new Set() };
    if (ctx.seen.has(_schema))
        return false;
    ctx.seen.add(_schema);
    const schema = _schema;
    const def = schema._zod.def;
    switch (def.type) {
        case "string":
        case "number":
        case "bigint":
        case "boolean":
        case "date":
        case "symbol":
        case "undefined":
        case "null":
        case "any":
        case "unknown":
        case "never":
        case "void":
        case "literal":
        case "enum":
        case "nan":
        case "file":
        case "template_literal":
            return false;
        case "array": {
            return isTransforming(def.element, ctx);
        }
        case "object": {
            for (const key in def.shape) {
                if (isTransforming(def.shape[key], ctx))
                    return true;
            }
            return false;
        }
        case "union": {
            for (const option of def.options) {
                if (isTransforming(option, ctx))
                    return true;
            }
            return false;
        }
        case "intersection": {
            return isTransforming(def.left, ctx) || isTransforming(def.right, ctx);
        }
        case "tuple": {
            for (const item of def.items) {
                if (isTransforming(item, ctx))
                    return true;
            }
            if (def.rest && isTransforming(def.rest, ctx))
                return true;
            return false;
        }
        case "record": {
            return isTransforming(def.keyType, ctx) || isTransforming(def.valueType, ctx);
        }
        case "map": {
            return isTransforming(def.keyType, ctx) || isTransforming(def.valueType, ctx);
        }
        case "set": {
            return isTransforming(def.valueType, ctx);
        }
        // inner types
        case "promise":
        case "optional":
        case "nonoptional":
        case "nullable":
        case "readonly":
            return isTransforming(def.innerType, ctx);
        case "lazy":
            return isTransforming(def.getter(), ctx);
        case "default": {
            return isTransforming(def.innerType, ctx);
        }
        case "prefault": {
            return isTransforming(def.innerType, ctx);
        }
        case "custom": {
            return false;
        }
        case "transform": {
            return true;
        }
        case "pipe": {
            return isTransforming(def.in, ctx) || isTransforming(def.out, ctx);
        }
        case "success": {
            return false;
        }
        case "catch": {
            return false;
        }
        case "function": {
            return false;
        }
    }
    throw new Error(`Unknown schema type: ${def.type}`);
}

var jsonSchema = {};

Object.defineProperty(jsonSchema, "__esModule", { value: true });

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __exportStar = (commonjsGlobal && commonjsGlobal.__exportStar) || function(m, exports) {
	    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
	};
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.JSONSchema = exports.locales = exports.regexes = exports.util = void 0;
	__exportStar(core$3, exports);
	__exportStar(parse$3, exports);
	__exportStar(errors$3, exports);
	__exportStar(schemas$3, exports);
	__exportStar(checks$2, exports);
	__exportStar(versions, exports);
	exports.util = __importStar(util$3);
	exports.regexes = __importStar(regexes);
	exports.locales = __importStar(locales);
	__exportStar(registries, exports);
	__exportStar(doc, exports);
	__exportStar(api, exports);
	__exportStar(toJsonSchema, exports);
	exports.JSONSchema = __importStar(jsonSchema);
} (core$4));

var schemas$1 = {};

var checks = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.toUpperCase = exports.toLowerCase = exports.trim = exports.normalize = exports.overwrite = exports.mime = exports.property = exports.endsWith = exports.startsWith = exports.includes = exports.uppercase = exports.lowercase = exports.regex = exports.length = exports.minLength = exports.maxLength = exports.size = exports.minSize = exports.maxSize = exports.multipleOf = exports.nonnegative = exports.nonpositive = exports.negative = exports.positive = exports.gte = exports.gt = exports.lte = exports.lt = void 0;
	var index_js_1 = core$4;
	Object.defineProperty(exports, "lt", { enumerable: true, get: function () { return index_js_1._lt; } });
	Object.defineProperty(exports, "lte", { enumerable: true, get: function () { return index_js_1._lte; } });
	Object.defineProperty(exports, "gt", { enumerable: true, get: function () { return index_js_1._gt; } });
	Object.defineProperty(exports, "gte", { enumerable: true, get: function () { return index_js_1._gte; } });
	Object.defineProperty(exports, "positive", { enumerable: true, get: function () { return index_js_1._positive; } });
	Object.defineProperty(exports, "negative", { enumerable: true, get: function () { return index_js_1._negative; } });
	Object.defineProperty(exports, "nonpositive", { enumerable: true, get: function () { return index_js_1._nonpositive; } });
	Object.defineProperty(exports, "nonnegative", { enumerable: true, get: function () { return index_js_1._nonnegative; } });
	Object.defineProperty(exports, "multipleOf", { enumerable: true, get: function () { return index_js_1._multipleOf; } });
	Object.defineProperty(exports, "maxSize", { enumerable: true, get: function () { return index_js_1._maxSize; } });
	Object.defineProperty(exports, "minSize", { enumerable: true, get: function () { return index_js_1._minSize; } });
	Object.defineProperty(exports, "size", { enumerable: true, get: function () { return index_js_1._size; } });
	Object.defineProperty(exports, "maxLength", { enumerable: true, get: function () { return index_js_1._maxLength; } });
	Object.defineProperty(exports, "minLength", { enumerable: true, get: function () { return index_js_1._minLength; } });
	Object.defineProperty(exports, "length", { enumerable: true, get: function () { return index_js_1._length; } });
	Object.defineProperty(exports, "regex", { enumerable: true, get: function () { return index_js_1._regex; } });
	Object.defineProperty(exports, "lowercase", { enumerable: true, get: function () { return index_js_1._lowercase; } });
	Object.defineProperty(exports, "uppercase", { enumerable: true, get: function () { return index_js_1._uppercase; } });
	Object.defineProperty(exports, "includes", { enumerable: true, get: function () { return index_js_1._includes; } });
	Object.defineProperty(exports, "startsWith", { enumerable: true, get: function () { return index_js_1._startsWith; } });
	Object.defineProperty(exports, "endsWith", { enumerable: true, get: function () { return index_js_1._endsWith; } });
	Object.defineProperty(exports, "property", { enumerable: true, get: function () { return index_js_1._property; } });
	Object.defineProperty(exports, "mime", { enumerable: true, get: function () { return index_js_1._mime; } });
	Object.defineProperty(exports, "overwrite", { enumerable: true, get: function () { return index_js_1._overwrite; } });
	Object.defineProperty(exports, "normalize", { enumerable: true, get: function () { return index_js_1._normalize; } });
	Object.defineProperty(exports, "trim", { enumerable: true, get: function () { return index_js_1._trim; } });
	Object.defineProperty(exports, "toLowerCase", { enumerable: true, get: function () { return index_js_1._toLowerCase; } });
	Object.defineProperty(exports, "toUpperCase", { enumerable: true, get: function () { return index_js_1._toUpperCase; } });
} (checks));

var iso = {};

var hasRequiredIso;

function requireIso () {
	if (hasRequiredIso) return iso;
	hasRequiredIso = 1;
	(function (exports) {
		var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ZodISODuration = exports.ZodISOTime = exports.ZodISODate = exports.ZodISODateTime = void 0;
		exports.datetime = datetime;
		exports.date = date;
		exports.time = time;
		exports.duration = duration;
		const core = __importStar(core$4);
		const schemas = __importStar(requireSchemas());
		exports.ZodISODateTime = core.$constructor("ZodISODateTime", (inst, def) => {
		    core.$ZodISODateTime.init(inst, def);
		    schemas.ZodStringFormat.init(inst, def);
		});
		function datetime(params) {
		    return core._isoDateTime(exports.ZodISODateTime, params);
		}
		exports.ZodISODate = core.$constructor("ZodISODate", (inst, def) => {
		    core.$ZodISODate.init(inst, def);
		    schemas.ZodStringFormat.init(inst, def);
		});
		function date(params) {
		    return core._isoDate(exports.ZodISODate, params);
		}
		exports.ZodISOTime = core.$constructor("ZodISOTime", (inst, def) => {
		    core.$ZodISOTime.init(inst, def);
		    schemas.ZodStringFormat.init(inst, def);
		});
		function time(params) {
		    return core._isoTime(exports.ZodISOTime, params);
		}
		exports.ZodISODuration = core.$constructor("ZodISODuration", (inst, def) => {
		    core.$ZodISODuration.init(inst, def);
		    schemas.ZodStringFormat.init(inst, def);
		});
		function duration(params) {
		    return core._isoDuration(exports.ZodISODuration, params);
		}
} (iso));
	return iso;
}

var parse$2 = {};

var errors$2 = {};

var __createBinding$5 = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault$5 = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar$5 = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding$5(result, mod, k);
    __setModuleDefault$5(result, mod);
    return result;
};
Object.defineProperty(errors$2, "__esModule", { value: true });
errors$2.ZodRealError = errors$2.ZodError = void 0;
const core$2 = __importStar$5(core$4);
const index_js_1 = core$4;
const util = __importStar$5(util$3);
const initializer = (inst, issues) => {
    index_js_1.$ZodError.init(inst, issues);
    inst.name = "ZodError";
    Object.defineProperties(inst, {
        format: {
            value: (mapper) => core$2.formatError(inst, mapper),
            // enumerable: false,
        },
        flatten: {
            value: (mapper) => core$2.flattenError(inst, mapper),
            // enumerable: false,
        },
        addIssue: {
            value: (issue) => {
                inst.issues.push(issue);
                inst.message = JSON.stringify(inst.issues, util.jsonStringifyReplacer, 2);
            },
            // enumerable: false,
        },
        addIssues: {
            value: (issues) => {
                inst.issues.push(...issues);
                inst.message = JSON.stringify(inst.issues, util.jsonStringifyReplacer, 2);
            },
            // enumerable: false,
        },
        isEmpty: {
            get() {
                return inst.issues.length === 0;
            },
            // enumerable: false,
        },
    });
    // Object.defineProperty(inst, "isEmpty", {
    //   get() {
    //     return inst.issues.length === 0;
    //   },
    // });
};
errors$2.ZodError = core$2.$constructor("ZodError", initializer);
errors$2.ZodRealError = core$2.$constructor("ZodError", initializer, {
    Parent: Error,
});

var __createBinding$4 = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault$4 = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar$4 = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding$4(result, mod, k);
    __setModuleDefault$4(result, mod);
    return result;
};
Object.defineProperty(parse$2, "__esModule", { value: true });
parse$2.safeDecodeAsync = parse$2.safeEncodeAsync = parse$2.safeDecode = parse$2.safeEncode = parse$2.decodeAsync = parse$2.encodeAsync = parse$2.decode = parse$2.encode = parse$2.safeParseAsync = parse$2.safeParse = parse$2.parseAsync = parse$2.parse = void 0;
const core$1 = __importStar$4(core$4);
const errors_js_1 = errors$2;
parse$2.parse = core$1._parse(errors_js_1.ZodRealError);
parse$2.parseAsync = core$1._parseAsync(errors_js_1.ZodRealError);
parse$2.safeParse = core$1._safeParse(errors_js_1.ZodRealError);
parse$2.safeParseAsync = core$1._safeParseAsync(errors_js_1.ZodRealError);
// Codec functions
parse$2.encode = core$1._encode(errors_js_1.ZodRealError);
parse$2.decode = core$1._decode(errors_js_1.ZodRealError);
parse$2.encodeAsync = core$1._encodeAsync(errors_js_1.ZodRealError);
parse$2.decodeAsync = core$1._decodeAsync(errors_js_1.ZodRealError);
parse$2.safeEncode = core$1._safeEncode(errors_js_1.ZodRealError);
parse$2.safeDecode = core$1._safeDecode(errors_js_1.ZodRealError);
parse$2.safeEncodeAsync = core$1._safeEncodeAsync(errors_js_1.ZodRealError);
parse$2.safeDecodeAsync = core$1._safeDecodeAsync(errors_js_1.ZodRealError);

var hasRequiredSchemas;

function requireSchemas () {
	if (hasRequiredSchemas) return schemas$1;
	hasRequiredSchemas = 1;
	(function (exports) {
		var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ZodTransform = exports.ZodFile = exports.ZodLiteral = exports.ZodEnum = exports.ZodSet = exports.ZodMap = exports.ZodRecord = exports.ZodTuple = exports.ZodIntersection = exports.ZodDiscriminatedUnion = exports.ZodUnion = exports.ZodObject = exports.ZodArray = exports.ZodDate = exports.ZodVoid = exports.ZodNever = exports.ZodUnknown = exports.ZodAny = exports.ZodNull = exports.ZodUndefined = exports.ZodSymbol = exports.ZodBigIntFormat = exports.ZodBigInt = exports.ZodBoolean = exports.ZodNumberFormat = exports.ZodNumber = exports.ZodCustomStringFormat = exports.ZodJWT = exports.ZodE164 = exports.ZodBase64URL = exports.ZodBase64 = exports.ZodCIDRv6 = exports.ZodCIDRv4 = exports.ZodIPv6 = exports.ZodIPv4 = exports.ZodKSUID = exports.ZodXID = exports.ZodULID = exports.ZodCUID2 = exports.ZodCUID = exports.ZodNanoID = exports.ZodEmoji = exports.ZodURL = exports.ZodUUID = exports.ZodGUID = exports.ZodEmail = exports.ZodStringFormat = exports.ZodString = exports._ZodString = exports.ZodType = void 0;
		exports.stringbool = exports.ZodCustom = exports.ZodFunction = exports.ZodPromise = exports.ZodLazy = exports.ZodTemplateLiteral = exports.ZodReadonly = exports.ZodCodec = exports.ZodPipe = exports.ZodNaN = exports.ZodCatch = exports.ZodSuccess = exports.ZodNonOptional = exports.ZodPrefault = exports.ZodDefault = exports.ZodNullable = exports.ZodOptional = void 0;
		exports.string = string;
		exports.email = email;
		exports.guid = guid;
		exports.uuid = uuid;
		exports.uuidv4 = uuidv4;
		exports.uuidv6 = uuidv6;
		exports.uuidv7 = uuidv7;
		exports.url = url;
		exports.httpUrl = httpUrl;
		exports.emoji = emoji;
		exports.nanoid = nanoid;
		exports.cuid = cuid;
		exports.cuid2 = cuid2;
		exports.ulid = ulid;
		exports.xid = xid;
		exports.ksuid = ksuid;
		exports.ipv4 = ipv4;
		exports.ipv6 = ipv6;
		exports.cidrv4 = cidrv4;
		exports.cidrv6 = cidrv6;
		exports.base64 = base64;
		exports.base64url = base64url;
		exports.e164 = e164;
		exports.jwt = jwt;
		exports.stringFormat = stringFormat;
		exports.hostname = hostname;
		exports.hex = hex;
		exports.hash = hash;
		exports.number = number;
		exports.int = int;
		exports.float32 = float32;
		exports.float64 = float64;
		exports.int32 = int32;
		exports.uint32 = uint32;
		exports.boolean = boolean;
		exports.bigint = bigint;
		exports.int64 = int64;
		exports.uint64 = uint64;
		exports.symbol = symbol;
		exports.undefined = _undefined;
		exports.null = _null;
		exports.any = any;
		exports.unknown = unknown;
		exports.never = never;
		exports.void = _void;
		exports.date = date;
		exports.array = array;
		exports.keyof = keyof;
		exports.object = object;
		exports.strictObject = strictObject;
		exports.looseObject = looseObject;
		exports.union = union;
		exports.discriminatedUnion = discriminatedUnion;
		exports.intersection = intersection;
		exports.tuple = tuple;
		exports.record = record;
		exports.partialRecord = partialRecord;
		exports.map = map;
		exports.set = set;
		exports.enum = _enum;
		exports.nativeEnum = nativeEnum;
		exports.literal = literal;
		exports.file = file;
		exports.transform = transform;
		exports.optional = optional;
		exports.nullable = nullable;
		exports.nullish = nullish;
		exports._default = _default;
		exports.prefault = prefault;
		exports.nonoptional = nonoptional;
		exports.success = success;
		exports.catch = _catch;
		exports.nan = nan;
		exports.pipe = pipe;
		exports.codec = codec;
		exports.readonly = readonly;
		exports.templateLiteral = templateLiteral;
		exports.lazy = lazy;
		exports.promise = promise;
		exports._function = _function;
		exports.function = _function;
		exports._function = _function;
		exports.function = _function;
		exports.check = check;
		exports.custom = custom;
		exports.refine = refine;
		exports.superRefine = superRefine;
		exports.instanceof = _instanceof;
		exports.json = json;
		exports.preprocess = preprocess;
		const core = __importStar(core$4);
		const index_js_1 = core$4;
		const checks$1 = __importStar(checks);
		const iso = __importStar(requireIso());
		const parse = __importStar(parse$2);
		exports.ZodType = core.$constructor("ZodType", (inst, def) => {
		    core.$ZodType.init(inst, def);
		    inst.def = def;
		    inst.type = def.type;
		    Object.defineProperty(inst, "_def", { value: def });
		    // base methods
		    inst.check = (...checks) => {
		        return inst.clone({
		            ...def,
		            checks: [
		                ...(def.checks ?? []),
		                ...checks.map((ch) => typeof ch === "function" ? { _zod: { check: ch, def: { check: "custom" }, onattach: [] } } : ch),
		            ],
		        }
		        // { parent: true }
		        );
		    };
		    inst.clone = (def, params) => core.clone(inst, def, params);
		    inst.brand = () => inst;
		    inst.register = ((reg, meta) => {
		        reg.add(inst, meta);
		        return inst;
		    });
		    // parsing
		    inst.parse = (data, params) => parse.parse(inst, data, params, { callee: inst.parse });
		    inst.safeParse = (data, params) => parse.safeParse(inst, data, params);
		    inst.parseAsync = async (data, params) => parse.parseAsync(inst, data, params, { callee: inst.parseAsync });
		    inst.safeParseAsync = async (data, params) => parse.safeParseAsync(inst, data, params);
		    inst.spa = inst.safeParseAsync;
		    // encoding/decoding
		    inst.encode = (data, params) => parse.encode(inst, data, params);
		    inst.decode = (data, params) => parse.decode(inst, data, params);
		    inst.encodeAsync = async (data, params) => parse.encodeAsync(inst, data, params);
		    inst.decodeAsync = async (data, params) => parse.decodeAsync(inst, data, params);
		    inst.safeEncode = (data, params) => parse.safeEncode(inst, data, params);
		    inst.safeDecode = (data, params) => parse.safeDecode(inst, data, params);
		    inst.safeEncodeAsync = async (data, params) => parse.safeEncodeAsync(inst, data, params);
		    inst.safeDecodeAsync = async (data, params) => parse.safeDecodeAsync(inst, data, params);
		    // refinements
		    inst.refine = (check, params) => inst.check(refine(check, params));
		    inst.superRefine = (refinement) => inst.check(superRefine(refinement));
		    inst.overwrite = (fn) => inst.check(checks$1.overwrite(fn));
		    // wrappers
		    inst.optional = () => optional(inst);
		    inst.nullable = () => nullable(inst);
		    inst.nullish = () => optional(nullable(inst));
		    inst.nonoptional = (params) => nonoptional(inst, params);
		    inst.array = () => array(inst);
		    inst.or = (arg) => union([inst, arg]);
		    inst.and = (arg) => intersection(inst, arg);
		    inst.transform = (tx) => pipe(inst, transform(tx));
		    inst.default = (def) => _default(inst, def);
		    inst.prefault = (def) => prefault(inst, def);
		    // inst.coalesce = (def, params) => coalesce(inst, def, params);
		    inst.catch = (params) => _catch(inst, params);
		    inst.pipe = (target) => pipe(inst, target);
		    inst.readonly = () => readonly(inst);
		    // meta
		    inst.describe = (description) => {
		        const cl = inst.clone();
		        core.globalRegistry.add(cl, { description });
		        return cl;
		    };
		    Object.defineProperty(inst, "description", {
		        get() {
		            return core.globalRegistry.get(inst)?.description;
		        },
		        configurable: true,
		    });
		    inst.meta = (...args) => {
		        if (args.length === 0) {
		            return core.globalRegistry.get(inst);
		        }
		        const cl = inst.clone();
		        core.globalRegistry.add(cl, args[0]);
		        return cl;
		    };
		    // helpers
		    inst.isOptional = () => inst.safeParse(undefined).success;
		    inst.isNullable = () => inst.safeParse(null).success;
		    return inst;
		});
		/** @internal */
		exports._ZodString = core.$constructor("_ZodString", (inst, def) => {
		    core.$ZodString.init(inst, def);
		    exports.ZodType.init(inst, def);
		    const bag = inst._zod.bag;
		    inst.format = bag.format ?? null;
		    inst.minLength = bag.minimum ?? null;
		    inst.maxLength = bag.maximum ?? null;
		    // validations
		    inst.regex = (...args) => inst.check(checks$1.regex(...args));
		    inst.includes = (...args) => inst.check(checks$1.includes(...args));
		    inst.startsWith = (...args) => inst.check(checks$1.startsWith(...args));
		    inst.endsWith = (...args) => inst.check(checks$1.endsWith(...args));
		    inst.min = (...args) => inst.check(checks$1.minLength(...args));
		    inst.max = (...args) => inst.check(checks$1.maxLength(...args));
		    inst.length = (...args) => inst.check(checks$1.length(...args));
		    inst.nonempty = (...args) => inst.check(checks$1.minLength(1, ...args));
		    inst.lowercase = (params) => inst.check(checks$1.lowercase(params));
		    inst.uppercase = (params) => inst.check(checks$1.uppercase(params));
		    // transforms
		    inst.trim = () => inst.check(checks$1.trim());
		    inst.normalize = (...args) => inst.check(checks$1.normalize(...args));
		    inst.toLowerCase = () => inst.check(checks$1.toLowerCase());
		    inst.toUpperCase = () => inst.check(checks$1.toUpperCase());
		});
		exports.ZodString = core.$constructor("ZodString", (inst, def) => {
		    core.$ZodString.init(inst, def);
		    exports._ZodString.init(inst, def);
		    inst.email = (params) => inst.check(core._email(exports.ZodEmail, params));
		    inst.url = (params) => inst.check(core._url(exports.ZodURL, params));
		    inst.jwt = (params) => inst.check(core._jwt(exports.ZodJWT, params));
		    inst.emoji = (params) => inst.check(core._emoji(exports.ZodEmoji, params));
		    inst.guid = (params) => inst.check(core._guid(exports.ZodGUID, params));
		    inst.uuid = (params) => inst.check(core._uuid(exports.ZodUUID, params));
		    inst.uuidv4 = (params) => inst.check(core._uuidv4(exports.ZodUUID, params));
		    inst.uuidv6 = (params) => inst.check(core._uuidv6(exports.ZodUUID, params));
		    inst.uuidv7 = (params) => inst.check(core._uuidv7(exports.ZodUUID, params));
		    inst.nanoid = (params) => inst.check(core._nanoid(exports.ZodNanoID, params));
		    inst.guid = (params) => inst.check(core._guid(exports.ZodGUID, params));
		    inst.cuid = (params) => inst.check(core._cuid(exports.ZodCUID, params));
		    inst.cuid2 = (params) => inst.check(core._cuid2(exports.ZodCUID2, params));
		    inst.ulid = (params) => inst.check(core._ulid(exports.ZodULID, params));
		    inst.base64 = (params) => inst.check(core._base64(exports.ZodBase64, params));
		    inst.base64url = (params) => inst.check(core._base64url(exports.ZodBase64URL, params));
		    inst.xid = (params) => inst.check(core._xid(exports.ZodXID, params));
		    inst.ksuid = (params) => inst.check(core._ksuid(exports.ZodKSUID, params));
		    inst.ipv4 = (params) => inst.check(core._ipv4(exports.ZodIPv4, params));
		    inst.ipv6 = (params) => inst.check(core._ipv6(exports.ZodIPv6, params));
		    inst.cidrv4 = (params) => inst.check(core._cidrv4(exports.ZodCIDRv4, params));
		    inst.cidrv6 = (params) => inst.check(core._cidrv6(exports.ZodCIDRv6, params));
		    inst.e164 = (params) => inst.check(core._e164(exports.ZodE164, params));
		    // iso
		    inst.datetime = (params) => inst.check(iso.datetime(params));
		    inst.date = (params) => inst.check(iso.date(params));
		    inst.time = (params) => inst.check(iso.time(params));
		    inst.duration = (params) => inst.check(iso.duration(params));
		});
		function string(params) {
		    return core._string(exports.ZodString, params);
		}
		exports.ZodStringFormat = core.$constructor("ZodStringFormat", (inst, def) => {
		    core.$ZodStringFormat.init(inst, def);
		    exports._ZodString.init(inst, def);
		});
		exports.ZodEmail = core.$constructor("ZodEmail", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodEmail.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function email(params) {
		    return core._email(exports.ZodEmail, params);
		}
		exports.ZodGUID = core.$constructor("ZodGUID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodGUID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function guid(params) {
		    return core._guid(exports.ZodGUID, params);
		}
		exports.ZodUUID = core.$constructor("ZodUUID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodUUID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function uuid(params) {
		    return core._uuid(exports.ZodUUID, params);
		}
		function uuidv4(params) {
		    return core._uuidv4(exports.ZodUUID, params);
		}
		// ZodUUIDv6
		function uuidv6(params) {
		    return core._uuidv6(exports.ZodUUID, params);
		}
		// ZodUUIDv7
		function uuidv7(params) {
		    return core._uuidv7(exports.ZodUUID, params);
		}
		exports.ZodURL = core.$constructor("ZodURL", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodURL.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function url(params) {
		    return core._url(exports.ZodURL, params);
		}
		function httpUrl(params) {
		    return core._url(exports.ZodURL, {
		        protocol: /^https?$/,
		        hostname: core.regexes.domain,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodEmoji = core.$constructor("ZodEmoji", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodEmoji.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function emoji(params) {
		    return core._emoji(exports.ZodEmoji, params);
		}
		exports.ZodNanoID = core.$constructor("ZodNanoID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodNanoID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function nanoid(params) {
		    return core._nanoid(exports.ZodNanoID, params);
		}
		exports.ZodCUID = core.$constructor("ZodCUID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodCUID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function cuid(params) {
		    return core._cuid(exports.ZodCUID, params);
		}
		exports.ZodCUID2 = core.$constructor("ZodCUID2", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodCUID2.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function cuid2(params) {
		    return core._cuid2(exports.ZodCUID2, params);
		}
		exports.ZodULID = core.$constructor("ZodULID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodULID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function ulid(params) {
		    return core._ulid(exports.ZodULID, params);
		}
		exports.ZodXID = core.$constructor("ZodXID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodXID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function xid(params) {
		    return core._xid(exports.ZodXID, params);
		}
		exports.ZodKSUID = core.$constructor("ZodKSUID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodKSUID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function ksuid(params) {
		    return core._ksuid(exports.ZodKSUID, params);
		}
		exports.ZodIPv4 = core.$constructor("ZodIPv4", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodIPv4.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function ipv4(params) {
		    return core._ipv4(exports.ZodIPv4, params);
		}
		exports.ZodIPv6 = core.$constructor("ZodIPv6", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodIPv6.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function ipv6(params) {
		    return core._ipv6(exports.ZodIPv6, params);
		}
		exports.ZodCIDRv4 = core.$constructor("ZodCIDRv4", (inst, def) => {
		    core.$ZodCIDRv4.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function cidrv4(params) {
		    return core._cidrv4(exports.ZodCIDRv4, params);
		}
		exports.ZodCIDRv6 = core.$constructor("ZodCIDRv6", (inst, def) => {
		    core.$ZodCIDRv6.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function cidrv6(params) {
		    return core._cidrv6(exports.ZodCIDRv6, params);
		}
		exports.ZodBase64 = core.$constructor("ZodBase64", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodBase64.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function base64(params) {
		    return core._base64(exports.ZodBase64, params);
		}
		exports.ZodBase64URL = core.$constructor("ZodBase64URL", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodBase64URL.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function base64url(params) {
		    return core._base64url(exports.ZodBase64URL, params);
		}
		exports.ZodE164 = core.$constructor("ZodE164", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodE164.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function e164(params) {
		    return core._e164(exports.ZodE164, params);
		}
		exports.ZodJWT = core.$constructor("ZodJWT", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodJWT.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function jwt(params) {
		    return core._jwt(exports.ZodJWT, params);
		}
		exports.ZodCustomStringFormat = core.$constructor("ZodCustomStringFormat", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodCustomStringFormat.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function stringFormat(format, fnOrRegex, _params = {}) {
		    return core._stringFormat(exports.ZodCustomStringFormat, format, fnOrRegex, _params);
		}
		function hostname(_params) {
		    return core._stringFormat(exports.ZodCustomStringFormat, "hostname", core.regexes.hostname, _params);
		}
		function hex(_params) {
		    return core._stringFormat(exports.ZodCustomStringFormat, "hex", core.regexes.hex, _params);
		}
		function hash(alg, params) {
		    const enc = params?.enc ?? "hex";
		    const format = `${alg}_${enc}`;
		    const regex = core.regexes[format];
		    if (!regex)
		        throw new Error(`Unrecognized hash format: ${format}`);
		    return core._stringFormat(exports.ZodCustomStringFormat, format, regex, params);
		}
		exports.ZodNumber = core.$constructor("ZodNumber", (inst, def) => {
		    core.$ZodNumber.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.gt = (value, params) => inst.check(checks$1.gt(value, params));
		    inst.gte = (value, params) => inst.check(checks$1.gte(value, params));
		    inst.min = (value, params) => inst.check(checks$1.gte(value, params));
		    inst.lt = (value, params) => inst.check(checks$1.lt(value, params));
		    inst.lte = (value, params) => inst.check(checks$1.lte(value, params));
		    inst.max = (value, params) => inst.check(checks$1.lte(value, params));
		    inst.int = (params) => inst.check(int(params));
		    inst.safe = (params) => inst.check(int(params));
		    inst.positive = (params) => inst.check(checks$1.gt(0, params));
		    inst.nonnegative = (params) => inst.check(checks$1.gte(0, params));
		    inst.negative = (params) => inst.check(checks$1.lt(0, params));
		    inst.nonpositive = (params) => inst.check(checks$1.lte(0, params));
		    inst.multipleOf = (value, params) => inst.check(checks$1.multipleOf(value, params));
		    inst.step = (value, params) => inst.check(checks$1.multipleOf(value, params));
		    // inst.finite = (params) => inst.check(core.finite(params));
		    inst.finite = () => inst;
		    const bag = inst._zod.bag;
		    inst.minValue =
		        Math.max(bag.minimum ?? Number.NEGATIVE_INFINITY, bag.exclusiveMinimum ?? Number.NEGATIVE_INFINITY) ?? null;
		    inst.maxValue =
		        Math.min(bag.maximum ?? Number.POSITIVE_INFINITY, bag.exclusiveMaximum ?? Number.POSITIVE_INFINITY) ?? null;
		    inst.isInt = (bag.format ?? "").includes("int") || Number.isSafeInteger(bag.multipleOf ?? 0.5);
		    inst.isFinite = true;
		    inst.format = bag.format ?? null;
		});
		function number(params) {
		    return core._number(exports.ZodNumber, params);
		}
		exports.ZodNumberFormat = core.$constructor("ZodNumberFormat", (inst, def) => {
		    core.$ZodNumberFormat.init(inst, def);
		    exports.ZodNumber.init(inst, def);
		});
		function int(params) {
		    return core._int(exports.ZodNumberFormat, params);
		}
		function float32(params) {
		    return core._float32(exports.ZodNumberFormat, params);
		}
		function float64(params) {
		    return core._float64(exports.ZodNumberFormat, params);
		}
		function int32(params) {
		    return core._int32(exports.ZodNumberFormat, params);
		}
		function uint32(params) {
		    return core._uint32(exports.ZodNumberFormat, params);
		}
		exports.ZodBoolean = core.$constructor("ZodBoolean", (inst, def) => {
		    core.$ZodBoolean.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function boolean(params) {
		    return core._boolean(exports.ZodBoolean, params);
		}
		exports.ZodBigInt = core.$constructor("ZodBigInt", (inst, def) => {
		    core.$ZodBigInt.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.gte = (value, params) => inst.check(checks$1.gte(value, params));
		    inst.min = (value, params) => inst.check(checks$1.gte(value, params));
		    inst.gt = (value, params) => inst.check(checks$1.gt(value, params));
		    inst.gte = (value, params) => inst.check(checks$1.gte(value, params));
		    inst.min = (value, params) => inst.check(checks$1.gte(value, params));
		    inst.lt = (value, params) => inst.check(checks$1.lt(value, params));
		    inst.lte = (value, params) => inst.check(checks$1.lte(value, params));
		    inst.max = (value, params) => inst.check(checks$1.lte(value, params));
		    inst.positive = (params) => inst.check(checks$1.gt(BigInt(0), params));
		    inst.negative = (params) => inst.check(checks$1.lt(BigInt(0), params));
		    inst.nonpositive = (params) => inst.check(checks$1.lte(BigInt(0), params));
		    inst.nonnegative = (params) => inst.check(checks$1.gte(BigInt(0), params));
		    inst.multipleOf = (value, params) => inst.check(checks$1.multipleOf(value, params));
		    const bag = inst._zod.bag;
		    inst.minValue = bag.minimum ?? null;
		    inst.maxValue = bag.maximum ?? null;
		    inst.format = bag.format ?? null;
		});
		function bigint(params) {
		    return core._bigint(exports.ZodBigInt, params);
		}
		exports.ZodBigIntFormat = core.$constructor("ZodBigIntFormat", (inst, def) => {
		    core.$ZodBigIntFormat.init(inst, def);
		    exports.ZodBigInt.init(inst, def);
		});
		// int64
		function int64(params) {
		    return core._int64(exports.ZodBigIntFormat, params);
		}
		// uint64
		function uint64(params) {
		    return core._uint64(exports.ZodBigIntFormat, params);
		}
		exports.ZodSymbol = core.$constructor("ZodSymbol", (inst, def) => {
		    core.$ZodSymbol.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function symbol(params) {
		    return core._symbol(exports.ZodSymbol, params);
		}
		exports.ZodUndefined = core.$constructor("ZodUndefined", (inst, def) => {
		    core.$ZodUndefined.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function _undefined(params) {
		    return core._undefined(exports.ZodUndefined, params);
		}
		exports.ZodNull = core.$constructor("ZodNull", (inst, def) => {
		    core.$ZodNull.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function _null(params) {
		    return core._null(exports.ZodNull, params);
		}
		exports.ZodAny = core.$constructor("ZodAny", (inst, def) => {
		    core.$ZodAny.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function any() {
		    return core._any(exports.ZodAny);
		}
		exports.ZodUnknown = core.$constructor("ZodUnknown", (inst, def) => {
		    core.$ZodUnknown.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function unknown() {
		    return core._unknown(exports.ZodUnknown);
		}
		exports.ZodNever = core.$constructor("ZodNever", (inst, def) => {
		    core.$ZodNever.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function never(params) {
		    return core._never(exports.ZodNever, params);
		}
		exports.ZodVoid = core.$constructor("ZodVoid", (inst, def) => {
		    core.$ZodVoid.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function _void(params) {
		    return core._void(exports.ZodVoid, params);
		}
		exports.ZodDate = core.$constructor("ZodDate", (inst, def) => {
		    core.$ZodDate.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.min = (value, params) => inst.check(checks$1.gte(value, params));
		    inst.max = (value, params) => inst.check(checks$1.lte(value, params));
		    const c = inst._zod.bag;
		    inst.minDate = c.minimum ? new Date(c.minimum) : null;
		    inst.maxDate = c.maximum ? new Date(c.maximum) : null;
		});
		function date(params) {
		    return core._date(exports.ZodDate, params);
		}
		exports.ZodArray = core.$constructor("ZodArray", (inst, def) => {
		    core.$ZodArray.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.element = def.element;
		    inst.min = (minLength, params) => inst.check(checks$1.minLength(minLength, params));
		    inst.nonempty = (params) => inst.check(checks$1.minLength(1, params));
		    inst.max = (maxLength, params) => inst.check(checks$1.maxLength(maxLength, params));
		    inst.length = (len, params) => inst.check(checks$1.length(len, params));
		    inst.unwrap = () => inst.element;
		});
		function array(element, params) {
		    return core._array(exports.ZodArray, element, params);
		}
		// .keyof
		function keyof(schema) {
		    const shape = schema._zod.def.shape;
		    return _enum(Object.keys(shape));
		}
		exports.ZodObject = core.$constructor("ZodObject", (inst, def) => {
		    core.$ZodObjectJIT.init(inst, def);
		    exports.ZodType.init(inst, def);
		    index_js_1.util.defineLazy(inst, "shape", () => def.shape);
		    inst.keyof = () => _enum(Object.keys(inst._zod.def.shape));
		    inst.catchall = (catchall) => inst.clone({ ...inst._zod.def, catchall: catchall });
		    inst.passthrough = () => inst.clone({ ...inst._zod.def, catchall: unknown() });
		    inst.loose = () => inst.clone({ ...inst._zod.def, catchall: unknown() });
		    inst.strict = () => inst.clone({ ...inst._zod.def, catchall: never() });
		    inst.strip = () => inst.clone({ ...inst._zod.def, catchall: undefined });
		    inst.extend = (incoming) => {
		        return index_js_1.util.extend(inst, incoming);
		    };
		    inst.safeExtend = (incoming) => {
		        return index_js_1.util.safeExtend(inst, incoming);
		    };
		    inst.merge = (other) => index_js_1.util.merge(inst, other);
		    inst.pick = (mask) => index_js_1.util.pick(inst, mask);
		    inst.omit = (mask) => index_js_1.util.omit(inst, mask);
		    inst.partial = (...args) => index_js_1.util.partial(exports.ZodOptional, inst, args[0]);
		    inst.required = (...args) => index_js_1.util.required(exports.ZodNonOptional, inst, args[0]);
		});
		function object(shape, params) {
		    const def = {
		        type: "object",
		        get shape() {
		            index_js_1.util.assignProp(this, "shape", shape ? index_js_1.util.objectClone(shape) : {});
		            return this.shape;
		        },
		        ...index_js_1.util.normalizeParams(params),
		    };
		    return new exports.ZodObject(def);
		}
		// strictObject
		function strictObject(shape, params) {
		    return new exports.ZodObject({
		        type: "object",
		        get shape() {
		            index_js_1.util.assignProp(this, "shape", index_js_1.util.objectClone(shape));
		            return this.shape;
		        },
		        catchall: never(),
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		// looseObject
		function looseObject(shape, params) {
		    return new exports.ZodObject({
		        type: "object",
		        get shape() {
		            index_js_1.util.assignProp(this, "shape", index_js_1.util.objectClone(shape));
		            return this.shape;
		        },
		        catchall: unknown(),
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodUnion = core.$constructor("ZodUnion", (inst, def) => {
		    core.$ZodUnion.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.options = def.options;
		});
		function union(options, params) {
		    return new exports.ZodUnion({
		        type: "union",
		        options: options,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodDiscriminatedUnion = core.$constructor("ZodDiscriminatedUnion", (inst, def) => {
		    exports.ZodUnion.init(inst, def);
		    core.$ZodDiscriminatedUnion.init(inst, def);
		});
		function discriminatedUnion(discriminator, options, params) {
		    // const [options, params] = args;
		    return new exports.ZodDiscriminatedUnion({
		        type: "union",
		        options,
		        discriminator,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodIntersection = core.$constructor("ZodIntersection", (inst, def) => {
		    core.$ZodIntersection.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function intersection(left, right) {
		    return new exports.ZodIntersection({
		        type: "intersection",
		        left: left,
		        right: right,
		    });
		}
		exports.ZodTuple = core.$constructor("ZodTuple", (inst, def) => {
		    core.$ZodTuple.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.rest = (rest) => inst.clone({
		        ...inst._zod.def,
		        rest: rest,
		    });
		});
		function tuple(items, _paramsOrRest, _params) {
		    const hasRest = _paramsOrRest instanceof core.$ZodType;
		    const params = hasRest ? _params : _paramsOrRest;
		    const rest = hasRest ? _paramsOrRest : null;
		    return new exports.ZodTuple({
		        type: "tuple",
		        items: items,
		        rest,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodRecord = core.$constructor("ZodRecord", (inst, def) => {
		    core.$ZodRecord.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.keyType = def.keyType;
		    inst.valueType = def.valueType;
		});
		function record(keyType, valueType, params) {
		    return new exports.ZodRecord({
		        type: "record",
		        keyType,
		        valueType: valueType,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		// type alksjf = core.output<core.$ZodRecordKey>;
		function partialRecord(keyType, valueType, params) {
		    const k = core.clone(keyType);
		    k._zod.values = undefined;
		    return new exports.ZodRecord({
		        type: "record",
		        keyType: k,
		        valueType: valueType,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodMap = core.$constructor("ZodMap", (inst, def) => {
		    core.$ZodMap.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.keyType = def.keyType;
		    inst.valueType = def.valueType;
		});
		function map(keyType, valueType, params) {
		    return new exports.ZodMap({
		        type: "map",
		        keyType: keyType,
		        valueType: valueType,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodSet = core.$constructor("ZodSet", (inst, def) => {
		    core.$ZodSet.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.min = (...args) => inst.check(core._minSize(...args));
		    inst.nonempty = (params) => inst.check(core._minSize(1, params));
		    inst.max = (...args) => inst.check(core._maxSize(...args));
		    inst.size = (...args) => inst.check(core._size(...args));
		});
		function set(valueType, params) {
		    return new exports.ZodSet({
		        type: "set",
		        valueType: valueType,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodEnum = core.$constructor("ZodEnum", (inst, def) => {
		    core.$ZodEnum.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.enum = def.entries;
		    inst.options = Object.values(def.entries);
		    const keys = new Set(Object.keys(def.entries));
		    inst.extract = (values, params) => {
		        const newEntries = {};
		        for (const value of values) {
		            if (keys.has(value)) {
		                newEntries[value] = def.entries[value];
		            }
		            else
		                throw new Error(`Key ${value} not found in enum`);
		        }
		        return new exports.ZodEnum({
		            ...def,
		            checks: [],
		            ...index_js_1.util.normalizeParams(params),
		            entries: newEntries,
		        });
		    };
		    inst.exclude = (values, params) => {
		        const newEntries = { ...def.entries };
		        for (const value of values) {
		            if (keys.has(value)) {
		                delete newEntries[value];
		            }
		            else
		                throw new Error(`Key ${value} not found in enum`);
		        }
		        return new exports.ZodEnum({
		            ...def,
		            checks: [],
		            ...index_js_1.util.normalizeParams(params),
		            entries: newEntries,
		        });
		    };
		});
		function _enum(values, params) {
		    const entries = Array.isArray(values) ? Object.fromEntries(values.map((v) => [v, v])) : values;
		    return new exports.ZodEnum({
		        type: "enum",
		        entries,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		/** @deprecated This API has been merged into `z.enum()`. Use `z.enum()` instead.
		 *
		 * ```ts
		 * enum Colors { red, green, blue }
		 * z.enum(Colors);
		 * ```
		 */
		function nativeEnum(entries, params) {
		    return new exports.ZodEnum({
		        type: "enum",
		        entries,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodLiteral = core.$constructor("ZodLiteral", (inst, def) => {
		    core.$ZodLiteral.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.values = new Set(def.values);
		    Object.defineProperty(inst, "value", {
		        get() {
		            if (def.values.length > 1) {
		                throw new Error("This schema contains multiple valid literal values. Use `.values` instead.");
		            }
		            return def.values[0];
		        },
		    });
		});
		function literal(value, params) {
		    return new exports.ZodLiteral({
		        type: "literal",
		        values: Array.isArray(value) ? value : [value],
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodFile = core.$constructor("ZodFile", (inst, def) => {
		    core.$ZodFile.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.min = (size, params) => inst.check(core._minSize(size, params));
		    inst.max = (size, params) => inst.check(core._maxSize(size, params));
		    inst.mime = (types, params) => inst.check(core._mime(Array.isArray(types) ? types : [types], params));
		});
		function file(params) {
		    return core._file(exports.ZodFile, params);
		}
		exports.ZodTransform = core.$constructor("ZodTransform", (inst, def) => {
		    core.$ZodTransform.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.parse = (payload, _ctx) => {
		        if (_ctx.direction === "backward") {
		            throw new core.$ZodEncodeError(inst.constructor.name);
		        }
		        payload.addIssue = (issue) => {
		            if (typeof issue === "string") {
		                payload.issues.push(index_js_1.util.issue(issue, payload.value, def));
		            }
		            else {
		                // for Zod 3 backwards compatibility
		                const _issue = issue;
		                if (_issue.fatal)
		                    _issue.continue = false;
		                _issue.code ?? (_issue.code = "custom");
		                _issue.input ?? (_issue.input = payload.value);
		                _issue.inst ?? (_issue.inst = inst);
		                // _issue.continue ??= true;
		                payload.issues.push(index_js_1.util.issue(_issue));
		            }
		        };
		        const output = def.transform(payload.value, payload);
		        if (output instanceof Promise) {
		            return output.then((output) => {
		                payload.value = output;
		                return payload;
		            });
		        }
		        payload.value = output;
		        return payload;
		    };
		});
		function transform(fn) {
		    return new exports.ZodTransform({
		        type: "transform",
		        transform: fn,
		    });
		}
		exports.ZodOptional = core.$constructor("ZodOptional", (inst, def) => {
		    core.$ZodOptional.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function optional(innerType) {
		    return new exports.ZodOptional({
		        type: "optional",
		        innerType: innerType,
		    });
		}
		exports.ZodNullable = core.$constructor("ZodNullable", (inst, def) => {
		    core.$ZodNullable.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function nullable(innerType) {
		    return new exports.ZodNullable({
		        type: "nullable",
		        innerType: innerType,
		    });
		}
		// nullish
		function nullish(innerType) {
		    return optional(nullable(innerType));
		}
		exports.ZodDefault = core.$constructor("ZodDefault", (inst, def) => {
		    core.$ZodDefault.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.unwrap = () => inst._zod.def.innerType;
		    inst.removeDefault = inst.unwrap;
		});
		function _default(innerType, defaultValue) {
		    return new exports.ZodDefault({
		        type: "default",
		        innerType: innerType,
		        get defaultValue() {
		            return typeof defaultValue === "function" ? defaultValue() : index_js_1.util.shallowClone(defaultValue);
		        },
		    });
		}
		exports.ZodPrefault = core.$constructor("ZodPrefault", (inst, def) => {
		    core.$ZodPrefault.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function prefault(innerType, defaultValue) {
		    return new exports.ZodPrefault({
		        type: "prefault",
		        innerType: innerType,
		        get defaultValue() {
		            return typeof defaultValue === "function" ? defaultValue() : index_js_1.util.shallowClone(defaultValue);
		        },
		    });
		}
		exports.ZodNonOptional = core.$constructor("ZodNonOptional", (inst, def) => {
		    core.$ZodNonOptional.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function nonoptional(innerType, params) {
		    return new exports.ZodNonOptional({
		        type: "nonoptional",
		        innerType: innerType,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodSuccess = core.$constructor("ZodSuccess", (inst, def) => {
		    core.$ZodSuccess.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function success(innerType) {
		    return new exports.ZodSuccess({
		        type: "success",
		        innerType: innerType,
		    });
		}
		exports.ZodCatch = core.$constructor("ZodCatch", (inst, def) => {
		    core.$ZodCatch.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.unwrap = () => inst._zod.def.innerType;
		    inst.removeCatch = inst.unwrap;
		});
		function _catch(innerType, catchValue) {
		    return new exports.ZodCatch({
		        type: "catch",
		        innerType: innerType,
		        catchValue: (typeof catchValue === "function" ? catchValue : () => catchValue),
		    });
		}
		exports.ZodNaN = core.$constructor("ZodNaN", (inst, def) => {
		    core.$ZodNaN.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function nan(params) {
		    return core._nan(exports.ZodNaN, params);
		}
		exports.ZodPipe = core.$constructor("ZodPipe", (inst, def) => {
		    core.$ZodPipe.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.in = def.in;
		    inst.out = def.out;
		});
		function pipe(in_, out) {
		    return new exports.ZodPipe({
		        type: "pipe",
		        in: in_,
		        out: out,
		        // ...util.normalizeParams(params),
		    });
		}
		exports.ZodCodec = core.$constructor("ZodCodec", (inst, def) => {
		    exports.ZodPipe.init(inst, def);
		    core.$ZodCodec.init(inst, def);
		});
		function codec(in_, out, params) {
		    return new exports.ZodCodec({
		        type: "pipe",
		        in: in_,
		        out: out,
		        transform: params.decode,
		        reverseTransform: params.encode,
		    });
		}
		exports.ZodReadonly = core.$constructor("ZodReadonly", (inst, def) => {
		    core.$ZodReadonly.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function readonly(innerType) {
		    return new exports.ZodReadonly({
		        type: "readonly",
		        innerType: innerType,
		    });
		}
		exports.ZodTemplateLiteral = core.$constructor("ZodTemplateLiteral", (inst, def) => {
		    core.$ZodTemplateLiteral.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function templateLiteral(parts, params) {
		    return new exports.ZodTemplateLiteral({
		        type: "template_literal",
		        parts,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodLazy = core.$constructor("ZodLazy", (inst, def) => {
		    core.$ZodLazy.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.unwrap = () => inst._zod.def.getter();
		});
		function lazy(getter) {
		    return new exports.ZodLazy({
		        type: "lazy",
		        getter: getter,
		    });
		}
		exports.ZodPromise = core.$constructor("ZodPromise", (inst, def) => {
		    core.$ZodPromise.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function promise(innerType) {
		    return new exports.ZodPromise({
		        type: "promise",
		        innerType: innerType,
		    });
		}
		exports.ZodFunction = core.$constructor("ZodFunction", (inst, def) => {
		    core.$ZodFunction.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		function _function(params) {
		    return new exports.ZodFunction({
		        type: "function",
		        input: Array.isArray(params?.input) ? tuple(params?.input) : (params?.input ?? array(unknown())),
		        output: params?.output ?? unknown(),
		    });
		}
		exports.ZodCustom = core.$constructor("ZodCustom", (inst, def) => {
		    core.$ZodCustom.init(inst, def);
		    exports.ZodType.init(inst, def);
		});
		// custom checks
		function check(fn) {
		    const ch = new core.$ZodCheck({
		        check: "custom",
		        // ...util.normalizeParams(params),
		    });
		    ch._zod.check = fn;
		    return ch;
		}
		function custom(fn, _params) {
		    return core._custom(exports.ZodCustom, fn ?? (() => true), _params);
		}
		function refine(fn, _params = {}) {
		    return core._refine(exports.ZodCustom, fn, _params);
		}
		// superRefine
		function superRefine(fn) {
		    return core._superRefine(fn);
		}
		function _instanceof(cls, params = {
		    error: `Input not instance of ${cls.name}`,
		}) {
		    const inst = new exports.ZodCustom({
		        type: "custom",
		        check: "custom",
		        fn: (data) => data instanceof cls,
		        abort: true,
		        ...index_js_1.util.normalizeParams(params),
		    });
		    inst._zod.bag.Class = cls;
		    return inst;
		}
		// stringbool
		const stringbool = (...args) => core._stringbool({
		    Codec: exports.ZodCodec,
		    Boolean: exports.ZodBoolean,
		    String: exports.ZodString,
		}, ...args);
		exports.stringbool = stringbool;
		function json(params) {
		    const jsonSchema = lazy(() => {
		        return union([string(params), number(), boolean(), _null(), array(jsonSchema), record(string(), jsonSchema)]);
		    });
		    return jsonSchema;
		}
		// preprocess
		// /** @deprecated Use `z.pipe()` and `z.transform()` instead. */
		function preprocess(fn, schema) {
		    return pipe(transform(fn), schema);
		}
} (schemas$1));
	return schemas$1;
}

var compat = {};

(function (exports) {
	// Zod 3 compat layer
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.ZodFirstPartyTypeKind = exports.config = exports.$brand = exports.ZodIssueCode = void 0;
	exports.setErrorMap = setErrorMap;
	exports.getErrorMap = getErrorMap;
	const core = __importStar(core$4);
	/** @deprecated Use the raw string literal codes instead, e.g. "invalid_type". */
	exports.ZodIssueCode = {
	    invalid_type: "invalid_type",
	    too_big: "too_big",
	    too_small: "too_small",
	    invalid_format: "invalid_format",
	    not_multiple_of: "not_multiple_of",
	    unrecognized_keys: "unrecognized_keys",
	    invalid_union: "invalid_union",
	    invalid_key: "invalid_key",
	    invalid_element: "invalid_element",
	    invalid_value: "invalid_value",
	    custom: "custom",
	};
	var index_js_1 = core$4;
	Object.defineProperty(exports, "$brand", { enumerable: true, get: function () { return index_js_1.$brand; } });
	Object.defineProperty(exports, "config", { enumerable: true, get: function () { return index_js_1.config; } });
	/** @deprecated Use `z.config(params)` instead. */
	function setErrorMap(map) {
	    core.config({
	        customError: map,
	    });
	}
	/** @deprecated Use `z.config()` instead. */
	function getErrorMap() {
	    return core.config().customError;
	}
	/** @deprecated Do not use. Stub definition, only included for zod-to-json-schema compatibility. */
	var ZodFirstPartyTypeKind;
	(function (ZodFirstPartyTypeKind) {
	})(ZodFirstPartyTypeKind || (exports.ZodFirstPartyTypeKind = ZodFirstPartyTypeKind = {}));
} (compat));

var coerce = {};

var __createBinding$3 = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault$3 = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar$3 = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding$3(result, mod, k);
    __setModuleDefault$3(result, mod);
    return result;
};
Object.defineProperty(coerce, "__esModule", { value: true });
coerce.string = string;
coerce.number = number$2;
coerce.boolean = boolean;
coerce.bigint = bigint;
coerce.date = date$1;
const core = __importStar$3(core$4);
const schemas = __importStar$3(requireSchemas());
function string(params) {
    return core._coercedString(schemas.ZodString, params);
}
function number$2(params) {
    return core._coercedNumber(schemas.ZodNumber, params);
}
function boolean(params) {
    return core._coercedBoolean(schemas.ZodBoolean, params);
}
function bigint(params) {
    return core._coercedBigint(schemas.ZodBigInt, params);
}
function date$1(params) {
    return core._coercedDate(schemas.ZodDate, params);
}

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	var __exportStar = (commonjsGlobal && commonjsGlobal.__exportStar) || function(m, exports) {
	    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
	};
	var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
	    return (mod && mod.__esModule) ? mod : { "default": mod };
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.coerce = exports.iso = exports.ZodISODuration = exports.ZodISOTime = exports.ZodISODate = exports.ZodISODateTime = exports.locales = exports.NEVER = exports.util = exports.TimePrecision = exports.toJSONSchema = exports.flattenError = exports.formatError = exports.prettifyError = exports.treeifyError = exports.regexes = exports.clone = exports.$brand = exports.$input = exports.$output = exports.config = exports.registry = exports.globalRegistry = exports.core = void 0;
	exports.core = __importStar(core$4);
	__exportStar(requireSchemas(), exports);
	__exportStar(checks, exports);
	__exportStar(errors$2, exports);
	__exportStar(parse$2, exports);
	__exportStar(compat, exports);
	// zod-specified
	const index_js_1 = core$4;
	const en_js_1 = __importDefault(en);
	(0, index_js_1.config)((0, en_js_1.default)());
	var index_js_2 = core$4;
	Object.defineProperty(exports, "globalRegistry", { enumerable: true, get: function () { return index_js_2.globalRegistry; } });
	Object.defineProperty(exports, "registry", { enumerable: true, get: function () { return index_js_2.registry; } });
	Object.defineProperty(exports, "config", { enumerable: true, get: function () { return index_js_2.config; } });
	Object.defineProperty(exports, "$output", { enumerable: true, get: function () { return index_js_2.$output; } });
	Object.defineProperty(exports, "$input", { enumerable: true, get: function () { return index_js_2.$input; } });
	Object.defineProperty(exports, "$brand", { enumerable: true, get: function () { return index_js_2.$brand; } });
	Object.defineProperty(exports, "clone", { enumerable: true, get: function () { return index_js_2.clone; } });
	Object.defineProperty(exports, "regexes", { enumerable: true, get: function () { return index_js_2.regexes; } });
	Object.defineProperty(exports, "treeifyError", { enumerable: true, get: function () { return index_js_2.treeifyError; } });
	Object.defineProperty(exports, "prettifyError", { enumerable: true, get: function () { return index_js_2.prettifyError; } });
	Object.defineProperty(exports, "formatError", { enumerable: true, get: function () { return index_js_2.formatError; } });
	Object.defineProperty(exports, "flattenError", { enumerable: true, get: function () { return index_js_2.flattenError; } });
	Object.defineProperty(exports, "toJSONSchema", { enumerable: true, get: function () { return index_js_2.toJSONSchema; } });
	Object.defineProperty(exports, "TimePrecision", { enumerable: true, get: function () { return index_js_2.TimePrecision; } });
	Object.defineProperty(exports, "util", { enumerable: true, get: function () { return index_js_2.util; } });
	Object.defineProperty(exports, "NEVER", { enumerable: true, get: function () { return index_js_2.NEVER; } });
	exports.locales = __importStar(locales);
	// iso
	// must be exported from top-level
	// https://github.com/colinhacks/zod/issues/4491
	var iso_js_1 = requireIso();
	Object.defineProperty(exports, "ZodISODateTime", { enumerable: true, get: function () { return iso_js_1.ZodISODateTime; } });
	Object.defineProperty(exports, "ZodISODate", { enumerable: true, get: function () { return iso_js_1.ZodISODate; } });
	Object.defineProperty(exports, "ZodISOTime", { enumerable: true, get: function () { return iso_js_1.ZodISOTime; } });
	Object.defineProperty(exports, "ZodISODuration", { enumerable: true, get: function () { return iso_js_1.ZodISODuration; } });
	exports.iso = __importStar(requireIso());
	exports.coerce = __importStar(coerce);
} (external));

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	var __exportStar = (commonjsGlobal && commonjsGlobal.__exportStar) || function(m, exports) {
	    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.z = void 0;
	const z = __importStar(external);
	exports.z = z;
	__exportStar(external, exports);
	exports.default = z;
} (zod));

Object.defineProperty(periodName_enum, "__esModule", { value: true });
periodName_enum.periodNameSchema = periodName_enum.PeriodName = void 0;
const zod_1$9 = zod;
var PeriodName;
(function (PeriodName) {
    // Annual period
    PeriodName["A"] = "A";
    // Quarter periods
    PeriodName["Q1"] = "Q1";
    PeriodName["Q2"] = "Q2";
    PeriodName["Q3"] = "Q3";
    PeriodName["Q4"] = "Q4";
    // Half year periods
    PeriodName["H1"] = "H1";
    PeriodName["H2"] = "H2";
    // Month periods
    PeriodName["M1"] = "M1";
    PeriodName["M2"] = "M2";
    PeriodName["M3"] = "M3";
    PeriodName["M4"] = "M4";
    PeriodName["M5"] = "M5";
    PeriodName["M6"] = "M6";
    PeriodName["M7"] = "M7";
    PeriodName["M8"] = "M8";
    PeriodName["M9"] = "M9";
    PeriodName["M10"] = "M10";
    PeriodName["M11"] = "M11";
    PeriodName["M12"] = "M12";
    // YTD periods (examples are when fiscal year ends in December)
    PeriodName["YTD1"] = "YTD1";
    PeriodName["YTD2"] = "YTD2";
    PeriodName["YTD3"] = "YTD3";
    PeriodName["YTD4"] = "YTD4";
    PeriodName["YTD5"] = "YTD5";
    PeriodName["YTD6"] = "YTD6";
    PeriodName["YTD7"] = "YTD7";
    PeriodName["YTD8"] = "YTD8";
    PeriodName["YTD9"] = "YTD9";
    PeriodName["YTD10"] = "YTD10";
    PeriodName["YTD11"] = "YTD11";
    PeriodName["YTD12"] = "YTD12";
})(PeriodName || (periodName_enum.PeriodName = PeriodName = {}));
periodName_enum.periodNameSchema = zod_1$9.z.enum(PeriodName);

var selectedRdProjectCategory_enum = {};

Object.defineProperty(selectedRdProjectCategory_enum, "__esModule", { value: true });
selectedRdProjectCategory_enum.rdProjectCategoryLabel = selectedRdProjectCategory_enum.SelectedRdProjectCategory = void 0;
var SelectedRdProjectCategory;
(function (SelectedRdProjectCategory) {
    SelectedRdProjectCategory["RD_CREDIT"] = "RD_CREDIT";
    SelectedRdProjectCategory["SECTION_174_ONLY"] = "SECTION_174_ONLY";
    SelectedRdProjectCategory["EXCLUDED"] = "EXCLUDED";
})(SelectedRdProjectCategory || (selectedRdProjectCategory_enum.SelectedRdProjectCategory = SelectedRdProjectCategory = {}));
selectedRdProjectCategory_enum.rdProjectCategoryLabel = {
    [SelectedRdProjectCategory.RD_CREDIT]: 'R&D Credit',
    [SelectedRdProjectCategory.SECTION_174_ONLY]: '§174 Only',
    [SelectedRdProjectCategory.EXCLUDED]: 'Excluded',
};

var rdDocumentationStatus_enum = {};

Object.defineProperty(rdDocumentationStatus_enum, "__esModule", { value: true });
rdDocumentationStatus_enum.rdDocumentationStatusLabel = rdDocumentationStatus_enum.RdDocumentationStatus = void 0;
var RdDocumentationStatus;
(function (RdDocumentationStatus) {
    RdDocumentationStatus["COMPLETE"] = "COMPLETE";
    RdDocumentationStatus["INCOMPLETE"] = "INCOMPLETE";
    RdDocumentationStatus["NOT_APPLICABLE"] = "NOT_APPLICABLE";
})(RdDocumentationStatus || (rdDocumentationStatus_enum.RdDocumentationStatus = RdDocumentationStatus = {}));
rdDocumentationStatus_enum.rdDocumentationStatusLabel = {
    [RdDocumentationStatus.COMPLETE]: 'Complete',
    [RdDocumentationStatus.INCOMPLETE]: 'Incomplete',
    [RdDocumentationStatus.NOT_APPLICABLE]: 'Not Applicable',
};

var selectedSredProjectCategory_enum = {};

Object.defineProperty(selectedSredProjectCategory_enum, "__esModule", { value: true });
selectedSredProjectCategory_enum.sredProjectCategoryLabel = selectedSredProjectCategory_enum.SelectedSredProjectCategory = void 0;
var SelectedSredProjectCategory;
(function (SelectedSredProjectCategory) {
    SelectedSredProjectCategory["SRED_CREDIT"] = "SRED_CREDIT";
    SelectedSredProjectCategory["NOT_STARTED_OR_NONE"] = "NOT_STARTED_OR_NONE";
})(SelectedSredProjectCategory || (selectedSredProjectCategory_enum.SelectedSredProjectCategory = SelectedSredProjectCategory = {}));
selectedSredProjectCategory_enum.sredProjectCategoryLabel = {
    [SelectedSredProjectCategory.SRED_CREDIT]: 'SR&ED Credit',
    [SelectedSredProjectCategory.NOT_STARTED_OR_NONE]: 'Not Started / None',
};

var sredDocumentationStatus_enum = {};

Object.defineProperty(sredDocumentationStatus_enum, "__esModule", { value: true });
sredDocumentationStatus_enum.sredDocumentationStatusLabel = sredDocumentationStatus_enum.SredDocumentationStatus = void 0;
var SredDocumentationStatus;
(function (SredDocumentationStatus) {
    SredDocumentationStatus["COMPLETE"] = "COMPLETE";
    SredDocumentationStatus["INCOMPLETE"] = "INCOMPLETE";
    SredDocumentationStatus["NOT_APPLICABLE"] = "NOT_APPLICABLE";
})(SredDocumentationStatus || (sredDocumentationStatus_enum.SredDocumentationStatus = SredDocumentationStatus = {}));
sredDocumentationStatus_enum.sredDocumentationStatusLabel = {
    [SredDocumentationStatus.COMPLETE]: 'Complete',
    [SredDocumentationStatus.INCOMPLETE]: 'Incomplete',
    [SredDocumentationStatus.NOT_APPLICABLE]: 'Not Applicable',
};

var predictedCapitalizationStatus_enum = {};

Object.defineProperty(predictedCapitalizationStatus_enum, "__esModule", { value: true });
predictedCapitalizationStatus_enum.PREDICTED_CAPITALIZATION_STATUS_LABELS = predictedCapitalizationStatus_enum.PredictedCapitalizationStatus = void 0;
var PredictedCapitalizationStatus;
(function (PredictedCapitalizationStatus) {
    PredictedCapitalizationStatus["CAPITALIZABLE"] = "CAPITALIZABLE";
    PredictedCapitalizationStatus["NOT_CAPITALIZABLE"] = "NOT_CAPITALIZABLE";
    PredictedCapitalizationStatus["UNCERTAIN"] = "UNCERTAIN";
})(PredictedCapitalizationStatus || (predictedCapitalizationStatus_enum.PredictedCapitalizationStatus = PredictedCapitalizationStatus = {}));
predictedCapitalizationStatus_enum.PREDICTED_CAPITALIZATION_STATUS_LABELS = {
    [PredictedCapitalizationStatus.CAPITALIZABLE]: 'Capitalizable',
    [PredictedCapitalizationStatus.NOT_CAPITALIZABLE]: 'Not Capitalizable',
    [PredictedCapitalizationStatus.UNCERTAIN]: 'Uncertain',
};

var finchScopeType_enum = {};

var __importDefault$6 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(finchScopeType_enum, "__esModule", { value: true });
finchScopeType_enum.FinchScopeTypeSchema = finchScopeType_enum.FinchScopeType = void 0;
const zod_1$8 = __importDefault$6(zod);
var FinchScopeType;
(function (FinchScopeType) {
    FinchScopeType["HR_ONLY"] = "HR_ONLY";
    FinchScopeType["FULL_PAYROLL"] = "FULL_PAYROLL";
})(FinchScopeType || (finchScopeType_enum.FinchScopeType = FinchScopeType = {}));
finchScopeType_enum.FinchScopeTypeSchema = zod_1$8.default.enum([FinchScopeType.HR_ONLY, FinchScopeType.FULL_PAYROLL]);

var productType_enum = {};

Object.defineProperty(productType_enum, "__esModule", { value: true });
productType_enum.ProductType = void 0;
var ProductType;
(function (ProductType) {
    ProductType["US_RD"] = "US_RD";
    ProductType["US_ASC"] = "US_ASC";
    ProductType["CA_SRED"] = "CA_SRED";
})(ProductType || (productType_enum.ProductType = ProductType = {}));

var authErrorCode_enum = {};

Object.defineProperty(authErrorCode_enum, "__esModule", { value: true });
authErrorCode_enum.AuthErrorCode = void 0;
/**
 * Error codes surfaced on `ExtendedGraphQLErrors.extensions.errors[*].code` by
 * auth-related mutations and guards. Defined in `@common/neo.tax` so server
 * throws and frontend branches reference the exact same strings — prevents
 * typo drift between ends.
 *
 * Add a new member here whenever the backend starts throwing a new coded auth
 * error; referenced places will break at compile time and point you at every
 * caller that needs to handle it.
 */
var AuthErrorCode;
(function (AuthErrorCode) {
    AuthErrorCode["EMAIL_NOT_VERIFIED"] = "EMAIL_NOT_VERIFIED";
    /** The organization admits only its enterprise SSO provider, and this session signed in some other way. */
    AuthErrorCode["ENTERPRISE_SSO_REQUIRED"] = "ENTERPRISE_SSO_REQUIRED";
    AuthErrorCode["EMAIL_VERIFICATION_TOO_FREQUENT"] = "EMAIL_VERIFICATION_TOO_FREQUENT";
    AuthErrorCode["EMAIL_VERIFICATION_TOO_MANY_ATTEMPTS_TRY_LATER"] = "EMAIL_VERIFICATION_TOO_MANY_ATTEMPTS_TRY_LATER";
    AuthErrorCode["INVALID_CONTINUE_URL"] = "INVALID_CONTINUE_URL";
})(AuthErrorCode || (authErrorCode_enum.AuthErrorCode = AuthErrorCode = {}));

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.AuthErrorCode = exports.ProductType = exports.FinchScopeTypeSchema = exports.FinchScopeType = exports.PREDICTED_CAPITALIZATION_STATUS_LABELS = exports.PredictedCapitalizationStatus = exports.sredDocumentationStatusLabel = exports.SredDocumentationStatus = exports.sredProjectCategoryLabel = exports.SelectedSredProjectCategory = exports.rdDocumentationStatusLabel = exports.RdDocumentationStatus = exports.rdProjectCategoryLabel = exports.SelectedRdProjectCategory = exports.periodNameSchema = exports.PeriodName = exports.PayrollMonthExpenseProvider = exports.DayOfWeek = exports.ErrorStatusCode = exports.ValueType = exports.CompanyEntityType = exports.EligibilityStatus = exports.CorporationType = exports.ServiceOperationalStatus = exports.WageType = exports.PayrollProvider = exports.AccountingProvider = exports.PayrollAcquisitionMethod = exports.DateMonth = exports.AccountingAcquisitionMethod = exports.EntityType = exports.SalesChannel = exports.getRevenueSourceRank = exports.RevenueSource = exports.HardSciences = exports.RoundingMethod = exports.AccountType = exports.AccountClassification = exports.ServiceScope = exports.NeoTaxEnvironment = exports.SsoError = exports.SsoQueryField = exports.GitHubAppInstallStatus = exports.GitHubAppInstallQueryField = exports.QuickbooksMonth = exports.QuickbooksConnectionStatus = exports.QuickbooksQueryField = exports.convertExpenseCategoryToNum = exports.ExpenseCategory = exports.AnalyticIdentifier = void 0;
	var analytic_identifier_enum_1 = analyticIdentifier_enum;
	Object.defineProperty(exports, "AnalyticIdentifier", { enumerable: true, get: function () { return analytic_identifier_enum_1.AnalyticIdentifier; } });
	var expense_category_enums_1 = expenseCategory_enums;
	Object.defineProperty(exports, "ExpenseCategory", { enumerable: true, get: function () { return expense_category_enums_1.ExpenseCategory; } });
	var enums_utils_1 = enums_utils;
	Object.defineProperty(exports, "convertExpenseCategoryToNum", { enumerable: true, get: function () { return enums_utils_1.convertExpenseCategoryToNum; } });
	var quickbooks_1 = quickbooks;
	Object.defineProperty(exports, "QuickbooksQueryField", { enumerable: true, get: function () { return quickbooks_1.QuickbooksQueryField; } });
	Object.defineProperty(exports, "QuickbooksConnectionStatus", { enumerable: true, get: function () { return quickbooks_1.QuickbooksConnectionStatus; } });
	Object.defineProperty(exports, "QuickbooksMonth", { enumerable: true, get: function () { return quickbooks_1.QuickbooksMonth; } });
	var github_app_1 = githubApp;
	Object.defineProperty(exports, "GitHubAppInstallQueryField", { enumerable: true, get: function () { return github_app_1.GitHubAppInstallQueryField; } });
	Object.defineProperty(exports, "GitHubAppInstallStatus", { enumerable: true, get: function () { return github_app_1.GitHubAppInstallStatus; } });
	var sso_1 = sso;
	Object.defineProperty(exports, "SsoQueryField", { enumerable: true, get: function () { return sso_1.SsoQueryField; } });
	Object.defineProperty(exports, "SsoError", { enumerable: true, get: function () { return sso_1.SsoError; } });
	var neo_tax_environment_enum_1 = neoTaxEnvironment_enum;
	Object.defineProperty(exports, "NeoTaxEnvironment", { enumerable: true, get: function () { return neo_tax_environment_enum_1.NeoTaxEnvironment; } });
	var service_scope_enum_1 = serviceScope_enum;
	Object.defineProperty(exports, "ServiceScope", { enumerable: true, get: function () { return service_scope_enum_1.ServiceScope; } });
	var account_classification_enum_1 = accountClassification_enum;
	Object.defineProperty(exports, "AccountClassification", { enumerable: true, get: function () { return account_classification_enum_1.AccountClassification; } });
	var account_type_enum_1 = accountType_enum;
	Object.defineProperty(exports, "AccountType", { enumerable: true, get: function () { return account_type_enum_1.AccountType; } });
	var rounding_method_enum_1 = roundingMethod_enum;
	Object.defineProperty(exports, "RoundingMethod", { enumerable: true, get: function () { return rounding_method_enum_1.RoundingMethod; } });
	var hard_sciences_enum_1 = hardSciences_enum;
	Object.defineProperty(exports, "HardSciences", { enumerable: true, get: function () { return hard_sciences_enum_1.HardSciences; } });
	var revenue_source_enum_1 = revenueSource_enum;
	Object.defineProperty(exports, "RevenueSource", { enumerable: true, get: function () { return revenue_source_enum_1.RevenueSource; } });
	Object.defineProperty(exports, "getRevenueSourceRank", { enumerable: true, get: function () { return revenue_source_enum_1.getRevenueSourceRank; } });
	var sales_channel_enum_1 = salesChannel_enum;
	Object.defineProperty(exports, "SalesChannel", { enumerable: true, get: function () { return sales_channel_enum_1.SalesChannel; } });
	var entity_type_enum_1 = entityType_enum;
	Object.defineProperty(exports, "EntityType", { enumerable: true, get: function () { return entity_type_enum_1.EntityType; } });
	var accounting_acquisition_method_enum_1 = accountingAcquisitionMethod_enum;
	Object.defineProperty(exports, "AccountingAcquisitionMethod", { enumerable: true, get: function () { return accounting_acquisition_method_enum_1.AccountingAcquisitionMethod; } });
	var date_month_enum_1 = dateMonth_enum;
	Object.defineProperty(exports, "DateMonth", { enumerable: true, get: function () { return date_month_enum_1.DateMonth; } });
	var payroll_acquisition_method_enum_1 = payrollAcquisitionMethod_enum;
	Object.defineProperty(exports, "PayrollAcquisitionMethod", { enumerable: true, get: function () { return payroll_acquisition_method_enum_1.PayrollAcquisitionMethod; } });
	var accounting_provider_enum_1 = accountingProvider_enum;
	Object.defineProperty(exports, "AccountingProvider", { enumerable: true, get: function () { return accounting_provider_enum_1.AccountingProvider; } });
	var payroll_provider_enum_1 = payrollProvider_enum;
	Object.defineProperty(exports, "PayrollProvider", { enumerable: true, get: function () { return payroll_provider_enum_1.PayrollProvider; } });
	var wage_type_enum_1 = wageType_enum;
	Object.defineProperty(exports, "WageType", { enumerable: true, get: function () { return wage_type_enum_1.WageType; } });
	var service_operational_status_enum_1 = serviceOperationalStatus_enum;
	Object.defineProperty(exports, "ServiceOperationalStatus", { enumerable: true, get: function () { return service_operational_status_enum_1.ServiceOperationalStatus; } });
	var corporation_type_enum_1 = corporationType_enum;
	Object.defineProperty(exports, "CorporationType", { enumerable: true, get: function () { return corporation_type_enum_1.CorporationType; } });
	var eligibility_status_enum_1 = eligibilityStatus_enum;
	Object.defineProperty(exports, "EligibilityStatus", { enumerable: true, get: function () { return eligibility_status_enum_1.EligibilityStatus; } });
	var company_entity_type_enum_1 = companyEntityType_enum;
	Object.defineProperty(exports, "CompanyEntityType", { enumerable: true, get: function () { return company_entity_type_enum_1.CompanyEntityType; } });
	var value_type_enum_1 = valueType_enum;
	Object.defineProperty(exports, "ValueType", { enumerable: true, get: function () { return value_type_enum_1.ValueType; } });
	var error_status_code_1 = errorStatusCode;
	Object.defineProperty(exports, "ErrorStatusCode", { enumerable: true, get: function () { return error_status_code_1.ErrorStatusCode; } });
	var day_of_week_enum_1 = dayOfWeek_enum;
	Object.defineProperty(exports, "DayOfWeek", { enumerable: true, get: function () { return day_of_week_enum_1.DayOfWeek; } });
	var payroll_month_expense_provider_enum_1 = payrollMonthExpenseProvider_enum;
	Object.defineProperty(exports, "PayrollMonthExpenseProvider", { enumerable: true, get: function () { return payroll_month_expense_provider_enum_1.PayrollMonthExpenseProvider; } });
	var period_name_enum_1 = periodName_enum;
	Object.defineProperty(exports, "PeriodName", { enumerable: true, get: function () { return period_name_enum_1.PeriodName; } });
	Object.defineProperty(exports, "periodNameSchema", { enumerable: true, get: function () { return period_name_enum_1.periodNameSchema; } });
	var selected_rd_project_category_enum_1 = selectedRdProjectCategory_enum;
	Object.defineProperty(exports, "SelectedRdProjectCategory", { enumerable: true, get: function () { return selected_rd_project_category_enum_1.SelectedRdProjectCategory; } });
	Object.defineProperty(exports, "rdProjectCategoryLabel", { enumerable: true, get: function () { return selected_rd_project_category_enum_1.rdProjectCategoryLabel; } });
	var rd_documentation_status_enum_1 = rdDocumentationStatus_enum;
	Object.defineProperty(exports, "RdDocumentationStatus", { enumerable: true, get: function () { return rd_documentation_status_enum_1.RdDocumentationStatus; } });
	Object.defineProperty(exports, "rdDocumentationStatusLabel", { enumerable: true, get: function () { return rd_documentation_status_enum_1.rdDocumentationStatusLabel; } });
	var selected_sred_project_category_enum_1 = selectedSredProjectCategory_enum;
	Object.defineProperty(exports, "SelectedSredProjectCategory", { enumerable: true, get: function () { return selected_sred_project_category_enum_1.SelectedSredProjectCategory; } });
	Object.defineProperty(exports, "sredProjectCategoryLabel", { enumerable: true, get: function () { return selected_sred_project_category_enum_1.sredProjectCategoryLabel; } });
	var sred_documentation_status_enum_1 = sredDocumentationStatus_enum;
	Object.defineProperty(exports, "SredDocumentationStatus", { enumerable: true, get: function () { return sred_documentation_status_enum_1.SredDocumentationStatus; } });
	Object.defineProperty(exports, "sredDocumentationStatusLabel", { enumerable: true, get: function () { return sred_documentation_status_enum_1.sredDocumentationStatusLabel; } });
	var predicted_capitalization_status_enum_1 = predictedCapitalizationStatus_enum;
	Object.defineProperty(exports, "PredictedCapitalizationStatus", { enumerable: true, get: function () { return predicted_capitalization_status_enum_1.PredictedCapitalizationStatus; } });
	Object.defineProperty(exports, "PREDICTED_CAPITALIZATION_STATUS_LABELS", { enumerable: true, get: function () { return predicted_capitalization_status_enum_1.PREDICTED_CAPITALIZATION_STATUS_LABELS; } });
	var finch_scope_type_enum_1 = finchScopeType_enum;
	Object.defineProperty(exports, "FinchScopeType", { enumerable: true, get: function () { return finch_scope_type_enum_1.FinchScopeType; } });
	Object.defineProperty(exports, "FinchScopeTypeSchema", { enumerable: true, get: function () { return finch_scope_type_enum_1.FinchScopeTypeSchema; } });
	var product_type_enum_1 = productType_enum;
	Object.defineProperty(exports, "ProductType", { enumerable: true, get: function () { return product_type_enum_1.ProductType; } });
	var auth_error_code_enum_1 = authErrorCode_enum;
	Object.defineProperty(exports, "AuthErrorCode", { enumerable: true, get: function () { return auth_error_code_enum_1.AuthErrorCode; } });
} (enums));

var errors$1 = {};

// Note: This file is autogenerated using "resources/gen-version.js" script and
// automatically updated by "npm version" command.

/**
 * A string containing the version of the GraphQL.js library
 */
const version$1 = '16.8.1';
/**
 * An object containing the components of the GraphQL.js version string
 */

const versionInfo = Object.freeze({
  major: 16,
  minor: 8,
  patch: 1,
  preReleaseTag: null,
});

function devAssert(condition, message) {
  const booleanCondition = Boolean(condition);

  if (!booleanCondition) {
    throw new Error(message);
  }
}

/**
 * Returns true if the value acts like a Promise, i.e. has a "then" function,
 * otherwise returns false.
 */
function isPromise(value) {
  return (
    typeof (value === null || value === void 0 ? void 0 : value.then) ===
    'function'
  );
}

/**
 * Return true if `value` is object-like. A value is object-like if it's not
 * `null` and has a `typeof` result of "object".
 */
function isObjectLike$c(value) {
  return typeof value == 'object' && value !== null;
}

function invariant(condition, message) {
  const booleanCondition = Boolean(condition);

  if (!booleanCondition) {
    throw new Error(
      message != null ? message : 'Unexpected invariant triggered.',
    );
  }
}

const LineRegExp = /\r\n|[\n\r]/g;
/**
 * Represents a location in a Source.
 */

/**
 * Takes a Source and a UTF-8 character offset, and returns the corresponding
 * line and column as a SourceLocation.
 */
function getLocation(source, position) {
  let lastLineStart = 0;
  let line = 1;

  for (const match of source.body.matchAll(LineRegExp)) {
    typeof match.index === 'number' || invariant(false);

    if (match.index >= position) {
      break;
    }

    lastLineStart = match.index + match[0].length;
    line += 1;
  }

  return {
    line,
    column: position + 1 - lastLineStart,
  };
}

/**
 * Render a helpful description of the location in the GraphQL Source document.
 */
function printLocation(location) {
  return printSourceLocation(
    location.source,
    getLocation(location.source, location.start),
  );
}
/**
 * Render a helpful description of the location in the GraphQL Source document.
 */

function printSourceLocation(source, sourceLocation) {
  const firstLineColumnOffset = source.locationOffset.column - 1;
  const body = ''.padStart(firstLineColumnOffset) + source.body;
  const lineIndex = sourceLocation.line - 1;
  const lineOffset = source.locationOffset.line - 1;
  const lineNum = sourceLocation.line + lineOffset;
  const columnOffset = sourceLocation.line === 1 ? firstLineColumnOffset : 0;
  const columnNum = sourceLocation.column + columnOffset;
  const locationStr = `${source.name}:${lineNum}:${columnNum}\n`;
  const lines = body.split(/\r\n|[\n\r]/g);
  const locationLine = lines[lineIndex]; // Special case for minified documents

  if (locationLine.length > 120) {
    const subLineIndex = Math.floor(columnNum / 80);
    const subLineColumnNum = columnNum % 80;
    const subLines = [];

    for (let i = 0; i < locationLine.length; i += 80) {
      subLines.push(locationLine.slice(i, i + 80));
    }

    return (
      locationStr +
      printPrefixedLines([
        [`${lineNum} |`, subLines[0]],
        ...subLines.slice(1, subLineIndex + 1).map((subLine) => ['|', subLine]),
        ['|', '^'.padStart(subLineColumnNum)],
        ['|', subLines[subLineIndex + 1]],
      ])
    );
  }

  return (
    locationStr +
    printPrefixedLines([
      // Lines specified like this: ["prefix", "string"],
      [`${lineNum - 1} |`, lines[lineIndex - 1]],
      [`${lineNum} |`, locationLine],
      ['|', '^'.padStart(columnNum)],
      [`${lineNum + 1} |`, lines[lineIndex + 1]],
    ])
  );
}

function printPrefixedLines(lines) {
  const existingLines = lines.filter(([_, line]) => line !== undefined);
  const padLen = Math.max(...existingLines.map(([prefix]) => prefix.length));
  return existingLines
    .map(([prefix, line]) => prefix.padStart(padLen) + (line ? ' ' + line : ''))
    .join('\n');
}

function toNormalizedOptions(args) {
  const firstArg = args[0];

  if (firstArg == null || 'kind' in firstArg || 'length' in firstArg) {
    return {
      nodes: firstArg,
      source: args[1],
      positions: args[2],
      path: args[3],
      originalError: args[4],
      extensions: args[5],
    };
  }

  return firstArg;
}
/**
 * A GraphQLError describes an Error found during the parse, validate, or
 * execute phases of performing a GraphQL operation. In addition to a message
 * and stack trace, it also includes information about the locations in a
 * GraphQL document and/or execution result that correspond to the Error.
 */

class GraphQLError extends Error {
  /**
   * An array of `{ line, column }` locations within the source GraphQL document
   * which correspond to this error.
   *
   * Errors during validation often contain multiple locations, for example to
   * point out two things with the same name. Errors during execution include a
   * single location, the field which produced the error.
   *
   * Enumerable, and appears in the result of JSON.stringify().
   */

  /**
   * An array describing the JSON-path into the execution response which
   * corresponds to this error. Only included for errors during execution.
   *
   * Enumerable, and appears in the result of JSON.stringify().
   */

  /**
   * An array of GraphQL AST Nodes corresponding to this error.
   */

  /**
   * The source GraphQL document for the first location of this error.
   *
   * Note that if this Error represents more than one node, the source may not
   * represent nodes after the first node.
   */

  /**
   * An array of character offsets within the source GraphQL document
   * which correspond to this error.
   */

  /**
   * The original error thrown from a field resolver during execution.
   */

  /**
   * Extension fields to add to the formatted error.
   */

  /**
   * @deprecated Please use the `GraphQLErrorOptions` constructor overload instead.
   */
  constructor(message, ...rawArgs) {
    var _this$nodes, _nodeLocations$, _ref;

    const { nodes, source, positions, path, originalError, extensions } =
      toNormalizedOptions(rawArgs);
    super(message);
    this.name = 'GraphQLError';
    this.path = path !== null && path !== void 0 ? path : undefined;
    this.originalError =
      originalError !== null && originalError !== void 0
        ? originalError
        : undefined; // Compute list of blame nodes.

    this.nodes = undefinedIfEmpty(
      Array.isArray(nodes) ? nodes : nodes ? [nodes] : undefined,
    );
    const nodeLocations = undefinedIfEmpty(
      (_this$nodes = this.nodes) === null || _this$nodes === void 0
        ? void 0
        : _this$nodes.map((node) => node.loc).filter((loc) => loc != null),
    ); // Compute locations in the source for the given nodes/positions.

    this.source =
      source !== null && source !== void 0
        ? source
        : nodeLocations === null || nodeLocations === void 0
        ? void 0
        : (_nodeLocations$ = nodeLocations[0]) === null ||
          _nodeLocations$ === void 0
        ? void 0
        : _nodeLocations$.source;
    this.positions =
      positions !== null && positions !== void 0
        ? positions
        : nodeLocations === null || nodeLocations === void 0
        ? void 0
        : nodeLocations.map((loc) => loc.start);
    this.locations =
      positions && source
        ? positions.map((pos) => getLocation(source, pos))
        : nodeLocations === null || nodeLocations === void 0
        ? void 0
        : nodeLocations.map((loc) => getLocation(loc.source, loc.start));
    const originalExtensions = isObjectLike$c(
      originalError === null || originalError === void 0
        ? void 0
        : originalError.extensions,
    )
      ? originalError === null || originalError === void 0
        ? void 0
        : originalError.extensions
      : undefined;
    this.extensions =
      (_ref =
        extensions !== null && extensions !== void 0
          ? extensions
          : originalExtensions) !== null && _ref !== void 0
        ? _ref
        : Object.create(null); // Only properties prescribed by the spec should be enumerable.
    // Keep the rest as non-enumerable.

    Object.defineProperties(this, {
      message: {
        writable: true,
        enumerable: true,
      },
      name: {
        enumerable: false,
      },
      nodes: {
        enumerable: false,
      },
      source: {
        enumerable: false,
      },
      positions: {
        enumerable: false,
      },
      originalError: {
        enumerable: false,
      },
    }); // Include (non-enumerable) stack trace.

    /* c8 ignore start */
    // FIXME: https://github.com/graphql/graphql-js/issues/2317

    if (
      originalError !== null &&
      originalError !== void 0 &&
      originalError.stack
    ) {
      Object.defineProperty(this, 'stack', {
        value: originalError.stack,
        writable: true,
        configurable: true,
      });
    } else if (Error.captureStackTrace) {
      Error.captureStackTrace(this, GraphQLError);
    } else {
      Object.defineProperty(this, 'stack', {
        value: Error().stack,
        writable: true,
        configurable: true,
      });
    }
    /* c8 ignore stop */
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLError';
  }

  toString() {
    let output = this.message;

    if (this.nodes) {
      for (const node of this.nodes) {
        if (node.loc) {
          output += '\n\n' + printLocation(node.loc);
        }
      }
    } else if (this.source && this.locations) {
      for (const location of this.locations) {
        output += '\n\n' + printSourceLocation(this.source, location);
      }
    }

    return output;
  }

  toJSON() {
    const formattedError = {
      message: this.message,
    };

    if (this.locations != null) {
      formattedError.locations = this.locations;
    }

    if (this.path != null) {
      formattedError.path = this.path;
    }

    if (this.extensions != null && Object.keys(this.extensions).length > 0) {
      formattedError.extensions = this.extensions;
    }

    return formattedError;
  }
}

function undefinedIfEmpty(array) {
  return array === undefined || array.length === 0 ? undefined : array;
}
/**
 * See: https://spec.graphql.org/draft/#sec-Errors
 */

/**
 * Prints a GraphQLError to a string, representing useful location information
 * about the error's position in the source.
 *
 * @deprecated Please use `error.toString` instead. Will be removed in v17
 */
function printError(error) {
  return error.toString();
}
/**
 * Given a GraphQLError, format it according to the rules described by the
 * Response Format, Errors section of the GraphQL Specification.
 *
 * @deprecated Please use `error.toJSON` instead. Will be removed in v17
 */

function formatError(error) {
  return error.toJSON();
}

/**
 * Produces a GraphQLError representing a syntax error, containing useful
 * descriptive information about the syntax error's position in the source.
 */

function syntaxError(source, position, description) {
  return new GraphQLError(`Syntax Error: ${description}`, {
    source,
    positions: [position],
  });
}

/**
 * Contains a range of UTF-8 character offsets and token references that
 * identify the region of the source from which the AST derived.
 */
class Location {
  /**
   * The character offset at which this Node begins.
   */

  /**
   * The character offset at which this Node ends.
   */

  /**
   * The Token at which this Node begins.
   */

  /**
   * The Token at which this Node ends.
   */

  /**
   * The Source document the AST represents.
   */
  constructor(startToken, endToken, source) {
    this.start = startToken.start;
    this.end = endToken.end;
    this.startToken = startToken;
    this.endToken = endToken;
    this.source = source;
  }

  get [Symbol.toStringTag]() {
    return 'Location';
  }

  toJSON() {
    return {
      start: this.start,
      end: this.end,
    };
  }
}
/**
 * Represents a range of characters represented by a lexical token
 * within a Source.
 */

class Token {
  /**
   * The kind of Token.
   */

  /**
   * The character offset at which this Node begins.
   */

  /**
   * The character offset at which this Node ends.
   */

  /**
   * The 1-indexed line number on which this Token appears.
   */

  /**
   * The 1-indexed column number at which this Token begins.
   */

  /**
   * For non-punctuation tokens, represents the interpreted value of the token.
   *
   * Note: is undefined for punctuation tokens, but typed as string for
   * convenience in the parser.
   */

  /**
   * Tokens exist as nodes in a double-linked-list amongst all tokens
   * including ignored tokens. <SOF> is always the first node and <EOF>
   * the last.
   */
  constructor(kind, start, end, line, column, value) {
    this.kind = kind;
    this.start = start;
    this.end = end;
    this.line = line;
    this.column = column; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

    this.value = value;
    this.prev = null;
    this.next = null;
  }

  get [Symbol.toStringTag]() {
    return 'Token';
  }

  toJSON() {
    return {
      kind: this.kind,
      value: this.value,
      line: this.line,
      column: this.column,
    };
  }
}
/**
 * The list of all possible AST node types.
 */

/**
 * @internal
 */
const QueryDocumentKeys = {
  Name: [],
  Document: ['definitions'],
  OperationDefinition: [
    'name',
    'variableDefinitions',
    'directives',
    'selectionSet',
  ],
  VariableDefinition: ['variable', 'type', 'defaultValue', 'directives'],
  Variable: ['name'],
  SelectionSet: ['selections'],
  Field: ['alias', 'name', 'arguments', 'directives', 'selectionSet'],
  Argument: ['name', 'value'],
  FragmentSpread: ['name', 'directives'],
  InlineFragment: ['typeCondition', 'directives', 'selectionSet'],
  FragmentDefinition: [
    'name', // Note: fragment variable definitions are deprecated and will removed in v17.0.0
    'variableDefinitions',
    'typeCondition',
    'directives',
    'selectionSet',
  ],
  IntValue: [],
  FloatValue: [],
  StringValue: [],
  BooleanValue: [],
  NullValue: [],
  EnumValue: [],
  ListValue: ['values'],
  ObjectValue: ['fields'],
  ObjectField: ['name', 'value'],
  Directive: ['name', 'arguments'],
  NamedType: ['name'],
  ListType: ['type'],
  NonNullType: ['type'],
  SchemaDefinition: ['description', 'directives', 'operationTypes'],
  OperationTypeDefinition: ['type'],
  ScalarTypeDefinition: ['description', 'name', 'directives'],
  ObjectTypeDefinition: [
    'description',
    'name',
    'interfaces',
    'directives',
    'fields',
  ],
  FieldDefinition: ['description', 'name', 'arguments', 'type', 'directives'],
  InputValueDefinition: [
    'description',
    'name',
    'type',
    'defaultValue',
    'directives',
  ],
  InterfaceTypeDefinition: [
    'description',
    'name',
    'interfaces',
    'directives',
    'fields',
  ],
  UnionTypeDefinition: ['description', 'name', 'directives', 'types'],
  EnumTypeDefinition: ['description', 'name', 'directives', 'values'],
  EnumValueDefinition: ['description', 'name', 'directives'],
  InputObjectTypeDefinition: ['description', 'name', 'directives', 'fields'],
  DirectiveDefinition: ['description', 'name', 'arguments', 'locations'],
  SchemaExtension: ['directives', 'operationTypes'],
  ScalarTypeExtension: ['name', 'directives'],
  ObjectTypeExtension: ['name', 'interfaces', 'directives', 'fields'],
  InterfaceTypeExtension: ['name', 'interfaces', 'directives', 'fields'],
  UnionTypeExtension: ['name', 'directives', 'types'],
  EnumTypeExtension: ['name', 'directives', 'values'],
  InputObjectTypeExtension: ['name', 'directives', 'fields'],
};
const kindValues = new Set(Object.keys(QueryDocumentKeys));
/**
 * @internal
 */

function isNode$2(maybeNode) {
  const maybeKind =
    maybeNode === null || maybeNode === void 0 ? void 0 : maybeNode.kind;
  return typeof maybeKind === 'string' && kindValues.has(maybeKind);
}
/** Name */

var OperationTypeNode;

(function (OperationTypeNode) {
  OperationTypeNode['QUERY'] = 'query';
  OperationTypeNode['MUTATION'] = 'mutation';
  OperationTypeNode['SUBSCRIPTION'] = 'subscription';
})(OperationTypeNode || (OperationTypeNode = {}));

/**
 * The set of allowed directive location values.
 */
var DirectiveLocation;

(function (DirectiveLocation) {
  DirectiveLocation['QUERY'] = 'QUERY';
  DirectiveLocation['MUTATION'] = 'MUTATION';
  DirectiveLocation['SUBSCRIPTION'] = 'SUBSCRIPTION';
  DirectiveLocation['FIELD'] = 'FIELD';
  DirectiveLocation['FRAGMENT_DEFINITION'] = 'FRAGMENT_DEFINITION';
  DirectiveLocation['FRAGMENT_SPREAD'] = 'FRAGMENT_SPREAD';
  DirectiveLocation['INLINE_FRAGMENT'] = 'INLINE_FRAGMENT';
  DirectiveLocation['VARIABLE_DEFINITION'] = 'VARIABLE_DEFINITION';
  DirectiveLocation['SCHEMA'] = 'SCHEMA';
  DirectiveLocation['SCALAR'] = 'SCALAR';
  DirectiveLocation['OBJECT'] = 'OBJECT';
  DirectiveLocation['FIELD_DEFINITION'] = 'FIELD_DEFINITION';
  DirectiveLocation['ARGUMENT_DEFINITION'] = 'ARGUMENT_DEFINITION';
  DirectiveLocation['INTERFACE'] = 'INTERFACE';
  DirectiveLocation['UNION'] = 'UNION';
  DirectiveLocation['ENUM'] = 'ENUM';
  DirectiveLocation['ENUM_VALUE'] = 'ENUM_VALUE';
  DirectiveLocation['INPUT_OBJECT'] = 'INPUT_OBJECT';
  DirectiveLocation['INPUT_FIELD_DEFINITION'] = 'INPUT_FIELD_DEFINITION';
})(DirectiveLocation || (DirectiveLocation = {}));
/**
 * The enum type representing the directive location values.
 *
 * @deprecated Please use `DirectiveLocation`. Will be remove in v17.
 */

/**
 * The set of allowed kind values for AST nodes.
 */
var Kind;

(function (Kind) {
  Kind['NAME'] = 'Name';
  Kind['DOCUMENT'] = 'Document';
  Kind['OPERATION_DEFINITION'] = 'OperationDefinition';
  Kind['VARIABLE_DEFINITION'] = 'VariableDefinition';
  Kind['SELECTION_SET'] = 'SelectionSet';
  Kind['FIELD'] = 'Field';
  Kind['ARGUMENT'] = 'Argument';
  Kind['FRAGMENT_SPREAD'] = 'FragmentSpread';
  Kind['INLINE_FRAGMENT'] = 'InlineFragment';
  Kind['FRAGMENT_DEFINITION'] = 'FragmentDefinition';
  Kind['VARIABLE'] = 'Variable';
  Kind['INT'] = 'IntValue';
  Kind['FLOAT'] = 'FloatValue';
  Kind['STRING'] = 'StringValue';
  Kind['BOOLEAN'] = 'BooleanValue';
  Kind['NULL'] = 'NullValue';
  Kind['ENUM'] = 'EnumValue';
  Kind['LIST'] = 'ListValue';
  Kind['OBJECT'] = 'ObjectValue';
  Kind['OBJECT_FIELD'] = 'ObjectField';
  Kind['DIRECTIVE'] = 'Directive';
  Kind['NAMED_TYPE'] = 'NamedType';
  Kind['LIST_TYPE'] = 'ListType';
  Kind['NON_NULL_TYPE'] = 'NonNullType';
  Kind['SCHEMA_DEFINITION'] = 'SchemaDefinition';
  Kind['OPERATION_TYPE_DEFINITION'] = 'OperationTypeDefinition';
  Kind['SCALAR_TYPE_DEFINITION'] = 'ScalarTypeDefinition';
  Kind['OBJECT_TYPE_DEFINITION'] = 'ObjectTypeDefinition';
  Kind['FIELD_DEFINITION'] = 'FieldDefinition';
  Kind['INPUT_VALUE_DEFINITION'] = 'InputValueDefinition';
  Kind['INTERFACE_TYPE_DEFINITION'] = 'InterfaceTypeDefinition';
  Kind['UNION_TYPE_DEFINITION'] = 'UnionTypeDefinition';
  Kind['ENUM_TYPE_DEFINITION'] = 'EnumTypeDefinition';
  Kind['ENUM_VALUE_DEFINITION'] = 'EnumValueDefinition';
  Kind['INPUT_OBJECT_TYPE_DEFINITION'] = 'InputObjectTypeDefinition';
  Kind['DIRECTIVE_DEFINITION'] = 'DirectiveDefinition';
  Kind['SCHEMA_EXTENSION'] = 'SchemaExtension';
  Kind['SCALAR_TYPE_EXTENSION'] = 'ScalarTypeExtension';
  Kind['OBJECT_TYPE_EXTENSION'] = 'ObjectTypeExtension';
  Kind['INTERFACE_TYPE_EXTENSION'] = 'InterfaceTypeExtension';
  Kind['UNION_TYPE_EXTENSION'] = 'UnionTypeExtension';
  Kind['ENUM_TYPE_EXTENSION'] = 'EnumTypeExtension';
  Kind['INPUT_OBJECT_TYPE_EXTENSION'] = 'InputObjectTypeExtension';
})(Kind || (Kind = {}));
/**
 * The enum type representing the possible kind values of AST nodes.
 *
 * @deprecated Please use `Kind`. Will be remove in v17.
 */

/**
 * ```
 * WhiteSpace ::
 *   - "Horizontal Tab (U+0009)"
 *   - "Space (U+0020)"
 * ```
 * @internal
 */
function isWhiteSpace(code) {
  return code === 0x0009 || code === 0x0020;
}
/**
 * ```
 * Digit :: one of
 *   - `0` `1` `2` `3` `4` `5` `6` `7` `8` `9`
 * ```
 * @internal
 */

function isDigit$1(code) {
  return code >= 0x0030 && code <= 0x0039;
}
/**
 * ```
 * Letter :: one of
 *   - `A` `B` `C` `D` `E` `F` `G` `H` `I` `J` `K` `L` `M`
 *   - `N` `O` `P` `Q` `R` `S` `T` `U` `V` `W` `X` `Y` `Z`
 *   - `a` `b` `c` `d` `e` `f` `g` `h` `i` `j` `k` `l` `m`
 *   - `n` `o` `p` `q` `r` `s` `t` `u` `v` `w` `x` `y` `z`
 * ```
 * @internal
 */

function isLetter(code) {
  return (
    (code >= 0x0061 && code <= 0x007a) || // A-Z
    (code >= 0x0041 && code <= 0x005a) // a-z
  );
}
/**
 * ```
 * NameStart ::
 *   - Letter
 *   - `_`
 * ```
 * @internal
 */

function isNameStart(code) {
  return isLetter(code) || code === 0x005f;
}
/**
 * ```
 * NameContinue ::
 *   - Letter
 *   - Digit
 *   - `_`
 * ```
 * @internal
 */

function isNameContinue(code) {
  return isLetter(code) || isDigit$1(code) || code === 0x005f;
}

/**
 * Produces the value of a block string from its parsed raw value, similar to
 * CoffeeScript's block string, Python's docstring trim or Ruby's strip_heredoc.
 *
 * This implements the GraphQL spec's BlockStringValue() static algorithm.
 *
 * @internal
 */

function dedentBlockStringLines(lines) {
  var _firstNonEmptyLine2;

  let commonIndent = Number.MAX_SAFE_INTEGER;
  let firstNonEmptyLine = null;
  let lastNonEmptyLine = -1;

  for (let i = 0; i < lines.length; ++i) {
    var _firstNonEmptyLine;

    const line = lines[i];
    const indent = leadingWhitespace(line);

    if (indent === line.length) {
      continue; // skip empty lines
    }

    firstNonEmptyLine =
      (_firstNonEmptyLine = firstNonEmptyLine) !== null &&
      _firstNonEmptyLine !== void 0
        ? _firstNonEmptyLine
        : i;
    lastNonEmptyLine = i;

    if (i !== 0 && indent < commonIndent) {
      commonIndent = indent;
    }
  }

  return lines // Remove common indentation from all lines but first.
    .map((line, i) => (i === 0 ? line : line.slice(commonIndent))) // Remove leading and trailing blank lines.
    .slice(
      (_firstNonEmptyLine2 = firstNonEmptyLine) !== null &&
        _firstNonEmptyLine2 !== void 0
        ? _firstNonEmptyLine2
        : 0,
      lastNonEmptyLine + 1,
    );
}

function leadingWhitespace(str) {
  let i = 0;

  while (i < str.length && isWhiteSpace(str.charCodeAt(i))) {
    ++i;
  }

  return i;
}
/**
 * @internal
 */

function isPrintableAsBlockString(value) {
  if (value === '') {
    return true; // empty string is printable
  }

  let isEmptyLine = true;
  let hasIndent = false;
  let hasCommonIndent = true;
  let seenNonEmptyLine = false;

  for (let i = 0; i < value.length; ++i) {
    switch (value.codePointAt(i)) {
      case 0x0000:
      case 0x0001:
      case 0x0002:
      case 0x0003:
      case 0x0004:
      case 0x0005:
      case 0x0006:
      case 0x0007:
      case 0x0008:
      case 0x000b:
      case 0x000c:
      case 0x000e:
      case 0x000f:
        return false;
      // Has non-printable characters

      case 0x000d:
        //  \r
        return false;
      // Has \r or \r\n which will be replaced as \n

      case 10:
        //  \n
        if (isEmptyLine && !seenNonEmptyLine) {
          return false; // Has leading new line
        }

        seenNonEmptyLine = true;
        isEmptyLine = true;
        hasIndent = false;
        break;

      case 9: //   \t

      case 32:
        //  <space>
        hasIndent || (hasIndent = isEmptyLine);
        break;

      default:
        hasCommonIndent && (hasCommonIndent = hasIndent);
        isEmptyLine = false;
    }
  }

  if (isEmptyLine) {
    return false; // Has trailing empty lines
  }

  if (hasCommonIndent && seenNonEmptyLine) {
    return false; // Has internal indent
  }

  return true;
}
/**
 * Print a block string in the indented block form by adding a leading and
 * trailing blank line. However, if a block string starts with whitespace and is
 * a single-line, adding a leading blank line would strip that whitespace.
 *
 * @internal
 */

function printBlockString(value, options) {
  const escapedValue = value.replace(/"""/g, '\\"""'); // Expand a block string's raw value into independent lines.

  const lines = escapedValue.split(/\r\n|[\n\r]/g);
  const isSingleLine = lines.length === 1; // If common indentation is found we can fix some of those cases by adding leading new line

  const forceLeadingNewLine =
    lines.length > 1 &&
    lines
      .slice(1)
      .every((line) => line.length === 0 || isWhiteSpace(line.charCodeAt(0))); // Trailing triple quotes just looks confusing but doesn't force trailing new line

  const hasTrailingTripleQuotes = escapedValue.endsWith('\\"""'); // Trailing quote (single or double) or slash forces trailing new line

  const hasTrailingQuote = value.endsWith('"') && !hasTrailingTripleQuotes;
  const hasTrailingSlash = value.endsWith('\\');
  const forceTrailingNewline = hasTrailingQuote || hasTrailingSlash;
  const printAsMultipleLines =
    !(options !== null && options !== void 0 && options.minimize) && // add leading and trailing new lines only if it improves readability
    (!isSingleLine ||
      value.length > 70 ||
      forceTrailingNewline ||
      forceLeadingNewLine ||
      hasTrailingTripleQuotes);
  let result = ''; // Format a multi-line block quote to account for leading space.

  const skipLeadingNewLine = isSingleLine && isWhiteSpace(value.charCodeAt(0));

  if ((printAsMultipleLines && !skipLeadingNewLine) || forceLeadingNewLine) {
    result += '\n';
  }

  result += escapedValue;

  if (printAsMultipleLines || forceTrailingNewline) {
    result += '\n';
  }

  return '"""' + result + '"""';
}

/**
 * An exported enum describing the different kinds of tokens that the
 * lexer emits.
 */
var TokenKind;

(function (TokenKind) {
  TokenKind['SOF'] = '<SOF>';
  TokenKind['EOF'] = '<EOF>';
  TokenKind['BANG'] = '!';
  TokenKind['DOLLAR'] = '$';
  TokenKind['AMP'] = '&';
  TokenKind['PAREN_L'] = '(';
  TokenKind['PAREN_R'] = ')';
  TokenKind['SPREAD'] = '...';
  TokenKind['COLON'] = ':';
  TokenKind['EQUALS'] = '=';
  TokenKind['AT'] = '@';
  TokenKind['BRACKET_L'] = '[';
  TokenKind['BRACKET_R'] = ']';
  TokenKind['BRACE_L'] = '{';
  TokenKind['PIPE'] = '|';
  TokenKind['BRACE_R'] = '}';
  TokenKind['NAME'] = 'Name';
  TokenKind['INT'] = 'Int';
  TokenKind['FLOAT'] = 'Float';
  TokenKind['STRING'] = 'String';
  TokenKind['BLOCK_STRING'] = 'BlockString';
  TokenKind['COMMENT'] = 'Comment';
})(TokenKind || (TokenKind = {}));
/**
 * The enum type representing the token kinds values.
 *
 * @deprecated Please use `TokenKind`. Will be remove in v17.
 */

/**
 * Given a Source object, creates a Lexer for that source.
 * A Lexer is a stateful stream generator in that every time
 * it is advanced, it returns the next token in the Source. Assuming the
 * source lexes, the final Token emitted by the lexer will be of kind
 * EOF, after which the lexer will repeatedly return the same EOF token
 * whenever called.
 */

class Lexer {
  /**
   * The previously focused non-ignored token.
   */

  /**
   * The currently focused non-ignored token.
   */

  /**
   * The (1-indexed) line containing the current token.
   */

  /**
   * The character offset at which the current line begins.
   */
  constructor(source) {
    const startOfFileToken = new Token(TokenKind.SOF, 0, 0, 0, 0);
    this.source = source;
    this.lastToken = startOfFileToken;
    this.token = startOfFileToken;
    this.line = 1;
    this.lineStart = 0;
  }

  get [Symbol.toStringTag]() {
    return 'Lexer';
  }
  /**
   * Advances the token stream to the next non-ignored token.
   */

  advance() {
    this.lastToken = this.token;
    const token = (this.token = this.lookahead());
    return token;
  }
  /**
   * Looks ahead and returns the next non-ignored token, but does not change
   * the state of Lexer.
   */

  lookahead() {
    let token = this.token;

    if (token.kind !== TokenKind.EOF) {
      do {
        if (token.next) {
          token = token.next;
        } else {
          // Read the next token and form a link in the token linked-list.
          const nextToken = readNextToken(this, token.end); // @ts-expect-error next is only mutable during parsing.

          token.next = nextToken; // @ts-expect-error prev is only mutable during parsing.

          nextToken.prev = token;
          token = nextToken;
        }
      } while (token.kind === TokenKind.COMMENT);
    }

    return token;
  }
}
/**
 * @internal
 */

function isPunctuatorTokenKind(kind) {
  return (
    kind === TokenKind.BANG ||
    kind === TokenKind.DOLLAR ||
    kind === TokenKind.AMP ||
    kind === TokenKind.PAREN_L ||
    kind === TokenKind.PAREN_R ||
    kind === TokenKind.SPREAD ||
    kind === TokenKind.COLON ||
    kind === TokenKind.EQUALS ||
    kind === TokenKind.AT ||
    kind === TokenKind.BRACKET_L ||
    kind === TokenKind.BRACKET_R ||
    kind === TokenKind.BRACE_L ||
    kind === TokenKind.PIPE ||
    kind === TokenKind.BRACE_R
  );
}
/**
 * A Unicode scalar value is any Unicode code point except surrogate code
 * points. In other words, the inclusive ranges of values 0x0000 to 0xD7FF and
 * 0xE000 to 0x10FFFF.
 *
 * SourceCharacter ::
 *   - "Any Unicode scalar value"
 */

function isUnicodeScalarValue(code) {
  return (
    (code >= 0x0000 && code <= 0xd7ff) || (code >= 0xe000 && code <= 0x10ffff)
  );
}
/**
 * The GraphQL specification defines source text as a sequence of unicode scalar
 * values (which Unicode defines to exclude surrogate code points). However
 * JavaScript defines strings as a sequence of UTF-16 code units which may
 * include surrogates. A surrogate pair is a valid source character as it
 * encodes a supplementary code point (above U+FFFF), but unpaired surrogate
 * code points are not valid source characters.
 */

function isSupplementaryCodePoint(body, location) {
  return (
    isLeadingSurrogate(body.charCodeAt(location)) &&
    isTrailingSurrogate(body.charCodeAt(location + 1))
  );
}

function isLeadingSurrogate(code) {
  return code >= 0xd800 && code <= 0xdbff;
}

function isTrailingSurrogate(code) {
  return code >= 0xdc00 && code <= 0xdfff;
}
/**
 * Prints the code point (or end of file reference) at a given location in a
 * source for use in error messages.
 *
 * Printable ASCII is printed quoted, while other points are printed in Unicode
 * code point form (ie. U+1234).
 */

function printCodePointAt(lexer, location) {
  const code = lexer.source.body.codePointAt(location);

  if (code === undefined) {
    return TokenKind.EOF;
  } else if (code >= 0x0020 && code <= 0x007e) {
    // Printable ASCII
    const char = String.fromCodePoint(code);
    return char === '"' ? "'\"'" : `"${char}"`;
  } // Unicode code point

  return 'U+' + code.toString(16).toUpperCase().padStart(4, '0');
}
/**
 * Create a token with line and column location information.
 */

function createToken(lexer, kind, start, end, value) {
  const line = lexer.line;
  const col = 1 + start - lexer.lineStart;
  return new Token(kind, start, end, line, col, value);
}
/**
 * Gets the next token from the source starting at the given position.
 *
 * This skips over whitespace until it finds the next lexable token, then lexes
 * punctuators immediately or calls the appropriate helper function for more
 * complicated tokens.
 */

function readNextToken(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start;

  while (position < bodyLength) {
    const code = body.charCodeAt(position); // SourceCharacter

    switch (code) {
      // Ignored ::
      //   - UnicodeBOM
      //   - WhiteSpace
      //   - LineTerminator
      //   - Comment
      //   - Comma
      //
      // UnicodeBOM :: "Byte Order Mark (U+FEFF)"
      //
      // WhiteSpace ::
      //   - "Horizontal Tab (U+0009)"
      //   - "Space (U+0020)"
      //
      // Comma :: ,
      case 0xfeff: // <BOM>

      case 0x0009: // \t

      case 0x0020: // <space>

      case 0x002c:
        // ,
        ++position;
        continue;
      // LineTerminator ::
      //   - "New Line (U+000A)"
      //   - "Carriage Return (U+000D)" [lookahead != "New Line (U+000A)"]
      //   - "Carriage Return (U+000D)" "New Line (U+000A)"

      case 0x000a:
        // \n
        ++position;
        ++lexer.line;
        lexer.lineStart = position;
        continue;

      case 0x000d:
        // \r
        if (body.charCodeAt(position + 1) === 0x000a) {
          position += 2;
        } else {
          ++position;
        }

        ++lexer.line;
        lexer.lineStart = position;
        continue;
      // Comment

      case 0x0023:
        // #
        return readComment(lexer, position);
      // Token ::
      //   - Punctuator
      //   - Name
      //   - IntValue
      //   - FloatValue
      //   - StringValue
      //
      // Punctuator :: one of ! $ & ( ) ... : = @ [ ] { | }

      case 0x0021:
        // !
        return createToken(lexer, TokenKind.BANG, position, position + 1);

      case 0x0024:
        // $
        return createToken(lexer, TokenKind.DOLLAR, position, position + 1);

      case 0x0026:
        // &
        return createToken(lexer, TokenKind.AMP, position, position + 1);

      case 0x0028:
        // (
        return createToken(lexer, TokenKind.PAREN_L, position, position + 1);

      case 0x0029:
        // )
        return createToken(lexer, TokenKind.PAREN_R, position, position + 1);

      case 0x002e:
        // .
        if (
          body.charCodeAt(position + 1) === 0x002e &&
          body.charCodeAt(position + 2) === 0x002e
        ) {
          return createToken(lexer, TokenKind.SPREAD, position, position + 3);
        }

        break;

      case 0x003a:
        // :
        return createToken(lexer, TokenKind.COLON, position, position + 1);

      case 0x003d:
        // =
        return createToken(lexer, TokenKind.EQUALS, position, position + 1);

      case 0x0040:
        // @
        return createToken(lexer, TokenKind.AT, position, position + 1);

      case 0x005b:
        // [
        return createToken(lexer, TokenKind.BRACKET_L, position, position + 1);

      case 0x005d:
        // ]
        return createToken(lexer, TokenKind.BRACKET_R, position, position + 1);

      case 0x007b:
        // {
        return createToken(lexer, TokenKind.BRACE_L, position, position + 1);

      case 0x007c:
        // |
        return createToken(lexer, TokenKind.PIPE, position, position + 1);

      case 0x007d:
        // }
        return createToken(lexer, TokenKind.BRACE_R, position, position + 1);
      // StringValue

      case 0x0022:
        // "
        if (
          body.charCodeAt(position + 1) === 0x0022 &&
          body.charCodeAt(position + 2) === 0x0022
        ) {
          return readBlockString(lexer, position);
        }

        return readString(lexer, position);
    } // IntValue | FloatValue (Digit | -)

    if (isDigit$1(code) || code === 0x002d) {
      return readNumber(lexer, position, code);
    } // Name

    if (isNameStart(code)) {
      return readName(lexer, position);
    }

    throw syntaxError(
      lexer.source,
      position,
      code === 0x0027
        ? 'Unexpected single quote character (\'), did you mean to use a double quote (")?'
        : isUnicodeScalarValue(code) || isSupplementaryCodePoint(body, position)
        ? `Unexpected character: ${printCodePointAt(lexer, position)}.`
        : `Invalid character: ${printCodePointAt(lexer, position)}.`,
    );
  }

  return createToken(lexer, TokenKind.EOF, bodyLength, bodyLength);
}
/**
 * Reads a comment token from the source file.
 *
 * ```
 * Comment :: # CommentChar* [lookahead != CommentChar]
 *
 * CommentChar :: SourceCharacter but not LineTerminator
 * ```
 */

function readComment(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start + 1;

  while (position < bodyLength) {
    const code = body.charCodeAt(position); // LineTerminator (\n | \r)

    if (code === 0x000a || code === 0x000d) {
      break;
    } // SourceCharacter

    if (isUnicodeScalarValue(code)) {
      ++position;
    } else if (isSupplementaryCodePoint(body, position)) {
      position += 2;
    } else {
      break;
    }
  }

  return createToken(
    lexer,
    TokenKind.COMMENT,
    start,
    position,
    body.slice(start + 1, position),
  );
}
/**
 * Reads a number token from the source file, either a FloatValue or an IntValue
 * depending on whether a FractionalPart or ExponentPart is encountered.
 *
 * ```
 * IntValue :: IntegerPart [lookahead != {Digit, `.`, NameStart}]
 *
 * IntegerPart ::
 *   - NegativeSign? 0
 *   - NegativeSign? NonZeroDigit Digit*
 *
 * NegativeSign :: -
 *
 * NonZeroDigit :: Digit but not `0`
 *
 * FloatValue ::
 *   - IntegerPart FractionalPart ExponentPart [lookahead != {Digit, `.`, NameStart}]
 *   - IntegerPart FractionalPart [lookahead != {Digit, `.`, NameStart}]
 *   - IntegerPart ExponentPart [lookahead != {Digit, `.`, NameStart}]
 *
 * FractionalPart :: . Digit+
 *
 * ExponentPart :: ExponentIndicator Sign? Digit+
 *
 * ExponentIndicator :: one of `e` `E`
 *
 * Sign :: one of + -
 * ```
 */

function readNumber(lexer, start, firstCode) {
  const body = lexer.source.body;
  let position = start;
  let code = firstCode;
  let isFloat = false; // NegativeSign (-)

  if (code === 0x002d) {
    code = body.charCodeAt(++position);
  } // Zero (0)

  if (code === 0x0030) {
    code = body.charCodeAt(++position);

    if (isDigit$1(code)) {
      throw syntaxError(
        lexer.source,
        position,
        `Invalid number, unexpected digit after 0: ${printCodePointAt(
          lexer,
          position,
        )}.`,
      );
    }
  } else {
    position = readDigits(lexer, position, code);
    code = body.charCodeAt(position);
  } // Full stop (.)

  if (code === 0x002e) {
    isFloat = true;
    code = body.charCodeAt(++position);
    position = readDigits(lexer, position, code);
    code = body.charCodeAt(position);
  } // E e

  if (code === 0x0045 || code === 0x0065) {
    isFloat = true;
    code = body.charCodeAt(++position); // + -

    if (code === 0x002b || code === 0x002d) {
      code = body.charCodeAt(++position);
    }

    position = readDigits(lexer, position, code);
    code = body.charCodeAt(position);
  } // Numbers cannot be followed by . or NameStart

  if (code === 0x002e || isNameStart(code)) {
    throw syntaxError(
      lexer.source,
      position,
      `Invalid number, expected digit but got: ${printCodePointAt(
        lexer,
        position,
      )}.`,
    );
  }

  return createToken(
    lexer,
    isFloat ? TokenKind.FLOAT : TokenKind.INT,
    start,
    position,
    body.slice(start, position),
  );
}
/**
 * Returns the new position in the source after reading one or more digits.
 */

function readDigits(lexer, start, firstCode) {
  if (!isDigit$1(firstCode)) {
    throw syntaxError(
      lexer.source,
      start,
      `Invalid number, expected digit but got: ${printCodePointAt(
        lexer,
        start,
      )}.`,
    );
  }

  const body = lexer.source.body;
  let position = start + 1; // +1 to skip first firstCode

  while (isDigit$1(body.charCodeAt(position))) {
    ++position;
  }

  return position;
}
/**
 * Reads a single-quote string token from the source file.
 *
 * ```
 * StringValue ::
 *   - `""` [lookahead != `"`]
 *   - `"` StringCharacter+ `"`
 *
 * StringCharacter ::
 *   - SourceCharacter but not `"` or `\` or LineTerminator
 *   - `\u` EscapedUnicode
 *   - `\` EscapedCharacter
 *
 * EscapedUnicode ::
 *   - `{` HexDigit+ `}`
 *   - HexDigit HexDigit HexDigit HexDigit
 *
 * EscapedCharacter :: one of `"` `\` `/` `b` `f` `n` `r` `t`
 * ```
 */

function readString(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start + 1;
  let chunkStart = position;
  let value = '';

  while (position < bodyLength) {
    const code = body.charCodeAt(position); // Closing Quote (")

    if (code === 0x0022) {
      value += body.slice(chunkStart, position);
      return createToken(lexer, TokenKind.STRING, start, position + 1, value);
    } // Escape Sequence (\)

    if (code === 0x005c) {
      value += body.slice(chunkStart, position);
      const escape =
        body.charCodeAt(position + 1) === 0x0075 // u
          ? body.charCodeAt(position + 2) === 0x007b // {
            ? readEscapedUnicodeVariableWidth(lexer, position)
            : readEscapedUnicodeFixedWidth(lexer, position)
          : readEscapedCharacter(lexer, position);
      value += escape.value;
      position += escape.size;
      chunkStart = position;
      continue;
    } // LineTerminator (\n | \r)

    if (code === 0x000a || code === 0x000d) {
      break;
    } // SourceCharacter

    if (isUnicodeScalarValue(code)) {
      ++position;
    } else if (isSupplementaryCodePoint(body, position)) {
      position += 2;
    } else {
      throw syntaxError(
        lexer.source,
        position,
        `Invalid character within String: ${printCodePointAt(
          lexer,
          position,
        )}.`,
      );
    }
  }

  throw syntaxError(lexer.source, position, 'Unterminated string.');
} // The string value and lexed size of an escape sequence.

function readEscapedUnicodeVariableWidth(lexer, position) {
  const body = lexer.source.body;
  let point = 0;
  let size = 3; // Cannot be larger than 12 chars (\u{00000000}).

  while (size < 12) {
    const code = body.charCodeAt(position + size++); // Closing Brace (})

    if (code === 0x007d) {
      // Must be at least 5 chars (\u{0}) and encode a Unicode scalar value.
      if (size < 5 || !isUnicodeScalarValue(point)) {
        break;
      }

      return {
        value: String.fromCodePoint(point),
        size,
      };
    } // Append this hex digit to the code point.

    point = (point << 4) | readHexDigit(code);

    if (point < 0) {
      break;
    }
  }

  throw syntaxError(
    lexer.source,
    position,
    `Invalid Unicode escape sequence: "${body.slice(
      position,
      position + size,
    )}".`,
  );
}

function readEscapedUnicodeFixedWidth(lexer, position) {
  const body = lexer.source.body;
  const code = read16BitHexCode(body, position + 2);

  if (isUnicodeScalarValue(code)) {
    return {
      value: String.fromCodePoint(code),
      size: 6,
    };
  } // GraphQL allows JSON-style surrogate pair escape sequences, but only when
  // a valid pair is formed.

  if (isLeadingSurrogate(code)) {
    // \u
    if (
      body.charCodeAt(position + 6) === 0x005c &&
      body.charCodeAt(position + 7) === 0x0075
    ) {
      const trailingCode = read16BitHexCode(body, position + 8);

      if (isTrailingSurrogate(trailingCode)) {
        // JavaScript defines strings as a sequence of UTF-16 code units and
        // encodes Unicode code points above U+FFFF using a surrogate pair of
        // code units. Since this is a surrogate pair escape sequence, just
        // include both codes into the JavaScript string value. Had JavaScript
        // not been internally based on UTF-16, then this surrogate pair would
        // be decoded to retrieve the supplementary code point.
        return {
          value: String.fromCodePoint(code, trailingCode),
          size: 12,
        };
      }
    }
  }

  throw syntaxError(
    lexer.source,
    position,
    `Invalid Unicode escape sequence: "${body.slice(position, position + 6)}".`,
  );
}
/**
 * Reads four hexadecimal characters and returns the positive integer that 16bit
 * hexadecimal string represents. For example, "000f" will return 15, and "dead"
 * will return 57005.
 *
 * Returns a negative number if any char was not a valid hexadecimal digit.
 */

function read16BitHexCode(body, position) {
  // readHexDigit() returns -1 on error. ORing a negative value with any other
  // value always produces a negative value.
  return (
    (readHexDigit(body.charCodeAt(position)) << 12) |
    (readHexDigit(body.charCodeAt(position + 1)) << 8) |
    (readHexDigit(body.charCodeAt(position + 2)) << 4) |
    readHexDigit(body.charCodeAt(position + 3))
  );
}
/**
 * Reads a hexadecimal character and returns its positive integer value (0-15).
 *
 * '0' becomes 0, '9' becomes 9
 * 'A' becomes 10, 'F' becomes 15
 * 'a' becomes 10, 'f' becomes 15
 *
 * Returns -1 if the provided character code was not a valid hexadecimal digit.
 *
 * HexDigit :: one of
 *   - `0` `1` `2` `3` `4` `5` `6` `7` `8` `9`
 *   - `A` `B` `C` `D` `E` `F`
 *   - `a` `b` `c` `d` `e` `f`
 */

function readHexDigit(code) {
  return code >= 0x0030 && code <= 0x0039 // 0-9
    ? code - 0x0030
    : code >= 0x0041 && code <= 0x0046 // A-F
    ? code - 0x0037
    : code >= 0x0061 && code <= 0x0066 // a-f
    ? code - 0x0057
    : -1;
}
/**
 * | Escaped Character | Code Point | Character Name               |
 * | ----------------- | ---------- | ---------------------------- |
 * | `"`               | U+0022     | double quote                 |
 * | `\`               | U+005C     | reverse solidus (back slash) |
 * | `/`               | U+002F     | solidus (forward slash)      |
 * | `b`               | U+0008     | backspace                    |
 * | `f`               | U+000C     | form feed                    |
 * | `n`               | U+000A     | line feed (new line)         |
 * | `r`               | U+000D     | carriage return              |
 * | `t`               | U+0009     | horizontal tab               |
 */

function readEscapedCharacter(lexer, position) {
  const body = lexer.source.body;
  const code = body.charCodeAt(position + 1);

  switch (code) {
    case 0x0022:
      // "
      return {
        value: '\u0022',
        size: 2,
      };

    case 0x005c:
      // \
      return {
        value: '\u005c',
        size: 2,
      };

    case 0x002f:
      // /
      return {
        value: '\u002f',
        size: 2,
      };

    case 0x0062:
      // b
      return {
        value: '\u0008',
        size: 2,
      };

    case 0x0066:
      // f
      return {
        value: '\u000c',
        size: 2,
      };

    case 0x006e:
      // n
      return {
        value: '\u000a',
        size: 2,
      };

    case 0x0072:
      // r
      return {
        value: '\u000d',
        size: 2,
      };

    case 0x0074:
      // t
      return {
        value: '\u0009',
        size: 2,
      };
  }

  throw syntaxError(
    lexer.source,
    position,
    `Invalid character escape sequence: "${body.slice(
      position,
      position + 2,
    )}".`,
  );
}
/**
 * Reads a block string token from the source file.
 *
 * ```
 * StringValue ::
 *   - `"""` BlockStringCharacter* `"""`
 *
 * BlockStringCharacter ::
 *   - SourceCharacter but not `"""` or `\"""`
 *   - `\"""`
 * ```
 */

function readBlockString(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let lineStart = lexer.lineStart;
  let position = start + 3;
  let chunkStart = position;
  let currentLine = '';
  const blockLines = [];

  while (position < bodyLength) {
    const code = body.charCodeAt(position); // Closing Triple-Quote (""")

    if (
      code === 0x0022 &&
      body.charCodeAt(position + 1) === 0x0022 &&
      body.charCodeAt(position + 2) === 0x0022
    ) {
      currentLine += body.slice(chunkStart, position);
      blockLines.push(currentLine);
      const token = createToken(
        lexer,
        TokenKind.BLOCK_STRING,
        start,
        position + 3, // Return a string of the lines joined with U+000A.
        dedentBlockStringLines(blockLines).join('\n'),
      );
      lexer.line += blockLines.length - 1;
      lexer.lineStart = lineStart;
      return token;
    } // Escaped Triple-Quote (\""")

    if (
      code === 0x005c &&
      body.charCodeAt(position + 1) === 0x0022 &&
      body.charCodeAt(position + 2) === 0x0022 &&
      body.charCodeAt(position + 3) === 0x0022
    ) {
      currentLine += body.slice(chunkStart, position);
      chunkStart = position + 1; // skip only slash

      position += 4;
      continue;
    } // LineTerminator

    if (code === 0x000a || code === 0x000d) {
      currentLine += body.slice(chunkStart, position);
      blockLines.push(currentLine);

      if (code === 0x000d && body.charCodeAt(position + 1) === 0x000a) {
        position += 2;
      } else {
        ++position;
      }

      currentLine = '';
      chunkStart = position;
      lineStart = position;
      continue;
    } // SourceCharacter

    if (isUnicodeScalarValue(code)) {
      ++position;
    } else if (isSupplementaryCodePoint(body, position)) {
      position += 2;
    } else {
      throw syntaxError(
        lexer.source,
        position,
        `Invalid character within String: ${printCodePointAt(
          lexer,
          position,
        )}.`,
      );
    }
  }

  throw syntaxError(lexer.source, position, 'Unterminated string.');
}
/**
 * Reads an alphanumeric + underscore name from the source.
 *
 * ```
 * Name ::
 *   - NameStart NameContinue* [lookahead != NameContinue]
 * ```
 */

function readName(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start + 1;

  while (position < bodyLength) {
    const code = body.charCodeAt(position);

    if (isNameContinue(code)) {
      ++position;
    } else {
      break;
    }
  }

  return createToken(
    lexer,
    TokenKind.NAME,
    start,
    position,
    body.slice(start, position),
  );
}

const MAX_ARRAY_LENGTH = 10;
const MAX_RECURSIVE_DEPTH = 2;
/**
 * Used to print values in error messages.
 */

function inspect(value) {
  return formatValue(value, []);
}

function formatValue(value, seenValues) {
  switch (typeof value) {
    case 'string':
      return JSON.stringify(value);

    case 'function':
      return value.name ? `[function ${value.name}]` : '[function]';

    case 'object':
      return formatObjectValue(value, seenValues);

    default:
      return String(value);
  }
}

function formatObjectValue(value, previouslySeenValues) {
  if (value === null) {
    return 'null';
  }

  if (previouslySeenValues.includes(value)) {
    return '[Circular]';
  }

  const seenValues = [...previouslySeenValues, value];

  if (isJSONable(value)) {
    const jsonValue = value.toJSON(); // check for infinite recursion

    if (jsonValue !== value) {
      return typeof jsonValue === 'string'
        ? jsonValue
        : formatValue(jsonValue, seenValues);
    }
  } else if (Array.isArray(value)) {
    return formatArray(value, seenValues);
  }

  return formatObject(value, seenValues);
}

function isJSONable(value) {
  return typeof value.toJSON === 'function';
}

function formatObject(object, seenValues) {
  const entries = Object.entries(object);

  if (entries.length === 0) {
    return '{}';
  }

  if (seenValues.length > MAX_RECURSIVE_DEPTH) {
    return '[' + getObjectTag(object) + ']';
  }

  const properties = entries.map(
    ([key, value]) => key + ': ' + formatValue(value, seenValues),
  );
  return '{ ' + properties.join(', ') + ' }';
}

function formatArray(array, seenValues) {
  if (array.length === 0) {
    return '[]';
  }

  if (seenValues.length > MAX_RECURSIVE_DEPTH) {
    return '[Array]';
  }

  const len = Math.min(MAX_ARRAY_LENGTH, array.length);
  const remaining = array.length - len;
  const items = [];

  for (let i = 0; i < len; ++i) {
    items.push(formatValue(array[i], seenValues));
  }

  if (remaining === 1) {
    items.push('... 1 more item');
  } else if (remaining > 1) {
    items.push(`... ${remaining} more items`);
  }

  return '[' + items.join(', ') + ']';
}

function getObjectTag(object) {
  const tag = Object.prototype.toString
    .call(object)
    .replace(/^\[object /, '')
    .replace(/]$/, '');

  if (tag === 'Object' && typeof object.constructor === 'function') {
    const name = object.constructor.name;

    if (typeof name === 'string' && name !== '') {
      return name;
    }
  }

  return tag;
}

/**
 * A replacement for instanceof which includes an error warning when multi-realm
 * constructors are detected.
 * See: https://expressjs.com/en/advanced/best-practice-performance.html#set-node_env-to-production
 * See: https://webpack.js.org/guides/production/
 */

const instanceOf =
  /* c8 ignore next 6 */
  // FIXME: https://github.com/graphql/graphql-js/issues/2317
  globalThis.process && globalThis.process.env.NODE_ENV === 'production'
    ? function instanceOf(value, constructor) {
        return value instanceof constructor;
      }
    : function instanceOf(value, constructor) {
        if (value instanceof constructor) {
          return true;
        }

        if (typeof value === 'object' && value !== null) {
          var _value$constructor;

          // Prefer Symbol.toStringTag since it is immune to minification.
          const className = constructor.prototype[Symbol.toStringTag];
          const valueClassName = // We still need to support constructor's name to detect conflicts with older versions of this library.
            Symbol.toStringTag in value // @ts-expect-error TS bug see, https://github.com/microsoft/TypeScript/issues/38009
              ? value[Symbol.toStringTag]
              : (_value$constructor = value.constructor) === null ||
                _value$constructor === void 0
              ? void 0
              : _value$constructor.name;

          if (className === valueClassName) {
            const stringifiedValue = inspect(value);
            throw new Error(`Cannot use ${className} "${stringifiedValue}" from another module or realm.

Ensure that there is only one instance of "graphql" in the node_modules
directory. If different versions of "graphql" are the dependencies of other
relied on modules, use "resolutions" to ensure only one version is installed.

https://yarnpkg.com/en/docs/selective-version-resolutions

Duplicate "graphql" modules cannot be used at the same time since different
versions may have different capabilities and behavior. The data from one
version used in the function from another could produce confusing and
spurious results.`);
          }
        }

        return false;
      };

/**
 * A representation of source input to GraphQL. The `name` and `locationOffset` parameters are
 * optional, but they are useful for clients who store GraphQL documents in source files.
 * For example, if the GraphQL input starts at line 40 in a file named `Foo.graphql`, it might
 * be useful for `name` to be `"Foo.graphql"` and location to be `{ line: 40, column: 1 }`.
 * The `line` and `column` properties in `locationOffset` are 1-indexed.
 */
class Source {
  constructor(
    body,
    name = 'GraphQL request',
    locationOffset = {
      line: 1,
      column: 1,
    },
  ) {
    typeof body === 'string' ||
      devAssert(false, `Body must be a string. Received: ${inspect(body)}.`);
    this.body = body;
    this.name = name;
    this.locationOffset = locationOffset;
    this.locationOffset.line > 0 ||
      devAssert(
        false,
        'line in locationOffset is 1-indexed and must be positive.',
      );
    this.locationOffset.column > 0 ||
      devAssert(
        false,
        'column in locationOffset is 1-indexed and must be positive.',
      );
  }

  get [Symbol.toStringTag]() {
    return 'Source';
  }
}
/**
 * Test if the given value is a Source object.
 *
 * @internal
 */

function isSource(source) {
  return instanceOf(source, Source);
}

/**
 * Configuration options to control parser behavior
 */

/**
 * Given a GraphQL source, parses it into a Document.
 * Throws GraphQLError if a syntax error is encountered.
 */
function parse$1(source, options) {
  const parser = new Parser(source, options);
  return parser.parseDocument();
}
/**
 * Given a string containing a GraphQL value (ex. `[42]`), parse the AST for
 * that value.
 * Throws GraphQLError if a syntax error is encountered.
 *
 * This is useful within tools that operate upon GraphQL Values directly and
 * in isolation of complete GraphQL documents.
 *
 * Consider providing the results to the utility function: valueFromAST().
 */

function parseValue(source, options) {
  const parser = new Parser(source, options);
  parser.expectToken(TokenKind.SOF);
  const value = parser.parseValueLiteral(false);
  parser.expectToken(TokenKind.EOF);
  return value;
}
/**
 * Similar to parseValue(), but raises a parse error if it encounters a
 * variable. The return type will be a constant value.
 */

function parseConstValue(source, options) {
  const parser = new Parser(source, options);
  parser.expectToken(TokenKind.SOF);
  const value = parser.parseConstValueLiteral();
  parser.expectToken(TokenKind.EOF);
  return value;
}
/**
 * Given a string containing a GraphQL Type (ex. `[Int!]`), parse the AST for
 * that type.
 * Throws GraphQLError if a syntax error is encountered.
 *
 * This is useful within tools that operate upon GraphQL Types directly and
 * in isolation of complete GraphQL documents.
 *
 * Consider providing the results to the utility function: typeFromAST().
 */

function parseType(source, options) {
  const parser = new Parser(source, options);
  parser.expectToken(TokenKind.SOF);
  const type = parser.parseTypeReference();
  parser.expectToken(TokenKind.EOF);
  return type;
}
/**
 * This class is exported only to assist people in implementing their own parsers
 * without duplicating too much code and should be used only as last resort for cases
 * such as experimental syntax or if certain features could not be contributed upstream.
 *
 * It is still part of the internal API and is versioned, so any changes to it are never
 * considered breaking changes. If you still need to support multiple versions of the
 * library, please use the `versionInfo` variable for version detection.
 *
 * @internal
 */

class Parser {
  constructor(source, options = {}) {
    const sourceObj = isSource(source) ? source : new Source(source);
    this._lexer = new Lexer(sourceObj);
    this._options = options;
    this._tokenCounter = 0;
  }
  /**
   * Converts a name lex token into a name parse node.
   */

  parseName() {
    const token = this.expectToken(TokenKind.NAME);
    return this.node(token, {
      kind: Kind.NAME,
      value: token.value,
    });
  } // Implements the parsing rules in the Document section.

  /**
   * Document : Definition+
   */

  parseDocument() {
    return this.node(this._lexer.token, {
      kind: Kind.DOCUMENT,
      definitions: this.many(
        TokenKind.SOF,
        this.parseDefinition,
        TokenKind.EOF,
      ),
    });
  }
  /**
   * Definition :
   *   - ExecutableDefinition
   *   - TypeSystemDefinition
   *   - TypeSystemExtension
   *
   * ExecutableDefinition :
   *   - OperationDefinition
   *   - FragmentDefinition
   *
   * TypeSystemDefinition :
   *   - SchemaDefinition
   *   - TypeDefinition
   *   - DirectiveDefinition
   *
   * TypeDefinition :
   *   - ScalarTypeDefinition
   *   - ObjectTypeDefinition
   *   - InterfaceTypeDefinition
   *   - UnionTypeDefinition
   *   - EnumTypeDefinition
   *   - InputObjectTypeDefinition
   */

  parseDefinition() {
    if (this.peek(TokenKind.BRACE_L)) {
      return this.parseOperationDefinition();
    } // Many definitions begin with a description and require a lookahead.

    const hasDescription = this.peekDescription();
    const keywordToken = hasDescription
      ? this._lexer.lookahead()
      : this._lexer.token;

    if (keywordToken.kind === TokenKind.NAME) {
      switch (keywordToken.value) {
        case 'schema':
          return this.parseSchemaDefinition();

        case 'scalar':
          return this.parseScalarTypeDefinition();

        case 'type':
          return this.parseObjectTypeDefinition();

        case 'interface':
          return this.parseInterfaceTypeDefinition();

        case 'union':
          return this.parseUnionTypeDefinition();

        case 'enum':
          return this.parseEnumTypeDefinition();

        case 'input':
          return this.parseInputObjectTypeDefinition();

        case 'directive':
          return this.parseDirectiveDefinition();
      }

      if (hasDescription) {
        throw syntaxError(
          this._lexer.source,
          this._lexer.token.start,
          'Unexpected description, descriptions are supported only on type definitions.',
        );
      }

      switch (keywordToken.value) {
        case 'query':
        case 'mutation':
        case 'subscription':
          return this.parseOperationDefinition();

        case 'fragment':
          return this.parseFragmentDefinition();

        case 'extend':
          return this.parseTypeSystemExtension();
      }
    }

    throw this.unexpected(keywordToken);
  } // Implements the parsing rules in the Operations section.

  /**
   * OperationDefinition :
   *  - SelectionSet
   *  - OperationType Name? VariableDefinitions? Directives? SelectionSet
   */

  parseOperationDefinition() {
    const start = this._lexer.token;

    if (this.peek(TokenKind.BRACE_L)) {
      return this.node(start, {
        kind: Kind.OPERATION_DEFINITION,
        operation: OperationTypeNode.QUERY,
        name: undefined,
        variableDefinitions: [],
        directives: [],
        selectionSet: this.parseSelectionSet(),
      });
    }

    const operation = this.parseOperationType();
    let name;

    if (this.peek(TokenKind.NAME)) {
      name = this.parseName();
    }

    return this.node(start, {
      kind: Kind.OPERATION_DEFINITION,
      operation,
      name,
      variableDefinitions: this.parseVariableDefinitions(),
      directives: this.parseDirectives(false),
      selectionSet: this.parseSelectionSet(),
    });
  }
  /**
   * OperationType : one of query mutation subscription
   */

  parseOperationType() {
    const operationToken = this.expectToken(TokenKind.NAME);

    switch (operationToken.value) {
      case 'query':
        return OperationTypeNode.QUERY;

      case 'mutation':
        return OperationTypeNode.MUTATION;

      case 'subscription':
        return OperationTypeNode.SUBSCRIPTION;
    }

    throw this.unexpected(operationToken);
  }
  /**
   * VariableDefinitions : ( VariableDefinition+ )
   */

  parseVariableDefinitions() {
    return this.optionalMany(
      TokenKind.PAREN_L,
      this.parseVariableDefinition,
      TokenKind.PAREN_R,
    );
  }
  /**
   * VariableDefinition : Variable : Type DefaultValue? Directives[Const]?
   */

  parseVariableDefinition() {
    return this.node(this._lexer.token, {
      kind: Kind.VARIABLE_DEFINITION,
      variable: this.parseVariable(),
      type: (this.expectToken(TokenKind.COLON), this.parseTypeReference()),
      defaultValue: this.expectOptionalToken(TokenKind.EQUALS)
        ? this.parseConstValueLiteral()
        : undefined,
      directives: this.parseConstDirectives(),
    });
  }
  /**
   * Variable : $ Name
   */

  parseVariable() {
    const start = this._lexer.token;
    this.expectToken(TokenKind.DOLLAR);
    return this.node(start, {
      kind: Kind.VARIABLE,
      name: this.parseName(),
    });
  }
  /**
   * ```
   * SelectionSet : { Selection+ }
   * ```
   */

  parseSelectionSet() {
    return this.node(this._lexer.token, {
      kind: Kind.SELECTION_SET,
      selections: this.many(
        TokenKind.BRACE_L,
        this.parseSelection,
        TokenKind.BRACE_R,
      ),
    });
  }
  /**
   * Selection :
   *   - Field
   *   - FragmentSpread
   *   - InlineFragment
   */

  parseSelection() {
    return this.peek(TokenKind.SPREAD)
      ? this.parseFragment()
      : this.parseField();
  }
  /**
   * Field : Alias? Name Arguments? Directives? SelectionSet?
   *
   * Alias : Name :
   */

  parseField() {
    const start = this._lexer.token;
    const nameOrAlias = this.parseName();
    let alias;
    let name;

    if (this.expectOptionalToken(TokenKind.COLON)) {
      alias = nameOrAlias;
      name = this.parseName();
    } else {
      name = nameOrAlias;
    }

    return this.node(start, {
      kind: Kind.FIELD,
      alias,
      name,
      arguments: this.parseArguments(false),
      directives: this.parseDirectives(false),
      selectionSet: this.peek(TokenKind.BRACE_L)
        ? this.parseSelectionSet()
        : undefined,
    });
  }
  /**
   * Arguments[Const] : ( Argument[?Const]+ )
   */

  parseArguments(isConst) {
    const item = isConst ? this.parseConstArgument : this.parseArgument;
    return this.optionalMany(TokenKind.PAREN_L, item, TokenKind.PAREN_R);
  }
  /**
   * Argument[Const] : Name : Value[?Const]
   */

  parseArgument(isConst = false) {
    const start = this._lexer.token;
    const name = this.parseName();
    this.expectToken(TokenKind.COLON);
    return this.node(start, {
      kind: Kind.ARGUMENT,
      name,
      value: this.parseValueLiteral(isConst),
    });
  }

  parseConstArgument() {
    return this.parseArgument(true);
  } // Implements the parsing rules in the Fragments section.

  /**
   * Corresponds to both FragmentSpread and InlineFragment in the spec.
   *
   * FragmentSpread : ... FragmentName Directives?
   *
   * InlineFragment : ... TypeCondition? Directives? SelectionSet
   */

  parseFragment() {
    const start = this._lexer.token;
    this.expectToken(TokenKind.SPREAD);
    const hasTypeCondition = this.expectOptionalKeyword('on');

    if (!hasTypeCondition && this.peek(TokenKind.NAME)) {
      return this.node(start, {
        kind: Kind.FRAGMENT_SPREAD,
        name: this.parseFragmentName(),
        directives: this.parseDirectives(false),
      });
    }

    return this.node(start, {
      kind: Kind.INLINE_FRAGMENT,
      typeCondition: hasTypeCondition ? this.parseNamedType() : undefined,
      directives: this.parseDirectives(false),
      selectionSet: this.parseSelectionSet(),
    });
  }
  /**
   * FragmentDefinition :
   *   - fragment FragmentName on TypeCondition Directives? SelectionSet
   *
   * TypeCondition : NamedType
   */

  parseFragmentDefinition() {
    const start = this._lexer.token;
    this.expectKeyword('fragment'); // Legacy support for defining variables within fragments changes
    // the grammar of FragmentDefinition:
    //   - fragment FragmentName VariableDefinitions? on TypeCondition Directives? SelectionSet

    if (this._options.allowLegacyFragmentVariables === true) {
      return this.node(start, {
        kind: Kind.FRAGMENT_DEFINITION,
        name: this.parseFragmentName(),
        variableDefinitions: this.parseVariableDefinitions(),
        typeCondition: (this.expectKeyword('on'), this.parseNamedType()),
        directives: this.parseDirectives(false),
        selectionSet: this.parseSelectionSet(),
      });
    }

    return this.node(start, {
      kind: Kind.FRAGMENT_DEFINITION,
      name: this.parseFragmentName(),
      typeCondition: (this.expectKeyword('on'), this.parseNamedType()),
      directives: this.parseDirectives(false),
      selectionSet: this.parseSelectionSet(),
    });
  }
  /**
   * FragmentName : Name but not `on`
   */

  parseFragmentName() {
    if (this._lexer.token.value === 'on') {
      throw this.unexpected();
    }

    return this.parseName();
  } // Implements the parsing rules in the Values section.

  /**
   * Value[Const] :
   *   - [~Const] Variable
   *   - IntValue
   *   - FloatValue
   *   - StringValue
   *   - BooleanValue
   *   - NullValue
   *   - EnumValue
   *   - ListValue[?Const]
   *   - ObjectValue[?Const]
   *
   * BooleanValue : one of `true` `false`
   *
   * NullValue : `null`
   *
   * EnumValue : Name but not `true`, `false` or `null`
   */

  parseValueLiteral(isConst) {
    const token = this._lexer.token;

    switch (token.kind) {
      case TokenKind.BRACKET_L:
        return this.parseList(isConst);

      case TokenKind.BRACE_L:
        return this.parseObject(isConst);

      case TokenKind.INT:
        this.advanceLexer();
        return this.node(token, {
          kind: Kind.INT,
          value: token.value,
        });

      case TokenKind.FLOAT:
        this.advanceLexer();
        return this.node(token, {
          kind: Kind.FLOAT,
          value: token.value,
        });

      case TokenKind.STRING:
      case TokenKind.BLOCK_STRING:
        return this.parseStringLiteral();

      case TokenKind.NAME:
        this.advanceLexer();

        switch (token.value) {
          case 'true':
            return this.node(token, {
              kind: Kind.BOOLEAN,
              value: true,
            });

          case 'false':
            return this.node(token, {
              kind: Kind.BOOLEAN,
              value: false,
            });

          case 'null':
            return this.node(token, {
              kind: Kind.NULL,
            });

          default:
            return this.node(token, {
              kind: Kind.ENUM,
              value: token.value,
            });
        }

      case TokenKind.DOLLAR:
        if (isConst) {
          this.expectToken(TokenKind.DOLLAR);

          if (this._lexer.token.kind === TokenKind.NAME) {
            const varName = this._lexer.token.value;
            throw syntaxError(
              this._lexer.source,
              token.start,
              `Unexpected variable "$${varName}" in constant value.`,
            );
          } else {
            throw this.unexpected(token);
          }
        }

        return this.parseVariable();

      default:
        throw this.unexpected();
    }
  }

  parseConstValueLiteral() {
    return this.parseValueLiteral(true);
  }

  parseStringLiteral() {
    const token = this._lexer.token;
    this.advanceLexer();
    return this.node(token, {
      kind: Kind.STRING,
      value: token.value,
      block: token.kind === TokenKind.BLOCK_STRING,
    });
  }
  /**
   * ListValue[Const] :
   *   - [ ]
   *   - [ Value[?Const]+ ]
   */

  parseList(isConst) {
    const item = () => this.parseValueLiteral(isConst);

    return this.node(this._lexer.token, {
      kind: Kind.LIST,
      values: this.any(TokenKind.BRACKET_L, item, TokenKind.BRACKET_R),
    });
  }
  /**
   * ```
   * ObjectValue[Const] :
   *   - { }
   *   - { ObjectField[?Const]+ }
   * ```
   */

  parseObject(isConst) {
    const item = () => this.parseObjectField(isConst);

    return this.node(this._lexer.token, {
      kind: Kind.OBJECT,
      fields: this.any(TokenKind.BRACE_L, item, TokenKind.BRACE_R),
    });
  }
  /**
   * ObjectField[Const] : Name : Value[?Const]
   */

  parseObjectField(isConst) {
    const start = this._lexer.token;
    const name = this.parseName();
    this.expectToken(TokenKind.COLON);
    return this.node(start, {
      kind: Kind.OBJECT_FIELD,
      name,
      value: this.parseValueLiteral(isConst),
    });
  } // Implements the parsing rules in the Directives section.

  /**
   * Directives[Const] : Directive[?Const]+
   */

  parseDirectives(isConst) {
    const directives = [];

    while (this.peek(TokenKind.AT)) {
      directives.push(this.parseDirective(isConst));
    }

    return directives;
  }

  parseConstDirectives() {
    return this.parseDirectives(true);
  }
  /**
   * ```
   * Directive[Const] : @ Name Arguments[?Const]?
   * ```
   */

  parseDirective(isConst) {
    const start = this._lexer.token;
    this.expectToken(TokenKind.AT);
    return this.node(start, {
      kind: Kind.DIRECTIVE,
      name: this.parseName(),
      arguments: this.parseArguments(isConst),
    });
  } // Implements the parsing rules in the Types section.

  /**
   * Type :
   *   - NamedType
   *   - ListType
   *   - NonNullType
   */

  parseTypeReference() {
    const start = this._lexer.token;
    let type;

    if (this.expectOptionalToken(TokenKind.BRACKET_L)) {
      const innerType = this.parseTypeReference();
      this.expectToken(TokenKind.BRACKET_R);
      type = this.node(start, {
        kind: Kind.LIST_TYPE,
        type: innerType,
      });
    } else {
      type = this.parseNamedType();
    }

    if (this.expectOptionalToken(TokenKind.BANG)) {
      return this.node(start, {
        kind: Kind.NON_NULL_TYPE,
        type,
      });
    }

    return type;
  }
  /**
   * NamedType : Name
   */

  parseNamedType() {
    return this.node(this._lexer.token, {
      kind: Kind.NAMED_TYPE,
      name: this.parseName(),
    });
  } // Implements the parsing rules in the Type Definition section.

  peekDescription() {
    return this.peek(TokenKind.STRING) || this.peek(TokenKind.BLOCK_STRING);
  }
  /**
   * Description : StringValue
   */

  parseDescription() {
    if (this.peekDescription()) {
      return this.parseStringLiteral();
    }
  }
  /**
   * ```
   * SchemaDefinition : Description? schema Directives[Const]? { OperationTypeDefinition+ }
   * ```
   */

  parseSchemaDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('schema');
    const directives = this.parseConstDirectives();
    const operationTypes = this.many(
      TokenKind.BRACE_L,
      this.parseOperationTypeDefinition,
      TokenKind.BRACE_R,
    );
    return this.node(start, {
      kind: Kind.SCHEMA_DEFINITION,
      description,
      directives,
      operationTypes,
    });
  }
  /**
   * OperationTypeDefinition : OperationType : NamedType
   */

  parseOperationTypeDefinition() {
    const start = this._lexer.token;
    const operation = this.parseOperationType();
    this.expectToken(TokenKind.COLON);
    const type = this.parseNamedType();
    return this.node(start, {
      kind: Kind.OPERATION_TYPE_DEFINITION,
      operation,
      type,
    });
  }
  /**
   * ScalarTypeDefinition : Description? scalar Name Directives[Const]?
   */

  parseScalarTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('scalar');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.SCALAR_TYPE_DEFINITION,
      description,
      name,
      directives,
    });
  }
  /**
   * ObjectTypeDefinition :
   *   Description?
   *   type Name ImplementsInterfaces? Directives[Const]? FieldsDefinition?
   */

  parseObjectTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('type');
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();
    return this.node(start, {
      kind: Kind.OBJECT_TYPE_DEFINITION,
      description,
      name,
      interfaces,
      directives,
      fields,
    });
  }
  /**
   * ImplementsInterfaces :
   *   - implements `&`? NamedType
   *   - ImplementsInterfaces & NamedType
   */

  parseImplementsInterfaces() {
    return this.expectOptionalKeyword('implements')
      ? this.delimitedMany(TokenKind.AMP, this.parseNamedType)
      : [];
  }
  /**
   * ```
   * FieldsDefinition : { FieldDefinition+ }
   * ```
   */

  parseFieldsDefinition() {
    return this.optionalMany(
      TokenKind.BRACE_L,
      this.parseFieldDefinition,
      TokenKind.BRACE_R,
    );
  }
  /**
   * FieldDefinition :
   *   - Description? Name ArgumentsDefinition? : Type Directives[Const]?
   */

  parseFieldDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    const name = this.parseName();
    const args = this.parseArgumentDefs();
    this.expectToken(TokenKind.COLON);
    const type = this.parseTypeReference();
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.FIELD_DEFINITION,
      description,
      name,
      arguments: args,
      type,
      directives,
    });
  }
  /**
   * ArgumentsDefinition : ( InputValueDefinition+ )
   */

  parseArgumentDefs() {
    return this.optionalMany(
      TokenKind.PAREN_L,
      this.parseInputValueDef,
      TokenKind.PAREN_R,
    );
  }
  /**
   * InputValueDefinition :
   *   - Description? Name : Type DefaultValue? Directives[Const]?
   */

  parseInputValueDef() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    const name = this.parseName();
    this.expectToken(TokenKind.COLON);
    const type = this.parseTypeReference();
    let defaultValue;

    if (this.expectOptionalToken(TokenKind.EQUALS)) {
      defaultValue = this.parseConstValueLiteral();
    }

    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.INPUT_VALUE_DEFINITION,
      description,
      name,
      type,
      defaultValue,
      directives,
    });
  }
  /**
   * InterfaceTypeDefinition :
   *   - Description? interface Name Directives[Const]? FieldsDefinition?
   */

  parseInterfaceTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('interface');
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();
    return this.node(start, {
      kind: Kind.INTERFACE_TYPE_DEFINITION,
      description,
      name,
      interfaces,
      directives,
      fields,
    });
  }
  /**
   * UnionTypeDefinition :
   *   - Description? union Name Directives[Const]? UnionMemberTypes?
   */

  parseUnionTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('union');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const types = this.parseUnionMemberTypes();
    return this.node(start, {
      kind: Kind.UNION_TYPE_DEFINITION,
      description,
      name,
      directives,
      types,
    });
  }
  /**
   * UnionMemberTypes :
   *   - = `|`? NamedType
   *   - UnionMemberTypes | NamedType
   */

  parseUnionMemberTypes() {
    return this.expectOptionalToken(TokenKind.EQUALS)
      ? this.delimitedMany(TokenKind.PIPE, this.parseNamedType)
      : [];
  }
  /**
   * EnumTypeDefinition :
   *   - Description? enum Name Directives[Const]? EnumValuesDefinition?
   */

  parseEnumTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('enum');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const values = this.parseEnumValuesDefinition();
    return this.node(start, {
      kind: Kind.ENUM_TYPE_DEFINITION,
      description,
      name,
      directives,
      values,
    });
  }
  /**
   * ```
   * EnumValuesDefinition : { EnumValueDefinition+ }
   * ```
   */

  parseEnumValuesDefinition() {
    return this.optionalMany(
      TokenKind.BRACE_L,
      this.parseEnumValueDefinition,
      TokenKind.BRACE_R,
    );
  }
  /**
   * EnumValueDefinition : Description? EnumValue Directives[Const]?
   */

  parseEnumValueDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    const name = this.parseEnumValueName();
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.ENUM_VALUE_DEFINITION,
      description,
      name,
      directives,
    });
  }
  /**
   * EnumValue : Name but not `true`, `false` or `null`
   */

  parseEnumValueName() {
    if (
      this._lexer.token.value === 'true' ||
      this._lexer.token.value === 'false' ||
      this._lexer.token.value === 'null'
    ) {
      throw syntaxError(
        this._lexer.source,
        this._lexer.token.start,
        `${getTokenDesc(
          this._lexer.token,
        )} is reserved and cannot be used for an enum value.`,
      );
    }

    return this.parseName();
  }
  /**
   * InputObjectTypeDefinition :
   *   - Description? input Name Directives[Const]? InputFieldsDefinition?
   */

  parseInputObjectTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('input');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const fields = this.parseInputFieldsDefinition();
    return this.node(start, {
      kind: Kind.INPUT_OBJECT_TYPE_DEFINITION,
      description,
      name,
      directives,
      fields,
    });
  }
  /**
   * ```
   * InputFieldsDefinition : { InputValueDefinition+ }
   * ```
   */

  parseInputFieldsDefinition() {
    return this.optionalMany(
      TokenKind.BRACE_L,
      this.parseInputValueDef,
      TokenKind.BRACE_R,
    );
  }
  /**
   * TypeSystemExtension :
   *   - SchemaExtension
   *   - TypeExtension
   *
   * TypeExtension :
   *   - ScalarTypeExtension
   *   - ObjectTypeExtension
   *   - InterfaceTypeExtension
   *   - UnionTypeExtension
   *   - EnumTypeExtension
   *   - InputObjectTypeDefinition
   */

  parseTypeSystemExtension() {
    const keywordToken = this._lexer.lookahead();

    if (keywordToken.kind === TokenKind.NAME) {
      switch (keywordToken.value) {
        case 'schema':
          return this.parseSchemaExtension();

        case 'scalar':
          return this.parseScalarTypeExtension();

        case 'type':
          return this.parseObjectTypeExtension();

        case 'interface':
          return this.parseInterfaceTypeExtension();

        case 'union':
          return this.parseUnionTypeExtension();

        case 'enum':
          return this.parseEnumTypeExtension();

        case 'input':
          return this.parseInputObjectTypeExtension();
      }
    }

    throw this.unexpected(keywordToken);
  }
  /**
   * ```
   * SchemaExtension :
   *  - extend schema Directives[Const]? { OperationTypeDefinition+ }
   *  - extend schema Directives[Const]
   * ```
   */

  parseSchemaExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('schema');
    const directives = this.parseConstDirectives();
    const operationTypes = this.optionalMany(
      TokenKind.BRACE_L,
      this.parseOperationTypeDefinition,
      TokenKind.BRACE_R,
    );

    if (directives.length === 0 && operationTypes.length === 0) {
      throw this.unexpected();
    }

    return this.node(start, {
      kind: Kind.SCHEMA_EXTENSION,
      directives,
      operationTypes,
    });
  }
  /**
   * ScalarTypeExtension :
   *   - extend scalar Name Directives[Const]
   */

  parseScalarTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('scalar');
    const name = this.parseName();
    const directives = this.parseConstDirectives();

    if (directives.length === 0) {
      throw this.unexpected();
    }

    return this.node(start, {
      kind: Kind.SCALAR_TYPE_EXTENSION,
      name,
      directives,
    });
  }
  /**
   * ObjectTypeExtension :
   *  - extend type Name ImplementsInterfaces? Directives[Const]? FieldsDefinition
   *  - extend type Name ImplementsInterfaces? Directives[Const]
   *  - extend type Name ImplementsInterfaces
   */

  parseObjectTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('type');
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();

    if (
      interfaces.length === 0 &&
      directives.length === 0 &&
      fields.length === 0
    ) {
      throw this.unexpected();
    }

    return this.node(start, {
      kind: Kind.OBJECT_TYPE_EXTENSION,
      name,
      interfaces,
      directives,
      fields,
    });
  }
  /**
   * InterfaceTypeExtension :
   *  - extend interface Name ImplementsInterfaces? Directives[Const]? FieldsDefinition
   *  - extend interface Name ImplementsInterfaces? Directives[Const]
   *  - extend interface Name ImplementsInterfaces
   */

  parseInterfaceTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('interface');
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();

    if (
      interfaces.length === 0 &&
      directives.length === 0 &&
      fields.length === 0
    ) {
      throw this.unexpected();
    }

    return this.node(start, {
      kind: Kind.INTERFACE_TYPE_EXTENSION,
      name,
      interfaces,
      directives,
      fields,
    });
  }
  /**
   * UnionTypeExtension :
   *   - extend union Name Directives[Const]? UnionMemberTypes
   *   - extend union Name Directives[Const]
   */

  parseUnionTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('union');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const types = this.parseUnionMemberTypes();

    if (directives.length === 0 && types.length === 0) {
      throw this.unexpected();
    }

    return this.node(start, {
      kind: Kind.UNION_TYPE_EXTENSION,
      name,
      directives,
      types,
    });
  }
  /**
   * EnumTypeExtension :
   *   - extend enum Name Directives[Const]? EnumValuesDefinition
   *   - extend enum Name Directives[Const]
   */

  parseEnumTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('enum');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const values = this.parseEnumValuesDefinition();

    if (directives.length === 0 && values.length === 0) {
      throw this.unexpected();
    }

    return this.node(start, {
      kind: Kind.ENUM_TYPE_EXTENSION,
      name,
      directives,
      values,
    });
  }
  /**
   * InputObjectTypeExtension :
   *   - extend input Name Directives[Const]? InputFieldsDefinition
   *   - extend input Name Directives[Const]
   */

  parseInputObjectTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword('extend');
    this.expectKeyword('input');
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const fields = this.parseInputFieldsDefinition();

    if (directives.length === 0 && fields.length === 0) {
      throw this.unexpected();
    }

    return this.node(start, {
      kind: Kind.INPUT_OBJECT_TYPE_EXTENSION,
      name,
      directives,
      fields,
    });
  }
  /**
   * ```
   * DirectiveDefinition :
   *   - Description? directive @ Name ArgumentsDefinition? `repeatable`? on DirectiveLocations
   * ```
   */

  parseDirectiveDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword('directive');
    this.expectToken(TokenKind.AT);
    const name = this.parseName();
    const args = this.parseArgumentDefs();
    const repeatable = this.expectOptionalKeyword('repeatable');
    this.expectKeyword('on');
    const locations = this.parseDirectiveLocations();
    return this.node(start, {
      kind: Kind.DIRECTIVE_DEFINITION,
      description,
      name,
      arguments: args,
      repeatable,
      locations,
    });
  }
  /**
   * DirectiveLocations :
   *   - `|`? DirectiveLocation
   *   - DirectiveLocations | DirectiveLocation
   */

  parseDirectiveLocations() {
    return this.delimitedMany(TokenKind.PIPE, this.parseDirectiveLocation);
  }
  /*
   * DirectiveLocation :
   *   - ExecutableDirectiveLocation
   *   - TypeSystemDirectiveLocation
   *
   * ExecutableDirectiveLocation : one of
   *   `QUERY`
   *   `MUTATION`
   *   `SUBSCRIPTION`
   *   `FIELD`
   *   `FRAGMENT_DEFINITION`
   *   `FRAGMENT_SPREAD`
   *   `INLINE_FRAGMENT`
   *
   * TypeSystemDirectiveLocation : one of
   *   `SCHEMA`
   *   `SCALAR`
   *   `OBJECT`
   *   `FIELD_DEFINITION`
   *   `ARGUMENT_DEFINITION`
   *   `INTERFACE`
   *   `UNION`
   *   `ENUM`
   *   `ENUM_VALUE`
   *   `INPUT_OBJECT`
   *   `INPUT_FIELD_DEFINITION`
   */

  parseDirectiveLocation() {
    const start = this._lexer.token;
    const name = this.parseName();

    if (Object.prototype.hasOwnProperty.call(DirectiveLocation, name.value)) {
      return name;
    }

    throw this.unexpected(start);
  } // Core parsing utility functions

  /**
   * Returns a node that, if configured to do so, sets a "loc" field as a
   * location object, used to identify the place in the source that created a
   * given parsed object.
   */

  node(startToken, node) {
    if (this._options.noLocation !== true) {
      node.loc = new Location(
        startToken,
        this._lexer.lastToken,
        this._lexer.source,
      );
    }

    return node;
  }
  /**
   * Determines if the next token is of a given kind
   */

  peek(kind) {
    return this._lexer.token.kind === kind;
  }
  /**
   * If the next token is of the given kind, return that token after advancing the lexer.
   * Otherwise, do not change the parser state and throw an error.
   */

  expectToken(kind) {
    const token = this._lexer.token;

    if (token.kind === kind) {
      this.advanceLexer();
      return token;
    }

    throw syntaxError(
      this._lexer.source,
      token.start,
      `Expected ${getTokenKindDesc(kind)}, found ${getTokenDesc(token)}.`,
    );
  }
  /**
   * If the next token is of the given kind, return "true" after advancing the lexer.
   * Otherwise, do not change the parser state and return "false".
   */

  expectOptionalToken(kind) {
    const token = this._lexer.token;

    if (token.kind === kind) {
      this.advanceLexer();
      return true;
    }

    return false;
  }
  /**
   * If the next token is a given keyword, advance the lexer.
   * Otherwise, do not change the parser state and throw an error.
   */

  expectKeyword(value) {
    const token = this._lexer.token;

    if (token.kind === TokenKind.NAME && token.value === value) {
      this.advanceLexer();
    } else {
      throw syntaxError(
        this._lexer.source,
        token.start,
        `Expected "${value}", found ${getTokenDesc(token)}.`,
      );
    }
  }
  /**
   * If the next token is a given keyword, return "true" after advancing the lexer.
   * Otherwise, do not change the parser state and return "false".
   */

  expectOptionalKeyword(value) {
    const token = this._lexer.token;

    if (token.kind === TokenKind.NAME && token.value === value) {
      this.advanceLexer();
      return true;
    }

    return false;
  }
  /**
   * Helper function for creating an error when an unexpected lexed token is encountered.
   */

  unexpected(atToken) {
    const token =
      atToken !== null && atToken !== void 0 ? atToken : this._lexer.token;
    return syntaxError(
      this._lexer.source,
      token.start,
      `Unexpected ${getTokenDesc(token)}.`,
    );
  }
  /**
   * Returns a possibly empty list of parse nodes, determined by the parseFn.
   * This list begins with a lex token of openKind and ends with a lex token of closeKind.
   * Advances the parser to the next lex token after the closing token.
   */

  any(openKind, parseFn, closeKind) {
    this.expectToken(openKind);
    const nodes = [];

    while (!this.expectOptionalToken(closeKind)) {
      nodes.push(parseFn.call(this));
    }

    return nodes;
  }
  /**
   * Returns a list of parse nodes, determined by the parseFn.
   * It can be empty only if open token is missing otherwise it will always return non-empty list
   * that begins with a lex token of openKind and ends with a lex token of closeKind.
   * Advances the parser to the next lex token after the closing token.
   */

  optionalMany(openKind, parseFn, closeKind) {
    if (this.expectOptionalToken(openKind)) {
      const nodes = [];

      do {
        nodes.push(parseFn.call(this));
      } while (!this.expectOptionalToken(closeKind));

      return nodes;
    }

    return [];
  }
  /**
   * Returns a non-empty list of parse nodes, determined by the parseFn.
   * This list begins with a lex token of openKind and ends with a lex token of closeKind.
   * Advances the parser to the next lex token after the closing token.
   */

  many(openKind, parseFn, closeKind) {
    this.expectToken(openKind);
    const nodes = [];

    do {
      nodes.push(parseFn.call(this));
    } while (!this.expectOptionalToken(closeKind));

    return nodes;
  }
  /**
   * Returns a non-empty list of parse nodes, determined by the parseFn.
   * This list may begin with a lex token of delimiterKind followed by items separated by lex tokens of tokenKind.
   * Advances the parser to the next lex token after last item in the list.
   */

  delimitedMany(delimiterKind, parseFn) {
    this.expectOptionalToken(delimiterKind);
    const nodes = [];

    do {
      nodes.push(parseFn.call(this));
    } while (this.expectOptionalToken(delimiterKind));

    return nodes;
  }

  advanceLexer() {
    const { maxTokens } = this._options;

    const token = this._lexer.advance();

    if (maxTokens !== undefined && token.kind !== TokenKind.EOF) {
      ++this._tokenCounter;

      if (this._tokenCounter > maxTokens) {
        throw syntaxError(
          this._lexer.source,
          token.start,
          `Document contains more that ${maxTokens} tokens. Parsing aborted.`,
        );
      }
    }
  }
}
/**
 * A helper function to describe a token as a string for debugging.
 */

function getTokenDesc(token) {
  const value = token.value;
  return getTokenKindDesc(token.kind) + (value != null ? ` "${value}"` : '');
}
/**
 * A helper function to describe a token kind as a string for debugging.
 */

function getTokenKindDesc(kind) {
  return isPunctuatorTokenKind(kind) ? `"${kind}"` : kind;
}

const MAX_SUGGESTIONS = 5;
/**
 * Given [ A, B, C ] return ' Did you mean A, B, or C?'.
 */

function didYouMean(firstArg, secondArg) {
  const [subMessage, suggestionsArg] = secondArg
    ? [firstArg, secondArg]
    : [undefined, firstArg];
  let message = ' Did you mean ';

  if (subMessage) {
    message += subMessage + ' ';
  }

  const suggestions = suggestionsArg.map((x) => `"${x}"`);

  switch (suggestions.length) {
    case 0:
      return '';

    case 1:
      return message + suggestions[0] + '?';

    case 2:
      return message + suggestions[0] + ' or ' + suggestions[1] + '?';
  }

  const selected = suggestions.slice(0, MAX_SUGGESTIONS);
  const lastItem = selected.pop();
  return message + selected.join(', ') + ', or ' + lastItem + '?';
}

/**
 * Returns the first argument it receives.
 */
function identityFunc(x) {
  return x;
}

/**
 * Creates a keyed JS object from an array, given a function to produce the keys
 * for each value in the array.
 *
 * This provides a convenient lookup for the array items if the key function
 * produces unique results.
 * ```ts
 * const phoneBook = [
 *   { name: 'Jon', num: '555-1234' },
 *   { name: 'Jenny', num: '867-5309' }
 * ]
 *
 * const entriesByName = keyMap(
 *   phoneBook,
 *   entry => entry.name
 * )
 *
 * // {
 * //   Jon: { name: 'Jon', num: '555-1234' },
 * //   Jenny: { name: 'Jenny', num: '867-5309' }
 * // }
 *
 * const jennyEntry = entriesByName['Jenny']
 *
 * // { name: 'Jenny', num: '857-6309' }
 * ```
 */
function keyMap(list, keyFn) {
  const result = Object.create(null);

  for (const item of list) {
    result[keyFn(item)] = item;
  }

  return result;
}

/**
 * Creates a keyed JS object from an array, given a function to produce the keys
 * and a function to produce the values from each item in the array.
 * ```ts
 * const phoneBook = [
 *   { name: 'Jon', num: '555-1234' },
 *   { name: 'Jenny', num: '867-5309' }
 * ]
 *
 * // { Jon: '555-1234', Jenny: '867-5309' }
 * const phonesByName = keyValMap(
 *   phoneBook,
 *   entry => entry.name,
 *   entry => entry.num
 * )
 * ```
 */
function keyValMap(list, keyFn, valFn) {
  const result = Object.create(null);

  for (const item of list) {
    result[keyFn(item)] = valFn(item);
  }

  return result;
}

/**
 * Creates an object map with the same keys as `map` and values generated by
 * running each value of `map` thru `fn`.
 */
function mapValue(map, fn) {
  const result = Object.create(null);

  for (const key of Object.keys(map)) {
    result[key] = fn(map[key], key);
  }

  return result;
}

/**
 * Returns a number indicating whether a reference string comes before, or after,
 * or is the same as the given string in natural sort order.
 *
 * See: https://en.wikipedia.org/wiki/Natural_sort_order
 *
 */
function naturalCompare(aStr, bStr) {
  let aIndex = 0;
  let bIndex = 0;

  while (aIndex < aStr.length && bIndex < bStr.length) {
    let aChar = aStr.charCodeAt(aIndex);
    let bChar = bStr.charCodeAt(bIndex);

    if (isDigit(aChar) && isDigit(bChar)) {
      let aNum = 0;

      do {
        ++aIndex;
        aNum = aNum * 10 + aChar - DIGIT_0;
        aChar = aStr.charCodeAt(aIndex);
      } while (isDigit(aChar) && aNum > 0);

      let bNum = 0;

      do {
        ++bIndex;
        bNum = bNum * 10 + bChar - DIGIT_0;
        bChar = bStr.charCodeAt(bIndex);
      } while (isDigit(bChar) && bNum > 0);

      if (aNum < bNum) {
        return -1;
      }

      if (aNum > bNum) {
        return 1;
      }
    } else {
      if (aChar < bChar) {
        return -1;
      }

      if (aChar > bChar) {
        return 1;
      }

      ++aIndex;
      ++bIndex;
    }
  }

  return aStr.length - bStr.length;
}
const DIGIT_0 = 48;
const DIGIT_9 = 57;

function isDigit(code) {
  return !isNaN(code) && DIGIT_0 <= code && code <= DIGIT_9;
}

/**
 * Given an invalid input string and a list of valid options, returns a filtered
 * list of valid options sorted based on their similarity with the input.
 */

function suggestionList(input, options) {
  const optionsByDistance = Object.create(null);
  const lexicalDistance = new LexicalDistance(input);
  const threshold = Math.floor(input.length * 0.4) + 1;

  for (const option of options) {
    const distance = lexicalDistance.measure(option, threshold);

    if (distance !== undefined) {
      optionsByDistance[option] = distance;
    }
  }

  return Object.keys(optionsByDistance).sort((a, b) => {
    const distanceDiff = optionsByDistance[a] - optionsByDistance[b];
    return distanceDiff !== 0 ? distanceDiff : naturalCompare(a, b);
  });
}
/**
 * Computes the lexical distance between strings A and B.
 *
 * The "distance" between two strings is given by counting the minimum number
 * of edits needed to transform string A into string B. An edit can be an
 * insertion, deletion, or substitution of a single character, or a swap of two
 * adjacent characters.
 *
 * Includes a custom alteration from Damerau-Levenshtein to treat case changes
 * as a single edit which helps identify mis-cased values with an edit distance
 * of 1.
 *
 * This distance can be useful for detecting typos in input or sorting
 */

class LexicalDistance {
  constructor(input) {
    this._input = input;
    this._inputLowerCase = input.toLowerCase();
    this._inputArray = stringToArray(this._inputLowerCase);
    this._rows = [
      new Array(input.length + 1).fill(0),
      new Array(input.length + 1).fill(0),
      new Array(input.length + 1).fill(0),
    ];
  }

  measure(option, threshold) {
    if (this._input === option) {
      return 0;
    }

    const optionLowerCase = option.toLowerCase(); // Any case change counts as a single edit

    if (this._inputLowerCase === optionLowerCase) {
      return 1;
    }

    let a = stringToArray(optionLowerCase);
    let b = this._inputArray;

    if (a.length < b.length) {
      const tmp = a;
      a = b;
      b = tmp;
    }

    const aLength = a.length;
    const bLength = b.length;

    if (aLength - bLength > threshold) {
      return undefined;
    }

    const rows = this._rows;

    for (let j = 0; j <= bLength; j++) {
      rows[0][j] = j;
    }

    for (let i = 1; i <= aLength; i++) {
      const upRow = rows[(i - 1) % 3];
      const currentRow = rows[i % 3];
      let smallestCell = (currentRow[0] = i);

      for (let j = 1; j <= bLength; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let currentCell = Math.min(
          upRow[j] + 1, // delete
          currentRow[j - 1] + 1, // insert
          upRow[j - 1] + cost, // substitute
        );

        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          // transposition
          const doubleDiagonalCell = rows[(i - 2) % 3][j - 2];
          currentCell = Math.min(currentCell, doubleDiagonalCell + 1);
        }

        if (currentCell < smallestCell) {
          smallestCell = currentCell;
        }

        currentRow[j] = currentCell;
      } // Early exit, since distance can't go smaller than smallest element of the previous row.

      if (smallestCell > threshold) {
        return undefined;
      }
    }

    const distance = rows[aLength % 3][bLength];
    return distance <= threshold ? distance : undefined;
  }
}

function stringToArray(str) {
  const strLength = str.length;
  const array = new Array(strLength);

  for (let i = 0; i < strLength; ++i) {
    array[i] = str.charCodeAt(i);
  }

  return array;
}

function toObjMap(obj) {
  if (obj == null) {
    return Object.create(null);
  }

  if (Object.getPrototypeOf(obj) === null) {
    return obj;
  }

  const map = Object.create(null);

  for (const [key, value] of Object.entries(obj)) {
    map[key] = value;
  }

  return map;
}

/**
 * Prints a string as a GraphQL StringValue literal. Replaces control characters
 * and excluded characters (" U+0022 and \\ U+005C) with escape sequences.
 */
function printString(str) {
  return `"${str.replace(escapedRegExp, escapedReplacer)}"`;
} // eslint-disable-next-line no-control-regex

const escapedRegExp = /[\x00-\x1f\x22\x5c\x7f-\x9f]/g;

function escapedReplacer(str) {
  return escapeSequences[str.charCodeAt(0)];
} // prettier-ignore

const escapeSequences = [
  '\\u0000',
  '\\u0001',
  '\\u0002',
  '\\u0003',
  '\\u0004',
  '\\u0005',
  '\\u0006',
  '\\u0007',
  '\\b',
  '\\t',
  '\\n',
  '\\u000B',
  '\\f',
  '\\r',
  '\\u000E',
  '\\u000F',
  '\\u0010',
  '\\u0011',
  '\\u0012',
  '\\u0013',
  '\\u0014',
  '\\u0015',
  '\\u0016',
  '\\u0017',
  '\\u0018',
  '\\u0019',
  '\\u001A',
  '\\u001B',
  '\\u001C',
  '\\u001D',
  '\\u001E',
  '\\u001F',
  '',
  '',
  '\\"',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '', // 2F
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '', // 3F
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '', // 4F
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '\\\\',
  '',
  '',
  '', // 5F
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '', // 6F
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '\\u007F',
  '\\u0080',
  '\\u0081',
  '\\u0082',
  '\\u0083',
  '\\u0084',
  '\\u0085',
  '\\u0086',
  '\\u0087',
  '\\u0088',
  '\\u0089',
  '\\u008A',
  '\\u008B',
  '\\u008C',
  '\\u008D',
  '\\u008E',
  '\\u008F',
  '\\u0090',
  '\\u0091',
  '\\u0092',
  '\\u0093',
  '\\u0094',
  '\\u0095',
  '\\u0096',
  '\\u0097',
  '\\u0098',
  '\\u0099',
  '\\u009A',
  '\\u009B',
  '\\u009C',
  '\\u009D',
  '\\u009E',
  '\\u009F',
];

/**
 * A visitor is provided to visit, it contains the collection of
 * relevant functions to be called during the visitor's traversal.
 */

const BREAK = Object.freeze({});
/**
 * visit() will walk through an AST using a depth-first traversal, calling
 * the visitor's enter function at each node in the traversal, and calling the
 * leave function after visiting that node and all of its child nodes.
 *
 * By returning different values from the enter and leave functions, the
 * behavior of the visitor can be altered, including skipping over a sub-tree of
 * the AST (by returning false), editing the AST by returning a value or null
 * to remove the value, or to stop the whole traversal by returning BREAK.
 *
 * When using visit() to edit an AST, the original AST will not be modified, and
 * a new version of the AST with the changes applied will be returned from the
 * visit function.
 *
 * ```ts
 * const editedAST = visit(ast, {
 *   enter(node, key, parent, path, ancestors) {
 *     // @return
 *     //   undefined: no action
 *     //   false: skip visiting this node
 *     //   visitor.BREAK: stop visiting altogether
 *     //   null: delete this node
 *     //   any value: replace this node with the returned value
 *   },
 *   leave(node, key, parent, path, ancestors) {
 *     // @return
 *     //   undefined: no action
 *     //   false: no action
 *     //   visitor.BREAK: stop visiting altogether
 *     //   null: delete this node
 *     //   any value: replace this node with the returned value
 *   }
 * });
 * ```
 *
 * Alternatively to providing enter() and leave() functions, a visitor can
 * instead provide functions named the same as the kinds of AST nodes, or
 * enter/leave visitors at a named key, leading to three permutations of the
 * visitor API:
 *
 * 1) Named visitors triggered when entering a node of a specific kind.
 *
 * ```ts
 * visit(ast, {
 *   Kind(node) {
 *     // enter the "Kind" node
 *   }
 * })
 * ```
 *
 * 2) Named visitors that trigger upon entering and leaving a node of a specific kind.
 *
 * ```ts
 * visit(ast, {
 *   Kind: {
 *     enter(node) {
 *       // enter the "Kind" node
 *     }
 *     leave(node) {
 *       // leave the "Kind" node
 *     }
 *   }
 * })
 * ```
 *
 * 3) Generic visitors that trigger upon entering and leaving any node.
 *
 * ```ts
 * visit(ast, {
 *   enter(node) {
 *     // enter any node
 *   },
 *   leave(node) {
 *     // leave any node
 *   }
 * })
 * ```
 */

function visit(root, visitor, visitorKeys = QueryDocumentKeys) {
  const enterLeaveMap = new Map();

  for (const kind of Object.values(Kind)) {
    enterLeaveMap.set(kind, getEnterLeaveForKind(visitor, kind));
  }
  /* eslint-disable no-undef-init */

  let stack = undefined;
  let inArray = Array.isArray(root);
  let keys = [root];
  let index = -1;
  let edits = [];
  let node = root;
  let key = undefined;
  let parent = undefined;
  const path = [];
  const ancestors = [];
  /* eslint-enable no-undef-init */

  do {
    index++;
    const isLeaving = index === keys.length;
    const isEdited = isLeaving && edits.length !== 0;

    if (isLeaving) {
      key = ancestors.length === 0 ? undefined : path[path.length - 1];
      node = parent;
      parent = ancestors.pop();

      if (isEdited) {
        if (inArray) {
          node = node.slice();
          let editOffset = 0;

          for (const [editKey, editValue] of edits) {
            const arrayKey = editKey - editOffset;

            if (editValue === null) {
              node.splice(arrayKey, 1);
              editOffset++;
            } else {
              node[arrayKey] = editValue;
            }
          }
        } else {
          node = Object.defineProperties(
            {},
            Object.getOwnPropertyDescriptors(node),
          );

          for (const [editKey, editValue] of edits) {
            node[editKey] = editValue;
          }
        }
      }

      index = stack.index;
      keys = stack.keys;
      edits = stack.edits;
      inArray = stack.inArray;
      stack = stack.prev;
    } else if (parent) {
      key = inArray ? index : keys[index];
      node = parent[key];

      if (node === null || node === undefined) {
        continue;
      }

      path.push(key);
    }

    let result;

    if (!Array.isArray(node)) {
      var _enterLeaveMap$get, _enterLeaveMap$get2;

      isNode$2(node) || devAssert(false, `Invalid AST Node: ${inspect(node)}.`);
      const visitFn = isLeaving
        ? (_enterLeaveMap$get = enterLeaveMap.get(node.kind)) === null ||
          _enterLeaveMap$get === void 0
          ? void 0
          : _enterLeaveMap$get.leave
        : (_enterLeaveMap$get2 = enterLeaveMap.get(node.kind)) === null ||
          _enterLeaveMap$get2 === void 0
        ? void 0
        : _enterLeaveMap$get2.enter;
      result =
        visitFn === null || visitFn === void 0
          ? void 0
          : visitFn.call(visitor, node, key, parent, path, ancestors);

      if (result === BREAK) {
        break;
      }

      if (result === false) {
        if (!isLeaving) {
          path.pop();
          continue;
        }
      } else if (result !== undefined) {
        edits.push([key, result]);

        if (!isLeaving) {
          if (isNode$2(result)) {
            node = result;
          } else {
            path.pop();
            continue;
          }
        }
      }
    }

    if (result === undefined && isEdited) {
      edits.push([key, node]);
    }

    if (isLeaving) {
      path.pop();
    } else {
      var _node$kind;

      stack = {
        inArray,
        index,
        keys,
        edits,
        prev: stack,
      };
      inArray = Array.isArray(node);
      keys = inArray
        ? node
        : (_node$kind = visitorKeys[node.kind]) !== null &&
          _node$kind !== void 0
        ? _node$kind
        : [];
      index = -1;
      edits = [];

      if (parent) {
        ancestors.push(parent);
      }

      parent = node;
    }
  } while (stack !== undefined);

  if (edits.length !== 0) {
    // New root
    return edits[edits.length - 1][1];
  }

  return root;
}
/**
 * Creates a new visitor instance which delegates to many visitors to run in
 * parallel. Each visitor will be visited for each node before moving on.
 *
 * If a prior visitor edits a node, no following visitors will see that node.
 */

function visitInParallel(visitors) {
  const skipping = new Array(visitors.length).fill(null);
  const mergedVisitor = Object.create(null);

  for (const kind of Object.values(Kind)) {
    let hasVisitor = false;
    const enterList = new Array(visitors.length).fill(undefined);
    const leaveList = new Array(visitors.length).fill(undefined);

    for (let i = 0; i < visitors.length; ++i) {
      const { enter, leave } = getEnterLeaveForKind(visitors[i], kind);
      hasVisitor || (hasVisitor = enter != null || leave != null);
      enterList[i] = enter;
      leaveList[i] = leave;
    }

    if (!hasVisitor) {
      continue;
    }

    const mergedEnterLeave = {
      enter(...args) {
        const node = args[0];

        for (let i = 0; i < visitors.length; i++) {
          if (skipping[i] === null) {
            var _enterList$i;

            const result =
              (_enterList$i = enterList[i]) === null || _enterList$i === void 0
                ? void 0
                : _enterList$i.apply(visitors[i], args);

            if (result === false) {
              skipping[i] = node;
            } else if (result === BREAK) {
              skipping[i] = BREAK;
            } else if (result !== undefined) {
              return result;
            }
          }
        }
      },

      leave(...args) {
        const node = args[0];

        for (let i = 0; i < visitors.length; i++) {
          if (skipping[i] === null) {
            var _leaveList$i;

            const result =
              (_leaveList$i = leaveList[i]) === null || _leaveList$i === void 0
                ? void 0
                : _leaveList$i.apply(visitors[i], args);

            if (result === BREAK) {
              skipping[i] = BREAK;
            } else if (result !== undefined && result !== false) {
              return result;
            }
          } else if (skipping[i] === node) {
            skipping[i] = null;
          }
        }
      },
    };
    mergedVisitor[kind] = mergedEnterLeave;
  }

  return mergedVisitor;
}
/**
 * Given a visitor instance and a node kind, return EnterLeaveVisitor for that kind.
 */

function getEnterLeaveForKind(visitor, kind) {
  const kindVisitor = visitor[kind];

  if (typeof kindVisitor === 'object') {
    // { Kind: { enter() {}, leave() {} } }
    return kindVisitor;
  } else if (typeof kindVisitor === 'function') {
    // { Kind() {} }
    return {
      enter: kindVisitor,
      leave: undefined,
    };
  } // { enter() {}, leave() {} }

  return {
    enter: visitor.enter,
    leave: visitor.leave,
  };
}
/**
 * Given a visitor instance, if it is leaving or not, and a node kind, return
 * the function the visitor runtime should call.
 *
 * @deprecated Please use `getEnterLeaveForKind` instead. Will be removed in v17
 */

/* c8 ignore next 8 */

function getVisitFn(visitor, kind, isLeaving) {
  const { enter, leave } = getEnterLeaveForKind(visitor, kind);
  return isLeaving ? leave : enter;
}

/**
 * Converts an AST into a string, using one set of reasonable
 * formatting rules.
 */

function print(ast) {
  return visit(ast, printDocASTReducer);
}
const MAX_LINE_LENGTH = 80;
const printDocASTReducer = {
  Name: {
    leave: (node) => node.value,
  },
  Variable: {
    leave: (node) => '$' + node.name,
  },
  // Document
  Document: {
    leave: (node) => join(node.definitions, '\n\n'),
  },
  OperationDefinition: {
    leave(node) {
      const varDefs = wrap('(', join(node.variableDefinitions, ', '), ')');
      const prefix = join(
        [
          node.operation,
          join([node.name, varDefs]),
          join(node.directives, ' '),
        ],
        ' ',
      ); // Anonymous queries with no directives or variable definitions can use
      // the query short form.

      return (prefix === 'query' ? '' : prefix + ' ') + node.selectionSet;
    },
  },
  VariableDefinition: {
    leave: ({ variable, type, defaultValue, directives }) =>
      variable +
      ': ' +
      type +
      wrap(' = ', defaultValue) +
      wrap(' ', join(directives, ' ')),
  },
  SelectionSet: {
    leave: ({ selections }) => block(selections),
  },
  Field: {
    leave({ alias, name, arguments: args, directives, selectionSet }) {
      const prefix = wrap('', alias, ': ') + name;
      let argsLine = prefix + wrap('(', join(args, ', '), ')');

      if (argsLine.length > MAX_LINE_LENGTH) {
        argsLine = prefix + wrap('(\n', indent(join(args, '\n')), '\n)');
      }

      return join([argsLine, join(directives, ' '), selectionSet], ' ');
    },
  },
  Argument: {
    leave: ({ name, value }) => name + ': ' + value,
  },
  // Fragments
  FragmentSpread: {
    leave: ({ name, directives }) =>
      '...' + name + wrap(' ', join(directives, ' ')),
  },
  InlineFragment: {
    leave: ({ typeCondition, directives, selectionSet }) =>
      join(
        [
          '...',
          wrap('on ', typeCondition),
          join(directives, ' '),
          selectionSet,
        ],
        ' ',
      ),
  },
  FragmentDefinition: {
    leave: (
      { name, typeCondition, variableDefinitions, directives, selectionSet }, // Note: fragment variable definitions are experimental and may be changed
    ) =>
      // or removed in the future.
      `fragment ${name}${wrap('(', join(variableDefinitions, ', '), ')')} ` +
      `on ${typeCondition} ${wrap('', join(directives, ' '), ' ')}` +
      selectionSet,
  },
  // Value
  IntValue: {
    leave: ({ value }) => value,
  },
  FloatValue: {
    leave: ({ value }) => value,
  },
  StringValue: {
    leave: ({ value, block: isBlockString }) =>
      isBlockString ? printBlockString(value) : printString(value),
  },
  BooleanValue: {
    leave: ({ value }) => (value ? 'true' : 'false'),
  },
  NullValue: {
    leave: () => 'null',
  },
  EnumValue: {
    leave: ({ value }) => value,
  },
  ListValue: {
    leave: ({ values }) => '[' + join(values, ', ') + ']',
  },
  ObjectValue: {
    leave: ({ fields }) => '{' + join(fields, ', ') + '}',
  },
  ObjectField: {
    leave: ({ name, value }) => name + ': ' + value,
  },
  // Directive
  Directive: {
    leave: ({ name, arguments: args }) =>
      '@' + name + wrap('(', join(args, ', '), ')'),
  },
  // Type
  NamedType: {
    leave: ({ name }) => name,
  },
  ListType: {
    leave: ({ type }) => '[' + type + ']',
  },
  NonNullType: {
    leave: ({ type }) => type + '!',
  },
  // Type System Definitions
  SchemaDefinition: {
    leave: ({ description, directives, operationTypes }) =>
      wrap('', description, '\n') +
      join(['schema', join(directives, ' '), block(operationTypes)], ' '),
  },
  OperationTypeDefinition: {
    leave: ({ operation, type }) => operation + ': ' + type,
  },
  ScalarTypeDefinition: {
    leave: ({ description, name, directives }) =>
      wrap('', description, '\n') +
      join(['scalar', name, join(directives, ' ')], ' '),
  },
  ObjectTypeDefinition: {
    leave: ({ description, name, interfaces, directives, fields }) =>
      wrap('', description, '\n') +
      join(
        [
          'type',
          name,
          wrap('implements ', join(interfaces, ' & ')),
          join(directives, ' '),
          block(fields),
        ],
        ' ',
      ),
  },
  FieldDefinition: {
    leave: ({ description, name, arguments: args, type, directives }) =>
      wrap('', description, '\n') +
      name +
      (hasMultilineItems(args)
        ? wrap('(\n', indent(join(args, '\n')), '\n)')
        : wrap('(', join(args, ', '), ')')) +
      ': ' +
      type +
      wrap(' ', join(directives, ' ')),
  },
  InputValueDefinition: {
    leave: ({ description, name, type, defaultValue, directives }) =>
      wrap('', description, '\n') +
      join(
        [name + ': ' + type, wrap('= ', defaultValue), join(directives, ' ')],
        ' ',
      ),
  },
  InterfaceTypeDefinition: {
    leave: ({ description, name, interfaces, directives, fields }) =>
      wrap('', description, '\n') +
      join(
        [
          'interface',
          name,
          wrap('implements ', join(interfaces, ' & ')),
          join(directives, ' '),
          block(fields),
        ],
        ' ',
      ),
  },
  UnionTypeDefinition: {
    leave: ({ description, name, directives, types }) =>
      wrap('', description, '\n') +
      join(
        ['union', name, join(directives, ' '), wrap('= ', join(types, ' | '))],
        ' ',
      ),
  },
  EnumTypeDefinition: {
    leave: ({ description, name, directives, values }) =>
      wrap('', description, '\n') +
      join(['enum', name, join(directives, ' '), block(values)], ' '),
  },
  EnumValueDefinition: {
    leave: ({ description, name, directives }) =>
      wrap('', description, '\n') + join([name, join(directives, ' ')], ' '),
  },
  InputObjectTypeDefinition: {
    leave: ({ description, name, directives, fields }) =>
      wrap('', description, '\n') +
      join(['input', name, join(directives, ' '), block(fields)], ' '),
  },
  DirectiveDefinition: {
    leave: ({ description, name, arguments: args, repeatable, locations }) =>
      wrap('', description, '\n') +
      'directive @' +
      name +
      (hasMultilineItems(args)
        ? wrap('(\n', indent(join(args, '\n')), '\n)')
        : wrap('(', join(args, ', '), ')')) +
      (repeatable ? ' repeatable' : '') +
      ' on ' +
      join(locations, ' | '),
  },
  SchemaExtension: {
    leave: ({ directives, operationTypes }) =>
      join(
        ['extend schema', join(directives, ' '), block(operationTypes)],
        ' ',
      ),
  },
  ScalarTypeExtension: {
    leave: ({ name, directives }) =>
      join(['extend scalar', name, join(directives, ' ')], ' '),
  },
  ObjectTypeExtension: {
    leave: ({ name, interfaces, directives, fields }) =>
      join(
        [
          'extend type',
          name,
          wrap('implements ', join(interfaces, ' & ')),
          join(directives, ' '),
          block(fields),
        ],
        ' ',
      ),
  },
  InterfaceTypeExtension: {
    leave: ({ name, interfaces, directives, fields }) =>
      join(
        [
          'extend interface',
          name,
          wrap('implements ', join(interfaces, ' & ')),
          join(directives, ' '),
          block(fields),
        ],
        ' ',
      ),
  },
  UnionTypeExtension: {
    leave: ({ name, directives, types }) =>
      join(
        [
          'extend union',
          name,
          join(directives, ' '),
          wrap('= ', join(types, ' | ')),
        ],
        ' ',
      ),
  },
  EnumTypeExtension: {
    leave: ({ name, directives, values }) =>
      join(['extend enum', name, join(directives, ' '), block(values)], ' '),
  },
  InputObjectTypeExtension: {
    leave: ({ name, directives, fields }) =>
      join(['extend input', name, join(directives, ' '), block(fields)], ' '),
  },
};
/**
 * Given maybeArray, print an empty string if it is null or empty, otherwise
 * print all items together separated by separator if provided
 */

function join(maybeArray, separator = '') {
  var _maybeArray$filter$jo;

  return (_maybeArray$filter$jo =
    maybeArray === null || maybeArray === void 0
      ? void 0
      : maybeArray.filter((x) => x).join(separator)) !== null &&
    _maybeArray$filter$jo !== void 0
    ? _maybeArray$filter$jo
    : '';
}
/**
 * Given array, print each item on its own line, wrapped in an indented `{ }` block.
 */

function block(array) {
  return wrap('{\n', indent(join(array, '\n')), '\n}');
}
/**
 * If maybeString is not null or empty, then wrap with start and end, otherwise print an empty string.
 */

function wrap(start, maybeString, end = '') {
  return maybeString != null && maybeString !== ''
    ? start + maybeString + end
    : '';
}

function indent(str) {
  return wrap('  ', str.replace(/\n/g, '\n  '));
}

function hasMultilineItems(maybeArray) {
  var _maybeArray$some;

  // FIXME: https://github.com/graphql/graphql-js/issues/2203

  /* c8 ignore next */
  return (_maybeArray$some =
    maybeArray === null || maybeArray === void 0
      ? void 0
      : maybeArray.some((str) => str.includes('\n'))) !== null &&
    _maybeArray$some !== void 0
    ? _maybeArray$some
    : false;
}

/**
 * Produces a JavaScript value given a GraphQL Value AST.
 *
 * Unlike `valueFromAST()`, no type is provided. The resulting JavaScript value
 * will reflect the provided GraphQL value AST.
 *
 * | GraphQL Value        | JavaScript Value |
 * | -------------------- | ---------------- |
 * | Input Object         | Object           |
 * | List                 | Array            |
 * | Boolean              | Boolean          |
 * | String / Enum        | String           |
 * | Int / Float          | Number           |
 * | Null                 | null             |
 *
 */

function valueFromASTUntyped(valueNode, variables) {
  switch (valueNode.kind) {
    case Kind.NULL:
      return null;

    case Kind.INT:
      return parseInt(valueNode.value, 10);

    case Kind.FLOAT:
      return parseFloat(valueNode.value);

    case Kind.STRING:
    case Kind.ENUM:
    case Kind.BOOLEAN:
      return valueNode.value;

    case Kind.LIST:
      return valueNode.values.map((node) =>
        valueFromASTUntyped(node, variables),
      );

    case Kind.OBJECT:
      return keyValMap(
        valueNode.fields,
        (field) => field.name.value,
        (field) => valueFromASTUntyped(field.value, variables),
      );

    case Kind.VARIABLE:
      return variables === null || variables === void 0
        ? void 0
        : variables[valueNode.name.value];
  }
}

/**
 * Upholds the spec rules about naming.
 */

function assertName(name) {
  name != null || devAssert(false, 'Must provide name.');
  typeof name === 'string' || devAssert(false, 'Expected name to be a string.');

  if (name.length === 0) {
    throw new GraphQLError('Expected name to be a non-empty string.');
  }

  for (let i = 1; i < name.length; ++i) {
    if (!isNameContinue(name.charCodeAt(i))) {
      throw new GraphQLError(
        `Names must only contain [_a-zA-Z0-9] but "${name}" does not.`,
      );
    }
  }

  if (!isNameStart(name.charCodeAt(0))) {
    throw new GraphQLError(
      `Names must start with [_a-zA-Z] but "${name}" does not.`,
    );
  }

  return name;
}
/**
 * Upholds the spec rules about naming enum values.
 *
 * @internal
 */

function assertEnumValueName(name) {
  if (name === 'true' || name === 'false' || name === 'null') {
    throw new GraphQLError(`Enum values cannot be named: ${name}`);
  }

  return assertName(name);
}

function isType(type) {
  return (
    isScalarType(type) ||
    isObjectType(type) ||
    isInterfaceType(type) ||
    isUnionType(type) ||
    isEnumType(type) ||
    isInputObjectType(type) ||
    isListType(type) ||
    isNonNullType(type)
  );
}
function assertType(type) {
  if (!isType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL type.`);
  }

  return type;
}
/**
 * There are predicates for each kind of GraphQL type.
 */

function isScalarType(type) {
  return instanceOf(type, GraphQLScalarType);
}
function assertScalarType(type) {
  if (!isScalarType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL Scalar type.`);
  }

  return type;
}
function isObjectType(type) {
  return instanceOf(type, GraphQLObjectType);
}
function assertObjectType(type) {
  if (!isObjectType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL Object type.`);
  }

  return type;
}
function isInterfaceType(type) {
  return instanceOf(type, GraphQLInterfaceType);
}
function assertInterfaceType(type) {
  if (!isInterfaceType(type)) {
    throw new Error(
      `Expected ${inspect(type)} to be a GraphQL Interface type.`,
    );
  }

  return type;
}
function isUnionType(type) {
  return instanceOf(type, GraphQLUnionType);
}
function assertUnionType(type) {
  if (!isUnionType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL Union type.`);
  }

  return type;
}
function isEnumType(type) {
  return instanceOf(type, GraphQLEnumType);
}
function assertEnumType(type) {
  if (!isEnumType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL Enum type.`);
  }

  return type;
}
function isInputObjectType(type) {
  return instanceOf(type, GraphQLInputObjectType);
}
function assertInputObjectType(type) {
  if (!isInputObjectType(type)) {
    throw new Error(
      `Expected ${inspect(type)} to be a GraphQL Input Object type.`,
    );
  }

  return type;
}
function isListType(type) {
  return instanceOf(type, GraphQLList);
}
function assertListType(type) {
  if (!isListType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL List type.`);
  }

  return type;
}
function isNonNullType(type) {
  return instanceOf(type, GraphQLNonNull);
}
function assertNonNullType(type) {
  if (!isNonNullType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL Non-Null type.`);
  }

  return type;
}
/**
 * These types may be used as input types for arguments and directives.
 */

function isInputType(type) {
  return (
    isScalarType(type) ||
    isEnumType(type) ||
    isInputObjectType(type) ||
    (isWrappingType(type) && isInputType(type.ofType))
  );
}
function assertInputType(type) {
  if (!isInputType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL input type.`);
  }

  return type;
}
/**
 * These types may be used as output types as the result of fields.
 */

function isOutputType(type) {
  return (
    isScalarType(type) ||
    isObjectType(type) ||
    isInterfaceType(type) ||
    isUnionType(type) ||
    isEnumType(type) ||
    (isWrappingType(type) && isOutputType(type.ofType))
  );
}
function assertOutputType(type) {
  if (!isOutputType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL output type.`);
  }

  return type;
}
/**
 * These types may describe types which may be leaf values.
 */

function isLeafType(type) {
  return isScalarType(type) || isEnumType(type);
}
function assertLeafType(type) {
  if (!isLeafType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL leaf type.`);
  }

  return type;
}
/**
 * These types may describe the parent context of a selection set.
 */

function isCompositeType(type) {
  return isObjectType(type) || isInterfaceType(type) || isUnionType(type);
}
function assertCompositeType(type) {
  if (!isCompositeType(type)) {
    throw new Error(
      `Expected ${inspect(type)} to be a GraphQL composite type.`,
    );
  }

  return type;
}
/**
 * These types may describe the parent context of a selection set.
 */

function isAbstractType(type) {
  return isInterfaceType(type) || isUnionType(type);
}
function assertAbstractType(type) {
  if (!isAbstractType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL abstract type.`);
  }

  return type;
}
/**
 * List Type Wrapper
 *
 * A list is a wrapping type which points to another type.
 * Lists are often created within the context of defining the fields of
 * an object type.
 *
 * Example:
 *
 * ```ts
 * const PersonType = new GraphQLObjectType({
 *   name: 'Person',
 *   fields: () => ({
 *     parents: { type: new GraphQLList(PersonType) },
 *     children: { type: new GraphQLList(PersonType) },
 *   })
 * })
 * ```
 */

class GraphQLList {
  constructor(ofType) {
    isType(ofType) ||
      devAssert(false, `Expected ${inspect(ofType)} to be a GraphQL type.`);
    this.ofType = ofType;
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLList';
  }

  toString() {
    return '[' + String(this.ofType) + ']';
  }

  toJSON() {
    return this.toString();
  }
}
/**
 * Non-Null Type Wrapper
 *
 * A non-null is a wrapping type which points to another type.
 * Non-null types enforce that their values are never null and can ensure
 * an error is raised if this ever occurs during a request. It is useful for
 * fields which you can make a strong guarantee on non-nullability, for example
 * usually the id field of a database row will never be null.
 *
 * Example:
 *
 * ```ts
 * const RowType = new GraphQLObjectType({
 *   name: 'Row',
 *   fields: () => ({
 *     id: { type: new GraphQLNonNull(GraphQLString) },
 *   })
 * })
 * ```
 * Note: the enforcement of non-nullability occurs within the executor.
 */

class GraphQLNonNull {
  constructor(ofType) {
    isNullableType(ofType) ||
      devAssert(
        false,
        `Expected ${inspect(ofType)} to be a GraphQL nullable type.`,
      );
    this.ofType = ofType;
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLNonNull';
  }

  toString() {
    return String(this.ofType) + '!';
  }

  toJSON() {
    return this.toString();
  }
}
/**
 * These types wrap and modify other types
 */

function isWrappingType(type) {
  return isListType(type) || isNonNullType(type);
}
function assertWrappingType(type) {
  if (!isWrappingType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL wrapping type.`);
  }

  return type;
}
/**
 * These types can all accept null as a value.
 */

function isNullableType(type) {
  return isType(type) && !isNonNullType(type);
}
function assertNullableType(type) {
  if (!isNullableType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL nullable type.`);
  }

  return type;
}
function getNullableType(type) {
  if (type) {
    return isNonNullType(type) ? type.ofType : type;
  }
}
/**
 * These named types do not include modifiers like List or NonNull.
 */

function isNamedType(type) {
  return (
    isScalarType(type) ||
    isObjectType(type) ||
    isInterfaceType(type) ||
    isUnionType(type) ||
    isEnumType(type) ||
    isInputObjectType(type)
  );
}
function assertNamedType(type) {
  if (!isNamedType(type)) {
    throw new Error(`Expected ${inspect(type)} to be a GraphQL named type.`);
  }

  return type;
}
function getNamedType(type) {
  if (type) {
    let unwrappedType = type;

    while (isWrappingType(unwrappedType)) {
      unwrappedType = unwrappedType.ofType;
    }

    return unwrappedType;
  }
}
/**
 * Used while defining GraphQL types to allow for circular references in
 * otherwise immutable type definitions.
 */

function resolveReadonlyArrayThunk(thunk) {
  return typeof thunk === 'function' ? thunk() : thunk;
}
function resolveObjMapThunk(thunk) {
  return typeof thunk === 'function' ? thunk() : thunk;
}
/**
 * Custom extensions
 *
 * @remarks
 * Use a unique identifier name for your extension, for example the name of
 * your library or project. Do not use a shortened identifier as this increases
 * the risk of conflicts. We recommend you add at most one extension field,
 * an object which can contain all the values you need.
 */

/**
 * Scalar Type Definition
 *
 * The leaf values of any request and input values to arguments are
 * Scalars (or Enums) and are defined with a name and a series of functions
 * used to parse input from ast or variables and to ensure validity.
 *
 * If a type's serialize function returns `null` or does not return a value
 * (i.e. it returns `undefined`) then an error will be raised and a `null`
 * value will be returned in the response. It is always better to validate
 *
 * Example:
 *
 * ```ts
 * const OddType = new GraphQLScalarType({
 *   name: 'Odd',
 *   serialize(value) {
 *     if (!Number.isFinite(value)) {
 *       throw new Error(
 *         `Scalar "Odd" cannot represent "${value}" since it is not a finite number.`,
 *       );
 *     }
 *
 *     if (value % 2 === 0) {
 *       throw new Error(`Scalar "Odd" cannot represent "${value}" since it is even.`);
 *     }
 *     return value;
 *   }
 * });
 * ```
 */
class GraphQLScalarType {
  constructor(config) {
    var _config$parseValue,
      _config$serialize,
      _config$parseLiteral,
      _config$extensionASTN;

    const parseValue =
      (_config$parseValue = config.parseValue) !== null &&
      _config$parseValue !== void 0
        ? _config$parseValue
        : identityFunc;
    this.name = assertName(config.name);
    this.description = config.description;
    this.specifiedByURL = config.specifiedByURL;
    this.serialize =
      (_config$serialize = config.serialize) !== null &&
      _config$serialize !== void 0
        ? _config$serialize
        : identityFunc;
    this.parseValue = parseValue;
    this.parseLiteral =
      (_config$parseLiteral = config.parseLiteral) !== null &&
      _config$parseLiteral !== void 0
        ? _config$parseLiteral
        : (node, variables) => parseValue(valueFromASTUntyped(node, variables));
    this.extensions = toObjMap(config.extensions);
    this.astNode = config.astNode;
    this.extensionASTNodes =
      (_config$extensionASTN = config.extensionASTNodes) !== null &&
      _config$extensionASTN !== void 0
        ? _config$extensionASTN
        : [];
    config.specifiedByURL == null ||
      typeof config.specifiedByURL === 'string' ||
      devAssert(
        false,
        `${this.name} must provide "specifiedByURL" as a string, ` +
          `but got: ${inspect(config.specifiedByURL)}.`,
      );
    config.serialize == null ||
      typeof config.serialize === 'function' ||
      devAssert(
        false,
        `${this.name} must provide "serialize" function. If this custom Scalar is also used as an input type, ensure "parseValue" and "parseLiteral" functions are also provided.`,
      );

    if (config.parseLiteral) {
      (typeof config.parseValue === 'function' &&
        typeof config.parseLiteral === 'function') ||
        devAssert(
          false,
          `${this.name} must provide both "parseValue" and "parseLiteral" functions.`,
        );
    }
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLScalarType';
  }

  toConfig() {
    return {
      name: this.name,
      description: this.description,
      specifiedByURL: this.specifiedByURL,
      serialize: this.serialize,
      parseValue: this.parseValue,
      parseLiteral: this.parseLiteral,
      extensions: this.extensions,
      astNode: this.astNode,
      extensionASTNodes: this.extensionASTNodes,
    };
  }

  toString() {
    return this.name;
  }

  toJSON() {
    return this.toString();
  }
}

/**
 * Object Type Definition
 *
 * Almost all of the GraphQL types you define will be object types. Object types
 * have a name, but most importantly describe their fields.
 *
 * Example:
 *
 * ```ts
 * const AddressType = new GraphQLObjectType({
 *   name: 'Address',
 *   fields: {
 *     street: { type: GraphQLString },
 *     number: { type: GraphQLInt },
 *     formatted: {
 *       type: GraphQLString,
 *       resolve(obj) {
 *         return obj.number + ' ' + obj.street
 *       }
 *     }
 *   }
 * });
 * ```
 *
 * When two types need to refer to each other, or a type needs to refer to
 * itself in a field, you can use a function expression (aka a closure or a
 * thunk) to supply the fields lazily.
 *
 * Example:
 *
 * ```ts
 * const PersonType = new GraphQLObjectType({
 *   name: 'Person',
 *   fields: () => ({
 *     name: { type: GraphQLString },
 *     bestFriend: { type: PersonType },
 *   })
 * });
 * ```
 */
class GraphQLObjectType {
  constructor(config) {
    var _config$extensionASTN2;

    this.name = assertName(config.name);
    this.description = config.description;
    this.isTypeOf = config.isTypeOf;
    this.extensions = toObjMap(config.extensions);
    this.astNode = config.astNode;
    this.extensionASTNodes =
      (_config$extensionASTN2 = config.extensionASTNodes) !== null &&
      _config$extensionASTN2 !== void 0
        ? _config$extensionASTN2
        : [];

    this._fields = () => defineFieldMap(config);

    this._interfaces = () => defineInterfaces(config);

    config.isTypeOf == null ||
      typeof config.isTypeOf === 'function' ||
      devAssert(
        false,
        `${this.name} must provide "isTypeOf" as a function, ` +
          `but got: ${inspect(config.isTypeOf)}.`,
      );
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLObjectType';
  }

  getFields() {
    if (typeof this._fields === 'function') {
      this._fields = this._fields();
    }

    return this._fields;
  }

  getInterfaces() {
    if (typeof this._interfaces === 'function') {
      this._interfaces = this._interfaces();
    }

    return this._interfaces;
  }

  toConfig() {
    return {
      name: this.name,
      description: this.description,
      interfaces: this.getInterfaces(),
      fields: fieldsToFieldsConfig(this.getFields()),
      isTypeOf: this.isTypeOf,
      extensions: this.extensions,
      astNode: this.astNode,
      extensionASTNodes: this.extensionASTNodes,
    };
  }

  toString() {
    return this.name;
  }

  toJSON() {
    return this.toString();
  }
}

function defineInterfaces(config) {
  var _config$interfaces;

  const interfaces = resolveReadonlyArrayThunk(
    (_config$interfaces = config.interfaces) !== null &&
      _config$interfaces !== void 0
      ? _config$interfaces
      : [],
  );
  Array.isArray(interfaces) ||
    devAssert(
      false,
      `${config.name} interfaces must be an Array or a function which returns an Array.`,
    );
  return interfaces;
}

function defineFieldMap(config) {
  const fieldMap = resolveObjMapThunk(config.fields);
  isPlainObj(fieldMap) ||
    devAssert(
      false,
      `${config.name} fields must be an object with field names as keys or a function which returns such an object.`,
    );
  return mapValue(fieldMap, (fieldConfig, fieldName) => {
    var _fieldConfig$args;

    isPlainObj(fieldConfig) ||
      devAssert(
        false,
        `${config.name}.${fieldName} field config must be an object.`,
      );
    fieldConfig.resolve == null ||
      typeof fieldConfig.resolve === 'function' ||
      devAssert(
        false,
        `${config.name}.${fieldName} field resolver must be a function if ` +
          `provided, but got: ${inspect(fieldConfig.resolve)}.`,
      );
    const argsConfig =
      (_fieldConfig$args = fieldConfig.args) !== null &&
      _fieldConfig$args !== void 0
        ? _fieldConfig$args
        : {};
    isPlainObj(argsConfig) ||
      devAssert(
        false,
        `${config.name}.${fieldName} args must be an object with argument names as keys.`,
      );
    return {
      name: assertName(fieldName),
      description: fieldConfig.description,
      type: fieldConfig.type,
      args: defineArguments(argsConfig),
      resolve: fieldConfig.resolve,
      subscribe: fieldConfig.subscribe,
      deprecationReason: fieldConfig.deprecationReason,
      extensions: toObjMap(fieldConfig.extensions),
      astNode: fieldConfig.astNode,
    };
  });
}

function defineArguments(config) {
  return Object.entries(config).map(([argName, argConfig]) => ({
    name: assertName(argName),
    description: argConfig.description,
    type: argConfig.type,
    defaultValue: argConfig.defaultValue,
    deprecationReason: argConfig.deprecationReason,
    extensions: toObjMap(argConfig.extensions),
    astNode: argConfig.astNode,
  }));
}

function isPlainObj(obj) {
  return isObjectLike$c(obj) && !Array.isArray(obj);
}

function fieldsToFieldsConfig(fields) {
  return mapValue(fields, (field) => ({
    description: field.description,
    type: field.type,
    args: argsToArgsConfig(field.args),
    resolve: field.resolve,
    subscribe: field.subscribe,
    deprecationReason: field.deprecationReason,
    extensions: field.extensions,
    astNode: field.astNode,
  }));
}
/**
 * @internal
 */

function argsToArgsConfig(args) {
  return keyValMap(
    args,
    (arg) => arg.name,
    (arg) => ({
      description: arg.description,
      type: arg.type,
      defaultValue: arg.defaultValue,
      deprecationReason: arg.deprecationReason,
      extensions: arg.extensions,
      astNode: arg.astNode,
    }),
  );
}
function isRequiredArgument(arg) {
  return isNonNullType(arg.type) && arg.defaultValue === undefined;
}

/**
 * Interface Type Definition
 *
 * When a field can return one of a heterogeneous set of types, a Interface type
 * is used to describe what types are possible, what fields are in common across
 * all types, as well as a function to determine which type is actually used
 * when the field is resolved.
 *
 * Example:
 *
 * ```ts
 * const EntityType = new GraphQLInterfaceType({
 *   name: 'Entity',
 *   fields: {
 *     name: { type: GraphQLString }
 *   }
 * });
 * ```
 */
class GraphQLInterfaceType {
  constructor(config) {
    var _config$extensionASTN3;

    this.name = assertName(config.name);
    this.description = config.description;
    this.resolveType = config.resolveType;
    this.extensions = toObjMap(config.extensions);
    this.astNode = config.astNode;
    this.extensionASTNodes =
      (_config$extensionASTN3 = config.extensionASTNodes) !== null &&
      _config$extensionASTN3 !== void 0
        ? _config$extensionASTN3
        : [];
    this._fields = defineFieldMap.bind(undefined, config);
    this._interfaces = defineInterfaces.bind(undefined, config);
    config.resolveType == null ||
      typeof config.resolveType === 'function' ||
      devAssert(
        false,
        `${this.name} must provide "resolveType" as a function, ` +
          `but got: ${inspect(config.resolveType)}.`,
      );
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLInterfaceType';
  }

  getFields() {
    if (typeof this._fields === 'function') {
      this._fields = this._fields();
    }

    return this._fields;
  }

  getInterfaces() {
    if (typeof this._interfaces === 'function') {
      this._interfaces = this._interfaces();
    }

    return this._interfaces;
  }

  toConfig() {
    return {
      name: this.name,
      description: this.description,
      interfaces: this.getInterfaces(),
      fields: fieldsToFieldsConfig(this.getFields()),
      resolveType: this.resolveType,
      extensions: this.extensions,
      astNode: this.astNode,
      extensionASTNodes: this.extensionASTNodes,
    };
  }

  toString() {
    return this.name;
  }

  toJSON() {
    return this.toString();
  }
}

/**
 * Union Type Definition
 *
 * When a field can return one of a heterogeneous set of types, a Union type
 * is used to describe what types are possible as well as providing a function
 * to determine which type is actually used when the field is resolved.
 *
 * Example:
 *
 * ```ts
 * const PetType = new GraphQLUnionType({
 *   name: 'Pet',
 *   types: [ DogType, CatType ],
 *   resolveType(value) {
 *     if (value instanceof Dog) {
 *       return DogType;
 *     }
 *     if (value instanceof Cat) {
 *       return CatType;
 *     }
 *   }
 * });
 * ```
 */
class GraphQLUnionType {
  constructor(config) {
    var _config$extensionASTN4;

    this.name = assertName(config.name);
    this.description = config.description;
    this.resolveType = config.resolveType;
    this.extensions = toObjMap(config.extensions);
    this.astNode = config.astNode;
    this.extensionASTNodes =
      (_config$extensionASTN4 = config.extensionASTNodes) !== null &&
      _config$extensionASTN4 !== void 0
        ? _config$extensionASTN4
        : [];
    this._types = defineTypes.bind(undefined, config);
    config.resolveType == null ||
      typeof config.resolveType === 'function' ||
      devAssert(
        false,
        `${this.name} must provide "resolveType" as a function, ` +
          `but got: ${inspect(config.resolveType)}.`,
      );
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLUnionType';
  }

  getTypes() {
    if (typeof this._types === 'function') {
      this._types = this._types();
    }

    return this._types;
  }

  toConfig() {
    return {
      name: this.name,
      description: this.description,
      types: this.getTypes(),
      resolveType: this.resolveType,
      extensions: this.extensions,
      astNode: this.astNode,
      extensionASTNodes: this.extensionASTNodes,
    };
  }

  toString() {
    return this.name;
  }

  toJSON() {
    return this.toString();
  }
}

function defineTypes(config) {
  const types = resolveReadonlyArrayThunk(config.types);
  Array.isArray(types) ||
    devAssert(
      false,
      `Must provide Array of types or a function which returns such an array for Union ${config.name}.`,
    );
  return types;
}

/**
 * Enum Type Definition
 *
 * Some leaf values of requests and input values are Enums. GraphQL serializes
 * Enum values as strings, however internally Enums can be represented by any
 * kind of type, often integers.
 *
 * Example:
 *
 * ```ts
 * const RGBType = new GraphQLEnumType({
 *   name: 'RGB',
 *   values: {
 *     RED: { value: 0 },
 *     GREEN: { value: 1 },
 *     BLUE: { value: 2 }
 *   }
 * });
 * ```
 *
 * Note: If a value is not provided in a definition, the name of the enum value
 * will be used as its internal value.
 */
class GraphQLEnumType {
  /* <T> */
  constructor(config) {
    var _config$extensionASTN5;

    this.name = assertName(config.name);
    this.description = config.description;
    this.extensions = toObjMap(config.extensions);
    this.astNode = config.astNode;
    this.extensionASTNodes =
      (_config$extensionASTN5 = config.extensionASTNodes) !== null &&
      _config$extensionASTN5 !== void 0
        ? _config$extensionASTN5
        : [];
    this._values = defineEnumValues(this.name, config.values);
    this._valueLookup = new Map(
      this._values.map((enumValue) => [enumValue.value, enumValue]),
    );
    this._nameLookup = keyMap(this._values, (value) => value.name);
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLEnumType';
  }

  getValues() {
    return this._values;
  }

  getValue(name) {
    return this._nameLookup[name];
  }

  serialize(outputValue) {
    const enumValue = this._valueLookup.get(outputValue);

    if (enumValue === undefined) {
      throw new GraphQLError(
        `Enum "${this.name}" cannot represent value: ${inspect(outputValue)}`,
      );
    }

    return enumValue.name;
  }

  parseValue(inputValue) /* T */
  {
    if (typeof inputValue !== 'string') {
      const valueStr = inspect(inputValue);
      throw new GraphQLError(
        `Enum "${this.name}" cannot represent non-string value: ${valueStr}.` +
          didYouMeanEnumValue(this, valueStr),
      );
    }

    const enumValue = this.getValue(inputValue);

    if (enumValue == null) {
      throw new GraphQLError(
        `Value "${inputValue}" does not exist in "${this.name}" enum.` +
          didYouMeanEnumValue(this, inputValue),
      );
    }

    return enumValue.value;
  }

  parseLiteral(valueNode, _variables) /* T */
  {
    // Note: variables will be resolved to a value before calling this function.
    if (valueNode.kind !== Kind.ENUM) {
      const valueStr = print(valueNode);
      throw new GraphQLError(
        `Enum "${this.name}" cannot represent non-enum value: ${valueStr}.` +
          didYouMeanEnumValue(this, valueStr),
        {
          nodes: valueNode,
        },
      );
    }

    const enumValue = this.getValue(valueNode.value);

    if (enumValue == null) {
      const valueStr = print(valueNode);
      throw new GraphQLError(
        `Value "${valueStr}" does not exist in "${this.name}" enum.` +
          didYouMeanEnumValue(this, valueStr),
        {
          nodes: valueNode,
        },
      );
    }

    return enumValue.value;
  }

  toConfig() {
    const values = keyValMap(
      this.getValues(),
      (value) => value.name,
      (value) => ({
        description: value.description,
        value: value.value,
        deprecationReason: value.deprecationReason,
        extensions: value.extensions,
        astNode: value.astNode,
      }),
    );
    return {
      name: this.name,
      description: this.description,
      values,
      extensions: this.extensions,
      astNode: this.astNode,
      extensionASTNodes: this.extensionASTNodes,
    };
  }

  toString() {
    return this.name;
  }

  toJSON() {
    return this.toString();
  }
}

function didYouMeanEnumValue(enumType, unknownValueStr) {
  const allNames = enumType.getValues().map((value) => value.name);
  const suggestedValues = suggestionList(unknownValueStr, allNames);
  return didYouMean('the enum value', suggestedValues);
}

function defineEnumValues(typeName, valueMap) {
  isPlainObj(valueMap) ||
    devAssert(
      false,
      `${typeName} values must be an object with value names as keys.`,
    );
  return Object.entries(valueMap).map(([valueName, valueConfig]) => {
    isPlainObj(valueConfig) ||
      devAssert(
        false,
        `${typeName}.${valueName} must refer to an object with a "value" key ` +
          `representing an internal value but got: ${inspect(valueConfig)}.`,
      );
    return {
      name: assertEnumValueName(valueName),
      description: valueConfig.description,
      value: valueConfig.value !== undefined ? valueConfig.value : valueName,
      deprecationReason: valueConfig.deprecationReason,
      extensions: toObjMap(valueConfig.extensions),
      astNode: valueConfig.astNode,
    };
  });
}

/**
 * Input Object Type Definition
 *
 * An input object defines a structured collection of fields which may be
 * supplied to a field argument.
 *
 * Using `NonNull` will ensure that a value must be provided by the query
 *
 * Example:
 *
 * ```ts
 * const GeoPoint = new GraphQLInputObjectType({
 *   name: 'GeoPoint',
 *   fields: {
 *     lat: { type: new GraphQLNonNull(GraphQLFloat) },
 *     lon: { type: new GraphQLNonNull(GraphQLFloat) },
 *     alt: { type: GraphQLFloat, defaultValue: 0 },
 *   }
 * });
 * ```
 */
class GraphQLInputObjectType {
  constructor(config) {
    var _config$extensionASTN6;

    this.name = assertName(config.name);
    this.description = config.description;
    this.extensions = toObjMap(config.extensions);
    this.astNode = config.astNode;
    this.extensionASTNodes =
      (_config$extensionASTN6 = config.extensionASTNodes) !== null &&
      _config$extensionASTN6 !== void 0
        ? _config$extensionASTN6
        : [];
    this._fields = defineInputFieldMap.bind(undefined, config);
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLInputObjectType';
  }

  getFields() {
    if (typeof this._fields === 'function') {
      this._fields = this._fields();
    }

    return this._fields;
  }

  toConfig() {
    const fields = mapValue(this.getFields(), (field) => ({
      description: field.description,
      type: field.type,
      defaultValue: field.defaultValue,
      deprecationReason: field.deprecationReason,
      extensions: field.extensions,
      astNode: field.astNode,
    }));
    return {
      name: this.name,
      description: this.description,
      fields,
      extensions: this.extensions,
      astNode: this.astNode,
      extensionASTNodes: this.extensionASTNodes,
    };
  }

  toString() {
    return this.name;
  }

  toJSON() {
    return this.toString();
  }
}

function defineInputFieldMap(config) {
  const fieldMap = resolveObjMapThunk(config.fields);
  isPlainObj(fieldMap) ||
    devAssert(
      false,
      `${config.name} fields must be an object with field names as keys or a function which returns such an object.`,
    );
  return mapValue(fieldMap, (fieldConfig, fieldName) => {
    !('resolve' in fieldConfig) ||
      devAssert(
        false,
        `${config.name}.${fieldName} field has a resolve property, but Input Types cannot define resolvers.`,
      );
    return {
      name: assertName(fieldName),
      description: fieldConfig.description,
      type: fieldConfig.type,
      defaultValue: fieldConfig.defaultValue,
      deprecationReason: fieldConfig.deprecationReason,
      extensions: toObjMap(fieldConfig.extensions),
      astNode: fieldConfig.astNode,
    };
  });
}

function isRequiredInputField(field) {
  return isNonNullType(field.type) && field.defaultValue === undefined;
}

/**
 * Provided two types, return true if the types are equal (invariant).
 */
function isEqualType(typeA, typeB) {
  // Equivalent types are equal.
  if (typeA === typeB) {
    return true;
  } // If either type is non-null, the other must also be non-null.

  if (isNonNullType(typeA) && isNonNullType(typeB)) {
    return isEqualType(typeA.ofType, typeB.ofType);
  } // If either type is a list, the other must also be a list.

  if (isListType(typeA) && isListType(typeB)) {
    return isEqualType(typeA.ofType, typeB.ofType);
  } // Otherwise the types are not equal.

  return false;
}
/**
 * Provided a type and a super type, return true if the first type is either
 * equal or a subset of the second super type (covariant).
 */

function isTypeSubTypeOf(schema, maybeSubType, superType) {
  // Equivalent type is a valid subtype
  if (maybeSubType === superType) {
    return true;
  } // If superType is non-null, maybeSubType must also be non-null.

  if (isNonNullType(superType)) {
    if (isNonNullType(maybeSubType)) {
      return isTypeSubTypeOf(schema, maybeSubType.ofType, superType.ofType);
    }

    return false;
  }

  if (isNonNullType(maybeSubType)) {
    // If superType is nullable, maybeSubType may be non-null or nullable.
    return isTypeSubTypeOf(schema, maybeSubType.ofType, superType);
  } // If superType type is a list, maybeSubType type must also be a list.

  if (isListType(superType)) {
    if (isListType(maybeSubType)) {
      return isTypeSubTypeOf(schema, maybeSubType.ofType, superType.ofType);
    }

    return false;
  }

  if (isListType(maybeSubType)) {
    // If superType is not a list, maybeSubType must also be not a list.
    return false;
  } // If superType type is an abstract type, check if it is super type of maybeSubType.
  // Otherwise, the child type is not a valid subtype of the parent type.

  return (
    isAbstractType(superType) &&
    (isInterfaceType(maybeSubType) || isObjectType(maybeSubType)) &&
    schema.isSubType(superType, maybeSubType)
  );
}
/**
 * Provided two composite types, determine if they "overlap". Two composite
 * types overlap when the Sets of possible concrete types for each intersect.
 *
 * This is often used to determine if a fragment of a given type could possibly
 * be visited in a context of another type.
 *
 * This function is commutative.
 */

function doTypesOverlap(schema, typeA, typeB) {
  // Equivalent types overlap
  if (typeA === typeB) {
    return true;
  }

  if (isAbstractType(typeA)) {
    if (isAbstractType(typeB)) {
      // If both types are abstract, then determine if there is any intersection
      // between possible concrete types of each.
      return schema
        .getPossibleTypes(typeA)
        .some((type) => schema.isSubType(typeB, type));
    } // Determine if the latter type is a possible concrete type of the former.

    return schema.isSubType(typeA, typeB);
  }

  if (isAbstractType(typeB)) {
    // Determine if the former type is a possible concrete type of the latter.
    return schema.isSubType(typeB, typeA);
  } // Otherwise the types do not overlap.

  return false;
}

/**
 * Maximum possible Int value as per GraphQL Spec (32-bit signed integer).
 * n.b. This differs from JavaScript's numbers that are IEEE 754 doubles safe up-to 2^53 - 1
 * */

const GRAPHQL_MAX_INT = 2147483647;
/**
 * Minimum possible Int value as per GraphQL Spec (32-bit signed integer).
 * n.b. This differs from JavaScript's numbers that are IEEE 754 doubles safe starting at -(2^53 - 1)
 * */

const GRAPHQL_MIN_INT = -2147483648;
const GraphQLInt = new GraphQLScalarType({
  name: 'Int',
  description:
    'The `Int` scalar type represents non-fractional signed whole numeric values. Int can represent values between -(2^31) and 2^31 - 1.',

  serialize(outputValue) {
    const coercedValue = serializeObject(outputValue);

    if (typeof coercedValue === 'boolean') {
      return coercedValue ? 1 : 0;
    }

    let num = coercedValue;

    if (typeof coercedValue === 'string' && coercedValue !== '') {
      num = Number(coercedValue);
    }

    if (typeof num !== 'number' || !Number.isInteger(num)) {
      throw new GraphQLError(
        `Int cannot represent non-integer value: ${inspect(coercedValue)}`,
      );
    }

    if (num > GRAPHQL_MAX_INT || num < GRAPHQL_MIN_INT) {
      throw new GraphQLError(
        'Int cannot represent non 32-bit signed integer value: ' +
          inspect(coercedValue),
      );
    }

    return num;
  },

  parseValue(inputValue) {
    if (typeof inputValue !== 'number' || !Number.isInteger(inputValue)) {
      throw new GraphQLError(
        `Int cannot represent non-integer value: ${inspect(inputValue)}`,
      );
    }

    if (inputValue > GRAPHQL_MAX_INT || inputValue < GRAPHQL_MIN_INT) {
      throw new GraphQLError(
        `Int cannot represent non 32-bit signed integer value: ${inputValue}`,
      );
    }

    return inputValue;
  },

  parseLiteral(valueNode) {
    if (valueNode.kind !== Kind.INT) {
      throw new GraphQLError(
        `Int cannot represent non-integer value: ${print(valueNode)}`,
        {
          nodes: valueNode,
        },
      );
    }

    const num = parseInt(valueNode.value, 10);

    if (num > GRAPHQL_MAX_INT || num < GRAPHQL_MIN_INT) {
      throw new GraphQLError(
        `Int cannot represent non 32-bit signed integer value: ${valueNode.value}`,
        {
          nodes: valueNode,
        },
      );
    }

    return num;
  },
});
const GraphQLFloat = new GraphQLScalarType({
  name: 'Float',
  description:
    'The `Float` scalar type represents signed double-precision fractional values as specified by [IEEE 754](https://en.wikipedia.org/wiki/IEEE_floating_point).',

  serialize(outputValue) {
    const coercedValue = serializeObject(outputValue);

    if (typeof coercedValue === 'boolean') {
      return coercedValue ? 1 : 0;
    }

    let num = coercedValue;

    if (typeof coercedValue === 'string' && coercedValue !== '') {
      num = Number(coercedValue);
    }

    if (typeof num !== 'number' || !Number.isFinite(num)) {
      throw new GraphQLError(
        `Float cannot represent non numeric value: ${inspect(coercedValue)}`,
      );
    }

    return num;
  },

  parseValue(inputValue) {
    if (typeof inputValue !== 'number' || !Number.isFinite(inputValue)) {
      throw new GraphQLError(
        `Float cannot represent non numeric value: ${inspect(inputValue)}`,
      );
    }

    return inputValue;
  },

  parseLiteral(valueNode) {
    if (valueNode.kind !== Kind.FLOAT && valueNode.kind !== Kind.INT) {
      throw new GraphQLError(
        `Float cannot represent non numeric value: ${print(valueNode)}`,
        valueNode,
      );
    }

    return parseFloat(valueNode.value);
  },
});
const GraphQLString = new GraphQLScalarType({
  name: 'String',
  description:
    'The `String` scalar type represents textual data, represented as UTF-8 character sequences. The String type is most often used by GraphQL to represent free-form human-readable text.',

  serialize(outputValue) {
    const coercedValue = serializeObject(outputValue); // Serialize string, boolean and number values to a string, but do not
    // attempt to coerce object, function, symbol, or other types as strings.

    if (typeof coercedValue === 'string') {
      return coercedValue;
    }

    if (typeof coercedValue === 'boolean') {
      return coercedValue ? 'true' : 'false';
    }

    if (typeof coercedValue === 'number' && Number.isFinite(coercedValue)) {
      return coercedValue.toString();
    }

    throw new GraphQLError(
      `String cannot represent value: ${inspect(outputValue)}`,
    );
  },

  parseValue(inputValue) {
    if (typeof inputValue !== 'string') {
      throw new GraphQLError(
        `String cannot represent a non string value: ${inspect(inputValue)}`,
      );
    }

    return inputValue;
  },

  parseLiteral(valueNode) {
    if (valueNode.kind !== Kind.STRING) {
      throw new GraphQLError(
        `String cannot represent a non string value: ${print(valueNode)}`,
        {
          nodes: valueNode,
        },
      );
    }

    return valueNode.value;
  },
});
const GraphQLBoolean = new GraphQLScalarType({
  name: 'Boolean',
  description: 'The `Boolean` scalar type represents `true` or `false`.',

  serialize(outputValue) {
    const coercedValue = serializeObject(outputValue);

    if (typeof coercedValue === 'boolean') {
      return coercedValue;
    }

    if (Number.isFinite(coercedValue)) {
      return coercedValue !== 0;
    }

    throw new GraphQLError(
      `Boolean cannot represent a non boolean value: ${inspect(coercedValue)}`,
    );
  },

  parseValue(inputValue) {
    if (typeof inputValue !== 'boolean') {
      throw new GraphQLError(
        `Boolean cannot represent a non boolean value: ${inspect(inputValue)}`,
      );
    }

    return inputValue;
  },

  parseLiteral(valueNode) {
    if (valueNode.kind !== Kind.BOOLEAN) {
      throw new GraphQLError(
        `Boolean cannot represent a non boolean value: ${print(valueNode)}`,
        {
          nodes: valueNode,
        },
      );
    }

    return valueNode.value;
  },
});
const GraphQLID = new GraphQLScalarType({
  name: 'ID',
  description:
    'The `ID` scalar type represents a unique identifier, often used to refetch an object or as key for a cache. The ID type appears in a JSON response as a String; however, it is not intended to be human-readable. When expected as an input type, any string (such as `"4"`) or integer (such as `4`) input value will be accepted as an ID.',

  serialize(outputValue) {
    const coercedValue = serializeObject(outputValue);

    if (typeof coercedValue === 'string') {
      return coercedValue;
    }

    if (Number.isInteger(coercedValue)) {
      return String(coercedValue);
    }

    throw new GraphQLError(
      `ID cannot represent value: ${inspect(outputValue)}`,
    );
  },

  parseValue(inputValue) {
    if (typeof inputValue === 'string') {
      return inputValue;
    }

    if (typeof inputValue === 'number' && Number.isInteger(inputValue)) {
      return inputValue.toString();
    }

    throw new GraphQLError(`ID cannot represent value: ${inspect(inputValue)}`);
  },

  parseLiteral(valueNode) {
    if (valueNode.kind !== Kind.STRING && valueNode.kind !== Kind.INT) {
      throw new GraphQLError(
        'ID cannot represent a non-string and non-integer value: ' +
          print(valueNode),
        {
          nodes: valueNode,
        },
      );
    }

    return valueNode.value;
  },
});
const specifiedScalarTypes = Object.freeze([
  GraphQLString,
  GraphQLInt,
  GraphQLFloat,
  GraphQLBoolean,
  GraphQLID,
]);
function isSpecifiedScalarType(type) {
  return specifiedScalarTypes.some(({ name }) => type.name === name);
} // Support serializing objects with custom valueOf() or toJSON() functions -
// a common way to represent a complex value which can be represented as
// a string (ex: MongoDB id objects).

function serializeObject(outputValue) {
  if (isObjectLike$c(outputValue)) {
    if (typeof outputValue.valueOf === 'function') {
      const valueOfResult = outputValue.valueOf();

      if (!isObjectLike$c(valueOfResult)) {
        return valueOfResult;
      }
    }

    if (typeof outputValue.toJSON === 'function') {
      return outputValue.toJSON();
    }
  }

  return outputValue;
}

/**
 * Test if the given value is a GraphQL directive.
 */

function isDirective(directive) {
  return instanceOf(directive, GraphQLDirective);
}
function assertDirective(directive) {
  if (!isDirective(directive)) {
    throw new Error(
      `Expected ${inspect(directive)} to be a GraphQL directive.`,
    );
  }

  return directive;
}
/**
 * Custom extensions
 *
 * @remarks
 * Use a unique identifier name for your extension, for example the name of
 * your library or project. Do not use a shortened identifier as this increases
 * the risk of conflicts. We recommend you add at most one extension field,
 * an object which can contain all the values you need.
 */

/**
 * Directives are used by the GraphQL runtime as a way of modifying execution
 * behavior. Type system creators will usually not create these directly.
 */
class GraphQLDirective {
  constructor(config) {
    var _config$isRepeatable, _config$args;

    this.name = assertName(config.name);
    this.description = config.description;
    this.locations = config.locations;
    this.isRepeatable =
      (_config$isRepeatable = config.isRepeatable) !== null &&
      _config$isRepeatable !== void 0
        ? _config$isRepeatable
        : false;
    this.extensions = toObjMap(config.extensions);
    this.astNode = config.astNode;
    Array.isArray(config.locations) ||
      devAssert(false, `@${config.name} locations must be an Array.`);
    const args =
      (_config$args = config.args) !== null && _config$args !== void 0
        ? _config$args
        : {};
    (isObjectLike$c(args) && !Array.isArray(args)) ||
      devAssert(
        false,
        `@${config.name} args must be an object with argument names as keys.`,
      );
    this.args = defineArguments(args);
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLDirective';
  }

  toConfig() {
    return {
      name: this.name,
      description: this.description,
      locations: this.locations,
      args: argsToArgsConfig(this.args),
      isRepeatable: this.isRepeatable,
      extensions: this.extensions,
      astNode: this.astNode,
    };
  }

  toString() {
    return '@' + this.name;
  }

  toJSON() {
    return this.toString();
  }
}

/**
 * Used to conditionally include fields or fragments.
 */
const GraphQLIncludeDirective = new GraphQLDirective({
  name: 'include',
  description:
    'Directs the executor to include this field or fragment only when the `if` argument is true.',
  locations: [
    DirectiveLocation.FIELD,
    DirectiveLocation.FRAGMENT_SPREAD,
    DirectiveLocation.INLINE_FRAGMENT,
  ],
  args: {
    if: {
      type: new GraphQLNonNull(GraphQLBoolean),
      description: 'Included when true.',
    },
  },
});
/**
 * Used to conditionally skip (exclude) fields or fragments.
 */

const GraphQLSkipDirective = new GraphQLDirective({
  name: 'skip',
  description:
    'Directs the executor to skip this field or fragment when the `if` argument is true.',
  locations: [
    DirectiveLocation.FIELD,
    DirectiveLocation.FRAGMENT_SPREAD,
    DirectiveLocation.INLINE_FRAGMENT,
  ],
  args: {
    if: {
      type: new GraphQLNonNull(GraphQLBoolean),
      description: 'Skipped when true.',
    },
  },
});
/**
 * Constant string used for default reason for a deprecation.
 */

const DEFAULT_DEPRECATION_REASON = 'No longer supported';
/**
 * Used to declare element of a GraphQL schema as deprecated.
 */

const GraphQLDeprecatedDirective = new GraphQLDirective({
  name: 'deprecated',
  description: 'Marks an element of a GraphQL schema as no longer supported.',
  locations: [
    DirectiveLocation.FIELD_DEFINITION,
    DirectiveLocation.ARGUMENT_DEFINITION,
    DirectiveLocation.INPUT_FIELD_DEFINITION,
    DirectiveLocation.ENUM_VALUE,
  ],
  args: {
    reason: {
      type: GraphQLString,
      description:
        'Explains why this element was deprecated, usually also including a suggestion for how to access supported similar data. Formatted using the Markdown syntax, as specified by [CommonMark](https://commonmark.org/).',
      defaultValue: DEFAULT_DEPRECATION_REASON,
    },
  },
});
/**
 * Used to provide a URL for specifying the behavior of custom scalar definitions.
 */

const GraphQLSpecifiedByDirective = new GraphQLDirective({
  name: 'specifiedBy',
  description: 'Exposes a URL that specifies the behavior of this scalar.',
  locations: [DirectiveLocation.SCALAR],
  args: {
    url: {
      type: new GraphQLNonNull(GraphQLString),
      description: 'The URL that specifies the behavior of this scalar.',
    },
  },
});
/**
 * The full list of specified directives.
 */

const specifiedDirectives = Object.freeze([
  GraphQLIncludeDirective,
  GraphQLSkipDirective,
  GraphQLDeprecatedDirective,
  GraphQLSpecifiedByDirective,
]);
function isSpecifiedDirective(directive) {
  return specifiedDirectives.some(({ name }) => name === directive.name);
}

/**
 * Returns true if the provided object is an Object (i.e. not a string literal)
 * and implements the Iterator protocol.
 *
 * This may be used in place of [Array.isArray()][isArray] to determine if
 * an object should be iterated-over e.g. Array, Map, Set, Int8Array,
 * TypedArray, etc. but excludes string literals.
 *
 * @example
 * ```ts
 * isIterableObject([ 1, 2, 3 ]) // true
 * isIterableObject(new Map()) // true
 * isIterableObject('ABC') // false
 * isIterableObject({ key: 'value' }) // false
 * isIterableObject({ length: 1, 0: 'Alpha' }) // false
 * ```
 */
function isIterableObject(maybeIterable) {
  return (
    typeof maybeIterable === 'object' &&
    typeof (maybeIterable === null || maybeIterable === void 0
      ? void 0
      : maybeIterable[Symbol.iterator]) === 'function'
  );
}

/**
 * Produces a GraphQL Value AST given a JavaScript object.
 * Function will match JavaScript/JSON values to GraphQL AST schema format
 * by using suggested GraphQLInputType. For example:
 *
 *     astFromValue("value", GraphQLString)
 *
 * A GraphQL type must be provided, which will be used to interpret different
 * JavaScript values.
 *
 * | JSON Value    | GraphQL Value        |
 * | ------------- | -------------------- |
 * | Object        | Input Object         |
 * | Array         | List                 |
 * | Boolean       | Boolean              |
 * | String        | String / Enum Value  |
 * | Number        | Int / Float          |
 * | Unknown       | Enum Value           |
 * | null          | NullValue            |
 *
 */

function astFromValue(value, type) {
  if (isNonNullType(type)) {
    const astValue = astFromValue(value, type.ofType);

    if (
      (astValue === null || astValue === void 0 ? void 0 : astValue.kind) ===
      Kind.NULL
    ) {
      return null;
    }

    return astValue;
  } // only explicit null, not undefined, NaN

  if (value === null) {
    return {
      kind: Kind.NULL,
    };
  } // undefined

  if (value === undefined) {
    return null;
  } // Convert JavaScript array to GraphQL list. If the GraphQLType is a list, but
  // the value is not an array, convert the value using the list's item type.

  if (isListType(type)) {
    const itemType = type.ofType;

    if (isIterableObject(value)) {
      const valuesNodes = [];

      for (const item of value) {
        const itemNode = astFromValue(item, itemType);

        if (itemNode != null) {
          valuesNodes.push(itemNode);
        }
      }

      return {
        kind: Kind.LIST,
        values: valuesNodes,
      };
    }

    return astFromValue(value, itemType);
  } // Populate the fields of the input object by creating ASTs from each value
  // in the JavaScript object according to the fields in the input type.

  if (isInputObjectType(type)) {
    if (!isObjectLike$c(value)) {
      return null;
    }

    const fieldNodes = [];

    for (const field of Object.values(type.getFields())) {
      const fieldValue = astFromValue(value[field.name], field.type);

      if (fieldValue) {
        fieldNodes.push({
          kind: Kind.OBJECT_FIELD,
          name: {
            kind: Kind.NAME,
            value: field.name,
          },
          value: fieldValue,
        });
      }
    }

    return {
      kind: Kind.OBJECT,
      fields: fieldNodes,
    };
  }

  if (isLeafType(type)) {
    // Since value is an internally represented value, it must be serialized
    // to an externally represented value before converting into an AST.
    const serialized = type.serialize(value);

    if (serialized == null) {
      return null;
    } // Others serialize based on their corresponding JavaScript scalar types.

    if (typeof serialized === 'boolean') {
      return {
        kind: Kind.BOOLEAN,
        value: serialized,
      };
    } // JavaScript numbers can be Int or Float values.

    if (typeof serialized === 'number' && Number.isFinite(serialized)) {
      const stringNum = String(serialized);
      return integerStringRegExp.test(stringNum)
        ? {
            kind: Kind.INT,
            value: stringNum,
          }
        : {
            kind: Kind.FLOAT,
            value: stringNum,
          };
    }

    if (typeof serialized === 'string') {
      // Enum types use Enum literals.
      if (isEnumType(type)) {
        return {
          kind: Kind.ENUM,
          value: serialized,
        };
      } // ID types can use Int literals.

      if (type === GraphQLID && integerStringRegExp.test(serialized)) {
        return {
          kind: Kind.INT,
          value: serialized,
        };
      }

      return {
        kind: Kind.STRING,
        value: serialized,
      };
    }

    throw new TypeError(`Cannot convert value to AST: ${inspect(serialized)}.`);
  }
  /* c8 ignore next 3 */
  // Not reachable, all possible types have been considered.

  invariant(false, 'Unexpected input type: ' + inspect(type));
}
/**
 * IntValue:
 *   - NegativeSign? 0
 *   - NegativeSign? NonZeroDigit ( Digit+ )?
 */

const integerStringRegExp = /^-?(?:0|[1-9][0-9]*)$/;

const __Schema = new GraphQLObjectType({
  name: '__Schema',
  description:
    'A GraphQL Schema defines the capabilities of a GraphQL server. It exposes all available types and directives on the server, as well as the entry points for query, mutation, and subscription operations.',
  fields: () => ({
    description: {
      type: GraphQLString,
      resolve: (schema) => schema.description,
    },
    types: {
      description: 'A list of all types supported by this server.',
      type: new GraphQLNonNull(new GraphQLList(new GraphQLNonNull(__Type))),

      resolve(schema) {
        return Object.values(schema.getTypeMap());
      },
    },
    queryType: {
      description: 'The type that query operations will be rooted at.',
      type: new GraphQLNonNull(__Type),
      resolve: (schema) => schema.getQueryType(),
    },
    mutationType: {
      description:
        'If this server supports mutation, the type that mutation operations will be rooted at.',
      type: __Type,
      resolve: (schema) => schema.getMutationType(),
    },
    subscriptionType: {
      description:
        'If this server support subscription, the type that subscription operations will be rooted at.',
      type: __Type,
      resolve: (schema) => schema.getSubscriptionType(),
    },
    directives: {
      description: 'A list of all directives supported by this server.',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(__Directive)),
      ),
      resolve: (schema) => schema.getDirectives(),
    },
  }),
});
const __Directive = new GraphQLObjectType({
  name: '__Directive',
  description:
    "A Directive provides a way to describe alternate runtime execution and type validation behavior in a GraphQL document.\n\nIn some cases, you need to provide options to alter GraphQL's execution behavior in ways field arguments will not suffice, such as conditionally including or skipping a field. Directives provide this by describing additional information to the executor.",
  fields: () => ({
    name: {
      type: new GraphQLNonNull(GraphQLString),
      resolve: (directive) => directive.name,
    },
    description: {
      type: GraphQLString,
      resolve: (directive) => directive.description,
    },
    isRepeatable: {
      type: new GraphQLNonNull(GraphQLBoolean),
      resolve: (directive) => directive.isRepeatable,
    },
    locations: {
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(__DirectiveLocation)),
      ),
      resolve: (directive) => directive.locations,
    },
    args: {
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(__InputValue)),
      ),
      args: {
        includeDeprecated: {
          type: GraphQLBoolean,
          defaultValue: false,
        },
      },

      resolve(field, { includeDeprecated }) {
        return includeDeprecated
          ? field.args
          : field.args.filter((arg) => arg.deprecationReason == null);
      },
    },
  }),
});
const __DirectiveLocation = new GraphQLEnumType({
  name: '__DirectiveLocation',
  description:
    'A Directive can be adjacent to many parts of the GraphQL language, a __DirectiveLocation describes one such possible adjacencies.',
  values: {
    QUERY: {
      value: DirectiveLocation.QUERY,
      description: 'Location adjacent to a query operation.',
    },
    MUTATION: {
      value: DirectiveLocation.MUTATION,
      description: 'Location adjacent to a mutation operation.',
    },
    SUBSCRIPTION: {
      value: DirectiveLocation.SUBSCRIPTION,
      description: 'Location adjacent to a subscription operation.',
    },
    FIELD: {
      value: DirectiveLocation.FIELD,
      description: 'Location adjacent to a field.',
    },
    FRAGMENT_DEFINITION: {
      value: DirectiveLocation.FRAGMENT_DEFINITION,
      description: 'Location adjacent to a fragment definition.',
    },
    FRAGMENT_SPREAD: {
      value: DirectiveLocation.FRAGMENT_SPREAD,
      description: 'Location adjacent to a fragment spread.',
    },
    INLINE_FRAGMENT: {
      value: DirectiveLocation.INLINE_FRAGMENT,
      description: 'Location adjacent to an inline fragment.',
    },
    VARIABLE_DEFINITION: {
      value: DirectiveLocation.VARIABLE_DEFINITION,
      description: 'Location adjacent to a variable definition.',
    },
    SCHEMA: {
      value: DirectiveLocation.SCHEMA,
      description: 'Location adjacent to a schema definition.',
    },
    SCALAR: {
      value: DirectiveLocation.SCALAR,
      description: 'Location adjacent to a scalar definition.',
    },
    OBJECT: {
      value: DirectiveLocation.OBJECT,
      description: 'Location adjacent to an object type definition.',
    },
    FIELD_DEFINITION: {
      value: DirectiveLocation.FIELD_DEFINITION,
      description: 'Location adjacent to a field definition.',
    },
    ARGUMENT_DEFINITION: {
      value: DirectiveLocation.ARGUMENT_DEFINITION,
      description: 'Location adjacent to an argument definition.',
    },
    INTERFACE: {
      value: DirectiveLocation.INTERFACE,
      description: 'Location adjacent to an interface definition.',
    },
    UNION: {
      value: DirectiveLocation.UNION,
      description: 'Location adjacent to a union definition.',
    },
    ENUM: {
      value: DirectiveLocation.ENUM,
      description: 'Location adjacent to an enum definition.',
    },
    ENUM_VALUE: {
      value: DirectiveLocation.ENUM_VALUE,
      description: 'Location adjacent to an enum value definition.',
    },
    INPUT_OBJECT: {
      value: DirectiveLocation.INPUT_OBJECT,
      description: 'Location adjacent to an input object type definition.',
    },
    INPUT_FIELD_DEFINITION: {
      value: DirectiveLocation.INPUT_FIELD_DEFINITION,
      description: 'Location adjacent to an input object field definition.',
    },
  },
});
const __Type = new GraphQLObjectType({
  name: '__Type',
  description:
    'The fundamental unit of any GraphQL Schema is the type. There are many kinds of types in GraphQL as represented by the `__TypeKind` enum.\n\nDepending on the kind of a type, certain fields describe information about that type. Scalar types provide no information beyond a name, description and optional `specifiedByURL`, while Enum types provide their values. Object and Interface types provide the fields they describe. Abstract types, Union and Interface, provide the Object types possible at runtime. List and NonNull types compose other types.',
  fields: () => ({
    kind: {
      type: new GraphQLNonNull(__TypeKind),

      resolve(type) {
        if (isScalarType(type)) {
          return TypeKind.SCALAR;
        }

        if (isObjectType(type)) {
          return TypeKind.OBJECT;
        }

        if (isInterfaceType(type)) {
          return TypeKind.INTERFACE;
        }

        if (isUnionType(type)) {
          return TypeKind.UNION;
        }

        if (isEnumType(type)) {
          return TypeKind.ENUM;
        }

        if (isInputObjectType(type)) {
          return TypeKind.INPUT_OBJECT;
        }

        if (isListType(type)) {
          return TypeKind.LIST;
        }

        if (isNonNullType(type)) {
          return TypeKind.NON_NULL;
        }
        /* c8 ignore next 3 */
        // Not reachable, all possible types have been considered)

        invariant(false, `Unexpected type: "${inspect(type)}".`);
      },
    },
    name: {
      type: GraphQLString,
      resolve: (type) => ('name' in type ? type.name : undefined),
    },
    description: {
      type: GraphQLString,
      resolve: (
        type, // FIXME: add test case
      ) =>
        /* c8 ignore next */
        'description' in type ? type.description : undefined,
    },
    specifiedByURL: {
      type: GraphQLString,
      resolve: (obj) =>
        'specifiedByURL' in obj ? obj.specifiedByURL : undefined,
    },
    fields: {
      type: new GraphQLList(new GraphQLNonNull(__Field)),
      args: {
        includeDeprecated: {
          type: GraphQLBoolean,
          defaultValue: false,
        },
      },

      resolve(type, { includeDeprecated }) {
        if (isObjectType(type) || isInterfaceType(type)) {
          const fields = Object.values(type.getFields());
          return includeDeprecated
            ? fields
            : fields.filter((field) => field.deprecationReason == null);
        }
      },
    },
    interfaces: {
      type: new GraphQLList(new GraphQLNonNull(__Type)),

      resolve(type) {
        if (isObjectType(type) || isInterfaceType(type)) {
          return type.getInterfaces();
        }
      },
    },
    possibleTypes: {
      type: new GraphQLList(new GraphQLNonNull(__Type)),

      resolve(type, _args, _context, { schema }) {
        if (isAbstractType(type)) {
          return schema.getPossibleTypes(type);
        }
      },
    },
    enumValues: {
      type: new GraphQLList(new GraphQLNonNull(__EnumValue)),
      args: {
        includeDeprecated: {
          type: GraphQLBoolean,
          defaultValue: false,
        },
      },

      resolve(type, { includeDeprecated }) {
        if (isEnumType(type)) {
          const values = type.getValues();
          return includeDeprecated
            ? values
            : values.filter((field) => field.deprecationReason == null);
        }
      },
    },
    inputFields: {
      type: new GraphQLList(new GraphQLNonNull(__InputValue)),
      args: {
        includeDeprecated: {
          type: GraphQLBoolean,
          defaultValue: false,
        },
      },

      resolve(type, { includeDeprecated }) {
        if (isInputObjectType(type)) {
          const values = Object.values(type.getFields());
          return includeDeprecated
            ? values
            : values.filter((field) => field.deprecationReason == null);
        }
      },
    },
    ofType: {
      type: __Type,
      resolve: (type) => ('ofType' in type ? type.ofType : undefined),
    },
  }),
});
const __Field = new GraphQLObjectType({
  name: '__Field',
  description:
    'Object and Interface types are described by a list of Fields, each of which has a name, potentially a list of arguments, and a return type.',
  fields: () => ({
    name: {
      type: new GraphQLNonNull(GraphQLString),
      resolve: (field) => field.name,
    },
    description: {
      type: GraphQLString,
      resolve: (field) => field.description,
    },
    args: {
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(__InputValue)),
      ),
      args: {
        includeDeprecated: {
          type: GraphQLBoolean,
          defaultValue: false,
        },
      },

      resolve(field, { includeDeprecated }) {
        return includeDeprecated
          ? field.args
          : field.args.filter((arg) => arg.deprecationReason == null);
      },
    },
    type: {
      type: new GraphQLNonNull(__Type),
      resolve: (field) => field.type,
    },
    isDeprecated: {
      type: new GraphQLNonNull(GraphQLBoolean),
      resolve: (field) => field.deprecationReason != null,
    },
    deprecationReason: {
      type: GraphQLString,
      resolve: (field) => field.deprecationReason,
    },
  }),
});
const __InputValue = new GraphQLObjectType({
  name: '__InputValue',
  description:
    'Arguments provided to Fields or Directives and the input fields of an InputObject are represented as Input Values which describe their type and optionally a default value.',
  fields: () => ({
    name: {
      type: new GraphQLNonNull(GraphQLString),
      resolve: (inputValue) => inputValue.name,
    },
    description: {
      type: GraphQLString,
      resolve: (inputValue) => inputValue.description,
    },
    type: {
      type: new GraphQLNonNull(__Type),
      resolve: (inputValue) => inputValue.type,
    },
    defaultValue: {
      type: GraphQLString,
      description:
        'A GraphQL-formatted string representing the default value for this input value.',

      resolve(inputValue) {
        const { type, defaultValue } = inputValue;
        const valueAST = astFromValue(defaultValue, type);
        return valueAST ? print(valueAST) : null;
      },
    },
    isDeprecated: {
      type: new GraphQLNonNull(GraphQLBoolean),
      resolve: (field) => field.deprecationReason != null,
    },
    deprecationReason: {
      type: GraphQLString,
      resolve: (obj) => obj.deprecationReason,
    },
  }),
});
const __EnumValue = new GraphQLObjectType({
  name: '__EnumValue',
  description:
    'One possible value for a given Enum. Enum values are unique values, not a placeholder for a string or numeric value. However an Enum value is returned in a JSON response as a string.',
  fields: () => ({
    name: {
      type: new GraphQLNonNull(GraphQLString),
      resolve: (enumValue) => enumValue.name,
    },
    description: {
      type: GraphQLString,
      resolve: (enumValue) => enumValue.description,
    },
    isDeprecated: {
      type: new GraphQLNonNull(GraphQLBoolean),
      resolve: (enumValue) => enumValue.deprecationReason != null,
    },
    deprecationReason: {
      type: GraphQLString,
      resolve: (enumValue) => enumValue.deprecationReason,
    },
  }),
});
var TypeKind;

(function (TypeKind) {
  TypeKind['SCALAR'] = 'SCALAR';
  TypeKind['OBJECT'] = 'OBJECT';
  TypeKind['INTERFACE'] = 'INTERFACE';
  TypeKind['UNION'] = 'UNION';
  TypeKind['ENUM'] = 'ENUM';
  TypeKind['INPUT_OBJECT'] = 'INPUT_OBJECT';
  TypeKind['LIST'] = 'LIST';
  TypeKind['NON_NULL'] = 'NON_NULL';
})(TypeKind || (TypeKind = {}));
const __TypeKind = new GraphQLEnumType({
  name: '__TypeKind',
  description: 'An enum describing what kind of type a given `__Type` is.',
  values: {
    SCALAR: {
      value: TypeKind.SCALAR,
      description: 'Indicates this type is a scalar.',
    },
    OBJECT: {
      value: TypeKind.OBJECT,
      description:
        'Indicates this type is an object. `fields` and `interfaces` are valid fields.',
    },
    INTERFACE: {
      value: TypeKind.INTERFACE,
      description:
        'Indicates this type is an interface. `fields`, `interfaces`, and `possibleTypes` are valid fields.',
    },
    UNION: {
      value: TypeKind.UNION,
      description:
        'Indicates this type is a union. `possibleTypes` is a valid field.',
    },
    ENUM: {
      value: TypeKind.ENUM,
      description:
        'Indicates this type is an enum. `enumValues` is a valid field.',
    },
    INPUT_OBJECT: {
      value: TypeKind.INPUT_OBJECT,
      description:
        'Indicates this type is an input object. `inputFields` is a valid field.',
    },
    LIST: {
      value: TypeKind.LIST,
      description: 'Indicates this type is a list. `ofType` is a valid field.',
    },
    NON_NULL: {
      value: TypeKind.NON_NULL,
      description:
        'Indicates this type is a non-null. `ofType` is a valid field.',
    },
  },
});
/**
 * Note that these are GraphQLField and not GraphQLFieldConfig,
 * so the format for args is different.
 */

const SchemaMetaFieldDef = {
  name: '__schema',
  type: new GraphQLNonNull(__Schema),
  description: 'Access the current type schema of this server.',
  args: [],
  resolve: (_source, _args, _context, { schema }) => schema,
  deprecationReason: undefined,
  extensions: Object.create(null),
  astNode: undefined,
};
const TypeMetaFieldDef = {
  name: '__type',
  type: __Type,
  description: 'Request the type information of a single type.',
  args: [
    {
      name: 'name',
      description: undefined,
      type: new GraphQLNonNull(GraphQLString),
      defaultValue: undefined,
      deprecationReason: undefined,
      extensions: Object.create(null),
      astNode: undefined,
    },
  ],
  resolve: (_source, { name }, _context, { schema }) => schema.getType(name),
  deprecationReason: undefined,
  extensions: Object.create(null),
  astNode: undefined,
};
const TypeNameMetaFieldDef = {
  name: '__typename',
  type: new GraphQLNonNull(GraphQLString),
  description: 'The name of the current Object type at runtime.',
  args: [],
  resolve: (_source, _args, _context, { parentType }) => parentType.name,
  deprecationReason: undefined,
  extensions: Object.create(null),
  astNode: undefined,
};
const introspectionTypes = Object.freeze([
  __Schema,
  __Directive,
  __DirectiveLocation,
  __Type,
  __Field,
  __InputValue,
  __EnumValue,
  __TypeKind,
]);
function isIntrospectionType(type) {
  return introspectionTypes.some(({ name }) => type.name === name);
}

/**
 * Test if the given value is a GraphQL schema.
 */

function isSchema(schema) {
  return instanceOf(schema, GraphQLSchema);
}
function assertSchema(schema) {
  if (!isSchema(schema)) {
    throw new Error(`Expected ${inspect(schema)} to be a GraphQL schema.`);
  }

  return schema;
}
/**
 * Custom extensions
 *
 * @remarks
 * Use a unique identifier name for your extension, for example the name of
 * your library or project. Do not use a shortened identifier as this increases
 * the risk of conflicts. We recommend you add at most one extension field,
 * an object which can contain all the values you need.
 */

/**
 * Schema Definition
 *
 * A Schema is created by supplying the root types of each type of operation,
 * query and mutation (optional). A schema definition is then supplied to the
 * validator and executor.
 *
 * Example:
 *
 * ```ts
 * const MyAppSchema = new GraphQLSchema({
 *   query: MyAppQueryRootType,
 *   mutation: MyAppMutationRootType,
 * })
 * ```
 *
 * Note: When the schema is constructed, by default only the types that are
 * reachable by traversing the root types are included, other types must be
 * explicitly referenced.
 *
 * Example:
 *
 * ```ts
 * const characterInterface = new GraphQLInterfaceType({
 *   name: 'Character',
 *   ...
 * });
 *
 * const humanType = new GraphQLObjectType({
 *   name: 'Human',
 *   interfaces: [characterInterface],
 *   ...
 * });
 *
 * const droidType = new GraphQLObjectType({
 *   name: 'Droid',
 *   interfaces: [characterInterface],
 *   ...
 * });
 *
 * const schema = new GraphQLSchema({
 *   query: new GraphQLObjectType({
 *     name: 'Query',
 *     fields: {
 *       hero: { type: characterInterface, ... },
 *     }
 *   }),
 *   ...
 *   // Since this schema references only the `Character` interface it's
 *   // necessary to explicitly list the types that implement it if
 *   // you want them to be included in the final schema.
 *   types: [humanType, droidType],
 * })
 * ```
 *
 * Note: If an array of `directives` are provided to GraphQLSchema, that will be
 * the exact list of directives represented and allowed. If `directives` is not
 * provided then a default set of the specified directives (e.g. `@include` and
 * `@skip`) will be used. If you wish to provide *additional* directives to these
 * specified directives, you must explicitly declare them. Example:
 *
 * ```ts
 * const MyAppSchema = new GraphQLSchema({
 *   ...
 *   directives: specifiedDirectives.concat([ myCustomDirective ]),
 * })
 * ```
 */
class GraphQLSchema {
  // Used as a cache for validateSchema().
  constructor(config) {
    var _config$extensionASTN, _config$directives;

    // If this schema was built from a source known to be valid, then it may be
    // marked with assumeValid to avoid an additional type system validation.
    this.__validationErrors = config.assumeValid === true ? [] : undefined; // Check for common mistakes during construction to produce early errors.

    isObjectLike$c(config) ||
      devAssert(false, 'Must provide configuration object.');
    !config.types ||
      Array.isArray(config.types) ||
      devAssert(
        false,
        `"types" must be Array if provided but got: ${inspect(config.types)}.`,
      );
    !config.directives ||
      Array.isArray(config.directives) ||
      devAssert(
        false,
        '"directives" must be Array if provided but got: ' +
          `${inspect(config.directives)}.`,
      );
    this.description = config.description;
    this.extensions = toObjMap(config.extensions);
    this.astNode = config.astNode;
    this.extensionASTNodes =
      (_config$extensionASTN = config.extensionASTNodes) !== null &&
      _config$extensionASTN !== void 0
        ? _config$extensionASTN
        : [];
    this._queryType = config.query;
    this._mutationType = config.mutation;
    this._subscriptionType = config.subscription; // Provide specified directives (e.g. @include and @skip) by default.

    this._directives =
      (_config$directives = config.directives) !== null &&
      _config$directives !== void 0
        ? _config$directives
        : specifiedDirectives; // To preserve order of user-provided types, we add first to add them to
    // the set of "collected" types, so `collectReferencedTypes` ignore them.

    const allReferencedTypes = new Set(config.types);

    if (config.types != null) {
      for (const type of config.types) {
        // When we ready to process this type, we remove it from "collected" types
        // and then add it together with all dependent types in the correct position.
        allReferencedTypes.delete(type);
        collectReferencedTypes(type, allReferencedTypes);
      }
    }

    if (this._queryType != null) {
      collectReferencedTypes(this._queryType, allReferencedTypes);
    }

    if (this._mutationType != null) {
      collectReferencedTypes(this._mutationType, allReferencedTypes);
    }

    if (this._subscriptionType != null) {
      collectReferencedTypes(this._subscriptionType, allReferencedTypes);
    }

    for (const directive of this._directives) {
      // Directives are not validated until validateSchema() is called.
      if (isDirective(directive)) {
        for (const arg of directive.args) {
          collectReferencedTypes(arg.type, allReferencedTypes);
        }
      }
    }

    collectReferencedTypes(__Schema, allReferencedTypes); // Storing the resulting map for reference by the schema.

    this._typeMap = Object.create(null);
    this._subTypeMap = Object.create(null); // Keep track of all implementations by interface name.

    this._implementationsMap = Object.create(null);

    for (const namedType of allReferencedTypes) {
      if (namedType == null) {
        continue;
      }

      const typeName = namedType.name;
      typeName ||
        devAssert(
          false,
          'One of the provided types for building the Schema is missing a name.',
        );

      if (this._typeMap[typeName] !== undefined) {
        throw new Error(
          `Schema must contain uniquely named types but contains multiple types named "${typeName}".`,
        );
      }

      this._typeMap[typeName] = namedType;

      if (isInterfaceType(namedType)) {
        // Store implementations by interface.
        for (const iface of namedType.getInterfaces()) {
          if (isInterfaceType(iface)) {
            let implementations = this._implementationsMap[iface.name];

            if (implementations === undefined) {
              implementations = this._implementationsMap[iface.name] = {
                objects: [],
                interfaces: [],
              };
            }

            implementations.interfaces.push(namedType);
          }
        }
      } else if (isObjectType(namedType)) {
        // Store implementations by objects.
        for (const iface of namedType.getInterfaces()) {
          if (isInterfaceType(iface)) {
            let implementations = this._implementationsMap[iface.name];

            if (implementations === undefined) {
              implementations = this._implementationsMap[iface.name] = {
                objects: [],
                interfaces: [],
              };
            }

            implementations.objects.push(namedType);
          }
        }
      }
    }
  }

  get [Symbol.toStringTag]() {
    return 'GraphQLSchema';
  }

  getQueryType() {
    return this._queryType;
  }

  getMutationType() {
    return this._mutationType;
  }

  getSubscriptionType() {
    return this._subscriptionType;
  }

  getRootType(operation) {
    switch (operation) {
      case OperationTypeNode.QUERY:
        return this.getQueryType();

      case OperationTypeNode.MUTATION:
        return this.getMutationType();

      case OperationTypeNode.SUBSCRIPTION:
        return this.getSubscriptionType();
    }
  }

  getTypeMap() {
    return this._typeMap;
  }

  getType(name) {
    return this.getTypeMap()[name];
  }

  getPossibleTypes(abstractType) {
    return isUnionType(abstractType)
      ? abstractType.getTypes()
      : this.getImplementations(abstractType).objects;
  }

  getImplementations(interfaceType) {
    const implementations = this._implementationsMap[interfaceType.name];
    return implementations !== null && implementations !== void 0
      ? implementations
      : {
          objects: [],
          interfaces: [],
        };
  }

  isSubType(abstractType, maybeSubType) {
    let map = this._subTypeMap[abstractType.name];

    if (map === undefined) {
      map = Object.create(null);

      if (isUnionType(abstractType)) {
        for (const type of abstractType.getTypes()) {
          map[type.name] = true;
        }
      } else {
        const implementations = this.getImplementations(abstractType);

        for (const type of implementations.objects) {
          map[type.name] = true;
        }

        for (const type of implementations.interfaces) {
          map[type.name] = true;
        }
      }

      this._subTypeMap[abstractType.name] = map;
    }

    return map[maybeSubType.name] !== undefined;
  }

  getDirectives() {
    return this._directives;
  }

  getDirective(name) {
    return this.getDirectives().find((directive) => directive.name === name);
  }

  toConfig() {
    return {
      description: this.description,
      query: this.getQueryType(),
      mutation: this.getMutationType(),
      subscription: this.getSubscriptionType(),
      types: Object.values(this.getTypeMap()),
      directives: this.getDirectives(),
      extensions: this.extensions,
      astNode: this.astNode,
      extensionASTNodes: this.extensionASTNodes,
      assumeValid: this.__validationErrors !== undefined,
    };
  }
}

function collectReferencedTypes(type, typeSet) {
  const namedType = getNamedType(type);

  if (!typeSet.has(namedType)) {
    typeSet.add(namedType);

    if (isUnionType(namedType)) {
      for (const memberType of namedType.getTypes()) {
        collectReferencedTypes(memberType, typeSet);
      }
    } else if (isObjectType(namedType) || isInterfaceType(namedType)) {
      for (const interfaceType of namedType.getInterfaces()) {
        collectReferencedTypes(interfaceType, typeSet);
      }

      for (const field of Object.values(namedType.getFields())) {
        collectReferencedTypes(field.type, typeSet);

        for (const arg of field.args) {
          collectReferencedTypes(arg.type, typeSet);
        }
      }
    } else if (isInputObjectType(namedType)) {
      for (const field of Object.values(namedType.getFields())) {
        collectReferencedTypes(field.type, typeSet);
      }
    }
  }

  return typeSet;
}

/**
 * Implements the "Type Validation" sub-sections of the specification's
 * "Type System" section.
 *
 * Validation runs synchronously, returning an array of encountered errors, or
 * an empty array if no errors were encountered and the Schema is valid.
 */

function validateSchema(schema) {
  // First check to ensure the provided value is in fact a GraphQLSchema.
  assertSchema(schema); // If this Schema has already been validated, return the previous results.

  if (schema.__validationErrors) {
    return schema.__validationErrors;
  } // Validate the schema, producing a list of errors.

  const context = new SchemaValidationContext(schema);
  validateRootTypes(context);
  validateDirectives(context);
  validateTypes(context); // Persist the results of validation before returning to ensure validation
  // does not run multiple times for this schema.

  const errors = context.getErrors();
  schema.__validationErrors = errors;
  return errors;
}
/**
 * Utility function which asserts a schema is valid by throwing an error if
 * it is invalid.
 */

function assertValidSchema(schema) {
  const errors = validateSchema(schema);

  if (errors.length !== 0) {
    throw new Error(errors.map((error) => error.message).join('\n\n'));
  }
}

class SchemaValidationContext {
  constructor(schema) {
    this._errors = [];
    this.schema = schema;
  }

  reportError(message, nodes) {
    const _nodes = Array.isArray(nodes) ? nodes.filter(Boolean) : nodes;

    this._errors.push(
      new GraphQLError(message, {
        nodes: _nodes,
      }),
    );
  }

  getErrors() {
    return this._errors;
  }
}

function validateRootTypes(context) {
  const schema = context.schema;
  const queryType = schema.getQueryType();

  if (!queryType) {
    context.reportError('Query root type must be provided.', schema.astNode);
  } else if (!isObjectType(queryType)) {
    var _getOperationTypeNode;

    context.reportError(
      `Query root type must be Object type, it cannot be ${inspect(
        queryType,
      )}.`,
      (_getOperationTypeNode = getOperationTypeNode(
        schema,
        OperationTypeNode.QUERY,
      )) !== null && _getOperationTypeNode !== void 0
        ? _getOperationTypeNode
        : queryType.astNode,
    );
  }

  const mutationType = schema.getMutationType();

  if (mutationType && !isObjectType(mutationType)) {
    var _getOperationTypeNode2;

    context.reportError(
      'Mutation root type must be Object type if provided, it cannot be ' +
        `${inspect(mutationType)}.`,
      (_getOperationTypeNode2 = getOperationTypeNode(
        schema,
        OperationTypeNode.MUTATION,
      )) !== null && _getOperationTypeNode2 !== void 0
        ? _getOperationTypeNode2
        : mutationType.astNode,
    );
  }

  const subscriptionType = schema.getSubscriptionType();

  if (subscriptionType && !isObjectType(subscriptionType)) {
    var _getOperationTypeNode3;

    context.reportError(
      'Subscription root type must be Object type if provided, it cannot be ' +
        `${inspect(subscriptionType)}.`,
      (_getOperationTypeNode3 = getOperationTypeNode(
        schema,
        OperationTypeNode.SUBSCRIPTION,
      )) !== null && _getOperationTypeNode3 !== void 0
        ? _getOperationTypeNode3
        : subscriptionType.astNode,
    );
  }
}

function getOperationTypeNode(schema, operation) {
  var _flatMap$find;

  return (_flatMap$find = [schema.astNode, ...schema.extensionASTNodes]
    .flatMap(
      // FIXME: https://github.com/graphql/graphql-js/issues/2203
      (schemaNode) => {
        var _schemaNode$operation;

        return (
          /* c8 ignore next */
          (_schemaNode$operation =
            schemaNode === null || schemaNode === void 0
              ? void 0
              : schemaNode.operationTypes) !== null &&
            _schemaNode$operation !== void 0
            ? _schemaNode$operation
            : []
        );
      },
    )
    .find((operationNode) => operationNode.operation === operation)) === null ||
    _flatMap$find === void 0
    ? void 0
    : _flatMap$find.type;
}

function validateDirectives(context) {
  for (const directive of context.schema.getDirectives()) {
    // Ensure all directives are in fact GraphQL directives.
    if (!isDirective(directive)) {
      context.reportError(
        `Expected directive but got: ${inspect(directive)}.`,
        directive === null || directive === void 0 ? void 0 : directive.astNode,
      );
      continue;
    } // Ensure they are named correctly.

    validateName(context, directive); // TODO: Ensure proper locations.
    // Ensure the arguments are valid.

    for (const arg of directive.args) {
      // Ensure they are named correctly.
      validateName(context, arg); // Ensure the type is an input type.

      if (!isInputType(arg.type)) {
        context.reportError(
          `The type of @${directive.name}(${arg.name}:) must be Input Type ` +
            `but got: ${inspect(arg.type)}.`,
          arg.astNode,
        );
      }

      if (isRequiredArgument(arg) && arg.deprecationReason != null) {
        var _arg$astNode;

        context.reportError(
          `Required argument @${directive.name}(${arg.name}:) cannot be deprecated.`,
          [
            getDeprecatedDirectiveNode(arg.astNode),
            (_arg$astNode = arg.astNode) === null || _arg$astNode === void 0
              ? void 0
              : _arg$astNode.type,
          ],
        );
      }
    }
  }
}

function validateName(context, node) {
  // Ensure names are valid, however introspection types opt out.
  if (node.name.startsWith('__')) {
    context.reportError(
      `Name "${node.name}" must not begin with "__", which is reserved by GraphQL introspection.`,
      node.astNode,
    );
  }
}

function validateTypes(context) {
  const validateInputObjectCircularRefs =
    createInputObjectCircularRefsValidator(context);
  const typeMap = context.schema.getTypeMap();

  for (const type of Object.values(typeMap)) {
    // Ensure all provided types are in fact GraphQL type.
    if (!isNamedType(type)) {
      context.reportError(
        `Expected GraphQL named type but got: ${inspect(type)}.`,
        type.astNode,
      );
      continue;
    } // Ensure it is named correctly (excluding introspection types).

    if (!isIntrospectionType(type)) {
      validateName(context, type);
    }

    if (isObjectType(type)) {
      // Ensure fields are valid
      validateFields(context, type); // Ensure objects implement the interfaces they claim to.

      validateInterfaces(context, type);
    } else if (isInterfaceType(type)) {
      // Ensure fields are valid.
      validateFields(context, type); // Ensure interfaces implement the interfaces they claim to.

      validateInterfaces(context, type);
    } else if (isUnionType(type)) {
      // Ensure Unions include valid member types.
      validateUnionMembers(context, type);
    } else if (isEnumType(type)) {
      // Ensure Enums have valid values.
      validateEnumValues(context, type);
    } else if (isInputObjectType(type)) {
      // Ensure Input Object fields are valid.
      validateInputFields(context, type); // Ensure Input Objects do not contain non-nullable circular references

      validateInputObjectCircularRefs(type);
    }
  }
}

function validateFields(context, type) {
  const fields = Object.values(type.getFields()); // Objects and Interfaces both must define one or more fields.

  if (fields.length === 0) {
    context.reportError(`Type ${type.name} must define one or more fields.`, [
      type.astNode,
      ...type.extensionASTNodes,
    ]);
  }

  for (const field of fields) {
    // Ensure they are named correctly.
    validateName(context, field); // Ensure the type is an output type

    if (!isOutputType(field.type)) {
      var _field$astNode;

      context.reportError(
        `The type of ${type.name}.${field.name} must be Output Type ` +
          `but got: ${inspect(field.type)}.`,
        (_field$astNode = field.astNode) === null || _field$astNode === void 0
          ? void 0
          : _field$astNode.type,
      );
    } // Ensure the arguments are valid

    for (const arg of field.args) {
      const argName = arg.name; // Ensure they are named correctly.

      validateName(context, arg); // Ensure the type is an input type

      if (!isInputType(arg.type)) {
        var _arg$astNode2;

        context.reportError(
          `The type of ${type.name}.${field.name}(${argName}:) must be Input ` +
            `Type but got: ${inspect(arg.type)}.`,
          (_arg$astNode2 = arg.astNode) === null || _arg$astNode2 === void 0
            ? void 0
            : _arg$astNode2.type,
        );
      }

      if (isRequiredArgument(arg) && arg.deprecationReason != null) {
        var _arg$astNode3;

        context.reportError(
          `Required argument ${type.name}.${field.name}(${argName}:) cannot be deprecated.`,
          [
            getDeprecatedDirectiveNode(arg.astNode),
            (_arg$astNode3 = arg.astNode) === null || _arg$astNode3 === void 0
              ? void 0
              : _arg$astNode3.type,
          ],
        );
      }
    }
  }
}

function validateInterfaces(context, type) {
  const ifaceTypeNames = Object.create(null);

  for (const iface of type.getInterfaces()) {
    if (!isInterfaceType(iface)) {
      context.reportError(
        `Type ${inspect(type)} must only implement Interface types, ` +
          `it cannot implement ${inspect(iface)}.`,
        getAllImplementsInterfaceNodes(type, iface),
      );
      continue;
    }

    if (type === iface) {
      context.reportError(
        `Type ${type.name} cannot implement itself because it would create a circular reference.`,
        getAllImplementsInterfaceNodes(type, iface),
      );
      continue;
    }

    if (ifaceTypeNames[iface.name]) {
      context.reportError(
        `Type ${type.name} can only implement ${iface.name} once.`,
        getAllImplementsInterfaceNodes(type, iface),
      );
      continue;
    }

    ifaceTypeNames[iface.name] = true;
    validateTypeImplementsAncestors(context, type, iface);
    validateTypeImplementsInterface(context, type, iface);
  }
}

function validateTypeImplementsInterface(context, type, iface) {
  const typeFieldMap = type.getFields(); // Assert each interface field is implemented.

  for (const ifaceField of Object.values(iface.getFields())) {
    const fieldName = ifaceField.name;
    const typeField = typeFieldMap[fieldName]; // Assert interface field exists on type.

    if (!typeField) {
      context.reportError(
        `Interface field ${iface.name}.${fieldName} expected but ${type.name} does not provide it.`,
        [ifaceField.astNode, type.astNode, ...type.extensionASTNodes],
      );
      continue;
    } // Assert interface field type is satisfied by type field type, by being
    // a valid subtype. (covariant)

    if (!isTypeSubTypeOf(context.schema, typeField.type, ifaceField.type)) {
      var _ifaceField$astNode, _typeField$astNode;

      context.reportError(
        `Interface field ${iface.name}.${fieldName} expects type ` +
          `${inspect(ifaceField.type)} but ${type.name}.${fieldName} ` +
          `is type ${inspect(typeField.type)}.`,
        [
          (_ifaceField$astNode = ifaceField.astNode) === null ||
          _ifaceField$astNode === void 0
            ? void 0
            : _ifaceField$astNode.type,
          (_typeField$astNode = typeField.astNode) === null ||
          _typeField$astNode === void 0
            ? void 0
            : _typeField$astNode.type,
        ],
      );
    } // Assert each interface field arg is implemented.

    for (const ifaceArg of ifaceField.args) {
      const argName = ifaceArg.name;
      const typeArg = typeField.args.find((arg) => arg.name === argName); // Assert interface field arg exists on object field.

      if (!typeArg) {
        context.reportError(
          `Interface field argument ${iface.name}.${fieldName}(${argName}:) expected but ${type.name}.${fieldName} does not provide it.`,
          [ifaceArg.astNode, typeField.astNode],
        );
        continue;
      } // Assert interface field arg type matches object field arg type.
      // (invariant)
      // TODO: change to contravariant?

      if (!isEqualType(ifaceArg.type, typeArg.type)) {
        var _ifaceArg$astNode, _typeArg$astNode;

        context.reportError(
          `Interface field argument ${iface.name}.${fieldName}(${argName}:) ` +
            `expects type ${inspect(ifaceArg.type)} but ` +
            `${type.name}.${fieldName}(${argName}:) is type ` +
            `${inspect(typeArg.type)}.`,
          [
            (_ifaceArg$astNode = ifaceArg.astNode) === null ||
            _ifaceArg$astNode === void 0
              ? void 0
              : _ifaceArg$astNode.type,
            (_typeArg$astNode = typeArg.astNode) === null ||
            _typeArg$astNode === void 0
              ? void 0
              : _typeArg$astNode.type,
          ],
        );
      } // TODO: validate default values?
    } // Assert additional arguments must not be required.

    for (const typeArg of typeField.args) {
      const argName = typeArg.name;
      const ifaceArg = ifaceField.args.find((arg) => arg.name === argName);

      if (!ifaceArg && isRequiredArgument(typeArg)) {
        context.reportError(
          `Object field ${type.name}.${fieldName} includes required argument ${argName} that is missing from the Interface field ${iface.name}.${fieldName}.`,
          [typeArg.astNode, ifaceField.astNode],
        );
      }
    }
  }
}

function validateTypeImplementsAncestors(context, type, iface) {
  const ifaceInterfaces = type.getInterfaces();

  for (const transitive of iface.getInterfaces()) {
    if (!ifaceInterfaces.includes(transitive)) {
      context.reportError(
        transitive === type
          ? `Type ${type.name} cannot implement ${iface.name} because it would create a circular reference.`
          : `Type ${type.name} must implement ${transitive.name} because it is implemented by ${iface.name}.`,
        [
          ...getAllImplementsInterfaceNodes(iface, transitive),
          ...getAllImplementsInterfaceNodes(type, iface),
        ],
      );
    }
  }
}

function validateUnionMembers(context, union) {
  const memberTypes = union.getTypes();

  if (memberTypes.length === 0) {
    context.reportError(
      `Union type ${union.name} must define one or more member types.`,
      [union.astNode, ...union.extensionASTNodes],
    );
  }

  const includedTypeNames = Object.create(null);

  for (const memberType of memberTypes) {
    if (includedTypeNames[memberType.name]) {
      context.reportError(
        `Union type ${union.name} can only include type ${memberType.name} once.`,
        getUnionMemberTypeNodes(union, memberType.name),
      );
      continue;
    }

    includedTypeNames[memberType.name] = true;

    if (!isObjectType(memberType)) {
      context.reportError(
        `Union type ${union.name} can only include Object types, ` +
          `it cannot include ${inspect(memberType)}.`,
        getUnionMemberTypeNodes(union, String(memberType)),
      );
    }
  }
}

function validateEnumValues(context, enumType) {
  const enumValues = enumType.getValues();

  if (enumValues.length === 0) {
    context.reportError(
      `Enum type ${enumType.name} must define one or more values.`,
      [enumType.astNode, ...enumType.extensionASTNodes],
    );
  }

  for (const enumValue of enumValues) {
    // Ensure valid name.
    validateName(context, enumValue);
  }
}

function validateInputFields(context, inputObj) {
  const fields = Object.values(inputObj.getFields());

  if (fields.length === 0) {
    context.reportError(
      `Input Object type ${inputObj.name} must define one or more fields.`,
      [inputObj.astNode, ...inputObj.extensionASTNodes],
    );
  } // Ensure the arguments are valid

  for (const field of fields) {
    // Ensure they are named correctly.
    validateName(context, field); // Ensure the type is an input type

    if (!isInputType(field.type)) {
      var _field$astNode2;

      context.reportError(
        `The type of ${inputObj.name}.${field.name} must be Input Type ` +
          `but got: ${inspect(field.type)}.`,
        (_field$astNode2 = field.astNode) === null || _field$astNode2 === void 0
          ? void 0
          : _field$astNode2.type,
      );
    }

    if (isRequiredInputField(field) && field.deprecationReason != null) {
      var _field$astNode3;

      context.reportError(
        `Required input field ${inputObj.name}.${field.name} cannot be deprecated.`,
        [
          getDeprecatedDirectiveNode(field.astNode),
          (_field$astNode3 = field.astNode) === null ||
          _field$astNode3 === void 0
            ? void 0
            : _field$astNode3.type,
        ],
      );
    }
  }
}

function createInputObjectCircularRefsValidator(context) {
  // Modified copy of algorithm from 'src/validation/rules/NoFragmentCycles.js'.
  // Tracks already visited types to maintain O(N) and to ensure that cycles
  // are not redundantly reported.
  const visitedTypes = Object.create(null); // Array of types nodes used to produce meaningful errors

  const fieldPath = []; // Position in the type path

  const fieldPathIndexByTypeName = Object.create(null);
  return detectCycleRecursive; // This does a straight-forward DFS to find cycles.
  // It does not terminate when a cycle was found but continues to explore
  // the graph to find all possible cycles.

  function detectCycleRecursive(inputObj) {
    if (visitedTypes[inputObj.name]) {
      return;
    }

    visitedTypes[inputObj.name] = true;
    fieldPathIndexByTypeName[inputObj.name] = fieldPath.length;
    const fields = Object.values(inputObj.getFields());

    for (const field of fields) {
      if (isNonNullType(field.type) && isInputObjectType(field.type.ofType)) {
        const fieldType = field.type.ofType;
        const cycleIndex = fieldPathIndexByTypeName[fieldType.name];
        fieldPath.push(field);

        if (cycleIndex === undefined) {
          detectCycleRecursive(fieldType);
        } else {
          const cyclePath = fieldPath.slice(cycleIndex);
          const pathStr = cyclePath.map((fieldObj) => fieldObj.name).join('.');
          context.reportError(
            `Cannot reference Input Object "${fieldType.name}" within itself through a series of non-null fields: "${pathStr}".`,
            cyclePath.map((fieldObj) => fieldObj.astNode),
          );
        }

        fieldPath.pop();
      }
    }

    fieldPathIndexByTypeName[inputObj.name] = undefined;
  }
}

function getAllImplementsInterfaceNodes(type, iface) {
  const { astNode, extensionASTNodes } = type;
  const nodes =
    astNode != null ? [astNode, ...extensionASTNodes] : extensionASTNodes; // FIXME: https://github.com/graphql/graphql-js/issues/2203

  return nodes
    .flatMap((typeNode) => {
      var _typeNode$interfaces;

      return (
        /* c8 ignore next */
        (_typeNode$interfaces = typeNode.interfaces) !== null &&
          _typeNode$interfaces !== void 0
          ? _typeNode$interfaces
          : []
      );
    })
    .filter((ifaceNode) => ifaceNode.name.value === iface.name);
}

function getUnionMemberTypeNodes(union, typeName) {
  const { astNode, extensionASTNodes } = union;
  const nodes =
    astNode != null ? [astNode, ...extensionASTNodes] : extensionASTNodes; // FIXME: https://github.com/graphql/graphql-js/issues/2203

  return nodes
    .flatMap((unionNode) => {
      var _unionNode$types;

      return (
        /* c8 ignore next */
        (_unionNode$types = unionNode.types) !== null &&
          _unionNode$types !== void 0
          ? _unionNode$types
          : []
      );
    })
    .filter((typeNode) => typeNode.name.value === typeName);
}

function getDeprecatedDirectiveNode(definitionNode) {
  var _definitionNode$direc;

  return definitionNode === null || definitionNode === void 0
    ? void 0
    : (_definitionNode$direc = definitionNode.directives) === null ||
      _definitionNode$direc === void 0
    ? void 0
    : _definitionNode$direc.find(
        (node) => node.name.value === GraphQLDeprecatedDirective.name,
      );
}

function typeFromAST(schema, typeNode) {
  switch (typeNode.kind) {
    case Kind.LIST_TYPE: {
      const innerType = typeFromAST(schema, typeNode.type);
      return innerType && new GraphQLList(innerType);
    }

    case Kind.NON_NULL_TYPE: {
      const innerType = typeFromAST(schema, typeNode.type);
      return innerType && new GraphQLNonNull(innerType);
    }

    case Kind.NAMED_TYPE:
      return schema.getType(typeNode.name.value);
  }
}

/**
 * TypeInfo is a utility class which, given a GraphQL schema, can keep track
 * of the current field and type definitions at any point in a GraphQL document
 * AST during a recursive descent by calling `enter(node)` and `leave(node)`.
 */

class TypeInfo {
  constructor(
    schema,
    /**
     * Initial type may be provided in rare cases to facilitate traversals
     *  beginning somewhere other than documents.
     */
    initialType,
    /** @deprecated will be removed in 17.0.0 */
    getFieldDefFn,
  ) {
    this._schema = schema;
    this._typeStack = [];
    this._parentTypeStack = [];
    this._inputTypeStack = [];
    this._fieldDefStack = [];
    this._defaultValueStack = [];
    this._directive = null;
    this._argument = null;
    this._enumValue = null;
    this._getFieldDef =
      getFieldDefFn !== null && getFieldDefFn !== void 0
        ? getFieldDefFn
        : getFieldDef$1;

    if (initialType) {
      if (isInputType(initialType)) {
        this._inputTypeStack.push(initialType);
      }

      if (isCompositeType(initialType)) {
        this._parentTypeStack.push(initialType);
      }

      if (isOutputType(initialType)) {
        this._typeStack.push(initialType);
      }
    }
  }

  get [Symbol.toStringTag]() {
    return 'TypeInfo';
  }

  getType() {
    if (this._typeStack.length > 0) {
      return this._typeStack[this._typeStack.length - 1];
    }
  }

  getParentType() {
    if (this._parentTypeStack.length > 0) {
      return this._parentTypeStack[this._parentTypeStack.length - 1];
    }
  }

  getInputType() {
    if (this._inputTypeStack.length > 0) {
      return this._inputTypeStack[this._inputTypeStack.length - 1];
    }
  }

  getParentInputType() {
    if (this._inputTypeStack.length > 1) {
      return this._inputTypeStack[this._inputTypeStack.length - 2];
    }
  }

  getFieldDef() {
    if (this._fieldDefStack.length > 0) {
      return this._fieldDefStack[this._fieldDefStack.length - 1];
    }
  }

  getDefaultValue() {
    if (this._defaultValueStack.length > 0) {
      return this._defaultValueStack[this._defaultValueStack.length - 1];
    }
  }

  getDirective() {
    return this._directive;
  }

  getArgument() {
    return this._argument;
  }

  getEnumValue() {
    return this._enumValue;
  }

  enter(node) {
    const schema = this._schema; // Note: many of the types below are explicitly typed as "unknown" to drop
    // any assumptions of a valid schema to ensure runtime types are properly
    // checked before continuing since TypeInfo is used as part of validation
    // which occurs before guarantees of schema and document validity.

    switch (node.kind) {
      case Kind.SELECTION_SET: {
        const namedType = getNamedType(this.getType());

        this._parentTypeStack.push(
          isCompositeType(namedType) ? namedType : undefined,
        );

        break;
      }

      case Kind.FIELD: {
        const parentType = this.getParentType();
        let fieldDef;
        let fieldType;

        if (parentType) {
          fieldDef = this._getFieldDef(schema, parentType, node);

          if (fieldDef) {
            fieldType = fieldDef.type;
          }
        }

        this._fieldDefStack.push(fieldDef);

        this._typeStack.push(isOutputType(fieldType) ? fieldType : undefined);

        break;
      }

      case Kind.DIRECTIVE:
        this._directive = schema.getDirective(node.name.value);
        break;

      case Kind.OPERATION_DEFINITION: {
        const rootType = schema.getRootType(node.operation);

        this._typeStack.push(isObjectType(rootType) ? rootType : undefined);

        break;
      }

      case Kind.INLINE_FRAGMENT:
      case Kind.FRAGMENT_DEFINITION: {
        const typeConditionAST = node.typeCondition;
        const outputType = typeConditionAST
          ? typeFromAST(schema, typeConditionAST)
          : getNamedType(this.getType());

        this._typeStack.push(isOutputType(outputType) ? outputType : undefined);

        break;
      }

      case Kind.VARIABLE_DEFINITION: {
        const inputType = typeFromAST(schema, node.type);

        this._inputTypeStack.push(
          isInputType(inputType) ? inputType : undefined,
        );

        break;
      }

      case Kind.ARGUMENT: {
        var _this$getDirective;

        let argDef;
        let argType;
        const fieldOrDirective =
          (_this$getDirective = this.getDirective()) !== null &&
          _this$getDirective !== void 0
            ? _this$getDirective
            : this.getFieldDef();

        if (fieldOrDirective) {
          argDef = fieldOrDirective.args.find(
            (arg) => arg.name === node.name.value,
          );

          if (argDef) {
            argType = argDef.type;
          }
        }

        this._argument = argDef;

        this._defaultValueStack.push(argDef ? argDef.defaultValue : undefined);

        this._inputTypeStack.push(isInputType(argType) ? argType : undefined);

        break;
      }

      case Kind.LIST: {
        const listType = getNullableType(this.getInputType());
        const itemType = isListType(listType) ? listType.ofType : listType; // List positions never have a default value.

        this._defaultValueStack.push(undefined);

        this._inputTypeStack.push(isInputType(itemType) ? itemType : undefined);

        break;
      }

      case Kind.OBJECT_FIELD: {
        const objectType = getNamedType(this.getInputType());
        let inputFieldType;
        let inputField;

        if (isInputObjectType(objectType)) {
          inputField = objectType.getFields()[node.name.value];

          if (inputField) {
            inputFieldType = inputField.type;
          }
        }

        this._defaultValueStack.push(
          inputField ? inputField.defaultValue : undefined,
        );

        this._inputTypeStack.push(
          isInputType(inputFieldType) ? inputFieldType : undefined,
        );

        break;
      }

      case Kind.ENUM: {
        const enumType = getNamedType(this.getInputType());
        let enumValue;

        if (isEnumType(enumType)) {
          enumValue = enumType.getValue(node.value);
        }

        this._enumValue = enumValue;
        break;
      }
    }
  }

  leave(node) {
    switch (node.kind) {
      case Kind.SELECTION_SET:
        this._parentTypeStack.pop();

        break;

      case Kind.FIELD:
        this._fieldDefStack.pop();

        this._typeStack.pop();

        break;

      case Kind.DIRECTIVE:
        this._directive = null;
        break;

      case Kind.OPERATION_DEFINITION:
      case Kind.INLINE_FRAGMENT:
      case Kind.FRAGMENT_DEFINITION:
        this._typeStack.pop();

        break;

      case Kind.VARIABLE_DEFINITION:
        this._inputTypeStack.pop();

        break;

      case Kind.ARGUMENT:
        this._argument = null;

        this._defaultValueStack.pop();

        this._inputTypeStack.pop();

        break;

      case Kind.LIST:
      case Kind.OBJECT_FIELD:
        this._defaultValueStack.pop();

        this._inputTypeStack.pop();

        break;

      case Kind.ENUM:
        this._enumValue = null;
        break;
    }
  }
}

/**
 * Not exactly the same as the executor's definition of getFieldDef, in this
 * statically evaluated environment we do not always have an Object type,
 * and need to handle Interface and Union types.
 */
function getFieldDef$1(schema, parentType, fieldNode) {
  const name = fieldNode.name.value;

  if (
    name === SchemaMetaFieldDef.name &&
    schema.getQueryType() === parentType
  ) {
    return SchemaMetaFieldDef;
  }

  if (name === TypeMetaFieldDef.name && schema.getQueryType() === parentType) {
    return TypeMetaFieldDef;
  }

  if (name === TypeNameMetaFieldDef.name && isCompositeType(parentType)) {
    return TypeNameMetaFieldDef;
  }

  if (isObjectType(parentType) || isInterfaceType(parentType)) {
    return parentType.getFields()[name];
  }
}
/**
 * Creates a new visitor instance which maintains a provided TypeInfo instance
 * along with visiting visitor.
 */

function visitWithTypeInfo(typeInfo, visitor) {
  return {
    enter(...args) {
      const node = args[0];
      typeInfo.enter(node);
      const fn = getEnterLeaveForKind(visitor, node.kind).enter;

      if (fn) {
        const result = fn.apply(visitor, args);

        if (result !== undefined) {
          typeInfo.leave(node);

          if (isNode$2(result)) {
            typeInfo.enter(result);
          }
        }

        return result;
      }
    },

    leave(...args) {
      const node = args[0];
      const fn = getEnterLeaveForKind(visitor, node.kind).leave;
      let result;

      if (fn) {
        result = fn.apply(visitor, args);
      }

      typeInfo.leave(node);
      return result;
    },
  };
}

function isDefinitionNode(node) {
  return (
    isExecutableDefinitionNode(node) ||
    isTypeSystemDefinitionNode(node) ||
    isTypeSystemExtensionNode(node)
  );
}
function isExecutableDefinitionNode(node) {
  return (
    node.kind === Kind.OPERATION_DEFINITION ||
    node.kind === Kind.FRAGMENT_DEFINITION
  );
}
function isSelectionNode(node) {
  return (
    node.kind === Kind.FIELD ||
    node.kind === Kind.FRAGMENT_SPREAD ||
    node.kind === Kind.INLINE_FRAGMENT
  );
}
function isValueNode(node) {
  return (
    node.kind === Kind.VARIABLE ||
    node.kind === Kind.INT ||
    node.kind === Kind.FLOAT ||
    node.kind === Kind.STRING ||
    node.kind === Kind.BOOLEAN ||
    node.kind === Kind.NULL ||
    node.kind === Kind.ENUM ||
    node.kind === Kind.LIST ||
    node.kind === Kind.OBJECT
  );
}
function isConstValueNode(node) {
  return (
    isValueNode(node) &&
    (node.kind === Kind.LIST
      ? node.values.some(isConstValueNode)
      : node.kind === Kind.OBJECT
      ? node.fields.some((field) => isConstValueNode(field.value))
      : node.kind !== Kind.VARIABLE)
  );
}
function isTypeNode(node) {
  return (
    node.kind === Kind.NAMED_TYPE ||
    node.kind === Kind.LIST_TYPE ||
    node.kind === Kind.NON_NULL_TYPE
  );
}
function isTypeSystemDefinitionNode(node) {
  return (
    node.kind === Kind.SCHEMA_DEFINITION ||
    isTypeDefinitionNode(node) ||
    node.kind === Kind.DIRECTIVE_DEFINITION
  );
}
function isTypeDefinitionNode(node) {
  return (
    node.kind === Kind.SCALAR_TYPE_DEFINITION ||
    node.kind === Kind.OBJECT_TYPE_DEFINITION ||
    node.kind === Kind.INTERFACE_TYPE_DEFINITION ||
    node.kind === Kind.UNION_TYPE_DEFINITION ||
    node.kind === Kind.ENUM_TYPE_DEFINITION ||
    node.kind === Kind.INPUT_OBJECT_TYPE_DEFINITION
  );
}
function isTypeSystemExtensionNode(node) {
  return node.kind === Kind.SCHEMA_EXTENSION || isTypeExtensionNode(node);
}
function isTypeExtensionNode(node) {
  return (
    node.kind === Kind.SCALAR_TYPE_EXTENSION ||
    node.kind === Kind.OBJECT_TYPE_EXTENSION ||
    node.kind === Kind.INTERFACE_TYPE_EXTENSION ||
    node.kind === Kind.UNION_TYPE_EXTENSION ||
    node.kind === Kind.ENUM_TYPE_EXTENSION ||
    node.kind === Kind.INPUT_OBJECT_TYPE_EXTENSION
  );
}

/**
 * Executable definitions
 *
 * A GraphQL document is only valid for execution if all definitions are either
 * operation or fragment definitions.
 *
 * See https://spec.graphql.org/draft/#sec-Executable-Definitions
 */
function ExecutableDefinitionsRule(context) {
  return {
    Document(node) {
      for (const definition of node.definitions) {
        if (!isExecutableDefinitionNode(definition)) {
          const defName =
            definition.kind === Kind.SCHEMA_DEFINITION ||
            definition.kind === Kind.SCHEMA_EXTENSION
              ? 'schema'
              : '"' + definition.name.value + '"';
          context.reportError(
            new GraphQLError(`The ${defName} definition is not executable.`, {
              nodes: definition,
            }),
          );
        }
      }

      return false;
    },
  };
}

/**
 * Fields on correct type
 *
 * A GraphQL document is only valid if all fields selected are defined by the
 * parent type, or are an allowed meta field such as __typename.
 *
 * See https://spec.graphql.org/draft/#sec-Field-Selections
 */
function FieldsOnCorrectTypeRule(context) {
  return {
    Field(node) {
      const type = context.getParentType();

      if (type) {
        const fieldDef = context.getFieldDef();

        if (!fieldDef) {
          // This field doesn't exist, lets look for suggestions.
          const schema = context.getSchema();
          const fieldName = node.name.value; // First determine if there are any suggested types to condition on.

          let suggestion = didYouMean(
            'to use an inline fragment on',
            getSuggestedTypeNames(schema, type, fieldName),
          ); // If there are no suggested types, then perhaps this was a typo?

          if (suggestion === '') {
            suggestion = didYouMean(getSuggestedFieldNames(type, fieldName));
          } // Report an error, including helpful suggestions.

          context.reportError(
            new GraphQLError(
              `Cannot query field "${fieldName}" on type "${type.name}".` +
                suggestion,
              {
                nodes: node,
              },
            ),
          );
        }
      }
    },
  };
}
/**
 * Go through all of the implementations of type, as well as the interfaces that
 * they implement. If any of those types include the provided field, suggest them,
 * sorted by how often the type is referenced.
 */

function getSuggestedTypeNames(schema, type, fieldName) {
  if (!isAbstractType(type)) {
    // Must be an Object type, which does not have possible fields.
    return [];
  }

  const suggestedTypes = new Set();
  const usageCount = Object.create(null);

  for (const possibleType of schema.getPossibleTypes(type)) {
    if (!possibleType.getFields()[fieldName]) {
      continue;
    } // This object type defines this field.

    suggestedTypes.add(possibleType);
    usageCount[possibleType.name] = 1;

    for (const possibleInterface of possibleType.getInterfaces()) {
      var _usageCount$possibleI;

      if (!possibleInterface.getFields()[fieldName]) {
        continue;
      } // This interface type defines this field.

      suggestedTypes.add(possibleInterface);
      usageCount[possibleInterface.name] =
        ((_usageCount$possibleI = usageCount[possibleInterface.name]) !==
          null && _usageCount$possibleI !== void 0
          ? _usageCount$possibleI
          : 0) + 1;
    }
  }

  return [...suggestedTypes]
    .sort((typeA, typeB) => {
      // Suggest both interface and object types based on how common they are.
      const usageCountDiff = usageCount[typeB.name] - usageCount[typeA.name];

      if (usageCountDiff !== 0) {
        return usageCountDiff;
      } // Suggest super types first followed by subtypes

      if (isInterfaceType(typeA) && schema.isSubType(typeA, typeB)) {
        return -1;
      }

      if (isInterfaceType(typeB) && schema.isSubType(typeB, typeA)) {
        return 1;
      }

      return naturalCompare(typeA.name, typeB.name);
    })
    .map((x) => x.name);
}
/**
 * For the field name provided, determine if there are any similar field names
 * that may be the result of a typo.
 */

function getSuggestedFieldNames(type, fieldName) {
  if (isObjectType(type) || isInterfaceType(type)) {
    const possibleFieldNames = Object.keys(type.getFields());
    return suggestionList(fieldName, possibleFieldNames);
  } // Otherwise, must be a Union type, which does not define fields.

  return [];
}

/**
 * Fragments on composite type
 *
 * Fragments use a type condition to determine if they apply, since fragments
 * can only be spread into a composite type (object, interface, or union), the
 * type condition must also be a composite type.
 *
 * See https://spec.graphql.org/draft/#sec-Fragments-On-Composite-Types
 */
function FragmentsOnCompositeTypesRule(context) {
  return {
    InlineFragment(node) {
      const typeCondition = node.typeCondition;

      if (typeCondition) {
        const type = typeFromAST(context.getSchema(), typeCondition);

        if (type && !isCompositeType(type)) {
          const typeStr = print(typeCondition);
          context.reportError(
            new GraphQLError(
              `Fragment cannot condition on non composite type "${typeStr}".`,
              {
                nodes: typeCondition,
              },
            ),
          );
        }
      }
    },

    FragmentDefinition(node) {
      const type = typeFromAST(context.getSchema(), node.typeCondition);

      if (type && !isCompositeType(type)) {
        const typeStr = print(node.typeCondition);
        context.reportError(
          new GraphQLError(
            `Fragment "${node.name.value}" cannot condition on non composite type "${typeStr}".`,
            {
              nodes: node.typeCondition,
            },
          ),
        );
      }
    },
  };
}

/**
 * Known argument names
 *
 * A GraphQL field is only valid if all supplied arguments are defined by
 * that field.
 *
 * See https://spec.graphql.org/draft/#sec-Argument-Names
 * See https://spec.graphql.org/draft/#sec-Directives-Are-In-Valid-Locations
 */
function KnownArgumentNamesRule(context) {
  return {
    // eslint-disable-next-line new-cap
    ...KnownArgumentNamesOnDirectivesRule(context),

    Argument(argNode) {
      const argDef = context.getArgument();
      const fieldDef = context.getFieldDef();
      const parentType = context.getParentType();

      if (!argDef && fieldDef && parentType) {
        const argName = argNode.name.value;
        const knownArgsNames = fieldDef.args.map((arg) => arg.name);
        const suggestions = suggestionList(argName, knownArgsNames);
        context.reportError(
          new GraphQLError(
            `Unknown argument "${argName}" on field "${parentType.name}.${fieldDef.name}".` +
              didYouMean(suggestions),
            {
              nodes: argNode,
            },
          ),
        );
      }
    },
  };
}
/**
 * @internal
 */

function KnownArgumentNamesOnDirectivesRule(context) {
  const directiveArgs = Object.create(null);
  const schema = context.getSchema();
  const definedDirectives = schema
    ? schema.getDirectives()
    : specifiedDirectives;

  for (const directive of definedDirectives) {
    directiveArgs[directive.name] = directive.args.map((arg) => arg.name);
  }

  const astDefinitions = context.getDocument().definitions;

  for (const def of astDefinitions) {
    if (def.kind === Kind.DIRECTIVE_DEFINITION) {
      var _def$arguments;

      // FIXME: https://github.com/graphql/graphql-js/issues/2203

      /* c8 ignore next */
      const argsNodes =
        (_def$arguments = def.arguments) !== null && _def$arguments !== void 0
          ? _def$arguments
          : [];
      directiveArgs[def.name.value] = argsNodes.map((arg) => arg.name.value);
    }
  }

  return {
    Directive(directiveNode) {
      const directiveName = directiveNode.name.value;
      const knownArgs = directiveArgs[directiveName];

      if (directiveNode.arguments && knownArgs) {
        for (const argNode of directiveNode.arguments) {
          const argName = argNode.name.value;

          if (!knownArgs.includes(argName)) {
            const suggestions = suggestionList(argName, knownArgs);
            context.reportError(
              new GraphQLError(
                `Unknown argument "${argName}" on directive "@${directiveName}".` +
                  didYouMean(suggestions),
                {
                  nodes: argNode,
                },
              ),
            );
          }
        }
      }

      return false;
    },
  };
}

/**
 * Known directives
 *
 * A GraphQL document is only valid if all `@directives` are known by the
 * schema and legally positioned.
 *
 * See https://spec.graphql.org/draft/#sec-Directives-Are-Defined
 */
function KnownDirectivesRule(context) {
  const locationsMap = Object.create(null);
  const schema = context.getSchema();
  const definedDirectives = schema
    ? schema.getDirectives()
    : specifiedDirectives;

  for (const directive of definedDirectives) {
    locationsMap[directive.name] = directive.locations;
  }

  const astDefinitions = context.getDocument().definitions;

  for (const def of astDefinitions) {
    if (def.kind === Kind.DIRECTIVE_DEFINITION) {
      locationsMap[def.name.value] = def.locations.map((name) => name.value);
    }
  }

  return {
    Directive(node, _key, _parent, _path, ancestors) {
      const name = node.name.value;
      const locations = locationsMap[name];

      if (!locations) {
        context.reportError(
          new GraphQLError(`Unknown directive "@${name}".`, {
            nodes: node,
          }),
        );
        return;
      }

      const candidateLocation = getDirectiveLocationForASTPath(ancestors);

      if (candidateLocation && !locations.includes(candidateLocation)) {
        context.reportError(
          new GraphQLError(
            `Directive "@${name}" may not be used on ${candidateLocation}.`,
            {
              nodes: node,
            },
          ),
        );
      }
    },
  };
}

function getDirectiveLocationForASTPath(ancestors) {
  const appliedTo = ancestors[ancestors.length - 1];
  'kind' in appliedTo || invariant(false);

  switch (appliedTo.kind) {
    case Kind.OPERATION_DEFINITION:
      return getDirectiveLocationForOperation(appliedTo.operation);

    case Kind.FIELD:
      return DirectiveLocation.FIELD;

    case Kind.FRAGMENT_SPREAD:
      return DirectiveLocation.FRAGMENT_SPREAD;

    case Kind.INLINE_FRAGMENT:
      return DirectiveLocation.INLINE_FRAGMENT;

    case Kind.FRAGMENT_DEFINITION:
      return DirectiveLocation.FRAGMENT_DEFINITION;

    case Kind.VARIABLE_DEFINITION:
      return DirectiveLocation.VARIABLE_DEFINITION;

    case Kind.SCHEMA_DEFINITION:
    case Kind.SCHEMA_EXTENSION:
      return DirectiveLocation.SCHEMA;

    case Kind.SCALAR_TYPE_DEFINITION:
    case Kind.SCALAR_TYPE_EXTENSION:
      return DirectiveLocation.SCALAR;

    case Kind.OBJECT_TYPE_DEFINITION:
    case Kind.OBJECT_TYPE_EXTENSION:
      return DirectiveLocation.OBJECT;

    case Kind.FIELD_DEFINITION:
      return DirectiveLocation.FIELD_DEFINITION;

    case Kind.INTERFACE_TYPE_DEFINITION:
    case Kind.INTERFACE_TYPE_EXTENSION:
      return DirectiveLocation.INTERFACE;

    case Kind.UNION_TYPE_DEFINITION:
    case Kind.UNION_TYPE_EXTENSION:
      return DirectiveLocation.UNION;

    case Kind.ENUM_TYPE_DEFINITION:
    case Kind.ENUM_TYPE_EXTENSION:
      return DirectiveLocation.ENUM;

    case Kind.ENUM_VALUE_DEFINITION:
      return DirectiveLocation.ENUM_VALUE;

    case Kind.INPUT_OBJECT_TYPE_DEFINITION:
    case Kind.INPUT_OBJECT_TYPE_EXTENSION:
      return DirectiveLocation.INPUT_OBJECT;

    case Kind.INPUT_VALUE_DEFINITION: {
      const parentNode = ancestors[ancestors.length - 3];
      'kind' in parentNode || invariant(false);
      return parentNode.kind === Kind.INPUT_OBJECT_TYPE_DEFINITION
        ? DirectiveLocation.INPUT_FIELD_DEFINITION
        : DirectiveLocation.ARGUMENT_DEFINITION;
    }
    // Not reachable, all possible types have been considered.

    /* c8 ignore next */

    default:
      invariant(false, 'Unexpected kind: ' + inspect(appliedTo.kind));
  }
}

function getDirectiveLocationForOperation(operation) {
  switch (operation) {
    case OperationTypeNode.QUERY:
      return DirectiveLocation.QUERY;

    case OperationTypeNode.MUTATION:
      return DirectiveLocation.MUTATION;

    case OperationTypeNode.SUBSCRIPTION:
      return DirectiveLocation.SUBSCRIPTION;
  }
}

/**
 * Known fragment names
 *
 * A GraphQL document is only valid if all `...Fragment` fragment spreads refer
 * to fragments defined in the same document.
 *
 * See https://spec.graphql.org/draft/#sec-Fragment-spread-target-defined
 */
function KnownFragmentNamesRule(context) {
  return {
    FragmentSpread(node) {
      const fragmentName = node.name.value;
      const fragment = context.getFragment(fragmentName);

      if (!fragment) {
        context.reportError(
          new GraphQLError(`Unknown fragment "${fragmentName}".`, {
            nodes: node.name,
          }),
        );
      }
    },
  };
}

/**
 * Known type names
 *
 * A GraphQL document is only valid if referenced types (specifically
 * variable definitions and fragment conditions) are defined by the type schema.
 *
 * See https://spec.graphql.org/draft/#sec-Fragment-Spread-Type-Existence
 */
function KnownTypeNamesRule(context) {
  const schema = context.getSchema();
  const existingTypesMap = schema ? schema.getTypeMap() : Object.create(null);
  const definedTypes = Object.create(null);

  for (const def of context.getDocument().definitions) {
    if (isTypeDefinitionNode(def)) {
      definedTypes[def.name.value] = true;
    }
  }

  const typeNames = [
    ...Object.keys(existingTypesMap),
    ...Object.keys(definedTypes),
  ];
  return {
    NamedType(node, _1, parent, _2, ancestors) {
      const typeName = node.name.value;

      if (!existingTypesMap[typeName] && !definedTypes[typeName]) {
        var _ancestors$;

        const definitionNode =
          (_ancestors$ = ancestors[2]) !== null && _ancestors$ !== void 0
            ? _ancestors$
            : parent;
        const isSDL = definitionNode != null && isSDLNode(definitionNode);

        if (isSDL && standardTypeNames.includes(typeName)) {
          return;
        }

        const suggestedTypes = suggestionList(
          typeName,
          isSDL ? standardTypeNames.concat(typeNames) : typeNames,
        );
        context.reportError(
          new GraphQLError(
            `Unknown type "${typeName}".` + didYouMean(suggestedTypes),
            {
              nodes: node,
            },
          ),
        );
      }
    },
  };
}
const standardTypeNames = [...specifiedScalarTypes, ...introspectionTypes].map(
  (type) => type.name,
);

function isSDLNode(value) {
  return (
    'kind' in value &&
    (isTypeSystemDefinitionNode(value) || isTypeSystemExtensionNode(value))
  );
}

/**
 * Lone anonymous operation
 *
 * A GraphQL document is only valid if when it contains an anonymous operation
 * (the query short-hand) that it contains only that one operation definition.
 *
 * See https://spec.graphql.org/draft/#sec-Lone-Anonymous-Operation
 */
function LoneAnonymousOperationRule(context) {
  let operationCount = 0;
  return {
    Document(node) {
      operationCount = node.definitions.filter(
        (definition) => definition.kind === Kind.OPERATION_DEFINITION,
      ).length;
    },

    OperationDefinition(node) {
      if (!node.name && operationCount > 1) {
        context.reportError(
          new GraphQLError(
            'This anonymous operation must be the only defined operation.',
            {
              nodes: node,
            },
          ),
        );
      }
    },
  };
}

/**
 * Lone Schema definition
 *
 * A GraphQL document is only valid if it contains only one schema definition.
 */
function LoneSchemaDefinitionRule(context) {
  var _ref, _ref2, _oldSchema$astNode;

  const oldSchema = context.getSchema();
  const alreadyDefined =
    (_ref =
      (_ref2 =
        (_oldSchema$astNode =
          oldSchema === null || oldSchema === void 0
            ? void 0
            : oldSchema.astNode) !== null && _oldSchema$astNode !== void 0
          ? _oldSchema$astNode
          : oldSchema === null || oldSchema === void 0
          ? void 0
          : oldSchema.getQueryType()) !== null && _ref2 !== void 0
        ? _ref2
        : oldSchema === null || oldSchema === void 0
        ? void 0
        : oldSchema.getMutationType()) !== null && _ref !== void 0
      ? _ref
      : oldSchema === null || oldSchema === void 0
      ? void 0
      : oldSchema.getSubscriptionType();
  let schemaDefinitionsCount = 0;
  return {
    SchemaDefinition(node) {
      if (alreadyDefined) {
        context.reportError(
          new GraphQLError(
            'Cannot define a new schema within a schema extension.',
            {
              nodes: node,
            },
          ),
        );
        return;
      }

      if (schemaDefinitionsCount > 0) {
        context.reportError(
          new GraphQLError('Must provide only one schema definition.', {
            nodes: node,
          }),
        );
      }

      ++schemaDefinitionsCount;
    },
  };
}

/**
 * No fragment cycles
 *
 * The graph of fragment spreads must not form any cycles including spreading itself.
 * Otherwise an operation could infinitely spread or infinitely execute on cycles in the underlying data.
 *
 * See https://spec.graphql.org/draft/#sec-Fragment-spreads-must-not-form-cycles
 */
function NoFragmentCyclesRule(context) {
  // Tracks already visited fragments to maintain O(N) and to ensure that cycles
  // are not redundantly reported.
  const visitedFrags = Object.create(null); // Array of AST nodes used to produce meaningful errors

  const spreadPath = []; // Position in the spread path

  const spreadPathIndexByName = Object.create(null);
  return {
    OperationDefinition: () => false,

    FragmentDefinition(node) {
      detectCycleRecursive(node);
      return false;
    },
  }; // This does a straight-forward DFS to find cycles.
  // It does not terminate when a cycle was found but continues to explore
  // the graph to find all possible cycles.

  function detectCycleRecursive(fragment) {
    if (visitedFrags[fragment.name.value]) {
      return;
    }

    const fragmentName = fragment.name.value;
    visitedFrags[fragmentName] = true;
    const spreadNodes = context.getFragmentSpreads(fragment.selectionSet);

    if (spreadNodes.length === 0) {
      return;
    }

    spreadPathIndexByName[fragmentName] = spreadPath.length;

    for (const spreadNode of spreadNodes) {
      const spreadName = spreadNode.name.value;
      const cycleIndex = spreadPathIndexByName[spreadName];
      spreadPath.push(spreadNode);

      if (cycleIndex === undefined) {
        const spreadFragment = context.getFragment(spreadName);

        if (spreadFragment) {
          detectCycleRecursive(spreadFragment);
        }
      } else {
        const cyclePath = spreadPath.slice(cycleIndex);
        const viaPath = cyclePath
          .slice(0, -1)
          .map((s) => '"' + s.name.value + '"')
          .join(', ');
        context.reportError(
          new GraphQLError(
            `Cannot spread fragment "${spreadName}" within itself` +
              (viaPath !== '' ? ` via ${viaPath}.` : '.'),
            {
              nodes: cyclePath,
            },
          ),
        );
      }

      spreadPath.pop();
    }

    spreadPathIndexByName[fragmentName] = undefined;
  }
}

/**
 * No undefined variables
 *
 * A GraphQL operation is only valid if all variables encountered, both directly
 * and via fragment spreads, are defined by that operation.
 *
 * See https://spec.graphql.org/draft/#sec-All-Variable-Uses-Defined
 */
function NoUndefinedVariablesRule(context) {
  let variableNameDefined = Object.create(null);
  return {
    OperationDefinition: {
      enter() {
        variableNameDefined = Object.create(null);
      },

      leave(operation) {
        const usages = context.getRecursiveVariableUsages(operation);

        for (const { node } of usages) {
          const varName = node.name.value;

          if (variableNameDefined[varName] !== true) {
            context.reportError(
              new GraphQLError(
                operation.name
                  ? `Variable "$${varName}" is not defined by operation "${operation.name.value}".`
                  : `Variable "$${varName}" is not defined.`,
                {
                  nodes: [node, operation],
                },
              ),
            );
          }
        }
      },
    },

    VariableDefinition(node) {
      variableNameDefined[node.variable.name.value] = true;
    },
  };
}

/**
 * No unused fragments
 *
 * A GraphQL document is only valid if all fragment definitions are spread
 * within operations, or spread within other fragments spread within operations.
 *
 * See https://spec.graphql.org/draft/#sec-Fragments-Must-Be-Used
 */
function NoUnusedFragmentsRule(context) {
  const operationDefs = [];
  const fragmentDefs = [];
  return {
    OperationDefinition(node) {
      operationDefs.push(node);
      return false;
    },

    FragmentDefinition(node) {
      fragmentDefs.push(node);
      return false;
    },

    Document: {
      leave() {
        const fragmentNameUsed = Object.create(null);

        for (const operation of operationDefs) {
          for (const fragment of context.getRecursivelyReferencedFragments(
            operation,
          )) {
            fragmentNameUsed[fragment.name.value] = true;
          }
        }

        for (const fragmentDef of fragmentDefs) {
          const fragName = fragmentDef.name.value;

          if (fragmentNameUsed[fragName] !== true) {
            context.reportError(
              new GraphQLError(`Fragment "${fragName}" is never used.`, {
                nodes: fragmentDef,
              }),
            );
          }
        }
      },
    },
  };
}

/**
 * No unused variables
 *
 * A GraphQL operation is only valid if all variables defined by an operation
 * are used, either directly or within a spread fragment.
 *
 * See https://spec.graphql.org/draft/#sec-All-Variables-Used
 */
function NoUnusedVariablesRule(context) {
  let variableDefs = [];
  return {
    OperationDefinition: {
      enter() {
        variableDefs = [];
      },

      leave(operation) {
        const variableNameUsed = Object.create(null);
        const usages = context.getRecursiveVariableUsages(operation);

        for (const { node } of usages) {
          variableNameUsed[node.name.value] = true;
        }

        for (const variableDef of variableDefs) {
          const variableName = variableDef.variable.name.value;

          if (variableNameUsed[variableName] !== true) {
            context.reportError(
              new GraphQLError(
                operation.name
                  ? `Variable "$${variableName}" is never used in operation "${operation.name.value}".`
                  : `Variable "$${variableName}" is never used.`,
                {
                  nodes: variableDef,
                },
              ),
            );
          }
        }
      },
    },

    VariableDefinition(def) {
      variableDefs.push(def);
    },
  };
}

/**
 * Sort ValueNode.
 *
 * This function returns a sorted copy of the given ValueNode.
 *
 * @internal
 */

function sortValueNode(valueNode) {
  switch (valueNode.kind) {
    case Kind.OBJECT:
      return { ...valueNode, fields: sortFields(valueNode.fields) };

    case Kind.LIST:
      return { ...valueNode, values: valueNode.values.map(sortValueNode) };

    case Kind.INT:
    case Kind.FLOAT:
    case Kind.STRING:
    case Kind.BOOLEAN:
    case Kind.NULL:
    case Kind.ENUM:
    case Kind.VARIABLE:
      return valueNode;
  }
}

function sortFields(fields) {
  return fields
    .map((fieldNode) => ({
      ...fieldNode,
      value: sortValueNode(fieldNode.value),
    }))
    .sort((fieldA, fieldB) =>
      naturalCompare(fieldA.name.value, fieldB.name.value),
    );
}

function reasonMessage(reason) {
  if (Array.isArray(reason)) {
    return reason
      .map(
        ([responseName, subReason]) =>
          `subfields "${responseName}" conflict because ` +
          reasonMessage(subReason),
      )
      .join(' and ');
  }

  return reason;
}
/**
 * Overlapping fields can be merged
 *
 * A selection set is only valid if all fields (including spreading any
 * fragments) either correspond to distinct response names or can be merged
 * without ambiguity.
 *
 * See https://spec.graphql.org/draft/#sec-Field-Selection-Merging
 */

function OverlappingFieldsCanBeMergedRule(context) {
  // A memoization for when two fragments are compared "between" each other for
  // conflicts. Two fragments may be compared many times, so memoizing this can
  // dramatically improve the performance of this validator.
  const comparedFragmentPairs = new PairSet(); // A cache for the "field map" and list of fragment names found in any given
  // selection set. Selection sets may be asked for this information multiple
  // times, so this improves the performance of this validator.

  const cachedFieldsAndFragmentNames = new Map();
  return {
    SelectionSet(selectionSet) {
      const conflicts = findConflictsWithinSelectionSet(
        context,
        cachedFieldsAndFragmentNames,
        comparedFragmentPairs,
        context.getParentType(),
        selectionSet,
      );

      for (const [[responseName, reason], fields1, fields2] of conflicts) {
        const reasonMsg = reasonMessage(reason);
        context.reportError(
          new GraphQLError(
            `Fields "${responseName}" conflict because ${reasonMsg}. Use different aliases on the fields to fetch both if this was intentional.`,
            {
              nodes: fields1.concat(fields2),
            },
          ),
        );
      }
    },
  };
}

/**
 * Algorithm:
 *
 * Conflicts occur when two fields exist in a query which will produce the same
 * response name, but represent differing values, thus creating a conflict.
 * The algorithm below finds all conflicts via making a series of comparisons
 * between fields. In order to compare as few fields as possible, this makes
 * a series of comparisons "within" sets of fields and "between" sets of fields.
 *
 * Given any selection set, a collection produces both a set of fields by
 * also including all inline fragments, as well as a list of fragments
 * referenced by fragment spreads.
 *
 * A) Each selection set represented in the document first compares "within" its
 * collected set of fields, finding any conflicts between every pair of
 * overlapping fields.
 * Note: This is the *only time* that a the fields "within" a set are compared
 * to each other. After this only fields "between" sets are compared.
 *
 * B) Also, if any fragment is referenced in a selection set, then a
 * comparison is made "between" the original set of fields and the
 * referenced fragment.
 *
 * C) Also, if multiple fragments are referenced, then comparisons
 * are made "between" each referenced fragment.
 *
 * D) When comparing "between" a set of fields and a referenced fragment, first
 * a comparison is made between each field in the original set of fields and
 * each field in the the referenced set of fields.
 *
 * E) Also, if any fragment is referenced in the referenced selection set,
 * then a comparison is made "between" the original set of fields and the
 * referenced fragment (recursively referring to step D).
 *
 * F) When comparing "between" two fragments, first a comparison is made between
 * each field in the first referenced set of fields and each field in the the
 * second referenced set of fields.
 *
 * G) Also, any fragments referenced by the first must be compared to the
 * second, and any fragments referenced by the second must be compared to the
 * first (recursively referring to step F).
 *
 * H) When comparing two fields, if both have selection sets, then a comparison
 * is made "between" both selection sets, first comparing the set of fields in
 * the first selection set with the set of fields in the second.
 *
 * I) Also, if any fragment is referenced in either selection set, then a
 * comparison is made "between" the other set of fields and the
 * referenced fragment.
 *
 * J) Also, if two fragments are referenced in both selection sets, then a
 * comparison is made "between" the two fragments.
 *
 */
// Find all conflicts found "within" a selection set, including those found
// via spreading in fragments. Called when visiting each SelectionSet in the
// GraphQL Document.
function findConflictsWithinSelectionSet(
  context,
  cachedFieldsAndFragmentNames,
  comparedFragmentPairs,
  parentType,
  selectionSet,
) {
  const conflicts = [];
  const [fieldMap, fragmentNames] = getFieldsAndFragmentNames(
    context,
    cachedFieldsAndFragmentNames,
    parentType,
    selectionSet,
  ); // (A) Find find all conflicts "within" the fields of this selection set.
  // Note: this is the *only place* `collectConflictsWithin` is called.

  collectConflictsWithin(
    context,
    conflicts,
    cachedFieldsAndFragmentNames,
    comparedFragmentPairs,
    fieldMap,
  );

  if (fragmentNames.length !== 0) {
    // (B) Then collect conflicts between these fields and those represented by
    // each spread fragment name found.
    for (let i = 0; i < fragmentNames.length; i++) {
      collectConflictsBetweenFieldsAndFragment(
        context,
        conflicts,
        cachedFieldsAndFragmentNames,
        comparedFragmentPairs,
        false,
        fieldMap,
        fragmentNames[i],
      ); // (C) Then compare this fragment with all other fragments found in this
      // selection set to collect conflicts between fragments spread together.
      // This compares each item in the list of fragment names to every other
      // item in that same list (except for itself).

      for (let j = i + 1; j < fragmentNames.length; j++) {
        collectConflictsBetweenFragments(
          context,
          conflicts,
          cachedFieldsAndFragmentNames,
          comparedFragmentPairs,
          false,
          fragmentNames[i],
          fragmentNames[j],
        );
      }
    }
  }

  return conflicts;
} // Collect all conflicts found between a set of fields and a fragment reference
// including via spreading in any nested fragments.

function collectConflictsBetweenFieldsAndFragment(
  context,
  conflicts,
  cachedFieldsAndFragmentNames,
  comparedFragmentPairs,
  areMutuallyExclusive,
  fieldMap,
  fragmentName,
) {
  const fragment = context.getFragment(fragmentName);

  if (!fragment) {
    return;
  }

  const [fieldMap2, referencedFragmentNames] =
    getReferencedFieldsAndFragmentNames(
      context,
      cachedFieldsAndFragmentNames,
      fragment,
    ); // Do not compare a fragment's fieldMap to itself.

  if (fieldMap === fieldMap2) {
    return;
  } // (D) First collect any conflicts between the provided collection of fields
  // and the collection of fields represented by the given fragment.

  collectConflictsBetween(
    context,
    conflicts,
    cachedFieldsAndFragmentNames,
    comparedFragmentPairs,
    areMutuallyExclusive,
    fieldMap,
    fieldMap2,
  ); // (E) Then collect any conflicts between the provided collection of fields
  // and any fragment names found in the given fragment.

  for (const referencedFragmentName of referencedFragmentNames) {
    // Memoize so two fragments are not compared for conflicts more than once.
    if (
      comparedFragmentPairs.has(
        referencedFragmentName,
        fragmentName,
        areMutuallyExclusive,
      )
    ) {
      continue;
    }

    comparedFragmentPairs.add(
      referencedFragmentName,
      fragmentName,
      areMutuallyExclusive,
    );
    collectConflictsBetweenFieldsAndFragment(
      context,
      conflicts,
      cachedFieldsAndFragmentNames,
      comparedFragmentPairs,
      areMutuallyExclusive,
      fieldMap,
      referencedFragmentName,
    );
  }
} // Collect all conflicts found between two fragments, including via spreading in
// any nested fragments.

function collectConflictsBetweenFragments(
  context,
  conflicts,
  cachedFieldsAndFragmentNames,
  comparedFragmentPairs,
  areMutuallyExclusive,
  fragmentName1,
  fragmentName2,
) {
  // No need to compare a fragment to itself.
  if (fragmentName1 === fragmentName2) {
    return;
  } // Memoize so two fragments are not compared for conflicts more than once.

  if (
    comparedFragmentPairs.has(
      fragmentName1,
      fragmentName2,
      areMutuallyExclusive,
    )
  ) {
    return;
  }

  comparedFragmentPairs.add(fragmentName1, fragmentName2, areMutuallyExclusive);
  const fragment1 = context.getFragment(fragmentName1);
  const fragment2 = context.getFragment(fragmentName2);

  if (!fragment1 || !fragment2) {
    return;
  }

  const [fieldMap1, referencedFragmentNames1] =
    getReferencedFieldsAndFragmentNames(
      context,
      cachedFieldsAndFragmentNames,
      fragment1,
    );
  const [fieldMap2, referencedFragmentNames2] =
    getReferencedFieldsAndFragmentNames(
      context,
      cachedFieldsAndFragmentNames,
      fragment2,
    ); // (F) First, collect all conflicts between these two collections of fields
  // (not including any nested fragments).

  collectConflictsBetween(
    context,
    conflicts,
    cachedFieldsAndFragmentNames,
    comparedFragmentPairs,
    areMutuallyExclusive,
    fieldMap1,
    fieldMap2,
  ); // (G) Then collect conflicts between the first fragment and any nested
  // fragments spread in the second fragment.

  for (const referencedFragmentName2 of referencedFragmentNames2) {
    collectConflictsBetweenFragments(
      context,
      conflicts,
      cachedFieldsAndFragmentNames,
      comparedFragmentPairs,
      areMutuallyExclusive,
      fragmentName1,
      referencedFragmentName2,
    );
  } // (G) Then collect conflicts between the second fragment and any nested
  // fragments spread in the first fragment.

  for (const referencedFragmentName1 of referencedFragmentNames1) {
    collectConflictsBetweenFragments(
      context,
      conflicts,
      cachedFieldsAndFragmentNames,
      comparedFragmentPairs,
      areMutuallyExclusive,
      referencedFragmentName1,
      fragmentName2,
    );
  }
} // Find all conflicts found between two selection sets, including those found
// via spreading in fragments. Called when determining if conflicts exist
// between the sub-fields of two overlapping fields.

function findConflictsBetweenSubSelectionSets(
  context,
  cachedFieldsAndFragmentNames,
  comparedFragmentPairs,
  areMutuallyExclusive,
  parentType1,
  selectionSet1,
  parentType2,
  selectionSet2,
) {
  const conflicts = [];
  const [fieldMap1, fragmentNames1] = getFieldsAndFragmentNames(
    context,
    cachedFieldsAndFragmentNames,
    parentType1,
    selectionSet1,
  );
  const [fieldMap2, fragmentNames2] = getFieldsAndFragmentNames(
    context,
    cachedFieldsAndFragmentNames,
    parentType2,
    selectionSet2,
  ); // (H) First, collect all conflicts between these two collections of field.

  collectConflictsBetween(
    context,
    conflicts,
    cachedFieldsAndFragmentNames,
    comparedFragmentPairs,
    areMutuallyExclusive,
    fieldMap1,
    fieldMap2,
  ); // (I) Then collect conflicts between the first collection of fields and
  // those referenced by each fragment name associated with the second.

  for (const fragmentName2 of fragmentNames2) {
    collectConflictsBetweenFieldsAndFragment(
      context,
      conflicts,
      cachedFieldsAndFragmentNames,
      comparedFragmentPairs,
      areMutuallyExclusive,
      fieldMap1,
      fragmentName2,
    );
  } // (I) Then collect conflicts between the second collection of fields and
  // those referenced by each fragment name associated with the first.

  for (const fragmentName1 of fragmentNames1) {
    collectConflictsBetweenFieldsAndFragment(
      context,
      conflicts,
      cachedFieldsAndFragmentNames,
      comparedFragmentPairs,
      areMutuallyExclusive,
      fieldMap2,
      fragmentName1,
    );
  } // (J) Also collect conflicts between any fragment names by the first and
  // fragment names by the second. This compares each item in the first set of
  // names to each item in the second set of names.

  for (const fragmentName1 of fragmentNames1) {
    for (const fragmentName2 of fragmentNames2) {
      collectConflictsBetweenFragments(
        context,
        conflicts,
        cachedFieldsAndFragmentNames,
        comparedFragmentPairs,
        areMutuallyExclusive,
        fragmentName1,
        fragmentName2,
      );
    }
  }

  return conflicts;
} // Collect all Conflicts "within" one collection of fields.

function collectConflictsWithin(
  context,
  conflicts,
  cachedFieldsAndFragmentNames,
  comparedFragmentPairs,
  fieldMap,
) {
  // A field map is a keyed collection, where each key represents a response
  // name and the value at that key is a list of all fields which provide that
  // response name. For every response name, if there are multiple fields, they
  // must be compared to find a potential conflict.
  for (const [responseName, fields] of Object.entries(fieldMap)) {
    // This compares every field in the list to every other field in this list
    // (except to itself). If the list only has one item, nothing needs to
    // be compared.
    if (fields.length > 1) {
      for (let i = 0; i < fields.length; i++) {
        for (let j = i + 1; j < fields.length; j++) {
          const conflict = findConflict(
            context,
            cachedFieldsAndFragmentNames,
            comparedFragmentPairs,
            false, // within one collection is never mutually exclusive
            responseName,
            fields[i],
            fields[j],
          );

          if (conflict) {
            conflicts.push(conflict);
          }
        }
      }
    }
  }
} // Collect all Conflicts between two collections of fields. This is similar to,
// but different from the `collectConflictsWithin` function above. This check
// assumes that `collectConflictsWithin` has already been called on each
// provided collection of fields. This is true because this validator traverses
// each individual selection set.

function collectConflictsBetween(
  context,
  conflicts,
  cachedFieldsAndFragmentNames,
  comparedFragmentPairs,
  parentFieldsAreMutuallyExclusive,
  fieldMap1,
  fieldMap2,
) {
  // A field map is a keyed collection, where each key represents a response
  // name and the value at that key is a list of all fields which provide that
  // response name. For any response name which appears in both provided field
  // maps, each field from the first field map must be compared to every field
  // in the second field map to find potential conflicts.
  for (const [responseName, fields1] of Object.entries(fieldMap1)) {
    const fields2 = fieldMap2[responseName];

    if (fields2) {
      for (const field1 of fields1) {
        for (const field2 of fields2) {
          const conflict = findConflict(
            context,
            cachedFieldsAndFragmentNames,
            comparedFragmentPairs,
            parentFieldsAreMutuallyExclusive,
            responseName,
            field1,
            field2,
          );

          if (conflict) {
            conflicts.push(conflict);
          }
        }
      }
    }
  }
} // Determines if there is a conflict between two particular fields, including
// comparing their sub-fields.

function findConflict(
  context,
  cachedFieldsAndFragmentNames,
  comparedFragmentPairs,
  parentFieldsAreMutuallyExclusive,
  responseName,
  field1,
  field2,
) {
  const [parentType1, node1, def1] = field1;
  const [parentType2, node2, def2] = field2; // If it is known that two fields could not possibly apply at the same
  // time, due to the parent types, then it is safe to permit them to diverge
  // in aliased field or arguments used as they will not present any ambiguity
  // by differing.
  // It is known that two parent types could never overlap if they are
  // different Object types. Interface or Union types might overlap - if not
  // in the current state of the schema, then perhaps in some future version,
  // thus may not safely diverge.

  const areMutuallyExclusive =
    parentFieldsAreMutuallyExclusive ||
    (parentType1 !== parentType2 &&
      isObjectType(parentType1) &&
      isObjectType(parentType2));

  if (!areMutuallyExclusive) {
    // Two aliases must refer to the same field.
    const name1 = node1.name.value;
    const name2 = node2.name.value;

    if (name1 !== name2) {
      return [
        [responseName, `"${name1}" and "${name2}" are different fields`],
        [node1],
        [node2],
      ];
    } // Two field calls must have the same arguments.

    if (!sameArguments(node1, node2)) {
      return [
        [responseName, 'they have differing arguments'],
        [node1],
        [node2],
      ];
    }
  } // The return type for each field.

  const type1 = def1 === null || def1 === void 0 ? void 0 : def1.type;
  const type2 = def2 === null || def2 === void 0 ? void 0 : def2.type;

  if (type1 && type2 && doTypesConflict(type1, type2)) {
    return [
      [
        responseName,
        `they return conflicting types "${inspect(type1)}" and "${inspect(
          type2,
        )}"`,
      ],
      [node1],
      [node2],
    ];
  } // Collect and compare sub-fields. Use the same "visited fragment names" list
  // for both collections so fields in a fragment reference are never
  // compared to themselves.

  const selectionSet1 = node1.selectionSet;
  const selectionSet2 = node2.selectionSet;

  if (selectionSet1 && selectionSet2) {
    const conflicts = findConflictsBetweenSubSelectionSets(
      context,
      cachedFieldsAndFragmentNames,
      comparedFragmentPairs,
      areMutuallyExclusive,
      getNamedType(type1),
      selectionSet1,
      getNamedType(type2),
      selectionSet2,
    );
    return subfieldConflicts(conflicts, responseName, node1, node2);
  }
}

function sameArguments(node1, node2) {
  const args1 = node1.arguments;
  const args2 = node2.arguments;

  if (args1 === undefined || args1.length === 0) {
    return args2 === undefined || args2.length === 0;
  }

  if (args2 === undefined || args2.length === 0) {
    return false;
  }
  /* c8 ignore next */

  if (args1.length !== args2.length) {
    /* c8 ignore next */
    return false;
    /* c8 ignore next */
  }

  const values2 = new Map(args2.map(({ name, value }) => [name.value, value]));
  return args1.every((arg1) => {
    const value1 = arg1.value;
    const value2 = values2.get(arg1.name.value);

    if (value2 === undefined) {
      return false;
    }

    return stringifyValue$1(value1) === stringifyValue$1(value2);
  });
}

function stringifyValue$1(value) {
  return print(sortValueNode(value));
} // Two types conflict if both types could not apply to a value simultaneously.
// Composite types are ignored as their individual field types will be compared
// later recursively. However List and Non-Null types must match.

function doTypesConflict(type1, type2) {
  if (isListType(type1)) {
    return isListType(type2)
      ? doTypesConflict(type1.ofType, type2.ofType)
      : true;
  }

  if (isListType(type2)) {
    return true;
  }

  if (isNonNullType(type1)) {
    return isNonNullType(type2)
      ? doTypesConflict(type1.ofType, type2.ofType)
      : true;
  }

  if (isNonNullType(type2)) {
    return true;
  }

  if (isLeafType(type1) || isLeafType(type2)) {
    return type1 !== type2;
  }

  return false;
} // Given a selection set, return the collection of fields (a mapping of response
// name to field nodes and definitions) as well as a list of fragment names
// referenced via fragment spreads.

function getFieldsAndFragmentNames(
  context,
  cachedFieldsAndFragmentNames,
  parentType,
  selectionSet,
) {
  const cached = cachedFieldsAndFragmentNames.get(selectionSet);

  if (cached) {
    return cached;
  }

  const nodeAndDefs = Object.create(null);
  const fragmentNames = Object.create(null);

  _collectFieldsAndFragmentNames(
    context,
    parentType,
    selectionSet,
    nodeAndDefs,
    fragmentNames,
  );

  const result = [nodeAndDefs, Object.keys(fragmentNames)];
  cachedFieldsAndFragmentNames.set(selectionSet, result);
  return result;
} // Given a reference to a fragment, return the represented collection of fields
// as well as a list of nested fragment names referenced via fragment spreads.

function getReferencedFieldsAndFragmentNames(
  context,
  cachedFieldsAndFragmentNames,
  fragment,
) {
  // Short-circuit building a type from the node if possible.
  const cached = cachedFieldsAndFragmentNames.get(fragment.selectionSet);

  if (cached) {
    return cached;
  }

  const fragmentType = typeFromAST(context.getSchema(), fragment.typeCondition);
  return getFieldsAndFragmentNames(
    context,
    cachedFieldsAndFragmentNames,
    fragmentType,
    fragment.selectionSet,
  );
}

function _collectFieldsAndFragmentNames(
  context,
  parentType,
  selectionSet,
  nodeAndDefs,
  fragmentNames,
) {
  for (const selection of selectionSet.selections) {
    switch (selection.kind) {
      case Kind.FIELD: {
        const fieldName = selection.name.value;
        let fieldDef;

        if (isObjectType(parentType) || isInterfaceType(parentType)) {
          fieldDef = parentType.getFields()[fieldName];
        }

        const responseName = selection.alias
          ? selection.alias.value
          : fieldName;

        if (!nodeAndDefs[responseName]) {
          nodeAndDefs[responseName] = [];
        }

        nodeAndDefs[responseName].push([parentType, selection, fieldDef]);
        break;
      }

      case Kind.FRAGMENT_SPREAD:
        fragmentNames[selection.name.value] = true;
        break;

      case Kind.INLINE_FRAGMENT: {
        const typeCondition = selection.typeCondition;
        const inlineFragmentType = typeCondition
          ? typeFromAST(context.getSchema(), typeCondition)
          : parentType;

        _collectFieldsAndFragmentNames(
          context,
          inlineFragmentType,
          selection.selectionSet,
          nodeAndDefs,
          fragmentNames,
        );

        break;
      }
    }
  }
} // Given a series of Conflicts which occurred between two sub-fields, generate
// a single Conflict.

function subfieldConflicts(conflicts, responseName, node1, node2) {
  if (conflicts.length > 0) {
    return [
      [responseName, conflicts.map(([reason]) => reason)],
      [node1, ...conflicts.map(([, fields1]) => fields1).flat()],
      [node2, ...conflicts.map(([, , fields2]) => fields2).flat()],
    ];
  }
}
/**
 * A way to keep track of pairs of things when the ordering of the pair does not matter.
 */

class PairSet {
  constructor() {
    this._data = new Map();
  }

  has(a, b, areMutuallyExclusive) {
    var _this$_data$get;

    const [key1, key2] = a < b ? [a, b] : [b, a];
    const result =
      (_this$_data$get = this._data.get(key1)) === null ||
      _this$_data$get === void 0
        ? void 0
        : _this$_data$get.get(key2);

    if (result === undefined) {
      return false;
    } // areMutuallyExclusive being false is a superset of being true, hence if
    // we want to know if this PairSet "has" these two with no exclusivity,
    // we have to ensure it was added as such.

    return areMutuallyExclusive ? true : areMutuallyExclusive === result;
  }

  add(a, b, areMutuallyExclusive) {
    const [key1, key2] = a < b ? [a, b] : [b, a];

    const map = this._data.get(key1);

    if (map === undefined) {
      this._data.set(key1, new Map([[key2, areMutuallyExclusive]]));
    } else {
      map.set(key2, areMutuallyExclusive);
    }
  }
}

/**
 * Possible fragment spread
 *
 * A fragment spread is only valid if the type condition could ever possibly
 * be true: if there is a non-empty intersection of the possible parent types,
 * and possible types which pass the type condition.
 */
function PossibleFragmentSpreadsRule(context) {
  return {
    InlineFragment(node) {
      const fragType = context.getType();
      const parentType = context.getParentType();

      if (
        isCompositeType(fragType) &&
        isCompositeType(parentType) &&
        !doTypesOverlap(context.getSchema(), fragType, parentType)
      ) {
        const parentTypeStr = inspect(parentType);
        const fragTypeStr = inspect(fragType);
        context.reportError(
          new GraphQLError(
            `Fragment cannot be spread here as objects of type "${parentTypeStr}" can never be of type "${fragTypeStr}".`,
            {
              nodes: node,
            },
          ),
        );
      }
    },

    FragmentSpread(node) {
      const fragName = node.name.value;
      const fragType = getFragmentType(context, fragName);
      const parentType = context.getParentType();

      if (
        fragType &&
        parentType &&
        !doTypesOverlap(context.getSchema(), fragType, parentType)
      ) {
        const parentTypeStr = inspect(parentType);
        const fragTypeStr = inspect(fragType);
        context.reportError(
          new GraphQLError(
            `Fragment "${fragName}" cannot be spread here as objects of type "${parentTypeStr}" can never be of type "${fragTypeStr}".`,
            {
              nodes: node,
            },
          ),
        );
      }
    },
  };
}

function getFragmentType(context, name) {
  const frag = context.getFragment(name);

  if (frag) {
    const type = typeFromAST(context.getSchema(), frag.typeCondition);

    if (isCompositeType(type)) {
      return type;
    }
  }
}

/**
 * Possible type extension
 *
 * A type extension is only valid if the type is defined and has the same kind.
 */
function PossibleTypeExtensionsRule(context) {
  const schema = context.getSchema();
  const definedTypes = Object.create(null);

  for (const def of context.getDocument().definitions) {
    if (isTypeDefinitionNode(def)) {
      definedTypes[def.name.value] = def;
    }
  }

  return {
    ScalarTypeExtension: checkExtension,
    ObjectTypeExtension: checkExtension,
    InterfaceTypeExtension: checkExtension,
    UnionTypeExtension: checkExtension,
    EnumTypeExtension: checkExtension,
    InputObjectTypeExtension: checkExtension,
  };

  function checkExtension(node) {
    const typeName = node.name.value;
    const defNode = definedTypes[typeName];
    const existingType =
      schema === null || schema === void 0 ? void 0 : schema.getType(typeName);
    let expectedKind;

    if (defNode) {
      expectedKind = defKindToExtKind[defNode.kind];
    } else if (existingType) {
      expectedKind = typeToExtKind(existingType);
    }

    if (expectedKind) {
      if (expectedKind !== node.kind) {
        const kindStr = extensionKindToTypeName(node.kind);
        context.reportError(
          new GraphQLError(`Cannot extend non-${kindStr} type "${typeName}".`, {
            nodes: defNode ? [defNode, node] : node,
          }),
        );
      }
    } else {
      const allTypeNames = Object.keys({
        ...definedTypes,
        ...(schema === null || schema === void 0
          ? void 0
          : schema.getTypeMap()),
      });
      const suggestedTypes = suggestionList(typeName, allTypeNames);
      context.reportError(
        new GraphQLError(
          `Cannot extend type "${typeName}" because it is not defined.` +
            didYouMean(suggestedTypes),
          {
            nodes: node.name,
          },
        ),
      );
    }
  }
}
const defKindToExtKind = {
  [Kind.SCALAR_TYPE_DEFINITION]: Kind.SCALAR_TYPE_EXTENSION,
  [Kind.OBJECT_TYPE_DEFINITION]: Kind.OBJECT_TYPE_EXTENSION,
  [Kind.INTERFACE_TYPE_DEFINITION]: Kind.INTERFACE_TYPE_EXTENSION,
  [Kind.UNION_TYPE_DEFINITION]: Kind.UNION_TYPE_EXTENSION,
  [Kind.ENUM_TYPE_DEFINITION]: Kind.ENUM_TYPE_EXTENSION,
  [Kind.INPUT_OBJECT_TYPE_DEFINITION]: Kind.INPUT_OBJECT_TYPE_EXTENSION,
};

function typeToExtKind(type) {
  if (isScalarType(type)) {
    return Kind.SCALAR_TYPE_EXTENSION;
  }

  if (isObjectType(type)) {
    return Kind.OBJECT_TYPE_EXTENSION;
  }

  if (isInterfaceType(type)) {
    return Kind.INTERFACE_TYPE_EXTENSION;
  }

  if (isUnionType(type)) {
    return Kind.UNION_TYPE_EXTENSION;
  }

  if (isEnumType(type)) {
    return Kind.ENUM_TYPE_EXTENSION;
  }

  if (isInputObjectType(type)) {
    return Kind.INPUT_OBJECT_TYPE_EXTENSION;
  }
  /* c8 ignore next 3 */
  // Not reachable. All possible types have been considered

  invariant(false, 'Unexpected type: ' + inspect(type));
}

function extensionKindToTypeName(kind) {
  switch (kind) {
    case Kind.SCALAR_TYPE_EXTENSION:
      return 'scalar';

    case Kind.OBJECT_TYPE_EXTENSION:
      return 'object';

    case Kind.INTERFACE_TYPE_EXTENSION:
      return 'interface';

    case Kind.UNION_TYPE_EXTENSION:
      return 'union';

    case Kind.ENUM_TYPE_EXTENSION:
      return 'enum';

    case Kind.INPUT_OBJECT_TYPE_EXTENSION:
      return 'input object';
    // Not reachable. All possible types have been considered

    /* c8 ignore next */

    default:
      invariant(false, 'Unexpected kind: ' + inspect(kind));
  }
}

/**
 * Provided required arguments
 *
 * A field or directive is only valid if all required (non-null without a
 * default value) field arguments have been provided.
 */
function ProvidedRequiredArgumentsRule(context) {
  return {
    // eslint-disable-next-line new-cap
    ...ProvidedRequiredArgumentsOnDirectivesRule(context),
    Field: {
      // Validate on leave to allow for deeper errors to appear first.
      leave(fieldNode) {
        var _fieldNode$arguments;

        const fieldDef = context.getFieldDef();

        if (!fieldDef) {
          return false;
        }

        const providedArgs = new Set( // FIXME: https://github.com/graphql/graphql-js/issues/2203
          /* c8 ignore next */
          (_fieldNode$arguments = fieldNode.arguments) === null ||
          _fieldNode$arguments === void 0
            ? void 0
            : _fieldNode$arguments.map((arg) => arg.name.value),
        );

        for (const argDef of fieldDef.args) {
          if (!providedArgs.has(argDef.name) && isRequiredArgument(argDef)) {
            const argTypeStr = inspect(argDef.type);
            context.reportError(
              new GraphQLError(
                `Field "${fieldDef.name}" argument "${argDef.name}" of type "${argTypeStr}" is required, but it was not provided.`,
                {
                  nodes: fieldNode,
                },
              ),
            );
          }
        }
      },
    },
  };
}
/**
 * @internal
 */

function ProvidedRequiredArgumentsOnDirectivesRule(context) {
  var _schema$getDirectives;

  const requiredArgsMap = Object.create(null);
  const schema = context.getSchema();
  const definedDirectives =
    (_schema$getDirectives =
      schema === null || schema === void 0
        ? void 0
        : schema.getDirectives()) !== null && _schema$getDirectives !== void 0
      ? _schema$getDirectives
      : specifiedDirectives;

  for (const directive of definedDirectives) {
    requiredArgsMap[directive.name] = keyMap(
      directive.args.filter(isRequiredArgument),
      (arg) => arg.name,
    );
  }

  const astDefinitions = context.getDocument().definitions;

  for (const def of astDefinitions) {
    if (def.kind === Kind.DIRECTIVE_DEFINITION) {
      var _def$arguments;

      // FIXME: https://github.com/graphql/graphql-js/issues/2203

      /* c8 ignore next */
      const argNodes =
        (_def$arguments = def.arguments) !== null && _def$arguments !== void 0
          ? _def$arguments
          : [];
      requiredArgsMap[def.name.value] = keyMap(
        argNodes.filter(isRequiredArgumentNode),
        (arg) => arg.name.value,
      );
    }
  }

  return {
    Directive: {
      // Validate on leave to allow for deeper errors to appear first.
      leave(directiveNode) {
        const directiveName = directiveNode.name.value;
        const requiredArgs = requiredArgsMap[directiveName];

        if (requiredArgs) {
          var _directiveNode$argume;

          // FIXME: https://github.com/graphql/graphql-js/issues/2203

          /* c8 ignore next */
          const argNodes =
            (_directiveNode$argume = directiveNode.arguments) !== null &&
            _directiveNode$argume !== void 0
              ? _directiveNode$argume
              : [];
          const argNodeMap = new Set(argNodes.map((arg) => arg.name.value));

          for (const [argName, argDef] of Object.entries(requiredArgs)) {
            if (!argNodeMap.has(argName)) {
              const argType = isType(argDef.type)
                ? inspect(argDef.type)
                : print(argDef.type);
              context.reportError(
                new GraphQLError(
                  `Directive "@${directiveName}" argument "${argName}" of type "${argType}" is required, but it was not provided.`,
                  {
                    nodes: directiveNode,
                  },
                ),
              );
            }
          }
        }
      },
    },
  };
}

function isRequiredArgumentNode(arg) {
  return arg.type.kind === Kind.NON_NULL_TYPE && arg.defaultValue == null;
}

/**
 * Scalar leafs
 *
 * A GraphQL document is valid only if all leaf fields (fields without
 * sub selections) are of scalar or enum types.
 */
function ScalarLeafsRule(context) {
  return {
    Field(node) {
      const type = context.getType();
      const selectionSet = node.selectionSet;

      if (type) {
        if (isLeafType(getNamedType(type))) {
          if (selectionSet) {
            const fieldName = node.name.value;
            const typeStr = inspect(type);
            context.reportError(
              new GraphQLError(
                `Field "${fieldName}" must not have a selection since type "${typeStr}" has no subfields.`,
                {
                  nodes: selectionSet,
                },
              ),
            );
          }
        } else if (!selectionSet) {
          const fieldName = node.name.value;
          const typeStr = inspect(type);
          context.reportError(
            new GraphQLError(
              `Field "${fieldName}" of type "${typeStr}" must have a selection of subfields. Did you mean "${fieldName} { ... }"?`,
              {
                nodes: node,
              },
            ),
          );
        }
      }
    },
  };
}

/**
 * Build a string describing the path.
 */
function printPathArray(path) {
  return path
    .map((key) =>
      typeof key === 'number' ? '[' + key.toString() + ']' : '.' + key,
    )
    .join('');
}

/**
 * Given a Path and a key, return a new Path containing the new key.
 */
function addPath(prev, key, typename) {
  return {
    prev,
    key,
    typename,
  };
}
/**
 * Given a Path, return an Array of the path keys.
 */

function pathToArray(path) {
  const flattened = [];
  let curr = path;

  while (curr) {
    flattened.push(curr.key);
    curr = curr.prev;
  }

  return flattened.reverse();
}

/**
 * Coerces a JavaScript value given a GraphQL Input Type.
 */
function coerceInputValue(inputValue, type, onError = defaultOnError) {
  return coerceInputValueImpl(inputValue, type, onError, undefined);
}

function defaultOnError(path, invalidValue, error) {
  let errorPrefix = 'Invalid value ' + inspect(invalidValue);

  if (path.length > 0) {
    errorPrefix += ` at "value${printPathArray(path)}"`;
  }

  error.message = errorPrefix + ': ' + error.message;
  throw error;
}

function coerceInputValueImpl(inputValue, type, onError, path) {
  if (isNonNullType(type)) {
    if (inputValue != null) {
      return coerceInputValueImpl(inputValue, type.ofType, onError, path);
    }

    onError(
      pathToArray(path),
      inputValue,
      new GraphQLError(
        `Expected non-nullable type "${inspect(type)}" not to be null.`,
      ),
    );
    return;
  }

  if (inputValue == null) {
    // Explicitly return the value null.
    return null;
  }

  if (isListType(type)) {
    const itemType = type.ofType;

    if (isIterableObject(inputValue)) {
      return Array.from(inputValue, (itemValue, index) => {
        const itemPath = addPath(path, index, undefined);
        return coerceInputValueImpl(itemValue, itemType, onError, itemPath);
      });
    } // Lists accept a non-list value as a list of one.

    return [coerceInputValueImpl(inputValue, itemType, onError, path)];
  }

  if (isInputObjectType(type)) {
    if (!isObjectLike$c(inputValue)) {
      onError(
        pathToArray(path),
        inputValue,
        new GraphQLError(`Expected type "${type.name}" to be an object.`),
      );
      return;
    }

    const coercedValue = {};
    const fieldDefs = type.getFields();

    for (const field of Object.values(fieldDefs)) {
      const fieldValue = inputValue[field.name];

      if (fieldValue === undefined) {
        if (field.defaultValue !== undefined) {
          coercedValue[field.name] = field.defaultValue;
        } else if (isNonNullType(field.type)) {
          const typeStr = inspect(field.type);
          onError(
            pathToArray(path),
            inputValue,
            new GraphQLError(
              `Field "${field.name}" of required type "${typeStr}" was not provided.`,
            ),
          );
        }

        continue;
      }

      coercedValue[field.name] = coerceInputValueImpl(
        fieldValue,
        field.type,
        onError,
        addPath(path, field.name, type.name),
      );
    } // Ensure every provided field is defined.

    for (const fieldName of Object.keys(inputValue)) {
      if (!fieldDefs[fieldName]) {
        const suggestions = suggestionList(
          fieldName,
          Object.keys(type.getFields()),
        );
        onError(
          pathToArray(path),
          inputValue,
          new GraphQLError(
            `Field "${fieldName}" is not defined by type "${type.name}".` +
              didYouMean(suggestions),
          ),
        );
      }
    }

    return coercedValue;
  }

  if (isLeafType(type)) {
    let parseResult; // Scalars and Enums determine if a input value is valid via parseValue(),
    // which can throw to indicate failure. If it throws, maintain a reference
    // to the original error.

    try {
      parseResult = type.parseValue(inputValue);
    } catch (error) {
      if (error instanceof GraphQLError) {
        onError(pathToArray(path), inputValue, error);
      } else {
        onError(
          pathToArray(path),
          inputValue,
          new GraphQLError(`Expected type "${type.name}". ` + error.message, {
            originalError: error,
          }),
        );
      }

      return;
    }

    if (parseResult === undefined) {
      onError(
        pathToArray(path),
        inputValue,
        new GraphQLError(`Expected type "${type.name}".`),
      );
    }

    return parseResult;
  }
  /* c8 ignore next 3 */
  // Not reachable, all possible types have been considered.

  invariant(false, 'Unexpected input type: ' + inspect(type));
}

/**
 * Produces a JavaScript value given a GraphQL Value AST.
 *
 * A GraphQL type must be provided, which will be used to interpret different
 * GraphQL Value literals.
 *
 * Returns `undefined` when the value could not be validly coerced according to
 * the provided type.
 *
 * | GraphQL Value        | JSON Value    |
 * | -------------------- | ------------- |
 * | Input Object         | Object        |
 * | List                 | Array         |
 * | Boolean              | Boolean       |
 * | String               | String        |
 * | Int / Float          | Number        |
 * | Enum Value           | Unknown       |
 * | NullValue            | null          |
 *
 */

function valueFromAST(valueNode, type, variables) {
  if (!valueNode) {
    // When there is no node, then there is also no value.
    // Importantly, this is different from returning the value null.
    return;
  }

  if (valueNode.kind === Kind.VARIABLE) {
    const variableName = valueNode.name.value;

    if (variables == null || variables[variableName] === undefined) {
      // No valid return value.
      return;
    }

    const variableValue = variables[variableName];

    if (variableValue === null && isNonNullType(type)) {
      return; // Invalid: intentionally return no value.
    } // Note: This does no further checking that this variable is correct.
    // This assumes that this query has been validated and the variable
    // usage here is of the correct type.

    return variableValue;
  }

  if (isNonNullType(type)) {
    if (valueNode.kind === Kind.NULL) {
      return; // Invalid: intentionally return no value.
    }

    return valueFromAST(valueNode, type.ofType, variables);
  }

  if (valueNode.kind === Kind.NULL) {
    // This is explicitly returning the value null.
    return null;
  }

  if (isListType(type)) {
    const itemType = type.ofType;

    if (valueNode.kind === Kind.LIST) {
      const coercedValues = [];

      for (const itemNode of valueNode.values) {
        if (isMissingVariable(itemNode, variables)) {
          // If an array contains a missing variable, it is either coerced to
          // null or if the item type is non-null, it considered invalid.
          if (isNonNullType(itemType)) {
            return; // Invalid: intentionally return no value.
          }

          coercedValues.push(null);
        } else {
          const itemValue = valueFromAST(itemNode, itemType, variables);

          if (itemValue === undefined) {
            return; // Invalid: intentionally return no value.
          }

          coercedValues.push(itemValue);
        }
      }

      return coercedValues;
    }

    const coercedValue = valueFromAST(valueNode, itemType, variables);

    if (coercedValue === undefined) {
      return; // Invalid: intentionally return no value.
    }

    return [coercedValue];
  }

  if (isInputObjectType(type)) {
    if (valueNode.kind !== Kind.OBJECT) {
      return; // Invalid: intentionally return no value.
    }

    const coercedObj = Object.create(null);
    const fieldNodes = keyMap(valueNode.fields, (field) => field.name.value);

    for (const field of Object.values(type.getFields())) {
      const fieldNode = fieldNodes[field.name];

      if (!fieldNode || isMissingVariable(fieldNode.value, variables)) {
        if (field.defaultValue !== undefined) {
          coercedObj[field.name] = field.defaultValue;
        } else if (isNonNullType(field.type)) {
          return; // Invalid: intentionally return no value.
        }

        continue;
      }

      const fieldValue = valueFromAST(fieldNode.value, field.type, variables);

      if (fieldValue === undefined) {
        return; // Invalid: intentionally return no value.
      }

      coercedObj[field.name] = fieldValue;
    }

    return coercedObj;
  }

  if (isLeafType(type)) {
    // Scalars and Enums fulfill parsing a literal value via parseLiteral().
    // Invalid values represent a failure to parse correctly, in which case
    // no value is returned.
    let result;

    try {
      result = type.parseLiteral(valueNode, variables);
    } catch (_error) {
      return; // Invalid: intentionally return no value.
    }

    if (result === undefined) {
      return; // Invalid: intentionally return no value.
    }

    return result;
  }
  /* c8 ignore next 3 */
  // Not reachable, all possible input types have been considered.

  invariant(false, 'Unexpected input type: ' + inspect(type));
} // Returns true if the provided valueNode is a variable which is not defined
// in the set of variables.

function isMissingVariable(valueNode, variables) {
  return (
    valueNode.kind === Kind.VARIABLE &&
    (variables == null || variables[valueNode.name.value] === undefined)
  );
}

/**
 * Prepares an object map of variableValues of the correct type based on the
 * provided variable definitions and arbitrary input. If the input cannot be
 * parsed to match the variable definitions, a GraphQLError will be thrown.
 *
 * Note: The returned value is a plain Object with a prototype, since it is
 * exposed to user code. Care should be taken to not pull values from the
 * Object prototype.
 */
function getVariableValues(schema, varDefNodes, inputs, options) {
  const errors = [];
  const maxErrors =
    options === null || options === void 0 ? void 0 : options.maxErrors;

  try {
    const coerced = coerceVariableValues(
      schema,
      varDefNodes,
      inputs,
      (error) => {
        if (maxErrors != null && errors.length >= maxErrors) {
          throw new GraphQLError(
            'Too many errors processing variables, error limit reached. Execution aborted.',
          );
        }

        errors.push(error);
      },
    );

    if (errors.length === 0) {
      return {
        coerced,
      };
    }
  } catch (error) {
    errors.push(error);
  }

  return {
    errors,
  };
}

function coerceVariableValues(schema, varDefNodes, inputs, onError) {
  const coercedValues = {};

  for (const varDefNode of varDefNodes) {
    const varName = varDefNode.variable.name.value;
    const varType = typeFromAST(schema, varDefNode.type);

    if (!isInputType(varType)) {
      // Must use input types for variables. This should be caught during
      // validation, however is checked again here for safety.
      const varTypeStr = print(varDefNode.type);
      onError(
        new GraphQLError(
          `Variable "$${varName}" expected value of type "${varTypeStr}" which cannot be used as an input type.`,
          {
            nodes: varDefNode.type,
          },
        ),
      );
      continue;
    }

    if (!hasOwnProperty$f(inputs, varName)) {
      if (varDefNode.defaultValue) {
        coercedValues[varName] = valueFromAST(varDefNode.defaultValue, varType);
      } else if (isNonNullType(varType)) {
        const varTypeStr = inspect(varType);
        onError(
          new GraphQLError(
            `Variable "$${varName}" of required type "${varTypeStr}" was not provided.`,
            {
              nodes: varDefNode,
            },
          ),
        );
      }

      continue;
    }

    const value = inputs[varName];

    if (value === null && isNonNullType(varType)) {
      const varTypeStr = inspect(varType);
      onError(
        new GraphQLError(
          `Variable "$${varName}" of non-null type "${varTypeStr}" must not be null.`,
          {
            nodes: varDefNode,
          },
        ),
      );
      continue;
    }

    coercedValues[varName] = coerceInputValue(
      value,
      varType,
      (path, invalidValue, error) => {
        let prefix =
          `Variable "$${varName}" got invalid value ` + inspect(invalidValue);

        if (path.length > 0) {
          prefix += ` at "${varName}${printPathArray(path)}"`;
        }

        onError(
          new GraphQLError(prefix + '; ' + error.message, {
            nodes: varDefNode,
            originalError: error,
          }),
        );
      },
    );
  }

  return coercedValues;
}
/**
 * Prepares an object map of argument values given a list of argument
 * definitions and list of argument AST nodes.
 *
 * Note: The returned value is a plain Object with a prototype, since it is
 * exposed to user code. Care should be taken to not pull values from the
 * Object prototype.
 */

function getArgumentValues(def, node, variableValues) {
  var _node$arguments;

  const coercedValues = {}; // FIXME: https://github.com/graphql/graphql-js/issues/2203

  /* c8 ignore next */

  const argumentNodes =
    (_node$arguments = node.arguments) !== null && _node$arguments !== void 0
      ? _node$arguments
      : [];
  const argNodeMap = keyMap(argumentNodes, (arg) => arg.name.value);

  for (const argDef of def.args) {
    const name = argDef.name;
    const argType = argDef.type;
    const argumentNode = argNodeMap[name];

    if (!argumentNode) {
      if (argDef.defaultValue !== undefined) {
        coercedValues[name] = argDef.defaultValue;
      } else if (isNonNullType(argType)) {
        throw new GraphQLError(
          `Argument "${name}" of required type "${inspect(argType)}" ` +
            'was not provided.',
          {
            nodes: node,
          },
        );
      }

      continue;
    }

    const valueNode = argumentNode.value;
    let isNull = valueNode.kind === Kind.NULL;

    if (valueNode.kind === Kind.VARIABLE) {
      const variableName = valueNode.name.value;

      if (
        variableValues == null ||
        !hasOwnProperty$f(variableValues, variableName)
      ) {
        if (argDef.defaultValue !== undefined) {
          coercedValues[name] = argDef.defaultValue;
        } else if (isNonNullType(argType)) {
          throw new GraphQLError(
            `Argument "${name}" of required type "${inspect(argType)}" ` +
              `was provided the variable "$${variableName}" which was not provided a runtime value.`,
            {
              nodes: valueNode,
            },
          );
        }

        continue;
      }

      isNull = variableValues[variableName] == null;
    }

    if (isNull && isNonNullType(argType)) {
      throw new GraphQLError(
        `Argument "${name}" of non-null type "${inspect(argType)}" ` +
          'must not be null.',
        {
          nodes: valueNode,
        },
      );
    }

    const coercedValue = valueFromAST(valueNode, argType, variableValues);

    if (coercedValue === undefined) {
      // Note: ValuesOfCorrectTypeRule validation should catch this before
      // execution. This is a runtime check to ensure execution does not
      // continue with an invalid argument value.
      throw new GraphQLError(
        `Argument "${name}" has invalid value ${print(valueNode)}.`,
        {
          nodes: valueNode,
        },
      );
    }

    coercedValues[name] = coercedValue;
  }

  return coercedValues;
}
/**
 * Prepares an object map of argument values given a directive definition
 * and a AST node which may contain directives. Optionally also accepts a map
 * of variable values.
 *
 * If the directive does not exist on the node, returns undefined.
 *
 * Note: The returned value is a plain Object with a prototype, since it is
 * exposed to user code. Care should be taken to not pull values from the
 * Object prototype.
 */

function getDirectiveValues(directiveDef, node, variableValues) {
  var _node$directives;

  const directiveNode =
    (_node$directives = node.directives) === null || _node$directives === void 0
      ? void 0
      : _node$directives.find(
          (directive) => directive.name.value === directiveDef.name,
        );

  if (directiveNode) {
    return getArgumentValues(directiveDef, directiveNode, variableValues);
  }
}

function hasOwnProperty$f(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

/**
 * Given a selectionSet, collects all of the fields and returns them.
 *
 * CollectFields requires the "runtime type" of an object. For a field that
 * returns an Interface or Union type, the "runtime type" will be the actual
 * object type returned by that field.
 *
 * @internal
 */

function collectFields(
  schema,
  fragments,
  variableValues,
  runtimeType,
  selectionSet,
) {
  const fields = new Map();
  collectFieldsImpl(
    schema,
    fragments,
    variableValues,
    runtimeType,
    selectionSet,
    fields,
    new Set(),
  );
  return fields;
}
/**
 * Given an array of field nodes, collects all of the subfields of the passed
 * in fields, and returns them at the end.
 *
 * CollectSubFields requires the "return type" of an object. For a field that
 * returns an Interface or Union type, the "return type" will be the actual
 * object type returned by that field.
 *
 * @internal
 */

function collectSubfields$1(
  schema,
  fragments,
  variableValues,
  returnType,
  fieldNodes,
) {
  const subFieldNodes = new Map();
  const visitedFragmentNames = new Set();

  for (const node of fieldNodes) {
    if (node.selectionSet) {
      collectFieldsImpl(
        schema,
        fragments,
        variableValues,
        returnType,
        node.selectionSet,
        subFieldNodes,
        visitedFragmentNames,
      );
    }
  }

  return subFieldNodes;
}

function collectFieldsImpl(
  schema,
  fragments,
  variableValues,
  runtimeType,
  selectionSet,
  fields,
  visitedFragmentNames,
) {
  for (const selection of selectionSet.selections) {
    switch (selection.kind) {
      case Kind.FIELD: {
        if (!shouldIncludeNode(variableValues, selection)) {
          continue;
        }

        const name = getFieldEntryKey(selection);
        const fieldList = fields.get(name);

        if (fieldList !== undefined) {
          fieldList.push(selection);
        } else {
          fields.set(name, [selection]);
        }

        break;
      }

      case Kind.INLINE_FRAGMENT: {
        if (
          !shouldIncludeNode(variableValues, selection) ||
          !doesFragmentConditionMatch(schema, selection, runtimeType)
        ) {
          continue;
        }

        collectFieldsImpl(
          schema,
          fragments,
          variableValues,
          runtimeType,
          selection.selectionSet,
          fields,
          visitedFragmentNames,
        );
        break;
      }

      case Kind.FRAGMENT_SPREAD: {
        const fragName = selection.name.value;

        if (
          visitedFragmentNames.has(fragName) ||
          !shouldIncludeNode(variableValues, selection)
        ) {
          continue;
        }

        visitedFragmentNames.add(fragName);
        const fragment = fragments[fragName];

        if (
          !fragment ||
          !doesFragmentConditionMatch(schema, fragment, runtimeType)
        ) {
          continue;
        }

        collectFieldsImpl(
          schema,
          fragments,
          variableValues,
          runtimeType,
          fragment.selectionSet,
          fields,
          visitedFragmentNames,
        );
        break;
      }
    }
  }
}
/**
 * Determines if a field should be included based on the `@include` and `@skip`
 * directives, where `@skip` has higher precedence than `@include`.
 */

function shouldIncludeNode(variableValues, node) {
  const skip = getDirectiveValues(GraphQLSkipDirective, node, variableValues);

  if ((skip === null || skip === void 0 ? void 0 : skip.if) === true) {
    return false;
  }

  const include = getDirectiveValues(
    GraphQLIncludeDirective,
    node,
    variableValues,
  );

  if (
    (include === null || include === void 0 ? void 0 : include.if) === false
  ) {
    return false;
  }

  return true;
}
/**
 * Determines if a fragment is applicable to the given type.
 */

function doesFragmentConditionMatch(schema, fragment, type) {
  const typeConditionNode = fragment.typeCondition;

  if (!typeConditionNode) {
    return true;
  }

  const conditionalType = typeFromAST(schema, typeConditionNode);

  if (conditionalType === type) {
    return true;
  }

  if (isAbstractType(conditionalType)) {
    return schema.isSubType(conditionalType, type);
  }

  return false;
}
/**
 * Implements the logic to compute the key of a given field's entry
 */

function getFieldEntryKey(node) {
  return node.alias ? node.alias.value : node.name.value;
}

/**
 * Subscriptions must only include a non-introspection field.
 *
 * A GraphQL subscription is valid only if it contains a single root field and
 * that root field is not an introspection field.
 *
 * See https://spec.graphql.org/draft/#sec-Single-root-field
 */
function SingleFieldSubscriptionsRule(context) {
  return {
    OperationDefinition(node) {
      if (node.operation === 'subscription') {
        const schema = context.getSchema();
        const subscriptionType = schema.getSubscriptionType();

        if (subscriptionType) {
          const operationName = node.name ? node.name.value : null;
          const variableValues = Object.create(null);
          const document = context.getDocument();
          const fragments = Object.create(null);

          for (const definition of document.definitions) {
            if (definition.kind === Kind.FRAGMENT_DEFINITION) {
              fragments[definition.name.value] = definition;
            }
          }

          const fields = collectFields(
            schema,
            fragments,
            variableValues,
            subscriptionType,
            node.selectionSet,
          );

          if (fields.size > 1) {
            const fieldSelectionLists = [...fields.values()];
            const extraFieldSelectionLists = fieldSelectionLists.slice(1);
            const extraFieldSelections = extraFieldSelectionLists.flat();
            context.reportError(
              new GraphQLError(
                operationName != null
                  ? `Subscription "${operationName}" must select only one top level field.`
                  : 'Anonymous Subscription must select only one top level field.',
                {
                  nodes: extraFieldSelections,
                },
              ),
            );
          }

          for (const fieldNodes of fields.values()) {
            const field = fieldNodes[0];
            const fieldName = field.name.value;

            if (fieldName.startsWith('__')) {
              context.reportError(
                new GraphQLError(
                  operationName != null
                    ? `Subscription "${operationName}" must not select an introspection top level field.`
                    : 'Anonymous Subscription must not select an introspection top level field.',
                  {
                    nodes: fieldNodes,
                  },
                ),
              );
            }
          }
        }
      }
    },
  };
}

/**
 * Groups array items into a Map, given a function to produce grouping key.
 */
function groupBy(list, keyFn) {
  const result = new Map();

  for (const item of list) {
    const key = keyFn(item);
    const group = result.get(key);

    if (group === undefined) {
      result.set(key, [item]);
    } else {
      group.push(item);
    }
  }

  return result;
}

/**
 * Unique argument definition names
 *
 * A GraphQL Object or Interface type is only valid if all its fields have uniquely named arguments.
 * A GraphQL Directive is only valid if all its arguments are uniquely named.
 */
function UniqueArgumentDefinitionNamesRule(context) {
  return {
    DirectiveDefinition(directiveNode) {
      var _directiveNode$argume;

      // FIXME: https://github.com/graphql/graphql-js/issues/2203

      /* c8 ignore next */
      const argumentNodes =
        (_directiveNode$argume = directiveNode.arguments) !== null &&
        _directiveNode$argume !== void 0
          ? _directiveNode$argume
          : [];
      return checkArgUniqueness(`@${directiveNode.name.value}`, argumentNodes);
    },

    InterfaceTypeDefinition: checkArgUniquenessPerField,
    InterfaceTypeExtension: checkArgUniquenessPerField,
    ObjectTypeDefinition: checkArgUniquenessPerField,
    ObjectTypeExtension: checkArgUniquenessPerField,
  };

  function checkArgUniquenessPerField(typeNode) {
    var _typeNode$fields;

    const typeName = typeNode.name.value; // FIXME: https://github.com/graphql/graphql-js/issues/2203

    /* c8 ignore next */

    const fieldNodes =
      (_typeNode$fields = typeNode.fields) !== null &&
      _typeNode$fields !== void 0
        ? _typeNode$fields
        : [];

    for (const fieldDef of fieldNodes) {
      var _fieldDef$arguments;

      const fieldName = fieldDef.name.value; // FIXME: https://github.com/graphql/graphql-js/issues/2203

      /* c8 ignore next */

      const argumentNodes =
        (_fieldDef$arguments = fieldDef.arguments) !== null &&
        _fieldDef$arguments !== void 0
          ? _fieldDef$arguments
          : [];
      checkArgUniqueness(`${typeName}.${fieldName}`, argumentNodes);
    }

    return false;
  }

  function checkArgUniqueness(parentName, argumentNodes) {
    const seenArgs = groupBy(argumentNodes, (arg) => arg.name.value);

    for (const [argName, argNodes] of seenArgs) {
      if (argNodes.length > 1) {
        context.reportError(
          new GraphQLError(
            `Argument "${parentName}(${argName}:)" can only be defined once.`,
            {
              nodes: argNodes.map((node) => node.name),
            },
          ),
        );
      }
    }

    return false;
  }
}

/**
 * Unique argument names
 *
 * A GraphQL field or directive is only valid if all supplied arguments are
 * uniquely named.
 *
 * See https://spec.graphql.org/draft/#sec-Argument-Names
 */
function UniqueArgumentNamesRule(context) {
  return {
    Field: checkArgUniqueness,
    Directive: checkArgUniqueness,
  };

  function checkArgUniqueness(parentNode) {
    var _parentNode$arguments;

    // FIXME: https://github.com/graphql/graphql-js/issues/2203

    /* c8 ignore next */
    const argumentNodes =
      (_parentNode$arguments = parentNode.arguments) !== null &&
      _parentNode$arguments !== void 0
        ? _parentNode$arguments
        : [];
    const seenArgs = groupBy(argumentNodes, (arg) => arg.name.value);

    for (const [argName, argNodes] of seenArgs) {
      if (argNodes.length > 1) {
        context.reportError(
          new GraphQLError(
            `There can be only one argument named "${argName}".`,
            {
              nodes: argNodes.map((node) => node.name),
            },
          ),
        );
      }
    }
  }
}

/**
 * Unique directive names
 *
 * A GraphQL document is only valid if all defined directives have unique names.
 */
function UniqueDirectiveNamesRule(context) {
  const knownDirectiveNames = Object.create(null);
  const schema = context.getSchema();
  return {
    DirectiveDefinition(node) {
      const directiveName = node.name.value;

      if (
        schema !== null &&
        schema !== void 0 &&
        schema.getDirective(directiveName)
      ) {
        context.reportError(
          new GraphQLError(
            `Directive "@${directiveName}" already exists in the schema. It cannot be redefined.`,
            {
              nodes: node.name,
            },
          ),
        );
        return;
      }

      if (knownDirectiveNames[directiveName]) {
        context.reportError(
          new GraphQLError(
            `There can be only one directive named "@${directiveName}".`,
            {
              nodes: [knownDirectiveNames[directiveName], node.name],
            },
          ),
        );
      } else {
        knownDirectiveNames[directiveName] = node.name;
      }

      return false;
    },
  };
}

/**
 * Unique directive names per location
 *
 * A GraphQL document is only valid if all non-repeatable directives at
 * a given location are uniquely named.
 *
 * See https://spec.graphql.org/draft/#sec-Directives-Are-Unique-Per-Location
 */
function UniqueDirectivesPerLocationRule(context) {
  const uniqueDirectiveMap = Object.create(null);
  const schema = context.getSchema();
  const definedDirectives = schema
    ? schema.getDirectives()
    : specifiedDirectives;

  for (const directive of definedDirectives) {
    uniqueDirectiveMap[directive.name] = !directive.isRepeatable;
  }

  const astDefinitions = context.getDocument().definitions;

  for (const def of astDefinitions) {
    if (def.kind === Kind.DIRECTIVE_DEFINITION) {
      uniqueDirectiveMap[def.name.value] = !def.repeatable;
    }
  }

  const schemaDirectives = Object.create(null);
  const typeDirectivesMap = Object.create(null);
  return {
    // Many different AST nodes may contain directives. Rather than listing
    // them all, just listen for entering any node, and check to see if it
    // defines any directives.
    enter(node) {
      if (!('directives' in node) || !node.directives) {
        return;
      }

      let seenDirectives;

      if (
        node.kind === Kind.SCHEMA_DEFINITION ||
        node.kind === Kind.SCHEMA_EXTENSION
      ) {
        seenDirectives = schemaDirectives;
      } else if (isTypeDefinitionNode(node) || isTypeExtensionNode(node)) {
        const typeName = node.name.value;
        seenDirectives = typeDirectivesMap[typeName];

        if (seenDirectives === undefined) {
          typeDirectivesMap[typeName] = seenDirectives = Object.create(null);
        }
      } else {
        seenDirectives = Object.create(null);
      }

      for (const directive of node.directives) {
        const directiveName = directive.name.value;

        if (uniqueDirectiveMap[directiveName]) {
          if (seenDirectives[directiveName]) {
            context.reportError(
              new GraphQLError(
                `The directive "@${directiveName}" can only be used once at this location.`,
                {
                  nodes: [seenDirectives[directiveName], directive],
                },
              ),
            );
          } else {
            seenDirectives[directiveName] = directive;
          }
        }
      }
    },
  };
}

/**
 * Unique enum value names
 *
 * A GraphQL enum type is only valid if all its values are uniquely named.
 */
function UniqueEnumValueNamesRule(context) {
  const schema = context.getSchema();
  const existingTypeMap = schema ? schema.getTypeMap() : Object.create(null);
  const knownValueNames = Object.create(null);
  return {
    EnumTypeDefinition: checkValueUniqueness,
    EnumTypeExtension: checkValueUniqueness,
  };

  function checkValueUniqueness(node) {
    var _node$values;

    const typeName = node.name.value;

    if (!knownValueNames[typeName]) {
      knownValueNames[typeName] = Object.create(null);
    } // FIXME: https://github.com/graphql/graphql-js/issues/2203

    /* c8 ignore next */

    const valueNodes =
      (_node$values = node.values) !== null && _node$values !== void 0
        ? _node$values
        : [];
    const valueNames = knownValueNames[typeName];

    for (const valueDef of valueNodes) {
      const valueName = valueDef.name.value;
      const existingType = existingTypeMap[typeName];

      if (isEnumType(existingType) && existingType.getValue(valueName)) {
        context.reportError(
          new GraphQLError(
            `Enum value "${typeName}.${valueName}" already exists in the schema. It cannot also be defined in this type extension.`,
            {
              nodes: valueDef.name,
            },
          ),
        );
      } else if (valueNames[valueName]) {
        context.reportError(
          new GraphQLError(
            `Enum value "${typeName}.${valueName}" can only be defined once.`,
            {
              nodes: [valueNames[valueName], valueDef.name],
            },
          ),
        );
      } else {
        valueNames[valueName] = valueDef.name;
      }
    }

    return false;
  }
}

/**
 * Unique field definition names
 *
 * A GraphQL complex type is only valid if all its fields are uniquely named.
 */
function UniqueFieldDefinitionNamesRule(context) {
  const schema = context.getSchema();
  const existingTypeMap = schema ? schema.getTypeMap() : Object.create(null);
  const knownFieldNames = Object.create(null);
  return {
    InputObjectTypeDefinition: checkFieldUniqueness,
    InputObjectTypeExtension: checkFieldUniqueness,
    InterfaceTypeDefinition: checkFieldUniqueness,
    InterfaceTypeExtension: checkFieldUniqueness,
    ObjectTypeDefinition: checkFieldUniqueness,
    ObjectTypeExtension: checkFieldUniqueness,
  };

  function checkFieldUniqueness(node) {
    var _node$fields;

    const typeName = node.name.value;

    if (!knownFieldNames[typeName]) {
      knownFieldNames[typeName] = Object.create(null);
    } // FIXME: https://github.com/graphql/graphql-js/issues/2203

    /* c8 ignore next */

    const fieldNodes =
      (_node$fields = node.fields) !== null && _node$fields !== void 0
        ? _node$fields
        : [];
    const fieldNames = knownFieldNames[typeName];

    for (const fieldDef of fieldNodes) {
      const fieldName = fieldDef.name.value;

      if (hasField(existingTypeMap[typeName], fieldName)) {
        context.reportError(
          new GraphQLError(
            `Field "${typeName}.${fieldName}" already exists in the schema. It cannot also be defined in this type extension.`,
            {
              nodes: fieldDef.name,
            },
          ),
        );
      } else if (fieldNames[fieldName]) {
        context.reportError(
          new GraphQLError(
            `Field "${typeName}.${fieldName}" can only be defined once.`,
            {
              nodes: [fieldNames[fieldName], fieldDef.name],
            },
          ),
        );
      } else {
        fieldNames[fieldName] = fieldDef.name;
      }
    }

    return false;
  }
}

function hasField(type, fieldName) {
  if (isObjectType(type) || isInterfaceType(type) || isInputObjectType(type)) {
    return type.getFields()[fieldName] != null;
  }

  return false;
}

/**
 * Unique fragment names
 *
 * A GraphQL document is only valid if all defined fragments have unique names.
 *
 * See https://spec.graphql.org/draft/#sec-Fragment-Name-Uniqueness
 */
function UniqueFragmentNamesRule(context) {
  const knownFragmentNames = Object.create(null);
  return {
    OperationDefinition: () => false,

    FragmentDefinition(node) {
      const fragmentName = node.name.value;

      if (knownFragmentNames[fragmentName]) {
        context.reportError(
          new GraphQLError(
            `There can be only one fragment named "${fragmentName}".`,
            {
              nodes: [knownFragmentNames[fragmentName], node.name],
            },
          ),
        );
      } else {
        knownFragmentNames[fragmentName] = node.name;
      }

      return false;
    },
  };
}

/**
 * Unique input field names
 *
 * A GraphQL input object value is only valid if all supplied fields are
 * uniquely named.
 *
 * See https://spec.graphql.org/draft/#sec-Input-Object-Field-Uniqueness
 */
function UniqueInputFieldNamesRule(context) {
  const knownNameStack = [];
  let knownNames = Object.create(null);
  return {
    ObjectValue: {
      enter() {
        knownNameStack.push(knownNames);
        knownNames = Object.create(null);
      },

      leave() {
        const prevKnownNames = knownNameStack.pop();
        prevKnownNames || invariant(false);
        knownNames = prevKnownNames;
      },
    },

    ObjectField(node) {
      const fieldName = node.name.value;

      if (knownNames[fieldName]) {
        context.reportError(
          new GraphQLError(
            `There can be only one input field named "${fieldName}".`,
            {
              nodes: [knownNames[fieldName], node.name],
            },
          ),
        );
      } else {
        knownNames[fieldName] = node.name;
      }
    },
  };
}

/**
 * Unique operation names
 *
 * A GraphQL document is only valid if all defined operations have unique names.
 *
 * See https://spec.graphql.org/draft/#sec-Operation-Name-Uniqueness
 */
function UniqueOperationNamesRule(context) {
  const knownOperationNames = Object.create(null);
  return {
    OperationDefinition(node) {
      const operationName = node.name;

      if (operationName) {
        if (knownOperationNames[operationName.value]) {
          context.reportError(
            new GraphQLError(
              `There can be only one operation named "${operationName.value}".`,
              {
                nodes: [
                  knownOperationNames[operationName.value],
                  operationName,
                ],
              },
            ),
          );
        } else {
          knownOperationNames[operationName.value] = operationName;
        }
      }

      return false;
    },

    FragmentDefinition: () => false,
  };
}

/**
 * Unique operation types
 *
 * A GraphQL document is only valid if it has only one type per operation.
 */
function UniqueOperationTypesRule(context) {
  const schema = context.getSchema();
  const definedOperationTypes = Object.create(null);
  const existingOperationTypes = schema
    ? {
        query: schema.getQueryType(),
        mutation: schema.getMutationType(),
        subscription: schema.getSubscriptionType(),
      }
    : {};
  return {
    SchemaDefinition: checkOperationTypes,
    SchemaExtension: checkOperationTypes,
  };

  function checkOperationTypes(node) {
    var _node$operationTypes;

    // See: https://github.com/graphql/graphql-js/issues/2203

    /* c8 ignore next */
    const operationTypesNodes =
      (_node$operationTypes = node.operationTypes) !== null &&
      _node$operationTypes !== void 0
        ? _node$operationTypes
        : [];

    for (const operationType of operationTypesNodes) {
      const operation = operationType.operation;
      const alreadyDefinedOperationType = definedOperationTypes[operation];

      if (existingOperationTypes[operation]) {
        context.reportError(
          new GraphQLError(
            `Type for ${operation} already defined in the schema. It cannot be redefined.`,
            {
              nodes: operationType,
            },
          ),
        );
      } else if (alreadyDefinedOperationType) {
        context.reportError(
          new GraphQLError(
            `There can be only one ${operation} type in schema.`,
            {
              nodes: [alreadyDefinedOperationType, operationType],
            },
          ),
        );
      } else {
        definedOperationTypes[operation] = operationType;
      }
    }

    return false;
  }
}

/**
 * Unique type names
 *
 * A GraphQL document is only valid if all defined types have unique names.
 */
function UniqueTypeNamesRule(context) {
  const knownTypeNames = Object.create(null);
  const schema = context.getSchema();
  return {
    ScalarTypeDefinition: checkTypeName,
    ObjectTypeDefinition: checkTypeName,
    InterfaceTypeDefinition: checkTypeName,
    UnionTypeDefinition: checkTypeName,
    EnumTypeDefinition: checkTypeName,
    InputObjectTypeDefinition: checkTypeName,
  };

  function checkTypeName(node) {
    const typeName = node.name.value;

    if (schema !== null && schema !== void 0 && schema.getType(typeName)) {
      context.reportError(
        new GraphQLError(
          `Type "${typeName}" already exists in the schema. It cannot also be defined in this type definition.`,
          {
            nodes: node.name,
          },
        ),
      );
      return;
    }

    if (knownTypeNames[typeName]) {
      context.reportError(
        new GraphQLError(`There can be only one type named "${typeName}".`, {
          nodes: [knownTypeNames[typeName], node.name],
        }),
      );
    } else {
      knownTypeNames[typeName] = node.name;
    }

    return false;
  }
}

/**
 * Unique variable names
 *
 * A GraphQL operation is only valid if all its variables are uniquely named.
 */
function UniqueVariableNamesRule(context) {
  return {
    OperationDefinition(operationNode) {
      var _operationNode$variab;

      // See: https://github.com/graphql/graphql-js/issues/2203

      /* c8 ignore next */
      const variableDefinitions =
        (_operationNode$variab = operationNode.variableDefinitions) !== null &&
        _operationNode$variab !== void 0
          ? _operationNode$variab
          : [];
      const seenVariableDefinitions = groupBy(
        variableDefinitions,
        (node) => node.variable.name.value,
      );

      for (const [variableName, variableNodes] of seenVariableDefinitions) {
        if (variableNodes.length > 1) {
          context.reportError(
            new GraphQLError(
              `There can be only one variable named "$${variableName}".`,
              {
                nodes: variableNodes.map((node) => node.variable.name),
              },
            ),
          );
        }
      }
    },
  };
}

/**
 * Value literals of correct type
 *
 * A GraphQL document is only valid if all value literals are of the type
 * expected at their position.
 *
 * See https://spec.graphql.org/draft/#sec-Values-of-Correct-Type
 */
function ValuesOfCorrectTypeRule(context) {
  return {
    ListValue(node) {
      // Note: TypeInfo will traverse into a list's item type, so look to the
      // parent input type to check if it is a list.
      const type = getNullableType(context.getParentInputType());

      if (!isListType(type)) {
        isValidValueNode(context, node);
        return false; // Don't traverse further.
      }
    },

    ObjectValue(node) {
      const type = getNamedType(context.getInputType());

      if (!isInputObjectType(type)) {
        isValidValueNode(context, node);
        return false; // Don't traverse further.
      } // Ensure every required field exists.

      const fieldNodeMap = keyMap(node.fields, (field) => field.name.value);

      for (const fieldDef of Object.values(type.getFields())) {
        const fieldNode = fieldNodeMap[fieldDef.name];

        if (!fieldNode && isRequiredInputField(fieldDef)) {
          const typeStr = inspect(fieldDef.type);
          context.reportError(
            new GraphQLError(
              `Field "${type.name}.${fieldDef.name}" of required type "${typeStr}" was not provided.`,
              {
                nodes: node,
              },
            ),
          );
        }
      }
    },

    ObjectField(node) {
      const parentType = getNamedType(context.getParentInputType());
      const fieldType = context.getInputType();

      if (!fieldType && isInputObjectType(parentType)) {
        const suggestions = suggestionList(
          node.name.value,
          Object.keys(parentType.getFields()),
        );
        context.reportError(
          new GraphQLError(
            `Field "${node.name.value}" is not defined by type "${parentType.name}".` +
              didYouMean(suggestions),
            {
              nodes: node,
            },
          ),
        );
      }
    },

    NullValue(node) {
      const type = context.getInputType();

      if (isNonNullType(type)) {
        context.reportError(
          new GraphQLError(
            `Expected value of type "${inspect(type)}", found ${print(node)}.`,
            {
              nodes: node,
            },
          ),
        );
      }
    },

    EnumValue: (node) => isValidValueNode(context, node),
    IntValue: (node) => isValidValueNode(context, node),
    FloatValue: (node) => isValidValueNode(context, node),
    StringValue: (node) => isValidValueNode(context, node),
    BooleanValue: (node) => isValidValueNode(context, node),
  };
}
/**
 * Any value literal may be a valid representation of a Scalar, depending on
 * that scalar type.
 */

function isValidValueNode(context, node) {
  // Report any error at the full type expected by the location.
  const locationType = context.getInputType();

  if (!locationType) {
    return;
  }

  const type = getNamedType(locationType);

  if (!isLeafType(type)) {
    const typeStr = inspect(locationType);
    context.reportError(
      new GraphQLError(
        `Expected value of type "${typeStr}", found ${print(node)}.`,
        {
          nodes: node,
        },
      ),
    );
    return;
  } // Scalars and Enums determine if a literal value is valid via parseLiteral(),
  // which may throw or return an invalid value to indicate failure.

  try {
    const parseResult = type.parseLiteral(
      node,
      undefined,
      /* variables */
    );

    if (parseResult === undefined) {
      const typeStr = inspect(locationType);
      context.reportError(
        new GraphQLError(
          `Expected value of type "${typeStr}", found ${print(node)}.`,
          {
            nodes: node,
          },
        ),
      );
    }
  } catch (error) {
    const typeStr = inspect(locationType);

    if (error instanceof GraphQLError) {
      context.reportError(error);
    } else {
      context.reportError(
        new GraphQLError(
          `Expected value of type "${typeStr}", found ${print(node)}; ` +
            error.message,
          {
            nodes: node,
            originalError: error,
          },
        ),
      );
    }
  }
}

/**
 * Variables are input types
 *
 * A GraphQL operation is only valid if all the variables it defines are of
 * input types (scalar, enum, or input object).
 *
 * See https://spec.graphql.org/draft/#sec-Variables-Are-Input-Types
 */
function VariablesAreInputTypesRule(context) {
  return {
    VariableDefinition(node) {
      const type = typeFromAST(context.getSchema(), node.type);

      if (type !== undefined && !isInputType(type)) {
        const variableName = node.variable.name.value;
        const typeName = print(node.type);
        context.reportError(
          new GraphQLError(
            `Variable "$${variableName}" cannot be non-input type "${typeName}".`,
            {
              nodes: node.type,
            },
          ),
        );
      }
    },
  };
}

/**
 * Variables in allowed position
 *
 * Variable usages must be compatible with the arguments they are passed to.
 *
 * See https://spec.graphql.org/draft/#sec-All-Variable-Usages-are-Allowed
 */
function VariablesInAllowedPositionRule(context) {
  let varDefMap = Object.create(null);
  return {
    OperationDefinition: {
      enter() {
        varDefMap = Object.create(null);
      },

      leave(operation) {
        const usages = context.getRecursiveVariableUsages(operation);

        for (const { node, type, defaultValue } of usages) {
          const varName = node.name.value;
          const varDef = varDefMap[varName];

          if (varDef && type) {
            // A var type is allowed if it is the same or more strict (e.g. is
            // a subtype of) than the expected type. It can be more strict if
            // the variable type is non-null when the expected type is nullable.
            // If both are list types, the variable item type can be more strict
            // than the expected item type (contravariant).
            const schema = context.getSchema();
            const varType = typeFromAST(schema, varDef.type);

            if (
              varType &&
              !allowedVariableUsage(
                schema,
                varType,
                varDef.defaultValue,
                type,
                defaultValue,
              )
            ) {
              const varTypeStr = inspect(varType);
              const typeStr = inspect(type);
              context.reportError(
                new GraphQLError(
                  `Variable "$${varName}" of type "${varTypeStr}" used in position expecting type "${typeStr}".`,
                  {
                    nodes: [varDef, node],
                  },
                ),
              );
            }
          }
        }
      },
    },

    VariableDefinition(node) {
      varDefMap[node.variable.name.value] = node;
    },
  };
}
/**
 * Returns true if the variable is allowed in the location it was found,
 * which includes considering if default values exist for either the variable
 * or the location at which it is located.
 */

function allowedVariableUsage(
  schema,
  varType,
  varDefaultValue,
  locationType,
  locationDefaultValue,
) {
  if (isNonNullType(locationType) && !isNonNullType(varType)) {
    const hasNonNullVariableDefaultValue =
      varDefaultValue != null && varDefaultValue.kind !== Kind.NULL;
    const hasLocationDefaultValue = locationDefaultValue !== undefined;

    if (!hasNonNullVariableDefaultValue && !hasLocationDefaultValue) {
      return false;
    }

    const nullableLocationType = locationType.ofType;
    return isTypeSubTypeOf(schema, varType, nullableLocationType);
  }

  return isTypeSubTypeOf(schema, varType, locationType);
}

// Spec Section: "Executable Definitions"

/**
 * This set includes all validation rules defined by the GraphQL spec.
 *
 * The order of the rules in this list has been adjusted to lead to the
 * most clear output when encountering multiple validation errors.
 */
const specifiedRules = Object.freeze([
  ExecutableDefinitionsRule,
  UniqueOperationNamesRule,
  LoneAnonymousOperationRule,
  SingleFieldSubscriptionsRule,
  KnownTypeNamesRule,
  FragmentsOnCompositeTypesRule,
  VariablesAreInputTypesRule,
  ScalarLeafsRule,
  FieldsOnCorrectTypeRule,
  UniqueFragmentNamesRule,
  KnownFragmentNamesRule,
  NoUnusedFragmentsRule,
  PossibleFragmentSpreadsRule,
  NoFragmentCyclesRule,
  UniqueVariableNamesRule,
  NoUndefinedVariablesRule,
  NoUnusedVariablesRule,
  KnownDirectivesRule,
  UniqueDirectivesPerLocationRule,
  KnownArgumentNamesRule,
  UniqueArgumentNamesRule,
  ValuesOfCorrectTypeRule,
  ProvidedRequiredArgumentsRule,
  VariablesInAllowedPositionRule,
  OverlappingFieldsCanBeMergedRule,
  UniqueInputFieldNamesRule,
]);
/**
 * @internal
 */

const specifiedSDLRules = Object.freeze([
  LoneSchemaDefinitionRule,
  UniqueOperationTypesRule,
  UniqueTypeNamesRule,
  UniqueEnumValueNamesRule,
  UniqueFieldDefinitionNamesRule,
  UniqueArgumentDefinitionNamesRule,
  UniqueDirectiveNamesRule,
  KnownTypeNamesRule,
  KnownDirectivesRule,
  UniqueDirectivesPerLocationRule,
  PossibleTypeExtensionsRule,
  KnownArgumentNamesOnDirectivesRule,
  UniqueArgumentNamesRule,
  UniqueInputFieldNamesRule,
  ProvidedRequiredArgumentsOnDirectivesRule,
]);

/**
 * An instance of this class is passed as the "this" context to all validators,
 * allowing access to commonly useful contextual information from within a
 * validation rule.
 */
class ASTValidationContext {
  constructor(ast, onError) {
    this._ast = ast;
    this._fragments = undefined;
    this._fragmentSpreads = new Map();
    this._recursivelyReferencedFragments = new Map();
    this._onError = onError;
  }

  get [Symbol.toStringTag]() {
    return 'ASTValidationContext';
  }

  reportError(error) {
    this._onError(error);
  }

  getDocument() {
    return this._ast;
  }

  getFragment(name) {
    let fragments;

    if (this._fragments) {
      fragments = this._fragments;
    } else {
      fragments = Object.create(null);

      for (const defNode of this.getDocument().definitions) {
        if (defNode.kind === Kind.FRAGMENT_DEFINITION) {
          fragments[defNode.name.value] = defNode;
        }
      }

      this._fragments = fragments;
    }

    return fragments[name];
  }

  getFragmentSpreads(node) {
    let spreads = this._fragmentSpreads.get(node);

    if (!spreads) {
      spreads = [];
      const setsToVisit = [node];
      let set;

      while ((set = setsToVisit.pop())) {
        for (const selection of set.selections) {
          if (selection.kind === Kind.FRAGMENT_SPREAD) {
            spreads.push(selection);
          } else if (selection.selectionSet) {
            setsToVisit.push(selection.selectionSet);
          }
        }
      }

      this._fragmentSpreads.set(node, spreads);
    }

    return spreads;
  }

  getRecursivelyReferencedFragments(operation) {
    let fragments = this._recursivelyReferencedFragments.get(operation);

    if (!fragments) {
      fragments = [];
      const collectedNames = Object.create(null);
      const nodesToVisit = [operation.selectionSet];
      let node;

      while ((node = nodesToVisit.pop())) {
        for (const spread of this.getFragmentSpreads(node)) {
          const fragName = spread.name.value;

          if (collectedNames[fragName] !== true) {
            collectedNames[fragName] = true;
            const fragment = this.getFragment(fragName);

            if (fragment) {
              fragments.push(fragment);
              nodesToVisit.push(fragment.selectionSet);
            }
          }
        }
      }

      this._recursivelyReferencedFragments.set(operation, fragments);
    }

    return fragments;
  }
}
class SDLValidationContext extends ASTValidationContext {
  constructor(ast, schema, onError) {
    super(ast, onError);
    this._schema = schema;
  }

  get [Symbol.toStringTag]() {
    return 'SDLValidationContext';
  }

  getSchema() {
    return this._schema;
  }
}
class ValidationContext extends ASTValidationContext {
  constructor(schema, ast, typeInfo, onError) {
    super(ast, onError);
    this._schema = schema;
    this._typeInfo = typeInfo;
    this._variableUsages = new Map();
    this._recursiveVariableUsages = new Map();
  }

  get [Symbol.toStringTag]() {
    return 'ValidationContext';
  }

  getSchema() {
    return this._schema;
  }

  getVariableUsages(node) {
    let usages = this._variableUsages.get(node);

    if (!usages) {
      const newUsages = [];
      const typeInfo = new TypeInfo(this._schema);
      visit(
        node,
        visitWithTypeInfo(typeInfo, {
          VariableDefinition: () => false,

          Variable(variable) {
            newUsages.push({
              node: variable,
              type: typeInfo.getInputType(),
              defaultValue: typeInfo.getDefaultValue(),
            });
          },
        }),
      );
      usages = newUsages;

      this._variableUsages.set(node, usages);
    }

    return usages;
  }

  getRecursiveVariableUsages(operation) {
    let usages = this._recursiveVariableUsages.get(operation);

    if (!usages) {
      usages = this.getVariableUsages(operation);

      for (const frag of this.getRecursivelyReferencedFragments(operation)) {
        usages = usages.concat(this.getVariableUsages(frag));
      }

      this._recursiveVariableUsages.set(operation, usages);
    }

    return usages;
  }

  getType() {
    return this._typeInfo.getType();
  }

  getParentType() {
    return this._typeInfo.getParentType();
  }

  getInputType() {
    return this._typeInfo.getInputType();
  }

  getParentInputType() {
    return this._typeInfo.getParentInputType();
  }

  getFieldDef() {
    return this._typeInfo.getFieldDef();
  }

  getDirective() {
    return this._typeInfo.getDirective();
  }

  getArgument() {
    return this._typeInfo.getArgument();
  }

  getEnumValue() {
    return this._typeInfo.getEnumValue();
  }
}

/**
 * Implements the "Validation" section of the spec.
 *
 * Validation runs synchronously, returning an array of encountered errors, or
 * an empty array if no errors were encountered and the document is valid.
 *
 * A list of specific validation rules may be provided. If not provided, the
 * default list of rules defined by the GraphQL specification will be used.
 *
 * Each validation rules is a function which returns a visitor
 * (see the language/visitor API). Visitor methods are expected to return
 * GraphQLErrors, or Arrays of GraphQLErrors when invalid.
 *
 * Validate will stop validation after a `maxErrors` limit has been reached.
 * Attackers can send pathologically invalid queries to induce a DoS attack,
 * so by default `maxErrors` set to 100 errors.
 *
 * Optionally a custom TypeInfo instance may be provided. If not provided, one
 * will be created from the provided schema.
 */

function validate$1(
  schema,
  documentAST,
  rules = specifiedRules,
  options,
  /** @deprecated will be removed in 17.0.0 */
  typeInfo = new TypeInfo(schema),
) {
  var _options$maxErrors;

  const maxErrors =
    (_options$maxErrors =
      options === null || options === void 0 ? void 0 : options.maxErrors) !==
      null && _options$maxErrors !== void 0
      ? _options$maxErrors
      : 100;
  documentAST || devAssert(false, 'Must provide document.'); // If the schema used for validation is invalid, throw an error.

  assertValidSchema(schema);
  const abortObj = Object.freeze({});
  const errors = [];
  const context = new ValidationContext(
    schema,
    documentAST,
    typeInfo,
    (error) => {
      if (errors.length >= maxErrors) {
        errors.push(
          new GraphQLError(
            'Too many validation errors, error limit reached. Validation aborted.',
          ),
        ); // eslint-disable-next-line @typescript-eslint/no-throw-literal

        throw abortObj;
      }

      errors.push(error);
    },
  ); // This uses a specialized visitor which runs multiple visitors in parallel,
  // while maintaining the visitor skip and break API.

  const visitor = visitInParallel(rules.map((rule) => rule(context))); // Visit the whole document with each instance of all provided rules.

  try {
    visit(documentAST, visitWithTypeInfo(typeInfo, visitor));
  } catch (e) {
    if (e !== abortObj) {
      throw e;
    }
  }

  return errors;
}
/**
 * @internal
 */

function validateSDL(
  documentAST,
  schemaToExtend,
  rules = specifiedSDLRules,
) {
  const errors = [];
  const context = new SDLValidationContext(
    documentAST,
    schemaToExtend,
    (error) => {
      errors.push(error);
    },
  );
  const visitors = rules.map((rule) => rule(context));
  visit(documentAST, visitInParallel(visitors));
  return errors;
}
/**
 * Utility function which asserts a SDL document is valid by throwing an error
 * if it is invalid.
 *
 * @internal
 */

function assertValidSDL(documentAST) {
  const errors = validateSDL(documentAST);

  if (errors.length !== 0) {
    throw new Error(errors.map((error) => error.message).join('\n\n'));
  }
}
/**
 * Utility function which asserts a SDL document is valid by throwing an error
 * if it is invalid.
 *
 * @internal
 */

function assertValidSDLExtension(documentAST, schema) {
  const errors = validateSDL(documentAST, schema);

  if (errors.length !== 0) {
    throw new Error(errors.map((error) => error.message).join('\n\n'));
  }
}

/**
 * Memoizes the provided three-argument function.
 */
function memoize3(fn) {
  let cache0;
  return function memoized(a1, a2, a3) {
    if (cache0 === undefined) {
      cache0 = new WeakMap();
    }

    let cache1 = cache0.get(a1);

    if (cache1 === undefined) {
      cache1 = new WeakMap();
      cache0.set(a1, cache1);
    }

    let cache2 = cache1.get(a2);

    if (cache2 === undefined) {
      cache2 = new WeakMap();
      cache1.set(a2, cache2);
    }

    let fnResult = cache2.get(a3);

    if (fnResult === undefined) {
      fnResult = fn(a1, a2, a3);
      cache2.set(a3, fnResult);
    }

    return fnResult;
  };
}

/**
 * This function transforms a JS object `ObjMap<Promise<T>>` into
 * a `Promise<ObjMap<T>>`
 *
 * This is akin to bluebird's `Promise.props`, but implemented only using
 * `Promise.all` so it will work with any implementation of ES6 promises.
 */
function promiseForObject(object) {
  return Promise.all(Object.values(object)).then((resolvedValues) => {
    const resolvedObject = Object.create(null);

    for (const [i, key] of Object.keys(object).entries()) {
      resolvedObject[key] = resolvedValues[i];
    }

    return resolvedObject;
  });
}

/**
 * Similar to Array.prototype.reduce(), however the reducing callback may return
 * a Promise, in which case reduction will continue after each promise resolves.
 *
 * If the callback does not return a Promise, then this function will also not
 * return a Promise.
 */
function promiseReduce(values, callbackFn, initialValue) {
  let accumulator = initialValue;

  for (const value of values) {
    accumulator = isPromise(accumulator)
      ? accumulator.then((resolved) => callbackFn(resolved, value))
      : callbackFn(accumulator, value);
  }

  return accumulator;
}

/**
 * Sometimes a non-error is thrown, wrap it as an Error instance to ensure a consistent Error interface.
 */

function toError(thrownValue) {
  return thrownValue instanceof Error
    ? thrownValue
    : new NonErrorThrown(thrownValue);
}

class NonErrorThrown extends Error {
  constructor(thrownValue) {
    super('Unexpected error value: ' + inspect(thrownValue));
    this.name = 'NonErrorThrown';
    this.thrownValue = thrownValue;
  }
}

/**
 * Given an arbitrary value, presumably thrown while attempting to execute a
 * GraphQL operation, produce a new GraphQLError aware of the location in the
 * document responsible for the original Error.
 */

function locatedError(rawOriginalError, nodes, path) {
  var _nodes;

  const originalError = toError(rawOriginalError); // Note: this uses a brand-check to support GraphQL errors originating from other contexts.

  if (isLocatedGraphQLError(originalError)) {
    return originalError;
  }

  return new GraphQLError(originalError.message, {
    nodes:
      (_nodes = originalError.nodes) !== null && _nodes !== void 0
        ? _nodes
        : nodes,
    source: originalError.source,
    positions: originalError.positions,
    path,
    originalError,
  });
}

function isLocatedGraphQLError(error) {
  return Array.isArray(error.path);
}

/**
 * A memoized collection of relevant subfields with regard to the return
 * type. Memoizing ensures the subfields are not repeatedly calculated, which
 * saves overhead when resolving lists of values.
 */

const collectSubfields = memoize3((exeContext, returnType, fieldNodes) =>
  collectSubfields$1(
    exeContext.schema,
    exeContext.fragments,
    exeContext.variableValues,
    returnType,
    fieldNodes,
  ),
);
/**
 * Terminology
 *
 * "Definitions" are the generic name for top-level statements in the document.
 * Examples of this include:
 * 1) Operations (such as a query)
 * 2) Fragments
 *
 * "Operations" are a generic name for requests in the document.
 * Examples of this include:
 * 1) query,
 * 2) mutation
 *
 * "Selections" are the definitions that can appear legally and at
 * single level of the query. These include:
 * 1) field references e.g `a`
 * 2) fragment "spreads" e.g. `...c`
 * 3) inline fragment "spreads" e.g. `...on Type { a }`
 */

/**
 * Data that must be available at all points during query execution.
 *
 * Namely, schema of the type system that is currently executing,
 * and the fragments defined in the query document
 */

/**
 * Implements the "Executing requests" section of the GraphQL specification.
 *
 * Returns either a synchronous ExecutionResult (if all encountered resolvers
 * are synchronous), or a Promise of an ExecutionResult that will eventually be
 * resolved and never rejected.
 *
 * If the arguments to this function do not result in a legal execution context,
 * a GraphQLError will be thrown immediately explaining the invalid input.
 */
function execute(args) {
  // Temporary for v15 to v16 migration. Remove in v17
  arguments.length < 2 ||
    devAssert(
      false,
      'graphql@16 dropped long-deprecated support for positional arguments, please pass an object instead.',
    );
  const { schema, document, variableValues, rootValue } = args; // If arguments are missing or incorrect, throw an error.

  assertValidExecutionArguments(schema, document, variableValues); // If a valid execution context cannot be created due to incorrect arguments,
  // a "Response" with only errors is returned.

  const exeContext = buildExecutionContext(args); // Return early errors if execution context failed.

  if (!('schema' in exeContext)) {
    return {
      errors: exeContext,
    };
  } // Return a Promise that will eventually resolve to the data described by
  // The "Response" section of the GraphQL specification.
  //
  // If errors are encountered while executing a GraphQL field, only that
  // field and its descendants will be omitted, and sibling fields will still
  // be executed. An execution which encounters errors will still result in a
  // resolved Promise.
  //
  // Errors from sub-fields of a NonNull type may propagate to the top level,
  // at which point we still log the error and null the parent field, which
  // in this case is the entire response.

  try {
    const { operation } = exeContext;
    const result = executeOperation(exeContext, operation, rootValue);

    if (isPromise(result)) {
      return result.then(
        (data) => buildResponse(data, exeContext.errors),
        (error) => {
          exeContext.errors.push(error);
          return buildResponse(null, exeContext.errors);
        },
      );
    }

    return buildResponse(result, exeContext.errors);
  } catch (error) {
    exeContext.errors.push(error);
    return buildResponse(null, exeContext.errors);
  }
}
/**
 * Also implements the "Executing requests" section of the GraphQL specification.
 * However, it guarantees to complete synchronously (or throw an error) assuming
 * that all field resolvers are also synchronous.
 */

function executeSync(args) {
  const result = execute(args); // Assert that the execution was synchronous.

  if (isPromise(result)) {
    throw new Error('GraphQL execution failed to complete synchronously.');
  }

  return result;
}
/**
 * Given a completed execution context and data, build the `{ errors, data }`
 * response defined by the "Response" section of the GraphQL specification.
 */

function buildResponse(data, errors) {
  return errors.length === 0
    ? {
        data,
      }
    : {
        errors,
        data,
      };
}
/**
 * Essential assertions before executing to provide developer feedback for
 * improper use of the GraphQL library.
 *
 * @internal
 */

function assertValidExecutionArguments(
  schema,
  document,
  rawVariableValues,
) {
  document || devAssert(false, 'Must provide document.'); // If the schema used for execution is invalid, throw an error.

  assertValidSchema(schema); // Variables, if provided, must be an object.

  rawVariableValues == null ||
    isObjectLike$c(rawVariableValues) ||
    devAssert(
      false,
      'Variables must be provided as an Object where each property is a variable value. Perhaps look to see if an unparsed JSON string was provided.',
    );
}
/**
 * Constructs a ExecutionContext object from the arguments passed to
 * execute, which we will pass throughout the other execution methods.
 *
 * Throws a GraphQLError if a valid execution context cannot be created.
 *
 * @internal
 */

function buildExecutionContext(args) {
  var _definition$name, _operation$variableDe;

  const {
    schema,
    document,
    rootValue,
    contextValue,
    variableValues: rawVariableValues,
    operationName,
    fieldResolver,
    typeResolver,
    subscribeFieldResolver,
  } = args;
  let operation;
  const fragments = Object.create(null);

  for (const definition of document.definitions) {
    switch (definition.kind) {
      case Kind.OPERATION_DEFINITION:
        if (operationName == null) {
          if (operation !== undefined) {
            return [
              new GraphQLError(
                'Must provide operation name if query contains multiple operations.',
              ),
            ];
          }

          operation = definition;
        } else if (
          ((_definition$name = definition.name) === null ||
          _definition$name === void 0
            ? void 0
            : _definition$name.value) === operationName
        ) {
          operation = definition;
        }

        break;

      case Kind.FRAGMENT_DEFINITION:
        fragments[definition.name.value] = definition;
        break;
    }
  }

  if (!operation) {
    if (operationName != null) {
      return [new GraphQLError(`Unknown operation named "${operationName}".`)];
    }

    return [new GraphQLError('Must provide an operation.')];
  } // FIXME: https://github.com/graphql/graphql-js/issues/2203

  /* c8 ignore next */

  const variableDefinitions =
    (_operation$variableDe = operation.variableDefinitions) !== null &&
    _operation$variableDe !== void 0
      ? _operation$variableDe
      : [];
  const coercedVariableValues = getVariableValues(
    schema,
    variableDefinitions,
    rawVariableValues !== null && rawVariableValues !== void 0
      ? rawVariableValues
      : {},
    {
      maxErrors: 50,
    },
  );

  if (coercedVariableValues.errors) {
    return coercedVariableValues.errors;
  }

  return {
    schema,
    fragments,
    rootValue,
    contextValue,
    operation,
    variableValues: coercedVariableValues.coerced,
    fieldResolver:
      fieldResolver !== null && fieldResolver !== void 0
        ? fieldResolver
        : defaultFieldResolver,
    typeResolver:
      typeResolver !== null && typeResolver !== void 0
        ? typeResolver
        : defaultTypeResolver,
    subscribeFieldResolver:
      subscribeFieldResolver !== null && subscribeFieldResolver !== void 0
        ? subscribeFieldResolver
        : defaultFieldResolver,
    errors: [],
  };
}
/**
 * Implements the "Executing operations" section of the spec.
 */

function executeOperation(exeContext, operation, rootValue) {
  const rootType = exeContext.schema.getRootType(operation.operation);

  if (rootType == null) {
    throw new GraphQLError(
      `Schema is not configured to execute ${operation.operation} operation.`,
      {
        nodes: operation,
      },
    );
  }

  const rootFields = collectFields(
    exeContext.schema,
    exeContext.fragments,
    exeContext.variableValues,
    rootType,
    operation.selectionSet,
  );
  const path = undefined;

  switch (operation.operation) {
    case OperationTypeNode.QUERY:
      return executeFields(exeContext, rootType, rootValue, path, rootFields);

    case OperationTypeNode.MUTATION:
      return executeFieldsSerially(
        exeContext,
        rootType,
        rootValue,
        path,
        rootFields,
      );

    case OperationTypeNode.SUBSCRIPTION:
      // TODO: deprecate `subscribe` and move all logic here
      // Temporary solution until we finish merging execute and subscribe together
      return executeFields(exeContext, rootType, rootValue, path, rootFields);
  }
}
/**
 * Implements the "Executing selection sets" section of the spec
 * for fields that must be executed serially.
 */

function executeFieldsSerially(
  exeContext,
  parentType,
  sourceValue,
  path,
  fields,
) {
  return promiseReduce(
    fields.entries(),
    (results, [responseName, fieldNodes]) => {
      const fieldPath = addPath(path, responseName, parentType.name);
      const result = executeField(
        exeContext,
        parentType,
        sourceValue,
        fieldNodes,
        fieldPath,
      );

      if (result === undefined) {
        return results;
      }

      if (isPromise(result)) {
        return result.then((resolvedResult) => {
          results[responseName] = resolvedResult;
          return results;
        });
      }

      results[responseName] = result;
      return results;
    },
    Object.create(null),
  );
}
/**
 * Implements the "Executing selection sets" section of the spec
 * for fields that may be executed in parallel.
 */

function executeFields(exeContext, parentType, sourceValue, path, fields) {
  const results = Object.create(null);
  let containsPromise = false;

  try {
    for (const [responseName, fieldNodes] of fields.entries()) {
      const fieldPath = addPath(path, responseName, parentType.name);
      const result = executeField(
        exeContext,
        parentType,
        sourceValue,
        fieldNodes,
        fieldPath,
      );

      if (result !== undefined) {
        results[responseName] = result;

        if (isPromise(result)) {
          containsPromise = true;
        }
      }
    }
  } catch (error) {
    if (containsPromise) {
      // Ensure that any promises returned by other fields are handled, as they may also reject.
      return promiseForObject(results).finally(() => {
        throw error;
      });
    }

    throw error;
  } // If there are no promises, we can just return the object

  if (!containsPromise) {
    return results;
  } // Otherwise, results is a map from field name to the result of resolving that
  // field, which is possibly a promise. Return a promise that will return this
  // same map, but with any promises replaced with the values they resolved to.

  return promiseForObject(results);
}
/**
 * Implements the "Executing fields" section of the spec
 * In particular, this function figures out the value that the field returns by
 * calling its resolve function, then calls completeValue to complete promises,
 * serialize scalars, or execute the sub-selection-set for objects.
 */

function executeField(exeContext, parentType, source, fieldNodes, path) {
  var _fieldDef$resolve;

  const fieldDef = getFieldDef(exeContext.schema, parentType, fieldNodes[0]);

  if (!fieldDef) {
    return;
  }

  const returnType = fieldDef.type;
  const resolveFn =
    (_fieldDef$resolve = fieldDef.resolve) !== null &&
    _fieldDef$resolve !== void 0
      ? _fieldDef$resolve
      : exeContext.fieldResolver;
  const info = buildResolveInfo(
    exeContext,
    fieldDef,
    fieldNodes,
    parentType,
    path,
  ); // Get the resolve function, regardless of if its result is normal or abrupt (error).

  try {
    // Build a JS object of arguments from the field.arguments AST, using the
    // variables scope to fulfill any variable references.
    // TODO: find a way to memoize, in case this field is within a List type.
    const args = getArgumentValues(
      fieldDef,
      fieldNodes[0],
      exeContext.variableValues,
    ); // The resolve function's optional third argument is a context value that
    // is provided to every resolve function within an execution. It is commonly
    // used to represent an authenticated user, or request-specific caches.

    const contextValue = exeContext.contextValue;
    const result = resolveFn(source, args, contextValue, info);
    let completed;

    if (isPromise(result)) {
      completed = result.then((resolved) =>
        completeValue(exeContext, returnType, fieldNodes, info, path, resolved),
      );
    } else {
      completed = completeValue(
        exeContext,
        returnType,
        fieldNodes,
        info,
        path,
        result,
      );
    }

    if (isPromise(completed)) {
      // Note: we don't rely on a `catch` method, but we do expect "thenable"
      // to take a second callback for the error case.
      return completed.then(undefined, (rawError) => {
        const error = locatedError(rawError, fieldNodes, pathToArray(path));
        return handleFieldError(error, returnType, exeContext);
      });
    }

    return completed;
  } catch (rawError) {
    const error = locatedError(rawError, fieldNodes, pathToArray(path));
    return handleFieldError(error, returnType, exeContext);
  }
}
/**
 * @internal
 */

function buildResolveInfo(
  exeContext,
  fieldDef,
  fieldNodes,
  parentType,
  path,
) {
  // The resolve function's optional fourth argument is a collection of
  // information about the current execution state.
  return {
    fieldName: fieldDef.name,
    fieldNodes,
    returnType: fieldDef.type,
    parentType,
    path,
    schema: exeContext.schema,
    fragments: exeContext.fragments,
    rootValue: exeContext.rootValue,
    operation: exeContext.operation,
    variableValues: exeContext.variableValues,
  };
}

function handleFieldError(error, returnType, exeContext) {
  // If the field type is non-nullable, then it is resolved without any
  // protection from errors, however it still properly locates the error.
  if (isNonNullType(returnType)) {
    throw error;
  } // Otherwise, error protection is applied, logging the error and resolving
  // a null value for this field if one is encountered.

  exeContext.errors.push(error);
  return null;
}
/**
 * Implements the instructions for completeValue as defined in the
 * "Value Completion" section of the spec.
 *
 * If the field type is Non-Null, then this recursively completes the value
 * for the inner type. It throws a field error if that completion returns null,
 * as per the "Nullability" section of the spec.
 *
 * If the field type is a List, then this recursively completes the value
 * for the inner type on each item in the list.
 *
 * If the field type is a Scalar or Enum, ensures the completed value is a legal
 * value of the type by calling the `serialize` method of GraphQL type
 * definition.
 *
 * If the field is an abstract type, determine the runtime type of the value
 * and then complete based on that type
 *
 * Otherwise, the field type expects a sub-selection set, and will complete the
 * value by executing all sub-selections.
 */

function completeValue(exeContext, returnType, fieldNodes, info, path, result) {
  // If result is an Error, throw a located error.
  if (result instanceof Error) {
    throw result;
  } // If field type is NonNull, complete for inner type, and throw field error
  // if result is null.

  if (isNonNullType(returnType)) {
    const completed = completeValue(
      exeContext,
      returnType.ofType,
      fieldNodes,
      info,
      path,
      result,
    );

    if (completed === null) {
      throw new Error(
        `Cannot return null for non-nullable field ${info.parentType.name}.${info.fieldName}.`,
      );
    }

    return completed;
  } // If result value is null or undefined then return null.

  if (result == null) {
    return null;
  } // If field type is List, complete each item in the list with the inner type

  if (isListType(returnType)) {
    return completeListValue(
      exeContext,
      returnType,
      fieldNodes,
      info,
      path,
      result,
    );
  } // If field type is a leaf type, Scalar or Enum, serialize to a valid value,
  // returning null if serialization is not possible.

  if (isLeafType(returnType)) {
    return completeLeafValue(returnType, result);
  } // If field type is an abstract type, Interface or Union, determine the
  // runtime Object type and complete for that type.

  if (isAbstractType(returnType)) {
    return completeAbstractValue(
      exeContext,
      returnType,
      fieldNodes,
      info,
      path,
      result,
    );
  } // If field type is Object, execute and complete all sub-selections.

  if (isObjectType(returnType)) {
    return completeObjectValue(
      exeContext,
      returnType,
      fieldNodes,
      info,
      path,
      result,
    );
  }
  /* c8 ignore next 6 */
  // Not reachable, all possible output types have been considered.

  invariant(
      false,
      'Cannot complete value of unexpected output type: ' + inspect(returnType),
    );
}
/**
 * Complete a list value by completing each item in the list with the
 * inner type
 */

function completeListValue(
  exeContext,
  returnType,
  fieldNodes,
  info,
  path,
  result,
) {
  if (!isIterableObject(result)) {
    throw new GraphQLError(
      `Expected Iterable, but did not find one for field "${info.parentType.name}.${info.fieldName}".`,
    );
  } // This is specified as a simple map, however we're optimizing the path
  // where the list contains no Promises by avoiding creating another Promise.

  const itemType = returnType.ofType;
  let containsPromise = false;
  const completedResults = Array.from(result, (item, index) => {
    // No need to modify the info object containing the path,
    // since from here on it is not ever accessed by resolver functions.
    const itemPath = addPath(path, index, undefined);

    try {
      let completedItem;

      if (isPromise(item)) {
        completedItem = item.then((resolved) =>
          completeValue(
            exeContext,
            itemType,
            fieldNodes,
            info,
            itemPath,
            resolved,
          ),
        );
      } else {
        completedItem = completeValue(
          exeContext,
          itemType,
          fieldNodes,
          info,
          itemPath,
          item,
        );
      }

      if (isPromise(completedItem)) {
        containsPromise = true; // Note: we don't rely on a `catch` method, but we do expect "thenable"
        // to take a second callback for the error case.

        return completedItem.then(undefined, (rawError) => {
          const error = locatedError(
            rawError,
            fieldNodes,
            pathToArray(itemPath),
          );
          return handleFieldError(error, itemType, exeContext);
        });
      }

      return completedItem;
    } catch (rawError) {
      const error = locatedError(rawError, fieldNodes, pathToArray(itemPath));
      return handleFieldError(error, itemType, exeContext);
    }
  });
  return containsPromise ? Promise.all(completedResults) : completedResults;
}
/**
 * Complete a Scalar or Enum by serializing to a valid value, returning
 * null if serialization is not possible.
 */

function completeLeafValue(returnType, result) {
  const serializedResult = returnType.serialize(result);

  if (serializedResult == null) {
    throw new Error(
      `Expected \`${inspect(returnType)}.serialize(${inspect(result)})\` to ` +
        `return non-nullable value, returned: ${inspect(serializedResult)}`,
    );
  }

  return serializedResult;
}
/**
 * Complete a value of an abstract type by determining the runtime object type
 * of that value, then complete the value for that type.
 */

function completeAbstractValue(
  exeContext,
  returnType,
  fieldNodes,
  info,
  path,
  result,
) {
  var _returnType$resolveTy;

  const resolveTypeFn =
    (_returnType$resolveTy = returnType.resolveType) !== null &&
    _returnType$resolveTy !== void 0
      ? _returnType$resolveTy
      : exeContext.typeResolver;
  const contextValue = exeContext.contextValue;
  const runtimeType = resolveTypeFn(result, contextValue, info, returnType);

  if (isPromise(runtimeType)) {
    return runtimeType.then((resolvedRuntimeType) =>
      completeObjectValue(
        exeContext,
        ensureValidRuntimeType(
          resolvedRuntimeType,
          exeContext,
          returnType,
          fieldNodes,
          info,
          result,
        ),
        fieldNodes,
        info,
        path,
        result,
      ),
    );
  }

  return completeObjectValue(
    exeContext,
    ensureValidRuntimeType(
      runtimeType,
      exeContext,
      returnType,
      fieldNodes,
      info,
      result,
    ),
    fieldNodes,
    info,
    path,
    result,
  );
}

function ensureValidRuntimeType(
  runtimeTypeName,
  exeContext,
  returnType,
  fieldNodes,
  info,
  result,
) {
  if (runtimeTypeName == null) {
    throw new GraphQLError(
      `Abstract type "${returnType.name}" must resolve to an Object type at runtime for field "${info.parentType.name}.${info.fieldName}". Either the "${returnType.name}" type should provide a "resolveType" function or each possible type should provide an "isTypeOf" function.`,
      fieldNodes,
    );
  } // releases before 16.0.0 supported returning `GraphQLObjectType` from `resolveType`
  // TODO: remove in 17.0.0 release

  if (isObjectType(runtimeTypeName)) {
    throw new GraphQLError(
      'Support for returning GraphQLObjectType from resolveType was removed in graphql-js@16.0.0 please return type name instead.',
    );
  }

  if (typeof runtimeTypeName !== 'string') {
    throw new GraphQLError(
      `Abstract type "${returnType.name}" must resolve to an Object type at runtime for field "${info.parentType.name}.${info.fieldName}" with ` +
        `value ${inspect(result)}, received "${inspect(runtimeTypeName)}".`,
    );
  }

  const runtimeType = exeContext.schema.getType(runtimeTypeName);

  if (runtimeType == null) {
    throw new GraphQLError(
      `Abstract type "${returnType.name}" was resolved to a type "${runtimeTypeName}" that does not exist inside the schema.`,
      {
        nodes: fieldNodes,
      },
    );
  }

  if (!isObjectType(runtimeType)) {
    throw new GraphQLError(
      `Abstract type "${returnType.name}" was resolved to a non-object type "${runtimeTypeName}".`,
      {
        nodes: fieldNodes,
      },
    );
  }

  if (!exeContext.schema.isSubType(returnType, runtimeType)) {
    throw new GraphQLError(
      `Runtime Object type "${runtimeType.name}" is not a possible type for "${returnType.name}".`,
      {
        nodes: fieldNodes,
      },
    );
  }

  return runtimeType;
}
/**
 * Complete an Object value by executing all sub-selections.
 */

function completeObjectValue(
  exeContext,
  returnType,
  fieldNodes,
  info,
  path,
  result,
) {
  // Collect sub-fields to execute to complete this value.
  const subFieldNodes = collectSubfields(exeContext, returnType, fieldNodes); // If there is an isTypeOf predicate function, call it with the
  // current result. If isTypeOf returns false, then raise an error rather
  // than continuing execution.

  if (returnType.isTypeOf) {
    const isTypeOf = returnType.isTypeOf(result, exeContext.contextValue, info);

    if (isPromise(isTypeOf)) {
      return isTypeOf.then((resolvedIsTypeOf) => {
        if (!resolvedIsTypeOf) {
          throw invalidReturnTypeError(returnType, result, fieldNodes);
        }

        return executeFields(
          exeContext,
          returnType,
          result,
          path,
          subFieldNodes,
        );
      });
    }

    if (!isTypeOf) {
      throw invalidReturnTypeError(returnType, result, fieldNodes);
    }
  }

  return executeFields(exeContext, returnType, result, path, subFieldNodes);
}

function invalidReturnTypeError(returnType, result, fieldNodes) {
  return new GraphQLError(
    `Expected value of type "${returnType.name}" but got: ${inspect(result)}.`,
    {
      nodes: fieldNodes,
    },
  );
}
/**
 * If a resolveType function is not given, then a default resolve behavior is
 * used which attempts two strategies:
 *
 * First, See if the provided value has a `__typename` field defined, if so, use
 * that value as name of the resolved type.
 *
 * Otherwise, test each possible type for the abstract type by calling
 * isTypeOf for the object being coerced, returning the first type that matches.
 */

const defaultTypeResolver = function (
  value,
  contextValue,
  info,
  abstractType,
) {
  // First, look for `__typename`.
  if (isObjectLike$c(value) && typeof value.__typename === 'string') {
    return value.__typename;
  } // Otherwise, test each possible type.

  const possibleTypes = info.schema.getPossibleTypes(abstractType);
  const promisedIsTypeOfResults = [];

  for (let i = 0; i < possibleTypes.length; i++) {
    const type = possibleTypes[i];

    if (type.isTypeOf) {
      const isTypeOfResult = type.isTypeOf(value, contextValue, info);

      if (isPromise(isTypeOfResult)) {
        promisedIsTypeOfResults[i] = isTypeOfResult;
      } else if (isTypeOfResult) {
        return type.name;
      }
    }
  }

  if (promisedIsTypeOfResults.length) {
    return Promise.all(promisedIsTypeOfResults).then((isTypeOfResults) => {
      for (let i = 0; i < isTypeOfResults.length; i++) {
        if (isTypeOfResults[i]) {
          return possibleTypes[i].name;
        }
      }
    });
  }
};
/**
 * If a resolve function is not given, then a default resolve behavior is used
 * which takes the property of the source object of the same name as the field
 * and returns it as the result, or if it's a function, returns the result
 * of calling that function while passing along args and context value.
 */

const defaultFieldResolver = function (
  source,
  args,
  contextValue,
  info,
) {
  // ensure source is a value for which property access is acceptable.
  if (isObjectLike$c(source) || typeof source === 'function') {
    const property = source[info.fieldName];

    if (typeof property === 'function') {
      return source[info.fieldName](args, contextValue, info);
    }

    return property;
  }
};
/**
 * This method looks up the field on the given type definition.
 * It has special casing for the three introspection fields,
 * __schema, __type and __typename. __typename is special because
 * it can always be queried as a field, even in situations where no
 * other fields are allowed, like on a Union. __schema and __type
 * could get automatically added to the query type, but that would
 * require mutating type definitions, which would cause issues.
 *
 * @internal
 */

function getFieldDef(schema, parentType, fieldNode) {
  const fieldName = fieldNode.name.value;

  if (
    fieldName === SchemaMetaFieldDef.name &&
    schema.getQueryType() === parentType
  ) {
    return SchemaMetaFieldDef;
  } else if (
    fieldName === TypeMetaFieldDef.name &&
    schema.getQueryType() === parentType
  ) {
    return TypeMetaFieldDef;
  } else if (fieldName === TypeNameMetaFieldDef.name) {
    return TypeNameMetaFieldDef;
  }

  return parentType.getFields()[fieldName];
}

/**
 * This is the primary entry point function for fulfilling GraphQL operations
 * by parsing, validating, and executing a GraphQL document along side a
 * GraphQL schema.
 *
 * More sophisticated GraphQL servers, such as those which persist queries,
 * may wish to separate the validation and execution phases to a static time
 * tooling step, and a server runtime step.
 *
 * Accepts either an object with named arguments, or individual arguments:
 *
 * schema:
 *    The GraphQL type system to use when validating and executing a query.
 * source:
 *    A GraphQL language formatted string representing the requested operation.
 * rootValue:
 *    The value provided as the first argument to resolver functions on the top
 *    level type (e.g. the query object type).
 * contextValue:
 *    The context value is provided as an argument to resolver functions after
 *    field arguments. It is used to pass shared information useful at any point
 *    during executing this query, for example the currently logged in user and
 *    connections to databases or other services.
 * variableValues:
 *    A mapping of variable name to runtime value to use for all variables
 *    defined in the requestString.
 * operationName:
 *    The name of the operation to use if requestString contains multiple
 *    possible operations. Can be omitted if requestString contains only
 *    one operation.
 * fieldResolver:
 *    A resolver function to use when one is not provided by the schema.
 *    If not provided, the default field resolver is used (which looks for a
 *    value or method on the source value with the field's name).
 * typeResolver:
 *    A type resolver function to use when none is provided by the schema.
 *    If not provided, the default type resolver is used (which looks for a
 *    `__typename` field or alternatively calls the `isTypeOf` method).
 */

function graphql$1(args) {
  // Always return a Promise for a consistent API.
  return new Promise((resolve) => resolve(graphqlImpl(args)));
}
/**
 * The graphqlSync function also fulfills GraphQL operations by parsing,
 * validating, and executing a GraphQL document along side a GraphQL schema.
 * However, it guarantees to complete synchronously (or throw an error) assuming
 * that all field resolvers are also synchronous.
 */

function graphqlSync(args) {
  const result = graphqlImpl(args); // Assert that the execution was synchronous.

  if (isPromise(result)) {
    throw new Error('GraphQL execution failed to complete synchronously.');
  }

  return result;
}

function graphqlImpl(args) {
  // Temporary for v15 to v16 migration. Remove in v17
  arguments.length < 2 ||
    devAssert(
      false,
      'graphql@16 dropped long-deprecated support for positional arguments, please pass an object instead.',
    );
  const {
    schema,
    source,
    rootValue,
    contextValue,
    variableValues,
    operationName,
    fieldResolver,
    typeResolver,
  } = args; // Validate Schema

  const schemaValidationErrors = validateSchema(schema);

  if (schemaValidationErrors.length > 0) {
    return {
      errors: schemaValidationErrors,
    };
  } // Parse

  let document;

  try {
    document = parse$1(source);
  } catch (syntaxError) {
    return {
      errors: [syntaxError],
    };
  } // Validate

  const validationErrors = validate$1(schema, document);

  if (validationErrors.length > 0) {
    return {
      errors: validationErrors,
    };
  } // Execute

  return execute({
    schema,
    document,
    rootValue,
    contextValue,
    variableValues,
    operationName,
    fieldResolver,
    typeResolver,
  });
}

/**
 * Returns true if the provided object implements the AsyncIterator protocol via
 * implementing a `Symbol.asyncIterator` method.
 */
function isAsyncIterable(maybeAsyncIterable) {
  return (
    typeof (maybeAsyncIterable === null || maybeAsyncIterable === void 0
      ? void 0
      : maybeAsyncIterable[Symbol.asyncIterator]) === 'function'
  );
}

/**
 * Given an AsyncIterable and a callback function, return an AsyncIterator
 * which produces values mapped via calling the callback function.
 */
function mapAsyncIterator(iterable, callback) {
  const iterator = iterable[Symbol.asyncIterator]();

  async function mapResult(result) {
    if (result.done) {
      return result;
    }

    try {
      return {
        value: await callback(result.value),
        done: false,
      };
    } catch (error) {
      /* c8 ignore start */
      // FIXME: add test case
      if (typeof iterator.return === 'function') {
        try {
          await iterator.return();
        } catch (_e) {
          /* ignore error */
        }
      }

      throw error;
      /* c8 ignore stop */
    }
  }

  return {
    async next() {
      return mapResult(await iterator.next());
    },

    async return() {
      // If iterator.return() does not exist, then type R must be undefined.
      return typeof iterator.return === 'function'
        ? mapResult(await iterator.return())
        : {
            value: undefined,
            done: true,
          };
    },

    async throw(error) {
      if (typeof iterator.throw === 'function') {
        return mapResult(await iterator.throw(error));
      }

      throw error;
    },

    [Symbol.asyncIterator]() {
      return this;
    },
  };
}

/**
 * Implements the "Subscribe" algorithm described in the GraphQL specification.
 *
 * Returns a Promise which resolves to either an AsyncIterator (if successful)
 * or an ExecutionResult (error). The promise will be rejected if the schema or
 * other arguments to this function are invalid, or if the resolved event stream
 * is not an async iterable.
 *
 * If the client-provided arguments to this function do not result in a
 * compliant subscription, a GraphQL Response (ExecutionResult) with
 * descriptive errors and no data will be returned.
 *
 * If the source stream could not be created due to faulty subscription
 * resolver logic or underlying systems, the promise will resolve to a single
 * ExecutionResult containing `errors` and no `data`.
 *
 * If the operation succeeded, the promise resolves to an AsyncIterator, which
 * yields a stream of ExecutionResults representing the response stream.
 *
 * Accepts either an object with named arguments, or individual arguments.
 */

async function subscribe$1(args) {
  // Temporary for v15 to v16 migration. Remove in v17
  arguments.length < 2 ||
    devAssert(
      false,
      'graphql@16 dropped long-deprecated support for positional arguments, please pass an object instead.',
    );
  const resultOrStream = await createSourceEventStream(args);

  if (!isAsyncIterable(resultOrStream)) {
    return resultOrStream;
  } // For each payload yielded from a subscription, map it over the normal
  // GraphQL `execute` function, with `payload` as the rootValue.
  // This implements the "MapSourceToResponseEvent" algorithm described in
  // the GraphQL specification. The `execute` function provides the
  // "ExecuteSubscriptionEvent" algorithm, as it is nearly identical to the
  // "ExecuteQuery" algorithm, for which `execute` is also used.

  const mapSourceToResponse = (payload) =>
    execute({ ...args, rootValue: payload }); // Map every source value to a ExecutionResult value as described above.

  return mapAsyncIterator(resultOrStream, mapSourceToResponse);
}

function toNormalizedArgs(args) {
  const firstArg = args[0];

  if (firstArg && 'document' in firstArg) {
    return firstArg;
  }

  return {
    schema: firstArg,
    // FIXME: when underlying TS bug fixed, see https://github.com/microsoft/TypeScript/issues/31613
    document: args[1],
    rootValue: args[2],
    contextValue: args[3],
    variableValues: args[4],
    operationName: args[5],
    subscribeFieldResolver: args[6],
  };
}
/**
 * Implements the "CreateSourceEventStream" algorithm described in the
 * GraphQL specification, resolving the subscription source event stream.
 *
 * Returns a Promise which resolves to either an AsyncIterable (if successful)
 * or an ExecutionResult (error). The promise will be rejected if the schema or
 * other arguments to this function are invalid, or if the resolved event stream
 * is not an async iterable.
 *
 * If the client-provided arguments to this function do not result in a
 * compliant subscription, a GraphQL Response (ExecutionResult) with
 * descriptive errors and no data will be returned.
 *
 * If the the source stream could not be created due to faulty subscription
 * resolver logic or underlying systems, the promise will resolve to a single
 * ExecutionResult containing `errors` and no `data`.
 *
 * If the operation succeeded, the promise resolves to the AsyncIterable for the
 * event stream returned by the resolver.
 *
 * A Source Event Stream represents a sequence of events, each of which triggers
 * a GraphQL execution for that event.
 *
 * This may be useful when hosting the stateful subscription service in a
 * different process or machine than the stateless GraphQL execution engine,
 * or otherwise separating these two steps. For more on this, see the
 * "Supporting Subscriptions at Scale" information in the GraphQL specification.
 */

async function createSourceEventStream(...rawArgs) {
  const args = toNormalizedArgs(rawArgs);
  const { schema, document, variableValues } = args; // If arguments are missing or incorrectly typed, this is an internal
  // developer mistake which should throw an early error.

  assertValidExecutionArguments(schema, document, variableValues); // If a valid execution context cannot be created due to incorrect arguments,
  // a "Response" with only errors is returned.

  const exeContext = buildExecutionContext(args); // Return early errors if execution context failed.

  if (!('schema' in exeContext)) {
    return {
      errors: exeContext,
    };
  }

  try {
    const eventStream = await executeSubscription(exeContext); // Assert field returned an event stream, otherwise yield an error.

    if (!isAsyncIterable(eventStream)) {
      throw new Error(
        'Subscription field must return Async Iterable. ' +
          `Received: ${inspect(eventStream)}.`,
      );
    }

    return eventStream;
  } catch (error) {
    // If it GraphQLError, report it as an ExecutionResult, containing only errors and no data.
    // Otherwise treat the error as a system-class error and re-throw it.
    if (error instanceof GraphQLError) {
      return {
        errors: [error],
      };
    }

    throw error;
  }
}

async function executeSubscription(exeContext) {
  const { schema, fragments, operation, variableValues, rootValue } =
    exeContext;
  const rootType = schema.getSubscriptionType();

  if (rootType == null) {
    throw new GraphQLError(
      'Schema is not configured to execute subscription operation.',
      {
        nodes: operation,
      },
    );
  }

  const rootFields = collectFields(
    schema,
    fragments,
    variableValues,
    rootType,
    operation.selectionSet,
  );
  const [responseName, fieldNodes] = [...rootFields.entries()][0];
  const fieldDef = getFieldDef(schema, rootType, fieldNodes[0]);

  if (!fieldDef) {
    const fieldName = fieldNodes[0].name.value;
    throw new GraphQLError(
      `The subscription field "${fieldName}" is not defined.`,
      {
        nodes: fieldNodes,
      },
    );
  }

  const path = addPath(undefined, responseName, rootType.name);
  const info = buildResolveInfo(
    exeContext,
    fieldDef,
    fieldNodes,
    rootType,
    path,
  );

  try {
    var _fieldDef$subscribe;

    // Implements the "ResolveFieldEventStream" algorithm from GraphQL specification.
    // It differs from "ResolveFieldValue" due to providing a different `resolveFn`.
    // Build a JS object of arguments from the field.arguments AST, using the
    // variables scope to fulfill any variable references.
    const args = getArgumentValues(fieldDef, fieldNodes[0], variableValues); // The resolve function's optional third argument is a context value that
    // is provided to every resolve function within an execution. It is commonly
    // used to represent an authenticated user, or request-specific caches.

    const contextValue = exeContext.contextValue; // Call the `subscribe()` resolver or the default resolver to produce an
    // AsyncIterable yielding raw payloads.

    const resolveFn =
      (_fieldDef$subscribe = fieldDef.subscribe) !== null &&
      _fieldDef$subscribe !== void 0
        ? _fieldDef$subscribe
        : exeContext.subscribeFieldResolver;
    const eventStream = await resolveFn(rootValue, args, contextValue, info);

    if (eventStream instanceof Error) {
      throw eventStream;
    }

    return eventStream;
  } catch (error) {
    throw locatedError(error, fieldNodes, pathToArray(path));
  }
}

/**
 * No deprecated
 *
 * A GraphQL document is only valid if all selected fields and all used enum values have not been
 * deprecated.
 *
 * Note: This rule is optional and is not part of the Validation section of the GraphQL
 * Specification. The main purpose of this rule is detection of deprecated usages and not
 * necessarily to forbid their use when querying a service.
 */
function NoDeprecatedCustomRule(context) {
  return {
    Field(node) {
      const fieldDef = context.getFieldDef();
      const deprecationReason =
        fieldDef === null || fieldDef === void 0
          ? void 0
          : fieldDef.deprecationReason;

      if (fieldDef && deprecationReason != null) {
        const parentType = context.getParentType();
        parentType != null || invariant(false);
        context.reportError(
          new GraphQLError(
            `The field ${parentType.name}.${fieldDef.name} is deprecated. ${deprecationReason}`,
            {
              nodes: node,
            },
          ),
        );
      }
    },

    Argument(node) {
      const argDef = context.getArgument();
      const deprecationReason =
        argDef === null || argDef === void 0
          ? void 0
          : argDef.deprecationReason;

      if (argDef && deprecationReason != null) {
        const directiveDef = context.getDirective();

        if (directiveDef != null) {
          context.reportError(
            new GraphQLError(
              `Directive "@${directiveDef.name}" argument "${argDef.name}" is deprecated. ${deprecationReason}`,
              {
                nodes: node,
              },
            ),
          );
        } else {
          const parentType = context.getParentType();
          const fieldDef = context.getFieldDef();
          (parentType != null && fieldDef != null) || invariant(false);
          context.reportError(
            new GraphQLError(
              `Field "${parentType.name}.${fieldDef.name}" argument "${argDef.name}" is deprecated. ${deprecationReason}`,
              {
                nodes: node,
              },
            ),
          );
        }
      }
    },

    ObjectField(node) {
      const inputObjectDef = getNamedType(context.getParentInputType());

      if (isInputObjectType(inputObjectDef)) {
        const inputFieldDef = inputObjectDef.getFields()[node.name.value];
        const deprecationReason =
          inputFieldDef === null || inputFieldDef === void 0
            ? void 0
            : inputFieldDef.deprecationReason;

        if (deprecationReason != null) {
          context.reportError(
            new GraphQLError(
              `The input field ${inputObjectDef.name}.${inputFieldDef.name} is deprecated. ${deprecationReason}`,
              {
                nodes: node,
              },
            ),
          );
        }
      }
    },

    EnumValue(node) {
      const enumValueDef = context.getEnumValue();
      const deprecationReason =
        enumValueDef === null || enumValueDef === void 0
          ? void 0
          : enumValueDef.deprecationReason;

      if (enumValueDef && deprecationReason != null) {
        const enumTypeDef = getNamedType(context.getInputType());
        enumTypeDef != null || invariant(false);
        context.reportError(
          new GraphQLError(
            `The enum value "${enumTypeDef.name}.${enumValueDef.name}" is deprecated. ${deprecationReason}`,
            {
              nodes: node,
            },
          ),
        );
      }
    },
  };
}

/**
 * Prohibit introspection queries
 *
 * A GraphQL document is only valid if all fields selected are not fields that
 * return an introspection type.
 *
 * Note: This rule is optional and is not part of the Validation section of the
 * GraphQL Specification. This rule effectively disables introspection, which
 * does not reflect best practices and should only be done if absolutely necessary.
 */
function NoSchemaIntrospectionCustomRule(context) {
  return {
    Field(node) {
      const type = getNamedType(context.getType());

      if (type && isIntrospectionType(type)) {
        context.reportError(
          new GraphQLError(
            `GraphQL introspection has been disabled, but the requested query contained the field "${node.name.value}".`,
            {
              nodes: node,
            },
          ),
        );
      }
    },
  };
}

/**
 * Produce the GraphQL query recommended for a full schema introspection.
 * Accepts optional IntrospectionOptions.
 */
function getIntrospectionQuery(options) {
  const optionsWithDefault = {
    descriptions: true,
    specifiedByUrl: false,
    directiveIsRepeatable: false,
    schemaDescription: false,
    inputValueDeprecation: false,
    ...options,
  };
  const descriptions = optionsWithDefault.descriptions ? 'description' : '';
  const specifiedByUrl = optionsWithDefault.specifiedByUrl
    ? 'specifiedByURL'
    : '';
  const directiveIsRepeatable = optionsWithDefault.directiveIsRepeatable
    ? 'isRepeatable'
    : '';
  const schemaDescription = optionsWithDefault.schemaDescription
    ? descriptions
    : '';

  function inputDeprecation(str) {
    return optionsWithDefault.inputValueDeprecation ? str : '';
  }

  return `
    query IntrospectionQuery {
      __schema {
        ${schemaDescription}
        queryType { name }
        mutationType { name }
        subscriptionType { name }
        types {
          ...FullType
        }
        directives {
          name
          ${descriptions}
          ${directiveIsRepeatable}
          locations
          args${inputDeprecation('(includeDeprecated: true)')} {
            ...InputValue
          }
        }
      }
    }

    fragment FullType on __Type {
      kind
      name
      ${descriptions}
      ${specifiedByUrl}
      fields(includeDeprecated: true) {
        name
        ${descriptions}
        args${inputDeprecation('(includeDeprecated: true)')} {
          ...InputValue
        }
        type {
          ...TypeRef
        }
        isDeprecated
        deprecationReason
      }
      inputFields${inputDeprecation('(includeDeprecated: true)')} {
        ...InputValue
      }
      interfaces {
        ...TypeRef
      }
      enumValues(includeDeprecated: true) {
        name
        ${descriptions}
        isDeprecated
        deprecationReason
      }
      possibleTypes {
        ...TypeRef
      }
    }

    fragment InputValue on __InputValue {
      name
      ${descriptions}
      type { ...TypeRef }
      defaultValue
      ${inputDeprecation('isDeprecated')}
      ${inputDeprecation('deprecationReason')}
    }

    fragment TypeRef on __Type {
      kind
      name
      ofType {
        kind
        name
        ofType {
          kind
          name
          ofType {
            kind
            name
            ofType {
              kind
              name
              ofType {
                kind
                name
                ofType {
                  kind
                  name
                  ofType {
                    kind
                    name
                    ofType {
                      kind
                      name
                      ofType {
                        kind
                        name
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;
}

/**
 * Returns an operation AST given a document AST and optionally an operation
 * name. If a name is not provided, an operation is only returned if only one is
 * provided in the document.
 */

function getOperationAST(documentAST, operationName) {
  let operation = null;

  for (const definition of documentAST.definitions) {
    if (definition.kind === Kind.OPERATION_DEFINITION) {
      var _definition$name;

      if (operationName == null) {
        // If no operation name was provided, only return an Operation if there
        // is one defined in the document. Upon encountering the second, return
        // null.
        if (operation) {
          return null;
        }

        operation = definition;
      } else if (
        ((_definition$name = definition.name) === null ||
        _definition$name === void 0
          ? void 0
          : _definition$name.value) === operationName
      ) {
        return definition;
      }
    }
  }

  return operation;
}

/**
 * Extracts the root type of the operation from the schema.
 *
 * @deprecated Please use `GraphQLSchema.getRootType` instead. Will be removed in v17
 */
function getOperationRootType(schema, operation) {
  if (operation.operation === 'query') {
    const queryType = schema.getQueryType();

    if (!queryType) {
      throw new GraphQLError(
        'Schema does not define the required query root type.',
        {
          nodes: operation,
        },
      );
    }

    return queryType;
  }

  if (operation.operation === 'mutation') {
    const mutationType = schema.getMutationType();

    if (!mutationType) {
      throw new GraphQLError('Schema is not configured for mutations.', {
        nodes: operation,
      });
    }

    return mutationType;
  }

  if (operation.operation === 'subscription') {
    const subscriptionType = schema.getSubscriptionType();

    if (!subscriptionType) {
      throw new GraphQLError('Schema is not configured for subscriptions.', {
        nodes: operation,
      });
    }

    return subscriptionType;
  }

  throw new GraphQLError(
    'Can only have query, mutation and subscription operations.',
    {
      nodes: operation,
    },
  );
}

/**
 * Build an IntrospectionQuery from a GraphQLSchema
 *
 * IntrospectionQuery is useful for utilities that care about type and field
 * relationships, but do not need to traverse through those relationships.
 *
 * This is the inverse of buildClientSchema. The primary use case is outside
 * of the server context, for instance when doing schema comparisons.
 */

function introspectionFromSchema(schema, options) {
  const optionsWithDefaults = {
    specifiedByUrl: true,
    directiveIsRepeatable: true,
    schemaDescription: true,
    inputValueDeprecation: true,
    ...options,
  };
  const document = parse$1(getIntrospectionQuery(optionsWithDefaults));
  const result = executeSync({
    schema,
    document,
  });
  (!result.errors && result.data) || invariant(false);
  return result.data;
}

/**
 * Build a GraphQLSchema for use by client tools.
 *
 * Given the result of a client running the introspection query, creates and
 * returns a GraphQLSchema instance which can be then used with all graphql-js
 * tools, but cannot be used to execute a query, as introspection does not
 * represent the "resolver", "parse" or "serialize" functions or any other
 * server-internal mechanisms.
 *
 * This function expects a complete introspection result. Don't forget to check
 * the "errors" field of a server response before calling this function.
 */

function buildClientSchema(introspection, options) {
  (isObjectLike$c(introspection) && isObjectLike$c(introspection.__schema)) ||
    devAssert(
      false,
      `Invalid or incomplete introspection result. Ensure that you are passing "data" property of introspection response and no "errors" was returned alongside: ${inspect(
        introspection,
      )}.`,
    ); // Get the schema from the introspection result.

  const schemaIntrospection = introspection.__schema; // Iterate through all types, getting the type definition for each.

  const typeMap = keyValMap(
    schemaIntrospection.types,
    (typeIntrospection) => typeIntrospection.name,
    (typeIntrospection) => buildType(typeIntrospection),
  ); // Include standard types only if they are used.

  for (const stdType of [...specifiedScalarTypes, ...introspectionTypes]) {
    if (typeMap[stdType.name]) {
      typeMap[stdType.name] = stdType;
    }
  } // Get the root Query, Mutation, and Subscription types.

  const queryType = schemaIntrospection.queryType
    ? getObjectType(schemaIntrospection.queryType)
    : null;
  const mutationType = schemaIntrospection.mutationType
    ? getObjectType(schemaIntrospection.mutationType)
    : null;
  const subscriptionType = schemaIntrospection.subscriptionType
    ? getObjectType(schemaIntrospection.subscriptionType)
    : null; // Get the directives supported by Introspection, assuming empty-set if
  // directives were not queried for.

  const directives = schemaIntrospection.directives
    ? schemaIntrospection.directives.map(buildDirective)
    : []; // Then produce and return a Schema with these types.

  return new GraphQLSchema({
    description: schemaIntrospection.description,
    query: queryType,
    mutation: mutationType,
    subscription: subscriptionType,
    types: Object.values(typeMap),
    directives,
    assumeValid:
      options === null || options === void 0 ? void 0 : options.assumeValid,
  }); // Given a type reference in introspection, return the GraphQLType instance.
  // preferring cached instances before building new instances.

  function getType(typeRef) {
    if (typeRef.kind === TypeKind.LIST) {
      const itemRef = typeRef.ofType;

      if (!itemRef) {
        throw new Error('Decorated type deeper than introspection query.');
      }

      return new GraphQLList(getType(itemRef));
    }

    if (typeRef.kind === TypeKind.NON_NULL) {
      const nullableRef = typeRef.ofType;

      if (!nullableRef) {
        throw new Error('Decorated type deeper than introspection query.');
      }

      const nullableType = getType(nullableRef);
      return new GraphQLNonNull(assertNullableType(nullableType));
    }

    return getNamedType(typeRef);
  }

  function getNamedType(typeRef) {
    const typeName = typeRef.name;

    if (!typeName) {
      throw new Error(`Unknown type reference: ${inspect(typeRef)}.`);
    }

    const type = typeMap[typeName];

    if (!type) {
      throw new Error(
        `Invalid or incomplete schema, unknown type: ${typeName}. Ensure that a full introspection query is used in order to build a client schema.`,
      );
    }

    return type;
  }

  function getObjectType(typeRef) {
    return assertObjectType(getNamedType(typeRef));
  }

  function getInterfaceType(typeRef) {
    return assertInterfaceType(getNamedType(typeRef));
  } // Given a type's introspection result, construct the correct
  // GraphQLType instance.

  function buildType(type) {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (type != null && type.name != null && type.kind != null) {
      // FIXME: Properly type IntrospectionType, it's a breaking change so fix in v17
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (type.kind) {
        case TypeKind.SCALAR:
          return buildScalarDef(type);

        case TypeKind.OBJECT:
          return buildObjectDef(type);

        case TypeKind.INTERFACE:
          return buildInterfaceDef(type);

        case TypeKind.UNION:
          return buildUnionDef(type);

        case TypeKind.ENUM:
          return buildEnumDef(type);

        case TypeKind.INPUT_OBJECT:
          return buildInputObjectDef(type);
      }
    }

    const typeStr = inspect(type);
    throw new Error(
      `Invalid or incomplete introspection result. Ensure that a full introspection query is used in order to build a client schema: ${typeStr}.`,
    );
  }

  function buildScalarDef(scalarIntrospection) {
    return new GraphQLScalarType({
      name: scalarIntrospection.name,
      description: scalarIntrospection.description,
      specifiedByURL: scalarIntrospection.specifiedByURL,
    });
  }

  function buildImplementationsList(implementingIntrospection) {
    // TODO: Temporary workaround until GraphQL ecosystem will fully support
    // 'interfaces' on interface types.
    if (
      implementingIntrospection.interfaces === null &&
      implementingIntrospection.kind === TypeKind.INTERFACE
    ) {
      return [];
    }

    if (!implementingIntrospection.interfaces) {
      const implementingIntrospectionStr = inspect(implementingIntrospection);
      throw new Error(
        `Introspection result missing interfaces: ${implementingIntrospectionStr}.`,
      );
    }

    return implementingIntrospection.interfaces.map(getInterfaceType);
  }

  function buildObjectDef(objectIntrospection) {
    return new GraphQLObjectType({
      name: objectIntrospection.name,
      description: objectIntrospection.description,
      interfaces: () => buildImplementationsList(objectIntrospection),
      fields: () => buildFieldDefMap(objectIntrospection),
    });
  }

  function buildInterfaceDef(interfaceIntrospection) {
    return new GraphQLInterfaceType({
      name: interfaceIntrospection.name,
      description: interfaceIntrospection.description,
      interfaces: () => buildImplementationsList(interfaceIntrospection),
      fields: () => buildFieldDefMap(interfaceIntrospection),
    });
  }

  function buildUnionDef(unionIntrospection) {
    if (!unionIntrospection.possibleTypes) {
      const unionIntrospectionStr = inspect(unionIntrospection);
      throw new Error(
        `Introspection result missing possibleTypes: ${unionIntrospectionStr}.`,
      );
    }

    return new GraphQLUnionType({
      name: unionIntrospection.name,
      description: unionIntrospection.description,
      types: () => unionIntrospection.possibleTypes.map(getObjectType),
    });
  }

  function buildEnumDef(enumIntrospection) {
    if (!enumIntrospection.enumValues) {
      const enumIntrospectionStr = inspect(enumIntrospection);
      throw new Error(
        `Introspection result missing enumValues: ${enumIntrospectionStr}.`,
      );
    }

    return new GraphQLEnumType({
      name: enumIntrospection.name,
      description: enumIntrospection.description,
      values: keyValMap(
        enumIntrospection.enumValues,
        (valueIntrospection) => valueIntrospection.name,
        (valueIntrospection) => ({
          description: valueIntrospection.description,
          deprecationReason: valueIntrospection.deprecationReason,
        }),
      ),
    });
  }

  function buildInputObjectDef(inputObjectIntrospection) {
    if (!inputObjectIntrospection.inputFields) {
      const inputObjectIntrospectionStr = inspect(inputObjectIntrospection);
      throw new Error(
        `Introspection result missing inputFields: ${inputObjectIntrospectionStr}.`,
      );
    }

    return new GraphQLInputObjectType({
      name: inputObjectIntrospection.name,
      description: inputObjectIntrospection.description,
      fields: () => buildInputValueDefMap(inputObjectIntrospection.inputFields),
    });
  }

  function buildFieldDefMap(typeIntrospection) {
    if (!typeIntrospection.fields) {
      throw new Error(
        `Introspection result missing fields: ${inspect(typeIntrospection)}.`,
      );
    }

    return keyValMap(
      typeIntrospection.fields,
      (fieldIntrospection) => fieldIntrospection.name,
      buildField,
    );
  }

  function buildField(fieldIntrospection) {
    const type = getType(fieldIntrospection.type);

    if (!isOutputType(type)) {
      const typeStr = inspect(type);
      throw new Error(
        `Introspection must provide output type for fields, but received: ${typeStr}.`,
      );
    }

    if (!fieldIntrospection.args) {
      const fieldIntrospectionStr = inspect(fieldIntrospection);
      throw new Error(
        `Introspection result missing field args: ${fieldIntrospectionStr}.`,
      );
    }

    return {
      description: fieldIntrospection.description,
      deprecationReason: fieldIntrospection.deprecationReason,
      type,
      args: buildInputValueDefMap(fieldIntrospection.args),
    };
  }

  function buildInputValueDefMap(inputValueIntrospections) {
    return keyValMap(
      inputValueIntrospections,
      (inputValue) => inputValue.name,
      buildInputValue,
    );
  }

  function buildInputValue(inputValueIntrospection) {
    const type = getType(inputValueIntrospection.type);

    if (!isInputType(type)) {
      const typeStr = inspect(type);
      throw new Error(
        `Introspection must provide input type for arguments, but received: ${typeStr}.`,
      );
    }

    const defaultValue =
      inputValueIntrospection.defaultValue != null
        ? valueFromAST(parseValue(inputValueIntrospection.defaultValue), type)
        : undefined;
    return {
      description: inputValueIntrospection.description,
      type,
      defaultValue,
      deprecationReason: inputValueIntrospection.deprecationReason,
    };
  }

  function buildDirective(directiveIntrospection) {
    if (!directiveIntrospection.args) {
      const directiveIntrospectionStr = inspect(directiveIntrospection);
      throw new Error(
        `Introspection result missing directive args: ${directiveIntrospectionStr}.`,
      );
    }

    if (!directiveIntrospection.locations) {
      const directiveIntrospectionStr = inspect(directiveIntrospection);
      throw new Error(
        `Introspection result missing directive locations: ${directiveIntrospectionStr}.`,
      );
    }

    return new GraphQLDirective({
      name: directiveIntrospection.name,
      description: directiveIntrospection.description,
      isRepeatable: directiveIntrospection.isRepeatable,
      locations: directiveIntrospection.locations.slice(),
      args: buildInputValueDefMap(directiveIntrospection.args),
    });
  }
}

/**
 * Produces a new schema given an existing schema and a document which may
 * contain GraphQL type extensions and definitions. The original schema will
 * remain unaltered.
 *
 * Because a schema represents a graph of references, a schema cannot be
 * extended without effectively making an entire copy. We do not know until it's
 * too late if subgraphs remain unchanged.
 *
 * This algorithm copies the provided schema, applying extensions while
 * producing the copy. The original schema remains unaltered.
 */
function extendSchema(schema, documentAST, options) {
  assertSchema(schema);
  (documentAST != null && documentAST.kind === Kind.DOCUMENT) ||
    devAssert(false, 'Must provide valid Document AST.');

  if (
    (options === null || options === void 0 ? void 0 : options.assumeValid) !==
      true &&
    (options === null || options === void 0
      ? void 0
      : options.assumeValidSDL) !== true
  ) {
    assertValidSDLExtension(documentAST, schema);
  }

  const schemaConfig = schema.toConfig();
  const extendedConfig = extendSchemaImpl(schemaConfig, documentAST, options);
  return schemaConfig === extendedConfig
    ? schema
    : new GraphQLSchema(extendedConfig);
}
/**
 * @internal
 */

function extendSchemaImpl(schemaConfig, documentAST, options) {
  var _schemaDef, _schemaDef$descriptio, _schemaDef2, _options$assumeValid;

  // Collect the type definitions and extensions found in the document.
  const typeDefs = [];
  const typeExtensionsMap = Object.create(null); // New directives and types are separate because a directives and types can
  // have the same name. For example, a type named "skip".

  const directiveDefs = [];
  let schemaDef; // Schema extensions are collected which may add additional operation types.

  const schemaExtensions = [];

  for (const def of documentAST.definitions) {
    if (def.kind === Kind.SCHEMA_DEFINITION) {
      schemaDef = def;
    } else if (def.kind === Kind.SCHEMA_EXTENSION) {
      schemaExtensions.push(def);
    } else if (isTypeDefinitionNode(def)) {
      typeDefs.push(def);
    } else if (isTypeExtensionNode(def)) {
      const extendedTypeName = def.name.value;
      const existingTypeExtensions = typeExtensionsMap[extendedTypeName];
      typeExtensionsMap[extendedTypeName] = existingTypeExtensions
        ? existingTypeExtensions.concat([def])
        : [def];
    } else if (def.kind === Kind.DIRECTIVE_DEFINITION) {
      directiveDefs.push(def);
    }
  } // If this document contains no new types, extensions, or directives then
  // return the same unmodified GraphQLSchema instance.

  if (
    Object.keys(typeExtensionsMap).length === 0 &&
    typeDefs.length === 0 &&
    directiveDefs.length === 0 &&
    schemaExtensions.length === 0 &&
    schemaDef == null
  ) {
    return schemaConfig;
  }

  const typeMap = Object.create(null);

  for (const existingType of schemaConfig.types) {
    typeMap[existingType.name] = extendNamedType(existingType);
  }

  for (const typeNode of typeDefs) {
    var _stdTypeMap$name;

    const name = typeNode.name.value;
    typeMap[name] =
      (_stdTypeMap$name = stdTypeMap[name]) !== null &&
      _stdTypeMap$name !== void 0
        ? _stdTypeMap$name
        : buildType(typeNode);
  }

  const operationTypes = {
    // Get the extended root operation types.
    query: schemaConfig.query && replaceNamedType(schemaConfig.query),
    mutation: schemaConfig.mutation && replaceNamedType(schemaConfig.mutation),
    subscription:
      schemaConfig.subscription && replaceNamedType(schemaConfig.subscription),
    // Then, incorporate schema definition and all schema extensions.
    ...(schemaDef && getOperationTypes([schemaDef])),
    ...getOperationTypes(schemaExtensions),
  }; // Then produce and return a Schema config with these types.

  return {
    description:
      (_schemaDef = schemaDef) === null || _schemaDef === void 0
        ? void 0
        : (_schemaDef$descriptio = _schemaDef.description) === null ||
          _schemaDef$descriptio === void 0
        ? void 0
        : _schemaDef$descriptio.value,
    ...operationTypes,
    types: Object.values(typeMap),
    directives: [
      ...schemaConfig.directives.map(replaceDirective),
      ...directiveDefs.map(buildDirective),
    ],
    extensions: Object.create(null),
    astNode:
      (_schemaDef2 = schemaDef) !== null && _schemaDef2 !== void 0
        ? _schemaDef2
        : schemaConfig.astNode,
    extensionASTNodes: schemaConfig.extensionASTNodes.concat(schemaExtensions),
    assumeValid:
      (_options$assumeValid =
        options === null || options === void 0
          ? void 0
          : options.assumeValid) !== null && _options$assumeValid !== void 0
        ? _options$assumeValid
        : false,
  }; // Below are functions used for producing this schema that have closed over
  // this scope and have access to the schema, cache, and newly defined types.

  function replaceType(type) {
    if (isListType(type)) {
      // @ts-expect-error
      return new GraphQLList(replaceType(type.ofType));
    }

    if (isNonNullType(type)) {
      // @ts-expect-error
      return new GraphQLNonNull(replaceType(type.ofType));
    } // @ts-expect-error FIXME

    return replaceNamedType(type);
  }

  function replaceNamedType(type) {
    // Note: While this could make early assertions to get the correctly
    // typed values, that would throw immediately while type system
    // validation with validateSchema() will produce more actionable results.
    return typeMap[type.name];
  }

  function replaceDirective(directive) {
    const config = directive.toConfig();
    return new GraphQLDirective({
      ...config,
      args: mapValue(config.args, extendArg),
    });
  }

  function extendNamedType(type) {
    if (isIntrospectionType(type) || isSpecifiedScalarType(type)) {
      // Builtin types are not extended.
      return type;
    }

    if (isScalarType(type)) {
      return extendScalarType(type);
    }

    if (isObjectType(type)) {
      return extendObjectType(type);
    }

    if (isInterfaceType(type)) {
      return extendInterfaceType(type);
    }

    if (isUnionType(type)) {
      return extendUnionType(type);
    }

    if (isEnumType(type)) {
      return extendEnumType(type);
    }

    if (isInputObjectType(type)) {
      return extendInputObjectType(type);
    }
    /* c8 ignore next 3 */
    // Not reachable, all possible type definition nodes have been considered.

    invariant(false, 'Unexpected type: ' + inspect(type));
  }

  function extendInputObjectType(type) {
    var _typeExtensionsMap$co;

    const config = type.toConfig();
    const extensions =
      (_typeExtensionsMap$co = typeExtensionsMap[config.name]) !== null &&
      _typeExtensionsMap$co !== void 0
        ? _typeExtensionsMap$co
        : [];
    return new GraphQLInputObjectType({
      ...config,
      fields: () => ({
        ...mapValue(config.fields, (field) => ({
          ...field,
          type: replaceType(field.type),
        })),
        ...buildInputFieldMap(extensions),
      }),
      extensionASTNodes: config.extensionASTNodes.concat(extensions),
    });
  }

  function extendEnumType(type) {
    var _typeExtensionsMap$ty;

    const config = type.toConfig();
    const extensions =
      (_typeExtensionsMap$ty = typeExtensionsMap[type.name]) !== null &&
      _typeExtensionsMap$ty !== void 0
        ? _typeExtensionsMap$ty
        : [];
    return new GraphQLEnumType({
      ...config,
      values: { ...config.values, ...buildEnumValueMap(extensions) },
      extensionASTNodes: config.extensionASTNodes.concat(extensions),
    });
  }

  function extendScalarType(type) {
    var _typeExtensionsMap$co2;

    const config = type.toConfig();
    const extensions =
      (_typeExtensionsMap$co2 = typeExtensionsMap[config.name]) !== null &&
      _typeExtensionsMap$co2 !== void 0
        ? _typeExtensionsMap$co2
        : [];
    let specifiedByURL = config.specifiedByURL;

    for (const extensionNode of extensions) {
      var _getSpecifiedByURL;

      specifiedByURL =
        (_getSpecifiedByURL = getSpecifiedByURL(extensionNode)) !== null &&
        _getSpecifiedByURL !== void 0
          ? _getSpecifiedByURL
          : specifiedByURL;
    }

    return new GraphQLScalarType({
      ...config,
      specifiedByURL,
      extensionASTNodes: config.extensionASTNodes.concat(extensions),
    });
  }

  function extendObjectType(type) {
    var _typeExtensionsMap$co3;

    const config = type.toConfig();
    const extensions =
      (_typeExtensionsMap$co3 = typeExtensionsMap[config.name]) !== null &&
      _typeExtensionsMap$co3 !== void 0
        ? _typeExtensionsMap$co3
        : [];
    return new GraphQLObjectType({
      ...config,
      interfaces: () => [
        ...type.getInterfaces().map(replaceNamedType),
        ...buildInterfaces(extensions),
      ],
      fields: () => ({
        ...mapValue(config.fields, extendField),
        ...buildFieldMap(extensions),
      }),
      extensionASTNodes: config.extensionASTNodes.concat(extensions),
    });
  }

  function extendInterfaceType(type) {
    var _typeExtensionsMap$co4;

    const config = type.toConfig();
    const extensions =
      (_typeExtensionsMap$co4 = typeExtensionsMap[config.name]) !== null &&
      _typeExtensionsMap$co4 !== void 0
        ? _typeExtensionsMap$co4
        : [];
    return new GraphQLInterfaceType({
      ...config,
      interfaces: () => [
        ...type.getInterfaces().map(replaceNamedType),
        ...buildInterfaces(extensions),
      ],
      fields: () => ({
        ...mapValue(config.fields, extendField),
        ...buildFieldMap(extensions),
      }),
      extensionASTNodes: config.extensionASTNodes.concat(extensions),
    });
  }

  function extendUnionType(type) {
    var _typeExtensionsMap$co5;

    const config = type.toConfig();
    const extensions =
      (_typeExtensionsMap$co5 = typeExtensionsMap[config.name]) !== null &&
      _typeExtensionsMap$co5 !== void 0
        ? _typeExtensionsMap$co5
        : [];
    return new GraphQLUnionType({
      ...config,
      types: () => [
        ...type.getTypes().map(replaceNamedType),
        ...buildUnionTypes(extensions),
      ],
      extensionASTNodes: config.extensionASTNodes.concat(extensions),
    });
  }

  function extendField(field) {
    return {
      ...field,
      type: replaceType(field.type),
      args: field.args && mapValue(field.args, extendArg),
    };
  }

  function extendArg(arg) {
    return { ...arg, type: replaceType(arg.type) };
  }

  function getOperationTypes(nodes) {
    const opTypes = {};

    for (const node of nodes) {
      var _node$operationTypes;

      // FIXME: https://github.com/graphql/graphql-js/issues/2203
      const operationTypesNodes =
        /* c8 ignore next */
        (_node$operationTypes = node.operationTypes) !== null &&
        _node$operationTypes !== void 0
          ? _node$operationTypes
          : [];

      for (const operationType of operationTypesNodes) {
        // Note: While this could make early assertions to get the correctly
        // typed values below, that would throw immediately while type system
        // validation with validateSchema() will produce more actionable results.
        // @ts-expect-error
        opTypes[operationType.operation] = getNamedType(operationType.type);
      }
    }

    return opTypes;
  }

  function getNamedType(node) {
    var _stdTypeMap$name2;

    const name = node.name.value;
    const type =
      (_stdTypeMap$name2 = stdTypeMap[name]) !== null &&
      _stdTypeMap$name2 !== void 0
        ? _stdTypeMap$name2
        : typeMap[name];

    if (type === undefined) {
      throw new Error(`Unknown type: "${name}".`);
    }

    return type;
  }

  function getWrappedType(node) {
    if (node.kind === Kind.LIST_TYPE) {
      return new GraphQLList(getWrappedType(node.type));
    }

    if (node.kind === Kind.NON_NULL_TYPE) {
      return new GraphQLNonNull(getWrappedType(node.type));
    }

    return getNamedType(node);
  }

  function buildDirective(node) {
    var _node$description;

    return new GraphQLDirective({
      name: node.name.value,
      description:
        (_node$description = node.description) === null ||
        _node$description === void 0
          ? void 0
          : _node$description.value,
      // @ts-expect-error
      locations: node.locations.map(({ value }) => value),
      isRepeatable: node.repeatable,
      args: buildArgumentMap(node.arguments),
      astNode: node,
    });
  }

  function buildFieldMap(nodes) {
    const fieldConfigMap = Object.create(null);

    for (const node of nodes) {
      var _node$fields;

      // FIXME: https://github.com/graphql/graphql-js/issues/2203
      const nodeFields =
        /* c8 ignore next */
        (_node$fields = node.fields) !== null && _node$fields !== void 0
          ? _node$fields
          : [];

      for (const field of nodeFields) {
        var _field$description;

        fieldConfigMap[field.name.value] = {
          // Note: While this could make assertions to get the correctly typed
          // value, that would throw immediately while type system validation
          // with validateSchema() will produce more actionable results.
          type: getWrappedType(field.type),
          description:
            (_field$description = field.description) === null ||
            _field$description === void 0
              ? void 0
              : _field$description.value,
          args: buildArgumentMap(field.arguments),
          deprecationReason: getDeprecationReason(field),
          astNode: field,
        };
      }
    }

    return fieldConfigMap;
  }

  function buildArgumentMap(args) {
    // FIXME: https://github.com/graphql/graphql-js/issues/2203
    const argsNodes =
      /* c8 ignore next */
      args !== null && args !== void 0 ? args : [];
    const argConfigMap = Object.create(null);

    for (const arg of argsNodes) {
      var _arg$description;

      // Note: While this could make assertions to get the correctly typed
      // value, that would throw immediately while type system validation
      // with validateSchema() will produce more actionable results.
      const type = getWrappedType(arg.type);
      argConfigMap[arg.name.value] = {
        type,
        description:
          (_arg$description = arg.description) === null ||
          _arg$description === void 0
            ? void 0
            : _arg$description.value,
        defaultValue: valueFromAST(arg.defaultValue, type),
        deprecationReason: getDeprecationReason(arg),
        astNode: arg,
      };
    }

    return argConfigMap;
  }

  function buildInputFieldMap(nodes) {
    const inputFieldMap = Object.create(null);

    for (const node of nodes) {
      var _node$fields2;

      // FIXME: https://github.com/graphql/graphql-js/issues/2203
      const fieldsNodes =
        /* c8 ignore next */
        (_node$fields2 = node.fields) !== null && _node$fields2 !== void 0
          ? _node$fields2
          : [];

      for (const field of fieldsNodes) {
        var _field$description2;

        // Note: While this could make assertions to get the correctly typed
        // value, that would throw immediately while type system validation
        // with validateSchema() will produce more actionable results.
        const type = getWrappedType(field.type);
        inputFieldMap[field.name.value] = {
          type,
          description:
            (_field$description2 = field.description) === null ||
            _field$description2 === void 0
              ? void 0
              : _field$description2.value,
          defaultValue: valueFromAST(field.defaultValue, type),
          deprecationReason: getDeprecationReason(field),
          astNode: field,
        };
      }
    }

    return inputFieldMap;
  }

  function buildEnumValueMap(nodes) {
    const enumValueMap = Object.create(null);

    for (const node of nodes) {
      var _node$values;

      // FIXME: https://github.com/graphql/graphql-js/issues/2203
      const valuesNodes =
        /* c8 ignore next */
        (_node$values = node.values) !== null && _node$values !== void 0
          ? _node$values
          : [];

      for (const value of valuesNodes) {
        var _value$description;

        enumValueMap[value.name.value] = {
          description:
            (_value$description = value.description) === null ||
            _value$description === void 0
              ? void 0
              : _value$description.value,
          deprecationReason: getDeprecationReason(value),
          astNode: value,
        };
      }
    }

    return enumValueMap;
  }

  function buildInterfaces(nodes) {
    // Note: While this could make assertions to get the correctly typed
    // values below, that would throw immediately while type system
    // validation with validateSchema() will produce more actionable results.
    // @ts-expect-error
    return nodes.flatMap(
      // FIXME: https://github.com/graphql/graphql-js/issues/2203
      (node) => {
        var _node$interfaces$map, _node$interfaces;

        return (
          /* c8 ignore next */
          (_node$interfaces$map =
            (_node$interfaces = node.interfaces) === null ||
            _node$interfaces === void 0
              ? void 0
              : _node$interfaces.map(getNamedType)) !== null &&
            _node$interfaces$map !== void 0
            ? _node$interfaces$map
            : []
        );
      },
    );
  }

  function buildUnionTypes(nodes) {
    // Note: While this could make assertions to get the correctly typed
    // values below, that would throw immediately while type system
    // validation with validateSchema() will produce more actionable results.
    // @ts-expect-error
    return nodes.flatMap(
      // FIXME: https://github.com/graphql/graphql-js/issues/2203
      (node) => {
        var _node$types$map, _node$types;

        return (
          /* c8 ignore next */
          (_node$types$map =
            (_node$types = node.types) === null || _node$types === void 0
              ? void 0
              : _node$types.map(getNamedType)) !== null &&
            _node$types$map !== void 0
            ? _node$types$map
            : []
        );
      },
    );
  }

  function buildType(astNode) {
    var _typeExtensionsMap$na;

    const name = astNode.name.value;
    const extensionASTNodes =
      (_typeExtensionsMap$na = typeExtensionsMap[name]) !== null &&
      _typeExtensionsMap$na !== void 0
        ? _typeExtensionsMap$na
        : [];

    switch (astNode.kind) {
      case Kind.OBJECT_TYPE_DEFINITION: {
        var _astNode$description;

        const allNodes = [astNode, ...extensionASTNodes];
        return new GraphQLObjectType({
          name,
          description:
            (_astNode$description = astNode.description) === null ||
            _astNode$description === void 0
              ? void 0
              : _astNode$description.value,
          interfaces: () => buildInterfaces(allNodes),
          fields: () => buildFieldMap(allNodes),
          astNode,
          extensionASTNodes,
        });
      }

      case Kind.INTERFACE_TYPE_DEFINITION: {
        var _astNode$description2;

        const allNodes = [astNode, ...extensionASTNodes];
        return new GraphQLInterfaceType({
          name,
          description:
            (_astNode$description2 = astNode.description) === null ||
            _astNode$description2 === void 0
              ? void 0
              : _astNode$description2.value,
          interfaces: () => buildInterfaces(allNodes),
          fields: () => buildFieldMap(allNodes),
          astNode,
          extensionASTNodes,
        });
      }

      case Kind.ENUM_TYPE_DEFINITION: {
        var _astNode$description3;

        const allNodes = [astNode, ...extensionASTNodes];
        return new GraphQLEnumType({
          name,
          description:
            (_astNode$description3 = astNode.description) === null ||
            _astNode$description3 === void 0
              ? void 0
              : _astNode$description3.value,
          values: buildEnumValueMap(allNodes),
          astNode,
          extensionASTNodes,
        });
      }

      case Kind.UNION_TYPE_DEFINITION: {
        var _astNode$description4;

        const allNodes = [astNode, ...extensionASTNodes];
        return new GraphQLUnionType({
          name,
          description:
            (_astNode$description4 = astNode.description) === null ||
            _astNode$description4 === void 0
              ? void 0
              : _astNode$description4.value,
          types: () => buildUnionTypes(allNodes),
          astNode,
          extensionASTNodes,
        });
      }

      case Kind.SCALAR_TYPE_DEFINITION: {
        var _astNode$description5;

        return new GraphQLScalarType({
          name,
          description:
            (_astNode$description5 = astNode.description) === null ||
            _astNode$description5 === void 0
              ? void 0
              : _astNode$description5.value,
          specifiedByURL: getSpecifiedByURL(astNode),
          astNode,
          extensionASTNodes,
        });
      }

      case Kind.INPUT_OBJECT_TYPE_DEFINITION: {
        var _astNode$description6;

        const allNodes = [astNode, ...extensionASTNodes];
        return new GraphQLInputObjectType({
          name,
          description:
            (_astNode$description6 = astNode.description) === null ||
            _astNode$description6 === void 0
              ? void 0
              : _astNode$description6.value,
          fields: () => buildInputFieldMap(allNodes),
          astNode,
          extensionASTNodes,
        });
      }
    }
  }
}
const stdTypeMap = keyMap(
  [...specifiedScalarTypes, ...introspectionTypes],
  (type) => type.name,
);
/**
 * Given a field or enum value node, returns the string value for the
 * deprecation reason.
 */

function getDeprecationReason(node) {
  const deprecated = getDirectiveValues(GraphQLDeprecatedDirective, node); // @ts-expect-error validated by `getDirectiveValues`

  return deprecated === null || deprecated === void 0
    ? void 0
    : deprecated.reason;
}
/**
 * Given a scalar node, returns the string value for the specifiedByURL.
 */

function getSpecifiedByURL(node) {
  const specifiedBy = getDirectiveValues(GraphQLSpecifiedByDirective, node); // @ts-expect-error validated by `getDirectiveValues`

  return specifiedBy === null || specifiedBy === void 0
    ? void 0
    : specifiedBy.url;
}

/**
 * This takes the ast of a schema document produced by the parse function in
 * src/language/parser.js.
 *
 * If no schema definition is provided, then it will look for types named Query,
 * Mutation and Subscription.
 *
 * Given that AST it constructs a GraphQLSchema. The resulting schema
 * has no resolve methods, so execution will use default resolvers.
 */
function buildASTSchema(documentAST, options) {
  (documentAST != null && documentAST.kind === Kind.DOCUMENT) ||
    devAssert(false, 'Must provide valid Document AST.');

  if (
    (options === null || options === void 0 ? void 0 : options.assumeValid) !==
      true &&
    (options === null || options === void 0
      ? void 0
      : options.assumeValidSDL) !== true
  ) {
    assertValidSDL(documentAST);
  }

  const emptySchemaConfig = {
    description: undefined,
    types: [],
    directives: [],
    extensions: Object.create(null),
    extensionASTNodes: [],
    assumeValid: false,
  };
  const config = extendSchemaImpl(emptySchemaConfig, documentAST, options);

  if (config.astNode == null) {
    for (const type of config.types) {
      switch (type.name) {
        // Note: While this could make early assertions to get the correctly
        // typed values below, that would throw immediately while type system
        // validation with validateSchema() will produce more actionable results.
        case 'Query':
          // @ts-expect-error validated in `validateSchema`
          config.query = type;
          break;

        case 'Mutation':
          // @ts-expect-error validated in `validateSchema`
          config.mutation = type;
          break;

        case 'Subscription':
          // @ts-expect-error validated in `validateSchema`
          config.subscription = type;
          break;
      }
    }
  }

  const directives = [
    ...config.directives, // If specified directives were not explicitly declared, add them.
    ...specifiedDirectives.filter((stdDirective) =>
      config.directives.every(
        (directive) => directive.name !== stdDirective.name,
      ),
    ),
  ];
  return new GraphQLSchema({ ...config, directives });
}
/**
 * A helper function to build a GraphQLSchema directly from a source
 * document.
 */

function buildSchema(source, options) {
  const document = parse$1(source, {
    noLocation:
      options === null || options === void 0 ? void 0 : options.noLocation,
    allowLegacyFragmentVariables:
      options === null || options === void 0
        ? void 0
        : options.allowLegacyFragmentVariables,
  });
  return buildASTSchema(document, {
    assumeValidSDL:
      options === null || options === void 0 ? void 0 : options.assumeValidSDL,
    assumeValid:
      options === null || options === void 0 ? void 0 : options.assumeValid,
  });
}

/**
 * Sort GraphQLSchema.
 *
 * This function returns a sorted copy of the given GraphQLSchema.
 */

function lexicographicSortSchema(schema) {
  const schemaConfig = schema.toConfig();
  const typeMap = keyValMap(
    sortByName(schemaConfig.types),
    (type) => type.name,
    sortNamedType,
  );
  return new GraphQLSchema({
    ...schemaConfig,
    types: Object.values(typeMap),
    directives: sortByName(schemaConfig.directives).map(sortDirective),
    query: replaceMaybeType(schemaConfig.query),
    mutation: replaceMaybeType(schemaConfig.mutation),
    subscription: replaceMaybeType(schemaConfig.subscription),
  });

  function replaceType(type) {
    if (isListType(type)) {
      // @ts-expect-error
      return new GraphQLList(replaceType(type.ofType));
    } else if (isNonNullType(type)) {
      // @ts-expect-error
      return new GraphQLNonNull(replaceType(type.ofType));
    } // @ts-expect-error FIXME: TS Conversion

    return replaceNamedType(type);
  }

  function replaceNamedType(type) {
    return typeMap[type.name];
  }

  function replaceMaybeType(maybeType) {
    return maybeType && replaceNamedType(maybeType);
  }

  function sortDirective(directive) {
    const config = directive.toConfig();
    return new GraphQLDirective({
      ...config,
      locations: sortBy(config.locations, (x) => x),
      args: sortArgs(config.args),
    });
  }

  function sortArgs(args) {
    return sortObjMap(args, (arg) => ({ ...arg, type: replaceType(arg.type) }));
  }

  function sortFields(fieldsMap) {
    return sortObjMap(fieldsMap, (field) => ({
      ...field,
      type: replaceType(field.type),
      args: field.args && sortArgs(field.args),
    }));
  }

  function sortInputFields(fieldsMap) {
    return sortObjMap(fieldsMap, (field) => ({
      ...field,
      type: replaceType(field.type),
    }));
  }

  function sortTypes(array) {
    return sortByName(array).map(replaceNamedType);
  }

  function sortNamedType(type) {
    if (isScalarType(type) || isIntrospectionType(type)) {
      return type;
    }

    if (isObjectType(type)) {
      const config = type.toConfig();
      return new GraphQLObjectType({
        ...config,
        interfaces: () => sortTypes(config.interfaces),
        fields: () => sortFields(config.fields),
      });
    }

    if (isInterfaceType(type)) {
      const config = type.toConfig();
      return new GraphQLInterfaceType({
        ...config,
        interfaces: () => sortTypes(config.interfaces),
        fields: () => sortFields(config.fields),
      });
    }

    if (isUnionType(type)) {
      const config = type.toConfig();
      return new GraphQLUnionType({
        ...config,
        types: () => sortTypes(config.types),
      });
    }

    if (isEnumType(type)) {
      const config = type.toConfig();
      return new GraphQLEnumType({
        ...config,
        values: sortObjMap(config.values, (value) => value),
      });
    }

    if (isInputObjectType(type)) {
      const config = type.toConfig();
      return new GraphQLInputObjectType({
        ...config,
        fields: () => sortInputFields(config.fields),
      });
    }
    /* c8 ignore next 3 */
    // Not reachable, all possible types have been considered.

    invariant(false, 'Unexpected type: ' + inspect(type));
  }
}

function sortObjMap(map, sortValueFn) {
  const sortedMap = Object.create(null);

  for (const key of Object.keys(map).sort(naturalCompare)) {
    sortedMap[key] = sortValueFn(map[key]);
  }

  return sortedMap;
}

function sortByName(array) {
  return sortBy(array, (obj) => obj.name);
}

function sortBy(array, mapToKey) {
  return array.slice().sort((obj1, obj2) => {
    const key1 = mapToKey(obj1);
    const key2 = mapToKey(obj2);
    return naturalCompare(key1, key2);
  });
}

function printSchema(schema) {
  return printFilteredSchema(
    schema,
    (n) => !isSpecifiedDirective(n),
    isDefinedType,
  );
}
function printIntrospectionSchema(schema) {
  return printFilteredSchema(schema, isSpecifiedDirective, isIntrospectionType);
}

function isDefinedType(type) {
  return !isSpecifiedScalarType(type) && !isIntrospectionType(type);
}

function printFilteredSchema(schema, directiveFilter, typeFilter) {
  const directives = schema.getDirectives().filter(directiveFilter);
  const types = Object.values(schema.getTypeMap()).filter(typeFilter);
  return [
    printSchemaDefinition(schema),
    ...directives.map((directive) => printDirective(directive)),
    ...types.map((type) => printType(type)),
  ]
    .filter(Boolean)
    .join('\n\n');
}

function printSchemaDefinition(schema) {
  if (schema.description == null && isSchemaOfCommonNames(schema)) {
    return;
  }

  const operationTypes = [];
  const queryType = schema.getQueryType();

  if (queryType) {
    operationTypes.push(`  query: ${queryType.name}`);
  }

  const mutationType = schema.getMutationType();

  if (mutationType) {
    operationTypes.push(`  mutation: ${mutationType.name}`);
  }

  const subscriptionType = schema.getSubscriptionType();

  if (subscriptionType) {
    operationTypes.push(`  subscription: ${subscriptionType.name}`);
  }

  return printDescription(schema) + `schema {\n${operationTypes.join('\n')}\n}`;
}
/**
 * GraphQL schema define root types for each type of operation. These types are
 * the same as any other type and can be named in any manner, however there is
 * a common naming convention:
 *
 * ```graphql
 *   schema {
 *     query: Query
 *     mutation: Mutation
 *     subscription: Subscription
 *   }
 * ```
 *
 * When using this naming convention, the schema description can be omitted.
 */

function isSchemaOfCommonNames(schema) {
  const queryType = schema.getQueryType();

  if (queryType && queryType.name !== 'Query') {
    return false;
  }

  const mutationType = schema.getMutationType();

  if (mutationType && mutationType.name !== 'Mutation') {
    return false;
  }

  const subscriptionType = schema.getSubscriptionType();

  if (subscriptionType && subscriptionType.name !== 'Subscription') {
    return false;
  }

  return true;
}

function printType(type) {
  if (isScalarType(type)) {
    return printScalar(type);
  }

  if (isObjectType(type)) {
    return printObject(type);
  }

  if (isInterfaceType(type)) {
    return printInterface(type);
  }

  if (isUnionType(type)) {
    return printUnion(type);
  }

  if (isEnumType(type)) {
    return printEnum(type);
  }

  if (isInputObjectType(type)) {
    return printInputObject(type);
  }
  /* c8 ignore next 3 */
  // Not reachable, all possible types have been considered.

  invariant(false, 'Unexpected type: ' + inspect(type));
}

function printScalar(type) {
  return (
    printDescription(type) + `scalar ${type.name}` + printSpecifiedByURL(type)
  );
}

function printImplementedInterfaces(type) {
  const interfaces = type.getInterfaces();
  return interfaces.length
    ? ' implements ' + interfaces.map((i) => i.name).join(' & ')
    : '';
}

function printObject(type) {
  return (
    printDescription(type) +
    `type ${type.name}` +
    printImplementedInterfaces(type) +
    printFields(type)
  );
}

function printInterface(type) {
  return (
    printDescription(type) +
    `interface ${type.name}` +
    printImplementedInterfaces(type) +
    printFields(type)
  );
}

function printUnion(type) {
  const types = type.getTypes();
  const possibleTypes = types.length ? ' = ' + types.join(' | ') : '';
  return printDescription(type) + 'union ' + type.name + possibleTypes;
}

function printEnum(type) {
  const values = type
    .getValues()
    .map(
      (value, i) =>
        printDescription(value, '  ', !i) +
        '  ' +
        value.name +
        printDeprecated(value.deprecationReason),
    );
  return printDescription(type) + `enum ${type.name}` + printBlock(values);
}

function printInputObject(type) {
  const fields = Object.values(type.getFields()).map(
    (f, i) => printDescription(f, '  ', !i) + '  ' + printInputValue(f),
  );
  return printDescription(type) + `input ${type.name}` + printBlock(fields);
}

function printFields(type) {
  const fields = Object.values(type.getFields()).map(
    (f, i) =>
      printDescription(f, '  ', !i) +
      '  ' +
      f.name +
      printArgs(f.args, '  ') +
      ': ' +
      String(f.type) +
      printDeprecated(f.deprecationReason),
  );
  return printBlock(fields);
}

function printBlock(items) {
  return items.length !== 0 ? ' {\n' + items.join('\n') + '\n}' : '';
}

function printArgs(args, indentation = '') {
  if (args.length === 0) {
    return '';
  } // If every arg does not have a description, print them on one line.

  if (args.every((arg) => !arg.description)) {
    return '(' + args.map(printInputValue).join(', ') + ')';
  }

  return (
    '(\n' +
    args
      .map(
        (arg, i) =>
          printDescription(arg, '  ' + indentation, !i) +
          '  ' +
          indentation +
          printInputValue(arg),
      )
      .join('\n') +
    '\n' +
    indentation +
    ')'
  );
}

function printInputValue(arg) {
  const defaultAST = astFromValue(arg.defaultValue, arg.type);
  let argDecl = arg.name + ': ' + String(arg.type);

  if (defaultAST) {
    argDecl += ` = ${print(defaultAST)}`;
  }

  return argDecl + printDeprecated(arg.deprecationReason);
}

function printDirective(directive) {
  return (
    printDescription(directive) +
    'directive @' +
    directive.name +
    printArgs(directive.args) +
    (directive.isRepeatable ? ' repeatable' : '') +
    ' on ' +
    directive.locations.join(' | ')
  );
}

function printDeprecated(reason) {
  if (reason == null) {
    return '';
  }

  if (reason !== DEFAULT_DEPRECATION_REASON) {
    const astValue = print({
      kind: Kind.STRING,
      value: reason,
    });
    return ` @deprecated(reason: ${astValue})`;
  }

  return ' @deprecated';
}

function printSpecifiedByURL(scalar) {
  if (scalar.specifiedByURL == null) {
    return '';
  }

  const astValue = print({
    kind: Kind.STRING,
    value: scalar.specifiedByURL,
  });
  return ` @specifiedBy(url: ${astValue})`;
}

function printDescription(def, indentation = '', firstInBlock = true) {
  const { description } = def;

  if (description == null) {
    return '';
  }

  const blockString = print({
    kind: Kind.STRING,
    value: description,
    block: isPrintableAsBlockString(description),
  });
  const prefix =
    indentation && !firstInBlock ? '\n' + indentation : indentation;
  return prefix + blockString.replace(/\n/g, '\n' + indentation) + '\n';
}

/**
 * Provided a collection of ASTs, presumably each from different files,
 * concatenate the ASTs together into batched AST, useful for validating many
 * GraphQL source files which together represent one conceptual application.
 */

function concatAST(documents) {
  const definitions = [];

  for (const doc of documents) {
    definitions.push(...doc.definitions);
  }

  return {
    kind: Kind.DOCUMENT,
    definitions,
  };
}

/**
 * separateOperations accepts a single AST document which may contain many
 * operations and fragments and returns a collection of AST documents each of
 * which contains a single operation as well the fragment definitions it
 * refers to.
 */

function separateOperations(documentAST) {
  const operations = [];
  const depGraph = Object.create(null); // Populate metadata and build a dependency graph.

  for (const definitionNode of documentAST.definitions) {
    switch (definitionNode.kind) {
      case Kind.OPERATION_DEFINITION:
        operations.push(definitionNode);
        break;

      case Kind.FRAGMENT_DEFINITION:
        depGraph[definitionNode.name.value] = collectDependencies(
          definitionNode.selectionSet,
        );
        break;
    }
  } // For each operation, produce a new synthesized AST which includes only what
  // is necessary for completing that operation.

  const separatedDocumentASTs = Object.create(null);

  for (const operation of operations) {
    const dependencies = new Set();

    for (const fragmentName of collectDependencies(operation.selectionSet)) {
      collectTransitiveDependencies(dependencies, depGraph, fragmentName);
    } // Provides the empty string for anonymous operations.

    const operationName = operation.name ? operation.name.value : ''; // The list of definition nodes to be included for this operation, sorted
    // to retain the same order as the original document.

    separatedDocumentASTs[operationName] = {
      kind: Kind.DOCUMENT,
      definitions: documentAST.definitions.filter(
        (node) =>
          node === operation ||
          (node.kind === Kind.FRAGMENT_DEFINITION &&
            dependencies.has(node.name.value)),
      ),
    };
  }

  return separatedDocumentASTs;
}

// From a dependency graph, collects a list of transitive dependencies by
// recursing through a dependency graph.
function collectTransitiveDependencies(collected, depGraph, fromName) {
  if (!collected.has(fromName)) {
    collected.add(fromName);
    const immediateDeps = depGraph[fromName];

    if (immediateDeps !== undefined) {
      for (const toName of immediateDeps) {
        collectTransitiveDependencies(collected, depGraph, toName);
      }
    }
  }
}

function collectDependencies(selectionSet) {
  const dependencies = [];
  visit(selectionSet, {
    FragmentSpread(node) {
      dependencies.push(node.name.value);
    },
  });
  return dependencies;
}

/**
 * Strips characters that are not significant to the validity or execution
 * of a GraphQL document:
 *   - UnicodeBOM
 *   - WhiteSpace
 *   - LineTerminator
 *   - Comment
 *   - Comma
 *   - BlockString indentation
 *
 * Note: It is required to have a delimiter character between neighboring
 * non-punctuator tokens and this function always uses single space as delimiter.
 *
 * It is guaranteed that both input and output documents if parsed would result
 * in the exact same AST except for nodes location.
 *
 * Warning: It is guaranteed that this function will always produce stable results.
 * However, it's not guaranteed that it will stay the same between different
 * releases due to bugfixes or changes in the GraphQL specification.
 *
 * Query example:
 *
 * ```graphql
 * query SomeQuery($foo: String!, $bar: String) {
 *   someField(foo: $foo, bar: $bar) {
 *     a
 *     b {
 *       c
 *       d
 *     }
 *   }
 * }
 * ```
 *
 * Becomes:
 *
 * ```graphql
 * query SomeQuery($foo:String!$bar:String){someField(foo:$foo bar:$bar){a b{c d}}}
 * ```
 *
 * SDL example:
 *
 * ```graphql
 * """
 * Type description
 * """
 * type Foo {
 *   """
 *   Field description
 *   """
 *   bar: String
 * }
 * ```
 *
 * Becomes:
 *
 * ```graphql
 * """Type description""" type Foo{"""Field description""" bar:String}
 * ```
 */

function stripIgnoredCharacters(source) {
  const sourceObj = isSource(source) ? source : new Source(source);
  const body = sourceObj.body;
  const lexer = new Lexer(sourceObj);
  let strippedBody = '';
  let wasLastAddedTokenNonPunctuator = false;

  while (lexer.advance().kind !== TokenKind.EOF) {
    const currentToken = lexer.token;
    const tokenKind = currentToken.kind;
    /**
     * Every two non-punctuator tokens should have space between them.
     * Also prevent case of non-punctuator token following by spread resulting
     * in invalid token (e.g. `1...` is invalid Float token).
     */

    const isNonPunctuator = !isPunctuatorTokenKind(currentToken.kind);

    if (wasLastAddedTokenNonPunctuator) {
      if (isNonPunctuator || currentToken.kind === TokenKind.SPREAD) {
        strippedBody += ' ';
      }
    }

    const tokenBody = body.slice(currentToken.start, currentToken.end);

    if (tokenKind === TokenKind.BLOCK_STRING) {
      strippedBody += printBlockString(currentToken.value, {
        minimize: true,
      });
    } else {
      strippedBody += tokenBody;
    }

    wasLastAddedTokenNonPunctuator = isNonPunctuator;
  }

  return strippedBody;
}

/* c8 ignore start */

/**
 * Upholds the spec rules about naming.
 * @deprecated Please use `assertName` instead. Will be removed in v17
 */

function assertValidName(name) {
  const error = isValidNameError(name);

  if (error) {
    throw error;
  }

  return name;
}
/**
 * Returns an Error if a name is invalid.
 * @deprecated Please use `assertName` instead. Will be removed in v17
 */

function isValidNameError(name) {
  typeof name === 'string' || devAssert(false, 'Expected name to be a string.');

  if (name.startsWith('__')) {
    return new GraphQLError(
      `Name "${name}" must not begin with "__", which is reserved by GraphQL introspection.`,
    );
  }

  try {
    assertName(name);
  } catch (error) {
    return error;
  }
}
/* c8 ignore stop */

var BreakingChangeType;

(function (BreakingChangeType) {
  BreakingChangeType['TYPE_REMOVED'] = 'TYPE_REMOVED';
  BreakingChangeType['TYPE_CHANGED_KIND'] = 'TYPE_CHANGED_KIND';
  BreakingChangeType['TYPE_REMOVED_FROM_UNION'] = 'TYPE_REMOVED_FROM_UNION';
  BreakingChangeType['VALUE_REMOVED_FROM_ENUM'] = 'VALUE_REMOVED_FROM_ENUM';
  BreakingChangeType['REQUIRED_INPUT_FIELD_ADDED'] =
    'REQUIRED_INPUT_FIELD_ADDED';
  BreakingChangeType['IMPLEMENTED_INTERFACE_REMOVED'] =
    'IMPLEMENTED_INTERFACE_REMOVED';
  BreakingChangeType['FIELD_REMOVED'] = 'FIELD_REMOVED';
  BreakingChangeType['FIELD_CHANGED_KIND'] = 'FIELD_CHANGED_KIND';
  BreakingChangeType['REQUIRED_ARG_ADDED'] = 'REQUIRED_ARG_ADDED';
  BreakingChangeType['ARG_REMOVED'] = 'ARG_REMOVED';
  BreakingChangeType['ARG_CHANGED_KIND'] = 'ARG_CHANGED_KIND';
  BreakingChangeType['DIRECTIVE_REMOVED'] = 'DIRECTIVE_REMOVED';
  BreakingChangeType['DIRECTIVE_ARG_REMOVED'] = 'DIRECTIVE_ARG_REMOVED';
  BreakingChangeType['REQUIRED_DIRECTIVE_ARG_ADDED'] =
    'REQUIRED_DIRECTIVE_ARG_ADDED';
  BreakingChangeType['DIRECTIVE_REPEATABLE_REMOVED'] =
    'DIRECTIVE_REPEATABLE_REMOVED';
  BreakingChangeType['DIRECTIVE_LOCATION_REMOVED'] =
    'DIRECTIVE_LOCATION_REMOVED';
})(BreakingChangeType || (BreakingChangeType = {}));
var DangerousChangeType;

(function (DangerousChangeType) {
  DangerousChangeType['VALUE_ADDED_TO_ENUM'] = 'VALUE_ADDED_TO_ENUM';
  DangerousChangeType['TYPE_ADDED_TO_UNION'] = 'TYPE_ADDED_TO_UNION';
  DangerousChangeType['OPTIONAL_INPUT_FIELD_ADDED'] =
    'OPTIONAL_INPUT_FIELD_ADDED';
  DangerousChangeType['OPTIONAL_ARG_ADDED'] = 'OPTIONAL_ARG_ADDED';
  DangerousChangeType['IMPLEMENTED_INTERFACE_ADDED'] =
    'IMPLEMENTED_INTERFACE_ADDED';
  DangerousChangeType['ARG_DEFAULT_VALUE_CHANGE'] = 'ARG_DEFAULT_VALUE_CHANGE';
})(DangerousChangeType || (DangerousChangeType = {}));

/**
 * Given two schemas, returns an Array containing descriptions of all the types
 * of breaking changes covered by the other functions down below.
 */
function findBreakingChanges(oldSchema, newSchema) {
  // @ts-expect-error
  return findSchemaChanges(oldSchema, newSchema).filter(
    (change) => change.type in BreakingChangeType,
  );
}
/**
 * Given two schemas, returns an Array containing descriptions of all the types
 * of potentially dangerous changes covered by the other functions down below.
 */

function findDangerousChanges(oldSchema, newSchema) {
  // @ts-expect-error
  return findSchemaChanges(oldSchema, newSchema).filter(
    (change) => change.type in DangerousChangeType,
  );
}

function findSchemaChanges(oldSchema, newSchema) {
  return [
    ...findTypeChanges(oldSchema, newSchema),
    ...findDirectiveChanges(oldSchema, newSchema),
  ];
}

function findDirectiveChanges(oldSchema, newSchema) {
  const schemaChanges = [];
  const directivesDiff = diff(
    oldSchema.getDirectives(),
    newSchema.getDirectives(),
  );

  for (const oldDirective of directivesDiff.removed) {
    schemaChanges.push({
      type: BreakingChangeType.DIRECTIVE_REMOVED,
      description: `${oldDirective.name} was removed.`,
    });
  }

  for (const [oldDirective, newDirective] of directivesDiff.persisted) {
    const argsDiff = diff(oldDirective.args, newDirective.args);

    for (const newArg of argsDiff.added) {
      if (isRequiredArgument(newArg)) {
        schemaChanges.push({
          type: BreakingChangeType.REQUIRED_DIRECTIVE_ARG_ADDED,
          description: `A required arg ${newArg.name} on directive ${oldDirective.name} was added.`,
        });
      }
    }

    for (const oldArg of argsDiff.removed) {
      schemaChanges.push({
        type: BreakingChangeType.DIRECTIVE_ARG_REMOVED,
        description: `${oldArg.name} was removed from ${oldDirective.name}.`,
      });
    }

    if (oldDirective.isRepeatable && !newDirective.isRepeatable) {
      schemaChanges.push({
        type: BreakingChangeType.DIRECTIVE_REPEATABLE_REMOVED,
        description: `Repeatable flag was removed from ${oldDirective.name}.`,
      });
    }

    for (const location of oldDirective.locations) {
      if (!newDirective.locations.includes(location)) {
        schemaChanges.push({
          type: BreakingChangeType.DIRECTIVE_LOCATION_REMOVED,
          description: `${location} was removed from ${oldDirective.name}.`,
        });
      }
    }
  }

  return schemaChanges;
}

function findTypeChanges(oldSchema, newSchema) {
  const schemaChanges = [];
  const typesDiff = diff(
    Object.values(oldSchema.getTypeMap()),
    Object.values(newSchema.getTypeMap()),
  );

  for (const oldType of typesDiff.removed) {
    schemaChanges.push({
      type: BreakingChangeType.TYPE_REMOVED,
      description: isSpecifiedScalarType(oldType)
        ? `Standard scalar ${oldType.name} was removed because it is not referenced anymore.`
        : `${oldType.name} was removed.`,
    });
  }

  for (const [oldType, newType] of typesDiff.persisted) {
    if (isEnumType(oldType) && isEnumType(newType)) {
      schemaChanges.push(...findEnumTypeChanges(oldType, newType));
    } else if (isUnionType(oldType) && isUnionType(newType)) {
      schemaChanges.push(...findUnionTypeChanges(oldType, newType));
    } else if (isInputObjectType(oldType) && isInputObjectType(newType)) {
      schemaChanges.push(...findInputObjectTypeChanges(oldType, newType));
    } else if (isObjectType(oldType) && isObjectType(newType)) {
      schemaChanges.push(
        ...findFieldChanges(oldType, newType),
        ...findImplementedInterfacesChanges(oldType, newType),
      );
    } else if (isInterfaceType(oldType) && isInterfaceType(newType)) {
      schemaChanges.push(
        ...findFieldChanges(oldType, newType),
        ...findImplementedInterfacesChanges(oldType, newType),
      );
    } else if (oldType.constructor !== newType.constructor) {
      schemaChanges.push({
        type: BreakingChangeType.TYPE_CHANGED_KIND,
        description:
          `${oldType.name} changed from ` +
          `${typeKindName(oldType)} to ${typeKindName(newType)}.`,
      });
    }
  }

  return schemaChanges;
}

function findInputObjectTypeChanges(oldType, newType) {
  const schemaChanges = [];
  const fieldsDiff = diff(
    Object.values(oldType.getFields()),
    Object.values(newType.getFields()),
  );

  for (const newField of fieldsDiff.added) {
    if (isRequiredInputField(newField)) {
      schemaChanges.push({
        type: BreakingChangeType.REQUIRED_INPUT_FIELD_ADDED,
        description: `A required field ${newField.name} on input type ${oldType.name} was added.`,
      });
    } else {
      schemaChanges.push({
        type: DangerousChangeType.OPTIONAL_INPUT_FIELD_ADDED,
        description: `An optional field ${newField.name} on input type ${oldType.name} was added.`,
      });
    }
  }

  for (const oldField of fieldsDiff.removed) {
    schemaChanges.push({
      type: BreakingChangeType.FIELD_REMOVED,
      description: `${oldType.name}.${oldField.name} was removed.`,
    });
  }

  for (const [oldField, newField] of fieldsDiff.persisted) {
    const isSafe = isChangeSafeForInputObjectFieldOrFieldArg(
      oldField.type,
      newField.type,
    );

    if (!isSafe) {
      schemaChanges.push({
        type: BreakingChangeType.FIELD_CHANGED_KIND,
        description:
          `${oldType.name}.${oldField.name} changed type from ` +
          `${String(oldField.type)} to ${String(newField.type)}.`,
      });
    }
  }

  return schemaChanges;
}

function findUnionTypeChanges(oldType, newType) {
  const schemaChanges = [];
  const possibleTypesDiff = diff(oldType.getTypes(), newType.getTypes());

  for (const newPossibleType of possibleTypesDiff.added) {
    schemaChanges.push({
      type: DangerousChangeType.TYPE_ADDED_TO_UNION,
      description: `${newPossibleType.name} was added to union type ${oldType.name}.`,
    });
  }

  for (const oldPossibleType of possibleTypesDiff.removed) {
    schemaChanges.push({
      type: BreakingChangeType.TYPE_REMOVED_FROM_UNION,
      description: `${oldPossibleType.name} was removed from union type ${oldType.name}.`,
    });
  }

  return schemaChanges;
}

function findEnumTypeChanges(oldType, newType) {
  const schemaChanges = [];
  const valuesDiff = diff(oldType.getValues(), newType.getValues());

  for (const newValue of valuesDiff.added) {
    schemaChanges.push({
      type: DangerousChangeType.VALUE_ADDED_TO_ENUM,
      description: `${newValue.name} was added to enum type ${oldType.name}.`,
    });
  }

  for (const oldValue of valuesDiff.removed) {
    schemaChanges.push({
      type: BreakingChangeType.VALUE_REMOVED_FROM_ENUM,
      description: `${oldValue.name} was removed from enum type ${oldType.name}.`,
    });
  }

  return schemaChanges;
}

function findImplementedInterfacesChanges(oldType, newType) {
  const schemaChanges = [];
  const interfacesDiff = diff(oldType.getInterfaces(), newType.getInterfaces());

  for (const newInterface of interfacesDiff.added) {
    schemaChanges.push({
      type: DangerousChangeType.IMPLEMENTED_INTERFACE_ADDED,
      description: `${newInterface.name} added to interfaces implemented by ${oldType.name}.`,
    });
  }

  for (const oldInterface of interfacesDiff.removed) {
    schemaChanges.push({
      type: BreakingChangeType.IMPLEMENTED_INTERFACE_REMOVED,
      description: `${oldType.name} no longer implements interface ${oldInterface.name}.`,
    });
  }

  return schemaChanges;
}

function findFieldChanges(oldType, newType) {
  const schemaChanges = [];
  const fieldsDiff = diff(
    Object.values(oldType.getFields()),
    Object.values(newType.getFields()),
  );

  for (const oldField of fieldsDiff.removed) {
    schemaChanges.push({
      type: BreakingChangeType.FIELD_REMOVED,
      description: `${oldType.name}.${oldField.name} was removed.`,
    });
  }

  for (const [oldField, newField] of fieldsDiff.persisted) {
    schemaChanges.push(...findArgChanges(oldType, oldField, newField));
    const isSafe = isChangeSafeForObjectOrInterfaceField(
      oldField.type,
      newField.type,
    );

    if (!isSafe) {
      schemaChanges.push({
        type: BreakingChangeType.FIELD_CHANGED_KIND,
        description:
          `${oldType.name}.${oldField.name} changed type from ` +
          `${String(oldField.type)} to ${String(newField.type)}.`,
      });
    }
  }

  return schemaChanges;
}

function findArgChanges(oldType, oldField, newField) {
  const schemaChanges = [];
  const argsDiff = diff(oldField.args, newField.args);

  for (const oldArg of argsDiff.removed) {
    schemaChanges.push({
      type: BreakingChangeType.ARG_REMOVED,
      description: `${oldType.name}.${oldField.name} arg ${oldArg.name} was removed.`,
    });
  }

  for (const [oldArg, newArg] of argsDiff.persisted) {
    const isSafe = isChangeSafeForInputObjectFieldOrFieldArg(
      oldArg.type,
      newArg.type,
    );

    if (!isSafe) {
      schemaChanges.push({
        type: BreakingChangeType.ARG_CHANGED_KIND,
        description:
          `${oldType.name}.${oldField.name} arg ${oldArg.name} has changed type from ` +
          `${String(oldArg.type)} to ${String(newArg.type)}.`,
      });
    } else if (oldArg.defaultValue !== undefined) {
      if (newArg.defaultValue === undefined) {
        schemaChanges.push({
          type: DangerousChangeType.ARG_DEFAULT_VALUE_CHANGE,
          description: `${oldType.name}.${oldField.name} arg ${oldArg.name} defaultValue was removed.`,
        });
      } else {
        // Since we looking only for client's observable changes we should
        // compare default values in the same representation as they are
        // represented inside introspection.
        const oldValueStr = stringifyValue(oldArg.defaultValue, oldArg.type);
        const newValueStr = stringifyValue(newArg.defaultValue, newArg.type);

        if (oldValueStr !== newValueStr) {
          schemaChanges.push({
            type: DangerousChangeType.ARG_DEFAULT_VALUE_CHANGE,
            description: `${oldType.name}.${oldField.name} arg ${oldArg.name} has changed defaultValue from ${oldValueStr} to ${newValueStr}.`,
          });
        }
      }
    }
  }

  for (const newArg of argsDiff.added) {
    if (isRequiredArgument(newArg)) {
      schemaChanges.push({
        type: BreakingChangeType.REQUIRED_ARG_ADDED,
        description: `A required arg ${newArg.name} on ${oldType.name}.${oldField.name} was added.`,
      });
    } else {
      schemaChanges.push({
        type: DangerousChangeType.OPTIONAL_ARG_ADDED,
        description: `An optional arg ${newArg.name} on ${oldType.name}.${oldField.name} was added.`,
      });
    }
  }

  return schemaChanges;
}

function isChangeSafeForObjectOrInterfaceField(oldType, newType) {
  if (isListType(oldType)) {
    return (
      // if they're both lists, make sure the underlying types are compatible
      (isListType(newType) &&
        isChangeSafeForObjectOrInterfaceField(
          oldType.ofType,
          newType.ofType,
        )) || // moving from nullable to non-null of the same underlying type is safe
      (isNonNullType(newType) &&
        isChangeSafeForObjectOrInterfaceField(oldType, newType.ofType))
    );
  }

  if (isNonNullType(oldType)) {
    // if they're both non-null, make sure the underlying types are compatible
    return (
      isNonNullType(newType) &&
      isChangeSafeForObjectOrInterfaceField(oldType.ofType, newType.ofType)
    );
  }

  return (
    // if they're both named types, see if their names are equivalent
    (isNamedType(newType) && oldType.name === newType.name) || // moving from nullable to non-null of the same underlying type is safe
    (isNonNullType(newType) &&
      isChangeSafeForObjectOrInterfaceField(oldType, newType.ofType))
  );
}

function isChangeSafeForInputObjectFieldOrFieldArg(oldType, newType) {
  if (isListType(oldType)) {
    // if they're both lists, make sure the underlying types are compatible
    return (
      isListType(newType) &&
      isChangeSafeForInputObjectFieldOrFieldArg(oldType.ofType, newType.ofType)
    );
  }

  if (isNonNullType(oldType)) {
    return (
      // if they're both non-null, make sure the underlying types are
      // compatible
      (isNonNullType(newType) &&
        isChangeSafeForInputObjectFieldOrFieldArg(
          oldType.ofType,
          newType.ofType,
        )) || // moving from non-null to nullable of the same underlying type is safe
      (!isNonNullType(newType) &&
        isChangeSafeForInputObjectFieldOrFieldArg(oldType.ofType, newType))
    );
  } // if they're both named types, see if their names are equivalent

  return isNamedType(newType) && oldType.name === newType.name;
}

function typeKindName(type) {
  if (isScalarType(type)) {
    return 'a Scalar type';
  }

  if (isObjectType(type)) {
    return 'an Object type';
  }

  if (isInterfaceType(type)) {
    return 'an Interface type';
  }

  if (isUnionType(type)) {
    return 'a Union type';
  }

  if (isEnumType(type)) {
    return 'an Enum type';
  }

  if (isInputObjectType(type)) {
    return 'an Input type';
  }
  /* c8 ignore next 3 */
  // Not reachable, all possible types have been considered.

  invariant(false, 'Unexpected type: ' + inspect(type));
}

function stringifyValue(value, type) {
  const ast = astFromValue(value, type);
  ast != null || invariant(false);
  return print(sortValueNode(ast));
}

function diff(oldArray, newArray) {
  const added = [];
  const removed = [];
  const persisted = [];
  const oldMap = keyMap(oldArray, ({ name }) => name);
  const newMap = keyMap(newArray, ({ name }) => name);

  for (const oldItem of oldArray) {
    const newItem = newMap[oldItem.name];

    if (newItem === undefined) {
      removed.push(oldItem);
    } else {
      persisted.push([oldItem, newItem]);
    }
  }

  for (const newItem of newArray) {
    if (oldMap[newItem.name] === undefined) {
      added.push(newItem);
    }
  }

  return {
    added,
    persisted,
    removed,
  };
}

/**
 * GraphQL.js provides a reference implementation for the GraphQL specification
 * but is also a useful utility for operating on GraphQL files and building
 * sophisticated tools.
 *
 * This primary module exports a general purpose function for fulfilling all
 * steps of the GraphQL specification in a single operation, but also includes
 * utilities for every part of the GraphQL specification:
 *
 *   - Parsing the GraphQL language.
 *   - Building a GraphQL type schema.
 *   - Validating a GraphQL request against a type schema.
 *   - Executing a GraphQL request against a type schema.
 *
 * This also includes utility functions for operating on GraphQL types and
 * GraphQL documents to facilitate building tools.
 *
 * You may also import from each sub-directory directly. For example, the
 * following two import statements are equivalent:
 *
 * ```ts
 * import { parse } from 'graphql';
 * import { parse } from 'graphql/language';
 * ```
 *
 * @packageDocumentation
 */
// The GraphQL.js version info.

var graphql = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BREAK: BREAK,
    get BreakingChangeType () { return BreakingChangeType; },
    DEFAULT_DEPRECATION_REASON: DEFAULT_DEPRECATION_REASON,
    get DangerousChangeType () { return DangerousChangeType; },
    get DirectiveLocation () { return DirectiveLocation; },
    ExecutableDefinitionsRule: ExecutableDefinitionsRule,
    FieldsOnCorrectTypeRule: FieldsOnCorrectTypeRule,
    FragmentsOnCompositeTypesRule: FragmentsOnCompositeTypesRule,
    GRAPHQL_MAX_INT: GRAPHQL_MAX_INT,
    GRAPHQL_MIN_INT: GRAPHQL_MIN_INT,
    GraphQLBoolean: GraphQLBoolean,
    GraphQLDeprecatedDirective: GraphQLDeprecatedDirective,
    GraphQLDirective: GraphQLDirective,
    GraphQLEnumType: GraphQLEnumType,
    GraphQLError: GraphQLError,
    GraphQLFloat: GraphQLFloat,
    GraphQLID: GraphQLID,
    GraphQLIncludeDirective: GraphQLIncludeDirective,
    GraphQLInputObjectType: GraphQLInputObjectType,
    GraphQLInt: GraphQLInt,
    GraphQLInterfaceType: GraphQLInterfaceType,
    GraphQLList: GraphQLList,
    GraphQLNonNull: GraphQLNonNull,
    GraphQLObjectType: GraphQLObjectType,
    GraphQLScalarType: GraphQLScalarType,
    GraphQLSchema: GraphQLSchema,
    GraphQLSkipDirective: GraphQLSkipDirective,
    GraphQLSpecifiedByDirective: GraphQLSpecifiedByDirective,
    GraphQLString: GraphQLString,
    GraphQLUnionType: GraphQLUnionType,
    get Kind () { return Kind; },
    KnownArgumentNamesRule: KnownArgumentNamesRule,
    KnownDirectivesRule: KnownDirectivesRule,
    KnownFragmentNamesRule: KnownFragmentNamesRule,
    KnownTypeNamesRule: KnownTypeNamesRule,
    Lexer: Lexer,
    Location: Location,
    LoneAnonymousOperationRule: LoneAnonymousOperationRule,
    LoneSchemaDefinitionRule: LoneSchemaDefinitionRule,
    NoDeprecatedCustomRule: NoDeprecatedCustomRule,
    NoFragmentCyclesRule: NoFragmentCyclesRule,
    NoSchemaIntrospectionCustomRule: NoSchemaIntrospectionCustomRule,
    NoUndefinedVariablesRule: NoUndefinedVariablesRule,
    NoUnusedFragmentsRule: NoUnusedFragmentsRule,
    NoUnusedVariablesRule: NoUnusedVariablesRule,
    get OperationTypeNode () { return OperationTypeNode; },
    OverlappingFieldsCanBeMergedRule: OverlappingFieldsCanBeMergedRule,
    PossibleFragmentSpreadsRule: PossibleFragmentSpreadsRule,
    PossibleTypeExtensionsRule: PossibleTypeExtensionsRule,
    ProvidedRequiredArgumentsRule: ProvidedRequiredArgumentsRule,
    ScalarLeafsRule: ScalarLeafsRule,
    SchemaMetaFieldDef: SchemaMetaFieldDef,
    SingleFieldSubscriptionsRule: SingleFieldSubscriptionsRule,
    Source: Source,
    Token: Token,
    get TokenKind () { return TokenKind; },
    TypeInfo: TypeInfo,
    get TypeKind () { return TypeKind; },
    TypeMetaFieldDef: TypeMetaFieldDef,
    TypeNameMetaFieldDef: TypeNameMetaFieldDef,
    UniqueArgumentDefinitionNamesRule: UniqueArgumentDefinitionNamesRule,
    UniqueArgumentNamesRule: UniqueArgumentNamesRule,
    UniqueDirectiveNamesRule: UniqueDirectiveNamesRule,
    UniqueDirectivesPerLocationRule: UniqueDirectivesPerLocationRule,
    UniqueEnumValueNamesRule: UniqueEnumValueNamesRule,
    UniqueFieldDefinitionNamesRule: UniqueFieldDefinitionNamesRule,
    UniqueFragmentNamesRule: UniqueFragmentNamesRule,
    UniqueInputFieldNamesRule: UniqueInputFieldNamesRule,
    UniqueOperationNamesRule: UniqueOperationNamesRule,
    UniqueOperationTypesRule: UniqueOperationTypesRule,
    UniqueTypeNamesRule: UniqueTypeNamesRule,
    UniqueVariableNamesRule: UniqueVariableNamesRule,
    ValidationContext: ValidationContext,
    ValuesOfCorrectTypeRule: ValuesOfCorrectTypeRule,
    VariablesAreInputTypesRule: VariablesAreInputTypesRule,
    VariablesInAllowedPositionRule: VariablesInAllowedPositionRule,
    __Directive: __Directive,
    __DirectiveLocation: __DirectiveLocation,
    __EnumValue: __EnumValue,
    __Field: __Field,
    __InputValue: __InputValue,
    __Schema: __Schema,
    __Type: __Type,
    __TypeKind: __TypeKind,
    assertAbstractType: assertAbstractType,
    assertCompositeType: assertCompositeType,
    assertDirective: assertDirective,
    assertEnumType: assertEnumType,
    assertEnumValueName: assertEnumValueName,
    assertInputObjectType: assertInputObjectType,
    assertInputType: assertInputType,
    assertInterfaceType: assertInterfaceType,
    assertLeafType: assertLeafType,
    assertListType: assertListType,
    assertName: assertName,
    assertNamedType: assertNamedType,
    assertNonNullType: assertNonNullType,
    assertNullableType: assertNullableType,
    assertObjectType: assertObjectType,
    assertOutputType: assertOutputType,
    assertScalarType: assertScalarType,
    assertSchema: assertSchema,
    assertType: assertType,
    assertUnionType: assertUnionType,
    assertValidName: assertValidName,
    assertValidSchema: assertValidSchema,
    assertWrappingType: assertWrappingType,
    astFromValue: astFromValue,
    buildASTSchema: buildASTSchema,
    buildClientSchema: buildClientSchema,
    buildSchema: buildSchema,
    coerceInputValue: coerceInputValue,
    concatAST: concatAST,
    createSourceEventStream: createSourceEventStream,
    defaultFieldResolver: defaultFieldResolver,
    defaultTypeResolver: defaultTypeResolver,
    doTypesOverlap: doTypesOverlap,
    execute: execute,
    executeSync: executeSync,
    extendSchema: extendSchema,
    findBreakingChanges: findBreakingChanges,
    findDangerousChanges: findDangerousChanges,
    formatError: formatError,
    getArgumentValues: getArgumentValues,
    getDirectiveValues: getDirectiveValues,
    getEnterLeaveForKind: getEnterLeaveForKind,
    getIntrospectionQuery: getIntrospectionQuery,
    getLocation: getLocation,
    getNamedType: getNamedType,
    getNullableType: getNullableType,
    getOperationAST: getOperationAST,
    getOperationRootType: getOperationRootType,
    getVariableValues: getVariableValues,
    getVisitFn: getVisitFn,
    graphql: graphql$1,
    graphqlSync: graphqlSync,
    introspectionFromSchema: introspectionFromSchema,
    introspectionTypes: introspectionTypes,
    isAbstractType: isAbstractType,
    isCompositeType: isCompositeType,
    isConstValueNode: isConstValueNode,
    isDefinitionNode: isDefinitionNode,
    isDirective: isDirective,
    isEnumType: isEnumType,
    isEqualType: isEqualType,
    isExecutableDefinitionNode: isExecutableDefinitionNode,
    isInputObjectType: isInputObjectType,
    isInputType: isInputType,
    isInterfaceType: isInterfaceType,
    isIntrospectionType: isIntrospectionType,
    isLeafType: isLeafType,
    isListType: isListType,
    isNamedType: isNamedType,
    isNonNullType: isNonNullType,
    isNullableType: isNullableType,
    isObjectType: isObjectType,
    isOutputType: isOutputType,
    isRequiredArgument: isRequiredArgument,
    isRequiredInputField: isRequiredInputField,
    isScalarType: isScalarType,
    isSchema: isSchema,
    isSelectionNode: isSelectionNode,
    isSpecifiedDirective: isSpecifiedDirective,
    isSpecifiedScalarType: isSpecifiedScalarType,
    isType: isType,
    isTypeDefinitionNode: isTypeDefinitionNode,
    isTypeExtensionNode: isTypeExtensionNode,
    isTypeNode: isTypeNode,
    isTypeSubTypeOf: isTypeSubTypeOf,
    isTypeSystemDefinitionNode: isTypeSystemDefinitionNode,
    isTypeSystemExtensionNode: isTypeSystemExtensionNode,
    isUnionType: isUnionType,
    isValidNameError: isValidNameError,
    isValueNode: isValueNode,
    isWrappingType: isWrappingType,
    lexicographicSortSchema: lexicographicSortSchema,
    locatedError: locatedError,
    parse: parse$1,
    parseConstValue: parseConstValue,
    parseType: parseType,
    parseValue: parseValue,
    print: print,
    printError: printError,
    printIntrospectionSchema: printIntrospectionSchema,
    printLocation: printLocation,
    printSchema: printSchema,
    printSourceLocation: printSourceLocation,
    printType: printType,
    resolveObjMapThunk: resolveObjMapThunk,
    resolveReadonlyArrayThunk: resolveReadonlyArrayThunk,
    responsePathAsArray: pathToArray,
    separateOperations: separateOperations,
    specifiedDirectives: specifiedDirectives,
    specifiedRules: specifiedRules,
    specifiedScalarTypes: specifiedScalarTypes,
    stripIgnoredCharacters: stripIgnoredCharacters,
    subscribe: subscribe$1,
    syntaxError: syntaxError,
    typeFromAST: typeFromAST,
    validate: validate$1,
    validateSchema: validateSchema,
    valueFromAST: valueFromAST,
    valueFromASTUntyped: valueFromASTUntyped,
    version: version$1,
    versionInfo: versionInfo,
    visit: visit,
    visitInParallel: visitInParallel,
    visitWithTypeInfo: visitWithTypeInfo
});

var require$$0$1 = /*@__PURE__*/getAugmentedNamespace(graphql);

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.handleCaughtError = exports.createClientErrors = exports.createClientError = exports.createError = exports.createNamedErrorClass = exports.ErrorWithStatusCode = exports.ExtendedGraphQLErrors = void 0;
	const graphql_1 = require$$0$1;
	const enums_1 = enums;
	class ExtendedGraphQLErrors extends graphql_1.GraphQLError {
	    constructor(errors) {
	        super('Operation failed with one or more errors.');
	        this.extensions = {
	            code: 'EXTENDED_GRAPHQL_ERRORS',
	            errors,
	        };
	    }
	}
	exports.ExtendedGraphQLErrors = ExtendedGraphQLErrors;
	class ErrorWithStatusCode extends Error {
	    constructor(message, name, statusCode = enums_1.ErrorStatusCode.INTERNAL_SERVER_ERROR) {
	        super(message);
	        this.name = name || this.constructor.name;
	        this.statusCode = statusCode;
	    }
	}
	exports.ErrorWithStatusCode = ErrorWithStatusCode;
	/**
	 * Factory function to create a simple named Error class.
	 *
	 * @param className class name
	 * @param statusCode error status code
	 */
	const createNamedErrorClass = (className, errorStatusCode = enums_1.ErrorStatusCode.INTERNAL_SERVER_ERROR) => {
	    const NewErrorClass = class extends ErrorWithStatusCode {
	        constructor(message, name = className, statusCode = errorStatusCode) {
	            super(message);
	            this.name = name;
	            this.statusCode = statusCode;
	        }
	    };
	    // requires this for both class and instances to be properly named
	    Object.defineProperty(NewErrorClass, 'name', { value: className });
	    return NewErrorClass;
	};
	exports.createNamedErrorClass = createNamedErrorClass;
	/**
	 * Factory function to create one off named error for sentry. Named errors are useful for grouping errors in sentry and
	 * improving the way they are displayed in Slack.
	 *
	 * @param name class name
	 * @param message error message
	 * @param statusCode error status code
	 */
	const createError = (name, message, statusCode) => new ((0, exports.createNamedErrorClass)(name, statusCode))(message);
	exports.createError = createError;
	const createClientError = (description, code = 'BAD_REQUEST', className = 'ExtendedGraphQLErrors') => {
	    const error = new ExtendedGraphQLErrors([{ code, description }]);
	    // requires this for both class and instances to be properly named in Sentry
	    Object.defineProperty(error, 'name', { value: className });
	    return error;
	};
	exports.createClientError = createClientError;
	const createClientErrors = (errors, className = 'ExtendedGraphQLErrors') => {
	    const error = new ExtendedGraphQLErrors(errors);
	    // requires this for both class and instances to be properly named in Sentry
	    Object.defineProperty(error, 'name', { value: className });
	    return error;
	};
	exports.createClientErrors = createClientErrors;
	/**
	 * @param err error caught in a try/catch
	 * @param expectedErrorClass class expected to be in the error's prototype chain
	 * @param errorHandler function used to handle error if class expectedInPrototype is found in its prototype chain
	 * @throws UnexpectedPrimitiveErrorType is error is a primitive type.
	 */
	const handleCaughtError = (err, expectedErrorClass, errorHandler) => {
	    if (err instanceof expectedErrorClass) {
	        return errorHandler(err);
	    }
	    console.error((0, exports.createError)('UnexpectedErrorType', err));
	    throw (0, exports.createError)('UnexpectedErrorType', 'Please check logs for details.');
	};
	exports.handleCaughtError = handleCaughtError;
} (errors$1));

(function (exports) {
	var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
	    return (mod && mod.__esModule) ? mod : { "default": mod };
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.getNextMonthYear = exports.getPreviousMonthYear = exports.isDateInMonthYearRange = exports.isTaxYearTraditional = exports.subtractMonths = exports.addMonths = exports.getEndOfDay = exports.getFirstDayOfTheMonth = exports.getLastDayOfTheMonth = exports.getMonthLongAbbreviation = exports.getMonthShortAbbreviation = exports.placedInServiceDate = exports.isStandardFiscalYear = exports.getFirstDayOfPeriodByLastDay = exports.removeZeroPadding = exports.addZeroPadding = exports.isAfterInitialTaxDeadline = exports.toIsoDateString = exports.currentDateToIsoString = exports.getLastDayOfTheYear = exports.sameDateString = exports.isLeapYear = exports.upperBoundForTaxReturnFilingDate = exports.getEndOfYear = exports.getStartOfYear = exports.getEntryForTaxYear = exports.getEntryForCurrentTaxYear = exports.getYearFromStringDate = exports.isDateString = exports.isDatetime = exports.getDatetime = exports.getCurrentDatetime = exports.getCurrentYear = exports.getCurrentDateObject = exports.getCurrentDate = exports.longDateFormat = exports.americanDateFormat = exports.formatDate = exports.formatDayjs = exports.endOfFiscalYearDate = exports.getCurrentTaxYear = exports.getExtensionTaxDeadline = exports.getInitialTaxDeadline = exports.isYyyyMmDd = exports.dateToDayjs = void 0;
	const dayjs_1 = __importDefault(dayjs_minExports);
	const customParseFormat_1 = __importDefault(customParseFormatExports);
	const isLeapYear_1 = __importDefault(isLeapYearExports);
	const utc_1 = __importDefault(utcExports);
	const enums_1 = enums;
	const errors_1 = errors$1;
	dayjs_1.default.extend(utc_1.default);
	dayjs_1.default.extend(customParseFormat_1.default);
	dayjs_1.default.extend(isLeapYear_1.default);
	// Only avoids Saturday and Sunday. Not Holidays.
	const avoidWeekend = (date) => {
	    if (date.get('day') === enums_1.DayOfWeek.SATURDAY) {
	        return date.add(2, 'day');
	    }
	    if (date.get('day') === enums_1.DayOfWeek.SUNDAY) {
	        return date.add(1, 'day');
	    }
	    return date;
	};
	const dateToDayjs = (date) => (0, dayjs_1.default)(date).utc();
	exports.dateToDayjs = dateToDayjs;
	const isYyyyMmDd = (date) => (0, dayjs_1.default)(date, 'YYYY-MM-DD').isValid();
	exports.isYyyyMmDd = isYyyyMmDd;
	// Gets the standard tax deadline (usually april 15th)
	// Adds four months and 15 days to last day of period
	// Moves forward if that day happens to be on a weekend
	const getInitialTaxDeadline = (lastDayOfPeriod) => {
	    if (!(0, exports.isYyyyMmDd)(lastDayOfPeriod)) {
	        throw (0, errors_1.createError)('InvalidDate', `Input (${lastDayOfPeriod}) could not be parsed into a Dayjs object`);
	    }
	    const convertedLastDay = (0, exports.dateToDayjs)(lastDayOfPeriod);
	    const deadline = convertedLastDay.add(4, 'month');
	    const deadlineSetFifteen = deadline.set('date', 15).endOf('day');
	    switch (deadlineSetFifteen.day()) {
	        case enums_1.DayOfWeek.FRIDAY:
	        case enums_1.DayOfWeek.SATURDAY:
	            // Add 3 days if April 15 is Friday or Saturday -> Monday or Tuesday
	            return deadlineSetFifteen.add(3, 'day');
	        case enums_1.DayOfWeek.SUNDAY:
	            // Add 2 days if April 15 is Sunday -> Tuesday
	            return deadlineSetFifteen.add(2, 'day');
	        default:
	            return deadlineSetFifteen;
	    }
	};
	exports.getInitialTaxDeadline = getInitialTaxDeadline;
	// If lastDayOfPeriod is december 31st, we can assume the late tax deadline is October 15th
	// Otherwise, we have to calculate 10 months and 15 days into the future
	// If that date lands on a weekend, move it forward to a monday
	// TODO: Check if national holiday and move it even further if so
	/**
	 * @param lastDayOfPeriod
	 * lastDayOfPeriod should be in format: "YYYY-MM-DD"
	 */
	const getExtensionTaxDeadline = (lastDayOfPeriod) => {
	    const deadline = (0, exports.getInitialTaxDeadline)(lastDayOfPeriod).add(6, 'month');
	    return avoidWeekend(deadline);
	};
	exports.getExtensionTaxDeadline = getExtensionTaxDeadline;
	// The current tax year (i.e., previous fiscal year) is determined by the
	// following period:
	//
	// - If current date is after the extension tax deadline, the current tax year is the year of the first day of the period + 1
	// - If it is before, the current tax year is the year of the first date of the period
	// FYI- This does not work for short year. This simply subtracts 11 months days from the last day of period. Will need to update this
	// To accept first day of period as well - https://linear.app/neo-tax/issue/NEO-3439/update-the-getcurrenttaxyear-to-take-in-firstdayofperiod
	/**
	 * @param lastDayOfPeriod
	 * lastDayOfPeriod should be in format: "YYYY-MM-DD"
	 */
	const getCurrentTaxYear = (lastDayOfPeriod) => {
	    const firstDayOfPeriod = (0, exports.getFirstDayOfPeriodByLastDay)(lastDayOfPeriod);
	    const firstDayOfPeriodConverted = (0, exports.dateToDayjs)(firstDayOfPeriod);
	    const firstDayOfPeriodYear = firstDayOfPeriodConverted.get('year');
	    return firstDayOfPeriodYear;
	};
	exports.getCurrentTaxYear = getCurrentTaxYear;
	const endOfFiscalYearDate = (endOfFiscalYearMonth, year) => dayjs_1.default
	    .utc()
	    .set('month', endOfFiscalYearMonth - 1) // Dayjs 0-indexes month
	    .endOf('month')
	    .set('year', year)
	    .toDate();
	exports.endOfFiscalYearDate = endOfFiscalYearDate;
	// TODO: figure out where else this can be used in the code
	const formatDayjs = (date) => date.format('YYYY-MM-DD');
	exports.formatDayjs = formatDayjs;
	const formatDate = (date) => dayjs_1.default.utc(date).format('YYYY-MM-DD');
	exports.formatDate = formatDate;
	const americanDateFormat = (date, options = { zeroPadded: true }) => (0, exports.dateToDayjs)(date).format(options.zeroPadded ? 'MM/DD/YYYY' : 'M/D/YYYY');
	exports.americanDateFormat = americanDateFormat;
	const longDateFormat = (date) => (0, exports.dateToDayjs)(date).format('MMMM D, YYYY');
	exports.longDateFormat = longDateFormat;
	const getCurrentDate = () => (0, exports.formatDayjs)(dayjs_1.default.utc());
	exports.getCurrentDate = getCurrentDate;
	const getCurrentDateObject = () => dayjs_1.default.utc().toDate();
	exports.getCurrentDateObject = getCurrentDateObject;
	const getCurrentYear = () => dayjs_1.default.utc().year();
	exports.getCurrentYear = getCurrentYear;
	// If the time is in UTC, add a Z directly after the time without a space. Z is the zone designator for the zero UTC offset.
	// "09:30 UTC" is therefore represented as "09:30Z" or "T0930Z". "14:45:15 UTC" would be "14:45:15Z" or "T144515Z".
	const getCurrentDatetime = () => `${dayjs_1.default.utc().format('YYYYMMDDTHHmmssSSS')}Z`;
	exports.getCurrentDatetime = getCurrentDatetime;
	const getDatetime = (date) => `${dayjs_1.default.utc(date).format('YYYYMMDDTHHmmssSSS')}Z`;
	exports.getDatetime = getDatetime;
	const isDatetime = (date) => (0, dayjs_1.default)(date.substring(0, date.length - 1), 'YYYYMMDDTHHmmssSSS').isValid();
	exports.isDatetime = isDatetime;
	const isDateString = (date) => new Date(date).toString() != 'Invalid Date';
	exports.isDateString = isDateString;
	const getYearFromStringDate = (date) => {
	    if (!(0, exports.isDateString)(date)) {
	        throw (0, errors_1.createError)('InvalidDate', `Input (${date}) could not be parsed into a Date object`);
	    }
	    return new Date(date).getUTCFullYear();
	};
	exports.getYearFromStringDate = getYearFromStringDate;
	const getEntryForCurrentTaxYear = (entries, lastDayOfPeriod) => (0, exports.getEntryForTaxYear)(entries, (0, exports.getCurrentTaxYear)(lastDayOfPeriod));
	exports.getEntryForCurrentTaxYear = getEntryForCurrentTaxYear;
	const getEntryForTaxYear = (entries, year) => entries.find((e) => e.year === year) || null;
	exports.getEntryForTaxYear = getEntryForTaxYear;
	const getStartOfYear = (year) => `${year}-01-01`;
	exports.getStartOfYear = getStartOfYear;
	const getEndOfYear = (year) => `${year}-12-31`;
	exports.getEndOfYear = getEndOfYear;
	// Return whichever date is earlier: the current date, or Oct 17th for the `year` passed in
	const upperBoundForTaxReturnFilingDate = (lastDayOfPeriod) => {
	    const currentDate = dayjs_1.default.utc();
	    const toReturn = currentDate.isBefore((0, exports.getExtensionTaxDeadline)(lastDayOfPeriod))
	        ? currentDate
	        : (0, exports.getExtensionTaxDeadline)(lastDayOfPeriod);
	    return toReturn;
	};
	exports.upperBoundForTaxReturnFilingDate = upperBoundForTaxReturnFilingDate;
	const isLeapYear = (year) => (0, dayjs_1.default)(`${year}-01-01`).isLeapYear();
	exports.isLeapYear = isLeapYear;
	const sameDateString = (a, b) => (0, dayjs_1.default)(a).diff((0, dayjs_1.default)(b)) === 0;
	exports.sameDateString = sameDateString;
	const getLastDayOfTheYear = (date) => {
	    const lastDayOfTheYear = new Date(date); // to keep all other values the same
	    // december 31st
	    lastDayOfTheYear.setUTCMonth(11);
	    lastDayOfTheYear.setUTCDate(31);
	    return lastDayOfTheYear;
	};
	exports.getLastDayOfTheYear = getLastDayOfTheYear;
	const currentDateToIsoString = () => dayjs_1.default.utc().toDate().toISOString();
	exports.currentDateToIsoString = currentDateToIsoString;
	/**
	 * @param date Date object
	 * @returns String with date in ISO format (YYYY-MM-DD)
	 */
	const toIsoDateString = (date) => date.toISOString().split('T')[0];
	exports.toIsoDateString = toIsoDateString;
	/**
	 * @param date {Date} Defaults to current date if no value supplied
	 * @returns {boolean} True if date is after the initial tax deadline for the year, otherwise false
	 */
	const isAfterInitialTaxDeadline = (lastDayOfPeriod, date = new Date()) => {
	    const initialTaxDeadline = (0, exports.getInitialTaxDeadline)(lastDayOfPeriod);
	    return (0, dayjs_1.default)(date).isAfter(initialTaxDeadline);
	};
	exports.isAfterInitialTaxDeadline = isAfterInitialTaxDeadline;
	/**
	 * Takes a string and adds zero in the beginning if value is a single digit. This ensures months/days will always be two characters long.
	 * e.g '1' turns into '01'
	 * @param monthOrDay string representation of a month or day. Not zero indexed. (1-12 for month) (1-31 for day)
	 * @returns {string} returns value but adds zero padding if needed
	 */
	const addZeroPadding = (monthOrDay) => (0 + monthOrDay).slice(-2);
	exports.addZeroPadding = addZeroPadding;
	const removeZeroPadding = (zeroPaddedMonthOrDay) => parseInt(zeroPaddedMonthOrDay).toString();
	exports.removeZeroPadding = removeZeroPadding;
	// Works only when assuming a full 12 month fiscal year
	const getFirstDayOfPeriodByLastDay = (lastDayOfPeriod) => {
	    const lastDayConverted = (0, dayjs_1.default)(lastDayOfPeriod).utc();
	    return lastDayConverted.subtract(11, 'months').startOf('month').toISOString();
	};
	exports.getFirstDayOfPeriodByLastDay = getFirstDayOfPeriodByLastDay;
	// This function expects YYYY-MM-DD format because we save the date that way in our database
	const isStandardFiscalYear = (startDate, endDate) => {
	    if (!(0, exports.isYyyyMmDd)(startDate) || !(0, exports.isYyyyMmDd)(endDate)) {
	        throw (0, errors_1.createError)('InvalidDate', `Input (startDate: ${startDate} or endDate: ${endDate}) is not YYYY-MM-DD format.`);
	    }
	    return startDate.endsWith('01-01') && endDate.endsWith('12-31');
	};
	exports.isStandardFiscalYear = isStandardFiscalYear;
	// "placed in service date" refers to the date when an asset is ready and
	// available for use in its intended function or purpose. It is an important
	// concept for tax purposes as it determines when certain tax benefits and
	// deductions related to the asset begin.
	const placedInServiceDate = (endDate) => (0, exports.dateToDayjs)(endDate).subtract(6, 'months').toDate();
	exports.placedInServiceDate = placedInServiceDate;
	/**
	 * Get the 3-letter month abbreviation (Jan, Feb, ...)
	 * @param month - value of the month to get abbreviation for (range 0 ~ 11)
	 * @returns the month abbreviation
	 */
	const getMonthShortAbbreviation = (month) => {
	    const date = new Date();
	    date.setDate(1);
	    return Intl.DateTimeFormat('en-US', { month: 'short' }).format(date.setMonth(month));
	};
	exports.getMonthShortAbbreviation = getMonthShortAbbreviation;
	const getMonthLongAbbreviation = (month) => {
	    const date = new Date();
	    date.setDate(1);
	    return Intl.DateTimeFormat('en-US', { month: 'long' }).format(date.setMonth(month));
	};
	exports.getMonthLongAbbreviation = getMonthLongAbbreviation;
	const getLastDayOfTheMonth = (date) => {
	    const lastDayOfTheMonth = new Date(date); // to keep all other values the same
	    // set date to the 1st to prevent overflow, for example Jan 31st + 30 days = March 3rd.
	    lastDayOfTheMonth.setUTCDate(1);
	    lastDayOfTheMonth.setUTCMonth(date.getUTCMonth() + 1);
	    lastDayOfTheMonth.setUTCDate(0); // setting to 0 reverses by one day
	    return lastDayOfTheMonth;
	};
	exports.getLastDayOfTheMonth = getLastDayOfTheMonth;
	const getFirstDayOfTheMonth = (date) => {
	    const firstDayOfTheMonth = new Date(date); // to keep all other values the same
	    firstDayOfTheMonth.setUTCDate(1);
	    return firstDayOfTheMonth;
	};
	exports.getFirstDayOfTheMonth = getFirstDayOfTheMonth;
	const getEndOfDay = (date = new Date()) => {
	    const datetime = new Date(date);
	    datetime.setUTCHours(23, 59, 59, 999);
	    return datetime;
	};
	exports.getEndOfDay = getEndOfDay;
	const addMonths = (date, numberMonths) => {
	    let datejs = (0, dayjs_1.default)(date).utc();
	    datejs = datejs.add(numberMonths, 'month');
	    return datejs.format('YYYY-MM-DD');
	};
	exports.addMonths = addMonths;
	const subtractMonths = (date, monthCount) => (0, dayjs_1.default)(date).utc().subtract(monthCount, 'month').toDate();
	exports.subtractMonths = subtractMonths;
	const isTaxYearTraditional = ({ firstDayOfPeriod, lastDayOfPeriod, }) => {
	    const firstDay = (0, exports.dateToDayjs)(firstDayOfPeriod).utc();
	    const lastDay = (0, exports.dateToDayjs)(lastDayOfPeriod).utc();
	    return (firstDay.get('month') === 0 &&
	        firstDay.get('date') === 1 &&
	        lastDay.get('month') === 11 &&
	        lastDay.get('date') === 31);
	};
	exports.isTaxYearTraditional = isTaxYearTraditional;
	const isDateInMonthYearRange = (date, startMonthYear, endMonthYear) => {
	    const year = date.getUTCFullYear();
	    const month = date.getUTCMonth(); // JavaScript months are 0-indexed, and we use 0 indexed month in MonthYear
	    if (year < startMonthYear.year || year > endMonthYear.year) {
	        return false;
	    }
	    if (year === startMonthYear.year && month < startMonthYear.month) {
	        return false;
	    }
	    if (year === endMonthYear.year && month > endMonthYear.month) {
	        return false;
	    }
	    return true;
	};
	exports.isDateInMonthYearRange = isDateInMonthYearRange;
	const getPreviousMonthYear = (monthYear) => ({
	    month: monthYear.month === 0 ? 11 : monthYear.month - 1,
	    year: monthYear.month === 0 ? monthYear.year - 1 : monthYear.year,
	});
	exports.getPreviousMonthYear = getPreviousMonthYear;
	const getNextMonthYear = (monthYear) => ({
	    month: monthYear.month === 11 ? 0 : monthYear.month + 1,
	    year: monthYear.month === 11 ? monthYear.year + 1 : monthYear.year,
	});
	exports.getNextMonthYear = getNextMonthYear;
} (date_utils));

var organizationMembersExport_utils = {};

Object.defineProperty(organizationMembersExport_utils, "__esModule", { value: true });
organizationMembersExport_utils.organizationMembersFileName = void 0;
/**
 * Names an organization members export after the organization and the UTC date it was taken, so a
 * reviewer collecting these every quarter can tell two exports apart without opening them. UTC
 * matches the dates written inside the file, so the name and the contents never disagree.
 */
const organizationMembersFileName = (organizationName, exportedOn) => {
    const slug = organizationName
        // Decompose accents so `Société` slugs to `societe` rather than losing the letters to hyphens.
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'organization';
    return `${slug}-members-${exportedOn.toISOString().slice(0, 10)}.csv`;
};
organizationMembersExport_utils.organizationMembersFileName = organizationMembersFileName;

var monthYear_utils = {};

(function (exports) {
	var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
	    return (mod && mod.__esModule) ? mod : { "default": mod };
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.calculateEarliestAffectedCapitalizationMonth = exports.CapitalizationChangeReason = exports.getMonthYearPeriodSuperset = exports.dateStringToReadableMonthYearString = exports.applyCompletedEndMonthYearRule = exports.getMonthYearPeriodUsingDates = exports.getTaxYearMonthYearPeriod = exports.getCurrentFiscalYearMonthYearPeriod = exports.getEarliestAndLatestMonthYear = exports.formatTaxPeriod = exports.formatFiscalPeriod = exports.formatFiscalOrTaxPeriod = exports.getPeriodName = exports.getMonthPeriodName = exports.MONTH_PERIOD_NAMES = exports.getLastQuarterInPeriod = exports.formatMonthYearPeriod = exports.formatMonthYear = exports.calculateQuarter = exports.doPeriodsOverlap = exports.getMonthYearPeriodIntersection = exports.areSameMonthYearPeriod = exports.isPeriodFullYear = exports.getNumberOfMonthsInPeriod = exports.getNumMonthsBetweenInclusively = exports.areMonthYearEqual = exports.isMonthYearBefore = exports.minMonthYear = exports.maxMonthYear = exports.sameMonthYear = exports.getMonthName = exports.getMonthsBetweenAsc = exports.generateMonthYearInRange = exports.getLastDateStringOfMonthYear = exports.getLastDateOfMonthYear = exports.getFirstDateOfMonthYear = exports.getFirstDateStringOfMonthYear = exports.stringToMonthYear = exports.dateToMonthYearString = exports.toMonthYearString = exports.toMonthYearPeriod = exports.dateToMonthYear = exports.isMonthYearInRange = exports.isMonthYearAfterOrEqual = exports.isMonthYearBeforeOrEqual = exports.isMonthYear = void 0;
	const dayjs_1 = __importDefault(dayjs_minExports);
	const utc_1 = __importDefault(utcExports);
	const date_utils_1 = date_utils;
	const enums_1 = enums;
	const errors_1 = errors$1;
	dayjs_1.default.extend(utc_1.default);
	const isMonthYear = (monthYear) => {
	    if (!monthYear) {
	        return false;
	    }
	    const { month, year } = monthYear;
	    return typeof month === 'number' && typeof year === 'number';
	};
	exports.isMonthYear = isMonthYear;
	const isMonthYearBeforeOrEqual = (a, b) => a.year < b.year || (a.year === b.year && a.month <= b.month);
	exports.isMonthYearBeforeOrEqual = isMonthYearBeforeOrEqual;
	const isMonthYearAfterOrEqual = (a, b) => a.year > b.year || (a.year === b.year && a.month >= b.month);
	exports.isMonthYearAfterOrEqual = isMonthYearAfterOrEqual;
	/**
	 * Check whether the <month, year> is inclusively within the given range
	 * @param monthYear - the pair of month year
	 * @param startMonthYear - the start of the range (inclusive)
	 * @param endMonthYear - the end of the range (inclusive)
	 * @example
	 * isMonthYearInRange({ month: 2, year: 2020 }, { month: 1, year: 2020 }, { month: 3, year: 2020 }) // true
	 * isMonthYearInRange({ month: 6, year: 2020 }, { month: 1, year: 2020 }, { month: 3, year: 2020 }) // false
	 */
	const isMonthYearInRange = (monthYear, startMonthYear, endMonthYear) => {
	    const isAfterStart = (0, exports.isMonthYearAfterOrEqual)(monthYear, startMonthYear);
	    const isBeforeEnd = (0, exports.isMonthYearBeforeOrEqual)(monthYear, endMonthYear);
	    return isAfterStart && isBeforeEnd;
	};
	exports.isMonthYearInRange = isMonthYearInRange;
	/**
	 * Convert a date to the <month, year> (ignoring the day of the month)
	 */
	const dateToMonthYear = (date) => {
	    const result = new Date(date);
	    result.setUTCDate(1);
	    return {
	        month: result.getUTCMonth(),
	        year: result.getUTCFullYear(),
	    };
	};
	exports.dateToMonthYear = dateToMonthYear;
	const toMonthYearPeriod = (start, end) => {
	    if (start > end) {
	        throw (0, errors_1.createError)('InvalidPeriod', 'start must be before end');
	    }
	    return {
	        startMonthYear: (0, exports.dateToMonthYear)(start),
	        endMonthYear: (0, exports.dateToMonthYear)(end),
	    };
	};
	exports.toMonthYearPeriod = toMonthYearPeriod;
	/**
	 * Get the YYYY-MM string of the {@link MonthYear} object.
	 * @param monthYear - the month-year as a {@link MonthYear}
	 * @returns a string in the format of YYYY-MM
	 * @example
	 * toMonthYearString({ month: 0, year: 2020 }) // '2020-01'
	 * toMonthYearString({ month: 11, year: 2020 }) // '2020-12'
	 */
	const toMonthYearString = (monthYear) => {
	    const month = (monthYear.month + 1).toString().padStart(2, '0');
	    return `${monthYear.year}-${month}`;
	};
	exports.toMonthYearString = toMonthYearString;
	const dateToMonthYearString = (date) => {
	    const monthYear = (0, exports.dateToMonthYear)(date);
	    return (0, exports.toMonthYearString)(monthYear);
	};
	exports.dateToMonthYearString = dateToMonthYearString;
	/**
	 * Get a {@link MonthYear} object from a YYYY-MM string.
	 * @param monthYearString - a string in the format of YYYY-MM
	 * @returns the month-year as a {@link MonthYear}
	 * @example
	 * stringToMonthYear('2020-01') // { month: 0, year: 2020 }
	 * stringToMonthYear('2020-12') // { month: 11, year: 2020 }
	 */
	const stringToMonthYear = (monthYearString) => (0, exports.dateToMonthYear)(new Date(`${monthYearString}-01`));
	exports.stringToMonthYear = stringToMonthYear;
	/**
	 * Get the first day of the month in the given <month, year> as a string
	 */
	const getFirstDateStringOfMonthYear = (monthYear) => {
	    const monthYearString = (0, exports.toMonthYearString)(monthYear);
	    return `${monthYearString}-01`;
	};
	exports.getFirstDateStringOfMonthYear = getFirstDateStringOfMonthYear;
	/**
	 * Get the first day of the month in the given <month, year> as a Date
	 */
	const getFirstDateOfMonthYear = (monthYear) => {
	    const monthYearString = (0, exports.toMonthYearString)(monthYear);
	    return dayjs_1.default.utc(`${monthYearString}-01`).toDate();
	};
	exports.getFirstDateOfMonthYear = getFirstDateOfMonthYear;
	/**
	 * Get the last day of the month in the given <month, year> as a Date
	 */
	const getLastDateOfMonthYear = (monthYear) => {
	    const monthYearString = (0, exports.toMonthYearString)(monthYear);
	    return dayjs_1.default.utc(`${monthYearString}-01`).endOf('month').toDate();
	};
	exports.getLastDateOfMonthYear = getLastDateOfMonthYear;
	/**
	 * Get the last day of the month in the given <month, year> as a string
	 */
	const getLastDateStringOfMonthYear = (monthYear) => {
	    const monthYearString = typeof monthYear === 'string' ? monthYear : (0, exports.toMonthYearString)(monthYear);
	    return dayjs_1.default.utc(`${monthYearString}-01`).endOf('month').format('YYYY-MM-DD');
	};
	exports.getLastDateStringOfMonthYear = getLastDateStringOfMonthYear;
	const generateMonthYearInRange = (period) => {
	    const nextMonthYear = (current) => ({
	        year: current.month === 11 ? current.year + 1 : current.year,
	        month: current.month === 11 ? 0 : current.month + 1,
	    });
	    const inner = (startMonthYear, endMonthYear) => (startMonthYear.year === endMonthYear.year && startMonthYear.month > endMonthYear.month) ||
	        startMonthYear.year > endMonthYear.year
	        ? []
	        : [startMonthYear, ...inner(nextMonthYear(startMonthYear), endMonthYear)];
	    return inner(period.startMonthYear, period.endMonthYear);
	};
	exports.generateMonthYearInRange = generateMonthYearInRange;
	/**
	 * @param startDate - inclusive
	 * @param endDate - inclusive
	 */
	const getMonthsBetweenAsc = (startDate, endDate) => {
	    if (startDate > endDate) {
	        throw new Error('startDate must be before endDate');
	    }
	    const months = [];
	    const date = new Date(startDate);
	    while (date <= endDate) {
	        date.setUTCDate(1); // set to first day of month to prevent overflow since not all months have the same number of days
	        // casting since this is guaranteed to be in range 0-11, see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getUTCMonth
	        const month = date.getUTCMonth();
	        months.push({ month, year: date.getUTCFullYear() });
	        date.setUTCMonth(date.getUTCMonth() + 1);
	    }
	    return months;
	};
	exports.getMonthsBetweenAsc = getMonthsBetweenAsc;
	// Any number between 2 and 27 is fine
	// the purpose of this const is to avoid the unintended month set when calling date.setUTCDate
	// e.g.
	// date.setUTCDate(0, 1) can mean Jan 1st or Dec 31st depending on the time and the local timezone the code is running
	// (05:00am on Jan 1st in UTC is Dec 31st in UTC-19)
	const DAY_OF_MONTH = 15;
	const getMonthName = (month, formatOption = 'short') => {
	    const date = new Date();
	    date.setUTCMonth(month, DAY_OF_MONTH);
	    return date.toLocaleString('default', { month: formatOption });
	};
	exports.getMonthName = getMonthName;
	const sameMonthYear = (a, b) => a.year === b.year && a.month === b.month;
	exports.sameMonthYear = sameMonthYear;
	/**
	 * Given two month years return the greater month year
	 */
	const maxMonthYear = (a, b) => {
	    if ((0, exports.isMonthYearBefore)(a, b)) {
	        return b;
	    }
	    return a;
	};
	exports.maxMonthYear = maxMonthYear;
	const minMonthYear = (a, b) => {
	    if ((0, exports.isMonthYearBefore)(a, b)) {
	        return a;
	    }
	    return b;
	};
	exports.minMonthYear = minMonthYear;
	const isMonthYearBefore = (a, b) => {
	    if (a.year < b.year) {
	        return true;
	    }
	    else if (a.year > b.year) {
	        return false;
	    }
	    return a.month < b.month;
	};
	exports.isMonthYearBefore = isMonthYearBefore;
	const areMonthYearEqual = (a, b) => a.year === b.year && a.month === b.month;
	exports.areMonthYearEqual = areMonthYearEqual;
	/**
	 * Get the number of months between two MonthYear objects inclusively.
	 *
	 * @deprecated use {@link getNumberOfMonthsInPeriod} instead
	 */
	const getNumMonthsBetweenInclusively = (a, b) => {
	    const yearDiff = b.year - a.year;
	    const monthDiff = b.month - a.month;
	    return yearDiff * 12 + monthDiff + 1;
	};
	exports.getNumMonthsBetweenInclusively = getNumMonthsBetweenInclusively;
	/**
	 * Get the number of months between the time period (inclusive).
	 *
	 * @param monthYearPeriod - the period to calculate from
	 * @returns number of months
	 * @example calling getNumberOfMonthsInPeriod() on period { start: 2022/4, end: 2022/5 } returns 2
	 * @example calling getNumberOfMonthsInPeriod() on period { start: 2021/10, end: 2022/5 } returns 8
	 */
	const getNumberOfMonthsInPeriod = (monthYearPeriod) => {
	    const { endMonthYear, startMonthYear } = monthYearPeriod;
	    const difference = 12 * (endMonthYear.year - startMonthYear.year) + (endMonthYear.month - startMonthYear.month);
	    if (difference < 0) {
	        throw (0, errors_1.createError)('InvalidPeriodError', 'Start of the period should be earlier than the end of it');
	    }
	    return difference + 1;
	};
	exports.getNumberOfMonthsInPeriod = getNumberOfMonthsInPeriod;
	const isPeriodFullYear = (period) => {
	    const num = (0, exports.getNumberOfMonthsInPeriod)(period);
	    return num === 12;
	};
	exports.isPeriodFullYear = isPeriodFullYear;
	const areSameMonthYearPeriod = (a, b) => a.startMonthYear.month === b.startMonthYear.month &&
	    a.startMonthYear.year === b.startMonthYear.year &&
	    a.endMonthYear.month === b.endMonthYear.month &&
	    a.endMonthYear.year === b.endMonthYear.year;
	exports.areSameMonthYearPeriod = areSameMonthYearPeriod;
	/**
	 * Determines the intersection of two MonthYearPeriods, which is defined as the maximum of the start MonthYears and the
	 * minimum of the end MonthYears. If the maximum start MonthYear is later than the minimum end MonthYear, there is no
	 * intersection.
	 */
	const getMonthYearPeriodIntersection = (periods) => {
	    if (periods.length < 2) {
	        throw new Error('Must provide at least two MonthYearPeriods to find intersection');
	    }
	    const periodIntersection = periods.reduce((memo, period) => {
	        const startMonthYear = (0, exports.maxMonthYear)(memo.startMonthYear, period.startMonthYear);
	        const endMonthYear = (0, exports.minMonthYear)(memo.endMonthYear, period.endMonthYear);
	        return { startMonthYear, endMonthYear };
	    }, { ...periods[0] });
	    if ((0, exports.isMonthYearBefore)(periodIntersection.endMonthYear, periodIntersection.startMonthYear)) {
	        throw new Error('No intersection between the given MonthYearPeriods');
	    }
	    return periodIntersection;
	};
	exports.getMonthYearPeriodIntersection = getMonthYearPeriodIntersection;
	/**
	 * Returns true if the two periods overlap, false otherwise.
	 * @param period1 - the first period
	 * @param period2 - the second period
	 * @returns true if the periods overlap, false otherwise
	 */
	const doPeriodsOverlap = (period1, period2) => {
	    const startMonthYear = (0, exports.maxMonthYear)(period1.startMonthYear, period2.startMonthYear);
	    const endMonthYear = (0, exports.minMonthYear)(period1.endMonthYear, period2.endMonthYear);
	    // If endMonthYear is before startMonthYear, there is no overlap
	    return !(0, exports.isMonthYearBefore)(endMonthYear, startMonthYear);
	};
	exports.doPeriodsOverlap = doPeriodsOverlap;
	/**
	 * Returns the quarter number for a given month.
	 * @param month represents a month in a year, 0-indexed like Date.getMonth()
	 * @param fiscalYearEndMonth represents the last month in the year, 0-indexed like Date.getMonth()
	 * @returns quarter number
	 */
	const calculateQuarter = (month, fiscalYearEndMonth = 11) => {
	    const DECEMBER_ZERO_INDEXED = 11;
	    const monthOffset = DECEMBER_ZERO_INDEXED - fiscalYearEndMonth;
	    const normalizedMonth = ((month + monthOffset) % 12) + 1;
	    return Math.ceil(normalizedMonth / 3);
	};
	exports.calculateQuarter = calculateQuarter;
	/**
	 * @example Jan '23
	 */
	const formatMonthYear = (monthYear) => {
	    const date = (0, exports.getFirstDateOfMonthYear)(monthYear);
	    const monthFormatted = date.toLocaleString('default', {
	        month: 'short',
	        timeZone: 'UTC', // required otherwise `toLocaleString` will use the local timezone
	    });
	    // Not using Intl.DateTimeFormatOptions for year because it doesn't support 2-digit year with apostrophe
	    const yearFormatted = monthYear.year.toString().slice(-2);
	    return `${monthFormatted} '${yearFormatted}`;
	};
	exports.formatMonthYear = formatMonthYear;
	/**
	 * @example { start: "Feb '22", end: "Jan '23" }
	 */
	const formatMonthYearPeriod = (period) => ({
	    start: (0, exports.formatMonthYear)(period.startMonthYear),
	    end: (0, exports.formatMonthYear)(period.endMonthYear),
	});
	exports.formatMonthYearPeriod = formatMonthYearPeriod;
	const getLastQuarterInPeriod = (period, fiscalYearEndMonth) => {
	    if (period.endMonthYear.month === fiscalYearEndMonth) {
	        return enums_1.PeriodName.Q4;
	    }
	    if ((period.endMonthYear.month + 3 - fiscalYearEndMonth + 12) % 12 === 0) {
	        return enums_1.PeriodName.Q3;
	    }
	    if ((period.endMonthYear.month + 6 - fiscalYearEndMonth + 12) % 12 === 0) {
	        return enums_1.PeriodName.Q2;
	    }
	    if ((period.endMonthYear.month + 9 - fiscalYearEndMonth + 12) % 12 === 0) {
	        return enums_1.PeriodName.Q1;
	    }
	    throw (0, errors_1.createError)('InvalidPeriodError', "Period doesn't end with a quarter");
	};
	exports.getLastQuarterInPeriod = getLastQuarterInPeriod;
	exports.MONTH_PERIOD_NAMES = [
	    enums_1.PeriodName.M1,
	    enums_1.PeriodName.M2,
	    enums_1.PeriodName.M3,
	    enums_1.PeriodName.M4,
	    enums_1.PeriodName.M5,
	    enums_1.PeriodName.M6,
	    enums_1.PeriodName.M7,
	    enums_1.PeriodName.M8,
	    enums_1.PeriodName.M9,
	    enums_1.PeriodName.M10,
	    enums_1.PeriodName.M11,
	    enums_1.PeriodName.M12,
	];
	/**
	 * Examples of fiscal month indexing:
	 *
	 * Case 1: December fiscal year end (fiscalYearEndMonth = 11)
	 * - January (month = 0) becomes fiscal month 1
	 * - February (month = 1) becomes fiscal month 2
	 * - December (month = 11) becomes fiscal month 12
	 *
	 * Case 2: January fiscal year end (fiscalYearEndMonth = 0)
	 * - February (month = 1) becomes fiscal month 1
	 * - March (month = 2) becomes fiscal month 2
	 * - January (month = 0) becomes fiscal month 12
	 *
	 * The formula (month + 11 - fiscalYearEndMonth) % 12 normalizes any month
	 * to its corresponding fiscal month index (0-11) based on the fiscal year end.
	 */
	const getMonthPeriodName = (month, fiscalYearEndMonth) => {
	    const fiscalMonthIndex = (month + 11 - fiscalYearEndMonth) % 12;
	    return exports.MONTH_PERIOD_NAMES[fiscalMonthIndex];
	};
	exports.getMonthPeriodName = getMonthPeriodName;
	/**
	 * Validates and returns Year-to-Date (YTD) period based on fiscal year settings.
	 *
	 * For a period to be a valid YTD:
	 * 1. It must start in the first month of the fiscal year
	 *    - Example: If fiscal year ends in December (11), YTD must start in January (0)
	 *    - Example: If fiscal year ends in March (2), YTD must start in April (3)
	 *
	 * 2. It must end in the expected month based on the number of months in the period
	 *    - Example: For a 3-month YTD with December year-end:
	 *      Start: January (0), End: March (2)
	 *    - Example: For a 6-month YTD with March year-end:
	 *      Start: April (3), End: September (8)
	 *
	 * @param period - The period to validate
	 * @param fiscalYearEndMonth - The last month of the fiscal year (0-11, where 0 is January)
	 * @param numberOfMonthsInPeriod - How many months the period should contain
	 * @throws {InvalidPeriodError} If the period doesn't match expected start/end months
	 * @returns {PeriodName.YTD} If the period is a valid YTD
	 */
	const returnYtdWhenValid = (period, fiscalYearEndMonth, numberOfMonthsInPeriod) => {
	    const expectedStartMonth = (fiscalYearEndMonth + 1) % 12;
	    const expectedEndMonth = (fiscalYearEndMonth + numberOfMonthsInPeriod + 12) % 12;
	    if (period.startMonthYear.month !== expectedStartMonth) {
	        throw (0, errors_1.createError)('InvalidPeriodError', `Start month of the period must be ${(0, date_utils_1.getMonthShortAbbreviation)(expectedStartMonth)} to be a valid period`);
	    }
	    if (period.endMonthYear.month !== expectedEndMonth) {
	        throw (0, errors_1.createError)('InvalidPeriodError', `End month of the period must be ${(0, date_utils_1.getMonthShortAbbreviation)(expectedEndMonth)} to be a valid period`);
	    }
	    switch (numberOfMonthsInPeriod) {
	        case 1:
	            return enums_1.PeriodName.YTD1;
	        case 2:
	            return enums_1.PeriodName.YTD2;
	        case 3:
	            return enums_1.PeriodName.YTD3;
	        case 4:
	            return enums_1.PeriodName.YTD4;
	        case 5:
	            return enums_1.PeriodName.YTD5;
	        case 6:
	            return enums_1.PeriodName.YTD6;
	        case 7:
	            return enums_1.PeriodName.YTD7;
	        case 8:
	            return enums_1.PeriodName.YTD8;
	        case 9:
	            return enums_1.PeriodName.YTD9;
	        case 10:
	            return enums_1.PeriodName.YTD10;
	        case 11:
	            return enums_1.PeriodName.YTD11;
	        case 12:
	            return enums_1.PeriodName.YTD12;
	        default:
	            throw (0, errors_1.createError)('InvalidPeriodError', 'Period must be a valid period');
	    }
	};
	const getPeriodName = (period, fiscalYearEndMonth) => {
	    const numberOfMonthsInPeriod = (0, exports.getNumberOfMonthsInPeriod)(period);
	    if (numberOfMonthsInPeriod > 12) {
	        throw (0, errors_1.createError)('InvalidPeriodError', `invalid period: ${period}`);
	    }
	    switch (numberOfMonthsInPeriod) {
	        // when the period is a full year
	        case 12:
	            if (period.endMonthYear.month !== fiscalYearEndMonth) {
	                throw (0, errors_1.createError)('InvalidPeriodError', 'End month of the period must be the fiscal year end month');
	            }
	            return enums_1.PeriodName.A;
	        case 11:
	        case 10:
	        case 9:
	        case 8:
	        case 7:
	            return returnYtdWhenValid(period, fiscalYearEndMonth, numberOfMonthsInPeriod);
	        // when the period is 6 months
	        case 6: {
	            const lastQuarter = (0, exports.getLastQuarterInPeriod)(period, fiscalYearEndMonth);
	            switch (lastQuarter) {
	                case enums_1.PeriodName.Q1:
	                    throw (0, errors_1.createError)('InvalidPeriodError', 'Period must be a valid period');
	                case enums_1.PeriodName.Q2:
	                    return enums_1.PeriodName.H1;
	                case enums_1.PeriodName.Q3:
	                    throw (0, errors_1.createError)('InvalidPeriodError', 'Period must be a valid period');
	                case enums_1.PeriodName.Q4:
	                    return enums_1.PeriodName.H2;
	                default:
	                    throw (0, errors_1.createError)('InvalidPeriodError', 'Period must be a valid period');
	            }
	        }
	        case 5:
	        case 4:
	            return returnYtdWhenValid(period, fiscalYearEndMonth, numberOfMonthsInPeriod);
	        // when the period is 3 months
	        case 3:
	            return (0, exports.getLastQuarterInPeriod)(period, fiscalYearEndMonth);
	        case 2:
	            return returnYtdWhenValid(period, fiscalYearEndMonth, numberOfMonthsInPeriod);
	        // when the period is 1 month
	        case 1:
	            return (0, exports.getMonthPeriodName)(period.startMonthYear.month, fiscalYearEndMonth);
	        default:
	            throw (0, errors_1.createError)('InvalidPeriodError', 'Period must be a valid period');
	    }
	};
	exports.getPeriodName = getPeriodName;
	const formatFiscalOrTaxPeriod = (period, fiscalYearEndMonth, periodType) => {
	    const numberOfMonthsInPeriod = (0, exports.getNumberOfMonthsInPeriod)(period);
	    const periodLabel = periodType === 'fiscal' ? 'FY' : 'TY';
	    const year = periodType === 'fiscal' ? period.endMonthYear.year : period.startMonthYear.year;
	    const yearLabel = `${periodLabel} ${year}`;
	    switch (numberOfMonthsInPeriod) {
	        // Quarter
	        case 3: {
	            const quarter = (0, exports.calculateQuarter)(period.endMonthYear.month, fiscalYearEndMonth);
	            return {
	                periodLabel,
	                year,
	                yearLabel,
	                quarter,
	                quarterLabel: `Q${quarter}`,
	            };
	        }
	        // Year
	        case 12:
	            return {
	                periodLabel,
	                year,
	                yearLabel,
	                quarter: null,
	                quarterLabel: null,
	            };
	        default:
	            throw (0, errors_1.createError)('UnexpectedFiscalOrTaxPeriod', `Unexpected number of months in period: ${numberOfMonthsInPeriod}`);
	    }
	};
	exports.formatFiscalOrTaxPeriod = formatFiscalOrTaxPeriod;
	const formatFiscalPeriod = (period, fiscalYearEndMonth) => (0, exports.formatFiscalOrTaxPeriod)(period, fiscalYearEndMonth, 'fiscal');
	exports.formatFiscalPeriod = formatFiscalPeriod;
	const formatTaxPeriod = (period, fiscalYearEndMonth) => (0, exports.formatFiscalOrTaxPeriod)(period, fiscalYearEndMonth, 'tax');
	exports.formatTaxPeriod = formatTaxPeriod;
	const getEarliestAndLatestMonthYear = (monthYears) => {
	    if (monthYears.length === 0) {
	        throw (0, errors_1.createError)('MissingMonthYearError', 'Must provide at least one MonthYear');
	    }
	    let earliestMonthYear = monthYears[0];
	    let latestMonthYear = monthYears[0];
	    for (const monthYear of monthYears) {
	        if ((0, exports.isMonthYearBefore)(monthYear, earliestMonthYear)) {
	            earliestMonthYear = monthYear;
	        }
	        if ((0, exports.isMonthYearBefore)(latestMonthYear, monthYear)) {
	            latestMonthYear = monthYear;
	        }
	    }
	    return {
	        startMonthYear: earliestMonthYear,
	        endMonthYear: latestMonthYear,
	    };
	};
	exports.getEarliestAndLatestMonthYear = getEarliestAndLatestMonthYear;
	const getCurrentFiscalYearMonthYearPeriod = (yearEndDateMonth) => {
	    const currentDate = (0, date_utils_1.getCurrentDateObject)();
	    const currentYear = currentDate.getFullYear();
	    const taxYearEndDateThisYear = (0, date_utils_1.getLastDayOfTheMonth)(new Date(currentYear, yearEndDateMonth, 1));
	    const taxYearEndDateLastYear = (0, date_utils_1.getLastDayOfTheMonth)(new Date(currentYear - 1, yearEndDateMonth, 1));
	    const currentTaxYearEndDate = taxYearEndDateThisYear > currentDate ? taxYearEndDateLastYear : taxYearEndDateThisYear;
	    const currentTaxEndYear = currentTaxYearEndDate.getFullYear();
	    const currentTaxYearEndMonth = currentTaxYearEndDate.getMonth();
	    const startYear = currentTaxYearEndMonth === enums_1.DateMonth.December ? currentTaxEndYear : currentTaxEndYear - 1;
	    const endYear = currentTaxEndYear;
	    const startMonth = (yearEndDateMonth + 1) % 12;
	    const startMonthYear = { month: startMonth, year: startYear };
	    const endMonthYear = { month: yearEndDateMonth, year: endYear };
	    return { startMonthYear, endMonthYear };
	};
	exports.getCurrentFiscalYearMonthYearPeriod = getCurrentFiscalYearMonthYearPeriod;
	const getTaxYearMonthYearPeriod = (yearEndDateMonth, taxYear) => {
	    const startMonth = (yearEndDateMonth + 1) % 12;
	    const startMonthYear = { month: startMonth, year: taxYear };
	    const endYear = yearEndDateMonth === enums_1.DateMonth.December ? taxYear : taxYear + 1;
	    const endMonthYear = { month: yearEndDateMonth, year: endYear };
	    return { startMonthYear, endMonthYear };
	};
	exports.getTaxYearMonthYearPeriod = getTaxYearMonthYearPeriod;
	const getMonthYearPeriodUsingDates = (startDate, endDate) => {
	    const startMonth = new Date(startDate).getUTCMonth();
	    const startYear = new Date(startDate).getUTCFullYear();
	    const endMonth = new Date(endDate).getUTCMonth();
	    const endYear = new Date(endDate).getUTCFullYear();
	    const startMonthYear = { month: startMonth, year: startYear };
	    const endMonthYear = { month: endMonth, year: endYear };
	    return { startMonthYear, endMonthYear };
	};
	exports.getMonthYearPeriodUsingDates = getMonthYearPeriodUsingDates;
	const applyCompletedEndMonthYearRule = (monthYearPeriod) => {
	    const currentDate = (0, date_utils_1.getCurrentDateObject)();
	    const currentMonth = currentDate.getMonth();
	    const currentYear = currentDate.getFullYear();
	    const currentMonthYear = {
	        month: currentMonth,
	        year: currentYear,
	    };
	    if ((0, exports.isMonthYearBefore)(currentMonthYear, monthYearPeriod.startMonthYear)) {
	        throw (0, errors_1.createError)('CurrentEndMonthYearError', `Today's month-year ${currentMonthYear.month}-${currentMonthYear.year} before start month-year ${monthYearPeriod.startMonthYear.month}-${monthYearPeriod.startMonthYear.year}`);
	    }
	    if ((0, exports.isMonthYearBefore)(currentMonthYear, monthYearPeriod.endMonthYear)) {
	        return {
	            startMonthYear: monthYearPeriod.startMonthYear,
	            endMonthYear: currentMonthYear,
	        };
	    }
	    return monthYearPeriod;
	};
	exports.applyCompletedEndMonthYearRule = applyCompletedEndMonthYearRule;
	const dateStringToReadableMonthYearString = (dateString) => {
	    const datejs = dayjs_1.default.utc(dateString);
	    return datejs.format('MMMM YYYY');
	};
	exports.dateStringToReadableMonthYearString = dateStringToReadableMonthYearString;
	/**
	 * Determines the superset (union) of MonthYearPeriods, which is defined as the minimum of the start MonthYears and the
	 * maximum of the end MonthYears. This creates a single period that encompasses all the provided periods.
	 *
	 * @param periods - Array of MonthYearPeriods to find the superset of
	 * @returns A single MonthYearPeriod that encompasses all input periods
	 * @throws Error if the input array is empty
	 */
	const getMonthYearPeriodSuperset = (periods) => {
	    if (periods.length === 0) {
	        throw (0, errors_1.createError)('MissingMonthYearPeriodError', 'Must provide at least one MonthYearPeriod to find superset');
	    }
	    if (periods.length === 1) {
	        return { ...periods[0] };
	    }
	    const periodSuperset = periods.reduce((acc, period) => {
	        const startMonthYear = (0, exports.minMonthYear)(acc.startMonthYear, period.startMonthYear);
	        const endMonthYear = (0, exports.maxMonthYear)(acc.endMonthYear, period.endMonthYear);
	        return { startMonthYear, endMonthYear };
	    }, { ...periods[0] });
	    return periodSuperset;
	};
	exports.getMonthYearPeriodSuperset = getMonthYearPeriodSuperset;
	var CapitalizationChangeReason;
	(function (CapitalizationChangeReason) {
	    CapitalizationChangeReason["MONTHS_NOW_CAPITALIZED"] = "MONTHS_NOW_CAPITALIZED";
	    CapitalizationChangeReason["MONTHS_NO_LONGER_CAPITALIZED"] = "MONTHS_NO_LONGER_CAPITALIZED";
	    CapitalizationChangeReason["CAPITALIZATION_PERIOD_CHANGED"] = "CAPITALIZATION_PERIOD_CHANGED";
	})(CapitalizationChangeReason || (exports.CapitalizationChangeReason = CapitalizationChangeReason = {}));
	/**
	 * Calculates the earliest month that would be affected by a capitalization period change.
	 * Returns null if no approved months would be affected.
	 *
	 * This is used to determine which approval months need to be reset when capitalization
	 * dates are modified.
	 *
	 * @returns The earliest affected month-year with reason, or null if no approvals would be affected
	 */
	const calculateEarliestAffectedCapitalizationMonth = (input) => {
	    const { approvedThrough, newCapitalizationEnd, newCapitalizationStart, oldCapitalizationEnd, oldCapitalizationStart, } = input;
	    // If capitalization is being disabled entirely (both dates become null), clear all approvals
	    if (!newCapitalizationStart && !newCapitalizationEnd && (oldCapitalizationStart || oldCapitalizationEnd)) {
	        // Return the earliest possible month (will clear all)
	        return {
	            monthYear: oldCapitalizationStart ?? { month: 1, year: 1970 },
	            reason: CapitalizationChangeReason.MONTHS_NO_LONGER_CAPITALIZED,
	        };
	    }
	    // If there's no change, no clearing needed
	    if (oldCapitalizationStart?.month === newCapitalizationStart?.month &&
	        oldCapitalizationStart?.year === newCapitalizationStart?.year &&
	        oldCapitalizationEnd?.month === newCapitalizationEnd?.month &&
	        oldCapitalizationEnd?.year === newCapitalizationEnd?.year) {
	        return null;
	    }
	    // If old dates were null but new dates exist, check if new capitalization is in approved range
	    if (!oldCapitalizationStart && newCapitalizationStart) {
	        // When transitioning from expensed to capitalized, we need to reset approvals
	        // for any months that will now become capitalized within the approved range
	        if ((0, exports.isMonthYearBeforeOrEqual)(newCapitalizationStart, approvedThrough)) {
	            return {
	                monthYear: newCapitalizationStart,
	                reason: CapitalizationChangeReason.MONTHS_NOW_CAPITALIZED,
	            };
	        }
	        return null;
	    }
	    // If old start was null and new start is also null, no start date changes to track
	    if (!oldCapitalizationStart) {
	        return null;
	    }
	    let earliestAffected = null;
	    let earliestReason = null;
	    // Check for changes at the capitalization start
	    if (newCapitalizationStart && !(0, exports.areMonthYearEqual)(newCapitalizationStart, oldCapitalizationStart)) {
	        // Capitalization start date changed - earliest affected is the earlier of old and new start
	        earliestAffected = (0, exports.minMonthYear)(oldCapitalizationStart, newCapitalizationStart);
	        earliestReason = CapitalizationChangeReason.CAPITALIZATION_PERIOD_CHANGED;
	    }
	    // Check for changes at the capitalization end (shrinking)
	    if (oldCapitalizationEnd && newCapitalizationEnd && (0, exports.isMonthYearBefore)(newCapitalizationEnd, oldCapitalizationEnd)) {
	        // Capitalization end date moved earlier (shrinking) - first removed month is one after new end
	        // Note: months are 0-indexed in MonthYear (0-11)
	        const firstRemovedMonth = newCapitalizationEnd.month === 11 ? 0 : newCapitalizationEnd.month + 1;
	        const firstRemovedYear = newCapitalizationEnd.month === 11 ? newCapitalizationEnd.year + 1 : newCapitalizationEnd.year;
	        const firstRemoved = { month: firstRemovedMonth, year: firstRemovedYear };
	        // Only consider if this is in the approved range (firstRemoved <= approvedThrough)
	        if (!(0, exports.isMonthYearBefore)(approvedThrough, firstRemoved)) {
	            if (!earliestAffected || (0, exports.isMonthYearBefore)(firstRemoved, earliestAffected)) {
	                earliestAffected = firstRemoved;
	                earliestReason = CapitalizationChangeReason.MONTHS_NO_LONGER_CAPITALIZED;
	            }
	        }
	    }
	    // Check for changes at the capitalization end (expanding)
	    if (oldCapitalizationEnd && newCapitalizationEnd && (0, exports.isMonthYearBefore)(oldCapitalizationEnd, newCapitalizationEnd)) {
	        // Capitalization end date moved later (expanding) - first added month is one after old end
	        const firstAddedMonth = oldCapitalizationEnd.month === 11 ? 0 : oldCapitalizationEnd.month + 1;
	        const firstAddedYear = oldCapitalizationEnd.month === 11 ? oldCapitalizationEnd.year + 1 : oldCapitalizationEnd.year;
	        const firstAdded = { month: firstAddedMonth, year: firstAddedYear };
	        // Only consider if this is in the approved range (firstAdded <= approvedThrough)
	        if (!(0, exports.isMonthYearBefore)(approvedThrough, firstAdded)) {
	            if (!earliestAffected || (0, exports.isMonthYearBefore)(firstAdded, earliestAffected)) {
	                earliestAffected = firstAdded;
	                earliestReason = CapitalizationChangeReason.MONTHS_NOW_CAPITALIZED;
	            }
	        }
	    }
	    // Check for end date removal (value → null)
	    // When end date is removed, months after old end become capitalized
	    if (oldCapitalizationEnd && !newCapitalizationEnd && newCapitalizationStart) {
	        const firstAddedMonth = oldCapitalizationEnd.month === 11 ? 0 : oldCapitalizationEnd.month + 1;
	        const firstAddedYear = oldCapitalizationEnd.month === 11 ? oldCapitalizationEnd.year + 1 : oldCapitalizationEnd.year;
	        const firstAdded = { month: firstAddedMonth, year: firstAddedYear };
	        // Only consider if this is in the approved range (firstAdded <= approvedThrough)
	        if (!(0, exports.isMonthYearBefore)(approvedThrough, firstAdded)) {
	            if (!earliestAffected || (0, exports.isMonthYearBefore)(firstAdded, earliestAffected)) {
	                earliestAffected = firstAdded;
	                earliestReason = CapitalizationChangeReason.MONTHS_NOW_CAPITALIZED;
	            }
	        }
	    }
	    // Check for end date addition (null → value)
	    // When end date is added, months after new end are no longer capitalized
	    if (!oldCapitalizationEnd && newCapitalizationEnd && oldCapitalizationStart) {
	        const firstRemovedMonth = newCapitalizationEnd.month === 11 ? 0 : newCapitalizationEnd.month + 1;
	        const firstRemovedYear = newCapitalizationEnd.month === 11 ? newCapitalizationEnd.year + 1 : newCapitalizationEnd.year;
	        const firstRemoved = { month: firstRemovedMonth, year: firstRemovedYear };
	        // Only consider if this is in the approved range (firstRemoved <= approvedThrough)
	        if (!(0, exports.isMonthYearBefore)(approvedThrough, firstRemoved)) {
	            if (!earliestAffected || (0, exports.isMonthYearBefore)(firstRemoved, earliestAffected)) {
	                earliestAffected = firstRemoved;
	                earliestReason = CapitalizationChangeReason.MONTHS_NO_LONGER_CAPITALIZED;
	            }
	        }
	    }
	    // If no affected months or earliest affected is after approved range, no clearing needed
	    if (!earliestAffected || !earliestReason || (0, exports.isMonthYearBefore)(approvedThrough, earliestAffected)) {
	        return null;
	    }
	    return { monthYear: earliestAffected, reason: earliestReason };
	};
	exports.calculateEarliestAffectedCapitalizationMonth = calculateEarliestAffectedCapitalizationMonth;
} (monthYear_utils));

var number_utils = {};

var numeralExports = {};
var numeral = {
  get exports(){ return numeralExports; },
  set exports(v){ numeralExports = v; },
};

/*! @preserve
 * numeral.js
 * version : 2.0.6
 * author : Adam Draper
 * license : MIT
 * http://adamwdraper.github.com/Numeral-js/
 */

(function (module) {
	(function (global, factory) {
	    if (module.exports) {
	        module.exports = factory();
	    } else {
	        global.numeral = factory();
	    }
	}(commonjsGlobal, function () {
	    /************************************
	        Variables
	    ************************************/

	    var numeral,
	        _,
	        VERSION = '2.0.6',
	        formats = {},
	        locales = {},
	        defaults = {
	            currentLocale: 'en',
	            zeroFormat: null,
	            nullFormat: null,
	            defaultFormat: '0,0',
	            scalePercentBy100: true
	        },
	        options = {
	            currentLocale: defaults.currentLocale,
	            zeroFormat: defaults.zeroFormat,
	            nullFormat: defaults.nullFormat,
	            defaultFormat: defaults.defaultFormat,
	            scalePercentBy100: defaults.scalePercentBy100
	        };


	    /************************************
	        Constructors
	    ************************************/

	    // Numeral prototype object
	    function Numeral(input, number) {
	        this._input = input;

	        this._value = number;
	    }

	    numeral = function(input) {
	        var value,
	            kind,
	            unformatFunction,
	            regexp;

	        if (numeral.isNumeral(input)) {
	            value = input.value();
	        } else if (input === 0 || typeof input === 'undefined') {
	            value = 0;
	        } else if (input === null || _.isNaN(input)) {
	            value = null;
	        } else if (typeof input === 'string') {
	            if (options.zeroFormat && input === options.zeroFormat) {
	                value = 0;
	            } else if (options.nullFormat && input === options.nullFormat || !input.replace(/[^0-9]+/g, '').length) {
	                value = null;
	            } else {
	                for (kind in formats) {
	                    regexp = typeof formats[kind].regexps.unformat === 'function' ? formats[kind].regexps.unformat() : formats[kind].regexps.unformat;

	                    if (regexp && input.match(regexp)) {
	                        unformatFunction = formats[kind].unformat;

	                        break;
	                    }
	                }

	                unformatFunction = unformatFunction || numeral._.stringToNumber;

	                value = unformatFunction(input);
	            }
	        } else {
	            value = Number(input)|| null;
	        }

	        return new Numeral(input, value);
	    };

	    // version number
	    numeral.version = VERSION;

	    // compare numeral object
	    numeral.isNumeral = function(obj) {
	        return obj instanceof Numeral;
	    };

	    // helper functions
	    numeral._ = _ = {
	        // formats numbers separators, decimals places, signs, abbreviations
	        numberToFormat: function(value, format, roundingFunction) {
	            var locale = locales[numeral.options.currentLocale],
	                negP = false,
	                optDec = false,
	                leadingCount = 0,
	                abbr = '',
	                trillion = 1000000000000,
	                billion = 1000000000,
	                million = 1000000,
	                thousand = 1000,
	                decimal = '',
	                neg = false,
	                abbrForce, // force abbreviation
	                abs,
	                int,
	                precision,
	                signed,
	                thousands,
	                output;

	            // make sure we never format a null value
	            value = value || 0;

	            abs = Math.abs(value);

	            // see if we should use parentheses for negative number or if we should prefix with a sign
	            // if both are present we default to parentheses
	            if (numeral._.includes(format, '(')) {
	                negP = true;
	                format = format.replace(/[\(|\)]/g, '');
	            } else if (numeral._.includes(format, '+') || numeral._.includes(format, '-')) {
	                signed = numeral._.includes(format, '+') ? format.indexOf('+') : value < 0 ? format.indexOf('-') : -1;
	                format = format.replace(/[\+|\-]/g, '');
	            }

	            // see if abbreviation is wanted
	            if (numeral._.includes(format, 'a')) {
	                abbrForce = format.match(/a(k|m|b|t)?/);

	                abbrForce = abbrForce ? abbrForce[1] : false;

	                // check for space before abbreviation
	                if (numeral._.includes(format, ' a')) {
	                    abbr = ' ';
	                }

	                format = format.replace(new RegExp(abbr + 'a[kmbt]?'), '');

	                if (abs >= trillion && !abbrForce || abbrForce === 't') {
	                    // trillion
	                    abbr += locale.abbreviations.trillion;
	                    value = value / trillion;
	                } else if (abs < trillion && abs >= billion && !abbrForce || abbrForce === 'b') {
	                    // billion
	                    abbr += locale.abbreviations.billion;
	                    value = value / billion;
	                } else if (abs < billion && abs >= million && !abbrForce || abbrForce === 'm') {
	                    // million
	                    abbr += locale.abbreviations.million;
	                    value = value / million;
	                } else if (abs < million && abs >= thousand && !abbrForce || abbrForce === 'k') {
	                    // thousand
	                    abbr += locale.abbreviations.thousand;
	                    value = value / thousand;
	                }
	            }

	            // check for optional decimals
	            if (numeral._.includes(format, '[.]')) {
	                optDec = true;
	                format = format.replace('[.]', '.');
	            }

	            // break number and format
	            int = value.toString().split('.')[0];
	            precision = format.split('.')[1];
	            thousands = format.indexOf(',');
	            leadingCount = (format.split('.')[0].split(',')[0].match(/0/g) || []).length;

	            if (precision) {
	                if (numeral._.includes(precision, '[')) {
	                    precision = precision.replace(']', '');
	                    precision = precision.split('[');
	                    decimal = numeral._.toFixed(value, (precision[0].length + precision[1].length), roundingFunction, precision[1].length);
	                } else {
	                    decimal = numeral._.toFixed(value, precision.length, roundingFunction);
	                }

	                int = decimal.split('.')[0];

	                if (numeral._.includes(decimal, '.')) {
	                    decimal = locale.delimiters.decimal + decimal.split('.')[1];
	                } else {
	                    decimal = '';
	                }

	                if (optDec && Number(decimal.slice(1)) === 0) {
	                    decimal = '';
	                }
	            } else {
	                int = numeral._.toFixed(value, 0, roundingFunction);
	            }

	            // check abbreviation again after rounding
	            if (abbr && !abbrForce && Number(int) >= 1000 && abbr !== locale.abbreviations.trillion) {
	                int = String(Number(int) / 1000);

	                switch (abbr) {
	                    case locale.abbreviations.thousand:
	                        abbr = locale.abbreviations.million;
	                        break;
	                    case locale.abbreviations.million:
	                        abbr = locale.abbreviations.billion;
	                        break;
	                    case locale.abbreviations.billion:
	                        abbr = locale.abbreviations.trillion;
	                        break;
	                }
	            }


	            // format number
	            if (numeral._.includes(int, '-')) {
	                int = int.slice(1);
	                neg = true;
	            }

	            if (int.length < leadingCount) {
	                for (var i = leadingCount - int.length; i > 0; i--) {
	                    int = '0' + int;
	                }
	            }

	            if (thousands > -1) {
	                int = int.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + locale.delimiters.thousands);
	            }

	            if (format.indexOf('.') === 0) {
	                int = '';
	            }

	            output = int + decimal + (abbr ? abbr : '');

	            if (negP) {
	                output = (negP && neg ? '(' : '') + output + (negP && neg ? ')' : '');
	            } else {
	                if (signed >= 0) {
	                    output = signed === 0 ? (neg ? '-' : '+') + output : output + (neg ? '-' : '+');
	                } else if (neg) {
	                    output = '-' + output;
	                }
	            }

	            return output;
	        },
	        // unformats numbers separators, decimals places, signs, abbreviations
	        stringToNumber: function(string) {
	            var locale = locales[options.currentLocale],
	                stringOriginal = string,
	                abbreviations = {
	                    thousand: 3,
	                    million: 6,
	                    billion: 9,
	                    trillion: 12
	                },
	                abbreviation,
	                value,
	                regexp;

	            if (options.zeroFormat && string === options.zeroFormat) {
	                value = 0;
	            } else if (options.nullFormat && string === options.nullFormat || !string.replace(/[^0-9]+/g, '').length) {
	                value = null;
	            } else {
	                value = 1;

	                if (locale.delimiters.decimal !== '.') {
	                    string = string.replace(/\./g, '').replace(locale.delimiters.decimal, '.');
	                }

	                for (abbreviation in abbreviations) {
	                    regexp = new RegExp('[^a-zA-Z]' + locale.abbreviations[abbreviation] + '(?:\\)|(\\' + locale.currency.symbol + ')?(?:\\))?)?$');

	                    if (stringOriginal.match(regexp)) {
	                        value *= Math.pow(10, abbreviations[abbreviation]);
	                        break;
	                    }
	                }

	                // check for negative number
	                value *= (string.split('-').length + Math.min(string.split('(').length - 1, string.split(')').length - 1)) % 2 ? 1 : -1;

	                // remove non numbers
	                string = string.replace(/[^0-9\.]+/g, '');

	                value *= Number(string);
	            }

	            return value;
	        },
	        isNaN: function(value) {
	            return typeof value === 'number' && isNaN(value);
	        },
	        includes: function(string, search) {
	            return string.indexOf(search) !== -1;
	        },
	        insert: function(string, subString, start) {
	            return string.slice(0, start) + subString + string.slice(start);
	        },
	        reduce: function(array, callback /*, initialValue*/) {
	            if (this === null) {
	                throw new TypeError('Array.prototype.reduce called on null or undefined');
	            }

	            if (typeof callback !== 'function') {
	                throw new TypeError(callback + ' is not a function');
	            }

	            var t = Object(array),
	                len = t.length >>> 0,
	                k = 0,
	                value;

	            if (arguments.length === 3) {
	                value = arguments[2];
	            } else {
	                while (k < len && !(k in t)) {
	                    k++;
	                }

	                if (k >= len) {
	                    throw new TypeError('Reduce of empty array with no initial value');
	                }

	                value = t[k++];
	            }
	            for (; k < len; k++) {
	                if (k in t) {
	                    value = callback(value, t[k], k, t);
	                }
	            }
	            return value;
	        },
	        /**
	         * Computes the multiplier necessary to make x >= 1,
	         * effectively eliminating miscalculations caused by
	         * finite precision.
	         */
	        multiplier: function (x) {
	            var parts = x.toString().split('.');

	            return parts.length < 2 ? 1 : Math.pow(10, parts[1].length);
	        },
	        /**
	         * Given a variable number of arguments, returns the maximum
	         * multiplier that must be used to normalize an operation involving
	         * all of them.
	         */
	        correctionFactor: function () {
	            var args = Array.prototype.slice.call(arguments);

	            return args.reduce(function(accum, next) {
	                var mn = _.multiplier(next);
	                return accum > mn ? accum : mn;
	            }, 1);
	        },
	        /**
	         * Implementation of toFixed() that treats floats more like decimals
	         *
	         * Fixes binary rounding issues (eg. (0.615).toFixed(2) === '0.61') that present
	         * problems for accounting- and finance-related software.
	         */
	        toFixed: function(value, maxDecimals, roundingFunction, optionals) {
	            var splitValue = value.toString().split('.'),
	                minDecimals = maxDecimals - (optionals || 0),
	                boundedPrecision,
	                optionalsRegExp,
	                power,
	                output;

	            // Use the smallest precision value possible to avoid errors from floating point representation
	            if (splitValue.length === 2) {
	              boundedPrecision = Math.min(Math.max(splitValue[1].length, minDecimals), maxDecimals);
	            } else {
	              boundedPrecision = minDecimals;
	            }

	            power = Math.pow(10, boundedPrecision);

	            // Multiply up by precision, round accurately, then divide and use native toFixed():
	            output = (roundingFunction(value + 'e+' + boundedPrecision) / power).toFixed(boundedPrecision);

	            if (optionals > maxDecimals - boundedPrecision) {
	                optionalsRegExp = new RegExp('\\.?0{1,' + (optionals - (maxDecimals - boundedPrecision)) + '}$');
	                output = output.replace(optionalsRegExp, '');
	            }

	            return output;
	        }
	    };

	    // avaliable options
	    numeral.options = options;

	    // avaliable formats
	    numeral.formats = formats;

	    // avaliable formats
	    numeral.locales = locales;

	    // This function sets the current locale.  If
	    // no arguments are passed in, it will simply return the current global
	    // locale key.
	    numeral.locale = function(key) {
	        if (key) {
	            options.currentLocale = key.toLowerCase();
	        }

	        return options.currentLocale;
	    };

	    // This function provides access to the loaded locale data.  If
	    // no arguments are passed in, it will simply return the current
	    // global locale object.
	    numeral.localeData = function(key) {
	        if (!key) {
	            return locales[options.currentLocale];
	        }

	        key = key.toLowerCase();

	        if (!locales[key]) {
	            throw new Error('Unknown locale : ' + key);
	        }

	        return locales[key];
	    };

	    numeral.reset = function() {
	        for (var property in defaults) {
	            options[property] = defaults[property];
	        }
	    };

	    numeral.zeroFormat = function(format) {
	        options.zeroFormat = typeof(format) === 'string' ? format : null;
	    };

	    numeral.nullFormat = function (format) {
	        options.nullFormat = typeof(format) === 'string' ? format : null;
	    };

	    numeral.defaultFormat = function(format) {
	        options.defaultFormat = typeof(format) === 'string' ? format : '0.0';
	    };

	    numeral.register = function(type, name, format) {
	        name = name.toLowerCase();

	        if (this[type + 's'][name]) {
	            throw new TypeError(name + ' ' + type + ' already registered.');
	        }

	        this[type + 's'][name] = format;

	        return format;
	    };


	    numeral.validate = function(val, culture) {
	        var _decimalSep,
	            _thousandSep,
	            _currSymbol,
	            _valArray,
	            _abbrObj,
	            _thousandRegEx,
	            localeData,
	            temp;

	        //coerce val to string
	        if (typeof val !== 'string') {
	            val += '';

	            if (console.warn) {
	                console.warn('Numeral.js: Value is not string. It has been co-erced to: ', val);
	            }
	        }

	        //trim whitespaces from either sides
	        val = val.trim();

	        //if val is just digits return true
	        if (!!val.match(/^\d+$/)) {
	            return true;
	        }

	        //if val is empty return false
	        if (val === '') {
	            return false;
	        }

	        //get the decimal and thousands separator from numeral.localeData
	        try {
	            //check if the culture is understood by numeral. if not, default it to current locale
	            localeData = numeral.localeData(culture);
	        } catch (e) {
	            localeData = numeral.localeData(numeral.locale());
	        }

	        //setup the delimiters and currency symbol based on culture/locale
	        _currSymbol = localeData.currency.symbol;
	        _abbrObj = localeData.abbreviations;
	        _decimalSep = localeData.delimiters.decimal;
	        if (localeData.delimiters.thousands === '.') {
	            _thousandSep = '\\.';
	        } else {
	            _thousandSep = localeData.delimiters.thousands;
	        }

	        // validating currency symbol
	        temp = val.match(/^[^\d]+/);
	        if (temp !== null) {
	            val = val.substr(1);
	            if (temp[0] !== _currSymbol) {
	                return false;
	            }
	        }

	        //validating abbreviation symbol
	        temp = val.match(/[^\d]+$/);
	        if (temp !== null) {
	            val = val.slice(0, -1);
	            if (temp[0] !== _abbrObj.thousand && temp[0] !== _abbrObj.million && temp[0] !== _abbrObj.billion && temp[0] !== _abbrObj.trillion) {
	                return false;
	            }
	        }

	        _thousandRegEx = new RegExp(_thousandSep + '{2}');

	        if (!val.match(/[^\d.,]/g)) {
	            _valArray = val.split(_decimalSep);
	            if (_valArray.length > 2) {
	                return false;
	            } else {
	                if (_valArray.length < 2) {
	                    return ( !! _valArray[0].match(/^\d+.*\d$/) && !_valArray[0].match(_thousandRegEx));
	                } else {
	                    if (_valArray[0].length === 1) {
	                        return ( !! _valArray[0].match(/^\d+$/) && !_valArray[0].match(_thousandRegEx) && !! _valArray[1].match(/^\d+$/));
	                    } else {
	                        return ( !! _valArray[0].match(/^\d+.*\d$/) && !_valArray[0].match(_thousandRegEx) && !! _valArray[1].match(/^\d+$/));
	                    }
	                }
	            }
	        }

	        return false;
	    };


	    /************************************
	        Numeral Prototype
	    ************************************/

	    numeral.fn = Numeral.prototype = {
	        clone: function() {
	            return numeral(this);
	        },
	        format: function(inputString, roundingFunction) {
	            var value = this._value,
	                format = inputString || options.defaultFormat,
	                kind,
	                output,
	                formatFunction;

	            // make sure we have a roundingFunction
	            roundingFunction = roundingFunction || Math.round;

	            // format based on value
	            if (value === 0 && options.zeroFormat !== null) {
	                output = options.zeroFormat;
	            } else if (value === null && options.nullFormat !== null) {
	                output = options.nullFormat;
	            } else {
	                for (kind in formats) {
	                    if (format.match(formats[kind].regexps.format)) {
	                        formatFunction = formats[kind].format;

	                        break;
	                    }
	                }

	                formatFunction = formatFunction || numeral._.numberToFormat;

	                output = formatFunction(value, format, roundingFunction);
	            }

	            return output;
	        },
	        value: function() {
	            return this._value;
	        },
	        input: function() {
	            return this._input;
	        },
	        set: function(value) {
	            this._value = Number(value);

	            return this;
	        },
	        add: function(value) {
	            var corrFactor = _.correctionFactor.call(null, this._value, value);

	            function cback(accum, curr, currI, O) {
	                return accum + Math.round(corrFactor * curr);
	            }

	            this._value = _.reduce([this._value, value], cback, 0) / corrFactor;

	            return this;
	        },
	        subtract: function(value) {
	            var corrFactor = _.correctionFactor.call(null, this._value, value);

	            function cback(accum, curr, currI, O) {
	                return accum - Math.round(corrFactor * curr);
	            }

	            this._value = _.reduce([value], cback, Math.round(this._value * corrFactor)) / corrFactor;

	            return this;
	        },
	        multiply: function(value) {
	            function cback(accum, curr, currI, O) {
	                var corrFactor = _.correctionFactor(accum, curr);
	                return Math.round(accum * corrFactor) * Math.round(curr * corrFactor) / Math.round(corrFactor * corrFactor);
	            }

	            this._value = _.reduce([this._value, value], cback, 1);

	            return this;
	        },
	        divide: function(value) {
	            function cback(accum, curr, currI, O) {
	                var corrFactor = _.correctionFactor(accum, curr);
	                return Math.round(accum * corrFactor) / Math.round(curr * corrFactor);
	            }

	            this._value = _.reduce([this._value, value], cback);

	            return this;
	        },
	        difference: function(value) {
	            return Math.abs(numeral(this._value).subtract(value).value());
	        }
	    };

	    /************************************
	        Default Locale && Format
	    ************************************/

	    numeral.register('locale', 'en', {
	        delimiters: {
	            thousands: ',',
	            decimal: '.'
	        },
	        abbreviations: {
	            thousand: 'k',
	            million: 'm',
	            billion: 'b',
	            trillion: 't'
	        },
	        ordinal: function(number) {
	            var b = number % 10;
	            return (~~(number % 100 / 10) === 1) ? 'th' :
	                (b === 1) ? 'st' :
	                (b === 2) ? 'nd' :
	                (b === 3) ? 'rd' : 'th';
	        },
	        currency: {
	            symbol: '$'
	        }
	    });

	    

	(function() {
	        numeral.register('format', 'bps', {
	            regexps: {
	                format: /(BPS)/,
	                unformat: /(BPS)/
	            },
	            format: function(value, format, roundingFunction) {
	                var space = numeral._.includes(format, ' BPS') ? ' ' : '',
	                    output;

	                value = value * 10000;

	                // check for space before BPS
	                format = format.replace(/\s?BPS/, '');

	                output = numeral._.numberToFormat(value, format, roundingFunction);

	                if (numeral._.includes(output, ')')) {
	                    output = output.split('');

	                    output.splice(-1, 0, space + 'BPS');

	                    output = output.join('');
	                } else {
	                    output = output + space + 'BPS';
	                }

	                return output;
	            },
	            unformat: function(string) {
	                return +(numeral._.stringToNumber(string) * 0.0001).toFixed(15);
	            }
	        });
	})();


	(function() {
	        var decimal = {
	            base: 1000,
	            suffixes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
	        },
	        binary = {
	            base: 1024,
	            suffixes: ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
	        };

	    var allSuffixes =  decimal.suffixes.concat(binary.suffixes.filter(function (item) {
	            return decimal.suffixes.indexOf(item) < 0;
	        }));
	        var unformatRegex = allSuffixes.join('|');
	        // Allow support for BPS (http://www.investopedia.com/terms/b/basispoint.asp)
	        unformatRegex = '(' + unformatRegex.replace('B', 'B(?!PS)') + ')';

	    numeral.register('format', 'bytes', {
	        regexps: {
	            format: /([0\s]i?b)/,
	            unformat: new RegExp(unformatRegex)
	        },
	        format: function(value, format, roundingFunction) {
	            var output,
	                bytes = numeral._.includes(format, 'ib') ? binary : decimal,
	                suffix = numeral._.includes(format, ' b') || numeral._.includes(format, ' ib') ? ' ' : '',
	                power,
	                min,
	                max;

	            // check for space before
	            format = format.replace(/\s?i?b/, '');

	            for (power = 0; power <= bytes.suffixes.length; power++) {
	                min = Math.pow(bytes.base, power);
	                max = Math.pow(bytes.base, power + 1);

	                if (value === null || value === 0 || value >= min && value < max) {
	                    suffix += bytes.suffixes[power];

	                    if (min > 0) {
	                        value = value / min;
	                    }

	                    break;
	                }
	            }

	            output = numeral._.numberToFormat(value, format, roundingFunction);

	            return output + suffix;
	        },
	        unformat: function(string) {
	            var value = numeral._.stringToNumber(string),
	                power,
	                bytesMultiplier;

	            if (value) {
	                for (power = decimal.suffixes.length - 1; power >= 0; power--) {
	                    if (numeral._.includes(string, decimal.suffixes[power])) {
	                        bytesMultiplier = Math.pow(decimal.base, power);

	                        break;
	                    }

	                    if (numeral._.includes(string, binary.suffixes[power])) {
	                        bytesMultiplier = Math.pow(binary.base, power);

	                        break;
	                    }
	                }

	                value *= (bytesMultiplier || 1);
	            }

	            return value;
	        }
	    });
	})();


	(function() {
	        numeral.register('format', 'currency', {
	        regexps: {
	            format: /(\$)/
	        },
	        format: function(value, format, roundingFunction) {
	            var locale = numeral.locales[numeral.options.currentLocale],
	                symbols = {
	                    before: format.match(/^([\+|\-|\(|\s|\$]*)/)[0],
	                    after: format.match(/([\+|\-|\)|\s|\$]*)$/)[0]
	                },
	                output,
	                symbol,
	                i;

	            // strip format of spaces and $
	            format = format.replace(/\s?\$\s?/, '');

	            // format the number
	            output = numeral._.numberToFormat(value, format, roundingFunction);

	            // update the before and after based on value
	            if (value >= 0) {
	                symbols.before = symbols.before.replace(/[\-\(]/, '');
	                symbols.after = symbols.after.replace(/[\-\)]/, '');
	            } else if (value < 0 && (!numeral._.includes(symbols.before, '-') && !numeral._.includes(symbols.before, '('))) {
	                symbols.before = '-' + symbols.before;
	            }

	            // loop through each before symbol
	            for (i = 0; i < symbols.before.length; i++) {
	                symbol = symbols.before[i];

	                switch (symbol) {
	                    case '$':
	                        output = numeral._.insert(output, locale.currency.symbol, i);
	                        break;
	                    case ' ':
	                        output = numeral._.insert(output, ' ', i + locale.currency.symbol.length - 1);
	                        break;
	                }
	            }

	            // loop through each after symbol
	            for (i = symbols.after.length - 1; i >= 0; i--) {
	                symbol = symbols.after[i];

	                switch (symbol) {
	                    case '$':
	                        output = i === symbols.after.length - 1 ? output + locale.currency.symbol : numeral._.insert(output, locale.currency.symbol, -(symbols.after.length - (1 + i)));
	                        break;
	                    case ' ':
	                        output = i === symbols.after.length - 1 ? output + ' ' : numeral._.insert(output, ' ', -(symbols.after.length - (1 + i) + locale.currency.symbol.length - 1));
	                        break;
	                }
	            }


	            return output;
	        }
	    });
	})();


	(function() {
	        numeral.register('format', 'exponential', {
	        regexps: {
	            format: /(e\+|e-)/,
	            unformat: /(e\+|e-)/
	        },
	        format: function(value, format, roundingFunction) {
	            var output,
	                exponential = typeof value === 'number' && !numeral._.isNaN(value) ? value.toExponential() : '0e+0',
	                parts = exponential.split('e');

	            format = format.replace(/e[\+|\-]{1}0/, '');

	            output = numeral._.numberToFormat(Number(parts[0]), format, roundingFunction);

	            return output + 'e' + parts[1];
	        },
	        unformat: function(string) {
	            var parts = numeral._.includes(string, 'e+') ? string.split('e+') : string.split('e-'),
	                value = Number(parts[0]),
	                power = Number(parts[1]);

	            power = numeral._.includes(string, 'e-') ? power *= -1 : power;

	            function cback(accum, curr, currI, O) {
	                var corrFactor = numeral._.correctionFactor(accum, curr),
	                    num = (accum * corrFactor) * (curr * corrFactor) / (corrFactor * corrFactor);
	                return num;
	            }

	            return numeral._.reduce([value, Math.pow(10, power)], cback, 1);
	        }
	    });
	})();


	(function() {
	        numeral.register('format', 'ordinal', {
	        regexps: {
	            format: /(o)/
	        },
	        format: function(value, format, roundingFunction) {
	            var locale = numeral.locales[numeral.options.currentLocale],
	                output,
	                ordinal = numeral._.includes(format, ' o') ? ' ' : '';

	            // check for space before
	            format = format.replace(/\s?o/, '');

	            ordinal += locale.ordinal(value);

	            output = numeral._.numberToFormat(value, format, roundingFunction);

	            return output + ordinal;
	        }
	    });
	})();


	(function() {
	        numeral.register('format', 'percentage', {
	        regexps: {
	            format: /(%)/,
	            unformat: /(%)/
	        },
	        format: function(value, format, roundingFunction) {
	            var space = numeral._.includes(format, ' %') ? ' ' : '',
	                output;

	            if (numeral.options.scalePercentBy100) {
	                value = value * 100;
	            }

	            // check for space before %
	            format = format.replace(/\s?\%/, '');

	            output = numeral._.numberToFormat(value, format, roundingFunction);

	            if (numeral._.includes(output, ')')) {
	                output = output.split('');

	                output.splice(-1, 0, space + '%');

	                output = output.join('');
	            } else {
	                output = output + space + '%';
	            }

	            return output;
	        },
	        unformat: function(string) {
	            var number = numeral._.stringToNumber(string);
	            if (numeral.options.scalePercentBy100) {
	                return number * 0.01;
	            }
	            return number;
	        }
	    });
	})();


	(function() {
	        numeral.register('format', 'time', {
	        regexps: {
	            format: /(:)/,
	            unformat: /(:)/
	        },
	        format: function(value, format, roundingFunction) {
	            var hours = Math.floor(value / 60 / 60),
	                minutes = Math.floor((value - (hours * 60 * 60)) / 60),
	                seconds = Math.round(value - (hours * 60 * 60) - (minutes * 60));

	            return hours + ':' + (minutes < 10 ? '0' + minutes : minutes) + ':' + (seconds < 10 ? '0' + seconds : seconds);
	        },
	        unformat: function(string) {
	            var timeArray = string.split(':'),
	                seconds = 0;

	            // turn hours and minutes into seconds and add them all up
	            if (timeArray.length === 3) {
	                // hours
	                seconds = seconds + (Number(timeArray[0]) * 60 * 60);
	                // minutes
	                seconds = seconds + (Number(timeArray[1]) * 60);
	                // seconds
	                seconds = seconds + Number(timeArray[2]);
	            } else if (timeArray.length === 2) {
	                // minutes
	                seconds = seconds + (Number(timeArray[0]) * 60);
	                // seconds
	                seconds = seconds + Number(timeArray[1]);
	            }
	            return Number(seconds);
	        }
	    });
	})();

	return numeral;
	}));
} (numeral));

var __importDefault$5 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(number_utils, "__esModule", { value: true });
number_utils.floorToNearest = number_utils.roundToNearest = number_utils.isBetweenNumbersInclusively = number_utils.isNotPositiveNumber = number_utils.round80To100TaxRule = number_utils.toNumOrZero = number_utils.divideBy100 = number_utils.areTwoNumbersCloseEnough = number_utils.abbreviateNumber = number_utils.formatNumberWithCommas = void 0;
const numeral_1 = __importDefault$5(numeralExports);
const enums_1$1 = enums;
const errors_1 = errors$1;
const formatNumberWithCommas = (num) => (0, numeral_1.default)(num).format('0,0');
number_utils.formatNumberWithCommas = formatNumberWithCommas;
// Calculates significant figures with suffixes K/M/B/T, e.g. 1234 = 1.23K
const SUFFIXES = ['', 'K', 'M', 'B', 'T'];
const abbreviateNumber = ({ inputNumber, sigFigs = 3 }) => {
    const absoluteInputNumber = Math.abs(inputNumber);
    // Only assigns sig figs and suffixes for numbers > 1
    if (absoluteInputNumber <= 1) {
        const baseRound = absoluteInputNumber.toPrecision(sigFigs);
        return { abbreviation: baseRound, base: +baseRound };
    }
    // Calculate for numbers > 1
    const power10 = Math.log10(absoluteInputNumber);
    // 0 = '', 1 = 'K', 2 = 'M', 3 = 'B', 4 = 'T'
    // 100: power10 = 2, suffixNum = 0, suffix = ''
    // 1000: power10 = 3, suffixNum = 1, suffix = 'K'
    const suffixNum = Math.floor(power10 / 3);
    const suffix = SUFFIXES[suffixNum];
    // Would be 1 for '', 1000 for 'K', 1000000 for 'M', etc.
    const suffixPower10 = Math.pow(10, suffixNum * 3);
    const base = (absoluteInputNumber / suffixPower10) * Math.sign(inputNumber);
    const baseRound = base.toPrecision(sigFigs);
    return {
        abbreviation: `${baseRound}${suffix}`,
        base: +baseRound,
        suffix,
    };
};
number_utils.abbreviateNumber = abbreviateNumber;
const areTwoNumbersCloseEnough = (a, b, discrepancy = 0.01) => {
    if (a === b) {
        return true;
    }
    if (a === 0 || b === 0) {
        return false;
    }
    return Math.abs(a / b - 1) < discrepancy;
};
number_utils.areTwoNumbersCloseEnough = areTwoNumbersCloseEnough;
const divideBy100 = (a) => a / 100;
number_utils.divideBy100 = divideBy100;
const toNumOrZero = (a) => a ?? 0;
number_utils.toNumOrZero = toNumOrZero;
const round80To100TaxRule = ({ type, value }) => {
    if (value < 0) {
        throw (0, errors_1.createError)('Round80To100TaxRuleError', 'Must be 0 or greater');
    }
    if (type === enums_1$1.ValueType.PERCENT) {
        if (value > 100) {
            throw (0, errors_1.createError)('Round80To100TaxRuleError', 'Fractions must be within 100 or less');
        }
        return value >= 80 ? 100 : value;
    }
    if (type === enums_1$1.ValueType.FRACTION) {
        if (value > 1) {
            throw (0, errors_1.createError)('Round80To100TaxRuleError', 'Fractions must be 1 or less');
        }
        return value >= 0.8 ? 1.0 : value;
    }
    throw (0, errors_1.createError)(`Round80To100TaxRuleError: Invalid ValueType passed for this function: ${type}`);
};
number_utils.round80To100TaxRule = round80To100TaxRule;
const isNotPositiveNumber = (value) => isNaN(value) || value < 0;
number_utils.isNotPositiveNumber = isNotPositiveNumber;
const isBetweenNumbersInclusively = (value, min, max) => value >= min && value <= max;
number_utils.isBetweenNumbersInclusively = isBetweenNumbersInclusively;
const roundToNearest = (value, decimals) => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
};
number_utils.roundToNearest = roundToNearest;
const floorToNearest = (value, decimals) => {
    const factor = 10 ** decimals;
    return Math.floor(value * factor) / factor;
};
number_utils.floorToNearest = floorToNearest;

var money_utils = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.centsToNumberWithCommas = exports.convertToCents = exports.centsToDollars = exports.dollarsToCents = exports.removeDollarSignAndCommas = exports.parseCents = exports.formatDollarsFromCents = exports.formatNumberToUsd = exports.roundNumber = void 0;
	const enums_1 = enums;
	const errors_1 = errors$1;
	const number_utils_1 = number_utils;
	const roundNumber = (number, roundingMethod) => {
	    if (roundingMethod === enums_1.RoundingMethod.FLOOR) {
	        return Math.floor(number);
	    }
	    return Math.round(number);
	};
	exports.roundNumber = roundNumber;
	const formatNumberToUsd = (number, options) => {
	    const { minimumFractionDigits = 0, roundingMethod = enums_1.RoundingMethod.ROUND, showCents = true, withDollarSign = true, } = options || {};
	    const value = showCents ? number : (0, exports.roundNumber)(number, roundingMethod);
	    const formatter = new Intl.NumberFormat('en-US', {
	        currency: 'USD',
	        minimumFractionDigits,
	        style: withDollarSign ? 'currency' : 'decimal',
	    });
	    return formatter.format(value);
	};
	exports.formatNumberToUsd = formatNumberToUsd;
	const formatDollarsFromCents = (amountInCents, options) => (0, exports.formatNumberToUsd)((0, number_utils_1.divideBy100)((0, exports.parseCents)(amountInCents)), options);
	exports.formatDollarsFromCents = formatDollarsFromCents;
	/**
	 * Format cents function that takes in a number representing the amount of something
	 * in cents and formats it according to our rules:
	 *  1. Check for NaN
	 *  2. We use floor to remove decimals. This is used when dealing with money
	 *     amounts, and since all our money values are stored in cents as integers this
	 *     will remove decimals that happen as a byproduct of a math operation.
	 *
	 * @param n cents to format
	 * @returns formatted number
	 */
	const parseCents = (n) => {
	    if (isNaN(n)) {
	        throw (0, errors_1.createError)('CentsError', `Invalid number, n: ${n}`);
	    }
	    // To remove potential sign on 0 (-0 or +0) we always return 0 if the number
	    // is 0 (this works because -0 === 0 === +0)
	    return n === 0 ? 0 : parseInt(n.toFixed(2));
	};
	exports.parseCents = parseCents;
	const removeDollarSignAndCommas = (input) => input.replace(/\$|,/g, '');
	exports.removeDollarSignAndCommas = removeDollarSignAndCommas;
	/**
	 * Format dollars number to cents number. e.g. 1.00 -> 100
	 * @param dollarsFloat dollars number to format
	 * @returns cents number
	 */
	const dollarsToCents = (dollarsFloat) => (0, exports.parseCents)(dollarsFloat * 100);
	exports.dollarsToCents = dollarsToCents;
	/**
	 * Format cents number to dollars number with 2 decimal places. e.g. 100 -> 1.00
	 * @param centsFloat cents number to format
	 * @returns dollars number
	 */
	const centsToDollars = (centsFloat) => {
	    const dollarsFloat = centsFloat / 100;
	    return parseFloat(dollarsFloat.toFixed(2));
	};
	exports.centsToDollars = centsToDollars;
	/**
	 * Format dollar string to cents number, or return null if the value isn't a valid number (empty, invalid string).
	 * @param dollar dollar string or number to format. In case of string, it can have a dollar sign and commas. e.g. "$1,000.00"
	 * @returns cents number
	 */
	const convertToCents = (dollars) => {
	    if (!dollars || !dollars.trim()) {
	        return null;
	    }
	    const stringValue = (0, exports.removeDollarSignAndCommas)(dollars.trim());
	    const isValidNumber = /^-?\d*\.?\d*$/.test(stringValue);
	    if (!isValidNumber) {
	        return null;
	    }
	    const numericValue = Number.parseFloat(stringValue);
	    return (0, exports.dollarsToCents)(numericValue);
	};
	exports.convertToCents = convertToCents;
	/**
	 * Convert a cents to a string with commas taking into account 2 decimal points.
	 */
	const centsToNumberWithCommas = (cents) => {
	    const dollar = cents / 100;
	    return dollar.toLocaleString('en', { maximumFractionDigits: 2 });
	};
	exports.centsToNumberWithCommas = centsToNumberWithCommas;
} (money_utils));

var company_utils = {};

Object.defineProperty(company_utils, "__esModule", { value: true });
company_utils.getActualBusinessStartIsoDate = void 0;
/**
 * @returns earliest incorporation date in ISO format or null. When it derives the date from only the year
 * it will assume Jan 1st of the available year.
 */
const getActualBusinessStartIsoDate = (businessStartValues, options = {}) => {
    const { incorporationDate, incorporationYear, originalBusinessStartYear, priorBusinessIncorporationDate } = options;
    // below if a value exists both in the company and in the options object we take the value from the options object
    const currentIncorporation = incorporationDate || businessStartValues.incorporationDate;
    const currentIncorporationDateNum = currentIncorporation ? new Date(currentIncorporation).getTime() : null;
    const currentIncorporationYear = incorporationYear || businessStartValues.incorporationYear;
    const currentIncorporationYearDateNum = currentIncorporationYear
        ? new Date(`${currentIncorporationYear}-01-01T00:00:00.000Z`).getTime()
        : null;
    const currentPriorBusinessIncorporationDate = priorBusinessIncorporationDate || businessStartValues.priorBusinessIncorporationDate;
    const currentPriorBusinessIncorporationDateYearNum = currentPriorBusinessIncorporationDate
        ? new Date(currentPriorBusinessIncorporationDate).getTime()
        : null;
    const currentOriginalBusinessStartYear = originalBusinessStartYear || businessStartValues.originalBusinessStartYear;
    const currentOriginalBusinessStartYearDateNum = currentOriginalBusinessStartYear
        ? new Date(`${currentOriginalBusinessStartYear}-01-01T00:00:00.000Z`).getTime()
        : null;
    // For either current incorporation or prior incorporation, we use date over year
    // We then take the minimum between current and prior incorporation
    const currentIncorporationDate = currentIncorporationDateNum || currentIncorporationYearDateNum;
    const currentPriorDate = currentPriorBusinessIncorporationDateYearNum || currentOriginalBusinessStartYearDateNum;
    let earliestIncorporationDate = currentPriorDate || currentIncorporationDate;
    if (!earliestIncorporationDate) {
        return null;
    }
    if (currentIncorporationDate && currentIncorporationDate < earliestIncorporationDate) {
        earliestIncorporationDate = currentIncorporationDate;
    }
    return new Date(earliestIncorporationDate).toISOString();
};
company_utils.getActualBusinessStartIsoDate = getActualBusinessStartIsoDate;

var promise = {};

Object.defineProperty(promise, "__esModule", { value: true });
promise.pause = void 0;
/**
 * Function used in async/await to pause execution for a set number of milliseconds.
 * Note, the number of milliseconds in a minimum, not a guarantee.
 */
const pause = (milliseconds) => new Promise((resolve) => {
    setTimeout(() => resolve(), milliseconds);
});
promise.pause = pause;

var questions = {};

var types = {};

Object.defineProperty(types, "__esModule", { value: true });
types.ResearchActivities = types.DocumentationTools = void 0;
var DocumentationTools;
(function (DocumentationTools) {
    DocumentationTools["EMPLOYEE_CALENDARS"] = "Employee Calendars";
    DocumentationTools["MEETING_AGENDAS"] = "Meeting Agendas";
    DocumentationTools["EMAILS"] = "E-mails";
    DocumentationTools["MEETING_MINUTES"] = "Meeting Minutes";
    DocumentationTools["GANTT_CHARTS"] = "Gantt Charts";
    DocumentationTools["PROJECT_MANAGEMENT_SOFTWARE"] = "Project Management Software";
    DocumentationTools["ARCHITECTURE_DIAGRAMS"] = "Architecture Diagrams";
    DocumentationTools["PROTOTYPE_BUILD_SHEETS"] = "Prototype Build Sheets";
    DocumentationTools["COMPUTATIONAL_DOCUMENTS"] = "Computational Documents";
    DocumentationTools["PROCESS_CHARTS"] = "Process Charts";
    DocumentationTools["DESIGN_PROTOTYPES"] = "Design Prototypes";
    DocumentationTools["CONCEPT_DRAWINGS"] = "Concept Drawings";
    DocumentationTools["MOCKUPS"] = "Mockups";
    DocumentationTools["SOFTWARE_SOURCE_CODE"] = "Software Source Code";
    DocumentationTools["COMMIT_LOGS"] = "Commit Logs";
    DocumentationTools["PULL_REQUESTS"] = "Pull Requests";
    DocumentationTools["CLOUD_COMPUTING"] = "Cloud Computing Usage Records";
    DocumentationTools["RELEASE_NOTES"] = "Release Notes";
    DocumentationTools["API_USAGE"] = "API Usage Logs";
    DocumentationTools["API_DOCS"] = "API Documentation";
    DocumentationTools["LAB_TESTS"] = "Lab Test Results";
})(DocumentationTools || (types.DocumentationTools = DocumentationTools = {}));
var ResearchActivities;
(function (ResearchActivities) {
    ResearchActivities["RESEARCH_PAPERS"] = "Research Papers/Drafts";
    ResearchActivities["WHITE_PAPERS"] = "White Papers";
    ResearchActivities["PATENTS"] = "Patents";
})(ResearchActivities || (types.ResearchActivities = ResearchActivities = {}));

var utils = {};

Object.defineProperty(utils, "__esModule", { value: true });
utils.divideResearchActivities = utils.combineTools = utils.divideTools = void 0;
const types_1 = types;
const divideTools = (tools) => {
    const planningTools = [];
    const designTools = [];
    const implementationTools = [];
    const otherTools = [];
    tools.forEach((tool) => {
        switch (tool) {
            case types_1.DocumentationTools.EMPLOYEE_CALENDARS:
            case types_1.DocumentationTools.MEETING_AGENDAS:
            case types_1.DocumentationTools.EMAILS:
            case types_1.DocumentationTools.MEETING_MINUTES:
            case types_1.DocumentationTools.GANTT_CHARTS:
            case types_1.DocumentationTools.PROJECT_MANAGEMENT_SOFTWARE:
                planningTools.push(tool);
                break;
            case types_1.DocumentationTools.ARCHITECTURE_DIAGRAMS:
            case types_1.DocumentationTools.PROTOTYPE_BUILD_SHEETS:
            case types_1.DocumentationTools.COMPUTATIONAL_DOCUMENTS:
            case types_1.DocumentationTools.PROCESS_CHARTS:
            case types_1.DocumentationTools.DESIGN_PROTOTYPES:
            case types_1.DocumentationTools.CONCEPT_DRAWINGS:
            case types_1.DocumentationTools.MOCKUPS:
                designTools.push(tool);
                break;
            case types_1.DocumentationTools.SOFTWARE_SOURCE_CODE:
            case types_1.DocumentationTools.COMMIT_LOGS:
            case types_1.DocumentationTools.PULL_REQUESTS:
            case types_1.DocumentationTools.CLOUD_COMPUTING:
            case types_1.DocumentationTools.RELEASE_NOTES:
            case types_1.DocumentationTools.API_USAGE:
            case types_1.DocumentationTools.API_DOCS:
            case types_1.DocumentationTools.LAB_TESTS:
                implementationTools.push(tool);
                break;
            default:
                otherTools.push(tool);
                break;
        }
    });
    return { planningTools, designTools, implementationTools, otherTools };
};
utils.divideTools = divideTools;
const combineTools = (toolsArrays) => {
    const { designTools, implementationTools, otherTools, planningTools } = toolsArrays;
    return [...planningTools, ...designTools, ...implementationTools, ...otherTools];
};
utils.combineTools = combineTools;
const divideResearchActivities = (allActivities) => {
    const researchActivities = [];
    const patentDescriptions = [];
    allActivities.forEach((activity) => {
        switch (activity) {
            case types_1.ResearchActivities.RESEARCH_PAPERS:
            case types_1.ResearchActivities.WHITE_PAPERS:
            case types_1.ResearchActivities.PATENTS:
                researchActivities.push(activity);
                break;
            default:
                patentDescriptions.push(activity);
                break;
        }
    });
    return { researchActivities, patentDescriptions };
};
utils.divideResearchActivities = divideResearchActivities;

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.divideResearchActivities = exports.combineTools = exports.divideTools = exports.ResearchActivities = exports.DocumentationTools = void 0;
	var types_1 = types;
	Object.defineProperty(exports, "DocumentationTools", { enumerable: true, get: function () { return types_1.DocumentationTools; } });
	Object.defineProperty(exports, "ResearchActivities", { enumerable: true, get: function () { return types_1.ResearchActivities; } });
	var utils_1 = utils;
	Object.defineProperty(exports, "divideTools", { enumerable: true, get: function () { return utils_1.divideTools; } });
	Object.defineProperty(exports, "combineTools", { enumerable: true, get: function () { return utils_1.combineTools; } });
	Object.defineProperty(exports, "divideResearchActivities", { enumerable: true, get: function () { return utils_1.divideResearchActivities; } });
} (questions));

var email_utils = {};

Object.defineProperty(email_utils, "__esModule", { value: true });
email_utils.normalizeEmail = normalizeEmail;
email_utils.isEmailIdentifier = isEmailIdentifier;
email_utils.normalizeEmailIdentifier = normalizeEmailIdentifier;
email_utils.isNeoTaxEmail = isNeoTaxEmail;
function normalizeEmail(email) {
    if (email === null) {
        return null;
    }
    return email.trim().toLowerCase();
}
/**
 * Whether a provider identifier carries an email address, and is therefore safe to case-normalize.
 *
 * An email address is a case-insensitive identifier: `Dev@Example.com` and `dev@example.com` reach
 * the same inbox and are the same person. Contributor matching already relies on that when it joins
 * ticketing users to payroll users by email, so identifiers are treated the same way.
 *
 * The test is `@`-presence rather than an email pattern, which keeps it expressible identically as
 * SQL `LIKE '%@%'`. `LowercaseEmailShapedTicketingPids1787356800000` normalized the already-stored
 * identifiers on that predicate, and the two have to agree exactly: a value the migration lowercased
 * but this function skips is stored lowercase while its incoming counterpart is not, which is the
 * mismatch the whole mechanism exists to prevent. A pattern also has to be written twice, once for
 * JavaScript and once for POSIX, and the two disagree on Unicode whitespace and on non-ASCII
 * addresses that this deliberately still normalizes.
 *
 * The cost is that a provider issuing two case-differing non-email tokens containing an `@` would
 * have them folded onto one identifier. No company in production holds such a pair — the migration's
 * pre-flight guard checks for exactly that and came back empty.
 */
function isEmailIdentifier(identifier) {
    return identifier !== null && identifier.includes('@');
}
function normalizeEmailIdentifier(identifier) {
    if (!isEmailIdentifier(identifier)) {
        return identifier;
    }
    return normalizeEmail(identifier);
}
/** Whether an address belongs to Neo.Tax staff rather than to a customer. Case-insensitive. */
function isNeoTaxEmail(email) {
    return normalizeEmail(email).endsWith('@neo.tax');
}

var retry_utils = {};

var pRetryExports = {};
var pRetry$1 = {
  get exports(){ return pRetryExports; },
  set exports(v){ pRetryExports = v; },
};

var retryExports = {};
var retry$2 = {
  get exports(){ return retryExports; },
  set exports(v){ retryExports = v; },
};

var retry$1 = {};

function RetryOperation(timeouts, options) {
  // Compatibility for the old (timeouts, retryForever) signature
  if (typeof options === 'boolean') {
    options = { forever: options };
  }

  this._originalTimeouts = JSON.parse(JSON.stringify(timeouts));
  this._timeouts = timeouts;
  this._options = options || {};
  this._maxRetryTime = options && options.maxRetryTime || Infinity;
  this._fn = null;
  this._errors = [];
  this._attempts = 1;
  this._operationTimeout = null;
  this._operationTimeoutCb = null;
  this._timeout = null;
  this._operationStart = null;
  this._timer = null;

  if (this._options.forever) {
    this._cachedTimeouts = this._timeouts.slice(0);
  }
}
var retry_operation = RetryOperation;

RetryOperation.prototype.reset = function() {
  this._attempts = 1;
  this._timeouts = this._originalTimeouts.slice(0);
};

RetryOperation.prototype.stop = function() {
  if (this._timeout) {
    clearTimeout(this._timeout);
  }
  if (this._timer) {
    clearTimeout(this._timer);
  }

  this._timeouts       = [];
  this._cachedTimeouts = null;
};

RetryOperation.prototype.retry = function(err) {
  if (this._timeout) {
    clearTimeout(this._timeout);
  }

  if (!err) {
    return false;
  }
  var currentTime = new Date().getTime();
  if (err && currentTime - this._operationStart >= this._maxRetryTime) {
    this._errors.push(err);
    this._errors.unshift(new Error('RetryOperation timeout occurred'));
    return false;
  }

  this._errors.push(err);

  var timeout = this._timeouts.shift();
  if (timeout === undefined) {
    if (this._cachedTimeouts) {
      // retry forever, only keep last error
      this._errors.splice(0, this._errors.length - 1);
      timeout = this._cachedTimeouts.slice(-1);
    } else {
      return false;
    }
  }

  var self = this;
  this._timer = setTimeout(function() {
    self._attempts++;

    if (self._operationTimeoutCb) {
      self._timeout = setTimeout(function() {
        self._operationTimeoutCb(self._attempts);
      }, self._operationTimeout);

      if (self._options.unref) {
          self._timeout.unref();
      }
    }

    self._fn(self._attempts);
  }, timeout);

  if (this._options.unref) {
      this._timer.unref();
  }

  return true;
};

RetryOperation.prototype.attempt = function(fn, timeoutOps) {
  this._fn = fn;

  if (timeoutOps) {
    if (timeoutOps.timeout) {
      this._operationTimeout = timeoutOps.timeout;
    }
    if (timeoutOps.cb) {
      this._operationTimeoutCb = timeoutOps.cb;
    }
  }

  var self = this;
  if (this._operationTimeoutCb) {
    this._timeout = setTimeout(function() {
      self._operationTimeoutCb();
    }, self._operationTimeout);
  }

  this._operationStart = new Date().getTime();

  this._fn(this._attempts);
};

RetryOperation.prototype.try = function(fn) {
  console.log('Using RetryOperation.try() is deprecated');
  this.attempt(fn);
};

RetryOperation.prototype.start = function(fn) {
  console.log('Using RetryOperation.start() is deprecated');
  this.attempt(fn);
};

RetryOperation.prototype.start = RetryOperation.prototype.try;

RetryOperation.prototype.errors = function() {
  return this._errors;
};

RetryOperation.prototype.attempts = function() {
  return this._attempts;
};

RetryOperation.prototype.mainError = function() {
  if (this._errors.length === 0) {
    return null;
  }

  var counts = {};
  var mainError = null;
  var mainErrorCount = 0;

  for (var i = 0; i < this._errors.length; i++) {
    var error = this._errors[i];
    var message = error.message;
    var count = (counts[message] || 0) + 1;

    counts[message] = count;

    if (count >= mainErrorCount) {
      mainError = error;
      mainErrorCount = count;
    }
  }

  return mainError;
};

(function (exports) {
	var RetryOperation = retry_operation;

	exports.operation = function(options) {
	  var timeouts = exports.timeouts(options);
	  return new RetryOperation(timeouts, {
	      forever: options && (options.forever || options.retries === Infinity),
	      unref: options && options.unref,
	      maxRetryTime: options && options.maxRetryTime
	  });
	};

	exports.timeouts = function(options) {
	  if (options instanceof Array) {
	    return [].concat(options);
	  }

	  var opts = {
	    retries: 10,
	    factor: 2,
	    minTimeout: 1 * 1000,
	    maxTimeout: Infinity,
	    randomize: false
	  };
	  for (var key in options) {
	    opts[key] = options[key];
	  }

	  if (opts.minTimeout > opts.maxTimeout) {
	    throw new Error('minTimeout is greater than maxTimeout');
	  }

	  var timeouts = [];
	  for (var i = 0; i < opts.retries; i++) {
	    timeouts.push(this.createTimeout(i, opts));
	  }

	  if (options && options.forever && !timeouts.length) {
	    timeouts.push(this.createTimeout(i, opts));
	  }

	  // sort the array numerically ascending
	  timeouts.sort(function(a,b) {
	    return a - b;
	  });

	  return timeouts;
	};

	exports.createTimeout = function(attempt, opts) {
	  var random = (opts.randomize)
	    ? (Math.random() + 1)
	    : 1;

	  var timeout = Math.round(random * Math.max(opts.minTimeout, 1) * Math.pow(opts.factor, attempt));
	  timeout = Math.min(timeout, opts.maxTimeout);

	  return timeout;
	};

	exports.wrap = function(obj, options, methods) {
	  if (options instanceof Array) {
	    methods = options;
	    options = null;
	  }

	  if (!methods) {
	    methods = [];
	    for (var key in obj) {
	      if (typeof obj[key] === 'function') {
	        methods.push(key);
	      }
	    }
	  }

	  for (var i = 0; i < methods.length; i++) {
	    var method   = methods[i];
	    var original = obj[method];

	    obj[method] = function retryWrapper(original) {
	      var op       = exports.operation(options);
	      var args     = Array.prototype.slice.call(arguments, 1);
	      var callback = args.pop();

	      args.push(function(err) {
	        if (op.retry(err)) {
	          return;
	        }
	        if (err) {
	          arguments[0] = op.mainError();
	        }
	        callback.apply(this, arguments);
	      });

	      op.attempt(function() {
	        original.apply(obj, args);
	      });
	    }.bind(obj, original);
	    obj[method].options = options;
	  }
	};
} (retry$1));

(function (module) {
	module.exports = retry$1;
} (retry$2));

const retry = retryExports;

const networkErrorMsgs = [
	'Failed to fetch', // Chrome
	'NetworkError when attempting to fetch resource.', // Firefox
	'The Internet connection appears to be offline.', // Safari
	'Network request failed' // `cross-fetch`
];

class AbortError extends Error {
	constructor(message) {
		super();

		if (message instanceof Error) {
			this.originalError = message;
			({message} = message);
		} else {
			this.originalError = new Error(message);
			this.originalError.stack = this.stack;
		}

		this.name = 'AbortError';
		this.message = message;
	}
}

const decorateErrorWithCounts = (error, attemptNumber, options) => {
	// Minus 1 from attemptNumber because the first attempt does not count as a retry
	const retriesLeft = options.retries - (attemptNumber - 1);

	error.attemptNumber = attemptNumber;
	error.retriesLeft = retriesLeft;
	return error;
};

const isNetworkError = errorMessage => networkErrorMsgs.includes(errorMessage);

const pRetry = (input, options) => new Promise((resolve, reject) => {
	options = {
		onFailedAttempt: () => {},
		retries: 10,
		...options
	};

	const operation = retry.operation(options);

	operation.attempt(async attemptNumber => {
		try {
			resolve(await input(attemptNumber));
		} catch (error) {
			if (!(error instanceof Error)) {
				reject(new TypeError(`Non-error was thrown: "${error}". You should only throw errors.`));
				return;
			}

			if (error instanceof AbortError) {
				operation.stop();
				reject(error.originalError);
			} else if (error instanceof TypeError && !isNetworkError(error.message)) {
				operation.stop();
				reject(error);
			} else {
				decorateErrorWithCounts(error, attemptNumber, options);

				try {
					await options.onFailedAttempt(error);
				} catch (error) {
					reject(error);
					return;
				}

				if (!operation.retry(error)) {
					reject(operation.mainError());
				}
			}
		}
	});
});

pRetry$1.exports = pRetry;
// TODO: remove this in the next major version
pRetryExports.default = pRetry;

pRetryExports.AbortError = AbortError;

// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  // lazy load so that environments that need to polyfill have a chance to do so
  if (!getRandomValues) {
    // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation.
    getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);

    if (!getRandomValues) {
      throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
    }
  }

  return getRandomValues(rnds8);
}

var REGEX = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000)$/i;

function validate(uuid) {
  return typeof uuid === 'string' && REGEX.test(uuid);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

const byteToHex = [];

for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).slice(1));
}

function unsafeStringify(arr, offset = 0) {
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}

function stringify(arr, offset = 0) {
  const uuid = unsafeStringify(arr, offset); // Consistency check for valid UUID.  If this throws, it's likely due to one
  // of the following:
  // - One or more input array values don't map to a hex octet (leading to
  // "undefined" in the uuid)
  // - Invalid input values for the RFC `version` or `variant` fields

  if (!validate(uuid)) {
    throw TypeError('Stringified UUID is invalid');
  }

  return uuid;
}

//
// Inspired by https://github.com/LiosK/UUID.js
// and http://docs.python.org/library/uuid.html

let _nodeId;

let _clockseq; // Previous uuid creation time


let _lastMSecs = 0;
let _lastNSecs = 0; // See https://github.com/uuidjs/uuid for API details

function v1(options, buf, offset) {
  let i = buf && offset || 0;
  const b = buf || new Array(16);
  options = options || {};
  let node = options.node || _nodeId;
  let clockseq = options.clockseq !== undefined ? options.clockseq : _clockseq; // node and clockseq need to be initialized to random values if they're not
  // specified.  We do this lazily to minimize issues related to insufficient
  // system entropy.  See #189

  if (node == null || clockseq == null) {
    const seedBytes = options.random || (options.rng || rng)();

    if (node == null) {
      // Per 4.5, create and 48-bit node id, (47 random bits + multicast bit = 1)
      node = _nodeId = [seedBytes[0] | 0x01, seedBytes[1], seedBytes[2], seedBytes[3], seedBytes[4], seedBytes[5]];
    }

    if (clockseq == null) {
      // Per 4.2.2, randomize (14 bit) clockseq
      clockseq = _clockseq = (seedBytes[6] << 8 | seedBytes[7]) & 0x3fff;
    }
  } // UUID timestamps are 100 nano-second units since the Gregorian epoch,
  // (1582-10-15 00:00).  JSNumbers aren't precise enough for this, so
  // time is handled internally as 'msecs' (integer milliseconds) and 'nsecs'
  // (100-nanoseconds offset from msecs) since unix epoch, 1970-01-01 00:00.


  let msecs = options.msecs !== undefined ? options.msecs : Date.now(); // Per 4.2.1.2, use count of uuid's generated during the current clock
  // cycle to simulate higher resolution clock

  let nsecs = options.nsecs !== undefined ? options.nsecs : _lastNSecs + 1; // Time since last uuid creation (in msecs)

  const dt = msecs - _lastMSecs + (nsecs - _lastNSecs) / 10000; // Per 4.2.1.2, Bump clockseq on clock regression

  if (dt < 0 && options.clockseq === undefined) {
    clockseq = clockseq + 1 & 0x3fff;
  } // Reset nsecs if clock regresses (new clockseq) or we've moved onto a new
  // time interval


  if ((dt < 0 || msecs > _lastMSecs) && options.nsecs === undefined) {
    nsecs = 0;
  } // Per 4.2.1.2 Throw error if too many uuids are requested


  if (nsecs >= 10000) {
    throw new Error("uuid.v1(): Can't create more than 10M uuids/sec");
  }

  _lastMSecs = msecs;
  _lastNSecs = nsecs;
  _clockseq = clockseq; // Per 4.1.4 - Convert from unix epoch to Gregorian epoch

  msecs += 12219292800000; // `time_low`

  const tl = ((msecs & 0xfffffff) * 10000 + nsecs) % 0x100000000;
  b[i++] = tl >>> 24 & 0xff;
  b[i++] = tl >>> 16 & 0xff;
  b[i++] = tl >>> 8 & 0xff;
  b[i++] = tl & 0xff; // `time_mid`

  const tmh = msecs / 0x100000000 * 10000 & 0xfffffff;
  b[i++] = tmh >>> 8 & 0xff;
  b[i++] = tmh & 0xff; // `time_high_and_version`

  b[i++] = tmh >>> 24 & 0xf | 0x10; // include version

  b[i++] = tmh >>> 16 & 0xff; // `clock_seq_hi_and_reserved` (Per 4.2.2 - include variant)

  b[i++] = clockseq >>> 8 | 0x80; // `clock_seq_low`

  b[i++] = clockseq & 0xff; // `node`

  for (let n = 0; n < 6; ++n) {
    b[i + n] = node[n];
  }

  return buf || unsafeStringify(b);
}

function parse(uuid) {
  if (!validate(uuid)) {
    throw TypeError('Invalid UUID');
  }

  let v;
  const arr = new Uint8Array(16); // Parse ########-....-....-....-............

  arr[0] = (v = parseInt(uuid.slice(0, 8), 16)) >>> 24;
  arr[1] = v >>> 16 & 0xff;
  arr[2] = v >>> 8 & 0xff;
  arr[3] = v & 0xff; // Parse ........-####-....-....-............

  arr[4] = (v = parseInt(uuid.slice(9, 13), 16)) >>> 8;
  arr[5] = v & 0xff; // Parse ........-....-####-....-............

  arr[6] = (v = parseInt(uuid.slice(14, 18), 16)) >>> 8;
  arr[7] = v & 0xff; // Parse ........-....-....-####-............

  arr[8] = (v = parseInt(uuid.slice(19, 23), 16)) >>> 8;
  arr[9] = v & 0xff; // Parse ........-....-....-....-############
  // (Use "/" to avoid 32-bit truncation when bit-shifting high-order bytes)

  arr[10] = (v = parseInt(uuid.slice(24, 36), 16)) / 0x10000000000 & 0xff;
  arr[11] = v / 0x100000000 & 0xff;
  arr[12] = v >>> 24 & 0xff;
  arr[13] = v >>> 16 & 0xff;
  arr[14] = v >>> 8 & 0xff;
  arr[15] = v & 0xff;
  return arr;
}

function stringToBytes(str) {
  str = unescape(encodeURIComponent(str)); // UTF8 escape

  const bytes = [];

  for (let i = 0; i < str.length; ++i) {
    bytes.push(str.charCodeAt(i));
  }

  return bytes;
}

const DNS = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';
const URL$1 = '6ba7b811-9dad-11d1-80b4-00c04fd430c8';
function v35(name, version, hashfunc) {
  function generateUUID(value, namespace, buf, offset) {
    var _namespace;

    if (typeof value === 'string') {
      value = stringToBytes(value);
    }

    if (typeof namespace === 'string') {
      namespace = parse(namespace);
    }

    if (((_namespace = namespace) === null || _namespace === void 0 ? void 0 : _namespace.length) !== 16) {
      throw TypeError('Namespace must be array-like (16 iterable integer values, 0-255)');
    } // Compute hash of namespace and value, Per 4.3
    // Future: Use spread syntax when supported on all platforms, e.g. `bytes =
    // hashfunc([...namespace, ... value])`


    let bytes = new Uint8Array(16 + value.length);
    bytes.set(namespace);
    bytes.set(value, namespace.length);
    bytes = hashfunc(bytes);
    bytes[6] = bytes[6] & 0x0f | version;
    bytes[8] = bytes[8] & 0x3f | 0x80;

    if (buf) {
      offset = offset || 0;

      for (let i = 0; i < 16; ++i) {
        buf[offset + i] = bytes[i];
      }

      return buf;
    }

    return unsafeStringify(bytes);
  } // Function#name is not settable on some platforms (#270)


  try {
    generateUUID.name = name; // eslint-disable-next-line no-empty
  } catch (err) {} // For CommonJS default export support


  generateUUID.DNS = DNS;
  generateUUID.URL = URL$1;
  return generateUUID;
}

/*
 * Browser-compatible JavaScript MD5
 *
 * Modification of JavaScript MD5
 * https://github.com/blueimp/JavaScript-MD5
 *
 * Copyright 2011, Sebastian Tschan
 * https://blueimp.net
 *
 * Licensed under the MIT license:
 * https://opensource.org/licenses/MIT
 *
 * Based on
 * A JavaScript implementation of the RSA Data Security, Inc. MD5 Message
 * Digest Algorithm, as defined in RFC 1321.
 * Version 2.2 Copyright (C) Paul Johnston 1999 - 2009
 * Other contributors: Greg Holt, Andrew Kepert, Ydnar, Lostinet
 * Distributed under the BSD License
 * See http://pajhome.org.uk/crypt/md5 for more info.
 */
function md5(bytes) {
  if (typeof bytes === 'string') {
    const msg = unescape(encodeURIComponent(bytes)); // UTF8 escape

    bytes = new Uint8Array(msg.length);

    for (let i = 0; i < msg.length; ++i) {
      bytes[i] = msg.charCodeAt(i);
    }
  }

  return md5ToHexEncodedArray(wordsToMd5(bytesToWords(bytes), bytes.length * 8));
}
/*
 * Convert an array of little-endian words to an array of bytes
 */


function md5ToHexEncodedArray(input) {
  const output = [];
  const length32 = input.length * 32;
  const hexTab = '0123456789abcdef';

  for (let i = 0; i < length32; i += 8) {
    const x = input[i >> 5] >>> i % 32 & 0xff;
    const hex = parseInt(hexTab.charAt(x >>> 4 & 0x0f) + hexTab.charAt(x & 0x0f), 16);
    output.push(hex);
  }

  return output;
}
/**
 * Calculate output length with padding and bit length
 */


function getOutputLength(inputLength8) {
  return (inputLength8 + 64 >>> 9 << 4) + 14 + 1;
}
/*
 * Calculate the MD5 of an array of little-endian words, and a bit length.
 */


function wordsToMd5(x, len) {
  /* append padding */
  x[len >> 5] |= 0x80 << len % 32;
  x[getOutputLength(len) - 1] = len;
  let a = 1732584193;
  let b = -271733879;
  let c = -1732584194;
  let d = 271733878;

  for (let i = 0; i < x.length; i += 16) {
    const olda = a;
    const oldb = b;
    const oldc = c;
    const oldd = d;
    a = md5ff(a, b, c, d, x[i], 7, -680876936);
    d = md5ff(d, a, b, c, x[i + 1], 12, -389564586);
    c = md5ff(c, d, a, b, x[i + 2], 17, 606105819);
    b = md5ff(b, c, d, a, x[i + 3], 22, -1044525330);
    a = md5ff(a, b, c, d, x[i + 4], 7, -176418897);
    d = md5ff(d, a, b, c, x[i + 5], 12, 1200080426);
    c = md5ff(c, d, a, b, x[i + 6], 17, -1473231341);
    b = md5ff(b, c, d, a, x[i + 7], 22, -45705983);
    a = md5ff(a, b, c, d, x[i + 8], 7, 1770035416);
    d = md5ff(d, a, b, c, x[i + 9], 12, -1958414417);
    c = md5ff(c, d, a, b, x[i + 10], 17, -42063);
    b = md5ff(b, c, d, a, x[i + 11], 22, -1990404162);
    a = md5ff(a, b, c, d, x[i + 12], 7, 1804603682);
    d = md5ff(d, a, b, c, x[i + 13], 12, -40341101);
    c = md5ff(c, d, a, b, x[i + 14], 17, -1502002290);
    b = md5ff(b, c, d, a, x[i + 15], 22, 1236535329);
    a = md5gg(a, b, c, d, x[i + 1], 5, -165796510);
    d = md5gg(d, a, b, c, x[i + 6], 9, -1069501632);
    c = md5gg(c, d, a, b, x[i + 11], 14, 643717713);
    b = md5gg(b, c, d, a, x[i], 20, -373897302);
    a = md5gg(a, b, c, d, x[i + 5], 5, -701558691);
    d = md5gg(d, a, b, c, x[i + 10], 9, 38016083);
    c = md5gg(c, d, a, b, x[i + 15], 14, -660478335);
    b = md5gg(b, c, d, a, x[i + 4], 20, -405537848);
    a = md5gg(a, b, c, d, x[i + 9], 5, 568446438);
    d = md5gg(d, a, b, c, x[i + 14], 9, -1019803690);
    c = md5gg(c, d, a, b, x[i + 3], 14, -187363961);
    b = md5gg(b, c, d, a, x[i + 8], 20, 1163531501);
    a = md5gg(a, b, c, d, x[i + 13], 5, -1444681467);
    d = md5gg(d, a, b, c, x[i + 2], 9, -51403784);
    c = md5gg(c, d, a, b, x[i + 7], 14, 1735328473);
    b = md5gg(b, c, d, a, x[i + 12], 20, -1926607734);
    a = md5hh(a, b, c, d, x[i + 5], 4, -378558);
    d = md5hh(d, a, b, c, x[i + 8], 11, -2022574463);
    c = md5hh(c, d, a, b, x[i + 11], 16, 1839030562);
    b = md5hh(b, c, d, a, x[i + 14], 23, -35309556);
    a = md5hh(a, b, c, d, x[i + 1], 4, -1530992060);
    d = md5hh(d, a, b, c, x[i + 4], 11, 1272893353);
    c = md5hh(c, d, a, b, x[i + 7], 16, -155497632);
    b = md5hh(b, c, d, a, x[i + 10], 23, -1094730640);
    a = md5hh(a, b, c, d, x[i + 13], 4, 681279174);
    d = md5hh(d, a, b, c, x[i], 11, -358537222);
    c = md5hh(c, d, a, b, x[i + 3], 16, -722521979);
    b = md5hh(b, c, d, a, x[i + 6], 23, 76029189);
    a = md5hh(a, b, c, d, x[i + 9], 4, -640364487);
    d = md5hh(d, a, b, c, x[i + 12], 11, -421815835);
    c = md5hh(c, d, a, b, x[i + 15], 16, 530742520);
    b = md5hh(b, c, d, a, x[i + 2], 23, -995338651);
    a = md5ii(a, b, c, d, x[i], 6, -198630844);
    d = md5ii(d, a, b, c, x[i + 7], 10, 1126891415);
    c = md5ii(c, d, a, b, x[i + 14], 15, -1416354905);
    b = md5ii(b, c, d, a, x[i + 5], 21, -57434055);
    a = md5ii(a, b, c, d, x[i + 12], 6, 1700485571);
    d = md5ii(d, a, b, c, x[i + 3], 10, -1894986606);
    c = md5ii(c, d, a, b, x[i + 10], 15, -1051523);
    b = md5ii(b, c, d, a, x[i + 1], 21, -2054922799);
    a = md5ii(a, b, c, d, x[i + 8], 6, 1873313359);
    d = md5ii(d, a, b, c, x[i + 15], 10, -30611744);
    c = md5ii(c, d, a, b, x[i + 6], 15, -1560198380);
    b = md5ii(b, c, d, a, x[i + 13], 21, 1309151649);
    a = md5ii(a, b, c, d, x[i + 4], 6, -145523070);
    d = md5ii(d, a, b, c, x[i + 11], 10, -1120210379);
    c = md5ii(c, d, a, b, x[i + 2], 15, 718787259);
    b = md5ii(b, c, d, a, x[i + 9], 21, -343485551);
    a = safeAdd(a, olda);
    b = safeAdd(b, oldb);
    c = safeAdd(c, oldc);
    d = safeAdd(d, oldd);
  }

  return [a, b, c, d];
}
/*
 * Convert an array bytes to an array of little-endian words
 * Characters >255 have their high-byte silently ignored.
 */


function bytesToWords(input) {
  if (input.length === 0) {
    return [];
  }

  const length8 = input.length * 8;
  const output = new Uint32Array(getOutputLength(length8));

  for (let i = 0; i < length8; i += 8) {
    output[i >> 5] |= (input[i / 8] & 0xff) << i % 32;
  }

  return output;
}
/*
 * Add integers, wrapping at 2^32. This uses 16-bit operations internally
 * to work around bugs in some JS interpreters.
 */


function safeAdd(x, y) {
  const lsw = (x & 0xffff) + (y & 0xffff);
  const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
  return msw << 16 | lsw & 0xffff;
}
/*
 * Bitwise rotate a 32-bit number to the left.
 */


function bitRotateLeft(num, cnt) {
  return num << cnt | num >>> 32 - cnt;
}
/*
 * These functions implement the four basic operations the algorithm uses.
 */


function md5cmn(q, a, b, x, s, t) {
  return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b);
}

function md5ff(a, b, c, d, x, s, t) {
  return md5cmn(b & c | ~b & d, a, b, x, s, t);
}

function md5gg(a, b, c, d, x, s, t) {
  return md5cmn(b & d | c & ~d, a, b, x, s, t);
}

function md5hh(a, b, c, d, x, s, t) {
  return md5cmn(b ^ c ^ d, a, b, x, s, t);
}

function md5ii(a, b, c, d, x, s, t) {
  return md5cmn(c ^ (b | ~d), a, b, x, s, t);
}

const v3 = v35('v3', 0x30, md5);
var v3$1 = v3;

const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
var native = {
  randomUUID
};

function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }

  options = options || {};
  const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  if (buf) {
    offset = offset || 0;

    for (let i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }

    return buf;
  }

  return unsafeStringify(rnds);
}

// Adapted from Chris Veness' SHA1 code at
// http://www.movable-type.co.uk/scripts/sha1.html
function f$7(s, x, y, z) {
  switch (s) {
    case 0:
      return x & y ^ ~x & z;

    case 1:
      return x ^ y ^ z;

    case 2:
      return x & y ^ x & z ^ y & z;

    case 3:
      return x ^ y ^ z;
  }
}

function ROTL(x, n) {
  return x << n | x >>> 32 - n;
}

function sha1(bytes) {
  const K = [0x5a827999, 0x6ed9eba1, 0x8f1bbcdc, 0xca62c1d6];
  const H = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476, 0xc3d2e1f0];

  if (typeof bytes === 'string') {
    const msg = unescape(encodeURIComponent(bytes)); // UTF8 escape

    bytes = [];

    for (let i = 0; i < msg.length; ++i) {
      bytes.push(msg.charCodeAt(i));
    }
  } else if (!Array.isArray(bytes)) {
    // Convert Array-like to Array
    bytes = Array.prototype.slice.call(bytes);
  }

  bytes.push(0x80);
  const l = bytes.length / 4 + 2;
  const N = Math.ceil(l / 16);
  const M = new Array(N);

  for (let i = 0; i < N; ++i) {
    const arr = new Uint32Array(16);

    for (let j = 0; j < 16; ++j) {
      arr[j] = bytes[i * 64 + j * 4] << 24 | bytes[i * 64 + j * 4 + 1] << 16 | bytes[i * 64 + j * 4 + 2] << 8 | bytes[i * 64 + j * 4 + 3];
    }

    M[i] = arr;
  }

  M[N - 1][14] = (bytes.length - 1) * 8 / Math.pow(2, 32);
  M[N - 1][14] = Math.floor(M[N - 1][14]);
  M[N - 1][15] = (bytes.length - 1) * 8 & 0xffffffff;

  for (let i = 0; i < N; ++i) {
    const W = new Uint32Array(80);

    for (let t = 0; t < 16; ++t) {
      W[t] = M[i][t];
    }

    for (let t = 16; t < 80; ++t) {
      W[t] = ROTL(W[t - 3] ^ W[t - 8] ^ W[t - 14] ^ W[t - 16], 1);
    }

    let a = H[0];
    let b = H[1];
    let c = H[2];
    let d = H[3];
    let e = H[4];

    for (let t = 0; t < 80; ++t) {
      const s = Math.floor(t / 20);
      const T = ROTL(a, 5) + f$7(s, b, c, d) + e + K[s] + W[t] >>> 0;
      e = d;
      d = c;
      c = ROTL(b, 30) >>> 0;
      b = a;
      a = T;
    }

    H[0] = H[0] + a >>> 0;
    H[1] = H[1] + b >>> 0;
    H[2] = H[2] + c >>> 0;
    H[3] = H[3] + d >>> 0;
    H[4] = H[4] + e >>> 0;
  }

  return [H[0] >> 24 & 0xff, H[0] >> 16 & 0xff, H[0] >> 8 & 0xff, H[0] & 0xff, H[1] >> 24 & 0xff, H[1] >> 16 & 0xff, H[1] >> 8 & 0xff, H[1] & 0xff, H[2] >> 24 & 0xff, H[2] >> 16 & 0xff, H[2] >> 8 & 0xff, H[2] & 0xff, H[3] >> 24 & 0xff, H[3] >> 16 & 0xff, H[3] >> 8 & 0xff, H[3] & 0xff, H[4] >> 24 & 0xff, H[4] >> 16 & 0xff, H[4] >> 8 & 0xff, H[4] & 0xff];
}

const v5 = v35('v5', 0x50, sha1);
var v5$1 = v5;

var nil = '00000000-0000-0000-0000-000000000000';

function version(uuid) {
  if (!validate(uuid)) {
    throw TypeError('Invalid UUID');
  }

  return parseInt(uuid.slice(14, 15), 16);
}

var esmBrowser = /*#__PURE__*/Object.freeze({
    __proto__: null,
    NIL: nil,
    parse: parse,
    stringify: stringify,
    v1: v1,
    v3: v3$1,
    v4: v4,
    v5: v5$1,
    validate: validate,
    version: version
});

var require$$0 = /*@__PURE__*/getAugmentedNamespace(esmBrowser);

var json_utils = {};

var string_utils = {};

Object.defineProperty(string_utils, "__esModule", { value: true });
string_utils.formatAsElementId = string_utils.capitalizeFirstLetter = string_utils.formatArrayToCommaAndString = string_utils.sanitizeString = string_utils.normalizeWhitespaceAndCase = string_utils.escapeLikePattern = string_utils.parsePercentageIntegerValue = string_utils.spaceToDash = string_utils.combineAddresses = string_utils.truncateString = string_utils.snakeCase = string_utils.kebabCase = string_utils.constantCase = string_utils.capitalCase = void 0;
/**
 * Like sentence case, but with the first letter of each word capitalized. Not the same as PascalCase because this function includes spaces between words.
 *
 * Handles camelCase, PascalCase, kebab-case, snake_case, and CONSTANT_CASE.
 */
const capitalCase = (str) => str
    .replace(/([a-z])([A-Z])/g, '$1 $2') // adds spaces to camelCase and PascalCase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // adds spaces to camelCase and PascalCase
    .replace(/[_-]+/g, ' ') // converts kebab-case and snake_case separators to spaces
    .replace(/[\s]+/g, ' ') // converts multiple spaces to single spaces
    .toLowerCase() // converts CONSTANT_CASE to lower case
    .replace(/(^|\s)([a-z])/g, (match) => match.toUpperCase()); // converts first letter of each word to uppercase
string_utils.capitalCase = capitalCase;
/**
 * Handles camelCase, PascalCase, snake_case, kebab-case, and sentence case.
 */
const constantCase = (str) => str
    .replace(/([a-z])([A-Z])/g, '$1_$2') // converts camelCase and PascalCase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2') // converts camelCase and PascalCase
    .replace(/[/\s-]+/g, '_') // converts certain separators to underscores
    .replace(/['",.!?]+/g, '') // removes common punctuation
    .toUpperCase();
string_utils.constantCase = constantCase;
/**
 * Handles camelCase, PascalCase, snake_case, CONSTANT_CASE, and sentence case.
 */
const kebabCase = (str) => str
    .replace(/([a-z])([A-Z])/g, '$1-$2') // converts camelCase and PascalCase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2') // converts camelCase and PascalCase
    .replace(/[_/\s]+/g, '-') // converts certain separators to hyphens
    .replace(/['",.!?]+/g, '') // removes common punctuation
    .toLowerCase();
string_utils.kebabCase = kebabCase;
const snakeCase = (str) => str
    .replace(/([a-z])([A-Z])/g, '$1_$2') // converts camelCase and PascalCase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2') // converts camelCase and PascalCase
    .toLowerCase();
string_utils.snakeCase = snakeCase;
/**
 * Truncates a string if it is longer than the specified maximum length. Truncates after last whole word and adds ellipses by default.
 */
const truncateString = (text, maxLength, options = {}) => {
    if (text === undefined || text === null || text.length <= maxLength) {
        return text;
    }
    const optionsMerged = {
        includeEllipses: true,
        truncateWords: false,
        ...options,
    };
    let truncatedText = text.slice(0, maxLength);
    if (!optionsMerged.truncateWords) {
        // Truncate the string to the last space before the maximum length UNLESS the string is a single word
        const lastSpaceIndex = truncatedText.lastIndexOf(' ');
        if (lastSpaceIndex > 0) {
            truncatedText = truncatedText.slice(0, lastSpaceIndex);
        }
    }
    if (optionsMerged.includeEllipses) {
        truncatedText += '...';
    }
    return truncatedText;
};
string_utils.truncateString = truncateString;
const combineAddresses = (addresses) => addresses
    .filter((address) => !!address)
    .map((address) => address.trim())
    .join(', ');
string_utils.combineAddresses = combineAddresses;
const spaceToDash = (str) => str.replace(/ /g, '-');
string_utils.spaceToDash = spaceToDash;
const parsePercentageIntegerValue = (value) => {
    if (!value) {
        return null;
    }
    const trimmed = value.trim().replace(/%$/, '');
    if (!trimmed) {
        return null;
    }
    const integerValue = parseInt(trimmed);
    if (isNaN(integerValue) || integerValue < 0 || integerValue > 100) {
        return null;
    }
    return integerValue;
};
string_utils.parsePercentageIntegerValue = parsePercentageIntegerValue;
/**
 * Escapes SQL LIKE/ILIKE special characters (%, _, \) and normalizes
 * empty/whitespace-only strings to null. Use before passing user input
 * to queries that build patterns like `'%' || :keyword || '%'`.
 */
const escapeLikePattern = (keyword) => {
    const trimmed = keyword?.trim();
    if (!trimmed)
        return null;
    return trimmed.replace(/[\\%_]/g, '\\$&');
};
string_utils.escapeLikePattern = escapeLikePattern;
/**
 * Trims, collapses internal whitespace runs to a single space, and lowercases — so strings differing only
 * in casing or spacing compare equal. The mechanical half of a comparison key; what counts as "the same
 * value" is the caller's rule to name and own.
 */
const normalizeWhitespaceAndCase = (str) => str.trim().replace(/\s+/g, ' ').toLowerCase();
string_utils.normalizeWhitespaceAndCase = normalizeWhitespaceAndCase;
const sanitizeString = (s) => {
    // Remove control characters: 0x00-0x08, 0x0B, 0x0C, 0x0E-0x1F, 0x7F
    // Using RegExp constructor with unicode escapes to avoid linter error
    const pattern = String.raw `[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]`;
    return s.replace(new RegExp(pattern, 'g'), '');
};
string_utils.sanitizeString = sanitizeString;
const formatArrayToCommaAndString = (arr) => {
    if (arr.length === 0) {
        return '';
    }
    else if (arr.length === 1) {
        return arr[0];
    }
    else if (arr.length === 2) {
        return arr.join(' and ');
    }
    else {
        const lastItem = arr[arr.length - 1];
        const excludeLastItem = arr.slice(0, arr.length - 1);
        return `${excludeLastItem.join(', ')}, and ${lastItem}`;
    }
};
string_utils.formatArrayToCommaAndString = formatArrayToCommaAndString;
const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);
string_utils.capitalizeFirstLetter = capitalizeFirstLetter;
/**
 * - Converts to lowercase
 * - Replaces spaces with dashes
 * - Removes unsupported characters (keeping only letters, numbers, dashes, underscores)
 */
const formatAsElementId = (str) => str
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^a-z0-9-_]/g, '');
string_utils.formatAsElementId = formatAsElementId;

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.safeDateExtractFromJson = exports.areJsonEqual = exports.sanitizeJson = exports.sortJsonByKeys = exports.toJson = exports.getStaticCopy = void 0;
	const string_utils_1 = string_utils;
	// eslint-disable-next-line @typescript-eslint/no-restricted-types
	const executeUpThePrototypeChain = (obj, func) => {
	    if (!(obj instanceof Object)) {
	        return [];
	    }
	    const results = [];
	    let prototype = obj;
	    do {
	        results.push(func(prototype));
	        prototype = Object.getPrototypeOf(prototype);
	    } while (prototype);
	    return results;
	};
	const getEvaluatedGetters = (obj) => {
	    if (!(obj instanceof Object)) {
	        return {};
	    }
	    const getters = Object.entries(Object.getOwnPropertyDescriptors(obj)).reduce((acc, [name, descriptor]) => {
	        if (typeof descriptor.get === 'function') {
	            acc.push([name, descriptor.get]);
	        }
	        return acc;
	    }, []);
	    return getters.reduce((acc, [name, getter]) => {
	        try {
	            acc[name] = getter.call(obj);
	        }
	        catch (err) {
	            const message = 'message' in err ? err.message : 'Error when calling getter';
	            acc[name] = message;
	        }
	        return acc;
	    }, {});
	};
	const getAllEvaluatedGetters = (obj) => {
	    if (!(obj instanceof Object)) {
	        return {};
	    }
	    const getters = executeUpThePrototypeChain(obj, (prototype) => getEvaluatedGetters(prototype));
	    return getters.reduce((acc, getter) => ({ ...acc, ...getter }), {});
	};
	/**
	 * Returns an object containing all the fields of the given object even if they are not enumerable, excluding getters or methods.
	 * One exception is Dates, which are objects with only methods in JS. Dates will be converted to ISO strings.
	 */
	const getOwnFields = (obj) => {
	    if (!(obj instanceof Object)) {
	        return {};
	    }
	    return Object.entries(Object.getOwnPropertyDescriptors(obj)).reduce((acc, [name, descriptor]) => {
	        if (typeof descriptor.value !== 'undefined' && typeof descriptor.value !== 'function') {
	            // we need to handle Dates separately because they are objects with only methods in JS. The data for their date
	            // representation is directly accessible.
	            const value = descriptor.value instanceof Date ? descriptor.value.toISOString() : descriptor.value;
	            acc[name] = value;
	        }
	        return acc;
	    }, {});
	};
	const getAllFields = (obj) => {
	    if (!(obj instanceof Object)) {
	        return {};
	    }
	    const fields = executeUpThePrototypeChain(obj, (prototype) => getOwnFields(prototype));
	    return fields.reduce((acc, field) => ({ ...acc, ...field }), {});
	};
	/**
	 * Constructs a static copy of an object, including all getters which have been evaluated and added to the copy with the
	 * same key as the getter in the original object. This function is recursive, so all nested objects will also be copied.
	 *
	 * Circular references will be replaced with the string '[Circular <ClassName>]' in the copy.
	 * Symbol values will be replaced with the string '[Symbol(<SymbolName>)]' in the copy.
	 *
	 * @param obj - The object to copy
	 * @param fieldsToCopy - An optional list of fields to copy if {@link obj} is an Object. If this is not provided, all fields will be copied.
	 * @param seenBranches - A set of objects that have already been copied. This is used to detect circular references.
	 * @returns - A deep copy of the object, including all evaluated getters.
	 */
	const getStaticCopy = (obj, fieldsToCopy = null, 
	// eslint-disable-next-line @typescript-eslint/no-restricted-types
	seenBranches = new Set()) => {
	    // primitive values are pass by value, so we can just return them
	    if (!(obj instanceof Object)) {
	        // feel free to improve the typing here to prevent having to do an unchecked cast
	        return obj;
	    }
	    seenBranches.add(obj);
	    const getters = getAllEvaluatedGetters(obj);
	    const fields = getAllFields(obj);
	    let entries = Object.entries({ ...fields, ...getters });
	    if (fieldsToCopy) {
	        // create a set from the array to make the lookup faster
	        const fieldsToCopySet = new Set(fieldsToCopy);
	        entries = entries.filter(([key]) => fieldsToCopySet.has(key));
	    }
	    const newFields = entries.map(([key, value]) => {
	        if (value instanceof Object) {
	            if (seenBranches.has(value)) {
	                const constructorName = value.constructor.name;
	                return [key, `[Circular ${constructorName}]`];
	            }
	            seenBranches.add(value);
	            return [key, (0, exports.getStaticCopy)(value, null, seenBranches)];
	        }
	        if (typeof value === 'symbol') {
	            return [key, `[Symbol(${value.description})]`];
	        }
	        return [key, value];
	    });
	    return Object.fromEntries(newFields);
	};
	exports.getStaticCopy = getStaticCopy;
	/**
	 * Convert just about anything to JSON. The following limitations prevent the generated JSON from being used to reconstruct the
	 * original object:
	 * - objects will be ignored
	 * - circular references will be replaced with the string '[Circular <ClassName>]'
	 * - Symbol values will be replaced with the string '[Symbol(<SymbolName>)]' in the copy
	 *
	 * @param value - The value to convert to JSON
	 * @param replacer — An array of strings and numbers that acts as an approved list for selecting the object properties that will be stringified.
	 * Defaults to null, which means all properties will be included.
	 * @param space — Adds indentation, white space, and line break characters to the return-value JSON text to make it easier to read.
	 * Defaults to 2.
	 * @returns - The JSON representation of the value, including all evaluated getters and fields up the prototype chain.
	 */
	const toJson = (value, replacer = null, space = 2) => JSON.stringify((0, exports.getStaticCopy)(value, replacer), null, space);
	exports.toJson = toJson;
	const sortJsonByKeys = (json) => {
	    if (Array.isArray(json)) {
	        // Sort arrays recursively
	        return json.map(exports.sortJsonByKeys);
	    }
	    else if (json !== null && typeof json === 'object') {
	        // Sort object keys
	        return Object.keys(json)
	            .sort()
	            .reduce((acc, key) => {
	            acc[key] = (0, exports.sortJsonByKeys)(json[key]);
	            return acc;
	        }, {});
	    }
	    // return primitives as the raw value
	    return json;
	};
	exports.sortJsonByKeys = sortJsonByKeys;
	/**
	 * Recursively sanitizes all strings in a JSON structure by removing control characters.
	 * This prevents postgres errors when saving text/jsonb containing null bytes or other control chars.
	 */
	const sanitizeJson = (json) => {
	    if (Array.isArray(json)) {
	        return json.map(exports.sanitizeJson);
	    }
	    else if (json !== null && typeof json === 'object') {
	        return Object.keys(json).reduce((acc, key) => {
	            acc[(0, string_utils_1.sanitizeString)(key)] = (0, exports.sanitizeJson)(json[key]);
	            return acc;
	        }, {});
	    }
	    else if (typeof json === 'string') {
	        return (0, string_utils_1.sanitizeString)(json);
	    }
	    return json;
	};
	exports.sanitizeJson = sanitizeJson;
	const areJsonEqual = (json1, json2) => JSON.stringify((0, exports.sortJsonByKeys)(json1)) === JSON.stringify((0, exports.sortJsonByKeys)(json2));
	exports.areJsonEqual = areJsonEqual;
	/**
	 * Safely extracts a nested property from a Json object and converts it to the specified type
	 * @param obj - The Json object to extract from
	 * @param path - Array of property names to traverse
	 * @returns The converted value or null if the path doesn't exist or validation fails
	 */
	const safeDateExtractFromJson = (obj, path) => {
	    if (!obj || typeof obj !== 'object' || obj === null) {
	        return null;
	    }
	    let current = obj;
	    for (const key of path) {
	        if (typeof current !== 'object' || current === null || !(key in current)) {
	            return null;
	        }
	        current = current[key];
	    }
	    if (typeof current !== 'string' && typeof current !== 'number') {
	        return null;
	    }
	    if (typeof current === 'string' && current.trim() === '') {
	        return null;
	    }
	    const date = new Date(current);
	    if (isNaN(date.getTime())) {
	        return null;
	    }
	    return date;
	};
	exports.safeDateExtractFromJson = safeDateExtractFromJson;
} (json_utils));

(function (exports) {
	var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
	    return (mod && mod.__esModule) ? mod : { "default": mod };
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.retry = exports.MAX_NUM_RETRY_REQUESTS = void 0;
	const p_retry_1 = __importDefault(pRetryExports);
	const uuid_1 = require$$0;
	const errors_1 = errors$1;
	const json_utils_1 = json_utils;
	const MIN_RETRY_REQUESTS = 1;
	exports.MAX_NUM_RETRY_REQUESTS = 6;
	/**
	 * {@link retry} requires that the thrown value is an instance of {@link Error} for it to work. Since any data type
	 * can be thrown, this function converts an unknown thrown value into an error that extends {@link Error} so that
	 * retry can work as expected.
	 */
	const wrapWithThrownError = (func) => async (attemptNumber) => {
	    try {
	        return await func(attemptNumber);
	    }
	    catch (err) {
	        // We don't always capture all error information
	        // so output everything to support debugging as
	        // retry errors are often the hardest to debug
	        // example: statusCode and statusMessage
	        console.error(err);
	        if (err instanceof Error) {
	            throw err;
	        }
	        throw (0, errors_1.createError)('NonErrorThrownInRetry', (0, json_utils_1.toJson)(err));
	    }
	};
	/**
	 * Retry resolving a function until it resolves or the max number of retries is reached. Exponential back-off is used between retries. The formula
	 * used to calculate the individual timeouts is: minTimeout * Math.pow(factor, attempt). If all retries fail, the last error is thrown.
	 *
	 * For example if minTimeout = 1000, exponent = 2, and numRetries = 6:
	 *
	 * - 1000 * 2^(1 - 1) = 1000 ms
	 * - 1000 * 2^(2 - 1) = 2000 ms
	 * - 1000 * 2^(3 - 1) = 4000 ms
	 * - 1000 * 2^(4 - 1) = 8000 ms
	 * - 1000 * 2^(5 - 1) = 16000 ms
	 * - 1000 * 2^(6 - 1) = 32000 ms
	 *
	 * total = 63 seconds
	 *
	 * @param fn - The function to retry
	 * @param options.errorMessage - The error message to include in log when a retry occurs.
	 * @param options.numRetries - The number of times to retry the function. Defaults to 6.
	 * @param options.minTimeout - The minimum timeout between retries. Defaults to 1000 ms.
	 * @param options.exponent - The exponent to use when calculating the timeout between retries. Defaults to 2.
	 * @param options.suppressErrors - If true, errors will not be logged to the console each time the function is retried. Defaults to false.
	 * @param options.randomize - If true, the timeout between retries will be randomized. Defaults to false.
	 * @returns The resolved value of the function
	 * @throws The last error thrown by the function
	 */
	const retry = (fn, options = {}) => {
	    const retries = options.numRetries ?? 6;
	    if (retries < MIN_RETRY_REQUESTS || retries > exports.MAX_NUM_RETRY_REQUESTS) {
	        throw new Error(`Configured max number of retries must be between ${MIN_RETRY_REQUESTS} and ${exports.MAX_NUM_RETRY_REQUESTS}`);
	    }
	    const minTimeout = options.minTimeout ?? 1000;
	    const exponent = options.exponent ?? 2;
	    const randomize = options.randomize ?? false;
	    const retryTraceId = (0, uuid_1.v4)();
	    return (0, p_retry_1.default)(wrapWithThrownError(fn), {
	        retries,
	        minTimeout,
	        factor: exponent,
	        randomize,
	        onFailedAttempt: (error) => {
	            if (options.suppressErrors) {
	                return;
	            }
	            if (error.retriesLeft > 0) {
	                const errorMessage = error.message ?? options.errorMessage ?? 'Unknown error';
	                console.error('RetryAttemptFailure', `${errorMessage} attempt ${error.attemptNumber} of ${error.attemptNumber + error.retriesLeft} traceId ${retryTraceId} using function ${fn.toString()}`);
	            }
	        },
	    });
	};
	exports.retry = retry;
} (retry_utils));

var types_utils = {};

(function (exports) {
	var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
	    return (mod && mod.__esModule) ? mod : { "default": mod };
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.singleTenantDomainRedirectInputSchema = exports.singleTenantRedirectUrlSchema = exports.singleTenantEmailDomainSchema = exports.parseOrThrowClientError = exports.enumSchemaFromDynamicArray = exports.ssoProviderNameSchema = exports.SSO_PROVIDER_NAME_REGEX = exports.monthYearStringSchema = exports.monthYearPeriodNullableSchema = exports.monthYearPeriodSchema = exports.monthYearSchema = exports.vendorSchema = exports.notSpecifiedTransform = exports.dollarsToCentsSchema = exports.emailSchema = exports.uuidSchema = exports.monthSchema = exports.yearSchema = exports.intSchema = exports.positiveIntSchema = exports.nonNegativeNumberSchema = exports.isoDateTimeStringSchema = exports.dateTimeOptionalTimeZoneStringSchema = exports.dateTimeOptionalTimeZoneRegex = exports.dateCoercionSchema = exports.dateStringSchema = exports.datetimeSchema = exports.commaDelimitedStringSchema = exports.nullableOptionalStringSchema = exports.nullableStringSchema = exports.stringSchema = exports.jsonArraySchema = exports.jsonObjectSchema = exports.jsonSchema = exports.assertNeverType = exports.assertNever = void 0;
	const zod_1 = __importDefault(zod);
	const errors_1 = errors$1;
	const money_utils_1 = money_utils;
	const assertNever = (value) => {
	    throw (0, errors_1.createError)('ImpossibleValueError', `Unexpected code path with value: ${value}`);
	};
	exports.assertNever = assertNever;
	const assertNeverType = (typeValue, typeName = 'type') => {
	    throw new TypeError(`Unexpected ${typeName}: ${typeValue}`);
	};
	exports.assertNeverType = assertNeverType;
	const literalSchema = zod_1.default.union([zod_1.default.string(), zod_1.default.number(), zod_1.default.boolean(), zod_1.default.null(), zod_1.default.date()]);
	exports.jsonSchema = zod_1.default.lazy(() => zod_1.default.union([literalSchema, zod_1.default.array(exports.jsonSchema), zod_1.default.record(zod_1.default.string(), exports.jsonSchema)]));
	exports.jsonObjectSchema = zod_1.default.record(zod_1.default.string(), exports.jsonSchema);
	exports.jsonArraySchema = exports.jsonSchema.array();
	exports.stringSchema = zod_1.default.string().min(1);
	exports.nullableStringSchema = zod_1.default.preprocess((arg) => {
	    if (typeof arg === 'string' && arg.length === 0) {
	        return null;
	    }
	    return arg;
	}, exports.stringSchema.nullable());
	exports.nullableOptionalStringSchema = exports.nullableStringSchema.optional();
	/**
	 * Parse a comma-delimited string into an array of strings. Trims whitespace from each value. Allows empty strings.
	 */
	exports.commaDelimitedStringSchema = zod_1.default.preprocess((arg) => {
	    // If the argument is a string, split it by comma and trim each value
	    if (typeof arg === 'string') {
	        return arg.split(',').map((value) => value.trim());
	    }
	    // Otherwise, return the argument unchanged so it can be validated by the schema
	    return arg;
	}, zod_1.default.array(zod_1.default.string()));
	exports.datetimeSchema = zod_1.default.preprocess((arg) => {
	    if (typeof arg == 'string' || arg instanceof Date) {
	        return new Date(arg);
	    }
	}, zod_1.default.date());
	const dateRegex = /\d{4}-(0\d|1[0-2])-([0-2]\d|3[01])/;
	exports.dateStringSchema = zod_1.default.preprocess((arg) => {
	    if (typeof arg == 'string') {
	        return arg.slice(0, 10);
	    }
	}, exports.stringSchema.regex(dateRegex));
	exports.dateCoercionSchema = zod_1.default.coerce.date();
	exports.dateTimeOptionalTimeZoneRegex = /\d{4}-?(0\d|1[0-2])-?([0-2]\d|3[01])T\d\d:?\d\d:?\d\d(.\d\d\d)?([Z]|[+-]\d\d:?\d\d)?/;
	exports.dateTimeOptionalTimeZoneStringSchema = exports.stringSchema.regex(exports.dateTimeOptionalTimeZoneRegex);
	/**
	 * Schema that validates and converts datetime strings to ISO format for external API consumption.
	 * Accepts various datetime formats and ensures output is a valid ISO string.
	 */
	exports.isoDateTimeStringSchema = zod_1.default.preprocess((arg) => {
	    if (!arg || typeof arg !== 'string') {
	        throw new zod_1.default.ZodError([
	            {
	                code: 'custom',
	                message: 'Missing or invalid datetime value',
	                path: [],
	            },
	        ]);
	    }
	    try {
	        const date = new Date(arg);
	        if (isNaN(date.getTime())) {
	            throw new zod_1.default.ZodError([
	                {
	                    code: 'custom',
	                    message: `Invalid datetime format: ${arg}`,
	                    path: [],
	                },
	            ]);
	        }
	        return date.toISOString();
	    }
	    catch (error) {
	        if (error instanceof zod_1.default.ZodError) {
	            throw error;
	        }
	        throw new zod_1.default.ZodError([
	            {
	                code: 'custom',
	                message: `Failed to parse datetime: ${arg}`,
	                path: [],
	            },
	        ]);
	    }
	}, exports.stringSchema);
	exports.nonNegativeNumberSchema = zod_1.default.number().nonnegative();
	exports.positiveIntSchema = exports.nonNegativeNumberSchema.int();
	exports.intSchema = zod_1.default.int();
	exports.yearSchema = zod_1.default.number().gte(1900).lte(9999);
	exports.monthSchema = zod_1.default.number().gte(0).lte(11);
	exports.uuidSchema = exports.stringSchema.uuid();
	exports.emailSchema = zod_1.default.email();
	exports.dollarsToCentsSchema = zod_1.default.preprocess((arg) => {
	    if (typeof arg == 'number') {
	        return (0, money_utils_1.parseCents)(arg * 100);
	    }
	}, zod_1.default.number());
	const notSpecifiedTransform = (name) => {
	    if (!name || name.length === 0 || name === 'Not Specified') {
	        return 'Not Specified';
	    }
	    return name;
	};
	exports.notSpecifiedTransform = notSpecifiedTransform;
	exports.vendorSchema = zod_1.default.preprocess((arg) => {
	    if (typeof arg == 'string') {
	        return (0, exports.notSpecifiedTransform)(arg);
	    }
	}, zod_1.default.string());
	exports.monthYearSchema = zod_1.default.object({
	    month: exports.monthSchema,
	    year: exports.yearSchema,
	});
	exports.monthYearPeriodSchema = zod_1.default.object({
	    startMonthYear: exports.monthYearSchema,
	    endMonthYear: exports.monthYearSchema,
	});
	exports.monthYearPeriodNullableSchema = zod_1.default.object({
	    startMonthYear: exports.monthYearSchema.nullable(),
	    endMonthYear: exports.monthYearSchema.nullable(),
	});
	exports.monthYearStringSchema = zod_1.default.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
	exports.SSO_PROVIDER_NAME_REGEX = /^(oidc\.|saml\.)/;
	exports.ssoProviderNameSchema = zod_1.default.string().regex(exports.SSO_PROVIDER_NAME_REGEX, "Must start with 'oidc.' or 'saml.'");
	/** Intended to be used with dynamic arrays only. Static arrays are easily created with `z.enum`. */
	const enumSchemaFromDynamicArray = (array) => {
	    if (array.length === 0) {
	        throw new Error('Array must contain at least one element to create a Zod enum');
	    }
	    return zod_1.default.enum([array[0], ...array.slice(1)]);
	};
	exports.enumSchemaFromDynamicArray = enumSchemaFromDynamicArray;
	const parseOrThrowClientError = (schema, value) => {
	    const parsed = schema.safeParse(value);
	    if (!parsed.success) {
	        const errors = parsed.error.issues.map((zodError) => ({
	            code: zodError.code.toUpperCase(),
	            description: `${zodError.message} at ${zodError.path.join('.')} field`,
	        }));
	        throw (0, errors_1.createClientErrors)(errors);
	    }
	    return parsed.data;
	};
	exports.parseOrThrowClientError = parseOrThrowClientError;
	const isHttpUrl = (value) => {
	    try {
	        const { protocol } = new URL(value);
	        return protocol === 'https:' || protocol === 'http:';
	    }
	    catch {
	        return false;
	    }
	};
	/** Labels of letters/digits/hyphens (not starting/ending with a hyphen), at least one dot + a TLD. */
	const SINGLE_TENANT_DOMAIN_REGEX = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;
	/** Validates an email domain (e.g. "acme.com") for a single-tenant redirect; trims + lowercases. */
	exports.singleTenantEmailDomainSchema = zod_1.default
	    .string()
	    .transform((value) => value.trim().toLowerCase())
	    .refine((value) => value.length > 0, 'Domain is required.')
	    .refine((value) => !value.includes('@'), 'Enter just the domain, not an email address (e.g. acme.com).')
	    .refine((value) => SINGLE_TENANT_DOMAIN_REGEX.test(value), 'Enter a valid domain, e.g. acme.com.');
	/** Validates a single-tenant instance redirect URL; must be a full http(s) URL. */
	exports.singleTenantRedirectUrlSchema = zod_1.default
	    .string()
	    .transform((value) => value.trim())
	    .refine((value) => value.length > 0, 'Redirect URL is required.')
	    .refine((value) => isHttpUrl(value), 'Enter a full URL, e.g. https://acme.neo.tax.');
	/** Combined input schema for creating/updating a single-tenant domain redirect. */
	exports.singleTenantDomainRedirectInputSchema = zod_1.default.object({
	    emailDomain: exports.singleTenantEmailDomainSchema,
	    redirectUrl: exports.singleTenantRedirectUrlSchema,
	});
} (types_utils));

var slack_types = {};

var __importDefault$4 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(slack_types, "__esModule", { value: true });
slack_types.slackEventDtoSchema = void 0;
const zod_1$7 = __importDefault$4(zod);
const additionalDataSchema = zod_1$7.default.record(zod_1$7.default.string(), zod_1$7.default.string());
const slackEventBaseSchema = zod_1$7.default.object({
    eventName: zod_1$7.default.string(),
    additionalData: additionalDataSchema.optional(),
});
const companyInfoSchema = zod_1$7.default.object({
    userEmail: zod_1$7.default.string().optional(),
    companyId: zod_1$7.default.string(),
    companyName: zod_1$7.default.string(),
});
const userInfoSchema = zod_1$7.default.object({
    userEmail: zod_1$7.default.string(),
    companyId: zod_1$7.default.string().optional(),
    companyName: zod_1$7.default.string().optional(),
});
slack_types.slackEventDtoSchema = slackEventBaseSchema.and(companyInfoSchema.or(userInfoSchema));

var interfaces = {};

var errors = {};

var baseError_interface = {};

Object.defineProperty(baseError_interface, "__esModule", { value: true });
baseError_interface.BaseErrorSchema = void 0;
const zod_1$6 = zod;
/**
 * This schema should not be used to parse most errors especially known errors.
 * It should be used to create and extend new error schemas. It acts as a pattern.
 * `code` is always required whereas `description` should be either required or
 * replaced by other more descriptive properties.
 **/
baseError_interface.BaseErrorSchema = zod_1$6.z.object({
    code: zod_1$6.z.string().min(1),
    description: zod_1$6.z.string().min(1).optional(),
});

var graphqlError_interface = {};

Object.defineProperty(graphqlError_interface, "__esModule", { value: true });
graphqlError_interface.safeParseExtendedGraphQlError = graphqlError_interface.safeParseExtendedApolloError = graphqlError_interface.parseExtendedGraphQlError = graphqlError_interface.parseExtendedApolloError = void 0;
const zod_1$5 = zod;
/// HELPER FUNCTIONS
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeApolloErrorSchema = (errorSchema) => {
    const GraphQlErrorSchema = makeGraphQlErrorSchema(errorSchema);
    return zod_1$5.z.object({
        graphQLErrors: zod_1$5.z.array(GraphQlErrorSchema),
    });
};
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeGraphQlErrorSchema = (errorSchema) => zod_1$5.z.object({
    message: zod_1$5.z.string().min(1),
    extensions: zod_1$5.z.object({
        code: zod_1$5.z.string().min(1),
        errors: zod_1$5.z.array(errorSchema),
    }),
});
/// PARSING FUNCTIONS
const parseExtendedApolloError = (error, errorSchema) => {
    const ApolloErrorSchema = makeApolloErrorSchema(errorSchema);
    return ApolloErrorSchema.parse(error);
};
graphqlError_interface.parseExtendedApolloError = parseExtendedApolloError;
const parseExtendedGraphQlError = (error, errorSchema) => {
    const GraphQlErrorSchema = makeGraphQlErrorSchema(errorSchema);
    return GraphQlErrorSchema.parse(error);
};
graphqlError_interface.parseExtendedGraphQlError = parseExtendedGraphQlError;
const safeParseExtendedApolloError = (error, errorSchema) => {
    const ApolloErrorSchema = makeApolloErrorSchema(errorSchema);
    return ApolloErrorSchema.safeParse(error);
};
graphqlError_interface.safeParseExtendedApolloError = safeParseExtendedApolloError;
const safeParseExtendedGraphQlError = (error, errorSchema) => {
    const GraphQlErrorSchema = makeGraphQlErrorSchema(errorSchema);
    return GraphQlErrorSchema.safeParse(error);
};
graphqlError_interface.safeParseExtendedGraphQlError = safeParseExtendedGraphQlError;

var projectDeletionError_interface = {};

var __createBinding$2 = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault$2 = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar$2 = (commonjsGlobal && commonjsGlobal.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding$2(result, mod, k[i]);
        __setModuleDefault$2(result, mod);
        return result;
    };
})();
Object.defineProperty(projectDeletionError_interface, "__esModule", { value: true });
projectDeletionError_interface.RdProjectDeletionErrorSchema = projectDeletionError_interface.AscProjectDeletionErrorSchema = void 0;
const z$8 = __importStar$2(zod);
const base_error_interface_1$3 = baseError_interface;
projectDeletionError_interface.AscProjectDeletionErrorSchema = base_error_interface_1$3.BaseErrorSchema.extend({
    code: z$8.enum(['ASC_PROJECT_HAS_TICKETS']),
    description: z$8.string().min(1),
});
projectDeletionError_interface.RdProjectDeletionErrorSchema = base_error_interface_1$3.BaseErrorSchema.extend({
    code: z$8.enum(['RD_PROJECT_HAS_TICKETS']),
    description: z$8.string().min(1),
});

var questionAnswerError_interface = {};

var __createBinding$1 = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault$1 = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar$1 = (commonjsGlobal && commonjsGlobal.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding$1(result, mod, k[i]);
        __setModuleDefault$1(result, mod);
        return result;
    };
})();
Object.defineProperty(questionAnswerError_interface, "__esModule", { value: true });
questionAnswerError_interface.QuestionAnswerErrorSchema = void 0;
const z$7 = __importStar$1(zod);
const base_error_interface_1$2 = baseError_interface;
questionAnswerError_interface.QuestionAnswerErrorSchema = base_error_interface_1$2.BaseErrorSchema.extend({
    code: z$7.enum(['INVALID_ANSWER']),
    questionCode: z$7.string().min(1),
    description: z$7.string().min(1),
});

var standardError_interface = {};

Object.defineProperty(standardError_interface, "__esModule", { value: true });
standardError_interface.StandardErrorSchema = void 0;
const zod_1$4 = zod;
const base_error_interface_1$1 = baseError_interface;
/**
 * This schema can be used for generic errors when making a new schema is not necessary.
 **/
standardError_interface.StandardErrorSchema = base_error_interface_1$1.BaseErrorSchema.extend({
    code: zod_1$4.z.enum(['BAD_REQUEST', 'CONFLICT', 'NOT_FOUND']),
    description: zod_1$4.z.string(),
});

var projectDocumentParsingError_interface = {};

var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(projectDocumentParsingError_interface, "__esModule", { value: true });
projectDocumentParsingError_interface.DocumentNotParsableErrorSchema = void 0;
const z$6 = __importStar(zod);
const base_error_interface_1 = baseError_interface;
projectDocumentParsingError_interface.DocumentNotParsableErrorSchema = base_error_interface_1.BaseErrorSchema.extend({
    code: z$6.enum(['DOCUMENT_NOT_PARSABLE']),
    description: z$6.string().min(1),
});

var applyWeightedRdPercentageToExpensesError_interface = {};

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || (function () {
	    var ownKeys = function(o) {
	        ownKeys = Object.getOwnPropertyNames || function (o) {
	            var ar = [];
	            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
	            return ar;
	        };
	        return ownKeys(o);
	    };
	    return function (mod) {
	        if (mod && mod.__esModule) return mod;
	        var result = {};
	        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
	        __setModuleDefault(result, mod);
	        return result;
	    };
	})();
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.ApplyWeightedRdPercentageToExpensesErrorSchema = exports.ApplyWeightedRdPercentageToExpensesErrorCodes = void 0;
	const z = __importStar(zod);
	const base_error_interface_1 = baseError_interface;
	exports.ApplyWeightedRdPercentageToExpensesErrorCodes = {
	    INVALID_QUALIFIED_EXPENSES: 'APPLY_WEIGHTED_RD_PERCENTAGE_TO_EXPENSES_INVALID_QUALIFIED_EXPENSES',
	    INVALID_QUALIFIED_AMOUNT: 'APPLY_WEIGHTED_RD_PERCENTAGE_TO_EXPENSES_INVALID_QUALIFIED_AMOUNT',
	};
	exports.ApplyWeightedRdPercentageToExpensesErrorSchema = base_error_interface_1.BaseErrorSchema.extend({
	    code: z.enum([
	        exports.ApplyWeightedRdPercentageToExpensesErrorCodes.INVALID_QUALIFIED_EXPENSES,
	        exports.ApplyWeightedRdPercentageToExpensesErrorCodes.INVALID_QUALIFIED_AMOUNT,
	    ]),
	    description: z.string().min(1),
	});
} (applyWeightedRdPercentageToExpensesError_interface));

var organizationInvitationError_interface = {};

(function (exports) {
	var __createBinding = (commonjsGlobal && commonjsGlobal.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (commonjsGlobal && commonjsGlobal.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (commonjsGlobal && commonjsGlobal.__importStar) || (function () {
	    var ownKeys = function(o) {
	        ownKeys = Object.getOwnPropertyNames || function (o) {
	            var ar = [];
	            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
	            return ar;
	        };
	        return ownKeys(o);
	    };
	    return function (mod) {
	        if (mod && mod.__esModule) return mod;
	        var result = {};
	        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
	        __setModuleDefault(result, mod);
	        return result;
	    };
	})();
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.OrganizationInvitationErrorSchema = exports.OrganizationInvitationErrorCodes = void 0;
	const z = __importStar(zod);
	const base_error_interface_1 = baseError_interface;
	exports.OrganizationInvitationErrorCodes = {
	    // Acceptance failures
	    NOT_FOUND: 'INVITATION_NOT_FOUND',
	    EXPIRED: 'INVITATION_EXPIRED',
	    REVOKED: 'INVITATION_REVOKED',
	    ALREADY_ACCEPTED: 'INVITATION_ALREADY_ACCEPTED',
	    EMAIL_MISMATCH: 'INVITATION_EMAIL_MISMATCH',
	    SSO_PROVIDER_MISMATCH: 'SSO_PROVIDER_MISMATCH',
	    // Invitee already belongs to an organization at acceptance time. Distinct from the
	    // creation-time CONFLICT_MEMBER_OF_OTHER_ORG: that one is shown to the inviting admin
	    // ("can't invite this person"); this one is shown to the invitee ("you can't accept").
	    // The invitation is also marked terminal when this fires so the admin's view reflects
	    // reality rather than showing a perpetually pending invite.
	    INVITEE_ALREADY_IN_ORGANIZATION: 'INVITEE_ALREADY_IN_ORGANIZATION',
	    // Conflicts at invitation creation time
	    CONFLICT_ALREADY_MEMBER: 'INVITATION_CONFLICT_ALREADY_MEMBER',
	    CONFLICT_MEMBER_OF_OTHER_ORG: 'INVITATION_CONFLICT_MEMBER_OF_OTHER_ORG',
	    CONFLICT_PENDING_TO_OTHER_ORG: 'INVITATION_CONFLICT_PENDING_TO_OTHER_ORG',
	    // Resend rate limit
	    RESEND_TOO_FREQUENT: 'INVITATION_RESEND_TOO_FREQUENT',
	};
	exports.OrganizationInvitationErrorSchema = base_error_interface_1.BaseErrorSchema.extend({
	    code: z.enum([
	        exports.OrganizationInvitationErrorCodes.NOT_FOUND,
	        exports.OrganizationInvitationErrorCodes.EXPIRED,
	        exports.OrganizationInvitationErrorCodes.REVOKED,
	        exports.OrganizationInvitationErrorCodes.ALREADY_ACCEPTED,
	        exports.OrganizationInvitationErrorCodes.EMAIL_MISMATCH,
	        exports.OrganizationInvitationErrorCodes.SSO_PROVIDER_MISMATCH,
	        exports.OrganizationInvitationErrorCodes.INVITEE_ALREADY_IN_ORGANIZATION,
	        exports.OrganizationInvitationErrorCodes.CONFLICT_ALREADY_MEMBER,
	        exports.OrganizationInvitationErrorCodes.CONFLICT_MEMBER_OF_OTHER_ORG,
	        exports.OrganizationInvitationErrorCodes.CONFLICT_PENDING_TO_OTHER_ORG,
	        exports.OrganizationInvitationErrorCodes.RESEND_TOO_FREQUENT,
	    ]),
	    description: z.string().min(1),
	});
} (organizationInvitationError_interface));

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.OrganizationInvitationErrorCodes = exports.OrganizationInvitationErrorSchema = exports.ApplyWeightedRdPercentageToExpensesErrorCodes = exports.ApplyWeightedRdPercentageToExpensesErrorSchema = exports.DocumentNotParsableErrorSchema = exports.StandardErrorSchema = exports.QuestionAnswerErrorSchema = exports.RdProjectDeletionErrorSchema = exports.AscProjectDeletionErrorSchema = exports.safeParseExtendedGraphQlError = exports.safeParseExtendedApolloError = exports.parseExtendedGraphQlError = exports.parseExtendedApolloError = exports.BaseErrorSchema = void 0;
	var base_error_interface_1 = baseError_interface;
	Object.defineProperty(exports, "BaseErrorSchema", { enumerable: true, get: function () { return base_error_interface_1.BaseErrorSchema; } });
	var graphql_error_interface_1 = graphqlError_interface;
	Object.defineProperty(exports, "parseExtendedApolloError", { enumerable: true, get: function () { return graphql_error_interface_1.parseExtendedApolloError; } });
	Object.defineProperty(exports, "parseExtendedGraphQlError", { enumerable: true, get: function () { return graphql_error_interface_1.parseExtendedGraphQlError; } });
	Object.defineProperty(exports, "safeParseExtendedApolloError", { enumerable: true, get: function () { return graphql_error_interface_1.safeParseExtendedApolloError; } });
	Object.defineProperty(exports, "safeParseExtendedGraphQlError", { enumerable: true, get: function () { return graphql_error_interface_1.safeParseExtendedGraphQlError; } });
	var project_deletion_error_interface_1 = projectDeletionError_interface;
	Object.defineProperty(exports, "AscProjectDeletionErrorSchema", { enumerable: true, get: function () { return project_deletion_error_interface_1.AscProjectDeletionErrorSchema; } });
	Object.defineProperty(exports, "RdProjectDeletionErrorSchema", { enumerable: true, get: function () { return project_deletion_error_interface_1.RdProjectDeletionErrorSchema; } });
	var question_answer_error_interface_1 = questionAnswerError_interface;
	Object.defineProperty(exports, "QuestionAnswerErrorSchema", { enumerable: true, get: function () { return question_answer_error_interface_1.QuestionAnswerErrorSchema; } });
	var standard_error_interface_1 = standardError_interface;
	Object.defineProperty(exports, "StandardErrorSchema", { enumerable: true, get: function () { return standard_error_interface_1.StandardErrorSchema; } });
	var project_document_parsing_error_interface_1 = projectDocumentParsingError_interface;
	Object.defineProperty(exports, "DocumentNotParsableErrorSchema", { enumerable: true, get: function () { return project_document_parsing_error_interface_1.DocumentNotParsableErrorSchema; } });
	var apply_weighted_rd_percentage_to_expenses_error_interface_1 = applyWeightedRdPercentageToExpensesError_interface;
	Object.defineProperty(exports, "ApplyWeightedRdPercentageToExpensesErrorSchema", { enumerable: true, get: function () { return apply_weighted_rd_percentage_to_expenses_error_interface_1.ApplyWeightedRdPercentageToExpensesErrorSchema; } });
	Object.defineProperty(exports, "ApplyWeightedRdPercentageToExpensesErrorCodes", { enumerable: true, get: function () { return apply_weighted_rd_percentage_to_expenses_error_interface_1.ApplyWeightedRdPercentageToExpensesErrorCodes; } });
	var organization_invitation_error_interface_1 = organizationInvitationError_interface;
	Object.defineProperty(exports, "OrganizationInvitationErrorSchema", { enumerable: true, get: function () { return organization_invitation_error_interface_1.OrganizationInvitationErrorSchema; } });
	Object.defineProperty(exports, "OrganizationInvitationErrorCodes", { enumerable: true, get: function () { return organization_invitation_error_interface_1.OrganizationInvitationErrorCodes; } });
} (errors));

var webApp = {};

var ssoState_interface = {};

var __importDefault$3 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(ssoState_interface, "__esModule", { value: true });
ssoState_interface.SsoStateSchema = void 0;
const zod_1$3 = __importDefault$3(zod);
ssoState_interface.SsoStateSchema = zod_1$3.default.object({
    existingQueryParams: zod_1$3.default.string().optional(),
    successRedirectUrl: zod_1$3.default.string(),
    errorRedirectUrl: zod_1$3.default.string(),
});

var intuitSsoState_interface = {};

var baseCallbackState_interface = {};

Object.defineProperty(baseCallbackState_interface, "__esModule", { value: true });
baseCallbackState_interface.BaseCallbackPayloadState = void 0;
const types_utils_1$1 = types_utils;
baseCallbackState_interface.BaseCallbackPayloadState = {
    userId: types_utils_1$1.stringSchema,
    companyId: types_utils_1$1.stringSchema,
    existingQueryParams: types_utils_1$1.stringSchema.optional(),
    successRedirectUrl: types_utils_1$1.stringSchema,
    errorRedirectUrl: types_utils_1$1.stringSchema,
};

(function (exports) {
	var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
	    return (mod && mod.__esModule) ? mod : { "default": mod };
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.QuickbooksCallbackPayloadStateSchema = exports.IntuitSsoCallbackPayloadStateSchema = exports.QuickbooksDataLinkingCallbackPayloadStateSchema = void 0;
	const zod_1 = __importDefault(zod);
	const types_utils_1 = types_utils;
	const base_callback_state_interface_1 = baseCallbackState_interface;
	exports.QuickbooksDataLinkingCallbackPayloadStateSchema = zod_1.default.object({
	    ...base_callback_state_interface_1.BaseCallbackPayloadState,
	    isSso: zod_1.default.literal(false),
	});
	exports.IntuitSsoCallbackPayloadStateSchema = zod_1.default.object({
	    successRedirectUrl: types_utils_1.stringSchema,
	    errorRedirectUrl: types_utils_1.stringSchema,
	    isSso: zod_1.default.literal(true),
	});
	exports.QuickbooksCallbackPayloadStateSchema = zod_1.default.union([
	    exports.QuickbooksDataLinkingCallbackPayloadStateSchema,
	    exports.IntuitSsoCallbackPayloadStateSchema,
	]);
} (intuitSsoState_interface));

var gustoSsoState_interface = {};

var __importDefault$2 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(gustoSsoState_interface, "__esModule", { value: true });
gustoSsoState_interface.GustoCallbackPayloadStateSchema = void 0;
const zod_1$2 = __importDefault$2(zod);
const base_callback_state_interface_1$1 = baseCallbackState_interface;
gustoSsoState_interface.GustoCallbackPayloadStateSchema = zod_1$2.default.object(base_callback_state_interface_1$1.BaseCallbackPayloadState);

var thomsonReutersSsoState_interface = {};

var __importDefault$1 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(thomsonReutersSsoState_interface, "__esModule", { value: true });
thomsonReutersSsoState_interface.ThomsonReutersCallbackPayloadStateSchema = void 0;
const zod_1$1 = __importDefault$1(zod);
const types_utils_1 = types_utils;
const base_callback_state_interface_1 = baseCallbackState_interface;
thomsonReutersSsoState_interface.ThomsonReutersCallbackPayloadStateSchema = zod_1$1.default.object({
    ...base_callback_state_interface_1.BaseCallbackPayloadState,
    action: types_utils_1.stringSchema,
    binderId: types_utils_1.stringSchema,
    taxYear: types_utils_1.yearSchema,
    userId: types_utils_1.stringSchema,
});

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.ThomsonReutersCallbackPayloadStateSchema = exports.GustoCallbackPayloadStateSchema = exports.QuickbooksCallbackPayloadStateSchema = exports.IntuitSsoCallbackPayloadStateSchema = exports.QuickbooksDataLinkingCallbackPayloadStateSchema = exports.SsoStateSchema = void 0;
	var sso_state_interface_1 = ssoState_interface;
	Object.defineProperty(exports, "SsoStateSchema", { enumerable: true, get: function () { return sso_state_interface_1.SsoStateSchema; } });
	var intuit_sso_state_interface_1 = intuitSsoState_interface;
	Object.defineProperty(exports, "QuickbooksDataLinkingCallbackPayloadStateSchema", { enumerable: true, get: function () { return intuit_sso_state_interface_1.QuickbooksDataLinkingCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "IntuitSsoCallbackPayloadStateSchema", { enumerable: true, get: function () { return intuit_sso_state_interface_1.IntuitSsoCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "QuickbooksCallbackPayloadStateSchema", { enumerable: true, get: function () { return intuit_sso_state_interface_1.QuickbooksCallbackPayloadStateSchema; } });
	var gusto_sso_state_interface_1 = gustoSsoState_interface;
	Object.defineProperty(exports, "GustoCallbackPayloadStateSchema", { enumerable: true, get: function () { return gusto_sso_state_interface_1.GustoCallbackPayloadStateSchema; } });
	var thomson_reuters_sso_state_interface_1 = thomsonReutersSsoState_interface;
	Object.defineProperty(exports, "ThomsonReutersCallbackPayloadStateSchema", { enumerable: true, get: function () { return thomson_reuters_sso_state_interface_1.ThomsonReutersCallbackPayloadStateSchema; } });
} (webApp));

var finch_interface = {};

// Available provider keys
Object.defineProperty(finch_interface, "__esModule", { value: true });
finch_interface.FinchProviderKeySchema = void 0;
const zod_1 = zod;
/**
 * https://developer.tryfinch.com/integrations/providers
 */
finch_interface.FinchProviderKeySchema = zod_1.z.enum([
    'justworks',
    'trinet',
    'quickbooks',
    'adp_run',
    'adp_workforce_now',
    'ceridian_dayforce',
    'paychex_flex',
    'paylocity',
    'workday',
    'zenefits',
]);

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.FinchProviderKeySchema = exports.ThomsonReutersCallbackPayloadStateSchema = exports.GustoCallbackPayloadStateSchema = exports.QuickbooksCallbackPayloadStateSchema = exports.IntuitSsoCallbackPayloadStateSchema = exports.QuickbooksDataLinkingCallbackPayloadStateSchema = exports.SsoStateSchema = exports.OrganizationInvitationErrorCodes = exports.OrganizationInvitationErrorSchema = exports.DocumentNotParsableErrorSchema = exports.ApplyWeightedRdPercentageToExpensesErrorSchema = exports.QuestionAnswerErrorSchema = exports.RdProjectDeletionErrorSchema = exports.AscProjectDeletionErrorSchema = exports.StandardErrorSchema = exports.BaseErrorSchema = exports.safeParseExtendedGraphQlError = exports.safeParseExtendedApolloError = exports.parseExtendedGraphQlError = exports.parseExtendedApolloError = void 0;
	var errors_1 = errors;
	Object.defineProperty(exports, "parseExtendedApolloError", { enumerable: true, get: function () { return errors_1.parseExtendedApolloError; } });
	Object.defineProperty(exports, "parseExtendedGraphQlError", { enumerable: true, get: function () { return errors_1.parseExtendedGraphQlError; } });
	Object.defineProperty(exports, "safeParseExtendedApolloError", { enumerable: true, get: function () { return errors_1.safeParseExtendedApolloError; } });
	Object.defineProperty(exports, "safeParseExtendedGraphQlError", { enumerable: true, get: function () { return errors_1.safeParseExtendedGraphQlError; } });
	Object.defineProperty(exports, "BaseErrorSchema", { enumerable: true, get: function () { return errors_1.BaseErrorSchema; } });
	Object.defineProperty(exports, "StandardErrorSchema", { enumerable: true, get: function () { return errors_1.StandardErrorSchema; } });
	Object.defineProperty(exports, "AscProjectDeletionErrorSchema", { enumerable: true, get: function () { return errors_1.AscProjectDeletionErrorSchema; } });
	Object.defineProperty(exports, "RdProjectDeletionErrorSchema", { enumerable: true, get: function () { return errors_1.RdProjectDeletionErrorSchema; } });
	Object.defineProperty(exports, "QuestionAnswerErrorSchema", { enumerable: true, get: function () { return errors_1.QuestionAnswerErrorSchema; } });
	Object.defineProperty(exports, "ApplyWeightedRdPercentageToExpensesErrorSchema", { enumerable: true, get: function () { return errors_1.ApplyWeightedRdPercentageToExpensesErrorSchema; } });
	Object.defineProperty(exports, "DocumentNotParsableErrorSchema", { enumerable: true, get: function () { return errors_1.DocumentNotParsableErrorSchema; } });
	Object.defineProperty(exports, "OrganizationInvitationErrorSchema", { enumerable: true, get: function () { return errors_1.OrganizationInvitationErrorSchema; } });
	Object.defineProperty(exports, "OrganizationInvitationErrorCodes", { enumerable: true, get: function () { return errors_1.OrganizationInvitationErrorCodes; } });
	var web_app_1 = webApp;
	Object.defineProperty(exports, "SsoStateSchema", { enumerable: true, get: function () { return web_app_1.SsoStateSchema; } });
	Object.defineProperty(exports, "QuickbooksDataLinkingCallbackPayloadStateSchema", { enumerable: true, get: function () { return web_app_1.QuickbooksDataLinkingCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "IntuitSsoCallbackPayloadStateSchema", { enumerable: true, get: function () { return web_app_1.IntuitSsoCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "QuickbooksCallbackPayloadStateSchema", { enumerable: true, get: function () { return web_app_1.QuickbooksCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "GustoCallbackPayloadStateSchema", { enumerable: true, get: function () { return web_app_1.GustoCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "ThomsonReutersCallbackPayloadStateSchema", { enumerable: true, get: function () { return web_app_1.ThomsonReutersCallbackPayloadStateSchema; } });
	var finch_interface_1 = finch_interface;
	Object.defineProperty(exports, "FinchProviderKeySchema", { enumerable: true, get: function () { return finch_interface_1.FinchProviderKeySchema; } });
} (interfaces));

var array_utils = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.deduplicate = exports.chunk = exports.removeElement = exports.range = exports.moveElement = exports.insertElements = exports.calculateStep = void 0;
	const errors_1 = errors$1;
	const calculateStep = (difference, step) => {
	    if (typeof step === 'number') {
	        if ((step > 0 && difference < 0) || (step < 0 && difference > 0)) {
	            throw (0, errors_1.createError)('CalculateStepError', `step: ${step}, difference: ${difference}`);
	        }
	        return step;
	    }
	    if (difference > 0) {
	        return 1;
	    }
	    return -1;
	};
	exports.calculateStep = calculateStep;
	/**
	 * Returns a new array with the elements at `index` inserted.
	 */
	const insertElements = (arr, index, ...items) => {
	    if (index < 0 || index > arr.length) {
	        throw new Error(`index is out of bounds: ${index}`);
	    }
	    const newArr = [...arr];
	    newArr.splice(index, 0, ...items);
	    return newArr;
	};
	exports.insertElements = insertElements;
	/**
	 * Returns a new array moving the element at `fromIndex` to `toIndex`.
	 */
	const moveElement = (arr, fromIndex, toIndex) => {
	    if (fromIndex < 0 || fromIndex >= arr.length) {
	        throw new Error(`fromIndex is out of bounds: ${fromIndex}`);
	    }
	    if (toIndex < 0 || toIndex >= arr.length) {
	        throw new Error(`toIndex is out of bounds: ${toIndex}`);
	    }
	    const element = arr[fromIndex];
	    const newArr = [...arr];
	    newArr.splice(fromIndex, 1);
	    newArr.splice(toIndex, 0, element);
	    return newArr;
	};
	exports.moveElement = moveElement;
	/**
	 * Range function with configurable step and non-inclusive `end` value.
	 */
	const range = (start, end, step) => {
	    if (step === 0) {
	        throw (0, errors_1.createError)('RangeStepError', 'Step cannot be 0');
	    }
	    const difference = end - start;
	    if (typeof step === 'number' && ((step > 0 && difference < 0) || (step < 0 && difference > 0))) {
	        throw (0, errors_1.createError)('RangeStepError', `The step ${step} is not compatible with the range ${start} to ${end}. You can not reach the end from the current start using that step polarity.`);
	    }
	    const calculatedStep = (0, exports.calculateStep)(difference, step);
	    const length = Math.floor(difference / calculatedStep);
	    return Array.from({ length }, (_, i) => start + i * calculatedStep);
	};
	exports.range = range;
	/**
	 * Returns a new array with the element at `index` removed.
	 */
	const removeElement = (arr, index, deleteCount = 1) => {
	    if (index < 0 || index >= arr.length) {
	        throw new Error(`index is out of bounds: ${index}`);
	    }
	    const newArr = [...arr];
	    newArr.splice(index, deleteCount);
	    return newArr;
	};
	exports.removeElement = removeElement;
	const chunk = (arr, size) => Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, (i + 1) * size));
	exports.chunk = chunk;
	const deduplicate = (arr) => [...new Set(arr)];
	exports.deduplicate = deduplicate;
} (array_utils));

var pageStatus = {};

var pageStatus_utils = {};

Object.defineProperty(pageStatus_utils, "__esModule", { value: true });
pageStatus_utils.questionKeyForPyr = pageStatus_utils.questionKeyForQre = void 0;
const questionKeyForQre = (taxYearStartDate) => `qre-${taxYearStartDate}`;
pageStatus_utils.questionKeyForQre = questionKeyForQre;
const questionKeyForPyr = (taxYearStartDate) => `pyr-${taxYearStartDate}`;
pageStatus_utils.questionKeyForPyr = questionKeyForPyr;

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.questionKeyForPyr = exports.questionKeyForQre = void 0;
	var page_status_utils_1 = pageStatus_utils;
	Object.defineProperty(exports, "questionKeyForQre", { enumerable: true, get: function () { return page_status_utils_1.questionKeyForQre; } });
	Object.defineProperty(exports, "questionKeyForPyr", { enumerable: true, get: function () { return page_status_utils_1.questionKeyForPyr; } });
} (pageStatus));

var csv = {};

var csv_constants = {};

(function (exports) {
	// Why do we have two sets of column labels?
	// The regular labels are used for validation and parsing and should rarely change.
	// The augmented labels are used to provide additional information about the column to the user.
	// This additional information is ignored during validation and parsing.
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS = exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS = exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS = void 0;
	exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS = {
	    rdCategory: 'Expense Category',
	    name: 'Name',
	    amount: 'Amount',
	    state: 'State',
	    country: 'Country',
	    account: 'Account',
	    department: 'Department',
	    costCenter: 'Cost Center',
	    entity: 'Entity',
	};
	// WARNING: Don't use line breaks (\n) in the headers, because they won't display consistently in
	// spreadsheet apps with unpredictable wrapping that can confuse users.
	exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = {
	    rdCategory: exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS.rdCategory +
	        ' (Required) (Enter ONE category for each expense: Contractors, Cloud, Patents, Supplies)',
	    name: exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS.name + ' (Required)',
	    amount: exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS.amount + ' (Required)',
	    state: exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS.state + ' (Required if Country is US) (Use the two-letter code)',
	    country: exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS.country + ' (Recommended)',
	    account: exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS.account + ' (Recommended)',
	    department: exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS.department + ' (Recommended)',
	    costCenter: exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS.costCenter + ' (Recommended)',
	    entity: exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS.entity + ' (Required when multiple entities perform R&D)',
	};
	exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS = {
	    fullName: 'Name',
	    title: 'Job Title',
	    department: 'Department',
	    pid: 'Employee ID',
	    managerPid: 'Manager ID',
	    email: 'Work Email',
	    employeeWages: 'W-2 Box 1 Wages',
	    contractorWages: 'Contractor Wages',
	    state: 'State',
	    country: 'Country',
	    hireDate: 'Hire Date',
	    terminationDate: 'Termination Date',
	    costCenter: 'Cost Center',
	    entity: 'Entity',
	};
	// WARNING: Don't use line breaks (\n) in the headers, because they won't display consistently in
	// spreadsheet apps with unpredictable wrapping that can confuse users.
	exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = {
	    fullName: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.fullName + ' (Required)',
	    title: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.title + ' (Required)',
	    department: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.department + ' (Recommended)',
	    pid: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.pid + ' (Recommended)',
	    managerPid: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.managerPid + ' (Recommended)',
	    email: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.email + ' (Recommended)',
	    employeeWages: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.employeeWages +
	        ' (Required) (For international employees, enter the equivalent)',
	    contractorWages: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.contractorWages +
	        ' (Required for Contractors) (For non-W2 contractors, enter the amount in this column)',
	    state: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.state + ' (Required if Country is US) (Use the two-letter code)',
	    country: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.country + ' (Required)',
	    hireDate: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.hireDate + ' (Recommended) (Format: MM/DD/YYYY)',
	    terminationDate: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.terminationDate + ' (Recommended) (Format: MM/DD/YYYY)',
	    costCenter: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.costCenter + ' (Recommended)',
	    entity: exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS.entity + ' (Required when multiple entities perform R&D)',
	};
	exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS = {
	    fullName: 'Name',
	    title: 'Job Title',
	    department: 'Department',
	    email: 'Work Email',
	    salaryCompensationDollars: 'Salary/Benefits Cost',
	    stockCompensationDollars: 'Stock Based Comp Cost',
	    country: 'Country',
	    pid: 'Employee ID',
	    managerPid: 'Manager ID',
	    hireDate: 'Hire Date',
	    terminationDate: 'Termination Date',
	    costCenter: 'Cost Center',
	    entity: 'Entity',
	};
	// WARNING: Don't use line breaks (\n) in the headers, because they won't display consistently in
	// spreadsheet apps with unpredictable wrapping that can confuse users.
	exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = {
	    fullName: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.fullName + ' (Required)',
	    title: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.title + ' (Required)',
	    department: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.department + ' (Recommended)',
	    email: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.email + ' (Required)',
	    salaryCompensationDollars: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.salaryCompensationDollars + ' (Required)',
	    stockCompensationDollars: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.stockCompensationDollars + ' (Recommended)',
	    country: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.country + ' (Required)',
	    pid: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.pid + ' (Recommended)',
	    managerPid: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.managerPid + ' (Recommended)',
	    hireDate: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.hireDate + ' (Recommended) (Format: MM/DD/YYYY)',
	    terminationDate: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.terminationDate + ' (Recommended) (Format: MM/DD/YYYY)',
	    costCenter: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.costCenter + ' (Recommended)',
	    entity: exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS.entity + ' (Required when multiple entities perform R&D)',
	};
} (csv_constants));

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS = exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS = exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS = void 0;
	var csv_constants_1 = csv_constants;
	Object.defineProperty(exports, "GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS", { enumerable: true, get: function () { return csv_constants_1.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS; } });
	Object.defineProperty(exports, "GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED", { enumerable: true, get: function () { return csv_constants_1.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED; } });
	Object.defineProperty(exports, "GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS", { enumerable: true, get: function () { return csv_constants_1.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS; } });
	Object.defineProperty(exports, "GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED", { enumerable: true, get: function () { return csv_constants_1.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED; } });
	Object.defineProperty(exports, "GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS", { enumerable: true, get: function () { return csv_constants_1.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS; } });
	Object.defineProperty(exports, "GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED", { enumerable: true, get: function () { return csv_constants_1.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED; } });
} (csv));

var function_utils = {};

Object.defineProperty(function_utils, "__esModule", { value: true });
function_utils.throttle = void 0;
const throttle = (fn, delay) => {
    let lastCallTime = 0;
    const throttledFn = (...args) => {
        // Using Date.now() allows the throttle function to be more precise and
        // reliable in managing the rate at which the original function is called.
        const now = Date.now();
        const timeSinceLastCall = now - lastCallTime;
        if (timeSinceLastCall >= delay) {
            lastCallTime = now;
            fn(...args);
            return;
        }
    };
    return throttledFn;
};
function_utils.throttle = throttle;

var loginMethodHeader = {};

Object.defineProperty(loginMethodHeader, "__esModule", { value: true });
loginMethodHeader.LOGIN_METHOD_WINDOW_SECONDS = loginMethodHeader.LOGIN_METHOD_MAGIC_LINK = loginMethodHeader.LOGIN_METHOD_HEADER_NAME = void 0;
/**
 * Cross-workspace contract for disambiguating magic-link from email/password logins.
 *
 * Firebase reports BOTH email/password and email-link (magic-link) sign-ins as
 * `sign_in_provider === 'password'`, so the two are indistinguishable from the decoded token alone.
 * The web app stamps this short-lived request header on a magic-link sign-in and the server's auth
 * guard reads it to record the correct login method in the audit log.
 *
 * It travels as a request header rather than a cookie because the API is a different origin than the
 * web app in every environment (a different port locally, an `*.api.neo.tax` subdomain in
 * staging/production). A host-only cookie set on the web-app origin is never sent to `/graphql`, so
 * the cookie this replaces always read as empty server-side and every magic-link login mis-recorded
 * as EMAIL_PASSWORD. Custom request headers ride cross-origin under the API's `origin: '*'` CORS, the
 * same way the existing `version` header already does.
 *
 * The value is client-supplied, so it is an audit-quality hint, not a security control — a caller
 * could stamp it on any login. That trust level is identical to the cookie it replaces.
 */
// Lowercase: HTTP header names are case-insensitive and Express exposes them lowercased.
loginMethodHeader.LOGIN_METHOD_HEADER_NAME = 'x-login-method';
loginMethodHeader.LOGIN_METHOD_MAGIC_LINK = 'magic_link';
// The web app keeps stamping the header for this window after a magic-link sign-in — long enough to
// cover the audit log's 60s fresh-login window — then stops.
loginMethodHeader.LOGIN_METHOD_WINDOW_SECONDS = 62;

var blockingFunction = {};

/**
 * Cross-workspace contract for telling the web app *which* sign-in failure happened, so its copy can differ
 * wherever the remedy does.
 *
 * The codes travel by two routes, hence the two objects below. A sign-in blocking function's rejection reaches
 * the browser as `auth/internal-error` whatever the cause, carrying the function's own message inside the error
 * string, so those codes are matched within that message. Provisioning refuses inside a mutation, so its codes
 * arrive as coded GraphQL errors. Either way the web app keys on the code rather than on prose that could be
 * reworded, which is why they live here instead of as string literals on each side.
 *
 * Of everything these distinguish, only a backend that could not answer is worth retrying — whether Identity
 * Platform timed out on the function or the function could not read the account. Showing "please try again"
 * for any of the rest is a loop that cannot succeed.
 */
Object.defineProperty(blockingFunction, "__esModule", { value: true });
blockingFunction.EMAIL_SESSION_CLAIM = blockingFunction.ProvisioningErrorCodes = blockingFunction.BlockingFunctionRejectionCodes = void 0;
/** Markers the sign-in blocking function embeds in the rejection Identity Platform relays to the browser. */
blockingFunction.BlockingFunctionRejectionCodes = {
    /** The identity provider authenticated the user but published no email address under any name we searched. */
    NO_PROVIDER_EMAIL: 'NEOTAX_NO_PROVIDER_EMAIL',
    /**
     * An enterprise provider would have joined an account that already signs in another way. Refused because an
     * OIDC/SAML provider asserts its own email addresses, so accepting the match would let anyone controlling a
     * configured provider claim an existing account. The user can resolve it by signing in as they did originally.
     */
    ENTERPRISE_LINK_REFUSED: 'NEOTAX_ENTERPRISE_LINK_REFUSED',
    /**
     * The account could not be read to vet an enterprise sign-in, so the sign-in was refused rather than let
     * through unvetted. A failed or slow read, not a conflict: a retry normally succeeds.
     */
    ACCOUNT_UNREADABLE: 'NEOTAX_ACCOUNT_UNREADABLE',
    /**
     * The email address is on a domain we refuse accounts for (BLACKLISTED_DOMAINS — e.g. disposable-email
     * providers). Applied at both account creation and sign-in, so a junk-domain account is turned away
     * before it exists and any that predate the list can no longer sign in.
     */
    BLACKLISTED_DOMAIN: 'NEOTAX_BLACKLISTED_DOMAIN',
};
/**
 * Codes on the GraphQL errors provisioning raises when it will not attach a sign-in to an existing account.
 *
 * Neither code's copy may name the provider the organization requires: reaching this point takes only
 * persuading some identity provider to assert an address, so naming it would tell whoever did that what to
 * configure next. The id belongs on the login audit row.
 */
blockingFunction.ProvisioningErrorCodes = {
    /** The organization requires a different enterprise provider than the one signing in. */
    SSO_PROVIDER_MISMATCH: 'SSO_PROVIDER_MISMATCH',
    /** The address belongs to an account whose sign-in identity this is not, for any other reason. */
    ACCOUNT_LINKED_TO_ANOTHER_IDENTITY: 'ACCOUNT_LINKED_TO_ANOTHER_IDENTITY',
};
/**
 * Session claim carrying the address for providers Firebase stored without one.
 *
 * A blocking function cannot write `email` onto the Firebase user record — the reserved fields it may
 * set are `emailVerified`, `displayName`, `photoURL`, `customClaims` and session claims — so for a
 * provider that publishes the address under a non-standard name, `user.email` stays empty and this
 * claim is the only place the address appears. Both the web app reading it off the signed-in session
 * and the backend reading it off the verified token depend on the same name, which is why it lives
 * here rather than as a literal in each.
 *
 * A *session* claim rather than a custom one, so the address lives only in this session's tokens
 * instead of being persisted onto the Firebase user.
 */
blockingFunction.EMAIL_SESSION_CLAIM = 'neoTaxEmail';

var statusCodes_utils = {};

Object.defineProperty(statusCodes_utils, "__esModule", { value: true });
statusCodes_utils.ErrorStatusToHttpStatusCode = void 0;
const enums_1 = enums;
/** Should be used for HTTP REST requests, generally not GraphQL. */
statusCodes_utils.ErrorStatusToHttpStatusCode = {
    [enums_1.ErrorStatusCode.OUTDATED_VERSION]: 400,
    [enums_1.ErrorStatusCode.UNAUTHENTICATED]: 401,
    [enums_1.ErrorStatusCode.UNAUTHORIZED]: 403,
    [enums_1.ErrorStatusCode.TOO_MANY_REQUESTS]: 429,
    [enums_1.ErrorStatusCode.INTERNAL_SERVER_ERROR]: 500,
};

var ascProjects = {};

var ascProjectsAllocation_utils = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.getAverageAllocationInPeriod = exports.updateAllocationsInPeriod = exports.updateAllocationPercent = exports.getAdjustedPercent = exports.calculateMaxLimit = exports.sortAllAscProjectAllocations = exports.assignPriority = exports.roundProjectAllocationPercents = exports.roundAllocationsToTotal = exports.removeUseAllocationPercent = exports.getLatestAllocationPercent = exports.ALLOCATION_DIFF_THRESHOLD = exports.AllocationPriority = void 0;
	const constants_1 = constants$1;
	const errors_1 = errors$1;
	const month_year_utils_1 = monthYear_utils;
	const number_utils_1 = number_utils;
	var AllocationPriority;
	(function (AllocationPriority) {
	    AllocationPriority["LOW"] = "LOW";
	    AllocationPriority["MEDIUM"] = "MEDIUM";
	    AllocationPriority["HIGH"] = "HIGH";
	})(AllocationPriority || (exports.AllocationPriority = AllocationPriority = {}));
	const ALLOCATION_PRIORITY_ORDER = [AllocationPriority.HIGH, AllocationPriority.MEDIUM, AllocationPriority.LOW];
	exports.ALLOCATION_DIFF_THRESHOLD = 0.01;
	const getLatestAllocationPercent = (ascProjectMonthAllocation) => ascProjectMonthAllocation.useAllocationPercent ?? ascProjectMonthAllocation.allocationPercent;
	exports.getLatestAllocationPercent = getLatestAllocationPercent;
	const removeUseAllocationPercent = (ascProjectIdToPmas) => Object.fromEntries(Object.entries(ascProjectIdToPmas).map(([ascProjectId, ascProjectAllocations]) => [
	    ascProjectId,
	    ascProjectAllocations.map((ascProjectAllocation) => ({
	        ...ascProjectAllocation,
	        useAllocationPercent: null,
	    })),
	]));
	exports.removeUseAllocationPercent = removeUseAllocationPercent;
	/**
	 * Rounds allocation percentages and adjusts them to ensure the total equals at most 100%.
	 * If rounding causes the total to exceed 100%, the largest allocations are reduced.
	 * This prevents negative non-project percentages and ensures consistent rounding behavior.
	 *
	 * @param allocations - Array of raw allocation percentages (can have decimals)
	 * @returns Array of rounded percentages that sum to at most 100%
	 */
	const roundAllocationsToTotal = (allocations) => {
	    // Round all allocations
	    const rounded = allocations.map((allocation) => (0, number_utils_1.roundToNearest)(allocation, constants_1.USER_ROUNDING_NUM_DECIMALS));
	    let total = rounded.reduce((sum, val) => sum + val, 0);
	    // If total exceeds 100%, adjust the largest allocations down
	    if (total > 100) {
	        const excess = total - 100;
	        const originalTotal = total;
	        // Create array of indices sorted by value (descending) to process largest first
	        const indexedValues = rounded.map((value, index) => ({ value, index })).sort((a, b) => b.value - a.value);
	        // Iterate through allocations, applying proportional reductions until excess is consumed
	        // Calculate what percentage each allocation represents of the original total
	        let remainingExcess = excess;
	        for (const item of indexedValues) {
	            if (remainingExcess <= 0)
	                break;
	            if (rounded[item.index] <= 0)
	                continue;
	            // Calculate what percentage this allocation represents of the original total
	            const proportion = item.value / originalTotal;
	            // Calculate proportional reduction: this allocation's share of the excess
	            const proportionalReduction = proportion * excess;
	            // Round the reduction
	            const reduction = Math.round(proportionalReduction);
	            // Don't reduce more than the allocation value or more than we still need to reduce
	            const finalReduction = Math.min(reduction, rounded[item.index], remainingExcess);
	            rounded[item.index] -= finalReduction;
	            total -= finalReduction;
	            remainingExcess -= finalReduction;
	        }
	        // The proportional pass places nothing when each allocation's share of the excess rounds to zero
	        // — 18 projects at 6% apiece each owe 0.44 of an 8 point excess. Drain whatever is left one
	        // rounding step at a time, largest allocation first, revisiting the list until the total lands on
	        // 100. Taking the whole remainder out of a single allocation instead zeroes that project out and
	        // still leaves the total above 100.
	        const step = 10 ** -constants_1.USER_ROUNDING_NUM_DECIMALS;
	        while (remainingExcess > 0) {
	            const reducibleIndexes = rounded
	                .map((value, index) => ({ value, index }))
	                .filter((item) => item.value > 0)
	                .sort((a, b) => b.value - a.value)
	                .map((item) => item.index);
	            if (reducibleIndexes.length === 0) {
	                break;
	            }
	            for (const index of reducibleIndexes) {
	                if (remainingExcess <= 0) {
	                    break;
	                }
	                const reduction = Math.min(step, rounded[index], remainingExcess);
	                rounded[index] -= reduction;
	                total -= reduction;
	                remainingExcess -= reduction;
	            }
	        }
	    }
	    return rounded;
	};
	exports.roundAllocationsToTotal = roundAllocationsToTotal;
	/**
	 * The single normalization primitive for a contributor's per-project allocation percentages.
	 * Rounds the raw percents as a group (see {@link roundAllocationsToTotal}) so they can never sum
	 * past 100, and derives the residual non-project percent (100 − Σ).
	 *
	 * Every path that turns raw per-project allocations into stored/whole percents (wage-report upload,
	 * report generation, the client allocation modal) funnels through this so rounding stays consistent
	 * and non-project time can never go negative. Rounding each project independently can push the total
	 * to 101% even when the raw values sum to exactly 100% (e.g. two 12.5% projects each round up to
	 * 13%), which drives non-project time negative and lets the overshoot survive into a capitalized
	 * percentage above 100%.
	 *
	 * @param rawProjectPercents - Raw per-project allocation percentages (may have decimals), assumed
	 *   non-negative; input order is preserved in `projectPercents`.
	 */
	const roundProjectAllocationPercents = (rawProjectPercents) => {
	    const projectPercents = (0, exports.roundAllocationsToTotal)(rawProjectPercents);
	    const nonProjectPercent = 100 - projectPercents.reduce((sum, percent) => sum + percent, 0);
	    return { projectPercents, nonProjectPercent };
	};
	exports.roundProjectAllocationPercents = roundProjectAllocationPercents;
	const assignPriority = (ascProjectIdToCurrentPma, ascProjectIdToDeltaAveragePercent) => {
	    const result = {};
	    Object.entries(ascProjectIdToCurrentPma).forEach(([ascProjectId, ascProjectAllocations]) => {
	        const delta = ascProjectIdToDeltaAveragePercent.get(ascProjectId);
	        if (typeof delta !== 'number') {
	            throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${ascProjectId} does not have a delta. This should not happen.`);
	        }
	        if (delta < 0) {
	            result[ascProjectId] = ascProjectAllocations.map((allocation) => {
	                const { allocationPercent } = allocation;
	                if (allocationPercent > 0) {
	                    return {
	                        ...allocation,
	                        priority: AllocationPriority.HIGH,
	                    };
	                }
	                return {
	                    ...allocation,
	                    priority: AllocationPriority.LOW,
	                };
	            });
	        }
	        else {
	            result[ascProjectId] = ascProjectAllocations.map((allocation, index) => {
	                const { allocationPercent } = allocation;
	                if (allocationPercent > 0) {
	                    return {
	                        ...allocation,
	                        priority: AllocationPriority.HIGH,
	                    };
	                }
	                const prev = index > 0 ? ascProjectAllocations[index - 1] : undefined;
	                const next = index < ascProjectAllocations.length - 1 ? ascProjectAllocations[index + 1] : undefined;
	                const prevHasAllocation = prev && prev.allocationPercent > 0;
	                const nextHasAllocation = next && next.allocationPercent > 0;
	                if (prevHasAllocation || nextHasAllocation) {
	                    return {
	                        ...allocation,
	                        priority: AllocationPriority.MEDIUM,
	                    };
	                }
	                return {
	                    ...allocation,
	                    priority: AllocationPriority.LOW,
	                };
	            });
	        }
	    });
	    return result;
	};
	exports.assignPriority = assignPriority;
	// Sort ASC projects from the lowest delta to the highest delta
	// When it comes to positive delta ASC projects, bring ASC projects with 0% months first.
	const sortAllAscProjectAllocations = (allAscProjectAllocationsMap, ascProjectIdToDeltaAveragePercent) => {
	    const sortedAllAscProjectAllocations = Object.entries(allAscProjectAllocationsMap).sort(([ascProjectIdA], [ascProjectIdB]) => {
	        const deltaA = ascProjectIdToDeltaAveragePercent.get(ascProjectIdA);
	        const deltaB = ascProjectIdToDeltaAveragePercent.get(ascProjectIdB);
	        if (typeof deltaA !== 'number' || typeof deltaB !== 'number') {
	            throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${!deltaA ? ascProjectIdA : ascProjectIdB} does not have a delta average percent. This should not happen.`);
	        }
	        if (deltaA <= 0 || deltaB <= 0) {
	            return deltaA - deltaB;
	        }
	        const ascProjectAllocationsA = allAscProjectAllocationsMap[ascProjectIdA];
	        const ascProjectAllocationsB = allAscProjectAllocationsMap[ascProjectIdB];
	        const numberOfZeroPercentMonthsInAscProjectA = ascProjectAllocationsA.filter((allocation) => allocation.allocationPercent === 0).length;
	        const numberOfZeroPercentMonthsInAscProjectB = ascProjectAllocationsB.filter((allocation) => allocation.allocationPercent === 0).length;
	        return numberOfZeroPercentMonthsInAscProjectB - numberOfZeroPercentMonthsInAscProjectA;
	    });
	    return sortedAllAscProjectAllocations.map(([ascProjectId]) => ascProjectId);
	};
	exports.sortAllAscProjectAllocations = sortAllAscProjectAllocations;
	const calculateMaxLimit = ({ ascProjectId, ascProjectIdToPmas, month, nonProjectTime, year, }) => {
	    const otherAscProjectAllocationsInSameMonth = Object.entries(ascProjectIdToPmas).reduce((acc, [id, ascProjectAllocations]) => {
	        if (id === ascProjectId) {
	            // skip the current project
	            return acc;
	        }
	        // TODO use a map to improve performance
	        const ascProjectAllocationInSameMonth = ascProjectAllocations.find((a) => a.month === month && a.year === year);
	        if (!ascProjectAllocationInSameMonth) {
	            throw (0, errors_1.createError)(`ASC Project allocation not found for month:${month} and year:${year}, ascProjectId ${ascProjectId}`);
	        }
	        acc += (0, exports.getLatestAllocationPercent)(ascProjectAllocationInSameMonth);
	        return acc;
	    }, 0);
	    return Math.abs(100 - (otherAscProjectAllocationsInSameMonth + nonProjectTime));
	};
	exports.calculateMaxLimit = calculateMaxLimit;
	const getAdjustedPercent = ({ ascProjectAllocation, currentPriorityAllocations, currentPriorityTotal, deltaTotal, maxLimit, }) => Math.max(0, Math.min(maxLimit, (0, exports.getLatestAllocationPercent)(ascProjectAllocation) +
	    deltaTotal *
	        // If the current priority total is 0, it means that all the allocations in this priority are 0%.
	        // In this case, we should distribute the delta evenly.
	        (currentPriorityTotal === 0
	            ? 1 / currentPriorityAllocations.length
	            : (0, exports.getLatestAllocationPercent)(ascProjectAllocation) / currentPriorityTotal)));
	exports.getAdjustedPercent = getAdjustedPercent;
	const updateAllocationPercent = ({ ascProjectId, ascProjectIdToPmas, newAverage, newNonProjectTime, priority, shouldFill, }) => {
	    const currentAscProjectAllocations = ascProjectIdToPmas[ascProjectId];
	    if (!currentAscProjectAllocations) {
	        throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${ascProjectId} does not have any allocations. This should not happen.`);
	    }
	    const newTotal = newAverage * currentAscProjectAllocations.length;
	    const originalTotal = currentAscProjectAllocations.reduce((a, b) => a + (0, exports.getLatestAllocationPercent)(b), 0);
	    const deltaTotal = newTotal - originalTotal;
	    const currentPriorityAllocations = currentAscProjectAllocations.filter((ascProjectAllocation) => ascProjectAllocation.priority === priority);
	    if (currentPriorityAllocations.length === 0) {
	        // nothing to do
	        return { done: false, ascProjectIdToPmas };
	    }
	    const currentPriorityTotal = currentPriorityAllocations.reduce((a, b) => a + (0, exports.getLatestAllocationPercent)(b), 0);
	    const newAllocations = currentAscProjectAllocations.map((ascProjectAllocation) => {
	        if (ascProjectAllocation.priority !== priority) {
	            return { ...ascProjectAllocation, maxLimitReached: false };
	        }
	        const { month, year } = ascProjectAllocation;
	        const maxLimit = (0, exports.calculateMaxLimit)({
	            month,
	            year,
	            nonProjectTime: newNonProjectTime,
	            ascProjectIdToPmas,
	            ascProjectId,
	        });
	        const adjustedPercent = shouldFill
	            ? maxLimit
	            : (0, exports.getAdjustedPercent)({
	                currentPriorityAllocations,
	                currentPriorityTotal,
	                deltaTotal,
	                maxLimit,
	                ascProjectAllocation,
	            });
	        const maxLimitReached = Math.abs(adjustedPercent - maxLimit) < exports.ALLOCATION_DIFF_THRESHOLD;
	        return {
	            ...ascProjectAllocation,
	            useAllocationPercent: adjustedPercent,
	            maxLimitReached,
	        };
	    });
	    ascProjectIdToPmas[ascProjectId] = newAllocations;
	    const updatedTotal = newAllocations.reduce((a, b) => a + (0, exports.getLatestAllocationPercent)(b), 0);
	    const diff = updatedTotal - newTotal;
	    const done = Math.abs(diff) < exports.ALLOCATION_DIFF_THRESHOLD;
	    const allMonthsHitMaxLimit = newAllocations
	        .filter((allocation) => allocation.priority === priority)
	        .every((allocation) => allocation.maxLimitReached);
	    if (!done && !allMonthsHitMaxLimit) {
	        // re-running allocation to fill up the remaining
	        return (0, exports.updateAllocationPercent)({
	            ascProjectIdToPmas,
	            newAverage,
	            newNonProjectTime,
	            priority,
	            ascProjectId,
	            shouldFill,
	        });
	    }
	    return {
	        done,
	        ascProjectIdToPmas,
	    };
	};
	exports.updateAllocationPercent = updateAllocationPercent;
	/**
	 * Clamp every month's `useAllocationPercent` into the valid [0, 100] range. A single month's
	 * allocation can exceed 100 because {@link calculateMaxLimit} bounds each month by
	 * `Math.abs(100 - (otherAllocations + nonProjectTime))`, which grows past 100 when non-project time
	 * is negative (e.g. project averages that sum past 100% due to rounding). A monthly allocation
	 * above 100% is meaningless and is rejected when the PMA is persisted, so this guarantees callers
	 * always receive a valid range.
	 */
	const clampUseAllocationPercentInPeriod = (ascProjectIdToPmas) => Object.fromEntries(Object.entries(ascProjectIdToPmas).map(([ascProjectId, pmas]) => [
	    ascProjectId,
	    pmas.map((pma) => ({
	        ...pma,
	        useAllocationPercent: pma.useAllocationPercent == null
	            ? pma.useAllocationPercent
	            : Math.min(100, Math.max(0, pma.useAllocationPercent)),
	    })),
	]));
	/**
	 * Will attempt to use {@link updateAllocationsUsingProportionateMethod} to update the allocations. There is not always a solution to the allocation
	 * problem. In this case, we will assign the average percent to all the months uniformly.
	 */
	const updateAllocationsInPeriod = ({ ascProjectIdToPmas, newAverages, newNonProjectTime, }) => {
	    // validate input
	    // Non-project time is the residual `100 - Σ project percents`, so a negative value means the
	    // project percents summed past 100% for that contributor-month. The sum check below cannot catch
	    // that on its own — it asserts projects + non-project = 100, and 101 + (-1) reconciles to 100,
	    // which is exactly how the overshoot in OPS-843 reached stored data and surfaced as a capitalized
	    // percentage above 100%.
	    if (newNonProjectTime < 0) {
	        throw (0, errors_1.createError)('UpdateAllocationsError', `Non project time cannot be negative, meaning the project allocations sum to more than 100%. Non project time: ${newNonProjectTime}`);
	    }
	    if (newAverages.length !== Object.keys(ascProjectIdToPmas).length) {
	        throw (0, errors_1.createError)('UpdateAllocationsError', `Number of ASC projects in the new averages does not match the number of ASC projects in the current allocations. new averages: ${newAverages.length}, current allocations: ${ascProjectIdToPmas.length}`);
	    }
	    const perAscProjectNumPmas = new Set(Object.entries(ascProjectIdToPmas).map(([_key, pmas]) => pmas.length));
	    if (perAscProjectNumPmas.size > 1) {
	        throw (0, errors_1.createError)('UpdateAllocationsError', 'Not all ASC project have the same number of PMAs');
	    }
	    const newAllocationSum = newAverages.reduce((acc, average) => {
	        acc += average.allocationPercent;
	        return acc;
	    }, newNonProjectTime);
	    if (Math.abs(newAllocationSum - 100) > exports.ALLOCATION_DIFF_THRESHOLD) {
	        throw (0, errors_1.createError)('UpdateAllocationsError', `The sum of new averages and the new non project time is not 100. Sum: ${newAllocationSum}`);
	    }
	    // DO NOT REMOVE THIS LINE, IT IS ESSENTIAL FOR HOW THIS ALGORITHM WORKS!
	    const ascProjectIdToPmasWithoutUseAllocationPercent = (0, exports.removeUseAllocationPercent)(ascProjectIdToPmas);
	    // create maps for efficient access during computation
	    const ascProjectIdToNewAveragePercent = new Map(newAverages.map((average) => [average.ascProjectId, average.allocationPercent]));
	    const ascProjectIdToOriginalAveragePercent = new Map(Object.entries(ascProjectIdToPmasWithoutUseAllocationPercent).map(([ascProjectId, pmas]) => {
	        const originalPercentTotal = pmas.reduce((a, b) => a + b.allocationPercent, 0);
	        const ascProjectAverage = originalPercentTotal / pmas.length;
	        return [ascProjectId, ascProjectAverage];
	    }));
	    const ascProjectIdToDeltaAveragePercent = new Map([...ascProjectIdToNewAveragePercent.entries()].map(([ascProjectId, newAverage]) => {
	        const originalAverage = ascProjectIdToOriginalAveragePercent.get(ascProjectId);
	        if (typeof originalAverage !== 'number') {
	            throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${ascProjectId} does not have an original average percent. This should not happen.`);
	        }
	        return [ascProjectId, newAverage - originalAverage];
	    }));
	    // if all the deltas are within min diff threshold, then we can just return the current allocations
	    const areAllDeltaZero = [...ascProjectIdToDeltaAveragePercent.values()].every((delta) => Math.abs(delta) < exports.ALLOCATION_DIFF_THRESHOLD);
	    if (areAllDeltaZero) {
	        return { newAscProjectIdToPmas: ascProjectIdToPmasWithoutUseAllocationPercent, fallbackError: null };
	    }
	    try {
	        return {
	            newAscProjectIdToPmas: clampUseAllocationPercentInPeriod(updateAllocationsUsingProportionateMethod({
	                newNonProjectTime,
	                ascProjectIdToDeltaAveragePercent: ascProjectIdToDeltaAveragePercent,
	                ascProjectIdToNewAveragePercent: ascProjectIdToNewAveragePercent,
	                ascProjectIdToPmas: ascProjectIdToPmasWithoutUseAllocationPercent,
	            })),
	            fallbackError: null,
	        };
	    }
	    catch (error) {
	        // fallback to uniform allocations
	        return {
	            newAscProjectIdToPmas: clampUseAllocationPercentInPeriod(updateAllocationsUsingUniformMethod({
	                ascProjectIdToNewAveragePercent: ascProjectIdToNewAveragePercent,
	                ascProjectIdToPmas: ascProjectIdToPmasWithoutUseAllocationPercent,
	            })),
	            fallbackError: error instanceof Error ? error : new Error(String(error)),
	        };
	    }
	};
	exports.updateAllocationsInPeriod = updateAllocationsInPeriod;
	/**
	 * Steps to calculate the new allocations is described in the notion doc
	 * https://www.notion.so/neo-tax/Soft-Cap-Allocation-Edit-Strategy-54d37ce0116a401a9ad29a819788351c?pvs=4
	 */
	const updateAllocationsUsingProportionateMethod = ({ ascProjectIdToDeltaAveragePercent, ascProjectIdToNewAveragePercent, ascProjectIdToPmas, newNonProjectTime, }) => {
	    const ascProjectIdToPmaWithPriority = (0, exports.assignPriority)(ascProjectIdToPmas, ascProjectIdToDeltaAveragePercent);
	    const sortedAscProjectIds = (0, exports.sortAllAscProjectAllocations)(ascProjectIdToPmaWithPriority, ascProjectIdToDeltaAveragePercent);
	    let currentAllAscProjectAllocations = ascProjectIdToPmaWithPriority;
	    sortedAscProjectIds.map((ascProjectId, index) => {
	        const isLastAscProject = index === sortedAscProjectIds.length - 1;
	        let currentPriorityIndex = 0;
	        let currentDone = false;
	        while (!currentDone && currentPriorityIndex < ALLOCATION_PRIORITY_ORDER.length) {
	            const priority = ALLOCATION_PRIORITY_ORDER[currentPriorityIndex];
	            const newAverage = ascProjectIdToNewAveragePercent.get(ascProjectId);
	            if (typeof newAverage !== 'number') {
	                throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${ascProjectId} does not have a new average percent. This should not happen.`);
	            }
	            const allocationResults = (0, exports.updateAllocationPercent)({
	                ascProjectIdToPmas: ascProjectIdToPmaWithPriority,
	                newAverage,
	                newNonProjectTime,
	                priority,
	                ascProjectId,
	                shouldFill: isLastAscProject,
	            });
	            currentAllAscProjectAllocations = allocationResults.ascProjectIdToPmas;
	            currentDone = allocationResults.done;
	            currentPriorityIndex += 1;
	        }
	        return {
	            ascProjectId,
	            done: currentDone,
	        };
	    });
	    const currentAllAscProjectAllocationsEntries = Object.entries(currentAllAscProjectAllocations);
	    const months = currentAllAscProjectAllocationsEntries[0][1];
	    for (const { month, year } of months) {
	        const totalAllocationAcrossAllProjectsForMonthYear = currentAllAscProjectAllocationsEntries.reduce((acc, [_ascProjectId, pmasWithPriority]) => {
	            // find allocation for that month and year
	            // TODO use a month year key and map to improve performance
	            const ascProjectAllocation = pmasWithPriority.find((allocation) => allocation.month === month && allocation.year === year);
	            if (!ascProjectAllocation) {
	                throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project allocation not found for month:${month} and year:${year}`);
	            }
	            acc += (0, exports.getLatestAllocationPercent)(ascProjectAllocation);
	            return acc;
	        }, 0);
	        if (Math.abs(totalAllocationAcrossAllProjectsForMonthYear + newNonProjectTime - 100) > exports.ALLOCATION_DIFF_THRESHOLD) {
	            throw (0, errors_1.createError)('UpdateAllocationsError', `The sum of allocations for month ${month} and year ${year} is not 100. Sum: ${totalAllocationAcrossAllProjectsForMonthYear}`);
	        }
	    }
	    return currentAllAscProjectAllocations;
	};
	const updateAllocationsUsingUniformMethod = ({ ascProjectIdToNewAveragePercent, ascProjectIdToPmas, }) => {
	    for (const [ascProjectId, averagePercent] of ascProjectIdToNewAveragePercent.entries()) {
	        const pmas = ascProjectIdToPmas[ascProjectId];
	        if (!pmas) {
	            throw (0, errors_1.createError)('UpdateAllocationsError', `ASC Project ${ascProjectId} does not have any allocations. This should not happen.`);
	        }
	        const updatedPmas = pmas.map((pma) => ({
	            ...pma,
	            useAllocationPercent: averagePercent,
	        }));
	        ascProjectIdToPmas[ascProjectId] = updatedPmas;
	    }
	    return ascProjectIdToPmas;
	};
	/**
	 * HACK: this is an approximation at best, because we do not consider the number of work days in each month while averaging
	 * the allocation percentages. However, because using constant non-project turns pmas into a relative and approximate value this
	 * is considered good enough for now.
	 *
	 * @returns the float average percent without any rounding
	 */
	const getAverageAllocationInPeriod = (ascProjectMonthAllocations, monthYearPeriod) => {
	    const fixedMonthYearPeriod = (0, month_year_utils_1.applyCompletedEndMonthYearRule)(monthYearPeriod);
	    const { endMonthYear, startMonthYear } = fixedMonthYearPeriod;
	    const numberOfMonths = (0, month_year_utils_1.getNumberOfMonthsInPeriod)(fixedMonthYearPeriod);
	    const pmasInPeriod = ascProjectMonthAllocations.filter((pma) => (0, month_year_utils_1.isMonthYearInRange)(pma, startMonthYear, endMonthYear));
	    const averagePercent = pmasInPeriod.reduce((acc, pma) => acc + (pma.useAllocationPercent ?? pma.allocationPercent), 0) / numberOfMonths;
	    return averagePercent;
	};
	exports.getAverageAllocationInPeriod = getAverageAllocationInPeriod;
} (ascProjectsAllocation_utils));

var ascLockBoundary_utils = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.maxMonthYearNullable = exports.isProjectDateRangeFullyLocked = exports.formatLockBoundary = exports.validateNotBeforeLockBoundary = exports.isBeforeOrOnLockBoundary = exports.getFirstAllowableCapitalizationStartMonthYear = exports.isCapitalizationEndDateLocked = exports.isCapitalizationStartDateLocked = exports.isCapitalizationStatusLocked = void 0;
	const date_utils_1 = date_utils;
	const month_year_utils_1 = monthYear_utils;
	// Regex for YYYY-MM format
	const LOCK_BOUNDARY_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;
	/**
	 * Parses and validates a lock boundary string.
	 * @param lockBoundary - The lock boundary in YYYY-MM format
	 * @returns The parsed MonthYear
	 * @throws Error if the lock boundary is not in valid YYYY-MM format
	 */
	const parseLockBoundary = (lockBoundary) => {
	    if (!LOCK_BOUNDARY_REGEX.test(lockBoundary)) {
	        throw new Error(`Invalid lock boundary format: "${lockBoundary}". Expected YYYY-MM format (e.g., "2024-06").`);
	    }
	    return (0, month_year_utils_1.stringToMonthYear)(lockBoundary);
	};
	/**
	 * Determines if capitalization status is locked based on lock boundary.
	 * Status is locked when a capitalization start date exists and is on or before the lock boundary.
	 *
	 * @param capitalizationStartDate - The project's capitalization start date (Date, string, or null)
	 * @param lockBoundary - The lock boundary in YYYY-MM format (or null if not set)
	 * @returns true if status is locked, false otherwise
	 */
	const isCapitalizationStatusLocked = (capitalizationStartDate, lockBoundary) => {
	    if (!lockBoundary) {
	        return false;
	    }
	    if (!capitalizationStartDate) {
	        return false;
	    }
	    const lockBoundaryMonthYear = parseLockBoundary(lockBoundary);
	    const startDateMonthYear = (0, month_year_utils_1.dateToMonthYear)(capitalizationStartDate);
	    // Locked if start date is on or before lock boundary
	    return (0, month_year_utils_1.isMonthYearBeforeOrEqual)(startDateMonthYear, lockBoundaryMonthYear);
	};
	exports.isCapitalizationStatusLocked = isCapitalizationStatusLocked;
	/**
	 * Determines if capitalization start date is locked based on lock boundary.
	 * Same logic as status - both are locked together.
	 */
	const isCapitalizationStartDateLocked = (capitalizationStartDate, lockBoundary) => (0, exports.isCapitalizationStatusLocked)(capitalizationStartDate, lockBoundary);
	exports.isCapitalizationStartDateLocked = isCapitalizationStartDateLocked;
	/**
	 * Determines if capitalization end date is locked based on lock boundary.
	 * End date is more permissive - it can be modified if it's on or after the lock boundary.
	 *
	 * @param capitalizationEndDate - The project's capitalization end date (Date, string, or null)
	 * @param lockBoundary - The lock boundary in YYYY-MM format (or null if not set)
	 * @returns true if end date is locked, false otherwise
	 */
	const isCapitalizationEndDateLocked = (capitalizationEndDate, lockBoundary) => {
	    if (!lockBoundary) {
	        return false;
	    }
	    if (!capitalizationEndDate) {
	        return false;
	    }
	    const lockBoundaryMonthYear = parseLockBoundary(lockBoundary);
	    const endDateMonthYear = (0, month_year_utils_1.dateToMonthYear)(capitalizationEndDate);
	    // Locked only if end date is strictly before lock boundary.
	    // If it's on the boundary, it can still be modified.
	    return (0, month_year_utils_1.isMonthYearBefore)(endDateMonthYear, lockBoundaryMonthYear);
	};
	exports.isCapitalizationEndDateLocked = isCapitalizationEndDateLocked;
	/**
	 * Gets the first allowable month/year for capitalization start date.
	 * Returns the month AFTER the lock boundary, or null if no lock boundary.
	 */
	const getFirstAllowableCapitalizationStartMonthYear = (lockBoundary) => {
	    if (!lockBoundary) {
	        return null;
	    }
	    const lockBoundaryMonthYear = parseLockBoundary(lockBoundary);
	    return (0, date_utils_1.getNextMonthYear)(lockBoundaryMonthYear);
	};
	exports.getFirstAllowableCapitalizationStartMonthYear = getFirstAllowableCapitalizationStartMonthYear;
	/**
	 * Checks if a MonthYear value is before or on the lock boundary.
	 * Used for form validation.
	 */
	const isBeforeOrOnLockBoundary = (value, lockBoundary) => {
	    if (!value || !lockBoundary) {
	        return false;
	    }
	    const lockBoundaryMonthYear = parseLockBoundary(lockBoundary);
	    return (0, month_year_utils_1.isMonthYearBeforeOrEqual)(value, lockBoundaryMonthYear);
	};
	exports.isBeforeOrOnLockBoundary = isBeforeOrOnLockBoundary;
	/**
	 * Validates a MonthYear value against the lock boundary and returns an error message if invalid.
	 * Returns null if the value is valid (after the lock boundary or no lock boundary set).
	 * Used for form validation to DRY up error message generation.
	 */
	const validateNotBeforeLockBoundary = (value, lockBoundary) => {
	    if ((0, exports.isBeforeOrOnLockBoundary)(value, lockBoundary)) {
	        return `Cannot set date before locked period (${(0, exports.formatLockBoundary)(lockBoundary)})`;
	    }
	    return null;
	};
	exports.validateNotBeforeLockBoundary = validateNotBeforeLockBoundary;
	/**
	 * Formats a lock boundary string for display.
	 * @example "2024-06" → "Jun 2024"
	 */
	const formatLockBoundary = (lockBoundary) => {
	    if (!lockBoundary) {
	        return '';
	    }
	    const monthYear = parseLockBoundary(lockBoundary);
	    return `${(0, date_utils_1.getMonthLongAbbreviation)(monthYear.month)} ${monthYear.year}`;
	};
	exports.formatLockBoundary = formatLockBoundary;
	/**
	 * Checks if a project's date range falls entirely within the locked period.
	 * This means no valid capitalization dates can be set (all dates within project range are locked).
	 *
	 * @param projectEndDate - The project's end date (Date, string, or null)
	 * @param lockBoundary - The lock boundary in YYYY-MM format (or null if not set)
	 * @returns true if the entire project date range is locked, false otherwise
	 */
	const isProjectDateRangeFullyLocked = (projectEndDate, lockBoundary) => {
	    if (!lockBoundary || !projectEndDate) {
	        return false;
	    }
	    const lockBoundaryMonthYear = parseLockBoundary(lockBoundary);
	    const endDateMonthYear = (0, month_year_utils_1.dateToMonthYear)(projectEndDate);
	    // If project ends on or before the lock boundary, all dates are locked
	    return (0, month_year_utils_1.isMonthYearBeforeOrEqual)(endDateMonthYear, lockBoundaryMonthYear);
	};
	exports.isProjectDateRangeFullyLocked = isProjectDateRangeFullyLocked;
	/**
	 * Returns the maximum of two MonthYear values, or the one that's not null.
	 * Used for determining the best date when toggling capitalization on.
	 */
	const maxMonthYearNullable = (a, b) => {
	    if (!a)
	        return b;
	    if (!b)
	        return a;
	    const dateA = (0, month_year_utils_1.getFirstDateOfMonthYear)(a);
	    const dateB = (0, month_year_utils_1.getFirstDateOfMonthYear)(b);
	    return dateA > dateB ? a : b;
	};
	exports.maxMonthYearNullable = maxMonthYearNullable;
} (ascLockBoundary_utils));

var capitalizableProjectClustering_utils = {};

var ascClusterType_enum = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.ASC_NOISE_CLUSTER_TYPES = exports.AscClusterType = void 0;
	exports.isAscNoiseClusterType = isAscNoiseClusterType;
	var AscClusterType;
	(function (AscClusterType) {
	    AscClusterType["SMART_MAIN"] = "SMART_MAIN";
	    AscClusterType["SMART_NOISE"] = "SMART_NOISE";
	    AscClusterType["CAPITALIZABLE_PROJECT_MAIN"] = "CAPITALIZABLE_PROJECT_MAIN";
	    AscClusterType["CAPITALIZABLE_PROJECT_NOISE"] = "CAPITALIZABLE_PROJECT_NOISE";
	    AscClusterType["DETERMINISTIC_HAS_ROOT"] = "DETERMINISTIC_HAS_ROOT";
	    AscClusterType["DETERMINISTIC_NO_ROOT"] = "DETERMINISTIC_NO_ROOT";
	    AscClusterType["MANUALLY_CREATED"] = "MANUALLY_CREATED";
	    AscClusterType["MERGED"] = "MERGED";
	})(AscClusterType || (exports.AscClusterType = AscClusterType = {}));
	exports.ASC_NOISE_CLUSTER_TYPES = [
	    AscClusterType.SMART_NOISE,
	    AscClusterType.CAPITALIZABLE_PROJECT_NOISE,
	];
	function isAscNoiseClusterType(type) {
	    return exports.ASC_NOISE_CLUSTER_TYPES.includes(type);
	}
} (ascClusterType_enum));

(function (exports) {
	// Shared rule for the capitalizable project clustering "matching target" status.
	// Both the server (sync pipeline, capitalization determination) and the web-app
	// (project list/drawer/review modal) derive their behavior from this predicate.
	// Keep this file pure: no UI strings — those belong to the web-app utility that
	// wraps `getMatchingTargetStatus`. See spec §3.
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.isUsedInCapitalizableProjectClustering = exports.getCapitalizableProjectClusteringStatus = exports.CLUSTERING_METHOD = exports.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE = void 0;
	const month_year_utils_1 = monthYear_utils;
	const asc_cluster_type_enum_1 = ascClusterType_enum;
	// String values match the persisted enum on `asc_projects.capitalizable_project_clustering_override`.
	// Kept as a const object instead of a TypeScript enum so this module has no
	// dependency on the dirty-data entity layer or the GraphQL codegen output.
	exports.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE = {
	    INCLUDED: 'INCLUDED',
	    EXCLUDED: 'EXCLUDED',
	};
	// Mirror of `ClusteringMethod` enum values from the GraphQL schema. Kept as string
	// literals so this module stays free of dependencies on the codegen output.
	exports.CLUSTERING_METHOD = {
	    CAPITALIZABLE_PROJECT: 'CAPITALIZABLE_PROJECT',
	    DETERMINISTIC: 'DETERMINISTIC',
	    SMART: 'SMART',
	};
	/**
	 * Active matching target list (mirrors `AscProjectRepository.findActiveAscProjectMatchTargets`):
	 *   1. override = INCLUDED                                                       -> active
	 *   2. override IS NULL AND cap_start IS NOT NULL AND
	 *      (cap_end IS NULL OR cap_end in/after the last completed ASC sync's
	 *      end month) AND activity is current (end_date in/after that month)         -> active (default rule)
	 *   3. override IS NULL AND cluster_type = MAIN AND start_date IS NULL           -> active (fresh MAIN)
	 * Otherwise inactive. EXCLUDED override always inactive. A capitalizing project whose activity has
	 * gone stale (end_date before the boundary month) is 'no-recent-activity' (inactive).
	 *
	 * `lastCompletedAscSyncPeriodEnd` is the period-end date of the company's last
	 * completed ASC sync (`Company.lastCompletedAscSyncPeriodEnd` in GraphQL). A cap_end
	 * in/after that month is a "boundary" end date — an auto-set artifact of that sync's
	 * data horizon that the next sync may extend — so the project is still an active
	 * matching target. When null/omitted, clause 2 requires cap_end IS NULL as before.
	 */
	const getCapitalizableProjectClusteringStatus = (project, clusteringMethod, lastCompletedAscSyncPeriodEnd) => {
	    if (clusteringMethod !== exports.CLUSTERING_METHOD.CAPITALIZABLE_PROJECT) {
	        return { isActive: false, reason: 'not-applicable' };
	    }
	    const override = project.capitalizableProjectClusteringOverride ?? null;
	    if (override === exports.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE.INCLUDED) {
	        return { isActive: true, reason: 'included' };
	    }
	    if (override === exports.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE.EXCLUDED) {
	        return { isActive: false, reason: 'excluded' };
	    }
	    if (project.capitalizationStartDate) {
	        // A capitalizing project stays a matching target only while its activity is current — its last
	        // activity month (end_date) must be on/after the last completed sync's end month. Null-safe: no
	        // completed sync (boundary null) or no recorded activity (end_date null) keeps it active. A
	        // finalized cap_end (capitalization-ended) still wins over stale activity below.
	        const hasRecentActivity = lastCompletedAscSyncPeriodEnd == null ||
	            project.endDate == null ||
	            !(0, month_year_utils_1.isMonthYearBefore)((0, month_year_utils_1.dateToMonthYear)(project.endDate), (0, month_year_utils_1.dateToMonthYear)(lastCompletedAscSyncPeriodEnd));
	        if (project.capitalizationEndDate) {
	            const isBoundaryCapEnd = lastCompletedAscSyncPeriodEnd != null &&
	                !(0, month_year_utils_1.isMonthYearBefore)((0, month_year_utils_1.dateToMonthYear)(project.capitalizationEndDate), (0, month_year_utils_1.dateToMonthYear)(lastCompletedAscSyncPeriodEnd));
	            if (isBoundaryCapEnd) {
	                return hasRecentActivity
	                    ? { isActive: true, reason: 'default-active-boundary-extend' }
	                    : { isActive: false, reason: 'no-recent-activity' };
	            }
	            return { isActive: false, reason: 'capitalization-ended' };
	        }
	        return hasRecentActivity
	            ? { isActive: true, reason: 'default-active' }
	            : { isActive: false, reason: 'no-recent-activity' };
	    }
	    // cap_start IS NULL — check clause 3 (fresh MAIN cluster project without effort data yet).
	    if (project.clusterType === asc_cluster_type_enum_1.AscClusterType.CAPITALIZABLE_PROJECT_MAIN && !project.startDate) {
	        return { isActive: true, reason: 'default-active-fresh-main' };
	    }
	    return { isActive: false, reason: 'capitalization-not-started' };
	};
	exports.getCapitalizableProjectClusteringStatus = getCapitalizableProjectClusteringStatus;
	const isUsedInCapitalizableProjectClustering = (project, clusteringMethod, lastCompletedAscSyncPeriodEnd) => (0, exports.getCapitalizableProjectClusteringStatus)(project, clusteringMethod, lastCompletedAscSyncPeriodEnd).isActive;
	exports.isUsedInCapitalizableProjectClustering = isUsedInCapitalizableProjectClustering;
} (capitalizableProjectClustering_utils));

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.ASC_NOISE_CLUSTER_TYPES = exports.isAscNoiseClusterType = exports.AscClusterType = exports.isUsedInCapitalizableProjectClustering = exports.getCapitalizableProjectClusteringStatus = exports.CLUSTERING_METHOD = exports.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE = exports.maxMonthYearNullable = exports.formatLockBoundary = exports.validateNotBeforeLockBoundary = exports.isBeforeOrOnLockBoundary = exports.getFirstAllowableCapitalizationStartMonthYear = exports.isProjectDateRangeFullyLocked = exports.isCapitalizationEndDateLocked = exports.isCapitalizationStartDateLocked = exports.isCapitalizationStatusLocked = exports.getAverageAllocationInPeriod = exports.ALLOCATION_DIFF_THRESHOLD = exports.roundProjectAllocationPercents = exports.roundAllocationsToTotal = exports.updateAllocationsInPeriod = exports.getLatestAllocationPercent = void 0;
	var asc_projects_allocation_utils_1 = ascProjectsAllocation_utils;
	Object.defineProperty(exports, "getLatestAllocationPercent", { enumerable: true, get: function () { return asc_projects_allocation_utils_1.getLatestAllocationPercent; } });
	Object.defineProperty(exports, "updateAllocationsInPeriod", { enumerable: true, get: function () { return asc_projects_allocation_utils_1.updateAllocationsInPeriod; } });
	Object.defineProperty(exports, "roundAllocationsToTotal", { enumerable: true, get: function () { return asc_projects_allocation_utils_1.roundAllocationsToTotal; } });
	Object.defineProperty(exports, "roundProjectAllocationPercents", { enumerable: true, get: function () { return asc_projects_allocation_utils_1.roundProjectAllocationPercents; } });
	Object.defineProperty(exports, "ALLOCATION_DIFF_THRESHOLD", { enumerable: true, get: function () { return asc_projects_allocation_utils_1.ALLOCATION_DIFF_THRESHOLD; } });
	Object.defineProperty(exports, "getAverageAllocationInPeriod", { enumerable: true, get: function () { return asc_projects_allocation_utils_1.getAverageAllocationInPeriod; } });
	var asc_lock_boundary_utils_1 = ascLockBoundary_utils;
	Object.defineProperty(exports, "isCapitalizationStatusLocked", { enumerable: true, get: function () { return asc_lock_boundary_utils_1.isCapitalizationStatusLocked; } });
	Object.defineProperty(exports, "isCapitalizationStartDateLocked", { enumerable: true, get: function () { return asc_lock_boundary_utils_1.isCapitalizationStartDateLocked; } });
	Object.defineProperty(exports, "isCapitalizationEndDateLocked", { enumerable: true, get: function () { return asc_lock_boundary_utils_1.isCapitalizationEndDateLocked; } });
	Object.defineProperty(exports, "isProjectDateRangeFullyLocked", { enumerable: true, get: function () { return asc_lock_boundary_utils_1.isProjectDateRangeFullyLocked; } });
	Object.defineProperty(exports, "getFirstAllowableCapitalizationStartMonthYear", { enumerable: true, get: function () { return asc_lock_boundary_utils_1.getFirstAllowableCapitalizationStartMonthYear; } });
	Object.defineProperty(exports, "isBeforeOrOnLockBoundary", { enumerable: true, get: function () { return asc_lock_boundary_utils_1.isBeforeOrOnLockBoundary; } });
	Object.defineProperty(exports, "validateNotBeforeLockBoundary", { enumerable: true, get: function () { return asc_lock_boundary_utils_1.validateNotBeforeLockBoundary; } });
	Object.defineProperty(exports, "formatLockBoundary", { enumerable: true, get: function () { return asc_lock_boundary_utils_1.formatLockBoundary; } });
	Object.defineProperty(exports, "maxMonthYearNullable", { enumerable: true, get: function () { return asc_lock_boundary_utils_1.maxMonthYearNullable; } });
	var capitalizable_project_clustering_utils_1 = capitalizableProjectClustering_utils;
	Object.defineProperty(exports, "CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE", { enumerable: true, get: function () { return capitalizable_project_clustering_utils_1.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE; } });
	Object.defineProperty(exports, "CLUSTERING_METHOD", { enumerable: true, get: function () { return capitalizable_project_clustering_utils_1.CLUSTERING_METHOD; } });
	Object.defineProperty(exports, "getCapitalizableProjectClusteringStatus", { enumerable: true, get: function () { return capitalizable_project_clustering_utils_1.getCapitalizableProjectClusteringStatus; } });
	Object.defineProperty(exports, "isUsedInCapitalizableProjectClustering", { enumerable: true, get: function () { return capitalizable_project_clustering_utils_1.isUsedInCapitalizableProjectClustering; } });
	var asc_cluster_type_enum_1 = ascClusterType_enum;
	Object.defineProperty(exports, "AscClusterType", { enumerable: true, get: function () { return asc_cluster_type_enum_1.AscClusterType; } });
	Object.defineProperty(exports, "isAscNoiseClusterType", { enumerable: true, get: function () { return asc_cluster_type_enum_1.isAscNoiseClusterType; } });
	Object.defineProperty(exports, "ASC_NOISE_CLUSTER_TYPES", { enumerable: true, get: function () { return asc_cluster_type_enum_1.ASC_NOISE_CLUSTER_TYPES; } });
} (ascProjects));

var error_utils = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.extendedGraphQlErrorHasErrorCode = exports.extendedApolloErrorHasErrorCode = exports.hasStandardErrorCode = exports.hasErrorCode = exports.getStandardErrors = exports.getExtendedErrors = void 0;
	const interfaces_1 = interfaces;
	/**
	 * Extracts errors from an error object.
	 *
	 * @param error An error object that should be an ApolloError or GraphQlError
	 * @param schema The schema of the anticipated errors.
	 */
	const getExtendedErrors = (error, schema) => {
	    const parsedApolloError = (0, interfaces_1.safeParseExtendedApolloError)(error, schema);
	    if (parsedApolloError.success) {
	        return parsedApolloError.data.graphQLErrors.flatMap((graphQlError) => graphQlError.extensions.errors);
	    }
	    const parsedGraphQlError = (0, interfaces_1.safeParseExtendedGraphQlError)(error, schema);
	    if (parsedGraphQlError.success) {
	        return parsedGraphQlError.data.extensions.errors;
	    }
	    // Log the error and re-throw it. Let consumer determine how to handle it since it can't be parsed.
	    console.error(error);
	    throw error;
	};
	exports.getExtendedErrors = getExtendedErrors;
	const getStandardErrors = (error) => (0, exports.getExtendedErrors)(error, interfaces_1.StandardErrorSchema);
	exports.getStandardErrors = getStandardErrors;
	/**
	 * Parses the error and checks if it has a specific error code.
	 */
	const hasErrorCode = (error, errorCode, schema) => {
	    const parsedApolloError = (0, interfaces_1.safeParseExtendedApolloError)(error, schema);
	    if (parsedApolloError.success) {
	        return (0, exports.extendedApolloErrorHasErrorCode)(parsedApolloError.data, errorCode);
	    }
	    const parsedGraphQlError = (0, interfaces_1.safeParseExtendedGraphQlError)(error, schema);
	    if (parsedGraphQlError.success) {
	        return (0, exports.extendedGraphQlErrorHasErrorCode)(parsedGraphQlError.data, errorCode);
	    }
	    return false;
	};
	exports.hasErrorCode = hasErrorCode;
	/**
	 * Parses the error and checks if it has a StandardError code.
	 */
	const hasStandardErrorCode = (error, errorCode) => (0, exports.hasErrorCode)(error, errorCode, interfaces_1.StandardErrorSchema);
	exports.hasStandardErrorCode = hasStandardErrorCode;
	/**
	 * Accepts a previously parsed error and checks if it has a specific error code.
	 */
	const extendedApolloErrorHasErrorCode = (extendedApolloError, errorCode) => extendedApolloError.graphQLErrors.some((graphQlError) => graphQlError.extensions.errors.some((error) => error.code === errorCode));
	exports.extendedApolloErrorHasErrorCode = extendedApolloErrorHasErrorCode;
	/**
	 * Accepts a previously parsed error and checks if it has a specific error code.
	 */
	const extendedGraphQlErrorHasErrorCode = (extendedGraphQlError, errorCode) => extendedGraphQlError.extensions.errors.some((error) => error.code === errorCode);
	exports.extendedGraphQlErrorHasErrorCode = extendedGraphQlErrorHasErrorCode;
} (error_utils));

var environment_utils = {};

Object.defineProperty(environment_utils, "__esModule", { value: true });
environment_utils.isProductionLikeEnvironment = void 0;
const neo_tax_environment_enum_1 = neoTaxEnvironment_enum;
/**
 * @returns true if environment is production-like
 */
const isProductionLikeEnvironment = (neoTaxEnvironment) => [
    neo_tax_environment_enum_1.NeoTaxEnvironment.PRODUCTION,
    neo_tax_environment_enum_1.NeoTaxEnvironment.THOMSON_REUTERS,
    neo_tax_environment_enum_1.NeoTaxEnvironment.NARVAR,
    neo_tax_environment_enum_1.NeoTaxEnvironment.ZELIS,
    neo_tax_environment_enum_1.NeoTaxEnvironment.NBH,
    neo_tax_environment_enum_1.NeoTaxEnvironment.ADOBE,
    neo_tax_environment_enum_1.NeoTaxEnvironment.LENDINGCLUB,
    neo_tax_environment_enum_1.NeoTaxEnvironment.CAPITALONE,
    neo_tax_environment_enum_1.NeoTaxEnvironment.BULLHORN,
    neo_tax_environment_enum_1.NeoTaxEnvironment.DEMO,
    neo_tax_environment_enum_1.NeoTaxEnvironment.QUALCOMM,
    neo_tax_environment_enum_1.NeoTaxEnvironment.EXPEDIA,
    neo_tax_environment_enum_1.NeoTaxEnvironment.SHELL,
].includes(neoTaxEnvironment);
environment_utils.isProductionLikeEnvironment = isProductionLikeEnvironment;

var fiscalYear_utils = {};

Object.defineProperty(fiscalYear_utils, "__esModule", { value: true });
fiscalYear_utils.findFiscalYearsInPeriod = void 0;
const findFiscalYearsInPeriod = (fiscalYears, period) => {
    const fiscalYearsInPeriod = fiscalYears.filter((fiscalYear) => {
        const taxYearStartDate = new Date(fiscalYear.taxYearStartDate);
        const taxYearEndDate = new Date(fiscalYear.taxYearEndDate);
        const { endMonthYear, startMonthYear } = period;
        const periodStartDate = new Date(startMonthYear.year, startMonthYear.month, 1);
        const periodEndDate = new Date(endMonthYear.year, endMonthYear.month, 1);
        return (
        // fiscal year begins before the start of period and ends during or after the period
        (taxYearStartDate <= periodStartDate && periodStartDate <= taxYearEndDate) ||
            //   fiscal year begins during or before the period and ends after the period
            (taxYearStartDate <= periodEndDate && periodEndDate <= taxYearEndDate) ||
            //   fiscal year begins and ends within the period
            (periodStartDate <= taxYearStartDate && taxYearEndDate <= periodEndDate));
    });
    return fiscalYearsInPeriod;
};
fiscalYear_utils.findFiscalYearsInPeriod = findFiscalYearsInPeriod;

var priorYearData_utils = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.getFiscalPeriodsForRevenues = exports.getFiscalPeriodsForQres = exports.determineTaxYearEndDate = exports.determineTaxYearStartDate = void 0;
	const array_utils_1 = array_utils;
	const date_utils_1 = date_utils;
	const month_year_utils_1 = monthYear_utils;
	const determineTaxYearStartDate = ({ periodDates, shortYearPeriod, year, }) => {
	    if (!shortYearPeriod && periodDates) {
	        return year + periodDates.firstDayOfPeriod.slice(4);
	    }
	    if (!shortYearPeriod) {
	        throw new Error('Either shortYearPeriod or periodDates must be provided to determine tax year start date');
	    }
	    const isStandardToFiscalYearConversion = shortYearPeriod.startMonthYear.month === 0;
	    return isStandardToFiscalYearConversion
	        ? getStandardYearToFiscalYearConversionStartDate(year, shortYearPeriod)
	        : getFiscalYearToStandardYearConversionStartDate(year, shortYearPeriod);
	};
	exports.determineTaxYearStartDate = determineTaxYearStartDate;
	const getFiscalYearToStandardYearConversionStartDate = (year, shortYearPeriod) => {
	    const { endMonthYear, startMonthYear } = shortYearPeriod;
	    const shortYearStartYear = startMonthYear.year;
	    const shortYearStartMonth = startMonthYear.month;
	    const shortYearEndMonth = endMonthYear.month;
	    const shortYearStartMonthYear = (0, month_year_utils_1.toMonthYearString)(startMonthYear);
	    if (year === shortYearStartYear) {
	        return `${shortYearStartMonthYear}-01`;
	    }
	    else if (year > shortYearStartYear) {
	        // If the year is greater than the short year, we use the previous year and add 1 month to the short year end month
	        return (0, date_utils_1.addMonths)(new Date(year - 1, shortYearEndMonth, 1), 1);
	    }
	    else {
	        // If the year is before the short year, we use the tax year and the short year start month
	        return `${year}-${(shortYearStartMonth + 1).toString().padStart(2, '0')}-01`;
	    }
	};
	const getStandardYearToFiscalYearConversionStartDate = (year, shortYearPeriod) => {
	    const { endMonthYear, startMonthYear } = shortYearPeriod;
	    const shortYearStartYear = startMonthYear.year;
	    const shortYearStartMonth = startMonthYear.month;
	    const shortYearEndMonth = endMonthYear.month;
	    const shortYearStartMonthYear = (0, month_year_utils_1.toMonthYearString)(startMonthYear);
	    if (year === shortYearStartYear) {
	        return `${shortYearStartMonthYear}-01`;
	    }
	    else if (year > shortYearStartYear) {
	        // If the year is more than 1 year after the short year, we use the tax year and add 1 month to the short year end month
	        return (0, date_utils_1.addMonths)(new Date(year, shortYearEndMonth, 1), 1);
	    }
	    else {
	        // If the year is before the short year, we use the tax year and the short year start month
	        return `${year}-${(shortYearStartMonth + 1).toString().padStart(2, '0')}-01`;
	    }
	};
	const determineTaxYearEndDate = ({ periodDates, shortYearPeriod, year, }) => {
	    if (!shortYearPeriod && periodDates) {
	        const periodStartingYear = periodDates.firstDayOfPeriod.slice(0, 4);
	        const periodEndingYear = periodDates.lastDayOfPeriod.slice(0, 4);
	        const sameYear = periodStartingYear === periodEndingYear;
	        return (sameYear ? year : year + 1) + periodDates.lastDayOfPeriod.slice(4);
	    }
	    if (!shortYearPeriod) {
	        throw new Error('Either shortYearPeriod or periodDates must be provided to determine tax year end date');
	    }
	    const isStandardToFiscalYearConversion = shortYearPeriod.startMonthYear.month === 0;
	    return isStandardToFiscalYearConversion
	        ? getStandardYearToFiscalYearConversionEndDate(year, shortYearPeriod)
	        : getFiscalYearToStandardYearConversionEndDate(year, shortYearPeriod);
	};
	exports.determineTaxYearEndDate = determineTaxYearEndDate;
	const getFiscalYearToStandardYearConversionEndDate = (year, shortYearPeriod) => {
	    const { endMonthYear, startMonthYear } = shortYearPeriod;
	    const shortYearStartYear = startMonthYear.year;
	    const shortYearStartMonth = startMonthYear.month;
	    const shortYearEndMonth = endMonthYear.month;
	    const shortYearEndYear = endMonthYear.year;
	    if (year === shortYearStartYear) {
	        return (0, month_year_utils_1.getLastDateStringOfMonthYear)(endMonthYear);
	    }
	    else if (year > shortYearEndYear && shortYearEndMonth === 11) {
	        return (0, date_utils_1.getLastDayOfTheMonth)(new Date(year, shortYearEndMonth, 1))
	            .toISOString()
	            .slice(0, 10);
	    }
	    else if (year > shortYearStartYear && shortYearEndMonth != 11) {
	        return (0, date_utils_1.getLastDayOfTheMonth)(new Date(year + 1, shortYearEndMonth, 1))
	            .toISOString()
	            .slice(0, 10);
	    }
	    else {
	        const taxYearEndDate = new Date(year + 1, shortYearStartMonth, 1);
	        taxYearEndDate.setDate(taxYearEndDate.getDate() - 1);
	        return taxYearEndDate.toISOString().slice(0, 10);
	    }
	};
	const getStandardYearToFiscalYearConversionEndDate = (year, shortYearPeriod) => {
	    const { endMonthYear, startMonthYear } = shortYearPeriod;
	    const shortYearStartYear = startMonthYear.year;
	    const shortYearStartMonth = startMonthYear.month;
	    const shortYearEndMonth = endMonthYear.month;
	    const shortYearEndYear = endMonthYear.year;
	    if (year === shortYearStartYear) {
	        return (0, month_year_utils_1.getLastDateStringOfMonthYear)(endMonthYear);
	    }
	    else if (year > shortYearEndYear && shortYearEndMonth === 11) {
	        return (0, date_utils_1.getLastDayOfTheMonth)(new Date(year, shortYearEndMonth, 1))
	            .toISOString()
	            .slice(0, 10);
	    }
	    else if (year > shortYearStartYear && shortYearEndMonth != 11) {
	        return (0, date_utils_1.getLastDayOfTheMonth)(new Date(year + 1, shortYearEndMonth, 1))
	            .toISOString()
	            .slice(0, 10);
	    }
	    else if (year < shortYearStartYear && shortYearStartMonth === 0) {
	        const taxYearEndDate = new Date(year + 1, shortYearStartMonth, 1);
	        taxYearEndDate.setDate(taxYearEndDate.getDate() - 1);
	        return taxYearEndDate.toISOString().slice(0, 10);
	    }
	    else {
	        const taxYearEndDate = new Date(year, shortYearStartMonth, 1);
	        taxYearEndDate.setDate(taxYearEndDate.getDate() - 1);
	        return taxYearEndDate.toISOString().slice(0, 10);
	    }
	};
	/**
	 * Get the prior fiscal periods for QREs.
	 * @param firstYear - The first year of the range of years to get the tax year and period for.
	 * @param shortYearPeriod - The short tax year period to use for the tax year and period.
	 * @param periodDates - The period dates to use when there is no shortYearPeriod. This is most likely the tax filing start and end dates.
	 * @returns The tax year and period for the given years.
	 */
	const getFiscalPeriodsForQres = ({ firstYear, periodDates, shortYearPeriod, }) => {
	    const currentTaxYear = (0, date_utils_1.getCurrentTaxYear)(periodDates.lastDayOfPeriod);
	    const years = getYearsForQres({ firstYear, shortYearPeriod, currentTaxYear });
	    const includeYearAfterShortYear = currentTaxYear !== shortYearPeriod?.startMonthYear.year;
	    return getFiscalPeriodsBasedOnYears({ years, periodDates, shortYearPeriod, includeYearAfterShortYear });
	};
	exports.getFiscalPeriodsForQres = getFiscalPeriodsForQres;
	/**
	 * Get the prior fiscal periods for Revenues.
	 * @param firstYear - The first year of the range of years to get the tax year and period for.
	 * @param shortYearPeriod - The short tax year period to use for the tax year and period.
	 * @param periodDates - The period dates to use when there is no shortYearPeriod. This is most likely the tax filing start and end dates.
	 * @returns The tax year and period for the given years.
	 */
	const getFiscalPeriodsForRevenues = ({ firstYear, periodDates, shortYearPeriod, }) => {
	    const currentTaxYear = (0, date_utils_1.getCurrentTaxYear)(periodDates.lastDayOfPeriod);
	    const years = getYearsForRevenues({ firstYear, currentTaxYear });
	    // Include year after short year is always true for revenues as we use it to get the current tax year revenue data
	    return getFiscalPeriodsBasedOnYears({ years, periodDates, shortYearPeriod, includeYearAfterShortYear: true });
	};
	exports.getFiscalPeriodsForRevenues = getFiscalPeriodsForRevenues;
	const getFiscalPeriodsBasedOnYears = ({ includeYearAfterShortYear = false, periodDates, shortYearPeriod, years, }) => {
	    if (!years.length) {
	        return [];
	    }
	    // Handle case when no shortYearPeriod is provided
	    if (!shortYearPeriod) {
	        if (!periodDates) {
	            throw new Error('Either shortYearPeriod or periodDates must be provided');
	        }
	        return years.map((year) => ({
	            firstDayOfPeriod: (0, exports.determineTaxYearStartDate)({ year, periodDates }),
	            lastDayOfPeriod: (0, exports.determineTaxYearEndDate)({ year, periodDates }),
	        }));
	    }
	    const sortedYears = years.sort((a, b) => b - a);
	    const periods = [];
	    const shortYearStartYear = shortYearPeriod.startMonthYear.year;
	    // Add periods for years less than or equal to short year
	    sortedYears
	        .filter((year) => year <= shortYearStartYear)
	        .forEach((year) => {
	        periods.push({
	            firstDayOfPeriod: (0, exports.determineTaxYearStartDate)({ year, shortYearPeriod }),
	            lastDayOfPeriod: (0, exports.determineTaxYearEndDate)({ year, shortYearPeriod }),
	        });
	    });
	    // Adds period after short year- special case because it has the same tax year as short year
	    const shortYearEndDate = new Date(shortYearPeriod.endMonthYear.year, shortYearPeriod.endMonthYear.month, 1);
	    const firstDayOfTaxPeriodAfterShortYear = (0, date_utils_1.addMonths)(shortYearEndDate, 1);
	    const shortYearEndDatePlusOneYear = new Date(shortYearPeriod.endMonthYear.year + 1, shortYearPeriod.endMonthYear.month, 1);
	    const lastDayOfTaxPeriodAfterShortYear = (0, date_utils_1.getLastDayOfTheMonth)(shortYearEndDatePlusOneYear).toISOString().slice(0, 10);
	    if (years.includes(shortYearPeriod.startMonthYear.year) && includeYearAfterShortYear) {
	        // Pushes period after short year
	        periods.push({
	            firstDayOfPeriod: firstDayOfTaxPeriodAfterShortYear,
	            lastDayOfPeriod: lastDayOfTaxPeriodAfterShortYear,
	        });
	    }
	    // Add periods for years after short year
	    sortedYears
	        .filter((year) => year > shortYearStartYear)
	        .forEach((year) => {
	        periods.push({
	            firstDayOfPeriod: (0, exports.determineTaxYearStartDate)({ year, shortYearPeriod }),
	            lastDayOfPeriod: (0, exports.determineTaxYearEndDate)({ year, shortYearPeriod }),
	        });
	    });
	    return periods.sort((a, b) => {
	        const dateA = new Date(a.firstDayOfPeriod);
	        const dateB = new Date(b.firstDayOfPeriod);
	        return dateB.getTime() - dateA.getTime();
	    });
	};
	const getYearsForQres = ({ currentTaxYear, firstYear, shortYearPeriod, }) => {
	    const isShortYearThisYear = shortYearPeriod?.startMonthYear.year === currentTaxYear;
	    const mostRecentClosedTaxYear = isShortYearThisYear ? currentTaxYear : currentTaxYear - 1;
	    const upperBoundForQreYears = firstYear ? Math.min(mostRecentClosedTaxYear, firstYear + 9) : mostRecentClosedTaxYear;
	    const lowerBoundForQreYears = firstYear ? firstYear - 1 : currentTaxYear - 4;
	    return (0, array_utils_1.range)(upperBoundForQreYears, lowerBoundForQreYears, -1);
	};
	const getYearsForRevenues = ({ currentTaxYear, firstYear, }) => {
	    const upperBoundForRevenueYears = firstYear ? Math.min(currentTaxYear, firstYear + 9) : currentTaxYear;
	    const lowerBoundForRevenueYears = firstYear ? firstYear - 1 : currentTaxYear - 5;
	    return (0, array_utils_1.range)(upperBoundForRevenueYears, lowerBoundForRevenueYears, -1);
	};
} (priorYearData_utils));

var rdProjectCategory_utils = {};

Object.defineProperty(rdProjectCategory_utils, "__esModule", { value: true });
rdProjectCategory_utils.calculateRdProjectCategory = void 0;
const selected_rd_project_category_enum_1 = selectedRdProjectCategory_enum;
const calculateRdProjectCategory = ({ consideredForRd, isSection174Eligible, }) => {
    if (consideredForRd) {
        return selected_rd_project_category_enum_1.SelectedRdProjectCategory.RD_CREDIT;
    }
    if (isSection174Eligible) {
        return selected_rd_project_category_enum_1.SelectedRdProjectCategory.SECTION_174_ONLY;
    }
    return selected_rd_project_category_enum_1.SelectedRdProjectCategory.EXCLUDED;
};
rdProjectCategory_utils.calculateRdProjectCategory = calculateRdProjectCategory;

var injector = {};

/* eslint-disable @typescript-eslint/no-restricted-types, @typescript-eslint/no-unsafe-function-type */
Object.defineProperty(injector, "__esModule", { value: true });
injector.Injector = void 0;
// TODO: This is shallow class and we could just export result of `NestFactory.create(InternalApiModule)`
// from a file and use where this is used and in main.ts
class Injector {
    static setModuleRef(ref) {
        Injector.moduleRef = ref;
    }
    static get(type) {
        return Injector.moduleRef.get(type);
    }
}
injector.Injector = Injector;

var standardUseCase = {};

var phasedUseCase = {};

Object.defineProperty(phasedUseCase, "__esModule", { value: true });
phasedUseCase.PhasedUseCase = void 0;
class PhasedUseCase {
    /**
     * Identifier used by subclasses to label metrics/logging.
     * Defaults to the runtime class name; override to pin a stable identifier
     * (e.g. against future build-time name mangling).
     */
    get useCaseName() {
        return this.constructor.name;
    }
    /**
     * Hook for wrapping a phase (e.g. with timing/memory instrumentation).
     * Default is pass-through; override in a subclass to add measurement.
     */
    async _runPhase(_phase, _input, run) {
        return await run();
    }
}
phasedUseCase.PhasedUseCase = PhasedUseCase;

Object.defineProperty(standardUseCase, "__esModule", { value: true });
standardUseCase.StandardUseCase = void 0;
const phased_use_case_1$2 = phasedUseCase;
/**
 * @/deprecated Use CommandUseCase or QueryUseCase instead. (@miguel - this has not been agreed to yet, there is active WIP on design here)
 */
class StandardUseCase extends phased_use_case_1$2.PhasedUseCase {
    async execute(input) {
        const loaded = await this._runPhase('load', input, () => this._load(input));
        const transformed = await this._runPhase('transform', loaded, () => this._transform(loaded));
        const saved = await this._runPhase('save', transformed, () => this._save(transformed));
        return {
            loaded,
            transformed,
            saved,
        };
    }
    /**
     * Preview the use case result without saving to database.
     * Runs _load and _transform phases but skips _save.
     * Useful for showing users what changes would be made before committing.
     */
    async preview(input) {
        const loaded = await this._runPhase('load', input, () => this._load(input));
        const transformed = await this._runPhase('transform', loaded, () => this._transform(loaded));
        return {
            loaded,
            transformed,
        };
    }
}
standardUseCase.StandardUseCase = StandardUseCase;

var commandUseCase = {};

Object.defineProperty(commandUseCase, "__esModule", { value: true });
commandUseCase.CommandUseCase = void 0;
const phased_use_case_1$1 = phasedUseCase;
class CommandUseCase extends phased_use_case_1$1.PhasedUseCase {
    async execute(input) {
        const loadResult = await this._runPhase('load', input, () => this._load(input));
        if (!loadResult.success) {
            return loadResult;
        }
        const transformResult = await this._runPhase('transform', loadResult, () => this._transform(input, loadResult.result));
        if (!transformResult.success) {
            return transformResult;
        }
        const saveResult = await this._runPhase('save', transformResult, () => this._save(loadResult.result, transformResult.result));
        if (!saveResult.success) {
            return saveResult;
        }
        const output = await this._runPhase('present', saveResult, () => this._present(input, loadResult.result, transformResult.result, saveResult.result));
        return { success: true, result: output };
    }
}
commandUseCase.CommandUseCase = CommandUseCase;

var queryUseCase = {};

Object.defineProperty(queryUseCase, "__esModule", { value: true });
queryUseCase.QueryUseCase = void 0;
const phased_use_case_1 = phasedUseCase;
class QueryUseCase extends phased_use_case_1.PhasedUseCase {
    _transform(_input, _loaded) {
        return { success: true, result: null };
    }
    async execute(input) {
        const loadResult = await this._runPhase('load', input, () => this._load(input));
        if (!loadResult.success) {
            return loadResult;
        }
        const transformResult = await this._runPhase('transform', loadResult, () => this._transform(input, loadResult.result));
        if (!transformResult.success) {
            return transformResult;
        }
        const output = await this._runPhase('present', transformResult, () => this._present(input, loadResult.result, transformResult.result));
        return { success: true, result: output };
    }
}
queryUseCase.QueryUseCase = QueryUseCase;

var random = {};

Object.defineProperty(random, "__esModule", { value: true });
random.Random = void 0;
const uuid_1 = require$$0;
class Random {
    static uuid() {
        return (0, uuid_1.v4)();
    }
    static hash() {
        return Random.uuid().replace(/-/g, '');
    }
}
random.Random = Random;

var stack = {};

Object.defineProperty(stack, "__esModule", { value: true });
stack.trackStackDepth = void 0;
let maxStackDepth = 0;
/**
 * Tracks the current stack depth by temporarily increasing V8's stack trace limit.
 * This gives us a more accurate measurement of actual stack depth.
 * Note: This is a temporary solution and should be used carefully as it can impact performance.
 */
const trackStackDepth = (location) => {
    // Save current limit
    const originalLimit = Error.stackTraceLimit;
    try {
        // Temporarily increase limit to get full stack
        Error.stackTraceLimit = Infinity;
        const stack = new Error().stack;
        const depth = stack?.split('\n').length ?? 0;
        if (depth > maxStackDepth) {
            maxStackDepth = depth;
            // eslint-disable-next-line no-console
            console.log(`New max stack depth: ${depth} at ${location}`);
        }
    }
    finally {
        // Restore original limit
        Error.stackTraceLimit = originalLimit;
    }
};
stack.trackStackDepth = trackStackDepth;

var rdProjectEligibility = {};

var determineRdProjectEligibility_utils = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.determineRdProjectEligibility = exports.RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING = void 0;
	exports.RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING = {
	    NotQualifiedTechnicalUncertaintyReasoning: 'The project does not meet the permitted purpose requirement for the R&D Credit under Sections 41 and 174, because substantially all of the project did not involve technical uncertainties.',
	    EligibleCustomerFacingReasoning: "This project meets the permitted purpose requirement for the R&D Credit under Sections 41 and 174. Substantially all of the project's work involved addressing core technical uncertainties and followed a process of experimentation and the project was focused on customer-facing product/process.",
	    EligibleInternalUseInnovativeReasoning: "This project meets the permitted purpose requirement for the R&D Credit under Sections 41 and 174. Substantially all of the project's work involved addressing core technical uncertainties and followed a process of experimentation. The project focused on internal-use software, but it meets the high threshold for innovation, involved significant economic risk, and was not commercially available.",
	    NotQualifiedInternalUseReasoning: 'The project does not meet the permitted purpose requirement for the R&D Credit under Sections 41 and 174. The project focuses on internal use software and does not meet the more stringent requirements of high innovation, significant economic risk, and not being commercially available.',
	};
	const determineRdProjectEligibility = (answers) => {
	    // Q1
	    if (answers.isTechnicallyUncertain == null)
	        return { status: 'pending', currentQuestionIndex: 1 };
	    if (answers.isTechnicallyUncertain === false)
	        return {
	            status: 'complete',
	            outcome: 'NOT_QUALIFIED',
	            currentQuestionIndex: 1,
	            qualifiedPurposeReasoning: exports.RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING.NotQualifiedTechnicalUncertaintyReasoning,
	        };
	    // Q2
	    if (answers.isCustomerFacingDevelopment == null)
	        return { status: 'pending', currentQuestionIndex: 2 };
	    if (answers.isCustomerFacingDevelopment === true)
	        return {
	            status: 'complete',
	            outcome: 'ELIGIBLE',
	            currentQuestionIndex: 2,
	            qualifiedPurposeReasoning: exports.RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING.EligibleCustomerFacingReasoning,
	        };
	    // Q3
	    if (answers.isInnovativeAndRisky == null)
	        return { status: 'pending', currentQuestionIndex: 3 };
	    if (answers.isInnovativeAndRisky === true) {
	        return {
	            status: 'complete',
	            outcome: 'ELIGIBLE',
	            currentQuestionIndex: 3,
	            qualifiedPurposeReasoning: exports.RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING.EligibleInternalUseInnovativeReasoning,
	        };
	    }
	    else {
	        return {
	            status: 'complete',
	            outcome: 'NOT_QUALIFIED',
	            currentQuestionIndex: 3,
	            qualifiedPurposeReasoning: exports.RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING.NotQualifiedInternalUseReasoning,
	        };
	    }
	};
	exports.determineRdProjectEligibility = determineRdProjectEligibility;
} (determineRdProjectEligibility_utils));

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING = exports.determineRdProjectEligibility = void 0;
	var determine_rd_project_eligibility_utils_1 = determineRdProjectEligibility_utils;
	Object.defineProperty(exports, "determineRdProjectEligibility", { enumerable: true, get: function () { return determine_rd_project_eligibility_utils_1.determineRdProjectEligibility; } });
	Object.defineProperty(exports, "RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING", { enumerable: true, get: function () { return determine_rd_project_eligibility_utils_1.RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING; } });
} (rdProjectEligibility));

var nameParser = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.parsePersonName = exports.isSurnameParticle = void 0;
	const SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v', 'phd', 'md', 'esq', 'cpa']);
	const PREFIXES = new Set(['dr', 'mr', 'mrs', 'ms', 'mx']);
	// Common surname particles. Used to detect compound last names like "van der Berg"
	// in non-comma input. Matched case-insensitively so ALL-CAPS payroll feeds (common
	// from Gusto / ADP / Rippling exports) parse the same as mixed-case ones.
	const LAST_NAME_PARTICLES = new Set([
	    'van',
	    'von',
	    'der',
	    'den',
	    'de',
	    'del',
	    'della',
	    'di',
	    'da',
	    'du',
	    'la',
	    'le',
	    'los',
	    'las',
	    'dos',
	    'do',
	    'ten',
	    'ter',
	    'bin',
	    'ibn',
	    'ben',
	    'al',
	    'el',
	]);
	const stripTrailingPunctuation = (token) => token.replace(/[.,;]+$/u, '');
	const normalizeToken = (token) => stripTrailingPunctuation(token).toLowerCase();
	const isSuffix = (token) => SUFFIXES.has(normalizeToken(token));
	const isPrefix = (token) => PREFIXES.has(normalizeToken(token));
	/** Whether a token is a surname particle rather than a name in its own right, as in "van der Berg". */
	const isSurnameParticle = (token) => LAST_NAME_PARTICLES.has(normalizeToken(token));
	exports.isSurnameParticle = isSurnameParticle;
	const splitWhitespace = (input) => input.split(/\s+/u).filter((t) => t.length > 0);
	/**
	 * Parse a person name string into a normalized full form and a last-name key suitable for
	 * cohort indexing. Handles both "First Last" and "Last, First" formats and strips common
	 * honorific prefixes (Dr/Mr/Mrs/Ms/Mx) and generational/professional suffixes
	 * (Jr/Sr/II/III/IV/V/PhD/MD/Esq/CPA).
	 *
	 * Returns null if the input is empty or contains no recognizable last token.
	 */
	const parsePersonName = (raw) => {
	    if (raw === null || raw === undefined) {
	        return null;
	    }
	    const trimmed = raw.trim();
	    if (trimmed.length === 0) {
	        return null;
	    }
	    let firstTokens;
	    let lastTokens;
	    // Detect "Last, First" vs "First Last, Suffix": only treat the comma as a Last/First
	    // separator when the post-comma chunk contains at least one non-suffix token.
	    const commaIndex = trimmed.indexOf(',');
	    const postCommaTokens = commaIndex !== -1 ? splitWhitespace(trimmed.slice(commaIndex + 1)).filter((t) => !isPrefix(t)) : [];
	    const isLastFirstFormat = commaIndex !== -1 && postCommaTokens.some((t) => !isSuffix(t));
	    if (isLastFirstFormat) {
	        const lastChunk = trimmed.slice(0, commaIndex);
	        lastTokens = splitWhitespace(lastChunk).filter((t) => !isSuffix(t));
	        firstTokens = postCommaTokens.filter((t) => !isSuffix(t));
	    }
	    else {
	        const allTokens = splitWhitespace(trimmed).filter((t) => !isPrefix(t) && !isSuffix(t));
	        if (allTokens.length === 0) {
	            return null;
	        }
	        // If a known particle ("van", "de", "von", ...) appears mid-name with at least
	        // one token on each side, treat everything from that particle onward as the
	        // last name. This produces the same result as the comma format for compound
	        // surnames so both formats land in the same last-name cohort.
	        const particleIdx = allTokens.findIndex((t) => (0, exports.isSurnameParticle)(t));
	        if (particleIdx >= 1 && particleIdx < allTokens.length - 1) {
	            firstTokens = allTokens.slice(0, particleIdx);
	            lastTokens = allTokens.slice(particleIdx);
	        }
	        else {
	            lastTokens = [allTokens[allTokens.length - 1]];
	            firstTokens = allTokens.slice(0, -1);
	        }
	    }
	    if (lastTokens.length === 0) {
	        return null;
	    }
	    const lastNormalized = lastTokens.map(normalizeToken).join(' ');
	    if (lastNormalized.length === 0) {
	        return null;
	    }
	    const firstNormalized = firstTokens
	        .map(normalizeToken)
	        .filter((t) => t.length > 0)
	        .join(' ');
	    const fullParts = [...firstTokens, ...lastTokens].map(normalizeToken).filter((t) => t.length > 0);
	    const full = fullParts.join(' ');
	    return { full, first: firstNormalized, last: lastNormalized };
	};
	exports.parsePersonName = parsePersonName;
} (nameParser));

var jaroWinkler = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.jaroWinklerSimilarity = exports.jaroSimilarity = void 0;
	/**
	 * Jaro similarity: a metric in [0, 1] over two strings, where 1 is identical.
	 * Counts matching characters (within a sliding window) and penalizes transpositions.
	 */
	const jaroSimilarity = (s1, s2) => {
	    if (s1 === s2) {
	        return 1;
	    }
	    if (s1.length === 0 || s2.length === 0) {
	        return 0;
	    }
	    const matchWindow = Math.max(0, Math.floor(Math.max(s1.length, s2.length) / 2) - 1);
	    const s1Matches = new Array(s1.length).fill(false);
	    const s2Matches = new Array(s2.length).fill(false);
	    let matches = 0;
	    for (let i = 0; i < s1.length; i += 1) {
	        const start = Math.max(0, i - matchWindow);
	        const end = Math.min(i + matchWindow + 1, s2.length);
	        for (let j = start; j < end; j += 1) {
	            if (s2Matches[j])
	                continue;
	            if (s1[i] !== s2[j])
	                continue;
	            s1Matches[i] = true;
	            s2Matches[j] = true;
	            matches += 1;
	            break;
	        }
	    }
	    if (matches === 0) {
	        return 0;
	    }
	    let transpositions = 0;
	    let k = 0;
	    for (let i = 0; i < s1.length; i += 1) {
	        if (!s1Matches[i])
	            continue;
	        while (!s2Matches[k])
	            k += 1;
	        if (s1[i] !== s2[k])
	            transpositions += 1;
	        k += 1;
	    }
	    return (matches / s1.length + matches / s2.length + (matches - transpositions / 2) / matches) / 3;
	};
	exports.jaroSimilarity = jaroSimilarity;
	/**
	 * Jaro-Winkler similarity: Jaro with an explicit bonus for shared prefix characters
	 * (up to 4 chars). Designed for name matching — used by the US Census Bureau and credit
	 * bureaus because it weights the start of a string heavily, which matches human intuition
	 * about typos vs different names.
	 *
	 * @param prefixScale - Per-prefix-char boost. Standard value 0.1; max 0.25.
	 * @param maxPrefixLength - Cap on how many leading shared chars contribute. Standard 4.
	 */
	const jaroWinklerSimilarity = (s1, s2, prefixScale = 0.1, maxPrefixLength = 4) => {
	    const jaro = (0, exports.jaroSimilarity)(s1, s2);
	    if (jaro === 0 || jaro === 1) {
	        return jaro;
	    }
	    let prefixLength = 0;
	    const limit = Math.min(maxPrefixLength, s1.length, s2.length);
	    while (prefixLength < limit && s1[prefixLength] === s2[prefixLength]) {
	        prefixLength += 1;
	    }
	    return jaro + prefixLength * prefixScale * (1 - jaro);
	};
	exports.jaroWinklerSimilarity = jaroWinklerSimilarity;
} (jaroWinkler));

var contributorMatcher = {};

Object.defineProperty(contributorMatcher, "__esModule", { value: true });
contributorMatcher.findBestContributorMatch = contributorMatcher.buildContributorMatchIndex = contributorMatcher.DEFAULT_FUZZY_MATCH_THRESHOLD = void 0;
const jaro_winkler_1 = jaroWinkler;
const name_parser_1 = nameParser;
/** Fallback threshold when no per-company override is configured. */
contributorMatcher.DEFAULT_FUZZY_MATCH_THRESHOLD = 0.9;
const pushIntoMultimap = (map, key, value) => {
    const existing = map.get(key);
    if (existing) {
        existing.push(value);
    }
    else {
        map.set(key, [value]);
    }
};
/**
 * Build an index over a list of candidate items keyed by normalized full name and last name.
 *
 * Cost is O(n) and the resulting index is intended to be reused across many lookups
 * (e.g. one per payroll row) so each lookup is bounded by the size of the last-name
 * cohort rather than the full candidate set.
 */
const buildContributorMatchIndex = (items, getName) => {
    const byExactName = new Map();
    const byLastName = new Map();
    for (const item of items) {
        const raw = getName(item);
        const parsed = (0, name_parser_1.parsePersonName)(raw);
        if (!parsed) {
            continue;
        }
        pushIntoMultimap(byExactName, parsed.full, item);
        pushIntoMultimap(byLastName, parsed.last, { item, full: parsed.full, first: parsed.first });
    }
    return { byExactName, byLastName };
};
contributorMatcher.buildContributorMatchIndex = buildContributorMatchIndex;
/**
 * Find the best contributor for a query name using:
 * 1. Exact full-name fast path. When multiple DB rows share the same canonical name they
 *    are treated as duplicates of the same person — first one is picked deterministically.
 * 2. Jaro-Winkler scoring against the candidates that share the query's last name. JW is
 *    prefix-weighted, which matches human intuition about typos: "Jhon"/"John" scores
 *    high, "DoriAnn"/"Brianna" does not.
 *
 * Cohort entries are first grouped by normalized full name so duplicates compete as a
 * single candidate. Ties **between distinct names** at the top score are rejected (returns
 * null) to avoid silently picking between two genuinely different people.
 */
const findBestContributorMatch = (index, rawName, threshold) => {
    const parsed = (0, name_parser_1.parsePersonName)(rawName);
    if (!parsed) {
        return null;
    }
    const exactMatches = index.byExactName.get(parsed.full);
    if (exactMatches !== undefined && exactMatches.length > 0) {
        return { item: exactMatches[0], score: 1, winners: exactMatches };
    }
    const cohort = index.byLastName.get(parsed.last);
    if (!cohort || cohort.length === 0) {
        return null;
    }
    // No first-name signal on the query — only the exact-match path could have helped here,
    // and it didn't. Bail rather than score against an empty string and inflate every
    // candidate to the same low Jaro value.
    if (parsed.first.length === 0) {
        return null;
    }
    // Group cohort entries by normalized full name so N rows with identical canonical
    // names (e.g. five "Jennifer Jones" in the table) compete as one candidate. We
    // keep the first-name string alongside, since within a cohort the last name is
    // identical for everyone and only the first/middle portion carries match signal.
    const itemsByFull = new Map();
    for (const entry of cohort) {
        const existing = itemsByFull.get(entry.full);
        if (existing) {
            existing.items.push(entry.item);
        }
        else {
            itemsByFull.set(entry.full, { items: [entry.item], first: entry.first });
        }
    }
    let bestScore = -1;
    let bestFull = null;
    let tied = false;
    for (const [full, { first }] of itemsByFull) {
        if (first.length === 0) {
            // Candidate has no first name (single-token canonical name). The exact path
            // would have caught a real match; skip in the fuzzy scan to avoid scoring "".
            continue;
        }
        // Score the first-and-middle portion only. The last name is guaranteed identical
        // within this cohort, so including it double-counts and lets common-surname
        // pairs like "Mona Ballard" vs "Mary Ballard" sneak above the threshold.
        const score = (0, jaro_winkler_1.jaroWinklerSimilarity)(parsed.first, first);
        if (score < threshold) {
            continue;
        }
        if (score > bestScore) {
            bestScore = score;
            bestFull = full;
            tied = false;
        }
        else if (score === bestScore) {
            tied = true;
        }
    }
    if (bestFull === null || tied) {
        return null;
    }
    const winners = itemsByFull.get(bestFull).items;
    return { item: winners[0], score: bestScore, winners };
};
contributorMatcher.findBestContributorMatch = findBestContributorMatch;

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.isDatetime = exports.getDatetime = exports.getMonthLongAbbreviation = exports.getMonthShortAbbreviation = exports.getEndOfDay = exports.getFirstDayOfTheMonth = exports.getLastDayOfTheMonth = exports.longDateFormat = exports.placedInServiceDate = exports.americanDateFormat = exports.dateToDayjs = exports.isStandardFiscalYear = exports.getFirstDayOfPeriodByLastDay = exports.removeZeroPadding = exports.addZeroPadding = exports.isAfterInitialTaxDeadline = exports.toIsoDateString = exports.getEntryForTaxYear = exports.currentDateToIsoString = exports.getLastDayOfTheYear = exports.sameDateString = exports.isLeapYear = exports.upperBoundForTaxReturnFilingDate = exports.getEndOfYear = exports.getStartOfYear = exports.getEntryForCurrentTaxYear = exports.getYearFromStringDate = exports.isDateString = exports.getCurrentDatetime = exports.getCurrentYear = exports.getCurrentDateObject = exports.getCurrentDate = exports.formatDate = exports.formatDayjs = exports.endOfFiscalYearDate = exports.getCurrentTaxYear = exports.getExtensionTaxDeadline = exports.getInitialTaxDeadline = exports.computeNeoTaxFeeDetails = exports.MAX_SEARCH_FIREBASE_IDENTITIES_BY_NAME = exports.MAX_SEARCH_FIREBASE_IDENTITIES = exports.TEST_CAPTURED_EMAILS_LOG_PATH = exports.PER_CONTRIBUTOR_ALLOWED_ROUNDING_ERROR = exports.DB_ROUNDING_NUM_DECIMALS = exports.USER_ROUNDING_NUM_DECIMALS = exports.US_COUNTRY_CODE = exports.LAST_MONTH_DATE_DICT = exports.OLDEST_SUPPORTED_QRE_PYR_YEAR = exports.COMMON_CONSTANTS = exports.MAX_RD_PAYROLL_TAX_CREDIT_CENTS = void 0;
	exports.CapitalizationChangeReason = exports.calculateEarliestAffectedCapitalizationMonth = exports.getMonthYearPeriodSuperset = exports.dateStringToReadableMonthYearString = exports.getMonthYearPeriodUsingDates = exports.getTaxYearMonthYearPeriod = exports.MONTH_PERIOD_NAMES = exports.getPeriodName = exports.getCurrentFiscalYearMonthYearPeriod = exports.getEarliestAndLatestMonthYear = exports.isMonthYear = exports.formatMonthYear = exports.formatMonthYearPeriod = exports.formatTaxPeriod = exports.formatFiscalPeriod = exports.formatFiscalOrTaxPeriod = exports.calculateQuarter = exports.areSameMonthYearPeriod = exports.isPeriodFullYear = exports.doPeriodsOverlap = exports.getMonthYearPeriodIntersection = exports.getNumberOfMonthsInPeriod = exports.getNumMonthsBetweenInclusively = exports.toMonthYearPeriod = exports.isMonthYearBefore = exports.sameMonthYear = exports.minMonthYear = exports.maxMonthYear = exports.getMonthName = exports.getMonthsBetweenAsc = exports.generateMonthYearInRange = exports.getLastDateStringOfMonthYear = exports.getLastDateOfMonthYear = exports.getFirstDateStringOfMonthYear = exports.getFirstDateOfMonthYear = exports.stringToMonthYear = exports.dateToMonthYearString = exports.toMonthYearString = exports.dateToMonthYear = exports.areMonthYearEqual = exports.isMonthYearInRange = exports.isMonthYearAfterOrEqual = exports.isMonthYearBeforeOrEqual = exports.organizationMembersFileName = exports.getNextMonthYear = exports.getPreviousMonthYear = exports.isDateInMonthYearRange = exports.isTaxYearTraditional = exports.subtractMonths = exports.addMonths = void 0;
	exports.GitHubAppInstallQueryField = exports.QuickbooksMonth = exports.QuickbooksConnectionStatus = exports.AccountingAcquisitionMethod = exports.EntityType = exports.SalesChannel = exports.getRevenueSourceRank = exports.RevenueSource = exports.HardSciences = exports.RoundingMethod = exports.AccountType = exports.AccountClassification = exports.ServiceScope = exports.NeoTaxEnvironment = exports.SsoQueryField = exports.QuickbooksQueryField = exports.convertExpenseCategoryToNum = exports.ExpenseCategory = exports.AnalyticIdentifier = exports.ResearchActivities = exports.DocumentationTools = exports.divideResearchActivities = exports.combineTools = exports.divideTools = exports.ExtendedGraphQLErrors = exports.ErrorWithStatusCode = exports.handleCaughtError = exports.createClientErrors = exports.createClientError = exports.createError = exports.createNamedErrorClass = exports.pause = exports.getActualBusinessStartIsoDate = exports.centsToNumberWithCommas = exports.centsToDollars = exports.dollarsToCents = exports.convertToCents = exports.removeDollarSignAndCommas = exports.parseCents = exports.formatDollarsFromCents = exports.formatNumberToUsd = exports.floorToNearest = exports.roundToNearest = exports.isNotPositiveNumber = exports.round80To100TaxRule = exports.toNumOrZero = exports.divideBy100 = exports.areTwoNumbersCloseEnough = exports.abbreviateNumber = exports.formatNumberWithCommas = void 0;
	exports.dateTimeOptionalTimeZoneStringSchema = exports.uuidSchema = exports.vendorSchema = exports.dollarsToCentsSchema = exports.commaDelimitedStringSchema = exports.nullableOptionalStringSchema = exports.nullableStringSchema = exports.stringSchema = exports.positiveIntSchema = exports.datetimeSchema = exports.jsonObjectSchema = exports.jsonSchema = exports.assertNeverType = exports.assertNever = exports.MAX_NUM_RETRY_REQUESTS = exports.retry = exports.isNeoTaxEmail = exports.isEmailIdentifier = exports.normalizeEmailIdentifier = exports.normalizeEmail = exports.AuthErrorCode = exports.ProductType = exports.FinchScopeTypeSchema = exports.FinchScopeType = exports.sredDocumentationStatusLabel = exports.SredDocumentationStatus = exports.sredProjectCategoryLabel = exports.SelectedSredProjectCategory = exports.rdDocumentationStatusLabel = exports.RdDocumentationStatus = exports.PredictedCapitalizationStatus = exports.PREDICTED_CAPITALIZATION_STATUS_LABELS = exports.rdProjectCategoryLabel = exports.SelectedRdProjectCategory = exports.periodNameSchema = exports.PeriodName = exports.PayrollMonthExpenseProvider = exports.ErrorStatusCode = exports.ValueType = exports.CompanyEntityType = exports.EligibilityStatus = exports.CorporationType = exports.ServiceOperationalStatus = exports.WageType = exports.PayrollProvider = exports.AccountingProvider = exports.PayrollAcquisitionMethod = exports.SsoError = exports.DateMonth = exports.GitHubAppInstallStatus = void 0;
	exports.AscProjectDeletionErrorSchema = exports.StandardErrorSchema = exports.BaseErrorSchema = exports.safeParseExtendedGraphQlError = exports.safeParseExtendedApolloError = exports.parseExtendedGraphQlError = exports.parseExtendedApolloError = exports.ThomsonReutersCallbackPayloadStateSchema = exports.GustoCallbackPayloadStateSchema = exports.QuickbooksCallbackPayloadStateSchema = exports.IntuitSsoCallbackPayloadStateSchema = exports.QuickbooksDataLinkingCallbackPayloadStateSchema = exports.SsoStateSchema = exports.formatAsElementId = exports.capitalizeFirstLetter = exports.snakeCase = exports.formatArrayToCommaAndString = exports.escapeLikePattern = exports.normalizeWhitespaceAndCase = exports.sanitizeString = exports.combineAddresses = exports.truncateString = exports.spaceToDash = exports.kebabCase = exports.constantCase = exports.capitalCase = exports.parsePercentageIntegerValue = exports.slackEventDtoSchema = exports.singleTenantDomainRedirectInputSchema = exports.singleTenantRedirectUrlSchema = exports.singleTenantEmailDomainSchema = exports.SSO_PROVIDER_NAME_REGEX = exports.ssoProviderNameSchema = exports.monthYearStringSchema = exports.parseOrThrowClientError = exports.enumSchemaFromDynamicArray = exports.monthYearPeriodNullableSchema = exports.monthYearPeriodSchema = exports.monthYearSchema = exports.monthSchema = exports.emailSchema = exports.yearSchema = exports.nonNegativeNumberSchema = exports.intSchema = exports.jsonArraySchema = exports.notSpecifiedTransform = exports.dateCoercionSchema = exports.dateTimeOptionalTimeZoneRegex = exports.dateStringSchema = exports.isoDateTimeStringSchema = void 0;
	exports.maxMonthYearNullable = exports.formatLockBoundary = exports.validateNotBeforeLockBoundary = exports.isBeforeOrOnLockBoundary = exports.getFirstAllowableCapitalizationStartMonthYear = exports.isProjectDateRangeFullyLocked = exports.isCapitalizationEndDateLocked = exports.isCapitalizationStartDateLocked = exports.isCapitalizationStatusLocked = exports.getAverageAllocationInPeriod = exports.ALLOCATION_DIFF_THRESHOLD = exports.roundProjectAllocationPercents = exports.roundAllocationsToTotal = exports.updateAllocationsInPeriod = exports.getLatestAllocationPercent = exports.ErrorStatusToHttpStatusCode = exports.ProvisioningErrorCodes = exports.EMAIL_SESSION_CLAIM = exports.BlockingFunctionRejectionCodes = exports.LOGIN_METHOD_WINDOW_SECONDS = exports.LOGIN_METHOD_MAGIC_LINK = exports.LOGIN_METHOD_HEADER_NAME = exports.throttle = exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = exports.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS = exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = exports.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS = exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED = exports.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS = exports.toJson = exports.sortJsonByKeys = exports.sanitizeJson = exports.safeDateExtractFromJson = exports.getStaticCopy = exports.areJsonEqual = exports.questionKeyForPyr = exports.questionKeyForQre = exports.deduplicate = exports.chunk = exports.removeElement = exports.range = exports.moveElement = exports.insertElements = exports.OrganizationInvitationErrorCodes = exports.OrganizationInvitationErrorSchema = exports.DocumentNotParsableErrorSchema = exports.FinchProviderKeySchema = exports.ApplyWeightedRdPercentageToExpensesErrorSchema = exports.QuestionAnswerErrorSchema = exports.RdProjectDeletionErrorSchema = void 0;
	exports.findBestContributorMatch = exports.buildContributorMatchIndex = exports.DEFAULT_FUZZY_MATCH_THRESHOLD = exports.jaroWinklerSimilarity = exports.parsePersonName = exports.isSurnameParticle = exports.RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING = exports.determineRdProjectEligibility = exports.trackStackDepth = exports.Random = exports.QueryUseCase = exports.CommandUseCase = exports.StandardUseCase = exports.Injector = exports.calculateRdProjectCategory = exports.getFiscalPeriodsForRevenues = exports.getFiscalPeriodsForQres = exports.determineTaxYearEndDate = exports.determineTaxYearStartDate = exports.findFiscalYearsInPeriod = exports.isProductionLikeEnvironment = exports.hasStandardErrorCode = exports.hasErrorCode = exports.getStandardErrors = exports.getExtendedErrors = exports.extendedGraphQlErrorHasErrorCode = exports.extendedApolloErrorHasErrorCode = exports.ASC_NOISE_CLUSTER_TYPES = exports.isAscNoiseClusterType = exports.AscClusterType = exports.isUsedInCapitalizableProjectClustering = exports.getCapitalizableProjectClusteringStatus = exports.CLUSTERING_METHOD = exports.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE = void 0;
	var constants_1 = constants$1;
	Object.defineProperty(exports, "MAX_RD_PAYROLL_TAX_CREDIT_CENTS", { enumerable: true, get: function () { return constants_1.MAX_RD_PAYROLL_TAX_CREDIT_CENTS; } });
	Object.defineProperty(exports, "COMMON_CONSTANTS", { enumerable: true, get: function () { return constants_1.COMMON_CONSTANTS; } });
	Object.defineProperty(exports, "OLDEST_SUPPORTED_QRE_PYR_YEAR", { enumerable: true, get: function () { return constants_1.OLDEST_SUPPORTED_QRE_PYR_YEAR; } });
	Object.defineProperty(exports, "LAST_MONTH_DATE_DICT", { enumerable: true, get: function () { return constants_1.LAST_MONTH_DATE_DICT; } });
	Object.defineProperty(exports, "US_COUNTRY_CODE", { enumerable: true, get: function () { return constants_1.US_COUNTRY_CODE; } });
	Object.defineProperty(exports, "USER_ROUNDING_NUM_DECIMALS", { enumerable: true, get: function () { return constants_1.USER_ROUNDING_NUM_DECIMALS; } });
	Object.defineProperty(exports, "DB_ROUNDING_NUM_DECIMALS", { enumerable: true, get: function () { return constants_1.DB_ROUNDING_NUM_DECIMALS; } });
	Object.defineProperty(exports, "PER_CONTRIBUTOR_ALLOWED_ROUNDING_ERROR", { enumerable: true, get: function () { return constants_1.PER_CONTRIBUTOR_ALLOWED_ROUNDING_ERROR; } });
	Object.defineProperty(exports, "TEST_CAPTURED_EMAILS_LOG_PATH", { enumerable: true, get: function () { return constants_1.TEST_CAPTURED_EMAILS_LOG_PATH; } });
	Object.defineProperty(exports, "MAX_SEARCH_FIREBASE_IDENTITIES", { enumerable: true, get: function () { return constants_1.MAX_SEARCH_FIREBASE_IDENTITIES; } });
	Object.defineProperty(exports, "MAX_SEARCH_FIREBASE_IDENTITIES_BY_NAME", { enumerable: true, get: function () { return constants_1.MAX_SEARCH_FIREBASE_IDENTITIES_BY_NAME; } });
	var neo_tax_fee_details_1 = neoTaxFeeDetails;
	Object.defineProperty(exports, "computeNeoTaxFeeDetails", { enumerable: true, get: function () { return neo_tax_fee_details_1.computeNeoTaxFeeDetails; } });
	var date_utils_1 = date_utils;
	Object.defineProperty(exports, "getInitialTaxDeadline", { enumerable: true, get: function () { return date_utils_1.getInitialTaxDeadline; } });
	Object.defineProperty(exports, "getExtensionTaxDeadline", { enumerable: true, get: function () { return date_utils_1.getExtensionTaxDeadline; } });
	Object.defineProperty(exports, "getCurrentTaxYear", { enumerable: true, get: function () { return date_utils_1.getCurrentTaxYear; } });
	Object.defineProperty(exports, "endOfFiscalYearDate", { enumerable: true, get: function () { return date_utils_1.endOfFiscalYearDate; } });
	Object.defineProperty(exports, "formatDayjs", { enumerable: true, get: function () { return date_utils_1.formatDayjs; } });
	Object.defineProperty(exports, "formatDate", { enumerable: true, get: function () { return date_utils_1.formatDate; } });
	Object.defineProperty(exports, "getCurrentDate", { enumerable: true, get: function () { return date_utils_1.getCurrentDate; } });
	Object.defineProperty(exports, "getCurrentDateObject", { enumerable: true, get: function () { return date_utils_1.getCurrentDateObject; } });
	Object.defineProperty(exports, "getCurrentYear", { enumerable: true, get: function () { return date_utils_1.getCurrentYear; } });
	Object.defineProperty(exports, "getCurrentDatetime", { enumerable: true, get: function () { return date_utils_1.getCurrentDatetime; } });
	Object.defineProperty(exports, "isDateString", { enumerable: true, get: function () { return date_utils_1.isDateString; } });
	Object.defineProperty(exports, "getYearFromStringDate", { enumerable: true, get: function () { return date_utils_1.getYearFromStringDate; } });
	Object.defineProperty(exports, "getEntryForCurrentTaxYear", { enumerable: true, get: function () { return date_utils_1.getEntryForCurrentTaxYear; } });
	Object.defineProperty(exports, "getStartOfYear", { enumerable: true, get: function () { return date_utils_1.getStartOfYear; } });
	Object.defineProperty(exports, "getEndOfYear", { enumerable: true, get: function () { return date_utils_1.getEndOfYear; } });
	Object.defineProperty(exports, "upperBoundForTaxReturnFilingDate", { enumerable: true, get: function () { return date_utils_1.upperBoundForTaxReturnFilingDate; } });
	Object.defineProperty(exports, "isLeapYear", { enumerable: true, get: function () { return date_utils_1.isLeapYear; } });
	Object.defineProperty(exports, "sameDateString", { enumerable: true, get: function () { return date_utils_1.sameDateString; } });
	Object.defineProperty(exports, "getLastDayOfTheYear", { enumerable: true, get: function () { return date_utils_1.getLastDayOfTheYear; } });
	Object.defineProperty(exports, "currentDateToIsoString", { enumerable: true, get: function () { return date_utils_1.currentDateToIsoString; } });
	Object.defineProperty(exports, "getEntryForTaxYear", { enumerable: true, get: function () { return date_utils_1.getEntryForTaxYear; } });
	Object.defineProperty(exports, "toIsoDateString", { enumerable: true, get: function () { return date_utils_1.toIsoDateString; } });
	Object.defineProperty(exports, "isAfterInitialTaxDeadline", { enumerable: true, get: function () { return date_utils_1.isAfterInitialTaxDeadline; } });
	Object.defineProperty(exports, "addZeroPadding", { enumerable: true, get: function () { return date_utils_1.addZeroPadding; } });
	Object.defineProperty(exports, "removeZeroPadding", { enumerable: true, get: function () { return date_utils_1.removeZeroPadding; } });
	Object.defineProperty(exports, "getFirstDayOfPeriodByLastDay", { enumerable: true, get: function () { return date_utils_1.getFirstDayOfPeriodByLastDay; } });
	Object.defineProperty(exports, "isStandardFiscalYear", { enumerable: true, get: function () { return date_utils_1.isStandardFiscalYear; } });
	Object.defineProperty(exports, "dateToDayjs", { enumerable: true, get: function () { return date_utils_1.dateToDayjs; } });
	Object.defineProperty(exports, "americanDateFormat", { enumerable: true, get: function () { return date_utils_1.americanDateFormat; } });
	Object.defineProperty(exports, "placedInServiceDate", { enumerable: true, get: function () { return date_utils_1.placedInServiceDate; } });
	Object.defineProperty(exports, "longDateFormat", { enumerable: true, get: function () { return date_utils_1.longDateFormat; } });
	Object.defineProperty(exports, "getLastDayOfTheMonth", { enumerable: true, get: function () { return date_utils_1.getLastDayOfTheMonth; } });
	Object.defineProperty(exports, "getFirstDayOfTheMonth", { enumerable: true, get: function () { return date_utils_1.getFirstDayOfTheMonth; } });
	Object.defineProperty(exports, "getEndOfDay", { enumerable: true, get: function () { return date_utils_1.getEndOfDay; } });
	Object.defineProperty(exports, "getMonthShortAbbreviation", { enumerable: true, get: function () { return date_utils_1.getMonthShortAbbreviation; } });
	Object.defineProperty(exports, "getMonthLongAbbreviation", { enumerable: true, get: function () { return date_utils_1.getMonthLongAbbreviation; } });
	Object.defineProperty(exports, "getDatetime", { enumerable: true, get: function () { return date_utils_1.getDatetime; } });
	Object.defineProperty(exports, "isDatetime", { enumerable: true, get: function () { return date_utils_1.isDatetime; } });
	Object.defineProperty(exports, "addMonths", { enumerable: true, get: function () { return date_utils_1.addMonths; } });
	Object.defineProperty(exports, "subtractMonths", { enumerable: true, get: function () { return date_utils_1.subtractMonths; } });
	Object.defineProperty(exports, "isTaxYearTraditional", { enumerable: true, get: function () { return date_utils_1.isTaxYearTraditional; } });
	Object.defineProperty(exports, "isDateInMonthYearRange", { enumerable: true, get: function () { return date_utils_1.isDateInMonthYearRange; } });
	Object.defineProperty(exports, "getPreviousMonthYear", { enumerable: true, get: function () { return date_utils_1.getPreviousMonthYear; } });
	Object.defineProperty(exports, "getNextMonthYear", { enumerable: true, get: function () { return date_utils_1.getNextMonthYear; } });
	var organization_members_export_utils_1 = organizationMembersExport_utils;
	Object.defineProperty(exports, "organizationMembersFileName", { enumerable: true, get: function () { return organization_members_export_utils_1.organizationMembersFileName; } });
	var month_year_utils_1 = monthYear_utils;
	Object.defineProperty(exports, "isMonthYearBeforeOrEqual", { enumerable: true, get: function () { return month_year_utils_1.isMonthYearBeforeOrEqual; } });
	Object.defineProperty(exports, "isMonthYearAfterOrEqual", { enumerable: true, get: function () { return month_year_utils_1.isMonthYearAfterOrEqual; } });
	Object.defineProperty(exports, "isMonthYearInRange", { enumerable: true, get: function () { return month_year_utils_1.isMonthYearInRange; } });
	Object.defineProperty(exports, "areMonthYearEqual", { enumerable: true, get: function () { return month_year_utils_1.areMonthYearEqual; } });
	Object.defineProperty(exports, "dateToMonthYear", { enumerable: true, get: function () { return month_year_utils_1.dateToMonthYear; } });
	Object.defineProperty(exports, "toMonthYearString", { enumerable: true, get: function () { return month_year_utils_1.toMonthYearString; } });
	Object.defineProperty(exports, "dateToMonthYearString", { enumerable: true, get: function () { return month_year_utils_1.dateToMonthYearString; } });
	Object.defineProperty(exports, "stringToMonthYear", { enumerable: true, get: function () { return month_year_utils_1.stringToMonthYear; } });
	Object.defineProperty(exports, "getFirstDateOfMonthYear", { enumerable: true, get: function () { return month_year_utils_1.getFirstDateOfMonthYear; } });
	Object.defineProperty(exports, "getFirstDateStringOfMonthYear", { enumerable: true, get: function () { return month_year_utils_1.getFirstDateStringOfMonthYear; } });
	Object.defineProperty(exports, "getLastDateOfMonthYear", { enumerable: true, get: function () { return month_year_utils_1.getLastDateOfMonthYear; } });
	Object.defineProperty(exports, "getLastDateStringOfMonthYear", { enumerable: true, get: function () { return month_year_utils_1.getLastDateStringOfMonthYear; } });
	Object.defineProperty(exports, "generateMonthYearInRange", { enumerable: true, get: function () { return month_year_utils_1.generateMonthYearInRange; } });
	Object.defineProperty(exports, "getMonthsBetweenAsc", { enumerable: true, get: function () { return month_year_utils_1.getMonthsBetweenAsc; } });
	Object.defineProperty(exports, "getMonthName", { enumerable: true, get: function () { return month_year_utils_1.getMonthName; } });
	Object.defineProperty(exports, "maxMonthYear", { enumerable: true, get: function () { return month_year_utils_1.maxMonthYear; } });
	Object.defineProperty(exports, "minMonthYear", { enumerable: true, get: function () { return month_year_utils_1.minMonthYear; } });
	Object.defineProperty(exports, "sameMonthYear", { enumerable: true, get: function () { return month_year_utils_1.sameMonthYear; } });
	Object.defineProperty(exports, "isMonthYearBefore", { enumerable: true, get: function () { return month_year_utils_1.isMonthYearBefore; } });
	Object.defineProperty(exports, "toMonthYearPeriod", { enumerable: true, get: function () { return month_year_utils_1.toMonthYearPeriod; } });
	Object.defineProperty(exports, "getNumMonthsBetweenInclusively", { enumerable: true, get: function () { return month_year_utils_1.getNumMonthsBetweenInclusively; } });
	Object.defineProperty(exports, "getNumberOfMonthsInPeriod", { enumerable: true, get: function () { return month_year_utils_1.getNumberOfMonthsInPeriod; } });
	Object.defineProperty(exports, "getMonthYearPeriodIntersection", { enumerable: true, get: function () { return month_year_utils_1.getMonthYearPeriodIntersection; } });
	Object.defineProperty(exports, "doPeriodsOverlap", { enumerable: true, get: function () { return month_year_utils_1.doPeriodsOverlap; } });
	Object.defineProperty(exports, "isPeriodFullYear", { enumerable: true, get: function () { return month_year_utils_1.isPeriodFullYear; } });
	Object.defineProperty(exports, "areSameMonthYearPeriod", { enumerable: true, get: function () { return month_year_utils_1.areSameMonthYearPeriod; } });
	Object.defineProperty(exports, "calculateQuarter", { enumerable: true, get: function () { return month_year_utils_1.calculateQuarter; } });
	Object.defineProperty(exports, "formatFiscalOrTaxPeriod", { enumerable: true, get: function () { return month_year_utils_1.formatFiscalOrTaxPeriod; } });
	Object.defineProperty(exports, "formatFiscalPeriod", { enumerable: true, get: function () { return month_year_utils_1.formatFiscalPeriod; } });
	Object.defineProperty(exports, "formatTaxPeriod", { enumerable: true, get: function () { return month_year_utils_1.formatTaxPeriod; } });
	Object.defineProperty(exports, "formatMonthYearPeriod", { enumerable: true, get: function () { return month_year_utils_1.formatMonthYearPeriod; } });
	Object.defineProperty(exports, "formatMonthYear", { enumerable: true, get: function () { return month_year_utils_1.formatMonthYear; } });
	Object.defineProperty(exports, "isMonthYear", { enumerable: true, get: function () { return month_year_utils_1.isMonthYear; } });
	Object.defineProperty(exports, "getEarliestAndLatestMonthYear", { enumerable: true, get: function () { return month_year_utils_1.getEarliestAndLatestMonthYear; } });
	Object.defineProperty(exports, "getCurrentFiscalYearMonthYearPeriod", { enumerable: true, get: function () { return month_year_utils_1.getCurrentFiscalYearMonthYearPeriod; } });
	Object.defineProperty(exports, "getPeriodName", { enumerable: true, get: function () { return month_year_utils_1.getPeriodName; } });
	Object.defineProperty(exports, "MONTH_PERIOD_NAMES", { enumerable: true, get: function () { return month_year_utils_1.MONTH_PERIOD_NAMES; } });
	Object.defineProperty(exports, "getTaxYearMonthYearPeriod", { enumerable: true, get: function () { return month_year_utils_1.getTaxYearMonthYearPeriod; } });
	Object.defineProperty(exports, "getMonthYearPeriodUsingDates", { enumerable: true, get: function () { return month_year_utils_1.getMonthYearPeriodUsingDates; } });
	Object.defineProperty(exports, "dateStringToReadableMonthYearString", { enumerable: true, get: function () { return month_year_utils_1.dateStringToReadableMonthYearString; } });
	Object.defineProperty(exports, "getMonthYearPeriodSuperset", { enumerable: true, get: function () { return month_year_utils_1.getMonthYearPeriodSuperset; } });
	Object.defineProperty(exports, "calculateEarliestAffectedCapitalizationMonth", { enumerable: true, get: function () { return month_year_utils_1.calculateEarliestAffectedCapitalizationMonth; } });
	Object.defineProperty(exports, "CapitalizationChangeReason", { enumerable: true, get: function () { return month_year_utils_1.CapitalizationChangeReason; } });
	var number_utils_1 = number_utils;
	Object.defineProperty(exports, "formatNumberWithCommas", { enumerable: true, get: function () { return number_utils_1.formatNumberWithCommas; } });
	Object.defineProperty(exports, "abbreviateNumber", { enumerable: true, get: function () { return number_utils_1.abbreviateNumber; } });
	Object.defineProperty(exports, "areTwoNumbersCloseEnough", { enumerable: true, get: function () { return number_utils_1.areTwoNumbersCloseEnough; } });
	Object.defineProperty(exports, "divideBy100", { enumerable: true, get: function () { return number_utils_1.divideBy100; } });
	Object.defineProperty(exports, "toNumOrZero", { enumerable: true, get: function () { return number_utils_1.toNumOrZero; } });
	Object.defineProperty(exports, "round80To100TaxRule", { enumerable: true, get: function () { return number_utils_1.round80To100TaxRule; } });
	Object.defineProperty(exports, "isNotPositiveNumber", { enumerable: true, get: function () { return number_utils_1.isNotPositiveNumber; } });
	Object.defineProperty(exports, "roundToNearest", { enumerable: true, get: function () { return number_utils_1.roundToNearest; } });
	Object.defineProperty(exports, "floorToNearest", { enumerable: true, get: function () { return number_utils_1.floorToNearest; } });
	var money_utils_1 = money_utils;
	Object.defineProperty(exports, "formatNumberToUsd", { enumerable: true, get: function () { return money_utils_1.formatNumberToUsd; } });
	Object.defineProperty(exports, "formatDollarsFromCents", { enumerable: true, get: function () { return money_utils_1.formatDollarsFromCents; } });
	Object.defineProperty(exports, "parseCents", { enumerable: true, get: function () { return money_utils_1.parseCents; } });
	Object.defineProperty(exports, "removeDollarSignAndCommas", { enumerable: true, get: function () { return money_utils_1.removeDollarSignAndCommas; } });
	Object.defineProperty(exports, "convertToCents", { enumerable: true, get: function () { return money_utils_1.convertToCents; } });
	Object.defineProperty(exports, "dollarsToCents", { enumerable: true, get: function () { return money_utils_1.dollarsToCents; } });
	Object.defineProperty(exports, "centsToDollars", { enumerable: true, get: function () { return money_utils_1.centsToDollars; } });
	Object.defineProperty(exports, "centsToNumberWithCommas", { enumerable: true, get: function () { return money_utils_1.centsToNumberWithCommas; } });
	var company_utils_1 = company_utils;
	Object.defineProperty(exports, "getActualBusinessStartIsoDate", { enumerable: true, get: function () { return company_utils_1.getActualBusinessStartIsoDate; } });
	var promise_1 = promise;
	Object.defineProperty(exports, "pause", { enumerable: true, get: function () { return promise_1.pause; } });
	var errors_1 = errors$1;
	Object.defineProperty(exports, "createNamedErrorClass", { enumerable: true, get: function () { return errors_1.createNamedErrorClass; } });
	Object.defineProperty(exports, "createError", { enumerable: true, get: function () { return errors_1.createError; } });
	Object.defineProperty(exports, "createClientError", { enumerable: true, get: function () { return errors_1.createClientError; } });
	Object.defineProperty(exports, "createClientErrors", { enumerable: true, get: function () { return errors_1.createClientErrors; } });
	Object.defineProperty(exports, "handleCaughtError", { enumerable: true, get: function () { return errors_1.handleCaughtError; } });
	Object.defineProperty(exports, "ErrorWithStatusCode", { enumerable: true, get: function () { return errors_1.ErrorWithStatusCode; } });
	Object.defineProperty(exports, "ExtendedGraphQLErrors", { enumerable: true, get: function () { return errors_1.ExtendedGraphQLErrors; } });
	var questions_1 = questions;
	Object.defineProperty(exports, "divideTools", { enumerable: true, get: function () { return questions_1.divideTools; } });
	Object.defineProperty(exports, "combineTools", { enumerable: true, get: function () { return questions_1.combineTools; } });
	Object.defineProperty(exports, "divideResearchActivities", { enumerable: true, get: function () { return questions_1.divideResearchActivities; } });
	Object.defineProperty(exports, "DocumentationTools", { enumerable: true, get: function () { return questions_1.DocumentationTools; } });
	Object.defineProperty(exports, "ResearchActivities", { enumerable: true, get: function () { return questions_1.ResearchActivities; } });
	var enums_1 = enums;
	Object.defineProperty(exports, "AnalyticIdentifier", { enumerable: true, get: function () { return enums_1.AnalyticIdentifier; } });
	Object.defineProperty(exports, "ExpenseCategory", { enumerable: true, get: function () { return enums_1.ExpenseCategory; } });
	Object.defineProperty(exports, "convertExpenseCategoryToNum", { enumerable: true, get: function () { return enums_1.convertExpenseCategoryToNum; } });
	Object.defineProperty(exports, "QuickbooksQueryField", { enumerable: true, get: function () { return enums_1.QuickbooksQueryField; } });
	Object.defineProperty(exports, "SsoQueryField", { enumerable: true, get: function () { return enums_1.SsoQueryField; } });
	Object.defineProperty(exports, "NeoTaxEnvironment", { enumerable: true, get: function () { return enums_1.NeoTaxEnvironment; } });
	Object.defineProperty(exports, "ServiceScope", { enumerable: true, get: function () { return enums_1.ServiceScope; } });
	Object.defineProperty(exports, "AccountClassification", { enumerable: true, get: function () { return enums_1.AccountClassification; } });
	Object.defineProperty(exports, "AccountType", { enumerable: true, get: function () { return enums_1.AccountType; } });
	Object.defineProperty(exports, "RoundingMethod", { enumerable: true, get: function () { return enums_1.RoundingMethod; } });
	Object.defineProperty(exports, "HardSciences", { enumerable: true, get: function () { return enums_1.HardSciences; } });
	Object.defineProperty(exports, "RevenueSource", { enumerable: true, get: function () { return enums_1.RevenueSource; } });
	Object.defineProperty(exports, "getRevenueSourceRank", { enumerable: true, get: function () { return enums_1.getRevenueSourceRank; } });
	Object.defineProperty(exports, "SalesChannel", { enumerable: true, get: function () { return enums_1.SalesChannel; } });
	Object.defineProperty(exports, "EntityType", { enumerable: true, get: function () { return enums_1.EntityType; } });
	Object.defineProperty(exports, "AccountingAcquisitionMethod", { enumerable: true, get: function () { return enums_1.AccountingAcquisitionMethod; } });
	Object.defineProperty(exports, "QuickbooksConnectionStatus", { enumerable: true, get: function () { return enums_1.QuickbooksConnectionStatus; } });
	Object.defineProperty(exports, "QuickbooksMonth", { enumerable: true, get: function () { return enums_1.QuickbooksMonth; } });
	Object.defineProperty(exports, "GitHubAppInstallQueryField", { enumerable: true, get: function () { return enums_1.GitHubAppInstallQueryField; } });
	Object.defineProperty(exports, "GitHubAppInstallStatus", { enumerable: true, get: function () { return enums_1.GitHubAppInstallStatus; } });
	Object.defineProperty(exports, "DateMonth", { enumerable: true, get: function () { return enums_1.DateMonth; } });
	Object.defineProperty(exports, "SsoError", { enumerable: true, get: function () { return enums_1.SsoError; } });
	Object.defineProperty(exports, "PayrollAcquisitionMethod", { enumerable: true, get: function () { return enums_1.PayrollAcquisitionMethod; } });
	Object.defineProperty(exports, "AccountingProvider", { enumerable: true, get: function () { return enums_1.AccountingProvider; } });
	Object.defineProperty(exports, "PayrollProvider", { enumerable: true, get: function () { return enums_1.PayrollProvider; } });
	Object.defineProperty(exports, "WageType", { enumerable: true, get: function () { return enums_1.WageType; } });
	Object.defineProperty(exports, "ServiceOperationalStatus", { enumerable: true, get: function () { return enums_1.ServiceOperationalStatus; } });
	Object.defineProperty(exports, "CorporationType", { enumerable: true, get: function () { return enums_1.CorporationType; } });
	Object.defineProperty(exports, "EligibilityStatus", { enumerable: true, get: function () { return enums_1.EligibilityStatus; } });
	Object.defineProperty(exports, "CompanyEntityType", { enumerable: true, get: function () { return enums_1.CompanyEntityType; } });
	Object.defineProperty(exports, "ValueType", { enumerable: true, get: function () { return enums_1.ValueType; } });
	Object.defineProperty(exports, "ErrorStatusCode", { enumerable: true, get: function () { return enums_1.ErrorStatusCode; } });
	Object.defineProperty(exports, "PayrollMonthExpenseProvider", { enumerable: true, get: function () { return enums_1.PayrollMonthExpenseProvider; } });
	Object.defineProperty(exports, "PeriodName", { enumerable: true, get: function () { return enums_1.PeriodName; } });
	Object.defineProperty(exports, "periodNameSchema", { enumerable: true, get: function () { return enums_1.periodNameSchema; } });
	Object.defineProperty(exports, "SelectedRdProjectCategory", { enumerable: true, get: function () { return enums_1.SelectedRdProjectCategory; } });
	Object.defineProperty(exports, "rdProjectCategoryLabel", { enumerable: true, get: function () { return enums_1.rdProjectCategoryLabel; } });
	Object.defineProperty(exports, "PREDICTED_CAPITALIZATION_STATUS_LABELS", { enumerable: true, get: function () { return enums_1.PREDICTED_CAPITALIZATION_STATUS_LABELS; } });
	Object.defineProperty(exports, "PredictedCapitalizationStatus", { enumerable: true, get: function () { return enums_1.PredictedCapitalizationStatus; } });
	Object.defineProperty(exports, "RdDocumentationStatus", { enumerable: true, get: function () { return enums_1.RdDocumentationStatus; } });
	Object.defineProperty(exports, "rdDocumentationStatusLabel", { enumerable: true, get: function () { return enums_1.rdDocumentationStatusLabel; } });
	Object.defineProperty(exports, "SelectedSredProjectCategory", { enumerable: true, get: function () { return enums_1.SelectedSredProjectCategory; } });
	Object.defineProperty(exports, "sredProjectCategoryLabel", { enumerable: true, get: function () { return enums_1.sredProjectCategoryLabel; } });
	Object.defineProperty(exports, "SredDocumentationStatus", { enumerable: true, get: function () { return enums_1.SredDocumentationStatus; } });
	Object.defineProperty(exports, "sredDocumentationStatusLabel", { enumerable: true, get: function () { return enums_1.sredDocumentationStatusLabel; } });
	Object.defineProperty(exports, "FinchScopeType", { enumerable: true, get: function () { return enums_1.FinchScopeType; } });
	Object.defineProperty(exports, "FinchScopeTypeSchema", { enumerable: true, get: function () { return enums_1.FinchScopeTypeSchema; } });
	Object.defineProperty(exports, "ProductType", { enumerable: true, get: function () { return enums_1.ProductType; } });
	Object.defineProperty(exports, "AuthErrorCode", { enumerable: true, get: function () { return enums_1.AuthErrorCode; } });
	var email_utils_1 = email_utils;
	Object.defineProperty(exports, "normalizeEmail", { enumerable: true, get: function () { return email_utils_1.normalizeEmail; } });
	Object.defineProperty(exports, "normalizeEmailIdentifier", { enumerable: true, get: function () { return email_utils_1.normalizeEmailIdentifier; } });
	Object.defineProperty(exports, "isEmailIdentifier", { enumerable: true, get: function () { return email_utils_1.isEmailIdentifier; } });
	Object.defineProperty(exports, "isNeoTaxEmail", { enumerable: true, get: function () { return email_utils_1.isNeoTaxEmail; } });
	var retry_utils_1 = retry_utils;
	Object.defineProperty(exports, "retry", { enumerable: true, get: function () { return retry_utils_1.retry; } });
	Object.defineProperty(exports, "MAX_NUM_RETRY_REQUESTS", { enumerable: true, get: function () { return retry_utils_1.MAX_NUM_RETRY_REQUESTS; } });
	var types_utils_1 = types_utils;
	Object.defineProperty(exports, "assertNever", { enumerable: true, get: function () { return types_utils_1.assertNever; } });
	Object.defineProperty(exports, "assertNeverType", { enumerable: true, get: function () { return types_utils_1.assertNeverType; } });
	Object.defineProperty(exports, "jsonSchema", { enumerable: true, get: function () { return types_utils_1.jsonSchema; } });
	Object.defineProperty(exports, "jsonObjectSchema", { enumerable: true, get: function () { return types_utils_1.jsonObjectSchema; } });
	Object.defineProperty(exports, "datetimeSchema", { enumerable: true, get: function () { return types_utils_1.datetimeSchema; } });
	Object.defineProperty(exports, "positiveIntSchema", { enumerable: true, get: function () { return types_utils_1.positiveIntSchema; } });
	Object.defineProperty(exports, "stringSchema", { enumerable: true, get: function () { return types_utils_1.stringSchema; } });
	Object.defineProperty(exports, "nullableStringSchema", { enumerable: true, get: function () { return types_utils_1.nullableStringSchema; } });
	Object.defineProperty(exports, "nullableOptionalStringSchema", { enumerable: true, get: function () { return types_utils_1.nullableOptionalStringSchema; } });
	Object.defineProperty(exports, "commaDelimitedStringSchema", { enumerable: true, get: function () { return types_utils_1.commaDelimitedStringSchema; } });
	Object.defineProperty(exports, "dollarsToCentsSchema", { enumerable: true, get: function () { return types_utils_1.dollarsToCentsSchema; } });
	Object.defineProperty(exports, "vendorSchema", { enumerable: true, get: function () { return types_utils_1.vendorSchema; } });
	Object.defineProperty(exports, "uuidSchema", { enumerable: true, get: function () { return types_utils_1.uuidSchema; } });
	Object.defineProperty(exports, "dateTimeOptionalTimeZoneStringSchema", { enumerable: true, get: function () { return types_utils_1.dateTimeOptionalTimeZoneStringSchema; } });
	Object.defineProperty(exports, "isoDateTimeStringSchema", { enumerable: true, get: function () { return types_utils_1.isoDateTimeStringSchema; } });
	Object.defineProperty(exports, "dateStringSchema", { enumerable: true, get: function () { return types_utils_1.dateStringSchema; } });
	Object.defineProperty(exports, "dateTimeOptionalTimeZoneRegex", { enumerable: true, get: function () { return types_utils_1.dateTimeOptionalTimeZoneRegex; } });
	Object.defineProperty(exports, "dateCoercionSchema", { enumerable: true, get: function () { return types_utils_1.dateCoercionSchema; } });
	Object.defineProperty(exports, "notSpecifiedTransform", { enumerable: true, get: function () { return types_utils_1.notSpecifiedTransform; } });
	Object.defineProperty(exports, "jsonArraySchema", { enumerable: true, get: function () { return types_utils_1.jsonArraySchema; } });
	Object.defineProperty(exports, "intSchema", { enumerable: true, get: function () { return types_utils_1.intSchema; } });
	Object.defineProperty(exports, "nonNegativeNumberSchema", { enumerable: true, get: function () { return types_utils_1.nonNegativeNumberSchema; } });
	Object.defineProperty(exports, "yearSchema", { enumerable: true, get: function () { return types_utils_1.yearSchema; } });
	Object.defineProperty(exports, "emailSchema", { enumerable: true, get: function () { return types_utils_1.emailSchema; } });
	Object.defineProperty(exports, "monthSchema", { enumerable: true, get: function () { return types_utils_1.monthSchema; } });
	Object.defineProperty(exports, "monthYearSchema", { enumerable: true, get: function () { return types_utils_1.monthYearSchema; } });
	Object.defineProperty(exports, "monthYearPeriodSchema", { enumerable: true, get: function () { return types_utils_1.monthYearPeriodSchema; } });
	Object.defineProperty(exports, "monthYearPeriodNullableSchema", { enumerable: true, get: function () { return types_utils_1.monthYearPeriodNullableSchema; } });
	Object.defineProperty(exports, "enumSchemaFromDynamicArray", { enumerable: true, get: function () { return types_utils_1.enumSchemaFromDynamicArray; } });
	Object.defineProperty(exports, "parseOrThrowClientError", { enumerable: true, get: function () { return types_utils_1.parseOrThrowClientError; } });
	Object.defineProperty(exports, "monthYearStringSchema", { enumerable: true, get: function () { return types_utils_1.monthYearStringSchema; } });
	Object.defineProperty(exports, "ssoProviderNameSchema", { enumerable: true, get: function () { return types_utils_1.ssoProviderNameSchema; } });
	Object.defineProperty(exports, "SSO_PROVIDER_NAME_REGEX", { enumerable: true, get: function () { return types_utils_1.SSO_PROVIDER_NAME_REGEX; } });
	Object.defineProperty(exports, "singleTenantEmailDomainSchema", { enumerable: true, get: function () { return types_utils_1.singleTenantEmailDomainSchema; } });
	Object.defineProperty(exports, "singleTenantRedirectUrlSchema", { enumerable: true, get: function () { return types_utils_1.singleTenantRedirectUrlSchema; } });
	Object.defineProperty(exports, "singleTenantDomainRedirectInputSchema", { enumerable: true, get: function () { return types_utils_1.singleTenantDomainRedirectInputSchema; } });
	var slack_types_1 = slack_types;
	Object.defineProperty(exports, "slackEventDtoSchema", { enumerable: true, get: function () { return slack_types_1.slackEventDtoSchema; } });
	var string_utils_1 = string_utils;
	Object.defineProperty(exports, "parsePercentageIntegerValue", { enumerable: true, get: function () { return string_utils_1.parsePercentageIntegerValue; } });
	Object.defineProperty(exports, "capitalCase", { enumerable: true, get: function () { return string_utils_1.capitalCase; } });
	Object.defineProperty(exports, "constantCase", { enumerable: true, get: function () { return string_utils_1.constantCase; } });
	Object.defineProperty(exports, "kebabCase", { enumerable: true, get: function () { return string_utils_1.kebabCase; } });
	Object.defineProperty(exports, "spaceToDash", { enumerable: true, get: function () { return string_utils_1.spaceToDash; } });
	Object.defineProperty(exports, "truncateString", { enumerable: true, get: function () { return string_utils_1.truncateString; } });
	Object.defineProperty(exports, "combineAddresses", { enumerable: true, get: function () { return string_utils_1.combineAddresses; } });
	Object.defineProperty(exports, "sanitizeString", { enumerable: true, get: function () { return string_utils_1.sanitizeString; } });
	Object.defineProperty(exports, "normalizeWhitespaceAndCase", { enumerable: true, get: function () { return string_utils_1.normalizeWhitespaceAndCase; } });
	Object.defineProperty(exports, "escapeLikePattern", { enumerable: true, get: function () { return string_utils_1.escapeLikePattern; } });
	Object.defineProperty(exports, "formatArrayToCommaAndString", { enumerable: true, get: function () { return string_utils_1.formatArrayToCommaAndString; } });
	Object.defineProperty(exports, "snakeCase", { enumerable: true, get: function () { return string_utils_1.snakeCase; } });
	Object.defineProperty(exports, "capitalizeFirstLetter", { enumerable: true, get: function () { return string_utils_1.capitalizeFirstLetter; } });
	Object.defineProperty(exports, "formatAsElementId", { enumerable: true, get: function () { return string_utils_1.formatAsElementId; } });
	var interfaces_1 = interfaces;
	Object.defineProperty(exports, "SsoStateSchema", { enumerable: true, get: function () { return interfaces_1.SsoStateSchema; } });
	Object.defineProperty(exports, "QuickbooksDataLinkingCallbackPayloadStateSchema", { enumerable: true, get: function () { return interfaces_1.QuickbooksDataLinkingCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "IntuitSsoCallbackPayloadStateSchema", { enumerable: true, get: function () { return interfaces_1.IntuitSsoCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "QuickbooksCallbackPayloadStateSchema", { enumerable: true, get: function () { return interfaces_1.QuickbooksCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "GustoCallbackPayloadStateSchema", { enumerable: true, get: function () { return interfaces_1.GustoCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "ThomsonReutersCallbackPayloadStateSchema", { enumerable: true, get: function () { return interfaces_1.ThomsonReutersCallbackPayloadStateSchema; } });
	Object.defineProperty(exports, "parseExtendedApolloError", { enumerable: true, get: function () { return interfaces_1.parseExtendedApolloError; } });
	Object.defineProperty(exports, "parseExtendedGraphQlError", { enumerable: true, get: function () { return interfaces_1.parseExtendedGraphQlError; } });
	Object.defineProperty(exports, "safeParseExtendedApolloError", { enumerable: true, get: function () { return interfaces_1.safeParseExtendedApolloError; } });
	Object.defineProperty(exports, "safeParseExtendedGraphQlError", { enumerable: true, get: function () { return interfaces_1.safeParseExtendedGraphQlError; } });
	Object.defineProperty(exports, "BaseErrorSchema", { enumerable: true, get: function () { return interfaces_1.BaseErrorSchema; } });
	Object.defineProperty(exports, "StandardErrorSchema", { enumerable: true, get: function () { return interfaces_1.StandardErrorSchema; } });
	Object.defineProperty(exports, "AscProjectDeletionErrorSchema", { enumerable: true, get: function () { return interfaces_1.AscProjectDeletionErrorSchema; } });
	Object.defineProperty(exports, "RdProjectDeletionErrorSchema", { enumerable: true, get: function () { return interfaces_1.RdProjectDeletionErrorSchema; } });
	Object.defineProperty(exports, "QuestionAnswerErrorSchema", { enumerable: true, get: function () { return interfaces_1.QuestionAnswerErrorSchema; } });
	Object.defineProperty(exports, "ApplyWeightedRdPercentageToExpensesErrorSchema", { enumerable: true, get: function () { return interfaces_1.ApplyWeightedRdPercentageToExpensesErrorSchema; } });
	Object.defineProperty(exports, "FinchProviderKeySchema", { enumerable: true, get: function () { return interfaces_1.FinchProviderKeySchema; } });
	Object.defineProperty(exports, "DocumentNotParsableErrorSchema", { enumerable: true, get: function () { return interfaces_1.DocumentNotParsableErrorSchema; } });
	Object.defineProperty(exports, "OrganizationInvitationErrorSchema", { enumerable: true, get: function () { return interfaces_1.OrganizationInvitationErrorSchema; } });
	Object.defineProperty(exports, "OrganizationInvitationErrorCodes", { enumerable: true, get: function () { return interfaces_1.OrganizationInvitationErrorCodes; } });
	var array_utils_1 = array_utils;
	Object.defineProperty(exports, "insertElements", { enumerable: true, get: function () { return array_utils_1.insertElements; } });
	Object.defineProperty(exports, "moveElement", { enumerable: true, get: function () { return array_utils_1.moveElement; } });
	Object.defineProperty(exports, "range", { enumerable: true, get: function () { return array_utils_1.range; } });
	Object.defineProperty(exports, "removeElement", { enumerable: true, get: function () { return array_utils_1.removeElement; } });
	Object.defineProperty(exports, "chunk", { enumerable: true, get: function () { return array_utils_1.chunk; } });
	Object.defineProperty(exports, "deduplicate", { enumerable: true, get: function () { return array_utils_1.deduplicate; } });
	var page_status_1 = pageStatus;
	Object.defineProperty(exports, "questionKeyForQre", { enumerable: true, get: function () { return page_status_1.questionKeyForQre; } });
	Object.defineProperty(exports, "questionKeyForPyr", { enumerable: true, get: function () { return page_status_1.questionKeyForPyr; } });
	var json_utils_1 = json_utils;
	Object.defineProperty(exports, "areJsonEqual", { enumerable: true, get: function () { return json_utils_1.areJsonEqual; } });
	Object.defineProperty(exports, "getStaticCopy", { enumerable: true, get: function () { return json_utils_1.getStaticCopy; } });
	Object.defineProperty(exports, "safeDateExtractFromJson", { enumerable: true, get: function () { return json_utils_1.safeDateExtractFromJson; } });
	Object.defineProperty(exports, "sanitizeJson", { enumerable: true, get: function () { return json_utils_1.sanitizeJson; } });
	Object.defineProperty(exports, "sortJsonByKeys", { enumerable: true, get: function () { return json_utils_1.sortJsonByKeys; } });
	Object.defineProperty(exports, "toJson", { enumerable: true, get: function () { return json_utils_1.toJson; } });
	var csv_1 = csv;
	Object.defineProperty(exports, "GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS", { enumerable: true, get: function () { return csv_1.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS; } });
	Object.defineProperty(exports, "GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED", { enumerable: true, get: function () { return csv_1.GENERIC_ACCOUNTING_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED; } });
	Object.defineProperty(exports, "GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS", { enumerable: true, get: function () { return csv_1.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS; } });
	Object.defineProperty(exports, "GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED", { enumerable: true, get: function () { return csv_1.GENERIC_PAYROLL_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED; } });
	Object.defineProperty(exports, "GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS", { enumerable: true, get: function () { return csv_1.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS; } });
	Object.defineProperty(exports, "GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED", { enumerable: true, get: function () { return csv_1.GENERIC_PAYROLL_MONTH_EXPENSE_CSV_COLUMN_LABELS_AUGMENTED; } });
	var function_utils_1 = function_utils;
	Object.defineProperty(exports, "throttle", { enumerable: true, get: function () { return function_utils_1.throttle; } });
	var login_method_header_1 = loginMethodHeader;
	Object.defineProperty(exports, "LOGIN_METHOD_HEADER_NAME", { enumerable: true, get: function () { return login_method_header_1.LOGIN_METHOD_HEADER_NAME; } });
	Object.defineProperty(exports, "LOGIN_METHOD_MAGIC_LINK", { enumerable: true, get: function () { return login_method_header_1.LOGIN_METHOD_MAGIC_LINK; } });
	Object.defineProperty(exports, "LOGIN_METHOD_WINDOW_SECONDS", { enumerable: true, get: function () { return login_method_header_1.LOGIN_METHOD_WINDOW_SECONDS; } });
	var blocking_function_1 = blockingFunction;
	Object.defineProperty(exports, "BlockingFunctionRejectionCodes", { enumerable: true, get: function () { return blocking_function_1.BlockingFunctionRejectionCodes; } });
	Object.defineProperty(exports, "EMAIL_SESSION_CLAIM", { enumerable: true, get: function () { return blocking_function_1.EMAIL_SESSION_CLAIM; } });
	Object.defineProperty(exports, "ProvisioningErrorCodes", { enumerable: true, get: function () { return blocking_function_1.ProvisioningErrorCodes; } });
	var status_codes_utils_1 = statusCodes_utils;
	Object.defineProperty(exports, "ErrorStatusToHttpStatusCode", { enumerable: true, get: function () { return status_codes_utils_1.ErrorStatusToHttpStatusCode; } });
	var asc_projects_1 = ascProjects;
	Object.defineProperty(exports, "getLatestAllocationPercent", { enumerable: true, get: function () { return asc_projects_1.getLatestAllocationPercent; } });
	Object.defineProperty(exports, "updateAllocationsInPeriod", { enumerable: true, get: function () { return asc_projects_1.updateAllocationsInPeriod; } });
	Object.defineProperty(exports, "roundAllocationsToTotal", { enumerable: true, get: function () { return asc_projects_1.roundAllocationsToTotal; } });
	Object.defineProperty(exports, "roundProjectAllocationPercents", { enumerable: true, get: function () { return asc_projects_1.roundProjectAllocationPercents; } });
	Object.defineProperty(exports, "ALLOCATION_DIFF_THRESHOLD", { enumerable: true, get: function () { return asc_projects_1.ALLOCATION_DIFF_THRESHOLD; } });
	Object.defineProperty(exports, "getAverageAllocationInPeriod", { enumerable: true, get: function () { return asc_projects_1.getAverageAllocationInPeriod; } });
	Object.defineProperty(exports, "isCapitalizationStatusLocked", { enumerable: true, get: function () { return asc_projects_1.isCapitalizationStatusLocked; } });
	Object.defineProperty(exports, "isCapitalizationStartDateLocked", { enumerable: true, get: function () { return asc_projects_1.isCapitalizationStartDateLocked; } });
	Object.defineProperty(exports, "isCapitalizationEndDateLocked", { enumerable: true, get: function () { return asc_projects_1.isCapitalizationEndDateLocked; } });
	Object.defineProperty(exports, "isProjectDateRangeFullyLocked", { enumerable: true, get: function () { return asc_projects_1.isProjectDateRangeFullyLocked; } });
	Object.defineProperty(exports, "getFirstAllowableCapitalizationStartMonthYear", { enumerable: true, get: function () { return asc_projects_1.getFirstAllowableCapitalizationStartMonthYear; } });
	Object.defineProperty(exports, "isBeforeOrOnLockBoundary", { enumerable: true, get: function () { return asc_projects_1.isBeforeOrOnLockBoundary; } });
	Object.defineProperty(exports, "validateNotBeforeLockBoundary", { enumerable: true, get: function () { return asc_projects_1.validateNotBeforeLockBoundary; } });
	Object.defineProperty(exports, "formatLockBoundary", { enumerable: true, get: function () { return asc_projects_1.formatLockBoundary; } });
	Object.defineProperty(exports, "maxMonthYearNullable", { enumerable: true, get: function () { return asc_projects_1.maxMonthYearNullable; } });
	Object.defineProperty(exports, "CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE", { enumerable: true, get: function () { return asc_projects_1.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE; } });
	Object.defineProperty(exports, "CLUSTERING_METHOD", { enumerable: true, get: function () { return asc_projects_1.CLUSTERING_METHOD; } });
	Object.defineProperty(exports, "getCapitalizableProjectClusteringStatus", { enumerable: true, get: function () { return asc_projects_1.getCapitalizableProjectClusteringStatus; } });
	Object.defineProperty(exports, "isUsedInCapitalizableProjectClustering", { enumerable: true, get: function () { return asc_projects_1.isUsedInCapitalizableProjectClustering; } });
	Object.defineProperty(exports, "AscClusterType", { enumerable: true, get: function () { return asc_projects_1.AscClusterType; } });
	Object.defineProperty(exports, "isAscNoiseClusterType", { enumerable: true, get: function () { return asc_projects_1.isAscNoiseClusterType; } });
	Object.defineProperty(exports, "ASC_NOISE_CLUSTER_TYPES", { enumerable: true, get: function () { return asc_projects_1.ASC_NOISE_CLUSTER_TYPES; } });
	var error_utils_1 = error_utils;
	Object.defineProperty(exports, "extendedApolloErrorHasErrorCode", { enumerable: true, get: function () { return error_utils_1.extendedApolloErrorHasErrorCode; } });
	Object.defineProperty(exports, "extendedGraphQlErrorHasErrorCode", { enumerable: true, get: function () { return error_utils_1.extendedGraphQlErrorHasErrorCode; } });
	Object.defineProperty(exports, "getExtendedErrors", { enumerable: true, get: function () { return error_utils_1.getExtendedErrors; } });
	Object.defineProperty(exports, "getStandardErrors", { enumerable: true, get: function () { return error_utils_1.getStandardErrors; } });
	Object.defineProperty(exports, "hasErrorCode", { enumerable: true, get: function () { return error_utils_1.hasErrorCode; } });
	Object.defineProperty(exports, "hasStandardErrorCode", { enumerable: true, get: function () { return error_utils_1.hasStandardErrorCode; } });
	var environment_utils_1 = environment_utils;
	Object.defineProperty(exports, "isProductionLikeEnvironment", { enumerable: true, get: function () { return environment_utils_1.isProductionLikeEnvironment; } });
	var fiscal_year_utils_1 = fiscalYear_utils;
	Object.defineProperty(exports, "findFiscalYearsInPeriod", { enumerable: true, get: function () { return fiscal_year_utils_1.findFiscalYearsInPeriod; } });
	var prior_year_data_utils_1 = priorYearData_utils;
	Object.defineProperty(exports, "determineTaxYearStartDate", { enumerable: true, get: function () { return prior_year_data_utils_1.determineTaxYearStartDate; } });
	Object.defineProperty(exports, "determineTaxYearEndDate", { enumerable: true, get: function () { return prior_year_data_utils_1.determineTaxYearEndDate; } });
	Object.defineProperty(exports, "getFiscalPeriodsForQres", { enumerable: true, get: function () { return prior_year_data_utils_1.getFiscalPeriodsForQres; } });
	Object.defineProperty(exports, "getFiscalPeriodsForRevenues", { enumerable: true, get: function () { return prior_year_data_utils_1.getFiscalPeriodsForRevenues; } });
	var rd_project_category_utils_1 = rdProjectCategory_utils;
	Object.defineProperty(exports, "calculateRdProjectCategory", { enumerable: true, get: function () { return rd_project_category_utils_1.calculateRdProjectCategory; } });
	var injector_1 = injector;
	Object.defineProperty(exports, "Injector", { enumerable: true, get: function () { return injector_1.Injector; } });
	var standard_use_case_1 = standardUseCase;
	Object.defineProperty(exports, "StandardUseCase", { enumerable: true, get: function () { return standard_use_case_1.StandardUseCase; } });
	var command_use_case_1 = commandUseCase;
	Object.defineProperty(exports, "CommandUseCase", { enumerable: true, get: function () { return command_use_case_1.CommandUseCase; } });
	var query_use_case_1 = queryUseCase;
	Object.defineProperty(exports, "QueryUseCase", { enumerable: true, get: function () { return query_use_case_1.QueryUseCase; } });
	var random_1 = random;
	Object.defineProperty(exports, "Random", { enumerable: true, get: function () { return random_1.Random; } });
	var stack_1 = stack;
	Object.defineProperty(exports, "trackStackDepth", { enumerable: true, get: function () { return stack_1.trackStackDepth; } });
	var rd_project_eligibility_1 = rdProjectEligibility;
	Object.defineProperty(exports, "determineRdProjectEligibility", { enumerable: true, get: function () { return rd_project_eligibility_1.determineRdProjectEligibility; } });
	Object.defineProperty(exports, "RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING", { enumerable: true, get: function () { return rd_project_eligibility_1.RD_PROJECT_ELIGIBILITY_QUALIFIED_PURPOSE_REASONING; } });
	var name_parser_1 = nameParser;
	Object.defineProperty(exports, "isSurnameParticle", { enumerable: true, get: function () { return name_parser_1.isSurnameParticle; } });
	Object.defineProperty(exports, "parsePersonName", { enumerable: true, get: function () { return name_parser_1.parsePersonName; } });
	var jaro_winkler_1 = jaroWinkler;
	Object.defineProperty(exports, "jaroWinklerSimilarity", { enumerable: true, get: function () { return jaro_winkler_1.jaroWinklerSimilarity; } });
	var contributor_matcher_1 = contributorMatcher;
	Object.defineProperty(exports, "DEFAULT_FUZZY_MATCH_THRESHOLD", { enumerable: true, get: function () { return contributor_matcher_1.DEFAULT_FUZZY_MATCH_THRESHOLD; } });
	Object.defineProperty(exports, "buildContributorMatchIndex", { enumerable: true, get: function () { return contributor_matcher_1.buildContributorMatchIndex; } });
	Object.defineProperty(exports, "findBestContributorMatch", { enumerable: true, get: function () { return contributor_matcher_1.findBestContributorMatch; } });
} (lib));

const monthOptions = Object.entries(lib.DateMonth)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key, value]) => ({
    label: lib.capitalCase(key),
    value: value.toString(),
}));
const possibleMonthDateOptions = Array.from({ length: 31 }, (_, i) => i + 1).map((day) => ({
    value: day.toString(),
    label: day.toString(),
}));
const getMonthDateOptions = (month) => month !== null && month !== undefined && month in lib.LAST_MONTH_DATE_DICT
    ? possibleMonthDateOptions.slice(0, Number.parseInt(lib.LAST_MONTH_DATE_DICT[month], 10))
    : possibleMonthDateOptions;

const Overflow = {
    auto: 'overflow-auto',
    hidden: 'overflow-hidden',
    clip: 'overflow-clip',
    visible: 'overflow-visible',
    scroll: 'overflow-scroll',
    'x-auto': 'overflow-x-auto',
    'y-auto': 'overflow-y-auto',
    'x-hidden': 'overflow-x-hidden',
    'y-hidden': 'overflow-y-hidden',
    'x-clip': 'overflow-x-clip',
    'y-clip': 'overflow-y-clip',
    'x-visible': 'overflow-x-visible',
    'y-visible': 'overflow-y-visible',
    'x-scroll': 'overflow-x-scroll',
    'y-scroll': 'overflow-y-scroll',
};
const getOverflow = (overflow) => Overflow[overflow];

const BorderRadius = {
    none: 'rounded-none', // 0px
    sm: 'rounded-sm', // 0.125rem
    base: 'rounded', // 0.25rem
    md: 'rounded-md', // 0.375rem
    lg: 'rounded-lg', // 0.5rem
    full: 'rounded-full', // 9999px
};
const getBorderRadius = (borderRadius) => BorderRadius[borderRadius];

const WHITE_SPACE = {
    inherit: '',
    normal: 'whitespace-normal',
    nowrap: 'whitespace-nowrap',
    pre: 'whitespace-pre',
    'pre-line': 'whitespace-pre-line',
    'pre-wrap': 'whitespace-pre-wrap',
    'break-spaces': 'whitespace-break-spaces',
};
const getWhiteSpace = (whiteSpace) => WHITE_SPACE[whiteSpace];

const getInitials = (name, { maxInitials }) => name
    .split(/\s/)
    .map((part, _, parts) => part.substring(0, parts.length > 1 ? 1 : maxInitials))
    .filter((value) => !!value)
    .slice(0, maxInitials)
    .join('')
    .toUpperCase();

const TEXT_TRANSFORM = {
    inherit: '',
    normal: 'normal-case',
    uppercase: 'uppercase',
    lowercase: 'lowercase',
    capitalize: 'capitalize',
};
const getTextTransform = (textTransform) => TEXT_TRANSFORM[textTransform];

const TEXT_OVERFLOW = {
    inherit: '',
    /** `truncate` class sets the following CSS:
     * overflow: hidden;
     * text-overflow: ellipsis;
     * white-space: nowrap;
     **/
    truncate: 'truncate',
    ellipsis: 'text-ellipsis',
    clip: 'text-clip',
};
const getTextOverflow = (textOverflow) => TEXT_OVERFLOW[textOverflow];

const TEXT_DECORATION = {
    inherit: '',
    underline: 'underline',
    'line-through': 'line-through',
    none: 'no-underline',
};
const getTextDecoration = (textDecoration) => TEXT_DECORATION[textDecoration];

const useEventListener = ({ element, eventName, handler, }) => {
    useLayoutEffect$1(() => {
        const htmlElement = typeof element === 'function' ? element() : element || window;
        htmlElement?.addEventListener(eventName, handler);
        return () => {
            htmlElement?.removeEventListener(eventName, handler);
        };
    }, [element, eventName, handler]);
};

const useScrollbarWidth = () => 
// from https://davidwalsh.name/detect-scrollbar-width
React__default.useMemo(() => {
    const scrollDiv = document.createElement('div');
    scrollDiv.setAttribute('style', 'width: 100px; height: 100px; overflow: scroll; position:absolute; top:-9999px;');
    document.body.appendChild(scrollDiv);
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);
    return scrollbarWidth;
}, []);

const useElementSize = () => {
    // Mutable values like 'ref.current' aren't valid dependencies
    // because mutating them doesn't re-render the component.
    // Instead, we use a state as a ref to be reactive.
    const [ref, setRef] = useState(null);
    const [size, setSize] = useState({
        width: 0,
        height: 0,
    });
    const handleSize = useCallback(() => {
        setSize({
            width: ref?.offsetWidth || window.innerWidth,
            height: ref?.offsetHeight || window.innerHeight,
        });
    }, [ref?.offsetHeight, ref?.offsetWidth]);
    useEventListener({ eventName: 'resize', handler: handleSize });
    useLayoutEffect$1(() => {
        handleSize();
    }, [handleSize]);
    return [setRef, size];
};

const useSelectable = (initialState) => {
    const [selectedItem, setSelectedItem] = useState(initialState);
    const onSelect = (newSelectedItem) => {
        setSelectedItem(newSelectedItem);
    };
    return {
        onSelect,
        selectedItem,
        setSelectedItem,
    };
};

const useHoverIntent = ({ defaultTimeout } = { defaultTimeout: 350 }) => {
    const hoverTimeoutRef = useRef(null);
    const onMouseEnterHoverIntent = (onMouseEnter, msTimeout) => (event) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        // This set timeout creates a hover intent that attempts to determine the user's intent
        hoverTimeoutRef.current = setTimeout(() => {
            onMouseEnter(event);
        }, msTimeout ?? defaultTimeout);
    };
    const onMouseLeaveHoverIntent = (onMouseLeave) => (event) => {
        onMouseLeave?.(event);
        // Clear timeout if the mouse leaves the canvas
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
    };
    return {
        onMouseEnterHoverIntent,
        onMouseLeaveHoverIntent,
    };
};

const useStepper = ({ onStepsComplete, steps, }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const hasPreviousStep = currentStep - 1 >= 0;
    const hasNextStep = currentStep + 1 < steps;
    useEffect(() => {
        if (currentStep === steps - 1)
            onStepsComplete?.();
    }, [currentStep, onStepsComplete, steps]);
    const nextStep = () => {
        if (hasNextStep)
            setCurrentStep((current) => current + 1);
    };
    const prevStep = () => {
        if (hasPreviousStep)
            setCurrentStep((current) => current - 1);
    };
    return {
        currentStep,
        hasNextStep,
        hasPreviousStep,
        nextStep,
        prevStep,
        setCurrentStep,
    };
};

class SympatheticHighlightOrchestrator {
    elements;
    constructor() {
        this.elements = {};
        this.highlight = this.highlight.bind(this);
        this.removeHighlight = this.removeHighlight.bind(this);
    }
    subscribe(elementId, cb) {
        this.elements = {
            ...this.elements,
            [elementId]: {
                highlight: () => {
                    cb.onHighlight();
                },
                removeHighlight: () => {
                    cb.onRemoveHighlight();
                },
            },
        };
    }
    unsubscribe(elementId) {
        const { [elementId]: _, ...restElements } = this.elements;
        this.elements = restElements;
    }
    highlight(elementId) {
        this.elements[elementId]?.highlight();
    }
    removeHighlight(elementId) {
        this.elements[elementId]?.removeHighlight();
    }
}
const SympatheticHighlightContext = createContext(null);
const useSympatheticHighlightContext = () => {
    const sympatheticHighlightOrchestrator = useContext(SympatheticHighlightContext);
    if (!sympatheticHighlightOrchestrator) {
        throw new Error('Sympathetic Highlight context cannot be used outside a SympatheticHighlightProvider');
    }
    return sympatheticHighlightOrchestrator;
};
const useSympatheticHighlight = (elementId) => {
    const orchestrator = useSympatheticHighlightContext();
    const [isSympatheticHighlighted, setIsSympatheticHighlighted] = useState(false);
    useEffect(() => {
        orchestrator.subscribe(elementId, {
            onHighlight: () => {
                setIsSympatheticHighlighted(true);
            },
            onRemoveHighlight: () => {
                setIsSympatheticHighlighted(false);
            },
        });
        return () => {
            orchestrator.unsubscribe(elementId);
            setIsSympatheticHighlighted(false);
        };
    }, [elementId, orchestrator]);
    return { highlight: orchestrator.highlight, isSympatheticHighlighted, removeHighlight: orchestrator.removeHighlight };
};
const SympatheticHighlightProvider = ({ children }) => {
    const orchestrator = new SympatheticHighlightOrchestrator();
    return React__default.createElement(SympatheticHighlightContext.Provider, { value: orchestrator }, children);
};

const useScrollableElement = () => {
    const componentRef = useRef();
    const [hasScroll, setHasScroll] = useState(false);
    useEffect(() => {
        if (!componentRef.current) {
            return;
        }
        const resizeObserver = new ResizeObserver(() => {
            const isComponentScrollable = componentRef.current && componentRef.current.clientHeight < componentRef.current.scrollHeight;
            if (!hasScroll && isComponentScrollable) {
                setHasScroll(true);
            }
            else if (hasScroll && !isComponentScrollable) {
                setHasScroll(false);
            }
        });
        resizeObserver.observe(componentRef.current);
        return () => resizeObserver.disconnect();
    }, [hasScroll]);
    return {
        componentRef,
        hasScroll,
    };
};

const pipe$2 = (...fns) => (...x) => fns.reduce((v, f) => f(...v), x);

var classnamesExports = {};
var classnames = {
  get exports(){ return classnamesExports; },
  set exports(v){ classnamesExports = v; },
};

/*!
  Copyright (c) 2018 Jed Watson.
  Licensed under the MIT License (MIT), see
  http://jedwatson.github.io/classnames
*/

(function (module) {
	/* global define */

	(function () {

		var hasOwn = {}.hasOwnProperty;

		function classNames() {
			var classes = [];

			for (var i = 0; i < arguments.length; i++) {
				var arg = arguments[i];
				if (!arg) continue;

				var argType = typeof arg;

				if (argType === 'string' || argType === 'number') {
					classes.push(arg);
				} else if (Array.isArray(arg)) {
					if (arg.length) {
						var inner = classNames.apply(null, arg);
						if (inner) {
							classes.push(inner);
						}
					}
				} else if (argType === 'object') {
					if (arg.toString === Object.prototype.toString) {
						for (var key in arg) {
							if (hasOwn.call(arg, key) && arg[key]) {
								classes.push(key);
							}
						}
					} else {
						classes.push(arg.toString());
					}
				}
			}

			return classes.join(' ');
		}

		if (module.exports) {
			classNames.default = classNames;
			module.exports = classNames;
		} else {
			window.classNames = classNames;
		}
	}());
} (classnames));

var cn = classnamesExports;

var dist$1 = {};

var tailwind = {};

var domElements = {};

Object.defineProperty(domElements, "__esModule", { value: true });
const elementsArray = [
    "a",
    "abbr",
    "address",
    "area",
    "article",
    "aside",
    "audio",
    "b",
    "base",
    "bdi",
    "bdo",
    "big",
    "blockquote",
    "body",
    "br",
    "button",
    "canvas",
    "caption",
    "cite",
    "code",
    "col",
    "colgroup",
    "data",
    "datalist",
    "dd",
    "del",
    "details",
    "dfn",
    "dialog",
    "div",
    "dl",
    "dt",
    "em",
    "embed",
    "fieldset",
    "figcaption",
    "figure",
    "footer",
    "form",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "head",
    "header",
    "hgroup",
    "hr",
    "html",
    "i",
    "iframe",
    "img",
    "input",
    "ins",
    "kbd",
    "keygen",
    "label",
    "legend",
    "li",
    "link",
    "main",
    "map",
    "mark",
    "menu",
    "menuitem",
    "meta",
    "meter",
    "nav",
    "noscript",
    "object",
    "ol",
    "optgroup",
    "option",
    "output",
    "p",
    "param",
    "picture",
    "pre",
    "progress",
    "q",
    "rp",
    "rt",
    "ruby",
    "s",
    "samp",
    "script",
    "section",
    "select",
    "small",
    "source",
    "span",
    "strong",
    "style",
    "sub",
    "summary",
    "sup",
    "table",
    "tbody",
    "td",
    "textarea",
    "tfoot",
    "th",
    "thead",
    "time",
    "title",
    "tr",
    "track",
    "u",
    "ul",
    "var",
    "video",
    "wbr",
    "circle",
    "clipPath",
    "defs",
    "ellipse",
    "foreignObject",
    "g",
    "image",
    "line",
    "linearGradient",
    "marker",
    "mask",
    "path",
    "pattern",
    "polygon",
    "polyline",
    "radialGradient",
    "rect",
    "stop",
    "svg",
    "text",
    "tspan"
];
domElements.default = elementsArray;

var distExports = {};
var dist = {
  get exports(){ return distExports; },
  set exports(v){ distExports = v; },
};

var tailwindMerge_cjs_production_min = {};

var hasRequiredTailwindMerge_cjs_production_min;

function requireTailwindMerge_cjs_production_min () {
	if (hasRequiredTailwindMerge_cjs_production_min) return tailwindMerge_cjs_production_min;
	hasRequiredTailwindMerge_cjs_production_min = 1;
function r(){for(var r,o,t=0,n="";t<arguments.length;)(r=arguments[t++])&&(o=e(r))&&(n&&(n+=" "),n+=o);return n}function e(r){if("string"==typeof r)return r;for(var o,t="",n=0;n<r.length;n++)r[n]&&(o=e(r[n]))&&(t&&(t+=" "),t+=o);return t}Object.defineProperty(tailwindMerge_cjs_production_min,"__esModule",{value:!0});var o="-";function t(r){var e=function(r){var e=r.theme,o=r.prefix,t={nextPart:new Map,validators:[]},n=function(r,e){return e?r.map((function(r){return [r[0],r[1].map((function(r){return "string"==typeof r?e+r:"object"==typeof r?Object.fromEntries(Object.entries(r).map((function(r){return [e+r[0],r[1]]}))):r}))]})):r}(Object.entries(r.classGroups),o);return n.forEach((function(r){a(r[1],t,r[0],e);})),t}(r),t=r.conflictingClassGroups,l=r.conflictingClassGroupModifiers,s=void 0===l?{}:l;return {getClassGroupId:function(r){var t=r.split(o);return ""===t[0]&&1!==t.length&&t.shift(),n(t,e)||function(r){if(i.test(r)){var e=i.exec(r)[1],o=e?.substring(0,e.indexOf(":"));if(o)return "arbitrary.."+o}}(r)},getConflictingClassGroupIds:function(r,e){var o=t[r]||[];return e&&s[r]?[].concat(o,s[r]):o}}}function n(r,e){if(0===r.length)return e.classGroupId;var t=e.nextPart.get(r[0]),i=t?n(r.slice(1),t):void 0;if(i)return i;if(0!==e.validators.length){var a=r.join(o);return e.validators.find((function(r){return (0, r.validator)(a)}))?.classGroupId}}var i=/^\[(.+)\]$/;function a(r,e,o,t){r.forEach((function(r){if("string"!=typeof r){if("function"==typeof r)return r.isThemeGetter?void a(r(t),e,o,t):void e.validators.push({validator:r,classGroupId:o});Object.entries(r).forEach((function(r){a(r[1],l(e,r[0]),o,t);}));}else (""===r?e:l(e,r)).classGroupId=o;}));}function l(r,e){var t=r;return e.split(o).forEach((function(r){t.nextPart.has(r)||t.nextPart.set(r,{nextPart:new Map,validators:[]}),t=t.nextPart.get(r);})),t}function s(r){if(r<1)return {get:function(){},set:function(){}};var e=0,o=new Map,t=new Map;function n(n,i){o.set(n,i),++e>r&&(e=0,t=o,o=new Map);}return {get:function(r){var e=o.get(r);return void 0!==e?e:void 0!==(e=t.get(r))?(n(r,e),e):void 0},set:function(r,e){o.has(r)?o.set(r,e):n(r,e);}}}var c="!";function d(r){var e=r.separator||":",o=1===e.length,t=e[0],n=e.length;return function(r){for(var i,a=[],l=0,s=0,d=0;d<r.length;d++){var u=r[d];if(0===l){if(u===t&&(o||r.slice(d,d+n)===e)){a.push(r.slice(s,d)),s=d+n;continue}if("/"===u){i=d;continue}}"["===u?l++:"]"===u&&l--;}var p=0===a.length?r:r.substring(s),f=p.startsWith(c);return {modifiers:a,hasImportantModifier:f,baseClassName:f?p.substring(1):p,maybePostfixModifierPosition:i&&i>s?i-s:void 0}}}var u=/\s+/;function p(){for(var e=arguments.length,o=new Array(e),n=0;n<e;n++)o[n]=arguments[n];var i,a,l,p=function(r){var e=o[0],n=o.slice(1).reduce((function(r,e){return e(r)}),e());return i=function(r){return {cache:s(r.cacheSize),splitModifiers:d(r),...t(r)}}(n),a=i.cache.get,l=i.cache.set,p=f,f(r)};function f(r){var e=a(r);if(e)return e;var o=function(r,e){var o=e.splitModifiers,t=e.getClassGroupId,n=e.getConflictingClassGroupIds,i=new Set;return r.trim().split(u).map((function(r){var e=o(r),n=e.modifiers,i=e.hasImportantModifier,a=e.baseClassName,l=e.maybePostfixModifierPosition,s=t(l?a.substring(0,l):a),d=Boolean(l);if(!s){if(!l)return {isTailwindClass:!1,originalClassName:r};if(!(s=t(a)))return {isTailwindClass:!1,originalClassName:r};d=!1;}var u=function(r){if(r.length<=1)return r;var e=[],o=[];return r.forEach((function(r){"["===r[0]?(e.push.apply(e,o.sort().concat([r])),o=[]):o.push(r);})),e.push.apply(e,o.sort()),e}(n).join(":");return {isTailwindClass:!0,modifierId:i?u+c:u,classGroupId:s,originalClassName:r,hasPostfixModifier:d}})).reverse().filter((function(r){if(!r.isTailwindClass)return !0;var e=r.modifierId,o=r.classGroupId,t=r.hasPostfixModifier,a=e+o;return !i.has(a)&&(i.add(a),n(o,t).forEach((function(r){return i.add(e+r)})),!0)})).reverse().map((function(r){return r.originalClassName})).join(" ")}(r,i);return l(r,o),o}return function(){return p(r.apply(null,arguments))}}function f(r){var e=function(e){return e[r]||[]};return e.isThemeGetter=!0,e}var b=/^\[(?:([a-z-]+):)?(.+)\]$/i,m=/^\d+\/\d+$/,g=new Set(["px","full","screen"]),h=/^(\d+(\.\d+)?)?(xs|sm|md|lg|xl)$/,v=/\d+(%|px|r?em|[sdl]?v([hwib]|min|max)|pt|pc|in|cm|mm|cap|ch|ex|r?lh|cq(w|h|i|b|min|max))|\b(calc|min|max|clamp)\(.+\)|^0$/,y=/^-?((\d+)?\.?(\d+)[a-z]+|0)_-?((\d+)?\.?(\d+)[a-z]+|0)/;function x(r){return M(r)||g.has(r)||m.test(r)||w(r)}function w(r){return T(r,"length",O)}function k(r){return T(r,"size",E)}function z(r){return T(r,"position",E)}function C(r){return T(r,"url",_)}function j(r){return T(r,"number",M)}function M(r){return !Number.isNaN(Number(r))}function G(r){return r.endsWith("%")&&M(r.slice(0,-1))}function P(r){return W(r)||T(r,"number",W)}function I(r){return b.test(r)}function A(){return !0}function N(r){return h.test(r)}function S(r){return T(r,"",$)}function T(r,e,o){var t=b.exec(r);return !!t&&(t[1]?t[1]===e:o(t[2]))}function O(r){return v.test(r)}function E(){return !1}function _(r){return r.startsWith("url(")}function W(r){return Number.isInteger(Number(r))}function $(r){return y.test(r)}var R={__proto__:null,isAny:A,isArbitraryLength:w,isArbitraryNumber:j,isArbitraryPosition:z,isArbitraryShadow:S,isArbitrarySize:k,isArbitraryUrl:C,isArbitraryValue:I,isArbitraryWeight:j,isInteger:P,isLength:x,isNumber:M,isPercent:G,isTshirtSize:N};function q(){var r=f("colors"),e=f("spacing"),o=f("blur"),t=f("brightness"),n=f("borderColor"),i=f("borderRadius"),a=f("borderSpacing"),l=f("borderWidth"),s=f("contrast"),c=f("grayscale"),d=f("hueRotate"),u=f("invert"),p=f("gap"),b=f("gradientColorStops"),m=f("gradientColorStopPositions"),g=f("inset"),h=f("margin"),v=f("opacity"),y=f("padding"),T=f("saturate"),O=f("scale"),E=f("sepia"),_=f("skew"),W=f("space"),$=f("translate"),R=function(){return ["auto",I,e]},q=function(){return [I,e]},L=function(){return ["",x]},B=function(){return ["auto",M,I]},D=function(){return ["","0",I]},J=function(){return [M,j]},U=function(){return [M,I]};return {cacheSize:500,theme:{colors:[A],spacing:[x],blur:["none","",N,I],brightness:J(),borderColor:[r],borderRadius:["none","","full",N,I],borderSpacing:q(),borderWidth:L(),contrast:J(),grayscale:D(),hueRotate:U(),invert:D(),gap:q(),gradientColorStops:[r],gradientColorStopPositions:[G,w],inset:R(),margin:R(),opacity:J(),padding:q(),saturate:J(),scale:J(),sepia:D(),skew:U(),space:q(),translate:q()},classGroups:{aspect:[{aspect:["auto","square","video",I]}],container:["container"],columns:[{columns:[N]}],"break-after":[{"break-after":["auto","avoid","all","avoid-page","page","left","right","column"]}],"break-before":[{"break-before":["auto","avoid","all","avoid-page","page","left","right","column"]}],"break-inside":[{"break-inside":["auto","avoid","avoid-page","avoid-column"]}],"box-decoration":[{"box-decoration":["slice","clone"]}],box:[{box:["border","content"]}],display:["block","inline-block","inline","flex","inline-flex","table","inline-table","table-caption","table-cell","table-column","table-column-group","table-footer-group","table-header-group","table-row-group","table-row","flow-root","grid","inline-grid","contents","list-item","hidden"],float:[{float:["right","left","none"]}],clear:[{clear:["left","right","both","none"]}],isolation:["isolate","isolation-auto"],"object-fit":[{object:["contain","cover","fill","none","scale-down"]}],"object-position":[{object:[].concat(["bottom","center","left","left-bottom","left-top","right","right-bottom","right-top","top"],[I])}],overflow:[{overflow:["auto","hidden","clip","visible","scroll"]}],"overflow-x":[{"overflow-x":["auto","hidden","clip","visible","scroll"]}],"overflow-y":[{"overflow-y":["auto","hidden","clip","visible","scroll"]}],overscroll:[{overscroll:["auto","contain","none"]}],"overscroll-x":[{"overscroll-x":["auto","contain","none"]}],"overscroll-y":[{"overscroll-y":["auto","contain","none"]}],position:["static","fixed","absolute","relative","sticky"],inset:[{inset:[g]}],"inset-x":[{"inset-x":[g]}],"inset-y":[{"inset-y":[g]}],start:[{start:[g]}],end:[{end:[g]}],top:[{top:[g]}],right:[{right:[g]}],bottom:[{bottom:[g]}],left:[{left:[g]}],visibility:["visible","invisible","collapse"],z:[{z:["auto",P]}],basis:[{basis:R()}],"flex-direction":[{flex:["row","row-reverse","col","col-reverse"]}],"flex-wrap":[{flex:["wrap","wrap-reverse","nowrap"]}],flex:[{flex:["1","auto","initial","none",I]}],grow:[{grow:D()}],shrink:[{shrink:D()}],order:[{order:["first","last","none",P]}],"grid-cols":[{"grid-cols":[A]}],"col-start-end":[{col:["auto",{span:["full",P]},I]}],"col-start":[{"col-start":B()}],"col-end":[{"col-end":B()}],"grid-rows":[{"grid-rows":[A]}],"row-start-end":[{row:["auto",{span:[P]},I]}],"row-start":[{"row-start":B()}],"row-end":[{"row-end":B()}],"grid-flow":[{"grid-flow":["row","col","dense","row-dense","col-dense"]}],"auto-cols":[{"auto-cols":["auto","min","max","fr",I]}],"auto-rows":[{"auto-rows":["auto","min","max","fr",I]}],gap:[{gap:[p]}],"gap-x":[{"gap-x":[p]}],"gap-y":[{"gap-y":[p]}],"justify-content":[{justify:["normal"].concat(["start","end","center","between","around","evenly","stretch"])}],"justify-items":[{"justify-items":["start","end","center","stretch"]}],"justify-self":[{"justify-self":["auto","start","end","center","stretch"]}],"align-content":[{content:["normal"].concat(["start","end","center","between","around","evenly","stretch"],["baseline"])}],"align-items":[{items:["start","end","center","baseline","stretch"]}],"align-self":[{self:["auto","start","end","center","stretch","baseline"]}],"place-content":[{"place-content":[].concat(["start","end","center","between","around","evenly","stretch"],["baseline"])}],"place-items":[{"place-items":["start","end","center","baseline","stretch"]}],"place-self":[{"place-self":["auto","start","end","center","stretch"]}],p:[{p:[y]}],px:[{px:[y]}],py:[{py:[y]}],ps:[{ps:[y]}],pe:[{pe:[y]}],pt:[{pt:[y]}],pr:[{pr:[y]}],pb:[{pb:[y]}],pl:[{pl:[y]}],m:[{m:[h]}],mx:[{mx:[h]}],my:[{my:[h]}],ms:[{ms:[h]}],me:[{me:[h]}],mt:[{mt:[h]}],mr:[{mr:[h]}],mb:[{mb:[h]}],ml:[{ml:[h]}],"space-x":[{"space-x":[W]}],"space-x-reverse":["space-x-reverse"],"space-y":[{"space-y":[W]}],"space-y-reverse":["space-y-reverse"],w:[{w:["auto","min","max","fit",I,e]}],"min-w":[{"min-w":["min","max","fit",I,x]}],"max-w":[{"max-w":["0","none","full","min","max","fit","prose",{screen:[N]},N,I]}],h:[{h:[I,e,"auto","min","max","fit"]}],"min-h":[{"min-h":["min","max","fit",I,x]}],"max-h":[{"max-h":[I,e,"min","max","fit"]}],"font-size":[{text:["base",N,w]}],"font-smoothing":["antialiased","subpixel-antialiased"],"font-style":["italic","not-italic"],"font-weight":[{font:["thin","extralight","light","normal","medium","semibold","bold","extrabold","black",j]}],"font-family":[{font:[A]}],"fvn-normal":["normal-nums"],"fvn-ordinal":["ordinal"],"fvn-slashed-zero":["slashed-zero"],"fvn-figure":["lining-nums","oldstyle-nums"],"fvn-spacing":["proportional-nums","tabular-nums"],"fvn-fraction":["diagonal-fractions","stacked-fractons"],tracking:[{tracking:["tighter","tight","normal","wide","wider","widest",I]}],"line-clamp":[{"line-clamp":["none",M,j]}],leading:[{leading:["none","tight","snug","normal","relaxed","loose",I,x]}],"list-image":[{"list-image":["none",I]}],"list-style-type":[{list:["none","disc","decimal",I]}],"list-style-position":[{list:["inside","outside"]}],"placeholder-color":[{placeholder:[r]}],"placeholder-opacity":[{"placeholder-opacity":[v]}],"text-alignment":[{text:["left","center","right","justify","start","end"]}],"text-color":[{text:[r]}],"text-opacity":[{"text-opacity":[v]}],"text-decoration":["underline","overline","line-through","no-underline"],"text-decoration-style":[{decoration:[].concat(["solid","dashed","dotted","double","none"],["wavy"])}],"text-decoration-thickness":[{decoration:["auto","from-font",x]}],"underline-offset":[{"underline-offset":["auto",I,x]}],"text-decoration-color":[{decoration:[r]}],"text-transform":["uppercase","lowercase","capitalize","normal-case"],"text-overflow":["truncate","text-ellipsis","text-clip"],indent:[{indent:q()}],"vertical-align":[{align:["baseline","top","middle","bottom","text-top","text-bottom","sub","super",I]}],whitespace:[{whitespace:["normal","nowrap","pre","pre-line","pre-wrap","break-spaces"]}],break:[{break:["normal","words","all","keep"]}],hyphens:[{hyphens:["none","manual","auto"]}],content:[{content:["none",I]}],"bg-attachment":[{bg:["fixed","local","scroll"]}],"bg-clip":[{"bg-clip":["border","padding","content","text"]}],"bg-opacity":[{"bg-opacity":[v]}],"bg-origin":[{"bg-origin":["border","padding","content"]}],"bg-position":[{bg:[].concat(["bottom","center","left","left-bottom","left-top","right","right-bottom","right-top","top"],[z])}],"bg-repeat":[{bg:["no-repeat",{repeat:["","x","y","round","space"]}]}],"bg-size":[{bg:["auto","cover","contain",k]}],"bg-image":[{bg:["none",{"gradient-to":["t","tr","r","br","b","bl","l","tl"]},C]}],"bg-color":[{bg:[r]}],"gradient-from-pos":[{from:[m]}],"gradient-via-pos":[{via:[m]}],"gradient-to-pos":[{to:[m]}],"gradient-from":[{from:[b]}],"gradient-via":[{via:[b]}],"gradient-to":[{to:[b]}],rounded:[{rounded:[i]}],"rounded-s":[{"rounded-s":[i]}],"rounded-e":[{"rounded-e":[i]}],"rounded-t":[{"rounded-t":[i]}],"rounded-r":[{"rounded-r":[i]}],"rounded-b":[{"rounded-b":[i]}],"rounded-l":[{"rounded-l":[i]}],"rounded-ss":[{"rounded-ss":[i]}],"rounded-se":[{"rounded-se":[i]}],"rounded-ee":[{"rounded-ee":[i]}],"rounded-es":[{"rounded-es":[i]}],"rounded-tl":[{"rounded-tl":[i]}],"rounded-tr":[{"rounded-tr":[i]}],"rounded-br":[{"rounded-br":[i]}],"rounded-bl":[{"rounded-bl":[i]}],"border-w":[{border:[l]}],"border-w-x":[{"border-x":[l]}],"border-w-y":[{"border-y":[l]}],"border-w-s":[{"border-s":[l]}],"border-w-e":[{"border-e":[l]}],"border-w-t":[{"border-t":[l]}],"border-w-r":[{"border-r":[l]}],"border-w-b":[{"border-b":[l]}],"border-w-l":[{"border-l":[l]}],"border-opacity":[{"border-opacity":[v]}],"border-style":[{border:[].concat(["solid","dashed","dotted","double","none"],["hidden"])}],"divide-x":[{"divide-x":[l]}],"divide-x-reverse":["divide-x-reverse"],"divide-y":[{"divide-y":[l]}],"divide-y-reverse":["divide-y-reverse"],"divide-opacity":[{"divide-opacity":[v]}],"divide-style":[{divide:["solid","dashed","dotted","double","none"]}],"border-color":[{border:[n]}],"border-color-x":[{"border-x":[n]}],"border-color-y":[{"border-y":[n]}],"border-color-t":[{"border-t":[n]}],"border-color-r":[{"border-r":[n]}],"border-color-b":[{"border-b":[n]}],"border-color-l":[{"border-l":[n]}],"divide-color":[{divide:[n]}],"outline-style":[{outline:[""].concat(["solid","dashed","dotted","double","none"])}],"outline-offset":[{"outline-offset":[I,x]}],"outline-w":[{outline:[x]}],"outline-color":[{outline:[r]}],"ring-w":[{ring:L()}],"ring-w-inset":["ring-inset"],"ring-color":[{ring:[r]}],"ring-opacity":[{"ring-opacity":[v]}],"ring-offset-w":[{"ring-offset":[x]}],"ring-offset-color":[{"ring-offset":[r]}],shadow:[{shadow:["","inner","none",N,S]}],"shadow-color":[{shadow:[A]}],opacity:[{opacity:[v]}],"mix-blend":[{"mix-blend":["normal","multiply","screen","overlay","darken","lighten","color-dodge","color-burn","hard-light","soft-light","difference","exclusion","hue","saturation","color","luminosity","plus-lighter"]}],"bg-blend":[{"bg-blend":["normal","multiply","screen","overlay","darken","lighten","color-dodge","color-burn","hard-light","soft-light","difference","exclusion","hue","saturation","color","luminosity","plus-lighter"]}],filter:[{filter:["","none"]}],blur:[{blur:[o]}],brightness:[{brightness:[t]}],contrast:[{contrast:[s]}],"drop-shadow":[{"drop-shadow":["","none",N,I]}],grayscale:[{grayscale:[c]}],"hue-rotate":[{"hue-rotate":[d]}],invert:[{invert:[u]}],saturate:[{saturate:[T]}],sepia:[{sepia:[E]}],"backdrop-filter":[{"backdrop-filter":["","none"]}],"backdrop-blur":[{"backdrop-blur":[o]}],"backdrop-brightness":[{"backdrop-brightness":[t]}],"backdrop-contrast":[{"backdrop-contrast":[s]}],"backdrop-grayscale":[{"backdrop-grayscale":[c]}],"backdrop-hue-rotate":[{"backdrop-hue-rotate":[d]}],"backdrop-invert":[{"backdrop-invert":[u]}],"backdrop-opacity":[{"backdrop-opacity":[v]}],"backdrop-saturate":[{"backdrop-saturate":[T]}],"backdrop-sepia":[{"backdrop-sepia":[E]}],"border-collapse":[{border:["collapse","separate"]}],"border-spacing":[{"border-spacing":[a]}],"border-spacing-x":[{"border-spacing-x":[a]}],"border-spacing-y":[{"border-spacing-y":[a]}],"table-layout":[{table:["auto","fixed"]}],caption:[{caption:["top","bottom"]}],transition:[{transition:["none","all","","colors","opacity","shadow","transform",I]}],duration:[{duration:U()}],ease:[{ease:["linear","in","out","in-out",I]}],delay:[{delay:U()}],animate:[{animate:["none","spin","ping","pulse","bounce",I]}],transform:[{transform:["","gpu","none"]}],scale:[{scale:[O]}],"scale-x":[{"scale-x":[O]}],"scale-y":[{"scale-y":[O]}],rotate:[{rotate:[P,I]}],"translate-x":[{"translate-x":[$]}],"translate-y":[{"translate-y":[$]}],"skew-x":[{"skew-x":[_]}],"skew-y":[{"skew-y":[_]}],"transform-origin":[{origin:["center","top","top-right","right","bottom-right","bottom","bottom-left","left","top-left",I]}],accent:[{accent:["auto",r]}],appearance:["appearance-none"],cursor:[{cursor:["auto","default","pointer","wait","text","move","help","not-allowed","none","context-menu","progress","cell","crosshair","vertical-text","alias","copy","no-drop","grab","grabbing","all-scroll","col-resize","row-resize","n-resize","e-resize","s-resize","w-resize","ne-resize","nw-resize","se-resize","sw-resize","ew-resize","ns-resize","nesw-resize","nwse-resize","zoom-in","zoom-out",I]}],"caret-color":[{caret:[r]}],"pointer-events":[{"pointer-events":["none","auto"]}],resize:[{resize:["none","y","x",""]}],"scroll-behavior":[{scroll:["auto","smooth"]}],"scroll-m":[{"scroll-m":q()}],"scroll-mx":[{"scroll-mx":q()}],"scroll-my":[{"scroll-my":q()}],"scroll-ms":[{"scroll-ms":q()}],"scroll-me":[{"scroll-me":q()}],"scroll-mt":[{"scroll-mt":q()}],"scroll-mr":[{"scroll-mr":q()}],"scroll-mb":[{"scroll-mb":q()}],"scroll-ml":[{"scroll-ml":q()}],"scroll-p":[{"scroll-p":q()}],"scroll-px":[{"scroll-px":q()}],"scroll-py":[{"scroll-py":q()}],"scroll-ps":[{"scroll-ps":q()}],"scroll-pe":[{"scroll-pe":q()}],"scroll-pt":[{"scroll-pt":q()}],"scroll-pr":[{"scroll-pr":q()}],"scroll-pb":[{"scroll-pb":q()}],"scroll-pl":[{"scroll-pl":q()}],"snap-align":[{snap:["start","end","center","align-none"]}],"snap-stop":[{snap:["normal","always"]}],"snap-type":[{snap:["none","x","y","both"]}],"snap-strictness":[{snap:["mandatory","proximity"]}],touch:[{touch:["auto","none","pinch-zoom","manipulation",{pan:["x","left","right","y","up","down"]}]}],select:[{select:["none","text","all","auto"]}],"will-change":[{"will-change":["auto","scroll","contents","transform",I]}],fill:[{fill:[r,"none"]}],"stroke-w":[{stroke:[x,j]}],stroke:[{stroke:[r,"none"]}],sr:["sr-only","not-sr-only"]},conflictingClassGroups:{overflow:["overflow-x","overflow-y"],overscroll:["overscroll-x","overscroll-y"],inset:["inset-x","inset-y","start","end","top","right","bottom","left"],"inset-x":["right","left"],"inset-y":["top","bottom"],flex:["basis","grow","shrink"],gap:["gap-x","gap-y"],p:["px","py","ps","pe","pt","pr","pb","pl"],px:["pr","pl"],py:["pt","pb"],m:["mx","my","ms","me","mt","mr","mb","ml"],mx:["mr","ml"],my:["mt","mb"],"font-size":["leading"],"fvn-normal":["fvn-ordinal","fvn-slashed-zero","fvn-figure","fvn-spacing","fvn-fraction"],"fvn-ordinal":["fvn-normal"],"fvn-slashed-zero":["fvn-normal"],"fvn-figure":["fvn-normal"],"fvn-spacing":["fvn-normal"],"fvn-fraction":["fvn-normal"],rounded:["rounded-s","rounded-e","rounded-t","rounded-r","rounded-b","rounded-l","rounded-ss","rounded-se","rounded-ee","rounded-es","rounded-tl","rounded-tr","rounded-br","rounded-bl"],"rounded-s":["rounded-ss","rounded-es"],"rounded-e":["rounded-se","rounded-ee"],"rounded-t":["rounded-tl","rounded-tr"],"rounded-r":["rounded-tr","rounded-br"],"rounded-b":["rounded-br","rounded-bl"],"rounded-l":["rounded-tl","rounded-bl"],"border-spacing":["border-spacing-x","border-spacing-y"],"border-w":["border-w-s","border-w-e","border-w-t","border-w-r","border-w-b","border-w-l"],"border-w-x":["border-w-r","border-w-l"],"border-w-y":["border-w-t","border-w-b"],"border-color":["border-color-t","border-color-r","border-color-b","border-color-l"],"border-color-x":["border-color-r","border-color-l"],"border-color-y":["border-color-t","border-color-b"],"scroll-m":["scroll-mx","scroll-my","scroll-ms","scroll-me","scroll-mt","scroll-mr","scroll-mb","scroll-ml"],"scroll-mx":["scroll-mr","scroll-ml"],"scroll-my":["scroll-mt","scroll-mb"],"scroll-p":["scroll-px","scroll-py","scroll-ps","scroll-pe","scroll-pt","scroll-pr","scroll-pb","scroll-pl"],"scroll-px":["scroll-pr","scroll-pl"],"scroll-py":["scroll-pt","scroll-pb"]},conflictingClassGroupModifiers:{"font-size":["leading"]}}}function L(r,e){for(var o in e)J(r,o,e[o]);return r}var B=Object.prototype.hasOwnProperty,D=new Set(["string","number","boolean"]);function J(r,e,o){if(B.call(r,e)&&!D.has(typeof o)&&null!==o){if(Array.isArray(o)&&Array.isArray(r[e]))r[e]=r[e].concat(o);else if("object"==typeof o&&"object"==typeof r[e]){if(null===r[e])return void(r[e]=o);for(var t in o)J(r[e],t,o[t]);}}else r[e]=o;}var U=p(q),V=r;tailwindMerge_cjs_production_min.createTailwindMerge=p,tailwindMerge_cjs_production_min.extendTailwindMerge=function(r){for(var e=arguments.length,o=new Array(e>1?e-1:0),t=1;t<e;t++)o[t-1]=arguments[t];return p.apply(void 0,"function"==typeof r?[q,r].concat(o):[function(){return L(q(),r)}].concat(o))},tailwindMerge_cjs_production_min.fromTheme=f,tailwindMerge_cjs_production_min.getDefaultConfig=q,tailwindMerge_cjs_production_min.join=V,tailwindMerge_cjs_production_min.mergeConfigs=L,tailwindMerge_cjs_production_min.twJoin=r,tailwindMerge_cjs_production_min.twMerge=U,tailwindMerge_cjs_production_min.validators=R;
	
	return tailwindMerge_cjs_production_min;
}

var tailwindMerge_cjs_development = {};

var hasRequiredTailwindMerge_cjs_development;

function requireTailwindMerge_cjs_development () {
	if (hasRequiredTailwindMerge_cjs_development) return tailwindMerge_cjs_development;
	hasRequiredTailwindMerge_cjs_development = 1;

	Object.defineProperty(tailwindMerge_cjs_development, '__esModule', { value: true });

	/**
	 * The code in this file is copied from https://github.com/lukeed/clsx and modified to suit the needs of tailwind-merge better.
	 *
	 * Specifically:
	 * - Runtime code from https://github.com/lukeed/clsx/blob/v1.2.1/src/index.js
	 * - TypeScript types from https://github.com/lukeed/clsx/blob/v1.2.1/clsx.d.ts
	 *
	 * Original code has MIT license: Copyright (c) Luke Edwards <luke.edwards05@gmail.com> (lukeed.com)
	 */
	function twJoin() {
	  var index = 0;
	  var argument;
	  var resolvedValue;
	  var string = '';
	  while (index < arguments.length) {
	    if (argument = arguments[index++]) {
	      if (resolvedValue = toValue(argument)) {
	        string && (string += ' ');
	        string += resolvedValue;
	      }
	    }
	  }
	  return string;
	}
	function toValue(mix) {
	  if (typeof mix === 'string') {
	    return mix;
	  }
	  var resolvedValue;
	  var string = '';
	  for (var k = 0; k < mix.length; k++) {
	    if (mix[k]) {
	      if (resolvedValue = toValue(mix[k])) {
	        string && (string += ' ');
	        string += resolvedValue;
	      }
	    }
	  }
	  return string;
	}

	var CLASS_PART_SEPARATOR = '-';
	function createClassUtils(config) {
	  var classMap = createClassMap(config);
	  var conflictingClassGroups = config.conflictingClassGroups,
	    _config$conflictingCl = config.conflictingClassGroupModifiers,
	    conflictingClassGroupModifiers = _config$conflictingCl === void 0 ? {} : _config$conflictingCl;
	  function getClassGroupId(className) {
	    var classParts = className.split(CLASS_PART_SEPARATOR);
	    // Classes like `-inset-1` produce an empty string as first classPart. We assume that classes for negative values are used correctly and remove it from classParts.
	    if (classParts[0] === '' && classParts.length !== 1) {
	      classParts.shift();
	    }
	    return getGroupRecursive(classParts, classMap) || getGroupIdForArbitraryProperty(className);
	  }
	  function getConflictingClassGroupIds(classGroupId, hasPostfixModifier) {
	    var conflicts = conflictingClassGroups[classGroupId] || [];
	    if (hasPostfixModifier && conflictingClassGroupModifiers[classGroupId]) {
	      return [].concat(conflicts, conflictingClassGroupModifiers[classGroupId]);
	    }
	    return conflicts;
	  }
	  return {
	    getClassGroupId: getClassGroupId,
	    getConflictingClassGroupIds: getConflictingClassGroupIds
	  };
	}
	function getGroupRecursive(classParts, classPartObject) {
	  if (classParts.length === 0) {
	    return classPartObject.classGroupId;
	  }
	  var currentClassPart = classParts[0];
	  var nextClassPartObject = classPartObject.nextPart.get(currentClassPart);
	  var classGroupFromNextClassPart = nextClassPartObject ? getGroupRecursive(classParts.slice(1), nextClassPartObject) : undefined;
	  if (classGroupFromNextClassPart) {
	    return classGroupFromNextClassPart;
	  }
	  if (classPartObject.validators.length === 0) {
	    return undefined;
	  }
	  var classRest = classParts.join(CLASS_PART_SEPARATOR);
	  return classPartObject.validators.find(function (_ref) {
	    var validator = _ref.validator;
	    return validator(classRest);
	  })?.classGroupId;
	}
	var arbitraryPropertyRegex = /^\[(.+)\]$/;
	function getGroupIdForArbitraryProperty(className) {
	  if (arbitraryPropertyRegex.test(className)) {
	    var arbitraryPropertyClassName = arbitraryPropertyRegex.exec(className)[1];
	    var property = arbitraryPropertyClassName?.substring(0, arbitraryPropertyClassName.indexOf(':'));
	    if (property) {
	      // I use two dots here because one dot is used as prefix for class groups in plugins
	      return 'arbitrary..' + property;
	    }
	  }
	}
	/**
	 * Exported for testing only
	 */
	function createClassMap(config) {
	  var theme = config.theme,
	    prefix = config.prefix;
	  var classMap = {
	    nextPart: new Map(),
	    validators: []
	  };
	  var prefixedClassGroupEntries = getPrefixedClassGroupEntries(Object.entries(config.classGroups), prefix);
	  prefixedClassGroupEntries.forEach(function (_ref2) {
	    var classGroupId = _ref2[0],
	      classGroup = _ref2[1];
	    processClassesRecursively(classGroup, classMap, classGroupId, theme);
	  });
	  return classMap;
	}
	function processClassesRecursively(classGroup, classPartObject, classGroupId, theme) {
	  classGroup.forEach(function (classDefinition) {
	    if (typeof classDefinition === 'string') {
	      var classPartObjectToEdit = classDefinition === '' ? classPartObject : getPart(classPartObject, classDefinition);
	      classPartObjectToEdit.classGroupId = classGroupId;
	      return;
	    }
	    if (typeof classDefinition === 'function') {
	      if (isThemeGetter(classDefinition)) {
	        processClassesRecursively(classDefinition(theme), classPartObject, classGroupId, theme);
	        return;
	      }
	      classPartObject.validators.push({
	        validator: classDefinition,
	        classGroupId: classGroupId
	      });
	      return;
	    }
	    Object.entries(classDefinition).forEach(function (_ref3) {
	      var key = _ref3[0],
	        classGroup = _ref3[1];
	      processClassesRecursively(classGroup, getPart(classPartObject, key), classGroupId, theme);
	    });
	  });
	}
	function getPart(classPartObject, path) {
	  var currentClassPartObject = classPartObject;
	  path.split(CLASS_PART_SEPARATOR).forEach(function (pathPart) {
	    if (!currentClassPartObject.nextPart.has(pathPart)) {
	      currentClassPartObject.nextPart.set(pathPart, {
	        nextPart: new Map(),
	        validators: []
	      });
	    }
	    currentClassPartObject = currentClassPartObject.nextPart.get(pathPart);
	  });
	  return currentClassPartObject;
	}
	function isThemeGetter(func) {
	  return func.isThemeGetter;
	}
	function getPrefixedClassGroupEntries(classGroupEntries, prefix) {
	  if (!prefix) {
	    return classGroupEntries;
	  }
	  return classGroupEntries.map(function (_ref4) {
	    var classGroupId = _ref4[0],
	      classGroup = _ref4[1];
	    var prefixedClassGroup = classGroup.map(function (classDefinition) {
	      if (typeof classDefinition === 'string') {
	        return prefix + classDefinition;
	      }
	      if (typeof classDefinition === 'object') {
	        return Object.fromEntries(Object.entries(classDefinition).map(function (_ref5) {
	          var key = _ref5[0],
	            value = _ref5[1];
	          return [prefix + key, value];
	        }));
	      }
	      return classDefinition;
	    });
	    return [classGroupId, prefixedClassGroup];
	  });
	}

	// LRU cache inspired from hashlru (https://github.com/dominictarr/hashlru/blob/v1.0.4/index.js) but object replaced with Map to improve performance
	function createLruCache(maxCacheSize) {
	  if (maxCacheSize < 1) {
	    return {
	      get: function get() {
	        return undefined;
	      },
	      set: function set() {}
	    };
	  }
	  var cacheSize = 0;
	  var cache = new Map();
	  var previousCache = new Map();
	  function update(key, value) {
	    cache.set(key, value);
	    cacheSize++;
	    if (cacheSize > maxCacheSize) {
	      cacheSize = 0;
	      previousCache = cache;
	      cache = new Map();
	    }
	  }
	  return {
	    get: function get(key) {
	      var value = cache.get(key);
	      if (value !== undefined) {
	        return value;
	      }
	      if ((value = previousCache.get(key)) !== undefined) {
	        update(key, value);
	        return value;
	      }
	    },
	    set: function set(key, value) {
	      if (cache.has(key)) {
	        cache.set(key, value);
	      } else {
	        update(key, value);
	      }
	    }
	  };
	}

	var IMPORTANT_MODIFIER = '!';
	function createSplitModifiers(config) {
	  var separator = config.separator || ':';
	  var isSeparatorSingleCharacter = separator.length === 1;
	  var firstSeparatorCharacter = separator[0];
	  var separatorLength = separator.length;
	  // splitModifiers inspired by https://github.com/tailwindlabs/tailwindcss/blob/v3.2.2/src/util/splitAtTopLevelOnly.js
	  return function splitModifiers(className) {
	    var modifiers = [];
	    var bracketDepth = 0;
	    var modifierStart = 0;
	    var postfixModifierPosition;
	    for (var index = 0; index < className.length; index++) {
	      var currentCharacter = className[index];
	      if (bracketDepth === 0) {
	        if (currentCharacter === firstSeparatorCharacter && (isSeparatorSingleCharacter || className.slice(index, index + separatorLength) === separator)) {
	          modifiers.push(className.slice(modifierStart, index));
	          modifierStart = index + separatorLength;
	          continue;
	        }
	        if (currentCharacter === '/') {
	          postfixModifierPosition = index;
	          continue;
	        }
	      }
	      if (currentCharacter === '[') {
	        bracketDepth++;
	      } else if (currentCharacter === ']') {
	        bracketDepth--;
	      }
	    }
	    var baseClassNameWithImportantModifier = modifiers.length === 0 ? className : className.substring(modifierStart);
	    var hasImportantModifier = baseClassNameWithImportantModifier.startsWith(IMPORTANT_MODIFIER);
	    var baseClassName = hasImportantModifier ? baseClassNameWithImportantModifier.substring(1) : baseClassNameWithImportantModifier;
	    var maybePostfixModifierPosition = postfixModifierPosition && postfixModifierPosition > modifierStart ? postfixModifierPosition - modifierStart : undefined;
	    return {
	      modifiers: modifiers,
	      hasImportantModifier: hasImportantModifier,
	      baseClassName: baseClassName,
	      maybePostfixModifierPosition: maybePostfixModifierPosition
	    };
	  };
	}
	/**
	 * Sorts modifiers according to following schema:
	 * - Predefined modifiers are sorted alphabetically
	 * - When an arbitrary variant appears, it must be preserved which modifiers are before and after it
	 */
	function sortModifiers(modifiers) {
	  if (modifiers.length <= 1) {
	    return modifiers;
	  }
	  var sortedModifiers = [];
	  var unsortedModifiers = [];
	  modifiers.forEach(function (modifier) {
	    var isArbitraryVariant = modifier[0] === '[';
	    if (isArbitraryVariant) {
	      sortedModifiers.push.apply(sortedModifiers, unsortedModifiers.sort().concat([modifier]));
	      unsortedModifiers = [];
	    } else {
	      unsortedModifiers.push(modifier);
	    }
	  });
	  sortedModifiers.push.apply(sortedModifiers, unsortedModifiers.sort());
	  return sortedModifiers;
	}

	function createConfigUtils(config) {
	  return {
	    cache: createLruCache(config.cacheSize),
	    splitModifiers: createSplitModifiers(config),
	    ...createClassUtils(config)
	  };
	}

	var SPLIT_CLASSES_REGEX = /\s+/;
	function mergeClassList(classList, configUtils) {
	  var splitModifiers = configUtils.splitModifiers,
	    getClassGroupId = configUtils.getClassGroupId,
	    getConflictingClassGroupIds = configUtils.getConflictingClassGroupIds;
	  /**
	   * Set of classGroupIds in following format:
	   * `{importantModifier}{variantModifiers}{classGroupId}`
	   * @example 'float'
	   * @example 'hover:focus:bg-color'
	   * @example 'md:!pr'
	   */
	  var classGroupsInConflict = new Set();
	  return classList.trim().split(SPLIT_CLASSES_REGEX).map(function (originalClassName) {
	    var _splitModifiers = splitModifiers(originalClassName),
	      modifiers = _splitModifiers.modifiers,
	      hasImportantModifier = _splitModifiers.hasImportantModifier,
	      baseClassName = _splitModifiers.baseClassName,
	      maybePostfixModifierPosition = _splitModifiers.maybePostfixModifierPosition;
	    var classGroupId = getClassGroupId(maybePostfixModifierPosition ? baseClassName.substring(0, maybePostfixModifierPosition) : baseClassName);
	    var hasPostfixModifier = Boolean(maybePostfixModifierPosition);
	    if (!classGroupId) {
	      if (!maybePostfixModifierPosition) {
	        return {
	          isTailwindClass: false,
	          originalClassName: originalClassName
	        };
	      }
	      classGroupId = getClassGroupId(baseClassName);
	      if (!classGroupId) {
	        return {
	          isTailwindClass: false,
	          originalClassName: originalClassName
	        };
	      }
	      hasPostfixModifier = false;
	    }
	    var variantModifier = sortModifiers(modifiers).join(':');
	    var modifierId = hasImportantModifier ? variantModifier + IMPORTANT_MODIFIER : variantModifier;
	    return {
	      isTailwindClass: true,
	      modifierId: modifierId,
	      classGroupId: classGroupId,
	      originalClassName: originalClassName,
	      hasPostfixModifier: hasPostfixModifier
	    };
	  }).reverse()
	  // Last class in conflict wins, so we need to filter conflicting classes in reverse order.
	  .filter(function (parsed) {
	    if (!parsed.isTailwindClass) {
	      return true;
	    }
	    var modifierId = parsed.modifierId,
	      classGroupId = parsed.classGroupId,
	      hasPostfixModifier = parsed.hasPostfixModifier;
	    var classId = modifierId + classGroupId;
	    if (classGroupsInConflict.has(classId)) {
	      return false;
	    }
	    classGroupsInConflict.add(classId);
	    getConflictingClassGroupIds(classGroupId, hasPostfixModifier).forEach(function (group) {
	      return classGroupsInConflict.add(modifierId + group);
	    });
	    return true;
	  }).reverse().map(function (parsed) {
	    return parsed.originalClassName;
	  }).join(' ');
	}

	function createTailwindMerge() {
	  for (var _len = arguments.length, createConfig = new Array(_len), _key = 0; _key < _len; _key++) {
	    createConfig[_key] = arguments[_key];
	  }
	  var configUtils;
	  var cacheGet;
	  var cacheSet;
	  var functionToCall = initTailwindMerge;
	  function initTailwindMerge(classList) {
	    var firstCreateConfig = createConfig[0],
	      restCreateConfig = createConfig.slice(1);
	    var config = restCreateConfig.reduce(function (previousConfig, createConfigCurrent) {
	      return createConfigCurrent(previousConfig);
	    }, firstCreateConfig());
	    configUtils = createConfigUtils(config);
	    cacheGet = configUtils.cache.get;
	    cacheSet = configUtils.cache.set;
	    functionToCall = tailwindMerge;
	    return tailwindMerge(classList);
	  }
	  function tailwindMerge(classList) {
	    var cachedResult = cacheGet(classList);
	    if (cachedResult) {
	      return cachedResult;
	    }
	    var result = mergeClassList(classList, configUtils);
	    cacheSet(classList, result);
	    return result;
	  }
	  return function callTailwindMerge() {
	    return functionToCall(twJoin.apply(null, arguments));
	  };
	}

	function fromTheme(key) {
	  var themeGetter = function themeGetter(theme) {
	    return theme[key] || [];
	  };
	  themeGetter.isThemeGetter = true;
	  return themeGetter;
	}

	var arbitraryValueRegex = /^\[(?:([a-z-]+):)?(.+)\]$/i;
	var fractionRegex = /^\d+\/\d+$/;
	var stringLengths = /*#__PURE__*/new Set(['px', 'full', 'screen']);
	var tshirtUnitRegex = /^(\d+(\.\d+)?)?(xs|sm|md|lg|xl)$/;
	var lengthUnitRegex = /\d+(%|px|r?em|[sdl]?v([hwib]|min|max)|pt|pc|in|cm|mm|cap|ch|ex|r?lh|cq(w|h|i|b|min|max))|\b(calc|min|max|clamp)\(.+\)|^0$/;
	// Shadow always begins with x and y offset separated by underscore
	var shadowRegex = /^-?((\d+)?\.?(\d+)[a-z]+|0)_-?((\d+)?\.?(\d+)[a-z]+|0)/;
	function isLength(value) {
	  return isNumber(value) || stringLengths.has(value) || fractionRegex.test(value) || isArbitraryLength(value);
	}
	function isArbitraryLength(value) {
	  return getIsArbitraryValue(value, 'length', isLengthOnly);
	}
	function isArbitrarySize(value) {
	  return getIsArbitraryValue(value, 'size', isNever);
	}
	function isArbitraryPosition(value) {
	  return getIsArbitraryValue(value, 'position', isNever);
	}
	function isArbitraryUrl(value) {
	  return getIsArbitraryValue(value, 'url', isUrl);
	}
	function isArbitraryNumber(value) {
	  return getIsArbitraryValue(value, 'number', isNumber);
	}
	/**
	 * @deprecated Will be removed in next major version. Use `isArbitraryNumber` instead.
	 */
	var isArbitraryWeight = isArbitraryNumber;
	function isNumber(value) {
	  return !Number.isNaN(Number(value));
	}
	function isPercent(value) {
	  return value.endsWith('%') && isNumber(value.slice(0, -1));
	}
	function isInteger(value) {
	  return isIntegerOnly(value) || getIsArbitraryValue(value, 'number', isIntegerOnly);
	}
	function isArbitraryValue(value) {
	  return arbitraryValueRegex.test(value);
	}
	function isAny() {
	  return true;
	}
	function isTshirtSize(value) {
	  return tshirtUnitRegex.test(value);
	}
	function isArbitraryShadow(value) {
	  return getIsArbitraryValue(value, '', isShadow);
	}
	function getIsArbitraryValue(value, label, testValue) {
	  var result = arbitraryValueRegex.exec(value);
	  if (result) {
	    if (result[1]) {
	      return result[1] === label;
	    }
	    return testValue(result[2]);
	  }
	  return false;
	}
	function isLengthOnly(value) {
	  return lengthUnitRegex.test(value);
	}
	function isNever() {
	  return false;
	}
	function isUrl(value) {
	  return value.startsWith('url(');
	}
	function isIntegerOnly(value) {
	  return Number.isInteger(Number(value));
	}
	function isShadow(value) {
	  return shadowRegex.test(value);
	}

	var validators = {
	    __proto__: null,
	    isAny: isAny,
	    isArbitraryLength: isArbitraryLength,
	    isArbitraryNumber: isArbitraryNumber,
	    isArbitraryPosition: isArbitraryPosition,
	    isArbitraryShadow: isArbitraryShadow,
	    isArbitrarySize: isArbitrarySize,
	    isArbitraryUrl: isArbitraryUrl,
	    isArbitraryValue: isArbitraryValue,
	    isArbitraryWeight: isArbitraryWeight,
	    isInteger: isInteger,
	    isLength: isLength,
	    isNumber: isNumber,
	    isPercent: isPercent,
	    isTshirtSize: isTshirtSize
	};

	function getDefaultConfig() {
	  var colors = fromTheme('colors');
	  var spacing = fromTheme('spacing');
	  var blur = fromTheme('blur');
	  var brightness = fromTheme('brightness');
	  var borderColor = fromTheme('borderColor');
	  var borderRadius = fromTheme('borderRadius');
	  var borderSpacing = fromTheme('borderSpacing');
	  var borderWidth = fromTheme('borderWidth');
	  var contrast = fromTheme('contrast');
	  var grayscale = fromTheme('grayscale');
	  var hueRotate = fromTheme('hueRotate');
	  var invert = fromTheme('invert');
	  var gap = fromTheme('gap');
	  var gradientColorStops = fromTheme('gradientColorStops');
	  var gradientColorStopPositions = fromTheme('gradientColorStopPositions');
	  var inset = fromTheme('inset');
	  var margin = fromTheme('margin');
	  var opacity = fromTheme('opacity');
	  var padding = fromTheme('padding');
	  var saturate = fromTheme('saturate');
	  var scale = fromTheme('scale');
	  var sepia = fromTheme('sepia');
	  var skew = fromTheme('skew');
	  var space = fromTheme('space');
	  var translate = fromTheme('translate');
	  var getOverscroll = function getOverscroll() {
	    return ['auto', 'contain', 'none'];
	  };
	  var getOverflow = function getOverflow() {
	    return ['auto', 'hidden', 'clip', 'visible', 'scroll'];
	  };
	  var getSpacingWithAutoAndArbitrary = function getSpacingWithAutoAndArbitrary() {
	    return ['auto', isArbitraryValue, spacing];
	  };
	  var getSpacingWithArbitrary = function getSpacingWithArbitrary() {
	    return [isArbitraryValue, spacing];
	  };
	  var getLengthWithEmpty = function getLengthWithEmpty() {
	    return ['', isLength];
	  };
	  var getNumberWithAutoAndArbitrary = function getNumberWithAutoAndArbitrary() {
	    return ['auto', isNumber, isArbitraryValue];
	  };
	  var getPositions = function getPositions() {
	    return ['bottom', 'center', 'left', 'left-bottom', 'left-top', 'right', 'right-bottom', 'right-top', 'top'];
	  };
	  var getLineStyles = function getLineStyles() {
	    return ['solid', 'dashed', 'dotted', 'double', 'none'];
	  };
	  var getBlendModes = function getBlendModes() {
	    return ['normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten', 'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference', 'exclusion', 'hue', 'saturation', 'color', 'luminosity', 'plus-lighter'];
	  };
	  var getAlign = function getAlign() {
	    return ['start', 'end', 'center', 'between', 'around', 'evenly', 'stretch'];
	  };
	  var getZeroAndEmpty = function getZeroAndEmpty() {
	    return ['', '0', isArbitraryValue];
	  };
	  var getBreaks = function getBreaks() {
	    return ['auto', 'avoid', 'all', 'avoid-page', 'page', 'left', 'right', 'column'];
	  };
	  var getNumber = function getNumber() {
	    return [isNumber, isArbitraryNumber];
	  };
	  var getNumberAndArbitrary = function getNumberAndArbitrary() {
	    return [isNumber, isArbitraryValue];
	  };
	  return {
	    cacheSize: 500,
	    theme: {
	      colors: [isAny],
	      spacing: [isLength],
	      blur: ['none', '', isTshirtSize, isArbitraryValue],
	      brightness: getNumber(),
	      borderColor: [colors],
	      borderRadius: ['none', '', 'full', isTshirtSize, isArbitraryValue],
	      borderSpacing: getSpacingWithArbitrary(),
	      borderWidth: getLengthWithEmpty(),
	      contrast: getNumber(),
	      grayscale: getZeroAndEmpty(),
	      hueRotate: getNumberAndArbitrary(),
	      invert: getZeroAndEmpty(),
	      gap: getSpacingWithArbitrary(),
	      gradientColorStops: [colors],
	      gradientColorStopPositions: [isPercent, isArbitraryLength],
	      inset: getSpacingWithAutoAndArbitrary(),
	      margin: getSpacingWithAutoAndArbitrary(),
	      opacity: getNumber(),
	      padding: getSpacingWithArbitrary(),
	      saturate: getNumber(),
	      scale: getNumber(),
	      sepia: getZeroAndEmpty(),
	      skew: getNumberAndArbitrary(),
	      space: getSpacingWithArbitrary(),
	      translate: getSpacingWithArbitrary()
	    },
	    classGroups: {
	      // Layout
	      /**
	       * Aspect Ratio
	       * @see https://tailwindcss.com/docs/aspect-ratio
	       */
	      aspect: [{
	        aspect: ['auto', 'square', 'video', isArbitraryValue]
	      }],
	      /**
	       * Container
	       * @see https://tailwindcss.com/docs/container
	       */
	      container: ['container'],
	      /**
	       * Columns
	       * @see https://tailwindcss.com/docs/columns
	       */
	      columns: [{
	        columns: [isTshirtSize]
	      }],
	      /**
	       * Break After
	       * @see https://tailwindcss.com/docs/break-after
	       */
	      'break-after': [{
	        'break-after': getBreaks()
	      }],
	      /**
	       * Break Before
	       * @see https://tailwindcss.com/docs/break-before
	       */
	      'break-before': [{
	        'break-before': getBreaks()
	      }],
	      /**
	       * Break Inside
	       * @see https://tailwindcss.com/docs/break-inside
	       */
	      'break-inside': [{
	        'break-inside': ['auto', 'avoid', 'avoid-page', 'avoid-column']
	      }],
	      /**
	       * Box Decoration Break
	       * @see https://tailwindcss.com/docs/box-decoration-break
	       */
	      'box-decoration': [{
	        'box-decoration': ['slice', 'clone']
	      }],
	      /**
	       * Box Sizing
	       * @see https://tailwindcss.com/docs/box-sizing
	       */
	      box: [{
	        box: ['border', 'content']
	      }],
	      /**
	       * Display
	       * @see https://tailwindcss.com/docs/display
	       */
	      display: ['block', 'inline-block', 'inline', 'flex', 'inline-flex', 'table', 'inline-table', 'table-caption', 'table-cell', 'table-column', 'table-column-group', 'table-footer-group', 'table-header-group', 'table-row-group', 'table-row', 'flow-root', 'grid', 'inline-grid', 'contents', 'list-item', 'hidden'],
	      /**
	       * Floats
	       * @see https://tailwindcss.com/docs/float
	       */
	      "float": [{
	        "float": ['right', 'left', 'none']
	      }],
	      /**
	       * Clear
	       * @see https://tailwindcss.com/docs/clear
	       */
	      clear: [{
	        clear: ['left', 'right', 'both', 'none']
	      }],
	      /**
	       * Isolation
	       * @see https://tailwindcss.com/docs/isolation
	       */
	      isolation: ['isolate', 'isolation-auto'],
	      /**
	       * Object Fit
	       * @see https://tailwindcss.com/docs/object-fit
	       */
	      'object-fit': [{
	        object: ['contain', 'cover', 'fill', 'none', 'scale-down']
	      }],
	      /**
	       * Object Position
	       * @see https://tailwindcss.com/docs/object-position
	       */
	      'object-position': [{
	        object: [].concat(getPositions(), [isArbitraryValue])
	      }],
	      /**
	       * Overflow
	       * @see https://tailwindcss.com/docs/overflow
	       */
	      overflow: [{
	        overflow: getOverflow()
	      }],
	      /**
	       * Overflow X
	       * @see https://tailwindcss.com/docs/overflow
	       */
	      'overflow-x': [{
	        'overflow-x': getOverflow()
	      }],
	      /**
	       * Overflow Y
	       * @see https://tailwindcss.com/docs/overflow
	       */
	      'overflow-y': [{
	        'overflow-y': getOverflow()
	      }],
	      /**
	       * Overscroll Behavior
	       * @see https://tailwindcss.com/docs/overscroll-behavior
	       */
	      overscroll: [{
	        overscroll: getOverscroll()
	      }],
	      /**
	       * Overscroll Behavior X
	       * @see https://tailwindcss.com/docs/overscroll-behavior
	       */
	      'overscroll-x': [{
	        'overscroll-x': getOverscroll()
	      }],
	      /**
	       * Overscroll Behavior Y
	       * @see https://tailwindcss.com/docs/overscroll-behavior
	       */
	      'overscroll-y': [{
	        'overscroll-y': getOverscroll()
	      }],
	      /**
	       * Position
	       * @see https://tailwindcss.com/docs/position
	       */
	      position: ['static', 'fixed', 'absolute', 'relative', 'sticky'],
	      /**
	       * Top / Right / Bottom / Left
	       * @see https://tailwindcss.com/docs/top-right-bottom-left
	       */
	      inset: [{
	        inset: [inset]
	      }],
	      /**
	       * Right / Left
	       * @see https://tailwindcss.com/docs/top-right-bottom-left
	       */
	      'inset-x': [{
	        'inset-x': [inset]
	      }],
	      /**
	       * Top / Bottom
	       * @see https://tailwindcss.com/docs/top-right-bottom-left
	       */
	      'inset-y': [{
	        'inset-y': [inset]
	      }],
	      /**
	       * Start
	       * @see https://tailwindcss.com/docs/top-right-bottom-left
	       */
	      start: [{
	        start: [inset]
	      }],
	      /**
	       * End
	       * @see https://tailwindcss.com/docs/top-right-bottom-left
	       */
	      end: [{
	        end: [inset]
	      }],
	      /**
	       * Top
	       * @see https://tailwindcss.com/docs/top-right-bottom-left
	       */
	      top: [{
	        top: [inset]
	      }],
	      /**
	       * Right
	       * @see https://tailwindcss.com/docs/top-right-bottom-left
	       */
	      right: [{
	        right: [inset]
	      }],
	      /**
	       * Bottom
	       * @see https://tailwindcss.com/docs/top-right-bottom-left
	       */
	      bottom: [{
	        bottom: [inset]
	      }],
	      /**
	       * Left
	       * @see https://tailwindcss.com/docs/top-right-bottom-left
	       */
	      left: [{
	        left: [inset]
	      }],
	      /**
	       * Visibility
	       * @see https://tailwindcss.com/docs/visibility
	       */
	      visibility: ['visible', 'invisible', 'collapse'],
	      /**
	       * Z-Index
	       * @see https://tailwindcss.com/docs/z-index
	       */
	      z: [{
	        z: ['auto', isInteger]
	      }],
	      // Flexbox and Grid
	      /**
	       * Flex Basis
	       * @see https://tailwindcss.com/docs/flex-basis
	       */
	      basis: [{
	        basis: getSpacingWithAutoAndArbitrary()
	      }],
	      /**
	       * Flex Direction
	       * @see https://tailwindcss.com/docs/flex-direction
	       */
	      'flex-direction': [{
	        flex: ['row', 'row-reverse', 'col', 'col-reverse']
	      }],
	      /**
	       * Flex Wrap
	       * @see https://tailwindcss.com/docs/flex-wrap
	       */
	      'flex-wrap': [{
	        flex: ['wrap', 'wrap-reverse', 'nowrap']
	      }],
	      /**
	       * Flex
	       * @see https://tailwindcss.com/docs/flex
	       */
	      flex: [{
	        flex: ['1', 'auto', 'initial', 'none', isArbitraryValue]
	      }],
	      /**
	       * Flex Grow
	       * @see https://tailwindcss.com/docs/flex-grow
	       */
	      grow: [{
	        grow: getZeroAndEmpty()
	      }],
	      /**
	       * Flex Shrink
	       * @see https://tailwindcss.com/docs/flex-shrink
	       */
	      shrink: [{
	        shrink: getZeroAndEmpty()
	      }],
	      /**
	       * Order
	       * @see https://tailwindcss.com/docs/order
	       */
	      order: [{
	        order: ['first', 'last', 'none', isInteger]
	      }],
	      /**
	       * Grid Template Columns
	       * @see https://tailwindcss.com/docs/grid-template-columns
	       */
	      'grid-cols': [{
	        'grid-cols': [isAny]
	      }],
	      /**
	       * Grid Column Start / End
	       * @see https://tailwindcss.com/docs/grid-column
	       */
	      'col-start-end': [{
	        col: ['auto', {
	          span: ['full', isInteger]
	        }, isArbitraryValue]
	      }],
	      /**
	       * Grid Column Start
	       * @see https://tailwindcss.com/docs/grid-column
	       */
	      'col-start': [{
	        'col-start': getNumberWithAutoAndArbitrary()
	      }],
	      /**
	       * Grid Column End
	       * @see https://tailwindcss.com/docs/grid-column
	       */
	      'col-end': [{
	        'col-end': getNumberWithAutoAndArbitrary()
	      }],
	      /**
	       * Grid Template Rows
	       * @see https://tailwindcss.com/docs/grid-template-rows
	       */
	      'grid-rows': [{
	        'grid-rows': [isAny]
	      }],
	      /**
	       * Grid Row Start / End
	       * @see https://tailwindcss.com/docs/grid-row
	       */
	      'row-start-end': [{
	        row: ['auto', {
	          span: [isInteger]
	        }, isArbitraryValue]
	      }],
	      /**
	       * Grid Row Start
	       * @see https://tailwindcss.com/docs/grid-row
	       */
	      'row-start': [{
	        'row-start': getNumberWithAutoAndArbitrary()
	      }],
	      /**
	       * Grid Row End
	       * @see https://tailwindcss.com/docs/grid-row
	       */
	      'row-end': [{
	        'row-end': getNumberWithAutoAndArbitrary()
	      }],
	      /**
	       * Grid Auto Flow
	       * @see https://tailwindcss.com/docs/grid-auto-flow
	       */
	      'grid-flow': [{
	        'grid-flow': ['row', 'col', 'dense', 'row-dense', 'col-dense']
	      }],
	      /**
	       * Grid Auto Columns
	       * @see https://tailwindcss.com/docs/grid-auto-columns
	       */
	      'auto-cols': [{
	        'auto-cols': ['auto', 'min', 'max', 'fr', isArbitraryValue]
	      }],
	      /**
	       * Grid Auto Rows
	       * @see https://tailwindcss.com/docs/grid-auto-rows
	       */
	      'auto-rows': [{
	        'auto-rows': ['auto', 'min', 'max', 'fr', isArbitraryValue]
	      }],
	      /**
	       * Gap
	       * @see https://tailwindcss.com/docs/gap
	       */
	      gap: [{
	        gap: [gap]
	      }],
	      /**
	       * Gap X
	       * @see https://tailwindcss.com/docs/gap
	       */
	      'gap-x': [{
	        'gap-x': [gap]
	      }],
	      /**
	       * Gap Y
	       * @see https://tailwindcss.com/docs/gap
	       */
	      'gap-y': [{
	        'gap-y': [gap]
	      }],
	      /**
	       * Justify Content
	       * @see https://tailwindcss.com/docs/justify-content
	       */
	      'justify-content': [{
	        justify: ['normal'].concat(getAlign())
	      }],
	      /**
	       * Justify Items
	       * @see https://tailwindcss.com/docs/justify-items
	       */
	      'justify-items': [{
	        'justify-items': ['start', 'end', 'center', 'stretch']
	      }],
	      /**
	       * Justify Self
	       * @see https://tailwindcss.com/docs/justify-self
	       */
	      'justify-self': [{
	        'justify-self': ['auto', 'start', 'end', 'center', 'stretch']
	      }],
	      /**
	       * Align Content
	       * @see https://tailwindcss.com/docs/align-content
	       */
	      'align-content': [{
	        content: ['normal'].concat(getAlign(), ['baseline'])
	      }],
	      /**
	       * Align Items
	       * @see https://tailwindcss.com/docs/align-items
	       */
	      'align-items': [{
	        items: ['start', 'end', 'center', 'baseline', 'stretch']
	      }],
	      /**
	       * Align Self
	       * @see https://tailwindcss.com/docs/align-self
	       */
	      'align-self': [{
	        self: ['auto', 'start', 'end', 'center', 'stretch', 'baseline']
	      }],
	      /**
	       * Place Content
	       * @see https://tailwindcss.com/docs/place-content
	       */
	      'place-content': [{
	        'place-content': [].concat(getAlign(), ['baseline'])
	      }],
	      /**
	       * Place Items
	       * @see https://tailwindcss.com/docs/place-items
	       */
	      'place-items': [{
	        'place-items': ['start', 'end', 'center', 'baseline', 'stretch']
	      }],
	      /**
	       * Place Self
	       * @see https://tailwindcss.com/docs/place-self
	       */
	      'place-self': [{
	        'place-self': ['auto', 'start', 'end', 'center', 'stretch']
	      }],
	      // Spacing
	      /**
	       * Padding
	       * @see https://tailwindcss.com/docs/padding
	       */
	      p: [{
	        p: [padding]
	      }],
	      /**
	       * Padding X
	       * @see https://tailwindcss.com/docs/padding
	       */
	      px: [{
	        px: [padding]
	      }],
	      /**
	       * Padding Y
	       * @see https://tailwindcss.com/docs/padding
	       */
	      py: [{
	        py: [padding]
	      }],
	      /**
	       * Padding Start
	       * @see https://tailwindcss.com/docs/padding
	       */
	      ps: [{
	        ps: [padding]
	      }],
	      /**
	       * Padding End
	       * @see https://tailwindcss.com/docs/padding
	       */
	      pe: [{
	        pe: [padding]
	      }],
	      /**
	       * Padding Top
	       * @see https://tailwindcss.com/docs/padding
	       */
	      pt: [{
	        pt: [padding]
	      }],
	      /**
	       * Padding Right
	       * @see https://tailwindcss.com/docs/padding
	       */
	      pr: [{
	        pr: [padding]
	      }],
	      /**
	       * Padding Bottom
	       * @see https://tailwindcss.com/docs/padding
	       */
	      pb: [{
	        pb: [padding]
	      }],
	      /**
	       * Padding Left
	       * @see https://tailwindcss.com/docs/padding
	       */
	      pl: [{
	        pl: [padding]
	      }],
	      /**
	       * Margin
	       * @see https://tailwindcss.com/docs/margin
	       */
	      m: [{
	        m: [margin]
	      }],
	      /**
	       * Margin X
	       * @see https://tailwindcss.com/docs/margin
	       */
	      mx: [{
	        mx: [margin]
	      }],
	      /**
	       * Margin Y
	       * @see https://tailwindcss.com/docs/margin
	       */
	      my: [{
	        my: [margin]
	      }],
	      /**
	       * Margin Start
	       * @see https://tailwindcss.com/docs/margin
	       */
	      ms: [{
	        ms: [margin]
	      }],
	      /**
	       * Margin End
	       * @see https://tailwindcss.com/docs/margin
	       */
	      me: [{
	        me: [margin]
	      }],
	      /**
	       * Margin Top
	       * @see https://tailwindcss.com/docs/margin
	       */
	      mt: [{
	        mt: [margin]
	      }],
	      /**
	       * Margin Right
	       * @see https://tailwindcss.com/docs/margin
	       */
	      mr: [{
	        mr: [margin]
	      }],
	      /**
	       * Margin Bottom
	       * @see https://tailwindcss.com/docs/margin
	       */
	      mb: [{
	        mb: [margin]
	      }],
	      /**
	       * Margin Left
	       * @see https://tailwindcss.com/docs/margin
	       */
	      ml: [{
	        ml: [margin]
	      }],
	      /**
	       * Space Between X
	       * @see https://tailwindcss.com/docs/space
	       */
	      'space-x': [{
	        'space-x': [space]
	      }],
	      /**
	       * Space Between X Reverse
	       * @see https://tailwindcss.com/docs/space
	       */
	      'space-x-reverse': ['space-x-reverse'],
	      /**
	       * Space Between Y
	       * @see https://tailwindcss.com/docs/space
	       */
	      'space-y': [{
	        'space-y': [space]
	      }],
	      /**
	       * Space Between Y Reverse
	       * @see https://tailwindcss.com/docs/space
	       */
	      'space-y-reverse': ['space-y-reverse'],
	      // Sizing
	      /**
	       * Width
	       * @see https://tailwindcss.com/docs/width
	       */
	      w: [{
	        w: ['auto', 'min', 'max', 'fit', isArbitraryValue, spacing]
	      }],
	      /**
	       * Min-Width
	       * @see https://tailwindcss.com/docs/min-width
	       */
	      'min-w': [{
	        'min-w': ['min', 'max', 'fit', isArbitraryValue, isLength]
	      }],
	      /**
	       * Max-Width
	       * @see https://tailwindcss.com/docs/max-width
	       */
	      'max-w': [{
	        'max-w': ['0', 'none', 'full', 'min', 'max', 'fit', 'prose', {
	          screen: [isTshirtSize]
	        }, isTshirtSize, isArbitraryValue]
	      }],
	      /**
	       * Height
	       * @see https://tailwindcss.com/docs/height
	       */
	      h: [{
	        h: [isArbitraryValue, spacing, 'auto', 'min', 'max', 'fit']
	      }],
	      /**
	       * Min-Height
	       * @see https://tailwindcss.com/docs/min-height
	       */
	      'min-h': [{
	        'min-h': ['min', 'max', 'fit', isArbitraryValue, isLength]
	      }],
	      /**
	       * Max-Height
	       * @see https://tailwindcss.com/docs/max-height
	       */
	      'max-h': [{
	        'max-h': [isArbitraryValue, spacing, 'min', 'max', 'fit']
	      }],
	      // Typography
	      /**
	       * Font Size
	       * @see https://tailwindcss.com/docs/font-size
	       */
	      'font-size': [{
	        text: ['base', isTshirtSize, isArbitraryLength]
	      }],
	      /**
	       * Font Smoothing
	       * @see https://tailwindcss.com/docs/font-smoothing
	       */
	      'font-smoothing': ['antialiased', 'subpixel-antialiased'],
	      /**
	       * Font Style
	       * @see https://tailwindcss.com/docs/font-style
	       */
	      'font-style': ['italic', 'not-italic'],
	      /**
	       * Font Weight
	       * @see https://tailwindcss.com/docs/font-weight
	       */
	      'font-weight': [{
	        font: ['thin', 'extralight', 'light', 'normal', 'medium', 'semibold', 'bold', 'extrabold', 'black', isArbitraryNumber]
	      }],
	      /**
	       * Font Family
	       * @see https://tailwindcss.com/docs/font-family
	       */
	      'font-family': [{
	        font: [isAny]
	      }],
	      /**
	       * Font Variant Numeric
	       * @see https://tailwindcss.com/docs/font-variant-numeric
	       */
	      'fvn-normal': ['normal-nums'],
	      /**
	       * Font Variant Numeric
	       * @see https://tailwindcss.com/docs/font-variant-numeric
	       */
	      'fvn-ordinal': ['ordinal'],
	      /**
	       * Font Variant Numeric
	       * @see https://tailwindcss.com/docs/font-variant-numeric
	       */
	      'fvn-slashed-zero': ['slashed-zero'],
	      /**
	       * Font Variant Numeric
	       * @see https://tailwindcss.com/docs/font-variant-numeric
	       */
	      'fvn-figure': ['lining-nums', 'oldstyle-nums'],
	      /**
	       * Font Variant Numeric
	       * @see https://tailwindcss.com/docs/font-variant-numeric
	       */
	      'fvn-spacing': ['proportional-nums', 'tabular-nums'],
	      /**
	       * Font Variant Numeric
	       * @see https://tailwindcss.com/docs/font-variant-numeric
	       */
	      'fvn-fraction': ['diagonal-fractions', 'stacked-fractons'],
	      /**
	       * Letter Spacing
	       * @see https://tailwindcss.com/docs/letter-spacing
	       */
	      tracking: [{
	        tracking: ['tighter', 'tight', 'normal', 'wide', 'wider', 'widest', isArbitraryValue]
	      }],
	      /**
	       * Line Clamp
	       * @see https://tailwindcss.com/docs/line-clamp
	       */
	      'line-clamp': [{
	        'line-clamp': ['none', isNumber, isArbitraryNumber]
	      }],
	      /**
	       * Line Height
	       * @see https://tailwindcss.com/docs/line-height
	       */
	      leading: [{
	        leading: ['none', 'tight', 'snug', 'normal', 'relaxed', 'loose', isArbitraryValue, isLength]
	      }],
	      /**
	       * List Style Image
	       * @see https://tailwindcss.com/docs/list-style-image
	       */
	      'list-image': [{
	        'list-image': ['none', isArbitraryValue]
	      }],
	      /**
	       * List Style Type
	       * @see https://tailwindcss.com/docs/list-style-type
	       */
	      'list-style-type': [{
	        list: ['none', 'disc', 'decimal', isArbitraryValue]
	      }],
	      /**
	       * List Style Position
	       * @see https://tailwindcss.com/docs/list-style-position
	       */
	      'list-style-position': [{
	        list: ['inside', 'outside']
	      }],
	      /**
	       * Placeholder Color
	       * @deprecated since Tailwind CSS v3.0.0
	       * @see https://tailwindcss.com/docs/placeholder-color
	       */
	      'placeholder-color': [{
	        placeholder: [colors]
	      }],
	      /**
	       * Placeholder Opacity
	       * @see https://tailwindcss.com/docs/placeholder-opacity
	       */
	      'placeholder-opacity': [{
	        'placeholder-opacity': [opacity]
	      }],
	      /**
	       * Text Alignment
	       * @see https://tailwindcss.com/docs/text-align
	       */
	      'text-alignment': [{
	        text: ['left', 'center', 'right', 'justify', 'start', 'end']
	      }],
	      /**
	       * Text Color
	       * @see https://tailwindcss.com/docs/text-color
	       */
	      'text-color': [{
	        text: [colors]
	      }],
	      /**
	       * Text Opacity
	       * @see https://tailwindcss.com/docs/text-opacity
	       */
	      'text-opacity': [{
	        'text-opacity': [opacity]
	      }],
	      /**
	       * Text Decoration
	       * @see https://tailwindcss.com/docs/text-decoration
	       */
	      'text-decoration': ['underline', 'overline', 'line-through', 'no-underline'],
	      /**
	       * Text Decoration Style
	       * @see https://tailwindcss.com/docs/text-decoration-style
	       */
	      'text-decoration-style': [{
	        decoration: [].concat(getLineStyles(), ['wavy'])
	      }],
	      /**
	       * Text Decoration Thickness
	       * @see https://tailwindcss.com/docs/text-decoration-thickness
	       */
	      'text-decoration-thickness': [{
	        decoration: ['auto', 'from-font', isLength]
	      }],
	      /**
	       * Text Underline Offset
	       * @see https://tailwindcss.com/docs/text-underline-offset
	       */
	      'underline-offset': [{
	        'underline-offset': ['auto', isArbitraryValue, isLength]
	      }],
	      /**
	       * Text Decoration Color
	       * @see https://tailwindcss.com/docs/text-decoration-color
	       */
	      'text-decoration-color': [{
	        decoration: [colors]
	      }],
	      /**
	       * Text Transform
	       * @see https://tailwindcss.com/docs/text-transform
	       */
	      'text-transform': ['uppercase', 'lowercase', 'capitalize', 'normal-case'],
	      /**
	       * Text Overflow
	       * @see https://tailwindcss.com/docs/text-overflow
	       */
	      'text-overflow': ['truncate', 'text-ellipsis', 'text-clip'],
	      /**
	       * Text Indent
	       * @see https://tailwindcss.com/docs/text-indent
	       */
	      indent: [{
	        indent: getSpacingWithArbitrary()
	      }],
	      /**
	       * Vertical Alignment
	       * @see https://tailwindcss.com/docs/vertical-align
	       */
	      'vertical-align': [{
	        align: ['baseline', 'top', 'middle', 'bottom', 'text-top', 'text-bottom', 'sub', 'super', isArbitraryValue]
	      }],
	      /**
	       * Whitespace
	       * @see https://tailwindcss.com/docs/whitespace
	       */
	      whitespace: [{
	        whitespace: ['normal', 'nowrap', 'pre', 'pre-line', 'pre-wrap', 'break-spaces']
	      }],
	      /**
	       * Word Break
	       * @see https://tailwindcss.com/docs/word-break
	       */
	      "break": [{
	        "break": ['normal', 'words', 'all', 'keep']
	      }],
	      /**
	       * Hyphens
	       * @see https://tailwindcss.com/docs/hyphens
	       */
	      hyphens: [{
	        hyphens: ['none', 'manual', 'auto']
	      }],
	      /**
	       * Content
	       * @see https://tailwindcss.com/docs/content
	       */
	      content: [{
	        content: ['none', isArbitraryValue]
	      }],
	      // Backgrounds
	      /**
	       * Background Attachment
	       * @see https://tailwindcss.com/docs/background-attachment
	       */
	      'bg-attachment': [{
	        bg: ['fixed', 'local', 'scroll']
	      }],
	      /**
	       * Background Clip
	       * @see https://tailwindcss.com/docs/background-clip
	       */
	      'bg-clip': [{
	        'bg-clip': ['border', 'padding', 'content', 'text']
	      }],
	      /**
	       * Background Opacity
	       * @deprecated since Tailwind CSS v3.0.0
	       * @see https://tailwindcss.com/docs/background-opacity
	       */
	      'bg-opacity': [{
	        'bg-opacity': [opacity]
	      }],
	      /**
	       * Background Origin
	       * @see https://tailwindcss.com/docs/background-origin
	       */
	      'bg-origin': [{
	        'bg-origin': ['border', 'padding', 'content']
	      }],
	      /**
	       * Background Position
	       * @see https://tailwindcss.com/docs/background-position
	       */
	      'bg-position': [{
	        bg: [].concat(getPositions(), [isArbitraryPosition])
	      }],
	      /**
	       * Background Repeat
	       * @see https://tailwindcss.com/docs/background-repeat
	       */
	      'bg-repeat': [{
	        bg: ['no-repeat', {
	          repeat: ['', 'x', 'y', 'round', 'space']
	        }]
	      }],
	      /**
	       * Background Size
	       * @see https://tailwindcss.com/docs/background-size
	       */
	      'bg-size': [{
	        bg: ['auto', 'cover', 'contain', isArbitrarySize]
	      }],
	      /**
	       * Background Image
	       * @see https://tailwindcss.com/docs/background-image
	       */
	      'bg-image': [{
	        bg: ['none', {
	          'gradient-to': ['t', 'tr', 'r', 'br', 'b', 'bl', 'l', 'tl']
	        }, isArbitraryUrl]
	      }],
	      /**
	       * Background Color
	       * @see https://tailwindcss.com/docs/background-color
	       */
	      'bg-color': [{
	        bg: [colors]
	      }],
	      /**
	       * Gradient Color Stops From Position
	       * @see https://tailwindcss.com/docs/gradient-color-stops
	       */
	      'gradient-from-pos': [{
	        from: [gradientColorStopPositions]
	      }],
	      /**
	       * Gradient Color Stops Via Position
	       * @see https://tailwindcss.com/docs/gradient-color-stops
	       */
	      'gradient-via-pos': [{
	        via: [gradientColorStopPositions]
	      }],
	      /**
	       * Gradient Color Stops To Position
	       * @see https://tailwindcss.com/docs/gradient-color-stops
	       */
	      'gradient-to-pos': [{
	        to: [gradientColorStopPositions]
	      }],
	      /**
	       * Gradient Color Stops From
	       * @see https://tailwindcss.com/docs/gradient-color-stops
	       */
	      'gradient-from': [{
	        from: [gradientColorStops]
	      }],
	      /**
	       * Gradient Color Stops Via
	       * @see https://tailwindcss.com/docs/gradient-color-stops
	       */
	      'gradient-via': [{
	        via: [gradientColorStops]
	      }],
	      /**
	       * Gradient Color Stops To
	       * @see https://tailwindcss.com/docs/gradient-color-stops
	       */
	      'gradient-to': [{
	        to: [gradientColorStops]
	      }],
	      // Borders
	      /**
	       * Border Radius
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      rounded: [{
	        rounded: [borderRadius]
	      }],
	      /**
	       * Border Radius Start
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-s': [{
	        'rounded-s': [borderRadius]
	      }],
	      /**
	       * Border Radius End
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-e': [{
	        'rounded-e': [borderRadius]
	      }],
	      /**
	       * Border Radius Top
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-t': [{
	        'rounded-t': [borderRadius]
	      }],
	      /**
	       * Border Radius Right
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-r': [{
	        'rounded-r': [borderRadius]
	      }],
	      /**
	       * Border Radius Bottom
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-b': [{
	        'rounded-b': [borderRadius]
	      }],
	      /**
	       * Border Radius Left
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-l': [{
	        'rounded-l': [borderRadius]
	      }],
	      /**
	       * Border Radius Start Start
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-ss': [{
	        'rounded-ss': [borderRadius]
	      }],
	      /**
	       * Border Radius Start End
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-se': [{
	        'rounded-se': [borderRadius]
	      }],
	      /**
	       * Border Radius End End
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-ee': [{
	        'rounded-ee': [borderRadius]
	      }],
	      /**
	       * Border Radius End Start
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-es': [{
	        'rounded-es': [borderRadius]
	      }],
	      /**
	       * Border Radius Top Left
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-tl': [{
	        'rounded-tl': [borderRadius]
	      }],
	      /**
	       * Border Radius Top Right
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-tr': [{
	        'rounded-tr': [borderRadius]
	      }],
	      /**
	       * Border Radius Bottom Right
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-br': [{
	        'rounded-br': [borderRadius]
	      }],
	      /**
	       * Border Radius Bottom Left
	       * @see https://tailwindcss.com/docs/border-radius
	       */
	      'rounded-bl': [{
	        'rounded-bl': [borderRadius]
	      }],
	      /**
	       * Border Width
	       * @see https://tailwindcss.com/docs/border-width
	       */
	      'border-w': [{
	        border: [borderWidth]
	      }],
	      /**
	       * Border Width X
	       * @see https://tailwindcss.com/docs/border-width
	       */
	      'border-w-x': [{
	        'border-x': [borderWidth]
	      }],
	      /**
	       * Border Width Y
	       * @see https://tailwindcss.com/docs/border-width
	       */
	      'border-w-y': [{
	        'border-y': [borderWidth]
	      }],
	      /**
	       * Border Width Start
	       * @see https://tailwindcss.com/docs/border-width
	       */
	      'border-w-s': [{
	        'border-s': [borderWidth]
	      }],
	      /**
	       * Border Width End
	       * @see https://tailwindcss.com/docs/border-width
	       */
	      'border-w-e': [{
	        'border-e': [borderWidth]
	      }],
	      /**
	       * Border Width Top
	       * @see https://tailwindcss.com/docs/border-width
	       */
	      'border-w-t': [{
	        'border-t': [borderWidth]
	      }],
	      /**
	       * Border Width Right
	       * @see https://tailwindcss.com/docs/border-width
	       */
	      'border-w-r': [{
	        'border-r': [borderWidth]
	      }],
	      /**
	       * Border Width Bottom
	       * @see https://tailwindcss.com/docs/border-width
	       */
	      'border-w-b': [{
	        'border-b': [borderWidth]
	      }],
	      /**
	       * Border Width Left
	       * @see https://tailwindcss.com/docs/border-width
	       */
	      'border-w-l': [{
	        'border-l': [borderWidth]
	      }],
	      /**
	       * Border Opacity
	       * @see https://tailwindcss.com/docs/border-opacity
	       */
	      'border-opacity': [{
	        'border-opacity': [opacity]
	      }],
	      /**
	       * Border Style
	       * @see https://tailwindcss.com/docs/border-style
	       */
	      'border-style': [{
	        border: [].concat(getLineStyles(), ['hidden'])
	      }],
	      /**
	       * Divide Width X
	       * @see https://tailwindcss.com/docs/divide-width
	       */
	      'divide-x': [{
	        'divide-x': [borderWidth]
	      }],
	      /**
	       * Divide Width X Reverse
	       * @see https://tailwindcss.com/docs/divide-width
	       */
	      'divide-x-reverse': ['divide-x-reverse'],
	      /**
	       * Divide Width Y
	       * @see https://tailwindcss.com/docs/divide-width
	       */
	      'divide-y': [{
	        'divide-y': [borderWidth]
	      }],
	      /**
	       * Divide Width Y Reverse
	       * @see https://tailwindcss.com/docs/divide-width
	       */
	      'divide-y-reverse': ['divide-y-reverse'],
	      /**
	       * Divide Opacity
	       * @see https://tailwindcss.com/docs/divide-opacity
	       */
	      'divide-opacity': [{
	        'divide-opacity': [opacity]
	      }],
	      /**
	       * Divide Style
	       * @see https://tailwindcss.com/docs/divide-style
	       */
	      'divide-style': [{
	        divide: getLineStyles()
	      }],
	      /**
	       * Border Color
	       * @see https://tailwindcss.com/docs/border-color
	       */
	      'border-color': [{
	        border: [borderColor]
	      }],
	      /**
	       * Border Color X
	       * @see https://tailwindcss.com/docs/border-color
	       */
	      'border-color-x': [{
	        'border-x': [borderColor]
	      }],
	      /**
	       * Border Color Y
	       * @see https://tailwindcss.com/docs/border-color
	       */
	      'border-color-y': [{
	        'border-y': [borderColor]
	      }],
	      /**
	       * Border Color Top
	       * @see https://tailwindcss.com/docs/border-color
	       */
	      'border-color-t': [{
	        'border-t': [borderColor]
	      }],
	      /**
	       * Border Color Right
	       * @see https://tailwindcss.com/docs/border-color
	       */
	      'border-color-r': [{
	        'border-r': [borderColor]
	      }],
	      /**
	       * Border Color Bottom
	       * @see https://tailwindcss.com/docs/border-color
	       */
	      'border-color-b': [{
	        'border-b': [borderColor]
	      }],
	      /**
	       * Border Color Left
	       * @see https://tailwindcss.com/docs/border-color
	       */
	      'border-color-l': [{
	        'border-l': [borderColor]
	      }],
	      /**
	       * Divide Color
	       * @see https://tailwindcss.com/docs/divide-color
	       */
	      'divide-color': [{
	        divide: [borderColor]
	      }],
	      /**
	       * Outline Style
	       * @see https://tailwindcss.com/docs/outline-style
	       */
	      'outline-style': [{
	        outline: [''].concat(getLineStyles())
	      }],
	      /**
	       * Outline Offset
	       * @see https://tailwindcss.com/docs/outline-offset
	       */
	      'outline-offset': [{
	        'outline-offset': [isArbitraryValue, isLength]
	      }],
	      /**
	       * Outline Width
	       * @see https://tailwindcss.com/docs/outline-width
	       */
	      'outline-w': [{
	        outline: [isLength]
	      }],
	      /**
	       * Outline Color
	       * @see https://tailwindcss.com/docs/outline-color
	       */
	      'outline-color': [{
	        outline: [colors]
	      }],
	      /**
	       * Ring Width
	       * @see https://tailwindcss.com/docs/ring-width
	       */
	      'ring-w': [{
	        ring: getLengthWithEmpty()
	      }],
	      /**
	       * Ring Width Inset
	       * @see https://tailwindcss.com/docs/ring-width
	       */
	      'ring-w-inset': ['ring-inset'],
	      /**
	       * Ring Color
	       * @see https://tailwindcss.com/docs/ring-color
	       */
	      'ring-color': [{
	        ring: [colors]
	      }],
	      /**
	       * Ring Opacity
	       * @see https://tailwindcss.com/docs/ring-opacity
	       */
	      'ring-opacity': [{
	        'ring-opacity': [opacity]
	      }],
	      /**
	       * Ring Offset Width
	       * @see https://tailwindcss.com/docs/ring-offset-width
	       */
	      'ring-offset-w': [{
	        'ring-offset': [isLength]
	      }],
	      /**
	       * Ring Offset Color
	       * @see https://tailwindcss.com/docs/ring-offset-color
	       */
	      'ring-offset-color': [{
	        'ring-offset': [colors]
	      }],
	      // Effects
	      /**
	       * Box Shadow
	       * @see https://tailwindcss.com/docs/box-shadow
	       */
	      shadow: [{
	        shadow: ['', 'inner', 'none', isTshirtSize, isArbitraryShadow]
	      }],
	      /**
	       * Box Shadow Color
	       * @see https://tailwindcss.com/docs/box-shadow-color
	       */
	      'shadow-color': [{
	        shadow: [isAny]
	      }],
	      /**
	       * Opacity
	       * @see https://tailwindcss.com/docs/opacity
	       */
	      opacity: [{
	        opacity: [opacity]
	      }],
	      /**
	       * Mix Blend Mode
	       * @see https://tailwindcss.com/docs/mix-blend-mode
	       */
	      'mix-blend': [{
	        'mix-blend': getBlendModes()
	      }],
	      /**
	       * Background Blend Mode
	       * @see https://tailwindcss.com/docs/background-blend-mode
	       */
	      'bg-blend': [{
	        'bg-blend': getBlendModes()
	      }],
	      // Filters
	      /**
	       * Filter
	       * @deprecated since Tailwind CSS v3.0.0
	       * @see https://tailwindcss.com/docs/filter
	       */
	      filter: [{
	        filter: ['', 'none']
	      }],
	      /**
	       * Blur
	       * @see https://tailwindcss.com/docs/blur
	       */
	      blur: [{
	        blur: [blur]
	      }],
	      /**
	       * Brightness
	       * @see https://tailwindcss.com/docs/brightness
	       */
	      brightness: [{
	        brightness: [brightness]
	      }],
	      /**
	       * Contrast
	       * @see https://tailwindcss.com/docs/contrast
	       */
	      contrast: [{
	        contrast: [contrast]
	      }],
	      /**
	       * Drop Shadow
	       * @see https://tailwindcss.com/docs/drop-shadow
	       */
	      'drop-shadow': [{
	        'drop-shadow': ['', 'none', isTshirtSize, isArbitraryValue]
	      }],
	      /**
	       * Grayscale
	       * @see https://tailwindcss.com/docs/grayscale
	       */
	      grayscale: [{
	        grayscale: [grayscale]
	      }],
	      /**
	       * Hue Rotate
	       * @see https://tailwindcss.com/docs/hue-rotate
	       */
	      'hue-rotate': [{
	        'hue-rotate': [hueRotate]
	      }],
	      /**
	       * Invert
	       * @see https://tailwindcss.com/docs/invert
	       */
	      invert: [{
	        invert: [invert]
	      }],
	      /**
	       * Saturate
	       * @see https://tailwindcss.com/docs/saturate
	       */
	      saturate: [{
	        saturate: [saturate]
	      }],
	      /**
	       * Sepia
	       * @see https://tailwindcss.com/docs/sepia
	       */
	      sepia: [{
	        sepia: [sepia]
	      }],
	      /**
	       * Backdrop Filter
	       * @deprecated since Tailwind CSS v3.0.0
	       * @see https://tailwindcss.com/docs/backdrop-filter
	       */
	      'backdrop-filter': [{
	        'backdrop-filter': ['', 'none']
	      }],
	      /**
	       * Backdrop Blur
	       * @see https://tailwindcss.com/docs/backdrop-blur
	       */
	      'backdrop-blur': [{
	        'backdrop-blur': [blur]
	      }],
	      /**
	       * Backdrop Brightness
	       * @see https://tailwindcss.com/docs/backdrop-brightness
	       */
	      'backdrop-brightness': [{
	        'backdrop-brightness': [brightness]
	      }],
	      /**
	       * Backdrop Contrast
	       * @see https://tailwindcss.com/docs/backdrop-contrast
	       */
	      'backdrop-contrast': [{
	        'backdrop-contrast': [contrast]
	      }],
	      /**
	       * Backdrop Grayscale
	       * @see https://tailwindcss.com/docs/backdrop-grayscale
	       */
	      'backdrop-grayscale': [{
	        'backdrop-grayscale': [grayscale]
	      }],
	      /**
	       * Backdrop Hue Rotate
	       * @see https://tailwindcss.com/docs/backdrop-hue-rotate
	       */
	      'backdrop-hue-rotate': [{
	        'backdrop-hue-rotate': [hueRotate]
	      }],
	      /**
	       * Backdrop Invert
	       * @see https://tailwindcss.com/docs/backdrop-invert
	       */
	      'backdrop-invert': [{
	        'backdrop-invert': [invert]
	      }],
	      /**
	       * Backdrop Opacity
	       * @see https://tailwindcss.com/docs/backdrop-opacity
	       */
	      'backdrop-opacity': [{
	        'backdrop-opacity': [opacity]
	      }],
	      /**
	       * Backdrop Saturate
	       * @see https://tailwindcss.com/docs/backdrop-saturate
	       */
	      'backdrop-saturate': [{
	        'backdrop-saturate': [saturate]
	      }],
	      /**
	       * Backdrop Sepia
	       * @see https://tailwindcss.com/docs/backdrop-sepia
	       */
	      'backdrop-sepia': [{
	        'backdrop-sepia': [sepia]
	      }],
	      // Tables
	      /**
	       * Border Collapse
	       * @see https://tailwindcss.com/docs/border-collapse
	       */
	      'border-collapse': [{
	        border: ['collapse', 'separate']
	      }],
	      /**
	       * Border Spacing
	       * @see https://tailwindcss.com/docs/border-spacing
	       */
	      'border-spacing': [{
	        'border-spacing': [borderSpacing]
	      }],
	      /**
	       * Border Spacing X
	       * @see https://tailwindcss.com/docs/border-spacing
	       */
	      'border-spacing-x': [{
	        'border-spacing-x': [borderSpacing]
	      }],
	      /**
	       * Border Spacing Y
	       * @see https://tailwindcss.com/docs/border-spacing
	       */
	      'border-spacing-y': [{
	        'border-spacing-y': [borderSpacing]
	      }],
	      /**
	       * Table Layout
	       * @see https://tailwindcss.com/docs/table-layout
	       */
	      'table-layout': [{
	        table: ['auto', 'fixed']
	      }],
	      /**
	       * Caption Side
	       * @see https://tailwindcss.com/docs/caption-side
	       */
	      caption: [{
	        caption: ['top', 'bottom']
	      }],
	      // Transitions and Animation
	      /**
	       * Tranisition Property
	       * @see https://tailwindcss.com/docs/transition-property
	       */
	      transition: [{
	        transition: ['none', 'all', '', 'colors', 'opacity', 'shadow', 'transform', isArbitraryValue]
	      }],
	      /**
	       * Transition Duration
	       * @see https://tailwindcss.com/docs/transition-duration
	       */
	      duration: [{
	        duration: getNumberAndArbitrary()
	      }],
	      /**
	       * Transition Timing Function
	       * @see https://tailwindcss.com/docs/transition-timing-function
	       */
	      ease: [{
	        ease: ['linear', 'in', 'out', 'in-out', isArbitraryValue]
	      }],
	      /**
	       * Transition Delay
	       * @see https://tailwindcss.com/docs/transition-delay
	       */
	      delay: [{
	        delay: getNumberAndArbitrary()
	      }],
	      /**
	       * Animation
	       * @see https://tailwindcss.com/docs/animation
	       */
	      animate: [{
	        animate: ['none', 'spin', 'ping', 'pulse', 'bounce', isArbitraryValue]
	      }],
	      // Transforms
	      /**
	       * Transform
	       * @see https://tailwindcss.com/docs/transform
	       */
	      transform: [{
	        transform: ['', 'gpu', 'none']
	      }],
	      /**
	       * Scale
	       * @see https://tailwindcss.com/docs/scale
	       */
	      scale: [{
	        scale: [scale]
	      }],
	      /**
	       * Scale X
	       * @see https://tailwindcss.com/docs/scale
	       */
	      'scale-x': [{
	        'scale-x': [scale]
	      }],
	      /**
	       * Scale Y
	       * @see https://tailwindcss.com/docs/scale
	       */
	      'scale-y': [{
	        'scale-y': [scale]
	      }],
	      /**
	       * Rotate
	       * @see https://tailwindcss.com/docs/rotate
	       */
	      rotate: [{
	        rotate: [isInteger, isArbitraryValue]
	      }],
	      /**
	       * Translate X
	       * @see https://tailwindcss.com/docs/translate
	       */
	      'translate-x': [{
	        'translate-x': [translate]
	      }],
	      /**
	       * Translate Y
	       * @see https://tailwindcss.com/docs/translate
	       */
	      'translate-y': [{
	        'translate-y': [translate]
	      }],
	      /**
	       * Skew X
	       * @see https://tailwindcss.com/docs/skew
	       */
	      'skew-x': [{
	        'skew-x': [skew]
	      }],
	      /**
	       * Skew Y
	       * @see https://tailwindcss.com/docs/skew
	       */
	      'skew-y': [{
	        'skew-y': [skew]
	      }],
	      /**
	       * Transform Origin
	       * @see https://tailwindcss.com/docs/transform-origin
	       */
	      'transform-origin': [{
	        origin: ['center', 'top', 'top-right', 'right', 'bottom-right', 'bottom', 'bottom-left', 'left', 'top-left', isArbitraryValue]
	      }],
	      // Interactivity
	      /**
	       * Accent Color
	       * @see https://tailwindcss.com/docs/accent-color
	       */
	      accent: [{
	        accent: ['auto', colors]
	      }],
	      /**
	       * Appearance
	       * @see https://tailwindcss.com/docs/appearance
	       */
	      appearance: ['appearance-none'],
	      /**
	       * Cursor
	       * @see https://tailwindcss.com/docs/cursor
	       */
	      cursor: [{
	        cursor: ['auto', 'default', 'pointer', 'wait', 'text', 'move', 'help', 'not-allowed', 'none', 'context-menu', 'progress', 'cell', 'crosshair', 'vertical-text', 'alias', 'copy', 'no-drop', 'grab', 'grabbing', 'all-scroll', 'col-resize', 'row-resize', 'n-resize', 'e-resize', 's-resize', 'w-resize', 'ne-resize', 'nw-resize', 'se-resize', 'sw-resize', 'ew-resize', 'ns-resize', 'nesw-resize', 'nwse-resize', 'zoom-in', 'zoom-out', isArbitraryValue]
	      }],
	      /**
	       * Caret Color
	       * @see https://tailwindcss.com/docs/just-in-time-mode#caret-color-utilities
	       */
	      'caret-color': [{
	        caret: [colors]
	      }],
	      /**
	       * Pointer Events
	       * @see https://tailwindcss.com/docs/pointer-events
	       */
	      'pointer-events': [{
	        'pointer-events': ['none', 'auto']
	      }],
	      /**
	       * Resize
	       * @see https://tailwindcss.com/docs/resize
	       */
	      resize: [{
	        resize: ['none', 'y', 'x', '']
	      }],
	      /**
	       * Scroll Behavior
	       * @see https://tailwindcss.com/docs/scroll-behavior
	       */
	      'scroll-behavior': [{
	        scroll: ['auto', 'smooth']
	      }],
	      /**
	       * Scroll Margin
	       * @see https://tailwindcss.com/docs/scroll-margin
	       */
	      'scroll-m': [{
	        'scroll-m': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Margin X
	       * @see https://tailwindcss.com/docs/scroll-margin
	       */
	      'scroll-mx': [{
	        'scroll-mx': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Margin Y
	       * @see https://tailwindcss.com/docs/scroll-margin
	       */
	      'scroll-my': [{
	        'scroll-my': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Margin Start
	       * @see https://tailwindcss.com/docs/scroll-margin
	       */
	      'scroll-ms': [{
	        'scroll-ms': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Margin End
	       * @see https://tailwindcss.com/docs/scroll-margin
	       */
	      'scroll-me': [{
	        'scroll-me': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Margin Top
	       * @see https://tailwindcss.com/docs/scroll-margin
	       */
	      'scroll-mt': [{
	        'scroll-mt': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Margin Right
	       * @see https://tailwindcss.com/docs/scroll-margin
	       */
	      'scroll-mr': [{
	        'scroll-mr': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Margin Bottom
	       * @see https://tailwindcss.com/docs/scroll-margin
	       */
	      'scroll-mb': [{
	        'scroll-mb': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Margin Left
	       * @see https://tailwindcss.com/docs/scroll-margin
	       */
	      'scroll-ml': [{
	        'scroll-ml': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Padding
	       * @see https://tailwindcss.com/docs/scroll-padding
	       */
	      'scroll-p': [{
	        'scroll-p': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Padding X
	       * @see https://tailwindcss.com/docs/scroll-padding
	       */
	      'scroll-px': [{
	        'scroll-px': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Padding Y
	       * @see https://tailwindcss.com/docs/scroll-padding
	       */
	      'scroll-py': [{
	        'scroll-py': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Padding Start
	       * @see https://tailwindcss.com/docs/scroll-padding
	       */
	      'scroll-ps': [{
	        'scroll-ps': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Padding End
	       * @see https://tailwindcss.com/docs/scroll-padding
	       */
	      'scroll-pe': [{
	        'scroll-pe': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Padding Top
	       * @see https://tailwindcss.com/docs/scroll-padding
	       */
	      'scroll-pt': [{
	        'scroll-pt': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Padding Right
	       * @see https://tailwindcss.com/docs/scroll-padding
	       */
	      'scroll-pr': [{
	        'scroll-pr': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Padding Bottom
	       * @see https://tailwindcss.com/docs/scroll-padding
	       */
	      'scroll-pb': [{
	        'scroll-pb': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Padding Left
	       * @see https://tailwindcss.com/docs/scroll-padding
	       */
	      'scroll-pl': [{
	        'scroll-pl': getSpacingWithArbitrary()
	      }],
	      /**
	       * Scroll Snap Align
	       * @see https://tailwindcss.com/docs/scroll-snap-align
	       */
	      'snap-align': [{
	        snap: ['start', 'end', 'center', 'align-none']
	      }],
	      /**
	       * Scroll Snap Stop
	       * @see https://tailwindcss.com/docs/scroll-snap-stop
	       */
	      'snap-stop': [{
	        snap: ['normal', 'always']
	      }],
	      /**
	       * Scroll Snap Type
	       * @see https://tailwindcss.com/docs/scroll-snap-type
	       */
	      'snap-type': [{
	        snap: ['none', 'x', 'y', 'both']
	      }],
	      /**
	       * Scroll Snap Type Strictness
	       * @see https://tailwindcss.com/docs/scroll-snap-type
	       */
	      'snap-strictness': [{
	        snap: ['mandatory', 'proximity']
	      }],
	      /**
	       * Touch Action
	       * @see https://tailwindcss.com/docs/touch-action
	       */
	      touch: [{
	        touch: ['auto', 'none', 'pinch-zoom', 'manipulation', {
	          pan: ['x', 'left', 'right', 'y', 'up', 'down']
	        }]
	      }],
	      /**
	       * User Select
	       * @see https://tailwindcss.com/docs/user-select
	       */
	      select: [{
	        select: ['none', 'text', 'all', 'auto']
	      }],
	      /**
	       * Will Change
	       * @see https://tailwindcss.com/docs/will-change
	       */
	      'will-change': [{
	        'will-change': ['auto', 'scroll', 'contents', 'transform', isArbitraryValue]
	      }],
	      // SVG
	      /**
	       * Fill
	       * @see https://tailwindcss.com/docs/fill
	       */
	      fill: [{
	        fill: [colors, 'none']
	      }],
	      /**
	       * Stroke Width
	       * @see https://tailwindcss.com/docs/stroke-width
	       */
	      'stroke-w': [{
	        stroke: [isLength, isArbitraryNumber]
	      }],
	      /**
	       * Stroke
	       * @see https://tailwindcss.com/docs/stroke
	       */
	      stroke: [{
	        stroke: [colors, 'none']
	      }],
	      // Accessibility
	      /**
	       * Screen Readers
	       * @see https://tailwindcss.com/docs/screen-readers
	       */
	      sr: ['sr-only', 'not-sr-only']
	    },
	    conflictingClassGroups: {
	      overflow: ['overflow-x', 'overflow-y'],
	      overscroll: ['overscroll-x', 'overscroll-y'],
	      inset: ['inset-x', 'inset-y', 'start', 'end', 'top', 'right', 'bottom', 'left'],
	      'inset-x': ['right', 'left'],
	      'inset-y': ['top', 'bottom'],
	      flex: ['basis', 'grow', 'shrink'],
	      gap: ['gap-x', 'gap-y'],
	      p: ['px', 'py', 'ps', 'pe', 'pt', 'pr', 'pb', 'pl'],
	      px: ['pr', 'pl'],
	      py: ['pt', 'pb'],
	      m: ['mx', 'my', 'ms', 'me', 'mt', 'mr', 'mb', 'ml'],
	      mx: ['mr', 'ml'],
	      my: ['mt', 'mb'],
	      'font-size': ['leading'],
	      'fvn-normal': ['fvn-ordinal', 'fvn-slashed-zero', 'fvn-figure', 'fvn-spacing', 'fvn-fraction'],
	      'fvn-ordinal': ['fvn-normal'],
	      'fvn-slashed-zero': ['fvn-normal'],
	      'fvn-figure': ['fvn-normal'],
	      'fvn-spacing': ['fvn-normal'],
	      'fvn-fraction': ['fvn-normal'],
	      rounded: ['rounded-s', 'rounded-e', 'rounded-t', 'rounded-r', 'rounded-b', 'rounded-l', 'rounded-ss', 'rounded-se', 'rounded-ee', 'rounded-es', 'rounded-tl', 'rounded-tr', 'rounded-br', 'rounded-bl'],
	      'rounded-s': ['rounded-ss', 'rounded-es'],
	      'rounded-e': ['rounded-se', 'rounded-ee'],
	      'rounded-t': ['rounded-tl', 'rounded-tr'],
	      'rounded-r': ['rounded-tr', 'rounded-br'],
	      'rounded-b': ['rounded-br', 'rounded-bl'],
	      'rounded-l': ['rounded-tl', 'rounded-bl'],
	      'border-spacing': ['border-spacing-x', 'border-spacing-y'],
	      'border-w': ['border-w-s', 'border-w-e', 'border-w-t', 'border-w-r', 'border-w-b', 'border-w-l'],
	      'border-w-x': ['border-w-r', 'border-w-l'],
	      'border-w-y': ['border-w-t', 'border-w-b'],
	      'border-color': ['border-color-t', 'border-color-r', 'border-color-b', 'border-color-l'],
	      'border-color-x': ['border-color-r', 'border-color-l'],
	      'border-color-y': ['border-color-t', 'border-color-b'],
	      'scroll-m': ['scroll-mx', 'scroll-my', 'scroll-ms', 'scroll-me', 'scroll-mt', 'scroll-mr', 'scroll-mb', 'scroll-ml'],
	      'scroll-mx': ['scroll-mr', 'scroll-ml'],
	      'scroll-my': ['scroll-mt', 'scroll-mb'],
	      'scroll-p': ['scroll-px', 'scroll-py', 'scroll-ps', 'scroll-pe', 'scroll-pt', 'scroll-pr', 'scroll-pb', 'scroll-pl'],
	      'scroll-px': ['scroll-pr', 'scroll-pl'],
	      'scroll-py': ['scroll-pt', 'scroll-pb']
	    },
	    conflictingClassGroupModifiers: {
	      'font-size': ['leading']
	    }
	  };
	}

	/**
	 * @param baseConfig Config where other config will be merged into. This object will be mutated.
	 * @param configExtension Partial config to merge into the `baseConfig`.
	 */
	function mergeConfigs(baseConfig, configExtension) {
	  for (var key in configExtension) {
	    mergePropertyRecursively(baseConfig, key, configExtension[key]);
	  }
	  return baseConfig;
	}
	var hasOwnProperty = Object.prototype.hasOwnProperty;
	var overrideTypes = /*#__PURE__*/new Set(['string', 'number', 'boolean']);
	function mergePropertyRecursively(baseObject, mergeKey, mergeValue) {
	  if (!hasOwnProperty.call(baseObject, mergeKey) || overrideTypes.has(typeof mergeValue) || mergeValue === null) {
	    baseObject[mergeKey] = mergeValue;
	    return;
	  }
	  if (Array.isArray(mergeValue) && Array.isArray(baseObject[mergeKey])) {
	    baseObject[mergeKey] = baseObject[mergeKey].concat(mergeValue);
	    return;
	  }
	  if (typeof mergeValue === 'object' && typeof baseObject[mergeKey] === 'object') {
	    if (baseObject[mergeKey] === null) {
	      baseObject[mergeKey] = mergeValue;
	      return;
	    }
	    for (var nextKey in mergeValue) {
	      mergePropertyRecursively(baseObject[mergeKey], nextKey, mergeValue[nextKey]);
	    }
	  }
	}

	function extendTailwindMerge(configExtension) {
	  for (var _len = arguments.length, createConfig = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
	    createConfig[_key - 1] = arguments[_key];
	  }
	  return typeof configExtension === 'function' ? createTailwindMerge.apply(void 0, [getDefaultConfig, configExtension].concat(createConfig)) : createTailwindMerge.apply(void 0, [function () {
	    return mergeConfigs(getDefaultConfig(), configExtension);
	  }].concat(createConfig));
	}

	var twMerge = /*#__PURE__*/createTailwindMerge(getDefaultConfig);

	/**
	 * @deprecated Will be removed in next major version. Use `twJoin` instead.
	 */
	var join = twJoin;

	tailwindMerge_cjs_development.createTailwindMerge = createTailwindMerge;
	tailwindMerge_cjs_development.extendTailwindMerge = extendTailwindMerge;
	tailwindMerge_cjs_development.fromTheme = fromTheme;
	tailwindMerge_cjs_development.getDefaultConfig = getDefaultConfig;
	tailwindMerge_cjs_development.join = join;
	tailwindMerge_cjs_development.mergeConfigs = mergeConfigs;
	tailwindMerge_cjs_development.twJoin = twJoin;
	tailwindMerge_cjs_development.twMerge = twMerge;
	tailwindMerge_cjs_development.validators = validators;
	
	return tailwindMerge_cjs_development;
}

(function (module) {

	if (process.env.NODE_ENV === 'production') {
	  module.exports = requireTailwindMerge_cjs_production_min();
	} else {
	  module.exports = requireTailwindMerge_cjs_development();
	}
} (dist));

(function (exports) {
	var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
	    return (mod && mod.__esModule) ? mod : { "default": mod };
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.cleanTemplate = exports.mergeArrays = void 0;
	const react_1 = __importDefault(React__default);
	const domElements_1 = __importDefault(domElements);
	const tailwind_merge_1 = distExports;
	const isTwElement = Symbol("isTwElement?");
	const mergeArrays = (template, templateElements) => {
	    return template.reduce((acc, c, i) => acc.concat(c || [], templateElements[i] || []), []);
	};
	exports.mergeArrays = mergeArrays;
	const cleanTemplate = (template, inheritedClasses = "") => {
	    const newClasses = template
	        .join(" ")
	        .trim()
	        .replace(/\n/g, " ")
	        .replace(/\s{2,}/g, " ")
	        .split(" ")
	        .filter((c) => c !== ",");
	    const inheritedClassesArray = inheritedClasses ? inheritedClasses.split(" ") : [];
	    return (0, tailwind_merge_1.twMerge)(...newClasses
	        .concat(inheritedClassesArray)
	        .filter((c) => c !== " "));
	};
	exports.cleanTemplate = cleanTemplate;
	const removeTransientProps = ([key]) => key.charAt(0) !== "$";
	const templateFunctionFactory = (Element) => {
	    return (template, ...templateElements) => {
	        const TwComponent = react_1.default.forwardRef(({ $as, ...props }, ref) => {
	            const FinalElement = $as || Element;
	            const filteredProps = FinalElement[isTwElement] === true
	                ? props
	                : Object.fromEntries(Object.entries(props).filter(removeTransientProps));
	            return (react_1.default.createElement(FinalElement, { ...filteredProps, ref: ref, className: (0, exports.cleanTemplate)((0, exports.mergeArrays)(template, templateElements.map((t) => t({ ...props, $as }))), props.className) }));
	        });
	        TwComponent[isTwElement] = true;
	        if (typeof Element !== "string") {
	            TwComponent.displayName = Element.displayName || Element.name || "tw.Component";
	        }
	        else {
	            TwComponent.displayName = "tw." + Element;
	        }
	        return TwComponent;
	    };
	};
	const intrinsicElementsMap = domElements_1.default.reduce((acc, DomElement) => ({
	    ...acc,
	    [DomElement]: templateFunctionFactory(DomElement)
	}), {});
	const tw = Object.assign(templateFunctionFactory, intrinsicElementsMap);
	exports.default = tw;
} (tailwind));

var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(dist$1, "__esModule", { value: true });
var _default$1 = dist$1.default = void 0;
const tailwind_1 = __importDefault(tailwind);
_default$1 = dist$1.default = tailwind_1.default;

const AvatarVariantStyles = {
    circular: 'rounded-full',
    rounded: 'rounded-md',
};
const AvatarSizesStyle = {
    4: 'h-4 w-4 text-xs',
    6: 'h-6 w-6 text-xs',
    8: 'h-8 w-8 text-sm',
    10: 'h-10 w-10 text-medium',
    12: 'h-12 w-12 text-lg',
    14: 'h-14 w-14 text-xl',
    16: 'h-16 w-16 text-2xl',
};
const StyledImgAvatar = _default$1.img `
  ${({ $variant }) => AvatarVariantStyles[$variant]}
  ${({ $size }) => AvatarSizesStyle[$size]}
  inline-block
`;
const StyledImgAvatarStacked = _default$1.img `
  ${({ $variant }) => AvatarVariantStyles[$variant]}
  ${({ $size }) => AvatarSizesStyle[$size]}
  inline-block
  ring-2
  ring-white
`;
const StyledSpanAvatar = _default$1.span `
  ${({ $variant }) => AvatarVariantStyles[$variant]}
  ${({ $size }) => AvatarSizesStyle[$size]}
  inline-flex
  items-center
  justify-center
  bg-gray-500
  select-none
`;
const StyledSpanAvatarStacked = _default$1.span `
  ${({ $variant }) => AvatarVariantStyles[$variant]}
  ${({ $size }) => AvatarSizesStyle[$size]}
  select-none
  inline-flex
  items-center
  justify-center
  bg-gray-500
  ring-2
  ring-white
`;
const StyledSpanAvatarInitials = _default$1.span `
  ${({ $size }) => AvatarSizesStyle[$size]}
  font-medium
  leading-none
  text-white
`;
const StyledSvgAvatar = _default$1.svg `
  h-full
  w-full
  text-gray-300
`;

const avatarColorsMap = [
    'bg-orange-700',
    'bg-lemon-700',
    'bg-green-700',
    'bg-aqua-700',
    'bg-blue-700',
    'bg-avocado-700',
    'bg-lime-700',
];
const Avatar = ({ className, imgSrc, size = 10, stacked, text, variant = 'circular', }) => {
    const ImgAvatar = stacked ? StyledImgAvatarStacked : StyledImgAvatar;
    const SpanAvatar = stacked ? StyledSpanAvatarStacked : StyledSpanAvatar;
    if (imgSrc) {
        return React__default.createElement(ImgAvatar, { "$size": size, "$variant": variant, alt: "avatar", className: className, src: imgSrc });
    }
    else if (text) {
        const initials = getInitials(text, { maxInitials: 2 });
        const color = convertTextToColor(initials, avatarColorsMap);
        return (React__default.createElement(SpanAvatar, { "$size": size, "$variant": variant, className: cn(color, className) },
            React__default.createElement(StyledSpanAvatarInitials, null, initials)));
    }
    return (React__default.createElement(SpanAvatar, { "$size": size, "$variant": variant, className: cn('overflow-hidden bg-gray-100', className) },
        React__default.createElement(StyledSvgAvatar, { fill: "currentColor", viewBox: "0 0 24 24" },
            React__default.createElement("path", { d: "M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" }))));
};
const AvatarMemo = memo(Avatar);

let t$3=typeof window!="undefined"?useLayoutEffect$1:useEffect;

function s$7(e){let r=useRef(e);return t$3(()=>{r.current=e;},[e]),r}

function i$4(e,o){let[u,t]=useState(e),r=s$7(e);return t$3(()=>t(r.current),[r,t,...o]),u}

function o$5(){let a=[],i=[],n={enqueue(e){i.push(e);},addEventListener(e,t,r,s){return e.addEventListener(t,r,s),n.add(()=>e.removeEventListener(t,r,s))},requestAnimationFrame(...e){let t=requestAnimationFrame(...e);return n.add(()=>cancelAnimationFrame(t))},nextFrame(...e){return n.requestAnimationFrame(()=>n.requestAnimationFrame(...e))},setTimeout(...e){let t=setTimeout(...e);return n.add(()=>clearTimeout(t))},add(e){return a.push(e),()=>{let t=a.indexOf(e);if(t>=0){let[r]=a.splice(t,1);r();}}},dispose(){for(let e of a.splice(0))e();},async workQueue(){for(let e of i.splice(0))await e();}};return n}

function p$6(){let[e]=useState(o$5);return useEffect(()=>()=>e.dispose(),[e]),e}

let o$4=function(t){let e=s$7(t);return React__default.useCallback((...r)=>e.current(...r),[e])};

let r$4={serverHandoffComplete:!1};function a$3(){let[e,f]=useState(r$4.serverHandoffComplete);return useEffect(()=>{e!==!0&&f(!0);},[e]),useEffect(()=>{r$4.serverHandoffComplete===!1&&(r$4.serverHandoffComplete=!0);},[]),e}

var u$6;let l$3=0;function r$3(){return ++l$3}let I$1=(u$6=React__default.useId)!=null?u$6:function(){let n=a$3(),[e,o]=React__default.useState(n?r$3:null);return t$3(()=>{e===null&&o(r$3());},[e]),e!=null?""+e:void 0};

function u$5(r,n,...a){if(r in n){let e=n[r];return typeof e=="function"?e(...a):e}let t=new Error(`Tried to handle "${r}" but there is no handler defined. Only defined handlers are: ${Object.keys(n).map(e=>`"${e}"`).join(", ")}.`);throw Error.captureStackTrace&&Error.captureStackTrace(t,u$5),t}

function t$2(n){return typeof window=="undefined"?null:n instanceof Node?n.ownerDocument:n!=null&&n.hasOwnProperty("current")&&n.current instanceof Node?n.current.ownerDocument:document}

let f$6=["[contentEditable=true]","[tabindex]","a[href]","area[href]","button:not([disabled])","iframe","input:not([disabled])","select:not([disabled])","textarea:not([disabled])"].map(e=>`${e}:not([tabindex='-1'])`).join(",");var p$5=(o=>(o[o.First=1]="First",o[o.Previous=2]="Previous",o[o.Next=4]="Next",o[o.Last=8]="Last",o[o.WrapAround=16]="WrapAround",o[o.NoScroll=32]="NoScroll",o))(p$5||{}),L$4=(n=>(n[n.Error=0]="Error",n[n.Overflow=1]="Overflow",n[n.Success=2]="Success",n[n.Underflow=3]="Underflow",n))(L$4||{}),N$9=(t=>(t[t.Previous=-1]="Previous",t[t.Next=1]="Next",t))(N$9||{});function T$5(e=document.body){return e==null?[]:Array.from(e.querySelectorAll(f$6))}var b$5=(t=>(t[t.Strict=0]="Strict",t[t.Loose=1]="Loose",t))(b$5||{});function S$3(e,r=0){var t;return e===((t=t$2(e))==null?void 0:t.body)?!1:u$5(r,{[0](){return e.matches(f$6)},[1](){let l=e;for(;l!==null;){if(l.matches(f$6))return !0;l=l.parentElement;}return !1}})}function F$5(e){e==null||e.focus({preventScroll:!0});}let M$5=["textarea","input"].join(",");function h$3(e){var r,t;return (t=(r=e==null?void 0:e.matches)==null?void 0:r.call(e,M$5))!=null?t:!1}function v$4(e,r=t=>t){return e.slice().sort((t,l)=>{let n=r(t),i=r(l);if(n===null||i===null)return 0;let o=n.compareDocumentPosition(i);return o&Node.DOCUMENT_POSITION_FOLLOWING?-1:o&Node.DOCUMENT_POSITION_PRECEDING?1:0})}function H$4(e,r,t=!0){let l=Array.isArray(e)?e.length>0?e[0].ownerDocument:document:e.ownerDocument,n=Array.isArray(e)?t?v$4(e):e:T$5(e),i=l.activeElement,o=(()=>{if(r&5)return 1;if(r&10)return -1;throw new Error("Missing Focus.First, Focus.Previous, Focus.Next or Focus.Last")})(),d=(()=>{if(r&1)return 0;if(r&2)return Math.max(0,n.indexOf(i))-1;if(r&4)return Math.max(0,n.indexOf(i))+1;if(r&8)return n.length-1;throw new Error("Missing Focus.First, Focus.Previous, Focus.Next or Focus.Last")})(),m=r&32?{preventScroll:!0}:{},c=0,s=n.length,u;do{if(c>=s||c+s<=0)return 0;let a=d+c;if(r&16)a=(a+s)%s;else {if(a<0)return 3;if(a>=s)return 1}u=n[a],u==null||u.focus(m),c+=o;}while(u!==l.activeElement);return r&6&&h$3(u)&&u.select(),u.hasAttribute("tabindex")||u.setAttribute("tabindex","0"),2}

function s$6(e,r,n){let o=s$7(r);useEffect(()=>{function t(i){o.current(i);}return window.addEventListener(e,t,n),()=>window.removeEventListener(e,t,n)},[e,n]);}

function L$3(f,c,u=!0){let l=useRef(!1);useEffect(()=>{requestAnimationFrame(()=>{l.current=u;});},[u]);function i(t,o){if(!l.current||t.defaultPrevented)return;let E=function r(e){return typeof e=="function"?r(e()):Array.isArray(e)||e instanceof Set?e:[e]}(f),n=o(t);if(n!==null&&!!n.ownerDocument.documentElement.contains(n)){for(let r of E){if(r===null)continue;let e=r instanceof HTMLElement?r:r.current;if(e!=null&&e.contains(n))return}return !S$3(n,b$5.Loose)&&n.tabIndex!==-1&&t.preventDefault(),c(t,n)}}s$6("click",t=>i(t,o=>o.target),!0),s$6("blur",t=>i(t,()=>window.document.activeElement instanceof HTMLIFrameElement?window.document.activeElement:null),!0);}

function i$3(t){var n;if(t.type)return t.type;let e=(n=t.as)!=null?n:"button";if(typeof e=="string"&&e.toLowerCase()==="button")return "button"}function s$5(t,e){let[n,u]=useState(()=>i$3(t));return t$3(()=>{u(i$3(t));},[t.type,t.as]),t$3(()=>{n||!e.current||e.current instanceof HTMLButtonElement&&!e.current.hasAttribute("type")&&u("button");},[n,e]),n}

let u$4=Symbol();function T$4(t,n=!0){return Object.assign(t,{[u$4]:n})}function y$2(...t){let n=useRef(t);useEffect(()=>{n.current=t;},[t]);let c=o$4(e=>{for(let o of n.current)o!=null&&(typeof o=="function"?o(e):o.current=e);});return t.every(e=>e==null||(e==null?void 0:e[u$4]))?void 0:c}

function F$4({container:e,accept:t,walk:r,enabled:c=!0}){let o=useRef(t),l=useRef(r);useEffect(()=>{o.current=t,l.current=r;},[t,r]),t$3(()=>{if(!e||!c)return;let n=t$2(e);if(!n)return;let f=o.current,p=l.current,d=Object.assign(i=>f(i),{acceptNode:f}),u=n.createTreeWalker(e,NodeFilter.SHOW_ELEMENT,d,!1);for(;u.nextNode();)p(u.currentNode);},[e,c,o,l]);}

function f$5(r){throw new Error("Unexpected object: "+r)}var a$2=(e=>(e[e.First=0]="First",e[e.Previous=1]="Previous",e[e.Next=2]="Next",e[e.Last=3]="Last",e[e.Specific=4]="Specific",e[e.Nothing=5]="Nothing",e))(a$2||{});function x$7(r,n){let t=n.resolveItems();if(t.length<=0)return null;let l=n.resolveActiveIndex(),s=l!=null?l:-1,d=(()=>{switch(r.focus){case 0:return t.findIndex(e=>!n.resolveDisabled(e));case 1:{let e=t.slice().reverse().findIndex((i,c,u)=>s!==-1&&u.length-c-1>=s?!1:!n.resolveDisabled(i));return e===-1?e:t.length-1-e}case 2:return t.findIndex((e,i)=>i<=s?!1:!n.resolveDisabled(e));case 3:{let e=t.slice().reverse().findIndex(i=>!n.resolveDisabled(i));return e===-1?e:t.length-1-e}case 4:return t.findIndex(e=>n.resolveId(e)===r.id);case 5:return null;default:f$5(r);}})();return d===-1?l:d}

var x$6=(n=>(n[n.None=0]="None",n[n.RenderStrategy=1]="RenderStrategy",n[n.Static=2]="Static",n))(x$6||{}),R$1=(e=>(e[e.Unmount=0]="Unmount",e[e.Hidden=1]="Hidden",e))(R$1||{});function _$3({ourProps:r,theirProps:t,slot:e,defaultTag:n,features:a,visible:s=!0,name:l}){let o=y$1(t,r);if(s)return f$4(o,e,n,l);let d=a!=null?a:0;if(d&2){let{static:i=!1,...u}=o;if(i)return f$4(u,e,n,l)}if(d&1){let{unmount:i=!0,...u}=o;return u$5(i?0:1,{[0](){return null},[1](){return f$4({...u,hidden:!0,style:{display:"none"}},e,n,l)}})}return f$4(o,e,n,l)}function f$4(r,t={},e,n){let{as:a=e,children:s,refName:l="ref",...o}=m$2(r,["unmount","static"]),d=r.ref!==void 0?{[l]:r.ref}:{},i=typeof s=="function"?s(t):s;o.className&&typeof o.className=="function"&&(o.className=o.className(t));let u={};if(a===Fragment&&Object.keys(g$4(o)).length>0){if(!isValidElement$1(i)||Array.isArray(i)&&i.length>1)throw new Error(['Passing props on "Fragment"!',"",`The current component <${n} /> is rendering a "Fragment".`,"However we need to passthrough the following props:",Object.keys(o).map(p=>`  - ${p}`).join(`
`),"","You can apply a few solutions:",['Add an `as="..."` prop, to ensure that we render an actual element instead of a "Fragment".',"Render a single element as the child so that we can forward the props onto that element."].map(p=>`  - ${p}`).join(`
`)].join(`
`));return cloneElement(i,Object.assign({},y$1(i.props,g$4(m$2(o,["ref"]))),u,d))}return createElement$1(a,Object.assign({},m$2(o,["ref"]),a!==Fragment&&d,a!==Fragment&&u),i)}function y$1(...r){if(r.length===0)return {};if(r.length===1)return r[0];let t={},e={};for(let a of r)for(let s in a)s.startsWith("on")&&typeof a[s]=="function"?((e[s])!=null||(e[s]=[]),e[s].push(a[s])):t[s]=a[s];if(t.disabled||t["aria-disabled"])return Object.assign(t,Object.fromEntries(Object.keys(e).map(a=>[a,void 0])));for(let a in e)Object.assign(t,{[a](s,...l){let o=e[a];for(let d of o){if(s.defaultPrevented)return;d(s,...l);}}});return t}function H$3(r){var t;return Object.assign(forwardRef(r),{displayName:(t=r.displayName)!=null?t:r.name})}function g$4(r){let t=Object.assign({},r);for(let e in t)t[e]===void 0&&delete t[e];return t}function m$2(r,t=[]){let e=Object.assign({},r);for(let n of t)n in e&&delete e[n];return e}

function r$2(n){let e=n.parentElement,l=null;for(;e&&!(e instanceof HTMLFieldSetElement);)e instanceof HTMLLegendElement&&(l=e),e=e.parentElement;let t=(e==null?void 0:e.getAttribute("disabled"))==="";return t&&i$2(l)?!1:t}function i$2(n){if(!n)return !1;let e=n.previousElementSibling;for(;e!==null;){if(e instanceof HTMLLegendElement)return !1;e=e.previousElementSibling;}return !0}

function e$2(n={},r=null,t=[]){for(let[i,o]of Object.entries(n))f$3(t,s$4(r,i),o);return t}function s$4(n,r){return n?n+"["+r+"]":r}function f$3(n,r,t){if(Array.isArray(t))for(let[i,o]of t.entries())f$3(n,s$4(r,i.toString()),o);else t instanceof Date?n.push([r,t.toISOString()]):typeof t=="boolean"?n.push([r,t?"1":"0"]):typeof t=="string"?n.push([r,t]):typeof t=="number"?n.push([r,`${t}`]):t==null?n.push([r,""]):e$2(t,r,n);}function p$4(n){var t;let r=(t=n==null?void 0:n.form)!=null?t:n.closest("form");if(!!r){for(let i of r.elements)if(i.tagName==="INPUT"&&i.type==="submit"||i.tagName==="BUTTON"&&i.type==="submit"||i.nodeName==="INPUT"&&i.type==="image"){i.click();return}}}

let a$1="div";var s$3=(e=>(e[e.None=1]="None",e[e.Focusable=2]="Focusable",e[e.Hidden=4]="Hidden",e))(s$3||{});let h$2=H$3(function(t,o){let{features:e=1,...r}=t,d={ref:o,"aria-hidden":(e&2)===2?!0:void 0,style:{position:"absolute",width:1,height:1,padding:0,margin:-1,overflow:"hidden",clip:"rect(0, 0, 0, 0)",whiteSpace:"nowrap",borderWidth:"0",...(e&4)===4&&(e&2)!==2&&{display:"none"}}};return _$3({ourProps:d,theirProps:r,slot:{},defaultTag:a$1,name:"Hidden"})});

let o$3=createContext(null);o$3.displayName="OpenClosedContext";var p$3=(e=>(e[e.Open=0]="Open",e[e.Closed=1]="Closed",e))(p$3||{});function s$2(){return useContext(o$3)}function C$6({value:t,children:n}){return React__default.createElement(o$3.Provider,{value:t},n)}

var o$2=(r=>(r.Space=" ",r.Enter="Enter",r.Escape="Escape",r.Backspace="Backspace",r.Delete="Delete",r.ArrowLeft="ArrowLeft",r.ArrowUp="ArrowUp",r.ArrowRight="ArrowRight",r.ArrowDown="ArrowDown",r.Home="Home",r.End="End",r.PageUp="PageUp",r.PageDown="PageDown",r.Tab="Tab",r))(o$2||{});

var Se$3=(o=>(o[o.Open=0]="Open",o[o.Closed=1]="Closed",o))(Se$3||{}),Pe$1=(o=>(o[o.Single=0]="Single",o[o.Multi=1]="Multi",o))(Pe$1||{}),Ae$1=(o=>(o[o.Pointer=0]="Pointer",o[o.Other=1]="Other",o))(Ae$1||{}),Ie$2=(t=>(t[t.OpenCombobox=0]="OpenCombobox",t[t.CloseCombobox=1]="CloseCombobox",t[t.GoToOption=2]="GoToOption",t[t.RegisterOption=3]="RegisterOption",t[t.UnregisterOption=4]="UnregisterOption",t))(Ie$2||{});function q$2(n,a=o=>o){let o=n.activeOptionIndex!==null?n.options[n.activeOptionIndex]:null,e=v$4(a(n.options.slice()),t=>t.dataRef.current.domRef.current),i=o?e.indexOf(o):null;return i===-1&&(i=null),{options:e,activeOptionIndex:i}}let De$1={[1](n){return n.dataRef.current.disabled||n.comboboxState===1?n:{...n,activeOptionIndex:null,comboboxState:1}},[0](n){if(n.dataRef.current.disabled||n.comboboxState===0)return n;let a=n.activeOptionIndex,{isSelected:o}=n.dataRef.current,e=n.options.findIndex(i=>o(i.dataRef.current.value));return e!==-1&&(a=e),{...n,comboboxState:0,activeOptionIndex:a}},[2](n,a){var i;if(n.dataRef.current.disabled||n.dataRef.current.optionsRef.current&&!n.dataRef.current.optionsPropsRef.current.static&&n.comboboxState===1)return n;let o=q$2(n);if(o.activeOptionIndex===null){let t=o.options.findIndex(p=>!p.dataRef.current.disabled);t!==-1&&(o.activeOptionIndex=t);}let e=x$7(a,{resolveItems:()=>o.options,resolveActiveIndex:()=>o.activeOptionIndex,resolveId:t=>t.id,resolveDisabled:t=>t.dataRef.current.disabled});return {...n,...o,activeOptionIndex:e,activationTrigger:(i=a.trigger)!=null?i:1}},[3]:(n,a)=>{let o={id:a.id,dataRef:a.dataRef},e=q$2(n,t=>[...t,o]);n.activeOptionIndex===null&&n.dataRef.current.isSelected(a.dataRef.current.value)&&(e.activeOptionIndex=e.options.indexOf(o));let i={...n,...e,activationTrigger:1};return n.dataRef.current.__demoMode&&n.dataRef.current.value===void 0&&(i.activeOptionIndex=0),i},[4]:(n,a)=>{let o=q$2(n,e=>{let i=e.findIndex(t=>t.id===a.id);return i!==-1&&e.splice(i,1),e});return {...n,...o,activationTrigger:1}}},X$2=createContext(null);X$2.displayName="ComboboxActionsContext";function N$8(n){let a=useContext(X$2);if(a===null){let o=new Error(`<${n} /> is missing a parent <Combobox /> component.`);throw Error.captureStackTrace&&Error.captureStackTrace(o,N$8),o}return a}let z$5=createContext(null);z$5.displayName="ComboboxDataContext";function k$5(n){let a=useContext(z$5);if(a===null){let o=new Error(`<${n} /> is missing a parent <Combobox /> component.`);throw Error.captureStackTrace&&Error.captureStackTrace(o,k$5),o}return a}function Ee$1(n,a){return u$5(a.type,De$1,n,a)}let he$2=Fragment,Le$3=H$3(function(a,o){let{name:e,value:i,onChange:t,disabled:p=!1,__demoMode:b=!1,nullable:r=!1,multiple:u=!1,...C}=a,[f,R]=useReducer(Ee$1,{dataRef:createRef(),comboboxState:b?0:1,options:[],activeOptionIndex:null,activationTrigger:1}),g=useRef(!1),x=useRef({static:!1,hold:!1}),c=useRef({displayValue:void 0}),m=useRef(null),U=useRef(null),M=useRef(null),h=useRef(null),D=o$4((s,d)=>s===d),T=useCallback(s=>u$5(l.mode,{[1]:()=>i.some(d=>D(d,s)),[0]:()=>D(i,s)}),[i]),l=useMemo(()=>({...f,optionsPropsRef:x,inputPropsRef:c,labelRef:m,inputRef:U,buttonRef:M,optionsRef:h,value:i,disabled:p,mode:u?1:0,get activeOptionIndex(){if(g.current&&f.activeOptionIndex===null&&f.options.length>0){let s=f.options.findIndex(d=>!d.dataRef.current.disabled);if(s!==-1)return s}return f.activeOptionIndex},compare:D,isSelected:T,nullable:r,__demoMode:b}),[i,p,u,r,b,f]);t$3(()=>{f.dataRef.current=l;},[l]),L$3([l.buttonRef,l.inputRef,l.optionsRef],()=>R({type:1}),l.comboboxState===0);let j=useMemo(()=>({open:l.comboboxState===0,disabled:p,activeIndex:l.activeOptionIndex,activeOption:l.activeOptionIndex===null?null:l.options[l.activeOptionIndex].dataRef.current.value}),[l,p]),v=useCallback(()=>{var d;if(!l.inputRef.current)return;let s=c.current.displayValue;typeof s=="function"?l.inputRef.current.value=(d=s(i))!=null?d:"":typeof i=="string"?l.inputRef.current.value=i:l.inputRef.current.value="";},[i,l.inputRef,c]),P=o$4(s=>{let d=l.options.find(L=>L.id===s);!d||(K(d.dataRef.current.value),v());}),G=o$4(()=>{if(l.activeOptionIndex!==null){let{dataRef:s,id:d}=l.options[l.activeOptionIndex];K(s.current.value),v(),R({type:2,focus:a$2.Specific,id:d});}}),oe=o$4(()=>{R({type:0}),g.current=!0;}),ne=o$4(()=>{R({type:1}),g.current=!1;}),re=o$4((s,d,L)=>(g.current=!1,s===a$2.Specific?R({type:2,focus:a$2.Specific,id:d,trigger:L}):R({type:2,focus:s,trigger:L}))),ie=o$4((s,d)=>(R({type:3,id:s,dataRef:d}),()=>R({type:4,id:s}))),K=o$4(s=>u$5(l.mode,{[0](){return t(s)},[1](){let d=l.value.slice(),L=d.indexOf(s);return L===-1?d.push(s):d.splice(L,1),t(d)}})),ae=useMemo(()=>({onChange:K,registerOption:ie,goToOption:re,closeCombobox:ne,openCombobox:oe,selectActiveOption:G,selectOption:P}),[]);t$3(()=>{l.comboboxState===1&&v();},[v,l.comboboxState]),t$3(v,[v]);let le=o===null?{}:{ref:o};return React__default.createElement(X$2.Provider,{value:ae},React__default.createElement(z$5.Provider,{value:l},React__default.createElement(C$6,{value:u$5(l.comboboxState,{[0]:p$3.Open,[1]:p$3.Closed})},e!=null&&i!=null&&e$2({[e]:i}).map(([s,d])=>React__default.createElement(h$2,{features:s$3.Hidden,...g$4({key:s,as:"input",type:"hidden",hidden:!0,readOnly:!0,name:s,value:d})})),_$3({ourProps:le,theirProps:C,slot:j,defaultTag:he$2,name:"Combobox"}))))}),Me$3="input",_e$2=H$3(function(a,o){var h,D;let{value:e,onChange:i,displayValue:t,type:p="text",...b}=a,r=k$5("Combobox.Input"),u=N$8("Combobox.Input"),C=y$2(r.inputRef,o),f=r.inputPropsRef,R=`headlessui-combobox-input-${I$1()}`,g=p$6();t$3(()=>{f.current.displayValue=t;},[t,f]);let x=o$4(T=>{switch(T.key){case o$2.Backspace:case o$2.Delete:if(r.mode!==0||!r.nullable)return;let l=T.currentTarget;g.requestAnimationFrame(()=>{l.value===""&&(u.onChange(null),r.optionsRef.current&&(r.optionsRef.current.scrollTop=0),u.goToOption(a$2.Nothing));});break;case o$2.Enter:if(r.comboboxState!==0)return;if(T.preventDefault(),T.stopPropagation(),r.activeOptionIndex===null){u.closeCombobox();return}u.selectActiveOption(),r.mode===0&&u.closeCombobox();break;case o$2.ArrowDown:return T.preventDefault(),T.stopPropagation(),u$5(r.comboboxState,{[0]:()=>{u.goToOption(a$2.Next);},[1]:()=>{u.openCombobox();}});case o$2.ArrowUp:return T.preventDefault(),T.stopPropagation(),u$5(r.comboboxState,{[0]:()=>{u.goToOption(a$2.Previous);},[1]:()=>{u.openCombobox(),g.nextFrame(()=>{r.value||u.goToOption(a$2.Last);});}});case o$2.Home:case o$2.PageUp:return T.preventDefault(),T.stopPropagation(),u.goToOption(a$2.First);case o$2.End:case o$2.PageDown:return T.preventDefault(),T.stopPropagation(),u.goToOption(a$2.Last);case o$2.Escape:return r.comboboxState!==0?void 0:(T.preventDefault(),r.optionsRef.current&&!r.optionsPropsRef.current.static&&T.stopPropagation(),u.closeCombobox());case o$2.Tab:if(r.comboboxState!==0)return;u.selectActiveOption(),u.closeCombobox();break}}),c=o$4(T=>{u.openCombobox(),i==null||i(T);}),m=i$4(()=>{if(!!r.labelRef.current)return [r.labelRef.current.id].join(" ")},[r.labelRef.current]),U=useMemo(()=>({open:r.comboboxState===0,disabled:r.disabled}),[r]),M={ref:C,id:R,role:"combobox",type:p,"aria-controls":(h=r.optionsRef.current)==null?void 0:h.id,"aria-expanded":r.disabled?void 0:r.comboboxState===0,"aria-activedescendant":r.activeOptionIndex===null||(D=r.options[r.activeOptionIndex])==null?void 0:D.id,"aria-multiselectable":r.mode===1?!0:void 0,"aria-labelledby":m,disabled:r.disabled,onKeyDown:x,onChange:c};return _$3({ourProps:M,theirProps:b,slot:U,defaultTag:Me$3,name:"Combobox.Input"})}),Fe$2="button",we$2=H$3(function(a,o){var x;let e=k$5("Combobox.Button"),i=N$8("Combobox.Button"),t=y$2(e.buttonRef,o),p=`headlessui-combobox-button-${I$1()}`,b=p$6(),r=o$4(c=>{switch(c.key){case o$2.ArrowDown:return c.preventDefault(),c.stopPropagation(),e.comboboxState===1&&i.openCombobox(),b.nextFrame(()=>{var m;return (m=e.inputRef.current)==null?void 0:m.focus({preventScroll:!0})});case o$2.ArrowUp:return c.preventDefault(),c.stopPropagation(),e.comboboxState===1&&(i.openCombobox(),b.nextFrame(()=>{e.value||i.goToOption(a$2.Last);})),b.nextFrame(()=>{var m;return (m=e.inputRef.current)==null?void 0:m.focus({preventScroll:!0})});case o$2.Escape:return e.comboboxState!==0?void 0:(c.preventDefault(),e.optionsRef.current&&!e.optionsPropsRef.current.static&&c.stopPropagation(),i.closeCombobox(),b.nextFrame(()=>{var m;return (m=e.inputRef.current)==null?void 0:m.focus({preventScroll:!0})}));default:return}}),u=o$4(c=>{if(r$2(c.currentTarget))return c.preventDefault();e.comboboxState===0?i.closeCombobox():(c.preventDefault(),i.openCombobox()),b.nextFrame(()=>{var m;return (m=e.inputRef.current)==null?void 0:m.focus({preventScroll:!0})});}),C=i$4(()=>{if(!!e.labelRef.current)return [e.labelRef.current.id,p].join(" ")},[e.labelRef.current,p]),f=useMemo(()=>({open:e.comboboxState===0,disabled:e.disabled}),[e]),R=a,g={ref:t,id:p,type:s$5(a,e.buttonRef),tabIndex:-1,"aria-haspopup":!0,"aria-controls":(x=e.optionsRef.current)==null?void 0:x.id,"aria-expanded":e.disabled?void 0:e.comboboxState===0,"aria-labelledby":C,disabled:e.disabled,onClick:u,onKeyDown:r};return _$3({ourProps:g,theirProps:R,slot:f,defaultTag:Fe$2,name:"Combobox.Button"})}),ke$2="label",Ue$1=H$3(function(a,o){let e=k$5("Combobox.Label"),i=`headlessui-combobox-label-${I$1()}`,t=y$2(e.labelRef,o),p=o$4(()=>{var C;return (C=e.inputRef.current)==null?void 0:C.focus({preventScroll:!0})}),b=useMemo(()=>({open:e.comboboxState===0,disabled:e.disabled}),[e]);return _$3({ourProps:{ref:t,id:i,onClick:p},theirProps:a,slot:b,defaultTag:ke$2,name:"Combobox.Label"})}),Be$3="ul",Ve$1=x$6.RenderStrategy|x$6.Static,je$1=H$3(function(a,o){var g;let{hold:e=!1,...i}=a,t=k$5("Combobox.Options"),p=y$2(t.optionsRef,o),b=`headlessui-combobox-options-${I$1()}`,r=s$2(),u=(()=>r!==null?r===p$3.Open:t.comboboxState===0)();t$3(()=>{var x;t.optionsPropsRef.current.static=(x=a.static)!=null?x:!1;},[t.optionsPropsRef,a.static]),t$3(()=>{t.optionsPropsRef.current.hold=e;},[t.optionsPropsRef,e]),F$4({container:t.optionsRef.current,enabled:t.comboboxState===0,accept(x){return x.getAttribute("role")==="option"?NodeFilter.FILTER_REJECT:x.hasAttribute("role")?NodeFilter.FILTER_SKIP:NodeFilter.FILTER_ACCEPT},walk(x){x.setAttribute("role","none");}});let C=i$4(()=>{var x,c,m;return (m=(x=t.labelRef.current)==null?void 0:x.id)!=null?m:(c=t.buttonRef.current)==null?void 0:c.id},[t.labelRef.current,t.buttonRef.current]),f=useMemo(()=>({open:t.comboboxState===0}),[t]),R={"aria-activedescendant":t.activeOptionIndex===null||(g=t.options[t.activeOptionIndex])==null?void 0:g.id,"aria-labelledby":C,role:"listbox",id:b,ref:p};return _$3({ourProps:R,theirProps:i,slot:f,defaultTag:Be$3,features:Ve$1,visible:u,name:"Combobox.Options"})}),Ge$1="li",He$2=H$3(function(a,o){var l,j;let{disabled:e=!1,value:i,...t}=a,p=k$5("Combobox.Option"),b=N$8("Combobox.Option"),r=`headlessui-combobox-option-${I$1()}`,u=p.activeOptionIndex!==null?p.options[p.activeOptionIndex].id===r:!1,C=p.isSelected(i),f=useRef(null),R=s$7({disabled:e,value:i,domRef:f,textValue:(j=(l=f.current)==null?void 0:l.textContent)==null?void 0:j.toLowerCase()}),g=y$2(o,f),x=o$4(()=>b.selectOption(r));t$3(()=>b.registerOption(r,R),[R,r]);let c=useRef(!p.__demoMode);t$3(()=>{if(!p.__demoMode)return;let v=o$5();return v.requestAnimationFrame(()=>{c.current=!0;}),v.dispose},[]),t$3(()=>{if(p.comboboxState!==0||!u||!c.current||p.activationTrigger===0)return;let v=o$5();return v.requestAnimationFrame(()=>{var P,G;(G=(P=f.current)==null?void 0:P.scrollIntoView)==null||G.call(P,{block:"nearest"});}),v.dispose},[f,u,p.comboboxState,p.activationTrigger,p.activeOptionIndex]);let m=o$4(v=>{var P;if(e)return v.preventDefault();x(),p.mode===0&&(b.closeCombobox(),(P=p.inputRef.current)==null||P.focus({preventScroll:!0}));}),U=o$4(()=>{if(e)return b.goToOption(a$2.Nothing);b.goToOption(a$2.Specific,r);}),M=o$4(()=>{e||u||b.goToOption(a$2.Specific,r,0);}),h=o$4(()=>{e||!u||p.optionsPropsRef.current.hold||b.goToOption(a$2.Nothing);}),D=useMemo(()=>({active:u,selected:C,disabled:e}),[u,C,e]);return _$3({ourProps:{id:r,ref:g,role:"option",tabIndex:e===!0?void 0:-1,"aria-disabled":e===!0?!0:void 0,"aria-selected":C===!0?!0:void 0,disabled:void 0,onClick:m,onFocus:U,onPointerMove:M,onMouseMove:M,onPointerLeave:h,onMouseLeave:h},theirProps:t,slot:D,defaultTag:Ge$1,name:"Combobox.Option"})}),Ot=Object.assign(Le$3,{Input:_e$2,Button:we$2,Label:Ue$1,Options:je$1,Option:He$2});

var s$1=(r=>(r[r.Forwards=0]="Forwards",r[r.Backwards=1]="Backwards",r))(s$1||{});function n$2(){let e=useRef(0);return s$6("keydown",o=>{o.key==="Tab"&&(e.current=o.shiftKey?1:0);},!0),e}

function f$2(){let e=useRef(!1);return t$3(()=>(e.current=!0,()=>{e.current=!1;}),[]),e}

function n$1(...e){return useMemo(()=>t$2(...e),[...e])}

function E$4(n,e,a,t){let i=s$7(a);useEffect(()=>{n=n!=null?n:window;function r(o){i.current(o);}return n.addEventListener(e,r,t),()=>n.removeEventListener(e,r,t)},[n,e,t]);}

function t$1(e){typeof queueMicrotask=="function"?queueMicrotask(e):Promise.resolve().then(e).catch(o=>setTimeout(()=>{throw o}));}

function m$1(o,t){let r=useRef([]),e=o$4(o);useEffect(()=>{for(let[u,f]of t.entries())if(r.current[u]!==f){let i=e(t);return r.current=t,i}},[e,...t]);}

let N$7="div";var F$3=(r=>(r[r.None=1]="None",r[r.InitialFocus=2]="InitialFocus",r[r.TabLock=4]="TabLock",r[r.FocusLock=8]="FocusLock",r[r.RestoreFocus=16]="RestoreFocus",r[r.All=30]="All",r))(F$3||{});let fe$3=Object.assign(H$3(function(n,e){let l=useRef(null),u=y$2(l,e),{initialFocus:f,containers:r,features:o=30,...c}=n;a$3()||(o=1);let s=n$1(l);V$2({ownerDocument:s},Boolean(o&16));let O=x$5({ownerDocument:s,container:l,initialFocus:f},Boolean(o&2));G$2({ownerDocument:s,container:l,containers:r,previousActiveElement:O},Boolean(o&8));let v=n$2(),p=o$4(()=>{let T=l.current;!T||u$5(v.current,{[s$1.Forwards]:()=>H$4(T,p$5.First),[s$1.Backwards]:()=>H$4(T,p$5.Last)});}),j={ref:u};return React__default.createElement(React__default.Fragment,null,Boolean(o&4)&&React__default.createElement(h$2,{as:"button",type:"button",onFocus:p,features:s$3.Focusable}),_$3({ourProps:j,theirProps:c,defaultTag:N$7,name:"FocusTrap"}),Boolean(o&4)&&React__default.createElement(h$2,{as:"button",type:"button",onFocus:p,features:s$3.Focusable}))}),{features:F$3});function V$2({ownerDocument:t},n){let e=useRef(null);E$4(t==null?void 0:t.defaultView,"focusout",u=>{!n||e.current||(e.current=u.target);},!0),m$1(()=>{n||((t==null?void 0:t.activeElement)===(t==null?void 0:t.body)&&F$5(e.current),e.current=null);},[n]);let l=useRef(!1);useEffect(()=>(l.current=!1,()=>{l.current=!0,t$1(()=>{!l.current||(F$5(e.current),e.current=null);});}),[]);}function x$5({ownerDocument:t,container:n,initialFocus:e},l){let u=useRef(null);return m$1(()=>{if(!l)return;let f=n.current;if(!f)return;let r=t==null?void 0:t.activeElement;if(e!=null&&e.current){if((e==null?void 0:e.current)===r){u.current=r;return}}else if(f.contains(r)){u.current=r;return}e!=null&&e.current?F$5(e.current):H$4(f,p$5.First)===L$4.Error&&console.warn("There are no focusable elements inside the <FocusTrap />"),u.current=t==null?void 0:t.activeElement;},[l]),u}function G$2({ownerDocument:t,container:n,containers:e,previousActiveElement:l},u){let f=f$2();E$4(t==null?void 0:t.defaultView,"focus",r=>{if(!u||!f.current)return;let o=new Set(e==null?void 0:e.current);o.add(n);let c=l.current;if(!c)return;let s=r.target;s&&s instanceof HTMLElement?W$2(o,s)?(l.current=s,F$5(s)):(r.preventDefault(),r.stopPropagation(),F$5(c)):F$5(l.current);},!0);}function W$2(t,n){var e;for(let l of t)if((e=l.current)!=null&&e.contains(n))return !0;return !1}

let i$1=new Set,r$1=new Map;function u$3(t){t.setAttribute("aria-hidden","true"),t.inert=!0;}function l$2(t){let n=r$1.get(t);!n||(n["aria-hidden"]===null?t.removeAttribute("aria-hidden"):t.setAttribute("aria-hidden",n["aria-hidden"]),t.inert=n.inert);}function M$4(t,n=!0){t$3(()=>{if(!n||!t.current)return;let o=t.current,a=t$2(o);if(!!a){i$1.add(o);for(let e of r$1.keys())e.contains(o)&&(l$2(e),r$1.delete(e));return a.querySelectorAll("body > *").forEach(e=>{if(e instanceof HTMLElement){for(let f of i$1)if(e.contains(f))return;i$1.size===1&&(r$1.set(e,{"aria-hidden":e.getAttribute("aria-hidden"),inert:e.inert}),u$3(e));}}),()=>{if(i$1.delete(o),i$1.size>0)a.querySelectorAll("body > *").forEach(e=>{if(e instanceof HTMLElement&&!r$1.has(e)){for(let f of i$1)if(e.contains(f))return;r$1.set(e,{"aria-hidden":e.getAttribute("aria-hidden"),inert:e.inert}),u$3(e);}});else for(let e of r$1.keys())l$2(e),r$1.delete(e);}}},[n]);}

let e$1=createContext(!1);function l$1(){return useContext(e$1)}function P$4(o){return React__default.createElement(e$1.Provider,{value:o.force},o.children)}

function H$2(i){let u=l$1(),o=useContext(E$3),e=n$1(i),[r,f]=useState(()=>{if(!u&&o!==null||typeof window=="undefined")return null;let n=e==null?void 0:e.getElementById("headlessui-portal-root");if(n)return n;if(e===null)return null;let t=e.createElement("div");return t.setAttribute("id","headlessui-portal-root"),e.body.appendChild(t)});return useEffect(()=>{r!==null&&(e!=null&&e.body.contains(r)||e==null||e.body.appendChild(r));},[r,e]),useEffect(()=>{u||o!==null&&f(o.current);},[o,f,u]),r}let x$4=Fragment,_$2=H$3(function(u,o){let e=u,r=useRef(null),f=y$2(T$4(a=>{r.current=a;}),o),n=n$1(r),t=H$2(r),[l]=useState(()=>{var a;return typeof window=="undefined"?null:(a=n==null?void 0:n.createElement("div"))!=null?a:null}),A=a$3(),p=useRef(!1);return t$3(()=>{if(p.current=!1,!(!t||!l))return t.contains(l)||(l.setAttribute("data-headlessui-portal",""),t.appendChild(l)),()=>{p.current=!0,t$1(()=>{var a;!p.current||!t||!l||(t.removeChild(l),t.childNodes.length<=0&&((a=t.parentElement)==null||a.removeChild(t)));});}},[t,l]),A?!t||!l?null:createPortal(_$3({ourProps:{ref:f},theirProps:e,defaultTag:x$4,name:"Portal"}),l):null}),U$1=Fragment,E$3=createContext(null),j$4=H$3(function(u,o){let{target:e,...r}=u,n={ref:y$2(o)};return React__default.createElement(E$3.Provider,{value:e},_$3({ourProps:n,theirProps:r,defaultTag:U$1,name:"Popover.Group"}))}),X$1=Object.assign(_$2,{Group:j$4});

let d=createContext(null);function u$2(){let r=useContext(d);if(r===null){let t=new Error("You used a <Description /> component, but it is not inside a relevant parent.");throw Error.captureStackTrace&&Error.captureStackTrace(t,u$2),t}return r}function k$4(){let[r,t]=useState([]);return [r.length>0?r.join(" "):void 0,useMemo(()=>function(e){let i=o$4(n=>(t(o=>[...o,n]),()=>t(o=>{let c=o.slice(),p=c.indexOf(n);return p!==-1&&c.splice(p,1),c}))),s=useMemo(()=>({register:i,slot:e.slot,name:e.name,props:e.props}),[i,e.slot,e.name,e.props]);return React__default.createElement(d.Provider,{value:s},e.children)},[t])]}let S$2="p",F$2=H$3(function(t,a){let e=u$2(),i=`headlessui-description-${I$1()}`,s=y$2(a);t$3(()=>e.register(i),[i,e.register]);let n=t,o={ref:s,...e.props,id:i};return _$3({ourProps:o,theirProps:n,slot:e.slot||{},defaultTag:S$2,name:e.name||"Description"})});

let o$1=createContext(()=>{});o$1.displayName="StackContext";var f$1=(e=>(e[e.Add=0]="Add",e[e.Remove=1]="Remove",e))(f$1||{});function x$3(){return useContext(o$1)}function C$5({children:u,onUpdate:r,type:e,element:n}){let c=x$3(),t=o$4((...a)=>{r==null||r(...a),c(...a);});return t$3(()=>(t(0,e,n),()=>t(1,e,n)),[t,e,n]),React__default.createElement(o$1.Provider,{value:t},u)}

var be$4=(t=>(t[t.Open=0]="Open",t[t.Closed=1]="Closed",t))(be$4||{}),Ce$3=(e=>(e[e.SetTitleId=0]="SetTitleId",e))(Ce$3||{});let Oe$1={[0](i,e){return i.titleId===e.id?i:{...i,titleId:e.id}}},M$3=createContext(null);M$3.displayName="DialogContext";function L$2(i){let e=useContext(M$3);if(e===null){let t=new Error(`<${i} /> is missing a parent <Dialog /> component.`);throw Error.captureStackTrace&&Error.captureStackTrace(t,L$2),t}return e}function Se$2(i,e){return u$5(e.type,Oe$1,i,e)}let Le$2="div",we$1=x$6.RenderStrategy|x$6.Static,Fe$1=H$3(function(e,t){let{open:o,onClose:n,initialFocus:d,__demoMode:g=!1,...c}=e,[m,D]=useState(0),v=s$2();o===void 0&&v!==null&&(o=u$5(v,{[p$3.Open]:!0,[p$3.Closed]:!1}));let a=useRef(new Set),f=useRef(null),J=y$2(f,t),W=useRef(null),P=n$1(f),B=e.hasOwnProperty("open")||v!==null,G=e.hasOwnProperty("onClose");if(!B&&!G)throw new Error("You have to provide an `open` and an `onClose` prop to the `Dialog` component.");if(!B)throw new Error("You provided an `onClose` prop to the `Dialog`, but forgot an `open` prop.");if(!G)throw new Error("You provided an `open` prop to the `Dialog`, but forgot an `onClose` prop.");if(typeof o!="boolean")throw new Error(`You provided an \`open\` prop to the \`Dialog\`, but the value is not a boolean. Received: ${o}`);if(typeof n!="function")throw new Error(`You provided an \`onClose\` prop to the \`Dialog\`, but the value is not a function. Received: ${n}`);let s=o?0:1,[y,Q]=useReducer(Se$2,{titleId:null,descriptionId:null,panelRef:createRef()}),R=o$4(()=>n(!1)),U=o$4(r=>Q({type:0,id:r})),_=a$3()?g?!1:s===0:!1,w=m>1,$=useContext(M$3)!==null,X=w?"parent":"leaf";M$4(f,w?_:!1),L$3(()=>{var l,p;return [...Array.from((l=P==null?void 0:P.querySelectorAll("body > *, [data-headlessui-portal]"))!=null?l:[]).filter(T=>!(!(T instanceof HTMLElement)||T.contains(W.current)||y.panelRef.current&&T.contains(y.panelRef.current))),(p=y.panelRef.current)!=null?p:f.current]},R,_&&!w),E$4(P==null?void 0:P.defaultView,"keydown",r=>{r.defaultPrevented||r.key===o$2.Escape&&s===0&&(w||(r.preventDefault(),r.stopPropagation(),R()));}),useEffect(()=>{var j;if(s!==0||$)return;let r=t$2(f);if(!r)return;let l=r.documentElement,p=(j=r.defaultView)!=null?j:window,T=l.style.overflow,le=l.style.paddingRight,Y=p.innerWidth-l.clientWidth;if(l.style.overflow="hidden",Y>0){let ne=l.clientWidth-l.offsetWidth,ie=Y-ne;l.style.paddingRight=`${ie}px`;}return ()=>{l.style.overflow=T,l.style.paddingRight=le;}},[s,$]),useEffect(()=>{if(s!==0||!f.current)return;let r=new IntersectionObserver(l=>{for(let p of l)p.boundingClientRect.x===0&&p.boundingClientRect.y===0&&p.boundingClientRect.width===0&&p.boundingClientRect.height===0&&R();});return r.observe(f.current),()=>r.disconnect()},[s,f,R]);let[Z,ee]=k$4(),te=`headlessui-dialog-${I$1()}`,oe=useMemo(()=>[{dialogState:s,close:R,setTitleId:U},y],[s,y,R,U]),N=useMemo(()=>({open:s===0}),[s]),re={ref:J,id:te,role:"dialog","aria-modal":s===0?!0:void 0,"aria-labelledby":y.titleId,"aria-describedby":Z};return React__default.createElement(C$5,{type:"Dialog",element:f,onUpdate:o$4((r,l,p)=>{l==="Dialog"&&u$5(r,{[f$1.Add](){a.current.add(p),D(T=>T+1);},[f$1.Remove](){a.current.add(p),D(T=>T-1);}});})},React__default.createElement(P$4,{force:!0},React__default.createElement(X$1,null,React__default.createElement(M$3.Provider,{value:oe},React__default.createElement(X$1.Group,{target:f},React__default.createElement(P$4,{force:!1},React__default.createElement(ee,{slot:N,name:"Dialog.Description"},React__default.createElement(fe$3,{initialFocus:d,containers:a,features:_?u$5(X,{parent:fe$3.features.RestoreFocus,leaf:fe$3.features.All&~fe$3.features.FocusLock}):fe$3.features.None},_$3({ourProps:re,theirProps:c,slot:N,defaultTag:Le$2,features:we$1,visible:s===0,name:"Dialog"})))))))),React__default.createElement(h$2,{features:s$3.Hidden,ref:W}))}),ke$1="div",Me$2=H$3(function(e,t){let[{dialogState:o,close:n}]=L$2("Dialog.Overlay"),d=y$2(t),g=`headlessui-dialog-overlay-${I$1()}`,c=o$4(a=>{if(a.target===a.currentTarget){if(r$2(a.currentTarget))return a.preventDefault();a.preventDefault(),a.stopPropagation(),n();}}),m=useMemo(()=>({open:o===0}),[o]);return _$3({ourProps:{ref:d,id:g,"aria-hidden":!0,onClick:c},theirProps:e,slot:m,defaultTag:ke$1,name:"Dialog.Overlay"})}),_e$1="div",Ie$1=H$3(function(e,t){let[{dialogState:o},n]=L$2("Dialog.Backdrop"),d=y$2(t),g=`headlessui-dialog-backdrop-${I$1()}`;useEffect(()=>{if(n.panelRef.current===null)throw new Error("A <Dialog.Backdrop /> component is being used, but a <Dialog.Panel /> component is missing.")},[n.panelRef]);let c=useMemo(()=>({open:o===0}),[o]);return React__default.createElement(P$4,{force:!0},React__default.createElement(X$1,null,_$3({ourProps:{ref:d,id:g,"aria-hidden":!0},theirProps:e,slot:c,defaultTag:_e$1,name:"Dialog.Backdrop"})))}),xe$2="div",He$1=H$3(function(e,t){let[{dialogState:o},n]=L$2("Dialog.Panel"),d=y$2(t,n.panelRef),g=`headlessui-dialog-panel-${I$1()}`,c=useMemo(()=>({open:o===0}),[o]),m=o$4(a=>{a.stopPropagation();});return _$3({ourProps:{ref:d,id:g,onClick:m},theirProps:e,slot:c,defaultTag:xe$2,name:"Dialog.Panel"})}),We$2="h2",Be$2=H$3(function(e,t){let[{dialogState:o,setTitleId:n}]=L$2("Dialog.Title"),d=`headlessui-dialog-title-${I$1()}`,g=y$2(t);useEffect(()=>(n(d),()=>n(null)),[d,n]);let c=useMemo(()=>({open:o===0}),[o]);return _$3({ourProps:{ref:g,id:d},theirProps:e,slot:c,defaultTag:We$2,name:"Dialog.Title"})}),mt=Object.assign(Fe$1,{Backdrop:Ie$1,Panel:He$1,Overlay:Me$2,Title:Be$2,Description:F$2});

var Ae=(c=>(c[c.Open=0]="Open",c[c.Closed=1]="Closed",c))(Ae||{}),Ce$2=(n=>(n[n.TogglePopover=0]="TogglePopover",n[n.ClosePopover=1]="ClosePopover",n[n.SetButton=2]="SetButton",n[n.SetButtonId=3]="SetButtonId",n[n.SetPanel=4]="SetPanel",n[n.SetPanelId=5]="SetPanelId",n))(Ce$2||{});let Re$2={[0]:r=>({...r,popoverState:u$5(r.popoverState,{[0]:1,[1]:0})}),[1](r){return r.popoverState===1?r:{...r,popoverState:1}},[2](r,t){return r.button===t.button?r:{...r,button:t.button}},[3](r,t){return r.buttonId===t.buttonId?r:{...r,buttonId:t.buttonId}},[4](r,t){return r.panel===t.panel?r:{...r,panel:t.panel}},[5](r,t){return r.panelId===t.panelId?r:{...r,panelId:t.panelId}}},ee$2=createContext(null);ee$2.displayName="PopoverContext";function J$3(r){let t=useContext(ee$2);if(t===null){let c=new Error(`<${r} /> is missing a parent <Popover /> component.`);throw Error.captureStackTrace&&Error.captureStackTrace(c,J$3),c}return t}let te$2=createContext(null);te$2.displayName="PopoverAPIContext";function oe(r){let t=useContext(te$2);if(t===null){let c=new Error(`<${r} /> is missing a parent <Popover /> component.`);throw Error.captureStackTrace&&Error.captureStackTrace(c,oe),c}return t}let re$2=createContext(null);re$2.displayName="PopoverGroupContext";function ce$2(){return useContext(re$2)}let ne$1=createContext(null);ne$1.displayName="PopoverPanelContext";function Oe(){return useContext(ne$1)}function Le$1(r,t){return u$5(t.type,Re$2,r,t)}let Fe="div",Ie=H$3(function(t,c){var R;let o=`headlessui-popover-button-${I$1()}`,d=`headlessui-popover-panel-${I$1()}`,a=useRef(null),n=y$2(c,T$4(e=>{a.current=e;})),g=useReducer(Le$1,{popoverState:1,button:null,buttonId:o,panel:null,panelId:d,beforePanelSentinel:createRef(),afterPanelSentinel:createRef()}),[{popoverState:m,button:l,panel:v,beforePanelSentinel:p,afterPanelSentinel:B},s]=g,T=n$1((R=a.current)!=null?R:l);useEffect(()=>s({type:3,buttonId:o}),[o,s]),useEffect(()=>s({type:5,panelId:d}),[d,s]);let f=useMemo(()=>{if(!l||!v)return !1;for(let e of document.querySelectorAll("body > *"))if(Number(e==null?void 0:e.contains(l))^Number(e==null?void 0:e.contains(v)))return !0;return !1},[l,v]),P=useMemo(()=>({buttonId:o,panelId:d,close:()=>s({type:1})}),[o,d,s]),y=ce$2(),A=y==null?void 0:y.registerPopover,L=o$4(()=>{var e;return (e=y==null?void 0:y.isFocusWithinPopoverGroup())!=null?e:(T==null?void 0:T.activeElement)&&((l==null?void 0:l.contains(T.activeElement))||(v==null?void 0:v.contains(T.activeElement)))});useEffect(()=>A==null?void 0:A(P),[A,P]),E$4(T==null?void 0:T.defaultView,"focus",e=>{var i,S,O,N;m===0&&(L()||!l||!v||(S=(i=p.current)==null?void 0:i.contains)!=null&&S.call(i,e.target)||(N=(O=B.current)==null?void 0:O.contains)!=null&&N.call(O,e.target)||s({type:1}));},!0),L$3([l,v],(e,i)=>{s({type:1}),S$3(i,b$5.Loose)||(e.preventDefault(),l==null||l.focus());},m===0);let F=o$4(e=>{s({type:1});let i=(()=>e?e instanceof HTMLElement?e:e.current instanceof HTMLElement?e.current:l:l)();i==null||i.focus();}),D=useMemo(()=>({close:F,isPortalled:f}),[F,f]),u=useMemo(()=>({open:m===0,close:F}),[m,F]),E=t,C={ref:n};return React__default.createElement(ee$2.Provider,{value:g},React__default.createElement(te$2.Provider,{value:D},React__default.createElement(C$6,{value:u$5(m,{[0]:p$3.Open,[1]:p$3.Closed})},_$3({ourProps:C,theirProps:E,slot:u,defaultTag:Fe,name:"Popover"}))))}),Me$1="button",Be$1=H$3(function(t,c){let[o,d]=J$3("Popover.Button"),{isPortalled:a}=oe("Popover.Button"),n=useRef(null),g=`headlessui-focus-sentinel-${I$1()}`,m=ce$2(),l=m==null?void 0:m.closeOthers,v=Oe(),p=v===null?!1:v===o.panelId,B=y$2(n,c,p?null:e=>d({type:2,button:e})),s=y$2(n,c),T=n$1(n),f=o$4(e=>{var i,S,O;if(p){if(o.popoverState===1)return;switch(e.key){case o$2.Space:case o$2.Enter:e.preventDefault(),(S=(i=e.target).click)==null||S.call(i),d({type:1}),(O=o.button)==null||O.focus();break}}else switch(e.key){case o$2.Space:case o$2.Enter:e.preventDefault(),e.stopPropagation(),o.popoverState===1&&(l==null||l(o.buttonId)),d({type:0});break;case o$2.Escape:if(o.popoverState!==0)return l==null?void 0:l(o.buttonId);if(!n.current||(T==null?void 0:T.activeElement)&&!n.current.contains(T.activeElement))return;e.preventDefault(),e.stopPropagation(),d({type:1});break}}),P=o$4(e=>{p||e.key===o$2.Space&&e.preventDefault();}),y=o$4(e=>{var i,S;r$2(e.currentTarget)||t.disabled||(p?(d({type:1}),(i=o.button)==null||i.focus()):(e.preventDefault(),e.stopPropagation(),o.popoverState===1&&(l==null||l(o.buttonId)),d({type:0}),(S=o.button)==null||S.focus()));}),A=o$4(e=>{e.preventDefault(),e.stopPropagation();}),L=o.popoverState===0,F=useMemo(()=>({open:L}),[L]),D=s$5(t,n),u=t,E=p?{ref:s,type:D,onKeyDown:f,onClick:y}:{ref:B,id:o.buttonId,type:D,"aria-expanded":t.disabled?void 0:o.popoverState===0,"aria-controls":o.panel?o.panelId:void 0,onKeyDown:f,onKeyUp:P,onClick:y,onMouseDown:A},C=n$2(),R=o$4(()=>{let e=o.panel;if(!e)return;function i(){u$5(C.current,{[s$1.Forwards]:()=>H$4(e,p$5.First),[s$1.Backwards]:()=>H$4(e,p$5.Last)});}i();});return React__default.createElement(React__default.Fragment,null,_$3({ourProps:E,theirProps:u,slot:F,defaultTag:Me$1,name:"Popover.Button"}),L&&!p&&a&&React__default.createElement(h$2,{id:g,features:s$3.Focusable,as:"button",type:"button",onFocus:R}))}),he$1="div",De=x$6.RenderStrategy|x$6.Static,xe$1=H$3(function(t,c){let[{popoverState:o},d]=J$3("Popover.Overlay"),a=y$2(c),n=`headlessui-popover-overlay-${I$1()}`,g=s$2(),m=(()=>g!==null?g===p$3.Open:o===0)(),l=o$4(s=>{if(r$2(s.currentTarget))return s.preventDefault();d({type:1});}),v=useMemo(()=>({open:o===0}),[o]);return _$3({ourProps:{ref:a,id:n,"aria-hidden":!0,onClick:l},theirProps:t,slot:v,defaultTag:he$1,features:De,visible:m,name:"Popover.Overlay"})}),He="div",Ge=x$6.RenderStrategy|x$6.Static,ke=H$3(function(t,c){let{focus:o=!1,...d}=t,[a,n]=J$3("Popover.Panel"),{close:g,isPortalled:m}=oe("Popover.Panel"),l=`headlessui-focus-sentinel-before-${I$1()}`,v=`headlessui-focus-sentinel-after-${I$1()}`,p=useRef(null),B=y$2(p,c,u=>{n({type:4,panel:u});}),s=n$1(p),T=s$2(),f=(()=>T!==null?T===p$3.Open:a.popoverState===0)(),P=o$4(u=>{var E;switch(u.key){case o$2.Escape:if(a.popoverState!==0||!p.current||(s==null?void 0:s.activeElement)&&!p.current.contains(s.activeElement))return;u.preventDefault(),u.stopPropagation(),n({type:1}),(E=a.button)==null||E.focus();break}});useEffect(()=>{var u;t.static||a.popoverState===1&&((u=t.unmount)!=null?u:!0)&&n({type:4,panel:null});},[a.popoverState,t.unmount,t.static,n]),useEffect(()=>{if(!o||a.popoverState!==0||!p.current)return;let u=s==null?void 0:s.activeElement;p.current.contains(u)||H$4(p.current,p$5.First);},[o,p,a.popoverState]);let y=useMemo(()=>({open:a.popoverState===0,close:g}),[a,g]),A={ref:B,id:a.panelId,onKeyDown:P,onBlur:o&&a.popoverState===0?u=>{var C,R,e,i,S;let E=u.relatedTarget;!E||!p.current||(C=p.current)!=null&&C.contains(E)||(n({type:1}),(((e=(R=a.beforePanelSentinel.current)==null?void 0:R.contains)==null?void 0:e.call(R,E))||((S=(i=a.afterPanelSentinel.current)==null?void 0:i.contains)==null?void 0:S.call(i,E)))&&E.focus({preventScroll:!0}));}:void 0,tabIndex:-1},L=n$2(),F=o$4(()=>{let u=p.current;if(!u)return;function E(){u$5(L.current,{[s$1.Forwards]:()=>{H$4(u,p$5.First);},[s$1.Backwards]:()=>{var C;(C=a.button)==null||C.focus({preventScroll:!0});}});}E();}),D=o$4(()=>{let u=p.current;if(!u)return;function E(){u$5(L.current,{[s$1.Forwards]:()=>{var O,N,le;if(!a.button)return;let C=T$5(),R=C.indexOf(a.button),e=C.slice(0,R+1),S=[...C.slice(R+1),...e];for(let K of S.slice())if(((N=(O=K==null?void 0:K.id)==null?void 0:O.startsWith)==null?void 0:N.call(O,"headlessui-focus-sentinel-"))||((le=a.panel)==null?void 0:le.contains(K))){let ae=S.indexOf(K);ae!==-1&&S.splice(ae,1);}H$4(S,p$5.First,!1);},[s$1.Backwards]:()=>H$4(u,p$5.Last)});}E();});return React__default.createElement(ne$1.Provider,{value:a.panelId},f&&m&&React__default.createElement(h$2,{id:l,ref:a.beforePanelSentinel,features:s$3.Focusable,as:"button",type:"button",onFocus:F}),_$3({ourProps:A,theirProps:d,slot:y,defaultTag:He,features:Ge,visible:f,name:"Popover.Panel"}),f&&m&&React__default.createElement(h$2,{id:v,ref:a.afterPanelSentinel,features:s$3.Focusable,as:"button",type:"button",onFocus:D}))}),_e="div",we=H$3(function(t,c){let o=useRef(null),d=y$2(o,c),[a,n]=useState([]),g=o$4(f=>{n(P=>{let y=P.indexOf(f);if(y!==-1){let A=P.slice();return A.splice(y,1),A}return P});}),m=o$4(f=>(n(P=>[...P,f]),()=>g(f))),l=o$4(()=>{var y;let f=t$2(o);if(!f)return !1;let P=f.activeElement;return (y=o.current)!=null&&y.contains(P)?!0:a.some(A=>{var L,F;return ((L=f.getElementById(A.buttonId))==null?void 0:L.contains(P))||((F=f.getElementById(A.panelId))==null?void 0:F.contains(P))})}),v=o$4(f=>{for(let P of a)P.buttonId!==f&&P.close();}),p=useMemo(()=>({registerPopover:m,unregisterPopover:g,isFocusWithinPopoverGroup:l,closeOthers:v}),[m,g,l,v]),B=useMemo(()=>({}),[]),s=t,T={ref:d};return React__default.createElement(re$2.Provider,{value:p},_$3({ourProps:T,theirProps:s,slot:B,defaultTag:_e,name:"Popover.Group"}))}),Tt$1=Object.assign(Ie,{Button:Be$1,Overlay:xe$1,Panel:ke,Group:we});

function b$4(g=0){let[r,l]=useState(g),u=useCallback(e=>l(a=>a|e),[r]),n=useCallback(e=>Boolean(r&e),[r]),o=useCallback(e=>l(a=>a&~e),[l]),s=useCallback(e=>l(a=>a^e),[l]);return {addFlag:u,hasFlag:n,removeFlag:o,toggleFlag:s}}

let u$1=createContext(null);function c$3(){let o=useContext(u$1);if(o===null){let t=new Error("You used a <Label /> component, but it is not inside a relevant parent.");throw Error.captureStackTrace&&Error.captureStackTrace(t,c$3),t}return o}function M$2(){let[o,t]=useState([]);return [o.length>0?o.join(" "):void 0,useMemo(()=>function(e){let l=o$4(a=>(t(i=>[...i,a]),()=>t(i=>{let n=i.slice(),d=n.indexOf(a);return d!==-1&&n.splice(d,1),n}))),r=useMemo(()=>({register:l,slot:e.slot,name:e.name,props:e.props}),[l,e.slot,e.name,e.props]);return React__default.createElement(u$1.Provider,{value:r},e.children)},[t])]}let h$1="label",F$1=H$3(function(t,s){let{passive:e=!1,...l}=t,r=c$3(),a=`headlessui-label-${I$1()}`,i=y$2(s);t$3(()=>r.register(a),[a,r.register]);let n={ref:i,...r.props,id:a};return e&&("onClick"in n&&delete n.onClick,"onClick"in l&&delete l.onClick),_$3({ourProps:n,theirProps:l,slot:r.slot||{},defaultTag:h$1,name:r.name||"Label"})});

var ue$2=(t=>(t[t.RegisterOption=0]="RegisterOption",t[t.UnregisterOption=1]="UnregisterOption",t))(ue$2||{});let ce$1={[0](n,i){let t=[...n.options,{id:i.id,element:i.element,propsRef:i.propsRef}];return {...n,options:v$4(t,o=>o.element.current)}},[1](n,i){let t=n.options.slice(),o=n.options.findIndex(T=>T.id===i.id);return o===-1?n:(t.splice(o,1),{...n,options:t})}},N$6=createContext(null);N$6.displayName="RadioGroupContext";function Q$2(n){let i=useContext(N$6);if(i===null){let t=new Error(`<${n} /> is missing a parent <RadioGroup /> component.`);throw Error.captureStackTrace&&Error.captureStackTrace(t,Q$2),t}return i}function fe$2(n,i){return u$5(i.type,ce$1,n,i)}let me$1="div",be$3=H$3(function(i,t){let{value:o,name:T,onChange:R,disabled:A=!1,...L}=i,u=o$4((e,a)=>e===a),[x,h]=useReducer(fe$2,{options:[]}),l=x.options,[G,c]=M$2(),[y,w]=k$4(),f=`headlessui-radiogroup-${I$1()}`,g=useRef(null),I=y$2(g,t),k=useMemo(()=>l.find(e=>!e.propsRef.current.disabled),[l]),O=useMemo(()=>l.some(e=>u(e.propsRef.current.value,o)),[l,o]),m=o$4(e=>{var r;if(A||u(e,o))return !1;let a=(r=l.find(s=>u(s.propsRef.current.value,e)))==null?void 0:r.propsRef.current;return a!=null&&a.disabled?!1:(R(e),!0)});F$4({container:g.current,accept(e){return e.getAttribute("role")==="radio"?NodeFilter.FILTER_REJECT:e.hasAttribute("role")?NodeFilter.FILTER_SKIP:NodeFilter.FILTER_ACCEPT},walk(e){e.setAttribute("role","none");}});let S=o$4(e=>{let a=g.current;if(!a)return;let r=t$2(a),s=l.filter(p=>p.propsRef.current.disabled===!1).map(p=>p.element.current);switch(e.key){case o$2.Enter:p$4(e.currentTarget);break;case o$2.ArrowLeft:case o$2.ArrowUp:if(e.preventDefault(),e.stopPropagation(),H$4(s,p$5.Previous|p$5.WrapAround)===L$4.Success){let d=l.find(v=>v.element.current===(r==null?void 0:r.activeElement));d&&m(d.propsRef.current.value);}break;case o$2.ArrowRight:case o$2.ArrowDown:if(e.preventDefault(),e.stopPropagation(),H$4(s,p$5.Next|p$5.WrapAround)===L$4.Success){let d=l.find(v=>v.element.current===(r==null?void 0:r.activeElement));d&&m(d.propsRef.current.value);}break;case o$2.Space:{e.preventDefault(),e.stopPropagation();let p=l.find(d=>d.element.current===(r==null?void 0:r.activeElement));p&&m(p.propsRef.current.value);}break}}),C=o$4(e=>(h({type:0,...e}),()=>h({type:1,id:e.id}))),_=useMemo(()=>({registerOption:C,firstOption:k,containsCheckedOption:O,change:m,disabled:A,value:o,compare:u}),[C,k,O,m,A,o,u]),M={ref:I,id:f,role:"radiogroup","aria-labelledby":G,"aria-describedby":y,onKeyDown:S};return React__default.createElement(w,{name:"RadioGroup.Description"},React__default.createElement(c,{name:"RadioGroup.Label"},React__default.createElement(N$6.Provider,{value:_},T!=null&&o!=null&&e$2({[T]:o}).map(([e,a])=>React__default.createElement(h$2,{features:s$3.Hidden,...g$4({key:e,as:"input",type:"radio",checked:a!=null,hidden:!0,readOnly:!0,name:e,value:a})})),_$3({ourProps:M,theirProps:L,defaultTag:me$1,name:"RadioGroup"}))))});var Te=(t=>(t[t.Empty=1]="Empty",t[t.Active=2]="Active",t))(Te||{});let Re$1="div",ye$2=H$3(function(i,t){let o=useRef(null),T=y$2(o,t),R=`headlessui-radiogroup-option-${I$1()}`,[A,L]=M$2(),[u,x]=k$4(),{addFlag:h,removeFlag:l,hasFlag:G}=b$4(1),{value:c,disabled:y=!1,...w}=i,f=useRef({value:c,disabled:y});t$3(()=>{f.current.value=c;},[c,f]),t$3(()=>{f.current.disabled=y;},[y,f]);let{registerOption:g,disabled:I,change:k,firstOption:O,containsCheckedOption:m,value:S,compare:C}=Q$2("RadioGroup.Option");t$3(()=>g({id:R,element:o,propsRef:f}),[R,g,o,i]);let _=o$4(()=>{var v;!k(c)||(h(2),(v=o.current)==null||v.focus());}),M=o$4(()=>h(2)),e=o$4(()=>l(2)),a=(O==null?void 0:O.id)===R,r=I||y,s=C(S,c),p={ref:T,id:R,role:"radio","aria-checked":s?"true":"false","aria-labelledby":A,"aria-describedby":u,"aria-disabled":r?!0:void 0,tabIndex:(()=>r?-1:s||!m&&a?0:-1)(),onClick:r?void 0:_,onFocus:r?void 0:M,onBlur:r?void 0:e},d=useMemo(()=>({checked:s,disabled:r,active:G(2)}),[s,r,G]);return React__default.createElement(x,{name:"RadioGroup.Description"},React__default.createElement(L,{name:"RadioGroup.Label"},_$3({ourProps:p,theirProps:w,slot:d,defaultTag:Re$1,name:"RadioGroup.Option"})))}),je=Object.assign(be$3,{Option:ye$2,Label:F$1,Description:F$2});

let p$2=createContext(null);p$2.displayName="GroupContext";let j$3=Fragment;function N$5(m){let[n,i]=useState(null),[r,s]=M$2(),[l,c]=k$4(),d=useMemo(()=>({switch:n,setSwitch:i,labelledby:r,describedby:l}),[n,i,r,l]),u={},e=m;return React__default.createElement(c,{name:"Switch.Description"},React__default.createElement(s,{name:"Switch.Label",props:{onClick(){!n||(n.click(),n.focus({preventScroll:!0}));}}},React__default.createElement(p$2.Provider,{value:d},_$3({ourProps:u,theirProps:e,defaultTag:j$3,name:"Switch.Group"}))))}let $$2="button",q$1=H$3(function(n,i){let{checked:r,onChange:s,name:l,value:c,...d}=n,u=`headlessui-switch-${I$1()}`,e=useContext(p$2),f=useRef(null),S=y$2(f,i,e===null?null:e.setSwitch),h=o$4(()=>s(!r)),E=o$4(t=>{if(r$2(t.currentTarget))return t.preventDefault();t.preventDefault(),h();}),w=o$4(t=>{t.key===o$2.Space?(t.preventDefault(),h()):t.key===o$2.Enter&&p$4(t.currentTarget);}),P=o$4(t=>t.preventDefault()),v=useMemo(()=>({checked:r}),[r]),g={id:u,ref:S,role:"switch",type:s$5(n,f),tabIndex:0,"aria-checked":r,"aria-labelledby":e==null?void 0:e.labelledby,"aria-describedby":e==null?void 0:e.describedby,onClick:E,onKeyUp:w,onKeyPress:P};return React__default.createElement(React__default.Fragment,null,l!=null&&r&&React__default.createElement(h$2,{features:s$3.Hidden,...g$4({as:"input",type:"checkbox",hidden:!0,readOnly:!0,checked:r,name:l,value:c})}),_$3({ourProps:g,theirProps:d,slot:v,defaultTag:$$2,name:"Switch"}))}),ue$1=Object.assign(q$1,{Group:N$5,Label:F$1,Description:F$2});

function l(r){let e={called:!1};return (...t)=>{if(!e.called)return e.called=!0,r(...t)}}

function f(t,...e){t&&e.length>0&&t.classList.add(...e);}function g$3(t,...e){t&&e.length>0&&t.classList.remove(...e);}var T$3=(n=>(n.Ended="ended",n.Cancelled="cancelled",n))(T$3||{});function c$2(t,e){let n=o$5();if(!t)return n.dispose;let{transitionDuration:d,transitionDelay:a}=getComputedStyle(t),[s,u]=[d,a].map(i=>{let[o=0]=i.split(",").filter(Boolean).map(r=>r.includes("ms")?parseFloat(r):parseFloat(r)*1e3).sort((r,l)=>l-r);return o});if(s+u!==0){let i=[];i.push(n.addEventListener(t,"transitionrun",o=>{o.target===o.currentTarget&&(i.splice(0).forEach(r=>r()),i.push(n.addEventListener(t,"transitionend",r=>{r.target===r.currentTarget&&(e("ended"),i.splice(0).forEach(l=>l()));}),n.addEventListener(t,"transitioncancel",r=>{r.target===r.currentTarget&&(e("cancelled"),i.splice(0).forEach(l=>l()));})));}));}else e("ended");return n.add(()=>e("cancelled")),n.dispose}function C$4(t,e,n,d){let a=n?"enter":"leave",s=o$5(),u=d!==void 0?l(d):()=>{},m=u$5(a,{enter:()=>e.enter,leave:()=>e.leave}),i=u$5(a,{enter:()=>e.enterTo,leave:()=>e.leaveTo}),o=u$5(a,{enter:()=>e.enterFrom,leave:()=>e.leaveFrom});return g$3(t,...e.enter,...e.enterTo,...e.enterFrom,...e.leave,...e.leaveFrom,...e.leaveTo,...e.entered),f(t,...m,...o),s.nextFrame(()=>{g$3(t,...o),f(t,...i),c$2(t,r=>(r==="ended"&&(g$3(t,...m),f(t,...e.entered)),u(r)));}),s.dispose}

function H$1({container:u,direction:o,classes:c,events:t,onStart:d,onStop:l}){let f=f$2(),m=p$6(),e=s$7(o),b=o$4(()=>u$5(e.current,{enter:()=>t.current.beforeEnter(),leave:()=>t.current.beforeLeave(),idle:()=>{}})),p=o$4(()=>u$5(e.current,{enter:()=>t.current.afterEnter(),leave:()=>t.current.afterLeave(),idle:()=>{}}));t$3(()=>{let r=o$5();m.add(r.dispose);let i=u.current;if(!!i&&e.current!=="idle"&&!!f.current)return r.dispose(),b(),d.current(e.current),r.add(C$4(i,c.current,e.current==="enter",v=>{r.dispose(),u$5(v,{[T$3.Ended](){p(),l.current(e.current);},[T$3.Cancelled]:()=>{}});})),r.dispose},[o]);}

function h(e=""){return e.split(" ").filter(n=>n.trim().length>1)}let N$4=createContext(null);N$4.displayName="TransitionContext";var ge$2=(t=>(t.Visible="visible",t.Hidden="hidden",t))(ge$2||{});function ve$2(){let e=useContext(N$4);if(e===null)throw new Error("A <Transition.Child /> is used but it is missing a parent <Transition /> or <Transition.Root />.");return e}function Ce$1(){let e=useContext(L$1);if(e===null)throw new Error("A <Transition.Child /> is used but it is missing a parent <Transition /> or <Transition.Root />.");return e}let L$1=createContext(null);L$1.displayName="NestingContext";function w$4(e){return "children"in e?w$4(e.children):e.current.filter(({state:n})=>n==="visible").length>0}function Z$1(e){let n=s$7(e),t=useRef([]),r=f$2(),s=o$4((l,o=R$1.Hidden)=>{let a=t.current.findIndex(({id:u})=>u===l);a!==-1&&(u$5(o,{[R$1.Unmount](){t.current.splice(a,1);},[R$1.Hidden](){t.current[a].state="hidden";}}),t$1(()=>{var u;!w$4(t)&&r.current&&((u=n.current)==null||u.call(n));}));}),g=o$4(l=>{let o=t.current.find(({id:a})=>a===l);return o?o.state!=="visible"&&(o.state="visible"):t.current.push({id:l,state:"visible"}),()=>s(l,R$1.Unmount)});return useMemo(()=>({children:t,register:g,unregister:s}),[g,s,t])}function be$2(){}let Se$1=["beforeEnter","afterEnter","beforeLeave","afterLeave"];function $$1(e){var t;let n={};for(let r of Se$1)n[r]=(t=e[r])!=null?t:be$2;return n}function Ee(e){let n=useRef($$1(e));return useEffect(()=>{n.current=$$1(e);},[e]),n}let xe="div",ee$1=x$6.RenderStrategy,te$1=H$3(function(n,t){let{beforeEnter:r,afterEnter:s,beforeLeave:g,afterLeave:l,enter:o,enterFrom:a,enterTo:u,entered:A,leave:v,leaveFrom:E,leaveTo:x,...y}=n,d=useRef(null),D=y$2(d,t),[i,C]=useState("visible"),p=y.unmount?R$1.Unmount:R$1.Hidden,{show:b,appear:re,initial:ne}=ve$2(),{register:H,unregister:P}=Ce$1(),F=useRef(null),f=I$1();useEffect(()=>{if(!!f)return H(f)},[H,f]),useEffect(()=>{if(p===R$1.Hidden&&!!f){if(b&&i!=="visible"){C("visible");return}u$5(i,{["hidden"]:()=>P(f),["visible"]:()=>H(f)});}},[i,f,H,P,b,p]);let ie=s$7({enter:h(o),enterFrom:h(a),enterTo:h(u),entered:h(A),leave:h(v),leaveFrom:h(E),leaveTo:h(x)}),se=Ee({beforeEnter:r,afterEnter:s,beforeLeave:g,afterLeave:l}),O=a$3();useEffect(()=>{if(O&&i==="visible"&&d.current===null)throw new Error("Did you forget to passthrough the `ref` to the actual DOM node?")},[d,i,O]);let M=ne&&!re,oe=(()=>!O||M||F.current===b?"idle":b?"enter":"leave")(),_=useRef(!1),B=Z$1(()=>{_.current||(C("hidden"),P(f));});H$1({container:d,classes:ie,events:se,direction:oe,onStart:s$7(()=>{_.current=!0;}),onStop:s$7(ue=>{_.current=!1,ue==="leave"&&!w$4(B)&&(C("hidden"),P(f));})}),useEffect(()=>{!M||(p===R$1.Hidden?F.current=null:F.current=b);},[b,M,i]);let le=y,ae={ref:D};return React__default.createElement(L$1.Provider,{value:B},React__default.createElement(C$6,{value:u$5(i,{["visible"]:p$3.Open,["hidden"]:p$3.Closed})},_$3({ourProps:ae,theirProps:le,defaultTag:xe,features:ee$1,visible:i==="visible",name:"Transition.Child"})))}),G$1=H$3(function(n,t){let{show:r,appear:s=!1,unmount:g,...l}=n,o=useRef(null),a=y$2(o,t);a$3();let u=s$2();if(r===void 0&&u!==null&&(r=u$5(u,{[p$3.Open]:!0,[p$3.Closed]:!1})),![!0,!1].includes(r))throw new Error("A <Transition /> is used but it is missing a `show={true | false}` prop.");let[A,v]=useState(r?"visible":"hidden"),E=Z$1(()=>{v("hidden");}),[x,y]=useState(!0),d=useRef([r]);t$3(()=>{x!==!1&&d.current[d.current.length-1]!==r&&(d.current.push(r),y(!1));},[d,r]);let D=useMemo(()=>({show:r,appear:s,initial:x}),[r,s,x]);useEffect(()=>{if(r)v("visible");else if(!w$4(E))v("hidden");else {let C=o.current;if(!C)return;let p=C.getBoundingClientRect();p.x===0&&p.y===0&&p.width===0&&p.height===0&&v("hidden");}},[r,E]);let i={unmount:g};return React__default.createElement(L$1.Provider,{value:E},React__default.createElement(N$4.Provider,{value:D},_$3({ourProps:{...i,as:Fragment,children:React__default.createElement(te$1,{ref:a,...i,...l})},theirProps:{},defaultTag:Fragment,features:ee$1,visible:A==="visible",name:"Transition"})))}),ye$1=H$3(function(n,t){let r=useContext(N$4)!==null,s=s$2()!==null;return React__default.createElement(React__default.Fragment,null,!r&&s?React__default.createElement(G$1,{ref:t,...n}):React__default.createElement(te$1,{ref:t,...n}))}),We$1=Object.assign(G$1,{Child:ye$1,Root:G$1});

const Backdrop = ({ show }) => (React__default.createElement(We$1, { show: show, as: Fragment, appear: true, enter: "ease-in-out duration-100", enterFrom: "opacity-50", enterTo: "opacity-100", leave: "ease-in-out duration-[1200ms]", leaveFrom: "opacity-100", leaveTo: "opacity-0" },
    React__default.createElement("div", { className: "absolute inset-0 bg-white bg-opacity-75 transition-opacity z-overlay" })));

/**
 * It's `true` if it is running in a browser environment or `false` if it is not
 * (SSR).
 * @example
 * const title = canUseDOM ? document.title : "";
 */
const canUseDOM$1 = checkIsBrowser(); // Check if we can use the DOM. Useful for SSR purposes

function checkIsBrowser() {
  var _window$document;

  return typeof window !== "undefined" && !!((_window$document = window.document) != null && _window$document.createElement);
}
/**
 * Returns `element.ownerDocument || document`.
 */


function getDocument(node) {
  return node ? node.ownerDocument || node : document;
}
/**
 * Returns `element.ownerDocument.defaultView || window`.
 */

function getWindow$2(node) {
  return getDocument(node).defaultView || window;
}
/**
 * Returns `element.ownerDocument.activeElement`.
 */

function getActiveElement(node, activeDescendant) {
  if (activeDescendant === void 0) {
    activeDescendant = false;
  }

  const {
    activeElement
  } = getDocument(node);

  if (!(activeElement != null && activeElement.nodeName)) {
    // In IE11, activeElement might be an empty object if we're interacting
    // with elements inside of an iframe.
    return null;
  }

  if (isFrame(activeElement) && activeElement.contentDocument) {
    return getActiveElement(activeElement.contentDocument.body, activeDescendant);
  }

  if (activeDescendant) {
    const id = activeElement.getAttribute("aria-activedescendant");

    if (id) {
      const element = getDocument(activeElement).getElementById(id);

      if (element) {
        return element;
      }
    }
  }

  return activeElement;
}
/**
 * Similar to `Element.prototype.contains`, but a little bit faster when
 * `element` is the same as `child`.
 * @example
 * contains(
 *   document.getElementById("parent"),
 *   document.getElementById("child")
 * );
 */

function contains(parent, child) {
  return parent === child || parent.contains(child);
}
/**
 * Checks whether `element` is a frame element.
 */

function isFrame(element) {
  return element.tagName === "IFRAME";
}
/**
 * Checks whether `element` is a native HTML button element.
 * @example
 * isButton(document.querySelector("button")); // true
 * isButton(document.querySelector("input[type='button']")); // true
 * isButton(document.querySelector("div")); // false
 * isButton(document.querySelector("input[type='text']")); // false
 * isButton(document.querySelector("div[role='button']")); // false
 */

function isButton(element) {
  const tagName = element.tagName.toLowerCase();
  if (tagName === "button") return true;

  if (tagName === "input" && element.type) {
    return buttonInputTypes.indexOf(element.type) !== -1;
  }

  return false;
}
const buttonInputTypes = ["button", "color", "file", "image", "reset", "submit"];
/**
 * Ponyfill for `Element.prototype.matches`
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/matches
 */

function matches(element, selectors) {
  if ("matches" in element) {
    return element.matches(selectors);
  }

  if ("msMatchesSelector" in element) {
    return element.msMatchesSelector(selectors);
  }

  return element.webkitMatchesSelector(selectors);
}
/**
 * Checks if the element is visible or not.
 */

function isVisible(element) {
  const htmlElement = element;
  return htmlElement.offsetWidth > 0 || htmlElement.offsetHeight > 0 || element.getClientRects().length > 0;
}
/**
 * Ponyfill for `Element.prototype.closest`
 * @example
 * closest(document.getElementById("id"), "div");
 * // same as
 * document.getElementById("id").closest("div");
 */

function closest(element, selectors) {
  if ("closest" in element) return element.closest(selectors);

  do {
    if (matches(element, selectors)) return element;
    element = element.parentElement || element.parentNode;
  } while (element !== null && element.nodeType === 1);

  return null;
}
/**
 * Check whether the given element is a text field, where text field is defined
 * by the ability to select within the input.
 * @example
 * isTextField(document.querySelector("div")); // false
 * isTextField(document.querySelector("input")); // true
 * isTextField(document.querySelector("input[type='button']")); // false
 * isTextField(document.querySelector("textarea")); // true
 */

function isTextField(element) {
  try {
    const isTextInput = element instanceof HTMLInputElement && element.selectionStart !== null;
    const isTextArea = element.tagName === "TEXTAREA";
    return isTextInput || isTextArea || false;
  } catch (error) {
    // Safari throws an exception when trying to get `selectionStart` on
    // non-text <input> elements (which, understandably, don't have the text
    // selection API). We catch this via a try/catch block, as opposed to a more
    // explicit check of the element's input types, because of Safari's
    // non-standard behavior. This also means we don't have to worry about the
    // list of input types that support `selectionStart` changing as the HTML
    // spec evolves over time.
    return false;
  }
}
/**
 * Returns the element's role attribute, if it has one.
 */

function getPopupRole(element, fallback) {
  const allowedPopupRoles = ["dialog", "menu", "listbox", "tree", "grid"];
  const role = element == null ? void 0 : element.getAttribute("role");

  if (role && allowedPopupRoles.indexOf(role) !== -1) {
    return role;
  }

  return fallback;
}
/**
 * Returns the item role attribute based on the popup's role.
 */

function getPopupItemRole(element, fallback) {
  var _itemRoleByPopupRole$;

  const itemRoleByPopupRole = {
    menu: "menuitem",
    listbox: "option",
    tree: "treeitem",
    grid: "gridcell"
  };
  const popupRole = getPopupRole(element);
  if (!popupRole) return fallback;
  const key = popupRole;
  return (_itemRoleByPopupRole$ = itemRoleByPopupRole[key]) != null ? _itemRoleByPopupRole$ : fallback;
}
/**
 * Returns the scrolling container element of a given element.
 */

function getScrollingElement(element) {
  if (!element) return null;

  if (element.clientHeight && element.scrollHeight > element.clientHeight) {
    const {
      overflowY
    } = getComputedStyle(element);
    const isScrollable = overflowY !== "visible" && overflowY !== "hidden";
    if (isScrollable) return element;
  }

  return getScrollingElement(element.parentElement) || document.scrollingElement || document.body;
}

/**
 * Detects if the device has touch capabilities.
 */

function isTouchDevice() {
  return canUseDOM$1 && !!navigator.maxTouchPoints;
}
/**
 * Detects Apple device.
 */

function isApple() {
  if (!canUseDOM$1) return false;
  return /mac|iphone|ipad|ipod/i.test(navigator.platform);
}
/**
 * Detects Safari browser.
 */

function isSafari() {
  return canUseDOM$1 && isApple() && /apple/i.test(navigator.vendor);
}
/**
 * Detects Firefox browser.
 */

function isFirefox() {
  return canUseDOM$1 && /firefox\//i.test(navigator.userAgent);
}
/**
 * Detects Mac computer.
 */

function isMac() {
  return canUseDOM$1 && navigator.platform.startsWith("Mac") && !isTouchDevice();
}

/**
 * Returns `true` if `event` has been fired within a React Portal element.
 */

function isPortalEvent(event) {
  return !contains(event.currentTarget, event.target);
}
/**
 * Returns `true` if `event.target` and `event.currentTarget` are the same.
 */

function isSelfTarget(event) {
  return event.target === event.currentTarget;
}
/**
 * Checks whether the user event is triggering a page navigation in a new tab.
 */

function isOpeningInNewTab(event) {
  const element = event.currentTarget;
  if (!element) return false;
  const isAppleDevice = isApple();
  if (isAppleDevice && !event.metaKey) return false;
  if (!isAppleDevice && !event.ctrlKey) return false;
  const tagName = element.tagName.toLowerCase();
  if (tagName === "a") return true;
  if (tagName === "button" && element.type === "submit") return true;
  if (tagName === "input" && element.type === "submit") return true;
  return false;
}
/**
 * Checks whether the user event is triggering a download.
 */

function isDownloading(event) {
  const element = event.currentTarget;
  if (!element) return false;
  const tagName = element.tagName.toLowerCase();
  if (!event.altKey) return false;
  if (tagName === "a") return true;
  if (tagName === "button" && element.type === "submit") return true;
  if (tagName === "input" && element.type === "submit") return true;
  return false;
}
/**
 * Creates and dispatches a blur event.
 * @example
 * fireBlurEvent(document.getElementById("id"));
 */

function fireBlurEvent(element, eventInit) {
  const event = new FocusEvent("blur", eventInit);
  const defaultAllowed = element.dispatchEvent(event);
  const bubbleInit = { ...eventInit,
    bubbles: true
  };
  element.dispatchEvent(new FocusEvent("focusout", bubbleInit));
  return defaultAllowed;
}
/**
 * Creates and dispatches a focus event.
 * @example
 * fireFocusEvent(document.getElementById("id"));
 */

function fireFocusEvent(element, eventInit) {
  const event = new FocusEvent("focus", eventInit);
  const defaultAllowed = element.dispatchEvent(event);
  const bubbleInit = { ...eventInit,
    bubbles: true
  };
  element.dispatchEvent(new FocusEvent("focusin", bubbleInit));
  return defaultAllowed;
}
/**
 * Creates and dispatches a keyboard event.
 * @example
 * fireKeyboardEvent(document.getElementById("id"), "keydown", {
 *   key: "ArrowDown",
 *   shiftKey: true,
 * });
 */

function fireKeyboardEvent(element, type, eventInit) {
  const event = new KeyboardEvent(type, eventInit);
  return element.dispatchEvent(event);
}
/**
 * Creates and dispatches a click event.
 * @example
 * fireClickEvent(document.getElementById("id"));
 */

function fireClickEvent(element, eventInit) {
  const event = new MouseEvent("click", eventInit);
  return element.dispatchEvent(event);
}
/**
 * Checks whether the focus/blur event is happening from/to outside of the
 * container element.
 * @example
 * const element = document.getElementById("id");
 * element.addEventListener("blur", (event) => {
 *   if (isFocusEventOutside(event)) {
 *     // ...
 *   }
 * });
 */

function isFocusEventOutside(event, container) {
  const containerElement = container || event.currentTarget;
  const relatedTarget = event.relatedTarget;
  return !relatedTarget || !contains(containerElement, relatedTarget);
}
/**
 * Runs a callback on the next animation frame, but before a certain event.
 */

function queueBeforeEvent(element, type, callback) {
  const raf = requestAnimationFrame(() => {
    element.removeEventListener(type, callImmediately, true);
    callback();
  });

  const callImmediately = () => {
    cancelAnimationFrame(raf);
    callback();
  }; // By listening to the event in the capture phase, we make sure the callback
  // is fired before the respective React events.


  element.addEventListener(type, callImmediately, {
    once: true,
    capture: true
  });
  return raf;
}
/**
 * Adds a global event listener, including on child frames.
 */

function addGlobalEventListener(type, listener, options, scope) {
  if (scope === void 0) {
    scope = window;
  }

  // Prevent errors from "sandbox" frames.
  try {
    scope.document.addEventListener(type, listener, options);
  } catch (e) {}

  const listeners = [];

  for (let i = 0; i < ((_scope$frames = scope.frames) == null ? void 0 : _scope$frames.length); i += 1) {
    var _scope$frames;

    const frameWindow = scope.frames[i];

    if (frameWindow) {
      listeners.push(addGlobalEventListener(type, listener, options, frameWindow));
    }
  }

  const removeEventListener = () => {
    try {
      scope.document.removeEventListener(type, listener, options);
    } catch (e) {}

    listeners.forEach(listener => listener());
  };

  return removeEventListener;
}

/**
 * Empty function.
 */
function noop$7() {}
/**
 * Compares two objects.
 * @example
 * shallowEqual({ a: "a" }, {}); // false
 * shallowEqual({ a: "a" }, { b: "b" }); // false
 * shallowEqual({ a: "a" }, { a: "a" }); // true
 * shallowEqual({ a: "a" }, { a: "a", b: "b" }); // false
 */

function shallowEqual(a, b) {
  if (a === b) return true;
  if (!a) return false;
  if (!b) return false;
  if (typeof a !== "object") return false;
  if (typeof b !== "object") return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  const {
    length
  } = aKeys;
  if (bKeys.length !== length) return false;

  for (const key of aKeys) {
    if (a[key] !== b[key]) {
      return false;
    }
  }

  return true;
}
/**
 * Receives a `setState` argument and calls it with `currentValue` if it's a
 * function. Otherwise return the argument as the new value.
 * @example
 * applyState((value) => value + 1, 1); // 2
 * applyState(2, 1); // 2
 */

function applyState(argument, currentValue) {
  if (isUpdater(argument)) {
    const value = isLazyValue(currentValue) ? currentValue() : currentValue;
    return argument(value);
  }

  return argument;
}

function isUpdater(argument) {
  return typeof argument === "function";
}

function isLazyValue(value) {
  return typeof value === "function";
}
/**
 * Sets both a function and object React ref.
 */


function setRef(ref, value) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}
/**
 * Checks whether `prop` is an own property of `obj` or not.
 */

function hasOwnProperty$e(object, prop) {
  return Object.prototype.hasOwnProperty.call(object, prop);
}
/**
 * Receives functions as arguments and returns a new function that calls all.
 */

function chain() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return function () {
    for (const fn of fns) {
      if (typeof fn === "function") {
        // @ts-ignore
        fn(...arguments);
      }
    }
  };
}
/**
 * Returns a string with the truthy values of `args` separated by space.
 */

function cx() {
  for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    args[_key2] = arguments[_key2];
  }

  return args.filter(Boolean).join(" ") || undefined;
}
/**
 * Removes diatrics from a string.
 * TODO: Check if it works on WebView Android.
 */

function normalizeString(str) {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}
/**
 * Queues a function to be called at the end of the current event loop.
 */

function queueMicrotask$1(callback) {
  if (window.queueMicrotask) {
    return window.queueMicrotask(callback);
  }

  Promise.resolve().then(callback);
}

// @ts-ignore Access React v18 hooks using string concatenation in order to
// prevent Webpack from inferring that they are not present in React v17. For
// example, React.useId will raise a compile time error when using React v17,
// but React['use' + 'Id'] will not.
const useReactId = React["use" + "Id"]; // @ts-ignore

const useInsertionEffect = React["use" + "InsertionEffect"];
/**
 * `React.useLayoutEffect` that fallbacks to `React.useEffect` on server side.
 */

const useSafeLayoutEffect = canUseDOM$1 ? useLayoutEffect$1 : useEffect;
/**
 * Returns a value that never changes even if the argument is updated.
 * @example
 * function Component({ prop }) {
 *   const initialProp = useInitialValue(prop);
 * }
 */

function useInitialValue$1(value) {
  const [initialValue] = useState(value);
  return initialValue;
}
/**
 * Returns a value that is lazily initiated and never changes.
 * @example
 * function Component() {
 *   const set = useLazyValue(() => new Set());
 * }
 */

function useLazyValue(init) {
  const ref = useRef();

  if (ref.current === undefined) {
    ref.current = init();
  }

  return ref.current;
}
/**
 * Creates a `React.RefObject` that is constantly updated with the incoming
 * value.
 * @example
 * function Component({ prop }) {
 *   const propRef = useLiveRef(prop);
 * }
 */

function useLiveRef(value) {
  const ref = useRef(value);
  useSafeLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}
/**
 * Keeps the reference of the previous value to be used in the render phase.
 */

function usePreviousValue(value) {
  const [previousValue, setPreviousValue] = useState(value);

  if (value !== previousValue) {
    setPreviousValue(value);
  }

  return previousValue;
}
/**
 * Creates a stable callback function that has access to the latest state and
 * can be used within event handlers and effect callbacks. Throws when used in
 * the render phase.
 * @example
 * function Component(props) {
 *   const onClick = useEvent(props.onClick);
 *   React.useEffect(() => {}, [onClick]);
 * }
 */

function useEvent$1(callback) {
  const ref = useRef(() => {
    throw new Error("Cannot call an event handler while rendering.");
  });

  if (useInsertionEffect) {
    useInsertionEffect(() => {
      ref.current = callback;
    });
  } else {
    ref.current = callback;
  }

  return useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return ref.current == null ? void 0 : ref.current(...args);
  }, []);
}
/**
 * Merges React Refs into a single memoized function ref so you can pass it to
 * an element.
 * @example
 * const Component = React.forwardRef((props, ref) => {
 *   const internalRef = React.useRef();
 *   return <div {...props} ref={useForkRef(internalRef, ref)} />;
 * });
 */

function useForkRef() {
  for (var _len2 = arguments.length, refs = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    refs[_key2] = arguments[_key2];
  }

  return useMemo(() => {
    if (!refs.some(Boolean)) return;
    return value => {
      refs.forEach(ref => {
        setRef(ref, value);
      });
    };
  }, refs);
}
/**
 * Returns the ref element's ID.
 */

function useRefId(ref, deps) {
  const [id, setId] = useState(undefined);
  useSafeLayoutEffect(() => {
    var _ref$current;

    setId(ref == null ? void 0 : (_ref$current = ref.current) == null ? void 0 : _ref$current.id);
  }, deps);
  return id;
}
/**
 * Generates a unique ID. Uses React's useId if available.
 */

function useId(defaultId) {
  if (useReactId) {
    const reactId = useReactId();
    if (defaultId) return defaultId;
    return reactId;
  }

  const [id, setId] = useState(defaultId);
  useSafeLayoutEffect(() => {
    if (defaultId || id) return;
    const random = Math.random().toString(36).substr(2, 6);
    setId("id-" + random);
  }, [defaultId, id]);
  return defaultId || id;
}
/**
 * Returns the tag name by parsing an element ref and the `as` prop.
 * @example
 * function Component(props) {
 *   const ref = React.useRef();
 *   const tagName = useTagName(ref, "button"); // div
 *   return <div ref={ref} {...props} />;
 * }
 */

function useTagName(ref, type) {
  const [tagName, setTagName] = useState(() => stringOrUndefined(type));
  useSafeLayoutEffect(() => {
    var _ref$current2;

    setTagName((ref == null ? void 0 : (_ref$current2 = ref.current) == null ? void 0 : _ref$current2.tagName.toLowerCase()) || stringOrUndefined(type));
  }, [ref, type]);
  return tagName;
}

function stringOrUndefined(type) {
  if (typeof type === "string") {
    return type;
  }

  return;
}
/**
 * A custom version of `React.useState` that uses the `state` and `setState`
 * arguments. If they're not provided, it will use the internal state.
 */

function useControlledState(defaultState, state, setState) {
  const [localState, setLocalState] = useState(defaultState);
  const nextState = state !== undefined ? state : localState;
  const stateRef = useLiveRef(state);
  const setStateRef = useLiveRef(setState);
  const nextStateRef = useLiveRef(nextState);
  const setNextState = useCallback(prevValue => {
    const setStateProp = setStateRef.current;

    if (setStateProp) {
      if (isSetNextState(setStateProp)) {
        setStateProp(prevValue);
      } else {
        const nextValue = applyState(prevValue, nextStateRef.current);
        nextStateRef.current = nextValue;
        setStateProp(nextValue);
      }
    }

    if (stateRef.current === undefined) {
      setLocalState(prevValue);
    }
  }, []);
  defineSetNextState(setNextState);
  return [nextState, setNextState];
}
const SET_NEXT_STATE = Symbol("setNextState");

function isSetNextState(arg) {
  return arg[SET_NEXT_STATE] === true;
}

function defineSetNextState(arg) {
  if (!isSetNextState(arg)) {
    Object.defineProperty(arg, SET_NEXT_STATE, {
      value: true
    });
  }
}
/**
 * A React hook similar to `useState` and `useReducer`, but with the only
 * purpose of re-rendering the component.
 */


function useForceUpdate$1() {
  return useReducer(() => [], []);
}
/**
 * Returns an event callback similar to `useEvent`, but this also accepts a
 * boolean value, which will be turned into a function.
 */

function useBooleanEvent(booleanOrCallback) {
  return useEvent$1(typeof booleanOrCallback === "function" ? booleanOrCallback : () => booleanOrCallback);
}
/**
 * Returns props with an additional `wrapElement` prop.
 */

function useWrapElement(props, callback, deps) {
  if (deps === void 0) {
    deps = [];
  }

  const wrapElement = useCallback(element => {
    if (props.wrapElement) {
      element = props.wrapElement(element);
    }

    return callback(element);
  }, [...deps, props.wrapElement]);
  return { ...props,
    wrapElement
  };
}
/**
 * Merges the portalRef prop and returns a `domReady` to be used in the
 * components that use Portal underneath.
 */

function usePortalRef(portalProp, portalRefProp) {
  if (portalProp === void 0) {
    portalProp = false;
  }

  const [portalNode, setPortalNode] = useState(null);
  const portalRef = useForkRef(setPortalNode, portalRefProp);
  const domReady = !portalProp || portalNode;
  return {
    portalRef,
    portalNode,
    domReady
  };
}
/**
 * Returns a function that checks whether the mouse is moving.
 */

function useIsMouseMoving() {
  useEffect(() => {
    // We're not returning the event listener cleanup function here because we
    // may lose some events if this component is unmounted, but others are
    // still mounted.
    addGlobalEventListener("mousemove", setMouseMoving, true); // See https://github.com/ariakit/ariakit/issues/1137

    addGlobalEventListener("mousedown", resetMouseMoving, true);
    addGlobalEventListener("mouseup", resetMouseMoving, true);
    addGlobalEventListener("keydown", resetMouseMoving, true);
    addGlobalEventListener("scroll", resetMouseMoving, true);
  }, []);
  const isMouseMoving = useEvent$1(() => mouseMoving);
  return isMouseMoving;
}
let mouseMoving = false;
let previousScreenX = 0;
let previousScreenY = 0;

function hasMouseMovement(event) {
  const movementX = event.movementX || event.screenX - previousScreenX;
  const movementY = event.movementY || event.screenY - previousScreenY;
  previousScreenX = event.screenX;
  previousScreenY = event.screenY;
  return movementX || movementY || process.env.NODE_ENV === "test";
}

function setMouseMoving(event) {
  if (!hasMouseMovement(event)) return;
  mouseMoving = true;
}

function resetMouseMoving() {
  mouseMoving = false;
}

function isRenderProp(children) {
  return typeof children === "function";
}
/**
 * Creates a type-safe component with the `as` prop and `React.forwardRef`.
 *
 * @example
 * import { createComponent } from "ariakit-utils/system";
 *
 * type Props = {
 *   as?: "div";
 *   customProp?: boolean;
 * };
 *
 * const Component = createComponent<Props>(({ customProp, ...props }) => {
 *   return <div {...props} />;
 * });
 *
 * <Component as="button" customProp />
 */


function createComponent(render) {
  const Role = (props, ref) => render({
    ref,
    ...props
  });

  return /*#__PURE__*/forwardRef(Role);
}
/**
 * Creates a React element that supports the `as` prop, children as a
 * function (render props) and a `wrapElement` function.
 *
 * @example
 * import { createElement } from "ariakit-utils/system";
 *
 * function Component() {
 *   const props = {
 *     as: "button" as const,
 *     children: (htmlProps) => <button {...htmlProps} />,
 *     wrapElement: (element) => <div>{element}</div>,
 *   };
 *   return createElement("div", props);
 * }
 */

function createElement(Type, props) {
  const {
    as: As,
    wrapElement,
    ...rest
  } = props;
  let element;

  if (As && typeof As !== "string") {
    element = /*#__PURE__*/jsx(As, { ...rest
    });
  } else if (isRenderProp(props.children)) {
    const {
      children,
      ...otherProps
    } = rest;
    element = props.children(otherProps);
  } else if (As) {
    element = /*#__PURE__*/jsx(As, { ...rest
    });
  } else {
    element = /*#__PURE__*/jsx(Type, { ...rest
    });
  }

  if (wrapElement) {
    return wrapElement(element);
  }

  return element;
}
/**
 * Creates a component hook that accepts props and returns props so they can be
 * passed to a React element.
 *
 * @example
 * import { Options, createHook } from "ariakit-utils/system";
 *
 * type Props = Options<"div"> & {
 *   customProp?: boolean;
 * };
 *
 * const useComponent = createHook<Props>(({ customProp, ...props }) => {
 *   return props;
 * });
 *
 * const props = useComponent({ as: "button", customProp: true });
 */

function createHook(useProps) {
  const useRole = function (props) {
    if (props === void 0) {
      props = {};
    }

    const htmlProps = useProps(props);
    const copy = {};

    for (const prop in htmlProps) {
      if (hasOwnProperty$e(htmlProps, prop) && htmlProps[prop] !== undefined) {
        // @ts-expect-error
        copy[prop] = htmlProps[prop];
      }
    }

    return copy;
  };

  return useRole;
}

const selector = "input:not([type='hidden']):not([disabled]), select:not([disabled]), " + "textarea:not([disabled]), a[href], button:not([disabled]), [tabindex], " + "iframe, object, embed, area[href], audio[controls], video[controls], " + "[contenteditable]:not([contenteditable='false'])";

function hasNegativeTabIndex(element) {
  const tabIndex = parseInt(element.getAttribute("tabindex") || "0", 10);
  return tabIndex < 0;
}
/**
 * Checks whether `element` is focusable or not.
 * @example
 * isFocusable(document.querySelector("input")); // true
 * isFocusable(document.querySelector("input[tabindex='-1']")); // true
 * isFocusable(document.querySelector("input[hidden]")); // false
 * isFocusable(document.querySelector("input:disabled")); // false
 */


function isFocusable(element) {
  return matches(element, selector) && isVisible(element);
}
/**
 * Checks whether `element` is tabbable or not.
 * @example
 * isTabbable(document.querySelector("input")); // true
 * isTabbable(document.querySelector("input[tabindex='-1']")); // false
 * isTabbable(document.querySelector("input[hidden]")); // false
 * isTabbable(document.querySelector("input:disabled")); // false
 */

function isTabbable(element) {
  return isFocusable(element) && !hasNegativeTabIndex(element);
}
/**
 * Returns all the focusable elements in `container`.
 */

function getAllFocusableIn(container, includeContainer) {
  const elements = Array.from(container.querySelectorAll(selector));

  if (includeContainer) {
    elements.unshift(container);
  }

  const focusableElements = elements.filter(isFocusable);
  focusableElements.forEach((element, i) => {
    if (isFrame(element) && element.contentDocument) {
      const frameBody = element.contentDocument.body;
      focusableElements.splice(i, 1, ...getAllFocusableIn(frameBody));
    }
  });
  return focusableElements;
}
/**
 * Returns all the tabbable elements in `container`, including the container
 * itself.
 */

function getAllTabbableIn(container, includeContainer, fallbackToFocusable) {
  const elements = Array.from(container.querySelectorAll(selector));
  const tabbableElements = elements.filter(isTabbable);

  if (includeContainer && isTabbable(container)) {
    tabbableElements.unshift(container);
  }

  tabbableElements.forEach((element, i) => {
    if (isFrame(element) && element.contentDocument) {
      const frameBody = element.contentDocument.body;
      const allFrameTabbable = getAllTabbableIn(frameBody, false, fallbackToFocusable);
      tabbableElements.splice(i, 1, ...allFrameTabbable);
    }
  });

  if (!tabbableElements.length && fallbackToFocusable) {
    return elements;
  }

  return tabbableElements;
}
/**
 * Returns the first tabbable element in `container`, including the container
 * itself if it's tabbable.
 */

function getFirstTabbableIn(container, includeContainer, fallbackToFocusable) {
  const [first] = getAllTabbableIn(container, includeContainer, fallbackToFocusable);
  return first || null;
}
/**
 * Returns the next tabbable element in `container`.
 */

function getNextTabbableIn(container, includeContainer, fallbackToFirst, fallbackToFocusable) {
  const activeElement = getActiveElement(container);
  const allFocusable = getAllFocusableIn(container, includeContainer);
  const activeIndex = allFocusable.indexOf(activeElement);
  const nextFocusableElements = allFocusable.slice(activeIndex + 1);
  return nextFocusableElements.find(isTabbable) || (fallbackToFirst ? allFocusable.find(isTabbable) : null) || (fallbackToFocusable ? nextFocusableElements[0] : null) || null;
}
/**
 * Returns the next tabbable element in the document.
 */

function getNextTabbable(fallbackToFirst, fallbackToFocusable) {
  return getNextTabbableIn(document.body, false, fallbackToFirst, fallbackToFocusable);
}
/**
 * Returns the previous tabbable element in `container`.
 *
 */

function getPreviousTabbableIn(container, includeContainer, fallbackToLast, fallbackToFocusable) {
  const activeElement = getActiveElement(container);
  const allFocusable = getAllFocusableIn(container, includeContainer).reverse();
  const activeIndex = allFocusable.indexOf(activeElement);
  const previousFocusableElements = allFocusable.slice(activeIndex + 1);
  return previousFocusableElements.find(isTabbable) || (fallbackToLast ? allFocusable.find(isTabbable) : null) || (fallbackToFocusable ? previousFocusableElements[0] : null) || null;
}
/**
 * Returns the previous tabbable element in the document.
 */

function getPreviousTabbable(fallbackToFirst, fallbackToFocusable) {
  return getPreviousTabbableIn(document.body, false, fallbackToFirst, fallbackToFocusable);
}
/**
 * Checks if `element` has focus. Elements that are referenced by
 * `aria-activedescendant` are also considered.
 * @example
 * hasFocus(document.getElementById("id"));
 */

function hasFocus(element) {
  const activeElement = getActiveElement(element);
  if (!activeElement) return false;
  if (activeElement === element) return true;
  const activeDescendant = activeElement.getAttribute("aria-activedescendant");
  if (!activeDescendant) return false;
  return activeDescendant === element.id;
}
/**
 * Checks if `element` has focus within. Elements that are referenced by
 * `aria-activedescendant` are also considered.
 * @example
 * hasFocusWithin(document.getElementById("id"));
 */

function hasFocusWithin(element) {
  const activeElement = getActiveElement(element);
  if (!activeElement) return false;
  if (contains(element, activeElement)) return true;
  const activeDescendant = activeElement.getAttribute("aria-activedescendant");
  if (!activeDescendant) return false;
  if (!("id" in element)) return false;
  if (activeDescendant === element.id) return true;
  return !!element.querySelector("#" + CSS.escape(activeDescendant));
}
/**
 * Focus on an element only if it's not already focused.
 */

function focusIfNeeded(element) {
  if (!hasFocusWithin(element) && isFocusable(element)) {
    element.focus();
  }
}
/**
 * Disable focus on `element`.
 */

function disableFocus(element) {
  var _element$getAttribute;

  const currentTabindex = (_element$getAttribute = element.getAttribute("tabindex")) != null ? _element$getAttribute : "";
  element.setAttribute("data-tabindex", currentTabindex);
  element.setAttribute("tabindex", "-1");
}
/**
 * Makes elements inside container not tabbable.
 */

function disableFocusIn(container, includeContainer) {
  const tabbableElements = getAllTabbableIn(container, includeContainer);
  tabbableElements.forEach(disableFocus);
}
/**
 * Restores tabbable elements inside container that were affected by
 * disableFocusIn.
 */

function restoreFocusIn(container) {
  const elements = container.querySelectorAll("[data-tabindex]");

  const restoreTabIndex = element => {
    const tabindex = element.getAttribute("data-tabindex");
    element.removeAttribute("data-tabindex");

    if (tabindex) {
      element.setAttribute("tabindex", tabindex);
    } else {
      element.removeAttribute("tabindex");
    }
  };

  if (container.hasAttribute("data-tabindex")) {
    restoreTabIndex(container);
  }

  elements.forEach(restoreTabIndex);
}
/**
 * Focus on element and scroll into view.
 */

function focusIntoView(element, options) {
  if (!("scrollIntoView" in element)) {
    element.focus();
  } else {
    element.focus({
      preventScroll: true
    });
    element.scrollIntoView({
      block: "nearest",
      inline: "nearest",
      ...options
    });
  }
}

const isSafariBrowser$1 = isSafari();
const alwaysFocusVisibleInputTypes = ["text", "search", "url", "tel", "email", "password", "number", "date", "month", "week", "time", "datetime", "datetime-local"];

function isAlwaysFocusVisible(element) {
  const {
    tagName,
    readOnly,
    type
  } = element;
  if (tagName === "TEXTAREA" && !readOnly) return true;
  if (tagName === "SELECT" && !readOnly) return true;

  if (tagName === "INPUT" && !readOnly) {
    return alwaysFocusVisibleInputTypes.includes(type);
  }

  if (element.isContentEditable) return true;
  return false;
} // See https://github.com/ariakit/ariakit/issues/1257


function isAlwaysFocusVisibleDelayed(element) {
  const role = element.getAttribute("role");
  if (role === "combobox") return true;
  return false;
}

function getLabels(element) {
  if ("labels" in element) {
    return element.labels;
  }

  return null;
}

function isNativeCheckboxOrRadio(element) {
  const tagName = element.tagName.toLowerCase();

  if (tagName === "input" && element.type) {
    return element.type === "radio" || element.type === "checkbox";
  }

  return false;
}

function isNativeTabbable(tagName) {
  if (!tagName) return true;
  return tagName === "button" || tagName === "input" || tagName === "select" || tagName === "textarea" || tagName === "a";
}

function supportsDisabledAttribute(tagName) {
  if (!tagName) return true;
  return tagName === "button" || tagName === "input" || tagName === "select" || tagName === "textarea";
}

function getTabIndex(focusable, trulyDisabled, nativeTabbable, supportsDisabled, tabIndexProp) {
  if (!focusable) {
    return tabIndexProp;
  }

  if (trulyDisabled) {
    if (nativeTabbable && !supportsDisabled) {
      // Anchor, audio and video tags don't support the `disabled` attribute.
      // We must pass tabIndex={-1} so they don't receive focus on tab.
      return -1;
    } // Elements that support the `disabled` attribute don't need tabIndex.


    return;
  }

  if (nativeTabbable) {
    // If the element is enabled and it's natively tabbable, we don't need to
    // specify a tabIndex attribute unless it's explicitly set by the user.
    return tabIndexProp;
  } // If the element is enabled and is not natively tabbable, we have to
  // fallback tabIndex={0}.


  return tabIndexProp || 0;
}

function useDisableEvent(onEvent, disabled) {
  return useEvent$1(event => {
    onEvent == null ? void 0 : onEvent(event);
    if (event.defaultPrevented) return;

    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  });
} // isKeyboardModality should be true by defaault.


let isKeyboardModality = true;

function onGlobalMouseDown(event) {
  const target = event.target;

  if (target && "hasAttribute" in target) {
    // If the target element is already focus-visible, we keep the keyboard
    // modality.
    if (!target.hasAttribute("data-focus-visible")) {
      isKeyboardModality = false;
    }
  }
}

function onGlobalKeyDown(event) {
  if (event.metaKey) return;
  if (event.ctrlKey) return;
  isKeyboardModality = true;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element that can be focused.
 * @see https://ariakit.org/components/focusable
 * @example
 * ```jsx
 * const props = useFocusable();
 * <Role {...props}>Focusable</Role>
 * ```
 */


const useFocusable = createHook(_ref => {
  let {
    focusable = true,
    accessibleWhenDisabled,
    autoFocus,
    onFocusVisible,
    ...props
  } = _ref;
  const ref = useRef(null); // Add global event listeners to determine whether the user is using a
  // keyboard to navigate the site or not.

  useEffect(() => {
    if (!focusable) return;
    addGlobalEventListener("mousedown", onGlobalMouseDown, true);
    addGlobalEventListener("keydown", onGlobalKeyDown, true);
  }, [focusable]); // Safari and Firefox on Apple devices don't focus on checkboxes or radio
  // buttons when their labels are clicked. This effect will make sure the
  // focusable element is focused on label click.

  if (isSafariBrowser$1) {
    useEffect(() => {
      if (!focusable) return;
      const element = ref.current;
      if (!element) return;
      if (!isNativeCheckboxOrRadio(element)) return;
      const labels = getLabels(element);
      if (!labels) return;

      const onMouseUp = () => queueMicrotask$1(() => element.focus());

      labels.forEach(label => label.addEventListener("mouseup", onMouseUp));
      return () => {
        labels.forEach(label => label.removeEventListener("mouseup", onMouseUp));
      };
    }, [focusable]);
  }

  const disabled = focusable && props.disabled;
  const trulyDisabled = !!disabled && !accessibleWhenDisabled;
  const [focusVisible, setFocusVisible] = useState(false); // When the focusable element is disabled, it doesn't trigger a blur event
  // so we can't set focusVisible to false there. Instead, we have to do it
  // here by checking the element's disabled attribute.

  useEffect(() => {
    if (!focusable) return;

    if (trulyDisabled && focusVisible) {
      setFocusVisible(false);
    }
  }, [focusable, trulyDisabled, focusVisible]); // When an element that has focus becomes hidden, it doesn't trigger a blur
  // event so we can't set focusVisible to false there. We observe the element
  // and check if it's still focusable. Otherwise, we set focusVisible to
  // false.

  useEffect(() => {
    if (!focusable) return;
    if (!focusVisible) return;
    const element = ref.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(() => {
      if (!isFocusable(element)) {
        setFocusVisible(false);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [focusable, focusVisible]); // Disable events when the element is disabled.

  const onKeyPressCapture = useDisableEvent(props.onKeyPressCapture, disabled);
  const onMouseDownCapture = useDisableEvent(props.onMouseDownCapture, disabled);
  const onClickCapture = useDisableEvent(props.onClickCapture, disabled);
  const onMouseDownProp = props.onMouseDown;
  const onMouseDown = useEvent$1(event => {
    onMouseDownProp == null ? void 0 : onMouseDownProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    const element = event.currentTarget; // Safari doesn't focus on buttons on mouse down like other
    // browsers/platforms. Instead, it focuses on the closest focusable
    // ancestor element, which is ultimately the body element. So we make sure
    // to give focus to this Focusable element on mouse down so it works
    // consistently across browsers.

    if (!isSafariBrowser$1) return;
    if (isPortalEvent(event)) return;
    if (!isButton(element) && !isNativeCheckboxOrRadio(element)) return; // In future versions os Safari, it may change this behavior and start
    // focusing on buttons on mouse down. To account for that, we check if the
    // element has received focus before.

    let receivedFocus = false;

    const onFocus = () => {
      receivedFocus = true;
    };

    const options = {
      capture: true,
      once: true
    };
    element.addEventListener("focusin", onFocus, options); // We can't focus right away after on mouse down, otherwise it would
    // prevent drag events from happening. So we queue the focus to the next
    // animation frame, but always before the next mouseup event. The mouseup
    // event might happen before the next animation frame on touch devices or
    // by tapping on a MacBook's trackpad, for example.

    queueBeforeEvent(element, "mouseup", () => {
      element.removeEventListener("focusin", onFocus, true);
      if (receivedFocus) return;
      focusIfNeeded(element);
    });
  });

  const handleFocusVisible = (event, currentTarget) => {
    if (currentTarget) {
      event.currentTarget = currentTarget;
    }

    onFocusVisible == null ? void 0 : onFocusVisible(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    const element = event.currentTarget;
    if (!element) return; // Some extensions like 1password dispatches some keydown events on
    // autofill and immediately moves focus to the next field. That's why we
    // need to check if the current element is still focused.

    if (!hasFocus(element)) return;
    setFocusVisible(true);
  };

  const onKeyDownCaptureProp = props.onKeyDownCapture;
  const onKeyDownCapture = useEvent$1(event => {
    onKeyDownCaptureProp == null ? void 0 : onKeyDownCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    if (focusVisible) return;
    if (event.metaKey) return;
    if (event.altKey) return;
    if (event.ctrlKey) return;
    if (!isSelfTarget(event)) return;
    const element = event.currentTarget;
    queueMicrotask$1(() => handleFocusVisible(event, element));
  });
  const onFocusCaptureProp = props.onFocusCapture;
  const onFocusCapture = useEvent$1(event => {
    onFocusCaptureProp == null ? void 0 : onFocusCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;

    if (!isSelfTarget(event)) {
      setFocusVisible(false);
      return;
    }

    const element = event.currentTarget;

    const applyFocusVisible = () => handleFocusVisible(event, element);

    if (isKeyboardModality || isAlwaysFocusVisible(event.target)) {
      queueMicrotask$1(applyFocusVisible);
    } // See https://github.com/ariakit/ariakit/issues/1257
    else if (isAlwaysFocusVisibleDelayed(event.target)) {
      queueBeforeEvent(event.target, "focusout", applyFocusVisible);
    } else {
      setFocusVisible(false);
    }
  });
  const onBlurProp = props.onBlur; // Note: Can't use onBlurCapture here otherwise it will not work with
  // CompositeItem's with the virtualFocus state set to true.

  const onBlur = useEvent$1(event => {
    onBlurProp == null ? void 0 : onBlurProp(event);
    if (!focusable) return;

    if (isFocusEventOutside(event)) {
      setFocusVisible(false);
    }
  }); // The native autoFocus prop is problematic in many ways. For example, when
  // an element has the native autofocus attribute, the focus event will be
  // triggered before React effects (even layout effects) and before refs are
  // assigned. This means we won't have access to the element's ref or
  // anything else that's set up by React effects on the onFocus event. So we
  // don't pass the autoFocus prop to the element and instead manually focus
  // the element when it's mounted. The order in which this effect runs also
  // matters. It must be declared here after all the event callbacks above so
  // the event callback effects run before this one. See
  // https://twitter.com/diegohaz/status/1408180632933388289

  useSafeLayoutEffect(() => {
    if (!focusable) return;

    if (autoFocus) {
      var _ref$current;

      (_ref$current = ref.current) == null ? void 0 : _ref$current.focus();
    }
  }, [focusable, autoFocus]);
  const tagName = useTagName(ref, props.as);
  const nativeTabbable = focusable && isNativeTabbable(tagName);
  const supportsDisabled = focusable && supportsDisabledAttribute(tagName);
  const style = trulyDisabled ? {
    pointerEvents: "none",
    ...props.style
  } : props.style;
  props = {
    "data-focus-visible": focusable && focusVisible ? "" : undefined,
    "aria-disabled": disabled ? true : undefined,
    ...props,
    ref: useForkRef(ref, props.ref),
    style,
    tabIndex: getTabIndex(focusable, trulyDisabled, nativeTabbable, supportsDisabled, props.tabIndex),
    disabled: supportsDisabled && trulyDisabled ? true : undefined,
    // TODO: Test Focusable contentEditable.
    contentEditable: disabled ? undefined : props.contentEditable,
    onKeyPressCapture,
    onClickCapture,
    onMouseDownCapture,
    onMouseDown,
    onKeyDownCapture,
    onFocusCapture,
    onBlur
  };
  return props;
});
/**
 * A component that renders an element that can be focused.
 * @see https://ariakit.org/components/focusable
 * @example
 * ```jsx
 * <Focusable>Focusable</Focusable>
 * ```
 */

const Focusable = createComponent(props => {
  props = useFocusable(props);
  return createElement("div", props);
});

if (process.env.NODE_ENV !== "production") {
  Focusable.displayName = "Focusable";
}

function isNativeClick(event) {
  if (!event.isTrusted) return false; // istanbul ignore next: can't test trusted events yet

  const element = event.currentTarget;
  return isButton(element) || element.tagName === "SUMMARY" || element.tagName === "INPUT" || element.tagName === "TEXTAREA" || element.tagName === "A" || element.tagName === "SELECT";
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component. If the element is not a native clickable element (like a
 * button), the hook will return additional props to make sure it's accessible.
 * @see https://ariakit.org/components/command
 * @example
 * ```jsx
 * const props = useCommand({ as: "div" });
 * <Role {...props}>Accessible button</Role>
 * ```
 */


const useCommand = createHook(_ref => {
  let {
    clickOnEnter = true,
    clickOnSpace = true,
    ...props
  } = _ref;
  const ref = useRef(null);
  const tagName = useTagName(ref, props.as);
  const [isNativeButton, setIsNativeButton] = useState(() => !!tagName && isButton({
    tagName,
    type: props.type
  }));
  useEffect(() => {
    if (!ref.current) return;
    setIsNativeButton(isButton(ref.current));
  }, []);
  const [active, setActive] = useState(false);
  const activeRef = useRef(false);
  const isDuplicate = ("data-command" in props);
  const onKeyDownProp = props.onKeyDown;
  const onKeyDown = useEvent$1(event => {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    const element = event.currentTarget;
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (props.disabled) return;
    if (!isSelfTarget(event)) return;
    if (isTextField(element)) return;
    if (element.isContentEditable) return;
    const isEnter = clickOnEnter && event.key === "Enter";
    const isSpace = clickOnSpace && event.key === " ";
    const shouldPreventEnter = event.key === "Enter" && !clickOnEnter;
    const shouldPreventSpace = event.key === " " && !clickOnSpace;

    if (shouldPreventEnter || shouldPreventSpace) {
      event.preventDefault();
      return;
    }

    if (isEnter || isSpace) {
      const nativeClick = isNativeClick(event);

      if (isEnter) {
        if (!nativeClick) {
          event.preventDefault();
          const {
            view,
            ...eventInit
          } = event; // Fire a click event instead of calling element.click() directly
          // so we can pass along the modifier state.

          const click = () => fireClickEvent(element, eventInit); // If this element is a link with target="_blank", Firefox will
          // block the "popup" if the click event is dispatched synchronously
          // or in a microtask. Queueing the event asynchronously fixes that.


          if (isFirefox()) {
            queueBeforeEvent(element, "keyup", click);
          } else {
            queueMicrotask$1(click);
          }
        }
      } else if (isSpace) {
        activeRef.current = true;

        if (!nativeClick) {
          event.preventDefault();
          setActive(true);
        }
      }
    }
  });
  const onKeyUpProp = props.onKeyUp;
  const onKeyUp = useEvent$1(event => {
    onKeyUpProp == null ? void 0 : onKeyUpProp(event);
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (props.disabled) return;
    if (event.metaKey) return;
    const isSpace = clickOnSpace && event.key === " ";

    if (activeRef.current && isSpace) {
      activeRef.current = false;

      if (!isNativeClick(event)) {
        setActive(false);
        const element = event.currentTarget;
        const {
          view,
          ...eventInit
        } = event;
        queueMicrotask$1(() => fireClickEvent(element, eventInit));
      }
    }
  });
  props = {
    "data-command": "",
    "data-active": active ? "" : undefined,
    type: isNativeButton ? "button" : undefined,
    ...props,
    ref: useForkRef(ref, props.ref),
    onKeyDown,
    onKeyUp
  };
  props = useFocusable(props);
  return props;
});
/**
 * A component that renders a native clickable element (a button). If another
 * element is passed to the `as` prop, this component will make sure the
 * rendered element is accessible.
 * @see https://ariakit.org/components/command
 * @example
 * ```jsx
 * <Command as="div">Accessible button</Command>
 * ```
 */

const Command = createComponent(props => {
  props = useCommand(props);
  return createElement("button", props);
});

if (process.env.NODE_ENV !== "production") {
  Command.displayName = "Command";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component. If the element is not a native button, the hook will
 * return additional props to make sure it's accessible.
 * @see https://ariakit.org/components/button
 * @example
 * ```jsx
 * const props = useButton({ as: "div" });
 * <Role {...props}>Accessible button</Role>
 * ```
 */

const useButton = createHook(props => {
  const ref = useRef(null);
  const tagName = useTagName(ref, props.as || "button");
  const [isNativeButton, setIsNativeButton] = useState(() => !!tagName && isButton({
    tagName,
    type: props.type
  }));
  useEffect(() => {
    if (!ref.current) return;
    setIsNativeButton(isButton(ref.current));
  }, []);
  props = {
    role: !isNativeButton && tagName !== "a" ? "button" : undefined,
    ...props,
    ref: useForkRef(ref, props.ref)
  };
  props = useCommand(props);
  return props;
});
/**
 * A component that renders a native accessible button. If another element is
 * passed to the `as` prop, this component will make sure the rendered element
 * is accessible.
 * @see https://ariakit.org/components/button
 * @example
 * ```jsx
 * <Button as="div">Accessible button</Button>
 * ```
 */

const Button$2 = createComponent(props => {
  const htmlProps = useButton(props);
  return createElement("button", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Button$2.displayName = "Button";
}

const CheckboxCheckedContext = /*#__PURE__*/createContext(false);

function getStateChecked(stateValue, elementValue) {
  if (stateValue === undefined) return;

  if (elementValue) {
    if (Array.isArray(stateValue)) {
      return stateValue.includes(elementValue);
    }

    return stateValue === elementValue;
  }

  if (Array.isArray(stateValue)) {
    return false;
  }

  if (typeof stateValue === "boolean") {
    return stateValue;
  }

  return false;
}

function setMixed(element, mixed) {
  if (mixed) {
    element.indeterminate = true;
  } else if (element.indeterminate) {
    element.indeterminate = false;
  }
}

function isNativeCheckbox(tagName, type) {
  return tagName === "input" && (!type || type === "checkbox");
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component. If the element is not a native checkbox, the hook will
 * return additional props to make sure it's accessible.
 * @see https://ariakit.org/components/checkbox
 * @example
 * ```jsx
 * const props = useCheckbox({ as: "div" });
 * <Role {...props}>Accessible checkbox</Role>
 * ```
 */


const useCheckbox = createHook(_ref => {
  let {
    state,
    value,
    checked: checkedProp,
    defaultChecked,
    ...props
  } = _ref;
  const [checked, setChecked] = useControlledState(defaultChecked != null ? defaultChecked : false, checkedProp != null ? checkedProp : getStateChecked(state == null ? void 0 : state.value, value));
  const ref = useRef(null);
  const tagName = useTagName(ref, props.as || "input");
  const nativeCheckbox = isNativeCheckbox(tagName, props.type);
  const mixed = checked ? checked === "mixed" : undefined;
  const isChecked = checked === "mixed" ? false : checked;
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    setMixed(element, mixed);
    element.checked = isChecked;
  }, [mixed, isChecked]);
  const onChangeProp = props.onChange;
  const onChange = useEvent$1(event => {
    if (props.disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    setMixed(event.currentTarget, mixed);

    if (!nativeCheckbox) {
      // If the element is not a native checkbox, we need to manually update
      // its checked property.
      event.currentTarget.checked = !event.currentTarget.checked;
    }

    onChangeProp == null ? void 0 : onChangeProp(event);
    if (event.defaultPrevented) return;
    const elementChecked = event.currentTarget.checked;
    setChecked(elementChecked);
    state == null ? void 0 : state.setValue(prevValue => {
      if (!value) return elementChecked;
      if (!Array.isArray(prevValue)) return value;
      if (elementChecked) return [...prevValue, value];
      return prevValue.filter(v => v !== value);
    });
  });
  const onClickProp = props.onClick;
  const onClick = useEvent$1(event => {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (nativeCheckbox) return; // @ts-expect-error The onChange event expects a ChangeEvent, but here we
    // need to pass a MouseEvent.

    onChange(event);
  });
  props = useWrapElement(props, element => /*#__PURE__*/jsx(CheckboxCheckedContext.Provider, {
    value: isChecked,
    children: element
  }), [isChecked]);
  props = {
    role: !nativeCheckbox ? "checkbox" : undefined,
    type: nativeCheckbox ? "checkbox" : undefined,
    "aria-checked": checked,
    ...props,
    ref: useForkRef(ref, props.ref),
    onChange,
    onClick
  };
  props = useCommand({
    clickOnEnter: !nativeCheckbox,
    ...props
  });
  return {
    value: nativeCheckbox ? value : undefined,
    checked: isChecked,
    ...props
  };
});
/**
 * A component that renders a native accessible checkbox. If another element is
 * passed to the `as` prop, this component will make sure the rendered element
 * is accessible.
 * @see https://ariakit.org/components/checkbox
 * @example
 * ```jsx
 * <Checkbox as="div">Accessible checkbox</Checkbox>
 * ```
 */

const Checkbox$1 = createComponent(props => {
  const htmlProps = useCheckbox(props);
  return createElement("input", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Checkbox$1.displayName = "Checkbox";
}

/**
 * Transforms `arg` into an array if it's not already.
 * @example
 * toArray("a"); // ["a"]
 * toArray(["a"]); // ["a"]
 */
function toArray$1(arg) {
  if (Array.isArray(arg)) {
    return arg;
  }

  return typeof arg !== "undefined" ? [arg] : [];
}
/**
 * Immutably adds an index to an array.
 * @example
 * addItemToArray(["a", "b", "d"], "c", 2); // ["a", "b", "c", "d"]
 * @returns {Array} A new array with the item in the passed array index.
 */

function addItemToArray(array, item, index) {
  if (index === void 0) {
    index = -1;
  }

  if (!(index in array)) {
    return [...array, item];
  }

  return [...array.slice(0, index), item, ...array.slice(index)];
}
/**
 * Flattens a 2D array into a one-dimensional array.
 * @example
 * flatten2DArray([["a"], ["b"], ["c"]]); // ["a", "b", "c"]
 *
 * @returns {Array} A one-dimensional array.
 */

function flatten2DArray(array) {
  const flattened = [];

  for (const row of array) {
    flattened.push(...row);
  }

  return flattened;
}
/**
 * Immutably reverses an array.
 * @example
 * reverseArray(["a", "b", "c"]); // ["c", "b", "a"]
 * @returns {Array} Reversed array.
 */

function reverseArray(array) {
  return array.slice().reverse();
}

const GET_STATE = Symbol("getState");
const SUBSCRIBE$1 = Symbol("subscribe");
const TIMESTAMP = Symbol("timestamp");
const INITIAL_CONTEXT = Symbol("initialContext");

function getState(state) {
  if (!state) return state;
  const fn = state[GET_STATE];
  if (fn) return fn();
  return state;
}

function hasSubscribe(state) {
  if (!state) return false;
  return !!state[SUBSCRIBE$1];
}

function getSubscribe(state) {
  if (!state) return;
  return state[SUBSCRIBE$1];
}

function getLatest(a, b) {
  if (!b) return a;
  if (!a) return b;
  if (!(TIMESTAMP in b)) return a;
  if (!(TIMESTAMP in a)) return b;
  if (a[TIMESTAMP] >= b[TIMESTAMP]) return a;
  return b;
}

function defineGetState(state, currentState) {
  if (currentState === void 0) {
    currentState = state;
  }

  Object.defineProperty(state, GET_STATE, {
    value: () => currentState,
    writable: true
  });
}

function defineSubscribe(state, subscribe) {
  if (!(SUBSCRIBE$1 in state)) {
    Object.defineProperty(state, SUBSCRIBE$1, {
      value: subscribe
    });
  }
}

function defineTimestamp(state) {
  if (!(TIMESTAMP in state)) {
    Object.defineProperty(state, TIMESTAMP, {
      value: Date.now(),
      writable: true
    });
  }
}

function patchState(state) {
  Object.defineProperty(state, TIMESTAMP, {
    value: Date.now(),
    writable: true
  });
}

function defineInitialContext(context) {
  const initialContext = /*#__PURE__*/createContext(undefined);
  Object.defineProperty(context, INITIAL_CONTEXT, {
    value: initialContext
  });
  return initialContext;
}

function hasInitialContext(stateOrContext) {
  return stateOrContext && INITIAL_CONTEXT in stateOrContext;
}

function getInitialContext(context) {
  if (!hasInitialContext(context)) return;
  const ctx = context;
  return ctx[INITIAL_CONTEXT];
}
/**
 * Creates a context that can be passed to `useStore` and `useStoreProvider`.
 */


function createStoreContext() {
  const context = /*#__PURE__*/createContext(undefined);
  defineInitialContext(context);
  return context;
}
/**
 * Creates a type-safe component with the `as` prop, `state` prop,
 * `React.forwardRef` and `React.memo`.
 *
 * @example
 * import { Options, createMemoComponent } from "ariakit-utils/store";
 *
 * type Props = Options<"div"> & {
 *   state?: { customProp: boolean };
 * };
 *
 * const Component = createMemoComponent<Props>(
 *   ({ state, ...props }) => <div {...props} />
 * );
 *
 * <Component as="button" state={{ customProp: true }} />
 */

function createMemoComponent(render, propsAreEqual) {
  if (propsAreEqual === void 0) {
    propsAreEqual = shallowEqual;
  }

  const Role = createComponent(render);
  return /*#__PURE__*/memo(Role, (prev, next) => {
    const {
      state: prevState,
      ...prevProps
    } = prev;
    const {
      state: nextState,
      ...nextProps
    } = next;

    if (nextState && hasSubscribe(nextState)) {
      return propsAreEqual(prevProps, nextProps);
    }

    return propsAreEqual(prev, next);
  });
}
/**
 * Returns props with a `wrapElement` function that wraps an element with a
 * React Context Provider that provides a store context to consumers.
 * @example
 * import * as React from "react";
 * import { useStoreProvider } from "ariakit-utils/store";
 *
 * const StoreContext = createStoreContext();
 *
 * function Component({ state, ...props }) {
 *   const { wrapElement } = useStoreProvider({ state, ...props }, StoreContext);
 *   return wrapElement(<div {...props} />);
 * }
 */

function useStoreProvider(_ref, context) {
  let {
    state,
    ...props
  } = _ref;
  const initialValue = useInitialValue$1(state);
  const value = state && hasSubscribe(state) ? initialValue : state;
  defineGetState(value, state);
  const initialContext = getInitialContext(context);
  return useWrapElement(props, element => {
    if (value && initialContext) {
      element = /*#__PURE__*/jsx(initialContext.Provider, {
        value: value,
        children: element
      });
    }

    if (state) {
      element = /*#__PURE__*/jsx(context.Provider, {
        value: state,
        children: element
      });
    }

    return element;
  }, [value, initialContext, state, context]);
}
/**
 * Adds publishing capabilities to state so it can be passed to `useStore` or
 * `useStoreProvider` later.
 * @example
 * import { useStorePublisher } from "ariakit-utils/store";
 *
 * function useComponentState() {
 *   const state = React.useMemo(() => ({ a: "a" }), []);
 *   return useStorePublisher(state);
 * }
 */

function useStorePublisher(state) {
  const listeners = useLazyValue(() => new Set());
  useSafeLayoutEffect(() => {
    patchState(state);

    for (const listener of listeners) {
      listener(state);
    }
  }, [state]);
  const subscribe = useCallback(listener => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
  defineSubscribe(state, subscribe);
  defineGetState(state);
  defineTimestamp(state);
  return state;
}
/**
 * Handles state updates on the state or context state passed as the first
 * argument based on the filter argument.
 * @example
 * import { useStore } from "ariakit-utils/store";
 *
 * const ContextState = createContextState();
 *
 * function Component({ state }) {
 *   state = useStore(state || ContextState, ["stateProp"]);
 * }
 */

function useStore(stateOrContext, filter) {
  const contextState = useContext(getContext(stateOrContext, filter));
  const externalState = hasInitialContext(stateOrContext) ? contextState : stateOrContext;
  const [internalState, setState] = useState(() => getState(externalState));
  const state = hasSubscribe(externalState) && hasSubscribe(internalState) ? getLatest(internalState, externalState) : externalState;
  const subscribe = getSubscribe(externalState);
  const prevStateRef = useRef(null);
  const deps = toArray$1(filter);
  const noFilter = !filter;
  useSafeLayoutEffect(() => {
    if (!subscribe || !setState) return;
    if (noFilter) return subscribe(setState);
    if (!deps.length) return;
    return subscribe(nextState => {
      const prevState = prevStateRef.current;
      prevStateRef.current = nextState;

      const filterDep = dep => {
        if (typeof dep === "function") {
          const result = dep(nextState); // TODO: We probably need different functions for:
          // useStore(context, [(nextState) => nextState.activeId === id]);
          // useStore(context, [(nextState) => nextState.booleanProp]);
          // Because in the second case we want to compare the result with the
          // previous state result.

          if (typeof result === "boolean") {
            return result || prevState && dep(prevState);
          } else if (prevState) {
            return result !== dep(prevState);
          }

          return result;
        }

        const key = dep;
        return (prevState == null ? void 0 : prevState[key]) !== nextState[key];
      };

      if (deps.some(filterDep)) {
        setState(nextState);
      }
    });
  }, [subscribe, setState, noFilter, ...deps]);
  return state;
}
const EmptyContext = /*#__PURE__*/createContext(undefined);

function getContext(stateOrContext, filter) {
  if (!hasInitialContext(stateOrContext)) {
    return EmptyContext;
  }

  if (filter) {
    return getInitialContext(stateOrContext);
  }

  return stateOrContext;
}

const CollectionItemContext = /*#__PURE__*/createContext(undefined);

function identity$9(value) {
  return value;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component. This hook will register the item in the collection state.
 * If this hook is used in a component that is wrapped by `Collection` or a
 * component that implements `useCollection`, there's no need to explicitly pass
 * the `state` prop.
 * @see https://ariakit.org/components/collection
 * @example
 * ```jsx
 * const state = useCollectionState();
 * const props = useCollectionItem({ state });
 * <Role {...props}>Item</Role>
 * ```
 */


const useCollectionItem = createHook(_ref => {
  var _state;

  let {
    state,
    shouldRegisterItem = true,
    getItem = identity$9,
    ...props
  } = _ref;
  state = useStore(state, ["registerItem"]);
  const contextRegisterItem = useContext(CollectionItemContext);
  const registerItem = ((_state = state) == null ? void 0 : _state.registerItem) || contextRegisterItem;
  const ref = useRef(null);
  useSafeLayoutEffect(() => {
    if (!shouldRegisterItem) return;
    return registerItem == null ? void 0 : registerItem(getItem({
      ref
    }));
  }, [shouldRegisterItem, getItem, registerItem]);
  props = { ...props,
    ref: useForkRef(ref, props.ref)
  };
  return props;
});
/**
 * A component that renders an item in a collection. The collection state can be
 * passed explicitly through the `state` prop or implicitly through the
 * `Collection` component.
 * @see https://ariakit.org/components/collection
 * @example
 * ```jsx
 * const state = useCollectionState();
 * <CollectionItem state={state}>Item 1</CollectionItem>
 * <CollectionItem state={state}>Item 2</CollectionItem>
 * <CollectionItem state={state}>Item 3</CollectionItem>
 * ```
 */

const CollectionItem = createComponent(props => {
  const htmlProps = useCollectionItem(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  CollectionItem.displayName = "CollectionItem";
}

function isElementPreceding(a, b) {
  return Boolean(b.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_PRECEDING);
}

function findDOMIndex(items, item) {
  const itemElement = item.ref.current;
  if (!itemElement) return -1;
  let length = items.length;
  if (!length) return -1; // Most of the times, the new item will be added at the end of the list, so we
  // do a findeIndex in reverse order, instead of wasting time searching the
  // index from the beginning.

  while (length--) {
    const currentItem = items[length];
    if (!(currentItem != null && currentItem.ref.current)) continue;

    if (isElementPreceding(currentItem.ref.current, itemElement)) {
      return length + 1;
    }
  }

  return 0;
}

function sortBasedOnDOMPosition(items) {
  const pairs = items.map((item, index) => [index, item]);
  let isOrderDifferent = false;
  pairs.sort((_ref, _ref2) => {
    let [indexA, a] = _ref;
    let [indexB, b] = _ref2;
    const elementA = a.ref.current;
    const elementB = b.ref.current;
    if (elementA === elementB) return 0;
    if (!elementA || !elementB) return 0; // a before b

    if (isElementPreceding(elementA, elementB)) {
      if (indexA > indexB) {
        isOrderDifferent = true;
      }

      return -1;
    } // a after b


    if (indexA < indexB) {
      isOrderDifferent = true;
    }

    return 1;
  });

  if (isOrderDifferent) {
    return pairs.map(_ref3 => {
      let [_, item] = _ref3;
      return item;
    });
  }

  return items;
}

function setItemsBasedOnDOMPosition(items, setItems) {
  const sortedItems = sortBasedOnDOMPosition(items);

  if (items !== sortedItems) {
    setItems(sortedItems);
  }
}

function getCommonParent(items) {
  var _firstItem$ref$curren;

  const firstItem = items[0];
  const lastItem = items[items.length - 1];
  let parentElement = firstItem == null ? void 0 : (_firstItem$ref$curren = firstItem.ref.current) == null ? void 0 : _firstItem$ref$curren.parentElement;

  while (parentElement) {
    const parent = parentElement;

    if (lastItem && parent.contains(lastItem.ref.current)) {
      return parentElement;
    }

    parentElement = parentElement.parentElement;
  }

  return getDocument(parentElement).body;
}

function useTimeoutObserver(items, setItems) {
  useEffect(() => {
    const callback = () => setItemsBasedOnDOMPosition(items, setItems);

    const timeout = setTimeout(callback);
    return () => clearTimeout(timeout);
  });
}

function useSortBasedOnDOMPosition(items, setItems) {
  // istanbul ignore else: JSDOM doesn't support IntersectionObverser
  // See https://github.com/jsdom/jsdom/issues/2032
  if (typeof IntersectionObserver !== "function") {
    useTimeoutObserver(items, setItems);
    return;
  }

  const previousItems = useRef([]);
  useEffect(() => {
    const callback = () => {
      const hasPreviousItems = !!previousItems.current.length;
      previousItems.current = items; // We don't want to sort items if items have been just registered.

      if (!hasPreviousItems) return;
      setItemsBasedOnDOMPosition(items, setItems);
    };

    const root = getCommonParent(items);
    const observer = new IntersectionObserver(callback, {
      root
    });
    items.forEach(item => {
      if (item.ref.current) {
        observer.observe(item.ref.current);
      }
    });
    return () => observer.disconnect();
  }, [items, setItems]);
}
/**
 * Provides state for the `Collection` components.
 * @see https://ariakit.org/components/collection
 * @example
 * ```jsx
 * const collection = useCollectionState();
 * <Collection state={collection}>
 *   <CollectionItem>Item 1</CollectionItem>
 *   <CollectionItem>Item 2</CollectionItem>
 *   <CollectionItem>Item 3</CollectionItem>
 * </Collection>
 * ```
 */


function useCollectionState(props) {
  if (props === void 0) {
    props = {};
  }

  const [items, setItems] = useControlledState([], props.items, props.setItems);
  useSortBasedOnDOMPosition(items, setItems);
  const registerItem = useCallback(item => {
    setItems(prevItems => {
      // Finds the item group based on the DOM hierarchy
      const index = findDOMIndex(prevItems, item);
      return addItemToArray(prevItems, item, index);
    });

    const unregisterItem = () => {
      setItems(prevItems => {
        const nextItems = prevItems.filter(_ref4 => {
          let {
            ref
          } = _ref4;
          return ref !== item.ref;
        });

        if (prevItems.length === nextItems.length) {
          // The item isn't registered, so do nothing
          return prevItems;
        }

        return nextItems;
      });
    };

    return unregisterItem;
  }, []);
  const state = useMemo(() => ({
    items,
    setItems,
    registerItem
  }), [items, setItems, registerItem]);
  return state;
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element that controls the visibility of a
 * disclosure content element.
 * @see https://ariakit.org/components/disclosure
 * @example
 * ```jsx
 * const state = useDisclosureState();
 * const props = useDisclosure({ state });
 * <Role {...props}>Disclosure</Role>
 * <DisclosureContent state={state}>Content</DisclosureContent>
 * ```
 */
const useDisclosure = createHook(_ref => {
  var _state$contentElement;

  let {
    state,
    toggleOnClick = true,
    ...props
  } = _ref;
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false); // Assigns the disclosureRef whenever it's undefined or disconnected from
  // the DOM. If this disclosure element is the disclosureRef, this element
  // will get the `aria-expanded` attribute set to `true` when the disclosure
  // content is open.

  useSafeLayoutEffect(() => {
    const currentDisclosure = state.disclosureRef.current;

    if (!currentDisclosure || !currentDisclosure.isConnected) {
      state.disclosureRef.current = ref.current;
    }

    const isCurrentDisclosure = state.disclosureRef.current === ref.current;
    setExpanded(state.open && isCurrentDisclosure);
  }, [state.disclosureRef, state.open]);
  const onMouseDownProp = props.onMouseDown;
  const onMouseDown = useEvent$1(event => {
    state.disclosureRef.current = event.currentTarget;
    onMouseDownProp == null ? void 0 : onMouseDownProp(event);
  });
  const onClickProp = props.onClick;
  const toggleOnClickProp = useBooleanEvent(toggleOnClick);
  const isDuplicate = ("data-disclosure" in props);
  const onClick = useEvent$1(event => {
    state.disclosureRef.current = event.currentTarget;
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (!toggleOnClickProp(event)) return;
    state.toggle();
  });
  props = {
    "data-disclosure": "",
    "aria-expanded": expanded,
    "aria-controls": (_state$contentElement = state.contentElement) == null ? void 0 : _state$contentElement.id,
    ...props,
    ref: useForkRef(ref, props.ref),
    onMouseDown,
    onClick
  };
  props = useButton(props);
  return props;
});
/**
 * A component that renders an element that controls the visibility of a
 * disclosure content element.
 * @see https://ariakit.org/components/disclosure
 * @example
 * ```jsx
 * const disclosure = useDisclosureState();
 * <Disclosure state={disclosure}>Disclosure</Disclosure>
 * <DisclosureContent state={disclosure}>Content</DisclosureContent>
 * ```
 */

const Disclosure = createComponent(props => {
  const htmlProps = useDisclosure(props);
  return createElement("button", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Disclosure.displayName = "Disclosure";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a button that shows/hides a dialog.
 * @see https://ariakit.org/components/dialog
 * @example
 * ```jsx
 * const state = useDialogState();
 * const props = useDialogDisclosure({ state });
 * <Role {...props}>Disclosure</Role>
 * <Dialog state={state}>Content</Dialog>
 * ```
 */
const useDialogDisclosure = createHook(_ref => {
  let {
    state,
    ...props
  } = _ref;
  props = useDisclosure({
    state,
    ...props
  });
  return props;
});
/**
 * A component that renders a button that shows/hides a dialog.
 * @see https://ariakit.org/components/dialog
 * @example
 * ```jsx
 * const dialog = useDialogState();
 * <DialogDisclosure state={dialog}>Disclosure</DialogDisclosure>
 * <Dialog state={dialog}>Content</Dialog>
 * ```
 */

const DialogDisclosure = createComponent(props => {
  const htmlProps = useDialogDisclosure(props);
  return createElement("button", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  DialogDisclosure.displayName = "DialogDisclosure";
}

const GroupLabelContext = /*#__PURE__*/createContext(undefined);

const useGroup = createHook(props => {
  const [labelId, setLabelId] = useState();
  props = useWrapElement(props, element => /*#__PURE__*/jsx(GroupLabelContext.Provider, {
    value: setLabelId,
    children: element
  }), []);
  props = {
    role: "group",
    "aria-labelledby": labelId,
    ...props
  };
  return props;
});
/**
 * A component that renders a group element.
 * @see https://ariakit.org/components/group
 * @example
 * ```jsx
 * <Group>Group</Group>
 * ```
 */

const Group = createComponent(props => {
  const htmlProps = useGroup(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Group.displayName = "Group";
}

const NULL_ITEM = {
  id: null,
  ref: {
    current: null
  }
};

function getMaxRowLength(array) {
  let maxLength = 0;

  for (const {
    length
  } of array) {
    if (length > maxLength) {
      maxLength = length;
    }
  }

  return maxLength;
}
/**
 * Returns only enabled items.
 */


function getEnabledItems$1(items, excludeId) {
  return items.filter(item => {
    if (excludeId) {
      return !item.disabled && item.id !== excludeId;
    }

    return !item.disabled;
  });
}
/**
 * Finds the first enabled item.
 */

function findFirstEnabledItem(items, excludeId) {
  return items.find(item => {
    if (excludeId) {
      return !item.disabled && item.id !== excludeId;
    }

    return !item.disabled;
  });
}
/**
 * Fills rows with fewer items with empty items so they all have the same
 * length.
 */

function normalizeRows(rows, activeId, focusShift) {
  const maxLength = getMaxRowLength(rows);

  for (const row of rows) {
    for (let i = 0; i < maxLength; i += 1) {
      const item = row[i];

      if (!item || focusShift && item.disabled) {
        const isFirst = i === 0;
        const previousItem = isFirst && focusShift ? findFirstEnabledItem(row) : row[i - 1];
        row[i] = previousItem && activeId !== previousItem.id && focusShift ? previousItem : createEmptyItem(previousItem == null ? void 0 : previousItem.rowId);
      }
    }
  }

  return rows;
}

function createEmptyItem(rowId) {
  return {
    id: "__EMPTY_ITEM__",
    disabled: true,
    ref: {
      current: null
    },
    rowId
  };
}
/**
 * Finds the first enabled item by its id.
 */


function findEnabledItemById(items, id) {
  if (!id) return;
  return items.find(item => item.id === id && !item.disabled);
}
/**
 * Gets the active id. If `passedId` is provided, it's going to take
 * precedence.
 */

function getActiveId(items, activeId, passedId) {
  var _findFirstEnabledItem;

  if (passedId !== undefined) {
    return passedId;
  }

  if (activeId !== undefined) {
    return activeId;
  }

  return (_findFirstEnabledItem = findFirstEnabledItem(items)) == null ? void 0 : _findFirstEnabledItem.id;
}
/**
 * Gets all items with the passed rowId.
 */

function getItemsInRow(items, rowId) {
  return items.filter(item => item.rowId === rowId);
}
/**
 * Gets the opposite orientation.
 */

function getOppositeOrientation(orientation) {
  if (orientation === "vertical") return "horizontal";
  if (orientation === "horizontal") return "vertical";
  return;
}
/**
 * Creates a two-dimensional array with items grouped by their rowId's.
 */

function groupItemsByRows(items) {
  const rows = [];

  for (const item of items) {
    const row = rows.find(currentRow => {
      var _currentRow$;

      return ((_currentRow$ = currentRow[0]) == null ? void 0 : _currentRow$.rowId) === item.rowId;
    });

    if (row) {
      row.push(item);
    } else {
      rows.push([item]);
    }
  }

  return rows;
}
/**
 * Moves all the items before the passed `id` to the end of the array. This is
 * useful when we want to loop through the items in the same row or column as
 * the first items will be placed after the last items.
 *
 * The null item that's inserted when `shouldInsertNullItem` is set to `true`
 * represents the composite container itself. When the active item is null, the
 * composite container has focus.
 */

function flipItems(items, activeId, shouldInsertNullItem) {
  if (shouldInsertNullItem === void 0) {
    shouldInsertNullItem = false;
  }

  const index = items.findIndex(item => item.id === activeId);
  return [...items.slice(index + 1), ...(shouldInsertNullItem ? [NULL_ITEM] : []), ...items.slice(0, index)];
}
/**
 * Changes the order of the items list so they are ordered vertically. That is,
 * if the active item is the first item in the first row, the next item will be
 * the first item in the second row, which is what you would expect when moving
 * up/down.
 */

function verticalizeItems(items) {
  const rows = groupItemsByRows(items);
  const maxLength = getMaxRowLength(rows);
  const verticalized = [];

  for (let i = 0; i < maxLength; i += 1) {
    for (const row of rows) {
      const item = row[i];

      if (item) {
        verticalized.push({ ...item,
          // If there's no rowId, it means that it's not a grid composite, but
          // a single row instead. So, instead of verticalizing it, that is,
          // assigning a different rowId based on the column index, we keep it
          // undefined so they will be part of the same row. This is useful
          // when using up/down on one-dimensional composites.
          rowId: item.rowId ? "" + i : undefined
        });
      }
    }
  }

  return verticalized;
}
/**
 * Gets item id.
 */

function getContextId(state, context) {
  return context != null && context.baseRef && context.baseRef === (state == null ? void 0 : state.baseRef) ? context.id : undefined;
}
const FOCUS_SILENTLY = Symbol("FOCUS_SILENTLY");

/**
 * Focus an element with a flag. The `silentlyFocused` function needs to be
 * called later to check if the focus was silenced and to reset this state.
 */
function focusSilently(element) {
  element[FOCUS_SILENTLY] = true;
  element.focus();
}
/**
 * Checks whether the element has been focused with the `focusSilently` function
 * and resets the state.
 */

function silentlyFocused(element) {
  const isSilentlyFocused = element[FOCUS_SILENTLY];
  delete element[FOCUS_SILENTLY];
  return isSilentlyFocused;
}
/**
 * Determines whether the element is a composite item.
 */

function isItem(items, element, exclude) {
  if (!element) return false;
  return items.some(item => {
    if (exclude && item.ref.current === exclude) return false;
    return item.ref.current === element;
  });
}
const CompositeContext = createStoreContext();
const CompositeRowContext = /*#__PURE__*/createContext(undefined);
const CompositeItemContext = /*#__PURE__*/createContext(undefined);

function getMouseDestination(event) {
  const relatedTarget = event.relatedTarget;

  if ((relatedTarget == null ? void 0 : relatedTarget.nodeType) === Node.ELEMENT_NODE) {
    return relatedTarget;
  }

  return null;
}

function hoveringInside(event) {
  const nextElement = getMouseDestination(event);
  if (!nextElement) return false;
  return contains(event.currentTarget, nextElement);
}

function movingToAnotherItem(event) {
  const dest = getMouseDestination(event);
  if (!dest) return false;
  const item = closest(dest, "[data-composite-hover]");
  return !!item;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element in a composite widget that receives
 * focus on mouse move and loses focus to the composite base element on mouse
 * leave. This should be combined with the `CompositeItem` component, the
 * `useCompositeItem` hook or any component/hook that uses them underneath.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const state = useCompositeState();
 * const props = useCompositeHover({ state });
 * <CompositeItem state={state} {...props}>Item</CompositeItem>
 * ```
 */


const useCompositeHover = createHook(_ref => {
  let {
    state,
    focusOnHover = true,
    ...props
  } = _ref;
  state = useStore(state || CompositeContext, ["setActiveId", "baseRef"]);
  const isMouseMoving = useIsMouseMoving();
  const onMouseMoveProp = props.onMouseMove;
  const focusOnHoverProp = useBooleanEvent(focusOnHover);
  const onMouseMove = useEvent$1(event => {
    var _state2;

    onMouseMoveProp == null ? void 0 : onMouseMoveProp(event);
    if (event.defaultPrevented) return;
    if (!isMouseMoving()) return;
    if (!focusOnHoverProp(event)) return; // If we're hovering over an item that doesn't have DOM focus, we move
    // focus to the composite element. We're doing this here before setting
    // the active id because the composite element will automatically set the
    // active id to null when it receives focus.

    if (!hasFocusWithin(event.currentTarget)) {
      var _state, _state$baseRef$curren;

      (_state = state) == null ? void 0 : (_state$baseRef$curren = _state.baseRef.current) == null ? void 0 : _state$baseRef$curren.focus();
    }

    (_state2 = state) == null ? void 0 : _state2.setActiveId(event.currentTarget.id);
  });
  const onMouseLeaveProp = props.onMouseLeave;
  const onMouseLeave = useEvent$1(event => {
    var _state3, _state4, _state4$baseRef$curre;

    onMouseLeaveProp == null ? void 0 : onMouseLeaveProp(event);
    if (event.defaultPrevented) return;
    if (!isMouseMoving()) return;
    if (hoveringInside(event)) return;
    if (movingToAnotherItem(event)) return;
    if (!focusOnHoverProp(event)) return;
    (_state3 = state) == null ? void 0 : _state3.setActiveId(null); // Move focus to the composite element.

    (_state4 = state) == null ? void 0 : (_state4$baseRef$curre = _state4.baseRef.current) == null ? void 0 : _state4$baseRef$curre.focus();
  });
  props = {
    "data-composite-hover": "",
    ...props,
    onMouseMove,
    onMouseLeave
  };
  return props;
});
/**
 * A component that renders an element in a composite widget that receives focus
 * on mouse move and loses focus to the composite base element on mouse leave.
 * This should be combined with the `CompositeItem` component, the
 * `useCompositeItem` hook or any component/hook that uses them underneath.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const composite = useCompositeState();
 * <Composite state={composite}>
 *   <CompositeHover as={CompositeItem}>Item</CompositeHover>
 * </Composite>
 * ```
 */

const CompositeHover = createMemoComponent(props => {
  const htmlProps = useCompositeHover(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  CompositeHover.displayName = "CompositeHover";
}

function isEditableElement(element) {
  if (element.isContentEditable) return true;
  if (isTextField(element)) return true;
  return element.tagName === "INPUT" && !isButton(element);
}

function getNextPageOffset(scrollingElement, pageUp) {
  if (pageUp === void 0) {
    pageUp = false;
  }

  const height = scrollingElement.clientHeight;
  const {
    top
  } = scrollingElement.getBoundingClientRect(); // Calculates the size of the page based on the scrolling element's height.
  // This is similar to how browsers calculate the scroll position when pressing
  // spacebar, page up, or page down.

  const pageSize = Math.max(height * 0.875, height - 40) * 1.5;
  const pageOffset = pageUp ? height - pageSize + top : pageSize + top;

  if (scrollingElement.tagName === "HTML") {
    return pageOffset + scrollingElement.scrollTop;
  }

  return pageOffset;
}

function getItemOffset(itemElement, pageUp) {
  if (pageUp === void 0) {
    pageUp = false;
  }

  const {
    top
  } = itemElement.getBoundingClientRect();

  if (pageUp) {
    // PageUp is always the inverse of PageDown. On PageDown, we consider only
    // the top offset of the element. On PageUp we need to add the height of the
    // element as well so we consider the bottom of it.
    return top + itemElement.clientHeight;
  }

  return top;
}

function findNextPageItemId(element, items, next, pageUp) {
  if (pageUp === void 0) {
    pageUp = false;
  }

  if (!items) return;
  if (!next) return;
  const scrollingElement = getScrollingElement(element);
  if (!scrollingElement) return;
  const nextPageOffset = getNextPageOffset(scrollingElement, pageUp);
  let id;
  let prevDifference; // We need to loop through the next items to find the one that is closest to
  // the next page offset.

  for (let i = 0; i < items.length; i += 1) {
    const previousId = id;
    id = next(i);
    if (!id) break;
    if (id === previousId) continue;
    const item = findEnabledItemById(items, id);
    const itemElement = item == null ? void 0 : item.ref.current;
    if (!itemElement) continue;
    const itemOffset = getItemOffset(itemElement, pageUp);
    const difference = itemOffset - nextPageOffset;
    const absDifference = Math.abs(difference); // On PageUp, the element is at the next page if the difference between its
    // top offset (plus its height) and the next page offset is less than or
    // equal zero. On PageDown, the difference should be greater than or equal
    // zero.

    if (pageUp && difference <= 0 || !pageUp && difference >= 0) {
      // There may be cases when there's a lot of space between the pages, for
      // example, when there's a lot of disabled items. In this case, the first
      // item in the next page might not be the closest one. So we return the
      // previous item id if its difference is less than the current one.
      if (prevDifference !== undefined && prevDifference < absDifference) {
        id = previousId;
      }

      break;
    }

    prevDifference = absDifference;
  }

  return id;
}

function useItem$1(items, id) {
  return useMemo(() => {
    if (!id) return;
    return items == null ? void 0 : items.find(item => item.id === id);
  }, [items, id]);
}

function targetIsAnotherItem(event, items) {
  if (isSelfTarget(event)) return false;
  const target = event.target;
  return isItem(items, target, event.currentTarget);
}

function useRole(ref, props) {
  const [role, setRole] = useState(props.role);
  useSafeLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    setRole(element.getAttribute("role") || props.role);
  }, [props.role]);
  return role;
}

function requiresAriaSelected(role) {
  return role === "option" || role === "treeitem";
}

function supportsAriaSelected(role) {
  if (role === "option") return true;
  if (role === "tab") return true;
  if (role === "treeitem") return true;
  if (role === "gridcell") return true;
  if (role === "row") return true;
  if (role === "columnheader") return true;
  if (role === "rowheader") return true;
  return false;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a composite item.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const state = useCompositeState();
 * const props = useCompositeItem({ state });
 * <Role {...props}>Item 1</Role>
 * ```
 */


const useCompositeItem = createHook(_ref => {
  var _state6, _state23, _state24, _state26, _state27;

  let {
    state,
    rowId: rowIdProp,
    preventScrollOnKeyDown = false,
    moveOnKeyPress = true,
    getItem: getItemProp,
    ...props
  } = _ref;
  const id = useId(props.id);
  state = useStore(state || CompositeContext, [useCallback(s => s.activeId === id, [id]), "baseRef", "items", "virtualFocus", "registerItem", "setActiveId", "orientation", "up", "next", "down", "previous", "first", "last", "move"]);
  const ref = useRef(null);
  const row = useContext(CompositeRowContext);
  const rowId = rowIdProp != null ? rowIdProp : getContextId(state, row);
  const trulyDisabled = props.disabled && !props.accessibleWhenDisabled;
  const getItem = useCallback(item => {
    const nextItem = { ...item,
      id,
      rowId,
      disabled: !!trulyDisabled
    };

    if (getItemProp) {
      return getItemProp(nextItem);
    }

    return nextItem;
  }, [id, rowId, trulyDisabled, getItemProp]);
  const onFocusProp = props.onFocus;
  const hasFocusedComposite = useRef(false);
  const onFocus = useEvent$1(event => {
    var _state, _state2, _state4;

    onFocusProp == null ? void 0 : onFocusProp(event);
    if (event.defaultPrevented) return;
    if (isPortalEvent(event)) return;
    if (!id) return; // If the target is another item, this probably means that composite items
    // are nested. This is okay when building, for example, tree or treegrid
    // elements. In this case, we just ignore the focus event on this parent
    // item.

    if ((_state = state) != null && _state.items && targetIsAnotherItem(event, state.items)) return;

    if (((_state2 = state) == null ? void 0 : _state2.activeId) !== id) {
      var _state3;

      (_state3 = state) == null ? void 0 : _state3.setActiveId(id);
    } // When using aria-activedescendant, we want to make sure that the
    // composite container receives focus, not the composite item.


    if (!((_state4 = state) != null && _state4.virtualFocus)) return; // But we'll only do this if the focused element is the composite item
    // itself

    if (!isSelfTarget(event)) return; // and the composite item is not a text field or contenteditable element.

    if (isEditableElement(event.currentTarget)) return;
    const composite = state.baseRef.current;
    if (!composite) return;
    hasFocusedComposite.current = true; // If the previously focused element is a composite or composite item
    // component, we'll transfer focus silently to the composite element.
    // That's because this is just a transition event, the composite element
    // was likely already focused, so we're just immediately returning focus
    // to it when navigating through the items.

    const fromComposite = event.relatedTarget === composite || isItem(state.items, event.relatedTarget);

    if (fromComposite) {
      focusSilently(composite);
    } // Otherwise, the composite element is likely not focused, so we need this
    // focus event to propagate so consumers can use the onFocus prop on
    // <Composite>.
    else {
      composite.focus();
    }
  });
  const onBlurCaptureProp = props.onBlurCapture;
  const onBlurCapture = useEvent$1(event => {
    var _state5;

    onBlurCaptureProp == null ? void 0 : onBlurCaptureProp(event);
    if (event.defaultPrevented) return;

    if ((_state5 = state) != null && _state5.virtualFocus && hasFocusedComposite.current) {
      // When hasFocusedComposite is true, composite has been focused right
      // after focusing on this item. This is an intermediate blur event, so
      // we ignore it.
      hasFocusedComposite.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  });
  const onKeyDownProp = props.onKeyDown;
  const preventScrollOnKeyDownProp = useBooleanEvent(preventScrollOnKeyDown);
  const moveOnKeyPressProp = useBooleanEvent(moveOnKeyPress);
  const item = useItem$1((_state6 = state) == null ? void 0 : _state6.items, id);
  const isGrid = !!(item != null && item.rowId);
  const onKeyDown = useEvent$1(event => {
    var _state7, _state8, _state9, _state10, _state11, _state12;

    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;
    if (!isSelfTarget(event)) return;
    const isVertical = ((_state7 = state) == null ? void 0 : _state7.orientation) !== "horizontal";
    const isHorizontal = ((_state8 = state) == null ? void 0 : _state8.orientation) !== "vertical";
    const keyMap = {
      ArrowUp: (isGrid || isVertical) && ((_state9 = state) == null ? void 0 : _state9.up),
      ArrowRight: (isGrid || isHorizontal) && ((_state10 = state) == null ? void 0 : _state10.next),
      ArrowDown: (isGrid || isVertical) && ((_state11 = state) == null ? void 0 : _state11.down),
      ArrowLeft: (isGrid || isHorizontal) && ((_state12 = state) == null ? void 0 : _state12.previous),
      Home: () => {
        var _state14;

        if (!isGrid || event.ctrlKey) {
          var _state13;

          return (_state13 = state) == null ? void 0 : _state13.first();
        }

        return (_state14 = state) == null ? void 0 : _state14.previous(-1);
      },
      End: () => {
        var _state16;

        if (!isGrid || event.ctrlKey) {
          var _state15;

          return (_state15 = state) == null ? void 0 : _state15.last();
        }

        return (_state16 = state) == null ? void 0 : _state16.next(-1);
      },
      PageUp: () => {
        var _state17, _state18;

        return findNextPageItemId(event.currentTarget, (_state17 = state) == null ? void 0 : _state17.items, (_state18 = state) == null ? void 0 : _state18.up, true);
      },
      PageDown: () => {
        var _state19, _state20;

        return findNextPageItemId(event.currentTarget, (_state19 = state) == null ? void 0 : _state19.items, (_state20 = state) == null ? void 0 : _state20.down);
      }
    };
    const action = keyMap[event.key];

    if (action) {
      const nextId = action();

      if (preventScrollOnKeyDownProp(event) || nextId !== undefined) {
        var _state21;

        if (!moveOnKeyPressProp(event)) return;
        event.preventDefault();
        (_state21 = state) == null ? void 0 : _state21.move(nextId);
      }
    }
  });
  const providerValue = useMemo(() => {
    var _state22;

    return {
      id,
      baseRef: (_state22 = state) == null ? void 0 : _state22.baseRef
    };
  }, [id, (_state23 = state) == null ? void 0 : _state23.baseRef]);
  props = useWrapElement(props, element => /*#__PURE__*/jsx(CompositeItemContext.Provider, {
    value: providerValue,
    children: element
  }), [providerValue]);
  const isActiveItem = ((_state24 = state) == null ? void 0 : _state24.activeId) === id;
  const role = useRole(ref, props);
  let ariaSelected;

  if (isActiveItem) {
    var _state25;

    if (requiresAriaSelected(role)) {
      // When the active item role _requires_ the aria-selected attribute
      // (e.g., option, treeitem), we always set it to true.
      ariaSelected = true;
    } else if ((_state25 = state) != null && _state25.virtualFocus && supportsAriaSelected(role)) {
      // Otherwise, it will be set to true when virtualFocus is set to true
      // (meaning that the focus will be managed using the
      // aria-activedescendant attribute) and the aria-selected attribute is
      // _supported_ by the active item role.
      ariaSelected = true;
    }
  }

  const shouldTabIndex = !((_state26 = state) != null && _state26.virtualFocus) && isActiveItem || // We don't want to set tabIndex="-1" when using CompositeItem as a
  // standalone component, without state props.
  !((_state27 = state) != null && _state27.items.length);
  props = {
    id,
    "aria-selected": ariaSelected,
    "data-active-item": isActiveItem ? "" : undefined,
    ...props,
    ref: useForkRef(ref, props.ref),
    tabIndex: shouldTabIndex ? props.tabIndex : -1,
    onFocus,
    onBlurCapture,
    onKeyDown
  };
  props = useCommand(props);
  props = useCollectionItem({
    state,
    ...props,
    getItem,
    shouldRegisterItem: !!id ? props.shouldRegisterItem : false
  });
  return props;
});
/**
 * A component that renders a composite item.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const composite = useCompositeState();
 * <Composite state={composite}>
 *   <CompositeItem>Item 1</CompositeItem>
 *   <CompositeItem>Item 2</CompositeItem>
 *   <CompositeItem>Item 3</CompositeItem>
 * </Composite>
 * ```
 */

const CompositeItem = createMemoComponent(props => {
  const htmlProps = useCompositeItem(props);
  return createElement("button", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  CompositeItem.displayName = "CompositeItem";
}

function afterTimeout(timeoutMs, cb) {
  const timeoutId = setTimeout(cb, timeoutMs);
  return () => clearTimeout(timeoutId);
}

function afterPaint(cb) {
  let raf = requestAnimationFrame(() => {
    raf = requestAnimationFrame(cb);
  });
  return () => cancelAnimationFrame(raf);
}

function parseCSSTime() {
  for (var _len = arguments.length, times = new Array(_len), _key = 0; _key < _len; _key++) {
    times[_key] = arguments[_key];
  }

  return times.join(", ").split(", ").reduce((longestTime, currentTimeString) => {
    const currentTime = parseFloat(currentTimeString || "0s") * 1000; // When multiple times are specified, we want to use the longest one so we
    // wait until the longest transition has finished.

    if (currentTime > longestTime) return currentTime;
    return longestTime;
  }, 0);
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element that can be shown or hidden.
 * @see https://ariakit.org/components/disclosure
 * @example
 * ```jsx
 * const state = useDisclosureState();
 * const props = useDisclosureContent({ state });
 * <Disclosure state={state}>Disclosure</Disclosure>
 * <Role {...props}>Content</Role>
 * ```
 */


const useDisclosureContent = createHook(_ref => {
  let {
    state,
    ...props
  } = _ref;
  const id = useId(props.id);
  const [transition, setTransition] = useState(null);
  useSafeLayoutEffect(() => {
    var _state$contentElement;

    if (!state.animated) return; // When the disclosure content element is rendered in a portal, we need to
    // wait for the portal to be mounted and connected to the DOM before we
    // can start the animation.

    if (!((_state$contentElement = state.contentElement) != null && _state$contentElement.isConnected)) {
      setTransition(null);
      return;
    } // Double requestAnimationFrame is necessary here to avoid potential bugs
    // when the data attribute is added before the element is fully rendered
    // in the DOM, which wouldn't trigger the animation.


    return afterPaint(() => {
      setTransition(state.open ? "enter" : "leave");
    });
  }, [state.animated, state.contentElement, state.open]);
  useSafeLayoutEffect(() => {
    if (!state.animated) return;
    if (!state.contentElement) return;
    if (!transition) return;
    if (transition === "enter" && !state.open) return;
    if (transition === "leave" && state.open) return; // When the animated state is a number, the user has manually set the
    // animation timeout, so we just respect it.

    if (typeof state.animated === "number") {
      const timeoutMs = state.animated;
      return afterTimeout(timeoutMs, () => flushSync(state.stopAnimation));
    } // Otherwise, we need to parse the CSS transition/animation duration and
    // delay to know when the animation ends. This is safer than relying on
    // the transitionend/animationend events because it's not guaranteed that
    // these events will fire. For example, if the element is removed from the
    // DOM before the animation ends or if the animation wasn't triggered in
    // the first place, the events won't fire.


    const {
      transitionDuration,
      animationDuration,
      transitionDelay,
      animationDelay
    } = getComputedStyle(state.contentElement);
    const delay = parseCSSTime(transitionDelay, animationDelay);
    const duration = parseCSSTime(transitionDuration, animationDuration);
    const timeoutMs = delay + duration; // If the animation/transition delay and duration are 0, this means the
    // element is not animated with CSS (they may be using framer-motion,
    // react-spring, or something else). In this case, the user is responsible
    // for calling `stopAnimation` when the animation ends.

    if (!timeoutMs) return; // TODO: We should probably warn if `stopAnimation` hasn't been called
    // after X seconds.

    return afterTimeout(timeoutMs, () => flushSync(state.stopAnimation));
  }, [state.animated, state.contentElement, transition, state.open, state.stopAnimation]);
  const style = state.mounted || props.hidden === false ? props.style : { ...props.style,
    display: "none"
  };
  props = {
    id,
    "data-enter": transition === "enter" ? "" : undefined,
    "data-leave": transition === "leave" ? "" : undefined,
    hidden: !state.mounted,
    ...props,
    ref: useForkRef(id ? state.setContentElement : null, props.ref),
    style
  };
  return props;
});
/**
 * A component that renders an element that can be shown or hidden.
 * @see https://ariakit.org/components/disclosure
 * @example
 * ```jsx
 * const disclosure = useDisclosureState();
 * <Disclosure state={disclosure}>Disclosure</Disclosure>
 * <DisclosureContent state={disclosure}>Content</DisclosureContent>
 * ```
 */

const DisclosureContent = createComponent(props => {
  const htmlProps = useDisclosureContent(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  DisclosureContent.displayName = "DisclosureContent";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component. When applying the props returned by this hook to a
 * component, the component will be visually hidden, but still accessible to
 * screen readers.
 * @see https://ariakit.org/components/visually-hidden
 * @example
 * ```jsx
 * const props = useVisuallyHidden();
 * <a href="#">
 *   Learn more<Role {...props}> about the Solar System</Role>.
 * </a>
 * ```
 */
const useVisuallyHidden = createHook(props => {
  props = { ...props,
    style: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: "1px",
      margin: "-1px",
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      whiteSpace: "nowrap",
      width: "1px",
      ...props.style
    }
  };
  return props;
});
/**
 * A component that renders an element that's visually hidden, but still
 * accessible to screen readers.
 * @see https://ariakit.org/components/visually-hidden
 * @example
 * ```jsx
 * <a href="#">
 *   Learn more<VisuallyHidden> about the Solar System</VisuallyHidden>.
 * </a>
 * ```
 */

const VisuallyHidden = createComponent(props => {
  const htmlProps = useVisuallyHidden(props);
  return createElement("span", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  VisuallyHidden.displayName = "VisuallyHidden";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a focus trap element.
 * @see https://ariakit.org/components/focus-trap
 * @example
 * ```jsx
 * const props = useFocusTrap();
 * <Role {...props} />
 * ```
 */

const useFocusTrap = createHook(props => {
  props = {
    "data-focus-trap": "",
    tabIndex: 0,
    "aria-hidden": true,
    ...props,
    style: {
      // Prevents unintended scroll jumps.
      position: "fixed",
      top: 0,
      left: 0,
      ...props.style
    }
  };
  props = useVisuallyHidden(props);
  return props;
});
/**
 * A component that renders a focus trap element.
 * @see https://ariakit.org/components/focus-trap
 * @example
 * ```jsx
 * <FocusTrap onFocus={focusSomethingElse} />
 * ```
 */

const FocusTrap = createComponent(props => {
  const htmlProps = useFocusTrap(props);
  return createElement("span", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  FocusTrap.displayName = "FocusTrap";
}

const useFocusTrapRegion = createHook(_ref => {
  let {
    enabled = false,
    ...props
  } = _ref;
  const ref = useRef(null);
  props = useWrapElement(props, element => {
    const renderFocusTrap = () => {
      if (!enabled) return null;
      return /*#__PURE__*/jsx(FocusTrap, {
        onFocus: event => {
          const container = ref.current;
          if (!container) return;
          const tabbables = getAllTabbableIn(container, true);
          const first = tabbables[0];
          const last = tabbables[tabbables.length - 1]; // Fallbacks to the container element

          if (!tabbables.length) {
            container.focus();
            return;
          }

          if (event.relatedTarget === first) {
            last == null ? void 0 : last.focus();
          } else {
            first == null ? void 0 : first.focus();
          }
        }
      });
    };

    return /*#__PURE__*/jsxs(Fragment$1, {
      children: [renderFocusTrap(), element, renderFocusTrap()]
    });
  }, [enabled]);
  props = { ...props,
    ref: useForkRef(ref, props.ref)
  };
  return props;
});
/**
 * A component that renders a focus trap region element.
 * @see https://ariakit.org/components/focus-trap-region
 * @example
 * ```jsx
 * <FocusTrapRegion>
 *  <Button>click me</Button>
 *  <Button>trap focus</Button>
 *  <Button disabled>disabled Button</Button>
 * </FocusTrapRegion>
 * ```
 */

const FocusTrapRegion = createComponent(props => {
  const htmlProps = useFocusTrapRegion(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  FocusTrapRegion.displayName = "FocusTrapRegion";
}

const HeadingContext = /*#__PURE__*/createContext(0);

function HeadingLevel(_ref) {
  let {
    level,
    children
  } = _ref;
  const contextLevel = useContext(HeadingContext);
  const nextLevel = Math.max(Math.min(level || contextLevel + 1, 6), 1);
  return /*#__PURE__*/jsx(HeadingContext.Provider, {
    value: nextLevel,
    children: children
  });
}

/**
 * Stores the element that will contain the portal. By default, it will be the
 * body of the document.
 * @example
 * ```jsx
 * const container = document.getElementById("container");
 *
 * function App() {
 *   return (
 *     <PortalContext.Provider value={container}>
 *       <Portal />
 *     </PortalContext.Provider>
 *   );
 * }
 * ```
 */

const PortalContext = /*#__PURE__*/createContext(null);

function getRootElement(element) {
  return getDocument(element).body;
}

function getPortalElement(element, portalElement) {
  if (!portalElement) {
    return getDocument(element).createElement("div");
  }

  if (typeof portalElement === "function") {
    return portalElement(element);
  }

  return portalElement;
}

function getRandomId(prefix) {
  if (prefix === void 0) {
    prefix = "id";
  }

  return "" + (prefix ? prefix + "-" : "") + Math.random().toString(36).substr(2, 6);
}

function queueFocus(element) {
  queueMicrotask$1(() => {
    element == null ? void 0 : element.focus();
  });
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element using `ReactDOM.createPortal`.
 * @see https://ariakit.org/components/portal
 * @example
 * ```jsx
 * const props = usePortal();
 * <Role {...props}>Content</Role>
 * ```
 */


const usePortal = createHook(_ref => {
  let {
    preserveTabOrder,
    portalElement,
    portalRef,
    portal = true,
    ...props
  } = _ref;
  const ref = useRef(null);
  const refProp = useForkRef(ref, props.ref);
  const context = useContext(PortalContext);
  const [portalNode, setPortalNode] = useState(null);
  const beforeOutsideRef = useRef(null);
  const beforeInsideRef = useRef(null);
  const afterInsideRef = useRef(null);
  const afterOutsideRef = useRef(null); // Create the portal node and attach it to the DOM.

  useSafeLayoutEffect(() => {
    const element = ref.current;

    if (!element || !portal) {
      setPortalNode(null);
      return;
    }

    const portalEl = getPortalElement(element, portalElement); // TODO: Warn about portals as the document.body element.

    if (!portalEl) {
      setPortalNode(null);
      return;
    }

    const isPortalInDocument = portalEl.isConnected;

    if (!isPortalInDocument) {
      const rootElement = context || getRootElement(element);
      rootElement.appendChild(portalEl);
    } // If the portal element doesn't have an id already, set one.


    if (!portalEl.id) {
      // Use the element's id so rendering <Portal id="some-id" /> will
      // produce predictable results.
      portalEl.id = element.id ? element.id + "-portal" : getRandomId();
    } // Set the internal portal node state and the portalRef prop.


    setPortalNode(portalEl);
    setRef(portalRef, portalEl); // If the portal element was already in the document, we don't need to
    // remove it when the element is unmounted, so we just return.

    if (isPortalInDocument) return; // Otherwise, we need to remove the portal from the DOM.

    return () => {
      portalEl.remove();
      setRef(portalRef, null);
    };
  }, [portal, portalElement, context, portalRef]); // When preserveTabOrder is true, make sure elements inside the portal
  // element are tabbable only when the portal has already been focused,
  // either by tabbing into a focus trap element outside or using the mouse.

  useEffect(() => {
    if (!portalNode) return;
    if (!preserveTabOrder) return;
    let raf = 0;

    const onFocus = event => {
      if (isFocusEventOutside(event)) {
        const focusing = event.type === "focusin";
        if (focusing) return restoreFocusIn(portalNode); // Wait for the next frame to allow tabindex changes after the focus
        // event.

        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          disableFocusIn(portalNode, true);
        });
      }
    }; // Listen to the event on the capture phase so they run before the focus
    // trap elements onFocus prop is called.


    portalNode.addEventListener("focusin", onFocus, true);
    portalNode.addEventListener("focusout", onFocus, true);
    return () => {
      portalNode.removeEventListener("focusin", onFocus, true);
      portalNode.removeEventListener("focusout", onFocus, true);
    };
  }, [portalNode, preserveTabOrder]);
  props = useWrapElement(props, element => {
    element =
    /*#__PURE__*/
    // While the portal node is not in the DOM, we need to pass the
    // current context to the portal context, otherwise it's going to
    // reset to the body element on nested portals.
    jsx(PortalContext.Provider, {
      value: portalNode || context,
      children: element
    });
    if (!portal) return element;

    if (!portalNode) {
      // If the element should be rendered within a portal, but the portal
      // node is not yet in the DOM, we'll return an empty div element. We
      // assign the id to the element so we can use it to set the portal id
      // later on. We're using position: fixed here so that the browser
      // doesn't add margin to the element when setting gap on a parent
      // element.
      return /*#__PURE__*/jsx("span", {
        ref: refProp,
        id: props.id,
        style: {
          position: "fixed"
        }
      });
    }

    element = /*#__PURE__*/jsxs(Fragment$1, {
      children: [preserveTabOrder && portalNode && /*#__PURE__*/jsx(FocusTrap, {
        ref: beforeInsideRef,
        onFocus: event => {
          if (isFocusEventOutside(event, portalNode)) {
            queueFocus(getNextTabbable());
          } else {
            queueFocus(beforeOutsideRef.current);
          }
        }
      }), element, preserveTabOrder && portalNode && /*#__PURE__*/jsx(FocusTrap, {
        ref: afterInsideRef,
        onFocus: event => {
          if (isFocusEventOutside(event, portalNode)) {
            queueFocus(getPreviousTabbable());
          } else {
            queueFocus(afterOutsideRef.current);
          }
        }
      })]
    });

    if (portalNode) {
      element = /*#__PURE__*/createPortal(element, portalNode);
    }

    element = /*#__PURE__*/jsxs(Fragment$1, {
      children: [preserveTabOrder && portalNode && /*#__PURE__*/jsx(FocusTrap, {
        ref: beforeOutsideRef,
        onFocus: event => {
          if (isFocusEventOutside(event, portalNode)) {
            queueFocus(beforeInsideRef.current);
          } else {
            queueFocus(getPreviousTabbable());
          }
        }
      }), preserveTabOrder &&
      /*#__PURE__*/
      // We're using position: fixed here so that the browser doesn't
      // add margin to the element when setting gap on a parent element.
      jsx("span", {
        "aria-owns": portalNode == null ? void 0 : portalNode.id,
        style: {
          position: "fixed"
        }
      }), element, preserveTabOrder && portalNode && /*#__PURE__*/jsx(FocusTrap, {
        ref: afterOutsideRef,
        onFocus: event => {
          if (isFocusEventOutside(event, portalNode)) {
            queueFocus(afterInsideRef.current);
          } else {
            queueFocus(getNextTabbable());
          }
        }
      })]
    });
    return element;
  }, [portalNode, context, portal, props.id, preserveTabOrder]);
  props = { ...props,
    ref: refProp
  };
  return props;
});
/**
 * A component that renders an element using `ReactDOM.createPortal`.
 * @see https://ariakit.org/components/portal
 * @example
 * ```jsx
 * <Portal>Content</Portal>
 * ```
 */

const Portal = createComponent(props => {
  const htmlProps = usePortal(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Portal.displayName = "Portal";
}

const DialogContext = /*#__PURE__*/createContext(undefined);
const DialogHeadingContext = /*#__PURE__*/createContext(undefined);
const DialogDescriptionContext = /*#__PURE__*/createContext(undefined);

function usePreviousMouseDownRef(enabled) {
  const previousMouseDownRef = useRef();
  useEffect(() => {
    if (!enabled) {
      previousMouseDownRef.current = null;
      return;
    }

    const onMouseDown = event => {
      previousMouseDownRef.current = event.target;
    };

    return addGlobalEventListener("mousedown", onMouseDown, true);
  }, [enabled]);
  return previousMouseDownRef;
}

function DialogBackdrop(_ref) {
  var _state$contentElement;

  let {
    state,
    backdrop,
    backdropProps,
    hideOnInteractOutside = true,
    hideOnEscape = true,
    hidden,
    children
  } = _ref;
  const ref = useRef(null);
  state = useMemo(() => ({ ...state,
    // Override the setContentElement method to prevent the backdrop from
    // overwriting the dialog's content element.
    setContentElement: noop$7
  }), [state]);
  useSafeLayoutEffect(() => {
    const backdrop = ref.current;
    const dialog = state.contentElement;
    if (!backdrop) return;
    if (!dialog) return;
    backdrop.style.zIndex = getComputedStyle(dialog).zIndex;
  }, [state.contentElement]);
  const onClickProp = backdropProps == null ? void 0 : backdropProps.onClick;
  const hideOnInteractOutsideProp = useBooleanEvent(hideOnInteractOutside);
  const previousMouseDownRef = usePreviousMouseDownRef(state.mounted);
  const onClick = useEvent$1(event => {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (!isSelfTarget(event)) return;
    if (previousMouseDownRef.current !== event.currentTarget) return;
    if (!hideOnInteractOutsideProp(event)) return;
    event.stopPropagation();
    state.hide();
  });
  const onKeyDownProp = backdropProps == null ? void 0 : backdropProps.onKeyDown;
  const hideOnEscapeProp = useBooleanEvent(hideOnEscape); // When hideOnInteractOutside is false and the backdrop is clicked, the
  // backdrop will receive focus (because we set the tabIndex on it). Therefore,
  // the Escape key will not be captured by the Dialog component. So we listen
  // to it here.

  const onKeyDown = useEvent$1(event => {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;
    if (event.key !== "Escape") return;
    if (!isSelfTarget(event)) return;
    if (!hideOnEscapeProp(event)) return;
    state.hide();
  });
  const props = useDisclosureContent({
    state,
    id: undefined,
    role: "presentation",
    tabIndex: -1,
    hidden,
    ...backdropProps,
    ref: useForkRef(backdropProps == null ? void 0 : backdropProps.ref, ref),
    onClick,
    onKeyDown,
    style: {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      ...(backdropProps == null ? void 0 : backdropProps.style)
    }
  });
  const Component = typeof backdrop !== "boolean" ? backdrop || "div" : "div";
  return /*#__PURE__*/jsx(Component, { ...props,
    "data-backdrop": (_state$contentElement = state.contentElement) == null ? void 0 : _state$contentElement.id,
    children: children
  });
}

// We don't need to walk through certain tags.
const ignoreTags = ["SCRIPT", "STYLE"];
function isValidElement(element, ignoredElements) {
  if (ignoreTags.includes(element.tagName)) return false;
  return !ignoredElements.some(enabledElement => enabledElement && contains(element, enabledElement));
}
function walkTreeOutside(elements, callback) {
  for (let element of elements) {
    const document = getDocument(element); // Loops through the parent elements and then through each of their
    // children.

    while ((_element = element) != null && _element.parentElement && element !== document.body) {
      var _element;

      for (const child of element.parentElement.children) {
        if (isValidElement(child, elements)) {
          callback(child);
        }
      }

      element = element.parentElement;
    }
  }
}

function disableElement$1(element) {
  const previousAriaHidden = element.getAttribute("aria-hidden") || "";
  element.setAttribute("aria-hidden", "true");

  const enableElement = () => {
    if (previousAriaHidden) {
      element.setAttribute("aria-hidden", previousAriaHidden);
    } else {
      element.removeAttribute("aria-hidden");
    }
  };

  return enableElement;
}

function disableAccessibilityTreeOutside() {
  const cleanups = [];

  for (var _len = arguments.length, elements = new Array(_len), _key = 0; _key < _len; _key++) {
    elements[_key] = arguments[_key];
  }

  walkTreeOutside(elements, element => {
    cleanups.unshift(disableElement$1(element));
  });

  const restoreAccessibilityTree = () => {
    cleanups.forEach(fn => fn());
  };

  return restoreAccessibilityTree;
}

function disableElement(element) {
  if (!("style" in element)) return noop$7;
  const previousPointerEvents = element.style.pointerEvents;
  element.style.pointerEvents = "none";

  const enableElement = () => {
    element.style.pointerEvents = previousPointerEvents != null ? previousPointerEvents : "";
  };

  return enableElement;
}

function disablePointerEventsOutside() {
  const cleanups = [];

  for (var _len = arguments.length, elements = new Array(_len), _key = 0; _key < _len; _key++) {
    elements[_key] = arguments[_key];
  }

  walkTreeOutside(elements, element => {
    cleanups.unshift(disableElement(element));
  });

  const restorePointerEvents = () => {
    cleanups.forEach(fn => fn());
  };

  return restorePointerEvents;
}

function prependHiddenDismiss(container, onClick) {
  const document = getDocument(container);
  const button = document.createElement("button");
  button.type = "button";
  button.tabIndex = -1;
  button.textContent = "Dismiss popup"; // Visually hidden styles

  button.style.border = "0px";
  button.style.clip = "rect(0 0 0 0)";
  button.style.height = "1px";
  button.style.margin = "-1px";
  button.style.overflow = "hidden";
  button.style.padding = "0px";
  button.style.position = "absolute";
  button.style.whiteSpace = "nowrap";
  button.style.width = "1px";
  button.addEventListener("click", onClick);
  container.prepend(button);

  const removeHiddenDismiss = () => {
    button.removeEventListener("click", onClick);
    button.remove();
  };

  return removeHiddenDismiss;
}

/**
 * Controls which dialog is the champion that will control global behavior such
 * as body scrolling and accessibility tree outside.
 */

function useChampionDialog(dialogRef, attribute, enabled) {
  const [updated, retry] = useForceUpdate$1();
  const isChampionDialog = useCallback(() => {
    if (!enabled) return false;
    const dialog = dialogRef.current;
    if (!dialog) return false;
    const {
      body
    } = getDocument(dialog);
    const id = body.getAttribute(attribute);
    return !id || id === dialog.id;
  }, [updated, enabled, attribute]);
  useSafeLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!enabled) return;
    const {
      body
    } = getDocument(dialog);

    if (!isChampionDialog()) {
      const observer = new MutationObserver(() => flushSync(retry));
      observer.observe(body, {
        attributeFilter: [attribute]
      });
      return () => observer.disconnect();
    }

    body.setAttribute(attribute, dialog.id);
    return () => {
      body.removeAttribute(attribute);
    };
  }, [updated, dialogRef, enabled, isChampionDialog, attribute]);
  return isChampionDialog;
}

/**
 * When the focused child gets removed from the DOM, we make sure to move focus
 * to the dialog.
 */
function useFocusOnChildUnmount(dialogRef, state) {
  useEffect(() => {
    if (!state.open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const observer = new MutationObserver(_ref => {
      let [mutation] = _ref;
      if (!mutation) return; // If target is not dialog, then this observer was triggered by a nested
      // dialog, so we just ignore it here and let the nested dialog handle it.

      if (mutation.target !== dialog) return;
      const document = getDocument(dialog);
      const activeElement = getActiveElement(dialog); // We can check if the current focused element is the document body.

      if (activeElement === document.body) {
        dialog.focus();
      }
    });
    observer.observe(dialog, {
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, [state.open, dialogRef]);
}

function isInDocument(target) {
  if (target.tagName === "HTML") return true;
  return contains(getDocument(target).body, target);
}

function isDisclosure(disclosure, target) {
  if (contains(disclosure, target)) return true;
  const activeId = target.getAttribute("aria-activedescendant");

  if (activeId) {
    const activeElement = getDocument(disclosure).getElementById(activeId);

    if (activeElement) {
      return contains(disclosure, activeElement);
    }
  }

  return false;
}

function isBackdrop$1(dialog, target) {
  if (!dialog) return false;
  if (!target) return false;
  return target.hasAttribute("data-backdrop") && target.getAttribute("data-backdrop") === dialog.id;
}

function dialogContains(target) {
  return dialogRef => {
    const dialog = dialogRef.current;
    if (!dialog) return false;
    if (contains(dialog, target)) return true;
    if (isBackdrop$1(dialog, target)) return true;
    return false;
  };
}

function useEventOutside(_ref) {
  let {
    type,
    listener,
    dialogRef,
    nestedDialogs,
    disclosureRef,
    enabled,
    capture
  } = _ref;
  const callListener = useEvent$1(listener);
  const nestedDialogsRef = useLiveRef(nestedDialogs);
  useEffect(() => {
    if (!enabled) return;

    const onEvent = event => {
      const container = dialogRef.current;
      const disclosure = disclosureRef == null ? void 0 : disclosureRef.current;
      const target = event.target;
      if (!container) return;
      if (!target) return; // When an element is unmounted right after it receives focus, the focus
      // event is triggered after that, when the element isn't part of the
      // current document anymore. We just ignore it.

      if (!isInDocument(target)) return; // Event inside dialog

      if (contains(container, target)) return; // Event on disclosure

      if (disclosure && isDisclosure(disclosure, target)) return; // Event on focus trap

      if (target.hasAttribute("data-focus-trap")) return; // Event inside a nested dialog

      if (nestedDialogsRef.current.some(dialogContains(target))) return;
      callListener(event);
    };

    return addGlobalEventListener(type, onEvent, capture);
  }, [enabled, dialogRef, disclosureRef, callListener, capture]);
}

function shouldHideOnInteractOutside(hideOnInteractOutside, event) {
  if (typeof hideOnInteractOutside === "function") {
    return hideOnInteractOutside(event);
  }

  return !!hideOnInteractOutside;
}

function useHideOnInteractOutside(dialogRef, nestedDialogs, _ref2) {
  let {
    state,
    modal,
    hideOnInteractOutside,
    enabled = state.open
  } = _ref2;
  const previousMouseDownRef = usePreviousMouseDownRef(enabled);
  const props = {
    disclosureRef: state.disclosureRef,
    enabled,
    dialogRef,
    nestedDialogs,
    capture: true
  };
  useEventOutside({ ...props,
    type: "mousedown",
    listener: event => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (modal && !shouldHideOnInteractOutside(hideOnInteractOutside, event)) {
        // If the dialog is modal and the user clicked outside the dialog, but
        // shouldHideOnInteractOutside is false, we don't hide the dialog, but
        // ensure focus is placed on it. Otherwise the focus might end up on an
        // element outside of the dialog or the body element itself.
        dialog.focus();
        event.preventDefault();
        event.stopPropagation();
      }
    }
  });
  useEventOutside({ ...props,
    type: "click",
    listener: event => {
      const previousMouseDown = previousMouseDownRef.current; // If there's no previously mousedown'd element, this probably means that
      // the dialog opened with a mousedown event, and a subsequent click event
      // was dispatched outside of the dialog. See form-select example. We just
      // ignore this.

      if (!previousMouseDown) return;

      if (!shouldHideOnInteractOutside(hideOnInteractOutside, event)) {
        if (!modal) return;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const dialog = dialogRef.current;
      const draggingFromDialog = dialog && contains(dialog, previousMouseDown); // This prevents the dialog from closing by dragging the cursor (for
      // example, selecting some text inside the dialog and releasing the mouse
      // outside of it). See https://github.com/ariakit/ariakit/issues/1336

      if (draggingFromDialog) return;
      state.hide();
    }
  });
  useEventOutside({ ...props,
    type: "focusin",
    listener: event => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (!shouldHideOnInteractOutside(hideOnInteractOutside, event)) {
        if (!modal) return; // Same as the mousedown listener.

        dialog.focus();
        event.preventDefault();
        event.stopPropagation();
        return;
      } // Fix for https://github.com/ariakit/ariakit/issues/619


      if (event.target === getDocument(dialog)) return;
      state.hide();
    }
  });
  useEventOutside({ ...props,
    type: "contextmenu",
    listener: event => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (!shouldHideOnInteractOutside(hideOnInteractOutside, event)) {
        if (!modal) return; // Same as the mousedown listener.

        dialog.focus();
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      state.hide();
    }
  });
}

const NestedDialogsContext = /*#__PURE__*/createContext({});
/**
 * Handles nested dialogs.
 */

function useNestedDialogs(dialogRef, _ref) {
  let {
    state,
    modal
  } = _ref;
  const context = useContext(NestedDialogsContext);
  const [openModals, setOpenModals] = useState([]);
  const [nestedDialogs, setNestedDialogs] = useState([]);
  const addDialog = useCallback(ref => {
    const removeFromContext = context.addDialog == null ? void 0 : context.addDialog(ref);
    setNestedDialogs(dialogs => [...dialogs, ref]);
    return () => {
      removeFromContext == null ? void 0 : removeFromContext();
      setNestedDialogs(dialogs => dialogs.filter(dialog => dialog !== ref));
    };
  }, [context.addDialog]);
  const showModal = useCallback(ref => {
    const hideModal = context.showModal == null ? void 0 : context.showModal(ref);
    setOpenModals(modals => [...modals, ref]);
    return () => {
      hideModal == null ? void 0 : hideModal();
      setOpenModals(modals => modals.filter(modal => modal !== ref));
    };
  }, [context.showModal]); // If this is a nested dialog, add it to the context.

  useSafeLayoutEffect(() => {
    return context.addDialog == null ? void 0 : context.addDialog(dialogRef);
  }, [context.addDialog, dialogRef]);
  useSafeLayoutEffect(() => {
    if (!modal) return;
    if (!state.open) return;
    return context.showModal == null ? void 0 : context.showModal(dialogRef);
  }, [modal, state.open, context.showModal, dialogRef]); // Close all nested dialogs when parent dialog closes.

  useSafeLayoutEffect(() => {
    if (context.open === false && state.open) {
      state.hide();
    }
  }, [context.open, state.open, state.hide]); // Provider

  const providerValue = useMemo(() => ({
    open: state.open,
    addDialog,
    showModal
  }), [state.open, addDialog, showModal]);
  const wrapElement = useCallback(element => /*#__PURE__*/jsx(NestedDialogsContext.Provider, {
    value: providerValue,
    children: element
  }), [providerValue]);
  return {
    nestedDialogs,
    openModals,
    wrapElement
  };
}

// Based on https://github.com/floating-ui/floating-ui/blob/1201e72e67a80e479122293d46d96c9bbc8f156d/packages/react-dom-interactions/src/FloatingOverlay.tsx

function assignStyle(element, style) {
  if (!element) return () => {};
  const previousStyle = element.style.cssText;
  Object.assign(element.style, style);
  return () => {
    element.style.cssText = previousStyle;
  };
}

function setCSSProperty(element, property, value) {
  if (!element) return () => {};
  const previousValue = element.style.getPropertyValue(property);
  element.style.setProperty(property, value);
  return () => {
    if (previousValue) {
      element.style.setProperty(property, previousValue);
    } else {
      element.style.removeProperty(property);
    }
  };
}

function getPaddingProperty(documentElement) {
  // RTL <body> scrollbar
  const documentLeft = documentElement.getBoundingClientRect().left;
  const scrollbarX = Math.round(documentLeft) + documentElement.scrollLeft;
  return scrollbarX ? "paddingLeft" : "paddingRight";
}

function usePreventBodyScroll(dialogRef, enabled) {
  const isChampionDialog = useChampionDialog(dialogRef, "data-dialog-body-scroll", enabled);
  useSafeLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!isChampionDialog()) return;
    const doc = getDocument(dialog);
    const win = getWindow$2(dialog);
    const {
      documentElement,
      body
    } = doc;
    const scrollbarWidth = win.innerWidth - documentElement.clientWidth;

    const setScrollbarWidthProperty = () => setCSSProperty(documentElement, "--scrollbar-width", scrollbarWidth + "px");

    const paddingProperty = getPaddingProperty(documentElement);

    const setStyle = () => assignStyle(body, {
      overflow: "hidden",
      [paddingProperty]: scrollbarWidth + "px"
    }); // Only iOS doesn't respect `overflow: hidden` on document.body


    const setIOSStyle = () => {
      var _visualViewport$offse, _visualViewport$offse2;

      const {
        scrollX,
        scrollY,
        visualViewport
      } = win; // iOS 12 does not support `visuaViewport`.

      const offsetLeft = (_visualViewport$offse = visualViewport == null ? void 0 : visualViewport.offsetLeft) != null ? _visualViewport$offse : 0;
      const offsetTop = (_visualViewport$offse2 = visualViewport == null ? void 0 : visualViewport.offsetTop) != null ? _visualViewport$offse2 : 0;
      const restoreStyle = assignStyle(body, {
        position: "fixed",
        overflow: "hidden",
        top: -(scrollY - Math.floor(offsetTop)) + "px",
        left: -(scrollX - Math.floor(offsetLeft)) + "px",
        right: "0",
        [paddingProperty]: scrollbarWidth + "px"
      });
      return () => {
        restoreStyle(); // istanbul ignore next: JSDOM doesn't implement window.scrollTo

        if (process.env.NODE_ENV !== "test") {
          win.scrollTo(scrollX, scrollY);
        }
      };
    };

    const isIOS = isApple() && !isMac();
    return chain(setScrollbarWidthProperty(), isIOS ? setIOSStyle() : setStyle());
  }, [dialogRef, isChampionDialog]);
}

const isSafariBrowser = isSafari();

function isBackdrop(dialog, element) {
  const id = dialog.id;
  if (!id) return;
  return element.getAttribute("data-backdrop") === id;
}

function isInDialog(element) {
  return dialogRef => dialogRef.current && contains(dialogRef.current, element);
}

function isAlreadyFocusingAnotherElement(dialog, nestedDialogs) {
  const activeElement = getActiveElement();
  if (!activeElement) return false;
  if (contains(dialog, activeElement)) return false;
  if (isBackdrop(dialog, activeElement)) return false;
  if (nestedDialogs != null && nestedDialogs.some(isInDialog(activeElement))) return false;
  if (isFocusable(activeElement)) return true;
  return false;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a dialog element.
 * @see https://ariakit.org/components/dialog
 * @example
 * ```jsx
 * const state = useDialogState();
 * const props = useDialog({ state });
 * <Role {...props}>Dialog</Role>
 * ```
 */


const useDialog = createHook(_ref => {
  let {
    state,
    focusable = true,
    modal = true,
    portal = !!modal,
    backdrop = !!modal,
    backdropProps,
    hideOnEscape = true,
    hideOnInteractOutside = true,
    preventBodyScroll = !!modal,
    autoFocusOnShow = true,
    autoFocusOnHide = true,
    initialFocusRef,
    finalFocusRef,
    ...props
  } = _ref;
  const ref = useRef(null);
  const openRef = useRef(state.open); // domReady can be also the portal node element so it's updated when the
  // portal node changes (like in between re-renders), triggering effects
  // again.

  const {
    portalRef,
    portalNode,
    domReady
  } = usePortalRef(portal, props.portalRef); // Sets preserveTabOrder to true only if the dialog is not a modal and is
  // open.

  const preserveTabOrder = props.preserveTabOrder && !modal && state.mounted;
  const openStable = state.open && !state.animating; // Usually, we only want to disable the accessibility tree outside if the
  // dialog is a modal. But the Portal component can't preserve the tab order
  // on Safari/VoiceOver. By allowing only the dialog/portal to be accessible,
  // we provide a similar tab order flow. We don't need to disable pointer
  // events because it's just for screen readers.

  const shouldDisableAccessibilityTree = modal || portal && preserveTabOrder && isSafari();
  const id = useId(props.id); // Sets disclosure ref. It needs to be a layout effect so we get the focused
  // element right before the dialog is mounted.

  useSafeLayoutEffect(() => {
    if (!openStable) return;
    const dialog = ref.current;
    const activeElement = getActiveElement(dialog, true);
    if (!activeElement) return;
    if (activeElement.tagName === "BODY") return; // The disclosure element can't be inside the dialog.

    if (dialog && contains(dialog, activeElement)) return;
    state.disclosureRef.current = activeElement;
  }, [openStable]);
  const nested = useNestedDialogs(ref, {
    state,
    modal
  });
  const {
    nestedDialogs,
    openModals,
    wrapElement
  } = nested;
  const nestedDialogsRef = useLiveRef(nestedDialogs);
  usePreventBodyScroll(ref, preventBodyScroll && state.mounted); // When a focused child element is removed, focus will be placed on the
  // document's body. This will focus on the dialog instead.

  useFocusOnChildUnmount(ref, state);
  useHideOnInteractOutside(ref, nestedDialogs, {
    state,
    modal,
    hideOnInteractOutside,
    enabled: state.open
  }); // Safari does not focus on native buttons on mousedown. The
  // DialogDisclosure component normalizes this behavior using the
  // useFocusable hook, but the disclosure button may use a custom component,
  // and not DialogDisclosure. In this case, we need to make sure the
  // disclosure button gets focused here.

  if (isSafariBrowser) {
    useEffect(() => {
      if (!state.mounted) return;
      const disclosure = state.disclosureRef.current;
      if (!disclosure) return;
      if (!isButton(disclosure)) return;

      const onMouseDown = () => {
        let receivedFocus = false;

        const onFocus = () => {
          receivedFocus = true;
        };

        const options = {
          capture: true,
          once: true
        };
        disclosure.addEventListener("focusin", onFocus, options);
        queueBeforeEvent(disclosure, "mouseup", () => {
          disclosure.removeEventListener("focusin", onFocus, true);
          if (receivedFocus) return;
          focusIfNeeded(disclosure);
        });
      };

      disclosure.addEventListener("mousedown", onMouseDown);
      return () => {
        disclosure.removeEventListener("mousedown", onMouseDown);
      };
    }, [state.mounted, state.disclosureRef]);
  } // Renders a hidden dismiss button at the top of the modal dialog element.
  // So that screen reader users aren't trapped in the dialog when there's no
  // visible dismiss button.


  useEffect(() => {
    if (!state.mounted) return;
    if (!domReady) return;
    const dialog = ref.current;
    if (!dialog) return; // Usually, we only want to force the presence of a dismiss button if the
    // dialog is a modal. But, on Safari, since we're disabling the
    // accessibility tree outside, we need to ensure the user will be able to
    // close the dialog.

    if (shouldDisableAccessibilityTree) {
      // If there's already a DialogDismiss component, it does nothing.
      const existingDismiss = dialog.querySelector("[data-dialog-dismiss]");
      if (existingDismiss) return;
      return prependHiddenDismiss(dialog, state.hide);
    }

    return;
  }, [state.mounted, domReady, shouldDisableAccessibilityTree, state.hide]);
  const shouldDisableOutside = useChampionDialog(ref, "data-dialog-disable-outside", openStable && !openModals.length && shouldDisableAccessibilityTree); // Disables/enables the element tree around the modal dialog element.

  useSafeLayoutEffect(() => {
    // When the dialog is animating, we immediately restore the element tree
    // outside. This means the element tree will be enabled when the focus is
    // moved back to the disclosure element.
    if (!shouldDisableOutside()) return; // If portal is enabled, we get the portalNode instead of the dialog
    // element. This will consider nested dialogs as they will be children of
    // the portal node, but not the dialog. This also accounts for the tiny
    // delay before the dialog element is appended to the portal node, and the
    // portal node is added to the DOM.

    const element = portal ? portalNode : ref.current;

    if (modal) {
      return chain(disableAccessibilityTreeOutside(element), // When the backdrop is not visible, we also need to disable pointer
      // events outside of the modal dialog.
      !backdrop ? disablePointerEventsOutside(element) : null);
    }

    return disableAccessibilityTreeOutside(element);
  }, [shouldDisableOutside, portal, portalNode, modal, backdrop]);
  const prevInitialFocusRef = useRef();
  const mayAutoFocusOnShow = !!autoFocusOnShow;
  const autoFocusOnShowProp = useBooleanEvent(autoFocusOnShow); // Auto focus on show.

  useEffect(() => {
    var _nestedDialogsRef$cur;

    if (!openStable) return;
    if (!mayAutoFocusOnShow) return; // Makes sure to wait for the portalNode to be created before moving
    // focus. This is useful for when the Dialog component is unmounted
    // when hidden.

    if (!domReady) return; // If there are open nested dialogs, let them handle the focus.

    const hasNestedOpenDialog = (_nestedDialogsRef$cur = nestedDialogsRef.current) == null ? void 0 : _nestedDialogsRef$cur.some(child => child.current && !child.current.hidden);
    if (hasNestedOpenDialog) return; // The dialog element may change for different reasons. For example, when
    // the `modal`, `portal` or `backdrop` props change, the HTML structure
    // will also change, which will affect the dialog element reference.
    // That's why we're listening to `state.contentElement` here instead of
    // getting the `ref.current` value. This ensures this effect will re-run
    // when the dialog element reference changes.

    const dialog = state.contentElement;
    if (!(dialog != null && dialog.isConnected)) return;
    const initialFocus = initialFocusRef == null ? void 0 : initialFocusRef.current;
    const element = initialFocus || // We have to fallback to the first focusable element otherwise portaled
    // dialogs with preserveTabOrder set to true will not receive focus
    // properly because the elements aren't tabbable until the dialog
    // receives focus.
    getFirstTabbableIn(dialog, true, portal && preserveTabOrder) || dialog;
    const prevInitialFocus = prevInitialFocusRef.current;
    prevInitialFocusRef.current = initialFocus; // If the initial focus is the same as the previous initial focus and
    // there's already an element with focus inside the dialog, we don't
    // change focus here.

    if (initialFocus === prevInitialFocus) {
      const activeElement = getActiveElement(dialog, true);
      if (activeElement && contains(dialog, activeElement)) return;
    }

    if (!autoFocusOnShowProp(element)) return;
    element.focus();
  }, [openStable, mayAutoFocusOnShow, domReady, state.contentElement, initialFocusRef, portal, preserveTabOrder, autoFocusOnShowProp]);
  const mayAutoFocusOnHide = !!autoFocusOnHide;
  const autoFocusOnHideProp = useBooleanEvent(autoFocusOnHide); // Auto focus on hide.

  useEffect(() => {
    const dialog = ref.current;
    const prevOpen = openRef.current;
    openRef.current = state.open; // We only want to auto focus on hide if the dialog was open before.

    if (!prevOpen) return;
    if (!mayAutoFocusOnHide) return;
    if (!dialog) return; // A function so we can use it on the effect setup and cleanup phases.

    const focusOnHide = () => {
      const dialogs = nestedDialogsRef.current; // Hide was triggered by a click/focus on a tabbable element outside
      // the dialog or on another dialog. We won't change focus then.

      if (isAlreadyFocusingAnotherElement(dialog, dialogs)) return;
      let element = (finalFocusRef == null ? void 0 : finalFocusRef.current) || state.disclosureRef.current;

      if (element) {
        if (element.id) {
          const doc = getDocument(element);
          const selector = "[aria-activedescendant=\"" + element.id + "\"]";
          const composite = doc.querySelector(selector); // If the element is an item in a composite widget that handles
          // focus with the `aria-activedescendant` attribute, we want to
          // focus on the composite element itself.

          if (composite) {
            element = composite;
          }
        } // If the element is not focusable by the time the dialog is hidden,
        // it's probably because it's an element inside another popover or
        // menu that also got hidden when this dialog was shown. We'll try to
        // focus on their disclosure element instead.


        if (!isFocusable(element)) {
          const parentDialog = closest(element, "[data-dialog]");

          if (parentDialog && parentDialog.id) {
            const doc = getDocument(parentDialog);
            const selector = "[aria-controls~=\"" + parentDialog.id + "\"]";
            const control = doc.querySelector(selector);

            if (control) {
              element = control;
            }
          }
        }

        if (!autoFocusOnHideProp(element)) return;
        element.focus();
      }
    };

    if (!state.open) {
      // If this effect is running while state.open is false, this means
      // that the Dialog component doesn't get unmounted when it's not
      // open, so we can immediatelly move focus.
      return focusOnHide();
    } // Otherwise, we just return the focusOnHide function so it's going to
    // be executed when the Dialog component gets unmounted. This is useful
    // so we can support both mounting and unmounting Dialog components.


    return focusOnHide;
  }, [mayAutoFocusOnHide, state.open, finalFocusRef, state.disclosureRef, autoFocusOnHideProp]);
  const hideOnEscapeProp = useBooleanEvent(hideOnEscape); // Hide on Escape.

  useEffect(() => {
    if (!domReady) return;
    if (!state.mounted) return;

    const onKeyDown = event => {
      const dialog = ref.current;
      if (!dialog) return;
      const target = event.target;
      const disclosure = state.disclosureRef.current;
      if (event.key !== "Escape") return;
      if (event.defaultPrevented) return;
      if (!target) return; // This considers valid targets only the disclosure element or
      // descendants of the dialog element that are not descendants of nested
      // dialogs.

      const isValidTarget = () => {
        if (contains(dialog, target)) {
          const dialogs = nestedDialogsRef.current; // Since this is a native DOM event, it won't be triggered by
          // keystrokes on nested dialogs inside portals. But we still need to
          // check if the target is inside a nested non-portal dialog.

          const inNestedDialog = dialogs.some(isInDialog(target));
          if (inNestedDialog) return false;
          return true;
        }

        if (disclosure && contains(disclosure, target)) return true;
        return false;
      };

      if (isValidTarget() && hideOnEscapeProp(event)) {
        state.hide();
      }
    }; // We're attatching the listener to the document instead of the dialog
    // element so we can also listen to keystrokes on the disclosure element.
    // We can't do this on a onKeyDown prop on the disclosure element because
    // we don't have access to the hideOnEscape prop there.


    return addGlobalEventListener("keydown", onKeyDown);
  }, [domReady, state.mounted, state.disclosureRef, hideOnEscapeProp, state.hide]); // Wraps the element with the nested dialog context.

  props = useWrapElement(props, wrapElement, [wrapElement]); // Resets the heading levels inside the modal dialog so they start with h1.

  props = useWrapElement(props, element => /*#__PURE__*/jsx(HeadingLevel, {
    level: modal ? 1 : undefined,
    children: element
  }), [modal]); // Focus traps.

  props = useFocusTrapRegion({ ...props,
    enabled: state.open && modal && !openModals.length
  });
  const hiddenProp = props.hidden; // Wraps the dialog with a backdrop element if the backdrop prop is truthy.

  props = useWrapElement(props, element => {
    if (backdrop) {
      return /*#__PURE__*/jsx(DialogBackdrop, {
        state: state,
        backdrop: backdrop,
        backdropProps: backdropProps,
        hideOnInteractOutside: hideOnInteractOutside,
        hideOnEscape: hideOnEscape,
        hidden: hiddenProp,
        children: element
      });
    }

    return element;
  }, [state, backdrop, backdropProps, hideOnInteractOutside, hideOnEscape, hiddenProp]);
  const [headingId, setHeadingId] = useState();
  const [descriptionId, setDescriptionId] = useState();
  props = useWrapElement(props, element => /*#__PURE__*/jsx(DialogContext.Provider, {
    value: state,
    children: /*#__PURE__*/jsx(DialogHeadingContext.Provider, {
      value: setHeadingId,
      children: /*#__PURE__*/jsx(DialogDescriptionContext.Provider, {
        value: setDescriptionId,
        children: element
      })
    })
  }), [state]);
  props = {
    id,
    "data-dialog": "",
    role: "dialog",
    tabIndex: focusable ? -1 : undefined,
    "aria-labelledby": headingId,
    "aria-describedby": descriptionId,
    ...props,
    ref: useForkRef(ref, props.ref)
  };
  props = useDisclosureContent({
    state,
    ...props
  });
  props = useFocusable({ ...props,
    focusable
  });
  props = usePortal({
    portal,
    ...props,
    portalRef,
    preserveTabOrder
  });
  return props;
});
/**
 * A component that renders a dialog element.
 * @see https://ariakit.org/components/dialog
 * @example
 * ```jsx
 * const dialog = useDialogState();
 * <button onClick={dialog.toggle}>Open dialog</button>
 * <Dialog state={dialog}>Dialog</Dialog>
 * ```
 */

const Dialog = createComponent(props => {
  const htmlProps = useDialog(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Dialog.displayName = "Dialog";
}

const PopoverContext = /*#__PURE__*/createContext(undefined);

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a popover element.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const state = usePopoverState();
 * const props = usePopover({ state });
 * <Role {...props}>Popover</Role>
 * ```
 */
const usePopover = createHook(_ref => {
  let {
    state,
    modal = false,
    portal = !!modal,
    preserveTabOrder = true,
    autoFocusOnShow = true,
    wrapperProps,
    ...props
  } = _ref;
  const popoverRef = state.popoverRef; // Makes sure the wrapper element that's passed to popper has the same
  // z-index as the popover element so users only need to set the z-index
  // once.

  useSafeLayoutEffect(() => {
    const wrapper = popoverRef.current;
    const popover = state.contentElement;
    if (!wrapper) return;
    if (!popover) return;
    wrapper.style.zIndex = getComputedStyle(popover).zIndex;
  }, [popoverRef, state.contentElement]); // We have to wait for the popover to be positioned before we can move
  // focus, otherwise there may be scroll jumps. See popover-standalone
  // example test-browser file.

  const [canAutoFocusOnShow, setCanAutoFocusOnShow] = useState(false);
  const {
    portalRef,
    domReady
  } = usePortalRef(portal, props.portalRef);
  useSafeLayoutEffect(() => {
    var _state$contentElement;

    if (!domReady) return;
    if (!state.mounted) return;
    if (!((_state$contentElement = state.contentElement) != null && _state$contentElement.isConnected)) return;
    const raf = requestAnimationFrame(() => {
      setCanAutoFocusOnShow(true);
    });
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [domReady, state.mounted, state.contentElement]); // Wrap our element in a div that will be used to position the popover.
  // This way the user doesn't need to override the popper's position to
  // create animations.

  props = useWrapElement(props, element => /*#__PURE__*/jsx("div", {
    role: "presentation",
    ...wrapperProps,
    style: {
      position: state.fixed ? "fixed" : "absolute",
      top: 0,
      left: 0,
      ...(wrapperProps == null ? void 0 : wrapperProps.style)
    },
    ref: popoverRef,
    children: element
  }), [state.fixed, popoverRef, wrapperProps]);
  props = useWrapElement(props, element => /*#__PURE__*/jsx(PopoverContext.Provider, {
    value: state,
    children: element
  }), [state]);
  props = { ...props,
    style: {
      position: "relative",
      ...props.style
    }
  };
  props = useDialog({
    state,
    modal,
    preserveTabOrder,
    portal,
    autoFocusOnShow: canAutoFocusOnShow && autoFocusOnShow,
    ...props,
    portalRef
  });
  return props;
});
/**
 * A component that renders a popover element.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const popover = usePopoverState();
 * <PopoverDisclosure state={popover}>Disclosure</PopoverDisclosure>
 * <Popover state={popover}>Popover</Popover>
 * ```
 */

const Popover$1 = createComponent(props => {
  const htmlProps = usePopover(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Popover$1.displayName = "Popover";
}

/**
 * Provides state for the `Composite` component.
 * @example
 * ```jsx
 * const composite = useCompositeState();
 * <Composite state={composite}>
 *   <CompositeItem>Item 1</CompositeItem>
 *   <CompositeItem>Item 2</CompositeItem>
 *   <CompositeItem>Item 3</CompositeItem>
 * </Composite>
 * ```
 */

function useCompositeState(_temp) {
  var _props$includesBaseEl;

  let {
    orientation = "both",
    rtl = false,
    virtualFocus = false,
    focusLoop = false,
    focusWrap = false,
    focusShift = false,
    ...props
  } = _temp === void 0 ? {} : _temp;
  const collection = useCollectionState(props);
  const baseRef = useRef(null);
  const [moves, setMoves] = useControlledState(0, props.moves, props.setMoves);
  const [_activeId, setActiveId] = useControlledState(props.defaultActiveId, props.activeId, props.setActiveId);
  const activeId = useMemo(() => getActiveId(collection.items, _activeId), [collection.items, _activeId]);
  const initialActiveId = useInitialValue$1(activeId);
  const includesBaseElement = (_props$includesBaseEl = props.includesBaseElement) != null ? _props$includesBaseEl : initialActiveId === null;
  const activeIdRef = useLiveRef(activeId);
  const move = useCallback(id => {
    // move() does nothing
    if (id === undefined) return;
    setMoves(prevMoves => prevMoves + 1);
    setActiveId(id);
  }, []);
  const first = useCallback(() => {
    const firstItem = findFirstEnabledItem(collection.items);
    return firstItem == null ? void 0 : firstItem.id;
  }, [collection.items]);
  const last = useCallback(() => {
    const firstItem = findFirstEnabledItem(reverseArray(collection.items));
    return firstItem == null ? void 0 : firstItem.id;
  }, [collection.items]);
  const getNextId = useCallback((items, orientation, hasNullItem, skip) => {
    // RTL doesn't make sense on vertical navigation
    const isHorizontal = orientation !== "vertical";
    const isRTL = rtl && isHorizontal;
    const allItems = isRTL ? reverseArray(items) : items; // If there's no item focused, we just move the first one.

    if (activeIdRef.current == null) {
      var _findFirstEnabledItem;

      return (_findFirstEnabledItem = findFirstEnabledItem(allItems)) == null ? void 0 : _findFirstEnabledItem.id;
    }

    const activeItem = allItems.find(item => item.id === activeIdRef.current); // If there's no item focused, we just move to the first one.

    if (!activeItem) {
      var _findFirstEnabledItem2;

      return (_findFirstEnabledItem2 = findFirstEnabledItem(allItems)) == null ? void 0 : _findFirstEnabledItem2.id;
    }

    const isGrid = !!activeItem.rowId;
    const activeIndex = allItems.indexOf(activeItem);
    const nextItems = allItems.slice(activeIndex + 1);
    const nextItemsInRow = getItemsInRow(nextItems, activeItem.rowId); // Home, End, PageUp, PageDown

    if (skip !== undefined) {
      const nextEnabledItemsInRow = getEnabledItems$1(nextItemsInRow, activeIdRef.current);
      const nextItem = nextEnabledItemsInRow.slice(skip)[0] || // If we can't find an item, just return the last one.
      nextEnabledItemsInRow[nextEnabledItemsInRow.length - 1];
      return nextItem == null ? void 0 : nextItem.id;
    }

    const oppositeOrientation = getOppositeOrientation( // If it's a grid and orientation is not set, it's a next/previous
    // call, which is inherently horizontal. up/down will call next with
    // orientation set to vertical by default (see below on up/down
    // methods).
    isGrid ? orientation || "horizontal" : orientation);
    const canLoop = focusLoop && focusLoop !== oppositeOrientation;
    const canWrap = isGrid && focusWrap && focusWrap !== oppositeOrientation; // previous and up methods will set hasNullItem, but when calling next
    // directly, hasNullItem will only be true if if it's not a grid and
    // focusLoop is set to true, which means that pressing right or down keys
    // on grids will never focus the composite container element. On
    // one-dimensional composites that don't loop, pressing right or down
    // keys also doesn't focus on the composite container element.

    hasNullItem = hasNullItem || !isGrid && canLoop && includesBaseElement;

    if (canLoop) {
      const loopItems = canWrap && !hasNullItem ? allItems : getItemsInRow(allItems, activeItem.rowId);
      const sortedItems = flipItems(loopItems, activeIdRef.current, hasNullItem);
      const nextItem = findFirstEnabledItem(sortedItems, activeIdRef.current);
      return nextItem == null ? void 0 : nextItem.id;
    }

    if (canWrap) {
      const nextItem = findFirstEnabledItem( // We can use nextItems, which contains all the next items, including
      // items from other rows, to wrap between rows. However, if there is
      // a null item (the composite container), we'll only use the next
      // items in the row. So moving next from the last item will focus on
      // the composite container. On grid composites, horizontal navigation
      // never focuses on the composite container, only vertical.
      hasNullItem ? nextItemsInRow : nextItems, activeIdRef.current);
      const nextId = hasNullItem ? (nextItem == null ? void 0 : nextItem.id) || null : nextItem == null ? void 0 : nextItem.id;
      return nextId;
    }

    const nextItem = findFirstEnabledItem(nextItemsInRow, activeIdRef.current);

    if (!nextItem && hasNullItem) {
      return null;
    }

    return nextItem == null ? void 0 : nextItem.id;
  }, [focusLoop, focusWrap, includesBaseElement]);
  const next = useCallback(skip => {
    return getNextId(collection.items, orientation, false, skip);
  }, [getNextId, collection.items, orientation]);
  const previous = useCallback(skip => {
    var _findFirstEnabledItem3;

    // If activeId is initially set to null or if includesBaseElement is set
    // to true, then the composite container will be focusable while
    // navigating with arrow keys. But, if it's a grid, we don't want to
    // focus on the composite container with horizontal navigation.
    const isGrid = !!((_findFirstEnabledItem3 = findFirstEnabledItem(collection.items)) != null && _findFirstEnabledItem3.rowId);
    const hasNullItem = !isGrid && includesBaseElement;
    return getNextId(reverseArray(collection.items), orientation, hasNullItem, skip);
  }, [collection.items, getNextId, orientation, includesBaseElement]);
  const down = useCallback(skip => {
    const shouldShift = focusShift && !skip; // First, we make sure rows have the same number of items by filling it
    // with disabled fake items. Then, we reorganize the items.

    const verticalItems = verticalizeItems(flatten2DArray(normalizeRows(groupItemsByRows(collection.items), activeIdRef.current, shouldShift)));
    const canLoop = focusLoop && focusLoop !== "horizontal"; // Pressing down arrow key will only focus on the composite container if
    // loop is true, both, or vertical.

    const hasNullItem = canLoop && includesBaseElement;
    return getNextId(verticalItems, "vertical", hasNullItem, skip);
  }, [collection.items, getNextId, focusShift, focusLoop]);
  const up = useCallback(skip => {
    const shouldShift = focusShift && !skip;
    const verticalItems = verticalizeItems(reverseArray(flatten2DArray(normalizeRows(groupItemsByRows(collection.items), activeIdRef.current, shouldShift)))); // If activeId is initially set to null, we'll always focus on the
    // composite container when the up arrow key is pressed in the first row.

    const hasNullItem = includesBaseElement;
    return getNextId(verticalItems, "vertical", hasNullItem, skip);
  }, [collection.items, getNextId, focusShift]);
  const state = useMemo(() => ({ ...collection,
    baseRef,
    orientation,
    rtl,
    virtualFocus,
    focusLoop,
    focusWrap,
    focusShift,
    moves,
    setMoves,
    includesBaseElement,
    activeId,
    setActiveId,
    move,
    next,
    previous,
    up,
    down,
    first,
    last
  }), [collection, baseRef, orientation, rtl, virtualFocus, focusLoop, focusWrap, focusShift, moves, setMoves, includesBaseElement, activeId, setActiveId, move, next, previous, up, down, first, last]);
  return useStorePublisher(state);
}

/**
 * Custom positioning reference element.
 * @see https://floating-ui.com/docs/virtual-elements
 */

const min$1 = Math.min;
const max$1 = Math.max;
const round$1 = Math.round;
const floor$1 = Math.floor;
const createCoords = v => ({
  x: v,
  y: v
});
const oppositeSideMap = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom'
};
const oppositeAlignmentMap = {
  start: 'end',
  end: 'start'
};
function clamp(start, value, end) {
  return max$1(start, min$1(value, end));
}
function evaluate(value, param) {
  return typeof value === 'function' ? value(param) : value;
}
function getSide(placement) {
  return placement.split('-')[0];
}
function getAlignment(placement) {
  return placement.split('-')[1];
}
function getOppositeAxis(axis) {
  return axis === 'x' ? 'y' : 'x';
}
function getAxisLength(axis) {
  return axis === 'y' ? 'height' : 'width';
}
function getSideAxis(placement) {
  return ['top', 'bottom'].includes(getSide(placement)) ? 'y' : 'x';
}
function getAlignmentAxis(placement) {
  return getOppositeAxis(getSideAxis(placement));
}
function getAlignmentSides(placement, rects, rtl) {
  if (rtl === void 0) {
    rtl = false;
  }
  const alignment = getAlignment(placement);
  const alignmentAxis = getAlignmentAxis(placement);
  const length = getAxisLength(alignmentAxis);
  let mainAlignmentSide = alignmentAxis === 'x' ? alignment === (rtl ? 'end' : 'start') ? 'right' : 'left' : alignment === 'start' ? 'bottom' : 'top';
  if (rects.reference[length] > rects.floating[length]) {
    mainAlignmentSide = getOppositePlacement(mainAlignmentSide);
  }
  return [mainAlignmentSide, getOppositePlacement(mainAlignmentSide)];
}
function getExpandedPlacements(placement) {
  const oppositePlacement = getOppositePlacement(placement);
  return [getOppositeAlignmentPlacement(placement), oppositePlacement, getOppositeAlignmentPlacement(oppositePlacement)];
}
function getOppositeAlignmentPlacement(placement) {
  return placement.replace(/start|end/g, alignment => oppositeAlignmentMap[alignment]);
}
function getSideList(side, isStart, rtl) {
  const lr = ['left', 'right'];
  const rl = ['right', 'left'];
  const tb = ['top', 'bottom'];
  const bt = ['bottom', 'top'];
  switch (side) {
    case 'top':
    case 'bottom':
      if (rtl) return isStart ? rl : lr;
      return isStart ? lr : rl;
    case 'left':
    case 'right':
      return isStart ? tb : bt;
    default:
      return [];
  }
}
function getOppositeAxisPlacements(placement, flipAlignment, direction, rtl) {
  const alignment = getAlignment(placement);
  let list = getSideList(getSide(placement), direction === 'start', rtl);
  if (alignment) {
    list = list.map(side => side + "-" + alignment);
    if (flipAlignment) {
      list = list.concat(list.map(getOppositeAlignmentPlacement));
    }
  }
  return list;
}
function getOppositePlacement(placement) {
  return placement.replace(/left|right|bottom|top/g, side => oppositeSideMap[side]);
}
function expandPaddingObject(padding) {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...padding
  };
}
function getPaddingObject(padding) {
  return typeof padding !== 'number' ? expandPaddingObject(padding) : {
    top: padding,
    right: padding,
    bottom: padding,
    left: padding
  };
}
function rectToClientRect(rect) {
  return {
    ...rect,
    top: rect.y,
    left: rect.x,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  };
}

function computeCoordsFromPlacement(_ref, placement, rtl) {
  let {
    reference,
    floating
  } = _ref;
  const sideAxis = getSideAxis(placement);
  const alignmentAxis = getAlignmentAxis(placement);
  const alignLength = getAxisLength(alignmentAxis);
  const side = getSide(placement);
  const isVertical = sideAxis === 'y';
  const commonX = reference.x + reference.width / 2 - floating.width / 2;
  const commonY = reference.y + reference.height / 2 - floating.height / 2;
  const commonAlign = reference[alignLength] / 2 - floating[alignLength] / 2;
  let coords;
  switch (side) {
    case 'top':
      coords = {
        x: commonX,
        y: reference.y - floating.height
      };
      break;
    case 'bottom':
      coords = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;
    case 'right':
      coords = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;
    case 'left':
      coords = {
        x: reference.x - floating.width,
        y: commonY
      };
      break;
    default:
      coords = {
        x: reference.x,
        y: reference.y
      };
  }
  switch (getAlignment(placement)) {
    case 'start':
      coords[alignmentAxis] -= commonAlign * (rtl && isVertical ? -1 : 1);
      break;
    case 'end':
      coords[alignmentAxis] += commonAlign * (rtl && isVertical ? -1 : 1);
      break;
  }
  return coords;
}

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a given reference element.
 *
 * This export does not have any `platform` interface logic. You will need to
 * write one for the platform you are using Floating UI with.
 */
const computePosition$1 = async (reference, floating, config) => {
  const {
    placement = 'bottom',
    strategy = 'absolute',
    middleware = [],
    platform
  } = config;
  const validMiddleware = middleware.filter(Boolean);
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(floating));
  let rects = await platform.getElementRects({
    reference,
    floating,
    strategy
  });
  let {
    x,
    y
  } = computeCoordsFromPlacement(rects, placement, rtl);
  let statefulPlacement = placement;
  let middlewareData = {};
  let resetCount = 0;
  for (let i = 0; i < validMiddleware.length; i++) {
    const {
      name,
      fn
    } = validMiddleware[i];
    const {
      x: nextX,
      y: nextY,
      data,
      reset
    } = await fn({
      x,
      y,
      initialPlacement: placement,
      placement: statefulPlacement,
      strategy,
      middlewareData,
      rects,
      platform,
      elements: {
        reference,
        floating
      }
    });
    x = nextX != null ? nextX : x;
    y = nextY != null ? nextY : y;
    middlewareData = {
      ...middlewareData,
      [name]: {
        ...middlewareData[name],
        ...data
      }
    };
    if (reset && resetCount <= 50) {
      resetCount++;
      if (typeof reset === 'object') {
        if (reset.placement) {
          statefulPlacement = reset.placement;
        }
        if (reset.rects) {
          rects = reset.rects === true ? await platform.getElementRects({
            reference,
            floating,
            strategy
          }) : reset.rects;
        }
        ({
          x,
          y
        } = computeCoordsFromPlacement(rects, statefulPlacement, rtl));
      }
      i = -1;
      continue;
    }
  }
  return {
    x,
    y,
    placement: statefulPlacement,
    strategy,
    middlewareData
  };
};

/**
 * Resolves with an object of overflow side offsets that determine how much the
 * element is overflowing a given clipping boundary on each side.
 * - positive = overflowing the boundary by that number of pixels
 * - negative = how many pixels left before it will overflow
 * - 0 = lies flush with the boundary
 * @see https://floating-ui.com/docs/detectOverflow
 */
async function detectOverflow(state, options) {
  var _await$platform$isEle;
  if (options === void 0) {
    options = {};
  }
  const {
    x,
    y,
    platform,
    rects,
    elements,
    strategy
  } = state;
  const {
    boundary = 'clippingAncestors',
    rootBoundary = 'viewport',
    elementContext = 'floating',
    altBoundary = false,
    padding = 0
  } = evaluate(options, state);
  const paddingObject = getPaddingObject(padding);
  const altContext = elementContext === 'floating' ? 'reference' : 'floating';
  const element = elements[altBoundary ? altContext : elementContext];
  const clippingClientRect = rectToClientRect(await platform.getClippingRect({
    element: ((_await$platform$isEle = await (platform.isElement == null ? void 0 : platform.isElement(element))) != null ? _await$platform$isEle : true) ? element : element.contextElement || (await (platform.getDocumentElement == null ? void 0 : platform.getDocumentElement(elements.floating))),
    boundary,
    rootBoundary,
    strategy
  }));
  const rect = elementContext === 'floating' ? {
    ...rects.floating,
    x,
    y
  } : rects.reference;
  const offsetParent = await (platform.getOffsetParent == null ? void 0 : platform.getOffsetParent(elements.floating));
  const offsetScale = (await (platform.isElement == null ? void 0 : platform.isElement(offsetParent))) ? (await (platform.getScale == null ? void 0 : platform.getScale(offsetParent))) || {
    x: 1,
    y: 1
  } : {
    x: 1,
    y: 1
  };
  const elementClientRect = rectToClientRect(platform.convertOffsetParentRelativeRectToViewportRelativeRect ? await platform.convertOffsetParentRelativeRectToViewportRelativeRect({
    rect,
    offsetParent,
    strategy
  }) : rect);
  return {
    top: (clippingClientRect.top - elementClientRect.top + paddingObject.top) / offsetScale.y,
    bottom: (elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom) / offsetScale.y,
    left: (clippingClientRect.left - elementClientRect.left + paddingObject.left) / offsetScale.x,
    right: (elementClientRect.right - clippingClientRect.right + paddingObject.right) / offsetScale.x
  };
}

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * @see https://floating-ui.com/docs/arrow
 */
const arrow$1 = options => ({
  name: 'arrow',
  options,
  async fn(state) {
    const {
      x,
      y,
      placement,
      rects,
      platform,
      elements,
      middlewareData
    } = state;
    // Since `element` is required, we don't Partial<> the type.
    const {
      element,
      padding = 0
    } = evaluate(options, state) || {};
    if (element == null) {
      return {};
    }
    const paddingObject = getPaddingObject(padding);
    const coords = {
      x,
      y
    };
    const axis = getAlignmentAxis(placement);
    const length = getAxisLength(axis);
    const arrowDimensions = await platform.getDimensions(element);
    const isYAxis = axis === 'y';
    const minProp = isYAxis ? 'top' : 'left';
    const maxProp = isYAxis ? 'bottom' : 'right';
    const clientProp = isYAxis ? 'clientHeight' : 'clientWidth';
    const endDiff = rects.reference[length] + rects.reference[axis] - coords[axis] - rects.floating[length];
    const startDiff = coords[axis] - rects.reference[axis];
    const arrowOffsetParent = await (platform.getOffsetParent == null ? void 0 : platform.getOffsetParent(element));
    let clientSize = arrowOffsetParent ? arrowOffsetParent[clientProp] : 0;

    // DOM platform can return `window` as the `offsetParent`.
    if (!clientSize || !(await (platform.isElement == null ? void 0 : platform.isElement(arrowOffsetParent)))) {
      clientSize = elements.floating[clientProp] || rects.floating[length];
    }
    const centerToReference = endDiff / 2 - startDiff / 2;

    // If the padding is large enough that it causes the arrow to no longer be
    // centered, modify the padding so that it is centered.
    const largestPossiblePadding = clientSize / 2 - arrowDimensions[length] / 2 - 1;
    const minPadding = min$1(paddingObject[minProp], largestPossiblePadding);
    const maxPadding = min$1(paddingObject[maxProp], largestPossiblePadding);

    // Make sure the arrow doesn't overflow the floating element if the center
    // point is outside the floating element's bounds.
    const min$1$1 = minPadding;
    const max = clientSize - arrowDimensions[length] - maxPadding;
    const center = clientSize / 2 - arrowDimensions[length] / 2 + centerToReference;
    const offset = clamp(min$1$1, center, max);

    // If the reference is small enough that the arrow's padding causes it to
    // to point to nothing for an aligned placement, adjust the offset of the
    // floating element itself. To ensure `shift()` continues to take action,
    // a single reset is performed when this is true.
    const shouldAddOffset = !middlewareData.arrow && getAlignment(placement) != null && center != offset && rects.reference[length] / 2 - (center < min$1$1 ? minPadding : maxPadding) - arrowDimensions[length] / 2 < 0;
    const alignmentOffset = shouldAddOffset ? center < min$1$1 ? center - min$1$1 : center - max : 0;
    return {
      [axis]: coords[axis] + alignmentOffset,
      data: {
        [axis]: offset,
        centerOffset: center - offset - alignmentOffset,
        ...(shouldAddOffset && {
          alignmentOffset
        })
      },
      reset: shouldAddOffset
    };
  }
});

/**
 * Optimizes the visibility of the floating element by flipping the `placement`
 * in order to keep it in view when the preferred placement(s) will overflow the
 * clipping boundary. Alternative to `autoPlacement`.
 * @see https://floating-ui.com/docs/flip
 */
const flip$1 = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'flip',
    options,
    async fn(state) {
      var _middlewareData$arrow, _middlewareData$flip;
      const {
        placement,
        middlewareData,
        rects,
        initialPlacement,
        platform,
        elements
      } = state;
      const {
        mainAxis: checkMainAxis = true,
        crossAxis: checkCrossAxis = true,
        fallbackPlacements: specifiedFallbackPlacements,
        fallbackStrategy = 'bestFit',
        fallbackAxisSideDirection = 'none',
        flipAlignment = true,
        ...detectOverflowOptions
      } = evaluate(options, state);

      // If a reset by the arrow was caused due to an alignment offset being
      // added, we should skip any logic now since `flip()` has already done its
      // work.
      // https://github.com/floating-ui/floating-ui/issues/2549#issuecomment-1719601643
      if ((_middlewareData$arrow = middlewareData.arrow) != null && _middlewareData$arrow.alignmentOffset) {
        return {};
      }
      const side = getSide(placement);
      const isBasePlacement = getSide(initialPlacement) === initialPlacement;
      const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
      const fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipAlignment ? [getOppositePlacement(initialPlacement)] : getExpandedPlacements(initialPlacement));
      if (!specifiedFallbackPlacements && fallbackAxisSideDirection !== 'none') {
        fallbackPlacements.push(...getOppositeAxisPlacements(initialPlacement, flipAlignment, fallbackAxisSideDirection, rtl));
      }
      const placements = [initialPlacement, ...fallbackPlacements];
      const overflow = await detectOverflow(state, detectOverflowOptions);
      const overflows = [];
      let overflowsData = ((_middlewareData$flip = middlewareData.flip) == null ? void 0 : _middlewareData$flip.overflows) || [];
      if (checkMainAxis) {
        overflows.push(overflow[side]);
      }
      if (checkCrossAxis) {
        const sides = getAlignmentSides(placement, rects, rtl);
        overflows.push(overflow[sides[0]], overflow[sides[1]]);
      }
      overflowsData = [...overflowsData, {
        placement,
        overflows
      }];

      // One or more sides is overflowing.
      if (!overflows.every(side => side <= 0)) {
        var _middlewareData$flip2, _overflowsData$filter;
        const nextIndex = (((_middlewareData$flip2 = middlewareData.flip) == null ? void 0 : _middlewareData$flip2.index) || 0) + 1;
        const nextPlacement = placements[nextIndex];
        if (nextPlacement) {
          // Try next placement and re-run the lifecycle.
          return {
            data: {
              index: nextIndex,
              overflows: overflowsData
            },
            reset: {
              placement: nextPlacement
            }
          };
        }

        // First, find the candidates that fit on the mainAxis side of overflow,
        // then find the placement that fits the best on the main crossAxis side.
        let resetPlacement = (_overflowsData$filter = overflowsData.filter(d => d.overflows[0] <= 0).sort((a, b) => a.overflows[1] - b.overflows[1])[0]) == null ? void 0 : _overflowsData$filter.placement;

        // Otherwise fallback.
        if (!resetPlacement) {
          switch (fallbackStrategy) {
            case 'bestFit':
              {
                var _overflowsData$map$so;
                const placement = (_overflowsData$map$so = overflowsData.map(d => [d.placement, d.overflows.filter(overflow => overflow > 0).reduce((acc, overflow) => acc + overflow, 0)]).sort((a, b) => a[1] - b[1])[0]) == null ? void 0 : _overflowsData$map$so[0];
                if (placement) {
                  resetPlacement = placement;
                }
                break;
              }
            case 'initialPlacement':
              resetPlacement = initialPlacement;
              break;
          }
        }
        if (placement !== resetPlacement) {
          return {
            reset: {
              placement: resetPlacement
            }
          };
        }
      }
      return {};
    }
  };
};

// For type backwards-compatibility, the `OffsetOptions` type was also
// Derivable.

async function convertValueToCoords(state, options) {
  const {
    placement,
    platform,
    elements
  } = state;
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
  const side = getSide(placement);
  const alignment = getAlignment(placement);
  const isVertical = getSideAxis(placement) === 'y';
  const mainAxisMulti = ['left', 'top'].includes(side) ? -1 : 1;
  const crossAxisMulti = rtl && isVertical ? -1 : 1;
  const rawValue = evaluate(options, state);

  // eslint-disable-next-line prefer-const
  let {
    mainAxis,
    crossAxis,
    alignmentAxis
  } = typeof rawValue === 'number' ? {
    mainAxis: rawValue,
    crossAxis: 0,
    alignmentAxis: null
  } : {
    mainAxis: 0,
    crossAxis: 0,
    alignmentAxis: null,
    ...rawValue
  };
  if (alignment && typeof alignmentAxis === 'number') {
    crossAxis = alignment === 'end' ? alignmentAxis * -1 : alignmentAxis;
  }
  return isVertical ? {
    x: crossAxis * crossAxisMulti,
    y: mainAxis * mainAxisMulti
  } : {
    x: mainAxis * mainAxisMulti,
    y: crossAxis * crossAxisMulti
  };
}

/**
 * Modifies the placement by translating the floating element along the
 * specified axes.
 * A number (shorthand for `mainAxis` or distance), or an axes configuration
 * object may be passed.
 * @see https://floating-ui.com/docs/offset
 */
const offset = function (options) {
  if (options === void 0) {
    options = 0;
  }
  return {
    name: 'offset',
    options,
    async fn(state) {
      var _middlewareData$offse, _middlewareData$arrow;
      const {
        x,
        y,
        placement,
        middlewareData
      } = state;
      const diffCoords = await convertValueToCoords(state, options);

      // If the placement is the same and the arrow caused an alignment offset
      // then we don't need to change the positioning coordinates.
      if (placement === ((_middlewareData$offse = middlewareData.offset) == null ? void 0 : _middlewareData$offse.placement) && (_middlewareData$arrow = middlewareData.arrow) != null && _middlewareData$arrow.alignmentOffset) {
        return {};
      }
      return {
        x: x + diffCoords.x,
        y: y + diffCoords.y,
        data: {
          ...diffCoords,
          placement
        }
      };
    }
  };
};

/**
 * Optimizes the visibility of the floating element by shifting it in order to
 * keep it in view when it will overflow the clipping boundary.
 * @see https://floating-ui.com/docs/shift
 */
const shift$1 = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'shift',
    options,
    async fn(state) {
      const {
        x,
        y,
        placement
      } = state;
      const {
        mainAxis: checkMainAxis = true,
        crossAxis: checkCrossAxis = false,
        limiter = {
          fn: _ref => {
            let {
              x,
              y
            } = _ref;
            return {
              x,
              y
            };
          }
        },
        ...detectOverflowOptions
      } = evaluate(options, state);
      const coords = {
        x,
        y
      };
      const overflow = await detectOverflow(state, detectOverflowOptions);
      const crossAxis = getSideAxis(getSide(placement));
      const mainAxis = getOppositeAxis(crossAxis);
      let mainAxisCoord = coords[mainAxis];
      let crossAxisCoord = coords[crossAxis];
      if (checkMainAxis) {
        const minSide = mainAxis === 'y' ? 'top' : 'left';
        const maxSide = mainAxis === 'y' ? 'bottom' : 'right';
        const min = mainAxisCoord + overflow[minSide];
        const max = mainAxisCoord - overflow[maxSide];
        mainAxisCoord = clamp(min, mainAxisCoord, max);
      }
      if (checkCrossAxis) {
        const minSide = crossAxis === 'y' ? 'top' : 'left';
        const maxSide = crossAxis === 'y' ? 'bottom' : 'right';
        const min = crossAxisCoord + overflow[minSide];
        const max = crossAxisCoord - overflow[maxSide];
        crossAxisCoord = clamp(min, crossAxisCoord, max);
      }
      const limitedCoords = limiter.fn({
        ...state,
        [mainAxis]: mainAxisCoord,
        [crossAxis]: crossAxisCoord
      });
      return {
        ...limitedCoords,
        data: {
          x: limitedCoords.x - x,
          y: limitedCoords.y - y
        }
      };
    }
  };
};

/**
 * Provides data that allows you to change the size of the floating element —
 * for instance, prevent it from overflowing the clipping boundary or match the
 * width of the reference element.
 * @see https://floating-ui.com/docs/size
 */
const size$1 = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'size',
    options,
    async fn(state) {
      const {
        placement,
        rects,
        platform,
        elements
      } = state;
      const {
        apply = () => {},
        ...detectOverflowOptions
      } = evaluate(options, state);
      const overflow = await detectOverflow(state, detectOverflowOptions);
      const side = getSide(placement);
      const alignment = getAlignment(placement);
      const isYAxis = getSideAxis(placement) === 'y';
      const {
        width,
        height
      } = rects.floating;
      let heightSide;
      let widthSide;
      if (side === 'top' || side === 'bottom') {
        heightSide = side;
        widthSide = alignment === ((await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating))) ? 'start' : 'end') ? 'left' : 'right';
      } else {
        widthSide = side;
        heightSide = alignment === 'end' ? 'top' : 'bottom';
      }
      const overflowAvailableHeight = height - overflow[heightSide];
      const overflowAvailableWidth = width - overflow[widthSide];
      const noShift = !state.middlewareData.shift;
      let availableHeight = overflowAvailableHeight;
      let availableWidth = overflowAvailableWidth;
      if (isYAxis) {
        const maximumClippingWidth = width - overflow.left - overflow.right;
        availableWidth = alignment || noShift ? min$1(overflowAvailableWidth, maximumClippingWidth) : maximumClippingWidth;
      } else {
        const maximumClippingHeight = height - overflow.top - overflow.bottom;
        availableHeight = alignment || noShift ? min$1(overflowAvailableHeight, maximumClippingHeight) : maximumClippingHeight;
      }
      if (noShift && !alignment) {
        const xMin = max$1(overflow.left, 0);
        const xMax = max$1(overflow.right, 0);
        const yMin = max$1(overflow.top, 0);
        const yMax = max$1(overflow.bottom, 0);
        if (isYAxis) {
          availableWidth = width - 2 * (xMin !== 0 || xMax !== 0 ? xMin + xMax : max$1(overflow.left, overflow.right));
        } else {
          availableHeight = height - 2 * (yMin !== 0 || yMax !== 0 ? yMin + yMax : max$1(overflow.top, overflow.bottom));
        }
      }
      await apply({
        ...state,
        availableWidth,
        availableHeight
      });
      const nextDimensions = await platform.getDimensions(elements.floating);
      if (width !== nextDimensions.width || height !== nextDimensions.height) {
        return {
          reset: {
            rects: true
          }
        };
      }
      return {};
    }
  };
};

function getNodeName(node) {
  if (isNode$1(node)) {
    return (node.nodeName || '').toLowerCase();
  }
  // Mocked nodes in testing environments may not be instances of Node. By
  // returning `#document` an infinite loop won't occur.
  // https://github.com/floating-ui/floating-ui/issues/2317
  return '#document';
}
function getWindow$1(node) {
  var _node$ownerDocument;
  return (node == null || (_node$ownerDocument = node.ownerDocument) == null ? void 0 : _node$ownerDocument.defaultView) || window;
}
function getDocumentElement(node) {
  var _ref;
  return (_ref = (isNode$1(node) ? node.ownerDocument : node.document) || window.document) == null ? void 0 : _ref.documentElement;
}
function isNode$1(value) {
  return value instanceof Node || value instanceof getWindow$1(value).Node;
}
function isElement(value) {
  return value instanceof Element || value instanceof getWindow$1(value).Element;
}
function isHTMLElement$1(value) {
  return value instanceof HTMLElement || value instanceof getWindow$1(value).HTMLElement;
}
function isShadowRoot(value) {
  // Browsers without `ShadowRoot` support.
  if (typeof ShadowRoot === 'undefined') {
    return false;
  }
  return value instanceof ShadowRoot || value instanceof getWindow$1(value).ShadowRoot;
}
function isOverflowElement(element) {
  const {
    overflow,
    overflowX,
    overflowY,
    display
  } = getComputedStyle$1(element);
  return /auto|scroll|overlay|hidden|clip/.test(overflow + overflowY + overflowX) && !['inline', 'contents'].includes(display);
}
function isTableElement(element) {
  return ['table', 'td', 'th'].includes(getNodeName(element));
}
function isContainingBlock(element) {
  const webkit = isWebKit();
  const css = getComputedStyle$1(element);

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  return css.transform !== 'none' || css.perspective !== 'none' || (css.containerType ? css.containerType !== 'normal' : false) || !webkit && (css.backdropFilter ? css.backdropFilter !== 'none' : false) || !webkit && (css.filter ? css.filter !== 'none' : false) || ['transform', 'perspective', 'filter'].some(value => (css.willChange || '').includes(value)) || ['paint', 'layout', 'strict', 'content'].some(value => (css.contain || '').includes(value));
}
function getContainingBlock(element) {
  let currentNode = getParentNode(element);
  while (isHTMLElement$1(currentNode) && !isLastTraversableNode(currentNode)) {
    if (isContainingBlock(currentNode)) {
      return currentNode;
    } else {
      currentNode = getParentNode(currentNode);
    }
  }
  return null;
}
function isWebKit() {
  if (typeof CSS === 'undefined' || !CSS.supports) return false;
  return CSS.supports('-webkit-backdrop-filter', 'none');
}
function isLastTraversableNode(node) {
  return ['html', 'body', '#document'].includes(getNodeName(node));
}
function getComputedStyle$1(element) {
  return getWindow$1(element).getComputedStyle(element);
}
function getNodeScroll(element) {
  if (isElement(element)) {
    return {
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop
    };
  }
  return {
    scrollLeft: element.pageXOffset,
    scrollTop: element.pageYOffset
  };
}
function getParentNode(node) {
  if (getNodeName(node) === 'html') {
    return node;
  }
  const result =
  // Step into the shadow DOM of the parent of a slotted node.
  node.assignedSlot ||
  // DOM Element detected.
  node.parentNode ||
  // ShadowRoot detected.
  isShadowRoot(node) && node.host ||
  // Fallback.
  getDocumentElement(node);
  return isShadowRoot(result) ? result.host : result;
}
function getNearestOverflowAncestor(node) {
  const parentNode = getParentNode(node);
  if (isLastTraversableNode(parentNode)) {
    return node.ownerDocument ? node.ownerDocument.body : node.body;
  }
  if (isHTMLElement$1(parentNode) && isOverflowElement(parentNode)) {
    return parentNode;
  }
  return getNearestOverflowAncestor(parentNode);
}
function getOverflowAncestors(node, list, traverseIframes) {
  var _node$ownerDocument2;
  if (list === void 0) {
    list = [];
  }
  if (traverseIframes === void 0) {
    traverseIframes = true;
  }
  const scrollableAncestor = getNearestOverflowAncestor(node);
  const isBody = scrollableAncestor === ((_node$ownerDocument2 = node.ownerDocument) == null ? void 0 : _node$ownerDocument2.body);
  const win = getWindow$1(scrollableAncestor);
  if (isBody) {
    return list.concat(win, win.visualViewport || [], isOverflowElement(scrollableAncestor) ? scrollableAncestor : [], win.frameElement && traverseIframes ? getOverflowAncestors(win.frameElement) : []);
  }
  return list.concat(scrollableAncestor, getOverflowAncestors(scrollableAncestor, [], traverseIframes));
}

function getCssDimensions(element) {
  const css = getComputedStyle$1(element);
  // In testing environments, the `width` and `height` properties are empty
  // strings for SVG elements, returning NaN. Fallback to `0` in this case.
  let width = parseFloat(css.width) || 0;
  let height = parseFloat(css.height) || 0;
  const hasOffset = isHTMLElement$1(element);
  const offsetWidth = hasOffset ? element.offsetWidth : width;
  const offsetHeight = hasOffset ? element.offsetHeight : height;
  const shouldFallback = round$1(width) !== offsetWidth || round$1(height) !== offsetHeight;
  if (shouldFallback) {
    width = offsetWidth;
    height = offsetHeight;
  }
  return {
    width,
    height,
    $: shouldFallback
  };
}

function unwrapElement(element) {
  return !isElement(element) ? element.contextElement : element;
}

function getScale(element) {
  const domElement = unwrapElement(element);
  if (!isHTMLElement$1(domElement)) {
    return createCoords(1);
  }
  const rect = domElement.getBoundingClientRect();
  const {
    width,
    height,
    $
  } = getCssDimensions(domElement);
  let x = ($ ? round$1(rect.width) : rect.width) / width;
  let y = ($ ? round$1(rect.height) : rect.height) / height;

  // 0, NaN, or Infinity should always fallback to 1.

  if (!x || !Number.isFinite(x)) {
    x = 1;
  }
  if (!y || !Number.isFinite(y)) {
    y = 1;
  }
  return {
    x,
    y
  };
}

const noOffsets = /*#__PURE__*/createCoords(0);
function getVisualOffsets(element) {
  const win = getWindow$1(element);
  if (!isWebKit() || !win.visualViewport) {
    return noOffsets;
  }
  return {
    x: win.visualViewport.offsetLeft,
    y: win.visualViewport.offsetTop
  };
}
function shouldAddVisualOffsets(element, isFixed, floatingOffsetParent) {
  if (isFixed === void 0) {
    isFixed = false;
  }
  if (!floatingOffsetParent || isFixed && floatingOffsetParent !== getWindow$1(element)) {
    return false;
  }
  return isFixed;
}

function getBoundingClientRect(element, includeScale, isFixedStrategy, offsetParent) {
  if (includeScale === void 0) {
    includeScale = false;
  }
  if (isFixedStrategy === void 0) {
    isFixedStrategy = false;
  }
  const clientRect = element.getBoundingClientRect();
  const domElement = unwrapElement(element);
  let scale = createCoords(1);
  if (includeScale) {
    if (offsetParent) {
      if (isElement(offsetParent)) {
        scale = getScale(offsetParent);
      }
    } else {
      scale = getScale(element);
    }
  }
  const visualOffsets = shouldAddVisualOffsets(domElement, isFixedStrategy, offsetParent) ? getVisualOffsets(domElement) : createCoords(0);
  let x = (clientRect.left + visualOffsets.x) / scale.x;
  let y = (clientRect.top + visualOffsets.y) / scale.y;
  let width = clientRect.width / scale.x;
  let height = clientRect.height / scale.y;
  if (domElement) {
    const win = getWindow$1(domElement);
    const offsetWin = offsetParent && isElement(offsetParent) ? getWindow$1(offsetParent) : offsetParent;
    let currentIFrame = win.frameElement;
    while (currentIFrame && offsetParent && offsetWin !== win) {
      const iframeScale = getScale(currentIFrame);
      const iframeRect = currentIFrame.getBoundingClientRect();
      const css = getComputedStyle$1(currentIFrame);
      const left = iframeRect.left + (currentIFrame.clientLeft + parseFloat(css.paddingLeft)) * iframeScale.x;
      const top = iframeRect.top + (currentIFrame.clientTop + parseFloat(css.paddingTop)) * iframeScale.y;
      x *= iframeScale.x;
      y *= iframeScale.y;
      width *= iframeScale.x;
      height *= iframeScale.y;
      x += left;
      y += top;
      currentIFrame = getWindow$1(currentIFrame).frameElement;
    }
  }
  return rectToClientRect({
    width,
    height,
    x,
    y
  });
}

function convertOffsetParentRelativeRectToViewportRelativeRect(_ref) {
  let {
    rect,
    offsetParent,
    strategy
  } = _ref;
  const isOffsetParentAnElement = isHTMLElement$1(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  if (offsetParent === documentElement) {
    return rect;
  }
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  let scale = createCoords(1);
  const offsets = createCoords(0);
  if (isOffsetParentAnElement || !isOffsetParentAnElement && strategy !== 'fixed') {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isHTMLElement$1(offsetParent)) {
      const offsetRect = getBoundingClientRect(offsetParent);
      scale = getScale(offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    }
  }
  return {
    width: rect.width * scale.x,
    height: rect.height * scale.y,
    x: rect.x * scale.x - scroll.scrollLeft * scale.x + offsets.x,
    y: rect.y * scale.y - scroll.scrollTop * scale.y + offsets.y
  };
}

function getClientRects(element) {
  return Array.from(element.getClientRects());
}

function getWindowScrollBarX(element) {
  // If <html> has a CSS width greater than the viewport, then this will be
  // incorrect for RTL.
  return getBoundingClientRect(getDocumentElement(element)).left + getNodeScroll(element).scrollLeft;
}

// Gets the entire size of the scrollable document area, even extending outside
// of the `<html>` and `<body>` rect bounds if horizontally scrollable.
function getDocumentRect(element) {
  const html = getDocumentElement(element);
  const scroll = getNodeScroll(element);
  const body = element.ownerDocument.body;
  const width = max$1(html.scrollWidth, html.clientWidth, body.scrollWidth, body.clientWidth);
  const height = max$1(html.scrollHeight, html.clientHeight, body.scrollHeight, body.clientHeight);
  let x = -scroll.scrollLeft + getWindowScrollBarX(element);
  const y = -scroll.scrollTop;
  if (getComputedStyle$1(body).direction === 'rtl') {
    x += max$1(html.clientWidth, body.clientWidth) - width;
  }
  return {
    width,
    height,
    x,
    y
  };
}

function getViewportRect(element, strategy) {
  const win = getWindow$1(element);
  const html = getDocumentElement(element);
  const visualViewport = win.visualViewport;
  let width = html.clientWidth;
  let height = html.clientHeight;
  let x = 0;
  let y = 0;
  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;
    const visualViewportBased = isWebKit();
    if (!visualViewportBased || visualViewportBased && strategy === 'fixed') {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }
  return {
    width,
    height,
    x,
    y
  };
}

// Returns the inner client rect, subtracting scrollbars if present.
function getInnerBoundingClientRect(element, strategy) {
  const clientRect = getBoundingClientRect(element, true, strategy === 'fixed');
  const top = clientRect.top + element.clientTop;
  const left = clientRect.left + element.clientLeft;
  const scale = isHTMLElement$1(element) ? getScale(element) : createCoords(1);
  const width = element.clientWidth * scale.x;
  const height = element.clientHeight * scale.y;
  const x = left * scale.x;
  const y = top * scale.y;
  return {
    width,
    height,
    x,
    y
  };
}
function getClientRectFromClippingAncestor(element, clippingAncestor, strategy) {
  let rect;
  if (clippingAncestor === 'viewport') {
    rect = getViewportRect(element, strategy);
  } else if (clippingAncestor === 'document') {
    rect = getDocumentRect(getDocumentElement(element));
  } else if (isElement(clippingAncestor)) {
    rect = getInnerBoundingClientRect(clippingAncestor, strategy);
  } else {
    const visualOffsets = getVisualOffsets(element);
    rect = {
      ...clippingAncestor,
      x: clippingAncestor.x - visualOffsets.x,
      y: clippingAncestor.y - visualOffsets.y
    };
  }
  return rectToClientRect(rect);
}
function hasFixedPositionAncestor(element, stopNode) {
  const parentNode = getParentNode(element);
  if (parentNode === stopNode || !isElement(parentNode) || isLastTraversableNode(parentNode)) {
    return false;
  }
  return getComputedStyle$1(parentNode).position === 'fixed' || hasFixedPositionAncestor(parentNode, stopNode);
}

// A "clipping ancestor" is an `overflow` element with the characteristic of
// clipping (or hiding) child elements. This returns all clipping ancestors
// of the given element up the tree.
function getClippingElementAncestors(element, cache) {
  const cachedResult = cache.get(element);
  if (cachedResult) {
    return cachedResult;
  }
  let result = getOverflowAncestors(element, [], false).filter(el => isElement(el) && getNodeName(el) !== 'body');
  let currentContainingBlockComputedStyle = null;
  const elementIsFixed = getComputedStyle$1(element).position === 'fixed';
  let currentNode = elementIsFixed ? getParentNode(element) : element;

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  while (isElement(currentNode) && !isLastTraversableNode(currentNode)) {
    const computedStyle = getComputedStyle$1(currentNode);
    const currentNodeIsContaining = isContainingBlock(currentNode);
    if (!currentNodeIsContaining && computedStyle.position === 'fixed') {
      currentContainingBlockComputedStyle = null;
    }
    const shouldDropCurrentNode = elementIsFixed ? !currentNodeIsContaining && !currentContainingBlockComputedStyle : !currentNodeIsContaining && computedStyle.position === 'static' && !!currentContainingBlockComputedStyle && ['absolute', 'fixed'].includes(currentContainingBlockComputedStyle.position) || isOverflowElement(currentNode) && !currentNodeIsContaining && hasFixedPositionAncestor(element, currentNode);
    if (shouldDropCurrentNode) {
      // Drop non-containing blocks.
      result = result.filter(ancestor => ancestor !== currentNode);
    } else {
      // Record last containing block for next iteration.
      currentContainingBlockComputedStyle = computedStyle;
    }
    currentNode = getParentNode(currentNode);
  }
  cache.set(element, result);
  return result;
}

// Gets the maximum area that the element is visible in due to any number of
// clipping ancestors.
function getClippingRect(_ref) {
  let {
    element,
    boundary,
    rootBoundary,
    strategy
  } = _ref;
  const elementClippingAncestors = boundary === 'clippingAncestors' ? getClippingElementAncestors(element, this._c) : [].concat(boundary);
  const clippingAncestors = [...elementClippingAncestors, rootBoundary];
  const firstClippingAncestor = clippingAncestors[0];
  const clippingRect = clippingAncestors.reduce((accRect, clippingAncestor) => {
    const rect = getClientRectFromClippingAncestor(element, clippingAncestor, strategy);
    accRect.top = max$1(rect.top, accRect.top);
    accRect.right = min$1(rect.right, accRect.right);
    accRect.bottom = min$1(rect.bottom, accRect.bottom);
    accRect.left = max$1(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromClippingAncestor(element, firstClippingAncestor, strategy));
  return {
    width: clippingRect.right - clippingRect.left,
    height: clippingRect.bottom - clippingRect.top,
    x: clippingRect.left,
    y: clippingRect.top
  };
}

function getDimensions(element) {
  const {
    width,
    height
  } = getCssDimensions(element);
  return {
    width,
    height
  };
}

function getRectRelativeToOffsetParent(element, offsetParent, strategy) {
  const isOffsetParentAnElement = isHTMLElement$1(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  const isFixed = strategy === 'fixed';
  const rect = getBoundingClientRect(element, true, isFixed, offsetParent);
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const offsets = createCoords(0);
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isOffsetParentAnElement) {
      const offsetRect = getBoundingClientRect(offsetParent, true, isFixed, offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    } else if (documentElement) {
      offsets.x = getWindowScrollBarX(documentElement);
    }
  }
  return {
    x: rect.left + scroll.scrollLeft - offsets.x,
    y: rect.top + scroll.scrollTop - offsets.y,
    width: rect.width,
    height: rect.height
  };
}

function getTrueOffsetParent(element, polyfill) {
  if (!isHTMLElement$1(element) || getComputedStyle$1(element).position === 'fixed') {
    return null;
  }
  if (polyfill) {
    return polyfill(element);
  }
  return element.offsetParent;
}

// Gets the closest ancestor positioned element. Handles some edge cases,
// such as table ancestors and cross browser bugs.
function getOffsetParent(element, polyfill) {
  const window = getWindow$1(element);
  if (!isHTMLElement$1(element)) {
    return window;
  }
  let offsetParent = getTrueOffsetParent(element, polyfill);
  while (offsetParent && isTableElement(offsetParent) && getComputedStyle$1(offsetParent).position === 'static') {
    offsetParent = getTrueOffsetParent(offsetParent, polyfill);
  }
  if (offsetParent && (getNodeName(offsetParent) === 'html' || getNodeName(offsetParent) === 'body' && getComputedStyle$1(offsetParent).position === 'static' && !isContainingBlock(offsetParent))) {
    return window;
  }
  return offsetParent || getContainingBlock(element) || window;
}

const getElementRects = async function (_ref) {
  let {
    reference,
    floating,
    strategy
  } = _ref;
  const getOffsetParentFn = this.getOffsetParent || getOffsetParent;
  const getDimensionsFn = this.getDimensions;
  return {
    reference: getRectRelativeToOffsetParent(reference, await getOffsetParentFn(floating), strategy),
    floating: {
      x: 0,
      y: 0,
      ...(await getDimensionsFn(floating))
    }
  };
};

function isRTL(element) {
  return getComputedStyle$1(element).direction === 'rtl';
}

const platform = {
  convertOffsetParentRelativeRectToViewportRelativeRect,
  getDocumentElement,
  getClippingRect,
  getOffsetParent,
  getElementRects,
  getClientRects,
  getDimensions,
  getScale,
  isElement,
  isRTL
};

// https://samthor.au/2021/observing-dom/
function observeMove(element, onMove) {
  let io = null;
  let timeoutId;
  const root = getDocumentElement(element);
  function cleanup() {
    clearTimeout(timeoutId);
    io && io.disconnect();
    io = null;
  }
  function refresh(skip, threshold) {
    if (skip === void 0) {
      skip = false;
    }
    if (threshold === void 0) {
      threshold = 1;
    }
    cleanup();
    const {
      left,
      top,
      width,
      height
    } = element.getBoundingClientRect();
    if (!skip) {
      onMove();
    }
    if (!width || !height) {
      return;
    }
    const insetTop = floor$1(top);
    const insetRight = floor$1(root.clientWidth - (left + width));
    const insetBottom = floor$1(root.clientHeight - (top + height));
    const insetLeft = floor$1(left);
    const rootMargin = -insetTop + "px " + -insetRight + "px " + -insetBottom + "px " + -insetLeft + "px";
    const options = {
      rootMargin,
      threshold: max$1(0, min$1(1, threshold)) || 1
    };
    let isFirstUpdate = true;
    function handleObserve(entries) {
      const ratio = entries[0].intersectionRatio;
      if (ratio !== threshold) {
        if (!isFirstUpdate) {
          return refresh();
        }
        if (!ratio) {
          timeoutId = setTimeout(() => {
            refresh(false, 1e-7);
          }, 100);
        } else {
          refresh(false, ratio);
        }
      }
      isFirstUpdate = false;
    }

    // Older browsers don't support a `document` as the root and will throw an
    // error.
    try {
      io = new IntersectionObserver(handleObserve, {
        ...options,
        // Handle <iframe>s
        root: root.ownerDocument
      });
    } catch (e) {
      io = new IntersectionObserver(handleObserve, options);
    }
    io.observe(element);
  }
  refresh(true);
  return cleanup;
}

/**
 * Automatically updates the position of the floating element when necessary.
 * Should only be called when the floating element is mounted on the DOM or
 * visible on the screen.
 * @returns cleanup function that should be invoked when the floating element is
 * removed from the DOM or hidden from the screen.
 * @see https://floating-ui.com/docs/autoUpdate
 */
function autoUpdate(reference, floating, update, options) {
  if (options === void 0) {
    options = {};
  }
  const {
    ancestorScroll = true,
    ancestorResize = true,
    elementResize = typeof ResizeObserver === 'function',
    layoutShift = typeof IntersectionObserver === 'function',
    animationFrame = false
  } = options;
  const referenceEl = unwrapElement(reference);
  const ancestors = ancestorScroll || ancestorResize ? [...(referenceEl ? getOverflowAncestors(referenceEl) : []), ...getOverflowAncestors(floating)] : [];
  ancestors.forEach(ancestor => {
    ancestorScroll && ancestor.addEventListener('scroll', update, {
      passive: true
    });
    ancestorResize && ancestor.addEventListener('resize', update);
  });
  const cleanupIo = referenceEl && layoutShift ? observeMove(referenceEl, update) : null;
  let reobserveFrame = -1;
  let resizeObserver = null;
  if (elementResize) {
    resizeObserver = new ResizeObserver(_ref => {
      let [firstEntry] = _ref;
      if (firstEntry && firstEntry.target === referenceEl && resizeObserver) {
        // Prevent update loops when using the `size` middleware.
        // https://github.com/floating-ui/floating-ui/issues/1740
        resizeObserver.unobserve(floating);
        cancelAnimationFrame(reobserveFrame);
        reobserveFrame = requestAnimationFrame(() => {
          resizeObserver && resizeObserver.observe(floating);
        });
      }
      update();
    });
    if (referenceEl && !animationFrame) {
      resizeObserver.observe(referenceEl);
    }
    resizeObserver.observe(floating);
  }
  let frameId;
  let prevRefRect = animationFrame ? getBoundingClientRect(reference) : null;
  if (animationFrame) {
    frameLoop();
  }
  function frameLoop() {
    const nextRefRect = getBoundingClientRect(reference);
    if (prevRefRect && (nextRefRect.x !== prevRefRect.x || nextRefRect.y !== prevRefRect.y || nextRefRect.width !== prevRefRect.width || nextRefRect.height !== prevRefRect.height)) {
      update();
    }
    prevRefRect = nextRefRect;
    frameId = requestAnimationFrame(frameLoop);
  }
  update();
  return () => {
    ancestors.forEach(ancestor => {
      ancestorScroll && ancestor.removeEventListener('scroll', update);
      ancestorResize && ancestor.removeEventListener('resize', update);
    });
    cleanupIo && cleanupIo();
    resizeObserver && resizeObserver.disconnect();
    resizeObserver = null;
    if (animationFrame) {
      cancelAnimationFrame(frameId);
    }
  };
}

/**
 * Optimizes the visibility of the floating element by shifting it in order to
 * keep it in view when it will overflow the clipping boundary.
 * @see https://floating-ui.com/docs/shift
 */
const shift = shift$1;

/**
 * Optimizes the visibility of the floating element by flipping the `placement`
 * in order to keep it in view when the preferred placement(s) will overflow the
 * clipping boundary. Alternative to `autoPlacement`.
 * @see https://floating-ui.com/docs/flip
 */
const flip = flip$1;

/**
 * Provides data that allows you to change the size of the floating element —
 * for instance, prevent it from overflowing the clipping boundary or match the
 * width of the reference element.
 * @see https://floating-ui.com/docs/size
 */
const size = size$1;

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * @see https://floating-ui.com/docs/arrow
 */
const arrow = arrow$1;

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a given reference element.
 */
const computePosition = (reference, floating, options) => {
  // This caches the expensive `getClippingElementAncestors` function so that
  // multiple lifecycle resets re-use the same result. It only lives for a
  // single call. If other functions become expensive, we can add them as well.
  const cache = new Map();
  const mergedOptions = {
    platform,
    ...options
  };
  const platformWithCache = {
    ...mergedOptions.platform,
    _c: cache
  };
  return computePosition$1(reference, floating, {
    ...mergedOptions,
    platform: platformWithCache
  });
};

/**
 * Provides state for the `Disclosure` components.
 * @example
 * ```jsx
 * const disclosure = useDisclosureState();
 * <Disclosure state={disclosure}>Disclosure</Disclosure>
 * <DisclosureContent state={disclosure}>Content</DisclosureContent>
 * ```
 */
function useDisclosureState(_temp) {
  var _props$defaultOpen;

  let {
    animated = false,
    ...props
  } = _temp === void 0 ? {} : _temp;
  const disclosureRef = useRef(null);
  const [open, setOpen] = useControlledState((_props$defaultOpen = props.defaultOpen) != null ? _props$defaultOpen : false, props.open, props.setOpen);
  const [contentElement, setContentElement] = useState(null);
  const [animating, setAnimating] = useState(!!animated && open);
  const prevOpen = usePreviousValue(open);
  const mounted = open || animating;

  if (animated && !animating && prevOpen !== open) {
    setAnimating(true);
  }

  const show = useCallback(() => setOpen(true), [setOpen]);
  const hide = useCallback(() => setOpen(false), [setOpen]);
  const toggle = useCallback(() => setOpen(v => !v), [setOpen]);
  const stopAnimation = useCallback(() => setAnimating(false), []);
  const state = useMemo(() => ({
    disclosureRef,
    open,
    mounted,
    animated,
    animating,
    contentElement,
    setContentElement,
    setOpen,
    show,
    hide,
    toggle,
    stopAnimation
  }), [open, mounted, animated, animating, contentElement, setContentElement, setOpen, show, hide, toggle, stopAnimation]);
  return state;
}

/**
 * Provides state for the `Dialog` components.
 * @example
 * ```jsx
 * const dialog = useDialogState();
 * <button onClick={dialog.toggle}>Open dialog</button>
 * <Dialog state={dialog}>Content</Dialog>
 * ```
 */

function useDialogState(props) {
  if (props === void 0) {
    props = {};
  }

  const disclosure = useDisclosureState(props);
  return disclosure;
}

const middlewares = {
  arrow,
  flip,
  offset,
  shift,
  size
};

function createDOMRect(x, y, width, height) {
  if (x === void 0) {
    x = 0;
  }

  if (y === void 0) {
    y = 0;
  }

  if (width === void 0) {
    width = 0;
  }

  if (height === void 0) {
    height = 0;
  }

  if (typeof DOMRect === "function") {
    return new DOMRect(x, y, width, height);
  } // JSDOM doesn't support DOMRect constructor.


  const rect = {
    x,
    y,
    width,
    height,
    top: y,
    right: x + width,
    bottom: y + height,
    left: x
  };
  return { ...rect,
    toJSON: () => rect
  };
}

function getDOMRect(anchorRect) {
  if (!anchorRect) return createDOMRect();
  const {
    x,
    y,
    width,
    height
  } = anchorRect;
  return createDOMRect(x, y, width, height);
}

function getAnchorElement(anchorRef, getAnchorRect) {
  // https://floating-ui.com/docs/virtual-elements
  const contextElement = anchorRef.current || undefined;
  return {
    contextElement,
    getBoundingClientRect: () => {
      const anchor = anchorRef.current;
      const anchorRect = getAnchorRect(anchor);

      if (anchorRect || !anchor) {
        return getDOMRect(anchorRect);
      }

      return anchor.getBoundingClientRect();
    }
  };
}

function isValidPlacement(flip) {
  return /^(?:top|bottom|left|right)(?:-(?:start|end))?$/.test(flip);
}
/**
 * Provides state for the `Popover` components.
 * @example
 * ```jsx
 * const popover = usePopoverState();
 * <PopoverDisclosure state={popover}>Disclosure</PopoverDisclosure>
 * <Popover state={popover}>Popover</Popover>
 * ```
 */


function usePopoverState(_temp) {
  let {
    placement = "bottom",
    fixed = false,
    gutter,
    flip = true,
    shift = 0,
    slide = true,
    overlap = false,
    sameWidth = false,
    fitViewport = false,
    arrowPadding = 4,
    overflowPadding = 8,
    renderCallback,
    ...props
  } = _temp === void 0 ? {} : _temp;
  const dialog = useDialogState(props);

  const defaultGetAnchorRect = anchor => (anchor == null ? void 0 : anchor.getBoundingClientRect()) || null;

  const getAnchorRect = useEvent$1(props.getAnchorRect || defaultGetAnchorRect);
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const arrowRef = useRef(null);
  const [currentPlacement, setCurrentPlacement] = useState(placement);
  const [rendered, render] = useForceUpdate$1();
  useSafeLayoutEffect(() => {
    var _dialog$contentElemen;

    if (!((_dialog$contentElemen = dialog.contentElement) != null && _dialog$contentElemen.isConnected)) return;
    const popover = popoverRef.current;
    if (!popover) return;
    const anchor = getAnchorElement(anchorRef, getAnchorRect);
    const arrow = arrowRef.current;
    const arrowOffset = ((arrow == null ? void 0 : arrow.clientHeight) || 0) / 2;
    const finalGutter = typeof gutter === "number" ? gutter + arrowOffset : gutter != null ? gutter : arrowOffset;
    popover.style.setProperty("--popover-overflow-padding", overflowPadding + "px");

    const defaultRenderCallback = () => {
      const update = async () => {
        if (!dialog.mounted) return;
        const middleware = [// https://floating-ui.com/docs/offset
        middlewares.offset(_ref => {
          let {
            placement
          } = _ref;
          // If there's no placement alignment (*-start or *-end), we'll
          // fallback to the crossAxis offset as it also works for
          // center-aligned placements.
          const hasAlignment = !!placement.split("-")[1];
          return {
            crossAxis: !hasAlignment ? shift : undefined,
            mainAxis: finalGutter,
            alignmentAxis: shift
          };
        })];

        if (flip !== false) {
          const fallbackPlacements = typeof flip === "string" ? flip.split(" ") : undefined;

          if (fallbackPlacements !== undefined && !fallbackPlacements.every(isValidPlacement)) {
            throw new Error("`flip` expects a spaced-delimited list of placements");
          } // https://floating-ui.com/docs/flip


          middleware.push(middlewares.flip({
            padding: overflowPadding,
            fallbackPlacements: fallbackPlacements
          }));
        }

        if (slide || overlap) {
          // https://floating-ui.com/docs/shift
          middleware.push(middlewares.shift({
            mainAxis: slide,
            crossAxis: overlap,
            padding: overflowPadding
          }));
        } // https://floating-ui.com/docs/size


        middleware.push(middlewares.size({
          padding: overflowPadding,

          apply(_ref2) {
            let {
              availableWidth,
              availableHeight,
              rects
            } = _ref2;
            const referenceWidth = Math.round(rects.reference.width);
            availableWidth = Math.floor(availableWidth);
            availableHeight = Math.floor(availableHeight);
            popover.style.setProperty("--popover-anchor-width", referenceWidth + "px");
            popover.style.setProperty("--popover-available-width", availableWidth + "px");
            popover.style.setProperty("--popover-available-height", availableHeight + "px");

            if (sameWidth) {
              popover.style.width = referenceWidth + "px";
            }

            if (fitViewport) {
              popover.style.maxWidth = availableWidth + "px";
              popover.style.maxHeight = availableHeight + "px";
            }
          }

        }));

        if (arrow) {
          // https://floating-ui.com/docs/arrow
          middleware.push(middlewares.arrow({
            element: arrow,
            padding: arrowPadding
          }));
        } // https://floating-ui.com/docs/computePosition


        const pos = await computePosition(anchor, popover, {
          placement,
          strategy: fixed ? "fixed" : "absolute",
          middleware
        }); // Update the current placement state synchronously to avoid styling
        // flashes. For example, without this, a popover that has initially
        // placement set to "bottom", but gets flipped to "top" will have a
        // frame where the popover arrow is not properly rotated (PopoverArrow
        // uses the currentPlacement state).

        flushSync(() => {
          setCurrentPlacement(pos.placement);
        });
        const x = Math.round(pos.x);
        const y = Math.round(pos.y); // https://floating-ui.com/docs/misc#subpixel-and-accelerated-positioning

        Object.assign(popover.style, {
          top: "0",
          left: "0",
          transform: "translate3d(" + x + "px, " + y + "px, 0)"
        }); // https://floating-ui.com/docs/arrow#usage

        if (arrow && pos.middlewareData.arrow) {
          const {
            x: arrowX,
            y: arrowY
          } = pos.middlewareData.arrow;
          const dir = pos.placement.split("-")[0];
          Object.assign(arrow.style, {
            left: arrowX != null ? arrowX + "px" : "",
            top: arrowY != null ? arrowY + "px" : "",
            [dir]: "100%"
          });
        }
      }; // https://floating-ui.com/docs/autoUpdate


      return autoUpdate(anchor, popover, update, {
        // JSDOM doesn't support ResizeObserver
        elementResize: typeof ResizeObserver === "function"
      });
    };

    if (renderCallback) {
      return renderCallback({
        mounted: dialog.mounted,
        placement,
        fixed,
        gutter: finalGutter,
        shift,
        overlap,
        flip,
        sameWidth,
        fitViewport,
        arrowPadding,
        overflowPadding,
        popover,
        anchor,
        arrow,
        setPlacement: setCurrentPlacement,
        defaultRenderCallback
      });
    }

    return defaultRenderCallback();
  }, [rendered, dialog.contentElement, getAnchorRect, gutter, dialog.mounted, shift, overlap, flip, overflowPadding, slide, sameWidth, fitViewport, arrowPadding, placement, fixed, renderCallback]);
  const state = useMemo(() => ({ ...dialog,
    getAnchorRect,
    anchorRef,
    popoverRef,
    arrowRef,
    currentPlacement,
    placement,
    fixed,
    gutter,
    shift,
    flip,
    slide,
    overlap,
    sameWidth,
    fitViewport,
    arrowPadding,
    overflowPadding,
    render,
    renderCallback
  }), [dialog, getAnchorRect, currentPlacement, placement, fixed, gutter, shift, flip, slide, overlap, sameWidth, fitViewport, arrowPadding, overflowPadding, render, renderCallback]);
  return state;
}

function isGrid(items) {
  return items.some(item => !!item.rowId);
}

function isPrintableKey(event) {
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey;
}

function isModifierKey(event) {
  return event.key === "Shift" || event.key === "Control" || event.key === "Alt" || event.key === "Meta";
}

function canProxyKeyboardEvent(event, state) {
  if (!isSelfTarget(event)) return false;
  if (isModifierKey(event)) return false;
  const target = event.target;
  if (!target) return true;

  if (isTextField(target)) {
    // Printable characters shouldn't perform actions on the composite items if
    // the composite widget is a combobox.
    if (isPrintableKey(event)) return false;
    const grid = isGrid(state.items);
    const focusingInputOnly = state.activeId === null; // Pressing Home or End keys on the text field should only be allowed when
    // the widget has rows and the input is not the only element with focus.
    // That is, the aria-activedescendant has no value.

    const allowHorizontalNavigationOnItems = grid && !focusingInputOnly;
    const isHomeOrEnd = event.key === "Home" || event.key === "End"; // If there are no rows or the input is the only focused element, then we
    // should stop the event propagation so no action is performed on the
    // composite items, but only on the input, like moving the caret/selection.

    if (!allowHorizontalNavigationOnItems && isHomeOrEnd) return false;
  }

  return !event.isPropagationStopped();
}

function useKeyboardEventProxy(state, activeItem, onKeyboardEvent, previousElementRef) {
  return useEvent$1(event => {
    onKeyboardEvent == null ? void 0 : onKeyboardEvent(event);
    if (event.defaultPrevented) return;
    const activeElement = activeItem == null ? void 0 : activeItem.ref.current;
    if (!activeElement) return;
    if (!canProxyKeyboardEvent(event, state)) return;
    const {
      view,
      ...eventInit
    } = event;
    const previousElement = previousElementRef == null ? void 0 : previousElementRef.current; // If the active item element is not the previous element, this means that
    // it hasn't been focused (for example, when using composite hover). So we
    // focus on it before firing the keyboard event.

    if (activeElement !== previousElement) {
      activeElement.focus();
    }

    if (!fireKeyboardEvent(activeElement, event.type, eventInit)) {
      event.preventDefault();
    } // The event will be triggered on the composite item and then propagated up
    // to this composite element again, so we can pretend that it wasn't called
    // on this component in the first place.


    if (event.currentTarget.contains(activeElement)) {
      event.stopPropagation();
    }
  });
}

function findFirstEnabledItemInTheLastRow(items) {
  return findFirstEnabledItem(flatten2DArray(reverseArray(groupItemsByRows(items))));
}

function useScheduleFocus(activeItem) {
  const [scheduled, setScheduled] = useState(false);
  const schedule = useCallback(() => setScheduled(true), []);
  useEffect(() => {
    const activeElement = activeItem == null ? void 0 : activeItem.ref.current;

    if (scheduled && activeElement) {
      setScheduled(false);
      focusIntoView(activeElement);
    }
  }, [activeItem, scheduled]);
  return schedule;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a composite widget.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const state = useCompositeState();
 * const props = useComposite({ state });
 * <Role {...props}>
 *   <CompositeItem>Item 1</CompositeItem>
 *   <CompositeItem>Item 2</CompositeItem>
 * </Role>
 * ```
 */


const useComposite = createHook(_ref => {
  let {
    state,
    composite = true,
    focusOnMove = composite,
    moveOnKeyPress = true,
    ...props
  } = _ref;
  const ref = useRef(null);
  const virtualFocus = composite && state.virtualFocus;
  const activeItem = useMemo(() => findEnabledItemById(state.items, state.activeId), [state.items, state.activeId]);
  const activeItemRef = useLiveRef(activeItem);
  const previousElementRef = useRef(null);
  const isSelfActive = state.activeId === null;
  const isSelfAciveRef = useLiveRef(isSelfActive);
  const scheduleFocus = useScheduleFocus(activeItem); // Focus on the active item element.

  useSafeLayoutEffect(() => {
    var _activeItemRef$curren;

    if (!composite) return;
    if (!focusOnMove) return;
    if (!state.moves) return;
    const itemElement = (_activeItemRef$curren = activeItemRef.current) == null ? void 0 : _activeItemRef$curren.ref.current;
    if (!itemElement) return; // We're scheduling the focus on the next tick to avoid the `onFocus`
    // event on each item to be triggered before the state changes can
    // propagate to them.

    scheduleFocus();
  }, [composite, focusOnMove, state.moves]); // If composite.move(null) has been called, the composite container (this
  // element) should receive focus.

  useSafeLayoutEffect(() => {
    if (!composite) return;
    if (!state.moves) return;
    if (!isSelfAciveRef.current) return;
    const element = ref.current;
    if (!element) return;
    const previousElement = previousElementRef.current; // We have to clean up the previous element ref so an additional blur
    // event is not fired on it, for example, when looping through items while
    // includesBaseElement is true.

    previousElementRef.current = null;

    if (previousElement) {
      // We fire a blur event on the previous active item before moving focus
      // to the composite element so the events are dispatched in the right
      // order (blur, then focus).
      fireBlurEvent(previousElement, {
        relatedTarget: element
      });
    } // If the composite element is already focused, we still need to fire a
    // focus event on it so consumer props like onFocus are called.


    if (hasFocus(element)) {
      fireFocusEvent(element, {
        relatedTarget: previousElement
      });
    } else {
      element.focus();
    }
  }, [composite, state.moves]); // At this point, if the activeId has changed and we still have a
  // previousElement, this means that the previousElement hasn't been blurred,
  // so we do it here. This will be the scenario when moving through items, in
  // which case the onFocusCapture below event will stop propgation.

  useSafeLayoutEffect(() => {
    var _activeItemRef$curren2;

    if (!virtualFocus) return;
    if (!composite) return;
    const previousElement = previousElementRef.current;
    previousElementRef.current = null;
    if (!previousElement) return;
    const activeElement = (_activeItemRef$curren2 = activeItemRef.current) == null ? void 0 : _activeItemRef$curren2.ref.current;
    const relatedTarget = activeElement || getActiveElement(previousElement);
    fireBlurEvent(previousElement, {
      relatedTarget
    });
  }, [virtualFocus, composite, state.activeId]);
  const onKeyDownCapture = useKeyboardEventProxy(state, activeItem, props.onKeyDownCapture, previousElementRef);
  const onKeyUpCapture = useKeyboardEventProxy(state, activeItem, props.onKeyUpCapture, previousElementRef);
  const onFocusCaptureProp = props.onFocusCapture;
  const onFocusCapture = useEvent$1(event => {
    onFocusCaptureProp == null ? void 0 : onFocusCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!virtualFocus) return;
    const previousActiveElement = event.relatedTarget;
    const isSilentlyFocused = silentlyFocused(event.currentTarget);

    if (isSelfTarget(event) && isSilentlyFocused) {
      // Composite has been focused as a result of an item receiving focus.
      // The composite item will move focus back to the composite container.
      // In this case, we don't want to propagate this additional event nor
      // call the onFocus handler passed to <Composite onFocus={...} />.
      event.stopPropagation(); // We keep track of the previous active item element so we can manually
      // fire a blur event on it later when the focus is moved to another item
      // on the onBlurCapture event below.

      previousElementRef.current = previousActiveElement;
    }
  });
  const onFocusProp = props.onFocus;
  const onFocus = useEvent$1(event => {
    onFocusProp == null ? void 0 : onFocusProp(event);
    if (event.defaultPrevented) return;
    if (!composite) return;

    if (virtualFocus) {
      // This means that the composite element has been focused while the
      // composite item has not. For example, by clicking on the composite
      // element without touching any item, or by tabbing into the composite
      // element. In this case, we want to trigger focus on the item, just
      // like it would happen with roving tabindex. When it receives focus,
      // the composite item will move focus back to the composite element.
      if (isSelfTarget(event) && !isItem(state.items, event.relatedTarget)) {
        // By queueing a microtask, we ensure the scheduleFocus effect will be
        // triggered after all the other effects that might have changed the
        // active item. This also accounts for when the composite container is
        // focused right after it gets mounted (for example, in a dialog), in
        // which case state.items will not be populated yet.
        queueMicrotask$1(scheduleFocus);
      }
    } else if (isSelfTarget(event)) {
      // When the roving tabindex composite gets intentionally focused (for
      // example, by clicking directly on it, and not on an item), we make
      // sure to set the activeId to null (which means the composite element
      // itself has focus).
      state.setActiveId(null);
    }
  });
  const onBlurCaptureProp = props.onBlurCapture;
  const onBlurCapture = useEvent$1(event => {
    onBlurCaptureProp == null ? void 0 : onBlurCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!virtualFocus) return; // When virtualFocus is set to true, we move focus from the composite
    // container (this element) to the composite item that is being selected.
    // Then we move focus back to the composite container. This is so we can
    // provide the same API as the roving tabindex method, which means people
    // can attach onFocus/onBlur handlers on the CompositeItem component
    // regardless of whether virtualFocus is set to true or false. This
    // sequence of blurring and focusing on items and on the composite element
    // may be confusing, so we ignore intermediate focus and blur events by
    // stopping their propagation.

    const activeElement = (activeItem == null ? void 0 : activeItem.ref.current) || null;
    const nextActiveElement = event.relatedTarget;
    const nextActiveElementIsItem = isItem(state.items, nextActiveElement);
    const previousElement = previousElementRef.current;
    previousElementRef.current = null; // This is an intermediate blur event: blurring the composite container
    // to focus on an item (nextActiveElement).

    if (isSelfTarget(event) && nextActiveElementIsItem) {
      // The next active element will be the same as the active item in the
      // state in these two scenarios:
      //   - Moving focus with keyboard: the state is updated before the blur
      //     event is triggered, so here the active item is already pointing
      //     to the next active element.
      //   - Clicking on the active item with a pointer: this will trigger
      //     blur on the composite element and then the next active element
      //     will be the same as the active item. Clicking on an item other
      //     than the active one doesn't end up here as the activeItem state
      //     will be updated only after that.
      if (nextActiveElement === activeElement) {
        // If there's a previous active item and it's not a click action, then
        // we fire a blur event on it so it will work just like if it had DOM
        // focus before (like when using roving tabindex).
        if (previousElement && previousElement !== nextActiveElement) {
          fireBlurEvent(previousElement, event);
        }
      } // This will be true when the next active element is not the active
      // element, but there's an active item. This will only happen when
      // clicking with a pointer on a different item, when there's already an
      // item selected, in which case activeElement is the item that is
      // getting blurred, and nextActiveElement is the item that is being
      // clicked.
      else if (activeElement) {
        fireBlurEvent(activeElement, event);
      } // We want to ignore intermediate blur events, so we stop the
      // propagation of this event.


      event.stopPropagation();
    } else {
      const targetIsItem = isItem(state.items, event.target); // If target is not a composite item, it may be the composite element
      // itself (isSelfTarget) or a tabbable element inside the composite
      // element. This may be triggered by clicking outside of the composite
      // element or by tabbing out of it. In either cases, we want to fire a
      // blur event on the active item.

      if (!targetIsItem && activeElement) {
        fireBlurEvent(activeElement, event);
      }
    }
  });
  const onKeyDownProp = props.onKeyDown;
  const moveOnKeyPressProp = useBooleanEvent(moveOnKeyPress);
  const onKeyDown = useEvent$1(event => {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;
    if (!isSelfTarget(event)) return;
    if (activeItemRef.current) return;
    const isVertical = state.orientation !== "horizontal";
    const isHorizontal = state.orientation !== "vertical";
    const grid = isGrid(state.items);

    const up = () => {
      if (grid) {
        const item = state.items && findFirstEnabledItemInTheLastRow(state.items);
        return item == null ? void 0 : item.id;
      }

      return state.last();
    };

    const keyMap = {
      ArrowUp: (grid || isVertical) && up,
      ArrowRight: (grid || isHorizontal) && state.first,
      ArrowDown: (grid || isVertical) && state.first,
      ArrowLeft: (grid || isHorizontal) && state.last,
      Home: state.first,
      End: state.last,
      PageUp: state.first,
      PageDown: state.last
    };
    const action = keyMap[event.key];

    if (action) {
      const id = action();

      if (id !== undefined) {
        if (!moveOnKeyPressProp(event)) return;
        event.preventDefault();
        state.move(id);
      }
    }
  });
  props = useStoreProvider({
    state,
    ...props
  }, CompositeContext);
  const activeId = (activeItem == null ? void 0 : activeItem.id) || undefined;
  props = {
    "aria-activedescendant": virtualFocus ? activeId : undefined,
    ...props,
    ref: useForkRef(ref, composite ? state.baseRef : undefined, props.ref),
    onKeyDownCapture,
    onKeyUpCapture,
    onFocusCapture,
    onFocus,
    onBlurCapture,
    onKeyDown
  };
  const focusable = composite && (virtualFocus || state.activeId === null);
  props = useFocusable({
    focusable,
    ...props
  });
  return props;
});
/**
 * A component that renders a composite widget.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const composite = useCompositeState();
 * <Composite state={composite}>
 *   <CompositeItem>Item 1</CompositeItem>
 *   <CompositeItem>Item 2</CompositeItem>
 * </Composite>
 * ```
 */

const Composite = createComponent(props => {
  const htmlProps = useComposite(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Composite.displayName = "Composite";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element that will serve as the popover's
 * anchor. The popover will be positioned relative to this element.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const state = usePopoverState();
 * const props = usePopoverAnchor({ state });
 * <Role {...props}>Anchor</Role>
 * <Popover state={state}>Popover</Popover>
 * ```
 */
const usePopoverAnchor = createHook(_ref => {
  let {
    state,
    ...props
  } = _ref;
  props = { ...props,
    ref: useForkRef(state.anchorRef, props.ref)
  };
  return props;
});
/**
 * A component that renders an element that will serve as the popover's anchor.
 * The popover will be positioned relative to this element.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const popover = usePopoverState();
 * <PopoverAnchor state={popover}>Anchor</PopoverAnchor>
 * <Popover state={popover}>Popover</Popover>
 * ```
 */

const PopoverAnchor = createComponent(props => {
  const htmlProps = usePopoverAnchor(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  PopoverAnchor.displayName = "PopoverAnchor";
}

const usePopoverDisclosure = createHook(_ref => {
  let {
    state,
    ...props
  } = _ref;
  const onClickProp = props.onClick;
  const onClick = useEvent$1(event => {
    state.anchorRef.current = event.currentTarget;
    onClickProp == null ? void 0 : onClickProp(event);
  });
  props = useWrapElement(props, element => /*#__PURE__*/jsx(PopoverContext.Provider, {
    value: state,
    children: element
  }), [state]);
  props = { ...props,
    onClick
  };
  props = usePopoverAnchor({
    state,
    ...props
  });
  props = useDialogDisclosure({
    state,
    ...props
  });
  return props;
});
/**
 * A component that renders a button that controls the visibility of the popover
 * when clicked.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const popover = usePopoverState();
 * <PopoverDisclosure state={popover}>Disclosure</PopoverDisclosure>
 * <Popover state={popover}>Popover</Popover>
 * ```
 */

const PopoverDisclosure = createComponent(props => {
  const htmlProps = usePopoverDisclosure(props);
  return createElement("button", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  PopoverDisclosure.displayName = "PopoverDisclosure";
}

let chars$1 = "";

function clearChars() {
  chars$1 = "";
}

function isValidTypeaheadEvent(event) {
  const target = event.target;
  if (target && isTextField(target)) return false; // If the spacebar is pressed, we'll only consider it a valid typeahead event
  // if there were already other characters typed.

  if (event.key === " " && chars$1.length) return true;
  return event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey && // Matches any letter or number of any language.
  /^[\p{Letter}\p{Number}]$/u.test(event.key);
}

function isSelfTargetOrItem(event, items) {
  if (isSelfTarget(event)) return true;
  const target = event.target;
  if (!target) return false;
  const isItem = items.some(item => item.ref.current === target);
  return isItem;
}

function getEnabledItems(items) {
  return items.filter(item => !item.disabled);
}

function itemTextStartsWith(item, text) {
  var _item$ref$current;

  const itemText = (_item$ref$current = item.ref.current) == null ? void 0 : _item$ref$current.textContent;
  if (!itemText) return false;
  return normalizeString(itemText).toLowerCase().startsWith(text.toLowerCase());
}

function getSameInitialItems(items, char, activeId) {
  if (!activeId) return items;
  const activeItem = items.find(item => item.id === activeId);
  if (!activeItem) return items;
  if (!itemTextStartsWith(activeItem, char)) return items; // Typing "oo" will match "oof" instead of moving to the next item.

  if (chars$1 !== char && itemTextStartsWith(activeItem, chars$1)) return items; // If we're looping through the items, we'll want to reset the chars so "oo"
  // becomes just "o".

  chars$1 = char; // flipItems will put the previous items at the end of the list so we can loop
  // through them.

  return flipItems(items.filter(item => itemTextStartsWith(item, chars$1)), activeId).filter(item => item.id !== activeId);
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to add typeahead functionality to composite components.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const state = useCompositeState();
 * const props = useCompositeTypeahead({ state });
 * <Composite state={state} {...props}>
 *   <CompositeItem>Item 1</CompositeItem>
 *   <CompositeItem>Item 2</CompositeItem>
 * </Composite>
 * ```
 */


const useCompositeTypeahead = createHook(_ref => {
  let {
    state,
    typeahead = true,
    ...props
  } = _ref;
  state = useStore(state || CompositeContext, ["items", "activeId", "move"]);
  const onKeyDownCaptureProp = props.onKeyDownCapture;
  const cleanupTimeoutRef = useRef(0); // We have to listen to the event in the capture phase because the event
  // might be handled by a child component. For example, the space key may
  // trigger a click event on a child component. We need to prevent this
  // behavior if the character is a valid typeahead key.

  const onKeyDownCapture = useEvent$1(event => {
    var _state, _state2;

    onKeyDownCaptureProp == null ? void 0 : onKeyDownCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!typeahead) return;
    if (!((_state = state) != null && _state.items)) return;
    if (!isValidTypeaheadEvent(event)) return clearChars();
    let items = getEnabledItems(state.items);
    if (!isSelfTargetOrItem(event, items)) return clearChars();
    event.preventDefault(); // We need to clear the previous cleanup timeout so we can append the
    // pressed char to the existing one.

    window.clearTimeout(cleanupTimeoutRef.current); // Schedule a new cleanup timeout. After a short delay we'll reset the
    // characters so the next one counts as a new start character.

    cleanupTimeoutRef.current = window.setTimeout(() => {
      chars$1 = "";
    }, 500); // Always consider the lowercase version of the key.

    const char = event.key.toLowerCase();
    chars$1 += char;
    items = getSameInitialItems(items, char, (_state2 = state) == null ? void 0 : _state2.activeId);
    const item = items.find(item => itemTextStartsWith(item, chars$1));

    if (item) {
      state.move(item.id);
    } else {
      // Immediately clear the characters so the next keypress starts a new
      // search.
      clearChars();
    }
  });
  props = { ...props,
    onKeyDownCapture
  };
  return props;
});
/**
 * A component that adds typeahead functionality to composite components.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const composite = useCompositeState();
 * <Composite state={composite} as={CompositeTypeahead}>
 *   <CompositeItem>Item 1</CompositeItem>
 *   <CompositeItem>Item 2</CompositeItem>
 * </Composite>
 * ```
 */

const CompositeTypeahead = createComponent(props => {
  const htmlProps = useCompositeTypeahead(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  CompositeTypeahead.displayName = "CompositeTypeahead";
}

const FormContext = createStoreContext();

function getNamedElement(ref, name) {
  const element = ref.current;
  if (!element) return null;

  if (element.name !== name) {
    if (element.form) {
      return element.form.elements.namedItem(name);
    } else {
      const document = getDocument(element);
      return document.getElementsByName(name)[0];
    }
  }

  return element;
}

function findItem(items, name, type) {
  return items == null ? void 0 : items.find(item => item.type === type && item.name === name);
}

function useItem(state, name, type) {
  return useMemo(() => findItem(state == null ? void 0 : state.items, name, type), [state == null ? void 0 : state.items, name, type]);
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a form field. Unlike `useFormInput`, this hook
 * doesn't automatically returns the `value` and `onChange` props. This is so we
 * can use it not only for native form elements but also for custom components
 * whose value is not controlled by the native `value` and `onChange` props.
 * @see https://ariakit.org/components/form
 * @example
 * ```jsx
 * const state = useFormState({ defaultValues: { content: "" } });
 * const props = useFormField({ state, name: state.names.content });
 *
 * const setValue = useCallback((value) => {
 *   state.setValue(state.names.content, value);
 * }, [state.setValue, state.names.content]);
 *
 * <Form state={state}>
 *   <FormLabel name={state.names.content}>Content</FormLabel>
 *   <Role
 *     {...props}
 *     as={Editor}
 *     value={state.values.content}
 *     onChange={setValue}
 *   />
 * </Form>
 * ```
 */


const useFormField = createHook(_ref => {
  var _state, _state4;

  let {
    state,
    name: nameProp,
    getItem: getItemProp,
    touchOnBlur = true,
    ...props
  } = _ref;
  const name = "" + nameProp;
  state = useStore(state || FormContext, ["setFieldTouched", "useValidate", "setError", useCallback(s => s.getError(name), [name]), useCallback(s => s.getFieldTouched(name).toString(), [name]), useCallback(s => findItem(s.items, name, "label"), [name]), useCallback(s => findItem(s.items, name, "error"), [name]), useCallback(s => findItem(s.items, name, "description"), [name])]);
  const ref = useRef(null);
  const id = useId(props.id);
  (_state = state) == null ? void 0 : _state.useValidate(async () => {
    const element = getNamedElement(ref, name);
    if (!element) return; // Flush microtasks to make sure the validity state is up to date

    await Promise.resolve();

    if ("validity" in element && !element.validity.valid) {
      var _state2;

      (_state2 = state) == null ? void 0 : _state2.setError(name, element.validationMessage);
    }
  });
  const getItem = useCallback(item => {
    const nextItem = { ...item,
      id,
      name,
      type: "field"
    };

    if (getItemProp) {
      return getItemProp(nextItem);
    }

    return nextItem;
  }, [id, name, getItemProp]);
  const onBlurProp = props.onBlur;
  const touchOnBlurProp = useBooleanEvent(touchOnBlur);
  const onBlur = useEvent$1(event => {
    var _state3;

    onBlurProp == null ? void 0 : onBlurProp(event);
    if (event.defaultPrevented) return;
    if (!touchOnBlurProp(event)) return;
    (_state3 = state) == null ? void 0 : _state3.setFieldTouched(name, true);
  });
  const label = useItem(state, name, "label");
  const error = useItem(state, name, "error");
  const description = useItem(state, name, "description");
  const describedBy = cx(error == null ? void 0 : error.id, description == null ? void 0 : description.id, props["aria-describedby"]);
  const invalid = !!((_state4 = state) != null && _state4.getError(name)) && state.getFieldTouched(name);
  props = {
    id,
    "aria-labelledby": label == null ? void 0 : label.id,
    "aria-invalid": invalid,
    ...props,
    "aria-describedby": describedBy || undefined,
    ref: useForkRef(ref, props.ref),
    onBlur
  };
  props = useCollectionItem({
    state,
    ...props,
    name,
    getItem
  });
  return props;
});
/**
 * A component that renders a form field. Unlike `FormInput`, this component
 * doesn't automatically pass the `value` and `onChange` props down to the
 * underlying element. This is so we can use it not only for native form
 * elements but also for custom components whose value is not controlled by the
 * native `value` and `onChange` props.
 * @see https://ariakit.org/components/form
 * @example
 * ```jsx
 * const form = useFormState({ defaultValues: { content: "" } });
 *
 * const setValue = useCallback((value) => {
 *   form.setValue(form.names.content, value);
 * }, [form.setValue, form.names.content]);
 *
 * <Form state={form}>
 *   <FormLabel name={form.names.content}>Content</FormLabel>
 *   <FormField
 *     {...props}
 *     as={Editor}
 *     value={form.values.content}
 *     onChange={setValue}
 *   />
 * </Form>
 * ```
 */

const FormField = createMemoComponent(props => {
  const htmlProps = useFormField(props);
  return createElement("input", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  FormField.displayName = "FormField";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a form input. Unlike `useFormField`, this hook
 * returns the `value` and `onChange` props that can be passed to a native
 * input, select or textarea elements.
 * @see https://ariakit.org/components/form
 * @example
 * ```jsx
 * const state = useFormState({ defaultValues: { email: "" } });
 * const props = useFormInput({ state, name: state.names.email });
 * <Form state={state}>
 *   <FormLabel name={state.names.email}>Email</FormLabel>
 *   <Role as="input" {...props} />
 * </Form>
 * ```
 */
const useFormInput = createHook(_ref => {
  var _state2;

  let {
    state,
    name: nameProp,
    ...props
  } = _ref;
  const name = "" + nameProp;
  state = useStore(state || FormContext, ["setValue", useCallback(s => s.getValue(name), [name])]);
  const onChangeProp = props.onChange;
  const onChange = useEvent$1(event => {
    var _state;

    onChangeProp == null ? void 0 : onChangeProp(event);
    if (event.defaultPrevented) return;
    (_state = state) == null ? void 0 : _state.setValue(name, event.target.value);
  });
  const value = (_state2 = state) == null ? void 0 : _state2.getValue(name);
  props = {
    value,
    ...props,
    onChange
  };
  props = useFocusable(props);
  props = useFormField({
    state,
    name,
    ...props
  });
  return props;
});
/**
 * A component that renders a form input. Unlike `FormField`, this component
 * passes the `value` and `onChange` props down to the underlying element that
 * can be a native input, select or textarea elements.
 * @see https://ariakit.org/components/form
 * @example
 * ```jsx
 * const form = useFormState({ defaultValues: { email: "" } });
 * <Form state={form}>
 *   <FormLabel name={form.names.email}>Email</FormLabel>
 *   <FormInput name={form.names.email} />
 * </Form>
 * ```
 */

const FormInput = createMemoComponent(props => {
  const htmlProps = useFormInput(props);
  return createElement("input", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  FormInput.displayName = "FormInput";
}

const RadioContextState = createStoreContext();

function getIsChecked(value, stateValue) {
  if (stateValue === undefined) return;

  if (value != null && stateValue != null) {
    return stateValue === value;
  }

  return !!stateValue;
}

function isNativeRadio(tagName, type) {
  return tagName === "input" && (!type || type === "radio");
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a radio button element.
 * @see https://ariakit.org/components/radio
 * @example
 * ```jsx
 * const state = useRadioState();
 * const props = useRadio({ state, value: "Apple" });
 * <RadioGroup state={state}>
 *   <Role as="input" {...props} />
 *   <Radio value="Orange" />
 * </RadioGroup>
 * ```
 */


const useRadio = createHook(_ref => {
  var _state, _state2, _state4;

  let {
    state,
    value,
    checked,
    ...props
  } = _ref;
  const id = useId(props.id);
  state = useStore(state || RadioContextState, [useCallback(s => s.activeId === id, [id]), useCallback(s => s.value === value, [value]), "setActiveId", "setValue"]);
  const ref = useRef(null);
  const isActiveItemRef = useLiveRef(((_state = state) == null ? void 0 : _state.activeId) === id);
  const isChecked = checked != null ? checked : getIsChecked(value, (_state2 = state) == null ? void 0 : _state2.value); // When the radio state has a default value, we need to update the active id
  // to point to the checked element, otherwise it'll be the first item in the
  // list.

  useEffect(() => {
    if (!isActiveItemRef.current && isChecked) {
      var _state3;

      (_state3 = state) == null ? void 0 : _state3.setActiveId(id);
    }
  }, [isChecked, (_state4 = state) == null ? void 0 : _state4.setActiveId, id]);
  const onChangeProp = props.onChange;
  const tagName = useTagName(ref, props.as || "input");
  const nativeRadio = isNativeRadio(tagName, props.type);
  const onChange = useEvent$1(event => {
    var _state5;

    if (props.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!nativeRadio) {
      event.currentTarget.checked = true;
    }

    onChangeProp == null ? void 0 : onChangeProp(event);
    if (event.defaultPrevented) return;
    (_state5 = state) == null ? void 0 : _state5.setValue(value);
  });
  const onClickProp = props.onClick;
  const onClick = useEvent$1(event => {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (nativeRadio) return;
    onChange(event);
  });
  const onFocusProp = props.onFocus;
  const onFocus = useEvent$1(event => {
    var _state6;

    onFocusProp == null ? void 0 : onFocusProp(event);
    if (event.defaultPrevented) return;
    if (!nativeRadio) return;
    if (!((_state6 = state) != null && _state6.moves)) return;
    if (!isActiveItemRef.current) return;
    onChange(event);
  });
  props = {
    id,
    role: !nativeRadio ? "radio" : undefined,
    type: nativeRadio ? "radio" : undefined,
    "aria-checked": isChecked,
    ...props,
    ref: useForkRef(ref, props.ref),
    onChange,
    onClick,
    onFocus
  };
  props = useCompositeItem({
    state,
    clickOnEnter: !nativeRadio,
    ...props
  });
  return {
    value: nativeRadio ? value : undefined,
    checked: isChecked,
    ...props
  };
});
/**
 * A component that renders a radio button element.
 * @see https://ariakit.org/components/radio
 * @example
 * ```jsx
 * const radio = useRadioState();
 * <RadioGroup state={radio}>
 *   <Radio value="Apple" />
 *   <Radio value="Orange" />
 * </RadioGroup>
 * ```
 */

const Radio$1 = createMemoComponent(props => {
  const htmlProps = useRadio(props);
  return createElement("input", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Radio$1.displayName = "Radio";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an anchor element that will open a popover
 * (`Hovercard`) on hover.
 * @see https://ariakit.org/components/hovercard
 * @example
 * ```jsx
 * const state = useHovercardState();
 * const props = useHovercardAnchor({ state });
 * <Role as="a" {...props}>@username</Role>
 * <Hovercard state={state}>Details</Hovercard>
 * ```
 */
const useHovercardAnchor = createHook(_ref => {
  let {
    state,
    showOnHover = true,
    ...props
  } = _ref;
  const disabled = props.disabled || props["aria-disabled"] === true || props["aria-disabled"] === "true";
  const showTimeoutRef = useRef(0); // Clear the show timeout if the anchor is unmounted

  useEffect(() => () => window.clearTimeout(showTimeoutRef.current), []); // Clear the show timeout if the mouse leaves the anchor element. We're
  // using the native mouseleave event instead of React's onMouseLeave so we
  // bypass the event.stopPropagation() logic set on the Hovercard component
  // for when the mouse is moving toward the Hovercard.

  useEffect(() => {
    const onMouseLeave = event => {
      const element = state.anchorRef.current;
      if (!element) return;
      if (event.target !== element) return;
      window.clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = 0;
    };

    return addGlobalEventListener("mouseleave", onMouseLeave, true);
  }, [state.anchorRef]);
  const onMouseMoveProp = props.onMouseMove;
  const showOnHoverProp = useBooleanEvent(showOnHover);
  const isMouseMoving = useIsMouseMoving();
  const onMouseMove = useEvent$1(event => {
    state.anchorRef.current = event.currentTarget;
    onMouseMoveProp == null ? void 0 : onMouseMoveProp(event);
    if (disabled) return;
    if (event.defaultPrevented) return;
    if (showTimeoutRef.current) return;
    if (!isMouseMoving()) return;
    if (!showOnHoverProp(event)) return;
    showTimeoutRef.current = window.setTimeout(() => {
      showTimeoutRef.current = 0;
      state.show();
    }, state.showTimeout);
  });
  props = { ...props,
    ref: useForkRef(state.anchorRef, props.ref),
    onMouseMove
  };
  props = useFocusable(props);
  return props;
});
/**
 * A component that renders an anchor element that will open a popover
 * (`Hovercard`) on hover.
 * @see https://ariakit.org/components/hovercard
 * @example
 * ```jsx
 * const hovercard = useHovercardState();
 * <HovercardAnchor state={hovercard}>@username</HovercardAnchor>
 * <Hovercard state={hovercard}>Details</Hovercard>
 * ```
 */

const HovercardAnchor = createComponent(props => {
  const htmlProps = useHovercardAnchor(props);
  return createElement("a", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  HovercardAnchor.displayName = "HovercardAnchor";
}

/*
 * Copyright 2017 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Modifications Copyright 2019 - present by Diego Haz.
 *
 * Extracted the SVG path only.
 */
const POPOVER_ARROW_PATH = "M23,27.8c1.1,1.2,3.4,2.2,5,2.2h2H0h2c1.7,0,3.9-1,5-2.2l6.6-7.2c0.7-0.8,2-0.8,2.7,0L23,27.8L23,27.8z";

const defaultSize = 30;
const halfDefaultSize = defaultSize / 2;
const rotateMap = {
  top: "rotate(180 " + halfDefaultSize + " " + halfDefaultSize + ")",
  right: "rotate(-90 " + halfDefaultSize + " " + halfDefaultSize + ")",
  bottom: "rotate(0 " + halfDefaultSize + " " + halfDefaultSize + ")",
  left: "rotate(90 " + halfDefaultSize + " " + halfDefaultSize + ")"
};

function useComputedStyle(element) {
  const [style, setStyle] = useState();
  useSafeLayoutEffect(() => {
    if (!element) return;
    const computedStyle = getWindow$2(element).getComputedStyle(element);
    setStyle(computedStyle);
  }, [element]);
  return style;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an arrow inside a popover element.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const state = usePopoverState();
 * const props = usePopoverArrow({ state });
 * <Popover state={state}>
 *   <Role {...props} />
 *   Popover
 * </Popover>
 * ```
 */


const usePopoverArrow = createHook(_ref => {
  var _state, _state2, _state3;

  let {
    state,
    size = defaultSize,
    ...props
  } = _ref;
  const context = useContext(PopoverContext);
  state = state || context;
  const dir = (_state = state) == null ? void 0 : _state.currentPlacement.split("-")[0];
  const style = useComputedStyle((_state2 = state) == null ? void 0 : _state2.contentElement);
  const fill = (style == null ? void 0 : style.getPropertyValue("background-color")) || "none";
  const stroke = (style == null ? void 0 : style.getPropertyValue("border-" + dir + "-color")) || "none";
  const borderWidth = (style == null ? void 0 : style.getPropertyValue("border-" + dir + "-width")) || "0px";
  const strokeWidth = parseInt(borderWidth) * 2 * (defaultSize / size);
  const transform = rotateMap[dir];
  const children = useMemo(() => /*#__PURE__*/jsx("svg", {
    display: "block",
    viewBox: "0 0 30 30",
    children: /*#__PURE__*/jsxs("g", {
      transform: transform,
      children: [/*#__PURE__*/jsx("path", {
        fill: "none",
        d: POPOVER_ARROW_PATH
      }), /*#__PURE__*/jsx("path", {
        stroke: "none",
        d: POPOVER_ARROW_PATH
      })]
    })
  }), [transform]);
  props = {
    children,
    "aria-hidden": true,
    ...props,
    ref: useForkRef((_state3 = state) == null ? void 0 : _state3.arrowRef, props.ref),
    style: {
      // server side rendering
      position: "absolute",
      fontSize: size,
      width: "1em",
      height: "1em",
      pointerEvents: "none",
      fill,
      stroke,
      strokeWidth,
      ...props.style
    }
  };
  return props;
});
/**
 * A component that renders an arrow inside a `Popover` component.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const popover = usePopoverState();
 * <Popover state={popover}>
 *   <PopoverArrow />
 *   Popover
 * </Popover>
 * ```
 */

const PopoverArrow = createComponent(props => {
  const htmlProps = usePopoverArrow(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  PopoverArrow.displayName = "PopoverArrow";
}

/**
 * Provides state for the `Hovercard` components.
 * @example
 * ```jsx
 * const hovercard = useHovercardState({ placement: "top" });
 * <HovercardAnchor state={hovercard}>@username</HovercardAnchor>
 * <Hovercard state={hovercard}>Details</Hovercard>
 * ```
 */

function useHovercardState(_temp) {
  let {
    timeout = 500,
    placement = "bottom",
    showTimeout = timeout,
    hideTimeout = timeout,
    ...props
  } = _temp === void 0 ? {} : _temp;
  const [autoFocusOnShow, setAutoFocusOnShow] = useState(false);
  const popover = usePopoverState({
    placement,
    ...props
  });
  const state = useMemo(() => ({ ...popover,
    timeout,
    showTimeout,
    hideTimeout,
    autoFocusOnShow,
    setAutoFocusOnShow
  }), [popover, timeout, showTimeout, hideTimeout, autoFocusOnShow, setAutoFocusOnShow]);
  return state;
}

function getEventPoint(event) {
  return [event.clientX, event.clientY];
} // Based on https://github.com/metafloor/pointinpoly

function isPointInPolygon(point, polygon) {
  const [x, y] = point;
  let inside = false;
  const length = polygon.length;

  for (let l = length, i = 0, j = l - 1; i < l; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const [, vy] = polygon[j === 0 ? l - 1 : j - 1] || [0, 0];
    const where = (yi - yj) * (x - xi) - (xi - xj) * (y - yi);

    if (yj < yi) {
      if (y >= yj && y < yi) {
        // point on the line
        if (where === 0) return true;

        if (where > 0) {
          if (y === yj) {
            // ray intersects vertex
            if (y > vy) {
              inside = !inside;
            }
          } else {
            inside = !inside;
          }
        }
      }
    } else if (yi < yj) {
      if (y > yi && y <= yj) {
        // point on the line
        if (where === 0) return true;

        if (where < 0) {
          if (y === yj) {
            // ray intersects vertex
            if (y < vy) {
              inside = !inside;
            }
          } else {
            inside = !inside;
          }
        }
      }
    } else if (y == yi && (x >= xj && x <= xi || x >= xi && x <= xj)) {
      // point on horizontal edge
      return true;
    }
  }

  return inside;
}

function getEnterPointPlacement(enterPoint, rect) {
  const {
    top,
    right,
    bottom,
    left
  } = rect;
  const [x, y] = enterPoint;
  const placementX = x < left ? "left" : x > right ? "right" : null;
  const placementY = y < top ? "top" : y > bottom ? "bottom" : null;
  return [placementX, placementY];
}

function getElementPolygon(element, enterPoint) {
  const rect = element.getBoundingClientRect();
  const {
    top,
    right,
    bottom,
    left
  } = rect;
  const [x, y] = getEnterPointPlacement(enterPoint, rect);
  const polygon = [enterPoint];

  if (x) {
    if (y !== "top") {
      polygon.push([x === "left" ? left : right, top]);
    }

    polygon.push([x === "left" ? right : left, top]);
    polygon.push([x === "left" ? right : left, bottom]);

    if (y !== "bottom") {
      polygon.push([x === "left" ? left : right, bottom]);
    }
  } else if (y === "top") {
    polygon.push([left, top]);
    polygon.push([left, bottom]);
    polygon.push([right, bottom]);
    polygon.push([right, top]);
  } else {
    polygon.push([left, bottom]);
    polygon.push([left, top]);
    polygon.push([right, top]);
    polygon.push([right, bottom]);
  }

  return polygon;
}

function isMovingOnHovercard(target, card, anchor, nested) {
  // The hovercard element has focus so we should keep it visible.
  if (hasFocusWithin(card)) return true; // The mouse is moving on an element inside the hovercard.

  if (!target) return false;
  if (contains(card, target)) return true; // The mouse is moving on an element inside the anchor element.

  if (anchor && contains(anchor, target)) return true; // The mouse is moving on an element inside a nested hovercard.

  if (nested != null && nested.some(card => isMovingOnHovercard(target, card, anchor))) {
    return true;
  }

  return false;
} // The autoFocusOnShow state will be set to true when the hovercard disclosure
// element is clicked. We have to reset it to false when the hovercard element
// gets hidden or is unmounted.


function useAutoFocusOnShow(_ref) {
  let {
    state,
    ...props
  } = _ref;
  const mountedRef = useLiveRef(state.mounted); // Resets autoFocusOnShow

  useEffect(() => {
    if (!state.mounted) {
      state.setAutoFocusOnShow(false);
    }
  }, [state.mounted, state.setAutoFocusOnShow]); // On unmount as well.

  useEffect(() => () => {
    if (!mountedRef.current) {
      state.setAutoFocusOnShow(false);
    }
  }, [state.setAutoFocusOnShow]);
  return {
    // If the hovercard is modal, we should always autoFocus on show.
    autoFocusOnShow: !!props.modal || state.autoFocusOnShow,
    ...props
  };
} // When the hovercard element has focus, we should move focus back to the anchor
// element when the hovercard gets hidden (or unmounted).


function useAutoFocusOnHide(_ref2) {
  let {
    state,
    ...props
  } = _ref2;
  const [autoFocusOnHide, setAutoFocusOnHide] = useState(false); // Resets autoFocusOnHide

  useEffect(() => {
    if (!state.mounted) {
      setAutoFocusOnHide(false);
    }
  }, [state.mounted]);
  const onFocusProp = useEvent$1(props.onFocus);
  const onFocus = useCallback(event => {
    onFocusProp(event);
    if (event.defaultPrevented) return;
    setAutoFocusOnHide(true);
  }, [onFocusProp]);
  props = {
    autoFocusOnHide,
    finalFocusRef: state.anchorRef,
    ...props,
    onFocus
  };
  return props;
}

const NestedHovercardContext = /*#__PURE__*/createContext(null);
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a hovercard element, which is a popover that's
 * usually made visible by hovering the mouse cursor over an anchor element
 * (`HovercardAnchor`).
 * @see https://ariakit.org/components/hovercard
 * @example
 * ```jsx
 * const state = useHovercardState();
 * const props = useHovercard({ state });
 * <HovercardAnchor state={state}>@username</HovercardAnchor>
 * <Role {...props}>Details</Role>
 * ```
 */

const useHovercard = createHook(_ref3 => {
  let {
    state,
    modal = false,
    portal = !!modal,
    hideOnEscape = true,
    hideOnControl = false,
    hideOnHoverOutside = true,
    disablePointerEventsOnApproach = !!hideOnHoverOutside,
    ...props
  } = _ref3;
  const ref = useRef(null);
  const [nestedHovercards, setNestedHovercards] = useState([]);
  const hideTimeoutRef = useRef(0);
  const enterPointRef = useRef(null);
  const {
    portalRef,
    domReady
  } = usePortalRef(portal, props.portalRef);
  const hideOnEscapeProp = useBooleanEvent(hideOnEscape);
  const hideOnControlProp = useBooleanEvent(hideOnControl); // Hide on Escape/Control. Popover already handles this, but only when the
  // dialog, the backdrop or the disclosure elements are focused. Since the
  // hovercard, by default, does not receive focus when it's shown, we need to
  // handle this globally here.

  useEffect(() => {
    if (!state.open) return;
    return addGlobalEventListener("keydown", event => {
      if (event.defaultPrevented) return;
      const isEscape = event.key === "Escape" && hideOnEscapeProp(event);
      const isControl = event.key === "Control" && hideOnControlProp(event);

      if (isEscape || isControl) {
        state.hide();
      }
    });
  }, [state.open, hideOnEscapeProp, hideOnControlProp, state.hide]);
  const mayHideOnHoverOutside = !!hideOnHoverOutside;
  const hideOnHoverOutsideProp = useBooleanEvent(hideOnHoverOutside);
  const mayDisablePointerEvents = !!disablePointerEventsOnApproach;
  const disablePointerEventsProp = useBooleanEvent(disablePointerEventsOnApproach); // Checks whether the mouse is moving toward the hovercard. If not, hide the
  // card after a short delay (hideTimeout).

  useEffect(() => {
    if (!domReady) return;
    if (!state.mounted) return;
    if (!mayHideOnHoverOutside && !mayDisablePointerEvents) return;
    const element = ref.current;
    if (!element) return;

    const onMouseMove = event => {
      const enterPoint = enterPointRef.current;
      const target = event.target;
      const anchor = state.anchorRef.current; // Checks whether the hovercard element has focus or the mouse is moving
      // through valid hovercard elements.

      if (isMovingOnHovercard(target, element, anchor, nestedHovercards)) {
        // While the mouse is moving over the anchor element while the hover
        // card is open, keep track of the mouse position so we'll use the
        // last point before the mouse leaves the anchor element.
        enterPointRef.current = target && anchor && contains(anchor, target) ? getEventPoint(event) : null;
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = 0;
        return;
      } // If there's already a scheduled timeout to hide the hovercard, we do
      // nothing.


      if (hideTimeoutRef.current) return; // Enter point will be null when the user hovers over the hovercard
      // element.

      if (enterPoint) {
        const currentPoint = getEventPoint(event);
        const polygon = getElementPolygon(element, enterPoint); // If the current's event mouse position is inside the transit
        // polygon, this means that the mouse is moving toward the hover card,
        // so we disable this event. This is necessary because the mousemove
        // event may trigger focus on other elements and close the hovercard.

        if (isPointInPolygon(currentPoint, polygon)) {
          // Refreshes the enter point.
          enterPointRef.current = currentPoint;
          if (!disablePointerEventsProp(event)) return;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }

      if (!hideOnHoverOutsideProp(event)) return; // Otherwise, hide the hovercard after a short delay (hideTimeout).

      hideTimeoutRef.current = window.setTimeout(() => {
        hideTimeoutRef.current = 0;
        state.hide();
      }, state.hideTimeout);
    };

    return addGlobalEventListener("mousemove", onMouseMove, true);
  }, [domReady, state.mounted, mayHideOnHoverOutside, mayDisablePointerEvents, state.anchorRef, nestedHovercards, disablePointerEventsProp, hideOnHoverOutsideProp, state.hide, state.hideTimeout]); // Disable mouse events while the mouse is moving toward the hovercard. This
  // is necessary because these events may trigger focus on other elements and
  // close the hovercard while the user is moving the mouse toward it.

  useEffect(() => {
    if (!domReady) return;
    if (!state.mounted) return;
    if (!mayDisablePointerEvents) return;

    const disableEvent = event => {
      const element = ref.current;
      if (!element) return;
      const enterPoint = enterPointRef.current;
      if (!enterPoint) return;
      const polygon = getElementPolygon(element, enterPoint);

      if (isPointInPolygon(getEventPoint(event), polygon)) {
        if (!disablePointerEventsProp(event)) return;
        event.preventDefault();
        event.stopPropagation();
      }
    };

    return chain( // Note: we may need to add pointer events here in the future.
    addGlobalEventListener("mouseenter", disableEvent, true), addGlobalEventListener("mouseover", disableEvent, true), addGlobalEventListener("mouseout", disableEvent, true), addGlobalEventListener("mouseleave", disableEvent, true));
  }, [domReady, state.mounted, mayDisablePointerEvents, disablePointerEventsProp]);
  const registerOnParent = useContext(NestedHovercardContext); // Register the hovercard as a nested hovercard on the parent hovercard if
  // if it's not a modal, is portal and is mounted. We don't need to register
  // non-portal hovercards because they will be captured by the contains
  // function in the isMovingOnHovercard function above. This must be a layout
  // effect so we don't lose mouse move events right after the nested
  // hovercard has been mounted (for example, a submenu that's overlapping its
  // menu button and we keep moving the mouse while the submenu is due to
  // open).

  useSafeLayoutEffect(() => {
    if (modal) return;
    if (!portal) return;
    if (!state.mounted) return;
    if (!domReady) return;
    const element = ref.current;
    if (!element) return;
    return registerOnParent == null ? void 0 : registerOnParent(element);
  }, [modal, portal, state.mounted, domReady]);
  const registerNestedHovercard = useCallback(element => {
    setNestedHovercards(prevElements => [...prevElements, element]);
    const parentUnregister = registerOnParent == null ? void 0 : registerOnParent(element);
    return () => {
      setNestedHovercards(prevElements => prevElements.filter(item => item !== element));
      parentUnregister == null ? void 0 : parentUnregister();
    };
  }, [registerOnParent]);
  props = useWrapElement(props, element => /*#__PURE__*/jsx(NestedHovercardContext.Provider, {
    value: registerNestedHovercard,
    children: element
  }), [registerNestedHovercard]);
  props = { ...props,
    ref: useForkRef(ref, props.ref)
  };
  props = useAutoFocusOnHide({
    state,
    ...props
  });
  props = useAutoFocusOnShow({
    state,
    ...props
  });
  props = usePopover({
    hideOnEscape,
    state,
    modal,
    portal,
    ...props,
    portalRef
  });
  return props;
});
/**
 * A component that renders a hovercard element, which is a popover that's
 * usually made visible by hovering the mouse cursor over an anchor element
 * (`HovercardAnchor`).
 * @see https://ariakit.org/components/hovercard
 * @example
 * ```jsx
 * const hovercard = useHovercardState();
 * <HovercardAnchor state={hovercard}>@username</HovercardAnchor>
 * <Hovercard state={hovercard}>Details</Hovercard>
 * ```
 */

const Hovercard = createComponent(props => {
  const htmlProps = useHovercard(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Hovercard.displayName = "Hovercard";
}

const MenuBarContext = createStoreContext();
const MenuContext = createStoreContext();
function hasExpandedMenuButton(items, currentElement) {
  return !!(items != null && items.filter(item => item.ref.current !== currentElement).some(item => {
    var _item$ref$current;

    return ((_item$ref$current = item.ref.current) == null ? void 0 : _item$ref$current.getAttribute("aria-expanded")) === "true";
  }));
}

const pointsMap = {
  top: "4,10 8,6 12,10",
  right: "6,4 10,8 6,12",
  bottom: "4,6 8,10 12,6",
  left: "10,4 6,8 10,12"
};
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an arrow pointing to the popover position. It's
 * usually rendered inside the `PopoverDisclosure` component.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const state = usePopoverState();
 * const props = usePopoverDisclosureArrow({ state });
 * <PopoverDisclosure state={state}>
 *   Disclosure
 *   <Role {...props} />
 * </PopoverDisclosure>
 * ```
 */

const usePopoverDisclosureArrow = createHook(_ref => {
  var _placement, _state, _placement2;

  let {
    state,
    placement,
    ...props
  } = _ref;
  const context = useContext(PopoverContext);
  state = state || context;
  placement = (_placement = placement) != null ? _placement : (_state = state) == null ? void 0 : _state.placement;
  const dir = (_placement2 = placement) == null ? void 0 : _placement2.split("-")[0];
  const points = pointsMap[dir];
  const children = useMemo(() => /*#__PURE__*/jsx("svg", {
    display: "block",
    fill: "none",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "1.5pt",
    viewBox: "0 0 16 16",
    height: "1em",
    width: "1em",
    children: /*#__PURE__*/jsx("polyline", {
      points: points
    })
  }), [points]);
  props = {
    children,
    "aria-hidden": true,
    ...props,
    style: {
      width: "1em",
      height: "1em",
      pointerEvents: "none",
      ...props.style
    }
  };
  return props;
});
/**
 * A component that renders an arrow pointing to the popover position. It's
 * usually rendered inside the `PopoverDisclosure` component.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const popover = usePopoverState();
 * <PopoverDisclosure state={popover}>
 *   Disclosure
 *   <PopoverDisclosureArrow />
 * </PopoverDisclosure>
 * <Popover state={popover}>Popover</Popover>
 * ```
 */

const PopoverDisclosureArrow = createComponent(props => {
  const htmlProps = usePopoverDisclosureArrow(props);
  return createElement("span", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  PopoverDisclosureArrow.displayName = "PopoverDisclosureArrow";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an arrow pointing to the menu position, usually
 * inside a `MenuButton`.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const state = useMenuState();
 * const props = useMenuButtonArrow({ state });
 * <MenuButton state={state}>
 *   Edit
 *   <Role {...props} />
 * </MenuButton>
 * <Menu state={state}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */
const useMenuButtonArrow = createHook(_ref => {
  let {
    state,
    ...props
  } = _ref;
  props = usePopoverDisclosureArrow({
    state,
    ...props
  });
  return props;
});
/**
 * A component that renders an arrow pointing to the menu position, usually
 * inside a `MenuButton`.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const menu = useMenuState();
 * <MenuButton state={menu}>
 *   Edit
 *   <MenuButtonArrow />
 * </MenuButton>
 * <Menu state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */

const MenuButtonArrow = createComponent(props => {
  const htmlProps = useMenuButtonArrow(props);
  return createElement("span", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  MenuButtonArrow.displayName = "MenuButtonArrow";
}

function getInitialFocus(event, dir) {
  const keyMap = {
    ArrowDown: dir === "bottom" || dir === "top" ? "first" : false,
    ArrowUp: dir === "bottom" || dir === "top" ? "last" : false,
    ArrowRight: dir === "right" ? "first" : false,
    ArrowLeft: dir === "left" ? "first" : false
  };
  return keyMap[event.key];
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a menu button that triggers a dropdown menu.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const state = useMenuState();
 * const props = useMenuButton({ state });
 * <Role {...props}>Edit</Role>
 * <Menu state={state}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */


const useMenuButton = createHook(_ref => {
  let {
    state,
    focusable,
    accessibleWhenDisabled,
    showOnHover,
    ...props
  } = _ref;
  const ref = useRef(null);
  const parentMenu = useStore(MenuContext, ["items", "move", "contentElement"]);
  const parentMenuBar = useStore(MenuBarContext, ["items", "move"]);
  const hasParentMenu = !!parentMenu;
  const parentIsMenuBar = !!parentMenuBar && !hasParentMenu;
  const disabled = props.disabled || props["aria-disabled"] === true || props["aria-disabled"] === "true";
  useEffect(() => {
    // Makes sure that the menu button is assigned as the menu disclosure
    // element. This is needed to support screen reader focusing on sibling
    // menu items.
    state.disclosureRef.current = ref.current;
  });
  const onFocusProp = props.onFocus;
  const onFocus = useEvent$1(event => {
    onFocusProp == null ? void 0 : onFocusProp(event);
    if (disabled) return;
    if (event.defaultPrevented) return; // Reset the autoFocusOnShow state so we can focus the menu button while
    // the menu is open and press arrow keys to move focus to the menu items.

    state.setAutoFocusOnShow(false); // We need to unset the active menu item so no menu item appears active
    // while the menu button is focused.

    state.setActiveId(null); // When the menu button is focused, we'll only show its menu if it's in a
    // menu bar

    if (!parentMenuBar) return;
    if (!parentIsMenuBar) return; // and there's already another expanded menu button.

    if (hasExpandedMenuButton(parentMenuBar.items, event.currentTarget)) {
      state.show();
    }
  });
  const dir = state.placement.split("-")[0];
  const onKeyDownProp = props.onKeyDown;
  const onKeyDown = useEvent$1(event => {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (disabled) return;
    if (event.defaultPrevented) return;
    const initialFocus = getInitialFocus(event, dir);

    if (initialFocus) {
      event.preventDefault();
      state.show();
      state.setAutoFocusOnShow(true);
      state.setInitialFocus(initialFocus);
    }
  });
  const onClickProp = props.onClick;
  const onClick = useEvent$1(event => {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    const isKeyboardClick = !event.detail; // When the menu button is clicked, if the menu is hidden or if it's a
    // keyboard click (enter or space),

    if (!state.open || isKeyboardClick) {
      // we'll only automatically focus on the menu if it's not a submenu
      // button, or if it's a keyboard click.
      if (!hasParentMenu || isKeyboardClick) {
        state.setAutoFocusOnShow(true);
      }

      state.setInitialFocus(isKeyboardClick ? "first" : "container");
    } // On submenu buttons, we can't hide the submenu by clicking on the menu
    // button again.


    if (hasParentMenu) {
      state.show();
    }
  });

  if (hasParentMenu) {
    // On Safari, VO+Space triggers a click twice on native button elements
    // with role menuitem (https://bugs.webkit.org/show_bug.cgi?id=228318).
    // So, if the menu button is rendered within a menu, we need to render it
    // as another element.
    props = {
      as: "div",
      ...props
    };
  } // We'll use this id to render the aria-labelledby attribute on the menu.


  const id = useId(props.id); // When the menu button is rendered inside another menu, we set the role
  // attribute here so it doesn't get overridden by the button component with
  // role="button".

  const role = hasParentMenu || parentIsMenuBar ? getPopupItemRole(parentMenu == null ? void 0 : parentMenu.contentElement, "menuitem") : undefined;
  props = {
    id,
    role,
    "aria-haspopup": getPopupRole(state.contentElement, "menu"),
    ...props,
    ref: useForkRef(ref, props.ref),
    onFocus,
    onKeyDown,
    onClick
  };
  props = useHovercardAnchor({
    state,
    focusable,
    accessibleWhenDisabled,
    ...props,
    showOnHover: event => {
      if (typeof showOnHover === "function") return showOnHover(event);
      if (showOnHover != null) return showOnHover;
      if (hasParentMenu) return true;
      return parentIsMenuBar && hasExpandedMenuButton(parentMenuBar.items);
    }
  });
  props = usePopoverDisclosure({
    state,
    toggleOnClick: !hasParentMenu,
    focusable,
    accessibleWhenDisabled,
    ...props
  });
  props = useCompositeTypeahead({
    state,
    typeahead: parentIsMenuBar,
    ...props
  });
  return props;
});
/**
 * A component that renders a menu button that triggers a dropdown menu.
 * Usually, this is rendered as a native `button` element, but if it's a submenu
 * button rendered as a menu item inside another menu, it'll be rendered as a
 * `div`.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const menu = useMenuState();
 * <MenuButton state={menu}>Edit</MenuButton>
 * <Menu state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */

const MenuButton = createComponent(props => {
  const htmlProps = useMenuButton(props);
  return createElement("button", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  MenuButton.displayName = "MenuButton";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a menu item.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const state = useMenuState();
 * const undo = useMenuItem({ state });
 * const redo = useMenuItem({ state });
 * <MenuButton state={state}>Edit</MenuButton>
 * <Menu state={state}>
 *   <Role {...undo}>Undo</Role>
 *   <Role {...redo}>Redo</Role>
 * </Menu>
 * ```
 */
const useMenuItem = createHook(_ref => {
  let {
    state,
    hideOnClick = true,
    preventScrollOnKeyDown = true,
    focusOnHover,
    ...props
  } = _ref;
  // Use MenuBar state as a fallback.
  const menuBarState = useStore(state || MenuBarContext, ["items"]);
  state = useStore(state || MenuContext, ["move", "hideAll", "contentElement"]) || menuBarState;
  const onClickProp = props.onClick;
  const hideOnClickProp = useBooleanEvent(hideOnClick);
  const hideMenu = state && "hideAll" in state ? state.hideAll : undefined;
  const isWithinMenu = !!hideMenu;
  const onClick = useEvent$1(event => {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (isDownloading(event)) return;
    if (isOpeningInNewTab(event)) return;
    if (!hideMenu) return; // If this item is also a menu button, we don't want to hide the menu.

    const popupType = event.currentTarget.getAttribute("aria-haspopup");
    if (popupType === "menu") return;
    if (!hideOnClickProp(event)) return;
    hideMenu();
  });
  const role = getPopupItemRole(state && "contentElement" in state ? state.contentElement : null, "menuitem");
  props = {
    role,
    ...props,
    onClick
  };
  props = useCompositeItem({
    state,
    preventScrollOnKeyDown,
    ...props
  });
  props = useCompositeHover({
    state,
    ...props,
    focusOnHover: event => {
      var _state;

      if (typeof focusOnHover === "function") return focusOnHover(event);
      if (focusOnHover != null) return focusOnHover; // The menu container should be focused on mouseleave only if the menu
      // item is inside a menu, not a menu bar.

      if (event.type === "mouseleave") return isWithinMenu;

      if (isWithinMenu) {
        // If the menu item is also a submenu button, we should move actual
        // DOM focus to it so that the submenu will not close when the user
        // moves the cursor back to the menu button.
        if (event.currentTarget.hasAttribute("aria-expanded")) {
          event.currentTarget.focus();
        }

        return true;
      } // If the menu item is inside a menu bar, we should move DOM focus to
      // the menu item if there's another expanded menu button inside the menu
      // bar. Without this, the open menus in the menu bar wouldn't close.
      else if (hasExpandedMenuButton((_state = state) == null ? void 0 : _state.items, event.currentTarget)) {
        event.currentTarget.focus();
        return true;
      }

      return false;
    }
  });
  return props;
});
/**
 * A component that renders a menu item.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const menu = useMenuState();
 * <MenuButton state={menu}>Edit</MenuButton>
 * <Menu state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */

const MenuItem$1 = createMemoComponent(props => {
  const htmlProps = useMenuItem(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  MenuItem$1.displayName = "MenuItem";
}

function useAriaLabelledBy(_ref) {
  let {
    state,
    ...props
  } = _ref;
  const [id, setId] = useState(undefined);
  const label = props["aria-label"];
  useEffect(() => {
    const disclosure = state.disclosureRef.current;
    if (!disclosure) return;
    const menu = state.contentElement;
    if (!menu) return;
    const menuLabel = label || menu.hasAttribute("aria-label");

    if (menuLabel) {
      setId(undefined);
    } else if (disclosure.id) {
      setId(disclosure.id);
    }
  }, [label, state.disclosureRef, state.contentElement]);
  return id;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a menu list element.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const state = useMenuState();
 * const props = useMenuList({ state });
 * <MenuButton state={state}>Edit</MenuButton>
 * <Role {...props}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Role>
 * ```
 */


const useMenuList = createHook(_ref2 => {
  let {
    state,
    composite = true,
    ...props
  } = _ref2;
  const parentMenu = useStore(MenuContext, []);
  const parentMenuBar = useStore(MenuBarContext, ["items", "move", "next", "previous", "orientation"]);
  const hasParentMenu = !!parentMenu;
  const id = useId(props.id);
  const onKeyDownProp = props.onKeyDown;
  const dir = state.placement.split("-")[0];
  const orientation = state.orientation === "both" ? undefined : state.orientation;
  const isHorizontal = orientation !== "vertical";
  const isMenuBarHorizontal = !!parentMenuBar && (parentMenuBar == null ? void 0 : parentMenuBar.orientation) !== "vertical";
  const onKeyDown = useEvent$1(event => {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;

    if (hasParentMenu || parentMenuBar && !isHorizontal) {
      const hideMap = {
        ArrowRight: () => dir === "left" && !isHorizontal,
        ArrowLeft: () => dir === "right" && !isHorizontal,
        ArrowUp: () => dir === "bottom" && isHorizontal,
        ArrowDown: () => dir === "top" && isHorizontal
      };
      const action = hideMap[event.key];

      if (action != null && action()) {
        event.stopPropagation();
        event.preventDefault();
        return state.hide();
      }
    }

    if (parentMenuBar) {
      const keyMap = {
        ArrowRight: () => {
          if (!isMenuBarHorizontal) return;
          return parentMenuBar.next();
        },
        ArrowLeft: () => {
          if (!isMenuBarHorizontal) return;
          return parentMenuBar.previous();
        },
        ArrowDown: () => {
          if (isMenuBarHorizontal) return;
          return parentMenuBar.next();
        },
        ArrowUp: () => {
          if (isMenuBarHorizontal) return;
          return parentMenuBar.previous();
        }
      };
      const action = keyMap[event.key];
      const id = action == null ? void 0 : action();

      if (id !== undefined) {
        event.stopPropagation();
        event.preventDefault();
        parentMenuBar.move(id);
      }
    }
  });
  props = useStoreProvider({
    state,
    ...props
  }, MenuContext);
  const ariaLabelledBy = useAriaLabelledBy({
    state,
    ...props
  });
  const style = state.mounted ? props.style : { ...props.style,
    display: "none"
  };
  props = {
    id,
    "aria-labelledby": ariaLabelledBy,
    hidden: !state.mounted,
    ...props,
    ref: useForkRef(id ? state.setContentElement : null, props.ref),
    style,
    onKeyDown
  };

  if (composite) {
    props = {
      role: "menu",
      "aria-orientation": orientation,
      ...props
    };
  }

  props = useComposite({
    state,
    composite,
    ...props
  });
  props = useCompositeTypeahead({
    state,
    ...props
  });
  return props;
});
/**
 * A component that renders a menu list element.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const menu = useMenuState();
 * <MenuButton state={menu}>Edit</MenuButton>
 * <MenuList state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </MenuList>
 * ```
 */

const MenuList = createComponent(props => {
  const htmlProps = useMenuList(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  MenuList.displayName = "MenuList";
}

/**
 * Provides state for the `Menu` components.
 * @example
 * ```jsx
 * const menu = useMenuState({ placement: "top" });
 * <MenuButton state={menu}>Edit</MenuButton>
 * <Menu state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */
function useMenuState(_temp) {
  var _timeout;

  let {
    orientation = "vertical",
    timeout,
    hideTimeout = 0,
    ...props
  } = _temp === void 0 ? {} : _temp;
  const [initialFocus, setInitialFocus] = useState("container");
  const [values, setValues] = useControlledState(props.defaultValues || {}, props.values, props.setValues);
  const parentMenu = useStore(MenuContext, ["orientation", "hideAll"]);
  const parentMenuBar = useStore(MenuBarContext, ["orientation"]);
  const contextOrientation = (parentMenu == null ? void 0 : parentMenu.orientation) || (parentMenuBar == null ? void 0 : parentMenuBar.orientation);
  const parentIsMenuBar = !!parentMenuBar && !parentMenu; // Defines the placement of the menu popover based on the parent orientation.

  const placement = props.placement || (contextOrientation === "vertical" ? "right-start" : "bottom-start");
  timeout = ((_timeout = timeout) != null ? _timeout : parentIsMenuBar) ? 0 : 150;
  const composite = useCompositeState({
    orientation,
    ...props
  });
  const hovercard = useHovercardState({
    timeout,
    hideTimeout,
    ...props,
    placement
  }); // Resets the initial focus state when the menu is closed.

  useEffect(() => {
    if (hovercard.mounted) return;
    composite.setActiveId(null);
  }, [hovercard.mounted, composite.setActiveId]);
  const setValue = useCallback((name, value) => {
    // Preventing prototype pollution.
    if (name === "__proto__" || name === "constructor") return;
    setValues(prevValues => {
      const prevValue = prevValues[name];
      const nextValue = applyState(value, prevValue);

      if (nextValue === prevValue) {
        return prevValues;
      }

      return { ...prevValues,
        [name]: nextValue === undefined ? !!nextValue : nextValue
      };
    });
  }, [setValues]);
  const hideAll = useCallback(() => {
    hovercard.hide();
    parentMenu == null ? void 0 : parentMenu.hideAll();
  }, [hovercard.hide, parentMenu == null ? void 0 : parentMenu.hideAll]);
  const state = useMemo(() => ({ ...composite,
    ...hovercard,
    initialFocus,
    setInitialFocus,
    values,
    setValues,
    setValue,
    hideAll
  }), [composite, hovercard, initialFocus, setInitialFocus, values, setValues, setValue, hideAll]);
  return useStorePublisher(state);
}

function getItemRefById(items, id) {
  var _items$find;

  if (!id) return;
  return (_items$find = items.find(item => item.id === id)) == null ? void 0 : _items$find.ref;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a dropdown menu element.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const state = useMenuState();
 * const props = useMenu({ state });
 * <MenuButton state={state}>Edit</MenuButton>
 * <Role {...props}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Role>
 * ```
 */


const useMenu = createHook(_ref => {
  let {
    state,
    hideOnEscape = true,
    autoFocusOnShow = true,
    hideOnHoverOutside,
    ...props
  } = _ref;
  const parentMenu = useStore(MenuContext, []);
  const parentMenuBar = useStore(MenuBarContext, []);
  const hasParentMenu = !!parentMenu;
  const parentIsMenuBar = !!parentMenuBar && !hasParentMenu;
  const onKeyDownProp = props.onKeyDown;
  const hideOnEscapeProp = useBooleanEvent(hideOnEscape);
  const onKeyDown = useEvent$1(event => {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;

    if (event.key === "Escape") {
      if (!hideOnEscapeProp(event)) return;

      if (!hasParentMenu) {
        // On Esc, only stop propagation if there's no parent menu. Otherwise,
        // pressing Esc should close all menus
        event.stopPropagation();
      }

      return state.hide();
    }
  });
  props = { ...props,
    onKeyDown
  }; // The aria-labelledby prop on MenuList defaults to the MenuButton's id. On
  // Dialog/Popover/Hovercard/Menu, we need to consider MenuHeading as well
  // and it should take precedence. That's why we need to destructure this
  // prop here and check if aria-labelledby is set later.

  const {
    "aria-labelledby": ariaLabelledBy,
    ...menuListProps
  } = useMenuList({
    state,
    ...props
  });
  props = menuListProps;
  const [initialFocusRef, setInitialFocusRef] = useState(); // Sets the initial focus ref.

  useEffect(() => {
    let cleaning = false;
    setInitialFocusRef(prevInitialFocusRef => {
      if (cleaning) return;
      if (!state.autoFocusOnShow) return;
      if (prevInitialFocusRef) return prevInitialFocusRef;

      switch (state.initialFocus) {
        case "first":
          return getItemRefById(state.items, state.first());

        case "last":
          return getItemRefById(state.items, state.last());

        default:
          return state.baseRef;
      }
    });
    return () => {
      cleaning = true;
    };
  }, [state.autoFocusOnShow, state.initialFocus, state.items, state.first, state.last, state.baseRef]);
  const mayAutoFocusOnShow = !!autoFocusOnShow; // When the `autoFocusOnShow` prop is set to `true` (default), we'll only
  // move focus to the menu when there's an initialFocusRef set or the menu is
  // modal. Otherwise, users would have to manually call
  // state.setAutoFocusOnShow(true) every time they want to open the menu.
  // This differs from the usual dialog behavior that would automatically
  // focus on the dialog container when no initialFocusRef is set.

  const canAutoFocusOnShow = !!initialFocusRef || !!props.initialFocusRef || !!props.modal;
  props = useHovercard({
    state,
    initialFocusRef,
    autoFocusOnShow: mayAutoFocusOnShow ? canAutoFocusOnShow && autoFocusOnShow : state.autoFocusOnShow || !!props.modal,
    ...props,
    hideOnHoverOutside: event => {
      if (typeof hideOnHoverOutside === "function") {
        return hideOnHoverOutside(event);
      }

      if (hideOnHoverOutside != null) return hideOnHoverOutside;

      if (hasParentMenu) {
        parentMenu.setActiveId(null);
        return true;
      }

      if (!parentIsMenuBar) return false;
      const disclosure = state.disclosureRef.current;
      if (!disclosure) return true;
      if (hasFocusWithin(disclosure)) return false;
      return true;
    },
    // If it's a submenu, it shouldn't behave like a modal dialog, nor display
    // a backdrop.
    modal: hasParentMenu ? false : props.modal,
    backdrop: hasParentMenu ? false : props.backdrop,
    // If it's a submenu, hide on esc will be handled differently. That is,
    // event.stopPropagation() won't be called, so the parent menus will also
    // be closed.
    hideOnEscape: hasParentMenu ? false : hideOnEscape
  });
  props = {
    "aria-labelledby": ariaLabelledBy,
    ...props
  };
  return props;
});
/**
 * A component that renders a dropdown menu element.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const menu = useMenuState();
 * <MenuButton state={menu}>Edit</MenuButton>
 * <Menu state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */

const Menu$1 = createComponent(props => {
  const htmlProps = useMenu(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Menu$1.displayName = "Menu";
}

/**
 * A component that renders an abstract element that supports the `as` prop,
 * `children` as a function and a `wrapElement` prop that can be used to wrap
 * the underlying element with React Portal, Context or other component types.
 * @see https://ariakit.org/components/role
 * @example
 * ```jsx
 * <Role as="div" />
 * ```
 */

const Role = createComponent(props => {
  return createElement("div", props);
});

if (process.env.NODE_ENV !== "production") {
  Role.displayName = "Role";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an arrow pointing to the select popover position.
 * It's usually rendered inside the `Select` component.
 * @see https://ariakit.org/components/select
 * @example
 * ```jsx
 * const state = useSelectState();
 * const props = useSelectArrow({ state });
 * <Select state={state}>
 *   {state.value}
 *   <Role {...props} />
 * </Select>
 * <SelectPopover state={state}>
 *   <SelectItem value="Apple" />
 *   <SelectItem value="Orange" />
 * </SelectPopover>
 * ```
 */
const useSelectArrow = createHook(_ref => {
  let {
    state,
    ...props
  } = _ref;
  props = usePopoverDisclosureArrow({
    state,
    ...props
  });
  return props;
});
/**
 * A component that renders an arrow pointing to the select popover position.
 * It's usually rendered inside the `Select` component.
 * @see https://ariakit.org/components/select
 * @example
 * ```jsx
 * const select = useSelectState();
 * <Select state={select}>
 *   {select.value}
 *   <SelectArrow />
 * </Select>
 * <SelectPopover state={select}>
 *   <SelectItem value="Apple" />
 *   <SelectItem value="Orange" />
 * </SelectPopover>
 * ```
 */

const SelectArrow = createComponent(props => {
  const htmlProps = useSelectArrow(props);
  return createElement("span", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  SelectArrow.displayName = "SelectArrow";
}

const SelectItemCheckedContext = /*#__PURE__*/createContext(false);
const SelectContext = createStoreContext();
function findFirstEnabledItemWithValue(items) {
  return items.find(item => item.value != null && !item.disabled);
}
function findEnabledItemWithValueById(items, id) {
  return items.find(item => item.value != null && item.id === id && !item.disabled);
}
function findEnabledItemByValue(items, value) {
  return items.find(item => item.value === value && !item.disabled);
}

function isSelected(stateValue, itemValue) {
  if (stateValue == null) return false;
  if (itemValue == null) return false;

  if (Array.isArray(stateValue)) {
    return stateValue.includes(itemValue);
  }

  return stateValue === itemValue;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a select item.
 * @see https://ariakit.org/components/select
 * @example
 * ```jsx
 * const state = useSelectState();
 * const props = useSelectItem({ state, value: "Apple" });
 * <Role {...props} />
 * ```
 */


const useSelectItem = createHook(_ref => {
  var _state, _hideOnClick, _state4, _state5;

  let {
    state,
    value,
    getItem: getItemProp,
    hideOnClick,
    setValueOnClick = value != null,
    preventScrollOnKeyDown = true,
    focusOnHover = true,
    ...props
  } = _ref;
  state = useStore(state || SelectContext, [useCallback(s => isSelected(s.value, value), [value]), "setValue", "hide", "contentElement", "open"]);
  const disabled = props.disabled;
  const getItem = useCallback(item => {
    // When the item is disabled, we don't register its value.
    const nextItem = { ...item,
      value: disabled ? undefined : value
    };

    if (getItemProp) {
      return getItemProp(nextItem);
    }

    return nextItem;
  }, [disabled, value, getItemProp]);
  const multiSelectable = Array.isArray((_state = state) == null ? void 0 : _state.value);
  hideOnClick = (_hideOnClick = hideOnClick) != null ? _hideOnClick : value != null && !multiSelectable;
  const onClickProp = props.onClick;
  const setValueOnClickProp = useBooleanEvent(setValueOnClick);
  const hideOnClickProp = useBooleanEvent(hideOnClick);
  const onClick = useEvent$1(event => {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (isDownloading(event)) return;
    if (isOpeningInNewTab(event)) return;

    if (setValueOnClickProp(event) && value != null) {
      var _state2;

      (_state2 = state) == null ? void 0 : _state2.setValue(prevValue => {
        if (!Array.isArray(prevValue)) return value;

        if (prevValue.includes(value)) {
          return prevValue.filter(v => v !== value);
        }

        return [...prevValue, value];
      });
    }

    if (hideOnClickProp(event)) {
      var _state3;

      (_state3 = state) == null ? void 0 : _state3.hide();
    }
  });
  const selected = isSelected((_state4 = state) == null ? void 0 : _state4.value, value);
  props = useWrapElement(props, element => /*#__PURE__*/jsx(SelectItemCheckedContext.Provider, {
    value: selected,
    children: element
  }), [selected]);
  props = {
    role: getPopupItemRole((_state5 = state) == null ? void 0 : _state5.contentElement),
    "aria-selected": selected,
    children: value,
    ...props,
    onClick
  };
  props = useCompositeItem({
    state,
    getItem,
    preventScrollOnKeyDown,
    ...props
  });
  const focusOnHoverProp = useBooleanEvent(focusOnHover);
  props = useCompositeHover({
    state,
    ...props,
    // We have to disable focusOnHover when the popup is closed, otherwise
    // the active item will change to null (the container) when the popup is
    // closed by clicking on an item.
    focusOnHover: event => {
      var _state6;

      if (!focusOnHoverProp(event)) return false;
      return !!((_state6 = state) != null && _state6.open);
    }
  });
  return props;
});
/**
 * A component that renders a select item inside a select list or select
 * popover. The `role` prop will be automatically set based on the `SelectList`
 * or `SelectPopover` own `role` prop. For example, if the `SelectPopover`
 * component's `role` prop is set to `listbox` (default), the `SelectItem`
 * `role` will be set to `option`. By default, the `value` prop will be rendered
 * as the children, but this can be overriden.
 * @see https://ariakit.org/components/select
 * @example
 * ```jsx
 * const select = useSelectState();
 * <Select state={select} />
 * <SelectPopover state={select}>
 *   <SelectItem value="Apple" />
 *   <SelectItem value="Orange" />
 * </SelectPopover>
 * ```
 */

const SelectItem = createMemoComponent(props => {
  const htmlProps = useSelectItem(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  SelectItem.displayName = "SelectItem";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a select list.
 * @see https://ariakit.org/components/select
 * @example
 * ```jsx
 * const state = useSelectState();
 * const props = useSelectList({ state });
 * <Role {...props}>
 *   <SelectItem value="Apple" />
 *   <SelectItem value="Orange" />
 * </Role>
 * ```
 */
const useSelectList = createHook(_ref => {
  let {
    state,
    resetOnEscape = true,
    hideOnEnter = true,
    focusOnMove = true,
    composite = true,
    ...props
  } = _ref;
  const ref = useRef(null);
  const id = useId(props.id);
  const [defaultValue, setDefaultValue] = useState(state.value);
  const multiSelectable = Array.isArray(state.value); // Stores the intial value so we can reset it later when Escape is pressed

  useEffect(() => {
    if (state.mounted) return;
    setDefaultValue(state.value);
  }, [state.mounted, state.value]);
  resetOnEscape = resetOnEscape && !multiSelectable;
  const onKeyDownProp = props.onKeyDown;
  const resetOnEscapeProp = useBooleanEvent(resetOnEscape);
  const hideOnEnterProp = useBooleanEvent(hideOnEnter);
  const onKeyDown = useEvent$1(event => {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;

    if (event.key === "Escape" && resetOnEscapeProp(event)) {
      state.setValue(defaultValue);
    }

    if (event.key === " " || event.key === "Enter") {
      if (isSelfTarget(event) && hideOnEnterProp(event)) {
        event.preventDefault();
        state.hide();
      }
    }
  });
  props = useStoreProvider({
    state,
    ...props
  }, SelectContext);
  const labelId = useRefId(state.labelRef);
  const style = state.mounted ? props.style : { ...props.style,
    display: "none"
  };

  if (composite) {
    props = {
      role: "listbox",
      "aria-multiselectable": multiSelectable ? true : undefined,
      ...props
    };
  }

  props = {
    id,
    hidden: !state.mounted,
    "aria-labelledby": labelId,
    ...props,
    ref: useForkRef(id ? state.setContentElement : null, ref, props.ref),
    style,
    onKeyDown
  };
  props = useComposite({
    state,
    ...props,
    composite,
    focusOnMove: state.open && !state.animating && focusOnMove
  });
  props = useCompositeTypeahead({
    state,
    ...props
  });
  return props;
});
/**
 * A component that renders a select list. The `role` prop is set to `listbox`
 * by default, but can be overriden by any other valid select popup role
 * (`listbox`, `menu`, `tree`, `grid` or `dialog`). The `aria-labelledby` prop
 * is set to the select input element's `id` by default.
 * @see https://ariakit.org/components/select
 * @example
 * ```jsx
 * const select = useSelectState();
 * <Select state={select} />
 * <SelectList state={select}>
 *   <SelectItem value="Apple" />
 *   <SelectItem value="Orange" />
 * </SelectList>
 * ```
 */

const SelectList = createComponent(props => {
  const htmlProps = useSelectList(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  SelectList.displayName = "SelectList";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a select popover.
 * @see https://ariakit.org/components/select
 * @example
 * ```jsx
 * const state = useSelectState();
 * const props = useSelectPopover({ state });
 * <Role {...props}>
 *   <SelectItem value="Apple" />
 *   <SelectItem value="Orange" />
 * </Role>
 * ```
 */

const useSelectPopover = createHook(_ref => {
  var _values;

  let {
    state,
    ...props
  } = _ref;
  const values = toArray$1(state.value);
  const value = (_values = values[values.length - 1]) != null ? _values : "";
  const [item, setItem] = useState(null); // Sets the initial focus ref.

  useEffect(() => {
    let cleaning = false;
    setItem(prevItem => {
      if (cleaning) return null; // This must be state.open instead of state.mounted, otherwise
      // re-opening an animated popover before the leave animation is done
      // will not restore focus to the correct item.

      if (state.open && prevItem) return prevItem;
      const item = findEnabledItemByValue(state.items, value);
      return item || null;
    });
    return () => {
      cleaning = true;
    };
  }, [state.open, state.items, value]);
  props = useSelectList({
    state,
    ...props
  });
  props = usePopover({
    state,
    initialFocusRef: (item == null ? void 0 : item.ref) || state.baseRef,
    ...props
  });
  return props;
});
/**
 * A component that renders a select popover. The `role` prop is set to
 * `listbox` by default, but can be overriden by any other valid select popup
 * role (`listbox`, `menu`, `tree`, `grid` or `dialog`).
 * @see https://ariakit.org/components/select
 * @example
 * ```jsx
 * const select = useSelectState();
 * <Select state={select} />
 * <SelectPopover state={select}>
 *   <SelectItem value="Apple" />
 *   <SelectItem value="Orange" />
 * </SelectPopover>
 * ```
 */

const SelectPopover = createComponent(props => {
  const htmlProps = useSelectPopover(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  SelectPopover.displayName = "SelectPopover";
}

/**
 * Provides state for the `Select` components.
 * @example
 * ```jsx
 * const select = useSelectState({ defaultValue: "Apple" });
 * <Select state={select} />
 * <SelectPopover state={select}>
 *   <SelectItem value="Apple" />
 *   <SelectItem value="Orange" />
 * </SelectPopover>
 * ```
 */
function useSelectState(_temp) {
  var _props$defaultValue, _props$value;

  let {
    virtualFocus = true,
    orientation = "vertical",
    placement = "bottom-start",
    setValueOnMove = false,
    defaultActiveId = null,
    includesBaseElement = false,
    ...props
  } = _temp === void 0 ? {} : _temp;
  const selectRef = useRef(null);
  const labelRef = useRef(null);
  const [value, setValue] = useControlledState((_props$defaultValue = props.defaultValue) != null ? _props$defaultValue : "", props.value, props.setValue);
  const composite = useCompositeState({ ...props,
    virtualFocus,
    orientation,
    defaultActiveId,
    includesBaseElement
  });
  const popover = usePopoverState({ ...props,
    placement
  });
  const initialValue = useInitialValue$1((_props$value = props.value) != null ? _props$value : props.defaultValue);
  const compositeRef = useLiveRef(composite);
  const multiSelectable = Array.isArray(value); // Automatically sets the default value if it's not set.

  useEffect(() => {
    if (multiSelectable) return;
    if (initialValue != null) return;
    if (!composite.items.length) return;
    const item = findFirstEnabledItemWithValue(composite.items);
    if (!(item != null && item.value)) return;
    setValue(prevValue => {
      if (prevValue || !item.value) return prevValue;
      return item.value;
    });
  }, [multiSelectable, initialValue, composite.items, setValue]); // Sets the active id when the value changes and the popover is hidden.

  useEffect(() => {
    if (popover.mounted) return;
    const values = toArray$1(value);
    const lastValue = values[values.length - 1];
    if (!lastValue) return;
    const item = findEnabledItemByValue(composite.items, lastValue);
    if (!item) return;
    composite.setActiveId(item.id);
  }, [popover.mounted, composite.items, value, composite.setActiveId]);
  const mountedRef = useLiveRef(popover.mounted); // Sets the select value when the active item changes by moving (which usually
  // happens when moving to an item using the keyboard).

  useEffect(() => {
    if (multiSelectable) return;
    if (!setValueOnMove && mountedRef.current) return;
    const {
      activeId,
      items
    } = compositeRef.current;
    if (!composite.moves) return;
    if (!activeId) return;
    const item = findEnabledItemWithValueById(items, activeId);
    if ((item == null ? void 0 : item.value) == null) return;
    setValue(item.value);
  }, [multiSelectable, setValueOnMove, composite.moves, setValue]);
  const state = useMemo(() => ({ ...composite,
    ...popover,
    value,
    setValue,
    setValueOnMove,
    selectRef,
    labelRef
  }), [composite, popover, value, setValue, setValueOnMove]);
  return useStorePublisher(state);
}

function getSelectedValues(select) {
  return Array.from(select.selectedOptions).map(option => option.value);
} // When moving through the items when the select list is closed, we don't want
// to move to items without value, so we filter them out here.


function nextWithValue(items, next) {
  return () => {
    var _nextItem;

    const nextId = next();
    if (!nextId) return;
    let i = 0;
    let nextItem = items.find(item => item.id === nextId);
    const firstItem = nextItem;

    while (nextItem && nextItem.value == null) {
      const nextId = next(++i);
      if (!nextId) return;
      nextItem = items.find(item => item.id === nextId); // Prevents infinite loop when focusLoop is true

      if (nextItem === firstItem) break;
    }

    return (_nextItem = nextItem) == null ? void 0 : _nextItem.id;
  };
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a select button.
 * @see https://ariakit.org/components/select
 * @example
 * ```jsx
 * const state = useSelectState();
 * const props = useSelect({ state });
 * <Role {...props} />
 * ```
 */


const useSelect = createHook(_ref => {
  let {
    state,
    name,
    form,
    required,
    showOnKeyDown = true,
    moveOnKeyDown = true,
    toggleOnClick = false,
    toggleOnPress = !toggleOnClick,
    ...props
  } = _ref;
  toggleOnPress = toggleOnClick ? false : toggleOnPress;
  const onKeyDownProp = props.onKeyDown;
  const showOnKeyDownProp = useBooleanEvent(showOnKeyDown);
  const moveOnKeyDownProp = useBooleanEvent(moveOnKeyDown);
  const toggleOnPressProp = useBooleanEvent(toggleOnPress);
  const dir = state.placement.split("-")[0];
  const multiSelectable = Array.isArray(state.value);
  const onKeyDown = useEvent$1(event => {
    var _findFirstEnabledItem;

    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return; // toggleOnPress

    if (event.key === " " || event.key === "Enter") {
      if (toggleOnPressProp(event)) {
        event.preventDefault();
        state.toggle();
      }
    } // moveOnKeyDown


    const isVertical = state.orientation !== "horizontal";
    const isHorizontal = state.orientation !== "vertical";
    const isGrid = !!((_findFirstEnabledItem = findFirstEnabledItemWithValue(state.items)) != null && _findFirstEnabledItem.rowId);
    const moveKeyMap = {
      ArrowUp: (isGrid || isVertical) && nextWithValue(state.items, state.up),
      ArrowRight: (isGrid || isHorizontal) && nextWithValue(state.items, state.next),
      ArrowDown: (isGrid || isVertical) && nextWithValue(state.items, state.down),
      ArrowLeft: (isGrid || isHorizontal) && nextWithValue(state.items, state.previous)
    };
    const getId = moveKeyMap[event.key];

    if (getId && moveOnKeyDownProp(event)) {
      event.preventDefault();
      state.move(getId());
    } // showOnKeyDown


    const isTopOrBottom = dir === "top" || dir === "bottom";
    const isLeft = dir === "left";
    const isRight = dir === "right";
    const canShowKeyMap = {
      ArrowDown: isTopOrBottom,
      ArrowUp: isTopOrBottom,
      ArrowLeft: isLeft,
      ArrowRight: isRight
    };
    const canShow = canShowKeyMap[event.key];

    if (canShow && showOnKeyDownProp(event)) {
      event.preventDefault();
      state.show();
      state.move(state.activeId);
    }
  });
  const onMouseDownProp = props.onMouseDown;
  const onMouseDown = useEvent$1(event => {
    onMouseDownProp == null ? void 0 : onMouseDownProp(event);
    if (event.defaultPrevented) return;
    if (event.button) return;
    if (event.ctrlKey) return;
    if (!toggleOnPressProp(event)) return;
    const element = event.currentTarget;
    queueBeforeEvent(element, "focusin", () => {
      state.disclosureRef.current = element;
      state.toggle();
    });
  });
  props = useStoreProvider({
    state,
    ...props
  }, SelectContext);
  const [autofill, setAutofill] = useState(false);
  const nativeSelectChangedRef = useRef(false); // Resets the autofilled state when the select value changes, but only if
  // the change wasn't triggered by the native select element (which is an
  // autofill).

  useEffect(() => {
    const nativeSelectChanged = nativeSelectChangedRef.current;
    nativeSelectChangedRef.current = false;
    if (nativeSelectChanged) return;
    setAutofill(false);
  }, [state.value]);
  const labelId = useRefId(state.labelRef);
  const label = props["aria-label"];
  const labelledBy = props["aria-labelledby"] || labelId;
  const values = useMemo( // Filter out items without value and duplicate values.
  () => [...new Set(state.items.map(i => i.value).filter(i => i != null))], [state.items]); // Renders a native select element with the same value as the select so we
  // support browser autofill. When the native select value changes, the
  // onChange event is triggered and we set the autofill state to true.

  props = useWrapElement(props, element => /*#__PURE__*/jsxs(Fragment$1, {
    children: [/*#__PURE__*/jsx(VisuallyHidden, {
      as: "select",
      tabIndex: -1,
      "aria-hidden": true,
      "aria-label": label,
      "aria-labelledby": labelledBy,
      name: name,
      form: form,
      required: required,
      value: state.value,
      multiple: multiSelectable // Even though this element is visually hidden and is not tabbable,
      // it's still focusable. Some autofill extensions like 1password
      // will move focus to the next form element on autofill. In this
      // case, we want to move focus to our custom select element.
      ,
      onFocus: () => {
        var _state$selectRef$curr;

        return (_state$selectRef$curr = state.selectRef.current) == null ? void 0 : _state$selectRef$curr.focus();
      },
      onChange: event => {
        nativeSelectChangedRef.current = true;
        setAutofill(true);
        state.setValue(multiSelectable ? getSelectedValues(event.target) : event.target.value);
      },
      children: values.map(value => /*#__PURE__*/jsx("option", {
        value: value,
        children: value
      }, value))
    }), element]
  }), [label, labelledBy, name, form, required, state.value, multiSelectable, state.selectRef, state.setValue, values]);

  const children = /*#__PURE__*/jsxs(Fragment$1, {
    children: [state.value, /*#__PURE__*/jsx(SelectArrow, {})]
  });

  props = {
    role: "combobox",
    "aria-autocomplete": "none",
    "aria-labelledby": labelId,
    "aria-haspopup": getPopupRole(state.contentElement, "listbox"),
    "data-autofill": autofill ? "" : undefined,
    children,
    ...props,
    ref: useForkRef(state.selectRef, props.ref),
    onKeyDown,
    onMouseDown
  };
  props = usePopoverDisclosure({
    state,
    toggleOnClick,
    ...props
  });
  props = useCompositeTypeahead({
    state,
    ...props
  });
  return props;
});
/**
 * A component that renders a select button.
 * @see https://ariakit.org/components/select
 * @example
 * ```jsx
 * const select = useSelectState();
 * <Select state={select} />
 * <SelectPopover state={select}>
 *   <SelectItem value="Apple" />
 *   <SelectItem value="Orange" />
 * </SelectPopover>
 * ```
 */

const Select$1 = createComponent(props => {
  const htmlProps = useSelect(props);
  return createElement("button", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Select$1.displayName = "Select";
}

const TabContext = createStoreContext();

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a tab list element.
 * @see https://ariakit.org/components/tab
 * @example
 * ```jsx
 * const state = useTabState();
 * const props = useTabList({ state });
 * <Role {...props}>
 *   <Tab>Tab 1</Tab>
 *   <Tab>Tab 2</Tab>
 * </Role>
 * <TabPanel state={state}>Panel 1</TabPanel>
 * <TabPanel state={state}>Panel 2</TabPanel>
 * ```
 */
const useTabList = createHook(_ref => {
  let {
    state,
    ...props
  } = _ref;
  const orientation = state.orientation === "both" ? undefined : state.orientation;
  props = {
    role: "tablist",
    "aria-orientation": orientation,
    ...props
  };
  props = useStoreProvider({
    state,
    ...props
  }, TabContext);
  props = useComposite({
    state,
    ...props
  });
  return props;
});
/**
 * A component that renders a tab list element.
 * @see https://ariakit.org/components/tab
 * @example
 * ```jsx
 * const tab = useTabState();
 * <TabList state={tab}>
 *   <Tab>Tab 1</Tab>
 *   <Tab>Tab 2</Tab>
 * </TabList>
 * <TabPanel state={tab}>Panel 1</TabPanel>
 * <TabPanel state={tab}>Panel 2</TabPanel>
 * ```
 */

const TabList = createComponent(props => {
  const htmlProps = useTabList(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  TabList.displayName = "TabList";
}

function getTabId(panels, id) {
  var _panels$items$find;

  if (!id) return;
  return (_panels$items$find = panels.items.find(panel => panel.id === id)) == null ? void 0 : _panels$items$find.tabId;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a tab panel element.
 * @see https://ariakit.org/components/tab
 * @example
 * ```jsx
 * const state = useTabState();
 * const props = useTabPanel({ state });
 * <TabList state={state}>
 *   <Tab>Tab 1</Tab>
 * </TabList>
 * <Role {...props}>Panel 1</Role>
 * ```
 */


const useTabPanel = createHook(_ref => {
  let {
    state,
    tabId: tabIdProp,
    getItem: getItemProp,
    ...props
  } = _ref;
  const ref = useRef(null);
  const id = useId(props.id);
  const [hasTabbableChildren, setHasTabbableChildren] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const tabbable = getAllTabbableIn(element);
    setHasTabbableChildren(!!tabbable.length);
  }, []);
  const getItem = useCallback(item => {
    const nextItem = { ...item,
      id,
      tabId: tabIdProp
    };

    if (getItemProp) {
      return getItemProp(nextItem);
    }

    return nextItem;
  }, [id, tabIdProp, getItemProp]);
  const tabId = tabIdProp || getTabId(state.panels, id);
  const open = !!tabId && state.selectedId === tabId;
  props = {
    id,
    role: "tabpanel",
    "aria-labelledby": tabId || undefined,
    ...props,
    ref: useForkRef(ref, props.ref)
  };
  const disclosure = useDisclosureState({
    open
  });
  props = useFocusable({
    focusable: !hasTabbableChildren,
    ...props
  });
  props = useDisclosureContent({
    state: disclosure,
    ...props
  });
  props = useCollectionItem({
    state: state.panels,
    ...props,
    getItem
  });
  return props;
});
/**
 * A component that renders a tab panel element.
 * @see https://ariakit.org/components/tab
 * @example
 * ```jsx
 * const tab = useTabState();
 * <TabList state={tab}>
 *   <Tab>Tab 1</Tab>
 *   <Tab>Tab 2</Tab>
 * </TabList>
 * <TabPanel state={tab}>Panel 1</TabPanel>
 * <TabPanel state={tab}>Panel 2</TabPanel>
 * ```
 */

const TabPanel = createComponent(props => {
  const htmlProps = useTabPanel(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  TabPanel.displayName = "TabPanel";
}

function findEnabledTabById(items, id) {
  return items.find(item => item.id === id && !item.disabled && !item.dimmed);
}

function findFirstEnabledTab(items) {
  return items.find(item => !item.disabled && !item.dimmed);
}
/**
 * Provides state for the `Tab` components.
 * @example
 * ```jsx
 * const tab = useTabState();
 * <TabList state={tab}>
 *   <Tab>Tab 1</Tab>
 *   <Tab>Tab 2</Tab>
 * </TabList>
 * <TabPanel state={tab}>Panel 1</TabPanel>
 * <TabPanel state={tab}>Panel 2</TabPanel>
 * ```
 */


function useTabState(_temp) {
  let {
    orientation = "horizontal",
    focusLoop = true,
    selectOnMove = true,
    ...props
  } = _temp === void 0 ? {} : _temp;
  const [selectedId, setSelectedId] = useControlledState(props.defaultSelectedId, props.selectedId, props.setSelectedId);
  const composite = useCompositeState({
    orientation,
    focusLoop,
    ...props
  });
  const panels = useCollectionState();
  const compositeRef = useLiveRef(composite); // Selects the active tab when selectOnMove is true. Since we're listening to
  // the moves state, but not the activeId state, this effect will run only when
  // there's a move, which is usually triggered by moving through the tabs using
  // the keyboard.

  useEffect(() => {
    if (!selectOnMove) return;
    const {
      activeId,
      items
    } = compositeRef.current;
    if (!activeId) return;
    const tab = findEnabledTabById(items, activeId);
    if (!tab) return;
    setSelectedId(tab.id);
  }, [composite.moves, selectOnMove, setSelectedId]); // Keep activeId in sync with selectedId.

  useEffect(() => {
    if (selectedId === compositeRef.current.activeId) return;
    composite.setActiveId(selectedId);
  }, [selectedId, composite.setActiveId]); // Automatically set selectedId if it's undefined.

  useEffect(() => {
    if (selectedId !== undefined) return; // First, we try to set selectedId based on the current active tab.

    const activeId = compositeRef.current.activeId;
    const tab = findEnabledTabById(composite.items, activeId);

    if (tab) {
      setSelectedId(activeId);
    } // If there's no active tab or the active tab is dimmed, we get the first
    // enabled tab instead.
    else {
      const firstEnabledTab = findFirstEnabledTab(composite.items);
      setSelectedId(firstEnabledTab == null ? void 0 : firstEnabledTab.id);
    }
  }, [selectedId, composite.items, setSelectedId]); // Keep panels tabIds in sync with the current tabs.

  useEffect(() => {
    if (!composite.items.length) return;
    panels.setItems(prevPanels => {
      const hasOrphanPanels = prevPanels.some(panel => !panel.tabId);
      if (!hasOrphanPanels) return prevPanels;
      return prevPanels.map((panel, i) => {
        if (panel.tabId) return panel;
        const tab = composite.items[i];
        return { ...panel,
          tabId: tab == null ? void 0 : tab.id
        };
      });
    });
  }, [composite.items, panels.setItems]);
  const select = useCallback(id => {
    composite.move(id);
    setSelectedId(id);
  }, [composite.move, setSelectedId]);
  const state = useMemo(() => ({ ...composite,
    selectedId,
    setSelectedId,
    select,
    panels
  }), [composite, selectedId, setSelectedId, select, panels]);
  return useStorePublisher(state);
}

function getPanelId(panels, id) {
  var _panels$items$find;

  if (!id) return;
  return panels == null ? void 0 : (_panels$items$find = panels.items.find(panel => panel.tabId === id)) == null ? void 0 : _panels$items$find.id;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a tab element. The underlying element must be
 * wrapped in a `TabList` component or a component that implements the
 * `useTabList` props.
 * @see https://ariakit.org/components/tab
 * @example
 * ```jsx
 * const state = useTabState();
 * const props = useTab({ state });
 * <TabList state={state}>
 *   <Role {...props}>Tab 1</Role>
 * </TabList>
 * <TabPanel state={state}>Panel 1</TabPanel>
 * ```
 */


const useTab = createHook(_ref => {
  var _state2, _state3;

  let {
    state,
    accessibleWhenDisabled = true,
    getItem: getItemProp,
    ...props
  } = _ref;
  // Keep a reference to the default id so we can wait before all tabs have
  // been assigned an id before registering them in the state. See
  // https://github.com/ariakit/ariakit/issues/1721
  const defaultId = useId();
  const id = props.id || defaultId;
  state = useStore(state || TabContext, [useCallback(s => id && s.selectedId === id, [id]), "panels", "setSelectedId"]);
  const dimmed = props.disabled;
  const getItem = useCallback(item => {
    const nextItem = { ...item,
      dimmed
    };

    if (getItemProp) {
      return getItemProp(nextItem);
    }

    return nextItem;
  }, [dimmed, getItemProp]);
  const onClickProp = props.onClick;
  const onClick = useEvent$1(event => {
    var _state;

    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    (_state = state) == null ? void 0 : _state.setSelectedId(id);
  });
  const panelId = getPanelId((_state2 = state) == null ? void 0 : _state2.panels, id);
  props = {
    id,
    role: "tab",
    "aria-selected": !!id && ((_state3 = state) == null ? void 0 : _state3.selectedId) === id,
    "aria-controls": panelId || undefined,
    ...props,
    onClick
  };
  props = useCompositeItem({
    state,
    ...props,
    accessibleWhenDisabled,
    getItem,
    shouldRegisterItem: !!defaultId ? props.shouldRegisterItem : false
  });
  return props;
});
/**
 * A component that renders a tab element. The underlying element must be
 * wrapped in a `TabList` component.
 * @see https://ariakit.org/components/tab
 * @example
 * ```jsx
 * const tab = useTabState();
 * <TabList state={tab}>
 *   <Tab>Tab 1</Tab>
 *   <Tab>Tab 2</Tab>
 * </TabList>
 * <TabPanel state={tab}>Panel 1</TabPanel>
 * <TabPanel state={tab}>Panel 2</TabPanel>
 * ```
 */

const Tab = createMemoComponent(props => {
  const htmlProps = useTab(props);
  return createElement("button", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Tab.displayName = "Tab";
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element that will be labelled or described by
 * a `Tooltip` component. This component will also be used as the reference to
 * position the tooltip on the screen.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const state = useToolTipState();
 * const props = useTooltipAnchor({ state });
 * <Role {...props}>Anchor</Role>
 * <Tooltip state={state}>Tooltip</Tooltip>
 * ```
 */
const useTooltipAnchor = createHook(_ref => {
  var _state$contentElement, _state$contentElement2;

  let {
    state,
    described,
    ...props
  } = _ref;
  const onFocusProp = props.onFocus;
  const onFocus = useEvent$1(event => {
    onFocusProp == null ? void 0 : onFocusProp(event);
    if (event.defaultPrevented) return; // When using multiple anchors for the same tooltip, we need to re-render
    // the tooltip to update its position.

    if (state.anchorRef.current !== event.currentTarget) {
      state.anchorRef.current = event.currentTarget;
      state.render();
    }

    state.show();
  });
  const onBlurProp = props.onBlur;
  const onBlur = useEvent$1(event => {
    onBlurProp == null ? void 0 : onBlurProp(event);
    if (event.defaultPrevented) return;
    state.hide();
  });
  const onMouseEnterProp = props.onMouseEnter;
  const onMouseEnter = useEvent$1(event => {
    onMouseEnterProp == null ? void 0 : onMouseEnterProp(event);
    if (event.defaultPrevented) return; // When using multiple anchors for the same tooltip, we need to re-render
    // the tooltip to update its position.

    if (state.anchorRef.current !== event.currentTarget) {
      state.anchorRef.current = event.currentTarget;
      state.render();
    }

    state.show();
  });
  const onMouseLeaveProp = props.onMouseLeave;
  const onMouseLeave = useEvent$1(event => {
    onMouseLeaveProp == null ? void 0 : onMouseLeaveProp(event);
    if (event.defaultPrevented) return;
    state.hide();
  });
  props = {
    tabIndex: 0,
    "aria-labelledby": !described ? (_state$contentElement = state.contentElement) == null ? void 0 : _state$contentElement.id : undefined,
    "aria-describedby": described ? (_state$contentElement2 = state.contentElement) == null ? void 0 : _state$contentElement2.id : undefined,
    ...props,
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave
  };
  props = usePopoverAnchor({
    state,
    ...props
  });
  return props;
});
/**
 * A component that renders an element that will be labelled or described by
 * a `Tooltip` component. This component will also be used as the reference to
 * position the tooltip on the screen.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const tooltip = useTooltipState();
 * <TooltipAnchor state={tooltip}>Anchor</TooltipAnchor>
 * <Tooltip state={tooltip}>Tooltip</Tooltip>
 * ```
 */

const TooltipAnchor = createComponent(props => {
  const htmlProps = useTooltipAnchor(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  TooltipAnchor.displayName = "TooltipAnchor";
}

function createGlobalTooltipState() {
  return {
    activeRef: null,
    listeners: new Set(),

    subscribe(listener) {
      this.listeners.add(listener);
      return () => {
        this.listeners.delete(listener);
      };
    },

    show(ref) {
      this.activeRef = ref;
      this.listeners.forEach(listener => listener(ref));
    },

    hide(ref) {
      if (this.activeRef === ref) {
        this.activeRef = null;
        this.listeners.forEach(listener => listener(null));
      }
    }

  };
}
const TooltipContext = /*#__PURE__*/createContext(undefined);

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an arrow inside a tooltip element.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const state = useToolTipState();
 * const props = useTooltipArrow({ state });
 * <TooltipAnchor state={state}>Anchor</TooltipAnchor>
 * <Tooltip state={state}>
 *   <Role {...props} />
 *   Tooltip
 * </Tooltip>
 * ```
 */
const useTooltipArrow = createHook(_ref => {
  let {
    state,
    size = 16,
    ...props
  } = _ref;
  // We need to get the tooltip state here because Tooltip is not using the
  // Popover component, so PopoverArrow can't access the popover context.
  const context = useContext(TooltipContext);
  state = state || context;
  props = usePopoverArrow({
    state,
    size,
    ...props
  });
  return props;
});
/**
 * A component that renders an arrow inside a `Tooltip` component.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const tooltip = useTooltipState();
 * <TooltipAnchor state={tooltip}>Anchor</TooltipAnchor>
 * <Tooltip state={tooltip}>
 *   <TooltipArrow />
 *   Tooltip
 * </Tooltip>
 * ```
 */

const TooltipArrow = createComponent(props => {
  const htmlProps = useTooltipArrow(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  TooltipArrow.displayName = "TooltipArrow";
}

const globalState = createGlobalTooltipState();
/**
 * Provides state for the `Tooltip` components.
 * @example
 * ```jsx
 * const tooltip = useTooltipState();
 * <TooltipAnchor state={tooltip}>Anchor</TooltipAnchor>
 * <Tooltip state={tooltip}>Tooltip</Tooltip>
 * ```
 */

function useTooltipState(_temp) {
  var _props$defaultOpen, _props$defaultOpen2, _props$open;

  let {
    placement = "top",
    timeout = 0,
    gutter = 8,
    ...props
  } = _temp === void 0 ? {} : _temp;
  const ref = useRef();
  const showTimeout = useRef();
  const hideTimeout = useRef();
  const clearTimeouts = useCallback(() => {
    window.clearTimeout(showTimeout.current);
    window.clearTimeout(hideTimeout.current);
  }, []);
  const [_open, __setOpen] = useState((_props$defaultOpen = props.defaultOpen) != null ? _props$defaultOpen : false);

  const _setOpen = nextOpen => {
    props.setOpen == null ? void 0 : props.setOpen(nextOpen);

    if (props.open === undefined) {
      __setOpen(nextOpen);
    }
  };

  const [open, setOpen] = useControlledState((_props$defaultOpen2 = props.defaultOpen) != null ? _props$defaultOpen2 : false, (_props$open = props.open) != null ? _props$open : _open, nextOpen => {
    clearTimeouts();

    if (nextOpen) {
      if (!timeout || globalState.activeRef) {
        // If there's no timeout or an open tooltip already, we can show this
        // immediately.
        globalState.show(ref);
      } else {
        // There may be a reference with focus whose tooltip is still not
        // open. In this case, we want to update it before it gets shown.
        globalState.show(null); // Wait for the timeout to show the tooltip.

        showTimeout.current = window.setTimeout(() => {
          globalState.show(ref);

          _setOpen(nextOpen);
        }, timeout);
        return;
      }
    } else {
      // Let's give some time so people can move from a reference to
      // another and still show tooltips immediately.
      hideTimeout.current = window.setTimeout(() => {
        globalState.hide(ref);
      }, timeout);
    }

    _setOpen(nextOpen);
  });
  const popover = usePopoverState({
    placement,
    gutter,
    ...props,
    open,
    setOpen
  });
  useEffect(() => {
    return globalState.subscribe(activeRef => {
      if (activeRef !== ref) {
        clearTimeouts();

        if (popover.open) {
          // Make sure there will be only one tooltip open
          popover.hide();
        }
      }
    });
  }, [clearTimeouts, popover.open, popover.hide]);
  useEffect(() => () => {
    clearTimeouts();
    globalState.hide(ref);
  }, [clearTimeouts]);
  const state = useMemo(() => ({ ...popover,
    timeout
  }), [popover, timeout]);
  return state;
}

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a tooltip element.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const state = useToolTipState();
 * const props = useTooltip({ state });
 * <TooltipAnchor state={state}>Anchor</TooltipAnchor>
 * <Role {...props}>Tooltip</Role>
 * ```
 */
const useTooltip = createHook(_ref => {
  let {
    state,
    portal = true,
    hideOnEscape = true,
    hideOnControl = false,
    wrapperProps,
    ...props
  } = _ref;
  const popoverRef = state.popoverRef; // Makes sure the wrapper element that's passed to popper has the same
  // z-index as the popover element so users only need to set the z-index
  // once.

  useSafeLayoutEffect(() => {
    const wrapper = popoverRef.current;
    const tooltip = state.contentElement;
    if (!wrapper) return;
    if (!tooltip) return;
    wrapper.style.zIndex = getComputedStyle(tooltip).zIndex;
  }, [popoverRef, state.contentElement]);
  const hideOnEscapeProp = useBooleanEvent(hideOnEscape);
  const hideOnControlProp = useBooleanEvent(hideOnControl); // Hide on Escape/Control

  useEffect(() => {
    if (!state.open) return;
    return addGlobalEventListener("keydown", event => {
      if (event.defaultPrevented) return;
      const isEscape = event.key === "Escape" && hideOnEscapeProp(event);
      const isControl = event.key === "Control" && hideOnControlProp(event);

      if (isEscape || isControl) {
        state.hide();
      }
    });
  }, [state.open, hideOnEscapeProp, hideOnControlProp, state.hide]);
  props = useWrapElement(props, element => /*#__PURE__*/jsx("div", {
    role: "presentation",
    ...wrapperProps,
    style: {
      position: state.fixed ? "fixed" : "absolute",
      top: 0,
      left: 0,
      ...(wrapperProps == null ? void 0 : wrapperProps.style)
    },
    ref: popoverRef,
    children: element
  }), [state.fixed, popoverRef, wrapperProps]);
  props = useWrapElement(props, element => /*#__PURE__*/jsx(TooltipContext.Provider, {
    value: state,
    children: element
  }), [state]);
  props = {
    role: "tooltip",
    ...props
  };
  props = useDisclosureContent({
    state,
    ...props
  });
  props = usePortal({
    portal,
    ...props,
    preserveTabOrder: false
  });
  return props;
});
/**
 * A component that renders a tooltip element.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const tooltip = useTooltipState();
 * <TooltipAnchor state={tooltip}>Anchor</TooltipAnchor>
 * <Tooltip state={tooltip}>Tooltip</Tooltip>
 * ```
 */

const Tooltip$1 = createComponent(props => {
  const htmlProps = useTooltip(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  Tooltip$1.displayName = "Tooltip";
}

const CARD_ROUNDED_VARIANTS = {
    default: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
};
const StyledCard = _default$1(Role) `
  ${({ $roundedVariant }) => CARD_ROUNDED_VARIANTS[$roundedVariant]}
  ${({ $shadow }) => ($shadow ? 'shadow' : '')}
  ${({ $shadow }) => ($shadow ? 'border border-gray-200' : '')}
`;

const Card = ({ border = true, children, roundedVariant = 'default', shadow = true, ...props }) => (React__default.createElement(StyledCard, { "$roundedVariant": roundedVariant, "$shadow": shadow, "$border": border, ...props }, children));
const CardMemo = memo(Card);

const CheckboxAtom = _default$1(Checkbox$1) `
    focus:ring-primary-500
    h-4
    w-4
    text-primary-600
    border-gray-300
    rounded
    disabled:cursor-not-allowed
    disabled:bg-gray-100
    disabled:border-gray-200
`;

const GRID_TO_COLUMN_STYLE_MAP = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
    8: 'grid-cols-8',
    9: 'grid-cols-9',
    10: 'grid-cols-10',
    11: 'grid-cols-11',
    12: 'grid-cols-12',
};
const StyledGrid = _default$1.div `
  grid
  ${({ $cols }) => GRID_TO_COLUMN_STYLE_MAP[$cols]}
  ${({ $gap }) => GAP_XY[$gap]}
`;
const SIZE_SPAN_STYLE_MAP = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
    6: 'col-span-6',
    7: 'col-span-7',
    8: 'col-span-8',
    9: 'col-span-9',
    10: 'col-span-10',
    11: 'col-span-11',
    12: 'col-span-12',
};
const StyledGridItem = _default$1.div `
  ${({ $size }) => ($size ? SIZE_SPAN_STYLE_MAP[$size] : '')}
`;

const Grid$1 = ({ children, className, cols, gap }) => (React__default.createElement(StyledGrid, { "$cols": cols, "$gap": gap, className: className }, children));
memo(Grid$1);

const GridItem = ({ children, className, size }) => (React__default.createElement(StyledGridItem, { "$size": size, className: className }, children));

const IconSizesStyle = {
    2: 'h-2 w-2',
    3: 'h-3 w-3',
    4: 'h-4 w-4',
    [4.5]: 'h-[1.125rem] w-[1.125rem]',
    5: 'h-5 w-5',
    6: 'h-6 w-6',
    8: 'h-8 w-8',
    9: 'h-9 w-9',
    10: 'h-10 w-10',
    12: 'h-12 w-12',
    14: 'h-14 w-14',
    16: 'h-16 w-16',
};
const ICON_COLOR_VARIANTS = {
    default: '',
    primary: 'border-2 border-primary-600',
    primaryFilled: 'border-2 border-primary-600 bg-primary-600',
    secondary: 'border-2 border-blue-600',
    secondaryFilled: 'border-2 border-blue-600 bg-blue-600',
    red: 'border-2 border-rsbry-700',
    redFilled: 'border-2 border-rsbry-700 bg-rsbry-700',
    green: 'border-2 border-green-600',
    greenFilled: 'border-2 border-green-600 bg-green-600',
    gray: 'border-2 border-gray-600',
    grayFilled: 'border-2 border-gray-600 bg-gray-600',
    lightGray: 'border-2 border-gray-500',
    lightGrayFilled: 'border-2 border-gray-500 bg-gray-500',
    info: 'text-blue-600',
    success: 'text-green-300',
    warning: 'text-lemon-400',
    error: 'text-rsbry-600',
    none: 'text-gray-400',
};
const IconVariantContainerStyles = {
    none: '',
    circular: 'rounded-full',
    rounded: 'rounded-md',
};
const StyledIconContainer = _default$1.div `
  ${({ $colorVariant }) => ICON_COLOR_VARIANTS[$colorVariant]}
  ${({ $size }) => IconSizesStyle[$size]}
  ${({ $variant }) => IconVariantContainerStyles[$variant]}
  flex
  items-center
  justify-center
`;
const StyledIcon$3 = _default$1.div `
  ${({ $size }) => IconSizesStyle[$size]}
  flex
  [&>*]:grow
  items-center
  justify-center
`;

const Icon = ({ className, colorVariant = 'default', node, size, nodeSize = size, variant = 'none', testId, }) => (React__default.createElement(StyledIconContainer, { "$size": size, "$variant": variant, "$colorVariant": colorVariant, className: className, "data-testid": testId },
    React__default.createElement(StyledIcon$3, { "$size": nodeSize }, node)));
const IconMemo = memo(Icon);

const getSpinnerType = (variant) => {
    switch (variant) {
        case 'secondary':
            return 'border-gray-300 border-t-primary-700';
        case 'white':
            return 'border-gray-700 border-t-white';
        default:
            // primary
            return 'border-gray-300 border-t-primary-600';
    }
};
const getSpinnerSize = (size) => {
    switch (size) {
        case 'xs':
            return 'h-2 w-2';
        case 'sm':
            return 'h-4 w-4';
        case 'base':
            return 'h-6 w-6';
        case 'lg':
            return 'h-8 w-8';
        case 'xl':
            return 'h-12 w-12';
        default:
            return 'h-6 w-6';
    }
};
const StyledSpinner = _default$1.div `
  inline-block
  animate-spin
  rounded-full
  border-2
  ${({ $variant }) => getSpinnerType($variant)}
  ${({ $size }) => getSpinnerSize($size)}
`;

const Spinner = ({ className, size = 'base', variant = 'primary' }) => (React__default.createElement(StyledSpinner, { role: "status", "$size": size, "$variant": variant, className: className },
    React__default.createElement("span", { className: "sr-only" }, "Loading...")));

// Wraps Spinner in a centered container to preserve the LoadingProps API.
// Replaces the previous Lottie animation to eliminate unsafe-eval in CSP.
const Loading = ({ height }) => (React__default.createElement("div", { style: { height, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
    React__default.createElement(Spinner, { size: height >= 100 ? 'xl' : height >= 50 ? 'lg' : 'base' })));

const ProgressContainer = _default$1.div `
  bg-gray-300
  rounded-lg
  h-2.5
  w-full
  flex
`;
const ProgressBarSpan = _default$1.span `
  bg-blue-600
  rounded-lg
  h-2.5
  w-0
`;

const ProgressBar = ({ className, maxPercentage = 100 }) => {
    const [percentage, setPercentage] = useState(0);
    useEffect(() => {
        setPercentage(maxPercentage);
    }, [maxPercentage]);
    return (React__default.createElement(ProgressContainer, { className: className },
        React__default.createElement(ProgressBarSpan, { style: { width: `${percentage}%`, transition: 'width 30s' } })));
};

const ErrorText = _default$1.p `
  mb-0
  text-sm
  text-rsbry-600
  ${({ $centered }) => ($centered ? 'text-center' : '')}
`;

const GeneralFormError = ({ centered, children, className, error, id, }) => error ? (React__default.createElement(ErrorText, { id: id, "$centered": centered, className: className, role: "alert" }, children)) : null;
const GeneralFormErrorMemo = memo(GeneralFormError);

var _path$l;
function _extends$w() { return _extends$w = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$w.apply(null, arguments); }
var SvgArrowDown1 = function SvgArrowDown1(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$w({
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    x: "0px",
    y: "0px",
    viewBox: "0 0 24 24",
    xmlSpace: "preserve",
    enableBackground: "new 0 0 24 24",
    ref: ref
  }, props), _path$l || (_path$l = /*#__PURE__*/React.createElement("path", {
    d: "M12,19.5c-0.7,0-1.3-0.3-1.7-0.8L0.4,7.6C-0.2,6.8-0.1,5.6,0.7,5c0.8-0.6,1.9-0.6,2.5,0.2l8.6,9.8 c0.1,0.1,0.2,0.1,0.4,0c0,0,0,0,0,0l8.6-9.8c0.7-0.8,1.8-0.9,2.6-0.2s0.9,1.8,0.2,2.6c0,0,0,0-0.1,0.1l-9.8,11.1 C13.3,19.2,12.6,19.5,12,19.5z",
    fill: "currentColor"
  })));
};
var ForwardRef$s = /*#__PURE__*/forwardRef(SvgArrowDown1);
var Memo$s = /*#__PURE__*/memo(ForwardRef$s);

var _path$k;
function _extends$v() { return _extends$v = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$v.apply(null, arguments); }
var SvgCheck = function SvgCheck(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$v({
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$k || (_path$k = /*#__PURE__*/React.createElement("path", {
    d: "M5 13L9 17L19 7",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};
var ForwardRef$r = /*#__PURE__*/forwardRef(SvgCheck);
var Memo$r = /*#__PURE__*/memo(ForwardRef$r);

const StyledSelectContainer = _default$1.div `
  relative
  [&>div:nth-child(3)]:w-full
  [&>div:nth-child(3)]:!translate-y-[42px]
`;
const StyledSelect = _default$1(Select$1) `
  block
  w-full
  text-left
  rounded-md
  border
  border-gray-300
  py-2
  pl-3
  pr-10
  text-base
  focus:border-primary-500
  focus:outline-none
  focus:ring-primary-500
  sm:text-sm
  ${({ $isPlaceholder }) => ($isPlaceholder ? 'text-gray-400' : 'text-gray-800')}
`;
const StyledArrowSpan = _default$1.span `
  pointer-events-none
  absolute
  inset-y-0
  right-0
  flex
  items-center
  pr-2
  w-auto
  h-auto
`;
const StyledSelectPopover = _default$1(SelectPopover) `
  absolute
  z-modal
  max-h-60
  w-full
  overflow-auto
  rounded-md
  bg-white
  py-1
  text-base
  shadow-lg
  ring-1
  ring-black
  ring-opacity-5
  focus:outline-none
  sm:text-sm
`;
const StyledSelectItem = _default$1(SelectItem) `
  relative
  cursor-default
  select-none
  py-2
  pl-3
  pr-9
  text-gray-900
  [&[data-active-item]]:bg-primary-600
  [&[data-active-item]]:text-white
  [&[aria-disabled]]:opacity-50
  transition-colors
  ${({ $active }) => ($active ? 'font-medium' : '')}
`;
const StyledCheckSpan = _default$1.span `
  absolute
  inset-y-0
  right-0
  flex
  items-center
  pr-4
  text-primary-600
`;
const StyledSelectSectionLabel = _default$1.div `
  px-2 
  py-0.5 
  bg-gray-200 
  text-xs 
  font-medium 
  text-gray-800
`;

const isSelectSection = (option) => 'options' in option && Array.isArray(option.options);
const isSectionedOptions = (options) => options.length > 0 && isSelectSection(options[0]);
const getFlatOptions = (options) => {
    if (!options.length)
        return [];
    if (isSectionedOptions(options)) {
        return options.flatMap((section) => section.options);
    }
    return options;
};
const getLabel = ({ currentValue, options, placeholder, }) => {
    const flatOptions = getFlatOptions(options);
    const selectedOption = flatOptions.find((option) => option.value === currentValue);
    return selectedOption?.label || placeholder;
};

const getSelectPopoverId = (id) => `popover-${id}`;
const Select = ({ className, disabled, error, errorText, id, onChange, options, placeholder, usePortal = false, value, }) => {
    const selectState = useSelectState({
        setValue: onChange,
        value: value,
        defaultValue: placeholder,
        sameWidth: true,
    });
    const renderSelectItem = (option, value, selectState) => (React__default.createElement(StyledSelectItem, { key: option.value, value: option.value, "$active": option.value === value, disabled: option.disabled },
        option.label ? option.label : option.value,
        option.value === selectState.value && (React__default.createElement(StyledCheckSpan, null,
            React__default.createElement(Memo$r, { className: "h-3 w-3" })))));
    const renderOptions = (options) => {
        if (!options.length)
            return null;
        if (isSectionedOptions(options)) {
            return options.map((section, index) => (React__default.createElement(React__default.Fragment, { key: section.label || `section-${index}` },
                section.label && React__default.createElement(StyledSelectSectionLabel, null, section.label),
                section.options.map((option) => renderSelectItem(option, value, selectState)))));
        }
        return options.map((option) => renderSelectItem(option, value, selectState));
    };
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(StyledSelectContainer, { className: className },
            React__default.createElement(StyledSelect, { id: id, state: selectState, "$isPlaceholder": selectState.value === placeholder, onChange: onChange, disabled: disabled },
                getLabel({ options, placeholder, currentValue: selectState.value }),
                React__default.createElement(StyledArrowSpan, null,
                    React__default.createElement(Memo$s, { className: "h-[10px] text-gray-400" }))),
            React__default.createElement(StyledSelectPopover, { portal: usePortal, state: selectState, id: getSelectPopoverId(id) }, renderOptions(options))),
        React__default.createElement(GeneralFormError, { error: !!error && !!errorText, id: `${id}-error` }, errorText)));
};
const SelectMemo = memo(Select);

const useMonthDateOptions = (month) => useMemo(() => getMonthDateOptions(month), [month]);

const alignItems = {
    normal: '',
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
};
const justifyContent = {
    normal: '',
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    'space-between': 'justify-between',
    'space-around': 'justify-around',
    'space-evenly': 'justify-evenly',
};
const flexDirection = {
    row: '',
    column: 'flex-col',
    'row-reverse': 'flex-row-reverse',
};
const flexWrap = {
    nowrap: 'flex-nowrap',
    wrap: 'flex-wrap',
    'wrap-reverse': 'flex-wrap-reverse',
};
const getSpace = (space, direction) => {
    if (space === undefined)
        return '';
    if (typeof space === 'number' && space < 0)
        throw new TypeError('Unexpected negative space');
    return direction === 'row' ? getSpaceX(space) : getSpaceY(space);
};
const getGap = (gap) => {
    if (gap === undefined)
        return '';
    if (typeof gap === 'number' || typeof gap === 'string') {
        return getGapXY(gap);
    }
    if (Array.isArray(gap)) {
        if (gap.length !== 2) {
            throw new TypeError("Stack 'gap' prop is an array so its length MUST equal 2");
        }
        return [getGapX(gap[0]), getGapY(gap[1])].join(' ');
    }
    if (Object.getPrototypeOf(gap) === Object.prototype) {
        if (!Object.prototype.hasOwnProperty.call(gap, 'x') && !Object.prototype.hasOwnProperty.call(gap, 'y')) {
            throw new TypeError("Stack 'gap' prop is an object so it MUST include either an 'x' or 'y' key-value pair or both");
        }
        const gapClasses = [];
        if (gap.x != null) {
            gapClasses.push(getGapX(gap.x));
        }
        if (gap.y != null) {
            gapClasses.push(getGapY(gap.y));
        }
        return gapClasses.join(' ');
    }
    throw new TypeError(`Unexpected input for Stack "gap" prop. Value: ${JSON.stringify(gap)}. Type: ${typeof gap}.`);
};
const StyledStack$4 = _default$1(Role) `
  flex
  ${({ $direction }) => flexDirection[$direction]}
  ${({ $alignItems }) => alignItems[$alignItems]}
  ${({ $justifyContent }) => justifyContent[$justifyContent]}
  ${({ $direction, $space }) => getSpace($space, $direction)}
  ${({ $wrap }) => flexWrap[$wrap]}
  ${({ $gap }) => getGap($gap)}
  ${({ $paddingX }) => getPaddingX($paddingX)}
  ${({ $paddingY }) => getPaddingY($paddingY)}
  ${({ $boxShadow }) => getBoxShadow($boxShadow)}
  ${({ $borderRadius }) => getBorderRadius($borderRadius)}
  ${({ $overflow }) => getOverflow($overflow)}
  ${({ $fullWidth }) => ($fullWidth ? 'w-full' : '')}
  ${({ $fullHeight }) => ($fullHeight ? 'h-full' : '')}
`;

const StackWithForwardRef = ({ alignItems = 'normal', backgroundColor, borderRadius, boxShadow, children, className, color, direction = 'row', fullHeight, fullWidth, gap, justifyContent = 'normal', onMouseEnter, onMouseLeave, overflow, paddingX, paddingY, space, style, testId, wrap, ...restProps }, ref) => (React__default.createElement(StyledStack$4, { ref: ref, "$alignItems": alignItems, "$direction": direction, "$fullHeight": fullHeight, "$fullWidth": fullWidth, "$gap": gap, "$justifyContent": justifyContent, "$space": space, "$wrap": wrap, "$paddingX": paddingX, "$paddingY": paddingY, "$boxShadow": boxShadow, "$borderRadius": borderRadius, "$overflow": overflow, className: className, "data-testid": testId, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, style: {
        color,
        backgroundColor,
        ...style,
    }, ...restProps }, children));
// React's forwardRef render-fn type (ForwardRefRenderFunction) is non-generic, so this
// polymorphic <T extends ElementType> render function cannot satisfy it without a cast.
// This is inherent to forwardRef on React 18 and is not a workaround that can be cleaned up
// in place. It is removed when we upgrade to React 19, where `ref` is a plain prop and
// forwardRef is no longer needed. Runtime behavior is unchanged.
const Stack$5 = React__default.forwardRef(StackWithForwardRef);
const StackMemo = memo(Stack$5);

const FONT_SIZE = {
    inherit: '',
    '2xs': 'text-[0.5rem]',
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
    '5xl': 'text-5xl',
    '6xl': 'text-6xl',
    '7xl': 'text-7xl',
    '8xl': 'text-8xl',
    '9xl': 'text-9xl',
};
const FONT_WEIGHT = {
    inherit: '',
    thin: 'font-thin',
    extralight: 'font-extralight',
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    extrabold: 'font-extrabold',
    black: 'font-black',
};
const FONT_FAMILY = {
    inherit: '',
    caveat: 'font-caveat',
    sans: 'font-sans',
    serif: 'font-serif',
};
const LINE_HEIGHT = {
    inherit: '',
    3: 'leading-3',
    4: 'leading-4',
    5: 'leading-5',
    6: 'leading-6',
    7: 'leading-7',
    8: 'leading-8',
    9: 'leading-9',
    10: 'leading-10',
    none: 'leading-none',
    tight: 'leading-tight',
    snug: 'leading-snug',
    normal: 'leading-normal',
    relaxed: 'leading-relaxed',
    loose: 'leading-loose',
};
const TEXT_ALIGN = {
    inherit: '',
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justify: 'text-justify',
    start: 'text-start',
    end: 'text-end',
};
const StyledTypography$3 = _default$1.span `
  text-gray-900
  ${({ $fontFamily }) => FONT_FAMILY[$fontFamily]}
  ${({ $fontSize }) => FONT_SIZE[$fontSize]}
  ${({ $fontWeight }) => FONT_WEIGHT[$fontWeight]}
  ${({ $lineHeight }) => LINE_HEIGHT[$lineHeight]}
  ${({ $overflow }) => getOverflow($overflow)}
  ${({ $textAlign }) => TEXT_ALIGN[$textAlign]}
  ${({ $textOverflow }) => getTextOverflow($textOverflow)}
  ${({ $textTransform }) => getTextTransform($textTransform)}
  ${({ $textDecoration }) => getTextDecoration($textDecoration)}
  ${({ $whiteSpace }) => getWhiteSpace($whiteSpace)}
`;

const Typography = ({ as, children, className, color, fontFamily = 'sans', fontSize = 'base', fontWeight = 'normal', id, lineHeight, overflow, textAlign, textDecoration, textOverflow, textTransform, whiteSpace, ...restProps }) => (React__default.createElement(StyledTypography$3, { "$as": as, "$fontFamily": fontFamily, "$fontSize": fontSize, "$fontWeight": fontWeight, "$lineHeight": lineHeight, "$overflow": overflow, "$textAlign": textAlign, "$textOverflow": textOverflow, "$textTransform": textTransform, "$textDecoration": textDecoration, "$whiteSpace": whiteSpace, className: className, "data-testid": `typography-component-${id ?? ''}`, id: id, style: { color }, ...restProps }, children));
const TypographyMemo = memo(Typography);

function _extends$u() {
  return _extends$u = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends$u.apply(null, arguments);
}

var removeAccentsExports = {};
var removeAccents$1 = {
  get exports(){ return removeAccentsExports; },
  set exports(v){ removeAccentsExports = v; },
};

var characterMap = {
	"À": "A",
	"Á": "A",
	"Â": "A",
	"Ã": "A",
	"Ä": "A",
	"Å": "A",
	"Ấ": "A",
	"Ắ": "A",
	"Ẳ": "A",
	"Ẵ": "A",
	"Ặ": "A",
	"Æ": "AE",
	"Ầ": "A",
	"Ằ": "A",
	"Ȃ": "A",
	"Ç": "C",
	"Ḉ": "C",
	"È": "E",
	"É": "E",
	"Ê": "E",
	"Ë": "E",
	"Ế": "E",
	"Ḗ": "E",
	"Ề": "E",
	"Ḕ": "E",
	"Ḝ": "E",
	"Ȇ": "E",
	"Ì": "I",
	"Í": "I",
	"Î": "I",
	"Ï": "I",
	"Ḯ": "I",
	"Ȋ": "I",
	"Ð": "D",
	"Ñ": "N",
	"Ò": "O",
	"Ó": "O",
	"Ô": "O",
	"Õ": "O",
	"Ö": "O",
	"Ø": "O",
	"Ố": "O",
	"Ṍ": "O",
	"Ṓ": "O",
	"Ȏ": "O",
	"Ù": "U",
	"Ú": "U",
	"Û": "U",
	"Ü": "U",
	"Ý": "Y",
	"à": "a",
	"á": "a",
	"â": "a",
	"ã": "a",
	"ä": "a",
	"å": "a",
	"ấ": "a",
	"ắ": "a",
	"ẳ": "a",
	"ẵ": "a",
	"ặ": "a",
	"æ": "ae",
	"ầ": "a",
	"ằ": "a",
	"ȃ": "a",
	"ç": "c",
	"ḉ": "c",
	"è": "e",
	"é": "e",
	"ê": "e",
	"ë": "e",
	"ế": "e",
	"ḗ": "e",
	"ề": "e",
	"ḕ": "e",
	"ḝ": "e",
	"ȇ": "e",
	"ì": "i",
	"í": "i",
	"î": "i",
	"ï": "i",
	"ḯ": "i",
	"ȋ": "i",
	"ð": "d",
	"ñ": "n",
	"ò": "o",
	"ó": "o",
	"ô": "o",
	"õ": "o",
	"ö": "o",
	"ø": "o",
	"ố": "o",
	"ṍ": "o",
	"ṓ": "o",
	"ȏ": "o",
	"ù": "u",
	"ú": "u",
	"û": "u",
	"ü": "u",
	"ý": "y",
	"ÿ": "y",
	"Ā": "A",
	"ā": "a",
	"Ă": "A",
	"ă": "a",
	"Ą": "A",
	"ą": "a",
	"Ć": "C",
	"ć": "c",
	"Ĉ": "C",
	"ĉ": "c",
	"Ċ": "C",
	"ċ": "c",
	"Č": "C",
	"č": "c",
	"C̆": "C",
	"c̆": "c",
	"Ď": "D",
	"ď": "d",
	"Đ": "D",
	"đ": "d",
	"Ē": "E",
	"ē": "e",
	"Ĕ": "E",
	"ĕ": "e",
	"Ė": "E",
	"ė": "e",
	"Ę": "E",
	"ę": "e",
	"Ě": "E",
	"ě": "e",
	"Ĝ": "G",
	"Ǵ": "G",
	"ĝ": "g",
	"ǵ": "g",
	"Ğ": "G",
	"ğ": "g",
	"Ġ": "G",
	"ġ": "g",
	"Ģ": "G",
	"ģ": "g",
	"Ĥ": "H",
	"ĥ": "h",
	"Ħ": "H",
	"ħ": "h",
	"Ḫ": "H",
	"ḫ": "h",
	"Ĩ": "I",
	"ĩ": "i",
	"Ī": "I",
	"ī": "i",
	"Ĭ": "I",
	"ĭ": "i",
	"Į": "I",
	"į": "i",
	"İ": "I",
	"ı": "i",
	"Ĳ": "IJ",
	"ĳ": "ij",
	"Ĵ": "J",
	"ĵ": "j",
	"Ķ": "K",
	"ķ": "k",
	"Ḱ": "K",
	"ḱ": "k",
	"K̆": "K",
	"k̆": "k",
	"Ĺ": "L",
	"ĺ": "l",
	"Ļ": "L",
	"ļ": "l",
	"Ľ": "L",
	"ľ": "l",
	"Ŀ": "L",
	"ŀ": "l",
	"Ł": "l",
	"ł": "l",
	"Ḿ": "M",
	"ḿ": "m",
	"M̆": "M",
	"m̆": "m",
	"Ń": "N",
	"ń": "n",
	"Ņ": "N",
	"ņ": "n",
	"Ň": "N",
	"ň": "n",
	"ŉ": "n",
	"N̆": "N",
	"n̆": "n",
	"Ō": "O",
	"ō": "o",
	"Ŏ": "O",
	"ŏ": "o",
	"Ő": "O",
	"ő": "o",
	"Œ": "OE",
	"œ": "oe",
	"P̆": "P",
	"p̆": "p",
	"Ŕ": "R",
	"ŕ": "r",
	"Ŗ": "R",
	"ŗ": "r",
	"Ř": "R",
	"ř": "r",
	"R̆": "R",
	"r̆": "r",
	"Ȓ": "R",
	"ȓ": "r",
	"Ś": "S",
	"ś": "s",
	"Ŝ": "S",
	"ŝ": "s",
	"Ş": "S",
	"Ș": "S",
	"ș": "s",
	"ş": "s",
	"Š": "S",
	"š": "s",
	"Ţ": "T",
	"ţ": "t",
	"ț": "t",
	"Ț": "T",
	"Ť": "T",
	"ť": "t",
	"Ŧ": "T",
	"ŧ": "t",
	"T̆": "T",
	"t̆": "t",
	"Ũ": "U",
	"ũ": "u",
	"Ū": "U",
	"ū": "u",
	"Ŭ": "U",
	"ŭ": "u",
	"Ů": "U",
	"ů": "u",
	"Ű": "U",
	"ű": "u",
	"Ų": "U",
	"ų": "u",
	"Ȗ": "U",
	"ȗ": "u",
	"V̆": "V",
	"v̆": "v",
	"Ŵ": "W",
	"ŵ": "w",
	"Ẃ": "W",
	"ẃ": "w",
	"X̆": "X",
	"x̆": "x",
	"Ŷ": "Y",
	"ŷ": "y",
	"Ÿ": "Y",
	"Y̆": "Y",
	"y̆": "y",
	"Ź": "Z",
	"ź": "z",
	"Ż": "Z",
	"ż": "z",
	"Ž": "Z",
	"ž": "z",
	"ſ": "s",
	"ƒ": "f",
	"Ơ": "O",
	"ơ": "o",
	"Ư": "U",
	"ư": "u",
	"Ǎ": "A",
	"ǎ": "a",
	"Ǐ": "I",
	"ǐ": "i",
	"Ǒ": "O",
	"ǒ": "o",
	"Ǔ": "U",
	"ǔ": "u",
	"Ǖ": "U",
	"ǖ": "u",
	"Ǘ": "U",
	"ǘ": "u",
	"Ǚ": "U",
	"ǚ": "u",
	"Ǜ": "U",
	"ǜ": "u",
	"Ứ": "U",
	"ứ": "u",
	"Ṹ": "U",
	"ṹ": "u",
	"Ǻ": "A",
	"ǻ": "a",
	"Ǽ": "AE",
	"ǽ": "ae",
	"Ǿ": "O",
	"ǿ": "o",
	"Þ": "TH",
	"þ": "th",
	"Ṕ": "P",
	"ṕ": "p",
	"Ṥ": "S",
	"ṥ": "s",
	"X́": "X",
	"x́": "x",
	"Ѓ": "Г",
	"ѓ": "г",
	"Ќ": "К",
	"ќ": "к",
	"A̋": "A",
	"a̋": "a",
	"E̋": "E",
	"e̋": "e",
	"I̋": "I",
	"i̋": "i",
	"Ǹ": "N",
	"ǹ": "n",
	"Ồ": "O",
	"ồ": "o",
	"Ṑ": "O",
	"ṑ": "o",
	"Ừ": "U",
	"ừ": "u",
	"Ẁ": "W",
	"ẁ": "w",
	"Ỳ": "Y",
	"ỳ": "y",
	"Ȁ": "A",
	"ȁ": "a",
	"Ȅ": "E",
	"ȅ": "e",
	"Ȉ": "I",
	"ȉ": "i",
	"Ȍ": "O",
	"ȍ": "o",
	"Ȑ": "R",
	"ȑ": "r",
	"Ȕ": "U",
	"ȕ": "u",
	"B̌": "B",
	"b̌": "b",
	"Č̣": "C",
	"č̣": "c",
	"Ê̌": "E",
	"ê̌": "e",
	"F̌": "F",
	"f̌": "f",
	"Ǧ": "G",
	"ǧ": "g",
	"Ȟ": "H",
	"ȟ": "h",
	"J̌": "J",
	"ǰ": "j",
	"Ǩ": "K",
	"ǩ": "k",
	"M̌": "M",
	"m̌": "m",
	"P̌": "P",
	"p̌": "p",
	"Q̌": "Q",
	"q̌": "q",
	"Ř̩": "R",
	"ř̩": "r",
	"Ṧ": "S",
	"ṧ": "s",
	"V̌": "V",
	"v̌": "v",
	"W̌": "W",
	"w̌": "w",
	"X̌": "X",
	"x̌": "x",
	"Y̌": "Y",
	"y̌": "y",
	"A̧": "A",
	"a̧": "a",
	"B̧": "B",
	"b̧": "b",
	"Ḑ": "D",
	"ḑ": "d",
	"Ȩ": "E",
	"ȩ": "e",
	"Ɛ̧": "E",
	"ɛ̧": "e",
	"Ḩ": "H",
	"ḩ": "h",
	"I̧": "I",
	"i̧": "i",
	"Ɨ̧": "I",
	"ɨ̧": "i",
	"M̧": "M",
	"m̧": "m",
	"O̧": "O",
	"o̧": "o",
	"Q̧": "Q",
	"q̧": "q",
	"U̧": "U",
	"u̧": "u",
	"X̧": "X",
	"x̧": "x",
	"Z̧": "Z",
	"z̧": "z",
};

var chars = Object.keys(characterMap).join('|');
var allAccents = new RegExp(chars, 'g');
var firstAccent = new RegExp(chars, '');

var removeAccents = function(string) {	
	return string.replace(allAccents, function(match) {
		return characterMap[match];
	});
};

var hasAccents = function(string) {
	return !!string.match(firstAccent);
};

removeAccents$1.exports = removeAccents;
removeAccentsExports.has = hasAccents;
removeAccentsExports.remove = removeAccents;

var rankings = {
  CASE_SENSITIVE_EQUAL: 7,
  EQUAL: 6,
  STARTS_WITH: 5,
  WORD_STARTS_WITH: 4,
  CONTAINS: 3,
  ACRONYM: 2,
  MATCHES: 1,
  NO_MATCH: 0
};
matchSorter.rankings = rankings;

var defaultBaseSortFn = function defaultBaseSortFn(a, b) {
  return String(a.rankedValue).localeCompare(String(b.rankedValue));
};
/**
 * Takes an array of items and a value and returns a new array with the items that match the given value
 * @param {Array} items - the items to sort
 * @param {String} value - the value to use for ranking
 * @param {Object} options - Some options to configure the sorter
 * @return {Array} - the new sorted array
 */


function matchSorter(items, value, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      keys = _options.keys,
      _options$threshold = _options.threshold,
      threshold = _options$threshold === void 0 ? rankings.MATCHES : _options$threshold,
      _options$baseSort = _options.baseSort,
      baseSort = _options$baseSort === void 0 ? defaultBaseSortFn : _options$baseSort,
      _options$sorter = _options.sorter,
      sorter = _options$sorter === void 0 ? function (matchedItems) {
    return matchedItems.sort(function (a, b) {
      return sortRankedValues(a, b, baseSort);
    });
  } : _options$sorter;
  var matchedItems = items.reduce(reduceItemsToRanked, []);
  return sorter(matchedItems).map(function (_ref) {
    var item = _ref.item;
    return item;
  });

  function reduceItemsToRanked(matches, item, index) {
    var rankingInfo = getHighestRanking(item, keys, value, options);
    var rank = rankingInfo.rank,
        _rankingInfo$keyThres = rankingInfo.keyThreshold,
        keyThreshold = _rankingInfo$keyThres === void 0 ? threshold : _rankingInfo$keyThres;

    if (rank >= keyThreshold) {
      matches.push(_extends$u({}, rankingInfo, {
        item: item,
        index: index
      }));
    }

    return matches;
  }
}
/**
 * Gets the highest ranking for value for the given item based on its values for the given keys
 * @param {*} item - the item to rank
 * @param {Array} keys - the keys to get values from the item for the ranking
 * @param {String} value - the value to rank against
 * @param {Object} options - options to control the ranking
 * @return {{rank: Number, keyIndex: Number, keyThreshold: Number}} - the highest ranking
 */


function getHighestRanking(item, keys, value, options) {
  if (!keys) {
    // if keys is not specified, then we assume the item given is ready to be matched
    var stringItem = item;
    return {
      // ends up being duplicate of 'item' in matches but consistent
      rankedValue: stringItem,
      rank: getMatchRanking(stringItem, value, options),
      keyIndex: -1,
      keyThreshold: options.threshold
    };
  }

  var valuesToRank = getAllValuesToRank(item, keys);
  return valuesToRank.reduce(function (_ref2, _ref3, i) {
    var rank = _ref2.rank,
        rankedValue = _ref2.rankedValue,
        keyIndex = _ref2.keyIndex,
        keyThreshold = _ref2.keyThreshold;
    var itemValue = _ref3.itemValue,
        attributes = _ref3.attributes;
    var newRank = getMatchRanking(itemValue, value, options);
    var newRankedValue = rankedValue;
    var minRanking = attributes.minRanking,
        maxRanking = attributes.maxRanking,
        threshold = attributes.threshold;

    if (newRank < minRanking && newRank >= rankings.MATCHES) {
      newRank = minRanking;
    } else if (newRank > maxRanking) {
      newRank = maxRanking;
    }

    if (newRank > rank) {
      rank = newRank;
      keyIndex = i;
      keyThreshold = threshold;
      newRankedValue = itemValue;
    }

    return {
      rankedValue: newRankedValue,
      rank: rank,
      keyIndex: keyIndex,
      keyThreshold: keyThreshold
    };
  }, {
    rankedValue: item,
    rank: rankings.NO_MATCH,
    keyIndex: -1,
    keyThreshold: options.threshold
  });
}
/**
 * Gives a rankings score based on how well the two strings match.
 * @param {String} testString - the string to test against
 * @param {String} stringToRank - the string to rank
 * @param {Object} options - options for the match (like keepDiacritics for comparison)
 * @returns {Number} the ranking for how well stringToRank matches testString
 */


function getMatchRanking(testString, stringToRank, options) {
  testString = prepareValueForComparison(testString, options);
  stringToRank = prepareValueForComparison(stringToRank, options); // too long

  if (stringToRank.length > testString.length) {
    return rankings.NO_MATCH;
  } // case sensitive equals


  if (testString === stringToRank) {
    return rankings.CASE_SENSITIVE_EQUAL;
  } // Lower casing before further comparison


  testString = testString.toLowerCase();
  stringToRank = stringToRank.toLowerCase(); // case insensitive equals

  if (testString === stringToRank) {
    return rankings.EQUAL;
  } // starts with


  if (testString.startsWith(stringToRank)) {
    return rankings.STARTS_WITH;
  } // word starts with


  if (testString.includes(" " + stringToRank)) {
    return rankings.WORD_STARTS_WITH;
  } // contains


  if (testString.includes(stringToRank)) {
    return rankings.CONTAINS;
  } else if (stringToRank.length === 1) {
    // If the only character in the given stringToRank
    //   isn't even contained in the testString, then
    //   it's definitely not a match.
    return rankings.NO_MATCH;
  } // acronym


  if (getAcronym(testString).includes(stringToRank)) {
    return rankings.ACRONYM;
  } // will return a number between rankings.MATCHES and
  // rankings.MATCHES + 1 depending  on how close of a match it is.


  return getClosenessRanking(testString, stringToRank);
}
/**
 * Generates an acronym for a string.
 *
 * @param {String} string the string for which to produce the acronym
 * @returns {String} the acronym
 */


function getAcronym(string) {
  var acronym = '';
  var wordsInString = string.split(' ');
  wordsInString.forEach(function (wordInString) {
    var splitByHyphenWords = wordInString.split('-');
    splitByHyphenWords.forEach(function (splitByHyphenWord) {
      acronym += splitByHyphenWord.substr(0, 1);
    });
  });
  return acronym;
}
/**
 * Returns a score based on how spread apart the
 * characters from the stringToRank are within the testString.
 * A number close to rankings.MATCHES represents a loose match. A number close
 * to rankings.MATCHES + 1 represents a tighter match.
 * @param {String} testString - the string to test against
 * @param {String} stringToRank - the string to rank
 * @returns {Number} the number between rankings.MATCHES and
 * rankings.MATCHES + 1 for how well stringToRank matches testString
 */


function getClosenessRanking(testString, stringToRank) {
  var matchingInOrderCharCount = 0;
  var charNumber = 0;

  function findMatchingCharacter(matchChar, string, index) {
    for (var j = index, J = string.length; j < J; j++) {
      var stringChar = string[j];

      if (stringChar === matchChar) {
        matchingInOrderCharCount += 1;
        return j + 1;
      }
    }

    return -1;
  }

  function getRanking(spread) {
    var spreadPercentage = 1 / spread;
    var inOrderPercentage = matchingInOrderCharCount / stringToRank.length;
    var ranking = rankings.MATCHES + inOrderPercentage * spreadPercentage;
    return ranking;
  }

  var firstIndex = findMatchingCharacter(stringToRank[0], testString, 0);

  if (firstIndex < 0) {
    return rankings.NO_MATCH;
  }

  charNumber = firstIndex;

  for (var i = 1, I = stringToRank.length; i < I; i++) {
    var matchChar = stringToRank[i];
    charNumber = findMatchingCharacter(matchChar, testString, charNumber);
    var found = charNumber > -1;

    if (!found) {
      return rankings.NO_MATCH;
    }
  }

  var spread = charNumber - firstIndex;
  return getRanking(spread);
}
/**
 * Sorts items that have a rank, index, and keyIndex
 * @param {Object} a - the first item to sort
 * @param {Object} b - the second item to sort
 * @return {Number} -1 if a should come first, 1 if b should come first, 0 if equal
 */


function sortRankedValues(a, b, baseSort) {
  var aFirst = -1;
  var bFirst = 1;
  var aRank = a.rank,
      aKeyIndex = a.keyIndex;
  var bRank = b.rank,
      bKeyIndex = b.keyIndex;
  var same = aRank === bRank;

  if (same) {
    if (aKeyIndex === bKeyIndex) {
      // use the base sort function as a tie-breaker
      return baseSort(a, b);
    } else {
      return aKeyIndex < bKeyIndex ? aFirst : bFirst;
    }
  } else {
    return aRank > bRank ? aFirst : bFirst;
  }
}
/**
 * Prepares value for comparison by stringifying it, removing diacritics (if specified)
 * @param {String} value - the value to clean
 * @param {Object} options - {keepDiacritics: whether to remove diacritics}
 * @return {String} the prepared value
 */


function prepareValueForComparison(value, _ref4) {
  var keepDiacritics = _ref4.keepDiacritics;
  // value might not actually be a string at this point (we don't get to choose)
  // so part of preparing the value for comparison is ensure that it is a string
  value = "" + value; // toString

  if (!keepDiacritics) {
    value = removeAccentsExports(value);
  }

  return value;
}
/**
 * Gets value for key in item at arbitrarily nested keypath
 * @param {Object} item - the item
 * @param {Object|Function} key - the potentially nested keypath or property callback
 * @return {Array} - an array containing the value(s) at the nested keypath
 */


function getItemValues(item, key) {
  if (typeof key === 'object') {
    key = key.key;
  }

  var value;

  if (typeof key === 'function') {
    value = key(item);
  } else if (item == null) {
    value = null;
  } else if (Object.hasOwnProperty.call(item, key)) {
    value = item[key];
  } else if (key.includes('.')) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    return getNestedValues(key, item);
  } else {
    value = null;
  } // because `value` can also be undefined


  if (value == null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  return [String(value)];
}
/**
 * Given path: "foo.bar.baz"
 * And item: {foo: {bar: {baz: 'buzz'}}}
 *   -> 'buzz'
 * @param path a dot-separated set of keys
 * @param item the item to get the value from
 */


function getNestedValues(path, item) {
  var keys = path.split('.');
  var values = [item];

  for (var i = 0, I = keys.length; i < I; i++) {
    var nestedKey = keys[i];
    var nestedValues = [];

    for (var j = 0, J = values.length; j < J; j++) {
      var nestedItem = values[j];
      if (nestedItem == null) continue;

      if (Object.hasOwnProperty.call(nestedItem, nestedKey)) {
        var nestedValue = nestedItem[nestedKey];

        if (nestedValue != null) {
          nestedValues.push(nestedValue);
        }
      } else if (nestedKey === '*') {
        // ensure that values is an array
        nestedValues = nestedValues.concat(nestedItem);
      }
    }

    values = nestedValues;
  }

  if (Array.isArray(values[0])) {
    // keep allowing the implicit wildcard for an array of strings at the end of
    // the path; don't use `.flat()` because that's not available in node.js v10
    var result = [];
    return result.concat.apply(result, values);
  } // Based on our logic it should be an array of strings by now...
  // assuming the user's path terminated in strings


  return values;
}
/**
 * Gets all the values for the given keys in the given item and returns an array of those values
 * @param item - the item from which the values will be retrieved
 * @param keys - the keys to use to retrieve the values
 * @return objects with {itemValue, attributes}
 */


function getAllValuesToRank(item, keys) {
  var allValues = [];

  for (var j = 0, J = keys.length; j < J; j++) {
    var key = keys[j];
    var attributes = getKeyAttributes(key);
    var itemValues = getItemValues(item, key);

    for (var i = 0, I = itemValues.length; i < I; i++) {
      allValues.push({
        itemValue: itemValues[i],
        attributes: attributes
      });
    }
  }

  return allValues;
}

var defaultKeyAttributes = {
  maxRanking: Infinity,
  minRanking: -Infinity
};
/**
 * Gets all the attributes for the given key
 * @param key - the key from which the attributes will be retrieved
 * @return object containing the key's attributes
 */

function getKeyAttributes(key) {
  if (typeof key === 'string') {
    return defaultKeyAttributes;
  }

  return _extends$u({}, defaultKeyAttributes, key);
}

const Button$1 = _default$1(Ot.Button) `
  absolute
  inset-y-0
  right-3
  px-1
  flex
  items-center
`;
const CheckIcon = _default$1(Memo$r) `
  w-5
  h-5
`;
// This is inconsistent with DropdownButton (it uses Ariakit `MenuButtonArrow`)
const ChevronDownIcon = _default$1(Memo$s) `
  w-2
  text-gray-400
`;
const ComboboxBaseInput = _default$1(Ot.Input) `
  w-full
  bg-white
  text-sm
  font-normal
  placeholder:text-gray-400
  ${({ $error, disabled }) => {
    if (disabled) {
        return 'bg-gray-200 border-primary-600';
    }
    if ($error) {
        return `border-rsbry-300 text-rsbry-900 placeholder:text-rsbry-300
        focus:ring-rsbry-500 focus:border-rsbry-500 animate-shake`;
    }
    return '';
}}
`;
const InputItem = _default$1(ComboboxBaseInput) `
  border-none
  p-0
  overflow-hidden
  focus:ring-0
  focus:ring-offset-0
`;
const Input$2 = _default$1(ComboboxBaseInput) `
  border
  rounded-md
  py-2
  pr-10
  text-gray-900
  font-normal
  text-left
  shadow-sm

  focus:outline-none
  focus:ring-1
  ${({ $hasImage }) => ($hasImage ? 'pl-[3.25rem]' : 'pl-4')}
  ${({ $error, disabled }) => {
    if (disabled) {
        return 'bg-gray-200 border-primary-600';
    }
    if ($error) {
        return `border-rsbry-300 text-rsbry-900 placeholder:text-rsbry-300
        focus:ring-rsbry-500 focus:border-rsbry-500 animate-shake`;
    }
    return 'border-gray-300 focus:border-primary-600 focus:ring-primary-600 placeholder:text-gray-400';
}}
`;
const ListItem = _default$1.li `
  relative
  flex
  flex-row
  items-center
  space-x-3
  py-2
  pl-4
  pr-9
  text-sm
  cursor-default
  select-none
  ${({ $active }) => ($active ? 'bg-primary-600 text-white' : 'text-gray-700')}
  ${({ $isLastOptionInGroup }) => ($isLastOptionInGroup ? 'border-b border-gray-200' : '')}
`;
const ListItemText = _default$1.span `
  block
  truncate
  ${({ $active, $disabled, $selected }) => [$selected ? 'font-semibold' : '', $active ? 'text-white' : $disabled ? 'text-gray-300' : 'text-gray-500'].join(' ')}
`;
const OptionImage = _default$1.img `
  w-6
  h-6
  flex-shrink-0
  rounded-full
  object-cover
`;
const Options = _default$1(Ot.Options) `
  absolute
  z-10
  w-full
  max-h-56
  mt-1
  py-1
  overflow-auto
  rounded-md
  shadow-lg
  bg-white
  text-base
  ring-1
  ring-black
  ring-opacity-5
  focus:outline-none
`;
const SelectedOptionTrailingIconContainer = _default$1.span `
  absolute
  inset-y-0
  right-0
  flex
  items-center
  pr-4
  ${({ $active }) => ($active ? 'text-white' : 'text-inherit')}
`;
const SelectedOptionLeadingIconContainer = _default$1.span `
  absolute
  inset-y-0
  left-4
  flex
  items-center
`;
/**
 * Makes sure the button is appropriately aligned with the input.
 */
const InputWrapper = _default$1.div `
  relative
`;

const makeDisplayValueGetter = (options, optionValueKey, optionDisplayKey) => {
    const getDisplayValue = (optionValue) => {
        if (optionValue === undefined ||
            optionValue === null ||
            optionValueKey === undefined ||
            optionDisplayKey === undefined) {
            return '';
        }
        const option = options.find((option) => option[optionValueKey] === optionValue);
        return option?.[optionDisplayKey] ?? '';
    };
    return getDisplayValue;
};

const NOTIFICATION_COLOR_VARIANTS = {
    red: 'bg-rsbry-600',
    green: 'bg-green-400',
    gray: 'bg-gray-300',
};
const NotificationSizes = {
    0.5: 'h-1 w-1 ring-1 top-0 right-0',
    1: 'h-1.5 w-1.5 ring-2 top-0 right-0',
    1.5: 'h-2 w-2 ring-2 top-0 right-0',
    2: 'h-2 w-2 ring-2 top-0.5 right-0.5',
    2.5: 'h-2.5 w-2.5 ring-2 top-1 right-1',
    3: 'h-3 w-3 ring-2 top-1 right-1',
    3.5: 'h-3.5 w-3.5 ring-2 top-2 right-2',
    4: 'h-4 w-4 ring top-2 right-2',
};
const NOTIFICATION_RING_VARIANTS = {
    gray: 'ring-gray-200 group-hover:ring-gray-400',
    darkGray: '',
    white: 'ring-white',
};
const StyledNotification = _default$1.span `
  ${({ $colorVariant }) => NOTIFICATION_COLOR_VARIANTS[$colorVariant]}
  ${({ $size }) => NotificationSizes[$size]}
  ${({ $ringVariant }) => NOTIFICATION_RING_VARIANTS[$ringVariant]}
  absolute
  block
  -translate-y-1/2
  translate-x-1/2
  transform
  rounded-full
  transition-shadow
`;

const Notification = ({ className, colorVariant, ringVariant = 'white', size, }) => (React__default.createElement(StyledNotification, { className: className, "$ringVariant": ringVariant, "$colorVariant": colorVariant, "$size": size, "data-testid": "notification" }));
memo(Notification);

const StyledIconWithNotification = _default$1.div `
  relative
  inline-block
`;

const IconToNotificationMapping = {
    2: 0.5,
    4: 0.5,
    6: 1,
    8: 2,
    10: 2.5,
    12: 3,
    14: 3.5,
    16: 4,
};
const IconWithNotification = ({ className, isNotificationToggled = true, nodeSize, notification, size, ...props }) => (React__default.createElement(StyledIconWithNotification, { className: className },
    React__default.createElement(Icon, { size: size, nodeSize: nodeSize, ...props }),
    isNotificationToggled && (React__default.createElement(Notification, { size: IconToNotificationMapping[nodeSize ? nodeSize : size], colorVariant: notification.colorVariant, ringVariant: notification.ringVariant }))));
const IconWithNotificationMemo = memo(IconWithNotification);

const IconButtonSizesStyle = {
    xs: 'h-4 w-4',
    sm: 'h-6 w-6',
    base: 'h-8 w-8',
    lg: 'h-10 w-10',
    xl: 'h-12 w-12',
};
const ICON_BUTTON_COLOR_VARIANTS = {
    primary: 'text-primary-400 hover:text-primary-800',
    darkGray: 'bg-gray-800 text-gray-300 hover:bg-gray-200 hover:text-gray-600',
    darkGrayBorder: 'bg-gray-600 text-gray-300 hover:bg-gray-200 hover:text-gray-50',
    gray: 'text-gray-600 hover:bg-gray-400 hover:text-gray-50',
    grayBorder: 'text-gray-400 hover:bg-gray-400 hover:text-gray-50',
    lightGray: 'hover:bg-gray-200',
    transparent: 'text-gray-400 hover:text-gray-500 bg-transparent',
    inherit: 'text-inherit hover:text-inherit bg-transparent',
};
const ICON_BUTTON_ACTIVE_COLOR_VARIANTS = {
    primary: 'bg-transparent text-primary-400',
    darkGray: 'bg-gray-200 text-gray-600',
    darkGrayBorder: 'text-gray-300',
    gray: 'bg-gray-400 text-gray-50',
    grayBorder: 'text-gray-400',
    lightGray: 'bg-gray-100',
    transparent: 'text-gray-400 bg-transparent',
    inherit: 'text-inherit hover:text-inherit bg-transparent',
};
const ICON_BUTTON_ROUNDED_VARIANTS = {
    none: '',
    regular: 'rounded-md',
    left: 'rounded-l',
    right: 'rounded-r',
};
const StyledIconButton = _default$1(Button$2) `
  flex
  items-center
  justify-center
  group
  transition-colors
  ${({ $variant }) => ICON_BUTTON_COLOR_VARIANTS[$variant]}
  ${({ $size }) => IconButtonSizesStyle[$size]}
  ${({ $rounded }) => ICON_BUTTON_ROUNDED_VARIANTS[$rounded]}
  ${({ $active, $variant }) => $active ? ICON_BUTTON_ACTIVE_COLOR_VARIANTS[$variant] : ''}
`;

const GenericToIconSizingMap = {
    xs: 2,
    sm: 4,
    base: 6,
    lg: 8,
    xl: 10,
};
const IconButton = ({ active, className, icon, rounded = 'regular', size, testId, variant, ...props }) => (React__default.createElement(StyledIconButton, { "$size": size, "$rounded": rounded, "$active": active, "$variant": variant, className: className, "data-testid": testId, ...props },
    React__default.createElement(IconWithNotification, { variant: "none", size: GenericToIconSizingMap[size], ...icon, notification: { colorVariant: 'red', ringVariant: 'gray' } })));

var _path$j;
function _extends$t() { return _extends$t = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$t.apply(null, arguments); }
var SvgMenuVerticalDots = function SvgMenuVerticalDots(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$t({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 10 10",
    ref: ref
  }, props), _path$j || (_path$j = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    d: "M6.25 1.25a1.25 1.25 0 1 1 -2.5 0 1.25 1.25 0 0 1 2.5 0ZM5 6.25a1.25 1.25 0 1 0 0 -2.5 1.25 1.25 0 0 0 0 2.5ZM5 10a1.25 1.25 0 1 0 0 -2.5A1.25 1.25 0 0 0 5 10Z",
    clipRule: "evenodd",
    strokeWidth: 1
  })));
};
var ForwardRef$q = /*#__PURE__*/forwardRef(SvgMenuVerticalDots);
var Memo$q = /*#__PURE__*/memo(ForwardRef$q);

const StyledDropdownContainer = _default$1.div `
    inline-block
    text-left
    flex
    rounded-md
    shadow-sm
`;
const StyledDropdownMenuButton = _default$1(Button$2) `
    ${({ disabled }) => disabled
    ? 'bg-gray-200 text-gray-500 border-primary-600'
    : 'bg-white text-gray-700 hover:bg-gray-50 focus:border-primary-600 focus:ring-primary-600 focus:ring-1 focus:outline-none'}
    ${({ $active }) => $active
    ? 'bg-primary-50 hover:bg-primary-100 ring-1 border-primary-300 ring-primary-300 text-primary-700 [&_span]:!text-primary-700 [&_svg]:!text-primary-600'
    : 'border-gray-300'}
    rounded-md
    relative
    px-4
    py-2
    sm:text-sm
    border
    font-medium
    items-center
    grow
    text-left
    flex
    justify-between
    min-w-0
    shadow-sm
`;
const StyledDropdownMenu = _default$1(Menu$1) `
    origin-top-right
    absolute
    right-0
    mt-2
    w-56
    rounded-md
    shadow-lg
    bg-white
    ring-1
    ring-black
    ring-opacity-5
    focus:outline-none
    z-overlay
    max-h-96
    overflow-y-scroll
    overflow-x-hidden
`;
const StyledDropdownMenuItem = _default$1(MenuItem$1) `
    ${({ $selected }) => ($selected ? 'font-semibold' : '  font-normal')}
    text-gray-900
    hover:bg-gray-50
    p-2
    leading-5
    text-sm
    cursor-pointer
    flex
    align-center
    gap-1.5
`;
const StyledLeadingIcon = _default$1.span `
  -ml-1
  mr-2
  h-5
  w-5
  flex
  items-center
  justify-center
`;
const StyledDropdownArrow = _default$1.span `
  -mr-1
  ml-2
  h-5
  w-5
  flex
  items-center
  justify-center
`;
const StyledButtonTitle = _default$1(Typography) `
  truncate
  flex-1
`;
const StyledButtonPlaceholder = _default$1(StyledButtonTitle) `
  text-gray-500
`;

const DropdownButton = ({ active, className, disabled, id, leadingIcon, onMouseEnter, onMouseLeave, placeholder, state, trailingIcon, value, ...restProps }) => (React__default.createElement(StyledDropdownMenuButton, { "$active": active, className: className, disabled: disabled, id: id, placeholder: placeholder, state: state, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, ...restProps },
    leadingIcon && React__default.createElement(StyledLeadingIcon, null, leadingIcon),
    value ? (React__default.createElement(StyledButtonTitle, { fontSize: "sm", className: "menu-value" }, value)) : (React__default.createElement(StyledButtonPlaceholder, { fontSize: "sm", className: "menu-placeholder" }, placeholder)),
    React__default.createElement(StyledDropdownArrow, null, trailingIcon || React__default.createElement(MenuButtonArrow, null))));
const DropdownButtonMemo = memo(DropdownButton);

const DropdownMenuItem = ({ className, hideOnClick, onClick, option, selected, }) => {
    const handleOnClick = useCallback((e) => {
        e.stopPropagation();
        onClick(option.value);
    }, [onClick, option]);
    return (React__default.createElement(StyledDropdownMenuItem, { hideOnClick: hideOnClick, onClick: handleOnClick, "$selected": selected, className: className },
        React__default.createElement("div", { className: "w-3 h-3 text-primary-600", "aria-hidden": "true" }, selected ? React__default.createElement(Memo$r, null) : null),
        option.label));
};
const DropdownMenuItemMemo = memo(DropdownMenuItem);

const createDropdownMenuComponent = (isMemo) => function DropdownMenu({ active, as, className, hideOnClick, id, leadingIcon, onChange, options, disabled = options.length === 0, placeholder = '—', trailingIcon, value, usePortal, ...restProps }) {
    const state = useMenuState({ gutter: 1 });
    const DropdownMenuItemComponent = isMemo ? DropdownMenuItemMemo : DropdownMenuItem;
    const explicitOptions = options.map((option) => typeof option === 'object' ? option : { value: option, label: option.toString() });
    const selectedOption = explicitOptions.find((option) => Object.is(option.value, value));
    const commonProps = {
        value: selectedOption?.label,
        active,
        disabled,
        id,
        leadingIcon,
        placeholder,
        state,
        trailingIcon,
        className,
        onClick: (e) => {
            e.stopPropagation();
        },
    };
    return (React__default.createElement(React__default.Fragment, null,
        as ? (React__default.createElement(MenuButton, { as: as, ...commonProps, ...restProps })) : (React__default.createElement(DropdownButton, { as: MenuButton, ...commonProps })),
        React__default.createElement(StyledDropdownMenu, { state: state, portal: usePortal }, explicitOptions.map((option) => (React__default.createElement(DropdownMenuItemComponent, { hideOnClick: hideOnClick, key: option.value, option: option, selected: Object.is(option, selectedOption), onClick: onChange }))))));
};
const DropdownMenu = createDropdownMenuComponent(false);
const DropdownMenuMemo = memo(createDropdownMenuComponent(true));

const createDropdownMenuWithButtonComponent = (isMemo) => function DropdownMenuWithButton({ button, className, errorText, onChange, options, ...otherProps }) {
    const DropdownMenuComponent = isMemo ? DropdownMenuMemo : DropdownMenu;
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(StyledDropdownContainer, { className: className },
            React__default.createElement(DropdownMenuComponent, { className: "rounded-l-md rounded-r-none", onChange: onChange, options: options, id: otherProps.id || undefined, disabled: otherProps.disabled || undefined, value: otherProps.value || undefined, placeholder: otherProps.placeholder || undefined, leadingIcon: otherProps.leadingIcon || undefined, trailingIcon: otherProps.trailingIcon || undefined, active: otherProps.active || undefined, hideOnClick: otherProps.hideOnClick || undefined, usePortal: otherProps.usePortal || undefined }),
            button),
        React__default.createElement(GeneralFormError, { error: !!errorText, id: `${otherProps.id}-error` }, errorText)));
};
const DropdownMenuWithButton = createDropdownMenuWithButtonComponent(false);
const DropdownMenuWithButtonMemo = memo(createDropdownMenuWithButtonComponent(true));

const AutocompleteOptions = ({ groupEndValues = [], disabledKey = 'disabled', displayKey = 'label', options = [], valueKey = 'value', query, setQuery, allowCreate = false, isLoading, ...props }) => {
    const trimmedQuery = query.trim();
    const hasExactMatch = options.some((option) => option[displayKey] === trimmedQuery);
    return (React__default.createElement(We$1, { as: Fragment, leave: "transition ease-in duration-100", leaveFrom: "opacity-100", leaveTo: "opacity-0", afterLeave: () => setQuery('') },
        React__default.createElement(Options, { ...props },
            options.length > 0
                ? options.map((option) => (React__default.createElement(Ot.Option, { key: option[valueKey], value: option, disabled: !!option[disabledKey], as: Fragment }, ({ active, disabled, selected }) => (React__default.createElement(ListItem, { "$active": active, "$selected": selected, "$disabled": disabled, "$isLastOptionInGroup": trimmedQuery === '' && (groupEndValues.includes(option[valueKey]) || option.isLastOptionInGroup) },
                    option?.icon !== undefined && option?.icon,
                    option?.imageSrc !== undefined && (React__default.createElement(OptionImage, { src: option.imageSrc, srcSet: option.imageSrcSet, alt: "" })),
                    React__default.createElement(ListItemText, { "$active": active, "$selected": selected, "$disabled": disabled, title: option[displayKey] ?? undefined }, option[displayKey] ?? ''),
                    React__default.createElement(SelectedOptionTrailingIconContainer, { "$active": active },
                        selected && React__default.createElement(CheckIcon, { "aria-hidden": "true", color: colors$3.primary[600] }),
                        option.childMenu && (React__default.createElement(DropdownMenu, { as: IconButton, active: false, variant: "inherit", size: "xs", icon: { node: React__default.createElement(Memo$q, null), isNotificationToggled: false }, onChange: option.childMenu.onChange, options: option.childMenu.options, value: null, usePortal: true }))))))))
                : null,
            allowCreate && trimmedQuery !== '' && !hasExactMatch ? (React__default.createElement(Ot.Option, { value: trimmedQuery, as: Fragment }, ({ active, disabled, selected }) => (React__default.createElement(ListItem, { "$active": active, "$selected": selected, "$disabled": disabled },
                React__default.createElement(ListItemText, { "$active": active, "$selected": false }, selected ? `Remove "${trimmedQuery}"` : `Create "${trimmedQuery}"`))))) : null,
            isLoading && (React__default.createElement(Ot.Option, { value: null, disabled: true, as: Fragment },
                React__default.createElement(ListItem, null,
                    React__default.createElement(ListItemText, null, "Loading...")))),
            options.length === 0 && !isLoading && trimmedQuery !== '' && !allowCreate && (React__default.createElement(Ot.Option, { value: null, disabled: true, as: Fragment },
                React__default.createElement(ListItem, null,
                    React__default.createElement(ListItemText, null, "No matches")))))));
};

const AutocompleteInput = ({ className, disabled, error, errorText, groupEndValues = [], id, name, onChange, onCreate, optionDisabledKey, optionDisplayKey = 'label', options = [], optionValueKey = 'value', placeholder = 'Type to search', searchKeys = optionDisplayKey !== undefined ? [optionDisplayKey] : undefined, value, allowCreate = false, ...props }) => {
    const [selectedOption, setSelectedOption] = useState(options.find((option) => option[optionValueKey] === value));
    const [query, setQuery] = useState('');
    const hasInvalidValue = value !== undefined &&
        value !== null &&
        value !== '' &&
        options.find((option) => option[optionValueKey] === value) === undefined;
    const selectedOptionHasIcon = selectedOption?.icon !== undefined;
    const selectedOptionHasImage = selectedOption?.imageSrc !== undefined;
    const handleChangeInput = (e) => {
        setQuery(e.target.value);
    };
    const handleChangeCombobox = (selectedOption) => {
        if (typeof selectedOption === 'string') {
            onCreate?.(selectedOption);
            return;
        }
        const value = selectedOption?.[optionValueKey];
        setSelectedOption(selectedOption);
        onChange?.(value);
    };
    useEffect(() => {
        const option = options.find((option) => option[optionValueKey] === value);
        setSelectedOption(option);
    }, [optionValueKey, options, value]);
    const filteredOptions = query === '' ? options : matchSorter(options, query, { keys: searchKeys });
    return (React.createElement(Ot, { as: "div", name: name, value: selectedOption?.[optionValueKey], onChange: handleChangeCombobox, className: cn('relative', className), id: id, disabled: disabled, nullable: true, ...props },
        React.createElement(InputWrapper, null,
            selectedOptionHasIcon && (React.createElement(SelectedOptionLeadingIconContainer, null, selectedOption?.icon)),
            selectedOptionHasImage && (React.createElement(SelectedOptionLeadingIconContainer, null,
                React.createElement(OptionImage, { src: selectedOption?.imageSrc, srcSet: selectedOption?.imageSrcSet, alt: "" }))),
            React.createElement(Input$2, { onChange: handleChangeInput, displayValue: makeDisplayValueGetter(options, optionValueKey, optionDisplayKey), placeholder: placeholder, disabled: disabled, "$error": error || hasInvalidValue, "$hasImage": selectedOptionHasIcon || selectedOptionHasImage }),
            React.createElement(Button$1, { disabled: disabled },
                React.createElement(ChevronDownIcon, { "aria-hidden": "true" }))),
        React.createElement(AutocompleteOptions, { options: filteredOptions, valueKey: optionValueKey, displayKey: optionDisplayKey, disabledKey: optionDisabledKey, groupEndValues: groupEndValues, query: query, setQuery: setQuery, allowCreate: allowCreate }),
        React.createElement(GeneralFormError, { error: (!!error && !!errorText) || hasInvalidValue, id: `${id}-error` }, errorText ? errorText : 'Invalid value provided. Please select a new value.')));
};

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked, or until the next browser frame is drawn. The debounced function
 * comes with a `cancel` method to cancel delayed `func` invocations and a
 * `flush` method to immediately invoke them. Provide `options` to indicate
 * whether `func` should be invoked on the leading and/or trailing edge of the
 * `wait` timeout. The `func` is invoked with the last arguments provided to the
 * debounced function. Subsequent calls to the debounced function return the
 * result of the last `func` invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * If `wait` is omitted in an environment with `requestAnimationFrame`, `func`
 * invocation will be deferred until the next frame is drawn (typically about
 * 16ms).
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `debounce` and `throttle`.
 *
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0]
 *  The number of milliseconds to delay; if omitted, `requestAnimationFrame` is
 *  used (if available, otherwise it will be setTimeout(...,0)).
 * @param {Object} [options={}] The options object.
 *  Specify invoking on the leading edge of the timeout.
 * @param {boolean} [options.leading=false]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {number} [options.maxWait]
 *  Specify invoking on the trailing edge of the timeout.
 * @param {boolean} [options.trailing=true]
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * const resizeHandler = useDebouncedCallback(calculateLayout, 150);
 * window.addEventListener('resize', resizeHandler)
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * const clickHandler = useDebouncedCallback(sendMail, 300, {
 *   leading: true,
 *   trailing: false,
 * })
 * <button onClick={clickHandler}>click me</button>
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * const debounced = useDebouncedCallback(batchLog, 250, { 'maxWait': 1000 })
 * const source = new EventSource('/stream')
 * source.addEventListener('message', debounced)
 *
 * // Cancel the trailing debounced invocation.
 * window.addEventListener('popstate', debounced.cancel)
 *
 * // Check for pending invocations.
 * const status = debounced.pending() ? "Pending..." : "Ready"
 */
function useDebouncedCallback(func, wait, options) {
    var _this = this;
    var lastCallTime = useRef(null);
    var lastInvokeTime = useRef(0);
    var timerId = useRef(null);
    var lastArgs = useRef([]);
    var lastThis = useRef();
    var result = useRef();
    var funcRef = useRef(func);
    var mounted = useRef(true);
    funcRef.current = func;
    // Bypass `requestAnimationFrame` by explicitly setting `wait=0`.
    var useRAF = !wait && wait !== 0 && typeof window !== 'undefined';
    if (typeof func !== 'function') {
        throw new TypeError('Expected a function');
    }
    wait = +wait || 0;
    options = options || {};
    var leading = !!options.leading;
    var trailing = 'trailing' in options ? !!options.trailing : true; // `true` by default
    var maxing = 'maxWait' in options;
    var maxWait = maxing ? Math.max(+options.maxWait || 0, wait) : null;
    useEffect(function () {
        mounted.current = true;
        return function () {
            mounted.current = false;
        };
    }, []);
    // You may have a question, why we have so many code under the useMemo definition.
    //
    // This was made as we want to escape from useCallback hell and
    // not to initialize a number of functions each time useDebouncedCallback is called.
    //
    // It means that we have less garbage for our GC calls which improves performance.
    // Also, it makes this library smaller.
    //
    // And the last reason, that the code without lots of useCallback with deps is easier to read.
    // You have only one place for that.
    var debounced = useMemo(function () {
        var invokeFunc = function (time) {
            var args = lastArgs.current;
            var thisArg = lastThis.current;
            lastArgs.current = lastThis.current = null;
            lastInvokeTime.current = time;
            return (result.current = funcRef.current.apply(thisArg, args));
        };
        var startTimer = function (pendingFunc, wait) {
            if (useRAF)
                cancelAnimationFrame(timerId.current);
            timerId.current = useRAF ? requestAnimationFrame(pendingFunc) : setTimeout(pendingFunc, wait);
        };
        var shouldInvoke = function (time) {
            if (!mounted.current)
                return false;
            var timeSinceLastCall = time - lastCallTime.current;
            var timeSinceLastInvoke = time - lastInvokeTime.current;
            // Either this is the first call, activity has stopped and we're at the
            // trailing edge, the system time has gone backwards and we're treating
            // it as the trailing edge, or we've hit the `maxWait` limit.
            return (!lastCallTime.current ||
                timeSinceLastCall >= wait ||
                timeSinceLastCall < 0 ||
                (maxing && timeSinceLastInvoke >= maxWait));
        };
        var trailingEdge = function (time) {
            timerId.current = null;
            // Only invoke if we have `lastArgs` which means `func` has been
            // debounced at least once.
            if (trailing && lastArgs.current) {
                return invokeFunc(time);
            }
            lastArgs.current = lastThis.current = null;
            return result.current;
        };
        var timerExpired = function () {
            var time = Date.now();
            if (shouldInvoke(time)) {
                return trailingEdge(time);
            }
            // https://github.com/xnimorz/use-debounce/issues/97
            if (!mounted.current) {
                return;
            }
            // Remaining wait calculation
            var timeSinceLastCall = time - lastCallTime.current;
            var timeSinceLastInvoke = time - lastInvokeTime.current;
            var timeWaiting = wait - timeSinceLastCall;
            var remainingWait = maxing ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
            // Restart the timer
            startTimer(timerExpired, remainingWait);
        };
        var func = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            var time = Date.now();
            var isInvoking = shouldInvoke(time);
            lastArgs.current = args;
            lastThis.current = _this;
            lastCallTime.current = time;
            if (isInvoking) {
                if (!timerId.current && mounted.current) {
                    // Reset any `maxWait` timer.
                    lastInvokeTime.current = lastCallTime.current;
                    // Start the timer for the trailing edge.
                    startTimer(timerExpired, wait);
                    // Invoke the leading edge.
                    return leading ? invokeFunc(lastCallTime.current) : result.current;
                }
                if (maxing) {
                    // Handle invocations in a tight loop.
                    startTimer(timerExpired, wait);
                    return invokeFunc(lastCallTime.current);
                }
            }
            if (!timerId.current) {
                startTimer(timerExpired, wait);
            }
            return result.current;
        };
        func.cancel = function () {
            if (timerId.current) {
                useRAF ? cancelAnimationFrame(timerId.current) : clearTimeout(timerId.current);
            }
            lastInvokeTime.current = 0;
            lastArgs.current = lastCallTime.current = lastThis.current = timerId.current = null;
        };
        func.isPending = function () {
            return !!timerId.current;
        };
        func.flush = function () {
            return !timerId.current ? result.current : trailingEdge(Date.now());
        };
        return func;
    }, [leading, maxing, wait, maxWait, trailing, useRAF]);
    return debounced;
}

function _extends$s() { return _extends$s = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$s.apply(null, arguments); }
var SvgBoldClose = function SvgBoldClose(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$s({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    ref: ref
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M22.66,5.58a1.5,1.5,0,0,0,0-2.12L20.54,1.34a1.49,1.49,0,0,0-2.12,0L12.18,7.58a.27.27,0,0,1-.36,0L5.58,1.34a1.49,1.49,0,0,0-2.12,0L1.34,3.46a1.5,1.5,0,0,0,0,2.12l6.24,6.24a.25.25,0,0,1,0,.36L1.34,18.42a1.5,1.5,0,0,0,0,2.12l2.12,2.12a1.49,1.49,0,0,0,2.12,0l6.24-6.24a.27.27,0,0,1,.36,0l6.24,6.24a1.47,1.47,0,0,0,1.06.44,1.45,1.45,0,0,0,1.06-.44l2.12-2.12a1.5,1.5,0,0,0,0-2.12l-6.24-6.24a.25.25,0,0,1,0-.36Z",
    style: {
      fill: "currentColor"
    }
  }));
};
var ForwardRef$p = /*#__PURE__*/forwardRef(SvgBoldClose);
var Memo$p = /*#__PURE__*/memo(ForwardRef$p);

const TagContainer = _default$1(Stack$5) `
  ${({ $variant }) => $variant === 'primary' ? 'bg-primary-100 border-primary-300' : 'bg-gray-200 border-gray-400'}
  ${({ $rounded }) => ($rounded === 'full' ? 'rounded-full' : 'rounded-md')}
  ${({ $bordered }) => ($bordered ? 'border' : undefined)}
  max-w-full
  px-1
  py-0.5
`;
const TagLabel = _default$1(Typography) `
  ${({ $variant }) => ($variant === 'primary' ? 'text-primary-800' : 'text-gray-700')}
  pl-1
  max-w-full
  overflow-hidden
  text-ellipsis
`;

const Tag = ({ allowRemove = true, as, bordered = false, className, icon, id, label, onRemoveClick, rounded = 'full', variant = 'primary', }) => (React__default.createElement(TagContainer, { "$variant": variant, "$rounded": rounded, "$bordered": bordered, as: as, alignItems: "center", justifyContent: "center", className: className, id: id },
    icon,
    React__default.createElement(TagLabel, { "$variant": variant, as: "span", fontWeight: "medium", fontSize: "xs" }, label),
    allowRemove && (React__default.createElement(IconButton, { active: false, variant: variant, icon: {
            node: React__default.createElement(Memo$p, null),
            isNotificationToggled: false,
        }, onClick: onRemoveClick, rounded: "regular", size: "xs" }))));

const TagInputContainer = _default$1(Stack$5) `
  bg-white
  border
  border-gray-300
  rounded-md
  ${({ $focused }) => ($focused ? 'ring-1 ring-primary-600 border-primary-600' : '')}
`;
const InputContainer$1 = _default$1(Stack$5) `
  flex-1
  min-w-[100px]
  w-full
  h-5
`;
const Input$1 = _default$1(FormInput) `
  h-5
  w-full
  border-none
  p-0
  overflow-hidden
  text-sm
  font-normal
  placeholder:text-gray-400
  focus:ring-0
  focus:ring-offset-0
`;

const TagInput = ({ className, error, errorText, id, placeholder, setTags, tags, }) => {
    const [inputValue, setInputValue] = useState('');
    const [focused, setFocused] = useState(false);
    const removeTag = (index) => {
        const newTags = [...tags];
        newTags.splice(index, 1);
        setTags(newTags);
    };
    const commitInput = () => {
        setInputValue('');
        const seen = new Set(tags.map((tag) => tag.toLowerCase()));
        const added = inputValue
            .split(',')
            .map((tag) => tag.trim())
            .filter((tag) => {
            if (!tag || seen.has(tag.toLowerCase())) {
                return false;
            }
            seen.add(tag.toLowerCase());
            return true;
        });
        if (added.length === 0) {
            return;
        }
        setTags([...tags, ...added]);
    };
    const inputKeyDown = (e) => {
        if ((e.key === 'Enter' || e.key === ',') && inputValue) {
            e.preventDefault();
            commitInput();
        }
        else if (e.key === 'Backspace' && !inputValue) {
            removeTag(tags.length - 1);
        }
    };
    const handleChange = (e) => {
        setInputValue(e.target.value);
    };
    const handleBlur = () => {
        setFocused(false);
        commitInput();
    };
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(TagInputContainer, { as: "ul", alignItems: "center", wrap: "wrap", paddingY: 2, paddingX: 3, gap: 2, boxShadow: "sm", className: className, "$focused": focused },
            tags.map((tag, index) => (React__default.createElement(Tag, { as: "li", key: tag, label: tag, onRemoveClick: () => removeTag(index) }))),
            React__default.createElement(InputContainer$1, { alignItems: "center" },
                React__default.createElement(Input$1, { id: id, "aria-errormessage": `${id}-error`, "aria-invalid": error, placeholder: placeholder, type: "text", onKeyDown: inputKeyDown, onChange: handleChange, value: inputValue, onFocus: () => setFocused(true), onBlur: handleBlur }))),
        React__default.createElement(GeneralFormError, { error: !!error && !!errorText, id: `${id}-error` }, errorText)));
};

const AutocompleteTagInput = ({ className, disabled, error, errorText, groupEndValues = [], id, name, onChange, optionDisabledKey, optionDisplayKey = 'label', options = [], optionValueKey = 'value', placeholder = 'Type to search', searchKeys = optionDisplayKey !== undefined ? [optionDisplayKey] : undefined, values, allowCreate = true, allowRemove = true, tagProps = {}, searchKeyword, searchKeywordDebounce = 200, setSearchKeyword, isLoading, ...props }) => {
    const [selectedOptions, setSelectedOptions] = useState(values ?? []);
    const [query, setQuery] = useState(searchKeyword ?? '');
    const [focused, setFocused] = useState(false);
    const handleChangeInput = (e) => {
        setQuery(e.target.value);
        debouncedSetSearchKeyword(e.target.value);
    };
    const debouncedSetSearchKeyword = useDebouncedCallback((value) => {
        setSearchKeyword?.(value);
    }, searchKeywordDebounce);
    const handleChangeCombobox = (values) => {
        setSelectedOptions(values);
        onChange?.(values);
        setQuery('');
    };
    const removeTag = (index) => {
        const newTags = [...selectedOptions];
        newTags.splice(index, 1);
        setSelectedOptions(newTags);
        onChange?.(newTags);
    };
    const inputKeyDown = (e) => {
        if (allowRemove && e.key === 'Backspace' && !query) {
            removeTag(selectedOptions.length - 1);
        }
    };
    const filteredOptions = (query === '' ? options : matchSorter(options, query, { keys: searchKeys })).filter((option) => !selectedOptions.find((selectedOption) => selectedOption.value === option.value));
    useEffect(() => {
        setSelectedOptions(values ?? []);
    }, [values]);
    return (React__default.createElement(Ot, { as: "div", name: name, value: selectedOptions, onChange: handleChangeCombobox, className: cn('relative', className), id: id, disabled: disabled, nullable: true, multiple: true, ...props },
        React__default.createElement("div", { className: "relative" },
            React__default.createElement(TagInputContainer, { as: "ul", alignItems: "center", wrap: "wrap", paddingY: 2, paddingX: 3, gap: 2, boxShadow: "sm", "$focused": focused },
                selectedOptions.map((option, index) => (React__default.createElement(Tag, { as: "li", key: option[optionValueKey], label: option[optionDisplayKey], onRemoveClick: () => removeTag(index), allowRemove: allowRemove, ...option, ...tagProps }))),
                React__default.createElement(InputContainer$1, { alignItems: "center" },
                    React__default.createElement(InputItem, { onChange: handleChangeInput, displayValue: () => query, placeholder: !selectedOptions.length ? placeholder : '', disabled: disabled, onKeyDown: inputKeyDown, onFocus: () => setFocused(true), onBlur: () => setFocused(false) })))),
        React__default.createElement(AutocompleteOptions, { options: filteredOptions, valueKey: optionValueKey, displayKey: optionDisplayKey, disabledKey: optionDisabledKey, groupEndValues: groupEndValues, query: query, setQuery: setQuery, allowCreate: allowCreate, isLoading: isLoading }),
        React__default.createElement(GeneralFormError, { error: !!error && !!errorText, id: `${id}-error` }, errorText ? errorText : 'Invalid value provided. Please select a new value.')));
};

const Checkbox = ({ alignItems = 'start', checked = false, className, disabled = false, icon, id, name, onChange, subtitle, title, }) => {
    const handleOnChange = useCallback((e) => onChange?.(e.target.checked), [onChange]);
    return (React__default.createElement(Stack$5, { alignItems: alignItems, justifyContent: "start", className: cn(disabled ? 'opacity-70' : '', className) },
        React__default.createElement(Stack$5, { alignItems: "center", justifyContent: "start", className: "h-5" },
            React__default.createElement(CheckboxAtom, { id: id, name: name, checked: checked, disabled: disabled, onChange: handleOnChange })),
        React__default.createElement("div", { className: "ml-3" },
            React__default.createElement(Stack$5, { direction: "row", alignItems: "start", justifyContent: "start" },
                icon && React__default.createElement("div", { className: "w-5 h-5 mr-1" }, icon),
                React__default.createElement("label", { htmlFor: id, className: "font-medium text-gray-700" }, title)),
            subtitle && React__default.createElement("p", { className: "mb-0 text-gray-500" }, subtitle))));
};
const CheckboxMemo = memo(Checkbox);

const StyledCheckboxContainer = _default$1.div `
    w-full
`;

const CheckboxContainer = ({ children, className }) => (React__default.createElement(StyledCheckboxContainer, { className: className },
    React__default.createElement("fieldset", { className: "mt-4 gap-4 grid grid-cols-2" }, children)));
const CheckboxContainerMemo = memo(CheckboxContainer);

const StyledFormSection = _default$1.div `
    w-full
`;
const FormSectionTitle = _default$1.h4 `
    block
    text-base
    font-medium
    text-gray-700
`;
const FormSectionSubtitleText = _default$1.div `
    mt-1
    text-sm
    text-gray-500
`;
const FormSectionHelpText = FormSectionSubtitleText;
const FormSectionChildren = _default$1.div `
    mt-1
    space-y-6
`;

const FormSection = ({ children, className, subtitle, title }) => (React__default.createElement(StyledFormSection, { className: className },
    React__default.createElement(FormSectionTitle, null, title),
    subtitle && React__default.createElement(FormSectionSubtitleText, null, subtitle),
    children && React__default.createElement(FormSectionChildren, null, children)));
const FormSectionMemo = memo(FormSection);

const StyledIndent = _default$1.div `
${({ $border }) => ($border ? 'border-l-2 border-gray-200' : '')}
    pl-10
    space-y-8
`;

const Indent = ({ border = true, children, className }) => (React__default.createElement(StyledIndent, { "$border": border, className: className }, children));
const IndentMemo = memo(Indent);

function _objectWithoutPropertiesLoose$4(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

/**
 * Applies mask on element.
 * @constructor
 * @param {HTMLInputElement|HTMLTextAreaElement|MaskElement} el - Element to apply mask
 * @param {Object} opts - Custom mask options
 * @return {InputMask}
 */
function IMask(el) {
  let opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  // currently available only for input-like elements
  return new IMask.InputMask(el, opts);
}

/**
  Provides details of changing model value
  @param {Object} [details]
  @param {string} [details.inserted] - Inserted symbols
  @param {boolean} [details.skip] - Can skip chars
  @param {number} [details.removeCount] - Removed symbols count
  @param {number} [details.tailShift] - Additional offset if any changes occurred before tail
*/
class ChangeDetails {
  /** Inserted symbols */

  /** Can skip chars */

  /** Additional offset if any changes occurred before tail */

  /** Raw inserted is used by dynamic mask */

  constructor(details) {
    Object.assign(this, {
      inserted: '',
      rawInserted: '',
      skip: false,
      tailShift: 0
    }, details);
  }

  /**
    Aggregate changes
    @returns {ChangeDetails} `this`
  */
  aggregate(details) {
    this.rawInserted += details.rawInserted;
    this.skip = this.skip || details.skip;
    this.inserted += details.inserted;
    this.tailShift += details.tailShift;
    return this;
  }

  /** Total offset considering all changes */
  get offset() {
    return this.tailShift + this.inserted.length;
  }
}
IMask.ChangeDetails = ChangeDetails;

/** Checks if value is string */
function isString$1(str) {
  return typeof str === 'string' || str instanceof String;
}

/**
  Direction
  @prop {string} NONE
  @prop {string} LEFT
  @prop {string} FORCE_LEFT
  @prop {string} RIGHT
  @prop {string} FORCE_RIGHT
*/
const DIRECTION = {
  NONE: 'NONE',
  LEFT: 'LEFT',
  FORCE_LEFT: 'FORCE_LEFT',
  RIGHT: 'RIGHT',
  FORCE_RIGHT: 'FORCE_RIGHT'
};

/** */
function forceDirection(direction) {
  switch (direction) {
    case DIRECTION.LEFT:
      return DIRECTION.FORCE_LEFT;
    case DIRECTION.RIGHT:
      return DIRECTION.FORCE_RIGHT;
    default:
      return direction;
  }
}

/** Escapes regular expression control chars */
function escapeRegExp(str) {
  return str.replace(/([.*+?^=!:${}()|[\]\/\\])/g, '\\$1');
}
function normalizePrepare(prep) {
  return Array.isArray(prep) ? prep : [prep, new ChangeDetails()];
}

// cloned from https://github.com/epoberezkin/fast-deep-equal with small changes
function objectIncludes(b, a) {
  if (a === b) return true;
  var arrA = Array.isArray(a),
    arrB = Array.isArray(b),
    i;
  if (arrA && arrB) {
    if (a.length != b.length) return false;
    for (i = 0; i < a.length; i++) if (!objectIncludes(a[i], b[i])) return false;
    return true;
  }
  if (arrA != arrB) return false;
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    var dateA = a instanceof Date,
      dateB = b instanceof Date;
    if (dateA && dateB) return a.getTime() == b.getTime();
    if (dateA != dateB) return false;
    var regexpA = a instanceof RegExp,
      regexpB = b instanceof RegExp;
    if (regexpA && regexpB) return a.toString() == b.toString();
    if (regexpA != regexpB) return false;
    var keys = Object.keys(a);
    // if (keys.length !== Object.keys(b).length) return false;

    for (i = 0; i < keys.length; i++)
    // $FlowFixMe ... ???
    if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
    for (i = 0; i < keys.length; i++) if (!objectIncludes(b[keys[i]], a[keys[i]])) return false;
    return true;
  } else if (a && b && typeof a === 'function' && typeof b === 'function') {
    return a.toString() === b.toString();
  }
  return false;
}

/** Provides details of changing input */
class ActionDetails {
  /** Current input value */

  /** Current cursor position */

  /** Old input value */

  /** Old selection */

  constructor(value, cursorPos, oldValue, oldSelection) {
    this.value = value;
    this.cursorPos = cursorPos;
    this.oldValue = oldValue;
    this.oldSelection = oldSelection;

    // double check if left part was changed (autofilling, other non-standard input triggers)
    while (this.value.slice(0, this.startChangePos) !== this.oldValue.slice(0, this.startChangePos)) {
      --this.oldSelection.start;
    }
  }

  /**
    Start changing position
    @readonly
  */
  get startChangePos() {
    return Math.min(this.cursorPos, this.oldSelection.start);
  }

  /**
    Inserted symbols count
    @readonly
  */
  get insertedCount() {
    return this.cursorPos - this.startChangePos;
  }

  /**
    Inserted symbols
    @readonly
  */
  get inserted() {
    return this.value.substr(this.startChangePos, this.insertedCount);
  }

  /**
    Removed symbols count
    @readonly
  */
  get removedCount() {
    // Math.max for opposite operation
    return Math.max(this.oldSelection.end - this.startChangePos ||
    // for Delete
    this.oldValue.length - this.value.length, 0);
  }

  /**
    Removed symbols
    @readonly
  */
  get removed() {
    return this.oldValue.substr(this.startChangePos, this.removedCount);
  }

  /**
    Unchanged head symbols
    @readonly
  */
  get head() {
    return this.value.substring(0, this.startChangePos);
  }

  /**
    Unchanged tail symbols
    @readonly
  */
  get tail() {
    return this.value.substring(this.startChangePos + this.insertedCount);
  }

  /**
    Remove direction
    @readonly
  */
  get removeDirection() {
    if (!this.removedCount || this.insertedCount) return DIRECTION.NONE;

    // align right if delete at right
    return (this.oldSelection.end === this.cursorPos || this.oldSelection.start === this.cursorPos) &&
    // if not range removed (event with backspace)
    this.oldSelection.end === this.oldSelection.start ? DIRECTION.RIGHT : DIRECTION.LEFT;
  }
}

/** Provides details of continuous extracted tail */
class ContinuousTailDetails {
  /** Tail value as string */

  /** Tail start position */

  /** Start position */

  constructor() {
    let value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    let from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    let stop = arguments.length > 2 ? arguments[2] : undefined;
    this.value = value;
    this.from = from;
    this.stop = stop;
  }
  toString() {
    return this.value;
  }
  extend(tail) {
    this.value += String(tail);
  }
  appendTo(masked) {
    return masked.append(this.toString(), {
      tail: true
    }).aggregate(masked._appendPlaceholder());
  }
  get state() {
    return {
      value: this.value,
      from: this.from,
      stop: this.stop
    };
  }
  set state(state) {
    Object.assign(this, state);
  }
  unshift(beforePos) {
    if (!this.value.length || beforePos != null && this.from >= beforePos) return '';
    const shiftChar = this.value[0];
    this.value = this.value.slice(1);
    return shiftChar;
  }
  shift() {
    if (!this.value.length) return '';
    const shiftChar = this.value[this.value.length - 1];
    this.value = this.value.slice(0, -1);
    return shiftChar;
  }
}

/** Supported mask type */

/** Append flags */

/** Extract flags */

/** Provides common masking stuff */
class Masked {
  // $Shape<MaskedOptions>; TODO after fix https://github.com/facebook/flow/issues/4773

  /** @type {Mask} */

  /** */ // $FlowFixMe no ideas
  /** Transforms value before mask processing */
  /** Validates if value is acceptable */
  /** Does additional processing in the end of editing */
  /** Format typed value to string */
  /** Parse strgin to get typed value */
  /** Enable characters overwriting */
  /** */
  /** */
  /** */
  constructor(opts) {
    this._value = '';
    this._update(Object.assign({}, Masked.DEFAULTS, opts));
    this.isInitialized = true;
  }

  /** Sets and applies new options */
  updateOptions(opts) {
    if (!Object.keys(opts).length) return;
    // $FlowFixMe
    this.withValueRefresh(this._update.bind(this, opts));
  }

  /**
    Sets new options
    @protected
  */
  _update(opts) {
    Object.assign(this, opts);
  }

  /** Mask state */
  get state() {
    return {
      _value: this.value
    };
  }
  set state(state) {
    this._value = state._value;
  }

  /** Resets value */
  reset() {
    this._value = '';
  }

  /** */
  get value() {
    return this._value;
  }
  set value(value) {
    this.resolve(value);
  }

  /** Resolve new value */
  resolve(value) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      input: true
    };
    this.reset();
    this.append(value, flags, '');
    this.doCommit();
    return this.value;
  }

  /** */
  get unmaskedValue() {
    return this.value;
  }
  set unmaskedValue(value) {
    this.reset();
    this.append(value, {}, '');
    this.doCommit();
  }

  /** */
  get typedValue() {
    return this.doParse(this.value);
  }
  set typedValue(value) {
    this.value = this.doFormat(value);
  }

  /** Value that includes raw user input */
  get rawInputValue() {
    return this.extractInput(0, this.value.length, {
      raw: true
    });
  }
  set rawInputValue(value) {
    this.reset();
    this.append(value, {
      raw: true
    }, '');
    this.doCommit();
  }
  get displayValue() {
    return this.value;
  }

  /** */
  get isComplete() {
    return true;
  }

  /** */
  get isFilled() {
    return this.isComplete;
  }

  /** Finds nearest input position in direction */
  nearestInputPos(cursorPos, direction) {
    return cursorPos;
  }
  totalInputPositions() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    return Math.min(this.value.length, toPos - fromPos);
  }

  /** Extracts value in range considering flags */
  extractInput() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    return this.value.slice(fromPos, toPos);
  }

  /** Extracts tail in range */
  extractTail() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    return new ContinuousTailDetails(this.extractInput(fromPos, toPos), fromPos);
  }

  /** Appends tail */
  // $FlowFixMe no ideas
  appendTail(tail) {
    if (isString$1(tail)) tail = new ContinuousTailDetails(String(tail));
    return tail.appendTo(this);
  }

  /** Appends char */
  _appendCharRaw(ch) {
    if (!ch) return new ChangeDetails();
    this._value += ch;
    return new ChangeDetails({
      inserted: ch,
      rawInserted: ch
    });
  }

  /** Appends char */
  _appendChar(ch) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let checkTail = arguments.length > 2 ? arguments[2] : undefined;
    const consistentState = this.state;
    let details;
    [ch, details] = normalizePrepare(this.doPrepare(ch, flags));
    details = details.aggregate(this._appendCharRaw(ch, flags));
    if (details.inserted) {
      let consistentTail;
      let appended = this.doValidate(flags) !== false;
      if (appended && checkTail != null) {
        // validation ok, check tail
        const beforeTailState = this.state;
        if (this.overwrite === true) {
          consistentTail = checkTail.state;
          checkTail.unshift(this.value.length - details.tailShift);
        }
        let tailDetails = this.appendTail(checkTail);
        appended = tailDetails.rawInserted === checkTail.toString();

        // not ok, try shift
        if (!(appended && tailDetails.inserted) && this.overwrite === 'shift') {
          this.state = beforeTailState;
          consistentTail = checkTail.state;
          checkTail.shift();
          tailDetails = this.appendTail(checkTail);
          appended = tailDetails.rawInserted === checkTail.toString();
        }

        // if ok, rollback state after tail
        if (appended && tailDetails.inserted) this.state = beforeTailState;
      }

      // revert all if something went wrong
      if (!appended) {
        details = new ChangeDetails();
        this.state = consistentState;
        if (checkTail && consistentTail) checkTail.state = consistentTail;
      }
    }
    return details;
  }

  /** Appends optional placeholder at end */
  _appendPlaceholder() {
    return new ChangeDetails();
  }

  /** Appends optional eager placeholder at end */
  _appendEager() {
    return new ChangeDetails();
  }

  /** Appends symbols considering flags */
  // $FlowFixMe no ideas
  append(str, flags, tail) {
    if (!isString$1(str)) throw new Error('value should be string');
    const details = new ChangeDetails();
    const checkTail = isString$1(tail) ? new ContinuousTailDetails(String(tail)) : tail;
    if (flags !== null && flags !== void 0 && flags.tail) flags._beforeTailState = this.state;
    for (let ci = 0; ci < str.length; ++ci) {
      const d = this._appendChar(str[ci], flags, checkTail);
      if (!d.rawInserted && !this.doSkipInvalid(str[ci], flags, checkTail)) break;
      details.aggregate(d);
    }
    if ((this.eager === true || this.eager === 'append') && flags !== null && flags !== void 0 && flags.input && str) {
      details.aggregate(this._appendEager());
    }

    // append tail but aggregate only tailShift
    if (checkTail != null) {
      details.tailShift += this.appendTail(checkTail).tailShift;
      // TODO it's a good idea to clear state after appending ends
      // but it causes bugs when one append calls another (when dynamic dispatch set rawInputValue)
      // this._resetBeforeTailState();
    }

    return details;
  }

  /** */
  remove() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    this._value = this.value.slice(0, fromPos) + this.value.slice(toPos);
    return new ChangeDetails();
  }

  /** Calls function and reapplies current value */
  withValueRefresh(fn) {
    if (this._refreshing || !this.isInitialized) return fn();
    this._refreshing = true;
    const rawInput = this.rawInputValue;
    const value = this.value;
    const ret = fn();
    this.rawInputValue = rawInput;
    // append lost trailing chars at end
    if (this.value && this.value !== value && value.indexOf(this.value) === 0) {
      this.append(value.slice(this.value.length), {}, '');
    }
    delete this._refreshing;
    return ret;
  }

  /** */
  runIsolated(fn) {
    if (this._isolated || !this.isInitialized) return fn(this);
    this._isolated = true;
    const state = this.state;
    const ret = fn(this);
    this.state = state;
    delete this._isolated;
    return ret;
  }

  /** */
  doSkipInvalid(ch) {
    return this.skipInvalid;
  }

  /**
    Prepares string before mask processing
    @protected
  */
  doPrepare(str) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return this.prepare ? this.prepare(str, this, flags) : str;
  }

  /**
    Validates if value is acceptable
    @protected
  */
  doValidate(flags) {
    return (!this.validate || this.validate(this.value, this, flags)) && (!this.parent || this.parent.doValidate(flags));
  }

  /**
    Does additional processing in the end of editing
    @protected
  */
  doCommit() {
    if (this.commit) this.commit(this.value, this);
  }

  /** */
  doFormat(value) {
    return this.format ? this.format(value, this) : value;
  }

  /** */
  doParse(str) {
    return this.parse ? this.parse(str, this) : str;
  }

  /** */
  splice(start, deleteCount, inserted, removeDirection) {
    let flags = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {
      input: true
    };
    const tailPos = start + deleteCount;
    const tail = this.extractTail(tailPos);
    const eagerRemove = this.eager === true || this.eager === 'remove';
    let oldRawValue;
    if (eagerRemove) {
      removeDirection = forceDirection(removeDirection);
      oldRawValue = this.extractInput(0, tailPos, {
        raw: true
      });
    }
    let startChangePos = start;
    const details = new ChangeDetails();

    // if it is just deletion without insertion
    if (removeDirection !== DIRECTION.NONE) {
      startChangePos = this.nearestInputPos(start, deleteCount > 1 && start !== 0 && !eagerRemove ? DIRECTION.NONE : removeDirection);

      // adjust tailShift if start was aligned
      details.tailShift = startChangePos - start;
    }
    details.aggregate(this.remove(startChangePos));
    if (eagerRemove && removeDirection !== DIRECTION.NONE && oldRawValue === this.rawInputValue) {
      if (removeDirection === DIRECTION.FORCE_LEFT) {
        let valLength;
        while (oldRawValue === this.rawInputValue && (valLength = this.value.length)) {
          details.aggregate(new ChangeDetails({
            tailShift: -1
          })).aggregate(this.remove(valLength - 1));
        }
      } else if (removeDirection === DIRECTION.FORCE_RIGHT) {
        tail.unshift();
      }
    }
    return details.aggregate(this.append(inserted, flags, tail));
  }
  maskEquals(mask) {
    return this.mask === mask;
  }
  typedValueEquals(value) {
    const tval = this.typedValue;
    return value === tval || Masked.EMPTY_VALUES.includes(value) && Masked.EMPTY_VALUES.includes(tval) || this.doFormat(value) === this.doFormat(this.typedValue);
  }
}
Masked.DEFAULTS = {
  format: String,
  parse: v => v,
  skipInvalid: true
};
Masked.EMPTY_VALUES = [undefined, null, ''];
IMask.Masked = Masked;

/** Get Masked class by mask type */
function maskedClass(mask) {
  if (mask == null) {
    throw new Error('mask property should be defined');
  }

  // $FlowFixMe
  if (mask instanceof RegExp) return IMask.MaskedRegExp;
  // $FlowFixMe
  if (isString$1(mask)) return IMask.MaskedPattern;
  // $FlowFixMe
  if (mask instanceof Date || mask === Date) return IMask.MaskedDate;
  // $FlowFixMe
  if (mask instanceof Number || typeof mask === 'number' || mask === Number) return IMask.MaskedNumber;
  // $FlowFixMe
  if (Array.isArray(mask) || mask === Array) return IMask.MaskedDynamic;
  // $FlowFixMe
  if (IMask.Masked && mask.prototype instanceof IMask.Masked) return mask;
  // $FlowFixMe
  if (mask instanceof IMask.Masked) return mask.constructor;
  // $FlowFixMe
  if (mask instanceof Function) return IMask.MaskedFunction;
  console.warn('Mask not found for mask', mask); // eslint-disable-line no-console
  // $FlowFixMe
  return IMask.Masked;
}

/** Creates new {@link Masked} depending on mask type */
function createMask(opts) {
  // $FlowFixMe
  if (IMask.Masked && opts instanceof IMask.Masked) return opts;
  opts = Object.assign({}, opts);
  const mask = opts.mask;

  // $FlowFixMe
  if (IMask.Masked && mask instanceof IMask.Masked) return mask;
  const MaskedClass = maskedClass(mask);
  if (!MaskedClass) throw new Error('Masked class is not found for provided mask, appropriate module needs to be import manually before creating mask.');
  return new MaskedClass(opts);
}
IMask.createMask = createMask;

const _excluded$a = ["parent", "isOptional", "placeholderChar", "displayChar", "lazy", "eager"];

/** */

const DEFAULT_INPUT_DEFINITIONS = {
  '0': /\d/,
  'a': /[\u0041-\u005A\u0061-\u007A\u00AA\u00B5\u00BA\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u0527\u0531-\u0556\u0559\u0561-\u0587\u05D0-\u05EA\u05F0-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u08A0\u08A2-\u08AC\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0977\u0979-\u097F\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C33\u0C35-\u0C39\u0C3D\u0C58\u0C59\u0C60\u0C61\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D60\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F4\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1877\u1880-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191C\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19C1-\u19C7\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1CE9-\u1CEC\u1CEE-\u1CF1\u1CF5\u1CF6\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FCC\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA697\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA78E\uA790-\uA793\uA7A0-\uA7AA\uA7F8-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA80-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uABC0-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]/,
  // http://stackoverflow.com/a/22075070
  '*': /./
};

/** */
class PatternInputDefinition {
  /** */

  /** */

  /** */

  /** */

  /** */

  /** */

  /** */

  /** */

  constructor(opts) {
    const {
        parent,
        isOptional,
        placeholderChar,
        displayChar,
        lazy,
        eager
      } = opts,
      maskOpts = _objectWithoutPropertiesLoose$4(opts, _excluded$a);
    this.masked = createMask(maskOpts);
    Object.assign(this, {
      parent,
      isOptional,
      placeholderChar,
      displayChar,
      lazy,
      eager
    });
  }
  reset() {
    this.isFilled = false;
    this.masked.reset();
  }
  remove() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    if (fromPos === 0 && toPos >= 1) {
      this.isFilled = false;
      return this.masked.remove(fromPos, toPos);
    }
    return new ChangeDetails();
  }
  get value() {
    return this.masked.value || (this.isFilled && !this.isOptional ? this.placeholderChar : '');
  }
  get unmaskedValue() {
    return this.masked.unmaskedValue;
  }
  get displayValue() {
    return this.masked.value && this.displayChar || this.value;
  }
  get isComplete() {
    return Boolean(this.masked.value) || this.isOptional;
  }
  _appendChar(ch) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (this.isFilled) return new ChangeDetails();
    const state = this.masked.state;
    // simulate input
    const details = this.masked._appendChar(ch, flags);
    if (details.inserted && this.doValidate(flags) === false) {
      details.inserted = details.rawInserted = '';
      this.masked.state = state;
    }
    if (!details.inserted && !this.isOptional && !this.lazy && !flags.input) {
      details.inserted = this.placeholderChar;
    }
    details.skip = !details.inserted && !this.isOptional;
    this.isFilled = Boolean(details.inserted);
    return details;
  }
  append() {
    // TODO probably should be done via _appendChar
    return this.masked.append(...arguments);
  }
  _appendPlaceholder() {
    const details = new ChangeDetails();
    if (this.isFilled || this.isOptional) return details;
    this.isFilled = true;
    details.inserted = this.placeholderChar;
    return details;
  }
  _appendEager() {
    return new ChangeDetails();
  }
  extractTail() {
    return this.masked.extractTail(...arguments);
  }
  appendTail() {
    return this.masked.appendTail(...arguments);
  }
  extractInput() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    let flags = arguments.length > 2 ? arguments[2] : undefined;
    return this.masked.extractInput(fromPos, toPos, flags);
  }
  nearestInputPos(cursorPos) {
    let direction = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DIRECTION.NONE;
    const minPos = 0;
    const maxPos = this.value.length;
    const boundPos = Math.min(Math.max(cursorPos, minPos), maxPos);
    switch (direction) {
      case DIRECTION.LEFT:
      case DIRECTION.FORCE_LEFT:
        return this.isComplete ? boundPos : minPos;
      case DIRECTION.RIGHT:
      case DIRECTION.FORCE_RIGHT:
        return this.isComplete ? boundPos : maxPos;
      case DIRECTION.NONE:
      default:
        return boundPos;
    }
  }
  totalInputPositions() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    return this.value.slice(fromPos, toPos).length;
  }
  doValidate() {
    return this.masked.doValidate(...arguments) && (!this.parent || this.parent.doValidate(...arguments));
  }
  doCommit() {
    this.masked.doCommit();
  }
  get state() {
    return {
      masked: this.masked.state,
      isFilled: this.isFilled
    };
  }
  set state(state) {
    this.masked.state = state.masked;
    this.isFilled = state.isFilled;
  }
}

/** */

class PatternFixedDefinition {
  /** */

  /** */

  /** */

  /** */

  /** */

  /** */

  constructor(opts) {
    Object.assign(this, opts);
    this._value = '';
    this.isFixed = true;
  }
  get value() {
    return this._value;
  }
  get unmaskedValue() {
    return this.isUnmasking ? this.value : '';
  }
  get displayValue() {
    return this.value;
  }
  reset() {
    this._isRawInput = false;
    this._value = '';
  }
  remove() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this._value.length;
    this._value = this._value.slice(0, fromPos) + this._value.slice(toPos);
    if (!this._value) this._isRawInput = false;
    return new ChangeDetails();
  }
  nearestInputPos(cursorPos) {
    let direction = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DIRECTION.NONE;
    const minPos = 0;
    const maxPos = this._value.length;
    switch (direction) {
      case DIRECTION.LEFT:
      case DIRECTION.FORCE_LEFT:
        return minPos;
      case DIRECTION.NONE:
      case DIRECTION.RIGHT:
      case DIRECTION.FORCE_RIGHT:
      default:
        return maxPos;
    }
  }
  totalInputPositions() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this._value.length;
    return this._isRawInput ? toPos - fromPos : 0;
  }
  extractInput() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this._value.length;
    let flags = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    return flags.raw && this._isRawInput && this._value.slice(fromPos, toPos) || '';
  }
  get isComplete() {
    return true;
  }
  get isFilled() {
    return Boolean(this._value);
  }
  _appendChar(ch) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const details = new ChangeDetails();
    if (this.isFilled) return details;
    const appendEager = this.eager === true || this.eager === 'append';
    const appended = this.char === ch;
    const isResolved = appended && (this.isUnmasking || flags.input || flags.raw) && (!flags.raw || !appendEager) && !flags.tail;
    if (isResolved) details.rawInserted = this.char;
    this._value = details.inserted = this.char;
    this._isRawInput = isResolved && (flags.raw || flags.input);
    return details;
  }
  _appendEager() {
    return this._appendChar(this.char, {
      tail: true
    });
  }
  _appendPlaceholder() {
    const details = new ChangeDetails();
    if (this.isFilled) return details;
    this._value = details.inserted = this.char;
    return details;
  }
  extractTail() {
    arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    return new ContinuousTailDetails('');
  }

  // $FlowFixMe no ideas
  appendTail(tail) {
    if (isString$1(tail)) tail = new ContinuousTailDetails(String(tail));
    return tail.appendTo(this);
  }
  append(str, flags, tail) {
    const details = this._appendChar(str[0], flags);
    if (tail != null) {
      details.tailShift += this.appendTail(tail).tailShift;
    }
    return details;
  }
  doCommit() {}
  get state() {
    return {
      _value: this._value,
      _isRawInput: this._isRawInput
    };
  }
  set state(state) {
    Object.assign(this, state);
  }
}

const _excluded$9 = ["chunks"];
class ChunksTailDetails {
  /** */

  constructor() {
    let chunks = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    let from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    this.chunks = chunks;
    this.from = from;
  }
  toString() {
    return this.chunks.map(String).join('');
  }

  // $FlowFixMe no ideas
  extend(tailChunk) {
    if (!String(tailChunk)) return;
    if (isString$1(tailChunk)) tailChunk = new ContinuousTailDetails(String(tailChunk));
    const lastChunk = this.chunks[this.chunks.length - 1];
    const extendLast = lastChunk && (
    // if stops are same or tail has no stop
    lastChunk.stop === tailChunk.stop || tailChunk.stop == null) &&
    // if tail chunk goes just after last chunk
    tailChunk.from === lastChunk.from + lastChunk.toString().length;
    if (tailChunk instanceof ContinuousTailDetails) {
      // check the ability to extend previous chunk
      if (extendLast) {
        // extend previous chunk
        lastChunk.extend(tailChunk.toString());
      } else {
        // append new chunk
        this.chunks.push(tailChunk);
      }
    } else if (tailChunk instanceof ChunksTailDetails) {
      if (tailChunk.stop == null) {
        // unwrap floating chunks to parent, keeping `from` pos
        let firstTailChunk;
        while (tailChunk.chunks.length && tailChunk.chunks[0].stop == null) {
          firstTailChunk = tailChunk.chunks.shift();
          firstTailChunk.from += tailChunk.from;
          this.extend(firstTailChunk);
        }
      }

      // if tail chunk still has value
      if (tailChunk.toString()) {
        // if chunks contains stops, then popup stop to container
        tailChunk.stop = tailChunk.blockIndex;
        this.chunks.push(tailChunk);
      }
    }
  }
  appendTo(masked) {
    // $FlowFixMe
    if (!(masked instanceof IMask.MaskedPattern)) {
      const tail = new ContinuousTailDetails(this.toString());
      return tail.appendTo(masked);
    }
    const details = new ChangeDetails();
    for (let ci = 0; ci < this.chunks.length && !details.skip; ++ci) {
      const chunk = this.chunks[ci];
      const lastBlockIter = masked._mapPosToBlock(masked.value.length);
      const stop = chunk.stop;
      let chunkBlock;
      if (stop != null && (
      // if block not found or stop is behind lastBlock
      !lastBlockIter || lastBlockIter.index <= stop)) {
        if (chunk instanceof ChunksTailDetails ||
        // for continuous block also check if stop is exist
        masked._stops.indexOf(stop) >= 0) {
          const phDetails = masked._appendPlaceholder(stop);
          details.aggregate(phDetails);
        }
        chunkBlock = chunk instanceof ChunksTailDetails && masked._blocks[stop];
      }
      if (chunkBlock) {
        const tailDetails = chunkBlock.appendTail(chunk);
        tailDetails.skip = false; // always ignore skip, it will be set on last
        details.aggregate(tailDetails);
        masked._value += tailDetails.inserted;

        // get not inserted chars
        const remainChars = chunk.toString().slice(tailDetails.rawInserted.length);
        if (remainChars) details.aggregate(masked.append(remainChars, {
          tail: true
        }));
      } else {
        details.aggregate(masked.append(chunk.toString(), {
          tail: true
        }));
      }
    }
    return details;
  }
  get state() {
    return {
      chunks: this.chunks.map(c => c.state),
      from: this.from,
      stop: this.stop,
      blockIndex: this.blockIndex
    };
  }
  set state(state) {
    const {
        chunks
      } = state,
      props = _objectWithoutPropertiesLoose$4(state, _excluded$9);
    Object.assign(this, props);
    this.chunks = chunks.map(cstate => {
      const chunk = "chunks" in cstate ? new ChunksTailDetails() : new ContinuousTailDetails();
      // $FlowFixMe already checked above
      chunk.state = cstate;
      return chunk;
    });
  }
  unshift(beforePos) {
    if (!this.chunks.length || beforePos != null && this.from >= beforePos) return '';
    const chunkShiftPos = beforePos != null ? beforePos - this.from : beforePos;
    let ci = 0;
    while (ci < this.chunks.length) {
      const chunk = this.chunks[ci];
      const shiftChar = chunk.unshift(chunkShiftPos);
      if (chunk.toString()) {
        // chunk still contains value
        // but not shifted - means no more available chars to shift
        if (!shiftChar) break;
        ++ci;
      } else {
        // clean if chunk has no value
        this.chunks.splice(ci, 1);
      }
      if (shiftChar) return shiftChar;
    }
    return '';
  }
  shift() {
    if (!this.chunks.length) return '';
    let ci = this.chunks.length - 1;
    while (0 <= ci) {
      const chunk = this.chunks[ci];
      const shiftChar = chunk.shift();
      if (chunk.toString()) {
        // chunk still contains value
        // but not shifted - means no more available chars to shift
        if (!shiftChar) break;
        --ci;
      } else {
        // clean if chunk has no value
        this.chunks.splice(ci, 1);
      }
      if (shiftChar) return shiftChar;
    }
    return '';
  }
}

class PatternCursor {
  constructor(masked, pos) {
    this.masked = masked;
    this._log = [];
    const {
      offset,
      index
    } = masked._mapPosToBlock(pos) || (pos < 0 ?
    // first
    {
      index: 0,
      offset: 0
    } :
    // last
    {
      index: this.masked._blocks.length,
      offset: 0
    });
    this.offset = offset;
    this.index = index;
    this.ok = false;
  }
  get block() {
    return this.masked._blocks[this.index];
  }
  get pos() {
    return this.masked._blockStartPos(this.index) + this.offset;
  }
  get state() {
    return {
      index: this.index,
      offset: this.offset,
      ok: this.ok
    };
  }
  set state(s) {
    Object.assign(this, s);
  }
  pushState() {
    this._log.push(this.state);
  }
  popState() {
    const s = this._log.pop();
    this.state = s;
    return s;
  }
  bindBlock() {
    if (this.block) return;
    if (this.index < 0) {
      this.index = 0;
      this.offset = 0;
    }
    if (this.index >= this.masked._blocks.length) {
      this.index = this.masked._blocks.length - 1;
      this.offset = this.block.value.length;
    }
  }
  _pushLeft(fn) {
    this.pushState();
    for (this.bindBlock(); 0 <= this.index; --this.index, this.offset = ((_this$block = this.block) === null || _this$block === void 0 ? void 0 : _this$block.value.length) || 0) {
      var _this$block;
      if (fn()) return this.ok = true;
    }
    return this.ok = false;
  }
  _pushRight(fn) {
    this.pushState();
    for (this.bindBlock(); this.index < this.masked._blocks.length; ++this.index, this.offset = 0) {
      if (fn()) return this.ok = true;
    }
    return this.ok = false;
  }
  pushLeftBeforeFilled() {
    return this._pushLeft(() => {
      if (this.block.isFixed || !this.block.value) return;
      this.offset = this.block.nearestInputPos(this.offset, DIRECTION.FORCE_LEFT);
      if (this.offset !== 0) return true;
    });
  }
  pushLeftBeforeInput() {
    // cases:
    // filled input: 00|
    // optional empty input: 00[]|
    // nested block: XX<[]>|
    return this._pushLeft(() => {
      if (this.block.isFixed) return;
      this.offset = this.block.nearestInputPos(this.offset, DIRECTION.LEFT);
      return true;
    });
  }
  pushLeftBeforeRequired() {
    return this._pushLeft(() => {
      if (this.block.isFixed || this.block.isOptional && !this.block.value) return;
      this.offset = this.block.nearestInputPos(this.offset, DIRECTION.LEFT);
      return true;
    });
  }
  pushRightBeforeFilled() {
    return this._pushRight(() => {
      if (this.block.isFixed || !this.block.value) return;
      this.offset = this.block.nearestInputPos(this.offset, DIRECTION.FORCE_RIGHT);
      if (this.offset !== this.block.value.length) return true;
    });
  }
  pushRightBeforeInput() {
    return this._pushRight(() => {
      if (this.block.isFixed) return;

      // const o = this.offset;
      this.offset = this.block.nearestInputPos(this.offset, DIRECTION.NONE);
      // HACK cases like (STILL DOES NOT WORK FOR NESTED)
      // aa|X
      // aa<X|[]>X_    - this will not work
      // if (o && o === this.offset && this.block instanceof PatternInputDefinition) continue;
      return true;
    });
  }
  pushRightBeforeRequired() {
    return this._pushRight(() => {
      if (this.block.isFixed || this.block.isOptional && !this.block.value) return;

      // TODO check |[*]XX_
      this.offset = this.block.nearestInputPos(this.offset, DIRECTION.NONE);
      return true;
    });
  }
}

/** Masking by RegExp */
class MaskedRegExp extends Masked {
  /**
    @override
    @param {Object} opts
  */
  _update(opts) {
    if (opts.mask) opts.validate = value => value.search(opts.mask) >= 0;
    super._update(opts);
  }
}
IMask.MaskedRegExp = MaskedRegExp;

const _excluded$8 = ["_blocks"];

/**
  Pattern mask
  @param {Object} opts
  @param {Object} opts.blocks
  @param {Object} opts.definitions
  @param {string} opts.placeholderChar
  @param {string} opts.displayChar
  @param {boolean} opts.lazy
*/
class MaskedPattern extends Masked {
  /** */

  /** */

  /** Single char for empty input */

  /** Single char for filled input */

  /** Show placeholder only when needed */

  constructor() {
    let opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // TODO type $Shape<MaskedPatternOptions>={} does not work
    opts.definitions = Object.assign({}, DEFAULT_INPUT_DEFINITIONS, opts.definitions);
    super(Object.assign({}, MaskedPattern.DEFAULTS, opts));
  }

  /**
    @override
    @param {Object} opts
  */
  _update() {
    let opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    opts.definitions = Object.assign({}, this.definitions, opts.definitions);
    super._update(opts);
    this._rebuildMask();
  }

  /** */
  _rebuildMask() {
    const defs = this.definitions;
    this._blocks = [];
    this._stops = [];
    this._maskedBlocks = {};
    let pattern = this.mask;
    if (!pattern || !defs) return;
    let unmaskingBlock = false;
    let optionalBlock = false;
    for (let i = 0; i < pattern.length; ++i) {
      var _defs$char, _defs$char2;
      if (this.blocks) {
        const p = pattern.slice(i);
        const bNames = Object.keys(this.blocks).filter(bName => p.indexOf(bName) === 0);
        // order by key length
        bNames.sort((a, b) => b.length - a.length);
        // use block name with max length
        const bName = bNames[0];
        if (bName) {
          // $FlowFixMe no ideas
          const maskedBlock = createMask(Object.assign({
            parent: this,
            lazy: this.lazy,
            eager: this.eager,
            placeholderChar: this.placeholderChar,
            displayChar: this.displayChar,
            overwrite: this.overwrite
          }, this.blocks[bName]));
          if (maskedBlock) {
            this._blocks.push(maskedBlock);

            // store block index
            if (!this._maskedBlocks[bName]) this._maskedBlocks[bName] = [];
            this._maskedBlocks[bName].push(this._blocks.length - 1);
          }
          i += bName.length - 1;
          continue;
        }
      }
      let char = pattern[i];
      let isInput = (char in defs);
      if (char === MaskedPattern.STOP_CHAR) {
        this._stops.push(this._blocks.length);
        continue;
      }
      if (char === '{' || char === '}') {
        unmaskingBlock = !unmaskingBlock;
        continue;
      }
      if (char === '[' || char === ']') {
        optionalBlock = !optionalBlock;
        continue;
      }
      if (char === MaskedPattern.ESCAPE_CHAR) {
        ++i;
        char = pattern[i];
        if (!char) break;
        isInput = false;
      }
      const maskOpts = (_defs$char = defs[char]) !== null && _defs$char !== void 0 && _defs$char.mask && !(((_defs$char2 = defs[char]) === null || _defs$char2 === void 0 ? void 0 : _defs$char2.mask.prototype) instanceof IMask.Masked) ? defs[char] : {
        mask: defs[char]
      };
      const def = isInput ? new PatternInputDefinition(Object.assign({
        parent: this,
        isOptional: optionalBlock,
        lazy: this.lazy,
        eager: this.eager,
        placeholderChar: this.placeholderChar,
        displayChar: this.displayChar
      }, maskOpts)) : new PatternFixedDefinition({
        char,
        eager: this.eager,
        isUnmasking: unmaskingBlock
      });
      this._blocks.push(def);
    }
  }

  /**
    @override
  */
  get state() {
    return Object.assign({}, super.state, {
      _blocks: this._blocks.map(b => b.state)
    });
  }
  set state(state) {
    const {
        _blocks
      } = state,
      maskedState = _objectWithoutPropertiesLoose$4(state, _excluded$8);
    this._blocks.forEach((b, bi) => b.state = _blocks[bi]);
    super.state = maskedState;
  }

  /**
    @override
  */
  reset() {
    super.reset();
    this._blocks.forEach(b => b.reset());
  }

  /**
    @override
  */
  get isComplete() {
    return this._blocks.every(b => b.isComplete);
  }

  /**
    @override
  */
  get isFilled() {
    return this._blocks.every(b => b.isFilled);
  }
  get isFixed() {
    return this._blocks.every(b => b.isFixed);
  }
  get isOptional() {
    return this._blocks.every(b => b.isOptional);
  }

  /**
    @override
  */
  doCommit() {
    this._blocks.forEach(b => b.doCommit());
    super.doCommit();
  }

  /**
    @override
  */
  get unmaskedValue() {
    return this._blocks.reduce((str, b) => str += b.unmaskedValue, '');
  }
  set unmaskedValue(unmaskedValue) {
    super.unmaskedValue = unmaskedValue;
  }

  /**
    @override
  */
  get value() {
    // TODO return _value when not in change?
    return this._blocks.reduce((str, b) => str += b.value, '');
  }
  set value(value) {
    super.value = value;
  }
  get displayValue() {
    return this._blocks.reduce((str, b) => str += b.displayValue, '');
  }

  /**
    @override
  */
  appendTail(tail) {
    return super.appendTail(tail).aggregate(this._appendPlaceholder());
  }

  /**
    @override
  */
  _appendEager() {
    var _this$_mapPosToBlock;
    const details = new ChangeDetails();
    let startBlockIndex = (_this$_mapPosToBlock = this._mapPosToBlock(this.value.length)) === null || _this$_mapPosToBlock === void 0 ? void 0 : _this$_mapPosToBlock.index;
    if (startBlockIndex == null) return details;

    // TODO test if it works for nested pattern masks
    if (this._blocks[startBlockIndex].isFilled) ++startBlockIndex;
    for (let bi = startBlockIndex; bi < this._blocks.length; ++bi) {
      const d = this._blocks[bi]._appendEager();
      if (!d.inserted) break;
      details.aggregate(d);
    }
    return details;
  }

  /**
    @override
  */
  _appendCharRaw(ch) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const blockIter = this._mapPosToBlock(this.value.length);
    const details = new ChangeDetails();
    if (!blockIter) return details;
    for (let bi = blockIter.index;; ++bi) {
      var _flags$_beforeTailSta, _flags$_beforeTailSta2;
      const block = this._blocks[bi];
      if (!block) break;
      const blockDetails = block._appendChar(ch, Object.assign({}, flags, {
        _beforeTailState: (_flags$_beforeTailSta = flags._beforeTailState) === null || _flags$_beforeTailSta === void 0 ? void 0 : (_flags$_beforeTailSta2 = _flags$_beforeTailSta._blocks) === null || _flags$_beforeTailSta2 === void 0 ? void 0 : _flags$_beforeTailSta2[bi]
      }));
      const skip = blockDetails.skip;
      details.aggregate(blockDetails);
      if (skip || blockDetails.rawInserted) break; // go next char
    }

    return details;
  }

  /**
    @override
  */
  extractTail() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    const chunkTail = new ChunksTailDetails();
    if (fromPos === toPos) return chunkTail;
    this._forEachBlocksInRange(fromPos, toPos, (b, bi, bFromPos, bToPos) => {
      const blockChunk = b.extractTail(bFromPos, bToPos);
      blockChunk.stop = this._findStopBefore(bi);
      blockChunk.from = this._blockStartPos(bi);
      if (blockChunk instanceof ChunksTailDetails) blockChunk.blockIndex = bi;
      chunkTail.extend(blockChunk);
    });
    return chunkTail;
  }

  /**
    @override
  */
  extractInput() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    let flags = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    if (fromPos === toPos) return '';
    let input = '';
    this._forEachBlocksInRange(fromPos, toPos, (b, _, fromPos, toPos) => {
      input += b.extractInput(fromPos, toPos, flags);
    });
    return input;
  }
  _findStopBefore(blockIndex) {
    let stopBefore;
    for (let si = 0; si < this._stops.length; ++si) {
      const stop = this._stops[si];
      if (stop <= blockIndex) stopBefore = stop;else break;
    }
    return stopBefore;
  }

  /** Appends placeholder depending on laziness */
  _appendPlaceholder(toBlockIndex) {
    const details = new ChangeDetails();
    if (this.lazy && toBlockIndex == null) return details;
    const startBlockIter = this._mapPosToBlock(this.value.length);
    if (!startBlockIter) return details;
    const startBlockIndex = startBlockIter.index;
    const endBlockIndex = toBlockIndex != null ? toBlockIndex : this._blocks.length;
    this._blocks.slice(startBlockIndex, endBlockIndex).forEach(b => {
      if (!b.lazy || toBlockIndex != null) {
        // $FlowFixMe `_blocks` may not be present
        const args = b._blocks != null ? [b._blocks.length] : [];
        const bDetails = b._appendPlaceholder(...args);
        this._value += bDetails.inserted;
        details.aggregate(bDetails);
      }
    });
    return details;
  }

  /** Finds block in pos */
  _mapPosToBlock(pos) {
    let accVal = '';
    for (let bi = 0; bi < this._blocks.length; ++bi) {
      const block = this._blocks[bi];
      const blockStartPos = accVal.length;
      accVal += block.value;
      if (pos <= accVal.length) {
        return {
          index: bi,
          offset: pos - blockStartPos
        };
      }
    }
  }

  /** */
  _blockStartPos(blockIndex) {
    return this._blocks.slice(0, blockIndex).reduce((pos, b) => pos += b.value.length, 0);
  }

  /** */
  _forEachBlocksInRange(fromPos) {
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    let fn = arguments.length > 2 ? arguments[2] : undefined;
    const fromBlockIter = this._mapPosToBlock(fromPos);
    if (fromBlockIter) {
      const toBlockIter = this._mapPosToBlock(toPos);
      // process first block
      const isSameBlock = toBlockIter && fromBlockIter.index === toBlockIter.index;
      const fromBlockStartPos = fromBlockIter.offset;
      const fromBlockEndPos = toBlockIter && isSameBlock ? toBlockIter.offset : this._blocks[fromBlockIter.index].value.length;
      fn(this._blocks[fromBlockIter.index], fromBlockIter.index, fromBlockStartPos, fromBlockEndPos);
      if (toBlockIter && !isSameBlock) {
        // process intermediate blocks
        for (let bi = fromBlockIter.index + 1; bi < toBlockIter.index; ++bi) {
          fn(this._blocks[bi], bi, 0, this._blocks[bi].value.length);
        }

        // process last block
        fn(this._blocks[toBlockIter.index], toBlockIter.index, 0, toBlockIter.offset);
      }
    }
  }

  /**
    @override
  */
  remove() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    const removeDetails = super.remove(fromPos, toPos);
    this._forEachBlocksInRange(fromPos, toPos, (b, _, bFromPos, bToPos) => {
      removeDetails.aggregate(b.remove(bFromPos, bToPos));
    });
    return removeDetails;
  }

  /**
    @override
  */
  nearestInputPos(cursorPos) {
    let direction = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DIRECTION.NONE;
    if (!this._blocks.length) return 0;
    const cursor = new PatternCursor(this, cursorPos);
    if (direction === DIRECTION.NONE) {
      // -------------------------------------------------
      // NONE should only go out from fixed to the right!
      // -------------------------------------------------
      if (cursor.pushRightBeforeInput()) return cursor.pos;
      cursor.popState();
      if (cursor.pushLeftBeforeInput()) return cursor.pos;
      return this.value.length;
    }

    // FORCE is only about a|* otherwise is 0
    if (direction === DIRECTION.LEFT || direction === DIRECTION.FORCE_LEFT) {
      // try to break fast when *|a
      if (direction === DIRECTION.LEFT) {
        cursor.pushRightBeforeFilled();
        if (cursor.ok && cursor.pos === cursorPos) return cursorPos;
        cursor.popState();
      }

      // forward flow
      cursor.pushLeftBeforeInput();
      cursor.pushLeftBeforeRequired();
      cursor.pushLeftBeforeFilled();

      // backward flow
      if (direction === DIRECTION.LEFT) {
        cursor.pushRightBeforeInput();
        cursor.pushRightBeforeRequired();
        if (cursor.ok && cursor.pos <= cursorPos) return cursor.pos;
        cursor.popState();
        if (cursor.ok && cursor.pos <= cursorPos) return cursor.pos;
        cursor.popState();
      }
      if (cursor.ok) return cursor.pos;
      if (direction === DIRECTION.FORCE_LEFT) return 0;
      cursor.popState();
      if (cursor.ok) return cursor.pos;
      cursor.popState();
      if (cursor.ok) return cursor.pos;

      // cursor.popState();
      // if (
      //   cursor.pushRightBeforeInput() &&
      //   // TODO HACK for lazy if has aligned left inside fixed and has came to the start - use start position
      //   (!this.lazy || this.extractInput())
      // ) return cursor.pos;

      return 0;
    }
    if (direction === DIRECTION.RIGHT || direction === DIRECTION.FORCE_RIGHT) {
      // forward flow
      cursor.pushRightBeforeInput();
      cursor.pushRightBeforeRequired();
      if (cursor.pushRightBeforeFilled()) return cursor.pos;
      if (direction === DIRECTION.FORCE_RIGHT) return this.value.length;

      // backward flow
      cursor.popState();
      if (cursor.ok) return cursor.pos;
      cursor.popState();
      if (cursor.ok) return cursor.pos;
      return this.nearestInputPos(cursorPos, DIRECTION.LEFT);
    }
    return cursorPos;
  }

  /**
    @override
  */
  totalInputPositions() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    let total = 0;
    this._forEachBlocksInRange(fromPos, toPos, (b, _, bFromPos, bToPos) => {
      total += b.totalInputPositions(bFromPos, bToPos);
    });
    return total;
  }

  /** Get block by name */
  maskedBlock(name) {
    return this.maskedBlocks(name)[0];
  }

  /** Get all blocks by name */
  maskedBlocks(name) {
    const indices = this._maskedBlocks[name];
    if (!indices) return [];
    return indices.map(gi => this._blocks[gi]);
  }
}
MaskedPattern.DEFAULTS = {
  lazy: true,
  placeholderChar: '_'
};
MaskedPattern.STOP_CHAR = '`';
MaskedPattern.ESCAPE_CHAR = '\\';
MaskedPattern.InputDefinition = PatternInputDefinition;
MaskedPattern.FixedDefinition = PatternFixedDefinition;
IMask.MaskedPattern = MaskedPattern;

/** Pattern which accepts ranges */
class MaskedRange extends MaskedPattern {
  /**
    Optionally sets max length of pattern.
    Used when pattern length is longer then `to` param length. Pads zeros at start in this case.
  */

  /** Min bound */

  /** Max bound */

  /** */

  get _matchFrom() {
    return this.maxLength - String(this.from).length;
  }

  /**
    @override
  */
  _update(opts) {
    // TODO type
    opts = Object.assign({
      to: this.to || 0,
      from: this.from || 0,
      maxLength: this.maxLength || 0
    }, opts);
    let maxLength = String(opts.to).length;
    if (opts.maxLength != null) maxLength = Math.max(maxLength, opts.maxLength);
    opts.maxLength = maxLength;
    const fromStr = String(opts.from).padStart(maxLength, '0');
    const toStr = String(opts.to).padStart(maxLength, '0');
    let sameCharsCount = 0;
    while (sameCharsCount < toStr.length && toStr[sameCharsCount] === fromStr[sameCharsCount]) ++sameCharsCount;
    opts.mask = toStr.slice(0, sameCharsCount).replace(/0/g, '\\0') + '0'.repeat(maxLength - sameCharsCount);
    super._update(opts);
  }

  /**
    @override
  */
  get isComplete() {
    return super.isComplete && Boolean(this.value);
  }
  boundaries(str) {
    let minstr = '';
    let maxstr = '';
    const [, placeholder, num] = str.match(/^(\D*)(\d*)(\D*)/) || [];
    if (num) {
      minstr = '0'.repeat(placeholder.length) + num;
      maxstr = '9'.repeat(placeholder.length) + num;
    }
    minstr = minstr.padEnd(this.maxLength, '0');
    maxstr = maxstr.padEnd(this.maxLength, '9');
    return [minstr, maxstr];
  }

  // TODO str is a single char everytime
  /**
    @override
  */
  doPrepare(ch) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let details;
    [ch, details] = normalizePrepare(super.doPrepare(ch.replace(/\D/g, ''), flags));
    if (!this.autofix || !ch) return ch;
    const fromStr = String(this.from).padStart(this.maxLength, '0');
    const toStr = String(this.to).padStart(this.maxLength, '0');
    let nextVal = this.value + ch;
    if (nextVal.length > this.maxLength) return '';
    const [minstr, maxstr] = this.boundaries(nextVal);
    if (Number(maxstr) < this.from) return fromStr[nextVal.length - 1];
    if (Number(minstr) > this.to) {
      if (this.autofix === 'pad' && nextVal.length < this.maxLength) {
        return ['', details.aggregate(this.append(fromStr[nextVal.length - 1] + ch, flags))];
      }
      return toStr[nextVal.length - 1];
    }
    return ch;
  }

  /**
    @override
  */
  doValidate() {
    const str = this.value;
    const firstNonZero = str.search(/[^0]/);
    if (firstNonZero === -1 && str.length <= this._matchFrom) return true;
    const [minstr, maxstr] = this.boundaries(str);
    return this.from <= Number(maxstr) && Number(minstr) <= this.to && super.doValidate(...arguments);
  }
}
IMask.MaskedRange = MaskedRange;

/** Date mask */
class MaskedDate extends MaskedPattern {
  /** Pattern mask for date according to {@link MaskedDate#format} */

  /** Start date */

  /** End date */

  /** */

  /**
    @param {Object} opts
  */
  constructor(opts) {
    super(Object.assign({}, MaskedDate.DEFAULTS, opts));
  }

  /**
    @override
  */
  _update(opts) {
    if (opts.mask === Date) delete opts.mask;
    if (opts.pattern) opts.mask = opts.pattern;
    const blocks = opts.blocks;
    opts.blocks = Object.assign({}, MaskedDate.GET_DEFAULT_BLOCKS());
    // adjust year block
    if (opts.min) opts.blocks.Y.from = opts.min.getFullYear();
    if (opts.max) opts.blocks.Y.to = opts.max.getFullYear();
    if (opts.min && opts.max && opts.blocks.Y.from === opts.blocks.Y.to) {
      opts.blocks.m.from = opts.min.getMonth() + 1;
      opts.blocks.m.to = opts.max.getMonth() + 1;
      if (opts.blocks.m.from === opts.blocks.m.to) {
        opts.blocks.d.from = opts.min.getDate();
        opts.blocks.d.to = opts.max.getDate();
      }
    }
    Object.assign(opts.blocks, this.blocks, blocks);

    // add autofix
    Object.keys(opts.blocks).forEach(bk => {
      const b = opts.blocks[bk];
      if (!('autofix' in b) && 'autofix' in opts) b.autofix = opts.autofix;
    });
    super._update(opts);
  }

  /**
    @override
  */
  doValidate() {
    const date = this.date;
    return super.doValidate(...arguments) && (!this.isComplete || this.isDateExist(this.value) && date != null && (this.min == null || this.min <= date) && (this.max == null || date <= this.max));
  }

  /** Checks if date is exists */
  isDateExist(str) {
    return this.format(this.parse(str, this), this).indexOf(str) >= 0;
  }

  /** Parsed Date */
  get date() {
    return this.typedValue;
  }
  set date(date) {
    this.typedValue = date;
  }

  /**
    @override
  */
  get typedValue() {
    return this.isComplete ? super.typedValue : null;
  }
  set typedValue(value) {
    super.typedValue = value;
  }

  /**
    @override
  */
  maskEquals(mask) {
    return mask === Date || super.maskEquals(mask);
  }
}
MaskedDate.DEFAULTS = {
  pattern: 'd{.}`m{.}`Y',
  format: date => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return [day, month, year].join('.');
  },
  parse: str => {
    const [day, month, year] = str.split('.');
    return new Date(year, month - 1, day);
  }
};
MaskedDate.GET_DEFAULT_BLOCKS = () => ({
  d: {
    mask: MaskedRange,
    from: 1,
    to: 31,
    maxLength: 2
  },
  m: {
    mask: MaskedRange,
    from: 1,
    to: 12,
    maxLength: 2
  },
  Y: {
    mask: MaskedRange,
    from: 1900,
    to: 9999
  }
});
IMask.MaskedDate = MaskedDate;

/**
  Generic element API to use with mask
  @interface
*/
class MaskElement {
  /** */

  /** */

  /** */

  /** Safely returns selection start */
  get selectionStart() {
    let start;
    try {
      start = this._unsafeSelectionStart;
    } catch (e) {}
    return start != null ? start : this.value.length;
  }

  /** Safely returns selection end */
  get selectionEnd() {
    let end;
    try {
      end = this._unsafeSelectionEnd;
    } catch (e) {}
    return end != null ? end : this.value.length;
  }

  /** Safely sets element selection */
  select(start, end) {
    if (start == null || end == null || start === this.selectionStart && end === this.selectionEnd) return;
    try {
      this._unsafeSelect(start, end);
    } catch (e) {}
  }

  /** Should be overriden in subclasses */
  _unsafeSelect(start, end) {}
  /** Should be overriden in subclasses */
  get isActive() {
    return false;
  }
  /** Should be overriden in subclasses */
  bindEvents(handlers) {}
  /** Should be overriden in subclasses */
  unbindEvents() {}
}
IMask.MaskElement = MaskElement;

/** Bridge between HTMLElement and {@link Masked} */
class HTMLMaskElement extends MaskElement {
  /** Mapping between HTMLElement events and mask internal events */

  /** HTMLElement to use mask on */

  /**
    @param {HTMLInputElement|HTMLTextAreaElement} input
  */
  constructor(input) {
    super();
    this.input = input;
    this._handlers = {};
  }

  /** */
  // $FlowFixMe https://github.com/facebook/flow/issues/2839
  get rootElement() {
    var _this$input$getRootNo, _this$input$getRootNo2, _this$input;
    return (_this$input$getRootNo = (_this$input$getRootNo2 = (_this$input = this.input).getRootNode) === null || _this$input$getRootNo2 === void 0 ? void 0 : _this$input$getRootNo2.call(_this$input)) !== null && _this$input$getRootNo !== void 0 ? _this$input$getRootNo : document;
  }

  /**
    Is element in focus
    @readonly
  */
  get isActive() {
    //$FlowFixMe
    return this.input === this.rootElement.activeElement;
  }

  /**
    Returns HTMLElement selection start
    @override
  */
  get _unsafeSelectionStart() {
    return this.input.selectionStart;
  }

  /**
    Returns HTMLElement selection end
    @override
  */
  get _unsafeSelectionEnd() {
    return this.input.selectionEnd;
  }

  /**
    Sets HTMLElement selection
    @override
  */
  _unsafeSelect(start, end) {
    this.input.setSelectionRange(start, end);
  }

  /**
    HTMLElement value
    @override
  */
  get value() {
    return this.input.value;
  }
  set value(value) {
    this.input.value = value;
  }

  /**
    Binds HTMLElement events to mask internal events
    @override
  */
  bindEvents(handlers) {
    Object.keys(handlers).forEach(event => this._toggleEventHandler(HTMLMaskElement.EVENTS_MAP[event], handlers[event]));
  }

  /**
    Unbinds HTMLElement events to mask internal events
    @override
  */
  unbindEvents() {
    Object.keys(this._handlers).forEach(event => this._toggleEventHandler(event));
  }

  /** */
  _toggleEventHandler(event, handler) {
    if (this._handlers[event]) {
      this.input.removeEventListener(event, this._handlers[event]);
      delete this._handlers[event];
    }
    if (handler) {
      this.input.addEventListener(event, handler);
      this._handlers[event] = handler;
    }
  }
}
HTMLMaskElement.EVENTS_MAP = {
  selectionChange: 'keydown',
  input: 'input',
  drop: 'drop',
  click: 'click',
  focus: 'focus',
  commit: 'blur'
};
IMask.HTMLMaskElement = HTMLMaskElement;

class HTMLContenteditableMaskElement extends HTMLMaskElement {
  /**
    Returns HTMLElement selection start
    @override
  */
  get _unsafeSelectionStart() {
    const root = this.rootElement;
    const selection = root.getSelection && root.getSelection();
    const anchorOffset = selection && selection.anchorOffset;
    const focusOffset = selection && selection.focusOffset;
    if (focusOffset == null || anchorOffset == null || anchorOffset < focusOffset) {
      return anchorOffset;
    }
    return focusOffset;
  }

  /**
    Returns HTMLElement selection end
    @override
  */
  get _unsafeSelectionEnd() {
    const root = this.rootElement;
    const selection = root.getSelection && root.getSelection();
    const anchorOffset = selection && selection.anchorOffset;
    const focusOffset = selection && selection.focusOffset;
    if (focusOffset == null || anchorOffset == null || anchorOffset > focusOffset) {
      return anchorOffset;
    }
    return focusOffset;
  }

  /**
    Sets HTMLElement selection
    @override
  */
  _unsafeSelect(start, end) {
    if (!this.rootElement.createRange) return;
    const range = this.rootElement.createRange();
    range.setStart(this.input.firstChild || this.input, start);
    range.setEnd(this.input.lastChild || this.input, end);
    const root = this.rootElement;
    const selection = root.getSelection && root.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  /**
    HTMLElement value
    @override
  */
  get value() {
    // $FlowFixMe
    return this.input.textContent;
  }
  set value(value) {
    this.input.textContent = value;
  }
}
IMask.HTMLContenteditableMaskElement = HTMLContenteditableMaskElement;

const _excluded$7 = ["mask"];

/** Listens to element events and controls changes between element and {@link Masked} */
let InputMask$1 = class InputMask {
  /**
    View element
    @readonly
  */

  /**
    Internal {@link Masked} model
    @readonly
  */

  /**
    @param {MaskElement|HTMLInputElement|HTMLTextAreaElement} el
    @param {Object} opts
  */
  constructor(el, opts) {
    this.el = el instanceof MaskElement ? el : el.isContentEditable && el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA' ? new HTMLContenteditableMaskElement(el) : new HTMLMaskElement(el);
    this.masked = createMask(opts);
    this._listeners = {};
    this._value = '';
    this._unmaskedValue = '';
    this._saveSelection = this._saveSelection.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onDrop = this._onDrop.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onClick = this._onClick.bind(this);
    this.alignCursor = this.alignCursor.bind(this);
    this.alignCursorFriendly = this.alignCursorFriendly.bind(this);
    this._bindEvents();

    // refresh
    this.updateValue();
    this._onChange();
  }

  /** Read or update mask */
  get mask() {
    return this.masked.mask;
  }
  maskEquals(mask) {
    var _this$masked;
    return mask == null || ((_this$masked = this.masked) === null || _this$masked === void 0 ? void 0 : _this$masked.maskEquals(mask));
  }
  set mask(mask) {
    if (this.maskEquals(mask)) return;

    // $FlowFixMe No ideas ... after update
    if (!(mask instanceof IMask.Masked) && this.masked.constructor === maskedClass(mask)) {
      this.masked.updateOptions({
        mask
      });
      return;
    }
    const masked = createMask({
      mask
    });
    masked.unmaskedValue = this.masked.unmaskedValue;
    this.masked = masked;
  }

  /** Raw value */
  get value() {
    return this._value;
  }
  set value(str) {
    if (this.value === str) return;
    this.masked.value = str;
    this.updateControl();
    this.alignCursor();
  }

  /** Unmasked value */
  get unmaskedValue() {
    return this._unmaskedValue;
  }
  set unmaskedValue(str) {
    if (this.unmaskedValue === str) return;
    this.masked.unmaskedValue = str;
    this.updateControl();
    this.alignCursor();
  }

  /** Typed unmasked value */
  get typedValue() {
    return this.masked.typedValue;
  }
  set typedValue(val) {
    if (this.masked.typedValueEquals(val)) return;
    this.masked.typedValue = val;
    this.updateControl();
    this.alignCursor();
  }

  /** Display value */
  get displayValue() {
    return this.masked.displayValue;
  }

  /**
    Starts listening to element events
    @protected
  */
  _bindEvents() {
    this.el.bindEvents({
      selectionChange: this._saveSelection,
      input: this._onInput,
      drop: this._onDrop,
      click: this._onClick,
      focus: this._onFocus,
      commit: this._onChange
    });
  }

  /**
    Stops listening to element events
    @protected
   */
  _unbindEvents() {
    if (this.el) this.el.unbindEvents();
  }

  /**
    Fires custom event
    @protected
   */
  _fireEvent(ev) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    const listeners = this._listeners[ev];
    if (!listeners) return;
    listeners.forEach(l => l(...args));
  }

  /**
    Current selection start
    @readonly
  */
  get selectionStart() {
    return this._cursorChanging ? this._changingCursorPos : this.el.selectionStart;
  }

  /** Current cursor position */
  get cursorPos() {
    return this._cursorChanging ? this._changingCursorPos : this.el.selectionEnd;
  }
  set cursorPos(pos) {
    if (!this.el || !this.el.isActive) return;
    this.el.select(pos, pos);
    this._saveSelection();
  }

  /**
    Stores current selection
    @protected
  */
  _saveSelection( /* ev */
  ) {
    if (this.displayValue !== this.el.value) {
      console.warn('Element value was changed outside of mask. Syncronize mask using `mask.updateValue()` to work properly.'); // eslint-disable-line no-console
    }

    this._selection = {
      start: this.selectionStart,
      end: this.cursorPos
    };
  }

  /** Syncronizes model value from view */
  updateValue() {
    this.masked.value = this.el.value;
    this._value = this.masked.value;
  }

  /** Syncronizes view from model value, fires change events */
  updateControl() {
    const newUnmaskedValue = this.masked.unmaskedValue;
    const newValue = this.masked.value;
    const newDisplayValue = this.displayValue;
    const isChanged = this.unmaskedValue !== newUnmaskedValue || this.value !== newValue;
    this._unmaskedValue = newUnmaskedValue;
    this._value = newValue;
    if (this.el.value !== newDisplayValue) this.el.value = newDisplayValue;
    if (isChanged) this._fireChangeEvents();
  }

  /** Updates options with deep equal check, recreates @{link Masked} model if mask type changes */
  updateOptions(opts) {
    const {
        mask
      } = opts,
      restOpts = _objectWithoutPropertiesLoose$4(opts, _excluded$7);
    const updateMask = !this.maskEquals(mask);
    const updateOpts = !objectIncludes(this.masked, restOpts);
    if (updateMask) this.mask = mask;
    if (updateOpts) this.masked.updateOptions(restOpts);
    if (updateMask || updateOpts) this.updateControl();
  }

  /** Updates cursor */
  updateCursor(cursorPos) {
    if (cursorPos == null) return;
    this.cursorPos = cursorPos;

    // also queue change cursor for mobile browsers
    this._delayUpdateCursor(cursorPos);
  }

  /**
    Delays cursor update to support mobile browsers
    @private
  */
  _delayUpdateCursor(cursorPos) {
    this._abortUpdateCursor();
    this._changingCursorPos = cursorPos;
    this._cursorChanging = setTimeout(() => {
      if (!this.el) return; // if was destroyed
      this.cursorPos = this._changingCursorPos;
      this._abortUpdateCursor();
    }, 10);
  }

  /**
    Fires custom events
    @protected
  */
  _fireChangeEvents() {
    this._fireEvent('accept', this._inputEvent);
    if (this.masked.isComplete) this._fireEvent('complete', this._inputEvent);
  }

  /**
    Aborts delayed cursor update
    @private
  */
  _abortUpdateCursor() {
    if (this._cursorChanging) {
      clearTimeout(this._cursorChanging);
      delete this._cursorChanging;
    }
  }

  /** Aligns cursor to nearest available position */
  alignCursor() {
    this.cursorPos = this.masked.nearestInputPos(this.masked.nearestInputPos(this.cursorPos, DIRECTION.LEFT));
  }

  /** Aligns cursor only if selection is empty */
  alignCursorFriendly() {
    if (this.selectionStart !== this.cursorPos) return; // skip if range is selected
    this.alignCursor();
  }

  /** Adds listener on custom event */
  on(ev, handler) {
    if (!this._listeners[ev]) this._listeners[ev] = [];
    this._listeners[ev].push(handler);
    return this;
  }

  /** Removes custom event listener */
  off(ev, handler) {
    if (!this._listeners[ev]) return this;
    if (!handler) {
      delete this._listeners[ev];
      return this;
    }
    const hIndex = this._listeners[ev].indexOf(handler);
    if (hIndex >= 0) this._listeners[ev].splice(hIndex, 1);
    return this;
  }

  /** Handles view input event */
  _onInput(e) {
    this._inputEvent = e;
    this._abortUpdateCursor();

    // fix strange IE behavior
    if (!this._selection) return this.updateValue();
    const details = new ActionDetails(
    // new state
    this.el.value, this.cursorPos,
    // old state
    this.displayValue, this._selection);
    const oldRawValue = this.masked.rawInputValue;
    const offset = this.masked.splice(details.startChangePos, details.removed.length, details.inserted, details.removeDirection, {
      input: true,
      raw: true
    }).offset;

    // force align in remove direction only if no input chars were removed
    // otherwise we still need to align with NONE (to get out from fixed symbols for instance)
    const removeDirection = oldRawValue === this.masked.rawInputValue ? details.removeDirection : DIRECTION.NONE;
    let cursorPos = this.masked.nearestInputPos(details.startChangePos + offset, removeDirection);
    if (removeDirection !== DIRECTION.NONE) cursorPos = this.masked.nearestInputPos(cursorPos, DIRECTION.NONE);
    this.updateControl();
    this.updateCursor(cursorPos);
    delete this._inputEvent;
  }

  /** Handles view change event and commits model value */
  _onChange() {
    if (this.displayValue !== this.el.value) {
      this.updateValue();
    }
    this.masked.doCommit();
    this.updateControl();
    this._saveSelection();
  }

  /** Handles view drop event, prevents by default */
  _onDrop(ev) {
    ev.preventDefault();
    ev.stopPropagation();
  }

  /** Restore last selection on focus */
  _onFocus(ev) {
    this.alignCursorFriendly();
  }

  /** Restore last selection on focus */
  _onClick(ev) {
    this.alignCursorFriendly();
  }

  /** Unbind view events and removes element reference */
  destroy() {
    this._unbindEvents();
    // $FlowFixMe why not do so?
    this._listeners.length = 0;
    // $FlowFixMe
    delete this.el;
  }
};
IMask.InputMask = InputMask$1;

/** Pattern which validates enum values */
class MaskedEnum extends MaskedPattern {
  /**
    @override
    @param {Object} opts
  */
  _update(opts) {
    // TODO type
    if (opts.enum) opts.mask = '*'.repeat(opts.enum[0].length);
    super._update(opts);
  }

  /**
    @override
  */
  doValidate() {
    return this.enum.some(e => e.indexOf(this.unmaskedValue) >= 0) && super.doValidate(...arguments);
  }
}
IMask.MaskedEnum = MaskedEnum;

/**
  Number mask
  @param {Object} opts
  @param {string} opts.radix - Single char
  @param {string} opts.thousandsSeparator - Single char
  @param {Array<string>} opts.mapToRadix - Array of single chars
  @param {number} opts.min
  @param {number} opts.max
  @param {number} opts.scale - Digits after point
  @param {boolean} opts.signed - Allow negative
  @param {boolean} opts.normalizeZeros - Flag to remove leading and trailing zeros in the end of editing
  @param {boolean} opts.padFractionalZeros - Flag to pad trailing zeros after point in the end of editing
*/
class MaskedNumber extends Masked {
  /** Single char */

  /** Single char */

  /** Array of single chars */

  /** */

  /** */

  /** Digits after point */

  /** */

  /** Flag to remove leading and trailing zeros in the end of editing */

  /** Flag to pad trailing zeros after point in the end of editing */

  constructor(opts) {
    super(Object.assign({}, MaskedNumber.DEFAULTS, opts));
  }

  /**
    @override
  */
  _update(opts) {
    super._update(opts);
    this._updateRegExps();
  }

  /** */
  _updateRegExps() {
    let start = '^' + (this.allowNegative ? '[+|\\-]?' : '');
    let mid = '\\d*';
    let end = (this.scale ? "(".concat(escapeRegExp(this.radix), "\\d{0,").concat(this.scale, "})?") : '') + '$';
    this._numberRegExp = new RegExp(start + mid + end);
    this._mapToRadixRegExp = new RegExp("[".concat(this.mapToRadix.map(escapeRegExp).join(''), "]"), 'g');
    this._thousandsSeparatorRegExp = new RegExp(escapeRegExp(this.thousandsSeparator), 'g');
  }

  /** */
  _removeThousandsSeparators(value) {
    return value.replace(this._thousandsSeparatorRegExp, '');
  }

  /** */
  _insertThousandsSeparators(value) {
    // https://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
    const parts = value.split(this.radix);
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsSeparator);
    return parts.join(this.radix);
  }

  /**
    @override
  */
  doPrepare(ch) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    ch = this._removeThousandsSeparators(this.scale && this.mapToRadix.length && (
    /*
      radix should be mapped when
      1) input is done from keyboard = flags.input && flags.raw
      2) unmasked value is set = !flags.input && !flags.raw
      and should not be mapped when
      1) value is set = flags.input && !flags.raw
      2) raw value is set = !flags.input && flags.raw
    */
    flags.input && flags.raw || !flags.input && !flags.raw) ? ch.replace(this._mapToRadixRegExp, this.radix) : ch);
    const [prepCh, details] = normalizePrepare(super.doPrepare(ch, flags));
    if (ch && !prepCh) details.skip = true;
    return [prepCh, details];
  }

  /** */
  _separatorsCount(to) {
    let extendOnSeparators = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let count = 0;
    for (let pos = 0; pos < to; ++pos) {
      if (this._value.indexOf(this.thousandsSeparator, pos) === pos) {
        ++count;
        if (extendOnSeparators) to += this.thousandsSeparator.length;
      }
    }
    return count;
  }

  /** */
  _separatorsCountFromSlice() {
    let slice = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this._value;
    return this._separatorsCount(this._removeThousandsSeparators(slice).length, true);
  }

  /**
    @override
  */
  extractInput() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    let flags = arguments.length > 2 ? arguments[2] : undefined;
    [fromPos, toPos] = this._adjustRangeWithSeparators(fromPos, toPos);
    return this._removeThousandsSeparators(super.extractInput(fromPos, toPos, flags));
  }

  /**
    @override
  */
  _appendCharRaw(ch) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (!this.thousandsSeparator) return super._appendCharRaw(ch, flags);
    const prevBeforeTailValue = flags.tail && flags._beforeTailState ? flags._beforeTailState._value : this._value;
    const prevBeforeTailSeparatorsCount = this._separatorsCountFromSlice(prevBeforeTailValue);
    this._value = this._removeThousandsSeparators(this.value);
    const appendDetails = super._appendCharRaw(ch, flags);
    this._value = this._insertThousandsSeparators(this._value);
    const beforeTailValue = flags.tail && flags._beforeTailState ? flags._beforeTailState._value : this._value;
    const beforeTailSeparatorsCount = this._separatorsCountFromSlice(beforeTailValue);
    appendDetails.tailShift += (beforeTailSeparatorsCount - prevBeforeTailSeparatorsCount) * this.thousandsSeparator.length;
    appendDetails.skip = !appendDetails.rawInserted && ch === this.thousandsSeparator;
    return appendDetails;
  }

  /** */
  _findSeparatorAround(pos) {
    if (this.thousandsSeparator) {
      const searchFrom = pos - this.thousandsSeparator.length + 1;
      const separatorPos = this.value.indexOf(this.thousandsSeparator, searchFrom);
      if (separatorPos <= pos) return separatorPos;
    }
    return -1;
  }
  _adjustRangeWithSeparators(from, to) {
    const separatorAroundFromPos = this._findSeparatorAround(from);
    if (separatorAroundFromPos >= 0) from = separatorAroundFromPos;
    const separatorAroundToPos = this._findSeparatorAround(to);
    if (separatorAroundToPos >= 0) to = separatorAroundToPos + this.thousandsSeparator.length;
    return [from, to];
  }

  /**
    @override
  */
  remove() {
    let fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
    [fromPos, toPos] = this._adjustRangeWithSeparators(fromPos, toPos);
    const valueBeforePos = this.value.slice(0, fromPos);
    const valueAfterPos = this.value.slice(toPos);
    const prevBeforeTailSeparatorsCount = this._separatorsCount(valueBeforePos.length);
    this._value = this._insertThousandsSeparators(this._removeThousandsSeparators(valueBeforePos + valueAfterPos));
    const beforeTailSeparatorsCount = this._separatorsCountFromSlice(valueBeforePos);
    return new ChangeDetails({
      tailShift: (beforeTailSeparatorsCount - prevBeforeTailSeparatorsCount) * this.thousandsSeparator.length
    });
  }

  /**
    @override
  */
  nearestInputPos(cursorPos, direction) {
    if (!this.thousandsSeparator) return cursorPos;
    switch (direction) {
      case DIRECTION.NONE:
      case DIRECTION.LEFT:
      case DIRECTION.FORCE_LEFT:
        {
          const separatorAtLeftPos = this._findSeparatorAround(cursorPos - 1);
          if (separatorAtLeftPos >= 0) {
            const separatorAtLeftEndPos = separatorAtLeftPos + this.thousandsSeparator.length;
            if (cursorPos < separatorAtLeftEndPos || this.value.length <= separatorAtLeftEndPos || direction === DIRECTION.FORCE_LEFT) {
              return separatorAtLeftPos;
            }
          }
          break;
        }
      case DIRECTION.RIGHT:
      case DIRECTION.FORCE_RIGHT:
        {
          const separatorAtRightPos = this._findSeparatorAround(cursorPos);
          if (separatorAtRightPos >= 0) {
            return separatorAtRightPos + this.thousandsSeparator.length;
          }
        }
    }
    return cursorPos;
  }

  /**
    @override
  */
  doValidate(flags) {
    // validate as string
    let valid = Boolean(this._removeThousandsSeparators(this.value).match(this._numberRegExp));
    if (valid) {
      // validate as number
      const number = this.number;
      valid = valid && !isNaN(number) && (
      // check min bound for negative values
      this.min == null || this.min >= 0 || this.min <= this.number) && (
      // check max bound for positive values
      this.max == null || this.max <= 0 || this.number <= this.max);
    }
    return valid && super.doValidate(flags);
  }

  /**
    @override
  */
  doCommit() {
    if (this.value) {
      const number = this.number;
      let validnum = number;

      // check bounds
      if (this.min != null) validnum = Math.max(validnum, this.min);
      if (this.max != null) validnum = Math.min(validnum, this.max);
      if (validnum !== number) this.unmaskedValue = this.doFormat(validnum);
      let formatted = this.value;
      if (this.normalizeZeros) formatted = this._normalizeZeros(formatted);
      if (this.padFractionalZeros && this.scale > 0) formatted = this._padFractionalZeros(formatted);
      this._value = formatted;
    }
    super.doCommit();
  }

  /** */
  _normalizeZeros(value) {
    const parts = this._removeThousandsSeparators(value).split(this.radix);

    // remove leading zeros
    parts[0] = parts[0].replace(/^(\D*)(0*)(\d*)/, (match, sign, zeros, num) => sign + num);
    // add leading zero
    if (value.length && !/\d$/.test(parts[0])) parts[0] = parts[0] + '0';
    if (parts.length > 1) {
      parts[1] = parts[1].replace(/0*$/, ''); // remove trailing zeros
      if (!parts[1].length) parts.length = 1; // remove fractional
    }

    return this._insertThousandsSeparators(parts.join(this.radix));
  }

  /** */
  _padFractionalZeros(value) {
    if (!value) return value;
    const parts = value.split(this.radix);
    if (parts.length < 2) parts.push('');
    parts[1] = parts[1].padEnd(this.scale, '0');
    return parts.join(this.radix);
  }

  /** */
  doSkipInvalid(ch) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let checkTail = arguments.length > 2 ? arguments[2] : undefined;
    const dropFractional = this.scale === 0 && ch !== this.thousandsSeparator && (ch === this.radix || ch === MaskedNumber.UNMASKED_RADIX || this.mapToRadix.includes(ch));
    return super.doSkipInvalid(ch, flags, checkTail) && !dropFractional;
  }

  /**
    @override
  */
  get unmaskedValue() {
    return this._removeThousandsSeparators(this._normalizeZeros(this.value)).replace(this.radix, MaskedNumber.UNMASKED_RADIX);
  }
  set unmaskedValue(unmaskedValue) {
    super.unmaskedValue = unmaskedValue;
  }

  /**
    @override
  */
  get typedValue() {
    return this.doParse(this.unmaskedValue);
  }
  set typedValue(n) {
    this.rawInputValue = this.doFormat(n).replace(MaskedNumber.UNMASKED_RADIX, this.radix);
  }

  /** Parsed Number */
  get number() {
    return this.typedValue;
  }
  set number(number) {
    this.typedValue = number;
  }

  /**
    Is negative allowed
    @readonly
  */
  get allowNegative() {
    return this.signed || this.min != null && this.min < 0 || this.max != null && this.max < 0;
  }

  /**
    @override
  */
  typedValueEquals(value) {
    // handle  0 -> '' case (typed = 0 even if value = '')
    // for details see https://github.com/uNmAnNeR/imaskjs/issues/134
    return (super.typedValueEquals(value) || MaskedNumber.EMPTY_VALUES.includes(value) && MaskedNumber.EMPTY_VALUES.includes(this.typedValue)) && !(value === 0 && this.value === '');
  }
}
MaskedNumber.UNMASKED_RADIX = '.';
MaskedNumber.DEFAULTS = {
  radix: ',',
  thousandsSeparator: '',
  mapToRadix: [MaskedNumber.UNMASKED_RADIX],
  scale: 2,
  signed: false,
  normalizeZeros: true,
  padFractionalZeros: false,
  parse: Number,
  format: n => n.toLocaleString('en-US', {
    useGrouping: false,
    maximumFractionDigits: 20
  })
};
MaskedNumber.EMPTY_VALUES = [...Masked.EMPTY_VALUES, 0];
IMask.MaskedNumber = MaskedNumber;

/** Masking by custom Function */
class MaskedFunction extends Masked {
  /**
    @override
    @param {Object} opts
  */
  _update(opts) {
    if (opts.mask) opts.validate = opts.mask;
    super._update(opts);
  }
}
IMask.MaskedFunction = MaskedFunction;

const _excluded$6 = ["compiledMasks", "currentMaskRef", "currentMask"],
  _excluded2$1 = ["mask"];
/** Dynamic mask for choosing apropriate mask in run-time */
class MaskedDynamic extends Masked {
  /** Currently chosen mask */

  /** Compliled {@link Masked} options */

  /** Chooses {@link Masked} depending on input value */

  /**
    @param {Object} opts
  */
  constructor(opts) {
    super(Object.assign({}, MaskedDynamic.DEFAULTS, opts));
    this.currentMask = null;
  }

  /**
    @override
  */
  _update(opts) {
    super._update(opts);
    if ('mask' in opts) {
      // mask could be totally dynamic with only `dispatch` option
      this.compiledMasks = Array.isArray(opts.mask) ? opts.mask.map(m => createMask(m)) : [];

      // this.currentMask = this.doDispatch(''); // probably not needed but lets see
    }
  }

  /**
    @override
  */
  _appendCharRaw(ch) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const details = this._applyDispatch(ch, flags);
    if (this.currentMask) {
      details.aggregate(this.currentMask._appendChar(ch, this.currentMaskFlags(flags)));
    }
    return details;
  }
  _applyDispatch() {
    let appended = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let tail = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
    const prevValueBeforeTail = flags.tail && flags._beforeTailState != null ? flags._beforeTailState._value : this.value;
    const inputValue = this.rawInputValue;
    const insertValue = flags.tail && flags._beforeTailState != null ?
    // $FlowFixMe - tired to fight with type system
    flags._beforeTailState._rawInputValue : inputValue;
    const tailValue = inputValue.slice(insertValue.length);
    const prevMask = this.currentMask;
    const details = new ChangeDetails();
    const prevMaskState = prevMask === null || prevMask === void 0 ? void 0 : prevMask.state;

    // clone flags to prevent overwriting `_beforeTailState`
    this.currentMask = this.doDispatch(appended, Object.assign({}, flags), tail);

    // restore state after dispatch
    if (this.currentMask) {
      if (this.currentMask !== prevMask) {
        // if mask changed reapply input
        this.currentMask.reset();
        if (insertValue) {
          // $FlowFixMe - it's ok, we don't change current mask above
          const d = this.currentMask.append(insertValue, {
            raw: true
          });
          details.tailShift = d.inserted.length - prevValueBeforeTail.length;
        }
        if (tailValue) {
          // $FlowFixMe - it's ok, we don't change current mask above
          details.tailShift += this.currentMask.append(tailValue, {
            raw: true,
            tail: true
          }).tailShift;
        }
      } else {
        // Dispatch can do something bad with state, so
        // restore prev mask state
        this.currentMask.state = prevMaskState;
      }
    }
    return details;
  }
  _appendPlaceholder() {
    const details = this._applyDispatch(...arguments);
    if (this.currentMask) {
      details.aggregate(this.currentMask._appendPlaceholder());
    }
    return details;
  }

  /**
   @override
  */
  _appendEager() {
    const details = this._applyDispatch(...arguments);
    if (this.currentMask) {
      details.aggregate(this.currentMask._appendEager());
    }
    return details;
  }
  appendTail(tail) {
    const details = new ChangeDetails();
    if (tail) details.aggregate(this._applyDispatch('', {}, tail));
    return details.aggregate(this.currentMask ? this.currentMask.appendTail(tail) : super.appendTail(tail));
  }
  currentMaskFlags(flags) {
    var _flags$_beforeTailSta, _flags$_beforeTailSta2;
    return Object.assign({}, flags, {
      _beforeTailState: ((_flags$_beforeTailSta = flags._beforeTailState) === null || _flags$_beforeTailSta === void 0 ? void 0 : _flags$_beforeTailSta.currentMaskRef) === this.currentMask && ((_flags$_beforeTailSta2 = flags._beforeTailState) === null || _flags$_beforeTailSta2 === void 0 ? void 0 : _flags$_beforeTailSta2.currentMask) || flags._beforeTailState
    });
  }

  /**
    @override
  */
  doDispatch(appended) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let tail = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
    return this.dispatch(appended, this, flags, tail);
  }

  /**
    @override
  */
  doValidate(flags) {
    return super.doValidate(flags) && (!this.currentMask || this.currentMask.doValidate(this.currentMaskFlags(flags)));
  }

  /**
    @override
  */
  doPrepare(str) {
    let flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let [s, details] = normalizePrepare(super.doPrepare(str, flags));
    if (this.currentMask) {
      let currentDetails;
      [s, currentDetails] = normalizePrepare(super.doPrepare(s, this.currentMaskFlags(flags)));
      details = details.aggregate(currentDetails);
    }
    return [s, details];
  }

  /**
    @override
  */
  reset() {
    var _this$currentMask;
    (_this$currentMask = this.currentMask) === null || _this$currentMask === void 0 ? void 0 : _this$currentMask.reset();
    this.compiledMasks.forEach(m => m.reset());
  }

  /**
    @override
  */
  get value() {
    return this.currentMask ? this.currentMask.value : '';
  }
  set value(value) {
    super.value = value;
  }

  /**
    @override
  */
  get unmaskedValue() {
    return this.currentMask ? this.currentMask.unmaskedValue : '';
  }
  set unmaskedValue(unmaskedValue) {
    super.unmaskedValue = unmaskedValue;
  }

  /**
    @override
  */
  get typedValue() {
    return this.currentMask ? this.currentMask.typedValue : '';
  }

  // probably typedValue should not be used with dynamic
  set typedValue(value) {
    let unmaskedValue = String(value);

    // double check it
    if (this.currentMask) {
      this.currentMask.typedValue = value;
      unmaskedValue = this.currentMask.unmaskedValue;
    }
    this.unmaskedValue = unmaskedValue;
  }
  get displayValue() {
    return this.currentMask ? this.currentMask.displayValue : '';
  }

  /**
    @override
  */
  get isComplete() {
    var _this$currentMask2;
    return Boolean((_this$currentMask2 = this.currentMask) === null || _this$currentMask2 === void 0 ? void 0 : _this$currentMask2.isComplete);
  }

  /**
    @override
  */
  get isFilled() {
    var _this$currentMask3;
    return Boolean((_this$currentMask3 = this.currentMask) === null || _this$currentMask3 === void 0 ? void 0 : _this$currentMask3.isFilled);
  }

  /**
    @override
  */
  remove() {
    const details = new ChangeDetails();
    if (this.currentMask) {
      details.aggregate(this.currentMask.remove(...arguments))
      // update with dispatch
      .aggregate(this._applyDispatch());
    }
    return details;
  }

  /**
    @override
  */
  get state() {
    var _this$currentMask4;
    return Object.assign({}, super.state, {
      _rawInputValue: this.rawInputValue,
      compiledMasks: this.compiledMasks.map(m => m.state),
      currentMaskRef: this.currentMask,
      currentMask: (_this$currentMask4 = this.currentMask) === null || _this$currentMask4 === void 0 ? void 0 : _this$currentMask4.state
    });
  }
  set state(state) {
    const {
        compiledMasks,
        currentMaskRef,
        currentMask
      } = state,
      maskedState = _objectWithoutPropertiesLoose$4(state, _excluded$6);
    this.compiledMasks.forEach((m, mi) => m.state = compiledMasks[mi]);
    if (currentMaskRef != null) {
      this.currentMask = currentMaskRef;
      this.currentMask.state = currentMask;
    }
    super.state = maskedState;
  }

  /**
    @override
  */
  extractInput() {
    return this.currentMask ? this.currentMask.extractInput(...arguments) : '';
  }

  /**
    @override
  */
  extractTail() {
    return this.currentMask ? this.currentMask.extractTail(...arguments) : super.extractTail(...arguments);
  }

  /**
    @override
  */
  doCommit() {
    if (this.currentMask) this.currentMask.doCommit();
    super.doCommit();
  }

  /**
    @override
  */
  nearestInputPos() {
    return this.currentMask ? this.currentMask.nearestInputPos(...arguments) : super.nearestInputPos(...arguments);
  }
  get overwrite() {
    return this.currentMask ? this.currentMask.overwrite : super.overwrite;
  }
  set overwrite(overwrite) {
    console.warn('"overwrite" option is not available in dynamic mask, use this option in siblings');
  }
  get eager() {
    return this.currentMask ? this.currentMask.eager : super.eager;
  }
  set eager(eager) {
    console.warn('"eager" option is not available in dynamic mask, use this option in siblings');
  }
  get skipInvalid() {
    return this.currentMask ? this.currentMask.skipInvalid : super.skipInvalid;
  }
  set skipInvalid(skipInvalid) {
    if (this.isInitialized || skipInvalid !== Masked.DEFAULTS.skipInvalid) {
      console.warn('"skipInvalid" option is not available in dynamic mask, use this option in siblings');
    }
  }

  /**
    @override
  */
  maskEquals(mask) {
    return Array.isArray(mask) && this.compiledMasks.every((m, mi) => {
      if (!mask[mi]) return;
      const _mask$mi = mask[mi],
        {
          mask: oldMask
        } = _mask$mi,
        restOpts = _objectWithoutPropertiesLoose$4(_mask$mi, _excluded2$1);
      return objectIncludes(m, restOpts) && m.maskEquals(oldMask);
    });
  }

  /**
    @override
  */
  typedValueEquals(value) {
    var _this$currentMask5;
    return Boolean((_this$currentMask5 = this.currentMask) === null || _this$currentMask5 === void 0 ? void 0 : _this$currentMask5.typedValueEquals(value));
  }
}
MaskedDynamic.DEFAULTS = {
  dispatch: (appended, masked, flags, tail) => {
    if (!masked.compiledMasks.length) return;
    const inputValue = masked.rawInputValue;

    // simulate input
    const inputs = masked.compiledMasks.map((m, index) => {
      const isCurrent = masked.currentMask === m;
      const startInputPos = isCurrent ? m.value.length : m.nearestInputPos(m.value.length, DIRECTION.FORCE_LEFT);
      if (m.rawInputValue !== inputValue) {
        m.reset();
        m.append(inputValue, {
          raw: true
        });
      } else if (!isCurrent) {
        m.remove(startInputPos);
      }
      m.append(appended, masked.currentMaskFlags(flags));
      m.appendTail(tail);
      return {
        index,
        weight: m.rawInputValue.length,
        totalInputPositions: m.totalInputPositions(0, Math.max(startInputPos, m.nearestInputPos(m.value.length, DIRECTION.FORCE_LEFT)))
      };
    });

    // pop masks with longer values first
    inputs.sort((i1, i2) => i2.weight - i1.weight || i2.totalInputPositions - i1.totalInputPositions);
    return masked.compiledMasks[inputs[0].index];
  }
};
IMask.MaskedDynamic = MaskedDynamic;

/** Mask pipe source and destination types */
const PIPE_TYPE = {
  MASKED: 'value',
  UNMASKED: 'unmaskedValue',
  TYPED: 'typedValue'
};

/** Creates new pipe function depending on mask type, source and destination options */
function createPipe(mask) {
  let from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : PIPE_TYPE.MASKED;
  let to = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : PIPE_TYPE.MASKED;
  const masked = createMask(mask);
  return value => masked.runIsolated(m => {
    m[from] = value;
    return m[to];
  });
}

/** Pipes value through mask depending on mask type, source and destination options */
function pipe$1(value) {
  for (var _len = arguments.length, pipeArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    pipeArgs[_key - 1] = arguments[_key];
  }
  return createPipe(...pipeArgs)(value);
}
IMask.PIPE_TYPE = PIPE_TYPE;
IMask.createPipe = createPipe;
IMask.pipe = pipe$1;

try {
  globalThis.IMask = IMask;
} catch (e) {}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  Object.defineProperty(subClass, "prototype", {
    writable: false
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _objectWithoutPropertiesLoose$3(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties$1(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose$3(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }

  return _assertThisInitialized(self);
}

function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();

  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived),
        result;

    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

var propTypesExports = {};
var propTypes = {
  get exports(){ return propTypesExports; },
  set exports(v){ propTypesExports = v; },
};

var reactIsExports = {};
var reactIs = {
  get exports(){ return reactIsExports; },
  set exports(v){ reactIsExports = v; },
};

var reactIs_production_min = {};

/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_production_min;

function requireReactIs_production_min () {
	if (hasRequiredReactIs_production_min) return reactIs_production_min;
	hasRequiredReactIs_production_min = 1;
var b="function"===typeof Symbol&&Symbol.for,c=b?Symbol.for("react.element"):60103,d=b?Symbol.for("react.portal"):60106,e=b?Symbol.for("react.fragment"):60107,f=b?Symbol.for("react.strict_mode"):60108,g=b?Symbol.for("react.profiler"):60114,h=b?Symbol.for("react.provider"):60109,k=b?Symbol.for("react.context"):60110,l=b?Symbol.for("react.async_mode"):60111,m=b?Symbol.for("react.concurrent_mode"):60111,n=b?Symbol.for("react.forward_ref"):60112,p=b?Symbol.for("react.suspense"):60113,q=b?
	Symbol.for("react.suspense_list"):60120,r=b?Symbol.for("react.memo"):60115,t=b?Symbol.for("react.lazy"):60116,v=b?Symbol.for("react.block"):60121,w=b?Symbol.for("react.fundamental"):60117,x=b?Symbol.for("react.responder"):60118,y=b?Symbol.for("react.scope"):60119;
	function z(a){if("object"===typeof a&&null!==a){var u=a.$$typeof;switch(u){case c:switch(a=a.type,a){case l:case m:case e:case g:case f:case p:return a;default:switch(a=a&&a.$$typeof,a){case k:case n:case t:case r:case h:return a;default:return u}}case d:return u}}}function A(a){return z(a)===m}reactIs_production_min.AsyncMode=l;reactIs_production_min.ConcurrentMode=m;reactIs_production_min.ContextConsumer=k;reactIs_production_min.ContextProvider=h;reactIs_production_min.Element=c;reactIs_production_min.ForwardRef=n;reactIs_production_min.Fragment=e;reactIs_production_min.Lazy=t;reactIs_production_min.Memo=r;reactIs_production_min.Portal=d;
	reactIs_production_min.Profiler=g;reactIs_production_min.StrictMode=f;reactIs_production_min.Suspense=p;reactIs_production_min.isAsyncMode=function(a){return A(a)||z(a)===l};reactIs_production_min.isConcurrentMode=A;reactIs_production_min.isContextConsumer=function(a){return z(a)===k};reactIs_production_min.isContextProvider=function(a){return z(a)===h};reactIs_production_min.isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===c};reactIs_production_min.isForwardRef=function(a){return z(a)===n};reactIs_production_min.isFragment=function(a){return z(a)===e};reactIs_production_min.isLazy=function(a){return z(a)===t};
	reactIs_production_min.isMemo=function(a){return z(a)===r};reactIs_production_min.isPortal=function(a){return z(a)===d};reactIs_production_min.isProfiler=function(a){return z(a)===g};reactIs_production_min.isStrictMode=function(a){return z(a)===f};reactIs_production_min.isSuspense=function(a){return z(a)===p};
	reactIs_production_min.isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===e||a===m||a===g||a===f||a===p||a===q||"object"===typeof a&&null!==a&&(a.$$typeof===t||a.$$typeof===r||a.$$typeof===h||a.$$typeof===k||a.$$typeof===n||a.$$typeof===w||a.$$typeof===x||a.$$typeof===y||a.$$typeof===v)};reactIs_production_min.typeOf=z;
	return reactIs_production_min;
}

var reactIs_development = {};

/** @license React v16.13.1
 * react-is.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_development;

function requireReactIs_development () {
	if (hasRequiredReactIs_development) return reactIs_development;
	hasRequiredReactIs_development = 1;



	if (process.env.NODE_ENV !== "production") {
	  (function() {

	// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
	// nor polyfill, then a plain number is used for performance.
	var hasSymbol = typeof Symbol === 'function' && Symbol.for;
	var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
	var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
	var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
	var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
	var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
	var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
	var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
	// (unstable) APIs that have been removed. Can we remove the symbols?

	var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
	var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
	var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
	var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
	var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
	var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
	var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
	var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
	var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
	var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
	var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

	function isValidElementType(type) {
	  return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
	  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
	}

	function typeOf(object) {
	  if (typeof object === 'object' && object !== null) {
	    var $$typeof = object.$$typeof;

	    switch ($$typeof) {
	      case REACT_ELEMENT_TYPE:
	        var type = object.type;

	        switch (type) {
	          case REACT_ASYNC_MODE_TYPE:
	          case REACT_CONCURRENT_MODE_TYPE:
	          case REACT_FRAGMENT_TYPE:
	          case REACT_PROFILER_TYPE:
	          case REACT_STRICT_MODE_TYPE:
	          case REACT_SUSPENSE_TYPE:
	            return type;

	          default:
	            var $$typeofType = type && type.$$typeof;

	            switch ($$typeofType) {
	              case REACT_CONTEXT_TYPE:
	              case REACT_FORWARD_REF_TYPE:
	              case REACT_LAZY_TYPE:
	              case REACT_MEMO_TYPE:
	              case REACT_PROVIDER_TYPE:
	                return $$typeofType;

	              default:
	                return $$typeof;
	            }

	        }

	      case REACT_PORTAL_TYPE:
	        return $$typeof;
	    }
	  }

	  return undefined;
	} // AsyncMode is deprecated along with isAsyncMode

	var AsyncMode = REACT_ASYNC_MODE_TYPE;
	var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
	var ContextConsumer = REACT_CONTEXT_TYPE;
	var ContextProvider = REACT_PROVIDER_TYPE;
	var Element = REACT_ELEMENT_TYPE;
	var ForwardRef = REACT_FORWARD_REF_TYPE;
	var Fragment = REACT_FRAGMENT_TYPE;
	var Lazy = REACT_LAZY_TYPE;
	var Memo = REACT_MEMO_TYPE;
	var Portal = REACT_PORTAL_TYPE;
	var Profiler = REACT_PROFILER_TYPE;
	var StrictMode = REACT_STRICT_MODE_TYPE;
	var Suspense = REACT_SUSPENSE_TYPE;
	var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

	function isAsyncMode(object) {
	  {
	    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
	      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

	      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
	    }
	  }

	  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
	}
	function isConcurrentMode(object) {
	  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
	}
	function isContextConsumer(object) {
	  return typeOf(object) === REACT_CONTEXT_TYPE;
	}
	function isContextProvider(object) {
	  return typeOf(object) === REACT_PROVIDER_TYPE;
	}
	function isElement(object) {
	  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
	}
	function isForwardRef(object) {
	  return typeOf(object) === REACT_FORWARD_REF_TYPE;
	}
	function isFragment(object) {
	  return typeOf(object) === REACT_FRAGMENT_TYPE;
	}
	function isLazy(object) {
	  return typeOf(object) === REACT_LAZY_TYPE;
	}
	function isMemo(object) {
	  return typeOf(object) === REACT_MEMO_TYPE;
	}
	function isPortal(object) {
	  return typeOf(object) === REACT_PORTAL_TYPE;
	}
	function isProfiler(object) {
	  return typeOf(object) === REACT_PROFILER_TYPE;
	}
	function isStrictMode(object) {
	  return typeOf(object) === REACT_STRICT_MODE_TYPE;
	}
	function isSuspense(object) {
	  return typeOf(object) === REACT_SUSPENSE_TYPE;
	}

	reactIs_development.AsyncMode = AsyncMode;
	reactIs_development.ConcurrentMode = ConcurrentMode;
	reactIs_development.ContextConsumer = ContextConsumer;
	reactIs_development.ContextProvider = ContextProvider;
	reactIs_development.Element = Element;
	reactIs_development.ForwardRef = ForwardRef;
	reactIs_development.Fragment = Fragment;
	reactIs_development.Lazy = Lazy;
	reactIs_development.Memo = Memo;
	reactIs_development.Portal = Portal;
	reactIs_development.Profiler = Profiler;
	reactIs_development.StrictMode = StrictMode;
	reactIs_development.Suspense = Suspense;
	reactIs_development.isAsyncMode = isAsyncMode;
	reactIs_development.isConcurrentMode = isConcurrentMode;
	reactIs_development.isContextConsumer = isContextConsumer;
	reactIs_development.isContextProvider = isContextProvider;
	reactIs_development.isElement = isElement;
	reactIs_development.isForwardRef = isForwardRef;
	reactIs_development.isFragment = isFragment;
	reactIs_development.isLazy = isLazy;
	reactIs_development.isMemo = isMemo;
	reactIs_development.isPortal = isPortal;
	reactIs_development.isProfiler = isProfiler;
	reactIs_development.isStrictMode = isStrictMode;
	reactIs_development.isSuspense = isSuspense;
	reactIs_development.isValidElementType = isValidElementType;
	reactIs_development.typeOf = typeOf;
	  })();
	}
	return reactIs_development;
}

var hasRequiredReactIs;

function requireReactIs () {
	if (hasRequiredReactIs) return reactIsExports;
	hasRequiredReactIs = 1;
	(function (module) {

		if (process.env.NODE_ENV === 'production') {
		  module.exports = requireReactIs_production_min();
		} else {
		  module.exports = requireReactIs_development();
		}
} (reactIs));
	return reactIsExports;
}

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/

var objectAssign;
var hasRequiredObjectAssign;

function requireObjectAssign () {
	if (hasRequiredObjectAssign) return objectAssign;
	hasRequiredObjectAssign = 1;
	/* eslint-disable no-unused-vars */
	var getOwnPropertySymbols = Object.getOwnPropertySymbols;
	var hasOwnProperty = Object.prototype.hasOwnProperty;
	var propIsEnumerable = Object.prototype.propertyIsEnumerable;

	function toObject(val) {
		if (val === null || val === undefined) {
			throw new TypeError('Object.assign cannot be called with null or undefined');
		}

		return Object(val);
	}

	function shouldUseNative() {
		try {
			if (!Object.assign) {
				return false;
			}

			// Detect buggy property enumeration order in older V8 versions.

			// https://bugs.chromium.org/p/v8/issues/detail?id=4118
			var test1 = new String('abc');  // eslint-disable-line no-new-wrappers
			test1[5] = 'de';
			if (Object.getOwnPropertyNames(test1)[0] === '5') {
				return false;
			}

			// https://bugs.chromium.org/p/v8/issues/detail?id=3056
			var test2 = {};
			for (var i = 0; i < 10; i++) {
				test2['_' + String.fromCharCode(i)] = i;
			}
			var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
				return test2[n];
			});
			if (order2.join('') !== '0123456789') {
				return false;
			}

			// https://bugs.chromium.org/p/v8/issues/detail?id=3056
			var test3 = {};
			'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
				test3[letter] = letter;
			});
			if (Object.keys(Object.assign({}, test3)).join('') !==
					'abcdefghijklmnopqrst') {
				return false;
			}

			return true;
		} catch (err) {
			// We don't expect any of the above to throw, but better to be safe.
			return false;
		}
	}

	objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
		var from;
		var to = toObject(target);
		var symbols;

		for (var s = 1; s < arguments.length; s++) {
			from = Object(arguments[s]);

			for (var key in from) {
				if (hasOwnProperty.call(from, key)) {
					to[key] = from[key];
				}
			}

			if (getOwnPropertySymbols) {
				symbols = getOwnPropertySymbols(from);
				for (var i = 0; i < symbols.length; i++) {
					if (propIsEnumerable.call(from, symbols[i])) {
						to[symbols[i]] = from[symbols[i]];
					}
				}
			}
		}

		return to;
	};
	return objectAssign;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret_1;
var hasRequiredReactPropTypesSecret;

function requireReactPropTypesSecret () {
	if (hasRequiredReactPropTypesSecret) return ReactPropTypesSecret_1;
	hasRequiredReactPropTypesSecret = 1;

	var ReactPropTypesSecret = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';

	ReactPropTypesSecret_1 = ReactPropTypesSecret;
	return ReactPropTypesSecret_1;
}

var has;
var hasRequiredHas;

function requireHas () {
	if (hasRequiredHas) return has;
	hasRequiredHas = 1;
	has = Function.call.bind(Object.prototype.hasOwnProperty);
	return has;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var checkPropTypes_1;
var hasRequiredCheckPropTypes;

function requireCheckPropTypes () {
	if (hasRequiredCheckPropTypes) return checkPropTypes_1;
	hasRequiredCheckPropTypes = 1;

	var printWarning = function() {};

	if (process.env.NODE_ENV !== 'production') {
	  var ReactPropTypesSecret = requireReactPropTypesSecret();
	  var loggedTypeFailures = {};
	  var has = requireHas();

	  printWarning = function(text) {
	    var message = 'Warning: ' + text;
	    if (typeof console !== 'undefined') {
	      console.error(message);
	    }
	    try {
	      // --- Welcome to debugging React ---
	      // This error was thrown as a convenience so that you can use this stack
	      // to find the callsite that caused this warning to fire.
	      throw new Error(message);
	    } catch (x) { /**/ }
	  };
	}

	/**
	 * Assert that the values match with the type specs.
	 * Error messages are memorized and will only be shown once.
	 *
	 * @param {object} typeSpecs Map of name to a ReactPropType
	 * @param {object} values Runtime values that need to be type-checked
	 * @param {string} location e.g. "prop", "context", "child context"
	 * @param {string} componentName Name of the component for error messages.
	 * @param {?Function} getStack Returns the component stack.
	 * @private
	 */
	function checkPropTypes(typeSpecs, values, location, componentName, getStack) {
	  if (process.env.NODE_ENV !== 'production') {
	    for (var typeSpecName in typeSpecs) {
	      if (has(typeSpecs, typeSpecName)) {
	        var error;
	        // Prop type validation may throw. In case they do, we don't want to
	        // fail the render phase where it didn't fail before. So we log it.
	        // After these have been cleaned up, we'll let them throw.
	        try {
	          // This is intentionally an invariant that gets caught. It's the same
	          // behavior as without this statement except with a better message.
	          if (typeof typeSpecs[typeSpecName] !== 'function') {
	            var err = Error(
	              (componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' +
	              'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.' +
	              'This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`.'
	            );
	            err.name = 'Invariant Violation';
	            throw err;
	          }
	          error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret);
	        } catch (ex) {
	          error = ex;
	        }
	        if (error && !(error instanceof Error)) {
	          printWarning(
	            (componentName || 'React class') + ': type specification of ' +
	            location + ' `' + typeSpecName + '` is invalid; the type checker ' +
	            'function must return `null` or an `Error` but returned a ' + typeof error + '. ' +
	            'You may have forgotten to pass an argument to the type checker ' +
	            'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' +
	            'shape all require an argument).'
	          );
	        }
	        if (error instanceof Error && !(error.message in loggedTypeFailures)) {
	          // Only monitor this failure once because there tends to be a lot of the
	          // same error.
	          loggedTypeFailures[error.message] = true;

	          var stack = getStack ? getStack() : '';

	          printWarning(
	            'Failed ' + location + ' type: ' + error.message + (stack != null ? stack : '')
	          );
	        }
	      }
	    }
	  }
	}

	/**
	 * Resets warning cache when testing.
	 *
	 * @private
	 */
	checkPropTypes.resetWarningCache = function() {
	  if (process.env.NODE_ENV !== 'production') {
	    loggedTypeFailures = {};
	  }
	};

	checkPropTypes_1 = checkPropTypes;
	return checkPropTypes_1;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var factoryWithTypeCheckers;
var hasRequiredFactoryWithTypeCheckers;

function requireFactoryWithTypeCheckers () {
	if (hasRequiredFactoryWithTypeCheckers) return factoryWithTypeCheckers;
	hasRequiredFactoryWithTypeCheckers = 1;

	var ReactIs = requireReactIs();
	var assign = requireObjectAssign();

	var ReactPropTypesSecret = requireReactPropTypesSecret();
	var has = requireHas();
	var checkPropTypes = requireCheckPropTypes();

	var printWarning = function() {};

	if (process.env.NODE_ENV !== 'production') {
	  printWarning = function(text) {
	    var message = 'Warning: ' + text;
	    if (typeof console !== 'undefined') {
	      console.error(message);
	    }
	    try {
	      // --- Welcome to debugging React ---
	      // This error was thrown as a convenience so that you can use this stack
	      // to find the callsite that caused this warning to fire.
	      throw new Error(message);
	    } catch (x) {}
	  };
	}

	function emptyFunctionThatReturnsNull() {
	  return null;
	}

	factoryWithTypeCheckers = function(isValidElement, throwOnDirectAccess) {
	  /* global Symbol */
	  var ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
	  var FAUX_ITERATOR_SYMBOL = '@@iterator'; // Before Symbol spec.

	  /**
	   * Returns the iterator method function contained on the iterable object.
	   *
	   * Be sure to invoke the function with the iterable as context:
	   *
	   *     var iteratorFn = getIteratorFn(myIterable);
	   *     if (iteratorFn) {
	   *       var iterator = iteratorFn.call(myIterable);
	   *       ...
	   *     }
	   *
	   * @param {?object} maybeIterable
	   * @return {?function}
	   */
	  function getIteratorFn(maybeIterable) {
	    var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);
	    if (typeof iteratorFn === 'function') {
	      return iteratorFn;
	    }
	  }

	  /**
	   * Collection of methods that allow declaration and validation of props that are
	   * supplied to React components. Example usage:
	   *
	   *   var Props = require('ReactPropTypes');
	   *   var MyArticle = React.createClass({
	   *     propTypes: {
	   *       // An optional string prop named "description".
	   *       description: Props.string,
	   *
	   *       // A required enum prop named "category".
	   *       category: Props.oneOf(['News','Photos']).isRequired,
	   *
	   *       // A prop named "dialog" that requires an instance of Dialog.
	   *       dialog: Props.instanceOf(Dialog).isRequired
	   *     },
	   *     render: function() { ... }
	   *   });
	   *
	   * A more formal specification of how these methods are used:
	   *
	   *   type := array|bool|func|object|number|string|oneOf([...])|instanceOf(...)
	   *   decl := ReactPropTypes.{type}(.isRequired)?
	   *
	   * Each and every declaration produces a function with the same signature. This
	   * allows the creation of custom validation functions. For example:
	   *
	   *  var MyLink = React.createClass({
	   *    propTypes: {
	   *      // An optional string or URI prop named "href".
	   *      href: function(props, propName, componentName) {
	   *        var propValue = props[propName];
	   *        if (propValue != null && typeof propValue !== 'string' &&
	   *            !(propValue instanceof URI)) {
	   *          return new Error(
	   *            'Expected a string or an URI for ' + propName + ' in ' +
	   *            componentName
	   *          );
	   *        }
	   *      }
	   *    },
	   *    render: function() {...}
	   *  });
	   *
	   * @internal
	   */

	  var ANONYMOUS = '<<anonymous>>';

	  // Important!
	  // Keep this list in sync with production version in `./factoryWithThrowingShims.js`.
	  var ReactPropTypes = {
	    array: createPrimitiveTypeChecker('array'),
	    bigint: createPrimitiveTypeChecker('bigint'),
	    bool: createPrimitiveTypeChecker('boolean'),
	    func: createPrimitiveTypeChecker('function'),
	    number: createPrimitiveTypeChecker('number'),
	    object: createPrimitiveTypeChecker('object'),
	    string: createPrimitiveTypeChecker('string'),
	    symbol: createPrimitiveTypeChecker('symbol'),

	    any: createAnyTypeChecker(),
	    arrayOf: createArrayOfTypeChecker,
	    element: createElementTypeChecker(),
	    elementType: createElementTypeTypeChecker(),
	    instanceOf: createInstanceTypeChecker,
	    node: createNodeChecker(),
	    objectOf: createObjectOfTypeChecker,
	    oneOf: createEnumTypeChecker,
	    oneOfType: createUnionTypeChecker,
	    shape: createShapeTypeChecker,
	    exact: createStrictShapeTypeChecker,
	  };

	  /**
	   * inlined Object.is polyfill to avoid requiring consumers ship their own
	   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
	   */
	  /*eslint-disable no-self-compare*/
	  function is(x, y) {
	    // SameValue algorithm
	    if (x === y) {
	      // Steps 1-5, 7-10
	      // Steps 6.b-6.e: +0 != -0
	      return x !== 0 || 1 / x === 1 / y;
	    } else {
	      // Step 6.a: NaN == NaN
	      return x !== x && y !== y;
	    }
	  }
	  /*eslint-enable no-self-compare*/

	  /**
	   * We use an Error-like object for backward compatibility as people may call
	   * PropTypes directly and inspect their output. However, we don't use real
	   * Errors anymore. We don't inspect their stack anyway, and creating them
	   * is prohibitively expensive if they are created too often, such as what
	   * happens in oneOfType() for any type before the one that matched.
	   */
	  function PropTypeError(message, data) {
	    this.message = message;
	    this.data = data && typeof data === 'object' ? data: {};
	    this.stack = '';
	  }
	  // Make `instanceof Error` still work for returned errors.
	  PropTypeError.prototype = Error.prototype;

	  function createChainableTypeChecker(validate) {
	    if (process.env.NODE_ENV !== 'production') {
	      var manualPropTypeCallCache = {};
	      var manualPropTypeWarningCount = 0;
	    }
	    function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
	      componentName = componentName || ANONYMOUS;
	      propFullName = propFullName || propName;

	      if (secret !== ReactPropTypesSecret) {
	        if (throwOnDirectAccess) {
	          // New behavior only for users of `prop-types` package
	          var err = new Error(
	            'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
	            'Use `PropTypes.checkPropTypes()` to call them. ' +
	            'Read more at http://fb.me/use-check-prop-types'
	          );
	          err.name = 'Invariant Violation';
	          throw err;
	        } else if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
	          // Old behavior for people using React.PropTypes
	          var cacheKey = componentName + ':' + propName;
	          if (
	            !manualPropTypeCallCache[cacheKey] &&
	            // Avoid spamming the console because they are often not actionable except for lib authors
	            manualPropTypeWarningCount < 3
	          ) {
	            printWarning(
	              'You are manually calling a React.PropTypes validation ' +
	              'function for the `' + propFullName + '` prop on `' + componentName + '`. This is deprecated ' +
	              'and will throw in the standalone `prop-types` package. ' +
	              'You may be seeing this warning due to a third-party PropTypes ' +
	              'library. See https://fb.me/react-warning-dont-call-proptypes ' + 'for details.'
	            );
	            manualPropTypeCallCache[cacheKey] = true;
	            manualPropTypeWarningCount++;
	          }
	        }
	      }
	      if (props[propName] == null) {
	        if (isRequired) {
	          if (props[propName] === null) {
	            return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required ' + ('in `' + componentName + '`, but its value is `null`.'));
	          }
	          return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required in ' + ('`' + componentName + '`, but its value is `undefined`.'));
	        }
	        return null;
	      } else {
	        return validate(props, propName, componentName, location, propFullName);
	      }
	    }

	    var chainedCheckType = checkType.bind(null, false);
	    chainedCheckType.isRequired = checkType.bind(null, true);

	    return chainedCheckType;
	  }

	  function createPrimitiveTypeChecker(expectedType) {
	    function validate(props, propName, componentName, location, propFullName, secret) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== expectedType) {
	        // `propValue` being instance of, say, date/regexp, pass the 'object'
	        // check, but we can offer a more precise error message here rather than
	        // 'of type `object`'.
	        var preciseType = getPreciseType(propValue);

	        return new PropTypeError(
	          'Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + preciseType + '` supplied to `' + componentName + '`, expected ') + ('`' + expectedType + '`.'),
	          {expectedType: expectedType}
	        );
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createAnyTypeChecker() {
	    return createChainableTypeChecker(emptyFunctionThatReturnsNull);
	  }

	  function createArrayOfTypeChecker(typeChecker) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (typeof typeChecker !== 'function') {
	        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside arrayOf.');
	      }
	      var propValue = props[propName];
	      if (!Array.isArray(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an array.'));
	      }
	      for (var i = 0; i < propValue.length; i++) {
	        var error = typeChecker(propValue, i, componentName, location, propFullName + '[' + i + ']', ReactPropTypesSecret);
	        if (error instanceof Error) {
	          return error;
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createElementTypeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      if (!isValidElement(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createElementTypeTypeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      if (!ReactIs.isValidElementType(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement type.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createInstanceTypeChecker(expectedClass) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (!(props[propName] instanceof expectedClass)) {
	        var expectedClassName = expectedClass.name || ANONYMOUS;
	        var actualClassName = getClassName(props[propName]);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + actualClassName + '` supplied to `' + componentName + '`, expected ') + ('instance of `' + expectedClassName + '`.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createEnumTypeChecker(expectedValues) {
	    if (!Array.isArray(expectedValues)) {
	      if (process.env.NODE_ENV !== 'production') {
	        if (arguments.length > 1) {
	          printWarning(
	            'Invalid arguments supplied to oneOf, expected an array, got ' + arguments.length + ' arguments. ' +
	            'A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z]).'
	          );
	        } else {
	          printWarning('Invalid argument supplied to oneOf, expected an array.');
	        }
	      }
	      return emptyFunctionThatReturnsNull;
	    }

	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      for (var i = 0; i < expectedValues.length; i++) {
	        if (is(propValue, expectedValues[i])) {
	          return null;
	        }
	      }

	      var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
	        var type = getPreciseType(value);
	        if (type === 'symbol') {
	          return String(value);
	        }
	        return value;
	      });
	      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of value `' + String(propValue) + '` ' + ('supplied to `' + componentName + '`, expected one of ' + valuesString + '.'));
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createObjectOfTypeChecker(typeChecker) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (typeof typeChecker !== 'function') {
	        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside objectOf.');
	      }
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an object.'));
	      }
	      for (var key in propValue) {
	        if (has(propValue, key)) {
	          var error = typeChecker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	          if (error instanceof Error) {
	            return error;
	          }
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createUnionTypeChecker(arrayOfTypeCheckers) {
	    if (!Array.isArray(arrayOfTypeCheckers)) {
	      process.env.NODE_ENV !== 'production' ? printWarning('Invalid argument supplied to oneOfType, expected an instance of array.') : void 0;
	      return emptyFunctionThatReturnsNull;
	    }

	    for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
	      var checker = arrayOfTypeCheckers[i];
	      if (typeof checker !== 'function') {
	        printWarning(
	          'Invalid argument supplied to oneOfType. Expected an array of check functions, but ' +
	          'received ' + getPostfixForTypeWarning(checker) + ' at index ' + i + '.'
	        );
	        return emptyFunctionThatReturnsNull;
	      }
	    }

	    function validate(props, propName, componentName, location, propFullName) {
	      var expectedTypes = [];
	      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
	        var checker = arrayOfTypeCheckers[i];
	        var checkerResult = checker(props, propName, componentName, location, propFullName, ReactPropTypesSecret);
	        if (checkerResult == null) {
	          return null;
	        }
	        if (checkerResult.data && has(checkerResult.data, 'expectedType')) {
	          expectedTypes.push(checkerResult.data.expectedType);
	        }
	      }
	      var expectedTypesMessage = (expectedTypes.length > 0) ? ', expected one of type [' + expectedTypes.join(', ') + ']': '';
	      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`' + expectedTypesMessage + '.'));
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createNodeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (!isNode(props[propName])) {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`, expected a ReactNode.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function invalidValidatorError(componentName, location, propFullName, key, type) {
	    return new PropTypeError(
	      (componentName || 'React class') + ': ' + location + ' type `' + propFullName + '.' + key + '` is invalid; ' +
	      'it must be a function, usually from the `prop-types` package, but received `' + type + '`.'
	    );
	  }

	  function createShapeTypeChecker(shapeTypes) {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
	      }
	      for (var key in shapeTypes) {
	        var checker = shapeTypes[key];
	        if (typeof checker !== 'function') {
	          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
	        }
	        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	        if (error) {
	          return error;
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createStrictShapeTypeChecker(shapeTypes) {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
	      }
	      // We need to check all keys in case some are required but missing from props.
	      var allKeys = assign({}, props[propName], shapeTypes);
	      for (var key in allKeys) {
	        var checker = shapeTypes[key];
	        if (has(shapeTypes, key) && typeof checker !== 'function') {
	          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
	        }
	        if (!checker) {
	          return new PropTypeError(
	            'Invalid ' + location + ' `' + propFullName + '` key `' + key + '` supplied to `' + componentName + '`.' +
	            '\nBad object: ' + JSON.stringify(props[propName], null, '  ') +
	            '\nValid keys: ' + JSON.stringify(Object.keys(shapeTypes), null, '  ')
	          );
	        }
	        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	        if (error) {
	          return error;
	        }
	      }
	      return null;
	    }

	    return createChainableTypeChecker(validate);
	  }

	  function isNode(propValue) {
	    switch (typeof propValue) {
	      case 'number':
	      case 'string':
	      case 'undefined':
	        return true;
	      case 'boolean':
	        return !propValue;
	      case 'object':
	        if (Array.isArray(propValue)) {
	          return propValue.every(isNode);
	        }
	        if (propValue === null || isValidElement(propValue)) {
	          return true;
	        }

	        var iteratorFn = getIteratorFn(propValue);
	        if (iteratorFn) {
	          var iterator = iteratorFn.call(propValue);
	          var step;
	          if (iteratorFn !== propValue.entries) {
	            while (!(step = iterator.next()).done) {
	              if (!isNode(step.value)) {
	                return false;
	              }
	            }
	          } else {
	            // Iterator will provide entry [k,v] tuples rather than values.
	            while (!(step = iterator.next()).done) {
	              var entry = step.value;
	              if (entry) {
	                if (!isNode(entry[1])) {
	                  return false;
	                }
	              }
	            }
	          }
	        } else {
	          return false;
	        }

	        return true;
	      default:
	        return false;
	    }
	  }

	  function isSymbol(propType, propValue) {
	    // Native Symbol.
	    if (propType === 'symbol') {
	      return true;
	    }

	    // falsy value can't be a Symbol
	    if (!propValue) {
	      return false;
	    }

	    // 19.4.3.5 Symbol.prototype[@@toStringTag] === 'Symbol'
	    if (propValue['@@toStringTag'] === 'Symbol') {
	      return true;
	    }

	    // Fallback for non-spec compliant Symbols which are polyfilled.
	    if (typeof Symbol === 'function' && propValue instanceof Symbol) {
	      return true;
	    }

	    return false;
	  }

	  // Equivalent of `typeof` but with special handling for array and regexp.
	  function getPropType(propValue) {
	    var propType = typeof propValue;
	    if (Array.isArray(propValue)) {
	      return 'array';
	    }
	    if (propValue instanceof RegExp) {
	      // Old webkits (at least until Android 4.0) return 'function' rather than
	      // 'object' for typeof a RegExp. We'll normalize this here so that /bla/
	      // passes PropTypes.object.
	      return 'object';
	    }
	    if (isSymbol(propType, propValue)) {
	      return 'symbol';
	    }
	    return propType;
	  }

	  // This handles more types than `getPropType`. Only used for error messages.
	  // See `createPrimitiveTypeChecker`.
	  function getPreciseType(propValue) {
	    if (typeof propValue === 'undefined' || propValue === null) {
	      return '' + propValue;
	    }
	    var propType = getPropType(propValue);
	    if (propType === 'object') {
	      if (propValue instanceof Date) {
	        return 'date';
	      } else if (propValue instanceof RegExp) {
	        return 'regexp';
	      }
	    }
	    return propType;
	  }

	  // Returns a string that is postfixed to a warning about an invalid type.
	  // For example, "undefined" or "of type array"
	  function getPostfixForTypeWarning(value) {
	    var type = getPreciseType(value);
	    switch (type) {
	      case 'array':
	      case 'object':
	        return 'an ' + type;
	      case 'boolean':
	      case 'date':
	      case 'regexp':
	        return 'a ' + type;
	      default:
	        return type;
	    }
	  }

	  // Returns class name of the object, if any.
	  function getClassName(propValue) {
	    if (!propValue.constructor || !propValue.constructor.name) {
	      return ANONYMOUS;
	    }
	    return propValue.constructor.name;
	  }

	  ReactPropTypes.checkPropTypes = checkPropTypes;
	  ReactPropTypes.resetWarningCache = checkPropTypes.resetWarningCache;
	  ReactPropTypes.PropTypes = ReactPropTypes;

	  return ReactPropTypes;
	};
	return factoryWithTypeCheckers;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var factoryWithThrowingShims;
var hasRequiredFactoryWithThrowingShims;

function requireFactoryWithThrowingShims () {
	if (hasRequiredFactoryWithThrowingShims) return factoryWithThrowingShims;
	hasRequiredFactoryWithThrowingShims = 1;

	var ReactPropTypesSecret = requireReactPropTypesSecret();

	function emptyFunction() {}
	function emptyFunctionWithReset() {}
	emptyFunctionWithReset.resetWarningCache = emptyFunction;

	factoryWithThrowingShims = function() {
	  function shim(props, propName, componentName, location, propFullName, secret) {
	    if (secret === ReactPropTypesSecret) {
	      // It is still safe when called from React.
	      return;
	    }
	    var err = new Error(
	      'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
	      'Use PropTypes.checkPropTypes() to call them. ' +
	      'Read more at http://fb.me/use-check-prop-types'
	    );
	    err.name = 'Invariant Violation';
	    throw err;
	  }	  shim.isRequired = shim;
	  function getShim() {
	    return shim;
	  }	  // Important!
	  // Keep this list in sync with production version in `./factoryWithTypeCheckers.js`.
	  var ReactPropTypes = {
	    array: shim,
	    bigint: shim,
	    bool: shim,
	    func: shim,
	    number: shim,
	    object: shim,
	    string: shim,
	    symbol: shim,

	    any: shim,
	    arrayOf: getShim,
	    element: shim,
	    elementType: shim,
	    instanceOf: getShim,
	    node: shim,
	    objectOf: getShim,
	    oneOf: getShim,
	    oneOfType: getShim,
	    shape: getShim,
	    exact: getShim,

	    checkPropTypes: emptyFunctionWithReset,
	    resetWarningCache: emptyFunction
	  };

	  ReactPropTypes.PropTypes = ReactPropTypes;

	  return ReactPropTypes;
	};
	return factoryWithThrowingShims;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

if (process.env.NODE_ENV !== 'production') {
  var ReactIs = requireReactIs();

  // By explicitly using `prop-types` you are opting into new development behavior.
  // http://fb.me/prop-types-in-prod
  var throwOnDirectAccess = true;
  propTypes.exports = requireFactoryWithTypeCheckers()(ReactIs.isElement, throwOnDirectAccess);
} else {
  // By explicitly using `prop-types` you are opting into new production behavior.
  // http://fb.me/prop-types-in-prod
  propTypes.exports = requireFactoryWithThrowingShims()();
}

var MASK_PROPS = {
  // common
  mask: propTypesExports.oneOfType([propTypesExports.array, propTypesExports.func, propTypesExports.string, propTypesExports.instanceOf(RegExp), propTypesExports.oneOf([Date, Number, IMask.Masked]), propTypesExports.instanceOf(IMask.Masked)]),
  value: propTypesExports.any,
  unmask: propTypesExports.oneOfType([propTypesExports.bool, propTypesExports.oneOf(['typed'])]),
  prepare: propTypesExports.func,
  validate: propTypesExports.func,
  commit: propTypesExports.func,
  overwrite: propTypesExports.oneOfType([propTypesExports.bool, propTypesExports.oneOf(['shift'])]),
  eager: propTypesExports.bool,
  // events
  onAccept: propTypesExports.func,
  onComplete: propTypesExports.func,
  // pattern
  placeholderChar: propTypesExports.string,
  lazy: propTypesExports.bool,
  definitions: propTypesExports.object,
  blocks: propTypesExports.object,
  // date
  pattern: propTypesExports.string,
  format: propTypesExports.func,
  parse: propTypesExports.func,
  autofix: propTypesExports.oneOfType([propTypesExports.bool, propTypesExports.oneOf(['pad'])]),
  // number
  radix: propTypesExports.string,
  thousandsSeparator: propTypesExports.string,
  mapToRadix: propTypesExports.arrayOf(propTypesExports.string),
  scale: propTypesExports.number,
  signed: propTypesExports.bool,
  normalizeZeros: propTypesExports.bool,
  padFractionalZeros: propTypesExports.bool,
  min: propTypesExports.oneOfType([propTypesExports.number, propTypesExports.instanceOf(Date)]),
  max: propTypesExports.oneOfType([propTypesExports.number, propTypesExports.instanceOf(Date)]),
  // dynamic
  dispatch: propTypesExports.func,
  // ref
  inputRef: propTypesExports.oneOfType([propTypesExports.func, propTypesExports.shape({
    current: propTypesExports.object
  })])
};
var MASK_PROPS_NAMES = Object.keys(MASK_PROPS);
var NON_MASK_OPTIONS_PROPS_NAMES = ['value', 'unmask', 'onAccept', 'onComplete', 'inputRef'];
var MASK_OPTIONS_PROPS_NAMES = MASK_PROPS_NAMES.filter(function (pName) {
  return NON_MASK_OPTIONS_PROPS_NAMES.indexOf(pName) < 0;
});
// TODO
// 1. seems like it's wrong to have Opts as only mask options. Other component/input props should also be there. Where is "unmask" prop for instance?
// 2. Unmask should be infered from Opts (see https://github.com/uNmAnNeR/imaskjs/issues/554#issuecomment-1114014010)
function IMaskMixin(ComposedComponent) {
  var MaskedComponent = /*#__PURE__*/function (_React$Component) {
    _inherits(MaskedComponent, _React$Component);

    var _super = _createSuper(MaskedComponent);

    function MaskedComponent(props) {
      var _this;

      _classCallCheck(this, MaskedComponent);

      _this = _super.call(this, props);
      _this._inputRef = _this._inputRef.bind(_assertThisInitialized(_this));
      return _this;
    }

    _createClass(MaskedComponent, [{
      key: "componentDidMount",
      value: function componentDidMount() {
        if (!this.props.mask) return;
        this.initMask();
      }
    }, {
      key: "componentDidUpdate",
      value: function componentDidUpdate() {
        var props = this.props;

        var maskOptions = this._extractMaskOptionsFromProps(props);

        if (maskOptions.mask) {
          if (this.maskRef) {
            this.maskRef.updateOptions(maskOptions);
            if ('value' in props) this.maskValue = props.value;
          } else {
            this.initMask(maskOptions);
          }
        } else {
          this.destroyMask();
          if ('value' in props) this.element.value = props.value;
        }
      }
    }, {
      key: "componentWillUnmount",
      value: function componentWillUnmount() {
        this.destroyMask();
      }
    }, {
      key: "_inputRef",
      value: function _inputRef(el) {
        this.element = el;

        if (this.props.inputRef) {
          if (Object.prototype.hasOwnProperty.call(this.props.inputRef, 'current')) this.props.inputRef.current = el;else this.props.inputRef(el);
        }
      }
    }, {
      key: "initMask",
      value: function initMask() {
        var maskOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this._extractMaskOptionsFromProps(this.props);
        this.maskRef = IMask(this.element, maskOptions).on('accept', this._onAccept.bind(this)).on('complete', this._onComplete.bind(this));
        if ('value' in this.props) this.maskValue = this.props.value;
      }
    }, {
      key: "destroyMask",
      value: function destroyMask() {
        if (this.maskRef) {
          this.maskRef.destroy();
          delete this.maskRef;
        }
      }
    }, {
      key: "_extractMaskOptionsFromProps",
      value: function _extractMaskOptionsFromProps(props) {
        var cloneProps = Object.assign({}, props); // keep only mask options props

        Object.keys(cloneProps) // TODO why need cast to string?
        .filter(function (prop) {
          return MASK_OPTIONS_PROPS_NAMES.indexOf(prop) < 0;
        }).forEach(function (nonMaskProp) {
          delete cloneProps[nonMaskProp];
        });
        return cloneProps;
      }
    }, {
      key: "_extractNonMaskProps",
      value: function _extractNonMaskProps(props) {
        var cloneProps = Object.assign({}, props);
        MASK_PROPS_NAMES.forEach(function (maskProp) {
          delete cloneProps[maskProp];
        });
        return cloneProps;
      }
    }, {
      key: "maskValue",
      get: function get() {
        if (this.props.unmask === 'typed') return this.maskRef.typedValue;
        if (this.props.unmask) return this.maskRef.unmaskedValue;
        return this.maskRef.value;
      },
      set: function set(value) {
        value = value == null ? '' : value;
        if (this.props.unmask === 'typed') this.maskRef.typedValue = value;else if (this.props.unmask) this.maskRef.unmaskedValue = value;else this.maskRef.value = value;
      }
    }, {
      key: "_onAccept",
      value: function _onAccept(e) {
        if (this.props.onAccept && this.maskRef) this.props.onAccept(this.maskValue, this.maskRef, e);
      }
    }, {
      key: "_onComplete",
      value: function _onComplete(e) {
        if (this.props.onComplete && this.maskRef) this.props.onComplete(this.maskValue, this.maskRef, e);
      }
    }, {
      key: "render",
      value: function render() {
        return React__default.createElement(ComposedComponent, Object.assign({}, this._extractNonMaskProps(this.props), {
          inputRef: this._inputRef
        }));
      }
    }]);

    return MaskedComponent;
  }(React__default.Component);

  var nestedComponentName = ComposedComponent.displayName || ComposedComponent.name || 'Component';
  MaskedComponent.displayName = "IMask(".concat(nestedComponentName, ")");
  MaskedComponent.propTypes = MASK_PROPS;
  return MaskedComponent;
}

var _excluded$5 = ["inputRef"];
var IMaskInputClass = IMaskMixin(function (_ref) {
  var inputRef = _ref.inputRef,
      props = _objectWithoutProperties$1(_ref, _excluded$5);

  return React__default.createElement('input', Object.assign({}, props, {
    ref: inputRef
  }));
});

var IMaskInputFn = function IMaskInputFn(props, ref) {
  return React__default.createElement(IMaskInputClass, Object.assign({}, props, {
    ref: ref
  }));
};

var IMaskInput = React__default.forwardRef(IMaskInputFn);

const getButtonType = (type) => {
    switch (type) {
        case 'white':
            return `text-gray-700 bg-white
        border border-solid border-gray-300
        hover:cursor-pointer hover:bg-gray-50
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`;
        case 'disabled':
            return 'border border-solid border-gray-200 text-gray-400 bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500';
        case 'secondary':
            return `text-primary-700 bg-primary-100
        border border-solid border-primary-100
        hover:bg-primary-200 hover:border-primary-200
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-primary-500`;
        case 'form':
            return 'border border-gray-300 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none';
        case 'rsbry':
            return `border border-solid border-rsbry-600
          text-white bg-rsbry-600
          hover:bg-rsbry-700 hover:border-rsbry-700
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rsbry-500`;
        case 'action':
            return `text-rsbry-700 bg-rsbry-100
        border border-solid border-rsbry-100
        hover:bg-rsbry-200 hover:border-rsbry-200
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-rsbry-500`;
        case 'success':
            return `text-green-800 bg-green-200
        border border-solid border-green-200
        hover:bg-green-300 hover:border-green-300
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-green-500`;
        case 'info':
            return `text-blue-800 bg-blue-100
        border border-solid border-blue-100
        hover:bg-blue-200 hover:border-blue-200
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-500`;
        case 'warning':
            return `text-lemon-800 bg-lemon-200
        border border-solid border-lemon-200
        hover:bg-lemon-400 hover:border-lemon-400
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-lemon-500`;
        case 'error':
            return `text-rsbry-800 bg-rsbry-100
        border border-solid border-rsbry-100
        hover:bg-rsbry-200 hover:border-rsbry-200
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-rsbry-500`;
        default:
            // primary
            return `
        border border-solid border-primary-600
        text-white bg-primary-600
        hover:bg-primary-700 hover:border-primary-700
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`;
    }
};
const getButtonSize = (size) => {
    switch (size) {
        case 'xl':
            return 'px-6 py-3 text-base';
        case 'lg':
            return 'px-4 py-2 text-base';
        case 'sm':
            return 'px-3 py-2 text-sm leading-4';
        case 'xs':
            return 'px-2.5 py-1.5 text-xs';
        default:
            return 'px-4 py-2 text-sm';
    }
};
//TODO: Every size in Tailwind has a rounded-md style except for xs. 'regular' case must be updated: https://linear.app/neo-tax/issue/NEO-2055/update-button-in-ui-library-to-match-tailwind-ui-basekit-standards
const getRoundedClass = (rounded) => {
    switch (rounded) {
        case 'left':
            return 'rounded-l';
        case 'right':
            return 'rounded-r';
        case 'regular':
            return 'rounded-md';
        case 'full':
            return 'rounded-full';
        default:
            return '';
    }
};
// TODO Remove margin from StyledButton
// https://linear.app/neo-tax/issue/NEO-2156/remove-margin-from-button-component-in-ui-components
const StyledButton$1 = _default$1(Button$2) `
  relative
  inline-flex
  items-center
  justify-center
  font-medium
  shadow-sm
  m-1
  transition duration-300
  whitespace-nowrap
  ${({ $variant }) => getButtonType($variant)}
  ${({ $size }) => getButtonSize($size)}
  ${({ $rounded }) => getRoundedClass($rounded)}
`;
const StyledIcon$2 = _default$1.div `
  -ml-0.5
  mr-2
  h-4
  w-4
  flex
  items-center
  justify-center
`;
const StyledSpinnerWrapper = _default$1.span `
  absolute
  flex
  items-center
  justify-center
`;
const StyledButtonContent = _default$1.span `
  inline-flex
  items-center
  justify-center
  ${({ $isLoading }) => ($isLoading ? 'invisible' : 'visible')}
`;
const StyledButtonCheckbox = _default$1(Checkbox$1) `
  inline-flex
  items-center
  font-medium
  rounded-md
  shadow-sm
  transition duration-300
  ${({ $variant }) => getButtonType($variant)}
  ${({ $size }) => getButtonSize($size)}
  ${({ $rounded }) => getRoundedClass($rounded)}
  ${({ $checked }) => $checked ? 'bg-gray-200 text-gray-400 focus:ring-gray-500' : ''}
`;
const getCircularButtonSize = (size) => {
    switch (size) {
        case 'xl':
            return 'p-3';
        case 'lg':
            return 'p-2';
        case 'sm':
            return 'p-1.5';
        case 'xs':
            return 'p-1';
        default:
            return 'p-2';
    }
};
const getButtonCircularType = (type) => {
    switch (type) {
        case 'gray':
            return `
        bg-gray-100
        hover:bg-gray-200
        border-0
        shadow-none

        focus:outline-none
        focus:ring-2
        focus:ring-offset-2
        focus:ring-primary-500`;
        case 'simple':
            return `
        bg-white
        border-0
        shadow-none

        focus:outline-none
        focus:ring-2
        focus:ring-offset-2
        focus:ring-primary-500`;
        case 'secondary':
            return `
        fill-primary-700
        bg-primary-100

        border-solid
        border-primary-600

        hover:bg-primary-200
        hover:border-primary-700

        focus:outline-none
        focus:ring-2
        focus:ring-offset-2
        focus:ring-offset-white
        focus:ring-primary-500`;
        case 'disabled':
            return `
        fill-gray-400
        bg-gray-200

        focus:outline-none
        focus:ring-2
        focus:ring-offset-2
        focus:ring-gray-500`;
        default:
            return `
        fill-white
        bg-primary-600

        border-transparent

        hover:bg-primary-700
        hover:border-primary-700

        focus:ring-primary-500`;
    }
};
const StyledCircularButton = _default$1(Button$2) `
  inline-flex
  items-center
  border
  shadow-sm
  rounded-full

  focus:outline-none
  focus:ring-2
  focus:ring-offset-2

  transition duration-300

  ${({ $variant }) => getButtonCircularType($variant)}
  ${({ $size }) => getCircularButtonSize($size)}
`;
const getIconWrapperSize = (size) => {
    switch (size) {
        case 'xl':
        case 'lg':
            return 'h-6 w-6';
        default:
            return 'h-5 w-5';
    }
};
const StyledIconWrapper$2 = _default$1.div `
  ${({ $size }) => getIconWrapperSize($size)}
`;

const Button = ({ as, children, className, disabled, innerRef, isLoading, onClick, rounded, size = 'base', startIcon, type = 'button', variant = 'primary', ...props }) => (React__default.createElement(StyledButton$1, { "$variant": disabled || isLoading ? 'disabled' : variant, "$size": size, "$rounded": rounded, disabled: disabled || isLoading, onClick: onClick, type: type, className: className, as: as, ref: innerRef, ...props },
    isLoading && (React__default.createElement(StyledSpinnerWrapper, null,
        React__default.createElement(Spinner, { size: "sm", variant: "primary" }))),
    React__default.createElement(StyledButtonContent, { "$isLoading": isLoading },
        startIcon && (React__default.createElement(StyledIcon$2, { "aria-hidden": "true", "data-testid": "start-icon" }, startIcon)),
        children)));
const ButtonWithForwardRefInner = (props, ref) => React__default.createElement(Button, { ...props, innerRef: ref });
// Same non-generic forwardRef vs. polymorphic-generic cast as Stack.tsx — see there for the
// full rationale. Removed when we upgrade to React 19 (ref-as-prop makes forwardRef unnecessary).
const ButtonWithForwardRef = React__default.forwardRef(ButtonWithForwardRefInner);
const ButtonMemo = memo(Button);

const ButtonCheckBox = ({ checked, children, className, disabled, onChange, rounded, size = 'base', startIcon, variant = 'primary', }) => (React__default.createElement(StyledButtonCheckbox, { "$checked": checked, "$rounded": rounded, "$size": size, "$variant": disabled ? 'disabled' : variant, as: "div", checked: checked, className: className, disabled: disabled, onChange: onChange },
    startIcon && (React__default.createElement("div", { className: "-ml-0.5 mr-2 h-4 w-4", "aria-hidden": "true", "data-testid": "start-icon" }, startIcon)),
    children));
const ButtonCheckBoxMemo = memo(ButtonCheckBox);

const ButtonCircular = ({ as, className, disabled = false, icon, onClick, size = 'base', type = 'button', variant = 'primary', ...props }) => (React__default.createElement(StyledCircularButton, { "$size": size, "$variant": disabled ? 'disabled' : variant, onClick: onClick, type: type, className: className, as: as, ...props },
    React__default.createElement(StyledIconWrapper$2, { "$size": size, "aria-hidden": "true" }, icon)));
const ButtonCircularMemo = memo(ButtonCircular);

const InputContainer = _default$1.div `
  flex
  rounded-md
  shadow-sm
`;
const InlineInputContainer = _default$1.div `
  relative
  flex
  items-stretch
  flex-grow
  focus-within:z-10
  ${({ $isActive }) => $isActive
    ? '[&_input]:bg-primary-50 [&_input]:ring-1 [&_input]:border-primary-300 [&_input]:ring-primary-300 [&_input]:text-primary-700 [&_svg]:!text-primary-600'
    : ''}
`;
const AddOn = _default$1.div `
  inline-flex
  items-center
  rounded-l-md
  border
  border-r-0
  border-gray-300
  bg-gray-50
  px-3
  text-gray-500
  sm:text-sm
`;
const LeadingContent = _default$1.div `
  absolute
  inset-y-0
  left-0
  pl-3
  flex
  items-center
  pointer-events-none
  text-gray-400
`;
// inline-trailing-content custom class added to have a quicker access to this component in order to add custom styling
const TrailingContent = _default$1.div `
  inline-trailing-content
  absolute
  inset-y-0
  right-0
  pr-3
  flex
  items-center
  pointer-events-none
`;
const StyledInput$2 = _default$1.input `
  focus:ring-1
  focus:outline-none
  placeholder:text-gray-400
  shadow-sm
  block
  w-full
  sm:text-sm
  border
  ${({ $trailingButton }) => ($trailingButton ? 'rounded-l-md' : 'rounded-md')}
  ${({ $leadingContent }) => ($leadingContent ? 'pl-7 pr-3 py-2' : 'py-2 px-3')}
  ${({ $addOn }) => ($addOn ? 'rounded-l-none' : '')}
  ${({ $uppercase }) => ($uppercase ? 'uppercase' : '')}
  ${({ $error, disabled }) => {
    if (disabled)
        return 'bg-gray-200 border-primary-600';
    if ($error)
        return `border-rsbry-300 text-rsbry-900 placeholder:text-rsbry-300
        focus:ring-rsbry-500 focus:border-rsbry-500 animate-shake`;
    return 'border-gray-300 focus:border-primary-600 focus:ring-primary-600 placeholder:text-gray-400';
}}
`;
_default$1(IMaskInput) `
  focus:ring-1
  focus:outline-none
  placeholder:text-gray-400
  shadow-sm
  block
  w-full
  sm:text-sm
  border
  ${({ $trailingButton }) => ($trailingButton ? 'rounded-l-md' : 'rounded-md')}
  ${({ $leadingContent }) => ($leadingContent ? 'pl-7 pr-3 py-2' : 'py-2 px-3')}
  ${({ $addOn }) => ($addOn ? 'rounded-l-none' : '')}
  ${({ $uppercase }) => ($uppercase ? 'uppercase' : '')}
  ${({ $error, disabled }) => {
    if (disabled)
        return 'bg-gray-200 border-primary-600';
    if ($error)
        return `border-rsbry-300 text-rsbry-900 placeholder-rsbry-300
        focus:ring-rsbry-500 focus:border-rsbry-500 animate-shake`;
    return 'border-gray-300 focus:border-primary-600 focus:ring-primary-600 placeholder-gray-200';
}}
`;
const TrailingIcon = _default$1(Stack$5) `
  absolute
  inset-y-0
  right-0
  pr-3
  pointer-events-none
`;
const StyledTrailingButton = _default$1(Button) `
  ${({ $selected }) => $selected ? 'bg-primary-500 text-white border-primary-500 hover:bg-primary-700 hover:text-white' : ''}
  border-l-0
  m-0
`;
_default$1.p `
  mb-0
  text-sm
  text-rsbry-600
`;

const InputMasksMap = {
    PHONE: {
        mask: '(000) 000 0000',
    },
    EIN: {
        mask: '00-0000000',
    },
    NUMBER: {
        mask: Number,
        scale: 2, // digits after point, 0 for integers
        thousandsSeparator: ',',
        radix: '.', // fractional delimiter,
        unmask: 'typed',
    },
    PERCENTAGE: {
        mask: 'num%',
        lazy: false,
        blocks: {
            num: {
                mask: Number,
                min: 0,
                max: 100,
            },
        },
    },
    DATE: {
        mask: '00/00/0000',
    },
};

const InputMaskMixin = IMaskMixin(({ inputRef, ...props }) => React__default.createElement(StyledInput$2, { ...props, ref: inputRef }));
// Pulling out "type" because mask will take care of the type
const InputMask = ({ mask, onChange, returnValueType = 'typedValue', type: _type, ...props }) => (React__default.createElement(InputMaskMixin, { autoComplete: "off", unmask: "typed", onAccept: (_, mask) => {
        // IF the value is the same THEN don't trigger an update
        // This prevents triggering changes on mount when the value hasn't changed at all
        if (mask.value === props.value || mask.unmaskedValue === props.value || mask.typedValue === props.value) {
            return;
        }
        // IMask can return many types. Example when mask is Number...
        // 'value' -> '$1,234.50'
        // 'unmaskedValue' -> '1234.5'
        // 'typedValue' -> 1234.5
        onChange?.(mask[returnValueType] ?? '');
    }, ...InputMasksMap[mask], ...props }));

const InputWithForwardRef = ({ addOn, autoComplete, autoFocus, className, disabled, error, errorText, id, inlineLeadingContent, inlineTrailingContent, isActive, mask, maskReturnValueType, max, maxLength, min, name, onBlur, onChange, onClick, pattern, placeholder, trailingButton, trailingIcon, type, value, }, ref) => {
    const inputProps = {
        $error: error,
        $leadingContent: inlineLeadingContent !== undefined,
        $addOn: addOn !== undefined,
        $trailingButton: trailingButton !== undefined,
        $uppercase: type === 'date',
        'aria-errormessage': `${id}-error`,
        'aria-invalid': error,
        'data-testid': 'ui-component-input',
        autoComplete,
        autoFocus,
        disabled,
        id,
        min,
        max,
        maxLength,
        name,
        onChange,
        onClick,
        onBlur,
        pattern,
        placeholder,
        readOnly: !onChange,
        ref,
        type,
        value,
    };
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(InputContainer, { className: className },
            React__default.createElement(InlineInputContainer, { "$isActive": isActive },
                addOn && React__default.createElement(AddOn, null, addOn),
                inlineLeadingContent && React__default.createElement(LeadingContent, null, inlineLeadingContent),
                mask ? (React__default.createElement(InputMask, { ...inputProps, mask: mask, returnValueType: maskReturnValueType, onChange: onChange })) : (React__default.createElement(StyledInput$2, { ...inputProps })),
                inlineTrailingContent && React__default.createElement(TrailingContent, null, inlineTrailingContent),
                trailingIcon && React__default.createElement(TrailingIcon, { alignItems: "center" }, trailingIcon)),
            trailingButton),
        React__default.createElement(GeneralFormError, { error: !!error && !!errorText, id: `${id}-error` }, errorText)));
};
const Input = React__default.forwardRef(InputWithForwardRef);
const InputMemo = memo(Input);

const TrailingButton = ({ children, className, onClick, selected, startIcon, }) => (React__default.createElement(StyledTrailingButton, { "$selected": selected, startIcon: startIcon, rounded: "right", variant: "form", size: "base", onClick: onClick, className: className }, children));
const TrailingButtonMemo = memo(TrailingButton);

var InputMasks;
(function (InputMasks) {
    InputMasks["PHONE"] = "PHONE";
    InputMasks["EIN"] = "EIN";
    InputMasks["NUMBER"] = "NUMBER";
    InputMasks["PERCENTAGE"] = "PERCENTAGE";
    InputMasks["DATE"] = "DATE";
})(InputMasks || (InputMasks = {}));

const StyledInputFormContainer = _default$1.div `
  ${({ $withError }) => ($withError ? 'mb-1' : 'mb-6')}
  ${({ $horizontal }) => $horizontal ? 'grid grid-cols-7 items-baseline gap-4 pt-5 px-6' : 'flex flex-col gap-1'}
  ${({ $border, $horizontal }) => $border && $horizontal ? 'border-t border-gray-200 ' : ''}
`;
const StyledLabel$4 = _default$1.label `
  block
  text-sm
  font-medium
  text-gray-700
  ${({ $horizontal }) => ($horizontal ? 'col-span-2' : '')}
`;
const StyledInputContainer = _default$1.div `
  w-full
  ${({ $horizontal }) => ($horizontal ? 'col-span-5' : '')}
`;
const StyledHelpText = _default$1(FormSectionHelpText) `
  ${({ $horizontal }) => ($horizontal ? 'col-span-7' : '')}
`;

const InputFormLabel = ({ horizontal = false, inputId, label, secondaryLabel, }) => (React__default.createElement(React__default.Fragment, null, typeof label === 'string' ? (React__default.createElement(StyledLabel$4, { "$horizontal": horizontal, htmlFor: inputId },
    label,
    !!secondaryLabel && (React__default.createElement(Typography, { fontSize: "sm", color: colors$3.gray[400] },
        ' ',
        secondaryLabel)))) : (label)));

const InputFormContainer = ({ action, border = true, children, className, helpText, horizontal = false, inputId, label, secondaryLabel, withError = false, }) => (React__default.createElement(StyledInputFormContainer, { "$horizontal": horizontal, "$withError": withError, className: className, "$border": border },
    action ? (React__default.createElement(Stack$5, { justifyContent: "space-between", alignItems: "end", gap: 0.5 },
        React__default.createElement(InputFormLabel, { horizontal: horizontal, inputId: inputId, label: label, secondaryLabel: secondaryLabel }),
        action)) : (React__default.createElement(InputFormLabel, { horizontal: horizontal, inputId: inputId, label: label, secondaryLabel: secondaryLabel })),
    React__default.createElement(StyledInputContainer, { "$horizontal": horizontal }, children),
    !!helpText && !withError && React__default.createElement(StyledHelpText, { "$horizontal": horizontal }, helpText)));
const InputFormContainerMemo = memo(InputFormContainer);

const StyledTextAreaContainer = _default$1.div `
  relative
`;
const StyledTextArea = _default$1.textarea `
  focus:ring-1
  focus:outline-none
  placeholder:text-gray-400
  shadow-sm
  block
  sm:text-sm
  rounded-md
  w-full
  border
  ${({ $error, disabled }) => {
    if (disabled)
        return 'bg-gray-200 border-primary-600';
    if ($error)
        return `border-rsbry-300 text-rsbry-900 placeholder-rsbry-300
        focus:ring-rsbry-500 focus:border-rsbry-500 animate-shake`;
    return 'border-gray-300 focus:border-primary-600 focus:ring-primary-600 placeholder-gray-200';
}}
`;
const StyledTextAreaButton = _default$1(Button) `
  ${({ $selected }) => $selected ? 'bg-primary-500 text-white border-primary-500 hover:bg-primary-700 hover:text-white' : ''}
    rounded-md
    absolute
    top-0
    right-0
    -translate-y-[34px]
    m-0
`;

const TextArea = ({ className, disabled, error, errorText, id, onChange, textAreaButton, value, ...props }) => {
    const textAreaProps = {
        $error: error,
        'aria-errormessage': `${id}-error`,
        'aria-invalid': error,
        'data-testid': 'ui-component-text-area',
        disabled,
        id,
        ...props,
    };
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(StyledTextAreaContainer, { className: className },
            React__default.createElement(StyledTextArea, { ...textAreaProps, disabled: disabled, value: value, onChange: onChange }),
            textAreaButton),
        React__default.createElement(GeneralFormError, { error: !!error && !!errorText, id: `${id}-error` }, errorText)));
};
const TextAreaMemo = memo(TextArea);

const TextAreaButton = ({ children, className, onClick, selected, startIcon, }) => (React__default.createElement(StyledTextAreaButton, { "$selected": selected, startIcon: startIcon, rounded: "regular", variant: "form", size: "xs", onClick: onClick, className: className }, children));
const TextAreaButtonMemo = memo(TextAreaButton);

const LABEL_COLOR_VARIANTS = {
    white: 'text-white',
    gray: 'text-gray-700',
};
const toggleSize = {
    xl: 'w-[3.25rem] h-7',
    lg: 'w-[3.25rem] h-7',
    base: 'w-11 h-6',
    sm: 'w-9 h-5',
    xs: 'w-7 h-4',
};
const getToggleSpanSizeAndTranslation = (size, checked) => {
    switch (size) {
        case 'xl':
            return `w-6 h-6 ${checked ? 'translate-x-6' : 'translate-x-0'}`;
        case 'lg':
            return `w-6 h-6 ${checked ? 'translate-x-6' : 'translate-x-0'}`;
        case 'sm':
            return `w-4 h-4 ${checked ? 'translate-x-4' : 'translate-x-0'}`;
        case 'xs':
            return `w-3 h-3 ${checked ? 'translate-x-3' : 'translate-x-0'}`;
        default:
            return `w-5 h-5 ${checked ? 'translate-x-5' : 'translate-x-0'}`;
    }
};
const StyledToggleContainer = _default$1(Stack$5) `
  ${({ $disabled }) => ($disabled ? 'opacity-50' : '')}
`;
const StyledToggle = _default$1(ue$1) `
  relative
  inline-flex
  flex-shrink-0
  border-2
  border-transparent
  rounded-full
  transition-colors
  ease-in-out duration-200
  focus:outline-none
  focus:ring-2
  focus:ring-offset-2
  focus:ring-current
  text-primary-600
  ${({ $disabled }) => ($disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
  ${({ checked }) => (checked ? 'bg-current' : 'bg-gray-200')}
  ${({ $size }) => toggleSize[$size]}
`;
const StyledToggleSpan = _default$1.span `
  pointer-events-none
  inline-block
  rounded-full
  bg-white
  shadow
  transform
  ring-0
  transition
  ease-in-out
  duration-200
  flex
  justify-center
  items-center
  ${({ $checked, $size }) => getToggleSpanSizeAndTranslation($size, $checked)}
`;
const StyledLabel$3 = _default$1(Typography) `
  ${({ $colorVariant }) => LABEL_COLOR_VARIANTS[$colorVariant]}
`;

function _extends$r() { return _extends$r = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$r.apply(null, arguments); }
var SvgBoldCheck = function SvgBoldCheck(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$r({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    ref: ref
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M23.15,5.4l-2.8-2.8a.5.5,0,0,0-.7,0L7.85,14.4a.5.5,0,0,1-.7,0l-2.8-2.8a.5.5,0,0,0-.7,0L.85,14.4a.5.5,0,0,0,0,.7l6.3,6.3a.5.5,0,0,0,.7,0L23.15,6.1A.5.5,0,0,0,23.15,5.4Z",
    style: {
      fill: "currentColor"
    }
  }));
};
var ForwardRef$o = /*#__PURE__*/forwardRef(SvgBoldCheck);
var Memo$o = /*#__PURE__*/memo(ForwardRef$o);

// Actual switch/toggle- used within Toggle.tsx as well as any component where a simple switch is needed (without wrapper/title/subtitle)
const ToggleSwitch = ({ checked = false, className, disabled, id, onChange, size = 'base', withIcon = false, }) => (
// Headless UI switch overrides IDs. Custom attribute data-analytic-id needed for testing purposes
React__default.createElement(StyledToggle, { "data-analytic-id": id, checked: checked, onChange: onChange, disabled: disabled, "$disabled": disabled, "$size": size, className: className },
    React__default.createElement(StyledToggleSpan, { "aria-hidden": "true", "$checked": checked, "$size": size },
        withIcon && checked && React__default.createElement(Memo$o, { className: "w-2 h-2" }),
        withIcon && !checked && React__default.createElement(Memo$p, { className: "w-2 h-2 text-gray-400" }))));
const ToggleSwitchMemo = memo(ToggleSwitch);

// Overarching toggle component with a container, title, and subtitle
const Toggle = ({ alignItems = 'start', checked = false, className, disabled = false, id, labelColorVariant = 'gray', onChange, size = 'base', subtitle, title, withIcon = false, }) => (React__default.createElement(StyledToggleContainer, { alignItems: alignItems, "$disabled": disabled, className: className },
    React__default.createElement(Stack$5, { alignItems: "center", justifyContent: "start" },
        React__default.createElement(ToggleSwitch, { checked: checked, onChange: onChange, disabled: disabled, withIcon: withIcon, size: size })),
    React__default.createElement("div", { className: "ml-3" },
        React__default.createElement(StyledLabel$3, { as: "label", fontWeight: "medium", fontSize: size, "$colorVariant": labelColorVariant, htmlFor: id }, title),
        subtitle && (React__default.createElement(Typography, { as: "p", fontSize: size, className: "text-gray-500" }, subtitle)))));
const ToggleMemo = memo(Toggle);

const StyledTooltipAnchor = _default$1(TooltipAnchor) `
  cursor-default
`;
const StyledTooltip = _default$1(Tooltip$1) `
  z-tooltip
  py-2
  px-2
  text-xs
  text-gray-50
  bg-gray-700
  rounded-md
  shadow-sm
`;

const Tooltip = ({ children, className, anchorClassName, options = { showArrow: false }, text, }) => {
    const { showArrow, ...useTooltipStateOpts } = options;
    const tooltip = useTooltipState({
        timeout: 400,
        ...useTooltipStateOpts,
    });
    if (!text) {
        return React__default.createElement(React__default.Fragment, null, children);
    }
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(StyledTooltipAnchor, { className: anchorClassName, state: tooltip }, children),
        tooltip.open ? (React__default.createElement(StyledTooltip, { state: tooltip, className: className },
            showArrow ? React__default.createElement(TooltipArrow, null) : null,
            text)) : null));
};
const TooltipMemo = memo(Tooltip);

var _path$i;
function _extends$q() { return _extends$q = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$q.apply(null, arguments); }
var SvgAlertCircle = function SvgAlertCircle(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$q({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    ref: ref
  }, props), _path$i || (_path$i = /*#__PURE__*/React.createElement("path", {
    d: "M12,0A12.24,12.24,0,0,0,3.47,3.65,11.85,11.85,0,0,0,0,12.21,11.78,11.78,0,0,0,11.8,24H12A12.11,12.11,0,0,0,24,11.79h0A11.77,11.77,0,0,0,12,0ZM10.5,16.54A1.48,1.48,0,0,1,12,15h0a1.53,1.53,0,0,1,1.52,1.47A1.47,1.47,0,0,1,12.05,18h0A1.53,1.53,0,0,1,10.5,16.54Zm.5-4v-6a1,1,0,0,1,2,0v6a1,1,0,0,1-2,0Z",
    fill: "currentColor"
  })));
};
var ForwardRef$n = /*#__PURE__*/forwardRef(SvgAlertCircle);
var Memo$n = /*#__PURE__*/memo(ForwardRef$n);

const BG_COLOR_VARIANT = {
    gray: 'bg-gray-400',
    darkRsbry: 'bg-rsbry-600',
    primary: 'bg-primary-600',
    secondary: 'bg-blue-600',
    green: 'bg-green-300',
    lime: 'bg-lime-400',
    aqua: 'bg-aqua-500',
    avocado: 'bg-avocado-800',
    orange: 'bg-orange-400',
    lightRsbry: 'bg-rsbry-300',
    lemon: 'bg-lemon-300',
};
const StyledTypography$2 = _default$1(Typography) `
  w-[276px]
  truncate
`;
const StyledDot = _default$1.span `
  inline-block
  rounded-full
  w-2
  h-2
  ${({ $variant }) => BG_COLOR_VARIANT[$variant]}
`;
const StyledInput$1 = _default$1(Input) `
  w-[100px]
  h-[42px]
  m-0
`;
const StyledAlertCircle = _default$1(Memo$n) `
  w-6
  text-rsbry-600
`;
const IconPlaceholder = _default$1.div `
  w-6
`;

const AllocationInput = ({ error, errorText, label, variant, ...rest }) => (React__default.createElement(Stack$5, { alignItems: "center", justifyContent: "space-between" },
    React__default.createElement(Stack$5, { alignItems: "center", gap: 1.5 },
        React__default.createElement(StyledDot, { "$variant": variant }),
        React__default.createElement(StyledTypography$2, { as: "p", fontSize: "base", fontWeight: "medium" }, label)),
    React__default.createElement(Stack$5, { alignItems: "center", gap: 4 },
        error && errorText ? (React__default.createElement(Tooltip, { className: "max-w-[140px] !z-modal", text: errorText, options: {
                showArrow: true,
            } },
            React__default.createElement(StyledAlertCircle, null))) : (React__default.createElement(IconPlaceholder, null)),
        React__default.createElement(StyledInput$1, { mask: InputMasks.PERCENTAGE, maskReturnValueType: "unmaskedValue", onClick: (event) => {
                if (event.currentTarget.value === '0%')
                    event.currentTarget.select();
            }, ...rest, error: error }))));
memo(AllocationInput);

const StyledPillContainer = _default$1(Stack$5) `
  group
  w-full
  h-8
`;
const StyledPill = _default$1(Button$2) `
  block
  h-5
  w-[118px]
  rounded-lg
  border
  border-blue-700
  relative
  overflow-hidden
`;
const StyledTypography$1 = _default$1(Typography) `
  text-gray-400
  m-auto
  relative
`;
const StyledStack$3 = _default$1(Stack$5) `
  absolute
  top-0
  w-full
  h-5
`;
const StyledSelectedPercentage = _default$1.div `
  transition-colors
  bg-gray-800
  group-hover:bg-primary-600
`;
const StyledAlreadyAllocatedPercentage = _default$1.div `
  transition-colors
  bg-gray-200
  group-hover:bg-gray-300
`;
const StyledPercentage$1 = _default$1(Typography) `
  w-8
  text-right
  text-gray-800
  transition-colors
  group-hover:text-primary-600
`;

const AllocationPill = ({ alreadyAllocatedPercentage = 0, className, onClick, selectedPercentage, showSelectedPercentage = false, }) => (React__default.createElement(StyledPillContainer, { alignItems: "center", justifyContent: "center", gap: 1 },
    React__default.createElement(StyledPill, { className: className, onClick: onClick },
        React__default.createElement(StyledStack$3, null,
            React__default.createElement(StyledSelectedPercentage, { style: { width: `${selectedPercentage}%` } }),
            React__default.createElement(StyledAlreadyAllocatedPercentage, { style: { width: `${alreadyAllocatedPercentage}%` } })),
        React__default.createElement(StyledTypography$1, { as: "p", fontSize: "xs", fontWeight: "medium" }, "ADVANCED")),
    showSelectedPercentage ? (React__default.createElement(StyledPercentage$1, { as: "span", fontSize: "xs", fontWeight: "semibold", id: "selected-percentage", lineHeight: 5 },
        selectedPercentage,
        React__default.createElement(Typography, { className: "text-inherit", as: "span", fontSize: "xs", fontWeight: "normal", lineHeight: 5 }, "%"))) : null));
memo(AllocationPill);

const HoverInnerSquareColorTotalVariants = 9;
const HoverInnerSquareColorVariant = {
    base: 'group-hover:bg-primary-600 hover:border-primary-600 group-hover:border-primary-600',
    0: 'group-hover:bg-primary-100 hover:border-primary-600 group-hover:border-primary-600',
    1: 'group-hover:bg-blue-100 hover:border-blue-600 group-hover:border-blue-600',
    2: 'group-hover:bg-green-100 hover:border-green-300 group-hover:border-green-300',
    3: 'group-hover:bg-lime-100 hover:border-lime-300 group-hover:border-lime-300',
    4: 'group-hover:bg-aqua-100 hover:border-aqua-500 group-hover:border-aqua-500',
    5: 'group-hover:bg-avocado-100 hover:border-avocado-800 group-hover:border-avocado-800',
    6: 'group-hover:bg-orange-100 hover:border-orange-400 group-hover:border-orange-400',
    7: 'group-hover:bg-rsbry-100 hover:border-rsbry-300 group-hover:border-rsbry-300',
    8: 'group-hover:bg-lemon-100 hover:border-lemon-300 group-hover:border-lemon-300',
};
const BaseSelectedColorVariant = {
    base: 'text-gray-800',
    0: 'text-primary-800',
    1: 'text-blue-800',
    2: 'text-green-800',
    3: 'text-lime-800',
    4: 'text-aqua-800',
    5: 'text-avocado-800',
    6: 'text-orange-800',
    7: 'text-rsbry-800',
    8: 'text-lemon-800',
};
const CheckboxSquareColorVariant = {
    base: 'peer-checked:bg-gray-600 peer-checked:border-gray-600 hover:bg-primary-600 hover:border-primary-600',
    0: 'peer-checked:bg-primary-600 peer-checked:border-primary-600 hover:bg-primary-100 hover:border-primary-600 peer-checked:hover:border-primary-100',
    1: 'peer-checked:bg-blue-600 peer-checked:border-blue-600 hover:bg-blue-100 hover:border-blue-600 peer-checked:hover:border-blue-100',
    2: 'peer-checked:bg-green-300 peer-checked:border-green-300 hover:bg-green-100 hover:border-green-300 peer-checked:hover:border-green-100',
    3: 'peer-checked:bg-lime-300 peer-checked:border-lime-300 hover:bg-lime-100 hover:border-lime-300 peer-checked:hover:border-lime-100',
    4: 'peer-checked:bg-aqua-500 peer-checked:border-aqua-500 hover:bg-aqua-100 hover:border-aqua-500 peer-checked:hover:border-aqua-100',
    5: 'peer-checked:bg-avocado-800 peer-checked:border-avocado-800 hover:bg-avocado-100 hover:border-avocado-800 peer-checked:hover:border-avocado-100',
    6: 'peer-checked:bg-orange-400 peer-checked:border-orange-400 hover:bg-orange-100 hover:border-orange-400 peer-checked:hover:border-orange-100',
    7: 'peer-checked:bg-rsbry-300 peer-checked:border-rsbry-300 hover:bg-rsbry-100 hover:border-rsbry-300 peer-checked:hover:border-rsbry-100',
    8: 'peer-checked:bg-lemon-300 peer-checked:border-lemon-300 hover:bg-lemon-100 hover:border-lemon-300 peer-checked:hover:border-lemon-100',
};
const CheckboxSquareHighlightColorVariant = {
    base: '!border-primary-600',
    0: '!border-primary-100',
    1: '!border-blue-100',
    2: '!border-green-100',
    3: '!border-lime-100',
    4: '!border-aqua-100',
    5: '!border-avocado-100',
    6: '!border-orange-100',
    7: '!border-rsbry-100',
    8: '!border-lemon-100',
};
const SliderVariant = {
    selected: 'bg-gray-800',
    suggested: 'bg-gray-400',
    readOnlyBase: 'bg-gray-200 border-gray-300',
    readOnlyBaseHover: 'bg-gray-800 border-gray-400',
    readOnlyDisabled: 'h-1.5 bg-gray-300',
    readOnlySelected: 'bg-gray-800 border-gray-400',
    readOnlySelectedHover: 'bg-gray-200 border-gray-300',
    disabled: 'h-1.5 !bg-gray-300 !border-gray-300 relative top-1/2 -translate-y-1/2 cursor-not-allowed',
};
const PeerSliderVariant = {
    selected: 'peer-hover:[&>div]:bg-gray-800',
    suggested: 'peer-hover:[&>div]:bg-gray-400',
    readOnlyBase: '',
    readOnlyBaseHover: '',
    readOnlyDisabled: '',
    readOnlySelected: '',
    readOnlySelectedHover: '',
    disabled: '',
};
const PercentageVariant = {
    selected: 'text-current',
    suggested: 'text-gray-500',
    readOnlyBase: 'text-gray-500',
    readOnlyBaseHover: 'text-gray-400',
    readOnlyDisabled: '',
    readOnlySelected: 'text-current',
    readOnlySelectedHover: 'text-gray-200',
    disabled: '',
};
const StyledContainer$5 = _default$1(Stack$5) `
  pointer-events-auto
  group
  h-5
  ${({ $variant }) => BaseSelectedColorVariant[$variant]}
`;
const StyledAllocationSliderContainer = _default$1(Stack$5) `
  group
  peer
`;
const StyledSquare = _default$1(je.Option) `
  pointer-events-auto
  group
  peer
  h-5
  rounded-sm
  px-px
  focus:outline-primary-600
  peer-hover:[&>div]:transition-colors
  peer-hover:[&>div]:duration-500
  peer-hover:[&>div]:bg-gray-200
  peer-hover:[&>div]:border-gray-300
  ${({ $variant }) => PeerSliderVariant[$variant]}
`;
const StyledInnerSquare = _default$1.div `
  cursor-pointer
  h-5
  w-2.5
  rounded-sm
  border-gray-300
  border
  bg-gray-200
  group-hover:transition-colors
  group-hover:duration-500
  ${({ $hoverVariant }) => HoverInnerSquareColorVariant[$hoverVariant]}
  ${({ $variant }) => SliderVariant[$variant]}
`;
const StyledPercentage = _default$1(Typography) `
  w-8
  text-right
  text-gray-500
  peer-hover:transition-colors
  peer-hover:duration-500
  ${({ $textVariant }) => $textVariant === 'base' ? 'peer-hover:text-primary-600' : 'peer-hover:text-current'}
  ${({ $variant }) => PercentageVariant[$variant]}
`;
const StyledReadOnlySquare = _default$1.div `
  h-5
  w-2.5
  rounded-sm
  border
  ease-in-out
  transition-colors
  duration-200
  ${({ $variant }) => SliderVariant[$variant]}
`;
const StyledCheckboxSquare = _default$1.div `
  h-1.5
  w-2.5
  rounded-sm
  border
  bg-gray-300
  peer-checked:h-5
  cursor-pointer
  ${({ $colorVariant }) => CheckboxSquareColorVariant[$colorVariant]}
  ${({ $colorVariant, $sympatheticHighlight }) => $sympatheticHighlight ? `${CheckboxSquareHighlightColorVariant[$colorVariant]} shadow-md` : ''}
`;

const AllocationCheckbox = ({ checked, colorVariant, squareId, }) => {
    const elementId = `${squareId}-${checked ? 'checked' : 'unchecked'}`;
    const { highlight, isSympatheticHighlighted, removeHighlight } = useSympatheticHighlight(elementId);
    return (React__default.createElement(StyledCheckboxSquare, { "$colorVariant": colorVariant, "$sympatheticHighlight": isSympatheticHighlighted, onMouseEnter: () => {
            const selectedElementId = `${squareId}-checked`;
            if (elementId !== selectedElementId) {
                highlight(selectedElementId);
            }
        }, onMouseLeave: () => {
            const selectedElementId = `${squareId}-checked`;
            if (elementId !== selectedElementId) {
                removeHighlight(selectedElementId);
            }
        } }));
};

const StepByStepAllocationSlider = ({ namespace = '', onChange, projectId, projects, tooltip, variant = 'base', }) => {
    const colorVariant = typeof variant === 'string' ? variant : variant % HoverInnerSquareColorTotalVariants;
    const squares = projects.map((stepId, idx) => ({
        'aria-label': `${(idx + 1) * 10}%`,
        key: `${namespace}${(idx + 1) * 10}`,
        checked: stepId === projectId,
        onChange: () => {
            onChange({
                percentage: 10,
                newProjectId: projectId,
                previousProjectId: stepId,
            });
        },
        stepId,
    }));
    if (squares.length === 0) {
        return React__default.createElement(React__default.Fragment, null);
    }
    return (React__default.createElement(StyledContainer$5, { alignItems: "center", direction: "row", gap: 0.5 }, squares.map(({ checked, key, stepId, ...squareProps }) => (React__default.createElement(Tooltip, { key: key, className: "max-w-[220px] !z-modal bg-opacity-80", text: tooltip?.(stepId), options: {
            placement: 'right',
            showArrow: true,
            gutter: -2,
        } },
        React__default.createElement("label", null,
            React__default.createElement(Checkbox$1, { className: "sr-only peer", checked: checked, ...squareProps }),
            React__default.createElement(AllocationCheckbox, { squareId: key, checked: checked, colorVariant: colorVariant })))))));
};
const StepByStepAllocationSliderMemo = memo(StepByStepAllocationSlider);

const DEFAULT_STEP_INTERVAL = 10;
const generateSteps = (totalSteps, startPercentageFrom) => Array.from({ length: totalSteps }, (_, i) => (i + 1 + startPercentageFrom / DEFAULT_STEP_INTERVAL) * DEFAULT_STEP_INTERVAL);
const getSelectedVariant = (percentage, variantWhenTrue, variantWhenFalse = '') => percentage >= 1 ? variantWhenTrue : variantWhenFalse;

const AllocationSlider = ({ alreadyAllocatedPercentage = 0, direction = 'ltr', id, name, onChange, onSelect = () => { }, otherAllocations, projectId, projects, selectedPercentage, startPercentageFrom = 0, steps = 10, suggestedPercentage = 0, tooltip, variant = 'base', }) => {
    const colorVariant = typeof variant === 'string' ? variant : variant % HoverInnerSquareColorTotalVariants;
    const totalPercentage = steps * DEFAULT_STEP_INTERVAL + startPercentageFrom;
    const isHoverEventEnableRef = useRef(false);
    const [hoveredPercentage, setHoveredPercentage] = useState(0);
    const [selectedVariant, percentageToShow] = selectedPercentage !== 0 ? ['selected', selectedPercentage] : ['suggested', suggestedPercentage];
    const isDisabled = (step) => (totalPercentage - alreadyAllocatedPercentage) / step < 1;
    const { onMouseEnterHoverIntent, onMouseLeaveHoverIntent } = useHoverIntent();
    const { onMouseEnterHoverIntent: onMouseEnterCellHoverIntent, onMouseLeaveHoverIntent: onMouseLeaveCellHoverIntent } = useHoverIntent();
    const squares = generateSteps(steps, startPercentageFrom).map((step) => ({
        'aria-label': `${step}%`,
        value: step,
        $variant: getSelectedVariant(percentageToShow / step, selectedVariant),
        disabled: isDisabled(step),
        onMouseEnter: onMouseEnterCellHoverIntent(() => {
            setHoveredPercentage(step);
        }, isHoverEventEnableRef.current ? 0 : 200),
        onClick: () => {
            setHoveredPercentage(0);
            if (step === selectedPercentage) {
                onSelect?.(0);
            }
        },
        id: `${id}-${step}`,
    }));
    return (React__default.createElement(StyledContainer$5, { "$variant": colorVariant, alignItems: "center", gap: 1 },
        React__default.createElement(Stack$5, { gap: 0.5 },
            projects && projectId && onChange ? (React__default.createElement(StepByStepAllocationSlider, { projectId: projectId, projects: projects, onChange: onChange, variant: variant })) : null,
            squares.length > 0 ? (React__default.createElement(je, { id: id, className: "peer", name: name, value: selectedPercentage, onChange: onSelect },
                React__default.createElement(je.Label, { className: "sr-only" }, "allocation slider"),
                React__default.createElement(StyledAllocationSliderContainer, { alignItems: "center", direction: direction === 'ltr' ? 'row' : 'row-reverse', justifyContent: "end", onMouseEnter: onMouseEnterHoverIntent(() => {
                        isHoverEventEnableRef.current = true;
                    }, 200), onMouseLeave: onMouseLeaveHoverIntent((event) => {
                        onMouseLeaveCellHoverIntent(() => {
                            isHoverEventEnableRef.current = false;
                            setHoveredPercentage(0);
                        })(event);
                    }) }, squares.map(({ $variant, id, value, ...squareProps }) => (React__default.createElement(StyledSquare, { "analytic-id": id, "$variant": $variant, value: value, key: value, ...squareProps }, ({ disabled }) => (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement(je.Label, { className: "sr-only" }, `${value}%`),
                    React__default.createElement(StyledInnerSquare, { "$variant": disabled ? 'disabled' : $variant, "$hoverVariant": colorVariant }))))))))) : null,
            otherAllocations && projectId && onChange ? (React__default.createElement(StepByStepAllocationSlider, { namespace: "otherAllocations", onChange: onChange, projectId: projectId, projects: otherAllocations, variant: variant, tooltip: tooltip })) : null),
        React__default.createElement(StyledPercentage, { "$textVariant": colorVariant, "$variant": selectedVariant, as: "span", fontSize: "xs", fontWeight: "semibold", id: "selected-percentage", lineHeight: 5 },
            hoveredPercentage || percentageToShow,
            React__default.createElement(Typography, { className: "text-inherit", as: "span", fontSize: "xs", fontWeight: "normal", lineHeight: 5 }, "%"))));
};
const AllocationSliderMemo = memo(AllocationSlider);

const AllocationSliderInput = ({ errorText, hideLabel = false, id, label, name, onChange, onSelect, value, }) => (React.createElement(React.Fragment, null,
    React.createElement(Grid$1, { cols: 2, gap: 2 },
        React.createElement(AllocationSlider, { name: name, selectedPercentage: value, onChange: onChange, onSelect: onSelect }),
        React.createElement(Typography, { fontSize: "xs", fontWeight: "semibold", color: colors$3.gray[500], className: hideLabel ? 'sr-only' : '' }, label)),
    React.createElement(GeneralFormError, { error: !!errorText, id: `${id}-error` }, errorText)));

const steps = generateSteps(10, 0);
const ReadOnlyAllocationSlider = ({ alreadyAllocatedPercentage = 0, hovercardOpen = false, id, selectedPercentage, }) => {
    const isDisabled = (step) => (100 - alreadyAllocatedPercentage) / step < 1;
    const squares = steps.map((step) => ({
        'aria-label': `${step}%`,
        value: step,
        $variant: isDisabled(step)
            ? 'readOnlyDisabled'
            : getSelectedVariant(selectedPercentage / step, hovercardOpen ? 'readOnlySelectedHover' : 'readOnlySelected', hovercardOpen ? 'readOnlyBaseHover' : 'readOnlyBase'),
    }));
    return (React__default.createElement(StyledContainer$5, { id: id, alignItems: "center", direction: "row", gap: 0.5 }, squares.map(({ value, ...squareProps }) => (React__default.createElement(StyledReadOnlySquare, { key: value, ...squareProps })))));
};
const ReadOnlyAllocationSliderMemo = memo(ReadOnlyAllocationSlider);

const CHANGE_TEXT_VARIANTS = {
    gray: 'text-gray-400',
    primary: 'text-primary-300',
    secondary: 'text-blue-100',
    lightGray: 'text-gray-50',
    red: 'text-rsbry-600',
    green: ' text-green-600',
    rsbry: 'text-rsbry-500',
};
const StyledChangeText = _default$1.span `
  self-end
  leading-5
  ${({ $variant }) => CHANGE_TEXT_VARIANTS[$variant]}
`;
const EmojiContainer = _default$1.span `
  pr-1
`;

const getChangeText = (changeType, replacementText) => {
    if (replacementText) {
        return replacementText;
    }
    return changeType === 'plus' ? 'Money Back' : 'Money Owed';
};
const ChangeText = ({ changeType, className, negativeChangeText, positiveChangeText, variant = 'gray', }) => (React__default.createElement(StyledChangeText, { "$variant": variant, className: className }, changeType === 'plus' ? (React__default.createElement(React__default.Fragment, null,
    !positiveChangeText && (React__default.createElement(EmojiContainer, null,
        React__default.createElement("span", null, "\u2764\uFE0F"))),
    React__default.createElement("span", null, getChangeText('plus', positiveChangeText)))) : (React__default.createElement(React__default.Fragment, null,
    !negativeChangeText && (React__default.createElement(EmojiContainer, null,
        React__default.createElement("span", null, "\uD83D\uDC94"))),
    React__default.createElement("span", null, getChangeText('minus', negativeChangeText))))));
memo(ChangeText);

const AMOUNT_VARIANTS = {
    gray: 'text-gray-400',
    primary: 'text-primary-300',
    secondary: 'text-blue-100',
    lightGray: 'text-gray-50',
};
const CENTS_VARIANTS = {
    gray: 'text-gray-500',
    primary: 'text-primary-300',
    secondary: 'text-blue-300',
    lightGray: 'text-gray-300',
};
const StyledAmountRestPart = _default$1.span `
  ${({ $variant }) => AMOUNT_VARIANTS[$variant]}
`;
const StyledSignificantAmountPart = _default$1(StyledAmountRestPart) `
  font-semibold
`;
const StyledAmountCents = _default$1.span `
  ${({ $variant }) => CENTS_VARIANTS[$variant]}
`;

const ColoredAmount = ({ amountCents, showCents = true, variant = 'gray', withDollarSign = true, ...props }) => {
    const formattedNumber = amountCents != null
        ? lib.formatDollarsFromCents(amountCents, {
            showCents,
            withDollarSign,
            minimumFractionDigits: 2,
            roundingMethod: lib.RoundingMethod.FLOOR,
        })
        : `${withDollarSign ? '$ ' : ''}---`;
    const [wholeNumberPart, decimalPart] = formattedNumber.split('.');
    const [significativePart, ...restAmount] = wholeNumberPart.split(',');
    const restAmountConcat = restAmount.join(',');
    return (React__default.createElement(Typography, { fontWeight: "light", id: "colored-amount", ...props },
        React__default.createElement(StyledSignificantAmountPart, { "$variant": variant }, significativePart),
        restAmountConcat ? React__default.createElement(StyledAmountRestPart, { "$variant": variant },
            ",",
            restAmountConcat) : null,
        amountCents != null && showCents ? (React__default.createElement(StyledAmountCents, { "$variant": variant },
            React__default.createElement(StyledAmountCents, { "$variant": "gray" }, "."),
            decimalPart)) : null));
};
const ColoredAmountMemo = memo(ColoredAmount);

const kpiColorVariant = {
    gray: 'text-gray-400',
    primary: 'text-primary-300',
    secondary: 'text-blue-300',
    lightGray: 'text-gray-50',
};
const LEADING_CONTENT_VARIANTS = {
    gray: 'text-gray-300',
    primary: 'text-primary-300',
    secondary: 'text-blue-100',
    lightGray: 'text-gray-50',
};
const AmountKpiToIconMapping = {
    gray: 'grayFilled',
    primary: 'primaryFilled',
    secondary: 'secondaryFilled',
    lightGray: 'lightGrayFilled',
};
const TextContainer = _default$1(Stack$5) `
  pb-1
`;
const StyledAmountKpiContainer = _default$1(Stack$5) `
  space-x-3
  max-w-full
`;
const StyledTitleParagraph$1 = _default$1(Typography) `
  mb-0
  overflow-hidden
  text-ellipsis
  whitespace-nowrap
  tracking-wide
  uppercase
  ${({ $variant }) => kpiColorVariant[$variant]}
`;
const StyledTitleLeadingContent = _default$1(Typography) `
  ${({ $variant }) => LEADING_CONTENT_VARIANTS[$variant]}
`;
// Separator
const SEPARATOR_VARIANTS = {
    gray: 'text-gray-500',
    primary: 'text-primary-300',
    secondary: 'text-blue-300',
    lightGray: 'text-gray-300',
};
const StyledSeparator = _default$1.span `
  px-1
  leading-4
  ${({ $variant }) => SEPARATOR_VARIANTS[$variant]}
`;

const getChangeType = (amount) => {
    if (!amount) {
        return;
    }
    if (amount > 0) {
        return 'plus';
    }
    if (amount < 0) {
        return 'minus';
    }
    return;
};
const AmountKpi = ({ amountCents, className, icon, leadingAmountContent, negativeChangeText, positiveChangeText, showCents = true, showChangeType = false, title, variant = 'gray', }) => {
    const changeType = getChangeType(amountCents);
    return (React__default.createElement(StyledAmountKpiContainer, { className: className, alignItems: "center" },
        icon != null ? (React__default.createElement("div", { "data-testid": "icon-container" },
            React__default.createElement(Icon, { node: icon, nodeSize: 5, size: 8, variant: "circular", colorVariant: AmountKpiToIconMapping[variant] }),
            ' ')) : null,
        React__default.createElement(TextContainer, { direction: "column" },
            React__default.createElement(StyledTitleParagraph$1, { "$variant": variant, as: "p", fontSize: "xs", fontWeight: "semibold", lineHeight: 4 }, title),
            React__default.createElement(Stack$5, null,
                leadingAmountContent ? (React__default.createElement(StyledTitleLeadingContent, { "$variant": variant, lineHeight: 4 }, leadingAmountContent)) : null,
                React__default.createElement(ColoredAmount, { amountCents: amountCents, as: "p", showCents: showCents, variant: variant, lineHeight: 4 }),
                showChangeType && changeType && (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement(StyledSeparator, { "$variant": variant }, "|"),
                    React__default.createElement(ChangeText, { changeType: changeType, positiveChangeText: positiveChangeText, negativeChangeText: negativeChangeText, variant: changeType === 'plus' ? variant : 'rsbry', className: cn({
                            'leading-4': true,
                            'font-light': changeType === 'plus',
                            'font-semibold': changeType === 'minus',
                        }) })))))));
};
const AmountKpiMemo = memo(AmountKpi);

const StyledDivStack = _default$1.div `flex -space-x-1`;

const StackedGroupAvatars = ({ children, className }) => (React__default.createElement(StyledDivStack, { className: className }, React__default.Children.map(children, (child) => (React__default.createElement(Tooltip, { text: child.props.text || '' }, React__default.cloneElement(child, {
    stacked: true,
}))))));
const StackedGroupAvatarsMemo = memo(StackedGroupAvatars);

const AvatarTextContent = _default$1.div `
  ml-2
  max-w-[calc(100%-25px)]
  overflow-hidden
  text-ellipsis
  whitespace-nowrap
`;
const StyledAvatarWithTextContainerDiv = _default$1.div `
  flex
  items-center
  max-w-full
  text-gray-700
  leading-5
  text-sm
  font-medium
`;
// `truncate` on the paragraph itself: `text-ellipsis` on the container does not reach block children.
const StyledTitleParagraph = _default$1(Typography) `
  mb-0
  truncate
`;
const StyledSubtitleParagraph = _default$1(Typography) `
  leading-5
  text-xs
  font-medium
  text-gray-500
  mb-0
`;

const AvatarWithText = ({ className, imgSrc, placeholder, size, stacked, subtitle, title, variant, ...props }) => (React__default.createElement(StyledAvatarWithTextContainerDiv, { className: className },
    React__default.createElement("div", null,
        React__default.createElement(Avatar, { imgSrc: imgSrc, size: size, stacked: stacked, text: title, variant: variant })),
    React__default.createElement(AvatarTextContent, null,
        React__default.createElement(StyledTitleParagraph, { ...props }, title ?? placeholder),
        subtitle ? React__default.createElement(StyledSubtitleParagraph, { as: "p" }, subtitle) : null)));
const AvatarWithTextMemo = memo(AvatarWithText);

const BADGE_COLOR_VARIANTS = {
    default: 'bg-gray-200 text-gray-800',
    primary: 'bg-primary-200 text-primary-800',
    darkPrimary: 'bg-primary-600 text-white',
    secondary: 'bg-blue-200 text-blue-800',
    red: 'bg-rsbry-100 text-rsbry-800',
    yellow: 'bg-lemon-100 text-lemon-800',
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
    darkGreen: 'bg-green-400 text-white',
    clear: 'bg-white text-gray-800 border border-gray-300',
};
const BADGE_ICON_COLOR_VARIANTS = {
    default: 'text-gray-400 fill-gray-400 stroke-gray-400',
    primary: 'text-primary-400 fill-primary-400 stroke-primary-400',
    darkPrimary: 'text-primary-800 fill-primary-800 stroke-primary-800',
    secondary: 'text-blue-400 fill-blue-400 stroke-blue-400',
    red: 'text-rsbry-400 fill-rsbry-400 stroke-rsbry-400',
    yellow: 'text-lemon-400 fill-lemon-400 stroke-lemon-400',
    green: 'text-green-400 fill-green-400 stroke-green-400',
    blue: 'text-blue-400 fill-blue-400 stroke-blue-400',
    darkGreen: 'text-white fill-white stroke-white',
    clear: 'text-gray-400 fill-gray-400 stroke-gray-400',
};
const StyledBadge = _default$1.div `
  inline-flex
  items-center
  px-2.5
  py-0.5
  rounded-full
  text-xs
  font-medium
  ${({ $maxWidth }) => ($maxWidth ? 'overflow-hidden' : '')}
  ${({ $colorVariant }) => BADGE_COLOR_VARIANTS[$colorVariant]}
  ${({ $as, disabled }) => {
    const clickable = $as === 'button' || $as === 'a';
    return clickable && !disabled ? 'cursor-pointer' : clickable && disabled ? 'cursor-not-allowed opacity-25' : '';
}}
`;
const StyledCustomIcon = _default$1(Icon) `
  mr-1.5
  ${({ $colorVariant }) => BADGE_ICON_COLOR_VARIANTS[$colorVariant]}
`;
const StyledBadgeIcon = _default$1.svg `
  -ml-1
  mr-1.5
  h-2
  w-2
  ${({ $colorVariant }) => BADGE_ICON_COLOR_VARIANTS[$colorVariant]}
`;

const Badge = ({ as, children, className, colorVariant = 'default', disabled = false, dot = true, icon, maxWidth, ...props }) => {
    const style = maxWidth ? { maxWidth: `${maxWidth}px` } : {};
    const fullText = typeof children === 'string' ? children : '';
    return (React__default.createElement(StyledBadge, { "$as": as ?? 'div', "$colorVariant": colorVariant, className: className, disabled: disabled, style: style, title: maxWidth && fullText ? fullText : undefined, ...props },
        icon && React__default.createElement(StyledCustomIcon, { node: icon, size: 2, variant: "none" }),
        !icon && dot && (React__default.createElement(StyledBadgeIcon, { "data-testid": "badge-dot", "$colorVariant": colorVariant, viewBox: "0 0 8 8", xmlns: "http://www.w3.org/2000/svg", fill: "currentColor", stroke: "currentColor" },
            React__default.createElement("circle", { cx: 4, cy: 4, r: 3 }))),
        React__default.createElement("span", { className: maxWidth ? 'truncate block' : '' }, children)));
};
const BadgeMemo = memo(Badge);

const MAIN_CONTENT_ROUNDED_VARIANTS = {
    default: 'rounded-r',
    md: 'rounded-r-md',
    lg: 'rounded-r-lg',
    full: 'rounded-r-full',
};
const MainContentContainer = _default$1(Stack$5) `
  flex-1
  truncate
  transition-colors
  duration-200
  ${({ $roundedVariant }) => MAIN_CONTENT_ROUNDED_VARIANTS[$roundedVariant]}
`;
const TitleContainer$1 = _default$1.div `
  flex-1
  px-4
  py-2
  text-sm
  truncate
`;
const StyledSubTitleTypography = _default$1(Typography) `
  text-gray-500
`;

const CARD_WITH_ACTION_COLOR_VARIANTS = {
    primary: 'bg-primary-600 text-white',
    secondary: 'bg-blue-600 text-white',
    action: 'bg-rsbry-600 text-white',
    'action-secondary': 'bg-rsbry-100 text-rsbry-700',
};
const CARD_WITH_ACTION_FOCUS_COLOR_VARIANTS = {
    primary: 'focus:ring-primary-600',
    secondary: 'focus:ring-blue-600',
    action: 'focus:ring-rsbry-600',
    'action-secondary': 'focus:ring-rsbry-700',
};
const StyledCardWithAction = _default$1(Card) `
  focus:outline-none
  focus:ring-2
  focus:ring-offset-2
  ${({ $variant }) => CARD_WITH_ACTION_FOCUS_COLOR_VARIANTS[$variant]}
  ${({ $backgroundColor }) => $backgroundColor === 'gray' ? 'bg-gray-200 border border-gray-300 hover:bg-gray-300' : 'bg-white hover:bg-gray-50'}
`;
const CardWithActionContainer = _default$1.div `
  group
  flex
  h-full
  rounded-md
  ${({ $disabled }) => ($disabled ? 'shadow-none opacity-30' : '')}
`;
const IconContainer$2 = _default$1.div `
  flex
  flex-shrink-0
  items-center
  justify-center
  rounded-l-md
  w-16
  ${({ $cardIconShape, $variant }) => $cardIconShape === 'square' ? CARD_WITH_ACTION_COLOR_VARIANTS[$variant] : ''}
  ${({ $disabled }) => ($disabled ? 'bg-gray-600 text-gray-50' : '')}
`;
const CardIconContainer = _default$1.div `
  ${({ $variant }) => CARD_WITH_ACTION_COLOR_VARIANTS[$variant]}
  ${({ $cardIconShape }) => $cardIconShape === 'circle' ? 'h-8 w-8 p-1 rounded-full' : 'h-6 w-6'}
`;
const MainIconContainer = _default$1.div `
  flex-shrink-0
  pr-2
`;
const StyledIconWrapper$1 = _default$1.div `
  h-8
  w-8
  inline-flex
  items-center
  justify-center
`;
const StyledIcon$1 = _default$1.div `
  h-5
  w-5
  text-gray-400
  group-hover:text-gray-500
`;

const CardWithAction = ({ actionIcon, as, backgroundColor, cardIcon, cardIconShape = 'square', className, disabled, subTitle, title, variant = 'primary', ...props }) => (React__default.createElement(StyledCardWithAction, { as: as, disabled: disabled, "$variant": variant, "$backgroundColor": backgroundColor, roundedVariant: "md", ...props },
    React__default.createElement(CardWithActionContainer, { "$disabled": disabled, className: className },
        React__default.createElement(IconContainer$2, { "$disabled": disabled, "$variant": variant, "$cardIconShape": cardIconShape },
            React__default.createElement(CardIconContainer, { "$variant": variant, "$cardIconShape": cardIconShape }, cardIcon)),
        React__default.createElement(MainContentContainer, { justifyContent: "space-between", alignItems: "center", "$roundedVariant": "md" },
            React__default.createElement(TitleContainer$1, null,
                React__default.createElement(Typography, { as: "p", fontSize: "sm", fontWeight: "medium" }, title),
                subTitle ? (React__default.createElement(StyledSubTitleTypography, { as: "p", fontSize: "sm" }, subTitle)) : null),
            React__default.createElement(MainIconContainer, null,
                React__default.createElement(StyledIconWrapper$1, null,
                    React__default.createElement(StyledIcon$1, { "aria-hidden": "true" }, actionIcon)))))));
const CardWithActionMemo = memo(CardWithAction);

const StyledCardWithAvatar = _default$1(Card) `
  w-full
  hover:bg-gray-50
  focus:outline-none
  focus:ring-2
  focus:ring-offset-2
  focus:ring-primary-600
  transition-colors
  duration-200
  ${({ $active }) => ($active ? 'bg-white' : 'bg-transparent')}
`;
const CardWithAvatarTitleContainer = _default$1(TitleContainer$1) `
  pl-1
  pr-4
  py-2.5
  text-left
`;
const StyledTitleText = _default$1(Typography) `
  overflow-hidden
  whitespace-nowrap
  text-ellipsis
`;
const AvatarContainerStack = _default$1(Stack$5) `
  flex-shrink-0
  p-2
`;
const CardWithAvatarStack = _default$1(Stack$5) `
  group
  h-full
`;

const CardWithAvatar = ({ active = false, avatarProps, className, subTitle, title, ...props }) => (React__default.createElement(StyledCardWithAvatar, { className: className, "$active": active, roundedVariant: "md", shadow: active, border: active, ...props },
    React__default.createElement(CardWithAvatarStack, { alignItems: "center", justifyContent: "start" },
        avatarProps && (React__default.createElement(AvatarContainerStack, { justifyContent: "center", alignItems: "center", testId: "avatar" },
            React__default.createElement(Avatar, { size: 6, ...avatarProps }))),
        React__default.createElement(MainContentContainer, { justifyContent: "space-between", alignItems: "center", "$roundedVariant": "md" },
            React__default.createElement(CardWithAvatarTitleContainer, null,
                React__default.createElement(StyledTitleText, { as: "p", fontSize: "sm", fontWeight: "medium" }, title),
                subTitle ? (React__default.createElement(StyledSubTitleTypography, { as: "p", fontSize: "sm" }, subTitle)) : null)))));
const CardWithAvatarMemo = memo(CardWithAvatar);

var _path$h;
function _extends$p() { return _extends$p = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$p.apply(null, arguments); }
var SvgExclamationPoint = function SvgExclamationPoint(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$p({
    viewBox: "0 0 3 17",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$h || (_path$h = /*#__PURE__*/React.createElement("path", {
    d: "M1.375 1.5415V8.37484M1.375 15.2082H1.39208",
    stroke: "#B91C1C",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    fill: "currentColor"
  })));
};
var ForwardRef$m = /*#__PURE__*/forwardRef(SvgExclamationPoint);
var Memo$m = /*#__PURE__*/memo(ForwardRef$m);

const statusIcons = {
    success: {
        size: 8,
        nodeSize: 4,
        node: React__default.createElement(Memo$o, { className: "text-white" }),
        variant: 'circular',
        colorVariant: 'secondaryFilled',
        testId: 'success-avatar',
    },
    error: {
        size: 8,
        nodeSize: 4,
        node: React__default.createElement(Memo$m, { className: "w-full h-full text-rsbry-700" }),
        variant: 'circular',
        colorVariant: 'red',
        testId: 'error-avatar',
    },
    disabled: {
        className: 'opacity-50',
        size: 10,
        nodeSize: 8,
        variant: 'circular',
        node: null, // Temporarily set to null to satisfy typing
        colorVariant: 'secondary',
        testId: 'disabled-avatar',
    },
    default: {
        size: 8,
        nodeSize: 8,
        variant: 'circular',
        node: null, // Temporarily set to null to satisfy typing
        colorVariant: 'secondary',
        testId: 'default-avatar',
    },
};
const CompanyLogo = ({ logo, status }) => {
    if (status === 'default' || status === 'disabled') {
        statusIcons[status].node = logo;
    }
    const iconProps = statusIcons[status];
    return React__default.createElement(Icon, { ...iconProps, className: "border-none leading-none" });
};

const CompanyLogoIcon = ({ className, companyName, logo, status = 'default', }) => (React__default.createElement(Stack$5, { className: className, direction: "column", alignItems: "center" },
    React__default.createElement(CompanyLogo, { status: status, logo: logo }),
    React__default.createElement(Typography, { as: "p", fontSize: "xs", fontWeight: "medium", lineHeight: 6, className: "text-gray-400" }, companyName)));
const CompanyLogoMemo = memo(CompanyLogoIcon);

const ControlledAllocationSlider = ({ control, helpText, hideLabel, id, label, name, rules, secondaryLabel, }) => (React.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { ref: _ref, ...rest }, fieldState: { error } }) => {
        const errorMsg = error?.message;
        return (React.createElement(InputFormContainer, { label: label, helpText: helpText, inputId: id, withError: !!errorMsg },
            React.createElement(AllocationSliderInput, { id: id, label: secondaryLabel, hideLabel: hideLabel, value: rest.value, onSelect: rest.onChange, errorText: errorMsg })));
    } }));

const ControlledAutocompleteTagInput = ({ className, control, errorText, helpText, horizontal = false, id, label, name, rules, ...props }) => (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { onChange, ref: _ref, value }, fieldState: { error } }) => {
        const errorMsg = errorText || error?.message;
        return (React__default.createElement(InputFormContainer, { inputId: id, label: label, helpText: helpText, withError: !!errorMsg, horizontal: horizontal, className: className },
            React__default.createElement(AutocompleteTagInput, { id: id, error: !!errorMsg, errorText: errorMsg, name: name, onChange: onChange, values: value, ...props })));
    } }));

const ControlledAutocompleteInput = ({ action, allowCreate, className, control, disabled, errorText, groupEndValues, helpText, horizontal = false, id, label, name, onCreate, optionDisabledKey, optionDisplayKey, optionValueKey, options, placeholder, rules, searchKeys, }) => (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { name: controllerName, onChange: controllerOnChange, ref: _ref, value: controllerValue }, fieldState: { error }, }) => {
        const errorMsg = errorText || error?.message;
        return (React__default.createElement(InputFormContainer, { inputId: id, label: label, helpText: helpText, withError: !!errorMsg, horizontal: horizontal, className: className, action: action },
            React__default.createElement(AutocompleteInput, { id: id, error: !!errorMsg, errorText: errorMsg, disabled: disabled, groupEndValues: groupEndValues, name: controllerName, onChange: controllerOnChange, optionDisabledKey: optionDisabledKey, optionDisplayKey: optionDisplayKey, optionValueKey: optionValueKey, options: options, placeholder: placeholder, searchKeys: searchKeys, value: controllerValue, allowCreate: allowCreate, onCreate: onCreate })));
    } }));

const StyledGroup = _default$1(Group) `
  flex
`;
const StyledGroupOptionContent = _default$1.div `
  px-4
  py-2
  flex
  justify-center
  items-center
  font-medium
`;
const StyledOptionButton = _default$1(Button$2) `
  relative
  flex
  justify-center
  items-center
  border
  first:rounded-l-md
  last:rounded-r-md
  text-sm
  font-medium
  transition
  duration-300
  focus:z-10
  focus:outline-none
  focus:ring-1
  focus:ring-primary-500
  focus:border-primary-500
  ${({ $grow, $selected, $selectedLabelOnly }) => !$grow ? '' : !$selectedLabelOnly ? 'grow' : $selected ? 'grow' : 'grow-2'}
  ${({ $selected, disabled }) => $selected
    ? disabled
        ? 'bg-gray-400 text-white border-gray-400 cursor-not-allowed focus:ring-0 focus:border-gray-400'
        : 'bg-primary-500 text-white border-primary-500 hover:bg-primary-700'
    : disabled
        ? 'bg-gray-300 text-gray-400 border-gray-300 cursor-not-allowed focus:ring-0 focus:border-gray-300'
        : 'bg-white text-gray-400 border-gray-300 hover:bg-gray-50'}
`;
const StyledIcon = _default$1.div `
  -ml-0.5
  mr-2
  ${({ $selected, disabled }) => $selected ? 'text-white' : disabled ? 'text-gray-400' : 'text-gray-400'}
`;

const ButtonGroupOptionButton = ({ children, className, disabled = false, grow = false, id, onClick, option, selected = false, selectedLabelOnly = false, }) => {
    const handleOnClick = useCallback(() => {
        if (selected) {
            return;
        }
        onClick(option);
    }, [onClick, option, selected]);
    return (React__default.createElement(StyledOptionButton, { onClick: handleOnClick, "aria-pressed": selected, disabled: disabled, "$grow": grow, "$selected": selected, "$selectedLabelOnly": selectedLabelOnly, className: cn('button-group-option', className), id: id }, children));
};
// used `as` to forward type when using memo and generic components, see https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
const ButtonGroupOptionButtonMemo = memo(ButtonGroupOptionButton);

const createButtonGroupComponent = (isMemo) => function ButtonGroupComponent({ className, disabled = false, errorText, id, onClick, options, optionsGrow = false, selectedLabelOnly = false, value, }) {
    const ButtonGroupOptionComponent = isMemo ? ButtonGroupOptionButtonMemo : ButtonGroupOptionButton;
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(StyledGroup, { className: className }, options.map((option) => {
            const { icon, label, tooltip, value: optionValue } = option;
            const actualLabel = !selectedLabelOnly || (selectedLabelOnly && value === optionValue) ? label : '';
            const buttonContents = actualLabel ? (React__default.createElement(StyledGroupOptionContent, null,
                icon && (React__default.createElement(StyledIcon, { "$selected": optionValue === value, "$disabled": disabled, "aria-hidden": "true" }, icon)),
                actualLabel)) : (React__default.createElement("div", { "aria-hidden": "true" }, icon));
            return (React__default.createElement(ButtonGroupOptionComponent, { key: optionValue, option: optionValue, selected: optionValue === value, disabled: disabled || !!option.disabled, grow: optionsGrow, selectedLabelOnly: selectedLabelOnly, onClick: onClick, className: cn(!actualLabel && 'px-2'), id: `${id}-${optionValue}` }, tooltip ? (React__default.createElement(Tooltip, { text: tooltip, options: {
                    showArrow: true,
                }, anchorClassName: "cursor-unset" }, buttonContents)) : (buttonContents)));
        })),
        React__default.createElement(GeneralFormError, { error: !!errorText, id: `${id}-error` }, errorText)));
};
const ButtonGroup = createButtonGroupComponent(false);
const ButtonGroupMemo = memo(createButtonGroupComponent(true));

const ControlledButtonGroup = ({ className, control, errorText, helpText, horizontal = false, id, label, name, rules, ...buttonGroupProps }) => (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { onChange, ref: _ref, value, ...rest }, fieldState: { error } }) => {
        const errorMsg = errorText || error?.message;
        return (React__default.createElement(InputFormContainer, { inputId: id, label: label, helpText: helpText, withError: !!errorMsg, horizontal: horizontal, className: className },
            React__default.createElement(ButtonGroup, { ...rest, ...buttonGroupProps, id: id, value: value, onClick: (value) => onChange(value), errorText: errorMsg })));
    } }));

const ControlledCheckbox = ({ className, control, id, label, name, rules, ...checkboxProps }) => (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { ref: _ref, value, ...rest } }) => (React__default.createElement(Checkbox, { ...rest, ...checkboxProps, id: id, title: label, checked: value, className: className })) }));

const ControlledInput = ({ border, className, control, errorText, helpText, horizontal = false, id, label, name, rules, ...inputProps }) => (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { ref: _ref, ...rest }, fieldState: { error } }) => {
        const errorMsg = errorText || error?.message;
        return (React__default.createElement(InputFormContainer, { inputId: id, label: label, helpText: helpText, withError: !!errorMsg, horizontal: horizontal, className: className, border: border },
            React__default.createElement(Input, { ...rest, ...inputProps, id: id, error: !!errorMsg, errorText: errorMsg })));
    } }));

const ControlledMonthDatePicker = ({ control, monthAutoFocus, monthDateAutoFocus, monthDateDisabled, monthDateForm, monthDateHelpText, monthDateId, monthDateLabel, monthDateName, monthDatePlaceholder, monthDateRequired, monthDateRules, monthDateSize, monthDisabled, monthForm, monthHelpText, monthId, monthLabel, monthName, monthPlaceholder, monthRequired, monthRules, monthSize, ...props }) => {
    const monthDateSubscriberRef = useRef(() => { });
    const [month, setMonth] = React__default.useState('');
    const monthDateOptions = useMonthDateOptions(month);
    return (React__default.createElement(Stack$5, { direction: "row", space: 11, ...props },
        React__default.createElement(Controller$1, { control: control, name: monthName, rules: monthRules, render: ({ field: { ref: _ref, ...rest }, fieldState: { error } }) => {
                const errorMsg = error?.message;
                const handleChangeMonth = (month) => {
                    rest.onChange(month);
                    // Update the month so that the month date options are updated
                    setMonth(month);
                    // Always clear the month date even if the new month has the same number of days
                    // Why? Avoid the problem of the user changing months and forgetting to change the month date
                    // Example: Set June 30 -> Change month to July -> Forget to change month date to 31 (last day in July)
                    // monthDateSubscriber('');
                    monthDateSubscriberRef.current('');
                };
                return (React__default.createElement(InputFormContainer, { inputId: monthId, label: monthLabel, helpText: monthHelpText, withError: !!errorMsg, horizontal: false },
                    React__default.createElement(Select, { ...rest, id: monthId, autoFocus: monthAutoFocus, error: !!errorMsg, errorText: errorMsg, disabled: monthDisabled, form: monthForm, placeholder: monthPlaceholder, required: monthRequired, size: monthSize, onChange: handleChangeMonth, options: monthOptions })));
            } }),
        React__default.createElement(Controller$1, { control: control, name: monthDateName, rules: monthDateRules, render: ({ field: { ref: _ref, ...rest }, fieldState: { error } }) => {
                const errorMsg = error?.message;
                monthDateSubscriberRef.current = rest.onChange;
                return (React__default.createElement(InputFormContainer, { inputId: monthDateId, label: monthDateLabel, helpText: monthDateHelpText, withError: !!errorMsg, horizontal: false },
                    React__default.createElement(Select, { ...rest, id: monthDateId, autoFocus: monthDateAutoFocus, error: !!errorMsg, errorText: errorMsg, disabled: monthDateDisabled, form: monthDateForm, placeholder: monthDatePlaceholder, required: monthDateRequired, size: monthDateSize, options: monthDateOptions })));
            } })));
};

const MonthPicker = ({ autoFocus, className, disabled, error, errorText, form, helpText, horizontal, id, label, monthOptions: monthOptions$1, name, onChange, placeholder, required, size, usePortal, value, ...props }) => {
    const handleChange = (month) => {
        onChange?.(month);
    };
    return (React__default.createElement(InputFormContainer, { inputId: id, label: label, helpText: helpText, horizontal: horizontal, withError: error, className: className },
        React__default.createElement(Select, { id: id, autoFocus: autoFocus, disabled: disabled, error: error, errorText: errorText, form: form, name: name, placeholder: placeholder, required: required, size: size, value: value, onChange: handleChange, options: monthOptions$1 ?? monthOptions, usePortal: usePortal, ...props })));
};

const ControlledMonthPicker = ({ control, name, rules, ...props }) => (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { ref: _ref, ...rest } }) => {
        const handleChange = (month) => {
            rest.onChange(month);
        };
        return React__default.createElement(MonthPicker, { ...props, ...rest, onChange: handleChange });
    } }));

const RadioGroupOptionWrapper = _default$1.div `
  -space-y-px rounded-md bg-white
`;
function radioGroupOptionsClassNames(optionIdx, optionsLength, checked) {
    return cn(optionIdx === 0 ? 'rounded-tl-md rounded-tr-md' : '', optionIdx === optionsLength - 1 ? 'rounded-bl-md rounded-br-md' : '', checked ? 'z-10 border-primary-600 bg-primary-50' : 'border-gray-200', 'relative flex cursor-pointer border p-4 focus:outline-none');
}
const RadioGroupSpan = _default$1.span `
  ${({ $checked }) => $checked ? 'bg-primary-600 border-transparent' : 'bg-white border-gray-300'}
  ${({ $active }) => ($active ? 'ring-2 ring-offset-2 ring-primary-600' : '')}
  mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-full border flex items-center justify-center
`;
const RadioCircle = _default$1.span `
  rounded-full bg-white w-1.5 h-1.5
`;
const RadioGroupLabelWrapper = _default$1.span `
  ml-3 flex flex-col
`;
const RadioGroupLabel = _default$1(je.Label) `
  ${({ $checked }) => ($checked ? 'text-primary-900' : 'text-gray-900')}
  block text-sm font-medium
`;
const RadioGroupDescription = _default$1(je.Description) `
  ${({ $checked }) => ($checked ? 'text-primary-700' : 'text-gray-500')}
  block text-sm
`;

const RadioGroupCards = ({ helpText, horizontal = false, id, label, onChange, options, value, }) => (React__default.createElement(InputFormContainer, { inputId: id, label: label, helpText: helpText, horizontal: horizontal },
    React__default.createElement(je, { value: value, onChange: onChange },
        React__default.createElement(je.Label, { className: "sr-only" }, label),
        React__default.createElement(RadioGroupOptionWrapper, null, options.map((option, optionIdx) => (React__default.createElement(je.Option, { key: option.label, value: option.value, className: ({ checked }) => radioGroupOptionsClassNames(optionIdx, options.length, checked) }, ({ active, checked }) => (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(RadioGroupSpan, { "$checked": checked, "$active": active, "aria-hidden": "true" },
                React__default.createElement(RadioCircle, null)),
            React__default.createElement(RadioGroupLabelWrapper, null,
                React__default.createElement(RadioGroupLabel, { as: "span", "$checked": checked }, option.label),
                React__default.createElement(RadioGroupDescription, { as: "span", "$checked": checked }, option.description)))))))))));

const ControlledRadioGroup = (params) => {
    const { control, name, rules } = params;
    return (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { ref: _ref, ...rest } }) => (React__default.createElement(RadioGroupCards, { ...params, value: rest.value, onChange: rest.onChange })) }));
};

const ControlledTagInput = ({ className, control, errorText, helpText, horizontal = false, id, label, name, rules, ...props }) => (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { onChange, value }, fieldState: { error } }) => {
        const errorMsg = errorText || error?.message;
        return (React__default.createElement(InputFormContainer, { inputId: id, label: label, helpText: helpText, withError: !!errorMsg, horizontal: horizontal, className: className },
            React__default.createElement(TagInput, { id: id, error: !!errorMsg, errorText: errorMsg, tags: value, setTags: onChange, ...props })));
    } }));

const ControlledSelect = ({ className, control, helpText, horizontal = false, id, label, name, rules, ...selectProps }) => (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { ref: _ref, ...rest }, fieldState: { error } }) => {
        const errorMsg = error?.message;
        return (React__default.createElement(InputFormContainer, { inputId: id, label: label, helpText: helpText, withError: !!errorMsg, horizontal: horizontal, className: className },
            React__default.createElement(Select, { ...rest, ...selectProps, id: id })));
    } }));

const ControlledTextArea = ({ control, errorText, id, name, rules, type = 'text', ...textAreaProps }) => (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { ref: _ref, ...rest }, fieldState: { error } }) => {
        const errorMsg = errorText || error?.message;
        return React__default.createElement(TextArea, { ...rest, ...textAreaProps, id: id, type: type, error: !!errorMsg, errorText: errorMsg });
    } }));

const ControlledToggleSwitch = ({ className, control, id, name, rules, ...toggleSwitchProps }) => (React__default.createElement(Controller$1, { control: control, name: name, rules: rules, render: ({ field: { ref: _ref, value, ...rest } }) => (React__default.createElement(ToggleSwitch, { ...rest, ...toggleSwitchProps, id: id, checked: value, className: className })) }));

var _path$g;
function _extends$o() { return _extends$o = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$o.apply(null, arguments); }
var SvgAdd = function SvgAdd(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$o({
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$g || (_path$g = /*#__PURE__*/React.createElement("path", {
    d: "M0 12C0 12.3978 0.158035 12.7794 0.43934 13.0607C0.720644 13.342 1.10218 13.5 1.5 13.5H10.25C10.3163 13.5 10.3799 13.5263 10.4268 13.5732C10.4737 13.6201 10.5 13.6837 10.5 13.75V22.5C10.5 22.8978 10.658 23.2794 10.9393 23.5607C11.2206 23.842 11.6022 24 12 24C12.3978 24 12.7794 23.842 13.0607 23.5607C13.342 23.2794 13.5 22.8978 13.5 22.5V13.75C13.5 13.6837 13.5263 13.6201 13.5732 13.5732C13.6201 13.5263 13.6837 13.5 13.75 13.5H22.5C22.8978 13.5 23.2794 13.342 23.5607 13.0607C23.842 12.7794 24 12.3978 24 12C24 11.6022 23.842 11.2206 23.5607 10.9393C23.2794 10.658 22.8978 10.5 22.5 10.5H13.75C13.6837 10.5 13.6201 10.4737 13.5732 10.4268C13.5263 10.3799 13.5 10.3163 13.5 10.25V1.5C13.5 1.10218 13.342 0.720644 13.0607 0.43934C12.7794 0.158035 12.3978 0 12 0C11.6022 0 11.2206 0.158035 10.9393 0.43934C10.658 0.720644 10.5 1.10218 10.5 1.5V10.25C10.5 10.3163 10.4737 10.3799 10.4268 10.4268C10.3799 10.4737 10.3163 10.5 10.25 10.5H1.5C1.10218 10.5 0.720644 10.658 0.43934 10.9393C0.158035 11.2206 0 11.6022 0 12Z",
    fill: "currentColor"
  })));
};
var ForwardRef$l = /*#__PURE__*/forwardRef(SvgAdd);
var Memo$l = /*#__PURE__*/memo(ForwardRef$l);

var _path$f;
function _extends$n() { return _extends$n = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$n.apply(null, arguments); }
var SvgMarkArrowsRight = function SvgMarkArrowsRight(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$n({
    viewBox: "0 0 32 30",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$f || (_path$f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M28.9485 13.4456L2.51226 13.4456C1.83693 13.4187 1.19818 13.1314 0.729871 12.6442C0.261557 12.1569 0 11.5071 0 10.8313C0 10.1554 0.261557 9.50572 0.729871 9.01842C1.19818 8.53113 1.83693 8.2439 2.51226 8.21702L22.7679 8.21702L17.3862 4.35052C17.2115 4.21254 17.0629 4.04421 16.9475 3.85375C16.7709 3.57394 16.6527 3.26137 16.5997 2.93471C16.5783 2.79788 16.5672 2.65938 16.5667 2.52076C16.5677 2.3285 16.59 2.13701 16.6328 1.94949C16.7139 1.62693 16.8543 1.32229 17.0468 1.05119C17.2437 0.782389 17.4912 0.55455 17.7754 0.380598C18.0563 0.203318 18.3704 0.0849182 18.6986 0.0328222C19.0261 -0.0201698 19.3606 -0.00890643 19.6838 0.0659736C19.9533 0.141366 20.208 0.261685 20.4373 0.421941L30.7493 7.94386C31.2689 8.32249 31.6552 8.85599 31.8528 9.46783C32.0503 10.0797 32.049 10.7384 31.8491 11.3493C31.649 11.9604 31.2607 12.4923 30.7396 12.8689C30.2185 13.2455 29.5914 13.4474 28.9485 13.4456ZM28.9651 15.9961L2.52876 15.9961H2.52057C1.84524 16.023 1.20652 16.3102 0.738171 16.7975C0.269819 17.2848 0.00831498 17.9345 0.00831495 18.6103C0.00831493 19.2862 0.269818 19.9359 0.73817 20.4232C1.20652 20.9105 1.84524 21.1977 2.52057 21.2246L22.7679 21.2246L17.3862 25.0911C17.2115 25.2292 17.0629 25.3974 16.9475 25.5878C16.7709 25.8677 16.6527 26.1802 16.5997 26.5069C16.5783 26.6438 16.5672 26.7822 16.5667 26.9208C16.5677 27.1131 16.5899 27.3047 16.6328 27.4921C16.7139 27.8147 16.8543 28.1193 17.0468 28.3904C17.2436 28.6593 17.4912 28.8871 17.7755 29.061C18.0564 29.2383 18.3704 29.3567 18.6986 29.4088C19.026 29.4619 19.3607 29.4506 19.6838 29.3756C19.9532 29.3002 20.208 29.1799 20.4373 29.0197L30.7493 21.4978C31.2678 21.12 31.6535 20.588 31.8515 19.9778C32.0495 19.3675 32.0495 18.7104 31.8516 18.1002C31.6537 17.4899 31.268 16.9579 30.7496 16.58C30.2312 16.2021 29.6066 15.9977 28.9651 15.9961Z",
    fill: "currentColor"
  })));
};
var ForwardRef$k = /*#__PURE__*/forwardRef(SvgMarkArrowsRight);
var Memo$k = /*#__PURE__*/memo(ForwardRef$k);

var _path$e;
function _extends$m() { return _extends$m = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$m.apply(null, arguments); }
var SvgSubtract = function SvgSubtract(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$m({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    ref: ref
  }, props), _path$e || (_path$e = /*#__PURE__*/React.createElement("path", {
    d: "M1.5,13.5h21a1.5,1.5,0,0,0,0-3H1.5a1.5,1.5,0,0,0,0,3Z",
    fill: "currentColor"
  })));
};
var ForwardRef$j = /*#__PURE__*/forwardRef(SvgSubtract);
var Memo$j = /*#__PURE__*/memo(ForwardRef$j);

const StyledEquationOperatorContainer = _default$1(Stack$5) `
  bg-gray-50
  h-12
  w-12
  rounded-md
  p-3
`;

const icons = {
    addition: React__default.createElement(Memo$l, { className: "w-6 text-gray-500", "data-testid": "addition-icon" }),
    equalsTo: React__default.createElement(Memo$k, { className: "w-6 text-gray-500", "data-testid": "equalsTo-icon" }),
    subtraction: React__default.createElement(Memo$j, { className: "w-6 text-gray-500", "data-testid": "subtraction-icon" }),
};
const EquationOperator = ({ className, type }) => (React__default.createElement(StyledEquationOperatorContainer, { className: className, alignItems: "center", direction: "row", justifyContent: "center" }, icons[type]));
const EquationOperatorMemo = memo(EquationOperator);

const termColorVariant = {
    'nols-step-1': 'text-gray-800',
    'nols-step-2': 'text-gray-700',
    'nols-step-3': 'text-gray-600',
    'nols-step-4': 'text-gray-500',
    'nols-step-5': 'text-gray-400',
    'nols-step-6': 'text-gray-300',
    'nols-step-7': 'text-gray-200',
    'nols-step-8': 'text-gray-100',
    'income-step-1': 'text-blue-800',
    'income-step-2': 'text-blue-700',
    'income-step-3': 'text-blue-600',
    'income-step-4': 'text-blue-500',
    'income-step-5': 'text-blue-400',
    'income-step-6': 'text-blue-300',
    'income-step-7': 'text-blue-200',
    'income-step-8': 'text-blue-100',
    'expenses-step-1': 'text-rsbry-800',
    'expenses-step-2': 'text-rsbry-700',
    'expenses-step-3': 'text-rsbry-600',
    'expenses-step-4': 'text-rsbry-500',
    'expenses-step-5': 'text-rsbry-400',
    'expenses-step-6': 'text-rsbry-300',
    'expenses-step-7': 'text-rsbry-200',
    'expenses-step-8': 'text-rsbry-100',
    'calculations-step-1': 'text-lemon-800',
    'calculations-step-2': 'text-lemon-700',
    'calculations-step-3': 'text-lemon-600',
    'calculations-step-4': 'text-lemon-500',
    'calculations-step-5': 'text-lemon-400',
    'calculations-step-6': 'text-lemon-300',
    'calculations-step-7': 'text-lemon-200',
    'calculations-step-8': 'text-lemon-100',
    'outcomes-step-1': 'text-lime-800',
    'outcomes-step-2': 'text-lime-700',
    'outcomes-step-3': 'text-lime-600',
    'outcomes-step-4': 'text-lime-500',
    'outcomes-step-5': 'text-lime-400',
    'outcomes-step-6': 'text-lime-300',
    'outcomes-step-7': 'text-lime-200',
    'outcomes-step-8': 'text-lime-100',
};
const StyledText$1 = _default$1(Typography) `
  text-left
  ${({ $variant }) => termColorVariant[$variant]}
`;
const StyledStack$2 = _default$1(Stack$5) `
  max-w-[162px]
`;

const EquationTerm = ({ className, description, step, title, value, variant, }) => {
    const termVariant = `${variant}-${step}`;
    return (React__default.createElement(StyledStack$2, { className: className, direction: "column", alignItems: "start" },
        React__default.createElement(Tooltip, { className: "max-w-[160px] !z-modal", text: description, options: {
                placement: 'top',
                showArrow: true,
            } },
            React__default.createElement(StyledText$1, { "$variant": termVariant, as: "p", fontSize: "xs", fontWeight: "normal" }, title),
            React__default.createElement(StyledText$1, { "$variant": termVariant, as: "p", fontSize: "xs", fontWeight: "bold" }, value))));
};
const EquationTermMemo = memo(EquationTerm);

const FeedItemContainer = _default$1(Stack$5) `
  space-x-3
`;
const IconContainer$1 = _default$1.div `
  h-6
  w-6
  rounded-full
  text-gray-400
`;
const ContentContainer = _default$1.div `
  flex-1
  space-y-1
`;
const GrayTypography = _default$1(Typography) `
  text-gray-400
`;

const FeedItem = ({ className, description, icon, id, subtitle, supportText, title, }) => (React__default.createElement(FeedItemContainer, { id: id, className: className },
    React__default.createElement(IconContainer$1, null, icon),
    React__default.createElement(ContentContainer, null,
        React__default.createElement(Stack$5, { justifyContent: "space-between" },
            React__default.createElement(Stack$5, { direction: "column", alignItems: "start" },
                React__default.createElement(Typography, { as: "h3", fontSize: "sm", lineHeight: 5, fontWeight: "medium" }, title),
                React__default.createElement(GrayTypography, { as: "p", fontSize: "sm" }, description)),
            React__default.createElement(Stack$5, { direction: "column", alignItems: "end" },
                React__default.createElement(GrayTypography, { as: "p", fontSize: "sm" }, subtitle),
                React__default.createElement(GrayTypography, { as: "p", fontSize: "sm" }, supportText))))));

var _path$d;
function _extends$l() { return _extends$l = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$l.apply(null, arguments); }
var SvgErrorCircle = function SvgErrorCircle(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$l({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$d || (_path$d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM6.70711 5.29289C6.31658 4.90237 5.68342 4.90237 5.29289 5.29289C4.90237 5.68342 4.90237 6.31658 5.29289 6.70711L6.58579 8L5.29289 9.29289C4.90237 9.68342 4.90237 10.3166 5.29289 10.7071C5.68342 11.0976 6.31658 11.0976 6.70711 10.7071L8 9.41421L9.29289 10.7071C9.68342 11.0976 10.3166 11.0976 10.7071 10.7071C11.0976 10.3166 11.0976 9.68342 10.7071 9.29289L9.41421 8L10.7071 6.70711C11.0976 6.31658 11.0976 5.68342 10.7071 5.29289C10.3166 4.90237 9.68342 4.90237 9.29289 5.29289L8 6.58579L6.70711 5.29289Z",
    fill: "currentColor"
  })));
};
var ForwardRef$i = /*#__PURE__*/forwardRef(SvgErrorCircle);
var Memo$i = /*#__PURE__*/memo(ForwardRef$i);

var _path$c;
function _extends$k() { return _extends$k = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$k.apply(null, arguments); }
var SvgInfoCircle = function SvgInfoCircle(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$k({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$c || (_path$c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM9 4C9 4.55228 8.55228 5 8 5C7.44772 5 7 4.55228 7 4C7 3.44772 7.44772 3 8 3C8.55228 3 9 3.44772 9 4ZM7 7C6.44772 7 6 7.44772 6 8C6 8.55229 6.44772 9 7 9V12C7 12.5523 7.44772 13 8 13H9C9.55228 13 10 12.5523 10 12C10 11.4477 9.55228 11 9 11V8C9 7.44772 8.55228 7 8 7H7Z",
    fill: "currentColor"
  })));
};
var ForwardRef$h = /*#__PURE__*/forwardRef(SvgInfoCircle);
var Memo$h = /*#__PURE__*/memo(ForwardRef$h);

var _path$b;
function _extends$j() { return _extends$j = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$j.apply(null, arguments); }
var SvgSuccessCircle = function SvgSuccessCircle(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$j({
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$b || (_path$b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM11.7071 6.70711C12.0976 6.31658 12.0976 5.68342 11.7071 5.29289C11.3166 4.90237 10.6834 4.90237 10.2929 5.29289L7 8.58579L5.70711 7.29289C5.31658 6.90237 4.68342 6.90237 4.29289 7.29289C3.90237 7.68342 3.90237 8.31658 4.29289 8.70711L6.29289 10.7071C6.68342 11.0976 7.31658 11.0976 7.70711 10.7071L11.7071 6.70711Z",
    fill: "currentColor"
  })));
};
var ForwardRef$g = /*#__PURE__*/forwardRef(SvgSuccessCircle);
var Memo$g = /*#__PURE__*/memo(ForwardRef$g);

var _path$a;
function _extends$i() { return _extends$i = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$i.apply(null, arguments); }
var SvgWarningTriangle = function SvgWarningTriangle(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$i({
    viewBox: "0 0 16 14",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$a || (_path$a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M6.25694 1.09882C7.02154 -0.26048 8.97863 -0.260481 9.74324 1.09882L15.3235 11.0194C16.0735 12.3526 15.11 13.9999 13.5804 13.9999H2.41978C0.890129 13.9999 -0.073299 12.3526 0.676631 11.0194L6.25694 1.09882ZM9 11C9 11.5523 8.55229 12 8 12C7.44772 12 7 11.5523 7 11C7 10.4477 7.44772 10 8 10C8.55229 10 9 10.4477 9 11ZM8 3C7.44772 3 7 3.44772 7 4V7C7 7.55228 7.44772 8 8 8C8.55228 8 9 7.55228 9 7V4C9 3.44772 8.55228 3 8 3Z",
    fill: "currentColor"
  })));
};
var ForwardRef$f = /*#__PURE__*/forwardRef(SvgWarningTriangle);
var Memo$f = /*#__PURE__*/memo(ForwardRef$f);

const getAlertStyle = (severity) => {
    switch (severity) {
        case 'neutral':
            return 'bg-gray-50 text-gray-400';
        case 'warning':
            return 'bg-lemon-50 text-lemon-700';
        case 'success':
            return 'bg-green-50 text-green-700';
        case 'info':
            return 'bg-blue-50 text-blue-700';
        default:
            return 'bg-rsbry-50 text-rsbry-700';
    }
};
const getAlertHeaderTextColor = (severity) => {
    switch (severity) {
        case 'neutral':
            return 'text-gray-400';
        case 'warning':
            return 'text-lemon-800';
        case 'success':
            return 'text-green-800';
        case 'info':
            return 'text-blue-800';
        default:
            return 'text-rsbry-800';
    }
};
const getTextSizeClass = (size = 'sm') => {
    switch (size) {
        case 'xs':
            return 'text-xs';
        case 'sm':
            return 'text-sm';
        case 'base':
            return 'text-base';
        case 'lg':
            return 'text-lg';
        case 'xl':
            return 'text-xl';
        default:
            return 'text-sm';
    }
};
const StyledAlert = _default$1(Stack$5) `
    ${({ $severity }) => getAlertStyle($severity)}
    rounded-md
    p-4
`;
const StyledAlertHeader = _default$1.h3 `
  ${({ $size }) => getTextSizeClass($size)}
  font-medium
  text-inherit
  ${({ $severity }) => getAlertHeaderTextColor($severity)}
`;
const StyledAlertSubHeader = _default$1.div `
  ${({ $size }) => getTextSizeClass($size)}
  text-inherit
`;

const getIcon = (severity, icon) => {
    if (icon)
        return icon;
    switch (severity) {
        case 'neutral':
            return React__default.createElement(Memo$h, { "data-testid": "warning-triangle-icon", className: "text-gray-400", "aria-hidden": "true" });
        case 'warning':
            return React__default.createElement(Memo$f, { "data-testid": "warning-triangle-icon", className: "text-lemon-700", "aria-hidden": "true" });
        case 'success':
            return React__default.createElement(Memo$g, { "data-testid": "success-circle-icon", className: "text-green-700", "aria-hidden": "true" });
        case 'info':
            return React__default.createElement(Memo$h, { "data-testid": "info-circle-icon", className: "text-blue-700", "aria-hidden": "true" });
        default:
            return React__default.createElement(Memo$i, { "data-testid": "error-circle-icon", className: "text-rsbry-700", "aria-hidden": "true" });
    }
};
const Alert = ({ actions = [], className, icon, severity, subtitle, subtitleSize, title, titleSize, id, }) => (React__default.createElement(StyledAlert, { "$severity": severity, className: className, id: id, gap: 4, alignItems: (!subtitle || !title) && actions.length === 0 && 'center' },
    React__default.createElement(Icon, { node: getIcon(severity, icon), nodeSize: 4.5, size: 6, className: "flex-shrink-0", "aria-hidden": "true" }),
    React__default.createElement("div", { className: "flex flex-col space-y-1 w-full" },
        title && (React__default.createElement(StyledAlertHeader, { "$severity": severity, "$size": titleSize }, title)),
        subtitle && (React__default.createElement(StyledAlertSubHeader, { "$size": subtitleSize }, typeof subtitle === 'string' ? React__default.createElement("p", null, subtitle) : subtitle)),
        actions.length > 0 && (React__default.createElement("div", { className: "flex space-x-2" }, actions.map(({ label, ...props }) => (React__default.createElement(Button, { key: label, size: "sm", variant: severity === 'none' || severity === 'neutral' ? 'info' : severity, rounded: "regular", className: "m-0", ...props }, label))))))));
const AlertMemo = memo(Alert);

const StyledFormButtonWrapper = _default$1.div `
  w-full
  max-w-7xl
  py-6
`;
const StyledFormButtonGrid = _default$1.div `
  lg:grid
  lg:grid-cols-3
  lg:gap-6
`;
const StyledFormButtonLeftColumn = _default$1.div `
  lg:col-span-1
`;
const StyledFormButtonRightColumn = _default$1.div `
  flex
  flex-col
  mt-5
  lg:mt-0
  lg:col-span-2
`;
const StyledFormButtonAlertCard = _default$1.div `
  shadow
  sm:rounded-md
  sm:overflow-hidden
`;
const StyledFormButtonContainer = _default$1.div `
  my-3
  self-end
`;

const FormButton = ({ alert, className, ...props }) => (React__default.createElement(StyledFormButtonWrapper, { className: className },
    React__default.createElement(StyledFormButtonGrid, null,
        React__default.createElement(StyledFormButtonLeftColumn, null),
        React__default.createElement(StyledFormButtonRightColumn, null,
            React__default.createElement(StyledFormButtonAlertCard, null, !!alert && React__default.createElement(Alert, { ...alert })),
            React__default.createElement(StyledFormButtonContainer, null,
                React__default.createElement(Button, { className: "py-3", ...props }))))));

const IconContainer = _default$1.div `
  w-6
  h-6
  flex
  items-center
  justify-center
  text-gray-500
`;

const FormTitle = ({ className, icon, subtitle, title }) => (React__default.createElement(Stack$5, { direction: "column", space: 1, className: className },
    React__default.createElement(Stack$5, { alignItems: "center", space: 2.5 },
        !!icon && React__default.createElement(IconContainer, null, icon),
        React__default.createElement(Typography, { as: "h3", fontSize: "lg", fontWeight: "medium", lineHeight: 6, color: colors$3.gray[900] }, title)),
    subtitle && (React__default.createElement(Typography, { as: "p", fontSize: "sm", color: colors$3.gray[500], lineHeight: 5 }, subtitle))));
const FormTitleMemo = memo(FormTitle);

const colorVariant = (completed, total) => {
    if (completed === 0)
        return 'red';
    if (completed < total)
        return 'yellow';
    return 'green';
};
const createProgressComponent = (isMemo) => function Progress({ className, completed, total }) {
    // Do not use memoized components when the component should not be memoized. This could lead to high memory usage.
    const BadgeComponent = isMemo ? BadgeMemo : Badge;
    const stepsCompleted = completed === total;
    return (React__default.createElement(BadgeComponent, { colorVariant: colorVariant(completed, total), icon: stepsCompleted ? React__default.createElement(Memo$r, { style: { color: colors$3.green[400] } }) : undefined, className: className },
        completed,
        "/",
        total,
        " Complete"));
};
const Progress = createProgressComponent(false);
const ProgressMemo = memo(createProgressComponent(true));

const StyledTwoColumnLayoutStickyHeader = _default$1.div `
  sticky
  top-0
`;

const createFormStickyHeader = (isMemo) => function FormStickyHeader({ children, icon, progress, subtitle, title }) {
    const FormTitleComponent = isMemo ? FormTitleMemo : FormTitle;
    return (React__default.createElement(StyledTwoColumnLayoutStickyHeader, null,
        React__default.createElement(FormTitleComponent, { title: title, subtitle: subtitle, icon: icon }),
        !children && progress && React__default.createElement(Progress, { total: progress.total, completed: progress.completed }),
        children));
};
const FormStickyHeader = createFormStickyHeader(false);
const FormStickyHeaderMemo = memo(createFormStickyHeader(true));

var reactTableExports = {};
var reactTable = {
  get exports(){ return reactTableExports; },
  set exports(v){ reactTableExports = v; },
};

var reactTable_production_minExports = {};
var reactTable_production_min = {
  get exports(){ return reactTable_production_minExports; },
  set exports(v){ reactTable_production_minExports = v; },
};

var hasRequiredReactTable_production_min;

function requireReactTable_production_min () {
	if (hasRequiredReactTable_production_min) return reactTable_production_minExports;
	hasRequiredReactTable_production_min = 1;
	(function (module, exports) {
		!function(e,t){t(exports,React__default);}(commonjsGlobal,(function(e,t){function n(e,t,n,o,r,i,u){try{var l=e[i](u),s=l.value;}catch(e){return void n(e)}l.done?t(s):Promise.resolve(s).then(o,r);}function o(e){return function(){var t=this,o=arguments;return new Promise((function(r,i){var u=e.apply(t,o);function l(e){n(u,r,i,l,s,"next",e);}function s(e){n(u,r,i,l,s,"throw",e);}l(void 0);}))}}function r(){return (r=Object.assign||function(e){for(var t=1;t<arguments.length;t++){var n=arguments[t];for(var o in n)Object.prototype.hasOwnProperty.call(n,o)&&(e[o]=n[o]);}return e}).apply(this,arguments)}function i(e,t){if(null==e)return {};var n,o,r={},i=Object.keys(e);for(o=0;o<i.length;o++)n=i[o],t.indexOf(n)>=0||(r[n]=e[n]);return r}function u(e){var t=function(e,t){if("object"!=typeof e||null===e)return e;var n=e[Symbol.toPrimitive];if(void 0!==n){var o=n.call(e,t||"default");if("object"!=typeof o)return o;throw new TypeError("@@toPrimitive must return a primitive value.")}return ("string"===t?String:Number)(e)}(e,"string");return "symbol"==typeof t?t:String(t)}t=t&&Object.prototype.hasOwnProperty.call(t,"default")?t.default:t;var l={init:"init"},s=function(e){var t=e.value;return void 0===t?"":t},a=function(){return t.createElement(t.Fragment,null," ")},c={Cell:s,width:150,minWidth:0,maxWidth:Number.MAX_SAFE_INTEGER};function d(){for(var e=arguments.length,t=new Array(e),n=0;n<e;n++)t[n]=arguments[n];return t.reduce((function(e,t){var n=t.style,o=t.className;return e=r({},e,{},i(t,["style","className"])),n&&(e.style=e.style?r({},e.style||{},{},n||{}):n),o&&(e.className=e.className?e.className+" "+o:o),""===e.className&&delete e.className,e}),{})}var f=function(e,t){return void 0===t&&(t={}),function(n){return void 0===n&&(n={}),[].concat(e,[n]).reduce((function(e,o){return function e(t,n,o){return "function"==typeof n?e({},n(t,o)):Array.isArray(n)?d.apply(void 0,[t].concat(n)):d(t,n)}(e,o,r({},t,{userProps:n}))}),{})}},p=function(e,t,n,o){return void 0===n&&(n={}),e.reduce((function(e,t){return t(e,n)}),t)},g=function(e,t,n){return void 0===n&&(n={}),e.forEach((function(e){e(t,n);}))};function v(e,t,n,o){e.findIndex((function(e){return e.pluginName===n}));t.forEach((function(t){e.findIndex((function(e){return e.pluginName===t}));}));}function m(e,t){return "function"==typeof e?e(t):e}function h(e){var n=t.useRef();return n.current=e,t.useCallback((function(){return n.current}),[])}var y="undefined"!=typeof document?t.useLayoutEffect:t.useEffect;function w(e,n){var o=t.useRef(!1);y((function(){o.current&&e(),o.current=!0;}),n);}function R(e,t,n){return void 0===n&&(n={}),function(o,i){void 0===i&&(i={});var u="string"==typeof o?t[o]:o;if(void 0===u)throw console.info(t),new Error("Renderer Error ☝️");return b(u,r({},e,{column:t},n,{},i))}}function b(e,n){return function(e){return "function"==typeof e&&((t=Object.getPrototypeOf(e)).prototype&&t.prototype.isReactComponent);var t;}(o=e)||"function"==typeof o||function(e){return "object"==typeof e&&"symbol"==typeof e.$$typeof&&["react.memo","react.forward_ref"].includes(e.$$typeof.description)}(o)?t.createElement(e,n):e;var o;}function S(e,t,n){return void 0===n&&(n=0),e.map((function(e){return x(e=r({},e,{parent:t,depth:n})),e.columns&&(e.columns=S(e.columns,e,n+1)),e}))}function C(e){return G(e,"columns")}function x(e){var t=e.id,n=e.accessor,o=e.Header;if("string"==typeof n){t=t||n;var r=n.split(".");n=function(e){return function(e,t,n){if(!t)return e;var o,r="function"==typeof t?t:JSON.stringify(t),i=E.get(r)||function(){var e=function(e){return function e(t,n){void 0===n&&(n=[]);if(Array.isArray(t))for(var o=0;o<t.length;o+=1)e(t[o],n);else n.push(t);return n}(e).map((function(e){return String(e).replace(".","_")})).join(".").replace(T,".").replace(O,"").split(".")}(t);return E.set(r,e),e}();try{o=i.reduce((function(e,t){return e[t]}),e);}catch(e){}return void 0!==o?o:n}(e,r)};}if(!t&&"string"==typeof o&&o&&(t=o),!t&&e.columns)throw console.error(e),new Error('A column ID (or unique "Header" value) is required!');if(!t)throw console.error(e),new Error("A column ID (or string accessor) is required!");return Object.assign(e,{id:t,accessor:n}),e}function P(e,t){if(!t)throw new Error;return Object.assign(e,r({Header:a,Footer:a},c,{},t,{},e)),Object.assign(e,{originalWidth:e.width}),e}function B(e,t,n){void 0===n&&(n=function(){return {}});for(var o=[],i=e,u=0,l=function(){return u++},s=function(){var e={headers:[]},u=[],s=i.some((function(e){return e.parent}));i.forEach((function(o){var i,a=[].concat(u).reverse()[0];if(s){if(o.parent)i=r({},o.parent,{originalId:o.parent.id,id:o.parent.id+"_"+l(),headers:[o]},n(o));else i=P(r({originalId:o.id+"_placeholder",id:o.id+"_placeholder_"+l(),placeholderOf:o,headers:[o]},n(o)),t);a&&a.originalId===i.originalId?a.headers.push(o):u.push(i);}e.headers.push(o);})),o.push(e),i=u;};i.length;)s();return o.reverse()}var E=new Map;function I(){for(var e=arguments.length,t=new Array(e),n=0;n<e;n++)t[n]=arguments[n];for(var o=0;o<t.length;o+=1)if(void 0!==t[o])return t[o]}function F(e){if("function"==typeof e)return e}function G(e,t){var n=[];return function e(o){o.forEach((function(o){o[t]?e(o[t]):n.push(o);}));}(e),n}function A(e,t){var n=t.manualExpandedKey,o=t.expanded,r=t.expandSubRows,i=void 0===r||r,u=[];return e.forEach((function(e){return function e(t,r){void 0===r&&(r=!0),t.isExpanded=t.original&&t.original[n]||o[t.id],t.canExpand=t.subRows&&!!t.subRows.length,r&&u.push(t),t.subRows&&t.subRows.length&&t.isExpanded&&t.subRows.forEach((function(t){return e(t,i)}));}(e)})),u}function k(e,t,n){return F(e)||t[e]||n[e]||n.text}function H(e,t,n){return e?e(t,n):void 0===t}function W(){throw new Error("React-Table: You have not called prepareRow(row) one or more rows you are attempting to render.")}var z=null;var T=/\[/g,O=/\]/g;var M=function(e){return r({role:"table"},e)},j=function(e){return r({role:"rowgroup"},e)},L=function(e,t){var n=t.column;return r({key:"header_"+n.id,colSpan:n.totalVisibleHeaderCount,role:"columnheader"},e)},N=function(e,t){var n=t.column;return r({key:"footer_"+n.id,colSpan:n.totalVisibleHeaderCount},e)},D=function(e,t){return r({key:"headerGroup_"+t.index,role:"row"},e)},V=function(e,t){return r({key:"footerGroup_"+t.index},e)},_=function(e,t){return r({key:"row_"+t.row.id,role:"row"},e)},X=function(e,t){var n=t.cell;return r({key:"cell_"+n.row.id+"_"+n.column.id,role:"cell"},e)};function q(){return {useOptions:[],stateReducers:[],useControlledState:[],columns:[],columnsDeps:[],allColumns:[],allColumnsDeps:[],accessValue:[],materializedColumns:[],materializedColumnsDeps:[],useInstanceAfterData:[],visibleColumns:[],visibleColumnsDeps:[],headerGroups:[],headerGroupsDeps:[],useInstanceBeforeDimensions:[],useInstance:[],prepareRow:[],getTableProps:[M],getTableBodyProps:[j],getHeaderGroupProps:[D],getFooterGroupProps:[V],getHeaderProps:[L],getFooterProps:[N],getRowProps:[_],getCellProps:[X],useFinalInstance:[]}}l.resetHiddenColumns="resetHiddenColumns",l.toggleHideColumn="toggleHideColumn",l.setHiddenColumns="setHiddenColumns",l.toggleHideAllColumns="toggleHideAllColumns";var K=function(e){e.getToggleHiddenProps=[U],e.getToggleHideAllColumnsProps=[$],e.stateReducers.push(J),e.useInstanceBeforeDimensions.push(Y),e.headerGroupsDeps.push((function(e,t){var n=t.instance;return [].concat(e,[n.state.hiddenColumns])})),e.useInstance.push(Q);};K.pluginName="useColumnVisibility";var U=function(e,t){var n=t.column;return [e,{onChange:function(e){n.toggleHidden(!e.target.checked);},style:{cursor:"pointer"},checked:n.isVisible,title:"Toggle Column Visible"}]},$=function(e,t){var n=t.instance;return [e,{onChange:function(e){n.toggleHideAllColumns(!e.target.checked);},style:{cursor:"pointer"},checked:!n.allColumnsHidden&&!n.state.hiddenColumns.length,title:"Toggle All Columns Hidden",indeterminate:!n.allColumnsHidden&&n.state.hiddenColumns.length}]};function J(e,t,n,o){if(t.type===l.init)return r({hiddenColumns:[]},e);if(t.type===l.resetHiddenColumns)return r({},e,{hiddenColumns:o.initialState.hiddenColumns||[]});if(t.type===l.toggleHideColumn){var i=(void 0!==t.value?t.value:!e.hiddenColumns.includes(t.columnId))?[].concat(e.hiddenColumns,[t.columnId]):e.hiddenColumns.filter((function(e){return e!==t.columnId}));return r({},e,{hiddenColumns:i})}return t.type===l.setHiddenColumns?r({},e,{hiddenColumns:m(t.value,e.hiddenColumns)}):t.type===l.toggleHideAllColumns?r({},e,{hiddenColumns:(void 0!==t.value?t.value:!e.hiddenColumns.length)?o.allColumns.map((function(e){return e.id})):[]}):void 0}function Y(e){var n=e.headers,o=e.state.hiddenColumns;t.useRef(!1).current;var r=0;n.forEach((function(e){return r+=function e(t,n){t.isVisible=n&&!o.includes(t.id);var r=0;return t.headers&&t.headers.length?t.headers.forEach((function(n){return r+=e(n,t.isVisible)})):r=t.isVisible?1:0,t.totalVisibleHeaderCount=r,r}(e,!0)}));}function Q(e){var n=e.columns,o=e.flatHeaders,r=e.dispatch,i=e.allColumns,u=e.getHooks,s=e.state.hiddenColumns,a=e.autoResetHiddenColumns,c=void 0===a||a,d=h(e),p=i.length===s.length,g=t.useCallback((function(e,t){return r({type:l.toggleHideColumn,columnId:e,value:t})}),[r]),v=t.useCallback((function(e){return r({type:l.setHiddenColumns,value:e})}),[r]),m=t.useCallback((function(e){return r({type:l.toggleHideAllColumns,value:e})}),[r]),y=f(u().getToggleHideAllColumnsProps,{instance:d()});o.forEach((function(e){e.toggleHidden=function(t){r({type:l.toggleHideColumn,columnId:e.id,value:t});},e.getToggleHiddenProps=f(u().getToggleHiddenProps,{instance:d(),column:e});}));var R=h(c);w((function(){R()&&r({type:l.resetHiddenColumns});}),[r,n]),Object.assign(e,{allColumnsHidden:p,toggleHideColumn:g,setHiddenColumns:v,toggleHideAllColumns:m,getToggleHideAllColumnsProps:y});}var Z={},ee={},te=function(e,t,n){return e},ne=function(e,t){return e.subRows||[]},oe=function(e,t,n){return ""+(n?[n.id,t].join("."):t)},re=function(e){return e};function ie(e){var t=e.initialState,n=void 0===t?Z:t,o=e.defaultColumn,u=void 0===o?ee:o,l=e.getSubRows,s=void 0===l?ne:l,a=e.getRowId,c=void 0===a?oe:a,d=e.stateReducer,f=void 0===d?te:d,p=e.useControlledState,g=void 0===p?re:p;return r({},i(e,["initialState","defaultColumn","getSubRows","getRowId","stateReducer","useControlledState"]),{initialState:n,defaultColumn:u,getSubRows:s,getRowId:c,stateReducer:f,useControlledState:g})}function ue(e,t){void 0===t&&(t=0);var n=0,o=0,r=0,i=0;return e.forEach((function(e){var u=e.headers;if(e.totalLeft=t,u&&u.length){var l=ue(u,t),s=l[0],a=l[1],c=l[2],d=l[3];e.totalMinWidth=s,e.totalWidth=a,e.totalMaxWidth=c,e.totalFlexWidth=d;}else e.totalMinWidth=e.minWidth,e.totalWidth=Math.min(Math.max(e.minWidth,e.width),e.maxWidth),e.totalMaxWidth=e.maxWidth,e.totalFlexWidth=e.canResize?e.totalWidth:0;e.isVisible&&(t+=e.totalWidth,n+=e.totalMinWidth,o+=e.totalWidth,r+=e.totalMaxWidth,i+=e.totalFlexWidth);})),[n,o,r,i]}function le(e){var t=e.data,n=e.rows,o=e.flatRows,r=e.rowsById,i=e.column,u=e.getRowId,l=e.getSubRows,s=e.accessValueHooks,a=e.getInstance;t.forEach((function(e,c){return function e(n,c,d,f,g){void 0===d&&(d=0);var v=n,m=u(n,c,f),h=r[m];if(h)h.subRows&&h.originalSubRows.forEach((function(t,n){return e(t,n,d+1,h)}));else if((h={id:m,original:v,index:c,depth:d,cells:[{}]}).cells.map=W,h.cells.filter=W,h.cells.forEach=W,h.cells[0].getCellProps=W,h.values={},g.push(h),o.push(h),r[m]=h,h.originalSubRows=l(n,c),h.originalSubRows){var y=[];h.originalSubRows.forEach((function(t,n){return e(t,n,d+1,h,y)})),h.subRows=y;}i.accessor&&(h.values[i.id]=i.accessor(n,c,h,g,t)),h.values[i.id]=p(s,h.values[i.id],{row:h,column:i,instance:a()});}(e,c,0,void 0,n)}));}l.resetExpanded="resetExpanded",l.toggleRowExpanded="toggleRowExpanded",l.toggleAllRowsExpanded="toggleAllRowsExpanded";var se=function(e){e.getToggleAllRowsExpandedProps=[ae],e.getToggleRowExpandedProps=[ce],e.stateReducers.push(de),e.useInstance.push(fe),e.prepareRow.push(pe);};se.pluginName="useExpanded";var ae=function(e,t){var n=t.instance;return [e,{onClick:function(e){n.toggleAllRowsExpanded();},style:{cursor:"pointer"},title:"Toggle All Rows Expanded"}]},ce=function(e,t){var n=t.row;return [e,{onClick:function(){n.toggleRowExpanded();},style:{cursor:"pointer"},title:"Toggle Row Expanded"}]};function de(e,t,n,o){if(t.type===l.init)return r({expanded:{}},e);if(t.type===l.resetExpanded)return r({},e,{expanded:o.initialState.expanded||{}});if(t.type===l.toggleAllRowsExpanded){var s=t.value,a=o.rowsById,c=Object.keys(a).length===Object.keys(e.expanded).length;if(void 0!==s?s:!c){var d={};return Object.keys(a).forEach((function(e){d[e]=!0;})),r({},e,{expanded:d})}return r({},e,{expanded:{}})}if(t.type===l.toggleRowExpanded){var f,p=t.id,g=t.value,v=e.expanded[p],m=void 0!==g?g:!v;if(!v&&m)return r({},e,{expanded:r({},e.expanded,(f={},f[p]=!0,f))});if(v&&!m){var h=e.expanded;h[p];return r({},e,{expanded:i(h,[p].map(u))})}return e}}function fe(e){var n=e.data,o=e.rows,r=e.rowsById,i=e.manualExpandedKey,u=void 0===i?"expanded":i,s=e.paginateExpandedRows,a=void 0===s||s,c=e.expandSubRows,d=void 0===c||c,p=e.autoResetExpanded,g=void 0===p||p,m=e.getHooks,y=e.plugins,R=e.state.expanded,b=e.dispatch;v(y,["useSortBy","useGroupBy","usePivotColumns","useGlobalFilter"],"useExpanded");var S=h(g),C=Boolean(Object.keys(r).length&&Object.keys(R).length);C&&Object.keys(r).some((function(e){return !R[e]}))&&(C=!1),w((function(){S()&&b({type:l.resetExpanded});}),[b,n]);var x=t.useCallback((function(e,t){b({type:l.toggleRowExpanded,id:e,value:t});}),[b]),P=t.useCallback((function(e){return b({type:l.toggleAllRowsExpanded,value:e})}),[b]),B=t.useMemo((function(){return a?A(o,{manualExpandedKey:u,expanded:R,expandSubRows:d}):o}),[a,o,u,R,d]),E=t.useMemo((function(){return function(e){var t=0;return Object.keys(e).forEach((function(e){var n=e.split(".");t=Math.max(t,n.length);})),t}(R)}),[R]),I=h(e),F=f(m().getToggleAllRowsExpandedProps,{instance:I()});Object.assign(e,{preExpandedRows:o,expandedRows:B,rows:B,expandedDepth:E,isAllRowsExpanded:C,toggleRowExpanded:x,toggleAllRowsExpanded:P,getToggleAllRowsExpandedProps:F});}function pe(e,t){var n=t.instance.getHooks,o=t.instance;e.toggleRowExpanded=function(t){return o.toggleRowExpanded(e.id,t)},e.getToggleRowExpandedProps=f(n().getToggleRowExpandedProps,{instance:o,row:e});}var ge=function(e,t,n){return e=e.filter((function(e){return t.some((function(t){var o=e.values[t];return String(o).toLowerCase().includes(String(n).toLowerCase())}))}))};ge.autoRemove=function(e){return !e};var ve=function(e,t,n){return e.filter((function(e){return t.some((function(t){var o=e.values[t];return void 0===o||String(o).toLowerCase()===String(n).toLowerCase()}))}))};ve.autoRemove=function(e){return !e};var me=function(e,t,n){return e.filter((function(e){return t.some((function(t){var o=e.values[t];return void 0===o||String(o)===String(n)}))}))};me.autoRemove=function(e){return !e};var he=function(e,t,n){return e.filter((function(e){return t.some((function(t){return e.values[t].includes(n)}))}))};he.autoRemove=function(e){return !e||!e.length};var ye=function(e,t,n){return e.filter((function(e){return t.some((function(t){var o=e.values[t];return o&&o.length&&n.every((function(e){return o.includes(e)}))}))}))};ye.autoRemove=function(e){return !e||!e.length};var we=function(e,t,n){return e.filter((function(e){return t.some((function(t){var o=e.values[t];return o&&o.length&&n.some((function(e){return o.includes(e)}))}))}))};we.autoRemove=function(e){return !e||!e.length};var Re=function(e,t,n){return e.filter((function(e){return t.some((function(t){var o=e.values[t];return n.includes(o)}))}))};Re.autoRemove=function(e){return !e||!e.length};var be=function(e,t,n){return e.filter((function(e){return t.some((function(t){return e.values[t]===n}))}))};be.autoRemove=function(e){return void 0===e};var Se=function(e,t,n){return e.filter((function(e){return t.some((function(t){return e.values[t]==n}))}))};Se.autoRemove=function(e){return null==e};var Ce=function(e,t,n){var o=n||[],r=o[0],i=o[1];if((r="number"==typeof r?r:-1/0)>(i="number"==typeof i?i:1/0)){var u=r;r=i,i=u;}return e.filter((function(e){return t.some((function(t){var n=e.values[t];return n>=r&&n<=i}))}))};Ce.autoRemove=function(e){return !e||"number"!=typeof e[0]&&"number"!=typeof e[1]};var xe=Object.freeze({__proto__:null,text:ge,exactText:ve,exactTextCase:me,includes:he,includesAll:ye,includesSome:we,includesValue:Re,exact:be,equals:Se,between:Ce});l.resetFilters="resetFilters",l.setFilter="setFilter",l.setAllFilters="setAllFilters";var Pe=function(e){e.stateReducers.push(Be),e.useInstance.push(Ee);};function Be(e,t,n,o){if(t.type===l.init)return r({filters:[]},e);if(t.type===l.resetFilters)return r({},e,{filters:o.initialState.filters||[]});if(t.type===l.setFilter){var i=t.columnId,u=t.filterValue,s=o.allColumns,a=o.filterTypes,c=s.find((function(e){return e.id===i}));if(!c)throw new Error("React-Table: Could not find a column with id: "+i);var d=k(c.filter,a||{},xe),f=e.filters.find((function(e){return e.id===i})),p=m(u,f&&f.value);return H(d.autoRemove,p,c)?r({},e,{filters:e.filters.filter((function(e){return e.id!==i}))}):r({},e,f?{filters:e.filters.map((function(e){return e.id===i?{id:i,value:p}:e}))}:{filters:[].concat(e.filters,[{id:i,value:p}])})}if(t.type===l.setAllFilters){var g=t.filters,v=o.allColumns,h=o.filterTypes;return r({},e,{filters:m(g,e.filters).filter((function(e){var t=v.find((function(t){return t.id===e.id}));return !H(k(t.filter,h||{},xe).autoRemove,e.value,t)}))})}}function Ee(e){var n=e.data,o=e.rows,r=e.flatRows,i=e.rowsById,u=e.allColumns,s=e.filterTypes,a=e.manualFilters,c=e.defaultCanFilter,d=void 0!==c&&c,f=e.disableFilters,p=e.state.filters,g=e.dispatch,v=e.autoResetFilters,m=void 0===v||v,y=t.useCallback((function(e,t){g({type:l.setFilter,columnId:e,filterValue:t});}),[g]),R=t.useCallback((function(e){g({type:l.setAllFilters,filters:e});}),[g]);u.forEach((function(e){var t=e.id,n=e.accessor,o=e.defaultCanFilter,r=e.disableFilters;e.canFilter=n?I(!0!==r&&void 0,!0!==f&&void 0,!0):I(o,d,!1),e.setFilter=function(t){return y(e.id,t)};var i=p.find((function(e){return e.id===t}));e.filterValue=i&&i.value;}));var b=t.useMemo((function(){if(a||!p.length)return [o,r,i];var e=[],t={};return [function n(o,r){void 0===r&&(r=0);var i=o;return (i=p.reduce((function(e,t){var n=t.id,o=t.value,i=u.find((function(e){return e.id===n}));if(!i)return e;0===r&&(i.preFilteredRows=e);var l=k(i.filter,s||{},xe);return l?(i.filteredRows=l(e,[n],o),i.filteredRows):(console.warn("Could not find a valid 'column.filter' for column with the ID: "+i.id+"."),e)}),o)).forEach((function(o){e.push(o),t[o.id]=o,o.subRows&&(o.subRows=o.subRows&&o.subRows.length>0?n(o.subRows,r+1):o.subRows);})),i}(o),e,t]}),[a,p,o,r,i,u,s]),S=b[0],C=b[1],x=b[2];t.useMemo((function(){u.filter((function(e){return !p.find((function(t){return t.id===e.id}))})).forEach((function(e){e.preFilteredRows=S,e.filteredRows=S;}));}),[S,p,u]);var P=h(m);w((function(){P()&&g({type:l.resetFilters});}),[g,a?null:n]),Object.assign(e,{preFilteredRows:o,preFilteredFlatRows:r,preFilteredRowsById:i,filteredRows:S,filteredFlatRows:C,filteredRowsById:x,rows:S,flatRows:C,rowsById:x,setFilter:y,setAllFilters:R});}Pe.pluginName="useFilters",l.resetGlobalFilter="resetGlobalFilter",l.setGlobalFilter="setGlobalFilter";var Ie=function(e){e.stateReducers.push(Fe),e.useInstance.push(Ge);};function Fe(e,t,n,o){if(t.type===l.resetGlobalFilter)return r({},e,{globalFilter:o.initialState.globalFilter||void 0});if(t.type===l.setGlobalFilter){var u=t.filterValue,s=o.userFilterTypes,a=k(o.globalFilter,s||{},xe),c=m(u,e.globalFilter);if(H(a.autoRemove,c)){e.globalFilter;return i(e,["globalFilter"])}return r({},e,{globalFilter:c})}}function Ge(e){var n=e.data,o=e.rows,r=e.flatRows,i=e.rowsById,u=e.allColumns,s=e.filterTypes,a=e.globalFilter,c=e.manualGlobalFilter,d=e.state.globalFilter,f=e.dispatch,p=e.autoResetGlobalFilter,g=void 0===p||p,v=e.disableGlobalFilter,m=t.useCallback((function(e){f({type:l.setGlobalFilter,filterValue:e});}),[f]),y=t.useMemo((function(){if(c||void 0===d)return [o,r,i];var e=[],t={},n=k(a,s||{},xe);if(!n)return console.warn("Could not find a valid 'globalFilter' option."),o;u.forEach((function(e){var t=e.disableGlobalFilter;e.canFilter=I(!0!==t&&void 0,!0!==v&&void 0,!0);}));var l=u.filter((function(e){return !0===e.canFilter}));return [function o(r){return (r=n(r,l.map((function(e){return e.id})),d)).forEach((function(n){e.push(n),t[n.id]=n,n.subRows=n.subRows&&n.subRows.length?o(n.subRows):n.subRows;})),r}(o),e,t]}),[c,d,a,s,u,o,r,i,v]),R=y[0],b=y[1],S=y[2],C=h(g);w((function(){C()&&f({type:l.resetGlobalFilter});}),[f,c?null:n]),Object.assign(e,{preGlobalFilteredRows:o,preGlobalFilteredFlatRows:r,preGlobalFilteredRowsById:i,globalFilteredRows:R,globalFilteredFlatRows:b,globalFilteredRowsById:S,rows:R,flatRows:b,rowsById:S,setGlobalFilter:m,disableGlobalFilter:v});}function Ae(e,t){return t.reduce((function(e,t){return e+("number"==typeof t?t:0)}),0)}Ie.pluginName="useGlobalFilter";var ke=Object.freeze({__proto__:null,sum:Ae,min:function(e){var t=e[0]||0;return e.forEach((function(e){"number"==typeof e&&(t=Math.min(t,e));})),t},max:function(e){var t=e[0]||0;return e.forEach((function(e){"number"==typeof e&&(t=Math.max(t,e));})),t},minMax:function(e){var t=e[0]||0,n=e[0]||0;return e.forEach((function(e){"number"==typeof e&&(t=Math.min(t,e),n=Math.max(n,e));})),t+".."+n},average:function(e){return Ae(0,e)/e.length},median:function(e){if(!e.length)return null;var t=Math.floor(e.length/2),n=[].concat(e).sort((function(e,t){return e-t}));return e.length%2!=0?n[t]:(n[t-1]+n[t])/2},unique:function(e){return Array.from(new Set(e).values())},uniqueCount:function(e){return new Set(e).size},count:function(e){return e.length}}),He=[],We={};l.resetGroupBy="resetGroupBy",l.setGroupBy="setGroupBy",l.toggleGroupBy="toggleGroupBy";var ze=function(e){e.getGroupByToggleProps=[Te],e.stateReducers.push(Oe),e.visibleColumnsDeps.push((function(e,t){var n=t.instance;return [].concat(e,[n.state.groupBy])})),e.visibleColumns.push(Me),e.useInstance.push(Le),e.prepareRow.push(Ne);};ze.pluginName="useGroupBy";var Te=function(e,t){var n=t.header;return [e,{onClick:n.canGroupBy?function(e){e.persist(),n.toggleGroupBy();}:void 0,style:{cursor:n.canGroupBy?"pointer":void 0},title:"Toggle GroupBy"}]};function Oe(e,t,n,o){if(t.type===l.init)return r({groupBy:[]},e);if(t.type===l.resetGroupBy)return r({},e,{groupBy:o.initialState.groupBy||[]});if(t.type===l.setGroupBy)return r({},e,{groupBy:t.value});if(t.type===l.toggleGroupBy){var i=t.columnId,u=t.value,s=void 0!==u?u:!e.groupBy.includes(i);return r({},e,s?{groupBy:[].concat(e.groupBy,[i])}:{groupBy:e.groupBy.filter((function(e){return e!==i}))})}}function Me(e,t){var n=t.instance.state.groupBy,o=n.map((function(t){return e.find((function(e){return e.id===t}))})).filter(Boolean),r=e.filter((function(e){return !n.includes(e.id)}));return (e=[].concat(o,r)).forEach((function(e){e.isGrouped=n.includes(e.id),e.groupedIndex=n.indexOf(e.id);})),e}var je={};function Le(e){var n=e.data,o=e.rows,i=e.flatRows,u=e.rowsById,s=e.allColumns,a=e.flatHeaders,c=e.groupByFn,d=void 0===c?De:c,p=e.manualGroupBy,g=e.aggregations,m=void 0===g?je:g,y=e.plugins,R=e.state.groupBy,b=e.dispatch,S=e.autoResetGroupBy,C=void 0===S||S,x=e.disableGroupBy,P=e.defaultCanGroupBy,B=e.getHooks;v(y,["useColumnOrder","useFilters"],"useGroupBy");var E=h(e);s.forEach((function(t){var n=t.accessor,o=t.defaultGroupBy,r=t.disableGroupBy;t.canGroupBy=n?I(t.canGroupBy,!0!==r&&void 0,!0!==x&&void 0,!0):I(t.canGroupBy,o,P,!1),t.canGroupBy&&(t.toggleGroupBy=function(){return e.toggleGroupBy(t.id)}),t.Aggregated=t.Aggregated||t.Cell;}));var F=t.useCallback((function(e,t){b({type:l.toggleGroupBy,columnId:e,value:t});}),[b]),A=t.useCallback((function(e){b({type:l.setGroupBy,value:e});}),[b]);a.forEach((function(e){e.getGroupByToggleProps=f(B().getGroupByToggleProps,{instance:E(),header:e});}));var k=t.useMemo((function(){if(p||!R.length)return [o,i,u,He,We,i,u];var e=R.filter((function(e){return s.find((function(t){return t.id===e}))})),t=[],n={},l=[],a={},c=[],f={},g=function o(i,u,p){if(void 0===u&&(u=0),u===e.length)return i.map((function(e){return r({},e,{depth:u})}));var g=e[u],v=d(i,g);return Object.entries(v).map((function(r,i){var d=r[0],v=r[1],h=g+":"+d,y=o(v,u+1,h=p?p+">"+h:h),w=u?G(v,"leafRows"):v,R=function(t,n,o){var r={};return s.forEach((function(i){if(e.includes(i.id))r[i.id]=n[0]?n[0].values[i.id]:null;else {var u="function"==typeof i.aggregate?i.aggregate:m[i.aggregate]||ke[i.aggregate];if(u){var l=n.map((function(e){return e.values[i.id]})),s=t.map((function(e){var t=e.values[i.id];if(!o&&i.aggregateValue){var n="function"==typeof i.aggregateValue?i.aggregateValue:m[i.aggregateValue]||ke[i.aggregateValue];if(!n)throw console.info({column:i}),new Error("React Table: Invalid column.aggregateValue option for column listed above");t=n(t,e,i);}return t}));r[i.id]=u(s,l);}else {if(i.aggregate)throw console.info({column:i}),new Error("React Table: Invalid column.aggregate option for column listed above");r[i.id]=null;}}})),r}(w,v,u),b={id:h,isGrouped:!0,groupByID:g,groupByVal:d,values:R,subRows:y,leafRows:w,depth:u,index:i};return y.forEach((function(e){t.push(e),n[e.id]=e,e.isGrouped?(l.push(e),a[e.id]=e):(c.push(e),f[e.id]=e);})),b}))}(o);return g.forEach((function(e){t.push(e),n[e.id]=e,e.isGrouped?(l.push(e),a[e.id]=e):(c.push(e),f[e.id]=e);})),[g,t,n,l,a,c,f]}),[p,R,o,i,u,s,m,d]),H=k[0],W=k[1],z=k[2],T=k[3],O=k[4],M=k[5],j=k[6],L=h(C);w((function(){L()&&b({type:l.resetGroupBy});}),[b,p?null:n]),Object.assign(e,{preGroupedRows:o,preGroupedFlatRow:i,preGroupedRowsById:u,groupedRows:H,groupedFlatRows:W,groupedRowsById:z,onlyGroupedFlatRows:T,onlyGroupedRowsById:O,nonGroupedFlatRows:M,nonGroupedRowsById:j,rows:H,flatRows:W,rowsById:z,toggleGroupBy:F,setGroupBy:A});}function Ne(e){e.allCells.forEach((function(t){var n;t.isGrouped=t.column.isGrouped&&t.column.id===e.groupByID,t.isPlaceholder=!t.isGrouped&&t.column.isGrouped,t.isAggregated=!t.isGrouped&&!t.isPlaceholder&&(null==(n=e.subRows)?void 0:n.length);}));}function De(e,t){return e.reduce((function(e,n,o){var r=""+n.values[t];return e[r]=Array.isArray(e[r])?e[r]:[],e[r].push(n),e}),{})}var Ve=/([0-9]+)/gm;function _e(e,t){return e===t?0:e>t?1:-1}function Xe(e,t,n){return [e.values[n],t.values[n]]}function qe(e){return "number"==typeof e?isNaN(e)||e===1/0||e===-1/0?"":String(e):"string"==typeof e?e:""}var Ke=Object.freeze({__proto__:null,alphanumeric:function(e,t,n){var o=Xe(e,t,n),r=o[0],i=o[1];for(r=qe(r),i=qe(i),r=r.split(Ve).filter(Boolean),i=i.split(Ve).filter(Boolean);r.length&&i.length;){var u=r.shift(),l=i.shift(),s=parseInt(u,10),a=parseInt(l,10),c=[s,a].sort();if(isNaN(c[0])){if(u>l)return 1;if(l>u)return -1}else {if(isNaN(c[1]))return isNaN(s)?-1:1;if(s>a)return 1;if(a>s)return -1}}return r.length-i.length},datetime:function(e,t,n){var o=Xe(e,t,n),r=o[0],i=o[1];return _e(r=r.getTime(),i=i.getTime())},basic:function(e,t,n){var o=Xe(e,t,n);return _e(o[0],o[1])},string:function(e,t,n){var o=Xe(e,t,n),r=o[0],i=o[1];for(r=r.split("").filter(Boolean),i=i.split("").filter(Boolean);r.length&&i.length;){var u=r.shift(),l=i.shift(),s=u.toLowerCase(),a=l.toLowerCase();if(s>a)return 1;if(a>s)return -1;if(u>l)return 1;if(l>u)return -1}return r.length-i.length},number:function(e,t,n){var o=Xe(e,t,n),r=o[0],i=o[1],u=/[^0-9.]/gi;return _e(r=Number(String(r).replace(u,"")),i=Number(String(i).replace(u,"")))}});l.resetSortBy="resetSortBy",l.setSortBy="setSortBy",l.toggleSortBy="toggleSortBy",l.clearSortBy="clearSortBy",c.sortType="alphanumeric",c.sortDescFirst=!1;var Ue=function(e){e.getSortByToggleProps=[$e],e.stateReducers.push(Je),e.useInstance.push(Ye);};Ue.pluginName="useSortBy";var $e=function(e,t){var n=t.instance,o=t.column,r=n.isMultiSortEvent,i=void 0===r?function(e){return e.shiftKey}:r;return [e,{onClick:o.canSort?function(e){e.persist(),o.toggleSortBy(void 0,!n.disableMultiSort&&i(e));}:void 0,style:{cursor:o.canSort?"pointer":void 0},title:o.canSort?"Toggle SortBy":void 0}]};function Je(e,t,n,o){if(t.type===l.init)return r({sortBy:[]},e);if(t.type===l.resetSortBy)return r({},e,{sortBy:o.initialState.sortBy||[]});if(t.type===l.clearSortBy)return r({},e,{sortBy:e.sortBy.filter((function(e){return e.id!==t.columnId}))});if(t.type===l.setSortBy)return r({},e,{sortBy:t.sortBy});if(t.type===l.toggleSortBy){var i,u=t.columnId,s=t.desc,a=t.multi,c=o.allColumns,d=o.disableMultiSort,f=o.disableSortRemove,p=o.disableMultiRemove,g=o.maxMultiSortColCount,v=void 0===g?Number.MAX_SAFE_INTEGER:g,m=e.sortBy,h=c.find((function(e){return e.id===u})).sortDescFirst,y=m.find((function(e){return e.id===u})),w=m.findIndex((function(e){return e.id===u})),R=null!=s,b=[];return "toggle"!==(i=!d&&a?y?"toggle":"add":w!==m.length-1||1!==m.length?"replace":y?"toggle":"replace")||f||R||a&&p||!(y&&y.desc&&!h||!y.desc&&h)||(i="remove"),"replace"===i?b=[{id:u,desc:R?s:h}]:"add"===i?(b=[].concat(m,[{id:u,desc:R?s:h}])).splice(0,b.length-v):"toggle"===i?b=m.map((function(e){return e.id===u?r({},e,{desc:R?s:!y.desc}):e})):"remove"===i&&(b=m.filter((function(e){return e.id!==u}))),r({},e,{sortBy:b})}}function Ye(e){var n=e.data,o=e.rows,r=e.flatRows,i=e.allColumns,u=e.orderByFn,s=void 0===u?Qe:u,a=e.sortTypes,c=e.manualSortBy,d=e.defaultCanSort,p=e.disableSortBy,g=e.flatHeaders,m=e.state.sortBy,y=e.dispatch,R=e.plugins,b=e.getHooks,S=e.autoResetSortBy,C=void 0===S||S;v(R,["useFilters","useGlobalFilter","useGroupBy","usePivotColumns"],"useSortBy");var x=t.useCallback((function(e){y({type:l.setSortBy,sortBy:e});}),[y]),P=t.useCallback((function(e,t,n){y({type:l.toggleSortBy,columnId:e,desc:t,multi:n});}),[y]),B=h(e);g.forEach((function(e){var t=e.accessor,n=e.canSort,o=e.disableSortBy,r=e.id,i=t?I(!0!==o&&void 0,!0!==p&&void 0,!0):I(d,n,!1);e.canSort=i,e.canSort&&(e.toggleSortBy=function(t,n){return P(e.id,t,n)},e.clearSortBy=function(){y({type:l.clearSortBy,columnId:e.id});}),e.getSortByToggleProps=f(b().getSortByToggleProps,{instance:B(),column:e});var u=m.find((function(e){return e.id===r}));e.isSorted=!!u,e.sortedIndex=m.findIndex((function(e){return e.id===r})),e.isSortedDesc=e.isSorted?u.desc:void 0;}));var E=t.useMemo((function(){if(c||!m.length)return [o,r];var e=[],t=m.filter((function(e){return i.find((function(t){return t.id===e.id}))}));return [function n(o){var r=s(o,t.map((function(e){var t=i.find((function(t){return t.id===e.id}));if(!t)throw new Error("React-Table: Could not find a column with id: "+e.id+" while sorting");var n=t.sortType,o=F(n)||(a||{})[n]||Ke[n];if(!o)throw new Error("React-Table: Could not find a valid sortType of '"+n+"' for column '"+e.id+"'.");return function(t,n){return o(t,n,e.id,e.desc)}})),t.map((function(e){var t=i.find((function(t){return t.id===e.id}));return t&&t.sortInverted?e.desc:!e.desc})));return r.forEach((function(t){e.push(t),t.subRows&&0!==t.subRows.length&&(t.subRows=n(t.subRows));})),r}(o),e]}),[c,m,o,r,i,s,a]),G=E[0],A=E[1],k=h(C);w((function(){k()&&y({type:l.resetSortBy});}),[c?null:n]),Object.assign(e,{preSortedRows:o,preSortedFlatRows:r,sortedRows:G,sortedFlatRows:A,rows:G,flatRows:A,setSortBy:x,toggleSortBy:P});}function Qe(e,t,n){return [].concat(e).sort((function(e,o){for(var r=0;r<t.length;r+=1){var i=t[r],u=!1===n[r]||"desc"===n[r],l=i(e,o);if(0!==l)return u?-l:l}return n[0]?e.index-o.index:o.index-e.index}))}l.resetPage="resetPage",l.gotoPage="gotoPage",l.setPageSize="setPageSize";var Ze=function(e){e.stateReducers.push(et),e.useInstance.push(tt);};function et(e,t,n,o){if(t.type===l.init)return r({pageSize:10,pageIndex:0},e);if(t.type===l.resetPage)return r({},e,{pageIndex:o.initialState.pageIndex||0});if(t.type===l.gotoPage){var i=o.pageCount,u=o.page,s=m(t.pageIndex,e.pageIndex),a=!1;return s>e.pageIndex?a=-1===i?u.length>=e.pageSize:s<i:s<e.pageIndex&&(a=s>-1),a?r({},e,{pageIndex:s}):e}if(t.type===l.setPageSize){var c=t.pageSize,d=e.pageSize*e.pageIndex;return r({},e,{pageIndex:Math.floor(d/c),pageSize:c})}}function tt(e){var n=e.rows,o=e.autoResetPage,r=void 0===o||o,i=e.manualExpandedKey,u=void 0===i?"expanded":i,s=e.plugins,a=e.pageCount,c=e.paginateExpandedRows,d=void 0===c||c,f=e.expandSubRows,p=void 0===f||f,g=e.state,m=g.pageSize,y=g.pageIndex,R=g.expanded,b=g.globalFilter,S=g.filters,C=g.groupBy,x=g.sortBy,P=e.dispatch,B=e.data,E=e.manualPagination;v(s,["useGlobalFilter","useFilters","useGroupBy","useSortBy","useExpanded"],"usePagination");var I=h(r);w((function(){I()&&P({type:l.resetPage});}),[P,E?null:B,b,S,C,x]);var F=E?a:Math.ceil(n.length/m),G=t.useMemo((function(){return F>0?[].concat(new Array(F)).fill(null).map((function(e,t){return t})):[]}),[F]),k=t.useMemo((function(){var e;if(E)e=n;else {var t=m*y,o=t+m;e=n.slice(t,o);}return d?e:A(e,{manualExpandedKey:u,expanded:R,expandSubRows:p})}),[p,R,u,E,y,m,d,n]),H=y>0,W=-1===F?k.length>=m:y<F-1,z=t.useCallback((function(e){P({type:l.gotoPage,pageIndex:e});}),[P]),T=t.useCallback((function(){return z((function(e){return e-1}))}),[z]),O=t.useCallback((function(){return z((function(e){return e+1}))}),[z]),M=t.useCallback((function(e){P({type:l.setPageSize,pageSize:e});}),[P]);Object.assign(e,{pageOptions:G,pageCount:F,page:k,canPreviousPage:H,canNextPage:W,gotoPage:z,previousPage:T,nextPage:O,setPageSize:M});}Ze.pluginName="usePagination",l.resetPivot="resetPivot",l.togglePivot="togglePivot";var nt=function(e){e.getPivotToggleProps=[rt],e.stateReducers.push(it),e.useInstanceAfterData.push(ut),e.allColumns.push(lt),e.accessValue.push(st),e.materializedColumns.push(at),e.materializedColumnsDeps.push(ct),e.visibleColumns.push(dt),e.visibleColumnsDeps.push(ft),e.useInstance.push(pt),e.prepareRow.push(gt);};nt.pluginName="usePivotColumns";var ot=[],rt=function(e,t){var n=t.header;return [e,{onClick:n.canPivot?function(e){e.persist(),n.togglePivot();}:void 0,style:{cursor:n.canPivot?"pointer":void 0},title:"Toggle Pivot"}]};function it(e,t,n,o){if(t.type===l.init)return r({pivotColumns:ot},e);if(t.type===l.resetPivot)return r({},e,{pivotColumns:o.initialState.pivotColumns||ot});if(t.type===l.togglePivot){var i=t.columnId,u=t.value,s=void 0!==u?u:!e.pivotColumns.includes(i);return r({},e,s?{pivotColumns:[].concat(e.pivotColumns,[i])}:{pivotColumns:e.pivotColumns.filter((function(e){return e!==i}))})}}function ut(e){e.allColumns.forEach((function(t){t.isPivotSource=e.state.pivotColumns.includes(t.id);}));}function lt(e,t){var n=t.instance;return e.forEach((function(e){e.isPivotSource=n.state.pivotColumns.includes(e.id),e.uniqueValues=new Set;})),e}function st(e,t){var n=t.column;return n.uniqueValues&&void 0!==e&&n.uniqueValues.add(e),e}function at(e,t){var n=t.instance,o=n.allColumns,i=n.state;if(!i.pivotColumns.length||!i.groupBy||!i.groupBy.length)return e;var u=i.pivotColumns.map((function(e){return o.find((function(t){return t.id===e}))})).filter(Boolean),l=o.filter((function(e){return !e.isPivotSource&&!i.groupBy.includes(e.id)&&!i.pivotColumns.includes(e.id)})),s=C(function e(t,n,o){void 0===t&&(t=0),void 0===o&&(o=[]);var i=u[t];return i?Array.from(i.uniqueValues).sort().map((function(u){var l=r({},i,{Header:i.PivotHeader||"string"==typeof i.header?i.Header+": "+u:u,isPivotGroup:!0,parent:n,depth:t,id:n?n.id+"."+i.id+"."+u:i.id+"."+u,pivotValue:u});return l.columns=e(t+1,l,[].concat(o,[function(e){return e.values[i.id]===u}])),l})):l.map((function(e){return r({},e,{canPivot:!1,isPivoted:!0,parent:n,depth:t,id:""+(n?n.id+"."+e.id:e.id),accessor:function(t,n,r){if(o.every((function(e){return e(r)})))return r.values[e.id]}})}))}());return [].concat(e,s)}function ct(e,t){var n=t.instance.state,o=n.pivotColumns,r=n.groupBy;return [].concat(e,[o,r])}function dt(e,t){var n=t.instance.state;return e=e.filter((function(e){return !e.isPivotSource})),n.pivotColumns.length&&n.groupBy&&n.groupBy.length&&(e=e.filter((function(e){return e.isGrouped||e.isPivoted}))),e}function ft(e,t){var n=t.instance;return [].concat(e,[n.state.pivotColumns,n.state.groupBy])}function pt(e){var t=e.columns,n=e.allColumns,o=e.flatHeaders,r=e.getHooks,i=e.plugins,u=e.dispatch,s=e.autoResetPivot,a=void 0===s||s,c=e.manaulPivot,d=e.disablePivot,p=e.defaultCanPivot;v(i,["useGroupBy"],"usePivotColumns");var g=h(e);n.forEach((function(t){var n=t.accessor,o=t.defaultPivot,r=t.disablePivot;t.canPivot=n?I(t.canPivot,!0!==r&&void 0,!0!==d&&void 0,!0):I(t.canPivot,o,p,!1),t.canPivot&&(t.togglePivot=function(){return e.togglePivot(t.id)}),t.Aggregated=t.Aggregated||t.Cell;}));o.forEach((function(e){e.getPivotToggleProps=f(r().getPivotToggleProps,{instance:g(),header:e});}));var m=h(a);w((function(){m()&&u({type:l.resetPivot});}),[u,c?null:t]),Object.assign(e,{togglePivot:function(e,t){u({type:l.togglePivot,columnId:e,value:t});}});}function gt(e){e.allCells.forEach((function(e){e.isPivoted=e.column.isPivoted;}));}l.resetSelectedRows="resetSelectedRows",l.toggleAllRowsSelected="toggleAllRowsSelected",l.toggleRowSelected="toggleRowSelected",l.toggleAllPageRowsSelected="toggleAllPageRowsSelected";var vt=function(e){e.getToggleRowSelectedProps=[mt],e.getToggleAllRowsSelectedProps=[ht],e.getToggleAllPageRowsSelectedProps=[yt],e.stateReducers.push(wt),e.useInstance.push(Rt),e.prepareRow.push(bt);};vt.pluginName="useRowSelect";var mt=function(e,t){var n=t.instance,o=t.row,r=n.manualRowSelectedKey,i=void 0===r?"isSelected":r;return [e,{onChange:function(e){o.toggleRowSelected(e.target.checked);},style:{cursor:"pointer"},checked:!(!o.original||!o.original[i])||o.isSelected,title:"Toggle Row Selected",indeterminate:o.isSomeSelected}]},ht=function(e,t){var n=t.instance;return [e,{onChange:function(e){n.toggleAllRowsSelected(e.target.checked);},style:{cursor:"pointer"},checked:n.isAllRowsSelected,title:"Toggle All Rows Selected",indeterminate:Boolean(!n.isAllRowsSelected&&Object.keys(n.state.selectedRowIds).length)}]},yt=function(e,t){var n=t.instance;return [e,{onChange:function(e){n.toggleAllPageRowsSelected(e.target.checked);},style:{cursor:"pointer"},checked:n.isAllPageRowsSelected,title:"Toggle All Current Page Rows Selected",indeterminate:Boolean(!n.isAllPageRowsSelected&&n.page.some((function(e){var t=e.id;return n.state.selectedRowIds[t]})))}]};function wt(e,t,n,o){if(t.type===l.init)return r({selectedRowIds:{}},e);if(t.type===l.resetSelectedRows)return r({},e,{selectedRowIds:o.initialState.selectedRowIds||{}});if(t.type===l.toggleAllRowsSelected){var i=t.value,u=o.isAllRowsSelected,s=o.rowsById,a=o.nonGroupedRowsById,c=void 0===a?s:a,d=void 0!==i?i:!u,f=Object.assign({},e.selectedRowIds);return d?Object.keys(c).forEach((function(e){f[e]=!0;})):Object.keys(c).forEach((function(e){delete f[e];})),r({},e,{selectedRowIds:f})}if(t.type===l.toggleRowSelected){var p=t.id,g=t.value,v=o.rowsById,m=o.selectSubRows,h=void 0===m||m,y=o.getSubRows,w=e.selectedRowIds[p],R=void 0!==g?g:!w;if(w===R)return e;var b=r({},e.selectedRowIds);return function e(t){var n=v[t];if(n&&(n.isGrouped||(R?b[t]=!0:delete b[t]),h&&y(n)))return y(n).forEach((function(t){return e(t.id)}))}(p),r({},e,{selectedRowIds:b})}if(t.type===l.toggleAllPageRowsSelected){var S=t.value,C=o.page,x=o.rowsById,P=o.selectSubRows,B=void 0===P||P,E=o.isAllPageRowsSelected,I=o.getSubRows,F=void 0!==S?S:!E,G=r({},e.selectedRowIds);return C.forEach((function(e){return function e(t){var n=x[t];if(n.isGrouped||(F?G[t]=!0:delete G[t]),B&&I(n))return I(n).forEach((function(t){return e(t.id)}))}(e.id)})),r({},e,{selectedRowIds:G})}return e}function Rt(e){var n=e.data,o=e.rows,r=e.getHooks,i=e.plugins,u=e.rowsById,s=e.nonGroupedRowsById,a=void 0===s?u:s,c=e.autoResetSelectedRows,d=void 0===c||c,p=e.state.selectedRowIds,g=e.selectSubRows,m=void 0===g||g,y=e.dispatch,R=e.page,b=e.getSubRows;v(i,["useFilters","useGroupBy","useSortBy","useExpanded","usePagination"],"useRowSelect");var S=t.useMemo((function(){var e=[];return o.forEach((function(t){var n=m?function e(t,n,o){if(n[t.id])return !0;var r=o(t);if(r&&r.length){var i=!0,u=!1;return r.forEach((function(t){u&&!i||(e(t,n,o)?u=!0:i=!1);})),!!i||!!u&&null}return !1}(t,p,b):!!p[t.id];t.isSelected=!!n,t.isSomeSelected=null===n,n&&e.push(t);})),e}),[o,m,p,b]),C=Boolean(Object.keys(a).length&&Object.keys(p).length),x=C;C&&Object.keys(a).some((function(e){return !p[e]}))&&(C=!1),C||R&&R.length&&R.some((function(e){var t=e.id;return !p[t]}))&&(x=!1);var P=h(d);w((function(){P()&&y({type:l.resetSelectedRows});}),[y,n]);var B=t.useCallback((function(e){return y({type:l.toggleAllRowsSelected,value:e})}),[y]),E=t.useCallback((function(e){return y({type:l.toggleAllPageRowsSelected,value:e})}),[y]),I=t.useCallback((function(e,t){return y({type:l.toggleRowSelected,id:e,value:t})}),[y]),F=h(e),G=f(r().getToggleAllRowsSelectedProps,{instance:F()}),A=f(r().getToggleAllPageRowsSelectedProps,{instance:F()});Object.assign(e,{selectedFlatRows:S,isAllRowsSelected:C,isAllPageRowsSelected:x,toggleRowSelected:I,toggleAllRowsSelected:B,getToggleAllRowsSelectedProps:G,getToggleAllPageRowsSelectedProps:A,toggleAllPageRowsSelected:E});}function bt(e,t){var n=t.instance;e.toggleRowSelected=function(t){return n.toggleRowSelected(e.id,t)},e.getToggleRowSelectedProps=f(n.getHooks().getToggleRowSelectedProps,{instance:n,row:e});}var St=function(e){return {}},Ct=function(e){return {}};l.setRowState="setRowState",l.setCellState="setCellState",l.resetRowState="resetRowState";var xt=function(e){e.stateReducers.push(Pt),e.useInstance.push(Bt),e.prepareRow.push(Et);};function Pt(e,t,n,o){var i=o.initialRowStateAccessor,u=void 0===i?St:i,s=o.initialCellStateAccessor,a=void 0===s?Ct:s,c=o.rowsById;if(t.type===l.init)return r({rowState:{}},e);if(t.type===l.resetRowState)return r({},e,{rowState:o.initialState.rowState||{}});if(t.type===l.setRowState){var d,f=t.rowId,p=t.value,g=void 0!==e.rowState[f]?e.rowState[f]:u(c[f]);return r({},e,{rowState:r({},e.rowState,(d={},d[f]=m(p,g),d))})}if(t.type===l.setCellState){var v,h,y,w,R,b=t.rowId,S=t.columnId,C=t.value,x=void 0!==e.rowState[b]?e.rowState[b]:u(c[b]),P=void 0!==(null==x?void 0:null==(v=x.cellState)?void 0:v[S])?x.cellState[S]:a(null==(h=c[b])?void 0:null==(y=h.cells)?void 0:y.find((function(e){return e.column.id===S})));return r({},e,{rowState:r({},e.rowState,(R={},R[b]=r({},x,{cellState:r({},x.cellState||{},(w={},w[S]=m(C,P),w))}),R))})}}function Bt(e){var n=e.autoResetRowState,o=void 0===n||n,r=e.data,i=e.dispatch,u=t.useCallback((function(e,t){return i({type:l.setRowState,rowId:e,value:t})}),[i]),s=t.useCallback((function(e,t,n){return i({type:l.setCellState,rowId:e,columnId:t,value:n})}),[i]),a=h(o);w((function(){a()&&i({type:l.resetRowState});}),[r]),Object.assign(e,{setRowState:u,setCellState:s});}function Et(e,t){var n=t.instance,o=n.initialRowStateAccessor,r=void 0===o?St:o,i=n.initialCellStateAccessor,u=void 0===i?Ct:i,l=n.state.rowState;e&&(e.state=void 0!==l[e.id]?l[e.id]:r(e),e.setState=function(t){return n.setRowState(e.id,t)},e.cells.forEach((function(t){e.state.cellState||(e.state.cellState={}),t.state=void 0!==e.state.cellState[t.column.id]?e.state.cellState[t.column.id]:u(t),t.setState=function(o){return n.setCellState(e.id,t.column.id,o)};})));}xt.pluginName="useRowState",l.resetColumnOrder="resetColumnOrder",l.setColumnOrder="setColumnOrder";var It=function(e){e.stateReducers.push(Ft),e.visibleColumnsDeps.push((function(e,t){var n=t.instance;return [].concat(e,[n.state.columnOrder])})),e.visibleColumns.push(Gt),e.useInstance.push(At);};function Ft(e,t,n,o){return t.type===l.init?r({columnOrder:[]},e):t.type===l.resetColumnOrder?r({},e,{columnOrder:o.initialState.columnOrder||[]}):t.type===l.setColumnOrder?r({},e,{columnOrder:m(t.columnOrder,e.columnOrder)}):void 0}function Gt(e,t){var n=t.instance.state.columnOrder;if(!n||!n.length)return e;for(var o=[].concat(n),r=[].concat(e),i=[],u=function(){var e=o.shift(),t=r.findIndex((function(t){return t.id===e}));t>-1&&i.push(r.splice(t,1)[0]);};r.length&&o.length;)u();return [].concat(i,r)}function At(e){var n=e.dispatch;e.setColumnOrder=t.useCallback((function(e){return n({type:l.setColumnOrder,columnOrder:e})}),[n]);}It.pluginName="useColumnOrder",c.canResize=!0,l.columnStartResizing="columnStartResizing",l.columnResizing="columnResizing",l.columnDoneResizing="columnDoneResizing",l.resetResize="resetResize";var kt=function(e){e.getResizerProps=[Ht],e.getHeaderProps.push({style:{position:"relative"}}),e.stateReducers.push(Wt),e.useInstance.push(Tt),e.useInstanceBeforeDimensions.push(zt);},Ht=function(e,t){var n=t.instance,o=t.header,r=n.dispatch,i=function(e,t){var n=!1;if("touchstart"===e.type){if(e.touches&&e.touches.length>1)return;n=!0;}var o,i,u=function(e){var t=[];return function e(n){n.columns&&n.columns.length&&n.columns.map(e);t.push(n);}(e),t}(t).map((function(e){return [e.id,e.totalWidth]})),s=n?Math.round(e.touches[0].clientX):e.clientX,a=function(){window.cancelAnimationFrame(o),o=null,r({type:l.columnDoneResizing});},c=function(){window.cancelAnimationFrame(o),o=null,r({type:l.columnResizing,clientX:i});},d=function(e){i=e,o||(o=window.requestAnimationFrame(c));},f={mouse:{moveEvent:"mousemove",moveHandler:function(e){return d(e.clientX)},upEvent:"mouseup",upHandler:function(e){document.removeEventListener("mousemove",f.mouse.moveHandler),document.removeEventListener("mouseup",f.mouse.upHandler),a();}},touch:{moveEvent:"touchmove",moveHandler:function(e){return e.cancelable&&(e.preventDefault(),e.stopPropagation()),d(e.touches[0].clientX),!1},upEvent:"touchend",upHandler:function(e){document.removeEventListener(f.touch.moveEvent,f.touch.moveHandler),document.removeEventListener(f.touch.upEvent,f.touch.moveHandler),a();}}},p=n?f.touch:f.mouse,g=!!function(){if("boolean"==typeof z)return z;var e=!1;try{var t={get passive(){return e=!0,!1}};window.addEventListener("test",null,t),window.removeEventListener("test",null,t);}catch(t){e=!1;}return z=e}()&&{passive:!1};document.addEventListener(p.moveEvent,p.moveHandler,g),document.addEventListener(p.upEvent,p.upHandler,g),r({type:l.columnStartResizing,columnId:t.id,columnWidth:t.totalWidth,headerIdWidths:u,clientX:s});};return [e,{onMouseDown:function(e){return e.persist()||i(e,o)},onTouchStart:function(e){return e.persist()||i(e,o)},style:{cursor:"col-resize"},draggable:!1,role:"separator"}]};function Wt(e,t){if(t.type===l.init)return r({columnResizing:{columnWidths:{}}},e);if(t.type===l.resetResize)return r({},e,{columnResizing:{columnWidths:{}}});if(t.type===l.columnStartResizing){var n=t.clientX,o=t.columnId,i=t.columnWidth,u=t.headerIdWidths;return r({},e,{columnResizing:r({},e.columnResizing,{startX:n,headerIdWidths:u,columnWidth:i,isResizingColumn:o})})}if(t.type===l.columnResizing){var s=t.clientX,a=e.columnResizing,c=a.startX,d=a.columnWidth,f=a.headerIdWidths,p=(s-c)/d,g={};return (void 0===f?[]:f).forEach((function(e){var t=e[0],n=e[1];g[t]=Math.max(n+n*p,0);})),r({},e,{columnResizing:r({},e.columnResizing,{columnWidths:r({},e.columnResizing.columnWidths,{},g)})})}return t.type===l.columnDoneResizing?r({},e,{columnResizing:r({},e.columnResizing,{startX:null,isResizingColumn:null})}):void 0}kt.pluginName="useResizeColumns";var zt=function(e){var t=e.flatHeaders,n=e.disableResizing,o=e.getHooks,r=e.state.columnResizing,i=h(e);t.forEach((function(e){var t=I(!0!==e.disableResizing&&void 0,!0!==n&&void 0,!0);e.canResize=t,e.width=r.columnWidths[e.id]||e.originalWidth||e.width,e.isResizing=r.isResizingColumn===e.id,t&&(e.getResizerProps=f(o().getResizerProps,{instance:i(),header:e}));}));};function Tt(e){var n=e.plugins,o=e.dispatch,r=e.autoResetResize,i=void 0===r||r,u=e.columns;v(n,["useAbsoluteLayout"],"useResizeColumns");var s=h(i);w((function(){s()&&o({type:l.resetResize});}),[u]);var a=t.useCallback((function(){return o({type:l.resetResize})}),[o]);Object.assign(e,{resetResizing:a});}var Ot={position:"absolute",top:0},Mt=function(e){e.getTableBodyProps.push(jt),e.getRowProps.push(jt),e.getHeaderGroupProps.push(jt),e.getFooterGroupProps.push(jt),e.getHeaderProps.push((function(e,t){var n=t.column;return [e,{style:r({},Ot,{left:n.totalLeft+"px",width:n.totalWidth+"px"})}]})),e.getCellProps.push((function(e,t){var n=t.cell;return [e,{style:r({},Ot,{left:n.column.totalLeft+"px",width:n.column.totalWidth+"px"})}]})),e.getFooterProps.push((function(e,t){var n=t.column;return [e,{style:r({},Ot,{left:n.totalLeft+"px",width:n.totalWidth+"px"})}]}));};Mt.pluginName="useAbsoluteLayout";var jt=function(e,t){return [e,{style:{position:"relative",width:t.instance.totalColumnsWidth+"px"}}]},Lt={display:"inline-block",boxSizing:"border-box"},Nt=function(e,t){return [e,{style:{display:"flex",width:t.instance.totalColumnsWidth+"px"}}]},Dt=function(e){e.getRowProps.push(Nt),e.getHeaderGroupProps.push(Nt),e.getFooterGroupProps.push(Nt),e.getHeaderProps.push((function(e,t){var n=t.column;return [e,{style:r({},Lt,{width:n.totalWidth+"px"})}]})),e.getCellProps.push((function(e,t){var n=t.cell;return [e,{style:r({},Lt,{width:n.column.totalWidth+"px"})}]})),e.getFooterProps.push((function(e,t){var n=t.column;return [e,{style:r({},Lt,{width:n.totalWidth+"px"})}]}));};function Vt(e){e.getTableProps.push(_t),e.getRowProps.push(Xt),e.getHeaderGroupProps.push(Xt),e.getFooterGroupProps.push(Xt),e.getHeaderProps.push(qt),e.getCellProps.push(Kt),e.getFooterProps.push(Ut);}Dt.pluginName="useBlockLayout",Vt.pluginName="useFlexLayout";var _t=function(e,t){return [e,{style:{minWidth:t.instance.totalColumnsMinWidth+"px"}}]},Xt=function(e,t){return [e,{style:{display:"flex",flex:"1 0 auto",minWidth:t.instance.totalColumnsMinWidth+"px"}}]},qt=function(e,t){var n=t.column;return [e,{style:{boxSizing:"border-box",flex:n.totalFlexWidth?n.totalFlexWidth+" 0 auto":void 0,minWidth:n.totalMinWidth+"px",width:n.totalWidth+"px"}}]},Kt=function(e,t){var n=t.cell;return [e,{style:{boxSizing:"border-box",flex:n.column.totalFlexWidth+" 0 auto",minWidth:n.column.totalMinWidth+"px",width:n.column.totalWidth+"px"}}]},Ut=function(e,t){var n=t.column;return [e,{style:{boxSizing:"border-box",flex:n.totalFlexWidth?n.totalFlexWidth+" 0 auto":void 0,minWidth:n.totalMinWidth+"px",width:n.totalWidth+"px"}}]};function $t(e){e.stateReducers.push(Zt),e.getTableProps.push(Jt),e.getHeaderProps.push(Yt),e.getRowProps.push(Qt);}l.columnStartResizing="columnStartResizing",l.columnResizing="columnResizing",l.columnDoneResizing="columnDoneResizing",l.resetResize="resetResize",$t.pluginName="useGridLayout";var Jt=function(e,t){var n=t.instance;return [e,{style:{display:"grid",gridTemplateColumns:n.visibleColumns.map((function(e){var t;return n.state.gridLayout.columnWidths[e.id]?n.state.gridLayout.columnWidths[e.id]+"px":(null==(t=n.state.columnResizing)?void 0:t.isResizingColumn)?n.state.gridLayout.startWidths[e.id]+"px":"number"==typeof e.width?e.width+"px":e.width})).join(" ")}}]},Yt=function(e,t){var n=t.column;return [e,{id:"header-cell-"+n.id,style:{position:"sticky",gridColumn:"span "+n.totalVisibleHeaderCount}}]},Qt=function(e,t){var n=t.row;return n.isExpanded?[e,{style:{gridColumn:"1 / "+(n.cells.length+1)}}]:[e,{}]};function Zt(e,t,n,o){if(t.type===l.init)return r({gridLayout:{columnWidths:{}}},e);if(t.type===l.resetResize)return r({},e,{gridLayout:{columnWidths:{}}});if(t.type===l.columnStartResizing){var i=t.columnId,u=t.headerIdWidths,s=en(i);if(void 0!==s){var a=o.visibleColumns.reduce((function(e,t){var n;return r({},e,((n={})[t.id]=en(t.id),n))}),{}),c=o.visibleColumns.reduce((function(e,t){var n;return r({},e,((n={})[t.id]=t.minWidth,n))}),{}),d=o.visibleColumns.reduce((function(e,t){var n;return r({},e,((n={})[t.id]=t.maxWidth,n))}),{}),f=u.map((function(e){var t=e[0];return [t,en(t)]}));return r({},e,{gridLayout:r({},e.gridLayout,{startWidths:a,minWidths:c,maxWidths:d,headerIdGridWidths:f,columnWidth:s})})}return e}if(t.type===l.columnResizing){var p=t.clientX,g=e.columnResizing.startX,v=e.gridLayout,m=v.columnWidth,h=v.minWidths,y=v.maxWidths,w=v.headerIdGridWidths,R=(p-g)/m,b={};return (void 0===w?[]:w).forEach((function(e){var t=e[0],n=e[1];b[t]=Math.min(Math.max(h[t],n+n*R),y[t]);})),r({},e,{gridLayout:r({},e.gridLayout,{columnWidths:r({},e.gridLayout.columnWidths,{},b)})})}return t.type===l.columnDoneResizing?r({},e,{gridLayout:r({},e.gridLayout,{startWidths:{},minWidths:{},maxWidths:{}})}):void 0}function en(e){var t,n=null==(t=document.getElementById("header-cell-"+e))?void 0:t.offsetWidth;if(void 0!==n)return n}e._UNSTABLE_usePivotColumns=nt,e.actions=l,e.defaultColumn=c,e.defaultGroupByFn=De,e.defaultOrderByFn=Qe,e.defaultRenderer=s,e.emptyRenderer=a,e.ensurePluginOrder=v,e.flexRender=b,e.functionalUpdate=m,e.loopHooks=g,e.makePropGetter=f,e.makeRenderer=R,e.reduceHooks=p,e.safeUseLayoutEffect=y,e.useAbsoluteLayout=Mt,e.useAsyncDebounce=function(e,n){void 0===n&&(n=0);var r=t.useRef({}),i=h(e),u=h(n);return t.useCallback(function(){var e=o(regeneratorRuntime.mark((function e(){var t,n,l,s=arguments;return regeneratorRuntime.wrap((function(e){for(;;)switch(e.prev=e.next){case 0:for(t=s.length,n=new Array(t),l=0;l<t;l++)n[l]=s[l];return r.current.promise||(r.current.promise=new Promise((function(e,t){r.current.resolve=e,r.current.reject=t;}))),r.current.timeout&&clearTimeout(r.current.timeout),r.current.timeout=setTimeout(o(regeneratorRuntime.mark((function e(){return regeneratorRuntime.wrap((function(e){for(;;)switch(e.prev=e.next){case 0:return delete r.current.timeout,e.prev=1,e.t0=r.current,e.next=5,i().apply(void 0,n);case 5:e.t1=e.sent,e.t0.resolve.call(e.t0,e.t1),e.next=12;break;case 9:e.prev=9,e.t2=e.catch(1),r.current.reject(e.t2);case 12:return e.prev=12,delete r.current.promise,e.finish(12);case 15:case"end":return e.stop()}}),e,null,[[1,9,12,15]])}))),u()),e.abrupt("return",r.current.promise);case 5:case"end":return e.stop()}}),e)})));return function(){return e.apply(this,arguments)}}(),[i,u])},e.useBlockLayout=Dt,e.useColumnOrder=It,e.useExpanded=se,e.useFilters=Pe,e.useFlexLayout=Vt,e.useGetLatest=h,e.useGlobalFilter=Ie,e.useGridLayout=$t,e.useGroupBy=ze,e.useMountedLayoutEffect=w,e.usePagination=Ze,e.useResizeColumns=kt,e.useRowSelect=vt,e.useRowState=xt,e.useSortBy=Ue,e.useTable=function(e){for(var n=arguments.length,o=new Array(n>1?n-1:0),i=1;i<n;i++)o[i-1]=arguments[i];e=ie(e),o=[K].concat(o);var u=t.useRef({}),s=h(u.current);Object.assign(s(),r({},e,{plugins:o,hooks:q()})),o.filter(Boolean).forEach((function(e){e(s().hooks);}));var a=h(s().hooks);s().getHooks=a,delete s().hooks,Object.assign(s(),p(a().useOptions,ie(e)));var c=s(),d=c.data,v=c.columns,m=c.initialState,y=c.defaultColumn,w=c.getSubRows,b=c.getRowId,E=c.stateReducer,I=c.useControlledState,F=h(E),G=t.useCallback((function(e,t){if(!t.type)throw console.info({action:t}),new Error("Unknown Action 👆");return [].concat(a().stateReducers,Array.isArray(F())?F():[F()]).reduce((function(n,o){return o(n,t,e,s())||n}),e)}),[a,F,s]),A=t.useReducer(G,void 0,(function(){return G(m,{type:l.init})})),k=A[0],H=A[1],W=p([].concat(a().useControlledState,[I]),k,{instance:s()});Object.assign(s(),{state:W,dispatch:H});var z=t.useMemo((function(){return S(p(a().columns,v,{instance:s()}))}),[a,s,v].concat(p(a().columnsDeps,[],{instance:s()})));s().columns=z;var T=t.useMemo((function(){return p(a().allColumns,C(z),{instance:s()}).map(x)}),[z,a,s].concat(p(a().allColumnsDeps,[],{instance:s()})));s().allColumns=T;var O=t.useMemo((function(){for(var e=[],t=[],n={},o=[].concat(T);o.length;){var r=o.shift();le({data:d,rows:e,flatRows:t,rowsById:n,column:r,getRowId:b,getSubRows:w,accessValueHooks:a().accessValue,getInstance:s});}return [e,t,n]}),[T,d,b,w,a,s]),M=O[0],j=O[1],L=O[2];Object.assign(s(),{rows:M,initialRows:[].concat(M),flatRows:j,rowsById:L}),g(a().useInstanceAfterData,s());var N=t.useMemo((function(){return p(a().visibleColumns,T,{instance:s()}).map((function(e){return P(e,y)}))}),[a,T,s,y].concat(p(a().visibleColumnsDeps,[],{instance:s()})));T=t.useMemo((function(){var e=[].concat(N);return T.forEach((function(t){e.find((function(e){return e.id===t.id}))||e.push(t);})),e}),[T,N]),s().allColumns=T;var D=t.useMemo((function(){return p(a().headerGroups,B(N,y),s())}),[a,N,y,s].concat(p(a().headerGroupsDeps,[],{instance:s()})));s().headerGroups=D;var V=t.useMemo((function(){return D.length?D[0].headers:[]}),[D]);s().headers=V,s().flatHeaders=D.reduce((function(e,t){return [].concat(e,t.headers)}),[]),g(a().useInstanceBeforeDimensions,s());var _=N.filter((function(e){return e.isVisible})).map((function(e){return e.id})).sort().join("_");N=t.useMemo((function(){return N.filter((function(e){return e.isVisible}))}),[N,_]),s().visibleColumns=N;var X=ue(V),U=X[0],$=X[1],J=X[2];return s().totalColumnsMinWidth=U,s().totalColumnsWidth=$,s().totalColumnsMaxWidth=J,g(a().useInstance,s()),[].concat(s().flatHeaders,s().allColumns).forEach((function(e){e.render=R(s(),e),e.getHeaderProps=f(a().getHeaderProps,{instance:s(),column:e}),e.getFooterProps=f(a().getFooterProps,{instance:s(),column:e});})),s().headerGroups=t.useMemo((function(){return D.filter((function(e,t){return e.headers=e.headers.filter((function(e){return e.headers?function e(t){return t.filter((function(t){return t.headers?e(t.headers):t.isVisible})).length}(e.headers):e.isVisible})),!!e.headers.length&&(e.getHeaderGroupProps=f(a().getHeaderGroupProps,{instance:s(),headerGroup:e,index:t}),e.getFooterGroupProps=f(a().getFooterGroupProps,{instance:s(),headerGroup:e,index:t}),!0)}))}),[D,s,a]),s().footerGroups=[].concat(s().headerGroups).reverse(),s().prepareRow=t.useCallback((function(e){e.getRowProps=f(a().getRowProps,{instance:s(),row:e}),e.allCells=T.map((function(t){var n=e.values[t.id],o={column:t,row:e,value:n};return o.getCellProps=f(a().getCellProps,{instance:s(),cell:o}),o.render=R(s(),t,{row:e,cell:o,value:n}),o})),e.cells=N.map((function(t){return e.allCells.find((function(e){return e.column.id===t.id}))})),g(a().prepareRow,e,{instance:s()});}),[a,s,T,N]),s().getTableProps=f(a().getTableProps,{instance:s()}),s().getTableBodyProps=f(a().getTableBodyProps,{instance:s()}),g(a().useFinalInstance,s()),s()},Object.defineProperty(e,"__esModule",{value:!0});}));
		
} (reactTable_production_min, reactTable_production_minExports));
	return reactTable_production_minExports;
}

var reactTable_developmentExports = {};
var reactTable_development = {
  get exports(){ return reactTable_developmentExports; },
  set exports(v){ reactTable_developmentExports = v; },
};

var hasRequiredReactTable_development;

function requireReactTable_development () {
	if (hasRequiredReactTable_development) return reactTable_developmentExports;
	hasRequiredReactTable_development = 1;
	(function (module, exports) {
		(function (global, factory) {
		  factory(exports, React__default) ;
		}(commonjsGlobal, (function (exports, React) {
		  React = React && Object.prototype.hasOwnProperty.call(React, 'default') ? React['default'] : React;

		  function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
		    try {
		      var info = gen[key](arg);
		      var value = info.value;
		    } catch (error) {
		      reject(error);
		      return;
		    }

		    if (info.done) {
		      resolve(value);
		    } else {
		      Promise.resolve(value).then(_next, _throw);
		    }
		  }

		  function _asyncToGenerator(fn) {
		    return function () {
		      var self = this,
		          args = arguments;
		      return new Promise(function (resolve, reject) {
		        var gen = fn.apply(self, args);

		        function _next(value) {
		          asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
		        }

		        function _throw(err) {
		          asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
		        }

		        _next(undefined);
		      });
		    };
		  }

		  function _extends() {
		    _extends = Object.assign || function (target) {
		      for (var i = 1; i < arguments.length; i++) {
		        var source = arguments[i];

		        for (var key in source) {
		          if (Object.prototype.hasOwnProperty.call(source, key)) {
		            target[key] = source[key];
		          }
		        }
		      }

		      return target;
		    };

		    return _extends.apply(this, arguments);
		  }

		  function _objectWithoutPropertiesLoose(source, excluded) {
		    if (source == null) return {};
		    var target = {};
		    var sourceKeys = Object.keys(source);
		    var key, i;

		    for (i = 0; i < sourceKeys.length; i++) {
		      key = sourceKeys[i];
		      if (excluded.indexOf(key) >= 0) continue;
		      target[key] = source[key];
		    }

		    return target;
		  }

		  function _toPrimitive(input, hint) {
		    if (typeof input !== "object" || input === null) return input;
		    var prim = input[Symbol.toPrimitive];

		    if (prim !== undefined) {
		      var res = prim.call(input, hint || "default");
		      if (typeof res !== "object") return res;
		      throw new TypeError("@@toPrimitive must return a primitive value.");
		    }

		    return (hint === "string" ? String : Number)(input);
		  }

		  function _toPropertyKey(arg) {
		    var key = _toPrimitive(arg, "string");

		    return typeof key === "symbol" ? key : String(key);
		  }

		  var renderErr = 'Renderer Error ☝️';
		  var actions = {
		    init: 'init'
		  };
		  var defaultRenderer = function defaultRenderer(_ref) {
		    var _ref$value = _ref.value,
		        value = _ref$value === void 0 ? '' : _ref$value;
		    return value;
		  };
		  var emptyRenderer = function emptyRenderer() {
		    return React.createElement(React.Fragment, null, "\xA0");
		  };
		  var defaultColumn = {
		    Cell: defaultRenderer,
		    width: 150,
		    minWidth: 0,
		    maxWidth: Number.MAX_SAFE_INTEGER
		  };

		  function mergeProps() {
		    for (var _len = arguments.length, propList = new Array(_len), _key = 0; _key < _len; _key++) {
		      propList[_key] = arguments[_key];
		    }

		    return propList.reduce(function (props, next) {
		      var style = next.style,
		          className = next.className,
		          rest = _objectWithoutPropertiesLoose(next, ["style", "className"]);

		      props = _extends({}, props, {}, rest);

		      if (style) {
		        props.style = props.style ? _extends({}, props.style || {}, {}, style || {}) : style;
		      }

		      if (className) {
		        props.className = props.className ? props.className + ' ' + className : className;
		      }

		      if (props.className === '') {
		        delete props.className;
		      }

		      return props;
		    }, {});
		  }

		  function handlePropGetter(prevProps, userProps, meta) {
		    // Handle a lambda, pass it the previous props
		    if (typeof userProps === 'function') {
		      return handlePropGetter({}, userProps(prevProps, meta));
		    } // Handle an array, merge each item as separate props


		    if (Array.isArray(userProps)) {
		      return mergeProps.apply(void 0, [prevProps].concat(userProps));
		    } // Handle an object by default, merge the two objects


		    return mergeProps(prevProps, userProps);
		  }

		  var makePropGetter = function makePropGetter(hooks, meta) {
		    if (meta === void 0) {
		      meta = {};
		    }

		    return function (userProps) {
		      if (userProps === void 0) {
		        userProps = {};
		      }

		      return [].concat(hooks, [userProps]).reduce(function (prev, next) {
		        return handlePropGetter(prev, next, _extends({}, meta, {
		          userProps: userProps
		        }));
		      }, {});
		    };
		  };
		  var reduceHooks = function reduceHooks(hooks, initial, meta, allowUndefined) {
		    if (meta === void 0) {
		      meta = {};
		    }

		    return hooks.reduce(function (prev, next) {
		      var nextValue = next(prev, meta);

		      {
		        if (!allowUndefined && typeof nextValue === 'undefined') {
		          console.info(next);
		          throw new Error('React Table: A reducer hook ☝️ just returned undefined! This is not allowed.');
		        }
		      }

		      return nextValue;
		    }, initial);
		  };
		  var loopHooks = function loopHooks(hooks, context, meta) {
		    if (meta === void 0) {
		      meta = {};
		    }

		    return hooks.forEach(function (hook) {
		      var nextValue = hook(context, meta);

		      {
		        if (typeof nextValue !== 'undefined') {
		          console.info(hook, nextValue);
		          throw new Error('React Table: A loop-type hook ☝️ just returned a value! This is not allowed.');
		        }
		      }
		    });
		  };
		  function ensurePluginOrder(plugins, befores, pluginName, afters) {
		    if ( afters) {
		      throw new Error("Defining plugins in the \"after\" section of ensurePluginOrder is no longer supported (see plugin " + pluginName + ")");
		    }

		    var pluginIndex = plugins.findIndex(function (plugin) {
		      return plugin.pluginName === pluginName;
		    });

		    if (pluginIndex === -1) {
		      {
		        throw new Error("The plugin \"" + pluginName + "\" was not found in the plugin list!\nThis usually means you need to need to name your plugin hook by setting the 'pluginName' property of the hook function, eg:\n\n  " + pluginName + ".pluginName = '" + pluginName + "'\n");
		      }
		    }

		    befores.forEach(function (before) {
		      var beforeIndex = plugins.findIndex(function (plugin) {
		        return plugin.pluginName === before;
		      });

		      if (beforeIndex > -1 && beforeIndex > pluginIndex) {
		        {
		          throw new Error("React Table: The " + pluginName + " plugin hook must be placed after the " + before + " plugin hook!");
		        }
		      }
		    });
		  }
		  function functionalUpdate(updater, old) {
		    return typeof updater === 'function' ? updater(old) : updater;
		  }
		  function useGetLatest(obj) {
		    var ref = React.useRef();
		    ref.current = obj;
		    return React.useCallback(function () {
		      return ref.current;
		    }, []);
		  } // SSR has issues with useLayoutEffect still, so use useEffect during SSR

		  var safeUseLayoutEffect = typeof document !== 'undefined' ? React.useLayoutEffect : React.useEffect;
		  function useMountedLayoutEffect(fn, deps) {
		    var mountedRef = React.useRef(false);
		    safeUseLayoutEffect(function () {
		      if (mountedRef.current) {
		        fn();
		      }

		      mountedRef.current = true; // eslint-disable-next-line
		    }, deps);
		  }
		  function useAsyncDebounce(defaultFn, defaultWait) {
		    if (defaultWait === void 0) {
		      defaultWait = 0;
		    }

		    var debounceRef = React.useRef({});
		    var getDefaultFn = useGetLatest(defaultFn);
		    var getDefaultWait = useGetLatest(defaultWait);
		    return React.useCallback(
		    /*#__PURE__*/
		    function () {
		      var _ref2 = _asyncToGenerator(
		      /*#__PURE__*/
		      regeneratorRuntime.mark(function _callee2() {
		        var _len2,
		            args,
		            _key2,
		            _args2 = arguments;

		        return regeneratorRuntime.wrap(function _callee2$(_context2) {
		          while (1) {
		            switch (_context2.prev = _context2.next) {
		              case 0:
		                for (_len2 = _args2.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
		                  args[_key2] = _args2[_key2];
		                }

		                if (!debounceRef.current.promise) {
		                  debounceRef.current.promise = new Promise(function (resolve, reject) {
		                    debounceRef.current.resolve = resolve;
		                    debounceRef.current.reject = reject;
		                  });
		                }

		                if (debounceRef.current.timeout) {
		                  clearTimeout(debounceRef.current.timeout);
		                }

		                debounceRef.current.timeout = setTimeout(
		                /*#__PURE__*/
		                _asyncToGenerator(
		                /*#__PURE__*/
		                regeneratorRuntime.mark(function _callee() {
		                  return regeneratorRuntime.wrap(function _callee$(_context) {
		                    while (1) {
		                      switch (_context.prev = _context.next) {
		                        case 0:
		                          delete debounceRef.current.timeout;
		                          _context.prev = 1;
		                          _context.t0 = debounceRef.current;
		                          _context.next = 5;
		                          return getDefaultFn().apply(void 0, args);

		                        case 5:
		                          _context.t1 = _context.sent;

		                          _context.t0.resolve.call(_context.t0, _context.t1);

		                          _context.next = 12;
		                          break;

		                        case 9:
		                          _context.prev = 9;
		                          _context.t2 = _context["catch"](1);
		                          debounceRef.current.reject(_context.t2);

		                        case 12:
		                          _context.prev = 12;
		                          delete debounceRef.current.promise;
		                          return _context.finish(12);

		                        case 15:
		                        case "end":
		                          return _context.stop();
		                      }
		                    }
		                  }, _callee, null, [[1, 9, 12, 15]]);
		                })), getDefaultWait());
		                return _context2.abrupt("return", debounceRef.current.promise);

		              case 5:
		              case "end":
		                return _context2.stop();
		            }
		          }
		        }, _callee2);
		      }));

		      return function () {
		        return _ref2.apply(this, arguments);
		      };
		    }(), [getDefaultFn, getDefaultWait]);
		  }
		  function makeRenderer(instance, column, meta) {
		    if (meta === void 0) {
		      meta = {};
		    }

		    return function (type, userProps) {
		      if (userProps === void 0) {
		        userProps = {};
		      }

		      var Comp = typeof type === 'string' ? column[type] : type;

		      if (typeof Comp === 'undefined') {
		        console.info(column);
		        throw new Error(renderErr);
		      }

		      return flexRender(Comp, _extends({}, instance, {
		        column: column
		      }, meta, {}, userProps));
		    };
		  }
		  function flexRender(Comp, props) {
		    return isReactComponent(Comp) ? React.createElement(Comp, props) : Comp;
		  }

		  function isReactComponent(component) {
		    return isClassComponent(component) || typeof component === 'function' || isExoticComponent(component);
		  }

		  function isClassComponent(component) {
		    return typeof component === 'function' && function () {
		      var proto = Object.getPrototypeOf(component);
		      return proto.prototype && proto.prototype.isReactComponent;
		    }();
		  }

		  function isExoticComponent(component) {
		    return typeof component === 'object' && typeof component.$$typeof === 'symbol' && ['react.memo', 'react.forward_ref'].includes(component.$$typeof.description);
		  }

		  function linkColumnStructure(columns, parent, depth) {
		    if (depth === void 0) {
		      depth = 0;
		    }

		    return columns.map(function (column) {
		      column = _extends({}, column, {
		        parent: parent,
		        depth: depth
		      });
		      assignColumnAccessor(column);

		      if (column.columns) {
		        column.columns = linkColumnStructure(column.columns, column, depth + 1);
		      }

		      return column;
		    });
		  }
		  function flattenColumns(columns) {
		    return flattenBy(columns, 'columns');
		  }
		  function assignColumnAccessor(column) {
		    // First check for string accessor
		    var id = column.id,
		        accessor = column.accessor,
		        Header = column.Header;

		    if (typeof accessor === 'string') {
		      id = id || accessor;
		      var accessorPath = accessor.split('.');

		      accessor = function accessor(row) {
		        return getBy(row, accessorPath);
		      };
		    }

		    if (!id && typeof Header === 'string' && Header) {
		      id = Header;
		    }

		    if (!id && column.columns) {
		      console.error(column);
		      throw new Error('A column ID (or unique "Header" value) is required!');
		    }

		    if (!id) {
		      console.error(column);
		      throw new Error('A column ID (or string accessor) is required!');
		    }

		    Object.assign(column, {
		      id: id,
		      accessor: accessor
		    });
		    return column;
		  }
		  function decorateColumn(column, userDefaultColumn) {
		    if (!userDefaultColumn) {
		      throw new Error();
		    }

		    Object.assign(column, _extends({
		      // Make sure there is a fallback header, just in case
		      Header: emptyRenderer,
		      Footer: emptyRenderer
		    }, defaultColumn, {}, userDefaultColumn, {}, column));
		    Object.assign(column, {
		      originalWidth: column.width
		    });
		    return column;
		  } // Build the header groups from the bottom up

		  function makeHeaderGroups(allColumns, defaultColumn, additionalHeaderProperties) {
		    if (additionalHeaderProperties === void 0) {
		      additionalHeaderProperties = function additionalHeaderProperties() {
		        return {};
		      };
		    }

		    var headerGroups = [];
		    var scanColumns = allColumns;
		    var uid = 0;

		    var getUID = function getUID() {
		      return uid++;
		    };

		    var _loop = function _loop() {
		      // The header group we are creating
		      var headerGroup = {
		        headers: []
		      }; // The parent columns we're going to scan next

		      var parentColumns = [];
		      var hasParents = scanColumns.some(function (d) {
		        return d.parent;
		      }); // Scan each column for parents

		      scanColumns.forEach(function (column) {
		        // What is the latest (last) parent column?
		        var latestParentColumn = [].concat(parentColumns).reverse()[0];
		        var newParent;

		        if (hasParents) {
		          // If the column has a parent, add it if necessary
		          if (column.parent) {
		            newParent = _extends({}, column.parent, {
		              originalId: column.parent.id,
		              id: column.parent.id + "_" + getUID(),
		              headers: [column]
		            }, additionalHeaderProperties(column));
		          } else {
		            // If other columns have parents, we'll need to add a place holder if necessary
		            var originalId = column.id + "_placeholder";
		            newParent = decorateColumn(_extends({
		              originalId: originalId,
		              id: column.id + "_placeholder_" + getUID(),
		              placeholderOf: column,
		              headers: [column]
		            }, additionalHeaderProperties(column)), defaultColumn);
		          } // If the resulting parent columns are the same, just add
		          // the column and increment the header span


		          if (latestParentColumn && latestParentColumn.originalId === newParent.originalId) {
		            latestParentColumn.headers.push(column);
		          } else {
		            parentColumns.push(newParent);
		          }
		        }

		        headerGroup.headers.push(column);
		      });
		      headerGroups.push(headerGroup); // Start scanning the parent columns

		      scanColumns = parentColumns;
		    };

		    while (scanColumns.length) {
		      _loop();
		    }

		    return headerGroups.reverse();
		  }
		  var pathObjCache = new Map();
		  function getBy(obj, path, def) {
		    if (!path) {
		      return obj;
		    }

		    var cacheKey = typeof path === 'function' ? path : JSON.stringify(path);

		    var pathObj = pathObjCache.get(cacheKey) || function () {
		      var pathObj = makePathArray(path);
		      pathObjCache.set(cacheKey, pathObj);
		      return pathObj;
		    }();

		    var val;

		    try {
		      val = pathObj.reduce(function (cursor, pathPart) {
		        return cursor[pathPart];
		      }, obj);
		    } catch (e) {// continue regardless of error
		    }

		    return typeof val !== 'undefined' ? val : def;
		  }
		  function getFirstDefined() {
		    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
		      args[_key] = arguments[_key];
		    }

		    for (var i = 0; i < args.length; i += 1) {
		      if (typeof args[i] !== 'undefined') {
		        return args[i];
		      }
		    }
		  }
		  function isFunction(a) {
		    if (typeof a === 'function') {
		      return a;
		    }
		  }
		  function flattenBy(arr, key) {
		    var flat = [];

		    var recurse = function recurse(arr) {
		      arr.forEach(function (d) {
		        if (!d[key]) {
		          flat.push(d);
		        } else {
		          recurse(d[key]);
		        }
		      });
		    };

		    recurse(arr);
		    return flat;
		  }
		  function expandRows(rows, _ref) {
		    var manualExpandedKey = _ref.manualExpandedKey,
		        expanded = _ref.expanded,
		        _ref$expandSubRows = _ref.expandSubRows,
		        expandSubRows = _ref$expandSubRows === void 0 ? true : _ref$expandSubRows;
		    var expandedRows = [];

		    var handleRow = function handleRow(row, addToExpandedRows) {
		      if (addToExpandedRows === void 0) {
		        addToExpandedRows = true;
		      }

		      row.isExpanded = row.original && row.original[manualExpandedKey] || expanded[row.id];
		      row.canExpand = row.subRows && !!row.subRows.length;

		      if (addToExpandedRows) {
		        expandedRows.push(row);
		      }

		      if (row.subRows && row.subRows.length && row.isExpanded) {
		        row.subRows.forEach(function (row) {
		          return handleRow(row, expandSubRows);
		        });
		      }
		    };

		    rows.forEach(function (row) {
		      return handleRow(row);
		    });
		    return expandedRows;
		  }
		  function getFilterMethod(filter, userFilterTypes, filterTypes) {
		    return isFunction(filter) || userFilterTypes[filter] || filterTypes[filter] || filterTypes.text;
		  }
		  function shouldAutoRemoveFilter(autoRemove, value, column) {
		    return autoRemove ? autoRemove(value, column) : typeof value === 'undefined';
		  }
		  function unpreparedAccessWarning() {
		    throw new Error('React-Table: You have not called prepareRow(row) one or more rows you are attempting to render.');
		  }
		  var passiveSupported = null;
		  function passiveEventSupported() {
		    // memoize support to avoid adding multiple test events
		    if (typeof passiveSupported === 'boolean') return passiveSupported;
		    var supported = false;

		    try {
		      var options = {
		        get passive() {
		          supported = true;
		          return false;
		        }

		      };
		      window.addEventListener('test', null, options);
		      window.removeEventListener('test', null, options);
		    } catch (err) {
		      supported = false;
		    }

		    passiveSupported = supported;
		    return passiveSupported;
		  } //

		  var reOpenBracket = /\[/g;
		  var reCloseBracket = /\]/g;

		  function makePathArray(obj) {
		    return flattenDeep(obj) // remove all periods in parts
		    .map(function (d) {
		      return String(d).replace('.', '_');
		    }) // join parts using period
		    .join('.') // replace brackets with periods
		    .replace(reOpenBracket, '.').replace(reCloseBracket, '') // split it back out on periods
		    .split('.');
		  }

		  function flattenDeep(arr, newArr) {
		    if (newArr === void 0) {
		      newArr = [];
		    }

		    if (!Array.isArray(arr)) {
		      newArr.push(arr);
		    } else {
		      for (var i = 0; i < arr.length; i += 1) {
		        flattenDeep(arr[i], newArr);
		      }
		    }

		    return newArr;
		  }

		  var defaultGetTableProps = function defaultGetTableProps(props) {
		    return _extends({
		      role: 'table'
		    }, props);
		  };

		  var defaultGetTableBodyProps = function defaultGetTableBodyProps(props) {
		    return _extends({
		      role: 'rowgroup'
		    }, props);
		  };

		  var defaultGetHeaderProps = function defaultGetHeaderProps(props, _ref) {
		    var column = _ref.column;
		    return _extends({
		      key: "header_" + column.id,
		      colSpan: column.totalVisibleHeaderCount,
		      role: 'columnheader'
		    }, props);
		  };

		  var defaultGetFooterProps = function defaultGetFooterProps(props, _ref2) {
		    var column = _ref2.column;
		    return _extends({
		      key: "footer_" + column.id,
		      colSpan: column.totalVisibleHeaderCount
		    }, props);
		  };

		  var defaultGetHeaderGroupProps = function defaultGetHeaderGroupProps(props, _ref3) {
		    var index = _ref3.index;
		    return _extends({
		      key: "headerGroup_" + index,
		      role: 'row'
		    }, props);
		  };

		  var defaultGetFooterGroupProps = function defaultGetFooterGroupProps(props, _ref4) {
		    var index = _ref4.index;
		    return _extends({
		      key: "footerGroup_" + index
		    }, props);
		  };

		  var defaultGetRowProps = function defaultGetRowProps(props, _ref5) {
		    var row = _ref5.row;
		    return _extends({
		      key: "row_" + row.id,
		      role: 'row'
		    }, props);
		  };

		  var defaultGetCellProps = function defaultGetCellProps(props, _ref6) {
		    var cell = _ref6.cell;
		    return _extends({
		      key: "cell_" + cell.row.id + "_" + cell.column.id,
		      role: 'cell'
		    }, props);
		  };

		  function makeDefaultPluginHooks() {
		    return {
		      useOptions: [],
		      stateReducers: [],
		      useControlledState: [],
		      columns: [],
		      columnsDeps: [],
		      allColumns: [],
		      allColumnsDeps: [],
		      accessValue: [],
		      materializedColumns: [],
		      materializedColumnsDeps: [],
		      useInstanceAfterData: [],
		      visibleColumns: [],
		      visibleColumnsDeps: [],
		      headerGroups: [],
		      headerGroupsDeps: [],
		      useInstanceBeforeDimensions: [],
		      useInstance: [],
		      prepareRow: [],
		      getTableProps: [defaultGetTableProps],
		      getTableBodyProps: [defaultGetTableBodyProps],
		      getHeaderGroupProps: [defaultGetHeaderGroupProps],
		      getFooterGroupProps: [defaultGetFooterGroupProps],
		      getHeaderProps: [defaultGetHeaderProps],
		      getFooterProps: [defaultGetFooterProps],
		      getRowProps: [defaultGetRowProps],
		      getCellProps: [defaultGetCellProps],
		      useFinalInstance: []
		    };
		  }

		  actions.resetHiddenColumns = 'resetHiddenColumns';
		  actions.toggleHideColumn = 'toggleHideColumn';
		  actions.setHiddenColumns = 'setHiddenColumns';
		  actions.toggleHideAllColumns = 'toggleHideAllColumns';
		  var useColumnVisibility = function useColumnVisibility(hooks) {
		    hooks.getToggleHiddenProps = [defaultGetToggleHiddenProps];
		    hooks.getToggleHideAllColumnsProps = [defaultGetToggleHideAllColumnsProps];
		    hooks.stateReducers.push(reducer);
		    hooks.useInstanceBeforeDimensions.push(useInstanceBeforeDimensions);
		    hooks.headerGroupsDeps.push(function (deps, _ref) {
		      var instance = _ref.instance;
		      return [].concat(deps, [instance.state.hiddenColumns]);
		    });
		    hooks.useInstance.push(useInstance);
		  };
		  useColumnVisibility.pluginName = 'useColumnVisibility';

		  var defaultGetToggleHiddenProps = function defaultGetToggleHiddenProps(props, _ref2) {
		    var column = _ref2.column;
		    return [props, {
		      onChange: function onChange(e) {
		        column.toggleHidden(!e.target.checked);
		      },
		      style: {
		        cursor: 'pointer'
		      },
		      checked: column.isVisible,
		      title: 'Toggle Column Visible'
		    }];
		  };

		  var defaultGetToggleHideAllColumnsProps = function defaultGetToggleHideAllColumnsProps(props, _ref3) {
		    var instance = _ref3.instance;
		    return [props, {
		      onChange: function onChange(e) {
		        instance.toggleHideAllColumns(!e.target.checked);
		      },
		      style: {
		        cursor: 'pointer'
		      },
		      checked: !instance.allColumnsHidden && !instance.state.hiddenColumns.length,
		      title: 'Toggle All Columns Hidden',
		      indeterminate: !instance.allColumnsHidden && instance.state.hiddenColumns.length
		    }];
		  };

		  function reducer(state, action, previousState, instance) {
		    if (action.type === actions.init) {
		      return _extends({
		        hiddenColumns: []
		      }, state);
		    }

		    if (action.type === actions.resetHiddenColumns) {
		      return _extends({}, state, {
		        hiddenColumns: instance.initialState.hiddenColumns || []
		      });
		    }

		    if (action.type === actions.toggleHideColumn) {
		      var should = typeof action.value !== 'undefined' ? action.value : !state.hiddenColumns.includes(action.columnId);
		      var hiddenColumns = should ? [].concat(state.hiddenColumns, [action.columnId]) : state.hiddenColumns.filter(function (d) {
		        return d !== action.columnId;
		      });
		      return _extends({}, state, {
		        hiddenColumns: hiddenColumns
		      });
		    }

		    if (action.type === actions.setHiddenColumns) {
		      return _extends({}, state, {
		        hiddenColumns: functionalUpdate(action.value, state.hiddenColumns)
		      });
		    }

		    if (action.type === actions.toggleHideAllColumns) {
		      var shouldAll = typeof action.value !== 'undefined' ? action.value : !state.hiddenColumns.length;
		      return _extends({}, state, {
		        hiddenColumns: shouldAll ? instance.allColumns.map(function (d) {
		          return d.id;
		        }) : []
		      });
		    }
		  }

		  function useInstanceBeforeDimensions(instance) {
		    var headers = instance.headers,
		        hiddenColumns = instance.state.hiddenColumns;
		    var isMountedRef = React.useRef(false);

		    if (!isMountedRef.current) ;

		    var handleColumn = function handleColumn(column, parentVisible) {
		      column.isVisible = parentVisible && !hiddenColumns.includes(column.id);
		      var totalVisibleHeaderCount = 0;

		      if (column.headers && column.headers.length) {
		        column.headers.forEach(function (subColumn) {
		          return totalVisibleHeaderCount += handleColumn(subColumn, column.isVisible);
		        });
		      } else {
		        totalVisibleHeaderCount = column.isVisible ? 1 : 0;
		      }

		      column.totalVisibleHeaderCount = totalVisibleHeaderCount;
		      return totalVisibleHeaderCount;
		    };

		    var totalVisibleHeaderCount = 0;
		    headers.forEach(function (subHeader) {
		      return totalVisibleHeaderCount += handleColumn(subHeader, true);
		    });
		  }

		  function useInstance(instance) {
		    var columns = instance.columns,
		        flatHeaders = instance.flatHeaders,
		        dispatch = instance.dispatch,
		        allColumns = instance.allColumns,
		        getHooks = instance.getHooks,
		        hiddenColumns = instance.state.hiddenColumns,
		        _instance$autoResetHi = instance.autoResetHiddenColumns,
		        autoResetHiddenColumns = _instance$autoResetHi === void 0 ? true : _instance$autoResetHi;
		    var getInstance = useGetLatest(instance);
		    var allColumnsHidden = allColumns.length === hiddenColumns.length;
		    var toggleHideColumn = React.useCallback(function (columnId, value) {
		      return dispatch({
		        type: actions.toggleHideColumn,
		        columnId: columnId,
		        value: value
		      });
		    }, [dispatch]);
		    var setHiddenColumns = React.useCallback(function (value) {
		      return dispatch({
		        type: actions.setHiddenColumns,
		        value: value
		      });
		    }, [dispatch]);
		    var toggleHideAllColumns = React.useCallback(function (value) {
		      return dispatch({
		        type: actions.toggleHideAllColumns,
		        value: value
		      });
		    }, [dispatch]);
		    var getToggleHideAllColumnsProps = makePropGetter(getHooks().getToggleHideAllColumnsProps, {
		      instance: getInstance()
		    });
		    flatHeaders.forEach(function (column) {
		      column.toggleHidden = function (value) {
		        dispatch({
		          type: actions.toggleHideColumn,
		          columnId: column.id,
		          value: value
		        });
		      };

		      column.getToggleHiddenProps = makePropGetter(getHooks().getToggleHiddenProps, {
		        instance: getInstance(),
		        column: column
		      });
		    });
		    var getAutoResetHiddenColumns = useGetLatest(autoResetHiddenColumns);
		    useMountedLayoutEffect(function () {
		      if (getAutoResetHiddenColumns()) {
		        dispatch({
		          type: actions.resetHiddenColumns
		        });
		      }
		    }, [dispatch, columns]);
		    Object.assign(instance, {
		      allColumnsHidden: allColumnsHidden,
		      toggleHideColumn: toggleHideColumn,
		      setHiddenColumns: setHiddenColumns,
		      toggleHideAllColumns: toggleHideAllColumns,
		      getToggleHideAllColumnsProps: getToggleHideAllColumnsProps
		    });
		  }

		  var defaultInitialState = {};
		  var defaultColumnInstance = {};

		  var defaultReducer = function defaultReducer(state, action, prevState) {
		    return state;
		  };

		  var defaultGetSubRows = function defaultGetSubRows(row, index) {
		    return row.subRows || [];
		  };

		  var defaultGetRowId = function defaultGetRowId(row, index, parent) {
		    return "" + (parent ? [parent.id, index].join('.') : index);
		  };

		  var defaultUseControlledState = function defaultUseControlledState(d) {
		    return d;
		  };

		  function applyDefaults(props) {
		    var _props$initialState = props.initialState,
		        initialState = _props$initialState === void 0 ? defaultInitialState : _props$initialState,
		        _props$defaultColumn = props.defaultColumn,
		        defaultColumn = _props$defaultColumn === void 0 ? defaultColumnInstance : _props$defaultColumn,
		        _props$getSubRows = props.getSubRows,
		        getSubRows = _props$getSubRows === void 0 ? defaultGetSubRows : _props$getSubRows,
		        _props$getRowId = props.getRowId,
		        getRowId = _props$getRowId === void 0 ? defaultGetRowId : _props$getRowId,
		        _props$stateReducer = props.stateReducer,
		        stateReducer = _props$stateReducer === void 0 ? defaultReducer : _props$stateReducer,
		        _props$useControlledS = props.useControlledState,
		        useControlledState = _props$useControlledS === void 0 ? defaultUseControlledState : _props$useControlledS,
		        rest = _objectWithoutPropertiesLoose(props, ["initialState", "defaultColumn", "getSubRows", "getRowId", "stateReducer", "useControlledState"]);

		    return _extends({}, rest, {
		      initialState: initialState,
		      defaultColumn: defaultColumn,
		      getSubRows: getSubRows,
		      getRowId: getRowId,
		      stateReducer: stateReducer,
		      useControlledState: useControlledState
		    });
		  }

		  var useTable = function useTable(props) {
		    for (var _len = arguments.length, plugins = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
		      plugins[_key - 1] = arguments[_key];
		    }

		    // Apply default props
		    props = applyDefaults(props); // Add core plugins

		    plugins = [useColumnVisibility].concat(plugins); // Create the table instance

		    var instanceRef = React.useRef({}); // Create a getter for the instance (helps avoid a lot of potential memory leaks)

		    var getInstance = useGetLatest(instanceRef.current); // Assign the props, plugins and hooks to the instance

		    Object.assign(getInstance(), _extends({}, props, {
		      plugins: plugins,
		      hooks: makeDefaultPluginHooks()
		    })); // Allow plugins to register hooks as early as possible

		    plugins.filter(Boolean).forEach(function (plugin) {
		      plugin(getInstance().hooks);
		    }); // Consume all hooks and make a getter for them

		    var getHooks = useGetLatest(getInstance().hooks);
		    getInstance().getHooks = getHooks;
		    delete getInstance().hooks; // Allow useOptions hooks to modify the options coming into the table

		    Object.assign(getInstance(), reduceHooks(getHooks().useOptions, applyDefaults(props)));

		    var _getInstance = getInstance(),
		        data = _getInstance.data,
		        userColumns = _getInstance.columns,
		        initialState = _getInstance.initialState,
		        defaultColumn = _getInstance.defaultColumn,
		        getSubRows = _getInstance.getSubRows,
		        getRowId = _getInstance.getRowId,
		        stateReducer = _getInstance.stateReducer,
		        useControlledState = _getInstance.useControlledState; // Setup user reducer ref


		    var getStateReducer = useGetLatest(stateReducer); // Build the reducer

		    var reducer = React.useCallback(function (state, action) {
		      // Detect invalid actions
		      if (!action.type) {
		        console.info({
		          action: action
		        });
		        throw new Error('Unknown Action 👆');
		      } // Reduce the state from all plugin reducers


		      return [].concat(getHooks().stateReducers, Array.isArray(getStateReducer()) ? getStateReducer() : [getStateReducer()]).reduce(function (s, handler) {
		        return handler(s, action, state, getInstance()) || s;
		      }, state);
		    }, [getHooks, getStateReducer, getInstance]); // Start the reducer

		    var _React$useReducer = React.useReducer(reducer, undefined, function () {
		      return reducer(initialState, {
		        type: actions.init
		      });
		    }),
		        reducerState = _React$useReducer[0],
		        dispatch = _React$useReducer[1]; // Allow the user to control the final state with hooks


		    var state = reduceHooks([].concat(getHooks().useControlledState, [useControlledState]), reducerState, {
		      instance: getInstance()
		    });
		    Object.assign(getInstance(), {
		      state: state,
		      dispatch: dispatch
		    }); // Decorate All the columns

		    var columns = React.useMemo(function () {
		      return linkColumnStructure(reduceHooks(getHooks().columns, userColumns, {
		        instance: getInstance()
		      }));
		    }, [getHooks, getInstance, userColumns].concat(reduceHooks(getHooks().columnsDeps, [], {
		      instance: getInstance()
		    })));
		    getInstance().columns = columns; // Get the flat list of all columns and allow hooks to decorate
		    // those columns (and trigger this memoization via deps)

		    var allColumns = React.useMemo(function () {
		      return reduceHooks(getHooks().allColumns, flattenColumns(columns), {
		        instance: getInstance()
		      }).map(assignColumnAccessor);
		    }, [columns, getHooks, getInstance].concat(reduceHooks(getHooks().allColumnsDeps, [], {
		      instance: getInstance()
		    })));
		    getInstance().allColumns = allColumns; // Access the row model using initial columns

		    var _React$useMemo = React.useMemo(function () {
		      var rows = [];
		      var flatRows = [];
		      var rowsById = {};
		      var allColumnsQueue = [].concat(allColumns);

		      while (allColumnsQueue.length) {
		        var column = allColumnsQueue.shift();
		        accessRowsForColumn({
		          data: data,
		          rows: rows,
		          flatRows: flatRows,
		          rowsById: rowsById,
		          column: column,
		          getRowId: getRowId,
		          getSubRows: getSubRows,
		          accessValueHooks: getHooks().accessValue,
		          getInstance: getInstance
		        });
		      }

		      return [rows, flatRows, rowsById];
		    }, [allColumns, data, getRowId, getSubRows, getHooks, getInstance]),
		        rows = _React$useMemo[0],
		        flatRows = _React$useMemo[1],
		        rowsById = _React$useMemo[2];

		    Object.assign(getInstance(), {
		      rows: rows,
		      initialRows: [].concat(rows),
		      flatRows: flatRows,
		      rowsById: rowsById // materializedColumns,

		    });
		    loopHooks(getHooks().useInstanceAfterData, getInstance()); // Get the flat list of all columns AFTER the rows
		    // have been access, and allow hooks to decorate
		    // those columns (and trigger this memoization via deps)

		    var visibleColumns = React.useMemo(function () {
		      return reduceHooks(getHooks().visibleColumns, allColumns, {
		        instance: getInstance()
		      }).map(function (d) {
		        return decorateColumn(d, defaultColumn);
		      });
		    }, [getHooks, allColumns, getInstance, defaultColumn].concat(reduceHooks(getHooks().visibleColumnsDeps, [], {
		      instance: getInstance()
		    }))); // Combine new visible columns with all columns

		    allColumns = React.useMemo(function () {
		      var columns = [].concat(visibleColumns);
		      allColumns.forEach(function (column) {
		        if (!columns.find(function (d) {
		          return d.id === column.id;
		        })) {
		          columns.push(column);
		        }
		      });
		      return columns;
		    }, [allColumns, visibleColumns]);
		    getInstance().allColumns = allColumns;

		    {
		      var duplicateColumns = allColumns.filter(function (column, i) {
		        return allColumns.findIndex(function (d) {
		          return d.id === column.id;
		        }) !== i;
		      });

		      if (duplicateColumns.length) {
		        console.info(allColumns);
		        throw new Error("Duplicate columns were found with ids: \"" + duplicateColumns.map(function (d) {
		          return d.id;
		        }).join(', ') + "\" in the columns array above");
		      }
		    } // Make the headerGroups


		    var headerGroups = React.useMemo(function () {
		      return reduceHooks(getHooks().headerGroups, makeHeaderGroups(visibleColumns, defaultColumn), getInstance());
		    }, [getHooks, visibleColumns, defaultColumn, getInstance].concat(reduceHooks(getHooks().headerGroupsDeps, [], {
		      instance: getInstance()
		    })));
		    getInstance().headerGroups = headerGroups; // Get the first level of headers

		    var headers = React.useMemo(function () {
		      return headerGroups.length ? headerGroups[0].headers : [];
		    }, [headerGroups]);
		    getInstance().headers = headers; // Provide a flat header list for utilities

		    getInstance().flatHeaders = headerGroups.reduce(function (all, headerGroup) {
		      return [].concat(all, headerGroup.headers);
		    }, []);
		    loopHooks(getHooks().useInstanceBeforeDimensions, getInstance()); // Filter columns down to visible ones

		    var visibleColumnsDep = visibleColumns.filter(function (d) {
		      return d.isVisible;
		    }).map(function (d) {
		      return d.id;
		    }).sort().join('_');
		    visibleColumns = React.useMemo(function () {
		      return visibleColumns.filter(function (d) {
		        return d.isVisible;
		      });
		    }, // eslint-disable-next-line react-hooks/exhaustive-deps
		    [visibleColumns, visibleColumnsDep]);
		    getInstance().visibleColumns = visibleColumns; // Header Visibility is needed by this point

		    var _calculateHeaderWidth = calculateHeaderWidths(headers),
		        totalColumnsMinWidth = _calculateHeaderWidth[0],
		        totalColumnsWidth = _calculateHeaderWidth[1],
		        totalColumnsMaxWidth = _calculateHeaderWidth[2];

		    getInstance().totalColumnsMinWidth = totalColumnsMinWidth;
		    getInstance().totalColumnsWidth = totalColumnsWidth;
		    getInstance().totalColumnsMaxWidth = totalColumnsMaxWidth;
		    loopHooks(getHooks().useInstance, getInstance()) // Each materialized header needs to be assigned a render function and other
		    // prop getter properties here.
		    ;
		    [].concat(getInstance().flatHeaders, getInstance().allColumns).forEach(function (column) {
		      // Give columns/headers rendering power
		      column.render = makeRenderer(getInstance(), column); // Give columns/headers a default getHeaderProps

		      column.getHeaderProps = makePropGetter(getHooks().getHeaderProps, {
		        instance: getInstance(),
		        column: column
		      }); // Give columns/headers a default getFooterProps

		      column.getFooterProps = makePropGetter(getHooks().getFooterProps, {
		        instance: getInstance(),
		        column: column
		      });
		    });
		    getInstance().headerGroups = React.useMemo(function () {
		      return headerGroups.filter(function (headerGroup, i) {
		        // Filter out any headers and headerGroups that don't have visible columns
		        headerGroup.headers = headerGroup.headers.filter(function (column) {
		          var recurse = function recurse(headers) {
		            return headers.filter(function (column) {
		              if (column.headers) {
		                return recurse(column.headers);
		              }

		              return column.isVisible;
		            }).length;
		          };

		          if (column.headers) {
		            return recurse(column.headers);
		          }

		          return column.isVisible;
		        }); // Give headerGroups getRowProps

		        if (headerGroup.headers.length) {
		          headerGroup.getHeaderGroupProps = makePropGetter(getHooks().getHeaderGroupProps, {
		            instance: getInstance(),
		            headerGroup: headerGroup,
		            index: i
		          });
		          headerGroup.getFooterGroupProps = makePropGetter(getHooks().getFooterGroupProps, {
		            instance: getInstance(),
		            headerGroup: headerGroup,
		            index: i
		          });
		          return true;
		        }

		        return false;
		      });
		    }, [headerGroups, getInstance, getHooks]);
		    getInstance().footerGroups = [].concat(getInstance().headerGroups).reverse(); // The prepareRow function is absolutely necessary and MUST be called on
		    // any rows the user wishes to be displayed.

		    getInstance().prepareRow = React.useCallback(function (row) {
		      row.getRowProps = makePropGetter(getHooks().getRowProps, {
		        instance: getInstance(),
		        row: row
		      }); // Build the visible cells for each row

		      row.allCells = allColumns.map(function (column) {
		        var value = row.values[column.id];
		        var cell = {
		          column: column,
		          row: row,
		          value: value
		        }; // Give each cell a getCellProps base

		        cell.getCellProps = makePropGetter(getHooks().getCellProps, {
		          instance: getInstance(),
		          cell: cell
		        }); // Give each cell a renderer function (supports multiple renderers)

		        cell.render = makeRenderer(getInstance(), column, {
		          row: row,
		          cell: cell,
		          value: value
		        });
		        return cell;
		      });
		      row.cells = visibleColumns.map(function (column) {
		        return row.allCells.find(function (cell) {
		          return cell.column.id === column.id;
		        });
		      }); // need to apply any row specific hooks (useExpanded requires this)

		      loopHooks(getHooks().prepareRow, row, {
		        instance: getInstance()
		      });
		    }, [getHooks, getInstance, allColumns, visibleColumns]);
		    getInstance().getTableProps = makePropGetter(getHooks().getTableProps, {
		      instance: getInstance()
		    });
		    getInstance().getTableBodyProps = makePropGetter(getHooks().getTableBodyProps, {
		      instance: getInstance()
		    });
		    loopHooks(getHooks().useFinalInstance, getInstance());
		    return getInstance();
		  };

		  function calculateHeaderWidths(headers, left) {
		    if (left === void 0) {
		      left = 0;
		    }

		    var sumTotalMinWidth = 0;
		    var sumTotalWidth = 0;
		    var sumTotalMaxWidth = 0;
		    var sumTotalFlexWidth = 0;
		    headers.forEach(function (header) {
		      var subHeaders = header.headers;
		      header.totalLeft = left;

		      if (subHeaders && subHeaders.length) {
		        var _calculateHeaderWidth2 = calculateHeaderWidths(subHeaders, left),
		            totalMinWidth = _calculateHeaderWidth2[0],
		            totalWidth = _calculateHeaderWidth2[1],
		            totalMaxWidth = _calculateHeaderWidth2[2],
		            totalFlexWidth = _calculateHeaderWidth2[3];

		        header.totalMinWidth = totalMinWidth;
		        header.totalWidth = totalWidth;
		        header.totalMaxWidth = totalMaxWidth;
		        header.totalFlexWidth = totalFlexWidth;
		      } else {
		        header.totalMinWidth = header.minWidth;
		        header.totalWidth = Math.min(Math.max(header.minWidth, header.width), header.maxWidth);
		        header.totalMaxWidth = header.maxWidth;
		        header.totalFlexWidth = header.canResize ? header.totalWidth : 0;
		      }

		      if (header.isVisible) {
		        left += header.totalWidth;
		        sumTotalMinWidth += header.totalMinWidth;
		        sumTotalWidth += header.totalWidth;
		        sumTotalMaxWidth += header.totalMaxWidth;
		        sumTotalFlexWidth += header.totalFlexWidth;
		      }
		    });
		    return [sumTotalMinWidth, sumTotalWidth, sumTotalMaxWidth, sumTotalFlexWidth];
		  }

		  function accessRowsForColumn(_ref) {
		    var data = _ref.data,
		        rows = _ref.rows,
		        flatRows = _ref.flatRows,
		        rowsById = _ref.rowsById,
		        column = _ref.column,
		        getRowId = _ref.getRowId,
		        getSubRows = _ref.getSubRows,
		        accessValueHooks = _ref.accessValueHooks,
		        getInstance = _ref.getInstance;

		    // Access the row's data column-by-column
		    // We do it this way so we can incrementally add materialized
		    // columns after the first pass and avoid excessive looping
		    var accessRow = function accessRow(originalRow, rowIndex, depth, parent, parentRows) {
		      if (depth === void 0) {
		        depth = 0;
		      }

		      // Keep the original reference around
		      var original = originalRow;
		      var id = getRowId(originalRow, rowIndex, parent);
		      var row = rowsById[id]; // If the row hasn't been created, let's make it

		      if (!row) {
		        row = {
		          id: id,
		          original: original,
		          index: rowIndex,
		          depth: depth,
		          cells: [{}] // This is a dummy cell

		        }; // Override common array functions (and the dummy cell's getCellProps function)
		        // to show an error if it is accessed without calling prepareRow

		        row.cells.map = unpreparedAccessWarning;
		        row.cells.filter = unpreparedAccessWarning;
		        row.cells.forEach = unpreparedAccessWarning;
		        row.cells[0].getCellProps = unpreparedAccessWarning; // Create the cells and values

		        row.values = {}; // Push this row into the parentRows array

		        parentRows.push(row); // Keep track of every row in a flat array

		        flatRows.push(row); // Also keep track of every row by its ID

		        rowsById[id] = row; // Get the original subrows

		        row.originalSubRows = getSubRows(originalRow, rowIndex); // Then recursively access them

		        if (row.originalSubRows) {
		          var subRows = [];
		          row.originalSubRows.forEach(function (d, i) {
		            return accessRow(d, i, depth + 1, row, subRows);
		          }); // Keep the new subRows array on the row

		          row.subRows = subRows;
		        }
		      } else if (row.subRows) {
		        // If the row exists, then it's already been accessed
		        // Keep recursing, but don't worry about passing the
		        // accumlator array (those rows already exist)
		        row.originalSubRows.forEach(function (d, i) {
		          return accessRow(d, i, depth + 1, row);
		        });
		      } // If the column has an accessor, use it to get a value


		      if (column.accessor) {
		        row.values[column.id] = column.accessor(originalRow, rowIndex, row, parentRows, data);
		      } // Allow plugins to manipulate the column value


		      row.values[column.id] = reduceHooks(accessValueHooks, row.values[column.id], {
		        row: row,
		        column: column,
		        instance: getInstance()
		      }, true);
		    };

		    data.forEach(function (originalRow, rowIndex) {
		      return accessRow(originalRow, rowIndex, 0, undefined, rows);
		    });
		  }

		  actions.resetExpanded = 'resetExpanded';
		  actions.toggleRowExpanded = 'toggleRowExpanded';
		  actions.toggleAllRowsExpanded = 'toggleAllRowsExpanded';
		  var useExpanded = function useExpanded(hooks) {
		    hooks.getToggleAllRowsExpandedProps = [defaultGetToggleAllRowsExpandedProps];
		    hooks.getToggleRowExpandedProps = [defaultGetToggleRowExpandedProps];
		    hooks.stateReducers.push(reducer$1);
		    hooks.useInstance.push(useInstance$1);
		    hooks.prepareRow.push(prepareRow);
		  };
		  useExpanded.pluginName = 'useExpanded';

		  var defaultGetToggleAllRowsExpandedProps = function defaultGetToggleAllRowsExpandedProps(props, _ref) {
		    var instance = _ref.instance;
		    return [props, {
		      onClick: function onClick(e) {
		        instance.toggleAllRowsExpanded();
		      },
		      style: {
		        cursor: 'pointer'
		      },
		      title: 'Toggle All Rows Expanded'
		    }];
		  };

		  var defaultGetToggleRowExpandedProps = function defaultGetToggleRowExpandedProps(props, _ref2) {
		    var row = _ref2.row;
		    return [props, {
		      onClick: function onClick() {
		        row.toggleRowExpanded();
		      },
		      style: {
		        cursor: 'pointer'
		      },
		      title: 'Toggle Row Expanded'
		    }];
		  }; // Reducer


		  function reducer$1(state, action, previousState, instance) {
		    if (action.type === actions.init) {
		      return _extends({
		        expanded: {}
		      }, state);
		    }

		    if (action.type === actions.resetExpanded) {
		      return _extends({}, state, {
		        expanded: instance.initialState.expanded || {}
		      });
		    }

		    if (action.type === actions.toggleAllRowsExpanded) {
		      var value = action.value;
		      var rowsById = instance.rowsById;
		      var isAllRowsExpanded = Object.keys(rowsById).length === Object.keys(state.expanded).length;
		      var expandAll = typeof value !== 'undefined' ? value : !isAllRowsExpanded;

		      if (expandAll) {
		        var expanded = {};
		        Object.keys(rowsById).forEach(function (rowId) {
		          expanded[rowId] = true;
		        });
		        return _extends({}, state, {
		          expanded: expanded
		        });
		      }

		      return _extends({}, state, {
		        expanded: {}
		      });
		    }

		    if (action.type === actions.toggleRowExpanded) {
		      var id = action.id,
		          setExpanded = action.value;
		      var exists = state.expanded[id];
		      var shouldExist = typeof setExpanded !== 'undefined' ? setExpanded : !exists;

		      if (!exists && shouldExist) {
		        var _extends2;

		        return _extends({}, state, {
		          expanded: _extends({}, state.expanded, (_extends2 = {}, _extends2[id] = true, _extends2))
		        });
		      } else if (exists && !shouldExist) {
		        var _state$expanded = state.expanded;
		            _state$expanded[id];
		            var rest = _objectWithoutPropertiesLoose(_state$expanded, [id].map(_toPropertyKey));

		        return _extends({}, state, {
		          expanded: rest
		        });
		      } else {
		        return state;
		      }
		    }
		  }

		  function useInstance$1(instance) {
		    var data = instance.data,
		        rows = instance.rows,
		        rowsById = instance.rowsById,
		        _instance$manualExpan = instance.manualExpandedKey,
		        manualExpandedKey = _instance$manualExpan === void 0 ? 'expanded' : _instance$manualExpan,
		        _instance$paginateExp = instance.paginateExpandedRows,
		        paginateExpandedRows = _instance$paginateExp === void 0 ? true : _instance$paginateExp,
		        _instance$expandSubRo = instance.expandSubRows,
		        expandSubRows = _instance$expandSubRo === void 0 ? true : _instance$expandSubRo,
		        _instance$autoResetEx = instance.autoResetExpanded,
		        autoResetExpanded = _instance$autoResetEx === void 0 ? true : _instance$autoResetEx,
		        getHooks = instance.getHooks,
		        plugins = instance.plugins,
		        expanded = instance.state.expanded,
		        dispatch = instance.dispatch;
		    ensurePluginOrder(plugins, ['useSortBy', 'useGroupBy', 'usePivotColumns', 'useGlobalFilter'], 'useExpanded');
		    var getAutoResetExpanded = useGetLatest(autoResetExpanded);
		    var isAllRowsExpanded = Boolean(Object.keys(rowsById).length && Object.keys(expanded).length);

		    if (isAllRowsExpanded) {
		      if (Object.keys(rowsById).some(function (id) {
		        return !expanded[id];
		      })) {
		        isAllRowsExpanded = false;
		      }
		    } // Bypass any effects from firing when this changes


		    useMountedLayoutEffect(function () {
		      if (getAutoResetExpanded()) {
		        dispatch({
		          type: actions.resetExpanded
		        });
		      }
		    }, [dispatch, data]);
		    var toggleRowExpanded = React.useCallback(function (id, value) {
		      dispatch({
		        type: actions.toggleRowExpanded,
		        id: id,
		        value: value
		      });
		    }, [dispatch]);
		    var toggleAllRowsExpanded = React.useCallback(function (value) {
		      return dispatch({
		        type: actions.toggleAllRowsExpanded,
		        value: value
		      });
		    }, [dispatch]);
		    var expandedRows = React.useMemo(function () {
		      if (paginateExpandedRows) {
		        return expandRows(rows, {
		          manualExpandedKey: manualExpandedKey,
		          expanded: expanded,
		          expandSubRows: expandSubRows
		        });
		      }

		      return rows;
		    }, [paginateExpandedRows, rows, manualExpandedKey, expanded, expandSubRows]);
		    var expandedDepth = React.useMemo(function () {
		      return findExpandedDepth(expanded);
		    }, [expanded]);
		    var getInstance = useGetLatest(instance);
		    var getToggleAllRowsExpandedProps = makePropGetter(getHooks().getToggleAllRowsExpandedProps, {
		      instance: getInstance()
		    });
		    Object.assign(instance, {
		      preExpandedRows: rows,
		      expandedRows: expandedRows,
		      rows: expandedRows,
		      expandedDepth: expandedDepth,
		      isAllRowsExpanded: isAllRowsExpanded,
		      toggleRowExpanded: toggleRowExpanded,
		      toggleAllRowsExpanded: toggleAllRowsExpanded,
		      getToggleAllRowsExpandedProps: getToggleAllRowsExpandedProps
		    });
		  }

		  function prepareRow(row, _ref3) {
		    var getHooks = _ref3.instance.getHooks,
		        instance = _ref3.instance;

		    row.toggleRowExpanded = function (set) {
		      return instance.toggleRowExpanded(row.id, set);
		    };

		    row.getToggleRowExpandedProps = makePropGetter(getHooks().getToggleRowExpandedProps, {
		      instance: instance,
		      row: row
		    });
		  }

		  function findExpandedDepth(expanded) {
		    var maxDepth = 0;
		    Object.keys(expanded).forEach(function (id) {
		      var splitId = id.split('.');
		      maxDepth = Math.max(maxDepth, splitId.length);
		    });
		    return maxDepth;
		  }

		  var text = function text(rows, ids, filterValue) {
		    rows = rows.filter(function (row) {
		      return ids.some(function (id) {
		        var rowValue = row.values[id];
		        return String(rowValue).toLowerCase().includes(String(filterValue).toLowerCase());
		      });
		    });
		    return rows;
		  };

		  text.autoRemove = function (val) {
		    return !val;
		  };

		  var exactText = function exactText(rows, ids, filterValue) {
		    return rows.filter(function (row) {
		      return ids.some(function (id) {
		        var rowValue = row.values[id];
		        return rowValue !== undefined ? String(rowValue).toLowerCase() === String(filterValue).toLowerCase() : true;
		      });
		    });
		  };

		  exactText.autoRemove = function (val) {
		    return !val;
		  };

		  var exactTextCase = function exactTextCase(rows, ids, filterValue) {
		    return rows.filter(function (row) {
		      return ids.some(function (id) {
		        var rowValue = row.values[id];
		        return rowValue !== undefined ? String(rowValue) === String(filterValue) : true;
		      });
		    });
		  };

		  exactTextCase.autoRemove = function (val) {
		    return !val;
		  };

		  var includes = function includes(rows, ids, filterValue) {
		    return rows.filter(function (row) {
		      return ids.some(function (id) {
		        var rowValue = row.values[id];
		        return rowValue.includes(filterValue);
		      });
		    });
		  };

		  includes.autoRemove = function (val) {
		    return !val || !val.length;
		  };

		  var includesAll = function includesAll(rows, ids, filterValue) {
		    return rows.filter(function (row) {
		      return ids.some(function (id) {
		        var rowValue = row.values[id];
		        return rowValue && rowValue.length && filterValue.every(function (val) {
		          return rowValue.includes(val);
		        });
		      });
		    });
		  };

		  includesAll.autoRemove = function (val) {
		    return !val || !val.length;
		  };

		  var includesSome = function includesSome(rows, ids, filterValue) {
		    return rows.filter(function (row) {
		      return ids.some(function (id) {
		        var rowValue = row.values[id];
		        return rowValue && rowValue.length && filterValue.some(function (val) {
		          return rowValue.includes(val);
		        });
		      });
		    });
		  };

		  includesSome.autoRemove = function (val) {
		    return !val || !val.length;
		  };

		  var includesValue = function includesValue(rows, ids, filterValue) {
		    return rows.filter(function (row) {
		      return ids.some(function (id) {
		        var rowValue = row.values[id];
		        return filterValue.includes(rowValue);
		      });
		    });
		  };

		  includesValue.autoRemove = function (val) {
		    return !val || !val.length;
		  };

		  var exact = function exact(rows, ids, filterValue) {
		    return rows.filter(function (row) {
		      return ids.some(function (id) {
		        var rowValue = row.values[id];
		        return rowValue === filterValue;
		      });
		    });
		  };

		  exact.autoRemove = function (val) {
		    return typeof val === 'undefined';
		  };

		  var equals = function equals(rows, ids, filterValue) {
		    return rows.filter(function (row) {
		      return ids.some(function (id) {
		        var rowValue = row.values[id]; // eslint-disable-next-line eqeqeq

		        return rowValue == filterValue;
		      });
		    });
		  };

		  equals.autoRemove = function (val) {
		    return val == null;
		  };

		  var between = function between(rows, ids, filterValue) {
		    var _ref = filterValue || [],
		        min = _ref[0],
		        max = _ref[1];

		    min = typeof min === 'number' ? min : -Infinity;
		    max = typeof max === 'number' ? max : Infinity;

		    if (min > max) {
		      var temp = min;
		      min = max;
		      max = temp;
		    }

		    return rows.filter(function (row) {
		      return ids.some(function (id) {
		        var rowValue = row.values[id];
		        return rowValue >= min && rowValue <= max;
		      });
		    });
		  };

		  between.autoRemove = function (val) {
		    return !val || typeof val[0] !== 'number' && typeof val[1] !== 'number';
		  };

		  var filterTypes = /*#__PURE__*/Object.freeze({
		    __proto__: null,
		    text: text,
		    exactText: exactText,
		    exactTextCase: exactTextCase,
		    includes: includes,
		    includesAll: includesAll,
		    includesSome: includesSome,
		    includesValue: includesValue,
		    exact: exact,
		    equals: equals,
		    between: between
		  });

		  actions.resetFilters = 'resetFilters';
		  actions.setFilter = 'setFilter';
		  actions.setAllFilters = 'setAllFilters';
		  var useFilters = function useFilters(hooks) {
		    hooks.stateReducers.push(reducer$2);
		    hooks.useInstance.push(useInstance$2);
		  };
		  useFilters.pluginName = 'useFilters';

		  function reducer$2(state, action, previousState, instance) {
		    if (action.type === actions.init) {
		      return _extends({
		        filters: []
		      }, state);
		    }

		    if (action.type === actions.resetFilters) {
		      return _extends({}, state, {
		        filters: instance.initialState.filters || []
		      });
		    }

		    if (action.type === actions.setFilter) {
		      var columnId = action.columnId,
		          filterValue = action.filterValue;
		      var allColumns = instance.allColumns,
		          userFilterTypes = instance.filterTypes;
		      var column = allColumns.find(function (d) {
		        return d.id === columnId;
		      });

		      if (!column) {
		        throw new Error("React-Table: Could not find a column with id: " + columnId);
		      }

		      var filterMethod = getFilterMethod(column.filter, userFilterTypes || {}, filterTypes);
		      var previousfilter = state.filters.find(function (d) {
		        return d.id === columnId;
		      });
		      var newFilter = functionalUpdate(filterValue, previousfilter && previousfilter.value); //

		      if (shouldAutoRemoveFilter(filterMethod.autoRemove, newFilter, column)) {
		        return _extends({}, state, {
		          filters: state.filters.filter(function (d) {
		            return d.id !== columnId;
		          })
		        });
		      }

		      if (previousfilter) {
		        return _extends({}, state, {
		          filters: state.filters.map(function (d) {
		            if (d.id === columnId) {
		              return {
		                id: columnId,
		                value: newFilter
		              };
		            }

		            return d;
		          })
		        });
		      }

		      return _extends({}, state, {
		        filters: [].concat(state.filters, [{
		          id: columnId,
		          value: newFilter
		        }])
		      });
		    }

		    if (action.type === actions.setAllFilters) {
		      var filters = action.filters;
		      var _allColumns = instance.allColumns,
		          _userFilterTypes = instance.filterTypes;
		      return _extends({}, state, {
		        // Filter out undefined values
		        filters: functionalUpdate(filters, state.filters).filter(function (filter) {
		          var column = _allColumns.find(function (d) {
		            return d.id === filter.id;
		          });

		          var filterMethod = getFilterMethod(column.filter, _userFilterTypes || {}, filterTypes);

		          if (shouldAutoRemoveFilter(filterMethod.autoRemove, filter.value, column)) {
		            return false;
		          }

		          return true;
		        })
		      });
		    }
		  }

		  function useInstance$2(instance) {
		    var data = instance.data,
		        rows = instance.rows,
		        flatRows = instance.flatRows,
		        rowsById = instance.rowsById,
		        allColumns = instance.allColumns,
		        userFilterTypes = instance.filterTypes,
		        manualFilters = instance.manualFilters,
		        _instance$defaultCanF = instance.defaultCanFilter,
		        defaultCanFilter = _instance$defaultCanF === void 0 ? false : _instance$defaultCanF,
		        disableFilters = instance.disableFilters,
		        filters = instance.state.filters,
		        dispatch = instance.dispatch,
		        _instance$autoResetFi = instance.autoResetFilters,
		        autoResetFilters = _instance$autoResetFi === void 0 ? true : _instance$autoResetFi;
		    var setFilter = React.useCallback(function (columnId, filterValue) {
		      dispatch({
		        type: actions.setFilter,
		        columnId: columnId,
		        filterValue: filterValue
		      });
		    }, [dispatch]);
		    var setAllFilters = React.useCallback(function (filters) {
		      dispatch({
		        type: actions.setAllFilters,
		        filters: filters
		      });
		    }, [dispatch]);
		    allColumns.forEach(function (column) {
		      var id = column.id,
		          accessor = column.accessor,
		          columnDefaultCanFilter = column.defaultCanFilter,
		          columnDisableFilters = column.disableFilters; // Determine if a column is filterable

		      column.canFilter = accessor ? getFirstDefined(columnDisableFilters === true ? false : undefined, disableFilters === true ? false : undefined, true) : getFirstDefined(columnDefaultCanFilter, defaultCanFilter, false); // Provide the column a way of updating the filter value

		      column.setFilter = function (val) {
		        return setFilter(column.id, val);
		      }; // Provide the current filter value to the column for
		      // convenience


		      var found = filters.find(function (d) {
		        return d.id === id;
		      });
		      column.filterValue = found && found.value;
		    });

		    var _React$useMemo = React.useMemo(function () {
		      if (manualFilters || !filters.length) {
		        return [rows, flatRows, rowsById];
		      }

		      var filteredFlatRows = [];
		      var filteredRowsById = {}; // Filters top level and nested rows

		      var filterRows = function filterRows(rows, depth) {
		        if (depth === void 0) {
		          depth = 0;
		        }

		        var filteredRows = rows;
		        filteredRows = filters.reduce(function (filteredSoFar, _ref) {
		          var columnId = _ref.id,
		              filterValue = _ref.value;
		          // Find the filters column
		          var column = allColumns.find(function (d) {
		            return d.id === columnId;
		          });

		          if (!column) {
		            return filteredSoFar;
		          }

		          if (depth === 0) {
		            column.preFilteredRows = filteredSoFar;
		          }

		          var filterMethod = getFilterMethod(column.filter, userFilterTypes || {}, filterTypes);

		          if (!filterMethod) {
		            console.warn("Could not find a valid 'column.filter' for column with the ID: " + column.id + ".");
		            return filteredSoFar;
		          } // Pass the rows, id, filterValue and column to the filterMethod
		          // to get the filtered rows back


		          column.filteredRows = filterMethod(filteredSoFar, [columnId], filterValue);
		          return column.filteredRows;
		        }, rows); // Apply the filter to any subRows
		        // We technically could do this recursively in the above loop,
		        // but that would severely hinder the API for the user, since they
		        // would be required to do that recursion in some scenarios

		        filteredRows.forEach(function (row) {
		          filteredFlatRows.push(row);
		          filteredRowsById[row.id] = row;

		          if (!row.subRows) {
		            return;
		          }

		          row.subRows = row.subRows && row.subRows.length > 0 ? filterRows(row.subRows, depth + 1) : row.subRows;
		        });
		        return filteredRows;
		      };

		      return [filterRows(rows), filteredFlatRows, filteredRowsById];
		    }, [manualFilters, filters, rows, flatRows, rowsById, allColumns, userFilterTypes]),
		        filteredRows = _React$useMemo[0],
		        filteredFlatRows = _React$useMemo[1],
		        filteredRowsById = _React$useMemo[2];

		    React.useMemo(function () {
		      // Now that each filtered column has it's partially filtered rows,
		      // lets assign the final filtered rows to all of the other columns
		      var nonFilteredColumns = allColumns.filter(function (column) {
		        return !filters.find(function (d) {
		          return d.id === column.id;
		        });
		      }); // This essentially enables faceted filter options to be built easily
		      // using every column's preFilteredRows value

		      nonFilteredColumns.forEach(function (column) {
		        column.preFilteredRows = filteredRows;
		        column.filteredRows = filteredRows;
		      });
		    }, [filteredRows, filters, allColumns]);
		    var getAutoResetFilters = useGetLatest(autoResetFilters);
		    useMountedLayoutEffect(function () {
		      if (getAutoResetFilters()) {
		        dispatch({
		          type: actions.resetFilters
		        });
		      }
		    }, [dispatch, manualFilters ? null : data]);
		    Object.assign(instance, {
		      preFilteredRows: rows,
		      preFilteredFlatRows: flatRows,
		      preFilteredRowsById: rowsById,
		      filteredRows: filteredRows,
		      filteredFlatRows: filteredFlatRows,
		      filteredRowsById: filteredRowsById,
		      rows: filteredRows,
		      flatRows: filteredFlatRows,
		      rowsById: filteredRowsById,
		      setFilter: setFilter,
		      setAllFilters: setAllFilters
		    });
		  }

		  actions.resetGlobalFilter = 'resetGlobalFilter';
		  actions.setGlobalFilter = 'setGlobalFilter';
		  var useGlobalFilter = function useGlobalFilter(hooks) {
		    hooks.stateReducers.push(reducer$3);
		    hooks.useInstance.push(useInstance$3);
		  };
		  useGlobalFilter.pluginName = 'useGlobalFilter';

		  function reducer$3(state, action, previousState, instance) {
		    if (action.type === actions.resetGlobalFilter) {
		      return _extends({}, state, {
		        globalFilter: instance.initialState.globalFilter || undefined
		      });
		    }

		    if (action.type === actions.setGlobalFilter) {
		      var filterValue = action.filterValue;
		      var userFilterTypes = instance.userFilterTypes;
		      var filterMethod = getFilterMethod(instance.globalFilter, userFilterTypes || {}, filterTypes);
		      var newFilter = functionalUpdate(filterValue, state.globalFilter); //

		      if (shouldAutoRemoveFilter(filterMethod.autoRemove, newFilter)) {
		        state.globalFilter;
		            var stateWithoutGlobalFilter = _objectWithoutPropertiesLoose(state, ["globalFilter"]);

		        return stateWithoutGlobalFilter;
		      }

		      return _extends({}, state, {
		        globalFilter: newFilter
		      });
		    }
		  }

		  function useInstance$3(instance) {
		    var data = instance.data,
		        rows = instance.rows,
		        flatRows = instance.flatRows,
		        rowsById = instance.rowsById,
		        allColumns = instance.allColumns,
		        userFilterTypes = instance.filterTypes,
		        globalFilter = instance.globalFilter,
		        manualGlobalFilter = instance.manualGlobalFilter,
		        globalFilterValue = instance.state.globalFilter,
		        dispatch = instance.dispatch,
		        _instance$autoResetGl = instance.autoResetGlobalFilter,
		        autoResetGlobalFilter = _instance$autoResetGl === void 0 ? true : _instance$autoResetGl,
		        disableGlobalFilter = instance.disableGlobalFilter;
		    var setGlobalFilter = React.useCallback(function (filterValue) {
		      dispatch({
		        type: actions.setGlobalFilter,
		        filterValue: filterValue
		      });
		    }, [dispatch]); // TODO: Create a filter cache for incremental high speed multi-filtering
		    // This gets pretty complicated pretty fast, since you have to maintain a
		    // cache for each row group (top-level rows, and each row's recursive subrows)
		    // This would make multi-filtering a lot faster though. Too far?

		    var _React$useMemo = React.useMemo(function () {
		      if (manualGlobalFilter || typeof globalFilterValue === 'undefined') {
		        return [rows, flatRows, rowsById];
		      }

		      var filteredFlatRows = [];
		      var filteredRowsById = {};
		      var filterMethod = getFilterMethod(globalFilter, userFilterTypes || {}, filterTypes);

		      if (!filterMethod) {
		        console.warn("Could not find a valid 'globalFilter' option.");
		        return rows;
		      }

		      allColumns.forEach(function (column) {
		        var columnDisableGlobalFilter = column.disableGlobalFilter;
		        column.canFilter = getFirstDefined(columnDisableGlobalFilter === true ? false : undefined, disableGlobalFilter === true ? false : undefined, true);
		      });
		      var filterableColumns = allColumns.filter(function (c) {
		        return c.canFilter === true;
		      }); // Filters top level and nested rows

		      var filterRows = function filterRows(filteredRows) {
		        filteredRows = filterMethod(filteredRows, filterableColumns.map(function (d) {
		          return d.id;
		        }), globalFilterValue);
		        filteredRows.forEach(function (row) {
		          filteredFlatRows.push(row);
		          filteredRowsById[row.id] = row;
		          row.subRows = row.subRows && row.subRows.length ? filterRows(row.subRows) : row.subRows;
		        });
		        return filteredRows;
		      };

		      return [filterRows(rows), filteredFlatRows, filteredRowsById];
		    }, [manualGlobalFilter, globalFilterValue, globalFilter, userFilterTypes, allColumns, rows, flatRows, rowsById, disableGlobalFilter]),
		        globalFilteredRows = _React$useMemo[0],
		        globalFilteredFlatRows = _React$useMemo[1],
		        globalFilteredRowsById = _React$useMemo[2];

		    var getAutoResetGlobalFilter = useGetLatest(autoResetGlobalFilter);
		    useMountedLayoutEffect(function () {
		      if (getAutoResetGlobalFilter()) {
		        dispatch({
		          type: actions.resetGlobalFilter
		        });
		      }
		    }, [dispatch, manualGlobalFilter ? null : data]);
		    Object.assign(instance, {
		      preGlobalFilteredRows: rows,
		      preGlobalFilteredFlatRows: flatRows,
		      preGlobalFilteredRowsById: rowsById,
		      globalFilteredRows: globalFilteredRows,
		      globalFilteredFlatRows: globalFilteredFlatRows,
		      globalFilteredRowsById: globalFilteredRowsById,
		      rows: globalFilteredRows,
		      flatRows: globalFilteredFlatRows,
		      rowsById: globalFilteredRowsById,
		      setGlobalFilter: setGlobalFilter,
		      disableGlobalFilter: disableGlobalFilter
		    });
		  }

		  function sum(values, aggregatedValues) {
		    // It's faster to just add the aggregations together instead of
		    // process leaf nodes individually
		    return aggregatedValues.reduce(function (sum, next) {
		      return sum + (typeof next === 'number' ? next : 0);
		    }, 0);
		  }
		  function min(values) {
		    var min = values[0] || 0;
		    values.forEach(function (value) {
		      if (typeof value === 'number') {
		        min = Math.min(min, value);
		      }
		    });
		    return min;
		  }
		  function max(values) {
		    var max = values[0] || 0;
		    values.forEach(function (value) {
		      if (typeof value === 'number') {
		        max = Math.max(max, value);
		      }
		    });
		    return max;
		  }
		  function minMax(values) {
		    var min = values[0] || 0;
		    var max = values[0] || 0;
		    values.forEach(function (value) {
		      if (typeof value === 'number') {
		        min = Math.min(min, value);
		        max = Math.max(max, value);
		      }
		    });
		    return min + ".." + max;
		  }
		  function average(values) {
		    return sum(null, values) / values.length;
		  }
		  function median(values) {
		    if (!values.length) {
		      return null;
		    }

		    var mid = Math.floor(values.length / 2);
		    var nums = [].concat(values).sort(function (a, b) {
		      return a - b;
		    });
		    return values.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
		  }
		  function unique(values) {
		    return Array.from(new Set(values).values());
		  }
		  function uniqueCount(values) {
		    return new Set(values).size;
		  }
		  function count(values) {
		    return values.length;
		  }

		  var aggregations = /*#__PURE__*/Object.freeze({
		    __proto__: null,
		    sum: sum,
		    min: min,
		    max: max,
		    minMax: minMax,
		    average: average,
		    median: median,
		    unique: unique,
		    uniqueCount: uniqueCount,
		    count: count
		  });

		  var emptyArray = [];
		  var emptyObject = {}; // Actions

		  actions.resetGroupBy = 'resetGroupBy';
		  actions.setGroupBy = 'setGroupBy';
		  actions.toggleGroupBy = 'toggleGroupBy';
		  var useGroupBy = function useGroupBy(hooks) {
		    hooks.getGroupByToggleProps = [defaultGetGroupByToggleProps];
		    hooks.stateReducers.push(reducer$4);
		    hooks.visibleColumnsDeps.push(function (deps, _ref) {
		      var instance = _ref.instance;
		      return [].concat(deps, [instance.state.groupBy]);
		    });
		    hooks.visibleColumns.push(visibleColumns);
		    hooks.useInstance.push(useInstance$4);
		    hooks.prepareRow.push(prepareRow$1);
		  };
		  useGroupBy.pluginName = 'useGroupBy';

		  var defaultGetGroupByToggleProps = function defaultGetGroupByToggleProps(props, _ref2) {
		    var header = _ref2.header;
		    return [props, {
		      onClick: header.canGroupBy ? function (e) {
		        e.persist();
		        header.toggleGroupBy();
		      } : undefined,
		      style: {
		        cursor: header.canGroupBy ? 'pointer' : undefined
		      },
		      title: 'Toggle GroupBy'
		    }];
		  }; // Reducer


		  function reducer$4(state, action, previousState, instance) {
		    if (action.type === actions.init) {
		      return _extends({
		        groupBy: []
		      }, state);
		    }

		    if (action.type === actions.resetGroupBy) {
		      return _extends({}, state, {
		        groupBy: instance.initialState.groupBy || []
		      });
		    }

		    if (action.type === actions.setGroupBy) {
		      var value = action.value;
		      return _extends({}, state, {
		        groupBy: value
		      });
		    }

		    if (action.type === actions.toggleGroupBy) {
		      var columnId = action.columnId,
		          setGroupBy = action.value;
		      var resolvedGroupBy = typeof setGroupBy !== 'undefined' ? setGroupBy : !state.groupBy.includes(columnId);

		      if (resolvedGroupBy) {
		        return _extends({}, state, {
		          groupBy: [].concat(state.groupBy, [columnId])
		        });
		      }

		      return _extends({}, state, {
		        groupBy: state.groupBy.filter(function (d) {
		          return d !== columnId;
		        })
		      });
		    }
		  }

		  function visibleColumns(columns, _ref3) {
		    var groupBy = _ref3.instance.state.groupBy;
		    // Sort grouped columns to the start of the column list
		    // before the headers are built
		    var groupByColumns = groupBy.map(function (g) {
		      return columns.find(function (col) {
		        return col.id === g;
		      });
		    }).filter(Boolean);
		    var nonGroupByColumns = columns.filter(function (col) {
		      return !groupBy.includes(col.id);
		    });
		    columns = [].concat(groupByColumns, nonGroupByColumns);
		    columns.forEach(function (column) {
		      column.isGrouped = groupBy.includes(column.id);
		      column.groupedIndex = groupBy.indexOf(column.id);
		    });
		    return columns;
		  }

		  var defaultUserAggregations = {};

		  function useInstance$4(instance) {
		    var data = instance.data,
		        rows = instance.rows,
		        flatRows = instance.flatRows,
		        rowsById = instance.rowsById,
		        allColumns = instance.allColumns,
		        flatHeaders = instance.flatHeaders,
		        _instance$groupByFn = instance.groupByFn,
		        groupByFn = _instance$groupByFn === void 0 ? defaultGroupByFn : _instance$groupByFn,
		        manualGroupBy = instance.manualGroupBy,
		        _instance$aggregation = instance.aggregations,
		        userAggregations = _instance$aggregation === void 0 ? defaultUserAggregations : _instance$aggregation,
		        plugins = instance.plugins,
		        groupBy = instance.state.groupBy,
		        dispatch = instance.dispatch,
		        _instance$autoResetGr = instance.autoResetGroupBy,
		        autoResetGroupBy = _instance$autoResetGr === void 0 ? true : _instance$autoResetGr,
		        disableGroupBy = instance.disableGroupBy,
		        defaultCanGroupBy = instance.defaultCanGroupBy,
		        getHooks = instance.getHooks;
		    ensurePluginOrder(plugins, ['useColumnOrder', 'useFilters'], 'useGroupBy');
		    var getInstance = useGetLatest(instance);
		    allColumns.forEach(function (column) {
		      var accessor = column.accessor,
		          defaultColumnGroupBy = column.defaultGroupBy,
		          columnDisableGroupBy = column.disableGroupBy;
		      column.canGroupBy = accessor ? getFirstDefined(column.canGroupBy, columnDisableGroupBy === true ? false : undefined, disableGroupBy === true ? false : undefined, true) : getFirstDefined(column.canGroupBy, defaultColumnGroupBy, defaultCanGroupBy, false);

		      if (column.canGroupBy) {
		        column.toggleGroupBy = function () {
		          return instance.toggleGroupBy(column.id);
		        };
		      }

		      column.Aggregated = column.Aggregated || column.Cell;
		    });
		    var toggleGroupBy = React.useCallback(function (columnId, value) {
		      dispatch({
		        type: actions.toggleGroupBy,
		        columnId: columnId,
		        value: value
		      });
		    }, [dispatch]);
		    var setGroupBy = React.useCallback(function (value) {
		      dispatch({
		        type: actions.setGroupBy,
		        value: value
		      });
		    }, [dispatch]);
		    flatHeaders.forEach(function (header) {
		      header.getGroupByToggleProps = makePropGetter(getHooks().getGroupByToggleProps, {
		        instance: getInstance(),
		        header: header
		      });
		    });

		    var _React$useMemo = React.useMemo(function () {
		      if (manualGroupBy || !groupBy.length) {
		        return [rows, flatRows, rowsById, emptyArray, emptyObject, flatRows, rowsById];
		      } // Ensure that the list of filtered columns exist


		      var existingGroupBy = groupBy.filter(function (g) {
		        return allColumns.find(function (col) {
		          return col.id === g;
		        });
		      }); // Find the columns that can or are aggregating
		      // Uses each column to aggregate rows into a single value

		      var aggregateRowsToValues = function aggregateRowsToValues(leafRows, groupedRows, depth) {
		        var values = {};
		        allColumns.forEach(function (column) {
		          // Don't aggregate columns that are in the groupBy
		          if (existingGroupBy.includes(column.id)) {
		            values[column.id] = groupedRows[0] ? groupedRows[0].values[column.id] : null;
		            return;
		          } // Aggregate the values


		          var aggregateFn = typeof column.aggregate === 'function' ? column.aggregate : userAggregations[column.aggregate] || aggregations[column.aggregate];

		          if (aggregateFn) {
		            // Get the columnValues to aggregate
		            var groupedValues = groupedRows.map(function (row) {
		              return row.values[column.id];
		            }); // Get the columnValues to aggregate

		            var leafValues = leafRows.map(function (row) {
		              var columnValue = row.values[column.id];

		              if (!depth && column.aggregateValue) {
		                var aggregateValueFn = typeof column.aggregateValue === 'function' ? column.aggregateValue : userAggregations[column.aggregateValue] || aggregations[column.aggregateValue];

		                if (!aggregateValueFn) {
		                  console.info({
		                    column: column
		                  });
		                  throw new Error("React Table: Invalid column.aggregateValue option for column listed above");
		                }

		                columnValue = aggregateValueFn(columnValue, row, column);
		              }

		              return columnValue;
		            });
		            values[column.id] = aggregateFn(leafValues, groupedValues);
		          } else if (column.aggregate) {
		            console.info({
		              column: column
		            });
		            throw new Error("React Table: Invalid column.aggregate option for column listed above");
		          } else {
		            values[column.id] = null;
		          }
		        });
		        return values;
		      };

		      var groupedFlatRows = [];
		      var groupedRowsById = {};
		      var onlyGroupedFlatRows = [];
		      var onlyGroupedRowsById = {};
		      var nonGroupedFlatRows = [];
		      var nonGroupedRowsById = {}; // Recursively group the data

		      var groupUpRecursively = function groupUpRecursively(rows, depth, parentId) {
		        if (depth === void 0) {
		          depth = 0;
		        }

		        // This is the last level, just return the rows
		        if (depth === existingGroupBy.length) {
		          return rows.map(function (row) {
		            return _extends({}, row, {
		              depth: depth
		            });
		          });
		        }

		        var columnId = existingGroupBy[depth]; // Group the rows together for this level

		        var rowGroupsMap = groupByFn(rows, columnId); // Peform aggregations for each group

		        var aggregatedGroupedRows = Object.entries(rowGroupsMap).map(function (_ref4, index) {
		          var groupByVal = _ref4[0],
		              groupedRows = _ref4[1];
		          var id = columnId + ":" + groupByVal;
		          id = parentId ? parentId + ">" + id : id; // First, Recurse to group sub rows before aggregation

		          var subRows = groupUpRecursively(groupedRows, depth + 1, id); // Flatten the leaf rows of the rows in this group

		          var leafRows = depth ? flattenBy(groupedRows, 'leafRows') : groupedRows;
		          var values = aggregateRowsToValues(leafRows, groupedRows, depth);
		          var row = {
		            id: id,
		            isGrouped: true,
		            groupByID: columnId,
		            groupByVal: groupByVal,
		            values: values,
		            subRows: subRows,
		            leafRows: leafRows,
		            depth: depth,
		            index: index
		          };
		          subRows.forEach(function (subRow) {
		            groupedFlatRows.push(subRow);
		            groupedRowsById[subRow.id] = subRow;

		            if (subRow.isGrouped) {
		              onlyGroupedFlatRows.push(subRow);
		              onlyGroupedRowsById[subRow.id] = subRow;
		            } else {
		              nonGroupedFlatRows.push(subRow);
		              nonGroupedRowsById[subRow.id] = subRow;
		            }
		          });
		          return row;
		        });
		        return aggregatedGroupedRows;
		      };

		      var groupedRows = groupUpRecursively(rows);
		      groupedRows.forEach(function (subRow) {
		        groupedFlatRows.push(subRow);
		        groupedRowsById[subRow.id] = subRow;

		        if (subRow.isGrouped) {
		          onlyGroupedFlatRows.push(subRow);
		          onlyGroupedRowsById[subRow.id] = subRow;
		        } else {
		          nonGroupedFlatRows.push(subRow);
		          nonGroupedRowsById[subRow.id] = subRow;
		        }
		      }); // Assign the new data

		      return [groupedRows, groupedFlatRows, groupedRowsById, onlyGroupedFlatRows, onlyGroupedRowsById, nonGroupedFlatRows, nonGroupedRowsById];
		    }, [manualGroupBy, groupBy, rows, flatRows, rowsById, allColumns, userAggregations, groupByFn]),
		        groupedRows = _React$useMemo[0],
		        groupedFlatRows = _React$useMemo[1],
		        groupedRowsById = _React$useMemo[2],
		        onlyGroupedFlatRows = _React$useMemo[3],
		        onlyGroupedRowsById = _React$useMemo[4],
		        nonGroupedFlatRows = _React$useMemo[5],
		        nonGroupedRowsById = _React$useMemo[6];

		    var getAutoResetGroupBy = useGetLatest(autoResetGroupBy);
		    useMountedLayoutEffect(function () {
		      if (getAutoResetGroupBy()) {
		        dispatch({
		          type: actions.resetGroupBy
		        });
		      }
		    }, [dispatch, manualGroupBy ? null : data]);
		    Object.assign(instance, {
		      preGroupedRows: rows,
		      preGroupedFlatRow: flatRows,
		      preGroupedRowsById: rowsById,
		      groupedRows: groupedRows,
		      groupedFlatRows: groupedFlatRows,
		      groupedRowsById: groupedRowsById,
		      onlyGroupedFlatRows: onlyGroupedFlatRows,
		      onlyGroupedRowsById: onlyGroupedRowsById,
		      nonGroupedFlatRows: nonGroupedFlatRows,
		      nonGroupedRowsById: nonGroupedRowsById,
		      rows: groupedRows,
		      flatRows: groupedFlatRows,
		      rowsById: groupedRowsById,
		      toggleGroupBy: toggleGroupBy,
		      setGroupBy: setGroupBy
		    });
		  }

		  function prepareRow$1(row) {
		    row.allCells.forEach(function (cell) {
		      var _row$subRows;

		      // Grouped cells are in the groupBy and the pivot cell for the row
		      cell.isGrouped = cell.column.isGrouped && cell.column.id === row.groupByID; // Placeholder cells are any columns in the groupBy that are not grouped

		      cell.isPlaceholder = !cell.isGrouped && cell.column.isGrouped; // Aggregated cells are not grouped, not repeated, but still have subRows

		      cell.isAggregated = !cell.isGrouped && !cell.isPlaceholder && ((_row$subRows = row.subRows) == null ? void 0 : _row$subRows.length);
		    });
		  }

		  function defaultGroupByFn(rows, columnId) {
		    return rows.reduce(function (prev, row, i) {
		      // TODO: Might want to implement a key serializer here so
		      // irregular column values can still be grouped if needed?
		      var resKey = "" + row.values[columnId];
		      prev[resKey] = Array.isArray(prev[resKey]) ? prev[resKey] : [];
		      prev[resKey].push(row);
		      return prev;
		    }, {});
		  }

		  var reSplitAlphaNumeric = /([0-9]+)/gm; // Mixed sorting is slow, but very inclusive of many edge cases.
		  // It handles numbers, mixed alphanumeric combinations, and even
		  // null, undefined, and Infinity

		  var alphanumeric = function alphanumeric(rowA, rowB, columnId) {
		    var _getRowValuesByColumn = getRowValuesByColumnID(rowA, rowB, columnId),
		        a = _getRowValuesByColumn[0],
		        b = _getRowValuesByColumn[1]; // Force to strings (or "" for unsupported types)


		    a = toString(a);
		    b = toString(b); // Split on number groups, but keep the delimiter
		    // Then remove falsey split values

		    a = a.split(reSplitAlphaNumeric).filter(Boolean);
		    b = b.split(reSplitAlphaNumeric).filter(Boolean); // While

		    while (a.length && b.length) {
		      var aa = a.shift();
		      var bb = b.shift();
		      var an = parseInt(aa, 10);
		      var bn = parseInt(bb, 10);
		      var combo = [an, bn].sort(); // Both are string

		      if (isNaN(combo[0])) {
		        if (aa > bb) {
		          return 1;
		        }

		        if (bb > aa) {
		          return -1;
		        }

		        continue;
		      } // One is a string, one is a number


		      if (isNaN(combo[1])) {
		        return isNaN(an) ? -1 : 1;
		      } // Both are numbers


		      if (an > bn) {
		        return 1;
		      }

		      if (bn > an) {
		        return -1;
		      }
		    }

		    return a.length - b.length;
		  };
		  function datetime(rowA, rowB, columnId) {
		    var _getRowValuesByColumn2 = getRowValuesByColumnID(rowA, rowB, columnId),
		        a = _getRowValuesByColumn2[0],
		        b = _getRowValuesByColumn2[1];

		    a = a.getTime();
		    b = b.getTime();
		    return compareBasic(a, b);
		  }
		  function basic(rowA, rowB, columnId) {
		    var _getRowValuesByColumn3 = getRowValuesByColumnID(rowA, rowB, columnId),
		        a = _getRowValuesByColumn3[0],
		        b = _getRowValuesByColumn3[1];

		    return compareBasic(a, b);
		  }
		  function string(rowA, rowB, columnId) {
		    var _getRowValuesByColumn4 = getRowValuesByColumnID(rowA, rowB, columnId),
		        a = _getRowValuesByColumn4[0],
		        b = _getRowValuesByColumn4[1];

		    a = a.split('').filter(Boolean);
		    b = b.split('').filter(Boolean);

		    while (a.length && b.length) {
		      var aa = a.shift();
		      var bb = b.shift();
		      var alower = aa.toLowerCase();
		      var blower = bb.toLowerCase(); // Case insensitive comparison until characters match

		      if (alower > blower) {
		        return 1;
		      }

		      if (blower > alower) {
		        return -1;
		      } // If lowercase characters are identical


		      if (aa > bb) {
		        return 1;
		      }

		      if (bb > aa) {
		        return -1;
		      }

		      continue;
		    }

		    return a.length - b.length;
		  }
		  function number(rowA, rowB, columnId) {
		    var _getRowValuesByColumn5 = getRowValuesByColumnID(rowA, rowB, columnId),
		        a = _getRowValuesByColumn5[0],
		        b = _getRowValuesByColumn5[1];

		    var replaceNonNumeric = /[^0-9.]/gi;
		    a = Number(String(a).replace(replaceNonNumeric, ''));
		    b = Number(String(b).replace(replaceNonNumeric, ''));
		    return compareBasic(a, b);
		  } // Utils

		  function compareBasic(a, b) {
		    return a === b ? 0 : a > b ? 1 : -1;
		  }

		  function getRowValuesByColumnID(row1, row2, columnId) {
		    return [row1.values[columnId], row2.values[columnId]];
		  }

		  function toString(a) {
		    if (typeof a === 'number') {
		      if (isNaN(a) || a === Infinity || a === -Infinity) {
		        return '';
		      }

		      return String(a);
		    }

		    if (typeof a === 'string') {
		      return a;
		    }

		    return '';
		  }

		  var sortTypes = /*#__PURE__*/Object.freeze({
		    __proto__: null,
		    alphanumeric: alphanumeric,
		    datetime: datetime,
		    basic: basic,
		    string: string,
		    number: number
		  });

		  actions.resetSortBy = 'resetSortBy';
		  actions.setSortBy = 'setSortBy';
		  actions.toggleSortBy = 'toggleSortBy';
		  actions.clearSortBy = 'clearSortBy';
		  defaultColumn.sortType = 'alphanumeric';
		  defaultColumn.sortDescFirst = false;
		  var useSortBy = function useSortBy(hooks) {
		    hooks.getSortByToggleProps = [defaultGetSortByToggleProps];
		    hooks.stateReducers.push(reducer$5);
		    hooks.useInstance.push(useInstance$5);
		  };
		  useSortBy.pluginName = 'useSortBy';

		  var defaultGetSortByToggleProps = function defaultGetSortByToggleProps(props, _ref) {
		    var instance = _ref.instance,
		        column = _ref.column;
		    var _instance$isMultiSort = instance.isMultiSortEvent,
		        isMultiSortEvent = _instance$isMultiSort === void 0 ? function (e) {
		      return e.shiftKey;
		    } : _instance$isMultiSort;
		    return [props, {
		      onClick: column.canSort ? function (e) {
		        e.persist();
		        column.toggleSortBy(undefined, !instance.disableMultiSort && isMultiSortEvent(e));
		      } : undefined,
		      style: {
		        cursor: column.canSort ? 'pointer' : undefined
		      },
		      title: column.canSort ? 'Toggle SortBy' : undefined
		    }];
		  }; // Reducer


		  function reducer$5(state, action, previousState, instance) {
		    if (action.type === actions.init) {
		      return _extends({
		        sortBy: []
		      }, state);
		    }

		    if (action.type === actions.resetSortBy) {
		      return _extends({}, state, {
		        sortBy: instance.initialState.sortBy || []
		      });
		    }

		    if (action.type === actions.clearSortBy) {
		      var sortBy = state.sortBy;
		      var newSortBy = sortBy.filter(function (d) {
		        return d.id !== action.columnId;
		      });
		      return _extends({}, state, {
		        sortBy: newSortBy
		      });
		    }

		    if (action.type === actions.setSortBy) {
		      var _sortBy = action.sortBy;
		      return _extends({}, state, {
		        sortBy: _sortBy
		      });
		    }

		    if (action.type === actions.toggleSortBy) {
		      var columnId = action.columnId,
		          desc = action.desc,
		          multi = action.multi;
		      var allColumns = instance.allColumns,
		          disableMultiSort = instance.disableMultiSort,
		          disableSortRemove = instance.disableSortRemove,
		          disableMultiRemove = instance.disableMultiRemove,
		          _instance$maxMultiSor = instance.maxMultiSortColCount,
		          maxMultiSortColCount = _instance$maxMultiSor === void 0 ? Number.MAX_SAFE_INTEGER : _instance$maxMultiSor;
		      var _sortBy2 = state.sortBy; // Find the column for this columnId

		      var column = allColumns.find(function (d) {
		        return d.id === columnId;
		      });
		      var sortDescFirst = column.sortDescFirst; // Find any existing sortBy for this column

		      var existingSortBy = _sortBy2.find(function (d) {
		        return d.id === columnId;
		      });

		      var existingIndex = _sortBy2.findIndex(function (d) {
		        return d.id === columnId;
		      });

		      var hasDescDefined = typeof desc !== 'undefined' && desc !== null;
		      var _newSortBy = []; // What should we do with this sort action?

		      var sortAction;

		      if (!disableMultiSort && multi) {
		        if (existingSortBy) {
		          sortAction = 'toggle';
		        } else {
		          sortAction = 'add';
		        }
		      } else {
		        // Normal mode
		        if (existingIndex !== _sortBy2.length - 1 || _sortBy2.length !== 1) {
		          sortAction = 'replace';
		        } else if (existingSortBy) {
		          sortAction = 'toggle';
		        } else {
		          sortAction = 'replace';
		        }
		      } // Handle toggle states that will remove the sortBy


		      if (sortAction === 'toggle' && // Must be toggling
		      !disableSortRemove && // If disableSortRemove, disable in general
		      !hasDescDefined && ( // Must not be setting desc
		      multi ? !disableMultiRemove : true) && ( // If multi, don't allow if disableMultiRemove
		      existingSortBy && // Finally, detect if it should indeed be removed
		      existingSortBy.desc && !sortDescFirst || !existingSortBy.desc && sortDescFirst)) {
		        sortAction = 'remove';
		      }

		      if (sortAction === 'replace') {
		        _newSortBy = [{
		          id: columnId,
		          desc: hasDescDefined ? desc : sortDescFirst
		        }];
		      } else if (sortAction === 'add') {
		        _newSortBy = [].concat(_sortBy2, [{
		          id: columnId,
		          desc: hasDescDefined ? desc : sortDescFirst
		        }]); // Take latest n columns

		        _newSortBy.splice(0, _newSortBy.length - maxMultiSortColCount);
		      } else if (sortAction === 'toggle') {
		        // This flips (or sets) the
		        _newSortBy = _sortBy2.map(function (d) {
		          if (d.id === columnId) {
		            return _extends({}, d, {
		              desc: hasDescDefined ? desc : !existingSortBy.desc
		            });
		          }

		          return d;
		        });
		      } else if (sortAction === 'remove') {
		        _newSortBy = _sortBy2.filter(function (d) {
		          return d.id !== columnId;
		        });
		      }

		      return _extends({}, state, {
		        sortBy: _newSortBy
		      });
		    }
		  }

		  function useInstance$5(instance) {
		    var data = instance.data,
		        rows = instance.rows,
		        flatRows = instance.flatRows,
		        allColumns = instance.allColumns,
		        _instance$orderByFn = instance.orderByFn,
		        orderByFn = _instance$orderByFn === void 0 ? defaultOrderByFn : _instance$orderByFn,
		        userSortTypes = instance.sortTypes,
		        manualSortBy = instance.manualSortBy,
		        defaultCanSort = instance.defaultCanSort,
		        disableSortBy = instance.disableSortBy,
		        flatHeaders = instance.flatHeaders,
		        sortBy = instance.state.sortBy,
		        dispatch = instance.dispatch,
		        plugins = instance.plugins,
		        getHooks = instance.getHooks,
		        _instance$autoResetSo = instance.autoResetSortBy,
		        autoResetSortBy = _instance$autoResetSo === void 0 ? true : _instance$autoResetSo;
		    ensurePluginOrder(plugins, ['useFilters', 'useGlobalFilter', 'useGroupBy', 'usePivotColumns'], 'useSortBy');
		    var setSortBy = React.useCallback(function (sortBy) {
		      dispatch({
		        type: actions.setSortBy,
		        sortBy: sortBy
		      });
		    }, [dispatch]); // Updates sorting based on a columnId, desc flag and multi flag

		    var toggleSortBy = React.useCallback(function (columnId, desc, multi) {
		      dispatch({
		        type: actions.toggleSortBy,
		        columnId: columnId,
		        desc: desc,
		        multi: multi
		      });
		    }, [dispatch]); // use reference to avoid memory leak in #1608

		    var getInstance = useGetLatest(instance); // Add the getSortByToggleProps method to columns and headers

		    flatHeaders.forEach(function (column) {
		      var accessor = column.accessor,
		          defaultColumnCanSort = column.canSort,
		          columnDisableSortBy = column.disableSortBy,
		          id = column.id;
		      var canSort = accessor ? getFirstDefined(columnDisableSortBy === true ? false : undefined, disableSortBy === true ? false : undefined, true) : getFirstDefined(defaultCanSort, defaultColumnCanSort, false);
		      column.canSort = canSort;

		      if (column.canSort) {
		        column.toggleSortBy = function (desc, multi) {
		          return toggleSortBy(column.id, desc, multi);
		        };

		        column.clearSortBy = function () {
		          dispatch({
		            type: actions.clearSortBy,
		            columnId: column.id
		          });
		        };
		      }

		      column.getSortByToggleProps = makePropGetter(getHooks().getSortByToggleProps, {
		        instance: getInstance(),
		        column: column
		      });
		      var columnSort = sortBy.find(function (d) {
		        return d.id === id;
		      });
		      column.isSorted = !!columnSort;
		      column.sortedIndex = sortBy.findIndex(function (d) {
		        return d.id === id;
		      });
		      column.isSortedDesc = column.isSorted ? columnSort.desc : undefined;
		    });

		    var _React$useMemo = React.useMemo(function () {
		      if (manualSortBy || !sortBy.length) {
		        return [rows, flatRows];
		      }

		      var sortedFlatRows = []; // Filter out sortBys that correspond to non existing columns

		      var availableSortBy = sortBy.filter(function (sort) {
		        return allColumns.find(function (col) {
		          return col.id === sort.id;
		        });
		      });

		      var sortData = function sortData(rows) {
		        // Use the orderByFn to compose multiple sortBy's together.
		        // This will also perform a stable sorting using the row index
		        // if needed.
		        var sortedData = orderByFn(rows, availableSortBy.map(function (sort) {
		          // Support custom sorting methods for each column
		          var column = allColumns.find(function (d) {
		            return d.id === sort.id;
		          });

		          if (!column) {
		            throw new Error("React-Table: Could not find a column with id: " + sort.id + " while sorting");
		          }

		          var sortType = column.sortType; // Look up sortBy functions in this order:
		          // column function
		          // column string lookup on user sortType
		          // column string lookup on built-in sortType
		          // default function
		          // default string lookup on user sortType
		          // default string lookup on built-in sortType

		          var sortMethod = isFunction(sortType) || (userSortTypes || {})[sortType] || sortTypes[sortType];

		          if (!sortMethod) {
		            throw new Error("React-Table: Could not find a valid sortType of '" + sortType + "' for column '" + sort.id + "'.");
		          } // Return the correct sortFn.
		          // This function should always return in ascending order


		          return function (a, b) {
		            return sortMethod(a, b, sort.id, sort.desc);
		          };
		        }), // Map the directions
		        availableSortBy.map(function (sort) {
		          // Detect and use the sortInverted option
		          var column = allColumns.find(function (d) {
		            return d.id === sort.id;
		          });

		          if (column && column.sortInverted) {
		            return sort.desc;
		          }

		          return !sort.desc;
		        })); // If there are sub-rows, sort them

		        sortedData.forEach(function (row) {
		          sortedFlatRows.push(row);

		          if (!row.subRows || row.subRows.length === 0) {
		            return;
		          }

		          row.subRows = sortData(row.subRows);
		        });
		        return sortedData;
		      };

		      return [sortData(rows), sortedFlatRows];
		    }, [manualSortBy, sortBy, rows, flatRows, allColumns, orderByFn, userSortTypes]),
		        sortedRows = _React$useMemo[0],
		        sortedFlatRows = _React$useMemo[1];

		    var getAutoResetSortBy = useGetLatest(autoResetSortBy);
		    useMountedLayoutEffect(function () {
		      if (getAutoResetSortBy()) {
		        dispatch({
		          type: actions.resetSortBy
		        });
		      }
		    }, [manualSortBy ? null : data]);
		    Object.assign(instance, {
		      preSortedRows: rows,
		      preSortedFlatRows: flatRows,
		      sortedRows: sortedRows,
		      sortedFlatRows: sortedFlatRows,
		      rows: sortedRows,
		      flatRows: sortedFlatRows,
		      setSortBy: setSortBy,
		      toggleSortBy: toggleSortBy
		    });
		  }

		  function defaultOrderByFn(arr, funcs, dirs) {
		    return [].concat(arr).sort(function (rowA, rowB) {
		      for (var i = 0; i < funcs.length; i += 1) {
		        var sortFn = funcs[i];
		        var desc = dirs[i] === false || dirs[i] === 'desc';
		        var sortInt = sortFn(rowA, rowB);

		        if (sortInt !== 0) {
		          return desc ? -sortInt : sortInt;
		        }
		      }

		      return dirs[0] ? rowA.index - rowB.index : rowB.index - rowA.index;
		    });
		  }

		  var pluginName = 'usePagination'; // Actions

		  actions.resetPage = 'resetPage';
		  actions.gotoPage = 'gotoPage';
		  actions.setPageSize = 'setPageSize';
		  var usePagination = function usePagination(hooks) {
		    hooks.stateReducers.push(reducer$6);
		    hooks.useInstance.push(useInstance$6);
		  };
		  usePagination.pluginName = pluginName;

		  function reducer$6(state, action, previousState, instance) {
		    if (action.type === actions.init) {
		      return _extends({
		        pageSize: 10,
		        pageIndex: 0
		      }, state);
		    }

		    if (action.type === actions.resetPage) {
		      return _extends({}, state, {
		        pageIndex: instance.initialState.pageIndex || 0
		      });
		    }

		    if (action.type === actions.gotoPage) {
		      var pageCount = instance.pageCount,
		          page = instance.page;
		      var newPageIndex = functionalUpdate(action.pageIndex, state.pageIndex);
		      var canNavigate = false;

		      if (newPageIndex > state.pageIndex) {
		        // next page
		        canNavigate = pageCount === -1 ? page.length >= state.pageSize : newPageIndex < pageCount;
		      } else if (newPageIndex < state.pageIndex) {
		        // prev page
		        canNavigate = newPageIndex > -1;
		      }

		      if (!canNavigate) {
		        return state;
		      }

		      return _extends({}, state, {
		        pageIndex: newPageIndex
		      });
		    }

		    if (action.type === actions.setPageSize) {
		      var pageSize = action.pageSize;
		      var topRowIndex = state.pageSize * state.pageIndex;
		      var pageIndex = Math.floor(topRowIndex / pageSize);
		      return _extends({}, state, {
		        pageIndex: pageIndex,
		        pageSize: pageSize
		      });
		    }
		  }

		  function useInstance$6(instance) {
		    var rows = instance.rows,
		        _instance$autoResetPa = instance.autoResetPage,
		        autoResetPage = _instance$autoResetPa === void 0 ? true : _instance$autoResetPa,
		        _instance$manualExpan = instance.manualExpandedKey,
		        manualExpandedKey = _instance$manualExpan === void 0 ? 'expanded' : _instance$manualExpan,
		        plugins = instance.plugins,
		        userPageCount = instance.pageCount,
		        _instance$paginateExp = instance.paginateExpandedRows,
		        paginateExpandedRows = _instance$paginateExp === void 0 ? true : _instance$paginateExp,
		        _instance$expandSubRo = instance.expandSubRows,
		        expandSubRows = _instance$expandSubRo === void 0 ? true : _instance$expandSubRo,
		        _instance$state = instance.state,
		        pageSize = _instance$state.pageSize,
		        pageIndex = _instance$state.pageIndex,
		        expanded = _instance$state.expanded,
		        globalFilter = _instance$state.globalFilter,
		        filters = _instance$state.filters,
		        groupBy = _instance$state.groupBy,
		        sortBy = _instance$state.sortBy,
		        dispatch = instance.dispatch,
		        data = instance.data,
		        manualPagination = instance.manualPagination;
		    ensurePluginOrder(plugins, ['useGlobalFilter', 'useFilters', 'useGroupBy', 'useSortBy', 'useExpanded'], 'usePagination');
		    var getAutoResetPage = useGetLatest(autoResetPage);
		    useMountedLayoutEffect(function () {
		      if (getAutoResetPage()) {
		        dispatch({
		          type: actions.resetPage
		        });
		      }
		    }, [dispatch, manualPagination ? null : data, globalFilter, filters, groupBy, sortBy]);
		    var pageCount = manualPagination ? userPageCount : Math.ceil(rows.length / pageSize);
		    var pageOptions = React.useMemo(function () {
		      return pageCount > 0 ? [].concat(new Array(pageCount)).fill(null).map(function (d, i) {
		        return i;
		      }) : [];
		    }, [pageCount]);
		    var page = React.useMemo(function () {
		      var page;

		      if (manualPagination) {
		        page = rows;
		      } else {
		        var pageStart = pageSize * pageIndex;
		        var pageEnd = pageStart + pageSize;
		        page = rows.slice(pageStart, pageEnd);
		      }

		      if (paginateExpandedRows) {
		        return page;
		      }

		      return expandRows(page, {
		        manualExpandedKey: manualExpandedKey,
		        expanded: expanded,
		        expandSubRows: expandSubRows
		      });
		    }, [expandSubRows, expanded, manualExpandedKey, manualPagination, pageIndex, pageSize, paginateExpandedRows, rows]);
		    var canPreviousPage = pageIndex > 0;
		    var canNextPage = pageCount === -1 ? page.length >= pageSize : pageIndex < pageCount - 1;
		    var gotoPage = React.useCallback(function (pageIndex) {
		      dispatch({
		        type: actions.gotoPage,
		        pageIndex: pageIndex
		      });
		    }, [dispatch]);
		    var previousPage = React.useCallback(function () {
		      return gotoPage(function (old) {
		        return old - 1;
		      });
		    }, [gotoPage]);
		    var nextPage = React.useCallback(function () {
		      return gotoPage(function (old) {
		        return old + 1;
		      });
		    }, [gotoPage]);
		    var setPageSize = React.useCallback(function (pageSize) {
		      dispatch({
		        type: actions.setPageSize,
		        pageSize: pageSize
		      });
		    }, [dispatch]);
		    Object.assign(instance, {
		      pageOptions: pageOptions,
		      pageCount: pageCount,
		      page: page,
		      canPreviousPage: canPreviousPage,
		      canNextPage: canNextPage,
		      gotoPage: gotoPage,
		      previousPage: previousPage,
		      nextPage: nextPage,
		      setPageSize: setPageSize
		    });
		  }

		  actions.resetPivot = 'resetPivot';
		  actions.togglePivot = 'togglePivot';
		  var _UNSTABLE_usePivotColumns = function _UNSTABLE_usePivotColumns(hooks) {
		    hooks.getPivotToggleProps = [defaultGetPivotToggleProps];
		    hooks.stateReducers.push(reducer$7);
		    hooks.useInstanceAfterData.push(useInstanceAfterData);
		    hooks.allColumns.push(allColumns);
		    hooks.accessValue.push(accessValue);
		    hooks.materializedColumns.push(materializedColumns);
		    hooks.materializedColumnsDeps.push(materializedColumnsDeps);
		    hooks.visibleColumns.push(visibleColumns$1);
		    hooks.visibleColumnsDeps.push(visibleColumnsDeps);
		    hooks.useInstance.push(useInstance$7);
		    hooks.prepareRow.push(prepareRow$2);
		  };
		  _UNSTABLE_usePivotColumns.pluginName = 'usePivotColumns';
		  var defaultPivotColumns = [];

		  var defaultGetPivotToggleProps = function defaultGetPivotToggleProps(props, _ref) {
		    var header = _ref.header;
		    return [props, {
		      onClick: header.canPivot ? function (e) {
		        e.persist();
		        header.togglePivot();
		      } : undefined,
		      style: {
		        cursor: header.canPivot ? 'pointer' : undefined
		      },
		      title: 'Toggle Pivot'
		    }];
		  }; // Reducer


		  function reducer$7(state, action, previousState, instance) {
		    if (action.type === actions.init) {
		      return _extends({
		        pivotColumns: defaultPivotColumns
		      }, state);
		    }

		    if (action.type === actions.resetPivot) {
		      return _extends({}, state, {
		        pivotColumns: instance.initialState.pivotColumns || defaultPivotColumns
		      });
		    }

		    if (action.type === actions.togglePivot) {
		      var columnId = action.columnId,
		          setPivot = action.value;
		      var resolvedPivot = typeof setPivot !== 'undefined' ? setPivot : !state.pivotColumns.includes(columnId);

		      if (resolvedPivot) {
		        return _extends({}, state, {
		          pivotColumns: [].concat(state.pivotColumns, [columnId])
		        });
		      }

		      return _extends({}, state, {
		        pivotColumns: state.pivotColumns.filter(function (d) {
		          return d !== columnId;
		        })
		      });
		    }
		  }

		  function useInstanceAfterData(instance) {
		    instance.allColumns.forEach(function (column) {
		      column.isPivotSource = instance.state.pivotColumns.includes(column.id);
		    });
		  }

		  function allColumns(columns, _ref2) {
		    var instance = _ref2.instance;
		    columns.forEach(function (column) {
		      column.isPivotSource = instance.state.pivotColumns.includes(column.id);
		      column.uniqueValues = new Set();
		    });
		    return columns;
		  }

		  function accessValue(value, _ref3) {
		    var column = _ref3.column;

		    if (column.uniqueValues && typeof value !== 'undefined') {
		      column.uniqueValues.add(value);
		    }

		    return value;
		  }

		  function materializedColumns(materialized, _ref4) {
		    var instance = _ref4.instance;
		    var allColumns = instance.allColumns,
		        state = instance.state;

		    if (!state.pivotColumns.length || !state.groupBy || !state.groupBy.length) {
		      return materialized;
		    }

		    var pivotColumns = state.pivotColumns.map(function (id) {
		      return allColumns.find(function (d) {
		        return d.id === id;
		      });
		    }).filter(Boolean);
		    var sourceColumns = allColumns.filter(function (d) {
		      return !d.isPivotSource && !state.groupBy.includes(d.id) && !state.pivotColumns.includes(d.id);
		    });

		    var buildPivotColumns = function buildPivotColumns(depth, parent, pivotFilters) {
		      if (depth === void 0) {
		        depth = 0;
		      }

		      if (pivotFilters === void 0) {
		        pivotFilters = [];
		      }

		      var pivotColumn = pivotColumns[depth];

		      if (!pivotColumn) {
		        return sourceColumns.map(function (sourceColumn) {
		          // TODO: We could offer support here for renesting pivoted
		          // columns inside copies of their header groups. For now,
		          // that seems like it would be (1) overkill on nesting, considering
		          // you already get nesting for every pivot level and (2)
		          // really hard. :)
		          return _extends({}, sourceColumn, {
		            canPivot: false,
		            isPivoted: true,
		            parent: parent,
		            depth: depth,
		            id: "" + (parent ? parent.id + "." + sourceColumn.id : sourceColumn.id),
		            accessor: function accessor(originalRow, i, row) {
		              if (pivotFilters.every(function (filter) {
		                return filter(row);
		              })) {
		                return row.values[sourceColumn.id];
		              }
		            }
		          });
		        });
		      }

		      var uniqueValues = Array.from(pivotColumn.uniqueValues).sort();
		      return uniqueValues.map(function (uniqueValue) {
		        var columnGroup = _extends({}, pivotColumn, {
		          Header: pivotColumn.PivotHeader || typeof pivotColumn.header === 'string' ? pivotColumn.Header + ": " + uniqueValue : uniqueValue,
		          isPivotGroup: true,
		          parent: parent,
		          depth: depth,
		          id: parent ? parent.id + "." + pivotColumn.id + "." + uniqueValue : pivotColumn.id + "." + uniqueValue,
		          pivotValue: uniqueValue
		        });

		        columnGroup.columns = buildPivotColumns(depth + 1, columnGroup, [].concat(pivotFilters, [function (row) {
		          return row.values[pivotColumn.id] === uniqueValue;
		        }]));
		        return columnGroup;
		      });
		    };

		    var newMaterialized = flattenColumns(buildPivotColumns());
		    return [].concat(materialized, newMaterialized);
		  }

		  function materializedColumnsDeps(deps, _ref5) {
		    var _ref5$instance$state = _ref5.instance.state,
		        pivotColumns = _ref5$instance$state.pivotColumns,
		        groupBy = _ref5$instance$state.groupBy;
		    return [].concat(deps, [pivotColumns, groupBy]);
		  }

		  function visibleColumns$1(visibleColumns, _ref6) {
		    var state = _ref6.instance.state;
		    visibleColumns = visibleColumns.filter(function (d) {
		      return !d.isPivotSource;
		    });

		    if (state.pivotColumns.length && state.groupBy && state.groupBy.length) {
		      visibleColumns = visibleColumns.filter(function (column) {
		        return column.isGrouped || column.isPivoted;
		      });
		    }

		    return visibleColumns;
		  }

		  function visibleColumnsDeps(deps, _ref7) {
		    var instance = _ref7.instance;
		    return [].concat(deps, [instance.state.pivotColumns, instance.state.groupBy]);
		  }

		  function useInstance$7(instance) {
		    var columns = instance.columns,
		        allColumns = instance.allColumns,
		        flatHeaders = instance.flatHeaders,
		        getHooks = instance.getHooks,
		        plugins = instance.plugins,
		        dispatch = instance.dispatch,
		        _instance$autoResetPi = instance.autoResetPivot,
		        autoResetPivot = _instance$autoResetPi === void 0 ? true : _instance$autoResetPi,
		        manaulPivot = instance.manaulPivot,
		        disablePivot = instance.disablePivot,
		        defaultCanPivot = instance.defaultCanPivot;
		    ensurePluginOrder(plugins, ['useGroupBy'], 'usePivotColumns');
		    var getInstance = useGetLatest(instance);
		    allColumns.forEach(function (column) {
		      var accessor = column.accessor,
		          defaultColumnPivot = column.defaultPivot,
		          columnDisablePivot = column.disablePivot;
		      column.canPivot = accessor ? getFirstDefined(column.canPivot, columnDisablePivot === true ? false : undefined, disablePivot === true ? false : undefined, true) : getFirstDefined(column.canPivot, defaultColumnPivot, defaultCanPivot, false);

		      if (column.canPivot) {
		        column.togglePivot = function () {
		          return instance.togglePivot(column.id);
		        };
		      }

		      column.Aggregated = column.Aggregated || column.Cell;
		    });

		    var togglePivot = function togglePivot(columnId, value) {
		      dispatch({
		        type: actions.togglePivot,
		        columnId: columnId,
		        value: value
		      });
		    };

		    flatHeaders.forEach(function (header) {
		      header.getPivotToggleProps = makePropGetter(getHooks().getPivotToggleProps, {
		        instance: getInstance(),
		        header: header
		      });
		    });
		    var getAutoResetPivot = useGetLatest(autoResetPivot);
		    useMountedLayoutEffect(function () {
		      if (getAutoResetPivot()) {
		        dispatch({
		          type: actions.resetPivot
		        });
		      }
		    }, [dispatch, manaulPivot ? null : columns]);
		    Object.assign(instance, {
		      togglePivot: togglePivot
		    });
		  }

		  function prepareRow$2(row) {
		    row.allCells.forEach(function (cell) {
		      // Grouped cells are in the pivotColumns and the pivot cell for the row
		      cell.isPivoted = cell.column.isPivoted;
		    });
		  }

		  var pluginName$1 = 'useRowSelect'; // Actions

		  actions.resetSelectedRows = 'resetSelectedRows';
		  actions.toggleAllRowsSelected = 'toggleAllRowsSelected';
		  actions.toggleRowSelected = 'toggleRowSelected';
		  actions.toggleAllPageRowsSelected = 'toggleAllPageRowsSelected';
		  var useRowSelect = function useRowSelect(hooks) {
		    hooks.getToggleRowSelectedProps = [defaultGetToggleRowSelectedProps];
		    hooks.getToggleAllRowsSelectedProps = [defaultGetToggleAllRowsSelectedProps];
		    hooks.getToggleAllPageRowsSelectedProps = [defaultGetToggleAllPageRowsSelectedProps];
		    hooks.stateReducers.push(reducer$8);
		    hooks.useInstance.push(useInstance$8);
		    hooks.prepareRow.push(prepareRow$3);
		  };
		  useRowSelect.pluginName = pluginName$1;

		  var defaultGetToggleRowSelectedProps = function defaultGetToggleRowSelectedProps(props, _ref) {
		    var instance = _ref.instance,
		        row = _ref.row;
		    var _instance$manualRowSe = instance.manualRowSelectedKey,
		        manualRowSelectedKey = _instance$manualRowSe === void 0 ? 'isSelected' : _instance$manualRowSe;
		    var checked = false;

		    if (row.original && row.original[manualRowSelectedKey]) {
		      checked = true;
		    } else {
		      checked = row.isSelected;
		    }

		    return [props, {
		      onChange: function onChange(e) {
		        row.toggleRowSelected(e.target.checked);
		      },
		      style: {
		        cursor: 'pointer'
		      },
		      checked: checked,
		      title: 'Toggle Row Selected',
		      indeterminate: row.isSomeSelected
		    }];
		  };

		  var defaultGetToggleAllRowsSelectedProps = function defaultGetToggleAllRowsSelectedProps(props, _ref2) {
		    var instance = _ref2.instance;
		    return [props, {
		      onChange: function onChange(e) {
		        instance.toggleAllRowsSelected(e.target.checked);
		      },
		      style: {
		        cursor: 'pointer'
		      },
		      checked: instance.isAllRowsSelected,
		      title: 'Toggle All Rows Selected',
		      indeterminate: Boolean(!instance.isAllRowsSelected && Object.keys(instance.state.selectedRowIds).length)
		    }];
		  };

		  var defaultGetToggleAllPageRowsSelectedProps = function defaultGetToggleAllPageRowsSelectedProps(props, _ref3) {
		    var instance = _ref3.instance;
		    return [props, {
		      onChange: function onChange(e) {
		        instance.toggleAllPageRowsSelected(e.target.checked);
		      },
		      style: {
		        cursor: 'pointer'
		      },
		      checked: instance.isAllPageRowsSelected,
		      title: 'Toggle All Current Page Rows Selected',
		      indeterminate: Boolean(!instance.isAllPageRowsSelected && instance.page.some(function (_ref4) {
		        var id = _ref4.id;
		        return instance.state.selectedRowIds[id];
		      }))
		    }];
		  }; // eslint-disable-next-line max-params


		  function reducer$8(state, action, previousState, instance) {
		    if (action.type === actions.init) {
		      return _extends({
		        selectedRowIds: {}
		      }, state);
		    }

		    if (action.type === actions.resetSelectedRows) {
		      return _extends({}, state, {
		        selectedRowIds: instance.initialState.selectedRowIds || {}
		      });
		    }

		    if (action.type === actions.toggleAllRowsSelected) {
		      var setSelected = action.value;
		      var isAllRowsSelected = instance.isAllRowsSelected,
		          rowsById = instance.rowsById,
		          _instance$nonGroupedR = instance.nonGroupedRowsById,
		          nonGroupedRowsById = _instance$nonGroupedR === void 0 ? rowsById : _instance$nonGroupedR;
		      var selectAll = typeof setSelected !== 'undefined' ? setSelected : !isAllRowsSelected; // Only remove/add the rows that are visible on the screen
		      //  Leave all the other rows that are selected alone.

		      var selectedRowIds = Object.assign({}, state.selectedRowIds);

		      if (selectAll) {
		        Object.keys(nonGroupedRowsById).forEach(function (rowId) {
		          selectedRowIds[rowId] = true;
		        });
		      } else {
		        Object.keys(nonGroupedRowsById).forEach(function (rowId) {
		          delete selectedRowIds[rowId];
		        });
		      }

		      return _extends({}, state, {
		        selectedRowIds: selectedRowIds
		      });
		    }

		    if (action.type === actions.toggleRowSelected) {
		      var id = action.id,
		          _setSelected = action.value;
		      var _rowsById = instance.rowsById,
		          _instance$selectSubRo = instance.selectSubRows,
		          selectSubRows = _instance$selectSubRo === void 0 ? true : _instance$selectSubRo,
		          getSubRows = instance.getSubRows;
		      var isSelected = state.selectedRowIds[id];
		      var shouldExist = typeof _setSelected !== 'undefined' ? _setSelected : !isSelected;

		      if (isSelected === shouldExist) {
		        return state;
		      }

		      var newSelectedRowIds = _extends({}, state.selectedRowIds);

		      var handleRowById = function handleRowById(id) {
		        var row = _rowsById[id];

		        if (row) {
		          if (!row.isGrouped) {
		            if (shouldExist) {
		              newSelectedRowIds[id] = true;
		            } else {
		              delete newSelectedRowIds[id];
		            }
		          }

		          if (selectSubRows && getSubRows(row)) {
		            return getSubRows(row).forEach(function (row) {
		              return handleRowById(row.id);
		            });
		          }
		        }
		      };

		      handleRowById(id);
		      return _extends({}, state, {
		        selectedRowIds: newSelectedRowIds
		      });
		    }

		    if (action.type === actions.toggleAllPageRowsSelected) {
		      var _setSelected2 = action.value;

		      var page = instance.page,
		          _rowsById2 = instance.rowsById,
		          _instance$selectSubRo2 = instance.selectSubRows,
		          _selectSubRows = _instance$selectSubRo2 === void 0 ? true : _instance$selectSubRo2,
		          isAllPageRowsSelected = instance.isAllPageRowsSelected,
		          _getSubRows = instance.getSubRows;

		      var _selectAll = typeof _setSelected2 !== 'undefined' ? _setSelected2 : !isAllPageRowsSelected;

		      var _newSelectedRowIds = _extends({}, state.selectedRowIds);

		      var _handleRowById = function _handleRowById(id) {
		        var row = _rowsById2[id];

		        if (!row.isGrouped) {
		          if (_selectAll) {
		            _newSelectedRowIds[id] = true;
		          } else {
		            delete _newSelectedRowIds[id];
		          }
		        }

		        if (_selectSubRows && _getSubRows(row)) {
		          return _getSubRows(row).forEach(function (row) {
		            return _handleRowById(row.id);
		          });
		        }
		      };

		      page.forEach(function (row) {
		        return _handleRowById(row.id);
		      });
		      return _extends({}, state, {
		        selectedRowIds: _newSelectedRowIds
		      });
		    }

		    return state;
		  }

		  function useInstance$8(instance) {
		    var data = instance.data,
		        rows = instance.rows,
		        getHooks = instance.getHooks,
		        plugins = instance.plugins,
		        rowsById = instance.rowsById,
		        _instance$nonGroupedR2 = instance.nonGroupedRowsById,
		        nonGroupedRowsById = _instance$nonGroupedR2 === void 0 ? rowsById : _instance$nonGroupedR2,
		        _instance$autoResetSe = instance.autoResetSelectedRows,
		        autoResetSelectedRows = _instance$autoResetSe === void 0 ? true : _instance$autoResetSe,
		        selectedRowIds = instance.state.selectedRowIds,
		        _instance$selectSubRo3 = instance.selectSubRows,
		        selectSubRows = _instance$selectSubRo3 === void 0 ? true : _instance$selectSubRo3,
		        dispatch = instance.dispatch,
		        page = instance.page,
		        getSubRows = instance.getSubRows;
		    ensurePluginOrder(plugins, ['useFilters', 'useGroupBy', 'useSortBy', 'useExpanded', 'usePagination'], 'useRowSelect');
		    var selectedFlatRows = React.useMemo(function () {
		      var selectedFlatRows = [];
		      rows.forEach(function (row) {
		        var isSelected = selectSubRows ? getRowIsSelected(row, selectedRowIds, getSubRows) : !!selectedRowIds[row.id];
		        row.isSelected = !!isSelected;
		        row.isSomeSelected = isSelected === null;

		        if (isSelected) {
		          selectedFlatRows.push(row);
		        }
		      });
		      return selectedFlatRows;
		    }, [rows, selectSubRows, selectedRowIds, getSubRows]);
		    var isAllRowsSelected = Boolean(Object.keys(nonGroupedRowsById).length && Object.keys(selectedRowIds).length);
		    var isAllPageRowsSelected = isAllRowsSelected;

		    if (isAllRowsSelected) {
		      if (Object.keys(nonGroupedRowsById).some(function (id) {
		        return !selectedRowIds[id];
		      })) {
		        isAllRowsSelected = false;
		      }
		    }

		    if (!isAllRowsSelected) {
		      if (page && page.length && page.some(function (_ref5) {
		        var id = _ref5.id;
		        return !selectedRowIds[id];
		      })) {
		        isAllPageRowsSelected = false;
		      }
		    }

		    var getAutoResetSelectedRows = useGetLatest(autoResetSelectedRows);
		    useMountedLayoutEffect(function () {
		      if (getAutoResetSelectedRows()) {
		        dispatch({
		          type: actions.resetSelectedRows
		        });
		      }
		    }, [dispatch, data]);
		    var toggleAllRowsSelected = React.useCallback(function (value) {
		      return dispatch({
		        type: actions.toggleAllRowsSelected,
		        value: value
		      });
		    }, [dispatch]);
		    var toggleAllPageRowsSelected = React.useCallback(function (value) {
		      return dispatch({
		        type: actions.toggleAllPageRowsSelected,
		        value: value
		      });
		    }, [dispatch]);
		    var toggleRowSelected = React.useCallback(function (id, value) {
		      return dispatch({
		        type: actions.toggleRowSelected,
		        id: id,
		        value: value
		      });
		    }, [dispatch]);
		    var getInstance = useGetLatest(instance);
		    var getToggleAllRowsSelectedProps = makePropGetter(getHooks().getToggleAllRowsSelectedProps, {
		      instance: getInstance()
		    });
		    var getToggleAllPageRowsSelectedProps = makePropGetter(getHooks().getToggleAllPageRowsSelectedProps, {
		      instance: getInstance()
		    });
		    Object.assign(instance, {
		      selectedFlatRows: selectedFlatRows,
		      isAllRowsSelected: isAllRowsSelected,
		      isAllPageRowsSelected: isAllPageRowsSelected,
		      toggleRowSelected: toggleRowSelected,
		      toggleAllRowsSelected: toggleAllRowsSelected,
		      getToggleAllRowsSelectedProps: getToggleAllRowsSelectedProps,
		      getToggleAllPageRowsSelectedProps: getToggleAllPageRowsSelectedProps,
		      toggleAllPageRowsSelected: toggleAllPageRowsSelected
		    });
		  }

		  function prepareRow$3(row, _ref6) {
		    var instance = _ref6.instance;

		    row.toggleRowSelected = function (set) {
		      return instance.toggleRowSelected(row.id, set);
		    };

		    row.getToggleRowSelectedProps = makePropGetter(instance.getHooks().getToggleRowSelectedProps, {
		      instance: instance,
		      row: row
		    });
		  }

		  function getRowIsSelected(row, selectedRowIds, getSubRows) {
		    if (selectedRowIds[row.id]) {
		      return true;
		    }

		    var subRows = getSubRows(row);

		    if (subRows && subRows.length) {
		      var allChildrenSelected = true;
		      var someSelected = false;
		      subRows.forEach(function (subRow) {
		        // Bail out early if we know both of these
		        if (someSelected && !allChildrenSelected) {
		          return;
		        }

		        if (getRowIsSelected(subRow, selectedRowIds, getSubRows)) {
		          someSelected = true;
		        } else {
		          allChildrenSelected = false;
		        }
		      });
		      return allChildrenSelected ? true : someSelected ? null : false;
		    }

		    return false;
		  }

		  var defaultInitialRowStateAccessor = function defaultInitialRowStateAccessor(row) {
		    return {};
		  };

		  var defaultInitialCellStateAccessor = function defaultInitialCellStateAccessor(cell) {
		    return {};
		  }; // Actions


		  actions.setRowState = 'setRowState';
		  actions.setCellState = 'setCellState';
		  actions.resetRowState = 'resetRowState';
		  var useRowState = function useRowState(hooks) {
		    hooks.stateReducers.push(reducer$9);
		    hooks.useInstance.push(useInstance$9);
		    hooks.prepareRow.push(prepareRow$4);
		  };
		  useRowState.pluginName = 'useRowState';

		  function reducer$9(state, action, previousState, instance) {
		    var _instance$initialRowS = instance.initialRowStateAccessor,
		        initialRowStateAccessor = _instance$initialRowS === void 0 ? defaultInitialRowStateAccessor : _instance$initialRowS,
		        _instance$initialCell = instance.initialCellStateAccessor,
		        initialCellStateAccessor = _instance$initialCell === void 0 ? defaultInitialCellStateAccessor : _instance$initialCell,
		        rowsById = instance.rowsById;

		    if (action.type === actions.init) {
		      return _extends({
		        rowState: {}
		      }, state);
		    }

		    if (action.type === actions.resetRowState) {
		      return _extends({}, state, {
		        rowState: instance.initialState.rowState || {}
		      });
		    }

		    if (action.type === actions.setRowState) {
		      var _extends2;

		      var rowId = action.rowId,
		          value = action.value;
		      var oldRowState = typeof state.rowState[rowId] !== 'undefined' ? state.rowState[rowId] : initialRowStateAccessor(rowsById[rowId]);
		      return _extends({}, state, {
		        rowState: _extends({}, state.rowState, (_extends2 = {}, _extends2[rowId] = functionalUpdate(value, oldRowState), _extends2))
		      });
		    }

		    if (action.type === actions.setCellState) {
		      var _oldRowState$cellStat, _rowsById$_rowId, _rowsById$_rowId$cell, _extends3, _extends4;

		      var _rowId = action.rowId,
		          columnId = action.columnId,
		          _value = action.value;

		      var _oldRowState = typeof state.rowState[_rowId] !== 'undefined' ? state.rowState[_rowId] : initialRowStateAccessor(rowsById[_rowId]);

		      var oldCellState = typeof (_oldRowState == null ? void 0 : (_oldRowState$cellStat = _oldRowState.cellState) == null ? void 0 : _oldRowState$cellStat[columnId]) !== 'undefined' ? _oldRowState.cellState[columnId] : initialCellStateAccessor((_rowsById$_rowId = rowsById[_rowId]) == null ? void 0 : (_rowsById$_rowId$cell = _rowsById$_rowId.cells) == null ? void 0 : _rowsById$_rowId$cell.find(function (cell) {
		        return cell.column.id === columnId;
		      }));
		      return _extends({}, state, {
		        rowState: _extends({}, state.rowState, (_extends4 = {}, _extends4[_rowId] = _extends({}, _oldRowState, {
		          cellState: _extends({}, _oldRowState.cellState || {}, (_extends3 = {}, _extends3[columnId] = functionalUpdate(_value, oldCellState), _extends3))
		        }), _extends4))
		      });
		    }
		  }

		  function useInstance$9(instance) {
		    var _instance$autoResetRo = instance.autoResetRowState,
		        autoResetRowState = _instance$autoResetRo === void 0 ? true : _instance$autoResetRo,
		        data = instance.data,
		        dispatch = instance.dispatch;
		    var setRowState = React.useCallback(function (rowId, value) {
		      return dispatch({
		        type: actions.setRowState,
		        rowId: rowId,
		        value: value
		      });
		    }, [dispatch]);
		    var setCellState = React.useCallback(function (rowId, columnId, value) {
		      return dispatch({
		        type: actions.setCellState,
		        rowId: rowId,
		        columnId: columnId,
		        value: value
		      });
		    }, [dispatch]);
		    var getAutoResetRowState = useGetLatest(autoResetRowState);
		    useMountedLayoutEffect(function () {
		      if (getAutoResetRowState()) {
		        dispatch({
		          type: actions.resetRowState
		        });
		      }
		    }, [data]);
		    Object.assign(instance, {
		      setRowState: setRowState,
		      setCellState: setCellState
		    });
		  }

		  function prepareRow$4(row, _ref) {
		    var instance = _ref.instance;
		    var _instance$initialRowS2 = instance.initialRowStateAccessor,
		        initialRowStateAccessor = _instance$initialRowS2 === void 0 ? defaultInitialRowStateAccessor : _instance$initialRowS2,
		        _instance$initialCell2 = instance.initialCellStateAccessor,
		        initialCellStateAccessor = _instance$initialCell2 === void 0 ? defaultInitialCellStateAccessor : _instance$initialCell2,
		        rowState = instance.state.rowState;

		    if (row) {
		      row.state = typeof rowState[row.id] !== 'undefined' ? rowState[row.id] : initialRowStateAccessor(row);

		      row.setState = function (updater) {
		        return instance.setRowState(row.id, updater);
		      };

		      row.cells.forEach(function (cell) {
		        if (!row.state.cellState) {
		          row.state.cellState = {};
		        }

		        cell.state = typeof row.state.cellState[cell.column.id] !== 'undefined' ? row.state.cellState[cell.column.id] : initialCellStateAccessor(cell);

		        cell.setState = function (updater) {
		          return instance.setCellState(row.id, cell.column.id, updater);
		        };
		      });
		    }
		  }

		  actions.resetColumnOrder = 'resetColumnOrder';
		  actions.setColumnOrder = 'setColumnOrder';
		  var useColumnOrder = function useColumnOrder(hooks) {
		    hooks.stateReducers.push(reducer$a);
		    hooks.visibleColumnsDeps.push(function (deps, _ref) {
		      var instance = _ref.instance;
		      return [].concat(deps, [instance.state.columnOrder]);
		    });
		    hooks.visibleColumns.push(visibleColumns$2);
		    hooks.useInstance.push(useInstance$a);
		  };
		  useColumnOrder.pluginName = 'useColumnOrder';

		  function reducer$a(state, action, previousState, instance) {
		    if (action.type === actions.init) {
		      return _extends({
		        columnOrder: []
		      }, state);
		    }

		    if (action.type === actions.resetColumnOrder) {
		      return _extends({}, state, {
		        columnOrder: instance.initialState.columnOrder || []
		      });
		    }

		    if (action.type === actions.setColumnOrder) {
		      return _extends({}, state, {
		        columnOrder: functionalUpdate(action.columnOrder, state.columnOrder)
		      });
		    }
		  }

		  function visibleColumns$2(columns, _ref2) {
		    var columnOrder = _ref2.instance.state.columnOrder;

		    // If there is no order, return the normal columns
		    if (!columnOrder || !columnOrder.length) {
		      return columns;
		    }

		    var columnOrderCopy = [].concat(columnOrder); // If there is an order, make a copy of the columns

		    var columnsCopy = [].concat(columns); // And make a new ordered array of the columns

		    var columnsInOrder = []; // Loop over the columns and place them in order into the new array

		    var _loop = function _loop() {
		      var targetColumnId = columnOrderCopy.shift();
		      var foundIndex = columnsCopy.findIndex(function (d) {
		        return d.id === targetColumnId;
		      });

		      if (foundIndex > -1) {
		        columnsInOrder.push(columnsCopy.splice(foundIndex, 1)[0]);
		      }
		    };

		    while (columnsCopy.length && columnOrderCopy.length) {
		      _loop();
		    } // If there are any columns left, add them to the end


		    return [].concat(columnsInOrder, columnsCopy);
		  }

		  function useInstance$a(instance) {
		    var dispatch = instance.dispatch;
		    instance.setColumnOrder = React.useCallback(function (columnOrder) {
		      return dispatch({
		        type: actions.setColumnOrder,
		        columnOrder: columnOrder
		      });
		    }, [dispatch]);
		  }

		  defaultColumn.canResize = true; // Actions

		  actions.columnStartResizing = 'columnStartResizing';
		  actions.columnResizing = 'columnResizing';
		  actions.columnDoneResizing = 'columnDoneResizing';
		  actions.resetResize = 'resetResize';
		  var useResizeColumns = function useResizeColumns(hooks) {
		    hooks.getResizerProps = [defaultGetResizerProps];
		    hooks.getHeaderProps.push({
		      style: {
		        position: 'relative'
		      }
		    });
		    hooks.stateReducers.push(reducer$b);
		    hooks.useInstance.push(useInstance$b);
		    hooks.useInstanceBeforeDimensions.push(useInstanceBeforeDimensions$1);
		  };

		  var defaultGetResizerProps = function defaultGetResizerProps(props, _ref) {
		    var instance = _ref.instance,
		        header = _ref.header;
		    var dispatch = instance.dispatch;

		    var onResizeStart = function onResizeStart(e, header) {
		      var isTouchEvent = false;

		      if (e.type === 'touchstart') {
		        // lets not respond to multiple touches (e.g. 2 or 3 fingers)
		        if (e.touches && e.touches.length > 1) {
		          return;
		        }

		        isTouchEvent = true;
		      }

		      var headersToResize = getLeafHeaders(header);
		      var headerIdWidths = headersToResize.map(function (d) {
		        return [d.id, d.totalWidth];
		      });
		      var clientX = isTouchEvent ? Math.round(e.touches[0].clientX) : e.clientX;
		      var raf;
		      var mostRecentClientX;

		      var dispatchEnd = function dispatchEnd() {
		        window.cancelAnimationFrame(raf);
		        raf = null;
		        dispatch({
		          type: actions.columnDoneResizing
		        });
		      };

		      var dispatchMove = function dispatchMove() {
		        window.cancelAnimationFrame(raf);
		        raf = null;
		        dispatch({
		          type: actions.columnResizing,
		          clientX: mostRecentClientX
		        });
		      };

		      var scheduleDispatchMoveOnNextAnimationFrame = function scheduleDispatchMoveOnNextAnimationFrame(clientXPos) {
		        mostRecentClientX = clientXPos;

		        if (!raf) {
		          raf = window.requestAnimationFrame(dispatchMove);
		        }
		      };

		      var handlersAndEvents = {
		        mouse: {
		          moveEvent: 'mousemove',
		          moveHandler: function moveHandler(e) {
		            return scheduleDispatchMoveOnNextAnimationFrame(e.clientX);
		          },
		          upEvent: 'mouseup',
		          upHandler: function upHandler(e) {
		            document.removeEventListener('mousemove', handlersAndEvents.mouse.moveHandler);
		            document.removeEventListener('mouseup', handlersAndEvents.mouse.upHandler);
		            dispatchEnd();
		          }
		        },
		        touch: {
		          moveEvent: 'touchmove',
		          moveHandler: function moveHandler(e) {
		            if (e.cancelable) {
		              e.preventDefault();
		              e.stopPropagation();
		            }

		            scheduleDispatchMoveOnNextAnimationFrame(e.touches[0].clientX);
		            return false;
		          },
		          upEvent: 'touchend',
		          upHandler: function upHandler(e) {
		            document.removeEventListener(handlersAndEvents.touch.moveEvent, handlersAndEvents.touch.moveHandler);
		            document.removeEventListener(handlersAndEvents.touch.upEvent, handlersAndEvents.touch.moveHandler);
		            dispatchEnd();
		          }
		        }
		      };
		      var events = isTouchEvent ? handlersAndEvents.touch : handlersAndEvents.mouse;
		      var passiveIfSupported = passiveEventSupported() ? {
		        passive: false
		      } : false;
		      document.addEventListener(events.moveEvent, events.moveHandler, passiveIfSupported);
		      document.addEventListener(events.upEvent, events.upHandler, passiveIfSupported);
		      dispatch({
		        type: actions.columnStartResizing,
		        columnId: header.id,
		        columnWidth: header.totalWidth,
		        headerIdWidths: headerIdWidths,
		        clientX: clientX
		      });
		    };

		    return [props, {
		      onMouseDown: function onMouseDown(e) {
		        return e.persist() || onResizeStart(e, header);
		      },
		      onTouchStart: function onTouchStart(e) {
		        return e.persist() || onResizeStart(e, header);
		      },
		      style: {
		        cursor: 'col-resize'
		      },
		      draggable: false,
		      role: 'separator'
		    }];
		  };

		  useResizeColumns.pluginName = 'useResizeColumns';

		  function reducer$b(state, action) {
		    if (action.type === actions.init) {
		      return _extends({
		        columnResizing: {
		          columnWidths: {}
		        }
		      }, state);
		    }

		    if (action.type === actions.resetResize) {
		      return _extends({}, state, {
		        columnResizing: {
		          columnWidths: {}
		        }
		      });
		    }

		    if (action.type === actions.columnStartResizing) {
		      var clientX = action.clientX,
		          columnId = action.columnId,
		          columnWidth = action.columnWidth,
		          headerIdWidths = action.headerIdWidths;
		      return _extends({}, state, {
		        columnResizing: _extends({}, state.columnResizing, {
		          startX: clientX,
		          headerIdWidths: headerIdWidths,
		          columnWidth: columnWidth,
		          isResizingColumn: columnId
		        })
		      });
		    }

		    if (action.type === actions.columnResizing) {
		      var _clientX = action.clientX;

		      var _state$columnResizing = state.columnResizing,
		          startX = _state$columnResizing.startX,
		          _columnWidth = _state$columnResizing.columnWidth,
		          _state$columnResizing2 = _state$columnResizing.headerIdWidths,
		          _headerIdWidths = _state$columnResizing2 === void 0 ? [] : _state$columnResizing2;

		      var deltaX = _clientX - startX;
		      var percentageDeltaX = deltaX / _columnWidth;
		      var newColumnWidths = {};

		      _headerIdWidths.forEach(function (_ref2) {
		        var headerId = _ref2[0],
		            headerWidth = _ref2[1];
		        newColumnWidths[headerId] = Math.max(headerWidth + headerWidth * percentageDeltaX, 0);
		      });

		      return _extends({}, state, {
		        columnResizing: _extends({}, state.columnResizing, {
		          columnWidths: _extends({}, state.columnResizing.columnWidths, {}, newColumnWidths)
		        })
		      });
		    }

		    if (action.type === actions.columnDoneResizing) {
		      return _extends({}, state, {
		        columnResizing: _extends({}, state.columnResizing, {
		          startX: null,
		          isResizingColumn: null
		        })
		      });
		    }
		  }

		  var useInstanceBeforeDimensions$1 = function useInstanceBeforeDimensions(instance) {
		    var flatHeaders = instance.flatHeaders,
		        disableResizing = instance.disableResizing,
		        getHooks = instance.getHooks,
		        columnResizing = instance.state.columnResizing;
		    var getInstance = useGetLatest(instance);
		    flatHeaders.forEach(function (header) {
		      var canResize = getFirstDefined(header.disableResizing === true ? false : undefined, disableResizing === true ? false : undefined, true);
		      header.canResize = canResize;
		      header.width = columnResizing.columnWidths[header.id] || header.originalWidth || header.width;
		      header.isResizing = columnResizing.isResizingColumn === header.id;

		      if (canResize) {
		        header.getResizerProps = makePropGetter(getHooks().getResizerProps, {
		          instance: getInstance(),
		          header: header
		        });
		      }
		    });
		  };

		  function useInstance$b(instance) {
		    var plugins = instance.plugins,
		        dispatch = instance.dispatch,
		        _instance$autoResetRe = instance.autoResetResize,
		        autoResetResize = _instance$autoResetRe === void 0 ? true : _instance$autoResetRe,
		        columns = instance.columns;
		    ensurePluginOrder(plugins, ['useAbsoluteLayout'], 'useResizeColumns');
		    var getAutoResetResize = useGetLatest(autoResetResize);
		    useMountedLayoutEffect(function () {
		      if (getAutoResetResize()) {
		        dispatch({
		          type: actions.resetResize
		        });
		      }
		    }, [columns]);
		    var resetResizing = React.useCallback(function () {
		      return dispatch({
		        type: actions.resetResize
		      });
		    }, [dispatch]);
		    Object.assign(instance, {
		      resetResizing: resetResizing
		    });
		  }

		  function getLeafHeaders(header) {
		    var leafHeaders = [];

		    var recurseHeader = function recurseHeader(header) {
		      if (header.columns && header.columns.length) {
		        header.columns.map(recurseHeader);
		      }

		      leafHeaders.push(header);
		    };

		    recurseHeader(header);
		    return leafHeaders;
		  }

		  var cellStyles = {
		    position: 'absolute',
		    top: 0
		  };
		  var useAbsoluteLayout = function useAbsoluteLayout(hooks) {
		    hooks.getTableBodyProps.push(getRowStyles);
		    hooks.getRowProps.push(getRowStyles);
		    hooks.getHeaderGroupProps.push(getRowStyles);
		    hooks.getFooterGroupProps.push(getRowStyles);
		    hooks.getHeaderProps.push(function (props, _ref) {
		      var column = _ref.column;
		      return [props, {
		        style: _extends({}, cellStyles, {
		          left: column.totalLeft + "px",
		          width: column.totalWidth + "px"
		        })
		      }];
		    });
		    hooks.getCellProps.push(function (props, _ref2) {
		      var cell = _ref2.cell;
		      return [props, {
		        style: _extends({}, cellStyles, {
		          left: cell.column.totalLeft + "px",
		          width: cell.column.totalWidth + "px"
		        })
		      }];
		    });
		    hooks.getFooterProps.push(function (props, _ref3) {
		      var column = _ref3.column;
		      return [props, {
		        style: _extends({}, cellStyles, {
		          left: column.totalLeft + "px",
		          width: column.totalWidth + "px"
		        })
		      }];
		    });
		  };
		  useAbsoluteLayout.pluginName = 'useAbsoluteLayout';

		  var getRowStyles = function getRowStyles(props, _ref4) {
		    var instance = _ref4.instance;
		    return [props, {
		      style: {
		        position: 'relative',
		        width: instance.totalColumnsWidth + "px"
		      }
		    }];
		  };

		  var cellStyles$1 = {
		    display: 'inline-block',
		    boxSizing: 'border-box'
		  };

		  var getRowStyles$1 = function getRowStyles(props, _ref) {
		    var instance = _ref.instance;
		    return [props, {
		      style: {
		        display: 'flex',
		        width: instance.totalColumnsWidth + "px"
		      }
		    }];
		  };

		  var useBlockLayout = function useBlockLayout(hooks) {
		    hooks.getRowProps.push(getRowStyles$1);
		    hooks.getHeaderGroupProps.push(getRowStyles$1);
		    hooks.getFooterGroupProps.push(getRowStyles$1);
		    hooks.getHeaderProps.push(function (props, _ref2) {
		      var column = _ref2.column;
		      return [props, {
		        style: _extends({}, cellStyles$1, {
		          width: column.totalWidth + "px"
		        })
		      }];
		    });
		    hooks.getCellProps.push(function (props, _ref3) {
		      var cell = _ref3.cell;
		      return [props, {
		        style: _extends({}, cellStyles$1, {
		          width: cell.column.totalWidth + "px"
		        })
		      }];
		    });
		    hooks.getFooterProps.push(function (props, _ref4) {
		      var column = _ref4.column;
		      return [props, {
		        style: _extends({}, cellStyles$1, {
		          width: column.totalWidth + "px"
		        })
		      }];
		    });
		  };
		  useBlockLayout.pluginName = 'useBlockLayout';

		  function useFlexLayout(hooks) {
		    hooks.getTableProps.push(getTableProps);
		    hooks.getRowProps.push(getRowStyles$2);
		    hooks.getHeaderGroupProps.push(getRowStyles$2);
		    hooks.getFooterGroupProps.push(getRowStyles$2);
		    hooks.getHeaderProps.push(getHeaderProps);
		    hooks.getCellProps.push(getCellProps);
		    hooks.getFooterProps.push(getFooterProps);
		  }
		  useFlexLayout.pluginName = 'useFlexLayout';

		  var getTableProps = function getTableProps(props, _ref) {
		    var instance = _ref.instance;
		    return [props, {
		      style: {
		        minWidth: instance.totalColumnsMinWidth + "px"
		      }
		    }];
		  };

		  var getRowStyles$2 = function getRowStyles(props, _ref2) {
		    var instance = _ref2.instance;
		    return [props, {
		      style: {
		        display: 'flex',
		        flex: '1 0 auto',
		        minWidth: instance.totalColumnsMinWidth + "px"
		      }
		    }];
		  };

		  var getHeaderProps = function getHeaderProps(props, _ref3) {
		    var column = _ref3.column;
		    return [props, {
		      style: {
		        boxSizing: 'border-box',
		        flex: column.totalFlexWidth ? column.totalFlexWidth + " 0 auto" : undefined,
		        minWidth: column.totalMinWidth + "px",
		        width: column.totalWidth + "px"
		      }
		    }];
		  };

		  var getCellProps = function getCellProps(props, _ref4) {
		    var cell = _ref4.cell;
		    return [props, {
		      style: {
		        boxSizing: 'border-box',
		        flex: cell.column.totalFlexWidth + " 0 auto",
		        minWidth: cell.column.totalMinWidth + "px",
		        width: cell.column.totalWidth + "px"
		      }
		    }];
		  };

		  var getFooterProps = function getFooterProps(props, _ref5) {
		    var column = _ref5.column;
		    return [props, {
		      style: {
		        boxSizing: 'border-box',
		        flex: column.totalFlexWidth ? column.totalFlexWidth + " 0 auto" : undefined,
		        minWidth: column.totalMinWidth + "px",
		        width: column.totalWidth + "px"
		      }
		    }];
		  };

		  actions.columnStartResizing = 'columnStartResizing';
		  actions.columnResizing = 'columnResizing';
		  actions.columnDoneResizing = 'columnDoneResizing';
		  actions.resetResize = 'resetResize';
		  function useGridLayout(hooks) {
		    hooks.stateReducers.push(reducer$c);
		    hooks.getTableProps.push(getTableProps$1);
		    hooks.getHeaderProps.push(getHeaderProps$1);
		    hooks.getRowProps.push(getRowProps);
		  }
		  useGridLayout.pluginName = 'useGridLayout';

		  var getTableProps$1 = function getTableProps(props, _ref) {
		    var instance = _ref.instance;
		    var gridTemplateColumns = instance.visibleColumns.map(function (column) {
		      var _instance$state$colum;

		      if (instance.state.gridLayout.columnWidths[column.id]) return instance.state.gridLayout.columnWidths[column.id] + "px"; // When resizing, lock the width of all unset columns
		      // instead of using user-provided width or defaultColumn width,
		      // which could potentially be 'auto' or 'fr' units that don't scale linearly

		      if ((_instance$state$colum = instance.state.columnResizing) == null ? void 0 : _instance$state$colum.isResizingColumn) return instance.state.gridLayout.startWidths[column.id] + "px";
		      if (typeof column.width === 'number') return column.width + "px";
		      return column.width;
		    });
		    return [props, {
		      style: {
		        display: "grid",
		        gridTemplateColumns: gridTemplateColumns.join(" ")
		      }
		    }];
		  };

		  var getHeaderProps$1 = function getHeaderProps(props, _ref2) {
		    var column = _ref2.column;
		    return [props, {
		      id: "header-cell-" + column.id,
		      style: {
		        position: "sticky",
		        //enables a scroll wrapper to be placed around the table and have sticky headers
		        gridColumn: "span " + column.totalVisibleHeaderCount
		      }
		    }];
		  };

		  var getRowProps = function getRowProps(props, _ref3) {
		    var row = _ref3.row;

		    if (row.isExpanded) {
		      return [props, {
		        style: {
		          gridColumn: "1 / " + (row.cells.length + 1)
		        }
		      }];
		    }

		    return [props, {}];
		  };

		  function reducer$c(state, action, previousState, instance) {
		    if (action.type === actions.init) {
		      return _extends({
		        gridLayout: {
		          columnWidths: {}
		        }
		      }, state);
		    }

		    if (action.type === actions.resetResize) {
		      return _extends({}, state, {
		        gridLayout: {
		          columnWidths: {}
		        }
		      });
		    }

		    if (action.type === actions.columnStartResizing) {
		      var columnId = action.columnId,
		          headerIdWidths = action.headerIdWidths;
		      var columnWidth = getElementWidth(columnId);

		      if (columnWidth !== undefined) {
		        var startWidths = instance.visibleColumns.reduce(function (acc, column) {
		          var _extends2;

		          return _extends({}, acc, (_extends2 = {}, _extends2[column.id] = getElementWidth(column.id), _extends2));
		        }, {});
		        var minWidths = instance.visibleColumns.reduce(function (acc, column) {
		          var _extends3;

		          return _extends({}, acc, (_extends3 = {}, _extends3[column.id] = column.minWidth, _extends3));
		        }, {});
		        var maxWidths = instance.visibleColumns.reduce(function (acc, column) {
		          var _extends4;

		          return _extends({}, acc, (_extends4 = {}, _extends4[column.id] = column.maxWidth, _extends4));
		        }, {});
		        var headerIdGridWidths = headerIdWidths.map(function (_ref4) {
		          var headerId = _ref4[0];
		          return [headerId, getElementWidth(headerId)];
		        });
		        return _extends({}, state, {
		          gridLayout: _extends({}, state.gridLayout, {
		            startWidths: startWidths,
		            minWidths: minWidths,
		            maxWidths: maxWidths,
		            headerIdGridWidths: headerIdGridWidths,
		            columnWidth: columnWidth
		          })
		        });
		      } else {
		        return state;
		      }
		    }

		    if (action.type === actions.columnResizing) {
		      var clientX = action.clientX;
		      var startX = state.columnResizing.startX;

		      var _state$gridLayout = state.gridLayout,
		          _columnWidth = _state$gridLayout.columnWidth,
		          _minWidths = _state$gridLayout.minWidths,
		          _maxWidths = _state$gridLayout.maxWidths,
		          _state$gridLayout$hea = _state$gridLayout.headerIdGridWidths,
		          _headerIdGridWidths = _state$gridLayout$hea === void 0 ? [] : _state$gridLayout$hea;

		      var deltaX = clientX - startX;
		      var percentageDeltaX = deltaX / _columnWidth;
		      var newColumnWidths = {};

		      _headerIdGridWidths.forEach(function (_ref5) {
		        var headerId = _ref5[0],
		            headerWidth = _ref5[1];
		        newColumnWidths[headerId] = Math.min(Math.max(_minWidths[headerId], headerWidth + headerWidth * percentageDeltaX), _maxWidths[headerId]);
		      });

		      return _extends({}, state, {
		        gridLayout: _extends({}, state.gridLayout, {
		          columnWidths: _extends({}, state.gridLayout.columnWidths, {}, newColumnWidths)
		        })
		      });
		    }

		    if (action.type === actions.columnDoneResizing) {
		      return _extends({}, state, {
		        gridLayout: _extends({}, state.gridLayout, {
		          startWidths: {},
		          minWidths: {},
		          maxWidths: {}
		        })
		      });
		    }
		  }

		  function getElementWidth(columnId) {
		    var _document$getElementB;

		    var width = (_document$getElementB = document.getElementById("header-cell-" + columnId)) == null ? void 0 : _document$getElementB.offsetWidth;

		    if (width !== undefined) {
		      return width;
		    }
		  }

		  exports._UNSTABLE_usePivotColumns = _UNSTABLE_usePivotColumns;
		  exports.actions = actions;
		  exports.defaultColumn = defaultColumn;
		  exports.defaultGroupByFn = defaultGroupByFn;
		  exports.defaultOrderByFn = defaultOrderByFn;
		  exports.defaultRenderer = defaultRenderer;
		  exports.emptyRenderer = emptyRenderer;
		  exports.ensurePluginOrder = ensurePluginOrder;
		  exports.flexRender = flexRender;
		  exports.functionalUpdate = functionalUpdate;
		  exports.loopHooks = loopHooks;
		  exports.makePropGetter = makePropGetter;
		  exports.makeRenderer = makeRenderer;
		  exports.reduceHooks = reduceHooks;
		  exports.safeUseLayoutEffect = safeUseLayoutEffect;
		  exports.useAbsoluteLayout = useAbsoluteLayout;
		  exports.useAsyncDebounce = useAsyncDebounce;
		  exports.useBlockLayout = useBlockLayout;
		  exports.useColumnOrder = useColumnOrder;
		  exports.useExpanded = useExpanded;
		  exports.useFilters = useFilters;
		  exports.useFlexLayout = useFlexLayout;
		  exports.useGetLatest = useGetLatest;
		  exports.useGlobalFilter = useGlobalFilter;
		  exports.useGridLayout = useGridLayout;
		  exports.useGroupBy = useGroupBy;
		  exports.useMountedLayoutEffect = useMountedLayoutEffect;
		  exports.usePagination = usePagination;
		  exports.useResizeColumns = useResizeColumns;
		  exports.useRowSelect = useRowSelect;
		  exports.useRowState = useRowState;
		  exports.useSortBy = useSortBy;
		  exports.useTable = useTable;

		  Object.defineProperty(exports, '__esModule', { value: true });

		})));
		
} (reactTable_development, reactTable_developmentExports));
	return reactTable_developmentExports;
}

(function (module) {
	if (process.env.NODE_ENV === 'production') {
	  module.exports = requireReactTable_production_min();
	} else {
	  module.exports = requireReactTable_development();
	}
} (reactTable));

const ganttStickyColumnShadow = {
    boxShadow: '0px 0px 4px 1px rgba(0, 0, 0, 0.15)',
    clipPath: 'inset(0px -10px 0px 0px)',
};
const ganttStickyRowShadow = {
    boxShadow: '0px -2px 4px 3px rgba(0, 0, 0, 0.2)',
    clipPath: 'inset(0px 0px -10px 0px)',
};
const GanttChartContainer = _default$1(Stack$5) `
  max-w-full
  overflow-x-auto
  pb-4
`;
const ProjectLeftBar = _default$1.div `
  absolute
  h-full
  float-left
  ${({ $isClicked }) => `${$isClicked ? 'w-3' : 'w-1'}`}
  ${({ $variant }) => getRecipeClassName($variant, 'colorBg').bg}
`;
const ProjectName = _default$1.span `
  truncate
  underline
  cursor-pointer
  text-sm
  font-medium
  ${({ $variant }) => getRecipeClassName($variant, 'whiteBg').text}
`;
const ColorBlock = _default$1.div `
  w-full
  h-full
  ${({ $highlighted, $rowIndex, $variant }) => {
    if ($highlighted) {
        return getRecipeClassName($variant, 'colorBg').bg;
    }
    return $rowIndex % 2 === 0 ? 'bg-gray-300' : 'bg-gray-100';
}}
`;
const TableEndContainer = _default$1.div `
  relative
  px-8
  overflow-hidden
`;
const TableEndTextContainer = _default$1.div `
  absolute
  top-1/2
  left-1/2
  -rotate-90
  -translate-x-1/2
  -translate-y-1/2
`;
const ProjectColumnHead = _default$1.th `
  bg-gray-50
  h-1
  sticky
  top-0
  left-0
  z-10
`;
const ProjectColumnCell = _default$1.td `
  relative
  bg-white
  m-0
  p-0
  h-1
  sticky
  top-0
  left-0
  z-10
`;
_default$1.div `
  px-6
  h-full
  flex
  items-center
`;
const ProjectNameContainer = _default$1.div `
  max-w-[300px]
  px-6
  h-full
  flex
  items-center
  border-b
  border-b-gray-200
`;

const COLUMN_ID_PROJECT = 'project';
const validateDatePeriod = (period) => !!period.startDate && !!period.endDate;
const calculateDatePeriodUnion = (periods) => {
    if (periods.length === 0) {
        throw new Error('Cannot calculate period on empty data');
    }
    const datePeriodUnion = periods.reduce((result, period) => {
        if (!period.startDate || !period.endDate) {
            return {
                startDate: result.startDate,
                endDate: result.endDate,
            };
        }
        if (!result.startDate || !result.endDate) {
            return {
                startDate: period.startDate,
                endDate: period.endDate,
            };
        }
        return {
            startDate: period.startDate < result.startDate ? period.startDate : result.startDate,
            endDate: period.endDate > result.endDate ? period.endDate : result.endDate,
        };
    }, periods[0]);
    const isValidDatePeriod = validateDatePeriod(datePeriodUnion);
    if (!isValidDatePeriod) {
        throw new Error('Cannot calculate period on invalid data');
    }
    return datePeriodUnion;
};
const makeProjectColumn = ({ clickedRowId, data, onRowClick, onRowSelect, selectedRowIds, }) => ({
    id: COLUMN_ID_PROJECT,
    accessor: (datum) => datum,
    Header: () => {
        const handleSelectAll = selectedRowIds && onRowSelect
            ? () => {
                onRowSelect(selectedRowIds.length === data.length ? [] : data.map((datum) => datum.id));
            }
            : undefined;
        return (React__default.createElement(ProjectColumnHead, null,
            React__default.createElement(Stack$5, { alignItems: "center", className: "px-6 h-full", style: ganttStickyColumnShadow },
                selectedRowIds && (React__default.createElement(Checkbox, { id: "projects", name: "projects", checked: selectedRowIds.length === data.length, onChange: handleSelectAll })),
                React__default.createElement(Typography, { as: "span", fontSize: "sm", fontWeight: "medium" }, "Projects"))));
    },
    Cell: ({ row, value }) => {
        const checked = selectedRowIds?.includes(row.original.id);
        const clicked = clickedRowId === row.original.id;
        const rowEventHandlerData = {
            rowData: row.original,
        };
        const handleClick = onRowClick
            ? () => {
                onRowClick(rowEventHandlerData);
            }
            : undefined;
        const handleSelect = selectedRowIds && onRowSelect
            ? () => {
                onRowSelect(checked ? selectedRowIds.filter((id) => id !== row.original.id) : [...selectedRowIds, row.original.id]);
            }
            : undefined;
        return (React__default.createElement(ProjectColumnCell, null,
            React__default.createElement(ProjectLeftBar, { "$isClicked": clicked, "$variant": value.variant }),
            React__default.createElement(ProjectNameContainer, { style: ganttStickyColumnShadow },
                selectedRowIds && (React__default.createElement(Checkbox, { id: row.original.id, name: row.original.name, checked: checked, onChange: handleSelect })),
                React__default.createElement(ProjectName, { "$variant": value.variant, title: value.name, onClick: handleClick }, value.name))));
    },
    minWidth: 150,
});
const makePeriodColumns = ({ endMonthYear, hoveredCellData, onCellClick, onCellHover, startMonthYear, }) => {
    const { month: startMonth, year: startYear } = startMonthYear;
    const totalMonthCount = lib.getNumberOfMonthsInPeriod({ startMonthYear, endMonthYear });
    const columns = lib.range(startMonth, startMonth + totalMonthCount, 1).map((monthIncrement) => {
        const currentMonth = monthIncrement % 12;
        const currentYear = startYear + Math.floor(monthIncrement / 12);
        // Jan - Dec
        const monthAbbreviation = lib.getMonthShortAbbreviation(currentMonth);
        const isHoveredColumn = !!hoveredCellData && hoveredCellData.year === currentYear && hoveredCellData.month === currentMonth;
        const column = {
            id: `${currentYear}-${currentMonth}`,
            accessor: (datum) => datum,
            Header: () => (React__default.createElement("th", { className: "bg-gray-50" },
                React__default.createElement(Stack$5, { direction: "column", alignItems: "center", className: "py-2 w-[60px]", style: ganttStickyRowShadow },
                    React__default.createElement(Typography, { as: "p", fontSize: "xs", className: isHoveredColumn ? '' : 'text-gray-400' }, currentYear),
                    React__default.createElement(Typography, { as: "p", fontSize: "sm", fontWeight: "medium" }, monthAbbreviation.toUpperCase())))),
            Cell: ({ column, row }) => {
                const datum = row.original;
                const startMonthYear = datum.startDate ? lib.dateToMonthYear(datum.startDate) : null;
                const endMonthYear = datum.endDate ? lib.dateToMonthYear(datum.endDate) : null;
                const isInProjectPeriod = startMonthYear &&
                    endMonthYear &&
                    lib.isMonthYearInRange({ month: currentMonth, year: currentYear }, startMonthYear, endMonthYear);
                const isClickable = isInProjectPeriod && column.id !== COLUMN_ID_PROJECT && !!onCellClick;
                const cellEventHandlerData = {
                    rowData: datum,
                    year: currentYear,
                    month: currentMonth,
                };
                const handleClick = isClickable
                    ? () => {
                        onCellClick(cellEventHandlerData);
                    }
                    : undefined;
                const handleHover = onCellHover
                    ? (isHovered) => () => {
                        onCellHover(isHovered ? cellEventHandlerData : undefined);
                    }
                    : undefined;
                return (React__default.createElement("td", { className: "m-0 p-0" },
                    React__default.createElement("div", { className: "h-9" },
                        React__default.createElement(ColorBlock, { "$as": isClickable ? 'button' : 'div', "$variant": datum.variant, "$rowIndex": row.index, "$highlighted": isInProjectPeriod, onClick: handleClick, onMouseEnter: handleHover?.(true), onMouseLeave: handleHover?.(false) }))));
            },
        };
        return column;
    });
    return columns;
};

const useColumns = (data, startMonthYear, endMonthYear, clickedRowId, selectedRowIds, onCellClick, onRowClick, onRowSelect) => {
    const [hoveredCellData, setHoveredCellData] = useState(undefined);
    const columns = useMemo(() => {
        const projectColumn = makeProjectColumn({ data, clickedRowId, selectedRowIds, onRowClick, onRowSelect });
        const columns = makePeriodColumns({
            startMonthYear,
            endMonthYear,
            hoveredCellData,
            onCellClick,
            onCellHover: setHoveredCellData,
        });
        return [projectColumn, ...columns];
    }, [
        data,
        startMonthYear,
        endMonthYear,
        clickedRowId,
        selectedRowIds,
        hoveredCellData,
        onCellClick,
        onRowClick,
        onRowSelect,
    ]);
    return columns;
};

const useDatePeriod = ({ periods, options = {} }) => {
    const { fixedEndMonthYear, fixedStartMonthYear } = options;
    return useMemo(() => {
        if (!!fixedStartMonthYear && !!fixedEndMonthYear) {
            return {
                startMonthYear: fixedStartMonthYear,
                endMonthYear: fixedEndMonthYear,
            };
        }
        const datePeriodUnion = calculateDatePeriodUnion(periods);
        const monthYearPeriodUnion = lib.toMonthYearPeriod(datePeriodUnion.startDate, datePeriodUnion.endDate);
        return {
            startMonthYear: fixedStartMonthYear ?? monthYearPeriodUnion.startMonthYear,
            endMonthYear: fixedEndMonthYear ?? monthYearPeriodUnion.endMonthYear,
        };
    }, [periods, fixedStartMonthYear, fixedEndMonthYear]);
};

const GanttChart = ({ clickedRowId, data, endMonthYear: fixedEndMonthYear, onCellClick, onRowClick, onRowSelect, selectedRowIds, startMonthYear: fixedStartMonthYear, }) => {
    const { endMonthYear, startMonthYear } = useDatePeriod({
        periods: data,
        options: { fixedStartMonthYear, fixedEndMonthYear },
    });
    const columns = useColumns(data, startMonthYear, endMonthYear, clickedRowId, selectedRowIds, onCellClick, onRowClick, onRowSelect);
    const { getTableBodyProps, getTableProps, headerGroups, prepareRow, rows } = reactTableExports.useTable({ columns, data });
    return (React__default.createElement(GanttChartContainer, null,
        React__default.createElement("table", { ...getTableProps() },
            React__default.createElement("thead", null, headerGroups.map((headerGroup) => {
                const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                return (React__default.createElement("tr", { key: key, ...restHeaderGroupProps }, headerGroup.headers.map((column, index) => column.render('Header', { key: index }))));
            })),
            React__default.createElement("tbody", { ...getTableBodyProps() }, rows.map((row) => {
                prepareRow(row);
                const { key, ...restRowProps } = row.getRowProps();
                return (React__default.createElement("tr", { key: key, ...restRowProps }, row.cells.map((cell, index) => cell.render('Cell', { key: index }))));
            }))),
        React__default.createElement(TableEndContainer, null,
            React__default.createElement(TableEndTextContainer, null,
                React__default.createElement(Typography, { as: "p", title: "END OF PERIOD", fontSize: "lg", fontWeight: "bold", color: colors$3.gray[300], className: "truncate" }, "END OF PERIOD")))));
};

const ICON_BUTTON_GROUP_COLOR_VARIANTS = {
    primary: 'bg-transparent',
    darkGray: 'bg-gray-800',
    darkGrayBorder: 'bg-gray-600 border border-gray-200',
    gray: 'bg-gray-200',
    grayBorder: 'border border-gray-200',
    lightGray: 'bg-gray-100',
    transparent: 'bg-transparent',
    inherit: '',
};
const ICON_BUTTON_GROUP_ROUNDED_VARIANTS = {
    none: '',
    regular: 'rounded-md',
    top: 'rounded-t',
    bottom: 'rounded-b',
    left: 'rounded-l',
    right: 'rounded-r',
};
const StyledIconButtonGroup = _default$1(Stack$5) `
  gap-px
  overflow-hidden
  px-1
  ${({ $variant }) => ICON_BUTTON_GROUP_COLOR_VARIANTS[$variant]}
  ${({ $rounded }) => ICON_BUTTON_GROUP_ROUNDED_VARIANTS[$rounded]}
`;

const IconButtonGroup = ({ className, iconButtons, rounded = 'regular', variant, }) => (React__default.createElement(StyledIconButtonGroup, { "$rounded": rounded, "$variant": variant, className: className }, iconButtons.map(({ active, icon, id, size, ...rest }) => (React__default.createElement(IconButton, { id: id, key: id, variant: variant, active: active, icon: icon, size: size, ...rest })))));

const useClearLocationHashOnUnmount = ({ data, unused, url } = {}) => useEffect(() => () => {
    window.history.replaceState(data ?? '', unused ?? document.title, url ?? window.location.pathname + window.location.search);
}, [data, unused, url]);

const TableOfContentsContainer = _default$1(Stack$5) `
  min-w-[280px]
  overflow-y-auto
`;
const TableOfContentsTypography = _default$1(Typography) `
  py-2
  pl-4
  border-l-2
  hover:font-medium
  ${({ $active }) => ($active ? 'border-primary-600' : 'border-transparent')}
`;
/**
 * Developer MUST set height or max-height on a parent element to make the scroll work.
 */
const ScrollContainer = _default$1(Stack$5) `
  overflow-y-scroll
`;
const HeaderTypography = _default$1(Typography) `
  sticky
  top-0
  text-gray-700
  bg-white
`;
const BodyTypography = _default$1(Typography) `
  text-gray-500
`;

/**
 * Developer MUST set height or max-height to make the y-scroll work. Can be done
 * by styling the component directly which will affect the `Stack` wrapper.
 */
const IndexedScrollContainer = ({ bodyTypographyProps, currentLocationHash, headerTypographyProps, items, scrollContainerProps, ...stackProps }) => {
    useClearLocationHashOnUnmount();
    return (React.createElement(Stack$5, { direction: "row", gap: 4, ...stackProps },
        React.createElement(TableOfContentsContainer, { direction: "column" }, items.map(({ title }) => {
            const itemHash = '#' + lib.kebabCase(title);
            const matchesLocationHash = currentLocationHash === itemHash;
            return (React.createElement(TableOfContentsTypography, { key: title, as: "a", href: itemHash, fontSize: "sm", fontWeight: matchesLocationHash ? 'medium' : undefined, "$active": matchesLocationHash }, title));
        })),
        React.createElement(ScrollContainer, { direction: "column", space: 6, ...scrollContainerProps }, items.map(({ description, title }) => (React.createElement(Stack$5, { key: title, id: lib.kebabCase(title), direction: "column" },
            React.createElement(HeaderTypography, { as: "p", fontSize: "sm", fontWeight: "medium", ...headerTypographyProps }, title),
            React.createElement(BodyTypography, { as: "p", fontSize: "sm", ...bodyTypographyProps }, description)))))));
};

const StackStyled = _default$1(Stack$5) `
  relative
`;
const Overlay = _default$1.div `
  absolute
  inset-0
  z-10
  w-full
  h-full
  flex
  justify-center
  items-center
  bg-white/[0.6]
  backdrop-saturate-0
  !m-0
`;
const LoadingStyled = _default$1(Loading) `
  z-20
`;

const LoadingOverlay = ({ children, height = 120, loading = false, ...props }) => (React.createElement(StackStyled, { direction: "column", ...props },
    children,
    loading && (React.createElement(Overlay, null,
        React.createElement(LoadingStyled, { height: height })))));

const ScreenLoadingContainer = _default$1.div `bg-gray-50 w-screen h-screen flex items-center justify-center`;

const ScreenLoading = () => (React__default.createElement(ScreenLoadingContainer, null,
    React__default.createElement(Loading, { height: 160 })));

var _path$9;
function _extends$h() { return _extends$h = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$h.apply(null, arguments); }
var SvgMark = function SvgMark(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$h({
    viewBox: "0 0 27 32",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$9 || (_path$9 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M26.686 28.9485V2.51226C26.6592 1.83693 26.3719 1.19819 25.8846 0.729872C25.3973 0.261558 24.7476 0 24.0718 0C23.3959 0 22.7462 0.261558 22.2589 0.729872C21.7716 1.19819 21.4844 1.83693 21.4575 2.51226V22.7679L17.591 17.3862C17.453 17.2115 17.2847 17.0629 17.0942 16.9475C16.8144 16.7709 16.5018 16.6527 16.1752 16.5997C16.0384 16.5783 15.8999 16.5672 15.7612 16.5667C15.569 16.5677 15.3775 16.59 15.19 16.6328C14.8674 16.7139 14.5628 16.8543 14.2917 17.0468C14.0229 17.2437 13.795 17.4912 13.6211 17.7754C13.4438 18.0563 13.3254 18.3704 13.2733 18.6986C13.2203 19.0261 13.2316 19.3606 13.3064 19.6838C13.3818 19.9533 13.5022 20.208 13.6624 20.4373L21.1843 30.7493C21.563 31.2689 22.0965 31.6552 22.7083 31.8528C23.3201 32.0503 23.9788 32.049 24.5898 31.8491C25.2008 31.649 25.7328 31.2607 26.1094 30.7396C26.486 30.2185 26.6878 29.5914 26.686 28.9485ZM0 3.03491V29.4712V29.4794C0.0268794 30.1548 0.314064 30.7935 0.80139 31.2618C1.28872 31.7302 1.93837 31.9917 2.61423 31.9917C3.29011 31.9917 3.93976 31.7302 4.42709 31.2618C4.91441 30.7935 5.20159 30.1548 5.22847 29.4794V9.23207L9.09498 14.6138C9.23306 14.7885 9.40131 14.9371 9.59175 15.0525C9.87157 15.2291 10.1842 15.3473 10.5108 15.4003C10.6477 15.4217 10.7861 15.4328 10.9247 15.4333C11.117 15.4323 11.3086 15.4101 11.496 15.3672C11.8186 15.2861 12.1232 15.1457 12.3943 14.9532C12.6632 14.7564 12.891 14.5088 13.0649 14.2245C13.2422 13.9436 13.3606 13.6296 13.4127 13.3014C13.4658 12.974 13.4545 12.6393 13.3795 12.3162C13.3041 12.0468 13.1838 11.792 13.0236 11.5627L5.5017 1.25069C5.12388 0.732242 4.59186 0.346468 3.98166 0.1485C3.37144 -0.0494672 2.71429 -0.0494994 2.10406 0.148399C1.49384 0.346298 0.961772 0.732007 0.583892 1.25041C0.206012 1.76882 0.00164186 2.3934 0 3.03491Z",
    fill: "currentColor"
  })));
};
var ForwardRef$e = /*#__PURE__*/forwardRef(SvgMark);
var Memo$e = /*#__PURE__*/memo(ForwardRef$e);

const StyledNeoTaxLogo = _default$1(Memo$e) `
${({ $animated }) => ($animated ? 'animate-flip' : '')}
  text-primary-600
  inline-block
  h-12
  w-12
`;

const Logo = ({ animated, className, href, id }) => {
    if (href) {
        return (React__default.createElement("a", { id: id, className: className, href: href, target: "_blank", rel: "noopener noreferrer" },
            React__default.createElement(StyledNeoTaxLogo, { "$animated": animated })));
    }
    return React__default.createElement(StyledNeoTaxLogo, { "$animated": animated, id: id, className: className });
};

const MonthDatePicker = ({ monthAutoFocus, monthDateAutoFocus, monthDateDisabled, monthDateError, monthDateErrorText, monthDateForm, monthDateHelpText, monthDateId, monthDateLabel, monthDateName, monthDatePlaceholder, monthDateRequired, monthDateSize, monthDateValue, monthDisabled, monthError, monthErrorText, monthForm, monthHelpText, monthId, monthLabel, monthName, monthPlaceholder, monthRequired, monthSize, monthValue, onChangeMonth, onChangeMonthDate, usePortal, ...props }) => {
    const monthDateOptions = useMonthDateOptions(monthValue);
    const handleChangeMonth = (month) => {
        onChangeMonth?.(month);
        // Always clear the month date even if the new month has the same number of days
        // Why? Avoid the problem of the user changing months and forgetting to change the month date
        // Example: Set June 30 -> Change month to July -> Forget to change month date to 31 (last day in July)
        onChangeMonthDate?.('');
    };
    return (React__default.createElement(Stack$5, { direction: "row", space: 11, ...props },
        React__default.createElement(InputFormContainer, { inputId: monthId, label: monthLabel, helpText: monthHelpText, withError: monthError },
            React__default.createElement(Select, { id: monthId, autoFocus: monthAutoFocus, disabled: monthDisabled, error: monthError, errorText: monthErrorText, form: monthForm, name: monthName, placeholder: monthPlaceholder, required: monthRequired, size: monthSize, value: monthValue, onChange: handleChangeMonth, options: monthOptions, usePortal: usePortal })),
        React__default.createElement(InputFormContainer, { inputId: monthDateId, label: monthDateLabel, helpText: monthDateHelpText, withError: monthDateError },
            React__default.createElement(Select, { id: monthDateId, autoFocus: monthDateAutoFocus, error: monthDateError, errorText: monthDateErrorText, disabled: monthDateDisabled, form: monthDateForm, name: monthDateName, placeholder: monthDatePlaceholder, required: monthDateRequired, size: monthDateSize, value: monthDateValue, onChange: onChangeMonthDate, options: monthDateOptions, usePortal: usePortal }))));
};

const NavItemContext = createContext({
    open: false,
});

const NAV_ITEM_VARIANTS = {
    light: 'text-gray-600 hover:bg-gray-400 hover:text-gray-50',
    dark: 'text-gray-300 hover:bg-primary-600 hover:bg-opacity-75 hover:text-white',
};
const NAV_ITEM_ACTIVE_AND_OPEN_VARIANTS = {
    light: 'w-full bg-gray-200 hover:bg-gray-400 text-gray-600',
    dark: 'w-full bg-gray-800 text-gray-50 hover:bg-primary-600 hover:text-gray-50',
};
const NAV_ITEM_ACTIVE_VARIANTS = {
    light: 'bg-gray-200 hover:bg-gray-400 text-gray-900',
    dark: 'bg-gray-800 text-gray-50',
};
const NAV_ITEM_OPEN_VARIANTS = {
    light: 'w-full hover:bg-gray-400 text-gray-600 hover:text-gray-50',
    dark: 'w-full hover:bg-gray-700 text-gray-300 hover:text-gray-50',
};
const NAV_ITEM_ICON_VARIANTS = {
    light: 'text-gray-400 group-hover:text-gray-50',
    dark: 'text-inherit',
};
const NAV_ITEM_TEXT_VARIANTS = {
    light: 'text-gray-900',
    dark: 'text-inherit',
};
const NavItemIcon = _default$1(Icon) `
  flex-shrink-0
  transition-colors
  ${({ $variant }) => NAV_ITEM_ICON_VARIANTS[$variant]}
  ${({ $open }) => ($open ? 'mr-3' : '')}
`;
const SideNavTitleContainer = _default$1.div `
  flex-1
  whitespace-nowrap
  text-inherit
  pr-0.5
`;
const SideNavTitle = _default$1(Typography) `
  ${({ $variant }) => NAV_ITEM_TEXT_VARIANTS[$variant]}
`;
const getNavItemStyling = ($active, $open, $variant) => {
    if ($active && $open)
        return NAV_ITEM_ACTIVE_AND_OPEN_VARIANTS[$variant];
    if ($active)
        return NAV_ITEM_ACTIVE_VARIANTS[$variant];
    if ($open)
        return NAV_ITEM_OPEN_VARIANTS[$variant];
    return NAV_ITEM_VARIANTS[$variant]; // Only reaches this state if side nav has been forced shut (no open on hover)
};
const StyledNavItemContainer = _default$1(Stack$5) `
  ${({ $open }) => ($open ? 'w-full' : '')}
`;
const StyledStack$1 = _default$1(Stack$5) `
  px-3
  py-2
  group
  flex
  items-center
  text-left
  rounded-md
  transition-[padding]
  transition-colors
  overflow-x-hidden
  focus:outline-primary-600
  ${({ $active, $open, $variant }) => getNavItemStyling($active, $open, $variant)}
`;

const NavItem = ({ active, badge, className, icon, id, title, ...restProps }) => {
    const { open, variant } = useContext(NavItemContext);
    return (React__default.createElement(StyledNavItemContainer, { "$open": open, direction: "column" },
        React__default.createElement(StyledStack$1, { id: id, "$active": active, "$open": open, "$variant": variant, className: className, ...restProps },
            icon ? React__default.createElement(NavItemIcon, { "$variant": variant, node: icon, size: 6, variant: "none", "$open": open }) : null,
            open && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(SideNavTitleContainer, { "$variant": variant },
                    React__default.createElement(SideNavTitle, { "$variant": variant, fontSize: "sm", as: "span", fontWeight: "medium" }, title)),
                badge && React__default.createElement(Badge, { ...badge }))))));
};

const Label = _default$1(Typography) `
  shrink-2
`;

const QuickActionGroup = ({ label, onClick, options, ...props }) => {
    const handleClick = (option) => (e) => {
        const handleClick = option.onClick ?? onClick;
        if (!handleClick) {
            throw new Error('Click handler is required for QuickActionGroup. Please provide component `onClick` or `option.onClick` handler.');
        }
        handleClick?.(option.value, e);
    };
    return (React__default.createElement(Stack$5, { direction: "row", alignItems: "flex-start", gap: 2, ...props },
        label && (React__default.createElement(Label, { fontSize: "sm", fontWeight: "medium", color: colors$3.gray[500] }, label)),
        React__default.createElement(Stack$5, { direction: "row", wrap: "wrap", gap: 2 }, options.map((option) => (React__default.createElement(Badge, { as: "button", key: option.value, colorVariant: "default", dot: false, onClick: handleClick(option), disabled: option.disabled }, option.label))))));
};

const StyledRadio$1 = _default$1(Radio$1) `
  checked:bg-primary-600
  accent-primary-600
  text-primary-600
  focus:ring-primary-600
  cursor-pointer
`;
const StyledLabel$2 = _default$1.label `
  cursor-pointer
`;

const Radio = ({ alignLabel = 'start', checked, className, disabled = false, gap, id, // ID for analytics
label, onChange, testId, value, }) => (React__default.createElement(Stack$5, { className: className, alignItems: alignLabel, gap: gap },
    React__default.createElement(Stack$5, { testId: testId }, disabled ? (React__default.createElement(Icon, { node: React__default.createElement(Memo$j, { className: "text-gray-400" }), size: 4, variant: "none" })) : (React__default.createElement("div", null,
        React__default.createElement(StyledRadio$1, { id: id, value: value, checked: checked, onChange: () => onChange(value), disabled: disabled })))),
    React__default.createElement(StyledLabel$2, { htmlFor: id }, label)));

const RadioGroup = ({ className, cols = 2, currentValue, gap = 2, id, onChange, options, optionsGap = 5, }) => (React__default.createElement(Grid$1, { className: className, cols: cols, gap: optionsGap, id: id }, options.map((radio) => (React__default.createElement(GridItem, { key: radio.id },
    React__default.createElement(Radio, { ...radio, onChange: onChange, checked: radio.value === currentValue, gap: gap, label: React__default.createElement(Typography, { as: "span", fontWeight: "medium", fontSize: "sm" }, radio.label) }))))));

const StyledRadio = _default$1(Radio) `
  flex
  space-x-6
  px-6
  h-14
  ${({ disabled }) => (disabled ? 'opacity-50 pointer-events-none' : '')}
`;

const RadioWithIcon = ({ checked, className, disabled = false, icon, id, label, onChange, subContent, testId, value, }) => (React__default.createElement(StyledRadio, { label: React__default.createElement(Stack$5, { alignItems: "center", space: 2 },
        React__default.createElement(Icon, { node: icon.node, size: 10, variant: icon.variant }),
        React__default.createElement(Stack$5, { direction: "column", justifyContent: "center" },
            React__default.createElement(Typography, { as: "span", fontWeight: "medium", fontSize: "sm" }, label),
            subContent)), alignLabel: "center", testId: testId, onChange: onChange, id: id, value: value, checked: checked, disabled: disabled, className: className }));

const OuterContainer = _default$1.div `
  grid
  grid-cols-2
  gap-x-2.5
`;
const InnerContainer = _default$1.div `
  ${({ $hasBottomBorder }) => ($hasBottomBorder ? 'border-b border-gray-200' : '')}
`;
const StyledRadioWithIcon = _default$1(RadioWithIcon) `
  space-x-10
`;

const RadioWithIconGroup = ({ className, currentValue, onChange, radioWithIcons, }) => {
    const amountWithoutBottomBorder = radioWithIcons.length % 2 === 0 ? 2 : 1;
    return (React__default.createElement(OuterContainer, { className: className }, radioWithIcons.map((radioWithIcon, index) => (React__default.createElement(InnerContainer, { key: radioWithIcon.id, "$hasBottomBorder": index < radioWithIcons.length - amountWithoutBottomBorder },
        React__default.createElement(StyledRadioWithIcon, { ...radioWithIcon, onChange: onChange, checked: radioWithIcon.value === currentValue }))))));
};

const StyledNavItemDiv = _default$1.div `
  ${({ $open }) => ($open ? '' : 'items-center')}
  ${({ $isFooter }) => ($isFooter ? 'py-4 px-2 bottom-0' : '')}
  ${({ $isHeader }) => ($isHeader ? 'border-t border-gray-700 py-4 px-2' : '')}
  ${({ $floatingElement }) => ($floatingElement ? '!p-0' : '')}
    flex-shrink-0
    flex
    flex-col
`;

const getTestId = (id, active) => `${id}-${active ? 'active' : 'inactive'}`;
const SideNavItem = ({ active, badge, className, icon, id, isFooter = false, isHeader = false, title, ...rest }) => (React__default.createElement(StyledNavItemDiv, { "$isFooter": isFooter, "$isHeader": isHeader, "data-testid": getTestId(id, active), className: className },
    React__default.createElement(NavItem, { id: id, active: active, icon: icon, title: title, badge: badge, ...rest })));
memo(SideNavItem);

const StyledStack = _default$1(Stack$5) `
  relative
`;
const StyledInput = _default$1(FormInput) `
  w-full
  h-4
  rounded-lg
  [-webkit-appearance:none]
`;
const StyledBottomOutput = _default$1.output `
  mt-6
  -translate-x-2/4
  absolute
`;
const StyledRightOutput = _default$1(Stack$5) `
  w-[45px]
`;
const StyledTypography = _default$1(Typography) `
  text-gray-300
`;

const variantColors = {
    default: [colors$3.gray[800], colors$3.gray[400]],
    active: [colors$3.primary[600], colors$3.gray[400]],
    deemphasized: [colors$3.gray[400], colors$3.gray[300]],
};
const getPercentageFromLeft = ({ max, min, value }) => ((value - min) * 100) / (max - min);
// Used with "left" property for slider selector
const getLeftPositionCss = (value, min, max) => {
    const leftPercentage = getPercentageFromLeft({ value, min, max });
    const newPosition = 10 - leftPercentage * 0.2;
    return `calc(${leftPercentage}% + (${newPosition}px))`;
};
const getSliderGradient = (variant, value, min, max, isDisabled, isFocused) => {
    const leftPercentage = getPercentageFromLeft({ value, min, max });
    const bgColors = isFocused ? variantColors['active'] : variantColors[variant];
    const gradientColors = isDisabled
        ? [
            [colors$3.gray[300], leftPercentage],
            [colors$3.gray[300], leftPercentage],
        ]
        : [
            [bgColors[0], leftPercentage],
            [bgColors[1], leftPercentage],
        ];
    return {
        direction: 'to right',
        colors: gradientColors,
    };
};
const Slider = ({ backgroundColors, border, className, disabled = false, id, max = 100, min = 0, name, onChange, step, value = 0, valuePosition = 'right', variant = 'default', }) => {
    const [leftCss, setLeftCss] = useState(getLeftPositionCss(value, min, max));
    const [isFocused, setIsFocused] = useState(false);
    useEffect(() => {
        setLeftCss(getLeftPositionCss(value, min, max));
    }, [max, min, value]);
    const sliderGradient = !backgroundColors || disabled ? getSliderGradient(variant, value, min, max, disabled, isFocused) : backgroundColors;
    const backgroundImage = createLinearGradientCss(sliderGradient);
    return (React__default.createElement(StyledStack, { gap: 1, alignItems: valuePosition === 'right' && 'center' },
        React__default.createElement(StyledInput, { "data-testid": "slider", disabled: disabled, min: min, max: max, step: step, id: id, name: name, onChange: onChange, onFocus: (e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    // Not triggered when swapping focus between children
                    setIsFocused(true);
                }
            }, onBlur: (e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    // Not triggered when swapping focus between children
                    setIsFocused(false);
                }
            }, type: "range", value: value, className: cn('input-slider', '[&::-webkit-slider-thumb]:bg-white', { 'border border-gray-300 shadow-sm': !backgroundColors || border, 'input-slider-disabled': disabled }, className), style: { backgroundImage } }),
        valuePosition === 'right' && (React__default.createElement(StyledRightOutput, { as: "output", alignItems: "center", justifyContent: "end", "data-testid": "output" },
            React__default.createElement(Typography, { fontWeight: "medium" }, value),
            React__default.createElement(StyledTypography, null, "%"))),
        valuePosition === 'bottom' && (React__default.createElement(StyledBottomOutput, { "data-testid": "output", style: { left: leftCss } },
            React__default.createElement(Badge, { colorVariant: backgroundColors ? 'blue' : 'default' },
                value,
                "%")))));
};
const SliderMemo = memo(Slider);

const ToastDurationOptions = {
    /** Usage: Success messages that may come in quick succession. */
    SHORT: 1500,
    /** Usage: Typical message w/ title and description. */
    MEDIUM: 3000,
    /** Usage: Message w/ long description. */
    LONG: 6000,
    /** Usage: Message we want to ensure the user sees (e.g. errors or notification with immediate CTA). */
    INFINITE: -1,
};
const DEFAULT_TOAST_DURATION = ToastDurationOptions.MEDIUM;
/**
 * DEFAULT_TOAST_ERROR_DURATION was -1 but set to 5000 until we resolve pointer event issues when notifications open on top of sidedrawer in trouser.
 * Ticket: https://linear.app/neo-tax/issue/NEO-2999/fix-toast-notification-pointer-event-issues-when-trouser-side-drawer
 */
const DEFAULT_TOAST_ERROR_DURATION = ToastDurationOptions.MEDIUM;
/**
 * The delay before the toast is removed from the DOM after the close animation has completed.
 */
const ON_CLOSE_DELAY = 1000;

const ToastContainer = _default$1.div `
  w-full
  max-w-sm
  overflow-hidden
  rounded-lg
  p-4
  flex
  items-start
  space-x-3
  bg-white
  shadow-lg
  ring-1
  ring-black
  ring-opacity-5
  pointer-events-auto
`;
const ToastContent = _default$1.div `
  w-0
  flex-1
  pt-0.5
  flex
  flex-col
  space-y-1
`;
_default$1.div `
  flex
  items-center
  flex-shrink-0
  h-6
`;
_default$1.button `
  inline-flex
  rounded-md
  bg-white
  text-gray-400
  hover:text-gray-500
  focus:outline-none
  focus:ring-2
  focus:ring-blue-500
  focus:ring-offset-2
`;
const ToastRegionContainer = _default$1.div `
  fixed
  z-notification
  inset-0
  flex
  items-end
  px-4
  py-6
  pointer-events-none
  sm:items-start
  sm:p-6
`;
const ToastRegionContent = _default$1.div `
  flex
  w-full
  flex-col
  items-center
  space-y-4
  sm:items-end
`;

const getIconBySeverity = (severity) => {
    switch (severity) {
        case 'error':
            return React.createElement(Memo$i, { "data-testid": "error-circle-icon", "aria-hidden": "true" });
        case 'info':
            return React.createElement(Memo$h, { "data-testid": "info-circle-icon", "aria-hidden": "true" });
        case 'success':
            return React.createElement(Memo$g, { "data-testid": "success-circle-icon", "aria-hidden": "true" });
        case 'warning':
            return React.createElement(Memo$f, { "data-testid": "warning-triangle-icon", "aria-hidden": "true" });
        default:
            return;
    }
};
const getTimeoutByDuration = (duration, severity) => {
    if (typeof duration === 'number') {
        return duration;
    }
    if (duration !== undefined) {
        return ToastDurationOptions[duration];
    }
    return severity === 'error' ? DEFAULT_TOAST_ERROR_DURATION : DEFAULT_TOAST_DURATION;
};

/** Dynamically insert this into the notification region when it needs to be displayed. */
const Toast = ({ actions = [], description, duration, icon, onClose, severity = 'info', title, }) => {
    const [show, setShow] = React.useState(true);
    const timeoutRef = React.useRef(null);
    const finalIcon = icon ?? getIconBySeverity(severity);
    const handleClose = () => {
        setShow(false);
        if (onClose) {
            setTimeout(() => onClose(), ON_CLOSE_DELAY);
        }
    };
    const cancelTimeout = () => {
        // No-op until we can resolve the pointer event issues when notifications open on top of sidedrawer in trouser.
        // if (timeoutRef.current) {
        //   clearTimeout(timeoutRef.current);
        //   timeoutRef.current = null;
        // }
    };
    React.useEffect(() => {
        const timeout = getTimeoutByDuration(duration, severity);
        if (timeout < 0) {
            return;
        }
        timeoutRef.current = setTimeout(() => {
            handleClose();
        }, timeout);
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement(We$1, { appear: true, show: show, as: React.Fragment, enter: "transform transition ease-out duration-300", enterFrom: "translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2", enterTo: "translate-y-0 opacity-100 sm:translate-x-0", leave: "transform transition ease-in duration-300", leaveFrom: "opacity-100 min-h-full", leaveTo: "opacity-0 min-h-0" },
        React.createElement(ToastContainer, { onMouseEnter: cancelTimeout },
            !!finalIcon && (React.createElement(Icon, { node: finalIcon, nodeSize: 4.5, size: 6, colorVariant: severity === 'neutral' ? 'default' : severity, className: "flex-shrink-0", "aria-hidden": "true" })),
            React.createElement(ToastContent, null,
                React.createElement(Typography, { as: "p", fontSize: "sm", fontWeight: "medium" }, title),
                !!description &&
                    (typeof description === 'string' || typeof description === 'number' ? (React.createElement(Typography, { as: "p", fontSize: "sm", className: "text-gray-500" }, description)) : (description)),
                actions?.length > 0 && (React.createElement(Stack$5, { space: 2 }, actions.map(({ label, ...props }) => (React.createElement(Button, { key: label, size: "sm", variant: severity === 'none' || severity === 'neutral' ? 'info' : severity, rounded: "regular", className: "m-0", ...props }, label)))))))));
};

/** Global notification region. Render this permanently at the end of the document. */
const ToastRegion = ({ toastConfigs = [] }) => (React.createElement(ToastRegionContainer, { "aria-live": "assertive" },
    React.createElement(ToastRegionContent, null, toastConfigs.map((config) => (React.createElement(Toast, { key: config.id, ...config }))))));

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var COMMON_MIME_TYPES = new Map([
    // https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
    ['aac', 'audio/aac'],
    ['abw', 'application/x-abiword'],
    ['arc', 'application/x-freearc'],
    ['avif', 'image/avif'],
    ['avi', 'video/x-msvideo'],
    ['azw', 'application/vnd.amazon.ebook'],
    ['bin', 'application/octet-stream'],
    ['bmp', 'image/bmp'],
    ['bz', 'application/x-bzip'],
    ['bz2', 'application/x-bzip2'],
    ['cda', 'application/x-cdf'],
    ['csh', 'application/x-csh'],
    ['css', 'text/css'],
    ['csv', 'text/csv'],
    ['doc', 'application/msword'],
    ['docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    ['eot', 'application/vnd.ms-fontobject'],
    ['epub', 'application/epub+zip'],
    ['gz', 'application/gzip'],
    ['gif', 'image/gif'],
    ['heic', 'image/heic'],
    ['heif', 'image/heif'],
    ['htm', 'text/html'],
    ['html', 'text/html'],
    ['ico', 'image/vnd.microsoft.icon'],
    ['ics', 'text/calendar'],
    ['jar', 'application/java-archive'],
    ['jpeg', 'image/jpeg'],
    ['jpg', 'image/jpeg'],
    ['js', 'text/javascript'],
    ['json', 'application/json'],
    ['jsonld', 'application/ld+json'],
    ['mid', 'audio/midi'],
    ['midi', 'audio/midi'],
    ['mjs', 'text/javascript'],
    ['mp3', 'audio/mpeg'],
    ['mp4', 'video/mp4'],
    ['mpeg', 'video/mpeg'],
    ['mpkg', 'application/vnd.apple.installer+xml'],
    ['odp', 'application/vnd.oasis.opendocument.presentation'],
    ['ods', 'application/vnd.oasis.opendocument.spreadsheet'],
    ['odt', 'application/vnd.oasis.opendocument.text'],
    ['oga', 'audio/ogg'],
    ['ogv', 'video/ogg'],
    ['ogx', 'application/ogg'],
    ['opus', 'audio/opus'],
    ['otf', 'font/otf'],
    ['png', 'image/png'],
    ['pdf', 'application/pdf'],
    ['php', 'application/x-httpd-php'],
    ['ppt', 'application/vnd.ms-powerpoint'],
    ['pptx', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    ['rar', 'application/vnd.rar'],
    ['rtf', 'application/rtf'],
    ['sh', 'application/x-sh'],
    ['svg', 'image/svg+xml'],
    ['swf', 'application/x-shockwave-flash'],
    ['tar', 'application/x-tar'],
    ['tif', 'image/tiff'],
    ['tiff', 'image/tiff'],
    ['ts', 'video/mp2t'],
    ['ttf', 'font/ttf'],
    ['txt', 'text/plain'],
    ['vsd', 'application/vnd.visio'],
    ['wav', 'audio/wav'],
    ['weba', 'audio/webm'],
    ['webm', 'video/webm'],
    ['webp', 'image/webp'],
    ['woff', 'font/woff'],
    ['woff2', 'font/woff2'],
    ['xhtml', 'application/xhtml+xml'],
    ['xls', 'application/vnd.ms-excel'],
    ['xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    ['xml', 'application/xml'],
    ['xul', 'application/vnd.mozilla.xul+xml'],
    ['zip', 'application/zip'],
    ['7z', 'application/x-7z-compressed'],
    // Others
    ['mkv', 'video/x-matroska'],
    ['mov', 'video/quicktime'],
    ['msg', 'application/vnd.ms-outlook']
]);
function toFileWithPath(file, path) {
    var f = withMimeType(file);
    if (typeof f.path !== 'string') { // on electron, path is already set to the absolute path
        var webkitRelativePath = file.webkitRelativePath;
        Object.defineProperty(f, 'path', {
            value: typeof path === 'string'
                ? path
                // If <input webkitdirectory> is set,
                // the File will have a {webkitRelativePath} property
                // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/webkitdirectory
                : typeof webkitRelativePath === 'string' && webkitRelativePath.length > 0
                    ? webkitRelativePath
                    : file.name,
            writable: false,
            configurable: false,
            enumerable: true
        });
    }
    return f;
}
function withMimeType(file) {
    var name = file.name;
    var hasExtension = name && name.lastIndexOf('.') !== -1;
    if (hasExtension && !file.type) {
        var ext = name.split('.')
            .pop().toLowerCase();
        var type = COMMON_MIME_TYPES.get(ext);
        if (type) {
            Object.defineProperty(file, 'type', {
                value: type,
                writable: false,
                configurable: false,
                enumerable: true
            });
        }
    }
    return file;
}

var FILES_TO_IGNORE = [
    // Thumbnail cache files for macOS and Windows
    '.DS_Store',
    'Thumbs.db' // Windows
];
/**
 * Convert a DragEvent's DataTrasfer object to a list of File objects
 * NOTE: If some of the items are folders,
 * everything will be flattened and placed in the same list but the paths will be kept as a {path} property.
 *
 * EXPERIMENTAL: A list of https://developer.mozilla.org/en-US/docs/Web/API/FileSystemHandle objects can also be passed as an arg
 * and a list of File objects will be returned.
 *
 * @param evt
 */
function fromEvent(evt) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if (isObject$b(evt) && isDataTransfer(evt.dataTransfer)) {
                return [2 /*return*/, getDataTransferFiles(evt.dataTransfer, evt.type)];
            }
            else if (isChangeEvt(evt)) {
                return [2 /*return*/, getInputFiles(evt)];
            }
            else if (Array.isArray(evt) && evt.every(function (item) { return 'getFile' in item && typeof item.getFile === 'function'; })) {
                return [2 /*return*/, getFsHandleFiles(evt)];
            }
            return [2 /*return*/, []];
        });
    });
}
function isDataTransfer(value) {
    return isObject$b(value);
}
function isChangeEvt(value) {
    return isObject$b(value) && isObject$b(value.target);
}
function isObject$b(v) {
    return typeof v === 'object' && v !== null;
}
function getInputFiles(evt) {
    return fromList(evt.target.files).map(function (file) { return toFileWithPath(file); });
}
// Ee expect each handle to be https://developer.mozilla.org/en-US/docs/Web/API/FileSystemFileHandle
function getFsHandleFiles(handles) {
    return __awaiter(this, void 0, void 0, function () {
        var files;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, Promise.all(handles.map(function (h) { return h.getFile(); }))];
                case 1:
                    files = _a.sent();
                    return [2 /*return*/, files.map(function (file) { return toFileWithPath(file); })];
            }
        });
    });
}
function getDataTransferFiles(dt, type) {
    return __awaiter(this, void 0, void 0, function () {
        var items, files;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!dt.items) return [3 /*break*/, 2];
                    items = fromList(dt.items)
                        .filter(function (item) { return item.kind === 'file'; });
                    // According to https://html.spec.whatwg.org/multipage/dnd.html#dndevents,
                    // only 'dragstart' and 'drop' has access to the data (source node)
                    if (type !== 'drop') {
                        return [2 /*return*/, items];
                    }
                    return [4 /*yield*/, Promise.all(items.map(toFilePromises))];
                case 1:
                    files = _a.sent();
                    return [2 /*return*/, noIgnoredFiles(flatten$2(files))];
                case 2: return [2 /*return*/, noIgnoredFiles(fromList(dt.files)
                        .map(function (file) { return toFileWithPath(file); }))];
            }
        });
    });
}
function noIgnoredFiles(files) {
    return files.filter(function (file) { return FILES_TO_IGNORE.indexOf(file.name) === -1; });
}
// IE11 does not support Array.from()
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/from#Browser_compatibility
// https://developer.mozilla.org/en-US/docs/Web/API/FileList
// https://developer.mozilla.org/en-US/docs/Web/API/DataTransferItemList
function fromList(items) {
    if (items === null) {
        return [];
    }
    var files = [];
    // tslint:disable: prefer-for-of
    for (var i = 0; i < items.length; i++) {
        var file = items[i];
        files.push(file);
    }
    return files;
}
// https://developer.mozilla.org/en-US/docs/Web/API/DataTransferItem
function toFilePromises(item) {
    if (typeof item.webkitGetAsEntry !== 'function') {
        return fromDataTransferItem(item);
    }
    var entry = item.webkitGetAsEntry();
    // Safari supports dropping an image node from a different window and can be retrieved using
    // the DataTransferItem.getAsFile() API
    // NOTE: FileSystemEntry.file() throws if trying to get the file
    if (entry && entry.isDirectory) {
        return fromDirEntry(entry);
    }
    return fromDataTransferItem(item);
}
function flatten$2(items) {
    return items.reduce(function (acc, files) { return __spreadArray(__spreadArray([], __read(acc), false), __read((Array.isArray(files) ? flatten$2(files) : [files])), false); }, []);
}
function fromDataTransferItem(item) {
    var file = item.getAsFile();
    if (!file) {
        return Promise.reject("".concat(item, " is not a File"));
    }
    var fwp = toFileWithPath(file);
    return Promise.resolve(fwp);
}
// https://developer.mozilla.org/en-US/docs/Web/API/FileSystemEntry
function fromEntry(entry) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, entry.isDirectory ? fromDirEntry(entry) : fromFileEntry(entry)];
        });
    });
}
// https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryEntry
function fromDirEntry(entry) {
    var reader = entry.createReader();
    return new Promise(function (resolve, reject) {
        var entries = [];
        function readEntries() {
            var _this = this;
            // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryEntry/createReader
            // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryReader/readEntries
            reader.readEntries(function (batch) { return __awaiter(_this, void 0, void 0, function () {
                var files, err_1, items;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            if (!!batch.length) return [3 /*break*/, 5];
                            _a.label = 1;
                        case 1:
                            _a.trys.push([1, 3, , 4]);
                            return [4 /*yield*/, Promise.all(entries)];
                        case 2:
                            files = _a.sent();
                            resolve(files);
                            return [3 /*break*/, 4];
                        case 3:
                            err_1 = _a.sent();
                            reject(err_1);
                            return [3 /*break*/, 4];
                        case 4: return [3 /*break*/, 6];
                        case 5:
                            items = Promise.all(batch.map(fromEntry));
                            entries.push(items);
                            // Continue reading
                            readEntries();
                            _a.label = 6;
                        case 6: return [2 /*return*/];
                    }
                });
            }); }, function (err) {
                reject(err);
            });
        }
        readEntries();
    });
}
// https://developer.mozilla.org/en-US/docs/Web/API/FileSystemFileEntry
function fromFileEntry(entry) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, new Promise(function (resolve, reject) {
                    entry.file(function (file) {
                        var fwp = toFileWithPath(file, entry.fullPath);
                        resolve(fwp);
                    }, function (err) {
                        reject(err);
                    });
                })];
        });
    });
}

var _default = function (file, acceptedFiles) {
  if (file && acceptedFiles) {
    var acceptedFilesArray = Array.isArray(acceptedFiles) ? acceptedFiles : acceptedFiles.split(',');
    var fileName = file.name || '';
    var mimeType = (file.type || '').toLowerCase();
    var baseMimeType = mimeType.replace(/\/.*$/, '');
    return acceptedFilesArray.some(function (type) {
      var validType = type.trim().toLowerCase();

      if (validType.charAt(0) === '.') {
        return fileName.toLowerCase().endsWith(validType);
      } else if (validType.endsWith('/*')) {
        // This is something like a image/* mime type
        return baseMimeType === validType.replace(/\/.*$/, '');
      }

      return mimeType === validType;
    });
  }

  return true;
};

function _toConsumableArray$1(arr) { return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$1(arr) || _nonIterableSpread$1(); }

function _nonIterableSpread$1() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray$1(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$1(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$1(arr); }

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) { _defineProperty$2(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$2(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray$1(arr, i) { return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest$1(); }

function _nonIterableRest$1() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$1(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$1(arr) { if (Array.isArray(arr)) return arr; }

var FILE_INVALID_TYPE = "file-invalid-type";
var FILE_TOO_LARGE = "file-too-large";
var FILE_TOO_SMALL = "file-too-small";
var TOO_MANY_FILES = "too-many-files";

var getInvalidTypeRejectionErr = function getInvalidTypeRejectionErr(accept) {
  accept = Array.isArray(accept) && accept.length === 1 ? accept[0] : accept;
  var messageSuffix = Array.isArray(accept) ? "one of ".concat(accept.join(", ")) : accept;
  return {
    code: FILE_INVALID_TYPE,
    message: "File type must be ".concat(messageSuffix)
  };
};
var getTooLargeRejectionErr = function getTooLargeRejectionErr(maxSize) {
  return {
    code: FILE_TOO_LARGE,
    message: "File is larger than ".concat(maxSize, " ").concat(maxSize === 1 ? "byte" : "bytes")
  };
};
var getTooSmallRejectionErr = function getTooSmallRejectionErr(minSize) {
  return {
    code: FILE_TOO_SMALL,
    message: "File is smaller than ".concat(minSize, " ").concat(minSize === 1 ? "byte" : "bytes")
  };
};
var TOO_MANY_FILES_REJECTION = {
  code: TOO_MANY_FILES,
  message: "Too many files"
}; // Firefox versions prior to 53 return a bogus MIME type for every file drag, so dragovers with
// that MIME type will always be accepted

function fileAccepted(file, accept) {
  var isAcceptable = file.type === "application/x-moz-file" || _default(file, accept);
  return [isAcceptable, isAcceptable ? null : getInvalidTypeRejectionErr(accept)];
}
function fileMatchSize(file, minSize, maxSize) {
  if (isDefined$1(file.size)) {
    if (isDefined$1(minSize) && isDefined$1(maxSize)) {
      if (file.size > maxSize) return [false, getTooLargeRejectionErr(maxSize)];
      if (file.size < minSize) return [false, getTooSmallRejectionErr(minSize)];
    } else if (isDefined$1(minSize) && file.size < minSize) return [false, getTooSmallRejectionErr(minSize)];else if (isDefined$1(maxSize) && file.size > maxSize) return [false, getTooLargeRejectionErr(maxSize)];
  }

  return [true, null];
}

function isDefined$1(value) {
  return value !== undefined && value !== null;
}
/**
 *
 * @param {object} options
 * @param {File[]} options.files
 * @param {string|string[]} [options.accept]
 * @param {number} [options.minSize]
 * @param {number} [options.maxSize]
 * @param {boolean} [options.multiple]
 * @param {number} [options.maxFiles]
 * @param {(f: File) => FileError|FileError[]|null} [options.validator]
 * @returns
 */


function allFilesAccepted(_ref) {
  var files = _ref.files,
      accept = _ref.accept,
      minSize = _ref.minSize,
      maxSize = _ref.maxSize,
      multiple = _ref.multiple,
      maxFiles = _ref.maxFiles,
      validator = _ref.validator;

  if (!multiple && files.length > 1 || multiple && maxFiles >= 1 && files.length > maxFiles) {
    return false;
  }

  return files.every(function (file) {
    var _fileAccepted = fileAccepted(file, accept),
        _fileAccepted2 = _slicedToArray$1(_fileAccepted, 1),
        accepted = _fileAccepted2[0];

    var _fileMatchSize = fileMatchSize(file, minSize, maxSize),
        _fileMatchSize2 = _slicedToArray$1(_fileMatchSize, 1),
        sizeMatch = _fileMatchSize2[0];

    var customErrors = validator ? validator(file) : null;
    return accepted && sizeMatch && !customErrors;
  });
} // React's synthetic events has event.isPropagationStopped,
// but to remain compatibility with other libs (Preact) fall back
// to check event.cancelBubble

function isPropagationStopped(event) {
  if (typeof event.isPropagationStopped === "function") {
    return event.isPropagationStopped();
  } else if (typeof event.cancelBubble !== "undefined") {
    return event.cancelBubble;
  }

  return false;
}
function isEvtWithFiles(event) {
  if (!event.dataTransfer) {
    return !!event.target && !!event.target.files;
  } // https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer/types
  // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Recommended_drag_types#file


  return Array.prototype.some.call(event.dataTransfer.types, function (type) {
    return type === "Files" || type === "application/x-moz-file";
  });
}

function onDocumentDragOver(event) {
  event.preventDefault();
}

function isIe(userAgent) {
  return userAgent.indexOf("MSIE") !== -1 || userAgent.indexOf("Trident/") !== -1;
}

function isEdge(userAgent) {
  return userAgent.indexOf("Edge/") !== -1;
}

function isIeOrEdge() {
  var userAgent = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : window.navigator.userAgent;
  return isIe(userAgent) || isEdge(userAgent);
}
/**
 * This is intended to be used to compose event handlers
 * They are executed in order until one of them calls `event.isPropagationStopped()`.
 * Note that the check is done on the first invoke too,
 * meaning that if propagation was stopped before invoking the fns,
 * no handlers will be executed.
 *
 * @param {Function} fns the event hanlder functions
 * @return {Function} the event handler to add to an element
 */

function composeEventHandlers() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return function (event) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }

    return fns.some(function (fn) {
      if (!isPropagationStopped(event) && fn) {
        fn.apply(void 0, [event].concat(args));
      }

      return isPropagationStopped(event);
    });
  };
}
/**
 * canUseFileSystemAccessAPI checks if the [File System Access API](https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API)
 * is supported by the browser.
 * @returns {boolean}
 */

function canUseFileSystemAccessAPI() {
  return "showOpenFilePicker" in window;
}
/**
 * Convert the `{accept}` dropzone prop to the
 * `{types}` option for https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker
 *
 * @param {AcceptProp} accept
 * @returns {{accept: string[]}[]}
 */

function pickerOptionsFromAccept(accept) {
  if (isDefined$1(accept)) {
    var acceptForPicker = Object.entries(accept).filter(function (_ref2) {
      var _ref3 = _slicedToArray$1(_ref2, 2),
          mimeType = _ref3[0],
          ext = _ref3[1];

      var ok = true;

      if (!isMIMEType(mimeType)) {
        console.warn("Skipped \"".concat(mimeType, "\" because it is not a valid MIME type. Check https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types for a list of valid MIME types."));
        ok = false;
      }

      if (!Array.isArray(ext) || !ext.every(isExt)) {
        console.warn("Skipped \"".concat(mimeType, "\" because an invalid file extension was provided."));
        ok = false;
      }

      return ok;
    }).reduce(function (agg, _ref4) {
      var _ref5 = _slicedToArray$1(_ref4, 2),
          mimeType = _ref5[0],
          ext = _ref5[1];

      return _objectSpread$1(_objectSpread$1({}, agg), {}, _defineProperty$2({}, mimeType, ext));
    }, {});
    return [{
      // description is required due to https://crbug.com/1264708
      description: "Files",
      accept: acceptForPicker
    }];
  }

  return accept;
}
/**
 * Convert the `{accept}` dropzone prop to an array of MIME types/extensions.
 * @param {AcceptProp} accept
 * @returns {string}
 */

function acceptPropAsAcceptAttr(accept) {
  if (isDefined$1(accept)) {
    return Object.entries(accept).reduce(function (a, _ref6) {
      var _ref7 = _slicedToArray$1(_ref6, 2),
          mimeType = _ref7[0],
          ext = _ref7[1];

      return [].concat(_toConsumableArray$1(a), [mimeType], _toConsumableArray$1(ext));
    }, []) // Silently discard invalid entries as pickerOptionsFromAccept warns about these
    .filter(function (v) {
      return isMIMEType(v) || isExt(v);
    }).join(",");
  }

  return undefined;
}
/**
 * Check if v is an exception caused by aborting a request (e.g window.showOpenFilePicker()).
 *
 * See https://developer.mozilla.org/en-US/docs/Web/API/DOMException.
 * @param {any} v
 * @returns {boolean} True if v is an abort exception.
 */

function isAbort(v) {
  return v instanceof DOMException && (v.name === "AbortError" || v.code === v.ABORT_ERR);
}
/**
 * Check if v is a security error.
 *
 * See https://developer.mozilla.org/en-US/docs/Web/API/DOMException.
 * @param {any} v
 * @returns {boolean} True if v is a security error.
 */

function isSecurityError(v) {
  return v instanceof DOMException && (v.name === "SecurityError" || v.code === v.SECURITY_ERR);
}
/**
 * Check if v is a MIME type string.
 *
 * See accepted format: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#unique_file_type_specifiers.
 *
 * @param {string} v
 */

function isMIMEType(v) {
  return v === "audio/*" || v === "video/*" || v === "image/*" || v === "text/*" || /\w+\/[-+.\w]+/g.test(v);
}
/**
 * Check if v is a file extension.
 * @param {string} v
 */

function isExt(v) {
  return /^.*\.[\w]+$/.test(v);
}
/**
 * @typedef {Object.<string, string[]>} AcceptProp
 */

/**
 * @typedef {object} FileError
 * @property {string} message
 * @property {ErrorCode|string} code
 */

/**
 * @typedef {"file-invalid-type"|"file-too-large"|"file-too-small"|"too-many-files"} ErrorCode
 */

var _excluded$4 = ["children"],
    _excluded2 = ["open"],
    _excluded3 = ["refKey", "role", "onKeyDown", "onFocus", "onBlur", "onClick", "onDragEnter", "onDragOver", "onDragLeave", "onDrop"],
    _excluded4 = ["refKey", "onChange", "onClick"];

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty$1(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$2(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$2(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
/**
 * Convenience wrapper component for the `useDropzone` hook
 *
 * ```jsx
 * <Dropzone>
 *   {({getRootProps, getInputProps}) => (
 *     <div {...getRootProps()}>
 *       <input {...getInputProps()} />
 *       <p>Drag 'n' drop some files here, or click to select files</p>
 *     </div>
 *   )}
 * </Dropzone>
 * ```
 */

var Dropzone = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      params = _objectWithoutProperties(_ref, _excluded$4);

  var _useDropzone = useDropzone(params),
      open = _useDropzone.open,
      props = _objectWithoutProperties(_useDropzone, _excluded2);

  useImperativeHandle(ref, function () {
    return {
      open: open
    };
  }, [open]); // TODO: Figure out why react-styleguidist cannot create docs if we don't return a jsx element

  return /*#__PURE__*/React__default.createElement(Fragment, null, children(_objectSpread(_objectSpread({}, props), {}, {
    open: open
  })));
});
Dropzone.displayName = "Dropzone"; // Add default props for react-docgen

var defaultProps = {
  disabled: false,
  getFilesFromEvent: fromEvent,
  maxSize: Infinity,
  minSize: 0,
  multiple: true,
  maxFiles: 0,
  preventDropOnDocument: true,
  noClick: false,
  noKeyboard: false,
  noDrag: false,
  noDragEventsBubbling: false,
  validator: null,
  useFsAccessApi: true,
  autoFocus: false
};
Dropzone.defaultProps = defaultProps;
Dropzone.propTypes = {
  /**
   * Render function that exposes the dropzone state and prop getter fns
   *
   * @param {object} params
   * @param {Function} params.getRootProps Returns the props you should apply to the root drop container you render
   * @param {Function} params.getInputProps Returns the props you should apply to hidden file input you render
   * @param {Function} params.open Open the native file selection dialog
   * @param {boolean} params.isFocused Dropzone area is in focus
   * @param {boolean} params.isFileDialogActive File dialog is opened
   * @param {boolean} params.isDragActive Active drag is in progress
   * @param {boolean} params.isDragAccept Dragged files are accepted
   * @param {boolean} params.isDragReject Some dragged files are rejected
   * @param {File[]} params.acceptedFiles Accepted files
   * @param {FileRejection[]} params.fileRejections Rejected files and why they were rejected
   */
  children: propTypesExports.func,

  /**
   * Set accepted file types.
   * Checkout https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker types option for more information.
   * Keep in mind that mime type determination is not reliable across platforms. CSV files,
   * for example, are reported as text/plain under macOS but as application/vnd.ms-excel under
   * Windows. In some cases there might not be a mime type set at all (https://github.com/react-dropzone/react-dropzone/issues/276).
   */
  accept: propTypesExports.objectOf(propTypesExports.arrayOf(propTypesExports.string)),

  /**
   * Allow drag 'n' drop (or selection from the file dialog) of multiple files
   */
  multiple: propTypesExports.bool,

  /**
   * If false, allow dropped items to take over the current browser window
   */
  preventDropOnDocument: propTypesExports.bool,

  /**
   * If true, disables click to open the native file selection dialog
   */
  noClick: propTypesExports.bool,

  /**
   * If true, disables SPACE/ENTER to open the native file selection dialog.
   * Note that it also stops tracking the focus state.
   */
  noKeyboard: propTypesExports.bool,

  /**
   * If true, disables drag 'n' drop
   */
  noDrag: propTypesExports.bool,

  /**
   * If true, stops drag event propagation to parents
   */
  noDragEventsBubbling: propTypesExports.bool,

  /**
   * Minimum file size (in bytes)
   */
  minSize: propTypesExports.number,

  /**
   * Maximum file size (in bytes)
   */
  maxSize: propTypesExports.number,

  /**
   * Maximum accepted number of files
   * The default value is 0 which means there is no limitation to how many files are accepted.
   */
  maxFiles: propTypesExports.number,

  /**
   * Enable/disable the dropzone
   */
  disabled: propTypesExports.bool,

  /**
   * Use this to provide a custom file aggregator
   *
   * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
   */
  getFilesFromEvent: propTypesExports.func,

  /**
   * Cb for when closing the file dialog with no selection
   */
  onFileDialogCancel: propTypesExports.func,

  /**
   * Cb for when opening the file dialog
   */
  onFileDialogOpen: propTypesExports.func,

  /**
   * Set to true to use the https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API
   * to open the file picker instead of using an `<input type="file">` click event.
   */
  useFsAccessApi: propTypesExports.bool,

  /**
   * Set to true to focus the root element on render
   */
  autoFocus: propTypesExports.bool,

  /**
   * Cb for when the `dragenter` event occurs.
   *
   * @param {DragEvent} event
   */
  onDragEnter: propTypesExports.func,

  /**
   * Cb for when the `dragleave` event occurs
   *
   * @param {DragEvent} event
   */
  onDragLeave: propTypesExports.func,

  /**
   * Cb for when the `dragover` event occurs
   *
   * @param {DragEvent} event
   */
  onDragOver: propTypesExports.func,

  /**
   * Cb for when the `drop` event occurs.
   * Note that this callback is invoked after the `getFilesFromEvent` callback is done.
   *
   * Files are accepted or rejected based on the `accept`, `multiple`, `minSize` and `maxSize` props.
   * `accept` must be a valid [MIME type](http://www.iana.org/assignments/media-types/media-types.xhtml) according to [input element specification](https://www.w3.org/wiki/HTML/Elements/input/file) or a valid file extension.
   * If `multiple` is set to false and additional files are dropped,
   * all files besides the first will be rejected.
   * Any file which does not have a size in the [`minSize`, `maxSize`] range, will be rejected as well.
   *
   * Note that the `onDrop` callback will always be invoked regardless if the dropped files were accepted or rejected.
   * If you'd like to react to a specific scenario, use the `onDropAccepted`/`onDropRejected` props.
   *
   * `onDrop` will provide you with an array of [File](https://developer.mozilla.org/en-US/docs/Web/API/File) objects which you can then process and send to a server.
   * For example, with [SuperAgent](https://github.com/visionmedia/superagent) as a http/ajax library:
   *
   * ```js
   * function onDrop(acceptedFiles) {
   *   const req = request.post('/upload')
   *   acceptedFiles.forEach(file => {
   *     req.attach(file.name, file)
   *   })
   *   req.end(callback)
   * }
   * ```
   *
   * @param {File[]} acceptedFiles
   * @param {FileRejection[]} fileRejections
   * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
   */
  onDrop: propTypesExports.func,

  /**
   * Cb for when the `drop` event occurs.
   * Note that if no files are accepted, this callback is not invoked.
   *
   * @param {File[]} files
   * @param {(DragEvent|Event)} event
   */
  onDropAccepted: propTypesExports.func,

  /**
   * Cb for when the `drop` event occurs.
   * Note that if no files are rejected, this callback is not invoked.
   *
   * @param {FileRejection[]} fileRejections
   * @param {(DragEvent|Event)} event
   */
  onDropRejected: propTypesExports.func,

  /**
   * Cb for when there's some error from any of the promises.
   *
   * @param {Error} error
   */
  onError: propTypesExports.func,

  /**
   * Custom validation function. It must return null if there's no errors.
   * @param {File} file
   * @returns {FileError|FileError[]|null}
   */
  validator: propTypesExports.func
};
/**
 * A function that is invoked for the `dragenter`,
 * `dragover` and `dragleave` events.
 * It is not invoked if the items are not files (such as link, text, etc.).
 *
 * @callback dragCb
 * @param {DragEvent} event
 */

/**
 * A function that is invoked for the `drop` or input change event.
 * It is not invoked if the items are not files (such as link, text, etc.).
 *
 * @callback dropCb
 * @param {File[]} acceptedFiles List of accepted files
 * @param {FileRejection[]} fileRejections List of rejected files and why they were rejected
 * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
 */

/**
 * A function that is invoked for the `drop` or input change event.
 * It is not invoked if the items are files (such as link, text, etc.).
 *
 * @callback dropAcceptedCb
 * @param {File[]} files List of accepted files that meet the given criteria
 * (`accept`, `multiple`, `minSize`, `maxSize`)
 * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
 */

/**
 * A function that is invoked for the `drop` or input change event.
 *
 * @callback dropRejectedCb
 * @param {File[]} files List of rejected files that do not meet the given criteria
 * (`accept`, `multiple`, `minSize`, `maxSize`)
 * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
 */

/**
 * A function that is used aggregate files,
 * in a asynchronous fashion, from drag or input change events.
 *
 * @callback getFilesFromEvent
 * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
 * @returns {(File[]|Promise<File[]>)}
 */

/**
 * An object with the current dropzone state.
 *
 * @typedef {object} DropzoneState
 * @property {boolean} isFocused Dropzone area is in focus
 * @property {boolean} isFileDialogActive File dialog is opened
 * @property {boolean} isDragActive Active drag is in progress
 * @property {boolean} isDragAccept Dragged files are accepted
 * @property {boolean} isDragReject Some dragged files are rejected
 * @property {File[]} acceptedFiles Accepted files
 * @property {FileRejection[]} fileRejections Rejected files and why they were rejected
 */

/**
 * An object with the dropzone methods.
 *
 * @typedef {object} DropzoneMethods
 * @property {Function} getRootProps Returns the props you should apply to the root drop container you render
 * @property {Function} getInputProps Returns the props you should apply to hidden file input you render
 * @property {Function} open Open the native file selection dialog
 */

var initialState = {
  isFocused: false,
  isFileDialogActive: false,
  isDragActive: false,
  isDragAccept: false,
  isDragReject: false,
  acceptedFiles: [],
  fileRejections: []
};
/**
 * A React hook that creates a drag 'n' drop area.
 *
 * ```jsx
 * function MyDropzone(props) {
 *   const {getRootProps, getInputProps} = useDropzone({
 *     onDrop: acceptedFiles => {
 *       // do something with the File objects, e.g. upload to some server
 *     }
 *   });
 *   return (
 *     <div {...getRootProps()}>
 *       <input {...getInputProps()} />
 *       <p>Drag and drop some files here, or click to select files</p>
 *     </div>
 *   )
 * }
 * ```
 *
 * @function useDropzone
 *
 * @param {object} props
 * @param {import("./utils").AcceptProp} [props.accept] Set accepted file types.
 * Checkout https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker types option for more information.
 * Keep in mind that mime type determination is not reliable across platforms. CSV files,
 * for example, are reported as text/plain under macOS but as application/vnd.ms-excel under
 * Windows. In some cases there might not be a mime type set at all (https://github.com/react-dropzone/react-dropzone/issues/276).
 * @param {boolean} [props.multiple=true] Allow drag 'n' drop (or selection from the file dialog) of multiple files
 * @param {boolean} [props.preventDropOnDocument=true] If false, allow dropped items to take over the current browser window
 * @param {boolean} [props.noClick=false] If true, disables click to open the native file selection dialog
 * @param {boolean} [props.noKeyboard=false] If true, disables SPACE/ENTER to open the native file selection dialog.
 * Note that it also stops tracking the focus state.
 * @param {boolean} [props.noDrag=false] If true, disables drag 'n' drop
 * @param {boolean} [props.noDragEventsBubbling=false] If true, stops drag event propagation to parents
 * @param {number} [props.minSize=0] Minimum file size (in bytes)
 * @param {number} [props.maxSize=Infinity] Maximum file size (in bytes)
 * @param {boolean} [props.disabled=false] Enable/disable the dropzone
 * @param {getFilesFromEvent} [props.getFilesFromEvent] Use this to provide a custom file aggregator
 * @param {Function} [props.onFileDialogCancel] Cb for when closing the file dialog with no selection
 * @param {boolean} [props.useFsAccessApi] Set to true to use the https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API
 * to open the file picker instead of using an `<input type="file">` click event.
 * @param {boolean} autoFocus Set to true to auto focus the root element.
 * @param {Function} [props.onFileDialogOpen] Cb for when opening the file dialog
 * @param {dragCb} [props.onDragEnter] Cb for when the `dragenter` event occurs.
 * @param {dragCb} [props.onDragLeave] Cb for when the `dragleave` event occurs
 * @param {dragCb} [props.onDragOver] Cb for when the `dragover` event occurs
 * @param {dropCb} [props.onDrop] Cb for when the `drop` event occurs.
 * Note that this callback is invoked after the `getFilesFromEvent` callback is done.
 *
 * Files are accepted or rejected based on the `accept`, `multiple`, `minSize` and `maxSize` props.
 * `accept` must be an object with keys as a valid [MIME type](http://www.iana.org/assignments/media-types/media-types.xhtml) according to [input element specification](https://www.w3.org/wiki/HTML/Elements/input/file) and the value an array of file extensions (optional).
 * If `multiple` is set to false and additional files are dropped,
 * all files besides the first will be rejected.
 * Any file which does not have a size in the [`minSize`, `maxSize`] range, will be rejected as well.
 *
 * Note that the `onDrop` callback will always be invoked regardless if the dropped files were accepted or rejected.
 * If you'd like to react to a specific scenario, use the `onDropAccepted`/`onDropRejected` props.
 *
 * `onDrop` will provide you with an array of [File](https://developer.mozilla.org/en-US/docs/Web/API/File) objects which you can then process and send to a server.
 * For example, with [SuperAgent](https://github.com/visionmedia/superagent) as a http/ajax library:
 *
 * ```js
 * function onDrop(acceptedFiles) {
 *   const req = request.post('/upload')
 *   acceptedFiles.forEach(file => {
 *     req.attach(file.name, file)
 *   })
 *   req.end(callback)
 * }
 * ```
 * @param {dropAcceptedCb} [props.onDropAccepted]
 * @param {dropRejectedCb} [props.onDropRejected]
 * @param {(error: Error) => void} [props.onError]
 *
 * @returns {DropzoneState & DropzoneMethods}
 */

function useDropzone() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var _defaultProps$props = _objectSpread(_objectSpread({}, defaultProps), props),
      accept = _defaultProps$props.accept,
      disabled = _defaultProps$props.disabled,
      getFilesFromEvent = _defaultProps$props.getFilesFromEvent,
      maxSize = _defaultProps$props.maxSize,
      minSize = _defaultProps$props.minSize,
      multiple = _defaultProps$props.multiple,
      maxFiles = _defaultProps$props.maxFiles,
      onDragEnter = _defaultProps$props.onDragEnter,
      onDragLeave = _defaultProps$props.onDragLeave,
      onDragOver = _defaultProps$props.onDragOver,
      onDrop = _defaultProps$props.onDrop,
      onDropAccepted = _defaultProps$props.onDropAccepted,
      onDropRejected = _defaultProps$props.onDropRejected,
      onFileDialogCancel = _defaultProps$props.onFileDialogCancel,
      onFileDialogOpen = _defaultProps$props.onFileDialogOpen,
      useFsAccessApi = _defaultProps$props.useFsAccessApi,
      autoFocus = _defaultProps$props.autoFocus,
      preventDropOnDocument = _defaultProps$props.preventDropOnDocument,
      noClick = _defaultProps$props.noClick,
      noKeyboard = _defaultProps$props.noKeyboard,
      noDrag = _defaultProps$props.noDrag,
      noDragEventsBubbling = _defaultProps$props.noDragEventsBubbling,
      onError = _defaultProps$props.onError,
      validator = _defaultProps$props.validator;

  var acceptAttr = useMemo(function () {
    return acceptPropAsAcceptAttr(accept);
  }, [accept]);
  var pickerTypes = useMemo(function () {
    return pickerOptionsFromAccept(accept);
  }, [accept]);
  var onFileDialogOpenCb = useMemo(function () {
    return typeof onFileDialogOpen === "function" ? onFileDialogOpen : noop$6;
  }, [onFileDialogOpen]);
  var onFileDialogCancelCb = useMemo(function () {
    return typeof onFileDialogCancel === "function" ? onFileDialogCancel : noop$6;
  }, [onFileDialogCancel]);
  /**
   * @constant
   * @type {React.MutableRefObject<HTMLElement>}
   */

  var rootRef = useRef(null);
  var inputRef = useRef(null);

  var _useReducer = useReducer(reducer$1, initialState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  var isFocused = state.isFocused,
      isFileDialogActive = state.isFileDialogActive;
  var fsAccessApiWorksRef = useRef(typeof window !== "undefined" && window.isSecureContext && useFsAccessApi && canUseFileSystemAccessAPI()); // Update file dialog active state when the window is focused on

  var onWindowFocus = function onWindowFocus() {
    // Execute the timeout only if the file dialog is opened in the browser
    if (!fsAccessApiWorksRef.current && isFileDialogActive) {
      setTimeout(function () {
        if (inputRef.current) {
          var files = inputRef.current.files;

          if (!files.length) {
            dispatch({
              type: "closeDialog"
            });
            onFileDialogCancelCb();
          }
        }
      }, 300);
    }
  };

  useEffect(function () {
    window.addEventListener("focus", onWindowFocus, false);
    return function () {
      window.removeEventListener("focus", onWindowFocus, false);
    };
  }, [inputRef, isFileDialogActive, onFileDialogCancelCb, fsAccessApiWorksRef]);
  var dragTargetsRef = useRef([]);

  var onDocumentDrop = function onDocumentDrop(event) {
    if (rootRef.current && rootRef.current.contains(event.target)) {
      // If we intercepted an event for our instance, let it propagate down to the instance's onDrop handler
      return;
    }

    event.preventDefault();
    dragTargetsRef.current = [];
  };

  useEffect(function () {
    if (preventDropOnDocument) {
      document.addEventListener("dragover", onDocumentDragOver, false);
      document.addEventListener("drop", onDocumentDrop, false);
    }

    return function () {
      if (preventDropOnDocument) {
        document.removeEventListener("dragover", onDocumentDragOver);
        document.removeEventListener("drop", onDocumentDrop);
      }
    };
  }, [rootRef, preventDropOnDocument]); // Auto focus the root when autoFocus is true

  useEffect(function () {
    if (!disabled && autoFocus && rootRef.current) {
      rootRef.current.focus();
    }

    return function () {};
  }, [rootRef, autoFocus, disabled]);
  var onErrCb = useCallback(function (e) {
    if (onError) {
      onError(e);
    } else {
      // Let the user know something's gone wrong if they haven't provided the onError cb.
      console.error(e);
    }
  }, [onError]);
  var onDragEnterCb = useCallback(function (event) {
    event.preventDefault(); // Persist here because we need the event later after getFilesFromEvent() is done

    event.persist();
    stopPropagation(event);
    dragTargetsRef.current = [].concat(_toConsumableArray(dragTargetsRef.current), [event.target]);

    if (isEvtWithFiles(event)) {
      Promise.resolve(getFilesFromEvent(event)).then(function (files) {
        if (isPropagationStopped(event) && !noDragEventsBubbling) {
          return;
        }

        var fileCount = files.length;
        var isDragAccept = fileCount > 0 && allFilesAccepted({
          files: files,
          accept: acceptAttr,
          minSize: minSize,
          maxSize: maxSize,
          multiple: multiple,
          maxFiles: maxFiles,
          validator: validator
        });
        var isDragReject = fileCount > 0 && !isDragAccept;
        dispatch({
          isDragAccept: isDragAccept,
          isDragReject: isDragReject,
          isDragActive: true,
          type: "setDraggedFiles"
        });

        if (onDragEnter) {
          onDragEnter(event);
        }
      }).catch(function (e) {
        return onErrCb(e);
      });
    }
  }, [getFilesFromEvent, onDragEnter, onErrCb, noDragEventsBubbling, acceptAttr, minSize, maxSize, multiple, maxFiles, validator]);
  var onDragOverCb = useCallback(function (event) {
    event.preventDefault();
    event.persist();
    stopPropagation(event);
    var hasFiles = isEvtWithFiles(event);

    if (hasFiles && event.dataTransfer) {
      try {
        event.dataTransfer.dropEffect = "copy";
      } catch (_unused) {}
      /* eslint-disable-line no-empty */

    }

    if (hasFiles && onDragOver) {
      onDragOver(event);
    }

    return false;
  }, [onDragOver, noDragEventsBubbling]);
  var onDragLeaveCb = useCallback(function (event) {
    event.preventDefault();
    event.persist();
    stopPropagation(event); // Only deactivate once the dropzone and all children have been left

    var targets = dragTargetsRef.current.filter(function (target) {
      return rootRef.current && rootRef.current.contains(target);
    }); // Make sure to remove a target present multiple times only once
    // (Firefox may fire dragenter/dragleave multiple times on the same element)

    var targetIdx = targets.indexOf(event.target);

    if (targetIdx !== -1) {
      targets.splice(targetIdx, 1);
    }

    dragTargetsRef.current = targets;

    if (targets.length > 0) {
      return;
    }

    dispatch({
      type: "setDraggedFiles",
      isDragActive: false,
      isDragAccept: false,
      isDragReject: false
    });

    if (isEvtWithFiles(event) && onDragLeave) {
      onDragLeave(event);
    }
  }, [rootRef, onDragLeave, noDragEventsBubbling]);
  var setFiles = useCallback(function (files, event) {
    var acceptedFiles = [];
    var fileRejections = [];
    files.forEach(function (file) {
      var _fileAccepted = fileAccepted(file, acceptAttr),
          _fileAccepted2 = _slicedToArray(_fileAccepted, 2),
          accepted = _fileAccepted2[0],
          acceptError = _fileAccepted2[1];

      var _fileMatchSize = fileMatchSize(file, minSize, maxSize),
          _fileMatchSize2 = _slicedToArray(_fileMatchSize, 2),
          sizeMatch = _fileMatchSize2[0],
          sizeError = _fileMatchSize2[1];

      var customErrors = validator ? validator(file) : null;

      if (accepted && sizeMatch && !customErrors) {
        acceptedFiles.push(file);
      } else {
        var errors = [acceptError, sizeError];

        if (customErrors) {
          errors = errors.concat(customErrors);
        }

        fileRejections.push({
          file: file,
          errors: errors.filter(function (e) {
            return e;
          })
        });
      }
    });

    if (!multiple && acceptedFiles.length > 1 || multiple && maxFiles >= 1 && acceptedFiles.length > maxFiles) {
      // Reject everything and empty accepted files
      acceptedFiles.forEach(function (file) {
        fileRejections.push({
          file: file,
          errors: [TOO_MANY_FILES_REJECTION]
        });
      });
      acceptedFiles.splice(0);
    }

    dispatch({
      acceptedFiles: acceptedFiles,
      fileRejections: fileRejections,
      type: "setFiles"
    });

    if (onDrop) {
      onDrop(acceptedFiles, fileRejections, event);
    }

    if (fileRejections.length > 0 && onDropRejected) {
      onDropRejected(fileRejections, event);
    }

    if (acceptedFiles.length > 0 && onDropAccepted) {
      onDropAccepted(acceptedFiles, event);
    }
  }, [dispatch, multiple, acceptAttr, minSize, maxSize, maxFiles, onDrop, onDropAccepted, onDropRejected, validator]);
  var onDropCb = useCallback(function (event) {
    event.preventDefault(); // Persist here because we need the event later after getFilesFromEvent() is done

    event.persist();
    stopPropagation(event);
    dragTargetsRef.current = [];

    if (isEvtWithFiles(event)) {
      Promise.resolve(getFilesFromEvent(event)).then(function (files) {
        if (isPropagationStopped(event) && !noDragEventsBubbling) {
          return;
        }

        setFiles(files, event);
      }).catch(function (e) {
        return onErrCb(e);
      });
    }

    dispatch({
      type: "reset"
    });
  }, [getFilesFromEvent, setFiles, onErrCb, noDragEventsBubbling]); // Fn for opening the file dialog programmatically

  var openFileDialog = useCallback(function () {
    // No point to use FS access APIs if context is not secure
    // https://developer.mozilla.org/en-US/docs/Web/Security/Secure_Contexts#feature_detection
    if (fsAccessApiWorksRef.current) {
      dispatch({
        type: "openDialog"
      });
      onFileDialogOpenCb(); // https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker

      var opts = {
        multiple: multiple,
        types: pickerTypes
      };
      window.showOpenFilePicker(opts).then(function (handles) {
        return getFilesFromEvent(handles);
      }).then(function (files) {
        setFiles(files, null);
        dispatch({
          type: "closeDialog"
        });
      }).catch(function (e) {
        // AbortError means the user canceled
        if (isAbort(e)) {
          onFileDialogCancelCb(e);
          dispatch({
            type: "closeDialog"
          });
        } else if (isSecurityError(e)) {
          fsAccessApiWorksRef.current = false; // CORS, so cannot use this API
          // Try using the input

          if (inputRef.current) {
            inputRef.current.value = null;
            inputRef.current.click();
          } else {
            onErrCb(new Error("Cannot open the file picker because the https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API is not supported and no <input> was provided."));
          }
        } else {
          onErrCb(e);
        }
      });
      return;
    }

    if (inputRef.current) {
      dispatch({
        type: "openDialog"
      });
      onFileDialogOpenCb();
      inputRef.current.value = null;
      inputRef.current.click();
    }
  }, [dispatch, onFileDialogOpenCb, onFileDialogCancelCb, useFsAccessApi, setFiles, onErrCb, pickerTypes, multiple]); // Cb to open the file dialog when SPACE/ENTER occurs on the dropzone

  var onKeyDownCb = useCallback(function (event) {
    // Ignore keyboard events bubbling up the DOM tree
    if (!rootRef.current || !rootRef.current.isEqualNode(event.target)) {
      return;
    }

    if (event.key === " " || event.key === "Enter" || event.keyCode === 32 || event.keyCode === 13) {
      event.preventDefault();
      openFileDialog();
    }
  }, [rootRef, openFileDialog]); // Update focus state for the dropzone

  var onFocusCb = useCallback(function () {
    dispatch({
      type: "focus"
    });
  }, []);
  var onBlurCb = useCallback(function () {
    dispatch({
      type: "blur"
    });
  }, []); // Cb to open the file dialog when click occurs on the dropzone

  var onClickCb = useCallback(function () {
    if (noClick) {
      return;
    } // In IE11/Edge the file-browser dialog is blocking, therefore, use setTimeout()
    // to ensure React can handle state changes
    // See: https://github.com/react-dropzone/react-dropzone/issues/450


    if (isIeOrEdge()) {
      setTimeout(openFileDialog, 0);
    } else {
      openFileDialog();
    }
  }, [noClick, openFileDialog]);

  var composeHandler = function composeHandler(fn) {
    return disabled ? null : fn;
  };

  var composeKeyboardHandler = function composeKeyboardHandler(fn) {
    return noKeyboard ? null : composeHandler(fn);
  };

  var composeDragHandler = function composeDragHandler(fn) {
    return noDrag ? null : composeHandler(fn);
  };

  var stopPropagation = function stopPropagation(event) {
    if (noDragEventsBubbling) {
      event.stopPropagation();
    }
  };

  var getRootProps = useMemo(function () {
    return function () {
      var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref2$refKey = _ref2.refKey,
          refKey = _ref2$refKey === void 0 ? "ref" : _ref2$refKey,
          role = _ref2.role,
          onKeyDown = _ref2.onKeyDown,
          onFocus = _ref2.onFocus,
          onBlur = _ref2.onBlur,
          onClick = _ref2.onClick,
          onDragEnter = _ref2.onDragEnter,
          onDragOver = _ref2.onDragOver,
          onDragLeave = _ref2.onDragLeave,
          onDrop = _ref2.onDrop,
          rest = _objectWithoutProperties(_ref2, _excluded3);

      return _objectSpread(_objectSpread(_defineProperty$1({
        onKeyDown: composeKeyboardHandler(composeEventHandlers(onKeyDown, onKeyDownCb)),
        onFocus: composeKeyboardHandler(composeEventHandlers(onFocus, onFocusCb)),
        onBlur: composeKeyboardHandler(composeEventHandlers(onBlur, onBlurCb)),
        onClick: composeHandler(composeEventHandlers(onClick, onClickCb)),
        onDragEnter: composeDragHandler(composeEventHandlers(onDragEnter, onDragEnterCb)),
        onDragOver: composeDragHandler(composeEventHandlers(onDragOver, onDragOverCb)),
        onDragLeave: composeDragHandler(composeEventHandlers(onDragLeave, onDragLeaveCb)),
        onDrop: composeDragHandler(composeEventHandlers(onDrop, onDropCb)),
        role: typeof role === "string" && role !== "" ? role : "presentation"
      }, refKey, rootRef), !disabled && !noKeyboard ? {
        tabIndex: 0
      } : {}), rest);
    };
  }, [rootRef, onKeyDownCb, onFocusCb, onBlurCb, onClickCb, onDragEnterCb, onDragOverCb, onDragLeaveCb, onDropCb, noKeyboard, noDrag, disabled]);
  var onInputElementClick = useCallback(function (event) {
    event.stopPropagation();
  }, []);
  var getInputProps = useMemo(function () {
    return function () {
      var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref3$refKey = _ref3.refKey,
          refKey = _ref3$refKey === void 0 ? "ref" : _ref3$refKey,
          onChange = _ref3.onChange,
          onClick = _ref3.onClick,
          rest = _objectWithoutProperties(_ref3, _excluded4);

      var inputProps = _defineProperty$1({
        accept: acceptAttr,
        multiple: multiple,
        type: "file",
        style: {
          display: "none"
        },
        onChange: composeHandler(composeEventHandlers(onChange, onDropCb)),
        onClick: composeHandler(composeEventHandlers(onClick, onInputElementClick)),
        tabIndex: -1
      }, refKey, inputRef);

      return _objectSpread(_objectSpread({}, inputProps), rest);
    };
  }, [inputRef, accept, multiple, onDropCb, disabled]);
  return _objectSpread(_objectSpread({}, state), {}, {
    isFocused: isFocused && !disabled,
    getRootProps: getRootProps,
    getInputProps: getInputProps,
    rootRef: rootRef,
    inputRef: inputRef,
    open: composeHandler(openFileDialog)
  });
}
/**
 * @param {DropzoneState} state
 * @param {{type: string} & DropzoneState} action
 * @returns {DropzoneState}
 */

function reducer$1(state, action) {
  /* istanbul ignore next */
  switch (action.type) {
    case "focus":
      return _objectSpread(_objectSpread({}, state), {}, {
        isFocused: true
      });

    case "blur":
      return _objectSpread(_objectSpread({}, state), {}, {
        isFocused: false
      });

    case "openDialog":
      return _objectSpread(_objectSpread({}, initialState), {}, {
        isFileDialogActive: true
      });

    case "closeDialog":
      return _objectSpread(_objectSpread({}, state), {}, {
        isFileDialogActive: false
      });

    case "setDraggedFiles":
      return _objectSpread(_objectSpread({}, state), {}, {
        isDragActive: action.isDragActive,
        isDragAccept: action.isDragAccept,
        isDragReject: action.isDragReject
      });

    case "setFiles":
      return _objectSpread(_objectSpread({}, state), {}, {
        acceptedFiles: action.acceptedFiles,
        fileRejections: action.fileRejections
      });

    case "reset":
      return _objectSpread({}, initialState);

    default:
      return state;
  }
}

function noop$6() {}

var _path$8, _path2$3;
function _extends$g() { return _extends$g = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$g.apply(null, arguments); }
var SvgUploadCloud = function SvgUploadCloud(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$g({
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$8 || (_path$8 = /*#__PURE__*/React.createElement("path", {
    d: "M24 10.4212C23.9974 9.64474 23.8394 8.8767 23.5354 8.16227C23.2315 7.44783 22.7876 6.80144 22.23 6.26115C21.2956 5.35226 20.078 4.79108 18.78 4.67115C18.7412 4.66737 18.7038 4.65454 18.6708 4.6337C18.6378 4.61287 18.6101 4.5846 18.59 4.55115C17.8008 3.22797 16.6319 2.17274 15.2351 1.52258C13.8383 0.872419 12.2783 0.657387 10.7577 0.905423C9.23715 1.15346 7.82632 1.8531 6.70857 2.91343C5.59082 3.97376 4.81781 5.34576 4.49 6.85115C4.47269 6.89621 4.44271 6.9353 4.40367 6.9637C4.36463 6.99209 4.3182 7.00857 4.27 7.01115C3.32859 7.07558 2.42892 7.42429 1.69 8.01115C1.15504 8.44713 0.725279 8.99812 0.432684 9.62313C0.140088 10.2481 -0.00780742 10.9311 1.52481e-06 11.6212C-0.0236721 12.2125 0.0741493 12.8025 0.287424 13.3546C0.500699 13.9067 0.8249 14.4093 1.24 14.8312C2.23792 15.7043 3.51415 16.1935 4.84 16.2112C5.10522 16.2112 5.35957 16.1058 5.54711 15.9183C5.73464 15.7307 5.84 15.4764 5.84 15.2112C5.84 14.9459 5.73464 14.6916 5.54711 14.504C5.35957 14.3165 5.10522 14.2112 4.84 14.2112C4.04353 14.2069 3.27447 13.9198 2.67 13.4012C2.44045 13.1675 2.26226 12.8885 2.14688 12.582C2.0315 12.2754 1.98147 11.9482 2 11.6212C1.99893 11.2359 2.08416 10.8553 2.24941 10.5073C2.41467 10.1593 2.65576 9.85271 2.955 9.61006C3.25423 9.36741 3.60401 9.19486 3.97865 9.10507C4.35329 9.01527 4.74328 9.01052 5.12 9.09115C5.26074 9.11948 5.40593 9.11718 5.5457 9.08439C5.68547 9.05161 5.81654 8.98911 5.93 8.90115C6.04335 8.81436 6.1368 8.70431 6.20407 8.57839C6.27134 8.45248 6.31087 8.31362 6.32 8.17115C6.38894 6.90659 6.87852 5.70134 7.71094 4.74691C8.54337 3.79248 9.67089 3.14362 10.9143 2.90344C12.1578 2.66325 13.4459 2.84552 14.5738 3.42127C15.7018 3.99702 16.6051 4.93324 17.14 6.08115C17.2239 6.2576 17.3576 6.40561 17.5247 6.50691C17.6917 6.60821 17.8848 6.65835 18.08 6.65115C18.5877 6.62548 19.0952 6.70556 19.5703 6.88631C20.0454 7.06706 20.4778 7.34453 20.84 7.70115C21.2081 8.05188 21.5008 8.47397 21.7002 8.94164C21.8997 9.40931 22.0017 9.91273 22 10.4212C21.995 11.3659 21.641 12.2756 21.0061 12.9752C20.3711 13.6748 19.4999 14.115 18.56 14.2112C18.2948 14.2456 18.0541 14.3841 17.891 14.596C17.7278 14.8079 17.6555 15.0759 17.69 15.3412C17.7245 15.6064 17.8629 15.847 18.0748 16.0102C18.2867 16.1733 18.5548 16.2456 18.82 16.2112C20.245 16.0535 21.5616 15.3754 22.5175 14.307C23.4734 13.2385 24.0013 11.8548 24 10.4212Z",
    fill: "currentColor"
  })), _path2$3 || (_path2$3 = /*#__PURE__*/React.createElement("path", {
    d: "M12.71 9.01115C12.617 8.91742 12.5064 8.84303 12.3846 8.79226C12.2627 8.74149 12.132 8.71535 12 8.71535C11.868 8.71535 11.7373 8.74149 11.6154 8.79226C11.4936 8.84303 11.383 8.91742 11.29 9.01115L7.79 12.5112C7.65239 12.6515 7.55922 12.8294 7.52211 13.0224C7.48501 13.2155 7.50562 13.4152 7.58138 13.5965C7.65714 13.7779 7.78468 13.933 7.94806 14.0423C8.11145 14.1516 8.30342 14.2103 8.5 14.2112H10.25C10.3163 14.2112 10.3799 14.2375 10.4268 14.2844C10.4737 14.3313 10.5 14.3948 10.5 14.4612V21.7112C10.5 22.109 10.658 22.4905 10.9393 22.7718C11.2206 23.0531 11.6022 23.2112 12 23.2112C12.3978 23.2112 12.7794 23.0531 13.0607 22.7718C13.342 22.4905 13.5 22.109 13.5 21.7112V14.4612C13.5 14.3948 13.5263 14.3313 13.5732 14.2844C13.6201 14.2375 13.6837 14.2112 13.75 14.2112H15.5C15.6986 14.2123 15.8931 14.1543 16.0586 14.0445C16.2241 13.9347 16.3532 13.7781 16.4294 13.5946C16.5055 13.4112 16.5254 13.2092 16.4863 13.0145C16.4472 12.8197 16.351 12.641 16.21 12.5012L12.71 9.01115Z",
    fill: "currentColor"
  })));
};
var ForwardRef$d = /*#__PURE__*/forwardRef(SvgUploadCloud);
var Memo$d = /*#__PURE__*/memo(ForwardRef$d);

var FileMimeType;
(function (FileMimeType) {
    FileMimeType["ALL"] = "application/octet-stream";
    FileMimeType["CSV"] = "text/csv";
    FileMimeType["EXCEL"] = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    FileMimeType["GZIP"] = "application/gzip";
    FileMimeType["IMAGE_ALL"] = "image/*";
    FileMimeType["IMAGE_GIF"] = "image/gif";
    FileMimeType["IMAGE_JPEG"] = "image/jpeg";
    FileMimeType["IMAGE_PNG"] = "image/png";
    FileMimeType["JSON"] = "application/json";
    FileMimeType["PDF"] = "application/pdf";
    FileMimeType["POWERPOINT"] = "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    FileMimeType["TEXT_PLAIN"] = "text/plain";
    FileMimeType["WORD"] = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    FileMimeType["ZIP"] = "application/zip";
})(FileMimeType || (FileMimeType = {}));

const StyledUploadZoneWrapper = _default$1.div `
  border-dashed
  border-gray-300
  border-2
  p-6
  w-full
  rounded-md
  cursor-pointer
`;
const StyledTitle = _default$1(Typography) `
  pt-3
  pb-1
  text-gray-600
`;
const StyledUploadText = _default$1(Typography) `
  text-primary-600
  cursor-pointer
`;
const StyledFormatText = _default$1(Typography) `
  text-gray-500
`;
_default$1(Typography) `
  text-primary-600
`;

const mimeTypeExtensions = {
    [FileMimeType.ALL]: ['.*'],
    [FileMimeType.CSV]: ['.csv'],
    [FileMimeType.EXCEL]: ['.xlsx'],
    [FileMimeType.IMAGE_ALL]: ['.gif', '.jpeg', '.jpg', '.png'],
    [FileMimeType.IMAGE_GIF]: ['.gif'],
    [FileMimeType.GZIP]: ['.gz'],
    [FileMimeType.IMAGE_JPEG]: ['.jpeg', '.jpg'],
    [FileMimeType.IMAGE_PNG]: ['.png'],
    [FileMimeType.JSON]: ['.json'],
    [FileMimeType.PDF]: ['.pdf'],
    [FileMimeType.POWERPOINT]: ['.pptx'],
    [FileMimeType.TEXT_PLAIN]: ['.txt'],
    [FileMimeType.WORD]: ['.docx'],
    [FileMimeType.ZIP]: ['.zip'],
};

const makeAcceptMap = (mimeTypes) => {
    // there is no extension or mime type for all files, so we return undefined
    if (mimeTypes.includes(FileMimeType.ALL)) {
        return undefined;
    }
    return mimeTypes.reduce((memo, mimeType) => {
        memo[mimeType] = mimeTypeExtensions[mimeType];
        return memo;
    }, {});
};
const getMimeTypeExtensions = (mimeTypes) => mimeTypes.reduce((memo, mimeType) => {
    memo.push(...mimeTypeExtensions[mimeType]);
    return memo;
}, []);

const FileDropZone = ({ addFiles, allowMultipleSelect = false, allowedMimeTypes = [FileMimeType.ALL], className, disable = false, inputId, text, }) => {
    const { getInputProps, getRootProps } = useDropzone({
        accept: makeAcceptMap(allowedMimeTypes),
        multiple: allowMultipleSelect,
        noDrag: disable,
        noClick: disable,
        // the following function is called when files are dropped or selected and the files meet the accept and multiple criteria
        onDropAccepted: addFiles,
    });
    return (React__default.createElement(StyledUploadZoneWrapper, { className: className, ...getRootProps() },
        React__default.createElement(Stack$5, { direction: "column", alignItems: "center" },
            React__default.createElement("input", { id: inputId, ...getInputProps() }),
            React__default.createElement(Icon, { node: React__default.createElement(Memo$d, { className: "text-gray-400" }), variant: "none", size: 9 }),
            React__default.createElement(StyledTitle, { as: "h2", fontSize: "sm", fontWeight: "medium", id: "upload" },
                React__default.createElement(StyledUploadText, { as: "strong", fontSize: "sm", fontWeight: "medium" },
                    "Upload ",
                    text),
                ' ',
                "by clicking or drag and drop"),
            React__default.createElement(StyledFormatText, { as: "p", fontSize: "xs", fontWeight: "normal" },
                "Expecting",
                ' ',
                allowedMimeTypes.includes(FileMimeType.ALL) ? 'any' : getMimeTypeExtensions(allowedMimeTypes).join(', '),
                ' ',
                "format"))));
};

var _g$4;
function _extends$f() { return _extends$f = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$f.apply(null, arguments); }
var SvgDownload = function SvgDownload(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$f({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    ref: ref
  }, props), _g$4 || (_g$4 = /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M12.46 0.7A1.25 1.25 0 0 0 11.21 2v10.12a0.25 0.25 0 0 1 -0.25 0.25H8.8a1 1 0 0 0 -0.7 1.71l3.65 3.65a1 1 0 0 0 1.41 0l3.66 -3.65A1 1 0 0 0 17 13a1 1 0 0 0 -0.92 -0.62H14a0.25 0.25 0 0 1 -0.25 -0.25V2A1.25 1.25 0 0 0 12.46 0.7Z",
    fill: "currentColor",
    strokeWidth: 1
  }), /*#__PURE__*/React.createElement("path", {
    d: "M23.75 22a1.25 1.25 0 0 0 -1.25 -1.25h-21a1.25 1.25 0 0 0 0 2.5h21A1.25 1.25 0 0 0 23.75 22Z",
    fill: "currentColor",
    strokeWidth: 1
  }))));
};
var ForwardRef$c = /*#__PURE__*/forwardRef(SvgDownload);
var Memo$c = /*#__PURE__*/memo(ForwardRef$c);

/*
This component is based on https://tailwindui.com/components/application-ui/lists/stacked-lists#component-6a56f3c03589dfa48dc4e7eec7fbf6d0
*/
const FileDisplaySection = ({ disable = false, files, removeFile, ...defaultProps }) => {
    useMemo(() => {
        files.sort((a, b) => a.fileName.localeCompare(b.fileName));
    }, [files]);
    return (React__default.createElement("div", { ...defaultProps },
        React__default.createElement("ul", { role: "list", className: "divide-y divide-gray-100" }, files.map((file) => {
            const isUpdating = file.uploadPercent !== null && file.uploadPercent !== 100;
            return (React__default.createElement("li", { key: file.fileName, className: "flex items-center justify-between gap-x-6 py-3 px-4" },
                React__default.createElement("div", { className: "flex min-w-0 gap-x-4" },
                    React__default.createElement("div", { className: "min-w-0 flex-auto" },
                        React__default.createElement("div", { className: "flex items-center gap-x-2" },
                            React__default.createElement("p", { className: "text-xs font-semibold leading-6 text-gray-900 truncate" }, file.fileName),
                            file.url && (React__default.createElement("a", { href: file.url, target: "_blank", rel: "noopener noreferrer" },
                                React__default.createElement(Icon, { node: React__default.createElement(Memo$c, { className: "text-gray-400" }), size: 4, variant: "none" }))),
                            file.status === 'error' && file.errorMessage && (React__default.createElement(Tooltip, { text: file.errorMessage, className: "!z-modal whitespace-pre-line max-w-sm" },
                                React__default.createElement(Icon, { node: getIconBySeverity('error'), nodeSize: 4.5, size: 6, colorVariant: "error", className: "flex-shrink-0", "aria-hidden": "true" })))))),
                React__default.createElement(Button, { variant: "white", rounded: "full", size: "sm", disabled: isUpdating || disable, onClick: () => removeFile(file.fileName) }, !isUpdating || file.uploadPercent === null ? 'Remove' : `${file.uploadPercent.toFixed(0)}%`)));
        }))));
};

var _path$7;
function _extends$e() { return _extends$e = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$e.apply(null, arguments); }
var SvgQuestionHelpCircle = function SvgQuestionHelpCircle(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$e({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    ref: ref
  }, props), _path$7 || (_path$7 = /*#__PURE__*/React.createElement("path", {
    d: "M12,0A12,12,0,1,0,24,12,12,12,0,0,0,12,0Zm0,19a1.5,1.5,0,1,1,1.5-1.5A1.5,1.5,0,0,1,12,19Zm1.6-6.08a1,1,0,0,0-.6.92,1,1,0,0,1-2,0,3,3,0,0,1,1.8-2.75A2,2,0,1,0,10,9.25a1,1,0,0,1-2,0,4,4,0,1,1,5.6,3.67Z",
    fill: "currentColor"
  })));
};
var ForwardRef$b = /*#__PURE__*/forwardRef(SvgQuestionHelpCircle);
var Memo$b = /*#__PURE__*/memo(ForwardRef$b);

const HelpLinkStyled = _default$1.a `
  absolute
  top-0
  right-0
  -translate-y-[25%] translate-x-[125%]
  cursor-pointer
  hover:text-primary-600
`;

/**
 * A help link component that displays a question mark icon linking to help documentation.
 *
 * Note: This component must be placed within a parent element that has `position: relative` styling.
 * When used correctly, the help link will be positioned in the top right corner of the parent element
 * using absolute positioning.
 *
 * @example
 * <div style={{ position: 'relative' }}>
 *   <HelpLink link="https://help.docs.com/article" />
 * </div>
 */
const HelpLink = ({ className, href, id }) => (React__default.createElement(HelpLinkStyled, { href: href, target: "_blank", rel: "noopener noreferrer", className: className, id: id },
    React__default.createElement(Memo$b, { height: 12, width: 12 })));

const TitleWithHelp = ({ href, title }) => (React__default.createElement(Stack$5, { direction: "row", alignItems: "center", className: "relative inline" },
    React__default.createElement(Typography, { as: "h3", fontSize: "lg", fontWeight: "medium", className: "relative inline" }, title),
    React__default.createElement(HelpLink, { href: href })));

const StyledLabel$1 = _default$1.label `
  block
  text-sm
  font-medium
  text-gray-700
  relative
  self-start
`;

const LabelWithHelp = ({ href, inputId, label }) => (React__default.createElement(StyledLabel$1, { htmlFor: inputId },
    label,
    href && React__default.createElement(HelpLink, { href: href })));

var _g$3;
function _extends$d() { return _extends$d = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$d.apply(null, arguments); }
var SvgClose = function SvgClose(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$d({
    viewBox: "0 0 48 48",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _g$3 || (_g$3 = /*#__PURE__*/React.createElement("g", {
    transform: "matrix(2,0,0,2,0,0)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0.75 23.249L23.25 0.749",
    fill: "currentColor",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 1.5
  }), /*#__PURE__*/React.createElement("path", {
    d: "M23.25 23.249L0.75 0.749",
    fill: "currentColor",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 1.5
  }))));
};
var ForwardRef$a = /*#__PURE__*/forwardRef(SvgClose);
var Memo$a = /*#__PURE__*/memo(ForwardRef$a);

const StyledTrouserHeaderContainer = _default$1(Stack$5) `
  relative
  bg-gray-800
  ${({ $superUserMode }) => ($superUserMode ? 'bg-blue-900' : 'bg-gray-800')}
`;
const TrouserChildrenContainer = _default$1(Stack$5) `
  min-h-[52px]
  relative
`;
const StyledSuperUserModeContainer = _default$1(Stack$5) `
  absolute
  left-20
  top-5
  flex
  flex-col
  gap-3
`;
const StyledSuperUserModeText = _default$1(Typography) `
  uppercase
  text-rsbry-500
`;
const StyledMarkIconContainer = _default$1(Stack$5) `
  pl-7
  w-28
`;
const TrouserHeaderTitleContainer = _default$1(Stack$5) `
  min-h-[65px]
  border-b
  border-gray-600
  flex-1
`;
const StyledCloseButtonContainer = _default$1(Stack$5) `
  pr-6
  w-28
`;
const StyledCloseButton = _default$1(ButtonCircular) `
  transition
  duration-300
  hover:rotate-90
`;

const TrouserHeader = ({ children, className, onChangeHeight, onClose, superUserMode, superUserToggle, title, }) => {
    const [ref, { height }] = useElementSize();
    useEffect(() => {
        onChangeHeight?.(height);
    }, [height, onChangeHeight]);
    return (React__default.createElement(StyledTrouserHeaderContainer, { ref: ref, "$superUserMode": superUserMode, className: className, direction: "column" },
        React__default.createElement(Stack$5, null,
            React__default.createElement(StyledMarkIconContainer, { alignItems: "center" },
                React__default.createElement(Memo$e, { className: "h-8 text-primary-600" })),
            React__default.createElement(TrouserHeaderTitleContainer, { alignItems: "center", justifyContent: "center" }, title),
            React__default.createElement(StyledCloseButtonContainer, { alignItems: "center", justifyContent: "end" },
                React__default.createElement(StyledCloseButton, { onClick: onClose, size: "xs", icon: React__default.createElement(Memo$a, { className: "text-white m-1" }) }))),
        (children || superUserMode) && (React__default.createElement(TrouserChildrenContainer, { alignItems: "center", justifyContent: "center" },
            superUserMode && (React__default.createElement(StyledSuperUserModeContainer, null,
                React__default.createElement(StyledSuperUserModeText, { as: "p", fontSize: "l", fontWeight: "semibold", lineHeight: 4 }, "super user mode"),
                superUserToggle && React__default.createElement(Toggle, { ...superUserToggle }))),
            children))));
};

const TextColorVariant = {
    base: 'text-gray-400',
    bold: 'text-gray-600',
    0: 'text-primary-600',
    1: 'text-blue-600',
    2: 'text-green-300',
    3: 'text-lime-400',
    4: 'text-aqua-500',
    5: 'text-avocado-800',
    6: 'text-orange-400',
    7: 'text-rsbry-300',
    8: 'text-lemon-300',
};
const StyledProjectName = _default$1(Typography) `
  max-w-[124px]
  truncate
  ${({ $variant }) => TextColorVariant[$variant]}
`;
const StyledAllocationContainer = _default$1(Stack$5) `
  ${({ $currentPlacement }) => $currentPlacement === 'right-end' ? 'flex-col-reverse' : 'flex-col'}
`;
const StyledAllocationHeader = _default$1(Stack$5) `
  bg-gray-800
  w-full
  h-8
  pl-6
  overflow-hidden
  ${({ $currentPlacement }) => $currentPlacement === 'right-end' ? 'rounded-br-md' : 'rounded-tr-md'}
`;
const StyledProjectsContainer = _default$1(Stack$5) `
  w-full
  pl-6
  pr-3
  py-1.5
  ${({ $currentPlacement }) => $currentPlacement === 'right-end' ? 'flex-col-reverse' : 'flex-col'}
`;
const StyledProjectRow = _default$1(Stack$5) `
  py-1.5
`;
const StyledAdvancedButton = _default$1(Typography) `
  bg-primary-600
  hover:bg-primary-500
  h-full
  w-12
  text-white
`;
const StyledAllocationFooter = _default$1.div `
  bg-gray-200
  w-full
  h-8
  pl-6
  pr-3
  ${({ $currentPlacement }) => $currentPlacement === 'right-end' ? 'rounded-t-md' : 'rounded-b-md'}
`;

const ProjectAllocation = ({ allocationSliderId, currentPlacement, onAdvanceBtnClick, onChange, onSelect, otherAllocations, projects, totalAllocated, }) => {
    const sliderByProjects = projects
        .map((project) => Array.from({ length: project.percentage / 10 }, () => project.id))
        .flat();
    const sliderByOtherAllocations = otherAllocations
        .map((project) => Array.from({ length: project.percentage / 10 }, () => project.id))
        .flat();
    const hiddenOtherAllocations = otherAllocations.filter((allocation) => !allocation.visible);
    const getTooltip = (project) => (stepId) => {
        const hiddenAllocation = hiddenOtherAllocations.find((hiddenAllocation) => hiddenAllocation.id === stepId);
        return hiddenAllocation
            ? `This 10% is already allocated to ${hiddenAllocation.name}, clicking here will relocate that percentage to ${project.name}`
            : undefined;
    };
    return (React__default.createElement(SympatheticHighlightProvider, null,
        React__default.createElement(StyledAllocationContainer, { "$currentPlacement": currentPlacement },
            React__default.createElement(StyledAllocationHeader, { "$currentPlacement": currentPlacement, direction: "row-reverse" },
                React__default.createElement(StyledAdvancedButton, { as: Button$2, onClick: onAdvanceBtnClick, fontSize: "xs", fontWeight: "medium" }, "ADV")),
            React__default.createElement(StyledProjectsContainer, { "$currentPlacement": currentPlacement, direction: "column" }, projects.map((project, idx) => (React__default.createElement(StyledProjectRow, { key: project.id, gap: 1.5 },
                React__default.createElement(Stack$5, { gap: 0.5 },
                    React__default.createElement(AllocationSlider, { onChange: onChange, onSelect: (percentage) => {
                            onSelect({ percentage, projectId: project.id });
                        }, otherAllocations: sliderByOtherAllocations, projectId: project.id, projects: sliderByProjects, selectedPercentage: project.percentage, startPercentageFrom: project.percentage, steps: (100 - totalAllocated) / 10, tooltip: getTooltip(project), variant: idx, id: `${project.name}-${allocationSliderId}` })),
                React__default.createElement(StyledProjectName, { "$variant": project.percentage === 0 ? 'base' : idx % 9, as: "p", fontSize: "xs", fontWeight: "semibold", lineHeight: 5 }, project.name))))),
            React__default.createElement(StyledAllocationFooter, { "$currentPlacement": currentPlacement }, otherAllocations
                .filter((otherAllocation) => otherAllocation.visible)
                .map((otherAllocation) => (React__default.createElement(StyledProjectRow, { gap: 1.5, key: otherAllocation.id },
                React__default.createElement(Stack$5, { gap: 0.5 },
                    React__default.createElement(AllocationSlider, { direction: "rtl", onChange: onChange, onSelect: (percentage) => {
                            onSelect({ percentage, projectId: otherAllocation.id });
                        }, otherAllocations: sliderByOtherAllocations, projectId: otherAllocation.id, projects: sliderByProjects, selectedPercentage: otherAllocation.percentage, startPercentageFrom: otherAllocation.percentage, steps: (100 - totalAllocated) / 10, tooltip: getTooltip(otherAllocation), variant: "base", id: allocationSliderId })),
                React__default.createElement(StyledProjectName, { "$variant": otherAllocation.percentage === 0 ? 'base' : 'bold', as: "p", fontSize: "xs", fontWeight: "semibold", lineHeight: 5 }, otherAllocation.name))))))));
};

const StyledEquationContainer = _default$1(Stack$5) `
  h-24
  rounded-md
  shadow-md 
  bg-white
  p-6
`;

const SummaryEquation = ({ children, className }) => (React__default.createElement(StyledEquationContainer, { alignItems: "center", className: className, direction: "row", gap: 2, justifyContent: "space-between" }, children));
memo(SummaryEquation);

const SIDE_NAV_VARIANTS = {
    light: 'bg-gray-100',
    dark: 'bg-gray-600',
};
const SideNavContainer = _default$1(Stack$5) `
  bg-gray-100
  z-sidenav
  h-full
  relative
`;
const StyledSideNav$1 = _default$1.nav `
  ${({ $open }) => ($open ? 'flex flex-col w-[275px]' : 'w-20')}
  transition-[width]
  duration-300
  ease-in-out
  h-full
  absolute
  overflow-y-hidden
`;
const SideNavPlaceholder = _default$1.div `
  ${({ $open }) => ($open ? 'w-[275px]' : 'w-20')}
`;
const SectionsContainer = _default$1(Stack$5) `
  flex-1
  h-full
  overflow-y-hidden
 ${({ $variant }) => SIDE_NAV_VARIANTS[$variant]}
`;
const SideNavMenu = _default$1(Stack$5) `
  overflow-y-auto
  flex-1
  pt-5
  pb-4
  px-2
  scrollbar-none
  [&::-webkit-scrollbar]:hidden
  [-ms-overflow-style:none]
  [scrollbar-width:none]
`;
const TitleContainer = _default$1(Stack$5) `
  h-4
  px-3
`;
const SectionTitle$1 = _default$1(Typography) `
  text-gray-500
`;

// Passing in an open boolean allows the open/collapsed state of the side nav to be controlled externally by the parent.
const SideNav = ({ children, className, footer, gap, header, open, variant, }) => {
    const { componentRef, hasScroll } = useScrollableElement();
    const [openState, setOpenState] = useState(false);
    const internalOpenState = open || openState;
    const handleMouseEnter = useCallback(() => {
        if (open !== undefined)
            return;
        setOpenState(true);
    }, [open]);
    const handleMouseLeave = useCallback(() => {
        if (open !== undefined)
            return;
        setOpenState(false);
    }, [open]);
    const arrayChildren = Children.toArray(children);
    return (React__default.createElement(NavItemContext.Provider, { value: { open: internalOpenState, variant } },
        React__default.createElement(SideNavContainer, { className: className },
            React__default.createElement(StyledSideNav$1, { "$open": internalOpenState, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, "data-testid": "side-nav" },
                React__default.createElement(SectionsContainer, { direction: "column", "$variant": variant },
                    header && (React__default.createElement(StyledNavItemDiv, { "data-testid": "header-wrapper", "$isHeader": true, className: className }, header)),
                    React__default.createElement(SideNavMenu, { ref: componentRef, direction: "column", gap: gap, as: "ul" }, Children.map(arrayChildren, (child) => (React__default.createElement("li", null, child)))),
                    footer && typeof footer === 'function' ? (React__default.createElement(StyledNavItemDiv, { "data-testid": "footer-wrapper", "$floatingElement": hasScroll, "$isFooter": true, className: className }, footer({ isFixedBottom: hasScroll }))) : (React__default.createElement(StyledNavItemDiv, { "data-testid": "footer-wrapper", "$floatingElement": hasScroll, "$isFooter": true, className: className }, footer))))),
        React__default.createElement(SideNavPlaceholder, { "$open": open })));
};
const SideNavMemo = memo(SideNav);

const SideNavSection = ({ children, className, gap = 1, title }) => {
    const arrayChildren = Children.toArray(children);
    const { open } = useContext(NavItemContext);
    return (React__default.createElement(Stack$5, { className: className, direction: "column", gap: 1 },
        title ? (React__default.createElement(TitleContainer, null, open ? (React__default.createElement(SectionTitle$1, { fontSize: "xs", fontWeight: "semibold" }, title)) : null)) : null,
        React__default.createElement(Stack$5, { direction: "column", gap: gap, as: "ul" }, Children.map(arrayChildren, (child) => (React__default.createElement("li", null, child))))));
};
const SideNavSectionMemo = memo(SideNavSection);

const StyledSideNav = _default$1(SideNav) `
  border-0
  min-h-[inherit]
  h-[initial]
  [&>nav]:w-[292px]
`;
const StyledActionContainer = _default$1(Stack$5) `
  pl-4
  w-full
`;
const StyledItemsContainer = _default$1.div `
  px-1
  py-4
`;
const StepsContainer = _default$1.div `
  w-[calc(100%-275px)]
`;

const Wizard = ({ className, closeButtonId, navDisabled, nextButtonId, onClose, onStepsComplete, pages, }) => {
    const { currentStep, hasNextStep, hasPreviousStep, nextStep, prevStep, setCurrentStep } = useStepper({
        onStepsComplete,
        steps: pages.length,
    });
    return (React__default.createElement(Stack$5, { className: className },
        React__default.createElement(StyledSideNav, { variant: "light", open: true, footer: React__default.createElement(StyledActionContainer, { gap: 2 },
                React__default.createElement(Button, { className: "w-full", variant: "white", size: "base", rounded: "regular", disabled: !hasPreviousStep, onClick: prevStep }, "BACK"),
                hasNextStep ? (React__default.createElement(Button, { className: "w-full", variant: "primary", size: "base", rounded: "regular", onClick: nextStep, id: nextButtonId }, "NEXT")) : (React__default.createElement(Button, { className: "w-full", variant: "primary", size: "base", rounded: "regular", onClick: onClose, id: closeButtonId }, "CLOSE"))) },
            React__default.createElement(StyledItemsContainer, null, pages.map((page, idx) => (React__default.createElement(SideNavItem, { active: idx === currentStep, as: "button", badge: currentStep > idx
                    ? {
                        colorVariant: 'darkGreen',
                        dot: false,
                        children: React__default.createElement(Memo$r, { className: "w-3" }),
                    }
                    : undefined, disabled: navDisabled, icon: page.icon, id: page.label, key: page.label, title: page.label, onClick: () => {
                    setCurrentStep(idx);
                } }))))),
        React__default.createElement(StepsContainer, null, pages[currentStep].page)));
};

const PUBLISH = 0;
const SUBSCRIBE = 1;
const RESET = 2;
const VALUE = 4;
function compose(a, b) {
  return (arg) => a(b(arg));
}
function thrush(arg, proc) {
  return proc(arg);
}
function curry2to1(proc, arg1) {
  return (arg2) => proc(arg1, arg2);
}
function curry1to0(proc, arg) {
  return () => proc(arg);
}
function tap(arg, proc) {
  proc(arg);
  return arg;
}
function tup(...args) {
  return args;
}
function call$1(proc) {
  proc();
}
function always(value) {
  return () => value;
}
function joinProc(...procs) {
  return () => {
    procs.map(call$1);
  };
}
function isDefined(arg) {
  return arg !== void 0;
}
function noop$5() {
}
function subscribe(emitter, subscription) {
  return emitter(SUBSCRIBE, subscription);
}
function publish(publisher, value) {
  publisher(PUBLISH, value);
}
function reset(emitter) {
  emitter(RESET);
}
function getValue$2(depot) {
  return depot(VALUE);
}
function connect(emitter, publisher) {
  return subscribe(emitter, curry2to1(publisher, PUBLISH));
}
function handleNext(emitter, subscription) {
  const unsub = emitter(SUBSCRIBE, (value) => {
    unsub();
    subscription(value);
  });
  return unsub;
}
function stream() {
  const subscriptions = [];
  return (action, arg) => {
    switch (action) {
      case RESET:
        subscriptions.splice(0, subscriptions.length);
        return;
      case SUBSCRIBE:
        subscriptions.push(arg);
        return () => {
          const indexOf = subscriptions.indexOf(arg);
          if (indexOf > -1) {
            subscriptions.splice(indexOf, 1);
          }
        };
      case PUBLISH:
        subscriptions.slice().forEach((subscription) => {
          subscription(arg);
        });
        return;
      default:
        throw new Error(`unrecognized action ${action}`);
    }
  };
}
function statefulStream(initial) {
  let value = initial;
  const innerSubject = stream();
  return (action, arg) => {
    switch (action) {
      case SUBSCRIBE:
        const subscription = arg;
        subscription(value);
        break;
      case PUBLISH:
        value = arg;
        break;
      case VALUE:
        return value;
    }
    return innerSubject(action, arg);
  };
}
function eventHandler(emitter) {
  let unsub;
  let currentSubscription;
  const cleanup = () => unsub && unsub();
  return function(action, subscription) {
    switch (action) {
      case SUBSCRIBE:
        if (subscription) {
          if (currentSubscription === subscription) {
            return;
          }
          cleanup();
          currentSubscription = subscription;
          unsub = subscribe(emitter, subscription);
          return unsub;
        } else {
          cleanup();
          return noop$5;
        }
      case RESET:
        cleanup();
        currentSubscription = null;
        return;
      default:
        throw new Error(`unrecognized action ${action}`);
    }
  };
}
function streamFromEmitter(emitter) {
  return tap(stream(), (stream2) => connect(emitter, stream2));
}
function statefulStreamFromEmitter(emitter, initial) {
  return tap(statefulStream(initial), (stream2) => connect(emitter, stream2));
}
function combineOperators(...operators) {
  return (subscriber) => {
    return operators.reduceRight(thrush, subscriber);
  };
}
function pipe(source, ...operators) {
  const project = combineOperators(...operators);
  return (action, subscription) => {
    switch (action) {
      case SUBSCRIBE:
        return subscribe(source, project(subscription));
      case RESET:
        reset(source);
        return;
    }
  };
}
function defaultComparator(previous, next) {
  return previous === next;
}
function distinctUntilChanged(comparator = defaultComparator) {
  let current;
  return (done) => (next) => {
    if (!comparator(current, next)) {
      current = next;
      done(next);
    }
  };
}
function filter$1(predicate) {
  return (done) => (value) => {
    predicate(value) && done(value);
  };
}
function map$2(project) {
  return (done) => compose(done, project);
}
function mapTo(value) {
  return (done) => () => done(value);
}
function scan(scanner, initial) {
  return (done) => (value) => done(initial = scanner(initial, value));
}
function skip(times) {
  return (done) => (value) => {
    times > 0 ? times-- : done(value);
  };
}
function throttleTime(interval) {
  let currentValue = null;
  let timeout;
  return (done) => (value) => {
    currentValue = value;
    if (timeout) {
      return;
    }
    timeout = setTimeout(() => {
      timeout = void 0;
      done(currentValue);
    }, interval);
  };
}
function debounceTime(interval) {
  let currentValue;
  let timeout;
  return (done) => (value) => {
    currentValue = value;
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      done(currentValue);
    }, interval);
  };
}
function withLatestFrom(...sources) {
  const values = new Array(sources.length);
  let called = 0;
  let pendingCall = null;
  const allCalled = Math.pow(2, sources.length) - 1;
  sources.forEach((source, index) => {
    const bit = Math.pow(2, index);
    subscribe(source, (value) => {
      const prevCalled = called;
      called = called | bit;
      values[index] = value;
      if (prevCalled !== allCalled && called === allCalled && pendingCall) {
        pendingCall();
        pendingCall = null;
      }
    });
  });
  return (done) => (value) => {
    const call2 = () => done([value].concat(values));
    if (called === allCalled) {
      call2();
    } else {
      pendingCall = call2;
    }
  };
}
function merge$1(...sources) {
  return function(action, subscription) {
    switch (action) {
      case SUBSCRIBE:
        return joinProc(...sources.map((source) => subscribe(source, subscription)));
      case RESET:
        return;
      default:
        throw new Error(`unrecognized action ${action}`);
    }
  };
}
function duc(source, comparator = defaultComparator) {
  return pipe(source, distinctUntilChanged(comparator));
}
function combineLatest(...emitters) {
  const innerSubject = stream();
  const values = new Array(emitters.length);
  let called = 0;
  const allCalled = Math.pow(2, emitters.length) - 1;
  emitters.forEach((source, index) => {
    const bit = Math.pow(2, index);
    subscribe(source, (value) => {
      values[index] = value;
      called = called | bit;
      if (called === allCalled) {
        publish(innerSubject, values);
      }
    });
  });
  return function(action, subscription) {
    switch (action) {
      case SUBSCRIBE:
        if (called === allCalled) {
          subscription(values);
        }
        return subscribe(innerSubject, subscription);
      case RESET:
        return reset(innerSubject);
      default:
        throw new Error(`unrecognized action ${action}`);
    }
  };
}
function system(constructor, dependencies = [], { singleton } = { singleton: true }) {
  return {
    id: id(),
    constructor,
    dependencies,
    singleton
  };
}
const id = () => Symbol();
function init(systemSpec) {
  const singletons = /* @__PURE__ */ new Map();
  const _init = ({ id: id2, constructor, dependencies, singleton }) => {
    if (singleton && singletons.has(id2)) {
      return singletons.get(id2);
    }
    const system2 = constructor(dependencies.map((e) => _init(e)));
    if (singleton) {
      singletons.set(id2, system2);
    }
    return system2;
  };
  return _init(systemSpec);
}
function omit$1(keys, obj) {
  const result = {};
  const index = {};
  let idx = 0;
  const len = keys.length;
  while (idx < len) {
    index[keys[idx]] = 1;
    idx += 1;
  }
  for (const prop in obj) {
    if (!index.hasOwnProperty(prop)) {
      result[prop] = obj[prop];
    }
  }
  return result;
}
const useIsomorphicLayoutEffect$2 = typeof document !== "undefined" ? React__default.useLayoutEffect : React__default.useEffect;
function systemToComponent(systemSpec, map2, Root) {
  const requiredPropNames = Object.keys(map2.required || {});
  const optionalPropNames = Object.keys(map2.optional || {});
  const methodNames = Object.keys(map2.methods || {});
  const eventNames = Object.keys(map2.events || {});
  const Context = React__default.createContext({});
  function applyPropsToSystem(system2, props) {
    if (system2["propsReady"]) {
      publish(system2["propsReady"], false);
    }
    for (const requiredPropName of requiredPropNames) {
      const stream2 = system2[map2.required[requiredPropName]];
      publish(stream2, props[requiredPropName]);
    }
    for (const optionalPropName of optionalPropNames) {
      if (optionalPropName in props) {
        const stream2 = system2[map2.optional[optionalPropName]];
        publish(stream2, props[optionalPropName]);
      }
    }
    if (system2["propsReady"]) {
      publish(system2["propsReady"], true);
    }
  }
  function buildMethods(system2) {
    return methodNames.reduce((acc, methodName) => {
      acc[methodName] = (value) => {
        const stream2 = system2[map2.methods[methodName]];
        publish(stream2, value);
      };
      return acc;
    }, {});
  }
  function buildEventHandlers(system2) {
    return eventNames.reduce((handlers, eventName) => {
      handlers[eventName] = eventHandler(system2[map2.events[eventName]]);
      return handlers;
    }, {});
  }
  const Component = React__default.forwardRef((propsWithChildren, ref) => {
    const { children, ...props } = propsWithChildren;
    const [system2] = React__default.useState(() => {
      return tap(init(systemSpec), (system22) => applyPropsToSystem(system22, props));
    });
    const [handlers] = React__default.useState(curry1to0(buildEventHandlers, system2));
    useIsomorphicLayoutEffect$2(() => {
      for (const eventName of eventNames) {
        if (eventName in props) {
          subscribe(handlers[eventName], props[eventName]);
        }
      }
      return () => {
        Object.values(handlers).map(reset);
      };
    }, [props, handlers, system2]);
    useIsomorphicLayoutEffect$2(() => {
      applyPropsToSystem(system2, props);
    });
    React__default.useImperativeHandle(ref, always(buildMethods(system2)));
    return React__default.createElement(
      Context.Provider,
      { value: system2 },
      Root ? React__default.createElement(
        Root,
        omit$1([...requiredPropNames, ...optionalPropNames, ...eventNames], props),
        children
      ) : children
    );
  });
  const usePublisher2 = (key) => {
    return React__default.useCallback(curry2to1(publish, React__default.useContext(Context)[key]), [key]);
  };
  const useEmitterValue18 = (key) => {
    const system2 = React__default.useContext(Context);
    const source = system2[key];
    const cb = React__default.useCallback(
      (c) => {
        return subscribe(source, c);
      },
      [source]
    );
    return React__default.useSyncExternalStore(
      cb,
      () => getValue$2(source),
      () => getValue$2(source)
    );
  };
  const useEmitterValueLegacy = (key) => {
    const system2 = React__default.useContext(Context);
    const source = system2[key];
    const [value, setValue] = React__default.useState(curry1to0(getValue$2, source));
    useIsomorphicLayoutEffect$2(
      () => subscribe(source, (next) => {
        if (next !== value) {
          setValue(always(next));
        }
      }),
      [source, value]
    );
    return value;
  };
  const useEmitterValue2 = React__default.version.startsWith("18") ? useEmitterValue18 : useEmitterValueLegacy;
  const useEmitter2 = (key, callback) => {
    const context = React__default.useContext(Context);
    const source = context[key];
    useIsomorphicLayoutEffect$2(() => subscribe(source, callback), [callback, source]);
  };
  return {
    Component,
    usePublisher: usePublisher2,
    useEmitterValue: useEmitterValue2,
    useEmitter: useEmitter2
  };
}
const useIsomorphicLayoutEffect$1 = typeof document !== "undefined" ? React__default.useLayoutEffect : React__default.useEffect;
const useIsomorphicLayoutEffect$1$1 = useIsomorphicLayoutEffect$1;
var LogLevel = /* @__PURE__ */ ((LogLevel2) => {
  LogLevel2[LogLevel2["DEBUG"] = 0] = "DEBUG";
  LogLevel2[LogLevel2["INFO"] = 1] = "INFO";
  LogLevel2[LogLevel2["WARN"] = 2] = "WARN";
  LogLevel2[LogLevel2["ERROR"] = 3] = "ERROR";
  return LogLevel2;
})(LogLevel || {});
const CONSOLE_METHOD_MAP = {
  [
    0
    /* DEBUG */
  ]: "debug",
  [
    1
    /* INFO */
  ]: "log",
  [
    2
    /* WARN */
  ]: "warn",
  [
    3
    /* ERROR */
  ]: "error"
};
const getGlobalThis = () => typeof globalThis === "undefined" ? window : globalThis;
const loggerSystem = system(
  () => {
    const logLevel = statefulStream(
      3
      /* ERROR */
    );
    const log = statefulStream((label, message, level = 1) => {
      var _a;
      const currentLevel = (_a = getGlobalThis()["VIRTUOSO_LOG_LEVEL"]) != null ? _a : getValue$2(logLevel);
      if (level >= currentLevel) {
        console[CONSOLE_METHOD_MAP[level]](
          "%creact-virtuoso: %c%s %o",
          "color: #0253b3; font-weight: bold",
          "color: initial",
          label,
          message
        );
      }
    });
    return {
      log,
      logLevel
    };
  },
  [],
  { singleton: true }
);
function useSizeWithElRef(callback, enabled, skipAnimationFrame) {
  const ref = React__default.useRef(null);
  let callbackRef = (_el) => {
  };
  if (typeof ResizeObserver !== "undefined") {
    const observer = React__default.useMemo(() => {
      return new ResizeObserver((entries) => {
        const code = () => {
          const element = entries[0].target;
          if (element.offsetParent !== null) {
            callback(element);
          }
        };
        skipAnimationFrame ? code() : requestAnimationFrame(code);
      });
    }, [callback]);
    callbackRef = (elRef) => {
      if (elRef && enabled) {
        observer.observe(elRef);
        ref.current = elRef;
      } else {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
        ref.current = null;
      }
    };
  }
  return { ref, callbackRef };
}
function useSize(callback, enabled, skipAnimationFrame) {
  return useSizeWithElRef(callback, enabled, skipAnimationFrame).callbackRef;
}
function useChangedListContentsSizes(callback, itemSize, enabled, scrollContainerStateCallback, log, gap, customScrollParent, horizontalDirection, skipAnimationFrame) {
  const memoedCallback = React__default.useCallback(
    (el) => {
      const ranges = getChangedChildSizes(el.children, itemSize, horizontalDirection ? "offsetWidth" : "offsetHeight", log);
      let scrollableElement = el.parentElement;
      while (!scrollableElement.dataset["virtuosoScroller"]) {
        scrollableElement = scrollableElement.parentElement;
      }
      const windowScrolling = scrollableElement.lastElementChild.dataset["viewportType"] === "window";
      const scrollTop = customScrollParent ? horizontalDirection ? customScrollParent.scrollLeft : customScrollParent.scrollTop : windowScrolling ? horizontalDirection ? window.pageXOffset || document.documentElement.scrollLeft : window.pageYOffset || document.documentElement.scrollTop : horizontalDirection ? scrollableElement.scrollLeft : scrollableElement.scrollTop;
      const scrollHeight = customScrollParent ? horizontalDirection ? customScrollParent.scrollWidth : customScrollParent.scrollHeight : windowScrolling ? horizontalDirection ? document.documentElement.scrollWidth : document.documentElement.scrollHeight : horizontalDirection ? scrollableElement.scrollWidth : scrollableElement.scrollHeight;
      const viewportHeight = customScrollParent ? horizontalDirection ? customScrollParent.offsetWidth : customScrollParent.offsetHeight : windowScrolling ? horizontalDirection ? window.innerWidth : window.innerHeight : horizontalDirection ? scrollableElement.offsetWidth : scrollableElement.offsetHeight;
      scrollContainerStateCallback({
        scrollTop: Math.max(scrollTop, 0),
        scrollHeight,
        viewportHeight
      });
      gap == null ? void 0 : gap(
        horizontalDirection ? resolveGapValue$1("column-gap", getComputedStyle(el).columnGap, log) : resolveGapValue$1("row-gap", getComputedStyle(el).rowGap, log)
      );
      if (ranges !== null) {
        callback(ranges);
      }
    },
    [callback, itemSize, log, gap, customScrollParent, scrollContainerStateCallback]
  );
  return useSizeWithElRef(memoedCallback, enabled, skipAnimationFrame);
}
function getChangedChildSizes(children, itemSize, field, log) {
  const length = children.length;
  if (length === 0) {
    return null;
  }
  const results = [];
  for (let i = 0; i < length; i++) {
    const child = children.item(i);
    if (!child || child.dataset.index === void 0) {
      continue;
    }
    const index = parseInt(child.dataset.index);
    const knownSize = parseFloat(child.dataset.knownSize);
    const size = itemSize(child, field);
    if (size === 0) {
      log("Zero-sized element, this should not happen", { child }, LogLevel.ERROR);
    }
    if (size === knownSize) {
      continue;
    }
    const lastResult = results[results.length - 1];
    if (results.length === 0 || lastResult.size !== size || lastResult.endIndex !== index - 1) {
      results.push({ startIndex: index, endIndex: index, size });
    } else {
      results[results.length - 1].endIndex++;
    }
  }
  return results;
}
function resolveGapValue$1(property, value, log) {
  if (value !== "normal" && !(value == null ? void 0 : value.endsWith("px"))) {
    log(`${property} was not resolved to pixel value correctly`, value, LogLevel.WARN);
  }
  if (value === "normal") {
    return 0;
  }
  return parseInt(value != null ? value : "0", 10);
}
function correctItemSize(el, dimension) {
  return Math.round(el.getBoundingClientRect()[dimension]);
}
function approximatelyEqual(num1, num2) {
  return Math.abs(num1 - num2) < 1.01;
}
function useScrollTop(scrollContainerStateCallback, smoothScrollTargetReached, scrollerElement, scrollerRefCallback = noop$5, customScrollParent, horizontalDirection) {
  const scrollerRef = React__default.useRef(null);
  const scrollTopTarget = React__default.useRef(null);
  const timeoutRef = React__default.useRef(null);
  const handler = React__default.useCallback(
    (ev) => {
      const el = ev.target;
      const windowScroll = el === window || el === document;
      const scrollTop = horizontalDirection ? windowScroll ? window.pageXOffset || document.documentElement.scrollLeft : el.scrollLeft : windowScroll ? window.pageYOffset || document.documentElement.scrollTop : el.scrollTop;
      const scrollHeight = horizontalDirection ? windowScroll ? document.documentElement.scrollWidth : el.scrollWidth : windowScroll ? document.documentElement.scrollHeight : el.scrollHeight;
      const viewportHeight = horizontalDirection ? windowScroll ? window.innerWidth : el.offsetWidth : windowScroll ? window.innerHeight : el.offsetHeight;
      const call2 = () => {
        scrollContainerStateCallback({
          scrollTop: Math.max(scrollTop, 0),
          scrollHeight,
          viewportHeight
        });
      };
      if (ev.suppressFlushSync) {
        call2();
      } else {
        ReactDOM.flushSync(call2);
      }
      if (scrollTopTarget.current !== null) {
        if (scrollTop === scrollTopTarget.current || scrollTop <= 0 || scrollTop === scrollHeight - viewportHeight) {
          scrollTopTarget.current = null;
          smoothScrollTargetReached(true);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }
      }
    },
    [scrollContainerStateCallback, smoothScrollTargetReached]
  );
  React__default.useEffect(() => {
    const localRef = customScrollParent ? customScrollParent : scrollerRef.current;
    scrollerRefCallback(customScrollParent ? customScrollParent : scrollerRef.current);
    handler({ target: localRef, suppressFlushSync: true });
    localRef.addEventListener("scroll", handler, { passive: true });
    return () => {
      scrollerRefCallback(null);
      localRef.removeEventListener("scroll", handler);
    };
  }, [scrollerRef, handler, scrollerElement, scrollerRefCallback, customScrollParent]);
  function scrollToCallback(location) {
    const scrollerElement2 = scrollerRef.current;
    if (!scrollerElement2 || (horizontalDirection ? "offsetWidth" in scrollerElement2 && scrollerElement2.offsetWidth === 0 : "offsetHeight" in scrollerElement2 && scrollerElement2.offsetHeight === 0)) {
      return;
    }
    const isSmooth = location.behavior === "smooth";
    let offsetHeight;
    let scrollHeight;
    let scrollTop;
    if (scrollerElement2 === window) {
      scrollHeight = Math.max(
        correctItemSize(document.documentElement, horizontalDirection ? "width" : "height"),
        horizontalDirection ? document.documentElement.scrollWidth : document.documentElement.scrollHeight
      );
      offsetHeight = horizontalDirection ? window.innerWidth : window.innerHeight;
      scrollTop = horizontalDirection ? document.documentElement.scrollLeft : document.documentElement.scrollTop;
    } else {
      scrollHeight = scrollerElement2[horizontalDirection ? "scrollWidth" : "scrollHeight"];
      offsetHeight = correctItemSize(scrollerElement2, horizontalDirection ? "width" : "height");
      scrollTop = scrollerElement2[horizontalDirection ? "scrollLeft" : "scrollTop"];
    }
    const maxScrollTop = scrollHeight - offsetHeight;
    location.top = Math.ceil(Math.max(Math.min(maxScrollTop, location.top), 0));
    if (approximatelyEqual(offsetHeight, scrollHeight) || location.top === scrollTop) {
      scrollContainerStateCallback({ scrollTop, scrollHeight, viewportHeight: offsetHeight });
      if (isSmooth) {
        smoothScrollTargetReached(true);
      }
      return;
    }
    if (isSmooth) {
      scrollTopTarget.current = location.top;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        scrollTopTarget.current = null;
        smoothScrollTargetReached(true);
      }, 1e3);
    } else {
      scrollTopTarget.current = null;
    }
    if (horizontalDirection) {
      location = { left: location.top, behavior: location.behavior };
    }
    scrollerElement2.scrollTo(location);
  }
  function scrollByCallback(location) {
    if (horizontalDirection) {
      location = { left: location.top, behavior: location.behavior };
    }
    scrollerRef.current.scrollBy(location);
  }
  return { scrollerRef, scrollByCallback, scrollToCallback };
}
const domIOSystem = system(
  () => {
    const scrollContainerState = stream();
    const scrollTop = stream();
    const deviation = statefulStream(0);
    const smoothScrollTargetReached = stream();
    const statefulScrollTop = statefulStream(0);
    const viewportHeight = stream();
    const scrollHeight = stream();
    const headerHeight = statefulStream(0);
    const fixedHeaderHeight = statefulStream(0);
    const fixedFooterHeight = statefulStream(0);
    const footerHeight = statefulStream(0);
    const scrollTo = stream();
    const scrollBy = stream();
    const scrollingInProgress = statefulStream(false);
    const horizontalDirection = statefulStream(false);
    const skipAnimationFrameInResizeObserver = statefulStream(false);
    connect(
      pipe(
        scrollContainerState,
        map$2(({ scrollTop: scrollTop2 }) => scrollTop2)
      ),
      scrollTop
    );
    connect(
      pipe(
        scrollContainerState,
        map$2(({ scrollHeight: scrollHeight2 }) => scrollHeight2)
      ),
      scrollHeight
    );
    connect(scrollTop, statefulScrollTop);
    return {
      // input
      scrollContainerState,
      scrollTop,
      viewportHeight,
      headerHeight,
      fixedHeaderHeight,
      fixedFooterHeight,
      footerHeight,
      scrollHeight,
      smoothScrollTargetReached,
      horizontalDirection,
      skipAnimationFrameInResizeObserver,
      // signals
      scrollTo,
      scrollBy,
      // state
      statefulScrollTop,
      deviation,
      scrollingInProgress
    };
  },
  [],
  { singleton: true }
);
const NIL_NODE = { lvl: 0 };
function newAANode(k, v, lvl, l = NIL_NODE, r = NIL_NODE) {
  return { k, v, lvl, l, r };
}
function empty(node) {
  return node === NIL_NODE;
}
function newTree() {
  return NIL_NODE;
}
function remove(node, key) {
  if (empty(node))
    return NIL_NODE;
  const { k, l, r } = node;
  if (key === k) {
    if (empty(l)) {
      return r;
    } else if (empty(r)) {
      return l;
    } else {
      const [lastKey, lastValue] = last$2(l);
      return adjust(clone(node, { k: lastKey, v: lastValue, l: deleteLast(l) }));
    }
  } else if (key < k) {
    return adjust(clone(node, { l: remove(l, key) }));
  } else {
    return adjust(clone(node, { r: remove(r, key) }));
  }
}
function find(node, key) {
  if (empty(node)) {
    return;
  }
  if (key === node.k) {
    return node.v;
  } else if (key < node.k) {
    return find(node.l, key);
  } else {
    return find(node.r, key);
  }
}
function findMaxKeyValue(node, value, field = "k") {
  if (empty(node)) {
    return [-Infinity, void 0];
  }
  if (Number(node[field]) === value) {
    return [node.k, node.v];
  }
  if (Number(node[field]) < value) {
    const r = findMaxKeyValue(node.r, value, field);
    if (r[0] === -Infinity) {
      return [node.k, node.v];
    } else {
      return r;
    }
  }
  return findMaxKeyValue(node.l, value, field);
}
function insert(node, k, v) {
  if (empty(node)) {
    return newAANode(k, v, 1);
  }
  if (k === node.k) {
    return clone(node, { k, v });
  } else if (k < node.k) {
    return rebalance(clone(node, { l: insert(node.l, k, v) }));
  } else {
    return rebalance(clone(node, { r: insert(node.r, k, v) }));
  }
}
function walkWithin(node, start, end) {
  if (empty(node)) {
    return [];
  }
  const { k, v, l, r } = node;
  let result = [];
  if (k > start) {
    result = result.concat(walkWithin(l, start, end));
  }
  if (k >= start && k <= end) {
    result.push({ k, v });
  }
  if (k <= end) {
    result = result.concat(walkWithin(r, start, end));
  }
  return result;
}
function walk(node) {
  if (empty(node)) {
    return [];
  }
  return [...walk(node.l), { k: node.k, v: node.v }, ...walk(node.r)];
}
function last$2(node) {
  return empty(node.r) ? [node.k, node.v] : last$2(node.r);
}
function deleteLast(node) {
  return empty(node.r) ? node.l : adjust(clone(node, { r: deleteLast(node.r) }));
}
function clone(node, args) {
  return newAANode(
    args.k !== void 0 ? args.k : node.k,
    args.v !== void 0 ? args.v : node.v,
    args.lvl !== void 0 ? args.lvl : node.lvl,
    args.l !== void 0 ? args.l : node.l,
    args.r !== void 0 ? args.r : node.r
  );
}
function isSingle(node) {
  return empty(node) || node.lvl > node.r.lvl;
}
function rebalance(node) {
  return split(skew(node));
}
function adjust(node) {
  const { l, r, lvl } = node;
  if (r.lvl >= lvl - 1 && l.lvl >= lvl - 1) {
    return node;
  } else if (lvl > r.lvl + 1) {
    if (isSingle(l)) {
      return skew(clone(node, { lvl: lvl - 1 }));
    } else {
      if (!empty(l) && !empty(l.r)) {
        return clone(l.r, {
          l: clone(l, { r: l.r.l }),
          r: clone(node, {
            l: l.r.r,
            lvl: lvl - 1
          }),
          lvl
        });
      } else {
        throw new Error("Unexpected empty nodes");
      }
    }
  } else {
    if (isSingle(node)) {
      return split(clone(node, { lvl: lvl - 1 }));
    } else {
      if (!empty(r) && !empty(r.l)) {
        const rl = r.l;
        const rlvl = isSingle(rl) ? r.lvl - 1 : r.lvl;
        return clone(rl, {
          l: clone(node, {
            r: rl.l,
            lvl: lvl - 1
          }),
          r: split(clone(r, { l: rl.r, lvl: rlvl })),
          lvl: rl.lvl + 1
        });
      } else {
        throw new Error("Unexpected empty nodes");
      }
    }
  }
}
function rangesWithin(node, startIndex, endIndex) {
  if (empty(node)) {
    return [];
  }
  const adjustedStart = findMaxKeyValue(node, startIndex)[0];
  return toRanges(walkWithin(node, adjustedStart, endIndex));
}
function arrayToRanges(items, parser) {
  const length = items.length;
  if (length === 0) {
    return [];
  }
  let { index: start, value } = parser(items[0]);
  const result = [];
  for (let i = 1; i < length; i++) {
    const { index: nextIndex, value: nextValue } = parser(items[i]);
    result.push({ start, end: nextIndex - 1, value });
    start = nextIndex;
    value = nextValue;
  }
  result.push({ start, end: Infinity, value });
  return result;
}
function toRanges(nodes) {
  return arrayToRanges(nodes, ({ k: index, v: value }) => ({ index, value }));
}
function split(node) {
  const { r, lvl } = node;
  return !empty(r) && !empty(r.r) && r.lvl === lvl && r.r.lvl === lvl ? clone(r, { l: clone(node, { r: r.l }), lvl: lvl + 1 }) : node;
}
function skew(node) {
  const { l } = node;
  return !empty(l) && l.lvl === node.lvl ? clone(l, { r: clone(node, { l: l.r }) }) : node;
}
function findIndexOfClosestSmallerOrEqual(items, value, comparator, start = 0) {
  let end = items.length - 1;
  while (start <= end) {
    const index = Math.floor((start + end) / 2);
    const item = items[index];
    const match = comparator(item, value);
    if (match === 0) {
      return index;
    }
    if (match === -1) {
      if (end - start < 2) {
        return index - 1;
      }
      end = index - 1;
    } else {
      if (end === start) {
        return index;
      }
      start = index + 1;
    }
  }
  throw new Error(`Failed binary finding record in array - ${items.join(",")}, searched for ${value}`);
}
function findClosestSmallerOrEqual(items, value, comparator) {
  return items[findIndexOfClosestSmallerOrEqual(items, value, comparator)];
}
function findRange$1(items, startValue, endValue, comparator) {
  const startIndex = findIndexOfClosestSmallerOrEqual(items, startValue, comparator);
  const endIndex = findIndexOfClosestSmallerOrEqual(items, endValue, comparator, startIndex);
  return items.slice(startIndex, endIndex + 1);
}
const recalcSystem = system(
  () => {
    const recalcInProgress = statefulStream(false);
    return { recalcInProgress };
  },
  [],
  { singleton: true }
);
function rangeIncludes(refRange) {
  const { size, startIndex, endIndex } = refRange;
  return (range) => {
    return range.start === startIndex && (range.end === endIndex || range.end === Infinity) && range.value === size;
  };
}
function affectedGroupCount(offset, groupIndices) {
  let recognizedOffsetItems = 0;
  let groupIndex = 0;
  while (recognizedOffsetItems < offset) {
    recognizedOffsetItems += groupIndices[groupIndex + 1] - groupIndices[groupIndex] - 1;
    groupIndex++;
  }
  const offsetIsExact = recognizedOffsetItems === offset;
  return groupIndex - (offsetIsExact ? 0 : 1);
}
function insertRanges(sizeTree, ranges) {
  let syncStart = empty(sizeTree) ? 0 : Infinity;
  for (const range of ranges) {
    const { size, startIndex, endIndex } = range;
    syncStart = Math.min(syncStart, startIndex);
    if (empty(sizeTree)) {
      sizeTree = insert(sizeTree, 0, size);
      continue;
    }
    const overlappingRanges = rangesWithin(sizeTree, startIndex - 1, endIndex + 1);
    if (overlappingRanges.some(rangeIncludes(range))) {
      continue;
    }
    let firstPassDone = false;
    let shouldInsert = false;
    for (const { start: rangeStart, end: rangeEnd, value: rangeValue } of overlappingRanges) {
      if (!firstPassDone) {
        shouldInsert = rangeValue !== size;
        firstPassDone = true;
      } else {
        if (endIndex >= rangeStart || size === rangeValue) {
          sizeTree = remove(sizeTree, rangeStart);
        }
      }
      if (rangeEnd > endIndex && endIndex >= rangeStart) {
        if (rangeValue !== size) {
          sizeTree = insert(sizeTree, endIndex + 1, rangeValue);
        }
      }
    }
    if (shouldInsert) {
      sizeTree = insert(sizeTree, startIndex, size);
    }
  }
  return [sizeTree, syncStart];
}
function initialSizeState() {
  return {
    offsetTree: [],
    sizeTree: newTree(),
    groupOffsetTree: newTree(),
    lastIndex: 0,
    lastOffset: 0,
    lastSize: 0,
    groupIndices: []
  };
}
function indexComparator({ index: itemIndex }, index) {
  return index === itemIndex ? 0 : index < itemIndex ? -1 : 1;
}
function offsetComparator({ offset: itemOffset }, offset) {
  return offset === itemOffset ? 0 : offset < itemOffset ? -1 : 1;
}
function offsetPointParser(point) {
  return { index: point.index, value: point };
}
function rangesWithinOffsets(tree, startOffset, endOffset, minStartIndex = 0) {
  if (minStartIndex > 0) {
    startOffset = Math.max(startOffset, findClosestSmallerOrEqual(tree, minStartIndex, indexComparator).offset);
  }
  return arrayToRanges(findRange$1(tree, startOffset, endOffset, offsetComparator), offsetPointParser);
}
function createOffsetTree(prevOffsetTree, syncStart, sizeTree, gap) {
  let offsetTree = prevOffsetTree;
  let prevIndex = 0;
  let prevSize = 0;
  let prevOffset = 0;
  let startIndex = 0;
  if (syncStart !== 0) {
    startIndex = findIndexOfClosestSmallerOrEqual(offsetTree, syncStart - 1, indexComparator);
    const offsetInfo = offsetTree[startIndex];
    prevOffset = offsetInfo.offset;
    const kv = findMaxKeyValue(sizeTree, syncStart - 1);
    prevIndex = kv[0];
    prevSize = kv[1];
    if (offsetTree.length && offsetTree[startIndex].size === findMaxKeyValue(sizeTree, syncStart)[1]) {
      startIndex -= 1;
    }
    offsetTree = offsetTree.slice(0, startIndex + 1);
  } else {
    offsetTree = [];
  }
  for (const { start: startIndex2, value } of rangesWithin(sizeTree, syncStart, Infinity)) {
    const indexOffset = startIndex2 - prevIndex;
    const aOffset = indexOffset * prevSize + prevOffset + indexOffset * gap;
    offsetTree.push({
      offset: aOffset,
      size: value,
      index: startIndex2
    });
    prevIndex = startIndex2;
    prevOffset = aOffset;
    prevSize = value;
  }
  return {
    offsetTree,
    lastIndex: prevIndex,
    lastOffset: prevOffset,
    lastSize: prevSize
  };
}
function sizeStateReducer(state, [ranges, groupIndices, log, gap]) {
  if (ranges.length > 0) {
    log("received item sizes", ranges, LogLevel.DEBUG);
  }
  const sizeTree = state.sizeTree;
  let newSizeTree = sizeTree;
  let syncStart = 0;
  if (groupIndices.length > 0 && empty(sizeTree) && ranges.length === 2) {
    const groupSize = ranges[0].size;
    const itemSize = ranges[1].size;
    newSizeTree = groupIndices.reduce((tree, groupIndex) => {
      return insert(insert(tree, groupIndex, groupSize), groupIndex + 1, itemSize);
    }, newSizeTree);
  } else {
    [newSizeTree, syncStart] = insertRanges(newSizeTree, ranges);
  }
  if (newSizeTree === sizeTree) {
    return state;
  }
  const { offsetTree: newOffsetTree, lastIndex, lastSize, lastOffset } = createOffsetTree(state.offsetTree, syncStart, newSizeTree, gap);
  return {
    sizeTree: newSizeTree,
    offsetTree: newOffsetTree,
    lastIndex,
    lastOffset,
    lastSize,
    groupOffsetTree: groupIndices.reduce((tree, index) => {
      return insert(tree, index, offsetOf(index, newOffsetTree, gap));
    }, newTree()),
    groupIndices
  };
}
function offsetOf(index, tree, gap) {
  if (tree.length === 0) {
    return 0;
  }
  const { offset, index: startIndex, size } = findClosestSmallerOrEqual(tree, index, indexComparator);
  const itemCount = index - startIndex;
  const top = size * itemCount + (itemCount - 1) * gap + offset;
  return top > 0 ? top + gap : top;
}
function isGroupLocation(location) {
  return typeof location.groupIndex !== "undefined";
}
function originalIndexFromLocation(location, sizes, lastIndex) {
  if (isGroupLocation(location)) {
    return sizes.groupIndices[location.groupIndex] + 1;
  } else {
    const numericIndex = location.index === "LAST" ? lastIndex : location.index;
    let result = originalIndexFromItemIndex(numericIndex, sizes);
    result = Math.max(0, result, Math.min(lastIndex, result));
    return result;
  }
}
function originalIndexFromItemIndex(itemIndex, sizes) {
  if (!hasGroups(sizes)) {
    return itemIndex;
  }
  let groupOffset = 0;
  while (sizes.groupIndices[groupOffset] <= itemIndex + groupOffset) {
    groupOffset++;
  }
  return itemIndex + groupOffset;
}
function hasGroups(sizes) {
  return !empty(sizes.groupOffsetTree);
}
function sizeTreeToRanges(sizeTree) {
  return walk(sizeTree).map(({ k: startIndex, v: size }, index, sizeArray) => {
    const nextSize = sizeArray[index + 1];
    const endIndex = nextSize ? nextSize.k - 1 : Infinity;
    return { startIndex, endIndex, size };
  });
}
const SIZE_MAP = {
  offsetHeight: "height",
  offsetWidth: "width"
};
const sizeSystem = system(
  ([{ log }, { recalcInProgress }]) => {
    const sizeRanges = stream();
    const totalCount = stream();
    const statefulTotalCount = statefulStreamFromEmitter(totalCount, 0);
    const unshiftWith = stream();
    const shiftWith = stream();
    const firstItemIndex = statefulStream(0);
    const groupIndices = statefulStream([]);
    const fixedItemSize = statefulStream(void 0);
    const defaultItemSize = statefulStream(void 0);
    const itemSize = statefulStream((el, field) => correctItemSize(el, SIZE_MAP[field]));
    const data = statefulStream(void 0);
    const gap = statefulStream(0);
    const initial = initialSizeState();
    const sizes = statefulStreamFromEmitter(
      pipe(sizeRanges, withLatestFrom(groupIndices, log, gap), scan(sizeStateReducer, initial), distinctUntilChanged()),
      initial
    );
    const prevGroupIndices = statefulStreamFromEmitter(
      pipe(
        groupIndices,
        distinctUntilChanged(),
        scan((prev, curr) => ({ prev: prev.current, current: curr }), {
          prev: [],
          current: []
        }),
        map$2(({ prev }) => prev)
      ),
      []
    );
    connect(
      pipe(
        groupIndices,
        filter$1((indexes) => indexes.length > 0),
        withLatestFrom(sizes, gap),
        map$2(([groupIndices2, sizes2, gap2]) => {
          const groupOffsetTree = groupIndices2.reduce((tree, index, idx) => {
            return insert(tree, index, offsetOf(index, sizes2.offsetTree, gap2) || idx);
          }, newTree());
          return {
            ...sizes2,
            groupIndices: groupIndices2,
            groupOffsetTree
          };
        })
      ),
      sizes
    );
    connect(
      pipe(
        totalCount,
        withLatestFrom(sizes),
        filter$1(([totalCount2, { lastIndex }]) => {
          return totalCount2 < lastIndex;
        }),
        map$2(([totalCount2, { lastIndex, lastSize }]) => {
          return [
            {
              startIndex: totalCount2,
              endIndex: lastIndex,
              size: lastSize
            }
          ];
        })
      ),
      sizeRanges
    );
    connect(fixedItemSize, defaultItemSize);
    const trackItemSizes = statefulStreamFromEmitter(
      pipe(
        fixedItemSize,
        map$2((size) => size === void 0)
      ),
      true
    );
    connect(
      pipe(
        defaultItemSize,
        filter$1((value) => {
          return value !== void 0 && empty(getValue$2(sizes).sizeTree);
        }),
        map$2((size) => [{ startIndex: 0, endIndex: 0, size }])
      ),
      sizeRanges
    );
    const listRefresh = streamFromEmitter(
      pipe(
        sizeRanges,
        withLatestFrom(sizes),
        scan(
          ({ sizes: oldSizes }, [_, newSizes]) => {
            return {
              changed: newSizes !== oldSizes,
              sizes: newSizes
            };
          },
          { changed: false, sizes: initial }
        ),
        map$2((value) => value.changed)
      )
    );
    subscribe(
      pipe(
        firstItemIndex,
        scan(
          (prev, next) => {
            return { diff: prev.prev - next, prev: next };
          },
          { diff: 0, prev: 0 }
        ),
        map$2((val) => val.diff)
      ),
      (offset) => {
        const { groupIndices: groupIndices2 } = getValue$2(sizes);
        if (offset > 0) {
          publish(recalcInProgress, true);
          publish(unshiftWith, offset + affectedGroupCount(offset, groupIndices2));
        } else if (offset < 0) {
          const prevGroupIndicesValue = getValue$2(prevGroupIndices);
          if (prevGroupIndicesValue.length > 0) {
            offset -= affectedGroupCount(-offset, prevGroupIndicesValue);
          }
          publish(shiftWith, offset);
        }
      }
    );
    subscribe(pipe(firstItemIndex, withLatestFrom(log)), ([index, log2]) => {
      if (index < 0) {
        log2(
          "`firstItemIndex` prop should not be set to less than zero. If you don't know the total count, just use a very high value",
          { firstItemIndex },
          LogLevel.ERROR
        );
      }
    });
    const beforeUnshiftWith = streamFromEmitter(unshiftWith);
    connect(
      pipe(
        unshiftWith,
        withLatestFrom(sizes),
        map$2(([unshiftWith2, sizes2]) => {
          const groupedMode = sizes2.groupIndices.length > 0;
          const initialRanges = [];
          const defaultSize = sizes2.lastSize;
          if (groupedMode) {
            const firstGroupSize = find(sizes2.sizeTree, 0);
            let prependedGroupItemsCount = 0;
            let groupIndex = 0;
            while (prependedGroupItemsCount < unshiftWith2) {
              const theGroupIndex = sizes2.groupIndices[groupIndex];
              const groupItemCount = sizes2.groupIndices.length === groupIndex + 1 ? Infinity : sizes2.groupIndices[groupIndex + 1] - theGroupIndex - 1;
              initialRanges.push({
                startIndex: theGroupIndex,
                endIndex: theGroupIndex,
                size: firstGroupSize
              });
              initialRanges.push({
                startIndex: theGroupIndex + 1,
                endIndex: theGroupIndex + 1 + groupItemCount - 1,
                size: defaultSize
              });
              groupIndex++;
              prependedGroupItemsCount += groupItemCount + 1;
            }
            const sizeTreeKV = walk(sizes2.sizeTree);
            const firstGroupIsExpanded = prependedGroupItemsCount !== unshiftWith2;
            if (firstGroupIsExpanded) {
              sizeTreeKV.shift();
            }
            return sizeTreeKV.reduce(
              (acc, { k: index, v: size }) => {
                let ranges = acc.ranges;
                if (acc.prevSize !== 0) {
                  ranges = [
                    ...acc.ranges,
                    {
                      startIndex: acc.prevIndex,
                      endIndex: index + unshiftWith2 - 1,
                      size: acc.prevSize
                    }
                  ];
                }
                return {
                  ranges,
                  prevIndex: index + unshiftWith2,
                  prevSize: size
                };
              },
              {
                ranges: initialRanges,
                prevIndex: unshiftWith2,
                prevSize: 0
              }
            ).ranges;
          }
          return walk(sizes2.sizeTree).reduce(
            (acc, { k: index, v: size }) => {
              return {
                ranges: [...acc.ranges, { startIndex: acc.prevIndex, endIndex: index + unshiftWith2 - 1, size: acc.prevSize }],
                prevIndex: index + unshiftWith2,
                prevSize: size
              };
            },
            {
              ranges: [],
              prevIndex: 0,
              prevSize: defaultSize
            }
          ).ranges;
        })
      ),
      sizeRanges
    );
    const shiftWithOffset = streamFromEmitter(
      pipe(
        shiftWith,
        withLatestFrom(sizes, gap),
        map$2(([shiftWith2, { offsetTree }, gap2]) => {
          const newFirstItemIndex = -shiftWith2;
          return offsetOf(newFirstItemIndex, offsetTree, gap2);
        })
      )
    );
    connect(
      pipe(
        shiftWith,
        withLatestFrom(sizes, gap),
        map$2(([shiftWith2, sizes2, gap2]) => {
          const groupedMode = sizes2.groupIndices.length > 0;
          if (groupedMode) {
            if (empty(sizes2.sizeTree)) {
              return sizes2;
            }
            let newSizeTree = newTree();
            const prevGroupIndicesValue = getValue$2(prevGroupIndices);
            let removedItemsCount = 0;
            let groupIndex = 0;
            let groupOffset = 0;
            while (removedItemsCount < -shiftWith2) {
              groupOffset = prevGroupIndicesValue[groupIndex];
              const groupItemCount = prevGroupIndicesValue[groupIndex + 1] - groupOffset - 1;
              groupIndex++;
              removedItemsCount += groupItemCount + 1;
            }
            newSizeTree = walk(sizes2.sizeTree).reduce((acc, { k, v }) => {
              return insert(acc, Math.max(0, k + shiftWith2), v);
            }, newSizeTree);
            const aGroupIsShrunk = removedItemsCount !== -shiftWith2;
            if (aGroupIsShrunk) {
              const firstGroupSize = find(sizes2.sizeTree, groupOffset);
              newSizeTree = insert(newSizeTree, 0, firstGroupSize);
              const nextItemSize = findMaxKeyValue(sizes2.sizeTree, -shiftWith2 + 1)[1];
              newSizeTree = insert(newSizeTree, 1, nextItemSize);
            }
            return {
              ...sizes2,
              sizeTree: newSizeTree,
              ...createOffsetTree(sizes2.offsetTree, 0, newSizeTree, gap2)
            };
          } else {
            const newSizeTree = walk(sizes2.sizeTree).reduce((acc, { k, v }) => {
              return insert(acc, Math.max(0, k + shiftWith2), v);
            }, newTree());
            return {
              ...sizes2,
              sizeTree: newSizeTree,
              ...createOffsetTree(sizes2.offsetTree, 0, newSizeTree, gap2)
            };
          }
        })
      ),
      sizes
    );
    return {
      // input
      data,
      totalCount,
      sizeRanges,
      groupIndices,
      defaultItemSize,
      fixedItemSize,
      unshiftWith,
      shiftWith,
      shiftWithOffset,
      beforeUnshiftWith,
      firstItemIndex,
      gap,
      // output
      sizes,
      listRefresh,
      statefulTotalCount,
      trackItemSizes,
      itemSize
    };
  },
  tup(loggerSystem, recalcSystem),
  { singleton: true }
);
const SUPPORTS_SCROLL_TO_OPTIONS = typeof document !== "undefined" && "scrollBehavior" in document.documentElement.style;
function normalizeIndexLocation(location) {
  const result = typeof location === "number" ? { index: location } : location;
  if (!result.align) {
    result.align = "start";
  }
  if (!result.behavior || !SUPPORTS_SCROLL_TO_OPTIONS) {
    result.behavior = "auto";
  }
  if (!result.offset) {
    result.offset = 0;
  }
  return result;
}
const scrollToIndexSystem = system(
  ([
    { sizes, totalCount, listRefresh, gap },
    {
      scrollingInProgress,
      viewportHeight,
      scrollTo,
      smoothScrollTargetReached,
      headerHeight,
      footerHeight,
      fixedHeaderHeight,
      fixedFooterHeight
    },
    { log }
  ]) => {
    const scrollToIndex = stream();
    const scrollTargetReached = stream();
    const topListHeight = statefulStream(0);
    let unsubscribeNextListRefresh = null;
    let cleartTimeoutRef = null;
    let unsubscribeListRefresh = null;
    function cleanup() {
      if (unsubscribeNextListRefresh) {
        unsubscribeNextListRefresh();
        unsubscribeNextListRefresh = null;
      }
      if (unsubscribeListRefresh) {
        unsubscribeListRefresh();
        unsubscribeListRefresh = null;
      }
      if (cleartTimeoutRef) {
        clearTimeout(cleartTimeoutRef);
        cleartTimeoutRef = null;
      }
      publish(scrollingInProgress, false);
    }
    connect(
      pipe(
        scrollToIndex,
        withLatestFrom(sizes, viewportHeight, totalCount, topListHeight, headerHeight, footerHeight, log),
        withLatestFrom(gap, fixedHeaderHeight, fixedFooterHeight),
        map$2(
          ([
            [location, sizes2, viewportHeight2, totalCount2, topListHeight2, headerHeight2, footerHeight2, log2],
            gap2,
            fixedHeaderHeight2,
            fixedFooterHeight2
          ]) => {
            const normalLocation = normalizeIndexLocation(location);
            const { align, behavior, offset } = normalLocation;
            const lastIndex = totalCount2 - 1;
            const index = originalIndexFromLocation(normalLocation, sizes2, lastIndex);
            let top = offsetOf(index, sizes2.offsetTree, gap2) + headerHeight2;
            if (align === "end") {
              top += fixedHeaderHeight2 + findMaxKeyValue(sizes2.sizeTree, index)[1] - viewportHeight2 + fixedFooterHeight2;
              if (index === lastIndex) {
                top += footerHeight2;
              }
            } else if (align === "center") {
              top += (fixedHeaderHeight2 + findMaxKeyValue(sizes2.sizeTree, index)[1] - viewportHeight2 + fixedFooterHeight2) / 2;
            } else {
              top -= topListHeight2;
            }
            if (offset) {
              top += offset;
            }
            const retry = (listChanged) => {
              cleanup();
              if (listChanged) {
                log2("retrying to scroll to", { location }, LogLevel.DEBUG);
                publish(scrollToIndex, location);
              } else {
                publish(scrollTargetReached, true);
                log2("list did not change, scroll successful", {}, LogLevel.DEBUG);
              }
            };
            cleanup();
            if (behavior === "smooth") {
              let listChanged = false;
              unsubscribeListRefresh = subscribe(listRefresh, (changed) => {
                listChanged = listChanged || changed;
              });
              unsubscribeNextListRefresh = handleNext(smoothScrollTargetReached, () => {
                retry(listChanged);
              });
            } else {
              unsubscribeNextListRefresh = handleNext(pipe(listRefresh, watchChangesFor(150)), retry);
            }
            cleartTimeoutRef = setTimeout(() => {
              cleanup();
            }, 1200);
            publish(scrollingInProgress, true);
            log2("scrolling from index to", { index, top, behavior }, LogLevel.DEBUG);
            return { top, behavior };
          }
        )
      ),
      scrollTo
    );
    return {
      scrollToIndex,
      scrollTargetReached,
      topListHeight
    };
  },
  tup(sizeSystem, domIOSystem, loggerSystem),
  { singleton: true }
);
function watchChangesFor(limit) {
  return (done) => {
    const timeoutRef = setTimeout(() => {
      done(false);
    }, limit);
    return (value) => {
      if (value) {
        done(true);
        clearTimeout(timeoutRef);
      }
    };
  };
}
const UP = "up";
const DOWN = "down";
const NONE$1 = "none";
const INITIAL_BOTTOM_STATE = {
  atBottom: false,
  notAtBottomBecause: "NOT_SHOWING_LAST_ITEM",
  state: {
    offsetBottom: 0,
    scrollTop: 0,
    viewportHeight: 0,
    scrollHeight: 0
  }
};
const DEFAULT_AT_TOP_THRESHOLD = 0;
const stateFlagsSystem = system(([{ scrollContainerState, scrollTop, viewportHeight, headerHeight, footerHeight, scrollBy }]) => {
  const isAtBottom = statefulStream(false);
  const isAtTop = statefulStream(true);
  const atBottomStateChange = stream();
  const atTopStateChange = stream();
  const atBottomThreshold = statefulStream(4);
  const atTopThreshold = statefulStream(DEFAULT_AT_TOP_THRESHOLD);
  const isScrolling = statefulStreamFromEmitter(
    pipe(
      merge$1(pipe(duc(scrollTop), skip(1), mapTo(true)), pipe(duc(scrollTop), skip(1), mapTo(false), debounceTime(100))),
      distinctUntilChanged()
    ),
    false
  );
  const isScrollingBy = statefulStreamFromEmitter(
    pipe(merge$1(pipe(scrollBy, mapTo(true)), pipe(scrollBy, mapTo(false), debounceTime(200))), distinctUntilChanged()),
    false
  );
  connect(
    pipe(
      combineLatest(duc(scrollTop), duc(atTopThreshold)),
      map$2(([top, atTopThreshold2]) => top <= atTopThreshold2),
      distinctUntilChanged()
    ),
    isAtTop
  );
  connect(pipe(isAtTop, throttleTime(50)), atTopStateChange);
  const atBottomState = streamFromEmitter(
    pipe(
      combineLatest(scrollContainerState, duc(viewportHeight), duc(headerHeight), duc(footerHeight), duc(atBottomThreshold)),
      scan((current, [{ scrollTop: scrollTop2, scrollHeight }, viewportHeight2, _headerHeight, _footerHeight, atBottomThreshold2]) => {
        const isAtBottom2 = scrollTop2 + viewportHeight2 - scrollHeight > -atBottomThreshold2;
        const state = {
          viewportHeight: viewportHeight2,
          scrollTop: scrollTop2,
          scrollHeight
        };
        if (isAtBottom2) {
          let atBottomBecause;
          let scrollTopDelta;
          if (scrollTop2 > current.state.scrollTop) {
            atBottomBecause = "SCROLLED_DOWN";
            scrollTopDelta = current.state.scrollTop - scrollTop2;
          } else {
            atBottomBecause = "SIZE_DECREASED";
            scrollTopDelta = current.state.scrollTop - scrollTop2 || current.scrollTopDelta;
          }
          return {
            atBottom: true,
            state,
            atBottomBecause,
            scrollTopDelta
          };
        }
        let notAtBottomBecause;
        if (state.scrollHeight > current.state.scrollHeight) {
          notAtBottomBecause = "SIZE_INCREASED";
        } else if (viewportHeight2 < current.state.viewportHeight) {
          notAtBottomBecause = "VIEWPORT_HEIGHT_DECREASING";
        } else if (scrollTop2 < current.state.scrollTop) {
          notAtBottomBecause = "SCROLLING_UPWARDS";
        } else {
          notAtBottomBecause = "NOT_FULLY_SCROLLED_TO_LAST_ITEM_BOTTOM";
        }
        return {
          atBottom: false,
          notAtBottomBecause,
          state
        };
      }, INITIAL_BOTTOM_STATE),
      distinctUntilChanged((prev, next) => {
        return prev && prev.atBottom === next.atBottom;
      })
    )
  );
  const lastJumpDueToItemResize = statefulStreamFromEmitter(
    pipe(
      scrollContainerState,
      scan(
        (current, { scrollTop: scrollTop2, scrollHeight, viewportHeight: viewportHeight2 }) => {
          if (!approximatelyEqual(current.scrollHeight, scrollHeight)) {
            const atBottom = scrollHeight - (scrollTop2 + viewportHeight2) < 1;
            if (current.scrollTop !== scrollTop2 && atBottom) {
              return {
                scrollHeight,
                scrollTop: scrollTop2,
                jump: current.scrollTop - scrollTop2,
                changed: true
              };
            } else {
              return {
                scrollHeight,
                scrollTop: scrollTop2,
                jump: 0,
                changed: true
              };
            }
          } else {
            return {
              scrollTop: scrollTop2,
              scrollHeight,
              jump: 0,
              changed: false
            };
          }
        },
        { scrollHeight: 0, jump: 0, scrollTop: 0, changed: false }
      ),
      filter$1((value) => value.changed),
      map$2((value) => value.jump)
    ),
    0
  );
  connect(
    pipe(
      atBottomState,
      map$2((state) => state.atBottom)
    ),
    isAtBottom
  );
  connect(pipe(isAtBottom, throttleTime(50)), atBottomStateChange);
  const scrollDirection = statefulStream(DOWN);
  connect(
    pipe(
      scrollContainerState,
      map$2(({ scrollTop: scrollTop2 }) => scrollTop2),
      distinctUntilChanged(),
      scan(
        (acc, scrollTop2) => {
          if (getValue$2(isScrollingBy)) {
            return { direction: acc.direction, prevScrollTop: scrollTop2 };
          }
          return { direction: scrollTop2 < acc.prevScrollTop ? UP : DOWN, prevScrollTop: scrollTop2 };
        },
        { direction: DOWN, prevScrollTop: 0 }
      ),
      map$2((value) => value.direction)
    ),
    scrollDirection
  );
  connect(pipe(scrollContainerState, throttleTime(50), mapTo(NONE$1)), scrollDirection);
  const scrollVelocity = statefulStream(0);
  connect(
    pipe(
      isScrolling,
      filter$1((value) => !value),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      mapTo(0)
    ),
    scrollVelocity
  );
  connect(
    pipe(
      scrollTop,
      throttleTime(100),
      withLatestFrom(isScrolling),
      filter$1(([_, isScrolling2]) => !!isScrolling2),
      scan(([_, prev], [next]) => [prev, next], [0, 0]),
      map$2(([prev, next]) => next - prev)
    ),
    scrollVelocity
  );
  return {
    isScrolling,
    isAtTop,
    isAtBottom,
    atBottomState,
    atTopStateChange,
    atBottomStateChange,
    scrollDirection,
    atBottomThreshold,
    atTopThreshold,
    scrollVelocity,
    lastJumpDueToItemResize
  };
}, tup(domIOSystem));
const propsReadySystem = system(
  ([{ log }]) => {
    const propsReady = statefulStream(false);
    const didMount = streamFromEmitter(
      pipe(
        propsReady,
        filter$1((ready) => ready),
        distinctUntilChanged()
      )
    );
    subscribe(propsReady, (value) => {
      value && getValue$2(log)("props updated", {}, LogLevel.DEBUG);
    });
    return { propsReady, didMount };
  },
  tup(loggerSystem),
  { singleton: true }
);
function skipFrames(frameCount, callback) {
  if (frameCount == 0) {
    callback();
  } else {
    requestAnimationFrame(() => skipFrames(frameCount - 1, callback));
  }
}
function getInitialTopMostItemIndexNumber(location, totalCount) {
  const lastIndex = totalCount - 1;
  const index = typeof location === "number" ? location : location.index === "LAST" ? lastIndex : location.index;
  return index;
}
const initialTopMostItemIndexSystem = system(
  ([{ sizes, listRefresh, defaultItemSize }, { scrollTop }, { scrollToIndex, scrollTargetReached }, { didMount }]) => {
    const scrolledToInitialItem = statefulStream(true);
    const initialTopMostItemIndex = statefulStream(0);
    const initialItemFinalLocationReached = statefulStream(true);
    connect(
      pipe(
        didMount,
        withLatestFrom(initialTopMostItemIndex),
        filter$1(([_, location]) => !!location),
        mapTo(false)
      ),
      scrolledToInitialItem
    );
    connect(
      pipe(
        didMount,
        withLatestFrom(initialTopMostItemIndex),
        filter$1(([_, location]) => !!location),
        mapTo(false)
      ),
      initialItemFinalLocationReached
    );
    subscribe(
      pipe(
        combineLatest(listRefresh, didMount),
        withLatestFrom(scrolledToInitialItem, sizes, defaultItemSize, initialItemFinalLocationReached),
        filter$1(([[, didMount2], scrolledToInitialItem2, { sizeTree }, defaultItemSize2, scrollScheduled]) => {
          return didMount2 && (!empty(sizeTree) || isDefined(defaultItemSize2)) && !scrolledToInitialItem2 && !scrollScheduled;
        }),
        withLatestFrom(initialTopMostItemIndex)
      ),
      ([, initialTopMostItemIndex2]) => {
        handleNext(scrollTargetReached, () => {
          publish(initialItemFinalLocationReached, true);
        });
        skipFrames(4, () => {
          handleNext(scrollTop, () => {
            publish(scrolledToInitialItem, true);
          });
          publish(scrollToIndex, initialTopMostItemIndex2);
        });
      }
    );
    return {
      scrolledToInitialItem,
      initialTopMostItemIndex,
      initialItemFinalLocationReached
    };
  },
  tup(sizeSystem, domIOSystem, scrollToIndexSystem, propsReadySystem),
  { singleton: true }
);
function normalizeFollowOutput(follow) {
  if (!follow) {
    return false;
  }
  return follow === "smooth" ? "smooth" : "auto";
}
const behaviorFromFollowOutput = (follow, isAtBottom) => {
  if (typeof follow === "function") {
    return normalizeFollowOutput(follow(isAtBottom));
  }
  return isAtBottom && normalizeFollowOutput(follow);
};
const followOutputSystem = system(
  ([
    { totalCount, listRefresh },
    { isAtBottom, atBottomState },
    { scrollToIndex },
    { scrolledToInitialItem },
    { propsReady, didMount },
    { log },
    { scrollingInProgress }
  ]) => {
    const followOutput = statefulStream(false);
    const autoscrollToBottom = stream();
    let pendingScrollHandle = null;
    function scrollToBottom(followOutputBehavior) {
      publish(scrollToIndex, {
        index: "LAST",
        align: "end",
        behavior: followOutputBehavior
      });
    }
    subscribe(
      pipe(
        combineLatest(pipe(duc(totalCount), skip(1)), didMount),
        withLatestFrom(duc(followOutput), isAtBottom, scrolledToInitialItem, scrollingInProgress),
        map$2(([[totalCount2, didMount2], followOutput2, isAtBottom2, scrolledToInitialItem2, scrollingInProgress2]) => {
          let shouldFollow = didMount2 && scrolledToInitialItem2;
          let followOutputBehavior = "auto";
          if (shouldFollow) {
            followOutputBehavior = behaviorFromFollowOutput(followOutput2, isAtBottom2 || scrollingInProgress2);
            shouldFollow = shouldFollow && !!followOutputBehavior;
          }
          return { totalCount: totalCount2, shouldFollow, followOutputBehavior };
        }),
        filter$1(({ shouldFollow }) => shouldFollow)
      ),
      ({ totalCount: totalCount2, followOutputBehavior }) => {
        if (pendingScrollHandle) {
          pendingScrollHandle();
          pendingScrollHandle = null;
        }
        pendingScrollHandle = handleNext(listRefresh, () => {
          getValue$2(log)("following output to ", { totalCount: totalCount2 }, LogLevel.DEBUG);
          scrollToBottom(followOutputBehavior);
          pendingScrollHandle = null;
        });
      }
    );
    function trapNextSizeIncrease(followOutput2) {
      const cancel = handleNext(atBottomState, (state) => {
        if (followOutput2 && !state.atBottom && state.notAtBottomBecause === "SIZE_INCREASED" && !pendingScrollHandle) {
          getValue$2(log)("scrolling to bottom due to increased size", {}, LogLevel.DEBUG);
          scrollToBottom("auto");
        }
      });
      setTimeout(cancel, 100);
    }
    subscribe(
      pipe(
        combineLatest(duc(followOutput), totalCount, propsReady),
        filter$1(([follow, , ready]) => follow && ready),
        scan(
          ({ value }, [, next]) => {
            return { refreshed: value === next, value: next };
          },
          { refreshed: false, value: 0 }
        ),
        filter$1(({ refreshed }) => refreshed),
        withLatestFrom(followOutput, totalCount)
      ),
      ([, followOutput2]) => {
        if (getValue$2(scrolledToInitialItem)) {
          trapNextSizeIncrease(followOutput2 !== false);
        }
      }
    );
    subscribe(autoscrollToBottom, () => {
      trapNextSizeIncrease(getValue$2(followOutput) !== false);
    });
    subscribe(combineLatest(duc(followOutput), atBottomState), ([followOutput2, state]) => {
      if (followOutput2 && !state.atBottom && state.notAtBottomBecause === "VIEWPORT_HEIGHT_DECREASING") {
        scrollToBottom("auto");
      }
    });
    return { followOutput, autoscrollToBottom };
  },
  tup(sizeSystem, stateFlagsSystem, scrollToIndexSystem, initialTopMostItemIndexSystem, propsReadySystem, loggerSystem, domIOSystem)
);
function groupCountsToIndicesAndCount(counts) {
  return counts.reduce(
    (acc, groupCount) => {
      acc.groupIndices.push(acc.totalCount);
      acc.totalCount += groupCount + 1;
      return acc;
    },
    {
      totalCount: 0,
      groupIndices: []
    }
  );
}
const groupedListSystem = system(([{ totalCount, groupIndices, sizes }, { scrollTop, headerHeight }]) => {
  const groupCounts = stream();
  const topItemsIndexes = stream();
  const groupIndicesAndCount = streamFromEmitter(pipe(groupCounts, map$2(groupCountsToIndicesAndCount)));
  connect(
    pipe(
      groupIndicesAndCount,
      map$2((value) => value.totalCount)
    ),
    totalCount
  );
  connect(
    pipe(
      groupIndicesAndCount,
      map$2((value) => value.groupIndices)
    ),
    groupIndices
  );
  connect(
    pipe(
      combineLatest(scrollTop, sizes, headerHeight),
      filter$1(([_, sizes2]) => hasGroups(sizes2)),
      map$2(([scrollTop2, state, headerHeight2]) => findMaxKeyValue(state.groupOffsetTree, Math.max(scrollTop2 - headerHeight2, 0), "v")[0]),
      distinctUntilChanged(),
      map$2((index) => [index])
    ),
    topItemsIndexes
  );
  return { groupCounts, topItemsIndexes };
}, tup(sizeSystem, domIOSystem));
function tupleComparator(prev, current) {
  return !!(prev && prev[0] === current[0] && prev[1] === current[1]);
}
function rangeComparator(prev, next) {
  return !!(prev && prev.startIndex === next.startIndex && prev.endIndex === next.endIndex);
}
const TOP = "top";
const BOTTOM = "bottom";
const NONE = "none";
function getOverscan(overscan, end, direction) {
  if (typeof overscan === "number") {
    return direction === UP && end === TOP || direction === DOWN && end === BOTTOM ? overscan : 0;
  } else {
    if (direction === UP) {
      return end === TOP ? overscan.main : overscan.reverse;
    } else {
      return end === BOTTOM ? overscan.main : overscan.reverse;
    }
  }
}
function getViewportIncrease(value, end) {
  return typeof value === "number" ? value : value[end] || 0;
}
const sizeRangeSystem = system(
  ([{ scrollTop, viewportHeight, deviation, headerHeight, fixedHeaderHeight }]) => {
    const listBoundary = stream();
    const topListHeight = statefulStream(0);
    const increaseViewportBy = statefulStream(0);
    const overscan = statefulStream(0);
    const visibleRange = statefulStreamFromEmitter(
      pipe(
        combineLatest(
          duc(scrollTop),
          duc(viewportHeight),
          duc(headerHeight),
          duc(listBoundary, tupleComparator),
          duc(overscan),
          duc(topListHeight),
          duc(fixedHeaderHeight),
          duc(deviation),
          duc(increaseViewportBy)
        ),
        map$2(
          ([
            scrollTop2,
            viewportHeight2,
            headerHeight2,
            [listTop, listBottom],
            overscan2,
            topListHeight2,
            fixedHeaderHeight2,
            deviation2,
            increaseViewportBy2
          ]) => {
            const top = scrollTop2 - deviation2;
            const stickyHeaderHeight = topListHeight2 + fixedHeaderHeight2;
            const headerVisible = Math.max(headerHeight2 - top, 0);
            let direction = NONE;
            const topViewportAddition = getViewportIncrease(increaseViewportBy2, TOP);
            const bottomViewportAddition = getViewportIncrease(increaseViewportBy2, BOTTOM);
            listTop -= deviation2;
            listTop += headerHeight2 + fixedHeaderHeight2;
            listBottom += headerHeight2 + fixedHeaderHeight2;
            listBottom -= deviation2;
            if (listTop > scrollTop2 + stickyHeaderHeight - topViewportAddition) {
              direction = UP;
            }
            if (listBottom < scrollTop2 - headerVisible + viewportHeight2 + bottomViewportAddition) {
              direction = DOWN;
            }
            if (direction !== NONE) {
              return [
                Math.max(top - headerHeight2 - getOverscan(overscan2, TOP, direction) - topViewportAddition, 0),
                top - headerVisible - fixedHeaderHeight2 + viewportHeight2 + getOverscan(overscan2, BOTTOM, direction) + bottomViewportAddition
              ];
            }
            return null;
          }
        ),
        filter$1((value) => value != null),
        distinctUntilChanged(tupleComparator)
      ),
      [0, 0]
    );
    return {
      // input
      listBoundary,
      overscan,
      topListHeight,
      increaseViewportBy,
      // output
      visibleRange
    };
  },
  tup(domIOSystem),
  { singleton: true }
);
function probeItemSet(index, sizes, data) {
  if (hasGroups(sizes)) {
    const itemIndex = originalIndexFromItemIndex(index, sizes);
    const groupIndex = findMaxKeyValue(sizes.groupOffsetTree, itemIndex)[0];
    return [
      { index: groupIndex, size: 0, offset: 0 },
      { index: itemIndex, size: 0, offset: 0, data: data && data[0] }
    ];
  }
  return [{ index, size: 0, offset: 0, data: data && data[0] }];
}
const EMPTY_LIST_STATE = {
  items: [],
  topItems: [],
  offsetTop: 0,
  offsetBottom: 0,
  top: 0,
  bottom: 0,
  topListHeight: 0,
  totalCount: 0,
  firstItemIndex: 0
};
function transposeItems(items, sizes, firstItemIndex) {
  if (items.length === 0) {
    return [];
  }
  if (!hasGroups(sizes)) {
    return items.map((item) => ({ ...item, index: item.index + firstItemIndex, originalIndex: item.index }));
  }
  const startIndex = items[0].index;
  const endIndex = items[items.length - 1].index;
  const transposedItems = [];
  const groupRanges = rangesWithin(sizes.groupOffsetTree, startIndex, endIndex);
  let currentRange = void 0;
  let currentGroupIndex = 0;
  for (const item of items) {
    if (!currentRange || currentRange.end < item.index) {
      currentRange = groupRanges.shift();
      currentGroupIndex = sizes.groupIndices.indexOf(currentRange.start);
    }
    let transposedItem;
    if (item.index === currentRange.start) {
      transposedItem = {
        type: "group",
        index: currentGroupIndex
      };
    } else {
      transposedItem = {
        index: item.index - (currentGroupIndex + 1) + firstItemIndex,
        groupIndex: currentGroupIndex
      };
    }
    transposedItems.push({
      ...transposedItem,
      size: item.size,
      offset: item.offset,
      originalIndex: item.index,
      data: item.data
    });
  }
  return transposedItems;
}
function buildListState(items, topItems, totalCount, gap, sizes, firstItemIndex) {
  const { lastSize, lastOffset, lastIndex } = sizes;
  let offsetTop = 0;
  let bottom = 0;
  if (items.length > 0) {
    offsetTop = items[0].offset;
    const lastItem = items[items.length - 1];
    bottom = lastItem.offset + lastItem.size;
  }
  const itemCount = totalCount - lastIndex;
  const total = lastOffset + itemCount * lastSize + (itemCount - 1) * gap;
  const top = offsetTop;
  const offsetBottom = total - bottom;
  return {
    items: transposeItems(items, sizes, firstItemIndex),
    topItems: transposeItems(topItems, sizes, firstItemIndex),
    topListHeight: topItems.reduce((height, item) => item.size + height, 0),
    offsetTop,
    offsetBottom,
    top,
    bottom,
    totalCount,
    firstItemIndex
  };
}
function buildListStateFromItemCount(itemCount, initialTopMostItemIndex, sizes, firstItemIndex, gap, data) {
  let includedGroupsCount = 0;
  if (sizes.groupIndices.length > 0) {
    for (const index of sizes.groupIndices) {
      if (index - includedGroupsCount >= itemCount) {
        break;
      }
      includedGroupsCount++;
    }
  }
  const adjustedCount = itemCount + includedGroupsCount;
  const initialTopMostItemIndexNumber = getInitialTopMostItemIndexNumber(initialTopMostItemIndex, adjustedCount);
  const items = Array.from({ length: adjustedCount }).map((_, index) => ({
    index: index + initialTopMostItemIndexNumber,
    size: 0,
    offset: 0,
    data: data[index + initialTopMostItemIndexNumber]
  }));
  return buildListState(items, [], adjustedCount, gap, sizes, firstItemIndex);
}
const listStateSystem = system(
  ([
    { sizes, totalCount, data, firstItemIndex, gap },
    groupedListSystem2,
    { visibleRange, listBoundary, topListHeight: rangeTopListHeight },
    { scrolledToInitialItem, initialTopMostItemIndex },
    { topListHeight },
    stateFlags,
    { didMount },
    { recalcInProgress }
  ]) => {
    const topItemsIndexes = statefulStream([]);
    const initialItemCount = statefulStream(0);
    const itemsRendered = stream();
    connect(groupedListSystem2.topItemsIndexes, topItemsIndexes);
    const listState = statefulStreamFromEmitter(
      pipe(
        combineLatest(
          didMount,
          recalcInProgress,
          duc(visibleRange, tupleComparator),
          duc(totalCount),
          duc(sizes),
          duc(initialTopMostItemIndex),
          scrolledToInitialItem,
          duc(topItemsIndexes),
          duc(firstItemIndex),
          duc(gap),
          data
        ),
        filter$1(([mount, recalcInProgress2, , totalCount2, , , , , , , data2]) => {
          const dataChangeInProgress = data2 && data2.length !== totalCount2;
          return mount && !recalcInProgress2 && !dataChangeInProgress;
        }),
        map$2(
          ([
            ,
            ,
            [startOffset, endOffset],
            totalCount2,
            sizes2,
            initialTopMostItemIndex2,
            scrolledToInitialItem2,
            topItemsIndexes2,
            firstItemIndex2,
            gap2,
            data2
          ]) => {
            const sizesValue = sizes2;
            const { sizeTree, offsetTree } = sizesValue;
            const initialItemCountValue = getValue$2(initialItemCount);
            if (totalCount2 === 0) {
              return { ...EMPTY_LIST_STATE, totalCount: totalCount2 };
            }
            if (startOffset === 0 && endOffset === 0) {
              if (initialItemCountValue === 0) {
                return { ...EMPTY_LIST_STATE, totalCount: totalCount2 };
              } else {
                return buildListStateFromItemCount(initialItemCountValue, initialTopMostItemIndex2, sizes2, firstItemIndex2, gap2, data2 || []);
              }
            }
            if (empty(sizeTree)) {
              if (initialItemCountValue > 0) {
                return null;
              }
              const state = buildListState(
                probeItemSet(getInitialTopMostItemIndexNumber(initialTopMostItemIndex2, totalCount2), sizesValue, data2),
                [],
                totalCount2,
                gap2,
                sizesValue,
                firstItemIndex2
              );
              return state;
            }
            const topItems = [];
            if (topItemsIndexes2.length > 0) {
              const startIndex = topItemsIndexes2[0];
              const endIndex = topItemsIndexes2[topItemsIndexes2.length - 1];
              let offset = 0;
              for (const range of rangesWithin(sizeTree, startIndex, endIndex)) {
                const size = range.value;
                const rangeStartIndex = Math.max(range.start, startIndex);
                const rangeEndIndex = Math.min(range.end, endIndex);
                for (let i = rangeStartIndex; i <= rangeEndIndex; i++) {
                  topItems.push({ index: i, size, offset, data: data2 && data2[i] });
                  offset += size;
                }
              }
            }
            if (!scrolledToInitialItem2) {
              return buildListState([], topItems, totalCount2, gap2, sizesValue, firstItemIndex2);
            }
            const minStartIndex = topItemsIndexes2.length > 0 ? topItemsIndexes2[topItemsIndexes2.length - 1] + 1 : 0;
            const offsetPointRanges = rangesWithinOffsets(offsetTree, startOffset, endOffset, minStartIndex);
            if (offsetPointRanges.length === 0) {
              return null;
            }
            const maxIndex = totalCount2 - 1;
            const items = tap([], (result) => {
              for (const range of offsetPointRanges) {
                const point = range.value;
                let offset = point.offset;
                let rangeStartIndex = range.start;
                const size = point.size;
                if (point.offset < startOffset) {
                  rangeStartIndex += Math.floor((startOffset - point.offset + gap2) / (size + gap2));
                  const itemCount = rangeStartIndex - range.start;
                  offset += itemCount * size + itemCount * gap2;
                }
                if (rangeStartIndex < minStartIndex) {
                  offset += (minStartIndex - rangeStartIndex) * size;
                  rangeStartIndex = minStartIndex;
                }
                const endIndex = Math.min(range.end, maxIndex);
                for (let i = rangeStartIndex; i <= endIndex; i++) {
                  if (offset >= endOffset) {
                    break;
                  }
                  result.push({ index: i, size, offset, data: data2 && data2[i] });
                  offset += size + gap2;
                }
              }
            });
            return buildListState(items, topItems, totalCount2, gap2, sizesValue, firstItemIndex2);
          }
        ),
        //@ts-expect-error filter needs to be fixed
        filter$1((value) => value !== null),
        distinctUntilChanged()
      ),
      EMPTY_LIST_STATE
    );
    connect(
      pipe(
        data,
        filter$1(isDefined),
        map$2((data2) => data2 == null ? void 0 : data2.length)
      ),
      totalCount
    );
    connect(
      pipe(
        listState,
        map$2((value) => value.topListHeight)
      ),
      topListHeight
    );
    connect(topListHeight, rangeTopListHeight);
    connect(
      pipe(
        listState,
        map$2((state) => [state.top, state.bottom])
      ),
      listBoundary
    );
    connect(
      pipe(
        listState,
        map$2((state) => state.items)
      ),
      itemsRendered
    );
    const endReached = streamFromEmitter(
      pipe(
        listState,
        filter$1(({ items }) => items.length > 0),
        withLatestFrom(totalCount, data),
        filter$1(([{ items }, totalCount2]) => items[items.length - 1].originalIndex === totalCount2 - 1),
        map$2(([, totalCount2, data2]) => [totalCount2 - 1, data2]),
        distinctUntilChanged(tupleComparator),
        map$2(([count]) => count)
      )
    );
    const startReached = streamFromEmitter(
      pipe(
        listState,
        throttleTime(200),
        filter$1(({ items, topItems }) => {
          return items.length > 0 && items[0].originalIndex === topItems.length;
        }),
        map$2(({ items }) => items[0].index),
        distinctUntilChanged()
      )
    );
    const rangeChanged = streamFromEmitter(
      pipe(
        listState,
        filter$1(({ items }) => items.length > 0),
        map$2(({ items }) => {
          let startIndex = 0;
          let endIndex = items.length - 1;
          while (items[startIndex].type === "group" && startIndex < endIndex) {
            startIndex++;
          }
          while (items[endIndex].type === "group" && endIndex > startIndex) {
            endIndex--;
          }
          return {
            startIndex: items[startIndex].index,
            endIndex: items[endIndex].index
          };
        }),
        distinctUntilChanged(rangeComparator)
      )
    );
    return { listState, topItemsIndexes, endReached, startReached, rangeChanged, itemsRendered, initialItemCount, ...stateFlags };
  },
  tup(
    sizeSystem,
    groupedListSystem,
    sizeRangeSystem,
    initialTopMostItemIndexSystem,
    scrollToIndexSystem,
    stateFlagsSystem,
    propsReadySystem,
    recalcSystem
  ),
  { singleton: true }
);
const initialItemCountSystem = system(
  ([{ sizes, firstItemIndex, data, gap }, { initialTopMostItemIndex }, { initialItemCount, listState }, { didMount }]) => {
    connect(
      pipe(
        didMount,
        withLatestFrom(initialItemCount),
        filter$1(([, count]) => count !== 0),
        withLatestFrom(initialTopMostItemIndex, sizes, firstItemIndex, gap, data),
        map$2(([[, count], initialTopMostItemIndexValue, sizes2, firstItemIndex2, gap2, data2 = []]) => {
          return buildListStateFromItemCount(count, initialTopMostItemIndexValue, sizes2, firstItemIndex2, gap2, data2);
        })
      ),
      listState
    );
    return {};
  },
  tup(sizeSystem, initialTopMostItemIndexSystem, listStateSystem, propsReadySystem),
  { singleton: true }
);
const scrollSeekSystem = system(
  ([{ scrollVelocity }]) => {
    const isSeeking = statefulStream(false);
    const rangeChanged = stream();
    const scrollSeekConfiguration = statefulStream(false);
    connect(
      pipe(
        scrollVelocity,
        withLatestFrom(scrollSeekConfiguration, isSeeking, rangeChanged),
        filter$1(([_, config]) => !!config),
        map$2(([speed, config, isSeeking2, range]) => {
          const { exit, enter } = config;
          if (isSeeking2) {
            if (exit(speed, range)) {
              return false;
            }
          } else {
            if (enter(speed, range)) {
              return true;
            }
          }
          return isSeeking2;
        }),
        distinctUntilChanged()
      ),
      isSeeking
    );
    subscribe(
      pipe(combineLatest(isSeeking, scrollVelocity, rangeChanged), withLatestFrom(scrollSeekConfiguration)),
      ([[isSeeking2, velocity, range], config]) => isSeeking2 && config && config.change && config.change(velocity, range)
    );
    return { isSeeking, scrollSeekConfiguration, scrollVelocity, scrollSeekRangeChanged: rangeChanged };
  },
  tup(stateFlagsSystem),
  { singleton: true }
);
const topItemCountSystem = system(([{ topItemsIndexes }]) => {
  const topItemCount = statefulStream(0);
  connect(
    pipe(
      topItemCount,
      filter$1((length) => length > 0),
      map$2((length) => Array.from({ length }).map((_, index) => index))
    ),
    topItemsIndexes
  );
  return { topItemCount };
}, tup(listStateSystem));
const totalListHeightSystem = system(
  ([{ footerHeight, headerHeight, fixedHeaderHeight, fixedFooterHeight }, { listState }]) => {
    const totalListHeightChanged = stream();
    const totalListHeight = statefulStreamFromEmitter(
      pipe(
        combineLatest(footerHeight, fixedFooterHeight, headerHeight, fixedHeaderHeight, listState),
        map$2(([footerHeight2, fixedFooterHeight2, headerHeight2, fixedHeaderHeight2, listState2]) => {
          return footerHeight2 + fixedFooterHeight2 + headerHeight2 + fixedHeaderHeight2 + listState2.offsetBottom + listState2.bottom;
        })
      ),
      0
    );
    connect(duc(totalListHeight), totalListHeightChanged);
    return { totalListHeight, totalListHeightChanged };
  },
  tup(domIOSystem, listStateSystem),
  { singleton: true }
);
function simpleMemoize(func) {
  let called = false;
  let result;
  return () => {
    if (!called) {
      called = true;
      result = func();
    }
    return result;
  };
}
const isMobileSafari = simpleMemoize(() => {
  return /iP(ad|od|hone)/i.test(navigator.userAgent) && /WebKit/i.test(navigator.userAgent);
});
const upwardScrollFixSystem = system(
  ([
    { scrollBy, scrollTop, deviation, scrollingInProgress },
    { isScrolling, isAtBottom, scrollDirection, lastJumpDueToItemResize },
    { listState },
    { beforeUnshiftWith, shiftWithOffset, sizes, gap },
    { log },
    { recalcInProgress }
  ]) => {
    const deviationOffset = streamFromEmitter(
      pipe(
        listState,
        withLatestFrom(lastJumpDueToItemResize),
        scan(
          ([, prevItems, prevTotalCount, prevTotalHeight], [{ items, totalCount, bottom, offsetBottom }, lastJumpDueToItemResize2]) => {
            const totalHeight = bottom + offsetBottom;
            let newDev = 0;
            if (prevTotalCount === totalCount) {
              if (prevItems.length > 0 && items.length > 0) {
                const atStart = items[0].originalIndex === 0 && prevItems[0].originalIndex === 0;
                if (!atStart) {
                  newDev = totalHeight - prevTotalHeight;
                  if (newDev !== 0) {
                    newDev += lastJumpDueToItemResize2;
                  }
                }
              }
            }
            return [newDev, items, totalCount, totalHeight];
          },
          [0, [], 0, 0]
        ),
        filter$1(([amount]) => amount !== 0),
        withLatestFrom(scrollTop, scrollDirection, scrollingInProgress, isAtBottom, log, recalcInProgress),
        filter$1(([, scrollTop2, scrollDirection2, scrollingInProgress2, , , recalcInProgress2]) => {
          return !recalcInProgress2 && !scrollingInProgress2 && scrollTop2 !== 0 && scrollDirection2 === UP;
        }),
        map$2(([[amount], , , , , log2]) => {
          log2("Upward scrolling compensation", { amount }, LogLevel.DEBUG);
          return amount;
        })
      )
    );
    function scrollByWith(offset) {
      if (offset > 0) {
        publish(scrollBy, { top: -offset, behavior: "auto" });
        publish(deviation, 0);
      } else {
        publish(deviation, 0);
        publish(scrollBy, { top: -offset, behavior: "auto" });
      }
    }
    subscribe(pipe(deviationOffset, withLatestFrom(deviation, isScrolling)), ([offset, deviationAmount, isScrolling2]) => {
      if (isScrolling2 && isMobileSafari()) {
        publish(deviation, deviationAmount - offset);
      } else {
        scrollByWith(-offset);
      }
    });
    subscribe(
      pipe(
        combineLatest(statefulStreamFromEmitter(isScrolling, false), deviation, recalcInProgress),
        filter$1(([is, deviation2, recalc]) => !is && !recalc && deviation2 !== 0),
        map$2(([_, deviation2]) => deviation2),
        throttleTime(1)
      ),
      scrollByWith
    );
    connect(
      pipe(
        shiftWithOffset,
        map$2((offset) => {
          return { top: -offset };
        })
      ),
      scrollBy
    );
    subscribe(
      pipe(
        beforeUnshiftWith,
        withLatestFrom(sizes, gap),
        map$2(([offset, { lastSize: defaultItemSize, groupIndices, sizeTree }, gap2]) => {
          function getItemOffset(itemCount) {
            return itemCount * (defaultItemSize + gap2);
          }
          if (groupIndices.length === 0) {
            return getItemOffset(offset);
          } else {
            let amount = 0;
            const defaultGroupSize = find(sizeTree, 0);
            let recognizedOffsetItems = 0;
            let groupIndex = 0;
            while (recognizedOffsetItems < offset) {
              recognizedOffsetItems++;
              amount += defaultGroupSize;
              let groupItemCount = groupIndices.length === groupIndex + 1 ? Infinity : groupIndices[groupIndex + 1] - groupIndices[groupIndex] - 1;
              if (recognizedOffsetItems + groupItemCount > offset) {
                amount -= defaultGroupSize;
                groupItemCount = offset - recognizedOffsetItems + 1;
              }
              recognizedOffsetItems += groupItemCount;
              amount += getItemOffset(groupItemCount);
              groupIndex++;
            }
            return amount;
          }
        })
      ),
      (offset) => {
        publish(deviation, offset);
        requestAnimationFrame(() => {
          publish(scrollBy, { top: offset });
          requestAnimationFrame(() => {
            publish(deviation, 0);
            publish(recalcInProgress, false);
          });
        });
      }
    );
    return { deviation };
  },
  tup(domIOSystem, stateFlagsSystem, listStateSystem, sizeSystem, loggerSystem, recalcSystem)
);
const initialScrollTopSystem = system(
  ([{ didMount }, { scrollTo }, { listState }]) => {
    const initialScrollTop = statefulStream(0);
    subscribe(
      pipe(
        didMount,
        withLatestFrom(initialScrollTop),
        filter$1(([, offset]) => offset !== 0),
        map$2(([, offset]) => ({ top: offset }))
      ),
      (location) => {
        handleNext(
          pipe(
            listState,
            skip(1),
            filter$1((state) => state.items.length > 1)
          ),
          () => {
            requestAnimationFrame(() => {
              publish(scrollTo, location);
            });
          }
        );
      }
    );
    return {
      initialScrollTop
    };
  },
  tup(propsReadySystem, domIOSystem, listStateSystem),
  { singleton: true }
);
const alignToBottomSystem = system(
  ([{ viewportHeight }, { totalListHeight }]) => {
    const alignToBottom = statefulStream(false);
    const paddingTopAddition = statefulStreamFromEmitter(
      pipe(
        combineLatest(alignToBottom, viewportHeight, totalListHeight),
        filter$1(([enabled]) => enabled),
        map$2(([, viewportHeight2, totalListHeight2]) => {
          return Math.max(0, viewportHeight2 - totalListHeight2);
        }),
        throttleTime(0),
        distinctUntilChanged()
      ),
      0
    );
    return { alignToBottom, paddingTopAddition };
  },
  tup(domIOSystem, totalListHeightSystem),
  { singleton: true }
);
const windowScrollerSystem = system(([{ scrollTo, scrollContainerState }]) => {
  const windowScrollContainerState = stream();
  const windowViewportRect = stream();
  const windowScrollTo = stream();
  const useWindowScroll = statefulStream(false);
  const customScrollParent = statefulStream(void 0);
  connect(
    pipe(
      combineLatest(windowScrollContainerState, windowViewportRect),
      map$2(([{ viewportHeight, scrollTop: windowScrollTop, scrollHeight }, { offsetTop }]) => {
        return {
          scrollTop: Math.max(0, windowScrollTop - offsetTop),
          scrollHeight,
          viewportHeight
        };
      })
    ),
    scrollContainerState
  );
  connect(
    pipe(
      scrollTo,
      withLatestFrom(windowViewportRect),
      map$2(([scrollTo2, { offsetTop }]) => {
        return {
          ...scrollTo2,
          top: scrollTo2.top + offsetTop
        };
      })
    ),
    windowScrollTo
  );
  return {
    // config
    useWindowScroll,
    customScrollParent,
    // input
    windowScrollContainerState,
    windowViewportRect,
    // signals
    windowScrollTo
  };
}, tup(domIOSystem));
const defaultCalculateViewLocation = ({
  itemTop: itemTop2,
  itemBottom,
  viewportTop,
  viewportBottom,
  locationParams: { behavior, align, ...rest }
}) => {
  if (itemTop2 < viewportTop) {
    return { ...rest, behavior, align: align != null ? align : "start" };
  }
  if (itemBottom > viewportBottom) {
    return { ...rest, behavior, align: align != null ? align : "end" };
  }
  return null;
};
const scrollIntoViewSystem = system(
  ([
    { sizes, totalCount, gap },
    { scrollTop, viewportHeight, headerHeight, fixedHeaderHeight, fixedFooterHeight, scrollingInProgress },
    { scrollToIndex }
  ]) => {
    const scrollIntoView = stream();
    connect(
      pipe(
        scrollIntoView,
        withLatestFrom(sizes, viewportHeight, totalCount, headerHeight, fixedHeaderHeight, fixedFooterHeight, scrollTop),
        withLatestFrom(gap),
        map$2(([[viewLocation, sizes2, viewportHeight2, totalCount2, headerHeight2, fixedHeaderHeight2, fixedFooterHeight2, scrollTop2], gap2]) => {
          const { done, behavior, align, calculateViewLocation = defaultCalculateViewLocation, ...rest } = viewLocation;
          const actualIndex = originalIndexFromLocation(viewLocation, sizes2, totalCount2 - 1);
          const itemTop2 = offsetOf(actualIndex, sizes2.offsetTree, gap2) + headerHeight2 + fixedHeaderHeight2;
          const itemBottom = itemTop2 + findMaxKeyValue(sizes2.sizeTree, actualIndex)[1];
          const viewportTop = scrollTop2 + fixedHeaderHeight2;
          const viewportBottom = scrollTop2 + viewportHeight2 - fixedFooterHeight2;
          const location = calculateViewLocation({
            itemTop: itemTop2,
            itemBottom,
            viewportTop,
            viewportBottom,
            locationParams: { behavior, align, ...rest }
          });
          if (location) {
            done && handleNext(
              pipe(
                scrollingInProgress,
                filter$1((value) => value === false),
                // skips the initial publish of false, and the cleanup call.
                // but if scrollingInProgress is true, we skip the initial publish.
                skip(getValue$2(scrollingInProgress) ? 1 : 2)
              ),
              done
            );
          } else {
            done && done();
          }
          return location;
        }),
        filter$1((value) => value !== null)
      ),
      scrollToIndex
    );
    return {
      scrollIntoView
    };
  },
  tup(sizeSystem, domIOSystem, scrollToIndexSystem, listStateSystem, loggerSystem),
  { singleton: true }
);
const stateLoadSystem = system(
  ([
    { sizes, sizeRanges },
    { scrollTop, headerHeight },
    { initialTopMostItemIndex },
    { didMount },
    { useWindowScroll, windowScrollContainerState, windowViewportRect }
  ]) => {
    const getState = stream();
    const restoreStateFrom = statefulStream(void 0);
    const statefulWindowScrollContainerState = statefulStream(null);
    const statefulWindowViewportRect = statefulStream(null);
    connect(windowScrollContainerState, statefulWindowScrollContainerState);
    connect(windowViewportRect, statefulWindowViewportRect);
    subscribe(
      pipe(
        getState,
        withLatestFrom(sizes, scrollTop, useWindowScroll, statefulWindowScrollContainerState, statefulWindowViewportRect, headerHeight)
      ),
      ([callback, sizes2, scrollTop2, useWindowScroll2, windowScrollContainerState2, windowViewportRect2, headerHeight2]) => {
        const ranges = sizeTreeToRanges(sizes2.sizeTree);
        if (useWindowScroll2 && windowScrollContainerState2 !== null && windowViewportRect2 !== null) {
          scrollTop2 = windowScrollContainerState2.scrollTop - windowViewportRect2.offsetTop;
        }
        scrollTop2 -= headerHeight2;
        callback({ ranges, scrollTop: scrollTop2 });
      }
    );
    connect(pipe(restoreStateFrom, filter$1(isDefined), map$2(locationFromSnapshot)), initialTopMostItemIndex);
    connect(
      pipe(
        didMount,
        withLatestFrom(restoreStateFrom),
        filter$1(([, state]) => state !== void 0),
        distinctUntilChanged(),
        map$2(([, snapshot]) => {
          return snapshot.ranges;
        })
      ),
      sizeRanges
    );
    return {
      getState,
      restoreStateFrom
    };
  },
  tup(sizeSystem, domIOSystem, initialTopMostItemIndexSystem, propsReadySystem, windowScrollerSystem)
);
function locationFromSnapshot(snapshot) {
  return { offset: snapshot.scrollTop, index: 0, align: "start" };
}
const featureGroup1System = system(
  ([
    sizeRange,
    initialItemCount,
    propsReady,
    scrollSeek,
    totalListHeight,
    initialScrollTopSystem2,
    alignToBottom,
    windowScroller,
    scrollIntoView,
    logger
  ]) => {
    return {
      ...sizeRange,
      ...initialItemCount,
      ...propsReady,
      ...scrollSeek,
      ...totalListHeight,
      ...initialScrollTopSystem2,
      ...alignToBottom,
      ...windowScroller,
      ...scrollIntoView,
      ...logger
    };
  },
  tup(
    sizeRangeSystem,
    initialItemCountSystem,
    propsReadySystem,
    scrollSeekSystem,
    totalListHeightSystem,
    initialScrollTopSystem,
    alignToBottomSystem,
    windowScrollerSystem,
    scrollIntoViewSystem,
    loggerSystem
  )
);
const listSystem = system(
  ([
    {
      totalCount,
      sizeRanges,
      fixedItemSize,
      defaultItemSize,
      trackItemSizes,
      itemSize,
      data,
      firstItemIndex,
      groupIndices,
      statefulTotalCount,
      gap,
      sizes
    },
    { initialTopMostItemIndex, scrolledToInitialItem, initialItemFinalLocationReached },
    domIO,
    stateLoad,
    followOutput,
    { listState, topItemsIndexes, ...flags },
    { scrollToIndex },
    _,
    { topItemCount },
    { groupCounts },
    featureGroup1
  ]) => {
    connect(flags.rangeChanged, featureGroup1.scrollSeekRangeChanged);
    connect(
      pipe(
        featureGroup1.windowViewportRect,
        map$2((value) => value.visibleHeight)
      ),
      domIO.viewportHeight
    );
    return {
      // input
      totalCount,
      data,
      firstItemIndex,
      sizeRanges,
      initialTopMostItemIndex,
      scrolledToInitialItem,
      initialItemFinalLocationReached,
      topItemsIndexes,
      topItemCount,
      groupCounts,
      fixedItemHeight: fixedItemSize,
      defaultItemHeight: defaultItemSize,
      gap,
      ...followOutput,
      // output
      statefulTotalCount,
      listState,
      scrollToIndex,
      trackItemSizes,
      itemSize,
      groupIndices,
      // exported from stateFlagsSystem
      ...flags,
      // the bag of IO from featureGroup1System
      ...featureGroup1,
      ...domIO,
      sizes,
      ...stateLoad
    };
  },
  tup(
    sizeSystem,
    initialTopMostItemIndexSystem,
    domIOSystem,
    stateLoadSystem,
    followOutputSystem,
    listStateSystem,
    scrollToIndexSystem,
    upwardScrollFixSystem,
    topItemCountSystem,
    groupedListSystem,
    featureGroup1System
  )
);
const WEBKIT_STICKY = "-webkit-sticky";
const STICKY = "sticky";
const positionStickyCssValue = simpleMemoize(() => {
  if (typeof document === "undefined") {
    return STICKY;
  }
  const node = document.createElement("div");
  node.style.position = WEBKIT_STICKY;
  return node.style.position === WEBKIT_STICKY ? WEBKIT_STICKY : STICKY;
});
function useWindowViewportRectRef(callback, customScrollParent, skipAnimationFrame) {
  const viewportInfo = React__default.useRef(null);
  const calculateInfo = React__default.useCallback(
    (element) => {
      if (element === null || !element.offsetParent) {
        return;
      }
      const rect = element.getBoundingClientRect();
      const visibleWidth = rect.width;
      let visibleHeight, offsetTop;
      if (customScrollParent) {
        const customScrollParentRect = customScrollParent.getBoundingClientRect();
        const deltaTop = rect.top - customScrollParentRect.top;
        visibleHeight = customScrollParentRect.height - Math.max(0, deltaTop);
        offsetTop = deltaTop + customScrollParent.scrollTop;
      } else {
        visibleHeight = window.innerHeight - Math.max(0, rect.top);
        offsetTop = rect.top + window.pageYOffset;
      }
      viewportInfo.current = {
        offsetTop,
        visibleHeight,
        visibleWidth
      };
      callback(viewportInfo.current);
    },
    [callback, customScrollParent]
  );
  const { callbackRef, ref } = useSizeWithElRef(calculateInfo, true, skipAnimationFrame);
  const scrollAndResizeEventHandler = React__default.useCallback(() => {
    calculateInfo(ref.current);
  }, [calculateInfo, ref]);
  React__default.useEffect(() => {
    if (customScrollParent) {
      customScrollParent.addEventListener("scroll", scrollAndResizeEventHandler);
      const observer = new ResizeObserver(() => {
        requestAnimationFrame(scrollAndResizeEventHandler);
      });
      observer.observe(customScrollParent);
      return () => {
        customScrollParent.removeEventListener("scroll", scrollAndResizeEventHandler);
        observer.unobserve(customScrollParent);
      };
    } else {
      window.addEventListener("scroll", scrollAndResizeEventHandler);
      window.addEventListener("resize", scrollAndResizeEventHandler);
      return () => {
        window.removeEventListener("scroll", scrollAndResizeEventHandler);
        window.removeEventListener("resize", scrollAndResizeEventHandler);
      };
    }
  }, [scrollAndResizeEventHandler, customScrollParent]);
  return callbackRef;
}
const VirtuosoMockContext = React__default.createContext(void 0);
const VirtuosoGridMockContext = React__default.createContext(void 0);
function identity$8(value) {
  return value;
}
const listComponentPropsSystem = /* @__PURE__ */ system(() => {
  const itemContent = statefulStream((index) => `Item ${index}`);
  const context = statefulStream(null);
  const groupContent = statefulStream((index) => `Group ${index}`);
  const components = statefulStream({});
  const computeItemKey = statefulStream(identity$8);
  const headerFooterTag = statefulStream("div");
  const scrollerRef = statefulStream(noop$5);
  const distinctProp = (propName, defaultValue = null) => {
    return statefulStreamFromEmitter(
      pipe(
        components,
        map$2((components2) => components2[propName]),
        distinctUntilChanged()
      ),
      defaultValue
    );
  };
  return {
    context,
    itemContent,
    groupContent,
    components,
    computeItemKey,
    headerFooterTag,
    scrollerRef,
    FooterComponent: distinctProp("Footer"),
    HeaderComponent: distinctProp("Header"),
    TopItemListComponent: distinctProp("TopItemList"),
    ListComponent: distinctProp("List", "div"),
    ItemComponent: distinctProp("Item", "div"),
    GroupComponent: distinctProp("Group", "div"),
    ScrollerComponent: distinctProp("Scroller", "div"),
    EmptyPlaceholder: distinctProp("EmptyPlaceholder"),
    ScrollSeekPlaceholder: distinctProp("ScrollSeekPlaceholder")
  };
});
const combinedSystem$2 = /* @__PURE__ */ system(([listSystem2, propsSystem]) => {
  return { ...listSystem2, ...propsSystem };
}, tup(listSystem, listComponentPropsSystem));
const DefaultScrollSeekPlaceholder$1 = ({ height }) => /* @__PURE__ */ React__default.createElement("div", { style: { height } });
const GROUP_STYLE = { position: positionStickyCssValue(), zIndex: 1, overflowAnchor: "none" };
const ITEM_STYLE$1 = { overflowAnchor: "none" };
const HORIZONTAL_ITEM_STYLE = { ...ITEM_STYLE$1, display: "inline-block", height: "100%" };
const Items$1 = /* @__PURE__ */ React__default.memo(function VirtuosoItems({ showTopList = false }) {
  const listState = useEmitterValue$2("listState");
  const sizeRanges = usePublisher$2("sizeRanges");
  const useWindowScroll = useEmitterValue$2("useWindowScroll");
  const customScrollParent = useEmitterValue$2("customScrollParent");
  const windowScrollContainerStateCallback = usePublisher$2("windowScrollContainerState");
  const _scrollContainerStateCallback = usePublisher$2("scrollContainerState");
  const scrollContainerStateCallback = customScrollParent || useWindowScroll ? windowScrollContainerStateCallback : _scrollContainerStateCallback;
  const itemContent = useEmitterValue$2("itemContent");
  const context = useEmitterValue$2("context");
  const groupContent = useEmitterValue$2("groupContent");
  const trackItemSizes = useEmitterValue$2("trackItemSizes");
  const itemSize = useEmitterValue$2("itemSize");
  const log = useEmitterValue$2("log");
  const listGap = usePublisher$2("gap");
  const horizontalDirection = useEmitterValue$2("horizontalDirection");
  const { callbackRef } = useChangedListContentsSizes(
    sizeRanges,
    itemSize,
    trackItemSizes,
    showTopList ? noop$5 : scrollContainerStateCallback,
    log,
    listGap,
    customScrollParent,
    horizontalDirection,
    useEmitterValue$2("skipAnimationFrameInResizeObserver")
  );
  const [deviation, setDeviation] = React__default.useState(0);
  useEmitter$2("deviation", (value) => {
    if (deviation !== value) {
      setDeviation(value);
    }
  });
  const EmptyPlaceholder = useEmitterValue$2("EmptyPlaceholder");
  const ScrollSeekPlaceholder = useEmitterValue$2("ScrollSeekPlaceholder") || DefaultScrollSeekPlaceholder$1;
  const ListComponent = useEmitterValue$2("ListComponent");
  const ItemComponent = useEmitterValue$2("ItemComponent");
  const GroupComponent = useEmitterValue$2("GroupComponent");
  const computeItemKey = useEmitterValue$2("computeItemKey");
  const isSeeking = useEmitterValue$2("isSeeking");
  const hasGroups2 = useEmitterValue$2("groupIndices").length > 0;
  const alignToBottom = useEmitterValue$2("alignToBottom");
  const initialItemFinalLocationReached = useEmitterValue$2("initialItemFinalLocationReached");
  const containerStyle = showTopList ? {} : {
    boxSizing: "border-box",
    ...horizontalDirection ? {
      whiteSpace: "nowrap",
      display: "inline-block",
      height: "100%",
      paddingLeft: listState.offsetTop,
      paddingRight: listState.offsetBottom,
      marginLeft: deviation !== 0 ? deviation : alignToBottom ? "auto" : 0
    } : {
      marginTop: deviation !== 0 ? deviation : alignToBottom ? "auto" : 0,
      paddingTop: listState.offsetTop,
      paddingBottom: listState.offsetBottom
    },
    ...initialItemFinalLocationReached ? {} : { visibility: "hidden" }
  };
  if (!showTopList && listState.totalCount === 0 && EmptyPlaceholder) {
    return React__default.createElement(EmptyPlaceholder, contextPropIfNotDomElement(EmptyPlaceholder, context));
  }
  return React__default.createElement(
    ListComponent,
    {
      ...contextPropIfNotDomElement(ListComponent, context),
      ref: callbackRef,
      style: containerStyle,
      "data-testid": showTopList ? "virtuoso-top-item-list" : "virtuoso-item-list"
    },
    (showTopList ? listState.topItems : listState.items).map((item) => {
      const index = item.originalIndex;
      const key = computeItemKey(index + listState.firstItemIndex, item.data, context);
      if (isSeeking) {
        return React__default.createElement(ScrollSeekPlaceholder, {
          ...contextPropIfNotDomElement(ScrollSeekPlaceholder, context),
          key,
          index: item.index,
          height: item.size,
          type: item.type || "item",
          ...item.type === "group" ? {} : { groupIndex: item.groupIndex }
        });
      }
      if (item.type === "group") {
        return React__default.createElement(
          GroupComponent,
          {
            ...contextPropIfNotDomElement(GroupComponent, context),
            key,
            "data-index": index,
            "data-known-size": item.size,
            "data-item-index": item.index,
            style: GROUP_STYLE
          },
          groupContent(item.index, context)
        );
      } else {
        return React__default.createElement(
          ItemComponent,
          {
            ...contextPropIfNotDomElement(ItemComponent, context),
            ...itemPropIfNotDomElement(ItemComponent, item.data),
            key,
            "data-index": index,
            "data-known-size": item.size,
            "data-item-index": item.index,
            "data-item-group-index": item.groupIndex,
            style: horizontalDirection ? HORIZONTAL_ITEM_STYLE : ITEM_STYLE$1
          },
          hasGroups2 ? itemContent(item.index, item.groupIndex, item.data, context) : itemContent(item.index, item.data, context)
        );
      }
    })
  );
});
const scrollerStyle = {
  height: "100%",
  outline: "none",
  overflowY: "auto",
  position: "relative",
  WebkitOverflowScrolling: "touch"
};
const horizontalScrollerStyle = {
  outline: "none",
  overflowX: "auto",
  position: "relative"
};
const viewportStyle = (alignToBottom) => ({
  width: "100%",
  height: "100%",
  position: "absolute",
  top: 0,
  ...alignToBottom ? { display: "flex", flexDirection: "column" } : {}
});
const topItemListStyle = {
  width: "100%",
  position: positionStickyCssValue(),
  top: 0,
  zIndex: 1
};
function contextPropIfNotDomElement(element, context) {
  if (typeof element === "string") {
    return void 0;
  }
  return { context };
}
function itemPropIfNotDomElement(element, item) {
  return { item: typeof element === "string" ? void 0 : item };
}
const Header$1 = /* @__PURE__ */ React__default.memo(function VirtuosoHeader() {
  const Header2 = useEmitterValue$2("HeaderComponent");
  const headerHeight = usePublisher$2("headerHeight");
  const headerFooterTag = useEmitterValue$2("headerFooterTag");
  const ref = useSize(
    React__default.useMemo(() => (el) => headerHeight(correctItemSize(el, "height")), [headerHeight]),
    true,
    useEmitterValue$2("skipAnimationFrameInResizeObserver")
  );
  const context = useEmitterValue$2("context");
  return Header2 ? React__default.createElement(headerFooterTag, { ref }, React__default.createElement(Header2, contextPropIfNotDomElement(Header2, context))) : null;
});
const Footer$1 = /* @__PURE__ */ React__default.memo(function VirtuosoFooter() {
  const Footer2 = useEmitterValue$2("FooterComponent");
  const footerHeight = usePublisher$2("footerHeight");
  const headerFooterTag = useEmitterValue$2("headerFooterTag");
  const ref = useSize(
    React__default.useMemo(() => (el) => footerHeight(correctItemSize(el, "height")), [footerHeight]),
    true,
    useEmitterValue$2("skipAnimationFrameInResizeObserver")
  );
  const context = useEmitterValue$2("context");
  return Footer2 ? React__default.createElement(headerFooterTag, { ref }, React__default.createElement(Footer2, contextPropIfNotDomElement(Footer2, context))) : null;
});
function buildScroller({ usePublisher: usePublisher2, useEmitter: useEmitter2, useEmitterValue: useEmitterValue2 }) {
  const Scroller2 = React__default.memo(function VirtuosoScroller({ style, children, ...props }) {
    const scrollContainerStateCallback = usePublisher2("scrollContainerState");
    const ScrollerComponent = useEmitterValue2("ScrollerComponent");
    const smoothScrollTargetReached = usePublisher2("smoothScrollTargetReached");
    const scrollerRefCallback = useEmitterValue2("scrollerRef");
    const context = useEmitterValue2("context");
    const horizontalDirection = useEmitterValue2("horizontalDirection") || false;
    const { scrollerRef, scrollByCallback, scrollToCallback } = useScrollTop(
      scrollContainerStateCallback,
      smoothScrollTargetReached,
      ScrollerComponent,
      scrollerRefCallback,
      void 0,
      horizontalDirection
    );
    useEmitter2("scrollTo", scrollToCallback);
    useEmitter2("scrollBy", scrollByCallback);
    const defaultStyle = horizontalDirection ? horizontalScrollerStyle : scrollerStyle;
    return React__default.createElement(
      ScrollerComponent,
      {
        ref: scrollerRef,
        style: { ...defaultStyle, ...style },
        "data-testid": "virtuoso-scroller",
        "data-virtuoso-scroller": true,
        tabIndex: 0,
        ...props,
        ...contextPropIfNotDomElement(ScrollerComponent, context)
      },
      children
    );
  });
  return Scroller2;
}
function buildWindowScroller({ usePublisher: usePublisher2, useEmitter: useEmitter2, useEmitterValue: useEmitterValue2 }) {
  const Scroller2 = React__default.memo(function VirtuosoWindowScroller({ style, children, ...props }) {
    const scrollContainerStateCallback = usePublisher2("windowScrollContainerState");
    const ScrollerComponent = useEmitterValue2("ScrollerComponent");
    const smoothScrollTargetReached = usePublisher2("smoothScrollTargetReached");
    const totalListHeight = useEmitterValue2("totalListHeight");
    const deviation = useEmitterValue2("deviation");
    const customScrollParent = useEmitterValue2("customScrollParent");
    const context = useEmitterValue2("context");
    const { scrollerRef, scrollByCallback, scrollToCallback } = useScrollTop(
      scrollContainerStateCallback,
      smoothScrollTargetReached,
      ScrollerComponent,
      noop$5,
      customScrollParent
    );
    useIsomorphicLayoutEffect$1$1(() => {
      scrollerRef.current = customScrollParent ? customScrollParent : window;
      return () => {
        scrollerRef.current = null;
      };
    }, [scrollerRef, customScrollParent]);
    useEmitter2("windowScrollTo", scrollToCallback);
    useEmitter2("scrollBy", scrollByCallback);
    return React__default.createElement(
      ScrollerComponent,
      {
        style: { position: "relative", ...style, ...totalListHeight !== 0 ? { height: totalListHeight + deviation } : {} },
        "data-virtuoso-scroller": true,
        ...props,
        ...contextPropIfNotDomElement(ScrollerComponent, context)
      },
      children
    );
  });
  return Scroller2;
}
const Viewport$2 = ({ children }) => {
  const ctx = React__default.useContext(VirtuosoMockContext);
  const viewportHeight = usePublisher$2("viewportHeight");
  const fixedItemHeight = usePublisher$2("fixedItemHeight");
  const alignToBottom = useEmitterValue$2("alignToBottom");
  const horizontalDirection = useEmitterValue$2("horizontalDirection");
  const viewportSizeCallbackMemo = React__default.useMemo(
    () => compose(viewportHeight, (el) => correctItemSize(el, horizontalDirection ? "width" : "height")),
    [viewportHeight, horizontalDirection]
  );
  const viewportRef = useSize(viewportSizeCallbackMemo, true, useEmitterValue$2("skipAnimationFrameInResizeObserver"));
  React__default.useEffect(() => {
    if (ctx) {
      viewportHeight(ctx.viewportHeight);
      fixedItemHeight(ctx.itemHeight);
    }
  }, [ctx, viewportHeight, fixedItemHeight]);
  return /* @__PURE__ */ React__default.createElement("div", { style: viewportStyle(alignToBottom), ref: viewportRef, "data-viewport-type": "element" }, children);
};
const WindowViewport$2 = ({ children }) => {
  const ctx = React__default.useContext(VirtuosoMockContext);
  const windowViewportRect = usePublisher$2("windowViewportRect");
  const fixedItemHeight = usePublisher$2("fixedItemHeight");
  const customScrollParent = useEmitterValue$2("customScrollParent");
  const viewportRef = useWindowViewportRectRef(
    windowViewportRect,
    customScrollParent,
    useEmitterValue$2("skipAnimationFrameInResizeObserver")
  );
  const alignToBottom = useEmitterValue$2("alignToBottom");
  React__default.useEffect(() => {
    if (ctx) {
      fixedItemHeight(ctx.itemHeight);
      windowViewportRect({ offsetTop: 0, visibleHeight: ctx.viewportHeight, visibleWidth: 100 });
    }
  }, [ctx, windowViewportRect, fixedItemHeight]);
  return /* @__PURE__ */ React__default.createElement("div", { ref: viewportRef, style: viewportStyle(alignToBottom), "data-viewport-type": "window" }, children);
};
const TopItemListContainer = ({ children }) => {
  const TopItemList = useEmitterValue$2("TopItemListComponent") || "div";
  const headerHeight = useEmitterValue$2("headerHeight");
  const style = { ...topItemListStyle, marginTop: `${headerHeight}px` };
  const context = useEmitterValue$2("context");
  return React__default.createElement(TopItemList, { style, ...contextPropIfNotDomElement(TopItemList, context) }, children);
};
const ListRoot = /* @__PURE__ */ React__default.memo(function VirtuosoRoot(props) {
  const useWindowScroll = useEmitterValue$2("useWindowScroll");
  const showTopList = useEmitterValue$2("topItemsIndexes").length > 0;
  const customScrollParent = useEmitterValue$2("customScrollParent");
  const TheScroller = customScrollParent || useWindowScroll ? WindowScroller$2 : Scroller$2;
  const TheViewport = customScrollParent || useWindowScroll ? WindowViewport$2 : Viewport$2;
  return /* @__PURE__ */ React__default.createElement(TheScroller, { ...props }, showTopList && /* @__PURE__ */ React__default.createElement(TopItemListContainer, null, /* @__PURE__ */ React__default.createElement(Items$1, { showTopList: true })), /* @__PURE__ */ React__default.createElement(TheViewport, null, /* @__PURE__ */ React__default.createElement(Header$1, null), /* @__PURE__ */ React__default.createElement(Items$1, null), /* @__PURE__ */ React__default.createElement(Footer$1, null)));
});
const {
  Component: List,
  usePublisher: usePublisher$2,
  useEmitterValue: useEmitterValue$2,
  useEmitter: useEmitter$2
} = /* @__PURE__ */ systemToComponent(
  combinedSystem$2,
  {
    required: {},
    optional: {
      restoreStateFrom: "restoreStateFrom",
      context: "context",
      followOutput: "followOutput",
      itemContent: "itemContent",
      groupContent: "groupContent",
      overscan: "overscan",
      increaseViewportBy: "increaseViewportBy",
      totalCount: "totalCount",
      groupCounts: "groupCounts",
      topItemCount: "topItemCount",
      firstItemIndex: "firstItemIndex",
      initialTopMostItemIndex: "initialTopMostItemIndex",
      components: "components",
      atBottomThreshold: "atBottomThreshold",
      atTopThreshold: "atTopThreshold",
      computeItemKey: "computeItemKey",
      defaultItemHeight: "defaultItemHeight",
      fixedItemHeight: "fixedItemHeight",
      itemSize: "itemSize",
      scrollSeekConfiguration: "scrollSeekConfiguration",
      headerFooterTag: "headerFooterTag",
      data: "data",
      initialItemCount: "initialItemCount",
      initialScrollTop: "initialScrollTop",
      alignToBottom: "alignToBottom",
      useWindowScroll: "useWindowScroll",
      customScrollParent: "customScrollParent",
      scrollerRef: "scrollerRef",
      logLevel: "logLevel",
      horizontalDirection: "horizontalDirection",
      skipAnimationFrameInResizeObserver: "skipAnimationFrameInResizeObserver"
    },
    methods: {
      scrollToIndex: "scrollToIndex",
      scrollIntoView: "scrollIntoView",
      scrollTo: "scrollTo",
      scrollBy: "scrollBy",
      autoscrollToBottom: "autoscrollToBottom",
      getState: "getState"
    },
    events: {
      isScrolling: "isScrolling",
      endReached: "endReached",
      startReached: "startReached",
      rangeChanged: "rangeChanged",
      atBottomStateChange: "atBottomStateChange",
      atTopStateChange: "atTopStateChange",
      totalListHeightChanged: "totalListHeightChanged",
      itemsRendered: "itemsRendered",
      groupIndices: "groupIndices"
    }
  },
  ListRoot
);
const Scroller$2 = /* @__PURE__ */ buildScroller({ usePublisher: usePublisher$2, useEmitterValue: useEmitterValue$2, useEmitter: useEmitter$2 });
const WindowScroller$2 = /* @__PURE__ */ buildWindowScroller({ usePublisher: usePublisher$2, useEmitterValue: useEmitterValue$2, useEmitter: useEmitter$2 });
const GroupedVirtuoso = List;
const INITIAL_GRID_STATE = {
  items: [],
  offsetBottom: 0,
  offsetTop: 0,
  top: 0,
  bottom: 0,
  itemHeight: 0,
  itemWidth: 0
};
const PROBE_GRID_STATE = {
  items: [{ index: 0 }],
  offsetBottom: 0,
  offsetTop: 0,
  top: 0,
  bottom: 0,
  itemHeight: 0,
  itemWidth: 0
};
const { round, ceil, floor, min, max } = Math;
function buildProbeGridState(items) {
  return {
    ...PROBE_GRID_STATE,
    items
  };
}
function buildItems(startIndex, endIndex, data) {
  return Array.from({ length: endIndex - startIndex + 1 }).map((_, i) => {
    const dataItem = data === null ? null : data[i + startIndex];
    return { index: i + startIndex, data: dataItem };
  });
}
function gapComparator(prev, next) {
  return prev && prev.column === next.column && prev.row === next.row;
}
function dimensionComparator(prev, next) {
  return prev && prev.width === next.width && prev.height === next.height;
}
const gridSystem = /* @__PURE__ */ system(
  ([
    { overscan, visibleRange, listBoundary },
    { scrollTop, viewportHeight, scrollBy, scrollTo, smoothScrollTargetReached, scrollContainerState, footerHeight, headerHeight },
    stateFlags,
    scrollSeek,
    { propsReady, didMount },
    { windowViewportRect, useWindowScroll, customScrollParent, windowScrollContainerState, windowScrollTo },
    log
  ]) => {
    const totalCount = statefulStream(0);
    const initialItemCount = statefulStream(0);
    const gridState = statefulStream(INITIAL_GRID_STATE);
    const viewportDimensions = statefulStream({ height: 0, width: 0 });
    const itemDimensions = statefulStream({ height: 0, width: 0 });
    const scrollToIndex = stream();
    const scrollHeight = stream();
    const deviation = statefulStream(0);
    const data = statefulStream(null);
    const gap = statefulStream({ row: 0, column: 0 });
    const stateChanged = stream();
    const restoreStateFrom = stream();
    const stateRestoreInProgress = statefulStream(false);
    const initialTopMostItemIndex = statefulStream(0);
    const scrolledToInitialItem = statefulStream(true);
    const scrollScheduled = statefulStream(false);
    const horizontalDirection = statefulStream(false);
    subscribe(
      pipe(
        didMount,
        withLatestFrom(initialTopMostItemIndex),
        filter$1(([_, location]) => !!location)
      ),
      () => {
        publish(scrolledToInitialItem, false);
        publish(initialItemCount, 0);
      }
    );
    subscribe(
      pipe(
        combineLatest(didMount, scrolledToInitialItem, itemDimensions, viewportDimensions, initialTopMostItemIndex, scrollScheduled),
        filter$1(([didMount2, scrolledToInitialItem2, itemDimensions2, viewportDimensions2, , scrollScheduled2]) => {
          return didMount2 && !scrolledToInitialItem2 && itemDimensions2.height !== 0 && viewportDimensions2.height !== 0 && !scrollScheduled2;
        })
      ),
      ([, , , , initialTopMostItemIndex2]) => {
        publish(scrollScheduled, true);
        skipFrames(1, () => {
          publish(scrollToIndex, initialTopMostItemIndex2);
        });
        handleNext(pipe(scrollTop), () => {
          publish(listBoundary, [0, 0]);
          publish(scrolledToInitialItem, true);
        });
      }
    );
    connect(
      pipe(
        restoreStateFrom,
        filter$1((value) => value !== void 0 && value !== null && value.scrollTop > 0),
        mapTo(0)
      ),
      initialItemCount
    );
    subscribe(
      pipe(
        didMount,
        withLatestFrom(restoreStateFrom),
        filter$1(([, snapshot]) => snapshot !== void 0 && snapshot !== null)
      ),
      ([, snapshot]) => {
        if (!snapshot) {
          return;
        }
        publish(viewportDimensions, snapshot.viewport), publish(itemDimensions, snapshot == null ? void 0 : snapshot.item);
        publish(gap, snapshot.gap);
        if (snapshot.scrollTop > 0) {
          publish(stateRestoreInProgress, true);
          handleNext(pipe(scrollTop, skip(1)), (_value) => {
            publish(stateRestoreInProgress, false);
          });
          publish(scrollTo, { top: snapshot.scrollTop });
        }
      }
    );
    connect(
      pipe(
        viewportDimensions,
        map$2(({ height }) => height)
      ),
      viewportHeight
    );
    connect(
      pipe(
        combineLatest(
          duc(viewportDimensions, dimensionComparator),
          duc(itemDimensions, dimensionComparator),
          duc(gap, (prev, next) => prev && prev.column === next.column && prev.row === next.row),
          duc(scrollTop)
        ),
        map$2(([viewport, item, gap2, scrollTop2]) => ({
          viewport,
          item,
          gap: gap2,
          scrollTop: scrollTop2
        }))
      ),
      stateChanged
    );
    connect(
      pipe(
        combineLatest(
          duc(totalCount),
          visibleRange,
          duc(gap, gapComparator),
          duc(itemDimensions, dimensionComparator),
          duc(viewportDimensions, dimensionComparator),
          duc(data),
          duc(initialItemCount),
          duc(stateRestoreInProgress),
          duc(scrolledToInitialItem),
          duc(initialTopMostItemIndex)
        ),
        filter$1(([, , , , , , , stateRestoreInProgress2]) => {
          return !stateRestoreInProgress2;
        }),
        map$2(
          ([
            totalCount2,
            [startOffset, endOffset],
            gap2,
            item,
            viewport,
            data2,
            initialItemCount2,
            ,
            scrolledToInitialItem2,
            initialTopMostItemIndex2
          ]) => {
            const { row: rowGap, column: columnGap } = gap2;
            const { height: itemHeight, width: itemWidth } = item;
            const { width: viewportWidth } = viewport;
            if (initialItemCount2 === 0 && (totalCount2 === 0 || viewportWidth === 0)) {
              return INITIAL_GRID_STATE;
            }
            if (itemWidth === 0) {
              const startIndex2 = getInitialTopMostItemIndexNumber(initialTopMostItemIndex2, totalCount2);
              const endIndex2 = startIndex2 === 0 ? Math.max(initialItemCount2 - 1, 0) : startIndex2;
              return buildProbeGridState(buildItems(startIndex2, endIndex2, data2));
            }
            const perRow = itemsPerRow(viewportWidth, itemWidth, columnGap);
            let startIndex;
            let endIndex;
            if (!scrolledToInitialItem2) {
              startIndex = 0;
              endIndex = -1;
            } else if (startOffset === 0 && endOffset === 0 && initialItemCount2 > 0) {
              startIndex = 0;
              endIndex = initialItemCount2 - 1;
            } else {
              startIndex = perRow * floor((startOffset + rowGap) / (itemHeight + rowGap));
              endIndex = perRow * ceil((endOffset + rowGap) / (itemHeight + rowGap)) - 1;
              endIndex = min(totalCount2 - 1, max(endIndex, perRow - 1));
              startIndex = min(endIndex, max(0, startIndex));
            }
            const items = buildItems(startIndex, endIndex, data2);
            const { top, bottom } = gridLayout(viewport, gap2, item, items);
            const rowCount = ceil(totalCount2 / perRow);
            const totalHeight = rowCount * itemHeight + (rowCount - 1) * rowGap;
            const offsetBottom = totalHeight - bottom;
            return { items, offsetTop: top, offsetBottom, top, bottom, itemHeight, itemWidth };
          }
        )
      ),
      gridState
    );
    connect(
      pipe(
        data,
        filter$1((data2) => data2 !== null),
        map$2((data2) => data2.length)
      ),
      totalCount
    );
    connect(
      pipe(
        combineLatest(viewportDimensions, itemDimensions, gridState, gap),
        filter$1(([viewportDimensions2, itemDimensions2, { items }]) => {
          return items.length > 0 && itemDimensions2.height !== 0 && viewportDimensions2.height !== 0;
        }),
        map$2(([viewportDimensions2, itemDimensions2, { items }, gap2]) => {
          const { top, bottom } = gridLayout(viewportDimensions2, gap2, itemDimensions2, items);
          return [top, bottom];
        }),
        distinctUntilChanged(tupleComparator)
      ),
      listBoundary
    );
    const hasScrolled = statefulStream(false);
    connect(
      pipe(
        scrollTop,
        withLatestFrom(hasScrolled),
        map$2(([scrollTop2, hasScrolled2]) => {
          return hasScrolled2 || scrollTop2 !== 0;
        })
      ),
      hasScrolled
    );
    const endReached = streamFromEmitter(
      pipe(
        duc(gridState),
        filter$1(({ items }) => items.length > 0),
        withLatestFrom(totalCount, hasScrolled),
        filter$1(([{ items }, totalCount2, hasScrolled2]) => hasScrolled2 && items[items.length - 1].index === totalCount2 - 1),
        map$2(([, totalCount2]) => totalCount2 - 1),
        distinctUntilChanged()
      )
    );
    const startReached = streamFromEmitter(
      pipe(
        duc(gridState),
        filter$1(({ items }) => {
          return items.length > 0 && items[0].index === 0;
        }),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        mapTo(0),
        distinctUntilChanged()
      )
    );
    const rangeChanged = streamFromEmitter(
      pipe(
        duc(gridState),
        withLatestFrom(stateRestoreInProgress),
        filter$1(([{ items }, stateRestoreInProgress2]) => items.length > 0 && !stateRestoreInProgress2),
        map$2(([{ items }]) => {
          return {
            startIndex: items[0].index,
            endIndex: items[items.length - 1].index
          };
        }),
        distinctUntilChanged(rangeComparator),
        throttleTime(0)
      )
    );
    connect(rangeChanged, scrollSeek.scrollSeekRangeChanged);
    connect(
      pipe(
        scrollToIndex,
        withLatestFrom(viewportDimensions, itemDimensions, totalCount, gap),
        map$2(([location, viewportDimensions2, itemDimensions2, totalCount2, gap2]) => {
          const normalLocation = normalizeIndexLocation(location);
          const { align, behavior, offset } = normalLocation;
          let index = normalLocation.index;
          if (index === "LAST") {
            index = totalCount2 - 1;
          }
          index = max(0, index, min(totalCount2 - 1, index));
          let top = itemTop(viewportDimensions2, gap2, itemDimensions2, index);
          if (align === "end") {
            top = round(top - viewportDimensions2.height + itemDimensions2.height);
          } else if (align === "center") {
            top = round(top - viewportDimensions2.height / 2 + itemDimensions2.height / 2);
          }
          if (offset) {
            top += offset;
          }
          return { top, behavior };
        })
      ),
      scrollTo
    );
    const totalListHeight = statefulStreamFromEmitter(
      pipe(
        gridState,
        map$2((gridState2) => {
          return gridState2.offsetBottom + gridState2.bottom;
        })
      ),
      0
    );
    connect(
      pipe(
        windowViewportRect,
        map$2((viewportInfo) => ({ width: viewportInfo.visibleWidth, height: viewportInfo.visibleHeight }))
      ),
      viewportDimensions
    );
    return {
      // input
      data,
      totalCount,
      viewportDimensions,
      itemDimensions,
      scrollTop,
      scrollHeight,
      overscan,
      scrollBy,
      scrollTo,
      scrollToIndex,
      smoothScrollTargetReached,
      windowViewportRect,
      windowScrollTo,
      useWindowScroll,
      customScrollParent,
      windowScrollContainerState,
      deviation,
      scrollContainerState,
      footerHeight,
      headerHeight,
      initialItemCount,
      gap,
      restoreStateFrom,
      ...scrollSeek,
      initialTopMostItemIndex,
      horizontalDirection,
      // output
      gridState,
      totalListHeight,
      ...stateFlags,
      startReached,
      endReached,
      rangeChanged,
      stateChanged,
      propsReady,
      stateRestoreInProgress,
      ...log
    };
  },
  tup(sizeRangeSystem, domIOSystem, stateFlagsSystem, scrollSeekSystem, propsReadySystem, windowScrollerSystem, loggerSystem)
);
function gridLayout(viewport, gap, item, items) {
  const { height: itemHeight } = item;
  if (itemHeight === void 0 || items.length === 0) {
    return { top: 0, bottom: 0 };
  }
  const top = itemTop(viewport, gap, item, items[0].index);
  const bottom = itemTop(viewport, gap, item, items[items.length - 1].index) + itemHeight;
  return { top, bottom };
}
function itemTop(viewport, gap, item, index) {
  const perRow = itemsPerRow(viewport.width, item.width, gap.column);
  const rowCount = floor(index / perRow);
  const top = rowCount * item.height + max(0, rowCount - 1) * gap.row;
  return top > 0 ? top + gap.row : top;
}
function itemsPerRow(viewportWidth, itemWidth, gap) {
  return max(1, floor((viewportWidth + gap) / (floor(itemWidth) + gap)));
}
const gridComponentPropsSystem = /* @__PURE__ */ system(() => {
  const itemContent = statefulStream((index) => `Item ${index}`);
  const components = statefulStream({});
  const context = statefulStream(null);
  const itemClassName = statefulStream("virtuoso-grid-item");
  const listClassName = statefulStream("virtuoso-grid-list");
  const computeItemKey = statefulStream(identity$8);
  const headerFooterTag = statefulStream("div");
  const scrollerRef = statefulStream(noop$5);
  const distinctProp = (propName, defaultValue = null) => {
    return statefulStreamFromEmitter(
      pipe(
        components,
        map$2((components2) => components2[propName]),
        distinctUntilChanged()
      ),
      defaultValue
    );
  };
  return {
    context,
    itemContent,
    components,
    computeItemKey,
    itemClassName,
    listClassName,
    headerFooterTag,
    scrollerRef,
    FooterComponent: distinctProp("Footer"),
    HeaderComponent: distinctProp("Header"),
    ListComponent: distinctProp("List", "div"),
    ItemComponent: distinctProp("Item", "div"),
    ScrollerComponent: distinctProp("Scroller", "div"),
    ScrollSeekPlaceholder: distinctProp("ScrollSeekPlaceholder", "div")
  };
});
const combinedSystem$1 = /* @__PURE__ */ system(([gridSystem2, gridComponentPropsSystem2]) => {
  return { ...gridSystem2, ...gridComponentPropsSystem2 };
}, tup(gridSystem, gridComponentPropsSystem));
const GridItems = /* @__PURE__ */ React__default.memo(function GridItems2() {
  const gridState = useEmitterValue$1("gridState");
  const listClassName = useEmitterValue$1("listClassName");
  const itemClassName = useEmitterValue$1("itemClassName");
  const itemContent = useEmitterValue$1("itemContent");
  const computeItemKey = useEmitterValue$1("computeItemKey");
  const isSeeking = useEmitterValue$1("isSeeking");
  const scrollHeightCallback = usePublisher$1("scrollHeight");
  const ItemComponent = useEmitterValue$1("ItemComponent");
  const ListComponent = useEmitterValue$1("ListComponent");
  const ScrollSeekPlaceholder = useEmitterValue$1("ScrollSeekPlaceholder");
  const context = useEmitterValue$1("context");
  const itemDimensions = usePublisher$1("itemDimensions");
  const gridGap = usePublisher$1("gap");
  const log = useEmitterValue$1("log");
  const stateRestoreInProgress = useEmitterValue$1("stateRestoreInProgress");
  const listRef = useSize(
    React__default.useMemo(
      () => (el) => {
        const scrollHeight = el.parentElement.parentElement.scrollHeight;
        scrollHeightCallback(scrollHeight);
        const firstItem = el.firstChild;
        if (firstItem) {
          const { width, height } = firstItem.getBoundingClientRect();
          itemDimensions({ width, height });
        }
        gridGap({
          row: resolveGapValue("row-gap", getComputedStyle(el).rowGap, log),
          column: resolveGapValue("column-gap", getComputedStyle(el).columnGap, log)
        });
      },
      [scrollHeightCallback, itemDimensions, gridGap, log]
    ),
    true,
    false
  );
  if (stateRestoreInProgress) {
    return null;
  }
  return React__default.createElement(
    ListComponent,
    {
      ref: listRef,
      className: listClassName,
      ...contextPropIfNotDomElement(ListComponent, context),
      style: { paddingTop: gridState.offsetTop, paddingBottom: gridState.offsetBottom },
      "data-testid": "virtuoso-item-list"
    },
    gridState.items.map((item) => {
      const key = computeItemKey(item.index, item.data, context);
      return isSeeking ? React__default.createElement(ScrollSeekPlaceholder, {
        key,
        ...contextPropIfNotDomElement(ScrollSeekPlaceholder, context),
        index: item.index,
        height: gridState.itemHeight,
        width: gridState.itemWidth
      }) : React__default.createElement(
        ItemComponent,
        { ...contextPropIfNotDomElement(ItemComponent, context), className: itemClassName, "data-index": item.index, key },
        itemContent(item.index, item.data, context)
      );
    })
  );
});
const Header$2 = React__default.memo(function VirtuosoHeader2() {
  const Header2 = useEmitterValue$1("HeaderComponent");
  const headerHeight = usePublisher$1("headerHeight");
  const headerFooterTag = useEmitterValue$1("headerFooterTag");
  const ref = useSize(
    React__default.useMemo(() => (el) => headerHeight(correctItemSize(el, "height")), [headerHeight]),
    true,
    false
  );
  const context = useEmitterValue$1("context");
  return Header2 ? React__default.createElement(headerFooterTag, { ref }, React__default.createElement(Header2, contextPropIfNotDomElement(Header2, context))) : null;
});
const Footer = React__default.memo(function VirtuosoGridFooter() {
  const Footer2 = useEmitterValue$1("FooterComponent");
  const footerHeight = usePublisher$1("footerHeight");
  const headerFooterTag = useEmitterValue$1("headerFooterTag");
  const ref = useSize(
    React__default.useMemo(() => (el) => footerHeight(correctItemSize(el, "height")), [footerHeight]),
    true,
    false
  );
  const context = useEmitterValue$1("context");
  return Footer2 ? React__default.createElement(headerFooterTag, { ref }, React__default.createElement(Footer2, contextPropIfNotDomElement(Footer2, context))) : null;
});
const Viewport$1 = ({ children }) => {
  const ctx = React__default.useContext(VirtuosoGridMockContext);
  const itemDimensions = usePublisher$1("itemDimensions");
  const viewportDimensions = usePublisher$1("viewportDimensions");
  const viewportRef = useSize(
    React__default.useMemo(
      () => (el) => {
        viewportDimensions(el.getBoundingClientRect());
      },
      [viewportDimensions]
    ),
    true,
    false
  );
  React__default.useEffect(() => {
    if (ctx) {
      viewportDimensions({ height: ctx.viewportHeight, width: ctx.viewportWidth });
      itemDimensions({ height: ctx.itemHeight, width: ctx.itemWidth });
    }
  }, [ctx, viewportDimensions, itemDimensions]);
  return /* @__PURE__ */ React__default.createElement("div", { style: viewportStyle(false), ref: viewportRef }, children);
};
const WindowViewport$1 = ({ children }) => {
  const ctx = React__default.useContext(VirtuosoGridMockContext);
  const windowViewportRect = usePublisher$1("windowViewportRect");
  const itemDimensions = usePublisher$1("itemDimensions");
  const customScrollParent = useEmitterValue$1("customScrollParent");
  const viewportRef = useWindowViewportRectRef(windowViewportRect, customScrollParent, false);
  React__default.useEffect(() => {
    if (ctx) {
      itemDimensions({ height: ctx.itemHeight, width: ctx.itemWidth });
      windowViewportRect({ offsetTop: 0, visibleHeight: ctx.viewportHeight, visibleWidth: ctx.viewportWidth });
    }
  }, [ctx, windowViewportRect, itemDimensions]);
  return /* @__PURE__ */ React__default.createElement("div", { ref: viewportRef, style: viewportStyle(false) }, children);
};
const GridRoot = /* @__PURE__ */ React__default.memo(function GridRoot2({ ...props }) {
  const useWindowScroll = useEmitterValue$1("useWindowScroll");
  const customScrollParent = useEmitterValue$1("customScrollParent");
  const TheScroller = customScrollParent || useWindowScroll ? WindowScroller$1 : Scroller$1;
  const TheViewport = customScrollParent || useWindowScroll ? WindowViewport$1 : Viewport$1;
  return /* @__PURE__ */ React__default.createElement(TheScroller, { ...props }, /* @__PURE__ */ React__default.createElement(TheViewport, null, /* @__PURE__ */ React__default.createElement(Header$2, null), /* @__PURE__ */ React__default.createElement(GridItems, null), /* @__PURE__ */ React__default.createElement(Footer, null)));
});
const {
  Component: Grid,
  usePublisher: usePublisher$1,
  useEmitterValue: useEmitterValue$1,
  useEmitter: useEmitter$1
} = /* @__PURE__ */ systemToComponent(
  combinedSystem$1,
  {
    optional: {
      context: "context",
      totalCount: "totalCount",
      overscan: "overscan",
      itemContent: "itemContent",
      components: "components",
      computeItemKey: "computeItemKey",
      data: "data",
      initialItemCount: "initialItemCount",
      scrollSeekConfiguration: "scrollSeekConfiguration",
      headerFooterTag: "headerFooterTag",
      listClassName: "listClassName",
      itemClassName: "itemClassName",
      useWindowScroll: "useWindowScroll",
      customScrollParent: "customScrollParent",
      scrollerRef: "scrollerRef",
      logLevel: "logLevel",
      restoreStateFrom: "restoreStateFrom",
      initialTopMostItemIndex: "initialTopMostItemIndex"
    },
    methods: {
      scrollTo: "scrollTo",
      scrollBy: "scrollBy",
      scrollToIndex: "scrollToIndex"
    },
    events: {
      isScrolling: "isScrolling",
      endReached: "endReached",
      startReached: "startReached",
      rangeChanged: "rangeChanged",
      atBottomStateChange: "atBottomStateChange",
      atTopStateChange: "atTopStateChange",
      stateChanged: "stateChanged"
    }
  },
  GridRoot
);
const Scroller$1 = /* @__PURE__ */ buildScroller({ usePublisher: usePublisher$1, useEmitterValue: useEmitterValue$1, useEmitter: useEmitter$1 });
const WindowScroller$1 = /* @__PURE__ */ buildWindowScroller({ usePublisher: usePublisher$1, useEmitterValue: useEmitterValue$1, useEmitter: useEmitter$1 });
function resolveGapValue(property, value, log) {
  if (value !== "normal" && !(value == null ? void 0 : value.endsWith("px"))) {
    log(`${property} was not resolved to pixel value correctly`, value, LogLevel.WARN);
  }
  if (value === "normal") {
    return 0;
  }
  return parseInt(value != null ? value : "0", 10);
}
const tableComponentPropsSystem = /* @__PURE__ */ system(() => {
  const itemContent = statefulStream((index) => /* @__PURE__ */ React__default.createElement("td", null, "Item $", index));
  const context = statefulStream(null);
  const fixedHeaderContent = statefulStream(null);
  const fixedFooterContent = statefulStream(null);
  const components = statefulStream({});
  const computeItemKey = statefulStream(identity$8);
  const scrollerRef = statefulStream(noop$5);
  const distinctProp = (propName, defaultValue = null) => {
    return statefulStreamFromEmitter(
      pipe(
        components,
        map$2((components2) => components2[propName]),
        distinctUntilChanged()
      ),
      defaultValue
    );
  };
  return {
    context,
    itemContent,
    fixedHeaderContent,
    fixedFooterContent,
    components,
    computeItemKey,
    scrollerRef,
    TableComponent: distinctProp("Table", "table"),
    TableHeadComponent: distinctProp("TableHead", "thead"),
    TableFooterComponent: distinctProp("TableFoot", "tfoot"),
    TableBodyComponent: distinctProp("TableBody", "tbody"),
    TableRowComponent: distinctProp("TableRow", "tr"),
    ScrollerComponent: distinctProp("Scroller", "div"),
    EmptyPlaceholder: distinctProp("EmptyPlaceholder"),
    ScrollSeekPlaceholder: distinctProp("ScrollSeekPlaceholder"),
    FillerRow: distinctProp("FillerRow")
  };
});
const combinedSystem = /* @__PURE__ */ system(([listSystem2, propsSystem]) => {
  return { ...listSystem2, ...propsSystem };
}, tup(listSystem, tableComponentPropsSystem));
const DefaultScrollSeekPlaceholder = ({ height }) => /* @__PURE__ */ React__default.createElement("tr", null, /* @__PURE__ */ React__default.createElement("td", { style: { height } }));
const DefaultFillerRow = ({ height }) => /* @__PURE__ */ React__default.createElement("tr", null, /* @__PURE__ */ React__default.createElement("td", { style: { height, padding: 0, border: 0 } }));
const ITEM_STYLE = { overflowAnchor: "none" };
const Items = /* @__PURE__ */ React__default.memo(function VirtuosoItems2() {
  const listState = useEmitterValue("listState");
  const sizeRanges = usePublisher("sizeRanges");
  const useWindowScroll = useEmitterValue("useWindowScroll");
  const customScrollParent = useEmitterValue("customScrollParent");
  const windowScrollContainerStateCallback = usePublisher("windowScrollContainerState");
  const _scrollContainerStateCallback = usePublisher("scrollContainerState");
  const scrollContainerStateCallback = customScrollParent || useWindowScroll ? windowScrollContainerStateCallback : _scrollContainerStateCallback;
  const itemContent = useEmitterValue("itemContent");
  const trackItemSizes = useEmitterValue("trackItemSizes");
  const itemSize = useEmitterValue("itemSize");
  const log = useEmitterValue("log");
  const { callbackRef, ref } = useChangedListContentsSizes(
    sizeRanges,
    itemSize,
    trackItemSizes,
    scrollContainerStateCallback,
    log,
    void 0,
    customScrollParent,
    false,
    useEmitterValue("skipAnimationFrameInResizeObserver")
  );
  const [deviation, setDeviation] = React__default.useState(0);
  useEmitter("deviation", (value) => {
    if (deviation !== value) {
      ref.current.style.marginTop = `${value}px`;
      setDeviation(value);
    }
  });
  const EmptyPlaceholder = useEmitterValue("EmptyPlaceholder");
  const ScrollSeekPlaceholder = useEmitterValue("ScrollSeekPlaceholder") || DefaultScrollSeekPlaceholder;
  const FillerRow = useEmitterValue("FillerRow") || DefaultFillerRow;
  const TableBodyComponent = useEmitterValue("TableBodyComponent");
  const TableRowComponent = useEmitterValue("TableRowComponent");
  const computeItemKey = useEmitterValue("computeItemKey");
  const isSeeking = useEmitterValue("isSeeking");
  const paddingTopAddition = useEmitterValue("paddingTopAddition");
  const firstItemIndex = useEmitterValue("firstItemIndex");
  const statefulTotalCount = useEmitterValue("statefulTotalCount");
  const context = useEmitterValue("context");
  if (statefulTotalCount === 0 && EmptyPlaceholder) {
    return React__default.createElement(EmptyPlaceholder, contextPropIfNotDomElement(EmptyPlaceholder, context));
  }
  const paddingTop = listState.offsetTop + paddingTopAddition + deviation;
  const paddingBottom = listState.offsetBottom;
  const paddingTopEl = paddingTop > 0 ? /* @__PURE__ */ React__default.createElement(FillerRow, { height: paddingTop, key: "padding-top", context }) : null;
  const paddingBottomEl = paddingBottom > 0 ? /* @__PURE__ */ React__default.createElement(FillerRow, { height: paddingBottom, key: "padding-bottom", context }) : null;
  const items = listState.items.map((item) => {
    const index = item.originalIndex;
    const key = computeItemKey(index + firstItemIndex, item.data, context);
    if (isSeeking) {
      return React__default.createElement(ScrollSeekPlaceholder, {
        ...contextPropIfNotDomElement(ScrollSeekPlaceholder, context),
        key,
        index: item.index,
        height: item.size,
        type: item.type || "item"
      });
    }
    return React__default.createElement(
      TableRowComponent,
      {
        ...contextPropIfNotDomElement(TableRowComponent, context),
        ...itemPropIfNotDomElement(TableRowComponent, item.data),
        key,
        "data-index": index,
        "data-known-size": item.size,
        "data-item-index": item.index,
        style: ITEM_STYLE
      },
      itemContent(item.index, item.data, context)
    );
  });
  return React__default.createElement(
    TableBodyComponent,
    { ref: callbackRef, "data-testid": "virtuoso-item-list", ...contextPropIfNotDomElement(TableBodyComponent, context) },
    [paddingTopEl, ...items, paddingBottomEl]
  );
});
const Viewport = ({ children }) => {
  const ctx = React__default.useContext(VirtuosoMockContext);
  const viewportHeight = usePublisher("viewportHeight");
  const fixedItemHeight = usePublisher("fixedItemHeight");
  const viewportRef = useSize(
    React__default.useMemo(() => compose(viewportHeight, (el) => correctItemSize(el, "height")), [viewportHeight]),
    true,
    useEmitterValue("skipAnimationFrameInResizeObserver")
  );
  React__default.useEffect(() => {
    if (ctx) {
      viewportHeight(ctx.viewportHeight);
      fixedItemHeight(ctx.itemHeight);
    }
  }, [ctx, viewportHeight, fixedItemHeight]);
  return /* @__PURE__ */ React__default.createElement("div", { style: viewportStyle(false), ref: viewportRef, "data-viewport-type": "element" }, children);
};
const WindowViewport = ({ children }) => {
  const ctx = React__default.useContext(VirtuosoMockContext);
  const windowViewportRect = usePublisher("windowViewportRect");
  const fixedItemHeight = usePublisher("fixedItemHeight");
  const customScrollParent = useEmitterValue("customScrollParent");
  const viewportRef = useWindowViewportRectRef(
    windowViewportRect,
    customScrollParent,
    useEmitterValue("skipAnimationFrameInResizeObserver")
  );
  React__default.useEffect(() => {
    if (ctx) {
      fixedItemHeight(ctx.itemHeight);
      windowViewportRect({ offsetTop: 0, visibleHeight: ctx.viewportHeight, visibleWidth: 100 });
    }
  }, [ctx, windowViewportRect, fixedItemHeight]);
  return /* @__PURE__ */ React__default.createElement("div", { ref: viewportRef, style: viewportStyle(false), "data-viewport-type": "window" }, children);
};
const TableRoot = /* @__PURE__ */ React__default.memo(function TableVirtuosoRoot(props) {
  const useWindowScroll = useEmitterValue("useWindowScroll");
  const customScrollParent = useEmitterValue("customScrollParent");
  const fixedHeaderHeight = usePublisher("fixedHeaderHeight");
  const fixedFooterHeight = usePublisher("fixedFooterHeight");
  const fixedHeaderContent = useEmitterValue("fixedHeaderContent");
  const fixedFooterContent = useEmitterValue("fixedFooterContent");
  const context = useEmitterValue("context");
  const theadRef = useSize(
    React__default.useMemo(() => compose(fixedHeaderHeight, (el) => correctItemSize(el, "height")), [fixedHeaderHeight]),
    true,
    useEmitterValue("skipAnimationFrameInResizeObserver")
  );
  const tfootRef = useSize(
    React__default.useMemo(() => compose(fixedFooterHeight, (el) => correctItemSize(el, "height")), [fixedFooterHeight]),
    true,
    useEmitterValue("skipAnimationFrameInResizeObserver")
  );
  const TheScroller = customScrollParent || useWindowScroll ? WindowScroller : Scroller;
  const TheViewport = customScrollParent || useWindowScroll ? WindowViewport : Viewport;
  const TheTable = useEmitterValue("TableComponent");
  const TheTHead = useEmitterValue("TableHeadComponent");
  const TheTFoot = useEmitterValue("TableFooterComponent");
  const theHead = fixedHeaderContent ? React__default.createElement(
    TheTHead,
    {
      key: "TableHead",
      style: { zIndex: 2, position: "sticky", top: 0 },
      ref: theadRef,
      ...contextPropIfNotDomElement(TheTHead, context)
    },
    fixedHeaderContent()
  ) : null;
  const theFoot = fixedFooterContent ? React__default.createElement(
    TheTFoot,
    {
      key: "TableFoot",
      style: { zIndex: 1, position: "sticky", bottom: 0 },
      ref: tfootRef,
      ...contextPropIfNotDomElement(TheTFoot, context)
    },
    fixedFooterContent()
  ) : null;
  return /* @__PURE__ */ React__default.createElement(TheScroller, { ...props }, /* @__PURE__ */ React__default.createElement(TheViewport, null, React__default.createElement(
    TheTable,
    { style: { borderSpacing: 0, overflowAnchor: "none" }, ...contextPropIfNotDomElement(TheTable, context) },
    [theHead, /* @__PURE__ */ React__default.createElement(Items, { key: "TableBody" }), theFoot]
  )));
});
const {
  Component: Table$1,
  usePublisher,
  useEmitterValue,
  useEmitter
} = /* @__PURE__ */ systemToComponent(
  combinedSystem,
  {
    required: {},
    optional: {
      restoreStateFrom: "restoreStateFrom",
      context: "context",
      followOutput: "followOutput",
      firstItemIndex: "firstItemIndex",
      itemContent: "itemContent",
      fixedHeaderContent: "fixedHeaderContent",
      fixedFooterContent: "fixedFooterContent",
      overscan: "overscan",
      increaseViewportBy: "increaseViewportBy",
      totalCount: "totalCount",
      topItemCount: "topItemCount",
      initialTopMostItemIndex: "initialTopMostItemIndex",
      components: "components",
      groupCounts: "groupCounts",
      atBottomThreshold: "atBottomThreshold",
      atTopThreshold: "atTopThreshold",
      computeItemKey: "computeItemKey",
      defaultItemHeight: "defaultItemHeight",
      fixedItemHeight: "fixedItemHeight",
      itemSize: "itemSize",
      scrollSeekConfiguration: "scrollSeekConfiguration",
      data: "data",
      initialItemCount: "initialItemCount",
      initialScrollTop: "initialScrollTop",
      alignToBottom: "alignToBottom",
      useWindowScroll: "useWindowScroll",
      customScrollParent: "customScrollParent",
      scrollerRef: "scrollerRef",
      logLevel: "logLevel"
    },
    methods: {
      scrollToIndex: "scrollToIndex",
      scrollIntoView: "scrollIntoView",
      scrollTo: "scrollTo",
      scrollBy: "scrollBy",
      getState: "getState"
    },
    events: {
      isScrolling: "isScrolling",
      endReached: "endReached",
      startReached: "startReached",
      rangeChanged: "rangeChanged",
      atBottomStateChange: "atBottomStateChange",
      atTopStateChange: "atTopStateChange",
      totalListHeightChanged: "totalListHeightChanged",
      itemsRendered: "itemsRendered",
      groupIndices: "groupIndices"
    }
  },
  TableRoot
);
const Scroller = /* @__PURE__ */ buildScroller({ usePublisher, useEmitterValue, useEmitter });
const WindowScroller = /* @__PURE__ */ buildWindowScroller({ usePublisher, useEmitterValue, useEmitter });
const TableVirtuoso = Table$1;

// GroupedVirtuoso relies on z-indexes. Giving the container `z-index: 0` (and
// `position: relative`, which it already comes with) makes it the root of a new
// stacking context, so its z-indexes can't conflict with anything else.
const VirtualizedList = _default$1(GroupedVirtuoso) `
  z-0
`;
const Header = _default$1.div `
  px-4
  py-1
  overflow-hidden
  text-sm
  font-medium
  text-gray-500
  bg-gray-50
  text-ellipsis
  whitespace-nowrap
`;
const BorderWrapper = _default$1.div `
  border-b
  border-b-gray-200
`;

const StickyHeaderedList = ({ children: _, sections, testId, ...otherProps }) => {
    const allItems = sections.flatMap((s) => s.items);
    return (React__default.createElement(VirtualizedList, { groupCounts: sections.map((s) => s.items.length), groupContent: (index) => (React__default.createElement(BorderWrapper, null,
            React__default.createElement(Header, null, sections[index].title))), itemContent: (index) => React__default.createElement(BorderWrapper, null, allItems[index]), "data-testid": testId, ...otherProps }));
};
// adding this here to match styling with dev since web-app doesn't have tailwind
const GroupHeader = ({ children }) => (React__default.createElement(BorderWrapper, null,
    React__default.createElement(Header, null, children)));

let updateQueue = makeQueue();
const raf = fn => schedule(fn, updateQueue);
let writeQueue = makeQueue();

raf.write = fn => schedule(fn, writeQueue);

let onStartQueue = makeQueue();

raf.onStart = fn => schedule(fn, onStartQueue);

let onFrameQueue = makeQueue();

raf.onFrame = fn => schedule(fn, onFrameQueue);

let onFinishQueue = makeQueue();

raf.onFinish = fn => schedule(fn, onFinishQueue);

let timeouts = [];

raf.setTimeout = (handler, ms) => {
  let time = raf.now() + ms;

  let cancel = () => {
    let i = timeouts.findIndex(t => t.cancel == cancel);
    if (~i) timeouts.splice(i, 1);
    __raf.count -= ~i ? 1 : 0;
  };

  let timeout = {
    time,
    handler,
    cancel
  };
  timeouts.splice(findTimeout(time), 0, timeout);
  __raf.count += 1;
  start();
  return timeout;
};

let findTimeout = time => ~(~timeouts.findIndex(t => t.time > time) || ~timeouts.length);

raf.cancel = fn => {
  updateQueue.delete(fn);
  writeQueue.delete(fn);
};

raf.sync = fn => {
  sync = true;
  raf.batchedUpdates(fn);
  sync = false;
};

raf.throttle = fn => {
  let lastArgs;

  function queuedFn() {
    try {
      fn(...lastArgs);
    } finally {
      lastArgs = null;
    }
  }

  function throttled(...args) {
    lastArgs = args;
    raf.onStart(queuedFn);
  }

  throttled.handler = fn;

  throttled.cancel = () => {
    onStartQueue.delete(queuedFn);
    lastArgs = null;
  };

  return throttled;
};

let nativeRaf = typeof window != 'undefined' ? window.requestAnimationFrame : () => {};

raf.use = impl => nativeRaf = impl;

raf.now = typeof performance != 'undefined' ? () => performance.now() : Date.now;

raf.batchedUpdates = fn => fn();

raf.catch = console.error;
raf.frameLoop = 'always';

raf.advance = () => {
  if (raf.frameLoop !== 'demand') {
    console.warn('Cannot call the manual advancement of rafz whilst frameLoop is not set as demand');
  } else {
    update();
  }
};

let ts = -1;
let sync = false;

function schedule(fn, queue) {
  if (sync) {
    queue.delete(fn);
    fn(0);
  } else {
    queue.add(fn);
    start();
  }
}

function start() {
  if (ts < 0) {
    ts = 0;

    if (raf.frameLoop !== 'demand') {
      nativeRaf(loop);
    }
  }
}

function loop() {
  if (~ts) {
    nativeRaf(loop);
    raf.batchedUpdates(update);
  }
}

function update() {
  let prevTs = ts;
  ts = raf.now();
  let count = findTimeout(ts);

  if (count) {
    eachSafely(timeouts.splice(0, count), t => t.handler());
    __raf.count -= count;
  }

  onStartQueue.flush();
  updateQueue.flush(prevTs ? Math.min(64, ts - prevTs) : 16.667);
  onFrameQueue.flush();
  writeQueue.flush();
  onFinishQueue.flush();
}

function makeQueue() {
  let next = new Set();
  let current = next;
  return {
    add(fn) {
      __raf.count += current == next && !next.has(fn) ? 1 : 0;
      next.add(fn);
    },

    delete(fn) {
      __raf.count -= current == next && next.has(fn) ? 1 : 0;
      return next.delete(fn);
    },

    flush(arg) {
      if (current.size) {
        next = new Set();
        __raf.count -= current.size;
        eachSafely(current, fn => fn(arg) && next.add(fn));
        __raf.count += next.size;
        current = next;
      }
    }

  };
}

function eachSafely(values, each) {
  values.forEach(value => {
    try {
      each(value);
    } catch (e) {
      raf.catch(e);
    }
  });
}

const __raf = {
  count: 0,

  clear() {
    ts = -1;
    timeouts = [];
    onStartQueue = makeQueue();
    updateQueue = makeQueue();
    onFrameQueue = makeQueue();
    writeQueue = makeQueue();
    onFinishQueue = makeQueue();
    __raf.count = 0;
  }

};

function noop$4() {}
const defineHidden = (obj, key, value) => Object.defineProperty(obj, key, {
  value,
  writable: true,
  configurable: true
});
const is = {
  arr: Array.isArray,
  obj: a => !!a && a.constructor.name === 'Object',
  fun: a => typeof a === 'function',
  str: a => typeof a === 'string',
  num: a => typeof a === 'number',
  und: a => a === undefined
};
function isEqual(a, b) {
  if (is.arr(a)) {
    if (!is.arr(b) || a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }

    return true;
  }

  return a === b;
}
const each = (obj, fn) => obj.forEach(fn);
function eachProp(obj, fn, ctx) {
  if (is.arr(obj)) {
    for (let i = 0; i < obj.length; i++) {
      fn.call(ctx, obj[i], `${i}`);
    }

    return;
  }

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      fn.call(ctx, obj[key], key);
    }
  }
}
const toArray = a => is.und(a) ? [] : is.arr(a) ? a : [a];
function flush(queue, iterator) {
  if (queue.size) {
    const items = Array.from(queue);
    queue.clear();
    each(items, iterator);
  }
}
const flushCalls = (queue, ...args) => flush(queue, fn => fn(...args));

let createStringInterpolator$1;
let to$1;
let colors$1 = null;
let skipAnimation = false;
let willAdvance = noop$4;
const assign = globals => {
  if (globals.to) to$1 = globals.to;
  if (globals.now) raf.now = globals.now;
  if (globals.colors !== undefined) colors$1 = globals.colors;
  if (globals.skipAnimation != null) skipAnimation = globals.skipAnimation;
  if (globals.createStringInterpolator) createStringInterpolator$1 = globals.createStringInterpolator;
  if (globals.requestAnimationFrame) raf.use(globals.requestAnimationFrame);
  if (globals.batchedUpdates) raf.batchedUpdates = globals.batchedUpdates;
  if (globals.willAdvance) willAdvance = globals.willAdvance;
  if (globals.frameLoop) raf.frameLoop = globals.frameLoop;
};

var globals = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get createStringInterpolator () { return createStringInterpolator$1; },
  get to () { return to$1; },
  get colors () { return colors$1; },
  get skipAnimation () { return skipAnimation; },
  get willAdvance () { return willAdvance; },
  assign: assign
});

const startQueue = new Set();
let currentFrame = [];
let prevFrame = [];
let priority = 0;
const frameLoop = {
  get idle() {
    return !startQueue.size && !currentFrame.length;
  },

  start(animation) {
    if (priority > animation.priority) {
      startQueue.add(animation);
      raf.onStart(flushStartQueue);
    } else {
      startSafely(animation);
      raf(advance);
    }
  },

  advance,

  sort(animation) {
    if (priority) {
      raf.onFrame(() => frameLoop.sort(animation));
    } else {
      const prevIndex = currentFrame.indexOf(animation);

      if (~prevIndex) {
        currentFrame.splice(prevIndex, 1);
        startUnsafely(animation);
      }
    }
  },

  clear() {
    currentFrame = [];
    startQueue.clear();
  }

};

function flushStartQueue() {
  startQueue.forEach(startSafely);
  startQueue.clear();
  raf(advance);
}

function startSafely(animation) {
  if (!currentFrame.includes(animation)) startUnsafely(animation);
}

function startUnsafely(animation) {
  currentFrame.splice(findIndex(currentFrame, other => other.priority > animation.priority), 0, animation);
}

function advance(dt) {
  const nextFrame = prevFrame;

  for (let i = 0; i < currentFrame.length; i++) {
    const animation = currentFrame[i];
    priority = animation.priority;

    if (!animation.idle) {
      willAdvance(animation);
      animation.advance(dt);

      if (!animation.idle) {
        nextFrame.push(animation);
      }
    }
  }

  priority = 0;
  prevFrame = currentFrame;
  prevFrame.length = 0;
  currentFrame = nextFrame;
  return currentFrame.length > 0;
}

function findIndex(arr, test) {
  const index = arr.findIndex(test);
  return index < 0 ? arr.length : index;
}

const colors$2 = {
  transparent: 0x00000000,
  aliceblue: 0xf0f8ffff,
  antiquewhite: 0xfaebd7ff,
  aqua: 0x00ffffff,
  aquamarine: 0x7fffd4ff,
  azure: 0xf0ffffff,
  beige: 0xf5f5dcff,
  bisque: 0xffe4c4ff,
  black: 0x000000ff,
  blanchedalmond: 0xffebcdff,
  blue: 0x0000ffff,
  blueviolet: 0x8a2be2ff,
  brown: 0xa52a2aff,
  burlywood: 0xdeb887ff,
  burntsienna: 0xea7e5dff,
  cadetblue: 0x5f9ea0ff,
  chartreuse: 0x7fff00ff,
  chocolate: 0xd2691eff,
  coral: 0xff7f50ff,
  cornflowerblue: 0x6495edff,
  cornsilk: 0xfff8dcff,
  crimson: 0xdc143cff,
  cyan: 0x00ffffff,
  darkblue: 0x00008bff,
  darkcyan: 0x008b8bff,
  darkgoldenrod: 0xb8860bff,
  darkgray: 0xa9a9a9ff,
  darkgreen: 0x006400ff,
  darkgrey: 0xa9a9a9ff,
  darkkhaki: 0xbdb76bff,
  darkmagenta: 0x8b008bff,
  darkolivegreen: 0x556b2fff,
  darkorange: 0xff8c00ff,
  darkorchid: 0x9932ccff,
  darkred: 0x8b0000ff,
  darksalmon: 0xe9967aff,
  darkseagreen: 0x8fbc8fff,
  darkslateblue: 0x483d8bff,
  darkslategray: 0x2f4f4fff,
  darkslategrey: 0x2f4f4fff,
  darkturquoise: 0x00ced1ff,
  darkviolet: 0x9400d3ff,
  deeppink: 0xff1493ff,
  deepskyblue: 0x00bfffff,
  dimgray: 0x696969ff,
  dimgrey: 0x696969ff,
  dodgerblue: 0x1e90ffff,
  firebrick: 0xb22222ff,
  floralwhite: 0xfffaf0ff,
  forestgreen: 0x228b22ff,
  fuchsia: 0xff00ffff,
  gainsboro: 0xdcdcdcff,
  ghostwhite: 0xf8f8ffff,
  gold: 0xffd700ff,
  goldenrod: 0xdaa520ff,
  gray: 0x808080ff,
  green: 0x008000ff,
  greenyellow: 0xadff2fff,
  grey: 0x808080ff,
  honeydew: 0xf0fff0ff,
  hotpink: 0xff69b4ff,
  indianred: 0xcd5c5cff,
  indigo: 0x4b0082ff,
  ivory: 0xfffff0ff,
  khaki: 0xf0e68cff,
  lavender: 0xe6e6faff,
  lavenderblush: 0xfff0f5ff,
  lawngreen: 0x7cfc00ff,
  lemonchiffon: 0xfffacdff,
  lightblue: 0xadd8e6ff,
  lightcoral: 0xf08080ff,
  lightcyan: 0xe0ffffff,
  lightgoldenrodyellow: 0xfafad2ff,
  lightgray: 0xd3d3d3ff,
  lightgreen: 0x90ee90ff,
  lightgrey: 0xd3d3d3ff,
  lightpink: 0xffb6c1ff,
  lightsalmon: 0xffa07aff,
  lightseagreen: 0x20b2aaff,
  lightskyblue: 0x87cefaff,
  lightslategray: 0x778899ff,
  lightslategrey: 0x778899ff,
  lightsteelblue: 0xb0c4deff,
  lightyellow: 0xffffe0ff,
  lime: 0x00ff00ff,
  limegreen: 0x32cd32ff,
  linen: 0xfaf0e6ff,
  magenta: 0xff00ffff,
  maroon: 0x800000ff,
  mediumaquamarine: 0x66cdaaff,
  mediumblue: 0x0000cdff,
  mediumorchid: 0xba55d3ff,
  mediumpurple: 0x9370dbff,
  mediumseagreen: 0x3cb371ff,
  mediumslateblue: 0x7b68eeff,
  mediumspringgreen: 0x00fa9aff,
  mediumturquoise: 0x48d1ccff,
  mediumvioletred: 0xc71585ff,
  midnightblue: 0x191970ff,
  mintcream: 0xf5fffaff,
  mistyrose: 0xffe4e1ff,
  moccasin: 0xffe4b5ff,
  navajowhite: 0xffdeadff,
  navy: 0x000080ff,
  oldlace: 0xfdf5e6ff,
  olive: 0x808000ff,
  olivedrab: 0x6b8e23ff,
  orange: 0xffa500ff,
  orangered: 0xff4500ff,
  orchid: 0xda70d6ff,
  palegoldenrod: 0xeee8aaff,
  palegreen: 0x98fb98ff,
  paleturquoise: 0xafeeeeff,
  palevioletred: 0xdb7093ff,
  papayawhip: 0xffefd5ff,
  peachpuff: 0xffdab9ff,
  peru: 0xcd853fff,
  pink: 0xffc0cbff,
  plum: 0xdda0ddff,
  powderblue: 0xb0e0e6ff,
  purple: 0x800080ff,
  rebeccapurple: 0x663399ff,
  red: 0xff0000ff,
  rosybrown: 0xbc8f8fff,
  royalblue: 0x4169e1ff,
  saddlebrown: 0x8b4513ff,
  salmon: 0xfa8072ff,
  sandybrown: 0xf4a460ff,
  seagreen: 0x2e8b57ff,
  seashell: 0xfff5eeff,
  sienna: 0xa0522dff,
  silver: 0xc0c0c0ff,
  skyblue: 0x87ceebff,
  slateblue: 0x6a5acdff,
  slategray: 0x708090ff,
  slategrey: 0x708090ff,
  snow: 0xfffafaff,
  springgreen: 0x00ff7fff,
  steelblue: 0x4682b4ff,
  tan: 0xd2b48cff,
  teal: 0x008080ff,
  thistle: 0xd8bfd8ff,
  tomato: 0xff6347ff,
  turquoise: 0x40e0d0ff,
  violet: 0xee82eeff,
  wheat: 0xf5deb3ff,
  white: 0xffffffff,
  whitesmoke: 0xf5f5f5ff,
  yellow: 0xffff00ff,
  yellowgreen: 0x9acd32ff
};

const NUMBER = '[-+]?\\d*\\.?\\d+';
const PERCENTAGE = NUMBER + '%';

function call(...parts) {
  return '\\(\\s*(' + parts.join(')\\s*,\\s*(') + ')\\s*\\)';
}

const rgb$2 = new RegExp('rgb' + call(NUMBER, NUMBER, NUMBER));
const rgba$1 = new RegExp('rgba' + call(NUMBER, NUMBER, NUMBER, NUMBER));
const hsl$1 = new RegExp('hsl' + call(NUMBER, PERCENTAGE, PERCENTAGE));
const hsla$1 = new RegExp('hsla' + call(NUMBER, PERCENTAGE, PERCENTAGE, NUMBER));
const hex3 = /^#([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/;
const hex4 = /^#([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/;
const hex6 = /^#([0-9a-fA-F]{6})$/;
const hex8 = /^#([0-9a-fA-F]{8})$/;

function normalizeColor(color) {
  let match;

  if (typeof color === 'number') {
    return color >>> 0 === color && color >= 0 && color <= 0xffffffff ? color : null;
  }

  if (match = hex6.exec(color)) return parseInt(match[1] + 'ff', 16) >>> 0;

  if (colors$1 && colors$1[color] !== undefined) {
    return colors$1[color];
  }

  if (match = rgb$2.exec(color)) {
    return (parse255(match[1]) << 24 | parse255(match[2]) << 16 | parse255(match[3]) << 8 | 0x000000ff) >>> 0;
  }

  if (match = rgba$1.exec(color)) {
    return (parse255(match[1]) << 24 | parse255(match[2]) << 16 | parse255(match[3]) << 8 | parse1(match[4])) >>> 0;
  }

  if (match = hex3.exec(color)) {
    return parseInt(match[1] + match[1] + match[2] + match[2] + match[3] + match[3] + 'ff', 16) >>> 0;
  }

  if (match = hex8.exec(color)) return parseInt(match[1], 16) >>> 0;

  if (match = hex4.exec(color)) {
    return parseInt(match[1] + match[1] + match[2] + match[2] + match[3] + match[3] + match[4] + match[4], 16) >>> 0;
  }

  if (match = hsl$1.exec(color)) {
    return (hslToRgb(parse360(match[1]), parsePercentage(match[2]), parsePercentage(match[3])) | 0x000000ff) >>> 0;
  }

  if (match = hsla$1.exec(color)) {
    return (hslToRgb(parse360(match[1]), parsePercentage(match[2]), parsePercentage(match[3])) | parse1(match[4])) >>> 0;
  }

  return null;
}

function hue2rgb(p, q, t) {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function hslToRgb(h, s, l) {
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h + 1 / 3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1 / 3);
  return Math.round(r * 255) << 24 | Math.round(g * 255) << 16 | Math.round(b * 255) << 8;
}

function parse255(str) {
  const int = parseInt(str, 10);
  if (int < 0) return 0;
  if (int > 255) return 255;
  return int;
}

function parse360(str) {
  const int = parseFloat(str);
  return (int % 360 + 360) % 360 / 360;
}

function parse1(str) {
  const num = parseFloat(str);
  if (num < 0) return 0;
  if (num > 1) return 255;
  return Math.round(num * 255);
}

function parsePercentage(str) {
  const int = parseFloat(str);
  if (int < 0) return 0;
  if (int > 100) return 1;
  return int / 100;
}

function colorToRgba(input) {
  let int32Color = normalizeColor(input);
  if (int32Color === null) return input;
  int32Color = int32Color || 0;
  let r = (int32Color & 0xff000000) >>> 24;
  let g = (int32Color & 0x00ff0000) >>> 16;
  let b = (int32Color & 0x0000ff00) >>> 8;
  let a = (int32Color & 0x000000ff) / 255;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

const createInterpolator = (range, output, extrapolate) => {
  if (is.fun(range)) {
    return range;
  }

  if (is.arr(range)) {
    return createInterpolator({
      range,
      output: output,
      extrapolate
    });
  }

  if (is.str(range.output[0])) {
    return createStringInterpolator$1(range);
  }

  const config = range;
  const outputRange = config.output;
  const inputRange = config.range || [0, 1];
  const extrapolateLeft = config.extrapolateLeft || config.extrapolate || 'extend';
  const extrapolateRight = config.extrapolateRight || config.extrapolate || 'extend';

  const easing = config.easing || (t => t);

  return input => {
    const range = findRange(input, inputRange);
    return interpolate$1(input, inputRange[range], inputRange[range + 1], outputRange[range], outputRange[range + 1], easing, extrapolateLeft, extrapolateRight, config.map);
  };
};

function interpolate$1(input, inputMin, inputMax, outputMin, outputMax, easing, extrapolateLeft, extrapolateRight, map) {
  let result = map ? map(input) : input;

  if (result < inputMin) {
    if (extrapolateLeft === 'identity') return result;else if (extrapolateLeft === 'clamp') result = inputMin;
  }

  if (result > inputMax) {
    if (extrapolateRight === 'identity') return result;else if (extrapolateRight === 'clamp') result = inputMax;
  }

  if (outputMin === outputMax) return outputMin;
  if (inputMin === inputMax) return input <= inputMin ? outputMin : outputMax;
  if (inputMin === -Infinity) result = -result;else if (inputMax === Infinity) result = result - inputMin;else result = (result - inputMin) / (inputMax - inputMin);
  result = easing(result);
  if (outputMin === -Infinity) result = -result;else if (outputMax === Infinity) result = result + outputMin;else result = result * (outputMax - outputMin) + outputMin;
  return result;
}

function findRange(input, inputRange) {
  for (var i = 1; i < inputRange.length - 1; ++i) if (inputRange[i] >= input) break;

  return i - 1;
}

function _extends$c() {
  _extends$c = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$c.apply(this, arguments);
}

const $get = Symbol.for('FluidValue.get');
const $observers = Symbol.for('FluidValue.observers');

const hasFluidValue = arg => Boolean(arg && arg[$get]);

const getFluidValue = arg => arg && arg[$get] ? arg[$get]() : arg;

const getFluidObservers = target => target[$observers] || null;

function callFluidObserver(observer, event) {
  if (observer.eventObserved) {
    observer.eventObserved(event);
  } else {
    observer(event);
  }
}

function callFluidObservers(target, event) {
  let observers = target[$observers];

  if (observers) {
    observers.forEach(observer => {
      callFluidObserver(observer, event);
    });
  }
}

class FluidValue {
  constructor(get) {
    this[$get] = void 0;
    this[$observers] = void 0;

    if (!get && !(get = this.get)) {
      throw Error('Unknown getter');
    }

    setFluidGetter(this, get);
  }

}

const setFluidGetter = (target, get) => setHidden(target, $get, get);

function addFluidObserver(target, observer) {
  if (target[$get]) {
    let observers = target[$observers];

    if (!observers) {
      setHidden(target, $observers, observers = new Set());
    }

    if (!observers.has(observer)) {
      observers.add(observer);

      if (target.observerAdded) {
        target.observerAdded(observers.size, observer);
      }
    }
  }

  return observer;
}

function removeFluidObserver(target, observer) {
  let observers = target[$observers];

  if (observers && observers.has(observer)) {
    const count = observers.size - 1;

    if (count) {
      observers.delete(observer);
    } else {
      target[$observers] = null;
    }

    if (target.observerRemoved) {
      target.observerRemoved(count, observer);
    }
  }
}

const setHidden = (target, key, value) => Object.defineProperty(target, key, {
  value,
  writable: true,
  configurable: true
});

const numberRegex = /[+\-]?(?:0|[1-9]\d*)(?:\.\d*)?(?:[eE][+\-]?\d+)?/g;
const colorRegex = /(#(?:[0-9a-f]{2}){2,4}|(#[0-9a-f]{3})|(rgb|hsl)a?\((-?\d+%?[,\s]+){2,3}\s*[\d\.]+%?\))/gi;
const unitRegex = new RegExp(`(${numberRegex.source})(%|[a-z]+)`, 'i');
let namedColorRegex;
const rgbaRegex = /rgba\(([0-9\.-]+), ([0-9\.-]+), ([0-9\.-]+), ([0-9\.-]+)\)/gi;

const rgbaRound = (_, p1, p2, p3, p4) => `rgba(${Math.round(p1)}, ${Math.round(p2)}, ${Math.round(p3)}, ${p4})`;

const createStringInterpolator = config => {
  if (!namedColorRegex) namedColorRegex = colors$1 ? new RegExp(`(${Object.keys(colors$1).join('|')})(?!\\w)`, 'g') : /^\b$/;
  const output = config.output.map(value => getFluidValue(value).replace(colorRegex, colorToRgba).replace(namedColorRegex, colorToRgba));
  const keyframes = output.map(value => value.match(numberRegex).map(Number));
  const outputRanges = keyframes[0].map((_, i) => keyframes.map(values => {
    if (!(i in values)) {
      throw Error('The arity of each "output" value must be equal');
    }

    return values[i];
  }));
  const interpolators = outputRanges.map(output => createInterpolator(_extends$c({}, config, {
    output
  })));
  return input => {
    var _output$find;

    const missingUnit = !unitRegex.test(output[0]) && ((_output$find = output.find(value => unitRegex.test(value))) == null ? void 0 : _output$find.replace(numberRegex, ''));
    let i = 0;
    return output[0].replace(numberRegex, () => `${interpolators[i++](input)}${missingUnit || ''}`).replace(rgbaRegex, rgbaRound);
  };
};

const prefix = 'react-spring: ';

const once = fn => {
  const func = fn;
  let called = false;

  if (typeof func != 'function') {
    throw new TypeError(`${prefix}once requires a function parameter`);
  }

  return (...args) => {
    if (!called) {
      func(...args);
      called = true;
    }
  };
};

const warnInterpolate = once(console.warn);
function deprecateInterpolate() {
  warnInterpolate(`${prefix}The "interpolate" function is deprecated in v9 (use "to" instead)`);
}
const warnDirectCall = once(console.warn);
function deprecateDirectCall() {
  warnDirectCall(`${prefix}Directly calling start instead of using the api object is deprecated in v9 (use ".start" instead), this will be removed in later 0.X.0 versions`);
}

function isAnimatedString(value) {
  return is.str(value) && (value[0] == '#' || /\d/.test(value) || value in (colors$1 || {}));
}

const useOnce = effect => useEffect(effect, emptyDeps);
const emptyDeps = [];

function useForceUpdate() {
  const update = useState()[1];
  const mounted = useState(makeMountedRef)[0];
  useOnce(mounted.unmount);
  return () => {
    if (mounted.current) {
      update({});
    }
  };
}

function makeMountedRef() {
  const mounted = {
    current: true,
    unmount: () => () => {
      mounted.current = false;
    }
  };
  return mounted;
}

function useMemoOne(getResult, inputs) {
  const [initial] = useState(() => ({
    inputs,
    result: getResult()
  }));
  const committed = useRef();
  const prevCache = committed.current;
  let cache = prevCache;

  if (cache) {
    const useCache = Boolean(inputs && cache.inputs && areInputsEqual(inputs, cache.inputs));

    if (!useCache) {
      cache = {
        inputs,
        result: getResult()
      };
    }
  } else {
    cache = initial;
  }

  useEffect(() => {
    committed.current = cache;

    if (prevCache == initial) {
      initial.inputs = initial.result = undefined;
    }
  }, [cache]);
  return cache.result;
}

function areInputsEqual(next, prev) {
  if (next.length !== prev.length) {
    return false;
  }

  for (let i = 0; i < next.length; i++) {
    if (next[i] !== prev[i]) {
      return false;
    }
  }

  return true;
}

function usePrev(value) {
  const prevRef = useRef();
  useEffect(() => {
    prevRef.current = value;
  });
  return prevRef.current;
}

const useLayoutEffect = typeof window !== 'undefined' && window.document && window.document.createElement ? React.useLayoutEffect : React.useEffect;

const $node = Symbol.for('Animated:node');
const isAnimated = value => !!value && value[$node] === value;
const getAnimated = owner => owner && owner[$node];
const setAnimated = (owner, node) => defineHidden(owner, $node, node);
const getPayload = owner => owner && owner[$node] && owner[$node].getPayload();
class Animated {
  constructor() {
    this.payload = void 0;
    setAnimated(this, this);
  }

  getPayload() {
    return this.payload || [];
  }

}

class AnimatedValue extends Animated {
  constructor(_value) {
    super();
    this.done = true;
    this.elapsedTime = void 0;
    this.lastPosition = void 0;
    this.lastVelocity = void 0;
    this.v0 = void 0;
    this.durationProgress = 0;
    this._value = _value;

    if (is.num(this._value)) {
      this.lastPosition = this._value;
    }
  }

  static create(value) {
    return new AnimatedValue(value);
  }

  getPayload() {
    return [this];
  }

  getValue() {
    return this._value;
  }

  setValue(value, step) {
    if (is.num(value)) {
      this.lastPosition = value;

      if (step) {
        value = Math.round(value / step) * step;

        if (this.done) {
          this.lastPosition = value;
        }
      }
    }

    if (this._value === value) {
      return false;
    }

    this._value = value;
    return true;
  }

  reset() {
    const {
      done
    } = this;
    this.done = false;

    if (is.num(this._value)) {
      this.elapsedTime = 0;
      this.durationProgress = 0;
      this.lastPosition = this._value;
      if (done) this.lastVelocity = null;
      this.v0 = null;
    }
  }

}

class AnimatedString extends AnimatedValue {
  constructor(value) {
    super(0);
    this._string = null;
    this._toString = void 0;
    this._toString = createInterpolator({
      output: [value, value]
    });
  }

  static create(value) {
    return new AnimatedString(value);
  }

  getValue() {
    let value = this._string;
    return value == null ? this._string = this._toString(this._value) : value;
  }

  setValue(value) {
    if (is.str(value)) {
      if (value == this._string) {
        return false;
      }

      this._string = value;
      this._value = 1;
    } else if (super.setValue(value)) {
      this._string = null;
    } else {
      return false;
    }

    return true;
  }

  reset(goal) {
    if (goal) {
      this._toString = createInterpolator({
        output: [this.getValue(), goal]
      });
    }

    this._value = 0;
    super.reset();
  }

}

const TreeContext = {
  dependencies: null
};

class AnimatedObject extends Animated {
  constructor(source) {
    super();
    this.source = source;
    this.setValue(source);
  }

  getValue(animated) {
    const values = {};
    eachProp(this.source, (source, key) => {
      if (isAnimated(source)) {
        values[key] = source.getValue(animated);
      } else if (hasFluidValue(source)) {
        values[key] = getFluidValue(source);
      } else if (!animated) {
        values[key] = source;
      }
    });
    return values;
  }

  setValue(source) {
    this.source = source;
    this.payload = this._makePayload(source);
  }

  reset() {
    if (this.payload) {
      each(this.payload, node => node.reset());
    }
  }

  _makePayload(source) {
    if (source) {
      const payload = new Set();
      eachProp(source, this._addToPayload, payload);
      return Array.from(payload);
    }
  }

  _addToPayload(source) {
    if (TreeContext.dependencies && hasFluidValue(source)) {
      TreeContext.dependencies.add(source);
    }

    const payload = getPayload(source);

    if (payload) {
      each(payload, node => this.add(node));
    }
  }

}

class AnimatedArray extends AnimatedObject {
  constructor(source) {
    super(source);
  }

  static create(source) {
    return new AnimatedArray(source);
  }

  getValue() {
    return this.source.map(node => node.getValue());
  }

  setValue(source) {
    const payload = this.getPayload();

    if (source.length == payload.length) {
      return payload.map((node, i) => node.setValue(source[i])).some(Boolean);
    }

    super.setValue(source.map(makeAnimated));
    return true;
  }

}

function makeAnimated(value) {
  const nodeType = isAnimatedString(value) ? AnimatedString : AnimatedValue;
  return nodeType.create(value);
}

function getAnimatedType(value) {
  const parentNode = getAnimated(value);
  return parentNode ? parentNode.constructor : is.arr(value) ? AnimatedArray : isAnimatedString(value) ? AnimatedString : AnimatedValue;
}

function _extends$b() {
  _extends$b = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$b.apply(this, arguments);
}

const withAnimated = (Component, host) => {
  const hasInstance = !is.fun(Component) || Component.prototype && Component.prototype.isReactComponent;
  return forwardRef((givenProps, givenRef) => {
    const instanceRef = useRef(null);
    const ref = hasInstance && useCallback(value => {
      instanceRef.current = updateRef(givenRef, value);
    }, [givenRef]);
    const [props, deps] = getAnimatedState(givenProps, host);
    const forceUpdate = useForceUpdate();

    const callback = () => {
      const instance = instanceRef.current;

      if (hasInstance && !instance) {
        return;
      }

      const didUpdate = instance ? host.applyAnimatedValues(instance, props.getValue(true)) : false;

      if (didUpdate === false) {
        forceUpdate();
      }
    };

    const observer = new PropsObserver(callback, deps);
    const observerRef = useRef();
    useLayoutEffect(() => {
      const lastObserver = observerRef.current;
      observerRef.current = observer;
      each(deps, dep => addFluidObserver(dep, observer));

      if (lastObserver) {
        each(lastObserver.deps, dep => removeFluidObserver(dep, lastObserver));
        raf.cancel(lastObserver.update);
      }
    });
    useEffect(callback, []);
    useOnce(() => () => {
      const observer = observerRef.current;
      each(observer.deps, dep => removeFluidObserver(dep, observer));
    });
    const usedProps = host.getComponentProps(props.getValue());
    return React.createElement(Component, _extends$b({}, usedProps, {
      ref: ref
    }));
  });
};

class PropsObserver {
  constructor(update, deps) {
    this.update = update;
    this.deps = deps;
  }

  eventObserved(event) {
    if (event.type == 'change') {
      raf.write(this.update);
    }
  }

}

function getAnimatedState(props, host) {
  const dependencies = new Set();
  TreeContext.dependencies = dependencies;
  if (props.style) props = _extends$b({}, props, {
    style: host.createAnimatedStyle(props.style)
  });
  props = new AnimatedObject(props);
  TreeContext.dependencies = null;
  return [props, dependencies];
}

function updateRef(ref, value) {
  if (ref) {
    if (is.fun(ref)) ref(value);else ref.current = value;
  }

  return value;
}

const cacheKey = Symbol.for('AnimatedComponent');
const createHost = (components, {
  applyAnimatedValues: _applyAnimatedValues = () => false,
  createAnimatedStyle: _createAnimatedStyle = style => new AnimatedObject(style),
  getComponentProps: _getComponentProps = props => props
} = {}) => {
  const hostConfig = {
    applyAnimatedValues: _applyAnimatedValues,
    createAnimatedStyle: _createAnimatedStyle,
    getComponentProps: _getComponentProps
  };

  const animated = Component => {
    const displayName = getDisplayName(Component) || 'Anonymous';

    if (is.str(Component)) {
      Component = animated[Component] || (animated[Component] = withAnimated(Component, hostConfig));
    } else {
      Component = Component[cacheKey] || (Component[cacheKey] = withAnimated(Component, hostConfig));
    }

    Component.displayName = `Animated(${displayName})`;
    return Component;
  };

  eachProp(components, (Component, key) => {
    if (is.arr(components)) {
      key = getDisplayName(Component);
    }

    animated[key] = animated(Component);
  });
  return {
    animated
  };
};

const getDisplayName = arg => is.str(arg) ? arg : arg && is.str(arg.displayName) ? arg.displayName : is.fun(arg) && arg.name || null;

function _extends$a() {
  _extends$a = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$a.apply(this, arguments);
}

function callProp(value, ...args) {
  return is.fun(value) ? value(...args) : value;
}
const matchProp = (value, key) => value === true || !!(key && value && (is.fun(value) ? value(key) : toArray(value).includes(key)));
const resolveProp = (prop, key) => is.obj(prop) ? key && prop[key] : prop;
const getDefaultProp = (props, key) => props.default === true ? props[key] : props.default ? props.default[key] : undefined;

const noopTransform = value => value;

const getDefaultProps = (props, transform = noopTransform) => {
  let keys = DEFAULT_PROPS;

  if (props.default && props.default !== true) {
    props = props.default;
    keys = Object.keys(props);
  }

  const defaults = {};

  for (const key of keys) {
    const value = transform(props[key], key);

    if (!is.und(value)) {
      defaults[key] = value;
    }
  }

  return defaults;
};
const DEFAULT_PROPS = ['config', 'onProps', 'onStart', 'onChange', 'onPause', 'onResume', 'onRest'];
const RESERVED_PROPS = {
  config: 1,
  from: 1,
  to: 1,
  ref: 1,
  loop: 1,
  reset: 1,
  pause: 1,
  cancel: 1,
  reverse: 1,
  immediate: 1,
  default: 1,
  delay: 1,
  onProps: 1,
  onStart: 1,
  onChange: 1,
  onPause: 1,
  onResume: 1,
  onRest: 1,
  onResolve: 1,
  items: 1,
  trail: 1,
  sort: 1,
  expires: 1,
  initial: 1,
  enter: 1,
  update: 1,
  leave: 1,
  children: 1,
  onDestroyed: 1,
  keys: 1,
  callId: 1,
  parentId: 1
};

function getForwardProps(props) {
  const forward = {};
  let count = 0;
  eachProp(props, (value, prop) => {
    if (!RESERVED_PROPS[prop]) {
      forward[prop] = value;
      count++;
    }
  });

  if (count) {
    return forward;
  }
}

function inferTo(props) {
  const to = getForwardProps(props);

  if (to) {
    const out = {
      to
    };
    eachProp(props, (val, key) => key in to || (out[key] = val));
    return out;
  }

  return _extends$a({}, props);
}
function computeGoal(value) {
  value = getFluidValue(value);
  return is.arr(value) ? value.map(computeGoal) : isAnimatedString(value) ? globals.createStringInterpolator({
    range: [0, 1],
    output: [value, value]
  })(1) : value;
}
function hasProps(props) {
  for (const _ in props) return true;

  return false;
}
function isAsyncTo(to) {
  return is.fun(to) || is.arr(to) && is.obj(to[0]);
}
function detachRefs(ctrl, ref) {
  var _ctrl$ref;

  (_ctrl$ref = ctrl.ref) == null ? void 0 : _ctrl$ref.delete(ctrl);
  ref == null ? void 0 : ref.delete(ctrl);
}
function replaceRef(ctrl, ref) {
  if (ref && ctrl.ref !== ref) {
    var _ctrl$ref2;

    (_ctrl$ref2 = ctrl.ref) == null ? void 0 : _ctrl$ref2.delete(ctrl);
    ref.add(ctrl);
    ctrl.ref = ref;
  }
}

const config = {
  default: {
    tension: 170,
    friction: 26
  },
  gentle: {
    tension: 120,
    friction: 14
  },
  wobbly: {
    tension: 180,
    friction: 12
  },
  stiff: {
    tension: 210,
    friction: 20
  },
  slow: {
    tension: 280,
    friction: 60
  },
  molasses: {
    tension: 280,
    friction: 120
  }
};

const linear$2 = t => t;

const defaults = _extends$a({}, config.default, {
  mass: 1,
  damping: 1,
  easing: linear$2,
  clamp: false
});

class AnimationConfig {
  constructor() {
    this.tension = void 0;
    this.friction = void 0;
    this.frequency = void 0;
    this.damping = void 0;
    this.mass = void 0;
    this.velocity = 0;
    this.restVelocity = void 0;
    this.precision = void 0;
    this.progress = void 0;
    this.duration = void 0;
    this.easing = void 0;
    this.clamp = void 0;
    this.bounce = void 0;
    this.decay = void 0;
    this.round = void 0;
    Object.assign(this, defaults);
  }

}
function mergeConfig(config, newConfig, defaultConfig) {
  if (defaultConfig) {
    defaultConfig = _extends$a({}, defaultConfig);
    sanitizeConfig(defaultConfig, newConfig);
    newConfig = _extends$a({}, defaultConfig, newConfig);
  }

  sanitizeConfig(config, newConfig);
  Object.assign(config, newConfig);

  for (const key in defaults) {
    if (config[key] == null) {
      config[key] = defaults[key];
    }
  }

  let {
    mass,
    frequency,
    damping
  } = config;

  if (!is.und(frequency)) {
    if (frequency < 0.01) frequency = 0.01;
    if (damping < 0) damping = 0;
    config.tension = Math.pow(2 * Math.PI / frequency, 2) * mass;
    config.friction = 4 * Math.PI * damping * mass / frequency;
  }

  return config;
}

function sanitizeConfig(config, props) {
  if (!is.und(props.decay)) {
    config.duration = undefined;
  } else {
    const isTensionConfig = !is.und(props.tension) || !is.und(props.friction);

    if (isTensionConfig || !is.und(props.frequency) || !is.und(props.damping) || !is.und(props.mass)) {
      config.duration = undefined;
      config.decay = undefined;
    }

    if (isTensionConfig) {
      config.frequency = undefined;
    }
  }
}

const emptyArray = [];
class Animation {
  constructor() {
    this.changed = false;
    this.values = emptyArray;
    this.toValues = null;
    this.fromValues = emptyArray;
    this.to = void 0;
    this.from = void 0;
    this.config = new AnimationConfig();
    this.immediate = false;
  }

}

function scheduleProps(callId, {
  key,
  props,
  defaultProps,
  state,
  actions
}) {
  return new Promise((resolve, reject) => {
    var _props$cancel;

    let delay;
    let timeout;
    let cancel = matchProp((_props$cancel = props.cancel) != null ? _props$cancel : defaultProps == null ? void 0 : defaultProps.cancel, key);

    if (cancel) {
      onStart();
    } else {
      if (!is.und(props.pause)) {
        state.paused = matchProp(props.pause, key);
      }

      let pause = defaultProps == null ? void 0 : defaultProps.pause;

      if (pause !== true) {
        pause = state.paused || matchProp(pause, key);
      }

      delay = callProp(props.delay || 0, key);

      if (pause) {
        state.resumeQueue.add(onResume);
        actions.pause();
      } else {
        actions.resume();
        onResume();
      }
    }

    function onPause() {
      state.resumeQueue.add(onResume);
      state.timeouts.delete(timeout);
      timeout.cancel();
      delay = timeout.time - raf.now();
    }

    function onResume() {
      if (delay > 0 && !globals.skipAnimation) {
        timeout = raf.setTimeout(onStart, delay);
        state.pauseQueue.add(onPause);
        state.timeouts.add(timeout);
      } else {
        onStart();
      }
    }

    function onStart() {
      state.pauseQueue.delete(onPause);
      state.timeouts.delete(timeout);

      if (callId <= (state.cancelId || 0)) {
        cancel = true;
      }

      try {
        actions.start(_extends$a({}, props, {
          callId,
          cancel
        }), resolve);
      } catch (err) {
        reject(err);
      }
    }
  });
}

const getCombinedResult = (target, results) => results.length == 1 ? results[0] : results.some(result => result.cancelled) ? getCancelledResult(target.get()) : results.every(result => result.noop) ? getNoopResult(target.get()) : getFinishedResult(target.get(), results.every(result => result.finished));
const getNoopResult = value => ({
  value,
  noop: true,
  finished: true,
  cancelled: false
});
const getFinishedResult = (value, finished, cancelled = false) => ({
  value,
  finished,
  cancelled
});
const getCancelledResult = value => ({
  value,
  cancelled: true,
  finished: false
});

function runAsync(to, props, state, target) {
  const {
    callId,
    parentId,
    onRest
  } = props;
  const {
    asyncTo: prevTo,
    promise: prevPromise
  } = state;

  if (!parentId && to === prevTo && !props.reset) {
    return prevPromise;
  }

  return state.promise = (async () => {
    state.asyncId = callId;
    state.asyncTo = to;
    const defaultProps = getDefaultProps(props, (value, key) => key === 'onRest' ? undefined : value);
    let preventBail;
    let bail;
    const bailPromise = new Promise((resolve, reject) => (preventBail = resolve, bail = reject));

    const bailIfEnded = bailSignal => {
      const bailResult = callId <= (state.cancelId || 0) && getCancelledResult(target) || callId !== state.asyncId && getFinishedResult(target, false);

      if (bailResult) {
        bailSignal.result = bailResult;
        bail(bailSignal);
        throw bailSignal;
      }
    };

    const animate = (arg1, arg2) => {
      const bailSignal = new BailSignal();
      const skipAnimationSignal = new SkipAniamtionSignal();
      return (async () => {
        if (globals.skipAnimation) {
          stopAsync(state);
          skipAnimationSignal.result = getFinishedResult(target, false);
          bail(skipAnimationSignal);
          throw skipAnimationSignal;
        }

        bailIfEnded(bailSignal);
        const props = is.obj(arg1) ? _extends$a({}, arg1) : _extends$a({}, arg2, {
          to: arg1
        });
        props.parentId = callId;
        eachProp(defaultProps, (value, key) => {
          if (is.und(props[key])) {
            props[key] = value;
          }
        });
        const result = await target.start(props);
        bailIfEnded(bailSignal);

        if (state.paused) {
          await new Promise(resume => {
            state.resumeQueue.add(resume);
          });
        }

        return result;
      })();
    };

    let result;

    if (globals.skipAnimation) {
      stopAsync(state);
      return getFinishedResult(target, false);
    }

    try {
      let animating;

      if (is.arr(to)) {
        animating = (async queue => {
          for (const props of queue) {
            await animate(props);
          }
        })(to);
      } else {
          animating = Promise.resolve(to(animate, target.stop.bind(target)));
        }

      await Promise.all([animating.then(preventBail), bailPromise]);
      result = getFinishedResult(target.get(), true, false);
    } catch (err) {
      if (err instanceof BailSignal) {
        result = err.result;
      } else if (err instanceof SkipAniamtionSignal) {
        result = err.result;
      } else {
        throw err;
      }
    } finally {
      if (callId == state.asyncId) {
        state.asyncId = parentId;
        state.asyncTo = parentId ? prevTo : undefined;
        state.promise = parentId ? prevPromise : undefined;
      }
    }

    if (is.fun(onRest)) {
      raf.batchedUpdates(() => {
        onRest(result, target, target.item);
      });
    }

    return result;
  })();
}
function stopAsync(state, cancelId) {
  flush(state.timeouts, t => t.cancel());
  state.pauseQueue.clear();
  state.resumeQueue.clear();
  state.asyncId = state.asyncTo = state.promise = undefined;
  if (cancelId) state.cancelId = cancelId;
}
class BailSignal extends Error {
  constructor() {
    super('An async animation has been interrupted. You see this error because you ' + 'forgot to use `await` or `.catch(...)` on its returned promise.');
    this.result = void 0;
  }

}
class SkipAniamtionSignal extends Error {
  constructor() {
    super('SkipAnimationSignal');
    this.result = void 0;
  }

}

const isFrameValue = value => value instanceof FrameValue;
let nextId$1 = 1;
class FrameValue extends FluidValue {
  constructor(...args) {
    super(...args);
    this.id = nextId$1++;
    this.key = void 0;
    this._priority = 0;
  }

  get priority() {
    return this._priority;
  }

  set priority(priority) {
    if (this._priority != priority) {
      this._priority = priority;

      this._onPriorityChange(priority);
    }
  }

  get() {
    const node = getAnimated(this);
    return node && node.getValue();
  }

  to(...args) {
    return globals.to(this, args);
  }

  interpolate(...args) {
    deprecateInterpolate();
    return globals.to(this, args);
  }

  toJSON() {
    return this.get();
  }

  observerAdded(count) {
    if (count == 1) this._attach();
  }

  observerRemoved(count) {
    if (count == 0) this._detach();
  }

  _attach() {}

  _detach() {}

  _onChange(value, idle = false) {
    callFluidObservers(this, {
      type: 'change',
      parent: this,
      value,
      idle
    });
  }

  _onPriorityChange(priority) {
    if (!this.idle) {
      frameLoop.sort(this);
    }

    callFluidObservers(this, {
      type: 'priority',
      parent: this,
      priority
    });
  }

}

const $P = Symbol.for('SpringPhase');
const HAS_ANIMATED = 1;
const IS_ANIMATING = 2;
const IS_PAUSED = 4;
const hasAnimated = target => (target[$P] & HAS_ANIMATED) > 0;
const isAnimating = target => (target[$P] & IS_ANIMATING) > 0;
const isPaused = target => (target[$P] & IS_PAUSED) > 0;
const setActiveBit = (target, active) => active ? target[$P] |= IS_ANIMATING | HAS_ANIMATED : target[$P] &= ~IS_ANIMATING;
const setPausedBit = (target, paused) => paused ? target[$P] |= IS_PAUSED : target[$P] &= ~IS_PAUSED;

class SpringValue extends FrameValue {
  constructor(arg1, arg2) {
    super();
    this.key = void 0;
    this.animation = new Animation();
    this.queue = void 0;
    this.defaultProps = {};
    this._state = {
      paused: false,
      pauseQueue: new Set(),
      resumeQueue: new Set(),
      timeouts: new Set()
    };
    this._pendingCalls = new Set();
    this._lastCallId = 0;
    this._lastToId = 0;
    this._memoizedDuration = 0;

    if (!is.und(arg1) || !is.und(arg2)) {
      const props = is.obj(arg1) ? _extends$a({}, arg1) : _extends$a({}, arg2, {
        from: arg1
      });

      if (is.und(props.default)) {
        props.default = true;
      }

      this.start(props);
    }
  }

  get idle() {
    return !(isAnimating(this) || this._state.asyncTo) || isPaused(this);
  }

  get goal() {
    return getFluidValue(this.animation.to);
  }

  get velocity() {
    const node = getAnimated(this);
    return node instanceof AnimatedValue ? node.lastVelocity || 0 : node.getPayload().map(node => node.lastVelocity || 0);
  }

  get hasAnimated() {
    return hasAnimated(this);
  }

  get isAnimating() {
    return isAnimating(this);
  }

  get isPaused() {
    return isPaused(this);
  }

  advance(dt) {
    let idle = true;
    let changed = false;
    const anim = this.animation;
    let {
      config,
      toValues
    } = anim;
    const payload = getPayload(anim.to);

    if (!payload && hasFluidValue(anim.to)) {
      toValues = toArray(getFluidValue(anim.to));
    }

    anim.values.forEach((node, i) => {
      if (node.done) return;
      const to = node.constructor == AnimatedString ? 1 : payload ? payload[i].lastPosition : toValues[i];
      let finished = anim.immediate;
      let position = to;

      if (!finished) {
        position = node.lastPosition;

        if (config.tension <= 0) {
          node.done = true;
          return;
        }

        let elapsed = node.elapsedTime += dt;
        const from = anim.fromValues[i];
        const v0 = node.v0 != null ? node.v0 : node.v0 = is.arr(config.velocity) ? config.velocity[i] : config.velocity;
        let velocity;

        if (!is.und(config.duration)) {
          let p = 1;

          if (config.duration > 0) {
            if (this._memoizedDuration !== config.duration) {
              this._memoizedDuration = config.duration;

              if (node.durationProgress > 0) {
                node.elapsedTime = config.duration * node.durationProgress;
                elapsed = node.elapsedTime += dt;
              }
            }

            p = (config.progress || 0) + elapsed / this._memoizedDuration;
            p = p > 1 ? 1 : p < 0 ? 0 : p;
            node.durationProgress = p;
          }

          position = from + config.easing(p) * (to - from);
          velocity = (position - node.lastPosition) / dt;
          finished = p == 1;
        } else if (config.decay) {
            const decay = config.decay === true ? 0.998 : config.decay;
            const e = Math.exp(-(1 - decay) * elapsed);
            position = from + v0 / (1 - decay) * (1 - e);
            finished = Math.abs(node.lastPosition - position) < 0.1;
            velocity = v0 * e;
          } else {
              velocity = node.lastVelocity == null ? v0 : node.lastVelocity;
              const precision = config.precision || (from == to ? 0.005 : Math.min(1, Math.abs(to - from) * 0.001));
              const restVelocity = config.restVelocity || precision / 10;
              const bounceFactor = config.clamp ? 0 : config.bounce;
              const canBounce = !is.und(bounceFactor);
              const isGrowing = from == to ? node.v0 > 0 : from < to;
              let isMoving;
              let isBouncing = false;
              const step = 1;
              const numSteps = Math.ceil(dt / step);

              for (let n = 0; n < numSteps; ++n) {
                isMoving = Math.abs(velocity) > restVelocity;

                if (!isMoving) {
                  finished = Math.abs(to - position) <= precision;

                  if (finished) {
                    break;
                  }
                }

                if (canBounce) {
                  isBouncing = position == to || position > to == isGrowing;

                  if (isBouncing) {
                    velocity = -velocity * bounceFactor;
                    position = to;
                  }
                }

                const springForce = -config.tension * 0.000001 * (position - to);
                const dampingForce = -config.friction * 0.001 * velocity;
                const acceleration = (springForce + dampingForce) / config.mass;
                velocity = velocity + acceleration * step;
                position = position + velocity * step;
              }
            }

        node.lastVelocity = velocity;

        if (Number.isNaN(position)) {
          console.warn(`Got NaN while animating:`, this);
          finished = true;
        }
      }

      if (payload && !payload[i].done) {
        finished = false;
      }

      if (finished) {
        node.done = true;
      } else {
        idle = false;
      }

      if (node.setValue(position, config.round)) {
        changed = true;
      }
    });
    const node = getAnimated(this);
    const currVal = node.getValue();

    if (idle) {
      const finalVal = getFluidValue(anim.to);

      if ((currVal !== finalVal || changed) && !config.decay) {
        node.setValue(finalVal);

        this._onChange(finalVal);
      } else if (changed && config.decay) {
        this._onChange(currVal);
      }

      this._stop();
    } else if (changed) {
      this._onChange(currVal);
    }
  }

  set(value) {
    raf.batchedUpdates(() => {
      this._stop();

      this._focus(value);

      this._set(value);
    });
    return this;
  }

  pause() {
    this._update({
      pause: true
    });
  }

  resume() {
    this._update({
      pause: false
    });
  }

  finish() {
    if (isAnimating(this)) {
      const {
        to,
        config
      } = this.animation;
      raf.batchedUpdates(() => {
        this._onStart();

        if (!config.decay) {
          this._set(to, false);
        }

        this._stop();
      });
    }

    return this;
  }

  update(props) {
    const queue = this.queue || (this.queue = []);
    queue.push(props);
    return this;
  }

  start(to, arg2) {
    let queue;

    if (!is.und(to)) {
      queue = [is.obj(to) ? to : _extends$a({}, arg2, {
        to
      })];
    } else {
      queue = this.queue || [];
      this.queue = [];
    }

    return Promise.all(queue.map(props => this._update(props))).then(results => getCombinedResult(this, results));
  }

  stop(cancel) {
    const {
      to
    } = this.animation;

    this._focus(this.get());

    stopAsync(this._state, cancel && this._lastCallId);
    raf.batchedUpdates(() => this._stop(to, cancel));
    return this;
  }

  reset() {
    this._update({
      reset: true
    });
  }

  eventObserved(event) {
    if (event.type == 'change') {
      this._start();
    } else if (event.type == 'priority') {
      this.priority = event.priority + 1;
    }
  }

  _prepareNode(props) {
    const key = this.key || '';
    let {
      to,
      from
    } = props;
    to = is.obj(to) ? to[key] : to;

    if (to == null || isAsyncTo(to)) {
      to = undefined;
    }

    from = is.obj(from) ? from[key] : from;

    if (from == null) {
      from = undefined;
    }

    const range = {
      to,
      from
    };

    if (!hasAnimated(this)) {
      if (props.reverse) [to, from] = [from, to];
      from = getFluidValue(from);

      if (!is.und(from)) {
        this._set(from);
      } else if (!getAnimated(this)) {
          this._set(to);
        }
    }

    return range;
  }

  _update(_ref, isLoop) {
    let props = _extends$a({}, _ref);

    const {
      key,
      defaultProps
    } = this;
    if (props.default) Object.assign(defaultProps, getDefaultProps(props, (value, prop) => /^on/.test(prop) ? resolveProp(value, key) : value));
    mergeActiveFn(this, props, 'onProps');
    sendEvent(this, 'onProps', props, this);

    const range = this._prepareNode(props);

    if (Object.isFrozen(this)) {
      throw Error('Cannot animate a `SpringValue` object that is frozen. ' + 'Did you forget to pass your component to `animated(...)` before animating its props?');
    }

    const state = this._state;
    return scheduleProps(++this._lastCallId, {
      key,
      props,
      defaultProps,
      state,
      actions: {
        pause: () => {
          if (!isPaused(this)) {
            setPausedBit(this, true);
            flushCalls(state.pauseQueue);
            sendEvent(this, 'onPause', getFinishedResult(this, checkFinished(this, this.animation.to)), this);
          }
        },
        resume: () => {
          if (isPaused(this)) {
            setPausedBit(this, false);

            if (isAnimating(this)) {
              this._resume();
            }

            flushCalls(state.resumeQueue);
            sendEvent(this, 'onResume', getFinishedResult(this, checkFinished(this, this.animation.to)), this);
          }
        },
        start: this._merge.bind(this, range)
      }
    }).then(result => {
      if (props.loop && result.finished && !(isLoop && result.noop)) {
        const nextProps = createLoopUpdate(props);

        if (nextProps) {
          return this._update(nextProps, true);
        }
      }

      return result;
    });
  }

  _merge(range, props, resolve) {
    if (props.cancel) {
      this.stop(true);
      return resolve(getCancelledResult(this));
    }

    const hasToProp = !is.und(range.to);
    const hasFromProp = !is.und(range.from);

    if (hasToProp || hasFromProp) {
      if (props.callId > this._lastToId) {
        this._lastToId = props.callId;
      } else {
        return resolve(getCancelledResult(this));
      }
    }

    const {
      key,
      defaultProps,
      animation: anim
    } = this;
    const {
      to: prevTo,
      from: prevFrom
    } = anim;
    let {
      to = prevTo,
      from = prevFrom
    } = range;

    if (hasFromProp && !hasToProp && (!props.default || is.und(to))) {
      to = from;
    }

    if (props.reverse) [to, from] = [from, to];
    const hasFromChanged = !isEqual(from, prevFrom);

    if (hasFromChanged) {
      anim.from = from;
    }

    from = getFluidValue(from);
    const hasToChanged = !isEqual(to, prevTo);

    if (hasToChanged) {
      this._focus(to);
    }

    const hasAsyncTo = isAsyncTo(props.to);
    const {
      config
    } = anim;
    const {
      decay,
      velocity
    } = config;

    if (hasToProp || hasFromProp) {
      config.velocity = 0;
    }

    if (props.config && !hasAsyncTo) {
      mergeConfig(config, callProp(props.config, key), props.config !== defaultProps.config ? callProp(defaultProps.config, key) : void 0);
    }

    let node = getAnimated(this);

    if (!node || is.und(to)) {
      return resolve(getFinishedResult(this, true));
    }

    const reset = is.und(props.reset) ? hasFromProp && !props.default : !is.und(from) && matchProp(props.reset, key);
    const value = reset ? from : this.get();
    const goal = computeGoal(to);
    const isAnimatable = is.num(goal) || is.arr(goal) || isAnimatedString(goal);
    const immediate = !hasAsyncTo && (!isAnimatable || matchProp(defaultProps.immediate || props.immediate, key));

    if (hasToChanged) {
      const nodeType = getAnimatedType(to);

      if (nodeType !== node.constructor) {
        if (immediate) {
          node = this._set(goal);
        } else throw Error(`Cannot animate between ${node.constructor.name} and ${nodeType.name}, as the "to" prop suggests`);
      }
    }

    const goalType = node.constructor;
    let started = hasFluidValue(to);
    let finished = false;

    if (!started) {
      const hasValueChanged = reset || !hasAnimated(this) && hasFromChanged;

      if (hasToChanged || hasValueChanged) {
        finished = isEqual(computeGoal(value), goal);
        started = !finished;
      }

      if (!isEqual(anim.immediate, immediate) && !immediate || !isEqual(config.decay, decay) || !isEqual(config.velocity, velocity)) {
        started = true;
      }
    }

    if (finished && isAnimating(this)) {
      if (anim.changed && !reset) {
        started = true;
      } else if (!started) {
          this._stop(prevTo);
        }
    }

    if (!hasAsyncTo) {
      if (started || hasFluidValue(prevTo)) {
        anim.values = node.getPayload();
        anim.toValues = hasFluidValue(to) ? null : goalType == AnimatedString ? [1] : toArray(goal);
      }

      if (anim.immediate != immediate) {
        anim.immediate = immediate;

        if (!immediate && !reset) {
          this._set(prevTo);
        }
      }

      if (started) {
        const {
          onRest
        } = anim;
        each(ACTIVE_EVENTS, type => mergeActiveFn(this, props, type));
        const result = getFinishedResult(this, checkFinished(this, prevTo));
        flushCalls(this._pendingCalls, result);

        this._pendingCalls.add(resolve);

        if (anim.changed) raf.batchedUpdates(() => {
          anim.changed = !reset;
          onRest == null ? void 0 : onRest(result, this);

          if (reset) {
            callProp(defaultProps.onRest, result);
          } else {
              anim.onStart == null ? void 0 : anim.onStart(result, this);
            }
        });
      }
    }

    if (reset) {
      this._set(value);
    }

    if (hasAsyncTo) {
      resolve(runAsync(props.to, props, this._state, this));
    } else if (started) {
        this._start();
      } else if (isAnimating(this) && !hasToChanged) {
          this._pendingCalls.add(resolve);
        } else {
            resolve(getNoopResult(value));
          }
  }

  _focus(value) {
    const anim = this.animation;

    if (value !== anim.to) {
      if (getFluidObservers(this)) {
        this._detach();
      }

      anim.to = value;

      if (getFluidObservers(this)) {
        this._attach();
      }
    }
  }

  _attach() {
    let priority = 0;
    const {
      to
    } = this.animation;

    if (hasFluidValue(to)) {
      addFluidObserver(to, this);

      if (isFrameValue(to)) {
        priority = to.priority + 1;
      }
    }

    this.priority = priority;
  }

  _detach() {
    const {
      to
    } = this.animation;

    if (hasFluidValue(to)) {
      removeFluidObserver(to, this);
    }
  }

  _set(arg, idle = true) {
    const value = getFluidValue(arg);

    if (!is.und(value)) {
      const oldNode = getAnimated(this);

      if (!oldNode || !isEqual(value, oldNode.getValue())) {
        const nodeType = getAnimatedType(value);

        if (!oldNode || oldNode.constructor != nodeType) {
          setAnimated(this, nodeType.create(value));
        } else {
          oldNode.setValue(value);
        }

        if (oldNode) {
          raf.batchedUpdates(() => {
            this._onChange(value, idle);
          });
        }
      }
    }

    return getAnimated(this);
  }

  _onStart() {
    const anim = this.animation;

    if (!anim.changed) {
      anim.changed = true;
      sendEvent(this, 'onStart', getFinishedResult(this, checkFinished(this, anim.to)), this);
    }
  }

  _onChange(value, idle) {
    if (!idle) {
      this._onStart();

      callProp(this.animation.onChange, value, this);
    }

    callProp(this.defaultProps.onChange, value, this);

    super._onChange(value, idle);
  }

  _start() {
    const anim = this.animation;
    getAnimated(this).reset(getFluidValue(anim.to));

    if (!anim.immediate) {
      anim.fromValues = anim.values.map(node => node.lastPosition);
    }

    if (!isAnimating(this)) {
      setActiveBit(this, true);

      if (!isPaused(this)) {
        this._resume();
      }
    }
  }

  _resume() {
    if (globals.skipAnimation) {
      this.finish();
    } else {
      frameLoop.start(this);
    }
  }

  _stop(goal, cancel) {
    if (isAnimating(this)) {
      setActiveBit(this, false);
      const anim = this.animation;
      each(anim.values, node => {
        node.done = true;
      });

      if (anim.toValues) {
        anim.onChange = anim.onPause = anim.onResume = undefined;
      }

      callFluidObservers(this, {
        type: 'idle',
        parent: this
      });
      const result = cancel ? getCancelledResult(this.get()) : getFinishedResult(this.get(), checkFinished(this, goal != null ? goal : anim.to));
      flushCalls(this._pendingCalls, result);

      if (anim.changed) {
        anim.changed = false;
        sendEvent(this, 'onRest', result, this);
      }
    }
  }

}

function checkFinished(target, to) {
  const goal = computeGoal(to);
  const value = computeGoal(target.get());
  return isEqual(value, goal);
}

function createLoopUpdate(props, loop = props.loop, to = props.to) {
  let loopRet = callProp(loop);

  if (loopRet) {
    const overrides = loopRet !== true && inferTo(loopRet);
    const reverse = (overrides || props).reverse;
    const reset = !overrides || overrides.reset;
    return createUpdate(_extends$a({}, props, {
      loop,
      default: false,
      pause: undefined,
      to: !reverse || isAsyncTo(to) ? to : undefined,
      from: reset ? props.from : undefined,
      reset
    }, overrides));
  }
}
function createUpdate(props) {
  const {
    to,
    from
  } = props = inferTo(props);
  const keys = new Set();
  if (is.obj(to)) findDefined(to, keys);
  if (is.obj(from)) findDefined(from, keys);
  props.keys = keys.size ? Array.from(keys) : null;
  return props;
}
function declareUpdate(props) {
  const update = createUpdate(props);

  if (is.und(update.default)) {
    update.default = getDefaultProps(update);
  }

  return update;
}

function findDefined(values, keys) {
  eachProp(values, (value, key) => value != null && keys.add(key));
}

const ACTIVE_EVENTS = ['onStart', 'onRest', 'onChange', 'onPause', 'onResume'];

function mergeActiveFn(target, props, type) {
  target.animation[type] = props[type] !== getDefaultProp(props, type) ? resolveProp(props[type], target.key) : undefined;
}

function sendEvent(target, type, ...args) {
  var _target$animation$typ, _target$animation, _target$defaultProps$, _target$defaultProps;

  (_target$animation$typ = (_target$animation = target.animation)[type]) == null ? void 0 : _target$animation$typ.call(_target$animation, ...args);
  (_target$defaultProps$ = (_target$defaultProps = target.defaultProps)[type]) == null ? void 0 : _target$defaultProps$.call(_target$defaultProps, ...args);
}

const BATCHED_EVENTS = ['onStart', 'onChange', 'onRest'];
let nextId = 1;
class Controller {
  constructor(props, flush) {
    this.id = nextId++;
    this.springs = {};
    this.queue = [];
    this.ref = void 0;
    this._flush = void 0;
    this._initialProps = void 0;
    this._lastAsyncId = 0;
    this._active = new Set();
    this._changed = new Set();
    this._started = false;
    this._item = void 0;
    this._state = {
      paused: false,
      pauseQueue: new Set(),
      resumeQueue: new Set(),
      timeouts: new Set()
    };
    this._events = {
      onStart: new Map(),
      onChange: new Map(),
      onRest: new Map()
    };
    this._onFrame = this._onFrame.bind(this);

    if (flush) {
      this._flush = flush;
    }

    if (props) {
      this.start(_extends$a({
        default: true
      }, props));
    }
  }

  get idle() {
    return !this._state.asyncTo && Object.values(this.springs).every(spring => spring.idle);
  }

  get item() {
    return this._item;
  }

  set item(item) {
    this._item = item;
  }

  get() {
    const values = {};
    this.each((spring, key) => values[key] = spring.get());
    return values;
  }

  set(values) {
    for (const key in values) {
      const value = values[key];

      if (!is.und(value)) {
        this.springs[key].set(value);
      }
    }
  }

  update(props) {
    if (props) {
      this.queue.push(createUpdate(props));
    }

    return this;
  }

  start(props) {
    let {
      queue
    } = this;

    if (props) {
      queue = toArray(props).map(createUpdate);
    } else {
      this.queue = [];
    }

    if (this._flush) {
      return this._flush(this, queue);
    }

    prepareKeys(this, queue);
    return flushUpdateQueue(this, queue);
  }

  stop(arg, keys) {
    if (arg !== !!arg) {
      keys = arg;
    }

    if (keys) {
      const springs = this.springs;
      each(toArray(keys), key => springs[key].stop(!!arg));
    } else {
      stopAsync(this._state, this._lastAsyncId);
      this.each(spring => spring.stop(!!arg));
    }

    return this;
  }

  pause(keys) {
    if (is.und(keys)) {
      this.start({
        pause: true
      });
    } else {
      const springs = this.springs;
      each(toArray(keys), key => springs[key].pause());
    }

    return this;
  }

  resume(keys) {
    if (is.und(keys)) {
      this.start({
        pause: false
      });
    } else {
      const springs = this.springs;
      each(toArray(keys), key => springs[key].resume());
    }

    return this;
  }

  each(iterator) {
    eachProp(this.springs, iterator);
  }

  _onFrame() {
    const {
      onStart,
      onChange,
      onRest
    } = this._events;
    const active = this._active.size > 0;
    const changed = this._changed.size > 0;

    if (active && !this._started || changed && !this._started) {
      this._started = true;
      flush(onStart, ([onStart, result]) => {
        result.value = this.get();
        onStart(result, this, this._item);
      });
    }

    const idle = !active && this._started;
    const values = changed || idle && onRest.size ? this.get() : null;

    if (changed && onChange.size) {
      flush(onChange, ([onChange, result]) => {
        result.value = values;
        onChange(result, this, this._item);
      });
    }

    if (idle) {
      this._started = false;
      flush(onRest, ([onRest, result]) => {
        result.value = values;
        onRest(result, this, this._item);
      });
    }
  }

  eventObserved(event) {
    if (event.type == 'change') {
      this._changed.add(event.parent);

      if (!event.idle) {
        this._active.add(event.parent);
      }
    } else if (event.type == 'idle') {
      this._active.delete(event.parent);
    } else return;

    raf.onFrame(this._onFrame);
  }

}
function flushUpdateQueue(ctrl, queue) {
  return Promise.all(queue.map(props => flushUpdate(ctrl, props))).then(results => getCombinedResult(ctrl, results));
}
async function flushUpdate(ctrl, props, isLoop) {
  const {
    keys,
    to,
    from,
    loop,
    onRest,
    onResolve
  } = props;
  const defaults = is.obj(props.default) && props.default;

  if (loop) {
    props.loop = false;
  }

  if (to === false) props.to = null;
  if (from === false) props.from = null;
  const asyncTo = is.arr(to) || is.fun(to) ? to : undefined;

  if (asyncTo) {
    props.to = undefined;
    props.onRest = undefined;

    if (defaults) {
      defaults.onRest = undefined;
    }
  } else {
      each(BATCHED_EVENTS, key => {
        const handler = props[key];

        if (is.fun(handler)) {
          const queue = ctrl['_events'][key];

          props[key] = ({
            finished,
            cancelled
          }) => {
            const result = queue.get(handler);

            if (result) {
              if (!finished) result.finished = false;
              if (cancelled) result.cancelled = true;
            } else {
              queue.set(handler, {
                value: null,
                finished: finished || false,
                cancelled: cancelled || false
              });
            }
          };

          if (defaults) {
            defaults[key] = props[key];
          }
        }
      });
    }

  const state = ctrl['_state'];

  if (props.pause === !state.paused) {
    state.paused = props.pause;
    flushCalls(props.pause ? state.pauseQueue : state.resumeQueue);
  } else if (state.paused) {
      props.pause = true;
    }

  const promises = (keys || Object.keys(ctrl.springs)).map(key => ctrl.springs[key].start(props));
  const cancel = props.cancel === true || getDefaultProp(props, 'cancel') === true;

  if (asyncTo || cancel && state.asyncId) {
    promises.push(scheduleProps(++ctrl['_lastAsyncId'], {
      props,
      state,
      actions: {
        pause: noop$4,
        resume: noop$4,

        start(props, resolve) {
          if (cancel) {
            stopAsync(state, ctrl['_lastAsyncId']);
            resolve(getCancelledResult(ctrl));
          } else {
            props.onRest = onRest;
            resolve(runAsync(asyncTo, props, state, ctrl));
          }
        }

      }
    }));
  }

  if (state.paused) {
    await new Promise(resume => {
      state.resumeQueue.add(resume);
    });
  }

  const result = getCombinedResult(ctrl, await Promise.all(promises));

  if (loop && result.finished && !(isLoop && result.noop)) {
    const nextProps = createLoopUpdate(props, loop, to);

    if (nextProps) {
      prepareKeys(ctrl, [nextProps]);
      return flushUpdate(ctrl, nextProps, true);
    }
  }

  if (onResolve) {
    raf.batchedUpdates(() => onResolve(result, ctrl, ctrl.item));
  }

  return result;
}
function getSprings(ctrl, props) {
  const springs = _extends$a({}, ctrl.springs);

  if (props) {
    each(toArray(props), props => {
      if (is.und(props.keys)) {
        props = createUpdate(props);
      }

      if (!is.obj(props.to)) {
        props = _extends$a({}, props, {
          to: undefined
        });
      }

      prepareSprings(springs, props, key => {
        return createSpring(key);
      });
    });
  }

  setSprings(ctrl, springs);
  return springs;
}
function setSprings(ctrl, springs) {
  eachProp(springs, (spring, key) => {
    if (!ctrl.springs[key]) {
      ctrl.springs[key] = spring;
      addFluidObserver(spring, ctrl);
    }
  });
}

function createSpring(key, observer) {
  const spring = new SpringValue();
  spring.key = key;

  if (observer) {
    addFluidObserver(spring, observer);
  }

  return spring;
}

function prepareSprings(springs, props, create) {
  if (props.keys) {
    each(props.keys, key => {
      const spring = springs[key] || (springs[key] = create(key));
      spring['_prepareNode'](props);
    });
  }
}

function prepareKeys(ctrl, queue) {
  each(queue, props => {
    prepareSprings(ctrl.springs, props, key => {
      return createSpring(key, ctrl);
    });
  });
}

function _objectWithoutPropertiesLoose$1(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

const _excluded$3 = ["children"];
const SpringContext = _ref => {
  let {
    children
  } = _ref,
      props = _objectWithoutPropertiesLoose$1(_ref, _excluded$3);

  const inherited = useContext(ctx);
  const pause = props.pause || !!inherited.pause,
        immediate = props.immediate || !!inherited.immediate;
  props = useMemoOne(() => ({
    pause,
    immediate
  }), [pause, immediate]);
  const {
    Provider
  } = ctx;
  return React.createElement(Provider, {
    value: props
  }, children);
};
const ctx = makeContext(SpringContext, {});
SpringContext.Provider = ctx.Provider;
SpringContext.Consumer = ctx.Consumer;

function makeContext(target, init) {
  Object.assign(target, React.createContext(init));
  target.Provider._context = target;
  target.Consumer._context = target;
  return target;
}

const SpringRef = () => {
  const current = [];

  const SpringRef = function SpringRef(props) {
    deprecateDirectCall();
    const results = [];
    each(current, (ctrl, i) => {
      if (is.und(props)) {
        results.push(ctrl.start());
      } else {
        const update = _getProps(props, ctrl, i);

        if (update) {
          results.push(ctrl.start(update));
        }
      }
    });
    return results;
  };

  SpringRef.current = current;

  SpringRef.add = function (ctrl) {
    if (!current.includes(ctrl)) {
      current.push(ctrl);
    }
  };

  SpringRef.delete = function (ctrl) {
    const i = current.indexOf(ctrl);
    if (~i) current.splice(i, 1);
  };

  SpringRef.pause = function () {
    each(current, ctrl => ctrl.pause(...arguments));
    return this;
  };

  SpringRef.resume = function () {
    each(current, ctrl => ctrl.resume(...arguments));
    return this;
  };

  SpringRef.set = function (values) {
    each(current, ctrl => ctrl.set(values));
  };

  SpringRef.start = function (props) {
    const results = [];
    each(current, (ctrl, i) => {
      if (is.und(props)) {
        results.push(ctrl.start());
      } else {
        const update = this._getProps(props, ctrl, i);

        if (update) {
          results.push(ctrl.start(update));
        }
      }
    });
    return results;
  };

  SpringRef.stop = function () {
    each(current, ctrl => ctrl.stop(...arguments));
    return this;
  };

  SpringRef.update = function (props) {
    each(current, (ctrl, i) => ctrl.update(this._getProps(props, ctrl, i)));
    return this;
  };

  const _getProps = function _getProps(arg, ctrl, index) {
    return is.fun(arg) ? arg(index, ctrl) : arg;
  };

  SpringRef._getProps = _getProps;
  return SpringRef;
};

function useSprings(length, props, deps) {
  const propsFn = is.fun(props) && props;
  if (propsFn && !deps) deps = [];
  const ref = useMemo(() => propsFn || arguments.length == 3 ? SpringRef() : void 0, []);
  const layoutId = useRef(0);
  const forceUpdate = useForceUpdate();
  const state = useMemo(() => ({
    ctrls: [],
    queue: [],

    flush(ctrl, updates) {
      const springs = getSprings(ctrl, updates);
      const canFlushSync = layoutId.current > 0 && !state.queue.length && !Object.keys(springs).some(key => !ctrl.springs[key]);
      return canFlushSync ? flushUpdateQueue(ctrl, updates) : new Promise(resolve => {
        setSprings(ctrl, springs);
        state.queue.push(() => {
          resolve(flushUpdateQueue(ctrl, updates));
        });
        forceUpdate();
      });
    }

  }), []);
  const ctrls = useRef([...state.ctrls]);
  const updates = [];
  const prevLength = usePrev(length) || 0;
  useMemo(() => {
    each(ctrls.current.slice(length, prevLength), ctrl => {
      detachRefs(ctrl, ref);
      ctrl.stop(true);
    });
    ctrls.current.length = length;
    declareUpdates(prevLength, length);
  }, [length]);
  useMemo(() => {
    declareUpdates(0, Math.min(prevLength, length));
  }, deps);

  function declareUpdates(startIndex, endIndex) {
    for (let i = startIndex; i < endIndex; i++) {
      const ctrl = ctrls.current[i] || (ctrls.current[i] = new Controller(null, state.flush));
      const update = propsFn ? propsFn(i, ctrl) : props[i];

      if (update) {
        updates[i] = declareUpdate(update);
      }
    }
  }

  const springs = ctrls.current.map((ctrl, i) => getSprings(ctrl, updates[i]));
  const context = useContext(SpringContext);
  const prevContext = usePrev(context);
  const hasContext = context !== prevContext && hasProps(context);
  useLayoutEffect(() => {
    layoutId.current++;
    state.ctrls = ctrls.current;
    const {
      queue
    } = state;

    if (queue.length) {
      state.queue = [];
      each(queue, cb => cb());
    }

    each(ctrls.current, (ctrl, i) => {
      ref == null ? void 0 : ref.add(ctrl);

      if (hasContext) {
        ctrl.start({
          default: context
        });
      }

      const update = updates[i];

      if (update) {
        replaceRef(ctrl, update.ref);

        if (ctrl.ref) {
          ctrl.queue.push(update);
        } else {
          ctrl.start(update);
        }
      }
    });
  });
  useOnce(() => () => {
    each(state.ctrls, ctrl => ctrl.stop(true));
  });
  const values = springs.map(x => _extends$a({}, x));
  return ref ? [values, ref] : values;
}

function useSpring(props, deps) {
  const isFn = is.fun(props);
  const [[values], ref] = useSprings(1, isFn ? props : [props], isFn ? deps || [] : deps);
  return isFn || arguments.length == 2 ? [values, ref] : values;
}

let TransitionPhase;

(function (TransitionPhase) {
  TransitionPhase["MOUNT"] = "mount";
  TransitionPhase["ENTER"] = "enter";
  TransitionPhase["UPDATE"] = "update";
  TransitionPhase["LEAVE"] = "leave";
})(TransitionPhase || (TransitionPhase = {}));

function useTransition(data, props, deps) {
  const propsFn = is.fun(props) && props;
  const {
    reset,
    sort,
    trail = 0,
    expires = true,
    onDestroyed,
    ref: propsRef,
    config: propsConfig
  } = propsFn ? propsFn() : props;
  const ref = useMemo(() => propsFn || arguments.length == 3 ? SpringRef() : void 0, []);
  const items = toArray(data);
  const transitions = [];
  const usedTransitions = useRef(null);
  const prevTransitions = reset ? null : usedTransitions.current;
  useLayoutEffect(() => {
    usedTransitions.current = transitions;
  });
  useOnce(() => () => each(usedTransitions.current, t => {
    if (t.expired) {
      clearTimeout(t.expirationId);
    }

    detachRefs(t.ctrl, ref);
    t.ctrl.stop(true);
  }));
  const keys = getKeys(items, propsFn ? propsFn() : props, prevTransitions);
  const expired = reset && usedTransitions.current || [];
  useLayoutEffect(() => each(expired, ({
    ctrl,
    item,
    key
  }) => {
    detachRefs(ctrl, ref);
    callProp(onDestroyed, item, key);
  }));
  const reused = [];
  if (prevTransitions) each(prevTransitions, (t, i) => {
    if (t.expired) {
      clearTimeout(t.expirationId);
      expired.push(t);
    } else {
      i = reused[i] = keys.indexOf(t.key);
      if (~i) transitions[i] = t;
    }
  });
  each(items, (item, i) => {
    if (!transitions[i]) {
      transitions[i] = {
        key: keys[i],
        item,
        phase: TransitionPhase.MOUNT,
        ctrl: new Controller()
      };
      transitions[i].ctrl.item = item;
    }
  });

  if (reused.length) {
    let i = -1;
    const {
      leave
    } = propsFn ? propsFn() : props;
    each(reused, (keyIndex, prevIndex) => {
      const t = prevTransitions[prevIndex];

      if (~keyIndex) {
        i = transitions.indexOf(t);
        transitions[i] = _extends$a({}, t, {
          item: items[keyIndex]
        });
      } else if (leave) {
        transitions.splice(++i, 0, t);
      }
    });
  }

  if (is.fun(sort)) {
    transitions.sort((a, b) => sort(a.item, b.item));
  }

  let delay = -trail;
  const forceUpdate = useForceUpdate();
  const defaultProps = getDefaultProps(props);
  const changes = new Map();
  each(transitions, (t, i) => {
    const key = t.key;
    const prevPhase = t.phase;
    const p = propsFn ? propsFn() : props;
    let to;
    let phase;
    let propsDelay = callProp(p.delay || 0, key);

    if (prevPhase == TransitionPhase.MOUNT) {
      to = p.enter;
      phase = TransitionPhase.ENTER;
    } else {
      const isLeave = keys.indexOf(key) < 0;

      if (prevPhase != TransitionPhase.LEAVE) {
        if (isLeave) {
          to = p.leave;
          phase = TransitionPhase.LEAVE;
        } else if (to = p.update) {
          phase = TransitionPhase.UPDATE;
        } else return;
      } else if (!isLeave) {
        to = p.enter;
        phase = TransitionPhase.ENTER;
      } else return;
    }

    to = callProp(to, t.item, i);
    to = is.obj(to) ? inferTo(to) : {
      to
    };

    if (!to.config) {
      const config = propsConfig || defaultProps.config;
      to.config = callProp(config, t.item, i, phase);
    }

    delay += trail;

    const payload = _extends$a({}, defaultProps, {
      delay: propsDelay + delay,
      ref: propsRef,
      immediate: p.immediate,
      reset: false
    }, to);

    if (phase == TransitionPhase.ENTER && is.und(payload.from)) {
      const _p = propsFn ? propsFn() : props;

      const from = is.und(_p.initial) || prevTransitions ? _p.from : _p.initial;
      payload.from = callProp(from, t.item, i);
    }

    const {
      onResolve
    } = payload;

    payload.onResolve = result => {
      callProp(onResolve, result);
      const transitions = usedTransitions.current;
      const t = transitions.find(t => t.key === key);
      if (!t) return;

      if (result.cancelled && t.phase != TransitionPhase.UPDATE) {
        return;
      }

      if (t.ctrl.idle) {
        const idle = transitions.every(t => t.ctrl.idle);

        if (t.phase == TransitionPhase.LEAVE) {
          const expiry = callProp(expires, t.item);

          if (expiry !== false) {
            const expiryMs = expiry === true ? 0 : expiry;
            t.expired = true;

            if (!idle && expiryMs > 0) {
              if (expiryMs <= 0x7fffffff) t.expirationId = setTimeout(forceUpdate, expiryMs);
              return;
            }
          }
        }

        if (idle && transitions.some(t => t.expired)) {
          forceUpdate();
        }
      }
    };

    const springs = getSprings(t.ctrl, payload);
    changes.set(t, {
      phase,
      springs,
      payload
    });
  });
  const context = useContext(SpringContext);
  const prevContext = usePrev(context);
  const hasContext = context !== prevContext && hasProps(context);
  useLayoutEffect(() => {
    if (hasContext) each(transitions, t => {
      t.ctrl.start({
        default: context
      });
    });
  }, [context]);
  useLayoutEffect(() => {
    each(changes, ({
      phase,
      payload
    }, t) => {
      const {
        ctrl
      } = t;
      t.phase = phase;
      ref == null ? void 0 : ref.add(ctrl);

      if (hasContext && phase == TransitionPhase.ENTER) {
        ctrl.start({
          default: context
        });
      }

      if (payload) {
        replaceRef(ctrl, payload.ref);

        if (ctrl.ref) {
          ctrl.update(payload);
        } else {
          ctrl.start(payload);
        }
      }
    });
  }, reset ? void 0 : deps);

  const renderTransitions = render => React.createElement(React.Fragment, null, transitions.map((t, i) => {
    const {
      springs
    } = changes.get(t) || t.ctrl;
    const elem = render(_extends$a({}, springs), t.item, t, i);
    return elem && elem.type ? React.createElement(elem.type, _extends$a({}, elem.props, {
      key: is.str(t.key) || is.num(t.key) ? t.key : t.ctrl.id,
      ref: elem.ref
    })) : elem;
  }));

  return ref ? [renderTransitions, ref] : renderTransitions;
}
let nextKey = 1;

function getKeys(items, {
  key,
  keys = key
}, prevTransitions) {
  if (keys === null) {
    const reused = new Set();
    return items.map(item => {
      const t = prevTransitions && prevTransitions.find(t => t.item === item && t.phase !== TransitionPhase.LEAVE && !reused.has(t));

      if (t) {
        reused.add(t);
        return t.key;
      }

      return nextKey++;
    });
  }

  return is.und(keys) ? items : is.fun(keys) ? items.map(keys) : toArray(keys);
}

class Interpolation extends FrameValue {
  constructor(source, args) {
    super();
    this.key = void 0;
    this.idle = true;
    this.calc = void 0;
    this._active = new Set();
    this.source = source;
    this.calc = createInterpolator(...args);

    const value = this._get();

    const nodeType = getAnimatedType(value);
    setAnimated(this, nodeType.create(value));
  }

  advance(_dt) {
    const value = this._get();

    const oldValue = this.get();

    if (!isEqual(value, oldValue)) {
      getAnimated(this).setValue(value);

      this._onChange(value, this.idle);
    }

    if (!this.idle && checkIdle(this._active)) {
      becomeIdle(this);
    }
  }

  _get() {
    const inputs = is.arr(this.source) ? this.source.map(getFluidValue) : toArray(getFluidValue(this.source));
    return this.calc(...inputs);
  }

  _start() {
    if (this.idle && !checkIdle(this._active)) {
      this.idle = false;
      each(getPayload(this), node => {
        node.done = false;
      });

      if (globals.skipAnimation) {
        raf.batchedUpdates(() => this.advance());
        becomeIdle(this);
      } else {
        frameLoop.start(this);
      }
    }
  }

  _attach() {
    let priority = 1;
    each(toArray(this.source), source => {
      if (hasFluidValue(source)) {
        addFluidObserver(source, this);
      }

      if (isFrameValue(source)) {
        if (!source.idle) {
          this._active.add(source);
        }

        priority = Math.max(priority, source.priority + 1);
      }
    });
    this.priority = priority;

    this._start();
  }

  _detach() {
    each(toArray(this.source), source => {
      if (hasFluidValue(source)) {
        removeFluidObserver(source, this);
      }
    });

    this._active.clear();

    becomeIdle(this);
  }

  eventObserved(event) {
    if (event.type == 'change') {
      if (event.idle) {
        this.advance();
      } else {
        this._active.add(event.parent);

        this._start();
      }
    } else if (event.type == 'idle') {
        this._active.delete(event.parent);
      } else if (event.type == 'priority') {
          this.priority = toArray(this.source).reduce((highest, parent) => Math.max(highest, (isFrameValue(parent) ? parent.priority : 0) + 1), 0);
        }
  }

}

function isIdle(source) {
  return source.idle !== false;
}

function checkIdle(active) {
  return !active.size || Array.from(active).every(isIdle);
}

function becomeIdle(self) {
  if (!self.idle) {
    self.idle = true;
    each(getPayload(self), node => {
      node.done = true;
    });
    callFluidObservers(self, {
      type: 'idle',
      parent: self
    });
  }
}

const to = (source, ...args) => new Interpolation(source, args);

globals.assign({
  createStringInterpolator,
  to: (source, args) => new Interpolation(source, args)
});

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

const _excluded$2 = ["style", "children", "scrollTop", "scrollLeft"];
const isCustomPropRE = /^--/;

function dangerousStyleValue(name, value) {
  if (value == null || typeof value === 'boolean' || value === '') return '';
  if (typeof value === 'number' && value !== 0 && !isCustomPropRE.test(name) && !(isUnitlessNumber.hasOwnProperty(name) && isUnitlessNumber[name])) return value + 'px';
  return ('' + value).trim();
}

const attributeCache = {};
function applyAnimatedValues(instance, props) {
  if (!instance.nodeType || !instance.setAttribute) {
    return false;
  }

  const isFilterElement = instance.nodeName === 'filter' || instance.parentNode && instance.parentNode.nodeName === 'filter';

  const _ref = props,
        {
    style,
    children,
    scrollTop,
    scrollLeft
  } = _ref,
        attributes = _objectWithoutPropertiesLoose(_ref, _excluded$2);

  const values = Object.values(attributes);
  const names = Object.keys(attributes).map(name => isFilterElement || instance.hasAttribute(name) ? name : attributeCache[name] || (attributeCache[name] = name.replace(/([A-Z])/g, n => '-' + n.toLowerCase())));

  if (children !== void 0) {
    instance.textContent = children;
  }

  for (let name in style) {
    if (style.hasOwnProperty(name)) {
      const value = dangerousStyleValue(name, style[name]);

      if (isCustomPropRE.test(name)) {
        instance.style.setProperty(name, value);
      } else {
        instance.style[name] = value;
      }
    }
  }

  names.forEach((name, i) => {
    instance.setAttribute(name, values[i]);
  });

  if (scrollTop !== void 0) {
    instance.scrollTop = scrollTop;
  }

  if (scrollLeft !== void 0) {
    instance.scrollLeft = scrollLeft;
  }
}
let isUnitlessNumber = {
  animationIterationCount: true,
  borderImageOutset: true,
  borderImageSlice: true,
  borderImageWidth: true,
  boxFlex: true,
  boxFlexGroup: true,
  boxOrdinalGroup: true,
  columnCount: true,
  columns: true,
  flex: true,
  flexGrow: true,
  flexPositive: true,
  flexShrink: true,
  flexNegative: true,
  flexOrder: true,
  gridRow: true,
  gridRowEnd: true,
  gridRowSpan: true,
  gridRowStart: true,
  gridColumn: true,
  gridColumnEnd: true,
  gridColumnSpan: true,
  gridColumnStart: true,
  fontWeight: true,
  lineClamp: true,
  lineHeight: true,
  opacity: true,
  order: true,
  orphans: true,
  tabSize: true,
  widows: true,
  zIndex: true,
  zoom: true,
  fillOpacity: true,
  floodOpacity: true,
  stopOpacity: true,
  strokeDasharray: true,
  strokeDashoffset: true,
  strokeMiterlimit: true,
  strokeOpacity: true,
  strokeWidth: true
};

const prefixKey = (prefix, key) => prefix + key.charAt(0).toUpperCase() + key.substring(1);

const prefixes$2 = ['Webkit', 'Ms', 'Moz', 'O'];
isUnitlessNumber = Object.keys(isUnitlessNumber).reduce((acc, prop) => {
  prefixes$2.forEach(prefix => acc[prefixKey(prefix, prop)] = acc[prop]);
  return acc;
}, isUnitlessNumber);

const _excluded$1 = ["x", "y", "z"];
const domTransforms = /^(matrix|translate|scale|rotate|skew)/;
const pxTransforms = /^(translate)/;
const degTransforms = /^(rotate|skew)/;

const addUnit = (value, unit) => is.num(value) && value !== 0 ? value + unit : value;

const isValueIdentity = (value, id) => is.arr(value) ? value.every(v => isValueIdentity(v, id)) : is.num(value) ? value === id : parseFloat(value) === id;

class AnimatedStyle extends AnimatedObject {
  constructor(_ref) {
    let {
      x,
      y,
      z
    } = _ref,
        style = _objectWithoutPropertiesLoose(_ref, _excluded$1);

    const inputs = [];
    const transforms = [];

    if (x || y || z) {
      inputs.push([x || 0, y || 0, z || 0]);
      transforms.push(xyz => [`translate3d(${xyz.map(v => addUnit(v, 'px')).join(',')})`, isValueIdentity(xyz, 0)]);
    }

    eachProp(style, (value, key) => {
      if (key === 'transform') {
        inputs.push([value || '']);
        transforms.push(transform => [transform, transform === '']);
      } else if (domTransforms.test(key)) {
        delete style[key];
        if (is.und(value)) return;
        const unit = pxTransforms.test(key) ? 'px' : degTransforms.test(key) ? 'deg' : '';
        inputs.push(toArray(value));
        transforms.push(key === 'rotate3d' ? ([x, y, z, deg]) => [`rotate3d(${x},${y},${z},${addUnit(deg, unit)})`, isValueIdentity(deg, 0)] : input => [`${key}(${input.map(v => addUnit(v, unit)).join(',')})`, isValueIdentity(input, key.startsWith('scale') ? 1 : 0)]);
      }
    });

    if (inputs.length) {
      style.transform = new FluidTransform(inputs, transforms);
    }

    super(style);
  }

}

class FluidTransform extends FluidValue {
  constructor(inputs, transforms) {
    super();
    this._value = null;
    this.inputs = inputs;
    this.transforms = transforms;
  }

  get() {
    return this._value || (this._value = this._get());
  }

  _get() {
    let transform = '';
    let identity = true;
    each(this.inputs, (input, i) => {
      const arg1 = getFluidValue(input[0]);
      const [t, id] = this.transforms[i](is.arr(arg1) ? arg1 : input.map(getFluidValue));
      transform += ' ' + t;
      identity = identity && id;
    });
    return identity ? 'none' : transform;
  }

  observerAdded(count) {
    if (count == 1) each(this.inputs, input => each(input, value => hasFluidValue(value) && addFluidObserver(value, this)));
  }

  observerRemoved(count) {
    if (count == 0) each(this.inputs, input => each(input, value => hasFluidValue(value) && removeFluidObserver(value, this)));
  }

  eventObserved(event) {
    if (event.type == 'change') {
      this._value = null;
    }

    callFluidObservers(this, event);
  }

}

const primitives = ['a', 'abbr', 'address', 'area', 'article', 'aside', 'audio', 'b', 'base', 'bdi', 'bdo', 'big', 'blockquote', 'body', 'br', 'button', 'canvas', 'caption', 'cite', 'code', 'col', 'colgroup', 'data', 'datalist', 'dd', 'del', 'details', 'dfn', 'dialog', 'div', 'dl', 'dt', 'em', 'embed', 'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'html', 'i', 'iframe', 'img', 'input', 'ins', 'kbd', 'keygen', 'label', 'legend', 'li', 'link', 'main', 'map', 'mark', 'menu', 'menuitem', 'meta', 'meter', 'nav', 'noscript', 'object', 'ol', 'optgroup', 'option', 'output', 'p', 'param', 'picture', 'pre', 'progress', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'script', 'section', 'select', 'small', 'source', 'span', 'strong', 'style', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'textarea', 'tfoot', 'th', 'thead', 'time', 'title', 'tr', 'track', 'u', 'ul', 'var', 'video', 'wbr', 'circle', 'clipPath', 'defs', 'ellipse', 'foreignObject', 'g', 'image', 'line', 'linearGradient', 'mask', 'path', 'pattern', 'polygon', 'polyline', 'radialGradient', 'rect', 'stop', 'svg', 'text', 'tspan'];

const _excluded = ["scrollTop", "scrollLeft"];
globals.assign({
  batchedUpdates: unstable_batchedUpdates,
  createStringInterpolator,
  colors: colors$2
});
const host = createHost(primitives, {
  applyAnimatedValues,
  createAnimatedStyle: style => new AnimatedStyle(style),
  getComponentProps: _ref => {
    let props = _objectWithoutPropertiesLoose(_ref, _excluded);

    return props;
  }
});
const animated = host.animated;

function v$3(){return v$3=Object.assign||function(t){for(var o=1;o<arguments.length;o++){var i=arguments[o];for(var n in i)Object.prototype.hasOwnProperty.call(i,n)&&(t[n]=i[n]);}return t},v$3.apply(this,arguments)}var x$2={pointerEvents:"none",position:"absolute",zIndex:10,top:0,left:0},m=function(t,o){return "translate("+t+"px, "+o+"px)"},g$2=memo((function(t){var i,n=t.position,r=t.anchor,e=t.children,l=Vt(),d=tt(),p=d.animate,f=d.config,g=Ft(),b=g[0],w=g[1],T=useRef(!1),C=void 0,E=!1,P=w.width>0&&w.height>0,N=Math.round(n[0]),V=Math.round(n[1]);P&&("top"===r?(N-=w.width/2,V-=w.height+14):"right"===r?(N+=14,V-=w.height/2):"bottom"===r?(N-=w.width/2,V+=14):"left"===r?(N-=w.width+14,V-=w.height/2):"center"===r&&(N-=w.width/2,V-=w.height/2),C={transform:m(N,V)},T.current||(E=!0),T.current=[N,V]);var j=useSpring({to:C,config:f,immediate:!p||E}),O=v$3({},x$2,l.tooltip,{transform:null!=(i=j.transform)?i:m(N,V)});return jsx(animated.div,{ref:b,style:O,children:e})}));g$2.displayName="TooltipWrapper";var b$3=memo((function(t){var o=t.size,i=void 0===o?12:o,n=t.color,r=t.style;return jsx("span",{style:v$3({display:"block",width:i,height:i,background:n},void 0===r?{}:r)})})),w$3=memo((function(t){var o,i=t.id,n=t.value,r=t.format,e=t.enableChip,l=void 0!==e&&e,a=t.color,c=t.renderContent,h=Vt(),u=Xt(r);if("function"==typeof c)o=c();else {var f=n;void 0!==u&&void 0!==f&&(f=u(f)),o=jsxs("div",{style:h.tooltip.basic,children:[l&&jsx(b$3,{color:a,style:h.tooltip.chip}),void 0!==f?jsxs("span",{children:[i,": ",jsx("strong",{children:""+f})]}):i]});}return jsx("div",{style:h.tooltip.container,children:o})})),T$2={width:"100%",borderCollapse:"collapse"},C$3=memo((function(t){var o,i=t.title,n=t.rows,r=void 0===n?[]:n,e=t.renderContent,l=Vt();return r.length?(o="function"==typeof e?e():jsxs("div",{children:[i&&i,jsx("table",{style:v$3({},T$2,l.tooltip.table),children:jsx("tbody",{children:r.map((function(t,o){return jsx("tr",{children:t.map((function(t,o){return jsx("td",{style:l.tooltip.tableCell,children:t},o)}))},o)}))})})]}),jsx("div",{style:l.tooltip.container,children:o})):null}));C$3.displayName="TableTooltip";var E$2=memo((function(t){var o=t.x0,n=t.x1,r=t.y0,e=t.y1,l=Vt(),u=tt(),d=u.animate,p=u.config,f=useMemo((function(){return v$3({},l.crosshair.line,{pointerEvents:"none"})}),[l.crosshair.line]),x=useSpring({x1:o,x2:n,y1:r,y2:e,config:p,immediate:!d});return jsx(animated.line,v$3({},x,{fill:"none",style:f}))}));E$2.displayName="CrosshairLine";var P$3=memo((function(t){var o,i,n=t.width,r=t.height,e=t.type,l=t.x,a=t.y;return "cross"===e?(o={x0:l,x1:l,y0:0,y1:r},i={x0:0,x1:n,y0:a,y1:a}):"top-left"===e?(o={x0:l,x1:l,y0:0,y1:a},i={x0:0,x1:l,y0:a,y1:a}):"top"===e?o={x0:l,x1:l,y0:0,y1:a}:"top-right"===e?(o={x0:l,x1:l,y0:0,y1:a},i={x0:l,x1:n,y0:a,y1:a}):"right"===e?i={x0:l,x1:n,y0:a,y1:a}:"bottom-right"===e?(o={x0:l,x1:l,y0:a,y1:r},i={x0:l,x1:n,y0:a,y1:a}):"bottom"===e?o={x0:l,x1:l,y0:a,y1:r}:"bottom-left"===e?(o={x0:l,x1:l,y0:a,y1:r},i={x0:0,x1:l,y0:a,y1:a}):"left"===e?i={x0:0,x1:l,y0:a,y1:a}:"x"===e?o={x0:l,x1:l,y0:0,y1:r}:"y"===e&&(i={x0:0,x1:n,y0:a,y1:a}),jsxs(Fragment$1,{children:[o&&jsx(E$2,{x0:o.x0,x1:o.x1,y0:o.y0,y1:o.y1}),i&&jsx(E$2,{x0:i.x0,x1:i.x1,y0:i.y0,y1:i.y1})]})}));P$3.displayName="Crosshair";var N$3=createContext({showTooltipAt:function(){},showTooltipFromEvent:function(){},hideTooltip:function(){}}),V$1={isVisible:!1,position:[null,null],content:null,anchor:null},j$2=createContext(V$1),O$3=function(t){var o=useState(V$1),n=o[0],l=o[1],a=useCallback((function(t,o,i){var n=o[0],r=o[1];void 0===i&&(i="top"),l({isVisible:!0,position:[n,r],anchor:i,content:t});}),[l]),c=useCallback((function(o,i,n){void 0===n&&(n="top");var r=t.current.getBoundingClientRect(),e=i.clientX-r.left,a=i.clientY-r.top;"left"!==n&&"right"!==n||(n=e<r.width/2?"right":"left"),l({isVisible:!0,position:[e,a],anchor:n,content:o});}),[t,l]),s=useCallback((function(){l(V$1);}),[l]);return {actions:useMemo((function(){return {showTooltipAt:a,showTooltipFromEvent:c,hideTooltip:s}}),[a,c,s]),state:n}},k$3=function(){var t=useContext(N$3);if(void 0===t)throw new Error("useTooltip must be used within a TooltipProvider");return t},z$4=function(){var t=useContext(j$2);if(void 0===t)throw new Error("useTooltipState must be used within a TooltipProvider");return t},A$3=function(t){return t.isVisible},F=function(){var t=z$4();return A$3(t)?jsx(g$2,{position:t.position,anchor:t.anchor,children:t.content}):null},M$1=function(t){var o=t.container,i=t.children,n=O$3(o),r=n.actions,e=n.state;return jsx(N$3.Provider,{value:r,children:jsx(j$2.Provider,{value:e,children:i})})};

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */

function listCacheClear$1() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear$1;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */

function eq$5(value, other) {
  return value === other || (value !== value && other !== other);
}

var eq_1 = eq$5;

var eq$4 = eq_1;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf$4(array, key) {
  var length = array.length;
  while (length--) {
    if (eq$4(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

var _assocIndexOf = assocIndexOf$4;

var assocIndexOf$3 = _assocIndexOf;

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete$1(key) {
  var data = this.__data__,
      index = assocIndexOf$3(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete$1;

var assocIndexOf$2 = _assocIndexOf;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet$1(key) {
  var data = this.__data__,
      index = assocIndexOf$2(data, key);

  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet$1;

var assocIndexOf$1 = _assocIndexOf;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas$1(key) {
  return assocIndexOf$1(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas$1;

var assocIndexOf = _assocIndexOf;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet$1(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

var _listCacheSet = listCacheSet$1;

var listCacheClear = _listCacheClear,
    listCacheDelete = _listCacheDelete,
    listCacheGet = _listCacheGet,
    listCacheHas = _listCacheHas,
    listCacheSet = _listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache$4(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache$4.prototype.clear = listCacheClear;
ListCache$4.prototype['delete'] = listCacheDelete;
ListCache$4.prototype.get = listCacheGet;
ListCache$4.prototype.has = listCacheHas;
ListCache$4.prototype.set = listCacheSet;

var _ListCache = ListCache$4;

var ListCache$3 = _ListCache;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear$1() {
  this.__data__ = new ListCache$3;
  this.size = 0;
}

var _stackClear = stackClear$1;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function stackDelete$1(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete$1;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */

function stackGet$1(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet$1;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function stackHas$1(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas$1;

/** Detect free variable `global` from Node.js. */

var freeGlobal$1 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal$1;

var freeGlobal = _freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root$8 = freeGlobal || freeSelf || Function('return this')();

var _root = root$8;

var root$7 = _root;

/** Built-in value references. */
var Symbol$7 = root$7.Symbol;

var _Symbol = Symbol$7;

var Symbol$6 = _Symbol;

/** Used for built-in method references. */
var objectProto$g = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$d = objectProto$g.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$g.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol$6 ? Symbol$6.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag$1(value) {
  var isOwn = hasOwnProperty$d.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

var _getRawTag = getRawTag$1;

/** Used for built-in method references. */

var objectProto$f = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto$f.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString$1(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString$1;

var Symbol$5 = _Symbol,
    getRawTag = _getRawTag,
    objectToString = _objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = Symbol$5 ? Symbol$5.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag$8(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

var _baseGetTag = baseGetTag$8;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */

function isObject$a(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject$a;

var baseGetTag$7 = _baseGetTag,
    isObject$9 = isObject_1;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag$2 = '[object Function]',
    genTag$1 = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction$3(value) {
  if (!isObject$9(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = baseGetTag$7(value);
  return tag == funcTag$2 || tag == genTag$1 || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction$3;

var root$6 = _root;

/** Used to detect overreaching core-js shims. */
var coreJsData$1 = root$6['__core-js_shared__'];

var _coreJsData = coreJsData$1;

var coreJsData = _coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked$1(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

var _isMasked = isMasked$1;

/** Used for built-in method references. */

var funcProto$2 = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$2 = funcProto$2.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource$2(func) {
  if (func != null) {
    try {
      return funcToString$2.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

var _toSource = toSource$2;

var isFunction$2 = isFunction_1,
    isMasked = _isMasked,
    isObject$8 = isObject_1,
    toSource$1 = _toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
    objectProto$e = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$c = objectProto$e.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString$1.call(hasOwnProperty$c).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative$1(value) {
  if (!isObject$8(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction$2(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource$1(value));
}

var _baseIsNative = baseIsNative$1;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */

function getValue$1(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue$1;

var baseIsNative = _baseIsNative,
    getValue = _getValue;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative$7(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

var _getNative = getNative$7;

var getNative$6 = _getNative,
    root$5 = _root;

/* Built-in method references that are verified to be native. */
var Map$4 = getNative$6(root$5, 'Map');

var _Map = Map$4;

var getNative$5 = _getNative;

/* Built-in method references that are verified to be native. */
var nativeCreate$4 = getNative$5(Object, 'create');

var _nativeCreate = nativeCreate$4;

var nativeCreate$3 = _nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear$1() {
  this.__data__ = nativeCreate$3 ? nativeCreate$3(null) : {};
  this.size = 0;
}

var _hashClear = hashClear$1;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function hashDelete$1(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete$1;

var nativeCreate$2 = _nativeCreate;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$d = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$b = objectProto$d.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet$1(key) {
  var data = this.__data__;
  if (nativeCreate$2) {
    var result = data[key];
    return result === HASH_UNDEFINED$2 ? undefined : result;
  }
  return hasOwnProperty$b.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet$1;

var nativeCreate$1 = _nativeCreate;

/** Used for built-in method references. */
var objectProto$c = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$a = objectProto$c.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas$1(key) {
  var data = this.__data__;
  return nativeCreate$1 ? (data[key] !== undefined) : hasOwnProperty$a.call(data, key);
}

var _hashHas = hashHas$1;

var nativeCreate = _nativeCreate;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet$1(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet$1;

var hashClear = _hashClear,
    hashDelete = _hashDelete,
    hashGet = _hashGet,
    hashHas = _hashHas,
    hashSet = _hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash$1(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash$1.prototype.clear = hashClear;
Hash$1.prototype['delete'] = hashDelete;
Hash$1.prototype.get = hashGet;
Hash$1.prototype.has = hashHas;
Hash$1.prototype.set = hashSet;

var _Hash = Hash$1;

var Hash = _Hash,
    ListCache$2 = _ListCache,
    Map$3 = _Map;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear$1() {
  this.size = 0;
  this.__data__ = {
    'hash': new Hash,
    'map': new (Map$3 || ListCache$2),
    'string': new Hash
  };
}

var _mapCacheClear = mapCacheClear$1;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */

function isKeyable$1(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

var _isKeyable = isKeyable$1;

var isKeyable = _isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData$4(map, key) {
  var data = map.__data__;
  return isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

var _getMapData = getMapData$4;

var getMapData$3 = _getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete$1(key) {
  var result = getMapData$3(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete$1;

var getMapData$2 = _getMapData;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet$1(key) {
  return getMapData$2(this, key).get(key);
}

var _mapCacheGet = mapCacheGet$1;

var getMapData$1 = _getMapData;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas$1(key) {
  return getMapData$1(this, key).has(key);
}

var _mapCacheHas = mapCacheHas$1;

var getMapData = _getMapData;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet$1(key, value) {
  var data = getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet$1;

var mapCacheClear = _mapCacheClear,
    mapCacheDelete = _mapCacheDelete,
    mapCacheGet = _mapCacheGet,
    mapCacheHas = _mapCacheHas,
    mapCacheSet = _mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache$3(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache$3.prototype.clear = mapCacheClear;
MapCache$3.prototype['delete'] = mapCacheDelete;
MapCache$3.prototype.get = mapCacheGet;
MapCache$3.prototype.has = mapCacheHas;
MapCache$3.prototype.set = mapCacheSet;

var _MapCache = MapCache$3;

var ListCache$1 = _ListCache,
    Map$2 = _Map,
    MapCache$2 = _MapCache;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE$1 = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet$1(key, value) {
  var data = this.__data__;
  if (data instanceof ListCache$1) {
    var pairs = data.__data__;
    if (!Map$2 || (pairs.length < LARGE_ARRAY_SIZE$1 - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new MapCache$2(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet$1;

var ListCache = _ListCache,
    stackClear = _stackClear,
    stackDelete = _stackDelete,
    stackGet = _stackGet,
    stackHas = _stackHas,
    stackSet = _stackSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack$4(entries) {
  var data = this.__data__ = new ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack$4.prototype.clear = stackClear;
Stack$4.prototype['delete'] = stackDelete;
Stack$4.prototype.get = stackGet;
Stack$4.prototype.has = stackHas;
Stack$4.prototype.set = stackSet;

var _Stack = Stack$4;

var getNative$4 = _getNative;

var defineProperty$2 = (function() {
  try {
    var func = getNative$4(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

var _defineProperty = defineProperty$2;

var defineProperty$1 = _defineProperty;

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue$3(object, key, value) {
  if (key == '__proto__' && defineProperty$1) {
    defineProperty$1(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue$3;

var baseAssignValue$2 = _baseAssignValue,
    eq$3 = eq_1;

/**
 * This function is like `assignValue` except that it doesn't assign
 * `undefined` values.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignMergeValue$2(object, key, value) {
  if ((value !== undefined && !eq$3(object[key], value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue$2(object, key, value);
  }
}

var _assignMergeValue = assignMergeValue$2;

/**
 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */

function createBaseFor$1(fromRight) {
  return function(object, iteratee, keysFunc) {
    var index = -1,
        iterable = Object(object),
        props = keysFunc(object),
        length = props.length;

    while (length--) {
      var key = props[fromRight ? length : ++index];
      if (iteratee(iterable[key], key, iterable) === false) {
        break;
      }
    }
    return object;
  };
}

var _createBaseFor = createBaseFor$1;

var createBaseFor = _createBaseFor;

/**
 * The base implementation of `baseForOwn` which iterates over `object`
 * properties returned by `keysFunc` and invokes `iteratee` for each property.
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @returns {Object} Returns `object`.
 */
var baseFor$2 = createBaseFor();

var _baseFor = baseFor$2;

var _cloneBufferExports = {};
var _cloneBuffer = {
  get exports(){ return _cloneBufferExports; },
  set exports(v){ _cloneBufferExports = v; },
};

(function (module, exports) {
	var root = _root;

	/** Detect free variable `exports`. */
	var freeExports = exports && !exports.nodeType && exports;

	/** Detect free variable `module`. */
	var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

	/** Detect the popular CommonJS extension `module.exports`. */
	var moduleExports = freeModule && freeModule.exports === freeExports;

	/** Built-in value references. */
	var Buffer = moduleExports ? root.Buffer : undefined,
	    allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined;

	/**
	 * Creates a clone of  `buffer`.
	 *
	 * @private
	 * @param {Buffer} buffer The buffer to clone.
	 * @param {boolean} [isDeep] Specify a deep clone.
	 * @returns {Buffer} Returns the cloned buffer.
	 */
	function cloneBuffer(buffer, isDeep) {
	  if (isDeep) {
	    return buffer.slice();
	  }
	  var length = buffer.length,
	      result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

	  buffer.copy(result);
	  return result;
	}

	module.exports = cloneBuffer;
} (_cloneBuffer, _cloneBufferExports));

var root$4 = _root;

/** Built-in value references. */
var Uint8Array$3 = root$4.Uint8Array;

var _Uint8Array = Uint8Array$3;

var Uint8Array$2 = _Uint8Array;

/**
 * Creates a clone of `arrayBuffer`.
 *
 * @private
 * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
 * @returns {ArrayBuffer} Returns the cloned array buffer.
 */
function cloneArrayBuffer$3(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new Uint8Array$2(result).set(new Uint8Array$2(arrayBuffer));
  return result;
}

var _cloneArrayBuffer = cloneArrayBuffer$3;

var cloneArrayBuffer$2 = _cloneArrayBuffer;

/**
 * Creates a clone of `typedArray`.
 *
 * @private
 * @param {Object} typedArray The typed array to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned typed array.
 */
function cloneTypedArray$2(typedArray, isDeep) {
  var buffer = isDeep ? cloneArrayBuffer$2(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

var _cloneTypedArray = cloneTypedArray$2;

/**
 * Copies the values of `source` to `array`.
 *
 * @private
 * @param {Array} source The array to copy values from.
 * @param {Array} [array=[]] The array to copy values to.
 * @returns {Array} Returns `array`.
 */

function copyArray$2(source, array) {
  var index = -1,
      length = source.length;

  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}

var _copyArray = copyArray$2;

var isObject$7 = isObject_1;

/** Built-in value references. */
var objectCreate = Object.create;

/**
 * The base implementation of `_.create` without support for assigning
 * properties to the created object.
 *
 * @private
 * @param {Object} proto The object to inherit from.
 * @returns {Object} Returns the new object.
 */
var baseCreate$1 = (function() {
  function object() {}
  return function(proto) {
    if (!isObject$7(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object;
    object.prototype = undefined;
    return result;
  };
}());

var _baseCreate = baseCreate$1;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */

function overArg$2(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg$2;

var overArg$1 = _overArg;

/** Built-in value references. */
var getPrototype$3 = overArg$1(Object.getPrototypeOf, Object);

var _getPrototype = getPrototype$3;

/** Used for built-in method references. */

var objectProto$b = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype$3(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$b;

  return value === proto;
}

var _isPrototype = isPrototype$3;

var baseCreate = _baseCreate,
    getPrototype$2 = _getPrototype,
    isPrototype$2 = _isPrototype;

/**
 * Initializes an object clone.
 *
 * @private
 * @param {Object} object The object to clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneObject$2(object) {
  return (typeof object.constructor == 'function' && !isPrototype$2(object))
    ? baseCreate(getPrototype$2(object))
    : {};
}

var _initCloneObject = initCloneObject$2;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike$b(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike$b;

var baseGetTag$6 = _baseGetTag,
    isObjectLike$a = isObjectLike_1;

/** `Object#toString` result references. */
var argsTag$3 = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments$1(value) {
  return isObjectLike$a(value) && baseGetTag$6(value) == argsTag$3;
}

var _baseIsArguments = baseIsArguments$1;

var baseIsArguments = _baseIsArguments,
    isObjectLike$9 = isObjectLike_1;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$9 = objectProto$a.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$a.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments$4 = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
  return isObjectLike$9(value) && hasOwnProperty$9.call(value, 'callee') &&
    !propertyIsEnumerable$1.call(value, 'callee');
};

var isArguments_1 = isArguments$4;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */

var isArray$e = Array.isArray;

var isArray_1 = isArray$e;

/** Used as references for various `Number` constants. */

var MAX_SAFE_INTEGER$1 = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength$3(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}

var isLength_1 = isLength$3;

var isFunction$1 = isFunction_1,
    isLength$2 = isLength_1;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike$6(value) {
  return value != null && isLength$2(value.length) && !isFunction$1(value);
}

var isArrayLike_1 = isArrayLike$6;

var isArrayLike$5 = isArrayLike_1,
    isObjectLike$8 = isObjectLike_1;

/**
 * This method is like `_.isArrayLike` except that it also checks if `value`
 * is an object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array-like object,
 *  else `false`.
 * @example
 *
 * _.isArrayLikeObject([1, 2, 3]);
 * // => true
 *
 * _.isArrayLikeObject(document.body.children);
 * // => true
 *
 * _.isArrayLikeObject('abc');
 * // => false
 *
 * _.isArrayLikeObject(_.noop);
 * // => false
 */
function isArrayLikeObject$2(value) {
  return isObjectLike$8(value) && isArrayLike$5(value);
}

var isArrayLikeObject_1 = isArrayLikeObject$2;

var isBufferExports = {};
var isBuffer$4 = {
  get exports(){ return isBufferExports; },
  set exports(v){ isBufferExports = v; },
};

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */

function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

(function (module, exports) {
	var root = _root,
	    stubFalse = stubFalse_1;

	/** Detect free variable `exports`. */
	var freeExports = exports && !exports.nodeType && exports;

	/** Detect free variable `module`. */
	var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

	/** Detect the popular CommonJS extension `module.exports`. */
	var moduleExports = freeModule && freeModule.exports === freeExports;

	/** Built-in value references. */
	var Buffer = moduleExports ? root.Buffer : undefined;

	/* Built-in method references for those with the same name as other `lodash` methods. */
	var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

	/**
	 * Checks if `value` is a buffer.
	 *
	 * @static
	 * @memberOf _
	 * @since 4.3.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
	 * @example
	 *
	 * _.isBuffer(new Buffer(2));
	 * // => true
	 *
	 * _.isBuffer(new Uint8Array(2));
	 * // => false
	 */
	var isBuffer = nativeIsBuffer || stubFalse;

	module.exports = isBuffer;
} (isBuffer$4, isBufferExports));

var baseGetTag$5 = _baseGetTag,
    getPrototype$1 = _getPrototype,
    isObjectLike$7 = isObjectLike_1;

/** `Object#toString` result references. */
var objectTag$4 = '[object Object]';

/** Used for built-in method references. */
var funcProto = Function.prototype,
    objectProto$9 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$9.hasOwnProperty;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString.call(Object);

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject$2(value) {
  if (!isObjectLike$7(value) || baseGetTag$5(value) != objectTag$4) {
    return false;
  }
  var proto = getPrototype$1(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty$8.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString.call(Ctor) == objectCtorString;
}

var isPlainObject_1 = isPlainObject$2;

var baseGetTag$4 = _baseGetTag,
    isLength$1 = isLength_1,
    isObjectLike$6 = isObjectLike_1;

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
    arrayTag$2 = '[object Array]',
    boolTag$3 = '[object Boolean]',
    dateTag$3 = '[object Date]',
    errorTag$2 = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag$5 = '[object Map]',
    numberTag$4 = '[object Number]',
    objectTag$3 = '[object Object]',
    regexpTag$3 = '[object RegExp]',
    setTag$5 = '[object Set]',
    stringTag$4 = '[object String]',
    weakMapTag$2 = '[object WeakMap]';

var arrayBufferTag$3 = '[object ArrayBuffer]',
    dataViewTag$4 = '[object DataView]',
    float32Tag$2 = '[object Float32Array]',
    float64Tag$2 = '[object Float64Array]',
    int8Tag$2 = '[object Int8Array]',
    int16Tag$2 = '[object Int16Array]',
    int32Tag$2 = '[object Int32Array]',
    uint8Tag$2 = '[object Uint8Array]',
    uint8ClampedTag$2 = '[object Uint8ClampedArray]',
    uint16Tag$2 = '[object Uint16Array]',
    uint32Tag$2 = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag$2] = typedArrayTags[float64Tag$2] =
typedArrayTags[int8Tag$2] = typedArrayTags[int16Tag$2] =
typedArrayTags[int32Tag$2] = typedArrayTags[uint8Tag$2] =
typedArrayTags[uint8ClampedTag$2] = typedArrayTags[uint16Tag$2] =
typedArrayTags[uint32Tag$2] = true;
typedArrayTags[argsTag$2] = typedArrayTags[arrayTag$2] =
typedArrayTags[arrayBufferTag$3] = typedArrayTags[boolTag$3] =
typedArrayTags[dataViewTag$4] = typedArrayTags[dateTag$3] =
typedArrayTags[errorTag$2] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag$5] = typedArrayTags[numberTag$4] =
typedArrayTags[objectTag$3] = typedArrayTags[regexpTag$3] =
typedArrayTags[setTag$5] = typedArrayTags[stringTag$4] =
typedArrayTags[weakMapTag$2] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray$1(value) {
  return isObjectLike$6(value) &&
    isLength$1(value.length) && !!typedArrayTags[baseGetTag$4(value)];
}

var _baseIsTypedArray = baseIsTypedArray$1;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */

function baseUnary$5(func) {
  return function(value) {
    return func(value);
  };
}

var _baseUnary = baseUnary$5;

var _nodeUtilExports = {};
var _nodeUtil = {
  get exports(){ return _nodeUtilExports; },
  set exports(v){ _nodeUtilExports = v; },
};

(function (module, exports) {
	var freeGlobal = _freeGlobal;

	/** Detect free variable `exports`. */
	var freeExports = exports && !exports.nodeType && exports;

	/** Detect free variable `module`. */
	var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

	/** Detect the popular CommonJS extension `module.exports`. */
	var moduleExports = freeModule && freeModule.exports === freeExports;

	/** Detect free variable `process` from Node.js. */
	var freeProcess = moduleExports && freeGlobal.process;

	/** Used to access faster Node.js helpers. */
	var nodeUtil = (function() {
	  try {
	    // Use `util.types` for Node.js 10+.
	    var types = freeModule && freeModule.require && freeModule.require('util').types;

	    if (types) {
	      return types;
	    }

	    // Legacy `process.binding('util')` for Node.js < 10.
	    return freeProcess && freeProcess.binding && freeProcess.binding('util');
	  } catch (e) {}
	}());

	module.exports = nodeUtil;
} (_nodeUtil, _nodeUtilExports));

var baseIsTypedArray = _baseIsTypedArray,
    baseUnary$4 = _baseUnary,
    nodeUtil$3 = _nodeUtilExports;

/* Node.js helper references. */
var nodeIsTypedArray = nodeUtil$3 && nodeUtil$3.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray$3 = nodeIsTypedArray ? baseUnary$4(nodeIsTypedArray) : baseIsTypedArray;

var isTypedArray_1 = isTypedArray$3;

/**
 * Gets the value at `key`, unless `key` is "__proto__" or "constructor".
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */

function safeGet$2(object, key) {
  if (key === 'constructor' && typeof object[key] === 'function') {
    return;
  }

  if (key == '__proto__') {
    return;
  }

  return object[key];
}

var _safeGet = safeGet$2;

var baseAssignValue$1 = _baseAssignValue,
    eq$2 = eq_1;

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$8.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue$3(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$7.call(object, key) && eq$2(objValue, value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue$1(object, key, value);
  }
}

var _assignValue = assignValue$3;

var assignValue$2 = _assignValue,
    baseAssignValue = _baseAssignValue;

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject$6(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer
      ? customizer(object[key], source[key], key, object, source)
      : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      baseAssignValue(object, key, newValue);
    } else {
      assignValue$2(object, key, newValue);
    }
  }
  return object;
}

var _copyObject = copyObject$6;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */

function baseTimes$1(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

var _baseTimes = baseTimes$1;

/** Used as references for various `Number` constants. */

var MAX_SAFE_INTEGER = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex$4(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

var _isIndex = isIndex$4;

var baseTimes = _baseTimes,
    isArguments$3 = isArguments_1,
    isArray$d = isArray_1,
    isBuffer$3 = isBufferExports,
    isIndex$3 = _isIndex,
    isTypedArray$2 = isTypedArray_1;

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$7.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys$2(value, inherited) {
  var isArr = isArray$d(value),
      isArg = !isArr && isArguments$3(value),
      isBuff = !isArr && !isArg && isBuffer$3(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray$2(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$6.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           isIndex$3(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

var _arrayLikeKeys = arrayLikeKeys$2;

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */

function nativeKeysIn$1(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}

var _nativeKeysIn = nativeKeysIn$1;

var isObject$6 = isObject_1,
    isPrototype$1 = _isPrototype,
    nativeKeysIn = _nativeKeysIn;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$6.hasOwnProperty;

/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn$1(object) {
  if (!isObject$6(object)) {
    return nativeKeysIn(object);
  }
  var isProto = isPrototype$1(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$5.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

var _baseKeysIn = baseKeysIn$1;

var arrayLikeKeys$1 = _arrayLikeKeys,
    baseKeysIn = _baseKeysIn,
    isArrayLike$4 = isArrayLike_1;

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn$5(object) {
  return isArrayLike$4(object) ? arrayLikeKeys$1(object, true) : baseKeysIn(object);
}

var keysIn_1 = keysIn$5;

var copyObject$5 = _copyObject,
    keysIn$4 = keysIn_1;

/**
 * Converts `value` to a plain object flattening inherited enumerable string
 * keyed properties of `value` to own properties of the plain object.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {Object} Returns the converted plain object.
 * @example
 *
 * function Foo() {
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.assign({ 'a': 1 }, new Foo);
 * // => { 'a': 1, 'b': 2 }
 *
 * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
 * // => { 'a': 1, 'b': 2, 'c': 3 }
 */
function toPlainObject$1(value) {
  return copyObject$5(value, keysIn$4(value));
}

var toPlainObject_1 = toPlainObject$1;

var assignMergeValue$1 = _assignMergeValue,
    cloneBuffer$1 = _cloneBufferExports,
    cloneTypedArray$1 = _cloneTypedArray,
    copyArray$1 = _copyArray,
    initCloneObject$1 = _initCloneObject,
    isArguments$2 = isArguments_1,
    isArray$c = isArray_1,
    isArrayLikeObject$1 = isArrayLikeObject_1,
    isBuffer$2 = isBufferExports,
    isFunction = isFunction_1,
    isObject$5 = isObject_1,
    isPlainObject$1 = isPlainObject_1,
    isTypedArray$1 = isTypedArray_1,
    safeGet$1 = _safeGet,
    toPlainObject = toPlainObject_1;

/**
 * A specialized version of `baseMerge` for arrays and objects which performs
 * deep merges and tracks traversed objects enabling objects with circular
 * references to be merged.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {string} key The key of the value to merge.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} mergeFunc The function to merge values.
 * @param {Function} [customizer] The function to customize assigned values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMergeDeep$1(object, source, key, srcIndex, mergeFunc, customizer, stack) {
  var objValue = safeGet$1(object, key),
      srcValue = safeGet$1(source, key),
      stacked = stack.get(srcValue);

  if (stacked) {
    assignMergeValue$1(object, key, stacked);
    return;
  }
  var newValue = customizer
    ? customizer(objValue, srcValue, (key + ''), object, source, stack)
    : undefined;

  var isCommon = newValue === undefined;

  if (isCommon) {
    var isArr = isArray$c(srcValue),
        isBuff = !isArr && isBuffer$2(srcValue),
        isTyped = !isArr && !isBuff && isTypedArray$1(srcValue);

    newValue = srcValue;
    if (isArr || isBuff || isTyped) {
      if (isArray$c(objValue)) {
        newValue = objValue;
      }
      else if (isArrayLikeObject$1(objValue)) {
        newValue = copyArray$1(objValue);
      }
      else if (isBuff) {
        isCommon = false;
        newValue = cloneBuffer$1(srcValue, true);
      }
      else if (isTyped) {
        isCommon = false;
        newValue = cloneTypedArray$1(srcValue, true);
      }
      else {
        newValue = [];
      }
    }
    else if (isPlainObject$1(srcValue) || isArguments$2(srcValue)) {
      newValue = objValue;
      if (isArguments$2(objValue)) {
        newValue = toPlainObject(objValue);
      }
      else if (!isObject$5(objValue) || isFunction(objValue)) {
        newValue = initCloneObject$1(srcValue);
      }
    }
    else {
      isCommon = false;
    }
  }
  if (isCommon) {
    // Recursively merge objects and arrays (susceptible to call stack limits).
    stack.set(srcValue, newValue);
    mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
    stack['delete'](srcValue);
  }
  assignMergeValue$1(object, key, newValue);
}

var _baseMergeDeep = baseMergeDeep$1;

var Stack$3 = _Stack,
    assignMergeValue = _assignMergeValue,
    baseFor$1 = _baseFor,
    baseMergeDeep = _baseMergeDeep,
    isObject$4 = isObject_1,
    keysIn$3 = keysIn_1,
    safeGet = _safeGet;

/**
 * The base implementation of `_.merge` without support for multiple sources.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} [customizer] The function to customize merged values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMerge$1(object, source, srcIndex, customizer, stack) {
  if (object === source) {
    return;
  }
  baseFor$1(source, function(srcValue, key) {
    stack || (stack = new Stack$3);
    if (isObject$4(srcValue)) {
      baseMergeDeep(object, source, key, srcIndex, baseMerge$1, customizer, stack);
    }
    else {
      var newValue = customizer
        ? customizer(safeGet(object, key), srcValue, (key + ''), object, source, stack)
        : undefined;

      if (newValue === undefined) {
        newValue = srcValue;
      }
      assignMergeValue(object, key, newValue);
    }
  }, keysIn$3);
}

var _baseMerge = baseMerge$1;

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */

function identity$7(value) {
  return value;
}

var identity_1 = identity$7;

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */

function apply$1(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

var _apply = apply$1;

var apply = _apply;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest$2(func, start, transform) {
  start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return apply(func, this, otherArgs);
  };
}

var _overRest = overRest$2;

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */

function constant$2(value) {
  return function() {
    return value;
  };
}

var constant_1 = constant$2;

var constant$1 = constant_1,
    defineProperty = _defineProperty,
    identity$6 = identity_1;

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString$1 = !defineProperty ? identity$6 : function(func, string) {
  return defineProperty(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant$1(string),
    'writable': true
  });
};

var _baseSetToString = baseSetToString$1;

/** Used to detect hot functions by number of calls within a span of milliseconds. */

var HOT_COUNT = 800,
    HOT_SPAN = 16;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeNow = Date.now;

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut$1(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

var _shortOut = shortOut$1;

var baseSetToString = _baseSetToString,
    shortOut = _shortOut;

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString$2 = shortOut(baseSetToString);

var _setToString = setToString$2;

var identity$5 = identity_1,
    overRest$1 = _overRest,
    setToString$1 = _setToString;

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest$3(func, start) {
  return setToString$1(overRest$1(func, start, identity$5), func + '');
}

var _baseRest = baseRest$3;

var eq$1 = eq_1,
    isArrayLike$3 = isArrayLike_1,
    isIndex$2 = _isIndex,
    isObject$3 = isObject_1;

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall$2(value, index, object) {
  if (!isObject$3(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike$3(object) && isIndex$2(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq$1(object[index], value);
  }
  return false;
}

var _isIterateeCall = isIterateeCall$2;

var baseRest$2 = _baseRest,
    isIterateeCall$1 = _isIterateeCall;

/**
 * Creates a function like `_.assign`.
 *
 * @private
 * @param {Function} assigner The function to assign values.
 * @returns {Function} Returns the new assigner function.
 */
function createAssigner$1(assigner) {
  return baseRest$2(function(object, sources) {
    var index = -1,
        length = sources.length,
        customizer = length > 1 ? sources[length - 1] : undefined,
        guard = length > 2 ? sources[2] : undefined;

    customizer = (assigner.length > 3 && typeof customizer == 'function')
      ? (length--, customizer)
      : undefined;

    if (guard && isIterateeCall$1(sources[0], sources[1], guard)) {
      customizer = length < 3 ? undefined : customizer;
      length = 1;
    }
    object = Object(object);
    while (++index < length) {
      var source = sources[index];
      if (source) {
        assigner(object, source, index, customizer);
      }
    }
    return object;
  });
}

var _createAssigner = createAssigner$1;

var baseMerge = _baseMerge,
    createAssigner = _createAssigner;

/**
 * This method is like `_.assign` except that it recursively merges own and
 * inherited enumerable string keyed properties of source objects into the
 * destination object. Source properties that resolve to `undefined` are
 * skipped if a destination value exists. Array and plain object properties
 * are merged recursively. Other objects and value types are overridden by
 * assignment. Source objects are applied from left to right. Subsequent
 * sources overwrite property assignments of previous sources.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 0.5.0
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} [sources] The source objects.
 * @returns {Object} Returns `object`.
 * @example
 *
 * var object = {
 *   'a': [{ 'b': 2 }, { 'd': 4 }]
 * };
 *
 * var other = {
 *   'a': [{ 'c': 3 }, { 'e': 5 }]
 * };
 *
 * _.merge(object, other);
 * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
 */
var merge = createAssigner(function(object, source, srcIndex) {
  baseMerge(object, source, srcIndex);
});

var merge_1 = merge;

var baseGetTag$3 = _baseGetTag,
    isObjectLike$5 = isObjectLike_1;

/** `Object#toString` result references. */
var symbolTag$3 = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol$4(value) {
  return typeof value == 'symbol' ||
    (isObjectLike$5(value) && baseGetTag$3(value) == symbolTag$3);
}

var isSymbol_1 = isSymbol$4;

var isArray$b = isArray_1,
    isSymbol$3 = isSymbol_1;

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey$3(value, object) {
  if (isArray$b(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol$3(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

var _isKey = isKey$3;

var MapCache$1 = _MapCache;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize$1(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize$1.Cache || MapCache$1);
  return memoized;
}

// Expose `MapCache`.
memoize$1.Cache = MapCache$1;

var memoize_1 = memoize$1;

var memoize = memoize_1;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped$1(func) {
  var result = memoize(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

var _memoizeCapped = memoizeCapped$1;

var memoizeCapped = _memoizeCapped;

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath$1 = memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

var _stringToPath = stringToPath$1;

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */

function arrayMap$4(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

var _arrayMap = arrayMap$4;

var Symbol$4 = _Symbol,
    arrayMap$3 = _arrayMap,
    isArray$a = isArray_1,
    isSymbol$2 = isSymbol_1;

/** Used as references for various `Number` constants. */
var INFINITY$2 = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto$2 = Symbol$4 ? Symbol$4.prototype : undefined,
    symbolToString = symbolProto$2 ? symbolProto$2.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString$1(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray$a(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return arrayMap$3(value, baseToString$1) + '';
  }
  if (isSymbol$2(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$2) ? '-0' : result;
}

var _baseToString = baseToString$1;

var baseToString = _baseToString;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString$1(value) {
  return value == null ? '' : baseToString(value);
}

var toString_1 = toString$1;

var isArray$9 = isArray_1,
    isKey$2 = _isKey,
    stringToPath = _stringToPath,
    toString = toString_1;

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath$6(value, object) {
  if (isArray$9(value)) {
    return value;
  }
  return isKey$2(value, object) ? [value] : stringToPath(toString(value));
}

var _castPath = castPath$6;

var isSymbol$1 = isSymbol_1;

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey$6(value) {
  if (typeof value == 'string' || isSymbol$1(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

var _toKey = toKey$6;

var castPath$5 = _castPath,
    toKey$5 = _toKey;

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet$5(object, path) {
  path = castPath$5(path, object);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[toKey$5(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

var _baseGet = baseGet$5;

var baseGet$4 = _baseGet;

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get$1(object, path, defaultValue) {
  var result = object == null ? undefined : baseGet$4(object, path);
  return result === undefined ? defaultValue : result;
}

var get_1 = get$1;

var assignValue$1 = _assignValue,
    castPath$4 = _castPath,
    isIndex$1 = _isIndex,
    isObject$2 = isObject_1,
    toKey$4 = _toKey;

/**
 * The base implementation of `_.set`.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @param {Function} [customizer] The function to customize path creation.
 * @returns {Object} Returns `object`.
 */
function baseSet$2(object, path, value, customizer) {
  if (!isObject$2(object)) {
    return object;
  }
  path = castPath$4(path, object);

  var index = -1,
      length = path.length,
      lastIndex = length - 1,
      nested = object;

  while (nested != null && ++index < length) {
    var key = toKey$4(path[index]),
        newValue = value;

    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      return object;
    }

    if (index != lastIndex) {
      var objValue = nested[key];
      newValue = customizer ? customizer(objValue, key, nested) : undefined;
      if (newValue === undefined) {
        newValue = isObject$2(objValue)
          ? objValue
          : (isIndex$1(path[index + 1]) ? [] : {});
      }
    }
    assignValue$1(nested, key, newValue);
    nested = nested[key];
  }
  return object;
}

var _baseSet = baseSet$2;

var baseSet$1 = _baseSet;

/**
 * Sets the value at `path` of `object`. If a portion of `path` doesn't exist,
 * it's created. Arrays are created for missing index properties while objects
 * are created for all other missing properties. Use `_.setWith` to customize
 * `path` creation.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns `object`.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.set(object, 'a[0].b.c', 4);
 * console.log(object.a[0].b.c);
 * // => 4
 *
 * _.set(object, ['x', '0', 'y', 'z'], 5);
 * console.log(object.x[0].y.z);
 * // => 5
 */
function set(object, path, value) {
  return object == null ? object : baseSet$1(object, path, value);
}

var set_1 = set;

function define(constructor, factory, prototype) {
  constructor.prototype = factory.prototype = prototype;
  prototype.constructor = constructor;
}

function extend(parent, definition) {
  var prototype = Object.create(parent.prototype);
  for (var key in definition) prototype[key] = definition[key];
  return prototype;
}

function Color() {}

var darker = 0.7;
var brighter = 1 / darker;

var reI = "\\s*([+-]?\\d+)\\s*",
    reN = "\\s*([+-]?\\d*\\.?\\d+(?:[eE][+-]?\\d+)?)\\s*",
    reP = "\\s*([+-]?\\d*\\.?\\d+(?:[eE][+-]?\\d+)?)%\\s*",
    reHex = /^#([0-9a-f]{3,8})$/,
    reRgbInteger = new RegExp("^rgb\\(" + [reI, reI, reI] + "\\)$"),
    reRgbPercent = new RegExp("^rgb\\(" + [reP, reP, reP] + "\\)$"),
    reRgbaInteger = new RegExp("^rgba\\(" + [reI, reI, reI, reN] + "\\)$"),
    reRgbaPercent = new RegExp("^rgba\\(" + [reP, reP, reP, reN] + "\\)$"),
    reHslPercent = new RegExp("^hsl\\(" + [reN, reP, reP] + "\\)$"),
    reHslaPercent = new RegExp("^hsla\\(" + [reN, reP, reP, reN] + "\\)$");

var named = {
  aliceblue: 0xf0f8ff,
  antiquewhite: 0xfaebd7,
  aqua: 0x00ffff,
  aquamarine: 0x7fffd4,
  azure: 0xf0ffff,
  beige: 0xf5f5dc,
  bisque: 0xffe4c4,
  black: 0x000000,
  blanchedalmond: 0xffebcd,
  blue: 0x0000ff,
  blueviolet: 0x8a2be2,
  brown: 0xa52a2a,
  burlywood: 0xdeb887,
  cadetblue: 0x5f9ea0,
  chartreuse: 0x7fff00,
  chocolate: 0xd2691e,
  coral: 0xff7f50,
  cornflowerblue: 0x6495ed,
  cornsilk: 0xfff8dc,
  crimson: 0xdc143c,
  cyan: 0x00ffff,
  darkblue: 0x00008b,
  darkcyan: 0x008b8b,
  darkgoldenrod: 0xb8860b,
  darkgray: 0xa9a9a9,
  darkgreen: 0x006400,
  darkgrey: 0xa9a9a9,
  darkkhaki: 0xbdb76b,
  darkmagenta: 0x8b008b,
  darkolivegreen: 0x556b2f,
  darkorange: 0xff8c00,
  darkorchid: 0x9932cc,
  darkred: 0x8b0000,
  darksalmon: 0xe9967a,
  darkseagreen: 0x8fbc8f,
  darkslateblue: 0x483d8b,
  darkslategray: 0x2f4f4f,
  darkslategrey: 0x2f4f4f,
  darkturquoise: 0x00ced1,
  darkviolet: 0x9400d3,
  deeppink: 0xff1493,
  deepskyblue: 0x00bfff,
  dimgray: 0x696969,
  dimgrey: 0x696969,
  dodgerblue: 0x1e90ff,
  firebrick: 0xb22222,
  floralwhite: 0xfffaf0,
  forestgreen: 0x228b22,
  fuchsia: 0xff00ff,
  gainsboro: 0xdcdcdc,
  ghostwhite: 0xf8f8ff,
  gold: 0xffd700,
  goldenrod: 0xdaa520,
  gray: 0x808080,
  green: 0x008000,
  greenyellow: 0xadff2f,
  grey: 0x808080,
  honeydew: 0xf0fff0,
  hotpink: 0xff69b4,
  indianred: 0xcd5c5c,
  indigo: 0x4b0082,
  ivory: 0xfffff0,
  khaki: 0xf0e68c,
  lavender: 0xe6e6fa,
  lavenderblush: 0xfff0f5,
  lawngreen: 0x7cfc00,
  lemonchiffon: 0xfffacd,
  lightblue: 0xadd8e6,
  lightcoral: 0xf08080,
  lightcyan: 0xe0ffff,
  lightgoldenrodyellow: 0xfafad2,
  lightgray: 0xd3d3d3,
  lightgreen: 0x90ee90,
  lightgrey: 0xd3d3d3,
  lightpink: 0xffb6c1,
  lightsalmon: 0xffa07a,
  lightseagreen: 0x20b2aa,
  lightskyblue: 0x87cefa,
  lightslategray: 0x778899,
  lightslategrey: 0x778899,
  lightsteelblue: 0xb0c4de,
  lightyellow: 0xffffe0,
  lime: 0x00ff00,
  limegreen: 0x32cd32,
  linen: 0xfaf0e6,
  magenta: 0xff00ff,
  maroon: 0x800000,
  mediumaquamarine: 0x66cdaa,
  mediumblue: 0x0000cd,
  mediumorchid: 0xba55d3,
  mediumpurple: 0x9370db,
  mediumseagreen: 0x3cb371,
  mediumslateblue: 0x7b68ee,
  mediumspringgreen: 0x00fa9a,
  mediumturquoise: 0x48d1cc,
  mediumvioletred: 0xc71585,
  midnightblue: 0x191970,
  mintcream: 0xf5fffa,
  mistyrose: 0xffe4e1,
  moccasin: 0xffe4b5,
  navajowhite: 0xffdead,
  navy: 0x000080,
  oldlace: 0xfdf5e6,
  olive: 0x808000,
  olivedrab: 0x6b8e23,
  orange: 0xffa500,
  orangered: 0xff4500,
  orchid: 0xda70d6,
  palegoldenrod: 0xeee8aa,
  palegreen: 0x98fb98,
  paleturquoise: 0xafeeee,
  palevioletred: 0xdb7093,
  papayawhip: 0xffefd5,
  peachpuff: 0xffdab9,
  peru: 0xcd853f,
  pink: 0xffc0cb,
  plum: 0xdda0dd,
  powderblue: 0xb0e0e6,
  purple: 0x800080,
  rebeccapurple: 0x663399,
  red: 0xff0000,
  rosybrown: 0xbc8f8f,
  royalblue: 0x4169e1,
  saddlebrown: 0x8b4513,
  salmon: 0xfa8072,
  sandybrown: 0xf4a460,
  seagreen: 0x2e8b57,
  seashell: 0xfff5ee,
  sienna: 0xa0522d,
  silver: 0xc0c0c0,
  skyblue: 0x87ceeb,
  slateblue: 0x6a5acd,
  slategray: 0x708090,
  slategrey: 0x708090,
  snow: 0xfffafa,
  springgreen: 0x00ff7f,
  steelblue: 0x4682b4,
  tan: 0xd2b48c,
  teal: 0x008080,
  thistle: 0xd8bfd8,
  tomato: 0xff6347,
  turquoise: 0x40e0d0,
  violet: 0xee82ee,
  wheat: 0xf5deb3,
  white: 0xffffff,
  whitesmoke: 0xf5f5f5,
  yellow: 0xffff00,
  yellowgreen: 0x9acd32
};

define(Color, color, {
  copy: function(channels) {
    return Object.assign(new this.constructor, this, channels);
  },
  displayable: function() {
    return this.rgb().displayable();
  },
  hex: color_formatHex, // Deprecated! Use color.formatHex.
  formatHex: color_formatHex,
  formatHsl: color_formatHsl,
  formatRgb: color_formatRgb,
  toString: color_formatRgb
});

function color_formatHex() {
  return this.rgb().formatHex();
}

function color_formatHsl() {
  return hslConvert(this).formatHsl();
}

function color_formatRgb() {
  return this.rgb().formatRgb();
}

function color(format) {
  var m, l;
  format = (format + "").trim().toLowerCase();
  return (m = reHex.exec(format)) ? (l = m[1].length, m = parseInt(m[1], 16), l === 6 ? rgbn(m) // #ff0000
      : l === 3 ? new Rgb((m >> 8 & 0xf) | (m >> 4 & 0xf0), (m >> 4 & 0xf) | (m & 0xf0), ((m & 0xf) << 4) | (m & 0xf), 1) // #f00
      : l === 8 ? rgba(m >> 24 & 0xff, m >> 16 & 0xff, m >> 8 & 0xff, (m & 0xff) / 0xff) // #ff000000
      : l === 4 ? rgba((m >> 12 & 0xf) | (m >> 8 & 0xf0), (m >> 8 & 0xf) | (m >> 4 & 0xf0), (m >> 4 & 0xf) | (m & 0xf0), (((m & 0xf) << 4) | (m & 0xf)) / 0xff) // #f000
      : null) // invalid hex
      : (m = reRgbInteger.exec(format)) ? new Rgb(m[1], m[2], m[3], 1) // rgb(255, 0, 0)
      : (m = reRgbPercent.exec(format)) ? new Rgb(m[1] * 255 / 100, m[2] * 255 / 100, m[3] * 255 / 100, 1) // rgb(100%, 0%, 0%)
      : (m = reRgbaInteger.exec(format)) ? rgba(m[1], m[2], m[3], m[4]) // rgba(255, 0, 0, 1)
      : (m = reRgbaPercent.exec(format)) ? rgba(m[1] * 255 / 100, m[2] * 255 / 100, m[3] * 255 / 100, m[4]) // rgb(100%, 0%, 0%, 1)
      : (m = reHslPercent.exec(format)) ? hsla(m[1], m[2] / 100, m[3] / 100, 1) // hsl(120, 50%, 50%)
      : (m = reHslaPercent.exec(format)) ? hsla(m[1], m[2] / 100, m[3] / 100, m[4]) // hsla(120, 50%, 50%, 1)
      : named.hasOwnProperty(format) ? rgbn(named[format]) // eslint-disable-line no-prototype-builtins
      : format === "transparent" ? new Rgb(NaN, NaN, NaN, 0)
      : null;
}

function rgbn(n) {
  return new Rgb(n >> 16 & 0xff, n >> 8 & 0xff, n & 0xff, 1);
}

function rgba(r, g, b, a) {
  if (a <= 0) r = g = b = NaN;
  return new Rgb(r, g, b, a);
}

function rgbConvert(o) {
  if (!(o instanceof Color)) o = color(o);
  if (!o) return new Rgb;
  o = o.rgb();
  return new Rgb(o.r, o.g, o.b, o.opacity);
}

function rgb$1(r, g, b, opacity) {
  return arguments.length === 1 ? rgbConvert(r) : new Rgb(r, g, b, opacity == null ? 1 : opacity);
}

function Rgb(r, g, b, opacity) {
  this.r = +r;
  this.g = +g;
  this.b = +b;
  this.opacity = +opacity;
}

define(Rgb, rgb$1, extend(Color, {
  brighter: function(k) {
    k = k == null ? brighter : Math.pow(brighter, k);
    return new Rgb(this.r * k, this.g * k, this.b * k, this.opacity);
  },
  darker: function(k) {
    k = k == null ? darker : Math.pow(darker, k);
    return new Rgb(this.r * k, this.g * k, this.b * k, this.opacity);
  },
  rgb: function() {
    return this;
  },
  displayable: function() {
    return (-0.5 <= this.r && this.r < 255.5)
        && (-0.5 <= this.g && this.g < 255.5)
        && (-0.5 <= this.b && this.b < 255.5)
        && (0 <= this.opacity && this.opacity <= 1);
  },
  hex: rgb_formatHex, // Deprecated! Use color.formatHex.
  formatHex: rgb_formatHex,
  formatRgb: rgb_formatRgb,
  toString: rgb_formatRgb
}));

function rgb_formatHex() {
  return "#" + hex(this.r) + hex(this.g) + hex(this.b);
}

function rgb_formatRgb() {
  var a = this.opacity; a = isNaN(a) ? 1 : Math.max(0, Math.min(1, a));
  return (a === 1 ? "rgb(" : "rgba(")
      + Math.max(0, Math.min(255, Math.round(this.r) || 0)) + ", "
      + Math.max(0, Math.min(255, Math.round(this.g) || 0)) + ", "
      + Math.max(0, Math.min(255, Math.round(this.b) || 0))
      + (a === 1 ? ")" : ", " + a + ")");
}

function hex(value) {
  value = Math.max(0, Math.min(255, Math.round(value) || 0));
  return (value < 16 ? "0" : "") + value.toString(16);
}

function hsla(h, s, l, a) {
  if (a <= 0) h = s = l = NaN;
  else if (l <= 0 || l >= 1) h = s = NaN;
  else if (s <= 0) h = NaN;
  return new Hsl(h, s, l, a);
}

function hslConvert(o) {
  if (o instanceof Hsl) return new Hsl(o.h, o.s, o.l, o.opacity);
  if (!(o instanceof Color)) o = color(o);
  if (!o) return new Hsl;
  if (o instanceof Hsl) return o;
  o = o.rgb();
  var r = o.r / 255,
      g = o.g / 255,
      b = o.b / 255,
      min = Math.min(r, g, b),
      max = Math.max(r, g, b),
      h = NaN,
      s = max - min,
      l = (max + min) / 2;
  if (s) {
    if (r === max) h = (g - b) / s + (g < b) * 6;
    else if (g === max) h = (b - r) / s + 2;
    else h = (r - g) / s + 4;
    s /= l < 0.5 ? max + min : 2 - max - min;
    h *= 60;
  } else {
    s = l > 0 && l < 1 ? 0 : h;
  }
  return new Hsl(h, s, l, o.opacity);
}

function hsl(h, s, l, opacity) {
  return arguments.length === 1 ? hslConvert(h) : new Hsl(h, s, l, opacity == null ? 1 : opacity);
}

function Hsl(h, s, l, opacity) {
  this.h = +h;
  this.s = +s;
  this.l = +l;
  this.opacity = +opacity;
}

define(Hsl, hsl, extend(Color, {
  brighter: function(k) {
    k = k == null ? brighter : Math.pow(brighter, k);
    return new Hsl(this.h, this.s, this.l * k, this.opacity);
  },
  darker: function(k) {
    k = k == null ? darker : Math.pow(darker, k);
    return new Hsl(this.h, this.s, this.l * k, this.opacity);
  },
  rgb: function() {
    var h = this.h % 360 + (this.h < 0) * 360,
        s = isNaN(h) || isNaN(this.s) ? 0 : this.s,
        l = this.l,
        m2 = l + (l < 0.5 ? l : 1 - l) * s,
        m1 = 2 * l - m2;
    return new Rgb(
      hsl2rgb(h >= 240 ? h - 240 : h + 120, m1, m2),
      hsl2rgb(h, m1, m2),
      hsl2rgb(h < 120 ? h + 240 : h - 120, m1, m2),
      this.opacity
    );
  },
  displayable: function() {
    return (0 <= this.s && this.s <= 1 || isNaN(this.s))
        && (0 <= this.l && this.l <= 1)
        && (0 <= this.opacity && this.opacity <= 1);
  },
  formatHsl: function() {
    var a = this.opacity; a = isNaN(a) ? 1 : Math.max(0, Math.min(1, a));
    return (a === 1 ? "hsl(" : "hsla(")
        + (this.h || 0) + ", "
        + (this.s || 0) * 100 + "%, "
        + (this.l || 0) * 100 + "%"
        + (a === 1 ? ")" : ", " + a + ")");
  }
}));

/* From FvD 13.37, CSS Color Module Level 3 */
function hsl2rgb(h, m1, m2) {
  return (h < 60 ? m1 + (m2 - m1) * h / 60
      : h < 180 ? m2
      : h < 240 ? m1 + (m2 - m1) * (240 - h) / 60
      : m1) * 255;
}

const radians = Math.PI / 180;
const degrees = 180 / Math.PI;

var A$2 = -0.14861,
    B = +1.78277,
    C$2 = -0.29227,
    D$1 = -0.90649,
    E$1 = +1.97294,
    ED = E$1 * D$1,
    EB = E$1 * B,
    BC_DA = B * C$2 - D$1 * A$2;

function cubehelixConvert(o) {
  if (o instanceof Cubehelix) return new Cubehelix(o.h, o.s, o.l, o.opacity);
  if (!(o instanceof Rgb)) o = rgbConvert(o);
  var r = o.r / 255,
      g = o.g / 255,
      b = o.b / 255,
      l = (BC_DA * b + ED * r - EB * g) / (BC_DA + ED - EB),
      bl = b - l,
      k = (E$1 * (g - l) - C$2 * bl) / D$1,
      s = Math.sqrt(k * k + bl * bl) / (E$1 * l * (1 - l)), // NaN if l=0 or l=1
      h = s ? Math.atan2(k, bl) * degrees - 120 : NaN;
  return new Cubehelix(h < 0 ? h + 360 : h, s, l, o.opacity);
}

function cubehelix$1(h, s, l, opacity) {
  return arguments.length === 1 ? cubehelixConvert(h) : new Cubehelix(h, s, l, opacity == null ? 1 : opacity);
}

function Cubehelix(h, s, l, opacity) {
  this.h = +h;
  this.s = +s;
  this.l = +l;
  this.opacity = +opacity;
}

define(Cubehelix, cubehelix$1, extend(Color, {
  brighter: function(k) {
    k = k == null ? brighter : Math.pow(brighter, k);
    return new Cubehelix(this.h, this.s, this.l * k, this.opacity);
  },
  darker: function(k) {
    k = k == null ? darker : Math.pow(darker, k);
    return new Cubehelix(this.h, this.s, this.l * k, this.opacity);
  },
  rgb: function() {
    var h = isNaN(this.h) ? 0 : (this.h + 120) * radians,
        l = +this.l,
        a = isNaN(this.s) ? 0 : this.s * l * (1 - l),
        cosh = Math.cos(h),
        sinh = Math.sin(h);
    return new Rgb(
      255 * (l + a * (A$2 * cosh + B * sinh)),
      255 * (l + a * (C$2 * cosh + D$1 * sinh)),
      255 * (l + a * (E$1 * cosh)),
      this.opacity
    );
  }
}));

function basis(t1, v0, v1, v2, v3) {
  var t2 = t1 * t1, t3 = t2 * t1;
  return ((1 - 3 * t1 + 3 * t2 - t3) * v0
      + (4 - 6 * t2 + 3 * t3) * v1
      + (1 + 3 * t1 + 3 * t2 - 3 * t3) * v2
      + t3 * v3) / 6;
}

function basis$1(values) {
  var n = values.length - 1;
  return function(t) {
    var i = t <= 0 ? (t = 0) : t >= 1 ? (t = 1, n - 1) : Math.floor(t * n),
        v1 = values[i],
        v2 = values[i + 1],
        v0 = i > 0 ? values[i - 1] : 2 * v1 - v2,
        v3 = i < n - 1 ? values[i + 2] : 2 * v2 - v1;
    return basis((t - i / n) * n, v0, v1, v2, v3);
  };
}

var constant = x => () => x;

function linear$1(a, d) {
  return function(t) {
    return a + t * d;
  };
}

function exponential(a, b, y) {
  return a = Math.pow(a, y), b = Math.pow(b, y) - a, y = 1 / y, function(t) {
    return Math.pow(a + t * b, y);
  };
}

function hue(a, b) {
  var d = b - a;
  return d ? linear$1(a, d > 180 || d < -180 ? d - 360 * Math.round(d / 360) : d) : constant(isNaN(a) ? b : a);
}

function gamma(y) {
  return (y = +y) === 1 ? nogamma : function(a, b) {
    return b - a ? exponential(a, b, y) : constant(isNaN(a) ? b : a);
  };
}

function nogamma(a, b) {
  var d = b - a;
  return d ? linear$1(a, d) : constant(isNaN(a) ? b : a);
}

var rgb = (function rgbGamma(y) {
  var color = gamma(y);

  function rgb(start, end) {
    var r = color((start = rgb$1(start)).r, (end = rgb$1(end)).r),
        g = color(start.g, end.g),
        b = color(start.b, end.b),
        opacity = nogamma(start.opacity, end.opacity);
    return function(t) {
      start.r = r(t);
      start.g = g(t);
      start.b = b(t);
      start.opacity = opacity(t);
      return start + "";
    };
  }

  rgb.gamma = rgbGamma;

  return rgb;
})(1);

function rgbSpline(spline) {
  return function(colors) {
    var n = colors.length,
        r = new Array(n),
        g = new Array(n),
        b = new Array(n),
        i, color;
    for (i = 0; i < n; ++i) {
      color = rgb$1(colors[i]);
      r[i] = color.r || 0;
      g[i] = color.g || 0;
      b[i] = color.b || 0;
    }
    r = spline(r);
    g = spline(g);
    b = spline(b);
    color.opacity = 1;
    return function(t) {
      color.r = r(t);
      color.g = g(t);
      color.b = b(t);
      return color + "";
    };
  };
}

var rgbBasis = rgbSpline(basis$1);

function numberArray(a, b) {
  if (!b) b = [];
  var n = a ? Math.min(b.length, a.length) : 0,
      c = b.slice(),
      i;
  return function(t) {
    for (i = 0; i < n; ++i) c[i] = a[i] * (1 - t) + b[i] * t;
    return c;
  };
}

function isNumberArray(x) {
  return ArrayBuffer.isView(x) && !(x instanceof DataView);
}

function genericArray(a, b) {
  var nb = b ? b.length : 0,
      na = a ? Math.min(nb, a.length) : 0,
      x = new Array(na),
      c = new Array(nb),
      i;

  for (i = 0; i < na; ++i) x[i] = interpolate(a[i], b[i]);
  for (; i < nb; ++i) c[i] = b[i];

  return function(t) {
    for (i = 0; i < na; ++i) c[i] = x[i](t);
    return c;
  };
}

function date(a, b) {
  var d = new Date;
  return a = +a, b = +b, function(t) {
    return d.setTime(a * (1 - t) + b * t), d;
  };
}

function interpolateNumber(a, b) {
  return a = +a, b = +b, function(t) {
    return a * (1 - t) + b * t;
  };
}

function object(a, b) {
  var i = {},
      c = {},
      k;

  if (a === null || typeof a !== "object") a = {};
  if (b === null || typeof b !== "object") b = {};

  for (k in b) {
    if (k in a) {
      i[k] = interpolate(a[k], b[k]);
    } else {
      c[k] = b[k];
    }
  }

  return function(t) {
    for (k in i) c[k] = i[k](t);
    return c;
  };
}

var reA = /[-+]?(?:\d+\.?\d*|\.?\d+)(?:[eE][-+]?\d+)?/g,
    reB = new RegExp(reA.source, "g");

function zero(b) {
  return function() {
    return b;
  };
}

function one(b) {
  return function(t) {
    return b(t) + "";
  };
}

function R(a, b) {
  var bi = reA.lastIndex = reB.lastIndex = 0, // scan index for next number in b
      am, // current match in a
      bm, // current match in b
      bs, // string preceding current number in b, if any
      i = -1, // index in s
      s = [], // string constants and placeholders
      q = []; // number interpolators

  // Coerce inputs to strings.
  a = a + "", b = b + "";

  // Interpolate pairs of numbers in a & b.
  while ((am = reA.exec(a))
      && (bm = reB.exec(b))) {
    if ((bs = bm.index) > bi) { // a string precedes the next number in b
      bs = b.slice(bi, bs);
      if (s[i]) s[i] += bs; // coalesce with previous string
      else s[++i] = bs;
    }
    if ((am = am[0]) === (bm = bm[0])) { // numbers in a & b match
      if (s[i]) s[i] += bm; // coalesce with previous string
      else s[++i] = bm;
    } else { // interpolate non-matching numbers
      s[++i] = null;
      q.push({i: i, x: interpolateNumber(am, bm)});
    }
    bi = reB.lastIndex;
  }

  // Add remains of b.
  if (bi < b.length) {
    bs = b.slice(bi);
    if (s[i]) s[i] += bs; // coalesce with previous string
    else s[++i] = bs;
  }

  // Special optimization for only a single match.
  // Otherwise, interpolate each of the numbers and rejoin the string.
  return s.length < 2 ? (q[0]
      ? one(q[0].x)
      : zero(b))
      : (b = q.length, function(t) {
          for (var i = 0, o; i < b; ++i) s[(o = q[i]).i] = o.x(t);
          return s.join("");
        });
}

function interpolate(a, b) {
  var t = typeof b, c;
  return b == null || t === "boolean" ? constant(b)
      : (t === "number" ? interpolateNumber
      : t === "string" ? ((c = color(b)) ? (b = c, rgb) : R)
      : b instanceof color ? rgb
      : b instanceof Date ? date
      : isNumberArray(b) ? numberArray
      : Array.isArray(b) ? genericArray
      : typeof b.valueOf !== "function" && typeof b.toString !== "function" || isNaN(b) ? object
      : interpolateNumber)(a, b);
}

function interpolateRound(a, b) {
  return a = +a, b = +b, function(t) {
    return Math.round(a * (1 - t) + b * t);
  };
}

function cubehelix(hue) {
  return (function cubehelixGamma(y) {
    y = +y;

    function cubehelix(start, end) {
      var h = hue((start = cubehelix$1(start)).h, (end = cubehelix$1(end)).h),
          s = nogamma(start.s, end.s),
          l = nogamma(start.l, end.l),
          opacity = nogamma(start.opacity, end.opacity);
      return function(t) {
        start.h = h(t);
        start.s = s(t);
        start.l = l(Math.pow(t, y));
        start.opacity = opacity(t);
        return start + "";
      };
    }

    cubehelix.gamma = cubehelixGamma;

    return cubehelix;
  })(1);
}

cubehelix(hue);
var cubehelixLong = cubehelix(nogamma);

function piecewise(interpolate$1, values) {
  if (values === undefined) values = interpolate$1, interpolate$1 = interpolate;
  var i = 0, n = values.length - 1, v = values[0], I = new Array(n < 0 ? 0 : n);
  while (i < n) I[i] = interpolate$1(v, v = values[++i]);
  return function(t) {
    var i = Math.max(0, Math.min(n - 1, Math.floor(t *= n)));
    return I[i](t - i);
  };
}

var baseGetTag$2 = _baseGetTag,
    isArray$8 = isArray_1,
    isObjectLike$4 = isObjectLike_1;

/** `Object#toString` result references. */
var stringTag$3 = '[object String]';

/**
 * Checks if `value` is classified as a `String` primitive or object.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a string, else `false`.
 * @example
 *
 * _.isString('abc');
 * // => true
 *
 * _.isString(1);
 * // => false
 */
function isString(value) {
  return typeof value == 'string' ||
    (!isArray$8(value) && isObjectLike$4(value) && baseGetTag$2(value) == stringTag$3);
}

var isString_1 = isString;

/**
 * Gets the last element of `array`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to query.
 * @returns {*} Returns the last element of `array`.
 * @example
 *
 * _.last([1, 2, 3]);
 * // => 3
 */

function last$1(array) {
  var length = array == null ? 0 : array.length;
  return length ? array[length - 1] : undefined;
}

var last_1 = last$1;

function ascending(a, b) {
  return a < b ? -1 : a > b ? 1 : a >= b ? 0 : NaN;
}

function bisector(f) {
  let delta = f;
  let compare = f;

  if (f.length === 1) {
    delta = (d, x) => f(d) - x;
    compare = ascendingComparator(f);
  }

  function left(a, x, lo, hi) {
    if (lo == null) lo = 0;
    if (hi == null) hi = a.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (compare(a[mid], x) < 0) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  function right(a, x, lo, hi) {
    if (lo == null) lo = 0;
    if (hi == null) hi = a.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (compare(a[mid], x) > 0) hi = mid;
      else lo = mid + 1;
    }
    return lo;
  }

  function center(a, x, lo, hi) {
    if (lo == null) lo = 0;
    if (hi == null) hi = a.length;
    const i = left(a, x, lo, hi - 1);
    return i > lo && delta(a[i - 1], x) > -delta(a[i], x) ? i - 1 : i;
  }

  return {left, center, right};
}

function ascendingComparator(f) {
  return (d, x) => ascending(f(d), x);
}

function number$1(x) {
  return x === null ? NaN : +x;
}

const ascendingBisect = bisector(ascending);
const bisectRight = ascendingBisect.right;
bisector(number$1).center;
var bisect = bisectRight;

var e10 = Math.sqrt(50),
    e5 = Math.sqrt(10),
    e2 = Math.sqrt(2);

function ticks(start, stop, count) {
  var reverse,
      i = -1,
      n,
      ticks,
      step;

  stop = +stop, start = +start, count = +count;
  if (start === stop && count > 0) return [start];
  if (reverse = stop < start) n = start, start = stop, stop = n;
  if ((step = tickIncrement(start, stop, count)) === 0 || !isFinite(step)) return [];

  if (step > 0) {
    let r0 = Math.round(start / step), r1 = Math.round(stop / step);
    if (r0 * step < start) ++r0;
    if (r1 * step > stop) --r1;
    ticks = new Array(n = r1 - r0 + 1);
    while (++i < n) ticks[i] = (r0 + i) * step;
  } else {
    step = -step;
    let r0 = Math.round(start * step), r1 = Math.round(stop * step);
    if (r0 / step < start) ++r0;
    if (r1 / step > stop) --r1;
    ticks = new Array(n = r1 - r0 + 1);
    while (++i < n) ticks[i] = (r0 + i) / step;
  }

  if (reverse) ticks.reverse();

  return ticks;
}

function tickIncrement(start, stop, count) {
  var step = (stop - start) / Math.max(0, count),
      power = Math.floor(Math.log(step) / Math.LN10),
      error = step / Math.pow(10, power);
  return power >= 0
      ? (error >= e10 ? 10 : error >= e5 ? 5 : error >= e2 ? 2 : 1) * Math.pow(10, power)
      : -Math.pow(10, -power) / (error >= e10 ? 10 : error >= e5 ? 5 : error >= e2 ? 2 : 1);
}

function tickStep(start, stop, count) {
  var step0 = Math.abs(stop - start) / Math.max(0, count),
      step1 = Math.pow(10, Math.floor(Math.log(step0) / Math.LN10)),
      error = step0 / step1;
  if (error >= e10) step1 *= 10;
  else if (error >= e5) step1 *= 5;
  else if (error >= e2) step1 *= 2;
  return stop < start ? -step1 : step1;
}

function sequence(start, stop, step) {
  start = +start, stop = +stop, step = (n = arguments.length) < 2 ? (stop = start, start = 0, 1) : n < 3 ? 1 : +step;

  var i = -1,
      n = Math.max(0, Math.ceil((stop - start) / step)) | 0,
      range = new Array(n);

  while (++i < n) {
    range[i] = start + i * step;
  }

  return range;
}

function initRange(domain, range) {
  switch (arguments.length) {
    case 0: break;
    case 1: this.range(domain); break;
    default: this.range(range).domain(domain); break;
  }
  return this;
}

function initInterpolator(domain, interpolator) {
  switch (arguments.length) {
    case 0: break;
    case 1: {
      if (typeof domain === "function") this.interpolator(domain);
      else this.range(domain);
      break;
    }
    default: {
      this.domain(domain);
      if (typeof interpolator === "function") this.interpolator(interpolator);
      else this.range(interpolator);
      break;
    }
  }
  return this;
}

const implicit = Symbol("implicit");

function ordinal() {
  var index = new Map(),
      domain = [],
      range = [],
      unknown = implicit;

  function scale(d) {
    var key = d + "", i = index.get(key);
    if (!i) {
      if (unknown !== implicit) return unknown;
      index.set(key, i = domain.push(d));
    }
    return range[(i - 1) % range.length];
  }

  scale.domain = function(_) {
    if (!arguments.length) return domain.slice();
    domain = [], index = new Map();
    for (const value of _) {
      const key = value + "";
      if (index.has(key)) continue;
      index.set(key, domain.push(value));
    }
    return scale;
  };

  scale.range = function(_) {
    return arguments.length ? (range = Array.from(_), scale) : range.slice();
  };

  scale.unknown = function(_) {
    return arguments.length ? (unknown = _, scale) : unknown;
  };

  scale.copy = function() {
    return ordinal(domain, range).unknown(unknown);
  };

  initRange.apply(scale, arguments);

  return scale;
}

function band() {
  var scale = ordinal().unknown(undefined),
      domain = scale.domain,
      ordinalRange = scale.range,
      r0 = 0,
      r1 = 1,
      step,
      bandwidth,
      round = false,
      paddingInner = 0,
      paddingOuter = 0,
      align = 0.5;

  delete scale.unknown;

  function rescale() {
    var n = domain().length,
        reverse = r1 < r0,
        start = reverse ? r1 : r0,
        stop = reverse ? r0 : r1;
    step = (stop - start) / Math.max(1, n - paddingInner + paddingOuter * 2);
    if (round) step = Math.floor(step);
    start += (stop - start - step * (n - paddingInner)) * align;
    bandwidth = step * (1 - paddingInner);
    if (round) start = Math.round(start), bandwidth = Math.round(bandwidth);
    var values = sequence(n).map(function(i) { return start + step * i; });
    return ordinalRange(reverse ? values.reverse() : values);
  }

  scale.domain = function(_) {
    return arguments.length ? (domain(_), rescale()) : domain();
  };

  scale.range = function(_) {
    return arguments.length ? ([r0, r1] = _, r0 = +r0, r1 = +r1, rescale()) : [r0, r1];
  };

  scale.rangeRound = function(_) {
    return [r0, r1] = _, r0 = +r0, r1 = +r1, round = true, rescale();
  };

  scale.bandwidth = function() {
    return bandwidth;
  };

  scale.step = function() {
    return step;
  };

  scale.round = function(_) {
    return arguments.length ? (round = !!_, rescale()) : round;
  };

  scale.padding = function(_) {
    return arguments.length ? (paddingInner = Math.min(1, paddingOuter = +_), rescale()) : paddingInner;
  };

  scale.paddingInner = function(_) {
    return arguments.length ? (paddingInner = Math.min(1, _), rescale()) : paddingInner;
  };

  scale.paddingOuter = function(_) {
    return arguments.length ? (paddingOuter = +_, rescale()) : paddingOuter;
  };

  scale.align = function(_) {
    return arguments.length ? (align = Math.max(0, Math.min(1, _)), rescale()) : align;
  };

  scale.copy = function() {
    return band(domain(), [r0, r1])
        .round(round)
        .paddingInner(paddingInner)
        .paddingOuter(paddingOuter)
        .align(align);
  };

  return initRange.apply(rescale(), arguments);
}

function constants(x) {
  return function() {
    return x;
  };
}

function number(x) {
  return +x;
}

var unit = [0, 1];

function identity$4(x) {
  return x;
}

function normalize(a, b) {
  return (b -= (a = +a))
      ? function(x) { return (x - a) / b; }
      : constants(isNaN(b) ? NaN : 0.5);
}

function clamper(a, b) {
  var t;
  if (a > b) t = a, a = b, b = t;
  return function(x) { return Math.max(a, Math.min(b, x)); };
}

// normalize(a, b)(x) takes a domain value x in [a,b] and returns the corresponding parameter t in [0,1].
// interpolate(a, b)(t) takes a parameter t in [0,1] and returns the corresponding range value x in [a,b].
function bimap(domain, range, interpolate) {
  var d0 = domain[0], d1 = domain[1], r0 = range[0], r1 = range[1];
  if (d1 < d0) d0 = normalize(d1, d0), r0 = interpolate(r1, r0);
  else d0 = normalize(d0, d1), r0 = interpolate(r0, r1);
  return function(x) { return r0(d0(x)); };
}

function polymap(domain, range, interpolate) {
  var j = Math.min(domain.length, range.length) - 1,
      d = new Array(j),
      r = new Array(j),
      i = -1;

  // Reverse descending domains.
  if (domain[j] < domain[0]) {
    domain = domain.slice().reverse();
    range = range.slice().reverse();
  }

  while (++i < j) {
    d[i] = normalize(domain[i], domain[i + 1]);
    r[i] = interpolate(range[i], range[i + 1]);
  }

  return function(x) {
    var i = bisect(domain, x, 1, j) - 1;
    return r[i](d[i](x));
  };
}

function copy$1(source, target) {
  return target
      .domain(source.domain())
      .range(source.range())
      .interpolate(source.interpolate())
      .clamp(source.clamp())
      .unknown(source.unknown());
}

function transformer$2() {
  var domain = unit,
      range = unit,
      interpolate$1 = interpolate,
      transform,
      untransform,
      unknown,
      clamp = identity$4,
      piecewise,
      output,
      input;

  function rescale() {
    var n = Math.min(domain.length, range.length);
    if (clamp !== identity$4) clamp = clamper(domain[0], domain[n - 1]);
    piecewise = n > 2 ? polymap : bimap;
    output = input = null;
    return scale;
  }

  function scale(x) {
    return x == null || isNaN(x = +x) ? unknown : (output || (output = piecewise(domain.map(transform), range, interpolate$1)))(transform(clamp(x)));
  }

  scale.invert = function(y) {
    return clamp(untransform((input || (input = piecewise(range, domain.map(transform), interpolateNumber)))(y)));
  };

  scale.domain = function(_) {
    return arguments.length ? (domain = Array.from(_, number), rescale()) : domain.slice();
  };

  scale.range = function(_) {
    return arguments.length ? (range = Array.from(_), rescale()) : range.slice();
  };

  scale.rangeRound = function(_) {
    return range = Array.from(_), interpolate$1 = interpolateRound, rescale();
  };

  scale.clamp = function(_) {
    return arguments.length ? (clamp = _ ? true : identity$4, rescale()) : clamp !== identity$4;
  };

  scale.interpolate = function(_) {
    return arguments.length ? (interpolate$1 = _, rescale()) : interpolate$1;
  };

  scale.unknown = function(_) {
    return arguments.length ? (unknown = _, scale) : unknown;
  };

  return function(t, u) {
    transform = t, untransform = u;
    return rescale();
  };
}

function continuous() {
  return transformer$2()(identity$4, identity$4);
}

function formatDecimal$1(x) {
  return Math.abs(x = Math.round(x)) >= 1e21
      ? x.toLocaleString("en").replace(/,/g, "")
      : x.toString(10);
}

// Computes the decimal coefficient and exponent of the specified number x with
// significant digits p, where x is positive and p is in [1, 21] or undefined.
// For example, formatDecimalParts(1.23) returns ["123", 0].
function formatDecimalParts$1(x, p) {
  if ((i = (x = p ? x.toExponential(p - 1) : x.toExponential()).indexOf("e")) < 0) return null; // NaN, ±Infinity
  var i, coefficient = x.slice(0, i);

  // The string returned by toExponential either has the form \d\.\d+e[-+]\d+
  // (e.g., 1.2e+3) or the form \de[-+]\d+ (e.g., 1e+3).
  return [
    coefficient.length > 1 ? coefficient[0] + coefficient.slice(2) : coefficient,
    +x.slice(i + 1)
  ];
}

function exponent$1(x) {
  return x = formatDecimalParts$1(Math.abs(x)), x ? x[1] : NaN;
}

function formatGroup$1(grouping, thousands) {
  return function(value, width) {
    var i = value.length,
        t = [],
        j = 0,
        g = grouping[0],
        length = 0;

    while (i > 0 && g > 0) {
      if (length + g + 1 > width) g = Math.max(1, width - length);
      t.push(value.substring(i -= g, i + g));
      if ((length += g + 1) > width) break;
      g = grouping[j = (j + 1) % grouping.length];
    }

    return t.reverse().join(thousands);
  };
}

function formatNumerals$1(numerals) {
  return function(value) {
    return value.replace(/[0-9]/g, function(i) {
      return numerals[+i];
    });
  };
}

// [[fill]align][sign][symbol][0][width][,][.precision][~][type]
var re$1 = /^(?:(.)?([<>=^]))?([+\-( ])?([$#])?(0)?(\d+)?(,)?(\.\d+)?(~)?([a-z%])?$/i;

function formatSpecifier$1(specifier) {
  if (!(match = re$1.exec(specifier))) throw new Error("invalid format: " + specifier);
  var match;
  return new FormatSpecifier$1({
    fill: match[1],
    align: match[2],
    sign: match[3],
    symbol: match[4],
    zero: match[5],
    width: match[6],
    comma: match[7],
    precision: match[8] && match[8].slice(1),
    trim: match[9],
    type: match[10]
  });
}

formatSpecifier$1.prototype = FormatSpecifier$1.prototype; // instanceof

function FormatSpecifier$1(specifier) {
  this.fill = specifier.fill === undefined ? " " : specifier.fill + "";
  this.align = specifier.align === undefined ? ">" : specifier.align + "";
  this.sign = specifier.sign === undefined ? "-" : specifier.sign + "";
  this.symbol = specifier.symbol === undefined ? "" : specifier.symbol + "";
  this.zero = !!specifier.zero;
  this.width = specifier.width === undefined ? undefined : +specifier.width;
  this.comma = !!specifier.comma;
  this.precision = specifier.precision === undefined ? undefined : +specifier.precision;
  this.trim = !!specifier.trim;
  this.type = specifier.type === undefined ? "" : specifier.type + "";
}

FormatSpecifier$1.prototype.toString = function() {
  return this.fill
      + this.align
      + this.sign
      + this.symbol
      + (this.zero ? "0" : "")
      + (this.width === undefined ? "" : Math.max(1, this.width | 0))
      + (this.comma ? "," : "")
      + (this.precision === undefined ? "" : "." + Math.max(0, this.precision | 0))
      + (this.trim ? "~" : "")
      + this.type;
};

// Trims insignificant zeros, e.g., replaces 1.2000k with 1.2k.
function formatTrim$1(s) {
  out: for (var n = s.length, i = 1, i0 = -1, i1; i < n; ++i) {
    switch (s[i]) {
      case ".": i0 = i1 = i; break;
      case "0": if (i0 === 0) i0 = i; i1 = i; break;
      default: if (!+s[i]) break out; if (i0 > 0) i0 = 0; break;
    }
  }
  return i0 > 0 ? s.slice(0, i0) + s.slice(i1 + 1) : s;
}

var prefixExponent$1;

function formatPrefixAuto$1(x, p) {
  var d = formatDecimalParts$1(x, p);
  if (!d) return x + "";
  var coefficient = d[0],
      exponent = d[1],
      i = exponent - (prefixExponent$1 = Math.max(-8, Math.min(8, Math.floor(exponent / 3))) * 3) + 1,
      n = coefficient.length;
  return i === n ? coefficient
      : i > n ? coefficient + new Array(i - n + 1).join("0")
      : i > 0 ? coefficient.slice(0, i) + "." + coefficient.slice(i)
      : "0." + new Array(1 - i).join("0") + formatDecimalParts$1(x, Math.max(0, p + i - 1))[0]; // less than 1y!
}

function formatRounded$1(x, p) {
  var d = formatDecimalParts$1(x, p);
  if (!d) return x + "";
  var coefficient = d[0],
      exponent = d[1];
  return exponent < 0 ? "0." + new Array(-exponent).join("0") + coefficient
      : coefficient.length > exponent + 1 ? coefficient.slice(0, exponent + 1) + "." + coefficient.slice(exponent + 1)
      : coefficient + new Array(exponent - coefficient.length + 2).join("0");
}

var formatTypes$1 = {
  "%": (x, p) => (x * 100).toFixed(p),
  "b": (x) => Math.round(x).toString(2),
  "c": (x) => x + "",
  "d": formatDecimal$1,
  "e": (x, p) => x.toExponential(p),
  "f": (x, p) => x.toFixed(p),
  "g": (x, p) => x.toPrecision(p),
  "o": (x) => Math.round(x).toString(8),
  "p": (x, p) => formatRounded$1(x * 100, p),
  "r": formatRounded$1,
  "s": formatPrefixAuto$1,
  "X": (x) => Math.round(x).toString(16).toUpperCase(),
  "x": (x) => Math.round(x).toString(16)
};

function identity$3(x) {
  return x;
}

var map$1 = Array.prototype.map,
    prefixes$1 = ["y","z","a","f","p","n","µ","m","","k","M","G","T","P","E","Z","Y"];

function formatLocale$2(locale) {
  var group = locale.grouping === undefined || locale.thousands === undefined ? identity$3 : formatGroup$1(map$1.call(locale.grouping, Number), locale.thousands + ""),
      currencyPrefix = locale.currency === undefined ? "" : locale.currency[0] + "",
      currencySuffix = locale.currency === undefined ? "" : locale.currency[1] + "",
      decimal = locale.decimal === undefined ? "." : locale.decimal + "",
      numerals = locale.numerals === undefined ? identity$3 : formatNumerals$1(map$1.call(locale.numerals, String)),
      percent = locale.percent === undefined ? "%" : locale.percent + "",
      minus = locale.minus === undefined ? "−" : locale.minus + "",
      nan = locale.nan === undefined ? "NaN" : locale.nan + "";

  function newFormat(specifier) {
    specifier = formatSpecifier$1(specifier);

    var fill = specifier.fill,
        align = specifier.align,
        sign = specifier.sign,
        symbol = specifier.symbol,
        zero = specifier.zero,
        width = specifier.width,
        comma = specifier.comma,
        precision = specifier.precision,
        trim = specifier.trim,
        type = specifier.type;

    // The "n" type is an alias for ",g".
    if (type === "n") comma = true, type = "g";

    // The "" type, and any invalid type, is an alias for ".12~g".
    else if (!formatTypes$1[type]) precision === undefined && (precision = 12), trim = true, type = "g";

    // If zero fill is specified, padding goes after sign and before digits.
    if (zero || (fill === "0" && align === "=")) zero = true, fill = "0", align = "=";

    // Compute the prefix and suffix.
    // For SI-prefix, the suffix is lazily computed.
    var prefix = symbol === "$" ? currencyPrefix : symbol === "#" && /[boxX]/.test(type) ? "0" + type.toLowerCase() : "",
        suffix = symbol === "$" ? currencySuffix : /[%p]/.test(type) ? percent : "";

    // What format function should we use?
    // Is this an integer type?
    // Can this type generate exponential notation?
    var formatType = formatTypes$1[type],
        maybeSuffix = /[defgprs%]/.test(type);

    // Set the default precision if not specified,
    // or clamp the specified precision to the supported range.
    // For significant precision, it must be in [1, 21].
    // For fixed precision, it must be in [0, 20].
    precision = precision === undefined ? 6
        : /[gprs]/.test(type) ? Math.max(1, Math.min(21, precision))
        : Math.max(0, Math.min(20, precision));

    function format(value) {
      var valuePrefix = prefix,
          valueSuffix = suffix,
          i, n, c;

      if (type === "c") {
        valueSuffix = formatType(value) + valueSuffix;
        value = "";
      } else {
        value = +value;

        // Determine the sign. -0 is not less than 0, but 1 / -0 is!
        var valueNegative = value < 0 || 1 / value < 0;

        // Perform the initial formatting.
        value = isNaN(value) ? nan : formatType(Math.abs(value), precision);

        // Trim insignificant zeros.
        if (trim) value = formatTrim$1(value);

        // If a negative value rounds to zero after formatting, and no explicit positive sign is requested, hide the sign.
        if (valueNegative && +value === 0 && sign !== "+") valueNegative = false;

        // Compute the prefix and suffix.
        valuePrefix = (valueNegative ? (sign === "(" ? sign : minus) : sign === "-" || sign === "(" ? "" : sign) + valuePrefix;
        valueSuffix = (type === "s" ? prefixes$1[8 + prefixExponent$1 / 3] : "") + valueSuffix + (valueNegative && sign === "(" ? ")" : "");

        // Break the formatted value into the integer “value” part that can be
        // grouped, and fractional or exponential “suffix” part that is not.
        if (maybeSuffix) {
          i = -1, n = value.length;
          while (++i < n) {
            if (c = value.charCodeAt(i), 48 > c || c > 57) {
              valueSuffix = (c === 46 ? decimal + value.slice(i + 1) : value.slice(i)) + valueSuffix;
              value = value.slice(0, i);
              break;
            }
          }
        }
      }

      // If the fill character is not "0", grouping is applied before padding.
      if (comma && !zero) value = group(value, Infinity);

      // Compute the padding.
      var length = valuePrefix.length + value.length + valueSuffix.length,
          padding = length < width ? new Array(width - length + 1).join(fill) : "";

      // If the fill character is "0", grouping is applied after padding.
      if (comma && zero) value = group(padding + value, padding.length ? width - valueSuffix.length : Infinity), padding = "";

      // Reconstruct the final output based on the desired alignment.
      switch (align) {
        case "<": value = valuePrefix + value + valueSuffix + padding; break;
        case "=": value = valuePrefix + padding + value + valueSuffix; break;
        case "^": value = padding.slice(0, length = padding.length >> 1) + valuePrefix + value + valueSuffix + padding.slice(length); break;
        default: value = padding + valuePrefix + value + valueSuffix; break;
      }

      return numerals(value);
    }

    format.toString = function() {
      return specifier + "";
    };

    return format;
  }

  function formatPrefix(specifier, value) {
    var f = newFormat((specifier = formatSpecifier$1(specifier), specifier.type = "f", specifier)),
        e = Math.max(-8, Math.min(8, Math.floor(exponent$1(value) / 3))) * 3,
        k = Math.pow(10, -e),
        prefix = prefixes$1[8 + e / 3];
    return function(value) {
      return f(k * value) + prefix;
    };
  }

  return {
    format: newFormat,
    formatPrefix: formatPrefix
  };
}

var locale$2;
var format$1;
var formatPrefix;

defaultLocale$2({
  thousands: ",",
  grouping: [3],
  currency: ["$", ""]
});

function defaultLocale$2(definition) {
  locale$2 = formatLocale$2(definition);
  format$1 = locale$2.format;
  formatPrefix = locale$2.formatPrefix;
  return locale$2;
}

function precisionFixed(step) {
  return Math.max(0, -exponent$1(Math.abs(step)));
}

function precisionPrefix(step, value) {
  return Math.max(0, Math.max(-8, Math.min(8, Math.floor(exponent$1(value) / 3))) * 3 - exponent$1(Math.abs(step)));
}

function precisionRound(step, max) {
  step = Math.abs(step), max = Math.abs(max) - step;
  return Math.max(0, exponent$1(max) - exponent$1(step)) + 1;
}

function tickFormat(start, stop, count, specifier) {
  var step = tickStep(start, stop, count),
      precision;
  specifier = formatSpecifier$1(specifier == null ? ",f" : specifier);
  switch (specifier.type) {
    case "s": {
      var value = Math.max(Math.abs(start), Math.abs(stop));
      if (specifier.precision == null && !isNaN(precision = precisionPrefix(step, value))) specifier.precision = precision;
      return formatPrefix(specifier, value);
    }
    case "":
    case "e":
    case "g":
    case "p":
    case "r": {
      if (specifier.precision == null && !isNaN(precision = precisionRound(step, Math.max(Math.abs(start), Math.abs(stop))))) specifier.precision = precision - (specifier.type === "e");
      break;
    }
    case "f":
    case "%": {
      if (specifier.precision == null && !isNaN(precision = precisionFixed(step))) specifier.precision = precision - (specifier.type === "%") * 2;
      break;
    }
  }
  return format$1(specifier);
}

function linearish(scale) {
  var domain = scale.domain;

  scale.ticks = function(count) {
    var d = domain();
    return ticks(d[0], d[d.length - 1], count == null ? 10 : count);
  };

  scale.tickFormat = function(count, specifier) {
    var d = domain();
    return tickFormat(d[0], d[d.length - 1], count == null ? 10 : count, specifier);
  };

  scale.nice = function(count) {
    if (count == null) count = 10;

    var d = domain();
    var i0 = 0;
    var i1 = d.length - 1;
    var start = d[i0];
    var stop = d[i1];
    var prestep;
    var step;
    var maxIter = 10;

    if (stop < start) {
      step = start, start = stop, stop = step;
      step = i0, i0 = i1, i1 = step;
    }
    
    while (maxIter-- > 0) {
      step = tickIncrement(start, stop, count);
      if (step === prestep) {
        d[i0] = start;
        d[i1] = stop;
        return domain(d);
      } else if (step > 0) {
        start = Math.floor(start / step) * step;
        stop = Math.ceil(stop / step) * step;
      } else if (step < 0) {
        start = Math.ceil(start * step) / step;
        stop = Math.floor(stop * step) / step;
      } else {
        break;
      }
      prestep = step;
    }

    return scale;
  };

  return scale;
}

function linear() {
  var scale = continuous();

  scale.copy = function() {
    return copy$1(scale, linear());
  };

  initRange.apply(scale, arguments);

  return linearish(scale);
}

function quantize() {
  var x0 = 0,
      x1 = 1,
      n = 1,
      domain = [0.5],
      range = [0, 1],
      unknown;

  function scale(x) {
    return x != null && x <= x ? range[bisect(domain, x, 0, n)] : unknown;
  }

  function rescale() {
    var i = -1;
    domain = new Array(n);
    while (++i < n) domain[i] = ((i + 1) * x1 - (i - n) * x0) / (n + 1);
    return scale;
  }

  scale.domain = function(_) {
    return arguments.length ? ([x0, x1] = _, x0 = +x0, x1 = +x1, rescale()) : [x0, x1];
  };

  scale.range = function(_) {
    return arguments.length ? (n = (range = Array.from(_)).length - 1, rescale()) : range.slice();
  };

  scale.invertExtent = function(y) {
    var i = range.indexOf(y);
    return i < 0 ? [NaN, NaN]
        : i < 1 ? [x0, domain[0]]
        : i >= n ? [domain[n - 1], x1]
        : [domain[i - 1], domain[i]];
  };

  scale.unknown = function(_) {
    return arguments.length ? (unknown = _, scale) : scale;
  };

  scale.thresholds = function() {
    return domain.slice();
  };

  scale.copy = function() {
    return quantize()
        .domain([x0, x1])
        .range(range)
        .unknown(unknown);
  };

  return initRange.apply(linearish(scale), arguments);
}

var t0$1 = new Date,
    t1$1 = new Date;

function newInterval$1(floori, offseti, count, field) {

  function interval(date) {
    return floori(date = arguments.length === 0 ? new Date : new Date(+date)), date;
  }

  interval.floor = function(date) {
    return floori(date = new Date(+date)), date;
  };

  interval.ceil = function(date) {
    return floori(date = new Date(date - 1)), offseti(date, 1), floori(date), date;
  };

  interval.round = function(date) {
    var d0 = interval(date),
        d1 = interval.ceil(date);
    return date - d0 < d1 - date ? d0 : d1;
  };

  interval.offset = function(date, step) {
    return offseti(date = new Date(+date), step == null ? 1 : Math.floor(step)), date;
  };

  interval.range = function(start, stop, step) {
    var range = [], previous;
    start = interval.ceil(start);
    step = step == null ? 1 : Math.floor(step);
    if (!(start < stop) || !(step > 0)) return range; // also handles Invalid Date
    do range.push(previous = new Date(+start)), offseti(start, step), floori(start);
    while (previous < start && start < stop);
    return range;
  };

  interval.filter = function(test) {
    return newInterval$1(function(date) {
      if (date >= date) while (floori(date), !test(date)) date.setTime(date - 1);
    }, function(date, step) {
      if (date >= date) {
        if (step < 0) while (++step <= 0) {
          while (offseti(date, -1), !test(date)) {} // eslint-disable-line no-empty
        } else while (--step >= 0) {
          while (offseti(date, +1), !test(date)) {} // eslint-disable-line no-empty
        }
      }
    });
  };

  if (count) {
    interval.count = function(start, end) {
      t0$1.setTime(+start), t1$1.setTime(+end);
      floori(t0$1), floori(t1$1);
      return Math.floor(count(t0$1, t1$1));
    };

    interval.every = function(step) {
      step = Math.floor(step);
      return !isFinite(step) || !(step > 0) ? null
          : !(step > 1) ? interval
          : interval.filter(field
              ? function(d) { return field(d) % step === 0; }
              : function(d) { return interval.count(0, d) % step === 0; });
    };
  }

  return interval;
}

const durationSecond$1 = 1000;
const durationMinute$1 = durationSecond$1 * 60;
const durationHour$1 = durationMinute$1 * 60;
const durationDay = durationHour$1 * 24;
const durationWeek$1 = durationDay * 7;

var day = newInterval$1(
  date => date.setHours(0, 0, 0, 0),
  (date, step) => date.setDate(date.getDate() + step),
  (start, end) => (end - start - (end.getTimezoneOffset() - start.getTimezoneOffset()) * durationMinute$1) / durationDay,
  date => date.getDate() - 1
);

var timeDay = day;
day.range;

function weekday$1(i) {
  return newInterval$1(function(date) {
    date.setDate(date.getDate() - (date.getDay() + 7 - i) % 7);
    date.setHours(0, 0, 0, 0);
  }, function(date, step) {
    date.setDate(date.getDate() + step * 7);
  }, function(start, end) {
    return (end - start - (end.getTimezoneOffset() - start.getTimezoneOffset()) * durationMinute$1) / durationWeek$1;
  });
}

var sunday$1 = weekday$1(0);
var monday$1 = weekday$1(1);
var tuesday$1 = weekday$1(2);
var wednesday$1 = weekday$1(3);
var thursday$1 = weekday$1(4);
var friday$1 = weekday$1(5);
var saturday$1 = weekday$1(6);

sunday$1.range;
monday$1.range;
tuesday$1.range;
wednesday$1.range;
thursday$1.range;
friday$1.range;
saturday$1.range;

var year$1 = newInterval$1(function(date) {
  date.setMonth(0, 1);
  date.setHours(0, 0, 0, 0);
}, function(date, step) {
  date.setFullYear(date.getFullYear() + step);
}, function(start, end) {
  return end.getFullYear() - start.getFullYear();
}, function(date) {
  return date.getFullYear();
});

// An optimized implementation for this simple case.
year$1.every = function(k) {
  return !isFinite(k = Math.floor(k)) || !(k > 0) ? null : newInterval$1(function(date) {
    date.setFullYear(Math.floor(date.getFullYear() / k) * k);
    date.setMonth(0, 1);
    date.setHours(0, 0, 0, 0);
  }, function(date, step) {
    date.setFullYear(date.getFullYear() + step * k);
  });
};

var timeYear = year$1;
year$1.range;

var utcDay = newInterval$1(function(date) {
  date.setUTCHours(0, 0, 0, 0);
}, function(date, step) {
  date.setUTCDate(date.getUTCDate() + step);
}, function(start, end) {
  return (end - start) / durationDay;
}, function(date) {
  return date.getUTCDate() - 1;
});

var utcDay$1 = utcDay;
utcDay.range;

function utcWeekday$1(i) {
  return newInterval$1(function(date) {
    date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 7 - i) % 7);
    date.setUTCHours(0, 0, 0, 0);
  }, function(date, step) {
    date.setUTCDate(date.getUTCDate() + step * 7);
  }, function(start, end) {
    return (end - start) / durationWeek$1;
  });
}

var utcSunday$1 = utcWeekday$1(0);
var utcMonday$1 = utcWeekday$1(1);
var utcTuesday$1 = utcWeekday$1(2);
var utcWednesday$1 = utcWeekday$1(3);
var utcThursday$1 = utcWeekday$1(4);
var utcFriday$1 = utcWeekday$1(5);
var utcSaturday$1 = utcWeekday$1(6);

utcSunday$1.range;
utcMonday$1.range;
utcTuesday$1.range;
utcWednesday$1.range;
utcThursday$1.range;
utcFriday$1.range;
utcSaturday$1.range;

var utcYear$1 = newInterval$1(function(date) {
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
}, function(date, step) {
  date.setUTCFullYear(date.getUTCFullYear() + step);
}, function(start, end) {
  return end.getUTCFullYear() - start.getUTCFullYear();
}, function(date) {
  return date.getUTCFullYear();
});

// An optimized implementation for this simple case.
utcYear$1.every = function(k) {
  return !isFinite(k = Math.floor(k)) || !(k > 0) ? null : newInterval$1(function(date) {
    date.setUTCFullYear(Math.floor(date.getUTCFullYear() / k) * k);
    date.setUTCMonth(0, 1);
    date.setUTCHours(0, 0, 0, 0);
  }, function(date, step) {
    date.setUTCFullYear(date.getUTCFullYear() + step * k);
  });
};

var utcYear$2 = utcYear$1;
utcYear$1.range;

function localDate(d) {
  if (0 <= d.y && d.y < 100) {
    var date = new Date(-1, d.m, d.d, d.H, d.M, d.S, d.L);
    date.setFullYear(d.y);
    return date;
  }
  return new Date(d.y, d.m, d.d, d.H, d.M, d.S, d.L);
}

function utcDate(d) {
  if (0 <= d.y && d.y < 100) {
    var date = new Date(Date.UTC(-1, d.m, d.d, d.H, d.M, d.S, d.L));
    date.setUTCFullYear(d.y);
    return date;
  }
  return new Date(Date.UTC(d.y, d.m, d.d, d.H, d.M, d.S, d.L));
}

function newDate(y, m, d) {
  return {y: y, m: m, d: d, H: 0, M: 0, S: 0, L: 0};
}

function formatLocale$1(locale) {
  var locale_dateTime = locale.dateTime,
      locale_date = locale.date,
      locale_time = locale.time,
      locale_periods = locale.periods,
      locale_weekdays = locale.days,
      locale_shortWeekdays = locale.shortDays,
      locale_months = locale.months,
      locale_shortMonths = locale.shortMonths;

  var periodRe = formatRe(locale_periods),
      periodLookup = formatLookup(locale_periods),
      weekdayRe = formatRe(locale_weekdays),
      weekdayLookup = formatLookup(locale_weekdays),
      shortWeekdayRe = formatRe(locale_shortWeekdays),
      shortWeekdayLookup = formatLookup(locale_shortWeekdays),
      monthRe = formatRe(locale_months),
      monthLookup = formatLookup(locale_months),
      shortMonthRe = formatRe(locale_shortMonths),
      shortMonthLookup = formatLookup(locale_shortMonths);

  var formats = {
    "a": formatShortWeekday,
    "A": formatWeekday,
    "b": formatShortMonth,
    "B": formatMonth,
    "c": null,
    "d": formatDayOfMonth,
    "e": formatDayOfMonth,
    "f": formatMicroseconds,
    "g": formatYearISO,
    "G": formatFullYearISO,
    "H": formatHour24,
    "I": formatHour12,
    "j": formatDayOfYear,
    "L": formatMilliseconds,
    "m": formatMonthNumber,
    "M": formatMinutes,
    "p": formatPeriod,
    "q": formatQuarter,
    "Q": formatUnixTimestamp,
    "s": formatUnixTimestampSeconds,
    "S": formatSeconds,
    "u": formatWeekdayNumberMonday,
    "U": formatWeekNumberSunday,
    "V": formatWeekNumberISO,
    "w": formatWeekdayNumberSunday,
    "W": formatWeekNumberMonday,
    "x": null,
    "X": null,
    "y": formatYear,
    "Y": formatFullYear,
    "Z": formatZone,
    "%": formatLiteralPercent
  };

  var utcFormats = {
    "a": formatUTCShortWeekday,
    "A": formatUTCWeekday,
    "b": formatUTCShortMonth,
    "B": formatUTCMonth,
    "c": null,
    "d": formatUTCDayOfMonth,
    "e": formatUTCDayOfMonth,
    "f": formatUTCMicroseconds,
    "g": formatUTCYearISO,
    "G": formatUTCFullYearISO,
    "H": formatUTCHour24,
    "I": formatUTCHour12,
    "j": formatUTCDayOfYear,
    "L": formatUTCMilliseconds,
    "m": formatUTCMonthNumber,
    "M": formatUTCMinutes,
    "p": formatUTCPeriod,
    "q": formatUTCQuarter,
    "Q": formatUnixTimestamp,
    "s": formatUnixTimestampSeconds,
    "S": formatUTCSeconds,
    "u": formatUTCWeekdayNumberMonday,
    "U": formatUTCWeekNumberSunday,
    "V": formatUTCWeekNumberISO,
    "w": formatUTCWeekdayNumberSunday,
    "W": formatUTCWeekNumberMonday,
    "x": null,
    "X": null,
    "y": formatUTCYear,
    "Y": formatUTCFullYear,
    "Z": formatUTCZone,
    "%": formatLiteralPercent
  };

  var parses = {
    "a": parseShortWeekday,
    "A": parseWeekday,
    "b": parseShortMonth,
    "B": parseMonth,
    "c": parseLocaleDateTime,
    "d": parseDayOfMonth,
    "e": parseDayOfMonth,
    "f": parseMicroseconds,
    "g": parseYear,
    "G": parseFullYear,
    "H": parseHour24,
    "I": parseHour24,
    "j": parseDayOfYear,
    "L": parseMilliseconds,
    "m": parseMonthNumber,
    "M": parseMinutes,
    "p": parsePeriod,
    "q": parseQuarter,
    "Q": parseUnixTimestamp,
    "s": parseUnixTimestampSeconds,
    "S": parseSeconds,
    "u": parseWeekdayNumberMonday,
    "U": parseWeekNumberSunday,
    "V": parseWeekNumberISO,
    "w": parseWeekdayNumberSunday,
    "W": parseWeekNumberMonday,
    "x": parseLocaleDate,
    "X": parseLocaleTime,
    "y": parseYear,
    "Y": parseFullYear,
    "Z": parseZone,
    "%": parseLiteralPercent
  };

  // These recursive directive definitions must be deferred.
  formats.x = newFormat(locale_date, formats);
  formats.X = newFormat(locale_time, formats);
  formats.c = newFormat(locale_dateTime, formats);
  utcFormats.x = newFormat(locale_date, utcFormats);
  utcFormats.X = newFormat(locale_time, utcFormats);
  utcFormats.c = newFormat(locale_dateTime, utcFormats);

  function newFormat(specifier, formats) {
    return function(date) {
      var string = [],
          i = -1,
          j = 0,
          n = specifier.length,
          c,
          pad,
          format;

      if (!(date instanceof Date)) date = new Date(+date);

      while (++i < n) {
        if (specifier.charCodeAt(i) === 37) {
          string.push(specifier.slice(j, i));
          if ((pad = pads[c = specifier.charAt(++i)]) != null) c = specifier.charAt(++i);
          else pad = c === "e" ? " " : "0";
          if (format = formats[c]) c = format(date, pad);
          string.push(c);
          j = i + 1;
        }
      }

      string.push(specifier.slice(j, i));
      return string.join("");
    };
  }

  function newParse(specifier, Z) {
    return function(string) {
      var d = newDate(1900, undefined, 1),
          i = parseSpecifier(d, specifier, string += "", 0),
          week, day;
      if (i != string.length) return null;

      // If a UNIX timestamp is specified, return it.
      if ("Q" in d) return new Date(d.Q);
      if ("s" in d) return new Date(d.s * 1000 + ("L" in d ? d.L : 0));

      // If this is utcParse, never use the local timezone.
      if (Z && !("Z" in d)) d.Z = 0;

      // The am-pm flag is 0 for AM, and 1 for PM.
      if ("p" in d) d.H = d.H % 12 + d.p * 12;

      // If the month was not specified, inherit from the quarter.
      if (d.m === undefined) d.m = "q" in d ? d.q : 0;

      // Convert day-of-week and week-of-year to day-of-year.
      if ("V" in d) {
        if (d.V < 1 || d.V > 53) return null;
        if (!("w" in d)) d.w = 1;
        if ("Z" in d) {
          week = utcDate(newDate(d.y, 0, 1)), day = week.getUTCDay();
          week = day > 4 || day === 0 ? utcMonday$1.ceil(week) : utcMonday$1(week);
          week = utcDay$1.offset(week, (d.V - 1) * 7);
          d.y = week.getUTCFullYear();
          d.m = week.getUTCMonth();
          d.d = week.getUTCDate() + (d.w + 6) % 7;
        } else {
          week = localDate(newDate(d.y, 0, 1)), day = week.getDay();
          week = day > 4 || day === 0 ? monday$1.ceil(week) : monday$1(week);
          week = timeDay.offset(week, (d.V - 1) * 7);
          d.y = week.getFullYear();
          d.m = week.getMonth();
          d.d = week.getDate() + (d.w + 6) % 7;
        }
      } else if ("W" in d || "U" in d) {
        if (!("w" in d)) d.w = "u" in d ? d.u % 7 : "W" in d ? 1 : 0;
        day = "Z" in d ? utcDate(newDate(d.y, 0, 1)).getUTCDay() : localDate(newDate(d.y, 0, 1)).getDay();
        d.m = 0;
        d.d = "W" in d ? (d.w + 6) % 7 + d.W * 7 - (day + 5) % 7 : d.w + d.U * 7 - (day + 6) % 7;
      }

      // If a time zone is specified, all fields are interpreted as UTC and then
      // offset according to the specified time zone.
      if ("Z" in d) {
        d.H += d.Z / 100 | 0;
        d.M += d.Z % 100;
        return utcDate(d);
      }

      // Otherwise, all fields are in local time.
      return localDate(d);
    };
  }

  function parseSpecifier(d, specifier, string, j) {
    var i = 0,
        n = specifier.length,
        m = string.length,
        c,
        parse;

    while (i < n) {
      if (j >= m) return -1;
      c = specifier.charCodeAt(i++);
      if (c === 37) {
        c = specifier.charAt(i++);
        parse = parses[c in pads ? specifier.charAt(i++) : c];
        if (!parse || ((j = parse(d, string, j)) < 0)) return -1;
      } else if (c != string.charCodeAt(j++)) {
        return -1;
      }
    }

    return j;
  }

  function parsePeriod(d, string, i) {
    var n = periodRe.exec(string.slice(i));
    return n ? (d.p = periodLookup.get(n[0].toLowerCase()), i + n[0].length) : -1;
  }

  function parseShortWeekday(d, string, i) {
    var n = shortWeekdayRe.exec(string.slice(i));
    return n ? (d.w = shortWeekdayLookup.get(n[0].toLowerCase()), i + n[0].length) : -1;
  }

  function parseWeekday(d, string, i) {
    var n = weekdayRe.exec(string.slice(i));
    return n ? (d.w = weekdayLookup.get(n[0].toLowerCase()), i + n[0].length) : -1;
  }

  function parseShortMonth(d, string, i) {
    var n = shortMonthRe.exec(string.slice(i));
    return n ? (d.m = shortMonthLookup.get(n[0].toLowerCase()), i + n[0].length) : -1;
  }

  function parseMonth(d, string, i) {
    var n = monthRe.exec(string.slice(i));
    return n ? (d.m = monthLookup.get(n[0].toLowerCase()), i + n[0].length) : -1;
  }

  function parseLocaleDateTime(d, string, i) {
    return parseSpecifier(d, locale_dateTime, string, i);
  }

  function parseLocaleDate(d, string, i) {
    return parseSpecifier(d, locale_date, string, i);
  }

  function parseLocaleTime(d, string, i) {
    return parseSpecifier(d, locale_time, string, i);
  }

  function formatShortWeekday(d) {
    return locale_shortWeekdays[d.getDay()];
  }

  function formatWeekday(d) {
    return locale_weekdays[d.getDay()];
  }

  function formatShortMonth(d) {
    return locale_shortMonths[d.getMonth()];
  }

  function formatMonth(d) {
    return locale_months[d.getMonth()];
  }

  function formatPeriod(d) {
    return locale_periods[+(d.getHours() >= 12)];
  }

  function formatQuarter(d) {
    return 1 + ~~(d.getMonth() / 3);
  }

  function formatUTCShortWeekday(d) {
    return locale_shortWeekdays[d.getUTCDay()];
  }

  function formatUTCWeekday(d) {
    return locale_weekdays[d.getUTCDay()];
  }

  function formatUTCShortMonth(d) {
    return locale_shortMonths[d.getUTCMonth()];
  }

  function formatUTCMonth(d) {
    return locale_months[d.getUTCMonth()];
  }

  function formatUTCPeriod(d) {
    return locale_periods[+(d.getUTCHours() >= 12)];
  }

  function formatUTCQuarter(d) {
    return 1 + ~~(d.getUTCMonth() / 3);
  }

  return {
    format: function(specifier) {
      var f = newFormat(specifier += "", formats);
      f.toString = function() { return specifier; };
      return f;
    },
    parse: function(specifier) {
      var p = newParse(specifier += "", false);
      p.toString = function() { return specifier; };
      return p;
    },
    utcFormat: function(specifier) {
      var f = newFormat(specifier += "", utcFormats);
      f.toString = function() { return specifier; };
      return f;
    },
    utcParse: function(specifier) {
      var p = newParse(specifier += "", true);
      p.toString = function() { return specifier; };
      return p;
    }
  };
}

var pads = {"-": "", "_": " ", "0": "0"},
    numberRe = /^\s*\d+/, // note: ignores next directive
    percentRe = /^%/,
    requoteRe = /[\\^$*+?|[\]().{}]/g;

function pad(value, fill, width) {
  var sign = value < 0 ? "-" : "",
      string = (sign ? -value : value) + "",
      length = string.length;
  return sign + (length < width ? new Array(width - length + 1).join(fill) + string : string);
}

function requote(s) {
  return s.replace(requoteRe, "\\$&");
}

function formatRe(names) {
  return new RegExp("^(?:" + names.map(requote).join("|") + ")", "i");
}

function formatLookup(names) {
  return new Map(names.map((name, i) => [name.toLowerCase(), i]));
}

function parseWeekdayNumberSunday(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 1));
  return n ? (d.w = +n[0], i + n[0].length) : -1;
}

function parseWeekdayNumberMonday(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 1));
  return n ? (d.u = +n[0], i + n[0].length) : -1;
}

function parseWeekNumberSunday(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 2));
  return n ? (d.U = +n[0], i + n[0].length) : -1;
}

function parseWeekNumberISO(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 2));
  return n ? (d.V = +n[0], i + n[0].length) : -1;
}

function parseWeekNumberMonday(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 2));
  return n ? (d.W = +n[0], i + n[0].length) : -1;
}

function parseFullYear(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 4));
  return n ? (d.y = +n[0], i + n[0].length) : -1;
}

function parseYear(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 2));
  return n ? (d.y = +n[0] + (+n[0] > 68 ? 1900 : 2000), i + n[0].length) : -1;
}

function parseZone(d, string, i) {
  var n = /^(Z)|([+-]\d\d)(?::?(\d\d))?/.exec(string.slice(i, i + 6));
  return n ? (d.Z = n[1] ? 0 : -(n[2] + (n[3] || "00")), i + n[0].length) : -1;
}

function parseQuarter(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 1));
  return n ? (d.q = n[0] * 3 - 3, i + n[0].length) : -1;
}

function parseMonthNumber(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 2));
  return n ? (d.m = n[0] - 1, i + n[0].length) : -1;
}

function parseDayOfMonth(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 2));
  return n ? (d.d = +n[0], i + n[0].length) : -1;
}

function parseDayOfYear(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 3));
  return n ? (d.m = 0, d.d = +n[0], i + n[0].length) : -1;
}

function parseHour24(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 2));
  return n ? (d.H = +n[0], i + n[0].length) : -1;
}

function parseMinutes(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 2));
  return n ? (d.M = +n[0], i + n[0].length) : -1;
}

function parseSeconds(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 2));
  return n ? (d.S = +n[0], i + n[0].length) : -1;
}

function parseMilliseconds(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 3));
  return n ? (d.L = +n[0], i + n[0].length) : -1;
}

function parseMicroseconds(d, string, i) {
  var n = numberRe.exec(string.slice(i, i + 6));
  return n ? (d.L = Math.floor(n[0] / 1000), i + n[0].length) : -1;
}

function parseLiteralPercent(d, string, i) {
  var n = percentRe.exec(string.slice(i, i + 1));
  return n ? i + n[0].length : -1;
}

function parseUnixTimestamp(d, string, i) {
  var n = numberRe.exec(string.slice(i));
  return n ? (d.Q = +n[0], i + n[0].length) : -1;
}

function parseUnixTimestampSeconds(d, string, i) {
  var n = numberRe.exec(string.slice(i));
  return n ? (d.s = +n[0], i + n[0].length) : -1;
}

function formatDayOfMonth(d, p) {
  return pad(d.getDate(), p, 2);
}

function formatHour24(d, p) {
  return pad(d.getHours(), p, 2);
}

function formatHour12(d, p) {
  return pad(d.getHours() % 12 || 12, p, 2);
}

function formatDayOfYear(d, p) {
  return pad(1 + timeDay.count(timeYear(d), d), p, 3);
}

function formatMilliseconds(d, p) {
  return pad(d.getMilliseconds(), p, 3);
}

function formatMicroseconds(d, p) {
  return formatMilliseconds(d, p) + "000";
}

function formatMonthNumber(d, p) {
  return pad(d.getMonth() + 1, p, 2);
}

function formatMinutes(d, p) {
  return pad(d.getMinutes(), p, 2);
}

function formatSeconds(d, p) {
  return pad(d.getSeconds(), p, 2);
}

function formatWeekdayNumberMonday(d) {
  var day = d.getDay();
  return day === 0 ? 7 : day;
}

function formatWeekNumberSunday(d, p) {
  return pad(sunday$1.count(timeYear(d) - 1, d), p, 2);
}

function dISO(d) {
  var day = d.getDay();
  return (day >= 4 || day === 0) ? thursday$1(d) : thursday$1.ceil(d);
}

function formatWeekNumberISO(d, p) {
  d = dISO(d);
  return pad(thursday$1.count(timeYear(d), d) + (timeYear(d).getDay() === 4), p, 2);
}

function formatWeekdayNumberSunday(d) {
  return d.getDay();
}

function formatWeekNumberMonday(d, p) {
  return pad(monday$1.count(timeYear(d) - 1, d), p, 2);
}

function formatYear(d, p) {
  return pad(d.getFullYear() % 100, p, 2);
}

function formatYearISO(d, p) {
  d = dISO(d);
  return pad(d.getFullYear() % 100, p, 2);
}

function formatFullYear(d, p) {
  return pad(d.getFullYear() % 10000, p, 4);
}

function formatFullYearISO(d, p) {
  var day = d.getDay();
  d = (day >= 4 || day === 0) ? thursday$1(d) : thursday$1.ceil(d);
  return pad(d.getFullYear() % 10000, p, 4);
}

function formatZone(d) {
  var z = d.getTimezoneOffset();
  return (z > 0 ? "-" : (z *= -1, "+"))
      + pad(z / 60 | 0, "0", 2)
      + pad(z % 60, "0", 2);
}

function formatUTCDayOfMonth(d, p) {
  return pad(d.getUTCDate(), p, 2);
}

function formatUTCHour24(d, p) {
  return pad(d.getUTCHours(), p, 2);
}

function formatUTCHour12(d, p) {
  return pad(d.getUTCHours() % 12 || 12, p, 2);
}

function formatUTCDayOfYear(d, p) {
  return pad(1 + utcDay$1.count(utcYear$2(d), d), p, 3);
}

function formatUTCMilliseconds(d, p) {
  return pad(d.getUTCMilliseconds(), p, 3);
}

function formatUTCMicroseconds(d, p) {
  return formatUTCMilliseconds(d, p) + "000";
}

function formatUTCMonthNumber(d, p) {
  return pad(d.getUTCMonth() + 1, p, 2);
}

function formatUTCMinutes(d, p) {
  return pad(d.getUTCMinutes(), p, 2);
}

function formatUTCSeconds(d, p) {
  return pad(d.getUTCSeconds(), p, 2);
}

function formatUTCWeekdayNumberMonday(d) {
  var dow = d.getUTCDay();
  return dow === 0 ? 7 : dow;
}

function formatUTCWeekNumberSunday(d, p) {
  return pad(utcSunday$1.count(utcYear$2(d) - 1, d), p, 2);
}

function UTCdISO(d) {
  var day = d.getUTCDay();
  return (day >= 4 || day === 0) ? utcThursday$1(d) : utcThursday$1.ceil(d);
}

function formatUTCWeekNumberISO(d, p) {
  d = UTCdISO(d);
  return pad(utcThursday$1.count(utcYear$2(d), d) + (utcYear$2(d).getUTCDay() === 4), p, 2);
}

function formatUTCWeekdayNumberSunday(d) {
  return d.getUTCDay();
}

function formatUTCWeekNumberMonday(d, p) {
  return pad(utcMonday$1.count(utcYear$2(d) - 1, d), p, 2);
}

function formatUTCYear(d, p) {
  return pad(d.getUTCFullYear() % 100, p, 2);
}

function formatUTCYearISO(d, p) {
  d = UTCdISO(d);
  return pad(d.getUTCFullYear() % 100, p, 2);
}

function formatUTCFullYear(d, p) {
  return pad(d.getUTCFullYear() % 10000, p, 4);
}

function formatUTCFullYearISO(d, p) {
  var day = d.getUTCDay();
  d = (day >= 4 || day === 0) ? utcThursday$1(d) : utcThursday$1.ceil(d);
  return pad(d.getUTCFullYear() % 10000, p, 4);
}

function formatUTCZone() {
  return "+0000";
}

function formatLiteralPercent() {
  return "%";
}

function formatUnixTimestamp(d) {
  return +d;
}

function formatUnixTimestampSeconds(d) {
  return Math.floor(+d / 1000);
}

var locale$1;
var timeFormat;

defaultLocale$1({
  dateTime: "%x, %X",
  date: "%-m/%-d/%Y",
  time: "%-I:%M:%S %p",
  periods: ["AM", "PM"],
  days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  shortDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  shortMonths: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
});

function defaultLocale$1(definition) {
  locale$1 = formatLocale$1(definition);
  timeFormat = locale$1.format;
  locale$1.parse;
  locale$1.utcFormat;
  locale$1.utcParse;
  return locale$1;
}

function transformer$1() {
  var x0 = 0,
      x1 = 1,
      t0,
      t1,
      k10,
      transform,
      interpolator = identity$4,
      clamp = false,
      unknown;

  function scale(x) {
    return x == null || isNaN(x = +x) ? unknown : interpolator(k10 === 0 ? 0.5 : (x = (transform(x) - t0) * k10, clamp ? Math.max(0, Math.min(1, x)) : x));
  }

  scale.domain = function(_) {
    return arguments.length ? ([x0, x1] = _, t0 = transform(x0 = +x0), t1 = transform(x1 = +x1), k10 = t0 === t1 ? 0 : 1 / (t1 - t0), scale) : [x0, x1];
  };

  scale.clamp = function(_) {
    return arguments.length ? (clamp = !!_, scale) : clamp;
  };

  scale.interpolator = function(_) {
    return arguments.length ? (interpolator = _, scale) : interpolator;
  };

  function range(interpolate) {
    return function(_) {
      var r0, r1;
      return arguments.length ? ([r0, r1] = _, interpolator = interpolate(r0, r1), scale) : [interpolator(0), interpolator(1)];
    };
  }

  scale.range = range(interpolate);

  scale.rangeRound = range(interpolateRound);

  scale.unknown = function(_) {
    return arguments.length ? (unknown = _, scale) : unknown;
  };

  return function(t) {
    transform = t, t0 = t(x0), t1 = t(x1), k10 = t0 === t1 ? 0 : 1 / (t1 - t0);
    return scale;
  };
}

function copy(source, target) {
  return target
      .domain(source.domain())
      .interpolator(source.interpolator())
      .clamp(source.clamp())
      .unknown(source.unknown());
}

function sequential() {
  var scale = linearish(transformer$1()(identity$4));

  scale.copy = function() {
    return copy(scale, sequential());
  };

  return initInterpolator.apply(scale, arguments);
}

function transformer() {
  var x0 = 0,
      x1 = 0.5,
      x2 = 1,
      s = 1,
      t0,
      t1,
      t2,
      k10,
      k21,
      interpolator = identity$4,
      transform,
      clamp = false,
      unknown;

  function scale(x) {
    return isNaN(x = +x) ? unknown : (x = 0.5 + ((x = +transform(x)) - t1) * (s * x < s * t1 ? k10 : k21), interpolator(clamp ? Math.max(0, Math.min(1, x)) : x));
  }

  scale.domain = function(_) {
    return arguments.length ? ([x0, x1, x2] = _, t0 = transform(x0 = +x0), t1 = transform(x1 = +x1), t2 = transform(x2 = +x2), k10 = t0 === t1 ? 0 : 0.5 / (t1 - t0), k21 = t1 === t2 ? 0 : 0.5 / (t2 - t1), s = t1 < t0 ? -1 : 1, scale) : [x0, x1, x2];
  };

  scale.clamp = function(_) {
    return arguments.length ? (clamp = !!_, scale) : clamp;
  };

  scale.interpolator = function(_) {
    return arguments.length ? (interpolator = _, scale) : interpolator;
  };

  function range(interpolate) {
    return function(_) {
      var r0, r1, r2;
      return arguments.length ? ([r0, r1, r2] = _, interpolator = piecewise(interpolate, [r0, r1, r2]), scale) : [interpolator(0), interpolator(0.5), interpolator(1)];
    };
  }

  scale.range = range(interpolate);

  scale.rangeRound = range(interpolateRound);

  scale.unknown = function(_) {
    return arguments.length ? (unknown = _, scale) : unknown;
  };

  return function(t) {
    transform = t, t0 = t(x0), t1 = t(x1), t2 = t(x2), k10 = t0 === t1 ? 0 : 0.5 / (t1 - t0), k21 = t1 === t2 ? 0 : 0.5 / (t2 - t1), s = t1 < t0 ? -1 : 1;
    return scale;
  };
}

function diverging() {
  var scale = linearish(transformer()(identity$4));

  scale.copy = function() {
    return copy(scale, diverging());
  };

  return initInterpolator.apply(scale, arguments);
}

function colors(specifier) {
  var n = specifier.length / 6 | 0, colors = new Array(n), i = 0;
  while (i < n) colors[i] = "#" + specifier.slice(i * 6, ++i * 6);
  return colors;
}

var e = colors("1f77b4ff7f0e2ca02cd627289467bd8c564be377c27f7f7fbcbd2217becf");

var r = colors("7fc97fbeaed4fdc086ffff99386cb0f0027fbf5b17666666");

var n = colors("1b9e77d95f027570b3e7298a66a61ee6ab02a6761d666666");

var t = colors("a6cee31f78b4b2df8a33a02cfb9a99e31a1cfdbf6fff7f00cab2d66a3d9affff99b15928");

var o = colors("fbb4aeb3cde3ccebc5decbe4fed9a6ffffcce5d8bdfddaecf2f2f2");

var i = colors("b3e2cdfdcdaccbd5e8f4cae4e6f5c9fff2aef1e2cccccccc");

var u = colors("e41a1c377eb84daf4a984ea3ff7f00ffff33a65628f781bf999999");

var a = colors("66c2a5fc8d628da0cbe78ac3a6d854ffd92fe5c494b3b3b3");

var s = colors("8dd3c7ffffb3bebadafb807280b1d3fdb462b3de69fccde5d9d9d9bc80bdccebc5ffed6f");

var ramp$1 = scheme => rgbBasis(scheme[scheme.length - 1]);

var scheme$q = new Array(3).concat(
  "d8b365f5f5f55ab4ac",
  "a6611adfc27d80cdc1018571",
  "a6611adfc27df5f5f580cdc1018571",
  "8c510ad8b365f6e8c3c7eae55ab4ac01665e",
  "8c510ad8b365f6e8c3f5f5f5c7eae55ab4ac01665e",
  "8c510abf812ddfc27df6e8c3c7eae580cdc135978f01665e",
  "8c510abf812ddfc27df6e8c3f5f5f5c7eae580cdc135978f01665e",
  "5430058c510abf812ddfc27df6e8c3c7eae580cdc135978f01665e003c30",
  "5430058c510abf812ddfc27df6e8c3f5f5f5c7eae580cdc135978f01665e003c30"
).map(colors);

var b$2 = ramp$1(scheme$q);

var scheme$p = new Array(3).concat(
  "af8dc3f7f7f77fbf7b",
  "7b3294c2a5cfa6dba0008837",
  "7b3294c2a5cff7f7f7a6dba0008837",
  "762a83af8dc3e7d4e8d9f0d37fbf7b1b7837",
  "762a83af8dc3e7d4e8f7f7f7d9f0d37fbf7b1b7837",
  "762a839970abc2a5cfe7d4e8d9f0d3a6dba05aae611b7837",
  "762a839970abc2a5cfe7d4e8f7f7f7d9f0d3a6dba05aae611b7837",
  "40004b762a839970abc2a5cfe7d4e8d9f0d3a6dba05aae611b783700441b",
  "40004b762a839970abc2a5cfe7d4e8f7f7f7d9f0d3a6dba05aae611b783700441b"
).map(colors);

var v$2 = ramp$1(scheme$p);

var scheme$o = new Array(3).concat(
  "e9a3c9f7f7f7a1d76a",
  "d01c8bf1b6dab8e1864dac26",
  "d01c8bf1b6daf7f7f7b8e1864dac26",
  "c51b7de9a3c9fde0efe6f5d0a1d76a4d9221",
  "c51b7de9a3c9fde0eff7f7f7e6f5d0a1d76a4d9221",
  "c51b7dde77aef1b6dafde0efe6f5d0b8e1867fbc414d9221",
  "c51b7dde77aef1b6dafde0eff7f7f7e6f5d0b8e1867fbc414d9221",
  "8e0152c51b7dde77aef1b6dafde0efe6f5d0b8e1867fbc414d9221276419",
  "8e0152c51b7dde77aef1b6dafde0eff7f7f7e6f5d0b8e1867fbc414d9221276419"
).map(colors);

var _$1 = ramp$1(scheme$o);

var scheme$n = new Array(3).concat(
  "998ec3f7f7f7f1a340",
  "5e3c99b2abd2fdb863e66101",
  "5e3c99b2abd2f7f7f7fdb863e66101",
  "542788998ec3d8daebfee0b6f1a340b35806",
  "542788998ec3d8daebf7f7f7fee0b6f1a340b35806",
  "5427888073acb2abd2d8daebfee0b6fdb863e08214b35806",
  "5427888073acb2abd2d8daebf7f7f7fee0b6fdb863e08214b35806",
  "2d004b5427888073acb2abd2d8daebfee0b6fdb863e08214b358067f3b08",
  "2d004b5427888073acb2abd2d8daebf7f7f7fee0b6fdb863e08214b358067f3b08"
).map(colors);

var w$2 = ramp$1(scheme$n);

var scheme$m = new Array(3).concat(
  "ef8a62f7f7f767a9cf",
  "ca0020f4a58292c5de0571b0",
  "ca0020f4a582f7f7f792c5de0571b0",
  "b2182bef8a62fddbc7d1e5f067a9cf2166ac",
  "b2182bef8a62fddbc7f7f7f7d1e5f067a9cf2166ac",
  "b2182bd6604df4a582fddbc7d1e5f092c5de4393c32166ac",
  "b2182bd6604df4a582fddbc7f7f7f7d1e5f092c5de4393c32166ac",
  "67001fb2182bd6604df4a582fddbc7d1e5f092c5de4393c32166ac053061",
  "67001fb2182bd6604df4a582fddbc7f7f7f7d1e5f092c5de4393c32166ac053061"
).map(colors);

var O$2 = ramp$1(scheme$m);

var scheme$l = new Array(3).concat(
  "ef8a62ffffff999999",
  "ca0020f4a582bababa404040",
  "ca0020f4a582ffffffbababa404040",
  "b2182bef8a62fddbc7e0e0e09999994d4d4d",
  "b2182bef8a62fddbc7ffffffe0e0e09999994d4d4d",
  "b2182bd6604df4a582fddbc7e0e0e0bababa8787874d4d4d",
  "b2182bd6604df4a582fddbc7ffffffe0e0e0bababa8787874d4d4d",
  "67001fb2182bd6604df4a582fddbc7e0e0e0bababa8787874d4d4d1a1a1a",
  "67001fb2182bd6604df4a582fddbc7ffffffe0e0e0bababa8787874d4d4d1a1a1a"
).map(colors);

var k$2 = ramp$1(scheme$l);

var scheme$k = new Array(3).concat(
  "fc8d59ffffbf91bfdb",
  "d7191cfdae61abd9e92c7bb6",
  "d7191cfdae61ffffbfabd9e92c7bb6",
  "d73027fc8d59fee090e0f3f891bfdb4575b4",
  "d73027fc8d59fee090ffffbfe0f3f891bfdb4575b4",
  "d73027f46d43fdae61fee090e0f3f8abd9e974add14575b4",
  "d73027f46d43fdae61fee090ffffbfe0f3f8abd9e974add14575b4",
  "a50026d73027f46d43fdae61fee090e0f3f8abd9e974add14575b4313695",
  "a50026d73027f46d43fdae61fee090ffffbfe0f3f8abd9e974add14575b4313695"
).map(colors);

var z$3 = ramp$1(scheme$k);

var scheme$j = new Array(3).concat(
  "fc8d59ffffbf91cf60",
  "d7191cfdae61a6d96a1a9641",
  "d7191cfdae61ffffbfa6d96a1a9641",
  "d73027fc8d59fee08bd9ef8b91cf601a9850",
  "d73027fc8d59fee08bffffbfd9ef8b91cf601a9850",
  "d73027f46d43fdae61fee08bd9ef8ba6d96a66bd631a9850",
  "d73027f46d43fdae61fee08bffffbfd9ef8ba6d96a66bd631a9850",
  "a50026d73027f46d43fdae61fee08bd9ef8ba6d96a66bd631a9850006837",
  "a50026d73027f46d43fdae61fee08bffffbfd9ef8ba6d96a66bd631a9850006837"
).map(colors);

var A$1 = ramp$1(scheme$j);

var scheme$i = new Array(3).concat(
  "fc8d59ffffbf99d594",
  "d7191cfdae61abdda42b83ba",
  "d7191cfdae61ffffbfabdda42b83ba",
  "d53e4ffc8d59fee08be6f59899d5943288bd",
  "d53e4ffc8d59fee08bffffbfe6f59899d5943288bd",
  "d53e4ff46d43fdae61fee08be6f598abdda466c2a53288bd",
  "d53e4ff46d43fdae61fee08bffffbfe6f598abdda466c2a53288bd",
  "9e0142d53e4ff46d43fdae61fee08be6f598abdda466c2a53288bd5e4fa2",
  "9e0142d53e4ff46d43fdae61fee08bffffbfe6f598abdda466c2a53288bd5e4fa2"
).map(colors);

var j$1 = ramp$1(scheme$i);

var scheme$h = new Array(3).concat(
  "e5f5f999d8c92ca25f",
  "edf8fbb2e2e266c2a4238b45",
  "edf8fbb2e2e266c2a42ca25f006d2c",
  "edf8fbccece699d8c966c2a42ca25f006d2c",
  "edf8fbccece699d8c966c2a441ae76238b45005824",
  "f7fcfde5f5f9ccece699d8c966c2a441ae76238b45005824",
  "f7fcfde5f5f9ccece699d8c966c2a441ae76238b45006d2c00441b"
).map(colors);

var ue = ramp$1(scheme$h);

var scheme$g = new Array(3).concat(
  "e0ecf49ebcda8856a7",
  "edf8fbb3cde38c96c688419d",
  "edf8fbb3cde38c96c68856a7810f7c",
  "edf8fbbfd3e69ebcda8c96c68856a7810f7c",
  "edf8fbbfd3e69ebcda8c96c68c6bb188419d6e016b",
  "f7fcfde0ecf4bfd3e69ebcda8c96c68c6bb188419d6e016b",
  "f7fcfde0ecf4bfd3e69ebcda8c96c68c6bb188419d810f7c4d004b"
).map(colors);

var ae = ramp$1(scheme$g);

var scheme$f = new Array(3).concat(
  "e0f3dba8ddb543a2ca",
  "f0f9e8bae4bc7bccc42b8cbe",
  "f0f9e8bae4bc7bccc443a2ca0868ac",
  "f0f9e8ccebc5a8ddb57bccc443a2ca0868ac",
  "f0f9e8ccebc5a8ddb57bccc44eb3d32b8cbe08589e",
  "f7fcf0e0f3dbccebc5a8ddb57bccc44eb3d32b8cbe08589e",
  "f7fcf0e0f3dbccebc5a8ddb57bccc44eb3d32b8cbe0868ac084081"
).map(colors);

var se = ramp$1(scheme$f);

var scheme$e = new Array(3).concat(
  "fee8c8fdbb84e34a33",
  "fef0d9fdcc8afc8d59d7301f",
  "fef0d9fdcc8afc8d59e34a33b30000",
  "fef0d9fdd49efdbb84fc8d59e34a33b30000",
  "fef0d9fdd49efdbb84fc8d59ef6548d7301f990000",
  "fff7ecfee8c8fdd49efdbb84fc8d59ef6548d7301f990000",
  "fff7ecfee8c8fdd49efdbb84fc8d59ef6548d7301fb300007f0000"
).map(colors);

var ce = ramp$1(scheme$e);

var scheme$d = new Array(3).concat(
  "ece2f0a6bddb1c9099",
  "f6eff7bdc9e167a9cf02818a",
  "f6eff7bdc9e167a9cf1c9099016c59",
  "f6eff7d0d1e6a6bddb67a9cf1c9099016c59",
  "f6eff7d0d1e6a6bddb67a9cf3690c002818a016450",
  "fff7fbece2f0d0d1e6a6bddb67a9cf3690c002818a016450",
  "fff7fbece2f0d0d1e6a6bddb67a9cf3690c002818a016c59014636"
).map(colors);

var le = ramp$1(scheme$d);

var scheme$c = new Array(3).concat(
  "ece7f2a6bddb2b8cbe",
  "f1eef6bdc9e174a9cf0570b0",
  "f1eef6bdc9e174a9cf2b8cbe045a8d",
  "f1eef6d0d1e6a6bddb74a9cf2b8cbe045a8d",
  "f1eef6d0d1e6a6bddb74a9cf3690c00570b0034e7b",
  "fff7fbece7f2d0d1e6a6bddb74a9cf3690c00570b0034e7b",
  "fff7fbece7f2d0d1e6a6bddb74a9cf3690c00570b0045a8d023858"
).map(colors);

var fe$1 = ramp$1(scheme$c);

var scheme$b = new Array(3).concat(
  "e7e1efc994c7dd1c77",
  "f1eef6d7b5d8df65b0ce1256",
  "f1eef6d7b5d8df65b0dd1c77980043",
  "f1eef6d4b9dac994c7df65b0dd1c77980043",
  "f1eef6d4b9dac994c7df65b0e7298ace125691003f",
  "f7f4f9e7e1efd4b9dac994c7df65b0e7298ace125691003f",
  "f7f4f9e7e1efd4b9dac994c7df65b0e7298ace125698004367001f"
).map(colors);

var de = ramp$1(scheme$b);

var scheme$a = new Array(3).concat(
  "fde0ddfa9fb5c51b8a",
  "feebe2fbb4b9f768a1ae017e",
  "feebe2fbb4b9f768a1c51b8a7a0177",
  "feebe2fcc5c0fa9fb5f768a1c51b8a7a0177",
  "feebe2fcc5c0fa9fb5f768a1dd3497ae017e7a0177",
  "fff7f3fde0ddfcc5c0fa9fb5f768a1dd3497ae017e7a0177",
  "fff7f3fde0ddfcc5c0fa9fb5f768a1dd3497ae017e7a017749006a"
).map(colors);

var pe$1 = ramp$1(scheme$a);

var scheme$9 = new Array(3).concat(
  "edf8b17fcdbb2c7fb8",
  "ffffcca1dab441b6c4225ea8",
  "ffffcca1dab441b6c42c7fb8253494",
  "ffffccc7e9b47fcdbb41b6c42c7fb8253494",
  "ffffccc7e9b47fcdbb41b6c41d91c0225ea80c2c84",
  "ffffd9edf8b1c7e9b47fcdbb41b6c41d91c0225ea80c2c84",
  "ffffd9edf8b1c7e9b47fcdbb41b6c41d91c0225ea8253494081d58"
).map(colors);

var me = ramp$1(scheme$9);

var scheme$8 = new Array(3).concat(
  "f7fcb9addd8e31a354",
  "ffffccc2e69978c679238443",
  "ffffccc2e69978c67931a354006837",
  "ffffccd9f0a3addd8e78c67931a354006837",
  "ffffccd9f0a3addd8e78c67941ab5d238443005a32",
  "ffffe5f7fcb9d9f0a3addd8e78c67941ab5d238443005a32",
  "ffffe5f7fcb9d9f0a3addd8e78c67941ab5d238443006837004529"
).map(colors);

var he = ramp$1(scheme$8);

var scheme$7 = new Array(3).concat(
  "fff7bcfec44fd95f0e",
  "ffffd4fed98efe9929cc4c02",
  "ffffd4fed98efe9929d95f0e993404",
  "ffffd4fee391fec44ffe9929d95f0e993404",
  "ffffd4fee391fec44ffe9929ec7014cc4c028c2d04",
  "ffffe5fff7bcfee391fec44ffe9929ec7014cc4c028c2d04",
  "ffffe5fff7bcfee391fec44ffe9929ec7014cc4c02993404662506"
).map(colors);

var ge$1 = ramp$1(scheme$7);

var scheme$6 = new Array(3).concat(
  "ffeda0feb24cf03b20",
  "ffffb2fecc5cfd8d3ce31a1c",
  "ffffb2fecc5cfd8d3cf03b20bd0026",
  "ffffb2fed976feb24cfd8d3cf03b20bd0026",
  "ffffb2fed976feb24cfd8d3cfc4e2ae31a1cb10026",
  "ffffccffeda0fed976feb24cfd8d3cfc4e2ae31a1cb10026",
  "ffffccffeda0fed976feb24cfd8d3cfc4e2ae31a1cbd0026800026"
).map(colors);

var ye = ramp$1(scheme$6);

var scheme$5 = new Array(3).concat(
  "deebf79ecae13182bd",
  "eff3ffbdd7e76baed62171b5",
  "eff3ffbdd7e76baed63182bd08519c",
  "eff3ffc6dbef9ecae16baed63182bd08519c",
  "eff3ffc6dbef9ecae16baed64292c62171b5084594",
  "f7fbffdeebf7c6dbef9ecae16baed64292c62171b5084594",
  "f7fbffdeebf7c6dbef9ecae16baed64292c62171b508519c08306b"
).map(colors);

var J$2 = ramp$1(scheme$5);

var scheme$4 = new Array(3).concat(
  "e5f5e0a1d99b31a354",
  "edf8e9bae4b374c476238b45",
  "edf8e9bae4b374c47631a354006d2c",
  "edf8e9c7e9c0a1d99b74c47631a354006d2c",
  "edf8e9c7e9c0a1d99b74c47641ab5d238b45005a32",
  "f7fcf5e5f5e0c7e9c0a1d99b74c47641ab5d238b45005a32",
  "f7fcf5e5f5e0c7e9c0a1d99b74c47641ab5d238b45006d2c00441b"
).map(colors);

var K$1 = ramp$1(scheme$4);

var scheme$3 = new Array(3).concat(
  "f0f0f0bdbdbd636363",
  "f7f7f7cccccc969696525252",
  "f7f7f7cccccc969696636363252525",
  "f7f7f7d9d9d9bdbdbd969696636363252525",
  "f7f7f7d9d9d9bdbdbd969696737373525252252525",
  "fffffff0f0f0d9d9d9bdbdbd969696737373525252252525",
  "fffffff0f0f0d9d9d9bdbdbd969696737373525252252525000000"
).map(colors);

var L = ramp$1(scheme$3);

var scheme$2 = new Array(3).concat(
  "efedf5bcbddc756bb1",
  "f2f0f7cbc9e29e9ac86a51a3",
  "f2f0f7cbc9e29e9ac8756bb154278f",
  "f2f0f7dadaebbcbddc9e9ac8756bb154278f",
  "f2f0f7dadaebbcbddc9e9ac8807dba6a51a34a1486",
  "fcfbfdefedf5dadaebbcbddc9e9ac8807dba6a51a34a1486",
  "fcfbfdefedf5dadaebbcbddc9e9ac8807dba6a51a354278f3f007d"
).map(colors);

var Q$1 = ramp$1(scheme$2);

var scheme$1 = new Array(3).concat(
  "fee0d2fc9272de2d26",
  "fee5d9fcae91fb6a4acb181d",
  "fee5d9fcae91fb6a4ade2d26a50f15",
  "fee5d9fcbba1fc9272fb6a4ade2d26a50f15",
  "fee5d9fcbba1fc9272fb6a4aef3b2ccb181d99000d",
  "fff5f0fee0d2fcbba1fc9272fb6a4aef3b2ccb181d99000d",
  "fff5f0fee0d2fcbba1fc9272fb6a4aef3b2ccb181da50f1567000d"
).map(colors);

var W$1 = ramp$1(scheme$1);

var scheme = new Array(3).concat(
  "fee6cefdae6be6550d",
  "feeddefdbe85fd8d3cd94701",
  "feeddefdbe85fd8d3ce6550da63603",
  "feeddefdd0a2fdae6bfd8d3ce6550da63603",
  "feeddefdd0a2fdae6bfd8d3cf16913d948018c2d04",
  "fff5ebfee6cefdd0a2fdae6bfd8d3cf16913d948018c2d04",
  "fff5ebfee6cefdd0a2fdae6bfd8d3cf16913d94801a636037f2704"
).map(colors);

var N$2 = ramp$1(scheme);

function ne(t) {
  t = Math.max(0, Math.min(1, t));
  return "rgb("
      + Math.max(0, Math.min(255, Math.round(-4.54 - t * (35.34 - t * (2381.73 - t * (6402.7 - t * (7024.72 - t * 2710.57))))))) + ", "
      + Math.max(0, Math.min(255, Math.round(32.49 + t * (170.73 + t * (52.82 - t * (131.46 - t * (176.58 - t * 67.37))))))) + ", "
      + Math.max(0, Math.min(255, Math.round(81.24 + t * (442.36 - t * (2482.43 - t * (6167.24 - t * (6614.94 - t * 2475.67)))))))
      + ")";
}

var ie = cubehelixLong(cubehelix$1(300, 0.5, 0.0), cubehelix$1(-240, 0.5, 1.0));

var warm = cubehelixLong(cubehelix$1(-100, 0.75, 0.35), cubehelix$1(80, 1.50, 0.8));

var cool = cubehelixLong(cubehelix$1(260, 0.75, 0.35), cubehelix$1(80, 1.50, 0.8));

var c$1 = cubehelix$1();

function be$1(t) {
  if (t < 0 || t > 1) t -= Math.floor(t);
  var ts = Math.abs(t - 0.5);
  c$1.h = 360 * t - 100;
  c$1.s = 1.5 - 1.5 * ts;
  c$1.l = 0.8 - 0.9 * ts;
  return c$1 + "";
}

var c = rgb$1(),
    pi_1_3 = Math.PI / 3,
    pi_2_3 = Math.PI * 2 / 3;

function ve$1(t) {
  var x;
  t = (0.5 - t) * Math.PI;
  c.r = 255 * (x = Math.sin(t)) * x;
  c.g = 255 * (x = Math.sin(t + pi_1_3)) * x;
  c.b = 255 * (x = Math.sin(t + pi_2_3)) * x;
  return c + "";
}

function X(t) {
  t = Math.max(0, Math.min(1, t));
  return "rgb("
      + Math.max(0, Math.min(255, Math.round(34.61 + t * (1172.33 - t * (10793.56 - t * (33300.12 - t * (38394.49 - t * 14825.05))))))) + ", "
      + Math.max(0, Math.min(255, Math.round(23.31 + t * (557.33 + t * (1225.33 - t * (3574.96 - t * (1073.77 + t * 707.56))))))) + ", "
      + Math.max(0, Math.min(255, Math.round(27.2 + t * (3211.1 - t * (15327.97 - t * (27814 - t * (22569.18 - t * 6838.66)))))))
      + ")";
}

function ramp(range) {
  var n = range.length;
  return function(t) {
    return range[Math.max(0, Math.min(n - 1, Math.floor(t * n)))];
  };
}

var Y$2 = ramp(colors("44015444025645045745055946075a46085c460a5d460b5e470d60470e6147106347116447136548146748166848176948186a481a6c481b6d481c6e481d6f481f70482071482173482374482475482576482677482878482979472a7a472c7a472d7b472e7c472f7d46307e46327e46337f463480453581453781453882443983443a83443b84433d84433e85423f854240864241864142874144874045884046883f47883f48893e49893e4a893e4c8a3d4d8a3d4e8a3c4f8a3c508b3b518b3b528b3a538b3a548c39558c39568c38588c38598c375a8c375b8d365c8d365d8d355e8d355f8d34608d34618d33628d33638d32648e32658e31668e31678e31688e30698e306a8e2f6b8e2f6c8e2e6d8e2e6e8e2e6f8e2d708e2d718e2c718e2c728e2c738e2b748e2b758e2a768e2a778e2a788e29798e297a8e297b8e287c8e287d8e277e8e277f8e27808e26818e26828e26828e25838e25848e25858e24868e24878e23888e23898e238a8d228b8d228c8d228d8d218e8d218f8d21908d21918c20928c20928c20938c1f948c1f958b1f968b1f978b1f988b1f998a1f9a8a1e9b8a1e9c891e9d891f9e891f9f881fa0881fa1881fa1871fa28720a38620a48621a58521a68522a78522a88423a98324aa8325ab8225ac8226ad8127ad8128ae8029af7f2ab07f2cb17e2db27d2eb37c2fb47c31b57b32b67a34b67935b77937b87838b9773aba763bbb753dbc743fbc7340bd7242be7144bf7046c06f48c16e4ac16d4cc26c4ec36b50c46a52c56954c56856c66758c7655ac8645cc8635ec96260ca6063cb5f65cb5e67cc5c69cd5b6ccd5a6ece5870cf5773d05675d05477d1537ad1517cd2507fd34e81d34d84d44b86d54989d5488bd6468ed64590d74393d74195d84098d83e9bd93c9dd93ba0da39a2da37a5db36a8db34aadc32addc30b0dd2fb2dd2db5de2bb8de29bade28bddf26c0df25c2df23c5e021c8e020cae11fcde11dd0e11cd2e21bd5e21ad8e219dae319dde318dfe318e2e418e5e419e7e419eae51aece51befe51cf1e51df4e61ef6e620f8e621fbe723fde725"));

var magma = ramp(colors("00000401000501010601010802010902020b02020d03030f03031204041405041606051806051a07061c08071e0907200a08220b09240c09260d0a290e0b2b100b2d110c2f120d31130d34140e36150e38160f3b180f3d19103f1a10421c10441d11471e114920114b21114e22115024125325125527125829115a2a115c2c115f2d11612f116331116533106734106936106b38106c390f6e3b0f703d0f713f0f72400f74420f75440f764510774710784910784a10794c117a4e117b4f127b51127c52137c54137d56147d57157e59157e5a167e5c167f5d177f5f187f601880621980641a80651a80671b80681c816a1c816b1d816d1d816e1e81701f81721f817320817521817621817822817922827b23827c23827e24828025828125818326818426818627818827818928818b29818c29818e2a81902a81912b81932b80942c80962c80982d80992d809b2e7f9c2e7f9e2f7fa02f7fa1307ea3307ea5317ea6317da8327daa337dab337cad347cae347bb0357bb2357bb3367ab5367ab73779b83779ba3878bc3978bd3977bf3a77c03a76c23b75c43c75c53c74c73d73c83e73ca3e72cc3f71cd4071cf4070d0416fd2426fd3436ed5446dd6456cd8456cd9466bdb476adc4869de4968df4a68e04c67e24d66e34e65e44f64e55064e75263e85362e95462ea5661eb5760ec5860ed5a5fee5b5eef5d5ef05f5ef1605df2625df2645cf3655cf4675cf4695cf56b5cf66c5cf66e5cf7705cf7725cf8745cf8765cf9785df9795df97b5dfa7d5efa7f5efa815ffb835ffb8560fb8761fc8961fc8a62fc8c63fc8e64fc9065fd9266fd9467fd9668fd9869fd9a6afd9b6bfe9d6cfe9f6dfea16efea36ffea571fea772fea973feaa74feac76feae77feb078feb27afeb47bfeb67cfeb77efeb97ffebb81febd82febf84fec185fec287fec488fec68afec88cfeca8dfecc8ffecd90fecf92fed194fed395fed597fed799fed89afdda9cfddc9efddea0fde0a1fde2a3fde3a5fde5a7fde7a9fde9aafdebacfcecaefceeb0fcf0b2fcf2b4fcf4b6fcf6b8fcf7b9fcf9bbfcfbbdfcfdbf"));

var inferno = ramp(colors("00000401000501010601010802010a02020c02020e03021004031204031405041706041907051b08051d09061f0a07220b07240c08260d08290e092b10092d110a30120a32140b34150b37160b39180c3c190c3e1b0c411c0c431e0c451f0c48210c4a230c4c240c4f260c51280b53290b552b0b572d0b592f0a5b310a5c320a5e340a5f3609613809623909633b09643d09653e0966400a67420a68440a68450a69470b6a490b6a4a0c6b4c0c6b4d0d6c4f0d6c510e6c520e6d540f6d550f6d57106e59106e5a116e5c126e5d126e5f136e61136e62146e64156e65156e67166e69166e6a176e6c186e6d186e6f196e71196e721a6e741a6e751b6e771c6d781c6d7a1d6d7c1d6d7d1e6d7f1e6c801f6c82206c84206b85216b87216b88226a8a226a8c23698d23698f24699025689225689326679526679727669827669a28659b29649d29649f2a63a02a63a22b62a32c61a52c60a62d60a82e5fa92e5eab2f5ead305dae305cb0315bb1325ab3325ab43359b63458b73557b93556ba3655bc3754bd3853bf3952c03a51c13a50c33b4fc43c4ec63d4dc73e4cc83f4bca404acb4149cc4248ce4347cf4446d04545d24644d34743d44842d54a41d74b3fd84c3ed94d3dda4e3cdb503bdd513ade5238df5337e05536e15635e25734e35933e45a31e55c30e65d2fe75e2ee8602de9612bea632aeb6429eb6628ec6726ed6925ee6a24ef6c23ef6e21f06f20f1711ff1731df2741cf3761bf37819f47918f57b17f57d15f67e14f68013f78212f78410f8850ff8870ef8890cf98b0bf98c0af98e09fa9008fa9207fa9407fb9606fb9706fb9906fb9b06fb9d07fc9f07fca108fca309fca50afca60cfca80dfcaa0ffcac11fcae12fcb014fcb216fcb418fbb61afbb81dfbba1ffbbc21fbbe23fac026fac228fac42afac62df9c72ff9c932f9cb35f8cd37f8cf3af7d13df7d340f6d543f6d746f5d949f5db4cf4dd4ff4df53f4e156f3e35af3e55df2e661f2e865f2ea69f1ec6df1ed71f1ef75f1f179f2f27df2f482f3f586f3f68af4f88ef5f992f6fa96f8fb9af9fc9dfafda1fcffa4"));

var plasma = ramp(colors("0d088710078813078916078a19068c1b068d1d068e20068f2206902406912605912805922a05932c05942e05952f059631059733059735049837049938049a3a049a3c049b3e049c3f049c41049d43039e44039e46039f48039f4903a04b03a14c02a14e02a25002a25102a35302a35502a45601a45801a45901a55b01a55c01a65e01a66001a66100a76300a76400a76600a76700a86900a86a00a86c00a86e00a86f00a87100a87201a87401a87501a87701a87801a87a02a87b02a87d03a87e03a88004a88104a78305a78405a78606a68707a68808a68a09a58b0aa58d0ba58e0ca48f0da4910ea3920fa39410a29511a19613a19814a099159f9a169f9c179e9d189d9e199da01a9ca11b9ba21d9aa31e9aa51f99a62098a72197a82296aa2395ab2494ac2694ad2793ae2892b02991b12a90b22b8fb32c8eb42e8db52f8cb6308bb7318ab83289ba3388bb3488bc3587bd3786be3885bf3984c03a83c13b82c23c81c33d80c43e7fc5407ec6417dc7427cc8437bc9447aca457acb4679cc4778cc4977cd4a76ce4b75cf4c74d04d73d14e72d24f71d35171d45270d5536fd5546ed6556dd7566cd8576bd9586ada5a6ada5b69db5c68dc5d67dd5e66de5f65de6164df6263e06363e16462e26561e26660e3685fe4695ee56a5de56b5de66c5ce76e5be76f5ae87059e97158e97257ea7457eb7556eb7655ec7754ed7953ed7a52ee7b51ef7c51ef7e50f07f4ff0804ef1814df1834cf2844bf3854bf3874af48849f48948f58b47f58c46f68d45f68f44f79044f79143f79342f89441f89540f9973ff9983ef99a3efa9b3dfa9c3cfa9e3bfb9f3afba139fba238fca338fca537fca636fca835fca934fdab33fdac33fdae32fdaf31fdb130fdb22ffdb42ffdb52efeb72dfeb82cfeba2cfebb2bfebd2afebe2afec029fdc229fdc328fdc527fdc627fdc827fdca26fdcb26fccd25fcce25fcd025fcd225fbd324fbd524fbd724fad824fada24f9dc24f9dd25f8df25f8e125f7e225f7e425f6e626f6e826f5e926f5eb27f4ed27f3ee27f3f027f2f227f1f426f1f525f0f724f0f921"));

/** Used to stand-in for `undefined` hash values. */

var HASH_UNDEFINED = '__lodash_hash_undefined__';

/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */
function setCacheAdd$1(value) {
  this.__data__.set(value, HASH_UNDEFINED);
  return this;
}

var _setCacheAdd = setCacheAdd$1;

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {boolean} Returns `true` if `value` is found, else `false`.
 */

function setCacheHas$1(value) {
  return this.__data__.has(value);
}

var _setCacheHas = setCacheHas$1;

var MapCache = _MapCache,
    setCacheAdd = _setCacheAdd,
    setCacheHas = _setCacheHas;

/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */
function SetCache$2(values) {
  var index = -1,
      length = values == null ? 0 : values.length;

  this.__data__ = new MapCache;
  while (++index < length) {
    this.add(values[index]);
  }
}

// Add methods to `SetCache`.
SetCache$2.prototype.add = SetCache$2.prototype.push = setCacheAdd;
SetCache$2.prototype.has = setCacheHas;

var _SetCache = SetCache$2;

/**
 * The base implementation of `_.findIndex` and `_.findLastIndex` without
 * support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} predicate The function invoked per iteration.
 * @param {number} fromIndex The index to search from.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */

function baseFindIndex$1(array, predicate, fromIndex, fromRight) {
  var length = array.length,
      index = fromIndex + (fromRight ? 1 : -1);

  while ((fromRight ? index-- : ++index < length)) {
    if (predicate(array[index], index, array)) {
      return index;
    }
  }
  return -1;
}

var _baseFindIndex = baseFindIndex$1;

/**
 * The base implementation of `_.isNaN` without support for number objects.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
 */

function baseIsNaN$1(value) {
  return value !== value;
}

var _baseIsNaN = baseIsNaN$1;

/**
 * A specialized version of `_.indexOf` which performs strict equality
 * comparisons of values, i.e. `===`.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */

function strictIndexOf$1(array, value, fromIndex) {
  var index = fromIndex - 1,
      length = array.length;

  while (++index < length) {
    if (array[index] === value) {
      return index;
    }
  }
  return -1;
}

var _strictIndexOf = strictIndexOf$1;

var baseFindIndex = _baseFindIndex,
    baseIsNaN = _baseIsNaN,
    strictIndexOf = _strictIndexOf;

/**
 * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseIndexOf$1(array, value, fromIndex) {
  return value === value
    ? strictIndexOf(array, value, fromIndex)
    : baseFindIndex(array, baseIsNaN, fromIndex);
}

var _baseIndexOf = baseIndexOf$1;

var baseIndexOf = _baseIndexOf;

/**
 * A specialized version of `_.includes` for arrays without support for
 * specifying an index to search from.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */
function arrayIncludes$1(array, value) {
  var length = array == null ? 0 : array.length;
  return !!length && baseIndexOf(array, value, 0) > -1;
}

var _arrayIncludes = arrayIncludes$1;

/**
 * This function is like `arrayIncludes` except that it accepts a comparator.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @param {Function} comparator The comparator invoked per element.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */

function arrayIncludesWith$1(array, value, comparator) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (comparator(value, array[index])) {
      return true;
    }
  }
  return false;
}

var _arrayIncludesWith = arrayIncludesWith$1;

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function cacheHas$2(cache, key) {
  return cache.has(key);
}

var _cacheHas = cacheHas$2;

var SetCache$1 = _SetCache,
    arrayIncludes = _arrayIncludes,
    arrayIncludesWith = _arrayIncludesWith,
    arrayMap$2 = _arrayMap,
    baseUnary$3 = _baseUnary,
    cacheHas$1 = _cacheHas;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * The base implementation of methods like `_.difference` without support
 * for excluding multiple arrays or iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Array} values The values to exclude.
 * @param {Function} [iteratee] The iteratee invoked per element.
 * @param {Function} [comparator] The comparator invoked per element.
 * @returns {Array} Returns the new array of filtered values.
 */
function baseDifference$1(array, values, iteratee, comparator) {
  var index = -1,
      includes = arrayIncludes,
      isCommon = true,
      length = array.length,
      result = [],
      valuesLength = values.length;

  if (!length) {
    return result;
  }
  if (iteratee) {
    values = arrayMap$2(values, baseUnary$3(iteratee));
  }
  if (comparator) {
    includes = arrayIncludesWith;
    isCommon = false;
  }
  else if (values.length >= LARGE_ARRAY_SIZE) {
    includes = cacheHas$1;
    isCommon = false;
    values = new SetCache$1(values);
  }
  outer:
  while (++index < length) {
    var value = array[index],
        computed = iteratee == null ? value : iteratee(value);

    value = (comparator || value !== 0) ? value : 0;
    if (isCommon && computed === computed) {
      var valuesIndex = valuesLength;
      while (valuesIndex--) {
        if (values[valuesIndex] === computed) {
          continue outer;
        }
      }
      result.push(value);
    }
    else if (!includes(values, computed, comparator)) {
      result.push(value);
    }
  }
  return result;
}

var _baseDifference = baseDifference$1;

var baseDifference = _baseDifference,
    baseRest$1 = _baseRest,
    isArrayLikeObject = isArrayLikeObject_1;

/**
 * Creates an array excluding all given values using
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * **Note:** Unlike `_.pull`, this method returns a new array.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {...*} [values] The values to exclude.
 * @returns {Array} Returns the new array of filtered values.
 * @see _.difference, _.xor
 * @example
 *
 * _.without([2, 1, 2, 3], 1, 2);
 * // => [3]
 */
var without = baseRest$1(function(array, values) {
  return isArrayLikeObject(array)
    ? baseDifference(array, values)
    : [];
});

var without_1 = without;

var epsilon = 1e-12;

function Linear(context) {
  this._context = context;
}

Linear.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._point = 0;
  },
  lineEnd: function() {
    if (this._line || (this._line !== 0 && this._point === 1)) this._context.closePath();
    this._line = 1 - this._line;
  },
  point: function(x, y) {
    x = +x, y = +y;
    switch (this._point) {
      case 0: this._point = 1; this._line ? this._context.lineTo(x, y) : this._context.moveTo(x, y); break;
      case 1: this._point = 2; // proceed
      default: this._context.lineTo(x, y); break;
    }
  }
};

function dr(context) {
  return new Linear(context);
}

function noop$3() {}

function point$3(that, x, y) {
  that._context.bezierCurveTo(
    (2 * that._x0 + that._x1) / 3,
    (2 * that._y0 + that._y1) / 3,
    (that._x0 + 2 * that._x1) / 3,
    (that._y0 + 2 * that._y1) / 3,
    (that._x0 + 4 * that._x1 + x) / 6,
    (that._y0 + 4 * that._y1 + y) / 6
  );
}

function Basis(context) {
  this._context = context;
}

Basis.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._x0 = this._x1 =
    this._y0 = this._y1 = NaN;
    this._point = 0;
  },
  lineEnd: function() {
    switch (this._point) {
      case 3: point$3(this, this._x1, this._y1); // proceed
      case 2: this._context.lineTo(this._x1, this._y1); break;
    }
    if (this._line || (this._line !== 0 && this._point === 1)) this._context.closePath();
    this._line = 1 - this._line;
  },
  point: function(x, y) {
    x = +x, y = +y;
    switch (this._point) {
      case 0: this._point = 1; this._line ? this._context.lineTo(x, y) : this._context.moveTo(x, y); break;
      case 1: this._point = 2; break;
      case 2: this._point = 3; this._context.lineTo((5 * this._x0 + this._x1) / 6, (5 * this._y0 + this._y1) / 6); // proceed
      default: point$3(this, x, y); break;
    }
    this._x0 = this._x1, this._x1 = x;
    this._y0 = this._y1, this._y1 = y;
  }
};

function $e$1(context) {
  return new Basis(context);
}

function BasisClosed(context) {
  this._context = context;
}

BasisClosed.prototype = {
  areaStart: noop$3,
  areaEnd: noop$3,
  lineStart: function() {
    this._x0 = this._x1 = this._x2 = this._x3 = this._x4 =
    this._y0 = this._y1 = this._y2 = this._y3 = this._y4 = NaN;
    this._point = 0;
  },
  lineEnd: function() {
    switch (this._point) {
      case 1: {
        this._context.moveTo(this._x2, this._y2);
        this._context.closePath();
        break;
      }
      case 2: {
        this._context.moveTo((this._x2 + 2 * this._x3) / 3, (this._y2 + 2 * this._y3) / 3);
        this._context.lineTo((this._x3 + 2 * this._x2) / 3, (this._y3 + 2 * this._y2) / 3);
        this._context.closePath();
        break;
      }
      case 3: {
        this.point(this._x2, this._y2);
        this.point(this._x3, this._y3);
        this.point(this._x4, this._y4);
        break;
      }
    }
  },
  point: function(x, y) {
    x = +x, y = +y;
    switch (this._point) {
      case 0: this._point = 1; this._x2 = x, this._y2 = y; break;
      case 1: this._point = 2; this._x3 = x, this._y3 = y; break;
      case 2: this._point = 3; this._x4 = x, this._y4 = y; this._context.moveTo((this._x0 + 4 * this._x1 + x) / 6, (this._y0 + 4 * this._y1 + y) / 6); break;
      default: point$3(this, x, y); break;
    }
    this._x0 = this._x1, this._x1 = x;
    this._y0 = this._y1, this._y1 = y;
  }
};

function er$1(context) {
  return new BasisClosed(context);
}

function BasisOpen(context) {
  this._context = context;
}

BasisOpen.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._x0 = this._x1 =
    this._y0 = this._y1 = NaN;
    this._point = 0;
  },
  lineEnd: function() {
    if (this._line || (this._line !== 0 && this._point === 3)) this._context.closePath();
    this._line = 1 - this._line;
  },
  point: function(x, y) {
    x = +x, y = +y;
    switch (this._point) {
      case 0: this._point = 1; break;
      case 1: this._point = 2; break;
      case 2: this._point = 3; var x0 = (this._x0 + 4 * this._x1 + x) / 6, y0 = (this._y0 + 4 * this._y1 + y) / 6; this._line ? this._context.lineTo(x0, y0) : this._context.moveTo(x0, y0); break;
      case 3: this._point = 4; // proceed
      default: point$3(this, x, y); break;
    }
    this._x0 = this._x1, this._x1 = x;
    this._y0 = this._y1, this._y1 = y;
  }
};

function rr(context) {
  return new BasisOpen(context);
}

function Bundle(context, beta) {
  this._basis = new Basis(context);
  this._beta = beta;
}

Bundle.prototype = {
  lineStart: function() {
    this._x = [];
    this._y = [];
    this._basis.lineStart();
  },
  lineEnd: function() {
    var x = this._x,
        y = this._y,
        j = x.length - 1;

    if (j > 0) {
      var x0 = x[0],
          y0 = y[0],
          dx = x[j] - x0,
          dy = y[j] - y0,
          i = -1,
          t;

      while (++i <= j) {
        t = i / j;
        this._basis.point(
          this._beta * x[i] + (1 - this._beta) * (x0 + t * dx),
          this._beta * y[i] + (1 - this._beta) * (y0 + t * dy)
        );
      }
    }

    this._x = this._y = null;
    this._basis.lineEnd();
  },
  point: function(x, y) {
    this._x.push(+x);
    this._y.push(+y);
  }
};

var tr$1 = (function custom(beta) {

  function bundle(context) {
    return beta === 1 ? new Basis(context) : new Bundle(context, beta);
  }

  bundle.beta = function(beta) {
    return custom(+beta);
  };

  return bundle;
})(0.85);

function point$2(that, x, y) {
  that._context.bezierCurveTo(
    that._x1 + that._k * (that._x2 - that._x0),
    that._y1 + that._k * (that._y2 - that._y0),
    that._x2 + that._k * (that._x1 - x),
    that._y2 + that._k * (that._y1 - y),
    that._x2,
    that._y2
  );
}

function Cardinal(context, tension) {
  this._context = context;
  this._k = (1 - tension) / 6;
}

Cardinal.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._x0 = this._x1 = this._x2 =
    this._y0 = this._y1 = this._y2 = NaN;
    this._point = 0;
  },
  lineEnd: function() {
    switch (this._point) {
      case 2: this._context.lineTo(this._x2, this._y2); break;
      case 3: point$2(this, this._x1, this._y1); break;
    }
    if (this._line || (this._line !== 0 && this._point === 1)) this._context.closePath();
    this._line = 1 - this._line;
  },
  point: function(x, y) {
    x = +x, y = +y;
    switch (this._point) {
      case 0: this._point = 1; this._line ? this._context.lineTo(x, y) : this._context.moveTo(x, y); break;
      case 1: this._point = 2; this._x1 = x, this._y1 = y; break;
      case 2: this._point = 3; // proceed
      default: point$2(this, x, y); break;
    }
    this._x0 = this._x1, this._x1 = this._x2, this._x2 = x;
    this._y0 = this._y1, this._y1 = this._y2, this._y2 = y;
  }
};

var ir$1 = (function custom(tension) {

  function cardinal(context) {
    return new Cardinal(context, tension);
  }

  cardinal.tension = function(tension) {
    return custom(+tension);
  };

  return cardinal;
})(0);

function CardinalClosed(context, tension) {
  this._context = context;
  this._k = (1 - tension) / 6;
}

CardinalClosed.prototype = {
  areaStart: noop$3,
  areaEnd: noop$3,
  lineStart: function() {
    this._x0 = this._x1 = this._x2 = this._x3 = this._x4 = this._x5 =
    this._y0 = this._y1 = this._y2 = this._y3 = this._y4 = this._y5 = NaN;
    this._point = 0;
  },
  lineEnd: function() {
    switch (this._point) {
      case 1: {
        this._context.moveTo(this._x3, this._y3);
        this._context.closePath();
        break;
      }
      case 2: {
        this._context.lineTo(this._x3, this._y3);
        this._context.closePath();
        break;
      }
      case 3: {
        this.point(this._x3, this._y3);
        this.point(this._x4, this._y4);
        this.point(this._x5, this._y5);
        break;
      }
    }
  },
  point: function(x, y) {
    x = +x, y = +y;
    switch (this._point) {
      case 0: this._point = 1; this._x3 = x, this._y3 = y; break;
      case 1: this._point = 2; this._context.moveTo(this._x4 = x, this._y4 = y); break;
      case 2: this._point = 3; this._x5 = x, this._y5 = y; break;
      default: point$2(this, x, y); break;
    }
    this._x0 = this._x1, this._x1 = this._x2, this._x2 = x;
    this._y0 = this._y1, this._y1 = this._y2, this._y2 = y;
  }
};

var nr$1 = (function custom(tension) {

  function cardinal(context) {
    return new CardinalClosed(context, tension);
  }

  cardinal.tension = function(tension) {
    return custom(+tension);
  };

  return cardinal;
})(0);

function CardinalOpen(context, tension) {
  this._context = context;
  this._k = (1 - tension) / 6;
}

CardinalOpen.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._x0 = this._x1 = this._x2 =
    this._y0 = this._y1 = this._y2 = NaN;
    this._point = 0;
  },
  lineEnd: function() {
    if (this._line || (this._line !== 0 && this._point === 3)) this._context.closePath();
    this._line = 1 - this._line;
  },
  point: function(x, y) {
    x = +x, y = +y;
    switch (this._point) {
      case 0: this._point = 1; break;
      case 1: this._point = 2; break;
      case 2: this._point = 3; this._line ? this._context.lineTo(this._x2, this._y2) : this._context.moveTo(this._x2, this._y2); break;
      case 3: this._point = 4; // proceed
      default: point$2(this, x, y); break;
    }
    this._x0 = this._x1, this._x1 = this._x2, this._x2 = x;
    this._y0 = this._y1, this._y1 = this._y2, this._y2 = y;
  }
};

var or = (function custom(tension) {

  function cardinal(context) {
    return new CardinalOpen(context, tension);
  }

  cardinal.tension = function(tension) {
    return custom(+tension);
  };

  return cardinal;
})(0);

function point$1(that, x, y) {
  var x1 = that._x1,
      y1 = that._y1,
      x2 = that._x2,
      y2 = that._y2;

  if (that._l01_a > epsilon) {
    var a = 2 * that._l01_2a + 3 * that._l01_a * that._l12_a + that._l12_2a,
        n = 3 * that._l01_a * (that._l01_a + that._l12_a);
    x1 = (x1 * a - that._x0 * that._l12_2a + that._x2 * that._l01_2a) / n;
    y1 = (y1 * a - that._y0 * that._l12_2a + that._y2 * that._l01_2a) / n;
  }

  if (that._l23_a > epsilon) {
    var b = 2 * that._l23_2a + 3 * that._l23_a * that._l12_a + that._l12_2a,
        m = 3 * that._l23_a * (that._l23_a + that._l12_a);
    x2 = (x2 * b + that._x1 * that._l23_2a - x * that._l12_2a) / m;
    y2 = (y2 * b + that._y1 * that._l23_2a - y * that._l12_2a) / m;
  }

  that._context.bezierCurveTo(x1, y1, x2, y2, that._x2, that._y2);
}

function CatmullRom(context, alpha) {
  this._context = context;
  this._alpha = alpha;
}

CatmullRom.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._x0 = this._x1 = this._x2 =
    this._y0 = this._y1 = this._y2 = NaN;
    this._l01_a = this._l12_a = this._l23_a =
    this._l01_2a = this._l12_2a = this._l23_2a =
    this._point = 0;
  },
  lineEnd: function() {
    switch (this._point) {
      case 2: this._context.lineTo(this._x2, this._y2); break;
      case 3: this.point(this._x2, this._y2); break;
    }
    if (this._line || (this._line !== 0 && this._point === 1)) this._context.closePath();
    this._line = 1 - this._line;
  },
  point: function(x, y) {
    x = +x, y = +y;

    if (this._point) {
      var x23 = this._x2 - x,
          y23 = this._y2 - y;
      this._l23_a = Math.sqrt(this._l23_2a = Math.pow(x23 * x23 + y23 * y23, this._alpha));
    }

    switch (this._point) {
      case 0: this._point = 1; this._line ? this._context.lineTo(x, y) : this._context.moveTo(x, y); break;
      case 1: this._point = 2; break;
      case 2: this._point = 3; // proceed
      default: point$1(this, x, y); break;
    }

    this._l01_a = this._l12_a, this._l12_a = this._l23_a;
    this._l01_2a = this._l12_2a, this._l12_2a = this._l23_2a;
    this._x0 = this._x1, this._x1 = this._x2, this._x2 = x;
    this._y0 = this._y1, this._y1 = this._y2, this._y2 = y;
  }
};

var ar = (function custom(alpha) {

  function catmullRom(context) {
    return alpha ? new CatmullRom(context, alpha) : new Cardinal(context, 0);
  }

  catmullRom.alpha = function(alpha) {
    return custom(+alpha);
  };

  return catmullRom;
})(0.5);

function CatmullRomClosed(context, alpha) {
  this._context = context;
  this._alpha = alpha;
}

CatmullRomClosed.prototype = {
  areaStart: noop$3,
  areaEnd: noop$3,
  lineStart: function() {
    this._x0 = this._x1 = this._x2 = this._x3 = this._x4 = this._x5 =
    this._y0 = this._y1 = this._y2 = this._y3 = this._y4 = this._y5 = NaN;
    this._l01_a = this._l12_a = this._l23_a =
    this._l01_2a = this._l12_2a = this._l23_2a =
    this._point = 0;
  },
  lineEnd: function() {
    switch (this._point) {
      case 1: {
        this._context.moveTo(this._x3, this._y3);
        this._context.closePath();
        break;
      }
      case 2: {
        this._context.lineTo(this._x3, this._y3);
        this._context.closePath();
        break;
      }
      case 3: {
        this.point(this._x3, this._y3);
        this.point(this._x4, this._y4);
        this.point(this._x5, this._y5);
        break;
      }
    }
  },
  point: function(x, y) {
    x = +x, y = +y;

    if (this._point) {
      var x23 = this._x2 - x,
          y23 = this._y2 - y;
      this._l23_a = Math.sqrt(this._l23_2a = Math.pow(x23 * x23 + y23 * y23, this._alpha));
    }

    switch (this._point) {
      case 0: this._point = 1; this._x3 = x, this._y3 = y; break;
      case 1: this._point = 2; this._context.moveTo(this._x4 = x, this._y4 = y); break;
      case 2: this._point = 3; this._x5 = x, this._y5 = y; break;
      default: point$1(this, x, y); break;
    }

    this._l01_a = this._l12_a, this._l12_a = this._l23_a;
    this._l01_2a = this._l12_2a, this._l12_2a = this._l23_2a;
    this._x0 = this._x1, this._x1 = this._x2, this._x2 = x;
    this._y0 = this._y1, this._y1 = this._y2, this._y2 = y;
  }
};

var sr$1 = (function custom(alpha) {

  function catmullRom(context) {
    return alpha ? new CatmullRomClosed(context, alpha) : new CardinalClosed(context, 0);
  }

  catmullRom.alpha = function(alpha) {
    return custom(+alpha);
  };

  return catmullRom;
})(0.5);

function CatmullRomOpen(context, alpha) {
  this._context = context;
  this._alpha = alpha;
}

CatmullRomOpen.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._x0 = this._x1 = this._x2 =
    this._y0 = this._y1 = this._y2 = NaN;
    this._l01_a = this._l12_a = this._l23_a =
    this._l01_2a = this._l12_2a = this._l23_2a =
    this._point = 0;
  },
  lineEnd: function() {
    if (this._line || (this._line !== 0 && this._point === 3)) this._context.closePath();
    this._line = 1 - this._line;
  },
  point: function(x, y) {
    x = +x, y = +y;

    if (this._point) {
      var x23 = this._x2 - x,
          y23 = this._y2 - y;
      this._l23_a = Math.sqrt(this._l23_2a = Math.pow(x23 * x23 + y23 * y23, this._alpha));
    }

    switch (this._point) {
      case 0: this._point = 1; break;
      case 1: this._point = 2; break;
      case 2: this._point = 3; this._line ? this._context.lineTo(this._x2, this._y2) : this._context.moveTo(this._x2, this._y2); break;
      case 3: this._point = 4; // proceed
      default: point$1(this, x, y); break;
    }

    this._l01_a = this._l12_a, this._l12_a = this._l23_a;
    this._l01_2a = this._l12_2a, this._l12_2a = this._l23_2a;
    this._x0 = this._x1, this._x1 = this._x2, this._x2 = x;
    this._y0 = this._y1, this._y1 = this._y2, this._y2 = y;
  }
};

var lr$1 = (function custom(alpha) {

  function catmullRom(context) {
    return alpha ? new CatmullRomOpen(context, alpha) : new CardinalOpen(context, 0);
  }

  catmullRom.alpha = function(alpha) {
    return custom(+alpha);
  };

  return catmullRom;
})(0.5);

function LinearClosed(context) {
  this._context = context;
}

LinearClosed.prototype = {
  areaStart: noop$3,
  areaEnd: noop$3,
  lineStart: function() {
    this._point = 0;
  },
  lineEnd: function() {
    if (this._point) this._context.closePath();
  },
  point: function(x, y) {
    x = +x, y = +y;
    if (this._point) this._context.lineTo(x, y);
    else this._point = 1, this._context.moveTo(x, y);
  }
};

function ur$1(context) {
  return new LinearClosed(context);
}

function sign(x) {
  return x < 0 ? -1 : 1;
}

// Calculate the slopes of the tangents (Hermite-type interpolation) based on
// the following paper: Steffen, M. 1990. A Simple Method for Monotonic
// Interpolation in One Dimension. Astronomy and Astrophysics, Vol. 239, NO.
// NOV(II), P. 443, 1990.
function slope3(that, x2, y2) {
  var h0 = that._x1 - that._x0,
      h1 = x2 - that._x1,
      s0 = (that._y1 - that._y0) / (h0 || h1 < 0 && -0),
      s1 = (y2 - that._y1) / (h1 || h0 < 0 && -0),
      p = (s0 * h1 + s1 * h0) / (h0 + h1);
  return (sign(s0) + sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p)) || 0;
}

// Calculate a one-sided slope.
function slope2(that, t) {
  var h = that._x1 - that._x0;
  return h ? (3 * (that._y1 - that._y0) / h - t) / 2 : t;
}

// According to https://en.wikipedia.org/wiki/Cubic_Hermite_spline#Representations
// "you can express cubic Hermite interpolation in terms of cubic Bézier curves
// with respect to the four values p0, p0 + m0 / 3, p1 - m1 / 3, p1".
function point(that, t0, t1) {
  var x0 = that._x0,
      y0 = that._y0,
      x1 = that._x1,
      y1 = that._y1,
      dx = (x1 - x0) / 3;
  that._context.bezierCurveTo(x0 + dx, y0 + dx * t0, x1 - dx, y1 - dx * t1, x1, y1);
}

function MonotoneX(context) {
  this._context = context;
}

MonotoneX.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._x0 = this._x1 =
    this._y0 = this._y1 =
    this._t0 = NaN;
    this._point = 0;
  },
  lineEnd: function() {
    switch (this._point) {
      case 2: this._context.lineTo(this._x1, this._y1); break;
      case 3: point(this, this._t0, slope2(this, this._t0)); break;
    }
    if (this._line || (this._line !== 0 && this._point === 1)) this._context.closePath();
    this._line = 1 - this._line;
  },
  point: function(x, y) {
    var t1 = NaN;

    x = +x, y = +y;
    if (x === this._x1 && y === this._y1) return; // Ignore coincident points.
    switch (this._point) {
      case 0: this._point = 1; this._line ? this._context.lineTo(x, y) : this._context.moveTo(x, y); break;
      case 1: this._point = 2; break;
      case 2: this._point = 3; point(this, slope2(this, t1 = slope3(this, x, y)), t1); break;
      default: point(this, this._t0, t1 = slope3(this, x, y)); break;
    }

    this._x0 = this._x1, this._x1 = x;
    this._y0 = this._y1, this._y1 = y;
    this._t0 = t1;
  }
};

function MonotoneY(context) {
  this._context = new ReflectContext(context);
}

(MonotoneY.prototype = Object.create(MonotoneX.prototype)).point = function(x, y) {
  MonotoneX.prototype.point.call(this, y, x);
};

function ReflectContext(context) {
  this._context = context;
}

ReflectContext.prototype = {
  moveTo: function(x, y) { this._context.moveTo(y, x); },
  closePath: function() { this._context.closePath(); },
  lineTo: function(x, y) { this._context.lineTo(y, x); },
  bezierCurveTo: function(x1, y1, x2, y2, x, y) { this._context.bezierCurveTo(y1, x1, y2, x2, y, x); }
};

function monotoneX(context) {
  return new MonotoneX(context);
}

function monotoneY(context) {
  return new MonotoneY(context);
}

function Natural(context) {
  this._context = context;
}

Natural.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._x = [];
    this._y = [];
  },
  lineEnd: function() {
    var x = this._x,
        y = this._y,
        n = x.length;

    if (n) {
      this._line ? this._context.lineTo(x[0], y[0]) : this._context.moveTo(x[0], y[0]);
      if (n === 2) {
        this._context.lineTo(x[1], y[1]);
      } else {
        var px = controlPoints(x),
            py = controlPoints(y);
        for (var i0 = 0, i1 = 1; i1 < n; ++i0, ++i1) {
          this._context.bezierCurveTo(px[0][i0], py[0][i0], px[1][i0], py[1][i0], x[i1], y[i1]);
        }
      }
    }

    if (this._line || (this._line !== 0 && n === 1)) this._context.closePath();
    this._line = 1 - this._line;
    this._x = this._y = null;
  },
  point: function(x, y) {
    this._x.push(+x);
    this._y.push(+y);
  }
};

// See https://www.particleincell.com/2012/bezier-splines/ for derivation.
function controlPoints(x) {
  var i,
      n = x.length - 1,
      m,
      a = new Array(n),
      b = new Array(n),
      r = new Array(n);
  a[0] = 0, b[0] = 2, r[0] = x[0] + 2 * x[1];
  for (i = 1; i < n - 1; ++i) a[i] = 1, b[i] = 4, r[i] = 4 * x[i] + 2 * x[i + 1];
  a[n - 1] = 2, b[n - 1] = 7, r[n - 1] = 8 * x[n - 1] + x[n];
  for (i = 1; i < n; ++i) m = a[i] / b[i - 1], b[i] -= m, r[i] -= m * r[i - 1];
  a[n - 1] = r[n - 1] / b[n - 1];
  for (i = n - 2; i >= 0; --i) a[i] = (r[i] - a[i + 1]) / b[i];
  b[n - 1] = (x[n] + a[n - 1]) / 2;
  for (i = 0; i < n - 1; ++i) b[i] = 2 * x[i + 1] - a[i + 1];
  return [a, b];
}

function pr(context) {
  return new Natural(context);
}

function Step(context, t) {
  this._context = context;
  this._t = t;
}

Step.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._x = this._y = NaN;
    this._point = 0;
  },
  lineEnd: function() {
    if (0 < this._t && this._t < 1 && this._point === 2) this._context.lineTo(this._x, this._y);
    if (this._line || (this._line !== 0 && this._point === 1)) this._context.closePath();
    if (this._line >= 0) this._t = 1 - this._t, this._line = 1 - this._line;
  },
  point: function(x, y) {
    x = +x, y = +y;
    switch (this._point) {
      case 0: this._point = 1; this._line ? this._context.lineTo(x, y) : this._context.moveTo(x, y); break;
      case 1: this._point = 2; // proceed
      default: {
        if (this._t <= 0) {
          this._context.lineTo(this._x, y);
          this._context.lineTo(x, y);
        } else {
          var x1 = this._x * (1 - this._t) + x * this._t;
          this._context.lineTo(x1, this._y);
          this._context.lineTo(x1, y);
        }
        break;
      }
    }
    this._x = x, this._y = y;
  }
};

function hr(context) {
  return new Step(context, 0.5);
}

function stepBefore(context) {
  return new Step(context, 0);
}

function stepAfter(context) {
  return new Step(context, 1);
}

function wr(series, order) {
  if (!((n = series.length) > 1)) return;
  for (var i = 1, j, s0, s1 = series[order[0]], n, m = s1.length; i < n; ++i) {
    s0 = s1, s1 = series[order[i]];
    for (j = 0; j < m; ++j) {
      s1[j][1] += s1[j][0] = isNaN(s0[j][1]) ? s0[j][0] : s0[j][1];
    }
  }
}

function Rr(series) {
  var n = series.length, o = new Array(n);
  while (--n >= 0) o[n] = n;
  return o;
}

function kr(series, order) {
  if (!((n = series.length) > 0)) return;
  for (var i, n, j = 0, m = series[0].length, y; j < m; ++j) {
    for (y = i = 0; i < n; ++i) y += series[i][j][1] || 0;
    if (y) for (i = 0; i < n; ++i) series[i][j][1] /= y;
  }
  wr(series, order);
}

function _r(series, order) {
  if (!((n = series.length) > 0)) return;
  for (var i, j = 0, d, dy, yp, yn, n, m = series[order[0]].length; j < m; ++j) {
    for (yp = yn = 0, i = 0; i < n; ++i) {
      if ((dy = (d = series[order[i]][j])[1] - d[0]) > 0) {
        d[0] = yp, d[1] = yp += dy;
      } else if (dy < 0) {
        d[1] = yn, d[0] = yn += dy;
      } else {
        d[0] = 0, d[1] = dy;
      }
    }
  }
}

function xr(series, order) {
  if (!((n = series.length) > 0)) return;
  for (var j = 0, s0 = series[order[0]], n, m = s0.length; j < m; ++j) {
    for (var i = 0, y = 0; i < n; ++i) y += series[i][j][1] || 0;
    s0[j][1] += s0[j][0] = -y / 2;
  }
  wr(series, order);
}

function Or(series, order) {
  if (!((n = series.length) > 0) || !((m = (s0 = series[order[0]]).length) > 0)) return;
  for (var y = 0, j = 1, s0, m, n; j < m; ++j) {
    for (var i = 0, s1 = 0, s2 = 0; i < n; ++i) {
      var si = series[order[i]],
          sij0 = si[j][1] || 0,
          sij1 = si[j - 1][1] || 0,
          s3 = (sij0 - sij1) / 2;
      for (var k = 0; k < i; ++k) {
        var sk = series[order[k]],
            skj0 = sk[j][1] || 0,
            skj1 = sk[j - 1][1] || 0;
        s3 += skj0 - skj1;
      }
      s1 += sij0, s2 += s3 * sij0;
    }
    s0[j - 1][1] += s0[j - 1][0] = y;
    if (s1) y -= s2 / s1;
  }
  s0[j - 1][1] += s0[j - 1][0] = y;
  wr(series, order);
}

function appearance(series) {
  var peaks = series.map(peak);
  return Rr(series).sort(function(a, b) { return peaks[a] - peaks[b]; });
}

function peak(series) {
  var i = -1, j = 0, n = series.length, vi, vj = -Infinity;
  while (++i < n) if ((vi = +series[i][1]) > vj) vj = vi, j = i;
  return j;
}

function br(series) {
  var sums = series.map(sum);
  return Rr(series).sort(function(a, b) { return sums[a] - sums[b]; });
}

function sum(series) {
  var s = 0, i = -1, n = series.length, v;
  while (++i < n) if (v = +series[i][1]) s += v;
  return s;
}

function yr(series) {
  return br(series).reverse();
}

function vr(series) {
  var n = series.length,
      i,
      j,
      sums = series.map(sum),
      order = appearance(series),
      top = 0,
      bottom = 0,
      tops = [],
      bottoms = [];

  for (i = 0; i < n; ++i) {
    j = order[i];
    if (top < bottom) {
      top += sums[j];
      tops.push(j);
    } else {
      bottom += sums[j];
      bottoms.push(j);
    }
  }

  return bottoms.reverse().concat(tops);
}

function qr(series) {
  return Rr(series).reverse();
}

function formatDecimal(x) {
  return Math.abs(x = Math.round(x)) >= 1e21
      ? x.toLocaleString("en").replace(/,/g, "")
      : x.toString(10);
}

// Computes the decimal coefficient and exponent of the specified number x with
// significant digits p, where x is positive and p is in [1, 21] or undefined.
// For example, formatDecimalParts(1.23) returns ["123", 0].
function formatDecimalParts(x, p) {
  if ((i = (x = p ? x.toExponential(p - 1) : x.toExponential()).indexOf("e")) < 0) return null; // NaN, ±Infinity
  var i, coefficient = x.slice(0, i);

  // The string returned by toExponential either has the form \d\.\d+e[-+]\d+
  // (e.g., 1.2e+3) or the form \de[-+]\d+ (e.g., 1e+3).
  return [
    coefficient.length > 1 ? coefficient[0] + coefficient.slice(2) : coefficient,
    +x.slice(i + 1)
  ];
}

function exponent(x) {
  return x = formatDecimalParts(Math.abs(x)), x ? x[1] : NaN;
}

function formatGroup(grouping, thousands) {
  return function(value, width) {
    var i = value.length,
        t = [],
        j = 0,
        g = grouping[0],
        length = 0;

    while (i > 0 && g > 0) {
      if (length + g + 1 > width) g = Math.max(1, width - length);
      t.push(value.substring(i -= g, i + g));
      if ((length += g + 1) > width) break;
      g = grouping[j = (j + 1) % grouping.length];
    }

    return t.reverse().join(thousands);
  };
}

function formatNumerals(numerals) {
  return function(value) {
    return value.replace(/[0-9]/g, function(i) {
      return numerals[+i];
    });
  };
}

// [[fill]align][sign][symbol][0][width][,][.precision][~][type]
var re = /^(?:(.)?([<>=^]))?([+\-( ])?([$#])?(0)?(\d+)?(,)?(\.\d+)?(~)?([a-z%])?$/i;

function formatSpecifier(specifier) {
  if (!(match = re.exec(specifier))) throw new Error("invalid format: " + specifier);
  var match;
  return new FormatSpecifier({
    fill: match[1],
    align: match[2],
    sign: match[3],
    symbol: match[4],
    zero: match[5],
    width: match[6],
    comma: match[7],
    precision: match[8] && match[8].slice(1),
    trim: match[9],
    type: match[10]
  });
}

formatSpecifier.prototype = FormatSpecifier.prototype; // instanceof

function FormatSpecifier(specifier) {
  this.fill = specifier.fill === undefined ? " " : specifier.fill + "";
  this.align = specifier.align === undefined ? ">" : specifier.align + "";
  this.sign = specifier.sign === undefined ? "-" : specifier.sign + "";
  this.symbol = specifier.symbol === undefined ? "" : specifier.symbol + "";
  this.zero = !!specifier.zero;
  this.width = specifier.width === undefined ? undefined : +specifier.width;
  this.comma = !!specifier.comma;
  this.precision = specifier.precision === undefined ? undefined : +specifier.precision;
  this.trim = !!specifier.trim;
  this.type = specifier.type === undefined ? "" : specifier.type + "";
}

FormatSpecifier.prototype.toString = function() {
  return this.fill
      + this.align
      + this.sign
      + this.symbol
      + (this.zero ? "0" : "")
      + (this.width === undefined ? "" : Math.max(1, this.width | 0))
      + (this.comma ? "," : "")
      + (this.precision === undefined ? "" : "." + Math.max(0, this.precision | 0))
      + (this.trim ? "~" : "")
      + this.type;
};

// Trims insignificant zeros, e.g., replaces 1.2000k with 1.2k.
function formatTrim(s) {
  out: for (var n = s.length, i = 1, i0 = -1, i1; i < n; ++i) {
    switch (s[i]) {
      case ".": i0 = i1 = i; break;
      case "0": if (i0 === 0) i0 = i; i1 = i; break;
      default: if (!+s[i]) break out; if (i0 > 0) i0 = 0; break;
    }
  }
  return i0 > 0 ? s.slice(0, i0) + s.slice(i1 + 1) : s;
}

var prefixExponent;

function formatPrefixAuto(x, p) {
  var d = formatDecimalParts(x, p);
  if (!d) return x + "";
  var coefficient = d[0],
      exponent = d[1],
      i = exponent - (prefixExponent = Math.max(-8, Math.min(8, Math.floor(exponent / 3))) * 3) + 1,
      n = coefficient.length;
  return i === n ? coefficient
      : i > n ? coefficient + new Array(i - n + 1).join("0")
      : i > 0 ? coefficient.slice(0, i) + "." + coefficient.slice(i)
      : "0." + new Array(1 - i).join("0") + formatDecimalParts(x, Math.max(0, p + i - 1))[0]; // less than 1y!
}

function formatRounded(x, p) {
  var d = formatDecimalParts(x, p);
  if (!d) return x + "";
  var coefficient = d[0],
      exponent = d[1];
  return exponent < 0 ? "0." + new Array(-exponent).join("0") + coefficient
      : coefficient.length > exponent + 1 ? coefficient.slice(0, exponent + 1) + "." + coefficient.slice(exponent + 1)
      : coefficient + new Array(exponent - coefficient.length + 2).join("0");
}

var formatTypes = {
  "%": function(x, p) { return (x * 100).toFixed(p); },
  "b": function(x) { return Math.round(x).toString(2); },
  "c": function(x) { return x + ""; },
  "d": formatDecimal,
  "e": function(x, p) { return x.toExponential(p); },
  "f": function(x, p) { return x.toFixed(p); },
  "g": function(x, p) { return x.toPrecision(p); },
  "o": function(x) { return Math.round(x).toString(8); },
  "p": function(x, p) { return formatRounded(x * 100, p); },
  "r": formatRounded,
  "s": formatPrefixAuto,
  "X": function(x) { return Math.round(x).toString(16).toUpperCase(); },
  "x": function(x) { return Math.round(x).toString(16); }
};

function identity$2(x) {
  return x;
}

var map = Array.prototype.map,
    prefixes = ["y","z","a","f","p","n","µ","m","","k","M","G","T","P","E","Z","Y"];

function formatLocale(locale) {
  var group = locale.grouping === undefined || locale.thousands === undefined ? identity$2 : formatGroup(map.call(locale.grouping, Number), locale.thousands + ""),
      currencyPrefix = locale.currency === undefined ? "" : locale.currency[0] + "",
      currencySuffix = locale.currency === undefined ? "" : locale.currency[1] + "",
      decimal = locale.decimal === undefined ? "." : locale.decimal + "",
      numerals = locale.numerals === undefined ? identity$2 : formatNumerals(map.call(locale.numerals, String)),
      percent = locale.percent === undefined ? "%" : locale.percent + "",
      minus = locale.minus === undefined ? "-" : locale.minus + "",
      nan = locale.nan === undefined ? "NaN" : locale.nan + "";

  function newFormat(specifier) {
    specifier = formatSpecifier(specifier);

    var fill = specifier.fill,
        align = specifier.align,
        sign = specifier.sign,
        symbol = specifier.symbol,
        zero = specifier.zero,
        width = specifier.width,
        comma = specifier.comma,
        precision = specifier.precision,
        trim = specifier.trim,
        type = specifier.type;

    // The "n" type is an alias for ",g".
    if (type === "n") comma = true, type = "g";

    // The "" type, and any invalid type, is an alias for ".12~g".
    else if (!formatTypes[type]) precision === undefined && (precision = 12), trim = true, type = "g";

    // If zero fill is specified, padding goes after sign and before digits.
    if (zero || (fill === "0" && align === "=")) zero = true, fill = "0", align = "=";

    // Compute the prefix and suffix.
    // For SI-prefix, the suffix is lazily computed.
    var prefix = symbol === "$" ? currencyPrefix : symbol === "#" && /[boxX]/.test(type) ? "0" + type.toLowerCase() : "",
        suffix = symbol === "$" ? currencySuffix : /[%p]/.test(type) ? percent : "";

    // What format function should we use?
    // Is this an integer type?
    // Can this type generate exponential notation?
    var formatType = formatTypes[type],
        maybeSuffix = /[defgprs%]/.test(type);

    // Set the default precision if not specified,
    // or clamp the specified precision to the supported range.
    // For significant precision, it must be in [1, 21].
    // For fixed precision, it must be in [0, 20].
    precision = precision === undefined ? 6
        : /[gprs]/.test(type) ? Math.max(1, Math.min(21, precision))
        : Math.max(0, Math.min(20, precision));

    function format(value) {
      var valuePrefix = prefix,
          valueSuffix = suffix,
          i, n, c;

      if (type === "c") {
        valueSuffix = formatType(value) + valueSuffix;
        value = "";
      } else {
        value = +value;

        // Determine the sign. -0 is not less than 0, but 1 / -0 is!
        var valueNegative = value < 0 || 1 / value < 0;

        // Perform the initial formatting.
        value = isNaN(value) ? nan : formatType(Math.abs(value), precision);

        // Trim insignificant zeros.
        if (trim) value = formatTrim(value);

        // If a negative value rounds to zero after formatting, and no explicit positive sign is requested, hide the sign.
        if (valueNegative && +value === 0 && sign !== "+") valueNegative = false;

        // Compute the prefix and suffix.
        valuePrefix = (valueNegative ? (sign === "(" ? sign : minus) : sign === "-" || sign === "(" ? "" : sign) + valuePrefix;
        valueSuffix = (type === "s" ? prefixes[8 + prefixExponent / 3] : "") + valueSuffix + (valueNegative && sign === "(" ? ")" : "");

        // Break the formatted value into the integer “value” part that can be
        // grouped, and fractional or exponential “suffix” part that is not.
        if (maybeSuffix) {
          i = -1, n = value.length;
          while (++i < n) {
            if (c = value.charCodeAt(i), 48 > c || c > 57) {
              valueSuffix = (c === 46 ? decimal + value.slice(i + 1) : value.slice(i)) + valueSuffix;
              value = value.slice(0, i);
              break;
            }
          }
        }
      }

      // If the fill character is not "0", grouping is applied before padding.
      if (comma && !zero) value = group(value, Infinity);

      // Compute the padding.
      var length = valuePrefix.length + value.length + valueSuffix.length,
          padding = length < width ? new Array(width - length + 1).join(fill) : "";

      // If the fill character is "0", grouping is applied after padding.
      if (comma && zero) value = group(padding + value, padding.length ? width - valueSuffix.length : Infinity), padding = "";

      // Reconstruct the final output based on the desired alignment.
      switch (align) {
        case "<": value = valuePrefix + value + valueSuffix + padding; break;
        case "=": value = valuePrefix + padding + value + valueSuffix; break;
        case "^": value = padding.slice(0, length = padding.length >> 1) + valuePrefix + value + valueSuffix + padding.slice(length); break;
        default: value = padding + valuePrefix + value + valueSuffix; break;
      }

      return numerals(value);
    }

    format.toString = function() {
      return specifier + "";
    };

    return format;
  }

  function formatPrefix(specifier, value) {
    var f = newFormat((specifier = formatSpecifier(specifier), specifier.type = "f", specifier)),
        e = Math.max(-8, Math.min(8, Math.floor(exponent(value) / 3))) * 3,
        k = Math.pow(10, -e),
        prefix = prefixes[8 + e / 3];
    return function(value) {
      return f(k * value) + prefix;
    };
  }

  return {
    format: newFormat,
    formatPrefix: formatPrefix
  };
}

var locale;
var format;

defaultLocale({
  decimal: ".",
  thousands: ",",
  grouping: [3],
  currency: ["$", ""],
  minus: "-"
});

function defaultLocale(definition) {
  locale = formatLocale(definition);
  format = locale.format;
  locale.formatPrefix;
  return locale;
}

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */

function arraySome$1(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }
  return false;
}

var _arraySome = arraySome$1;

var SetCache = _SetCache,
    arraySome = _arraySome,
    cacheHas = _cacheHas;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$5 = 1,
    COMPARE_UNORDERED_FLAG$3 = 2;

/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */
function equalArrays$2(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$5,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  }
  // Check that cyclic values are equal.
  var arrStacked = stack.get(array);
  var othStacked = stack.get(other);
  if (arrStacked && othStacked) {
    return arrStacked == other && othStacked == array;
  }
  var index = -1,
      result = true,
      seen = (bitmask & COMPARE_UNORDERED_FLAG$3) ? new SetCache : undefined;

  stack.set(array, other);
  stack.set(other, array);

  // Ignore non-index properties.
  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, arrValue, index, other, array, stack)
        : customizer(arrValue, othValue, index, array, other, stack);
    }
    if (compared !== undefined) {
      if (compared) {
        continue;
      }
      result = false;
      break;
    }
    // Recursively compare arrays (susceptible to call stack limits).
    if (seen) {
      if (!arraySome(other, function(othValue, othIndex) {
            if (!cacheHas(seen, othIndex) &&
                (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
              return seen.push(othIndex);
            }
          })) {
        result = false;
        break;
      }
    } else if (!(
          arrValue === othValue ||
            equalFunc(arrValue, othValue, bitmask, customizer, stack)
        )) {
      result = false;
      break;
    }
  }
  stack['delete'](array);
  stack['delete'](other);
  return result;
}

var _equalArrays = equalArrays$2;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */

function mapToArray$1(map) {
  var index = -1,
      result = Array(map.size);

  map.forEach(function(value, key) {
    result[++index] = [key, value];
  });
  return result;
}

var _mapToArray = mapToArray$1;

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */

function setToArray$2(set) {
  var index = -1,
      result = Array(set.size);

  set.forEach(function(value) {
    result[++index] = value;
  });
  return result;
}

var _setToArray = setToArray$2;

var Symbol$3 = _Symbol,
    Uint8Array$1 = _Uint8Array,
    eq = eq_1,
    equalArrays$1 = _equalArrays,
    mapToArray = _mapToArray,
    setToArray$1 = _setToArray;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$4 = 1,
    COMPARE_UNORDERED_FLAG$2 = 2;

/** `Object#toString` result references. */
var boolTag$2 = '[object Boolean]',
    dateTag$2 = '[object Date]',
    errorTag$1 = '[object Error]',
    mapTag$4 = '[object Map]',
    numberTag$3 = '[object Number]',
    regexpTag$2 = '[object RegExp]',
    setTag$4 = '[object Set]',
    stringTag$2 = '[object String]',
    symbolTag$2 = '[object Symbol]';

var arrayBufferTag$2 = '[object ArrayBuffer]',
    dataViewTag$3 = '[object DataView]';

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = Symbol$3 ? Symbol$3.prototype : undefined,
    symbolValueOf$1 = symbolProto$1 ? symbolProto$1.valueOf : undefined;

/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalByTag$1(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag$3:
      if ((object.byteLength != other.byteLength) ||
          (object.byteOffset != other.byteOffset)) {
        return false;
      }
      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag$2:
      if ((object.byteLength != other.byteLength) ||
          !equalFunc(new Uint8Array$1(object), new Uint8Array$1(other))) {
        return false;
      }
      return true;

    case boolTag$2:
    case dateTag$2:
    case numberTag$3:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq(+object, +other);

    case errorTag$1:
      return object.name == other.name && object.message == other.message;

    case regexpTag$2:
    case stringTag$2:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == (other + '');

    case mapTag$4:
      var convert = mapToArray;

    case setTag$4:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$4;
      convert || (convert = setToArray$1);

      if (object.size != other.size && !isPartial) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked) {
        return stacked == other;
      }
      bitmask |= COMPARE_UNORDERED_FLAG$2;

      // Recursively compare objects (susceptible to call stack limits).
      stack.set(object, other);
      var result = equalArrays$1(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag$2:
      if (symbolValueOf$1) {
        return symbolValueOf$1.call(object) == symbolValueOf$1.call(other);
      }
  }
  return false;
}

var _equalByTag = equalByTag$1;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */

function arrayPush$3(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

var _arrayPush = arrayPush$3;

var arrayPush$2 = _arrayPush,
    isArray$7 = isArray_1;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys$2(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray$7(object) ? result : arrayPush$2(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys$2;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */

function arrayFilter$2(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

var _arrayFilter = arrayFilter$2;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */

function stubArray$2() {
  return [];
}

var stubArray_1 = stubArray$2;

var arrayFilter$1 = _arrayFilter,
    stubArray$1 = stubArray_1;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$5.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols$1 = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols$3 = !nativeGetSymbols$1 ? stubArray$1 : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return arrayFilter$1(nativeGetSymbols$1(object), function(symbol) {
    return propertyIsEnumerable.call(object, symbol);
  });
};

var _getSymbols = getSymbols$3;

var overArg = _overArg;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys$1 = overArg(Object.keys, Object);

var _nativeKeys = nativeKeys$1;

var isPrototype = _isPrototype,
    nativeKeys = _nativeKeys;

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$4.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys$1(object) {
  if (!isPrototype(object)) {
    return nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$4.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

var _baseKeys = baseKeys$1;

var arrayLikeKeys = _arrayLikeKeys,
    baseKeys = _baseKeys,
    isArrayLike$2 = isArrayLike_1;

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys$5(object) {
  return isArrayLike$2(object) ? arrayLikeKeys(object) : baseKeys(object);
}

var keys_1 = keys$5;

var baseGetAllKeys$1 = _baseGetAllKeys,
    getSymbols$2 = _getSymbols,
    keys$4 = keys_1;

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys$2(object) {
  return baseGetAllKeys$1(object, keys$4, getSymbols$2);
}

var _getAllKeys = getAllKeys$2;

var getAllKeys$1 = _getAllKeys;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$3 = 1;

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$3.hasOwnProperty;

/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalObjects$1(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$3,
      objProps = getAllKeys$1(object),
      objLength = objProps.length,
      othProps = getAllKeys$1(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }
  var index = objLength;
  while (index--) {
    var key = objProps[index];
    if (!(isPartial ? key in other : hasOwnProperty$3.call(other, key))) {
      return false;
    }
  }
  // Check that cyclic values are equal.
  var objStacked = stack.get(object);
  var othStacked = stack.get(other);
  if (objStacked && othStacked) {
    return objStacked == other && othStacked == object;
  }
  var result = true;
  stack.set(object, other);
  stack.set(other, object);

  var skipCtor = isPartial;
  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, objValue, key, other, object, stack)
        : customizer(objValue, othValue, key, object, other, stack);
    }
    // Recursively compare objects (susceptible to call stack limits).
    if (!(compared === undefined
          ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
          : compared
        )) {
      result = false;
      break;
    }
    skipCtor || (skipCtor = key == 'constructor');
  }
  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor;

    // Non `Object` object instances with different constructors are not equal.
    if (objCtor != othCtor &&
        ('constructor' in object && 'constructor' in other) &&
        !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
          typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }
  stack['delete'](object);
  stack['delete'](other);
  return result;
}

var _equalObjects = equalObjects$1;

var getNative$3 = _getNative,
    root$3 = _root;

/* Built-in method references that are verified to be native. */
var DataView$2 = getNative$3(root$3, 'DataView');

var _DataView = DataView$2;

var getNative$2 = _getNative,
    root$2 = _root;

/* Built-in method references that are verified to be native. */
var Promise$2 = getNative$2(root$2, 'Promise');

var _Promise = Promise$2;

var getNative$1 = _getNative,
    root$1 = _root;

/* Built-in method references that are verified to be native. */
var Set$3 = getNative$1(root$1, 'Set');

var _Set = Set$3;

var getNative = _getNative,
    root = _root;

/* Built-in method references that are verified to be native. */
var WeakMap$2 = getNative(root, 'WeakMap');

var _WeakMap = WeakMap$2;

var DataView$1 = _DataView,
    Map$1 = _Map,
    Promise$1 = _Promise,
    Set$2 = _Set,
    WeakMap$1 = _WeakMap,
    baseGetTag$1 = _baseGetTag,
    toSource = _toSource;

/** `Object#toString` result references. */
var mapTag$3 = '[object Map]',
    objectTag$2 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$3 = '[object Set]',
    weakMapTag$1 = '[object WeakMap]';

var dataViewTag$2 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = toSource(DataView$1),
    mapCtorString = toSource(Map$1),
    promiseCtorString = toSource(Promise$1),
    setCtorString = toSource(Set$2),
    weakMapCtorString = toSource(WeakMap$1);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag$4 = baseGetTag$1;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((DataView$1 && getTag$4(new DataView$1(new ArrayBuffer(1))) != dataViewTag$2) ||
    (Map$1 && getTag$4(new Map$1) != mapTag$3) ||
    (Promise$1 && getTag$4(Promise$1.resolve()) != promiseTag) ||
    (Set$2 && getTag$4(new Set$2) != setTag$3) ||
    (WeakMap$1 && getTag$4(new WeakMap$1) != weakMapTag$1)) {
  getTag$4 = function(value) {
    var result = baseGetTag$1(value),
        Ctor = result == objectTag$2 ? value.constructor : undefined,
        ctorString = Ctor ? toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$2;
        case mapCtorString: return mapTag$3;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$3;
        case weakMapCtorString: return weakMapTag$1;
      }
    }
    return result;
  };
}

var _getTag = getTag$4;

var Stack$2 = _Stack,
    equalArrays = _equalArrays,
    equalByTag = _equalByTag,
    equalObjects = _equalObjects,
    getTag$3 = _getTag,
    isArray$6 = isArray_1,
    isBuffer$1 = isBufferExports,
    isTypedArray = isTypedArray_1;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$2 = 1;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    objectTag$1 = '[object Object]';

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$2.hasOwnProperty;

/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function baseIsEqualDeep$1(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray$6(object),
      othIsArr = isArray$6(other),
      objTag = objIsArr ? arrayTag$1 : getTag$3(object),
      othTag = othIsArr ? arrayTag$1 : getTag$3(other);

  objTag = objTag == argsTag$1 ? objectTag$1 : objTag;
  othTag = othTag == argsTag$1 ? objectTag$1 : othTag;

  var objIsObj = objTag == objectTag$1,
      othIsObj = othTag == objectTag$1,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer$1(object)) {
    if (!isBuffer$1(other)) {
      return false;
    }
    objIsArr = true;
    objIsObj = false;
  }
  if (isSameTag && !objIsObj) {
    stack || (stack = new Stack$2);
    return (objIsArr || isTypedArray(object))
      ? equalArrays(object, other, bitmask, customizer, equalFunc, stack)
      : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }
  if (!(bitmask & COMPARE_PARTIAL_FLAG$2)) {
    var objIsWrapped = objIsObj && hasOwnProperty$2.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty$2.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;

      stack || (stack = new Stack$2);
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }
  if (!isSameTag) {
    return false;
  }
  stack || (stack = new Stack$2);
  return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}

var _baseIsEqualDeep = baseIsEqualDeep$1;

var baseIsEqualDeep = _baseIsEqualDeep,
    isObjectLike$3 = isObjectLike_1;

/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */
function baseIsEqual$2(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }
  if (value == null || other == null || (!isObjectLike$3(value) && !isObjectLike$3(other))) {
    return value !== value && other !== other;
  }
  return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual$2, stack);
}

var _baseIsEqual = baseIsEqual$2;

var baseGet$3 = _baseGet,
    baseSet = _baseSet,
    castPath$3 = _castPath;

/**
 * The base implementation of  `_.pickBy` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The source object.
 * @param {string[]} paths The property paths to pick.
 * @param {Function} predicate The function invoked per property.
 * @returns {Object} Returns the new object.
 */
function basePickBy$1(object, paths, predicate) {
  var index = -1,
      length = paths.length,
      result = {};

  while (++index < length) {
    var path = paths[index],
        value = baseGet$3(object, path);

    if (predicate(value, path)) {
      baseSet(result, castPath$3(path, object), value);
    }
  }
  return result;
}

var _basePickBy = basePickBy$1;

/**
 * The base implementation of `_.hasIn` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */

function baseHasIn$1(object, key) {
  return object != null && key in Object(object);
}

var _baseHasIn = baseHasIn$1;

var castPath$2 = _castPath,
    isArguments$1 = isArguments_1,
    isArray$5 = isArray_1,
    isIndex = _isIndex,
    isLength = isLength_1,
    toKey$3 = _toKey;

/**
 * Checks if `path` exists on `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @param {Function} hasFunc The function to check properties.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 */
function hasPath$1(object, path, hasFunc) {
  path = castPath$2(path, object);

  var index = -1,
      length = path.length,
      result = false;

  while (++index < length) {
    var key = toKey$3(path[index]);
    if (!(result = object != null && hasFunc(object, key))) {
      break;
    }
    object = object[key];
  }
  if (result || ++index != length) {
    return result;
  }
  length = object == null ? 0 : object.length;
  return !!length && isLength(length) && isIndex(key, length) &&
    (isArray$5(object) || isArguments$1(object));
}

var _hasPath = hasPath$1;

var baseHasIn = _baseHasIn,
    hasPath = _hasPath;

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b');
 * // => true
 *
 * _.hasIn(object, ['a', 'b']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn$2(object, path) {
  return object != null && hasPath(object, path, baseHasIn);
}

var hasIn_1 = hasIn$2;

var basePickBy = _basePickBy,
    hasIn$1 = hasIn_1;

/**
 * The base implementation of `_.pick` without support for individual
 * property identifiers.
 *
 * @private
 * @param {Object} object The source object.
 * @param {string[]} paths The property paths to pick.
 * @returns {Object} Returns the new object.
 */
function basePick$1(object, paths) {
  return basePickBy(object, paths, function(value, path) {
    return hasIn$1(object, path);
  });
}

var _basePick = basePick$1;

var Symbol$2 = _Symbol,
    isArguments = isArguments_1,
    isArray$4 = isArray_1;

/** Built-in value references. */
var spreadableSymbol = Symbol$2 ? Symbol$2.isConcatSpreadable : undefined;

/**
 * Checks if `value` is a flattenable `arguments` object or array.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
 */
function isFlattenable$1(value) {
  return isArray$4(value) || isArguments(value) ||
    !!(spreadableSymbol && value && value[spreadableSymbol]);
}

var _isFlattenable = isFlattenable$1;

var arrayPush$1 = _arrayPush,
    isFlattenable = _isFlattenable;

/**
 * The base implementation of `_.flatten` with support for restricting flattening.
 *
 * @private
 * @param {Array} array The array to flatten.
 * @param {number} depth The maximum recursion depth.
 * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
 * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
 * @param {Array} [result=[]] The initial result value.
 * @returns {Array} Returns the new flattened array.
 */
function baseFlatten$2(array, depth, predicate, isStrict, result) {
  var index = -1,
      length = array.length;

  predicate || (predicate = isFlattenable);
  result || (result = []);

  while (++index < length) {
    var value = array[index];
    if (depth > 0 && predicate(value)) {
      if (depth > 1) {
        // Recursively flatten arrays (susceptible to call stack limits).
        baseFlatten$2(value, depth - 1, predicate, isStrict, result);
      } else {
        arrayPush$1(result, value);
      }
    } else if (!isStrict) {
      result[result.length] = value;
    }
  }
  return result;
}

var _baseFlatten = baseFlatten$2;

var baseFlatten$1 = _baseFlatten;

/**
 * Flattens `array` a single level deep.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to flatten.
 * @returns {Array} Returns the new flattened array.
 * @example
 *
 * _.flatten([1, [2, [3, [4]], 5]]);
 * // => [1, 2, [3, [4]], 5]
 */
function flatten$1(array) {
  var length = array == null ? 0 : array.length;
  return length ? baseFlatten$1(array, 1) : [];
}

var flatten_1 = flatten$1;

var flatten = flatten_1,
    overRest = _overRest,
    setToString = _setToString;

/**
 * A specialized version of `baseRest` which flattens the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @returns {Function} Returns the new function.
 */
function flatRest$2(func) {
  return setToString(overRest(func, undefined, flatten), func + '');
}

var _flatRest = flatRest$2;

var basePick = _basePick,
    flatRest$1 = _flatRest;

/**
 * Creates an object composed of the picked `object` properties.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The source object.
 * @param {...(string|string[])} [paths] The property paths to pick.
 * @returns {Object} Returns the new object.
 * @example
 *
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 *
 * _.pick(object, ['a', 'c']);
 * // => { 'a': 1, 'c': 3 }
 */
flatRest$1(function(object, paths) {
  return object == null ? {} : basePick(object, paths);
});

function Gr(){return Gr=Object.assign||function(e){for(var r=1;r<arguments.length;r++){var t=arguments[r];for(var i in t)Object.prototype.hasOwnProperty.call(t,i)&&(e[i]=t[i]);}return e},Gr.apply(this,arguments)}function Lr(e,r){if(null==e)return {};var t,i,n={},o=Object.keys(e);for(i=0;i<o.length;i++)t=o[i],r.indexOf(t)>=0||(n[t]=e[t]);return n}var Ir={fill:propTypesExports.string,fontSize:propTypesExports.number,fontFamily:propTypesExports.string},Yr=propTypesExports.shape({domain:propTypesExports.shape({line:propTypesExports.shape({stroke:propTypesExports.string.isRequired,strokeWidth:propTypesExports.number.isRequired,strokeDasharray:propTypesExports.string}).isRequired}).isRequired,ticks:propTypesExports.shape({line:propTypesExports.shape({stroke:propTypesExports.string.isRequired,strokeWidth:propTypesExports.number.isRequired,strokeDasharray:propTypesExports.string}).isRequired,text:propTypesExports.shape(Gr({},Ir)).isRequired}).isRequired,legend:propTypesExports.shape({text:propTypesExports.shape(Gr({},Ir)).isRequired}).isRequired}),Ar=propTypesExports.shape({line:propTypesExports.shape({stroke:propTypesExports.string.isRequired,strokeWidth:propTypesExports.number.isRequired,strokeDasharray:propTypesExports.string}).isRequired}),Fr=propTypesExports.shape({hidden:propTypesExports.shape({symbol:propTypesExports.shape({fill:propTypesExports.string.isRequired,opacity:propTypesExports.number}).isRequired,text:propTypesExports.shape(Gr({},Ir,{opacity:propTypesExports.number})).isRequired}).isRequired,text:propTypesExports.shape(Gr({},Ir)).isRequired}),Er=propTypesExports.shape({text:propTypesExports.shape(Gr({},Ir)).isRequired}),Ur=propTypesExports.shape({text:propTypesExports.shape(Gr({},Ir)).isRequired}),Xr=propTypesExports.shape({text:propTypesExports.shape(Gr({},Ir)).isRequired}),Kr=propTypesExports.shape({line:propTypesExports.shape({stroke:propTypesExports.string.isRequired,strokeWidth:propTypesExports.number.isRequired,strokeDasharray:propTypesExports.string}).isRequired}),Hr=propTypesExports.shape({text:propTypesExports.shape(Gr({},Ir,{outlineWidth:propTypesExports.number.isRequired,outlineColor:propTypesExports.string.isRequired})).isRequired,link:propTypesExports.shape({stroke:propTypesExports.string.isRequired,strokeWidth:propTypesExports.number.isRequired,outlineWidth:propTypesExports.number.isRequired,outlineColor:propTypesExports.string.isRequired}).isRequired,outline:propTypesExports.shape({stroke:propTypesExports.string.isRequired,strokeWidth:propTypesExports.number.isRequired,outlineWidth:propTypesExports.number.isRequired,outlineColor:propTypesExports.string.isRequired}).isRequired,symbol:propTypesExports.shape({fill:propTypesExports.string.isRequired,outlineWidth:propTypesExports.number.isRequired,outlineColor:propTypesExports.string.isRequired}).isRequired});propTypesExports.shape({background:propTypesExports.string.isRequired,fontFamily:propTypesExports.string.isRequired,fontSize:propTypesExports.number.isRequired,textColor:propTypesExports.string.isRequired,axis:Yr.isRequired,grid:Ar.isRequired,legends:Fr.isRequired,labels:Er.isRequired,dots:Ur.isRequired,markers:Xr,crosshair:Kr.isRequired,annotations:Hr.isRequired});var Vr={background:"transparent",fontFamily:"sans-serif",fontSize:11,textColor:"#333333",axis:{domain:{line:{stroke:"transparent",strokeWidth:1}},ticks:{line:{stroke:"#777777",strokeWidth:1},text:{}},legend:{text:{fontSize:12}}},grid:{line:{stroke:"#dddddd",strokeWidth:1}},legends:{hidden:{symbol:{fill:"#333333",opacity:.6},text:{fill:"#333333",opacity:.6}},text:{},ticks:{line:{stroke:"#777777",strokeWidth:1},text:{fontSize:10}},title:{text:{}}},labels:{text:{}},markers:{lineColor:"#000000",lineStrokeWidth:1,text:{}},dots:{text:{}},tooltip:{container:{background:"white",color:"inherit",fontSize:"inherit",borderRadius:"2px",boxShadow:"0 1px 2px rgba(0, 0, 0, 0.25)",padding:"5px 9px"},basic:{whiteSpace:"pre",display:"flex",alignItems:"center"},chip:{marginRight:7},table:{},tableCell:{padding:"3px 5px"},tableCellValue:{fontWeight:"bold"}},crosshair:{line:{stroke:"#000000",strokeWidth:1,strokeOpacity:.75,strokeDasharray:"6 6"}},annotations:{text:{fontSize:13,outlineWidth:2,outlineColor:"#ffffff",outlineOpacity:1},link:{stroke:"#000000",strokeWidth:1,outlineWidth:2,outlineColor:"#ffffff",outlineOpacity:1},outline:{fill:"none",stroke:"#000000",strokeWidth:2,outlineWidth:2,outlineColor:"#ffffff",outlineOpacity:1},symbol:{fill:"#000000",outlineWidth:2,outlineColor:"#ffffff",outlineOpacity:1}}},Jr=["axis.ticks.text","axis.legend.text","legends.title.text","legends.text","legends.ticks.text","legends.title.text","labels.text","dots.text","markers.text","annotations.text"],Qr=function(e,r){var t=merge_1({},e,r);return Jr.forEach((function(e){void 0===get_1(t,e+".fontFamily")&&set_1(t,e+".fontFamily",t.fontFamily),void 0===get_1(t,e+".fontSize")&&set_1(t,e+".fontSize",t.fontSize),void 0===get_1(t,e+".fill")&&set_1(t,e+".fill",t.textColor);})),t},Zr=createContext(),$r=function(e){var t=e.children,i=e.animate,n=e.stiffness,o=e.damping,a=e.config,s=useMemo((function(){var e=isString_1(a)?config[a]:a;return {animate:i,springConfig:{stiffness:n,damping:o},config:e}}),[i,n,o,a]);return jsx(Zr.Provider,{value:s,children:t})},et={animate:propTypesExports.bool,motionStiffness:propTypesExports.number,motionDamping:propTypesExports.number,motionConfig:propTypesExports.oneOfType([propTypesExports.oneOf(Object.keys(config)),propTypesExports.shape({mass:propTypesExports.number,tension:propTypesExports.number,friction:propTypesExports.number,clamp:propTypesExports.bool,precision:propTypesExports.number,velocity:propTypesExports.number,duration:propTypesExports.number,easing:propTypesExports.func})])};$r.propTypes={children:propTypesExports.node.isRequired,animate:et.animate,stiffness:et.motionStiffness,damping:et.motionDamping,config:et.motionConfig};var rt={animate:!0,stiffness:90,damping:15,config:"default"};$r.defaultProps=rt;var tt=function(){return useContext(Zr)},nt={nivo:["#d76445","#f47560","#e8c1a0","#97e3d5","#61cdbb","#00b0a7"],BrBG:last_1(scheme$q),PRGn:last_1(scheme$p),PiYG:last_1(scheme$o),PuOr:last_1(scheme$n),RdBu:last_1(scheme$m),RdGy:last_1(scheme$l),RdYlBu:last_1(scheme$k),RdYlGn:last_1(scheme$j),spectral:last_1(scheme$i),blues:last_1(scheme$5),greens:last_1(scheme$4),greys:last_1(scheme$3),oranges:last_1(scheme),purples:last_1(scheme$2),reds:last_1(scheme$1),BuGn:last_1(scheme$h),BuPu:last_1(scheme$g),GnBu:last_1(scheme$f),OrRd:last_1(scheme$e),PuBuGn:last_1(scheme$d),PuBu:last_1(scheme$c),PuRd:last_1(scheme$b),RdPu:last_1(scheme$a),YlGnBu:last_1(scheme$9),YlGn:last_1(scheme$8),YlOrBr:last_1(scheme$7),YlOrRd:last_1(scheme$6)},ot=Object.keys(nt);({nivo:["#e8c1a0","#f47560","#f1e15b","#e8a838","#61cdbb","#97e3d5"],category10:e,accent:r,dark2:n,paired:t,pastel1:o,pastel2:i,set1:u,set2:a,set3:s,brown_blueGreen:last_1(scheme$q),purpleRed_green:last_1(scheme$p),pink_yellowGreen:last_1(scheme$o),purple_orange:last_1(scheme$n),red_blue:last_1(scheme$m),red_grey:last_1(scheme$l),red_yellow_blue:last_1(scheme$k),red_yellow_green:last_1(scheme$j),spectral:last_1(scheme$i),blues:last_1(scheme$5),greens:last_1(scheme$4),greys:last_1(scheme$3),oranges:last_1(scheme),purples:last_1(scheme$2),reds:last_1(scheme$1),blue_green:last_1(scheme$h),blue_purple:last_1(scheme$g),green_blue:last_1(scheme$f),orange_red:last_1(scheme$e),purple_blue_green:last_1(scheme$d),purple_blue:last_1(scheme$c),purple_red:last_1(scheme$b),red_purple:last_1(scheme$a),yellow_green_blue:last_1(scheme$9),yellow_green:last_1(scheme$8),yellow_orange_brown:last_1(scheme$7),yellow_orange_red:last_1(scheme$6)});propTypesExports.oneOfType([propTypesExports.oneOf(ot),propTypesExports.func,propTypesExports.arrayOf(propTypesExports.string)]);var ht={basis:$e$1,basisClosed:er$1,basisOpen:rr,bundle:tr$1,cardinal:ir$1,cardinalClosed:nr$1,cardinalOpen:or,catmullRom:ar,catmullRomClosed:sr$1,catmullRomOpen:lr$1,linear:dr,linearClosed:ur$1,monotoneX:monotoneX,monotoneY:monotoneY,natural:pr,step:hr,stepAfter:stepAfter,stepBefore:stepBefore},gt=Object.keys(ht);propTypesExports.oneOf(gt);gt.filter((function(e){return e.endsWith("Closed")}));without_1(gt,"bundle","basisClosed","basisOpen","cardinalClosed","cardinalOpen","catmullRomClosed","catmullRomOpen","linearClosed");var vt=without_1(gt,"bundle","basisClosed","basisOpen","cardinalClosed","cardinalOpen","catmullRomClosed","catmullRomOpen","linearClosed");propTypesExports.oneOf(vt);({defs:propTypesExports.arrayOf(propTypesExports.shape({id:propTypesExports.string.isRequired})).isRequired,fill:propTypesExports.arrayOf(propTypesExports.shape({id:propTypesExports.string.isRequired,match:propTypesExports.oneOfType([propTypesExports.oneOf(["*"]),propTypesExports.object,propTypesExports.func]).isRequired})).isRequired});var _t={ascending:br,descending:yr,insideOut:vr,none:Rr,reverse:qr},wt=Object.keys(_t);propTypesExports.oneOf(wt);var Ct={expand:kr,diverging:_r,none:wr,silhouette:xr,wiggle:Or},Wt=Object.keys(Ct);propTypesExports.oneOf(Wt);propTypesExports.shape({top:propTypesExports.number,right:propTypesExports.number,bottom:propTypesExports.number,left:propTypesExports.number}).isRequired;var Tt=["normal","multiply","screen","overlay","darken","lighten","color-dodge","color-burn","hard-light","soft-light","difference","exclusion","hue","saturation","color","luminosity"];propTypesExports.oneOf(Tt);ordinal(s);var Yt={top:0,right:0,bottom:0,left:0},At=function(e,t,i){return void 0===i&&(i={}),useMemo((function(){var r=Gr({},Yt,i);return {margin:r,innerWidth:e-r.left-r.right,innerHeight:t-r.top-r.bottom,outerWidth:e,outerHeight:t}}),[e,t,i.top,i.right,i.bottom,i.left])},Ft=function(){var e=useRef(null),r=useState({left:0,top:0,width:0,height:0}),t=r[0],a=r[1],s=useState((function(){return new ResizeObserver((function(e){var r=e[0];return a(r.contentRect)}))}))[0];return useEffect((function(){return e.current&&s.observe(e.current),function(){return s.disconnect()}}),[]),[e,t]},Et=function(e){return useMemo((function(){return Qr(Vr,e)}),[e])},Ut=function(e){return "function"==typeof e?e:"string"==typeof e?0===e.indexOf("time:")?timeFormat(e.slice("5")):format(e):function(e){return ""+e}},Xt=function(e){return useMemo((function(){return Ut(e)}),[e])},Kt=createContext(),Ht={},Nt=function(e){var r=e.theme,t=void 0===r?Ht:r,i=e.children,n=Et(t);return jsx(Kt.Provider,{value:n,children:i})};Nt.propTypes={children:propTypesExports.node.isRequired,theme:propTypesExports.object};var Vt=function(){return useContext(Kt)},Jt=function(e){var r=e.children,t=e.condition,i=e.wrapper;return t?cloneElement(i,{},r):r};Jt.propTypes={children:propTypesExports.node.isRequired,condition:propTypesExports.bool.isRequired,wrapper:propTypesExports.element.isRequired};var Qt={position:"relative"},Zt=function(e){var r=e.children,t=e.theme,n=e.renderWrapper,o=void 0===n||n,a=e.isInteractive,s=void 0===a||a,l=e.animate,d=e.motionStiffness,u=e.motionDamping,c=e.motionConfig,h=useRef(null);return jsx(Nt,{theme:t,children:jsx($r,{animate:l,stiffness:d,damping:u,config:c,children:jsx(M$1,{container:h,children:jsxs(Jt,{condition:o,wrapper:jsx("div",{style:Qt,ref:h}),children:[r,s&&jsx(F,{})]})})})})};Zt.propTypes={children:propTypesExports.element.isRequired,isInteractive:propTypesExports.bool,renderWrapper:propTypesExports.bool,theme:propTypesExports.object,animate:propTypesExports.bool,motionStiffness:propTypesExports.number,motionDamping:propTypesExports.number,motionConfig:propTypesExports.string};({children:propTypesExports.func.isRequired,isInteractive:propTypesExports.bool,renderWrapper:propTypesExports.bool,theme:propTypesExports.object.isRequired,animate:propTypesExports.bool.isRequired,motionStiffness:propTypesExports.number,motionDamping:propTypesExports.number,motionConfig:propTypesExports.string});({children:propTypesExports.func.isRequired});var ii=["id","colors"],ni=function(e){var r=e.id,t=e.colors,i=Lr(e,ii);return jsx("linearGradient",Gr({id:r,x1:0,x2:0,y1:0,y2:1},i,{children:t.map((function(e){var r=e.offset,t=e.color,i=e.opacity;return jsx("stop",{offset:r+"%",stopColor:t,stopOpacity:void 0!==i?i:1},r)}))}))};ni.propTypes={id:propTypesExports.string.isRequired,colors:propTypesExports.arrayOf(propTypesExports.shape({offset:propTypesExports.number.isRequired,color:propTypesExports.string.isRequired,opacity:propTypesExports.number})).isRequired,gradientTransform:propTypesExports.string};var ai={linearGradient:ni},si=memo((function(e){var r=e.id,t=e.background,i=e.color,n=e.size,o=e.padding,a=e.stagger,s=n+o,l=n/2,d=o/2;return !0===a&&(s=2*n+2*o),jsxs("pattern",{id:r,width:s,height:s,patternUnits:"userSpaceOnUse",children:[jsx("rect",{width:s,height:s,fill:t}),jsx("circle",{cx:d+l,cy:d+l,r:l,fill:i}),a&&jsx("circle",{cx:1.5*o+n+l,cy:1.5*o+n+l,r:l,fill:i})]})}));si.displayName="PatternDots",si.propTypes={id:propTypesExports.string.isRequired,color:propTypesExports.string.isRequired,background:propTypesExports.string.isRequired,size:propTypesExports.number.isRequired,padding:propTypesExports.number.isRequired,stagger:propTypesExports.bool.isRequired},si.defaultProps={color:"#000000",background:"#ffffff",size:4,padding:4,stagger:!1};var ui=function(e){return e*Math.PI/180},ci=function(e){return 180*e/Math.PI},pi=function(e,r){return {x:Math.cos(e)*r,y:Math.sin(e)*r}},hi=function(e){var r=e%360;return r<0&&(r+=360),r},yi={svg:{align:{left:"start",center:"middle",right:"end",start:"start",middle:"middle",end:"end"},baseline:{top:"text-before-edge",center:"central",bottom:"alphabetic"}},canvas:{align:{left:"left",center:"center",right:"right",start:"left",middle:"center",end:"right"},baseline:{top:"top",center:"middle",bottom:"bottom"}}},Ri=memo((function(e){var r=e.id,t=e.spacing,i=e.rotation,n=e.background,o=e.color,a=e.lineWidth,s=Math.round(i)%360,l=Math.abs(t);s>180?s-=360:s>90?s-=180:s<-180?s+=360:s<-90&&(s+=180);var d,u=l,c=l;return 0===s?d="\n                M 0 0 L "+u+" 0\n                M 0 "+c+" L "+u+" "+c+"\n            ":90===s?d="\n                M 0 0 L 0 "+c+"\n                M "+u+" 0 L "+u+" "+c+"\n            ":(u=Math.abs(l/Math.sin(ui(s))),c=l/Math.sin(ui(90-s)),d=s>0?"\n                    M 0 "+-c+" L "+2*u+" "+c+"\n                    M "+-u+" "+-c+" L "+u+" "+c+"\n                    M "+-u+" 0 L "+u+" "+2*c+"\n                ":"\n                    M "+-u+" "+c+" L "+u+" "+-c+"\n                    M "+-u+" "+2*c+" L "+2*u+" "+-c+"\n                    M 0 "+2*c+" L "+2*u+" 0\n                "),jsxs("pattern",{id:r,width:u,height:c,patternUnits:"userSpaceOnUse",children:[jsx("rect",{width:u,height:c,fill:n,stroke:"rgba(255, 0, 0, 0.1)",strokeWidth:0}),jsx("path",{d:d,strokeWidth:a,stroke:o,strokeLinecap:"square"})]})}));Ri.displayName="PatternLines",Ri.propTypes={id:propTypesExports.string.isRequired,spacing:propTypesExports.number.isRequired,rotation:propTypesExports.number.isRequired,background:propTypesExports.string.isRequired,color:propTypesExports.string.isRequired,lineWidth:propTypesExports.number.isRequired},Ri.defaultProps={spacing:5,rotation:0,color:"#000000",background:"#ffffff",lineWidth:2};var ki=memo((function(e){var r=e.id,t=e.background,i=e.color,n=e.size,o=e.padding,a=e.stagger,s=n+o,l=o/2;return !0===a&&(s=2*n+2*o),jsxs("pattern",{id:r,width:s,height:s,patternUnits:"userSpaceOnUse",children:[jsx("rect",{width:s,height:s,fill:t}),jsx("rect",{x:l,y:l,width:n,height:n,fill:i}),a&&jsx("rect",{x:1.5*o+n,y:1.5*o+n,width:n,height:n,fill:i})]})}));ki.displayName="PatternSquares",ki.propTypes={id:propTypesExports.string.isRequired,color:propTypesExports.string.isRequired,background:propTypesExports.string.isRequired,size:propTypesExports.number.isRequired,padding:propTypesExports.number.isRequired,stagger:propTypesExports.bool.isRequired},ki.defaultProps={color:"#000000",background:"#ffffff",size:4,padding:4,stagger:!1};var wi={patternDots:si,patternLines:Ri,patternSquares:ki},xi=["type"],Oi=Gr({},ai,wi),Ci=function(e){var r=e.defs;return !r||r.length<1?null:jsx("defs",{"aria-hidden":!0,children:r.map((function(e){var r=e.type,t=Lr(e,xi);return Oi[r]?createElement$1(Oi[r],Gr({key:t.id},t)):null}))})};Ci.propTypes={defs:propTypesExports.arrayOf(propTypesExports.shape({type:propTypesExports.oneOf(Object.keys(Oi)).isRequired,id:propTypesExports.string.isRequired}))};memo(Ci);({width:propTypesExports.number.isRequired,height:propTypesExports.number.isRequired,margin:propTypesExports.shape({top:propTypesExports.number.isRequired,left:propTypesExports.number.isRequired}).isRequired,defs:propTypesExports.array,children:propTypesExports.oneOfType([propTypesExports.arrayOf(propTypesExports.node),propTypesExports.node]).isRequired,role:propTypesExports.string,isFocusable:propTypesExports.bool,ariaLabel:propTypesExports.string,ariaLabelledBy:propTypesExports.string,ariaDescribedBy:propTypesExports.string});var zi=function(e){var r=e.size,t=e.color,i=e.borderWidth,n=e.borderColor;return jsx("circle",{r:r/2,fill:t,stroke:n,strokeWidth:i,style:{pointerEvents:"none"}})};zi.propTypes={size:propTypesExports.number.isRequired,color:propTypesExports.string.isRequired,borderWidth:propTypesExports.number.isRequired,borderColor:propTypesExports.string.isRequired};var Pi=memo(zi),Ti=function(e){var r=e.x,t=e.y,i=e.symbol,n=void 0===i?Pi:i,o=e.size,a=e.datum,s=e.color,l=e.borderWidth,u=e.borderColor,c=e.label,f=e.labelTextAnchor,p=void 0===f?"middle":f,h=e.labelYOffset,g=void 0===h?-12:h,m=Vt(),b=tt(),y=b.animate,v=b.config,R=useSpring({transform:"translate("+r+", "+t+")",config:v,immediate:!y});return jsxs(animated.g,{transform:R.transform,style:{pointerEvents:"none"},children:[createElement$1(n,{size:o,color:s,datum:a,borderWidth:l,borderColor:u}),c&&jsx("text",{textAnchor:p,y:g,style:m.dots.text,children:c})]})};Ti.propTypes={x:propTypesExports.number.isRequired,y:propTypesExports.number.isRequired,datum:propTypesExports.object.isRequired,size:propTypesExports.number.isRequired,color:propTypesExports.string.isRequired,borderWidth:propTypesExports.number.isRequired,borderColor:propTypesExports.string.isRequired,symbol:propTypesExports.oneOfType([propTypesExports.func,propTypesExports.object]),label:propTypesExports.oneOfType([propTypesExports.string,propTypesExports.number]),labelTextAnchor:propTypesExports.oneOf(["start","middle","end"]),labelYOffset:propTypesExports.number.isRequired};memo(Ti);var ji=function(e){var r=e.width,t=e.height,i=e.axis,n=e.scale,o=e.value,a=e.lineStyle,s=e.textStyle,l=e.legend,d=e.legendPosition,u=e.legendOffsetX,c=e.legendOffsetY,f=e.legendOrientation,p=Vt(),h=0,g=0,m=0,b=0;"y"===i?(m=n(o),g=r):(h=n(o),b=t);var y=null;if(l){var v=function(e){var r=e.axis,t=e.width,i=e.height,n=e.position,o=e.offsetX,a=e.offsetY,s=e.orientation,l=0,d=0,u="vertical"===s?-90:0,c="start";if("x"===r)switch(n){case"top-left":l=-o,d=a,c="end";break;case"top":d=-a,c="horizontal"===s?"middle":"start";break;case"top-right":l=o,d=a,c="horizontal"===s?"start":"end";break;case"right":l=o,d=i/2,c="horizontal"===s?"start":"middle";break;case"bottom-right":l=o,d=i-a,c="start";break;case"bottom":d=i+a,c="horizontal"===s?"middle":"end";break;case"bottom-left":d=i-a,l=-o,c="horizontal"===s?"end":"start";break;case"left":l=-o,d=i/2,c="horizontal"===s?"end":"middle";}else switch(n){case"top-left":l=o,d=-a,c="start";break;case"top":l=t/2,d=-a,c="horizontal"===s?"middle":"start";break;case"top-right":l=t-o,d=-a,c="horizontal"===s?"end":"start";break;case"right":l=t+o,c="horizontal"===s?"start":"middle";break;case"bottom-right":l=t-o,d=a,c="end";break;case"bottom":l=t/2,d=a,c="horizontal"===s?"middle":"end";break;case"bottom-left":l=o,d=a,c="horizontal"===s?"start":"end";break;case"left":l=-o,c="horizontal"===s?"end":"middle";}return {x:l,y:d,rotation:u,textAnchor:c}}({axis:i,width:r,height:t,position:d,offsetX:u,offsetY:c,orientation:f});y=jsx("text",{transform:"translate("+v.x+", "+v.y+") rotate("+v.rotation+")",textAnchor:v.textAnchor,dominantBaseline:"central",style:s,children:l});}return jsxs("g",{transform:"translate("+h+", "+m+")",children:[jsx("line",{x1:0,x2:g,y1:0,y2:b,stroke:p.markers.lineColor,strokeWidth:p.markers.lineStrokeWidth,style:a}),y]})};ji.propTypes={width:propTypesExports.number.isRequired,height:propTypesExports.number.isRequired,axis:propTypesExports.oneOf(["x","y"]).isRequired,scale:propTypesExports.func.isRequired,value:propTypesExports.oneOfType([propTypesExports.number,propTypesExports.string,propTypesExports.instanceOf(Date)]).isRequired,lineStyle:propTypesExports.object,textStyle:propTypesExports.object,legend:propTypesExports.string,legendPosition:propTypesExports.oneOf(["top-left","top","top-right","right","bottom-right","bottom","bottom-left","left"]),legendOffsetX:propTypesExports.number.isRequired,legendOffsetY:propTypesExports.number.isRequired,legendOrientation:propTypesExports.oneOf(["horizontal","vertical"]).isRequired},ji.defaultProps={legendPosition:"top-right",legendOffsetX:14,legendOffsetY:14,legendOrientation:"horizontal"};var Di=memo(ji),Gi=function(e){var r=e.markers,t=e.width,i=e.height,n=e.xScale,o=e.yScale;return r&&0!==r.length?r.map((function(e,r){return jsx(Di,Gr({},e,{width:t,height:i,scale:"y"===e.axis?o:n}),r)})):null};Gi.propTypes={width:propTypesExports.number.isRequired,height:propTypesExports.number.isRequired,xScale:propTypesExports.func.isRequired,yScale:propTypesExports.func.isRequired,markers:propTypesExports.arrayOf(propTypesExports.shape({axis:propTypesExports.oneOf(["x","y"]).isRequired,value:propTypesExports.oneOfType([propTypesExports.number,propTypesExports.string,propTypesExports.instanceOf(Date)]).isRequired,lineStyle:propTypesExports.object,textStyle:propTypesExports.object}))};memo(Gi);var Ui=function(e){return isFunction_1(e)?e:function(r){return get_1(r,e)}},Xi=function(e){return useMemo((function(){return Ui(e)}),[e])},Ji=function(e,r,t,i,n,o){return e<=n&&n<=e+t&&r<=o&&o<=r+i},Qi=function(e,r){var t=r.clientX,i=r.clientY,n=e.getBoundingClientRect();return [t-n.left,i-n.top]};

/**
 * This method returns `undefined`.
 *
 * @static
 * @memberOf _
 * @since 2.3.0
 * @category Util
 * @example
 *
 * _.times(2, _.noop);
 * // => [undefined, undefined]
 */

function noop$2() {
  // No operation performed.
}

var noop_1 = noop$2;

var Set$1 = _Set,
    noop$1 = noop_1,
    setToArray = _setToArray;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/**
 * Creates a set object of `values`.
 *
 * @private
 * @param {Array} values The values to add to the set.
 * @returns {Object} Returns the new set.
 */
!(Set$1 && (1 / setToArray(new Set$1([,-0]))[1]) == INFINITY) ? noop$1 : function(values) {
  return new Set$1(values);
};

var Stack$1 = _Stack,
    baseIsEqual$1 = _baseIsEqual;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$1 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;

/**
 * The base implementation of `_.isMatch` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Array} matchData The property names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */
function baseIsMatch$1(object, source, matchData, customizer) {
  var index = matchData.length,
      length = index,
      noCustomizer = !customizer;

  if (object == null) {
    return !length;
  }
  object = Object(object);
  while (index--) {
    var data = matchData[index];
    if ((noCustomizer && data[2])
          ? data[1] !== object[data[0]]
          : !(data[0] in object)
        ) {
      return false;
    }
  }
  while (++index < length) {
    data = matchData[index];
    var key = data[0],
        objValue = object[key],
        srcValue = data[1];

    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var stack = new Stack$1;
      if (customizer) {
        var result = customizer(objValue, srcValue, key, object, source, stack);
      }
      if (!(result === undefined
            ? baseIsEqual$1(srcValue, objValue, COMPARE_PARTIAL_FLAG$1 | COMPARE_UNORDERED_FLAG$1, customizer, stack)
            : result
          )) {
        return false;
      }
    }
  }
  return true;
}

var _baseIsMatch = baseIsMatch$1;

var isObject$1 = isObject_1;

/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */
function isStrictComparable$2(value) {
  return value === value && !isObject$1(value);
}

var _isStrictComparable = isStrictComparable$2;

var isStrictComparable$1 = _isStrictComparable,
    keys$3 = keys_1;

/**
 * Gets the property names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */
function getMatchData$1(object) {
  var result = keys$3(object),
      length = result.length;

  while (length--) {
    var key = result[length],
        value = object[key];

    result[length] = [key, value, isStrictComparable$1(value)];
  }
  return result;
}

var _getMatchData = getMatchData$1;

/**
 * A specialized version of `matchesProperty` for source values suitable
 * for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */

function matchesStrictComparable$2(key, srcValue) {
  return function(object) {
    if (object == null) {
      return false;
    }
    return object[key] === srcValue &&
      (srcValue !== undefined || (key in Object(object)));
  };
}

var _matchesStrictComparable = matchesStrictComparable$2;

var baseIsMatch = _baseIsMatch,
    getMatchData = _getMatchData,
    matchesStrictComparable$1 = _matchesStrictComparable;

/**
 * The base implementation of `_.matches` which doesn't clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatches$1(source) {
  var matchData = getMatchData(source);
  if (matchData.length == 1 && matchData[0][2]) {
    return matchesStrictComparable$1(matchData[0][0], matchData[0][1]);
  }
  return function(object) {
    return object === source || baseIsMatch(object, source, matchData);
  };
}

var _baseMatches = baseMatches$1;

var baseIsEqual = _baseIsEqual,
    get = get_1,
    hasIn = hasIn_1,
    isKey$1 = _isKey,
    isStrictComparable = _isStrictComparable,
    matchesStrictComparable = _matchesStrictComparable,
    toKey$2 = _toKey;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG = 1,
    COMPARE_UNORDERED_FLAG = 2;

/**
 * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatchesProperty$1(path, srcValue) {
  if (isKey$1(path) && isStrictComparable(srcValue)) {
    return matchesStrictComparable(toKey$2(path), srcValue);
  }
  return function(object) {
    var objValue = get(object, path);
    return (objValue === undefined && objValue === srcValue)
      ? hasIn(object, path)
      : baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG);
  };
}

var _baseMatchesProperty = baseMatchesProperty$1;

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */

function baseProperty$1(key) {
  return function(object) {
    return object == null ? undefined : object[key];
  };
}

var _baseProperty = baseProperty$1;

var baseGet$2 = _baseGet;

/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyDeep$1(path) {
  return function(object) {
    return baseGet$2(object, path);
  };
}

var _basePropertyDeep = basePropertyDeep$1;

var baseProperty = _baseProperty,
    basePropertyDeep = _basePropertyDeep,
    isKey = _isKey,
    toKey$1 = _toKey;

/**
 * Creates a function that returns the value at `path` of a given object.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': 2 } },
 *   { 'a': { 'b': 1 } }
 * ];
 *
 * _.map(objects, _.property('a.b'));
 * // => [2, 1]
 *
 * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
 * // => [1, 2]
 */
function property$1(path) {
  return isKey(path) ? baseProperty(toKey$1(path)) : basePropertyDeep(path);
}

var property_1 = property$1;

var baseMatches = _baseMatches,
    baseMatchesProperty = _baseMatchesProperty,
    identity$1 = identity_1,
    isArray$3 = isArray_1,
    property = property_1;

/**
 * The base implementation of `_.iteratee`.
 *
 * @private
 * @param {*} [value=_.identity] The value to convert to an iteratee.
 * @returns {Function} Returns the iteratee.
 */
function baseIteratee$2(value) {
  // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
  // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
  if (typeof value == 'function') {
    return value;
  }
  if (value == null) {
    return identity$1;
  }
  if (typeof value == 'object') {
    return isArray$3(value)
      ? baseMatchesProperty(value[0], value[1])
      : baseMatches(value);
  }
  return property(value);
}

var _baseIteratee = baseIteratee$2;

var baseFor = _baseFor,
    keys$2 = keys_1;

/**
 * The base implementation of `_.forOwn` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Object} Returns `object`.
 */
function baseForOwn$1(object, iteratee) {
  return object && baseFor(object, iteratee, keys$2);
}

var _baseForOwn = baseForOwn$1;

var isArrayLike$1 = isArrayLike_1;

/**
 * Creates a `baseEach` or `baseEachRight` function.
 *
 * @private
 * @param {Function} eachFunc The function to iterate over a collection.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseEach$1(eachFunc, fromRight) {
  return function(collection, iteratee) {
    if (collection == null) {
      return collection;
    }
    if (!isArrayLike$1(collection)) {
      return eachFunc(collection, iteratee);
    }
    var length = collection.length,
        index = fromRight ? length : -1,
        iterable = Object(collection);

    while ((fromRight ? index-- : ++index < length)) {
      if (iteratee(iterable[index], index, iterable) === false) {
        break;
      }
    }
    return collection;
  };
}

var _createBaseEach = createBaseEach$1;

var baseForOwn = _baseForOwn,
    createBaseEach = _createBaseEach;

/**
 * The base implementation of `_.forEach` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array|Object} Returns `collection`.
 */
var baseEach$2 = createBaseEach(baseForOwn);

var _baseEach = baseEach$2;

var baseEach$1 = _baseEach,
    isArrayLike = isArrayLike_1;

/**
 * The base implementation of `_.map` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function baseMap$1(collection, iteratee) {
  var index = -1,
      result = isArrayLike(collection) ? Array(collection.length) : [];

  baseEach$1(collection, function(value, key, collection) {
    result[++index] = iteratee(value, key, collection);
  });
  return result;
}

var _baseMap = baseMap$1;

/**
 * The base implementation of `_.sortBy` which uses `comparer` to define the
 * sort order of `array` and replaces criteria objects with their corresponding
 * values.
 *
 * @private
 * @param {Array} array The array to sort.
 * @param {Function} comparer The function to define sort order.
 * @returns {Array} Returns `array`.
 */

function baseSortBy$1(array, comparer) {
  var length = array.length;

  array.sort(comparer);
  while (length--) {
    array[length] = array[length].value;
  }
  return array;
}

var _baseSortBy = baseSortBy$1;

var isSymbol = isSymbol_1;

/**
 * Compares values to sort them in ascending order.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {number} Returns the sort order indicator for `value`.
 */
function compareAscending$1(value, other) {
  if (value !== other) {
    var valIsDefined = value !== undefined,
        valIsNull = value === null,
        valIsReflexive = value === value,
        valIsSymbol = isSymbol(value);

    var othIsDefined = other !== undefined,
        othIsNull = other === null,
        othIsReflexive = other === other,
        othIsSymbol = isSymbol(other);

    if ((!othIsNull && !othIsSymbol && !valIsSymbol && value > other) ||
        (valIsSymbol && othIsDefined && othIsReflexive && !othIsNull && !othIsSymbol) ||
        (valIsNull && othIsDefined && othIsReflexive) ||
        (!valIsDefined && othIsReflexive) ||
        !valIsReflexive) {
      return 1;
    }
    if ((!valIsNull && !valIsSymbol && !othIsSymbol && value < other) ||
        (othIsSymbol && valIsDefined && valIsReflexive && !valIsNull && !valIsSymbol) ||
        (othIsNull && valIsDefined && valIsReflexive) ||
        (!othIsDefined && valIsReflexive) ||
        !othIsReflexive) {
      return -1;
    }
  }
  return 0;
}

var _compareAscending = compareAscending$1;

var compareAscending = _compareAscending;

/**
 * Used by `_.orderBy` to compare multiple properties of a value to another
 * and stable sort them.
 *
 * If `orders` is unspecified, all values are sorted in ascending order. Otherwise,
 * specify an order of "desc" for descending or "asc" for ascending sort order
 * of corresponding values.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {boolean[]|string[]} orders The order to sort by for each property.
 * @returns {number} Returns the sort order indicator for `object`.
 */
function compareMultiple$1(object, other, orders) {
  var index = -1,
      objCriteria = object.criteria,
      othCriteria = other.criteria,
      length = objCriteria.length,
      ordersLength = orders.length;

  while (++index < length) {
    var result = compareAscending(objCriteria[index], othCriteria[index]);
    if (result) {
      if (index >= ordersLength) {
        return result;
      }
      var order = orders[index];
      return result * (order == 'desc' ? -1 : 1);
    }
  }
  // Fixes an `Array#sort` bug in the JS engine embedded in Adobe applications
  // that causes it, under certain circumstances, to provide the same value for
  // `object` and `other`. See https://github.com/jashkenas/underscore/pull/1247
  // for more details.
  //
  // This also ensures a stable sort in V8 and other engines.
  // See https://bugs.chromium.org/p/v8/issues/detail?id=90 for more details.
  return object.index - other.index;
}

var _compareMultiple = compareMultiple$1;

var arrayMap$1 = _arrayMap,
    baseGet$1 = _baseGet,
    baseIteratee$1 = _baseIteratee,
    baseMap = _baseMap,
    baseSortBy = _baseSortBy,
    baseUnary$2 = _baseUnary,
    compareMultiple = _compareMultiple,
    identity = identity_1,
    isArray$2 = isArray_1;

/**
 * The base implementation of `_.orderBy` without param guards.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function[]|Object[]|string[]} iteratees The iteratees to sort by.
 * @param {string[]} orders The sort orders of `iteratees`.
 * @returns {Array} Returns the new sorted array.
 */
function baseOrderBy$1(collection, iteratees, orders) {
  if (iteratees.length) {
    iteratees = arrayMap$1(iteratees, function(iteratee) {
      if (isArray$2(iteratee)) {
        return function(value) {
          return baseGet$1(value, iteratee.length === 1 ? iteratee[0] : iteratee);
        };
      }
      return iteratee;
    });
  } else {
    iteratees = [identity];
  }

  var index = -1;
  iteratees = arrayMap$1(iteratees, baseUnary$2(baseIteratee$1));

  var result = baseMap(collection, function(value, key, collection) {
    var criteria = arrayMap$1(iteratees, function(iteratee) {
      return iteratee(value);
    });
    return { 'criteria': criteria, 'index': ++index, 'value': value };
  });

  return baseSortBy(result, function(object, other) {
    return compareMultiple(object, other, orders);
  });
}

var _baseOrderBy = baseOrderBy$1;

var baseFlatten = _baseFlatten,
    baseOrderBy = _baseOrderBy,
    baseRest = _baseRest,
    isIterateeCall = _isIterateeCall;

/**
 * Creates an array of elements, sorted in ascending order by the results of
 * running each element in a collection thru each iteratee. This method
 * performs a stable sort, that is, it preserves the original sort order of
 * equal elements. The iteratees are invoked with one argument: (value).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {...(Function|Function[])} [iteratees=[_.identity]]
 *  The iteratees to sort by.
 * @returns {Array} Returns the new sorted array.
 * @example
 *
 * var users = [
 *   { 'user': 'fred',   'age': 48 },
 *   { 'user': 'barney', 'age': 36 },
 *   { 'user': 'fred',   'age': 30 },
 *   { 'user': 'barney', 'age': 34 }
 * ];
 *
 * _.sortBy(users, [function(o) { return o.user; }]);
 * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 30]]
 *
 * _.sortBy(users, ['user', 'age']);
 * // => objects for [['barney', 34], ['barney', 36], ['fred', 30], ['fred', 48]]
 */
baseRest(function(collection, iteratees) {
  if (collection == null) {
    return [];
  }
  var length = iteratees.length;
  if (length > 1 && isIterateeCall(collection, iteratees[0], iteratees[1])) {
    iteratees = [];
  } else if (length > 2 && isIterateeCall(iteratees[0], iteratees[1], iteratees[2])) {
    iteratees = [iteratees[0]];
  }
  return baseOrderBy(collection, baseFlatten(iteratees, 1), []);
});

var nodeUtil$2 = _nodeUtilExports;

/* Node.js helper references. */
nodeUtil$2 && nodeUtil$2.isDate;

var t0 = new Date,
    t1 = new Date;

function newInterval(floori, offseti, count, field) {

  function interval(date) {
    return floori(date = arguments.length === 0 ? new Date : new Date(+date)), date;
  }

  interval.floor = function(date) {
    return floori(date = new Date(+date)), date;
  };

  interval.ceil = function(date) {
    return floori(date = new Date(date - 1)), offseti(date, 1), floori(date), date;
  };

  interval.round = function(date) {
    var d0 = interval(date),
        d1 = interval.ceil(date);
    return date - d0 < d1 - date ? d0 : d1;
  };

  interval.offset = function(date, step) {
    return offseti(date = new Date(+date), step == null ? 1 : Math.floor(step)), date;
  };

  interval.range = function(start, stop, step) {
    var range = [], previous;
    start = interval.ceil(start);
    step = step == null ? 1 : Math.floor(step);
    if (!(start < stop) || !(step > 0)) return range; // also handles Invalid Date
    do range.push(previous = new Date(+start)), offseti(start, step), floori(start);
    while (previous < start && start < stop);
    return range;
  };

  interval.filter = function(test) {
    return newInterval(function(date) {
      if (date >= date) while (floori(date), !test(date)) date.setTime(date - 1);
    }, function(date, step) {
      if (date >= date) {
        if (step < 0) while (++step <= 0) {
          while (offseti(date, -1), !test(date)) {} // eslint-disable-line no-empty
        } else while (--step >= 0) {
          while (offseti(date, +1), !test(date)) {} // eslint-disable-line no-empty
        }
      }
    });
  };

  if (count) {
    interval.count = function(start, end) {
      t0.setTime(+start), t1.setTime(+end);
      floori(t0), floori(t1);
      return Math.floor(count(t0, t1));
    };

    interval.every = function(step) {
      step = Math.floor(step);
      return !isFinite(step) || !(step > 0) ? null
          : !(step > 1) ? interval
          : interval.filter(field
              ? function(d) { return field(d) % step === 0; }
              : function(d) { return interval.count(0, d) % step === 0; });
    };
  }

  return interval;
}

var millisecond = newInterval(function() {
  // noop
}, function(date, step) {
  date.setTime(+date + step);
}, function(start, end) {
  return end - start;
});

// An optimized implementation for this simple case.
millisecond.every = function(k) {
  k = Math.floor(k);
  if (!isFinite(k) || !(k > 0)) return null;
  if (!(k > 1)) return millisecond;
  return newInterval(function(date) {
    date.setTime(Math.floor(date / k) * k);
  }, function(date, step) {
    date.setTime(+date + step * k);
  }, function(start, end) {
    return (end - start) / k;
  });
};

var p$1 = millisecond;
millisecond.range;

var durationSecond = 1e3;
var durationMinute = 6e4;
var durationHour = 36e5;
var durationWeek = 6048e5;

var second = newInterval(function(date) {
  date.setTime(date - date.getMilliseconds());
}, function(date, step) {
  date.setTime(+date + step * durationSecond);
}, function(start, end) {
  return (end - start) / durationSecond;
}, function(date) {
  return date.getUTCSeconds();
});

var g$1 = second;
second.range;

var minute = newInterval(function(date) {
  date.setTime(date - date.getMilliseconds() - date.getSeconds() * durationSecond);
}, function(date, step) {
  date.setTime(+date + step * durationMinute);
}, function(start, end) {
  return (end - start) / durationMinute;
}, function(date) {
  return date.getMinutes();
});

var x$1 = minute;
minute.range;

var hour = newInterval(function(date) {
  date.setTime(date - date.getMilliseconds() - date.getSeconds() * durationSecond - date.getMinutes() * durationMinute);
}, function(date, step) {
  date.setTime(+date + step * durationHour);
}, function(start, end) {
  return (end - start) / durationHour;
}, function(date) {
  return date.getHours();
});

var T$1 = hour;
hour.range;

function weekday(i) {
  return newInterval(function(date) {
    date.setDate(date.getDate() - (date.getDay() + 7 - i) % 7);
    date.setHours(0, 0, 0, 0);
  }, function(date, step) {
    date.setDate(date.getDate() + step * 7);
  }, function(start, end) {
    return (end - start - (end.getTimezoneOffset() - start.getTimezoneOffset()) * durationMinute) / durationWeek;
  });
}

var sunday = weekday(0);
var monday = weekday(1);
var tuesday = weekday(2);
var wednesday = weekday(3);
var thursday = weekday(4);
var friday = weekday(5);
var saturday = weekday(6);

sunday.range;
monday.range;
tuesday.range;
wednesday.range;
thursday.range;
friday.range;
saturday.range;

var month = newInterval(function(date) {
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
}, function(date, step) {
  date.setMonth(date.getMonth() + step);
}, function(start, end) {
  return end.getMonth() - start.getMonth() + (end.getFullYear() - start.getFullYear()) * 12;
}, function(date) {
  return date.getMonth();
});

var N$1 = month;
month.range;

var year = newInterval(function(date) {
  date.setMonth(0, 1);
  date.setHours(0, 0, 0, 0);
}, function(date, step) {
  date.setFullYear(date.getFullYear() + step);
}, function(start, end) {
  return end.getFullYear() - start.getFullYear();
}, function(date) {
  return date.getFullYear();
});

// An optimized implementation for this simple case.
year.every = function(k) {
  return !isFinite(k = Math.floor(k)) || !(k > 0) ? null : newInterval(function(date) {
    date.setFullYear(Math.floor(date.getFullYear() / k) * k);
    date.setMonth(0, 1);
    date.setHours(0, 0, 0, 0);
  }, function(date, step) {
    date.setFullYear(date.getFullYear() + step * k);
  });
};

var I = year;
year.range;

var utcMinute = newInterval(function(date) {
  date.setUTCSeconds(0, 0);
}, function(date, step) {
  date.setTime(+date + step * durationMinute);
}, function(start, end) {
  return (end - start) / durationMinute;
}, function(date) {
  return date.getUTCMinutes();
});

var k$1 = utcMinute;
utcMinute.range;

var utcHour = newInterval(function(date) {
  date.setUTCMinutes(0, 0, 0);
}, function(date, step) {
  date.setTime(+date + step * durationHour);
}, function(start, end) {
  return (end - start) / durationHour;
}, function(date) {
  return date.getUTCHours();
});

var M = utcHour;
utcHour.range;

function utcWeekday(i) {
  return newInterval(function(date) {
    date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 7 - i) % 7);
    date.setUTCHours(0, 0, 0, 0);
  }, function(date, step) {
    date.setUTCDate(date.getUTCDate() + step * 7);
  }, function(start, end) {
    return (end - start) / durationWeek;
  });
}

var utcSunday = utcWeekday(0);
var utcMonday = utcWeekday(1);
var utcTuesday = utcWeekday(2);
var utcWednesday = utcWeekday(3);
var utcThursday = utcWeekday(4);
var utcFriday = utcWeekday(5);
var utcSaturday = utcWeekday(6);

utcSunday.range;
utcMonday.range;
utcTuesday.range;
utcWednesday.range;
utcThursday.range;
utcFriday.range;
utcSaturday.range;

var utcMonth = newInterval(function(date) {
  date.setUTCDate(1);
  date.setUTCHours(0, 0, 0, 0);
}, function(date, step) {
  date.setUTCMonth(date.getUTCMonth() + step);
}, function(start, end) {
  return end.getUTCMonth() - start.getUTCMonth() + (end.getUTCFullYear() - start.getUTCFullYear()) * 12;
}, function(date) {
  return date.getUTCMonth();
});

var z$2 = utcMonth;
utcMonth.range;

var utcYear = newInterval(function(date) {
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
}, function(date, step) {
  date.setUTCFullYear(date.getUTCFullYear() + step);
}, function(start, end) {
  return end.getUTCFullYear() - start.getUTCFullYear();
}, function(date) {
  return date.getUTCFullYear();
});

// An optimized implementation for this simple case.
utcYear.every = function(k) {
  return !isFinite(k = Math.floor(k)) || !(k > 0) ? null : newInterval(function(date) {
    date.setUTCFullYear(Math.floor(date.getUTCFullYear() / k) * k);
    date.setUTCMonth(0, 1);
    date.setUTCHours(0, 0, 0, 0);
  }, function(date, step) {
    date.setUTCFullYear(date.getUTCFullYear() + step * k);
  });
};

var P$2 = utcYear;
utcYear.range;

var nn=function(n){var t=n;return t.type="band",t};var pn=function(n){var t=n.bandwidth();if(0===t)return n;var r=t/2;return n.round()&&(r=Math.round(r)),function(t){var e;return (null!=(e=n(t))?e:0)+r}},hn={millisecond:[p$1,p$1],second:[g$1,g$1],minute:[x$1,k$1],hour:[T$1,M],day:[newInterval((function(n){return n.setHours(0,0,0,0)}),(function(n,t){return n.setDate(n.getDate()+t)}),(function(n,t){return (t.getTime()-n.getTime())/864e5}),(function(n){return Math.floor(n.getTime()/864e5)})),newInterval((function(n){return n.setUTCHours(0,0,0,0)}),(function(n,t){return n.setUTCDate(n.getUTCDate()+t)}),(function(n,t){return (t.getTime()-n.getTime())/864e5}),(function(n){return Math.floor(n.getTime()/864e5)}))],week:[sunday,utcSunday],sunday:[sunday,utcSunday],monday:[monday,utcMonday],tuesday:[tuesday,utcTuesday],wednesday:[wednesday,utcWednesday],thursday:[thursday,utcThursday],friday:[friday,utcFriday],saturday:[saturday,utcSaturday],month:[N$1,z$2],year:[I,P$2]},gn=Object.keys(hn),xn=new RegExp("^every\\s*(\\d+)?\\s*("+gn.join("|")+")s?$","i"),kn=function(n,t){if(Array.isArray(t))return t;if("string"==typeof t&&"useUTC"in n){var r=t.match(xn);if(r){var e=r[1],a=r[2],i=hn[a][n.useUTC?1:0];if("day"===a){var o,u,c=n.domain(),f=c[0],d=c[1],s=new Date(d);return s.setDate(s.getDate()+1),null!=(o=null==(u=i.every(Number(null!=e?e:1)))?void 0:u.range(f,s))?o:[]}if(void 0===e)return n.ticks(i);var l=i.every(Number(e));if(l)return n.ticks(l)}throw new Error("Invalid tickValues: "+t)}if("ticks"in n){if(void 0===t)return n.ticks();if("number"==typeof(m=t)&&isFinite(m)&&Math.floor(m)===m)return n.ticks(t)}var m;return n.domain()};

function v$1(){return v$1=Object.assign||function(t){for(var e=1;e<arguments.length;e++){var i=arguments[e];for(var n in i)Object.prototype.hasOwnProperty.call(i,n)&&(t[n]=i[n]);}return t},v$1.apply(this,arguments)}var p=function(t){var e,i=t.axis,n=t.scale,r=t.ticksPosition,a=t.tickValues,o=t.tickSize,s=t.tickPadding,c=t.tickRotation,f=t.engine,x=void 0===f?"svg":f,d=kn(n,a),y=yi[x],g="bandwidth"in n?pn(n):n,k={lineX:0,lineY:0},h={textX:0,textY:0},p="object"==typeof document&&"rtl"===document.dir,b=y.align.center,P=y.baseline.center;return "x"===i?(e=function(t){var e;return {x:null!=(e=g(t))?e:0,y:0}},k.lineY=o*("after"===r?1:-1),h.textY=(o+s)*("after"===r?1:-1),P="after"===r?y.baseline.top:y.baseline.bottom,0===c?b=y.align.center:"after"===r&&c<0||"before"===r&&c>0?(b=y.align[p?"left":"right"],P=y.baseline.center):("after"===r&&c>0||"before"===r&&c<0)&&(b=y.align[p?"right":"left"],P=y.baseline.center)):(e=function(t){var e;return {x:0,y:null!=(e=g(t))?e:0}},k.lineX=o*("after"===r?1:-1),h.textX=(o+s)*("after"===r?1:-1),b="after"===r?y.align.left:y.align.right),{ticks:d.map((function(t){return v$1({key:"number"==typeof t||"string"==typeof t?t:""+t,value:t},e(t),k,h)})),textAlign:b,textBaseline:P}},b$1=function(t,e){if(void 0===t||"function"==typeof t)return t;if("time"===e.type){var i=timeFormat(t);return function(t){return i(t instanceof Date?t:new Date(t))}}return format(t)},P$1=function(t){var e,i=t.width,n=t.height,r=t.scale,a=t.axis,o=t.values,l=(e=o,Array.isArray(e)?o:void 0)||kn(r,o),s="bandwidth"in r?pn(r):r,c="x"===a?l.map((function(t){var e,i;return {key:""+t,x1:null!=(e=s(t))?e:0,x2:null!=(i=s(t))?i:0,y1:0,y2:n}})):l.map((function(t){var e,n;return {key:""+t,x1:0,x2:i,y1:null!=(e=s(t))?e:0,y2:null!=(n=s(t))?n:0}}));return c},S$1=memo((function(t){var e,n=t.value,a=t.format,o=t.lineX,l=t.lineY,c=t.onClick,f=t.textBaseline,x=t.textAnchor,d=t.animatedProps,u=Vt(),m=null!=(e=null==a?void 0:a(n))?e:n,k=useMemo((function(){var t={opacity:d.opacity};return c?{style:v$1({},t,{cursor:"pointer"}),onClick:function(t){return c(t,m)}}:{style:t}}),[d.opacity,c,m]);return jsxs(animated.g,v$1({transform:d.transform},k,{children:[jsx("line",{x1:0,x2:o,y1:0,y2:l,style:u.axis.ticks.line}),jsx(animated.text,{dominantBaseline:f,textAnchor:x,transform:d.textTransform,style:u.axis.ticks.text,children:m})]}))})),T=memo((function(e){var n=e.axis,l=e.scale,f=e.x,x=void 0===f?0:f,d=e.y,u=void 0===d?0:d,m=e.length,k=e.ticksPosition,h=e.tickValues,P=e.tickSize,T=void 0===P?5:P,w=e.tickPadding,A=void 0===w?5:w,O=e.tickRotation,X=void 0===O?0:O,Y=e.format,W=e.renderTick,B=void 0===W?S$1:W,z=e.legend,V=e.legendPosition,C=void 0===V?"end":V,R=e.legendOffset,j=void 0===R?0:R,E=e.onClick,D=e.ariaHidden,q=Vt(),F=useMemo((function(){return b$1(Y,l)}),[Y,l]),H=p({axis:n,scale:l,ticksPosition:k,tickValues:h,tickSize:T,tickPadding:A,tickRotation:X}),N=H.ticks,I=H.textAlign,G=H.textBaseline,J=null;if(void 0!==z){var K,L=0,M=0,Q=0;"y"===n?(Q=-90,L=j,"start"===C?(K="start",M=m):"middle"===C?(K="middle",M=m/2):"end"===C&&(K="end")):(M=j,"start"===C?K="start":"middle"===C?(K="middle",L=m/2):"end"===C&&(K="end",L=m)),J=jsx("text",{transform:"translate("+L+", "+M+") rotate("+Q+")",textAnchor:K,style:v$1({dominantBaseline:"central"},q.axis.legend.text),children:z});}var U=tt(),Z=U.animate,$=U.config,_=useSpring({transform:"translate("+x+","+u+")",lineX2:"x"===n?m:0,lineY2:"x"===n?0:m,config:$,immediate:!Z}),tt$1=useTransition(N,{keys:function(t){return t.key},initial:function(t){return {opacity:1,transform:"translate("+t.x+","+t.y+")",textTransform:"translate("+t.textX+","+t.textY+") rotate("+X+")"}},from:function(t){return {opacity:0,transform:"translate("+t.x+","+t.y+")",textTransform:"translate("+t.textX+","+t.textY+") rotate("+X+")"}},enter:function(t){return {opacity:1,transform:"translate("+t.x+","+t.y+")",textTransform:"translate("+t.textX+","+t.textY+") rotate("+X+")"}},update:function(t){return {opacity:1,transform:"translate("+t.x+","+t.y+")",textTransform:"translate("+t.textX+","+t.textY+") rotate("+X+")"}},leave:{opacity:0},config:$,immediate:!Z});return jsxs(animated.g,{transform:_.transform,"aria-hidden":D,children:[tt$1((function(e,i,n,r){return React.createElement(B,v$1({tickIndex:r,format:F,rotate:X,textBaseline:G,textAnchor:I,animatedProps:e},i,E?{onClick:E}:{}))})),jsx(animated.line,{style:q.axis.domain.line,x1:0,x2:_.lineX2,y1:0,y2:_.lineY2}),J]})})),w$1={ticksPosition:propTypesExports.oneOf(["before","after"]),tickValues:propTypesExports.oneOfType([propTypesExports.number,propTypesExports.arrayOf(propTypesExports.oneOfType([propTypesExports.number,propTypesExports.string,propTypesExports.instanceOf(Date)])),propTypesExports.string]),tickSize:propTypesExports.number,tickPadding:propTypesExports.number,tickRotation:propTypesExports.number,format:propTypesExports.oneOfType([propTypesExports.func,propTypesExports.string]),renderTick:propTypesExports.func,legend:propTypesExports.node,legendPosition:propTypesExports.oneOf(["start","middle","end"]),legendOffset:propTypesExports.number,ariaHidden:propTypesExports.bool};propTypesExports.shape(w$1);var O$1=["top","right","bottom","left"];memo((function(t){var e=t.xScale,i=t.yScale,n=t.width,r=t.height,a={top:t.top,right:t.right,bottom:t.bottom,left:t.left};return jsx(Fragment$1,{children:O$1.map((function(t){var o=a[t];if(!o)return null;var l="top"===t||"bottom"===t;return jsx(T,v$1({},o,{axis:l?"x":"y",x:"right"===t?n:0,y:"bottom"===t?r:0,scale:l?e:i,length:l?n:r,ticksPosition:"top"===t||"left"===t?"before":"after"}),t)}))})}));var Y$1=memo((function(t){var e=t.animatedProps,i=Vt();return jsx(animated.line,v$1({},e,i.grid.line))})),W=memo((function(t){var e=t.lines,i=tt(),r=i.animate,a=i.config,l=useTransition(e,{keys:function(t){return t.key},initial:function(t){return {opacity:1,x1:t.x1,x2:t.x2,y1:t.y1,y2:t.y2}},from:function(t){return {opacity:0,x1:t.x1,x2:t.x2,y1:t.y1,y2:t.y2}},enter:function(t){return {opacity:1,x1:t.x1,x2:t.x2,y1:t.y1,y2:t.y2}},update:function(t){return {opacity:1,x1:t.x1,x2:t.x2,y1:t.y1,y2:t.y2}},leave:{opacity:0},config:a,immediate:!r});return jsx("g",{children:l((function(t,e){return createElement$1(Y$1,v$1({},e,{key:e.key,animatedProps:t}))}))})}));memo((function(t){var e=t.width,n=t.height,r=t.xScale,a=t.yScale,o=t.xValues,l=t.yValues,s=useMemo((function(){return !!r&&P$1({width:e,height:n,scale:r,axis:"x",values:o})}),[r,o,e,n]),c=useMemo((function(){return !!a&&P$1({width:e,height:n,scale:a,axis:"y",values:l})}),[n,e,a,l]);return jsxs(Fragment$1,{children:[s&&jsx(W,{lines:s}),c&&jsx(W,{lines:c})]})}));var z$1=function(t,e){var i,n=e.axis,r=e.scale,a=e.x,o=void 0===a?0:a,l=e.y,s=void 0===l?0:l,c=e.length,x=e.ticksPosition,d=e.tickValues,u=e.tickSize,m=void 0===u?5:u,y=e.tickPadding,g=void 0===y?5:y,k=e.tickRotation,h=void 0===k?0:k,v=e.format,b=e.legend,P=e.legendPosition,S=void 0===P?"end":P,T=e.legendOffset,w=void 0===T?0:T,A=e.theme,O=p({axis:n,scale:r,ticksPosition:x,tickValues:d,tickSize:m,tickPadding:g,tickRotation:h,engine:"canvas"}),X=O.ticks,Y=O.textAlign,W=O.textBaseline;t.save(),t.translate(o,s),t.textAlign=Y,t.textBaseline=W,t.font=(A.axis.ticks.text.fontWeight?A.axis.ticks.text.fontWeight+" ":"")+A.axis.ticks.text.fontSize+"px "+A.axis.ticks.text.fontFamily,(null!=(i=A.axis.domain.line.strokeWidth)?i:0)>0&&(t.lineWidth=Number(A.axis.domain.line.strokeWidth),t.lineCap="square",A.axis.domain.line.stroke&&(t.strokeStyle=A.axis.domain.line.stroke),t.beginPath(),t.moveTo(0,0),t.lineTo("x"===n?c:0,"x"===n?0:c),t.stroke());var B="function"==typeof v?v:function(t){return ""+t};if(X.forEach((function(e){var i;(null!=(i=A.axis.ticks.line.strokeWidth)?i:0)>0&&(t.lineWidth=Number(A.axis.ticks.line.strokeWidth),t.lineCap="square",A.axis.ticks.line.stroke&&(t.strokeStyle=A.axis.ticks.line.stroke),t.beginPath(),t.moveTo(e.x,e.y),t.lineTo(e.x+e.lineX,e.y+e.lineY),t.stroke());var n=B(e.value);t.save(),t.translate(e.x+e.textX,e.y+e.textY),t.rotate(ui(h)),A.axis.ticks.text.fill&&(t.fillStyle=A.axis.ticks.text.fill),t.fillText(String(n),0,0),t.restore();})),void 0!==b){var z=0,V=0,C=0,R="center";"y"===n?(C=-90,z=w,"start"===S?(R="start",V=c):"middle"===S?(R="center",V=c/2):"end"===S&&(R="end")):(V=w,"start"===S?R="start":"middle"===S?(R="center",z=c/2):"end"===S&&(R="end",z=c)),t.translate(z,V),t.rotate(ui(C)),t.font=(A.axis.legend.text.fontWeight?A.axis.legend.text.fontWeight+" ":"")+A.axis.legend.text.fontSize+"px "+A.axis.legend.text.fontFamily,A.axis.legend.text.fill&&(t.fillStyle=A.axis.legend.text.fill),t.textAlign=R,t.textBaseline="middle",t.fillText(b,0,0);}t.restore();},V=function(t,e){var i=e.xScale,n=e.yScale,r=e.width,a=e.height,o=e.top,l=e.right,s=e.bottom,c=e.left,f=e.theme,x={top:o,right:l,bottom:s,left:c};O$1.forEach((function(e){var o=x[e];if(!o)return null;var l="top"===e||"bottom"===e,s="top"===e||"left"===e?"before":"after",c=l?i:n,d=b$1(o.format,c);z$1(t,v$1({},o,{axis:l?"x":"y",x:"right"===e?r:0,y:"bottom"===e?a:0,scale:c,format:d,length:l?r:a,ticksPosition:s,theme:f}));}));},C$1=function(t,e){var i=e.width,n=e.height,r=e.scale,a=e.axis,o=e.values;P$1({width:i,height:n,scale:r,axis:a,values:o}).forEach((function(e){t.beginPath(),t.moveTo(e.x1,e.y1),t.lineTo(e.x2,e.y2),t.stroke();}));};

function qe(){return qe=Object.assign||function(e){for(var r=1;r<arguments.length;r++){var n=arguments[r];for(var t in n)Object.prototype.hasOwnProperty.call(n,t)&&(e[t]=n[t]);}return e},qe.apply(this,arguments)}function Re(e,r){(null==r||r>e.length)&&(r=e.length);for(var n=0,t=new Array(r);n<r;n++)t[n]=e[n];return t}function Se(e,r){var n="undefined"!=typeof Symbol&&e[Symbol.iterator]||e["@@iterator"];if(n)return (n=n.call(e)).next.bind(n);if(Array.isArray(e)||(n=function(e,r){if(e){if("string"==typeof e)return Re(e,r);var n=Object.prototype.toString.call(e).slice(8,-1);return "Object"===n&&e.constructor&&(n=e.constructor.name),"Map"===n||"Set"===n?Array.from(e):"Arguments"===n||/^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)?Re(e,r):void 0}}(e))||r&&e&&"number"==typeof e.length){n&&(e=n);var t=0;return function(){return t>=e.length?{done:!0}:{done:!1,value:e[t++]}}}throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.")}var Ce={nivo:["#e8c1a0","#f47560","#f1e15b","#e8a838","#61cdbb","#97e3d5"],category10:e,accent:r,dark2:n,paired:t,pastel1:o,pastel2:i,set1:u,set2:a,set3:s},Ve={brown_blueGreen:scheme$q,purpleRed_green:scheme$p,pink_yellowGreen:scheme$o,purple_orange:scheme$n,red_blue:scheme$m,red_grey:scheme$l,red_yellow_blue:scheme$k,red_yellow_green:scheme$j,spectral:scheme$i},Pe={brown_blueGreen:b$2,purpleRed_green:v$2,pink_yellowGreen:_$1,purple_orange:w$2,red_blue:O$2,red_grey:k$2,red_yellow_blue:z$3,red_yellow_green:A$1,spectral:j$1},Ue={blues:scheme$5,greens:scheme$4,greys:scheme$3,oranges:scheme,purples:scheme$2,reds:scheme$1,blue_green:scheme$h,blue_purple:scheme$g,green_blue:scheme$f,orange_red:scheme$e,purple_blue_green:scheme$d,purple_blue:scheme$c,purple_red:scheme$b,red_purple:scheme$a,yellow_green_blue:scheme$9,yellow_green:scheme$8,yellow_orange_brown:scheme$7,yellow_orange_red:scheme$6},Me={blues:J$2,greens:K$1,greys:L,oranges:N$2,purples:Q$1,reds:W$1,turbo:X,viridis:Y$2,inferno:inferno,magma:magma,plasma:plasma,cividis:ne,warm:warm,cool:cool,cubehelixDefault:ie,blue_green:ue,blue_purple:ae,green_blue:se,orange_red:ce,purple_blue_green:le,purple_blue:fe$1,purple_red:de,red_purple:pe$1,yellow_green_blue:me,yellow_green:he,yellow_orange_brown:ge$1,yellow_orange_red:ye},$e=qe({},Ce,Ve,Ue),Be=Object.keys($e),Ke={rainbow:be$1,sinebow:ve$1},Le=qe({},Pe,Me,Ke),Qe=function(e,r){if("function"==typeof e)return e;if(isPlainObject_1(e)){if(function(e){return void 0!==e.theme}(e)){if(void 0===r)throw new Error("Unable to use color from theme as no theme was provided");var n=get_1(r,e.theme);if(void 0===n)throw new Error("Color from theme is undefined at path: '"+e.theme+"'");return function(){return n}}if(function(e){return void 0!==e.from}(e)){var t=function(r){return get_1(r,e.from)};if(Array.isArray(e.modifiers)){for(var o,i=[],u=function(){var e=o.value,r=e[0],n=e[1];if("brighter"===r)i.push((function(e){return e.brighter(n)}));else if("darker"===r)i.push((function(e){return e.darker(n)}));else {if("opacity"!==r)throw new Error("Invalid color modifier: '"+r+"', must be one of: 'brighter', 'darker', 'opacity'");i.push((function(e){return e.opacity=n,e}));}},a=Se(e.modifiers);!(o=a()).done;)u();return 0===i.length?t:function(e){return i.reduce((function(e,r){return r(e)}),rgb$1(t(e))).toString()}}return t}throw new Error("Invalid color spec, you should either specify 'theme' or 'from' when using a config object")}return function(){return e}},We=function(e,r){return useMemo((function(){return Qe(e,r)}),[e,r])};propTypesExports.oneOfType([propTypesExports.func,propTypesExports.arrayOf(propTypesExports.string),propTypesExports.shape({scheme:propTypesExports.oneOf(Be).isRequired,size:propTypesExports.number}),propTypesExports.shape({datum:propTypesExports.string.isRequired}),propTypesExports.string]);propTypesExports.oneOfType([propTypesExports.string,propTypesExports.func,propTypesExports.shape({theme:propTypesExports.string.isRequired}),propTypesExports.shape({from:propTypesExports.string.isRequired,modifiers:propTypesExports.arrayOf(propTypesExports.array)})]);var Ze={scheme:"turbo"},er=function(e,r){var n=e.scheme,t=void 0===n?Ze.scheme:n,o=e.minValue,i=e.maxValue,u=void 0!==o?o:r.min,a=void 0!==i?i:r.max;return sequential().domain([u,a]).interpolator(Le[t])},nr={scheme:"red_yellow_blue",divergeAt:.5},tr=function(e,r){var n=e.scheme,t=void 0===n?nr.scheme:n,o=e.divergeAt,i=void 0===o?nr.divergeAt:o,u=e.minValue,a=e.maxValue,s=void 0!==u?u:r.min,c=void 0!==a?a:r.max,l=[s,s+(c-s)/2,c],f=Le[t],d=.5-i;return diverging((function(e){return f(e+d)})).domain(l).clamp(!0)},ir={scheme:"turbo",steps:7},ur=function(e,r){var n=quantize().domain(e.domain||[r.min,r.max]).nice();if("colors"in e)n.range(e.colors);else {var t=e.scheme||ir.scheme,o=void 0===e.steps?ir.steps:e.steps,i=Le[t],u=Array.from({length:o}).map((function(e,r){return i(r*(1/(o-1)))}));n.range(u);}return n},sr=function(e,r){if(function(e){return "sequential"===e.type}(e))return er(e,r);if(function(e){return "diverging"===e.type}(e))return tr(e,r);if(function(e){return "quantize"===e.type}(e))return ur(e,r);throw new Error("Invalid continuous color scale config")},lr=function(e,r){void 0===r&&(r=16);var n=e.domain();if("thresholds"in e){var t=[],o=linear().domain(n).range([0,1]);return e.range().forEach((function(r,n){var i=e.invertExtent(r),u=i[0],a=i[1];t.push({key:n+".0",offset:o(u),stopColor:r}),t.push({key:n+".1",offset:o(a),stopColor:r});})),t}var i=e.copy();return 2===n.length?i.domain([0,1]):3===n.length&&i.domain([0,.5,1]),i.ticks(r).map((function(e){return {key:""+e,offset:e,stopColor:""+i(e)}}))};

var y={length:200,thickness:16,direction:"row",tickPosition:"after",tickSize:4,tickSpacing:3,tickOverlap:!1,tickFormat:function(t){return ""+t},titleAlign:"start",titleOffset:4},x=function(t){var e=t.anchor,i=t.translateX,n=t.translateY,o=t.containerWidth,r=t.containerHeight,l=t.width,a=t.height,c=i,s=n;switch(e){case"top":c+=(o-l)/2;break;case"top-right":c+=o-l;break;case"right":c+=o-l,s+=(r-a)/2;break;case"bottom-right":c+=o-l,s+=r-a;break;case"bottom":c+=(o-l)/2,s+=r-a;break;case"bottom-left":s+=r-a;break;case"left":s+=(r-a)/2;break;case"center":c+=(o-l)/2,s+=(r-a)/2;}return {x:c,y:s}},A=function(t){var e,o=t.scale,r=t.ticks,a=t.length,c=void 0===a?y.length:a,s=t.thickness,d=void 0===s?y.thickness:s,h=t.direction,g=void 0===h?y.direction:h,m=t.tickPosition,f=void 0===m?y.tickPosition:m,u=t.tickSize,v=void 0===u?y.tickSize:u,p=t.tickSpacing,b=void 0===p?y.tickSpacing:p,k=t.tickOverlap,x=void 0===k?y.tickOverlap:k,S=t.tickFormat,A=void 0===S?y.tickFormat:S,W=t.title,z=t.titleAlign,C=void 0===z?y.titleAlign:z,O=t.titleOffset,B=void 0===O?y.titleOffset:O,w="column"===g?[].concat(o.domain()).reverse():o.domain(),X=linear().domain(w);2===w.length?X.range([0,c]):3===w.length&&X.range([0,c/2,c]),e="thresholds"in o?[w[0]].concat(o.thresholds(),[w[1]]):Array.isArray(r)?r:o.ticks(r);var Y,H,E,j,T,L,M=lr(o,32),F=Ut(A),P=[],R=0,q=0;if("row"===g){var V,D,G;Y=c,H=d,q=1;var I;T=0,E="start"===C?0:"middle"===C?c/2:c,"before"===f?(V=-v,D=x?d:0,G=-v-b,I="alphabetic",j=d+B,L="hanging"):(V=x?0:d,G=(D=d+v)+b,I="hanging",j=-B,L="alphabetic"),e.forEach((function(t){var e=X(t);P.push({x1:e,y1:V,x2:e,y2:D,text:F(t),textX:e,textY:G,textHorizontalAlign:"middle",textVerticalAlign:I});}));}else {var N,_,J,K;Y=d,H=c,R=1;T=-90,j="start"===C?c:"middle"===C?c/2:0,"before"===f?(_=x?d:0,J=(N=-v)-b,K="end",E=d+B,L="hanging"):(N=x?0:d,J=(_=d+v)+b,K="start",E=-B,L="alphabetic"),e.forEach((function(t){var e=X(t);P.push({x1:N,y1:e,x2:_,y2:e,text:F(t),textX:J,textY:e,textHorizontalAlign:K,textVerticalAlign:"central"});}));}return {width:Y,height:H,gradientX1:0,gradientY1:R,gradientX2:q,gradientY2:0,colorStops:M,ticks:P,titleText:W,titleX:E,titleY:j,titleRotation:T,titleHorizontalAlign:C,titleVerticalAlign:L}},E=function(t,e){var i=e.containerWidth,n=e.containerHeight,o=e.anchor,l=e.translateX,a=void 0===l?0:l,c=e.translateY,s=void 0===c?0:c,d=e.scale,h=e.length,g=void 0===h?y.length:h,m=e.thickness,f=void 0===m?y.thickness:m,u=e.direction,v=void 0===u?y.direction:u,p=e.ticks,b=e.tickPosition,k=void 0===b?y.tickPosition:b,S=e.tickSize,W=void 0===S?y.tickSize:S,z=e.tickSpacing,C=void 0===z?y.tickSpacing:z,O=e.tickOverlap,B=void 0===O?y.tickOverlap:O,w=e.tickFormat,X=void 0===w?y.tickFormat:w,Y=e.title,H=e.titleAlign,E=void 0===H?y.titleAlign:H,j=e.titleOffset,T=void 0===j?y.titleOffset:j,L=e.theme,M=A({scale:d,ticks:p,length:g,thickness:f,direction:v,tickPosition:k,tickSize:W,tickSpacing:C,tickOverlap:B,tickFormat:X,title:Y,titleAlign:E,titleOffset:T}),F=M.width,P=M.height,R=M.gradientX1,q=M.gradientY1,V=M.gradientX2,D=M.gradientY2,G=M.colorStops,I=M.ticks,N=M.titleText,_=M.titleX,J=M.titleY,K=M.titleRotation,Q=M.titleVerticalAlign,U=M.titleHorizontalAlign,Z=x({anchor:o,translateX:a,translateY:s,containerWidth:i,containerHeight:n,width:F,height:P}),$=Z.x,tt=Z.y,et={font:t.font,textAlign:t.textAlign,textBaseline:t.textBaseline};t.save(),t.translate($,tt);var it=t.createLinearGradient(R*F,q*P,V*F,D*P);G.forEach((function(t){it.addColorStop(t.offset,t.stopColor);})),t.fillStyle=it,t.fillRect(0,0,F,P),t.font=(L.legends.ticks.text.fontWeight?L.legends.ticks.text.fontWeight+" ":"")+L.legends.ticks.text.fontSize+"px "+L.legends.ticks.text.fontFamily,I.forEach((function(e){var i;(null!=(i=L.legends.ticks.line.strokeWidth)?i:0)>0&&(t.lineWidth=Number(L.axis.ticks.line.strokeWidth),L.axis.ticks.line.stroke&&(t.strokeStyle=L.axis.ticks.line.stroke),t.lineCap="square",t.beginPath(),t.moveTo(e.x1,e.y1),t.lineTo(e.x2,e.y2),t.stroke()),L.legends.ticks.text.fill&&(t.fillStyle=L.legends.ticks.text.fill),t.textAlign="middle"===e.textHorizontalAlign?"center":e.textHorizontalAlign,t.textBaseline="central"===e.textVerticalAlign?"middle":e.textVerticalAlign,t.fillText(e.text,e.textX,e.textY);})),N&&(t.save(),t.translate(_,J),t.rotate(ui(K)),t.font=(L.legends.title.text.fontWeight?L.legends.title.text.fontWeight+" ":"")+L.legends.title.text.fontSize+"px "+L.legends.title.text.fontFamily,L.legends.title.text.fill&&(t.fillStyle=L.legends.title.text.fill),t.textAlign="middle"===U?"center":U,t.textBaseline=Q,t.fillText(N,0,0),t.restore()),t.restore(),t.font=et.font,t.textAlign=et.textAlign,t.textBaseline=et.textBaseline;};({data:propTypesExports.arrayOf(propTypesExports.object),anchor:propTypesExports.oneOf(["top","top-right","right","bottom-right","bottom","bottom-left","left","top-left","center"]).isRequired,translateX:propTypesExports.number,translateY:propTypesExports.number,direction:propTypesExports.oneOf(["row","column"]).isRequired,itemsSpacing:propTypesExports.number,itemWidth:propTypesExports.number.isRequired,itemHeight:propTypesExports.number.isRequired,itemDirection:propTypesExports.oneOf(["left-to-right","right-to-left","top-to-bottom","bottom-to-top"]),itemTextColor:propTypesExports.string,itemBackground:propTypesExports.string,itemOpacity:propTypesExports.number,symbolShape:propTypesExports.oneOfType([propTypesExports.oneOf(["circle","diamond","square","triangle"]),propTypesExports.func]),symbolSize:propTypesExports.number,symbolSpacing:propTypesExports.number,symbolBorderWidth:propTypesExports.number,symbolBorderColor:propTypesExports.string,onClick:propTypesExports.func,onMouseEnter:propTypesExports.func,onMouseLeave:propTypesExports.func,effects:propTypesExports.arrayOf(propTypesExports.shape({on:propTypesExports.oneOfType([propTypesExports.oneOf(["hover"])]).isRequired,style:propTypesExports.shape({itemTextColor:propTypesExports.string,itemBackground:propTypesExports.string,itemOpacity:propTypesExports.number,symbolSize:propTypesExports.number,symbolBorderWidth:propTypesExports.number,symbolBorderColor:propTypesExports.string}).isRequired}))});

/**
 * A specialized version of `_.forEach` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns `array`.
 */

function arrayEach$1(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (iteratee(array[index], index, array) === false) {
      break;
    }
  }
  return array;
}

var _arrayEach = arrayEach$1;

var copyObject$4 = _copyObject,
    keys$1 = keys_1;

/**
 * The base implementation of `_.assign` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssign$1(object, source) {
  return object && copyObject$4(source, keys$1(source), object);
}

var _baseAssign = baseAssign$1;

var copyObject$3 = _copyObject,
    keysIn$2 = keysIn_1;

/**
 * The base implementation of `_.assignIn` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssignIn$1(object, source) {
  return object && copyObject$3(source, keysIn$2(source), object);
}

var _baseAssignIn = baseAssignIn$1;

var copyObject$2 = _copyObject,
    getSymbols$1 = _getSymbols;

/**
 * Copies own symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbols$1(source, object) {
  return copyObject$2(source, getSymbols$1(source), object);
}

var _copySymbols = copySymbols$1;

var arrayPush = _arrayPush,
    getPrototype = _getPrototype,
    getSymbols = _getSymbols,
    stubArray = stubArray_1;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own and inherited enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbolsIn$2 = !nativeGetSymbols ? stubArray : function(object) {
  var result = [];
  while (object) {
    arrayPush(result, getSymbols(object));
    object = getPrototype(object);
  }
  return result;
};

var _getSymbolsIn = getSymbolsIn$2;

var copyObject$1 = _copyObject,
    getSymbolsIn$1 = _getSymbolsIn;

/**
 * Copies own and inherited symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbolsIn$1(source, object) {
  return copyObject$1(source, getSymbolsIn$1(source), object);
}

var _copySymbolsIn = copySymbolsIn$1;

var baseGetAllKeys = _baseGetAllKeys,
    getSymbolsIn = _getSymbolsIn,
    keysIn$1 = keysIn_1;

/**
 * Creates an array of own and inherited enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeysIn$2(object) {
  return baseGetAllKeys(object, keysIn$1, getSymbolsIn);
}

var _getAllKeysIn = getAllKeysIn$2;

/** Used for built-in method references. */

var objectProto$1 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$1.hasOwnProperty;

/**
 * Initializes an array clone.
 *
 * @private
 * @param {Array} array The array to clone.
 * @returns {Array} Returns the initialized clone.
 */
function initCloneArray$1(array) {
  var length = array.length,
      result = new array.constructor(length);

  // Add properties assigned by `RegExp#exec`.
  if (length && typeof array[0] == 'string' && hasOwnProperty$1.call(array, 'index')) {
    result.index = array.index;
    result.input = array.input;
  }
  return result;
}

var _initCloneArray = initCloneArray$1;

var cloneArrayBuffer$1 = _cloneArrayBuffer;

/**
 * Creates a clone of `dataView`.
 *
 * @private
 * @param {Object} dataView The data view to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned data view.
 */
function cloneDataView$1(dataView, isDeep) {
  var buffer = isDeep ? cloneArrayBuffer$1(dataView.buffer) : dataView.buffer;
  return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
}

var _cloneDataView = cloneDataView$1;

/** Used to match `RegExp` flags from their coerced string values. */

var reFlags = /\w*$/;

/**
 * Creates a clone of `regexp`.
 *
 * @private
 * @param {Object} regexp The regexp to clone.
 * @returns {Object} Returns the cloned regexp.
 */
function cloneRegExp$1(regexp) {
  var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
  result.lastIndex = regexp.lastIndex;
  return result;
}

var _cloneRegExp = cloneRegExp$1;

var Symbol$1 = _Symbol;

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol$1 ? Symbol$1.prototype : undefined,
    symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

/**
 * Creates a clone of the `symbol` object.
 *
 * @private
 * @param {Object} symbol The symbol object to clone.
 * @returns {Object} Returns the cloned symbol object.
 */
function cloneSymbol$1(symbol) {
  return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
}

var _cloneSymbol = cloneSymbol$1;

var cloneArrayBuffer = _cloneArrayBuffer,
    cloneDataView = _cloneDataView,
    cloneRegExp = _cloneRegExp,
    cloneSymbol = _cloneSymbol,
    cloneTypedArray = _cloneTypedArray;

/** `Object#toString` result references. */
var boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    mapTag$2 = '[object Map]',
    numberTag$2 = '[object Number]',
    regexpTag$1 = '[object RegExp]',
    setTag$2 = '[object Set]',
    stringTag$1 = '[object String]',
    symbolTag$1 = '[object Symbol]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$1 = '[object DataView]',
    float32Tag$1 = '[object Float32Array]',
    float64Tag$1 = '[object Float64Array]',
    int8Tag$1 = '[object Int8Array]',
    int16Tag$1 = '[object Int16Array]',
    int32Tag$1 = '[object Int32Array]',
    uint8Tag$1 = '[object Uint8Array]',
    uint8ClampedTag$1 = '[object Uint8ClampedArray]',
    uint16Tag$1 = '[object Uint16Array]',
    uint32Tag$1 = '[object Uint32Array]';

/**
 * Initializes an object clone based on its `toStringTag`.
 *
 * **Note:** This function only supports cloning values with tags of
 * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
 *
 * @private
 * @param {Object} object The object to clone.
 * @param {string} tag The `toStringTag` of the object to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneByTag$1(object, tag, isDeep) {
  var Ctor = object.constructor;
  switch (tag) {
    case arrayBufferTag$1:
      return cloneArrayBuffer(object);

    case boolTag$1:
    case dateTag$1:
      return new Ctor(+object);

    case dataViewTag$1:
      return cloneDataView(object, isDeep);

    case float32Tag$1: case float64Tag$1:
    case int8Tag$1: case int16Tag$1: case int32Tag$1:
    case uint8Tag$1: case uint8ClampedTag$1: case uint16Tag$1: case uint32Tag$1:
      return cloneTypedArray(object, isDeep);

    case mapTag$2:
      return new Ctor;

    case numberTag$2:
    case stringTag$1:
      return new Ctor(object);

    case regexpTag$1:
      return cloneRegExp(object);

    case setTag$2:
      return new Ctor;

    case symbolTag$1:
      return cloneSymbol(object);
  }
}

var _initCloneByTag = initCloneByTag$1;

var getTag$2 = _getTag,
    isObjectLike$2 = isObjectLike_1;

/** `Object#toString` result references. */
var mapTag$1 = '[object Map]';

/**
 * The base implementation of `_.isMap` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 */
function baseIsMap$1(value) {
  return isObjectLike$2(value) && getTag$2(value) == mapTag$1;
}

var _baseIsMap = baseIsMap$1;

var baseIsMap = _baseIsMap,
    baseUnary$1 = _baseUnary,
    nodeUtil$1 = _nodeUtilExports;

/* Node.js helper references. */
var nodeIsMap = nodeUtil$1 && nodeUtil$1.isMap;

/**
 * Checks if `value` is classified as a `Map` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 * @example
 *
 * _.isMap(new Map);
 * // => true
 *
 * _.isMap(new WeakMap);
 * // => false
 */
var isMap$1 = nodeIsMap ? baseUnary$1(nodeIsMap) : baseIsMap;

var isMap_1 = isMap$1;

var getTag$1 = _getTag,
    isObjectLike$1 = isObjectLike_1;

/** `Object#toString` result references. */
var setTag$1 = '[object Set]';

/**
 * The base implementation of `_.isSet` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 */
function baseIsSet$1(value) {
  return isObjectLike$1(value) && getTag$1(value) == setTag$1;
}

var _baseIsSet = baseIsSet$1;

var baseIsSet = _baseIsSet,
    baseUnary = _baseUnary,
    nodeUtil = _nodeUtilExports;

/* Node.js helper references. */
var nodeIsSet = nodeUtil && nodeUtil.isSet;

/**
 * Checks if `value` is classified as a `Set` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 * @example
 *
 * _.isSet(new Set);
 * // => true
 *
 * _.isSet(new WeakSet);
 * // => false
 */
var isSet$1 = nodeIsSet ? baseUnary(nodeIsSet) : baseIsSet;

var isSet_1 = isSet$1;

var Stack = _Stack,
    arrayEach = _arrayEach,
    assignValue = _assignValue,
    baseAssign = _baseAssign,
    baseAssignIn = _baseAssignIn,
    cloneBuffer = _cloneBufferExports,
    copyArray = _copyArray,
    copySymbols = _copySymbols,
    copySymbolsIn = _copySymbolsIn,
    getAllKeys = _getAllKeys,
    getAllKeysIn$1 = _getAllKeysIn,
    getTag = _getTag,
    initCloneArray = _initCloneArray,
    initCloneByTag = _initCloneByTag,
    initCloneObject = _initCloneObject,
    isArray$1 = isArray_1,
    isBuffer = isBufferExports,
    isMap = isMap_1,
    isObject = isObject_1,
    isSet = isSet_1,
    keys = keys_1,
    keysIn = keysIn_1;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG$1 = 1,
    CLONE_FLAT_FLAG$1 = 2,
    CLONE_SYMBOLS_FLAG$1 = 4;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]',
    arrayTag = '[object Array]',
    boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    mapTag = '[object Map]',
    numberTag$1 = '[object Number]',
    objectTag = '[object Object]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    symbolTag = '[object Symbol]',
    weakMapTag = '[object WeakMap]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values supported by `_.clone`. */
var cloneableTags = {};
cloneableTags[argsTag] = cloneableTags[arrayTag] =
cloneableTags[arrayBufferTag] = cloneableTags[dataViewTag] =
cloneableTags[boolTag] = cloneableTags[dateTag] =
cloneableTags[float32Tag] = cloneableTags[float64Tag] =
cloneableTags[int8Tag] = cloneableTags[int16Tag] =
cloneableTags[int32Tag] = cloneableTags[mapTag] =
cloneableTags[numberTag$1] = cloneableTags[objectTag] =
cloneableTags[regexpTag] = cloneableTags[setTag] =
cloneableTags[stringTag] = cloneableTags[symbolTag] =
cloneableTags[uint8Tag] = cloneableTags[uint8ClampedTag] =
cloneableTags[uint16Tag] = cloneableTags[uint32Tag] = true;
cloneableTags[errorTag] = cloneableTags[funcTag] =
cloneableTags[weakMapTag] = false;

/**
 * The base implementation of `_.clone` and `_.cloneDeep` which tracks
 * traversed objects.
 *
 * @private
 * @param {*} value The value to clone.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Deep clone
 *  2 - Flatten inherited properties
 *  4 - Clone symbols
 * @param {Function} [customizer] The function to customize cloning.
 * @param {string} [key] The key of `value`.
 * @param {Object} [object] The parent object of `value`.
 * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
 * @returns {*} Returns the cloned value.
 */
function baseClone$1(value, bitmask, customizer, key, object, stack) {
  var result,
      isDeep = bitmask & CLONE_DEEP_FLAG$1,
      isFlat = bitmask & CLONE_FLAT_FLAG$1,
      isFull = bitmask & CLONE_SYMBOLS_FLAG$1;

  if (customizer) {
    result = object ? customizer(value, key, object, stack) : customizer(value);
  }
  if (result !== undefined) {
    return result;
  }
  if (!isObject(value)) {
    return value;
  }
  var isArr = isArray$1(value);
  if (isArr) {
    result = initCloneArray(value);
    if (!isDeep) {
      return copyArray(value, result);
    }
  } else {
    var tag = getTag(value),
        isFunc = tag == funcTag || tag == genTag;

    if (isBuffer(value)) {
      return cloneBuffer(value, isDeep);
    }
    if (tag == objectTag || tag == argsTag || (isFunc && !object)) {
      result = (isFlat || isFunc) ? {} : initCloneObject(value);
      if (!isDeep) {
        return isFlat
          ? copySymbolsIn(value, baseAssignIn(result, value))
          : copySymbols(value, baseAssign(result, value));
      }
    } else {
      if (!cloneableTags[tag]) {
        return object ? value : {};
      }
      result = initCloneByTag(value, tag, isDeep);
    }
  }
  // Check for circular references and return its corresponding clone.
  stack || (stack = new Stack);
  var stacked = stack.get(value);
  if (stacked) {
    return stacked;
  }
  stack.set(value, result);

  if (isSet(value)) {
    value.forEach(function(subValue) {
      result.add(baseClone$1(subValue, bitmask, customizer, subValue, value, stack));
    });
  } else if (isMap(value)) {
    value.forEach(function(subValue, key) {
      result.set(key, baseClone$1(subValue, bitmask, customizer, key, value, stack));
    });
  }

  var keysFunc = isFull
    ? (isFlat ? getAllKeysIn$1 : getAllKeys)
    : (isFlat ? keysIn : keys);

  var props = isArr ? undefined : keysFunc(value);
  arrayEach(props || value, function(subValue, key) {
    if (props) {
      key = subValue;
      subValue = value[key];
    }
    // Recursively populate clone (susceptible to call stack limits).
    assignValue(result, key, baseClone$1(subValue, bitmask, customizer, key, value, stack));
  });
  return result;
}

var _baseClone = baseClone$1;

/**
 * The base implementation of `_.slice` without an iteratee call guard.
 *
 * @private
 * @param {Array} array The array to slice.
 * @param {number} [start=0] The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the slice of `array`.
 */

function baseSlice$1(array, start, end) {
  var index = -1,
      length = array.length;

  if (start < 0) {
    start = -start > length ? 0 : (length + start);
  }
  end = end > length ? length : end;
  if (end < 0) {
    end += length;
  }
  length = start > end ? 0 : ((end - start) >>> 0);
  start >>>= 0;

  var result = Array(length);
  while (++index < length) {
    result[index] = array[index + start];
  }
  return result;
}

var _baseSlice = baseSlice$1;

var baseGet = _baseGet,
    baseSlice = _baseSlice;

/**
 * Gets the parent value at `path` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array} path The path to get the parent value of.
 * @returns {*} Returns the parent value.
 */
function parent$1(object, path) {
  return path.length < 2 ? object : baseGet(object, baseSlice(path, 0, -1));
}

var _parent = parent$1;

var castPath$1 = _castPath,
    last = last_1,
    parent = _parent,
    toKey = _toKey;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * The base implementation of `_.unset`.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {Array|string} path The property path to unset.
 * @returns {boolean} Returns `true` if the property is deleted, else `false`.
 */
function baseUnset$1(object, path) {
  path = castPath$1(path, object);

  // Prevent prototype pollution:
  // https://github.com/lodash/lodash/security/advisories/GHSA-xxjr-mmjv-4gpg
  // https://github.com/lodash/lodash/security/advisories/GHSA-f23m-r3pf-42rh
  var index = -1,
      length = path.length;

  if (!length) {
    return true;
  }

  while (++index < length) {
    var key = toKey(path[index]);

    // Always block "__proto__" anywhere in the path if it's not expected
    if (key === '__proto__' && !hasOwnProperty.call(object, '__proto__')) {
      return false;
    }

    // Block constructor/prototype as non-terminal traversal keys to prevent
    // escaping the object graph into built-in constructors and prototypes.
    if ((key === 'constructor' || key === 'prototype') && index < length - 1) {
      return false;
    }
  }

  var obj = parent(object, path);
  return obj == null || delete obj[toKey(last(path))];
}

var _baseUnset = baseUnset$1;

var isPlainObject = isPlainObject_1;

/**
 * Used by `_.omit` to customize its `_.cloneDeep` use to only clone plain
 * objects.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {string} key The key of the property to inspect.
 * @returns {*} Returns the uncloned value or `undefined` to defer cloning to `_.cloneDeep`.
 */
function customOmitClone$1(value) {
  return isPlainObject(value) ? undefined : value;
}

var _customOmitClone = customOmitClone$1;

var arrayMap = _arrayMap,
    baseClone = _baseClone,
    baseUnset = _baseUnset,
    castPath = _castPath,
    copyObject = _copyObject,
    customOmitClone = _customOmitClone,
    flatRest = _flatRest,
    getAllKeysIn = _getAllKeysIn;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG = 1,
    CLONE_FLAT_FLAG = 2,
    CLONE_SYMBOLS_FLAG = 4;

/**
 * The opposite of `_.pick`; this method creates an object composed of the
 * own and inherited enumerable property paths of `object` that are not omitted.
 *
 * **Note:** This method is considerably slower than `_.pick`.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The source object.
 * @param {...(string|string[])} [paths] The property paths to omit.
 * @returns {Object} Returns the new object.
 * @example
 *
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 *
 * _.omit(object, ['a', 'c']);
 * // => { 'b': '2' }
 */
var omit = flatRest(function(object, paths) {
  var result = {};
  if (object == null) {
    return result;
  }
  var isDeep = false;
  paths = arrayMap(paths, function(path) {
    path = castPath(path, object);
    isDeep || (isDeep = path.length > 1);
    return path;
  });
  copyObject(object, getAllKeysIn(object), result);
  if (isDeep) {
    result = baseClone(result, CLONE_DEEP_FLAG | CLONE_FLAT_FLAG | CLONE_SYMBOLS_FLAG, customOmitClone);
  }
  var length = paths.length;
  while (length--) {
    baseUnset(result, paths[length]);
  }
  return result;
});

var omit_1 = omit;

var baseGetTag = _baseGetTag,
    isObjectLike = isObjectLike_1;

/** `Object#toString` result references. */
var numberTag = '[object Number]';

/**
 * Checks if `value` is classified as a `Number` primitive or object.
 *
 * **Note:** To exclude `Infinity`, `-Infinity`, and `NaN`, which are
 * classified as numbers, use the `_.isFinite` method.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a number, else `false`.
 * @example
 *
 * _.isNumber(3);
 * // => true
 *
 * _.isNumber(Number.MIN_VALUE);
 * // => true
 *
 * _.isNumber(Infinity);
 * // => true
 *
 * _.isNumber('3');
 * // => false
 */
function isNumber(value) {
  return typeof value == 'number' ||
    (isObjectLike(value) && baseGetTag(value) == numberTag);
}

var isNumber_1 = isNumber;

var baseEach = _baseEach;

/**
 * The base implementation of `_.filter` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function baseFilter$1(collection, predicate) {
  var result = [];
  baseEach(collection, function(value, index, collection) {
    if (predicate(value, index, collection)) {
      result.push(value);
    }
  });
  return result;
}

var _baseFilter = baseFilter$1;

var arrayFilter = _arrayFilter,
    baseFilter = _baseFilter,
    baseIteratee = _baseIteratee,
    isArray = isArray_1;

/**
 * Iterates over elements of `collection`, returning an array of all elements
 * `predicate` returns truthy for. The predicate is invoked with three
 * arguments: (value, index|key, collection).
 *
 * **Note:** Unlike `_.remove`, this method returns a new array.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 * @see _.reject
 * @example
 *
 * var users = [
 *   { 'user': 'barney', 'age': 36, 'active': true },
 *   { 'user': 'fred',   'age': 40, 'active': false }
 * ];
 *
 * _.filter(users, function(o) { return !o.active; });
 * // => objects for ['fred']
 *
 * // The `_.matches` iteratee shorthand.
 * _.filter(users, { 'age': 36, 'active': true });
 * // => objects for ['barney']
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.filter(users, ['active', false]);
 * // => objects for ['fred']
 *
 * // The `_.property` iteratee shorthand.
 * _.filter(users, 'active');
 * // => objects for ['barney']
 *
 * // Combining several predicates using `_.overEvery` or `_.overSome`.
 * _.filter(users, _.overSome([{ 'age': 36 }, ['age', 40]]));
 * // => objects for ['fred', 'barney']
 */
function filter(collection, predicate) {
  var func = isArray(collection) ? arrayFilter : baseFilter;
  return func(collection, baseIteratee(predicate));
}

var filter_1 = filter;

function g(){return g=Object.assign||function(t){for(var n=1;n<arguments.length;n++){var i=arguments[n];for(var o in i)Object.prototype.hasOwnProperty.call(i,o)&&(t[o]=i[o]);}return t},g.apply(this,arguments)}var k={dotSize:4,noteWidth:120,noteTextOffset:8,animate:!0,motionStiffness:90,motionDamping:13},v=function(t){var n=typeof t;return "string"===n||"function"===n},b=function(t){return "circle"===t.type},w=function(t){return "dot"===t.type},z=function(t){return "rect"===t.type},P=function(t){var n=t.data,i=t.annotations,e=t.getPosition,r=t.getDimensions;return i.reduce((function(t,i){var s=i.offset||0;return [].concat(t,filter_1(n,i.match).map((function(t){var n=e(t),a=r(t);return (b(i)||z(i))&&(a.size=a.size+2*s,a.width=a.width+2*s,a.height=a.height+2*s),g({},omit_1(i,["match","offset"]),n,a,{size:i.size||a.size,datum:t})})))}),[])},S=function(t,n,i,o){var e=Math.atan2(o-n,i-t);return hi(ci(e))},C=function(t){var n,i,o=t.x,a=t.y,r=t.noteX,s=t.noteY,h=t.noteWidth,d=void 0===h?k.noteWidth:h,c=t.noteTextOffset,f=void 0===c?k.noteTextOffset:c;if(isNumber_1(r))n=o+r;else {if(void 0===r.abs)throw new Error("noteX should be either a number or an object containing an 'abs' property");n=r.abs;}if(isNumber_1(s))i=a+s;else {if(void 0===s.abs)throw new Error("noteY should be either a number or an object containing an 'abs' property");i=s.abs;}var y=o,x=a,m=S(o,a,n,i);if(b(t)){var p=pi(ui(m),t.size/2);y+=p.x,x+=p.y;}if(z(t)){var g=Math.round((m+90)/45)%8;0===g&&(x-=t.height/2),1===g&&(y+=t.width/2,x-=t.height/2),2===g&&(y+=t.width/2),3===g&&(y+=t.width/2,x+=t.height/2),4===g&&(x+=t.height/2),5===g&&(y-=t.width/2,x+=t.height/2),6===g&&(y-=t.width/2),7===g&&(y-=t.width/2,x-=t.height/2);}var W=n,v=n;return (m+90)%360>180?(W-=d,v-=d):v+=d,{points:[[y,x],[n,i],[v,i]],text:[W,i-f],angle:m+90}},O=function(t){var i=t.data,o=t.annotations,e=t.getPosition,a=t.getDimensions;return useMemo((function(){return P({data:i,annotations:o,getPosition:e,getDimensions:a})}),[i,o,e,a])},j=function(t){var i=t.annotations;return useMemo((function(){return i.map((function(t){return g({},t,{computed:C(g({},t))})}))}),[i])},q=function(t,n){n.forEach((function(n,i){var o=n[0],e=n[1];0===i?t.moveTo(o,e):t.lineTo(o,e);}));},J$1=function(t,n){var i=n.annotations,o=n.theme;0!==i.length&&(t.save(),i.forEach((function(n){if(!v(n.note))throw new Error("note is invalid for canvas implementation");o.annotations.link.outlineWidth>0&&(t.lineCap="square",t.strokeStyle=o.annotations.link.outlineColor,t.lineWidth=o.annotations.link.strokeWidth+2*o.annotations.link.outlineWidth,t.beginPath(),q(t,n.computed.points),t.stroke(),t.lineCap="butt"),b(n)&&o.annotations.outline.outlineWidth>0&&(t.strokeStyle=o.annotations.outline.outlineColor,t.lineWidth=o.annotations.outline.strokeWidth+2*o.annotations.outline.outlineWidth,t.beginPath(),t.arc(n.x,n.y,n.size/2,0,2*Math.PI),t.stroke()),w(n)&&o.annotations.symbol.outlineWidth>0&&(t.strokeStyle=o.annotations.symbol.outlineColor,t.lineWidth=2*o.annotations.symbol.outlineWidth,t.beginPath(),t.arc(n.x,n.y,n.size/2,0,2*Math.PI),t.stroke()),z(n)&&o.annotations.outline.outlineWidth>0&&(t.strokeStyle=o.annotations.outline.outlineColor,t.lineWidth=o.annotations.outline.strokeWidth+2*o.annotations.outline.outlineWidth,t.beginPath(),t.rect(n.x-n.width/2,n.y-n.height/2,n.width,n.height),t.stroke()),t.strokeStyle=o.annotations.link.stroke,t.lineWidth=o.annotations.link.strokeWidth,t.beginPath(),q(t,n.computed.points),t.stroke(),b(n)&&(t.strokeStyle=o.annotations.outline.stroke,t.lineWidth=o.annotations.outline.strokeWidth,t.beginPath(),t.arc(n.x,n.y,n.size/2,0,2*Math.PI),t.stroke()),w(n)&&(t.fillStyle=o.annotations.symbol.fill,t.beginPath(),t.arc(n.x,n.y,n.size/2,0,2*Math.PI),t.fill()),z(n)&&(t.strokeStyle=o.annotations.outline.stroke,t.lineWidth=o.annotations.outline.strokeWidth,t.beginPath(),t.rect(n.x-n.width/2,n.y-n.height/2,n.width,n.height),t.stroke()),"function"==typeof n.note?n.note(t,{datum:n.datum,x:n.computed.text[0],y:n.computed.text[1],theme:o}):(t.font=o.annotations.text.fontSize+"px "+o.annotations.text.fontFamily,t.textAlign="left",t.textBaseline="alphabetic",t.fillStyle=o.annotations.text.fill,t.strokeStyle=o.annotations.text.outlineColor,t.lineWidth=2*o.annotations.text.outlineWidth,o.annotations.text.outlineWidth>0&&(t.lineJoin="round",t.strokeText(n.note,n.computed.text[0],n.computed.text[1]),t.lineJoin="miter"),t.fillText(n.note,n.computed.text[0],n.computed.text[1]));})),t.restore());};

function Y(){return Y=Object.assign||function(e){for(var t=1;t<arguments.length;t++){var i=arguments[t];for(var o in i)Object.prototype.hasOwnProperty.call(i,o)&&(e[o]=i[o]);}return e},Y.apply(this,arguments)}function G(e,t){if(null==e)return {};var i,o,n={},r=Object.keys(e);for(o=0;o<r.length;o++)i=r[o],t.indexOf(i)>=0||(n[i]=e[i]);return n}var H={layers:["grid","axes","cells","legends","annotations"],forceSquare:!1,xInnerPadding:0,xOuterPadding:0,yInnerPadding:0,yOuterPadding:0,sizeVariation:!1,opacity:1,activeOpacity:1,inactiveOpacity:.15,borderWidth:0,borderColor:{from:"color",modifiers:[["darker",.8]]},enableGridX:!1,enableGridY:!1,enableLabels:!0,label:"formattedValue",labelTextColor:{from:"color",modifiers:[["darker",2]]},colors:{type:"sequential",scheme:"brown_blueGreen"},emptyColor:"#000000",legends:[],annotations:[],isInteractive:!0,hoverTarget:"rowColumn",tooltip:memo((function(e){var t=e.cell;return null===t.formattedValue?null:jsx(w$3,{id:t.serieId+" - "+t.data.x,value:t.formattedValue,enableChip:!0,color:t.color})})),animate:!0,motionConfig:"gentle"};Y({},H,{axisTop:{},axisRight:null,axisBottom:null,axisLeft:{},borderRadius:0,cellComponent:"rect"});var D=Y({},H,{axisTop:{},axisRight:null,axisBottom:null,axisLeft:{},renderCell:"rect",pixelRatio:"undefined"!=typeof window&&window.devicePixelRatio||1}),_=function(e){var t=e.width,i=e.height,o=e.rows,n=e.columns,r=t,a=i,l=0,d=0;if(e.forceSquare){var c=Math.max(t/n,0),u=Math.max(i/o,0),s=Math.min(c,u);l=(t-(r=s*n))/2,d=(i-(a=s*o))/2;}return {offsetX:l,offsetY:d,width:r,height:a}},J=function(e){var t=e.data,i=e.width,o=e.height,n=e.xInnerPadding,r=e.xOuterPadding,a=e.yInnerPadding,l=e.yOuterPadding,d=e.forceSquare,c=new Set,u=[],s=[],h=[];t.forEach((function(e){u.push(e.id),e.data.forEach((function(t){c.add(t.x);var i=null;void 0!==t.y&&null!==t.y&&(s.push(t.y),i=t.y),h.push({id:e.id+"."+t.x,serieId:e.id,value:i,data:t});}));}));var v=Array.from(c),f=_({width:i,height:o,columns:v.length,rows:u.length,forceSquare:d}),g=f.width,p=f.height,b=f.offsetX,y=f.offsetY,m=nn(band().domain(v).range([0,g]).paddingOuter(r).paddingInner(n)),x=nn(band().domain(u).range([0,p]).paddingOuter(l).paddingInner(a)),C=m.bandwidth(),w=x.bandwidth(),P=h.map((function(e){return Y({},e,{x:m(e.data.x)+C/2,y:x(e.serieId)+w/2,width:C,height:w})}));return {width:g,height:p,offsetX:b,offsetY:y,xScale:m,yScale:x,minValue:Math.min.apply(Math,s),maxValue:Math.max.apply(Math,s),cells:P}},K=function(e,t,i){if(!e)return function(){return 1};var o=linear().domain(e.values?e.values:[t,i]).range(e.sizes);return function(e){return null===e?1:o(e)}},N=function(e){return {x:e.x,y:e.y}},Q=function(e){return {size:Math.max(e.width,e.height),width:e.width,height:e.height}},U=function(e){var i=e.data,o=e.width,n=e.height,r=e.xInnerPadding,a=e.xOuterPadding,l=e.yInnerPadding,d=e.yOuterPadding,c=e.forceSquare;return useMemo((function(){return J({data:i,width:o,height:n,xInnerPadding:r,xOuterPadding:a,yInnerPadding:l,yOuterPadding:d,forceSquare:c})}),[i,o,n,r,a,l,d,c])},Z={cell:function(e,t){return e.id===t.id},row:function(e,t){return e.serieId===t.serieId},column:function(e,t){return e.data.x===t.data.x},rowColumn:function(e,t){return e.serieId===t.serieId||e.data.x===t.data.x}},$=function(e){var i,n,r,a=e.cells,l=e.minValue,s=e.maxValue,h=e.sizeVariation,v=e.colors,f=e.emptyColor,g=e.opacity,p=e.activeOpacity,b=e.inactiveOpacity,y=e.borderColor,m=e.label,x=e.labelTextColor,C=e.valueFormat,w=e.activeIds,P=useMemo((function(){return K(i,n,r)}),[i=h,n=l,r=s]),S=useMemo((function(){return "function"==typeof v?null:sr(v,{min:l,max:s})}),[v,l,s]),M=useCallback((function(e){if(null!==e.value){if("function"==typeof v)return v(e);if(null!==S)return S(e.value)}return f}),[v,S,f]),T=Vt(),W=We(y,T),L=We(x,T),k=Xt(C),V=Xi(m);return {cells:useMemo((function(){return a.map((function(e){var t=g;w.length>0&&(t=w.includes(e.id)?p:b);var i=P(e.value),o=Y({},e,{width:e.width*i,height:e.height*i,formattedValue:null!==e.value?k(e.value):null,opacity:t});return o.label=V(o),o.color=M(o),o.borderColor=W(o),o.labelTextColor=L(o),o}))}),[a,P,M,W,L,k,V,w,g,p,b]),colorScale:S}},ee=function(e){var o=e.data,n=e.valueFormat,r=e.width,a=e.height,l=e.xOuterPadding,d=void 0===l?H.xOuterPadding:l,c=e.xInnerPadding,u=void 0===c?H.xInnerPadding:c,s=e.yOuterPadding,h=void 0===s?H.yOuterPadding:s,v=e.yInnerPadding,f=void 0===v?H.yInnerPadding:v,g=e.forceSquare,p=void 0===g?H.forceSquare:g,b=e.sizeVariation,y=void 0===b?H.sizeVariation:b,m=e.colors,x=void 0===m?H.colors:m,C=e.emptyColor,w=void 0===C?H.emptyColor:C,P=e.opacity,O=void 0===P?H.opacity:P,I=e.activeOpacity,S=void 0===I?H.activeOpacity:I,M=e.inactiveOpacity,T=void 0===M?H.inactiveOpacity:M,W=e.borderColor,L=void 0===W?H.borderColor:W,k=e.label,V=void 0===k?H.label:k,R=e.labelTextColor,B=void 0===R?H.labelTextColor:R,q=e.hoverTarget,E=void 0===q?H.hoverTarget:q,z=useState(null),A=z[0],F=z[1],X=U({data:o,width:r,height:a,xOuterPadding:d,xInnerPadding:u,yOuterPadding:h,yInnerPadding:f,forceSquare:p}),Y=X.width,G=X.height,j=X.offsetX,D=X.offsetY,_=X.cells,J=X.xScale,K=X.yScale,N=X.minValue,Q=X.maxValue,ee=useMemo((function(){if(!A)return [];var e=Z[E];return _.filter((function(t){return e(t,A)})).map((function(e){return e.id}))}),[_,A,E]),te=$({cells:_,minValue:N,maxValue:Q,sizeVariation:y,colors:x,emptyColor:w,opacity:O,activeOpacity:S,inactiveOpacity:T,borderColor:L,label:V,labelTextColor:B,valueFormat:n,activeIds:ee});return {width:Y,height:G,offsetX:j,offsetY:D,cells:te.cells,xScale:J,yScale:K,colorScale:te.colorScale,activeCell:A,setActiveCell:F}},te=function(e,t){return O({data:e,annotations:t,getPosition:N,getDimensions:Q})};memo((function(e){var i=e.cell,o=e.borderWidth,n=e.borderRadius,r=e.animatedProps,a=e.onMouseEnter,l=e.onMouseMove,c=e.onMouseLeave,u=e.onClick,s=e.enableLabels,h=Vt(),v=useMemo((function(){return {onMouseEnter:a?a(i):void 0,onMouseMove:l?l(i):void 0,onMouseLeave:c?c(i):void 0,onClick:u?u(i):void 0}}),[i,a,l,c,u]);return jsxs(animated.g,Y({"data-testid":"cell."+i.id,style:{cursor:"pointer"},opacity:r.opacity},v,{transform:to([r.x,r.y,r.scale],(function(e,t,i){return "translate("+e+", "+t+") scale("+i+")"})),children:[jsx(animated.rect,{transform:to([r.width,r.height],(function(e,t){return "translate("+-.5*e+", "+-.5*t+")"})),fill:r.color,width:r.width,height:r.height,stroke:r.borderColor,strokeWidth:o,rx:n,ry:n},i.id),s&&jsx(animated.text,{textAnchor:"middle",dominantBaseline:"central",fill:r.labelTextColor,style:Y({},h.labels.text,{fill:void 0,userSelect:"none"}),children:i.label})]}))}));memo((function(e){var i=e.cell,o=e.borderWidth,n=e.animatedProps,r=e.onMouseEnter,a=e.onMouseMove,l=e.onMouseLeave,c=e.onClick,u=e.enableLabels,s=Vt(),h=useMemo((function(){return {onMouseEnter:r?r(i):void 0,onMouseMove:a?a(i):void 0,onMouseLeave:l?l(i):void 0,onClick:c?c(i):void 0}}),[i,r,a,l,c]);return jsxs(animated.g,Y({"data-testid":"cell."+i.id,style:{cursor:"pointer"},opacity:n.opacity},h,{transform:to([n.x,n.y],(function(e,t){return "translate("+e+", "+t+")"})),children:[jsx(animated.circle,{r:to([n.width,n.height],(function(e,t){return Math.min(e,t)/2})),fill:n.color,fillOpacity:n.opacity,strokeWidth:o,stroke:n.borderColor}),u&&jsx(animated.text,{dominantBaseline:"central",textAnchor:"middle",fill:n.labelTextColor,style:Y({},s.labels.text,{fill:void 0}),children:i.label})]}))}));var ve=function(e,t){var i=t.cell,o=i.x,n=i.y,r=i.width,a=i.height,l=i.color,d=i.borderColor,c=i.opacity,u=i.labelTextColor,s=i.label,h=t.borderWidth,v=t.enableLabels,f=t.theme;e.save(),e.globalAlpha=c,e.fillStyle=l,h>0&&(e.strokeStyle=d,e.lineWidth=h),e.fillRect(o-r/2,n-a/2,r,a),h>0&&e.strokeRect(o-r/2,n-a/2,r,a),v&&(e.fillStyle=u,e.font=(f.labels.text.fontWeight?f.labels.text.fontWeight+" ":"")+f.labels.text.fontSize+"px "+f.labels.text.fontFamily,e.textAlign="center",e.textBaseline="middle",e.fillText(s,o,n)),e.restore();},fe=function(e,t){var i=t.cell,o=i.x,n=i.y,r=i.width,a=i.height,l=i.color,d=i.borderColor,c=i.opacity,u=i.labelTextColor,s=i.label,h=t.borderWidth,v=t.enableLabels,f=t.theme;e.save(),e.globalAlpha=c;var g=Math.min(r,a)/2;e.fillStyle=l,h>0&&(e.strokeStyle=d,e.lineWidth=h),e.beginPath(),e.arc(o,n,g,0,2*Math.PI),e.fill(),h>0&&e.stroke(),v&&(e.fillStyle=u,e.font=(f.labels.text.fontWeight?f.labels.text.fontWeight+" ":"")+f.labels.text.fontSize+"px "+f.labels.text.fontFamily,e.textAlign="center",e.textBaseline="middle",e.fillText(s,o,n)),e.restore();},ge=["theme","isInteractive","animate","motionConfig","renderWrapper"],pe=function(e){var i,r=e.data,c=e.layers,u=void 0===c?D.layers:c,s=e.valueFormat,h=e.width,f=e.height,g=e.margin,y=e.xInnerPadding,m=void 0===y?D.xInnerPadding:y,w=e.xOuterPadding,O=void 0===w?D.xOuterPadding:w,I=e.yInnerPadding,S=void 0===I?D.yInnerPadding:I,M=e.yOuterPadding,L=void 0===M?D.yOuterPadding:M,R=e.forceSquare,B=void 0===R?D.forceSquare:R,q=e.sizeVariation,E$1=void 0===q?D.sizeVariation:q,z=e.renderCell,A=void 0===z?D.renderCell:z,F=e.opacity,X=void 0===F?D.opacity:F,G=e.activeOpacity,H=void 0===G?D.activeOpacity:G,j$1=e.inactiveOpacity,_=void 0===j$1?D.inactiveOpacity:j$1,J=e.borderWidth,K=void 0===J?D.borderWidth:J,N=e.borderColor,Q=void 0===N?D.borderColor:N,U=e.enableGridX,Z=void 0===U?D.enableGridX:U,$=e.enableGridY,ie=void 0===$?D.enableGridY:$,oe=e.axisTop,ne=void 0===oe?D.axisTop:oe,re=e.axisRight,ae=void 0===re?D.axisRight:re,le=e.axisBottom,de=void 0===le?D.axisBottom:le,ce=e.axisLeft,ue=void 0===ce?D.axisLeft:ce,se=e.enableLabels,he=void 0===se?D.enableLabels:se,ge=e.label,pe=void 0===ge?D.label:ge,be=e.labelTextColor,ye=void 0===be?D.labelTextColor:be,me=e.colors,xe=void 0===me?D.colors:me,Ce=e.emptyColor,we=void 0===Ce?D.emptyColor:Ce,Pe=e.legends,Oe=void 0===Pe?D.legends:Pe,Ie=e.annotations,Se=void 0===Ie?D.annotations:Ie,Me=e.isInteractive,Te=void 0===Me?D.isInteractive:Me,We=e.onClick,Le=e.hoverTarget,ke=void 0===Le?D.hoverTarget:Le,Ve=e.tooltip,Re=void 0===Ve?D.tooltip:Ve,Be=e.role,qe=e.ariaLabel,Ee=e.ariaLabelledBy,ze=e.ariaDescribedBy,Ae=e.pixelRatio,Fe=void 0===Ae?D.pixelRatio:Ae,Xe=useRef(null),Ye=At(h,f,g),Ge=Ye.margin,He=Ye.innerWidth,je=Ye.innerHeight,De=Ye.outerWidth,_e=Ye.outerHeight,Je=ee({data:r,valueFormat:s,width:He,height:je,xInnerPadding:m,xOuterPadding:O,yInnerPadding:S,yOuterPadding:L,forceSquare:B,sizeVariation:E$1,colors:xe,emptyColor:we,opacity:X,activeOpacity:H,inactiveOpacity:_,borderColor:Q,label:pe,labelTextColor:ye,hoverTarget:ke}),Ke=Je.width,Ne=Je.height,Qe=Je.offsetX,Ue=Je.offsetY,Ze=Je.xScale,$e=Je.yScale,et=Je.cells,tt=Je.colorScale,it=Je.activeCell,ot=Je.setActiveCell,nt=useMemo((function(){return Y({},Ge,{top:Ge.top+Ue,left:Ge.left+Qe})}),[Ge,Qe,Ue]),rt=te(et,Se),at=j({annotations:rt});i="function"==typeof A?A:"circle"===A?fe:ve;var lt=Vt(),dt=useMemo((function(){return {cells:et,activeCell:it,setActiveCell:ot}}),[et,it,ot]);useEffect((function(){if(null!==Xe.current){var e=Xe.current.getContext("2d");e&&(Xe.current.width=De*Fe,Xe.current.height=_e*Fe,e.scale(Fe,Fe),e.fillStyle=lt.background,e.fillRect(0,0,De,_e),e.translate(nt.left,nt.top),u.forEach((function(t){"grid"===t?(e.lineWidth=lt.grid.line.strokeWidth,e.strokeStyle=lt.grid.line.stroke,Z&&C$1(e,{width:Ke,height:Ne,scale:Ze,axis:"x"}),ie&&C$1(e,{width:Ke,height:Ne,scale:$e,axis:"y"})):"axes"===t?V(e,{xScale:Ze,yScale:$e,width:Ke,height:Ne,top:ne,right:ae,bottom:de,left:ue,theme:lt}):"cells"===t?(e.textAlign="center",e.textBaseline="middle",et.forEach((function(t){i(e,{cell:t,borderWidth:K,enableLabels:he,theme:lt});}))):"legends"===t&&null!==tt?Oe.forEach((function(t){E(e,Y({},t,{containerWidth:Ke,containerHeight:Ne,scale:tt,theme:lt}));})):"annotations"===t?J$1(e,{annotations:at,theme:lt}):"function"==typeof t&&t(e,dt);})));}}),[Xe,Fe,De,_e,Ke,Ne,nt,u,dt,et,i,Z,ie,ne,ae,de,ue,Ze,$e,lt,K,he,tt,Oe,at]);var ct=k$3(),ut=ct.showTooltipFromEvent,st=ct.hideTooltip,ht=useCallback((function(e){if(null!==Xe.current){var t=Qi(Xe.current,e),i=t[0],o=t[1],r=et.find((function(e){return Ji(e.x+nt.left-e.width/2,e.y+nt.top-e.height/2,e.width,e.height,i,o)}));void 0!==r?(ot(r),ut(createElement$1(Re,{cell:r}),e)):(ot(null),st());}}),[Xe,et,nt,ot,ut,st,Re]),vt=useCallback((function(){ot(null),st();}),[ot,st]),ft=useCallback((function(e){null!==it&&(null==We||We(it,e));}),[it,We]);return jsx("canvas",{ref:Xe,width:De*Fe,height:_e*Fe,style:{width:De,height:_e},onMouseEnter:Te?ht:void 0,onMouseMove:Te?ht:void 0,onMouseLeave:Te?vt:void 0,onClick:Te?ft:void 0,role:Be,"aria-label":qe,"aria-labelledby":Ee,"aria-describedby":ze})},be=function(e){var t=e.theme,i=e.isInteractive,o=void 0===i?D.isInteractive:i,n=e.animate,r=void 0===n?D.animate:n,a=e.motionConfig,l=void 0===a?D.motionConfig:a,d=e.renderWrapper,c=G(e,ge);return jsx(Zt,{isInteractive:o,animate:r,motionConfig:l,theme:t,renderWrapper:d,children:jsx(pe,Y({isInteractive:o},c))})};

const defaultCanvasAxisTop = (legend) => ({
    format: () => '',
    legend,
    legendOffset: -20,
    legendPosition: 'middle',
    tickSize: 0,
});
const defaultCanvasAxisLeft = (legend) => ({
    format: () => '',
    legend,
    legendOffset: -20,
    legendPosition: 'middle',
    tickSize: 0,
});
const defaultCanvasAxisBottom = (legend) => ({
    format: () => '',
    legend,
    legendOffset: 20,
    legendPosition: 'middle',
    tickSize: 0,
});
const defaultCanvasAxisRight = (legend) => ({
    format: () => '',
    legend,
    legendOffset: 20,
    legendPosition: 'middle',
    tickSize: 0,
});
const getAxis = (axis, defaultAxisGetter) => {
    if (!axis) {
        return null;
    }
    if (typeof axis === 'string') {
        return defaultAxisGetter(axis);
    }
    return axis;
};

const defaultMargin = { top: 36, right: 36, bottom: 36, left: 36 };

const theme = {
    axis: {
        legend: {
            text: {
                fontSize: 18,
                fontWeight: 500,
            },
        },
        ticks: {
            line: {
                strokeWidth: 0,
            },
        },
    },
    annotations: {
        text: {
            fontSize: 0,
            fill: 'transparent',
            outlineWidth: 0,
            outlineColor: 'transparent',
            outlineOpacity: 0,
        },
        link: {
            stroke: 'transparent',
            strokeWidth: 0,
            outlineWidth: 0,
            outlineColor: 'transparent',
            outlineOpacity: 0,
        },
        outline: {
            stroke: '#ffffff',
            strokeWidth: 3,
            outlineWidth: 3,
            outlineColor: '#000000',
            outlineOpacity: 0.15,
        },
        symbol: {
            fill: 'transparent',
            outlineWidth: 0,
            outlineColor: 'transparent',
            outlineOpacity: 0,
        },
    },
};

const defaultTileConfig = {
    borderWidth: 0,
    emptyColor: colors$3.gray[200],
    enableLabels: false,
};

const generateData = ({ from, generateValue, normalizeValue = (value) => value, xIdGenerator, xRange, xSteps, yIdGenerator, yRange, ySteps, }) => {
    const yMin = Math.min(...yRange);
    const yMax = Math.max(...yRange);
    const xMin = Math.min(...xRange);
    const xMax = Math.max(...xRange);
    const yTotalSteps = Math.floor((yMax - yMin) / ySteps) + 1;
    const xTotalSteps = Math.floor((xMax - xMin) / xSteps) + 1;
    const stepCounter = from === 'bottom_left' ? yTotalSteps - 1 : 0;
    const data = Array.from(Array(yTotalSteps).keys()).map((yIndex) => {
        const yId = yIdGenerator?.(yIndex) ?? `y${ySteps * Math.abs(stepCounter - yIndex) + yMin}`;
        return {
            id: yId,
            data: Array.from(Array(xTotalSteps).keys()).map((xIndex) => {
                const xId = xIdGenerator?.(xIndex) ?? `x${xSteps * xIndex + xMin}`;
                const value = generateValue(xId, yId);
                const normalizedValue = normalizeValue(value);
                return {
                    x: xId,
                    y: value,
                    normalizedValue,
                };
            }),
        };
    });
    return data;
};
const getSelectedRow = (cell, data) => {
    // We are sure it's always going to have data so we force casting
    const rawRow = data.find((datum) => datum.id === cell.serieId)?.data;
    const selectedRow = rawRow.map((datum) => ({ id: datum.x, value: datum.y, normalizedValue: datum.normalizedValue }));
    return selectedRow;
};
const getSelectedColumn = ({ cell, data, from, }) => {
    const rawColumns = data.map((datum) => ({ y: datum.id, x: datum.data.filter((xDatum) => xDatum.x === cell.data.x) }));
    const selectedColumn = rawColumns.map((datum) => {
        const [xPoint] = datum.x;
        return {
            id: datum.y,
            value: xPoint.y,
            normalizedValue: xPoint.normalizedValue,
        };
    });
    return from === 'bottom_left' ? selectedColumn.reverse() : selectedColumn;
};

const defaultGradient = [
    [[12, 35, 62], 0],
    [[44, 103, 171], 20],
    [[6, 171, 171], 45],
    [[105, 187, 80], 60],
    [[150, 224, 128], 72],
    [[245, 221, 142], 88],
    [[255, 207, 51], 100],
];
const getRgbTileColor = (selectedColorStop, gradient = defaultGradient) => {
    if (selectedColorStop !== null && selectedColorStop !== undefined) {
        const rgb = pickRgbFromGradient(selectedColorStop, gradient);
        return `rgb(${rgb.join()})`;
    }
    return colors$3.gray[200];
};

const generateSelectedStyle = (ctx, { color, height, width, x, y }) => {
    const xPosition = x - width / 2;
    const yPosition = y - height / 2;
    ctx.shadowColor = '#000000';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    ctx.fillStyle = color;
    ctx.fillRect(xPosition, yPosition, width, height);
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#FFFFFF';
    ctx.strokeRect(xPosition, yPosition, width, height);
};
// This tile was taken from the original and added select and hover events
// https://github.com/plouc/nivo/blob/master/packages/heatmap/src/canvas.tsx
const customTileRender = ({ canvasEl, hoverTileEvent, margin, onHover, selectedTile }) => (ctx, cellRenderProps) => {
    const { cell, enableLabels, theme } = cellRenderProps;
    const { labels: { text }, } = theme;
    const { left = 0, top = 0 } = margin;
    const { color, height, id, label, labelTextColor, opacity, width, x, y } = cell;
    ctx.save();
    ctx.globalAlpha = opacity;
    if (id === selectedTile) {
        generateSelectedStyle(ctx, cell);
    }
    else {
        if (hoverTileEvent && canvasEl) {
            // Using getRelativeCursor to get more accurate x and y position
            // casting as unknown as React.MouseEvent has no issues with getRelativeCursor implementation
            // https://github.com/plouc/nivo/blob/f1a0f11628a951eeade614fbb48b4e6f07b652c7/packages/core/src/lib/interactivity/index.js#L12
            const [offsetX, offsetY] = Qi(canvasEl, hoverTileEvent);
            const isItInRect = Ji(x + left - width / 2, y + top - height / 2, width, height, offsetX, offsetY);
            if (isItInRect) {
                generateSelectedStyle(ctx, cell);
                onHover?.(cell, hoverTileEvent);
            }
        }
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = color;
        ctx.fillRect(x - width / 2, y - height / 2, width, height);
    }
    if (enableLabels) {
        ctx.fillStyle = labelTextColor;
        ctx.font = `${text.fontWeight ? `${text.fontWeight} ` : ''}${text.fontSize}px ${text.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, x, y);
    }
    ctx.restore();
};

const InnerHeatmapCanvas = ({ axisBottom, axisLeft, axisRight, axisTop, colors, data, forceSquare, from, gradient = defaultGradient, height, initialSelectedTile = '', margin = defaultMargin, onChangeSelectedTile, onMouseEnter, onMouseLeave, onSelect, selectedTile, theme: theme$1 = theme, tileConfig = defaultTileConfig, tooltip, width, }) => {
    const { selectedItem: innerSelectedTile, setSelectedItem: handleOnSelect } = useSelectable(initialSelectedTile);
    const { selectedItem: hoverTileEvent, setSelectedItem: handleOnHover } = useSelectable(null);
    const hoverTimeoutRef = useRef(null);
    const heatmapContainerRef = useRef(null);
    const lastTileHoveredRef = useRef(null);
    const handleOnMouseMove = useCallback((event) => {
        // Clear timeout if the mouse is mover over the canvas before triggering the hover intent
        if (hoverTimeoutRef.current && !lastTileHoveredRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        // This set timeout creates a hover intent that attempts to determine the user's intent
        // to delay or prevent the accidental firing heatmap and sliders calcs
        // but once the event is fired, next tile hover events are fired immediately
        hoverTimeoutRef.current = setTimeout(() => {
            handleOnHover(event);
        }, lastTileHoveredRef.current ? 0 : 200);
    }, [handleOnHover]);
    const handleOnMouseLeave = useCallback((event) => {
        // Calls users callback if the hover intent was fired
        if (lastTileHoveredRef.current) {
            onMouseLeave?.(event);
        }
        // Clear lastTileHoveredRef when leaving the canvas
        // to start tracking the hover intent when user moves the mouse over the heatmap
        lastTileHoveredRef.current = null;
        // Clear timeout if the mouse leaves the canvas
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        handleOnHover(null);
    }, 
    // To prevent unnecessary effect based on the onMouseLeave callback.
    // Disabling here because onMouseLeave function is always redefined if not (well) memoized from parent.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [handleOnHover]);
    const getCanvasElement = useMemo(() => () => heatmapContainerRef.current?.getElementsByTagName('canvas')[0], []);
    useEventListener({ eventName: 'mousemove', handler: handleOnMouseMove, element: getCanvasElement });
    useEventListener({
        eventName: 'mouseleave',
        handler: handleOnMouseLeave,
        element: getCanvasElement,
    });
    const handleSelectTile = (cell, event) => {
        if (onChangeSelectedTile) {
            onChangeSelectedTile(cell, { selectedRow: getSelectedRow(cell, data), selectedColumn: getSelectedColumn({ cell, data, from }) }, event);
            return;
        }
        handleOnSelect(cell.id);
        onSelect?.(cell, { selectedRow: getSelectedRow(cell, data), selectedColumn: getSelectedColumn({ cell, data, from }) }, event);
    };
    const handleOnMouseEnterTile = (cell, event) => {
        onMouseEnter?.(cell, { selectedRow: getSelectedRow(cell, data), selectedColumn: getSelectedColumn({ cell, data, from }) }, event);
    };
    const onHover = (cell, event) => {
        if (lastTileHoveredRef.current?.id !== cell.id) {
            handleOnMouseEnterTile(cell, event);
        }
        lastTileHoveredRef.current = cell;
    };
    const getColors = (cell) => getRgbTileColor(cell.data.normalizedValue, gradient);
    return (React__default.createElement("div", { ref: heatmapContainerRef },
        React__default.createElement(be, { height: height, width: width, animate: false, axisBottom: getAxis(axisBottom, defaultCanvasAxisBottom), axisLeft: getAxis(axisLeft, defaultCanvasAxisLeft), axisRight: getAxis(axisRight, defaultCanvasAxisRight), axisTop: getAxis(axisTop, defaultCanvasAxisTop), borderWidth: tileConfig.borderWidth, forceSquare: forceSquare, renderCell: customTileRender({
                canvasEl: heatmapContainerRef.current?.getElementsByTagName('canvas')[0],
                hoverTileEvent,
                onHover,
                margin,
                selectedTile: selectedTile ?? innerSelectedTile,
            }), colors: colors || getColors, data: data, emptyColor: tileConfig.emptyColor, enableLabels: tileConfig.enableLabels, layers: ['grid', 'axes', 'cells'], margin: margin, onClick: handleSelectTile, theme: theme$1, tooltip: tooltip })));
};

const Heatmap = ({ from = 'bottom_left', generateValue, normalizeValue, xRange, xSteps, yRange, ySteps, ...restProps }) => {
    const data = generateData({
        from,
        generateValue,
        normalizeValue,
        xRange,
        xSteps,
        yRange,
        ySteps,
    });
    return React__default.createElement(InnerHeatmapCanvas, { data: data, from: from, ...restProps });
};

const StyledDiv$1 = _default$1.div `py-4 px-6 bg-gray-500 opacity-90 shadow-md rounded-lg`;

// Not using Tooltip from ariakit and instead using div element
// since nivo Heatmap renders tooltip with proper attributes
const HeatmapTooltip = ({ children, className }) => (React__default.createElement(StyledDiv$1, { className: className }, children));

const StyledContainer$4 = _default$1.div `
    ${({ $breakpoints }) => ($breakpoints ? 'container' : 'max-w-7xl')}
    mx-auto py-6 sm:px-6 lg:px-8
`;

const Container = ({ breakpoints, children, className }) => (React__default.createElement(StyledContainer$4, { "$breakpoints": breakpoints, className: className }, children));
const ContainerMemo = memo(Container);

const StyledContainer$3 = _default$1.div `mt-6 relative`;
const StyledDividerContainer = _default$1.div `
  absolute
  inset-0
  flex
  items-center
`;
const StyledDivider = _default$1.div `
  w-full
  border-t
  border-gray-300
`;
const StyledChildrenContainer$1 = _default$1.div `
  relative
  flex
  justify-center
  text-sm
`;
const StyledChildrenSpan = _default$1.span `
  px-2
  bg-white
  text-gray-500
`;

const Divider = ({ children, className }) => (React__default.createElement(StyledContainer$3, { className: className },
    React__default.createElement(StyledDividerContainer, { "aria-hidden": "true", "data-testid": "divider" },
        React__default.createElement(StyledDivider, null)),
    children ? (React__default.createElement(StyledChildrenContainer$1, null,
        React__default.createElement(StyledChildrenSpan, null, children))) : null));
const DividerMemo = memo(Divider);

const useControlledFlyoutMenu = ({ initialIsOpen = false } = {}) => {
    const [isOpen, setIsOpen] = useState(initialIsOpen);
    const toggle = () => setIsOpen((prevState) => !prevState);
    return {
        isOpen,
        setIsOpen,
        toggle,
    };
};

const FlyoutContent = ({ children, className, open }) => (React__default.createElement(We$1, { as: Fragment, enter: "transform transition ease-in-out duration-700", enterFrom: "-translate-y-full", enterTo: "translate-y-0", leave: "transform transition ease-in-out duration-700", leaveFrom: "translate-y-0", leaveTo: "-translate-y-full", show: open },
    React__default.createElement(Tt$1.Panel, { static: true, className: cn('absolute z-header inset-x-0 transform shadow-lg', className) }, children)));

const FlyoutToggler = ({ children, className }) => (React__default.createElement(Tt$1.Button, { className: className }, children));

const FlyoutMenu = ({ children, className }) => (React__default.createElement(Tt$1, { className: cn('relative', className) }, ({ close, open }) => (React__default.createElement(React__default.Fragment, null, children({
    close,
    FlyoutContent,
    FlyoutToggler,
    open,
})))));

const COLUMN_VARIANTS = {
    base: '',
    gray: 'bg-gray-300',
};
const VISIBILITY_VARIANTS = {
    hiddenOnMinWidth: 'hidden lg:flex',
    alwaysOnDisplay: 'flex',
};
const StyledLeftColumnLayout = _default$1.div `
  min-h-full
  flex
`;
const StyledColumn = _default$1.div `
  ${({ $columnVariant }) => COLUMN_VARIANTS[$columnVariant]}
  ${({ $visibilityVariant }) => VISIBILITY_VARIANTS[$visibilityVariant]}
  flex-col
`;
const StyledMainContent = _default$1.div `
  flex
  flex-col
  grow
`;
const StyledHeader = _default$1.div `
  pt-6
  px-6
  border-b
  border-gray-200
`;

const LEFT_COLUMN_PADDING = 48;
const LEFT_COLUMN_BREAKPOINT = 896; // LG breakpoint (976px) - Left Nav(80px)
const DEFAULT_COLUMN_WIDTH = 368;
const LeftColumnLayout = ({ children, className, columnContent, columnVariant = 'normal', headerContent, leftColumnVisibility = 'hiddenOnMinWidth', leftColumnWidth = DEFAULT_COLUMN_WIDTH, }) => {
    const [ref, { width }] = useElementSize();
    const childrenWidth = width < LEFT_COLUMN_BREAKPOINT ? width - LEFT_COLUMN_PADDING : width - LEFT_COLUMN_PADDING - leftColumnWidth;
    return (React__default.createElement(Stack$5, { direction: "column", className: className },
        headerContent && React__default.createElement(StyledHeader, null, headerContent),
        React__default.createElement(StyledLeftColumnLayout, { ref: ref },
            React__default.createElement(StyledColumn, { "$columnVariant": columnVariant, "$visibilityVariant": leftColumnVisibility, style: { width: leftColumnWidth } }, columnContent),
            React__default.createElement(StyledMainContent, null, React__default.cloneElement(children, {
                width: childrenWidth,
            })))));
};
const LeftColumnLayoutMemo = memo(LeftColumnLayout);

const StyledContainer$2 = _default$1.div `
  flex
  space-x-4
  ${({ $border }) => ($border ? 'border-b border-gray-300 pb-6' : '')}
`;
const StyledTextContainer = _default$1.div `
  flex
  flex-col
  space-y-2
  sm:flex-auto
`;
const StyledTrailingContent$1 = _default$1.div `
`;

const PageSubtitle = ({ children, ...props }) => (React__default.createElement(Typography, { as: "p", color: colors$3.gray[500], fontSize: "sm", fontWeight: "normal", lineHeight: 5, ...props }, children));
const PageSubtitleMemo = React__default.memo(PageSubtitle);

const PageTitle = ({ children, ...props }) => (React__default.createElement(Typography, { as: "h1", fontSize: "3xl", fontWeight: "semibold", lineHeight: 9, ...props }, children));
const PageTitleMemo = React__default.memo(PageTitle);

const PageHeader = ({ border, className, subtitle, title, trailingContent, }) => (React__default.createElement(StyledContainer$2, { className: className, "$border": border },
    React__default.createElement(StyledTextContainer, null,
        typeof title === 'object' ? title : React__default.createElement(PageTitle, null, title),
        typeof subtitle === 'object' ? subtitle : subtitle ? React__default.createElement(PageSubtitle, null, subtitle) : null),
    trailingContent ? React__default.createElement(StyledTrailingContent$1, null, trailingContent) : null));
const PageHeaderMemo = memo(PageHeader);

const SplitScreenContainer = _default$1.div `
  min-h-full
  flex
`;
const SplitScreenLeftSide = _default$1.div `
${({ $collapsibleSide }) => $collapsibleSide === 'left' ? 'hidden lg:block' : 'flex flex-1 flex-col lg:flex-none'}
  h-full
  overflow-auto
`;
const SplitScreenRightSide = _default$1.div `
  ${({ $collapsibleSide }) => ($collapsibleSide === 'right' ? 'hidden lg:block' : '')}
  relative
  w-0
  flex-1
`;

const SplitScreen = ({ className, collapsibleSide = 'right', leftSide, rightSide, }) => (React__default.createElement(SplitScreenContainer, { className: className },
    React__default.createElement(SplitScreenLeftSide, { "$collapsibleSide": collapsibleSide }, leftSide),
    React__default.createElement(SplitScreenRightSide, { "$collapsibleSide": collapsibleSide }, rightSide)));
const SplitScreenMemo = memo(SplitScreen);

const StyledContainer$1 = _default$1.div `
  flex
  flex-col
  h-full
  w-full
  overflow-hidden
`;
const StyledContentContainer = _default$1.div `
  inline-block
  min-w-full
  align-middle
  h-full
`;
const StyledChildrenContainer = _default$1.div `
  shadow
  ring-1
  ring-black
  ring-opacity-5
  h-full
`;

const TableContainer = ({ children, className, height }) => (React__default.createElement(StyledContainer$1, { className: className },
    React__default.createElement(StyledContentContainer, null,
        React__default.createElement(StyledChildrenContainer, { style: { height } }, children))));
const TableContainerMemo = memo(TableContainer);

const StyledTwoColumnLayoutWrapper = _default$1.div `
  w-full
  max-w-7xl
  last:mb-10
`;
const StyledTwoColumnLayout = _default$1.div `
  ${({ $grid }) => ($grid ? 'lg:grid lg:grid-cols-3 lg:gap-6' : 'flex h-full')}
`;
const StyledTwoColumnLayoutLeftColumn = _default$1.div `
  lg:col-span-1
`;
const StyledTwoColumnLayoutRightColumn = _default$1.div `
  lg:col-span-2
  ${({ $grid }) => ($grid ? '' : 'grow')}
  ${({ $disabled }) => ($disabled ? 'opacity-30' : '')}
`;

const TwoColumnLayout = ({ children, className, disabled, grid = true, leftColumn, }) => (React__default.createElement(StyledTwoColumnLayoutWrapper, { className: className },
    React__default.createElement(StyledTwoColumnLayout, { "$grid": grid },
        React__default.createElement(StyledTwoColumnLayoutLeftColumn, null, leftColumn),
        React__default.createElement(StyledTwoColumnLayoutRightColumn, { "$grid": grid, "$disabled": disabled }, children))));
const TwoColumnLayoutMemo = memo(TwoColumnLayout);

const StyledDiv = _default$1.div `
  flex
  bg-gray-100
  w-full
  h-6
  overflow-hidden
`;
const StyledSpan = _default$1.span `
  -translate-x-full
  flex-1
  inline-block
  bg-gradient-to-r
  from-[#FFFFFF00]
  via-[#FFFFFF99]
  to-[#FFFFFF00]
  animate-shimmer
`;

const Skeleton = ({ className, height, rounded, width }) => (React__default.createElement(StyledDiv, { className: cn(getHeight(height), getWidth(width), rounded ? 'rounded' : '', className) },
    React__default.createElement(StyledSpan, null)));

var _path$6, _path2$2, _path3, _path4;
function _extends$9() { return _extends$9 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$9.apply(null, arguments); }
var SvgExclamation = function SvgExclamation(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$9({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 24 24",
    strokeWidth: 1.5,
    ref: ref
  }, props), _path$6 || (_path$6 = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    d: "M12 19.5C11.7929 19.5 11.625 19.3321 11.625 19.125C11.625 18.9179 11.7929 18.75 12 18.75"
  })), _path2$2 || (_path2$2 = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    d: "M12 19.5C12.2071 19.5 12.375 19.3321 12.375 19.125C12.375 18.9179 12.2071 18.75 12 18.75"
  })), _path3 || (_path3 = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeMiterlimit: 10,
    d: "M12 15.75V8.25"
  })), _path4 || (_path4 = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    d: "M13.621 1.76001C13.4722 1.45702 13.2414 1.20181 12.9549 1.02333C12.6684 0.844851 12.3376 0.750244 12 0.750244C11.6624 0.750244 11.3316 0.844851 11.0451 1.02333C10.7586 1.20181 10.5278 1.45702 10.379 1.76001L0.90601 21.058C0.79198 21.29 0.738946 21.5472 0.751916 21.8053C0.764886 22.0635 0.843432 22.3141 0.980137 22.5334C1.11684 22.7528 1.3072 22.9337 1.53324 23.0591C1.75927 23.1845 2.01353 23.2502 2.27201 23.25H21.728C21.9865 23.2502 22.2407 23.1845 22.4668 23.0591C22.6928 22.9337 22.8832 22.7528 23.0199 22.5334C23.1566 22.3141 23.2351 22.0635 23.2481 21.8053C23.2611 21.5472 23.208 21.29 23.094 21.058L13.621 1.76001Z"
  })));
};
var ForwardRef$9 = /*#__PURE__*/forwardRef(SvgExclamation);
var Memo$9 = /*#__PURE__*/memo(ForwardRef$9);

const StyledDialogPanel$1 = _default$1(mt.Panel) `
  relative
  bg-white
  rounded-lg
  px-4
  pt-5
  pb-4
  text-left
  overflow-hidden
  shadow-xl
  transform
  transition-all
  sm:my-8
  sm:p-6
`;
const StyledTransitionAndPanelContainer = _default$1.div `
  flex
  items-end
  sm:items-center
  justify-center
  min-h-full
  p-4
  text-center
  sm:p-0
`;
const StyledAlertLayoutContainer = _default$1.div `
  sm:flex
  sm:items-start
`;
const StyledAlertLayoutIconContainer = _default$1.div `
  mx-auto
  flex-shrink-0
  flex
  items-center
  justify-center
  h-12
  w-12
  rounded-full
  bg-rsbry-100
  sm:mx-0
  sm:h-10
  sm:w-10
`;
const StyledAlertLayoutContent = _default$1.div `
  text-center sm:text-left
  mt-3 sm:mt-0 sm:ml-4
`;
const StyledAlertLayoutCopyParagraph = _default$1.p `
  text-sm
  text-gray-500
`;
const StyledAlertLayoutTrailingContent = _default$1.div `
  flex
  justify-end
`;
const StyledCopyAndChildrenContainer = _default$1.div `
  my-2
`;
const StyledModalActions = _default$1.div `
${({ $hasTopBorder }) => ($hasTopBorder ? 'border-t' : '')}
  grid
  grid-cols-2
  gap-2
  items-start
  border-gray-200
  pt-5
`;
const TextWrapper = _default$1.div `
  my-2
`;

const AlertModalLayout = ({ children, copy, title, trailingContent }) => (React__default.createElement(React__default.Fragment, null,
    React__default.createElement(StyledAlertLayoutContainer, null,
        React__default.createElement(StyledAlertLayoutIconContainer, null,
            React__default.createElement(Memo$9, { className: "h-5 w-5 text-rsbry-600", "aria-hidden": "true" })),
        React__default.createElement(StyledAlertLayoutContent, null,
            React__default.createElement(mt.Title, { as: "h3", className: "text-lg leading-6 font-medium text-gray-900" }, title),
            React__default.createElement(StyledCopyAndChildrenContainer, null,
                React__default.createElement(StyledAlertLayoutCopyParagraph, null, copy),
                React__default.createElement(StyledCopyAndChildrenContainer, null, children)))),
    React__default.createElement(StyledAlertLayoutTrailingContent, null, trailingContent)));
const AlertModalLayoutMemo = memo(AlertModalLayout);

const useModal = ({ initialIsOpen = false } = {}) => {
    const [isOpen, setIsOpen] = useState(initialIsOpen);
    const toggle = () => setIsOpen((prevState) => !prevState);
    return {
        isOpen,
        setIsOpen,
        toggle,
    };
};

const Modal = ({ children, className, isOpen, setIsOpen = () => { }, topMargin, leftMargin, }) => {
    const commonStyle = {
        marginTop: topMargin,
        marginLeft: leftMargin,
    };
    return (React__default.createElement(We$1.Root, { show: isOpen, as: Fragment },
        React__default.createElement(mt, { as: "div", className: "relative z-modal", onClose: setIsOpen },
            React__default.createElement(We$1.Child, { as: Fragment, enter: "ease-out duration-300", enterFrom: "opacity-0", enterTo: "opacity-100", leave: "ease-in duration-200", leaveFrom: "opacity-100", leaveTo: "opacity-0" },
                React__default.createElement("div", { style: commonStyle, className: "fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" })),
            React__default.createElement("div", { style: commonStyle, className: "fixed z-10 inset-0 overflow-y-auto" },
                React__default.createElement(StyledTransitionAndPanelContainer, null,
                    React__default.createElement(We$1.Child, { as: Fragment, enter: "ease-out duration-300", enterFrom: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95", enterTo: "opacity-100 translate-y-0 sm:scale-100", leave: "ease-in duration-200", leaveFrom: "opacity-100 translate-y-0 sm:scale-100", leaveTo: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" },
                        React__default.createElement(StyledDialogPanel$1, { className: cn('modal-children-panel', className) }, children)))))));
};
const ModalMemo = memo(Modal);

const ModalActions = ({ children, className, hasTopBorder = true }) => (React__default.createElement(StyledModalActions, { "$hasTopBorder": hasTopBorder, className: className }, children));
const ModalActionsMemo = memo(ModalActions);

const ModalHeader = ({ className, copy, alert, icon, iconBgColor = colors$3.green[100], subtitle, title, }) => (React__default.createElement("div", { className: className },
    React__default.createElement("div", { style: { backgroundColor: iconBgColor }, className: "mx-auto flex items-center justify-center h-12 w-12 rounded-full" }, icon),
    React__default.createElement("div", { className: "mt-3 text-center sm:mt-5" },
        React__default.createElement(Typography, { as: "h3", fontSize: "lg", fontWeight: "medium" }, title),
        subtitle && (React__default.createElement(TextWrapper, null,
            React__default.createElement(Typography, { as: "div", fontWeight: "medium" }, subtitle))),
        copy ? (React__default.createElement(TextWrapper, null,
            React__default.createElement(Typography, { as: "div", fontSize: "sm", className: "text-gray-500" }, copy))) : null,
        alert ? React__default.createElement("div", { className: "m-2" }, alert) : null)));
const ModalHeaderMemo = memo(ModalHeader);

const useTopNavBarSelector = ({ initialIsOpen = false } = {}) => {
    const [isOpen, setIsOpen] = useState(initialIsOpen);
    const toggle = () => setIsOpen((prevState) => !prevState);
    return {
        isOpen,
        setIsOpen,
        toggle,
    };
};

var _g$2;
function _extends$8() { return _extends$8 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$8.apply(null, arguments); }
var SvgChevronDown = function SvgChevronDown(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$8({
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    x: "0px",
    y: "0px",
    viewBox: "0 0 48 48",
    xmlSpace: "preserve",
    ref: ref
  }, props), _g$2 || (_g$2 = /*#__PURE__*/React.createElement("g", {
    transform: "matrix(2,0,0,2,0,0)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    className: "st0",
    d: "M12,19.5c-0.7,0-1.3-0.3-1.7-0.8L0.4,7.6C-0.2,6.8-0.1,5.6,0.7,5c0.8-0.6,1.9-0.6,2.5,0.2l8.6,9.8 c0.1,0.1,0.2,0.1,0.4,0c0,0,0,0,0,0l8.6-9.8c0.7-0.8,1.8-0.9,2.6-0.2s0.9,1.8,0.2,2.6c0,0,0,0-0.1,0.1l-9.8,11.1 C13.3,19.2,12.6,19.5,12,19.5z"
  }))));
};
var ForwardRef$8 = /*#__PURE__*/forwardRef(SvgChevronDown);
var Memo$8 = /*#__PURE__*/memo(ForwardRef$8);

const StyledButton = _default$1.button `
  transition duration-300 group
  flex flex-col justify-center items-center
  text-gray-900 h-full rounded-t-md
  px-3 py-2 relative min-h-[60px]
  hover:bg-gray-300
  ${({ $disabled }) => ($disabled ? 'hover:bg-gray-200 cursor-not-allowed' : '')}
  ${({ $open }) => ($open ? 'bg-gray-200' : 'bg-white')}
`;
const StyledSelectorContainer = _default$1.div `
  flex items-center gap-x-2
`;
const StyledSelectorContent = _default$1.div `
  ${({ $disabled }) => ($disabled ? '' : 'w-full')}
`;
const StyledHintSpan = _default$1.span `
  text-xs leading-none font-normal text-gray-500
`;
const StyledChevronDownIcon = _default$1(Memo$8) `
  w-5 h-5 p-1.5 text-gray-400
  transition duration-300 group-hover:scale-[1.5]
`;
const StyledCloseIcon = _default$1(Memo$a) `
  transition duration-300
  w-3 h-3 p-1
  rounded-full box-content
  text-white bg-gray-400
  hover:bg-primary-600
  ${({ $disabled }) => $disabled
    ? 'hover:bg-gray-200 bg-gray-200 hover:text-black text-black cursor-not-allowed'
    : 'scale-[0.85] group-hover:scale-100 hover:rotate-90'}
`;

const TopNavBarSelector = ({ children, className, disabled, id, onClick, open = false, }) => (React__default.createElement(StyledButton, { id: id, "$disabled": disabled, "$open": open, className: className, disabled: disabled, onClick: onClick },
    React__default.createElement(StyledSelectorContainer, null,
        React__default.createElement(StyledSelectorContent, { "$disabled": disabled }, children),
        !disabled && React__default.createElement(React__default.Fragment, null, !open ? React__default.createElement(StyledChevronDownIcon, null) : React__default.createElement(StyledCloseIcon, { "$disabled": disabled }))),
    open && !disabled ? React__default.createElement(StyledHintSpan, null, "Click to close") : null));
const TopNavBarSelectorMemo = memo(TopNavBarSelector);

const StyledPopover = _default$1(Popover$1) `
  bg-white
  z-overlay
  rounded-lg
  py-4
  px-6
  overflow-visible
  shadow-lg
`;

const Popover = ({ children, className, label, onMouseEnter, onMouseLeave, state, }) => (React__default.createElement(StyledPopover, { "aria-label": label, state: state, className: className, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave }, children));
const PopoverMemo = memo(Popover);

const ForeignObjectContent = _default$1.div `
  transparent
`;

const ScalableContainer = ({ children, className, height, width, }) => (React__default.createElement("svg", { className: className, viewBox: `0 0 ${width} ${height}`, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React__default.createElement("foreignObject", { width: "100%", height: "100%" },
        React__default.createElement(ForeignObjectContent, { xmlns: "http://www.w3.org/1999/xhtml" }, children))));
const ScalableContainerMemo = memo(ScalableContainer);

const StyledTabWrapper = _default$1.div `
  border-b
  border-gray-300
  text-primary-600
`;
const StyledBaseTabList = _default$1(TabList) `
  -mb-px
  flex
  ${({ $isFullWidth }) => ($isFullWidth ? '' : 'space-x-8')}
`;
const StyledBaseTab = _default$1(Tab) `
  ${({ $isFullWidth }) => ($isFullWidth ? 'grow' : '')}
  ${({ $current }) => $current ? 'border-current' : 'border-transparent hover:border-gray-400'}
  whitespace-nowrap
  py-4
  px-1
  border-b-2
  transition-all duration-200
  group
`;
const StyledBaseTabChildContainer = _default$1.div `
  ${({ $current }) => $current ? 'scale-100 text-current' : 'scale-95 text-gray-500 hover:text-gray-700'}
  transition-transform duration-300
  group-hover:scale-100
  font-medium
  text-sm
`;
const StyledBaseTabPanel = _default$1(TabPanel) `
  mt-4
`;
const StyledBaseTabPanelContainer = _default$1.div `
  grid
  grid-cols-1
  grid-rows-1
  ${({ $fullHeight }) => ($fullHeight ? 'h-full' : '')}
`;
_default$1.ul `
  -mb-px
  flex
  ${({ $isFullWidth }) => ($isFullWidth ? '' : 'space-x-8')}
`;
_default$1.li `
  list-none
`;
_default$1.span `
  transition-transform
  duration-300
  text-gray-500
  font-medium
  text-sm
  group-hover:scale-100
`;

const BaseTabs = ({ children, className, fullWidth = false, state }) => (React__default.createElement(StyledTabWrapper, { className: className },
    React__default.createElement(StyledBaseTabList, { "$isFullWidth": fullWidth, state: state }, Array.isArray(children)
        ? children.map((child) => React__default.cloneElement(child, {
            fullWidth,
            current: state.selectedId === child.props.tabId,
            key: child.props.tabId,
        }))
        : React__default.cloneElement(children, { fullWidth, current: state.activeId === children.props.tabId }))));

const TabPanelContainer = ({ children, className, fullHeight }) => (React__default.createElement(StyledBaseTabPanelContainer, { className: className, "$fullHeight": fullHeight }, children));

const BaseTabPanel = ({ children, className, state, tabId }) => (React__default.createElement(We$1, { className: cn('row-start-1 col-start-1', className), as: StyledBaseTabPanel, show: state.selectedId === tabId, enter: "transition-opacity ease-[cubic-bezier(0.42,0,0.34,01.01)] duration-300", enterFrom: "opacity-0", enterTo: "opacity-100", leave: "transition-opacity ease-[cubic-bezier(0.42,0,0.34,01.01)] duration-300", leaveFrom: "opacity-100", leaveTo: "opacity-0", state: state, tabId: tabId, hidden: false }, children));

const BaseTab = ({ className, current, fullWidth = false, label, tabId }) => (React__default.createElement(StyledBaseTab, { "$current": current, "$isFullWidth": fullWidth, id: tabId, className: className },
    React__default.createElement(StyledBaseTabChildContainer, { "$current": current }, label)));

const StyledText = _default$1.span `
  truncate
  inline-block
  w-full
  ${({ $alignText }) => ($alignText === 'start' ? '' : 'text-right')}
  ${({ $clickable }) => ($clickable ? 'text-primary-400 cursor-pointer' : 'text-gray-500')}
`;
const StyledTextAction = _default$1(Typography) `
  text-primary-600
  hover:text-primary-400
`;

const AbbrevNumberCell = ({ alignText = 'start', className, href, number, }) => (React__default.createElement(StyledText, { "$alignText": alignText, "$clickable": href !== undefined, className: className }, lib.abbreviateNumber({ inputNumber: number, sigFigs: 2 }).abbreviation));
const AbbrevNumberCellMemo = memo(AbbrevNumberCell);

const StyledReadOnlySliderContainer = _default$1(Stack$5) `
  w-full
  h-8
  rounded-l-md
  ease-in-out
  ${({ $hover }) => ($hover ? 'bg-gray-800 transition-colors duration-300' : '')}
`;
const StyledHovercard = _default$1(Hovercard) `
  bg-gray-50
  w-80
  rounded-bl-md
  rounded-tr-md
  shadow-lg
  z-hovercard
`;

const getStyledPercentageVariant = (percentage, hovercardOpen) => hovercardOpen
    ? percentage > 0
        ? 'readOnlySelectedHover'
        : 'readOnlyBaseHover'
    : percentage > 0
        ? 'readOnlySelected'
        : 'readOnlyBase';

const AllocationByProjectCell = ({ allocationSliderId, className, disabled = false, onAdvanceBtnClick, onChange, onSelect, otherAllocations, projects, totalDomesticRd, totalForeignRd, totalUnqualifiedRd, }) => {
    const hovercard = useHovercardState({
        gutter: 0,
        placement: 'right-start',
        timeout: 300,
        slide: false,
        flip: 'right-start right-end',
    });
    // Don't include Foreign R&D in the total allocation percentage for Domestic R&D
    const totalAllocationPercentage = totalDomesticRd + totalUnqualifiedRd;
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Tooltip, { text: disabled ? 'Create a project to change qualified R&D' : '', className: "!z-modal", options: {
                placement: 'top',
                showArrow: true,
                gutter: -8,
                timeout: 300,
            } },
            React__default.createElement(HovercardAnchor, { as: "div", disabled: disabled, state: hovercard },
                React__default.createElement(StyledReadOnlySliderContainer, { "$hover": hovercard.open, className: className, alignItems: "center" },
                    React__default.createElement(ReadOnlyAllocationSlider, { id: allocationSliderId, selectedPercentage: totalDomesticRd + totalUnqualifiedRd, alreadyAllocatedPercentage: totalForeignRd, hovercardOpen: hovercard.open }),
                    React__default.createElement(StyledPercentage, { "$textVariant": "base", "$variant": getStyledPercentageVariant(totalAllocationPercentage, hovercard.open), as: "span", fontSize: "xs", fontWeight: "semibold", lineHeight: 5 },
                        totalAllocationPercentage,
                        React__default.createElement(Typography, { className: "text-inherit", as: "span", fontSize: "xs", fontWeight: "normal", lineHeight: 5 }, "%"))))),
        React__default.createElement(We$1, { show: hovercard.open, enter: "ease-in-out transition-opacity duration-300", enterFrom: "opacity-0", enterTo: "opacity-100", leave: "ease-in-out transition-opacity duration-300", leaveFrom: "opacity-100", leaveTo: "opacity-0" },
            React__default.createElement(StyledHovercard, { state: { ...hovercard, open: true } },
                React__default.createElement(ProjectAllocation, { currentPlacement: hovercard.currentPlacement, onAdvanceBtnClick: onAdvanceBtnClick, onChange: onChange, onSelect: onSelect, otherAllocations: otherAllocations, projects: projects, totalAllocated: totalDomesticRd + totalForeignRd + totalUnqualifiedRd, allocationSliderId: allocationSliderId })))));
};
const AllocationByProjectCellMemo = memo(AllocationByProjectCell);

const StyledAmountSpan = _default$1.span `
  inline-block
  w-full
  text-right
  text-gray-500
  ${({ $withDollarSign }) => $withDollarSign ? 'first-letter:text-gray-300 first-letter:mr-1' : ''}
`;

const AmountCell = ({ amountInCents, className, showCents = true, withDollarSign = true, roundingMethod = lib.RoundingMethod.ROUND, }) => (React__default.createElement(StyledAmountSpan, { className: className, "$withDollarSign": withDollarSign }, amountInCents
    ? lib.formatNumberToUsd(amountInCents / 100, { withDollarSign, showCents, roundingMethod })
    : `${withDollarSign ? '$--' : '--'}`));
const AmountCellMemo = memo(AmountCell);

const AvatarTitleCell = ({ avatarUrl, className, size = 10, title, ...props }) => (React__default.createElement(AvatarWithText, { className: cn('text-gray-900', className), imgSrc: avatarUrl, size: size ?? 10, title: title, variant: "circular", fontWeight: "medium", ...props }));
const AvatarTitleCellMemo = memo(AvatarTitleCell);

var _path$5;
function _extends$7() { return _extends$7 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$7.apply(null, arguments); }
var SvgPlus = function SvgPlus(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$7({
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$5 || (_path$5 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M10.0001 5C10.5524 5 11.0001 5.44772 11.0001 6V9L14.0001 9C14.5524 9 15.0001 9.44772 15.0001 10C15.0001 10.5523 14.5524 11 14.0001 11H11.0001V14C11.0001 14.5523 10.5524 15 10.0001 15C9.44784 15 9.00012 14.5523 9.00012 14V11H6.00012C5.44784 11 5.00012 10.5523 5.00012 10C5.00012 9.44771 5.44784 9 6.00012 9L9.00012 9V6C9.00012 5.44772 9.44784 5 10.0001 5Z",
    fill: "currentColor"
  })));
};
var ForwardRef$7 = /*#__PURE__*/forwardRef(SvgPlus);
var Memo$7 = /*#__PURE__*/memo(ForwardRef$7);

const StyledAvatarContainerDiv = _default$1.div `
  flex
  items-center
`;
const StyledButtonAddCell = _default$1(Button$2) `
  w-6
  h-6
  rounded-full
`;
const StyledAddMemberContainerSpan = _default$1.span `
  w-6
  h-6
  rounded-full
  border-2
  border-dashed
  border-gray-300
  flex
  items-center
  justify-center
  text-gray-400
`;

const AvatarStackAddCell = ({ avatars, className, onClick }) => (React__default.createElement(StyledAvatarContainerDiv, { className: className },
    React__default.createElement("div", null,
        React__default.createElement(StackedGroupAvatars, null, avatars.map((avatar, index) => (React__default.createElement(Avatar, { imgSrc: avatar.avatarUrl, key: `${avatar.name}-${index}`, size: 6, text: avatar.name, variant: "circular" }))))),
    React__default.createElement("div", { className: "ml-2.5" }, onClick ? (React__default.createElement(StyledButtonAddCell, { onClick: onClick },
        React__default.createElement(StyledAddMemberContainerSpan, null,
            React__default.createElement(Memo$7, { className: "h-5 w-5", "aria-hidden": "true" })))) : null)));
const AvatarStackAddCellMemo = memo(AvatarStackAddCell);

const StyledFlag = _default$1.img `
  inline-block
  mr-2
`;
const StyledCountryText = _default$1(Stack$5) `
  truncate
  w-full
`;

// This country flag API uses country ISO 3166 codes  https://www.iban.com/country-codes
const CountryCell = ({ children, className, countryCode }) => (React__default.createElement(StyledCountryText, { className: className },
    countryCode ? (React__default.createElement(StyledFlag, { alt: `${countryCode} flag`, width: 20, src: `https://flagcdn.com/${countryCode}.svg` })) : null,
    children));
const CountryCellMemo = memo(CountryCell);

// Icon and text
const StyledEmailSpan = _default$1.span `flex gap-x-2 items-center w-full min-w-0 text-gray-400`;
// A flex item shrinks by default, so a long text would otherwise squeeze the icon.
const StyledIconWrapper = _default$1.span `flex shrink-0`;
// `min-w-0` lets the text shrink below its content width so `truncate` can take effect.
const StyledTruncatedText = _default$1.span `min-w-0 truncate`;

const IconAndTextCell = ({ className, icon, text }) => (React__default.createElement(StyledEmailSpan, { className: className },
    React__default.createElement(StyledIconWrapper, null, icon),
    React__default.createElement(StyledTruncatedText, { title: text }, text)));
const IconAndTextCellMemo = memo(IconAndTextCell);

var relativeTimeExports = {};
var relativeTime$1 = {
  get exports(){ return relativeTimeExports; },
  set exports(v){ relativeTimeExports = v; },
};

(function (module, exports) {
	!function(r,e){module.exports=e();}(commonjsGlobal,(function(){return function(r,e,t){r=r||{};var n=e.prototype,o={future:"in %s",past:"%s ago",s:"a few seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",M:"a month",MM:"%d months",y:"a year",yy:"%d years"};function i(r,e,t,o){return n.fromToBase(r,e,t,o)}t.en.relativeTime=o,n.fromToBase=function(e,n,i,d,u){for(var f,a,s,l=i.$locale().relativeTime||o,h=r.thresholds||[{l:"s",r:44,d:"second"},{l:"m",r:89},{l:"mm",r:44,d:"minute"},{l:"h",r:89},{l:"hh",r:21,d:"hour"},{l:"d",r:35},{l:"dd",r:25,d:"day"},{l:"M",r:45},{l:"MM",r:10,d:"month"},{l:"y",r:17},{l:"yy",d:"year"}],m=h.length,c=0;c<m;c+=1){var y=h[c];y.d&&(f=d?t(e).diff(i,y.d,!0):i.diff(e,y.d,!0));var p=(r.rounding||Math.round)(Math.abs(f));if(s=f>0,p<=y.r||!y.r){p<=1&&c>0&&(y=h[c-1]);var v=l[y.l];u&&(p=u(""+p)),a="string"==typeof v?v.replace("%d",p):v(p,n,y.l,s);break}}if(n)return a;var M=s?l.future:l.past;return "function"==typeof M?M(a):M.replace("%s",a)},n.to=function(r,e){return i(r,e,this,!0)},n.from=function(r,e){return i(r,e,this)};var d=function(r){return r.$u?t.utc():t()};n.toNow=function(r){return this.to(d(this),r)},n.fromNow=function(r){return this.from(d(this),r)};}}));
} (relativeTime$1));

var relativeTime = relativeTimeExports;

const StyledTimeSpan = _default$1.span `
  inline-block
  w-full
  text-right
  text-gray-500
`;

dayjs.extend(relativeTime);
// This function will be updated to reflect a new logic based on the new table
const HumanizedDateCell = ({ className, date }) => (React__default.createElement(StyledTimeSpan, { className: className }, dayjs(date).fromNow()));
const HumanizedDateCellMemo = memo(HumanizedDateCell);

const TextCell = ({ alignText = 'start', className, href, text }) => (React__default.createElement(StyledText, { "$alignText": alignText, "$clickable": href !== undefined, className: className }, text));
const TextCellMemo = memo(TextCell);

const StatusBox = _default$1.div `
  inline-block
  mr-0.5
  w-5
  h-5
  border
  rounded-sm
`;
const StatusLabel = _default$1.div `
  w-full
  text-left
`;

const stageColor = (stage) => {
    if (stage <= 0)
        return { bg: 'white', border: 'border-gray-500', text: 'text-gray-500' };
    if (stage <= 0.2)
        return { bg: 'bg-rsbry-200', border: 'border-rsbry-800', text: 'text-rsbry-800' };
    if (stage <= 0.4)
        return { bg: 'bg-pink-100', border: 'border-pink-800', text: 'text-pink-800' };
    if (stage <= 0.6)
        return { bg: 'bg-purple-200', border: 'border-purple-800', text: 'text-purple-800' };
    if (stage < 1)
        return { bg: 'bg-indigo-100', border: 'border-indigo-800', text: 'text-indigo-800' };
    return { bg: 'bg-blue-100', border: 'border-blue-800', text: 'text-blue-800' };
};
const Status$1 = ({ className, currentStage, currentStageNumber, stagesCount, }) => {
    if (currentStageNumber === null || stagesCount === null) {
        return React__default.createElement(StatusLabel, { className: cn('text-gray-500', className) }, currentStage);
    }
    const { bg, border, text } = stageColor(currentStageNumber / stagesCount);
    return (React__default.createElement("div", { className: className },
        Array(stagesCount)
            .fill(1)
            .map((_a, i) => (React__default.createElement(StatusBox, { key: `stage_${i}`, className: `${i < currentStageNumber ? bg : 'white'} ${border}` }))),
        React__default.createElement(StatusLabel, { className: text }, currentStage)));
};
const StatusMemo = memo(Status$1);

const StyledTextRightSpan = _default$1.span `font-medium inline-block w-full text-right`;
const StyledTextLeftSpan = _default$1.span `font-medium inline-block w-full text-left`;

const StyledFooterCell = _default$1.div `
  ${({ $alignText }) => ($alignText === 'start' ? 'justify-start' : 'justify-end')}
  text-gray-600
  font-medium
  tracking-wider
  flex
  items-center
  space-x-1
`;
const StyledPreText = _default$1.span `
  text-gray-300
  font-medium
`;

const FooterCell = ({ alignText = 'start', className, preText, text }) => (React__default.createElement(StyledFooterCell, { "$alignText": alignText, className: className },
    React__default.createElement(StyledPreText, null, preText),
    React__default.createElement("span", { className: "text-ellipsis overflow-hidden" }, text)));
memo(FooterCell);

const ACTION_COLUMN_ID = 'actionColumnId';
const addActionColumn = ({ cellRender, label, onClick, width, }) => ({
    customCellClassNames: (cell) => [
        { 'w-full text-right justify-end': cell.column.id === ACTION_COLUMN_ID },
    ],
    featureHooks: [
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                ...columns,
                {
                    id: ACTION_COLUMN_ID,
                    width,
                    Cell: cellRender
                        ? cellRender
                        : ({ row }) => (React__default.createElement(StyledTextAction, { as: "button", fontSize: "sm", fontWeight: "normal", onClick: (e) => {
                                onClick?.(e, row);
                            } }, label)),
                },
            ]);
        },
    ],
});

const condensedContent = {
    customCellClassNames: () => '!py-2 !h-[37px]',
};

const grayedOutRowBackground = (condition) => ({
    customRowClassNames: (row) => [{ 'bg-gray-100': condition(row) }],
});

const useFeatures = (...features) => {
    const customCellClassNames = features
        .filter((feature) => feature.customCellClassNames)
        .map((feature) => feature.customCellClassNames);
    const customRowClassNames = features
        .filter((feature) => feature.customRowClassNames)
        .map((feature) => feature.customRowClassNames);
    const featureHooks = features.filter((feature) => feature.featureHooks).flatMap((feature) => feature.featureHooks);
    const allStateReducers = features
        .map((feature) => feature.stateReducer)
        .filter((stateReducer) => stateReducer);
    let stateReducer;
    if (Array.isArray(allStateReducers) && allStateReducers.length) {
        stateReducer = (newState, action, previousState, instance) => pipe$2(...allStateReducers)(newState, action, previousState, instance);
    }
    const tableFeatures = {
        customCellClassNames,
        customRowClassNames,
        featureHooks,
        stateReducer,
    };
    return tableFeatures;
};

/**
 * Used for common state and business logic between Table and VirtualTable
 */
const useCommonTable = ({ columnOrder, columns, data, featureHooks = [], initialState = {}, stateReducer, tableOptions = {}, }) => {
    const columnsToRender = useMemo(() => {
        if (columnOrder) {
            // used to provide O(n) table reordering and filtering
            const columnMap = columns.reduce((map, column) => {
                const id = column.id || column.accessor;
                if (typeof id !== 'string') {
                    throw lib.createError('MissingTableColumnId', 'Table column must have an id or string accessor');
                }
                map[id] = column;
                return map;
            }, {});
            // filter and sort columns to match columnOrder array. Note that while react-table works with field order
            // in an object. This JS feature is not guaranteed in the specs, so useColumnOrder hook is used redundantly
            // to prevent future breaks
            return columnOrder.map((columnAccessor) => columnMap[columnAccessor]);
        }
        return columns;
    }, [columnOrder, columns]);
    const { footerGroups, getTableBodyProps, getTableProps, headerGroups, prepareRow, rows, setColumnOrder, state, visibleColumns, } = reactTableExports.useTable({
        columns: columnsToRender,
        data,
        initialState,
        stateReducer,
        ...tableOptions,
    }, ...featureHooks, reactTableExports.useColumnOrder);
    const showFooter = useMemo(() => columnsToRender.findIndex((column) => column.Footer !== undefined) !== -1, [columnsToRender]);
    useEffect(() => {
        if (!columnOrder) {
            return;
        }
        setColumnOrder(columnOrder);
    }, [columnOrder, setColumnOrder]);
    return {
        columnsToRender,
        footerGroups,
        getTableBodyProps,
        getTableProps,
        headerGroups,
        prepareRow,
        rows,
        showFooter,
        state,
        //returning visibleColumns to include columns that are not part of the data columns because of react-table features
        //like select, hide columns, action columns
        visibleColumns,
    };
};

_default$1.table `
  relative
  min-w-full
  border-separate
  border-spacing-0
`;
_default$1.tbody `
  bg-white
`;
_default$1.tfoot `
  bg-gray-50
  shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1),0_-2px_4px_-1px_rgba(0,0,0,0.06)]
  sticky
  -bottom-px
`;
_default$1.th `
  sticky 
  top-0
  z-th
  border-b
  border-gray-200
  bg-gray-50
  bg-opacity-75
  whitespace-nowrap
  px-6
  py-3.5
  leading-5
  text-left
  text-sm
  font-medium
  text-gray-900
  backdrop-blur
  backdrop-filter
`;
_default$1.td `
  border-b
  border-gray-200 
  whitespace-nowrap
  px-6
  py-4
  text-sm
`;
_default$1.td `
  whitespace-nowrap
  px-6
  py-3.5
  leading-4
  text-left
  text-xs
  font-medium
  text-gray-900
`;
const StyledFilteringOverlay = _default$1.div `
  absolute
  inset-0
  flex
  items-center
  justify-center
  z-50
  pointer-events-none
`;

const TableBody = React__default.forwardRef((props, ref) => (React__default.createElement("tbody", { ...props, ref: ref, className: "flex-1" })));
TableBody.displayName = 'TableBody';

const TableFoot = React__default.forwardRef(({ style, ...props }, ref) => (React__default.createElement("tfoot", { ...props, className: "bg-gray-50 shadow-[0_8px_26px_0_rgba(0,0,0,0.16)] h-12 sticky z-10", 
    // HACK: bottom:0 shows the elements so we need to offset it by -2px
    style: { ...style, bottom: '-2px' }, ref: ref })));
TableFoot.displayName = 'TableFoot';
const EmptyTableFoot = React__default.forwardRef((props, ref) => (React__default.createElement("tfoot", { ...props, className: "border-none", ref: ref })));
EmptyTableFoot.displayName = 'EmptyTableFoot';

const convertNumberToPx = (value) => `${value}px`;
const getWidthCssValue = (width) => typeof width === 'number' ? convertNumberToPx(width) : width;
const getGridColumnWidth = (width) => width ? getWidthCssValue(width) : 'minmax(150px, 1fr)';

const TableFooterContent = ({ columns, footerGroups, }) => {
    // this will break if columns are nested
    const gridColumnWidths = React__default.useMemo(() => columns.map(({ width }) => getGridColumnWidth(width)), [columns]);
    return (React__default.createElement(React__default.Fragment, null, footerGroups.map((footerGroup, footerGroupIndex) => (React__default.createElement("tr", { ...footerGroup.getFooterGroupProps({
            className: 'tr',
            style: {
                // cannot use tw classes since `getFooterGroupProps` adds inline css that must be overridden
                width: 'auto',
                display: 'grid',
                gridTemplateColumns: gridColumnWidths.join(' '),
            },
        }), 
        // footerGroup.id is undefined and causes missing key value error
        key: `tr-${footerGroupIndex}` }, footerGroup.headers.map((column, index) => (React__default.createElement("td", { ...column.getHeaderProps({
            className: cn('whitespace-nowrap px-6 py-3.5 leading-5 text-left text-sm font-medium text-gray-900', index === 0 && 'pl-6 pr-5', index === footerGroup.headers.length - 1 && 'pl-5 pr-6', 'td'),
        }), key: column.id }, column.render('Footer')))))))));
};

const TableHead = React__default.forwardRef((props, ref) => (React__default.createElement("thead", { ...props, ref: ref, className: "bg-gray-50" })));
TableHead.displayName = 'TableHead';

const TableHeaderContent = ({ columns, headerGroups, }) => {
    // this will break if columns are nested
    const gridColumnWidths = React__default.useMemo(() => columns.map(({ width }) => getGridColumnWidth(width)), [columns]);
    return (React__default.createElement(React__default.Fragment, null, headerGroups.map((headerGroup, headerGroupIndex) => (React__default.createElement("tr", { ...headerGroup.getHeaderGroupProps({
            className: 'tr bg-gray-50',
            style: {
                // cannot use tw classes since `getHeaderGroupProps` adds inline css that must be overridden
                width: 'auto',
                display: 'grid',
                gridTemplateColumns: gridColumnWidths.join(' '),
            },
        }), 
        // headerGroup.id is undefined and causes missing key value error
        key: `tr-${headerGroupIndex}` }, headerGroup.headers.map((column, index) => (React__default.createElement("td", { ...column.getHeaderProps({
            className: cn('sticky top-0 z-10 whitespace-nowrap px-6 py-3.5 leading-5 text-left text-sm font-medium text-gray-900', index === 0 && 'pl-6 pr-5', index === headerGroup.headers.length - 1 && 'pl-5 pr-6', 'th'),
            style: { width: '100%' },
        }), key: column.id }, column.render('Header')))))))));
};
React__default.memo(TableHeaderContent);

const TableItemContent = ({ customCellClassNames, row, }) => (React__default.createElement(React__default.Fragment, null, row.cells.map((cell, index) => (React__default.createElement("td", { className: cn('px-6 py-4 text-sm border-b border-gray-200 items-center', ...customCellClassNames.map((cnRule) => cnRule(cell)), index === 0 && 'pl-6 pr-5', index === row.cells.length - 1 && 'pl-5 pr-6', 'td'), style: { width: 'auto', display: 'flex' }, key: `${row.id}-${index}` }, cell.render('Cell'))))));

const TableRow = ({ children, columns, customRowClassNames, item, style, ...props }) => {
    const gridColumnWidths = React__default.useMemo(() => columns.map(({ width }) => getGridColumnWidth(width)), [columns]);
    return (React__default.createElement("tr", { ...props, className: cn(...customRowClassNames.map((cnRule) => cnRule(item)), 'tr bg-white flex-1'), style: {
            ...style,
            display: 'grid',
            gridTemplateColumns: gridColumnWidths.join(' '),
        } }, children));
};

/**
 * This is a special use case Table component for efficiently displaying many rows by dynamically rendering
 * only the rows in the current scroll view window. To accomplish this, we loose column auto resizing. To control
 * the width of each column from the parent component using the `width` property in the `Column` object. Default
 * width will be to equally distribute size among all columns. If you don't specify a width for a colum, it will
 * follow this rule.
 *
 * @param input.isFooterLoading - when the table footer should show a loading spinner
 * @param input.isBodyLoading - when the table body should show a loading overlay
 * @param input.threshold Number of rows from the bottom of the list at which to start fetching more data.
 */
const Table = ({ className, columnOrder, columns, autoHeight = false, customCellClassNames = [], customRowClassNames = [], data, featureHooks = [], initialState = {}, endReached, showZeroState = false, stateReducer, tableOptions, loadMoreRowFromBottomThreshold = 10, zeroState, isFooterLoading, isBodyLoading, }) => {
    const { footerGroups, headerGroups, prepareRow, rows, showFooter, visibleColumns } = useCommonTable({
        columns,
        columnOrder,
        data,
        featureHooks: [...featureHooks, reactTableExports.useBlockLayout],
        initialState,
        stateReducer,
        tableOptions,
    });
    const handleRangeChanged = (range) => {
        const { endIndex } = range;
        if (rows.length - endIndex <= loadMoreRowFromBottomThreshold) {
            endReached?.();
        }
    };
    // Returns one of three tfoot renderers for react-virtuoso's components.TableFoot slot,
    // depending on whether the footer is loading or empty.
    const getTableFooter = () => {
        if (isFooterLoading) {
            const LoadingFoot = forwardRef((_props, ref) => (React__default.createElement("tfoot", { ref: ref, className: "bg-gray-50 flex justify-center w-full h-12 z-10 sticky bottom-0" },
                React__default.createElement("tr", null,
                    React__default.createElement("td", { className: "flex justify-center items-center w-full h-full" },
                        React__default.createElement(Spinner, { size: "sm" }))))));
            LoadingFoot.displayName = 'LoadingTableFoot';
            return LoadingFoot;
        }
        return showFooter ? TableFoot : EmptyTableFoot;
    };
    if (autoHeight) {
        return (React__default.createElement("div", { className: "relative" },
            isBodyLoading && (React__default.createElement(StyledFilteringOverlay, null,
                React__default.createElement(Spinner, { size: "lg" }))),
            React__default.createElement("table", { className: cn(className, 'flex flex-col min-w-full divide-y divide-gray-300 w-fit', {
                    'pointer-events-none opacity-50': isBodyLoading,
                }) },
                React__default.createElement(TableHead, null,
                    React__default.createElement(TableHeaderContent, { headerGroups: headerGroups, columns: visibleColumns })),
                React__default.createElement("tbody", { className: "flex-1" }, rows.length === 0 && zeroState ? (React__default.createElement("tr", { className: "tr bg-white flex-1", style: { display: 'grid', gridTemplateColumns: '1fr' } },
                    React__default.createElement("td", { className: "td px-6 py-4 text-sm border-b border-gray-200 items-center", style: { display: 'flex' } }, zeroState))) : (rows.map((row) => {
                    prepareRow(row);
                    return (React__default.createElement(TableRow, { key: row.id, columns: visibleColumns, customRowClassNames: customRowClassNames, item: row },
                        React__default.createElement(TableItemContent, { row: row, customCellClassNames: customCellClassNames })));
                }))),
                showFooter && (React__default.createElement(TableFoot, null,
                    React__default.createElement(TableFooterContent, { footerGroups: footerGroups, columns: visibleColumns }))))));
    }
    return (React__default.createElement("div", { className: "relative h-full" },
        isBodyLoading && (React__default.createElement(StyledFilteringOverlay, null,
            React__default.createElement(Spinner, { size: "lg" }))),
        React__default.createElement(TableVirtuoso, { data: rows, totalCount: rows.length, rangeChanged: handleRangeChanged, itemContent: (_, row) => {
                prepareRow(row);
                return React__default.createElement(TableItemContent, { key: row.id, row: row, customCellClassNames: customCellClassNames });
            }, fixedHeaderContent: () => (React__default.createElement(TableHeaderContent, { headerGroups: headerGroups, columns: visibleColumns })), fixedFooterContent: () => showFooter ? React__default.createElement(TableFooterContent, { footerGroups: footerGroups, columns: visibleColumns }) : React__default.createElement(React__default.Fragment, null), components: {
                EmptyPlaceholder: () => (React__default.createElement(We$1, { as: "div", appear: true, show: showZeroState, className: "bg-white bg-opacity-75 sticky top-[49px] z-th backdrop-blur backdrop-filter", enter: "ease-in-out transition-all duration-500", enterFrom: "opacity-25 -translate-x-full", enterTo: "opacity-100 translate-x-0", leave: "ease-in-out transition-all duration-500", leaveFrom: "opacity-100 translate-x-0", leaveTo: "opacity-25 translate-x-full" }, zeroState)),
                Table: ({ ...props }) => (React__default.createElement("table", { ...props, className: cn(className, 'flex-col min-w-full min-h-full flex justify-between divide-y divide-gray-300 w-fit', { 'pointer-events-none opacity-50': isBodyLoading }) })),
                TableHead: TableHead,
                TableBody: TableBody,
                TableRow: ({ children, ...props }) => (React__default.createElement(TableRow, { columns: visibleColumns, customRowClassNames: customRowClassNames, ...props }, children)),
                TableFoot: getTableFooter(),
            } })));
};

var _path$4;
function _extends$6() { return _extends$6 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$6.apply(null, arguments); }
var SvgCollection = function SvgCollection(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$6({
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    style: {
      fill: "currentColor"
    },
    ref: ref
  }, props), _path$4 || (_path$4 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M7.88839 6.91322C7.88839 7.01196 7.98711 7.11068 8.08588 7.11068H16.4787C16.5775 7.11068 16.6762 7.01196 16.6762 6.91322V3.95103C16.6762 3.85229 16.5775 3.75354 16.4787 3.75354H14.7014V3.45733C14.7014 2.07499 13.6153 0.988846 12.233 0.988846C12.0355 0.988846 11.838 0.988846 11.5418 1.08759C10.4556 1.3838 9.66569 2.37119 9.76447 3.55608V3.75354H8.08588C7.98711 3.6548 7.88839 3.85229 7.88839 3.95103V6.91322ZM12.3317 2.46994C12.7267 2.46994 13.1216 2.76615 13.1216 3.1611C13.1216 3.55608 12.8254 3.95103 12.3317 3.95103C11.9367 3.95103 11.5418 3.6548 11.5418 3.1611C11.5418 2.76615 11.9367 2.46994 12.3317 2.46994ZM21.7119 5.72833C21.7119 4.64219 20.8232 3.75354 19.7371 3.75354H18.5523C18.3548 3.75354 18.256 3.85229 18.256 3.95103V5.43213C18.256 5.53087 18.3548 5.62959 18.4535 5.62959H19.2434C19.5396 5.62959 19.7371 5.82708 19.7371 6.12328V21.9216C19.7371 22.2178 19.5396 22.4153 19.2434 22.4153H5.4199C5.1237 22.4153 4.92621 22.2178 4.92621 21.9216V6.12328C4.92621 5.82708 5.1237 5.62959 5.4199 5.62959H6.20981C6.30858 5.72833 6.4073 5.62959 6.4073 5.43213V3.95103C6.4073 3.85229 6.30858 3.75354 6.11109 3.75354H4.92621C3.8401 3.75354 2.95142 4.64219 2.95142 5.72833V22.5141C2.95142 23.6002 3.8401 24.4888 4.92621 24.4888H19.7371C20.8232 24.4888 21.7119 23.6002 21.7119 22.5141V5.72833ZM9.07328 17.8733C9.07328 18.9594 8.1846 19.7494 7.1972 19.7494C6.50602 19.7494 5.81489 19.3544 5.6174 18.762C5.1237 17.8733 5.51862 16.7872 6.4073 16.2935C6.4073 16.2935 6.50602 16.1947 6.50602 16.096V12.4426C6.50602 11.554 7.1972 10.8628 8.08588 10.8628H9.9619C10.0607 10.8628 10.1594 10.7641 10.1594 10.6653V10.1716C10.1594 9.67791 10.5544 9.28296 11.0481 9.28296H13.8128C14.3065 9.28296 14.7014 9.67791 14.7014 10.1716V10.5666C14.7014 10.6653 14.8002 10.7641 14.8989 10.7641H16.4787C17.3674 10.7641 18.0586 11.4552 18.0586 12.3439V15.9972C18.0586 16.096 18.0586 16.1947 18.1573 16.1947C18.4535 16.2935 18.7497 16.5897 18.9472 16.9846C19.4409 17.8733 19.0459 18.9594 18.1573 19.4531C17.2687 19.9468 16.1825 19.5519 15.6888 18.6632C15.1951 17.7746 15.5901 16.6884 16.4787 16.1947C16.4787 16.1947 16.5775 16.096 16.5775 15.9972V12.3439C16.5775 12.2451 16.4787 12.1464 16.38 12.1464H14.8002C14.7014 12.1464 14.6027 12.2451 14.6027 12.3439V12.8376C14.6027 13.3313 14.2077 13.7262 13.7141 13.7262H13.2204C13.1216 13.7262 13.0229 13.825 13.0229 13.9237V15.9972C13.0229 16.096 13.0229 16.1947 13.1216 16.1947C13.4178 16.2935 13.7141 16.5897 13.9115 16.9846C14.4052 17.8733 14.0103 18.9594 13.1216 19.4531C12.233 19.9468 11.1468 19.5519 10.6531 18.6632C10.1594 17.7746 10.5544 16.6884 11.443 16.1947C11.443 16.1947 11.5418 16.096 11.5418 15.9972V13.9237C11.5418 13.825 11.443 13.7262 11.3443 13.7262H10.8506C10.3569 13.7262 9.9619 13.3313 9.9619 12.8376V12.3439C9.9619 12.2451 9.86318 12.1464 9.76447 12.1464H8.1846C8.08588 12.1464 7.98711 12.2451 7.98711 12.3439V15.9972C7.98711 16.096 7.98711 16.1947 8.08588 16.1947C8.6783 16.5897 9.07328 17.1821 9.07328 17.8733Z"
  })));
};
var ForwardRef$6 = /*#__PURE__*/forwardRef(SvgCollection);
var Memo$6 = /*#__PURE__*/memo(ForwardRef$6);

var _path$3, _path2$1;
function _extends$5() { return _extends$5 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$5.apply(null, arguments); }
var SvgOpenLink = function SvgOpenLink(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    viewBox: "0 0 24 24",
    fill: "currentColor",
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$3 || (_path$3 = /*#__PURE__*/React.createElement("path", {
    d: "M15.25 6.91665e-07H23.25C23.4481 0.00259135 23.6374 0.0824405 23.7775 0.222535C23.9176 0.36263 23.9974 0.551894 24 0.750001V8.62C24.0018 8.76666 23.96 8.91055 23.8799 9.03341C23.7998 9.15626 23.685 9.25253 23.55 9.31C23.4585 9.34988 23.3598 9.37031 23.26 9.37C23.1615 9.37047 23.0638 9.35125 22.9728 9.31347C22.8818 9.27569 22.7993 9.22011 22.73 9.15L19.44 5.91L9.96001 15.24C9.77214 15.4211 9.52094 15.5216 9.26001 15.52C9.1277 15.5195 8.99682 15.4927 8.87494 15.4412C8.75307 15.3897 8.64262 15.3145 8.55001 15.22C8.36376 15.0326 8.25922 14.7792 8.25922 14.515C8.25922 14.2508 8.36376 13.9974 8.55001 13.81L18 4.51L14.72 1.28C14.6159 1.17617 14.545 1.04366 14.5165 0.89939C14.488 0.755121 14.5032 0.605632 14.56 0.470001C14.6144 0.331316 14.7093 0.212261 14.8324 0.128405C14.9555 0.0445492 15.1011 -0.000202913 15.25 6.91665e-07Z"
  })), _path2$1 || (_path2$1 = /*#__PURE__*/React.createElement("path", {
    d: "M17.7929 10.9129C17.9804 10.7254 18.2348 10.62 18.5 10.62C18.7652 10.62 19.0196 10.7254 19.2071 10.9129C19.3946 11.1004 19.5 11.3548 19.5 11.62V22.12C19.5 22.6186 19.3019 23.0968 18.9494 23.4494C18.5968 23.8019 18.1186 24 17.62 24H1.87C1.37313 23.9974 0.897508 23.7981 0.547099 23.4458C0.196691 23.0936 -7.02886e-06 22.6169 1.88379e-10 22.12V6.37C1.88379e-10 5.87405 0.197017 5.3984 0.54771 5.04771C0.898403 4.69702 1.37405 4.5 1.87 4.5H12.37C12.6352 4.5 12.8896 4.60536 13.0771 4.79289C13.2646 4.98043 13.37 5.23478 13.37 5.5C13.37 5.76522 13.2646 6.01957 13.0771 6.20711C12.8896 6.39464 12.6352 6.5 12.37 6.5H2V22H17.5V11.62C17.5 11.3548 17.6054 11.1004 17.7929 10.9129Z"
  })));
};
var ForwardRef$5 = /*#__PURE__*/forwardRef(SvgOpenLink);
var Memo$5 = /*#__PURE__*/memo(ForwardRef$5);

var _path$2;
function _extends$4() { return _extends$4 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$4.apply(null, arguments); }
var SvgPassword = function SvgPassword(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    viewBox: "0 0 24 24",
    style: {
      fill: "currentColor"
    },
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    d: "M23.4999 4.40664e-06H20.4999C20.4344 -0.000795015 20.3693 0.0121378 20.3091 0.0379713C20.2488 0.0638048 20.1946 0.101967 20.1499 0.150004L10.6199 9.68001C9.02075 8.94839 7.21533 8.802 5.51917 9.26641C3.82301 9.73082 2.34398 10.7765 1.34054 12.2207C0.337094 13.6649 -0.126946 15.4158 0.0295069 17.1674C0.18596 18.919 0.952957 20.56 2.19647 21.8035C3.43998 23.047 5.08091 23.814 6.83253 23.9704C8.58414 24.1269 10.335 23.6628 11.7792 22.6594C13.2234 21.6559 14.2691 20.1769 14.7335 18.4808C15.1979 16.7846 15.0515 14.9792 14.3199 13.38L16.6399 11.07L18.5699 11.72C18.7384 11.7808 18.92 11.7952 19.0959 11.7618C19.2718 11.7283 19.4355 11.6483 19.5699 11.53C19.6993 11.4197 19.7984 11.2783 19.8579 11.1191C19.9173 10.9598 19.9353 10.7881 19.9099 10.62L19.5099 8.2L20.2299 7.47L21.6899 8.14001C21.8598 8.21547 22.0474 8.24207 22.2316 8.21682C22.4158 8.19156 22.5894 8.11546 22.7327 7.99704C22.876 7.87863 22.9835 7.7226 23.0431 7.54648C23.1027 7.37036 23.112 7.18112 23.0699 7L22.5599 5.19L23.8499 3.89C23.9033 3.84097 23.9446 3.78025 23.9707 3.71259C23.9967 3.64493 24.0067 3.57218 23.9999 3.5V0.500004C23.9999 0.367396 23.9473 0.240219 23.8535 0.146451C23.7597 0.0526828 23.6325 4.40664e-06 23.4999 4.40664e-06ZM6.99993 18.5C6.99993 18.7967 6.91195 19.0867 6.74713 19.3334C6.58231 19.58 6.34804 19.7723 6.07395 19.8858C5.79986 19.9994 5.49826 20.0291 5.20729 19.9712C4.91632 19.9133 4.64905 19.7704 4.43927 19.5607C4.22949 19.3509 4.08663 19.0836 4.02875 18.7926C3.97087 18.5017 4.00058 18.2001 4.11411 17.926C4.22764 17.6519 4.4199 17.4176 4.66657 17.2528C4.91325 17.088 5.20326 17 5.49993 17C5.89775 17 6.27928 17.158 6.56059 17.4393C6.84189 17.7206 6.99993 18.1022 6.99993 18.5Z"
  })));
};
var ForwardRef$4 = /*#__PURE__*/forwardRef(SvgPassword);
var Memo$4 = /*#__PURE__*/memo(ForwardRef$4);

var _path$1, _path2;
function _extends$3() { return _extends$3 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$3.apply(null, arguments); }
var SvgUsers = function SvgUsers(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    viewBox: "0 0 24 24",
    style: {
      fill: "currentColor"
    },
    xmlns: "http://www.w3.org/2000/svg",
    ref: ref
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    d: "M18.78 14.69L18 14.43C17.93 14.43 17.83 14.29 17.76 14.03C17.5925 13.3865 17.6204 12.7077 17.84 12.08C18.79 11.08 19.71 9.75 19.71 6.55C19.71 3.06 17.33 1.55 14.96 1.55C14.566 1.55219 14.1734 1.59916 13.79 1.69C13.7482 1.70114 13.7099 1.72252 13.6785 1.7522C13.647 1.78189 13.6235 1.81894 13.61 1.86C13.5907 1.89706 13.5807 1.93823 13.5807 1.98C13.5807 2.02178 13.5907 2.06294 13.61 2.1C14.6567 3.3616 15.2078 4.96142 15.16 6.6C15.16 9.88 14.24 11.6 13.16 12.83C13.1447 12.872 13.1447 12.918 13.16 12.96C13.1551 13.0099 13.1551 13.0601 13.16 13.11C13.1565 13.1633 13.1701 13.2163 13.1989 13.2613C13.2277 13.3063 13.2701 13.3409 13.32 13.36C15.73 14.22 19.46 15.56 19.46 20.55V22C19.46 22.07 19.46 22.15 19.46 22.22C19.455 22.2557 19.4578 22.292 19.4681 22.3266C19.4785 22.3611 19.4962 22.393 19.52 22.42C19.5422 22.4458 19.5699 22.4664 19.601 22.4802C19.6321 22.4941 19.6659 22.5008 19.7 22.5H23.5C23.6326 22.5 23.7598 22.4473 23.8536 22.3536C23.9473 22.2598 24 22.1326 24 22V20.5C24 16.55 21.23 15.57 18.78 14.69Z"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    d: "M18 20.5C18 16.55 15.23 15.57 12.78 14.69L12 14.43C11.93 14.43 11.83 14.29 11.76 14.03C11.5925 13.3865 11.6204 12.7077 11.84 12.08C12.79 11.08 13.71 9.75 13.71 6.55C13.71 3.06 11.33 1.55 8.96 1.55C6.59 1.55 4.25 3.06 4.25 6.55C4.25 9.75 5.17 11.04 6.12 12.07C6.34674 12.7135 6.37462 13.4104 6.2 14.07C6.13 14.33 6.03 14.44 5.96 14.47L5.2 14.74C2.76 15.56 0 16.54 0 20.5V22C0 22.1326 0.0526784 22.2598 0.146447 22.3536C0.240215 22.4473 0.367392 22.5 0.5 22.5H17.5C17.6326 22.5 17.7598 22.4473 17.8536 22.3536C17.9473 22.2598 18 22.1326 18 22V20.5Z"
  })));
};
var ForwardRef$3 = /*#__PURE__*/forwardRef(SvgUsers);
var Memo$3 = /*#__PURE__*/memo(ForwardRef$3);

const StyledTicketData = _default$1(Stack$5) `
  grow
  w-0
`;
// TODO: Make ticket color match project
// https://linear.app/neo-tax/issue/NEO-3978/ticket-name-color-should-match-project-color
const StyledTicketName = _default$1.div `
  text-ellipsis
  whitespace-nowrap
  overflow-hidden
  text-primary-600
  font-medium
  text-sm
  items-center
`;
const StyledDataItemValue = _default$1.div `
  text-ellipsis
  whitespace-nowrap
  overflow-hidden
  font-normal
  text-sm
  ${({ $noValue }) => ($noValue ? 'text-gray-300' : 'text-gray-500')}
`;

const DataItem = ({ description, icon, value }) => (React__default.createElement(Stack$5, { direction: "row", gap: 2, alignItems: "center" },
    React__default.createElement("dt", { title: description },
        React__default.createElement(Icon, { node: icon, size: 6, colorVariant: "none" })),
    React__default.createElement(StyledDataItemValue, { "$noValue": !value, title: value ?? 'None' }, value ?? 'None')));

const Ticket = ({ children: _, collectionName, contributorName, identifier, name, onSelectedChange, selected, ticketUrl, ...otherProps }) => {
    const selectable = typeof selected !== 'undefined';
    return (React__default.createElement(Stack$5, { paddingX: 4, paddingY: 4, gap: 2, ...otherProps },
        selectable && (React__default.createElement(CheckboxAtom, { checked: selected, onChange: onSelectedChange && ((e) => onSelectedChange(e.target.checked)) })),
        React__default.createElement(StyledTicketData, { direction: "column", gap: 2 },
            React__default.createElement(Stack$5, { justifyContent: "space-between", gap: 2 },
                React__default.createElement(StyledTicketName, { title: name ?? undefined }, name ?? '-- No Ticket Name --'),
                ticketUrl && (React__default.createElement("a", { href: ticketUrl, target: "_blank", rel: "noreferrer" },
                    React__default.createElement(Icon, { node: React__default.createElement(Memo$5, null), size: 4, colorVariant: "none" })))),
            React__default.createElement(Stack$5, { as: "dl", direction: "column", gap: 2 },
                React__default.createElement(DataItem, { description: "Assignee", icon: React__default.createElement(Memo$3, null), value: contributorName }),
                React__default.createElement(DataItem, { description: "Issue ID", icon: React__default.createElement(Memo$4, null), value: identifier }),
                React__default.createElement(DataItem, { key: collectionName, description: "Ticketing System Project", icon: React__default.createElement(Memo$6, null), value: collectionName })))));
};

const WidthBySide = {
    bottom: 'max-w-full',
    left: 'max-w-lg',
    right: 'max-w-lg',
    top: 'max-w-full',
};
const PositionBySide = {
    bottom: '',
    left: 'left-0',
    right: 'right-0',
    top: '',
};
const StyledOverlay = _default$1.div `
    fixed
    inset-0
    bg-gray-500
    bg-opacity-75
    transition-opacity
`;
const StyledDialogPanel = _default$1(mt.Panel) `
    pointer-events-auto
    w-screen
    max-w-md
    ${({ $side }) => WidthBySide[$side]}
`;
const StyledSideDrawer = _default$1.div `
    flex
    h-full
    flex-col
    bg-white
    shadow-xl
`;
const StyledHeaderBar = _default$1.div `
    bg-white
    p-6
`;
const StyledHeaderContentContainer = _default$1.div `
    flex
    items-start
`;
const StyledHeaderTextWrapper = _default$1.div `
    space-y-1
`;
const StyledHeaderTitle = _default$1(mt.Title) `
    text-lg
    font-medium
    text-gray-900
`;
const StyledHeaderSubtitle = _default$1.p `
    text-sm
    text-gray-500
`;
const StyledDrawerContainerFixed = _default$1.div `
    fixed
    inset-0
    overflow-hidden
`;
const StyledDrawerContainerAbsolute = _default$1.div `
    absolute
    inset-0
    overflow-hidden
`;
const StyledDrawerPositionContainer = _default$1.div `
    pointer-events-none
    h-full
    fixed
    bottom-0
    flex
    max-w-full
    ${({ $side }) => PositionBySide[$side]}
`;
_default$1.h1 `
    font-medium
    text-lg
    text-gray-900
`;
const StyledCloseContainer = _default$1.div `
    flex
    h-7
    items-center
`;
const StyledCloseButtonIcon = _default$1(Memo$a) `
    h-3
    w-3
    inline-block
`;
const StyledChildrenWrapper = _default$1.div `
    flex-1
    overflow-y-hidden
`;

const SideDrawer = ({ afterClose = () => { }, children, className, header, isOpen, 
//The onClose callback fires when an open dialog is dismissed, which happens when the user clicks outside the your Dialog.Panel or presses the Escape key
//For that reason, if we want to prevent dismissing a dialog, we are not going to set a `setIsOpen` callback
setIsOpen = () => { }, side = 'right', topMargin, }) => {
    const enterFromAndLeaveTo = {
        '-translate-y-full': side === 'top',
        'translate-y-full': side === 'bottom',
        'translate-x-full': side === 'right',
        '-translate-x-full': side === 'left',
    };
    const enterToAndLeaveFrom = {
        'translate-y-0': side === 'top',
        '-translate-y-0': side === 'bottom',
        '-translate-x-0': side === 'right',
        'translate-x-0': side === 'left',
    };
    return (React__default.createElement(We$1.Root, { appear: true, show: isOpen, as: Fragment },
        React__default.createElement(mt, { as: "div", className: cn('relative z-modal', className), onClose: setIsOpen },
            React__default.createElement(We$1.Child, { as: Fragment, enter: "ease-in-out duration-500", enterFrom: "opacity-0", enterTo: "opacity-100", leave: "ease-in-out duration-500", leaveFrom: "opacity-100", leaveTo: "opacity-0" },
                React__default.createElement(StyledOverlay, { "data-testid": "overlay" })),
            React__default.createElement(StyledDrawerContainerFixed, null,
                React__default.createElement(StyledDrawerContainerAbsolute, null,
                    React__default.createElement(StyledDrawerPositionContainer, { "$side": side, style: {
                            height: `calc(100% - ${typeof topMargin === 'string' ? topMargin : (topMargin ?? 0) + 'px'} )`,
                        } },
                        React__default.createElement(We$1.Child, { afterLeave: afterClose, as: Fragment, enter: "transform transition ease-in-out duration-500 sm:duration-700", enterFrom: cn(enterFromAndLeaveTo), enterTo: cn(enterToAndLeaveFrom), leave: "transform transition ease-in-out duration-500 sm:duration-700", leaveFrom: cn(enterToAndLeaveFrom), leaveTo: cn(enterFromAndLeaveTo) },
                            React__default.createElement(StyledDialogPanel, { "$side": side },
                                React__default.createElement(StyledSideDrawer, null,
                                    header,
                                    React__default.createElement(StyledChildrenWrapper, null, children))))))))));
};

const SideDrawerHeaderContent = ({ image, showAvatarPlaceholder, subtitle, title, withAvatar, }) => {
    if (withAvatar) {
        return (React__default.createElement(AvatarWithText, { imgSrc: image, variant: "circular", size: 10, title: title, text: showAvatarPlaceholder ? '' : title, subtitle: subtitle }));
    }
    return (React__default.createElement(StyledHeaderTextWrapper, null,
        React__default.createElement(StyledHeaderTitle, null, title),
        subtitle && React__default.createElement(StyledHeaderSubtitle, null, subtitle)));
};

const SideDrawerHeader = ({ image, setIsOpen, showAvatarPlaceholder, subtitle, title, withAvatar, }) => (React__default.createElement(StyledHeaderBar, null,
    React__default.createElement(StyledHeaderContentContainer, null,
        React__default.createElement(SideDrawerHeaderContent, { image: image, title: title, showAvatarPlaceholder: showAvatarPlaceholder, subtitle: subtitle, withAvatar: withAvatar }),
        React__default.createElement(StyledCloseContainer, null,
            React__default.createElement(Button, { className: "absolute right-6", onClick: () => setIsOpen(false), rounded: "regular", size: "xs", variant: "white" },
                React__default.createElement("span", { className: "sr-only" }, "Close panel"),
                React__default.createElement(StyledCloseButtonIcon, { "aria-hidden": true }))))));

const useSideDrawer = ({ initialOpen } = { initialOpen: false }) => {
    const [isOpen, setOpen] = useState(initialOpen);
    const toggle = () => setOpen((prevState) => !prevState);
    return {
        isOpen,
        setIsOpen: setOpen,
        toggle,
    };
};

const StyledSplitViewContainer = _default$1.div `
  flex
  w-full
  h-full
  overflow-x-hidden
`;
const StyledMainView = _default$1.div `
  flex-grow
  h-full
  ${({ $isResizing }) => $isResizing ? 'transition-none' : 'transition-all duration-500 ease-in-out'}
`;
const StyledDrawerView = _default$1.div `
  h-full
  ${({ $isResizing }) => $isResizing ? 'transition-none' : 'transition-all duration-500 ease-in-out'}
`;
const StyledDrawerInner = _default$1.div `
  h-full
  shadow-2xl
  bg-white
  ${({ $locked }) => ($locked ? 'opacity-50 pointer-events-none' : '')}
`;

const SplitView = ({ drawerView, fixedWidth, isOpen, locked, mainView, minWidth, onTransitionEnd, splitPercentage, }) => {
    const drawerRef = useRef(null);
    // Set through a ref because React 18 does not recognise `inert` as a DOM prop.
    useEffect(() => {
        drawerRef.current?.toggleAttribute('inert', Boolean(locked));
    }, [locked]);
    const drawerWidth = useMemo(() => {
        if (typeof fixedWidth !== 'undefined') {
            return fixedWidth;
        }
        if (typeof splitPercentage !== 'undefined') {
            return Math.max(window.innerWidth * (splitPercentage / 100), minWidth ?? 0);
        }
        throw new Error('Either splitPercentage or fixedWidth must be defined');
    }, [splitPercentage, minWidth, fixedWidth]);
    const [isResizing, setIsResizing] = useState(false);
    useEffect(() => {
        const handleResize = () => {
            setIsResizing(true);
            setTimeout(() => setIsResizing(false), 0);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const drawerViewStyle = {
        marginRight: isOpen ? '0' : `-${drawerWidth}px`,
        width: `${drawerWidth}px`,
    };
    const mainViewStyle = {
        width: isOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
    };
    return (React__default.createElement(StyledSplitViewContainer, null,
        React__default.createElement(StyledMainView, { "$isResizing": isResizing, style: mainViewStyle }, mainView),
        React__default.createElement(StyledDrawerView, { "$isResizing": isResizing, style: drawerViewStyle, onTransitionEnd: onTransitionEnd },
            React__default.createElement(StyledDrawerInner, { ref: drawerRef, "$locked": locked }, drawerView))));
};

const INITIAL_SPLIT_PERCENTAGE = 33;
const useSplitView = ({ initialOpen = false, initialSplitPercentage = INITIAL_SPLIT_PERCENTAGE, fixedWidth, } = {}) => {
    const [isOpen, setIsOpen] = useState(initialOpen);
    const [splitPercentage, setSplitPercentage] = useState(initialSplitPercentage);
    const [fixedWidthState, setFixedWidth] = useState(fixedWidth);
    const handleToggle = useCallback(() => {
        setIsOpen((prevState) => !prevState);
    }, []);
    const handleSplitPercentageChange = useCallback((percentage) => {
        setSplitPercentage(percentage);
    }, []);
    const handleFixedWidthChange = useCallback((width) => {
        setFixedWidth(width);
    }, []);
    return {
        isOpen,
        fixedWidth: fixedWidthState,
        splitPercentage,
        handleToggle,
        handleSplitPercentageChange,
        handleFixedWidthChange,
        setIsOpen,
    };
};

const StyledSectionTitle = _default$1.div `
    pb-5
`;
const StyledSectionTitleRow = _default$1.div `
    sm:flex
    sm:justify-between
`;
const LargeStyledTitle = _default$1.h2 `
    text-3xl
    leading-9
    font-semibold
    text-gray-900
`;
const BaseStyledTitle = _default$1.h3 `
    text-lg
    leading-6
    font-medium
    text-gray-900
`;
const StyledSectionSubtitle = _default$1.p `
    mt-2
    mb-0
    text-sm
    text-gray-500
`;
const StyledTrailingContent = _default$1.div `
${({ $alignContent }) => ($alignContent === 'top' ? 'items-start' : 'items-end')}
    mt-3
    sm:mt-0
    flex
`;

const SectionTitle = ({ alignTrailingContent = 'top', className, size = 'base', subtitle, title, trailingContent, }) => (React__default.createElement(StyledSectionTitle, { className: className },
    React__default.createElement(StyledSectionTitleRow, null,
        React__default.createElement("div", { className: "sm:w-0 sm:flex-1" },
            size === 'lg' && React__default.createElement(LargeStyledTitle, null, title),
            size === 'base' && React__default.createElement(BaseStyledTitle, null, title),
            subtitle && React__default.createElement(StyledSectionSubtitle, null, subtitle)),
        trailingContent && (React__default.createElement(StyledTrailingContent, { "$alignContent": alignTrailingContent, "data-testid": "trailing-content" }, trailingContent)))));
const SectionTitleMemo = memo(SectionTitle);

const StyledActionsMenu = _default$1(Menu$1) `
  z-modal
  min-w-[248px]
  p-1
  rounded-[10px]
  bg-white
  border
  border-gray-200
  outline-none
  shadow-[0_0_0_1px_rgba(16,24,40,0.04),0_12px_24px_-8px_rgba(16,24,40,0.16),0_4px_8px_-4px_rgba(16,24,40,0.08)]
`;
const StyledActionsMenuItem = _default$1(MenuItem$1) `
  flex
  items-start
  gap-2.5
  px-3
  py-2.5
  rounded-md
  text-sm
  font-medium
  text-left
  text-gray-800
  cursor-pointer
  outline-none
  select-none
  transition-colors
  duration-150
  [&[data-active-item]:not([aria-disabled='true'])]:bg-primary-50
  [&[data-active-item]:not([aria-disabled='true'])]:text-primary-800
  focus-visible:ring-2
  focus-visible:ring-primary-200
  [&[aria-disabled='true']]:cursor-not-allowed
  [&[aria-disabled='true']]:text-gray-400
`;
const StyledActionsMenuItemText = _default$1.span `
  flex
  flex-col
  flex-1
  min-w-0
  gap-0.5
`;
const StyledActionsMenuItemLabel = _default$1.span `
  truncate
`;
const StyledActionsMenuItemHint = _default$1.span `
  text-xs
  font-normal
  text-gray-400
`;

const ActionsMenuButton = ({ actions, collapseSingleAction = false, id, label, size = 'sm', }) => {
    const menuState = useMenuState({ gutter: 6, placement: 'bottom-end' });
    if (actions.length === 0) {
        return React__default.createElement(React__default.Fragment, null);
    }
    if (collapseSingleAction && actions.length === 1) {
        const [only] = actions;
        return (React__default.createElement(Button, { id: only.id ?? id, size: size, variant: "primary", rounded: "regular", disabled: only.disabled, title: only.disabled ? only.hint : undefined, onClick: only.onSelect }, only.label));
    }
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Button, { id: id, as: MenuButton, state: menuState, size: size, variant: "primary", rounded: "regular" },
            React__default.createElement(Stack$5, { direction: "row", alignItems: "center", gap: 2 },
                React__default.createElement("span", null, label),
                React__default.createElement(Icon, { size: 2, node: React__default.createElement(Memo$8, null) }))),
        React__default.createElement(StyledActionsMenu, { state: menuState, portal: true, "aria-label": label }, actions.map((action) => (React__default.createElement(StyledActionsMenuItem, { key: action.label, id: action.id, disabled: action.disabled, onClick: () => {
                menuState.hide();
                action.onSelect();
            } },
            React__default.createElement(StyledActionsMenuItemText, null,
                React__default.createElement(StyledActionsMenuItemLabel, null, action.label),
                action.disabled && action.hint && React__default.createElement(StyledActionsMenuItemHint, null, action.hint))))))));
};

const Menu = ({ children, className }) => (React__default.createElement("div", { className: className }, children));
const MenuMemo = memo(Menu);

const StyledDraggableMenuItem = _default$1.div `
    ${({ $isDragging }) => ($isDragging ? 'opacity-50' : 'opacity-100')}
    flex
    justify-between
    items-center
    text-gray-500
    border-t
    first:border-none
    border-gray-200
    text-sm
`;
const StyledMenuItem = _default$1.div `
    flex
    justify-between
    items-center
    p-4
    text-gray-500
    border-b
    border-gray-200
    text-sm
    h-14
`;
const IconAndTitle = _default$1.div `
    flex
    items-center
`;
const MenuItemIcon = _default$1.div `
    w-4
    h-4
    mr-2
    text-inherit
    focus:outline-primary-600
`;

const MenuItem = ({ icon, title, toggle }) => (React__default.createElement(StyledMenuItem, null,
    React__default.createElement(IconAndTitle, null,
        React__default.createElement(MenuItemIcon, { "data-testid": `icon-${title}` }, icon),
        title),
    toggle && (React__default.createElement("div", { "data-testid": `toggle-${title}` },
        React__default.createElement(ToggleSwitch, { checked: toggle.checked, disabled: toggle.disabled, onChange: toggle.onChange, withIcon: toggle.withIcon })))));
const MenuItemMemo = memo(MenuItem);

function useCombinedRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }

  return useMemo(() => node => {
    refs.forEach(ref => ref(node));
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  refs);
}

// https://github.com/facebook/react/blob/master/packages/shared/ExecutionEnvironment.js
const canUseDOM = typeof window !== 'undefined' && typeof window.document !== 'undefined' && typeof window.document.createElement !== 'undefined';

function isWindow(element) {
  const elementString = Object.prototype.toString.call(element);
  return elementString === '[object Window]' || // In Electron context the Window object serializes to [object global]
  elementString === '[object global]';
}

function isNode(node) {
  return 'nodeType' in node;
}

function getWindow(target) {
  var _target$ownerDocument, _target$ownerDocument2;

  if (!target) {
    return window;
  }

  if (isWindow(target)) {
    return target;
  }

  if (!isNode(target)) {
    return window;
  }

  return (_target$ownerDocument = (_target$ownerDocument2 = target.ownerDocument) == null ? void 0 : _target$ownerDocument2.defaultView) != null ? _target$ownerDocument : window;
}

function isDocument(node) {
  const {
    Document
  } = getWindow(node);
  return node instanceof Document;
}

function isHTMLElement(node) {
  if (isWindow(node)) {
    return false;
  }

  return node instanceof getWindow(node).HTMLElement;
}

function isSVGElement(node) {
  return node instanceof getWindow(node).SVGElement;
}

function getOwnerDocument(target) {
  if (!target) {
    return document;
  }

  if (isWindow(target)) {
    return target.document;
  }

  if (!isNode(target)) {
    return document;
  }

  if (isDocument(target)) {
    return target;
  }

  if (isHTMLElement(target)) {
    return target.ownerDocument;
  }

  return document;
}

/**
 * A hook that resolves to useEffect on the server and useLayoutEffect on the client
 * @param callback {function} Callback function that is invoked when the dependencies of the hook change
 */

const useIsomorphicLayoutEffect = canUseDOM ? useLayoutEffect$1 : useEffect;

function useEvent(handler) {
  const handlerRef = useRef(handler);
  useIsomorphicLayoutEffect(() => {
    handlerRef.current = handler;
  });
  return useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return handlerRef.current == null ? void 0 : handlerRef.current(...args);
  }, []);
}

function useInterval() {
  const intervalRef = useRef(null);
  const set = useCallback((listener, duration) => {
    intervalRef.current = setInterval(listener, duration);
  }, []);
  const clear = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);
  return [set, clear];
}

function useLatestValue(value, dependencies) {
  if (dependencies === void 0) {
    dependencies = [value];
  }

  const valueRef = useRef(value);
  useIsomorphicLayoutEffect(() => {
    if (valueRef.current !== value) {
      valueRef.current = value;
    }
  }, dependencies);
  return valueRef;
}

function useLazyMemo(callback, dependencies) {
  const valueRef = useRef();
  return useMemo(() => {
    const newValue = callback(valueRef.current);
    valueRef.current = newValue;
    return newValue;
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [...dependencies]);
}

function useNodeRef(onChange) {
  const onChangeHandler = useEvent(onChange);
  const node = useRef(null);
  const setNodeRef = useCallback(element => {
    if (element !== node.current) {
      onChangeHandler == null ? void 0 : onChangeHandler(element, node.current);
    }

    node.current = element;
  }, //eslint-disable-next-line
  []);
  return [node, setNodeRef];
}

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

let ids = {};
function useUniqueId(prefix, value) {
  return useMemo(() => {
    if (value) {
      return value;
    }

    const id = ids[prefix] == null ? 0 : ids[prefix] + 1;
    ids[prefix] = id;
    return prefix + "-" + id;
  }, [prefix, value]);
}

function createAdjustmentFn(modifier) {
  return function (object) {
    for (var _len = arguments.length, adjustments = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      adjustments[_key - 1] = arguments[_key];
    }

    return adjustments.reduce((accumulator, adjustment) => {
      const entries = Object.entries(adjustment);

      for (const [key, valueAdjustment] of entries) {
        const value = accumulator[key];

        if (value != null) {
          accumulator[key] = value + modifier * valueAdjustment;
        }
      }

      return accumulator;
    }, { ...object
    });
  };
}

const add = /*#__PURE__*/createAdjustmentFn(1);
const subtract = /*#__PURE__*/createAdjustmentFn(-1);

function hasViewportRelativeCoordinates(event) {
  return 'clientX' in event && 'clientY' in event;
}

function isKeyboardEvent(event) {
  if (!event) {
    return false;
  }

  const {
    KeyboardEvent
  } = getWindow(event.target);
  return KeyboardEvent && event instanceof KeyboardEvent;
}

function isTouchEvent(event) {
  if (!event) {
    return false;
  }

  const {
    TouchEvent
  } = getWindow(event.target);
  return TouchEvent && event instanceof TouchEvent;
}

/**
 * Returns the normalized x and y coordinates for mouse and touch events.
 */

function getEventCoordinates(event) {
  if (isTouchEvent(event)) {
    if (event.touches && event.touches.length) {
      const {
        clientX: x,
        clientY: y
      } = event.touches[0];
      return {
        x,
        y
      };
    } else if (event.changedTouches && event.changedTouches.length) {
      const {
        clientX: x,
        clientY: y
      } = event.changedTouches[0];
      return {
        x,
        y
      };
    }
  }

  if (hasViewportRelativeCoordinates(event)) {
    return {
      x: event.clientX,
      y: event.clientY
    };
  }

  return null;
}

const CSS$1 = /*#__PURE__*/Object.freeze({
  Translate: {
    toString(transform) {
      if (!transform) {
        return;
      }

      const {
        x,
        y
      } = transform;
      return "translate3d(" + (x ? Math.round(x) : 0) + "px, " + (y ? Math.round(y) : 0) + "px, 0)";
    }

  },
  Scale: {
    toString(transform) {
      if (!transform) {
        return;
      }

      const {
        scaleX,
        scaleY
      } = transform;
      return "scaleX(" + scaleX + ") scaleY(" + scaleY + ")";
    }

  },
  Transform: {
    toString(transform) {
      if (!transform) {
        return;
      }

      return [CSS$1.Translate.toString(transform), CSS$1.Scale.toString(transform)].join(' ');
    }

  },
  Transition: {
    toString(_ref) {
      let {
        property,
        duration,
        easing
      } = _ref;
      return property + " " + duration + "ms " + easing;
    }

  }
});

const SELECTOR = 'a,frame,iframe,input:not([type=hidden]):not(:disabled),select:not(:disabled),textarea:not(:disabled),button:not(:disabled),*[tabindex]';
function findFirstFocusableNode(element) {
  if (element.matches(SELECTOR)) {
    return element;
  }

  return element.querySelector(SELECTOR);
}

const hiddenStyles = {
  display: 'none'
};
function HiddenText(_ref) {
  let {
    id,
    value
  } = _ref;
  return React__default.createElement("div", {
    id: id,
    style: hiddenStyles
  }, value);
}

const visuallyHidden = {
  position: 'fixed',
  width: 1,
  height: 1,
  margin: -1,
  border: 0,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(100%)',
  whiteSpace: 'nowrap'
};
function LiveRegion(_ref) {
  let {
    id,
    announcement
  } = _ref;
  return React__default.createElement("div", {
    id: id,
    style: visuallyHidden,
    role: "status",
    "aria-live": "assertive",
    "aria-atomic": true
  }, announcement);
}

function useAnnouncement() {
  const [announcement, setAnnouncement] = useState('');
  const announce = useCallback(value => {
    if (value != null) {
      setAnnouncement(value);
    }
  }, []);
  return {
    announce,
    announcement
  };
}

const DndMonitorContext = /*#__PURE__*/createContext(null);

function useDndMonitor(listener) {
  const registerListener = useContext(DndMonitorContext);
  useEffect(() => {
    if (!registerListener) {
      throw new Error('useDndMonitor must be used within a children of <DndContext>');
    }

    const unsubscribe = registerListener(listener);
    return unsubscribe;
  }, [listener, registerListener]);
}

function useDndMonitorProvider() {
  const [listeners] = useState(() => new Set());
  const registerListener = useCallback(listener => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, [listeners]);
  const dispatch = useCallback(_ref => {
    let {
      type,
      event
    } = _ref;
    listeners.forEach(listener => {
      var _listener$type;

      return (_listener$type = listener[type]) == null ? void 0 : _listener$type.call(listener, event);
    });
  }, [listeners]);
  return [dispatch, registerListener];
}

const defaultScreenReaderInstructions = {
  draggable: "\n    To pick up a draggable item, press the space bar.\n    While dragging, use the arrow keys to move the item.\n    Press space again to drop the item in its new position, or press escape to cancel.\n  "
};
const defaultAnnouncements = {
  onDragStart(_ref) {
    let {
      active
    } = _ref;
    return "Picked up draggable item " + active.id + ".";
  },

  onDragOver(_ref2) {
    let {
      active,
      over
    } = _ref2;

    if (over) {
      return "Draggable item " + active.id + " was moved over droppable area " + over.id + ".";
    }

    return "Draggable item " + active.id + " is no longer over a droppable area.";
  },

  onDragEnd(_ref3) {
    let {
      active,
      over
    } = _ref3;

    if (over) {
      return "Draggable item " + active.id + " was dropped over droppable area " + over.id;
    }

    return "Draggable item " + active.id + " was dropped.";
  },

  onDragCancel(_ref4) {
    let {
      active
    } = _ref4;
    return "Dragging was cancelled. Draggable item " + active.id + " was dropped.";
  }

};

function Accessibility(_ref) {
  let {
    announcements = defaultAnnouncements,
    container,
    hiddenTextDescribedById,
    screenReaderInstructions = defaultScreenReaderInstructions
  } = _ref;
  const {
    announce,
    announcement
  } = useAnnouncement();
  const liveRegionId = useUniqueId("DndLiveRegion");
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  useDndMonitor(useMemo(() => ({
    onDragStart(_ref2) {
      let {
        active
      } = _ref2;
      announce(announcements.onDragStart({
        active
      }));
    },

    onDragMove(_ref3) {
      let {
        active,
        over
      } = _ref3;

      if (announcements.onDragMove) {
        announce(announcements.onDragMove({
          active,
          over
        }));
      }
    },

    onDragOver(_ref4) {
      let {
        active,
        over
      } = _ref4;
      announce(announcements.onDragOver({
        active,
        over
      }));
    },

    onDragEnd(_ref5) {
      let {
        active,
        over
      } = _ref5;
      announce(announcements.onDragEnd({
        active,
        over
      }));
    },

    onDragCancel(_ref6) {
      let {
        active,
        over
      } = _ref6;
      announce(announcements.onDragCancel({
        active,
        over
      }));
    }

  }), [announce, announcements]));

  if (!mounted) {
    return null;
  }

  const markup = React__default.createElement(React__default.Fragment, null, React__default.createElement(HiddenText, {
    id: hiddenTextDescribedById,
    value: screenReaderInstructions.draggable
  }), React__default.createElement(LiveRegion, {
    id: liveRegionId,
    announcement: announcement
  }));
  return container ? createPortal(markup, container) : markup;
}

var Action;

(function (Action) {
  Action["DragStart"] = "dragStart";
  Action["DragMove"] = "dragMove";
  Action["DragEnd"] = "dragEnd";
  Action["DragCancel"] = "dragCancel";
  Action["DragOver"] = "dragOver";
  Action["RegisterDroppable"] = "registerDroppable";
  Action["SetDroppableDisabled"] = "setDroppableDisabled";
  Action["UnregisterDroppable"] = "unregisterDroppable";
})(Action || (Action = {}));

function noop() {}

function useSensor(sensor, options) {
  return useMemo(() => ({
    sensor,
    options: options != null ? options : {}
  }), // eslint-disable-next-line react-hooks/exhaustive-deps
  [sensor, options]);
}

const defaultCoordinates = /*#__PURE__*/Object.freeze({
  x: 0,
  y: 0
});

/**
 * Returns the distance between two points
 */
function distanceBetween(p1, p2) {
  return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
}

/**
 * Sort collisions from smallest to greatest value
 */
function sortCollisionsAsc(_ref, _ref2) {
  let {
    data: {
      value: a
    }
  } = _ref;
  let {
    data: {
      value: b
    }
  } = _ref2;
  return a - b;
}
/**
 * Sort collisions from greatest to smallest value
 */

function sortCollisionsDesc(_ref3, _ref4) {
  let {
    data: {
      value: a
    }
  } = _ref3;
  let {
    data: {
      value: b
    }
  } = _ref4;
  return b - a;
}
function getFirstCollision(collisions, property) {
  if (!collisions || collisions.length === 0) {
    return null;
  }

  const [firstCollision] = collisions;
  return property ? firstCollision[property] : firstCollision;
}

/**
 * Returns the coordinates of the center of a given ClientRect
 */

function centerOfRectangle(rect, left, top) {
  if (left === void 0) {
    left = rect.left;
  }

  if (top === void 0) {
    top = rect.top;
  }

  return {
    x: left + rect.width * 0.5,
    y: top + rect.height * 0.5
  };
}
/**
 * Returns the closest rectangles from an array of rectangles to the center of a given
 * rectangle.
 */


const closestCenter = _ref => {
  let {
    collisionRect,
    droppableRects,
    droppableContainers
  } = _ref;
  const centerRect = centerOfRectangle(collisionRect, collisionRect.left, collisionRect.top);
  const collisions = [];

  for (const droppableContainer of droppableContainers) {
    const {
      id
    } = droppableContainer;
    const rect = droppableRects.get(id);

    if (rect) {
      const distBetween = distanceBetween(centerOfRectangle(rect), centerRect);
      collisions.push({
        id,
        data: {
          droppableContainer,
          value: distBetween
        }
      });
    }
  }

  return collisions.sort(sortCollisionsAsc);
};

/**
 * Returns the intersecting rectangle area between two rectangles
 */

function getIntersectionRatio(entry, target) {
  const top = Math.max(target.top, entry.top);
  const left = Math.max(target.left, entry.left);
  const right = Math.min(target.left + target.width, entry.left + entry.width);
  const bottom = Math.min(target.top + target.height, entry.top + entry.height);
  const width = right - left;
  const height = bottom - top;

  if (left < right && top < bottom) {
    const targetArea = target.width * target.height;
    const entryArea = entry.width * entry.height;
    const intersectionArea = width * height;
    const intersectionRatio = intersectionArea / (targetArea + entryArea - intersectionArea);
    return Number(intersectionRatio.toFixed(4));
  } // Rectangles do not overlap, or overlap has an area of zero (edge/corner overlap)


  return 0;
}
/**
 * Returns the rectangles that has the greatest intersection area with a given
 * rectangle in an array of rectangles.
 */

const rectIntersection = _ref => {
  let {
    collisionRect,
    droppableRects,
    droppableContainers
  } = _ref;
  const collisions = [];

  for (const droppableContainer of droppableContainers) {
    const {
      id
    } = droppableContainer;
    const rect = droppableRects.get(id);

    if (rect) {
      const intersectionRatio = getIntersectionRatio(rect, collisionRect);

      if (intersectionRatio > 0) {
        collisions.push({
          id,
          data: {
            droppableContainer,
            value: intersectionRatio
          }
        });
      }
    }
  }

  return collisions.sort(sortCollisionsDesc);
};

function adjustScale(transform, rect1, rect2) {
  return { ...transform,
    scaleX: rect1 && rect2 ? rect1.width / rect2.width : 1,
    scaleY: rect1 && rect2 ? rect1.height / rect2.height : 1
  };
}

function getRectDelta(rect1, rect2) {
  return rect1 && rect2 ? {
    x: rect1.left - rect2.left,
    y: rect1.top - rect2.top
  } : defaultCoordinates;
}

function createRectAdjustmentFn(modifier) {
  return function adjustClientRect(rect) {
    for (var _len = arguments.length, adjustments = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      adjustments[_key - 1] = arguments[_key];
    }

    return adjustments.reduce((acc, adjustment) => ({ ...acc,
      top: acc.top + modifier * adjustment.y,
      bottom: acc.bottom + modifier * adjustment.y,
      left: acc.left + modifier * adjustment.x,
      right: acc.right + modifier * adjustment.x
    }), { ...rect
    });
  };
}
const getAdjustedRect = /*#__PURE__*/createRectAdjustmentFn(1);

function parseTransform(transform) {
  if (transform.startsWith('matrix3d(')) {
    const transformArray = transform.slice(9, -1).split(/, /);
    return {
      x: +transformArray[12],
      y: +transformArray[13],
      scaleX: +transformArray[0],
      scaleY: +transformArray[5]
    };
  } else if (transform.startsWith('matrix(')) {
    const transformArray = transform.slice(7, -1).split(/, /);
    return {
      x: +transformArray[4],
      y: +transformArray[5],
      scaleX: +transformArray[0],
      scaleY: +transformArray[3]
    };
  }

  return null;
}

function inverseTransform(rect, transform, transformOrigin) {
  const parsedTransform = parseTransform(transform);

  if (!parsedTransform) {
    return rect;
  }

  const {
    scaleX,
    scaleY,
    x: translateX,
    y: translateY
  } = parsedTransform;
  const x = rect.left - translateX - (1 - scaleX) * parseFloat(transformOrigin);
  const y = rect.top - translateY - (1 - scaleY) * parseFloat(transformOrigin.slice(transformOrigin.indexOf(' ') + 1));
  const w = scaleX ? rect.width / scaleX : rect.width;
  const h = scaleY ? rect.height / scaleY : rect.height;
  return {
    width: w,
    height: h,
    top: y,
    right: x + w,
    bottom: y + h,
    left: x
  };
}

const defaultOptions = {
  ignoreTransform: false
};
/**
 * Returns the bounding client rect of an element relative to the viewport.
 */

function getClientRect(element, options) {
  if (options === void 0) {
    options = defaultOptions;
  }

  let rect = element.getBoundingClientRect();

  if (options.ignoreTransform) {
    const {
      getComputedStyle
    } = getWindow(element);
    const {
      transform,
      transformOrigin
    } = getComputedStyle(element);

    if (transform) {
      rect = inverseTransform(rect, transform, transformOrigin);
    }
  }

  const {
    top,
    left,
    width,
    height,
    bottom,
    right
  } = rect;
  return {
    top,
    left,
    width,
    height,
    bottom,
    right
  };
}
/**
 * Returns the bounding client rect of an element relative to the viewport.
 *
 * @remarks
 * The ClientRect returned by this method does not take into account transforms
 * applied to the element it measures.
 *
 */

function getTransformAgnosticClientRect(element) {
  return getClientRect(element, {
    ignoreTransform: true
  });
}

function getWindowClientRect(element) {
  const width = element.innerWidth;
  const height = element.innerHeight;
  return {
    top: 0,
    left: 0,
    right: width,
    bottom: height,
    width,
    height
  };
}

function isFixed(node, computedStyle) {
  if (computedStyle === void 0) {
    computedStyle = getWindow(node).getComputedStyle(node);
  }

  return computedStyle.position === 'fixed';
}

function isScrollable(element, computedStyle) {
  if (computedStyle === void 0) {
    computedStyle = getWindow(element).getComputedStyle(element);
  }

  const overflowRegex = /(auto|scroll|overlay)/;
  const properties = ['overflow', 'overflowX', 'overflowY'];
  return properties.some(property => {
    const value = computedStyle[property];
    return typeof value === 'string' ? overflowRegex.test(value) : false;
  });
}

function getScrollableAncestors(element, limit) {
  const scrollParents = [];

  function findScrollableAncestors(node) {
    if (limit != null && scrollParents.length >= limit) {
      return scrollParents;
    }

    if (!node) {
      return scrollParents;
    }

    if (isDocument(node) && node.scrollingElement != null && !scrollParents.includes(node.scrollingElement)) {
      scrollParents.push(node.scrollingElement);
      return scrollParents;
    }

    if (!isHTMLElement(node) || isSVGElement(node)) {
      return scrollParents;
    }

    if (scrollParents.includes(node)) {
      return scrollParents;
    }

    const {
      getComputedStyle
    } = getWindow(node);
    const computedStyle = getComputedStyle(node);

    if (node !== element) {
      if (isScrollable(node, computedStyle)) {
        scrollParents.push(node);
      }
    }

    if (isFixed(node, computedStyle)) {
      return scrollParents;
    }

    return findScrollableAncestors(node.parentNode);
  }

  if (!element) {
    return scrollParents;
  }

  return findScrollableAncestors(element);
}
function getFirstScrollableAncestor(node) {
  const [firstScrollableAncestor] = getScrollableAncestors(node, 1);
  return firstScrollableAncestor != null ? firstScrollableAncestor : null;
}

function getScrollableElement(element) {
  if (!canUseDOM || !element) {
    return null;
  }

  if (isWindow(element)) {
    return element;
  }

  if (!isNode(element)) {
    return null;
  }

  if (isDocument(element) || element === getOwnerDocument(element).scrollingElement) {
    return window;
  }

  if (isHTMLElement(element)) {
    return element;
  }

  return null;
}

function getScrollXCoordinate(element) {
  if (isWindow(element)) {
    return element.scrollX;
  }

  return element.scrollLeft;
}
function getScrollYCoordinate(element) {
  if (isWindow(element)) {
    return element.scrollY;
  }

  return element.scrollTop;
}
function getScrollCoordinates(element) {
  return {
    x: getScrollXCoordinate(element),
    y: getScrollYCoordinate(element)
  };
}

var Direction;

(function (Direction) {
  Direction[Direction["Forward"] = 1] = "Forward";
  Direction[Direction["Backward"] = -1] = "Backward";
})(Direction || (Direction = {}));

function isDocumentScrollingElement(element) {
  if (!canUseDOM || !element) {
    return false;
  }

  return element === document.scrollingElement;
}

function getScrollPosition(scrollingContainer) {
  const minScroll = {
    x: 0,
    y: 0
  };
  const dimensions = isDocumentScrollingElement(scrollingContainer) ? {
    height: window.innerHeight,
    width: window.innerWidth
  } : {
    height: scrollingContainer.clientHeight,
    width: scrollingContainer.clientWidth
  };
  const maxScroll = {
    x: scrollingContainer.scrollWidth - dimensions.width,
    y: scrollingContainer.scrollHeight - dimensions.height
  };
  const isTop = scrollingContainer.scrollTop <= minScroll.y;
  const isLeft = scrollingContainer.scrollLeft <= minScroll.x;
  const isBottom = scrollingContainer.scrollTop >= maxScroll.y;
  const isRight = scrollingContainer.scrollLeft >= maxScroll.x;
  return {
    isTop,
    isLeft,
    isBottom,
    isRight,
    maxScroll,
    minScroll
  };
}

const defaultThreshold = {
  x: 0.2,
  y: 0.2
};
function getScrollDirectionAndSpeed(scrollContainer, scrollContainerRect, _ref, acceleration, thresholdPercentage) {
  let {
    top,
    left,
    right,
    bottom
  } = _ref;

  if (acceleration === void 0) {
    acceleration = 10;
  }

  if (thresholdPercentage === void 0) {
    thresholdPercentage = defaultThreshold;
  }

  const {
    isTop,
    isBottom,
    isLeft,
    isRight
  } = getScrollPosition(scrollContainer);
  const direction = {
    x: 0,
    y: 0
  };
  const speed = {
    x: 0,
    y: 0
  };
  const threshold = {
    height: scrollContainerRect.height * thresholdPercentage.y,
    width: scrollContainerRect.width * thresholdPercentage.x
  };

  if (!isTop && top <= scrollContainerRect.top + threshold.height) {
    // Scroll Up
    direction.y = Direction.Backward;
    speed.y = acceleration * Math.abs((scrollContainerRect.top + threshold.height - top) / threshold.height);
  } else if (!isBottom && bottom >= scrollContainerRect.bottom - threshold.height) {
    // Scroll Down
    direction.y = Direction.Forward;
    speed.y = acceleration * Math.abs((scrollContainerRect.bottom - threshold.height - bottom) / threshold.height);
  }

  if (!isRight && right >= scrollContainerRect.right - threshold.width) {
    // Scroll Right
    direction.x = Direction.Forward;
    speed.x = acceleration * Math.abs((scrollContainerRect.right - threshold.width - right) / threshold.width);
  } else if (!isLeft && left <= scrollContainerRect.left + threshold.width) {
    // Scroll Left
    direction.x = Direction.Backward;
    speed.x = acceleration * Math.abs((scrollContainerRect.left + threshold.width - left) / threshold.width);
  }

  return {
    direction,
    speed
  };
}

function getScrollElementRect(element) {
  if (element === document.scrollingElement) {
    const {
      innerWidth,
      innerHeight
    } = window;
    return {
      top: 0,
      left: 0,
      right: innerWidth,
      bottom: innerHeight,
      width: innerWidth,
      height: innerHeight
    };
  }

  const {
    top,
    left,
    right,
    bottom
  } = element.getBoundingClientRect();
  return {
    top,
    left,
    right,
    bottom,
    width: element.clientWidth,
    height: element.clientHeight
  };
}

function getScrollOffsets(scrollableAncestors) {
  return scrollableAncestors.reduce((acc, node) => {
    return add(acc, getScrollCoordinates(node));
  }, defaultCoordinates);
}
function getScrollXOffset(scrollableAncestors) {
  return scrollableAncestors.reduce((acc, node) => {
    return acc + getScrollXCoordinate(node);
  }, 0);
}
function getScrollYOffset(scrollableAncestors) {
  return scrollableAncestors.reduce((acc, node) => {
    return acc + getScrollYCoordinate(node);
  }, 0);
}

function scrollIntoViewIfNeeded(element, measure) {
  if (measure === void 0) {
    measure = getClientRect;
  }

  if (!element) {
    return;
  }

  const {
    top,
    left,
    bottom,
    right
  } = measure(element);
  const firstScrollableAncestor = getFirstScrollableAncestor(element);

  if (!firstScrollableAncestor) {
    return;
  }

  if (bottom <= 0 || right <= 0 || top >= window.innerHeight || left >= window.innerWidth) {
    element.scrollIntoView({
      block: 'center',
      inline: 'center'
    });
  }
}

const properties = [['x', ['left', 'right'], getScrollXOffset], ['y', ['top', 'bottom'], getScrollYOffset]];
class Rect {
  constructor(rect, element) {
    this.rect = void 0;
    this.width = void 0;
    this.height = void 0;
    this.top = void 0;
    this.bottom = void 0;
    this.right = void 0;
    this.left = void 0;
    const scrollableAncestors = getScrollableAncestors(element);
    const scrollOffsets = getScrollOffsets(scrollableAncestors);
    this.rect = { ...rect
    };
    this.width = rect.width;
    this.height = rect.height;

    for (const [axis, keys, getScrollOffset] of properties) {
      for (const key of keys) {
        Object.defineProperty(this, key, {
          get: () => {
            const currentOffsets = getScrollOffset(scrollableAncestors);
            const scrollOffsetsDeltla = scrollOffsets[axis] - currentOffsets;
            return this.rect[key] + scrollOffsetsDeltla;
          },
          enumerable: true
        });
      }
    }

    Object.defineProperty(this, 'rect', {
      enumerable: false
    });
  }

}

class Listeners {
  constructor(target) {
    this.target = void 0;
    this.listeners = [];

    this.removeAll = () => {
      this.listeners.forEach(listener => {
        var _this$target;

        return (_this$target = this.target) == null ? void 0 : _this$target.removeEventListener(...listener);
      });
    };

    this.target = target;
  }

  add(eventName, handler, options) {
    var _this$target2;

    (_this$target2 = this.target) == null ? void 0 : _this$target2.addEventListener(eventName, handler, options);
    this.listeners.push([eventName, handler, options]);
  }

}

function getEventListenerTarget(target) {
  // If the `event.target` element is removed from the document events will still be targeted
  // at it, and hence won't always bubble up to the window or document anymore.
  // If there is any risk of an element being removed while it is being dragged,
  // the best practice is to attach the event listeners directly to the target.
  // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget
  const {
    EventTarget
  } = getWindow(target);
  return target instanceof EventTarget ? target : getOwnerDocument(target);
}

function hasExceededDistance(delta, measurement) {
  const dx = Math.abs(delta.x);
  const dy = Math.abs(delta.y);

  if (typeof measurement === 'number') {
    return Math.sqrt(dx ** 2 + dy ** 2) > measurement;
  }

  if ('x' in measurement && 'y' in measurement) {
    return dx > measurement.x && dy > measurement.y;
  }

  if ('x' in measurement) {
    return dx > measurement.x;
  }

  if ('y' in measurement) {
    return dy > measurement.y;
  }

  return false;
}

var EventName;

(function (EventName) {
  EventName["Click"] = "click";
  EventName["DragStart"] = "dragstart";
  EventName["Keydown"] = "keydown";
  EventName["ContextMenu"] = "contextmenu";
  EventName["Resize"] = "resize";
  EventName["SelectionChange"] = "selectionchange";
  EventName["VisibilityChange"] = "visibilitychange";
})(EventName || (EventName = {}));

function preventDefault(event) {
  event.preventDefault();
}
function stopPropagation(event) {
  event.stopPropagation();
}

var KeyboardCode;

(function (KeyboardCode) {
  KeyboardCode["Space"] = "Space";
  KeyboardCode["Down"] = "ArrowDown";
  KeyboardCode["Right"] = "ArrowRight";
  KeyboardCode["Left"] = "ArrowLeft";
  KeyboardCode["Up"] = "ArrowUp";
  KeyboardCode["Esc"] = "Escape";
  KeyboardCode["Enter"] = "Enter";
})(KeyboardCode || (KeyboardCode = {}));

const defaultKeyboardCodes = {
  start: [KeyboardCode.Space, KeyboardCode.Enter],
  cancel: [KeyboardCode.Esc],
  end: [KeyboardCode.Space, KeyboardCode.Enter]
};
const defaultKeyboardCoordinateGetter = (event, _ref) => {
  let {
    currentCoordinates
  } = _ref;

  switch (event.code) {
    case KeyboardCode.Right:
      return { ...currentCoordinates,
        x: currentCoordinates.x + 25
      };

    case KeyboardCode.Left:
      return { ...currentCoordinates,
        x: currentCoordinates.x - 25
      };

    case KeyboardCode.Down:
      return { ...currentCoordinates,
        y: currentCoordinates.y + 25
      };

    case KeyboardCode.Up:
      return { ...currentCoordinates,
        y: currentCoordinates.y - 25
      };
  }

  return undefined;
};

class KeyboardSensor {
  constructor(props) {
    this.props = void 0;
    this.autoScrollEnabled = false;
    this.referenceCoordinates = void 0;
    this.listeners = void 0;
    this.windowListeners = void 0;
    this.props = props;
    const {
      event: {
        target
      }
    } = props;
    this.props = props;
    this.listeners = new Listeners(getOwnerDocument(target));
    this.windowListeners = new Listeners(getWindow(target));
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.attach();
  }

  attach() {
    this.handleStart();
    this.windowListeners.add(EventName.Resize, this.handleCancel);
    this.windowListeners.add(EventName.VisibilityChange, this.handleCancel);
    setTimeout(() => this.listeners.add(EventName.Keydown, this.handleKeyDown));
  }

  handleStart() {
    const {
      activeNode,
      onStart
    } = this.props;
    const node = activeNode.node.current;

    if (node) {
      scrollIntoViewIfNeeded(node);
    }

    onStart(defaultCoordinates);
  }

  handleKeyDown(event) {
    if (isKeyboardEvent(event)) {
      const {
        active,
        context,
        options
      } = this.props;
      const {
        keyboardCodes = defaultKeyboardCodes,
        coordinateGetter = defaultKeyboardCoordinateGetter,
        scrollBehavior = 'smooth'
      } = options;
      const {
        code
      } = event;

      if (keyboardCodes.end.includes(code)) {
        this.handleEnd(event);
        return;
      }

      if (keyboardCodes.cancel.includes(code)) {
        this.handleCancel(event);
        return;
      }

      const {
        collisionRect
      } = context.current;
      const currentCoordinates = collisionRect ? {
        x: collisionRect.left,
        y: collisionRect.top
      } : defaultCoordinates;

      if (!this.referenceCoordinates) {
        this.referenceCoordinates = currentCoordinates;
      }

      const newCoordinates = coordinateGetter(event, {
        active,
        context: context.current,
        currentCoordinates
      });

      if (newCoordinates) {
        const coordinatesDelta = subtract(newCoordinates, currentCoordinates);
        const scrollDelta = {
          x: 0,
          y: 0
        };
        const {
          scrollableAncestors
        } = context.current;

        for (const scrollContainer of scrollableAncestors) {
          const direction = event.code;
          const {
            isTop,
            isRight,
            isLeft,
            isBottom,
            maxScroll,
            minScroll
          } = getScrollPosition(scrollContainer);
          const scrollElementRect = getScrollElementRect(scrollContainer);
          const clampedCoordinates = {
            x: Math.min(direction === KeyboardCode.Right ? scrollElementRect.right - scrollElementRect.width / 2 : scrollElementRect.right, Math.max(direction === KeyboardCode.Right ? scrollElementRect.left : scrollElementRect.left + scrollElementRect.width / 2, newCoordinates.x)),
            y: Math.min(direction === KeyboardCode.Down ? scrollElementRect.bottom - scrollElementRect.height / 2 : scrollElementRect.bottom, Math.max(direction === KeyboardCode.Down ? scrollElementRect.top : scrollElementRect.top + scrollElementRect.height / 2, newCoordinates.y))
          };
          const canScrollX = direction === KeyboardCode.Right && !isRight || direction === KeyboardCode.Left && !isLeft;
          const canScrollY = direction === KeyboardCode.Down && !isBottom || direction === KeyboardCode.Up && !isTop;

          if (canScrollX && clampedCoordinates.x !== newCoordinates.x) {
            const newScrollCoordinates = scrollContainer.scrollLeft + coordinatesDelta.x;
            const canScrollToNewCoordinates = direction === KeyboardCode.Right && newScrollCoordinates <= maxScroll.x || direction === KeyboardCode.Left && newScrollCoordinates >= minScroll.x;

            if (canScrollToNewCoordinates && !coordinatesDelta.y) {
              // We don't need to update coordinates, the scroll adjustment alone will trigger
              // logic to auto-detect the new container we are over
              scrollContainer.scrollTo({
                left: newScrollCoordinates,
                behavior: scrollBehavior
              });
              return;
            }

            if (canScrollToNewCoordinates) {
              scrollDelta.x = scrollContainer.scrollLeft - newScrollCoordinates;
            } else {
              scrollDelta.x = direction === KeyboardCode.Right ? scrollContainer.scrollLeft - maxScroll.x : scrollContainer.scrollLeft - minScroll.x;
            }

            if (scrollDelta.x) {
              scrollContainer.scrollBy({
                left: -scrollDelta.x,
                behavior: scrollBehavior
              });
            }

            break;
          } else if (canScrollY && clampedCoordinates.y !== newCoordinates.y) {
            const newScrollCoordinates = scrollContainer.scrollTop + coordinatesDelta.y;
            const canScrollToNewCoordinates = direction === KeyboardCode.Down && newScrollCoordinates <= maxScroll.y || direction === KeyboardCode.Up && newScrollCoordinates >= minScroll.y;

            if (canScrollToNewCoordinates && !coordinatesDelta.x) {
              // We don't need to update coordinates, the scroll adjustment alone will trigger
              // logic to auto-detect the new container we are over
              scrollContainer.scrollTo({
                top: newScrollCoordinates,
                behavior: scrollBehavior
              });
              return;
            }

            if (canScrollToNewCoordinates) {
              scrollDelta.y = scrollContainer.scrollTop - newScrollCoordinates;
            } else {
              scrollDelta.y = direction === KeyboardCode.Down ? scrollContainer.scrollTop - maxScroll.y : scrollContainer.scrollTop - minScroll.y;
            }

            if (scrollDelta.y) {
              scrollContainer.scrollBy({
                top: -scrollDelta.y,
                behavior: scrollBehavior
              });
            }

            break;
          }
        }

        this.handleMove(event, add(subtract(newCoordinates, this.referenceCoordinates), scrollDelta));
      }
    }
  }

  handleMove(event, coordinates) {
    const {
      onMove
    } = this.props;
    event.preventDefault();
    onMove(coordinates);
  }

  handleEnd(event) {
    const {
      onEnd
    } = this.props;
    event.preventDefault();
    this.detach();
    onEnd();
  }

  handleCancel(event) {
    const {
      onCancel
    } = this.props;
    event.preventDefault();
    this.detach();
    onCancel();
  }

  detach() {
    this.listeners.removeAll();
    this.windowListeners.removeAll();
  }

}
KeyboardSensor.activators = [{
  eventName: 'onKeyDown',
  handler: (event, _ref, _ref2) => {
    let {
      keyboardCodes = defaultKeyboardCodes,
      onActivation
    } = _ref;
    let {
      active
    } = _ref2;
    const {
      code
    } = event.nativeEvent;

    if (keyboardCodes.start.includes(code)) {
      const activator = active.activatorNode.current;

      if (activator && event.target !== activator) {
        return false;
      }

      event.preventDefault();
      onActivation == null ? void 0 : onActivation({
        event: event.nativeEvent
      });
      return true;
    }

    return false;
  }
}];

function isDistanceConstraint(constraint) {
  return Boolean(constraint && 'distance' in constraint);
}

function isDelayConstraint(constraint) {
  return Boolean(constraint && 'delay' in constraint);
}

class AbstractPointerSensor {
  constructor(props, events, listenerTarget) {
    var _getEventCoordinates;

    if (listenerTarget === void 0) {
      listenerTarget = getEventListenerTarget(props.event.target);
    }

    this.props = void 0;
    this.events = void 0;
    this.autoScrollEnabled = true;
    this.document = void 0;
    this.activated = false;
    this.initialCoordinates = void 0;
    this.timeoutId = null;
    this.listeners = void 0;
    this.documentListeners = void 0;
    this.windowListeners = void 0;
    this.props = props;
    this.events = events;
    const {
      event
    } = props;
    const {
      target
    } = event;
    this.props = props;
    this.events = events;
    this.document = getOwnerDocument(target);
    this.documentListeners = new Listeners(this.document);
    this.listeners = new Listeners(listenerTarget);
    this.windowListeners = new Listeners(getWindow(target));
    this.initialCoordinates = (_getEventCoordinates = getEventCoordinates(event)) != null ? _getEventCoordinates : defaultCoordinates;
    this.handleStart = this.handleStart.bind(this);
    this.handleMove = this.handleMove.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.removeTextSelection = this.removeTextSelection.bind(this);
    this.attach();
  }

  attach() {
    const {
      events,
      props: {
        options: {
          activationConstraint
        }
      }
    } = this;
    this.listeners.add(events.move.name, this.handleMove, {
      passive: false
    });
    this.listeners.add(events.end.name, this.handleEnd);
    this.windowListeners.add(EventName.Resize, this.handleCancel);
    this.windowListeners.add(EventName.DragStart, preventDefault);
    this.windowListeners.add(EventName.VisibilityChange, this.handleCancel);
    this.windowListeners.add(EventName.ContextMenu, preventDefault);
    this.documentListeners.add(EventName.Keydown, this.handleKeydown);

    if (activationConstraint) {
      if (isDistanceConstraint(activationConstraint)) {
        return;
      }

      if (isDelayConstraint(activationConstraint)) {
        this.timeoutId = setTimeout(this.handleStart, activationConstraint.delay);
        return;
      }
    }

    this.handleStart();
  }

  detach() {
    this.listeners.removeAll();
    this.windowListeners.removeAll(); // Wait until the next event loop before removing document listeners
    // This is necessary because we listen for `click` and `selection` events on the document

    setTimeout(this.documentListeners.removeAll, 50);

    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  handleStart() {
    const {
      initialCoordinates
    } = this;
    const {
      onStart
    } = this.props;

    if (initialCoordinates) {
      this.activated = true; // Stop propagation of click events once activation constraints are met

      this.documentListeners.add(EventName.Click, stopPropagation, {
        capture: true
      }); // Remove any text selection from the document

      this.removeTextSelection(); // Prevent further text selection while dragging

      this.documentListeners.add(EventName.SelectionChange, this.removeTextSelection);
      onStart(initialCoordinates);
    }
  }

  handleMove(event) {
    var _getEventCoordinates2;

    const {
      activated,
      initialCoordinates,
      props
    } = this;
    const {
      onMove,
      options: {
        activationConstraint
      }
    } = props;

    if (!initialCoordinates) {
      return;
    }

    const coordinates = (_getEventCoordinates2 = getEventCoordinates(event)) != null ? _getEventCoordinates2 : defaultCoordinates;
    const delta = subtract(initialCoordinates, coordinates);

    if (!activated && activationConstraint) {
      // Constraint validation
      if (isDelayConstraint(activationConstraint)) {
        if (hasExceededDistance(delta, activationConstraint.tolerance)) {
          return this.handleCancel();
        }

        return;
      }

      if (isDistanceConstraint(activationConstraint)) {
        if (activationConstraint.tolerance != null && hasExceededDistance(delta, activationConstraint.tolerance)) {
          return this.handleCancel();
        }

        if (hasExceededDistance(delta, activationConstraint.distance)) {
          return this.handleStart();
        }

        return;
      }
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    onMove(coordinates);
  }

  handleEnd() {
    const {
      onEnd
    } = this.props;
    this.detach();
    onEnd();
  }

  handleCancel() {
    const {
      onCancel
    } = this.props;
    this.detach();
    onCancel();
  }

  handleKeydown(event) {
    if (event.code === KeyboardCode.Esc) {
      this.handleCancel();
    }
  }

  removeTextSelection() {
    var _this$document$getSel;

    (_this$document$getSel = this.document.getSelection()) == null ? void 0 : _this$document$getSel.removeAllRanges();
  }

}

const events = {
  move: {
    name: 'pointermove'
  },
  end: {
    name: 'pointerup'
  }
};
class PointerSensor extends AbstractPointerSensor {
  constructor(props) {
    const {
      event
    } = props; // Pointer events stop firing if the target is unmounted while dragging
    // Therefore we attach listeners to the owner document instead

    const listenerTarget = getOwnerDocument(event.target);
    super(props, events, listenerTarget);
  }

}
PointerSensor.activators = [{
  eventName: 'onPointerDown',
  handler: (_ref, _ref2) => {
    let {
      nativeEvent: event
    } = _ref;
    let {
      onActivation
    } = _ref2;

    if (!event.isPrimary || event.button !== 0) {
      return false;
    }

    onActivation == null ? void 0 : onActivation({
      event
    });
    return true;
  }
}];

const events$1 = {
  move: {
    name: 'mousemove'
  },
  end: {
    name: 'mouseup'
  }
};
var MouseButton;

(function (MouseButton) {
  MouseButton[MouseButton["RightClick"] = 2] = "RightClick";
})(MouseButton || (MouseButton = {}));

class MouseSensor extends AbstractPointerSensor {
  constructor(props) {
    super(props, events$1, getOwnerDocument(props.event.target));
  }

}
MouseSensor.activators = [{
  eventName: 'onMouseDown',
  handler: (_ref, _ref2) => {
    let {
      nativeEvent: event
    } = _ref;
    let {
      onActivation
    } = _ref2;

    if (event.button === MouseButton.RightClick) {
      return false;
    }

    onActivation == null ? void 0 : onActivation({
      event
    });
    return true;
  }
}];

const events$2 = {
  move: {
    name: 'touchmove'
  },
  end: {
    name: 'touchend'
  }
};
class TouchSensor extends AbstractPointerSensor {
  constructor(props) {
    super(props, events$2);
  }

  static setup() {
    // Adding a non-capture and non-passive `touchmove` listener in order
    // to force `event.preventDefault()` calls to work in dynamically added
    // touchmove event handlers. This is required for iOS Safari.
    window.addEventListener(events$2.move.name, noop, {
      capture: false,
      passive: false
    });
    return function teardown() {
      window.removeEventListener(events$2.move.name, noop);
    }; // We create a new handler because the teardown function of another sensor
    // could remove our event listener if we use a referentially equal listener.

    function noop() {}
  }

}
TouchSensor.activators = [{
  eventName: 'onTouchStart',
  handler: (_ref, _ref2) => {
    let {
      nativeEvent: event
    } = _ref;
    let {
      onActivation
    } = _ref2;
    const {
      touches
    } = event;

    if (touches.length > 1) {
      return false;
    }

    onActivation == null ? void 0 : onActivation({
      event
    });
    return true;
  }
}];

var AutoScrollActivator;

(function (AutoScrollActivator) {
  AutoScrollActivator[AutoScrollActivator["Pointer"] = 0] = "Pointer";
  AutoScrollActivator[AutoScrollActivator["DraggableRect"] = 1] = "DraggableRect";
})(AutoScrollActivator || (AutoScrollActivator = {}));

var TraversalOrder;

(function (TraversalOrder) {
  TraversalOrder[TraversalOrder["TreeOrder"] = 0] = "TreeOrder";
  TraversalOrder[TraversalOrder["ReversedTreeOrder"] = 1] = "ReversedTreeOrder";
})(TraversalOrder || (TraversalOrder = {}));

function useAutoScroller(_ref) {
  let {
    acceleration,
    activator = AutoScrollActivator.Pointer,
    canScroll,
    draggingRect,
    enabled,
    interval = 5,
    order = TraversalOrder.TreeOrder,
    pointerCoordinates,
    scrollableAncestors,
    scrollableAncestorRects,
    delta,
    threshold
  } = _ref;
  const scrollIntent = useScrollIntent({
    delta,
    disabled: !enabled
  });
  const [setAutoScrollInterval, clearAutoScrollInterval] = useInterval();
  const scrollSpeed = useRef({
    x: 0,
    y: 0
  });
  const scrollDirection = useRef({
    x: 0,
    y: 0
  });
  const rect = useMemo(() => {
    switch (activator) {
      case AutoScrollActivator.Pointer:
        return pointerCoordinates ? {
          top: pointerCoordinates.y,
          bottom: pointerCoordinates.y,
          left: pointerCoordinates.x,
          right: pointerCoordinates.x
        } : null;

      case AutoScrollActivator.DraggableRect:
        return draggingRect;
    }
  }, [activator, draggingRect, pointerCoordinates]);
  const scrollContainerRef = useRef(null);
  const autoScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      return;
    }

    const scrollLeft = scrollSpeed.current.x * scrollDirection.current.x;
    const scrollTop = scrollSpeed.current.y * scrollDirection.current.y;
    scrollContainer.scrollBy(scrollLeft, scrollTop);
  }, []);
  const sortedScrollableAncestors = useMemo(() => order === TraversalOrder.TreeOrder ? [...scrollableAncestors].reverse() : scrollableAncestors, [order, scrollableAncestors]);
  useEffect(() => {
    if (!enabled || !scrollableAncestors.length || !rect) {
      clearAutoScrollInterval();
      return;
    }

    for (const scrollContainer of sortedScrollableAncestors) {
      if ((canScroll == null ? void 0 : canScroll(scrollContainer)) === false) {
        continue;
      }

      const index = scrollableAncestors.indexOf(scrollContainer);
      const scrollContainerRect = scrollableAncestorRects[index];

      if (!scrollContainerRect) {
        continue;
      }

      const {
        direction,
        speed
      } = getScrollDirectionAndSpeed(scrollContainer, scrollContainerRect, rect, acceleration, threshold);

      for (const axis of ['x', 'y']) {
        if (!scrollIntent[axis][direction[axis]]) {
          speed[axis] = 0;
          direction[axis] = 0;
        }
      }

      if (speed.x > 0 || speed.y > 0) {
        clearAutoScrollInterval();
        scrollContainerRef.current = scrollContainer;
        setAutoScrollInterval(autoScroll, interval);
        scrollSpeed.current = speed;
        scrollDirection.current = direction;
        return;
      }
    }

    scrollSpeed.current = {
      x: 0,
      y: 0
    };
    scrollDirection.current = {
      x: 0,
      y: 0
    };
    clearAutoScrollInterval();
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [acceleration, autoScroll, canScroll, clearAutoScrollInterval, enabled, interval, // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(rect), // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(scrollIntent), setAutoScrollInterval, scrollableAncestors, sortedScrollableAncestors, scrollableAncestorRects, // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(threshold)]);
}
const defaultScrollIntent = {
  x: {
    [Direction.Backward]: false,
    [Direction.Forward]: false
  },
  y: {
    [Direction.Backward]: false,
    [Direction.Forward]: false
  }
};

function useScrollIntent(_ref2) {
  let {
    delta,
    disabled
  } = _ref2;
  const previousDelta = usePrevious(delta);
  return useLazyMemo(previousIntent => {
    if (disabled || !previousDelta || !previousIntent) {
      // Reset scroll intent tracking when auto-scrolling is disabled
      return defaultScrollIntent;
    }

    const direction = {
      x: Math.sign(delta.x - previousDelta.x),
      y: Math.sign(delta.y - previousDelta.y)
    }; // Keep track of the user intent to scroll in each direction for both axis

    return {
      x: {
        [Direction.Backward]: previousIntent.x[Direction.Backward] || direction.x === -1,
        [Direction.Forward]: previousIntent.x[Direction.Forward] || direction.x === 1
      },
      y: {
        [Direction.Backward]: previousIntent.y[Direction.Backward] || direction.y === -1,
        [Direction.Forward]: previousIntent.y[Direction.Forward] || direction.y === 1
      }
    };
  }, [disabled, delta, previousDelta]);
}

function useCachedNode(draggableNodes, id) {
  const draggableNode = id !== null ? draggableNodes.get(id) : undefined;
  const node = draggableNode ? draggableNode.node.current : null;
  return useLazyMemo(cachedNode => {
    var _ref;

    if (id === null) {
      return null;
    } // In some cases, the draggable node can unmount while dragging
    // This is the case for virtualized lists. In those situations,
    // we fall back to the last known value for that node.


    return (_ref = node != null ? node : cachedNode) != null ? _ref : null;
  }, [node, id]);
}

function useCombineActivators(sensors, getSyntheticHandler) {
  return useMemo(() => sensors.reduce((accumulator, sensor) => {
    const {
      sensor: Sensor
    } = sensor;
    const sensorActivators = Sensor.activators.map(activator => ({
      eventName: activator.eventName,
      handler: getSyntheticHandler(activator.handler, sensor)
    }));
    return [...accumulator, ...sensorActivators];
  }, []), [sensors, getSyntheticHandler]);
}

var MeasuringStrategy;

(function (MeasuringStrategy) {
  MeasuringStrategy[MeasuringStrategy["Always"] = 0] = "Always";
  MeasuringStrategy[MeasuringStrategy["BeforeDragging"] = 1] = "BeforeDragging";
  MeasuringStrategy[MeasuringStrategy["WhileDragging"] = 2] = "WhileDragging";
})(MeasuringStrategy || (MeasuringStrategy = {}));

var MeasuringFrequency;

(function (MeasuringFrequency) {
  MeasuringFrequency["Optimized"] = "optimized";
})(MeasuringFrequency || (MeasuringFrequency = {}));

const defaultValue = /*#__PURE__*/new Map();
function useDroppableMeasuring(containers, _ref) {
  let {
    dragging,
    dependencies,
    config
  } = _ref;
  const [containerIdsScheduledForMeasurement, setContainerIdsScheduledForMeasurement] = useState(null);
  const measuringScheduled = containerIdsScheduledForMeasurement != null;
  const {
    frequency,
    measure,
    strategy
  } = config;
  const containersRef = useRef(containers);
  const disabled = isDisabled();
  const disabledRef = useLatestValue(disabled);
  const measureDroppableContainers = useCallback(function (ids) {
    if (ids === void 0) {
      ids = [];
    }

    if (disabledRef.current) {
      return;
    }

    setContainerIdsScheduledForMeasurement(value => value ? value.concat(ids) : ids);
  }, [disabledRef]);
  const timeoutId = useRef(null);
  const droppableRects = useLazyMemo(previousValue => {
    if (disabled && !dragging) {
      return defaultValue;
    }

    const ids = containerIdsScheduledForMeasurement;

    if (!previousValue || previousValue === defaultValue || containersRef.current !== containers || ids != null) {
      const map = new Map();

      for (let container of containers) {
        if (!container) {
          continue;
        }

        if (ids && ids.length > 0 && !ids.includes(container.id) && container.rect.current) {
          // This container does not need to be re-measured
          map.set(container.id, container.rect.current);
          continue;
        }

        const node = container.node.current;
        const rect = node ? new Rect(measure(node), node) : null;
        container.rect.current = rect;

        if (rect) {
          map.set(container.id, rect);
        }
      }

      return map;
    }

    return previousValue;
  }, [containers, containerIdsScheduledForMeasurement, dragging, disabled, measure]);
  useEffect(() => {
    containersRef.current = containers;
  }, [containers]);
  useEffect(() => {
    if (disabled) {
      return;
    }

    requestAnimationFrame(() => measureDroppableContainers());
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [dragging, disabled]);
  useEffect(() => {
    if (measuringScheduled) {
      setContainerIdsScheduledForMeasurement(null);
    }
  }, [measuringScheduled]);
  useEffect(() => {
    if (disabled || typeof frequency !== 'number' || timeoutId.current !== null) {
      return;
    }

    timeoutId.current = setTimeout(() => {
      measureDroppableContainers();
      timeoutId.current = null;
    }, frequency);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [frequency, disabled, measureDroppableContainers, ...dependencies]);
  return {
    droppableRects,
    measureDroppableContainers,
    measuringScheduled
  };

  function isDisabled() {
    switch (strategy) {
      case MeasuringStrategy.Always:
        return false;

      case MeasuringStrategy.BeforeDragging:
        return dragging;

      default:
        return !dragging;
    }
  }
}

function useInitialValue(value, computeFn) {
  return useLazyMemo(previousValue => {
    if (!value) {
      return null;
    }

    if (previousValue) {
      return previousValue;
    }

    return typeof computeFn === 'function' ? computeFn(value) : value;
  }, [computeFn, value]);
}

function useInitialRect(node, measure) {
  return useInitialValue(node, measure);
}

/**
 * Returns a new MutationObserver instance.
 * If `MutationObserver` is undefined in the execution environment, returns `undefined`.
 */

function useMutationObserver(_ref) {
  let {
    callback,
    disabled
  } = _ref;
  const handleMutations = useEvent(callback);
  const mutationObserver = useMemo(() => {
    if (disabled || typeof window === 'undefined' || typeof window.MutationObserver === 'undefined') {
      return undefined;
    }

    const {
      MutationObserver
    } = window;
    return new MutationObserver(handleMutations);
  }, [handleMutations, disabled]);
  useEffect(() => {
    return () => mutationObserver == null ? void 0 : mutationObserver.disconnect();
  }, [mutationObserver]);
  return mutationObserver;
}

/**
 * Returns a new ResizeObserver instance bound to the `onResize` callback.
 * If `ResizeObserver` is undefined in the execution environment, returns `undefined`.
 */

function useResizeObserver(_ref) {
  let {
    callback,
    disabled
  } = _ref;
  const handleResize = useEvent(callback);
  const resizeObserver = useMemo(() => {
    if (disabled || typeof window === 'undefined' || typeof window.ResizeObserver === 'undefined') {
      return undefined;
    }

    const {
      ResizeObserver
    } = window;
    return new ResizeObserver(handleResize);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [disabled]);
  useEffect(() => {
    return () => resizeObserver == null ? void 0 : resizeObserver.disconnect();
  }, [resizeObserver]);
  return resizeObserver;
}

function defaultMeasure(element) {
  return new Rect(getClientRect(element), element);
}

function useRect(element, measure, fallbackRect) {
  if (measure === void 0) {
    measure = defaultMeasure;
  }

  const [rect, measureRect] = useReducer(reducer, null);
  const mutationObserver = useMutationObserver({
    callback(records) {
      if (!element) {
        return;
      }

      for (const record of records) {
        const {
          type,
          target
        } = record;

        if (type === 'childList' && target instanceof HTMLElement && target.contains(element)) {
          measureRect();
          break;
        }
      }
    }

  });
  const resizeObserver = useResizeObserver({
    callback: measureRect
  });
  useIsomorphicLayoutEffect(() => {
    measureRect();

    if (element) {
      resizeObserver == null ? void 0 : resizeObserver.observe(element);
      mutationObserver == null ? void 0 : mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    } else {
      resizeObserver == null ? void 0 : resizeObserver.disconnect();
      mutationObserver == null ? void 0 : mutationObserver.disconnect();
    }
  }, [element]);
  return rect;

  function reducer(currentRect) {
    if (!element) {
      return null;
    }

    if (element.isConnected === false) {
      var _ref;

      // Fall back to last rect we measured if the element is
      // no longer connected to the DOM.
      return (_ref = currentRect != null ? currentRect : fallbackRect) != null ? _ref : null;
    }

    const newRect = measure(element);

    if (JSON.stringify(currentRect) === JSON.stringify(newRect)) {
      return currentRect;
    }

    return newRect;
  }
}

function useRectDelta(rect) {
  const initialRect = useInitialValue(rect);
  return getRectDelta(rect, initialRect);
}

const defaultValue$1 = [];
function useScrollableAncestors(node) {
  const previousNode = useRef(node);
  const ancestors = useLazyMemo(previousValue => {
    if (!node) {
      return defaultValue$1;
    }

    if (previousValue && previousValue !== defaultValue$1 && node && previousNode.current && node.parentNode === previousNode.current.parentNode) {
      return previousValue;
    }

    return getScrollableAncestors(node);
  }, [node]);
  useEffect(() => {
    previousNode.current = node;
  }, [node]);
  return ancestors;
}

function useScrollOffsets(elements) {
  const [scrollCoordinates, setScrollCoordinates] = useState(null);
  const prevElements = useRef(elements); // To-do: Throttle the handleScroll callback

  const handleScroll = useCallback(event => {
    const scrollingElement = getScrollableElement(event.target);

    if (!scrollingElement) {
      return;
    }

    setScrollCoordinates(scrollCoordinates => {
      if (!scrollCoordinates) {
        return null;
      }

      scrollCoordinates.set(scrollingElement, getScrollCoordinates(scrollingElement));
      return new Map(scrollCoordinates);
    });
  }, []);
  useEffect(() => {
    const previousElements = prevElements.current;

    if (elements !== previousElements) {
      cleanup(previousElements);
      const entries = elements.map(element => {
        const scrollableElement = getScrollableElement(element);

        if (scrollableElement) {
          scrollableElement.addEventListener('scroll', handleScroll, {
            passive: true
          });
          return [scrollableElement, getScrollCoordinates(scrollableElement)];
        }

        return null;
      }).filter(entry => entry != null);
      setScrollCoordinates(entries.length ? new Map(entries) : null);
      prevElements.current = elements;
    }

    return () => {
      cleanup(elements);
      cleanup(previousElements);
    };

    function cleanup(elements) {
      elements.forEach(element => {
        const scrollableElement = getScrollableElement(element);
        scrollableElement == null ? void 0 : scrollableElement.removeEventListener('scroll', handleScroll);
      });
    }
  }, [handleScroll, elements]);
  return useMemo(() => {
    if (elements.length) {
      return scrollCoordinates ? Array.from(scrollCoordinates.values()).reduce((acc, coordinates) => add(acc, coordinates), defaultCoordinates) : getScrollOffsets(elements);
    }

    return defaultCoordinates;
  }, [elements, scrollCoordinates]);
}

function useScrollOffsetsDelta(scrollOffsets, dependencies) {
  if (dependencies === void 0) {
    dependencies = [];
  }

  const initialScrollOffsets = useRef(null);
  useEffect(() => {
    initialScrollOffsets.current = null;
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  dependencies);
  useEffect(() => {
    const hasScrollOffsets = scrollOffsets !== defaultCoordinates;

    if (hasScrollOffsets && !initialScrollOffsets.current) {
      initialScrollOffsets.current = scrollOffsets;
    }

    if (!hasScrollOffsets && initialScrollOffsets.current) {
      initialScrollOffsets.current = null;
    }
  }, [scrollOffsets]);
  return initialScrollOffsets.current ? subtract(scrollOffsets, initialScrollOffsets.current) : defaultCoordinates;
}

function useSensorSetup(sensors) {
  useEffect(() => {
    if (!canUseDOM) {
      return;
    }

    const teardownFns = sensors.map(_ref => {
      let {
        sensor
      } = _ref;
      return sensor.setup == null ? void 0 : sensor.setup();
    });
    return () => {
      for (const teardown of teardownFns) {
        teardown == null ? void 0 : teardown();
      }
    };
  }, // TO-DO: Sensors length could theoretically change which would not be a valid dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  sensors.map(_ref2 => {
    let {
      sensor
    } = _ref2;
    return sensor;
  }));
}

function useSyntheticListeners(listeners, id) {
  return useMemo(() => {
    return listeners.reduce((acc, _ref) => {
      let {
        eventName,
        handler
      } = _ref;

      acc[eventName] = event => {
        handler(event, id);
      };

      return acc;
    }, {});
  }, [listeners, id]);
}

function useWindowRect(element) {
  return useMemo(() => element ? getWindowClientRect(element) : null, [element]);
}

const defaultValue$2 = [];
function useRects(elements, measure) {
  if (measure === void 0) {
    measure = getClientRect;
  }

  const [firstElement] = elements;
  const windowRect = useWindowRect(firstElement ? getWindow(firstElement) : null);
  const [rects, measureRects] = useReducer(reducer, defaultValue$2);
  const resizeObserver = useResizeObserver({
    callback: measureRects
  });

  if (elements.length > 0 && rects === defaultValue$2) {
    measureRects();
  }

  useIsomorphicLayoutEffect(() => {
    if (elements.length) {
      elements.forEach(element => resizeObserver == null ? void 0 : resizeObserver.observe(element));
    } else {
      resizeObserver == null ? void 0 : resizeObserver.disconnect();
      measureRects();
    }
  }, [elements]);
  return rects;

  function reducer() {
    if (!elements.length) {
      return defaultValue$2;
    }

    return elements.map(element => isDocumentScrollingElement(element) ? windowRect : new Rect(measure(element), element));
  }
}

function getMeasurableNode(node) {
  if (!node) {
    return null;
  }

  if (node.children.length > 1) {
    return node;
  }

  const firstChild = node.children[0];
  return isHTMLElement(firstChild) ? firstChild : node;
}

function useDragOverlayMeasuring(_ref) {
  let {
    measure
  } = _ref;
  const [rect, setRect] = useState(null);
  const handleResize = useCallback(entries => {
    for (const {
      target
    } of entries) {
      if (isHTMLElement(target)) {
        setRect(rect => {
          const newRect = measure(target);
          return rect ? { ...rect,
            width: newRect.width,
            height: newRect.height
          } : newRect;
        });
        break;
      }
    }
  }, [measure]);
  const resizeObserver = useResizeObserver({
    callback: handleResize
  });
  const handleNodeChange = useCallback(element => {
    const node = getMeasurableNode(element);
    resizeObserver == null ? void 0 : resizeObserver.disconnect();

    if (node) {
      resizeObserver == null ? void 0 : resizeObserver.observe(node);
    }

    setRect(node ? measure(node) : null);
  }, [measure, resizeObserver]);
  const [nodeRef, setRef] = useNodeRef(handleNodeChange);
  return useMemo(() => ({
    nodeRef,
    rect,
    setRef
  }), [rect, nodeRef, setRef]);
}

const defaultSensors = [{
  sensor: PointerSensor,
  options: {}
}, {
  sensor: KeyboardSensor,
  options: {}
}];
const defaultData = {
  current: {}
};
const defaultMeasuringConfiguration = {
  draggable: {
    measure: getTransformAgnosticClientRect
  },
  droppable: {
    measure: getTransformAgnosticClientRect,
    strategy: MeasuringStrategy.WhileDragging,
    frequency: MeasuringFrequency.Optimized
  },
  dragOverlay: {
    measure: getClientRect
  }
};

class DroppableContainersMap extends Map {
  get(id) {
    var _super$get;

    return id != null ? (_super$get = super.get(id)) != null ? _super$get : undefined : undefined;
  }

  toArray() {
    return Array.from(this.values());
  }

  getEnabled() {
    return this.toArray().filter(_ref => {
      let {
        disabled
      } = _ref;
      return !disabled;
    });
  }

  getNodeFor(id) {
    var _this$get$node$curren, _this$get;

    return (_this$get$node$curren = (_this$get = this.get(id)) == null ? void 0 : _this$get.node.current) != null ? _this$get$node$curren : undefined;
  }

}

const defaultPublicContext = {
  activatorEvent: null,
  active: null,
  activeNode: null,
  activeNodeRect: null,
  collisions: null,
  containerNodeRect: null,
  draggableNodes: /*#__PURE__*/new Map(),
  droppableRects: /*#__PURE__*/new Map(),
  droppableContainers: /*#__PURE__*/new DroppableContainersMap(),
  over: null,
  dragOverlay: {
    nodeRef: {
      current: null
    },
    rect: null,
    setRef: noop
  },
  scrollableAncestors: [],
  scrollableAncestorRects: [],
  measuringConfiguration: defaultMeasuringConfiguration,
  measureDroppableContainers: noop,
  windowRect: null,
  measuringScheduled: false
};
const defaultInternalContext = {
  activatorEvent: null,
  activators: [],
  active: null,
  activeNodeRect: null,
  ariaDescribedById: {
    draggable: ''
  },
  dispatch: noop,
  draggableNodes: /*#__PURE__*/new Map(),
  over: null,
  measureDroppableContainers: noop
};
const InternalContext = /*#__PURE__*/createContext(defaultInternalContext);
const PublicContext = /*#__PURE__*/createContext(defaultPublicContext);

function getInitialState() {
  return {
    draggable: {
      active: null,
      initialCoordinates: {
        x: 0,
        y: 0
      },
      nodes: new Map(),
      translate: {
        x: 0,
        y: 0
      }
    },
    droppable: {
      containers: new DroppableContainersMap()
    }
  };
}
function reducer(state, action) {
  switch (action.type) {
    case Action.DragStart:
      return { ...state,
        draggable: { ...state.draggable,
          initialCoordinates: action.initialCoordinates,
          active: action.active
        }
      };

    case Action.DragMove:
      if (!state.draggable.active) {
        return state;
      }

      return { ...state,
        draggable: { ...state.draggable,
          translate: {
            x: action.coordinates.x - state.draggable.initialCoordinates.x,
            y: action.coordinates.y - state.draggable.initialCoordinates.y
          }
        }
      };

    case Action.DragEnd:
    case Action.DragCancel:
      return { ...state,
        draggable: { ...state.draggable,
          active: null,
          initialCoordinates: {
            x: 0,
            y: 0
          },
          translate: {
            x: 0,
            y: 0
          }
        }
      };

    case Action.RegisterDroppable:
      {
        const {
          element
        } = action;
        const {
          id
        } = element;
        const containers = new DroppableContainersMap(state.droppable.containers);
        containers.set(id, element);
        return { ...state,
          droppable: { ...state.droppable,
            containers
          }
        };
      }

    case Action.SetDroppableDisabled:
      {
        const {
          id,
          key,
          disabled
        } = action;
        const element = state.droppable.containers.get(id);

        if (!element || key !== element.key) {
          return state;
        }

        const containers = new DroppableContainersMap(state.droppable.containers);
        containers.set(id, { ...element,
          disabled
        });
        return { ...state,
          droppable: { ...state.droppable,
            containers
          }
        };
      }

    case Action.UnregisterDroppable:
      {
        const {
          id,
          key
        } = action;
        const element = state.droppable.containers.get(id);

        if (!element || key !== element.key) {
          return state;
        }

        const containers = new DroppableContainersMap(state.droppable.containers);
        containers.delete(id);
        return { ...state,
          droppable: { ...state.droppable,
            containers
          }
        };
      }

    default:
      {
        return state;
      }
  }
}

function RestoreFocus(_ref) {
  let {
    disabled
  } = _ref;
  const {
    active,
    activatorEvent,
    draggableNodes
  } = useContext(InternalContext);
  const previousActivatorEvent = usePrevious(activatorEvent);
  const previousActiveId = usePrevious(active == null ? void 0 : active.id); // Restore keyboard focus on the activator node

  useEffect(() => {
    if (disabled) {
      return;
    }

    if (!activatorEvent && previousActivatorEvent && previousActiveId != null) {
      if (!isKeyboardEvent(previousActivatorEvent)) {
        return;
      }

      if (document.activeElement === previousActivatorEvent.target) {
        // No need to restore focus
        return;
      }

      const draggableNode = draggableNodes.get(previousActiveId);

      if (!draggableNode) {
        return;
      }

      const {
        activatorNode,
        node
      } = draggableNode;

      if (!activatorNode.current && !node.current) {
        return;
      }

      requestAnimationFrame(() => {
        for (const element of [activatorNode.current, node.current]) {
          if (!element) {
            continue;
          }

          const focusableNode = findFirstFocusableNode(element);

          if (focusableNode) {
            focusableNode.focus();
            break;
          }
        }
      });
    }
  }, [activatorEvent, disabled, draggableNodes, previousActiveId, previousActivatorEvent]);
  return null;
}

function applyModifiers(modifiers, _ref) {
  let {
    transform,
    ...args
  } = _ref;
  return modifiers != null && modifiers.length ? modifiers.reduce((accumulator, modifier) => {
    return modifier({
      transform: accumulator,
      ...args
    });
  }, transform) : transform;
}

function useMeasuringConfiguration(config) {
  return useMemo(() => ({
    draggable: { ...defaultMeasuringConfiguration.draggable,
      ...(config == null ? void 0 : config.draggable)
    },
    droppable: { ...defaultMeasuringConfiguration.droppable,
      ...(config == null ? void 0 : config.droppable)
    },
    dragOverlay: { ...defaultMeasuringConfiguration.dragOverlay,
      ...(config == null ? void 0 : config.dragOverlay)
    }
  }), // eslint-disable-next-line react-hooks/exhaustive-deps
  [config == null ? void 0 : config.draggable, config == null ? void 0 : config.droppable, config == null ? void 0 : config.dragOverlay]);
}

function useLayoutShiftScrollCompensation(_ref) {
  let {
    activeNode,
    measure,
    initialRect,
    config = true
  } = _ref;
  const initialized = useRef(false);
  const {
    x,
    y
  } = typeof config === 'boolean' ? {
    x: config,
    y: config
  } : config;
  useIsomorphicLayoutEffect(() => {
    const disabled = !x && !y;

    if (disabled || !activeNode) {
      initialized.current = false;
      return;
    }

    if (initialized.current || !initialRect) {
      // Return early if layout shift scroll compensation was already attempted
      // or if there is no initialRect to compare to.
      return;
    } // Get the most up to date node ref for the active draggable


    const node = activeNode == null ? void 0 : activeNode.node.current;

    if (!node || node.isConnected === false) {
      // Return early if there is no attached node ref or if the node is
      // disconnected from the document.
      return;
    }

    const rect = measure(node);
    const rectDelta = getRectDelta(rect, initialRect);

    if (!x) {
      rectDelta.x = 0;
    }

    if (!y) {
      rectDelta.y = 0;
    } // Only perform layout shift scroll compensation once


    initialized.current = true;

    if (Math.abs(rectDelta.x) > 0 || Math.abs(rectDelta.y) > 0) {
      const firstScrollableAncestor = getFirstScrollableAncestor(node);

      if (firstScrollableAncestor) {
        firstScrollableAncestor.scrollBy({
          top: rectDelta.y,
          left: rectDelta.x
        });
      }
    }
  }, [activeNode, x, y, initialRect, measure]);
}

const ActiveDraggableContext = /*#__PURE__*/createContext({ ...defaultCoordinates,
  scaleX: 1,
  scaleY: 1
});
var Status;

(function (Status) {
  Status[Status["Uninitialized"] = 0] = "Uninitialized";
  Status[Status["Initializing"] = 1] = "Initializing";
  Status[Status["Initialized"] = 2] = "Initialized";
})(Status || (Status = {}));

const DndContext = /*#__PURE__*/memo(function DndContext(_ref) {
  var _sensorContext$curren, _dragOverlay$nodeRef$, _dragOverlay$rect, _over$rect;

  let {
    id,
    accessibility,
    autoScroll = true,
    children,
    sensors = defaultSensors,
    collisionDetection = rectIntersection,
    measuring,
    modifiers,
    ...props
  } = _ref;
  const store = useReducer(reducer, undefined, getInitialState);
  const [state, dispatch] = store;
  const [dispatchMonitorEvent, registerMonitorListener] = useDndMonitorProvider();
  const [status, setStatus] = useState(Status.Uninitialized);
  const isInitialized = status === Status.Initialized;
  const {
    draggable: {
      active: activeId,
      nodes: draggableNodes,
      translate
    },
    droppable: {
      containers: droppableContainers
    }
  } = state;
  const node = activeId ? draggableNodes.get(activeId) : null;
  const activeRects = useRef({
    initial: null,
    translated: null
  });
  const active = useMemo(() => {
    var _node$data;

    return activeId != null ? {
      id: activeId,
      // It's possible for the active node to unmount while dragging
      data: (_node$data = node == null ? void 0 : node.data) != null ? _node$data : defaultData,
      rect: activeRects
    } : null;
  }, [activeId, node]);
  const activeRef = useRef(null);
  const [activeSensor, setActiveSensor] = useState(null);
  const [activatorEvent, setActivatorEvent] = useState(null);
  const latestProps = useLatestValue(props, Object.values(props));
  const draggableDescribedById = useUniqueId("DndDescribedBy", id);
  const enabledDroppableContainers = useMemo(() => droppableContainers.getEnabled(), [droppableContainers]);
  const measuringConfiguration = useMeasuringConfiguration(measuring);
  const {
    droppableRects,
    measureDroppableContainers,
    measuringScheduled
  } = useDroppableMeasuring(enabledDroppableContainers, {
    dragging: isInitialized,
    dependencies: [translate.x, translate.y],
    config: measuringConfiguration.droppable
  });
  const activeNode = useCachedNode(draggableNodes, activeId);
  const activationCoordinates = useMemo(() => activatorEvent ? getEventCoordinates(activatorEvent) : null, [activatorEvent]);
  const autoScrollOptions = getAutoScrollerOptions();
  const initialActiveNodeRect = useInitialRect(activeNode, measuringConfiguration.draggable.measure);
  useLayoutShiftScrollCompensation({
    activeNode: activeId ? draggableNodes.get(activeId) : null,
    config: autoScrollOptions.layoutShiftCompensation,
    initialRect: initialActiveNodeRect,
    measure: measuringConfiguration.draggable.measure
  });
  const activeNodeRect = useRect(activeNode, measuringConfiguration.draggable.measure, initialActiveNodeRect);
  const containerNodeRect = useRect(activeNode ? activeNode.parentElement : null);
  const sensorContext = useRef({
    activatorEvent: null,
    active: null,
    activeNode,
    collisionRect: null,
    collisions: null,
    droppableRects,
    draggableNodes,
    draggingNode: null,
    draggingNodeRect: null,
    droppableContainers,
    over: null,
    scrollableAncestors: [],
    scrollAdjustedTranslate: null
  });
  const overNode = droppableContainers.getNodeFor((_sensorContext$curren = sensorContext.current.over) == null ? void 0 : _sensorContext$curren.id);
  const dragOverlay = useDragOverlayMeasuring({
    measure: measuringConfiguration.dragOverlay.measure
  }); // Use the rect of the drag overlay if it is mounted

  const draggingNode = (_dragOverlay$nodeRef$ = dragOverlay.nodeRef.current) != null ? _dragOverlay$nodeRef$ : activeNode;
  const draggingNodeRect = isInitialized ? (_dragOverlay$rect = dragOverlay.rect) != null ? _dragOverlay$rect : activeNodeRect : null;
  const usesDragOverlay = Boolean(dragOverlay.nodeRef.current && dragOverlay.rect); // The delta between the previous and new position of the draggable node
  // is only relevant when there is no drag overlay

  const nodeRectDelta = useRectDelta(usesDragOverlay ? null : activeNodeRect); // Get the window rect of the dragging node

  const windowRect = useWindowRect(draggingNode ? getWindow(draggingNode) : null); // Get scrollable ancestors of the dragging node

  const scrollableAncestors = useScrollableAncestors(isInitialized ? overNode != null ? overNode : activeNode : null);
  const scrollableAncestorRects = useRects(scrollableAncestors); // Apply modifiers

  const modifiedTranslate = applyModifiers(modifiers, {
    transform: {
      x: translate.x - nodeRectDelta.x,
      y: translate.y - nodeRectDelta.y,
      scaleX: 1,
      scaleY: 1
    },
    activatorEvent,
    active,
    activeNodeRect,
    containerNodeRect,
    draggingNodeRect,
    over: sensorContext.current.over,
    overlayNodeRect: dragOverlay.rect,
    scrollableAncestors,
    scrollableAncestorRects,
    windowRect
  });
  const pointerCoordinates = activationCoordinates ? add(activationCoordinates, translate) : null;
  const scrollOffsets = useScrollOffsets(scrollableAncestors); // Represents the scroll delta since dragging was initiated

  const scrollAdjustment = useScrollOffsetsDelta(scrollOffsets); // Represents the scroll delta since the last time the active node rect was measured

  const activeNodeScrollDelta = useScrollOffsetsDelta(scrollOffsets, [activeNodeRect]);
  const scrollAdjustedTranslate = add(modifiedTranslate, scrollAdjustment);
  const collisionRect = draggingNodeRect ? getAdjustedRect(draggingNodeRect, modifiedTranslate) : null;
  const collisions = active && collisionRect ? collisionDetection({
    active,
    collisionRect,
    droppableRects,
    droppableContainers: enabledDroppableContainers,
    pointerCoordinates
  }) : null;
  const overId = getFirstCollision(collisions, 'id');
  const [over, setOver] = useState(null); // When there is no drag overlay used, we need to account for the
  // window scroll delta

  const appliedTranslate = usesDragOverlay ? modifiedTranslate : add(modifiedTranslate, activeNodeScrollDelta);
  const transform = adjustScale(appliedTranslate, (_over$rect = over == null ? void 0 : over.rect) != null ? _over$rect : null, activeNodeRect);
  const instantiateSensor = useCallback((event, _ref2) => {
    let {
      sensor: Sensor,
      options
    } = _ref2;

    if (activeRef.current == null) {
      return;
    }

    const activeNode = draggableNodes.get(activeRef.current);

    if (!activeNode) {
      return;
    }

    const activatorEvent = event.nativeEvent;
    const sensorInstance = new Sensor({
      active: activeRef.current,
      activeNode,
      event: activatorEvent,
      options,
      // Sensors need to be instantiated with refs for arguments that change over time
      // otherwise they are frozen in time with the stale arguments
      context: sensorContext,

      onStart(initialCoordinates) {
        const id = activeRef.current;

        if (id == null) {
          return;
        }

        const draggableNode = draggableNodes.get(id);

        if (!draggableNode) {
          return;
        }

        const {
          onDragStart
        } = latestProps.current;
        const event = {
          active: {
            id,
            data: draggableNode.data,
            rect: activeRects
          }
        };
        unstable_batchedUpdates(() => {
          onDragStart == null ? void 0 : onDragStart(event);
          setStatus(Status.Initializing);
          dispatch({
            type: Action.DragStart,
            initialCoordinates,
            active: id
          });
          dispatchMonitorEvent({
            type: 'onDragStart',
            event
          });
        });
      },

      onMove(coordinates) {
        dispatch({
          type: Action.DragMove,
          coordinates
        });
      },

      onEnd: createHandler(Action.DragEnd),
      onCancel: createHandler(Action.DragCancel)
    });
    unstable_batchedUpdates(() => {
      setActiveSensor(sensorInstance);
      setActivatorEvent(event.nativeEvent);
    });

    function createHandler(type) {
      return async function handler() {
        const {
          active,
          collisions,
          over,
          scrollAdjustedTranslate
        } = sensorContext.current;
        let event = null;

        if (active && scrollAdjustedTranslate) {
          const {
            cancelDrop
          } = latestProps.current;
          event = {
            activatorEvent,
            active: active,
            collisions,
            delta: scrollAdjustedTranslate,
            over
          };

          if (type === Action.DragEnd && typeof cancelDrop === 'function') {
            const shouldCancel = await Promise.resolve(cancelDrop(event));

            if (shouldCancel) {
              type = Action.DragCancel;
            }
          }
        }

        activeRef.current = null;
        unstable_batchedUpdates(() => {
          dispatch({
            type
          });
          setStatus(Status.Uninitialized);
          setOver(null);
          setActiveSensor(null);
          setActivatorEvent(null);
          const eventName = type === Action.DragEnd ? 'onDragEnd' : 'onDragCancel';

          if (event) {
            const handler = latestProps.current[eventName];
            handler == null ? void 0 : handler(event);
            dispatchMonitorEvent({
              type: eventName,
              event
            });
          }
        });
      };
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [draggableNodes]);
  const bindActivatorToSensorInstantiator = useCallback((handler, sensor) => {
    return (event, active) => {
      const nativeEvent = event.nativeEvent;
      const activeDraggableNode = draggableNodes.get(active);

      if ( // Another sensor is already instantiating
      activeRef.current !== null || // No active draggable
      !activeDraggableNode || // Event has already been captured
      nativeEvent.dndKit || nativeEvent.defaultPrevented) {
        return;
      }

      const activationContext = {
        active: activeDraggableNode
      };
      const shouldActivate = handler(event, sensor.options, activationContext);

      if (shouldActivate === true) {
        nativeEvent.dndKit = {
          capturedBy: sensor.sensor
        };
        activeRef.current = active;
        instantiateSensor(event, sensor);
      }
    };
  }, [draggableNodes, instantiateSensor]);
  const activators = useCombineActivators(sensors, bindActivatorToSensorInstantiator);
  useSensorSetup(sensors);
  useIsomorphicLayoutEffect(() => {
    if (activeNodeRect && status === Status.Initializing) {
      setStatus(Status.Initialized);
    }
  }, [activeNodeRect, status]);
  useEffect(() => {
    const {
      onDragMove
    } = latestProps.current;
    const {
      active,
      activatorEvent,
      collisions,
      over
    } = sensorContext.current;

    if (!active || !activatorEvent) {
      return;
    }

    const event = {
      active,
      activatorEvent,
      collisions,
      delta: {
        x: scrollAdjustedTranslate.x,
        y: scrollAdjustedTranslate.y
      },
      over
    };
    unstable_batchedUpdates(() => {
      onDragMove == null ? void 0 : onDragMove(event);
      dispatchMonitorEvent({
        type: 'onDragMove',
        event
      });
    });
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [scrollAdjustedTranslate.x, scrollAdjustedTranslate.y]);
  useEffect(() => {
    const {
      active,
      activatorEvent,
      collisions,
      droppableContainers,
      scrollAdjustedTranslate
    } = sensorContext.current;

    if (!active || activeRef.current == null || !activatorEvent || !scrollAdjustedTranslate) {
      return;
    }

    const {
      onDragOver
    } = latestProps.current;
    const overContainer = droppableContainers.get(overId);
    const over = overContainer && overContainer.rect.current ? {
      id: overContainer.id,
      rect: overContainer.rect.current,
      data: overContainer.data,
      disabled: overContainer.disabled
    } : null;
    const event = {
      active,
      activatorEvent,
      collisions,
      delta: {
        x: scrollAdjustedTranslate.x,
        y: scrollAdjustedTranslate.y
      },
      over
    };
    unstable_batchedUpdates(() => {
      setOver(over);
      onDragOver == null ? void 0 : onDragOver(event);
      dispatchMonitorEvent({
        type: 'onDragOver',
        event
      });
    });
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [overId]);
  useIsomorphicLayoutEffect(() => {
    sensorContext.current = {
      activatorEvent,
      active,
      activeNode,
      collisionRect,
      collisions,
      droppableRects,
      draggableNodes,
      draggingNode,
      draggingNodeRect,
      droppableContainers,
      over,
      scrollableAncestors,
      scrollAdjustedTranslate
    };
    activeRects.current = {
      initial: draggingNodeRect,
      translated: collisionRect
    };
  }, [active, activeNode, collisions, collisionRect, draggableNodes, draggingNode, draggingNodeRect, droppableRects, droppableContainers, over, scrollableAncestors, scrollAdjustedTranslate]);
  useAutoScroller({ ...autoScrollOptions,
    delta: translate,
    draggingRect: collisionRect,
    pointerCoordinates,
    scrollableAncestors,
    scrollableAncestorRects
  });
  const publicContext = useMemo(() => {
    const context = {
      active,
      activeNode,
      activeNodeRect,
      activatorEvent,
      collisions,
      containerNodeRect,
      dragOverlay,
      draggableNodes,
      droppableContainers,
      droppableRects,
      over,
      measureDroppableContainers,
      scrollableAncestors,
      scrollableAncestorRects,
      measuringConfiguration,
      measuringScheduled,
      windowRect
    };
    return context;
  }, [active, activeNode, activeNodeRect, activatorEvent, collisions, containerNodeRect, dragOverlay, draggableNodes, droppableContainers, droppableRects, over, measureDroppableContainers, scrollableAncestors, scrollableAncestorRects, measuringConfiguration, measuringScheduled, windowRect]);
  const internalContext = useMemo(() => {
    const context = {
      activatorEvent,
      activators,
      active,
      activeNodeRect,
      ariaDescribedById: {
        draggable: draggableDescribedById
      },
      dispatch,
      draggableNodes,
      over,
      measureDroppableContainers
    };
    return context;
  }, [activatorEvent, activators, active, activeNodeRect, dispatch, draggableDescribedById, draggableNodes, over, measureDroppableContainers]);
  return React__default.createElement(DndMonitorContext.Provider, {
    value: registerMonitorListener
  }, React__default.createElement(InternalContext.Provider, {
    value: internalContext
  }, React__default.createElement(PublicContext.Provider, {
    value: publicContext
  }, React__default.createElement(ActiveDraggableContext.Provider, {
    value: transform
  }, children)), React__default.createElement(RestoreFocus, {
    disabled: (accessibility == null ? void 0 : accessibility.restoreFocus) === false
  })), React__default.createElement(Accessibility, { ...accessibility,
    hiddenTextDescribedById: draggableDescribedById
  }));

  function getAutoScrollerOptions() {
    const activeSensorDisablesAutoscroll = (activeSensor == null ? void 0 : activeSensor.autoScrollEnabled) === false;
    const autoScrollGloballyDisabled = typeof autoScroll === 'object' ? autoScroll.enabled === false : autoScroll === false;
    const enabled = isInitialized && !activeSensorDisablesAutoscroll && !autoScrollGloballyDisabled;

    if (typeof autoScroll === 'object') {
      return { ...autoScroll,
        enabled
      };
    }

    return {
      enabled
    };
  }
});

const NullContext = /*#__PURE__*/createContext(null);
const defaultRole = 'button';
const ID_PREFIX$1 = 'Droppable';
function useDraggable(_ref) {
  let {
    id,
    data,
    disabled = false,
    attributes
  } = _ref;
  const key = useUniqueId(ID_PREFIX$1);
  const {
    activators,
    activatorEvent,
    active,
    activeNodeRect,
    ariaDescribedById,
    draggableNodes,
    over
  } = useContext(InternalContext);
  const {
    role = defaultRole,
    roleDescription = 'draggable',
    tabIndex = 0
  } = attributes != null ? attributes : {};
  const isDragging = (active == null ? void 0 : active.id) === id;
  const transform = useContext(isDragging ? ActiveDraggableContext : NullContext);
  const [node, setNodeRef] = useNodeRef();
  const [activatorNode, setActivatorNodeRef] = useNodeRef();
  const listeners = useSyntheticListeners(activators, id);
  const dataRef = useLatestValue(data);
  useIsomorphicLayoutEffect(() => {
    draggableNodes.set(id, {
      id,
      key,
      node,
      activatorNode,
      data: dataRef
    });
    return () => {
      const node = draggableNodes.get(id);

      if (node && node.key === key) {
        draggableNodes.delete(id);
      }
    };
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [draggableNodes, id]);
  const memoizedAttributes = useMemo(() => ({
    role,
    tabIndex,
    'aria-disabled': disabled,
    'aria-pressed': isDragging && role === defaultRole ? true : undefined,
    'aria-roledescription': roleDescription,
    'aria-describedby': ariaDescribedById.draggable
  }), [disabled, role, tabIndex, isDragging, roleDescription, ariaDescribedById.draggable]);
  return {
    active,
    activatorEvent,
    activeNodeRect,
    attributes: memoizedAttributes,
    isDragging,
    listeners: disabled ? undefined : listeners,
    node,
    over,
    setNodeRef,
    setActivatorNodeRef,
    transform
  };
}

function useDndContext() {
  return useContext(PublicContext);
}

const ID_PREFIX$1$1 = 'Droppable';
const defaultResizeObserverConfig = {
  timeout: 25
};
function useDroppable(_ref) {
  let {
    data,
    disabled = false,
    id,
    resizeObserverConfig
  } = _ref;
  const key = useUniqueId(ID_PREFIX$1$1);
  const {
    active,
    dispatch,
    over,
    measureDroppableContainers
  } = useContext(InternalContext);
  const previous = useRef({
    disabled
  });
  const resizeObserverConnected = useRef(false);
  const rect = useRef(null);
  const callbackId = useRef(null);
  const {
    disabled: resizeObserverDisabled,
    updateMeasurementsFor,
    timeout: resizeObserverTimeout
  } = { ...defaultResizeObserverConfig,
    ...resizeObserverConfig
  };
  const ids = useLatestValue(updateMeasurementsFor != null ? updateMeasurementsFor : id);
  const handleResize = useCallback(() => {
    if (!resizeObserverConnected.current) {
      // ResizeObserver invokes the `handleResize` callback as soon as `observe` is called,
      // assuming the element is rendered and displayed.
      resizeObserverConnected.current = true;
      return;
    }

    if (callbackId.current != null) {
      clearTimeout(callbackId.current);
    }

    callbackId.current = setTimeout(() => {
      measureDroppableContainers(Array.isArray(ids.current) ? ids.current : [ids.current]);
      callbackId.current = null;
    }, resizeObserverTimeout);
  }, //eslint-disable-next-line react-hooks/exhaustive-deps
  [resizeObserverTimeout]);
  const resizeObserver = useResizeObserver({
    callback: handleResize,
    disabled: resizeObserverDisabled || !active
  });
  const handleNodeChange = useCallback((newElement, previousElement) => {
    if (!resizeObserver) {
      return;
    }

    if (previousElement) {
      resizeObserver.unobserve(previousElement);
      resizeObserverConnected.current = false;
    }

    if (newElement) {
      resizeObserver.observe(newElement);
    }
  }, [resizeObserver]);
  const [nodeRef, setNodeRef] = useNodeRef(handleNodeChange);
  const dataRef = useLatestValue(data);
  useEffect(() => {
    if (!resizeObserver || !nodeRef.current) {
      return;
    }

    resizeObserver.disconnect();
    resizeObserverConnected.current = false;
    resizeObserver.observe(nodeRef.current);
  }, [nodeRef, resizeObserver]);
  useIsomorphicLayoutEffect(() => {
    dispatch({
      type: Action.RegisterDroppable,
      element: {
        id,
        key,
        disabled,
        node: nodeRef,
        rect,
        data: dataRef
      }
    });
    return () => dispatch({
      type: Action.UnregisterDroppable,
      key,
      id
    });
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [id]);
  useEffect(() => {
    if (disabled !== previous.current.disabled) {
      dispatch({
        type: Action.SetDroppableDisabled,
        id,
        key,
        disabled
      });
      previous.current.disabled = disabled;
    }
  }, [id, key, disabled, dispatch]);
  return {
    active,
    rect,
    isOver: (over == null ? void 0 : over.id) === id,
    node: nodeRef,
    over,
    setNodeRef
  };
}

const restrictToVerticalAxis = ({
  transform
}) => {
  return { ...transform,
    x: 0
  };
};

/**
 * Move an array item to a different position. Returns a new array with the item moved to the new position.
 */
function arrayMove(array, from, to) {
  const newArray = array.slice();
  newArray.splice(to < 0 ? newArray.length + to : to, 0, newArray.splice(from, 1)[0]);
  return newArray;
}

function getSortedRects(items, rects) {
  return items.reduce((accumulator, id, index) => {
    const rect = rects.get(id);

    if (rect) {
      accumulator[index] = rect;
    }

    return accumulator;
  }, Array(items.length));
}

function isValidIndex(index) {
  return index !== null && index >= 0;
}

function itemsEqual(a, b) {
  if (a === b) {
    return true;
  }

  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

function normalizeDisabled(disabled) {
  if (typeof disabled === 'boolean') {
    return {
      draggable: disabled,
      droppable: disabled
    };
  }

  return disabled;
}

const rectSortingStrategy = _ref => {
  let {
    rects,
    activeIndex,
    overIndex,
    index
  } = _ref;
  const newRects = arrayMove(rects, overIndex, activeIndex);
  const oldRect = rects[index];
  const newRect = newRects[index];

  if (!newRect || !oldRect) {
    return null;
  }

  return {
    x: newRect.left - oldRect.left,
    y: newRect.top - oldRect.top,
    scaleX: newRect.width / oldRect.width,
    scaleY: newRect.height / oldRect.height
  };
};

// To-do: We should be calculating scale transformation
const defaultScale$1 = {
  scaleX: 1,
  scaleY: 1
};
const verticalListSortingStrategy = _ref => {
  var _rects$activeIndex;

  let {
    activeIndex,
    activeNodeRect: fallbackActiveRect,
    index,
    rects,
    overIndex
  } = _ref;
  const activeNodeRect = (_rects$activeIndex = rects[activeIndex]) != null ? _rects$activeIndex : fallbackActiveRect;

  if (!activeNodeRect) {
    return null;
  }

  if (index === activeIndex) {
    const overIndexRect = rects[overIndex];

    if (!overIndexRect) {
      return null;
    }

    return {
      x: 0,
      y: activeIndex < overIndex ? overIndexRect.top + overIndexRect.height - (activeNodeRect.top + activeNodeRect.height) : overIndexRect.top - activeNodeRect.top,
      ...defaultScale$1
    };
  }

  const itemGap = getItemGap$1(rects, index, activeIndex);

  if (index > activeIndex && index <= overIndex) {
    return {
      x: 0,
      y: -activeNodeRect.height - itemGap,
      ...defaultScale$1
    };
  }

  if (index < activeIndex && index >= overIndex) {
    return {
      x: 0,
      y: activeNodeRect.height + itemGap,
      ...defaultScale$1
    };
  }

  return {
    x: 0,
    y: 0,
    ...defaultScale$1
  };
};

function getItemGap$1(clientRects, index, activeIndex) {
  const currentRect = clientRects[index];
  const previousRect = clientRects[index - 1];
  const nextRect = clientRects[index + 1];

  if (!currentRect) {
    return 0;
  }

  if (activeIndex < index) {
    return previousRect ? currentRect.top - (previousRect.top + previousRect.height) : nextRect ? nextRect.top - (currentRect.top + currentRect.height) : 0;
  }

  return nextRect ? nextRect.top - (currentRect.top + currentRect.height) : previousRect ? currentRect.top - (previousRect.top + previousRect.height) : 0;
}

const ID_PREFIX = 'Sortable';
const Context = /*#__PURE__*/React__default.createContext({
  activeIndex: -1,
  containerId: ID_PREFIX,
  disableTransforms: false,
  items: [],
  overIndex: -1,
  useDragOverlay: false,
  sortedRects: [],
  strategy: rectSortingStrategy,
  disabled: {
    draggable: false,
    droppable: false
  }
});
function SortableContext(_ref) {
  let {
    children,
    id,
    items: userDefinedItems,
    strategy = rectSortingStrategy,
    disabled: disabledProp = false
  } = _ref;
  const {
    active,
    dragOverlay,
    droppableRects,
    over,
    measureDroppableContainers,
    measuringScheduled
  } = useDndContext();
  const containerId = useUniqueId(ID_PREFIX, id);
  const useDragOverlay = Boolean(dragOverlay.rect !== null);
  const items = useMemo(() => userDefinedItems.map(item => typeof item === 'object' && 'id' in item ? item.id : item), [userDefinedItems]);
  const isDragging = active != null;
  const activeIndex = active ? items.indexOf(active.id) : -1;
  const overIndex = over ? items.indexOf(over.id) : -1;
  const previousItemsRef = useRef(items);
  const itemsHaveChanged = !itemsEqual(items, previousItemsRef.current);
  const disableTransforms = overIndex !== -1 && activeIndex === -1 || itemsHaveChanged;
  const disabled = normalizeDisabled(disabledProp);
  useIsomorphicLayoutEffect(() => {
    if (itemsHaveChanged && isDragging && !measuringScheduled) {
      measureDroppableContainers(items);
    }
  }, [itemsHaveChanged, items, isDragging, measureDroppableContainers, measuringScheduled]);
  useEffect(() => {
    previousItemsRef.current = items;
  }, [items]);
  const contextValue = useMemo(() => ({
    activeIndex,
    containerId,
    disabled,
    disableTransforms,
    items,
    overIndex,
    useDragOverlay,
    sortedRects: getSortedRects(items, droppableRects),
    strategy
  }), // eslint-disable-next-line react-hooks/exhaustive-deps
  [activeIndex, containerId, disabled.draggable, disabled.droppable, disableTransforms, items, overIndex, droppableRects, useDragOverlay, strategy]);
  return React__default.createElement(Context.Provider, {
    value: contextValue
  }, children);
}

const defaultNewIndexGetter = _ref => {
  let {
    id,
    items,
    activeIndex,
    overIndex
  } = _ref;
  return arrayMove(items, activeIndex, overIndex).indexOf(id);
};
const defaultAnimateLayoutChanges = _ref2 => {
  let {
    containerId,
    isSorting,
    wasDragging,
    index,
    items,
    newIndex,
    previousItems,
    previousContainerId,
    transition
  } = _ref2;

  if (!transition || !wasDragging) {
    return false;
  }

  if (previousItems !== items && index === newIndex) {
    return false;
  }

  if (isSorting) {
    return true;
  }

  return newIndex !== index && containerId === previousContainerId;
};
const defaultTransition = {
  duration: 200,
  easing: 'ease'
};
const transitionProperty = 'transform';
const disabledTransition = /*#__PURE__*/CSS$1.Transition.toString({
  property: transitionProperty,
  duration: 0,
  easing: 'linear'
});
const defaultAttributes = {
  roleDescription: 'sortable'
};

/*
 * When the index of an item changes while sorting,
 * we need to temporarily disable the transforms
 */

function useDerivedTransform(_ref) {
  let {
    disabled,
    index,
    node,
    rect
  } = _ref;
  const [derivedTransform, setDerivedtransform] = useState(null);
  const previousIndex = useRef(index);
  useIsomorphicLayoutEffect(() => {
    if (!disabled && index !== previousIndex.current && node.current) {
      const initial = rect.current;

      if (initial) {
        const current = getClientRect(node.current, {
          ignoreTransform: true
        });
        const delta = {
          x: initial.left - current.left,
          y: initial.top - current.top,
          scaleX: initial.width / current.width,
          scaleY: initial.height / current.height
        };

        if (delta.x || delta.y) {
          setDerivedtransform(delta);
        }
      }
    }

    if (index !== previousIndex.current) {
      previousIndex.current = index;
    }
  }, [disabled, index, node, rect]);
  useEffect(() => {
    if (derivedTransform) {
      requestAnimationFrame(() => {
        setDerivedtransform(null);
      });
    }
  }, [derivedTransform]);
  return derivedTransform;
}

function useSortable(_ref) {
  let {
    animateLayoutChanges = defaultAnimateLayoutChanges,
    attributes: userDefinedAttributes,
    disabled: localDisabled,
    data: customData,
    getNewIndex = defaultNewIndexGetter,
    id,
    strategy: localStrategy,
    resizeObserverConfig,
    transition = defaultTransition
  } = _ref;
  const {
    items,
    containerId,
    activeIndex,
    disabled: globalDisabled,
    disableTransforms,
    sortedRects,
    overIndex,
    useDragOverlay,
    strategy: globalStrategy
  } = useContext(Context);
  const disabled = normalizeLocalDisabled(localDisabled, globalDisabled);
  const index = items.indexOf(id);
  const data = useMemo(() => ({
    sortable: {
      containerId,
      index,
      items
    },
    ...customData
  }), [containerId, customData, index, items]);
  const itemsAfterCurrentSortable = useMemo(() => items.slice(items.indexOf(id)), [items, id]);
  const {
    rect,
    node,
    isOver,
    setNodeRef: setDroppableNodeRef
  } = useDroppable({
    id,
    data,
    disabled: disabled.droppable,
    resizeObserverConfig: {
      updateMeasurementsFor: itemsAfterCurrentSortable,
      ...resizeObserverConfig
    }
  });
  const {
    active,
    activatorEvent,
    activeNodeRect,
    attributes,
    setNodeRef: setDraggableNodeRef,
    listeners,
    isDragging,
    over,
    setActivatorNodeRef,
    transform
  } = useDraggable({
    id,
    data,
    attributes: { ...defaultAttributes,
      ...userDefinedAttributes
    },
    disabled: disabled.draggable
  });
  const setNodeRef = useCombinedRefs(setDroppableNodeRef, setDraggableNodeRef);
  const isSorting = Boolean(active);
  const displaceItem = isSorting && !disableTransforms && isValidIndex(activeIndex) && isValidIndex(overIndex);
  const shouldDisplaceDragSource = !useDragOverlay && isDragging;
  const dragSourceDisplacement = shouldDisplaceDragSource && displaceItem ? transform : null;
  const strategy = localStrategy != null ? localStrategy : globalStrategy;
  const finalTransform = displaceItem ? dragSourceDisplacement != null ? dragSourceDisplacement : strategy({
    rects: sortedRects,
    activeNodeRect,
    activeIndex,
    overIndex,
    index
  }) : null;
  const newIndex = isValidIndex(activeIndex) && isValidIndex(overIndex) ? getNewIndex({
    id,
    items,
    activeIndex,
    overIndex
  }) : index;
  const activeId = active == null ? void 0 : active.id;
  const previous = useRef({
    activeId,
    items,
    newIndex,
    containerId
  });
  const itemsHaveChanged = items !== previous.current.items;
  const shouldAnimateLayoutChanges = animateLayoutChanges({
    active,
    containerId,
    isDragging,
    isSorting,
    id,
    index,
    items,
    newIndex: previous.current.newIndex,
    previousItems: previous.current.items,
    previousContainerId: previous.current.containerId,
    transition,
    wasDragging: previous.current.activeId != null
  });
  const derivedTransform = useDerivedTransform({
    disabled: !shouldAnimateLayoutChanges,
    index,
    node,
    rect
  });
  useEffect(() => {
    if (isSorting && previous.current.newIndex !== newIndex) {
      previous.current.newIndex = newIndex;
    }

    if (containerId !== previous.current.containerId) {
      previous.current.containerId = containerId;
    }

    if (items !== previous.current.items) {
      previous.current.items = items;
    }
  }, [isSorting, newIndex, containerId, items]);
  useEffect(() => {
    if (activeId === previous.current.activeId) {
      return;
    }

    if (activeId && !previous.current.activeId) {
      previous.current.activeId = activeId;
      return;
    }

    const timeoutId = setTimeout(() => {
      previous.current.activeId = activeId;
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [activeId]);
  return {
    active,
    activeIndex,
    attributes,
    data,
    rect,
    index,
    newIndex,
    items,
    isOver,
    isSorting,
    isDragging,
    listeners,
    node,
    overIndex,
    over,
    setNodeRef,
    setActivatorNodeRef,
    setDroppableNodeRef,
    setDraggableNodeRef,
    transform: derivedTransform != null ? derivedTransform : finalTransform,
    transition: getTransition()
  };

  function getTransition() {
    if ( // Temporarily disable transitions for a single frame to set up derived transforms
    derivedTransform || // Or to prevent items jumping to back to their "new" position when items change
    itemsHaveChanged && previous.current.newIndex === index) {
      return disabledTransition;
    }

    if (shouldDisplaceDragSource && !isKeyboardEvent(activatorEvent) || !transition) {
      return undefined;
    }

    if (isSorting || shouldAnimateLayoutChanges) {
      return CSS$1.Transition.toString({ ...transition,
        property: transitionProperty
      });
    }

    return undefined;
  }
}

function normalizeLocalDisabled(localDisabled, globalDisabled) {
  var _localDisabled$dragga, _localDisabled$droppa;

  if (typeof localDisabled === 'boolean') {
    return {
      draggable: localDisabled,
      // Backwards compatibility
      droppable: false
    };
  }

  return {
    draggable: (_localDisabled$dragga = localDisabled == null ? void 0 : localDisabled.draggable) != null ? _localDisabled$dragga : globalDisabled.draggable,
    droppable: (_localDisabled$droppa = localDisabled == null ? void 0 : localDisabled.droppable) != null ? _localDisabled$droppa : globalDisabled.droppable
  };
}

[KeyboardCode.Down, KeyboardCode.Right, KeyboardCode.Up, KeyboardCode.Left];

const StyledContainer = _default$1(Stack$5) `border-b`;

const DraggableMenu = ({ children, className, onChange, }) => {
    const [sortedChildren, setSortedChildren] = useState(Array.isArray(children) ? children : [children]);
    const sensors = [useSensor(PointerSensor)];
    useEffect(() => {
        setSortedChildren((sortedChildren) => {
            const newChildren = Array.isArray(children) ? children : [children];
            const newSortedChildren = sortedChildren
                .map((sortedChild) => newChildren.find((child) => child.props.id === sortedChild.props.id))
                .filter(Boolean);
            if (newChildren.length === newSortedChildren.length) {
                return newSortedChildren;
            }
            else if (newChildren.length > newSortedChildren.length) {
                // When children's length increased, add the new children at the end of sort
                const addedChildren = newChildren.filter((newChild) => !newSortedChildren.find((child) => child.props.id === newChild.props.id));
                return [...newSortedChildren, ...addedChildren];
            }
            else {
                return sortedChildren
                    .map((sortedChild) => newChildren.find((newChild) => newChild.props.id === sortedChild.props.id))
                    .filter(Boolean);
            }
        });
    }, [children]);
    const handleDragEnd = ({ active, over }) => {
        if (active.id !== over?.id) {
            const oldIndex = sortedChildren.findIndex((child) => child.props.id === active.id);
            const newIndex = sortedChildren.findIndex((child) => child.props.id === over?.id);
            const newSortedChildren = arrayMove(sortedChildren, oldIndex, newIndex);
            setSortedChildren(newSortedChildren);
            onChange(newSortedChildren.map((child) => child.props.id));
        }
    };
    return (React__default.createElement(StyledContainer, { className: className, direction: "column", gap: 2.5 },
        React__default.createElement(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, modifiers: [restrictToVerticalAxis] },
            React__default.createElement(SortableContext, { items: sortedChildren.map((child) => child.props.id), strategy: verticalListSortingStrategy }, sortedChildren))));
};
const DraggableMenuMemo = memo(DraggableMenu);

var _g$1;
function _extends$2() { return _extends$2 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$2.apply(null, arguments); }
var SvgReorder = function SvgReorder(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    ref: ref
  }, props), _g$1 || (_g$1 = /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M14.86,23.27H1.5a1.25,1.25,0,0,1,0-2.5h21a1.25,1.25,0,0,1,0,2.5Z",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M22.5,3.23H1.5a1.25,1.25,0,0,1,0-2.5h21a1.25,1.25,0,0,1,0,2.5Z",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M22.5,9.91H1.5a1.25,1.25,0,0,1,0-2.5h21a1.25,1.25,0,0,1,0,2.5Z",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M22.5,16.59H1.5a1.25,1.25,0,0,1,0-2.5h21a1.25,1.25,0,0,1,0,2.5Z",
    fill: "currentColor"
  }))));
};
var ForwardRef$2 = /*#__PURE__*/forwardRef(SvgReorder);
var Memo$2 = /*#__PURE__*/memo(ForwardRef$2);

const DraggableMenuItem = ({ children, className, id, title, }) => {
    const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({ id: id });
    const style = {
        transform: CSS$1.Transform.toString(transform),
    };
    return (React__default.createElement(StyledDraggableMenuItem, { className: className, "$isDragging": isDragging, ref: setNodeRef, style: style },
        React__default.createElement(IconAndTitle, null,
            React__default.createElement(MenuItemIcon, { "data-testid": `grabber-${id}`, ...attributes, ...listeners },
                React__default.createElement(Memo$2, { className: "text-gray-200 cursor-grabbing" })),
            title),
        children));
};
const DraggableMenuItemMemo = memo(DraggableMenuItem);

const DraggableMenuItemWithToggle = ({ id, title, toggle, }) => (React__default.createElement(DraggableMenuItem, { id: id, title: title },
    React__default.createElement("div", { "data-testid": `toggle-${id}` },
        React__default.createElement(ToggleSwitch, { checked: toggle.checked, disabled: toggle.disabled, onChange: toggle.onChange, withIcon: toggle.withIcon }))));
const DraggableMenuItemWithToggleMemo = memo(DraggableMenuItemWithToggle);

const StyledGradientBox = _default$1.div `
    pt-2
    pb-6
    px-4
    flex
`;
const StyledLabel = _default$1(Typography) `
    text-gray-400
    leading-5
`;
const StyledGradient = _default$1.div `
    flex-1
    w-full
    h-3
`;
// Not Applicable Component
const StyledNotApplicable = _default$1.div `
  px-5
  flex
  flex-col
  justify-end
`;
const NotApplicableBox = _default$1.div `
  w-8
  h-4
  bg-gray-100
  relative
`;
const NotApplicableLine = _default$1.hr `
  border-1
  border-gray-400
  w-[18px]
  absolute
  top-1/2
  left-1/2
  -translate-y-2/4 
  -translate-x-2/4
`;

const NotApplicable = () => (React__default.createElement(StyledNotApplicable, { "data-testid": "n/a-legend" },
    React__default.createElement(NotApplicableBox, null,
        React__default.createElement(NotApplicableLine, null)),
    React__default.createElement(StyledLabel, { as: "label", fontWeight: "medium", fontSize: "sm", className: "text-center" }, "N/A")));

const GradientBox = ({ backgroundColors, className, leftTitle, rightTitle, startAdornmentType = 'none', }) => (React__default.createElement(StyledGradientBox, { className: className },
    startAdornmentType === 'n/a' && React__default.createElement(NotApplicable, null),
    React__default.createElement(Stack$5, { className: "w-full", alignItems: "center", justifyContent: "space-between", gap: 1.5 },
        React__default.createElement(StyledLabel, { as: "label", fontSize: "2xs" }, leftTitle),
        React__default.createElement(StyledGradient, { "data-testid": "gradient", style: {
                backgroundImage: createLinearGradientCss(backgroundColors),
            } }),
        React__default.createElement(StyledLabel, { as: "label", fontSize: "2xs" }, rightTitle))));
const GradientBoxMemo = memo(GradientBox);

var _path;
function _extends$1() { return _extends$1 = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends$1.apply(null, arguments); }
var SvgCurrencyDollarCircle = function SvgCurrencyDollarCircle(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    ref: ref
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    d: "M12,0A12,12,0,1,0,24,12,12,12,0,0,0,12,0Zm1.46,18.59a.25.25,0,0,0-.21.24v.77a1.25,1.25,0,0,1-2.5,0v-.71a.25.25,0,0,0-.25-.25H9.35a1.25,1.25,0,0,1,0-2.5h3.59A1.12,1.12,0,0,0,13.36,14L9.72,12.53a3.6,3.6,0,0,1,.82-6.92.25.25,0,0,0,.21-.25V4.6a1.25,1.25,0,0,1,2.5,0v.7a.25.25,0,0,0,.25.25h1.15a1.25,1.25,0,1,1,0,2.5H11.06a1.12,1.12,0,0,0-.42,2.16l3.64,1.46a3.6,3.6,0,0,1-.82,6.92Z",
    fill: "currentColor"
  })));
};
var ForwardRef$1 = /*#__PURE__*/forwardRef(SvgCurrencyDollarCircle);
var Memo$1 = /*#__PURE__*/memo(ForwardRef$1);

var _g;
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
var SvgPencil1 = function SvgPencil1(props, ref) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    ref: ref
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M15.07,4a.49.49,0,0,0-.36-.15h0a.5.5,0,0,0-.35.14L3.44,14.91a.5.5,0,0,0,0,.71l4.94,4.94a.51.51,0,0,0,.36.15.49.49,0,0,0,.35-.15L20,9.65a.51.51,0,0,0,0-.71Z",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M2.43,16.8a.51.51,0,0,0-.84.24L.08,23.31a.49.49,0,0,0,.14.47.51.51,0,0,0,.47.14L7,22.41a.49.49,0,0,0,.36-.35.52.52,0,0,0-.12-.49Z",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M23.2,2.92,21.08.8a2.52,2.52,0,0,0-3.54,0L16.13,2.22a.48.48,0,0,0,0,.7l4.95,5a.48.48,0,0,0,.7,0L23.2,6.45A2.5,2.5,0,0,0,23.2,2.92Z",
    fill: "currentColor"
  }))));
};
var ForwardRef = /*#__PURE__*/forwardRef(SvgPencil1);
var Memo = /*#__PURE__*/memo(ForwardRef);

const StyledStats = _default$1(Stack$5) `rounded-lg gap-px overflow-hidden`;
const StyledStat = _default$1.div `
   ${({ $disabled }) => ($disabled ? 'opacity-50 pointer-events-none' : '')}
   flex-1
   border-gray-200
   overflow-hidden
   bg-gray-50
   px-4
   py-5
   shadow
   sm:px-6
   sm:py-7
`;
const EditButton = _default$1(Button$2) `
   text-primary-600
   cursor-pointer
   flex
   items-center
   rounded-sm
   focus:outline-none
   focus:ring
   focus:ring-1
   focus:ring-primary-300
   [&>*]:focus:outline-none

`;
const IconBox = _default$1.div `
   rounded-md
   p-3 
   self-start
`;
const StatIcon = _default$1.div `
   flex-shrink-0
   h-6
   w-6
   text-white
   text-inherit
`;
const StatTitle = _default$1.p `
   truncate
   text-sm
   font-medium
   text-gray-500
   mb-0
`;
const StatValue = _default$1.p `
   text-2xl
   font-semibold
   text-gray-900
   leading-7
   mb-0
`;
_default$1.div `
   ml-2
   flex
   items-end
   text-sm
   [&>span]:font-semibold
`;
_default$1.span `
   text-green-600
`;
_default$1.span `
   text-rsbry-600
`;
_default$1.div `
   ml-0.5
`;
const FullStatValue = _default$1.p `
   text-sm
   leading-none
   font-semibold
   text-gray-400
   mb-0
`;
const StyledMoneyCircle = _default$1(Memo$1) `
   w-4
   h-4
   text-gray-400
   mr-0.5
`;
const StyledEditPencil = _default$1(Memo) `
   w-5
   h-5
   mr-0.5
`;

const calculateStatValue = ({ disabled, money, stat }) => {
    const { base, suffix = '' } = lib.abbreviateNumber({ inputNumber: stat });
    const stateValue = disabled || stat === 0 ? '---' : `${lib.formatNumberToUsd(base, { withDollarSign: money })}${suffix}`;
    return stateValue;
};
const calculateFullStatValue = (stat, disabled) => {
    let numberValue = '';
    if (disabled || stat === 0) {
        numberValue = '---';
    }
    else {
        numberValue = lib.formatNumberWithCommas(stat);
    }
    return numberValue;
};
const Stat = ({ changeType, direction, disabled = false, editButtonText, icon, iconBgColor, money = true, negativeChangeText, onEdit, positiveChangeText, stat, title, }) => (React__default.createElement(StyledStat, { "$disabled": disabled, "$direction": direction },
    React__default.createElement(Stack$5, { direction: "row", className: "gap-5" },
        icon && (React__default.createElement(IconBox, { style: { backgroundColor: iconBgColor } },
            React__default.createElement(StatIcon, { "data-testid": "stat-icon" }, icon))),
        React__default.createElement("div", { className: "w-full" },
            React__default.createElement(Stack$5, { direction: "row", justifyContent: "space-between" },
                React__default.createElement(StatTitle, null, title),
                onEdit && (React__default.createElement(EditButton, { onClick: onEdit, "data-testid": "edit-stat-btn" },
                    React__default.createElement(StyledEditPencil, null),
                    editButtonText && (React__default.createElement(Typography, { fontSize: "sm", as: "span", fontWeight: "normal" }, editButtonText))))),
            React__default.createElement(Stack$5, { justifyContent: "space-between" },
                React__default.createElement(StatValue, null, calculateStatValue({ stat, disabled, money })),
                changeType && (React__default.createElement(ChangeText, { changeType: changeType, positiveChangeText: positiveChangeText, negativeChangeText: negativeChangeText, variant: changeType === 'plus' ? 'green' : 'red', className: "font-semibold" })),
                !changeType && (React__default.createElement(Stack$5, { alignItems: "end" },
                    money && React__default.createElement(StyledMoneyCircle, null),
                    React__default.createElement(FullStatValue, null, calculateFullStatValue(stat, disabled)))))))));

const Stats = ({ className, direction = 'column', stats }) => (React__default.createElement(StyledStats, { className: className, direction: direction }, stats.map((stat) => (React__default.createElement(Stat, { key: stat.title, title: stat.title, stat: stat.stat, changeType: stat.changeType, onEdit: stat.onEdit, editButtonText: stat.editButtonText, icon: stat.icon, disabled: stat.disabled, iconBgColor: stat.iconBgColor, direction: direction })))));

export { AbbrevNumberCell, AbbrevNumberCellMemo, ActionsMenuButton, Alert, AlertMemo, AlertModalLayout, AlertModalLayoutMemo, AllocationByProjectCell, AllocationByProjectCellMemo, AllocationInput, AllocationPill, AllocationSlider, AllocationSliderInput, AllocationSliderMemo, AmountCell, AmountCellMemo, AmountKpi, AmountKpiMemo, AutocompleteInput, AutocompleteTagInput, Avatar, AvatarMemo, AvatarStackAddCell, AvatarStackAddCellMemo, AvatarTitleCell, AvatarTitleCellMemo, AvatarWithText, AvatarWithTextMemo, Backdrop, Badge, BadgeMemo, BaseTab, BaseTabPanel, BaseTabs, Button, ButtonCheckBox, ButtonCheckBoxMemo, ButtonCircular, ButtonCircularMemo, ButtonGroup, ButtonGroupMemo, ButtonMemo, ButtonWithForwardRef, Card, CardMemo, CardWithAction, CardWithActionMemo, CardWithAvatar, CardWithAvatarMemo, Checkbox, CheckboxAtom, CheckboxContainer, CheckboxContainerMemo, CheckboxMemo, ColorVariant, ColoredAmount, ColoredAmountMemo, CompanyLogoIcon, CompanyLogoMemo, Container, ContainerMemo, ControlledAllocationSlider, ControlledAutocompleteInput, ControlledAutocompleteTagInput, ControlledButtonGroup, ControlledCheckbox, ControlledInput, ControlledMonthDatePicker, ControlledMonthPicker, ControlledRadioGroup, ControlledSelect, ControlledTagInput, ControlledTextArea, ControlledToggleSwitch, CountryCell, CountryCellMemo, DEFAULT_TOAST_DURATION, DEFAULT_TOAST_ERROR_DURATION, Divider, DividerMemo, DraggableMenu, DraggableMenuItem, DraggableMenuItemMemo, DraggableMenuItemWithToggle, DraggableMenuItemWithToggleMemo, DraggableMenuMemo, DropdownButton, DropdownButtonMemo, DropdownMenu, DropdownMenuMemo, DropdownMenuWithButton, DropdownMenuWithButtonMemo, EquationOperator, EquationOperatorMemo, EquationTerm, EquationTermMemo, FeedItem, FileDisplaySection, FileDropZone, FileMimeType, FlyoutMenu, FooterCell, FormButton, FormSection, FormSectionHelpText, FormSectionMemo, FormStickyHeader, FormStickyHeaderMemo, FormTitle, FormTitleMemo, GAP_X, GAP_XY, GAP_Y, GanttChart, GeneralFormError, GeneralFormErrorMemo, GradientBox, GradientBoxMemo, Grid$1 as Grid, GridItem, GroupHeader, Heatmap, HeatmapTooltip, Height, HelpLink, HumanizedDateCell, HumanizedDateCellMemo, Icon, IconAndTextCell, IconAndTextCellMemo, IconButton, IconButtonGroup, IconMemo, IconWithNotification, IconWithNotificationMemo, Indent, IndentMemo, IndexedScrollContainer, Input, InputContainer$1 as InputContainer, InputFormContainer, InputFormContainerMemo, InputMasks, InputMemo, LabelWithHelp, LeftColumnLayout, LeftColumnLayoutMemo, Loading, LoadingOverlay, Logo, Menu, MenuItem, MenuItemMemo, MenuMemo, Modal, ModalActions, ModalActionsMemo, ModalHeader, ModalHeaderMemo, ModalMemo, MonthDatePicker, MonthPicker, NavItem, NavItemContext, PageHeader, PageHeaderMemo, PageSubtitle, PageSubtitleMemo, PageTitle, PageTitleMemo, Popover, PopoverDisclosure, PopoverMemo, Progress, ProgressBar, ProgressMemo, ProjectAllocation, QuickActionGroup, RadioGroup, RadioGroupCards, RadioWithIcon, RadioWithIconGroup, ReadOnlyAllocationSlider, ReadOnlyAllocationSliderMemo, ScalableContainer, ScalableContainerMemo, ScreenLoading, SectionTitle, SectionTitleMemo, Select, SelectMemo, SideDrawer, SideDrawerHeader, SideNav, SideNavItem, SideNavMemo, SideNavSection, SideNavSectionMemo, Skeleton, Slider, SliderMemo, Spinner, SplitScreen, SplitScreenMemo, SplitView, Stack$5 as Stack, StackMemo, StackedGroupAvatars, StackedGroupAvatarsMemo, Stat, Stats, Status$1 as Status, StatusMemo, StepByStepAllocationSlider, StepByStepAllocationSliderMemo, StickyHeaderedList, StyledNavItemDiv, StyledPercentage, StyledTextLeftSpan, StyledTextRightSpan, SummaryEquation, SympatheticHighlightProvider, TabPanelContainer, Table, TableContainer, TableContainerMemo, Tag, TagInput, TagInputContainer, TextArea, TextAreaButton, TextAreaButtonMemo, TextAreaMemo, TextCell, TextCellMemo, Ticket, TitleWithHelp, Toast, ToastDurationOptions, ToastRegion, Toggle, ToggleMemo, ToggleSwitch, ToggleSwitchMemo, Tooltip, TooltipMemo, TopNavBarSelector, TopNavBarSelectorMemo, TrailingButton, TrailingButtonMemo, TrouserHeader, TwoColumnLayout, TwoColumnLayoutMemo, Typography, TypographyMemo, Width, Wizard, addActionColumn, colors$3 as colors, condensedContent, convertTextToColor, createLinearGradientCss, defaultGradient, generateData, getBorderRadius, getBoxShadow, getGapX, getGapXY, getGapY, getHeight, getInitials, getMonthDateOptions, getOverflow, getPaddingX, getPaddingY, getRecipeClassName, getRecipeColor, getRgbTileColor, getSelectPopoverId, getSelectedColumn, getSelectedRow, getSpaceX, getSpaceY, getTextDecoration, getTextOverflow, getTextTransform, getWhiteSpace, getWidth, grayedOutRowBackground, monthOptions, normalizeValue, normalizeValueCubicRootExponentialScale, normalizeValueSquareRootExponentialScale, pickRgbColor, pickRgbFromGradient, pipe$2 as pipe, possibleMonthDateOptions, sanitizeRgbGradient, useCommonTable, useControlledFlyoutMenu, useElementSize, useEventListener, useFeatures, useHoverIntent, useModal, useMonthDateOptions, usePopoverState, useScrollableElement, useScrollbarWidth, useSelectable, useSideDrawer, useSplitView, useStepper, useSympatheticHighlight, useTabState, useTopNavBarSelector };
//# sourceMappingURL=neo.tax.esm.js.map
